var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, {
      get: all[name],
      enumerable: true,
      configurable: true,
      set: (newValue) => all[name] = () => newValue
    });
};

// src/renderer/shapes/types.ts
var BOX_STYLE_NONE = 0;
var BOX_STYLE_LIGHT = 1;
var BOX_STYLE_HEAVY = 2;
var BOX_STYLE_DOUBLE = 3;

// src/unicode/ghostty-symbol-ranges.ts
var GHOSTTY_SYMBOL_RANGES = [
  [8592, 8703],
  [9312, 9471],
  [9728, 10175],
  [57344, 63743],
  [127232, 127487],
  [127744, 128591],
  [128640, 128767],
  [983040, 1048573],
  [1048576, 1114109]
];

// src/unicode/symbols.ts
function isGraphicsElementCodepoint(cp) {
  const isBoxDrawing = cp >= 9472 && cp <= 9599;
  const isBlockElement = cp >= 9600 && cp <= 9631;
  const isLegacyComputing = cp >= 129792 && cp <= 130047 || cp >= 117760 && cp <= 118463;
  const isPowerline = cp >= 57520 && cp <= 57559;
  return isBoxDrawing || isBlockElement || isLegacyComputing || isPowerline;
}
function isGhosttySymbolCodepoint(cp) {
  if (!Number.isFinite(cp))
    return false;
  const codepoint = Math.trunc(cp);
  if (codepoint < 0 || codepoint > 1114111)
    return false;
  return isCodepointInRanges(codepoint, GHOSTTY_SYMBOL_RANGES);
}
function isSymbolLikeCodepoint(cp) {
  return isGraphicsElementCodepoint(cp) || isGhosttySymbolCodepoint(cp);
}
function isCodepointInRanges(cp, ranges) {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = lo + hi >> 1;
    const range = ranges[mid];
    const start = range[0];
    const end = range[1];
    if (cp < start) {
      hi = mid - 1;
      continue;
    }
    if (cp > end) {
      lo = mid + 1;
      continue;
    }
    return true;
  }
  return false;
}

// src/renderer/shapes/classify.ts
function isPrivateUse(cp) {
  return cp >= 57344 && cp <= 63743 || cp >= 983040 && cp <= 1048573 || cp >= 1048576 && cp <= 1114109;
}
function isSpaceCp(cp) {
  return cp === 0 || cp === 32 || cp === 8194;
}
function isBoxDrawing(cp) {
  return cp >= 9472 && cp <= 9599;
}
function isBlockElement(cp) {
  return cp >= 9600 && cp <= 9631;
}
function isLegacyComputing(cp) {
  return cp >= 129792 && cp <= 130047 || cp >= 117760 && cp <= 118463;
}
function isPowerline(cp) {
  return cp >= 57520 && cp <= 57559;
}
function isBraille(cp) {
  return cp >= 10240 && cp <= 10495;
}
function isGraphicsElement(cp) {
  return isGraphicsElementCodepoint(cp);
}
function isSymbolCp(cp) {
  return isSymbolLikeCodepoint(cp);
}

// src/renderer/shapes/geometry.ts
function applyAlpha(color, alpha) {
  return [color[0], color[1], color[2], color[3] * alpha];
}
function pushRect(out, x, y, w, h, color) {
  out.push(x, y, w, h, color[0], color[1], color[2], color[3]);
}
function pushRectSnapped(out, x, y, w, h, color) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.ceil(x + w);
  const y1 = Math.ceil(y + h);
  const width = Math.max(0, x1 - x0);
  const height = Math.max(0, y1 - y0);
  if (width <= 0 || height <= 0)
    return;
  out.push(x0, y0, width, height, color[0], color[1], color[2], color[3]);
}
function pushRectBox(out, x, y, w, h, color) {
  const x0 = Math.round(x);
  const y0 = Math.round(y);
  const width = Math.max(1, Math.round(w));
  const height = Math.max(1, Math.round(h));
  if (width <= 0 || height <= 0)
    return;
  out.push(x0, y0, width, height, color[0], color[1], color[2], color[3]);
}

// src/renderer/shapes/block-elements.ts
function fillFrac(out, x, y, cellW, cellH, fx0, fx1, fy0, fy1, color) {
  const px0 = x + cellW * fx0;
  const px1 = x + cellW * fx1;
  const py0 = y + cellH * fy0;
  const py1 = y + cellH * fy1;
  pushRectSnapped(out, px0, py0, px1 - px0, py1 - py0, color);
}
function drawBlockElement(cp, x, y, cellW, cellH, color, out) {
  const full = () => pushRectSnapped(out, x, y, cellW, cellH, color);
  const lower = (fraction) => fillFrac(out, x, y, cellW, cellH, 0, 1, 1 - fraction, 1, color);
  const upper = (fraction) => fillFrac(out, x, y, cellW, cellH, 0, 1, 0, fraction, color);
  const left = (fraction) => fillFrac(out, x, y, cellW, cellH, 0, fraction, 0, 1, color);
  const right = (fraction) => fillFrac(out, x, y, cellW, cellH, 1 - fraction, 1, 0, 1, color);
  switch (cp) {
    case 9600:
      upper(0.5);
      return true;
    case 9601:
      lower(0.125);
      return true;
    case 9602:
      lower(0.25);
      return true;
    case 9603:
      lower(0.375);
      return true;
    case 9604:
      lower(0.5);
      return true;
    case 9605:
      lower(0.625);
      return true;
    case 9606:
      lower(0.75);
      return true;
    case 9607:
      lower(0.875);
      return true;
    case 9608:
      full();
      return true;
    case 9609:
      left(0.875);
      return true;
    case 9610:
      left(0.75);
      return true;
    case 9611:
      left(0.625);
      return true;
    case 9612:
      left(0.5);
      return true;
    case 9613:
      left(0.375);
      return true;
    case 9614:
      left(0.25);
      return true;
    case 9615:
      left(0.125);
      return true;
    case 9616:
      right(0.5);
      return true;
    case 9617:
      pushRectSnapped(out, x, y, cellW, cellH, applyAlpha(color, 0.25));
      return true;
    case 9618:
      pushRectSnapped(out, x, y, cellW, cellH, applyAlpha(color, 0.5));
      return true;
    case 9619:
      pushRectSnapped(out, x, y, cellW, cellH, applyAlpha(color, 0.75));
      return true;
    case 9620:
      upper(0.125);
      return true;
    case 9621:
      right(0.125);
      return true;
    case 9622:
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0.5, 1, color);
      return true;
    case 9623:
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0.5, 1, color);
      return true;
    case 9624:
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0, 0.5, color);
      return true;
    case 9625:
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0, 1, color);
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0.5, 1, color);
      return true;
    case 9626:
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0, 0.5, color);
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0.5, 1, color);
      return true;
    case 9627:
      fillFrac(out, x, y, cellW, cellH, 0, 1, 0, 0.5, color);
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0.5, 1, color);
      return true;
    case 9628:
      fillFrac(out, x, y, cellW, cellH, 0, 1, 0, 0.5, color);
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0.5, 1, color);
      return true;
    case 9629:
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0, 0.5, color);
      return true;
    case 9630:
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0, 0.5, color);
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0.5, 1, color);
      return true;
    case 9631:
      fillFrac(out, x, y, cellW, cellH, 0.5, 1, 0, 1, color);
      fillFrac(out, x, y, cellW, cellH, 0, 0.5, 0.5, 1, color);
      return true;
    default:
      return false;
  }
}

// src/renderer/box-drawing-map.ts
var BOX_LINE_MAP = new Map([
  [9472, [0, 1, 0, 1]],
  [9473, [0, 2, 0, 2]],
  [9474, [1, 0, 1, 0]],
  [9475, [2, 0, 2, 0]],
  [9484, [0, 1, 1, 0]],
  [9485, [0, 2, 1, 0]],
  [9486, [0, 1, 2, 0]],
  [9487, [0, 2, 2, 0]],
  [9488, [0, 0, 1, 1]],
  [9489, [0, 0, 1, 2]],
  [9490, [0, 0, 2, 1]],
  [9491, [0, 0, 2, 2]],
  [9492, [1, 1, 0, 0]],
  [9493, [1, 2, 0, 0]],
  [9494, [2, 1, 0, 0]],
  [9495, [2, 2, 0, 0]],
  [9496, [1, 0, 0, 1]],
  [9497, [1, 0, 0, 2]],
  [9498, [2, 0, 0, 1]],
  [9499, [2, 0, 0, 2]],
  [9500, [1, 1, 1, 0]],
  [9501, [1, 2, 1, 0]],
  [9502, [2, 1, 1, 0]],
  [9503, [1, 1, 2, 0]],
  [9504, [2, 1, 2, 0]],
  [9505, [2, 2, 1, 0]],
  [9506, [1, 2, 2, 0]],
  [9507, [2, 2, 2, 0]],
  [9508, [1, 0, 1, 1]],
  [9509, [1, 0, 1, 2]],
  [9510, [2, 0, 1, 1]],
  [9511, [1, 0, 2, 1]],
  [9512, [2, 0, 2, 1]],
  [9513, [2, 0, 1, 2]],
  [9514, [1, 0, 2, 2]],
  [9515, [2, 0, 2, 2]],
  [9516, [0, 1, 1, 1]],
  [9517, [0, 1, 1, 2]],
  [9518, [0, 2, 1, 1]],
  [9519, [0, 2, 1, 2]],
  [9520, [0, 1, 2, 1]],
  [9521, [0, 1, 2, 2]],
  [9522, [0, 2, 2, 1]],
  [9523, [0, 2, 2, 2]],
  [9524, [1, 1, 0, 1]],
  [9525, [1, 1, 0, 2]],
  [9526, [1, 2, 0, 1]],
  [9527, [1, 2, 0, 2]],
  [9528, [2, 1, 0, 1]],
  [9529, [2, 1, 0, 2]],
  [9530, [2, 2, 0, 1]],
  [9531, [2, 2, 0, 2]],
  [9532, [1, 1, 1, 1]],
  [9533, [1, 1, 1, 2]],
  [9534, [1, 2, 1, 1]],
  [9535, [1, 2, 1, 2]],
  [9536, [2, 1, 1, 1]],
  [9537, [1, 1, 2, 1]],
  [9538, [2, 1, 2, 1]],
  [9539, [2, 1, 1, 2]],
  [9540, [2, 2, 1, 1]],
  [9541, [1, 1, 2, 2]],
  [9542, [1, 2, 2, 1]],
  [9543, [2, 2, 1, 2]],
  [9544, [1, 2, 2, 2]],
  [9545, [2, 1, 2, 2]],
  [9546, [2, 2, 2, 1]],
  [9547, [2, 2, 2, 2]],
  [9552, [0, 3, 0, 3]],
  [9553, [3, 0, 3, 0]],
  [9554, [0, 3, 1, 0]],
  [9555, [0, 1, 3, 0]],
  [9556, [0, 3, 3, 0]],
  [9557, [0, 0, 1, 3]],
  [9558, [0, 0, 3, 1]],
  [9559, [0, 0, 3, 3]],
  [9560, [1, 3, 0, 0]],
  [9561, [3, 1, 0, 0]],
  [9562, [3, 3, 0, 0]],
  [9563, [1, 0, 0, 3]],
  [9564, [3, 0, 0, 1]],
  [9565, [3, 0, 0, 3]],
  [9566, [1, 3, 1, 0]],
  [9567, [3, 1, 3, 0]],
  [9568, [3, 3, 3, 0]],
  [9569, [1, 0, 1, 3]],
  [9570, [3, 0, 3, 1]],
  [9571, [3, 0, 3, 3]],
  [9572, [0, 3, 1, 3]],
  [9573, [0, 1, 3, 1]],
  [9574, [0, 3, 3, 3]],
  [9575, [1, 3, 0, 3]],
  [9576, [3, 1, 0, 1]],
  [9577, [3, 3, 0, 3]],
  [9578, [1, 3, 1, 3]],
  [9579, [3, 1, 3, 1]],
  [9580, [3, 3, 3, 3]],
  [9588, [0, 0, 0, 1]],
  [9589, [1, 0, 0, 0]],
  [9590, [0, 1, 0, 0]],
  [9591, [0, 0, 1, 0]],
  [9592, [0, 0, 0, 2]],
  [9593, [2, 0, 0, 0]],
  [9594, [0, 2, 0, 0]],
  [9595, [0, 0, 2, 0]],
  [9596, [0, 2, 0, 1]],
  [9597, [1, 0, 2, 0]],
  [9598, [0, 1, 0, 2]],
  [9599, [2, 0, 1, 0]]
]);

// src/renderer/shapes/box-drawing/dashed-lines.ts
var satSub = (a, b) => a > b ? a - b : 0;
function drawDashedHorizontal(count, thickness, desiredGap, x, y, cellWInt, cellHInt, color, out) {
  const thickPx = Math.max(1, Math.round(thickness));
  const gapCount = count;
  if (cellWInt < count + gapCount) {
    const y02 = y + Math.floor(satSub(cellHInt, thickPx) / 2);
    pushRectBox(out, x, y02, cellWInt, thickPx, color);
    return;
  }
  const maxGap = Math.floor(cellWInt / (2 * count));
  const gapWidth = Math.min(Math.max(1, Math.round(desiredGap)), maxGap);
  const totalGapWidth = gapCount * gapWidth;
  const totalDashWidth = cellWInt - totalGapWidth;
  const dashWidth = Math.floor(totalDashWidth / count);
  let extra = totalDashWidth % count;
  const y0 = y + Math.floor(satSub(cellHInt, thickPx) / 2);
  let px = x + Math.floor(gapWidth / 2);
  for (let i = 0;i < count; i += 1) {
    let seg = dashWidth;
    if (extra > 0) {
      seg += 1;
      extra -= 1;
    }
    pushRectBox(out, px, y0, seg, thickPx, color);
    px += seg + gapWidth;
  }
}
function drawDashedVertical(count, thickness, desiredGap, x, y, cellWInt, cellHInt, color, out) {
  const thickPx = Math.max(1, Math.round(thickness));
  const gapCount = count;
  if (cellHInt < count + gapCount) {
    const x02 = x + Math.floor(satSub(cellWInt, thickPx) / 2);
    pushRectBox(out, x02, y, thickPx, cellHInt, color);
    return;
  }
  const maxGap = Math.floor(cellHInt / (2 * count));
  const gapHeight = Math.min(Math.max(1, Math.round(desiredGap)), maxGap);
  const totalGapHeight = gapCount * gapHeight;
  const totalDashHeight = cellHInt - totalGapHeight;
  const dashHeight = Math.floor(totalDashHeight / count);
  let extra = totalDashHeight % count;
  const x0 = x + Math.floor(satSub(cellWInt, thickPx) / 2);
  let py = y;
  for (let i = 0;i < count; i += 1) {
    let seg = dashHeight;
    if (extra > 0) {
      seg += 1;
      extra -= 1;
    }
    pushRectBox(out, x0, py, thickPx, seg, color);
    py += seg + gapHeight;
  }
}

// src/renderer/shapes/box-drawing/diagonal.ts
function drawDiagonal(dir, x, y, cellW, cellH, light, color, out) {
  const thickness = light;
  const steps = Math.max(2, Math.round(Math.max(cellW, cellH)));
  for (let i = 0;i < steps; i += 1) {
    const t = steps === 1 ? 0 : i / (steps - 1);
    const px = dir === "ul_lr" ? x + t * cellW : x + (1 - t) * cellW;
    const py = y + t * cellH;
    pushRectBox(out, px - thickness * 0.5, py - thickness * 0.5, thickness, thickness, color);
  }
}

// src/renderer/shapes/box-drawing/rounded-corner.ts
var sampleOffsets = [
  [0.25, 0.25],
  [0.75, 0.25],
  [0.25, 0.75],
  [0.75, 0.75]
];
var cubicPoint = (a, b, c, d, t) => {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const t2 = t * t;
  return {
    x: mt2 * mt * a.x + 3 * mt2 * t * b.x + 3 * mt * t2 * c.x + t2 * t * d.x,
    y: mt2 * mt * a.y + 3 * mt2 * t * b.y + 3 * mt * t2 * c.y + t2 * t * d.y
  };
};
var addSegment = (segments, a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len <= 0.000001)
    return;
  const ux = dx / len;
  const uy = dy / len;
  segments.push({ ax: a.x, ay: a.y, ux, uy, nx: -uy, ny: ux, len });
};
var sampleInsideStroke = (sx, sy, segments, half) => {
  for (const seg of segments) {
    const rx = sx - seg.ax;
    const ry = sy - seg.ay;
    const along = rx * seg.ux + ry * seg.uy;
    if (along < 0 || along > seg.len)
      continue;
    const perp = Math.abs(rx * seg.nx + ry * seg.ny);
    if (perp <= half + 0.000001)
      return true;
  }
  return false;
};
function drawRoundedCorner(cornerCp, x, y, cellW, cellH, light, color, out) {
  const thickness = Math.max(1, Math.round(light));
  const half = thickness * 0.5;
  const s = 0.25;
  const cx = x + Math.floor((cellW - thickness) * 0.5) + half;
  const cy = y + Math.floor((cellH - thickness) * 0.5) + half;
  const r = Math.min(cellW, cellH) * 0.5;
  const p0 = { x: cx, y };
  const p1 = { x: cx, y: cy - r };
  const c1 = { x: cx, y: cy - s * r };
  const c2 = { x: cx, y: cy };
  const p2 = { x: cx, y: cy };
  const p3 = { x: cx, y: cy };
  let p4 = { x: x + cellW, y: cy };
  switch (cornerCp) {
    case 9581:
      p0.y = y + cellH;
      p1.y = cy + r;
      c1.y = cy + s * r;
      c2.x = cx + s * r;
      p2.x = cx + r;
      p3.x = cx + r;
      p4 = { x: x + cellW, y: cy };
      break;
    case 9582:
      p0.y = y + cellH;
      p1.y = cy + r;
      c1.y = cy + s * r;
      c2.x = cx - s * r;
      p2.x = cx - r;
      p3.x = cx - r;
      p4 = { x, y: cy };
      break;
    case 9583:
      c2.x = cx - s * r;
      p2.x = cx - r;
      p3.x = cx - r;
      p4 = { x, y: cy };
      break;
    case 9584:
      c2.x = cx + s * r;
      p2.x = cx + r;
      p3.x = cx + r;
      p4 = { x: x + cellW, y: cy };
      break;
  }
  const steps = Math.max(10, Math.round(Math.max(cellW, cellH) * 1.5));
  const curvePoints = [];
  for (let i = 0;i <= steps; i += 1) {
    const t = i / steps;
    curvePoints.push(cubicPoint(p1, c1, c2, p2, t));
  }
  const segments = [];
  addSegment(segments, p0, p1);
  for (let i = 1;i < curvePoints.length; i += 1) {
    addSegment(segments, curvePoints[i - 1], curvePoints[i]);
  }
  addSegment(segments, p3, p4);
  const minX = Math.max(Math.floor(x), Math.floor(Math.min(p0.x, p4.x, cx - r) - half - 1));
  const maxX = Math.min(Math.ceil(x + cellW) - 1, Math.ceil(Math.max(p0.x, p4.x, cx + r) + half + 1));
  const minY = Math.max(Math.floor(y), Math.floor(Math.min(p0.y, p4.y, cy - r) - half - 1));
  const maxY = Math.min(Math.ceil(y + cellH) - 1, Math.ceil(Math.max(p0.y, p4.y, cy + r) + half + 1));
  if (maxX < minX || maxY < minY)
    return;
  for (let py = minY;py <= maxY; py += 1) {
    let runX = -1;
    let runCoverage = 0;
    for (let px = minX;px <= maxX; px += 1) {
      let coverage = 0;
      for (const [ox, oy] of sampleOffsets) {
        if (sampleInsideStroke(px + ox, py + oy, segments, half))
          coverage += 1;
      }
      if (coverage > 0 && runX < 0) {
        runX = px;
        runCoverage = coverage;
        continue;
      }
      if (coverage > 0 && coverage === runCoverage)
        continue;
      if (runX >= 0) {
        const alphaColor = [
          color[0],
          color[1],
          color[2],
          color[3] * (runCoverage / sampleOffsets.length)
        ];
        pushRectBox(out, runX, py, px - runX, 1, alphaColor);
        runX = coverage > 0 ? px : -1;
        runCoverage = coverage;
      }
    }
    if (runX >= 0) {
      const alphaColor = [
        color[0],
        color[1],
        color[2],
        color[3] * (runCoverage / sampleOffsets.length)
      ];
      pushRectBox(out, runX, py, maxX - runX + 1, 1, alphaColor);
    }
  }
}

// src/renderer/shapes/box-drawing/fallback.ts
function drawFallbackBoxDrawing(cp, x, y, cellW, cellH, color, out, light, heavy) {
  const cellWInt = Math.max(1, Math.round(cellW));
  const cellHInt = Math.max(1, Math.round(cellH));
  switch (cp) {
    case 9476:
      drawDashedHorizontal(3, light, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9477:
      drawDashedHorizontal(3, heavy, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9480:
      drawDashedHorizontal(4, light, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9481:
      drawDashedHorizontal(4, heavy, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9478:
      drawDashedVertical(3, light, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9479:
      drawDashedVertical(3, heavy, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9482:
      drawDashedVertical(4, light, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9483:
      drawDashedVertical(4, heavy, Math.max(4, light), x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9548:
      drawDashedHorizontal(2, light, light, x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9549:
      drawDashedHorizontal(2, heavy, heavy, x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9550:
      drawDashedVertical(2, light, heavy, x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9551:
      drawDashedVertical(2, heavy, heavy, x, y, cellWInt, cellHInt, color, out);
      return true;
    case 9581:
    case 9582:
    case 9583:
    case 9584:
      drawRoundedCorner(cp, x, y, cellW, cellH, light, color, out);
      return true;
    case 9585:
      drawDiagonal("ur_ll", x, y, cellW, cellH, light, color, out);
      return true;
    case 9586:
      drawDiagonal("ul_lr", x, y, cellW, cellH, light, color, out);
      return true;
    case 9587:
      drawDiagonal("ul_lr", x, y, cellW, cellH, light, color, out);
      drawDiagonal("ur_ll", x, y, cellW, cellH, light, color, out);
      return true;
    default:
      return false;
  }
}

// src/renderer/shapes/box-drawing/mapped.ts
var satSub2 = (a, b) => a > b ? a - b : 0;
function drawMappedBoxDrawing(spec, x, y, cellW, cellH, color, out, lightStroke, heavyStroke) {
  const [up, right, down, left] = spec;
  const light = lightStroke;
  const heavy = heavyStroke;
  const cellWInt = Math.max(1, Math.round(cellW));
  const cellHInt = Math.max(1, Math.round(cellH));
  const hLightTop = Math.floor(satSub2(cellHInt, light) / 2);
  const hLightBottom = hLightTop + light;
  const hHeavyTop = Math.floor(satSub2(cellHInt, heavy) / 2);
  const hHeavyBottom = hHeavyTop + heavy;
  const hDoubleTop = satSub2(hLightTop, light);
  const hDoubleBottom = hLightBottom + light;
  const vLightLeft = Math.floor(satSub2(cellWInt, light) / 2);
  const vLightRight = vLightLeft + light;
  const vHeavyLeft = Math.floor(satSub2(cellWInt, heavy) / 2);
  const vHeavyRight = vHeavyLeft + heavy;
  const vDoubleLeft = satSub2(vLightLeft, light);
  const vDoubleRight = vLightRight + light;
  const upBottom = left === BOX_STYLE_HEAVY || right === BOX_STYLE_HEAVY ? hHeavyBottom : left !== right || down === up ? left === BOX_STYLE_DOUBLE || right === BOX_STYLE_DOUBLE ? hDoubleBottom : hLightBottom : left === BOX_STYLE_NONE && right === BOX_STYLE_NONE ? hLightBottom : hLightTop;
  const downTop = left === BOX_STYLE_HEAVY || right === BOX_STYLE_HEAVY ? hHeavyTop : left !== right || up === down ? left === BOX_STYLE_DOUBLE || right === BOX_STYLE_DOUBLE ? hDoubleTop : hLightTop : left === BOX_STYLE_NONE && right === BOX_STYLE_NONE ? hLightTop : hLightBottom;
  const leftRight = up === BOX_STYLE_HEAVY || down === BOX_STYLE_HEAVY ? vHeavyRight : up !== down || left === right ? up === BOX_STYLE_DOUBLE || down === BOX_STYLE_DOUBLE ? vDoubleRight : vLightRight : up === BOX_STYLE_NONE && down === BOX_STYLE_NONE ? vLightRight : vLightLeft;
  const rightLeft = up === BOX_STYLE_HEAVY || down === BOX_STYLE_HEAVY ? vHeavyLeft : up !== down || right === left ? up === BOX_STYLE_DOUBLE || down === BOX_STYLE_DOUBLE ? vDoubleLeft : vLightLeft : up === BOX_STYLE_NONE && down === BOX_STYLE_NONE ? vLightLeft : vLightRight;
  const drawBox = (x0, y0, x1, y1) => {
    if (x1 <= x0 || y1 <= y0)
      return;
    pushRectSnapped(out, x + x0, y + y0, x1 - x0, y1 - y0, color);
  };
  const drawHorizontalBand = (style) => {
    if (style === BOX_STYLE_LIGHT) {
      drawBox(0, hLightTop, cellWInt, hLightBottom);
      return;
    }
    if (style === BOX_STYLE_HEAVY) {
      drawBox(0, hHeavyTop, cellWInt, hHeavyBottom);
      return;
    }
    if (style === BOX_STYLE_DOUBLE) {
      drawBox(0, hDoubleTop, cellWInt, hLightTop);
      drawBox(0, hLightBottom, cellWInt, hDoubleBottom);
    }
  };
  const drawVerticalBand = (style) => {
    if (style === BOX_STYLE_LIGHT) {
      drawBox(vLightLeft, 0, vLightRight, cellHInt);
      return;
    }
    if (style === BOX_STYLE_HEAVY) {
      drawBox(vHeavyLeft, 0, vHeavyRight, cellHInt);
      return;
    }
    if (style === BOX_STYLE_DOUBLE) {
      drawBox(vDoubleLeft, 0, vLightLeft, cellHInt);
      drawBox(vLightRight, 0, vDoubleRight, cellHInt);
    }
  };
  if (up === BOX_STYLE_NONE && down === BOX_STYLE_NONE && left !== BOX_STYLE_NONE && left === right) {
    drawHorizontalBand(left);
    return true;
  }
  if (left === BOX_STYLE_NONE && right === BOX_STYLE_NONE && up !== BOX_STYLE_NONE && up === down) {
    drawVerticalBand(up);
    return true;
  }
  switch (up) {
    case BOX_STYLE_NONE:
      break;
    case BOX_STYLE_LIGHT:
      drawBox(vLightLeft, 0, vLightRight, upBottom);
      break;
    case BOX_STYLE_HEAVY:
      drawBox(vHeavyLeft, 0, vHeavyRight, upBottom);
      break;
    case BOX_STYLE_DOUBLE: {
      const leftBottom = left === BOX_STYLE_DOUBLE ? hLightTop : upBottom;
      const rightBottom = right === BOX_STYLE_DOUBLE ? hLightTop : upBottom;
      drawBox(vDoubleLeft, 0, vLightLeft, leftBottom);
      drawBox(vLightRight, 0, vDoubleRight, rightBottom);
      break;
    }
  }
  switch (right) {
    case BOX_STYLE_NONE:
      break;
    case BOX_STYLE_LIGHT:
      drawBox(rightLeft, hLightTop, cellWInt, hLightBottom);
      break;
    case BOX_STYLE_HEAVY:
      drawBox(rightLeft, hHeavyTop, cellWInt, hHeavyBottom);
      break;
    case BOX_STYLE_DOUBLE: {
      const topLeft = up === BOX_STYLE_DOUBLE ? vLightRight : rightLeft;
      const bottomLeft = down === BOX_STYLE_DOUBLE ? vLightRight : rightLeft;
      drawBox(topLeft, hDoubleTop, cellWInt, hLightTop);
      drawBox(bottomLeft, hLightBottom, cellWInt, hDoubleBottom);
      break;
    }
  }
  switch (down) {
    case BOX_STYLE_NONE:
      break;
    case BOX_STYLE_LIGHT:
      drawBox(vLightLeft, downTop, vLightRight, cellHInt);
      break;
    case BOX_STYLE_HEAVY:
      drawBox(vHeavyLeft, downTop, vHeavyRight, cellHInt);
      break;
    case BOX_STYLE_DOUBLE: {
      const leftTop = left === BOX_STYLE_DOUBLE ? hLightBottom : downTop;
      const rightTop = right === BOX_STYLE_DOUBLE ? hLightBottom : downTop;
      drawBox(vDoubleLeft, leftTop, vLightLeft, cellHInt);
      drawBox(vLightRight, rightTop, vDoubleRight, cellHInt);
      break;
    }
  }
  switch (left) {
    case BOX_STYLE_NONE:
      break;
    case BOX_STYLE_LIGHT:
      drawBox(0, hLightTop, leftRight, hLightBottom);
      break;
    case BOX_STYLE_HEAVY:
      drawBox(0, hHeavyTop, leftRight, hHeavyBottom);
      break;
    case BOX_STYLE_DOUBLE: {
      const topRight = up === BOX_STYLE_DOUBLE ? vLightLeft : leftRight;
      const bottomRight = down === BOX_STYLE_DOUBLE ? vLightLeft : leftRight;
      drawBox(0, hDoubleTop, topRight, hLightTop);
      drawBox(0, hLightBottom, bottomRight, hDoubleBottom);
      break;
    }
  }
  return true;
}

// src/renderer/shapes/box-drawing/draw-box-drawing.ts
function drawBoxDrawing(cp, x, y, cellW, cellH, color, out, boxThicknessPx) {
  const hasBoxThickness = Number.isFinite(boxThicknessPx) && typeof boxThicknessPx === "number" && boxThicknessPx > 0;
  const lightStroke = hasBoxThickness ? Math.max(1, Math.round(boxThicknessPx)) : Math.max(1, Math.floor(cellH / 16));
  const heavyStroke = lightStroke * 2;
  const spec = BOX_LINE_MAP.get(cp);
  if (!spec) {
    return drawFallbackBoxDrawing(cp, x, y, cellW, cellH, color, out, lightStroke, heavyStroke);
  }
  return drawMappedBoxDrawing(spec, x, y, cellW, cellH, color, out, lightStroke, heavyStroke);
}

// src/renderer/shapes/braille.ts
function drawBraille(cp, x, y, cellW, cellH, color, out) {
  if (!isBraille(cp))
    return false;
  const bits = cp - 10240;
  if (!bits)
    return true;
  const dotW = Math.max(1, Math.round(cellW * 0.18));
  const dotH = Math.max(1, Math.round(cellH * 0.18));
  const colX = [x + cellW * 0.25 - dotW * 0.5, x + cellW * 0.75 - dotW * 0.5];
  const rowY = [
    y + cellH * 0.125 - dotH * 0.5,
    y + cellH * 0.375 - dotH * 0.5,
    y + cellH * 0.625 - dotH * 0.5,
    y + cellH * 0.875 - dotH * 0.5
  ];
  const dots = [
    [0, 0, 1],
    [0, 1, 2],
    [0, 2, 4],
    [1, 0, 8],
    [1, 1, 16],
    [1, 2, 32],
    [0, 3, 64],
    [1, 3, 128]
  ];
  for (const [cx, cy, mask] of dots) {
    if (bits & mask) {
      pushRectSnapped(out, colX[cx], rowY[cy], dotW, dotH, color);
    }
  }
  return true;
}

// src/renderer/shapes/powerline.ts
function drawPowerline(cp, x, y, cellW, cellH, color, out) {
  if (!isPowerline(cp))
    return false;
  const w = cellW;
  const h = cellH;
  const steps = Math.max(2, Math.round(Math.max(w, h)));
  const drawTriangle = (mode) => {
    for (let i = 0;i < steps; i += 1) {
      const t = steps === 1 ? 0 : i / (steps - 1);
      const py = y + t * h;
      let x0 = x;
      let x1 = x + w;
      if (mode === "right") {
        const span = w * (1 - Math.abs(t - 0.5) * 2);
        x1 = x + span;
      } else if (mode === "left") {
        const span = w * (1 - Math.abs(t - 0.5) * 2);
        x0 = x + (w - span);
      } else if (mode === "diag_ul_lr") {
        x1 = x + t * w;
      } else if (mode === "diag_ur_ll") {
        x0 = x + t * w;
      } else if (mode === "diag_ul_lr_inv") {
        x1 = x + (1 - t) * w;
      } else if (mode === "diag_ur_ll_inv") {
        x0 = x + (1 - t) * w;
      }
      pushRectSnapped(out, x0, py, Math.max(1, x1 - x0), 1, color);
    }
  };
  switch (cp) {
    case 57520:
      drawTriangle("right");
      return true;
    case 57522:
      drawTriangle("left");
      return true;
    case 57528:
      drawTriangle("diag_ul_lr");
      return true;
    case 57530:
      drawTriangle("diag_ur_ll");
      return true;
    case 57532:
      drawTriangle("diag_ul_lr_inv");
      return true;
    case 57534:
      drawTriangle("diag_ur_ll_inv");
      return true;
    case 57529:
    case 57531:
    case 57533:
    case 57535:
    case 57521:
    case 57523:
      drawTriangle(cp === 57529 || cp === 57535 ? "diag_ul_lr" : "diag_ur_ll");
      return true;
    default:
      return false;
  }
}

// src/renderer/shapes/glyph-box.ts
function constrainGlyphBox(glyph, constraint, metrics, constraintWidth) {
  if (!constraint)
    return glyph;
  const sizeMode = constraint.size ?? "none";
  const alignH = constraint.align_horizontal ?? "none";
  const alignV = constraint.align_vertical ?? "none";
  const padLeft = constraint.pad_left ?? 0;
  const padRight = constraint.pad_right ?? 0;
  const padTop = constraint.pad_top ?? 0;
  const padBottom = constraint.pad_bottom ?? 0;
  const relW = constraint.relative_width ?? 1;
  const relH = constraint.relative_height ?? 1;
  const relX = constraint.relative_x ?? 0;
  const relY = constraint.relative_y ?? 0;
  const maxWidth = constraint.max_constraint_width ?? 2;
  const minConstraintWidth = Math.min(constraintWidth, maxWidth);
  if (glyph.width <= 0 || glyph.height <= 0)
    return glyph;
  const groupWidth = glyph.width / relW;
  const groupHeight = glyph.height / relH;
  let groupX = glyph.x - groupWidth * relX;
  let groupY = glyph.y - groupHeight * relY;
  const padWidthFactor = minConstraintWidth - (padLeft + padRight);
  const padHeightFactor = 1 - (padBottom + padTop);
  const targetWidth = padWidthFactor * metrics.faceWidth;
  const baseHeight = (constraint.height ?? "cell") === "icon" ? minConstraintWidth > 1 ? metrics.iconHeight : metrics.iconHeightSingle : metrics.faceHeight;
  const targetHeight = padHeightFactor * baseHeight;
  let widthFactor = targetWidth / groupWidth;
  let heightFactor = targetHeight / groupHeight;
  const scaleDownFit = Math.min(1, widthFactor, heightFactor);
  const scaleCover = Math.min(widthFactor, heightFactor);
  if (sizeMode === "fit") {
    widthFactor = scaleDownFit;
    heightFactor = scaleDownFit;
  } else if (sizeMode === "cover") {
    widthFactor = scaleCover;
    heightFactor = scaleCover;
  } else if (sizeMode === "fit_cover1") {
    widthFactor = scaleCover;
    heightFactor = scaleCover;
    if (minConstraintWidth > 1 && heightFactor > 1) {
      const single = constrainGlyphBox({ x: 0, y: 0, width: groupWidth, height: groupHeight }, { ...constraint, max_constraint_width: 1 }, metrics, 1);
      const singleScale = single.height / groupHeight;
      heightFactor = Math.max(1, singleScale);
      widthFactor = heightFactor;
    }
  } else if (sizeMode === "stretch") {
  } else {
    widthFactor = 1;
    heightFactor = 1;
  }
  if (constraint.max_xy_ratio !== undefined && constraint.max_xy_ratio !== null) {
    const ratio = constraint.max_xy_ratio;
    if (groupWidth * widthFactor > groupHeight * heightFactor * ratio) {
      widthFactor = groupHeight * heightFactor * ratio / groupWidth;
    }
  }
  const centerX = groupX + groupWidth * 0.5;
  const centerY = groupY + groupHeight * 0.5;
  const scaledGroupWidth = groupWidth * widthFactor;
  const scaledGroupHeight = groupHeight * heightFactor;
  groupX = centerX - scaledGroupWidth * 0.5;
  groupY = centerY - scaledGroupHeight * 0.5;
  const padBottomDy = padBottom * metrics.faceHeight;
  const padTopDy = padTop * metrics.faceHeight;
  const startY = metrics.faceY + padBottomDy;
  const endY = metrics.faceY + (metrics.faceHeight - scaledGroupHeight - padTopDy);
  const centerYAligned = (startY + endY) * 0.5;
  if (!(sizeMode === "none" && alignV === "none")) {
    if (alignV === "start")
      groupY = startY;
    else if (alignV === "end")
      groupY = endY;
    else if (alignV === "center" || alignV === "center1")
      groupY = centerYAligned;
    else
      groupY = Math.max(startY, Math.min(groupY, endY));
  }
  const padLeftDx = padLeft * metrics.faceWidth;
  const padRightDx = padRight * metrics.faceWidth;
  const fullFaceSpan = metrics.faceWidth + (minConstraintWidth - 1) * metrics.cellWidth;
  const startX = padLeftDx;
  const endX = fullFaceSpan - scaledGroupWidth - padRightDx;
  if (!(sizeMode === "none" && alignH === "none")) {
    if (alignH === "start")
      groupX = startX;
    else if (alignH === "end")
      groupX = Math.max(startX, endX);
    else if (alignH === "center")
      groupX = Math.max(startX, (startX + endX) * 0.5);
    else if (alignH === "center1") {
      const end1 = metrics.faceWidth - scaledGroupWidth - padRightDx;
      groupX = Math.max(startX, (startX + end1) * 0.5);
    } else {
      groupX = Math.max(startX, Math.min(groupX, endX));
    }
  }
  return {
    width: glyph.width * widthFactor,
    height: glyph.height * heightFactor,
    x: groupX + scaledGroupWidth * relX,
    y: groupY + scaledGroupHeight * relY
  };
}

// src/renderer/shaders/rect.ts
var RECT_SHADER = `
struct Uniforms {
  res: vec2f,
  _pad: vec2f,
  blend: vec2f,
  _pad2: vec2f,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;

struct VSIn {
  @location(0) quad: vec2f,
  @location(1) pos: vec2f,
  @location(2) size: vec2f,
  @location(3) color: vec4f,
};

struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) color: vec4f,
};

// sRGB to linear conversion for proper blending
fn srgbToLinear(c: f32) -> f32 {
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return pow((c + 0.055) / 1.055, 2.4);
}

fn srgbToLinear3(c: vec3f) -> vec3f {
  return vec3f(srgbToLinear(c.x), srgbToLinear(c.y), srgbToLinear(c.z));
}

@vertex
fn vsMain(input: VSIn) -> VSOut {
  let pixel = input.pos + input.quad * input.size;
  let clip = vec2f(
    (pixel.x / uniforms.res.x) * 2.0 - 1.0,
    1.0 - (pixel.y / uniforms.res.y) * 2.0
  );

  var out: VSOut;
  out.position = vec4f(clip.x, clip.y, 0.0, 1.0);
  out.color = input.color;
  return out;
}

@fragment
fn fsMain(input: VSOut) -> @location(0) vec4f {
  let useLinear = uniforms.blend.x > 0.5;
  var color = input.color;
  if (useLinear) {
    color = vec4f(srgbToLinear3(color.rgb), color.a);
  }
  color = vec4f(color.rgb * color.a, color.a);
  return color;
}
`;
var RECT_SHADER_GL_VERT = `#version 300 es
precision highp float;

uniform vec2 u_resolution;

layout(location = 0) in vec2 a_quad;
layout(location = 1) in vec2 a_pos;
layout(location = 2) in vec2 a_size;
layout(location = 3) in vec4 a_color;

out vec4 v_color;

void main() {
  vec2 pixel = a_pos + a_quad * a_size;
  vec2 clip = vec2(
    (pixel.x / u_resolution.x) * 2.0 - 1.0,
    1.0 - (pixel.y / u_resolution.y) * 2.0
  );
  gl_Position = vec4(clip, 0.0, 1.0);
  v_color = a_color;
}
`;
var RECT_SHADER_GL_FRAG = `#version 300 es
precision highp float;

uniform vec2 u_blend;

in vec4 v_color;
out vec4 fragColor;

// sRGB to linear conversion for proper blending
float srgbToLinear(float c) {
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return pow((c + 0.055) / 1.055, 2.4);
}

vec3 srgbToLinear(vec3 c) {
  return vec3(srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b));
}

void main() {
  bool useLinear = u_blend.x > 0.5;
  vec4 color = v_color;
  if (useLinear) {
    color.rgb = srgbToLinear(color.rgb);
  }
  color.rgb *= color.a;
  fragColor = color;
}
`;

// src/renderer/shaders/glyph-wgsl.ts
var GLYPH_SHADER = `
struct Uniforms {
  res: vec2f,
  _pad: vec2f,
  blend: vec2f,
  _pad2: vec2f,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var atlasSampler: sampler;
@group(0) @binding(2) var atlasTex: texture_2d<f32>;

struct VSIn {
  @location(0) quad: vec2f,
  @location(1) pos: vec2f,
  @location(2) size: vec2f,
  @location(3) uv0: vec2f,
  @location(4) uv1: vec2f,
  @location(5) color: vec4f,
  @location(6) bg: vec4f,
  @location(7) slant: f32,
  @location(8) mode: f32,
};

struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
  @location(1) color: vec4f,
  @location(2) bg: vec4f,
  @location(3) mode: f32,
};

@vertex
fn vsMain(input: VSIn) -> VSOut {
  let pixel = input.pos +
    vec2f(input.quad.x * input.size.x + input.slant * (1.0 - input.quad.y), input.quad.y * input.size.y);
  let clip = vec2f(
    (pixel.x / uniforms.res.x) * 2.0 - 1.0,
    1.0 - (pixel.y / uniforms.res.y) * 2.0
  );
  let uv = input.uv0 + (input.uv1 - input.uv0) * input.quad;

  var out: VSOut;
  out.position = vec4f(clip.x, clip.y, 0.0, 1.0);
  out.uv = uv;
  out.color = input.color;
  out.bg = input.bg;
  out.mode = input.mode;
  return out;
}

// sRGB to linear conversion for proper blending
fn srgbToLinear(c: f32) -> f32 {
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return pow((c + 0.055) / 1.055, 2.4);
}

// Linear to sRGB conversion
fn linearToSrgb(c: f32) -> f32 {
  if (c <= 0.0031308) {
    return c * 12.92;
  }
  return 1.055 * pow(c, 1.0 / 2.4) - 0.055;
}

fn srgbToLinear3(c: vec3f) -> vec3f {
  return vec3f(srgbToLinear(c.x), srgbToLinear(c.y), srgbToLinear(c.z));
}

fn linearToSrgb3(c: vec3f) -> vec3f {
  return vec3f(linearToSrgb(c.x), linearToSrgb(c.y), linearToSrgb(c.z));
}

fn luminance(color: vec3f) -> f32 {
  return dot(color, vec3f(0.2126, 0.7152, 0.0722));
}

@fragment
fn fsMain(input: VSOut) -> @location(0) vec4f {
  let atlasSample = textureSample(atlasTex, atlasSampler, input.uv);
  let useLinear = uniforms.blend.x > 0.5;
  let useCorrection = uniforms.blend.y > 0.5;

  if (input.mode > 0.5) {
    var color = atlasSample;
    if (useLinear) {
      color = vec4f(srgbToLinear3(color.rgb), color.a);
    }
    return vec4f(color.rgb * color.a, color.a);
  }

  var alpha = atlasSample.a;

  var fg = input.color;
  var bg = input.bg;
  if (useLinear) {
    fg = vec4f(srgbToLinear3(fg.rgb), fg.a);
    bg = vec4f(srgbToLinear3(bg.rgb), bg.a);
  }
  fg = vec4f(fg.rgb * fg.a, fg.a);
  bg = vec4f(bg.rgb * bg.a, bg.a);

  if (useCorrection && useLinear) {
    let fg_l = luminance(fg.rgb);
    let bg_l = luminance(bg.rgb);
    if (abs(fg_l - bg_l) > 0.001) {
      let blend_l = srgbToLinear(linearToSrgb(fg_l) * alpha + linearToSrgb(bg_l) * (1.0 - alpha));
      alpha = clamp((blend_l - bg_l) / (fg_l - bg_l), 0.0, 1.0);
    }
  }

  var color = fg * alpha;
  return color;
}
`;
var GLYPH_SHADER_NEAREST = `
struct Uniforms {
  res: vec2f,
  _pad: vec2f,
  blend: vec2f,
  _pad2: vec2f,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var atlasSampler: sampler;
@group(0) @binding(2) var atlasTex: texture_2d<f32>;

struct VSIn {
  @location(0) quad: vec2f,
  @location(1) pos: vec2f,
  @location(2) size: vec2f,
  @location(3) uv0: vec2f,
  @location(4) uv1: vec2f,
  @location(5) color: vec4f,
  @location(6) bg: vec4f,
  @location(7) slant: f32,
  @location(8) mode: f32,
};

struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
  @location(1) color: vec4f,
  @location(2) bg: vec4f,
  @location(3) mode: f32,
};

@vertex
fn vsMain(input: VSIn) -> VSOut {
  let pixel = input.pos +
    vec2f(input.quad.x * input.size.x + input.slant * (1.0 - input.quad.y), input.quad.y * input.size.y);
  let clip = vec2f(
    (pixel.x / uniforms.res.x) * 2.0 - 1.0,
    1.0 - (pixel.y / uniforms.res.y) * 2.0
  );
  let uv = input.uv0 + (input.uv1 - input.uv0) * input.quad;

  var out: VSOut;
  out.position = vec4f(clip.x, clip.y, 0.0, 1.0);
  out.uv = uv;
  out.color = input.color;
  out.bg = input.bg;
  out.mode = input.mode;
  return out;
}

// sRGB to linear conversion for proper blending
fn srgbToLinear(c: f32) -> f32 {
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return pow((c + 0.055) / 1.055, 2.4);
}

// Linear to sRGB conversion
fn linearToSrgb(c: f32) -> f32 {
  if (c <= 0.0031308) {
    return c * 12.92;
  }
  return 1.055 * pow(c, 1.0 / 2.4) - 0.055;
}

fn srgbToLinear3(c: vec3f) -> vec3f {
  return vec3f(srgbToLinear(c.x), srgbToLinear(c.y), srgbToLinear(c.z));
}

fn linearToSrgb3(c: vec3f) -> vec3f {
  return vec3f(linearToSrgb(c.x), linearToSrgb(c.y), linearToSrgb(c.z));
}

fn luminance(color: vec3f) -> f32 {
  return dot(color, vec3f(0.2126, 0.7152, 0.0722));
}

@fragment
fn fsMain(input: VSOut) -> @location(0) vec4f {
  let atlasSample = textureSample(atlasTex, atlasSampler, input.uv);
  let useLinear = uniforms.blend.x > 0.5;
  let useCorrection = uniforms.blend.y > 0.5;

  if (input.mode > 0.5) {
    var color = atlasSample;
    if (useLinear) {
      color = vec4f(srgbToLinear3(color.rgb), color.a);
    }
    return vec4f(color.rgb * color.a, color.a);
  }

  var alpha = atlasSample.a;

  var fg = input.color;
  var bg = input.bg;
  if (useLinear) {
    fg = vec4f(srgbToLinear3(fg.rgb), fg.a);
    bg = vec4f(srgbToLinear3(bg.rgb), bg.a);
  }
  fg = vec4f(fg.rgb * fg.a, fg.a);
  bg = vec4f(bg.rgb * bg.a, bg.a);

  if (useCorrection && useLinear) {
    let fg_l = luminance(fg.rgb);
    let bg_l = luminance(bg.rgb);
    if (abs(fg_l - bg_l) > 0.001) {
      let blend_l = srgbToLinear(linearToSrgb(fg_l) * alpha + linearToSrgb(bg_l) * (1.0 - alpha));
      alpha = clamp((blend_l - bg_l) / (fg_l - bg_l), 0.0, 1.0);
    }
  }

  var color = fg * alpha;
  return color;
}
`;

// src/renderer/webgpu/setup.ts
function getPreferredAndSrgbFormats() {
  const preferredFormat = navigator.gpu.getPreferredCanvasFormat();
  const srgbFormat = preferredFormat === "bgra8unorm" ? "bgra8unorm-srgb" : preferredFormat === "rgba8unorm" ? "rgba8unorm-srgb" : preferredFormat;
  return { preferredFormat, srgbFormat };
}
function configureContextFormat(context, device, preferredFormat, srgbFormat) {
  let format = preferredFormat;
  try {
    context.configure({ device, format: srgbFormat, alphaMode: "opaque" });
    format = srgbFormat;
  } catch {
    context.configure({ device, format: preferredFormat, alphaMode: "opaque" });
  }
  return { format, srgbSwapchain: format.endsWith("-srgb") };
}
function createWebGPUCoreState(device, format, srgbSwapchain) {
  const quadVertices = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);
  const vertexBuffer = device.createBuffer({
    size: quadVertices.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    mappedAtCreation: true
  });
  new Float32Array(vertexBuffer.getMappedRange()).set(quadVertices);
  vertexBuffer.unmap();
  const rectModule = device.createShaderModule({ code: RECT_SHADER });
  const glyphModule = device.createShaderModule({ code: GLYPH_SHADER });
  const glyphNearestModule = device.createShaderModule({ code: GLYPH_SHADER_NEAREST });
  const rectPipeline = device.createRenderPipeline({
    layout: "auto",
    vertex: {
      module: rectModule,
      entryPoint: "vsMain",
      buffers: [
        {
          arrayStride: 8,
          attributes: [{ shaderLocation: 0, offset: 0, format: "float32x2" }]
        },
        {
          arrayStride: 32,
          stepMode: "instance",
          attributes: [
            { shaderLocation: 1, offset: 0, format: "float32x2" },
            { shaderLocation: 2, offset: 8, format: "float32x2" },
            { shaderLocation: 3, offset: 16, format: "float32x4" }
          ]
        }
      ]
    },
    fragment: {
      module: rectModule,
      entryPoint: "fsMain",
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            },
            alpha: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            }
          }
        }
      ]
    },
    primitive: { topology: "triangle-list", cullMode: "none" }
  });
  const glyphPipeline = device.createRenderPipeline({
    layout: "auto",
    vertex: {
      module: glyphModule,
      entryPoint: "vsMain",
      buffers: [
        {
          arrayStride: 8,
          attributes: [{ shaderLocation: 0, offset: 0, format: "float32x2" }]
        },
        {
          arrayStride: 72,
          stepMode: "instance",
          attributes: [
            { shaderLocation: 1, offset: 0, format: "float32x2" },
            { shaderLocation: 2, offset: 8, format: "float32x2" },
            { shaderLocation: 3, offset: 16, format: "float32x2" },
            { shaderLocation: 4, offset: 24, format: "float32x2" },
            { shaderLocation: 5, offset: 32, format: "float32x4" },
            { shaderLocation: 6, offset: 48, format: "float32x4" },
            { shaderLocation: 7, offset: 64, format: "float32" },
            { shaderLocation: 8, offset: 68, format: "float32" }
          ]
        }
      ]
    },
    fragment: {
      module: glyphModule,
      entryPoint: "fsMain",
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            },
            alpha: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            }
          }
        }
      ]
    },
    primitive: { topology: "triangle-list", cullMode: "none" }
  });
  const glyphPipelineNearest = device.createRenderPipeline({
    layout: "auto",
    vertex: {
      module: glyphNearestModule,
      entryPoint: "vsMain",
      buffers: [
        {
          arrayStride: 8,
          attributes: [{ shaderLocation: 0, offset: 0, format: "float32x2" }]
        },
        {
          arrayStride: 72,
          stepMode: "instance",
          attributes: [
            { shaderLocation: 1, offset: 0, format: "float32x2" },
            { shaderLocation: 2, offset: 8, format: "float32x2" },
            { shaderLocation: 3, offset: 16, format: "float32x2" },
            { shaderLocation: 4, offset: 24, format: "float32x2" },
            { shaderLocation: 5, offset: 32, format: "float32x4" },
            { shaderLocation: 6, offset: 48, format: "float32x4" },
            { shaderLocation: 7, offset: 64, format: "float32" },
            { shaderLocation: 8, offset: 68, format: "float32" }
          ]
        }
      ]
    },
    fragment: {
      module: glyphNearestModule,
      entryPoint: "fsMain",
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            },
            alpha: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            }
          }
        }
      ]
    },
    primitive: { topology: "triangle-list", cullMode: "none" }
  });
  return {
    device,
    format,
    srgbSwapchain,
    rectPipeline,
    glyphPipeline,
    glyphPipelineNearest,
    vertexBuffer
  };
}
async function initWebGPUCore(canvas) {
  if (!navigator.gpu)
    return null;
  const adapter = await navigator.gpu.requestAdapter();
  if (!adapter)
    return null;
  const device = await adapter.requestDevice();
  const context = canvas.getContext("webgpu");
  if (!context)
    return null;
  const { preferredFormat, srgbFormat } = getPreferredAndSrgbFormats();
  const { format, srgbSwapchain } = configureContextFormat(context, device, preferredFormat, srgbFormat);
  return createWebGPUCoreState(device, format, srgbSwapchain);
}
async function initWebGPU(canvas, options = {}) {
  const core = options.core ?? await initWebGPUCore(canvas);
  if (!core)
    return null;
  const context = canvas.getContext("webgpu");
  if (!context)
    return null;
  try {
    context.configure({ device: core.device, format: core.format, alphaMode: "opaque" });
  } catch {
    return null;
  }
  const uniformBuffer = core.device.createBuffer({
    size: 8 * 4,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
  });
  const rectBindGroup = core.device.createBindGroup({
    layout: core.rectPipeline.getBindGroupLayout(0),
    entries: [{ binding: 0, resource: { buffer: uniformBuffer } }]
  });
  return {
    core,
    device: core.device,
    context,
    format: core.format,
    srgbSwapchain: core.srgbSwapchain,
    rectPipeline: core.rectPipeline,
    glyphPipeline: core.glyphPipeline,
    glyphPipelineNearest: core.glyphPipelineNearest,
    rectBindGroup,
    uniformBuffer,
    vertexBuffer: core.vertexBuffer,
    rectInstanceBuffer: core.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
    }),
    rectCapacity: 4,
    glyphInstanceBuffer: core.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
    }),
    glyphCapacity: 4,
    glyphAtlases: new Map
  };
}

// src/renderer/shaders/glyph-gl.ts
var GLYPH_SHADER_GL_VERT = `#version 300 es
precision highp float;

uniform vec2 u_resolution;

layout(location = 0) in vec2 a_quad;
layout(location = 1) in vec2 a_pos;
layout(location = 2) in vec2 a_size;
layout(location = 3) in vec2 a_uv0;
layout(location = 4) in vec2 a_uv1;
layout(location = 5) in vec4 a_color;
layout(location = 6) in vec4 a_bg;
layout(location = 7) in float a_slant;
layout(location = 8) in float a_mode;

out vec2 v_uv;
out vec4 v_color;
out vec4 v_bg;
out float v_mode;

void main() {
  vec2 pixel = a_pos + vec2(a_quad.x * a_size.x + a_slant * (1.0 - a_quad.y), a_quad.y * a_size.y);
  vec2 clip = vec2(
    (pixel.x / u_resolution.x) * 2.0 - 1.0,
    1.0 - (pixel.y / u_resolution.y) * 2.0
  );
  v_uv = a_uv0 + (a_uv1 - a_uv0) * a_quad;
  gl_Position = vec4(clip, 0.0, 1.0);
  v_color = a_color;
  v_bg = a_bg;
  v_mode = a_mode;
}
`;
var GLYPH_SHADER_GL_FRAG = `#version 300 es
precision highp float;

uniform sampler2D u_atlas;
uniform vec2 u_blend;

in vec2 v_uv;
in vec4 v_color;
in vec4 v_bg;
in float v_mode;
out vec4 fragColor;

// sRGB to linear conversion for proper blending
float srgbToLinear(float c) {
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return pow((c + 0.055) / 1.055, 2.4);
}

// Linear to sRGB conversion
float linearToSrgb(float c) {
  if (c <= 0.0031308) {
    return c * 12.92;
  }
  return 1.055 * pow(c, 1.0 / 2.4) - 0.055;
}

vec3 srgbToLinear(vec3 c) {
  return vec3(srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b));
}

vec3 linearToSrgb(vec3 c) {
  return vec3(linearToSrgb(c.r), linearToSrgb(c.g), linearToSrgb(c.b));
}

float luminance(vec3 color) {
  return dot(color, vec3(0.2126, 0.7152, 0.0722));
}

void main() {
  vec4 atlasSample = texture(u_atlas, v_uv);
  bool useLinear = u_blend.x > 0.5;
  bool useCorrection = u_blend.y > 0.5;

  if (v_mode > 0.5) {
    vec4 color = atlasSample;
    if (useLinear) {
      color.rgb = srgbToLinear(color.rgb);
    }
    color.rgb *= color.a;
    fragColor = color;
    return;
  }

  vec4 fg = v_color;
  vec4 bg = v_bg;
  if (useLinear) {
    fg.rgb = srgbToLinear(fg.rgb);
    bg.rgb = srgbToLinear(bg.rgb);
  }
  fg.rgb *= fg.a;
  bg.rgb *= bg.a;

  float alpha = atlasSample.a;
  if (useCorrection && useLinear) {
    float fg_l = luminance(fg.rgb);
    float bg_l = luminance(bg.rgb);
    if (abs(fg_l - bg_l) > 0.001) {
      float blend_l = srgbToLinear(linearToSrgb(fg_l) * alpha + linearToSrgb(bg_l) * (1.0 - alpha));
      alpha = clamp((blend_l - bg_l) / (fg_l - bg_l), 0.0, 1.0);
    }
  }

  vec4 color = fg * alpha;
  fragColor = color;
}
`;

// src/renderer/webgpu/webgl.ts
function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader)
    return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}
function createProgram(gl, vertSrc, fragSrc) {
  const vert = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vert || !frag)
    return null;
  const program = gl.createProgram();
  if (!program)
    return null;
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error("Program link error:", gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  return program;
}
function initWebGL(canvas) {
  const gl = canvas.getContext("webgl2", { antialias: false, alpha: false });
  if (!gl)
    return null;
  const rectProgram = createProgram(gl, RECT_SHADER_GL_VERT, RECT_SHADER_GL_FRAG);
  const glyphProgram = createProgram(gl, GLYPH_SHADER_GL_VERT, GLYPH_SHADER_GL_FRAG);
  if (!rectProgram || !glyphProgram)
    return null;
  const rectResolutionLoc = gl.getUniformLocation(rectProgram, "u_resolution");
  const rectBlendLoc = gl.getUniformLocation(rectProgram, "u_blend");
  const glyphResolutionLoc = gl.getUniformLocation(glyphProgram, "u_resolution");
  const glyphBlendLoc = gl.getUniformLocation(glyphProgram, "u_blend");
  const glyphAtlasLoc = gl.getUniformLocation(glyphProgram, "u_atlas");
  if (!rectResolutionLoc || !rectBlendLoc || !glyphResolutionLoc || !glyphBlendLoc || !glyphAtlasLoc) {
    return null;
  }
  const quadVertices = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);
  const quadBuffer = gl.createBuffer();
  if (!quadBuffer)
    return null;
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);
  const rectVao = gl.createVertexArray();
  if (!rectVao)
    return null;
  gl.bindVertexArray(rectVao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  const rectInstanceBuffer = gl.createBuffer();
  if (!rectInstanceBuffer)
    return null;
  gl.bindBuffer(gl.ARRAY_BUFFER, rectInstanceBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, 1024, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 32, 0);
  gl.vertexAttribDivisor(1, 1);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 2, gl.FLOAT, false, 32, 8);
  gl.vertexAttribDivisor(2, 1);
  gl.enableVertexAttribArray(3);
  gl.vertexAttribPointer(3, 4, gl.FLOAT, false, 32, 16);
  gl.vertexAttribDivisor(3, 1);
  gl.bindVertexArray(null);
  const glyphVao = gl.createVertexArray();
  if (!glyphVao)
    return null;
  gl.bindVertexArray(glyphVao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  const glyphInstanceBuffer = gl.createBuffer();
  if (!glyphInstanceBuffer)
    return null;
  gl.bindBuffer(gl.ARRAY_BUFFER, glyphInstanceBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, 1024, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 72, 0);
  gl.vertexAttribDivisor(1, 1);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 2, gl.FLOAT, false, 72, 8);
  gl.vertexAttribDivisor(2, 1);
  gl.enableVertexAttribArray(3);
  gl.vertexAttribPointer(3, 2, gl.FLOAT, false, 72, 16);
  gl.vertexAttribDivisor(3, 1);
  gl.enableVertexAttribArray(4);
  gl.vertexAttribPointer(4, 2, gl.FLOAT, false, 72, 24);
  gl.vertexAttribDivisor(4, 1);
  gl.enableVertexAttribArray(5);
  gl.vertexAttribPointer(5, 4, gl.FLOAT, false, 72, 32);
  gl.vertexAttribDivisor(5, 1);
  gl.enableVertexAttribArray(6);
  gl.vertexAttribPointer(6, 4, gl.FLOAT, false, 72, 48);
  gl.vertexAttribDivisor(6, 1);
  gl.enableVertexAttribArray(7);
  gl.vertexAttribPointer(7, 1, gl.FLOAT, false, 72, 64);
  gl.vertexAttribDivisor(7, 1);
  gl.enableVertexAttribArray(8);
  gl.vertexAttribPointer(8, 1, gl.FLOAT, false, 72, 68);
  gl.vertexAttribDivisor(8, 1);
  gl.bindVertexArray(null);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  return {
    gl,
    rectProgram,
    glyphProgram,
    rectResolutionLoc,
    rectBlendLoc,
    glyphResolutionLoc,
    glyphBlendLoc,
    glyphAtlasLoc,
    quadBuffer,
    rectVao,
    glyphVao,
    rectInstanceBuffer,
    glyphInstanceBuffer,
    rectCapacity: 1024,
    glyphCapacity: 1024,
    glyphAtlases: new Map
  };
}

// src/renderer/webgpu/buffers.ts
function ensureInstanceBuffer(state, kind, byteLength) {
  const bufferKey = kind === "rect" ? "rectInstanceBuffer" : "glyphInstanceBuffer";
  const capKey = kind === "rect" ? "rectCapacity" : "glyphCapacity";
  if (byteLength <= state[capKey])
    return;
  const newSize = Math.max(byteLength, state[capKey] * 2, 1024);
  state[bufferKey] = state.device.createBuffer({
    size: newSize,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
  });
  state[capKey] = newSize;
}
function configureContext(state) {
  state.context.configure({
    device: state.device,
    format: state.format,
    alphaMode: "opaque"
  });
}
function ensureGLInstanceBuffer(state, kind, byteLength) {
  const { gl } = state;
  const bufferKey = kind === "rect" ? "rectInstanceBuffer" : "glyphInstanceBuffer";
  const capKey = kind === "rect" ? "rectCapacity" : "glyphCapacity";
  const vaoKey = kind === "rect" ? "rectVao" : "glyphVao";
  if (byteLength <= state[capKey])
    return;
  const newSize = Math.max(byteLength, state[capKey] * 2, 1024);
  gl.bindVertexArray(state[vaoKey]);
  gl.bindBuffer(gl.ARRAY_BUFFER, state[bufferKey]);
  gl.bufferData(gl.ARRAY_BUFFER, newSize, gl.DYNAMIC_DRAW);
  state[capKey] = newSize;
  gl.bindVertexArray(null);
}

// src/renderer/webgpu/state.ts
function createResizeState() {
  return {
    active: false,
    lastAt: 0,
    cols: 0,
    rows: 0,
    dpr: 1
  };
}
function createScrollbarState() {
  return {
    lastInputAt: 0,
    lastTotal: 0,
    lastOffset: 0,
    lastLen: 0
  };
}
// src/utils/base64.ts
var textDecoder = new TextDecoder;
function decodeBase64Bytes(text) {
  const cleaned = text.replace(/\s+/g, "");
  if (!cleaned)
    return new Uint8Array(0);
  if (typeof atob === "function") {
    const binary = atob(cleaned);
    const out = new Uint8Array(binary.length);
    for (let i = 0;i < binary.length; i += 1) {
      out[i] = binary.charCodeAt(i) & 255;
    }
    return out;
  }
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(cleaned, "base64"));
  }
  throw new Error("No base64 decoder available in this environment.");
}
function encodeBase64Bytes(bytes) {
  if (typeof btoa === "function") {
    let binary = "";
    const chunk = 32768;
    for (let i = 0;i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }
  throw new Error("No base64 encoder available in this environment.");
}

// src/wasm/embedded.ts
var WASM_BASE64 = `
AGFzbQEAAAAByAEdYAR/f39/AX9gBX9/f39/AGAGf39/f39/AX9gAX8AYAJ/fwBgA39/fwBgAn9+AGAEf35/fwBgA39/fwF/YAR/f39/AGAFf39/f38Bf2AB
fwF/YAZ/f39/f38AYAJ/fwF/YAAAYAN/fn8Bf2ADf39+AGACf34Bf2ADf39+AX9gBX9/f39+AGADf39/AX5gBH9/f34AYAV/f39/fgF/YAN/fn4AYAF/AX5g
C39/f39/f39/f39/AGACf38BfmAAAX9gBX9+fn5+AAILAQNlbnYDbG9nAAQDsQWvBQUFBgMHCAAEBAQEAwQBAAEDCQgECgIJCQkJCQkGCwgICwgBDAwMAwQG
BAYDCAQDBAQDAwMEAw0FAQMNAwMNAwsFCQQNAw0DCw0DDQMNAw0DAw0DBAUEAw0DDQMNAw0ECA0DBgQEAw0DDQMNAw0ECA0DDQQNDQ0NDQgDDQQDDgMDAwMD
AwMFAwUDAwMDAwMDAwMDAwMDAwMDAwMDAwMDBAQDAwMDAwMDBQQFAwMDAwUDBAMDBQMNDgUOAwQFBAUDBAMDAwMDAwMDAwMDAwQDAwQDBAQDAwMDAwMDAwQD
BAMDAwMFAwQDBAMDBAMDBAMEAwQDBAMEAwQDBAMEAwMDBAMEDwMADQQJBQYFBQ0NDQ0NDQ0NDwMNCQUDDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NBAUEEAUEBQEDDQ0N
DQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQsNAwEBAQMNDQ0NDQ0NDQ0NBA0NDQ0NDQ0NDQ0NDRENDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0IDQ0IDQ0K
DQ0JCQkCAwQDAw4OBQkEBAkFCRIFBRMJDAEEBAQFDQ0NBQsLBQwDCAUDDQ0IAwUDCwMEDQADBA4NBAMNDQgICAgEBAMJBA0LCAgFCwsDDgQEBQAODQsJDQ0F
DQ0UAAkVFgEFCQANDQMNCQgIAwMDAwMDDQQEDQUFAwUNDQMFBAQNBAQXBQQNDQ0YDQ0ADQUIDQ0AAAICAQ0NDQ0NDQQEGAAMDAwMDAwNCA0NCQkJCgoKDQQL
CwsLCw0LAAgLCwsLDQ0ICAgIBAQEDwQEBAQEBAQGBAQNDQQEBAsACAgACAgICwsLCAQNBQUZCAkIGgQNCwsLCwsLCwsLCwsLCwsLCwsLCwsLCwsLCwsLCwsL
CwsLGwsLCwQDAgIBCQgLHAQFAXABDw8FAwEAEwYJAX8BQYCAwAALB7QNQwZtZW1vcnkCAA1yZXN0dHlfY3JlYXRlAAYOcmVzdHR5X2Rlc3Ryb3kAJwxyZXN0
dHlfd3JpdGUALRZyZXN0dHlfc2Nyb2xsX3ZpZXdwb3J0AMMEFnJlc3R0eV9zY3JvbGxiYXJfdG90YWwAxQQXcmVzdHR5X3Njcm9sbGJhcl9vZmZzZXQAxgQU
cmVzdHR5X3Njcm9sbGJhcl9sZW4AxwQRcmVzdHR5X291dHB1dF9wdHIAyAQRcmVzdHR5X291dHB1dF9sZW4AyQQVcmVzdHR5X291dHB1dF9jb25zdW1lAMoE
G3Jlc3R0eV9raXR0eV9rZXlib2FyZF9mbGFncwDLBBlyZXN0dHlfc2V0X2RlZmF1bHRfY29sb3JzAMwEEnJlc3R0eV9zZXRfcGFsZXR0ZQDNBBRyZXN0dHlf
cmVzZXRfcGFsZXR0ZQDOBBtyZXN0dHlfZ2V0X2NvbG9yX2ZvcmVncm91bmQAzwQbcmVzdHR5X2dldF9jb2xvcl9iYWNrZ3JvdW5kANAEF3Jlc3R0eV9nZXRf
Y29sb3JfY3Vyc29yANEEGHJlc3R0eV9nZXRfcGFsZXR0ZV9jb2xvcgDSBBJyZXN0dHlfZ2V0X3BhbGV0dGUA0wQNcmVzdHR5X3Jlc2l6ZQDUBBVyZXN0dHlf
c2V0X3BpeGVsX3NpemUA1QQWcmVzdHR5X3NuYXBzaG90X2ltcG9ydADWBBZyZXN0dHlfc25hcHNob3RfZXhwb3J0AOsEE3Jlc3R0eV9zbmFwc2hvdF9wdHIA
8wQTcmVzdHR5X3NuYXBzaG90X2xlbgD0BBRyZXN0dHlfcmVuZGVyX3VwZGF0ZQD1BBByZXN0dHlfY2VsbHNfbGVuAIIFGnJlc3R0eV9jZWxsX2NvZGVwb2lu
dHNfcHRyAIMFHHJlc3R0eV9jZWxsX2NvbnRlbnRfdGFnc19wdHIAhAUUcmVzdHR5X2NlbGxfd2lkZV9wdHIAhQUVcmVzdHR5X2NlbGxfZmxhZ3NfcHRyAIYF
G3Jlc3R0eV9jZWxsX3N0eWxlX2ZsYWdzX3B0cgCHBSByZXN0dHlfY2VsbF91bmRlcmxpbmVfc3R5bGVzX3B0cgCIBRhyZXN0dHlfY2VsbF9saW5rX2lkc19w
dHIAiQUXcmVzdHR5X2NlbGxfZmdfcmdiYV9wdHIAigUXcmVzdHR5X2NlbGxfYmdfcmdiYV9wdHIAiwUXcmVzdHR5X2NlbGxfdWxfcmdiYV9wdHIAjAUXcmVz
dHR5X2xpbmtfb2Zmc2V0c19wdHIAjQUXcmVzdHR5X2xpbmtfbGVuZ3Roc19wdHIAjgUWcmVzdHR5X2xpbmtfYnVmZmVyX3B0cgCPBRFyZXN0dHlfbGlua19j
b3VudACQBRZyZXN0dHlfbGlua19idWZmZXJfbGVuAJEFIHJlc3R0eV9jZWxsX2dyYXBoZW1lX29mZnNldHNfcHRyAJIFIHJlc3R0eV9jZWxsX2dyYXBoZW1l
X2xlbmd0aHNfcHRyAJMFGnJlc3R0eV9ncmFwaGVtZV9idWZmZXJfcHRyAJQFGnJlc3R0eV9ncmFwaGVtZV9idWZmZXJfbGVuAJUFHnJlc3R0eV9yb3dfc2Vs
ZWN0aW9uX3N0YXJ0X3B0cgCWBRxyZXN0dHlfcm93X3NlbGVjdGlvbl9lbmRfcHRyAJcFFnJlc3R0eV9jdXJzb3JfaW5mb19wdHIAmAULcmVzdHR5X3Jvd3MA
mQULcmVzdHR5X2NvbHMAmgUWcmVzdHR5X2FjdGl2ZV9jdXJzb3JfeACbBRZyZXN0dHlfYWN0aXZlX2N1cnNvcl95AJwFGHJlc3R0eV9kZWJ1Z19zY3JvbGxf
bGVmdACdBRlyZXN0dHlfZGVidWdfc2Nyb2xsX3JpZ2h0AJ4FFnJlc3R0eV9kZWJ1Z190ZXJtX2NvbHMAnwUWcmVzdHR5X2RlYnVnX3Rlcm1fcm93cwCgBRZy
ZXN0dHlfZGVidWdfcGFnZV9jb2xzAKEFFnJlc3R0eV9kZWJ1Z19wYWdlX3Jvd3MAogUdcmVzdHR5X2tpdHR5X3BsYWNlbWVudF9zdHJpZGUAowUccmVzdHR5
X2tpdHR5X3BsYWNlbWVudF9jb3VudACkBRtyZXN0dHlfa2l0dHlfcGxhY2VtZW50c19wdHIApQUMcmVzdHR5X2FsbG9jAKYFC3Jlc3R0eV9mcmVlAKcFG2do
b3N0dHlfdnRfc2V0X2xvZ19jYWxsYmFjawCoBRByZXN0dHlfY2VsbHNfcHRyAK4FCR0BAEEBCw4HqQWqBTkOrAUeH6sFrQWlBKYEpwSoBAqThCmvBf0IBAd/
AX4CfwF+I4CAgIAAQeADayIDJICAgIAAIANBgKDLmAE2AiQgAyACLwHeECIEOwEqIAMgAi8B3BAiBTsBKCADIAIoAgg2AiACQAJAIAEoAgAiBkHQAkEDQQAg
ASgCBCIHKAIAEYCAgIAAgICAgAAiCA0AIABBGTsBiBIMAQsCQEHQAkUNACAIQaoBQdAC/AsACwJAIAhBB3ENACADQYABaiABIANBIGoQgoCAgAACQCADLwHQ
AyIJRQ0AIAYgCEHQAkEDQQAgB0EMaigCABGBgICAAICAgIAAIAAgCTsBiBIMAgsCQEHQAkUNACAIIANBgAFqQdAC/AoAAAsgA0EMakEIaiIGQQA2AgAgA0EA
OgAsIABBADsBiBIgACAFOwH6ESAAIAQ7AfgRIAMgCDYCECADIAg2AgwgACADKQIMNwIYIANBADYAGSAAQShqIANBHGooAgA2AgAgA0EALQCs3sGAAEEBcjoA
GCAAQSBqIAYpAgA3AgAgASkCACEKIANBgAFqQQhqQQAoArz4yYAANgIAIANBACkCtPjJgAA3A4ABQarVqtV6IQhBACEJAkAgBUGBBEkNAAJAAkBBAA0AIAqn
IAVBgHxqIglBAEEAIApCIIinKAIAEYCAgIAAgICAgAAiCA0BIAEpAgAhCgsgA0EMaiAKEIOAgIAAIABBGTsBiBIMAwsCQCAJRQ0AIAhBACAJ/AsACyADIAk2
AogBIAMgCDYChAELIANBlAFqQgA3AgAgA0GcAWpCADcCACADQaQBakIANwIAIANBrAFqQgA3AgAgA0G0AWpCADcCACADQbwBakIANwIAIANBxAFqQgA3AgAg
A0IANwKMASADIAU2AoABAkAgCUUNACAIQQAgCfwLAAsgBUF/aiIHIAVLDQACQCAHQQlJDQAgCEFBaiELIANBjQFqIQxBACEIQQghAQNAAkACQCABQf8DSw0A
IAwgCGoiBiAGLQAAQQFyOgAADAELIAhBQWogCU8NAyALIAhqIgYgBi0AAEEBcjoAAAsgAUEIaiIGIAFJDQIgCEEBaiEIIAYhASAGIAdJDQALCwJAQcwARSIB
DQAgA0EwaiADQYABakHMAPwKAAALAkAgAQ0AIABBLGogA0EwakHMAPwKAAALIABBADsB/BEgBEH//wNxQX9qIgFB//8DcSABRw0AIABBADsBgBIgACABOwH+
ESAFQX9qIgFB//8DcSABRw0AIAAgATsBghIgAEEAKQLY+cmAACIKNwKAASAAQYgBakEAKALg+cmAACIBNgIAIAAgCjcCjAEgAEGUAWogATYCAAJAQdAQRQ0A
IABBmAFqIAJBDGpB0BD8CgAACyAAQQxqQgA8AAAgAEKExICQAz4CCCAAQfYRakEAOgAAIABBgBA7AfQRIABBADoAhBIgAEEINgLwESAAQgA3A+gRIABCADcC
eCAAQRRqIAJBBGoxAAAiCjwAACAAIAI1AgAiDT4CECAAQQRqIAo8AAAgACANPgIADAELAAsgA0HgA2okgICAgAALix0FBH8BfgZ/An4CfyOAgICAAEGwA2si
AySAgICAACACKAIAIQQgAi8BCiEFIAIvAQghBiABKQIAIQcgA0EANgKgAyADQgA3A5gDIAMgBzcDkAMCQAJAAkACQAJAAkBBAA0AIANBkANqQbACQQMgCBCP
gICAACIIRQ0AAkBBrAJFDQAgCEEEakGqAUGsAvwLAAsgCEEHcQ0FIAggAygCoAM2AgAgAyAINgKgA0EADQAgA0GQA2pBsAJBAyAIEI+AgIAAIghFDQACQEGs
AkUNACAIQQRqQaoBQawC/AsACyAIQQdxDQUgCCADKAKgAzYCACADIAg2AqADQQANACADQZADakGwAkEDIAgQj4CAgAAiCEUNAAJAQawCRQ0AIAhBBGpBqgFB
rAL8CwALIAhBB3ENBSAIIAMoAqADNgIAIAMgCDYCoANBAA0AIANBkANqQbACQQMgCBCPgICAACIIRQ0AAkBBrAJFDQAgCEEEakGqAUGsAvwLAAsgCEEHcQ0F
IAggAygCoAM2AgAgAyAINgKgAyADKAKQAyEJIAMoApQDIQogAygCmAMhCCADKQKcAyEHIANBADYCoAMgA0IANwOYAyADQQApA+D6yYAANwOQA0EADQEgA0GQ
A2pBgIAkQRAgCBCPgICAACILRQ0BAkBB/P8jRQ0AIAtBBGpBqgFB/P8j/AsACyALQf//A3ENBSALIAMoAqADNgIAIAMgCzYCoANBAA0BIANBkANqQYCAJEEQ
IAgQj4CAgAAiC0UNAQJAQfz/I0UNACALQQRqQaoBQfz/I/wLAAsgC0H//wNxDQUgCyADKAKgAzYCACADIAs2AqADQQANASADQZADakGAgCRBECAIEI+AgIAA
IgtFDQECQEH8/yNFDQAgC0EEakGqAUH8/yP8CwALIAtB//8DcQ0FIAsgAygCoAM2AgAgAyALNgKgA0EADQEgA0GQA2pBgIAkQRAgCBCPgICAACILRQ0BAkBB
/P8jRQ0AIAtBBGpBqgFB/P8j/AsACyALQf//A3ENBSALIAMoAqADNgIAIAMgCzYCoAMgAygCkAMhDCADKAKUAyENIAMoApgDIQsgAykCnAMhDiABKQIAIQ8g
A0EANgKgAyADQgA3A5gDIAMgDzcDkANBAA0CIANBkANqQQxBAiAIEI+AgIAAIhBFDQIgEEKq1arVqtWq1ap/NwAAIBBBCGpBqtWq1Xo2AAAgEEEDcQ0FIBAg
AygCoAM2AgAgAyAQNgKgA0EADQIgA0GQA2pBDEECIAgQj4CAgAAiEEUNAiAQQqrVqtWq1arVqn83AAAgEEEIakGq1arVejYAACAQQQNxDQUgECADKAKgAzYC
ACADIBA2AqADQQANAiADQZADakEMQQIgCBCPgICAACIQRQ0CIBBCqtWq1arVqtWqfzcAACAQQQhqQarVqtV6NgAAIBBBA3ENBSAQIAMoAqADNgIAIAMgEDYC
oANBAA0CIANBkANqQQxBAiAIEI+AgIAAIhBFDQIgEEKq1arVqtWq1ap/NwAAIBBBCGpBqtWq1Xo2AAAgEEEDcQ0FIBAgAygCoAM2AgAgAyAQNgKgA0EADQIg
A0GQA2pBDEECIAgQj4CAgAAiEEUNAiAQQqrVqtWq1arVqn83AAAgEEEIakGq1arVejYAACAQQQNxDQUgECADKAKgAzYCACADIBA2AqADQQANAiADQZADakEM
QQIgCBCPgICAACIQRQ0CIBBCqtWq1arVqtWqfzcAACAQQQhqQarVqtV6NgAAIBBBA3ENBSAQIAMoAqADNgIAIAMgEDYCoANBAA0CIANBkANqQQxBAiAIEI+A
gIAAIhBFDQIgEEKq1arVqtWq1ap/NwAAIBBBCGpBqtWq1Xo2AAAgEEEDcQ0FIBAgAygCoAM2AgAgAyAQNgKgA0EADQIgA0GQA2pBDEECIAgQj4CAgAAiEEUN
AiAQQqrVqtWq1arVqn83AAAgEEEIakGq1arVejYAACAQQQNxRQ0DDAULAkAgAygCmAMiCEUNACADKAKQAyEBIAMoApQDIQsDQCAIQXxqIQIgCCgCACEIIAEg
AiACKAIAQQJBACALKAIMEYGAgIAAgICAgAAgCA0ACwtBGSELDAMLAkAgAygCmAMiAkUNACADKAKQAyELIAMoApQDIQUDQCACQXxqIQEgAigCACECIAsgASAB
KAIAQQJBACAFKAIMEYGAgIAAgICAgAAgAg0ACwtBGSELIAhFDQIDQCAIQXxqIQIgCCgCACEIIAkgAiACKAIAQQJBACAKKAIMEYGAgIAAgICAgAAgCA0ADAML
CwJAIAMoApgDIgJFDQAgAygCkAMhBSADKAKUAyEGA0AgAkF8aiEBIAIoAgAhAiAFIAEgASgCAEECQQAgBigCDBGBgICAAICAgIAAIAINAAsLAkAgC0UNAANA
IAtBfGohAiALKAIAIQsgDCACIAIoAgBBAkEAIA0oAgwRgYCAgACAgICAACALDQALC0EZIQsgCEUNAQNAIAhBfGohAiAIKAIAIQggCSACIAIoAgBBAkEAIAoo
AgwRgYCAgACAgICAACAIDQAMAgsLIBAgA0GQA2pBEGooAgA2AgAgA0HAAmpBEGoiESAQNgIAIANBwAJqQQhqIhAgA0GQA2pBCGopAwA3AwAgAyADKQOQAzcD
wAIgAyAONwOgAiADIAs2ApwCIAMgDTYCmAIgAyAMNgKUAiADIAc3AowCIAMgCDYCiAIgAyAKNgKEAiADIAk2AoACIAMgASkCADcD+AEgA0GwAmogECkDADcD
ACADQbgCaiARKAIANgIAIAMgAykDwAI3A6gCIANCADcD2AIgA0HgAmogA0H4AWogA0HYAmogBiAFEJCAgIAAAkAgAy8B7AIiC0UNACADQfgBahCRgICAAAwB
CyADKALoAiEMIAMoAuQCIQ0gAygC4AIhCSADQZADakGo38GAACAGQQEQkoCAgABBAiEIAkACQEHXASADLwGaAyADLwGgAxsiCkH//wNxIgsgBU8NACALRQ0B
IAVBf2pB//8DcSAKQf//A3FuQQJqIQgLIAitQoCAJH4iB0IgiKcNAgJAAkACQAJAIAMoArgCIghFDQAgAyAIKAIANgK4AgwBC0EADQEgA0GoAmpBDEECIAgQ
j4CAgAAiCEUNASAIQqrVqtWq1arVqn83AAAgCEEIakGq1arVejYAACAIQQNxDQULIAhCqtWq1arVqtWqfzcCACAIQQhqQarVqtV6NgIAIAlFDQQgCEEAOgAI
IAhBADYBBCAIIAk2AgAgA0HwAmpBCGpCADcDACADQfACakEQaiIQQQA2AgAgA0IANwPwAiADQfACaiADQfgBaiAIEJOAgIAAIgtB//8DcUUNASADQfACaiAD
QfgBahCUgICAACADQfgBahCRgICAAAwDCyADQfgBahCRgICAAEEZIQsMAgsgB6chCyADQQA6AIgDIANBCGpBEGohCiADKQPYAiEHAkBBxABFDQAgCiADQfgB
akHEAPwKAAALIANBoAFqQRBqIhEgECgCADYCACADQaABakEIaiIQIANB8AJqQQhqKQMANwMAIAMgAykD8AI3A6ABIAMgBTYCcCADIAs2AmwgAyAENgJoIAMg
DDYCZCADIA02AmAgAyAJNgJcIANCADcDECADIAc3AwggA0GEAWogESgCADYCACADQfwAaiAQKQMANwIAIAMgAykDoAE3AnQgA0EAOgCYASADIAU7AZYBIAMg
BjsBlAEgA0IANwKMASADIAg2AogBIANBADoAtAECQAJAAkACQCADKAJYIghFDQAgAyAIKAIANgJYDAELQRkhC0EADQEgA0HIAGpBDEECIAgQj4CAgAAiCEUN
ASAIQqrVqtWq1arVqn83AAAgCEEIakGq1arVejYAACAIQQNxDQULIAhCgICAgKDVqtWqfzcCBCAIIAk2AgAgCCADLQC0AToACCADQfQAaiAKIAgQk4CAgAAi
C0H//wNxRQ0BIAhCqtWq1arVqtWqfzcCACAIQQhqQarVqtV6NgIAIAggAygCWDYCACADIAg2AlgLIANBCGoQhICAgAAMAgsgCC8BBCIKIAgoAgAiCy8BqgJP
DQIgCC8BBiIFIAsvAagCTw0CIAsoAhAiCSALKAIYaiILIAlJDQIgC0EHcQ0CIAtFDQIgCSALIApBA3RqIgYoAgBqIgogCUkNAiAKQQdxDQIgCkUNAiADQcQB
akIANwAAIANBzAFqQgA3AAAgA0HUAWpCADcAACADQdwBakIANwAAIANB5AFqQgA3AAAgA0EBOgDwAUEAIQsgA0EAOgDvASADQQA6AO4BIANBADoA7QEgA0EA
OgDsASADQgA3ALwBIANBADoAtgEgA0EAOgC3ASADIAIoAgBFIgI6ALgBIAEpAgAhBwJAQZgBRQ0AIAAgA0EIakGYAfwKAAALIABCADcDoAEgACAHNwOYASAA
QagBakIANwMAIABBsAFqQgA3AwAgAEG4AWpCADcDACAAQcABakEANgIAIABBADsA2wEgAEEAOgDaASAAQQE7AdgBIABCADcD0AEgACAKIAVBA3RqNgLMASAA
IAY2AsgBIAAgCDYCxAEgACADKQC5ATcA3QEgAEHlAWogA0HBAWopAAA3AAAgAEHtAWogA0HJAWopAAA3AAAgAEH1AWogA0HRAWopAAA3AAAgAEH9AWogA0HZ
AWopAAA3AAAgAEGFAmogA0HhAWopAAA3AAAgAEGMAmogA0HoAWooAAA2AAAgAEGwAmpBADYCACAAQagCakIANwMAIABBoAJqQgA3AwAgAEGYAmpCADcDACAA
QgA3A5ACIABBACkDqPnJgAA3ALUCIAAgAjoAtAIgAEEAOgC9AiAAQcYCakEANgEAIABCADcBvgIgAEEAOgDKAgwBC0EaEIyAgIAAAAsgACALOwHQAiADQbAD
aiSAgICAAA8LAAvvAgEKfyOAgICAAEEQayICJICAgIAAIAIgAC0ADEEDcSIDOgAPAkAgA0UNACAAQQRqIQQgAachBSABQiCIp0EMaiEGA0AgAiADQX9qIANx
QQNxOgAMAkAgBCADaEEDcUECdGoiBygCACIAQcABaigCACIDRQ0AIAApApgBIgGnIQggAUIgiKchCQJAIAMoAhAiCkUNACADKAIMIQsCQCAKRQ0AIAtBqgEg
CvwLAAsgCCALIApBAEEAIAkoAgwRgYCAgACAgICAAAsCQCADLQAIQQFxDQAgAygCBCIKRQ0AIAMoAgAhCwJAIApFDQAgC0GqASAK/AsACyAIIAsgCkEAQQAg
CSgCDBGBgICAAICAgIAACyAIIANBFEECQQAgCUEMaigCABGBgICAAICAgIAACyAAEISAgIAAIAUgBygCAEHQAkEDQQAgBigCABGBgICAAICAgIAAIAItAAwi
A0EDcQ0ACwsgAkEQaiSAgICAAAvyBgUCfwF+A38BfgF/AkAgAC0AfEEBcQ0AIABB9ABqKAIAIgFBgICAgARPDQAgAEHsAGohAgJAIAFFDQAgACkDECIDQiCI
pyEEIAAoAmwhBSADpyEGAkAgAUECdCIBRQ0AIAVBqgEgAfwLAAsgBiAFIAFBAkEAIAQoAgwRgYCAgACAgICAAAsgAkKq1arVqtWq1ap/NwIAIAJBCGoiBEGq
1arVejYCAAJAIAAoAngiAUUNACAAKQMQIQMCQAJAIAEtAAAiBUEJTw0AQQIhBgwBCwJAIAVBEU8NAEEEIQYMAQsgBUEhTw0CIAVBIEYNAkEIIQYLQQEgBXSt
IAatfiIHQiCIpw0BIAenIgZBBGoiBSAGSQ0BAkAgBUUNACABQaoBIAX8CwALIAOnIAEgBUECQQAgA0IgiKcoAgwRgYCAgACAgICAAAsgAkKq1arVqtWq1ap/
NwIAIAJBEGpBqtWq1Xo2AgAgBEKq1arVqtWq1ap/NwIAIABBLGohCCAAKAIsIQUCQCAAKAJUIgJFDQAgACgCMCEGA0ACQCACKAIUIgFBgIAkTQ0AIAIoAhAh
BAJAIAFFDQAgBEGqASAB/AsACyAFIAQgAUEQQQAgBigCDBGBgICAAICAgIAACyACKAIMIgINAAsgCCgCACEFCwJAIAAoAjQiAkUNACAAKAIwIQQDQCACQXxq
IQEgAigCACECIAUgASABKAIAQQJBACAEKAIMEYGAgIAAgICAgAAgAg0ACwsgCEKq1arVqtWq1ap/NwIAIAhBEGpBqtWq1Xo2AgAgCEEIakKq1arVqtWq1ap/
NwIAIABBGGohBgJAIAAoAiAiAkUNACAAKAIYIQUgACgCHCEEA0AgAkF8aiEBIAIoAgAhAiAFIAEgASgCAEECQQAgBCgCDBGBgICAAICAgIAAIAINAAsLIAZC
qtWq1arVqtWqfzcDACAGQRBqQarVqtV6NgIAIAZBCGpCqtWq1arVqtWqfzcDACAAQcAAaiEGAkAgACgCSCICRQ0AIAAoAkAhBSAAKAJEIQQDQCACQXxqIQEg
AigCACECIAUgASABKAIAQQJBACAEKAIMEYGAgIAAgICAgAAgAg0ACwsgBkKq1arVqtWq1ap/NwMAIAZBEGpBqtWq1Xo2AgAgBkEIakKq1arVqtWq1ap/NwMA
DwsAC7w5ARV/I4CAgIAAQcAAayIEJICAgIAAIAQgA0H//wNxIAJB//8DcSIFbCIDQf////8DSzoACAJAAkAgA0GAgICABEkNAEEZIQMMAQsgAUIgiKchBiAB
pyEHAkACQAJAAkAgA0ECdCIIDQBBfCEJDAELAkAgByAIQQJBACAGKAIAEYCAgIAAgICAgAAiCQ0AQRkhAwwECwJAIAhFDQAgCUGqASAI/AsACyAJQQNxDQEL
QQAhCgJAAkACQAJAAkACQEEADQAgA0UNBCAHIANBAEEAIAYoAgARgICAgACAgICAACIKRQ0BAkAgA0UiCw0AIApBqgEgA/wLAAtBAA0DIAcgA0EAQQAgBigC
ABGAgICAAICAgIAAIgxFDQMgCw0FIAxBqgEgA/wLAAwFCyADDQBBqtWq1XohCQwBCyADQYCAgIAETw0EIAghCgtBGSEDIApFDQUCQCAKRQ0AIAlBqgEgCvwL
AAsgByAJIApBAkEAIAYoAgwRgYCAgACAgICAAAwFCwJAIAsNACAKQaoBIAP8CwALIAcgCiADQQBBACAGKAIMEYGAgIAAgICAgAAgA0GAgICABE8NAiAJIQ0g
CCELDAMLQQAhC0Gq1arVeiENQX8hCkF/IQxBAA0CCyAEIANBAEg6ABQCQAJAAkACQAJAAkACQAJAAkACQAJAIANBf0wNACADQQF0IgtFDQEgByALQQFBACAG
KAIAEYCAgIAAgICAgAAiDUUNAAJAIAtFIg4NACANQaoBIAv8CwALIA1BAXENCyAEIANBAEg6ABggA0F/TA0DIAcgC0EBQQAgBigCABGAgICAAICAgIAAIg9F
DQMCQCAODQAgD0GqASAL/AsACyAPQQFxRQ0CDAsLAkACQCADDQBBACEIQarVqtV6IQkMAQsCQCADRSICDQAgDEGqASAD/AsACyAHIAwgA0EAQQAgBigCDBGB
gICAAICAgIAAAkAgAg0AIApBqgEgA/wLAAsgByAKIANBAEEAIAYoAgwRgYCAgACAgICAACADQYCAgIAETw0LC0EZIQMgCEUNDAJAIAhFDQAgCUGqASAI/AsA
CyAHIAkgCEECQQAgBigCDBGBgICAAICAgIAADAwLIAQgA0EASCIPOgAYQX4hDSAPDQFBfiENQX4hDwtBACEOQQANAyADDQFBfyEODAILAkACQCADDQBBACEL
QarVqtV6IQ0MAQsgA0F/TA0ICwJAIAtFDQACQCALRQ0AIA1BqgEgC/wLAAsgByANIAtBAUEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQhBqtWq1Xoh
CQwBCwJAIANFIgINACAMQaoBIAP8CwALIAcgDCADQQBBACAGKAIMEYGAgIAAgICAgAACQCACDQAgCkGqASAD/AsACyAHIAogA0EAQQAgBigCDBGBgICAAICA
gIAAIANBgICAgARPDQgLQRkhAyAIRQ0JAkAgCEUNACAJQaoBIAj8CwALIAcgCSAIQQJBACAGKAIMEYGAgIAAgICAgAAMCQsgByADQQBBACAGKAIAEYCAgIAA
gICAgAAiDkUNAiADRQ0AIA5BqgEgA/wLAAsgA0H/////A00NAwwECyADDQBBqtWq1XohDwwBCyADQX9MDQMgCyEOCwJAIA5FDQACQCAORQ0AIA9BqgEgDvwL
AAsgByAPIA5BAUEAIAYoAgwRgYCAgACAgICAAAsCQCALRQ0AIA1BqtWq1XogAxshAgJAIAtFDQAgAkGqASAL/AsACyAHIAIgC0EBQQAgBigCDBGBgICAAICA
gIAACwJAAkAgAw0AQQAhCEGq1arVeiEJDAELAkAgA0UiAg0AIAxBqgEgA/wLAAsgByAMIANBAEEAIAYoAgwRgYCAgACAgICAAAJAIAINACAKQaoBIAP8CwAL
IAcgCiADQQBBACAGKAIMEYGAgIAAgICAgAAgA0GAgICABE8NAwtBGSEDIAhFDQQCQCAIRQ0AIAlBqgEgCPwLAAsgByAJIAhBAkEAIAYoAgwRgYCAgACAgICA
AAwECwJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAhFDQAgByAIQQJBACAGKAIAEYCAgIAAgICAgAAiEEUNDQJAIAhFIhENACAQQaoBIAj8CwALIBBBA3EN
DiAEIANB/////wNLOgAkIANBgICAgARPDQwgByAIQQJBACAGKAIAEYCAgIAAgICAgAAiEkUNDAJAIBENACASQaoBIAj8CwALIBJBA3ENDiAEIANB/////wNL
OgAoIANBgICAgARPDQEgByAIQQJBACAGKAIAEYCAgIAAgICAgAAiE0UNAQJAIBENACATQaoBIAj8CwALIBNBA3ENDiAEIANB/////wNLOgAsIANBgICAgARP
DQsgByAIQQJBACAGKAIAEYCAgIAAgICAgAAiFEUNCwJAIBENACAUQaoBIAj8CwALIBRBA3ENDiAEIANB/////wNLIhU6ADAgFQ0EIAcgCEECQQAgBigCABGA
gICAAICAgIAAIhVFDQQCQCARDQAgFUGqASAI/AsACyAVQQNxDQ4gBCADQf////8DSzoANCADQYCAgIAETw0FIAcgCEECQQAgBigCABGAgICAAICAgIAAIhZF
DQUCQCARDQAgFkGqASAI/AsACyAWQQNxDQ4MAwsgBCADQf////8DSyIROgAkQXwhECARDQsgBCAROgAoQXwhEkF8IRAgEUUNAQsCQAJAIAMNAEEAIQJBqtWq
1XohEgwBCyADQYCAgIAETw0NIAghAgsCQCACRQ0AAkAgAkUNACASQaoBIAL8CwALIAcgEiACQQJBACAGKAIMEYGAgIAAgICAgAALAkAgCEUNACAQQarVqtV6
IAMbIQICQCAIRQ0AIAJBqgEgCPwLAAsgByACIAhBAkEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQJBqtWq1XohDwwBCwJAIANFDQAgDkGqASAD/AsA
CyAHIA4gA0EAQQAgBigCDBGBgICAAICAgIAAIAshAgsCQCACRQ0AAkAgAkUNACAPQaoBIAL8CwALIAcgDyACQQFBACAGKAIMEYGAgIAAgICAgAALAkAgC0UN
ACANQarVqtV6IAMbIQICQCALRQ0AIAJBqgEgC/wLAAsgByACIAtBAUEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQhBqtWq1XohCQwBCwJAIANFIgIN
ACAMQaoBIAP8CwALIAcgDCADQQBBACAGKAIMEYGAgIAAgICAgAACQCACDQAgCkGqASAD/AsACyAHIAogA0EAQQAgBigCDBGBgICAAICAgIAAC0EZIQMgCEUN
DgJAIAhFDQAgCUGqASAI/AsACyAHIAkgCEECQQAgBigCDBGBgICAAICAgIAADA4LIAQgA0H/////A0siEToALEF8IRNBfCESQXwhECARDQggBCAROgAwQXwh
FEF8IRNBfCESQXwhECARDQEgBCADQf////8DSyIROgA0QXwhFUF8IRRBfCETQXwhEkF8IRAgEQ0CQXwhEEF8IRJBfCETQXwhFEF8IRVBfCEWCyAEQQA6ADhB
AUUNAiACQf//A3FFDQQgByAFQQF0IhFBAUEAIAYoAgARgICAgACAgICAACICRQ0CAkAgEUUiFw0AIAJBqgEgEfwLAAsgAkEBcQ0KQQANAyAHIBFBAUEAIAYo
AgARgICAgACAgICAACIYRQ0DAkAgFw0AIBhBqgEgEfwLAAsgGEEBcQ0KDAULAkACQCADDQBBACECQarVqtV6IRQMAQsgA0GAgICABE8NCiAIIQILAkAgAkUN
AAJAIAJFDQAgFEGqASAC/AsACyAHIBQgAkECQQAgBigCDBGBgICAAICAgIAACwJAIAhFDQAgE0Gq1arVeiADGyEFAkAgCEUiAg0AIAVBqgEgCPwLAAsgByAF
IAhBAkEAIAYoAgwRgYCAgACAgICAACASQarVqtV6IAMbIQUCQCACDQAgBUGqASAI/AsACyAHIAUgCEECQQAgBigCDBGBgICAAICAgIAAIBBBqtWq1XogAxsh
BQJAIAINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGAgIAAgICAgAALAkACQCADDQBBACECQarVqtV6IQ8MAQsCQCADRQ0AIA5BqgEgA/wLAAsgByAO
IANBAEEAIAYoAgwRgYCAgACAgICAACALIQILAkAgAkUNAAJAIAJFDQAgD0GqASAC/AsACyAHIA8gAkEBQQAgBigCDBGBgICAAICAgIAACwJAIAtFDQAgDUGq
1arVeiADGyECAkAgC0UNACACQaoBIAv8CwALIAcgAiALQQFBACAGKAIMEYGAgIAAgICAgAALAkACQCADDQBBACEIQarVqtV6IQkMAQsCQCADRSICDQAgDEGq
ASAD/AsACyAHIAwgA0EAQQAgBigCDBGBgICAAICAgIAAAkAgAg0AIApBqgEgA/wLAAsgByAKIANBAEEAIAYoAgwRgYCAgACAgICAAAtBGSEDIAhFDQsCQCAI
RQ0AIAlBqgEgCPwLAAsgByAJIAhBAkEAIAYoAgwRgYCAgACAgICAAAwLCwJAAkAgAw0AQQAhAkGq1arVeiEVDAELIANBgICAgARPDQkgCCECCwJAIAJFDQAC
QCACRQ0AIBVBqgEgAvwLAAsgByAVIAJBAkEAIAYoAgwRgYCAgACAgICAAAsCQCAIRQ0AIBRBqtWq1XogAxshBQJAIAhFIgINACAFQaoBIAj8CwALIAcgBSAI
QQJBACAGKAIMEYGAgIAAgICAgAAgE0Gq1arVeiADGyEFAkAgAg0AIAVBqgEgCPwLAAsgByAFIAhBAkEAIAYoAgwRgYCAgACAgICAACASQarVqtV6IAMbIQUC
QCACDQAgBUGqASAI/AsACyAHIAUgCEECQQAgBigCDBGBgICAAICAgIAAIBBBqtWq1XogAxshBQJAIAINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGA
gIAAgICAgAALAkACQCADDQBBACECQarVqtV6IQ8MAQsCQCADRQ0AIA5BqgEgA/wLAAsgByAOIANBAEEAIAYoAgwRgYCAgACAgICAACALIQILAkAgAkUNAAJA
IAJFDQAgD0GqASAC/AsACyAHIA8gAkEBQQAgBigCDBGBgICAAICAgIAACwJAIAtFDQAgDUGq1arVeiADGyECAkAgC0UNACACQaoBIAv8CwALIAcgAiALQQFB
ACAGKAIMEYGAgIAAgICAgAALAkACQCADDQBBACEIQarVqtV6IQkMAQsCQCADRSICDQAgDEGqASAD/AsACyAHIAwgA0EAQQAgBigCDBGBgICAAICAgIAAAkAg
Ag0AIApBqgEgA/wLAAsgByAKIANBAEEAIAYoAgwRgYCAgACAgICAAAtBGSEDIAhFDQoCQCAIRQ0AIAlBqgEgCPwLAAsgByAJIAhBAkEAIAYoAgwRgYCAgACA
gICAAAwKCwJAAkAgAw0AQQAhAkGq1arVeiEWDAELIANBgICAgARPDQggCCECCwJAIAJFDQACQCACRQ0AIBZBqgEgAvwLAAsgByAWIAJBAkEAIAYoAgwRgYCA
gACAgICAAAsCQCAIRQ0AIBVBqtWq1XogAxshBQJAIAhFIgINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGAgIAAgICAgAAgFEGq1arVeiADGyEFAkAg
Ag0AIAVBqgEgCPwLAAsgByAFIAhBAkEAIAYoAgwRgYCAgACAgICAACATQarVqtV6IAMbIQUCQCACDQAgBUGqASAI/AsACyAHIAUgCEECQQAgBigCDBGBgICA
AICAgIAAIBJBqtWq1XogAxshBQJAIAINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGAgIAAgICAgAAgEEGq1arVeiADGyEFAkAgAg0AIAVBqgEgCPwL
AAsgByAFIAhBAkEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQJBqtWq1XohDwwBCwJAIANFDQAgDkGqASAD/AsACyAHIA4gA0EAQQAgBigCDBGBgICA
AICAgIAAIAshAgsCQCACRQ0AAkAgAkUNACAPQaoBIAL8CwALIAcgDyACQQFBACAGKAIMEYGAgIAAgICAgAALAkAgC0UNACANQarVqtV6IAMbIQICQCALRQ0A
IAJBqgEgC/wLAAsgByACIAtBAUEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQhBqtWq1XohCQwBCwJAIANFIgINACAMQaoBIAP8CwALIAcgDCADQQBB
ACAGKAIMEYGAgIAAgICAgAACQCACDQAgCkGqASAD/AsACyAHIAogA0EAQQAgBigCDBGBgICAAICAgIAAC0EZIQMgCEUNCQJAIAhFDQAgCUGqASAI/AsACyAH
IAkgCEECQQAgBigCDBGBgICAAICAgIAADAkLAkAgFw0AIAJBqgEgEfwLAAsgByACIBFBAUEAIAYoAgwRgYCAgACAgICAAAwCC0F+IQJBfiEYQQANAQsgACAF
NgJsIAAgGDYCaCAAIAU2AmQgACACNgJgIAAgAzYCXCAAIBY2AlggACADNgJUIAAgFTYCUCAAIAM2AkwgACAUNgJIIAAgAzYCRCAAIBM2AkAgACADNgI8IAAg
EjYCOCAAIAM2AjQgACAQNgIwIAAgAzYCLCAAIA42AiggACADNgIkIAAgDzYCICAAIAM2AhwgACANNgIYIAAgAzYCFCAAIAw2AhAgACADNgIMIAAgCjYCCCAA
IAM2AgQgACAJNgIAQQAhAwwGCwJAAkAgAw0AQQAhAkGq1arVeiEWDAELIANBgICAgARPDQQgCCECCwJAIAJFDQACQCACRQ0AIBZBqgEgAvwLAAsgByAWIAJB
AkEAIAYoAgwRgYCAgACAgICAAAsCQCAIRQ0AIBVBqtWq1XogAxshBQJAIAhFIgINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGAgIAAgICAgAAgFEGq
1arVeiADGyEFAkAgAg0AIAVBqgEgCPwLAAsgByAFIAhBAkEAIAYoAgwRgYCAgACAgICAACATQarVqtV6IAMbIQUCQCACDQAgBUGqASAI/AsACyAHIAUgCEEC
QQAgBigCDBGBgICAAICAgIAAIBJBqtWq1XogAxshBQJAIAINACAFQaoBIAj8CwALIAcgBSAIQQJBACAGKAIMEYGAgIAAgICAgAAgEEGq1arVeiADGyEFAkAg
Ag0AIAVBqgEgCPwLAAsgByAFIAhBAkEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQJBqtWq1XohDwwBCwJAIANFDQAgDkGqASAD/AsACyAHIA4gA0EA
QQAgBigCDBGBgICAAICAgIAAIAshAgsCQCACRQ0AAkAgAkUNACAPQaoBIAL8CwALIAcgDyACQQFBACAGKAIMEYGAgIAAgICAgAALAkAgC0UNACANQarVqtV6
IAMbIQICQCALRQ0AIAJBqgEgC/wLAAsgByACIAtBAUEAIAYoAgwRgYCAgACAgICAAAsCQAJAIAMNAEEAIQhBqtWq1XohCQwBCwJAIANFIgINACAMQaoBIAP8
CwALIAcgDCADQQBBACAGKAIMEYGAgIAAgICAgAACQCACDQAgCkGqASAD/AsACyAHIAogA0EAQQAgBigCDBGBgICAAICAgIAAC0EZIQMgCEUNBQJAIAhFDQAg
CUGqASAI/AsACyAHIAkgCEECQQAgBigCDBGBgICAAICAgIAADAULAkACQCADDQBBACECQarVqtV6IRMMAQsgA0GAgICABE8NAyAIIQILAkAgAkUNAAJAIAJF
DQAgE0GqASAC/AsACyAHIBMgAkECQQAgBigCDBGBgICAAICAgIAACwJAIAhFDQAgEkGq1arVeiADGyECAkAgCEUiBQ0AIAJBqgEgCPwLAAsgByACIAhBAkEA
IAYoAgwRgYCAgACAgICAACAQQarVqtV6IAMbIQICQCAFDQAgAkGqASAI/AsACyAHIAIgCEECQQAgBigCDBGBgICAAICAgIAACwJAAkAgAw0AQQAhAkGq1arV
eiEPDAELAkAgA0UNACAOQaoBIAP8CwALIAcgDiADQQBBACAGKAIMEYGAgIAAgICAgAAgCyECCwJAIAJFDQACQCACRQ0AIA9BqgEgAvwLAAsgByAPIAJBAUEA
IAYoAgwRgYCAgACAgICAAAsCQCALRQ0AIA1BqtWq1XogAxshAgJAIAtFDQAgAkGqASAL/AsACyAHIAIgC0EBQQAgBigCDBGBgICAAICAgIAACwJAAkAgAw0A
QQAhCEGq1arVeiEJDAELAkAgA0UiAg0AIAxBqgEgA/wLAAsgByAMIANBAEEAIAYoAgwRgYCAgACAgICAAAJAIAINACAKQaoBIAP8CwALIAcgCiADQQBBACAG
KAIMEYGAgIAAgICAgAALQRkhAyAIRQ0EAkAgCEUNACAJQaoBIAj8CwALIAcgCSAIQQJBACAGKAIMEYGAgIAAgICAgAAMBAsCQAJAIAMNAEEAIQJBqtWq1Xoh
EAwBCyADQYCAgIAETw0CIAghAgsCQCACRQ0AAkAgAkUNACAQQaoBIAL8CwALIAcgECACQQJBACAGKAIMEYGAgIAAgICAgAALAkACQCADDQBBACECQarVqtV6
IQ8MAQsCQCADRQ0AIA5BqgEgA/wLAAsgByAOIANBAEEAIAYoAgwRgYCAgACAgICAACALIQILAkAgAkUNAAJAIAJFDQAgD0GqASAC/AsACyAHIA8gAkEBQQAg
BigCDBGBgICAAICAgIAACwJAIAtFDQAgDUGq1arVeiADGyECAkAgC0UNACACQaoBIAv8CwALIAcgAiALQQFBACAGKAIMEYGAgIAAgICAgAALAkACQCADDQBB
ACEIQarVqtV6IQkMAQsCQCADRSICDQAgDEGqASAD/AsACyAHIAwgA0EAQQAgBigCDBGBgICAAICAgIAAAkAgAg0AIApBqgEgA/wLAAsgByAKIANBAEEAIAYo
AgwRgYCAgACAgICAAAtBGSEDIAhFDQMCQCAIRQ0AIAlBqgEgCPwLAAsgByAJIAhBAkEAIAYoAgwRgYCAgACAgICAAAwDCwJAAkAgAw0AQQAhAkGq1arVeiEP
DAELAkAgA0UNACAOQaoBIAP8CwALIAcgDiADQQBBACAGKAIMEYGAgIAAgICAgAAgA0F/TA0BIAshAgsCQCACRQ0AAkAgAkUNACAPQaoBIAL8CwALIAcgDyAC
QQFBACAGKAIMEYGAgIAAgICAgAALAkAgC0UNACANQarVqtV6IAMbIQICQCALRQ0AIAJBqgEgC/wLAAsgByACIAtBAUEAIAYoAgwRgYCAgACAgICAAAsCQAJA
IAMNAEEAIQhBqtWq1XohCQwBCwJAIANFIgINACAMQaoBIAP8CwALIAcgDCADQQBBACAGKAIMEYGAgIAAgICAgAACQCACDQAgCkGqASAD/AsACyAHIAogA0EA
QQAgBigCDBGBgICAAICAgIAAIANBgICAgARPDQELQRkhAyAIRQ0CAkAgCEUNACAJQaoBIAj8CwALIAcgCSAIQQJBACAGKAIMEYGAgIAAgICAgAAMAgsAC0EZ
IQMgC0UNAAJAIAtFDQAgDUGqASAL/AsACyAHIA0gC0ECQQAgBigCDBGBgICAAICAgIAACyAAIAM7AXAgBEHAAGokgICAgAALngoCAn8CfiOAgICAAEGgJGsi
AySAgICAAEEAIQQCQAJAIABFDQAgAUUNAEEAIQQgA0EWakEAOgAAIANBJmpB/wE6AAAgA0E2akH/AToAACADQRtqQQAtAOfnyYAAOgAAIANBK2pBAC0A9+fJ
gAA6AAAgA0EAOwEUIANB//8DOwEkIANB//8DOwE0IAMgATsB3hAgAyAAOwHcECADQQE6ABggA0IANwIMIAMgAjYCCCADQQE6ACggA0IANwIcIANCADcCLCAD
QQE6ADggA0EALwDl58mAADsAGSADQQAvAPXnyYAAOwApIANBO2pBAC0Ah+jJgAA6AAAgA0EALwCF6MmAADsAOQJAQaAQRQ0AIANBPGpBiOjJgABBoBD8CgAA
CyADQgA8AAQgA0KExICQAz4CACADQeAQakHg+smAACADEIGAgIAAIAMvAegiDQBBACEEIANB8CJqQQApA+D6yYAAIgUgASAAEIWAgIAAIAMvAeAjDQAgA0GI
L0EDIAMQh4CAgAAiAkUNACACQarVqtV6NgCELyACQQdxDQEgAiAFNwKwGwJAQfgARQ0AIAIgA0HgEGpB+AD8CgAACyACIAE2AnwgAiAANgJ4AkBBiBFFDQAg
AkGAAWogA0HgEWpBiBH8CgAACwJAQaQRRQ0AIAJBgBxqQaoBQaQR/AsACwJAQagJRQ0AIAJBiBJqQbDeyYAAQagJ/AoAAAsCQEHwAEUNACACQawtaiADQfAi
akHwAPwKAAALIAIgADsBgi8gAiABOwGALyACQQApApCAwIAAIgY3ApwuIAJBpC5qQQAoApiAwIAAIgA2AgAgAiAGNwKoLiACQbAuaiAANgIAIAIgBjcCtC4g
AkG8LmogADYCACACQQApAtj5yYAAIgY3AsAuIAJByC5qQQAoAuD5yYAAIgA2AgAgAkHULmpBACgCpIDAgAA2AgAgAkEAKQKcgMCAADcCzC4gAkIANwLwLiAC
QfguakIANwIAIAJB4C5qIAA2AgAgAiAGNwLYLiACIAY3AuQuIAJB7C5qIAA2AgAgA0HyI2pCqtWq1arVqtWqfzcAACADQfojakKq1arVqtWq1ap/NwAAIANB
giRqQqrVqtWq1arVqn83AAAgA0GKJGpCqtWq1arVqtWqfzcAACADQZIkakKq1arVqtWq1ap/NwAAIANCqtWq1arVqtWqfzcA6iMgAkGGHGpBADoAACACQQA7
AYQcIAJCADcCyBsgAiAFNwK4GyACQdAbakIANwIAIAJB2BtqQgA3AgAgAkHgG2pCADcCACACQegbakIANwIAIAJB8BtqQgA3AgAgAkH4G2pBADYCACACQQE6
AJAcIAIgBTcCiBwgAkEAOgCAHCACIAJB2C5qNgL8GyACIAI2AsQbIAIgAjYCwBsCQEHTAEUNACACQZEcakEAQdMA/AsACyACQQA6AOQcAkBBgBBFDQAgAkHl
HGpBqgFBgBD8CwALIAJBACkDsN7BgAA3AqQtIAJBADoAoS0gAkEAOwCfLSACQarVqtV6NgCbLSACQQA6AJotIAJBADsBmC0gAkGULWogA0GWJGooAAA2AAAg
AkGNLWogA0GPJGopAAA3AAAgAkGFLWogA0GHJGopAAA3AAAgAkH9LGogA0H/I2opAAA3AAAgAkH1LGogA0H3I2opAAA3AAAgAkHtLGogA0HvI2opAAA3AAAg
AiADKQDnIzcA5SwgAiEECyADQaAkaiSAgICAACAEDwsAC7UEAQN/I4CAgIAAQRBrIgQkgICAgAAgBCACQR9xOgAPAkACQEF/IAFBBGoiBSAFIAFJGyIBQQEg
AnQiAiABIAJLGyIBQX9qZyICRQ0AAkBBICACayIGQf//A3EiBUF9aiICIAVLDQACQAJAIAJBDUkNACABQYOABGoiAiABTw0BDAILQgEgBq1C//8Dg4anIQEC
QCACQQJ0IgZB/P7JgABqIgUoAgAiAkUNACABIAJqQXxqIgEgAkkNAiABRQ0CIAFBA3ENAiAFIAEoAgA2AgAMBAsCQCAGQbD/yYAAaiIFKAIAIgJB//8DcQ0A
QQAhAgJAAkBBACgC5P/JgAAiBkUNACAGQfz/A2oiAiAGSQ0EIAJBA3ENBEEAIAIoAgA2AuT/yYAADAELQQFAACIGQX9GDQUgBkF/TA0DIAZBgIAETw0DIAZF
DQUgBkEQdCEGCyAGIAFqIgIgBkkNAiAFIAI2AgAgBiECDAQLIAIgAWoiASACSQ0BIAUgATYCAAwDC0IBQSAgAkEQdkF/amdrIgGtQv//A4OGpyIFQYCABE8N
AAJAIAJB//+DgARLDQACQCABQQJ0QeT/yYAAaiIGKAIAIgJFDQAgBUEQdCACakF8aiIBIAJJDQIgAUUNAiABQQNxDQIgBiABKAIANgIADAQLIAVAACICQX9G
DQIgAkF/TA0BIAJBgIAETw0BIAJBEHQhAgwDCyABQQ8QiICAgAAACwALQQAhAgsgBEEQaiSAgICAACACCw0AIAAgARCJgICAAAAL7wUCB38BfiOAgICAAEGw
IGsiAiSAgICAAAJAQY8gRQ0AIAJBAWpBqgFBjyD8CwALIAJCgCA3ApggIAJBuN7BgAA2ApAgIAIgAkEBajYClCBBACEDAkACQAJAAkADQCACKAKcICIEQRsg
A2siBWoiBiAESQ0CIANByN7BgABqIQcCQAJAIAYgAigCmCAiCEsNACAGIAhLDQUCQCACKAKUICAEaiIGQePewYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgAigCnCAiBiAFaiIEIAZJDQQgAiAENgKcIAwBCyACKAKQICgCACEGIAIgBTYCpCAgAiAHNgKgICACQaggaiACQZAgaiACQaAgakEBQQEgBhGB
gICAAICAgIAAIAIpA6ggIglCgICAgPD/P4NCAFINAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEbSQ0AC0EAIQMgAkGQIGogAEEKQQBBsNjJgAAQlYCAgABB
//8DcQ0AA0AgAigCnCAiBEEGIANrIgVqIgYgBEkNAiADQePewYAAaiEHAkACQCAGIAIoApggIghLDQAgBiAISw0GAkAgAigClCAgBGoiBkHp3sGAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAIoApwgIgYgBWoiBCAGSQ0EIAIgBDYCnCAMAQsgAigCkCAoAgAhBiACIAU2AqQgIAIgBzYCoCAgAkGoIGog
AkGQIGogAkGgIGpBAUEBIAYRgYCAgACAgICAACACKQOoICIJQoCAgIDw/z+DQgBSDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBkkNAAsgAkGQIGogAUEK
QQBBsNjJgAAQlYCAgABB//8DcQ0AIAIoApwgIgMgAigCmCAiAk0NASADIAIQiICAgAAACyACQYEgakGV08CAAE8NACACQZAgakGG08CAAE0aCwALIAYgCBCI
gICAAAALIAYgCBCIgICAAAALDQAgACABEIuAgIAAAAvvBQIHfwF+I4CAgIAAQbAgayICJICAgIAAAkBBjyBFDQAgAkEBakGqAUGPIPwLAAsgAkKAIDcCmCAg
AkG43sGAADYCkCAgAiACQQFqNgKUIEEAIQMCQAJAAkACQANAIAIoApwgIgRBDCADayIFaiIGIARJDQIgA0Hp3sGAAGohBwJAAkAgBiACKAKYICIISw0AIAYg
CEsNBQJAIAIoApQgIARqIgZB9d7BgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyACKAKcICIGIAVqIgQgBkkNBCACIAQ2ApwgDAELIAIoApAg
KAIAIQYgAiAFNgKkICACIAc2AqAgIAJBqCBqIAJBkCBqIAJBoCBqQQFBASAGEYGAgIAAgICAgAAgAikDqCAiCUKAgICA8P8/g0IAUg0CIAmnIQULIAMgBWoi
BSADSQ0CIAUhAyAFQQxJDQALQQAhAyACQZAgaiAAQQpBAEGw2MmAABCVgICAAEH//wNxDQADQCACKAKcICIEQRogA2siBWoiBiAESQ0CIANB9d7BgABqIQcC
QAJAIAYgAigCmCAiCEsNACAGIAhLDQYCQCACKAKUICAEaiIGQY/fwYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgAigCnCAiBiAFaiIEIAZJ
DQQgAiAENgKcIAwBCyACKAKQICgCACEGIAIgBTYCpCAgAiAHNgKgICACQaggaiACQZAgaiACQaAgakEBQQEgBhGBgICAAICAgIAAIAIpA6ggIglCgICAgPD/
P4NCAFINAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEaSQ0ACyACQZAgaiABQQpBAEGw2MmAABCVgICAAEH//wNxDQAgAigCnCAiAyACKAKYICICTQ0BIAMg
AhCIgICAAAALIAJBgSBqQZXTwIAATw0AIAJBkCBqQYbTwIAATRoLAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAsrACAAQf//A3FBA3QiAEHo+smAAGooAgAg
AEHs+smAAGooAgAQjYCAgAAAC8UDAgd/AX4jgICAgABBsCBrIgIkgICAgAACQEGPIEUNACACQQFqQaoBQY8g/AsACyACQoAgNwKYICACQbjewYAANgKQICAC
IAJBAWo2ApQgQQAhAwJAAkACQANAIAIoApwgIgRBGSADayIFaiIGIARJDQIgA0GP38GAAGohBwJAAkAgBiACKAKYICIISw0AIAYgCEsNBQJAIAIoApQgIARq
IgZBqN/BgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyACKAKcICIGIAVqIgQgBkkNBCACIAQ2ApwgDAELIAIoApAgKAIAIQYgAiAFNgKkICAC
IAc2AqAgIAJBqCBqIAJBkCBqIAJBoCBqQQFBASAGEYGAgIAAgICAgAAgAikDqCAiCUKAgICA8P8/g0IAUg0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQRlJ
DQALIAJBkCBqIAAgASABQQAtAMDYyYAAQSAQloCAgABB//8DcQ0AIAIoApwgIgMgAigCmCAiAk0NASADIAIQiICAgAAACyACQYEgakGV08CAAE8NACACQZAg
akGG08CAAE0aCwALIAYgCBCIgICAAAALjwQDAX4IfwF+QgAhBQJAAkACQCADRQ0AAkACQAJAIANBf2oiBg0AIAEoAgwhAwwBCyABKAIMIQMgAiEHIAYhCAJA
A0AgASgCCCIJIANJDQECQCABKAIEIANqIgogBygCACILIAdBBGooAgAiDCAJIANrIgkgDCAJSRsiA2pPDQAgCyAKIANqSQ0HCwJAIANFDQAgCiALIAP8CgAA
CyABKAIMIgogA2oiAyAKSQ0GIAEgAzYCDCAMIAlLDQMgB0EIaiEHIAhBf2oiCEUNAgwACwsgAyAJEIqAgIAAAAsCQAJAAkAgASgCCCIHIANJDQAgASgCBCAD
aiENIAIgBkEDdGoiDCgCACELIAcgA2shCiAMKAIEIgcOAgQBAgsgAyAHEIqAgIAAAAsgBCAKSQ0EAkAgCkUNACANIAstAAAgCvwLAAsgASgCDCIDIApqIgcg
A0kNBCABIAc2AgwMAQsgBEUNA0EAIQkgB60hDgNAIAmtIA5+IgVCIIinDQQgCiAFpyIDSQ0DAkAgDSADaiIIIAsgByAKIANrIgwgByAMSRsiA2pPDQAgCyAI
IANqSQ0FCwJAIANFDQAgCCALIAP8CgAACyABKAIMIgggA2oiAyAISQ0EIAEgAzYCDCAHIAxLDQEgBCAJQQFqIglGDQQMAAsLQoCAgIAgIQULIAAgBTcCAA8L
IAMgChCKgICAAAsAC5oJAQl/I4CAgIAAQRBrIgQkgICAgAAgBCACQR9xOgAPAkACQAJAAkAgAEEDcQ0AQQEgBC0AD3QhBQJAAkAgACgCCCICRQ0AIAJBfGoh
BgwBCyABIAVqIgYgAUkNASAGQRhqIgIgBkkNASACIAJBAXZqIgcgAkkNAUEAIQIgACgCACAHQQJBACAAKAIEKAIAEYCAgIAAgICAgAAiBkUNAyAGQQNxDQEg
BkEANgIEIAYgBzYCACAGIAAoAgg2AgQgAEEANgIMIAAgBkEEajYCCAsgBigCACIIQQhJDQMgBkEIaiEHQQAgBWshCSAFQX9qIQoCQCABIAVqIgIgAU8NACAH
IAAoAgwiC2oiAiAHSQ0BIAIgCmoiBSACSQ0BIAUgCXEiBSACayICIAVLDQEgCyACaiIFIAtJDQEgBSABaiICIAVJDQEgAiAIQXhqTQ0CIAJBCGoiCyACSQ0B
IAAoAgAgBiAIQQIgC0EAIAAoAgQoAgQRgoCAgACAgICAAEEBcUUNAQNAIAYgCzYCACAHIAAoAgwiCGoiAiAHSQ0CIAIgCmoiBSACSQ0CIAUgCXEiBSACayIC
IAVLDQIgCCACaiIFIAhJDQIgBSABaiICIAVJDQIgAiALQXhqTQ0DIAJBCGoiBSACSQ0CIAAoAgAgBiALQQIgBUEAIAAoAgQoAgQRgoCAgACAgICAACECIAUh
CyACQQFxDQAMAgsLAkACQCACQRhqIgwgAkkNAAwBCyAHIAAoAgwiC2oiAiAHSQ0BIAIgCmoiBSACSQ0BIAUgCXEiBSACayICIAVLDQEgCyACaiIFIAtJDQEg
BSABaiICIAVJDQEgAiAIQXhqTQ0CIAJBCGoiCyACSQ0BIAAoAgAgBiAIQQIgC0EAIAAoAgQoAgQRgoCAgACAgICAAEEBcUUNAQNAIAYgCzYCACAHIAAoAgwi
CGoiAiAHSQ0CIAIgCmoiBSACSQ0CIAUgCXEiBSACayICIAVLDQIgCCACaiIFIAhJDQIgBSABaiICIAVJDQIgAiALQXhqTQ0DIAJBCGoiBSACSQ0CIAAoAgAg
BiALQQIgBUEAIAAoAgQoAgQRgoCAgACAgICAACECIAUhCyACQQFxDQAMAgsLA0AgByAAKAIMIgtqIgIgB0kNASACIApqIgUgAkkNASAFIAlxIgUgAmsiAiAF
Sw0BIAsgAmoiBSALSQ0BIAUgAWoiAiAFSQ0BIAIgCEF4aiILTQ0CIAJBCGoiBSACSQ0BAkACQCAAKAIAIAYgCEECIAVBACAAKAIEKAIEEYKAgIAAgICAgABB
AXFFDQAgBiAFNgIAIAUhCAwBCyALIAxqIgIgC0kNAiACIAJBAXZqIgUgAkkNAkEAIQIgACgCACAFQQJBACAAKAIEKAIAEYCAgIAAgICAgAAiBkUNBCAGQQNx
DQIgBkEANgIEIAYgBTYCACAGIAAoAgg2AgQgAEEANgIMIAAgBkEEajYCCCAGKAIAIQgLIAZBCGohByAIQQdNDQQMAAsLAAsgACACNgIMIAcgBWohAgsgBEEQ
aiSAgICAACACDwtBCCAIEIqAgIAAAAurHQkGfwF+C38BfBp/AX4bfwN+AX8jgICAgABB8ABrIgUkgICAgAAgBUHQAGpBqN/BgAAgA0EBEJKAgIAAAkACQCAF
LwFgRQ0AQdcBIQZBgAEhB0HwACEIQYDAACEJQYAQIQoMAQsgBSkDUCILQiCIpyEKIAunIQkgBS8BXCEHIAUvAV4hCCAFLwFaIQYgBS8BWCEDCwJAAkAgA0H/
/wNxIgwgBkH//wNxIg1sIg5BgICAgAJPDQAgDUEDdCIPIA5BA3RqIhAgD0kNAAJAAkAgBw0AQQAhEUEAIRJBACETQQAhFEEAIRUMAQtCAUEgIAdBf2pnIhZr
rUL//wODhqciErhEAAAAAAAA6j+iIhdEAAAAAAAA8L9lDQEgF0QAAAAAAADwQWYNASAX/AMiEa1CJH4iC0IgiKcNASASQQF0QQNqQfz///8HcSITIAunaiIU
IBNJDQFBf0EAIBZrdEF/cyEVCyAQIBRqIhggEEkNACAJQQ9qIhYgCUkNACAWQYCAgIABTw0AIBZBBHZBP2pBBnYiGUEDdCIaIBZBBHRBgH5xaiIWIBpJDQAg
GEEHaiIbIBhJDQAgG0F4cSIcIBZqIh0gHEkNAAJAAkAgCQ0AQQAhHgwBC0IBQSAgCUF/akEEdmdrrUL//wODhqchHgsgHSAeQRNqQXxxIh8gHkECdGoiICAe
QQN0amoiGCAdSQ0AIApBH2oiFiAKSQ0AIBZBgICAwABPDQAgFkEFdkE/akEGdiIhQQN0IiIgFkEFdEGAeHFqIhYgIkkNACAYQQdqIhsgGEkNACAbQXhxIiMg
FmoiJCAjSQ0AIAhB//8DcSIYQRxuIRtBACElICQhFkEAISZBACEnQQAhKEEAISlBACEqAkAgGEEcSQ0AQgFBICAbQX9qZyIYa61C//8Dg4anIii4RAAAAAAA
AOo/oiIXRAAAAAAAAPC/ZQ0BIBdEAAAAAAAA8EFmDQEgF/wDIimtQhx+IgtCIIinDQEgKEEBdEEDakH8////B3EiJyALp2oiJiAnSQ0BICQgJmoiFiAkSQ0B
QX9BACAYa3RBf3MhJUIBQSAgG0EEdEF/amdrrYanISoLIBZBA2oiGCAWSQ0AIBhBfHEiKyAqQQF0ICpBE2pBfHEiGCAqQQJ0aiIbakEDakF8cWoiFiArSQ0A
IBZB//8DaiIsIBZJDQAgBEH//wNxRQ0AICBBcGohLSAfQXBqIS4gG0FwaiEvIBhBcGohMCABQRxqITEgCq1CIIYgCa2EITIgASgCICEzIAEoAhwhNCAMQQN0
IR8gKEEBdCE1IBJBAXQhNiABQQhqITcgJCAnaiI4ICRJITkgHCAaaiI6IBxJITsgIyAiaiI8ICNJIT0gECATaiI+IBBJIT8gCEEQdCAHciFAQQAgKUEcbCJB
QWRqIglBHG4iFkEBakEDcWshQkEAIBFBJGwiQ0FcaiJEQSRuIgpBAWpBA3FrIUUgLEGAgHxxIkZBgYAkSSFHIBZBA3FBA0YhSCAJQdQASSFJIApBA3FBA0Yh
SkEAIUtBACEsQQAhIANAAkACQCABKAIYIgdFDQAgASAHKAIANgIYDAELAkBBAA0AIDdBsAJBAyAJEI+AgIAAIgdFDQACQEGwAkUNACAHQaoBQbAC/AsACyAH
QQdxRQ0BDAMLAkAgLEUNAANAAkAgLCgCFCIJQYCAJE0NACAsKAIQIRYCQCAJRQ0AIBZBqgEgCfwLAAsgNCAWIAlBEEEAIDMoAgwRgYCAgACAgICAAAsgLCgC
DCIsDQALCyAAQRk7AQwMAwsCQEGwAkUNACAHQaoBQbAC/AsACwJAAkACQCBHDQBBAA0BIDQgRkEQQQAgMygCABGAgICAAICAgIAAIhhFDQECQCBGRQ0AIBhB
qgEgRvwLAAsgRiFMIBhB//8DcQ0EDAILAkACQAJAIAEoAiwiGEUNACABIBgoAgA2AiwMAQtBAA0BIDFBgIAkQRAgCRCPgICAACIYRQ0BAkBBgIAkRQ0AIBhB
qgFBgIAk/AsACyAYQf//A3ENBQtBgIAkIUxBgIAkRQ0CIBhBqgFBgIAk/AsADAILAkBBsAJFDQAgB0GqAUGwAvwLAAsgByABKAIYNgIAIAEgBzYCGAJAICxF
DQADQAJAICwoAhQiCUGAgCRNDQAgLCgCECEWAkAgCUUNACAWQaoBIAn8CwALIDQgFiAJQRBBACAzKAIMEYGAgIAAgICAgAALICwoAgwiLA0ACwsgAEEZOwEM
DAQLAkBBsAJFDQAgB0GqAUGwAvwLAAsgByABKAIYNgIAIAEgBzYCGAJAICxFDQADQAJAICwoAhQiCUGAgCRNDQAgLCgCECEWAkAgCUUNACAWQaoBIAn8CwAL
IDQgFiAJQRBBACAzKAIMEYGAgIAAgICAgAALICwoAgwiLA0ACwsgAEEZOwEMDAMLAkAgTEUNACAYQQAgTPwLAAsgGCAPaiIJIBhJDQEgCUEHcQ0BIAlFDQEg
GEEHcQ0BAkACQAJAAkACQAJAIAZB//8DcSJNRQ0AQQAhFiAYIQogDSEIA0AgFiAOTw0CIAkgGGsiGyAJSw0IIAogG603AwAgCSAfaiEJIBYgDGohFiAKQQhq
IQogCEF/aiIIDQALCwJAIEZFDQAgGEH4/wNxDQcLID8NBiAYIBBqIgkgGEkNBiAJRQ0GAkAgNkUNACAJQQAgNvwLAAsgGCA+aiIKIBhJDQYgCkUNBgJAIBFF
DQAgCiEJAkAgSg0AIEUhFiAKIQkDQCAJQSBqQQAoAry5yIAANgIAIAlBGGpBACkCtLnIgAA3AgAgCUEQakEAKQKsuciAADcCACAJQQhqQQApAqS5yIAANwIA
IAlBACkCnLnIgAA3AgAgCUEkaiEJIBZBAWoiFg0ACwsgREHsAEkNACAKIENqIQoDQCAJQSBqQQAoAry5yIAAIhY2AgAgCUEYakEAKQK0uciAACILNwIAIAlB
EGpBACkCrLnIgAAiTjcCACAJQQhqQQApAqS5yIAAIk83AgAgCUEAKQKcuciAACJQNwIAIAlBJGogUDcCACAJQSxqIE83AgAgCUE0aiBONwIAIAlBPGogCzcC
ACAJQcQAaiAWNgIAIAlByABqIFA3AgAgCUHQAGogTzcCACAJQdgAaiBONwIAIAlB4ABqIAs3AgAgCUHoAGogFjYCACAJQYwBaiAWNgIAIAlBhAFqIAs3AgAg
CUH8AGogTjcCACAJQfQAaiBPNwIAIAlB7ABqIFA3AgAgCUGQAWoiCSAKRw0ACwsCQEHCAEUiCA0AIAVBDGpBAEHCAPwLAAsgGCAjaiIJIBhJDQYgCUUNBgJA
ICJFDQAgCUH/ASAi/AsACyA9DQYgGCAcaiIJIBhJDQYgCUUNBgJAIBpFDQAgCUH/ASAa/AsACyA7DQYgGCAdaiIJQRBqIhZBA3ENBiAJQQA2AgwgCSAeNgII
IAkgLjYCBCAJIC02AgACQCAeRQ0AIBZBACAe/AsACyAWIBhrIhsgFksNBiAYICtqIglBADYCDCAJICo2AgggCSAwNgIEIAkgLzYCACAJQRBqIQkCQCAqRQ0A
IAlBACAq/AsACyAJIBhrIlEgCUsNBiA5DQYgGCAkaiIJIBhJDQYgCUUNBgJAIDVFDQAgCUEAIDX8CwALIBggOGoiCiAYSQ0GIApFDQYCQCApRQ0AIAohCQJA
IEgNACBCIRYgCiEJA0AgCUEYakEAKALYuciAADYCACAJQRBqQQApAtC5yIAANwIAIAlBCGpBACkCyLnIgAA3AgAgCUEAKQLAuciAADcCACAJQRxqIQkgFkEB
aiIWDQALCyBJDQAgCiBBaiEKA0AgCUEYakEAKALYuciAACIWNgIAIAlBEGpBACkC0LnIgAAiCzcCACAJQQhqQQApAsi5yIAAIk43AgAgCUEAKQLAuciAACJP
NwIAIAlBHGogTzcCACAJQSRqIE43AgAgCUEsaiALNwIAIAlBNGogFjYCACAJQThqIE83AgAgCUHAAGogTjcCACAJQcgAaiALNwIAIAlB0ABqIBY2AgAgCUHU
AGogTzcCACAJQdwAaiBONwIAIAlB5ABqIAs3AgAgCUHsAGogFjYCACAJQfAAaiIJIApHDQALCyAHIBU7AVwgByAUNgJYIAcgEzYCVCAHQQA2AlAgByASNgJM
IAcgETYCSCAHQQA2AkQgByA+NgJAIAcgEDYCPCAHIBs2AjggByA6NgI0IAcgGTYCMCAHIBw2AiwgByA8NgIoIAcgITYCJCAHICM2AiAgByAPNgIcIAdBADYC
GCAHIEY2AhQgByAYNgIQIAVBADoACAJAIAgNACAHQeAAaiAFQQxqQcIA/AoAAAsgByAlOwHIASAHICY2AsQBIAcgJzYCwAEgB0EANgK8ASAHICg2ArgBIAcg
KTYCtAEgB0EANgKwASAHIDg2AqwBIAcgJDYCqAEgByBRNgKkASAHQQE7AaIBAkBBygBFDQAgB0HMAWpBAEHKAPwLAAsgByAGOwGqAiAHIAM7AagCIAcgQDYC
pAIgByAGOwGiAiAHIAM7AaACIAcgMjcCmAIgB0EBOwGWAiAHIAUtAAg6AKwCIAcgAikDADcDACAHIARB//8DcSIJIE0gCSBNSRsiFjsBqgIgB0IANwIIIAkg
FmsiBEH//wNxIgogBEcNBiAgRQ0CIAcgIDYCCCAHIQkgICgCDCIWDQEMAwsgFiAOEIiAgIAAAAsgByAWNgIMIBYgBzYCCCAgIQkMAQsCQAJAICxFDQAgByAs
NgIMIAchFiAsKAIIIglFDQEgByAJNgIIIAkgBzYCDCAsIRYMAQsgB0EANgIMIAchCSAHISwMAgsgLCAHNgIIQQAhCSAWISwMAQsgICAHNgIMCyBLIExqIhYg
S0kNASACKQMAQgF8IgtCAFENASACIAs3AwAgFiFLIAkhICAKDQALICxFDQAgACAWNgIIIAAgCTYCBCAAICw2AgAgAEEAOwEMDAELAAsgBUHwAGokgICAgAAL
kwMBBX8gAEEcaiEBAkAgACgCJCICRQ0AIAAoAhwhAyAAKAIgIQQDQCACQXxqIQUgAigCACECIAMgBSAFKAIAQQJBACAEKAIMEYGAgIAAgICAgAAgAg0ACwsg
AUKq1arVqtWq1ap/NwIAIAFBEGpBqtWq1Xo2AgAgAUEIakKq1arVqtWq1ap/NwIAIABBCGohAQJAIAAoAhAiAkUNACAAKAIIIQMgACgCDCEEA0AgAkF8aiEF
IAIoAgAhAiADIAUgBSgCAEECQQAgBCgCDBGBgICAAICAgIAAIAINAAsLIAFCqtWq1arVqtWqfzcCACABQRBqQarVqtV6NgIAIAFBCGpCqtWq1arVqtWqfzcC
ACAAQTBqIQECQCAAKAI4IgJFDQAgACgCMCEDIAAoAjQhBANAIAJBfGohBSACKAIAIQIgAyAFIAUoAgBBAkEAIAQoAgwRgYCAgACAgICAACACDQALCyABQqrV
qtWq1arVqn83AgAgAUEQakGq1arVejYCACABQQhqQqrVqtWq1arVqn83AgAL0QcGAX8BfgV/AXwBfgJ/IAEoAgwhBCABKQIAIQUgAS8BCiEGIAEvAQghBwJA
AkAgA0H/AXENACAHIQIMAQsCQCAGIAdsIgNBgICAgAJPDQAgBkEDdCIGIANBA3RqIgMgBkkNACABLwEOIQggASgCBCEJIAEoAgAhBgJAAkAgAS8BDCIBDQBB
ACEKDAELQgFBICABQX9qZ2utQv//A4OGpyIBuEQAAAAAAADqP6IiC0QAAAAAAADwv2UNASALRAAAAAAAAPBBZg0BIAv8A61CJH4iDEIgiKcNASABQQF0QQNq
Qfz///8HcSIBIAynaiIKIAFJDQELIAMgCmoiByADSQ0AIAZBD2oiASAGSQ0AIAFBgICAgAFPDQAgAUEEdkE/akEDdkH4//8BcSINIAFBBHRBgH5xaiIDIA1J
DQAgB0EHaiIBIAdJDQAgAUF4cSIHIANqIgEgB0kNAAJAAkAgBg0AQQAhBgwBC0IBQSAgBkF/akEEdmdrrUL//wODhqchBgsgASAGQRNqQXxxIAZBDGxqIg1q
IgYgAUkNACAJQR9qIgEgCUkNACABQYCAgMAATw0AIAFBBXZBP2pBA3ZB+P8/cSIJIAFBBXRBgHhxaiIHIAlJDQAgBkEHaiIBIAZJDQAgAUF4cSIGIAdqIgEg
BkkNACAIQf//A3EiBkEcbiEIAkACQCAGQRxPDQBBACEJQQAhBgwBC0IBQSAgCEF/amdrrUL//wODhqciBrhEAAAAAAAA6j+iIgtEAAAAAAAA8L9lDQEgC0QA
AAAAAADwQWYNASAL/AOtQhx+IgxCIIinDQEgBkEBdEEDakH8////B3EiBiAMp2oiCSAGSQ0BIAEgCWoiDiABSQ0BQgFBICAIQQR0QX9qZ2uthqchBiAOIQEL
IAFBA2oiCCABSQ0AIAhBfHEiCCAGQQZsIAZBE2pBfHFqQQNqQXxxIgZqIgEgCEkNACABQf//A2oiCCABSQ0AIAhBgIB8cSIIIAZrIgEgCEsNACABIAlrIgYg
AUsNACAGQXxxIgEgB2siBiABSw0AIAZBeHEiBiANayIBIAZLDQAgASADayIGIAFLDQAgBkF4cSIGIAprIgEgBksNACABQYCAgIACTw0AIAFBA3RBYHEiASAC
Qf//A3FBBnRBwABqIgNuIQYCQCADIAFNDQAgAEEQakEAKALI38GAADYCACAAQQhqQQApAsDfwYAANwIAIABBACkCuN/BgAA3AgAPCyAGQYCABEkNAQsACyAA
QQA7ARAgACAENgIMIAAgBjsBCiAAIAI7AQggACAFNwIAC9IYBQV/AX4FfwF+BH8jgICAgABBsAFrIgMkgICAgAACQCAAKAIEIgRBAWoiBUUNACAALQAQQQFx
DQAgAEEBOgAQAkACQAJAAkACQAJAAkAgACgCCCIGIAVPDQADQEF/IAZBAXYgBmpBEGoiByAHIAZJGyIGIAVJDQALIAYgBEkNByAGQYCAgIAETw0HAkACQEEA
DQAgASkCACIIpyIJIAZBAnQiBEECQQAgCEIgiKciCigCABGAgICAAICAgIAAIgdFDQACQCAERQ0AIAdBqgEgBPwLAAsgB0EDcQ0JAkAgACgCBCIEDQAgACgC
CCIEQYCAgIAETw0KIARFDQIgACgCACELAkAgBEECdCIERQ0AIAtBqgEgBPwLAAsgCSALIARBAkEAIAooAgwRgYCAgACAgICAAAwCCyAAKAIIIgtBgICAgARP
DQkCQAJAIAsNAEEAIQxBqtWq1XohCwwBCyAAKAIAIgtBA3ENCiAEIQwLIAQgDEcNCQJAIAsgByAEQQJ0IgxqTw0AIAcgCyAMakkNCgsCQCAMRQ0AIAcgCyAM
/AoAAAsgACgCCCILQYCAgIAETw0JAkAgC0UNACAAKAIAIQwCQCALQQJ0IgtFDQAgDEGqASAL/AsACyAJIAwgC0ECQQAgCigCDBGBgICAAICAgIAACyAAIAQ2
AgQMAQsgAC0AEEEBcQ0CDAgLIAAgBzYCACAAIAY2AggLAkAgBUEQSw0AIAAtABBBAXFFDQcgAEEAOgAQIAAoAgwiDQ0EIAZBgICAgARPDQcgACgCACILQQNx
DQcCQAJAIAAoAgQiBEUNAEEAIQUgCyEHA0ACQCACIAcoAgBHDQAgBSAETw0DIANBAToAbEEBDQsMCQsgB0EEaiEHIAQgBUEBaiIFRw0ACwsgBCAGTw0IIAAg
BEEBajYCBCADQQA6AKgBQQANCCALIARBAnRqIQcMBgsgBSAEEIiAgIAAAAsCQCAAKAIMIg1FDQACQCANLQAAIgZBHU8NACAFIAZBAnRBzN/BgABqKAIASw0B
IAAtABBBAXFFDQgMBAsgBkEdEIiAgIAAAAsCQCAFQf////8ASw0AAkACQAJAAkAgBSAFQX9qZ0F/c0EfcUEBaiIGQQJ0QczfwYAAaigCAEsgBmoiBkEcSw0A
IAUgBkECdEHM38GAAGooAgBLDQtBAA0BIAEpAgAiCKdBAkEEQQggBkERSRsgBkEJSRsgBnQiBUEEaiIHQQJBACAIQiCIpygCABGAgICAAICAgIAAIg1FDQEC
QCAHRQ0AIA1BqgEgB/wLAAsgDUEDcQ0LIA1BBGohCwJAIAVFDQAgC0H/ASAF/AsACyANIAY6AAAgACgCDCIHRQ0GIAEpAgAhCCAHLQAAIgZBCU8NAkECIQUM
AwsgBkEdEIiAgIAAAAsgAC0AEEEBcUUNCQwDCwJAIAZBEU8NAEEEIQUMAQsgBkEhTw0IIAZBIEYNCEEIIQULQQEgBnStIAWtfiIOQiCIpw0HIA6nIgVBBGoi
BiAFSQ0HAkAgBkUNACAHQaoBIAb8CwALIAinIAcgBkECQQAgCEIgiKcoAgwRgYCAgACAgICAACANLQAAIQYMAgsgAC0AEEEBcUUNBgsgAEEQaiIGQQA6AAAg
A0GQAWpBEGogBigCADYCACADQZABakEIaiAAQQhqKQIANwMAIAMgACkCADcDkAECQAJAAkACQAJAAkAgACgCDCIGRQ0AIAYtAAAiB0EJSQ0CIAdBEUkNASAH
QSFPDQsgA0GAAWogA0GQAWogAiAGEJeAgIAAIANBgAFqIQYMAwsgAygCmAEiBkGAgICABE8NCgJAAkAgBg0AQQAhBUGq1arVeiEGDAELIAMoApABIgZBA3EN
CyADKAKUASEFCyAFRQ0EQQAhBwNAIAIgBigCAEYNBCAGQQRqIQYgBSAHQQFqIgdHDQAMBQsLIANB8ABqIANBkAFqIAIgBhCYgICAACADQfAAaiEGDAELIANB
qAFqIANBkAFqIAIgBhCZgICAACADQagBaiEGCyAGKQIAIghCgICAgPAfg1ANASAIpyEHCyAAKAIIIgZBgICAgARPDQYgACgCBCEFAkACQCAGDQBBACEEQarV
qtV6IQsMAQsgACgCACILQQNxDQcgBSEECwJAAkAgByAETw0AIAcgBUEAIAYbIgZPDQFBAQ0IIAsgB0ECdGohBwwGCyAHIAQQiICAgAAACyAHIAYQiICAgAAA
C0EZIQYMBAsCQAJAAkACQAJAIAZB/wFxIgdBCUkNACAHQRFJDQMgBkH/AXFBIU8NCSAAKAIIIgdBgICAgARPDQkgBw0BQQAhD0Gq1arVeiEQDAILIAAoAggi
B0GAgICABE8NCAJAAkAgBw0AQQAhD0Gq1arVeiEQDAELIAAoAgAiEEEDcQ0JIAAoAgQhDwsgD0UNAyANKAIAIgdB4AFxDQhBASAGQf8BcXQhAUF/IAd0QX9z
IRFBACESA0AgA0EQaiAQIBJBAnRqNQIAQoGAgIAQfiIIQtvR0IWa2t+BZ4VCACAIQqya4tOj0vD6H4VCABCvhYCAACADIAMpAxhC29HQhZra34FnhUIAIAMp
AxBCq8j1xcesh7ugf4VCABCvhYCAACASQYACRg0JIAMpAwggAykDAIWnIQlBACEFQQAhBiASIQoCQAJAA0AgCSAFaiARcSIHIAFPDQEgCyAHQQF0aiIHLQAA
IgxB/wFGDQICQAJAIActAAEiBEH/AXEgBkH/AXFJDQAgBiEEDAELIAcgCjoAACAHIAY6AAEgDCEKCyAEQf8BcUEBaiIGQf8BcSAGRw0MIAEgBUEBaiIFRg0M
DAALCyAHIAEQiICAgAAACyAHIAo6AAAgByAGOgABIBJBAWoiEiAPRw0ADAQLCyAAKAIAIhBBA3ENByAAKAIEIQ8LIAZB/wFxIgZBIEYNBiAPRQ0BIA0oAgAi
B0HgAXENBkEBIAZ0IQFBfyAHdEF/cyERQQAhEgNAIANB0ABqIBAgEkECdGo1AgBCgYCAgBB+IghC29HQhZra34FnhUIAIAhCrJri06PS8PofhUIAEK+FgIAA
IANBwABqIAMpA1hC29HQhZra34FnhUIAIAMpA1BCq8j1xcesh7ugf4VCABCvhYCAACADKQNIIAMpA0CFpyEJQQAhBUEAIQcgEiEKA0ACQAJAAkACQCAJIAVq
IBFxIgYgAU8NACALIAZBA3RqIgYoAgAiDEF/Rg0BIAYoAgQiBCAHSQ0CIAchBAwDCyAGIAEQiICAgAAACyAGIAo2AgAgBiAHNgIEIBJBAWoiEiAPRw0DDAUL
IAYgCjYCACAGIAc2AgQgDCEKCyAEQQFqIgdFDQggASAFQQFqIgVGDQgMAAsLCyAAKAIIIgdBgICAgARPDQUCQAJAIAcNAEEAIQ9BqtWq1XohEAwBCyAAKAIA
IhBBA3ENBiAAKAIEIQ8LIA9FDQAgDSgCACIHQeABcQ0FQQEgBkH/AXF0IQFBfyAHdEF/cyERQQAhEgNAIANBMGogECASQQJ0ajUCAEKBgICAEH4iCELb0dCF
mtrfgWeFQgAgCEKsmuLTo9Lw+h+FQgAQr4WAgAAgA0EgaiADKQM4QtvR0IWa2t+BZ4VCACADKQMwQqvI9cXHrIe7oH+FQgAQr4WAgAAgEkGAgARGDQYgAykD
KCADKQMghachCUEAIQVBACEGIBIhCgJAA0AgCSAFaiARcSIHIAFPDQECQCALIAdBAnRqIgcvAQAiDEH//wNGDQACQAJAIAcvAQIiBEH//wNxIAZB//8DcUkN
ACAGIQQMAQsgByAKOwEAIAcgBjsBAiAMIQoLIARB//8DcUEBaiIGQf//A3EgBkcNCSABIAVBAWoiBUYNCQwBCwsgByAKOwEAIAcgBjsBAiASQQFqIhIgD0cN
AQwCCwsgByABEIiAgIAAAAsgACANNgIMIAAtABBBAXFFDQQLIABBADoAEAsCQAJAIA0tAAAiBkEJSQ0AIAZBEUkNASAGQSFPDQQgA0GQAWogACACIA0QmoCA
gAAgAy0AnAFBAXENBCADKAKQASEHDAILIANB8ABqIAAgAiANEJuAgIAAIAMtAHxBAXENAyADKAJwIQcMAQsgA0GAAWogACACIA0QnICAgAAgAy0AjAFBAXEN
AiADKAKAASEHCyAHIAI2AgBBACEGCyADQbABaiSAgICAACAGDwsAC+wCBAF/AX4DfwF+AkAgAC0AEEEBcQ0AIABBCGooAgAiAkGAgICABE8NAAJAIAJFDQAg
ASkCACIDQiCIpyEEIAAoAgAhBSADpyEGAkAgAkECdCICRQ0AIAVBqgEgAvwLAAsgBiAFIAJBAkEAIAQoAgwRgYCAgACAgICAAAsgAEKq1arVqtWq1ap/NwIA
IABBCGoiBUGq1arVejYCAAJAIAAoAgwiAkUNACABKQIAIQMCQAJAIAItAAAiAUEJTw0AQQIhBAwBCwJAIAFBEU8NAEEEIQQMAQsgAUEhTw0CIAFBIEYNAkEI
IQQLQQEgAXStIAStfiIHQiCIpw0BIAenIgRBBGoiASAESQ0BAkAgAUUNACACQaoBIAH8CwALIAOnIAIgAUECQQAgA0IgiKcoAgwRgYCAgACAgICAAAsgAEKq
1arVqtWq1ap/NwIAIABBEGpBqtWq1Xo2AgAgBUKq1arVqtWq1ap/NwIADwsAC7wEAQV/I4CAgIAAQTBrIgUkgICAgAAgBUEoakGqfzoAACAFQSBqQqrVqtWq
1arVqn83AwAgBUEYakKq1arVqtWq1ap/NwMAIAVBEGpCqtWq1arVqtWqfzcDACAFQqrVqtWq1arVqn83AwgCQAJAAkACQCACQf8BcSIGQQpGDQBBN0HXACAD
QQFxGyEHQSEhAwNAIANBf2oiAiADSw0CIAVBCGogAmogASABIAZuIgggBmxrIgNBMHIgByADaiADQQpJGzoAACABIAZPIQkgAiEDIAghASAJDQAMBAsLAkAg
AUHjAEsNAEEhIQIMAgsgBSABIAFB5ABuIgZB5ABsa0EBdEGNosCAAGovAAA7ACcCQCABQZDOAE8NAEEfIQIgBiEBDAILIAUgBkHkAHBBAXRBjaLAgABqLwAA
OwAlIAFBkM4AbiEGAkAgAUHAhD1PDQBBHSECIAYhAQwCCyAFIAZB5ABwQQF0QY2iwIAAai8AADsAIyABQcCEPW4hBgJAIAFBgMLXL08NAEEbIQIgBiEBDAIL
IAUgBkHkAHBBAXRBjaLAgABqLwAAOwAhIAFBgMLXL24hAUEZIQIMAQsACwJAIAFBCk8NACAFQQhqIAJBf2oiAmogAUEwcjoAAAwBCyAFQQhqIAJBfmoiAmog
AUEBdEGNosCAAGovAAA7AAALIAAgBUEIaiACakEhIAJrIgEgBCgCCCABIAQtAAwbIAQtABAgBC0AERCWgICAACEBIAVBMGokgICAgAAgAQvMAQEBfwJAAkAg
AyACTQ0AQQAgAyACayIGIAYgA0sbIQYCQAJAAkACQCAEQQNxDgQAAQIDAAsgACABIAIQoICAgAAiA0H//wNxDQQgACAFIAYQooCAgAAPCyAGQQFqIgRFDQEg
ACAFIAZBAXYQooCAgAAiA0H//wNxDQMgACABIAIQoICAgAAiA0H//wNxDQMgACAFIARBAXYQooCAgAAPCyAAIAUgBhCigICAACIDQf//A3FFDQEMAgsACyAA
IAEgAhCggICAACEDCyADC8ABAQJ/I4CAgIAAQSBrIgQkgICAgAAgBEEQaiABQRBqKAIANgIAIARBCGogAUEIaikCADcDACAEIAEpAgA3AwACQAJAAkAgAy0A
ACIBQSBPDQAgBEEYaiAEIAIgAyADQQRqIgVBASABdCIBEKaAgIAAIAQtABxFDQECQCAEKAIYIgMgAU8NACAAQQE6AAQgACAFIANBA3RqKAIANgIADAMLIAMg
ARCIgICAAAsACyAAQgA3AgALIARBIGokgICAgAALwAEBAn8jgICAgABBIGsiBCSAgICAACAEQRBqIAFBEGooAgA2AgAgBEEIaiABQQhqKQIANwMAIAQgASkC
ADcDAAJAAkACQCADLQAAIgFBIE8NACAEQRhqIAQgAiADIANBBGoiBUEBIAF0IgEQpYCAgAAgBC0AHEUNAQJAIAQoAhgiAyABTw0AIABBAToABCAAIAUgA0EC
dGovAQA2AgAMAwsgAyABEIiAgIAACwALIABCADcCAAsgBEEgaiSAgICAAAvAAQECfyOAgICAAEEgayIEJICAgIAAIARBEGogAUEQaigCADYCACAEQQhqIAFB
CGopAgA3AwAgBCABKQIANwMAAkACQAJAIAMtAAAiAUEgTw0AIARBGGogBCACIAMgA0EEaiIFQQEgAXQiARCkgICAACAELQAcRQ0BAkAgBCgCGCIDIAFPDQAg
AEEBOgAEIAAgBSADQQF0ai0AADYCAAwDCyADIAEQiICAgAALAAsgAEIANwIACyAEQSBqJICAgIAAC8cFAwZ/AX4HfyOAgICAAEEgayIEJICAgIAAAkACQAJA
AkACQCABKAIIIgVBgICAgARPDQAgASgCBCEGAkACQCAFDQBBACEHQarVqtV6IQgMAQsgASgCACIIQQNxDQEgBiEHCyADLQAAIglBIE8NACAEQRBqIAKtQoGA
gIAQfiIKQtvR0IWa2t+BZ4VCACAKQqya4tOj0vD6H4VCABCvhYCAACAEIAQpAxhC29HQhZra34FnhUIAIAQpAxBCq8j1xcesh7ugf4VCABCvhYCAACADKAIA
IgtB4AFxDQAgA0EEaiEMQQEgCXQhDUF/IAt0QX9zIQ4gBCkDCCAEKQMAhachD0EAIQkCQANAIA8gCWogDnEiCyANTw0EAkAgDCALQQN0aiIQKQIAIgqnIgtB
f0cNACAGIAVPDQMgASAGQQFqNgIEIBAgBjYCACAQIAk2AgQMBgsgByALTQ0DAkAgAiAIIAtBAnRqIhEoAgBGDQAgCSAKQiCIpyIRSw0CIAlBf0YNAyANIAlB
AWoiCUcNAQwDCwsgACARNgIAIABBAToADCAHIAtNDQIgCyEGDAULIAYgBU8NACAJQQFqIQIgASAGQQFqNgIEIBAgCTYCBCAQIAY2AgAgEUEBaiEJA0AgDSAC
Rg0BIAMoAgAiEEHgAXENAQJAAkACQAJAIA8gAmpBfyAQdEF/c3EiECANTw0AIAwgEEEDdGoiDikCACIKpyIHQX9GDQEgCSAKQiCIpyIQSw0CIAkhEAwDCyAQ
IA0QiICAgAAACyAOIAk2AgQgDiALNgIADAYLIA4gCTYCBCAOIAs2AgAgByELCyACQQFqIQIgEEEBaiIJDQALCwALIAsgBxCIgICAAAALIAsgDRCIgICAAAAL
IABBADoADCAAIAggBkECdGo2AgALIAAgBjYCCCAAQarVqtV6NgIEIARBIGokgICAgAAL/QUDBn8Bfgl/I4CAgIAAQSBrIgQkgICAgAACQAJAAkACQAJAIAEo
AggiBUGAgICABE8NACABKAIEIQYCQAJAIAUNAEEAIQdBqtWq1XohCAwBCyABKAIAIghBA3ENASAGIQcLIAMtAAAiCUEgTw0AIARBEGogAq1CgYCAgBB+IgpC
29HQhZra34FnhUIAIApCrJri06PS8PofhUIAEK+FgIAAIAQgBCkDGELb0dCFmtrfgWeFQgAgBCkDEEKryPXFx6yHu6B/hUIAEK+FgIAAIAMoAgAiC0HgAXEN
ACADQQRqIQxBASAJdCEJQX8gC3RBf3MhDSAEKQMIIAQpAwCFpyEOQQAhD0EAIRACQANAIA4gD2ogDXEiCyAJTw0EAkAgDCALQQF0aiIRLwEAIhJB/wFxIgtB
/wFHDQAgBiAFTw0DIAEgBkEBajYCBCARIBA6AAEgBkGAAk8NAyARIAY6AAAMBgsgByALTQ0DAkAgAiAIIAtBAnRqIhMoAgBGDQAgEEH/AXEiCyASQQh2IhNL
DQIgC0EBaiIQQf8BcSAQRw0DIAkgD0EBaiIPRw0BDAMLCyAAIBM2AgAgAEEBOgAMIAcgC00NAiALIQYMBQsgBiAFTw0AIAEgBkEBajYCBCAGQYACTw0AIA9B
AWohCyARIBA6AAEgESAGOgAAIBNBAWohDwNAIAkgC0YNASADKAIAIhBB4AFxDQECQAJAAkACQCAOIAtqQX8gEHRBf3NxIhAgCU8NACAMIBBBAXRqIhEvAQAi
EEH/AXFB/wFGDQEgD0H/AXEgEEEIdiICSw0CIA8hAgwDCyAQIAkQiICAgAAACyARIA86AAEgESASOgAADAYLIBEgDzoAASARIBI6AAAgECESCyALQQFqIQsg
AkH/AXFBAWoiD0H/AXEgD0YNAAsLAAsgCyAHEIiAgIAAAAsgCyAJEIiAgIAAAAsgAEEAOgAMIAAgCCAGQQJ0ajYCAAsgACAGNgIIIABBqtWq1Xo2AgQgBEEg
aiSAgICAAAuIBgMGfwF+CX8jgICAgABBIGsiBCSAgICAAAJAAkACQAJAAkAgASgCCCIFQYCAgIAETw0AIAEoAgQhBgJAAkAgBQ0AQQAhB0Gq1arVeiEIDAEL
IAEoAgAiCEEDcQ0BIAYhBwsgAy0AACIJQSBPDQAgBEEQaiACrUKBgICAEH4iCkLb0dCFmtrfgWeFQgAgCkKsmuLTo9Lw+h+FQgAQr4WAgAAgBCAEKQMYQtvR
0IWa2t+BZ4VCACAEKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgAygCACILQeABcQ0AIANBBGohDEEBIAl0IQlBfyALdEF/cyENIAQpAwggBCkDAIWnIQ5BACEP
QQAhEAJAA0AgDiAPaiANcSILIAlPDQQCQCAMIAtBAnRqIhEoAgAiEkH//wNxIgtB//8DRw0AIAYgBU8NAyABIAZBAWo2AgQgESAQOwECIAZBgIAETw0DIBEg
BjsBAAwGCyAHIAtNDQMCQCACIAggC0ECdGoiEygCAEYNACAQQf//A3EiCyASQRB2IhNLDQIgC0EBaiIQQf//A3EgEEcNAyAJIA9BAWoiD0cNAQwDCwsgACAT
NgIAIABBAToADCAHIAtNDQIgCyEGDAULIAYgBU8NACABIAZBAWo2AgQgBkGAgARPDQAgD0EBaiELIBEgEDsBAiARIAY7AQAgE0EBaiEPA0AgCSALRg0BIAMo
AgAiEEHgAXENAQJAAkACQAJAIA4gC2pBfyAQdEF/c3EiECAJTw0AIAwgEEECdGoiESgCACIQQf//A3FB//8DRg0BIA9B//8DcSAQQRB2IgJLDQIgDyECDAML
IBAgCRCIgICAAAALIBEgDzsBAiARIBI7AQAMBgsgESAPOwECIBEgEjsBACAQIRILIAtBAWohCyACQf//A3FBAWoiD0H//wNxIA9GDQALCwALIAsgBxCIgICA
AAALIAsgCRCIgICAAAALIABBADoADCAAIAggBkECdGo2AgALIAAgBjYCCCAAQarVqtV6NgIEIARBIGokgICAgAALmwEBBH8gAUIgiKchAiABpyEDAkAgACgC
ECIERQ0AIAAoAgwhBQJAIARFDQAgBUGqASAE/AsACyADIAUgBEEAQQAgAigCDBGBgICAAICAgIAACwJAIAAtAAhBAXENACAAKAIEIgRFDQAgACgCACEAAkAg
BEUNACAAQaoBIAT8CwALIAMgACAEQQBBACACKAIMEYGAgIAAgICAgAALCwQAQQALBABBAgu1AgIIfwF+I4CAgIAAQRBrIgMkgICAgAACQAJAIAJFDQAgASAC
aiEEQQAhBQJAAkADQCAAKAIMIgYgAiAFayIHaiIIIAZJDQEgASAFaiEJAkACQCAIIAAoAggiCksNACAIIApLDQQCQCAAKAIEIAZqIgggBE8NACAJIAggB2pJ
DQQLAkAgB0UNACAIIAkgB/wKAAALIAAoAgwiCCAHaiIGIAhJDQMgACAGNgIMDAELIAAoAgAoAgAhCCADIAc2AgQgAyAJNgIAIANBCGogACADQQFBASAIEYGA
gIAAgICAgAAgAykDCCILQiCIpyIHQf//A3ENBSALpyEHCyAFIAdqIgcgBUkNASAHIQUgByACTw0DDAALCwALIAggChCIgICAAAALQQAhBwsgA0EQaiSAgICA
ACAHCy8AAkACQCAARQ0AIABBf2pnIgANAUEBEIyAgIAACwALQgFBICAAa61C//8Dg4anC4UCAgR/AX4jgICAgABBIGsiAySAgICAAAJAAkAgAkUNAAJAAkAD
QCAAKAIMIgQgAmoiBSAESQ0BAkACQCAFIAAoAggiBksNACAFIAZLDQQCQCACRQ0AIAAoAgQgBGogASAC/AsACyAAKAIMIgUgAmoiBCAFSQ0DIAAgBDYCDCAC
IQUMAQsgA0EBNgIUIAMgAToADyADIANBD2o2AhAgA0EYaiAAIANBEGpBASACEKOAgIAAIAMpAxgiB0IgiKciBUH//wNxDQUgB6chBQsgAiAFayIFIAJLDQEg
BSECIAVFDQMMAAsLAAsgBSAGEIiAgIAAAAtBACEFCyADQSBqJICAgIAAIAULygQDBn8BfgJ/I4CAgIAAQRBrIgUkgICAgAAgASgCCCEGIAEoAgQhBwJAAkAC
QCADQX9qIggNAEEAIQkMAQsgAigCBCEJIAhBAUYNACAJIQoDQCAKIAlqIgEgCk8hBCABIQogBA0ADAILCyACIAhBA3RqKAIEIgqtIAStfiILQiCIpw0AIAkg
C6dqIgwgCUkNACABKAIMIgkgDGoiDSAJSQ0AAkACQCANIAZNDQAgBUEIaiABIAIgAyAEIAEoAgAoAgARgYCAgACAgICAACAAIAUpAwg3AgAMAQsCQAJAAkAC
QAJAIAhFDQAgCSACKAIEIgpqIgMgBksNAQJAIAcgCWoiCSACKAIAIgMgCmpPDQAgAyAJIApqSQ0HCwJAIApFDQAgCSADIAr8CgAACyABKAIMIgMgCmoiCSAD
SQ0GIAEgCTYCDCACKAIMIQoLIAIgCEEDdGooAgAhCAJAAkAgCg4CBQABCyAJIARqIgogBksNAgJAIARFDQAgByAJaiAILQAAIAT8CwALIAEoAgwiCSAEaiIK
IAlJDQYgASAKNgIMDAQLIAggCmohAwNAIAkgCmoiAiAGSw0DAkAgByAJaiIJIANPDQAgCCAJIApqSQ0HCwJAIApFDQAgCSAIIAr8CgAACyABKAIMIgIgCmoi
CSACSQ0GIAEgCTYCDCAEQX9qIgRFDQQMAAsLIAMgBhCIgICAAAALIAogBhCIgICAAAALIAIgBhCIgICAAAALIABBADsBBCAAIAw2AgALIAVBEGokgICAgAAP
CwALngMDBH8BfgR/I4CAgIAAQSBrIgYkgICAgAACQAJAIAEoAggiB0GAgICABE8NAAJAAkAgBw0AQQAhCEGq1arVeiEJDAELIAEoAgAiCUEDcQ0BIAEoAgQh
CAsgBkEQaiACrUKBgICAEH4iCkLb0dCFmtrfgWeFQgAgCkKsmuLTo9Lw+h+FQgAQr4WAgAAgBiAGKQMYQtvR0IWa2t+BZ4VCACAGKQMQQqvI9cXHrIe7oH+F
QgAQr4WAgAAgAygCACIBQeABcQ0AQX8gAXRBf3MhCyAGKQMIIAYpAwCFpyEMQQAhAUEAIQMDQAJAAkACQAJAIAwgAWogC3EiDSAFTw0AAkAgBCANQQF0aiIO
LQAAIgdB/wFGDQAgDi0AAUH/AXEgA0H/AXEiA08NAgsgAEIANwIADAILIA0gBRCIgICAAAALIAggB00NBCACIAkgB0ECdGooAgBHDQEgAEEBOgAEIAAgDTYC
AAsgBkEgaiSAgICAAA8LIANBAWoiA0H/AXEgA0cNASAFIAFBAWoiAUcNAAsLAAsgByAIEIiAgIAAAAuiAwMEfwF+BH8jgICAgABBIGsiBiSAgICAAAJAAkAg
ASgCCCIHQYCAgIAETw0AAkACQCAHDQBBACEIQarVqtV6IQkMAQsgASgCACIJQQNxDQEgASgCBCEICyAGQRBqIAKtQoGAgIAQfiIKQtvR0IWa2t+BZ4VCACAK
Qqya4tOj0vD6H4VCABCvhYCAACAGIAYpAxhC29HQhZra34FnhUIAIAYpAxBCq8j1xcesh7ugf4VCABCvhYCAACADKAIAIgFB4AFxDQBBfyABdEF/cyELIAYp
AwggBikDAIWnIQxBACEBQQAhAwNAAkACQAJAAkAgDCABaiALcSINIAVPDQACQCAEIA1BAnRqIg4vAQAiB0H//wNGDQAgDi8BAkH//wNxIANB//8DcSIDTw0C
CyAAQgA3AgAMAgsgDSAFEIiAgIAAAAsgCCAHTQ0EIAIgCSAHQQJ0aigCAEcNASAAQQE6AAQgACANNgIACyAGQSBqJICAgIAADwsgA0EBaiIDQf//A3EgA0cN
ASAFIAFBAWoiAUcNAAsLAAsgByAIEIiAgIAAAAv/AgMEfwF+A38jgICAgABBIGsiBiSAgICAAAJAAkAgASgCCCIHQYCAgIAETw0AAkACQCAHDQBBACEIQarV
qtV6IQkMAQsgASgCACIJQQNxDQEgASgCBCEICyAGQRBqIAKtQoGAgIAQfiIKQtvR0IWa2t+BZ4VCACAKQqya4tOj0vD6H4VCABCvhYCAACAGIAYpAxhC29HQ
hZra34FnhUIAIAYpAxBCq8j1xcesh7ugf4VCABCvhYCAACADKAIAIgFB4AFxDQBBfyABdEF/cyELIAYpAwggBikDAIWnIQxBACEBA0ACQAJAAkACQCAMIAFq
IAtxIgMgBU8NAAJAIAQgA0EDdGoiDSgCACIHQX9GDQAgDSgCBCABTw0CCyAAQgA3AgAMAgsgAyAFEIiAgIAAAAsgByAITw0EIAIgCSAHQQJ0aigCAEcNASAA
QQE6AAQgACADNgIACyAGQSBqJICAgIAADwsgBSABQQFqIgFHDQALCwALIAcgCBCIgICAAAALqQ4DBX8CfgF/AkACQCAARQ0AAkAgAC0AtBxFDQAgAC0AsBxF
DQAgAEGYHGohAQJAIAAoAqgcIgJFDQAgAEGcHGooAgAhAyAAKAKYHCEEIAAoAqQcIQUCQCACRQ0AIAVBqgEgAvwLAAsgBCAFIAJBAEEAIAMoAgwRgYCAgACA
gICAAAsgAUKq1arVqtWq1ap/NwMAIAFBEGpCqtWq1arVqtWqfzcDACABQQhqQqrVqtWq1arVqn83AwALIAAtAOAcIgFBGEsNAQJAIAFBCEcNACAALQCQHEUN
ACAAKQOIHCEGAkACQCAAQcAcaigCACIBDQBBACEBQarVqtV6IQIMAQsgAa1CDH4iB0IgiKcNAyAAKAK4HCECIAenIQELIAFFDQAgBkIgiKchBSAGpyEDAkAg
AUUNACACQaoBIAH8CwALIAMgAiABQQJBACAFKAIMEYGAgIAAgICAgAALAkBB0QBFDQAgAEGUHGpBAEHRAPwLAAsgAEGAHGotAABBA0YNASAAQYgSaiAAQbAb
aiIBEKiAgIAAAkAgACgCNCICRQ0AIAApA7AbIQYgACgCMCEFAkAgAkUNACAFQaoBIAL8CwALIAanIAUgAkEAQQAgBkIgiKcoAgwRgYCAgACAgICAAAsCQEHM
AEUNACAAQSxqQaoBQcwA/AsACyAAQRhqIAApA7AbEIOAgIAAIABBgAFqIQICQCAAQYgBaigCACIFRQ0AIAEpAwAhBiACKAIAIQMCQCAFRQ0AIANBqgEgBfwL
AAsgBqcgAyAFQQBBACAGQiCIpygCDBGBgICAAICAgIAACyACQqrVqtWq1arVqn83AwAgAkEIakGq1arVejYCAAJAIABBlAFqKAIAIgJFDQAgACkDsBshBiAA
KAKMASEFAkAgAkUNACAFQaoBIAL8CwALIAanIAUgAkEAQQAgBkIgiKcoAgwRgYCAgACAgICAAAsCQEGIEkUNACAAQaoBQYgS/AsACyAAQawtaiAAKQOwGxCp
gICAACAAKQOwGyEGAkACQCAAQaQuaigCACICDQBBACEDQarVqtV6IQgMAQsgAkGAgICABE8NAiAAKAKcLiEIIAJBAnQhAwsgAEGcLmohBCAGQiCIpyECIAan
IQUCQCADRQ0AAkAgA0UNACAIQaoBIAP8CwALIAUgCCADQQJBACACKAIMEYGAgIAAgICAgAAgASkDACIGQiCIpyECIAanIQULIARCqtWq1arVqtWqfzcCAEGq
1arVeiEIIARBCGpBqtWq1Xo2AgACQAJAIABBsC5qKAIAIgMNAEEAIQMMAQsgA0GAgICABE8NAiAAKAKoLiEIIANBAnQhAwsgAEGoLmohBAJAIANFDQACQCAD
RQ0AIAhBqgEgA/wLAAsgBSAIIANBAkEAIAIoAgwRgYCAgACAgICAACABKQMAIgZCIIinIQIgBqchBQsgBEKq1arVqtWq1ap/NwMAQarVqtV6IQggBEEIakGq
1arVejYCAAJAAkAgAEG8LmooAgAiAw0AQQAhAwwBCyADQYCAgIAETw0CIAAoArQuIQggA0ECdCEDCyAAQbQuaiEEAkAgA0UNAAJAIANFDQAgCEGqASAD/AsA
CyAFIAggA0ECQQAgAigCDBGBgICAAICAgIAAIAEpAwAhBgsgBEKq1arVqtWq1ap/NwIAQarVqtV6IQMgBEEIakGq1arVejYCACAAQcAuaiECAkAgAEHILmoo
AgAiBUUNACACKAIAIQQCQCAFRQ0AIARBqgEgBfwLAAsgBqcgBCAFQQBBACAGQiCIpygCDBGBgICAAICAgIAAIAEpAwAhBgsgAkKq1arVqtWq1ap/NwMAIAJB
CGpBqtWq1Xo2AgACQAJAIABB1C5qKAIAIgINAEEAIQIMAQsgAq1CxAB+IgdCIIinDQIgACgCzC4hAyAHpyECCyAAQcwuaiEFAkAgAkUNACAGQiCIpyEEIAan
IQgCQCACRQ0AIANBqgEgAvwLAAsgCCADIAJBAkEAIAQoAgwRgYCAgACAgICAAAsgBUKq1arVqtWq1ap/NwIAIAVBCGpBqtWq1Xo2AgAgAEHYLmohAgJAIABB
4C5qKAIAIgVFDQAgASkDACEGIAIoAgAhAwJAIAVFDQAgA0GqASAF/AsACyAGpyADIAVBAEEAIAZCIIinKAIMEYGAgIAAgICAgAALIAJCqtWq1arVqtWqfzcD
ACACQQhqQarVqtV6NgIAIABB5C5qIQICQCAAQewuaigCACIFRQ0AIAEpAwAhBiACKAIAIQECQCAFRQ0AIAFBqgEgBfwLAAsgBqcgASAFQQBBACAGQiCIpygC
DBGBgICAAICAgIAACyACQqrVqtWq1arVqn83AgAgAkEIakGq1arVejYCACAAKAKwGyAAQYgvQQNBACAAQbQbaigCAEEMaigCABGBgICAAICAgIAACw8LAAv1
AwMKfwF+AX8CQCAAKALQCCICQdaq1aoBTw0AIABByAhqIgMoAgAhBAJAAkAgAg0AQQAhBUGq1arVeiEGQarVqtV6IQcMAQsgBCACQQN0IghqIgZBA3ENASAA
KALMCCEFIAYgCGogAkEMbGohBwsgBSAFRw0AAkAgBUUNAEEAIQkDQCAHIAlBDGxqIQoCQCAGIAlBA3RqKAIAIgJFDQAgASgCACEIIAEoAgQhCwNAIAJBfGoh
BCACKAIAIQIgCCAEIAQoAgBBAkEAIAsoAgwRgYCAgACAgICAACACDQALCyAKQQhqIgQ1AgBCLH4iDEIgiKcNAgJAIAynIgJFDQAgASkCACIMQiCIpyELIAoo
AgAhCCAMpyENAkAgAkUNACAIQaoBIAL8CwALIA0gCCACQQNBACALKAIMEYGAgIAAgICAgAALIApCqtWq1arVqtWqfzcCACAEQarVqtV6NgIAIAlBAWoiCSAF
Rw0ACyAAKALQCCECIAAoAsgIIQQLIAKtQjt+IgxCIIinDQACQCAMpyICRQ0AIAEpAgAiDEIgiKchCCAMpyELAkAgAkUNACAEQaoBIAL8CwALIAsgBCACQQNB
ACAIKAIMEYGAgIAAgICAgAALIANCqtWq1arVqtWqfzcDACADQQhqQarVqtV6NgIADwsAC9QKAQR/AkACQAJAIAAoAgQiAg0AQQAhA0Gq1arVeiEEDAELIAJB
gICAgARPDQEgACgCACEEIAJBAnQhAwsgAUIgiKchAiABpyEFAkAgA0UNAAJAIANFDQAgBEGqASAD/AsACyAFIAQgA0ECQQAgAigCDBGBgICAAICAgIAACwJA
IAAoAgwiA0UNACAAKAIIIQQCQCADRQ0AIARBqgEgA/wLAAsgBSAEIANBAEEAIAIoAgwRgYCAgACAgICAAAsCQCAAKAIUIgNFDQAgACgCECEEAkAgA0UNACAE
QaoBIAP8CwALIAUgBCADQQBBACACKAIMEYGAgIAAgICAgAALAkACQCAAKAIcIgMNAEEAIQNBqtWq1XohBAwBCyADQX9MDQEgACgCGCEEIANBAXQhAwsCQCAD
RQ0AAkAgA0UNACAEQaoBIAP8CwALIAUgBCADQQFBACACKAIMEYGAgIAAgICAgAALAkACQCAAKAIkIgMNAEEAIQNBqtWq1XohBAwBCyADQX9MDQEgACgCICEE
IANBAXQhAwsCQCADRQ0AAkAgA0UNACAEQaoBIAP8CwALIAUgBCADQQFBACACKAIMEYGAgIAAgICAgAALAkAgACgCLCIDRQ0AIAAoAighBAJAIANFDQAgBEGq
ASAD/AsACyAFIAQgA0EAQQAgAigCDBGBgICAAICAgIAACwJAAkAgACgCNCIDDQBBACEDQarVqtV6IQQMAQsgA0GAgICABE8NASAAKAIwIQQgA0ECdCEDCwJA
IANFDQACQCADRQ0AIARBqgEgA/wLAAsgBSAEIANBAkEAIAIoAgwRgYCAgACAgICAAAsCQAJAIAAoAjwiAw0AQQAhA0Gq1arVeiEEDAELIANBgICAgARPDQEg
ACgCOCEEIANBAnQhAwsCQCADRQ0AAkAgA0UNACAEQaoBIAP8CwALIAUgBCADQQJBACACKAIMEYGAgIAAgICAgAALAkACQCAAKAJEIgMNAEEAIQNBqtWq1Xoh
BAwBCyADQYCAgIAETw0BIAAoAkAhBCADQQJ0IQMLAkAgA0UNAAJAIANFDQAgBEGqASAD/AsACyAFIAQgA0ECQQAgAigCDBGBgICAAICAgIAACwJAAkAgACgC
TCIDDQBBACEDQarVqtV6IQQMAQsgA0GAgICABE8NASAAKAJIIQQgA0ECdCEDCwJAIANFDQACQCADRQ0AIARBqgEgA/wLAAsgBSAEIANBAkEAIAIoAgwRgYCA
gACAgICAAAsCQAJAIAAoAlQiAw0AQQAhA0Gq1arVeiEEDAELIANBgICAgARPDQEgACgCUCEEIANBAnQhAwsCQCADRQ0AAkAgA0UNACAEQaoBIAP8CwALIAUg
BCADQQJBACACKAIMEYGAgIAAgICAgAALAkACQCAAKAJcIgMNAEEAIQNBqtWq1XohBAwBCyADQYCAgIAETw0BIAAoAlghBCADQQJ0IQMLAkAgA0UNAAJAIANF
DQAgBEGqASAD/AsACyAFIAQgA0ECQQAgAigCDBGBgICAAICAgIAACwJAAkAgACgCZCIDDQBBACEDQarVqtV6IQQMAQsgA0F/TA0BIAAoAmAhBCADQQF0IQML
AkAgA0UNAAJAIANFDQAgBEGqASAD/AsACyAFIAQgA0EBQQAgAigCDBGBgICAAICAgIAACwJAAkAgACgCbCIDDQBBACEAQarVqtV6IQQMAQsgA0F/TA0BIAAo
AmghBCADQQF0IQALAkAgAEUNAAJAIABFDQAgBEGqASAA/AsACyAFIAQgAEEBQQAgAigCDBGBgICAAICAgIAACw8LAAvWAgMBfwF+An8CQCAAKAI0IgJFDQAg
ASkCACEDIAAoAjAhBAJAIAJFDQAgBEGqASAC/AsACyADpyAEIAJBAEEAIANCIIinKAIMEYGAgIAAgICAgAALAkBBzABFDQAgAEEsakGqAUHMAPwLAAsgAEEY
aiABKQIAEIOAgIAAIABBgAFqIQICQCAAQYgBaigCACIERQ0AIAEpAgAhAyACKAIAIQUCQCAERQ0AIAVBqgEgBPwLAAsgA6cgBSAEQQBBACADQiCIpygCDBGB
gICAAICAgIAACyACQqrVqtWq1arVqn83AwAgAkEIakGq1arVejYCAAJAIABBlAFqKAIAIgJFDQAgASkCACEDIAAoAowBIQECQCACRQ0AIAFBqgEgAvwLAAsg
A6cgASACQQBBACADQiCIpygCDBGBgICAAICAgIAACwJAQYgSRQ0AIABBqgFBiBL8CwALC4cBAgF+BH8CQCAAQQhqNQIAQix+IgJCIIinDQACQCACpyIDRQ0A
IAFCIIinIQQgACgCACEFIAGnIQYCQCADRQ0AIAVBqgEgA/wLAAsgBiAFIANBA0EAIAQoAgwRgYCAgACAgICAAAsgAEKq1arVqtWq1ap/NwIAIABBCGpBqtWq
1Xo2AgAPCwAL4AICBX8CfgJAIAAtACxFDQAgAC0AKEUNACAAQRBqIQECQCAAKAIgIgJFDQAgAEEUaigCACEDIAAoAhAhBCAAKAIcIQUCQCACRQ0AIAVBqgEg
AvwLAAsgBCAFIAJBAEEAIAMoAgwRgYCAgACAgICAAAsgAUKq1arVqtWq1ap/NwIAIAFBEGpCqtWq1arVqtWqfzcCACABQQhqQqrVqtWq1arVqn83AgALAkAC
QCAALQBYIgFBGEsNACABQQhHDQEgAC0ACEUNASAAKQIAIQYCQAJAIABBOGooAgAiAQ0AQQAhAUGq1arVeiECDAELIAGtQgx+IgdCIIinDQEgACgCMCECIAen
IQELIAFFDQEgBkIgiKchBSAGpyEDAkAgAUUNACACQaoBIAH8CwALIAMgAiABQQJBACAFKAIMEYGAgIAAgICAgAAMAQsACwJAQdEARQ0AIABBDGpBAEHRAPwL
AAsL+wwDCn8CfgV/I4CAgIAAQSBrIgMkgICAgAACQAJAIAANAEEBIQAMAQsCQCACRQ0AAkAgAC8B+hEiBEUNACAALwH4ESIFQf//A3FFDQACQCAALwGAEiIG
IARPDQAgAC8BghJB//8DcSIHIARPDQAgBiAHTw0AIAAvAfwRIgdB//8DcSAFQf//A3EiBk8NACAALwH+ESIIQf//A3EgBk8NACAHQf//A3EgCEH//wNxSQ0B
CyAAQQA7AYASIABBADsB/BEgACAEQX9qOwGCEiAAIAVBf2o7Af4RC0G6AkG5AkEALQDN48GAAEEBcSIJGyEKIABBxBtqIQsgAEG4G2ohDEEAKQOw48GAACIN
QoCAgIDwH4MhDiANpyEPIABBpi1qIQdBAC0AzuPBgABBA3EhECAAQYYcaiERA0AgAS0AACEEAkACQCAALQCaLUEPcUUNACAMIAQQroCAgAAMAQsgBEHA4MGA
AGotAAAhBgJAAkAgAC0AqC0iCEUNACAHLQAAQRZ0IAAvAaQtQQZ0ciAEQT9xciEFDAELQf8BIAZBD3F2IARxIQULIAAgBTsBpC0gByAFQYCA/ABxQRB2OgAA
AkACQCAIIAZBD3EiEmoiE0H/AXEiBiATRw0AAkACQAJAAkACQAJAAkACQCAGQesASw0AIAAgBkHA4sGAAGotAAAiBjoAqC0gBg4NAQICAgICAgICAgICAwIL
IAZB7AAQiICAgAAACyAAQQA7AaQtIAdBADoAACADIAU7AQwgAyAFOwEIIAMgBUEQdkEfcSIFOgAOIAMgBToACkEBIQYMAgsgA0EAOgAOIANBADsBDAwHCyAI
RSEGIABBADsBpC0gAEEAOgCoLSAHQQA6AAAgAyAPOwEMIAMgD0EQdjoADiAOUA0BCwJAIAMvAQwgAy0ADkEQdHIiBUEPSw0AAkBBAA0AAkACQAJAAkACQAJA
AkACQCAFQQ9xDhAKCgoICAAIAQIDBwcHBAUGCgsgDBCvgICAACAGDQ4MCwsCQCAAKALMGyIFRQ0AIAsgBRGDgICAAICAgIAACyAGDQ0MCgsgCygCAEEBELCA
gIAAIAYNDAwJCyAMQQEQsYCAgAAgBg0LDAgLIAsoAgAQsoCAgAAgBg0KDAcLIAsoAgAoAhggCmogEDoAACAGDQkMBgsgCygCACgCGEEAOgC5AiAGDQgMBQsg
CygCABCzgICAACAGDQcMBAsgAyAFOgATIANBE2oQtICAgAAgBg0GDAMLIAVBG0YNASALKAIAIAUQtYCAgAALIAYNBAwBCyAAQQA7AYQcIABBADsAny0gAEEB
OgCaLSAAQQA6AKEtIABBADsBmC0gEUEAOgAAIAYNAwsCQAJAIAAtAKgtIgZFDQAgBy0AAEEWdCAALwGkLUEGdHIgBEE/cXIhBAwBC0H/ASASdiAEcSEECyAA
IAQ7AaQtIAcgBEGAgPwAcUEQdjoAACAGIBJqIghB/wFxIgUgCEcNAAJAAkACQAJAAkAgBUHrAEsNACAAIAVBwOLBgABqLQAAIgU6AKgtIAUODQECAgICAgIC
AgICAgMCCyAFQewAEIiAgIAAAAsgAEEAOwGkLSAHQQA6AAAgAyAEOwEYIAMgBDsBFCADIARBEHZBH3EiBDoAGiADIAQ6ABYMAgsgA0EAOgAaIANBADsBGAwE
CyAAQQA7AaQtIABBADoAqC0gB0EAOgAAIAYNASADIA87ARggAyAPQRB2OgAaIA5QDQMLAkAgAy8BGCADLQAaQRB0ciIEQQ9LDQACQEEADQACQAJAAkACQAJA
AkACQAJAIARBD3EOEAwMDAgIAAgBAgMHBwcEBQYMCyAMEK+AgIAADAsLIAAoAswbIgRFDQogCyAEEYOAgIAAgICAgAAMCgsgCygCAEEBELCAgIAADAkLIAxB
ARCxgICAAAwICyALKAIAELKAgIAADAcLIAsoAgAoAhghBAJAIAkNACAEIBA6ALkCDAcLIAQgEDoAugIMBgsgCygCACgCGEEAOgC5AgwFCyALKAIAELOAgIAA
DAQLIAMgBDoAHyADQR9qELSAgIAADAMLIARBG0YNASALKAIAIAQQtYCAgAAMAgsACyAAQQA7AYQcIABBADsAny0gAEEBOgCaLSAAQQA6AKEtIABBADsBmC0g
EUEAOgAACyABQQFqIQEgAkF/aiICDQALC0EAIQALIANBIGokgICAgAAgAAuRvAEDBH8Bflx/I4CAgIAAQfAXayICJICAgIAAAkACQAJAIAAtAOIRIgNBD3Ei
BEUNAAJAIARBAUcNACABQf8BcUHbAEcNACAAQQM6AOIRDAMLAkACQCADQQ9xIgVBBUcNAAJAAkACQAJAIAFB/wFxIgRBaGoOJAEIAQICAgICAgICAgICAgIC
AgICAgICAgICAgICAgICAgIDAwALIARB/wBHDQEMBwsgAEEAOgDiEQwGCyAEQRBJDQQgAUH/AXFBGEkNBSABQfwBcUEcRg0FIAFBUGoiBEH/AXFBCk8NASAA
LQDoEUEYTw0FIAAgAC0A6RFBAXI6AOkRIABBfyAALwHgEUEQdK1CCn4iBqcgBkIgiKcbQRB2IARB/wFxaiIBQf//AyABQf//A0kbOwHgEQwFCyAALQDoESIE
QRhPDQQgACAEQQF0aiAALwHgETsBsBEgAC0A6BEhBCABQf8BcUE6Rw0BIARB/wFxQRhPDQIgACAALwFMIABBzgBqIgEtAABBEHRyQQEgBEH/AXF0ciIDOwFM
IAEgA0EQdjoAAAwBCwJAAkAgBUENSw0AIAFB/wFxQRxsIAVBAXRqIgRB0ePBgABqLQAAIQdB0JvCgAAhCEHQm8KAACEFIANBD3EiCSAEQdDjwYAAai0AACIK
QQ9xRg0BQYScwoAAIQUCQAJAAkAgCUF2ag4EBAIBAAILQbicwoAAIQUMAwtB0JvCgAAhBSAAQdAAaiABQQEQ+YCAgAAiBEUNAiACQQhqQShqIARBKGooAgA2
AgAgAkEIakEgaiAEQSBqKQIANwMAIAJBCGpBGGogBEEYaikCADcDACACQQhqQRBqIARBEGopAgA3AwAgAkEIakEIaiAEQQhqKQIANwMAIAJBBDoABCACQQE6
ADggAkEEOgA0IAIgBCkCADcDCCACQQhqIQUMAgtB0JvCgAAhBSADQQ9xQQ5JDQEMAwsgBUEOEIiAgIAAAAsCQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCAHQQ9xDgseHgABBQYJAgMHBCALIAJBADoAQiACQQA6ADwgAkEAOgBGIAJBAToAdCACQQA6AHAg
AiABQf8BcSIEOwFAIAIgBDsBRCACQcQAaiEIDB0LIAIgAToAfCACQQE6AHggAkEBOgCsASACQQE6AKgBIAJB/ABqIQgMHAsgAC0A6BEiBEEXSw0bAkAgAC0A
6RFFDQAgACAEQQF0aiAALwHgETsBsBEgAC0A6BFBAWoiBEH/AXEgBEcNHiAAIAQ6AOgRCyACQQI6ALgBIAAtAOcRIgNBBU8NCSAEQf8BcSIHQRlPDQogAEHj
EWohCSAAQbARaiELIAAvAUwgAEHOAGotAABBEHRyIQQgAUH/AXFB7QBGDQUgBEH///8HcUUNBSACIAQ7AcwBIAJBzgFqIARBEHY6AAAgAiABOgDQASACIAc2
AsgBIAIgCzYCxAEgAiADNgLAASACIAk2ArwBIAIgAi0AuAE6AOgBIAJBvAFqEPqAgIAADBsLIAIgAToA3AIgAkEGOgDYAiACQQE6AIwDIAJBBjoAiAMgAkHc
AmohCAwaCyACIAE6AJQDIAJBCToAkAMgAkEBOgDEAyACQQk6AMADIAJBlANqIQgMGQsgAC0A5xEiBEEDSw0XIAAgBGogAToA4xEgAC0A5xFBAWoiBEH/AXEg
BEcNGiAAIAQ6AOcRDBgLIAFB/gFxQTpGDRUgAEF/IAAvAeARQRB0rUIKfiIGpyAGQiCIpxtBEHYiAzsB4BEgAUH/AXFBUGoiBEH/AXEiByAERw0ZIAAgAC0A
6RFBAWo6AOkRIAAgAyAHaiIEQf//AyAEQf//A0kbOwHgEQwXCyAALQCsASIEQT9xQQFGDRYCQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAAtAHxFDQAgACgCXCABEPuAgIAAQf//A3EOA2ZoAWgLIARBP3EOLwFnTgIGFUwHQAhOThYXGAME
TAVMTExMTExMTk1BTUUoTExMTExMTExMTAlJTkpLZwsgAEEBOgCsAQxkCyABQf8BcUFQag4KCAkKCwwNDg8QERILIAFB/wFxQVBqDgwWFxgZGhscHR4fICEg
CyABQf8BcUFMag4IISIiIiIiIiMiCyABQf8BcUFQag4MJCUmJygpKissLS4vLgsgAUH/AXFBTWoOCS8wMDAwMDAwMTALIAFB/wFxQU9qDgsxMjMzMzMzMzMz
NDMLIAFB/wFxQU5qDgo0NjY1NjY2NjY3NgsgAUH/AXFBSWoOBTk6Ojo7OgsgAUH/AXFBSWoOBTw9PT0+PQsgAEECOgCsAQxaCyAAQQM6AKwBDFkLIABBBDoA
rAEMWAsgAEEFOgCsAQxXCyAAQQY6AKwBDFYLIABBBzoArAEMVQsgAEEIOgCsAQxUCyAAQQk6AKwBDFMLIABBCjoArAEMUgsgAEELOgCsAQxRCyAAQQE6AKwB
DFALIAFB/wFxQTBGDUwgAEEBOgCsAQxPCyABQf8BcUEwRg1KIABBAToArAEMTgsgAUH/AXFBOEYNSCAAQQE6AKwBDE0LAkAgAUH/AXFBO0YNACAAQQE6AKwB
DE0LIABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAxMCyAAQQ86AKwBDEsLIABBEDoArAEMSgsgAEEROgCs
AQxJCyAAQRI6AKwBDEgLIABBEzoArAEMRwsgAEEUOgCsAQxGCyAAQRU6AKwBDEULIABBFjoArAEMRAsgAEEXOgCsAQxDCyAAQRg6AKwBDEILIABBAToArAEM
QQsgAEEAOgB4IABBAToAfCAAQoAQNwJoIABBuN7BgAA2AmAgACAAQa0BajYCZCAAIABB4ABqNgJcDEALIABBHzoArAEMPwsgAEEBOgCsAQw+CyAALQBYRQ03
IABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAw9CwJAIAFB/wFxQTtGDQAgAEEBOgCsAQw9CyAALQBYRQ01
IABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAw8CyAAQSA6AKwBDDsLIABBIToArAEMOgsgAEEiOgCsAQw5
CyAAQSM6AKwBDDgLIABBJDoArAEMNwsgAEElOgCsAQw2CyAAQSY6AKwBDDULIABBJzoArAEMNAsgAEEoOgCsAQwzCyAAQSk6AKwBDDILIABBAToArAEMMQsg
AC0AWEUNKCAAQQA6AHggAEEBOgB8IABCgBA3AmggAEG43sGAADYCYCAAIABBrQFqNgJkIAAgAEHgAGo2AlwMMAsgAEEqOgCsAQwvCyAAQQE6AKwBDC4LIAAt
AFhFDSQgAEEAOgB4IABBAToAfCAAQoAQNwJoIABBuN7BgAA2AmAgACAAQa0BajYCZCAAIABB4ABqNgJcDC0LIABBGToArAEMLAsgAEEaOgCsAQwrCyAAQQE6
AKwBDCoLIABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAwpCyAAQRs6AKwBDCgLIABBHDoArAEMJwsgAEEB
OgCsAQwmCyAALQBYRQ0bIABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAwlCyABQf8BcUE2Rg0ZIABBAToA
rAEMJAsgAUH/AXFBMkYNFyAAQQE6AKwBDCMLIABBHjoArAEMIgsgAEEBOgCsAQwhCyAAQQA6AHggAEEBOgB8IABCgBA3AmggAEG43sGAADYCYCAAIABBrQFq
NgJkIAAgAEHgAGo2AlwMIAsgAUH/AXFBN0YNEiAAQQE6AKwBDB8LIABBLToArAEMHgsgAEEBOgCsAQwdCyAAQQA6AHggAEEBOgB8IABCgBA3AmggAEG43sGA
ADYCYCAAIABBrQFqNgJkIAAgAEHgAGo2AlwMHAsgAUH/AXFBMkYNDSAAQQE6AKwBDBsLAkAgAUH/AXFBO0YNACAAQQE6AKwBDBsLIABBADoAeCAAQQE6AHwg
AEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAAajYCXAwaCwJAIAFB/wFxQTtGDQAgAEEBOgCsAQwaCyAALQBYRQ0FIAApAlAhBiAAQQE6AHgg
AEEBOgB8QQANGwJAIAanQYAQQQBBACAGQiCIpygCABGAgICAAICAgIAAIgRFDQACQEGAEEUNACAEQaoBQYAQ/AsACyAAQoAQNwJwIAAgBDYCbCAAQdyVx4AA
NgJoIAAgBjcCYCAALQB8RQ0cAkAgAC0AeEUNACAAIABB6ABqNgJcDBsLQQBBARD8gICAAAALIAAtAHwNGyAAQQA6AHggAEEBOgB8IABCgBA3AmggAEG43sGA
ADYCYCAAIABB4ABqNgJcIAAgAEGtAWo2AmQMGQsCQCABQf8BcUE7Rg0AIABBAToArAEMGQsgAC0AWEUNCSAAQQA6AHggAEEBOgB8IABCgBA3AmggAEG43sGA
ADYCYCAAIABBrQFqNgJkIAAgAEHgAGo2AlwMGAsCQCABQf8BcUE7Rg0AIABBAToArAEMGAsgAC0AWEUNBCAAKQJQIQYgAEEBOgB4IABBAToAfEEADRkCQCAG
p0GAEEEAQQAgBkIgiKcoAgARgICAgACAgICAACIERQ0AAkBBgBBFDQAgBEGqAUGAEPwLAAsgAEKAEDcCcCAAIAQ2AmwgAEHclceAADYCaCAAIAY3AmAgAC0A
fEUNGgJAIAAtAHhFDQAgACAAQegAajYCXAwZC0EAQQEQ/ICAgAAACyAALQB8DRkgAEEAOgB4IABBAToAfCAAQoAQNwJoIABBuN7BgAA2AmAgACAAQeAAajYC
XCAAIABBrQFqNgJkDBcLAkAgAUH/AXFBO0YNACAAQQE6AKwBDBcLIABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAgAEGtAWo2AmQgACAAQeAA
ajYCXAwWCyACIAQ7AfwBIAJB/gFqIARBEHY6AAAgAiABOgCAAiACIAc2AvgBIAIgCzYC9AEgAiADNgLwASACIAk2AuwBIAJBAToAnAIgAiACLQC4AToAmAIg
AkHsAWohCAwVCyAALQDnESIEQQVPDQQgAiABOgCsAiACIAQ2AqgCIAJBAzoAoAIgAkEBOgDUAiACQQM6ANACIAIgAEHjEWo2AqQCIAJBpAJqIQgMFAsgAEEA
OgB4IABBAToAfCAAQoAQNwJoIABBuN7BgAA2AmAgACAAQa0BajYCZCAAIABB4ABqNgJcDBMLIABBADoAeCAAQQE6AHwgAEKAEDcCaCAAQbjewYAANgJgIAAg
AEGtAWo2AmQgACAAQeAAajYCXAwSCyADQQQQiICAgAAACyAHQRgQiICAgAAACyAEQQQQiICAgAAACyACIARBP3E6ALcBIAJBtwFqEP2AgIAAIABBAToArAEM
DgsgAEEuOgCsAQwNCyAAQSw6AKwBDAwLIABBKzoArAEMCwsgAEEdOgCsAQwKCyACQQc6ALYBIAJBtgFqEP2AgIAAIABBAToArAEMCQsgAkESOgC1ASACQbUB
ahD9gICAACAAQQE6AKwBDAgLIAJBEDoAtAEgAkG0AWoQ/YCAgAAgAEEBOgCsAQwHCyACQR86ALMBIAJBswFqEP2AgIAAIABBAToArAEMBgsgAkEPOgCyASAC
QbIBahD9gICAACAAQQE6AKwBDAULIABBDjoArAEMBAsgAEENOgCsAQwDCyAAQQw6AKwBDAILIAAtAOgRIgRBF0sNASAAIARBAXRqIAAvAeAROwGwESAALQDo
ESEEAkAgAUH/AXFBOkcNACAEQf8BcUEYTw0EIAAgAC8BTCAAQc4AaiIDLQAAQRB0ckEBIARB/wFxdHIiBzsBTCADIAdBEHY6AAALIARB/wFxQQFqIgRB/wFx
IARHDQMgAEEAOgDpESAAQQA7AeARIAAgBDoA6BEMAQsQ/oCAgAALQdCbwoAAIQQCQCAALQDiEUEPcSAKQQ9xIgNGDQBB7JzCgAAhBAJAAkACQAJAAkACQCAD
QX9qDg0BBAEEBAQBBAQDBAAGBAsgAEHQAGoQrICAgAAMAQsgAEEAOwFMIABBADoA6REgAEEAOwHgESAAQQA7AOcRIABBzgBqQQA6AAALQdCbwoAAIQQMAwtB
0JvCgAAhBCAALQDoESIKQRdLDQIgAC0A6RFFDQEgACAKQQF0aiAALwHgETsBsBEgAC0A6BFBAWoiCkH/AXEgCkcNBCAAIAo6AOgRDAELQdCbwoAAIQQgCkEP
cUEOTw0DDAELAkAgAC0A5xEiBEEFSQ0AIARBBBCIgICAAAALAkAgCkH/AXEiCkEZTw0AIAIgAToA3AMgAiAKNgLYAyACIAQ2AtADIAIgAEHjEWo2AswDIAJB
BToAyAMgAkEBOgD8AyACQQU6APgDIAIgAEGwEWo2AtQDIAJBzANqIQQMAQsgCkEYEIiAgIAAAAsgAkGABGpBMGogBUEwaigCADYCACACQYAEakEoaiAFQShq
KQIANwMAIAJBgARqQSBqIAVBIGopAgA3AwAgAkGABGpBGGogBUEYaikCADcDACACQYAEakEQaiAFQRBqKQIANwMAIAJBgARqQQhqIAVBCGopAgA3AwAgAkG8
BGogCEEIaikCADcCACACQYAEakHEAGogCEEQaikCADcCACACQcwEaiAIQRhqKQIANwIAIAJB1ARqIAhBIGopAgA3AgAgAkHcBGogCEEoaikCADcCACACQeQE
aiAIQTBqKAIANgIAIAIgBSkCADcDgAQgAiAIKQIANwK0BCAAIAM6AOIRIAJBmAVqIARBMGooAgA2AgAgAkGQBWogBEEoaikCADcDACACQYgFaiAEQSBqKQIA
NwMAIAJBgAVqIARBGGopAgA3AwAgAkH4BGogBEEQaikCADcDACACQfAEaiAEQQhqKQIANwMAIAIgBCkCADcD6AQgAEEMaiEMQQAtAMvjwYAAIQ1BAC0AyOPB
gAAhDkEALQC/48GAACEPQQAtAMLjwYAAIRBBAC0AxePBgAAhEUEALQDO48GAACESIAJBqBRqQRVqIRMgAkHEBWpBFWohFCACQeQFakEVaiEVIAJBhAZqQRVq
IRYgAkGkBmpBFWohFyACQcQGakEVaiEYIAJB5AZqQRVqIRkgAkH8BmpBFWohGiACQZQHakEVaiEbIAJBrAdqQRVqIRwgAkHEB2pBFWohHSACQeQHakEVaiEe
IAJBhAhqQRVqIR8gAkGkCGpBFWohICACQcQIakEVaiEhIAJB5AhqQRVqISIgAkH8CGpBFWohIyACQZwJakEVaiEkIAJBvAlqQRVqISUgAkHcCWpBFWohJiAC
QfwJakEVaiEnIAJBnApqQRVqISggAkG0CmpBFWohKSACQdQKakEVaiEqIAJB+ApqQRVqISsgAkGcC2pBFWohLCACQbwLakEVaiEtIAJBiAxqQRVqIS4gAkGg
DGpBFWohLyACQfQMakEVaiEwIAJBxAxqQRVqITEgAkHcDGpBFWohMiACQaANakEVaiEzIAJByA1qQRVqITQgAkHgDWpBFWohNSACQcAOakEVaiE2IAJBiA5q
QRVqITcgAkGoDmpBFWohOCACQcAPakEVaiE5IAJB2A5qQRVqITogAkGoD2pBFWohOyACQZAPakEVaiE8IAJB+A5qQRVqIT0gAkGAEWpBFWohPiACQegQakEV
aiE/IAJB2A9qQRVqIUAgAkHwD2pBFWohQSACQYgQakEVaiFCIAJBoBBqQRVqIUMgAkG4EGpBFWohRCACQdAQakEVaiFFIAJBmBFqQRVqIUYgAkGYEmpBFWoh
RyACQYASakEVaiFIIAJBsBFqQRVqIUkgAkHMEWpBFWohSiACQeQRakEVaiFLIAJBsBJqQRVqIUwgAkHIEmpBFWohTSACQZATakEVaiFOIAJB+BJqQRVqIU8g
AkHgEmpBFWohUCACQagTakEVaiFRIAJByBNqQRVqIVIgAkHoE2pBFWohUyACQYgUakEVaiFUIAJB7BZqQRhqIVUgAkHsFmpBFWohVkEALQC448GAAEEBcSFX
QQAtALnjwYAAQQNxIVhBAC0Au+PBgABBAXEhWUEALQC848GAAEEDcSFaQQAtAL7jwYAAQQFxIVtBAC0AwePBgABBAXEhXEEALQDK48GAAEEBcSFdQQAtAMfj
wYAAQQFxIV5BAC0AxOPBgABBAXEhX0EALQDN48GAAEEBcSFgIAJB4BZqQQtqIWFBACEEA0ACQCACQYAEaiAEQTRsaiIBLQAwRQ0AIAEvARAgAUESai0AAEEQ
dHIhByABQRVqIQkgAS0AFCELIAEtABMhYiABKAIMIQogASgCCCEDIAEoAgQhCCABKAIAIQUCQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAEtACxBD3EOCwAHAQIDjAGMAYwBBAUGjwEL
IAwoAgAgBRC1gICAAAyLAQsgC0FAag4+Z2UPEGQREmNiEwYHGhsODhwODh0eDg4IJg4nDg4ODg5jKCkJLS5iLzAOZGUxCjUONjcLDGsNDg4ODg4ODmYOCyAD
QQh2IQEgA0H/AXFBUGoOT1xfX19fX19bYF9fX19LSl9fXV5fWllfX1hfX19fV1ZVX19fX19fVFNfX1JfiQFfX19fX19RX19fX19fX19fX1BPX19fX19fX19f
X19fTE1OXwsgVUEQaiABQRhqIgFBEGooAgA2AgAgViAJLwAAOwAAIFUgASkCADcCACACIAc7AfwWIAJB7BZqQRJqIAdBEHY6AAAgVkECaiAJQQJqLQAAOgAA
IFVBCGogAUEIaikCADcCACACIAs6AIAXIAIgYjoA/xYgAiAKNgL4FiACIAM2AvQWIAIgCDYC8BYgAiAFNgLsFiACLQCUFw4ZQD9HSIgBRj5FRENCQYgBiAGI
AYgBiAGIAYgBiAGIAYgBiAGIAYgBiwELIAAtAEhBA0YNigEgAEECOgBIDIcBCwJAIAAtAEgOBIoBhwEAigGKAQsgAEEBOgBIDIYBCwJAIAAtAEgiAUF/akEC
SQ0AIAEOBIkBiQGJAYkBiQELIABBADoASAyFAQsgBcBBAEgNgwECQAJAAkACQAJAAkACQAJAAkAgBUH/AHEOEI0BjQGNAQgIAAgBAgMHBwcEBQYICyAAEK+A
gIAADIwBCyAAKAIUIgFFDYsBIAwgARGDgICAAICAgIAADIsBCyAMKAIAQQEQsICAgAAMigELIABBARCxgICAAAyJAQsgDCgCABCygICAAAyIAQsgDCgCACgC
GCEBAkAgYA0AIAEgEkEDcToAuQIMiAELIAEgEkEDcToAugIMhwELIAwoAgAoAhhBADoAuQIMhgELIAwoAgAQs4CAgAAMhQELIAIgBToAwwUgAkHDBWoQtICA
gAAMhAELQZa5yIAAIQEgCA4CDw4NC0GWuciAACEBIAgOAhEQDwsgCA4CFhwXC0Hy3siAACEBIAgOAiIhIAsgCA4CKCknCyAIDgIsMi0LIAgOAmFfYAsgCA4C
XFpbCyATIAkvAAA7AAAgAiAHOwG4FCACQboUaiAHQRB2OgAAIBNBAmogCUECai0AADoAACACIAs6ALwUIAIgYjoAuxQgAiAKNgK0FCACIAM2ArAUIAIgCDYC
rBQgAiAFNgKoFCACQagUahD/gICAAAx7CyAIRQ14IAIgCDYC4AUgAiAFNgLcBSACQdwFahCAgYCAAAx6CyAIRQ12IAIgCDYCgAYgAiAFNgL8BSACQfwFahCB
gYCAAAx5CyAIRQ10IAIgCDYCoAYgAiAFNgKcBiACQZwGahCCgYCAAAx4CyAIRQ1yIAIgCDYCwAYgAiAFNgK8BiACQbwGahCDgYCAAAx3CyAIRQ1wIAIgCDYC
4AYgAiAFNgLcBiACQdwGahCEgYCAAAx2C0GauciAACEBDAELQZi5yIAAQZq5yIAAIAUtAABBP0YbIQELAkACQAJAAkACQAJAAkACQCABLQABRQ0AIAEtAAAh
ASAKDgIDAQILIBkgCS8AADsAACACIAc7AfQGIAJB9gZqIAdBEHY6AAAgGUECaiAJQQJqLQAAOgAAIAJBygA6APgGIAIgYjoA9wYgAiAKNgLwBiACIAM2AuwG
IAIgCDYC6AYgAiAFNgLkBiACQeQGahCFgYCAAAx6CwJAIAMvAQAiC0EESQ0AIAtBFkcNAQsgC0H/AXEOFwECAwR8fHx8fHx8fHx8fHx8fHx8fHwFfAsgGiAJ
LwAAOwAAIAIgBzsBjAcgAkGOB2ogB0EQdjoAACAaQQJqIAlBAmotAAA6AAAgAkHKADoAkAcgAiBiOgCPByACIAo2AogHIAIgAzYChAcgAiAINgKAByACIAU2
AvwGIAJB/AZqEIWBgIAADHgLIAwoAgBBACABEIaBgIAADHcLIAwoAgBBASABEIaBgIAADHYLIAwoAgBBAiABEIaBgIAADHULIAwoAgBBAyABEIaBgIAADHQL
IAwoAgBBFiABEIaBgIAADHMLQZq5yIAAIQEMAQtBmLnIgABBmrnIgAAgBS0AAEE/RhshAQsCQAJAAkACQAJAAkAgAS0AAUUNACABLQAAIQEgCg4CAwECCyAb
IAkvAAA7AAAgAiAHOwGkByACQaYHaiAHQRB2OgAAIBtBAmogCUECai0AADoAACACQcsAOgCoByACIGI6AKcHIAIgCjYCoAcgAiADNgKcByACIAg2ApgHIAIg
BTYClAcgAkGUB2oQh4GAgAAMdQsgAy8BACILQQJLDQAgC0H/AXEOAwECAwELIBwgCS8AADsAACACIAc7AbwHIAJBvgdqIAdBEHY6AAAgHEECaiAJQQJqLQAA
OgAAIAJBywA6AMAHIAIgYjoAvwcgAiAKNgK4ByACIAM2ArQHIAIgCDYCsAcgAiAFNgKsByACQawHahCHgYCAAAxzCyAMKAIAQQAgARCIgYCAAAxyCyAMKAIA
QQEgARCIgYCAAAxxCyAMKAIAQQIgARCIgYCAAAxwCyAIRQ1oIAIgCDYC4AcgAiAFNgLcByACQdwHahCJgYCAAAxvCyAIRQ1mIAIgCDYCgAggAiAFNgL8ByAC
QfwHahCKgYCAAAxuCyAIRQ1kIAIgCDYCoAggAiAFNgKcCCACQZwIahCLgYCAAAxtCyAIRQ1iIAIgCDYCwAggAiAFNgK8CCACQbwIahCMgYCAAAxsCyAIRQ1g
IAIgCDYC4AggAiAFNgLcCCACQdwIahCNgYCAAAxrCyAKDgICAV4LIAIgCDYCmAkgAiAFNgKUCSACQZQJahCOgYCAAAxpCyADLwEADgYAXAFcXAJcCyAMKAIA
ENCAgIAADGcLIAwoAgAQj4GAgAAMWgsgDCgCACIBQgA3AzggAUHwAGpCADcDACABQegAakIANwMAIAFB4ABqQgA3AwAgAUHYAGpCADcDACABQdAAakIANwMA
IAFByABqQgA3AwAgAUHAAGpCADcDACABKAI0IghFDVkgASgCMEEAIAj8CwAMWQsCQCAFLQAAQT9HDQAgCkEBRw0AIAMvAQBBBUcNACAMKAIAEJCBgIAADGUL
ICMgCS8AADsAACACIAc7AYwJIAJBjglqIAdBEHY6AAAgI0ECaiAJQQJqLQAAOgAAIAJB1wA6AJAJIAIgYjoAjwkgAiAKNgKICSACIAM2AoQJIAJBATYCgAkg
AiAFNgL8CCACQfwIahCRgYCAAAxkCyAIRQ1WIAIgCDYCuAkgAiAFNgK0CSACQbQJahCSgYCAAAxjCyAIRQ1UIAIgCDYC2AkgAiAFNgLUCSACQdQJahCTgYCA
AAxiCyAIRQ1SIAIgCDYC+AkgAiAFNgL0CSACQfQJahCUgYCAAAxhCyAIRQ1QIAIgCDYCmAogAiAFNgKUCiACQZQKahCVgYCAAAxgC0Gw+cmAACEBDAELQeze
yIAAQe7eyIAAQbD5yYAAIAUtAAAiAUE9RhsgAUE+RhshAQsCQCABLQABRQ0AIAAgAS0AABDfgICAAAxeCyAoIAkvAAA7AAAgAiAHOwGsCiACQa4KaiAHQRB2
OgAAIChBAmogCUECai0AADoAACACQeMAOgCwCiACIGI6AK8KIAIgCjYCqAogAiADNgKkCiACIAg2AqAKIAIgBTYCnAogAkGcCmoQloGAgAAMXQsgCEUNSyAC
IAg2AtAKIAIgBTYCzAogAkHMCmoQl4GAgAAMXAsgCEUNSSACIAg2AvAKIAIgBTYC7AogAkHsCmoQmIGAgAAMWwsCQCAIRQ0AIAIgCDYCmAsgAiAFNgKUCyAC
QZQLahCZgYCAAAxbCwJAIApBAUYNACArIAkvAAA7AAAgAiAHOwGICyACQYoLaiAHQRB2OgAAICtBAmogCUECai0AADoAACACQecAOgCMCyACIGI6AIsLIAIg
CjYChAsgAiADNgKACyACQQA2AvwKIAIgBTYC+AogAkH4CmoQmoGAgAAMWwsCQAJAAkACQCADLwEAIgFB/wFLDQAgAUH/AXEOBAEDAwIDCyACIAE7AfYKIAJB
9gpqEJuBgIAADF0LIAwoAgAQj4GAgAAMXAsgDCgCACIBQgA3AzggAUHwAGpCADcDACABQegAakIANwMAIAFB4ABqQgA3AwAgAUHYAGpCADcDACABQdAAakIA
NwMAIAFByABqQgA3AwAgAUHAAGpCADcDACABKAI0IgNFDVsgASgCMEEAIAP8CwAMWwsgAiABOgCTCyACQZMLahCcgYCAAAxaCyAIRQ1GAkAgCEEBRw0AIAUt
AABBP0YNRwsgLCAJLwAAOwAAIAIgBzsBrAsgAkGuC2ogB0EQdjoAACAsQQJqIAlBAmotAAA6AAAgAkHoADoAsAsgAiBiOgCvCyACIAo2AqgLIAIgAzYCpAsg
AiAINgKgCyACIAU2ApwLIAJBnAtqEJ2BgIAADFkLIAhFDUQCQCAIQQFHDQAgBS0AAEE/Rg1FCyAtIAkvAAA7AAAgAiAHOwHMCyACQc4LaiAHQRB2OgAAIC1B
AmogCUECai0AADoAACACQewAOgDQCyACIGI6AM8LIAIgCjYCyAsgAiADNgLECyACIAg2AsALIAIgBTYCvAsgAkG8C2oQnYGAgAAMWAsgAiAINgLADCACIAU2
ArwMIAJBvAxqEJ6BgIAADFcLIAIgBzsB5AsgAkHmC2ogB0EQdjoAACACQQA2AugLIAIgCjYC4AsgAiADNgLcCyACQewLaiACQdwLahCfgYCAACACLQCADEUN
VgNAIAAgAkHsC2oQoIGAgAAgAkHsC2ogAkHcC2oQn4GAgAAgAi0AgAwNAAxXCwsCQCAFLQAAIgFBPkcNAAJAIAoNACAMKAIAIgEgAS8B9BFB+/8DcTsB9BEM
VwsgAy8BACIBQQRLDQtBFyABQf8BcXZBAXFFDQsgAkGI4QAgAUEDbEH//wFxdkEHcToAhwwCQCAKQQJNDQAgLyAJLwAAOwAAIAIgBzsBsAwgAkGyDGogB0EQ
djoAACAvQQJqIAlBAmotAAA6AAAgAkHtADoAtAwgAiBiOgCzDCACIAo2AqwMIAIgAzYCqAwgAkEBNgKkDCACIAU2AqAMIAJBoAxqEKGBgIAADFcLIAItAIcM
IQEgCkECRw1BIAFBB3EiBUEFSw1ZIAVBA0cNQSADLwECQQJHDUFBBSEBIAJBBToAhwwMQQsgAiABOgC7DCACQbsMahCigYCAAAxVCwJAAkACQAJAAkACQCAI
RQ0AIAUtAAAiAUE/Rw0FIApBAUcNASAIQQFGDQIgMiAJLwAAOwAAIAIgBzsB7AwgAkHuDGogB0EQdjoAACAyQQJqIAlBAmotAAA6AAAgAkHuADoA8AwgAiBi
OgDvDCACQQE2AugMIAIgAzYC5AwgAiAINgLgDCACIAU2AtwMIAJB3AxqEJ2BgIAADFoLIApBAUYNAgsgMSAJLwAAOwAAIAIgBzsB1AwgAkHWDGogB0EQdjoA
ACAxQQJqIAlBAmotAAA6AAAgAkHuADoA2AwgAiBiOgDXDCACIAo2AtAMIAIgAzYCzAwgAiAINgLIDCACIAU2AsQMIAJBxAxqEKOBgIAADFgLQeSHAiEBIAMv
AQBB5AdHDQEMQQsgAy8BACIBQXtqQf//A3FBAkkNQAsgMCAJLwAAOwAAIAIgBzsBhA0gAkGGDWogB0EQdjoAACAwQQJqIAlBAmotAAA6AAAgAkHuADoAiA0g
AiBiOgCHDSACQQE2AoANIAIgAzYC/AwgAiAINgL4DCACIAU2AvQMIAJB9AxqEKOBgIAADFULAkAgCEEBRg0AIAIgCDYClA0gAiAFNgKQDSACQZANahCkgYCA
AAxVCwJAIAFBPkcNACAMKAIAIgEgAS8B9BFB+/8DcTsB9BEMVQsgAiABOgCPDSACQY8NahClgYCAAAxUCwJAIAhBAkYNACACIAg2AsANIAIgBTYCvA0gAkG8
DWoQpoGAgAAMVAsgBS0AAEE/Rw08IAUtAAFBJEcNPAJAIApBAUYNACAzIAkvAAA7AAAgAiAHOwGwDSACQbINaiAHQRB2OgAAIAJBAjYCpA0gM0ECaiAJQQJq
LQAAOgAAIAJB8AA6ALQNIAIgYjoAsw0gAiAKNgKsDSACIAM2AqgNIAIgBTYCoA0gAkGgDWoQp4GAgAAMVAsgAkG4DWogAy8BACIBQQAQqIGAgAACQCACLQC6
DUUNACAAIAIvAbgNEKmBgIAADFQLIAAgAUEAEKqBgIAADFMLAkAgCEEBRg0AIAIgCDYChA4gAiAFNgKADiACQYAOahCmgYCAAAxTCwJAAkACQAJAAkACQAJA
AkAgBS0AACIBQWBqDgMAAwECC0EAIQEgCg4CQAQDC0Hy3siAACEBIAoOAj4FBAsgAUE+Rg0/CyACQQE2AvwNIAIgBTYC+A0gAkH4DWoQq4GAgAAMVgsgNCAJ
LwAAOwAAIAIgBzsB2A0gAkHaDWogB0EQdjoAACA0QQJqIAlBAmotAAA6AAAgAkHxADoA3A0gAiBiOgDbDSACIAo2AtQNIAIgAzYC0A0gAkEBNgLMDSACIAU2
AsgNIAJByA1qEKyBgIAADFULIAMvAQAiAUEHSQ07IAIgATsBxg0gAkHGDWoQrYGAgAAMVAtBsPnJgAAhAQw5CyADLwEAIgFBA0kNN0Gw+cmAACEBDDgLIAoO
AwEDBAILIDYgCS8AADsAACACIAc7AdAOIAJB0g5qIAdBEHY6AAAgNkECaiAJQQJqLQAAOgAAIAJB8gA6ANQOIAIgYjoA0w4gAiAKNgLMDiACIAM2AsgOIAIg
CDYCxA4gAiAFNgLADiACQcAOahCugYCAAAxQCyAMKAIAQQBBABCvgYCAAAxPCyA3IAkvAAA7AAAgAiAHOwGYDiACQZoOaiAHQRB2OgAAIDdBAmogCUECai0A
ADoAACACQfIAOgCcDiACIGI6AJsOIAIgCjYClA4gAiADNgKQDiACQQA2AowOIAIgBTYCiA4gAkGIDmoQsIGAgAAMTgsgDCgCACADLwEAQQAQr4GAgAAMTQsg
DCgCACADLwEAIAMvAQIQr4GAgAAMTAsCQCAFLQAAQT9HDQAgCkUNTANAIAJBog5qIAMvAQAiAUEAEKiBgIAAAkACQCACLQCkDkUNACAAIAIvAaIOELGBgIAA
DAELIAIgATsBpg4gAkGmDmoQsoGAgAALIANBAmohAyAKQX9qIgoNAAxNCwsgOCAJLwAAOwAAIAIgBzsBuA4gAkG6DmogB0EQdjoAACA4QQJqIAlBAmotAAA6
AAAgAkHyADoAvA4gAiBiOgC7DiACIAo2ArQOIAIgAzYCsA4gAkEBNgKsDiACIAU2AqgOIAJBqA5qELOBgIAADEsLIC4gCS8AADsAACACIAc7AZgMIAJBmgxq
IAdBEHY6AAAgLkECaiAJQQJqLQAAOgAAIAJB7QA6AJwMIAIgYjoAmwwgAiAKNgKUDCACIAM2ApAMIAJBATYCjAwgAiAFNgKIDCACQYgMahChgYCAAAxKCyAC
QaAXaiAFIAgQtIGAgAACQCACLQCkF0UNACAMKAIAIAIoAqAXNgLwEQxKCyACIAg2AqwXIAIgBTYCqBcgAkGoF2oQtYGAgAAMSQsCQCAFIAgQtoGAgABBAXEN
ABC3gYCAAAxJCyAAIAUgCBC4gYCAAAxICxC5gYCAAAxHCyAMKAIAQRhqKAIAELqBgIAADEYLIAIgCDYC5BcgAiAFNgLgFyACIAo2AtwXIAIgAzYC2BcgDCgC
AEEYaigCACACQdgXahC7gYCAAAxFCyAAKAIwIgFFDUQgDCAFIAggAyAKIAERgYCAgACAgICAAAxECyAAIAUgCBC8gYCAAAxDCyACQbAXakEIaiACQewWakEI
aikCADcDACACQbAXakEQaiACQewWakEQaikCADcDACACQbAXakEYaiBVKQIANwMAIAJBsBdqQSBqIAJB7BZqQSBqKAIANgIAIAIgAikC7BY3A7AXIAIgAi0A
kBdBH3E6ANQXIAIgAi0AkRdBAXE6ANUXIAAgAkGwF2oQvYGAgAAMQgsgACAFIAgQvoGAgAAMQQsgAiAINgKcFyACIAU2ApgXIAJBmBdqEL+BgIAADEALIAAg
AkHsFmoQwIGAgAAMPwsCQCAIRQ0AIAIgATsA3RYgAkHUFmpBC2ogAUEQdjoAACACQT46ANwWIAIgCDYC2BYgAiAFNgLUFiACQdQWahDxgICAAAw/CyAAQcIA
EPOAgIAADD4LAkAgCEUNACACIAE7ANEWIAJByBZqQQtqIAFBEHY6AAAgAkE9OgDQFiACIAg2AswWIAIgBTYCyBYgAkHIFmoQ8YCAgAAMPgsgAEHCABDugICA
AAw9CwJAIAhFDQAgAiABOwDFFiACQbwWakELaiABQRB2OgAAIAJB/AA6AMQWIAIgCDYCwBYgAiAFNgK8FiACQbwWahDsgICAAAw9CyAMKAIAKAIYIQECQCBd
DQAgASANQQNxOgC5Agw9CyABIA1BA3E6ALoCDDwLAkAgCEUNACACIAE7ALkWIAJBsBZqQQtqIAFBEHY6AAAgAkH9ADoAuBYgAiAINgK0FiACIAU2ArAWIAJB
sBZqEOqAgIAADDwLIAwoAgAoAhghAQJAIF4NACABIA5BA3E6ALkCDDwLIAEgDkEDcToAugIMOwsCQCAIRQ0AIAIgATsArRYgAkGkFmpBC2ogAUEQdjoAACAC
Qf4AOgCsFiACIAg2AqgWIAIgBTYCpBYgAkGkFmoQ6ICAgAAMOwsgDCgCACgCGCEBAkAgXw0AIAEgEUEDcToAuQIMOwsgASARQQNxOgC6Agw6CwJAIAhFDQAg
AiABOwChFiACQZgWakELaiABQRB2OgAAIAJB7wA6AKAWIAIgCDYCnBYgAiAFNgKYFiACQZgWahDbgICAAAw6CyAMKAIAKAIYIQECQCBcDQAgASAQQQNxOgC5
Agw6CyABIBBBA3E6ALoCDDkLAkAgCEUNACACIAE7AJUWIAJBjBZqQQtqIAFBEHY6AAAgAkHuADoAlBYgAiAINgKQFiACIAU2AowWIAJBjBZqENmAgIAADDkL
IAwoAgAoAhghAQJAIFsNACABIA9BA3E6ALkCDDkLIAEgD0EDcToAugIMOAsCQCAIRQ0AIAIgATsAiRYgAkGAFmpBC2ogAUEQdjoAACACQeMAOgCIFiACIAg2
AoQWIAIgBTYCgBYgAkGAFmoQ5oCAgAAMOAsgDCgCABDigICAAAw3CwJAIAhFDQAgAiABOwD9FSACQfQVakELaiABQRB2OgAAIAJB2gA6APwVIAIgCDYC+BUg
AiAFNgL0FSACQfQVahDdgICAAAw3CyAAQQAQ34CAgAAMNgsCQCAIRQ0AIAIgATsA8RUgAkHoFWpBC2ogAUEQdjoAACACQdcAOgDwFSACIAg2AuwVIAIgBTYC
6BUgAkHoFWoQ3YCAgAAMNgsgDCgCAEEYaigCAEEAOgDaAQw1CwJAIAhFDQAgAiABOwDlFSACQdwVakELaiABQRB2OgAAIAJB1gA6AOQVIAIgCDYC4BUgAiAF
NgLcFSACQdwVahDdgICAAAw1CyAMKAIAIgEoAhhBAToA2gEgASgCGEEBOgC9Agw0CwJAIAhFDQAgAiABOwDZFSACQdAVakELaiABQRB2OgAAIAJBzwA6ANgV
IAIgCDYC1BUgAiAFNgLQFSACQdAVahDbgICAAAw0CyBZDTYgDCgCACgCGCIBQQE6ALwCIAEgWjoAuwIgAiBaOgDvFwwzCwJAIAhFDQAgAiABOwDNFSACQcQV
akELaiABQRB2OgAAIAJBzgA6AMwVIAIgCDYCyBUgAiAFNgLEFSACQcQVahDZgICAAAwzCyBXDTUgDCgCACgCGCIBQQE6ALwCIAEgWDoAuwIgAiBYOgDvFwwy
CwJAIAhFDQAgAiABOwDBFSACQbgVakELaiABQRB2OgAAIAJBzQA6AMAVIAIgCDYCvBUgAiAFNgK4FSACQbgVahDXgICAAAwyCyAMKAIAENOAgIAADDELAkAg
CEUNACACIAE7ALUVIAJBrBVqQQtqIAFBEHY6AAAgAkHIADoAtBUgAiAINgKwFSACIAU2AqwVIAJBrBVqENGAgIAADDELIAwoAgAQ0ICAgAAMMAsCQCAIRQ0A
IAIgATsAqRUgAkGgFWpBC2ogAUEQdjoAACACQcUAOgCoFSACIAg2AqQVIAIgBTYCoBUgAkGgFWoQzoCAgAAMMAsgABDMgICAAAwvCwJAIAhFDQAgAiABOwCd
FSACQZQVakELaiABQRB2OgAAIAJBxAA6AJwVIAIgCDYCmBUgAiAFNgKUFSACQZQVahDKgICAAAwvCyAMKAIAEMeAgIAADC4LAkAgCEUNACACIAE7AIUVIAJB
/BRqQQtqIAFBEHY6AAAgAkE3OgCEFSACIAg2AoAVIAIgBTYC/BQgAkH8FGoQvYCAgAAMLgsgDCgCABC8gICAAAwtCwJAIAhBAUYNACACIAg2AuwUIAIgBTYC
6BQgAkHoFGoQuoCAgAAMLQsCQCAFLQAAIgFB/AFxQShHDQAgDCgCAEEYaigCACEDIAIgAUEDcSIBOgD7FCADIAFBAnNBAnRBvP7JgABqKAIAakEDOgAAIAJB
AzoA+hQMLQsgAkEBNgL0FCACIAU2AvAUIAJB8BRqELqAgIAADCwLAkAgCEEBRg0AIAIgCDYC2BQgAiAFNgLUFCACQdQUahC6gICAAAwsCwJAIAUtAAAiAUH8
AXFBKEcNACAMKAIAQRhqKAIAIQMgAiABQQNxIgE6AOcUIAMgAUECc0ECdEG8/smAAGooAgBqQQI6AAAgAkECOgDmFAwsCyACQQE2AuAUIAIgBTYC3BQgAkHc
FGoQuoCAgAAMKwsCQCAIQQFGDQAgAiAINgLEFCACIAU2AsAUIAJBwBRqELqAgIAADCsLAkAgBS0AACIBQfwBcUEoRw0AIAwoAgBBGGooAgAhAyACIAFBA3Ei
AToA0xQgAyABQQJzQQJ0Qbz+yYAAaigCAGpBAToAACACQQE6ANIUDCsLIAJBATYCzBQgAiAFNgLIFCACQcgUahC6gICAAAwqCyACIAE7AOkWIGEgAUEQdjoA
ACACIAM6AOgWIAIgCDYC5BYgAiAFNgLgFiACQeAWahDFgICAAAwpCwJAAkACQCAIDgIAAQILIAwoAgAQwYGAgAAMKgsgBS0AAEEjRw0AIAwoAgAQv4CAgAAM
KQsgAiABOwCRFSACQYgVakELaiABQRB2OgAAIAJBODoAkBUgAiAINgKMFSACIAU2AogVIAJBiBVqEMWAgIAADCgLAkAgCEUNACACIAg2AqQUIAIgBTYCoBQg
AkGgFGoQwoGAgAAMKAtBASEIQQEhAQJAAkACQAJAIAoOAwMCAQALIFQgCS8AADsAACACIAc7AZgUIAJBmhRqIAdBEHY6AAAgVEECaiAJQQJqLQAAOgAAIAIg
CzoAnBQgAiBiOgCbFCACIAo2ApQUIAIgAzYCkBQgAkEANgKMFCACIAU2AogUIAJBiBRqEMOBgIAADCoLIAMvAQIhCAsgAy8BACEBCyAMKAIAIAFB//8DcSAI
EMGAgIAADCcLAkAgCEUNACACIAg2AoQUIAIgBTYCgBQgAkGAFGoQxIGAgAAMJwtBASEBAkACQAJAIAoOAgEAAgsgAy8BACEBCyAMKAIAIgUoAhgvAdIBQQFq
IgNB//8DcSIIIANHDSogBSAIIAEQwYCAgAAMJwsgUyAJLwAAOwAAIAIgBzsB+BMgAkH6E2ogB0EQdjoAACBTQQJqIAlBAmotAAA6AAAgAiALOgD8EyACIGI6
APsTIAIgCjYC9BMgAiADNgLwEyACQQA2AuwTIAIgBTYC6BMgAkHoE2oQxYGAgAAMJgsCQCAIRQ0AIAIgCDYC5BMgAiAFNgLgEyACQeATahDGgYCAAAwmC0EB
IQECQAJAAkAgCg4CAgEACyBSIAkvAAA7AAAgAiAHOwHYEyACQdoTaiAHQRB2OgAAIFJBAmogCUECai0AADoAACACIAs6ANwTIAIgYjoA2xMgAiAKNgLUEyAC
IAM2AtATIAJBADYCzBMgAiAFNgLIEyACQcgTahDHgYCAAAwnCyADLwEAIQELIAwoAgAgARCwgICAAAwlCwJAIAhFDQAgAiAINgLEEyACIAU2AsATIAJBwBNq
EMiBgIAADCULQQEhAQJAAkACQCAKDgICAQALIFEgCS8AADsAACACIAc7AbgTIAJBuhNqIAdBEHY6AAAgUUECaiAJQQJqLQAAOgAAIAIgCzoAvBMgAiBiOgC7
EyACIAo2ArQTIAIgAzYCsBMgAkEANgKsEyACIAU2AqgTIAJBqBNqEMmBgIAADCYLIAMvAQAhAQsgDCgCACABENSAgIAADCQLAkACQCAIQQFHDQAgBS0AAEEk
Rg0BCyBQIAkvAAA7AAAgAiAHOwHwEiACQfISaiAHQRB2OgAAIFBBAmogCUECai0AADoAACACQf0AOgD0EiACIGI6APMSIAIgCjYC7BIgAiADNgLoEiACIAg2
AuQSIAIgBTYC4BIgAkHgEmoQyoGAgAAMJAsCQCAKQQFGDQAgTyAJLwAAOwAAIAIgBzsBiBMgAkGKE2ogB0EQdjoAACBPQQJqIAlBAmotAAA6AAAgAkH9ADoA
jBMgAiBiOgCLEyACIAo2AoQTIAIgAzYCgBMgAkEBNgL8EiACIAU2AvgSIAJB+BJqEMqBgIAADCQLAkACQAJAIAMvAQAOAgABAgsgDCgCAEEAOgCEEgwlCyAM
KAIAQQE6AIQSDCQLIE4gCS8AADsAACACIAc7AaATIAJBohNqIAdBEHY6AAAgTkECaiAJQQJqLQAAOgAAIAJB/QA6AKQTIAIgYjoAoxMgAkEBNgKcEyACIAM2
ApgTIAJBATYClBMgAiAFNgKQEyACQZATahDKgYCAAAwjCwJAIAhFDQAgTSAJLwAAOwAAIAIgBzsB2BIgAkHaEmogB0EQdjoAACBNQQJqIAlBAmotAAA6AAAg
AkHAADoA3BIgAiBiOgDbEiACIAo2AtQSIAIgAzYC0BIgAiAINgLMEiACIAU2AsgSIAJByBJqEMuBgIAADCMLQQEhAQJAAkACQCAKDgICAQALIEwgCS8AADsA
ACACIAc7AcASIAJBwhJqIAdBEHY6AAAgTEECaiAJQQJqLQAAOgAAIAJBwAA6AMQSIAIgYjoAwxIgAiAKNgK8EiACIAM2ArgSIAJBADYCtBIgAiAFNgKwEiAC
QbASahDMgYCAAAwkCyADLwEAIgFBASABQQFLGyEBCyAMKAIAIAEQzYGAgAAMIgsCQAJAAkACQAJAAkACQAJAIAUtAABBRGoOBAQBAwACCyAAEM6BgIAADCgL
IAoNA0EAIQEMBQsgSCAJLwAAOwAAIAIgBzsBkBIgAkGSEmogB0EQdjoAACBIQQJqIAlBAmotAAA6AAAgAkH1ADoAlBIgAiBiOgCTEiACIAo2AowSIAIgAzYC
iBIgAkEBNgKEEiACIAU2AoASIAJBgBJqELOBgIAADCYLQQAhASAKQQFHDQICQCADLwEAIgFBH0sNACABQR9xIQEMAwsgSSAJLwAAOwAAIAIgBzsBwBEgAkHC
EWogB0EQdjoAACBJQQJqIAlBAmotAAA6AAAgAkH1ADoAxBEgAiBiOgDDESACQQE2ArwRIAIgAzYCuBEgAkEBNgK0ESACIAU2ArARIAJBsBFqEM+BgIAADCUL
AkAgCkEBRg0AIABBARDQgYCAAAwlCyAAIAMvAQAQ0IGAgAAMJAsCQCADLwEAIgFBIEkNACBKIAkvAAA7AAAgAiAHOwHcESACQd4RaiAHQRB2OgAAIEpBAmog
CUECai0AADoAACACQfUAOgDgESACIGI6AN8RIAIgCjYC2BEgAiADNgLUESACQQE2AtARIAIgBTYCzBEgAkHMEWoQ0YGAgAAMJAsgAUEfcSEBIApBAUYNAQJA
AkACQCADLwECQX9qDgMEAQIACyBLIAkvAAA7AAAgAiAHOwH0ESACQfYRaiAHQRB2OgAAIEtBAmogCUECai0AADoAACACQfUAOgD4ESACIGI6APcRIAIgCjYC
8BEgAiADNgLsESACQQE2AugRIAIgBTYC5BEgAkHkEWoQ0YGAgAAMJQsgAiABOgD/ESAAIAEQ0oGAgAAMJAsgAiABOgD/ESAAIAEQ04GAgAAMIwsgAEEMaigC
ACgCGCIDIAMtAMYCQQFqQQdxIgU6AMYCIAMgBWogAToAvgIgAiABOgDLESAAKAI8IgFFDSIgDCABEYOAgIAAgICAgAAMIgsgAEEMaigCACgCGCIDIAMtAMYC
QQdxaiABOgC+AiACIAE6AP8RIAAoAjwiAUUNISAMIAERg4CAgACAgICAAAwhCyBHIAkvAAA7AAAgAiAHOwGoEiACQaoSaiAHQRB2OgAAIEdBAmogCUECai0A
ADoAACACQfUAOgCsEiACIGI6AKsSIAIgCjYCpBIgAiADNgKgEiACIAg2ApwSIAIgBTYCmBIgAkGYEmoQ1IGAgAAMIAsgDCgCABDBgYCAAAwfCwJAIAhFDQAg
RiAJLwAAOwAAIAIgBzsBqBEgAkGqEWogB0EQdjoAACBGQQJqIAlBAmotAAA6AAAgAkH0ADoArBEgAiBiOgCrESACIAo2AqQRIAIgAzYCoBEgAiAINgKcESAC
IAU2ApgRIAJBmBFqENWBgIAADB8LAkAgCkUNAAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAMvAQBBcmoOCgEAAgADAAAEBQYACyA/IAkvAAA7AAAgAiAH
OwH4ECACQfoQaiAHQRB2OgAAID9BAmogCUECai0AADoAACACQfQAOgD8ECACIGI6APsQIAIgCjYC9BAgAiADNgLwECACQQA2AuwQIAIgBTYC6BAgAkHoEGoQ
1oGAgAAMKwsgCkEBRg0KIEAgCS8AADsAACACIAc7AegPIAJB6g9qIAdBEHY6AAAgQEECaiAJQQJqLQAAOgAAIAJB9AA6AOwPIAIgYjoA6w8gAiAKNgLkDyAC
IAM2AuAPIAJBADYC3A8gAiAFNgLYDyACQdgPahDXgYCAAAwqCyAKQQFGDQggQSAJLwAAOwAAIAIgBzsBgBAgAkGCEGogB0EQdjoAACBBQQJqIAlBAmotAAA6
AAAgAkH0ADoAhBAgAiBiOgCDECACIAo2AvwPIAIgAzYC+A8gAkEANgL0DyACIAU2AvAPIAJB8A9qENiBgIAADCkLIApBAUYNBiBCIAkvAAA7AAAgAiAHOwGY
ECACQZoQaiAHQRB2OgAAIEJBAmogCUECai0AADoAACACQfQAOgCcECACIGI6AJsQIAIgCjYClBAgAiADNgKQECACQQA2AowQIAIgBTYCiBAgAkGIEGoQ2YGA
gAAMKAsgCkEBRg0EIEMgCS8AADsAACACIAc7AbAQIAJBshBqIAdBEHY6AAAgQ0ECaiAJQQJqLQAAOgAAIAJB9AA6ALQQIAIgYjoAsxAgAiAKNgKsECACIAM2
AqgQIAJBADYCpBAgAiAFNgKgECACQaAQahDagYCAAAwnCyAKQX5xQQJGDQEMAgsCQCAKQX5xQQJHDQAgAy8BAg4DJgAmAAsgRSAJLwAAOwAAIAIgBzsB4BAg
AkHiEGogB0EQdjoAACBFQQJqIAlBAmotAAA6AAAgAkH0ADoA5BAgAiBiOgDjECACIAo2AtwQIAIgAzYC2BAgAkEANgLUECACIAU2AtAQIAJB0BBqENuBgIAA
DCULIAMvAQIOAyQAJAALIEQgCS8AADsAACACIAc7AcgQIAJByhBqIAdBEHY6AAAgREECaiAJQQJqLQAAOgAAIAJB9AA6AMwQIAIgYjoAyxAgAiAKNgLEECAC
IAM2AsAQIAJBADYCvBAgAiAFNgK4ECACQbgQahDbgYCAAAwjCyAAQQMQ3IGAgAAMIgsgAEECENyBgIAADCELIABBARDcgYCAAAwgCyAAQQAQ3IGAgAAMHwsg
PiAJLwAAOwAAIAIgBzsBkBEgAkGSEWogB0EQdjoAACA+QQJqIAlBAmotAAA6AAAgAkH0ADoAlBEgAiBiOgCTESACQQA2AowRIAIgAzYCiBEgAkEANgKEESAC
IAU2AoARIAJBgBFqEN2BgIAADB4LAkACQAJAAkACQAJAAkAgBS0AAEFCag4CAAIBC0GABCEBIAoOAgUCAwsgOyAJLwAAOwAAIAIgBzsBuA8gAkG6D2ogB0EQ
djoAACA7QQJqIAlBAmotAAA6AAAgAkHzADoAvA8gAiBiOgC7DyACIAo2ArQPIAIgAzYCsA8gAkEBNgKsDyACIAU2AqgPIAJBqA9qELOBgIAADCILIApFDSED
QCACQfIOaiADLwEAIgFBABCogYCAAAJAAkAgAi0A9A5FDQAgDCgCACACLwHyDhDegYCAAAwBCyACIAE7AfYOIAJB9g5qEN+BgIAACyADQQJqIQMgCkF/aiIK
DQAMIgsLAkAgAy8BAA4CAwIACyA9IAkvAAA7AAAgAiAHOwGIDyACQYoPaiAHQRB2OgAAID1BAmogCUECai0AADoAACACQfMAOgCMDyACIGI6AIsPIAJBATYC
hA8gAiADNgKADyACQQE2AvwOIAIgBTYC+A4gAkH4DmoQ4IGAgAAMIAsgPCAJLwAAOwAAIAIgBzsBoA8gAkGiD2ogB0EQdjoAACA8QQJqIAlBAmotAAA6AAAg
AkHzADoApA8gAiBiOgCjDyACIAo2ApwPIAIgAzYCmA8gAkEBNgKUDyACIAU2ApAPIAJBkA9qEOCBgIAADB8LQYAIIQELIAwoAgAiAyADLwH0EUH/8wNxIAFy
OwH0EQwdCyA5IAkvAAA7AAAgAiAHOwHQDyACQdIPaiAHQRB2OgAAIDlBAmogCUECai0AADoAACACQfMAOgDUDyACIGI6ANMPIAIgCjYCzA8gAiADNgLIDyAC
IAg2AsQPIAIgBTYCwA8gAkHAD2oQroGAgAAMHAsCQAJAAkACQCAKDgMAAgMBCyAMKAIAEOGBgIAADB4LIDogCS8AADsAACACIAc7AegOIAJB6g5qIAdBEHY6
AAAgOkECaiAJQQJqLQAAOgAAIAJB8wA6AOwOIAIgYjoA6w4gAiAKNgLkDiACIAM2AuAOIAJBADYC3A4gAiAFNgLYDiACQdgOahDigYCAAAwdCyAMKAIAIAMv
AQBBABDjgYCAAAwcCyAMKAIAIAMvAQAgAy8BAhDjgYCAAAwbCyABQQJ0QbD+yYAAaigCACEBCwJAAkACQAJAIAEtAAFFDQAgAS0AAEEDcQ4EAQIDIAELIDUg
CS8AADsAACACIAc7AfANIAJB8g1qIAdBEHY6AAAgNUECaiAJQQJqLQAAOgAAIAJB8QA6APQNIAIgYjoA8w0gAiAKNgLsDSACIAM2AugNIAJBATYC5A0gAiAF
NgLgDSACQeANahDkgYCAAAwcCyAMKAIAQRhqKAIAQQA6ANoBDBsLIAwoAgAiASgCGEEBOgDaASABKAIYQQE6AL0CDBoLIAwoAgAiASgCGEEBOgDaASABKAIY
QQI6AL0CDBkLIAAgARDlgYCAAAwYCyAAEOaBgIAADBcLIAJBAjYCnA0gAiAFNgKYDSACQZgNahCmgYCAAAwWCyAAIAEQ54GAgAAMFQsgDCgCACIDIAMvAfQR
Qfv/A3E7AfQRAkAgAUEHcSIBQQVHDQAgDCgCACIBIAEvAfQRQQRyOwH0EQwVCyABQQZPDRcMFAsgCkUNEwNAIAJB1gtqIAMvAQAiASAIRRCogYCAAAJAAkAg
Ai0A2AsNACACIAE7AdoLIAJB2gtqEOiBgIAADAELIAAgAi8B1gsQ84CAgAALIANBAmohAyAKQX9qIgpFDRQMAAsLIApFDRIDQCACQbYLaiADLwEAIgEgCEUQ
qIGAgAACQAJAIAItALgLDQAgAiABOwG6CyACQboLahDogYCAAAwBCyAAIAIvAbYLEO6AgIAACyADQQJqIQMgCkF/aiIKRQ0TDAALC0EBIQECQAJAAkAgCg4C
AgEACyAqIAkvAAA7AAAgAiAHOwHkCiACQeYKaiAHQRB2OgAAICpBAmogCUECai0AADoAACACQeUAOgDoCiACIGI6AOcKIAIgCjYC4AogAiADNgLcCiACQQA2
AtgKIAIgBTYC1AogAkHUCmoQ6YGAgAAMEwsgAy8BACEBCyAMKAIAIAEQ6oGAgAAMEQtBASEBAkACQAJAIAoOAgEAAgsgAy8BACEBCyAMKAIAIgUoAhgvAdAB
QQFqIgNB//8DcSIIIANHDRQgBSABIAgQwYCAgAAMEQsgKSAJLwAAOwAAIAIgBzsBxAogAkHGCmogB0EQdjoAACApQQJqIAlBAmotAAA6AAAgAkHkADoAyAog
AiBiOgDHCiACIAo2AsAKIAIgAzYCvAogAkEANgK4CiACIAU2ArQKIAJBtApqEOuBgIAADBALQQEhAQJAAkACQCAKDgICAQALICcgCS8AADsAACACIAc7AYwK
IAJBjgpqIAdBEHY6AAAgJ0ECaiAJQQJqLQAAOgAAIAJB4gA6AJAKIAIgYjoAjwogAiAKNgKICiACIAM2AoQKIAJBADYCgAogAiAFNgL8CSACQfwJahDsgYCA
AAwRCyADLwEAIQELIAwoAgAgARDtgYCAAAwPC0EBIQECQAJAAkAgCg4CAgEACyAmIAkvAAA7AAAgAiAHOwHsCSACQe4JaiAHQRB2OgAAICZBAmogCUECai0A
ADoAACACQeEAOgDwCSACIGI6AO8JIAIgCjYC6AkgAiADNgLkCSACQQA2AuAJIAIgBTYC3AkgAkHcCWoQ7oGAgAAMEAsgAy8BACEBCyAMKAIAIAEQ74GAgAAM
DgtBASEBAkACQAJAIAoOAgIBAAsgJSAJLwAAOwAAIAIgBzsBzAkgAkHOCWogB0EQdjoAACAlQQJqIAlBAmotAAA6AAAgAkHaADoA0AkgAiBiOgDPCSACIAo2
AsgJIAIgAzYCxAkgAkEANgLACSACIAU2ArwJIAJBvAlqEPCBgIAADA8LIAMvAQAhAQsgACABEPGBgIAADA0LQQEhAQJAAkACQCAKDgICAQALICQgCS8AADsA
ACACIAc7AawJIAJBrglqIAdBEHY6AAAgJEECaiAJQQJqLQAAOgAAIAJB2AA6ALAJIAIgYjoArwkgAiAKNgKoCSACIAM2AqQJIAJBADYCoAkgAiAFNgKcCSAC
QZwJahDygYCAAAwOCyADLwEAIQELIAwoAgAgARDzgYCAAAwMCyAiIAkvAAA7AAAgAiAHOwH0CCACQfYIaiAHQRB2OgAAICJBAmogCUECai0AADoAACACQdcA
OgD4CCACIGI6APcIIAIgCjYC8AggAiADNgLsCCACQQA2AugIIAIgBTYC5AggAkHkCGoQkYGAgAAMCwtBASEBAkACQAJAIAoOAgIBAAsgISAJLwAAOwAAIAIg
BzsB1AggAkHWCGogB0EQdjoAACAhQQJqIAlBAmotAAA6AAAgAkHUADoA2AggAiBiOgDXCCACIAo2AtAIIAIgAzYCzAggAkEANgLICCACIAU2AsQIIAJBxAhq
EPSBgIAADAwLIAMvAQAhAQsgDCgCACABEPWBgIAADAoLQQEhAQJAAkACQCAKDgICAQALICAgCS8AADsAACACIAc7AbQIIAJBtghqIAdBEHY6AAAgIEECaiAJ
QQJqLQAAOgAAIAJB0wA6ALgIIAIgYjoAtwggAiAKNgKwCCACIAM2AqwIIAJBADYCqAggAiAFNgKkCCACQaQIahD2gYCAAAwLCyADLwEAIQELIAwoAgAgARD3
gYCAAAwJC0EBIQECQAJAAkAgCg4CAgEACyAfIAkvAAA7AAAgAiAHOwGUCCACQZYIaiAHQRB2OgAAIB9BAmogCUECai0AADoAACACQdAAOgCYCCACIGI6AJcI
IAIgCjYCkAggAiADNgKMCCACQQA2AogIIAIgBTYChAggAkGECGoQ+IGAgAAMCgsgAy8BACEBCyAMKAIAIAEQ+YGAgAAMCAtBASEBAkACQAJAIAoOAgIBAAsg
HiAJLwAAOwAAIAIgBzsB9AcgAkH2B2ogB0EQdjoAACAeQQJqIAlBAmotAAA6AAAgAkHNADoA+AcgAiBiOgD3ByACIAo2AvAHIAIgAzYC7AcgAkEANgLoByAC
IAU2AuQHIAJB5AdqEPqBgIAADAkLIAMvAQAhAQsgDCgCACABEPuBgIAADAcLQQEhAQJAAkACQCAKDgICAQALIB0gCS8AADsAACACIAc7AdQHIAJB1gdqIAdB
EHY6AAAgHUECaiAJQQJqLQAAOgAAIAJBzAA6ANgHIAIgYjoA1wcgAiAKNgLQByACIAM2AswHIAJBADYCyAcgAiAFNgLEByACQcQHahD8gYCAAAwICyADLwEA
IQELIAwoAgAgARDWgICAAAwGC0EBIQECQAJAAkAgCg4CAgEACyAYIAkvAAA7AAAgAiAHOwHUBiACQdYGaiAHQRB2OgAAIBhBAmogCUECai0AADoAACACQckA
OgDYBiACIGI6ANcGIAIgCjYC0AYgAiADNgLMBiACQQA2AsgGIAIgBTYCxAYgAkHEBmoQ/YGAgAAMBwsgAy8BACEBCyAAIAEQsYCAgAAMBQtBASEBAkACQAJA
IAoOAgIBAAsgFyAJLwAAOwAAIAIgBzsBtAYgAkG2BmogB0EQdjoAACAXQQJqIAlBAmotAAA6AAAgAkHGADoAuAYgAiBiOgC3BiACIAo2ArAGIAIgAzYCrAYg
AkEANgKoBiACIAU2AqQGIAJBpAZqEP6BgIAADAYLIAMvAQAhAQsgDCgCACABENSAgIAAIAwoAgAQsoCAgAAMBAtBASEBAkACQAJAIAoOAgIBAAsgFiAJLwAA
OwAAIAIgBzsBlAYgAkGWBmogB0EQdjoAACAWQQJqIAlBAmotAAA6AAAgAkHFADoAmAYgAiBiOgCXBiACIAo2ApAGIAIgAzYCjAYgAkEANgKIBiACIAU2AoQG
IAJBhAZqEMmBgIAADAULIAMvAQAhAQsgDCgCACABEP+BgIAAIAwoAgAQsoCAgAAMAwtBASEBAkACQAJAIAoOAgIBAAsgFSAJLwAAOwAAIAIgBzsB9AUgAkH2
BWogB0EQdjoAACAVQQJqIAlBAmotAAA6AAAgAkHDADoA+AUgAiBiOgD3BSACIAo2AvAFIAIgAzYC7AUgAkEANgLoBSACIAU2AuQFIAJB5AVqEICCgIAADAQL
IAMvAQAhAQsgDCgCACABEIGCgIAADAILQQEhAQJAAkACQCAKDgICAQALIBQgCS8AADsAACACIAc7AdQFIAJB1gVqIAdBEHY6AAAgFEECaiAJQQJqLQAAOgAA
IAJBwgA6ANgFIAIgYjoA1wUgAiAKNgLQBSACIAM2AswFIAJBADYCyAUgAiAFNgLEBSACQcQFahD+gYCAAAwDCyADLwEAIQELIAwoAgAgARD/gYCAAAwBCyAC
IAU6AKIFIAIgBUFAaiIBOgCjBSACQaIFahC2gICAAAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCAFQf8BcUH/fmoOPgIBAAMEAAAFAAAA
AAYHCAAAAAAAAAkKAAALABIAAAAAAAAMAAAAAAAAAAAAAA0OAAAAAAAAAAAAAAAAERAPAAsgAiABOgC8BSACQqrVqtUKNwK0BSACQbQFahDFgICAAAwRCyAC
QqrVqtUKNwKkBSACQaQFahC6gICAAAwQCyACQqrVqtUKNwKsBSACQawFahC6gICAAAwPCyAMKAIAEMeAgIAADA4LIAAQzICAgAAMDQsgDCgCABDQgICAAAwM
CyAMKAIAENOAgIAADAsLIFcNDSAMKAIAKAIYIgFBAToAvAIgASBYOgC7AiACIFg6AO8XDAoLIFkNDCAMKAIAKAIYIgFBAToAvAIgASBaOgC7AiACIFo6AO8X
DAkLIAwoAgAiASgCGEEBOgDaASABKAIYQQE6AL0CDAgLIAwoAgBBGGooAgBBADoA2gEMBwsgAEEAEN+AgIAADAYLIAwoAgAQ4oCAgAAMBQsgDCgCACgCGCEB
AkAgWw0AIAEgD0EDcToAuQIMBQsgASAPQQNxOgC6AgwECyAMKAIAKAIYIQECQCBcDQAgASAQQQNxOgC5AgwECyABIBBBA3E6ALoCDAMLIAwoAgAoAhghAQJA
IF8NACABIBFBA3E6ALkCDAMLIAEgEUEDcToAugIMAgsgDCgCACgCGCEBAkAgXg0AIAEgDkEDcToAuQIMAgsgASAOQQNxOgC6AgwBCyAMKAIAKAIYIQECQCBd
DQAgASANQQNxOgC5AgwBCyABIA1BA3E6ALoCCyAEQQFqIgRBA0YNBAwACwsgBEH/AXFBAWoiAUH/AXEgAUcNACAAQQA6AOkRIABBADsB4BEgACABOgDoEQwC
CwALAkBBAA0AAkACQAJAAkACQAJAAkACQCABQQ9xDhAJCQkICAAIAQIDBwcHBAUGCQsgABCvgICAAAwICyAAKAIUIgFFDQcgAEEMaiABEYOAgIAAgICAgAAM
BwsgAEEMaigCAEEBELCAgIAADAYLIABBARCxgICAAAwFCyAAQQxqKAIAELKAgIAADAQLIABBDGooAgAoAhghAEEALQDO48GAACEBAkBBAC0AzePBgABBAXEN
ACAAIAFBA3E6ALkCDAQLIAAgAUEDcToAugIMAwsgAEEMaigCACgCGEEAOgC5AgwCCyAAQQxqKAIAELOAgIAADAELIAIgAToAAyACQQNqELSAgIAACyACQfAX
aiSAgICAAAvaAQEFfyOAgICAAEGQAmsiASSAgICAAAJAAkAgACgCICICRQ0AIAFBCGogAEEMaiIDIAIRhICAgACAgICAACABKAIMIgJFDQAgASgCCCEEAkBB
gAJFDQAgAUEQakGqAUGAAvwLAAsgAkH/AUsNACABQRBqIAJqIQUCQCABQRBqIAQgAmpPDQAgBCAFSQ0CCwJAIAJFDQAgAUEQaiAEIAL8CgAACyAFQQA6AAAg
ACgCECIARQ0AIAMgAUEQaiACIAARhYCAgACAgICAAAsgAUGQAmokgICAgAAPCwALiAcCAX4LfwJAAkACQAJAAkAgADUCACAAQQRqMQAAQiCGhCICQoAEg0IA
Ug0AIAFBASABQQFLGyEBDAELIAFBASABQQFLGyEBIAJCgICCgAiDUEUNAQsgACgCGCIDIAMoAswBIAEgAy8B0AEiBCABIARJGyIBQQN0azYCzAEgAygCxAEi
BS8BBiABayIEQf//A3EgBEcNASAFIAQ7AQYgAy8B0AEgAUH//wNxayIBQf//A3EgAUcNASADIAE7AdABIAAoAhhBADoA2QEPCyACQoCAgIAIgyECAkAgACgC
GCIDLQDZAUUNACADQQA6ANkBIAFBf2ohASAAKAIYIQMLQQAgAC8BgBIiBCADLwHQASIGIARJGyEHIAAvAYISIQggAC8B/hEhCSAALwH8ESEKAkAgAkIAUg0A
IAYgB0cNACADLwHSASAKQf//A3FLDQAgAyAGIAoQ1YCAgAAMAgsgBiAHayIEQf//A3EgBEcNACADQdABaiELA0AgASAEQf//A3EiBCABIAQgAUkbIgRrIgUg
AUsNASAGQf//A3EgBEH//wNxIgFJDQEgAyADKALMASAEQQN0azYCzAEgAygCxAEiBC8BBiABayIDQf//A3EgA0cNASAEIAM7AQYgCy8BACABayIBQf//A3Eg
AUcNASALIAE7AQAgBUUNAgJAAkAgACgCGCILLwHSASIGIApB//8DcUcNACACUA0EIAsgCCAJENWAgIAAIAVBf2ohAQwBCwJAAkACQCAGRQ0AIAJQDQEMAgsg
Cy8B0AEgB0YNBQwECyALKALEASIDLwEGIQwgAygCACEBAkACQCADLwEEIgNFDQAgA0F/aiEDIAEvAaoCIQQMAQsgASgCCCIBRQ0EQQEhAwNAAkAgAyABLwGq
AiIESw0AIAQgA2shAwwCCyADIARrIQMgASgCCCIBRQ0FDAALCyADQf//A3EiDSAEQf//A3FPDQMgDEH//wNxIAEvAagCTw0DIAEoAhAiAyABKAIYaiIBIANJ
DQMgAUEHcQ0DIAFFDQMgAyABIA1BA3RqIgQoAgBqIgEgA0kNAyABQQdxDQMgAUUNAyAELQAEQQFxRQ0ECyAGQX9qIgFB//8DcSABRw0CIAsgCCABENWAgIAA
IAVBf2oiASAFSw0CCyAAKAIYIgNB0AFqIQsgAy8B0AEiBiAHayIEQf//A3EgBEYNAAsLAAsLwAIBCH8CQAJAIAFB//8DcSICRQ0AIAAoAgwiAygCGCIBLwHQ
ASEEQQAhBQNAIANBOGohBiAEIQcCQANAIAdB//8DcSADLwGCEk8NASAHQQFqQf//A3EgAS8BjAFPDQQgASABKALMAUEIajYCzAEgASgCxAEiCC8BBkEBaiIH
Qf//A3EgB0cNBCAIIAc7AQYgAS8B0AFBAWoiB0H//wNxIAdHDQQgASAHOwHQASADKAIYIgEvAdABIgdBA3YhCCAHQQdxQfi4yIAAai0AACEJAkACQCAHQf8D
Sw0AIAYgCGohCAwBCyAIQUBqIgggAygCNE8NBSADKAIwIAhqIQgLIAlB/wFxIgkgCC0AAHEgCUcNAAsLIARB//8DcSAAKAIMIgMoAhgiAS8B0AEiBEYNASAF
QQFqIgUgAkcNAAsLDwsAC+kBAQV/IAAoAhhBADoA2QEgACgCGCEBAkACQCAALQABQQFxRQ0AIAAvAYASIQIMAQtBACAALwGAEiIAIAEvAdABIABJGyECCwJA
IAJB//8DcSABLwGMAU8NACABKALEASACOwEGIAEoAsQBIgMvAQQiBCADKAIAIgAvAaoCTw0AIAMvAQYiBSAALwGoAk8NACAAKAIQIgMgACgCGGoiACADSQ0A
IABBB3ENACAARQ0AIAMgACAEQQN0aigCAGoiACADSQ0AIABBB3ENACAARQ0AIAEgAjsB0AEgASAAIAVBA3RqNgLMAQ8LAAvjBQMCfwF+An8jgICAgABBwBBr
IgEkgICAgAACQAJAAkACQCAAEMiAgIAAIgJB//8DcQ0AIAAtAABBCHFFDQEgABCygICAAAwBCyABIAI7AQYCQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQ
NwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQCABQYgQaiABQQZqEPWAgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICA
AAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HDAEEAQQAgA0IgiKcoAgARgICAgACAgICAACIARQ0AAkBBwwBFDQAgAEGqAUHDAPwLAAsg
AULDADcDmBAgASAANgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiABQQZqEPWAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiAB
QgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECAB
IAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyAAIAIgAEEAIAAQuYCAgAAMAQsgASgCmBAiAEUNACABKAKI
ECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAAL
AAucBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJA
AkACQCABQYgQaiAAEPaAgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg
+smAACIDp0E2QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83
AAAgAkEuakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNjcDmBAgASAC
NgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEPaAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQ
IQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQ
IAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASAB
KAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUg
ASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAvOBQMBfwF+An8j
gICAgABBwBBrIgIkgICAgAACQAJAAkAgACABEPeAgIAAIgFB//8DcUUNACACIAE7AQYCQEGAEEUNACACQQhqQaoBQYAQ/AsACyACQoAQNwKQECACQbjewYAA
NgKIECACIAJBCGo2AowQAkACQCACQYgQaiACQQZqEPiAgIAAQf//A3EOAwAEAQQLAkAgAigClBAiASACKAKQECIATQ0AIAEgABCIgICAAAwECyACKAKMECAB
EICAgIAADAELQQANAEEAKQPg+smAACIDp0HDAEEAQQAgA0IgiKcoAgARgICAgACAgICAACIBRQ0AAkBBwwBFDQAgAUGqAUHDAPwLAAsgAkLDADcDmBAgAiAB
NgKUECACQdyVx4AANgKQECACIAM3A4gQAkACQCACQZAQaiACQQZqEPiAgIAAQf//A3EOAwAEAQQLIAIoApwQIgEgAigCmBAiAEsNAiACQgA3A5gQIAIoApQQ
IQQgAkGq1arVejYClBAgAiAANgK8ECACIAE2ArgQIAIgBDYCtBAgAkGkEGogAkG0EGogAkGIEGoQuICAgAAgAiACKAK8ECIBNgKYECACIAIoArQQIgA2ApQQ
IAIgAigCuBA2ApwQAkAgAUUNACACKAKIECEEIAIoAowQIQUCQCABRQ0AIABBqgEgAfwLAAsgBCAAIAFBAEEAIAUoAgwRgYCAgACAgICAAAsgAi8BrBANASAC
KAKkECIAIAIoAqgQIgEQgICAgAAgAUUNAQJAIAFFDQAgAEGqASAB/AsACyACIAAgAUEAIAIQuYCAgAAMAQsgAigCmBAiAUUNACACKAKIECEEIAIoAowQIQUg
AigClBAhAAJAIAFFDQAgAEGqASAB/AsACyAEIAAgAUEAQQAgBSgCDBGBgICAAICAgIAACyACQcAQaiSAgICAAA8LIAEgABCIgICAAAALAAuEBgMCfwF+An8j
gICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAA
ELeAgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0ErQQBB
ACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEnakGq1arV
ejYAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCKzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEI
aiAAELeAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYC
tBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUC
QCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGq
ASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAg
BSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuHBgIHfwF+I4CAgIAAQTBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRB
HSADayIFaiIGIARJDQIgA0HRsMiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQe6wyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCLCACIAc2AiggAiAAIAJBKGpBAUEBIAYRgYCAgACAgICAACACKQMA
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQR1JDQALIAEtAAAhAyACQSBqQap/OgAAIAJBGGpCqtWq1arVqtWqfzcDACACQRBqQqrV
qtWq1arVqn83AwAgAkEIakKq1arVqtWq1ap/NwMAIAJCqtWq1arVqtWqfzcDAEEAIQUDQCACIAVqQSBqIANBD3EiBkEwciAGQdcAaiAGQQpJGzoAACAFQX9q
IQUgA0EPSyEGIANBBHYhAyAGDQALQQAhAyAAIAIgBWpBIWpBACAFayIFIAVBAC0AwNjJgABBIBCWgICAACIFQf//A3ENAANAIAAoAgwiBEEIIANrIgVqIgYg
BEkNAiADQe6wyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZB9rDIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAA
KAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIsIAIgBzYCKCACIAAgAkEoakEBQQEgBhGBgICAAICAgIAAIAIpAwAiCUIgiKciBUH/
/wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBCEkNAAsgAiABLQABOgAAIAAgAkEBEKCAgIAAIQULIAJBMGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsg
BiAIEIiAgIAAAAuABAMCfwF+Bn8jgICAgABBEGsiAySAgICAACABQQhqKAIAIQQgAikCACIFQiCIpyEGIAWnIQcgASgCACIIIQkCQAJAIAEoAgQiCg0AAkAg
BEUNAAJAIARFDQAgCUGqASAE/AsACyAHIAkgBEEAQQAgBigCDBGBgICAAICAgIAAC0EAIQsMAQtBACEIQQAhCyAERQ0AQQANAEEAIQgCQCAHIAkgBEEAIApB
ACAGKAIIEYKAgIAAgICAgAAiCw0AQQAhCwwBCyALIQggCiELCwJAAkACQCAIRQ0AIAFBCGpBACgC4PnJgAA2AgAgAUEAKQLY+cmAADcCAAwBC0EZIQRBAA0B
AkACQAJAIAEoAgQiCw0AQX8hCAwBCyAHIAtBAEEAIAYoAgARgICAgACAgICAACIIRQ0DAkAgC0UNACAIQaoBIAv8CwALIAsgASgCBEcNAQsCQCABKAIAIgQg
CCALak8NACAIIAQgC2pJDQELAkAgC0UNACAIIAQgC/wKAAALAkAgAUEIaigCACIERQ0AIAIpAgAhBSABKAIAIQICQCAERQ0AIAJBqgEgBPwLAAsgBacgAiAE
QQBBACAFQiCIpygCDBGBgICAAICAgIAACyABQgA3AgQMAQsACyAAIAs2AgQgACAINgIAQQAhBAsgACAEOwEIIANBEGokgICAgAAL1QIBA38jgICAgABBEGsi
BSSAgICAACAFIANBH3E6AA8CQCACQQRqIgMgAkkNAAJAIANBASAFLQAPdCICIAMgAksbIgNBf2pnIgJFDQBBICACayIGQf//A3EiB0F9aiICIAdLDQECQAJA
AkACQCACQQ1JDQAgA0GDgARqIgIgA08NAQwFC0IBIAatQv//A4OGpyABakF8aiIDIAFJDQQgA0UNBCADQQNxDQQgAyACQQJ0Qfz+yYAAaiICKAIANgIAIAIg
ATYCAAwBC0IBQSAgAkEQdkF/aiIDZ2siB61C//8Dg4anIgJBgIAETw0DIAJBEHQgAWpBfGoiAiABSQ0DIAJFDQMgAkEDcQ0DIANBgIABTw0BIAIgB0ECdEHk
/8mAAGoiAygCADYCACADIAE2AgALIAVBEGokgICAgAAPCyAHQQ8QiICAgAAAC0EBEIyAgIAAAAsAC5cGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQ
RQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQu4CAgABB//8DcQ4DAAQBBAsCQCAB
KAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTRBAEEAIANCIIinKAIAEYCAgIAAgICAgAAi
AkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcA
ACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNDcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAA
ELuAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuFCQMHfwF+A38jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQAJAAkACQANAIAAo
AgwiBEEvIANrIgVqIgYgBEkNBiADQfawyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQMCQCAAKAIEIARqIgZBpbHIgABPDQAgByAGIAVqSQ0JCwJAIAVF
DQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0IIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAA
IAIpAwgiCUIgiKciBUH//wNxDQYgCachBQsgAyAFaiIFIANJDQYgBSEDIAVBL0kNAAsgASgCBCEKIAEoAgAhC0EAIQMDQCAAKAIMIgRBAiADayIFaiIGIARJ
DQYgA0Gk08CAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0EAkAgACgCBCAEaiIGQabTwIAATw0AIAcgBiAFakkNCQsCQCAFRQ0AIAYgByAF/AoAAAsgACgC
DCIGIAVqIgQgBkkNCCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8D
cQ0GIAmnIQULIAMgBWoiBSADSQ0GIAUhAyAFQQJJDQALIApFDQMgCkF/aiEMQQAhAQNAIAAgCyABai0AAEEKQQBBsNjJgAAQlYCAgAAiBUH//wNxDQUCQCAB
IAxGDQBBACEDA0AgACgCDCIEQQIgA2siBWoiBiAESQ0IIANBq9PAgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBwJAIAAoAgQgBGoiBkGt08CAAE8NACAH
IAYgBWpJDQsLAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQogACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENCCAJpyEFCyADIAVqIgUgA0kNCCAFIQMgBUECSQ0ACwsgAUEBaiIBIApHDQAMBAsLIAYgCBCIgICA
AAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALQQAhAwNAIAAoAgwiBEECIANrIgVqIgYgBEkNAiADQa2AwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUC
QCAAKAIEIARqIgZBr4DAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAF
NgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBAkkNAAtBACEF
CyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALugEBAn8gACgCGCIBQQE6AIwCIAEgACgCGC8B0AE7AfwBIAEgACgCGC8B0gE7Af4BIAEgACgCGCICKQKg
ATcC4AEgAUH4AWogAkG4AWooAgA2AgAgAUHwAWogAkGwAWopAgA3AgAgAUHoAWogAkGoAWopAgA3AgAgASAAKAIYLQDaAUEBcToAgAIgASAAKAIYLQDZAUEB
cToAgQIgASAAKAIAQQh2QQFxOgCCAiABIAAoAhgpALUCNwCDAgv2BQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyAB
QoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEL6AgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAg
AhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0ElQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAA
IAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEdakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQiU3A5gQIAEgAjYC
lBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABC+gICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEE
IAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECAB
IAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgC
pBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEo
ApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALtQICB38BfiOAgICA
AEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEiIANrIgVqIgYgBEkNAiADQaWxyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARq
IgZBx7HIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYC
ACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBIkkNAAsgACABEIyEgIAAIQUL
IAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAuTDAMEfwF+BX8jgICAgABBwBBrIgEkgICAgAAgACgCGCICQbgBaiIDKAIAIQQgA0EAOwEAIAJBsAFqIgMp
AgAhBSADQgA3AgAgAUEQaiIDIAJBqAFqKQIANwMAIAFBGGoiBiAFNwMAIAFBIGoiByAENgIAIAEgAikCoAE3AwgCQAJAAkACQAJAIAAoAhgQwICAgABB//8D
cSICDQAgAEEAOwH8ESAALwH4EUF/aiICQf//A3EgAkcNAiAAQQA7AYASIAAgAjsB/hEgAC8B+hFBf2oiAkH//wNxIAJHDQIgACACOwGCEiAAIAA1AgBC//3/
/w+DPgIAIABBAUEBEMGAgIAAIAAoAhhByJ/HgABBtPrJgABBABDCgICAACAAKAIYIgIoAsQBKAIAIgQoAhAiAyACKALIASIIKAIAaiIGIANJDQIgBEEQaiEJ
A0AgBkEHcQ0DIAZFDQMgAi8B1AEhAwJAIAQvAagCIgdFDQAgA61C//8Dg0IahkKUAoQhBSAHQX9qQf////8BcSEKIAYhAgJAIAdBB3EiA0UNAANAIAIgBTcD
ACACQQhqIQIgA0F/aiIDDQALCwJAIApBB0kNACAGIAdBA3RqIQMDQCACIAU3AwAgAkE4aiAFNwMAIAJBMGogBTcDACACQShqIAU3AwAgAkEgaiAFNwMAIAJB
GGogBTcDACACQRBqIAU3AwAgAkEIaiAFNwMAIAJBwABqIgIgA0cNAAsLIAAoAhgiAi8B1AEhAwsCQCADQf//A3EiA0UNACAEKAJIIANNDQQgCSgCACIGIAQo
AkBqIgIgBkkNBCACQQNxDQQgAkUNBCACIANBJGxqIgMvASAiAkUNBCACIAdqIgJB//8DcSACRw0EIANBIGogAjsBACAIIAgpAwBCgICAgIABhDcDACAAKAIY
IQILIAIoAsgBIgIgAikDAEKAgICAgCCENwMAIAAvAfgRQX9qIgJB//8DcSIDIAJHDQMCQCAAKAIYIgIvAdIBIANHDQAgAEEBQQEQwYCAgAAMAwsgAkEBEMOA
gIAAIAAoAhgiAigCxAEoAgAiBEEQaiEJIAQoAhAiAyACKALIASIIKAIAaiIGIANPDQAMAwsLIAAoAhgiACABKQMINwKgASAAQbgBaiAHKAIANgIAIABBsAFq
IAYpAwA3AgAgAEGoAWogAykDADcCACABIAI7AQYCQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQCAB
QYgQaiABQQZqEMSAgIAAQf//A3EOAwADAQMLIAEoApQQIgIgASgCkBAiA0sNBCABKAKMECACEICAgIAADAELQQANAEEAKQPg+smAACIFp0HDAEEAQQAgBUIg
iKcoAgARgICAgACAgICAACICRQ0AAkBBwwBFDQAgAkGqAUHDAPwLAAsgAULDADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAU3A4gQAkACQCABQZAQaiAB
QQZqEMSAgIAAQf//A3EOAwADAQMLIAEoApwQIgIgASgCmBAiA0sNAyABQgA3A5gQIAEoApQQIQAgAUGq1arVejYClBAgASADNgK8ECABIAI2ArgQIAEgADYC
tBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECICNgKYECABIAEoArQQIgM2ApQQIAEgASgCuBA2ApwQAkAgAkUNACABKAKIECEAIAEoAowQIQQC
QCACRQ0AIANBqgEgAvwLAAsgACADIAJBAEEAIAQoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECIDIAEoAqgQIgIQgICAgAAgAkUNAQJAIAJFDQAgA0Gq
ASAC/AsACyACIAMgAkEAIAIQuYCAgAAMAQsgASgCmBAiAkUNACABKAKIECEAIAEoAowQIQQgASgClBAhAwJAIAJFDQAgA0GqASAC/AsACyAAIAMgAkEAQQAg
BCgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LAAsgAiADEIiAgIAAAAsgAiADEIiAgIAAAAvpSAcGfwF+BH8BfAF+J38DfiOAgICAAEGAAmsiASSAgICA
ACAAKALEASgCACECAkACQAJAAkACQAJAAkACQCAALwHUASIDRQ0AIAIoAkggA00NASACKAIQIgQgAigCQGoiBSAESQ0BIAVBA3ENASAFRQ0BIAUgA0EkbGoi
BS8BICIDRQ0BIAVBIGogA0F/aiIFOwEAIAVB//8DcQ0AIAIoAkQiBUF/aiIDIAVLDQEgAiADNgJEC0EAIQUgAEEAOwHUAQJAIAAvAbgBDQAgAC0ApAFB/wFx
DQAgAC0ArAFB/wFxDQAgAC0AtAFB/wFxRQ0HCyABQawBaiACQTxqIAIoAhAgAEGgAWoiAxCUg4CAACABLwGuASICRQ0FIAAoAsQBKAIAIQRBsPnJgAAhBQJA
AkAgAkFnag4OAAICAgICAgICAgICAgECC0Hy3siAACEFCyABQcQAaiAAIAQgBRCVg4CAACABQcQAaiECAkAgAS8BSCIFDh0CBQUFBQUFBQUFBQUFBQUFBQUF
BQUFBQUFBwUFAAULIAAoAsQBIgYpAgQiB6ciCEH//wNxQQFqIglB//8DcSICIAlHDQAgAUHMAGogBigCACIKQRBqIgtBACACEJaDgIAAIAEvAVQgAS8BViIC
bCIFQYCAgIACTw0AIAJBA3QiBCAFQQN0aiICIARJDQACQAJAIAEvAVgiBQ0AQQAhBQwBC0IBQSAgBUF/amdrrUL//wODhqciBbhEAAAAAAAA6j+iIgxEAAAA
AAAA8L9lDQEgDEQAAAAAAADwQWYNASAM/AOtQiR+Ig1CIIinDQEgBUEBdEEDakH8////B3EiBCANp2oiBSAESQ0BCyACIAVqIgQgAkkNACABKAJMIgVBD2oi
AiAFSQ0AIAJBgICAgAFPDQAgAkEEdkE/akEDdkH4//8BcSIOIAJBBHRBgH5xaiICIA5JDQAgBEEHaiIOIARJDQAgDkF4cSIEIAJqIgIgBEkNAAJAAkAgBQ0A
QQAhBQwBC0IBQSAgBUF/akEEdmdrrUL//wODhqchBQsgAiAFQRNqQXxxIAVBDGxqaiIFIAJJDQAgASgCUCIEQR9qIgIgBEkNACACQYCAgMAATw0AIAJBBXZB
P2pBA3ZB+P8/cSIEIAJBBXRBgHhxaiICIARJDQAgBUEHaiIEIAVJDQAgBEF4cSIFIAJqIgIgBUkNACABLwFaIgVBHG4hBAJAAkAgBUEcTw0AQQAhBQwBC0IB
QSAgBEF/amdrrUL//wODhqciBbhEAAAAAAAA6j+iIgxEAAAAAAAA8L9lDQEgDEQAAAAAAADwQWYNASAM/AOtQhx+Ig1CIIinDQEgBUEBdEEDakH8////B3Ei
BSANp2oiDiAFSQ0BIAIgDmoiDiACSQ0BQgFBICAEQQR0QX9qZ2uthqchBSAOIQILIAJBA2oiBCACSQ0AIARBfHEiBCAFQQZsIAVBE2pBfHFqQQNqQXxxaiIC
IARJDQAgAkH//wNqIg8gAkkNACABQdwAaiALIAhB//8DcSAKLwGqAiIEEJaDgIAAIAEvAWQgAS8BZiICbCIFQYCAgIACTw0AIAJBA3QiDiAFQQN0aiICIA5J
DQACQAJAIAEvAWgiBQ0AQQAhBQwBC0IBQSAgBUF/amdrrUL//wODhqciBbhEAAAAAAAA6j+iIgxEAAAAAAAA8L9lDQEgDEQAAAAAAADwQWYNASAM/AOtQiR+
Ig1CIIinDQEgBUEBdEEDakH8////B3EiDiANp2oiBSAOSQ0BCyACIAVqIg4gAkkNACABKAJcIgVBD2oiAiAFSQ0AIAJBgICAgAFPDQAgAkEEdkE/akEDdkH4
//8BcSIIIAJBBHRBgH5xaiICIAhJDQAgDkEHaiIIIA5JDQAgCEF4cSIOIAJqIgIgDkkNAAJAAkAgBQ0AQQAhBQwBC0IBQSAgBUF/akEEdmdrrUL//wODhqch
BQsgAiAFQRNqQXxxIAVBDGxqaiIFIAJJDQAgASgCYCIOQR9qIgIgDkkNACACQYCAgMAATw0AIAJBBXZBP2pBA3ZB+P8/cSIOIAJBBXRBgHhxaiICIA5JDQAg
BUEHaiIOIAVJDQAgDkF4cSIFIAJqIgIgBUkNACABLwFqIgVBHG4hDgJAAkAgBUEcTw0AQQAhBQwBC0IBQSAgDkF/amdrrUL//wODhqciBbhEAAAAAAAA6j+i
IgxEAAAAAAAA8L9lDQEgDEQAAAAAAADwQWYNASAM/AOtQhx+Ig1CIIinDQEgBUEBdEEDakH8////B3EiBSANp2oiCCAFSQ0BIAIgCGoiCCACSQ0BQgFBICAO
QQR0QX9qZ2uthqchBSAIIQILIAJBA2oiDiACSQ0AIA5BfHEiDiAFQQZsIAVBE2pBfHFqQQNqQXxxaiICIA5JDQAgAkH//wNqIgUgAkkNAAJAAkAgD0GAgHxx
IAVBgIB8cUkNACAKIRAMAQsgBCAJQf//A3EiBUkNBSAKIQICQAJAIAQgBUcNAAJAAkAgCigCDCICRQ0AQQEhBQNAIAUgAi8BqgIiBE0NAiAFIARrIQUgAigC
DCICDQALIARFDQkLIAFBADoAbAwCCyAFQX9qIgkgBUsNBwsgASAJOwF0IAEgAjYCcCABQQA6AGwgAUEBOgB8IAFBADoAeCABIAdCEIg9AXYgAUHwAGohBgsg
BigCACIQLwGqAiEEC0EcIQUgBEH//wNxQQJJDQYCQCAGLwEERQ0AIBAvAaYCIREgEC8BpAIhEiAQLwGiAiETIBAvAaACIQsgECgCnAIhFCAQKAKYAiEVAkAC
QCAAKAIoIhZFDQAgACAWKAIANgIoDAELQRkhBUEADQggAEEYakGwAkEDIAAQj4CAgAAiFkUNCAJAQbACRQ0AIBZBqgFBsAL8CwALIBZBB3ENAgsCQEGwAkUN
ACAWQaoBQbAC/AsACyATIAtsIg9BgICAgAJPDQEgE0EDdCIXIA9BA3RqIhggF0kNAQJAAkAgEg0AQQAhGUEAIRpBACEbQQAhHEEAIR0MAQtCAUEgIBJBf2pn
IgJrrUL//wODhqciGrhEAAAAAAAA6j+iIgxEAAAAAAAA8L9lDQIgDEQAAAAAAADwQWYNAiAM/AMiGa1CJH4iDUIgiKcNAiAaQQF0QQNqQfz///8HcSIbIA2n
aiIcIBtJDQJBf0EAIAJrdEF/cyEdCyAYIBxqIgUgGEkNASAVQQ9qIgIgFUkNASACQYCAgIABTw0BIAJBBHZBP2pBBnYiHkEDdCIfIAJBBHRBgH5xaiICIB9J
DQEgBUEHaiIEIAVJDQEgBEF4cSIgIAJqIiEgIEkNAQJAAkAgFQ0AQQAhIgwBC0IBQSAgFUF/akEEdmdrrUL//wODhqchIgsgISAiQRNqQXxxIiMgIkECdGoi
JCAiQQN0amoiBSAhSQ0BIBRBH2oiAiAUSQ0BIAJBgICAwABPDQEgAkEFdkE/akEGdiIlQQN0IiYgAkEFdEGAeHFqIgIgJkkNASAFQQdqIgQgBUkNASAEQXhx
IicgAmoiKCAnSQ0BIBFBHG4hBUEAISkgKCECQQAhKkEAIStBACEsQQAhLUEAIS4CQCARQRxJDQBCAUEgIAVBf2pnIgRrrUL//wODhqciLLhEAAAAAAAA6j+i
IgxEAAAAAAAA8L9lDQIgDEQAAAAAAADwQWYNAiAM/AMiLa1CHH4iDUIgiKcNAiAsQQF0QQNqQfz///8HcSIrIA2naiIqICtJDQIgKCAqaiICIChJDQJBf0EA
IARrdEF/cyEpQgFBICAFQQR0QX9qZ2uthqchLgsgAkEDaiIFIAJJDQEgBUF8cSIvIC5BAXQgLkETakF8cSIwIC5BAnRqIjFqQQNqQXxxaiICIC9JDQEgAkH/
/wNqIgUgAkkNASAAQSxqIQICQAJAAkAgBUGAgHxxIjJBgYAkSQ0AQQANASACKQIAIg2nIDJBEEEAIA1CIIinKAIAEYCAgIAAgICAgAAiDkUNAQJAIDJFDQAg
DkGqASAy/AsACyAyITMgDkH//wNxDQQMAgsCQAJAAkAgACgCPCIORQ0AIAAgDigCADYCPAwBC0EADQEgAkGAgCRBECAAEI+AgIAAIg5FDQECQEGAgCRFDQAg
DkGqAUGAgCT8CwALIA5B//8DcQ0FC0GAgCQhM0GAgCRFDQIgDkGqAUGAgCT8CwAMAgsCQEGwAkUNACAWQaoBQbAC/AsACyAWIAAoAig2AgAgACAWNgIoDAUL
AkBBsAJFDQAgFkGqAUGwAvwLAAsgFiAAKAIoNgIAIAAgFjYCKAwECwJAIDNFDQAgDkEAIDP8CwALIA4gF2oiAiAOSQ0BIAJBB3ENASACRQ0BIA5BB3ENAQJA
AkACQCATRQ0AIAtBA3QhNEEAIQUgDiEEIBMhCQNAIAUgD08NAiACIA5rIgggAksNBSAEIAitNwMAIARBCGohBCACIDRqIQIgBSALaiEFIAlBf2oiCQ0ACwsC
QCAyRQ0AIA5B+P8DcQ0ECyAYIBtqIgggGEkNAyAOIBhqIgIgDkkNAyACRQ0DAkAgGkEBdCIFRQ0AIAJBACAF/AsACyAOIAhqIgkgDkkNAyAJRQ0DAkAgGUUN
ACAJIQICQCAZQSRsIjRBXGoiD0EkbiIFQQNxQQNGDQBBACAFQQFqQQNxayEFQQAoAry5yIAAIQRBACkCtLnIgAAhDUEAKQKsuciAACE1QQApAqS5yIAAITZB
ACkCnLnIgAAhNyAJIQIDQCACQSBqIAQ2AgAgAkEYaiANNwIAIAJBEGogNTcCACACQQhqIDY3AgAgAiA3NwIAIAJBJGohAiAFQQFqIgUNAAsLIA9B7ABJDQAg
CSA0aiEEQQAoAry5yIAAIQVBACkCtLnIgAAhDUEAKQKsuciAACE1QQApAqS5yIAAITZBACkCnLnIgAAhNwNAIAJBIGogBTYCACACQRhqIA03AgAgAkEQaiA1
NwIAIAJBCGogNjcCACACIDc3AgAgAkEkaiA3NwIAIAJBLGogNjcCACACQTRqIDU3AgAgAkE8aiANNwIAIAJBxABqIAU2AgAgAkHIAGogNzcCACACQdAAaiA2
NwIAIAJB2ABqIDU3AgAgAkHgAGogDTcCACACQegAaiAFNgIAIAJBjAFqIAU2AgAgAkGEAWogDTcCACACQfwAaiA1NwIAIAJB9ABqIDY3AgAgAkHsAGogNzcC
ACACQZABaiICIARHDQALCwJAQcIARQ0AIAFBrAFqQQBBwgD8CwALIA4gJ2oiAiAOSQ0DIAJFDQMCQCAmRQ0AIAJB/wEgJvwLAAsgJyAmaiI0ICdJDQMgDiAg
aiICIA5JDQMgAkUNAwJAIB9FDQAgAkH/ASAf/AsACyAgIB9qIh8gIEkNAyAOICFqIgJBEGoiBUEDcQ0DIAJBADYCDCACICI2AgggAiAjQXBqNgIEIAIgJEFw
ajYCAAJAICJFDQAgBUEAICL8CwALIAUgDmsiIiAFSw0DIA4gL2oiAkEANgIMIAIgLjYCCCACIDBBcGo2AgQgAiAxQXBqNgIAIAJBEGohAgJAIC5FDQAgAkEA
IC78CwALIAIgDmsiLiACSw0DICggK2oiDyAoSQ0DIA4gKGoiAiAOSQ0DIAJFDQMCQCAsQQF0IgVFDQAgAkEAIAX8CwALIA4gD2oiCSAOSQ0DIAlFDQMgFK0h
NwJAIC1FDQAgCSECAkAgLUEcbCIhQWRqIhRBHG4iBUEDcUEDRg0AQQAgBUEBakEDcWshBUEAKALYuciAACEEQQApAtC5yIAAIQ1BACkCyLnIgAAhNUEAKQLA
uciAACE2IAkhAgNAIAJBGGogBDYCACACQRBqIA03AgAgAkEIaiA1NwIAIAIgNjcCACACQRxqIQIgBUEBaiIFDQALCyAUQdQASQ0AIAkgIWohBEEAKALYuciA
ACEFQQApAtC5yIAAIQ1BACkCyLnIgAAhNUEAKQLAuciAACE2A0AgAkEYaiAFNgIAIAJBEGogDTcCACACQQhqIDU3AgAgAiA2NwIAIAJBHGogNjcCACACQSRq
IDU3AgAgAkEsaiANNwIAIAJBNGogBTYCACACQThqIDY3AgAgAkHAAGogNTcCACACQcgAaiANNwIAIAJB0ABqIAU2AgAgAkHUAGogNjcCACACQdwAaiA1NwIA
IAJB5ABqIA03AgAgAkHsAGogBTYCACACQfAAaiICIARHDQALCyARQRB0IQIgN0IghiENIBWtITUgFiAdOwFcIBYgHDYCWCAWIBs2AlQgFkEANgJQIBYgGjYC
TCAWIBk2AkggFkEANgJEIBYgCDYCQCAWIBg2AjwgFiAiNgI4IBYgHzYCNCAWIB42AjAgFiAgNgIsIBYgNDYCKCAWICU2AiQgFiAnNgIgIBYgFzYCHCAWQQA2
AhggFiAyNgIUIBYgDjYCECABQQA6AKgBAkBBwgBFDQAgFkHgAGogAUGsAWpBwgD8CgAACyACIBJyIQIgDSA1hCENIBYgKTsByAEgFiAqNgLEASAWICs2AsAB
IBZBADYCvAEgFiAsNgK4ASAWIC02ArQBIBZBADYCsAEgFiAPNgKsASAWICg2AqgBIBYgLjYCpAEgFkEBOwGiAQJAQcoARQ0AIBZBzAFqQQBBygD8CwALIBYg
EzsBqgIgFiALOwGoAiAWIAI2AqQCIBYgEzsBogIgFiALOwGgAiAWIA03ApgCIBZBATsBlgIgFiABLQCoAToArAIgFiAAKQMANwMAIBZBADsBqgIgFkIANwII
IAApAwBCAXwiDVANAyAAIA03AwAgACgCXCICIDNqIgUgAkkNAyAAIAU2AlwgEC8BqgIiCCAGLwEEIgtrIhFB//8DcSICIBFHDQMgFiAROwGqAiACIBYvAaIC
Sw0DIAsgCEsNAyAIIBAvAaoCSw0DIBEgCEsNAyARIBFB//8DcUsNAyAQKAIQIgIgECgCGGoiBSACSQ0DIAVBB3ENAyAFRQ0DIBYoAhAiBCAWKAIYaiICIARJ
DQMgAkEHcQ0DIAJFDQMgEEEQaiEgAkAgEUUNACAWQRBqIQ4gBSALQQN0aiEFIBEhBAJAA0AgDiAgIAIgBUEAIBYvAagCEJeDgIAAIglB//8DcQ0BIAJBCGoh
AiAFQQhqIQUgBEF/aiIERQ0CDAALCyABIAk7Ae4BIAFB7gFqEJiDgIAAIAAgFhCZg4CAAEEcIQUMCgsgACgCdCICQYCAgIAETw0DAkACQCACDQBBACEFQarV
qtV6IQIMAQsgACgCbCICQQNxDQQgACgCcCEFCwJAIAVFDQADQAJAIAIoAgAiBCgCACAQRw0AIAQvAQQiDiAGLwEESQ0AIAQgFjYCACAOIAYvAQRrIg5B//8D
cSAORw0GIAQgDjsBBAsgAkEEaiECIAVBf2oiBQ0ACwsgECgCECIFIBAoAhhqIgIgBUkNAyACQQdxDQMgAkUNAwJAIAggC0YNACACIAtBA3RqISFBACEUA0Ag
ECgCECICICEgFEEDdGoiJygCAGoiKCACSQ0FIChBB3ENBSAoRQ0FIBAvAagCIRgCQCAnKQMAIg1CgICAgMAAg1ANAAJAAkAgGEUNAEEAIS4DQAJAICggLkED
dGoiIikDAEIDg0IBUg0AICIgICgCACI0ayILICJLDQogNCAQKAI4aiIOIDRJDQogDkUNCiAOQQNxDQogDkF8aiIVKAIARQ0KIA5BeGooAgAiAkF/aiIIIAJL
DQogAUEwaiALrUKBgICAEH4iDULb0dCFmtrfgWeFQgAgDUKsmuLTo9Lw+h+FQgAQr4WAgAAgAUEgaiABKQM4QtvR0IWa2t+BZ4VCACABKQMwQqvI9cXHrIe7
oH+FQgAQr4WAgAAgDiAIIAEpAyggASkDIIUiDadxIgRqLQAAIgVFDQogDkFwaiETIA1COYinIQYgDkF0aiEPIAghCQJAA0AgCSECAkAgBcBBf0oNACAGIAVB
/wBxRw0AIA4gDygCAGoiBSAOSQ0NIAVBA3ENDSAFRQ0NIAsgBSAEQQJ0aiIFKAIARg0CCyAEQQFqIgVFDQwgDiAFIAhxIgRqLQAAIgVFDQwgAkF/aiEJIAJF
DQwMAAsLIA4gEygCAGoiAiAOSQ0KIAJBA3ENCiACRQ0KIDQgAiAEQQN0aiIEKAIAaiICIDRJDQogAkEDcQ0KIAJFDQoCQAJAIAQoAgQiBA0AQQAhBAwBCyAE
QYCAgIAETw0LIARBAnQhBAsgBEEPaiIJIARJDQ4gNCAQKAI0aiIEIDRJDQ4gBEUNDiACIARrIgQgAksNDiAEQQ9xDQ4gNCAQKAIsaiICIDRJDQ4gAkEHcQ0O
IAJFDQ4gCUEPTQ0OIAIgBEEKdiILQQN0aiIGIAYpAwBCf0HAACAJQQR2IglBwAAgBEEEdkE/cSIEayIIIAkgCEkbIghrrYggBK2GhDcDACAJIAhrIgQgCUsN
DgJAAkAgBEHBAE8NACALQQFqIQYMAQsCQCAEQb9/aiIEQQN2Qfj///8BcUEIaiI0RQ0AIAZBCGpB/wEgNPwLAAsgCyAEQQZ2akECaiEGIAkgCCAEQUBxamtB
QGohBAsCQCAERQ0AIAIgBkEDdGoiAiACKQMAQn9BACAEa0E/ca2IhDcDAAsgDiAPKAIAaiICIA5JDQ4gAkEDcQ0OIAJFDQ4gBSACayIEIAVLDQ4gDiAEQQJ2
IgVqQQE6AAAgAiAEakGq1arVejYCACAOIBMoAgBqIgIgDkkNDiACQQNxDQ4gAkUNDiACIAVBA3RqQqrVqtWq1arVqn83AgAgFSgCACICQX9qIgUgAksNDiAV
IAU2AgAgIiAiKQMAQnyDNwMACyAuQQFqIi4gGEcNAAsgGCAQLwGoAiIFRw0BICcpAwAhDQsgJyANQv////+/f4MiDTcDAAwBCyAgKAIAIgQgJygCAGoiAiAE
SQ0KIAJBB3ENCiACRQ0KAkACQCAFRQ0AA0AgAikDAEIDg0IBUQ0CIAJBCGohAiAFQX9qIgUNAAsLICcgJykDAEL/////v3+DIg03AwAMAQsgJykDACENCwJA
IA1CgICAgIACg1ANAAJAIBhFDQBBACELA0ACQCAoIAtBA3RqIi4tAAVBIHFFDQAgLiAgKAIAIiJrIg8gLksNDSAiIBAoAqQBaiIFICJJDQ0gBUUNDSABIAU2
AvABIAVBA3ENDSAFQXxqKAIARQ0AIAVBeGooAgAiAkF/aiIIIAJLDQ0gAUEQaiAPrUKBgICAEH4iDULb0dCFmtrfgWeFQgAgDUKsmuLTo9Lw+h+FQgAQr4WA
gAAgASABKQMYQtvR0IWa2t+BZ4VCACABKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgBSAIIAEpAwggASkDAIUiDadxIg5qLQAAIgRFDQAgBUFwaiETIA1COYin
IQYgBUF0aiE0IAghCQJAA0AgCSECAkAgBMBBf0oNACAGIARB/wBxRw0AIAUgNCgCAGoiBCAFSQ0QIARBA3ENECAERQ0QIA8gBCAOQQJ0aiIEKAIARg0CCyAO
QQFqIgRFDQ8gBSAEIAhxIg5qLQAAIgRFDQIgAkF/aiEJIAJFDQIMAAsLIAUgEygCAGoiAiAFSQ0NIAJBAXENDSACRQ0NIAIgDkEBdGovAQAiBUUNDSAQKAK0
ASAFTQ0NICIgECgCrAFqIgIgIkkNDSACQQNxDQ0gAkUNDSACIAVBHGxqIgIvARgiBUUNDSACQRhqIAVBf2oiAjsBAAJAIAJB//8DcQ0AIBAoArABIgJBf2oi
BSACSw0OIBAgBTYCsAELIAFB8AFqIAQQsYKAgAAgLiAuKQMAQv///////3eDNwMACyALQQFqIgsgGEcNAAsLAkAgGCAQLwGoAiIFRw0AICcgJykDAEL/////
/32DIg03AwAMAQsgICgCACIEICcoAgBqIgIgBEkNCiACQQdxDQogAkUNCgJAAkAgBUUNAANAIAItAAVBIHENAiACQQhqIQIgBUF/aiIFDQALCyAnICcpAwBC
//////99gyINNwMADAELICcpAwAhDQsCQCANQoCAgICAAYNQDQACQCAYRQ0AICghAiAYIQQDQAJAIAIpAwAiDUKAgIDg//8Ag1ANACAQKAJIIA1CGoinQf//
A3EiCU0NDSAQKAIQIg4gECgCQGoiBSAOSQ0NIAVBA3ENDSAFRQ0NIAUgCUEkbGoiBS8BICIORQ0NIAVBIGogDkF/aiIFOwEAIAVB//8DcQ0AIBAoAkQiBUF/
aiIOIAVLDQ0gECAONgJECyACQQhqIQIgBEF/aiIEDQALCwJAIBggEC8BqAIiBUYNACAgKAIAIgQgJygCAGoiAiAESQ0LIAJBB3ENCyACRQ0LIAVFDQADQCAC
KAECQYD4/x9xDQIgAkEIaiECIAVBf2oiBQ0ACwsgJyAnKQMAQv//////foM3AwALAkAgGEEDdCICRQ0AIChBACAC/AsACyAUQQFqIhQgEUcNAAsLIBAvAaoC
IBFB//8DcWsiAkH//wNxIAJHDQcgECACOwGqAiAWIBA2AggCQAJAIBAoAgwiAkUNACAWIAI2AgwgAiAWNgIIDAELIBZBADYCDCAAIBY2AlgLIBAgFjYCDCAA
KALEASIFKAIAIgIgCkYNAiAFIAo2AgAgBSgCCCEGIAUvAQYhCSAFLwEEIQQgBSAHNwIEAkACQCAAKALEASIFKAIAIg4gAkcNACAFLwEEQf//A3EgBEcNACAF
LwEGIAlB//8DcUYNAQsgACgCyAEiBSAFKQMAQoCAgICAIIQ3AwAgBCACLwGqAk8NCCAJQf//A3EgAi8BqAJPDQggAigCECIFIAIoAhhqIg4gBUkNCCAOQQdx
DQggDkUNCCAFIA4gBEEDdGoiCCgCAGoiDiAFSQ0IIA5BB3ENCCAORQ0IIAggCCkDAEKAgICAgCCENwMAIAAoAsQBIgUoAgAhDgsCQCAOIAJHDQAgBSAGNgII
IAUgCTsBBiAFIAQ7AQQgBSACNgIAIAAoAsQBIQIMBQsCQCAALwHUASIIDQBBlLPIgAAhC0H4ssiAACEODAILIAFBgAFqQRhqIANBGGooAgA2AgAgAUGAAWpB
EGogA0EQaikCADcDACABQYABakEIaiADQQhqKQIANwMAIAFBAToAnAEgASADKQIANwOAASAOKAJIIAhNDQcgDigCECILIA4oAkBqIgUgC0kNByAFQQNxDQcg
BUUNByAFIAhBJGxqIgUvASAiCEUNByAFQSBqIAhBf2oiBTsBAAJAIAVB//8DcQ0AIA4oAkQiBUF/aiIIIAVLDQggDiAINgJECyABQZwBaiELIANCADcDACAA
QQA7AdQBIANBGGpBADYCACADQRBqQgA3AwAgA0EIakIANwMAIAFBgAFqIQ4MAQsgBSAPEIiAgIAAAAsCQCAAKALAAUUNACAALwHWASIPRQ0GIAAoAsQBKAIA
IggoArQBIA9NDQYgCCgCECIQIAgoAqwBaiIFIBBJDQYgBUEDcQ0GIAVFDQYgBSAPQRxsaiIFLwEYIg9FDQYgBUEYaiAPQX9qIgU7AQAgBUH//wNxDQAgCCgC
sAEiBUF/aiIPIAVLDQYgCCAPNgKwAQsgACgCxAEiBSAGNgIIIAUgCTsBBiAFIAQ7AQQgBSACNgIAAkAgCy0AAEUNACADIA4pAgA3AgAgA0EYaiICIA5BGGoo
AgA2AgAgA0EQaiIFIA5BEGopAgA3AgAgA0EIaiIEIA5BCGopAgA3AgAgABDAgICAAEH//wNxIg5FDQAgASAOOwGkASABQaQBahCag4CAACACQQA2AgAgBUIA
NwMAIARCADcDACADQgA3AwAgAEEAOwHUAQsgACgCwAEiAkUNACAAQQA2AsABIABBADsB1gECQCAAIAIoAgwgAigCEEEAIAIoAgAgAi0ACEEBcSIFG0EAIAIo
AgQgBRsQkYOAgAAiBUH//wNxRQ0AIAEgBTsBpgEgAUGmAWoQm4OAgAALIAApA5gBIg1CIIinIQQgDachDgJAIAIoAhAiBUUNACACKAIMIQkCQCAFRQ0AIAlB
qgEgBfwLAAsgDiAJIAVBAEEAIAQoAgwRgYCAgACAgICAAAsCQCACLQAIQQFxDQAgAigCBCIFRQ0AIAIoAgAhCQJAIAVFDQAgCUGqASAF/AsACyAOIAkgBUEA
QQAgBCgCDBGBgICAAICAgIAACyAAKAKYASACQRRBAkEAIABBnAFqKAIAQQxqKAIAEYGAgIAAgICAgAALIAAoAsQBIQIMAQsACyABQawBaiACKAIAIgJBPGog
AigCECADEJSDgIAAIAEvAa4BIgJFDQMCQCACQWdqDg4AAwMDAwMDAwMDAwMDAgMLEJyDgIAAC0EZIQUMAwsQnYOAgABBACEFDAILAAsgACABLwGsATsB1AFB
ACEFCyABQYACaiSAgICAACAFC+QDAQR/AkACQAJAIAAtAAFBAXFFDQAgAC8BghJBAWoiA0H//wNxIANHDQIgAC8B/hFBAWoiBEH//wNxIARHDQIgAC8B/BEh
BSAALwGAEiEGDAELIAAvAfgRIQQgAC8B+hEhA0EAIQVBACEGCyAAKAIYQQA6ANkBQQAgA0H//wNxIgMgBiACQQEgAkEBSxtqIgIgAyACSRsiAkF/aiIDIAMg
AksbIQICQAJAQQAgBEH//wNxIgMgBSABQQEgAUEBSxtqIgEgAyABSRsiAUF/aiIDIAMgAUsbIgEgACgCGCIALwHSAUcNACACQf//A3EgAC8B0AEiAU0NASAB
IAIgAWsiA0H//wNxaiICQf//A3EiASACRw0CIAEgAC8BjAFPDQIgACAAKALMASADQf//A3EiAkEDdGo2AswBIAAoAsQBIgMvAQYgAmoiAUH//wNxIAFHDQIg
AyABOwEGIAAvAdABIAJqIgJB//8DcSACRw0CIAAgAjsB0AEPCyAAIAIgARDVgICAAA8LIAAgACgCzAEgASACa0H//wNxIgJBA3RrNgLMASAAKALEASIDLwEG
IAJrIgFB//8DcSABRw0AIAMgATsBBiAALwHQASACayICQf//A3EgAkcNACAAIAI7AdABDwsAC84FAQx/I4CAgIAAQTBrIgQkgICAgAAgBCAAIAEgAhCfg4CA
ACAEQSRqIAQQoIOAgAACQCAELQAsRQ0AAkACQAJAAkAgA0EBcQ0ADAELA0AgBCgCJCIFKAIQIgIgBSgCGGoiAyACSQ0DIANBB3ENAyADRQ0DIAQoAigiAUH/
/wNxIgIgAUEQdiIBSw0CAkAgASACRg0AIAVBEGohBiABIAJrIQcgAyACQQN0aiEIQQAhCQNAIAYoAgAiAiAIIAlBA3RqIgooAgAiC2oiDCACSQ0FIAxBB3EN
BSAMRQ0FAkAgAC8BjAEiBUUNACAMQQhqIQ1BACEOA0ACQAJAAkAgDiAFRg0AIAwgDkEDdCICaiIPLQAFQRBxDQIgDSACaiECIA4hAQJAA0ACQCABQQFqIgEg
BUkNACAFIQEMAgsgAi0ABSEDIAJBCGohAiADQRBxRQ0ACwsgASAOSQ0BIAAgBiAKIA8gASAOaxCzgoCAACABIQ4gASAFSQ0DDAQLIAUgBRCIgICAAAALIA4g
ARCKgICAAAALIA5BAWoiDiAFSQ0ACwsgCiALNgIAIAogCikDAEKAgICAgCCENwMAIAlBAWoiCSAHRw0ACwsgBEEkaiAEEKCDgIAAIAQtACwNAAwECwsDQCAE
KAIkIgMoAhAiAiADKAIYaiIOIAJJDQIgDkEHcQ0CIA5FDQIgBCgCKCIBQf//A3EiAiABQRB2IgFLDQECQCABIAJGDQAgA0EQaiEDIAEgAmshBSAOIAJBA3Rq
IQIDQCADKAIAIg4gAigCACIPaiIBIA5JDQQgAUEHcQ0EIAFFDQQgACADIAIgASAALwGMARCzgoCAACACQQRqQYACNgIAIAIgDzYCACACQQhqIQIgBUF/aiIF
DQALCyAEQSRqIAQQoIOAgAAgBC0ALEUNAwwACwsgAiABEIqAgIAAAAsACyAEQTBqJICAgIAAC44MAgp/AX4jgICAgABBMGsiAiSAgICAAAJAIAAvAdIBIAFB
//8DcWoiA0H//wNxIgQgA0cNACAEIAAvAY4BTw0AIAAgAzsB0gEgACgCxAEiBS8BBCIGQQFqIgNB//8DcSIEIANHDQAgBSgCACIHLwGqAiAEayIEQf//A3Eg
BEcNACAFLwEGIQgCQAJAIAFB//8DcSAEQf//A3FLDQAgBiABQf//A3FqIgNB//8DIANB//8DSRshBCAHIQMMAQsgBygCDCIDRQ0BIAEgBGtB//8DcSEBA0AC
QCABIAMvAaoCIgRLDQAgAUF/aiIEIAFLDQMMAgsgASAEayEBIAMoAgwiAw0ADAILCyACQQA6AAQCQAJAIAcgA0cNACAGIARB//8DcUYNAQsgACgCyAEiASAB
KQMAQoCAgICAIIQ3AwAgAy8BqgIgBEH//wNxTQ0BIAhB//8DcSADLwGoAk8NASADKAIQIgEgAygCGGoiByABSQ0BIAdBB3ENASAHRQ0BIAEgByAEQQN0aiIF
KAIAaiIHIAFJDQEgB0EHcQ0BIAdFDQEgBSAFKQMAQoCAgICAIIQ3AwAgACgCxAEiBSgCACEHCwJAAkAgByADRw0AIAVBADYCCCAFIAg7AQYgBSAEOwEEIAUg
AzYCAAwBCyAAQaABaiEBAkACQCAALwHUASIGDQBBlLPIgAAhCUH4ssiAACEFDAELIAJBCGpBGGogAUEYaigCADYCACACQQhqQRBqIAFBEGopAgA3AwAgAkEI
akEIaiABQQhqKQIANwMAIAJBAToAJCACIAEpAgA3AwggBygCSCAGTQ0CIAcoAhAiCSAHKAJAaiIFIAlJDQIgBUEDcQ0CIAVFDQIgBSAGQSRsaiIFLwEgIgZF
DQIgBUEgaiAGQX9qIgU7AQACQCAFQf//A3ENACAHKAJEIgVBf2oiBiAFSw0DIAcgBjYCRAsgAkEkaiEJIAFCADcDACAAQQA7AdQBIAFBGGpBADYCACABQRBq
QgA3AwAgAUEIakIANwMAIAJBCGohBQsCQCAAKALAAUUNACAALwHWASIKRQ0CIAAoAsQBKAIAIgYoArQBIApNDQIgBigCECILIAYoAqwBaiIHIAtJDQIgB0ED
cQ0CIAdFDQIgByAKQRxsaiIHLwEYIgpFDQIgB0EYaiAKQX9qIgc7AQAgB0H//wNxDQAgBigCsAEiB0F/aiIKIAdLDQIgBiAKNgKwAQsgACgCxAEiB0EANgII
IAcgCDsBBiAHIAQ7AQQgByADNgIAAkAgCS0AAEUNACABIAUpAgA3AgAgAUEYaiIDIAVBGGooAgA2AgAgAUEQaiIEIAVBEGopAgA3AgAgAUEIaiIHIAVBCGop
AgA3AgAgABDAgICAAEH//wNxIgVFDQAgAiAFOwEsIAJBLGoQmoOAgAAgA0EANgIAIARCADcDACAHQgA3AwAgAUIANwMAIABBADsB1AELIAAoAsABIgNFDQAg
AEEANgLAASAAQQA7AdYBAkAgACADKAIMIAMoAhBBACADKAIAIAMtAAhBAXEiARtBACADKAIEIAEbEJGDgIAAIgFB//8DcUUNACACIAE7AS4gAkEuahCbg4CA
AAsgACkDmAEiDEIgiKchBCAMpyEHAkAgAygCECIBRQ0AIAMoAgwhBQJAIAFFDQAgBUGqASAB/AsACyAHIAUgAUEAQQAgBCgCDBGBgICAAICAgIAACwJAIAMt
AAhBAXENACADKAIEIgFFDQAgAygCACEFAkAgAUUNACAFQaoBIAH8CwALIAcgBSABQQBBACAEKAIMEYGAgIAAgICAgAALIAAoApgBIANBFEECQQAgAEGcAWoo
AgBBDGooAgARgYCAgACAgICAAAsgACgCxAEiAS8BBCIEIAEoAgAiAy8BqgJPDQAgAS8BBiIHIAMvAagCTw0AIAMoAhAiASADKAIYaiIDIAFJDQAgA0EHcQ0A
IANFDQAgASADIARBA3RqIgQoAgBqIgMgAUkNACADQQdxDQAgA0UNACAAIAQ2AsgBIAAgAyAHQQN0ajYCzAEgAkEwaiSAgICAAA8LAAvNBAIHfwF+I4CAgIAA
QRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0HT4MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAE
aiIGQY3hyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2
AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTpJDQALQQAhAyAAQQBBAEHQ
ABCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiADQY26yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBkrrI
gABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhq
IAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBUkNAAsgACABLwEAEJ+EgIAAIQULIAJB
EGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAuJBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyAB
QoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMaAgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAg
AhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EuQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAA
IAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEmakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arV
qn83AAAgAUIuNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQxoCAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECIC
Sw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2
ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICA
AICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0A
IAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiA
gIAAAAsAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKyADayIFaiIGIARJDQIgA0HHsciAAGohBwJAAkAgBiAAKAIIIghL
DQAgBiAISw0FAkAgACgCBCAEaiIGQfKxyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgC
ACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAF
QStJDQALIAAgARCMhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALzAUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkACQAJAIAAQyICAgAAi
AEH//wNxRQ0AIAEgADsBBgJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAIAFBiBBqIAFBBmoQyYCA
gABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcMAQQBBACAD
QiCIpygCABGAgICAAICAgIAAIgBFDQACQEHDAEUNACAAQaoBQcMA/AsACyABQsMANwOYECABIAA2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBq
IAFBBmoQyYCAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAE
NgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAh
BQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACAC
QaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBB
ACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC7AKAQh/I4CAgIAAQSBrIgEkgICAgAAgACgCGCICQQA6ANkBAkACQAJAAkAC
QCACLwHSASIDIAAvAfwRIgRJDQAgAyAALwH+ESIFTQ0BCyAALwH4EUF/aiIAQf//A3EiBCAARw0BAkAgAyAETw0AIAJBARDDgICAAAsgAi0A3AEhAAJAAkAg
Ai0A2wFBA3FFDQAgAEEBcUUNAQwECyAAQQFxDQJBACEADAQLIAIoAsgBIgIgAikDAEL//////3ODQoCAgICACIQ3AwBBACEADAMLAkACQAJAAkACQAJAAkAg
AyAFRw0AIAIvAdABIgYgAC8BgBIiB0kNACAGIAAvAYISIghLDQAgByAEckUNASAHDQUgAC8B+hFBf2oiBUH//wNxIAVHIQcMBAsgAyAFSQ0BDAILIAAvAfoR
QX9qIgVB//8DcSIHIAVHDQUCQCAIIAdGDQAgByAFRyEHDAMLIAIQtYOAgAAhACACLQDcASEDIAItANsBIQQCQCAAQf//A3FFDQACQCAEQQNxRQ0AAkAgA0EB
cUUNACACQQA7ANsBDAoLIAIoAsgBIgIgAikDAEL//////3ODQoCAgICACIQ3AwAMCQsgA0EBcUUNCAwGCwJAIARBA3FFDQAgA0EBcQ0HIAIoAsgBIgIgAikD
AEL//////3ODQoCAgICACIQ3AwBBACEADAgLIANBAXENBUEAIQAMBwsgAkEBEMOAgIAACyACLQDcASEAAkAgAi0A2wFBA3FFDQAgAEEBcQ0FIAIoAsgBIgIg
AikDAEL//////3ODQoCAgICACIQ3AwBBACEADAYLIABBAXENA0EAIQAMBQsgB0EBcQ0CIAggBUH//wNxRw0AIAIvAdQBRQ0BIAEgAkGoAWovAQAgAkGqAWot
AABBEHRyIAJBrAFqLQAAELODgIAAIAEtAAhFDQEgASkDAFANAQsgAEEBELeCgIAAIQAgAi0A3AEhAyACLQDbASEEAkAgAEH//wNxRQ0AAkAgBEEDcUUNAAJA
IANBAXFFDQAgAkEAOwDbAQwGCyACKALIASICIAIpAwBC//////9zg0KAgICAgAiENwMADAULIANBAXENAgwECwJAIARBA3FFDQAgA0EBcQ0DIAIoAsgBIgIg
AikDAEL//////3ODQoCAgICACIQ3AwBBACEADAQLIANBAXENAUEAIQAMAwsgAUEAOgAYIAEgBDYCFCABQQA7ARAgAyAEayIAQf//A3EiBCAARw0AAkAgAiAB
QRBqIAQQuYOAgAAiAEH//wNxRQ0AIAItANwBIQMCQCACLQDbAUEDcUUNAAJAIANBAXFFDQAgAkEAOwDbAQwFCyACKALIASICIAIpAwBC//////9zg0KAgICA
gAiENwMADAQLIANBAXENAQwDCyACLwHQASAGRw0AIAIvAdIBIANHDQAgA0F/aiIAQf//A3EgAEcNACACIAA7AdIBIAJBARDDgICAAAJAIAIQwICAgABB//8D
cSIARQ0AIAEgADsBHiABQR5qELuDgIAAIAJBoAFqIgBBGGpBADYCACAAQRBqQgA3AgAgAEEIakIANwIAIABCADcCACACEMCAgIAAQf//A3EiAEUNACAAEIyA
gIAAAAsgAi0A3AEhAAJAIAItANsBQQNxRQ0AIABBAXENAiACKALIASICIAIpAwBC//////9zg0KAgICAgAiENwMAQQAhAAwDCyAAQQFxDQBBACEADAILAAtB
ACEAIAJBADsA2wELIAFBIGokgICAgAAgAAvMBAIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0HT
4MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQY3hyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVq
IgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmn
IQULIAMgBWoiBSADSQ0CIAUhAyAFQTpJDQALQQAhAyAAQQBBAEEoEJ6EgIAAIgVB//8DcQ0AA0AgACgCDCIEQQUgA2siBWoiBiAESQ0CIANBjbrIgABqIQcC
QAJAIAYgACgCCCIISw0AIAYgCEsNBgJAIAAoAgQgBGoiBkGSusiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQg
ACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVq
IgUgA0kNAiAFIQMgBUEFSQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICAgAAAC4QGAwJ/AX4CfyOAgICA
AEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQy4CA
gABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQStBAEEAIANC
IIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSdqQarVqtV6NgAA
IAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIrNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ
y4CAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECAB
QaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABF
DQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8
CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIM
EYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKCADayIF
aiIGIARJDQIgA0H4sciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQaCyyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoA
AAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIin
IgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQShJDQALIAAgARCMhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL3gUDAn8BfgJ/
I4CAgIAAQcAQayIBJICAgIAAAkACQAJAAkAgACgCDBDIgICAACICQf//A3ENACAAKAIMELKAgIAADAELIAEgAjsBBgJAQYAQRQ0AIAFBCGpBqgFBgBD8CwAL
IAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAIAFBiBBqIAFBBmoQzYCAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACAC
EIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcMAQQBBACADQiCIpygCABGAgICAAICAgIAAIgBFDQACQEHDAEUNACAAQaoBQcMA
/AsACyABQsMANwOYECABIAA2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAFBBmoQzYCAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECIC
Sw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2
ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICA
AICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAAgAiAAQQAgABC5gICAAAwBCyABKAKYECIARQ0A
IAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiA
gIAAAAsAC8wEAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYgBEkNAiADQdPgyIAAaiEHAkACQCAGIAAoAggi
CEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjeHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAA
KAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSED
IAVBOkkNAAtBACEDIABBAEEAQSkQnoSAgAAiBUH//wNxDQADQCAAKAIMIgRBBSADayIFaiIGIARJDQIgA0GNusiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0GAkAgACgCBCAEaiIGQZK6yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQVJDQAL
IAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALiQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBF
DQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDPgICAAEH//wNxDgMABAEECwJAIAEo
ApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBL0EAQQAgA0IgiKcoAgARgICAgACAgICAACIC
RQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBJ2pCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arV
qn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEM+AgIAAQf//A3EOAwAE
AQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGI
EGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsg
BCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQ
uYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyAB
QcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSwgA2siBWoiBiAESQ0CIANBoLLI
gABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHMssiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIE
IAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEF
CyADIAVqIgUgA0kNAiAFIQMgBUEsSQ0ACyAAIAEQjISAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC3YBA38gACgCGC8B0AEiAUEHcSECIAFB
A3YhAwJAIAFB/wNLDQAgACADaiIAIAJB+LjIgABqLQAAIAAtADhyOgA4DwsCQCADQUBqIgEgACgCNE8NACAAKAIwIAFqIgAgAkH4uMiAAGotAAAgAC0AAHI6
AAAPCwALiQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJA
AkACQAJAAkAgAUGIEGogABDSgICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBB
ACkD4PrJgAAiA6dBLUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq
1ap/NwAAIAJBJWpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLTcDmBAgASACNgKUECABQdyVx4AANgKQ
ECABIAM3A4gQAkACQCABQYgQakEIaiAAENKAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAg
ASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAg
AEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQ
gICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAg
AkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABB
ACEDAkACQAJAA0AgACgCDCIEQSogA2siBWoiBiAESQ0CIANBzLLIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH2ssiAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEqSQ0ACyAAIAEQjISAgAAhBQsgAkEQaiSAgICAACAF
DwsACyAGIAgQiICAgAAAC4QBAQV/AkACQCAAKAIYIgEvAdIBIgIgAC8B/BFHDQAgAS8B0AEiAyAALwGAEiIESQ0AIAMgAC8BghJNDQELIABBARDUgICAAA8L
IAEtANkBIQUgASAEIAIQ1YCAgAAgAEEBENaAgIAAIAAoAhggAyACENWAgIAAIAAoAhggBUEBcToA2QEL5gsCCn8BfiOAgICAAEEwayICJICAgIAAIAAoAhhB
ADoA2QEgACgCGCIDIAMvAdIBIgQgBEEAIAAvAfwRIgAgBCAASRtrQf//A3EiACABQQEgAUEBSxsiASAAIAFJGyIBazsB0gEgAygCxAEiBS8BBiEGIAUoAgAh
BwJAAkACQCAFLwEEIgggAUkNACAIIAFrIQQgByEADAELIAcoAggiAEUNASABIAhrQf//A3EhAQNAAkAgASAALwGqAiIESw0AIAQgAWshBAwCCyABIARrIQEg
ACgCCCIADQAMAgsLIAJBADoABAJAAkAgByAARw0AIAggBEH//wNxRg0BCyADKALIASIBIAEpAwBCgICAgIAghDcDACAEQf//A3EiBSAALwGqAk8NASAGQf//
A3EgAC8BqAJPDQEgACgCECIBIAAoAhhqIgcgAUkNASAHQQdxDQEgB0UNASABIAcgBUEDdGoiBSgCAGoiByABSQ0BIAdBB3ENASAHRQ0BIAUgBSkDAEKAgICA
gCCENwMAIAMoAsQBIgUoAgAhBwsCQAJAIAcgAEcNACAFQQA2AgggBSAGOwEGIAUgBDsBBCAFIAA2AgAMAQsgA0GgAWohAQJAAkAgAy8B1AEiCA0AQZSzyIAA
IQlB+LLIgAAhBQwBCyACQQhqQRhqIAFBGGooAgA2AgAgAkEIakEQaiABQRBqKQIANwMAIAJBCGpBCGogAUEIaikCADcDACACQQE6ACQgAiABKQIANwMIIAco
AkggCE0NAiAHKAIQIgkgBygCQGoiBSAJSQ0CIAVBA3ENAiAFRQ0CIAUgCEEkbGoiBS8BICIIRQ0CIAVBIGogCEF/aiIFOwEAAkAgBUH//wNxDQAgBygCRCIF
QX9qIgggBUsNAyAHIAg2AkQLIAJBJGohCSABQgA3AwAgA0EAOwHUASABQRhqQQA2AgAgAUEQakIANwMAIAFBCGpCADcDACACQQhqIQULAkAgAygCwAFFDQAg
Ay8B1gEiCkUNAiADKALEASgCACIIKAK0ASAKTQ0CIAgoAhAiCyAIKAKsAWoiByALSQ0CIAdBA3ENAiAHRQ0CIAcgCkEcbGoiBy8BGCIKRQ0CIAdBGGogCkF/
aiIHOwEAIAdB//8DcQ0AIAgoArABIgdBf2oiCiAHSw0CIAggCjYCsAELIAMoAsQBIgdBADYCCCAHIAY7AQYgByAEOwEEIAcgADYCAAJAIAktAABFDQAgASAF
KQIANwIAIAFBGGoiACAFQRhqKAIANgIAIAFBEGoiBCAFQRBqKQIANwIAIAFBCGoiByAFQQhqKQIANwIAIAMQwICAgABB//8DcSIFRQ0AIAIgBTsBLCACQSxq
EJqDgIAAIABBADYCACAEQgA3AwAgB0IANwMAIAFCADcDACADQQA7AdQBCyADKALAASIARQ0AIANBADYCwAEgA0EAOwHWAQJAIAMgACgCDCAAKAIQQQAgACgC
ACAALQAIQQFxIgEbQQAgACgCBCABGxCRg4CAACIBQf//A3FFDQAgAiABOwEuIAJBLmoQm4OAgAALIAMpApgBIgxCIIinIQQgDKchBwJAIAAoAhAiAUUNACAA
KAIMIQUCQCABRQ0AIAVBqgEgAfwLAAsgByAFIAFBAEEAIAQoAgwRgYCAgACAgICAAAsCQCAALQAIQQFxDQAgACgCBCIBRQ0AIAAoAgAhBQJAIAFFDQAgBUGq
ASAB/AsACyAHIAUgAUEAQQAgBCgCDBGBgICAAICAgIAACyADKAKYASAAQRRBAkEAIANBnAFqKAIAQQxqKAIAEYGAgIAAgICAgAALIAMoAsQBIgEvAQQiBCAB
KAIAIgAvAaoCTw0AIAEvAQYiByAALwGoAk8NACAAKAIQIgEgACgCGGoiACABSQ0AIABBB3ENACAARQ0AIAEgACAEQQN0aiIEKAIAaiIAIAFJDQAgAEEHcQ0A
IABFDQAgAyAENgLIASADIAAgB0EDdGo2AswBIAJBMGokgICAgAAPCwALmg4CCn8BfiOAgICAAEHQAGsiAySAgICAAAJAIAFB//8DcSAALwGMAU8NACACQf//
A3EgAC8BjgFPDQACQAJAAkACQCACQf//A3EiBCAALwHSASIFSQ0AIAQgBUsNASAAKALEASIGLwEEIQcgBigCACEEIAYhBQwDCyAAKALEASIGKAIAIQQCQCAF
IAJrIgVB//8DcSAGLwEEIghLDQAgCCAFayEHDAILIAQoAggiBEUNAyAFIAhrQf//A3EhBQNAAkAgBSAELwGqAiIISw0AIAggBWshBwwDCyAFIAhrIQUgBCgC
CCIEDQAMBAsLIAAoAsQBIgYvAQQiB0EBaiIEQf//A3EiCCAERw0CIAYoAgAiBC8BqgIgCGsiCEH//wNxIAhHDQICQAJAIAIgBWsiBUH//wNxIAhB//8DcUsN
ACAHIAVB//8DcWoiBUH//wMgBUH//wNJGyEHDAELIAQoAgwiBEUNAyAFIAhrQf//A3EhBQNAAkAgBSAELwGqAiIISw0AIAVBf2oiByAFSw0FDAILIAUgCGsh
BSAEKAIMIgQNAAwECwsgA0EBOgAkIANBADoAICADQQA6ABQgA0EYaiEFDAELIANBAToAECADQQA6AAwgA0EAOgAAIANBBGohBQsgBSgCCCEJIAAgAjsB0gEg
ACABOwHQAQJAAkAgBigCACIIIARHDQAgBi8BBEH//wNxIAdB//8DcUcNACAGLwEGIAFB//8DcUYNAQsgACgCyAEiBSAFKQMAQoCAgICAIIQ3AwAgB0H//wNx
IgYgBC8BqgJPDQEgAUH//wNxIAQvAagCTw0BIAQoAhAiBSAEKAIYaiIIIAVJDQEgCEEHcQ0BIAhFDQEgBSAIIAZBA3RqIgYoAgBqIgggBUkNASAIQQdxDQEg
CEUNASAGIAYpAwBCgICAgIAghDcDACAAKALEASIGKAIAIQgLAkACQCAIIARHDQAgBiAJNgIIIAYgATsBBiAGIAc7AQQgBiAENgIADAELIABBoAFqIQUCQAJA
IAAvAdQBIgINAEGUs8iAACEKQfiyyIAAIQYMAQsgA0EoakEYaiAFQRhqKAIANgIAIANBKGpBEGogBUEQaikCADcDACADQShqQQhqIAVBCGopAgA3AwAgA0EB
OgBEIAMgBSkCADcDKCAIKAJIIAJNDQIgCCgCECIKIAgoAkBqIgYgCkkNAiAGQQNxDQIgBkUNAiAGIAJBJGxqIgYvASAiAkUNAiAGQSBqIAJBf2oiBjsBAAJA
IAZB//8DcQ0AIAgoAkQiBkF/aiICIAZLDQMgCCACNgJECyADQcQAaiEKIAVCADcDACAAQQA7AdQBIAVBGGpBADYCACAFQRBqQgA3AwAgBUEIakIANwMAIANB
KGohBgsCQCAAKALAAUUNACAALwHWASILRQ0CIAAoAsQBKAIAIgIoArQBIAtNDQIgAigCECIMIAIoAqwBaiIIIAxJDQIgCEEDcQ0CIAhFDQIgCCALQRxsaiII
LwEYIgtFDQIgCEEYaiALQX9qIgg7AQAgCEH//wNxDQAgAigCsAEiCEF/aiILIAhLDQIgAiALNgKwAQsgACgCxAEiCCAJNgIIIAggATsBBiAIIAc7AQQgCCAE
NgIAAkAgCi0AAEUNACAFIAYpAgA3AgAgBUEYaiIEIAZBGGooAgA2AgAgBUEQaiIIIAZBEGopAgA3AgAgBUEIaiIBIAZBCGopAgA3AgAgABDAgICAAEH//wNx
IgZFDQAgAyAGOwFMIANBzABqEJqDgIAAIARBADYCACAIQgA3AwAgAUIANwMAIAVCADcDACAAQQA7AdQBCyAAKALAASIERQ0AIABBADYCwAEgAEEAOwHWAQJA
IAAgBCgCDCAEKAIQQQAgBCgCACAELQAIQQFxIgUbQQAgBCgCBCAFGxCRg4CAACIFQf//A3FFDQAgAyAFOwFOIANBzgBqEJuDgIAACyAAKQOYASINQiCIpyEI
IA2nIQYCQCAEKAIQIgVFDQAgBCgCDCEBAkAgBUUNACABQaoBIAX8CwALIAYgASAFQQBBACAIKAIMEYGAgIAAgICAgAALAkAgBC0ACEEBcQ0AIAQoAgQiBUUN
ACAEKAIAIQECQCAFRQ0AIAFBqgEgBfwLAAsgBiABIAVBAEEAIAgoAgwRgYCAgACAgICAAAsgACgCmAEgBEEUQQJBACAAQZwBaigCAEEMaigCABGBgICAAICA
gIAACyAAKALEASIFLwEEIgggBSgCACIELwGqAk8NACAFLwEGIgYgBC8BqAJPDQAgBCgCECIFIAQoAhhqIgQgBUkNACAEQQdxDQAgBEUNACAFIAQgCEEDdGoi
CCgCAGoiBCAFSQ0AIARBB3ENACAERQ0AIAAgCDYCyAEgACAEIAZBA3RqNgLMASADQdAAaiSAgICAAA8LAAuCDgIMfwF+I4CAgIAAQSBrIgIkgICAgAACQAJA
IAFFDQAgACgCGCIDLwHSASIEIAAvAfwRSQ0AIAQgAC8B/hEiBUsNACADLwHQASIGIAAvAYASIgdJDQAgBiAALwGCEiIISw0AQQAhCQJAIAcNACAALwH6EUF/
aiIGQf//A3EiByAGRw0CIAggB08hCQsgBSAEayIFQf//A3EiBiAFRw0BIAZBAWoiCEH//wNxIAhHDQEgAygCxAEiBy8BBCIKQQFqIgVB//8DcSIGIAVHDQEg
BygCACIFLwGqAiAGayIGQf//A3EgBkcNASAHLwEGIQsCQAJAIAhBf2oiB0H//wNxIAZB//8DcUsNACAKIAdB//8DcWoiBkH//wMgBkH//wNJGyEKDAELIAUo
AgwiBUUNAiAHIAZrQf//A3EhBgNAAkAgBiAFLwGqAiIHSw0AIAZBf2oiCiAGSw0EDAILIAYgB2shBiAFKAIMIgUNAAwDCwsgAkEAOgAMAkACQAJAAkAgAygC
UCIHRQ0AIAMgBygCADYCUAwBC0EZIQZBAA0BIANBwABqQQxBAiABEI+AgIAAIgdFDQEgB0Kq1arVqtWq1ap/NwAAIAdBCGpBqtWq1Xo2AAAgB0EDcQ0ECyAH
QarVqtV6NgIIIAcgCzsBBiAHIAo7AQQgByAFNgIAIAdBADoACAJAIANB7ABqIANBEGogBxCTgICAACIGQf//A3ENACABIAhB//8DcSIIIAEgCEkbIQwMAgsg
B0Kq1arVqtWq1ap/NwIAIAdBCGpBqtWq1Xo2AgAgByADKAJQNgIAIAMgBzYCUAsgAiAGOwEOIAJBDmoQioSAgAAACwNAIAcvAQQiCyAHKAIAIgEvAaoCIgZP
DQIgBy8BBiINIAEvAagCTw0CIAEoAhAiBSABKAIYaiIDIAVJDQIgA0EHcQ0CIANFDQIgBSADIAtBA3RqIgMoAgBqIgogBUkNAiAKQQdxDQIgCkUNAiABQRBq
IQoCQAJAAkACQCAIIAxNDQACQCAMIAtLDQAgCyAMayEFDAILIAEoAggiAUUNBiAMIAtrIQUDQAJAIAUgAS8BqgIiBksNACAGIAVrIQUMAwsgBSAGayEFIAEo
AggiAQ0ADAcLCyAAIAogAxC2g4CAACAHKAIAIgYoAhAiBSADKAIAaiIBIAVJDQUgAUEHcQ0FIAFFDQUgAC8BghJBAWoiC0H//wNxIgUgC0cNBQJAAkAgAC8B
gBIiCyAFSw0AIAUgBi8BqAIiCksNASAAKAIYIAZBEGogAyABIAtBA3RqIAUgC2sQs4KAgAAMAwsgCyAFEIqAgIAAAAsgBSAKEIiAgIAAAAsgBUH//wNxIgsg
Bk8NBCANIAEvAagCTw0EIAEoAhAiBiABKAIYaiIFIAZJDQQgBUEHcQ0EIAVFDQQgBiAFIAtBA3RqIgUoAgBqIgsgBkkNBCALQQdxDQQgC0UNBCAAIAogAxC2
g4CAACAAIAFBEGoiCyAFELaDgIAAAkACQAJAIAlFDQAgBSAFKQMAQv////9vgzcDACADIAMpAwBC/////2+DNwMAIAUgBSkDAEL/////X4M3AwAgAyADKQMA
Qv////9fgyIONwMAIAEgBygCACIGRw0BIAMgBSkDADcDACAFIA43AwAMAwsgASAHKAIAIgZGDQELIAAvAYISQQFqIgFB//8DcSIKIAFHDQUgBkEQaiALIAMg
BSAALwGAEiAKEJeDgIAAIgFB//8DcUUNASABQWNqQf//A3EiAUEHSw0FIAJBEGogACgCGCAHKAIAIAFBAnRBzP7JgABqKAIAEJWDgIAAIAIvARQOHQIFBQUF
BQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQsgAC8BghIgAC8BgBIiAWsiC0H//wNxIgogC0cNBCAKQQFqIgtB//8DcSIKIAtHDQQgBkEQaiAFIAEgAyABIAoQ
t4OAgAALIAcvAQQiBiAHKAIAIgEvAaoCTw0DIAcvAQYgAS8BqAJPDQMgASgCECIFIAEoAhhqIgEgBUkNAyABQQdxDQMgAUUNAyAFIAEgBkEDdGoiBigCAGoi
ASAFSQ0DIAFBB3ENAyABRQ0DIAYgBikDAEKAgICAgCCENwMAIAhBf2ohCCAHKAIAIQECQAJAIAcvAQQiBUUNACAFQX9qIQUMAQsCQCABKAIIIgFFDQBBASEF
A0ACQCAFIAEvAaoCIgZLDQAgBiAFayEFDAMLIAUgBmshBSABKAIIIgENAAsLIAJBADoAGAwBCyAHQQA6AAggByAFOwEEIAcgATYCACACQQA6ABgLIAgNAAsg
ACgCGCAHEOWAgIAAIAAoAhggAC8BgBIgBBDVgICAACAAKAIYQQA6ANkBCyACQSBqJICAgIAADwsAC5cGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQ
RQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ2ICAgABB//8DcQ4DAAQBBAsCQCAB
KAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTNBAEEAIANCIIinKAIAEYCAgIAAgICAgAAi
AkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQS9qQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcA
ACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCMzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAA
ENiAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTAgA2si
BWoiBiAESQ0CIANBmLPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHIs8iAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwK
AAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCI
pyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEwSQ0ACyAAIAEQjISAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC5cGAwJ/AX4C
fyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBq
IAAQ2oCAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTRB
AEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarV
qtV6NgAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNDcDmBAgASACNgKUECABQdyVx4AANgKQ
ECABIAM3A4gQAkACQCABQYgQakEIaiAAENqAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAg
ASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAg
AEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQ
gICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAg
AkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABB
ACEDAkACQAJAA0AgACgCDCIEQTEgA2siBWoiBiAESQ0CIANByLPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH5s8iAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUExSQ0ACyAAIAEQjISAgAAhBQsgAkEQaiSAgICAACAF
DwsACyAGIAgQiICAgAAAC5cGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEg
AUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ3ICAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICA
gAAMAQtBAA0AQQApA+D6yYAAIgOnQTRBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJB
CGpCqtWq1arVqtWqfzcAACACQTBqQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFC
NDcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAENyAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3
A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEo
ArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsg
AS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEE
IAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1
AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTEgA2siBWoiBiAESQ0CIANB+bPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsN
BQJAIAAoAgQgBGoiBkGqtMiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiAC
IAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUExSQ0ACyAA
IAEQjISAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwAL
IAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ3oCAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAg
ACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83
AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arV
qtWqfzcAACABQjA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDegICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQ
IgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAi
ADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGA
gIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBF
DQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQ
iICAgAAACwALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEtIANrIgVqIgYgBEkNAiADQaq0yIAAaiEHAkACQCAGIAAoAggi
CEsNACAGIAhLDQUCQCAAKAIEIARqIgZB17TIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAA
KAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSED
IAVBLUkNAAsgACABEIyEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAv4BgMBfwF+An8jgICAgABBwBBrIgIkgICAgAACQAJAAkACQAJAAkAC
QCABQQNxDgQBAAMFAQsgAEHIocCAAEEKEOCAgIAAIQEMAQsgAEG7ocCAAEEMEOCAgIAAIQELIAFB//8DcUUNACACIAE7AQYCQEGAEEUNACACQQhqQaoBQYAQ
/AsACyACQoAQNwKQECACQbjewYAANgKIECACIAJBCGo2AowQAkACQCACQYgQaiACQQZqEOGAgIAAQf//A3EOAwAEAQQLIAIoApQQIgEgAigCkBAiAEsNBCAC
KAKMECABEICAgIAADAELQQANAEEAKQPg+smAACIDp0E6QQBBACADQiCIpygCABGAgICAAICAgIAAIgFFDQAgAUKq1arVqtWq1ap/NwAAIAFBEGpCqtWq1arV
qtWqfzcAACABQQhqQqrVqtWq1arVqn83AAAgAUE4akGq1X47AAAgAUEwakKq1arVqtWq1ap/NwAAIAFBKGpCqtWq1arVqtWqfzcAACABQSBqQqrVqtWq1arV
qn83AAAgAUEYakKq1arVqtWq1ap/NwAAIAJCOjcDmBAgAiABNgKUECACQdyVx4AANgKQECACIAM3A4gQAkACQCACQYgQakEIaiACQQZqEOGAgIAAQf//A3EO
AwAEAQQLIAIoApwQIgEgAigCmBAiAEsNAiACQgA3A5gQIAIoApQQIQQgAkGq1arVejYClBAgAiAANgK8ECACIAE2ArgQIAIgBDYCtBAgAkGkEGogAkG0EGog
AkGIEGoQuICAgAAgAiACKAK8ECIBNgKYECACIAIoArQQIgA2ApQQIAIgAigCuBA2ApwQAkAgAUUNACACKAKIECEEIAIoAowQIQUCQCABRQ0AIABBqgEgAfwL
AAsgBCAAIAFBAEEAIAUoAgwRgYCAgACAgICAAAsgAi8BrBANASACKAKkECIAIAIoAqgQIgEQgICAgAAgAUUNAQJAIAFFDQAgAEGqASAB/AsACyACIAAgAUEA
IAIQuYCAgAAMAQsgAigCmBAiAUUNACACKAKIECEEIAIoAowQIQUgAigClBAhAAJAIAFFDQAgAEGqASAB/AsACyAEIAAgAUEAQQAgBSgCDBGBgICAAICAgIAA
CyACQcAQaiSAgICAAA8LIAEgABCIgICAAAsACyABIAAQiICAgAAAC5YGAQp/I4CAgIAAQRBrIgMkgICAgABBACEEAkAgAkUNACAAKAJEIQQCQAJAAkACQCAC
QYCAwABJDQAgBEEANgIEIAMgACgCRCIFKAIEIgZBgIDAAGoiByAGSSIIOgAIIAMgCDoAByAHIAZJDQECQCAFKAIIIgQgB08NAANAQX8gBEEBdiAEakHAAGoi
BiAGIARJGyIEIAdJDQALIAUgACkCACAEEIKCgIAAIgRB//8DcQ0FIAUoAgQiBkGAgMAAaiIHIAZJIQgLIAhBAXENAyAHIAUoAghLDQMgASACaiIEQYCAQGoh
AiAFIAc2AgQCQCAFKAIAIAZqIgYgBE8NACACIAZBgIDAAGpJDQQLAkBBgIDAAEUNACAGIAJBgIDAAPwKAAALQQAhBAwECyAEKAIEIgYgAmoiByAGSQ0CAkAg
B0GBgMAASQ0AAkAgBiAHQYCAQGoiBUsNACAEQQA2AgQMAQsgBCgCACEIIAYgBWsiCUEDcSEKQQAhBAJAIAJB/P8/Sw0AIAggBWohCyAJQXxxIQxBACEEA0Ag
CCAEaiIGIAsgBGoiBy0AADoAACAGQQFqIAdBAWotAAA6AAAgBkECaiAHQQJqLQAAOgAAIAZBA2ogB0EDai0AADoAACAMIARBBGoiBEcNAAsLAkAgCkUNACAI
IARqIQRBACACa0EDcSEGA0AgBCAEIAVqLQAAOgAAIARBAWohBCAGQX9qIgYNAAsLIAAoAkQgCTYCBAsgAyAAKAJEIgUoAgQiBiACaiIHIAZJIgg6AAwgAyAI
OgALIAcgBk8NAQtBGSEEDAILAkAgBSgCCCIEIAdPDQADQEF/IARBAXYgBGpBwABqIgYgBiAESRsiBCAHSQ0ACyAFIAApAgAgBBCCgoCAACIEQf//A3ENAiAF
KAIEIgYgAmoiByAGSSEICyAIQQFxDQAgByAFKAIISw0AIAUgBzYCBAJAIAUoAgAgBmoiBCABIAJqTw0AIAEgBCACakkNAQsCQCACRQ0AIAQgASAC/AoAAAtB
ACEEDAELAAsgA0EQaiSAgICAACAEC80EAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEExIANrIgVqIgYgBEkNAiADQdy5yIAA
aiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjbrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAG
SQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsg
AyAFaiIFIANJDQIgBSEDIAVBMUkNAAtBACEDIABBAEEAQcAAEJ6EgIAAIgVB//8DcQ0AA0AgACgCDCIEQQUgA2siBWoiBiAESQ0CIANBjbrIgABqIQcCQAJA
IAYgACgCCCIISw0AIAYgCEsNBgJAIAAoAgQgBGoiBkGSusiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAE
NgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUg
A0kNAiAFIQMgBUEFSQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICAgAAAC4MOAwh/AX4DfyOAgICAAEEw
ayIBJICAgIAAIABBADoAKAJAIAAtACRBAXFFDQAgACgCHCICRQ0AIABBGGoiAyACNgIAIAMgAikCmAEQ44CAgAAgAUEYakGo38GAACADKAIAIgMvAYwBQQEQ
koCAgAACQAJAIAEvAShFDQBB1wEhAgwBCyABLwEiIgJFDQELAkACQCADLwGOASIEDQBBACEFDAELIARBf2pB//8DcSACbkEBaiEFCwJAIAMoAlQiAkUNACAD
KAIsIQYgAygCMCEHA0ACQCACKAIUIgRBgIAkTQ0AIAIoAhAhCAJAIARFDQAgCEGqASAE/AsACyAGIAggBEEQQQAgBygCDBGBgICAAICAgIAACyACKAIMIgIN
AAsLIAWtQoCAJH4iCUIgiKcNACABQQI6AAwgASAJpzYCCCADQSxqIAFBCGoQ5ICAgAAgA0EANgI8IAFBAjoAFCABIAVBsAJsNgIQIANBGGogAUEQahDkgICA
ACADQQA2AigCQAJAAkAgAygCNCICRQ0AA0AgAkF8aiIIKAIAIgRBB00NAgJAIARBeGoiBEUNACAIQQhqQQAgBPwLAAsgAigCACICDQALCyABQRhqIANBEGog
AyADLwGMASADLwGOARCQgICAACABLwEkRQ0BDAILQQggBBCKgICAAAALIAMgASgCIDYCXCADIAEpAxg3AlQgAyADLwGOATYCaCADKAJ0IgJBgICAgARPDQAg
AygCcCEIAkACQCACDQBBqtWq1XohAgwBCyADKAJsIgJBA3ENAQsCQCAIRQ0AA0AgAygCVCIGRQ0CIAIoAgAiBCAGNgIAIARBAToACCAEQQA2AQQgAkEEaiEC
IAhBf2oiCA0ACwsgAygCgAFBADoACCADQQA6AJABIAMoAlQiBEUNACADKALEASIGKAIAIgIgBEcNACAGLwEGDQAgBi8BBA0AIAIvAaoCRQ0AIAIvAagCRQ0A
IAIoAhAiCCACKAIYaiIEIAhJDQAgBEEHcQ0AIARFDQAgCCAEKAIAaiIHIAhJDQAgB0EHcQ0AIAdFDQAgA0GgAWohAgJAIANBwAFqKAIAIghFDQAgAykCmAEi
CUIgiKchBSAJpyEKAkAgCCgCECILRQ0AIAgoAgwhDAJAIAtFDQAgDEGqASAL/AsACyAKIAwgC0EAQQAgBSgCDBGBgICAAICAgIAACwJAIAgtAAhBAXENACAI
KAIEIgtFDQAgCCgCACEMAkAgC0UNACAMQaoBIAv8CwALIAogDCALQQBBACAFKAIMEYGAgIAAgICAgAALIAogCEEUQQJBACAFQQxqKAIAEYGAgIAAgICAgAAL
IANBAToA2AEgAyAHNgLMASADIAQ2AsgBIAMgBjYCxAEgA0IANwHQASACQgA3AwAgA0IANwPgASADQQA2ANkBIAJBCGpCADcDACACQRBqQgA3AwAgAkEYakIA
NwMAIAJBIGpBADYCACADQegBakIANwMAIANB8AFqQgA3AwAgA0H4AWpCADcDACADQYACakIANwMAIANBiAJqQgA3AwAgA0EAKQOo+cmAADcAtQIgA0HCAmpC
ADcAACADQgA3AL0CIANBkAJqIQICQCADLQCwAkUNAAJAIAMtAKgCQQFxRQ0AIAMoApQCIQQgAyADKAKQAhDlgICAACADIAQQ5YCAgAALIAMgAy0AygJBAXI6
AMoCCyACQgA3AwAgAkEgakEANgIAIAJBGGpCADcDACACQRBqQgA3AwAgAkEIakIANwMAIABB9hFqQQA6AAAgAEGAEDsB9BEgAEEMakIAPAAAIABChMSAkAM+
AgggAEIANwM4IABBwABqQgA3AwAgAEHIAGpCADcDACAAQdAAakIANwMAIABB2ABqQgA3AwAgAEHgAGpCADcDACAAQegAakIANwMAIABB8ABqQgA3AwAgAEEE
aiAAQRRqMQAAPAAAIAAgADUCED4CAAJAIAAoAjQiAkUNACAAKAIwQQAgAvwLAAsgACgCLCICQX9qIgQgAksNAEE5IQNBCCECAkADQCACIARPDQECQAJAIAJB
/wNLDQAgACADaiIEIAQtAABBAXI6AAAMAQsgA0GIf2ogACgCNE8NAyAAKAIwIANqQYh/aiIEIAQtAABBAXI6AAALIAJBCGoiBCACSQ0CIANBAWohAyAEIQIg
ACgCLCIIQX9qIgQgCEsNAgwACwsgAEEAOgCEEiAAQQA2ApABIABBADYChAEgAEIANwPoESAAQQA7AfwRIAAvAfgRQX9qIgJB//8DcSACRw0AIABBADsBgBIg
ACACOwH+ESAALwH6EUF/aiICQf//A3EgAkcNACAAIAI7AYISIABB9hFqIgIgAi0AAEECcjoAACABQTBqJICAgIAADwsAC8ICAQd/AkAgAC0ADCICQQJxDQAg
AEGq1arVejYCCA8LIAAgAkH9AXE6AAwgACgCCCECIABBqtWq1Xo2AggCQCACRQ0AIAFCIIinIQMgAachBAJAIAJBwAFqKAIAIgBFDQAgAikCmAEiAUIgiKch
BSABpyEGAkAgACgCECIHRQ0AIAAoAgwhCAJAIAdFDQAgCEGqASAH/AsACyAGIAggB0EAQQAgBSgCDBGBgICAAICAgIAACwJAIAAtAAhBAXENACAAKAIEIgdF
DQAgACgCACEIAkAgB0UNACAIQaoBIAf8CwALIAYgCCAHQQBBACAFKAIMEYGAgIAAgICAgAALIAYgAEEUQQJBACAFQQxqKAIAEYGAgIAAgICAgAALIAIQhICA
gAAgBCACQdACQQNBACADQQxqKAIAEYGAgIAAgICAgAALC8YEAQV/AkACQAJAAkACQAJAAkACQAJAAkAgAS0ABA4EAwABCQMLIAAoAggiAUUNBEEAIQIgASED
A0AgA0F8aigCACIEQXhqIgUgBEsNCSACIAVqIgQgAkkNCSAEIQIgAygCACIDDQAMAgsLIAEoAgAhBgJAAkAgACgCCCIBDQBBACEEDAELQQAhAiABIQMDQCAD
QXxqKAIAIgRBeGoiBSAESw0JIAIgBWoiBCACSQ0JIAQhAiADKAIAIgMNAAsLIAYgBCAGIARJGyEECyAERQ0BIARBCGoiBSAESQ0GIAFFDQMgASgCACIDDQQg
ASECDAULIAAoAgghAQsgAUUNACAAKAIAIQMgACgCBCEEA0AgAUF8aiECIAEoAgAhASADIAIgAigCAEECQQAgBCgCDBGBgICAAICAgIAAIAENAAsLIABCADcC
CA8LIABBADYCDA8LA0AgACgCACABQXxqIgEgASgCAEECQQAgACgCBCgCDBGBgICAAICAgIAAIAMiAiEBIAIoAgAiAw0ACwsgACACNgIIIABBADYCDAJAIAJB
fGoiASgCACICIAVGDQACQCAAKAIAIAEgAkECIAVBACAAKAIEKAIEEYKAgIAAgICAgABBAXFFDQAgASAFNgIADwsgACgCACAFQQJBACAAKAIEKAIAEYCAgIAA
gICAgAAiA0UNACAAKAIAIAEgAkECQQAgACgCBCgCDBGBgICAAICAgIAAIANBA3ENASADQQA2AgQgAyAFNgIAIAAgA0EEajYCCAsPCwAL5AIBBH8CQCABIAAo
AoABRg0AIAAtAHxBAXENACAAQewAaiECIABBAToAfAJAAkACQAJAAkACQCAAKAJ4IgNFDQAgAy0AACIEQQlJDQEgBEERSQ0CIARBIU8NBiACIAEgAxDQg4CA
ACEEDAMLIAAoAnQiA0GAgICABE8NBUEAIQQCQAJAIAMNAEGq1arVeiEDQQAhBQwBCyAAKAJsIgNBA3ENBiAAKAJwIQULIAVFDQJBACEEA0AgASADKAIARg0E
IANBBGohAyAFIARBAWoiBEcNAAtBACEEDAILIAIgASADENGDgIAAIQQMAQsgAiABIAMQ0oOAgAAhBAsgAC0AfEEBcUUNAiAAQQA6AHwgBEEBcQ0BDwsgAiAE
ENODgIAAIAAtAHxBAXFFDQEgAEEAOgB8CyABQqrVqtWq1arVqn83AgAgAUEIakGq1arVejYCACABIAAoAlA2AgAgACABNgJQDwsAC4kGAwJ/AX4CfyOAgICA
AEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ54CA
gABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTBBAEEAIANC
IIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arV
qn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpB
CGogABDngICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2
ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEF
AkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJB
qgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEA
IAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEt
IANrIgVqIgYgBEkNAiADQde0yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBhLXIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAH
IAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgi
CUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBLUkNAAsgACABEIyEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAuqBgMC
fwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCAB
QYgQaiAAEOmAgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACID
p0E7QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkE3
akGq1arVejYAACACQTBqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI7
NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ6YCAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcD
mBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgC
tBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyAB
LwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQg
ASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC7UC
Agd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBOCADayIFaiIGIARJDQIgA0GEtciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0F
AkAgACgCBCAEaiIGQby1yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIg
BTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQThJDQALIAAg
ARCMhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALqgYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsg
AUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDrgICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAA
IAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBO0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcA
ACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBN2pBqtWq1Xo2AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcA
ACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCOzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAA
EOuAgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTggA2si
BWoiBiAESQ0CIANBvLXIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH0tciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwK
AAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCI
pyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE4SQ0ACyAAIAEQjISAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC6oGAwJ/AX4C
fyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBq
IAAQ7YCAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTtB
AEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTdqQarV
qtV6NgAAIAJBMGpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjs3A5gQ
IAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDtgICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECAB
KAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECIC
NgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQ
DQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKM
ECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALtQICB38B
fiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE4IANrIgVqIgYgBEkNAiADQfS1yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAA
KAIEIARqIgZBrLbIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIE
IAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBOEkNAAsgACABEIyE
gIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvTBQMBfwF+An8jgICAgABBwBBrIgIkgICAgAACQAJAAkAgAEEMaiABQQEQ74CAgAAiAEH//wNx
RQ0AIAIgADsBBgJAQYAQRQ0AIAJBCGpBqgFBgBD8CwALIAJCgBA3ApAQIAJBuN7BgAA2AogQIAIgAkEIajYCjBACQAJAIAJBiBBqIAJBBmoQ8ICAgABB//8D
cQ4DAAQBBAsCQCACKAKUECIAIAIoApAQIgFNDQAgACABEIiAgIAADAQLIAIoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcMAQQBBACADQiCIpygC
ABGAgICAAICAgIAAIgBFDQACQEHDAEUNACAAQaoBQcMA/AsACyACQsMANwOYECACIAA2ApQQIAJB3JXHgAA2ApAQIAIgAzcDiBACQAJAIAJBkBBqIAJBBmoQ
8ICAgABB//8DcQ4DAAQBBAsgAigCnBAiACACKAKYECIBSw0CIAJCADcDmBAgAigClBAhBCACQarVqtV6NgKUECACIAE2ArwQIAIgADYCuBAgAiAENgK0ECAC
QaQQaiACQbQQaiACQYgQahC4gICAACACIAIoArwQIgA2ApgQIAIgAigCtBAiATYClBAgAiACKAK4EDYCnBACQCAARQ0AIAIoAogQIQQgAigCjBAhBQJAIABF
DQAgAUGqASAA/AsACyAEIAEgAEEAQQAgBSgCDBGBgICAAICAgIAACyACLwGsEA0BIAIoAqQQIgEgAigCqBAiABCAgICAACAARQ0BAkAgAEUNACABQaoBIAD8
CwALIAIgASAAQQAgAhC5gICAAAwBCyACKAKYECIARQ0AIAIoAogQIQQgAigCjBAhBSACKAKUECEBAkAgAEUNACABQaoBIAD8CwALIAQgASAAQQBBACAFKAIM
EYGAgIAAgICAgAALIAJBwBBqJICAgIAADwsgACABEIiAgIAAAAsAC6wKAwJ/AX4BfyAAKAIAIAEgAhCeg4CAAAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAIAFB//8DcSIDQekPSg0AAkAgA0GYeGoOMg4RDxAYBwgREREREREREQkKERERERERERERERERERERERERERERERER
ERERERERBAwFAAsgA0F9ag5DBRAXARAXDBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBcQEBAQEBACEBAQEBAQEBAQEBAQEBAQEBAQEBAQChALIANB
6g9GDRYgA0GAEEYNFiADQZSAAkcNDwwWCyAAKAIAQQFBARDBgICAAAwVCyAAKAIAQQAgAhC+g4CAACIEQf//A3FFDRQMFQsgACgCAEEBIAIQvoOAgAAiBEH/
/wNxRQ0TDBQLIAAoAgBBAiACEL6DgIAAIgRB//8DcUUNEgwTCwJAIAAoAgAiAzUCACADQQRqIgQxAABCIIaEIgVCgIABg0IAUg0AIAQgBUIgiDwAACADIAVC
3//+/w+DPgIADBILIAQgBUIgiDwAACADIAVC3////w+DQiBCACACQQFxIgQbhD4CACADIAMoAhhBmAFqQYQBQdAAIAQbIAMvAfgREMaDgIAAIgRB//8DcQ0S
IANBAkEAEIaBgIAAIANBAUEBEMGAgIAADBELIAAoAgAiAyADLwH0EUG//ANxQcAAQQAgAkEBcRtyOwH0EQwQCyAAKAIAIgMgAy8B9BFBv/wDcUGAAUEAIAJB
AXEbcjsB9BEMDwsgACgCACIDIAMvAfQRQb/8A3FBwAFBACACQQFxG3I7AfQRDA4LIAAoAgAiAyADLwH0EUG//ANxQYACQQAgAkEBcRtyOwH0EQwNCyACQQFx
DQwgACgCAEEAOwGAEiAAKAIAIgQvAfoRQX9qIgNB//8DcSADRw0GIAQgAzsBghIMDAsgACgCACEDIAJBAXENCiADEMGBgIAADAsLIAAoAgAiBC8B9BEgBEH2
EWoiBi0AAEEQdHJBR3EhAyACQQFxDQggBCADOwH0ESAGIANBEHY6AAAMCgsgACgCACIDLwH0ESADQfYRaiIGLQAAQRB0ckFHcSEEIAJBAXENBiADIAQ7AfQR
IAYgBEEQdjoAAAwJCyAAKAIAIgMvAfQRIANB9hFqIgYtAABBEHRyQUdxIQQgAkEBcQ0EIAMgBDsB9BEgBiAEQRB2OgAADAgLIAAoAgAiAy8B9BEgA0H2EWoi
Bi0AAEEQdHJBR3EhBCACQQFxDQIgAyAEOwH0ESAGIARBEHY6AAAMBwsgARDRgoCAAEEBcQ0GCwALIAYgBEEQdjoAACADIARBIHI7AfQRDAQLIAYgBEEQdjoA
ACADIARBGHI7AfQRDAMLIAYgBEEQdjoAACADIARBEHI7AfQRDAILIAYgA0EQdjoAACAEIANBCHI7AfQRDAELIAMoAhgiBEEBOgCMAiAEIAMoAhgvAdABOwH8
ASAEIAMoAhgvAdIBOwH+ASAEIAMoAhgiBikCoAE3AuABIARB+AFqIAZBuAFqKAIANgIAIARB8AFqIAZBsAFqKQIANwIAIARB6AFqIAZBqAFqKQIANwIAIAQg
AygCGC0A2gFBAXE6AIACIAQgAygCGC0A2QFBAXE6AIECIAQgAygCAEEIdkEBcToAggIgBCADKAIYKQC1AjcAgwILQQAhBCAAKAIsIgNFDQAgACABIAIgAxGF
gICAAICAgIAAQQAPCyAEC8wEAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYgBEkNAiADQdPgyIAAaiEHAkAC
QCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjeHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAg
BDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIF
IANJDQIgBSEDIAVBOkkNAAtBACEDIABBAEEAQSwQnoSAgAAiBUH//wNxDQADQCAAKAIMIgRBBSADayIFaiIGIARJDQIgA0GNusiAAGohBwJAAkAgBiAAKAII
IghLDQAgBiAISw0GAkAgACgCBCAEaiIGQZK6yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsg
ACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUh
AyAFQQVJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALhAYDAn8BfgJ/I4CAgIAAQcAQayIBJICA
gIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDygICAAEH//wNxDgMA
BAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBK0EAQQAgA0IgiKcoAgARgICA
gACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBJ2pBqtWq1Xo2AAAgAkEgakKq1arV
qtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQis3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDygICAAEH//wNx
DgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBq
IAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8
CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABB
ACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEoIANrIgVqIgYgBEkNAiAD
Qay2yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB1LbIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYg
BWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIg
CachBQsgAyAFaiIFIANJDQIgBSEDIAVBKEkNAAsgACABEIyEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvTBQMBfwF+An8jgICAgABBwBBr
IgIkgICAgAACQAJAAkAgAEEMaiABQQAQ74CAgAAiAEH//wNxRQ0AIAIgADsBBgJAQYAQRQ0AIAJBCGpBqgFBgBD8CwALIAJCgBA3ApAQIAJBuN7BgAA2AogQ
IAIgAkEIajYCjBACQAJAIAJBiBBqIAJBBmoQ9ICAgABB//8DcQ4DAAQBBAsCQCACKAKUECIAIAIoApAQIgFNDQAgACABEIiAgIAADAQLIAIoAowQIAAQgICA
gAAMAQtBAA0AQQApA+D6yYAAIgOnQcMAQQBBACADQiCIpygCABGAgICAAICAgIAAIgBFDQACQEHDAEUNACAAQaoBQcMA/AsACyACQsMANwOYECACIAA2ApQQ
IAJB3JXHgAA2ApAQIAIgAzcDiBACQAJAIAJBkBBqIAJBBmoQ9ICAgABB//8DcQ4DAAQBBAsgAigCnBAiACACKAKYECIBSw0CIAJCADcDmBAgAigClBAhBCAC
QarVqtV6NgKUECACIAE2ArwQIAIgADYCuBAgAiAENgK0ECACQaQQaiACQbQQaiACQYgQahC4gICAACACIAIoArwQIgA2ApgQIAIgAigCtBAiATYClBAgAiAC
KAK4EDYCnBACQCAARQ0AIAIoAogQIQQgAigCjBAhBQJAIABFDQAgAUGqASAA/AsACyAEIAEgAEEAQQAgBSgCDBGBgICAAICAgIAACyACLwGsEA0BIAIoAqQQ
IgEgAigCqBAiABCAgICAACAARQ0BAkAgAEUNACABQaoBIAD8CwALIAIgASAAQQAgAhC5gICAAAwBCyACKAKYECIARQ0AIAIoAogQIQQgAigCjBAhBSACKAKU
ECEBAkAgAEUNACABQaoBIAD8CwALIAQgASAAQQBBACAFKAIMEYGAgIAAgICAgAALIAJBwBBqJICAgIAADwsgACABEIiAgIAAAAsAC8wEAgd/AX4jgICAgABB
EGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYgBEkNAiADQdPgyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARq
IgZBjeHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYC
ACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBOkkNAAtBACEDIABBAEEAQS0Q
noSAgAAiBUH//wNxDQADQCAAKAIMIgRBBSADayIFaiIGIARJDQIgA0GNusiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0GAkAgACgCBCAEaiIGQZK6yIAA
Tw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAA
IAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQVJDQALIAAgAS8BABCfhICAACEFCyACQRBq
JICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALzAQCB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQAJAA0AgACgCDCIEQTogA2siBWoi
BiAESQ0CIANB0+DIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGN4ciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAAL
IAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIF
Qf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE6SQ0AC0EAIQMgAEEAQQBBBhCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiAD
QY26yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBkrrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYg
BWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIg
CachBQsgAyAFaiIFIANJDQIgBSEDIAVBBUkNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAv0AwIH
fwF+I4CAgIAAQTBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTMgA2siBWoiBiAESQ0CIANB1LbIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJA
IAAoAgQgBGoiBkGHt8iAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2
AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEzSQ0ACyABLQAA
IQMgAkEIakEgakGqfzoAACACQSBqQqrVqtWq1arVqn83AwAgAkEYakKq1arVqtWq1ap/NwMAIAJBEGpCqtWq1arVqtWqfzcDACACQqrVqtWq1arVqn83AwhB
ACEFA0AgAkEIaiAFakEgaiADQQ9xIgZBMHIgBkHXAGogBkEKSRs6AAAgBUF/aiEFIANBD0shBiADQQR2IQMgBg0ACyAAIAJBCGogBWpBIWpBACAFayIDIANB
AC0AwNjJgABBIBCWgICAACEFCyACQTBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALqSYFBH8BfgR/AX4FfyOAgICAAEHAAGsiAiSAgICAAAJAAkAgAC0AhBJB
AXENAAJAAkACQCAAKAIYIgMvAdABIgQgAC8BghIiBUsNACAFQQFqIgVB//8DcSAFRg0BDAILIAAvAfoRIQULIAA1AgAgAEEEajEAAEIghoQhBgJAAkACQAJA
AkACQAJAAkACQAJAAkACQCABQf///wBxQf8BSw0AQQEhBAwBCwJAIARFDQAgBkKAgICAgASDUA0AAkACQCAGQoAEg1ANACADLQDZAUEBcyEHDAELIAVB//8D
cUF/aiIHQf//A3EiCCAHRw0NQQEhByAEIAhHDQAgAygCzAEpAwAiBkICg0IBiKcgBkL8//8Dg1ByIQcLAkAgAygCzAEiCCAHQQFxIglBA3RrIgopAwAiBkKA
gICAgIADg0KAgICAgIACUg0AIARBAkEBIAdBAXEbIglJDQ0gCCAJQQN0ayIKKQMAIQYLIAZCAoNCAFINACAGQvz//wODUA0AIAJBADoACyAGQgKIpyEEAkAC
QCAGp0EBcUUNACAKIAMoAsQBKAIAIgMoAhAiB2siCCAKSw0OIAcgAygCOGoiAyAHSQ0OIANFDQ4gAkEMaiADIAgQo4OAgAACQAJAIAItABQNAEEAIQNBACEI
DAELIAcgAigCDGoiAyAHSQ0PIANBA3ENDyADRQ0PIAIoAhAhCAsgA0UNDiAIRQ0AA0AgBCADLwEAIANBAmotAABBEHRyIgcgAkELahDag4CAAEEBcQ0PIANB
BGohAyAHIQQgCEF/aiIIDQAMAgsLIAQhBwsgByABIAJBC2oQ2oOAgABBAXENACACQQA6ABsCQCABQf7//wBxQY78A0cNACAKKQMAQgKIpyIDQf8BcSADQYD+
/wBxQQd2QaCdwoAAai8BAGoiA0H/9wFLDQUgA0EBdEGgncOAAGovAQAiA0EeTw0GIANBAXRBoY3HgABqLQAAQQFxRQ0OQQFBfiABQf///wBxQY/8A0YbIQMM
CwsgAUH/AXEgAUGA/v8AcUEHdkGgncKAAGovAQBqIgRB//cBSw0GQQEhAwJAIARBAXRBoJ3DgABqLwEAIgRBHk8NACAEQQF0QaCNx4AAai0AAEEEcUUNCwwM
CyAEQR4QiICAgAAACwJAAkACQAJAIAFB/wFxIAFBgP7/AHFBB3ZBoJ3CgABqLwEAaiIDQf/3AUsNACADQQF0QaCdw4AAai8BACIDQR5PDQEgA0EBdEGgjceA
AGovAQBBA3EiBA4EAgMDDwMLIANBgPgBEIiAgIAAAAsgA0EeEIiAgIAAAAsgAC0ABEEgcQ0NIAAoAhgiAC8B0AEiAw0CDAkLIAA1AgAgAEEEajEAAEIghoQh
BiAAKAIYIQMLIAAgATsB6BEgAEEBOgDsESAAQeoRaiABQRB2QR9xIgc6AAAgAiABOwE4IAIgBzoAOiADLQDZAUUNBiAGQoAEg0IAUg0BDAYLIAAoAswBIgVB
eGoiBCkDACIGQoCAgICAgAODQoCAgICAgAJSDQQgA0EBRg0JIAVBcGoiBCkDACEGDAQLIAAQ2YOAgAAiA0H//wNxDQogADUCACAAQQRqMQAAQiCGhCEGDAQL
IANBgPgBEIiAgIAAAAsgA0EeEIiAgIAAAAsgBEGA+AEQiICAgAAACyAGQgKDQgBSDQEgBkL8//8Dg1ANAQJAIAFB/v//AHFBjvwDRw0AAkAgBkICiKciA0H/
AXEgA0GA/v8AcUEHdkGgncKAAGovAQBqIgNB//cBSw0AAkAgA0EBdEGgncOAAGovAQAiBUEeTw0AQQAhAyAFQQF0QaCNx4AAai8BAEEDdkEfcUELRw0JDAIL
IAVBHhCIgICAAAALIANBgPgBEIiAgIAAAAsgACAEIAEQ24OAgAAhAwwGCwJAIAZCAoNQDQAgBCAAKAIYLwHQAWogAC8B+hFPDQAgACAEEM2BgIAACwJAAkAC
QCAEQQFHDQAgACgCGCgCyAEiAyADKQMAQoCAgICAIIQ3AwACQAJAIAAoAhgiBC0AvAJFDQAgBC0AuwIhAyAEQQA7ALsCIAAoAhghBAwBCyAELQC5AiEDCyAB
QSAgBCADQQNxQQJzQQJ0Qbz+yYAAaigCAGotAAAiB0EedEEedUF/SiIDGyEIIAFB////AHFB/wFLDQEgAw0BQdyRx4AAQdyNx4AAIAdBAXEbIAFB////AHFB
AXRqLwEAIQgMAQsgBUH//wNxIAAvAYASayIDQf//A3EiBCADRw0FAkACQAJAAkAgBEECSQ0AIAVB//8DcUF/aiIDQf//A3EiBCADRw0JIAAoAhgiAy8B0AEg
BEcNAyAALQABQQJxRQ0KQQAhBCAFQf//A3EgAC8B+hFGDQEMAgsgACgCGCgCyAEiASABKQMAQoCAgICAIIQ3AwAgAEEAQQAQ3IOAgAAMBAsgAygCyAEiAyAD
KQMAQoCAgIAQhDcDAEEDIQQLIABBACAEENyDgIAAIAAQ2YOAgAAiA0H//wNxDQggACgCGCEDCyADKALIASIDIAMpAwBCgICAgIAghDcDACAAIAFBARDcg4CA
ACAAKAIYIgEvAdABQQFqIgNB//8DcSIEIANHDQUgBCABLwGMAU8NBSABIAEoAswBQQhqNgLMASABKALEASIELwEGQQFqIgNB//8DcSADRw0FIAQgAzsBBiAB
LwHQAUEBaiIDQf//A3EgA0cNBSABIAM7AdABIABBAEECENyDgIAADAELAkACQAJAAkAgBCgCzAEiAykDACIGQiqIp0EDcQ4EAwABAwMLIAAvAfoRQX9qIgFB
//8DcSIHIAFHDQcgBC8B0AEiAUH//wNxIAdPDQIgAUEBakH//wNxIAQvAYwBTw0HIAQgBCgCxAEoAgBBEGogBCgCyAEgA0EIakEBELOCgIAAIAAoAhgiAS8B
0gFFDQIgAS8B0AFBAUsNAiABEN2DgIAAIgEpAwAiBkKAgICAgIADg0KAgICAgIADUQ0BDAILIAQvAdABRQ0GIAMgBkL///////98gzcDACAAKAIYIgEvAdAB
RQ0GIAEgASgCxAEoAgBBEGogASgCyAEgASgCzAFBeGpBARCzgoCAACAAKAIYIgEvAdIBRQ0BIAEvAdABQQFLDQEgARDdg4CAACIBKQMAIgZCgICAgICAA4NC
gICAgICAA1INAQsgASAGQv///////3yDNwMACyAAKAIYIQcCQCADKQMAQgODQgFSDQAgBygCxAEoAgAiBEEQaiADEKyDgIAAIAQoAhAiCSAAKAIYIgcoAsgB
IgooAgBqIgEgCUkNBSABQQdxDQUgAUUNBQJAIAQvAagCIgRFDQADQCABKQMAQgODQgFRDQIgAUEIaiEBIARBf2oiBA0ACwsgCiAKKQMAQv////+/f4M3AwAg
ACgCGCEHCwJAIAcvAdQBIAMpAwBCGoinIgpB//8DcSIERiIBDQAgBEUNACAHKALIAS0ABEEIcUUNBSAHKALEASgCACIHKAJIIApB//8DcSIJTQ0FIAcoAhAi
CiAHKAJAaiIEIApJDQUgBEEDcQ0FIARFDQUgBCAJQSRsaiIELwEgIgpFDQUgBEEgaiAKQX9qIgQ7AQAgBEH//wNxDQAgBygCRCIEQX9qIgogBEsNBSAHIAo2
AkQLIAMgAykDACILQoCAgHyDIAitQv///wCDQgKGhCIGNwMAIAMgACgCGDMB1AFCGoYgBkL8//+fgIB8g4QiBjcDACADQoCAgICAgARCACAAKAIYLQDaARsg
BkL8//////94g4QiBjcDACADIAAoAhgxANsBQgODQi6GIAZC/P//////RIOENwMAIANBADsBBgJAIAENACADKQMAIgZCgICA4P//AINQDQAgACgCGCgCxAEo
AgAiASgCSCAGQhqIp0H//wNxIgdNDQUgASgCECIEIAEoAkBqIgEgBEkNBSABQQNxDQUgAUUNBSABIAdBJGxqIgQvASAiAUUNBSABQQFqIgFB//8DcSABRw0F
IARBIGogATsBACAAKAIYKALIASIBIAEpAwBCgICAgIABhDcDAAsCQCAAKAIYIgEvAdYBRQ0AIAEQ3oOAgAAiAUH//wNxRQ0BIAIgATsBPiACQT5qEN+DgIAA
IAMtAAVBIHENBQwBCyALQoCAgICAgAiDUA0AIAEoAsQBKAIAIgRBEGogAxCtg4CAACAEKAIQIgMgACgCGCgCyAEiBygCAGoiASADSQ0EIAFBB3ENBCABRQ0E
AkAgBC8BqAIiA0UNAANAIAEtAAVBIHENAiABQQhqIQEgA0F/aiIDDQALCyAHIAcpAwBC//////99gzcDAAsgBUH//wNxQX9qIgFB//8DcSIDIAFHDQMCQCAA
KAIYIgAvAdABIgEgA0cNACAAQQE6ANkBDAULIAFBAWoiAUH//wNxIgMgAUcNAyADIAAvAYwBTw0DIAAgACgCzAFBCGo2AswBIAAoAsQBIgMvAQZBAWoiAUH/
/wNxIAFHDQMgAyABOwEGIAAvAdABQQFqIgFB//8DcSABRw0DIAAgATsB0AEMBAsQ4IOAgAAMAwsgAiADQQNxOgAbCwJAAkACQAJAAkAgAi0AG0EDcQ4EBAAB
BQQLIAopAwBCgICAgICAA4NCgICAgICAAVENAyAAKAIYIAkQ4YOAgAAgBUH//wNxQX9qIgdB//8DcSIDIAdHDQQCQAJAIAAoAhgiBC8B0AEgA0cNACAALQAB
QQJxRQ0HIAVB//8DcSAALwH6ESIDRg0BDAMLIAogCikDAEL///////98g0KAgICAgIABhDcDACAKIQUMAwsgBCgCyAEiBCAEKQMAQoCAgIAQhDcDAAwBCyAK
KQMAIgZCgICAgICAA4NCgICAgICAAVINAiAKIAZC////////fIM3AwAgCUF/aiIDQf//A3EiBCADRw0DIAAoAhgiBy8B0AEgBEkNAyAHKALMASADQf//A3FB
A3RrIgMgAykDAEL///////98gzcDACAFQf//A3FBf2oiA0H//wNxIgQgA0cNAwJAIAAoAhgiAy8B0AEgBEcNACADQQA6ANkBDAMLIANBARDhg4CAAAwCCyAK
KQMAIgZCAoinIQQCQAJAIAZCA4NCAVINACAKIAZCgYCA/P//fINCgICAgICAA0IAIAVB//8DcSADRhuENwMAIAAQ2YOAgAAiA0H//wNxDQYgACAEQQEQ3IOA
gAAgACgCGCgCxAEiAy8BBCIJIAMoAgAiCC8BqgIiBE8NBCADLwEGIgUgCC8BqAJPDQQgCCgCECIMIAgoAhhqIgMgDEkNBCADQQdxDQQgA0UNBCAMIAMgCUED
dGoiDSgCAGoiAyAMSQ0EIANBB3ENBCADRQ0EIAMgBUEDdGohBQJAIAlFDQAgCUF/aiEDIAghCQwCCyAIKAIIIglFDQJBASEDA0ACQCADIAkvAaoCIgRLDQAg
BCADayEDDAMLIAMgBGshAyAJKAIIIglFDQMMAAsLIABBAEF/QQAgBUH//wNxIANGGxDcg4CAACAAENmDgIAAIgNB//8DcQ0FIAAgBEEBENyDgIAAIAAoAhgo
AswBIQUMAQsgA0H//wNxIg4gBE8NAiAHQf//A3EiDyAJLwGoAk8NAiAJKAIQIgMgCSgCGGoiBCADSQ0CIARBB3ENAiAERQ0CIAMgBCAOQQN0aiIOKAIAaiIE
IANJDQIgBEEHcQ0CIARFDQIgCUEQaiEQAkACQCAIIAlHDQAgCiAMayIEIApLDQQgBSAMayIPIAVLDQQgDCAIKAI4aiIDIAxJDQQgA0UNBCACIAM2AhwgAkEg
aiADIAQQrYKAgAAgAi0AKEUNBCACKAIkKQIAIQYgAkEcaiACKAIgEK6CgIAAIAMgDyAGEK+CgIAAIAogCikDAEJ8gzcDACAFIAUpAwBCfINCAYQ3AwAgDSAN
KQMAQoCAgIDAAIQ3AwAMAQsgBCAPQQN0aiIKIANrIgggCksNAyADIAkoAjhqIgQgA0kNAyAERQ0DIAJBLGogBCAIEKODgIAAAkACQCACLQA0DQBBACEEQQAh
CAwBCyADIAIoAixqIgQgA0kNBCAEQQNxDQQgBEUNBCACKAIwIQgLIARFDQMCQCAIRQ0AA0AgACgCGCAFIAQvAQAgBEECai0AAEEQdHIQ24OAgAAiA0H//wNx
DQcgBEEEaiEEIAhBf2oiCA0ACwsgECAKEKyDgIAACyAQKAIAIgQgDigCAGoiAyAESQ0CIANBB3ENAiADRQ0CAkAgCS8BqAIiBEUNAANAIAMpAwBCA4NCAVEN
AiADQQhqIQMgBEF/aiIEDQALCyAOIA4pAwBC/////79/gzcDAAsgACgCGEEBEOKDgIAAIABBAEECENyDgIAAAkAgACgCGCIDLwHQASAHQf//A3FHDQAgA0EB
OgDZASAFIQoMAQsgA0EBEOKDgIAAIAUhCgsgACgCGCgCyAEiAyADKQMAQoCAgICAIIQ3AwAgACgCGCAKIAEQ24OAgAAhAwwCCwALQQAhAwsgAkHAAGokgICA
gAAgAwvMBAIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0HT4MiAAGohBwJAAkAgBiAAKAIIIghL
DQAgBiAISw0FAkAgACgCBCAEaiIGQY3hyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgC
ACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAF
QTpJDQALQQAhAyAAQQBBAEEAEJ6EgIAAIgVB//8DcQ0AA0AgACgCDCIEQQUgA2siBWoiBiAESQ0CIANBjbrIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsN
BgJAIAAoAgQgBGoiBkGSusiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiAC
IAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEFSQ0ACyAA
IAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICAgAAAC+DcAgQJfwF+Cn8CfiOAgICAAEHwG2siAySAgICAAEEAIQQC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQCAALQBcIgVBP3EOLzAwFgAWMBUVMAECKTAwIhUVFRUVFRUVFRUoJyYwGzAVFRUVFRUVFRUVFRowGRgXIwsgAC0ALEUNEyAAKAIMIQYCQANAIAYo
AggiByAGKAIMIgVJDSsCQCAHIAVHDQAgA0EAOgDICiAGKAIAKAIAIQUgA0EBNgL0CiADIANByApqNgLwCiADQegLaiAGIANB8ApqQQFBASAFEYGAgIAAgICA
gAAgAy8B7AsNFCADKALoC0UNAQwCCwsgBSAHTw0EIAYoAgQgBWpBADoAACAGKAIMQQFqIgVFDSogBiAFNgIMCyAAKAIMIgUoAgwiBiAFKAIIIgdLDRIgBSgC
BCEHIABBAjoAWCAGQX9qIgUgBksNKSAFIAZPDRACQCAHIAVqLQAAIgYNACAAQTBqIQQgACAFNgI0IAAgBzYCMAwwCyAGEIOCgIAAAAsgAC0ALEUNDiAAKAIM
IQYCQANAIAYoAggiByAGKAIMIgVJDSoCQCAHIAVHDQAgA0EAOgDICiAGKAIAKAIAIQUgA0EBNgL0CiADIANByApqNgLwCiADQegLaiAGIANB8ApqQQFBASAF
EYGAgIAAgICAgAAgAy8B7AsNDyADKALoC0UNAQwCCwsgBSAHTw0CIAYoAgQgBWpBADoAACAGKAIMQQFqIgVFDSkgBiAFNgIMCyAAKAIMIgUoAgwiBiAFKAII
IgdLDQ0gBSgCBCEHIABBBToAWCAGQX9qIgUgBksNKCAFIAZPDQsCQCAHIAVqLQAAIgYNACAAQTBqIQQgACAFNgI0IAAgBzYCMAwvCyAGEIOCgIAAAAsgAC0A
LEUNCSAAKAIMIQYCQAJAA0AgBigCCCIHIAYoAgwiBUkNKgJAIAcgBUcNACADQQA6AMgKIAYoAgAoAgAhBSADQQE2AvQKIAMgA0HICmo2AvAKIANB6AtqIAYg
A0HwCmpBAUEBIAURgYCAgACAgICAACADLwHsCw0LIAMoAugLRQ0BDAILCyAFIAdPDQEgBigCBCAFakEAOgAAIAYoAgxBAWoiBUUNKSAGIAU2AgwLIAAoAgwi
BigCDCIIIAYoAggiBUsNCSAIRQ0HIAYoAgQhCUEAIQZBASEHAkADQCAJIAZqIgUtAABBO0YNASAHQQFqIQcgCCAGQQFqIgZGDQkMAAsLIABBCjoAWCAGQQFq
IgpFDSggCEF/aiIEIAhLDSggBCAKSQ0GIAQgCE8NBSAJIARqLQAAIgINBCAAIAQgCms2AjwgACAJIApqNgI4IABCADcCMCAGIAhPDQNBACEEIAVBADoAACAK
QQNxIQsCQCAGQQNJDQAgCkF8cSEBQQAhBANAAkAgCSAEaiIFLQAAQTpHDQAgBUEAOgAACwJAIAVBAWoiAi0AAEE6Rw0AIAJBADoAAAsCQCAFQQJqIgItAABB
OkcNACACQQA6AAALAkAgBUEDaiIFLQAAQTpHDQAgBUEAOgAACyABIARBBGoiBEcNAAsLAkAgC0UNACAHQQNxIQcgCSAEaiEFA0ACQCAFLQAAQTpHDQAgBUEA
OgAACyAFQQFqIQUgB0F/aiIHDQALCyAAQTBqIQRBACkD4PrJgAAiDEIgiKchDSADQfAKakEIaiEOIAynIQ9BACEBAkACQAJAAkACQAJAAkACQAJAAkACQANA
IAEiEEEBaiIHIApPDQEgBiAQayELIAkgB2ohAkEAIQUDQCAHIAVqIgEgB0kNNQJAIAIgBWotAABFDQAgCyAFQQFqIgVGDQMMAQsLIAFBAWoiAUUNNCABIBBJ
DQIgASAISw0DIAEgEEYNASAJIBBqIQcgASAQayELIAVBAmohAkEAIQUCQANAIAcgBWotAABBPUYNASACIAVBAWoiBUYNAwwACwsgBSALSw0EIAVBAWoiEEUN
NCALQX9qIgIgC0sNNCACIBBJDQUgAiALTw0GIAcgAmotAAAiCw0HAkACQCAHIAVB5Z/AgABBAhCEgoCAAEEBcUUNACACIBBGDQEgAC0AWCIFQQpHDQsgACAC
IBBrNgI0IAAgByAQajYCMAwBCyADIAU2AvwJIAMgBzYC+AkCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G43sGAADYC8AogAyADQegLajYC
9AoCQAJAIANB8ApqIANB+AlqEIWCgIAAQf//A3EOAwA3ATcLIAMoAvwKIgUgAygC+AoiB0sNDSADKAL0CiAFEICAgIAADAELQQANACAPQTdBAEEAIA0oAgAR
gICAgACAgICAACIFRQ0AIAVCqtWq1arVqtWqfzcAACAFQRBqQqrVqtWq1arVqn83AAAgBUEIakKq1arVqtWq1ap/NwAAIAVBL2pCqtWq1arVqtWqfzcAACAF
QShqQqrVqtWq1arVqn83AAAgBUEgakKq1arVqtWq1ap/NwAAIAVBGGpCqtWq1arVqtWqfzcAACADQjc3A4ALIAMgBTYC/AogA0HclceAADYC+AogAyAMNwPw
CgJAAkAgDiADQfgJahCFgoCAAEH//wNxDgMANwE3CyADKAKECyIFIAMoAoALIgdLDQogA0IANwOACyADKAL8CiECIANBqtWq1Xo2AvwKIAMgBzYC0AogAyAF
NgLMCiADIAI2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBTYCgAsgAyADKALICiIHNgL8CiADIAMoAswKNgKECwJAIAVFDQAgAygC8Aoh
AiADKAL0CiELAkAgBUUNACAHQaoBIAX8CwALIAIgByAFQQBBACALKAIMEYGAgIAAgICAgAALIAMvAagKDQEgAygCoAoiByADKAKkCiIFEICAgIAAIAVFDQEC
QCAFRQ0AIAdBqgEgBfwLAAsgAyAHIAVBACADELmAgIAADAELIAMoAoALIgVFDQAgAygC8AohAiADKAL0CiELIAMoAvwKIQcCQCAFRQ0AIAdBqgEgBfwLAAsg
AiAHIAVBAEEAIAsoAgwRgYCAgACAgICAAAsgASAKSQ0ACwsgAC0AWCIGQQpHDQggACgCPA04AkAgBCgCAEUNACAAQQE6AFwMOAsgBEEoakEAKAKI4MeAADYC
ACAEQSBqQQApAoDgx4AANwIAIARBGGpBACkC+N/HgAA3AgAgBEEQakEAKQLw38eAADcCACAEQQhqQQApAujfx4AANwIAIARBACkC4N/HgAA3AgAMOAsgECAB
EIqAgIAAAAsgASAIEIiAgIAAAAsgBSALEIiAgIAAAAsgECACEIqAgIAAAAsgAkEBaiALEIiAgIAAAAsgCxCDgoCAAAALIAUgBxCIgICAAAALIAVBChCGgoCA
AAALIAZBChCGgoCAAAALIAUgBxCIgICAAAALIAUgBxCIgICAAAALIAUgBxCIgICAAAALIAUgBxCIgICAAAALIAYgCBCIgICAAAALIAIQg4KAgAAACyAEQQFq
IAgQiICAgAAACyAKIAQQioCAgAAACyAAQQE6AFwMJQsgAEEBOgBcDCQLIAggBRCIgICAAAALIABBAToAXAwiCyAFQQFqIAYQiICAgAAACyAAQQE6AFwMIAsg
BiAHEIiAgIAAAAsgAEEBOgBcDB8LIAVBAWogBhCIgICAAAALIABBAToAXAwcCyAGIAcQiICAgAAACyAAQQE6AFwMGwsgA0HIAGpBCGogAEEIaigCACIGNgIA
IAMgACkCADcDSAJAAkACQCAGQf8BcUUNAAJAIAAtACwNAEEAIQZBot7BgAAhBwwDCyAAKAIMIgcoAgwiBiAHKAIIIglLDQEgBygCBCEHDAILIABBAToAXAwc
CyAGIAkQiICAgAAACwJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAIAVBP3FBemoOJAABDg4ODg4ODgMEBQYHCAkKCwwODg4ODg4CDRcWFRQTEhEQDw4LQQAhCyAAQQA6AFQgAEEHOgBYIANB
OzoAlAEgA0EANgKQASADIAY2AowBIAMgBzYCiAEgA0HYC2pBACgC9LjIgAA2AgAgA0HQC2pBACkC7LjIgAA3AwAgA0HIC2pBACkC5LjIgAA3AwAgA0HAC2pB
ACkC3LjIgAA3AwAgA0EAKQLUuMiAADcDuAsCQAJAIAYNAEEAIQlBACEIDAELQQAhBQNAAkAgByAFaiIELQAAQTtGDQAgAyAFNgKQAUEAIQggBiEJQQAhDyAG
IAVGDS0gBSEJIAUgBk8NKiAFIQkDQCAHIAlqLQAAQTtGDSogBiAJQQFqIglHDQALIAYhCQwpCyAGIAVBAWoiBUcNAAsgAyAGNgKQAUEAIQggBiEJC0EAIQ8M
KgsgAEEBOgBUIABBBzoAWCADQTs6AJQBQQAhCyADQQA2ApABIAMgBjYCjAEgAyAHNgKIASADQdgLakEAKAL0uMiAADYCACADQdALakEAKQLsuMiAADcDACAD
QcgLakEAKQLkuMiAADcDACADQcALakEAKQLcuMiAADcDACADQQApAtS4yIAANwO4CwJAAkAgBg0AQQAhCUEAIQgMAQtBACEFA0ACQCAHIAVqIgQtAABBO0YN
ACADIAU2ApABQQAhCCAGIQlBACEPIAYgBUYNJyAFIQkgBSAGTw0kIAUhCQNAIAcgCWotAABBO0YNJCAGIAlBAWoiCUcNAAsgBiEJDCMLIAYgBUEBaiIFRw0A
CyADIAY2ApABQQAhCCAGIQkLQQAhDwwkCyAAQQw6AFQgAEEHOgBYIANBOzoAlAFBACELIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAA
NgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAGDQBBACEIQQAhCgwB
C0EAIQUDQAJAIAcgBWoiBC0AAEE7Rg0AIAMgBTYCkAFBACEKIAYhCEEAIQkgBiAFRg0hIAUhCSAFIAZPDR4gBSEJA0AgByAJai0AAEE7Rg0eIAYgCUEBaiIJ
Rw0ACyAGIQkMHQsgBiAFQQFqIgVHDQALIAMgBjYCkAFBACEKIAYhCAtBACEJDB4LIABBAjoAVCAAQQc6AFggA0E7OgCUASADQQA2ApABIAMgBjYCjAEgAyAH
NgKIASADQZACaiADQcgAakEKIANBiAFqEIeCgIAAIABBMGohBCADLwG0AiIJDSkgA0G4AmpBIGogA0GQAmpBIGooAgA2AgAgA0G4AmpBGGogA0GQAmpBGGop
AgA3AwAgA0G4AmpBEGogA0GQAmpBEGopAgA3AwAgA0G4AmpBCGogA0GQAmpBCGopAgA3AwAgAyADKQKQAjcDuAIgA0G4AmohBgwoCyAAQQM6AFQgAEEHOgBY
IANBOzoAlAEgA0EANgKQASADIAY2AowBIAMgBzYCiAEgA0HgAmogA0HIAGpBCyADQYgBahCHgoCAACAAQTBqIQQgAy8BhAMiCQ0oIANBiANqQSBqIANB4AJq
QSBqKAIANgIAIANBiANqQRhqIANB4AJqQRhqKQIANwMAIANBiANqQRBqIANB4AJqQRBqKQIANwMAIANBiANqQQhqIANB4AJqQQhqKQIANwMAIAMgAykC4AI3
A4gDIANBiANqIQYMJwsgAEEEOgBUIABBBzoAWCADQTs6AJQBIANBADYCkAEgAyAGNgKMASADIAc2AogBIANBsANqIANByABqQQwgA0GIAWoQh4KAgAAgAEEw
aiEEIAMvAdQDIgkNJyADQdgDakEgaiADQbADakEgaigCADYCACADQdgDakEYaiADQbADakEYaikCADcDACADQdgDakEQaiADQbADakEQaikCADcDACADQdgD
akEIaiADQbADakEIaikCADcDACADIAMpArADNwPYAyADQdgDaiEGDCYLIABBBToAVCAAQQc6AFggA0E7OgCUASADQQA2ApABIAMgBjYCjAEgAyAHNgKIASAD
QYAEaiADQcgAakENIANBiAFqEIeCgIAAIABBMGohBCADLwGkBCIJDSYgA0GoBGpBIGogA0GABGpBIGooAgA2AgAgA0GoBGpBGGogA0GABGpBGGopAgA3AwAg
A0GoBGpBEGogA0GABGpBEGopAgA3AwAgA0GoBGpBCGogA0GABGpBCGopAgA3AwAgAyADKQKABDcDqAQgA0GoBGohBgwlCyAAQQY6AFQgAEEHOgBYIANBOzoA
lAEgA0EANgKQASADIAY2AowBIAMgBzYCiAEgA0HQBGogA0HIAGpBDiADQYgBahCHgoCAACAAQTBqIQQgAy8B9AQiCQ0lIANB+ARqQSBqIANB0ARqQSBqKAIA
NgIAIANB+ARqQRhqIANB0ARqQRhqKQIANwMAIANB+ARqQRBqIANB0ARqQRBqKQIANwMAIANB+ARqQQhqIANB0ARqQQhqKQIANwMAIAMgAykC0AQ3A/gEIANB
+ARqIQYMJAsgAEEHOgBUIABBBzoAWCADQTs6AJQBIANBADYCkAEgAyAGNgKMASADIAc2AogBIANBoAVqIANByABqQQ8gA0GIAWoQh4KAgAAgAEEwaiEEIAMv
AcQFIgkNJCADQcgFakEgaiADQaAFakEgaigCADYCACADQcgFakEYaiADQaAFakEYaikCADcDACADQcgFakEQaiADQaAFakEQaikCADcDACADQcgFakEIaiAD
QaAFakEIaikCADcDACADIAMpAqAFNwPIBSADQcgFaiEGDCMLIABBCDoAVCAAQQc6AFggA0E7OgCUASADQQA2ApABIAMgBjYCjAEgAyAHNgKIASADQfAFaiAD
QcgAakEQIANBiAFqEIeCgIAAIABBMGohBCADLwGUBiIJDSMgA0GYBmpBIGogA0HwBWpBIGooAgA2AgAgA0GYBmpBGGogA0HwBWpBGGopAgA3AwAgA0GYBmpB
EGogA0HwBWpBEGopAgA3AwAgA0GYBmpBCGogA0HwBWpBCGopAgA3AwAgAyADKQLwBTcDmAYgA0GYBmohBgwiCyAAQQk6AFQgAEEHOgBYIANBOzoAlAEgA0EA
NgKQASADIAY2AowBIAMgBzYCiAEgA0HABmogA0HIAGpBESADQYgBahCHgoCAACAAQTBqIQQgAy8B5AYiCQ0iIANB6AZqQSBqIANBwAZqQSBqKAIANgIAIANB
6AZqQRhqIANBwAZqQRhqKQIANwMAIANB6AZqQRBqIANBwAZqQRBqKQIANwMAIANB6AZqQQhqIANBwAZqQQhqKQIANwMAIAMgAykCwAY3A+gGIANB6AZqIQYM
IQsgAEEKOgBUIABBBzoAWCADQTs6AJQBIANBADYCkAEgAyAGNgKMASADIAc2AogBIANBkAdqIANByABqQRIgA0GIAWoQh4KAgAAgAEEwaiEEIAMvAbQHIgkN
ISADQbgHakEgaiADQZAHakEgaigCADYCACADQbgHakEYaiADQZAHakEYaikCADcDACADQbgHakEQaiADQZAHakEQaikCADcDACADQbgHakEIaiADQZAHakEI
aikCADcDACADIAMpApAHNwO4ByADQbgHaiEGDCALIABBCzoAVCAAQQc6AFggA0E7OgCUASADQQA2ApABIAMgBjYCjAEgAyAHNgKIASADQeAHaiADQcgAakET
IANBiAFqEIeCgIAAIABBMGohBCADLwGECCIJDSAgA0GICGpBIGogA0HgB2pBIGooAgA2AgAgA0GICGpBGGogA0HgB2pBGGopAgA3AwAgA0GICGpBEGogA0Hg
B2pBEGopAgA3AwAgA0GICGpBCGogA0HgB2pBCGopAgA3AwAgAyADKQLgBzcDiAggA0GICGohBgwfCyAAQQ46AFQgAEEHOgBYIANBOzoAlAFBACEIIANBADYC
kAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAANgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMA
IANBACkC1LjIgAA3A7gLAkAgBg0AQQAhC0EAIQoMDwtBACEFA0ACQCAHIAVqIgktAABBO0YNAEEAIQsgBiEIQQAhCiAGIAVGDRAgBSEEIAUgBk8NDSAFIQQD
QCAHIARqLQAAQTtGDQ0gBiAEQQFqIgRHDQALIAYhBAwMCyAGIAVBAWoiBUcNAAtBACELIAYhCEEAIQoMDgsgAEEBOgBcDDsLIABBFzoAVCAAQQc6AFggA0E7
OgCUAUEAIQkgA0EANgKQASADIAY2AowBIAMgBzYCiAEgA0HYC2pBACgC9LjIgAA2AgAgA0HQC2pBACkC7LjIgAA3AwAgA0HIC2pBACkC5LjIgAA3AwAgA0HA
C2pBACkC3LjIgAA3AwAgA0EAKQLUuMiAADcDuAsCQAJAIAYNAEEAIQVBACEIDAELQQAhCQJAAkACQAJAAkACQCAHLQAAQTtHDQBBASEFA0AgBiAFRg0CIAcg
BWohCSAFQQFqIgQhBSAJLQAAQTtGDQALIARBf2ohCQsgCSEFIAkgBk8NAiAJIQUDQCAHIAVqLQAAQTtGDQIgBiAFQQFqIgVHDQALIAYhBQwBC0EAIQUgBiEJ
QQAhCAwECyAJIAVLDQELIAUgBk0NASAFIAYQiICAgAAACyAJIAUQioCAgAAACyAFIAlrIQggByAJaiEFCyAAQTBqIQQCQAJAAkAgBUUNAEHUuMiAACEGIAkg
CGogCUkNMQwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEgAygC4AsiBkECOgAIIAZBgiY7AAAgA0G4C2ohBgsgA0HoC2pBIGogBkEgaigC
ADYCACADQegLakEYaiAGQRhqKQIANwMAIANB6AtqQRBqIAZBEGopAgA3AwAgA0HoC2pBCGogBkEIaikCADcDACADIAYpAgA3A+gLIANB6AtqIQYMHQsgA0G4
C2ogAykDSBCJgoCAAAwdCyAAQRY6AFQgAEEHOgBYIANBOzoAlAFBACEJIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAANgIAIANB0Atq
QQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAGDQBBACEFQQAhCAwBC0EAIQkCQAJA
AkACQAJAAkAgBy0AAEE7Rw0AQQEhBQNAIAYgBUYNAiAHIAVqIQkgBUEBaiIEIQUgCS0AAEE7Rg0ACyAEQX9qIQkLIAkhBSAJIAZPDQIgCSEFA0AgByAFai0A
AEE7Rg0CIAYgBUEBaiIFRw0ACyAGIQUMAQtBACEFIAYhCUEAIQgMBAsgCSAFSw0BCyAFIAZNDQEgBSAGEIiAgIAAAAsgCSAFEIqAgIAAAAsgBSAJayEIIAcg
CWohBQsgAEEwaiEEAkACQAJAIAVFDQBB1LjIgAAhBiAJIAhqIAlJDTAMAQsgA0HgC2ogA0G4C2ogA0HIAGoQiIKAgAAgAy8B5AsiCQ0BIAMoAuALIgZBAjoA
CCAGQYIkOwAAIANBuAtqIQYLIANB8ApqQSBqIAZBIGooAgA2AgAgA0HwCmpBGGogBkEYaikCADcDACADQfAKakEQaiAGQRBqKQIANwMAIANB8ApqQQhqIAZB
CGopAgA3AwAgAyAGKQIANwPwCiADQfAKaiEGDBwLIANBuAtqIAMpA0gQiYKAgAAMHAsgAEEVOgBUIABBBzoAWCADQTs6AJQBQQAhCSADQQA2ApABIAMgBjYC
jAEgAyAHNgKIASADQdgLakEAKAL0uMiAADYCACADQdALakEAKQLsuMiAADcDACADQcgLakEAKQLkuMiAADcDACADQcALakEAKQLcuMiAADcDACADQQApAtS4
yIAANwO4CwJAAkAgBg0AQQAhBUEAIQgMAQtBACEJAkACQAJAAkACQAJAIActAABBO0cNAEEBIQUDQCAGIAVGDQIgByAFaiEJIAVBAWoiBCEFIAktAABBO0YN
AAsgBEF/aiEJCyAJIQUgCSAGTw0CIAkhBQNAIAcgBWotAABBO0YNAiAGIAVBAWoiBUcNAAsgBiEFDAELQQAhBSAGIQlBACEIDAQLIAkgBUsNAQsgBSAGTQ0B
IAUgBhCIgICAAAALIAkgBRCKgICAAAALIAUgCWshCCAHIAlqIQULIABBMGohBAJAAkACQCAFRQ0AQdS4yIAAIQYgCSAIaiAJSQ0vDAELIANB4AtqIANBuAtq
IANByABqEIiCgIAAIAMvAeQLIgkNASADKALgCyIGQQI6AAggBkGCIjsAACADQbgLaiEGCyADQcgKakEgaiAGQSBqKAIANgIAIANByApqQRhqIAZBGGopAgA3
AwAgA0HICmpBEGogBkEQaikCADcDACADQcgKakEIaiAGQQhqKQIANwMAIAMgBikCADcDyAogA0HICmohBgwbCyADQbgLaiADKQNIEImCgIAADBsLIABBFDoA
VCAAQQc6AFggA0E7OgCUAUEAIQkgA0EANgKQASADIAY2AowBIAMgBzYCiAEgA0HYC2pBACgC9LjIgAA2AgAgA0HQC2pBACkC7LjIgAA3AwAgA0HIC2pBACkC
5LjIgAA3AwAgA0HAC2pBACkC3LjIgAA3AwAgA0EAKQLUuMiAADcDuAsCQAJAIAYNAEEAIQVBACEIDAELQQAhCQJAAkACQAJAAkACQCAHLQAAQTtHDQBBASEF
A0AgBiAFRg0CIAcgBWohCSAFQQFqIgQhBSAJLQAAQTtGDQALIARBf2ohCQsgCSEFIAkgBk8NAiAJIQUDQCAHIAVqLQAAQTtGDQIgBiAFQQFqIgVHDQALIAYh
BQwBC0EAIQUgBiEJQQAhCAwECyAJIAVLDQELIAUgBk0NASAFIAYQiICAgAAACyAJIAUQioCAgAAACyAFIAlrIQggByAJaiEFCyAAQTBqIQQCQAJAAkAgBUUN
AEHUuMiAACEGIAkgCGogCUkNLgwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEgAygC4AsiBkECOgAIIAZBgiA7AAAgA0G4C2ohBgsgA0Gg
CmpBIGogBkEgaigCADYCACADQaAKakEYaiAGQRhqKQIANwMAIANBoApqQRBqIAZBEGopAgA3AwAgA0GgCmpBCGogBkEIaikCADcDACADIAYpAgA3A6AKIANB
oApqIQYMGgsgA0G4C2ogAykDSBCJgoCAAAwaCyAAQRM6AFQgAEEHOgBYIANBOzoAlAFBACEIIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4
yIAANgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAGDQBBACELQQAh
CgwBC0EAIQUCQAJAAkACQANAAkAgByAFaiIJLQAAQTtGDQBBACELIAYhCEEAIQogBiAFRg0GIAUhBCAFIAZPDQMgBSEEA0AgByAEai0AAEE7Rg0DIAYgBEEB
aiIERw0ACyAGIQQMAgsgBiAFQQFqIgVHDQALQQAhCyAGIQhBACEKDAQLIAUgBEsNAQsgBCAGTQ0BIAQgBhCIgICAAAALIAUgBBCKgICAAAALIAQgBWshCiAF
IQggCSELCyAAQTBqIQQCQAJAAkAgC0UNAEHUuMiAACEGIAggCmogCEkNLQwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEgAygC4AsiBkEC
OgAIIAZBgh47AAAgA0G4C2ohBgsgA0H4CWpBIGogBkEgaigCADYCACADQfgJakEYaiAGQRhqKQIANwMAIANB+AlqQRBqIAZBEGopAgA3AwAgA0H4CWpBCGog
BkEIaikCADcDACADIAYpAgA3A/gJIANB+AlqIQYMGQsgA0G4C2ogAykDSBCJgoCAAAwZCyAAQRI6AFQgAEEHOgBYIANBOzoAlAFBACEIIANBADYCkAEgAyAG
NgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAANgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC
1LjIgAA3A7gLAkACQCAGDQBBACELQQAhCgwBC0EAIQUCQAJAAkACQANAAkAgByAFaiIJLQAAQTtGDQBBACELIAYhCEEAIQogBiAFRg0GIAUhBCAFIAZPDQMg
BSEEA0AgByAEai0AAEE7Rg0DIAYgBEEBaiIERw0ACyAGIQQMAgsgBiAFQQFqIgVHDQALQQAhCyAGIQhBACEKDAQLIAUgBEsNAQsgBCAGTQ0BIAQgBhCIgICA
AAALIAUgBBCKgICAAAALIAQgBWshCiAFIQggCSELCyAAQTBqIQQCQAJAAkAgC0UNAEHUuMiAACEGIAggCmogCEkNLAwBCyADQeALaiADQbgLaiADQcgAahCI
goCAACADLwHkCyIJDQEgAygC4AsiBkECOgAIIAZBghw7AAAgA0G4C2ohBgsgA0HQCWpBIGogBkEgaigCADYCACADQdAJakEYaiAGQRhqKQIANwMAIANB0Alq
QRBqIAZBEGopAgA3AwAgA0HQCWpBCGogBkEIaikCADcDACADIAYpAgA3A9AJIANB0AlqIQYMGAsgA0G4C2ogAykDSBCJgoCAAAwYCyAAQRE6AFQgAEEHOgBY
IANBOzoAlAFBACEIIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAANgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMA
IANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAGDQBBACELQQAhCgwBC0EAIQUCQAJAAkACQANAAkAgByAFaiIJLQAAQTtGDQBBACELIAYh
CEEAIQogBiAFRg0GIAUhBCAFIAZPDQMgBSEEA0AgByAEai0AAEE7Rg0DIAYgBEEBaiIERw0ACyAGIQQMAgsgBiAFQQFqIgVHDQALQQAhCyAGIQhBACEKDAQL
IAUgBEsNAQsgBCAGTQ0BIAQgBhCIgICAAAALIAUgBBCKgICAAAALIAQgBWshCiAFIQggCSELCyAAQTBqIQQCQAJAAkAgC0UNAEHUuMiAACEGIAggCmogCEkN
KwwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEgAygC4AsiBkECOgAIIAZBgho7AAAgA0G4C2ohBgsgA0GoCWpBIGogBkEgaigCADYCACAD
QagJakEYaiAGQRhqKQIANwMAIANBqAlqQRBqIAZBEGopAgA3AwAgA0GoCWpBCGogBkEIaikCADcDACADIAYpAgA3A6gJIANBqAlqIQYMFwsgA0G4C2ogAykD
SBCJgoCAAAwXCyAAQRA6AFQgAEEHOgBYIANBOzoAlAFBACEIIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB2AtqQQAoAvS4yIAANgIAIANB0AtqQQApAuy4
yIAANwMAIANBuAtqQRBqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAGDQBBACELQQAhCgwBC0EAIQUCQAJAAkAC
QANAAkAgByAFaiIJLQAAQTtGDQBBACELIAYhCEEAIQogBiAFRg0GIAUhBCAFIAZPDQMgBSEEA0AgByAEai0AAEE7Rg0DIAYgBEEBaiIERw0ACyAGIQQMAgsg
BiAFQQFqIgVHDQALQQAhCyAGIQhBACEKDAQLIAUgBEsNAQsgBCAGTQ0BIAQgBhCIgICAAAALIAUgBBCKgICAAAALIAQgBWshCiAFIQggCSELCyAAQTBqIQQC
QAJAAkAgC0UNAEHUuMiAACEGIAggCmogCEkNKgwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEgAygC4AsiBkECOgAIIAZBghg7AAAgA0G4
C2ohBgsgA0GACWpBIGogBkEgaigCADYCACADQYAJakEYaiAGQRhqKQIANwMAIANBgAlqQRBqIAZBEGopAgA3AwAgA0GACWpBCGogBkEIaikCADcDACADIAYp
AgA3A4AJIANBgAlqIQYMFgsgA0G4C2ogAykDSBCJgoCAAAwWCyAAQQ86AFQgAEEHOgBYIANBOzoAlAFBACEIIANBADYCkAEgAyAGNgKMASADIAc2AogBIANB
2AtqQQAoAvS4yIAANgIAIANB0AtqQQApAuy4yIAANwMAIANByAtqQQApAuS4yIAANwMAIANBwAtqQQApAty4yIAANwMAIANBACkC1LjIgAA3A7gLAkACQCAG
DQBBACELQQAhCgwBC0EAIQUCQAJAAkACQANAAkAgByAFaiIJLQAAQTtGDQBBACELIAYhCEEAIQogBiAFRg0GIAUhBCAFIAZPDQMgBSEEA0AgByAEai0AAEE7
Rg0DIAYgBEEBaiIERw0ACyAGIQQMAgsgBiAFQQFqIgVHDQALQQAhCyAGIQhBACEKDAQLIAUgBEsNAQsgBCAGTQ0BIAQgBhCIgICAAAALIAUgBBCKgICAAAAL
IAQgBWshCiAFIQggCSELCyAAQTBqIQQCQAJAAkAgC0UNAEHUuMiAACEGIAggCmogCEkNKQwBCyADQeALaiADQbgLaiADQcgAahCIgoCAACADLwHkCyIJDQEg
AygC4AsiBkECOgAIIAZBghY7AAAgA0G4C2ohBgsgA0HYCGpBIGogBkEgaigCADYCACADQdgIakEYaiAGQRhqKQIANwMAIANB2AhqQRBqIAZBEGopAgA3AwAg
A0HYCGpBCGogBkEIaikCADcDACADIAYpAgA3A9gIIANB2AhqIQYMFQsgA0G4C2ogAykDSBCJgoCAAAwVCyAFIARLDQELIAQgBk0NASAEIAYQiICAgAAACyAF
IAQQioCAgAAACyAEIAVrIQogBSEIIAkhCwsgAEEwaiEEAkACQAJAIAtFDQBB1LjIgAAhBiAIIApqIAhJDSQMAQsgA0HgC2ogA0G4C2ogA0HIAGoQiIKAgAAg
Ay8B5AsiCQ0BIAMoAuALIgZBAjoACCAGQYIUOwAAIANBuAtqIQYLIANBsAhqQSBqIAZBIGooAgA2AgAgA0GwCGpBGGogBkEYaikCADcDACADQbAIakEQaiAG
QRBqKQIANwMAIANBsAhqQQhqIAZBCGopAgA3AwAgAyAGKQIANwOwCCADQbAIaiEGDBALIANBuAtqIAMpA0gQiYKAgAAMEAsgBSAJSw0BCyAJIAZNDQEgCSAG
EIiAgIAAAAsgBSAJEIqAgIAAAAsgCSAFayEJIAUhCCAEIQoLAkACQCAKDQBBACEJDAELIAggCWoiBSAISQ0WIAMgBTYCkAEgBSEIIAohCwsgAEEwaiEEAkAC
QAJAIAtFDQAgBkF/aiEKIAdBAWohEEEALQDK+cmAAEEHcSENQQAtAMv5yYAAQQdxIQ9BAC0AzPnJgABBB3EhDkEALQDN+cmAAEEHcSERQQAtAM75yYAAQQdx
IRIgCCEFA0ACQCAJRQ0AIANBoAtqIAsgCRCKgoCAACADLwGiCw0AAkACQCADLwGgC0EXdEEXdSIJQQBIDQAgAyAJOgCzCyADQQA6ALQLDAELIANBAToAtAsg
CUH4AXENAQJAIAlBB3EiCSANRg0AIAkgD0YNACAJIA5GDQAgCSARRg0AIAkgEkcNAgsgCUEFTw0iIAMgCToAswsLIANB4AtqIANBuAtqIANByABqEIiCgIAA
IAMvAeQLIgkNBCADKALgCyIFQQI6AAggBSADLQC0CzoAACAFIAMtALMLOgABIAghBQsCQAJAAkACQCAFIAZPDQACQCAHIAVqLQAAQTtHDQADQCAKIAVGDQMg
ECAFaiEJIAVBAWoiCCEFIAktAABBO0YNAAsLIAMgCDYCkAELQQAhCyAFIAZGDQEgBSEJAkAgBSAGTw0AA0AgByAJai0AAEE7Rg0BIAYgCUEBaiIJRw0ACyAG
IQkLAkACQCAJIAVJDQAgCSAGTQ0BIAkgBhCIgICAAAALIAUgCRCKgICAAAALIAcgBWohCyAJIAVrIQkMAgsgAyAGNgKQAUEAIQsgBiEICyAGIQVBACEJCwJA
AkAgCw0AQQAhC0EAIQkMAQsgBSAJaiIIIAVJDRogAyAINgKQASAIIQULIAsNAAsgAygC2AsNAQsgA0GoC2ogA0G4C2ogA0HIAGoQiIKAgAAgAy8BrAsiCQ0B
IAMoAqgLIgZBACkCyLjIgAA3AgAgBkEIakEAKALQuMiAADYCAAsgA0HoAWpBIGogA0G4C2pBIGooAgA2AgAgA0HoAWpBGGogA0G4C2pBGGopAwA3AwAgA0Ho
AWpBEGogA0G4C2pBEGopAwA3AwAgA0HoAWpBCGogA0G4C2pBCGopAwA3AwAgAyADKQO4CzcD6AEgA0HoAWohBgwLCyADQbgLaiADKQNIEImCgIAADAsLIAUg
CUsNAQsgCSAGTQ0BIAkgBhCIgICAAAALIAUgCRCKgICAAAALIAkgBWshDyAFIQkgBCEICwJAAkAgCA0AQQAhDwwBCyAJIA9qIgUgCUkNESADIAU2ApABIAUh
CSAIIQsLIABBMGohBAJAIAtFDQAgBkF/aiEKIAdBAWohEEEALQDK+cmAAEEHcSEOQQAtAMv5yYAAQQdxIRFBAC0AzPnJgABBB3EhEkEALQDN+cmAAEEHcSET
QQAtAM75yYAAQQdxIRQDQAJAAkACQAJAIAkgBk8NAAJAIAcgCWotAABBO0cNAANAIAogCUYNAyAQIAlqIQUgCUEBaiEJIAUtAABBO0YNAAsLIAMgCTYCkAEL
QQAhCCAGIQVBACENIAkgBkYNAiAJIQUgCSAGTw0BA0AgByAFai0AAEE7Rg0CIAYgBUEBaiIFRw0ACyAGIQUMAQsgAyAGNgKQAUEAIQggBiEFQQAhDQwBCwJA
AkAgBSAJSQ0AIAUgBk0NASAFIAYQiICAgAAACyAJIAUQioCAgAAACyAHIAlqIQggBSAJayENIAkhBQsCQAJAIAgNAEEAIQhBACENDAELIAUgDWoiCSAFSQ0T
IAMgCTYCkAEgCSEFCyAIRQ0BIANBmgtqIAsgDxCKgoCAACADLwGcCw0BIANBAToAswsgAy8BmgsiCUEHSw0BAkAgCUEHcSIJIA5GDQAgCSARRg0AIAkgEkYN
ACAJIBNGDQAgCSAURw0CCyAJQQVPDRkgAyAJOgCfCwJAAkACQAJAAkAgCCANQf+hwIAAQQEQhIKAgABBAXENACADQagLaiAIIA0Qi4KAgAAgAy8BrAsNBiAD
LQCqCyEIIAMvAagLIQsgA0HgC2ogA0G4C2ogA0HIAGoQiIKAgAAgAy8B5AsiCQ0CIAMoAuALIgkgCyAIQRB0ciIIOwEAIAlBADoACCAJIAMtALMLOgAEIAkg
Ay0Anws6AAUgCUECaiAIQRB2OgAADAELIANBoAtqIANBuAtqIANByABqEIiCgIAAIAMvAaQLIgkNASADKAKgCyIJQQE6AAggCSADLQCzCzoAACAJIAMtAJ8L
OgABCyAFIAZPDQEDQAJAIAcgBWotAABBO0YNACADIAU2ApABDAMLIAYgBUEBaiIFRw0ACyADIAY2ApABQQAhCyAGIQlBACEPDAILIANBuAtqIAMpA0gQiYKA
gAAMCgtBACELIAYhCUEAIQ8gBSAGRg0AIAUhCQJAIAUgBk8NAANAIAcgCWotAABBO0YNASAGIAlBAWoiCUcNAAsgBiEJCwJAAkAgCSAFSQ0AIAkgBk0NASAJ
IAYQiICAgAAACyAFIAkQioCAgAAACyAHIAVqIQsgCSAFayEPIAUhCQsCQAJAIAsNAEEAIQtBACEPDAELIAkgD2oiBSAJSQ0TIAMgBTYCkAEgBSEJCyALDQAL
CyADQcABakEgaiADQbgLakEgaigCADYCACADQcABakEYaiADQbgLakEYaikDADcDACADQcABakEQaiADQbgLakEQaikDADcDACADQcABakEIaiADQbgLakEI
aikDADcDACADIAMpA7gLNwPAASADQcABaiEGDAULIAUgCUsNAQsgCSAGTQ0BIAkgBhCIgICAAAALIAUgCRCKgICAAAALIAkgBWshDyAFIQkgBCEICwJAAkAg
CA0AQQAhDwwBCyAJIA9qIgUgCUkNDCADIAU2ApABIAUhCSAIIQsLIABBMGohBAJAIAtFDQAgBkF/aiEKIAdBAWohEEEALQDK+cmAAEEHcSEOQQAtAMv5yYAA
QQdxIRFBAC0AzPnJgABBB3EhEkEALQDN+cmAAEEHcSETQQAtAM75yYAAQQdxIRQDQAJAAkACQAJAIAkgBk8NAAJAIAcgCWotAABBO0cNAANAIAogCUYNAyAQ
IAlqIQUgCUEBaiEJIAUtAABBO0YNAAsLIAMgCTYCkAELQQAhCCAGIQVBACENIAkgBkYNAiAJIQUgCSAGTw0BA0AgByAFai0AAEE7Rg0CIAYgBUEBaiIFRw0A
CyAGIQUMAQsgAyAGNgKQAUEAIQggBiEFQQAhDQwBCwJAAkAgBSAJSQ0AIAUgBk0NASAFIAYQiICAgAAACyAJIAUQioCAgAAACyAHIAlqIQggBSAJayENIAkh
BQsCQAJAIAgNAEEAIQhBACENDAELIAUgDWoiCSAFSQ0OIAMgCTYCkAEgCSEFCyAIRQ0BIANBmgtqIAsgDxCKgoCAACADLwGcCw0BAkACQCADLwGaC0EXdEEX
dSIJQQBIDQAgAyAJOgCfCyADQQA6ALMLDAELIANBAToAswsgCUH4AXENAgJAIAlBB3EiCSAORg0AIAkgEUYNACAJIBJGDQAgCSATRg0AIAkgFEcNAwsgCUEF
Tw0VIAMgCToAnwsLAkACQAJAAkACQCAIIA1B/6HAgABBARCEgoCAAEEBcUUNACADQaALaiADQbgLaiADQcgAahCIgoCAACADLwGkCyIJDQIgAygCoAsiCUEB
OgAIIAkgAy0Asws6AAAgCSADLQCfCzoAAQwBCyADQagLaiAIIA0Qi4KAgAAgAy8BrAsNBSADLQCqCyEIIAMvAagLIQsgA0HgC2ogA0G4C2ogA0HIAGoQiIKA
gAAgAy8B5AsiCQ0BIAMoAuALIgkgCyAIQRB0ciIIOwEAIAlBADoACCAJIAMtALMLOgAEIAkgAy0Anws6AAUgCUECaiAIQRB2OgAACyAFIAZPDQEDQAJAIAcg
BWotAABBO0YNACADIAU2ApABDAMLIAYgBUEBaiIFRw0ACyADIAY2ApABQQAhCyAGIQlBACEPDAILIANBuAtqIAMpA0gQiYKAgAAMBQtBACELIAYhCUEAIQ8g
BSAGRg0AIAUhCQJAIAUgBk8NAANAIAcgCWotAABBO0YNASAGIAlBAWoiCUcNAAsgBiEJCwJAAkAgCSAFSQ0AIAkgBk0NASAJIAYQiICAgAAACyAFIAkQioCA
gAAACyAHIAVqIQsgCSAFayEPIAUhCQsCQAJAIAsNAEEAIQtBACEPDAELIAkgD2oiBSAJSQ0OIAMgBTYCkAEgBSEJCyALDQALCyADQZgBakEgaiADQbgLakEg
aigCADYCACADQZgBakEYaiADQbgLakEYaikDADcDACADQZgBakEQaiADQbgLakEQaikDADcDACADQZgBakEIaiADQbgLakEIaikDADcDACADIAMpA7gLNwOY
ASADQZgBaiEGCyADQdgAakEgaiIFIAZBIGooAgA2AgAgA0HYAGpBGGoiByAGQRhqKQIANwMAIANB2ABqQRBqIgkgBkEQaikCADcDACADQdgAakEIaiIIIAZB
CGopAgA3AwAgAyAGKQIANwNYIARBIGogBSgCADYCACAEQRhqIAcpAwA3AgAgBEEQaiAJKQMANwIAIARBCGogCCkDADcCACAEIAMpA1g3AgAMAQsgAC0AXCEF
IAMgBjYChAEgAyAHNgKAASADIAk7AX4gAyAFQT9xOgB8AkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQK
AkACQAJAIANB8ApqIANB/ABqEIyCgIAAQf//A3EOAwATARMLIAMoAvwKIgYgAygC+AoiBUsNBCADKAL0CiAGEICAgIAADAELQQANAEEAKQPg+smAACIMp0HG
AEEAQQAgDEIgiKcoAgARgICAgACAgICAACIGRQ0AAkBBxgBFDQAgBkGqAUHGAPwLAAsgA0LGADcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIAw3A/AKAkAC
QCADQfgKaiADQfwAahCMgoCAAEH//wNxDgMADAEMCyADKAKECyIGIAMoAoALIgVLDQMgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAG
NgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8Aoh
ByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAALIAMvAagKDQEgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQEC
QCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAELIAMoAoALIgZFDQAgAygC8AohByADKAL0CiEJIAMoAvwKIQUCQCAGRQ0AIAVBqgEgBvwLAAsg
ByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgBEEgakEAKAL0uMiAADYCACAEQRhqQQApAuy4yIAANwIAIARBEGpBACkC5LjIgAA3AgAgBEEIakEAKQLcuMiA
ADcCACAEQQApAtS4yIAANwIACyAAIAJB/wFxQQBHIAFB/wFxQQdGcToAVQwcCyAGIAUQiICAgAAMBwsgBiAFEIiAgIAAAAsCQAJAAkACQCAALQAsRQ0AIAAo
AgwhBgJAAkACQANAIAYoAggiByAGKAIMIgVJDQ0CQCAHIAVHDQAgA0EAOgDICiAGKAIAKAIAIQUgA0EBNgL0CiADIANByApqNgLwCiADQegLaiAGIANB8Apq
QQFBASAFEYGAgIAAgICAgAAgAy8B7AsNAyADKALoC0UNAQwCCwsgBSAHTw0GIAYoAgQgBWpBADoAACAGKAIMQQFqIgVFDRMgBiAFNgIMCyAAKAIMIgUoAgwi
BiAFKAIIIgdLDQMgBSgCBCEHIABBAToAWCAGQX9qIgUgBksNEiAFIAZPDQQgByAFai0AACIGDQEgAEEwaiEEIAAgBTYCNCAAIAc2AjAMHwsgAEEBOgBcDB0L
IAYQg4KAgAAACyAAQQE6AFwMHAsgBiAHEIiAgIAAAAsgBUEBaiAGEIiAgIAAAAsgBSAHEIiAgIAAAAsCQAJAAkACQAJAAkAgAC0ALEUNACAAKAIMIgYoAgwi
BSAGKAIIIgdLDQIgBigCBCEHIAVFDQRBACEGA0AgByAGai0AAEE7Rg0CIAUgBkEBaiIGRg0FDAALCyAAQQE6AFwMHQsgBiAFSw0BIAZBAWoiCUUNCAJAIAUg
CUkNACAFIAlrIQQgByAJaiEJIAYhBQwECyAJIAUQioCAgAAACyAFIAcQiICAgAAACyAGIAUQiICAgAAAC0EAIQlBACEECyAAIAQ2AjwgACAJNgI4IAAgBzYC
MCAAQRc6AFggACAFNgI0IAAgAkH/AXFBAEcgAUH/AXFBB0ZxOgBAIABBMGohBAwYCwJAAkACQAJAAkACQCAALQAsRQ0AIAAoAgwhBgJAA0AgBigCCCIHIAYo
AgwiBUkNCwJAIAcgBUcNACADQQA6AMgKIAYoAgAoAgAhBSADQQE2AvQKIAMgA0HICmo2AvAKIANB6AtqIAYgA0HwCmpBAUEBIAURgYCAgACAgICAACADLwHs
Cw0GIAMoAugLRQ0BDAILCyAFIAdPDQUgBigCBCAFakEAOgAAIAYoAgxBAWoiBUUNESAGIAU2AgwLIAAoAgwiBSgCDCIGIAUoAggiB0sNASAFKAIEIQgCQAJA
AkACQCAGRQ0AQQAhBwJAA0AgCCAHaiIFLQAAQT1GDQEgBiAHQQFqIgdGDQIMAAsLIAYgB00NASAFQQA6AAAgByAGQX9qIglPDQIgCCAJai0AACIJDQMgBiAH
a0F+aiELIAVBAWohAQwJCyAGQX9qIgcgBksNDCAHIAZPDQUCQCAIIAdqLQAAIgYNAEEAIQFBACELDAkLIAYQg4KAgAAACyAHIAYQiICAgAAACyAHQQFqIAkQ
ioCAgAAACyAJEIOCgIAAAAsgAEEBOgBcDBwLIAYgBxCIgICAAAALIAdBAWogBhCIgICAAAALIABBAToAXAwYCyAFIAcQiICAgAAACwJAAkAgB0FoakFsSQ0A
IAdBAnRBjOPHgABqKAIAIQIDQCACQQN0IgZBzODHgABqKAIAIAdHDQEgBkHI4MeAAGooAgAhBiAIIQUgByEEAkADQCAGLQAAIglBv39qQf8BcUEaSUEFdCAJ
ckH/AXEgBS0AACIJQb9/akH/AXFBGklBBXQgCXJB/wFxRw0BIAZBAWohBiAFQQFqIQUgBEF/aiIERQ0EDAALCyACQQFqIgJFDQYgAkEkSQ0ACwsgAEIANwIw
QQAhBCAAQdgAakEANgIAIABB0ABqQgA3AgAgAEHIAGpCADcCACAAQcAAakIANwIAIABBOGpCADcCAAwYCwJAAkACQAJAAkAgAkHo4seAAGotAABBP3EiBkF3
akEbSQ0AIAYOCQAAAAAAAAEAAg8LIABCADcCMEEAIQQgAEHYAGpBADYCACAAQdAAakIANwIAIABByABqQgA3AgAgAEHAAGpCADcCACAAQThqQgA3AgAMGwsg
AUUNAQJAIAsNACAAQgA3AjBBACEEIABB2ABqQQA2AgAgAEHQAGpCADcCACAAQcgAakIANwIAIABBwABqQgA3AgAgAEE4akIANwIADBsLAkAgAS0AAEE6Rg0A
IABCADcCMEEAIQQgAEHYAGpBADYCACAAQdAAakIANwIAIABByABqQgA3AgAgAEHAAGpCADcCACAAQThqQgA3AgAMGwsCQAJAAkACQCABIAtqLQAAIgYNACAB
QQFqIQYgC0F/aiIFDgIBAgMLIAYQg4KAgAAACyAAQgA3AjBBACEEIABB2ABqQQA2AgAgAEHQAGpCADcCACAAQcgAakIANwIAIABBwABqQgA3AgAgAEE4akIA
NwIADBwLIAYtAABBP0cNACAAQgA3AjBBACEEIABB2ABqQQA2AgAgAEHQAGpCADcCACAAQcgAakIANwIAIABBwABqQgA3AgAgAEE4akIANwIADBsLIABB4wA6
ADggAEEEOgBYIAAgBTYCNCAAIAY2AjAgAEEwaiEEDBoLIAFFDQEgAEEwaiEGAkAgCw0AIAZCADcCAEEAIQQgBkEoakEANgIAIAZBIGpCADcCACAGQRhqQgA3
AgAgBkEQakIANwIAIAZBCGpCADcCAAwaCyAAIAs2AjQgACABNgIwIABBBToAWCAGIQQMGQsgAEIANwIwQQAhBCAAQdgAakEANgIAIABB0ABqQgA3AgAgAEHI
AGpCADcCACAAQcAAakIANwIAIABBOGpCADcCAAwYCyAAQgA3AjBBACEEIABB2ABqQQA2AgAgAEHQAGpCADcCACAAQcgAakIANwIAIABBwABqQgA3AgAgAEE4
akIANwIADBcLAkACQCAALQAsRQ0AIAAoAgwhBgJAAkACQAJAAkACQAJAA0AgBigCCCIHIAYoAgwiBUkNDAJAIAcgBUcNACADQQA6AMgKIAYoAgAoAgAhBSAD
QQE2AvQKIAMgA0HICmo2AvAKIANB6AtqIAYgA0HwCmpBAUEBIAURgYCAgACAgICAACADLwHsCw0HIAMoAugLRQ0BDAILCyAFIAdPDQEgBigCBCAFakEAOgAA
IAYoAgxBAWoiBUUNEiAGIAU2AgwLIAAoAgwiBigCDCIFIAYoAggiB0sNBSAFRQ0DIAYoAgQhB0EAIQYCQANAIAcgBmoiCS0AAEE7Rg0BIAUgBkEBaiIGRg0F
DAALCyAGIAVLDQICQAJAIAcgBkGbgsCAAEEGEISCgIAAQQFxDQAgAyAGNgLUCSADIAc2AtAJAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANB
uN7BgAA2AvAKIAMgA0HoC2o2AvQKAkACQAJAIANB8ApqIANB0AlqEI2CgIAAQf//A3EOAwAWARYLIAMoAvwKIgYgAygC+AoiBUsNAyADKAL0CiAGEICAgIAA
DAELQQANAEEAKQPg+smAACIMp0E4QQBBACAMQiCIpygCABGAgICAAICAgIAAIgZFDQAgBkKq1arVqtWq1ap/NwAAIAZBEGpCqtWq1arVqtWqfzcAACAGQQhq
QqrVqtWq1arVqn83AAAgBkEwakKq1arVqtWq1ap/NwAAIAZBKGpCqtWq1arVqtWqfzcAACAGQSBqQqrVqtWq1arVqn83AAAgBkEYakKq1arVqtWq1ap/NwAA
IANCODcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIAw3A/AKAkACQCADQfAKakEIaiADQdAJahCNgoCAAEH//wNxDgMADwEPCyADKAKECyIGIAMoAoALIgVL
DQUgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYC
gAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAA
gICAgAALIAMvAagKDQEgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQECQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAELIAMoAoALIgZFDQAg
AygC8AohByADKAL0CiEJIAMoAvwKIQUCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAEEBOgBcDB8LIAZBAWoiBEUNCwJA
IAQgBU8NACAHIARqIQEgBiAFa0ECaiECQQAhBgNAIAQgBmoiCCAESQ0NAkAgCSAGakEBai0AAEE7Rg0AIAIgBmohCCAGQQFqIQYgCEUNAgwBCwsgCCAFSQ0J
IAggBRCIgICAAAALAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKQQAhBgJAAkADQCADKAL8CiIJQccA
IAZrIgVqIgcgCUkNDiAGQarGyYAAaiEEAkACQCAHIAMoAvgKIghLDQACQCAHIAhLDQACQCADKAL0CiAJaiIHQfHGyYAATw0AIAQgByAFakkNEgsCQCAFRQ0A
IAcgBCAF/AoAAAsgAygC/AoiByAFaiIJIAdJDREgAyAJNgL8CgwCCyAHIAgQiICAgAAACyADKALwCigCACEHIAMgBTYCpAogAyAENgKgCiADQcgKaiADQfAK
aiADQaAKakEBQQEgBxGBgICAAICAgIAAIAMpA8gKIgynIQUCQCAMQiCIp0H//wNxDgMBFwAXC0EADQJBACkD4PrJgAAiDKdBxwBBAEEAIAxCIIinKAIAEYCA
gIAAgICAgAAiBkUNAgJAQccARQ0AIAZBqgFBxwD8CwALIANCxwA3A4ALIAMgBjYC/AogA0HclceAADYC+AogAyAMNwPwCiADQfgKaiECQQAhBgNAIAMoAoQL
IglBxwAgBmsiBWoiByAJSQ0QIAZBqsbJgABqIQQCQAJAIAcgAygCgAsiCEsNAAJAIAcgCEsNAAJAIAMoAvwKIAlqIgdB8cbJgABPDQAgBCAHIAVqSQ0UCwJA
IAVFDQAgByAEIAX8CgAACyADKAKECyIHIAVqIgkgB0kNEyADIAk2AoQLDAILIAcgCBCIgICAAAALIAMoAvgKKAIAIQcgAyAFNgL8CSADIAQ2AvgJIANByApq
IAIgA0H4CWpBAUEBIAcRgYCAgACAgICAACADKQPICiIMpyEFAkAgDEIgiKdB//8DcQ4DARIAEgsgAygCgAsiBkUNBCADKALwCiEHIAMoAvQKIQkgAygC/Aoh
BQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAADAQLIAYgBWoiBSAGSQ0QIAUhBiAFQccASQ0ACwJAIAMoAoQLIgYgAygCgAsi
BUsNACADQgA3A4ALIAMoAvwKIQcgA0Gq1arVejYC/AogAyAFNgLQCiADIAY2AswKIAMgBzYCyAogA0GgCmogA0HICmogA0HwCmoQuICAgAAgAyADKALQCiIG
NgKACyADIAMoAsgKIgU2AvwKIAMgAygCzAo2AoQLAkAgBkUNACADKALwCiEHIAMoAvQKIQkCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCA
gACAgICAAAsgAy8BqAoNAyADKAKgCiIFIAMoAqQKIgYQgICAgAAgBkUNAwJAIAZFDQAgBUGqASAG/AsACyADIAUgBkEAIAMQuYCAgAAMAwsgBiAFEIiAgIAA
AAsgBiAFaiIFIAZJDQ4gBSEGIAVBxwBJDQALIAMoAvwKIgYgAygC+AoiBUsNASADKAL0CiAGEICAgIAACyAAQQE6AFwMHwsgBiAFEIiAgIAAAAsgBiAFEIiA
gIAAAAsgBSAHEIiAgIAAAAsgBiAFEIiAgIAAAAsgBiAFEIiAgIAAAAsgAEEBOgBcDBkLIABBAToAXAwYCyAFIAcQiICAgAAACyAAQQE6AFwMFwsgByAIakEA
OgAAIAhBAWohCQJAIAggBUF/aiIGTw0AAkAgByAGai0AACIFDQAgACAHIAlqNgI4IAAgCCAEazYCNCAAIAE2AjAgAEEJOgBYIAAgBiAJazYCPCAAQTBqIQQM
GAsgBRCDgoCAAAALIAkgBhCKgICAAAALAkACQAJAIAAtACxFDQACQCAAKAIMIgUoAgwiBiAFKAIIIgdLDQACQCAGDQAgAEEBOgBcDBoLAkACQAJAAkACQAJA
AkACQAJAAkAgBSgCBCIFLQAAQb9/ag4QAgMFBg0NDQ0EDQ0ADQgNAQ0LIAZBAUYNBgwMCyAAQQM6AFggAEEANgI0IABBot7BgAA2AjAgAEEDOgA4IANBAzoA
6AsgBkEBRw0HDAoLIABBAzoAWCAAQQA2AjQgAEGi3sGAADYCMCAAQQE6ADggA0EBOgCACSAGQQFGDQkgBS0AAUE7Rw0KIAAgBkF+ajYCNCAAIAVBAmo2AjAM
CQsgAEEDOgBYIABBADYCNCAAQaLewYAANgIwIABBBDoAOCADQQQ6AKgJIAZBAUYNCCAFLQABQTtHDQkgACAGQX5qNgI0IAAgBUECajYCMAwICyAAQQM6AFgg
AEEANgI0IABBot7BgAA2AjAgAEEFOgA4IANBBToA0AkgBkEBRg0HIAUtAAFBO0cNCCAAIAZBfmo2AjQgACAFQQJqNgIwDAcLIABBAzoAWCAAQQA2AjQgAEGi
3sGAADYCMCAAQQY6ADggA0EGOgD4CSAGQQFGDQYgBS0AAUE7Rw0HIAAgBkF+ajYCNCAAIAVBAmo2AjAMBgsgAEEDOgBYIABBADYCNCAAQaLewYAANgIwIABB
BzoAOCADQQc6AKAKIAZBAUYNBSAFLQABQTtHDQYgACAGQX5qNgI0IAAgBUECajYCMAwFCyAAQQM6AFggAEEANgI0IABBot7BgAA2AjAgAEEAOgA4IANBADoA
yAoMBAsgAEEDOgBYIABBADYCNCAAQaLewYAANgIwIABBAjoAOCADQQI6APAKIAZBAUYNAyAFLQABQTtHDQQgACAGQX5qNgI0IAAgBUECajYCMAwDCyAFLQAB
QTtHDQMgACAGQX5qNgI0IAAgBUECajYCMAwCCyAGIAcQiICAgAAACyAAQQE6AFwMFwsgAEEwaiEEDBYLIABBAToAXAwVCyAALQAsRQ0EIAAoAgwhBgJAAkAC
QAJAAkACQAJAA0AgBigCCCIHIAYoAgwiBUkNDwJAIAcgBUcNACADQQA6AMgKIAYoAgAoAgAhBSADQQE2AvQKIAMgA0HICmo2AvAKIANB6AtqIAYgA0HwCmpB
AUEBIAURgYCAgACAgICAACADLwHsCw0NIAMoAugLRQ0BDAILCyAFIAdPDQEgBigCBCAFakEAOgAAIAYoAgxBAWoiBUUNDiAGIAU2AgwLIAAoAgwiBSgCDCIG
IAUoAggiB0sNBCAGRQ0JIAUoAgQhC0EAIQcCQANAIAsgB2otAABBO0YNASAGIAdBAWoiB0YNCwwACwsgB0EBaiIJRQ0NIAZBf2oiBSAGSw0NIAUgCUkNAyAF
IAZPDQIgCyAFai0AACIEDQEgBSAJayIIQYAgTQ0FAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKQQAh
BgNAIAMoAvwKIglBLyAGayIFaiIHIAlJDQ4gBkHCw8mAAGohBAJAAkAgByADKAL4CiIISw0AAkAgByAISw0AAkAgAygC9AogCWoiB0Hxw8mAAE8NACAEIAcg
BWpJDRILAkAgBUUNACAHIAQgBfwKAAALIAMoAvwKIgcgBWoiCSAHSQ0RIAMgCTYC/AoMAgsgByAIEIiAgIAAAAsgAygC8AooAgAhByADIAU2AqQKIAMgBDYC
oAogA0HICmogA0HwCmogA0GgCmpBAUEBIAcRgYCAgACAgICAACADKQPICiIMpyEFAkAgDEIgiKdB//8DcQ4DARAAEAtBAA0MQQApA+D6yYAAIgynQS9BAEEA
IAxCIIinKAIAEYCAgIAAgICAgAAiBkUNDCAGQqrVqtWq1arVqn83AAAgBkEQakKq1arVqtWq1ap/NwAAIAZBCGpCqtWq1arVqtWqfzcAACAGQSdqQqrVqtWq
1arVqn83AAAgBkEgakKq1arVqtWq1ap/NwAAIAZBGGpCqtWq1arVqtWqfzcAACADQi83A4ALIAMgBjYC/AogA0HclceAADYC+AogAyAMNwPwCiADQfAKakEI
aiECQQAhBgNAIAMoAoQLIglBLyAGayIFaiIHIAlJDRAgBkHCw8mAAGohBAJAAkAgByADKAKACyIISw0AAkAgByAISw0AAkAgAygC/AogCWoiB0Hxw8mAAE8N
ACAEIAcgBWpJDRQLAkAgBUUNACAHIAQgBfwKAAALIAMoAoQLIgcgBWoiCSAHSQ0MIAMgCTYChAsMAgsgByAIEIiAgIAAAAsgAygC+AooAgAhByADIAU2AvwJ
IAMgBDYC+AkgA0HICmogAiADQfgJakEBQQEgBxGBgICAAICAgIAAIAMpA8gKIgynIQUCQCAMQiCIp0H//wNxDgMBCwALCyADKAKACyIGRQ0OIAMoAvAKIQcg
AygC9AohCSADKAL8CiEFAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAAMDgsgBiAFaiIFIAZJDQkgBSEGIAVBL0kNAAsCQCAD
KAKECyIGIAMoAoALIgVLDQAgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiA
gIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAG
QQBBACAJKAIMEYGAgIAAgICAgAALIAMvAagKDQ0gAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQ0CQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAA
DA0LIAYgBRCIgICAAAALIAYgBWoiBSAGSQ0OIAUhBiAFQS9JDQALAkAgAygC/AoiBiADKAL4CiIFTQ0AIAYgBRCIgICAAAALIAMoAvQKIAYQgICAgAAMCgsg
BSAHEIiAgIAAAAsgBBCDgoCAAAALIAVBAWogBhCIgICAAAALIAkgBRCKgICAAAALIAYgBxCIgICAAAALAkACQAJAAkACQAJAIAsgCWoiASAIELaBgIAAQQFx
RQ0AQQAhBANAAkACQCAEIAhJDQBBACEFQQAhAgwBCwJAAkAgASAEaiwAACIFQX9MDQBBoN7JgAAhBQwBCwJAIAVBYHFBQEcNAEGk3smAACEFDAELAkAgBUFw
cUFgRw0AQajeyYAAIQUMAQtBrN7JgABBwrrIgAAgBUF4cUFwRhshBQsgBS8BACIJDQYgBCAFLQACQQdxIgVqIgkgBEkNCCAJIAVrIgQgCUsNCCAJIARJDQUg
CSAISw0EIAEgBGohBSAJIARrIQIgCSEECyAFRQ0GAkACQAJAAkACQCACQX9qDgQAAQIDEwsgBS0AACEFIANBADoA6gsgAyAFOwHoCyADQQA7ANEJIANB6Atq
IQUgA0HQCWohCQwDCyAFLQAAIglB4AFxQcABRw0KAkAgBSwAASIFQUBIDQAgA0EAOgDqCyADQYDgADsB6AsgA0EAOgD6CSADQQA7AfgJIANB+AlqIQUgA0Ho
C2ohCQwDCwJAIAlBH3EiCUEBSw0AIANBADoA6gsgA0GA4gA7AegLIANBADoA+gkgA0EAOwH4CSADQfgJaiEFIANB6AtqIQkMAwsgA0EAOgD6CSADQQA7AOkL
IAMgCUEGdCAFQT9xcjsB+AkgA0H4CWohBSADQegLaiEJDAILIAUtAAAiAkHwAXFB4AFHDQlBMCEJAkACQCAFLAABIgpBv39KDQAgBSwAAiIFQb9/Sg0AQTEh
CSAKQT9xQQZ0IAJBD3FBDHRyIgJB/w9LDQELIAMgCTsA0QkgA0GgCmohBSADQdAJaiEJDAILAkAgAiAFQT9xciIFQYDwA3FBgLADRw0AIANBADoA0gkgA0GA
5AA7AdAJIANBADoAogogA0EAOwGgCiADQaAKaiEFIANB0AlqIQkMAgsgA0EAOgCiCiADIAU7AaAKIANBADsA0QkgA0GgCmohBSADQdAJaiEJDAELIAUtAAAi
CUH4AXFB8AFHDQgCQCAFLAABIgJBQEgNACADQQA6AOoLIANBgOAAOwHoCyADQQA6AMoKIANBADsByAogA0HICmohBSADQegLaiEJDAELAkAgBSwAAiIKQUBI
DQAgA0EAOgDqCyADQYDgADsB6AsgA0EAOgDKCiADQQA7AcgKIANByApqIQUgA0HoC2ohCQwBCwJAIAUsAAMiBUFASA0AIANBADoA6gsgA0GA4AA7AegLIANB
ADoAygogA0EAOwHICiADQcgKaiEFIANB6AtqIQkMAQsCQCACQT9xQQZ0IAlBB3FBDHRyIglB/wdLDQAgA0EAOgDqCyADQYDiADsB6AsgA0EAOgDKCiADQQA7
AcgKIANByApqIQUgA0HoC2ohCQwBCwJAIAlBgIgBSQ0AIANBADoA6gsgA0GA5gA7AegLIANBADoAygogA0EAOwHICiADQcgKaiEFIANB6AtqIQkMAQsgA0EA
OwDpCyADIAkgCkE/cXJBBnQgBUE/cXIiBTsByAogAyAFQRB2OgDKCiADQcgKaiEFIANB6AtqIQkLIAkvAAEiCQ0CIAMgBS8BACAFQQJqLQAAQRB0ciIFQRB2
QR9xIgk6APIKIAVB//8DcSAJQRB0ciIFQf8ARg0BIAVBIEkNASAFQeD//wBxQYABRw0ACwsCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G4
3sGAADYC8AogAyADQegLajYC9ApBACEGA0AgAygC/AoiCUHBACAGayIFaiIHIAlJDQYgBkHxw8mAAGohBAJAAkAgByADKAL4CiIISw0AAkAgByAISw0AAkAg
AygC9AogCWoiB0GyxMmAAE8NACAEIAcgBWpJDQoLAkAgBUUNACAHIAQgBfwKAAALIAMoAvwKIgcgBWoiCSAHSQ0JIAMgCTYC/AoMAgsgByAIEIiAgIAAAAsg
AygC8AooAgAhByADIAU2AqQKIAMgBDYCoAogA0HICmogA0HwCmogA0GgCmpBAUEBIAcRgYCAgACAgICAACADKQPICiIMpyEFAkAgDEIgiKdB//8DcQ4DAQ8A
DwtBAA0LQQApA+D6yYAAIgynQcEAQQBBACAMQiCIpygCABGAgICAAICAgIAAIgZFDQsCQEHBAEUNACAGQaoBQcEA/AsACyADQsEANwOACyADIAY2AvwKIANB
3JXHgAA2AvgKIAMgDDcD8AogA0H4CmohAkEAIQYDQCADKAKECyIJQcEAIAZrIgVqIgcgCUkNCCAGQfHDyYAAaiEEAkACQCAHIAMoAoALIghLDQACQCAHIAhL
DQACQCADKAL8CiAJaiIHQbLEyYAATw0AIAQgByAFakkNDAsCQCAFRQ0AIAcgBCAF/AoAAAsgAygChAsiByAFaiIJIAdJDQsgAyAJNgKECwwCCyAHIAgQiICA
gAAACyADKAL4CigCACEHIAMgBTYC/AkgAyAENgL4CSADQcgKaiACIANB+AlqQQFBASAHEYGAgIAAgICAgAAgAykDyAoiDKchBQJAIAxCIIinQf//A3EOAwEK
AAoLIAMoAoALIgZFDQ0gAygC8AohByADKAL0CiEJIAMoAvwKIQUCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAwNCyAGIAVq
IgUgBkkNCCAFIQYgBUHBAEkNAAsCQCADKAKECyIGIAMoAoALIgVLDQAgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAc2
AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohByADKAL0CiEJ
AkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAALIAMvAagKDQwgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQwCQCAGRQ0AIAVB
qgEgBvwLAAsgAyAFIAZBACADELmAgIAADAwLIAYgBRCIgICAAAALIAYgBWoiBSAGSQ0GIAUhBiAFQcEASQ0ACwJAIAMoAvwKIgYgAygC+AoiBUsNACADKAL0
CiAGEICAgIAADAoLIAYgBRCIgICAAAALIAkQjICAgAAACyAJIAgQiICAgAAACyAEIAkQioCAgAAACyAJEIyAgIAAAAsgAEEANgA5IABBAToAOCAAIAg2AjQg
ACABNgIwIABBFjoAWCAAQT1qQQA6AAAgAEEwaiEEIAdFDRQgByAGSw0CIABBOWohFUEALQDR+cmAAEEDcSEWQQAtAND5yYAAQQNxIRRBAC0Az/nJgABBA3Eh
E0EAKQPg+smAACIMQiCIpyEPIANB8ApqQQhqIRIgDKchDkEAIQpBASEQA0ACQAJAAkACQAJAAkAgEEH/AXENAEEAIRBBACEJQQAhBQwBC0EAIQZBACEQIAch
BQJAIAogB08NACAHIAprIQggCyAKaiEJQQAhBgJAA0AgCiAGaiIFIApJDQkgCSAGai0AAEE6Rg0BIAggBkEBaiIGRw0AC0EAIQZBACEQIAchBQwBCyAFQQFq
IgZFDQdBASEQCyAFIApJDQIgBSAHSw0BIAsgCmohCSAFIAprIQUgBiEKCyAJRQ0ZAkAgBUUNAEEAIQYCQAJAAkADQCAJIAZqIggtAABBPUYNASAFIAZBAWoi
BkYNAgwACwsgBkEBaiICRQ0IIAYgBU0NASAGIAUQiICAgAAAC0EAIQZBACEIIAVBAUcNAQwFCyAGQQFGDQMLAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANC
gBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKQQAhBgNAIAMoAvwKIghBOiAGayIFaiIJIAhJDQYgBkGyxMmAAGohAgJAAkAgCSADKAL4CiIBSw0AAkAg
CSABSw0AAkAgAygC9AogCGoiCUHsxMmAAE8NACACIAkgBWpJDQoLAkAgBUUNACAJIAIgBfwKAAALIAMoAvwKIgkgBWoiCCAJSQ0JIAMgCDYC/AoMAgsgCSAB
EIiAgIAAAAsgAygC8AooAgAhCSADIAU2AqQKIAMgAjYCoAogA0HICmogA0HwCmogA0GgCmpBAUEBIAkRgYCAgACAgICAACADKQPICiIXpyEFAkAgF0IgiKdB
//8DcQ4DAQ8ADwtBAA0GIA5BOkEAQQAgDygCABGAgICAAICAgIAAIgZFDQYgBkKq1arVqtWq1ap/NwAAIAZBEGpCqtWq1arVqtWqfzcAACAGQQhqQqrVqtWq
1arVqn83AAAgBkE4akGq1X47AAAgBkEwakKq1arVqtWq1ap/NwAAIAZBKGpCqtWq1arVqtWqfzcAACAGQSBqQqrVqtWq1arVqn83AAAgBkEYakKq1arVqtWq
1ap/NwAAIANCOjcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIAw3A/AKQQAhBgNAIAMoAoQLIghBOiAGayIFaiIJIAhJDQggBkGyxMmAAGohAgJAAkAgCSAD
KAKACyIBSw0AAkAgCSABSw0AAkAgAygC/AogCGoiCUHsxMmAAE8NACACIAkgBWpJDQwLAkAgBUUNACAJIAIgBfwKAAALIAMoAoQLIgkgBWoiCCAJSQ0LIAMg
CDYChAsMAgsgCSABEIiAgIAAAAsgAygC+AooAgAhCSADIAU2AvwJIAMgAjYC+AkgA0HICmogEiADQfgJakEBQQEgCRGBgICAAICAgIAAIAMpA8gKIhenIQUC
QCAXQiCIp0H//wNxDgMBCgAKCyADKAKACyIGRQ0IIAMoAvAKIQkgAygC9AohCCADKAL8CiEFAkAgBkUNACAFQaoBIAb8CwALIAkgBSAGQQBBACAIKAIMEYGA
gIAAgICAgAAMCAsgBiAFaiIFIAZJDQggBSEGIAVBOkkNAAsCQCADKAKECyIGIAMoAoALIgVLDQAgA0IANwOACyADKAL8CiEJIANBqtWq1Xo2AvwKIAMgBTYC
0AogAyAGNgLMCiADIAk2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAg
AygC8AohCSADKAL0CiEIAkAgBkUNACAFQaoBIAb8CwALIAkgBSAGQQBBACAIKAIMEYGAgIAAgICAgAALIAMvAagKDQcgAygCoAoiBSADKAKkCiIGEICAgIAA
IAZFDQcCQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAcLIAYgBRCIgICAAAALIAYgBWoiBSAGSQ0GIAUhBiAFQTpJDQALAkAgAygC/AoiBiAD
KAL4CiIFSw0AIAMoAvQKIAYQgICAgAAMBQsgBiAFEIiAgIAAAAsgBSAHEIiAgIAAAAsgCiAFEIqAgIAAAAsgBSEBAkAgAiAFTw0AIAhBAWohCCAGIAVrQQFq
IQ1BACEGAkADQCACIAZqIgEgAkkNBSAIIAZqLQAAQT1GDQEgDSAGQQFqIgZqDQALIAUhAQwBCyABQX9GDQoLAkACQCABIAJJDQAgASAFTQ0BIAEgBRCIgICA
AAALIAIgARCKgICAAAALIAkgAmohBiABIAJrIQgLAkAgBg0AAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2
AvQKQQAhBgNAIAMoAvwKIghBKSAGayIFaiIJIAhJDQMgBkHsxMmAAGohAgJAAkAgCSADKAL4CiIBSw0AAkAgCSABSw0AAkAgAygC9AogCGoiCUGVxcmAAE8N
ACACIAkgBWpJDQcLAkAgBUUNACAJIAIgBfwKAAALIAMoAvwKIgkgBWoiCCAJSQ0GIAMgCDYC/AoMAgsgCSABEIiAgIAAAAsgAygC8AooAgAhCSADIAU2AqQK
IAMgAjYCoAogA0HICmogA0HwCmogA0GgCmpBAUEBIAkRgYCAgACAgICAACADKQPICiIXpyEFAkAgF0IgiKdB//8DcQ4DAQwADAtBAA0DIA5BKUEAQQAgDygC
ABGAgICAAICAgIAAIgZFDQMgBkKq1arVqtWq1ap/NwAAIAZBEGpCqtWq1arVqtWqfzcAACAGQQhqQqrVqtWq1arVqn83AAAgBkEoakGqfzoAACAGQSBqQqrV
qtWq1arVqn83AAAgBkEYakKq1arVqtWq1ap/NwAAIANCKTcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIAw3A/AKQQAhBgNAIAMoAoQLIghBKSAGayIFaiIJ
IAhJDQUgBkHsxMmAAGohAgJAAkAgCSADKAKACyIBSw0AAkAgCSABSw0AAkAgAygC/AogCGoiCUGVxcmAAE8NACACIAkgBWpJDQkLAkAgBUUNACAJIAIgBfwK
AAALIAMoAoQLIgkgBWoiCCAJSQ0IIAMgCDYChAsMAgsgCSABEIiAgIAAAAsgAygC+AooAgAhCSADIAU2AvwJIAMgAjYC+AkgA0HICmogEiADQfgJakEBQQEg
CRGBgICAAICAgIAAIAMpA8gKIhenIQUCQCAXQiCIp0H//wNxDgMBBwAHCyADKAKACyIGRQ0FIAMoAvAKIQkgAygC9AohCCADKAL8CiEFAkAgBkUNACAFQaoB
IAb8CwALIAkgBSAGQQBBACAIKAIMEYGAgIAAgICAgAAMBQsgBiAFaiIFIAZJDQUgBSEGIAVBKUkNAAsCQCADKAKECyIGIAMoAoALIgVLDQAgA0IANwOACyAD
KAL8CiEJIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAk2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIF
NgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohCSADKAL0CiEIAkAgBkUNACAFQaoBIAb8CwALIAkgBSAGQQBBACAIKAIMEYGAgIAAgICAgAALIAMvAagK
DQQgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQQCQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAQLIAYgBRCIgICAAAALIAYgBWoiBSAGSQ0D
IAUhBiAFQSlJDQALAkAgAygC/AoiBiADKAL4CiIFSw0AIAMoAvQKIAYQgICAgAAMAgsgBiAFEIiAgIAAAAsCQCAIRQ0AIAktAAAhDQJAAkACQAJAAkAgBi0A
ACIFQVVqDgMAAgECC0EqIQIgCEF/aiIBRQ0DIAZBAWoiBS0AAEHfAEYNAyAGIAhqQX9qLQAAQd8ARg0DQQAhBgNAAkAgBS0AACIIQd8ARg0AIAhBUGpB/wFx
IghBCUsNBQJAAkAgBkH/AXEiBg0AQQAhBgwBCyADIAZBCmwiBkEIdiIRQQBHOgDoCyARDQULIAMgBkH/AXEgCGoiBkH/AXEiCCAGRzoA6AsgCCAGRw0ECyAF
QQFqIQUgAUF/aiIBDQALIAZB/wFxQQ9LDQIgAyAGQQ9xIgY6APAKIAZBEHQhAgwDC0EqIQIgCEF/aiIBRQ0CIAZBAWoiBS0AAEHfAEYNAiAGIAhqQX9qLQAA
Qd8ARg0CAkACQANAAkAgBS0AACIGQd8ARg0AIAZBUGpB/wFxQQlLDQYgBkEwSw0CCyAFQQFqIQUgAUF/aiIBRQ0CDAALC0EBIQIMAwtBACECIANBADoA6AsM
AgtBKiECIAVB3wBGDQEgBiAIakF/ai0AAEHfAEYNAUEAIQUCQANAAkAgBi0AACIBQd8ARg0AIAFBUGpB/wFxIgFBCUsNBAJAAkAgBUH/AXEiBQ0AQQAhBQwB
CyADIAVBCmwiBUEIdiIRQQBHOgDoCyARDQMLIAMgBUH/AXEgAWoiBUH/AXEiASAFRzoA6AsgASAFRw0CCyAGQQFqIQYgCEF/aiIIDQALIAVB/wFxQQ9LDQAg
AyAFQQ9xIgY6APAKIAZBEHQhAgwCC0EBIQIMAQtBASECCyACQf//A3ENACACQRB2IQYCQAJAAkACQAJAAkACQAJAAkACQCANQZx/ag4UAgUFBQQFBQUFBQEF
BQUFBgUFAwAFCyAGQRx0QRx1QX9MDQkgFSAGQQdxOgAADAoLIAAgBjoAOgwJCyAAIAY6ADsMCAsCQCATIAZGDQAgFCAGRg0AIBYgBkcNBwsgBkEDcSIGQQNG
DQ8gACAGOgA8DAcLAkAgEyAGRg0AIBQgBkYNACAWIAZHDQYLIAZBA3EiBkEDRg0OIAAgBjoAPQwGCyADIAktAAA6AKgJAkBBgBBFDQAgA0HoC2pBqgFBgBD8
CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKAkACQCADQfAKaiADQagJahCOgoCAAEH//wNxDgMADwEPCyADKAL8CiIGIAMoAvgKIgVLDQMg
AygC9AogBhCAgICAAAwGC0EADQUgDkEuQQBBACAPKAIAEYCAgIAAgICAgAAiBkUNBSAGQqrVqtWq1arVqn83AAAgBkEQakKq1arVqtWq1ap/NwAAIAZBCGpC
qtWq1arVqtWqfzcAACAGQSZqQqrVqtWq1arVqn83AAAgBkEgakKq1arVqtWq1ap/NwAAIAZBGGpCqtWq1arVqtWqfzcAACADQi43A4ALIAMgBjYC/AogA0Hc
lceAADYC+AogAyAMNwPwCgJAAkAgEiADQagJahCOgoCAAEH//wNxDgMACAEICyADKAKECyIGIAMoAoALIgVLDQIgA0IANwOACyADKAL8CiEJIANBqtWq1Xo2
AvwKIAMgBTYC0AogAyAGNgLMCiADIAk2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKE
CwJAIAZFDQAgAygC8AohCSADKAL0CiEIAkAgBkUNACAFQaoBIAb8CwALIAkgBSAGQQBBACAIKAIMEYGAgIAAgICAgAALIAMvAagKDQYgAygCoAoiBSADKAKk
CiIGEICAgIAAIAZFDQYCQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAYLIAMoAoALIgZFDQUgAygC8AohCSADKAL0CiEIIAMoAvwKIQUCQCAG
RQ0AIAVBqgEgBvwLAAsgCSAFIAZBAEEAIAgoAgwRgYCAgACAgICAAAwFCyAGDQIMAwsgBiAFEIiAgIAAAAsgBiAFEIiAgIAAAAsgBkEcdEEcdUF/TA0AIAAg
BkEHcToAOAwBCyAJLQAAIQYgA0EtOwHSCSADIAY6ANAJAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQK
AkACQCADQfAKaiADQdAJahCPgoCAAEH//wNxDgMACgEKCyADKAL8CiIGIAMoAvgKIgVLDQMgAygC9AogBhCAgICAAAwBC0EADQAgDkE7QQBBACAPKAIAEYCA
gIAAgICAgAAiBkUNACAGQqrVqtWq1arVqn83AAAgBkEQakKq1arVqtWq1ap/NwAAIAZBCGpCqtWq1arVqtWqfzcAACAGQTdqQarVqtV6NgAAIAZBMGpCqtWq
1arVqtWqfzcAACAGQShqQqrVqtWq1arVqn83AAAgBkEgakKq1arVqtWq1ap/NwAAIAZBGGpCqtWq1arVqtWqfzcAACADQjs3A4ALIAMgBjYC/AogA0HclceA
ADYC+AogAyAMNwPwCgJAAkACQCASIANB0AlqEI+CgIAAQf//A3EOAwAEAQQLIAMoAoQLIgYgAygCgAsiBUsNASADQgA3A4ALIAMoAvwKIQkgA0Gq1arVejYC
/AogAyAFNgLQCiADIAY2AswKIAMgCTYCyAogA0GgCmogA0HICmogA0HwCmoQuICAgAAgAyADKALQCiIGNgKACyADIAMoAsgKIgU2AvwKIAMgAygCzAo2AoQL
AkAgBkUNACADKALwCiEJIAMoAvQKIQgCQCAGRQ0AIAVBqgEgBvwLAAsgCSAFIAZBAEEAIAgoAgwRgYCAgACAgICAAAsgAy8BqAoNAiADKAKgCiIFIAMoAqQK
IgYQgICAgAAgBkUNAgJAIAZFDQAgBUGqASAG/AsACyADIAUgBkEAIAMQuYCAgAAMAgsgAygCgAsiBkUNASADKALwCiEJIAMoAvQKIQggAygC/AohBQJAIAZF
DQAgBUGqASAG/AsACyAJIAUgBkEAQQAgCCgCDBGBgICAAICAgIAADAELCyAGIAUQiICAgAAACwALIAYgBRCIgICAAAALIAcgBhCIgICAAAALAkBBgBBFDQAg
A0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKQQAhBgNAIAMoAvwKIglBPCAGayIFaiIHIAlJDQQgBkGGw8mAAGohBAJA
AkAgByADKAL4CiIISw0AAkAgByAISw0AAkAgAygC9AogCWoiB0HCw8mAAE8NACAEIAcgBWpJDQgLAkAgBUUNACAHIAQgBfwKAAALIAMoAvwKIgcgBWoiCSAH
SQ0HIAMgCTYC/AoMAgsgByAIEIiAgIAAAAsgAygC8AooAgAhByADIAU2AqQKIAMgBDYCoAogA0HICmogA0HwCmogA0GgCmpBAUEBIAcRgYCAgACAgICAACAD
KQPICiIMpyEFAkAgDEIgiKdB//8DcQ4DAQYABgtBAA0CQQApA+D6yYAAIgynQTxBAEEAIAxCIIinKAIAEYCAgIAAgICAgAAiBkUNAiAGQqrVqtWq1arVqn83
AAAgBkEQakKq1arVqtWq1ap/NwAAIAZBCGpCqtWq1arVqtWqfzcAACAGQThqQarVqtV6NgAAIAZBMGpCqtWq1arVqtWqfzcAACAGQShqQqrVqtWq1arVqn83
AAAgBkEgakKq1arVqtWq1ap/NwAAIAZBGGpCqtWq1arVqtWqfzcAACADQjw3A4ALIAMgBjYC/AogA0HclceAADYC+AogAyAMNwPwCiADQfAKakEIaiECQQAh
BgNAIAMoAoQLIglBPCAGayIFaiIHIAlJDQYgBkGGw8mAAGohBAJAAkAgByADKAKACyIISw0AAkAgByAISw0AAkAgAygC/AogCWoiB0HCw8mAAE8NACAEIAcg
BWpJDQoLAkAgBUUNACAHIAQgBfwKAAALIAMoAoQLIgcgBWoiCSAHSQ0JIAMgCTYChAsMAgsgByAIEIiAgIAAAAsgAygC+AooAgAhByADIAU2AvwJIAMgBDYC
+AkgA0HICmogAiADQfgJakEBQQEgBxGBgICAAICAgIAAIAMpA8gKIgynIQUCQCAMQiCIp0H//wNxDgMBCAAICyADKAKACyIGRQ0EIAMoAvAKIQcgAygC9Aoh
CSADKAL8CiEFAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAAMBAsgBiAFaiIFIAZJDQYgBSEGIAVBPEkNAAsCQCADKAKECyIG
IAMoAoALIgVLDQAgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMg
AygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJ
KAIMEYGAgIAAgICAgAALIAMvAagKDQMgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQMCQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAMLIAYg
BRCIgICAAAALIAYgBWoiBSAGSQ0EIAUhBiAFQTxJDQALIAMoAvwKIgYgAygC+AoiBUsNASADKAL0CiAGEICAgIAACyAAQQE6AFwMDgsgBiAFEIiAgIAAAAsC
QAJAAkACQCAALQAsRQ0AAkAgACgCDCIGKAIMIgUgBigCCCIHSw0AAkAgBQ0AIABBAToAXAwTCyAGKAIEIQZBBiEHAkACQAJAIAVBBkkNAEEAIQIgBkGBosCA
AEYNByAGKAAAQfPohZMHRw0BIAYoAAJB4eTR6wNHDQEMBwsgBUEESQ0BC0EEIQdBASECIAZBiKLAgABGDQUgBigAAEHl3JHrA0YNBQsgAyAGNgLQCSADIAVB
CiAFQQpJGzYC1AkCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G43sGAADYC8AogAyADQegLajYC9AoCQAJAAkAgA0HwCmogA0HQCWoQkIKA
gABB//8DcQ4DAAgBCAsgAygC/AoiBiADKAL4CiIFSw0FIAMoAvQKIAYQgICAgAAMAQtBAA0AQQApA+D6yYAAIgynQdMAQQBBACAMQiCIpygCABGAgICAAICA
gIAAIgZFDQACQEHTAEUNACAGQaoBQdMA/AsACyADQtMANwOACyADIAY2AvwKIANB3JXHgAA2AvgKIAMgDDcD8AoCQAJAIANB+ApqIANB0AlqEJCCgIAAQf//
A3EOAwAIAQgLIAMoAoQLIgYgAygCgAsiBUsNBCADQgA3A4ALIAMoAvwKIQcgA0Gq1arVejYC/AogAyAFNgLQCiADIAY2AswKIAMgBzYCyAogA0GgCmogA0HI
CmogA0HwCmoQuICAgAAgAyADKALQCiIGNgKACyADIAMoAsgKIgU2AvwKIAMgAygCzAo2AoQLAkAgBkUNACADKALwCiEHIAMoAvQKIQkCQCAGRQ0AIAVBqgEg
BvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAy8BqAoNASADKAKgCiIFIAMoAqQKIgYQgICAgAAgBkUNAQJAIAZFDQAgBUGqASAG/AsACyADIAUg
BkEAIAMQuYCAgAAMAQsgAygCgAsiBkUNACADKALwCiEHIAMoAvQKIQkgAygC/AohBQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICA
gIAACyAAQQE6AFwMEgsgBSAHEIiAgIAAAAsgAEEBOgBcDBALIAYgBRCIgICAAAALIAYgBRCIgICAAAALAkAgBSAHTw0AIAcgBRCKgICAAAALAkACQAJAAkAg
BSAHRg0AQoCAgIAQIQwgBiAHaiIJIQYgBSAHayIIIQUCQANAIAYtAABBO0YNASAGQQFqIQYgDEIBfCEMIAVBf2oiBQ0AC0IAIQwLIAggDKcgDEKAgICA8B+D
UBsiBSAISw0BIAVBv39qQUBJDQNBACEGDAILAkBBgBBFDQAgA0HoC2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKQQAhBgJA
AkADQCADKAL8CiIJQTkgBmsiBWoiByAJSQ0GIAZBo8HJgABqIQQCQAJAIAcgAygC+AoiCEsNAAJAIAcgCEsNAAJAIAMoAvQKIAlqIgdB3MHJgABPDQAgBCAH
IAVqSQ0KCwJAIAVFDQAgByAEIAX8CgAACyADKAL8CiIHIAVqIgkgB0kNCSADIAk2AvwKDAILIAcgCBCIgICAAAALIAMoAvAKKAIAIQcgAyAFNgKkCiADIAQ2
AqAKIANByApqIANB8ApqIANBoApqQQFBASAHEYGAgIAAgICAgAAgAykDyAoiDKchBQJAIAxCIIinQf//A3EOAwEIAAgLQQANAkEAKQPg+smAACIMp0E5QQBB
ACAMQiCIpygCABGAgICAAICAgIAAIgZFDQIgBkKq1arVqtWq1ap/NwAAIAZBEGpCqtWq1arVqtWqfzcAACAGQQhqQqrVqtWq1arVqn83AAAgBkE4akGqfzoA
ACAGQTBqQqrVqtWq1arVqn83AAAgBkEoakKq1arVqtWq1ap/NwAAIAZBIGpCqtWq1arVqtWqfzcAACAGQRhqQqrVqtWq1arVqn83AAAgA0I5NwOACyADIAY2
AvwKIANB3JXHgAA2AvgKIAMgDDcD8AogA0HwCmpBCGohAkEAIQYDQCADKAKECyIJQTkgBmsiBWoiByAJSQ0IIAZBo8HJgABqIQQCQAJAIAcgAygCgAsiCEsN
AAJAIAcgCEsNAAJAIAMoAvwKIAlqIgdB3MHJgABPDQAgBCAHIAVqSQ0MCwJAIAVFDQAgByAEIAX8CgAACyADKAKECyIHIAVqIgkgB0kNCyADIAk2AoQLDAIL
IAcgCBCIgICAAAALIAMoAvgKKAIAIQcgAyAFNgL8CSADIAQ2AvgJIANByApqIAIgA0H4CWpBAUEBIAcRgYCAgACAgICAACADKQPICiIMpyEFAkAgDEIgiKdB
//8DcQ4DAQoACgsgAygCgAsiBkUNBCADKALwCiEHIAMoAvQKIQkgAygC/AohBQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAA
DAQLIAYgBWoiBSAGSQ0IIAUhBiAFQTlJDQALAkAgAygChAsiBiADKAKACyIFSw0AIANCADcDgAsgAygC/AohByADQarVqtV6NgL8CiADIAU2AtAKIAMgBjYC
zAogAyAHNgLICiADQaAKaiADQcgKaiADQfAKahC4gICAACADIAMoAtAKIgY2AoALIAMgAygCyAoiBTYC/AogAyADKALMCjYChAsCQCAGRQ0AIAMoAvAKIQcg
AygC9AohCQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAACyADLwGoCg0DIAMoAqAKIgUgAygCpAoiBhCAgICAACAGRQ0DAkAg
BkUNACAFQaoBIAb8CwALIAMgBSAGQQAgAxC5gICAAAwDCyAGIAUQiICAgAAACyAGIAVqIgUgBkkNBiAFIQYgBUE5SQ0ACyADKAL8CiIGIAMoAvgKIgVLDQEg
AygC9AogBhCAgICAAAsgAEEBOgBcDBALIAYgBRCIgICAAAALIAUgCBCIgICAAAALAkACQAJAAkACQAJAAkACQAJAA0AgCSAGai0AACIHQYF/akH/AXFBoQFJ
DQEgBSAGQQFqIgZGDQIMAAsLIAMgBzoAgAkCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G43sGAADYC8AogAyADQegLajYC9AogA0HwCmog
A0GACWoQkYKAgABB//8DcQ4DAQkCCQsgBSAISQ0DQQAhBkGi3sGAACEHDAQLIAMoAvwKIgYgAygC+AoiBUsNBSADKAL0CiAGEICAgIAADAELQQANAEEAKQPg
+smAACIMp0HQAEEAQQAgDEIgiKcoAgARgICAgACAgICAACIGRQ0AAkBB0ABFDQAgBkGqAUHQAPwLAAsgA0LQADcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiAD
IAw3A/AKAkACQCADQfgKaiADQYAJahCRgoCAAEH//wNxDgMACAEICyADKAKECyIGIAMoAoALIgVLDQQgA0IANwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMg
BTYC0AogAyAGNgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyADKALICiIFNgL8CiADIAMoAswKNgKECwJAIAZF
DQAgAygC8AohByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAALIAMvAagKDQEgAygCoAoiBSADKAKkCiIGEICA
gIAAIAZFDQECQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAELIAMoAoALIgZFDQAgAygC8AohByADKAL0CiEJIAMoAvwKIQUCQCAGRQ0AIAVB
qgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAEEBOgBcDBELIAggBUEBaiIHayEGIAkgB2ohBwsgACACOgBAIABBGDoAWCAAIAc2AjggACAF
NgI0IAAgCTYCMCAAIAY2AjwgAEEwaiEEDBALIAYgBRCIgICAAAALIAYgBRCIgICAAAALIAMgBTYCqAkCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC
+AogA0G43sGAADYC8AogAyADQegLajYC9AoCQAJAAkAgA0HwCmogA0GoCWoQkoKAgABB//8DcQ4DAAMBAwsgAygC/AoiBiADKAL4CiIFSw0EIAMoAvQKIAYQ
gICAgAAMAQtBAA0AQQApA+D6yYAAIgynQdEAQQBBACAMQiCIpygCABGAgICAAICAgIAAIgZFDQACQEHRAEUNACAGQaoBQdEA/AsACyADQtEANwOACyADIAY2
AvwKIANB3JXHgAA2AvgKIAMgDDcD8AoCQAJAIANB+ApqIANBqAlqEJKCgIAAQf//A3EOAwADAQMLIAMoAoQLIgYgAygCgAsiBUsNAyADQgA3A4ALIAMoAvwK
IQcgA0Gq1arVejYC/AogAyAFNgLQCiADIAY2AswKIAMgBzYCyAogA0GgCmogA0HICmogA0HwCmoQuICAgAAgAyADKALQCiIGNgKACyADIAMoAsgKIgU2AvwK
IAMgAygCzAo2AoQLAkAgBkUNACADKALwCiEHIAMoAvQKIQkCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAy8BqAoNASAD
KAKgCiIFIAMoAqQKIgYQgICAgAAgBkUNAQJAIAZFDQAgBUGqASAG/AsACyADIAUgBkEAIAMQuYCAgAAMAQsgAygCgAsiBkUNACADKALwCiEHIAMoAvQKIQkg
AygC/AohBQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAACyAAQQE6AFwMDQsACyAGIAUQiICAgAAACyAGIAUQiICAgAAACwJA
AkACQAJAAkAgAC0ALEUNACAAKAIMIQYCQAJAAkACQAJAA0AgBigCCCIHIAYoAgwiBUkNDgJAIAcgBUcNACADQQA6AMgKIAYoAgAoAgAhBSADQQE2AvQKIAMg
A0HICmo2AvAKIANB6AtqIAYgA0HwCmpBAUEBIAURgYCAgACAgICAACADLwHsCw0DIAMoAugLRQ0BDAILCwJAIAUgB0kNACAFIAcQiICAgAAMDgsgBigCBCAF
akEAOgAAIAYoAgxBAWoiBUUNDSAGIAU2AgwLIAAoAgwiBigCDCIFIAYoAggiB0sNBSAFDgICAQMLIABBAToAXAwQCyAAQQE6AFwMDwtBAEEAEIiAgIAAAAsg
BigCBCIGLQAAQTtGDQMCQCAGLQABQTtGDQAgAEEBOgBcDA4LIABBBDoAWCAAIAYtAAA6ADggBUF9aiIFQX1LDQICQCAGQQJqIgYgBWotAAAiBw0AIAAgBjYC
MAwFCyAHEIOCgIAAAAsgAEEBOgBcDA0LIAUgBxCIgICAAAALQQJBARCKgICAAAALIABB4wA6ADggAEEEOgBYAkAgBkEBaiIGIAVBfmoiBWotAAAiBw0AIAAg
BjYCMAwBCyAHEIOCgIAAAAsgACAFNgI0IABBMGohBAwJCwJAAkACQAJAAkAgAC0ALEUNACAAKAIMIQYCQANAIAYoAggiByAGKAIMIgVJDQkCQCAHIAVHDQAg
A0EAOgDICiAGKAIAKAIAIQUgA0EBNgL0CiADIANByApqNgLwCiADQegLaiAGIANB8ApqQQFBASAFEYGAgIAAgICAgAAgAy8B7AsNBSADKALoC0UNAQwCCwsg
BSAHTw0FIAYoAgQgBWpBADoAACAGKAIMQQFqIgVFDQggBiAFNgIMCyAAKAIMIgUoAgwiBiAFKAIIIgdLDQEgBSgCBCEHIABBBjoAWCAGQX9qIgUgBksNByAF
IAZPDQMCQCAHIAVqLQAAIgYNACAAQTBqIQQgACAFNgI0IAAgBzYCMAwOCyAGEIOCgIAAAAsgAEEBOgBcDAwLIAYgBxCIgICAAAALIABBAToAXAwJCyAFQQFq
IAYQiICAgAAACyAFIAcQiICAgAAACwJAIAAtAAhFDQAgAC0ALEUNACAAKQIAIQwgAEEIOgBYIABBOGpBACgCxODHgAA2AgAgAEEAKQK84MeAADcCMCAAIAJB
/wFxQQBHIAFB/wFxQQdGcToAPAJAIAAoAgwiBigCDCINIAYoAggiBUsNACAAQTBqIQRBACkD4PrJgAAiF0IgiKchESADQfAKakEIaiETIAYoAgQhDyAXpyES
QQEhCkEAIQECQAJAAkACQANAAkACQAJAAkACQCAKQf8BcQ0AQQAhCkEAIQhBACECDAELQQAhCkEAIQYgDSEFAkAgDSABTQ0AIA0gAWshCSAPIAFqIQdBACEG
AkADQCABIAZqIgUgAUkNDyAHIAZqLQAAQTtGDQEgCSAGQQFqIgZHDQALQQAhCkEAIQYgDSEFDAELIAVBAWoiBkUNDUEBIQoLIAUgAUkNAiAFIA1LDQEgDyAB
aiEIIAUgAWshAiAGIQELIAhFDREgACgCNEGOBEkNAgJAQYAQRQ0AIANB6AtqQaoBQYAQ/AsACyADQoAQNwL4CiADQbjewYAANgLwCiADIANB6AtqNgL0CkEA
IQYDQCADKAL8CiIJQd0AIAZrIgVqIgcgCUkNDCAGQZ3pyIAAaiEEAkACQCAHIAMoAvgKIghLDQACQCAHIAhLDQACQCADKAL0CiAJaiIHQfrpyIAATw0AIAQg
ByAFakkNEAsCQCAFRQ0AIAcgBCAF/AoAAAsgAygC/AoiByAFaiIJIAdJDQ8gAyAJNgL8CgwCCyAHIAgQiICAgAAACyADKALwCigCACEHIAMgBTYCpAogAyAE
NgKgCiADQcgKaiADQfAKaiADQaAKakEBQQEgBxGBgICAAICAgIAAIAMpA8gKIgynIQUCQCAMQiCIp0H//wNxDgMBDgAOC0EADQtBACkD4PrJgAAiDKdB3QBB
AEEAIAxCIIinKAIAEYCAgIAAgICAgAAiBkUNCwJAQd0ARQ0AIAZBqgFB3QD8CwALIANC3QA3A4ALIAMgBjYC/AogA0HclceAADYC+AogAyAMNwPwCiADQfgK
aiECQQAhBgNAIAMoAoQLIglB3QAgBmsiBWoiByAJSQ0OIAZBnenIgABqIQQCQAJAIAcgAygCgAsiCEsNAAJAIAcgCEsNAAJAIAMoAvwKIAlqIgdB+unIgABP
DQAgBCAHIAVqSQ0SCwJAIAVFDQAgByAEIAX8CgAACyADKAKECyIHIAVqIgkgB0kNESADIAk2AoQLDAILIAcgCBCIgICAAAALIAMoAvgKKAIAIQcgAyAFNgKE
CSADIAQ2AoAJIANByApqIAIgA0GACWpBAUEBIAcRgYCAgACAgICAACADKQPICiIMpyEFAkAgDEIgiKdB//8DcQ4DARAAEAsgAygCgAsiBkUNDSADKALwCiEH
IAMoAvQKIQkgAygC/AohBQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAADA0LIAYgBWoiBSAGSQ0OIAUhBiAFQd0ASQ0ACwJA
IAMoAoQLIgYgAygCgAsiBUsNACADQgA3A4ALIAMoAvwKIQcgA0Gq1arVejYC/AogAyAFNgLQCiADIAY2AswKIAMgBzYCyAogA0GgCmogA0HICmogA0HwCmoQ
uICAgAAgAyADKALQCiIGNgKACyADIAMoAsgKIgU2AvwKIAMgAygCzAo2AoQLAkAgBkUNACADKALwCiEHIAMoAvQKIQkCQCAGRQ0AIAVBqgEgBvwLAAsgByAF
IAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAy8BqAoNDCADKAKgCiIFIAMoAqQKIgYQgICAgAAgBkUNDAJAIAZFDQAgBUGqASAG/AsACyADIAUgBkEAIAMQuYCA
gAAMDAsgBiAFEIiAgIAAAAsgBiAFaiIFIAZJDQwgBSEGIAVB3QBJDQALAkAgAygC/AoiBiADKAL4CiIFSw0AIAMoAvQKIAYQgICAgAAMCgsgBiAFEIiAgIAA
AAsgBSANEIiAgIAAAAsgASAFEIqAgIAAAAsCQAJAAkAgAkUNAEEAIQYCQANAIAggBmotAABBPUYNASACIAZBAWoiBkcNAAsgAiEQIAIhBgwDCyAGQQFqIhBF
DQsgBiACSw0BIAYNAgsCQEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G43sGAADYC8AogAyADQegLajYC9ApBACEGA0AgAygC/AoiCUHBACAG
ayIFaiIHIAlJDQsgBkH66ciAAGohCAJAAkAgByADKAL4CiICSw0AAkAgByACSw0AAkAgAygC9AogCWoiB0G76siAAE8NACAIIAcgBWpJDQ8LAkAgBUUNACAH
IAggBfwKAAALIAMoAvwKIgcgBWoiCSAHSQ0OIAMgCTYC/AoMAgsgByACEIiAgIAAAAsgAygC8AooAgAhByADIAU2AqQKIAMgCDYCoAogA0HICmogA0HwCmog
A0GgCmpBAUEBIAcRgYCAgACAgICAACADKQPICiIYpyEFAkAgGEIgiKdB//8DcQ4DAQ0ADQtBAA0EIBJBwQBBAEEAIBEoAgARgICAgACAgICAACIGRQ0EAkBB
wQBFDQAgBkGqAUHBAPwLAAsgA0LBADcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIBc3A/AKQQAhBgNAIAMoAoQLIglBwQAgBmsiBWoiByAJSQ0NIAZB+unI
gABqIQgCQAJAIAcgAygCgAsiAksNAAJAIAcgAksNAAJAIAMoAvwKIAlqIgdBu+rIgABPDQAgCCAHIAVqSQ0RCwJAIAVFDQAgByAIIAX8CgAACyADKAKECyIH
IAVqIgkgB0kNECADIAk2AoQLDAILIAcgAhCIgICAAAALIAMoAvgKKAIAIQcgAyAFNgKECSADIAg2AoAJIANByApqIBMgA0GACWpBAUEBIAcRgYCAgACAgICA
ACADKQPICiIYpyEFAkAgGEIgiKdB//8DcQ4DAQ8ADwsgAygCgAsiBkUNBiADKALwCiEHIAMoAvQKIQkgAygC/AohBQJAIAZFDQAgBUGqASAG/AsACyAHIAUg
BkEAQQAgCSgCDBGBgICAAICAgIAADAYLIAYgBWoiBSAGSQ0NIAUhBiAFQcEASQ0ACwJAIAMoAoQLIgYgAygCgAsiBUsNACADQgA3A4ALIAMoAvwKIQcgA0Gq
1arVejYC/AogAyAFNgLQCiADIAY2AswKIAMgBzYCyAogA0GgCmogA0HICmogA0HwCmoQuICAgAAgAyADKALQCiIGNgKACyADIAMoAsgKIgU2AvwKIAMgAygC
zAo2AoQLAkAgBkUNACADKALwCiEHIAMoAvQKIQkCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAsgAy8BqAoNBSADKAKgCiIF
IAMoAqQKIgYQgICAgAAgBkUNBQJAIAZFDQAgBUGqASAG/AsACyADIAUgBkEAIAMQuYCAgAAMBQsgBiAFEIiAgIAAAAsgBiAFaiIFIAZJDQsgBSEGIAVBwQBJ
DQALAkAgAygC/AoiBiADKAL4CiIFSw0AIAMoAvQKIAYQgICAgAAMAwsgBiAFEIiAgIAAAAsgBiACEIiAgIAAAAsCQAJAAkAgBkFiakFoSQ0AIAZBAnRB0LfI
gABqKAIAIQsDQCALQQN0IgdBjLfIgABqKAIAIAZHDQEgCCEFIAYhCSAHQYi3yIAAaigCACIHIAhGDQICQANAIActAAAgBS0AAEcNASAHQQFqIQcgBUEBaiEF
IAlBf2oiCUUNBAwACwsgC0EBaiILRQ0MIAtBCEkNAAsLIANBADoA6AYCQAJAAkACQAJAIAgtAABB3wBGDQAgCCAGakF/ai0AAEHfAEYNAEEAIQcgCCEFIAYh
CQNAAkAgBS0AACILQd8ARg0AIAtBUGpB/wFxIgtBCUsNAgJAAkAgB0H/AXEiBw0AQQAhBwwBCyADIAdBCmwiB0EIdiIOQQBHOgDoCyAODQMLIAMgB0H/AXEg
C2oiB0H/AXEiCyAHRzoA6AsgCyAHRw0CCyAFQQFqIQUgCUF/aiIJRQ0CDAALCyADQQA6AMAGIANBADoA6AYgAyAGNgKMCCADIAg2AogIAkBBgBBFDQAgA0Ho
C2pBqgFBgBD8CwALIANCgBA3AvgKIANBuN7BgAA2AvAKIAMgA0HoC2o2AvQKIANB8ApqIANBiAhqEJOCgIAAQf//A3EOAwEOAg4LIAMgBzoAwAYMBAsgAygC
/AoiBiADKAL4CiIFSw0BIAMoAvQKIAYQgICAgAAMBAtBAA0DIBJBwgBBAEEAIBEoAgARgICAgACAgICAACIGRQ0DAkBBwgBFDQAgBkGqAUHCAPwLAAsgA0LC
ADcDgAsgAyAGNgL8CiADQdyVx4AANgL4CiADIBc3A/AKAkACQAJAIBMgA0GICGoQk4KAgABB//8DcQ4DAA4BDgsgAygChAsiBiADKAKACyIFSw0BIANCADcD
gAsgAygC/AohByADQarVqtV6NgL8CiADIAU2AtAKIAMgBjYCzAogAyAHNgLICiADQaAKaiADQcgKaiADQfAKahC4gICAACADIAMoAtAKIgY2AoALIAMgAygC
yAoiBTYC/AogAyADKALMCjYChAsCQCAGRQ0AIAMoAvAKIQcgAygC9AohCQJAIAZFDQAgBUGqASAG/AsACyAHIAUgBkEAQQAgCSgCDBGBgICAAICAgIAACyAD
LwGoCg0FIAMoAqAKIgUgAygCpAoiBhCAgICAACAGRQ0FAkAgBkUNACAFQaoBIAb8CwALIAMgBSAGQQAgAxC5gICAAAwFCyADKAKACyIGRQ0EIAMoAvAKIQcg
AygC9AohCSADKAL8CiEFAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAAMBAsgBiAFEIiAgIAAAAsgBiAFEIiAgIAAAAsgA0EB
OgDoBiADIAtByLfIgABqLQAAQQdxOgDABgsCQCACIBBJDQACQAJAIAIgEGsiBUUNACAIIBBqIQdBACEGA0ACQCAHIAZqIgstAAAiCUEgRg0AIAUhEAJAAkAg
BSAGTQ0AIAggAmpBf2ohByAFIRACQANAIBBBf2oiCCAFTw0BIActAABBIEcNAiAHQX9qIQcgCCEQIAggBksNAAsgBiEQDAILIBBBf2ogBRCIgICAAAALIAYg
EEsNBwsgECAFSw0HIBAgBkYNAiAJQT9HDQMgEEF/aiAGRw0DIANBADoA2AkgAyADLQDABjoA0QkgAyADLQDoBjoA0AkgBCAMIANB0AlqEJSCgIAAIgZB//8D
cUUNBSADIAY7AbgHIANBuAdqEJWCgIAADAULIAUgBkEBaiIGRw0ACwsgA0ECOgCwCSADIAMtAMAGOgCpCSADIAMtAOgGOgCoCSAEIAwgA0GoCWoQlIKAgAAi
BkH//wNxRQ0CIAMgBjsBkAcgA0GQB2oQlYKAgAAMAgsgA0GwCGogCyAQIAZrIgUQi4KAgAACQCADLwG0CCIGRQ0AIAZBK0cNCiADIAU2AtwIIAMgCzYC2AgC
QEGAEEUNACADQegLakGqAUGAEPwLAAsgA0KAEDcC+AogA0G43sGAADYC8AogAyADQegLajYC9AoCQAJAIANB8ApqIANB2AhqEJaCgIAAQf//A3EOAwAMAQwL
IAMoAvwKIgYgAygC+AoiBUsNByADKAL0CiAGEICAgIAADAMLQQANAiASQcsAQQBBACARKAIAEYCAgIAAgICAgAAiBkUNAgJAQcsARQ0AIAZBqgFBywD8CwAL
IANCywA3A4ALIAMgBjYC/AogA0HclceAADYC+AogAyAXNwPwCgJAAkAgEyADQdgIahCWgoCAAEH//wNxDgMADAEMCyADKAKECyIGIAMoAoALIgVLDQYgA0IA
NwOACyADKAL8CiEHIANBqtWq1Xo2AvwKIAMgBTYC0AogAyAGNgLMCiADIAc2AsgKIANBoApqIANByApqIANB8ApqELiAgIAAIAMgAygC0AoiBjYCgAsgAyAD
KALICiIFNgL8CiADIAMoAswKNgKECwJAIAZFDQAgAygC8AohByADKAL0CiEJAkAgBkUNACAFQaoBIAb8CwALIAcgBSAGQQBBACAJKAIMEYGAgIAAgICAgAAL
IAMvAagKDQMgAygCoAoiBSADKAKkCiIGEICAgIAAIAZFDQMCQCAGRQ0AIAVBqgEgBvwLAAsgAyAFIAZBACADELmAgIAADAMLIAMoAoALIgZFDQIgAygC8Aoh
ByADKAL0CiEJIAMoAvwKIQUCQCAGRQ0AIAVBqgEgBvwLAAsgByAFIAZBAEEAIAkoAgwRgYCAgACAgICAAAwCCyADIAMtALIIOgD6CSADIAMvAbAIOwH4CSAD
QQE6AIAKIAMgAy0AwAY6AP0JIAMgAy0A6AY6APwJIAQgDCADQfgJahCUgoCAACIGQf//A3FFDQEgAyAGOwHgByADQeAHahCVgoCAAAwBCwsgECACEIqAgIAA
AAsgBiAQEIqAgIAAAAsgECAFEIiAgIAAAAsgBiAFEIiAgIAAAAsgBiAFEIiAgIAAAAsgDSAFEIiAgIAAAAsgAEEBOgBcDAYLAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAAtACxFDQAgACgCDCIGKAIMIgUgBigCCCIHSw0BIAVFDSACQAJAAkAC
QAJAAkACQAJAAkAgBigCBCIHLQAAQU9qDgkBAgMEAAUGBwgpCyAAQdgAakEAKAK44MeAADYCACAAQdAAakEAKQKw4MeAADcCACAAQcgAakEAKQKo4MeAADcC
ACAAQcAAakEAKQKg4MeAADcCACAAQThqQQApApjgx4AANwIAIABBACkCkODHgAA3AjAgAEEwaiEEDC8LIAVBAUYNJwJAAkACQAJAIActAAFBUGoODAIDASsr
KysrKysrACsLIABBDDoAWCADQegLaiAHQQJqIAVBfmpBChCXgoCAACAAQeQAIAMvAegLIgZBkM4AIAZBkM4ASRsgAy8B6gsbOwEwIABBMGohBAwxCyAAQQM6
AFggAEEANgI0IABBot7BgAA2AjAgAEEBOgA4IANBAToA6AsgAEEwaiEEDDALIAVBAkYNJyAFQQRJDSggBy0AAkE7Rw0oAkACQAJAAkAgBy0AA0FQag4EAAEC
AywLIABBFDoAWCAAQQAvAZa5yIAAIgY7ATIgACAGOwEwIABBMGohBAwyCyAAQRQ6AFggAEEALwGYuciAACIGOwEyIAAgBjsBMCAAQTBqIQQMMQsgAEEAOwEw
IABBFDoAWCAAQQAvAZa5yIAAOwEyIABBMGohBAwwCyAAQQA7ATAgAEEUOgBYIABBAC8BmLnIgAA7ATIgAEEwaiEEDC8LIAVBA0kNJyAHLQACQTtHDSdBACEE
IAZBABD7gICAAEH//wNxDQogACgCDCIGKAIMIgUgBigCCCIHSw0JIAYoAgQhByAAQRU6AFggBUF/aiIGIAVLDSggBkEDSQ0LIAYgBU8NDAJAIAcgBmotAAAi
BQ0AIABBMGohBCAAIAZBfWo2AjQgACAHQQNqNgIwDC8LIAUQg4KAgAAACyAFQQFGDSYgBy0AAUE7Rw0mQQAhBCAGQQAQ+4CAgABB//8DcQ0NIAAoAgwiBigC
DCIFIAYoAggiB0sNDCAGKAIEIQcgAEENOgBYIAVBf2oiBiAFSw0nIAZBAkkNDiAGIAVPDQ8CQCAHIAZqLQAAIgUNACAAQTBqIQQgACAGQX5qNgI0IAAgB0EC
ajYCMAwuCyAFEIOCgIAAAAsgBUEBRg0lIActAAFBO0cNJQJAIAVBAkcNACAAQgA3AjAgAEEOOgBYIABBOGpBADYCACAAQTBqIQQMLQtBACEEIAZBABD7gICA
AEH//wNxDRAgACgCDCIGKAIMIgUgBigCCCIHSw0PIAYoAgQhByAAQQE6ADggAEEOOgBYIAVBf2oiBiAFSw0mIAZBAkkNESAGIAVPDRICQCAHIAZqLQAAIgUN
ACAAQTBqIQQgACAGQX5qNgI0IAAgB0ECajYCMAwtCyAFEIOCgIAAAAsgBUEBRg0kIAVBA0kNJCAHLQABQf8BcUE7Rw0kQYACIQlBASEEQQAhCAJAAkACQAJA
AkAgBy0AAkFQag4FAgQDAQApC0EAIQlBBCEEDAMLQQMhCAsgAEEAOwE0IAAgCDYCMCAAQQ86AFggAEEwaiEEDC0LQQAhCUECIQQLIAAgCTsBNCAAIAQ2AjAg
AEEPOgBYIABBMGohBCAFQQRJDSsgBy0AA0E7Rw0rIANB6AtqIAdBBGogBUF8ahCYgoCAAEGM4MeAACEGIAMvAewLDSIgA0EBOgDxCiADIAMoAugLIgZB5AAg
BkHkAEkbOgDwCiADQfAKaiEGDCILIAVBAUYNIyAHLQABQTtHDSNBACEEIAZBABD7gICAAEH//wNxDRIgACgCDCIGKAIMIgUgBigCCCIHSw0RIAYoAgQhByAA
QRE6AFggBUF/aiIGIAVLDSQgBkECSQ0TIAYgBU8NFAJAIAcgBmotAAAiBQ0AIABBMGohBCAAIAZBfmo2AjQgACAHQQJqNgIwDCsLIAUQg4KAgAAACyAFQQFG
DSIgBy0AAUE7Rw0iQQAhBCAGQQAQ+4CAgABB//8DcQ0VIAAoAgwiBigCDCIFIAYoAggiB0sNFCAGKAIEIQcgAEESOgBYIAVBf2oiBiAFSw0jIAZBAkkNFiAG
IAVPDRcCQCAHIAZqLQAAIgUNACAAQTBqIQQgACAGQX5qNgI0IAAgB0ECajYCMAwqCyAFEIOCgIAAAAsgBUEBRg0hIActAAFBO0cNIUEAIQQgBkEAEPuAgIAA
Qf//A3ENGCAAKAIMIgYoAgwiBSAGKAIIIgdLDRcgBigCBCEHIABBEzoAWCAFQX9qIgYgBUsNIiAGQQJJDRkgBiAFTw0aAkAgByAGai0AACIFDQAgAEEwaiEE
IAAgBkF+ajYCNCAAIAdBAmo2AjAMKQsgBRCDgoCAAAALIAVBAUYNICAHLQABQTtHDSBBACEEIAZBABD7gICAAEH//wNxDRsgACgCDCIGKAIMIgUgBigCCCIH
Sw0aIAYoAgQhByAAQQU6AFggBUF/aiIGIAVLDSEgBkECSQ0cIAYgBU8NHQJAIAcgBmotAAAiBQ0AIABBMGohBCAAIAZBfmo2AjQgACAHQQJqNgIwDCgLIAUQ
g4KAgAAACyAAQQE6AFwMJQsgBSAHEIiAgIAAAAsgBSAHEIiAgIAAAAsgAEEBOgBcDCMLQQMgBhCKgICAAAALIAZBAWogBRCIgICAAAALIAUgBxCIgICAAAAL
IABBAToAXAwfC0ECIAYQioCAgAAACyAGQQFqIAUQiICAgAAACyAFIAcQiICAgAAACyAAQQE6AFwMGwtBAiAGEIqAgIAAAAsgBkEBaiAFEIiAgIAAAAsgBSAH
EIiAgIAAAAsgAEEBOgBcDBcLQQIgBhCKgICAAAALIAZBAWogBRCIgICAAAALIAUgBxCIgICAAAALIABBAToAXAwTC0ECIAYQioCAgAAACyAGQQFqIAUQiICA
gAAACyAFIAcQiICAgAAACyAAQQE6AFwMDwtBAiAGEIqAgIAAAAsgBkEBaiAFEIiAgIAAAAsgBSAHEIiAgIAAAAsgAEEBOgBcDAsLQQIgBhCKgICAAAALIAZB
AWogBRCIgICAAAALIAAgBi8AADsBNAwICyAAQRQ6AFggAEEALwGYuciAACIGOwEyIAAgBjsBMCAAQTBqIQQMBwsCQANAIAYoAggiByAGKAIMIgVJDQICQCAH
IAVHDQAgA0EAOgDICiAGKAIAKAIAIQUgA0EBNgL0CiADIANByApqNgLwCiADQegLaiAGIANB8ApqQQFBASAFEYGAgIAAgICAgAAgAy8B7AsNBSADKALoC0UN
AQwCCwsgBSAHTw0FIAYoAgQgBWpBADoAACAGKAIMQQFqIgVFDQEgBiAFNgIMCyAAKAIMIgUoAgwiBiAFKAIIIgdLDQEgBSgCBCEHIABBADYCNCAAQaLewYAA
NgIwIABBCToAWCAGQX9qIgUgBksNACAFIAZPDQMCQCAHIAVqLQAAIgYNACAAQTBqIQQgACAFNgI8IAAgBzYCOAwHCyAGEIOCgIAAAAsACyAGIAcQiICAgAAA
CyAAQQE6AFwMAgsgBUEBaiAGEIiAgIAAAAsgBSAHEIiAgIAAAAtBACEECyADQfAbaiSAgICAACAEC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQ
RQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQm4KAgABB//8DcQ4DAAQBBAsCQCAB
KAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQdUAQQBBACADQiCIpygCABGAgICAAICAgIAA
IgJFDQACQEHVAEUNACACQaoBQdUA/AsACyABQtUANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQm4KAgABB//8DcQ4DAAQB
BAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQ
ahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAE
IAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5
gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFB
wBBqJICAgIAADwsgACACEIiAgIAAAAsAC9gBAQN/I4CAgIAAQSBrIgIkgICAgAACQAJAAkADQCAAKAIIIgMgACgCDCIESQ0DAkAgAyAERw0AIAIgAToADyAA
KAIAKAIAIQQgAkEBNgIUIAIgAkEPajYCECACQRhqIAAgAkEQakEBQQEgBBGBgICAAICAgIAAIAIvARwiBA0DIAIoAhhFDQEMAgsLAkAgBCADSQ0AIAQgAxCI
gICAAAwDCyAAKAIEIARqIAE6AAAgACgCDEEBaiIERQ0CIAAgBDYCDAtBACEECyACQSBqJICAgIAAIAQPCwALaAEBfyOAgICAAEEQayICJICAgIAAIAJBCkEF
IABBAXEiABs2AgwgAkHmlsCAAEHon8CAACAAGzYCCCACQQpBBSABQQFxIgAbNgIEIAJB5pbAgABB6J/AgAAgABs2AgAgAhCZgoCAAAALrQUDAn8BfgJ/I4CA
gIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCa
goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dB0ABBAEEA
IANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQdAARQ0AIAJBqgFB0AD8CwALIAFC0AA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQ
EGogABCagoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2
ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEF
AkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJB
qgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEA
IAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwAL6wkDB38BfgF/I4CAgIAAQcAQayIAJICAgIAAAkBBgBBFDQAgAEGqAUGAEPwL
AAsgAEKAEDcCiBAgAEG43sGAADYCgBAgACAANgKEEEEAIQECQAJAAkADQCAAKAKMECICQSwgAWsiA2oiBCACSQ0CIAFByLrIgABqIQUCQAJAIAQgACgCiBAi
BksNAAJAIAQgBksNAAJAIAAoAoQQIAJqIgRB9LrIgABPDQAgBSAEIANqSQ0GCwJAIANFDQAgBCAFIAP8CgAACyAAKAKMECIEIANqIgIgBEkNBSAAIAI2AowQ
DAILIAQgBhCIgICAAAALIAAoAoAQKAIAIQQgACADNgKcECAAIAU2ApgQIABBsBBqIABBgBBqIABBmBBqQQFBASAEEYGAgIAAgICAgAAgACkDsBAiB6chAwJA
IAdCIIinQf//A3EOAwEEAAQLQQANAkEAKQPg+smAACIHp0EsQQBBACAHQiCIpygCABGAgICAAICAgIAAIgFFDQIgAUKq1arVqtWq1ap/NwAAIAFBEGpCqtWq
1arVqtWqfzcAACABQQhqQqrVqtWq1arVqn83AAAgAUEoakGq1arVejYAACABQSBqQqrVqtWq1arVqn83AAAgAUEYakKq1arVqtWq1ap/NwAAIABCLDcDkBAg
ACABNgKMECAAQdyVx4AANgKIECAAIAc3A4AQIABBgBBqQQhqIQhBACEBA0AgACgClBAiAkEsIAFrIgNqIgQgAkkNBCABQci6yIAAaiEFAkACQCAEIAAoApAQ
IgZLDQACQCAEIAZLDQACQCAAKAKMECACaiIEQfS6yIAATw0AIAUgBCADakkNCAsCQCADRQ0AIAQgBSAD/AoAAAsgACgClBAiBCADaiICIARJDQcgACACNgKU
EAwCCyAEIAYQiICAgAAACyAAKAKIECgCACEEIAAgAzYCrBAgACAFNgKoECAAQbAQaiAIIABBqBBqQQFBASAEEYGAgIAAgICAgAAgACkDsBAiB6chAwJAIAdC
IIinQf//A3EOAwEGAAYLIAAoApAQIgFFDQQgACgCgBAhBCAAKAKEECECIAAoAowQIQMCQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACA
gICAAAwECyABIANqIgMgAUkNBCADIQEgA0EsSQ0ACwJAIAAoApQQIgEgACgCkBAiA0sNACAAQgA3A5AQIAAoAowQIQQgAEGq1arVejYCjBAgACADNgK4ECAA
IAE2ArQQIAAgBDYCsBAgAEGYEGogAEGwEGogAEGAEGoQuICAgAAgACAAKAK4ECIBNgKQECAAIAAoArAQIgM2AowQIAAgACgCtBA2ApQQAkAgAUUNACAAKAKA
ECEEIAAoAoQQIQICQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAsgAC8BoBANAyAAKAKYECIDIAAoApwQIgEQgICAgAAgAUUN
AwJAIAFFDQAgA0GqASAB/AsACyAAIAMgAUEAIAAQuYCAgAAMAwsgASADEIiAgIAAAAsgASADaiIDIAFJDQIgAyEBIANBLEkNAAsgACgCjBAiASAAKAKIECID
Sw0CIAAoAoQQIAEQgICAgAALIABBwBBqJICAgIAADwsACyABIAMQiICAgAAAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFB
gBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQh4OAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQ
IgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQS5BAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq
1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSZqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpC
qtWq1arVqtWqfzcAACABQi43A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCHg4CAAEH//wNxDgMABAEECyABKAKcECIA
IAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEg
ASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEo
ApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAP
CyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASAB
QQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCdgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICA
AAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAg
AUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCdgoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq
1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQ
NgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiAB
KAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQIC
QCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQ
ayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCfgoCAAEH/
/wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIin
KAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCf
goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFB
pBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUN
ACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwL
AAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwR
gYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsg
AUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABChgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAA
IAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFB
xQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABChgoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJL
DQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYC
mBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAA
gICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAg
ASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICA
gAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJA
AkACQAJAAkAgAUGIEGogABCigoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBB
ACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYC
kBAgASADNwOIEAJAAkAgAUGQEGogABCigoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEg
AjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABF
DQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICA
gIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJB
qgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAA
AkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCkgoCAAEH//wNxDgMABAEE
CwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAA
gICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCkgoCAAEH//wNx
DgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBq
IAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8
CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABB
ACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALlwYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAg
AUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABClgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAM
BAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBM0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrV
qtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBL2pBqtWq1Xo2AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrV
qtWq1arVqn83AAAgAUIzNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQpYKAgABB//8DcQ4DAAQBBAsgASgCnBAiACAB
KAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEo
ArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKY
ECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsg
ACACEIiAgIAAAAsAC/UKAgd/AX4jgICAgABBoAJrIgMkgICAgAACQAJAAkACQAJAAkACQAJAA0AgACgCGCEEAkAgAUH/AXEiAUEWRg0AIAQtAL0CIQUCQAJA
IAEOBAUABAEICyAAQQEgAhCIgYCAAAJAIAAoAhgiBC8B0gEiAUUNACADQQA7AZACIANBADoAjAIgA0EAOgCYAiADIAFBf2pB//8DcTYClAIgA0EBOgCcAiAE
Qcifx4AAIANBkAJqIAVBA3FBAUYgAnIQwoCAgAAgACgCGCEECyAELQDZAUUNCgwHCyAEQdSfx4AAQbT6yYAAEMCDgIAAIAQQwYOAgAAMCQsgBBDCg4CAACIB
Qf//A3FFDQcgAyABOwECIANBAmoQw4OAgABBAiEBDAALCyAALQAoQQFxDQQgBCgCWCIGRQ0DIAYvAaoCQX9qIgdB//8DcSAHRw0DIAYvAagCQX9qIghB//8D
cSAIRw0DIANBADoABCAELwGOASEEIAYhAQJAA0AgBEH//wNxIAEvAaoCIglNDQEgBCAJayEEIAEoAggiAQ0ADAULCyADQQA6AEAgA0ECOgBsIANBADoAaCAD
QQA6AFwgA0EAOwFaIAMgATYCVCADQQE6AFAgA0EAOgBMIAMgCDsBSiADIAc7AUggAyAGNgJEIANBAjoAYCADQQA6AGQgAyAJIARrOwFYIANB8ABqIANBxABq
EKCDgIAAIAMtAHhFDQEgA0G4AWpBIGogA0HEAGpBIGooAgA2AgAgA0G4AWpBGGogA0HEAGpBGGopAgA3AwAgA0G4AWpBEGogA0HEAGpBEGopAgA3AwAgA0G4
AWpBCGogA0HEAGpBCGopAgA3AwAgAyADKQJENwO4ASADQQE6AOQBIAMgAykDcCIKNwLcASAKQjCIp0F/aiIEQf//A3EgBEcNAyADIAQ7AegBIANBuAFqIQQM
AgsgAEEAIAIQiIGAgAAgACgCGCIELwHSAUEBaiIBQf//A3EiCSABRw0CAkAgCSAALwH4EU8NACADQQA6AIgCIANBADsBgAIgAyABQf//A3E2AoQCIAQgA0GA
AmpBtPrJgAAgBUEDcUEBRiACchDCgICAACAAKAIYIQQLIAQtANkBRQ0FDAILIANBqgFqQgA3AQAgA0GAAWpBIGogA0HEAGpBIGooAgA2AgAgA0GAAWpBGGog
A0HEAGpBGGopAgA3AwAgA0GAAWpBEGogA0HEAGpBEGopAgA3AwAgA0GAAWpBCGogA0HEAGpBCGopAgA3AwAgAyADKQJENwOAASADQgA3AqQBIANBgAFqIQQL
IANBCGpBMGogBEEwaigCADYCACADQQhqQShqIARBKGopAgA3AwAgA0EIakEgaiAEQSBqKQIANwMAIANBCGpBGGogBEEYaikCADcDACADQQhqQRBqIARBEGop
AgA3AwAgA0EIakEIaiAEQQhqKQIANwMAIAMgBCkCADcDCCADQfABaiADQQhqEMSDgIAAIAMtAPwBRQ0BIAMvAfQBIgkgAygC8AEiBC8BqgJPDQAgAy8B9gEg
BC8BqAJPDQAgBCgCECIBIAQoAhhqIgQgAUkNACAEQQdxDQAgBEUNACABIAQgCUEDdGoiCSgCAGoiBCABSQ0AIARBB3ENACAERQ0AAkAgCSkDAEIliKdBA3Ei
BEF/akECSQ0AIAQOBAIBAQECCyAAKAIYIgQQwoOAgABB//8DcQ0BIAQQwYOAgAAMAQsACyAAKAIYQcifx4AAQbT6yYAAIAVBA3FBAUYgAnIQwoCAgAAgACgC
GEEAOgDZASAAQfYRaiIAIAAtAABBAnI6AAAMAQsgBBDBg4CAACAAKAIYQQA6ANkBCyADQaACaiSAgICAAAuJBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAAC
QEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEKaCgIAAQf//A3EOAwAEAQQL
AkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EwQQBBACADQiCIpygCABGAgICAAICA
gIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq
1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIwNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQpoKAgABB//8D
cQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQ
aiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA
/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAA
QQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICA
gAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC6kFAQl/I4CAgIAAQRBrIgMkgICAgAACQAJAAkACQAJAAkACQAJAAkACQAJAIAFB/wFxDgMBAgADCyAA
LwH6ESEEDAcLIAAoAhgiBC8B0AEiAQ0CQQAhAQwDCyAAKAIYIgQvAdABIQEgBCgCzAEpAwBCgICAgICAA4NCgICAgICAAVINAyABQf//A3FBAWoiAUH//wNx
IAFGDQMMBAsgAyABOgAPIANBD2oQ+YOAgAAMBgsgASAEKALMASkDAEKAgICAgIADg0KAgICAgIACUWtB//8DcSEBCyAEELSCgIAAIAAvAfoRIQQMAwsgAUH/
/wNxQQFqIgRB//8DcSAERg0BCwALQQAhAQsgACgCGEEAOgDZASAAKAIYKALIASIFIAUpAwBCgICAgIAghDcDACAEQf//A3EhBCAAKAIYIgYoAswBIgcgBi8B
0AEiCEEDdGshAAJAAkACQCAGLQC9AkEDcUEBRg0AIAJBAXENACAEIAFJDQEgBiAGKALEASgCAEEQaiAGKALIASAAIAFBA3RqIAQgAWsQs4KAgAAMAwsgBCAB
SQ0AIAQgAWsiBUUNAiAAIAFBA3QiAWohCSAGKALEASgCAEEQaiEKIAYoAsgBIQsgASAIQQN0ayAHakEIaiEIQQAhAgNAIAIgBUYNAgJAIAkgAkEDdCIAaiIH
LQAFQRBxDQAgCCAAaiEAIAIhAQJAA0ACQCABQQFqIgEgBUkNACAFIQEMAgsgAC0ABSEEIABBCGohACAEQRBxRQ0ACwsCQCABIAJJDQAgBiAKIAsgByABIAJr
ELOCgIAAIAEhAiABIAVPDQUMAgsgAiABEIqAgIAAAAsgAkEBaiICIAVPDQMMAAsLIAEgBBCKgICAAAALIAUgBRCIgICAAAALIANBEGokgICAgAALrQUDAn8B
fgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGI
EGogABCogoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dB
xQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJA
AkAgAUGQEGogABCogoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4
ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCAB
KAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAA
RQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCAC
IABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEI
akGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCqgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAg
ASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJA
QcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCqgoCAAEH//wNxDgMABAEECyABKAKc
ECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAA
IAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBB
ACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAEL
IAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICA
gAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAg
ASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABC1goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCA
gICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYC
lBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABC1goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFB
qtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEo
ArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAi
AiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQ
IQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAA
QcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABC5goCA
AEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANC
IIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGog
ABC5goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQ
IAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAg
AEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEg
APwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUo
AgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwL
AAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABC7goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0N
ACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJB
qgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABC7goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQ
IgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAi
ADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGA
gIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBF
DQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQ
iICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKM
EAJAAkACQAJAAkAgAUGIEGogABC9goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EA
DQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceA
ADYCkBAgASADNwOIEAJAAkAgAUGQEGogABC9goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQ
IAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJA
IABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIA
EICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALdgEDfyAAKAIYLwHQASIBQQdxIQIgAUED
diEDAkAgAUH/A0sNACAAIANqIgAgAkH4uMiAAGotAAAgAC0AOHM6ADgPCwJAIANBQGoiASAAKAI0Tw0AIAAoAjAgAWoiACACQfi4yIAAai0AACAALQAAczoA
AA8LAAuMAgEEfyAAQgA3AzggAEHwAGpCADcDACAAQegAakIANwMAIABB4ABqQgA3AwAgAEHYAGpCADcDACAAQdAAakIANwMAIABByABqQgA3AwAgAEHAAGpC
ADcDAAJAIAAoAjQiAUUNACAAKAIwQQAgAfwLAAsCQAJAIAAoAiwiAUF/aiICIAFLDQBBOSEDQQghAQNAIAEgAk8NAgJAAkAgAUH/A0sNACAAIANqIgIgAi0A
AEEBcjoAAAwBCyADQYh/aiAAKAI0Tw0CIAAoAjAgA2pBiH9qIgIgAi0AAEEBcjoAAAsgAUEIaiICIAFJDQEgA0EBaiEDIAIhASAAKAIsIgRBf2oiAiAETQ0A
CwsACwucBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkAC
QAJAAkACQCABQYgQaiAAELyCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEA
KQPg+smAACIDp0E3QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arV
qn83AAAgAkEvakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNzcDmBAg
ASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAELyCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEo
ApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2
ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBAN
ASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQ
IQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+
An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQ
aiAAEL+CgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HF
AEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkAC
QCABQZAQaiAAEL+CgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQ
IAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEo
AowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABF
DQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIg
AEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhq
QaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMGCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACAB
KAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBB
xQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEMGCgIAAQf//A3EOAwAEAQQLIAEoApwQ
IgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAg
ASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEA
IAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsg
ASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICA
AA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECAB
IAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMOCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICA
gIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKU
ECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEMOCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq
1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgC
uBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECIC
IAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAh
AgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABB
wBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMaCgIAA
Qf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0Ig
iKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAA
EMaCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAucBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsA
CyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMeCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0A
IAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E3QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/
NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEvakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq
1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEMeCgIAAQf//A3EO
AwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGog
AUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwL
AAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEA
IAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAA
CyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECAB
QbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEMmCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwE
CyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULF
ADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEMmCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQ
IAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQ
IgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8B
rBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEo
AowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMC
fwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCAB
QYgQaiAAEMuCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACID
p0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQ
AkACQCABQZAQaiAAEMuCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2
ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEE
IAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJA
IABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAE
IAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACAB
QQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEM+CgIAAQf//A3EOAwAEAQQLAkAgASgClBAi
ACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0A
AkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEM+CgIAAQf//A3EOAwAEAQQLIAEo
ApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICA
gAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABB
AEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAM
AQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSA
gICAAA8LIAAgAhCIgICAAAALAAuJBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKI
ECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEM2CgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAA
EICAgIAADAELQQANAEEAKQPg+smAACIDp0EvQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcA
ACACQQhqQqrVqtWq1arVqn83AAAgAkEnakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIvNwOYECABIAI2
ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQzYKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAh
BCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAg
ASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEo
AqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSAB
KAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC4QGAwJ/AX4CfyOA
gICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ
zIKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQStBAEEA
IANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSdqQarVqtV6
NgAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIrNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhq
IAAQzIKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0
ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJA
IABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoB
IAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC4QGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8
CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQzoKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJN
DQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQStBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arV
qn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSdqQarVqtV6NgAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arV
qn83AAAgAUIrNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQzoKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECIC
Sw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2
ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICA
AICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0A
IAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiA
gIAAAAsAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBAC
QAJAAkACQAJAIAFBiBBqIAAQ0IKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0A
QQApA+D6yYAAIgOnQS5BAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arV
qtWqfzcAACACQSZqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQi43A5gQIAEgAjYClBAgAUHclceAADYC
kBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDQgoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQ
IAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJA
IABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIA
EICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICA
gIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDagoCAAEH//wNxDgMA
BAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCA
gIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFBxQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABDagoCAAEH/
/wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFB
tBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoB
IAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASAC
IABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACA
gICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALjxQBC38jgICAgABB0ABrIgIkgICAgAACQAJAAkAgASgCBCIDIAEoAgwiBEsNACAEQQFqIgVFDQEg
ASAFNgIMIABBhJbHgABB7JXHgAAgBBsiASkCADcCACAAQQhqIAFBCGopAgA3AgAgAEEQaiABQRBqKQIANwIADAILIARBGE8NACADIARrIQYgASgCACEHIAFB
CmotAAAhCCABLwEIIQkgASAEQQFqIgo2AgwgByAEQQF0aiILLwEAIQUCQCAJIAhBEHRyIglBASAEdHEiCEH///8HcUUNAAJAIAVBWmoiDEEUSw0AQQEgDHRB
gYjAAHENAQsgBUEERg0AIARBF0YNASAEQQJqIQQgCUH///8HcSEFAkADQCAFIARBf2p2QQFxRQ0BIARBAWoiBEEZRw0ACyABIARBf2o2AgwMAgsgAEEBOgAU
IAEgBDYCDCAAQQE6ABAgACADNgIEIAAgBzYCACAEIAprIgEgBEsNASABQQFqIgFFDQEgACALNgIIIAAgASAGIAEgBkkbNgIMDAILAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCAFDjwAAQIDGAQFBgcIFxcXFxcXFxcXFxcJCgsMDRcODxAXFxcXFxcX
FxkRFxcXFxcXFxcaEhcXFxMXFBcXFRYXCyAAQRBqQQApAvyVx4AANwIAIABBCGpBACkC9JXHgAA3AgAgAEEAKQLslceAADcCAAwgCyAAQRBqQQApAqyWx4AA
NwIAIABBCGpBACkCpJbHgAA3AgAgAEEAKQKclseAADcCAAwfCyAAQRBqQQApAsSWx4AANwIAIABBCGpBACkCvJbHgAA3AgAgAEEAKQK0lseAADcCAAweCyAA
QRBqQQApAtyWx4AANwIAIABBCGpBACkC1JbHgAA3AgAgAEEAKQLMlseAADcCAAwdCyAAQRBqQQApAvSWx4AANwIAIABBCGpBACkC7JbHgAA3AgAgAEEAKQLk
lseAADcCAAwcCyAAQRBqQQApAvSWx4AANwIAIABBCGpBACkC7JbHgAA3AgAgAEEAKQLklseAADcCAAwbCyAAQRBqQQApAoyXx4AANwIAIABBCGpBACkChJfH
gAA3AgAgAEEAKQL8lseAADcCAAwaCyAAQRBqQQApAqSXx4AANwIAIABBCGpBACkCnJfHgAA3AgAgAEEAKQKUl8eAADcCAAwZCyAAQRBqQQApAryXx4AANwIA
IABBCGpBACkCtJfHgAA3AgAgAEEAKQKsl8eAADcCAAwYCyAAQQc6ABAgAEEBOgAUIABBAjoAAAwXCyAAQRBqQQApAtSXx4AANwIAIABBCGpBACkCzJfHgAA3
AgAgAEEAKQLEl8eAADcCAAwWCyAAQRBqQQApAuyXx4AANwIAIABBCGpBACkC5JfHgAA3AgAgAEEAKQLcl8eAADcCAAwVCyAAQQc6ABAgAEEBOgAUIABBADoA
AAwUCyAAQRBqQQApAoSYx4AANwIAIABBCGpBACkC/JfHgAA3AgAgAEEAKQL0l8eAADcCAAwTCyAAQRBqQQApApyYx4AANwIAIABBCGpBACkClJjHgAA3AgAg
AEEAKQKMmMeAADcCAAwSCyAAQRBqQQApArSYx4AANwIAIABBCGpBACkCrJjHgAA3AgAgAEEAKQKkmMeAADcCAAwRCyAAQRBqQQApAsyYx4AANwIAIABBCGpB
ACkCxJjHgAA3AgAgAEEAKQK8mMeAADcCAAwQCyAAQRBqQQApAuSYx4AANwIAIABBCGpBACkC3JjHgAA3AgAgAEEAKQLUmMeAADcCAAwPCyAAQRBqQQApAvyY
x4AANwIAIABBCGpBACkC9JjHgAA3AgAgAEEAKQLsmMeAADcCAAwOCyAAQRBqQQApApSZx4AANwIAIABBCGpBACkCjJnHgAA3AgAgAEEAKQKEmceAADcCAAwN
CyAAQRBqQQApAqyZx4AANwIAIABBCGpBACkCpJnHgAA3AgAgAEEAKQKcmceAADcCAAwMCyAGQQFLDQUMCQsgAEEQakEAKQLEmceAADcCACAAQQhqQQApAryZ
x4AANwIAIABBACkCtJnHgAA3AgAMCgsgBUFiakH//wNxQQhJDQQgBUH4/wNxQShGDQUgBUGmf2pB//8DcUEISQ0GIAVBnH9qQf//A3FBCE8NByAAQRs6ABAg
AEEBOgAUIAsvAQBBpH9qIgFB//8DcSIEIAFHDQggBEGAAk8NCCAAIAE6AAAMCQsCQCAIQf///wdxDQAgAEEHOgAQIABBAToAFCAAQQE6AAAMCQsgBkECSQ0G
IARBF0YNB0EBIQUCQCAJQf///wdxIAp2QQFxRQ0AIAEQ04KAgAAMBwsgAEEBOgAUIABBBzoAECABIARBAmo2AgwCQCALLwECIgFBBUsNACABIQULIAAgBUEH
cToAAAwICyAGQQFNDQUCQAJAIAsvAQJBfmoOBAAHBwEHCyACQQhqIAEgCyAGIAhB////B3FBAEcQ1IKAgAAgAi0AHEUNBiAAIAIpAgg3AgAgAEEBOgAUIABB
EGogAkEIakEQaigCADYCACAAQQhqIAJBCGpBCGopAgA3AgAMCAsgBkECRg0FIABBAToAFCAAQR46ABAgASAEQQNqNgIMIAAgCy0ABDoAAAwHCyAGQQFNDQQC
QAJAIAsvAQJBfmoOBAAGBgEGCyACQSBqIAEgCyAGIAhB////B3FBAEcQ1YKAgAAgAi0ANEUNBSAAIAIpAiA3AgAgAEEBOgAUIABBEGogAkEgakEQaigCADYC
ACAAQQhqIAJBIGpBCGopAgA3AgAMBwsgBkECRg0EIABBAToAFCAAQR06ABAgASAEQQNqNgIMIAAgCy0ABDoAAAwGCwJAAkAgCy8BAkF+ag4EAAUFAQULIAJB
OGogASALIAYgCEH///8HcUEARxDWgoCAACACLQBMRQ0EIAAgAikCODcCACAAQQE6ABQgAEEQaiACQThqQRBqKAIANgIAIABBCGogAkE4akEIaikCADcCAAwG
CyAGQQJGDQMgAEEBOgAUIABBCToAECABIARBA2o2AgwgACALLQAEOgAADAULIABBGDoAECAAQQE6ABQgCy8BAEFiaiIBQf//A3EgAUcNAyABQf//A3FBgAJP
DQMgACABOgAADAQLIABBFzoAECAAQQE6ABQgCy8BAEFYaiIBQf//A3EiBCABRw0CIARBgAJPDQIgACABOgAADAMLIABBHDoAECAAQQE6ABQgCy8BAEGuf2oi
AUH//wNxIAFHDQEgAUH//wNxQYACTw0BIAAgAToAAAwCCyAAQQE6ABAgAEEBOgAUIAAgBjYCDCAAIAs2AgggACABKQIANwIADAELAAsgAkHQAGokgICAgAAL
yQgBBX8jgICAgABBIGsiAiSAgICAAAJAIAEtABAiA0EBRg0AAkAgA0EfRg0AIAJBGGogACgCDEEYaigCACIAQbgBaigCADYCACACQRBqIABBsAFqKQIANwMA
IAJBCGogAEGoAWopAgA3AwAgAiAAKQKgATcDACAAQaABaiEEAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkAgAw4fAB8BAgMEBQYHCAkKCwwNDg8QERITFBUXFhgZGxodHAALIARCADcDACAEQRhqQQA2AgAgBEEQakIANwMAIARBCGpCADcDAAwdCyAAIAAv
AbgBQQFyOwG4AQwcCyAAIAAvAbgBQfr/A3E7AbgBDBsLIAAgAC8BuAFBAnI7AbgBDBoLIAAgAC8BuAFB/f8DcTsBuAEMGQsgACAALwG4AUEEcjsBuAEMGAsg
ACABLQAAQQdxQQh0IAAvAbgBQf/xA3FyOwG4AQwXCyAAIAEvAQAiAzoAsAEgACABQQJqLQAAOgCyASAAIANBCHY6ALEBIABBAjoAtAEMFgsgACABLQAAOgCw
ASAAQQE6ALQBDBULIABCADcDsAEMFAsgACAALwG4AUGAAXI7AbgBDBMLIAAgAC8BuAFB//4DcTsBuAEMEgsgACAALwG4AUEIcjsBuAEMEQsgACAALwG4AUH3
/wNxOwG4AQwQCyAAIAAvAbgBQRByOwG4AQwPCyAAIAAvAbgBQe//A3E7AbgBDA4LIAAgAC8BuAFBIHI7AbgBDA0LIAAgAC8BuAFB3/8DcTsBuAEMDAsgACAA
LwG4AUHAAHI7AbgBDAsLIAAgAC8BuAFBv/8DcTsBuAEMCgsgACABLwEAIgM6AKABIAAgAUECai0AADoAogEgACADQQh2OgChASAAQQI6AKQBDAkLIAAgAS8B
ACIDOgCoASAAIAFBAmotAAA6AKoBIAAgA0EIdjoAqQEgAEECOgCsAQwICyAAIAEtAAA6AKABIABBAToApAEMBwsgACABLQAAOgCoASAAQQE6AKwBDAYLIARC
ADcDAAwFCyAAQgA3A6gBDAQLIAAgAS0AADoAoAEgAEEBOgCkAQwDCyAAIAEtAAA6AKgBIABBAToArAEMAgsgACABLQAAOgCgASAAQQE6AKQBDAELIAAgAS0A
ADoAqAEgAEEBOgCsAQsgABDAgICAAEH//wNxRQ0BIAQgAikDADcCACAEQRhqIgEgAkEYaigCADYCACAEQRBqIgMgAkEQaikDADcCACAEQQhqIgUgAkEIaikD
ADcCACAAEMCAgIAAQf//A3EiBkUNASACIAY7AR4gAkEeahDXgoCAACABQQA2AgAgA0IANwMAIAVCADcDACAEQgA3AwAgABDAgICAAEH//wNxIgBFDQEgABCM
gICAAAsACyACQSBqJICAgIAAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQ
IAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ2IKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQ
gICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAA
IAJBCGpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjA3A5gQIAEgAjYC
lBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDYgoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEE
IAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECAB
IAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgC
pBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEo
ApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALlwYDAn8BfgJ/I4CA
gIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDZ
goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBNEEAQQAg
A0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBMGpBqtWq1Xo2
AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI0NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEg
AzcDiBACQAJAIAFBiBBqQQhqIAAQ2YKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2
ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0A
IAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICA
ACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoB
IAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC6gGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJA
QYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ24KAgABB//8DcQ4DAAQBBAsC
QCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTpBAEEAIANCIIinKAIAEYCAgIAAgICA
gAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQThqQarVfjsAACACQTBqQqrVqtWq1arVqn83
AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI6NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEg
AzcDiBACQAJAIAFBiBBqQQhqIAAQ24KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2
ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0A
IAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICA
ACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoB
IAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJA
QYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ3oKAgABB//8DcQ4DAAQBBAsC
QCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcUAQQBBACADQiCIpygCABGAgICAAICA
gIAAIgJFDQACQEHFAEUNACACQaoBQcUA/AsACyABQsUANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ3oKAgABB//8DcQ4D
AAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiAB
QYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsA
CyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAg
ARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAAL
IAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC5cGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFB
uN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ3YKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQL
IAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTRBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arV
qtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arV
qtWq1ap/NwAAIAFCNDcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEN2CgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgC
mBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8
ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwR
gYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAi
AEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAg
AhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2
AowQAkACQAJAAkACQCABQYgQaiAAEN+CgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAEL
QQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyV
x4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEN+CgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYC
lBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQ
AkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQ
IgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABF
DQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuEBgMCfwF+An8jgICAgABBwBBrIgEk
gICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEOCCgIAAQf//A3EO
AwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EsQQBBACADQiCIpygCABGA
gICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEoakGq1arVejYAACACQSBqQqrV
qtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLDcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEOCCgIAAQf//
A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0
EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEg
APwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIg
AEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICA
gIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuGEAECfwJAAkACQAJAAkACQAJAIAFB//8DcSIDQQJHDQAgAkEBcQ0BCwJAIANBBEcNACACQQFxDQIL
AkAgAUH//wNxIgNBDEcNACACQQFxDQMLAkAgA0EURw0AIAJBAXENBAsCQAJAAkAgAUH//wNxIgNBAUcNACACQQFxDQBBARDRgoCAAEEBcQ0BDAgLIANBA0cN
ASACQQFxDQFBAxDRgoCAAEEBcUUNByAAQQE6AAIgAEEDOwEADwsgAEEBOgACIABBATsBAA8LAkACQCABQf//A3EiA0EERiACQQFzIgRxDQAgA0EFRw0BIAJB
AXENAUEFENGCgIAAQQFxRQ0HIABBAToAAiAAQQU7AQAPC0GEgH5BBCACQQFxGyICENGCgIAAQQFxRQ0GIABBAToAAiAAIAI7AQAPCwJAIAFB//8DcSIDQQZH
DQAgAkEBcQ0AQQYQ0YKAgABBAXFFDQYgAEEBOgACIABBBjsBAA8LAkAgA0EHRw0AIAJBAXENAEEHENGCgIAAQQFxRQ0GIABBAToAAiAAQQc7AQAPCwJAIAFB
//8DcSIDQQhHDQAgAkEBcQ0AQQgQ0YKAgABBAXFFDQYgAEEBOgACIABBCDsBAA8LAkAgA0EJRw0AIAJBAXENAEEJENGCgIAAQQFxRQ0GIABBAToAAiAAQQk7
AQAPCwJAAkAgAUH//wNxIgNBDEYgBHENACADQRlHDQEgAkEBcQ0BQRkQ0YKAgABBAXFFDQcgAEEBOgACIABBGTsBAA8LQYyAfkEMIAJBAXEbIgIQ0YKAgABB
AXFFDQYgAEEBOgACIAAgAjsBAA8LAkAgAUH//wNxIgNBKEcNACACQQFxDQBBKBDRgoCAAEEBcUUNBiAAQQE6AAIgAEEoOwEADwsCQCADQS1HDQAgAkEBcQ0A
QS0Q0YKAgABBAXFFDQYgAEEBOgACIABBLTsBAA8LAkAgAUH//wNxIgNBL0cNACACQQFxDQBBLxDRgoCAAEEBcUUNBiAAQQE6AAIgAEEvOwEADwsCQCADQcIA
Rw0AIAJBAXENAEHCABDRgoCAAEEBcUUNBiAAQQE6AAIgAEHCADsBAA8LAkAgAUH//wNxIgNBxQBHDQAgAkEBcQ0AQcUAENGCgIAAQQFxRQ0GIABBAToAAiAA
QcUAOwEADwsCQCADQegHRw0AIAJBAXENAEHoBxDRgoCAAEEBcUUNBiAAQQE6AAIgAEHoBzsBAA8LAkAgAUH//wNxIgNB6gdHDQAgAkEBcQ0AQeoHENGCgIAA
QQFxRQ0GIABBAToAAiAAQeoHOwEADwsCQCADQesHRw0AIAJBAXENAEHrBxDRgoCAAEEBcUUNBiAAQQE6AAIgAEHrBzsBAA8LAkAgAUH//wNxIgNB7AdHDQAg
AkEBcQ0AQewHENGCgIAAQQFxRQ0GIABBAToAAiAAQewHOwEADwsCQCADQe0HRw0AIAJBAXENAEHtBxDRgoCAAEEBcUUNBiAAQQE6AAIgAEHtBzsBAA8LAkAg
AUH//wNxIgNB7gdHDQAgAkEBcQ0AQe4HENGCgIAAQQFxRQ0GIABBAToAAiAAQe4HOwEADwsCQCADQe8HRw0AIAJBAXENAEHvBxDRgoCAAEEBcUUNBiAAQQE6
AAIgAEHvBzsBAA8LAkAgAUH//wNxIgNB9wdHDQAgAkEBcQ0AQfcHENGCgIAAQQFxRQ0GIABBAToAAiAAQfcHOwEADwsCQCADQfgHRw0AIAJBAXENAEH4BxDR
goCAAEEBcUUNBiAAQQE6AAIgAEH4BzsBAA8LAkAgAUH//wNxIgNBiwhHDQAgAkEBcQ0AQYsIENGCgIAAQQFxRQ0GIABBAToAAiAAQYsIOwEADwsCQCADQYwI
Rw0AIAJBAXENAEGMCBDRgoCAAEEBcUUNBiAAQQE6AAIgAEGMCDsBAA8LAkAgAUH//wNxIgNBjwhHDQAgAkEBcQ0AQY8IENGCgIAAQQFxRQ0GIABBAToAAiAA
QY8IOwEADwsCQCADQZUIRw0AIAJBAXENAEGVCBDRgoCAAEEBcUUNBiAAQQE6AAIgAEGVCDsBAA8LAkAgAUH//wNxIgNBlwhHDQAgAkEBcQ0AQZcIENGCgIAA
QQFxRQ0GIABBAToAAiAAQZcIOwEADwsCQCADQZgIRw0AIAJBAXENAEGYCBDRgoCAAEEBcUUNBiAAQQE6AAIgAEGYCDsBAA8LAkAgAUH//wNxIgNBmQhHDQAg
AkEBcQ0AQZkIENGCgIAAQQFxRQ0GIABBAToAAiAAQZkIOwEADwsCQCADQdQPRw0AIAJBAXENAEHUDxDRgoCAAEEBcUUNBiAAQQE6AAIgAEHUDzsBAA8LAkAg
AUH//wNxIgNB6g9HDQAgAkEBcQ0AQeoPENGCgIAAQQFxRQ0GIABBAToAAiAAQeoPOwEADwsCQCADQesPRw0AIAJBAXENAEHrDxDRgoCAAEEBcUUNBiAAQQE6
AAIgAEHrDzsBAA8LAkAgAUH//wNxIgFB7w9HDQAgAkEBcQ0AQe8PENGCgIAAQQFxRQ0GIABBAToAAiAAQe8POwEADwsgAUGAEEcNBCACQQFxDQRBgBAQ0YKA
gABBAXFFDQUgAEEBOgACIABBgBA7AQAPCyAAQQE6AAIgAEGCgAI7AQAPCyAAQQE6AAIgAEGEgAI7AQAPCyAAQQE6AAIgAEGMgAI7AQAPCyAAQQE6AAIgAEGU
gAI7AQAPCyAAQQA2AQAPCwALggEBA38jgICAgABBEGsiAiSAgICAAEEAIQMgACgCDCEEIAJBDGogAUH//wFxIAHBQQBIEKiBgIAAIABBDGohAAJAIAItAA5F
DQBBgIAEQYCACCAEIAIvAQwQ4YKAgABBAXEbIQMLIAAgAyABQf//A3FyEOKCgIAAIAJBEGokgICAgAALjQEBA38jgICAgABBEGsiAySAgICAACAAKAIMIQQg
A0EMaiABQf//AXEiASACEKiBgIAAIABBDGohBUEAIQACQCADLQAORQ0AQYCABEGAgAggBCADLwEMEOGCgIAAQQFxGyEACyAFIABBgIB+QQAgAkEBcRsgAXJB
//8DcXIQ4oKAgAAgA0EQaiSAgICAAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAA
NgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEOaCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKM
ECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAg
ASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEOaCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQ
IQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQ
IAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASAB
KAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUg
ASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAucBgMCfwF+An8j
gICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAA
EOSCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E2QQBB
ACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEuakKq1arV
qtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCNjcDmBAgASACNgKUECABQdyVx4AA
NgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEOSCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYC
lBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQ
AkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQ
IgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABF
DQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuJBgMCfwF+An8jgICAgABBwBBrIgEk
gICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEOOCgIAAQf//A3EO
AwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EvQQBBACADQiCIpygCABGA
gICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEnakKq1arVqtWq1ap/NwAAIAJB
IGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIvNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ44KA
gABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQ
aiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAg
AkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwAL
IAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGA
gIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFC
gBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ64KAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACAC
EIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcUAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHFAEUNACACQaoBQcUA
/AsACyABQsUANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ64KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0C
IAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQ
IAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICA
gIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEo
AogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAA
AAsAC10BAX8CQCABQf//A3EiAUEBIAFBAUsbIgMgAC8B+BEiASACQf//A3EiAiABIAJJGyABIAIbIgFPDQAgACABQX9qOwH+ESAAIANBf2o7AfwRIABBAUEB
EMGAgIAACwuJBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQ
AkACQAJAAkACQCABQYgQaiAAEOeCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQAN
AEEAKQPg+smAACIDp0EtQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq
1arVqn83AAAgAkElakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUItNwOYECABIAI2ApQQIAFB3JXHgAA2
ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ54KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKU
ECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBAC
QCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAi
ABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUN
ACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC7UWAgN/AX4jgICAgABBwBBrIgIkgICA
gAAgACgCDCEDAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QCABQf//A3EiBEHnB0oNACAEQX9qDkUBJQIDBAUGBwglJQklJSUlJSUlJSUlJSUKJSUlJSUlJSUlJSUlJSULJSUlJQwlDSUlJSUlJSUlJSUlJSUlJSUlJQ4l
JQ8lCwJAIARBmHhqDjIQJRESExQVFiUlJSUlJSUXGCUlJSUlJSUlJSUlJSUlJSUlJRkaJSUbJSUlJSUcJR0eHwALAkAgBEGscGoOLSAlJSUlJSUlJSUlJSUl
JSUlJSUlJSUhIiUlJSMlJSUlJSUlJSUlJSUlJSUlJAALAkACQAJAIARB/v99ag4TKCcAJycnJycnJwEnJycnJycnAicLIAMgAzUCAEL9////D4MgAzUCCEIC
gyIFhD4CACAFQgGIpyEDDCgLIAMgAzUCAEL7////D4MgAzUCCEIEgyIFhD4CACAFQgKIpyEDDCcLIAMgAzUCAEL3////D4MgAzUCCEIIgyIFhD4CACAFQgOI
pyEDDCYLIAMgAzUCAELv////D4MgAzUCCEIQgyIFhD4CACAFQgSIpyEDDCULIAMgAzUCAELf////D4MgAzUCCEIggyIFhD4CACAFQgWIpyEDDCQLIAMgAzUC
AEK/////D4MgAzUCCELAAIMiBYQ+AgAgBUIGiKchAwwjCyADIAM1AgBC//7//w+DIAM1AghCgAGDIgWEPgIAIAVCB4inIQMMIgsgAyADNQIAQv/9//8PgyAD
NQIIQoACgyIFhD4CACAFQgiIpyEDDCELIAMgAzUCAEL/+///D4MgAzUCCEKABIMiBYQ+AgAgBUIJiKchAwwgCyADIAM1AgBC//f//w+DIAM1AghCgAiDIgWE
PgIAIAVCCoinIQMMHwsgAyADNQIAQv/v//8PgyADNQIIQoAQgyIFhD4CACAFQguIpyEDDB4LIAMgAzUCAEL/3///D4MgAzUCCEKAIIMiBYQ+AgAgBUIMiKch
AwwdCyADIAM1AgBC/7///w+DIAM1AghCgMAAgyIFhD4CACAFQg2IpyEDDBwLIAMgAzUCAEL///7/D4MgAzUCCEKAgAGDIgWEPgIAIAVCDoinIQMMGwsgAyAD
NQIAQv///f8PgyADNQIIQoCAAoMiBYQ+AgAgBUIPiKchAwwaCyADIAM1AgBC///7/w+DIAM1AghCgIAEgyIFhD4CACAFQhCIpyEDDBkLIAMgAzUCAEL///f/
D4MgAzUCCEKAgAiDIgWEPgIAIAVCEYinIQMMGAsgAyADNQIAQv//7/8PgyADNQIIQoCAEIMiBYQ+AgAgBUISiKchAwwXCyADIAM1AgBC///f/w+DIAM1AghC
gIAggyIFhD4CACAFQhOIpyEDDBYLIAMgAzUCAEL//7//D4MgAzUCCEKAgMAAgyIFhD4CACAFQhSIpyEDDBULIAMgAzUCAEL////+D4MgAzUCCEKAgIABgyIF
hD4CACAFQhWIpyEDDBQLIAMgAzUCAEL////9D4MgAzUCCEKAgIACgyIFhD4CACAFQhaIpyEDDBMLIAMgAzUCAEL////7D4MgAzUCCEKAgIAEgyIFhD4CACAF
QheIpyEDDBILIAMgAzUCAEL////3D4MgAzUCCEKAgIAIgyIFhD4CACAFQhiIpyEDDBELIAMgAzUCAEL////vD4MgAzUCCEKAgIAQgyIFhD4CACAFQhmIpyED
DBALIAMgAzUCAEL////fD4MgAzUCCEKAgIAggyIFhD4CACAFQhqIpyEDDA8LIAMgAzUCAEL///+/D4MgAzUCCEKAgIDAAIMiBYQ+AgAgBUIbiKchAwwOCyAD
IAM1AgBC/////w6DIAM1AghCgICAgAGDIgWEPgIAIAVCHIinIQMMDQsgAyADNQIAQv////8NgyADNQIIQoCAgIACgyIFhD4CACAFQh2IpyEDDAwLIAMgAzUC
AEL/////C4MgAzUCCEKAgICABIMiBYQ+AgAgBUIeiKchAwwLCyADIAM1AgBC/////weDIAM1AggiBUKAgICACIOEPgIAIAVCH4inIQMMCgsgA0EEaiIEIAQx
AABCIIZCgICAgOAfgyADQQxqMQAAQiCGQoCAgIAQgyIFhEIgiDwAACAFQiCIpyEDDAkLIANBBGoiBCAEMQAAQiCGQoCAgIDQH4MgA0EMajEAAEIghkKAgICA
IIMiBYRCIIg8AAAgBUIhiKchAwwICyADQQRqIgQgBDEAAEIghkKAgICAsB+DIANBDGoxAABCIIZCgICAgMAAgyIFhEIgiDwAACAFQiKIpyEDDAcLIANBBGoi
BCAEMQAAQiCGQoCAgIDwHoMgA0EMajEAAEIghkKAgICAgAGDIgWEQiCIPAAAIAVCI4inIQMMBgsgA0EEaiIEIAQxAABCIIZCgICAgPAdgyADQQxqMQAAQiCG
QoCAgICAAoMiBYRCIIg8AAAgBUIkiKchAwwFCyADQQRqIgQgBDEAAEIghkKAgICA8BuDIANBDGoxAABCIIZCgICAgIAEgyIFhEIgiDwAACAFQiWIpyEDDAQL
IANBBGoiBCAEMQAAQiCGQoCAgIDwF4MgA0EMajEAAEIghkKAgICAgAiDIgWEQiCIPAAAIAVCJoinIQMMAwsgA0EEaiIEIAQxAABCIIZCgICAgPAPgyADQQxq
MQAAQiCGIgVCgICAgIAQg4RCIIg8AAAgBUIniKchAwwCCyABENGCgIAAQQFxGgwCCyADIAM1AgBC/v///w+DIAM1AggiBUIBg4Q+AgAgBachAwsCQAJAAkAg
AEEMaiABIAMQ74CAgAAiA0H//wNxRQ0AIAIgAzsBBgJAQYAQRQ0AIAJBCGpBqgFBgBD8CwALIAJCgBA3ApAQIAJBuN7BgAA2AogQIAIgAkEIajYCjBACQAJA
IAJBiBBqIAJBBmoQ6IKAgABB//8DcQ4DAAUBBQsgAigClBAiAyACKAKQECIASw0DIAIoAowQIAMQgICAgAAMAQtBAA0AQQApA+D6yYAAIgWnQcMAQQBBACAF
QiCIpygCABGAgICAAICAgIAAIgNFDQACQEHDAEUNACADQaoBQcMA/AsACyACQsMANwOYECACIAM2ApQQIAJB3JXHgAA2ApAQIAIgBTcDiBACQAJAIAJBkBBq
IAJBBmoQ6IKAgABB//8DcQ4DAAUBBQsgAigCnBAiAyACKAKYECIASw0CIAJCADcDmBAgAigClBAhASACQarVqtV6NgKUECACIAA2ArwQIAIgAzYCuBAgAiAB
NgK0ECACQaQQaiACQbQQaiACQYgQahC4gICAACACIAIoArwQIgM2ApgQIAIgAigCtBAiADYClBAgAiACKAK4EDYCnBACQCADRQ0AIAIoAogQIQEgAigCjBAh
BAJAIANFDQAgAEGqASAD/AsACyABIAAgA0EAQQAgBCgCDBGBgICAAICAgIAACyACLwGsEA0BIAIoAqQQIgAgAigCqBAiAxCAgICAACADRQ0BAkAgA0UNACAA
QaoBIAP8CwALIAIgACADQQAgAhC5gICAAAwBCyACKAKYECIDRQ0AIAIoAogQIQEgAigCjBAhBCACKAKUECEAAkAgA0UNACAAQaoBIAP8CwALIAEgACADQQBB
ACAEKAIMEYGAgIAAgICAgAALIAJBwBBqJICAgIAADwsgAyAAEIiAgIAAAAsgAyAAEIiAgIAAAAsAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQ
RQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ6YKAgABB//8DcQ4DAAQBBAsCQCAB
KAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQS9BAEEAIANCIIinKAIAEYCAgIAAgICAgAAi
AkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSdqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq
1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQi83A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDpgoCAAEH//wNxDgMA
BAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFB
iBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwAL
IAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACAB
ELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsg
AUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALnAYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG4
3sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDqgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsg
ASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBNUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq
1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBLWpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpC
qtWq1arVqtWqfzcAACABQjU3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDqgoCAAEH//wNxDgMABAEECyABKAKcECIA
IAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEg
ASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEo
ApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAP
CyAAIAIQiICAgAAACwAL3QECAn4Ef0IAIQMCQAJAAkAgAkFsakFwTw0AQgAhBAwBCyACQQJ0Qeyex4AAaigCACEFAkADQAJAIAVBA3QiBkHQmceAAGooAgAg
AkYNAEIAIQQMAwsgASEHIAIhCCAGQcyZx4AAaigCACIGIAFGDQECQANAIAYtAAAgBy0AAEcNASAGQQFqIQYgB0EBaiEHIAhBf2oiCEUNAwwACwsgBUEBaiIF
RQ0DIAVBOEkNAAtCACEEDAELIAVBAnRBjJ3HgABqNQIAIQRCgICAgBAhAwsgACAEIAOENwIADwsAC4IGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQ
RQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQkIOAgABB//8DcQ4DAAQBBAsCQCAB
KAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQSpBAEEAIANCIIinKAIAEYCAgIAAgICAgAAi
AkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQarVfjsAACACQSBqQqrVqtWq1arVqn83AAAg
AkEYakKq1arVqtWq1ap/NwAAIAFCKjcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEJCDgIAAQf//A3EOAwAEAQQLIAEo
ApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICA
gAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABB
AEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAM
AQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSA
gICAAA8LIAAgAhCIgICAAAALAAvFAwIGfwF+AkACQCABRQ0AQQAhAgJAAkACQAJAAkADQAJAAkAgACACai0AACIDwCIEQQBIDQAgAkEBaiECDAELQQAhBSAD
QfLjx4AAai0AACIDQfEBRg0IIAIgA0EHcSIGaiIHIAJJDQIgByABSw0IIARBPmpB/wFxQTJLDQIgAkEBaiIERQ0CIAQgAU8NAyAAIARqLQAAIgRCgMGChIkQ
IANBAXZB+ABxrSIIiKdB/wFxSQ0IIARCv//+/PsRIAiIp0H/AXFLDQgCQAJAAkAgBkF+ag4DAAECBQsgAkECaiIDIAJJIQQgAyECIAQNBAwCCyACQQJqIgMg
AkkNAyADIAFPDQUgACADaiwAAEG/f0oNCSACQQNqIgMgAk8hBCADIQIgBA0BDAMLIAJBAmoiAyACSQ0CIAMgAU8NBSAAIANqLAAAQb9/Sg0IIAJBA2oiAyAC
SQ0CIAMgAU8NBiAAIANqLAAAQb9/Sg0IIAJBBGoiAyACTyEEIAMhAiAERQ0CCyACIAFPDQYMAAsLAAsgBCABEIiAgIAAAAsgAyABEIiAgIAAAAsgAyABEIiA
gIAAAAsgAyABEIiAgIAAAAtBASEFCyAFC5gJAwd/AX4BfyOAgICAAEHAEGsiACSAgICAAAJAQYAQRQ0AIABBqgFBgBD8CwALIABCgBA3AogQIABBuN7BgAA2
AoAQIAAgADYChBBBACEBAkACQAJAA0AgACgCjBAiAkHGACABayIDaiIEIAJJDQIgAUH03siAAGohBQJAAkAgBCAAKAKIECIGSw0AAkAgBCAGSw0AAkAgACgC
hBAgAmoiBEG638iAAE8NACAFIAQgA2pJDQYLAkAgA0UNACAEIAUgA/wKAAALIAAoAowQIgQgA2oiAiAESQ0FIAAgAjYCjBAMAgsgBCAGEIiAgIAAAAsgACgC
gBAoAgAhBCAAIAM2ApwQIAAgBTYCmBAgAEGwEGogAEGAEGogAEGYEGpBAUEBIAQRgYCAgACAgICAACAAKQOwECIHpyEDAkAgB0IgiKdB//8DcQ4DAQQABAtB
AA0CQQApA+D6yYAAIgenQcYAQQBBACAHQiCIpygCABGAgICAAICAgIAAIgFFDQICQEHGAEUNACABQaoBQcYA/AsACyAAQsYANwOQECAAIAE2AowQIABB3JXH
gAA2AogQIAAgBzcDgBAgAEGIEGohCEEAIQEDQCAAKAKUECICQcYAIAFrIgNqIgQgAkkNBCABQfTeyIAAaiEFAkACQCAEIAAoApAQIgZLDQACQCAEIAZLDQAC
QCAAKAKMECACaiIEQbrfyIAATw0AIAUgBCADakkNCAsCQCADRQ0AIAQgBSAD/AoAAAsgACgClBAiBCADaiICIARJDQcgACACNgKUEAwCCyAEIAYQiICAgAAA
CyAAKAKIECgCACEEIAAgAzYCrBAgACAFNgKoECAAQbAQaiAIIABBqBBqQQFBASAEEYGAgIAAgICAgAAgACkDsBAiB6chAwJAIAdCIIinQf//A3EOAwEGAAYL
IAAoApAQIgFFDQQgACgCgBAhBCAAKAKEECECIAAoAowQIQMCQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAwECyABIANqIgMg
AUkNBCADIQEgA0HGAEkNAAsCQCAAKAKUECIBIAAoApAQIgNLDQAgAEIANwOQECAAKAKMECEEIABBqtWq1Xo2AowQIAAgAzYCuBAgACABNgK0ECAAIAQ2ArAQ
IABBmBBqIABBsBBqIABBgBBqELiAgIAAIAAgACgCuBAiATYCkBAgACAAKAKwECIDNgKMECAAIAAoArQQNgKUEAJAIAFFDQAgACgCgBAhBCAAKAKEECECAkAg
AUUNACADQaoBIAH8CwALIAQgAyABQQBBACACKAIMEYGAgIAAgICAgAALIAAvAaAQDQMgACgCmBAiAyAAKAKcECIBEICAgIAAIAFFDQMCQCABRQ0AIANBqgEg
AfwLAAsgACADIAFBACAAELmAgIAADAMLIAEgAxCIgICAAAALIAEgA2oiAyABSQ0CIAMhASADQcYASQ0ACyAAKAKMECIBIAAoAogQIgNLDQIgACgChBAgARCA
gICAAAsgAEHAEGokgICAgAAPCwALIAEgAxCIgICAAAALiQwDAn8BfgJ/I4CAgIAAQdAQayIDJICAgIAAAkACQCACQYAITQ0AIAMgAjYCDAJAQYAQRQ0AIANB
GGpBqgFBgBD8CwALIANCgBA3AqAQIANBuN7BgAA2ApgQIAMgA0EYajYCnBACQAJAIANBmBBqIANBDGoQioOAgABB//8DcQ4DAAMBAwsCQCADKAKkECICIAMo
AqAQIgRNDQAgAiAEEIiAgIAADAMLIAMoApwQIAIQgICAgABBgAghAgwBCwJAAkBBAA0AQQApA+D6yYAAIgWnQc0AQQBBACAFQiCIpygCABGAgICAAICAgIAA
IgJFDQACQEHNAEUNACACQaoBQc0A/AsACyADQs0ANwOoECADIAI2AqQQIANB3JXHgAA2AqAQIAMgBTcDmBACQAJAIANBoBBqIANBDGoQioOAgABB//8DcQ4D
AAUBBQsgAygCrBAiAiADKAKoECIESw0CIANCADcDqBAgAygCpBAhBiADQarVqtV6NgKkECADIAQ2AswQIAMgAjYCyBAgAyAGNgLEECADQbQQaiADQcQQaiAD
QZgQahC4gICAACADIAMoAswQIgI2AqgQIAMgAygCxBAiBDYCpBAgAyADKALIEDYCrBACQCACRQ0AIAMoApgQIQYgAygCnBAhBwJAIAJFDQAgBEGqASAC/AsA
CyAGIAQgAkEAQQAgBygCDBGBgICAAICAgIAACyADLwG8EA0BIAMoArQQIgQgAygCuBAiAhCAgICAACACRQ0BAkAgAkUNACAEQaoBIAL8CwALIAMgBCACQQAg
AxC5gICAAEGACCECDAMLIAMoAqgQIgJFDQAgAygCmBAhBiADKAKcECEHIAMoAqQQIQQCQCACRQ0AIARBqgEgAvwLAAsgBiAEIAJBAEEAIAcoAgwRgYCAgACA
gICAAAtBgAghAgwBCyACIAQQiICAgAAACwJAAkACQAJAIABBDGoiBCgCACABIAIQi4OAgAAiAkH//wNxDQAgACgCKCICRQ0BIAQgAhGDgICAAICAgIAADAEL
IAMgAjsBEgJAQYAQRQ0AIANBGGpBqgFBgBD8CwALIANCgBA3AqAQIANBuN7BgAA2ApgQIAMgA0EYajYCnBACQAJAIANBmBBqIANBEmoQjIOAgABB//8DcQ4D
AAUBBQsgAygCpBAiAiADKAKgECIASw0DIAMoApwQIAIQgICAgAAMAQtBAA0AQQApA+D6yYAAIgWnQTRBAEEAIAVCIIinKAIAEYCAgIAAgICAgAAiAkUNACAC
QqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBq
QqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIANCNDcDqBAgAyACNgKkECADQdyVx4AANgKgECADIAU3A5gQAkACQCADQZgQakEIaiADQRJqEIyD
gIAAQf//A3EOAwAFAQULIAMoAqwQIgIgAygCqBAiAEsNAiADQgA3A6gQIAMoAqQQIQEgA0Gq1arVejYCpBAgAyAANgLMECADIAI2AsgQIAMgATYCxBAgA0G0
EGogA0HEEGogA0GYEGoQuICAgAAgAyADKALMECICNgKoECADIAMoAsQQIgA2AqQQIAMgAygCyBA2AqwQAkAgAkUNACADKAKYECEBIAMoApwQIQQCQCACRQ0A
IABBqgEgAvwLAAsgASAAIAJBAEEAIAQoAgwRgYCAgACAgICAAAsgAy8BvBANASADKAK0ECIAIAMoArgQIgIQgICAgAAgAkUNAQJAIAJFDQAgAEGqASAC/AsA
CyADIAAgAkEAIAMQuYCAgAAMAQsgAygCqBAiAkUNACADKAKYECEBIAMoApwQIQQgAygCpBAhAAJAIAJFDQAgAEGqASAC/AsACyABIAAgAkEAQQAgBCgCDBGB
gICAAICAgIAACyADQdAQaiSAgICAAA8LIAIgABCIgICAAAALIAIgABCIgICAAAALAAv7CQMHfwF+AX8jgICAgABBwBBrIgAkgICAgAACQEGAEEUNACAAQaoB
QYAQ/AsACyAAQoAQNwKIECAAQbjewYAANgKAECAAIAA2AoQQQQAhAQJAAkACQANAIAAoAowQIgJBMSABayIDaiIEIAJJDQIgAUGd4MiAAGohBQJAAkAgBCAA
KAKIECIGSw0AAkAgBCAGSw0AAkAgACgChBAgAmoiBEHO4MiAAE8NACAFIAQgA2pJDQYLAkAgA0UNACAEIAUgA/wKAAALIAAoAowQIgQgA2oiAiAESQ0FIAAg
AjYCjBAMAgsgBCAGEIiAgIAAAAsgACgCgBAoAgAhBCAAIAM2ApwQIAAgBTYCmBAgAEGwEGogAEGAEGogAEGYEGpBAUEBIAQRgYCAgACAgICAACAAKQOwECIH
pyEDAkAgB0IgiKdB//8DcQ4DAQQABAtBAA0CQQApA+D6yYAAIgenQTFBAEEAIAdCIIinKAIAEYCAgIAAgICAgAAiAUUNAiABQqrVqtWq1arVqn83AAAgAUEQ
akKq1arVqtWq1ap/NwAAIAFBCGpCqtWq1arVqtWqfzcAACABQTBqQap/OgAAIAFBKGpCqtWq1arVqtWqfzcAACABQSBqQqrVqtWq1arVqn83AAAgAUEYakKq
1arVqtWq1ap/NwAAIABCMTcDkBAgACABNgKMECAAQdyVx4AANgKIECAAIAc3A4AQIABBgBBqQQhqIQhBACEBA0AgACgClBAiAkExIAFrIgNqIgQgAkkNBCAB
QZ3gyIAAaiEFAkACQCAEIAAoApAQIgZLDQACQCAEIAZLDQACQCAAKAKMECACaiIEQc7gyIAATw0AIAUgBCADakkNCAsCQCADRQ0AIAQgBSAD/AoAAAsgACgC
lBAiBCADaiICIARJDQcgACACNgKUEAwCCyAEIAYQiICAgAAACyAAKAKIECgCACEEIAAgAzYCrBAgACAFNgKoECAAQbAQaiAIIABBqBBqQQFBASAEEYGAgIAA
gICAgAAgACkDsBAiB6chAwJAIAdCIIinQf//A3EOAwEGAAYLIAAoApAQIgFFDQQgACgCgBAhBCAAKAKEECECIAAoAowQIQMCQCABRQ0AIANBqgEgAfwLAAsg
BCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAwECyABIANqIgMgAUkNBCADIQEgA0ExSQ0ACwJAIAAoApQQIgEgACgCkBAiA0sNACAAQgA3A5AQIAAoAowQIQQg
AEGq1arVejYCjBAgACADNgK4ECAAIAE2ArQQIAAgBDYCsBAgAEGYEGogAEGwEGogAEGAEGoQuICAgAAgACAAKAK4ECIBNgKQECAAIAAoArAQIgM2AowQIAAg
ACgCtBA2ApQQAkAgAUUNACAAKAKAECEEIAAoAoQQIQICQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAsgAC8BoBANAyAAKAKY
ECIDIAAoApwQIgEQgICAgAAgAUUNAwJAIAFFDQAgA0GqASAB/AsACyAAIAMgAUEAIAAQuYCAgAAMAwsgASADEIiAgIAAAAsgASADaiIDIAFJDQIgAyEBIANB
MUkNAAsgACgCjBAiASAAKAKIECIDSw0CIAAoAoQQIAEQgICAgAALIABBwBBqJICAgIAADwsACyABIAMQiICAgAAAC48DAwR/AX4BfwJAAkACQCAALwHWASIB
DQAgACgCwAFFDQEMAgsgACgCxAEoAgAiAigCtAEgAU0NASACKAIQIgMgAigCrAFqIgQgA0kNASAEQQNxDQEgBEUNASAEIAFBHGxqIgQvARgiAUUNASAEQRhq
IAFBf2oiBDsBAAJAIARB//8DcQ0AIAIoArABIgRBf2oiASAESw0CIAIgATYCsAELIAAoAsABIgRFDQEgACkDmAEiBUIgiKchASAFpyEDAkAgBCgCECICRQ0A
IAQoAgwhBgJAIAJFDQAgBkGqASAC/AsACyADIAYgAkEAQQAgASgCDBGBgICAAICAgIAACwJAIAQtAAhBAXENACAEKAIEIgJFDQAgBCgCACEEAkAgAkUNACAE
QaoBIAL8CwALIAMgBCACQQBBACABKAIMEYGAgIAAgICAgAALIAAoAsABIgRFDQEgACgCmAEgBEEUQQJBACAAQZwBaigCAEEMaigCABGBgICAAICAgIAAIABB
ADYCwAEgAEEAOwHWAQsPCwAL4AUDAX8BfgJ/I4CAgIAAQcAQayICJICAgIAAAkACQAJAIAAgASgCACABKAIEIAEoAgggASgCDBCRg4CAACIBQf//A3FFDQAg
AiABOwEGAkBBgBBFDQAgAkEIakGqAUGAEPwLAAsgAkKAEDcCkBAgAkG43sGAADYCiBAgAiACQQhqNgKMEAJAAkAgAkGIEGogAkEGahCSg4CAAEH//wNxDgMA
BAEECwJAIAIoApQQIgEgAigCkBAiAE0NACABIAAQiICAgAAMBAsgAigCjBAgARCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBwwBBAEEAIANCIIinKAIAEYCA
gIAAgICAgAAiAUUNAAJAQcMARQ0AIAFBqgFBwwD8CwALIAJCwwA3A5gQIAIgATYClBAgAkHclceAADYCkBAgAiADNwOIEAJAAkAgAkGQEGogAkEGahCSg4CA
AEH//wNxDgMABAEECyACKAKcECIBIAIoApgQIgBLDQIgAkIANwOYECACKAKUECEEIAJBqtWq1Xo2ApQQIAIgADYCvBAgAiABNgK4ECACIAQ2ArQQIAJBpBBq
IAJBtBBqIAJBiBBqELiAgIAAIAIgAigCvBAiATYCmBAgAiACKAK0ECIANgKUECACIAIoArgQNgKcEAJAIAFFDQAgAigCiBAhBCACKAKMECEFAkAgAUUNACAA
QaoBIAH8CwALIAQgACABQQBBACAFKAIMEYGAgIAAgICAgAALIAIvAawQDQEgAigCpBAiACACKAKoECIBEICAgIAAIAFFDQECQCABRQ0AIABBqgEgAfwLAAsg
AiAAIAFBACACELmAgIAADAELIAIoApgQIgFFDQAgAigCiBAhBCACKAKMECEFIAIoApQQIQACQCABRQ0AIABBqgEgAfwLAAsgBCAAIAFBAEEAIAUoAgwRgYCA
gACAgICAAAsgAkHAEGokgICAgAAPCyABIAAQiICAgAAACwALkwQBBH8CQCACRQ0AA0AgAS8BACABQQJqLQAAQRB0ciEDAkACQAJAAkACQCABQQhqLQAAQQNx
DgQEAQIABAsACyABQQVqLQAAIQQgAUEEai0AAEEBcUUNAQJAAkACQCAEQQdxDgUAAQUFAgULIAAoAgwiBCADOwGoASAEQQE6AKwBIARBqgFqIANBEHY6AAAM
BAsgACgCDCIEIAM7AZgBIARBAToAnAEgBEGaAWogA0EQdjoAAAwDCyAAKAIMIgQgAzsBuAEgBEEBOgC8ASAEQboBaiADQRB2OgAADAILIANBgP7/B3FBCHYh
BAJAIANBAXENACAAKAIMIgMgAy8B9BFBgIACcjsB9BEgACgCDCIFIARB/wFxIgZBAnRqIgMgAy8ByAk7AcgBIANBygFqIANByglqLQAAOgAAIAUgBEEDdkEc
cWoiAyADKALIEUF+IAZ3cTYCyBEMAgsCQAJAAkAgBEEHcQ4FAAEEBAIECyAAKAIMIgMgAykCsAE3AqgBDAMLIAAoAgwiAyADKQKgATcCmAEMAgsgACgCDCID
IAMpAsABNwK4AQwBCyAAKAIMIgUgBS8B9BFBgIACcjsB9BEgACgCDCIFIARBAnRqIgYgAzsByAEgBkHKAWogA0EQdjoAACAFIARBA3ZBHHFqIgMgAygCyBFB
ASAEdHI2AsgRCyABQQxqIQEgAkF/aiICDQALCwuaCQMBfwF+DX8jgICAgABBMGsiAiSAgICAACACQSBqIAFBIGopAgAiAzcDACACQRhqIAFBGGopAgA3AwAg
AkEQaiABQRBqKQIANwMAIAJBCGogAUEIaikCADcDACACIAEpAgA3AwACQAJAIAOnIgRFDQBBACEFIAIoAhghBiACKAIcIQcDQAJAAkACQAJAAkACQAJAAkAC
QAJAAkAgBUECSQ0AIAcgAi0ALEEfcSIITQ0CIAlBAWoiCkUNDSAGIAhBAnRqKAIAIQECQCAKIAtHDQAgCEEBaiIKQR9xIgggCkcNDiACIAg6ACwgC0F/TA0O
IAtBAXQhC0EAIQoLIAFFDQwgASAJQQxsaiEBIAVBAWohBSAKIQkMAQsgAiAFQQxsaiEBAkAgBUEBRg0AQQEhBQwBC0EAIQkgAkEAOgAsQQQhC0ECIQULIAEv
AQAgAUECai0AAEEQdHIhCiABLQAIQQdxDgUBCAIHCAsLIAggBxCIgICAAAwKCyABLQAFIQggAS0ABEEDcQ4EAQYCCQELIApBgP7/B3FBCHYhASAKQQNxDgQC
BQMIAgsgACgCDCIBIAEvAfQRQYCAAnI7AfQRIAAoAgwiASAIQQJ0aiIMIAo7AcgBIAxBygFqIApBEHY6AAAgASAIQQN2QRxxaiIBIAEoAsgRQQEgCHRyNgLI
EQwECyAIQR9xIgFBc2pBB0kNAwJAAkACQCABQXZqDgMAAQIJCyAAKAIMIgEgCjsBqAEgAUEBOgCsASABQaoBaiAKQRB2OgAADAULIAAoAgwiASAKOwGYASAB
QQE6AJwBIAFBmgFqIApBEHY6AAAMBAsgACgCDCIBIAo7AbgBIAFBAToAvAEgAUG6AWogCkEQdjoAAAwDCyAAKAIMIgogCi8B9BFBgIACcjsB9BEgACgCDCII
IAFB/wFxIgxBAnRqIgogCi8ByAk7AcgBIApBygFqIApByglqLQAAOgAAIAggAUEDdkEccWoiASABKALIEUF+IAx3cTYCyBEMAgsgAUEfcSIBQXNqQQdJDQEC
QAJAAkAgAUF2ag4DAAECBwsgACgCDCIBIAEpArABNwKoAQwDCyAAKAIMIgEgASkCoAE3ApgBDAILIAAoAgwiASABKQLAATcCuAEMAQsgACgCDCIBQcwRaiEK
IAFByBFqIQ0gASgCyBEhDEEAIQFBByEIA0ACQAJAIAxFDQAgCiEOIAEhDwwBCwJAA0AgCEUNASABQSBqIg8gAUkNByAIQX9qIQggCigCACEMIA8hASAKQQRq
Ig4hCiAMDQIMAAsLIA1CADcCACANQRhqQgA3AgAgDUEQakIANwIAIA1BCGpCADcCAAwCCyAMaCIKIA9qIgEgCkkNBCAAKAIMIgogCi8B9BFBgIACcjsB9BEg
AUGAAk8NBCAAKAIMIhAgAUECdGoiCiAKLwHICTsByAEgCkHKAWogCkHKCWotAAA6AAAgECABQQN2Qfz///8BcWoiCiAKKALIEUF+IAF3cTYCyBEgDEF/aiAM
cSEMIA4hCiAPIQEMAAsLIAUgBEkNAAsLIAJBMGokgICAgAAPCwAL2wYDAX8BfgF/I4CAgIAAQcAQayIDJICAgIAAAkACQAJAAkAgACgCDCABIAIQjoOAgAAi
AkH//wNxDQAgACgCLCICRQ0BIABBDGogAhGDgICAAICAgIAADAELIAMgAjsBBgJAQYAQRQ0AIANBCGpBqgFBgBD8CwALIANCgBA3ApAQIANBuN7BgAA2AogQ
IAMgA0EIajYCjBACQAJAIANBiBBqIANBBmoQj4OAgABB//8DcQ4DAAQBBAsCQCADKAKUECIAIAMoApAQIgJNDQAgACACEIiAgIAADAQLIAMoAowQIAAQgICA
gAAMAQtBAA0AQQApA+D6yYAAIgSnQTJBAEEAIARCIIinKAIAEYCAgIAAgICAgAAiAEUNACAAQqrVqtWq1arVqn83AAAgAEEQakKq1arVqtWq1ap/NwAAIABB
CGpCqtWq1arVqtWqfzcAACAAQTBqQarVfjsAACAAQShqQqrVqtWq1arVqn83AAAgAEEgakKq1arVqtWq1ap/NwAAIABBGGpCqtWq1arVqtWqfzcAACADQjI3
A5gQIAMgADYClBAgA0HclceAADYCkBAgAyAENwOIEAJAAkAgA0GIEGpBCGogA0EGahCPg4CAAEH//wNxDgMABAEECyADKAKcECIAIAMoApgQIgJLDQIgA0IA
NwOYECADKAKUECEBIANBqtWq1Xo2ApQQIAMgAjYCvBAgAyAANgK4ECADIAE2ArQQIANBpBBqIANBtBBqIANBiBBqELiAgIAAIAMgAygCvBAiADYCmBAgAyAD
KAK0ECICNgKUECADIAMoArgQNgKcEAJAIABFDQAgAygCiBAhASADKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQBBACAFKAIMEYGAgIAAgICAgAAL
IAMvAawQDQEgAygCpBAiAiADKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgAyACIABBACADELmAgIAADAELIAMoApgQIgBFDQAgAygCiBAh
ASADKAKMECEFIAMoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgASACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgA0HAEGokgICAgAAPCyAAIAIQiICAgAAACwAL
rwYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJA
AkAgAUGIEGogABCNg4CAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJ
gAAiA6dBP0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAA
IAJBN2pCqtWq1arVqtWqfzcAACACQTBqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arV
qn83AAAgAUI/NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQjYOAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECIC
Sw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2
ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICA
AICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0A
IAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiA
gIAAAAsAC/YFAwJ/AX4BfyOAgICAAEHAEGsiAiSAgICAAAJAAkACQAJAIAAoAgwgARCIg4CAACIDQf//A3ENACAAKAI0IgNFDQEgAEEMaiABLQAIIAMRhICA
gACAgICAAAwBCyACIAM7AQYCQEGAEEUNACACQQhqQaoBQYAQ/AsACyACQoAQNwKQECACQbjewYAANgKIECACIAJBCGo2AowQAkACQCACQYgQaiACQQZqEImD
gIAAQf//A3EOAwAEAQQLAkAgAigClBAiACACKAKQECIBTQ0AIAAgARCIgICAAAwECyACKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIEp0HDAEEAQQAg
BEIgiKcoAgARgICAgACAgICAACIARQ0AAkBBwwBFDQAgAEGqAUHDAPwLAAsgAkLDADcDmBAgAiAANgKUECACQdyVx4AANgKQECACIAQ3A4gQAkACQCACQZAQ
aiACQQZqEImDgIAAQf//A3EOAwAEAQQLIAIoApwQIgAgAigCmBAiAUsNAiACQgA3A5gQIAIoApQQIQMgAkGq1arVejYClBAgAiABNgK8ECACIAA2ArgQIAIg
AzYCtBAgAkGkEGogAkG0EGogAkGIEGoQuICAgAAgAiACKAK8ECIANgKYECACIAIoArQQIgE2ApQQIAIgAigCuBA2ApwQAkAgAEUNACACKAKIECEDIAIoAowQ
IQUCQCAARQ0AIAFBqgEgAPwLAAsgAyABIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAi8BrBANASACKAKkECIBIAIoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAg
AUGqASAA/AsACyAAIAEgAEEAIAAQuYCAgAAMAQsgAigCmBAiAEUNACACKAKIECEDIAIoAowQIQUgAigClBAhAQJAIABFDQAgAUGqASAA/AsACyADIAEgAEEA
QQAgBSgCDBGBgICAAICAgIAACyACQcAQaiSAgICAAA8LIAAgARCIgICAAAALAAvNCgcCfwF+BH8BfgF/AX4CfyOAgICAAEHgEGsiASSAgICAAAJAAkAgACgC
GCICLQCMAkUNACABQRBqIAJB6AFqKQIANwMAIAFBGGogAkHwAWopAgA3AwAgAUEgaiACQfgBaigCADYCACABIAIpAuABNwMIQoACQgAgAi0AggJBAXEbIQMg
Ai0AgAIhBCACLQCBAiEFIAIvAfwBIQYgAi8B/gEhByACKQCDAiEIDAELQQAhBCABQSBqQQA2AgBCACEDIAFBGGpCADcDACABQRBqQgA3AwAgAUIANwMIQQAp
A6j5yYAAIQhBACEFQQAhBkEAIQcLIAIgASkDCDcCoAEgAkG4AWogAUEgaigCADYCACACQbABaiABQRhqKQMANwIAIAJBqAFqIAFBEGopAwA3AgACQAJAIAAo
AhgQwICAgABB//8DcSICRQ0AIAEgAjsBJgJAQYAQRQ0AIAFBKGpBqgFBgBD8CwALIAFCgBA3ArAQIAFBuN7BgAA2AqgQIAEgAUEoajYCrBACQAJAAkACQCAB
QagQaiABQSZqEJODgIAAQf//A3EOAwAFAQULAkAgASgCtBAiAiABKAKwECIJTQ0AIAIgCRCIgICAAAwFCyABKAKsECACEICAgIAADAELQQANAEEAKQPg+smA
ACIKp0E8QQBBACAKQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAg
AkE4akGq1arVejYAACACQTBqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAg
AUI8NwO4ECABIAI2ArQQIAFB3JXHgAA2ArAQIAEgCjcDqBACQAJAIAFBqBBqQQhqIAFBJmoQk4OAgABB//8DcQ4DAAUBBQsgASgCvBAiAiABKAK4ECIJSw0C
IAFCADcDuBAgASgCtBAhCyABQarVqtV6NgK0ECABIAk2AtwQIAEgAjYC2BAgASALNgLUECABQcQQaiABQdQQaiABQagQahC4gICAACABIAEoAtwQIgI2ArgQ
IAEgASgC1BAiCTYCtBAgASABKALYEDYCvBACQCACRQ0AIAEoAqgQIQsgASgCrBAhDAJAIAJFDQAgCUGqASAC/AsACyALIAkgAkEAQQAgDCgCDBGBgICAAICA
gIAACyABLwHMEA0BIAEoAsQQIgkgASgCyBAiAhCAgICAACACRQ0BAkAgAkUNACAJQaoBIAL8CwALIAEgCSACQQAgARC5gICAAAwBCyABKAK4ECICRQ0AIAEo
AqgQIQsgASgCrBAhDCABKAK0ECEJAkAgAkUNACAJQaoBIAL8CwALIAsgCSACQQBBACAMKAIMEYGAgIAAgICAgAALIAAoAhgiAkIANwKgASACQbgBakEANgIA
IAJBsAFqQgA3AgAgAkGoAWpCADcCACAAKAIYEMCAgIAAQf//A3EiAkUNASACEIyAgIAAAAsgAiAJEIiAgIAAAAsgACgCGCAINwC1AiAAIAA1AgBC//3//w+D
IAOEPgIAIAAoAhggBUEBcToA2QEgACgCGCAEQQFxOgDaASAALwH6EUF/aiICQf//A3EgAkcNACAALwH4EUF/aiIFQf//A3EiBCAFRw0AIAAoAhggBkH//wNx
IgAgAkH//wNxIgIgACACSRsgB0H//wNxIgAgBCAAIARJGxDVgICAACABQeAQaiSAgICAAA8LAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUN
ACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEIaDgIAAQf//A3EOAwAEAQQLAkAgASgC
lBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACIC
RQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEIaDgIAAQf//A3EOAwAEAQQL
IAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQ
uICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCAC
IABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCA
gAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQ
aiSAgICAAA8LIAAgAhCIgICAAAALAAuBBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAA
NgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEIWDgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKM
ECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EpQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWq
fzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEoakGqfzoAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCKTcDmBAgASACNgKUECAB
QdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEIWDgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq
1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgC
uBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECIC
IAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAh
AgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABB
wBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEISDgIAA
Qf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HFAEEAQQAgA0Ig
iKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAA
EISDgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuBBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsA
CyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEIODgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0A
IAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EpQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/
NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEoakGqfzoAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAA
IAFCKTcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEIODgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiAB
QgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECAB
IAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKI
ECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAAL
AAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJA
AkACQCABQYgQaiAAEIKDgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg
+smAACIDp0HFAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBxQBFDQAgAkGqAUHFAPwLAAsgAULFADcDmBAgASACNgKUECABQdyVx4AANgKQECAB
IAM3A4gQAkACQCABQZAQaiAAEIKDgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8
ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACAB
KAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAg
AEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA
/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuUBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGA
EEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEIGDgIAAQf//A3EOAwAEAQQLAkAg
ASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0ExQQBBACADQiCIpygCABGAgICAAICAgIAA
IgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEwakGqfzoAACACQShqQqrVqtWq1arVqn83AAAg
AkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjE3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCB
g4CAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFB
pBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUN
ACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwL
AAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwR
gYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsg
AUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCAg4CAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAA
IAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBxQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcUARQ0AIAJBqgFB
xQD8CwALIAFCxQA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCAg4CAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJL
DQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYC
mBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAA
gICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAg
ASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICA
gAAACwALiQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJA
AkACQAJAAkAgAUGIEGogABCggoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBB
ACkD4PrJgAAiA6dBL0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq
1ap/NwAAIAJBJ2pCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLzcDmBAgASACNgKUECABQdyVx4AANgKQ
ECABIAM3A4gQAkACQCABQYgQakEIaiAAEKCCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAg
ASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAg
AEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQ
gICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAg
AkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuJBgMCfwF+An8jgICAgABBwBBrIgEkgICA
gAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEP+CgIAAQf//A3EOAwAE
AQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EwQQBBACADQiCIpygCABGAgICA
AICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpC
qtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIwNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ/4KAgABB
//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiAB
QbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGq
ASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEg
AiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAA
gICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC5UGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3
ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ/oKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiA
gIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTJBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQ
akKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVfjsAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpC
qtWq1arVqtWqfzcAACABQjI3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABD+goCAAEH//wNxDgMABAEECyABKAKcECIA
IAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEg
ASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEo
ApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAP
CyAAIAIQiICAgAAACwALgQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASAB
QQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABD9goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICA
AAwBC0EADQBBACkD4PrJgAAiA6dBKUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEI
akKq1arVqtWq1ap/NwAAIAJBKGpBqn86AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQik3A5gQIAEgAjYClBAgAUHclceAADYC
kBAgASADNwOIEAJAAkAgAUGIEGpBCGogABD9goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQ
IAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJA
IABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIA
EICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwAL1AsDCH8BfgR/I4CAgIAAQYABayICJICA
gIAAIAAoAhhBADoA2QECQAJAIAAoAhgiAy8B0AEiBCAALwGAEkkNACAEIAAvAYISIgVLDQAgAygCxAEoAgAiBkEQaiEHAkAgAygCzAEiCCkDAEKAgICAgIAD
g0KAgICAgIACUg0AIARFDQIgAyAHIAMoAsgBIAhBeGpBAhCzgoCAACAALwGCEiEFIAAoAhgiAy8B0AEhBAsgBUH//wNxIARB//8DcWsiBEH//wNxIgUgBEcN
ASAFQQFqIgRB//8DcSAERw0BAkAgCCAEQX9qQf//A3FBA3RqIgUpAwBCgICAgICAA4NCgICAgICAAVINACADIAcgAygCyAEgBUECELOCgIAACyAEQf//A3Ei
AyABIAMgASADSRsiCWsiA0H//wNxIANHDQECQCADQf//A3FFDQACQCAIIANBf2pB//8DcUEDdCIEaiIDKQMAQoCAgICAgAODQoCAgICAgAFSDQAgAykDCEKA
gICAgIADg0KAgICAgIACUg0DIAAoAhgiASAHIAEoAsgBIANBAhCzgoCAAAsgCUEDdCEFIAggBGohBANAIAMgBWoiASkDAEIDgyEKAkACQAJAAkAgAykDAEID
g0IBUQ0AIApCAVENAQwDCyAEIAcoAgAiC2siDCAESyENIApCAVENASANDQYgBSAEaiINIAtrIg4gDUsNBiALIAYoAjhqIg0gC0kNBiANRQ0GIAIgDTYCBCAC
QQhqIA0gDBCtgoCAACACLQAQRQ0GIAIoAgwpAgAhCiACQQRqIAIoAggQroKAgAAgDSAOIAoQr4KAgAAMAgsgBSAEaiILIAcoAgAiDWsiDCALSw0FIAQgDWsi
DiAESw0FIA0gBigCOGoiCyANSQ0FIAtFDQUgAiALNgIUIAJBGGogCyAMEK2CgIAAIAItACBFDQUgAigCHCkCACEKIAJBFGogAigCGBCugoCAACALIA4gChCv
goCAAAwBCyANDQQgBSAEaiINIAtrIg4gDUsNBCALIAYoAjhqIg0gC0kNBCANRQ0EIAJBJGogDSAMEK2CgIAAIAItACxFDQQgAigCKCELIAJBMGogDSAOEK2C
gIAAIAItADhFDQQgCykCACEKIAsgAigCNCINKQIANwIAIA0gCjcCAAsgASkDAEKAgICAgIAIgyEKAkACQCADLQAFQSBxDQAgClANASAFIARqIg0gBygCACIL
ayIOIA1LDQUgBCALayIMIARLDQUgCyAGKAKkAWoiDSALSQ0FIA1FDQUgAiANNgJMIAJB0ABqIA0gDhCwgoCAACACLQBYRQ0FIAIoAlQvAQAhCyACQcwAaiAC
KAJQELGCgIAAIAJB9ABqIAJBzABqIAwQsoKAgAACQCACLQB8DQAgAigCdCAMNgIACyACKAJ4IAs7AQAMAQsgBCAHKAIAIgtrIgwgBEshDQJAIApCAFINACAN
DQUgBSAEaiINIAtrIg4gDUsNBSALIAYoAqQBaiINIAtJDQUgDUUNBSACIA02AjwgAkHAAGogDSAMELCCgIAAIAItAEhFDQUgAigCRC8BACELIAJBPGogAigC
QBCxgoCAACACQfQAaiACQTxqIA4QsoKAgAACQCACLQB8DQAgAigCdCAONgIACyACKAJ4IAs7AQAMAQsgDQ0EIAUgBGoiDSALayIOIA1LDQQgCyAGKAKkAWoi
DSALSQ0EIA1FDQQgAkHcAGogDSAMELCCgIAAIAItAGRFDQQgAigCYCELIAJB6ABqIA0gDhCwgoCAACACLQBwRQ0EIAsvAQAhDSALIAIoAmwiDC8BADsBACAM
IA07AQALIAEpAwAhCiABIAMpAwA3AwAgAyAKNwMAIARBeGohBCADQXhqIgMgCE8NAAsLIAAoAhgiAyAHIAMoAsgBIAggCRCzgoCAACAAKAIYKALIASIDIAMp
AwBCgICAgIAghDcDAAsgAkGAAWokgICAgAAPCwALqQwCCH8BfiOAgICAAEHAEGsiASSAgICAACABQSBqQqrVqtWq1arVqn83AwAgAUEYakKq1arVqtWq1ap/
NwMAIAFBEGpCqtWq1arVqtWqfzcDACABQqrVqtWq1arVqn83AwggASAAKAIIKAIYIgIgAi0AxgJBB3FqLQC+AkEfcSIDOgAGIAEgAzoAByABQiA3ApAQIAFB
uN7BgAA2AogQIAEgAUEIajYCjBBBACECAkACQAJAAkACQAJAAkACQAJAA0AgASgClBAiBCACQQNzIgVqIgYgBEkNBCACQc2wyIAAaiEHAkACQCAGIAEoApAQ
IghLDQAgBiAISw0HAkAgASgCjBAgBGoiBkHQsMiAAE8NACAHIAYgBWpJDQcLAkAgBUUNACAGIAcgBfwKAAALIAEoApQQIgYgBWoiBCAGSQ0GIAEgBDYClBAM
AQsgASgCiBAoAgAhBiABIAU2AqQQIAEgBzYCoBAgAUGwEGogAUGIEGogAUGgEGpBAUEBIAYRgYCAgACAgICAACABKQOwECIJQiCIpyIFQf//A3ENAiAJpyEF
CyACIAVqIgUgAkkNBCAFIQIgBUEDSQ0ACyABQYgQaiADQQpBAEGw2MmAABCVgICAACIFQf//A3ENAAJAA0AgASgClBAiAkEBaiIFRQ0FAkAgBSABKAKQECIG
Sw0AIAIgBk8NCAJAIAEoAowQIAJqIgJB0bDIgABPDQAgAkEBakHQsMiAAEsNBwsgAkH1ADoAACABKAKUEEEBaiICRQ0GIAEgAjYClBAMAgsgASgCiBAoAgAh
AiABQQE2AqQQIAFB0LDIgAA2AqAQIAFBsBBqIAFBiBBqIAFBoBBqQQFBASACEYGAgIAAgICAgAAgASkDsBAiCUIgiKciBUH//wNxDQIgCadFDQALIAEoApQQ
IQILIAIgASgCkBAiBU0NASACIAUQiICAgAAACyAFQf//A3FBAkcNAkElIQIMAQsgACABKAKMECACEOCAgIAAIgJB//8DcUUNBgsgASACOwEEAkBBgBBFDQAg
AUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkAgAUGIEGogAUEEahD5goCAAEH//wNxDgMAAgECCyABKAKUECICIAEo
ApAQIgVLDQUgASgCjBAgAhCAgICAAAwGC0EADQVBACkD4PrJgAAiCadBOkEAQQAgCUIgiKcoAgARgICAgACAgICAACICRQ0FIAJCqtWq1arVqtWqfzcAACAC
QRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBOGpBqtV+OwAAIAJBMGpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEg
akKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjo3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASAJNwOIEAJAAkAgAUGIEGpBCGogAUEEahD5
goCAAEH//wNxDgMAAgECCyABKAKcECICIAEoApgQIgVLDQQgAUIANwOYECABKAKUECEGIAFBqtWq1Xo2ApQQIAEgBTYCuBAgASACNgK0ECABIAY2ArAQIAFB
oBBqIAFBsBBqIAFBiBBqELiAgIAAIAEgASgCuBAiAjYCmBAgASABKAKwECIFNgKUECABIAEoArQQNgKcEAJAIAJFDQAgASgCiBAhBiABKAKMECEEAkAgAkUN
ACAFQaoBIAL8CwALIAYgBSACQQBBACAEKAIMEYGAgIAAgICAgAALIAEvAagQDQYgASgCoBAiBSABKAKkECICEICAgIAAIAJFDQYCQCACRQ0AIAVBqgEgAvwL
AAsgASAFIAJBACABELmAgIAADAYLIAEoApgQIgJFDQUgASgCiBAhBiABKAKMECEEIAEoApQQIQUCQCACRQ0AIAVBqgEgAvwLAAsgBiAFIAJBAEEAIAQoAgwR
gYCAgACAgICAAAwFCwALIAYgCBCIgICAAAALIAJBAWogBhCIgICAAAALIAIgBRCIgICAAAALIAIgBRCIgICAAAALIAFBwBBqJICAgIAAC5wGAwJ/AX4CfyOA
gICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ
+oKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTdBAEEA
IANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQS9qQqrVqtWq
1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI3NwOYECABIAI2ApQQIAFB3JXHgAA2
ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ+oKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKU
ECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBAC
QCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAi
ABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUN
ACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC4QDAQR/IAAoAgwoAhghAgJAAkAgAUH/
/wNxIgNBB0sNACADRQ0BIAJBvgJqIgQgAi0AxgIiA0EHcWpBADoAACACIANBf2pBB3E6AMYCIAFB//8DcSIDQQFGDQEgBCACLQDGAiIFQQdxakEAOgAAIAIg
BUF/akEHcToAxgIgA0ECRg0BIAQgAi0AxgIiA0EHcWpBADoAACACIANBf2pBB3E6AMYCIAFB//8DcSIDQQNGDQEgBCACLQDGAiIFQQdxakEAOgAAIAIgBUF/
akEHcToAxgIgA0EERg0BIAQgAi0AxgIiA0EHcWpBADoAACACIANBf2pBB3E6AMYCIAFB//8DcSIBQQVGDQEgBCACLQDGAiIDQQdxakEAOgAAIAIgA0F/akEH
cToAxgIgAUEGRg0BIAQgAi0AxgIiAUEHcWpBADoAACACIAFBf2pBB3E6AMYCDAELIAJCADcAvgIgAkEAOgDGAgsCQCAAKAI8IgJFDQAgAEEMaiACEYOAgIAA
gICAgAALC5wGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBAC
QAJAAkACQAJAIAFBiBBqIAAQ+4KAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0A
QQApA+D6yYAAIgOnQTZBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arV
qtWqfzcAACACQS5qQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI2NwOY
ECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ+4KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAg
ASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAi
AjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGs
EA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgC
jBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC3MBAn8j
gICAgABBEGsiAiSAgICAACAAKAIMKAIYIQMgAiABQR9xIgE6AA8gAyADLQDGAkEHcWoiAyABIAMtAL4CckEfcToAvgICQCAAKAI8IgNFDQAgAEEMaiADEYOA
gIAAgICAgAALIAJBEGokgICAgAALdgECfyOAgICAAEEQayICJICAgIAAIAAoAgwoAhghAyACIAFBH3EiAToADyADIAMtAMYCQQdxaiIDIAFBf3MgAy0AvgJx
QR9xOgC+AgJAIAAoAjwiA0UNACAAQQxqIAMRg4CAgACAgICAAAsgAkEQaiSAgICAAAuVBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhq
QaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEPyCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACAB
KAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0EyQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq
1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEwakGq1X47AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq
1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIyNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ/IKAgABB//8D
cQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQ
aiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA
/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAA
QQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICA
gAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQ
IAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ+IKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAA
DAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcUAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHFAEUNACACQaoBQcUA/AsACyAB
QsUANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ+IKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcD
mBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgC
tBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyAB
LwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQg
ASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC60F
AwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJA
IAFBiBBqIAAQ9oKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAA
IgOnQcEAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHBAEUNACACQaoBQcEA/AsACyABQsEANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcD
iBACQAJAIAFBkBBqIAAQ9oKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEg
ADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQ
IQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0B
AkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwAL
IAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC68GAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0A
IAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ8YKAgABB//8DcQ4DAAQBBAsCQCABKAKU
ECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQT1BAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUN
ACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTVqQqrVqtWq1arVqn83AAAgAkEwakKq1arVqtWq1ap/
NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCPTcDmBAgASACNgKUECABQdyVx4AANgKQECAB
IAM3A4gQAkACQCABQYgQakEIaiAAEPGCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASAC
NgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUN
ACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICA
gAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGq
ASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuvBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAAC
QEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEPKCgIAAQf//A3EOAwAEAQQL
AkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E9QQBBACADQiCIpygCABGAgICAAICA
gIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkE1akKq1arVqtWq1ap/NwAAIAJBMGpCqtWq
1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQj03A5gQIAEgAjYClBAgAUHclceA
ADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDygoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2
ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKc
EAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKo
ECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALrwYDAn8BfgJ/I4CAgIAAQcAQayIB
JICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDzgoCAAEH//wNx
DgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBPUEAQQAgA0IgiKcoAgAR
gICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBNWpCqtWq1arVqtWqfzcAACAC
QTBqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI9NwOYECABIAI2ApQQ
IAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ84KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCAB
QarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASAB
KAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQ
IgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKU
ECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC68GAwJ/AX4CfyOAgICA
AEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ9IKA
gABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQT1BAEEAIANC
IIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTVqQqrVqtWq1arV
qn83AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCPTcDmBAg
ASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEPSCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEo
ApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2
ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBAN
ASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQ
IQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuxBgMCfwF+
An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQ
aiAAEPWCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HA
AEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBOGpC
qtWq1arVqtWqfzcAACACQTBqQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAg
AULAADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEPWCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiAB
QgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECAB
IAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKI
ECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAAL
AAvcJgIKfwN+I4CAgIAAQYATayICJICAgIAAIAIgACgCDCIDQRhqKAIAQZgBaikCADcDAAJAQQFFDQAgAEEMaiEEIAJBgAI2AhBBACEFIAJBADYCCCACQQE6
AJQCIAJBADYCrAIgAkKq1arVCjcCpAIgAkHclceAADYCoAIgAkGAuciAADYCnAIgAiACQRRqNgIMIAIgAjYCmAIgAkGgAmohBgJAAkACQAJAAkACQAJAIAFB
A3FBA0cNAAJAAkAgA0GQAWooAgAiAQ0AQQAhAwwBCyADQYwBaigCACIFIAFBf2oiA2otAAAiAQ0CC0EAIQEgA0EAIAUbIQcgBUGi3sGAACAFGyEIAkADQCAC
KAKsAiIJIAFBA3MiBWoiAyAJSQ0JIAFBkLnIgABqIQoCQAJAIAMgAigCqAIiC0sNACADIAtLDQYCQCACKAKkAiAJaiIDQZO5yIAATw0AIAogAyAFakkNDAsC
QCAFRQ0AIAMgCiAF/AoAAAsgAigCrAIiAyAFaiIJIANJDQsgAiAJNgKsAgwBCyACKAKgAigCACEDIAIgBTYCzBIgAiAKNgLIEiACQcgCaiAGIAJByBJqQQFB
ASADEYGAgIAAgICAgAAgAikDyAIiDEKAgICA8P8/g0IAUg0CIAynIQULIAEgBWoiBSABSQ0JIAUhASAFQQNJDQALQQAhASAGIAggByAHQQAtAMDYyYAAQSAQ
loCAgABB//8DcQ0AA0AgAigCrAIiCUECIAFrIgVqIgMgCUkNCSABQZO5yIAAaiEKAkACQCADIAIoAqgCIgtLDQAgAyALSw0HAkAgAigCpAIgCWoiA0GVuciA
AE8NACAKIAMgBWpJDQwLAkAgBUUNACADIAogBfwKAAALIAIoAqwCIgMgBWoiCSADSQ0LIAIgCTYCrAIMAQsgAigCoAIoAgAhAyACIAU2AswSIAIgCjYCyBIg
AkHIAmogBiACQcgSakEBQQEgAxGBgICAAICAgIAAIAIpA8gCIgxCgICAgPD/P4NCAFINAiAMpyEFCyABIAVqIgUgAUkNCSAFIQEgBUECTw0HDAALCyACKAKo
AiIBRQ0GIAIoApgCIQMgAigCnAIhCSACKAKkAiEFAkAgAUUNACAFQaoBIAH8CwALIAMgBSABQQBBACAJKAIMEYGAgIAAgICAgAAMBgsgACgCJCIFDQMMBQsg
ARCDgoCAAAALIAMgCxCIgICAAAALIAMgCxCIgICAAAALIAJBsAJqIAQgBRGEgICAAICAgIAAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAgAi0A
vAJFDQAgAUEDcQ4DAQIDAQsgAigCqAIiAUUNECACKAKYAiEDIAIoApwCIQkgAigCpAIhBQJAIAFFDQAgBUGqASAB/AsACyADIAUgAUEAQQAgCSgCDBGBgICA
AICAgIAADBALIAIzAboCIAI1ArACfiENIAIzAbgCIAI1ArQCfiEMQQAhAQNAIAIoAqwCIglBBCABayIFaiIDIAlJDREgAUHE1ciAAGohCgJAAkAgAyACKAKo
AiILSw0AIAMgC0sNBgJAIAIoAqQCIAlqIgNByNXIgABPDQAgCiADIAVqSQ0UCwJAIAVFDQAgAyAKIAX8CgAACyACKAKsAiIDIAVqIgkgA0kNEyACIAk2AqwC
DAELIAIoAqACKAIAIQMgAiAFNgLMEiACIAo2AsgSIAJByAJqIAYgAkHIEmpBAUEBIAMRgYCAgACAgICAACACKQPIAiIOQiCIpyIFQf//A3ENBCAOpyEFCyAB
IAVqIgUgAUkNESAFIQEgBUEESQ0ACyAGIAwQ74KAgAAiBUH//wNxDQIDQCACKAKsAiIBQQFqIgVFDRECQAJAIAUgAigCqAIiA0sNACABIANPDQcCQCACKAKk
AiABaiIBQcywyIAATw0AIAFBAWpBy7DIgABLDRQLIAFBOzoAACACKAKsAkEBaiIBRQ0TIAIgATYCrAIMAQsgAigCoAIoAgAhASACQQE2AswSIAJBy7DIgAA2
AsgSIAJByAJqIAYgAkHIEmpBAUEBIAERgYCAgACAgICAACACKQPIAiIMQiCIpyIFQf//A3ENBCAMp0UNAQsLIAYgDRDvgoCAACIFQf//A3ENAgNAIAIoAqwC
IgFBAWoiBUUNEQJAIAUgAigCqAIiA0sNACABIANPDQcCQCACKAKkAiABaiIBQcTVyIAATw0AIAFBAWpBw9XIgABLDRMLIAFB9AA6AAAgAigCrAJBAWoiAUUN
EiACIAE2AqwCDBALIAIoAqACKAIAIQEgAkEBNgLMEiACQcPVyIAANgLIEiACQcgCaiAGIAJByBJqQQFBASABEYGAgIAAgICAgAAgAikDyAIiDEIgiKciBUH/
/wNxDQMgDKcNDwwACwtBACEBIAIoArACIQggAigCtAIhBwNAIAIoAqwCIglBBCABayIFaiIDIAlJDRAgAUHI1ciAAGohCgJAAkAgAyACKAKoAiILSw0AIAMg
C0sNCAJAIAIoAqQCIAlqIgNBzNXIgABPDQAgCiADIAVqSQ0TCwJAIAVFDQAgAyAKIAX8CgAACyACKAKsAiIDIAVqIgkgA0kNEiACIAk2AqwCDAELIAIoAqAC
KAIAIQMgAiAFNgLMEiACIAo2AsgSIAJByAJqIAYgAkHIEmpBAUEBIAMRgYCAgACAgICAACACKQPIAiIMQiCIpyIFQf//A3ENAyAMpyEFCyABIAVqIgUgAUkN
ECAFIQEgBUEESQ0ACyAGIAdBCkEAQbDYyYAAEJWAgIAAIgVB//8DcQ0BA0AgAigCrAIiAUEBaiIFRQ0QAkACQCAFIAIoAqgCIgNLDQAgASADTw0JAkAgAigC
pAIgAWoiAUHMsMiAAE8NACABQQFqQcuwyIAASw0TCyABQTs6AAAgAigCrAJBAWoiAUUNEiACIAE2AqwCDAELIAIoAqACKAIAIQEgAkEBNgLMEiACQcuwyIAA
NgLIEiACQcgCaiAGIAJByBJqQQFBASABEYGAgIAAgICAgAAgAikDyAIiDEIgiKciBUH//wNxDQMgDKdFDQELCyAGIAhBCkEAQbDYyYAAEJWAgIAAIgVB//8D
cQ0BA0AgAigCrAIiAUEBaiIFRQ0QAkAgBSACKAKoAiIDSw0AIAEgA08NCQJAIAIoAqQCIAFqIgFBxNXIgABPDQAgAUEBakHD1ciAAEsNEgsgAUH0ADoAACAC
KAKsAkEBaiIBRQ0RIAIgATYCrAIMDwsgAigCoAIoAgAhASACQQE2AswSIAJBw9XIgAA2AsgSIAJByAJqIAYgAkHIEmpBAUEBIAERgYCAgACAgICAACACKQPI
AiIMQiCIpyIFQf//A3ENAiAMpw0ODAALC0EAIQEgAi8BugIhCCACLwG4AiEHA0AgAigCrAIiCUEEIAFrIgVqIgMgCUkNDyABQczVyIAAaiEKAkACQCADIAIo
AqgCIgtLDQAgAyALSw0KAkAgAigCpAIgCWoiA0HQ1ciAAE8NACAKIAMgBWpJDRILAkAgBUUNACADIAogBfwKAAALIAIoAqwCIgMgBWoiCSADSQ0RIAIgCTYC
rAIMAQsgAigCoAIoAgAhAyACIAU2AswSIAIgCjYCyBIgAkHIAmogBiACQcgSakEBQQEgAxGBgICAAICAgIAAIAIpA8gCIgxCIIinIgVB//8DcQ0CIAynIQUL
IAEgBWoiBSABSQ0PIAUhASAFQQRJDQALIAYgB0EKQQBBsNjJgAAQlYCAgAAiBUH//wNxDQADQCACKAKsAiIBQQFqIgVFDQ8CQAJAIAUgAigCqAIiA0sNACAB
IANPDQsCQCACKAKkAiABaiIBQcywyIAATw0AIAFBAWpBy7DIgABLDRILIAFBOzoAACACKAKsAkEBaiIBRQ0RIAIgATYCrAIMAQsgAigCoAIoAgAhASACQQE2
AswSIAJBy7DIgAA2AsgSIAJByAJqIAYgAkHIEmpBAUEBIAERgYCAgACAgICAACACKQPIAiIMQiCIpyIFQf//A3ENAiAMp0UNAQsLIAYgCEEKQQBBsNjJgAAQ
lYCAgAAiBUH//wNxDQADQCACKAKsAiIBQQFqIgVFDQ8CQCAFIAIoAqgCIgNLDQAgASADTw0LAkAgAigCpAIgAWoiAUHE1ciAAE8NACABQQFqQcPVyIAASw0R
CyABQfQAOgAAIAIoAqwCQQFqIgFFDRAgAiABNgKsAgwOCyACKAKgAigCACEBIAJBATYCzBIgAkHD1ciAADYCyBIgAkHIAmogBiACQcgSakEBQQEgARGBgICA
AICAgIAAIAIpA8gCIgxCIIinIgVB//8DcQ0BIAynDQ0MAAsLIAIgBTsBwgICQEGAEEUNACACQcgCakGqAUGAEPwLAAsgAkKAEDcC0BIgAkG43sGAADYCyBIg
AiACQcgCajYCzBICQAJAAkAgAkHIEmogAkHCAmoQ8IKAgABB//8DcQ4DABABEAsgAigC1BIiASACKALQEiIFSw0MIAIoAswSIAEQgICAgAAMAQtBAA0AQQAp
A+D6yYAAIgynQTtBAEEAIAxCIIinKAIAEYCAgIAAgICAgAAiAUUNACABQqrVqtWq1arVqn83AAAgAUEQakKq1arVqtWq1ap/NwAAIAFBCGpCqtWq1arVqtWq
fzcAACABQTdqQarVqtV6NgAAIAFBMGpCqtWq1arVqtWqfzcAACABQShqQqrVqtWq1arVqn83AAAgAUEgakKq1arVqtWq1ap/NwAAIAFBGGpCqtWq1arVqtWq
fzcAACACQjs3A9gSIAIgATYC1BIgAkHclceAADYC0BIgAiAMNwPIEgJAAkAgAkHIEmpBCGogAkHCAmoQ8IKAgABB//8DcQ4DABABEAsgAigC3BIiASACKALY
EiIFSw0LIAJCADcD2BIgAigC1BIhAyACQarVqtV6NgLUEiACIAU2AvgSIAIgATYC9BIgAiADNgLwEiACQeASaiACQfASaiACQcgSahC4gICAACACIAIoAvgS
IgE2AtgSIAIgAigC8BIiBTYC1BIgAiACKAL0EjYC3BICQCABRQ0AIAIoAsgSIQMgAigCzBIhCQJAIAFFDQAgBUGqASAB/AsACyADIAUgAUEAQQAgCSgCDBGB
gICAAICAgIAACyACLwHoEg0BIAIoAuASIgUgAigC5BIiARCAgICAACABRQ0BAkAgAUUNACAFQaoBIAH8CwALIAIgBSABQQAgAhC5gICAAAwBCyACKALYEiIB
RQ0AIAIoAsgSIQMgAigCzBIhCSACKALUEiEFAkAgAUUNACAFQaoBIAH8CwALIAMgBSABQQBBACAJKAIMEYGAgIAAgICAgAALIAIoAqgCIgFFDQwgAigCmAIh
AyACKAKcAiEJIAIoAqQCIQUCQCABRQ0AIAVBqgEgAfwLAAsgAyAFIAFBAEEAIAkoAgwRgYCAgACAgICAAAwMCyADIAsQiICAgAAACyABQQFqIAMQiICAgAAA
CyABQQFqIAMQiICAgAAACyADIAsQiICAgAAACyABQQFqIAMQiICAgAAACyABQQFqIAMQiICAgAAACyADIAsQiICAgAAACyABQQFqIAMQiICAgAAACyABQQFq
IAMQiICAgAAACyABIAUQiICAgAAACyABIAUQiICAgAAACyACIAIpA5gCIgw3A/ASAkACQAJAIAIoAqwCIgEgAigCqAIiBUsNACACQgA3A6gCIAIoAqQCIQMg
AkGq1arVejYCpAIgAiAFNgLQEiACIAM2AsgSIAIgATYCzBIgAUEBaiIBRQ0EAkACQCACQcgSaiAMIAEQgoKAgABB//8DcQ0AIAIoAswSIgEgAigC0BJPDQYg
AigCyBIgAWpBADoAACACIAFBAWo2AswSIAJByAJqIAJByBJqIAJB8BJqELiAgIAAIAIvAdACDQAgAigCzAIiAUF/aiIFIAFLDQYgBSABTw0DIAIoAsgCIgEg
BWotAAAiA0UNASADEIOCgIAAAAsgAiACKALQEiIBNgKoAiACIAIoAsgSIgU2AqQCIAIgAigCzBI2AqwCIAFFDQQgAigCmAIhAyACKAKcAiEJAkAgAUUNACAF
QaoBIAH8CwALIAMgBSABQQBBACAJKAIMEYGAgIAAgICAgAAMBAsgAiACKALIEjYCpAIgAiACKQLMEkIgiTcDqAIgACgCECIDRQ0CIAQgASAFIAMRhYCAgACA
gICAAAwCCyABIAUQiICAgAAACyAFQQFqIAEQiICAgAAACwJAIAVBAWoiBUUNACABQaoBIAX8CwALAkACQCABIAIoAgwiA0kNACADIAIoAhBqIgkgA0kNAyAJ
IAFNDQAgASAFaiIKIAFJDQMgCiAJSw0DIAogAyACKAIIIgFqRw0BIAEgBWsiBSABSw0DIAIgBTYCCAwBCyACKAIAIAEgBUEAQQAgAigCBCgCDBGBgICAAICA
gIAACyACKAKoAiIBRQ0AIAIoApgCIQMgAigCnAIhCSACKAKkAiEFAkAgAUUNACAFQaoBIAH8CwALIAMgBSABQQBBACAJKAIMEYGAgIAAgICAgAALIAJBgBNq
JICAgIAADwsAC5wGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYC
jBACQAJAAkACQAJAIAFBiBBqIAAQ94KAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtB
AA0AQQApA+D6yYAAIgOnQTVBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq
1arVqtWqfzcAACACQS1qQqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI1
NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ94KAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcD
mBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgC
tBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyAB
LwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQg
ASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC/UI
AgF/An4CQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCABQf//
A3EiAkHnB0oNACACQX9qDkUBJQIDBAUGBwglJQklJSUlJSUlJSUlJSUKJSUlJSUlJSUlJSUlJSULJSUlJQwlDSUlJSUlJSUlJSUlJSUlJSUlJQ4lJQ8lCwJA
IAJBmHhqDjIQJRESExQVFiUlJSUlJSUXGCUlJSUlJSUlJSUlJSUlJSUlJRkaJSUbJSUlJSUcJR0eHwALAkAgAkGscGoOLSAlJSUlJSUlJSUlJSUlJSUlJSUl
JSUhIiUlJSMlJSUlJSUlJSUlJSUlJSUlJAALQgEhA0L+/////x8hBAJAAkAgAkH+/31qDhMoJicmJiYmJiYmACYmJiYmJiYBJgtCBCEDQvv/////HyEEDCcL
QgghA0L3/////x8hBAwmC0IQIQNC7/////8fIQQMJQtCICEDQt//////HyEEDCQLQsAAIQNCv/////8fIQQMIwtCgAEhA0L//v///x8hBAwiC0KAAiEDQv/9
////HyEEDCELQoAEIQNC//v///8fIQQMIAtCgAghA0L/9////x8hBAwfC0KAECEDQv/v////HyEEDB4LQoAgIQNC/9////8fIQQMHQtCgMAAIQNC/7////8f
IQQMHAtCgIABIQNC///+//8fIQQMGwtCgIACIQNC///9//8fIQQMGgtCgIAEIQNC///7//8fIQQMGQtCgIAIIQNC///3//8fIQQMGAtCgIAQIQNC///v//8f
IQQMFwtCgIAgIQNC///f//8fIQQMFgtCgIDAACEDQv//v///HyEEDBULQoCAgAEhA0L////+/x8hBAwUC0KAgIACIQNC/////f8fIQQMEwtCgICABCEDQv//
//v/HyEEDBILQoCAgAghA0L////3/x8hBAwRC0KAgIAQIQNC////7/8fIQQMEAtCgICAICEDQv///9//HyEEDA8LQoCAgMAAIQNC////v/8fIQQMDgtCgICA
gAEhA0L//////h8hBAwNC0KAgICAAiEDQv/////9HyEEDAwLQoCAgIAEIQNC//////sfIQQMCwtCgICAgAghA0L/////9x8hBAwKC0KAgICAECEDQv/////v
HyEEDAkLQoCAgIAgIQNC/////98fIQQMCAtCgICAgMAAIQNC/////78fIQQMBwtCgICAgIABIQNC//////8eIQQMBgtCgICAgIACIQNC//////8dIQQMBQtC
gICAgIAEIQNC//////8bIQQMBAtCgICAgIAIIQNC//////8XIQQMAwtCgICAgIAQIQNC//////8PIQQMAgsgARDRgoCAAEEBcRoAC0ICIQNC/f////8fIQQL
IAAgADUCCCAAQQxqIgIxAABCIIaEIASDIAA1AgAgAEEEajEAAEIghoQgA4OEIgM+AgggAiADQiCIPAAAC4QGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJA
QYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ7YKAgABB//8DcQ4DAAQBBAsC
QCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQSxBAEEAIANCIIinKAIAEYCAgIAAgICA
gAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQarVqtV6NgAAIAJBIGpCqtWq1arVqtWq
fzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIsNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ7YKAgABB//8DcQ4DAAQB
BAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQ
ahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAE
IAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5
gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFB
wBBqJICAgIAADwsgACACEIiAgIAAAAsAC5cGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7B
gAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ7oKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEo
AowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTNBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq
1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQS9qQarVqtV6NgAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq
1ap/NwAAIAFCMzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEO6CgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAi
AksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIA
NgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCA
gACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUN
ACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCI
gICAAAALAAv4AQECfwJAAkAgAC0AAkEEcUUNACAALwH6ESIBQQJJDQEgAEEAOwGAEiAAIAFBf2o7AYISIABBAUEBEMGAgIAADwsgACgCGCIBQQE6AIwCIAEg
ACgCGC8B0AE7AfwBIAEgACgCGC8B0gE7Af4BIAEgACgCGCICKQKgATcC4AEgAUH4AWogAkG4AWooAgA2AgAgAUHwAWogAkGwAWopAgA3AgAgAUHoAWogAkGo
AWopAgA3AgAgASAAKAIYLQDaAUEBcToAgAIgASAAKAIYLQDZAUEBcToAgQIgASAAKAIAQQh2QQFxOgCCAiABIAAoAhgpALUCNwCDAgsLiQYDAn8BfgJ/I4CA
gIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDs
goCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBLUEAQQAg
A0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBJWpCqtWq1arV
qtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLTcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQ
akEIaiAAEOyCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEg
BDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQ
IQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAg
AkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEA
QQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAtoAQF/AkAgAC0AAkEEcUUNACABQf//A3EiAUEBIAFBAUsbIgMgAC8B+hEi
ASACQf//A3EiAiABIAJJGyABIAIbIgJPDQAgACACQX9qOwGCEiAAIANBf2o7AYASIABBAUEBEMGAgIAACwucBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAAC
QEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEOWCgIAAQf//A3EOAwAEAQQL
AkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E4QQBBACADQiCIpygCABGAgICAAICA
gIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq
1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCODcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCAB
QYgQakEIaiAAEOWCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQ
IAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEo
AowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABF
DQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIg
AEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAt8AgF+An9CACECQQEhAwJAAkACQAJAQQEgAUEHcXQiBEHVAHENACAE
QSpxRQ0BQoAgIQILAkAgAUEHcQ4IAwMDAAACAgEDC0ECIQMMAgsAC0EAIQMLIAAoAgwiASABNQIAQv/f//8PgyAChD4CACAAKAIMKAIYIAM6ANgBC5UHAgp/
AX4jgICAgABB0AJrIgEkgICAgAAgAEEMaiECAkACQCAAKAJAIgMNAEEAIQNBot7BgAAhBAwBCyABQQhqIAIgAxGEgICAAICAgIAAIAEoAgwhAyABKAIIIQQL
AkBBoAJFDQAgAUEQakGqAUGgAvwLAAsgAUKgAjcCuAIgAUG43sGAADYCsAIgBEGzgMCAACADGyEFIANBCiADGyEGIAEgAUEQajYCtAJBACEDAkACQAJAAkAC
QAJAAkADQCABKAK8AiIHQQQgA2siBGoiCCAHSQ0HIANBzuXIgABqIQkCQAJAIAggASgCuAIiCksNACAIIApLDQYCQCABKAK0AiAHaiIIQdLlyIAATw0AIAkg
CCAEakkNCgsCQCAERQ0AIAggCSAE/AoAAAsgASgCvAIiCCAEaiIHIAhJDQkgASAHNgK8AgwBCyABKAKwAigCACEIIAEgBDYCxAIgASAJNgLAAiABQcgCaiAB
QbACaiABQcACakEBQQEgCBGBgICAAICAgIAAIAEpA8gCIgtCIIinIgRB//8DcQ0CIAunIQQLIAMgBGoiBCADSQ0HIAQhAyAEQQRJDQALQQAhAyABQbACaiAF
IAYgBkEALQDA2MmAAEEgEJaAgIAAIgRB//8DcQ0AA0AgASgCvAIiByADQQNzIgRqIgggB0kNByADQfShwIAAaiEJAkACQCAIIAEoArgCIgpLDQAgCCAKSw0H
AkAgASgCtAIgB2oiCEH3ocCAAE8NACAJIAggBGpJDQoLAkAgBEUNACAIIAkgBPwKAAALIAEoArwCIgggBGoiByAISQ0JIAEgBzYCvAIMAQsgASgCsAIoAgAh
CCABIAQ2AsQCIAEgCTYCwAIgAUHIAmogAUGwAmogAUHAAmpBAUEBIAgRgYCAgACAgICAACABKQPIAiILQiCIpyIEQf//A3ENAiALpyEECyADIARqIgQgA0kN
ByAEIQMgBEEDSQ0ACyABKAK8AiIDIAEoArgCIgRNDQEgAyAEEIiAgIAAAAsgBEH//wNxQQJGDQEMBQsgA0F/aiIEIANLDQQgBCADTw0DAkAgASgCtAIiAyAE
ai0AACIIRQ0AIAgQg4KAgAAACyAAKAIQIghFDQAgAiADIAQgCBGFgICAAICAgIAACyABQdACaiSAgICAAA8LIAggChCIgICAAAALIAggChCIgICAAAALIARB
AWogAxCIgICAAAALAAuJEAIIfwF+I4CAgIAAQcAQayICJICAgIAAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCABQf//A3EiAUF7ag4CAgEACyABQeSH
AkYNCwwMCyAAKAIIIgEoAhgiAy8B0AEhBCABLQABQQFxRQ0BQQAgAy8B0gEiAyABLwH8EWsiBSAFIANLGyEDQQAgBEH//wNxIgQgAS8BgBJrIgEgASAESxsh
BAwCCyAAQeOSwIAAQQQQ4ICAgAAhAQwCCyADLwHSASEDCyACQcAAakKq1arVqtWq1ap/NwMAIAJBOGpCqtWq1arVqtWqfzcDACACQTBqQqrVqtWq1arVqn83
AwAgAkEoakKq1arVqtWq1ap/NwMAIAJBIGpCqtWq1arVqtWqfzcDACACQRhqQqrVqtWq1arVqn83AwAgAkEQakKq1arVqtWq1ap/NwMAIAJCqtWq1arVqtWq
fzcDCCACQsAANwKQECACQbjewYAANgKIECADQf//A3FBAWohBiAEQf//A3FBAWohByACIAJBCGo2AowQQQAhAQJAAkADQCACKAKUECIFQQIgAWsiBGoiAyAF
SQ0LIAFBybDIgABqIQgCQAJAIAMgAigCkBAiCUsNACADIAlLDQcCQCACKAKMECAFaiIDQcuwyIAATw0AIAggAyAEakkNDgsCQCAERQ0AIAMgCCAE/AoAAAsg
AigClBAiAyAEaiIFIANJDQ0gAiAFNgKUEAwBCyACKAKIECgCACEDIAIgBDYCpBAgAiAINgKgECACQbAQaiACQYgQaiACQaAQakEBQQEgAxGBgICAAICAgIAA
IAIpA7AQIgpCIIinIgRB//8DcQ0CIAqnIQQLIAEgBGoiBCABSQ0LIAQhASAEQQJJDQALIAJBiBBqIAZBCkEAQbDYyYAAEJWAgIAAIgRB//8DcQ0AA0AgAigC
lBAiAUEBaiIERQ0LAkACQCAEIAIoApAQIgNLDQAgASADTw0IAkAgAigCjBAgAWoiAUHMsMiAAE8NACABQQFqQcuwyIAASw0OCyABQTs6AAAgAigClBBBAWoi
AUUNDSACIAE2ApQQDAELIAIoAogQKAIAIQEgAkEBNgKkECACQcuwyIAANgKgECACQbAQaiACQYgQaiACQaAQakEBQQEgARGBgICAAICAgIAAIAIpA7AQIgpC
IIinIgRB//8DcQ0CIAqnRQ0BCwsgAkGIEGogB0EKQQBBsNjJgAAQlYCAgAAiBEH//wNxDQACQANAIAIoApQQIgFBAWoiBEUNDAJAIAQgAigCkBAiA0sNACAB
IANPDQkCQCACKAKMECABaiIBQc2wyIAATw0AIAFBAWpBzLDIgABLDQ4LIAFB0gA6AAAgAigClBBBAWoiAUUNDSACIAE2ApQQDAILIAIoAogQKAIAIQEgAkEB
NgKkECACQcywyIAANgKgECACQbAQaiACQYgQaiACQaAQakEBQQEgARGBgICAAICAgIAAIAIpA7AQIgpCIIinIgRB//8DcQ0CIAqnRQ0ACyACKAKUECEBCyAB
IAIoApAQIgRNDQEgASAEEIiAgIAAAAsgBEH//wNxQQJHDQlBJSEBDAILIAAgAigCjBAgARDggICAACEBCyABQf//A3FFDQYLIAIgATsBBgJAQYAQRQ0AIAJB
CGpBqgFBgBD8CwALIAJCgBA3ApAQIAJBuN7BgAA2AogQIAIgAkEIajYCjBACQAJAIAJBiBBqIAJBBmoQ3IKAgABB//8DcQ4DAAgBCAsgAigClBAiASACKAKQ
ECIESw0FIAIoAowQIAEQgICAgAAMBgtBAA0FQQApA+D6yYAAIgqnQTpBAEEAIApCIIinKAIAEYCAgIAAgICAgAAiAUUNBSABQqrVqtWq1arVqn83AAAgAUEQ
akKq1arVqtWq1ap/NwAAIAFBCGpCqtWq1arVqtWqfzcAACABQThqQarVfjsAACABQTBqQqrVqtWq1arVqn83AAAgAUEoakKq1arVqtWq1ap/NwAAIAFBIGpC
qtWq1arVqtWqfzcAACABQRhqQqrVqtWq1arVqn83AAAgAkI6NwOYECACIAE2ApQQIAJB3JXHgAA2ApAQIAIgCjcDiBACQAJAIAJBiBBqQQhqIAJBBmoQ3IKA
gABB//8DcQ4DAAgBCAsgAigCnBAiASACKAKYECIESw0EIAJCADcDmBAgAigClBAhAyACQarVqtV6NgKUECACIAQ2ArgQIAIgATYCtBAgAiADNgKwECACQaAQ
aiACQbAQaiACQYgQahC4gICAACACIAIoArgQIgE2ApgQIAIgAigCsBAiBDYClBAgAiACKAK0EDYCnBACQCABRQ0AIAIoAogQIQMgAigCjBAhBQJAIAFFDQAg
BEGqASAB/AsACyADIAQgAUEAQQAgBSgCDBGBgICAAICAgIAACyACLwGoEA0GIAIoAqAQIgQgAigCpBAiARCAgICAACABRQ0GAkAgAUUNACAEQaoBIAH8CwAL
IAIgBCABQQAgAhC5gICAAAwGCyACKAKYECIBRQ0FIAIoAogQIQMgAigCjBAhBSACKAKUECEEAkAgAUUNACAEQaoBIAH8CwALIAMgBCABQQBBACAFKAIMEYGA
gIAAgICAgAAMBQsgAyAJEIiAgIAAAAsgAUEBaiADEIiAgIAAAAsgAUEBaiADEIiAgIAAAAsgASAEEIiAgIAAAAsgASAEEIiAgIAAAAsgAkHAEGokgICAgAAP
CwAL9gUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkAC
QAJAAkAgAUGIEGogABDSgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD
4PrJgAAiA6dBJ0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/
NwAAIAJBH2pCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUInNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhq
IAAQ0oKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0
ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJA
IABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoB
IAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC4EGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8
CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQyoKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJN
DQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQSlBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arV
qn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQap/OgAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83
AAAgAUIpNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQyoKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0C
IAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQ
IAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICA
gIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEo
AogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAA
AAsAC2QBAn8CQCAAKAIYIgIvAdIBQQFqIgNB//8DcSADRw0AIAIvAdABQQFqIgJB//8DcSACRw0AIAAgA0H//wNxIAFB//8DcWoiA0H//wMgA0H//wNJGyAC
Qf//A3EQwYCAgAAPCwALgQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASAB
QQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDIgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICA
AAwBC0EADQBBACkD4PrJgAAiA6dBKUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEI
akKq1arVqtWq1ap/NwAAIAJBKGpBqn86AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQik3A5gQIAEgAjYClBAgAUHclceAADYC
kBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDIgoCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQ
IAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJA
IABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIA
EICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALlQYDAn8BfgJ/I4CAgIAAQcAQayIBJICA
gIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDEgoCAAEH//wNxDgMA
BAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBMkEAQQAgA0IgiKcoAgARgICA
gACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBMGpBqtV+OwAAIAJBKGpCqtWq1arV
qtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCMjcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQ
akEIaiAAEMSCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEg
BDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQ
IQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAg
AkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEA
QQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAubBgIDfwF+I4CAgIAAQcAQayICJICAgIAAIAIgAEHqEWotAAA6AAogAiAA
LwHoETsBCAJAAkACQCAALQDsEUUNACABQQEgAUEBSxshASACLwEIIAItAApBEHRyIQMCQANAIAAgAxD3gICAACIEQf//A3ENASABQX9qIgFFDQIMAAsLIAIg
BDsBBgJAQYAQRQ0AIAJBCGpBqgFBgBD8CwALIAJCgBA3ApAQIAJBuN7BgAA2AogQIAIgAkEIajYCjBACQAJAIAJBiBBqIAJBBmoQxYKAgABB//8DcQ4DAAQB
BAsCQCACKAKUECIBIAIoApAQIgBNDQAgASAAEIiAgIAADAQLIAIoAowQIAEQgICAgAAMAQtBAA0AQQApA+D6yYAAIgWnQcMAQQBBACAFQiCIpygCABGAgICA
AICAgIAAIgFFDQACQEHDAEUNACABQaoBQcMA/AsACyACQsMANwOYECACIAE2ApQQIAJB3JXHgAA2ApAQIAIgBTcDiBACQAJAIAJBkBBqIAJBBmoQxYKAgABB
//8DcQ4DAAQBBAsgAigCnBAiASACKAKYECIASw0CIAJCADcDmBAgAigClBAhAyACQarVqtV6NgKUECACIAA2ArwQIAIgATYCuBAgAiADNgK0ECACQaQQaiAC
QbQQaiACQYgQahC4gICAACACIAIoArwQIgE2ApgQIAIgAigCtBAiADYClBAgAiACKAK4EDYCnBACQCABRQ0AIAIoAogQIQMgAigCjBAhBAJAIAFFDQAgAEGq
ASAB/AsACyADIAAgAUEAQQAgBCgCDBGBgICAAICAgIAACyACLwGsEA0BIAIoAqQQIgAgAigCqBAiARCAgICAACABRQ0BAkAgAUUNACAAQaoBIAH8CwALIAEg
ACABQQAgARC5gICAAAwBCyACKAKYECIBRQ0AIAIoAogQIQMgAigCjBAhBCACKAKUECEAAkAgAUUNACAAQaoBIAH8CwALIAMgACABQQBBACAEKAIMEYGAgIAA
gICAgAALIAJBwBBqJICAgIAADwsgASAAEIiAgIAAAAsAC4EGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3
ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQwoKAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiA
gIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQSlBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQ
akKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQShqQap/OgAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIpNwOY
ECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQwoKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAg
ASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAi
AjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGs
EA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgC
jBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC2QBAn8C
QCAAKAIYIgIvAdIBQQFqIgNB//8DcSADRw0AIAIvAdABQQFqIgJB//8DcSACRw0AIAAgA0H//wNxIAJB//8DcSABQf//A3FqIgNB//8DIANB//8DSRsQwYCA
gAAPCwALpwYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJA
AkACQAJAAkAgAUGIEGogABDAgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBB
ACkD4PrJgAAiA6dBOUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq
1ap/NwAAIAJBOGpBqn86AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/
NwAAIAFCOTcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEMCCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksN
AiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKY
ECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACA
gICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACAB
KAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICA
AAALAAvMAgEJfwJAAkAgAUH//wNxIgJFDQAgACgCDCIDKAIYIgEvAdABIQRBACEFA0BBACEGAkAgAy0AAUEBcUUNACADLwGAEiEGCyADQThqIQcgBkH//wNx
IQggBCEGAkADQCAGQf//A3EgCE0NASABIAEoAswBQXhqNgLMASABKALEASIJLwEGQX9qIgZB//8DcSAGRw0EIAkgBjsBBiABLwHQAUF/aiIGQf//A3EgBkcN
BCABIAY7AdABIAMoAhgiAS8B0AEiBkEDdiEJIAZBB3FB+LjIgABqLQAAIQoCQAJAIAZB/wNLDQAgByAJaiEJDAELIAlBQGoiCSADKAI0Tw0FIAMoAjAgCWoh
CQsgCkH/AXEiCiAJLQAAcSAKRw0ACwsgBEH//wNxIAAoAgwiAygCGCIBLwHQASIERg0BIAVBAWoiBSACRw0ACwsPCwALnAYDAn8BfgJ/I4CAgIAAQcAQayIB
JICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABC+goCAAEH//wNx
DgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBNkEAQQAgA0IgiKcoAgAR
gICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBLmpCqtWq1arVqtWqfzcAACAC
QShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjY3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOI
EAJAAkAgAUGIEGpBCGogABC+goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAg
ASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgC
iBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABF
DQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwL
AAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALqAQBCX8CQAJAIAAvAfoRIAAoAhgiAi8B0AEiA2siBEH/
/wNxIgUgBEcNAAJAIAUgBSABQQEgAUEBSxsiASAFIAFJGyIBRg0AIAFB//8DcUF/aiIFQf//A3EiBCAFRw0BIAMgBGoiBEH//wNxIARHDQEgBEH//wNxIAIv
AYwBTw0BIAEhBCACKALMASAFQf//A3FBA3RqKQMAQoCAgICAgAODQoCAgICAgAFSDQAgAUH//wNxQQFqIgRB//8DcSAERw0BCyACIAMQrIKAgAAgACgCGCIC
LwHQASAEQf//A3EiAWoiBUH//wNxIAVHDQAgAiAFEKyCgIAAIAAoAhgQtIKAgAAgACgCGCgCyAEiBSAFKQMAQoCAgICAIIQ3AwAgACgCGCIGKALEASgCAEEQ
aiEHIAYoAsgBIQggBigCzAEhCSAGLQC9AkEDcUEBRg0BIAYgByAIIAkgARCzgoCAAA8LAAsCQAJAIARB//8DcUUNACAJQQhqIQpBACECA0AgAiABRg0CAkAg
CSACQQN0IgBqIgMtAAVBEHENACAKIABqIQAgAiEEAkADQAJAIARBAWoiBCABSQ0AIAEhBAwCCyAALQAFIQUgAEEIaiEAIAVBEHFFDQALCwJAIAQgAkkNACAG
IAcgCCADIAQgAmsQs4KAgAAgBCECIAQgAU8NAwwCCyACIAQQioCAgAAACyACQQFqIgIgAUkNAAsLDwsgASABEIiAgIAAAAuUBgMCfwF+An8jgICAgABBwBBr
IgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAELqCgIAAQf//
A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0ExQQBBACADQiCIpygC
ABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEwakGqfzoAACACQShqQqrV
qtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjE3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAg
AUGIEGpBCGogABC6goCAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4
ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCAB
KAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAA
RQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCAC
IABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALXAEEfyAAKAIYIgItANkBIQMgAi8B0gEhBCACLwHQASEFIAIgAC8B
gBIgAC8B/BEQ1YCAgAAgACABENaAgIAAIAAoAhggBSAEENWAgIAAIAAoAhggA0EBcToA2QELiQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAg
AUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABC2goCAAEH//wNxDgMABAEECwJAIAEoApQQ
IgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBL0EAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0A
IAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBJ2pCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83
AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCLzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAELaCgIAAQf//A3EOAwAEAQQL
IAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQ
uICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCAC
IABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCA
gAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQ
aiSAgICAAA8LIAAgAhCIgICAAAALAAvOBQMBfwF+An8jgICAgABBwBBrIgIkgICAgAACQAJAAkAgACABELeCgIAAIgFB//8DcUUNACACIAE7AQYCQEGAEEUN
ACACQQhqQaoBQYAQ/AsACyACQoAQNwKQECACQbjewYAANgKIECACIAJBCGo2AowQAkACQCACQYgQaiACQQZqELiCgIAAQf//A3EOAwAEAQQLAkAgAigClBAi
ASACKAKQECIATQ0AIAEgABCIgICAAAwECyACKAKMECABEICAgIAADAELQQANAEEAKQPg+smAACIDp0HDAEEAQQAgA0IgiKcoAgARgICAgACAgICAACIBRQ0A
AkBBwwBFDQAgAUGqAUHDAPwLAAsgAkLDADcDmBAgAiABNgKUECACQdyVx4AANgKQECACIAM3A4gQAkACQCACQZAQaiACQQZqELiCgIAAQf//A3EOAwAEAQQL
IAIoApwQIgEgAigCmBAiAEsNAiACQgA3A5gQIAIoApQQIQQgAkGq1arVejYClBAgAiAANgK8ECACIAE2ArgQIAIgBDYCtBAgAkGkEGogAkG0EGogAkGIEGoQ
uICAgAAgAiACKAK8ECIBNgKYECACIAIoArQQIgA2ApQQIAIgAigCuBA2ApwQAkAgAUUNACACKAKIECEEIAIoAowQIQUCQCABRQ0AIABBqgEgAfwLAAsgBCAA
IAFBAEEAIAUoAgwRgYCAgACAgICAAAsgAi8BrBANASACKAKkECIAIAIoAqgQIgEQgICAgAAgAUUNAQJAIAFFDQAgAEGqASAB/AsACyACIAAgAUEAIAIQuYCA
gAAMAQsgAigCmBAiAUUNACACKAKIECEEIAIoAowQIQUgAigClBAhAAJAIAFFDQAgAEGqASAB/AsACyAEIAAgAUEAQQAgBSgCDBGBgICAAICAgIAACyACQcAQ
aiSAgICAAA8LIAEgABCIgICAAAALAAucBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAA
NgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEKuCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKM
ECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E3QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWq
fzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEvakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arV
qtWq1ap/NwAAIAFCNzcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEKuCgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgC
mBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8
ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwR
gYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAi
AEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAg
AhCIgICAAAALAAvaCgMKfwF+A38jgICAgABBgAFrIgIkgICAgAACQAJAIAFFDQAgACgCGCIDLwHQASIEIAAvAYASSQ0AIAAvAYISIgUgBEkNACAFIARrQf//
A3FBAWoiBkH//wNxIgcgBkcNASADKALEASgCACEIIAMoAswBIQUgAyAEEKyCgIAAIAAoAhgiAy8B0AEgASAHIAEgB0kbIgRqIgFB//8DcSABRw0BIAMgARCs
goCAACAALwGCEkEBaiIBQf//A3EgAUcNASAAKAIYIAEQrIKAgAAgBkH//wNxIARB//8DcWsiAUH//wNxIAFHDQEgCEEQaiEJAkACQCABQf//A3EiCg0AIAUh
AQwBCyAEQQN0IQMgBSABQX9qQf//A3FBA3RqIQsgBSEBA0AgASkDAEIDgyEMAkACQAJAAkAgASADaiIEKQMAQgODQgFRDQAgDEIBUQ0BDAMLIAMgBWoiDSAJ
KAIAIgdrIg4gDUshDSAMQgFRDQEgDQ0GIAUgB2siDyAFSw0GIAcgCCgCOGoiDSAHSQ0GIA1FDQYgAiANNgIEIAJBCGogDSAOEK2CgIAAIAItABBFDQYgAigC
DCkCACEMIAJBBGogAigCCBCugoCAACANIA8gDBCvgoCAAAwCCyAFIAkoAgAiDWsiDiAFSw0FIAMgBWoiByANayIPIAdLDQUgDSAIKAI4aiIHIA1JDQUgB0UN
BSACIAc2AhQgAkEYaiAHIA4QrYKAgAAgAi0AIEUNBSACKAIcKQIAIQwgAkEUaiACKAIYEK6CgIAAIAcgDyAMEK+CgIAADAELIA0NBCAFIAdrIg8gBUsNBCAH
IAgoAjhqIg0gB0kNBCANRQ0EIAJBJGogDSAOEK2CgIAAIAItACxFDQQgAigCKCEHIAJBMGogDSAPEK2CgIAAIAItADhFDQQgBykCACEMIAcgAigCNCINKQIA
NwIAIA0gDDcCAAsgASkDAEKAgICAgIAIgyEMAkACQCAELQAFQSBxDQAgDFANASAFIAkoAgAiB2siDyAFSw0FIAMgBWoiDSAHayIOIA1LDQUgByAIKAKkAWoi
DSAHSQ0FIA1FDQUgAiANNgJMIAJB0ABqIA0gDxCwgoCAACACLQBYRQ0FIAIoAlQvAQAhByACQcwAaiACKAJQELGCgIAAIAJB9ABqIAJBzABqIA4QsoKAgAAC
QCACLQB8DQAgAigCdCAONgIACyACKAJ4IAc7AQAMAQsgAyAFaiINIAkoAgAiB2siDiANSyENAkAgDEIAUg0AIA0NBSAFIAdrIg8gBUsNBSAHIAgoAqQBaiIN
IAdJDQUgDUUNBSACIA02AjwgAkHAAGogDSAOELCCgIAAIAItAEhFDQUgAigCRC8BACEHIAJBPGogAigCQBCxgoCAACACQfQAaiACQTxqIA8QsoKAgAACQCAC
LQB8DQAgAigCdCAPNgIACyACKAJ4IAc7AQAMAQsgDQ0EIAUgB2siDyAFSw0EIAcgCCgCpAFqIg0gB0kNBCANRQ0EIAJB3ABqIA0gDhCwgoCAACACLQBkRQ0E
IAIoAmAhByACQegAaiANIA8QsIKAgAAgAi0AcEUNBCAHLwEAIQ0gByACKAJsIg4vAQA7AQAgDiANOwEACyABKQMAIQwgASAEKQMANwMAIAQgDDcDACAFQQhq
IQUgAUEIaiIBIAtNDQALCyAGQf//A3EgCmsiBUH//wNxIAVHDQEgACgCGCIEIAkgBCgCyAEgASAFQf//A3EQs4KAgAAgACgCGBC0goCAACAAKAIYKALIASIB
IAEpAwBCgICAgIAghDcDAAsgAkGAAWokgICAgAAPCwAL9gUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcC
kBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCpgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICA
gAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBKEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBq
QqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIoNwOYECABIAI2ApQQIAFB
3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQqYKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarV
qtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4
EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIg
ASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECEC
AkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC5UOAg9/AX4jgICAgABBIGsi
AiSAgICAAAJAAkACQAJAAkAgAUUNACAAKAIYIgMvAdIBIgQgAC8B/BFJDQAgBCAALwH+ESIFSw0AIAMvAdABIgYgAC8BgBIiB0kNACAGIAAvAYISIghLDQBB
ACEJAkAgBw0AIAAvAfoRQX9qIgZB//8DcSIHIAZHDQUgCCAHTyEJCyAFIARrIgVB//8DcSIGIAVHDQQgBkEBaiIFQf//A3EgBUcNBCADKALEASEHAkACQCAD
KAJQIgZFDQAgAyAGKAIANgJQDAELQRkhCkEADQMgA0HAAGpBDEECIAEQj4CAgAAiBkUNAyAGQqrVqtWq1arVqn83AAAgBkEIakGq1arVejYAACAGQQNxDQUL
IAZCqtWq1arVqtWqfzcCACAGQQhqIghBqtWq1Xo2AgAgBiAHKQIANwIAIAggB0EIaigCADYCACADQewAaiADQRBqIAYQk4CAgAAiCkH//wNxDQEgBUH//wNx
IAEgBUH//wNxIgsgASALSRsiDEH//wNxayIBQf//A3EiDSABRw0DQQAhCANAIAYvAQQiDiAGKAIAIgEvAaoCIgVPDQUgBi8BBiIPIAEvAagCTw0FIAEoAhAi
AyABKAIYaiIHIANJDQUgB0EHcQ0FIAdFDQUgAyAHIA5BA3RqIgcoAgBqIgogA0kNBSAKQQdxDQUgCkUNBSABQRBqIQoCQAJAAkACQAJAAkAgCCANSQ0AIAAg
CiAHELaDgIAAIAYoAgAiBSgCECIDIAcoAgBqIgEgA0kNCyABQQdxDQsgAUUNCyAALwGCEkEBaiIKQf//A3EiAyAKRw0LIAAvAYASIgogA0sNASADIAUvAagC
Ig5LDQIgACgCGCAFQRBqIAcgASAKQQN0aiADIAprELOCgIAADAQLIAUgDkEBakH//wNxayIQQf//A3EiAyAQRw0KAkAgDCADSw0AIAwgDmoiA0H//wMgA0H/
/wNJGyEODAMLIAEoAgwiAUUNCiAMIANrIQMDQAJAIAMgAS8BqgIiBUsNACADQX9qIg4gA0sNDAwECyADIAVrIQMgASgCDCIBDQAMCwsLIAogAxCKgICAAAAL
IAMgDhCIgICAAAALIAUgDkH//wNxTQ0HIA8gAS8BqAJPDQcgASgCECIFIAEoAhhqIgMgBUkNByADQQdxDQcgA0UNByAFIAMgDkEDdGoiAygCAGoiDiAFSQ0H
IA5BB3ENByAORQ0HIAAgCiAHELaDgIAAIAAgAUEQaiIKIAMQtoOAgAACQAJAAkAgCUUNACADIAMpAwBC/////2+DNwMAIAcgBykDAEL/////b4M3AwAgAyAD
KQMAQv////9fgzcDACAHIAcpAwBC/////1+DIhE3AwAgASAGKAIAIgVHDQEgByADKQMANwMAIAMgETcDAAwDCyABIAYoAgAiBUYNAQsgAC8BghJBAWoiAUH/
/wNxIg4gAUcNCCAFQRBqIAogByADIAAvAYASIA4Ql4OAgAAiAUH//wNxRQ0BIAFBY2pB//8DcSIBQQdLDQggAkEQaiAAKAIYIAYoAgAgAUECdEHM/smAAGoo
AgAQlYOAgAAgAi8BFA4dAggICAgICAgICAgICAgICAgICAgICAgICAgICAgICyAALwGCEiAALwGAEiIBayIKQf//A3EiDiAKRw0HIA5BAWoiCkH//wNxIg4g
CkcNByAFQRBqIAMgASAHIAEgDhC3g4CAAAsgBi8BBCIFIAYoAgAiAS8BqgJPDQYgBi8BBiABLwGoAk8NBiABKAIQIgMgASgCGGoiASADSQ0GIAFBB3ENBiAB
RQ0GIAMgASAFQQN0aiIFKAIAaiIBIANJDQYgAUEHcQ0GIAFFDQYgBSAFKQMAQoCAgICAIIQ3AwAgBi8BBEEBaiIFQf//A3EiAyAFRw0GIAYoAgAiAS8BqgIi
ByADSQ0GIAhBAWohCAJAIAcgA0cNAAJAAkAgASgCDCIBRQ0AQQEhAwNAIAMgAS8BqgIiBU0NAiADIAVrIQMgASgCDCIBDQALIAVFDQkLIAJBADoAGAwCCyAD
QX9qIgUgA0sNBwsgBkEAOgAIIAYgBTsBBCAGIAE2AgAgAkEAOgAYCyAIIAtJDQALIAAoAhggBhDlgICAACAAKAIYIAAvAYASIAQQ1YCAgAAgACgCGEEAOgDZ
AQsgAkEgaiSAgICAAA8LIAZCqtWq1arVqtWqfzcCACAIQarVqtV6NgIAIAYgAygCUDYCACADIAY2AlALIAIgCjsBDiACQQ5qELiDgIAAAAsgBi8BBCIDIAYo
AgAiAS8BqgJPDQAgBi8BBiABLwGoAk8NACABKAIQIgUgASgCGGoiASAFSQ0AIAFBB3ENACABRQ0AIAUgASADQQN0aigCAGoiASAFSQ0AIAFBB3EaAAsAC/YF
AwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJA
IAFBiBBqIAAQp4KAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAA
IgOnQShBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACAC
QSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCKDcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEKeC
gIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGk
EGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsA
CyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGB
gICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuXBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyAB
QoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEKOCgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAg
AhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E0QQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAA
IAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkEwakGq1arVejYAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAA
IAJBGGpCqtWq1arVqtWqfzcAACABQjQ3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCjgoCAAEH//wNxDgMABAEECyAB
KAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiA
gIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAA
QQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAA
DAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGok
gICAgAAPCyAAIAIQiICAgAAACwALlAYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYC
iBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCcgoCAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAg
ABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBMUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83
AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBMGpBqn86AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAg
AUIxNwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQnIKAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFC
ADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEg
ASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAA
CyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQ
IQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsA
C44BAQN/IAAoAhhBADoA2QECQAJAAkAgAC8B/hEiAiAAKAIYIgMvAdIBIgRPDQAgAC8B+BEgBGsiAEH//wNxIgQgAEcNASAEQX9qIgBB//8DcSAARw0BDAIL
IAIgBGshAAwBCwALIAMgAEH//wNxIgAgAUH//wNxIgRBASAEQQFLGyIEIAAgBEkbEMOAgIAAC5UGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0A
IAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQnoKAgABB//8DcQ4DAAQBBAsCQCABKAKU
ECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTJBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUN
ACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVfjsAACACQShqQqrVqtWq1arVqn83AAAgAkEg
akKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjI3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCegoCA
AEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBq
IAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACAC
QaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsg
ASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCA
gACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwAL8wEBA38gACgCGEEAOgDZAQJAAkACQCAALwGCEiICIAAoAhgiAy8B0AEiBE8NACAALwH6ESAE
ayIAQf//A3EiAiAARw0CIAJBf2oiAEH//wNxIABGDQEMAgsgAiAEayEACyAEIABB//8DcSIAIAFB//8DcSIBQQEgAUEBSxsiASAAIAFJGyIAaiIEQf//A3Ei
ASAERw0AIAEgAy8BjAFPDQAgAyADKALMASAAQQN0ajYCzAEgAygCxAEiAS8BBiAAaiIEQf//A3EgBEcNACABIAQ7AQYgAy8B0AEgAGoiAEH//wNxIABHDQAg
AyAAOwHQAQ8LAAuBAwEIfyOAgICAAEEQayIDJICAgIAAQQAhBAJAIAAoAggiBSACTw0AIAFCIIinIQYgAachByAAKAIAIQgCQAJAIAUNAEEAIQRBACEJDAEL
QQAhBEEAIQlBAA0AQQAhBAJAIAcgCCAFQQAgAkEAIAYoAggRgoCAgACAgICAACIJDQBBACEJDAELIAkhBCACIQkLAkACQCAERQ0AIAAgBDYCACAJIQIMAQtB
GSEEQQANASAHIAJBAEEAIAYoAgARgICAgACAgICAACIJRQ0BAkAgAkUNACAJQaoBIAL8CwALAkAgACgCBCIEIAJLDQACQAJAIAkgACgCACIKIARqTw0AIAog
CSAEakkNAQsCQCAERQ0AIAkgCiAE/AoAAAsCQCAFRQ0AAkAgBUUNACAIQaoBIAX8CwALIAcgCCAFQQBBACAGKAIMEYGAgIAAgICAgAALIAAgCTYCAAwCCwAL
IAQgAhCIgICAAAALIAAgAjYCCEEAIQQLIANBEGokgICAgAAgBAsNAEEAIAAQhYSAgAAAC/cCAQN/AkAgASADRg0AQQAPC0EBIQQCQAJAIAFFDQAgACACQarV
qtV6IAMbIgVGDQACQCABQRBLDQACQCABQQNLDQAgAC0AACAFLQAARiAAIAFBf2oiA2otAAAgBSADai0AAEZxIAAgAUEBdiIDai0AACAFIANqLQAARnEPCyAB
QXxqIgMgAUEBdkEMcSICayIEIANLDQICQCACQQRqIgYgAUsNACAEQQRqIgYgAUsNACAFIAJqKAAAIAAgAmooAABGIAUgA2ooAAAgACADaigAAEYgBSgAACAA
KAAARnFxIAUgBGooAAAgACAEaigAAEZxDwsgBiABEIiAgIAAAAsgAUF/akECdiECQQAhAwJAAkADQCADQQRqIgQgAUsNAQJAIAAgA2ooAAAgBSADaigAAEcN
ACAEIQMgAkF/aiICRQ0DDAELC0EADwsgA0EEaiABEIiAgIAAAAsgACABQXxqIgNqKAAAIAUgA2ooAABGIQQLIAQPCwALpgQCB38BfiOAgICAAEEQayICJICA
gIAAQQAhAwJAAkACQAJAA0AgACgCDCIEQTMgA2siBWoiBiAESQ0BIANB6ejIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBAJAIAAoAgQgBGoiBkGc6ciA
AE8NACAHIAYgBWpJDQQLAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQMgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGog
ACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENBSAJpyEFCyADIAVqIgUgA0kNASAFIQMgBUEzSQ0ACyAAIAEoAgAgASgCBCIDIANBAC0A
wNjJgABBIBCWgICAACIFQf//A3ENAwNAIAAoAgwiA0EBaiIFRQ0BAkAgBSAAKAIIIgZLDQAgAyAGTw0EAkAgACgCBCADaiIDQZ3pyIAATw0AIANBAWpBnOnI
gABLDQMLIANBJzoAACAAKAIMQQFqIgNFDQIgACADNgIMQQAhBQwFCyAAKAIAKAIAIQMgAkEBNgIEIAJBnOnIgAA2AgAgAkEIaiAAIAJBAUEBIAMRgYCAgACA
gICAACACKQMIIglCIIinIgVB//8DcQ0EQQAhBSAJp0UNAAwECwsACyAGIAgQiICAgAAACyADQQFqIAYQiICAgAAACyACQRBqJICAgIAAIAULtgMBA38jgICA
gABBIGsiAiSAgICAAEEPIQNBgYbAgAAhBAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAgAUEfcUF9ag4UEwABAgMEBQYHCAkKCwwN
Dg8QERITC0ESIQNBtonAgAAhBAwSC0EKIQNBn57AgAAhBAwRC0ELIQNBn5vAgAAhBAwQC0HrkMCAACEEDA8LQRQhA0Gtk8CAACEEDA4LQRkhA0GxkcCAACEE
DA0LQcCFwIAAIQQMDAtBDSEDQaCfwIAAIQQMCwtBDCEDQdmOwIAAIQQMCgtBFyEDQciCwIAAIQQMCQtBFyEDQYGcwIAAIQQMCAtBFiEDQf2EwIAAIQQMBwtB
ESEDQaGEwIAAIQQMBgtBrY/AgAAhBAwFC0ESIQNB6YnAgAAhBAwEC0EiIQNB45zAgAAhBAwDC0EWIQNBjJHAgAAhBAwCC0EOIQNB0IbAgAAhBAwBC0ERIQNB
1JbAgAAhBAsCQCAAQR9xQRhLDQAgAkEIaiAAEIaEgIAAIAIgAzYCFCACIAQ2AhAgAiACKQMINwIYIAJBEGoQmYKAgAALAAuzBgERfyOAgICAAEHgAGsiBCSA
gICAACAEQRhqQSBqIgVBACgC9LjIgAA2AgAgBEEYakEYaiIGQQApAuy4yIAANwMAIARBGGpBEGoiB0EAKQLkuMiAADcDACAEQRhqQQhqIghBACkC3LjIgAA3
AwAgBEEAKQLUuMiAADcDGCAEIAJBH3E6AEMgBEEQaiADEJeEgIAAAkACQCAEKAIQIgJFDQAgBCgCFCEJQQAtAKDVyYAAQR9xIQpBAC0AodXJgABBH3EhC0EA
LQCi1cmAAEEfcSEMQQAtAKPVyYAAQR9xIQ1BAC0ApNXJgABBH3EhDkEALQCl1cmAAEEfcSEPQQAtAKbVyYAAQR9xIRBBAC0Ap9XJgABBH3EhEUEALQCo1cmA
AEEfcSESQQAtAKnVyYAAQR9xIRMDQAJAAkAgAiAJQf+hwIAAQQEQhIKAgABBAXFFDQAgBEHEAGogBEEYaiABEIiCgIAAIAQvAUgiAg0EIAQoAkQiAkEBOgAI
IAJBAjoAACACIAQtAEMiCUEfcToAAQwBCyAEQcwAaiACIAkQi4KAgAAgBC8BUA0CIAQvAUwhCSAELQBOIRQgBEHUAGogBEEYaiABEIiCgIAAIAQvAVgiAg0D
IAQoAlQiAiAJIBRBEHRyIgk7AQAgAkEAOgAIIAJBAjoABCACQQJqIAlBEHY6AAAgAiAELQBDIglBH3E6AAULAkACQAJAIAlBH3FBAWoiCUEfcSICIAlHDQAC
QCACIApGDQAgAiALRg0AIAIgDEYNACACIA1GDQAgAiAORg0AIAIgD0YNACACIBBGDQAgAiARRg0AIAIgEkYNACACIBNHDQILIAlBFmpBH3FBCk8NACAEQQE6
AF0gBCACOgBcIARB3ABqIQIMAgsAC0HGusiAACECCyACLwAAIgJBgAJJDQEgBCACQR9xOgBDIARBCGogAxCXhICAACAEKAIMIQkgBCgCCCICDQALCyAAIAQp
Axg3AgAgAEEgaiAFKAIANgIAIABBGGogBikDADcCACAAQRBqIAcpAwA3AgAgAEEIaiAIKQMANwIAQQAhAgsgACACOwEkIARB4ABqJICAgIAAC7cIAwR/AX4J
fyOAgICAAEEQayIDJICAgIAAAkACQCABKAIgIgRBAWoiBUUNACAFQQJqIgYgBUkNACACKQIAIQcCQEEBIAZnQR9zIgJ0IAZGDQAgAkEfcUEBaiICQR9xIAJH
DQELIAJBf2pBH3FBf2oiCEEfcSAIRw0AIAEoAhwiCUEgTw0AAkACQAJAIAhBH3EiCiAJQR9xSw0AIAkhCgwBCyADQQA6AAgCQAJAQQFFDQAgB6ciCyAKQQJ0
IgxBAkEAIAdCIIinIg0oAgARgICAgACAgICAACIERQ0AAkAgDEUiDg0AIARBqgEgDPwLAAsgBEEDcQ0EQQAhBiADQQA6AAcCQAJAIAlBH3EiDw0AQQAhAgwB
C0EAIQICQANAIAogBkEfcSIGTQ0BAkAgASgCHCIQIAZNDQAgBCAGQQJ0IgZqIAEoAhggBmooAgA2AgAgAyACQQFqQR9xIgI6AAcgAiEGIAIgD08NAwwBCwsg
BiAQEIiAgIAAAAsgBiAKEIiAgIAAAAsgCEEfcSACTQ0BA0ACQCAKIAJBH3EiEEsNACAQIAoQiICAgAAACyACQR9xQQJqIgZBH3EgBkcNBSADQQEgBnStQgx+
IgdCIIinIgZBAEc6AAwCQCAGDQACQAJAIAenIg8NAEF8IQYMAQsgCyAPQQJBACANKAIAEYCAgIAAgICAgAAiBkUNAQJAIA9FDQAgBkGqASAP/AsACyAGQQNx
DQcLIAQgEEECdGogBjYCACADIAJBAWpBH3EiAjoAByAIQR9xIAJLDQEMAwsLAkAgAkEfcSAJQR9xIghNDQADQAJAIAogAkEfcSIGSw0AIAYgChCIgICAAAAL
IAJBH3FBAmoiAUEfcSABRw0GQQEgAXStQgx+IgdCIIinDQYgBCAGQQJ0aigCACEBAkAgB6ciBkUNACABQaoBIAb8CwALIAsgASAGQQJBACANKAIMEYGAgIAA
gICAgAAgAyACQX9qQR9xIgI6AAcgAiAISw0ACwsCQCAODQAgBEGqASAM/AsACyALIAQgDEECQQAgDSgCDBGBgICAAICAgIAAC0EZIQIMAgsCQAJAIAEoAhwi
Ag0AQQAhAkGq1arVeiEGDAELIAJBgICAgARPDQMgASgCGCEGIAJBAnQhAgsCQCACRQ0AAkAgAkUNACAGQaoBIAL8CwALIAsgBiACQQJBACANKAIMEYGAgIAA
gICAgAALIAEgCjYCHCABIAQ2AhggASgCICEECwJAAkAgBEEBSw0AIAEgBEEMbGohAgwBCyAEQQJqIgIgBEkNAiACQQEgAmciBkEfc3RrIgQgAksNAiAKQR0g
BmsiAk0NAyABKAIYIAJBAnRqKAIAIARBDGxqIQILIAAgAjYCACABIAU2AiBBACECCyAAIAI7AQQgA0EQaiSAgICAAA8LAAsgAiAKEIiAgIAAAAukAwEGfyOA
gICAAEEQayICJICAgIAAAkACQCAAKAIcIgNBIE8NACABQiCIpyEEIAGnIQUgAiADQR9xIgY6AA8CQCAGRQ0AA0AgAiAGQX9qQR9xIgY6AA8gACgCHCIDIAZN
DQMgBkECaiIDQR9xIANHDQJBASADdK1CDH4iAUIgiKcNAiAAKAIYIAZBAnRqKAIAIQMCQCABpyIHRQ0AIANBqgEgB/wLAAsgBSADIAdBAkEAIAQoAgwRgYCA
gACAgICAACAGDQALIAAoAhwhAwsCQAJAIAMNAEEAIQZBqtWq1XohBwwBCyADQYCAgIAETw0BIAAoAhghByADQQJ0IQYLAkAgBkUNAAJAIAZFDQAgB0GqASAG
/AsACyAFIAcgBkECQQAgBCgCDBGBgICAAICAgIAACyAAQqrVqtWq1arVqn83AgAgAEEgakGq1arVejYCACAAQRhqQqrVqtWq1arVqn83AgAgAEEQakKq1arV
qtWq1ap/NwIAIABBCGpCqtWq1arVqtWqfzcCACACQRBqJICAgIAADwsACyAGIAMQiICAgAAAC68DAQV/I4CAgIAAQRBrIgMkgICAgAACQAJAIAINAEGAgKgB
IQQMAQsCQAJAAkACQCABLQAAQVVqDgMAAgECCyADIAFBAWogAkF/ahCghICAAAwCC0GAgKgBIQQgAkF/aiIFRQ0CIANBADsBBiABQQFqIgYtAABB3wBGDQIg
ASACakF/ai0AAEHfAEYNAgJAAkADQAJAIAYtAAAiAkHfAEYNACACQVBqQf8BcSIBQQlLDQYCQAJAAkAgAy8BBkH/A3EiB0UNACADIAdBCmwiAkH/A3E7AQ4g
AyACQQl2IgJBAEc6AAwgAkUNAUGAgAQhBAwJCwJAIAJBMEsNAEEAIQIMAgtBgIAEIQQMCAsgAyADLwEOQf8DcSICOwEGIAMgAiABayICQf8DcSIBOwEOIAMg
ASACRzoADCABIAJHDQMgAyADLwEOQf8DcSICOwEACyADIAI7AQYLIAZBAWohBiAFQX9qIgVFDQIMAAsLQYCABCEEDAMLIAMgAy8BBkH/A3EiBDsBAAwCCyAD
IAEgAhCghICAAAsgAygCACEECyAAIAQ2AQAgA0EQaiSAgICAAAuZLQEOfyOAgICAAEEgayIDJICAgIAAAkACQCACDQAgAEKAgICAsAU3AgAMAQsCQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQCABLQAAIgRBI0YNACADQRRqQQJqIQUgA0EQakECaiEGIANBBGpBAmohByADQQJqIQhBACEJAkACQAJAAkADQAJA
IAEgCWotAABBIEYNACACIQogAiAJTQ0DIAFBf2ohCyACIQoDQCAKQX9qIgwgAk8NAyALIApqLQAAQSBHDQQgDCEKIAwgCUsNAAsgCSEKDAQLIAIgCUEBaiIJ
Rw0ACyACIQkgAiEKDAMLIApBf2ogAhCIgICAAAALIAkgCksNAwsgCiACSw0DCwJAIAogCWsiDUFpakFsSQ0AIAEgCWohDiANQQJ0QbyvyIAAaigCACEPA0Ag
D0EDdCIKQZjmx4AAaigCACANRw0BIA4hCSANIQwgCkGU5seAAGooAgAiCiAORg0RAkADQCAKLQAAIgtBv39qQf8BcUEaSUEFdCALckH/AXEgCS0AACILQb9/
akH/AXFBGklBBXQgC3JB/wFxRw0BIApBAWohCiAJQQFqIQkgDEF/aiIMRQ0TDAALCyAPQQFqIg9FDQkgD0GOBkkNAAsLAkACQCACQQlJDQAgAUHTocCAAEYN
ASAEQfIARw0AIAEtAAJB/wFxQeIARw0AIAEtAAFB/wFxQecARg0BCyAAQoCAgICwBTcCAAwRCwJAIAFBBEEDIAEtAAMiBEHpAEYbIg5qIgstAABBOkYNACAA
QoCAgICwBTcCAAwRCyACIA5BAWoiCmshDyABIApqIRBBACEJA0AgCiAJaiINIApJDQgCQCALIAlqIgxBAWotAABBL0YNACAPIAlBAWoiCUYNBQwBCwsgDiAN
Tw0EIA0gAksNBSANIAprIg0gCmpBAWoiCyAKSQ0HAkAgBEHpAEcNACADIBAgDRCIhICAACADLwEAIgpFDQcgACAKOwEEDBELIANBBGogECANEImEgIAAIAch
CCADLwEEIgpFDQYgACAKOwEEDBALIAJBfGoOCgwNDQsNDQoNDQkNCyAJIAoQioCAgAAACyAKIAIQiICAgAAMBAsgAEKAgICAsAU3AgAMDAsgCiANEIqAgIAA
AAsgDSACEIiAgIAAAAsCQAJAAkACQAJAAkAgCyACTw0AIAgtAAAhCCABIAtqIQ8gDiACayAJakEDaiENQQAhCQNAIAsgCWoiCiALSQ0HAkAgDCAJakECai0A
AEEvRg0AIA0gCWohCiAJQQFqIQkgCkUNAgwBCwsgCiACSw0HIAogC2siCkEBaiIJRQ0GIAsgCWoiCSALSQ0GIARB6QBHDQEgA0EIaiAPIAoQiISAgAAgAy8B
CCIKRQ0CIAAgCjsBBAwPCyAAQoCAgICwBTcCAAwOCyADQQxqIA8gChCJhICAACADLwEMIgpFDQEgACAKOwEEDA0LIAIgCUkNBSADLQAKIQogA0EQaiABIAlq
IAIgCWsQiISAgAAgAy8BECIJRQ0BIAAgCTsBBAwMCyACIAlJDQQgAy0ADiEKIANBFGogASAJaiACIAlrEImEgIAAIAUhBiADLwEUIgkNAQsgAEEAOwEEIABB
AmogBi0AADoAACAAIApBCHQgCEH/AXFyOwEADAoLIAAgCTsBBAwJCwALIAogAhCIgICAAAALIAkgAhCKgICAAAALAkACQAJAAkACQAJAAkACQAJAIAEtAAEi
CUHfAEYNACABQQRqLQAAIgpB3wBGDQACQCAJQVBqIgJB/wFxQQpJDQACQAJAIAlBv39qQf8BcUEaTw0AQckBIQIMAQsgCUGff2pB/wFxQRpPDQJBqQEhAgsg
AiAJaiICQf8BcUEQTw0BCyADQQA6AB5BAUUNACACQf8BcSEMAkAgAS0AAiIJQd8ARg0AAkAgCUFQaiILQf8BcUEKSQ0AAkACQCAJQb9/akH/AXFBGk8NAEHJ
ASELDAELIAlBn39qQf8BcUEZSw0DQakBIQsLIAsgCWoiC0H/AXFBD0sNAgtBACEJAkAgAkH/AXFFDQAgA0EAOgAeQQANAiAMQQR0IQkLIANBADoAHkEADQEg
CSALQf8BcXIhDAsCQCABLQADIglB3wBGDQACQCAJQVBqIgJB/wFxQQpJDQACQAJAIAlBv39qQf8BcUEaTw0AQckBIQIMAQsgCUGff2pB/wFxQRlLDQNBqQEh
AgsgAiAJaiICQf8BcUEPSw0CC0EAIQkCQCAMRQ0AIANBADoAHkEADQIgDEEEdCEJCyADQQA6AB5BAA0BIAkgAkH/AXFyIQwLAkAgCkFQaiIJQf8BcUEKSQ0A
AkACQCAKQb9/akH/AXFBGk8NAEHJASEJDAELIApBn39qQf8BcUEZSw0CQakBIQkLIAkgCmoiCUH/AXFBD0sNAQtBACEKAkAgDEUNACADQQA6AB5BAA0BIAxB
BHQhCgsgA0EAOgAeQQANACAKIAlB/wFxckH//wNxQYECbiEKIAEtAAUiCUHfAEYNBCABQQhqLQAAIgJB3wBGDQQgCUFQaiIMQf8BcUEKSQ0DIAlBv39qQf8B
cUEaTw0BQckBIQwMAgsgAEErOwEEDA0LIAlBn39qQf8BcUEaTw0CQakBIQwLIAwgCWoiDEH/AXFBEE8NAQsgA0EAOgAeQQFFDQAgDEH/AXEhCwJAIAEtAAYi
CUHfAEYNAAJAIAlBUGoiDUH/AXFBCkkNAAJAAkAgCUG/f2pB/wFxQRpPDQBByQEhDQwBCyAJQZ9/akH/AXFBGUsNA0GpASENCyANIAlqIg1B/wFxQQ9LDQIL
QQAhCQJAIAxB/wFxRQ0AIANBADoAHkEADQIgC0EEdCEJCyADQQA6AB5BAA0BIAkgDUH/AXFyIQsLAkAgAS0AByIJQd8ARg0AAkAgCUFQaiIMQf8BcUEKSQ0A
AkACQCAJQb9/akH/AXFBGk8NAEHJASEMDAELIAlBn39qQf8BcUEZSw0DQakBIQwLIAwgCWoiDEH/AXFBD0sNAgtBACEJAkAgC0UNACADQQA6AB5BAA0CIAtB
BHQhCQsgA0EAOgAeQQANASAJIAxB/wFxciELCwJAIAJBUGoiCUH/AXFBCkkNAAJAAkAgAkG/f2pB/wFxQRpPDQBByQEhCQwBCyACQZ9/akH/AXFBGUsNAkGp
ASEJCyAJIAJqIglB/wFxQQ9LDQELQQAhAgJAIAtFDQAgA0EAOgAeQQANASALQQR0IQILIANBADoAHkEADQAgAiAJQf8BcXJB//8DcUGBAm4hAiABLQAJIglB
3wBGDQQgAUEMai0AACIMQd8ARg0EIAlBUGoiC0H/AXFBCkkNAyAJQb9/akH/AXFBGk8NAUHJASELDAILIABBKzsBBAwJCyAJQZ9/akH/AXFBGk8NAkGpASEL
CyALIAlqIgtB/wFxQRBPDQELIANBADoAHkEBRQ0AIAtB/wFxIQ0CQCABLQAKIglB3wBGDQACQCAJQVBqIg9B/wFxQQpJDQACQAJAIAlBv39qQf8BcUEaTw0A
QckBIQ8MAQsgCUGff2pB/wFxQRlLDQNBqQEhDwsgDyAJaiIPQf8BcUEPSw0CC0EAIQkCQCALQf8BcUUNACADQQA6AB5BAA0CIA1BBHQhCQsgA0EAOgAeQQAN
ASAJIA9B/wFxciENCwJAIAEtAAsiCUHfAEYNAAJAIAlBUGoiC0H/AXFBCkkNAAJAAkAgCUG/f2pB/wFxQRpPDQBByQEhCwwBCyAJQZ9/akH/AXFBGUsNA0Gp
ASELCyALIAlqIgtB/wFxQQ9LDQILQQAhCQJAIA1FDQAgA0EAOgAeQQANAiANQQR0IQkLIANBADoAHkEADQEgCSALQf8BcXIhDQsCQCAMQVBqIglB/wFxQQpJ
DQACQAJAIAxBv39qQf8BcUEaTw0AQckBIQkMAQsgDEGff2pB/wFxQRlLDQJBqQEhCQsgCSAMaiIJQf8BcUEPSw0BC0EAIQwCQCANRQ0AIANBADoAHkEADQEg
DUEEdCEMCyADQQA6AB5BAA0AIABBADsBBCAAIAJBCHQgCnI7AQAgAEECaiAMIAlB/wFxckH//wNxQYECbjoAAAwGCyAAQSs7AQQMBQsCQAJAAkACQAJAAkAC
QAJAAkAgAS0AASIJQd8ARg0AIAFBA2otAAAiCkHfAEYNAAJAIAlBUGoiAkH/AXFBCkkNAAJAAkAgCUG/f2pB/wFxQRpPDQBByQEhAgwBCyAJQZ9/akH/AXFB
Gk8NAkGpASECCyACIAlqIgJB/wFxQRBPDQELIANBADoAHkEBRQ0AIAJB/wFxIQwCQCABLQACIglB3wBGDQACQCAJQVBqIgtB/wFxQQpJDQACQAJAIAlBv39q
Qf8BcUEaTw0AQckBIQsMAQsgCUGff2pB/wFxQRlLDQNBqQEhCwsgCyAJaiILQf8BcUEPSw0CC0EAIQkCQCACQf8BcUUNACADQQA6AB5BAA0CIAxBBHQhCQsg
A0EAOgAeQQANASAJIAtB/wFxciEMCwJAIApBUGoiCUH/AXFBCkkNAAJAAkAgCkG/f2pB/wFxQRpPDQBByQEhCQwBCyAKQZ9/akH/AXFBGUsNAkGpASEJCyAJ
IApqIglB/wFxQQ9LDQELQQAhCgJAIAxFDQAgA0EAOgAeQQANASAMQQR0IQoLIANBADoAHkEADQAgCiAJQf8BcXJB/wFsQf8fbiEKIAEtAAQiCUHfAEYNBCAB
QQZqLQAAIgJB3wBGDQQgCUFQaiIMQf8BcUEKSQ0DIAlBv39qQf8BcUEaTw0BQckBIQwMAgsgAEErOwEEDAwLIAlBn39qQf8BcUEaTw0CQakBIQwLIAwgCWoi
DEH/AXFBEE8NAQsgA0EAOgAeQQFFDQAgDEH/AXEhCwJAIAEtAAUiCUHfAEYNAAJAIAlBUGoiDUH/AXFBCkkNAAJAAkAgCUG/f2pB/wFxQRpPDQBByQEhDQwB
CyAJQZ9/akH/AXFBGUsNA0GpASENCyANIAlqIg1B/wFxQQ9LDQILQQAhCQJAIAxB/wFxRQ0AIANBADoAHkEADQIgC0EEdCEJCyADQQA6AB5BAA0BIAkgDUH/
AXFyIQsLAkAgAkFQaiIJQf8BcUEKSQ0AAkACQCACQb9/akH/AXFBGk8NAEHJASEJDAELIAJBn39qQf8BcUEZSw0CQakBIQkLIAkgAmoiCUH/AXFBD0sNAQtB
ACECAkAgC0UNACADQQA6AB5BAA0BIAtBBHQhAgsgA0EAOgAeQQANACACIAlB/wFxckH/AWxB/x9uIQIgAS0AByIJQd8ARg0EIAFBCWotAAAiDEHfAEYNBCAJ
QVBqIgtB/wFxQQpJDQMgCUG/f2pB/wFxQRpPDQFByQEhCwwCCyAAQSs7AQQMCAsgCUGff2pB/wFxQRpPDQJBqQEhCwsgCyAJaiILQf8BcUEQTw0BCyADQQA6
AB5BAUUNACALQf8BcSENAkAgAS0ACCIJQd8ARg0AAkAgCUFQaiIBQf8BcUEKSQ0AAkACQCAJQb9/akH/AXFBGk8NAEHJASEBDAELIAlBn39qQf8BcUEZSw0D
QakBIQELIAEgCWoiAUH/AXFBD0sNAgtBACEJAkAgC0H/AXFFDQAgA0EAOgAeQQANAiANQQR0IQkLIANBADoAHkEADQEgCSABQf8BcXIhDQsCQCAMQVBqIglB
/wFxQQpJDQACQAJAIAxBv39qQf8BcUEaTw0AQckBIQkMAQsgDEGff2pB/wFxQRlLDQJBqQEhCQsgCSAMaiIJQf8BcUEPSw0BC0EAIQwCQCANRQ0AIANBADoA
HkEADQEgDUEEdCEMCyADQQA6AB5BAA0AIABBADsBBCAAIAJBCHQgCnI7AQAgAEECaiAMIAlB/wFxckH/AWxB/x9uOgAADAULIABBKzsBBAwECwJAAkACQAJA
AkACQAJAAkACQCABLQABIglB3wBGDQAgAUECai0AACIKQd8ARg0AAkAgCUFQaiICQf8BcUEKSQ0AAkACQCAJQb9/akH/AXFBGk8NAEHJASECDAELIAlBn39q
Qf8BcUEaTw0CQakBIQILIAIgCWoiAkH/AXFBEE8NAQsgA0EAOgAeQQFFDQACQCAKQVBqIglB/wFxQQpJDQACQAJAIApBv39qQf8BcUEaTw0AQckBIQkMAQsg
CkGff2pB/wFxQRlLDQJBqQEhCQsgCSAKaiIJQf8BcUEPSw0BC0EAIQoCQCACQf8BcUUNACADQQA6AB5BAA0BIAJBBHRB8AFxIQoLIANBADoAHkEADQAgAS0A
AyICQd8ARg0EIAFBBGotAAAiDEHfAEYNBCACQVBqIgtB/wFxQQpJDQMgAkG/f2pB/wFxQRpPDQFByQEhCwwCCyAAQSs7AQQMCwsgAkGff2pB/wFxQRpPDQJB
qQEhCwsgCyACaiILQf8BcUEQTw0BCyADQQA6AB5BAUUNAAJAIAxBUGoiAkH/AXFBCkkNAAJAAkAgDEG/f2pB/wFxQRpPDQBByQEhAgwBCyAMQZ9/akH/AXFB
GUsNAkGpASECCyACIAxqIgJB/wFxQQ9LDQELQQAhDAJAIAtB/wFxRQ0AIANBADoAHkEADQEgC0EEdEHwAXEhDAsgA0EAOgAeQQANACABLQAFIgtB3wBGDQQg
AUEGai0AACIBQd8ARg0EIAtBUGoiDUH/AXFBCkkNAyALQb9/akH/AXFBGk8NAUHJASENDAILIABBKzsBBAwHCyALQZ9/akH/AXFBGk8NAkGpASENCyANIAtq
Ig1B/wFxQRBPDQELIANBADoAHkEBRQ0AAkAgAUFQaiILQf8BcUEKSQ0AAkACQCABQb9/akH/AXFBGk8NAEHJASELDAELIAFBn39qQf8BcUEZSw0CQakBIQsL
IAsgAWoiC0H/AXFBD0sNAQtBACEBAkAgDUH/AXFFDQAgA0EAOgAeQQANASANQQR0QfABcSEBCyADQQA6AB5BAA0AIABBADsBBCAAQQJqIAEgC3I6AAAgACAM
IAJB/wFxckEIdCAKIAlB/wFxcnI7AQAMBAsgAEErOwEEDAMLAkACQCABQQFqLQAAIglB3wBGDQACQCAJQVBqIgpB/wFxQQpJDQACQAJAIAlBv39qQf8BcUEa
Tw0AQckBIQoMAQsgCUGff2pB/wFxQRpPDQJBqQEhCgsgCiAJaiIKQf8BcUEQTw0BC0EBDQELIABBKzsBBAwDCwJAAkAgAUECai0AACIJQd8ARg0AAkAgCUFQ
aiICQf8BcUEKSQ0AAkACQCAJQb9/akH/AXFBGk8NAEHJASECDAELIAlBn39qQf8BcUEaTw0CQakBIQILIAIgCWoiAkH/AXFBEE8NAQtBAQ0BCyAAQSs7AQQM
AwsCQAJAIAFBA2otAAAiCUHfAEYNAAJAIAlBUGoiDEH/AXFBCkkNAAJAAkAgCUG/f2pB/wFxQRpPDQBByQEhDAwBCyAJQZ9/akH/AXFBGk8NAkGpASEMCyAM
IAlqIgxB/wFxQRBPDQELQQENAQsgAEErOwEEDAMLIABBADsBBCAAQQJqIAxBgIDEAGxBEHY6AAAgACACQRFsQQh0IApBEWxB/wFxcjsBAAwCCyAAQoCAgICw
BTcCAAwBCyAAQQA7AQQgACAPQQJ0QYSXyIAAaiIJLwEAOwEAIABBAmogCUECai0AADoAAAsgA0EgaiSAgICAAAv4BgIHfwF+I4CAgIAAQRBrIgIkgICAgABB
ACEDAkACQAJAAkACQANAIAAoAgwiBEElIANrIgVqIgYgBEkNAiADQfHGyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBlsfJgABP
DQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAg
AkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBJUkNAAsgAEGw2MmAACABLQAAEKGEgIAAIgVB
//8DcQ0AQQAhAwNAIAAoAgwiBEETIANrIgVqIgYgBEkNAiADQZbHyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBqcfJgABPDQAg
ByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEB
QQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBE0kNAAsgACABLwECEJ+EgIAAIgVB//8DcQ0AQQAh
AwNAIAAoAgwiBEEGIANrIgVqIgYgBEkNAiADQanHyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQcCQCAAKAIEIARqIgZBr8fJgABPDQAgByAGIAVqSQ0F
CwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICA
AICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBkkNAAsgACABKAIEIAEoAggiAyADQQAtAMDYyYAAQSAQloCAgAAh
BQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICAgAAACyAGIAgQiICAgAAAC8wCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkAD
QCAAKAIMIgRBNSADayIFaiIGIARJDQIgA0H1xcmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQarGyYAATw0AIAcgBiAFakkNBQsC
QCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACA
gICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTVJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQUL
IAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAueBAIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBKiADayIFaiIGIARJDQEg
A0GVxcmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0EAkAgACgCBCAEaiIGQb/FyYAATw0AIAcgBiAFakkNBAsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIG
IAVqIgQgBkkNAyAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0F
IAmnIQULIAMgBWoiBSADSQ0BIAUhAyAFQSpJDQALIAIgAS0AADoACCAAIAJBCGpBARCggICAACIFQf//A3ENAwNAIAAoAgwiA0EBaiIFRQ0BAkAgBSAAKAII
IgZLDQAgAyAGTw0EAkAgACgCBCADaiIDQZ3pyIAATw0AIANBAWpBnOnIgABLDQMLIANBJzoAACAAKAIMQQFqIgNFDQIgACADNgIMQQAhBQwFCyAAKAIAKAIA
IQMgAkEBNgIEIAJBnOnIgAA2AgAgAkEIaiAAIAJBAUEBIAMRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0EQQAhBSAJp0UNAAwECwsACyAGIAgQiICA
gAAACyADQQFqIAYQiICAgAAACyACQRBqJICAgIAAIAUL1wQCB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQAJAA0AgACgCDCIEQTMgA2siBWoiBiAE
SQ0CIANBv8XJgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHyxcmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAo
AgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//
A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEzSQ0ACyACIAEtAAA6AAggACACQQhqQQEQoICAgAAiBUH//wNxDQBBACEDA0AgACgCDCIEIANBA3MiBWoi
BiAESQ0CIANB8sXJgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBgJAIAAoAgQgBGoiBkH1xcmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAAL
IAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIF
Qf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEDSQ0ACyAAIAEvAQIQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICA
gAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB0AAgA2siBWoiBiAESQ0CIANB08DJgABqIQcCQAJAIAYgACgCCCIISw0A
IAYgCEsNBQJAIAAoAgQgBGoiBkGjwcmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAo
AgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHQ
AEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC/EFAgd/AX4jgICAgABBMGsiAiSA
gICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE7IANrIgVqIgYgBEkNAiADQafCyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB4sLJ
gABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIsIAIgBzYCKCACIAAg
AkEoakEBQQEgBhGBgICAAICAgIAAIAIpAwAiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBO0kNAAsgAS0AACEDIAJBIGpBqn86AAAg
AkEYakKq1arVqtWq1ap/NwMAIAJBEGpCqtWq1arVqtWqfzcDACACQQhqQqrVqtWq1arVqn83AwAgAkKq1arVqtWq1ap/NwMAQQAhBQNAIAIgBWpBIGogA0EP
cSIGQTByIAZB1wBqIAZBCkkbOgAAIAVBf2ohBSADQQ9LIQYgA0EEdiEDIAYNAAtBACEDIAAgAiAFakEhakEAIAVrQQJBAC0A9MLJgABBMBCWgICAACIFQf//
A3ENAANAIAAoAgwiBEEOIANrIgVqIgYgBEkNAiADQfjCyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBhsPJgABPDQAgByAGIAVq
SQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIsIAIgBzYCKCACIAAgAkEoakEBQQEgBhGB
gICAAICAgIAAIAIpAwAiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBDkkNAAtBACEFCyACQTBqJICAgIAAIAUPCwALIAYgCBCIgICA
AAALIAYgCBCIgICAAAALgwcCB38BfiOAgICAAEEwayICJICAgIAAQQAhAwJAAkACQAJAAkADQCAAKAIMIgRBOSADayIFaiIGIARJDQEgA0HcwcmAAGohBwJA
AkAgBiAAKAIIIghLDQAgBiAISw0EAkAgACgCBCAEaiIGQZXCyYAATw0AIAcgBiAFakkNBAsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNAyAA
IAQ2AgwMAQsgACgCACgCACEGIAIgBTYCLCACIAc2AiggAiAAIAJBKGpBAUEBIAYRgYCAgACAgICAACACKQMAIglCIIinIgVB//8DcQ0GIAmnIQULIAMgBWoi
BSADSQ0BIAUhAyAFQTlJDQALQQAhAyAAIAEoAgBBCkEAQbDYyYAAEJWAgIAAIgVB//8DcQ0EA0AgACgCDCIEQREgA2siBWoiBiAESQ0BIANBlcLJgABqIQcC
QAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGmwsmAAE8NACAHIAYgBWpJDQQLAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQMg
ACAENgIMDAELIAAoAgAoAgAhBiACIAU2AiwgAiAHNgIoIAIgACACQShqQQFBASAGEYGAgIAAgICAgAAgAikDACIJQiCIpyIFQf//A3ENBiAJpyEFCyADIAVq
IgUgA0kNASAFIQMgBUERSQ0ACyACQRdqQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwMAIAJBCGpCqtWq1arVqtWqfzcDACACQqrVqtWq1arVqn83
AwAgAkG26AA7AB8gACACQR9qQQIQoICAgAAiBUH//wNxDQQDQCAAKAIMIgNBAWoiBUUNAQJAIAUgACgCCCIGSw0AIAMgBk8NBQJAIAAoAgQgA2oiA0GnwsmA
AE8NACADQQFqQabCyYAASw0DCyADQSk6AAAgACgCDEEBaiIDRQ0CIAAgAzYCDEEAIQUMBgsgACgCACgCACEDIAJBATYCLCACQabCyYAANgIoIAIgACACQShq
QQFBASADEYGAgIAAgICAgAAgAikDACIJQiCIpyIFQf//A3ENBUEAIQUgCadFDQAMBQsLAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAsgA0EBaiAGEIiAgIAA
AAsgAkEwaiSAgICAACAFC8wCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgQgA0E/cyIFaiIGIARJDQIgA0G76siAAGohBwJAAkAg
BiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQfrqyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2
AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSAD
SQ0CIAUhAyAFQT9JDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAuTBQMIfwF+An8j
gICAgABBEGsiAySAgICAAAJAAkAgACgCBCIEQQFqIgVFDQACQAJAAkAgACgCCCIGIAVJDQAgBiEHDAELIAYhBwNAQX8gB0EBdiAHakEFaiIEIAQgB0kbIgcg
BUkNAAsgAUIgiKchCCAAKAIAIQkgAachCgJAAkAgBkUNACAGrUIMfiIBQiCIpw0EQQAhBCADIAetQgx+IgtCIIinIgVBAEc6AAggBQ0AAkAgCiAJIAGnQQIg
C6ciDEEAIAgoAggRgoCAgACAgICAACINDQBBACEFDAILAkAgDEUNACANQQNxDQULAkAgDA0AQQAhBUGq1arVeiEEDAILIAwgDEEMbiIFQQxsaw0EIA0hBAwB
C0EAIQRBACEFCwJAAkAgBEUNACAAIAQ2AgAgBSEHDAELIAMgB61CDH4iAUIgiKciBEEARzoADAJAIARFDQBBGSEHDAMLAkACQCABpyIFDQBBfCEEDAELAkAg
CiAFQQJBACAIKAIAEYCAgIAAgICAgAAiBA0AQRkhBwwECwJAIAVFDQAgBEGqASAF/AsACyAEQQNxDQQLIAAoAgQiBSAHSw0EAkAgBCAAKAIAIgwgBUEMbCIF
ak8NACAMIAQgBWpJDQQLAkAgBUUNACAEIAwgBfwKAAALAkAgBkUNAAJAIAZBDGwiBUUNACAJQaoBIAX8CwALIAogCSAFQQJBACAIKAIMEYGAgIAAgICAgAAL
IAAgBDYCAAsgACAHNgIIIAAoAgQhBAsgBCAHTw0BIAAgBEEBajYCBCAAKAIAIARBDGxqIgdBCGogAkEIaigCADYCACAHIAIpAgA3AgBBACEHCyADQRBqJICA
gIAAIAcPCwALIAUgBxCIgICAAAALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYC
iBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCHhICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAg
ABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBygBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcoARQ0AIAJBqgFBygD8CwALIAFCygA3A5gQIAEg
AjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCHhICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEE
IAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECAB
IAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgC
pBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEo
ApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALzgICB38BfiOAgICA
AEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHIACADayIFaiIGIARJDQIgA0GLwMmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAE
aiIGQdPAyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2
AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcgASQ0ACyAAIAEoAgAgASgC
BCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL2QIBBH8jgICAgABBEGsiBCSAgICAAEEAIQVBgICoASEGAkAg
AkUNACABLQAAQd8ARg0AIAEgAmpBf2otAABB3wBGDQAgA0H/AXEhB0EAIQMCQANAAkAgAS0AACIFQd8ARg0AAkACQAJAIAVBUGoiBkH/AXFBCU0NAAJAIAVB
v39qQf8BcUEaSQ0AIAVBn39qQf8BcUEaTw0CIAVBqX9qIQYMAQsgBUFJaiEGCyAGQf8BcSIFIAdJDQELQYCAqAEhBgwDCwJAAkACQCADQf//A3EiAw0AQQAh
AwwBCyAEIAMgB2wiA0EQdiIGQQBHOgAMIAYNAQsgBCADQf//A3EgBWoiA0H//wNxIgUgA0c6AA4gBSADRg0BC0EAIQVBgIAEIQYMAwsgAUEBaiEBIAJBf2oi
Ag0AC0EAIQYLIANB//8DcSEFCyAAIAYgBXI2AQAgBEEQaiSAgICAAAuEAgMBfwJ+A38jgICAgABBEGsiAySAgICAAEIAIQRCgICAgKAFIQUCQCACRQ0AIAEt
AABB3wBGDQAgASACakF/ai0AAEHfAEYNAEEAIQYDQAJAIAEtAAAiB0HfAEYNAAJAIAdBUGpB/wFxIghBCU0NAEKAgICAoAUhBUEArSEEDAMLAkACQAJAIAYN
AEEAIQcMAQsgAyAGrUIKfiIFQiCIpyIHQQBHOgAIIAcNASAFpyEHCyADIAcgCGoiBiAHSToADCAGIAdPDQELQoCAgIAQIQUMAgsgAUEBaiEBIAJBf2oiAg0A
C0IAIQUgBq0hBAsgACAFIASENwIAIANBEGokgICAgAALlggDB38BfgF/I4CAgIAAQbAgayIBJICAgIAAAkBBjyBFDQAgAUEBakGqAUGPIPwLAAsgAUKAIDcC
mCAgAUG43sGAADYCkCAgASABQQFqNgKUIEEAIQICQAJAAkACQAJAA0AgASgCnCAiA0EXIAJrIgRqIgUgA0kNAiACQZiwyIAAaiEGAkACQCAFIAEoApggIgdL
DQAgBSAHSw0FAkAgASgClCAgA2oiBUGvsMiAAE8NACAGIAUgBGpJDQULAkAgBEUNACAFIAYgBPwKAAALIAEoApwgIgUgBGoiAyAFSQ0EIAEgAzYCnCAMAQsg
ASgCkCAoAgAhBSABIAQ2AqQgIAEgBjYCoCAgAUGoIGogAUGQIGogAUGgIGpBAUEBIAURgYCAgACAgICAACABKQOoICIIQoCAgIDw/z+DQgBSDQIgCKchBAsg
AiAEaiIEIAJJDQIgBCECIARBF0kNAAtBACECIAFBkCBqIAAoAgAgACgCBCIEIARBAC0AwNjJgAAiCUEgEJaAgIAAQf//A3ENAANAIAEoApwgIgMgAkEPcyIE
aiIFIANJDQIgAkGvsMiAAGohBgJAAkAgBSABKAKYICIHSw0AIAUgB0sNBgJAIAEoApQgIANqIgVBvrDIgABPDQAgBiAFIARqSQ0FCwJAIARFDQAgBSAGIAT8
CgAACyABKAKcICIFIARqIgMgBUkNBCABIAM2ApwgDAELIAEoApAgKAIAIQUgASAENgKkICABIAY2AqAgIAFBqCBqIAFBkCBqIAFBoCBqQQFBASAFEYGAgIAA
gICAgAAgASkDqCAiCEKAgICA8P8/g0IAUg0CIAinIQQLIAIgBGoiBCACSQ0CIAQhAiAEQQ9JDQALIAFBkCBqIAAoAgggACgCDCICIAIgCUEgEJaAgIAAQf//
A3ENAEEAIQIDQCABKAKcICIDQQsgAmsiBGoiBSADSQ0CIAJBvrDIgABqIQYCQAJAIAUgASgCmCAiB0sNACAFIAdLDQcCQCABKAKUICADaiIFQcmwyIAATw0A
IAYgBSAEakkNBQsCQCAERQ0AIAUgBiAE/AoAAAsgASgCnCAiBSAEaiIDIAVJDQQgASADNgKcIAwBCyABKAKQICgCACEFIAEgBDYCpCAgASAGNgKgICABQagg
aiABQZAgaiABQaAgakEBQQEgBRGBgICAAICAgIAAIAEpA6ggIghCgICAgPD/P4NCAFINAiAIpyEECyACIARqIgQgAkkNAiAEIQIgBEELSQ0ACyABKAKcICIC
IAEoApggIgFNDQEgAiABEIiAgIAAAAsgAUGBIGpBldPAgABPDQAgAUGQIGpBhtPAgABNGgsACyAFIAcQiICAgAAACyAFIAcQiICAgAAACyAFIAcQiICAgAAA
C8YEAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE2IANrIgVqIgYgBEkNAiADQfS6yIAAaiEHAkACQCAGIAAoAggiCEsNACAG
IAhLDQUCQCAAKAIEIARqIgZBqrvIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIA
IQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBNkkN
AAsgAEGw2MmAACABLQAAEKGEgIAAIgVB//8DcQ0AQQAhAwNAIAAoAgwiBEEXIANrIgVqIgYgBEkNAiADQaq7yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhL
DQYCQCAAKAIEIARqIgZBwbvIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYg
AiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBF0kNAAtB
ACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALqAMCB38BfiOAgICAAEHAAGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB
0gAgA2siBWoiBiAESQ0CIANBwbvIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGTvMiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAG
IAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgwgAiAHNgIIIAJBEGogACACQQhqQQFBASAGEYGAgIAAgICAgAAg
AikDECIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHSAEkNAAsgAkEQakEoaiABQShqKQIANwMAIAJBEGpBIGogAUEgaikCADcDACAC
QRBqQRhqIAFBGGopAgA3AwAgAkEQakEQaiABQRBqKQIANwMAIAJBEGpBCGogAUEIaikCADcDACACIAEpAgA3AxAgAkEQaiAAEKKEgIAAIQULIAJBwABqJICA
gIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEuIANrIgVqIgYgBEkNAiADQZO8yIAAaiEH
AkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBwbzIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0E
IAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAF
aiIFIANJDQIgBSEDIAVBLkkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACED
AkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQcG8yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBg73IgABPDQAgByAG
IAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEg
BhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEg
EJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQS8gA2siBWoi
BiAESQ0CIANBg73IgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGyvciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAAL
IAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIF
Qf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEvSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICA
gABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANBsr3IgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQg
BGoiBkH0vciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAH
NgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEo
AgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkAD
QCAAKAIMIgRBLCADayIFaiIGIARJDQIgA0H0vciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQaC+yIAATw0AIAcgBiAFakkNBQsC
QCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACA
gICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSxJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCI
gICAAAALzgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0GgvsiAAGohBwJAAkAgBiAAKAIIIghL
DQAgBiAISw0FAkAgACgCBCAEaiIGQeK+yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgC
ACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAF
QcIASQ0ACyAAIAEoAgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALzgICB38BfiOAgICAAEEQayIC
JICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0HivsiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQaS/
yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEI
aiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEoAgAgASgCBCIDIANB
AC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwi
BEExIANrIgVqIgYgBEkNAiADQaS/yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB1b/IgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAg
BiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIp
AwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBMUkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvO
AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQdW/yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhL
DQUCQCAAKAIEIARqIgZBl8DIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYg
AiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQAL
IAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABB
ACEDAkACQAJAA0AgACgCDCIEQTAgA2siBWoiBiAESQ0CIANBl8DIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHHwMiAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEwSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAF
DwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBLSADayIFaiIGIARJDQIgA0HHwMiAAGohBwJAAkAg
BiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQfTAyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2
AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSAD
SQ0CIAUhAyAFQS1JDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkAC
QANAIAAoAgwiBEElIANrIgVqIgYgBEkNAiADQfTAyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBmcHIgABPDQAgByAGIAVqSQ0F
CwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICA
AICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBJUkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAI
EIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQZnByIAAaiEHAkACQCAGIAAoAggi
CEsNACAGIAhLDQUCQCAAKAIEIARqIgZB28HIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAA
KAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSED
IAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBr
IgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSUgA2siBWoiBiAESQ0CIANB28HIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGA
wsiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJB
CGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUElSQ0ACyAAIAEQo4SAgAAhBQsgAkEQ
aiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANBgMLI
gABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHCwsiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIE
IAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEF
CyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAA
C7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBNCADayIFaiIGIARJDQIgA0HCwsiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQfbCyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTRJDQAL
IAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL0wYBCX8CQAJAIAFB//8DcSICIAAoAsQBIgMoAgAiBC8BqAIiBUsNACAEQRBqIQYC
QCACIAVHDQAgACgCyAEiAi0ABEEBcUUNAiADLwEEIgcgBC8BqgJPDQEgAy8BBiABQf//A3FPDQEgBCgCECIFIAQoAhhqIgQgBUkNASAEQQdxDQEgBEUNASAF
IAQgB0EDdGooAgBqIgQgBUkNASAEQQdxDQEgBEUNASAEIAFBf2pB//8DcUEDdGoiBCkDAEKAgICAgIADg0KAgICAgIADUg0CIAAgBiACIARBARCzgoCAAA8L
AkAgAUH//wNxIgJBAk8NAAJAIAAoAsgBLQAEQQJxRQ0AIAMvAQQiCCAELwGqAiIHTw0CIAMvAQYiCSAFTw0CIAQoAhAiAyAEKAIYaiIKIANJDQIgCkEHcQ0C
IApFDQIgAyAKIAhBA3RqKAIAaiIKIANJDQIgCkEHcQ0CIApFDQIgCikDAEKAgICAgIADg0KAgICAgIABUg0AAkACQCAIRQ0AIAhBf2ohAwwBCyAEKAIIIgRF
DQFBASEDA0ACQCADIAQvAaoCIgdLDQAgByADayEDDAILIAMgB2shAyAEKAIIIgQNAAwCCwsgA0H//wNxIgogB08NAiAJIAQvAagCIghPDQIgBCgCECIDIAQo
AhhqIgcgA0kNAiAHQQdxDQIgB0UNAiADIAcgCkEDdGoiCigCAGoiByADSQ0CIAdBB3ENAiAHRQ0CIAcgCEF/akH//wNxQQN0aiIDKQMAQoCAgICAgAODQoCA
gICAgANSDQAgACAEQRBqIAogA0EBELOCgIAACyABQf//A3FFDQILIAIgBU8NACAAKALEASIFLwEEIgMgBSgCACIELwGqAk8NACAFLwEGIAQvAagCIgJPDQAg
BCgCECIFIAQoAhhqIgQgBUkNACAEQQdxDQAgBEUNACAFIAQgA0EDdGooAgBqIgQgBUkNACAEQQdxDQAgBEUNAAJAIAFBf2pB//8DcSIBIAJPDQACQCAEIAFB
A3RqIgQpAwBCKoinQQNxDgQDAAMCAwsCQCABQQJqIgEgAksNACAAIAYgACgCyAEgBEECELOCgIAADwsgASACEIiAgIAAAAsgASACEIiAgIAACwALC+ADAwN/
AX4GfyOAgICAAEEwayIDJICAgIAAAkAgAUUNACABQQNxDQACQAJAAkAgAUF8aigCAEUNACABQXhqKAIAIgRBf2oiBSAESw0DIANBEGogAq1CgYCAgBB+IgZC
29HQhZra34FnhUIAIAZCrJri06PS8PofhUIAEK+FgIAAIAMgAykDGELb0dCFmtrfgWeFQgAgAykDEEKryPXFx6yHu6B/hUIAEK+FgIAAIAEgBSADKQMIIAMp
AwCFIgancSIHai0AACIIRQ0AIAFBcGohCSAGQjmIpyEKIARBf2ohCyABQXRqIQwDQCALIQQCQCAIwEF/Sg0AIAogCEH/AHFHDQAgASAMKAIAaiIIIAFJDQUg
CEEDcQ0FIAhFDQUgAiAIIAdBAnRqIggoAgBGDQMLIAdBAWoiCEUNBCABIAggBXEiB2otAAAiCEUNASAEQX9qIQsgBA0ACwtBACEEIANBLmpBADoAACADQQA7
ASxBACEBQQAhCAwBCyABIAkoAgBqIgQgAUkNASAEQQNxDQEgBEUNASAEIAdBA3RqIQRBASEBCyAAIAE6AAggACAENgIEIAAgCDYCACAAIAMvASw7AAkgAEEL
aiADQS5qLQAAOgAAIANBMGokgICAgAAPCwALhQIBA38CQAJAIAAoAgAiAkUNACACQQNxDQAgAiACQXRqKAIAaiIDIAJJDQAgA0EDcQ0AIANFDQAgASADayIE
IAFLDQAgAiAEQQJ2IgFqQQE6AAAgACgCACICRQ0AIAJBA3ENACACIAJBdGooAgBqIgMgAkkNACADQQNxDQAgA0UNACADIARqQarVqtV6NgIAIAAoAgAiAkUN
ACACQQNxDQAgAiACQXBqKAIAaiIDIAJJDQAgA0EDcQ0AIANFDQAgAyABQQN0akKq1arVqtWq1ap/NwIAIAAoAgAiAkUNACACQQNxDQAgAkF8aiIAKAIAIgJB
f2oiAyACTQ0BCwALIAAgAzYCAAv+BAMBfwF+Cn8jgICAgABBMGsiAySAgICAACADQRBqIAGtQoGAgIAQfiIEQtvR0IWa2t+BZ4VCACAEQqya4tOj0vD6H4VC
ABCvhYCAACADIAMpAxhC29HQhZra34FnhUIAIAMpAxBCq8j1xcesh7ugf4VCABCvhYCAAAJAIABFDQAgAEEDcQ0AIABBeGooAgAiBUF/aiIGIAVLDQAgAykD
CCADKQMAhSIEQjmIpyEHAkACQAJAIAAgBiAEp3EiCGoiCS0AACIKDQAgBSELDAELIABBdGohDEEBIQ0gBSELA0ACQAJAIArAQX9KDQAgByAKQf8AcUcNACAA
IAwoAgBqIgogAEkNBSAKQQNxDQUgCkUNBSABIAogCEECdGoiCigCAEcNASAAIABBcGooAgBqIg0gAEkNBSANQQNxDQUgDUUNBSADQQE6ACwgDSAIQQN0aiEN
IANBLGohAAwECyALIAVHDQAgCCAFIApB/wFxQQFGGyELCyAIQQFqIgpFDQMgACAKIAZxIghqIgktAAAiCkUNASAFIA1HIQ4gDUEBaiENIA4NAAsLIAAgC2og
CSALIAVJIgUbIAdBgAFyOgAAIAAgAEF0aigCAGoiCiAASQ0BIApBA3ENASAKRQ0BIAAgAEFwaigCAGoiDSAASQ0BIA1BA3ENASANRQ0BIAogCyAIIAUbIghB
AnRqIgpBqtWq1Xo2AgAgDSAIQQN0aiINQqrVqtWq1arVqn83AgAgAEF8aiIAKAIAQQFqIghFDQEgACAINgIAIANBADoALCADQSxqIQALAkAgAC0AAEEBcQ0A
IAogATYCAAsgDSACNwIAIANBMGokgICAgAAPCwAL4AMDA38BfgZ/I4CAgIAAQTBrIgMkgICAgAACQCABRQ0AIAFBA3ENAAJAAkACQCABQXxqKAIARQ0AIAFB
eGooAgAiBEF/aiIFIARLDQMgA0EQaiACrUKBgICAEH4iBkLb0dCFmtrfgWeFQgAgBkKsmuLTo9Lw+h+FQgAQr4WAgAAgAyADKQMYQtvR0IWa2t+BZ4VCACAD
KQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgASAFIAMpAwggAykDAIUiBqdxIgdqLQAAIghFDQAgAUFwaiEJIAZCOYinIQogBEF/aiELIAFBdGohDANAIAshBAJA
IAjAQX9KDQAgCiAIQf8AcUcNACABIAwoAgBqIgggAUkNBSAIQQNxDQUgCEUNBSACIAggB0ECdGoiCCgCAEYNAwsgB0EBaiIIRQ0EIAEgCCAFcSIHai0AACII
RQ0BIARBf2ohCyAEDQALC0EAIQQgA0EuakEAOgAAIANBADsBLEEAIQFBACEIDAELIAEgCSgCAGoiBCABSQ0BIARBAXENASAERQ0BQQEhASAEIAdBAXRqIQQL
IAAgAToACCAAIAQ2AgQgACAINgIAIAAgAy8BLDsACSAAQQtqIANBLmotAAA6AAAgA0EwaiSAgICAAA8LAAv+AQEDfwJAAkAgACgCACICRQ0AIAJBA3ENACAC
IAJBdGooAgBqIgMgAkkNACADQQNxDQAgA0UNACABIANrIgQgAUsNACACIARBAnYiAWpBAToAACAAKAIAIgJFDQAgAkEDcQ0AIAIgAkF0aigCAGoiAyACSQ0A
IANBA3ENACADRQ0AIAMgBGpBqtWq1Xo2AgAgACgCACICRQ0AIAJBA3ENACACIAJBcGooAgBqIgMgAkkNACADQQFxDQAgA0UNACADIAFBAXRqQarVAjsBACAA
KAIAIgJFDQAgAkEDcQ0AIAJBfGoiACgCACICQX9qIgMgAk0NAQsACyAAIAM2AgALnQUDAX8Bfgt/I4CAgIAAQTBrIgMkgICAgAAgA0EQaiACrUKBgICAEH4i
BELb0dCFmtrfgWeFQgAgBEKsmuLTo9Lw+h+FQgAQr4WAgAAgAyADKQMYQtvR0IWa2t+BZ4VCACADKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAACQAJAIAEoAgAi
BUUNACAFQQNxDQAgBUF4aigCACIGQX9qIgcgBksNACADKQMIIAMpAwCFIgRCOYinIQgCQAJAIAUgByAEp3EiCWoiCi0AACILDQAgBiEMDAELIAVBdGohDUEB
IQ4gBiEMA0ACQAJAIAvAQX9KDQAgCCALQf8AcUcNACAFIA0oAgBqIgsgBUkNBCALQQNxDQQgC0UNBCACIAsgCUECdGoiCygCAEcNASAFIAVBcGooAgBqIg4g
BUkNBCAOQQFxDQQgDkUNBCADQQE6ACggDiAJQQF0aiEJIANBKGohDgwFCyAMIAZHDQAgCSAGIAtB/wFxQQFGGyEMCyAJQQFqIgtFDQIgBSALIAdxIglqIgot
AAAiC0UNASAGIA5GIQ8gDkEBaiEOIA9FDQALCyAFIAxqIAogDCAGSSIGGyAIQYABcjoAACABKAIAIgtFDQAgC0EDcQ0AIAsgC0F0aigCAGoiDiALSQ0AIA5B
A3ENACAORQ0AIAsgC0FwaigCAGoiBSALSQ0AIAVBAXENACAFRQ0AIA4gDCAJIAYbIglBAnRqIgtBqtWq1Xo2AgAgBSAJQQF0aiIJQarVAjsBACABKAIAIg5F
DQAgDkEDcQ0AIA5BfGoiDigCAEEBaiIFRQ0AIA4gBTYCACADQQA6ACwgA0EsaiEODAELAAsgACAJNgIEIAAgCzYCACAAIA4tAAA6AAggA0EwaiSAgICAAAu5
CQMFfwF+AX8CQAJAIAEoAgAiBSABKAIIaiIGIAVJDQAgBkEHcQ0AIAZFDQAgBiACSw0AIAEvAZoCQX9qIgdB//8DcSIIIAdHDQAgCEEDdCAGaiACSQ0AIAUg
AigCAGoiBiAFSQ0AIAZBB3ENACAGRQ0AAkACQAJAAkACQCAERQ0AIAEvAZgCIgVFDQEgBiADSw0FIAMgBEEDdGoiCUF4aiAGIAVBA3RqQXhqSw0FIAIpAwAi
CkKAgICAwACDUA0EIAMhBiAEIQUDQAJAIAYpAwBCA4NCAVINACABIAYQrIOAgAALIAZBCGohBiAFQX9qIgUNAAsgBCAALwGMAUYNAiABKAIAIgUgAigCAGoi
BiAFSQ0FIAZBB3ENBSAGRQ0FAkAgAS8BmAIiBUUNAANAIAYpAwBCA4NCAVENBSAGQQhqIQYgBUF/aiIFDQALCyACIAIpAwBC/////79/gyIKNwMADAQLQQBB
ABCIgICAAAALQQBBABCIgICAAAALIAIgAikDAEL/////v3+DIgo3AwAMAQsgAikDACEKCwJAIApCgICAgIACg1ANACAEQQEgBEEBSxshBSADIQYDQAJAIAYt
AAVBIHFFDQAgASAGEK2DgIAACyAGQQhqIQYgBUF/aiIFDQALAkAgBCAALwGMAUcNACACIAIpAwBC//////99gyIKNwMADAELIAEoAgAiBSACKAIAaiIGIAVJ
DQEgBkEHcQ0BIAZFDQECQAJAIAEvAZgCIgVFDQADQCAGLQAFQSBxDQIgBkEIaiEGIAVBf2oiBQ0ACwsgAiACKQMAQv//////fYMiCjcDAAwBCyACKQMAIQoL
AkAgCkKAgICAgAGDUA0AIARBASAEQQFLGyEHIAMhBgNAAkAgBikDACIKQoCAgOD//wCDUA0AIAEoAjggCkIaiKdB//8DcSILTQ0DIAEoAgAiCCABKAIwaiIF
IAhJDQMgBUEDcQ0DIAVFDQMgBSALQSRsaiIFLwEgIghFDQMgBUEgaiAIQX9qIgU7AQAgBUH//wNxDQAgASgCNCIFQX9qIgggBUsNAyABIAg2AjQLIAZBCGoh
BiAHQX9qIgcNAAsCQCAEIAAvAYwBRg0AIAEoAgAiBSACKAIAaiIGIAVJDQIgBkEHcQ0CIAZFDQIgAS8BmAIiAUUNAANAIAYoAQJBgPj/H3ENAiAGQQhqIQYg
AUF/aiIBDQALCyACIAIpAwBC//////9+gzcDAAtCACEKIAAvAdQBRQ0BIABBqAFqLwEAIABBqgFqLQAAQRB0ciEBAkACQCAAQawBai0AAA4DAwABAgsgAUEC
dEH8B3FBAnKtIQoMAgsgAUGAgPwHcUEQdq1CEoYgAUECdEH8/w9xQQNyrYQhCgwBCwALAkAgBEH/////AXFFDQAgBEF/akH/////AXEhBgJAIARBB3EiAUUN
AANAIAMgCjcDACADQQhqIQMgAUF/aiIBDQALCyAGQQdJDQADQCADIAo3AwAgA0E4aiAKNwMAIANBMGogCjcDACADQShqIAo3AwAgA0EgaiAKNwMAIANBGGog
CjcDACADQRBqIAo3AwAgA0EIaiAKNwMAIANBwABqIgMgCUcNAAsLC4QEAwF/AX4JfyAAQQA6ANkBAkACQCAAKALIASIBKQMAIgJCgICAgBCDQgBRDQAgASAC
Qv////9vgzcDACAAKALEASIDLwEEIgRBAWoiBUH//wNxIgYgBUcNASADKAIAIgcvAaoCIgggBkkNASADLwEGIQkCQAJAAkAgCCAFQf//A3FGDQAgCCEKIAch
BgwBCyAHKAIMIgZFDQFBASELAkADQCALIAYvAaoCIgpNDQEgCyAKayELIAYoAgwiBg0ACyAKDQIMBAsgC0F/aiIFIAtLDQMLIAVB//8DcSIHIApPDQIgCUH/
/wNxIAYvAagCTw0CIAYoAhAiCyAGKAIYaiIGIAtJDQIgBkEHcQ0CIAZFDQIgCyAGIAdBA3RqIgooAgBqIgYgC0kNAiAGQQdxDQIgBkUNAiAKIAopAwBC////
/1+DNwMAIAAoAsQBIgMvAQQhBCADKAIAIgcvAaoCIQgLIARB//8DcSIKIAhB//8DcU8NASADLwEGIAcvAagCIgNPDQEgBygCECIGIAcoAhhqIgsgBkkNASAL
QQdxDQEgC0UNASAGIAsgCkEDdGooAgBqIgsgBkkNASALQQdxDQEgC0UNASALIANBf2pB//8DcUEDdGoiBikDAEKAgICAgIADg0KAgICAgIADUg0AIAAgB0EQ
aiABIAZBARCzgoCAAAsPCwALzgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0H2wsiAAGohBwJA
AkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQbjDyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAA
IAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoi
BSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEoAgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38B
fiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEsIANrIgVqIgYgBEkNAiADQbjDyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAA
KAIEIARqIgZB5MPIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIE
IAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBLEkNAAsgACABEKOE
gIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAuLAgEHfyAAKAIYIgItANkBIQMgAi8B0gEhBCACLwHQASEFAkACQAJAAkACQCAALwH8ESIGIAAv
AYASIgdyQf//A3ENACAALwH6EUF/aiIIQf//A3EgCEcNAkEAIQcgAC8BghIgCEH//wNxRw0AIAAvAf4RIgZBAWoiB0H//wNxIAdHDQIgAkEAIAYQ1YCAgAAg
ASAHQf//A3EiAiABIAJJGyICRQ0DDAELIAIgByAGENWAgIAAIAAgARD7gYCAAAwCCwNAIAAoAhgQtYOAgAAiB0H//wNxDQMgAkF/aiICRQ0CDAALCwALQQAh
BwsgACgCGCAFIAQQ1YCAgAAgACgCGCADQQFxOgDZASAHC8wEAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYg
BEkNAiADQdPgyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjeHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAA
KAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH/
/wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBOkkNAAtBACEDIABBAEEAQSIQnoSAgAAiBUH//wNxDQADQCAAKAIMIgRBBSADayIFaiIGIARJDQIgA0GN
usiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0GAkAgACgCBCAEaiIGQZK6yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVq
IgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmn
IQULIAMgBWoiBSADSQ0CIAUhAyAFQQVJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALzgICB38B
fiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0Hkw8iAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAg
ACgCBCAEaiIGQabEyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYC
BCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEo
AgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJA
AkACQANAIAAoAgwiBEEuIANrIgVqIgYgBEkNAiADQabEyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB1MTIgABPDQAgByAGIAVq
SQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGB
gICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBLkkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsg
BiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQdTEyIAAaiEHAkACQCAGIAAo
AggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBlsXIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwB
CyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIg
BSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAA
QRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTQgA2siBWoiBiAESQ0CIANBlsXIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoi
BkHKxciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIA
IAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE0SQ0ACyAAIAEQo4SAgAAhBQsg
AkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANB
ysXIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGMxsiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAF
aiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICA
gAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBMyADayIFaiIGIARJDQIgA0GMxsiAAGohBwJAAkAgBiAAKAIIIghLDQAg
BiAISw0FAkAgACgCBCAEaiIGQb/GyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgC
ACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTNJ
DQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALzgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHC
ACADayIFaiIGIARJDQIgA0G/xsiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQYHHyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEoAgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICA
gIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE2IANrIgVqIgYgBEkNAiADQYHHyIAAaiEH
AkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBt8fIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0E
IAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAF
aiIFIANJDQIgBSEDIAVBNkkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACED
AkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQbfHyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB+cfIgABPDQAgByAG
IAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEg
BhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEg
EJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSYgA2siBWoi
BiAESQ0CIANB+cfIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGfyMiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAAL
IAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIF
Qf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEmSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICA
gABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANBn8jIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQg
BGoiBkHhyMiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAH
NgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEo
AgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkAD
QCAAKAIMIgRBLyADayIFaiIGIARJDQIgA0HhyMiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZDJyIAATw0AIAcgBiAFakkNBQsC
QCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACA
gICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQS9JDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCI
gICAAAALzAQCB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQAJAA0AgACgCDCIEQTogA2siBWoiBiAESQ0CIANB0+DIgABqIQcCQAJAIAYgACgCCCII
Sw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGN4ciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAo
AgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMg
BUE6SQ0AC0EAIQMgAEEAQQBBARCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiADQY26yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhL
DQYCQCAAKAIEIARqIgZBkrrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYg
AiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBUkNAAsg
ACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJA
A0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQZDJyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB0snIgABPDQAgByAGIAVqSQ0F
CwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICA
AICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAA
IQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTQgA2siBWoiBiAESQ0C
IANB0snIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGGysiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwi
BiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3EN
AiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE0SQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsi
AiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBJiADayIFaiIGIARJDQIgA0GGysiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQazK
yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEI
aiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSZJDQALIAAgARCjhICAACEFCyACQRBq
JICAgIAAIAUPCwALIAYgCBCIgICAAAALzgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0GsysiA
AGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQe7KyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQg
BkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQUL
IAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEoAgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
tQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEmIANrIgVqIgYgBEkNAiADQe7KyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhL
DQUCQCAAKAIEIARqIgZBlMvIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYg
AiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBJkkNAAsg
ACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANr
IgVqIgYgBEkNAiADQZTLyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB1svIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8
CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIg
iKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAg
BQ8LAAsgBiAIEIiAgIAAAAvCAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSkgA2siBWoiBiAESQ0CIANB1svIgABqIQcCQAJA
IAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH/y8iAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAE
NgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUg
A0kNAiAFIQMgBUEpSQ0ACyAAIAEvAQBBCkEAQbDYyYAAEJWAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIk
gICAgABBACEDAkACQAJAA0AgACgCDCIEQSwgA2siBWoiBiAESQ0CIANB/8vIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGrzMiA
AE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGog
ACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEsSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSA
gICAACAFDwsACyAGIAgQiICAgAAAC7wCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKSADayIFaiIGIARJDQIgA0GrzMiAAGoh
BwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQdTMyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkN
BCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMg
BWoiBSADSQ0CIAUhAyAFQSlJDQALIABBAEEAIAEtAAAQpISAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICAgABBEGsiAiSA
gICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANB1MzIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGWzciA
AE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGog
ACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEoAgQiAyADQQAt
AMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB
KyADayIFaiIGIARJDQIgA0GWzciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQcHNyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQStJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALhAIB
AX9BASEBAkACQAJAIABB//8DcSIAQecHSg0AIABBf2oORQIBAgICAgICAgEBAgEBAQEBAQEBAQEBAQIBAQEBAQEBAQEBAQEBAQIBAQEBAgECAQEBAQEBAQEB
AQEBAQEBAQEBAgEBAgELAkAgAEGYeGoOMgIBAgICAgICAQEBAQEBAQICAQEBAQEBAQEBAQEBAQEBAQEBAgIBAQIBAQEBAQIBAgICAAsCQCAAQaxwag4tAgEB
AQEBAQEBAQEBAQEBAQEBAQEBAQICAQEBAgEBAQEBAQEBAQEBAQEBAQECAAsgAEH+/31qIgBBEksNAEEBIAB0QYWIEHENAQtBACEBCyABC8ICAgd/AX4jgICA
gABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBJSADayIFaiIGIARJDQIgA0HBzciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAE
aiIGQebNyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2
AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSVJDQALIAAgAS8BAEEKQQBB
sNjJgAAQlYCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC6YBAQV/AkACQCAAKAIEIgFBf2oiAiABSw0AQQAhAwJAIAAoAgwiBCACTw0AQQAh
A0EAQRggBGsiAiACQRhLGyECIAEgBEF/c2ohBSAAQQpqIQEDQCACIANGDQIgAC8BCCABLQAAQRB0ciAEIANqdkEBcUUNASAFIANBAWoiA0cNAAsgBSEDCyAD
QQFqIgNFDQAgBCADaiIDIARPDQELAAsgACADNgIMC6EDAQJ/I4CAgIAAQRBrIgUkgICAgAACQAJAIANBBU8NACAAQgA3AgAgAEEQakIANwIAIABBCGpCADcC
AAwBCwJAAkAgAi8BAkECRw0AAkAgBEEBcQ0AIAEoAgwiA0EEaiIEIANJDQEgASAENgIMIABBAToAFCAAQRU6ABAgAEECaiACLwEIOgAAIAAgAi8BBkEIdCAC
LQAEcjsBACAFQRU6AAQMAwsCQAJAIAEQ54OAgABBfWoOAgEAAwsgASgCDCIEQQVqIgYgBEkNASABIAY2AgwCQCADQQVGDQAgAEEBOgAUIABBFToAECAAQQJq
IAIvAQo6AAAgACACLwEIQQh0IAItAAZyOwEAIAVBFToADAwEC0EFQQUQiICAgAAMAQsgASgCDCIDQQRqIgQgA0kNACABIAQ2AgwgAEEBOgAUIABBFToAECAA
QQJqIAIvAQg6AAAgACACLwEGQQh0IAItAARyOwEAIAVBFToACAwCCwALIAEQ04KAgAAgAEEQakIANwIAIABBCGpCADcCACAAQgA3AgALIAVBEGokgICAgAAL
oQMBAn8jgICAgABBEGsiBSSAgICAAAJAAkAgA0EFTw0AIABCADcCACAAQRBqQgA3AgAgAEEIakIANwIADAELAkACQCACLwECQQJHDQACQCAEQQFxDQAgASgC
DCIDQQRqIgQgA0kNASABIAQ2AgwgAEEBOgAUIABBFjoAECAAQQJqIAIvAQg6AAAgACACLwEGQQh0IAItAARyOwEAIAVBFjoABAwDCwJAAkAgARDng4CAAEF9
ag4CAQADCyABKAIMIgRBBWoiBiAESQ0BIAEgBjYCDAJAIANBBUYNACAAQQE6ABQgAEEWOgAQIABBAmogAi8BCjoAACAAIAIvAQhBCHQgAi0ABnI7AQAgBUEW
OgAMDAQLQQVBBRCIgICAAAwBCyABKAIMIgNBBGoiBCADSQ0AIAEgBDYCDCAAQQE6ABQgAEEWOgAQIABBAmogAi8BCDoAACAAIAIvAQZBCHQgAi0ABHI7AQAg
BUEWOgAIDAILAAsgARDTgoCAACAAQRBqQgA3AgAgAEEIakIANwIAIABCADcCAAsgBUEQaiSAgICAAAuhAwECfyOAgICAAEEQayIFJICAgIAAAkACQCADQQVP
DQAgAEIANwIAIABBEGpCADcCACAAQQhqQgA3AgAMAQsCQAJAIAIvAQJBAkcNAAJAIARBAXENACABKAIMIgNBBGoiBCADSQ0BIAEgBDYCDCAAQQE6ABQgAEEI
OgAQIABBAmogAi8BCDoAACAAIAIvAQZBCHQgAi0ABHI7AQAgBUEIOgAEDAMLAkACQCABEOeDgIAAQX1qDgIBAAMLIAEoAgwiBEEFaiIGIARJDQEgASAGNgIM
AkAgA0EFRg0AIABBAToAFCAAQQg6ABAgAEECaiACLwEKOgAAIAAgAi8BCEEIdCACLQAGcjsBACAFQQg6AAwMBAtBBUEFEIiAgIAADAELIAEoAgwiA0EEaiIE
IANJDQAgASAENgIMIABBAToAFCAAQQg6ABAgAEECaiACLwEIOgAAIAAgAi8BBkEIdCACLQAEcjsBACAFQQg6AAgMAgsACyABENOCgIAAIABBEGpCADcCACAA
QQhqQgA3AgAgAEIANwIACyAFQRBqJICAgIAAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFB
uN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQmYSAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQL
IAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcwAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHMAEUNACACQaoBQcwA/AsACyABQswA
NwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQmYSAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAg
ASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAi
AjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGs
EA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgC
jBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC7UCAgd/
AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBLSADayIFaiIGIARJDQIgA0HmzciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAg
ACgCBCAEaiIGQZPOyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYC
BCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQS1JDQALIAAgARCj
hICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALwgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEyIANrIgVqIgYg
BEkNAiADQZPOyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBxc7IgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAA
KAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH/
/wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBMkkNAAsgACABLQAAQQpBAEGw2MmAABCVgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
zgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0HFzsiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQYfPyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0A
CyAAIAEoAgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAA
QQAhAwJAAkACQANAIAAoAgwiBEE3IANrIgVqIgYgBEkNAiADQYfPyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBvs/IgABPDQAg
ByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEB
QQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBN0kNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAg
BQ8LAAsgBiAIEIiAgIAAAAvNBAIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBMSADayIFaiIGIARJDQIgA0HcuciAAGohBwJA
AkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQY26yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAA
IAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoi
BSADSQ0CIAUhAyAFQTFJDQALQQAhAyAAQQBBAEHBABCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiADQY26yIAAaiEHAkACQCAGIAAo
AggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBkrrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwB
CyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIg
BSEDIAVBBUkNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAvCAgIHfwF+I4CAgIAAQRBrIgIkgICA
gABBACEDAkACQAJAA0AgACgCDCIEQTIgA2siBWoiBiAESQ0CIANBvs/IgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHwz8iAAE8N
ACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACAC
QQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEySQ0ACyAAIAEtAABBCkEAQbDYyYAAEJWAgIAA
IQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkN
AiADQfDPyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBstDIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIM
IgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNx
DQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAI
EIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQbLQyIAAaiEHAkACQCAGIAAoAggi
CEsNACAGIAhLDQUCQCAAKAIEIARqIgZB9NDIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAA
KAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSED
IAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBr
IgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSkgA2siBWoiBiAESQ0CIANB9NDIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGd
0ciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJB
CGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEpSQ0ACyAAIAEQo4SAgAAhBQsgAkEQ
aiSAgICAACAFDwsACyAGIAgQiICAgAAAC8cGAQF/AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQCABQf//A3EiAkHnB0oNACACQX9qDkUBJQIDBAUGBwglJQklJSUlJSUlJSUlJSUKJSUlJSUlJSUlJSUlJSULJSUlJQwlDSUlJSUl
JSUlJSUlJSUlJSUlJQ4lJQ8lCwJAIAJBmHhqDjIQJRESExQVFiUlJSUlJSUXGCUlJSUlJSUlJSUlJSUlJSUlJRkaJSUbJSUlJSUcJR0eHwALAkAgAkGscGoO
LSAlJSUlJSUlJSUlJSUlJSUlJSUlJSUhIiUlJSMlJSUlJSUlJSUlJSUlJSUlJAALAkACQAJAAkAgAkH+/31qDhMAKAEoKCgoKCgoAigoKCgoKCgDKAsgACgC
AA8LIAAtAABBAnFBAXYPCyAALQAAQQRxQQJ2DwsgAC0AAEEIcUEDdg8LIAAtAABBEHFBBHYPCyAALQAAQSBxQQV2DwsgAC0AAEHAAHFBBnYPCyAALQAAQQd2
DwsgAC0AAUEBcQ8LIAAtAAFBAnFBAXYPCyAALQABQQRxQQJ2DwsgAC0AAUEIcUEDdg8LIAAtAAFBEHFBBHYPCyAALQABQSBxQQV2DwsgAC0AAUHAAHFBBnYP
CyAALQABQQd2DwsgAC0AAkEBcQ8LIAAtAAJBAnFBAXYPCyAALQACQQRxQQJ2DwsgAC0AAkEIcUEDdg8LIAAtAAJBEHFBBHYPCyAALQACQSBxQQV2DwsgAC0A
AkHAAHFBBnYPCyAALQACQQd2DwsgAC0AA0EBcQ8LIAAtAANBAnFBAXYPCyAALQADQQRxQQJ2DwsgAC0AA0EIcUEDdg8LIAAtAANBEHFBBHYPCyAALQADQSBx
QQV2DwsgAC0AA0HAAHFBBnYPCyAALQADQQd2DwsgAC0ABEEBcQ8LIAAtAARBAnFBAXYPCyAALQAEQQRxQQJ2DwsgAC0ABEEIcUEDdg8LIAAtAARBEHFBBHYP
CyAALQAEQSBxQQV2DwsgAC0ABEHAAHFBBnYPCyAAQQRqMQAAQgeIpw8LIAEQ0YKAgABBAXEaAAvYDgIJfwF+I4CAgIAAQeAQayICJICAgIAAIAJCqtWq1arV
qtWqfzcABSACQqrVqtWq1arVqn83AwAgAkINNwIcIAJBuN7BgAA2AhQgAiACNgIYIAIgAUH//wFxOwHUEEGi3sGAAEH/ocCAACABQYCAAnEiAxshBCABQRB2
IQUgA0EPdkEBcyEGQQAhAQJAAkACQAJAAkACQAJAAkACQAJAA0AgAigCICIHQQIgAWsiA2oiCCAHSQ0DIAFBybDIgABqIQkCQAJAIAggAigCHCIKSw0AIAgg
CksNBgJAIAIoAhggB2oiCEHLsMiAAE8NACAJIAggA2pJDQYLAkAgA0UNACAIIAkgA/wKAAALIAIoAiAiCCADaiIHIAhJDQUgAiAHNgIgDAELIAIoAhQoAgAh
CCACIAM2AqwQIAIgCTYCqBAgAkEoaiACQRRqIAJBqBBqQQFBASAIEYGAgIAAgICAgAAgAikDKCILQiCIpyIDQf//A3ENAiALpyEDCyABIANqIgMgAUkNAyAD
IQEgA0ECSQ0ACyACQRRqIAQgBiAGQQAtAMDYyYAAQSAQloCAgAAiA0H//wNxDQAgAkEUaiACLwHUEEH//wFxQQpBAEGw2MmAABCVgICAACIDQf//A3ENAANA
IAIoAiAiAUEBaiIDRQ0DAkACQCADIAIoAhwiCEsNACABIAhPDQcCQCACKAIYIAFqIgFBzLDIgABPDQAgAUEBakHLsMiAAEsNBgsgAUE7OgAAIAIoAiBBAWoi
AUUNBSACIAE2AiAMAQsgAigCFCgCACEBIAJBATYCrBAgAkHLsMiAADYCqBAgAkEoaiACQRRqIAJBqBBqQQFBASABEYGAgIAAgICAgAAgAikDKCILQiCIpyID
Qf//A3ENAiALp0UNAQsLQQAhASACQRRqIAVB/wFxQQpBAEGw2MmAABCVgICAACIDQf//A3ENAANAIAIoAiAiB0ECIAFrIgNqIgggB0kNAyABQczlyIAAaiEJ
AkACQCAIIAIoAhwiCksNACAIIApLDQgCQCACKAIYIAdqIghBzuXIgABPDQAgCSAIIANqSQ0GCwJAIANFDQAgCCAJIAP8CgAACyACKAIgIgggA2oiByAISQ0F
IAIgBzYCIAwBCyACKAIUKAIAIQggAiADNgKsECACIAk2AqgQIAJBKGogAkEUaiACQagQakEBQQEgCBGBgICAAICAgIAAIAIpAygiC0IgiKciA0H//wNxDQIg
C6chAwsgASADaiIDIAFJDQMgAyEBIANBAkkNAAsgAigCICIBIAIoAhwiA0sNBiABQQ1JDQEgAUENEIiAgIAAAAsgAiADOwEmAkBBgBBFDQAgAkEoakGqAUGA
EPwLAAsgAkKAEDcCsBAgAkG43sGAADYCqBAgAiACQShqNgKsEAJAAkAgAkGoEGogAkEmahCahICAAEH//wNxDgMAAwEDCyACKAK0ECIBIAIoArAQIgNLDQgg
AigCrBAgARCAgICAAAwJC0EADQhBACkD4PrJgAAiC6dBO0EAQQAgC0IgiKcoAgARgICAgACAgICAACIBRQ0IIAFCqtWq1arVqtWqfzcAACABQRBqQqrVqtWq
1arVqn83AAAgAUEIakKq1arVqtWq1ap/NwAAIAFBN2pBqtWq1Xo2AAAgAUEwakKq1arVqtWq1ap/NwAAIAFBKGpCqtWq1arVqtWqfzcAACABQSBqQqrVqtWq
1arVqn83AAAgAUEYakKq1arVqtWq1ap/NwAAIAJCOzcDuBAgAiABNgK0ECACQdyVx4AANgKwECACIAs3A6gQAkACQCACQagQakEIaiACQSZqEJqEgIAAQf//
A3EOAwADAQMLIAIoArwQIgEgAigCuBAiA0sNByACQgA3A7gQIAIoArQQIQggAkGq1arVejYCtBAgAiADNgLcECACIAE2AtgQIAIgCDYC1BAgAkHEEGogAkHU
EGogAkGoEGoQuICAgAAgAiACKALcECIBNgK4ECACIAIoAtQQIgM2ArQQIAIgAigC2BA2ArwQAkAgAUUNACACKAKoECEIIAIoAqwQIQcCQCABRQ0AIANBqgEg
AfwLAAsgCCADIAFBAEEAIAcoAgwRgYCAgACAgICAAAsgAi8BzBANCSACKALEECIDIAIoAsgQIgEQgICAgAAgAUUNCQJAIAFFDQAgA0GqASAB/AsACyACIAMg
AUEAIAIQuYCAgAAMCQsgAigCuBAiAUUNCCACKAKoECEIIAIoAqwQIQcgAigCtBAhAwJAIAFFDQAgA0GqASAB/AsACyAIIAMgAUEAQQAgBygCDBGBgICAAICA
gIAADAgLIAIgAWpBADoAACAAKAIEIgNFDQcgACACIAEgAxGFgICAAICAgIAADAcLAAsgCCAKEIiAgIAAAAsgAUEBaiAIEIiAgIAAAAsgCCAKEIiAgIAAAAsg
ASADEIiAgIAAAAsgASADEIiAgIAAAAsgASADEIiAgIAAAAsgAkHgEGokgICAgAALwgICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwi
BEEtIANrIgVqIgYgBEkNAiADQZ3RyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBytHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAg
BiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIp
AwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBLUkNAAsgACABLwEAQQpBAEGw2MmAABCVgICAACEFCyACQRBqJICAgIAAIAUPCwAL
IAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEzIANrIgVqIgYgBEkNAiADQcrRyIAAaiEHAkACQCAGIAAo
AggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB/dHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwB
CyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIg
BSEDIAVBM0kNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0Ag
ACgCDCIEQTUgA2siBWoiBiAESQ0CIANB/dHIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGy0siAAE8NACAHIAYgBWpJDQULAkAg
BUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICA
gAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE1SQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICA
gAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANBstLIgABqIQcCQAJAIAYgACgCCCIISw0A
IAYgCEsNBQJAIAAoAgQgBGoiBkH00siAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAo
AgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHC
AEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSA
gICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKiADayIFaiIGIARJDQIgA0H00siAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZ7TyIAA
Tw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAA
IAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSpJDQALIAAgARCjhICAACEFCyACQRBqJICA
gIAAIAUPCwALIAYgCBCIgICAAAALzAQCB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQAJAA0AgACgCDCIEQTogA2siBWoiBiAESQ0CIANB0+DIgABq
IQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGN4ciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJ
DQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyAD
IAVqIgUgA0kNAiAFIQMgBUE6SQ0AC0EAIQMgAEEAQQBBLxCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiADQY26yIAAaiEHAkACQCAG
IAAoAggiCEsNACAGIAhLDQYCQCAAKAIEIARqIgZBkrrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYC
DAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJ
DQIgBSEDIAVBBUkNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAvCAgIHfwF+I4CAgIAAQRBrIgIk
gICAgABBACEDAkACQAJAA0AgACgCDCIEQS0gA2siBWoiBiAESQ0CIANBntPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHL08iA
AE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGog
ACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEtSQ0ACyAAIAEvAQBBCkEAQbDYyYAAEJWA
gIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTIgA2siBWoiBiAE
SQ0CIANBy9PIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH908iAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAo
AgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//
A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEySQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7cCAgd/AX4jgICAgABB
EGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANB/dPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoi
BkG/1MiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIA
IAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABEKOEgIAAIQUL
IAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSogA2siBWoiBiAESQ0CIANB
v9TIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHp1MiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAF
aiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUEqSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC8ICAgd/AX4jgICAgABBEGsiAiSA
gICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKiADayIFaiIGIARJDQIgA0Hp1MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZPVyIAA
Tw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAA
IAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSpJDQALIAAgAS8BAEEKQQBBsNjJgAAQlYCA
gAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBMCADayIFaiIGIARJ
DQIgA0GT1ciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQcPVyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgC
DCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8D
cQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTBJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICA38BfiOAgICAAEHQ
AGsiAiSAgICAAAJAAkAgAUL/////D1YNACAAIAGnQQpBAEGw2MmAABCVgICAACEDDAELQcEAIQQCQEHBAEUNACACQQ9qQaoBQcEA/AsACwJAA0AgASEFIARB
fmoiAyAESw0BIAJBD2ogA2ogBSAFQuQAgCIBQuQAfn2nQQF0QY2iwIAAai8AADsAACADIQQgBUKPzgBWDQALAkACQCAFQugHWg0AIANBf2oiBCADSw0CIAJB
D2ogBGogAadBMHI6AAAMAQsgA0F+aiIEIANLDQEgAkEPaiAEaiABp0EBdEGNosCAAGovAAA7AAALIAAgAkEPaiAEakHBACAEayIDIANBAC0AwNjJgABBIBCW
gICAACEDDAELAAsgAkHQAGokgICAgAAgAwu4AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTkgA2siBWoiBiAESQ0CIANBpNbJ
gABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHd1smAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIE
IAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEF
CyADIAVqIgUgA0kNAiAFIQMgBUE5SQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSA
gICAAEEAIQMCQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0HQ1ciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQYrWyIAA
Tw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAA
IAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTpJDQALIAAgARCjhICAACEFCyACQRBqJICA
gIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYgBEkNAiADQYrWyIAAaiEH
AkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBxNbIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0E
IAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAF
aiIFIANJDQIgBSEDIAVBOkkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACED
AkACQAJAA0AgACgCDCIEQTogA2siBWoiBiAESQ0CIANBxNbIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkH+1siAAE8NACAHIAYg
BWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAG
EYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE6SQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsA
CyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0H+1siAAGohBwJAAkAgBiAA
KAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQbjXyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwM
AQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0C
IAUhAyAFQTpJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANA
IAAoAgwiBEE9IANrIgVqIgYgBEkNAiADQbjXyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB9dfIgABPDQAgByAGIAVqSQ0FCwJA
IAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICA
gIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBPUkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiA
gIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQT4gA2siBWoiBiAESQ0CIANB9dfIgABqIQcCQAJAIAYgACgCCCIISw0A
IAYgCEsNBQJAIAAoAgQgBGoiBkGz2MiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAo
AgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE+
SQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB
MiADayIFaiIGIARJDQIgA0Gz2MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQeXYyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTJJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtwIC
B38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0Hl2MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0F
AkAgACgCBCAEaiIGQafZyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIg
BTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAA
IAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC80EAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkACQANAIAAoAgwiBEExIANr
IgVqIgYgBEkNAiADQdy5yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjbrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8
CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIg
iKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBMUkNAAtBACEDIABBAEEAQcIAEJ6EgIAAIgVB//8DcQ0AA0AgACgCDCIEQQUgA2siBWoiBiAE
SQ0CIANBjbrIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBgJAIAAoAgQgBGoiBkGSusiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAo
AgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//
A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEFSQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAACyAGIAgQiICAgAAA
C7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBNCADayIFaiIGIARJDQIgA0Gn2ciAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQdvZyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTRJDQAL
IAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEzIANr
IgVqIgYgBEkNAiADQdvZyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjtrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8
CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIg
iKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBM0kNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+
I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQS8gA2siBWoiBiAESQ0CIANBjtrIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAo
AgQgBGoiBkG92siAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQg
AiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEvSQ0ACyAAIAEQo4SA
gAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBJiADayIFaiIGIARJ
DQIgA0G92siAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQePayIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgC
DCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8D
cQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSZJDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQ
ayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEEvIANrIgVqIgYgBEkNAiADQePayIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB
ktvIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACAC
QQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBL0kNAAsgACABEKOEgIAAIQULIAJB
EGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQS0gA2siBWoiBiAESQ0CIANBktvI
gABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkG/28iAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIE
IAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEF
CyADIAVqIgUgA0kNAiAFIQMgBUEtSQ0ACyAAIAEQo4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICA
AEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANBv9vIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGB3MiAAE8N
ACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACAC
QQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDY
yYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBLiAD
ayIFaiIGIARJDQIgA0GB3MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQa/cyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF
/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglC
IIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQS5JDQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALzgICB38B
fiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0Gv3MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAg
ACgCBCAEaiIGQfHcyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYC
BCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEo
AgAgASgCBCIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALtQICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJA
AkACQANAIAAoAgwiBEEmIANrIgVqIgYgBEkNAiADQfHcyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBl93IgABPDQAgByAGIAVq
SQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGB
gICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBJkkNAAsgACABEKOEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsg
BiAIEIiAgIAAAAvOAgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQZfdyIAAaiEHAkACQCAGIAAo
AggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB2d3IgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwB
CyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIg
BSEDIAVBwgBJDQALIAAgASgCACABKAIEIgMgA0EALQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu1AgIHfwF+I4CAgIAA
QRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQSYgA2siBWoiBiAESQ0CIANB2d3IgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoi
BkH/3ciAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIA
IAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEmSQ0ACyAAIAEQo4SAgAAhBQsg
AkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC84CAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBwgAgA2siBWoiBiAESQ0CIANB
/93IgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkHB3siAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAF
aiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUHCAEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICA
gAAAC7UCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBKyADayIFaiIGIARJDQIgA0HB3siAAGohBwJAAkAgBiAAKAIIIghLDQAg
BiAISw0FAkAgACgCBCAEaiIGQezeyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgC
ACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQStJ
DQALIAAgARCjhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALsg4DBn8BfgF/I4CAgIAAQSBrIgIkgICAgAAgASgCBCEDIAEoAgAhBAJAAkAC
QAJAAkACQAJAAkACQCABLQAIQQdxDggAAQcGBQQDAgALQQAhASAAKAIYLwHQASIEQQAgAC8BgBIiBSAEIAVJG0YNByAAELKAgIAAIAAQyICAgAAhAQwHCwJA
IAAoAhgiBi8B0AEiAUEAIAAvAYASIgUgASAFSRtGDQAgABCygICAACAAEMiAgIAAIgFB//8DcQ0HIAAoAhghBgsgAkENaiAEIAMQi4SAgAAgBkEBOgDHAiAG
QQI7ANsBIAYoAsgBIgEgASkDAEL//////3ODQoCAgICACEKAgICAgAQgAi0ADUEAIAItAA4bIgFBAnEbhDcDACACIAFBA3E6AA9BACEBIANFDQYgBCEFIAMh
BwJAAkACQAJAAkACQAJAA0BBACEBQoCAgIAQIQgCQANAIAUgAWotAABBO0YNASAIQgF8IQggByABQQFqIgFHDQALQgAhCAsgByAIpyAIQoCAgIDwH4NQGyIJ
IAdLDQICQAJAIAlFDQBBACEBAkADQCAFIAFqLQAAQT1GDQEgCSABQQFqIgFGDQIMAAsLIAEgCUsNBSAFIAFBpYPAgABBBhCEgoCAAEEBcUUNACABQQFqIgFF
DQcCQCAJIAFJDQAgBSABaiIFIAkgAWsiAUH00sCAAEEBEISCgIAAQQFxRQ0CQezeyIAAIQEMBAsgASAJEIqAgIAAAAsgCSAHTw0FIAUgCUEBaiIBaiEFIAcg
AWsiBw0BDAULCwJAIAUgAUHe0sCAAEEBEISCgIAAQQFxRQ0AQfLeyIAAIQEMAQtB7t7IgABBsPnJgAAgBSABQe+EwIAAQQQQhIKAgABBAXEbIQELIAEvAAAi
AUH/AU0NAiAAIAAvAfQRQfz/A3EgAUEDcXI7AfQRDAILIAkgBxCIgICAAAALIAEgCRCIgICAAAwBCyAEIQUgAyEHAkACQAJAA0BBACEBQoCAgIAQIQgCQANA
IAUgAWotAABBO0YNASAIQgF8IQggByABQQFqIgFHDQALQgAhCAsgByAIpyAIQoCAgIDwH4NQGyIJIAdLDQECQAJAIAlFDQBBACEBAkADQCAFIAFqLQAAQT1G
DQEgCSABQQFqIgFGDQIMAAsLIAEgCUsNBCAFIAFBqYnAgABBDBCEgoCAAEEBcQ0BCyAJIAdPDQQgBSAJQQFqIgFqIQUgByABayIHDQEMBAsLIAFBAWoiAUUN
AwJAIAkgAUkNAEGauciAACEHAkAgCSABa0EBRw0AQZa5yIAAQZi5yIAAQZq5yIAAIAUgAWotAAAiAUExRhsgAUEwRhshBwsgBy8AACIBQf8BTQ0DIAFBAXFF
DQNBACEBIAZBAC8B7OPHgAA7AMgCDA0LIAEgCRCKgICAAAALIAkgBxCIgICAAAALIAEgCRCIgICAAAALA0BBACEBQoCAgIAQIQgCQANAIAQgAWotAABBO0YN
ASAIQgF8IQggAyABQQFqIgFHDQALQgAhCAsgAyAIpyAIQoCAgIDwH4NQGyIFIANLDQICQAJAIAVFDQBBACEBAkADQCAEIAFqLQAAQT1GDQEgBSABQQFqIgFG
DQIMAAsLIAEgBUsNBSAEIAFBoZTAgABBAhCEgoCAAEEBcQ0BC0EAIQEgBSADTw0LIAQgBUEBaiIFaiEEIAMgBWsiA0UNCwwBCwsgAUEBaiIBRQ0AAkACQAJA
IAUgAUkNACAEIAFqIQMCQCAFIAFrIgFBAUYNAEHy3siAAEGw+cmAACADIAFBx5vAgABBBBCEgoCAAEEBcRshBAwDC0Hs3siAACEEAkACQCADLQAAQZN/ag4L
BAEBAQEBAQEBAwABC0Hw3siAACEEDAMLQbD5yYAAIQQMAgsgASAFEIqAgIAAAAtB7t7IgAAhBAtBACEBIAQvAAAiBEGAAkkNCSAGQQI6AMgCIAYgBEEDcToA
yQIMCQsACyAFIAMQiICAgAAACyABIAUQiICAgAAAC0EAIQEgACgCGEEAOwDbAQwFC0EAIQEgACgCGEEAOwDbASAAKAIYIgQoAsgBIgUpAwAiCEKAgICAgAyD
UA0EIAQvAdABDQQgBSAIQv//////c4M3AwAMBAsgACgCGCIEQQE6ANsBQQAhASAEQQAtAPHjx4AAQQFxOgDcAQwDCyAAKAIYIgRBAToA2wFBACEBIARBAC0A
7+PHgABBAXE6ANwBDAILIAAoAhghBSACQR1qIAQgAxCLhICAACAFQQE6AMcCIAVBAjsA2wFBACEBIAUoAsgBIgQgBCkDAEL//////3ODQoCAgICACEKAgICA
gAQgAi0AHUEAIAItAB4bIgRBAnEbhDcDACACIARBA3E6AB8MAQsgAiADNgIUIAIgBDYCECACQQE6ABggACACQRBqEIiDgIAAIQELIAJBIGokgICAgAAgAQvN
BAIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAAkADQCAAKAIMIgRBOiADayIFaiIGIARJDQIgA0HT4MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQY3hyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTpJDQAL
QQAhAyAAQQBBAEHcABCehICAACIFQf//A3ENAANAIAAoAgwiBEEFIANrIgVqIgYgBEkNAiADQY26yIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQYCQCAA
KAIEIARqIgZBkrrIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIE
IAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBUkNAAsgACABLwEA
EJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAsgBiAIEIiAgIAAAAuzBwIHfwF+I4CAgIAAQTBrIgIkgICAgABBACEDAkACQAJAAkACQANA
IAAoAgwiBEEnIANrIgVqIgYgBEkNAiADQd3WyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBhNfJgABPDQAgByAGIAVqSQ0FCwJA
IAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIsIAIgBzYCKCACIAAgAkEoakEBQQEgBhGBgICAAICA
gIAAIAIpAwAiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBJ0kNAAtBACEDIAAgASgCAEEKQQBBsNjJgAAQlYCAgAAiBUH//wNxDQAD
QCAAKAIMIgRBFCADayIFaiIGIARJDQIgA0GE18mAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0GAkAgACgCBCAEaiIGQZjXyYAATw0AIAcgBiAFakkNBQsC
QCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCLCACIAc2AiggAiAAIAJBKGpBAUEBIAYRgYCAgACA
gICAACACKQMAIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQRRJDQALIAJBFWpCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83
AwAgAkEIakKq1arVqtWq1ap/NwMAIAJCqtWq1arVqtWqfzcDACACQbHgyKEDNgAdIAAgAkEdakEEEKCAgIAAIgVB//8DcQ0AQQAhAwNAIAAoAgwiBEEMIANr
IgVqIgYgBEkNAiADQZjXyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQcCQCAAKAIEIARqIgZBpNfJgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8
CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIsIAIgBzYCKCACIAAgAkEoakEBQQEgBhGBgICAAICAgIAAIAIpAwAiCUIg
iKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBDEkNAAtBACEFCyACQTBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYg
CBCIgICAAAALwQMDAn8BfgN/I4CAgIAAQRBrIgMkgICAgABBACEEIABBADYCkAECQAJAIAJFDQAgAEEYaigCAEGYAWopAgAhBUEAIQYgA0EAOgAMIANBADoA
CwJAQQENAEEZIQQMAQsgAEGMAWohBwJAAkAgACgClAEiBCACSQ0AIAIhBEEAIQgMAQsDQEF/IARBAXYgBGpBwABqIgYgBiAESRsiBCACSQ0ACyAHIAUgBBCC
goCAACIEQf//A3ENASAAKAKQASIIIAJqIgQgCEkhBgsgBkEBcQ0BIAQgACgClAFLDQEgACAENgKQAQJAIAAoAowBIAhqIgQgASACak8NACABIAQgAmpJDQIL
AkAgAkUNACAEIAEgAvwKAAALIAAoApABIgZBAWoiAkUNAQJAIAAoApQBIgQgAk8NACAAQRhqKAIAQZgBaikCACEFA0BBfyAEQQF2IARqQcAAaiIGIAYgBEkb
IgQgAkkNAAsgByAFIAQQgoKAgAAiBEH//wNxDQEgACgClAEhBCAAKAKQASEGCyAGIARPDQEgACAGQQFqNgKQAUEAIQQgACgCjAEgBmpBADoAAAsgA0EQaiSA
gICAACAEDwsAC7gCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBMiADayIFaiIGIARJDQIgA0Gk18mAAGohBwJAAkAgBiAAKAII
IghLDQAgBiAISw0FAkAgACgCBCAEaiIGQdbXyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsg
ACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUh
AyAFQTJJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALzAICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANA
IAAoAgwiBEE8IANrIgVqIgYgBEkNAiADQbrfyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB9t/IgABPDQAgByAGIAVqSQ0FCwJA
IAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICA
gIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBPEkNAAsgACABKAIAIAEoAgQiAyADQQAtAMDYyYAAQSAQloCAgAAhBQsg
AkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC8EDAwJ/AX4DfyOAgICAAEEQayIDJICAgIAAQQAhBCAAQQA2AoQBAkACQCACRQ0AIABBGGooAgBBmAFqKQIA
IQVBACEGIANBADoADCADQQA6AAsCQEEBDQBBGSEEDAELIABBgAFqIQcCQAJAIAAoAogBIgQgAkkNACACIQRBACEIDAELA0BBfyAEQQF2IARqQcAAaiIGIAYg
BEkbIgQgAkkNAAsgByAFIAQQgoKAgAAiBEH//wNxDQEgACgChAEiCCACaiIEIAhJIQYLIAZBAXENASAEIAAoAogBSw0BIAAgBDYChAECQCAAKAKAASAIaiIE
IAEgAmpPDQAgASAEIAJqSQ0CCwJAIAJFDQAgBCABIAL8CgAACyAAKAKEASIGQQFqIgJFDQECQCAAKAKIASIEIAJPDQAgAEEYaigCAEGYAWopAgAhBQNAQX8g
BEEBdiAEakHAAGoiBiAGIARJGyIEIAJJDQALIAcgBSAEEIKCgIAAIgRB//8DcQ0BIAAoAogBIQQgACgChAEhBgsgBiAETw0BIAAgBkEBajYChAFBACEEIAAo
AoABIAZqQQA6AAALIANBEGokgICAgAAgBA8LAAu4AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTAgA2siBWoiBiAESQ0CIANB
1tfJgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGG2MmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAF
aiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUEwSQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC8wCAgd/AX4jgICAgABBEGsi
AiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBJyADayIFaiIGIARJDQIgA0H238iAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZ3g
yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEI
aiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQSdJDQALIAAgASgCACABKAIEIgMgA0EA
LQDA2MmAAEEgEJaAgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvEAwEBfyOAgICAAEEwayIFJICAgIAAIAUgAjYCFCAFIAE2AhACQAJAAkAg
A0UNACAFIAQ2AgggBSADNgIEIAVBADoADAwBCyAFIAAoArwBIgI2AgQgBUEBOgAMIAJBAWoiAkUNASAAIAI2ArwBCwJAA0ACQAJAAkACQCAAIAVBBGoQsYOA
gAAiAkH//wNxDioFBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGAAYGBgYGBgYGBgYGBgYBAgMGC0EZIQIgAw0EIAAoArwBIgNBf2oiASADSw0FIAAgATYCvAEM
BAsgBUEYaiAAIAAoAsQBKAIAQfDeyIAAEJWDgIAAIAUvARwiAkUNAiADDQMgACgCvAEiA0F/aiIBIANLDQQgACABNgK8AQwDCyAFQSBqIAAgACgCxAEoAgBB
7t7IgAAQlYOAgAAgBS8BJCICRQ0BIAMNAiAAKAK8ASIDQX9qIgEgA0sNAyAAIAE2ArwBDAILIAVBKGogACAAKALEASgCAEGw+cmAABCVg4CAACAFLwEsIgJF
DQALIAMNACAAKAK8ASIDQX9qIgEgA0sNASAAIAE2ArwBCyAFQTBqJICAgIAAIAIPCwALzQQCB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQAJAA0Ag
ACgCDCIEQTogA2siBWoiBiAESQ0CIANB0+DIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGN4ciAAE8NACAHIAYgBWpJDQULAkAg
BUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICA
gAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE6SQ0AC0EAIQMgAEEAQQBB1QAQnoSAgAAiBUH//wNxDQADQCAAKAIMIgRB
BSADayIFaiIGIARJDQIgA0GNusiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0GAkAgACgCBCAEaiIGQZK6yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQVJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
IAYgCBCIgICAAAALuAICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE6IANrIgVqIgYgBEkNAiADQdLlyIAAaiEHAkACQCAGIAAo
AggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBjObIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwB
CyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIg
BSEDIAVBOkkNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvwAwIEfwF8I4CAgIAAQRBrIgQkgICAgAACQAJAAkAgAiAB
KAIEaiIFIAJJDQAgBUEDcQ0AIAVFDQACQCABLwFmIgZBAkkNAANAIAUgBkF/aiIGQf//A3FBJGxqLwEgDQEgASAGOwFmIAEgAiAGEPSDgIAAIAEvAWYiBkEB
Sw0ACwsgBEEMaiABIAIgAxD1g4CAAAJAIAQtAA5FDQAgBSAELwEMIgZBJGxqIgIvASBBAWoiAUH//wNxIAFHDQEgAEEAOwECIAAgBjsBACACQSBqIAE7AQAM
AwsCQCABLwFkRQ0AIABBgIDkADYBAAwDCwJAIAEoAgwiBiABLwFmIgdLDQAgBrhEzczMzMzM7D+iIghEAAAAAAAA8L9lDQEgCEQAAAAAAADwQGYNAQJAIAEo
AgggCPwDTw0AIABBgICYATYBAAwECyAAQYCA5AA2AQAMAwsgBSABIAIgAyAHEPaDgIAAIgJB//8DcUEkbGoiBS8BIEEBaiIGQf//A3EiAyAGRw0AIAVBIGog
BjsBACADQQFHDQAgASgCCEEBaiIGRQ0AIAEgBjYCCCACQf//A3EiBiABLwFmRw0BIAZBAWoiBkH//wNxIAZHDQAgASAGOwFmDAELAAsgAEEAOwECIAAgAjsB
AAsgBEEQaiSAgICAAAuuDgMDfwF+An8jgICAgABB0BBrIgQkgICAgAACQAJAAkAgAiABKALEASgCAEYNACAEIAEgAiADEOiDgIAAIAQvAQQiAg0CIAQoAgAh
AgwBCyAEQQhqIAEgAiADEOiDgIAAIAQvAQwiAg0BIAQoAgghAgJAAkAgAS8B1AFFDQAgBEEYaiACQTxqIAIoAhAgAUGgAWoiAxCUg4CAAAJAIAQvARoiBQ0A
IAEgBC8BGDsB1AEMAQsgBCAFOwEQAkBBgBBFDQAgBEEYakGqAUGAEPwLAAsgBEKAEDcCoBAgBEG43sGAADYCmBAgBCAEQRhqNgKcEAJAAkACQAJAIARBmBBq
IARBEGoQ6YOAgABB//8DcQ4DAAUBBQsCQCAEKAKkECIFIAQoAqAQIgZNDQAgBSAGEIiAgIAADAULIAQoApwQIAUQgICAgAAMAQtBAA0AQQApA+D6yYAAIgen
QdoAQQBBACAHQiCIpygCABGAgICAAICAgIAAIgVFDQACQEHaAEUNACAFQaoBQdoA/AsACyAEQtoANwOoECAEIAU2AqQQIARB3JXHgAA2AqAQIAQgBzcDmBAC
QAJAIARBoBBqIARBEGoQ6YOAgABB//8DcQ4DAAUBBQsgBCgCrBAiBSAEKAKoECIGSw0CIARCADcDqBAgBCgCpBAhCCAEQarVqtV6NgKkECAEIAY2AswQIAQg
BTYCyBAgBCAINgLEECAEQbQQaiAEQcQQaiAEQZgQahC4gICAACAEIAQoAswQIgU2AqgQIAQgBCgCxBAiBjYCpBAgBCAEKALIEDYCrBACQCAFRQ0AIAQoApgQ
IQggBCgCnBAhCQJAIAVFDQAgBkGqASAF/AsACyAIIAYgBUEAQQAgCSgCDBGBgICAAICAgIAACyAELwG8EA0BIAQoArQQIgYgBCgCuBAiBRCAgICAACAFRQ0B
AkAgBUUNACAGQaoBIAX8CwALIAQgBiAFQQAgBBC5gICAAAwBCyAEKAKoECIFRQ0AIAQoApgQIQggBCgCnBAhCSAEKAKkECEGAkAgBUUNACAGQaoBIAX8CwAL
IAggBiAFQQBBACAJKAIMEYGAgIAAgICAgAALIANCADcDACADQRhqQQA2AgAgA0EQakIANwMAIANBCGpCADcDACABQQA7AdQBDAELIAUgBhCIgICAAAALAkAg
ASgCwAEiAw0AIAEQwYOAgAAMAgsgAUEANgLAASABQQA7AdYBAkAgASADELGDgIAAIgVB//8DcUUNACAEIAU7ARICQEGAEEUNACAEQRhqQaoBQYAQ/AsACyAE
QoAQNwKgECAEQbjewYAANgKYECAEIARBGGo2ApwQAkACQAJAIARBmBBqIARBEmoQ6oOAgABB//8DcQ4DAAQBBAsgBCgCpBAiBSAEKAKgECIGSw0BIAQoApwQ
IAUQgICAgAAMAgtBAA0BQQApA+D6yYAAIgenQd4AQQBBACAHQiCIpygCABGAgICAAICAgIAAIgVFDQECQEHeAEUNACAFQaoBQd4A/AsACyAEQt4ANwOoECAE
IAU2AqQQIARB3JXHgAA2AqAQIAQgBzcDmBACQAJAAkAgBEGgEGogBEESahDqg4CAAEH//wNxDgMABQEFCyAEKAKsECIFIAQoAqgQIgZLDQEgBEIANwOoECAE
KAKkECEIIARBqtWq1Xo2AqQQIAQgBjYCzBAgBCAFNgLIECAEIAg2AsQQIARBtBBqIARBxBBqIARBmBBqELiAgIAAIAQgBCgCzBAiBTYCqBAgBCAEKALEECIG
NgKkECAEIAQoAsgQNgKsEAJAIAVFDQAgBCgCmBAhCCAEKAKcECEJAkAgBUUNACAGQaoBIAX8CwALIAggBiAFQQBBACAJKAIMEYGAgIAAgICAgAALIAQvAbwQ
DQMgBCgCtBAiBiAEKAK4ECIFEICAgIAAIAVFDQMCQCAFRQ0AIAZBqgEgBfwLAAsgBCAGIAVBACAEELmAgIAADAMLIAQoAqgQIgVFDQIgBCgCmBAhCCAEKAKc
ECEJIAQoAqQQIQYCQCAFRQ0AIAZBqgEgBfwLAAsgCCAGIAVBAEEAIAkoAgwRgYCAgACAgICAAAwCCyAFIAYQiICAgAAACyAFIAYQiICAgAAACyABKQOYASIH
QiCIpyEGIAenIQgCQCADKAIQIgVFDQAgAygCDCEJAkAgBUUNACAJQaoBIAX8CwALIAggCSAFQQBBACAGKAIMEYGAgIAAgICAgAALAkAgAy0ACEEBcQ0AIAMo
AgQiBUUNACADKAIAIQkCQCAFRQ0AIAlBqgEgBfwLAAsgCCAJIAVBAEEAIAYoAgwRgYCAgACAgICAAAsgASgCmAEgA0EUQQJBACABQZwBaigCAEEMaigCABGB
gICAAICAgIAAIAEQwYOAgAAMAQsACyAAIAI2AgBBACECCyAAIAI7AQQgBEHQEGokgICAgAALvhAEC38Bfgx/AXwjgICAgABBwIABayIEJICAgIAAAkAgAyAC
TQ0AIAMgAS8BmgJLDQACQEGAwABFDQAgBEHAAGpBAEGAwAD8CwALIAEoAgAiBSABKAIIaiIGIAVJDQAgBkEHcQ0AIAZFDQAgAyACayEHIAYgAkEDdGohCEEA
IQkCQCADIAJGIgoNAEEAIQlBACELA0AgBSAIIAtBA3RqKAIAaiIMIAVJDQIgDEEHcQ0CIAxFDQICQCABLwGYAiINRQ0AQQAhDgNAAkAgDCAOQQN0aiIGKQMA
Ig9CgICA4P//AINQDQAgBEHAAGogD0IaiKciEEEDdkH8P3FqIhEgESgCAEEBIBB0cjYCAAsCQCAPQgODQgFSDQAgBiAFayISIAZLDQUgBSABKAIoaiIRIAVJ
DQUgEUUNBSARQQNxDQUCQAJAIBFBfGooAgBFDQAgEUF4aigCACIGQX9qIhMgBksNByAEQTBqIBKtQoGAgIAQfiIPQtvR0IWa2t+BZ4VCACAPQqya4tOj0vD6
H4VCABCvhYCAACAEQSBqIAQpAzhC29HQhZra34FnhUIAIAQpAzBCq8j1xcesh7ugf4VCABCvhYCAACARIBMgBCkDKCAEKQMghSIPp3EiFGotAAAiEEUNACAR
QXBqIRUgD0I5iKchFiARQXRqIRcgEyEYAkADQCAYIQYCQCAQwEF/Sg0AIBYgEEH/AHFHDQAgESAXKAIAaiIQIBFJDQogEEEDcQ0KIBBFDQogEiAQIBRBAnRq
KAIARg0CCyAUQQFqIhBFDQlBACEZAkAgESAQIBNxIhRqLQAAIhBFDQAgBkF/aiEYQQAhGiAGDQEMBAsLQQAhGgwCCyARIBUoAgBqIgYgEUkNByAGQQNxDQcg
BkUNByAFIAYgFEEDdGopAgAiD6dqIhkgBUkNByAZQQNxDQcgGUUNByAPQiCIpyEaDAELQQAhGUEAIRoLIBlFDQAgGkGAgICABE8NBSAaQQJ0IgZBD2oiECAG
SQ0FIAkgEEFwcWoiBiAJSQ0FIAYhCQsgDkEBaiIOIA1HDQALCyALQQFqIgsgB0cNAAsLAkBBgMAARQ0AIARBwMAAaiAEQcAAakGAwAD8CgAAC0EAIRFBACEQ
A0AgECAEQcDAAGogEWoiBigCAGlqIhQgEEkNASAUIAZBBGooAgBpaiIQIBRJDQEgECAGQQhqKAIAaWoiFCAQSQ0BIBQgBkEMaigCAGlqIhAgFEkNASARQRBq
IhFBgMAARw0AC0EAIQxBACEbAkAgEEUNACAQQQFqIgZFDQEgBrhEAAAAAAAA6j+jmyIcRAAAAAAAAPBBZg0BIBz8AyEbCwJAQYDAAEUNACAEQcAAakEAQYDA
APwLAAtBACELAkAgCg0AQQAhC0EAIRVBACEMA0AgBSAIIBVBA3RqKAIAaiISIAVJDQIgEkEHcQ0CIBJFDQICQCABLwGYAiIXRQ0AQQAhEwNAAkAgEiATQQN0
aiIGLQAFQSBxRQ0AIAxBAWoiDEUNBSAGIAVrIhYgBksNBSAFIAEoApQBaiIRIAVJDQUgEUUNBSARQQNxDQUgEUF8aigCAEUNACARQXhqKAIAIgZBf2oiGSAG
Sw0FIARBEGogFq1CgYCAgBB+Ig9C29HQhZra34FnhUIAIA9CrJri06PS8PofhUIAEK+FgIAAIAQgBCkDGELb0dCFmtrfgWeFQgAgBCkDEEKryPXFx6yHu6B/
hUIAEK+FgIAAIBEgGSAEKQMIIAQpAwCFIg+ncSIUai0AACIQRQ0AIBFBcGohDSAPQjmIpyEaIBFBdGohDiAZIRgCQANAIBghBgJAIBDAQX9KDQAgGiAQQf8A
cUcNACARIA4oAgBqIhAgEUkNCCAQQQNxDQggEEUNCCAWIBAgFEECdGooAgBGDQILIBRBAWoiEEUNByARIBAgGXEiFGotAAAiEEUNAiAGQX9qIRggBkUNAgwA
CwsgESANKAIAaiIGIBFJDQUgBkEBcQ0FIAZFDQUgBiAUQQF0ai8BACEGAkBBgMAARQ0AIARBwMAAaiAEQcAAakGAwAD8CgAACyAEQcDAAGogBkEFdkECdCIQ
aigCAEEBIAZ0IhFxDQAgBEHAAGogEGoiECAQKAIAIBFyNgIAIAZFDQUgASgCpAEgBk0NBSAFIAEoApwBaiIQIAVJDQUgEEEDcQ0FIBBFDQUgECAGQRxsaiIG
LwEYRQ0FIAYoAhAiEEEfaiIRIBBJDQUgCyARQWBxaiIQIAtJDQUCQCAGLQAIQQFxRQ0AIBAhCwwBCyAGKAIEIgZBH2oiESAGSQ0FIBAgEUFgcWoiCyAQSQ0F
CyATQQFqIhMgF0cNAAsLIBVBAWoiFSAHRw0ACwsCQEGAwABFDQAgBEHAwABqIARBwABqQYDAAPwKAAALQQAhEUEAIRADQCAQIARBwMAAaiARaiIGKAIAaWoi
FCAQSQ0BIBQgBkEEaigCAGlqIhAgFEkNASAQIAZBCGooAgBpaiIUIBBJDQEgFCAGQQxqKAIAaWoiECAUSQ0BIBFBEGoiEUGAwABHDQALAkACQCAQDQBBACEG
DAELIBBBAWoiBkUNASAGuEQAAAAAAADqP6ObIhxEAAAAAAAA8EFmDQEgHPwDIQYLIAAgAS8BmAI7AQggAyACSQ0AIAdBgIAETw0AIAAgBzsBCiAbQYCABE8N
ACAAIAk2AgAgACAbOwEMIAYgDEF/akEEdkEBaiIQIAYgEEsbIAYgDButQhx+Ig9CIIinDQAgD6ciBkGAgARPDQAgACALNgIEIAAgBjsBDiAEQcCAAWokgICA
gAAPCwALjh8EB38Bfgd/A34jgICAgABBoAFrIgYkgICAgAACQCAFIAAvAZgCIgcgAS8BmAIiCCAHIAhJGyIIIAUgCEkbIgkgBEkNACABKAIAIgUgAygCAGoi
CiAFSQ0AIApBB3ENACAKRQ0AIAAoAgAiBSACKAIAaiILIAVJDQAgC0EHcQ0AIAtFDQAgCSAEayEMAkAgAikDACINQoCAgIDAA4NCAFENACAFIA2naiIOIAVJ
DQEgDkEHcQ0BIA5FDQEgDiAEQQN0aiEPAkAgDUKAgICAwACDUA0AAkAgCSAERg0AIABBHGohECAOIARBA3RqIQggDyEFIAwhEQNAAkAgBSkDAEIDg0IBUg0A
IAggACgCACIHayISIAhLDQUgByAAKAIoaiITIAdJDQUgE0UNBSAGIBM2AgQgBkEIaiATIBIQrYKAgAAgBi0AEEUNBSAHIAYoAgwiEigCAGoiEyAHSQ0FIBNB
A3ENBSATRQ0FIAYoAgghFCAQIAcgEyASKAIEEKKDgIAAIAZBBGogFBCugoCAACAFIAUpAwBCfIM3AwALIAhBCGohCCAFQQhqIQUgEUF/aiIRDQALIAAvAZgC
IQcLAkAgDCAHQf//A3EiCEcNACACIAIpAwBC/////79/gyINNwMADAELIAAoAgAiESACKAIAaiIFIBFJDQIgBUEHcQ0CIAVFDQICQAJAIAdB//8DcUUNAANA
IAUpAwBCA4NCAVENAiAFQQhqIQUgCEF/aiIIDQALCyACIAIpAwBC/////79/gyINNwMADAELIAIpAwAhDQsCQCANQoCAgICAAoNQDQACQCAJIARGDQAgDiAE
QQN0aiEIIA8hBSAMIREDQAJAIAUtAAVBIHFFDQAgCCAAKAIAIgdrIhIgCEsNBSAHIAAoApQBaiITIAdJDQUgE0UNBSAGIBM2AhQgBkEYaiATIBIQsIKAgAAg
Bi0AIEUNACAGKAIcLwEAIhJFDQUgACgCpAEgEk0NBSAHIAAoApwBaiITIAdJDQUgE0EDcQ0FIBNFDQUgEyASQRxsaiIHLwEYIhNFDQUgB0EYaiATQX9qIgc7
AQACQCAHQf//A3ENACAAKAKgASIHQX9qIhMgB0sNBiAAIBM2AqABCyAGQRRqIAYoAhgQsYKAgAAgBSAFKQMAQv///////3eDNwMACyAFQQhqIQUgCEEIaiEI
IBFBf2oiEQ0ACwsCQCAMIAAvAZgCIghHDQAgAiACKQMAQv//////fYMiDTcDAAwBCyAAKAIAIgcgAigCAGoiBSAHSQ0CIAVBB3ENAiAFRQ0CAkACQCAIRQ0A
A0AgBS0ABUEgcQ0CIAVBCGohBSAIQX9qIggNAAsLIAIgAikDAEL//////32DIg03AwAMAQsgAikDACENCwJAIA1CgICAgIABg1ANAAJAIAkgBEYNACAPIQUg
DCEHA0ACQCAFKQMAIg1CgICA4P//AINQDQAgACgCOCANQhqIp0H//wNxIhNNDQUgACgCACIRIAAoAjBqIgggEUkNBSAIQQNxDQUgCEUNBSAIIBNBJGxqIggv
ASAiEUUNBSAIQSBqIBFBf2oiCDsBACAIQf//A3ENACAAKAI0IghBf2oiESAISw0FIAAgETYCNAsgBUEIaiEFIAdBf2oiBw0ACwsCQCAMIAAvAZgCIghGDQAg
ACgCACIHIAIoAgBqIgUgB0kNAyAFQQdxDQMgBUUNAyAIRQ0AA0AgBSgBAkGA+P8fcQ0CIAVBCGohBSAIQX9qIggNAAsLIAIgAikDAEL//////36DNwMACyAM
QQN0IgVFDQAgD0EAIAX8CwALIAkgBEkNACAEQQN0IQUgBEEDdCEIIAMpAwAhDQJAAkAgDCAALwGYAkkNACANQoCAgIBwgyENIAIoAgAhBwwBCyACKQMAIhVC
gICAgPADgyANQoCAgICAXIOEIBUgDYRCgICAgIAgg4QhDSAVpyEHCyAKIAVqIQUgCyAIaiEOIAIgDSAHrYQ3AwACQAJAAkACQCADLQAEQRxxRQ0AIAkgBEYN
ASAAQSxqIQ8gAEGYAWohECAAQRxqIQMgCyAEQQN0IghqIREgCiAIaiEHIAGtQiCGIACthCEWIA4hCCAMIQkDQCAIQvz//5+AgHdC////n4CAdyAFKQMAIg1C
A4NCAVEbIA2DIhc3AwACQCAFKQMAIhVCA4NCAVINACAHIAEoAgAiE2siEiAHSw0GIBMgASgCKGoiBCATSQ0GIARFDQYgBkEkaiAEIBIQo4OAgAACQAJAIAYt
ACwNAEEAIQRBACESDAELIBMgBigCJGoiBCATSQ0HIARBA3ENByAERQ0HIAYoAighEgsgBEUNBiAXQgKDQgBSDQYgDUL8//8Dg1ANBiAXQgGDUEUNBiARIAAo
AgAiE2siCyARSw0GIBMgACgCKGoiFCATSQ0GIBRFDQYgBiAUNgIwIBJFDQYgBiASQf////8DSyIUOgCcAQJAIBQNACATIAMoAgBqIgogE0kNByAKQQdxDQcg
CkUNByAGQZQBaiAKIAAoAiAgEkECdCIUQX9qQQR2QQFqQQAgFBsQpIOAgAAgBi0AmAFFDQAgEyAAKAIkaiIKIBNJDQcgCkUNByAGKAKUASITQf////8ASw0H
IAogE0EEdGoiE0EDcQ0HAkAgEyAEIBRqTw0AIAQgEyAUakkNCAsCQCAURQ0AIBMgBCAU/AoAAAsgEyAAKAIAayIEIBNLDQcCQCAGQTBqIAsgEq1CIIYgBK2E
EKWDgIAAQf//A3FFDQAgAyAAKAIAIBMgEhCig4CAAEEjIQoMBgsgCCAIKQMAQnyDQgGENwMAIAIgAikDAEKAgICAwACENwMAIAUpAwAhFQwBC0EkIQoMBAsC
QCAVQoCAgICAgAiDUA0AIAcgASgCACIEayISIAdLDQYgBCABKAKUAWoiEyAESQ0GIBNFDQYgBkE0aiATIBIQpoOAgAAgBi0ANkUNBiAAKAIAIRIgBi8BNCET
AkACQAJAAkACQCABIABHDQAgE0UNCyAAKAKkASATTQ0LIBIgACgCnAFqIgQgEkkNCyAEQQNxDQsgBEUNCyAEIBNBHGxqIhIvARgiBEUNCyAEQQFqIgRB//8D
cSAERw0LIBJBGGogBDsBACARIAAoAgAiBGsiFCARSw0LIAQgACgClAFqIhIgBEkNCyASRQ0LIAYgEjYCOCAGQTxqIAZBOGogFBCng4CAACAGLwFIRQ0BQSIh
CgwJCyASIAAoApQBaiIUIBJJDQogFEUNCiAUQQNxDQpBIiEKIBRBfGooAgAgFEF4aigCAE8NCCATRQ0KIAEoAqQBIBNNDQogBCABKAKcAWoiFCAESQ0KIBRB
A3ENCiAURQ0KIBQgE0EcbGoiBC8BGEUNCiAGQcwAaiAQIBIgBCAWEKiDgIAAIAYtAE5FDQEgBi8BTCITRQ0KIAAoAqQBIBNNDQogACgCACISIAAoApwBaiIE
IBJJDQogBEEDcQ0KIARFDQogBCATQRxsaiISLwEYIgRFDQogBEEBaiIEQf//A3EgBEcNCiASQRhqIAQ7AQAMAwsgBigCQCEEIAYtAERFDQEgBC8BACIURQ0J
IAAoAqQBIBRNDQkgACgCACIKIAAoApwBaiISIApJDQkgEkEDcQ0JIBJFDQkgEiAUQRxsaiISLwEYIhRFDQkgEkEYaiAUQX9qIhI7AQACQCASQf//A3ENACAA
KAKgASISQX9qIhQgEksNCiAAIBQ2AqABCyAELwEAIBNHDQEgAi0ABEEQcUUNCSAIIAgpAwBCgICAgICACIQ3AwAMAwsgBkHQAGogBCABIAAQqYOAgAACQCAG
LwFkRQ0AQR8hCgwHCyAGQQA2AmwgBiAANgJoIAZB8gBqIBAgACgCACAGQdAAaiATIAZB6ABqEKqDgIAAAkACQCAGLwF2IgRFDQBBICEKIARBZ2oODggKCgoK
CgoKCgoKCgoBCgsgBi8BciATIAYtAHQbIRMMAgtBISEKDAYLIAQgEzsBACAIIAgpAwBCgICAgICACIQ3AwAgAiACKQMAQoCAgICAAoQ3AwAMAQsgESAAKAIA
IgRrIhQgEUsNBiAEIAAoApQBaiISIARJDQYgEkUNBiAGIBI2AnggBkH8AGogBkH4AGogFBCng4CAACAGLwGIAQ0EIAYoAoABIQQCQCAGLQCEAUUNACAELwEA
IhRFDQcgACgCpAEgFE0NByAAKAIAIgogACgCnAFqIhIgCkkNByASQQNxDQcgEkUNByASIBRBHGxqIhIvARgiFEUNByASQRhqIBRBf2oiEjsBAAJAIBJB//8D
cQ0AIAAoAqABIhJBf2oiFCASSw0IIAAgFDYCoAELIAQvAQAgE0H//wNxRw0AIAItAARBEHFFDQcgCCAIKQMAQoCAgICAgAiENwMADAELIAQgEzsBACAIIAgp
AwBCgICAgICACIQ3AwAgAiACKQMAQoCAgICAAoQ3AwALAkAgBSgBAkGA+P8fcUUNACACIAIpAwBCgICAgIABhDcDAAJAIAEgAEcNACAIIAgpAwBC////n4CA
f4MgBSkDACIVQoCAgOD//wCDIg2ENwMAIA1CAFENByAAKAI4IBVCGoinQf//A3EiEk0NByAAKAIAIgQgACgCMGoiEyAESQ0HIBNBA3ENByATRQ0HIBMgEkEk
bGoiBC8BICITRQ0HIBNBAWoiE0H//wNxIBNHDQcgBEEgaiATOwEADAELIAUpAwBCGoinIhRB//8DcSIERQ0GIAEoAjggBE0NBiABKAIAIhIgASgCMGoiEyAS
SQ0GIBNBA3ENBiATRQ0GIBMgBEEkbGoiEy8BIEUNBiAGQY4BaiAPIAAoAgAgEyAUEKuDgIAAAkAgBi8BkgEiE0UNAEEdIQoCQCATQWdqDg4GCAgICAgICAgI
CAgIAAgLQR4hCgwFCwJAAkAgBi0AkAFFDQAgBi8BjgEhEwwBCyAFKQMAQhqIpyETCyAIIBOtQv//A4NCGoYgCCkDAEL///+fgIB/g4Q3AwALIBFBCGohESAI
QQhqIQggB0EIaiEHIAVBCGohBSAJQX9qIgkNAAwCCwsCQCAOIAogCUEDdCIIak8NACAFIAsgCGpJDQQLIAxBA3QiCEUNACAOIAUgCPwKAAALQQAhCiAALwGY
AiABLwGYAiIATQ0AIABBf2oiBUH//wNxIgAgBUcNAiAMIABNDQEgDiAAQQN0aiIAKQMAIg1CgICAgICAA4NCgICAgICAA1INACAAIA1C////////fIM3AwAL
IAZBoAFqJICAgIAAIAoPCyAAIAwQiICAgAAACwALnAYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAg
AUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCdhICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAM
BAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBNUEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrV
qtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBLWpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJB
GGpCqtWq1arVqtWqfzcAACABQjU3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCdhICAAEH//wNxDgMABAEECyABKAKc
ECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAA
IAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBB
ACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAEL
IAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICA
gAAPCyAAIAIQiICAgAAACwAL4gEBBH8CQCAAKAJcIgIgASgCFGsiAyACSw0AIAAgAzYCXAJAAkAgASgCFCICQYCAJEsNAAJAIAJFDQAgASgCEEEAIAL8CwAL
IAEoAhAhAgJAQYCAJEUNACACQaoBQYCAJPwLAAsgAiAAKAI8NgIAIAAgAjYCPAwBCyAAKAIsIQQgACgCMCEFIAEoAhAhAwJAIAJFDQAgA0GqASAC/AsACyAE
IAMgAkEQQQAgBSgCDBGBgICAAICAgIAACwJAQbACRQ0AIAFBqgFBsAL8CwALIAEgACgCKDYCACAAIAE2AigPCwALrwYDAn8BfgJ/I4CAgIAAQcAQayIBJICA
gIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCwg4CAAEH//wNxDgMA
BAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBPUEAQQAgA0IgiKcoAgARgICA
gACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBNWpCqtWq1arVqtWqfzcAACACQTBq
QqrVqtWq1arVqn83AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUI9NwOYECABIAI2ApQQIAFB
3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQsIOAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarV
qtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4
EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIg
ASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECEC
AkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC60FAwJ/AX4CfyOAgICAAEHA
EGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQsoOAgABB
//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcEAQQBBACADQiCI
pygCABGAgICAAICAgIAAIgJFDQACQEHBAEUNACACQaoBQcEA/AsACyABQsEANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ
soOAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECAB
QaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABF
DQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8
CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIM
EYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC5EKAwd/AX4BfyOAgICAAEHAEGsiACSAgICAAAJAQYAQRQ0AIABBqgFBgBD8CwALIABC
gBA3AogQIABBuN7BgAA2AoAQIAAgADYChBBBACEBAkACQAJAA0AgACgCjBAiAkE8IAFrIgNqIgQgAkkNAiABQYzmyIAAaiEFAkACQCAEIAAoAogQIgZLDQAC
QCAEIAZLDQACQCAAKAKEECACaiIEQcjmyIAATw0AIAUgBCADakkNBgsCQCADRQ0AIAQgBSAD/AoAAAsgACgCjBAiBCADaiICIARJDQUgACACNgKMEAwCCyAE
IAYQiICAgAAACyAAKAKAECgCACEEIAAgAzYCnBAgACAFNgKYECAAQbAQaiAAQYAQaiAAQZgQakEBQQEgBBGBgICAAICAgIAAIAApA7AQIgenIQMCQCAHQiCI
p0H//wNxDgMBBAAEC0EADQJBACkD4PrJgAAiB6dBPEEAQQAgB0IgiKcoAgARgICAgACAgICAACIBRQ0CIAFCqtWq1arVqtWqfzcAACABQRBqQqrVqtWq1arV
qn83AAAgAUEIakKq1arVqtWq1ap/NwAAIAFBOGpBqtWq1Xo2AAAgAUEwakKq1arVqtWq1ap/NwAAIAFBKGpCqtWq1arVqtWqfzcAACABQSBqQqrVqtWq1arV
qn83AAAgAUEYakKq1arVqtWq1ap/NwAAIABCPDcDkBAgACABNgKMECAAQdyVx4AANgKIECAAIAc3A4AQIABBgBBqQQhqIQhBACEBA0AgACgClBAiAkE8IAFr
IgNqIgQgAkkNBCABQYzmyIAAaiEFAkACQCAEIAAoApAQIgZLDQACQCAEIAZLDQACQCAAKAKMECACaiIEQcjmyIAATw0AIAUgBCADakkNCAsCQCADRQ0AIAQg
BSAD/AoAAAsgACgClBAiBCADaiICIARJDQcgACACNgKUEAwCCyAEIAYQiICAgAAACyAAKAKIECgCACEEIAAgAzYCrBAgACAFNgKoECAAQbAQaiAIIABBqBBq
QQFBASAEEYGAgIAAgICAgAAgACkDsBAiB6chAwJAIAdCIIinQf//A3EOAwEGAAYLIAAoApAQIgFFDQQgACgCgBAhBCAAKAKEECECIAAoAowQIQMCQCABRQ0A
IANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAwECyABIANqIgMgAUkNBCADIQEgA0E8SQ0ACwJAIAAoApQQIgEgACgCkBAiA0sNACAAQgA3
A5AQIAAoAowQIQQgAEGq1arVejYCjBAgACADNgK4ECAAIAE2ArQQIAAgBDYCsBAgAEGYEGogAEGwEGogAEGAEGoQuICAgAAgACAAKAK4ECIBNgKQECAAIAAo
ArAQIgM2AowQIAAgACgCtBA2ApQQAkAgAUUNACAAKAKAECEEIAAoAoQQIQICQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAsg
AC8BoBANAyAAKAKYECIDIAAoApwQIgEQgICAgAAgAUUNAwJAIAFFDQAgA0GqASAB/AsACyAAIAMgAUEAIAAQuYCAgAAMAwsgASADEIiAgIAAAAsgASADaiID
IAFJDQIgAyEBIANBPEkNAAsgACgCjBAiASAAKAKIECIDSw0CIAAoAoQQIAEQgICAgAALIABBwBBqJICAgIAADwsACyABIAMQiICAgAAAC/4JAwd/AX4BfyOA
gICAAEHAEGsiACSAgICAAAJAQYAQRQ0AIABBqgFBgBD8CwALIABCgBA3AogQIABBuN7BgAA2AoAQIAAgADYChBBBACEBAkACQAJAA0AgACgCjBAiAkE0IAFr
IgNqIgQgAkkNAiABQcjmyIAAaiEFAkACQCAEIAAoAogQIgZLDQACQCAEIAZLDQACQCAAKAKEECACaiIEQfzmyIAATw0AIAUgBCADakkNBgsCQCADRQ0AIAQg
BSAD/AoAAAsgACgCjBAiBCADaiICIARJDQUgACACNgKMEAwCCyAEIAYQiICAgAAACyAAKAKAECgCACEEIAAgAzYCnBAgACAFNgKYECAAQbAQaiAAQYAQaiAA
QZgQakEBQQEgBBGBgICAAICAgIAAIAApA7AQIgenIQMCQCAHQiCIp0H//wNxDgMBBAAEC0EADQJBACkD4PrJgAAiB6dBNEEAQQAgB0IgiKcoAgARgICAgACA
gICAACIBRQ0CIAFCqtWq1arVqtWqfzcAACABQRBqQqrVqtWq1arVqn83AAAgAUEIakKq1arVqtWq1ap/NwAAIAFBMGpBqtWq1Xo2AAAgAUEoakKq1arVqtWq
1ap/NwAAIAFBIGpCqtWq1arVqtWqfzcAACABQRhqQqrVqtWq1arVqn83AAAgAEI0NwOQECAAIAE2AowQIABB3JXHgAA2AogQIAAgBzcDgBAgAEGAEGpBCGoh
CEEAIQEDQCAAKAKUECICQTQgAWsiA2oiBCACSQ0EIAFByObIgABqIQUCQAJAIAQgACgCkBAiBksNAAJAIAQgBksNAAJAIAAoAowQIAJqIgRB/ObIgABPDQAg
BSAEIANqSQ0ICwJAIANFDQAgBCAFIAP8CgAACyAAKAKUECIEIANqIgIgBEkNByAAIAI2ApQQDAILIAQgBhCIgICAAAALIAAoAogQKAIAIQQgACADNgKsECAA
IAU2AqgQIABBsBBqIAggAEGoEGpBAUEBIAQRgYCAgACAgICAACAAKQOwECIHpyEDAkAgB0IgiKdB//8DcQ4DAQYABgsgACgCkBAiAUUNBCAAKAKAECEEIAAo
AoQQIQIgACgCjBAhAwJAIAFFDQAgA0GqASAB/AsACyAEIAMgAUEAQQAgAigCDBGBgICAAICAgIAADAQLIAEgA2oiAyABSQ0EIAMhASADQTRJDQALAkAgACgC
lBAiASAAKAKQECIDSw0AIABCADcDkBAgACgCjBAhBCAAQarVqtV6NgKMECAAIAM2ArgQIAAgATYCtBAgACAENgKwECAAQZgQaiAAQbAQaiAAQYAQahC4gICA
ACAAIAAoArgQIgE2ApAQIAAgACgCsBAiAzYCjBAgACAAKAK0EDYClBACQCABRQ0AIAAoAoAQIQQgACgChBAhAgJAIAFFDQAgA0GqASAB/AsACyAEIAMgAUEA
QQAgAigCDBGBgICAAICAgIAACyAALwGgEA0DIAAoApgQIgMgACgCnBAiARCAgICAACABRQ0DAkAgAUUNACADQaoBIAH8CwALIAAgAyABQQAgABC5gICAAAwD
CyABIAMQiICAgAAACyABIANqIgMgAUkNAiADIQEgA0E0SQ0ACyAAKAKMECIBIAAoAogQIgNLDQIgACgChBAgARCAgICAAAsgAEHAEGokgICAgAAPCwALIAEg
AxCIgICAAAAL0w8CAX8BfgJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQCABQf//A3EiA0HnB0oNACADQX9qDkUBJgIDBAUGBwgmJgkmJiYmJiYmJiYmJiYKJiYmJiYmJiYmJiYmJiYLJiYmJgwmDSYmJiYmJiYmJiYmJiYmJiYm
Jg4mJg8mCwJAIANBmHhqDjIQJhESExQVFiYmJiYmJiYXGCYmJiYmJiYmJiYmJiYmJiYmJhkaJiYbJiYmJiYcJh0eHwALAkAgA0GscGoOLSAmJiYmJiYmJiYm
JiYmJiYmJiYmJiYhIiYmJiMmJiYmJiYmJiYmJiYmJiYmJAALAkACQAJAAkAgA0H+/31qDhMAKQEpKSkpKSkpAikpKSkpKSkDKQsgADUCACAAQQRqMQAAQiCG
hEJ+gyACrUIBg4QhBAwnCyAANQIAIABBBGoxAABCIIaEQn2DQgJCACACQQFxG4QhBAwmCyAANQIAIABBBGoxAABCIIaEQnuDQgRCACACQQFxG4QhBAwlCyAA
NQIAIABBBGoxAABCIIaEQneDQghCACACQQFxG4QhBAwkCyAANQIAIABBBGoxAABCIIaEQm+DQhBCACACQQFxG4QhBAwjCyAANQIAIABBBGoxAABCIIaEQl+D
QiBCACACQQFxG4QhBAwiCyAANQIAIABBBGoxAABCIIaEQr9/g0LAAEIAIAJBAXEbhCEEDCELIAA1AgAgAEEEajEAAEIghoRC/36DQoABQgAgAkEBcRuEIQQM
IAsgADUCACAAQQRqMQAAQiCGhEL/fYNCgAJCACACQQFxG4QhBAwfCyAANQIAIABBBGoxAABCIIaEQv97g0KABEIAIAJBAXEbhCEEDB4LIAA1AgAgAEEEajEA
AEIghoRC/3eDQoAIQgAgAkEBcRuEIQQMHQsgADUCACAAQQRqMQAAQiCGhEL/b4NCgBBCACACQQFxG4QhBAwcCyAANQIAIABBBGoxAABCIIaEQv9fg0KAIEIA
IAJBAXEbhCEEDBsLIAA1AgAgAEEEajEAAEIghoRC/79/g0KAwABCACACQQFxG4QhBAwaCyAANQIAIABBBGoxAABCIIaEQv//foNCgIABQgAgAkEBcRuEIQQM
GQsgADUCACAAQQRqMQAAQiCGhEL//32DQoCAAkIAIAJBAXEbhCEEDBgLIAA1AgAgAEEEajEAAEIghoRC//97g0KAgARCACACQQFxG4QhBAwXCyAANQIAIABB
BGoxAABCIIaEQv//d4NCgIAIQgAgAkEBcRuEIQQMFgsgADUCACAAQQRqMQAAQiCGhEL//2+DQoCAEEIAIAJBAXEbhCEEDBULIAA1AgAgAEEEajEAAEIghoRC
//9fg0KAgCBCACACQQFxG4QhBAwUCyAANQIAIABBBGoxAABCIIaEQv//v3+DQoCAwABCACACQQFxG4QhBAwTCyAANQIAIABBBGoxAABCIIaEQv///36DQoCA
gAFCACACQQFxG4QhBAwSCyAANQIAIABBBGoxAABCIIaEQv///32DQoCAgAJCACACQQFxG4QhBAwRCyAANQIAIABBBGoxAABCIIaEQv///3uDQoCAgARCACAC
QQFxG4QhBAwQCyAANQIAIABBBGoxAABCIIaEQv///3eDQoCAgAhCACACQQFxG4QhBAwPCyAANQIAIABBBGoxAABCIIaEQv///2+DQoCAgBBCACACQQFxG4Qh
BAwOCyAANQIAIABBBGoxAABCIIaEQv///1+DQoCAgCBCACACQQFxG4QhBAwNCyAANQIAIABBBGoxAABCIIaEQv///79/g0KAgIDAAEIAIAJBAXEbhCEEDAwL
IAA1AgAgAEEEajEAAEIghoRC/////36DQoCAgIABQgAgAkEBcRuEIQQMCwsgADUCACAAQQRqMQAAQiCGhEL/////fYNCgICAgAJCACACQQFxG4QhBAwKCyAA
NQIAIABBBGoxAABCIIaEQv////97g0KAgICABEIAIAJBAXEbhCEEDAkLIAA1AgAgAEEEajEAAEIghoRC/////3eDQoCAgIAIQgAgAkEBcRuEIQQMCAsgADUC
ACAAQQRqMQAAQiCGhEL/////b4NCgICAgBBCACACQQFxG4QhBAwHCyAANQIAIABBBGoxAABCIIaEQv////9fg0KAgICAIEIAIAJBAXEbhCEEDAYLIAA1AgAg
AEEEajEAAEIghoRC/////79/g0KAgICAwABCACACQQFxG4QhBAwFCyAANQIAIABBBGoxAABCIIaEQv//////foNCgICAgIABQgAgAkEBcRuEIQQMBAsgADUC
ACAAQQRqMQAAQiCGhEL//////32DQoCAgICAAkIAIAJBAXEbhCEEDAMLIAA1AgAgAEEEajEAAEIghoRC//////97g0KAgICAgARCACACQQFxG4QhBAwCCyAA
NQIAIABBBGoxAABCIIaEQv//////d4NCgICAgIAIQgAgAkEBcRuEIQQMAQsgADUCACAAQQRqMQAAQiCGhEL//////w+DQoCAgICAcEIAIAJBAXEbhCEECyAA
IAQ+AgAgAEEEaiAEQiCIPAAADwsgARDRgoCAAEEBcRoAC7wFAQd/I4CAgIAAQdAAayIEJICAgIAAAkAgAi8BACIFIAEvAYwBTw0AIAIoAgQhBiAEQcAAaiAB
IAItAAgQuoOAgAAgBC8BRCIHQQFqIghB//8DcSIJIAhHDQAgBCgCQCIILwGqAiAJayIJQf//A3EgCUcNAAJAAkAgBiAJQf//A3EiCUsNACAGIAdqIglB//8D
IAlB//8DSRshBwwBCyAIKAIMIghFDQEgBiAJayEJAkADQCAJIAgvAaoCIgZNDQEgCSAGayEJIAgoAgwiCA0ADAMLCyAJQX9qIgcgCUsNAQsCQAJAAkACQCAD
LQAMRQ0AIAMvAQAiCiABLwGMAU8NBCADKAIEIQIgBEHAAGogASADLQAIELqDgIAAIAQvAUQiAUEBaiIJQf//A3EiBiAJRw0EIAQoAkAiCS8BqgIgBmsiBkH/
/wNxIAZHDQQCQAJAIAIgBkH//wNxIgZLDQAgAiABaiIGQf//AyAGQf//A0kbIQEMAQsgCSgCDCIJRQ0FIAIgBmshBgJAA0AgBiAJLwGqAiIBTQ0BIAYgAWsh
BiAJKAIMIglFDQcMAAsLIAZBf2oiASAGSw0FCyAEQQA2AhQgBCAKOwESIAQgATsBECAEIAk2AgwgBEEAOgBMIARBDGohCQwBCyAEQRxqIAEgAi0ACBDrg4CA
ACAEQRxqIQkgBC0AKEUNAQsgBEE8aiAJQQhqKAAANgAAIABBAToADCAAQQA2AgggACAFOwEGIAAgBzsBBCAAIAg2AgAgAEECOgAcIABBAToAICAEIAkpAAA3
ADQgACAEKQAxNwANIABBFGogBEE4aikAADcAACAEQQE6ACwgBEECOgAwDAELIABCADcCACAAQQE6ACAgAEEYakIANwIAIABBEGpCADcCACAAQQhqQgA3AgAL
IARB0ABqJICAgIAADwsAC6sJAQd/I4CAgIAAQSBrIgIkgICAgAACQAJAAkACQAJAAkACQAJAAkACQAJAAkAgAS0AIEEBcQ0AAkACQAJAAkAgAS0ADEUNACAB
LwEEIQMgASgCACEEIAEoAhAhBSABLQAcQQNxDgQBAgMPAQsgAkEIakEANgIAIAJCADcDAAwMCyAEKAIIIgVFDQMgASAFNgIAIAFBAToADCAFLwGqAkF/aiIF
Qf//A3EgBUcNDSABQQA6AAggAUEAOwEGIAEgBTsBBAwKCyAFRQ0MIAFCADcCACABQQhqQgA3AgAgAkEBOgAIIAIgBDYCACADIAMgBSADIAVJG2siAUH//wNx
IAFHDQwgAiABOwEEIANBf2oiAUH//wNxIAFHDQwgAiABOwEGDAoLAkAgBSAERg0AIAQoAggiBUUNAyABIAU2AgAgAUEBOgAMIAUvAaoCQX9qIgVB//8DcSAF
Rw0MIAFBADoACCABQQA7AQYgASAFOwEEDAgLIAEvARQhBSABQgA3AgAgAUEIakIANwIAIAMgBUH//wNxSQ0GIAIgBTsBBCACIAQ2AgAgAkEBOgAIIANBAWoi
AUH//wNxIAFHDQsgAiABOwEGDAkLAkACQAJAAkAgAS0ADEUNACABLwEEIQQgASgCACEDIAEoAhAhBSABLQAcQQNxDgQBAgMOAQsgAkEYakEANgIAIAJCADcD
ECACQRBqIQEMDAsCQCADKAIMIgVFDQAgAUEAOgAIIAFBADYCBCABIAU2AgAgAUEBOgAMDAcLIAFCADcCACABQQhqQgA3AgAMBgsgBUUNCyADLwGqAiAEQf//
A3EiBmsiB0H//wNxIgggB0cNCyABQgA3AgAgAUEIakIANwIAIAJBAToAGCACIAM2AhAgAiAEOwEUIAYgCCAFIAggBUkbaiIBQf//A3EgAUcNCyACIAE7ARYg
AkEQaiEBDAoLAkAgBSADRg0AAkAgAygCDCIFRQ0AIAFBADoACCABQQA2AgQgASAFNgIAIAFBAToADAwFCyABQgA3AgAgAUEIakIANwIADAQLIAEvARQhBSAB
QgA3AgAgAUEIakIANwIAIARB//8DcSAFQf//A3EiAUsNAiACIAQ7ARQgAiADNgIQIAJBAToAGCABQQFqIgFB//8DcSABRw0KIAIgATsBFiACQRBqIQEMCQsg
AUIANwIAIAFBCGpCADcCAAwGCyABQgA3AgAgAUEIakIANwIADAQLIAJBEGpBCGpBADYCACACQgA3AxAgAkEQaiEBDAYLIAIgBDsBFCACQQE6ABggAiADNgIQ
IAIgAy8BqgI7ARYgAkEQaiEBDAULIAIgBDsBFCACQQE6ABggAiADNgIQIAIgAy8BqgI7ARYgAkEQaiEBDAQLIAJBCGpBADYCACACQgA3AwAMAgsgAkEAOwEE
IAIgBDYCACACQQE6AAggA0EBaiIBQf//A3EgAUcNAyACIAE7AQYMAQsgAkEAOwEEIAIgBDYCACACQQE6AAggA0EBaiIBQf//A3EgAUcNAiACIAE7AQYLIAIh
AQsgACABKQIANwIAIABBCGogAUEIaigCADYCACACQSBqJICAgIAADwsAC9YCAQV/AkACQAJAAkACQAJAIAAtAJABDgQCAQAFAgsgACgCWCICRQ0EIAAoAoAB
IQMgAC8BjgEhBAJAA0AgBEH//wNxIAIvAaoCIgVNDQEgBCAFayEEIAIoAggiAg0ADAYLCwJAAkAgAygCACIGIAJGDQADQCACKAIMIgJFDQIgAiAGRw0ADAYL
CyADLwEEIAUgBGtB//8DcU8NBAsgAC0AiAFFDQECQCAAKAKEASICIAFPDQAgAEEBOgCQAQ8LIAAgAiABazYChAEPCyAAKAJUIgZFDQMgACgCWCICRQ0DIAAv
AY4BIQQCQANAIARB//8DcSACLwGqAiIFTQ0BIAQgBWshBCACKAIIIgJFDQUMAAsLAkAgBiACRg0AA0AgAigCDCICRQ0CIAIgBkcNAAwDCwsgBEH//wNxIAVG
DQELDwsgAEEAOgCQAQ8LIABBADoAkAEPCwALzQIBAn8CQAJAAkAgAw0AQQAhAwwBCyADQYCAgIAETw0BIANBAnQhAwsgA0EPaiIEIANJDQAgASAAKAIIaiID
IAFJDQAgA0UNACACIANrIgMgAksNACADQQ9xDQAgASAAKAIAaiICIAFJDQAgAkEHcQ0AIAJFDQAgBEEPTQ0AIAIgA0EKdiIFQQN0aiIBIAEpAwBCf0HAACAE
QQR2IgBBwAAgA0EEdkE/cSIBayIDIAAgA0kbIgNrrYggAa2GhDcDACAAIANrIgEgAEsNACAFQQFqIQMCQCABQcEASQ0AAkAgAUG/f2pBA3ZB+P///wFxQQhq
IgBFDQAgAiAFQQN0akEIakH/ASAA/AsACwNAIANBAWohAyABQUBqIgFBwABLDQALCwJAIAFFDQAgAiADQQN0aiIDIAMpAwBCf0EAIAFrQT9xrYiENwMACw8L
AAvWAwMDfwF+Bn8jgICAgABBMGsiAySAgICAAAJAIAFFDQAgAUEDcQ0AAkACQAJAIAFBfGooAgBFDQAgAUF4aigCACIEQX9qIgUgBEsNAyADQRBqIAKtQoGA
gIAQfiIGQtvR0IWa2t+BZ4VCACAGQqya4tOj0vD6H4VCABCvhYCAACADIAMpAxhC29HQhZra34FnhUIAIAMpAxBCq8j1xcesh7ugf4VCABCvhYCAACABIAUg
AykDCCADKQMAhSIGp3EiB2otAAAiCEUNACABQXBqIQkgBkI5iKchCiAEQX9qIQsgAUF0aiEMA0AgCyEEAkAgCMBBf0oNACAKIAhB/wBxRw0AIAEgDCgCAGoi
CCABSQ0FIAhBA3ENBSAIRQ0FIAIgCCAHQQJ0aigCAEYNAwsgB0EBaiIIRQ0EIAEgCCAFcSIHai0AACIIRQ0BIARBf2ohCyAEDQALC0EAIQQgA0EuakEAOgAA
IANBADsBLEIAIQYMAQsgASAJKAIAaiIEIAFJDQEgBEEDcQ0BIARFDQEgBCAHQQN0aikCACEGQQEhBAsgACAEOgAIIAAgBjcCACAAIAMvASw7AAkgAEELaiAD
QS5qLQAAOgAAIANBMGokgICAgAAPCwAL5AYGAn8CfgR/AX4DfwN+AkACQAJAAkACQCADQcAASw0AIAJFDQIgA0F/aiIEIANLDQQgBA0BQQAhBQNAAkAgASkD
ACIGUA0AIAYhBwwFCyABQQhqIQEgAiAFQQFqIgVHDQAMAwsLAkAgAkUNACABQQhqIQhBACEFAkACQAJAAkACQANAAkACQCABIAUiCUEDdCIKaiILKQMAIgZC
f4V5IgxCAFINACAJQQFqIQUMAQsgAyAMpyINayIOIQQgCUEBaiIPIQUCQCAOQcEASQ0AIAggCmohCiAPIQUgDiEEA0ACQCAFIAJJDQAgAEIANwIADwsgCikD
AEJ/Ug0CIApBCGohCiAFQQFqIQUgBEFAaiIEQcAASw0ACwsgBSACTw0DIAQgASAFQQN0aiIKKQMAQn+FeqdNDQILIAUgAk8NBgwACwsgC0J/QcAAIA1rIgRB
P3GthiAGhTcDACAOQUBxIgUgDksNCCAFQQZ2IgMgD2oiCyACSw0BIA5BP3EhAgJAIAVBwABJDQAgA0EBIANBAUsbQQN0IgVFDQAgASAPQQN0akEAIAX8CwAL
IAINAgwDCyAFIAIQiICAgAAMBwsgCyACEIiAgIAAAAsgCiAKKQMAQn9BACAOa0E/ca2IhTcDAAsgCUH///8fSw0EIABBAToABCAAIAlBBnQgBEH/AHFyNgIA
DwsgAEIANwIADwsgBK0iDEIHgyEQQgAgDEL4////D4N9IRFBACEFIANBv39qQUFJIQogBEEISSEEA0AgCg0DQgAhDCABIAVBA3RqIgkpAwAiBiEHAkAgBA0A
QgQhDCAGIQcDQCAGIAxCBHyIIAYgDEIDfIggBiAMQgJ8iCAGIAxCAXyIIAYgDIggBiAMQn98iCAGIAxCfnyIIAYgDEJ9fIggB4ODg4ODg4ODIQcgESAMQgh8
Igx8QgRSDQALIAxCfHwhDAsCQCAQUA0AIAxCAXwhDCAQIRIDQCAGIAyIIAeDIQcgDEIBfCEMIBJCf3wiEkIAUg0ACwsCQCAHUA0AIAkhAQwDCyAFQQFqIgUg
AkcNAAsLIABCADcCAA8LQcAAIANrIgpBwABPDQAgAUJ/IAqtiCAHeiIMhiAGhTcDACAFQYCAgCBPDQAgAEEBOgAEIAAgBUEGdCAMp3I2AgAPCwAL6QMDBH8B
fgF/I4CAgIAAQSBrIgMkgICAgAACQCAAKAIAIgQgARDtg4CAAEEBcQ0AIARFDQAgBEEDcQ0AIARBeGooAgAiBSAEQXxqKAIAIgZJDQACQAJAIAUgBkcNAEEZ
IQUMAQsgBCABEO2DgIAAQQFxDQEgA0EQaiABrUKBgICAEH4iB0Lb0dCFmtrfgWeFQgAgB0KsmuLTo9Lw+h+FQgAQr4WAgAAgAyADKQMYQtvR0IWa2t+BZ4VC
ACADKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgBUF/aiIGIAVLDQECQCAEIAYgAykDCCADKQMAhSIHp3EiBWoiCCwAAEF/Sg0AA0AgBUEBaiIFRQ0DIAQgBSAG
cSIFaiIILAAAQQBIDQALCyAIIAdCOYinQYABcjoAACAAKAIAIgRFDQEgBEEDcQ0BIAQgBEF0aigCAGoiBiAESQ0BIAZBA3ENASAGRQ0BIAYgBUECdGogATYC
ACAAKAIAIgRFDQEgBEEDcQ0BIAQgBEFwaigCAGoiBiAESQ0BIAZBA3ENASAGRQ0BIAYgBUEDdGogAjcCACAAKAIAIgVFDQEgBUEDcQ0BIAVBfGoiBSgCAEEB
aiIERQ0BIAUgBDYCAEEAIQULIANBIGokgICAgAAgBQ8LAAu+AwMEfwF+Bn8jgICAgABBIGsiAySAgICAAAJAIAFFDQAgAUEDcQ0AQQAhBAJAAkAgAUF8aigC
AA0AQQAhBQwBCyABQXhqKAIAIgVBf2oiBiAFSw0BIANBEGogAq1CgYCAgBB+IgdC29HQhZra34FnhUIAIAdCrJri06PS8PofhUIAEK+FgIAAIAMgAykDGELb
0dCFmtrfgWeFQgAgAykDEEKryPXFx6yHu6B/hUIAEK+FgIAAAkAgASAGIAMpAwggAykDAIUiB6dxIghqLQAAIgkNAEEAIQUMAQsgAUFwaiEKIAdCOYinIQsg
BUF/aiEMIAFBdGohDQJAA0AgDCEFAkAgCcBBf0oNACALIAlB/wBxRw0AIAEgDSgCAGoiCSABSQ0EIAlBA3ENBCAJRQ0EIAIgCSAIQQJ0aigCAEYNAgsgCEEB
aiIJRQ0DAkAgASAJIAZxIghqLQAAIgkNAEEAIQUMAwsgBUF/aiEMIAUNAAtBACEFDAELIAEgCigCAGoiBSABSQ0BIAVBAXENASAFRQ0BIAUgCEEBdGovAQAh
BEGAgAQhBQsgACAFIARyNgEAIANBIGokgICAgAAPCwALywQDBX8BfgV/I4CAgIAAQcAAayIDJICAgIAAAkACQAJAIAEoAgAiBEUNACAEQQNxDQAgBEF4aigC
ACIFIARBfGooAgAiBkkNAAJAIAUgBkYiBw0AIANBMGogASACELKCgIAAIANBKmogA0E7ai0AADoAACADIAMvADk7ASggAy0AOCEEIAMoAjQhBSADKAIwIQEM
AgsCQAJAIAVFDQAgA0EQaiACrUKBgICAEH4iCELb0dCFmtrfgWeFQgAgCEKsmuLTo9Lw+h+FQgAQr4WAgAAgAyADKQMYQtvR0IWa2t+BZ4VCACADKQMQQqvI
9cXHrIe7oH+FQgAQr4WAgAAgBCAFQX9qIgkgAykDCCADKQMAhSIIp3EiBmotAAAiAUUNAEEZQQAgBxshCiAEQXBqIQsgCEI5iKchDCAEQXRqIQ0gCSEHAkAD
QCAHIQUCQCABwEF/Sg0AIAwgAUH/AHFHDQAgBCANKAIAaiIBIARJDQUgAUEDcQ0FIAFFDQUgAiABIAZBAnRqIgEoAgBGDQILIAZBAWoiAUUNBCAEIAEgCXEi
BmotAAAiAUUNAyAFQX9qIQcgBQ0ADAMLCyAEIAsoAgBqIgUgBEkNAiAFQQFxDQIgBUUNAkEBIQQgA0EBOgAsIAUgBkEBdGohBQwDC0EZIQoLDAILAAtBACEK
IARBAXENACABIAI2AgALIAAgBDoACCAAIAU2AgQgACABNgIAIAAgAy8BKDsACSAAIAo7AQwgAEELaiADQSpqLQAAOgAAIANBwABqJICAgIAAC/MCBAV/An4B
fwF+I4CAgIAAQSBrIgUkgICAgAACQAJAAkAgAiABKAIAaiIGIAJJDQAgBkEBcQ0AIAZFDQAgAiABKAIEaiIHIAJJDQAgB0EDcQ0AIAdFDQAgBKciCCAEQiCI
pyIJIAMQ7oOAgAAhCiABLwEsQQFqIgJB//8DcSACRw0AIAKtQv//A4MhC0IAIQQDQAJAIAYgCiAEfCABMwEgg6dBAXRqLwEAIgwNACAAQQA2AQAMBAsgBUEQ
aiAHIAxBHGxqIgJBEGopAgA3AwAgBUEYaiACQRhqKAIANgIAIAVBCGogAkEIaikCADcDACAFIAIpAgA3AwACQCAEIAUzARYiDVgNACAAQQA2AQAMBAsCQAJA
IAQgDVINACAFLwEYQf//A3FFDQAgCCAJIAMgBRDvg4CAAEEBcQ0BCyALIARCAXwiBFENAwwBCwsgAEEBOgACIAAgDDsBAAwCCwALIABBADYBAAsgBUEgaiSA
gICAAAvkBQEIfyOAgICAAEEgayIEJICAgIAAIARBCGpBAmogAUELai0AADoAACAEIAEvAAk7AQggASgCECEFIAEoAgwhBiABLQAIIQcgASgCBCEIIAEoAgAh
CQJAAkAgAiADRw0AIAAgBzoACCAAIAg2AgQgACAJNgIAIAAgBTYCECAAIAY2AgwgACABQQlqIgEvAAA7AAkgAEELaiABQQJqLQAAOgAAQQAhAQwBCwJAAkAg
AigCACIKIAZqIgEgCkkNACABRQ0AIAVFDQACQEEADQAgAygCACIKIANBEGoiCygCAGoiBiAKSQ0BIAZBB3ENASAGRQ0BIARBFGogBiADKAIUIAVBf2pBBXZB
AWoQpIOAgAAgBC0AGEUNACAKIAMoAhhqIgYgCkkNASAGRQ0BIAQoAhQiCkH///8/Sw0BAkAgBiAKQQV0aiIGIAEgBWpPDQAgBiAFaiABSw0CCwJAIAVFDQAg
BiABIAX8CgAACyAGIAMoAgAiAWsiCiAGSw0BIAdBAXENAiACKAIAIgYgCWoiAiAGSQ0BIAJFDQEgCEUNAQJAAkACQEEADQAgASALKAIAaiIGIAFJDQQgBkEH
cQ0EIAZFDQQgBEEUaiAGIAMoAhQgCEF/akEFdkEBahCkg4CAACAELQAYDQEgAygCACEBCyABIApqIgMgAU8NAQwDCyABIAMoAhhqIgYgAUkNAiAGRQ0CIAQo
AhQiAUH///8/Sw0CAkAgBiABQQV0aiIBIAIgCGpPDQAgASAIaiACSw0DCwJAIAhFDQAgASACIAj8CgAACyAEQQA6AAwgASADKAIAayIJIAFLDQIgBC0ADCEH
DAMLIANFDQEgCyABIAMgBRDwg4CAAAtBGSEBDAILAAsgACAHOgAIIAAgCDYCBCAAIAk2AgAgACAELwEIOwAJIAAgBTYCECAAIAo2AgwgAEELaiAEQQpqLQAA
OgAAQQAhAQsgACABOwEUIARBIGokgICAgAALggYBB38jgICAgABBEGsiBiSAgICAAAJAAkACQCACIAEoAgRqIgcgAkkNACAHQQNxDQAgB0UNACAEQf//A3Ei
CEUNACAIIAEvAW5PDQECQAJAAkAgByAEQf//A3FBHGxqIggvARgNAAJAIAEvAWxFDQAgAEEEakEALwH25ceAADsBACAAQQAoAfLlx4AANgEADAYLIAEgAiAE
IAUpAgAQ8YOAgAAgBSgCACEIIAZBDGogASACIAMgBSkCABCog4CAAAJAIAYtAA5FDQAgCEUNBCADKAIQIQkgAygCDCEFIAYvAQwhAiAIQRBqIQogAy0ACEEB
cQ0CIAgoAgAiCyADKAIAaiIMIAtJDQQgDEUNBCAKIAsgDCADKAIEEPCDgIAADAILIAEgAiADIAQgBSkCABDyg4CAACECDAILIAUoAgAiByAFKAIEIAMgCBDv
g4CAAEEBcUUNAyAHRQ0CIAMoAhAhBSADKAIMIQIgB0EQaiEEAkAgAy0ACEEBcQ0AIAcoAgAiASADKAIAaiIKIAFJDQMgCkUNAyAEIAEgCiADKAIEEPCDgIAA
CyAHKAIAIgEgAmoiAiABSQ0CIAJFDQIgBCABIAIgBRDwg4CAACAILwEYQQFqIgFB//8DcSABRw0CIABBADYBACAIIAE7ARggAEEEakEAOwEADAQLIAgoAgAi
AyAFaiIFIANJDQEgBUUNASAKIAMgBSAJEPCDgIAACyAHIAJB//8DcUEcbGoiAy8BGEEBaiIHQf//A3EgB0cNACADQRhqIAc7AQAgASgCCEEBaiIHRQ0AIAEg
BzYCCEHysciAACEBAkAgAkH//wNxIARB//8DcUYNACAGQQE6AAQgBiACOwECIAZBADsBBiAGQQJqIQELIAAgASgBADYBACAAQQRqIAFBBGovAQA7AQAMAgsA
CyAGQQhqIAEgAiADIAUQ84OAgAACQCAGLwEKIgFFDQAgACABOwEEDAELIABBADsBBCAAQQE6AAIgACAGLwEIOwEACyAGQRBqJICAgIAAC/oFAQp/I4CAgIAA
QRBrIgUkgICAgAACQAJAAkACQCACIAEoAgRqIgYgAkkNACAGQQNxDQAgBkUNACAEQf//A3EiB0UNACAHIAEvAWZPDQECQAJAAkACQAJAIAYgBEH//wNxQSRs
aiIHLwEgIggNAEEZIQcgAS8BZA0IIAEgAiAEEPSDgIAAIAVBDGogASACIAMQ9YOAgAAgBS0ADkUNASAFLwEMIQIMAgsgAy8BGCAHLwEYRw0FIAMtAAQiBiAH
LQAERw0FIANBEmotAAAhCSADLwEQIQogA0EKai0AACELIAMvAQghDCADLwEAIANBAmotAABBEHRyIQ0gAy0AFCEOIAMtAAwhBAJAAkACQCAGDgMCAAEHCyAH
LQAAIA1B/wFxRw0HDAELIA1B////B3EgBy8BACAHQQJqLQAAQRB0ckcNBgsgBCAHLQAMRw0FIAwgC0EQdHIhBgJAAkACQCAEDgMCAAEHCyAHLQAIIAZB/wFx
Rw0HDAELIAZB////B3EgBy8BCCAHQQpqLQAAQRB0ckcNBgsgDiAHLQAURw0FIAogCUEQdHIhBgJAIA4OAwQDAAULIAZB////B3EgBy8BECAHQRJqLQAAQRB0
ckcNBQwDCyABIAIgAyAEEPaDgIAAIQILIAYgAkH//wNxQSRsaiIDLwEgQQFqIgZB//8DcSAGRw0CIANBIGogBjsBACABKAIIQQFqIgZFDQIgASAGNgIIQfKx
yIAAIQECQCACQf//A3EgBEH//wNxRg0AIAVBAToAAiAFIAI7AQAgBSEBCyABKAEAIgFBgID8B3EhAiABQYCAgHhxIQYMBAsgBy0AECAGQf8BcUcNAgsgCEEB
aiIBQf//A3EgAUcNACAHIAE7ASBBACEGQQAhAkEAIQEMAgsACyAFQQhqIAEgAiADEJSDgIAAIAUvAQoiBw0BQYCABCECQQAhBiAFLwEIIQELIAAgAiAGciAB
Qf//A3FyNgEAQQAhBwsgACAHOwEEIAVBEGokgICAgAALugEBBX8jgICAgABBEGsiAiSAgICAAAJAIAEgACgCACIDayIEIAFLDQAgAyAAKAIoaiIFIANJDQAg
BUUNACACIAU2AgAgAkEEaiAFIAQQrYKAgAAgAi0ADEUNACADIAIoAggiBCgCAGoiBSADSQ0AIAVBA3ENACAFRQ0AIAIoAgQhBiAAQRxqIAMgBSAEKAIEEKKD
gIAAIAIgBhCugoCAACABIAEpAwBCfIM3AwAgAkEQaiSAgICAAA8LAAuHAgEEfyOAgICAAEEQayICJICAgIAAAkAgASAAKAIAIgNrIgQgAUsNACADIAAoApQB
aiIFIANJDQAgBUUNACACIAU2AgAgAkEEaiAFIAQQsIKAgAACQCACLQAMRQ0AIAIoAggvAQAiBEUNASAAKAKkASAETQ0BIAMgACgCnAFqIgUgA0kNASAFQQNx
DQEgBUUNASAFIARBHGxqIgMvARgiBUUNASADQRhqIAVBf2oiAzsBAAJAIANB//8DcQ0AIAAoAqABIgNBf2oiBSADSw0CIAAgBTYCoAELIAIgAigCBBCxgoCA
ACABIAEpAwBC////////d4M3AwALIAJBEGokgICAgAAPCwALijcGCX8Bfg5/AXwXfwN+I4CAgIAAQfAAayICJICAgIAAAkAgASgCWCIDRQ0AAkACQAJAAkAg
Ay8BogIgAy8BqgIiBEsNACACQdAAakGo38GAACABLwGMASIFQQEQkoCAgAACQAJAIAIvAWBFDQBB1wEhBkGAASEHQfAAIQhBgMAAIQlBgBAhCgwBCyACKQNQ
IgtCIIinIQogC6chCSACLwFcIQcgAi8BXiEIIAIvAVohBiACLwFYIQULIAEoAlQiDEUNAiAMIANHDQEMAgsgAyAEQQFqOwGqAiABKAJoQQFqIgRFDQMgASAE
NgJoIABBACkDwJ/HgAA3AgAMAgsgASgCXCIEQYCAJGoiDSAESQ0CIA0gAUHgAGooAgAiBCABQeQAaigCACIOIAQgDksbTQ0AAkACQAJAAkAgDCgCCCINRQ0A
IA0gDCgCDCIENgIMIAQNAQwCCyABIAwoAgwiBDYCVCAERQ0BCyAEIAwoAgg2AggMAQsgASAMKAIINgJYCyABKAJoIg0gDC8BqgJrIgQgDUsNAiABIAQ2Amgg
BEEBaiIERQ0CAkACQAJAAkAgBCABLwGOAU8NAAJAIAEoAlQiBEUNACAMIAQ2AgwgBCgCCCIDRQ0CIAwgAzYCCCADIAw2AgwgBCAMNgIIDAQLIAEgDDYCWCAB
IAw2AlQgDEIANwIIDAMLIAEtAJABQQJHDQEgAS0AiAFFDQECQCABKAKEASIEIAwvAaoCIg1PDQAgAUEBOgCQAQwCCyABIAQgDWs2AoQBDAELIAxBADYCCCAB
IAw2AlQgBCAMNgIIDAELIAEoAnQiBEGAgICABE8NAwJAAkAgBA0AQQAhDkGq1arVeiEEDAELIAEoAmwiBEEDcQ0EIAEoAnAhDgsCQCAORQ0AA0ACQCAEKAIA
Ig0oAgAgDEcNACABKAJUIg9FDQYgDUEBOgAIIA1BADYBBCANIA82AgALIARBBGohBCAOQX9qIg4NAAsLIAEoAoABQQA6AAgCQCAMKAIUIgRBgIAkSw0AIAwo
AhAhDwJAIARFDQAgD0EAIAT8CwALIAVB//8DcSIQIAZB//8DcSIRbCISQYCAgIACTw0EIBFBA3QiEyASQQN0aiIUIBNJDQQCQAJAIAcNAEEAIRVBACEWQQAh
F0EAIRhBACEZDAELQgFBICAHQX9qZyIEa61C//8Dg4anIha4RAAAAAAAAOo/oiIaRAAAAAAAAPC/ZQ0FIBpEAAAAAAAA8EFmDQUgGvwDIhWtQiR+IgtCIIin
DQUgFkEBdEEDakH8////B3EiFyALp2oiGCAXSQ0FQX9BACAEa3RBf3MhGQsgFCAYaiINIBRJDQQgCUEPaiIEIAlJDQQgBEGAgICAAU8NBCAEQQR2QT9qQQZ2
IhtBA3QiHCAEQQR0QYB+cWoiBCAcSQ0EIA1BB2oiDiANSQ0EIA5BeHEiHSAEaiIeIB1JDQQCQAJAIAkNAEEAIR8MAQtCAUEgIAlBf2pBBHZna61C//8Dg4an
IR8LIB4gH0ETakF8cSIgIB9BAnRqIiEgH0EDdGpqIg0gHkkNBCAKQR9qIgQgCkkNBCAEQYCAgMAATw0EIARBBXZBP2pBBnYiIkEDdCIjIARBBXRBgHhxaiIE
ICNJDQQgDUEHaiIOIA1JDQQgDkF4cSIkIARqIiUgJEkNBCAIQf//A3EiBEEcbiENAkACQCAEQRxPDQBBECEmQQAhJyAlIQ5BACEoQQAhKUEAISpBACErQQAh
LAwBC0IBQSAgDUF/amciBGutQv//A4OGpyIouEQAAAAAAADqP6IiGkQAAAAAAADwv2UNBSAaRAAAAAAAAPBBZg0FIBr8AyInrUIcfiILQiCIpw0FIChBAXRB
A2pB/P///wdxIikgC6dqIiogKUkNBSAlICpqIg4gJUkNBSANQQR0EKGAgIAAIiwgLEF/anENBSAsQYCAgIAETw0FQX9BACAEa3RBf3MhKyAsQRNqQXxxISYL
ICYgLEECdGoiBCAmSQ0EIAQgLEEBdGoiDSAESQ0EIA1BA2oiLSANSQ0EIARBcGoiLiAESw0EIA5BA2oiBCAOSQ0EIARBfHEiLyAtQXxxaiIEIC9JDQQgBEH/
/wNqIjAgBEkNBCAPIBNqIgQgD0kNBCAEQQdxDQQgBEUNBCAPQQdxDQQgD0UNBAJAAkACQCAGQf//A3FFDQAgEEEDdCExQQAhDSAPIQ4DQCANIBJPDQIgBCAP
ayItIARLDQggDiAtrTcDACAEIDFqIQQgDSAQaiENIA5BCGohDiARQX9qIhENAAsLIDBBgIB8cSEQAkAgD0H4/wNxRQ0AIBANBwsgFCAXaiItIBRJDQYgDyAU
aiIEIA9JDQYgBEUNBgJAIBZBAXQiDUUNACAEQQAgDfwLAAsgDyAtaiIRIA9JDQYgEUUNBgJAIBVFDQAgESEEAkAgFUEkbCIxQVxqIhJBJG4iDUEDcUEDRg0A
QQAgDUEBakEDcWshDUEAKAK8uciAACEOQQApArS5yIAAIQtBACkCrLnIgAAhMkEAKQKkuciAACEzQQApApy5yIAAITQgESEEA0AgBEEgaiAONgIAIARBGGog
CzcCACAEQRBqIDI3AgAgBEEIaiAzNwIAIAQgNDcCACAEQSRqIQQgDUEBaiINDQALCyASQewASQ0AIBEgMWohDkEAKAK8uciAACENQQApArS5yIAAIQtBACkC
rLnIgAAhMkEAKQKkuciAACEzQQApApy5yIAAITQDQCAEQSBqIA02AgAgBEEYaiALNwIAIARBEGogMjcCACAEQQhqIDM3AgAgBCA0NwIAIARBJGogNDcCACAE
QSxqIDM3AgAgBEE0aiAyNwIAIARBPGogCzcCACAEQcQAaiANNgIAIARByABqIDQ3AgAgBEHQAGogMzcCACAEQdgAaiAyNwIAIARB4ABqIAs3AgAgBEHoAGog
DTYCACAEQYwBaiANNgIAIARBhAFqIAs3AgAgBEH8AGogMjcCACAEQfQAaiAzNwIAIARB7ABqIDQ3AgAgBEGQAWoiBCAORw0ACwsgDyAkaiIEIA9JDQYgBEUN
BgJAICNFDQAgBEH/ASAj/AsACyAkICNqIjEgJEkNBiAPIB1qIgQgD0kNBiAERQ0GAkAgHEUNACAEQf8BIBz8CwALIB0gHGoiHCAdSQ0GIA8gHmoiBEEQaiIN
QQNxDQYgBEEANgIMIAQgHzYCCCAEICBBcGo2AgQgBCAhQXBqNgIAAkAgH0UNACANQQAgH/wLAAsgDSAPayIfIA1LDQYgDyAvaiIEQQA2AgwgBCAsNgIIIAQg
JkFwajYCBCAEIC42AgAgBEEQaiEEAkAgLEUNACAEQQAgLPwLAAsgL0EQaiIsIARLDQYgJSApaiISICVJDQYgDyAlaiIEIA9JDQYgBEUNBgJAIChBAXQiDUUN
ACAEQQAgDfwLAAsgDyASaiIRIA9JDQYgEUUNBiAKrSE0AkAgJ0UNACARIQQCQCAnQRxsIiNBZGoiCkEcbiINQQNxQQNGDQBBACANQQFqQQNxayENQQAoAti5
yIAAIQ5BACkC0LnIgAAhC0EAKQLIuciAACEyQQApAsC5yIAAITMgESEEA0AgBEEYaiAONgIAIARBEGogCzcCACAEQQhqIDI3AgAgBCAzNwIAIARBHGohBCAN
QQFqIg0NAAsLIApB1ABJDQAgESAjaiEOQQAoAti5yIAAIQ1BACkC0LnIgAAhC0EAKQLIuciAACEyQQApAsC5yIAAITMDQCAEQRhqIA02AgAgBEEQaiALNwIA
IARBCGogMjcCACAEIDM3AgAgBEEcaiAzNwIAIARBJGogMjcCACAEQSxqIAs3AgAgBEE0aiANNgIAIARBOGogMzcCACAEQcAAaiAyNwIAIARByABqIAs3AgAg
BEHQAGogDTYCACAEQdQAaiAzNwIAIARB3ABqIDI3AgAgBEHkAGogCzcCACAEQewAaiANNgIAIARB8ABqIgQgDkcNAAsLIAhBEHQhBCA0QiCGIQsgCa0hMiAM
IBk7AVwgDCAYNgJYIAwgFzYCVCAMQQA2AlAgDCAWNgJMIAwgFTYCSCAMQQA2AkQgDCAtNgJAIAwgFDYCPCAMIB82AjggDCAcNgI0IAwgGzYCMCAMIB02Aiwg
DCAxNgIoIAwgIjYCJCAMICQ2AiAgDCATNgIcIAxBADYCGCAMIBA2AhQgDCAPNgIQIAJBADoABAJAQcIARQ0AIAxB4ABqQQBBwgD8CwALIAQgB3IhBCALIDKE
IQsgDCArOwHIASAMICo2AsQBIAwgKTYCwAEgDEEANgK8ASAMICg2ArgBIAwgJzYCtAEgDEEANgKwASAMIBI2AqwBIAwgJTYCqAEgDCAsNgKkASAMQQE7AaIB
AkBBygBFDQAgDEHMAWpBAEHKAPwLAAsgDCAFOwGoAiAMIAQ2AqQCIAwgBjsBogIgDCAFOwGgAiAMIAs3ApgCIAxBATsBlgIgDEEBOwGqAiAMIAM2AgggDCAC
LQAEOgCsAgJAIAMoAgwiBEUNACAMIAQ2AgwgBCAMNgIIDAILIAxBADYCDCABIAw2AlgMAQsgDSASEIiAgIAAAAsgAyAMNgIMIAEoAmhBAWoiBEUNBCABIAQ2
AmggDCkDAEIBfCILQgBRDQQgASALNwMIIAwgASkDACILNwMAIAtCAXwiC0IAUQ0EIABBADsBBCAAIAw2AgAgASALNwMADAMLIAEoAlwiAyAEayIEIANLDQMg
ASAENgJcAkACQCAMKAIUIgRBgIAkSw0AAkAgBEUNACAMKAIQQQAgBPwLAAsgDCgCECEEAkBBgIAkRQ0AIARBqgFBgIAk/AsACyAEIAEoAjw2AgAgASAENgI8
DAELIAEoAiwhDSABKAIwIQ4gDCgCECEDAkAgBEUNACADQaoBIAT8CwALIA0gAyAEQRBBACAOKAIMEYGAgIAAgICAgAALAkBBsAJFDQAgDEGqAUGwAvwLAAsg
DCABKAIoNgIAIAEgDDYCKAwBCyABKAJUIAxHDQIgASgCaCIEIAwvAaoCaiIMIARJDQIgASAMNgJoCwJAAkACQAJAIAEoAigiDUUNACABIA0oAgA2AigMAQtB
AA0BIAFBGGpBsAJBAyAEEI+AgIAAIg1FDQECQEGwAkUNACANQaoBQbAC/AsACyANQQdxDQQLAkBBsAJFDQAgDUGqAUGwAvwLAAsgBUH//wNxIhIgBkH//wNx
Ig9sIi1BgICAgAJPDQMgD0EDdCIeIC1BA3RqIhQgHkkNAwJAAkAgBw0AQQAhIUEAISlBACEWQQAhFUEAIScMAQtCAUEgIAdBf2pnIgRrrUL//wODhqciFbhE
AAAAAAAA6j+iIhpEAAAAAAAA8L9lDQQgGkQAAAAAAADwQWYNBCAa/AMiJ61CJH4iC0IgiKcNBCAVQQF0QQNqQfz///8HcSIWIAunaiIpIBZJDQRBf0EAIARr
dEF/cyEhCyAUIClqIgwgFEkNAyAJQQ9qIgQgCUkNAyAEQYCAgIABTw0DIARBBHZBP2pBBnYiG0EDdCIcIARBBHRBgH5xaiIEIBxJDQMgDEEHaiIDIAxJDQMg
A0F4cSIlIARqIiQgJUkNAwJAAkAgCQ0AQQAhLAwBC0IBQSAgCUF/akEEdmdrrUL//wODhqchLAsgJCAsQRNqQXxxIhkgLEECdGoiMCAsQQN0amoiDCAkSQ0D
IApBH2oiBCAKSQ0DIARBgICAwABPDQMgBEEFdkE/akEGdiIiQQN0IiMgBEEFdEGAeHFqIgQgI0kNAyAMQQdqIgMgDEkNAyADQXhxIh0gBGoiHyAdSQ0DIAhB
//8DcSIMQRxuIQNBACEgIB8hBEEAIRhBACETQQAhJkEAIS9BACExAkAgDEEcSQ0AQgFBICADQX9qZyIMa61C//8Dg4anIia4RAAAAAAAAOo/oiIaRAAAAAAA
APC/ZQ0EIBpEAAAAAAAA8EFmDQQgGvwDIi+tQhx+IgtCIIinDQQgJkEBdEEDakH8////B3EiEyALp2oiGCATSQ0EIB8gGGoiBCAfSQ0EQX9BACAMa3RBf3Mh
IEIBQSAgA0EEdEF/amdrrYanITELIARBA2oiDCAESQ0DIAxBfHEiKCAxQQF0IDFBE2pBfHEiLiAxQQJ0aiIrakEDakF8cWoiBCAoSQ0DIARB//8DaiIMIARJ
DQMgAUEsaiEEAkACQCAMQYCAfHEiF0GBgCRJDQBBAA0BIAQpAgAiC6cgF0EQQQAgC0IgiKcoAgARgICAgACAgICAACIORQ0BAkAgF0UNACAOQaoBIBf8CwAL
IBchKiAOQf//A3ENBQwDCwJAAkAgASgCPCIORQ0AIAEgDigCADYCPAwBC0EADQEgBEGAgCRBECAEEI+AgIAAIg5FDQECQEGAgCRFDQAgDkGqAUGAgCT8CwAL
IA5B//8DcQ0FC0GAgCQhKkGAgCRFDQIgDkGqAUGAgCT8CwAMAgsCQEGwAkUNACANQaoBQbAC/AsACyANIAEoAig2AgAgASANNgIoCyAAQRk7AQQMAQsCQCAq
RQ0AIA5BACAq/AsACyAOIB5qIgQgDkkNASAEQQdxDQEgBEUNASAOQQdxDQECQAJAAkACQAJAIAZB//8DcUUNACASQQN0IRBBACEMIA4hAwNAIAwgLU8NAiAE
IA5rIhEgBEsNByADIBGtNwMAIAQgEGohBCAMIBJqIQwgA0EIaiEDIA9Bf2oiDw0ACwsCQCAXRQ0AIA5B+P8DcQ0GCyAUIBZqIhEgFEkNBSAOIBRqIgQgDkkN
BSAERQ0FAkAgFUEBdCIMRQ0AIARBACAM/AsACyAOIBFqIg8gDkkNBSAPRQ0FAkAgJ0UNACAPIQQCQCAnQSRsIhJBXGoiLUEkbiIMQQNxQQNGDQBBACAMQQFq
QQNxayEMQQAoAry5yIAAIQNBACkCtLnIgAAhC0EAKQKsuciAACEyQQApAqS5yIAAITNBACkCnLnIgAAhNCAPIQQDQCAEQSBqIAM2AgAgBEEYaiALNwIAIARB
EGogMjcCACAEQQhqIDM3AgAgBCA0NwIAIARBJGohBCAMQQFqIgwNAAsLIC1B7ABJDQAgDyASaiEDQQAoAry5yIAAIQxBACkCtLnIgAAhC0EAKQKsuciAACEy
QQApAqS5yIAAITNBACkCnLnIgAAhNANAIARBIGogDDYCACAEQRhqIAs3AgAgBEEQaiAyNwIAIARBCGogMzcCACAEIDQ3AgAgBEEkaiA0NwIAIARBLGogMzcC
ACAEQTRqIDI3AgAgBEE8aiALNwIAIARBxABqIAw2AgAgBEHIAGogNDcCACAEQdAAaiAzNwIAIARB2ABqIDI3AgAgBEHgAGogCzcCACAEQegAaiAMNgIAIARB
jAFqIAw2AgAgBEGEAWogCzcCACAEQfwAaiAyNwIAIARB9ABqIDM3AgAgBEHsAGogNDcCACAEQZABaiIEIANHDQALCwJAQcIARQ0AIAJBDGpBAEHCAPwLAAsg
DiAdaiIEIA5JDQUgBEUNBQJAICNFDQAgBEH/ASAj/AsACyAdICNqIhIgHUkNBSAOICVqIgQgDkkNBSAERQ0FAkAgHEUNACAEQf8BIBz8CwALICUgHGoiECAl
SQ0FIA4gJGoiBEEQaiIMQQNxDQUgBEEANgIMIAQgLDYCCCAEIBlBcGo2AgQgBCAwQXBqNgIAAkAgLEUNACAMQQAgLPwLAAsgDCAOayIsIAxLDQUgDiAoaiIE
QQA2AgwgBCAxNgIIIAQgLkFwajYCBCAEICtBcGo2AgAgBEEQaiEEAkAgMUUNACAEQQAgMfwLAAsgBCAOayIxIARLDQUgHyATaiItIB9JDQUgDiAfaiIEIA5J
DQUgBEUNBQJAICZBAXQiDEUNACAEQQAgDPwLAAsgDiAtaiIPIA5JDQUgD0UNBSAKrSE0AkAgL0UNACAPIQQCQCAvQRxsIiRBZGoiCkEcbiIMQQNxQQNGDQBB
ACAMQQFqQQNxayEMQQAoAti5yIAAIQNBACkC0LnIgAAhC0EAKQLIuciAACEyQQApAsC5yIAAITMgDyEEA0AgBEEYaiADNgIAIARBEGogCzcCACAEQQhqIDI3
AgAgBCAzNwIAIARBHGohBCAMQQFqIgwNAAsLIApB1ABJDQAgDyAkaiEDQQAoAti5yIAAIQxBACkC0LnIgAAhC0EAKQLIuciAACEyQQApAsC5yIAAITMDQCAE
QRhqIAw2AgAgBEEQaiALNwIAIARBCGogMjcCACAEIDM3AgAgBEEcaiAzNwIAIARBJGogMjcCACAEQSxqIAs3AgAgBEE0aiAMNgIAIARBOGogMzcCACAEQcAA
aiAyNwIAIARByABqIAs3AgAgBEHQAGogDDYCACAEQdQAaiAzNwIAIARB3ABqIDI3AgAgBEHkAGogCzcCACAEQewAaiAMNgIAIARB8ABqIgQgA0cNAAsLIDRC
IIYhCyAJrSEyIAhBEHQhBCANICE7AVwgDSApNgJYIA0gFjYCVCANQQA2AlAgDSAVNgJMIA0gJzYCSCANQQA2AkQgDSARNgJAIA0gFDYCPCANICw2AjggDSAQ
NgI0IA0gGzYCMCANICU2AiwgDSASNgIoIA0gIjYCJCANIB02AiAgDSAeNgIcIA1BADYCGCANIBc2AhQgDSAONgIQIAJBADoACAJAQcIARQ0AIA1B4ABqIAJB
DGpBwgD8CgAACyALIDKEIQsgBCAHciEEIA0gIDsByAEgDSAYNgLEASANIBM2AsABIA1BADYCvAEgDSAmNgK4ASANIC82ArQBIA1BADYCsAEgDSAtNgKsASAN
IB82AqgBIA0gMTYCpAEgDUEBOwGiAQJAQcoARQ0AIA1BzAFqQQBBygD8CwALIA0gBjsBqgIgDSAFOwGoAiANIAQ2AqQCIA0gBjsBogIgDSAFOwGgAiANIAs3
ApgCIA1BATsBlgIgDSACLQAIOgCsAiANIAEpAwA3AwAgDUEAOwGqAiANQgA3AgggASkDAEIBfCILUA0FIAEgCzcDACABKAJcIgQgKmoiDCAESQ0FIAEgDDYC
XCABKAJYIgRFDQIgDSAENgIIIAQoAgwiDEUNASANIAw2AgwgDCANNgIIIAQgDTYCDAwDCyAMIC0QiICAgAAACyANQQA2AgwgASANNgJYIAQgDTYCDAwBCwJA
AkAgASgCVCIERQ0AIA0gBDYCDCAEKAIIIgxFDQEgDSAMNgIIIAwgDTYCDCAEIA02AggMAgsgASANNgJYIAEgDTYCVCANQgA3AggMAQsgDUEANgIIIAEgDTYC
VCAEIA02AggLIA1BATsBqgIgASgCaEEBaiIERQ0BIABBADsBBCAAIA02AgAgASAENgJoCyACQfAAaiSAgICAAA8LAAurAgEDfwJAIAEvAQQiA0EBaiIEQf//
A3EiBSAERw0AIAEoAgAiBC8BqgIgBWsiBUH//wNxIAVHDQACQCACIAVB//8DcSIFSw0AIAAgBDYCACAAQQA6ABAgAEEAOgAIIAAgAS8BBjsBBiAAIAIgA2oi
AkH//wMgAkH//wNJGzsBBA8LIAIgBWshAgJAAkADQCAEKAIMIgVFDQEgAiAFIgQvAaoCIgVNDQIgAiAFayECDAALCyAAIAQ2AgAgAEEBOgAQIAQvAaoCQX9q
IgRB//8DcSAERw0BIAAgBDsBBCAAIAI2AgwgAEEAOgAIIAAgAS8BBjsBBg8LIAAgBDYCACAAQQA6ABAgAkF/aiIEIAJLDQAgACAEOwEEIABBADoACCAAIAEv
AQY7AQYPCwALuAICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE7IANrIgVqIgYgBEkNAiADQY3hyIAAaiEHAkACQCAGIAAoAggi
CEsNACAGIAhLDQUCQCAAKAIEIARqIgZByOHIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAA
KAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSED
IAVBO0kNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAuqDAMHfwF+BH8jgICAgABB0ABrIgIkgICAgAAgAkEMakECaiAB
QQtqLQAAOgAAIAIgAS8ACTsBDCABLQAIIQMgASgCBCEEIAEoAgAhBSABKAIMIQYgASgCECEHIAAQuoGAgABBGSEIAkAgACkDmAEiCadBFEECQQAgCUIgiKco
AgARgICAgACAgICAACIBRQ0AIAFCqtWq1arVqtWqfzcAACABQRBqQarVqtV6NgAAIAFBCGpCqtWq1arVqtWqfzcAAAJAAkAgAUEDcQ0AAkACQEEADQAgACkD
mAEiCUIgiKchCiAJpyELAkACQCAHDQBBfyEMDAELIAsgB0EAQQAgCigCABGAgICAAICAgIAAIgxFDQEgB0UNACAMQaoBIAf8CwALAkAgDCAGIAdqTw0AIAYg
DCAHakkNAwsCQCAHRSINDQAgDCAGIAf8CgAACwJAIANBAXFFDQAgAkEgakECaiACQQxqQQJqLQAAOgAAIAIgAi8BDDsBICAFIQYMAgsgAkEAOgBEAkBBAA0A
AkACQCAEDQBBfyEGDAELIAsgBEEAQQAgCigCABGAgICAAICAgIAAIgZFDQEgBEUNACAGQaoBIAT8CwALAkAgBiAFIARqTw0AIAUgBiAEakkNBAsCQCAERQ0A
IAYgBSAE/AoAAAsgAi0ARCEDDAILIAdFDQACQCANDQAgDEGqASAH/AsACyALIAwgB0EAQQAgCigCDBGBgICAAICAgIAACyAAKAKYASABQRRBAkEAIABBnAFq
KAIAQQxqKAIAEYGAgIAAgICAgAAMAwsgAkEQakECaiACQSBqQQJqLQAAIgg6AAAgAiACLwEgIgU7ARAgASADOgAIIAEgBDYCBCABIAY2AgAgASAFOwAJIAFB
C2ogCDoAACABIAc2AhAgASAMNgIMIAdFDQACQAJAAkACQEEADQAgACgCxAEoAgAiCCgCECIDIAhBIGoiBCgCAGoiBiADSQ0EIAZBB3ENBCAGRQ0EIAJBxABq
IAYgCCgCJCAHQX9qQQV2QQFqEKSDgIAAIAItAEhFDQAgAyAIKAIoaiIGIANJDQQgBkUNBCACKAJEIgNB////P0sNBCAHIAEoAhBHDQQCQCABKAIMIgUgBiAD
QQV0aiIGIAdqTw0AIAYgBSAHakkNBQsgCEEQaiEDAkAgDQ0AIAYgBSAH/AoAAAsgBiADKAIAIgdrIgUgBksNBCABKAIAIQogASgCECEMIAEtAAhBAXENAiAB
KAIEIgZFDQQCQEEADQAgByAEKAIAaiILIAdJDQUgC0EHcQ0FIAtFDQUgAkHEAGogCyAIKAIkIAZBf2pBBXZBAWoQpIOAgAAgAi0ASA0CIAMoAgAhBwsgByAF
aiIIIAdJDQQgCEUNBCAEIAcgCCAMEPCDgIAAC0EnIQgMBAsgByAIKAIoaiILIAdJDQIgC0UNAiACKAJEIgdB////P0sNAgJAIAsgB0EFdGoiCyAKIAZqTw0A
IAsgBmogCksNAwsCQCAGRQ0AIAsgCiAG/AoAAAsgAkEAOgAcIAsgAygCACIHayIKIAtLDQIMAQsgAkEBOgAcCyACIAY2AiQgAiAKNgIgIAIgDDYCMCACIAU2
AiwgAiACLQAcIgs6ACggAkEANgI4IAIgAzYCNCACQTxqIAhBqAFqIAcgAkEgaiACQTRqEPODgIAAAkACQAJAAkAgAi8BPiIHQWdqDg4BBAQEBAQEBAQEBAQE
AgALIAcNAyAAIAIvATw7AdYBIAAgATYCwAFBACEIDAULIAtBAXENASADKAIAIgcgCmoiCCAHSQ0CIAhFDQIgBCAHIAggBhDwg4CAAAwBCwJAIAtBAXENACAD
KAIAIgcgCmoiCCAHSQ0CIAhFDQIgBCAHIAggBhDwg4CAAAsgAygCACIHIAVqIgggB0kNASAIRQ0BIAQgByAIIAwQ8IOAgABBKSEIDAILIAMoAgAiByAFaiII
IAdJDQAgCEUNACAEIAcgCCAMEPCDgIAAQSghCAwBCwALIAEgACkDmAEQnYCAgAAgACgCmAEgAUEUQQJBACAAQZwBaigCAEEMaigCABGBgICAAICAgIAACyAC
QdAAaiSAgICAACAIC7gCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgQgA0E/cyIFaiIGIARJDQIgA0HI4ciAAGohBwJAAkAgBiAA
KAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQYfiyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwM
AQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0C
IAUhAyAFQT9JDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALmAEAAkACQAJAAkAgAkH/AXEOAwABAgMLIABCADcDACAA
QQhqQgA3AwAPCyAAIAApAwBCgPj/n4CAQIMgAUECdEH8B3FBAnKthDcDACAAQQE6AAggAEEAOwEGDwsgACABQYCA/AdxQRB2rUIShiABQQJ0Qfz/D3FBA3Kt
hCAAMwEGQjCGhDcDACAAQQE6AAggAEEAOwEGDwsAC8IPAgt/AX4jgICAgABBMGsiASSAgICAAAJAIAAoAsQBIgIvAQQiA0EBaiIEQf//A3EiBSAERw0AIAIo
AgAiBi8BqgIiByAFSQ0AIAIvAQYhCCAGIQUCQCAHIARB//8DcUcNACAGKAIMIgVFDQFBASEHAkADQCAHIAUvAaoCIgRNDQEgByAEayEHIAUoAgwiBQ0ADAML
CyAHQX9qIgQgB0sNAQsgAUEAOgAEAkACQCAGIAVHDQAgAyAEQf//A3FGDQELIAAoAsgBIgcgBykDAEKAgICAgCCENwMAIARB//8DcSICIAUvAaoCTw0BIAhB
//8DcSAFLwGoAk8NASAFKAIQIgcgBSgCGGoiBiAHSQ0BIAZBB3ENASAGRQ0BIAcgBiACQQN0aiICKAIAaiIGIAdJDQEgBkEHcQ0BIAZFDQEgAiACKQMAQoCA
gICAIIQ3AwAgACgCxAEiAigCACEGCwJAAkAgBiAFRw0AIAJBADYCCCACIAg7AQYgAiAEOwEEIAIgBTYCAAwBCyAAQaABaiEHAkACQCAALwHUASIDDQBBlLPI
gAAhCUH4ssiAACECDAELIAFBCGpBGGogB0EYaigCADYCACABQQhqQRBqIAdBEGopAgA3AwAgAUEIakEIaiAHQQhqKQIANwMAIAFBAToAJCABIAcpAgA3Awgg
BigCSCADTQ0CIAYoAhAiCSAGKAJAaiICIAlJDQIgAkEDcQ0CIAJFDQIgAiADQSRsaiICLwEgIgNFDQIgAkEgaiADQX9qIgI7AQACQCACQf//A3ENACAGKAJE
IgJBf2oiAyACSw0DIAYgAzYCRAsgAUEkaiEJIAdCADcDACAAQQA7AdQBIAdBGGpBADYCACAHQRBqQgA3AwAgB0EIakIANwMAIAFBCGohAgsCQCAAKALAAUUN
ACAALwHWASIKRQ0CIAAoAsQBKAIAIgMoArQBIApNDQIgAygCECILIAMoAqwBaiIGIAtJDQIgBkEDcQ0CIAZFDQIgBiAKQRxsaiIGLwEYIgpFDQIgBkEYaiAK
QX9qIgY7AQAgBkH//wNxDQAgAygCsAEiBkF/aiIKIAZLDQIgAyAKNgKwAQsgACgCxAEiBkEANgIIIAYgCDsBBiAGIAQ7AQQgBiAFNgIAAkAgCS0AAEUNACAH
IAIpAgA3AgAgB0EYaiIFIAJBGGooAgA2AgAgB0EQaiIEIAJBEGopAgA3AgAgB0EIaiIGIAJBCGopAgA3AgAgABDAgICAAEH//wNxIgJFDQAgASACOwEsIAFB
LGoQmoOAgAAgBUEANgIAIARCADcDACAGQgA3AwAgB0IANwMAIABBADsB1AELIAAoAsABIgVFDQAgAEEANgLAASAAQQA7AdYBAkAgACAFKAIMIAUoAhBBACAF
KAIAIAUtAAhBAXEiBxtBACAFKAIEIAcbEJGDgIAAIgdB//8DcUUNACABIAc7AS4gAUEuahCbg4CAAAsgACkDmAEiDEIgiKchBCAMpyEGAkAgBSgCECIHRQ0A
IAUoAgwhAgJAIAdFDQAgAkGqASAH/AsACyAGIAIgB0EAQQAgBCgCDBGBgICAAICAgIAACwJAIAUtAAhBAXENACAFKAIEIgdFDQAgBSgCACECAkAgB0UNACAC
QaoBIAf8CwALIAYgAiAHQQBBACAEKAIMEYGAgIAAgICAgAALIAAoApgBIAVBFEECQQAgAEGcAWooAgBBDGooAgARgYCAgACAgICAAAsgACgCWCIFRQ0AAkAC
QAJAAkACQANAIAUgACgCxAEiBygCAEYNASAFKAIIIgRFDQYgBCgCECIHIAQoAhhqIgIgB0kNBiACQQdxDQYgAkUNBiAFKAIQIgYgBSgCGGoiByAGSQ0GIAdB
B3ENBiAHRQ0GIAUvAaoCIghBf2oiBiAISw0GIAYgCE8NBSAGIAZHDQYgByAGQQN0IgZqKQMAIQwCQCAGRQ0AIAdBCGogByAG/AoAAAsgByAMNwMAIAQvAaoC
QX9qIgZB//8DcSIIIAZHDQYgBUEQaiAEQRBqIAcgAiAIQQN0akEAIAUvAagCEJeDgIAAIgdB//8DcQ0CIAVBAToArAIgBSgCCCIFRQ0GDAALCyAFKAIQIgYg
BSgCGGoiBCAGSQ0EIARBB3ENBCAERQ0EIAcvAQQiAiAFLwGqAiIHSw0BIAcgAmsiB0F/aiIGIAdLDQQgBiAHTw0CIAdBf2ogBkcNBCAFQRBqIQggBCACQQN0
aiIHIAZBA3QiBmopAwAhDAJAIAZFDQAgB0EIaiAHIAb8CgAACyAHIAw3AwAgCCgCACIGIAQgACgCxAEvAQRBA3RqIgQoAgBqIgcgBkkNBCAHQQdxDQQgB0UN
BCAAIAggBCAHIAUvAagCELOCgIAAIAVBAToArAIgACgCxAEiBy8BBCIEIAcoAgAiBS8BqgJPDQQgBy8BBiIGIAUvAagCTw0EIAUoAhAiByAFKAIYaiIFIAdJ
DQQgBUEHcQ0EIAVFDQQgByAFIARBA3RqIgQoAgBqIgUgB0kNBCAFQQdxDQQgBUUNBCAAIAQ2AsgBIAAgBSAGQQN0ajYCzAFBACEHCyABQTBqJICAgIAAIAcP
CyACIAcQioCAgAAACyAGIAcQiICAgAAACyAGIAgQiICAgAAACwAL5SoDCH8BfgV/I4CAgIAAQfAAayIBJICAgIAAIAAoAsgBIgIgAikDAEKAgICAgCCENwMA
AkACQAJAAkACQCAALwGOASICQX9qIgNB//8DcSIEIANHDQACQAJAAkACQAJAIAAvAdIBIgVB//8DcSAERw0AIAAtALQCRQ0BIAAoAsQBIQMCQCACQQFHDQAg
AygCACIDKAIQIgQgACgCyAEiBSgCAGoiAiAESQ0GIAJBB3ENBiACRQ0GIAAgA0EQaiAFIAIgAy8BqAIQs4KAgAAgACgCyAEiAiACKQMAQoCAgICAIIQ3AwAM
BQsgAUEYakEIaiADQQhqKAIANgIAIAEgAykCADcDGCAALwGMAUUNBSAAKAJYIgRFDQUCQANAIAJB//8DcSAELwGqAiIDTQ0BIAIgA2shAiAEKAIIIgQNAAwH
CwsgAyACayIGQf//A3FBAWoiBUH//wNxIgcgBUcNBSADIAdJDQUgBCgCECIFIAQoAhhqIgggBUkNBSAIQQdxDQUgCEUNBQJAIAJB//8DcUUNACAIIAZB//8D
cSIFQQN0aiICKQMAIQkCQCAFQX9zIANqQQN0IgNFDQAgAiACQQhqIAP8CgAACyACIANqIAk3AwAgACgCdCICQYCAgIAETw0GAkACQCACDQBBACECQarVqtV6
IQoMAQsgACgCbCIKQQNxDQcgACgCcCECCwJAIAJFDQAgAkEBcSELQQAhAwJAIAJBAUYNACACQX5xIQdBACEDIAZB//8DcSEMIAohAgNAAkAgAigCACIFKAIA
IARHDQAgBS8BBCINIAxNDQAgBSANQX9qOwEECwJAIAJBBGooAgAiBSgCACAERw0AIAUvAQQiDSAMTQ0AIAUgDUF/ajsBBAsgAkEIaiECIAcgA0ECaiIDRw0A
CwsgC0UNACAKIANBAnRqKAIAIgIoAgAgBEcNACACLwEEIgMgBkH//wNxTQ0AIAIgA0F/ajsBBAsgAEEBEKGDgIAAIARBAToArAICQCAEKAIMIgNFDQADQCAI
IQUgBCECIAMiBCgCECIDIAQoAhhqIgggA0kNCCAIQQdxDQggCEUNCCACLwGqAkF/aiIDQf//A3EiByADRw0IIAJBEGogBEEQaiAFIAdBA3RqIAhBACACLwGo
AhCXg4CAACIDQf//A3ENDAJAAkACQCAELwGqAiICRQ0AIAgpAwAhCQJAIAJBA3RBeGoiAkUNACAIIAhBCGogAvwKAAALIAggAmogCTcDACAEQQE6AKwCIAAo
AnQiAkGAgICABE8NCwJAIAINAEEAIQNBqtWq1XohAkEARQ0DDAILIAAoAmwiAkEDcQ0LIAAoAnAiA0UNAgwBC0EAQQAQiICAgAAACwNAAkAgAigCACIFKAIA
IARHDQACQAJAIAUvAQQiB0UNACAHQX9qIQcMAQsgBCgCCCIHRQ0MIAUgBzYCACAHLwGqAkF/aiIHQf//A3EgB0cNDAsgBSAHOwEECyACQQRqIQIgA0F/aiID
DQALCyAEKAIMIgMNAAsLIAQvAaoCQX9qIgJB//8DcSIDIAJHDQYgBCgCECICIAggA0EDdGoiCygCAGoiDiACSQ0GIA5BB3ENBiAORQ0GIAQvAagCIQYgCykD
ACIJQoCAgIDAAINQDQQCQAJAIAZFDQAgBEEsaiEKIA4hAyAOIQIgBiEFA0ACQCACKQMAQgODQgFSDQAgAyAEKAIQIghrIgwgA0sNCiAIIAQoAjhqIgcgCEkN
CiAHRQ0KIAEgBzYCXCABQeAAaiAHIAwQrYKAgAAgAS0AaEUNCiAEKAIQIgggASgCZCIMKAIAaiIHIAhJDQogB0EDcQ0KIAdFDQogASgCYCENIAogCCAHIAwo
AgQQooOAgAAgAUHcAGogDRCugoCAACACIAIpAwBCfIM3AwALIANBCGohAyACQQhqIQIgBUF/aiIFDQALIAYgBC8BqAIiA0cNASALKQMAIQkLIAsgCUL/////
v3+DIgk3AwAMBQsgBCgCECIFIAsoAgBqIgIgBUkNBiACQQdxDQYgAkUNBgJAIANFDQADQCACKQMAQgODQgFRDQUgAkEIaiECIANBf2oiAw0ACwsgCyALKQMA
Qv////+/f4MiCTcDAAwEC0EAQQAQiICAgAAACyAFQf//A3EgA0H//wNxTw0EIAAoAsQBKAIAIQQgAUEMaiAAEK6DgIAAIAEvARAiAw0IAkACQCABKAIMRQ0A
IAAQtIOAgAAiA0H//wNxDQoMAQsCQCAAKALEASIFKAIAIgIgACgCWEcNACAEIAJHDQYgBS8BBCIIQQFqIgNB//8DcSIEIANHDQYgAi8BqgIiByAESQ0GAkAC
QCAHIANB//8DcUYNACAIQQFqIQQMAQsgAigCDCICRQ0HQQEhAwJAA0AgAyACLwGqAiIETQ0BIAMgBGshAyACKAIMIgJFDQkMAAsLIANBf2oiBCADSw0HCyAF
QQA6AAggBSAEOwEEIAUgAjYCACABQQA6ABQgACgCxAEiBSgCACICKAIQIgQgAigCGGoiAyAESQ0GIANBB3ENBiADRQ0GAkACQCAFLwEEIgcgAi8BqgIiBEsN
ACAEIAdrIgRBf2oiBSAESw0IIAUgBE8NASAEQX9qIAVHDQggAyAHQQN0aiIDIAVBA3QiBGopAwAhCQJAIARFDQAgA0EIaiADIAT8CgAACyADIAk3AwAgAkEB
OgCsAiAAKALEASIDLwEEIgQgAygCACICLwGqAk8NCCADLwEGIgUgAi8BqAJPDQggAigCECIDIAIoAhhqIgIgA0kNCCACQQdxDQggAkUNCCADIAIgBEEDdGoi
BCgCAGoiAiADSQ0IIAJBB3ENCCACRQ0IIAAgBDYCyAEgACACIAVBA3RqNgLMAQwDCyAHIAQQioCAgAAACyAFIAQQiICAgAAACyAAELSDgIAAIgNB//8DcQ0J
C0EAIQMgAC8B1AFFDQggAEGoAWovAQAgAEGqAWotAABBEHRyIQICQAJAAkAgAEGsAWotAAAOAwsBAAcLIAJBgID8B3FBEHatQhKGIAJBAnRB/P8PcUEDcq2E
IQkMAQsgAkECdEH8B3FBAnKtIQkLIAAvAYwBIgVFDQggBUF/akH/////AXEhCCAAKALMASAALwHQAUEDdGsiByECAkAgBUEHcSIERQ0AIAchAgNAIAIgCTcD
ACACQQhqIQIgBEF/aiIEDQALCyAIQQdJDQggByAFQQN0aiEEA0AgAiAJNwMAIAJBOGogCTcDACACQTBqIAk3AwAgAkEoaiAJNwMAIAJBIGogCTcDACACQRhq
IAk3AwAgAkEQaiAJNwMAIAJBCGogCTcDACACQcAAaiICIARHDQAMCQsLIAAoAsQBKAIAIQIgAUHgAGogABCug4CAACABLwFkIgMNBwJAAkACQCACIAAoAsQB
IgcoAgAiBUcNACAHLwEEIghBAWoiAkH//wNxIgMgAkcNBiAFLwGqAiIEIANJDQYgBy8BBiEMAkAgBCACQf//A3FGDQAgCEEBaiEEIAUhAgwCCyAFKAIMIgJF
DQZBASEDAkADQCADIAIvAaoCIgRNDQEgAyAEayEDIAIoAgwiAkUNCAwACwsgA0F/aiIEIANLDQYMAQsgAUEoakEIaiAHQQhqKAIANgIAIAEgBykCACIJNwMo
IAEgAC8B0AEiDDsBLiAJpyECIAFBKGohCCABLwEsIQQMAQsgASAMOwEuIAEgBDsBLCABIAI2AiggAUEAOgBcIAFBADoAMCABQShqIQgLAkACQCAFIAJHDQAg
By8BBCAEQf//A3FHDQAgBy8BBkH//wNxIAxB//8DcUYNAQsgACgCyAEiAyADKQMAQoCAgICAIIQ3AwAgBEH//wNxIgUgAi8BqgJPDQQgDEH//wNxIAIvAagC
Tw0EIAIoAhAiAyACKAIYaiIEIANJDQQgBEEHcQ0EIARFDQQgAyAEIAVBA3RqIgUoAgBqIgQgA0kNBCAEQQdxDQQgBEUNBCAFIAUpAwBCgICAgIAghDcDACAA
KALEASIHKAIAIQULAkACQCAFIAJHDQAgByAIKQIANwIAIAdBCGogCEEIaigCADYCAAwBCyAAQaABaiECAkACQCAALwHUASIEDQBBlLPIgAAhB0H4ssiAACED
DAELIAFBOGpBGGogAkEYaigCADYCACABQThqQRBqIAJBEGopAgA3AwAgAUE4akEIaiACQQhqKQIANwMAIAFBAToAVCABIAIpAgA3AzggBSgCSCAETQ0FIAUo
AhAiByAFKAJAaiIDIAdJDQUgA0EDcQ0FIANFDQUgAyAEQSRsaiIDLwEgIgRFDQUgA0EgaiAEQX9qIgM7AQACQCADQf//A3ENACAFKAJEIgNBf2oiBCADSw0G
IAUgBDYCRAsgAUHUAGohByACQgA3AwAgAEEAOwHUASACQRhqQQA2AgAgAkEQakIANwMAIAJBCGpCADcDACABQThqIQMLAkAgACgCwAFFDQAgAC8B1gEiDEUN
BSAAKALEASgCACIFKAK0ASAMTQ0FIAUoAhAiDSAFKAKsAWoiBCANSQ0FIARBA3ENBSAERQ0FIAQgDEEcbGoiBC8BGCIMRQ0FIARBGGogDEF/aiIEOwEAIARB
//8DcQ0AIAUoArABIgRBf2oiDCAESw0FIAUgDDYCsAELIAAoAsQBIgQgCCkCADcCACAEQQhqIAhBCGooAgA2AgACQCAHLQAARQ0AIAIgAykCADcCACACQRhq
IgQgA0EYaigCADYCACACQRBqIgUgA0EQaikCADcCACACQQhqIANBCGopAgA3AgAgABDAgICAAEH//wNxIgNFDQAgASADOwFsIAFB7ABqEJqDgIAAIARBADYC
ACAFQgA3AwAgAkEIakIANwMAIAJCADcDACAAQQA7AdQBCyAAKALAASICRQ0AIABBADYCwAEgAEEAOwHWAQJAIAAgAigCDCACKAIQQQAgAigCACACLQAIQQFx
IgMbQQAgAigCBCADGxCRg4CAACIDQf//A3FFDQAgASADOwEYIAFBGGoQm4OAgAALIAApA5gBIglCIIinIQQgCachBQJAIAIoAhAiA0UNACACKAIMIQcCQCAD
RQ0AIAdBqgEgA/wLAAsgBSAHIANBAEEAIAQoAgwRgYCAgACAgICAAAsCQCACLQAIQQFxDQAgAigCBCIDRQ0AIAIoAgAhBwJAIANFDQAgB0GqASAD/AsACyAF
IAcgA0EAQQAgBCgCDBGBgICAAICAgIAACyAAKAKYASACQRRBAkEAIABBnAFqKAIAQQxqKAIAEYGAgIAAgICAgAALIAAoAsQBIgMvAQQiBCADKAIAIgIvAaoC
Tw0DIAMvAQYiBSACLwGoAk8NAyACKAIQIgMgAigCGGoiAiADSQ0DIAJBB3ENAyACRQ0DIAMgAiAEQQN0aiIEKAIAaiICIANJDQMgAkEHcQ0DIAJFDQMgACAE
NgLIASAAIAIgBUEDdGo2AswBIAQgBCkDAEKAgICAgCCENwMAIAAtAKwBRQ0CIAAoAsQBKAIAIgMoAhAiBCAAKALIASIFKAIAaiICIARJDQMgAkEHcQ0DIAJF
DQMgACADQRBqIAUgAiADLwGoAhCzgoCAAAwCCyALKQMAIQkLAkAgCUKAgICAgAKDUA0AAkAgBkUNACAOIQMgDiECIAYhBQNAAkAgAi0ABUEgcUUNACADIAQo
AhAiCGsiDCADSw0FIAggBCgCpAFqIgcgCEkNBSAHRQ0FIAEgBzYCbCABQShqIAcgDBCwgoCAACABLQAwRQ0AIAEoAiwvAQAiCEUNBSAEKAK0ASAITQ0FIAQo
AhAiDCAEKAKsAWoiByAMSQ0FIAdBA3ENBSAHRQ0FIAcgCEEcbGoiBy8BGCIIRQ0FIAdBGGogCEF/aiIHOwEAAkAgB0H//wNxDQAgBCgCsAEiB0F/aiIIIAdL
DQYgBCAINgKwAQsgAUHsAGogASgCKBCxgoCAACACIAIpAwBC////////d4M3AwALIANBCGohAyACQQhqIQIgBUF/aiIFDQALCwJAIAYgBC8BqAIiA0cNACAL
IAspAwBC//////99gyIJNwMADAELIAQoAhAiBSALKAIAaiICIAVJDQIgAkEHcQ0CIAJFDQICQAJAIANFDQADQCACLQAFQSBxDQIgAkEIaiECIANBf2oiAw0A
CwsgCyALKQMAQv//////fYMiCTcDAAwBCyALKQMAIQkLAkAgCUKAgICAgAGDUA0AAkAgBkUNACAOIQIgBiEFA0ACQCACKQMAIglCgICA4P//AINQDQAgBCgC
SCAJQhqIp0H//wNxIghNDQUgBCgCECIHIAQoAkBqIgMgB0kNBSADQQNxDQUgA0UNBSADIAhBJGxqIgMvASAiB0UNBSADQSBqIAdBf2oiAzsBACADQf//A3EN
ACAEKAJEIgNBf2oiByADSw0FIAQgBzYCRAsgAkEIaiECIAVBf2oiBQ0ACwsCQCAGIAQvAagCIgNGDQAgBCgCECIEIAsoAgBqIgIgBEkNAyACQQdxDQMgAkUN
AyADRQ0AA0AgAigBAkGA+P8fcQ0CIAJBCGohAiADQX9qIgMNAAsLIAsgCykDAEL//////36DNwMACwJAIAZBA3QiAkUNACAOQQAgAvwLAAsgACgCxAEiAiAB
KQMYNwIAIAJBCGogAUEYakEIaigCADYCACAAKALEASIDLwEEIgQgAygCACICLwGqAk8NASADLwEGIgUgAi8BqAJPDQEgAigCECIDIAIoAhhqIgIgA0kNASAC
QQdxDQEgAkUNASADIAIgBEEDdGoiBCgCAGoiAiADSQ0BIAJBB3ENASACRQ0BIAAgBDYCyAEgACACIAVBA3RqNgLMAQtBACEDIAAvAdQBRQ0EIABBqAFqLwEA
IABBqgFqLQAAQRB0ciECIABBrAFqLQAADgMEAgEACwALIAJBgID8B3FBEHatQhKGIAJBAnRB/P8PcUEDcq2EIQkMAQsgAkECdEH8B3FBAnKtIQkLIAAvAYwB
IgVFDQAgBUF/akH/////AXEhCCAAKALMASAALwHQAUEDdGsiByECAkAgBUEHcSIERQ0AIAchAgNAIAIgCTcDACACQQhqIQIgBEF/aiIEDQALCyAIQQdJDQAg
ByAFQQN0aiEEA0AgAiAJNwMAIAJBOGogCTcDACACQTBqIAk3AwAgAkEoaiAJNwMAIAJBIGogCTcDACACQRhqIAk3AwAgAkEQaiAJNwMAIAJBCGogCTcDACAC
QcAAaiICIARHDQALCyABQfAAaiSAgICAACADC4UFAwR/AX4CfwJAAkAgASgCACIDIAIoAgBqIgQgA0kNACAEQQdxDQAgBEUNACAALwH6EUF/aiIDQf//A3Ei
BSADRw0AAkACQCAALwGCEiIGIAVGDQAgAC8BgBJBAUsNAQsgAS8BmAJBf2oiA0H//wNxIgUgA0cNASAEIAVBA3RqIgMpAwAiB0KAgICAgIADg0KAgICAgIAD
Ug0AIAMgB0L///////98gzcDACAALwGCEiEGCwJAIAQgAC8BgBIiA0EDdGoiCCkDAEKAgICAgIADg0KAgICAgIACUg0AIANBf2oiA0H//wNxIANHDQECQCAE
IANB//8DcUEDdGoiCSkDAEIDg0IBUg0AIAEgCRCsg4CAACABKAIAIgUgAigCAGoiAyAFSQ0CIANBB3ENAiADRQ0CAkAgAS8BmAIiBUUNAANAIAMpAwBCA4NC
AVENAiADQQhqIQMgBUF/aiIFDQALCyACIAIpAwBC/////79/gzcDAAsgCSAJKQMAQoOAgPz//3yDNwMAIAggCCkDAEL///////98gzcDAAsCQCAEIAZB//8D
cUEDdGoiBSkDACIHQoCAgICAgAODQoCAgICAgAFRDQAPCyAALwGCEkEBaiIDQf//A3EiBiADRw0AIAdCA4NCAVINASABIAUQrIOAgAAgASgCACIAIAIoAgBq
IgMgAEkNACADQQdxDQAgA0UNAAJAIAEvAZgCIgBFDQADQCADKQMAQgODQgFRDQMgA0EIaiEDIABBf2oiAA0ACwsgAiACKQMAQv////+/f4M3AwAMAQsACyAF
IAUpAwBCg4CA/P//fIM3AwAgBCAGQQN0aiIDIAMpAwBC////////fIM3AwAL8g8EBX8Bfgp/AX4jgICAgABB0ABrIgYkgICAgAACQAJAAkACQCAAKAIAIgcg
ASgCAGoiCCAHSQ0AIAhBB3ENACAIRQ0AIAcgAygCAGoiCSAHSQ0AIAlBB3ENACAJRQ0AIAkgBEEDdGohCgJAIAMpAwAiC0KAgICAwACDUA0AAkAgBUUNACAA
QRxqIQwgCSAEQQN0aiENIAohByAFIQ4DQAJAIAcpAwBCA4NCAVINACANIAAoAgAiD2siECANSw0EIA8gACgCKGoiESAPSQ0EIBFFDQQgBiARNgIEIAZBCGog
ESAQEK2CgIAAIAYtABBFDQQgDyAGKAIMIhAoAgBqIhEgD0kNBCARQQNxDQQgEUUNBCAGKAIIIRIgDCAPIBEgECgCBBCig4CAACAGQQRqIBIQroKAgAAgByAH
KQMAQnyDNwMACyANQQhqIQ0gB0EIaiEHIA5Bf2oiDg0ACwsCQCAFIAAvAZgCIg1HDQAgAyADKQMAQv////+/f4MiCzcDAAwBCyAAKAIAIg8gAygCAGoiByAP
SQ0BIAdBB3ENASAHRQ0BAkACQCANRQ0AA0AgBykDAEIDg0IBUQ0CIAdBCGohByANQX9qIg0NAAsLIAMgAykDAEL/////v3+DIgs3AwAMAQsgAykDACELCwJA
IAtCgICAgIACg1ANAAJAIAVFDQAgCSAEQQN0aiENIAohByAFIQ4DQAJAIActAAVBIHFFDQAgDSAAKAIAIg9rIhAgDUsNBCAPIAAoApQBaiIRIA9JDQQgEUUN
BCAGIBE2AhQgBkEYaiARIBAQsIKAgAAgBi0AIEUNACAGKAIcLwEAIhBFDQQgACgCpAEgEE0NBCAPIAAoApwBaiIRIA9JDQQgEUEDcQ0EIBFFDQQgESAQQRxs
aiIPLwEYIhFFDQQgD0EYaiARQX9qIg87AQACQCAPQf//A3ENACAAKAKgASIPQX9qIhEgD0sNBSAAIBE2AqABCyAGQRRqIAYoAhgQsYKAgAAgByAHKQMAQv//
/////3eDNwMACyAHQQhqIQcgDUEIaiENIA5Bf2oiDg0ACwsCQCAFIAAvAZgCIg1HDQAgAyADKQMAQv//////fYMiCzcDAAwBCyAAKAIAIg8gAygCAGoiByAP
SQ0BIAdBB3ENASAHRQ0BAkACQCANRQ0AA0AgBy0ABUEgcQ0CIAdBCGohByANQX9qIg0NAAsLIAMgAykDAEL//////32DIgs3AwAMAQsgAykDACELCwJAIAtC
gICAgIABg1ANAAJAIAVFDQAgCiEHIAUhDwNAAkAgBykDACILQoCAgOD//wCDUA0AIAAoAjggC0IaiKdB//8DcSIRTQ0EIAAoAgAiDiAAKAIwaiINIA5JDQQg
DUEDcQ0EIA1FDQQgDSARQSRsaiINLwEgIg5FDQQgDUEgaiAOQX9qIg07AQAgDUH//wNxDQAgACgCNCINQX9qIg4gDUsNBCAAIA42AjQLIAdBCGohByAPQX9q
Ig8NAAsLAkAgBSAALwGYAiINRg0AIAAoAgAiDyADKAIAaiIHIA9JDQIgB0EHcQ0CIAdFDQIgDUUNAANAIAcoAQJBgPj/H3ENAiAHQQhqIQcgDUF/aiINDQAL
CyADIAMpAwBC//////9+gzcDAAsgAkEDdCEHAkAgBUEDdCITRSIUDQAgCkEAIBP8CwALIAggB2ohFQJAAkAgAS0ABEEccUUNACAFRQ0DIAggAkEDdGohDyAJ
IARBA3RqIQ4gFSENIAohByAFIRADQCAHIA0pAwAiCzcDAAJAIAtCA4NCAVINACAHIAtCfIM3AwAgDyAAKAIAIhJrIgwgD0sNBCAOIBJrIgkgDksNBCASIAAo
AihqIhEgEkkNBCARRQ0EIAYgETYCJCAGQShqIBEgDBCtgoCAACAGLQAwRQ0EIAYoAiwpAgAhCyAGQSRqIAYoAigQroKAgAAgESAJIAsQr4KAgAAgDSANKQMA
QnyDNwMAIAcgBykDAEJ8g0IBhDcDACADIAMpAwBCgICAgMAAhDcDACANKQMAIQsLAkAgC0KAgICAgIAIg1ANACAHIAcpAwBC////////d4M3AwAgDS0ABUEg
cUUNBCAPIAAoAgAiEWsiCSAPSw0EIA4gEWsiDCAOSw0EIBEgACgClAFqIhIgEUkNBCASRQ0EIAYgEjYCNCAGQThqIBIgCRCwgoCAACAGLQBARQ0EIAYoAjwv
AQAhESAGQTRqIAYoAjgQsYKAgAAgBkHEAGogBkE0aiAMELKCgIAAAkAgBi0ATA0AIAYoAkQgDDYCAAsgBigCSCAROwEAIAcgBykDAEKAgICAgIAIhDcDACAD
IAMpAwBCgICAgIAChDcDAAsgD0EIaiEPIA1BCGohDSAOQQhqIQ4gB0EIaiEHIBBBf2oiEA0ADAILCwJAIAogFSAFQQN0IgdqTw0AIBUgCiAHakkNAgsgFA0A
IAogFSAT/AoAAAsgAykDACILQoCAgICAAYNQRQ0DQgAhFiAFRQ0CIAUhBwJAA0AgCigBAkGA+P8fcQ0BIApBCGohCiAHQX9qIgdFDQQMAAsLQoCAgICAASEW
DAILAAsgAykDACILQoCAgICAAYNQRQ0BQgAhFgsgAyAWIAuENwMACwJAIBQNACAVQQAgE/wLAAsCQCAFIAAvAZgCRw0AIAEgASkDAEL/////v3yDNwMACyAG
QdAAaiSAgICAAAuVBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2
AowQAkACQAJAAkACQCABQYgQaiAAEPeDgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAEL
QQANAEEAKQPg+smAACIDp0EyQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrV
qtWq1arVqn83AAAgAkEwakGq1X47AAAgAkEoakKq1arVqtWq1ap/NwAAIAJBIGpCqtWq1arVqtWqfzcAACACQRhqQqrVqtWq1arVqn83AAAgAUIyNwOYECAB
IAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBiBBqQQhqIAAQ94OAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgC
lBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYC
lBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0B
IAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAh
BSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC8AqAwp/AX4D
fyOAgICAAEHwAGsiAySAgICAAAJAIAEvAQAgAC8BjAFPDQAgASgCBCEEIANB5ABqIAAgAS0ACBC6g4CAACADLwFoIgVBAWoiAUH//wNxIgYgAUcNACADKAJk
IgcvAaoCIgggBmsiAUH//wNxIAFHDQACQAJAIAQgAUH//wNxIgFLDQAgBCAFaiIBQf//AyABQf//A0kbIQkMAQsgBygCDCIHRQ0BIAQgAWshAQJAA0AgASAH
LwGqAiIITQ0BIAEgCGshASAHKAIMIgcNAAwDCwsgAUF/aiIJIAFLDQELIAcoAhAiASAHKAIYaiIFIAFJDQAgBUEHcQ0AIAVFDQAgCCAJQf//A3FrIgRB//8D
cSIGIARHDQAgBSAJQQN0aiEKAkACQAJAAkAgAiAGTw0AIAEgCigCAGoiCyABSQ0EIAtBB3ENBCALRQ0EIAcvAagCIQwgCikDACINQoCAgIDAAINQDQICQAJA
IAxFDQAgB0EsaiEOIAshCCALIQEgDCEEA0ACQCABKQMAQgODQgFSDQAgCCAHKAIQIgVrIg8gCEsNCCAFIAcoAjhqIgYgBUkNCCAGRQ0IIAMgBjYCBCADQQhq
IAYgDxCtgoCAACADLQAQRQ0IIAcoAhAiBSADKAIMIg8oAgBqIgYgBUkNCCAGQQNxDQggBkUNCCADKAIIIRAgDiAFIAYgDygCBBCig4CAACADQQRqIBAQroKA
gAAgASABKQMAQnyDNwMACyAIQQhqIQggAUEIaiEBIARBf2oiBA0ACyAMIAcvAagCIghHDQELIAogCikDAEL/////v3+DIg03AwAMAwsgBygCECIEIAooAgBq
IgEgBEkNBCABQQdxDQQgAUUNBAJAIAhFDQADQCABKQMAQgODQgFRDQMgAUEIaiEBIAhBf2oiCA0ACwsgCiAKKQMAQv////+/f4MiDTcDAAwCCwJAAkACQCAI
IAlB//8DcSIBSQ0AIAggAUYNASAKKQMAIQ0CQCAIIAlBf3NqQQN0IgFFDQAgCiAKQQhqIAH8CgAACyAKIAFqIA03AwAgB0EBOgCsAiAHLwGqAiAJQf//A3Fr
IgFB//8DcSIKIAFHDQYgAC0AkAFBAkcNAiAALQCIAUUNAiAAKAKAASIBKAIAIAdHDQIgAS8BBCIBIAlB//8DcUkNAiABRQ0CIAAoAoQBIgFBf2oiCCABSw0G
IAAgCDYChAEMAgsgCSAIEIqAgIAAAAtBAEEAEIiAgIAAAAsgACgCdCIBQYCAgIAETw0DAkACQCABDQBBACEIQarVqtV6IQEMAQsgACgCbCIBQQNxDQQgACgC
cCEICwJAIAhFDQAgCUH//wNxIQkDQAJAIAEoAgAiBCgCACAHRw0AIAQvAQQiBiAJSQ0AAkAgBg0AIARBADsBBgwBCyAEIAZBf2o7AQQLIAFBBGohASAIQX9q
IggNAAsLAkAgBygCDCIERQ0AA0AgCiEIIAUhBiAHIQEgBCIHKAIQIgQgBygCGGoiBSAESQ0FIAVBB3ENBSAFRQ0FIAEvAaoCQX9qIgRB//8DcSIKIARHDQUg
AUEQaiAHQRBqIgkgBiAKQQN0aiAFQQAgAS8BqAIQl4OAgAAiBkH//wNxDQQgAiAIayIEIAJLDQUCQAJAAkACQCAEIAcvAaoCIgFPDQAgBygCECIBIAUoAgBq
IgsgAUkNCSALQQdxDQkgC0UNCSAHLwGoAiEMIAUpAwAiDUKAgICAwACDUA0CAkACQCAMRQ0AIAdBLGohDiALIQggCyEBIAwhAgNAAkAgASkDAEIDg0IBUg0A
IAggCSgCACIGayIPIAhLDQ0gBiAHKAI4aiIKIAZJDQ0gCkUNDSADIAo2AiQgA0EoaiAKIA8QrYKAgAAgAy0AMEUNDSAGIAMoAiwiDygCAGoiCiAGSQ0NIApB
A3ENDSAKRQ0NIAMoAighECAOIAYgCiAPKAIEEKKDgIAAIANBJGogEBCugoCAACABIAEpAwBCfIM3AwALIAhBCGohCCABQQhqIQEgAkF/aiICDQALIAwgBy8B
qAIiCEcNAQsgBSAFKQMAQv////+/f4MiDTcDAAwDCyAJKAIAIgYgBSgCAGoiASAGSQ0JIAFBB3ENCSABRQ0JAkAgCEUNAANAIAEpAwBCA4NCAVENAyABQQhq
IQEgCEF/aiIIDQALCyAFIAUpAwBC/////79/gyINNwMADAILAkACQCABRQ0AIAUpAwAhDQJAIAFBA3RBeGoiAUUNACAFIAVBCGogAfwKAAALIAUgAWogDTcD
ACAHQQE6AKwCIAggBy8BqgJqIgogCEkNCiAALQCQAUECRw0BIAAtAIgBRQ0BIAAoAoABKAIAIAdHDQEgACgChAEiAUF/aiIIIAFLDQogACAINgKEAQwBC0EA
QQAQiICAgAAACyAAKAJ0IgFBgICAgARPDQgCQAJAIAENAEEAIQhBqtWq1XohAUEARQ0EDAELIAAoAmwiAUEDcQ0JIAAoAnAiCEUNAwsDQAJAIAEoAgAiBCgC
ACAHRw0AAkACQCAELwEEIgZFDQAgBkF/aiEGDAELIAcoAggiBkUNCyAEIAY2AgAgBi8BqgJBf2oiBkH//wNxIAZHDQsLIAQgBjsBBAsgAUEEaiEBIAhBf2oi
CEUNAwwACwsgBSkDACENCwJAIA1CgICAgIACg1ANAAJAIAxFDQAgCyEBIAshCCAMIQIDQAJAIAEtAAVBIHFFDQAgCCAJKAIAIgZrIg8gCEsNCiAGIAcoAqQB
aiIKIAZJDQogCkUNCiADIAo2AjQgA0E4aiAKIA8QsIKAgAAgAy0AQEUNACADKAI8LwEAIg9FDQogBygCtAEgD00NCiAGIAcoAqwBaiIKIAZJDQogCkEDcQ0K
IApFDQogCiAPQRxsaiIGLwEYIgpFDQogBkEYaiAKQX9qIgY7AQACQCAGQf//A3ENACAHKAKwASIGQX9qIgogBksNCyAHIAo2ArABCyADQTRqIAMoAjgQsYKA
gAAgASABKQMAQv///////3eDNwMACyABQQhqIQEgCEEIaiEIIAJBf2oiAg0ACwsCQCAMIAcvAagCIghHDQAgBSAFKQMAQv//////fYMiDTcDAAwBCyAJKAIA
IgYgBSgCAGoiASAGSQ0HIAFBB3ENByABRQ0HAkACQCAIRQ0AA0AgAS0ABUEgcQ0CIAFBCGohASAIQX9qIggNAAsLIAUgBSkDAEL//////32DIg03AwAMAQsg
BSkDACENCwJAIA1CgICAgIABg1ANAAJAIAxFDQAgCyEBIAwhBgNAAkAgASkDACINQoCAgOD//wCDUA0AIAcoAkggDUIaiKdB//8DcSIKTQ0KIAcoAhAiAiAH
KAJAaiIIIAJJDQogCEEDcQ0KIAhFDQogCCAKQSRsaiIILwEgIgJFDQogCEEgaiACQX9qIgg7AQAgCEH//wNxDQAgBygCRCIIQX9qIgIgCEsNCiAHIAI2AkQL
IAFBCGohASAGQX9qIgYNAAsLAkAgDCAHLwGoAiIIRg0AIAkoAgAiBiAFKAIAaiIBIAZJDQggAUEHcQ0IIAFFDQggCEUNAANAIAEoAQJBgPj/H3ENAiABQQhq
IQEgCEF/aiIIDQALCyAFIAUpAwBC//////9+gzcDAAsCQCAMQQN0IgFFDQAgC0EAIAH8CwALAkAgBEF/Rw0AQX9BABCIgICAAAALIAUpAwAhDQJAIARBA3Qi
AUUNACAFIAVBCGogAfwKAAALIAUgAWogDTcDACAHQQE6AKwCAkAgAC0AkAFBAkcNACAALQCIAUUNACAAKAKAASIBKAIAIAdHDQAgBCABLwEESQ0AIAAoAoQB
IgFBf2oiCCABSw0HIAAgCDYChAELIAAoAnQiAUGAgICABE8NBkEAIQYCQAJAIAENAEGq1arVeiEBQQAhCAwBCyAAKAJsIgFBA3ENByAAKAJwIQgLIAhFDQUD
QAJAIAEoAgAiBSgCACAHRw0AIAQgBS8BBCIASQ0AAkACQCAARQ0AIABBf2ohAAwBCyAHKAIIIgBFDQkgBSAANgIAIAAvAaoCQX9qIgBB//8DcSAARw0JCyAF
IAA7AQQLIAFBBGohASAIQX9qIghFDQYMAAsLIAcoAgwiBA0ACwsgBy8BqgJBf2oiAUH//wNxIgggAUcNAyAHKAIQIgEgBSAIQQN0aiIJKAIAaiIQIAFJDQMg
EEEHcQ0DIBBFDQMgBy8BqAIhCgJAIAkpAwAiDUKAgICAwACDUA0AAkACQCAKRQ0AIAdBLGohDyAQIQggECEBIAohBANAAkAgASkDAEIDg0IBUg0AIAggBygC
ECIFayIAIAhLDQggBSAHKAI4aiIGIAVJDQggBkUNCCADIAY2AkQgA0HIAGogBiAAEK2CgIAAIAMtAFBFDQggBygCECIFIAMoAkwiACgCAGoiBiAFSQ0IIAZB
A3ENCCAGRQ0IIAMoAkghAiAPIAUgBiAAKAIEEKKDgIAAIANBxABqIAIQroKAgAAgASABKQMAQnyDNwMACyAIQQhqIQggAUEIaiEBIARBf2oiBA0ACyAKIAcv
AagCIghHDQELIAkgCSkDAEL/////v3+DIg03AwAMAQsgBygCECIEIAkoAgBqIgEgBEkNBCABQQdxDQQgAUUNBAJAAkAgCEUNAANAIAEpAwBCA4NCAVENAiAB
QQhqIQEgCEF/aiIIDQALCyAJIAkpAwBC/////79/gyINNwMADAELIAkpAwAhDQsCQCANQoCAgICAAoNQDQACQCAKRQ0AIBAhASAQIQggCiEEA0ACQCABLQAF
QSBxRQ0AIAggBygCECIFayIAIAhLDQcgBSAHKAKkAWoiBiAFSQ0HIAZFDQcgAyAGNgJUIANB2ABqIAYgABCwgoCAACADLQBgRQ0AIAMoAlwvAQAiBUUNByAH
KAK0ASAFTQ0HIAcoAhAiACAHKAKsAWoiBiAASQ0HIAZBA3ENByAGRQ0HIAYgBUEcbGoiBi8BGCIFRQ0HIAZBGGogBUF/aiIGOwEAAkAgBkH//wNxDQAgBygC
sAEiBkF/aiIFIAZLDQggByAFNgKwAQsgA0HUAGogAygCWBCxgoCAACABIAEpAwBC////////d4M3AwALIAFBCGohASAIQQhqIQggBEF/aiIEDQALCwJAIAog
By8BqAIiCEcNACAJIAkpAwBC//////99gyINNwMADAELIAcoAhAiBCAJKAIAaiIBIARJDQQgAUEHcQ0EIAFFDQQCQAJAIAhFDQADQCABLQAFQSBxDQIgAUEI
aiEBIAhBf2oiCA0ACwsgCSAJKQMAQv//////fYMiDTcDAAwBCyAJKQMAIQ0LAkAgDUKAgICAgAGDUA0AAkAgCkUNACAQIQEgCiEEA0ACQCABKQMAIg1CgICA
4P//AINQDQAgBygCSCANQhqIp0H//wNxIgVNDQcgBygCECIGIAcoAkBqIgggBkkNByAIQQNxDQcgCEUNByAIIAVBJGxqIggvASAiBkUNByAIQSBqIAZBf2oi
CDsBACAIQf//A3ENACAHKAJEIghBf2oiBiAISw0HIAcgBjYCRAsgAUEIaiEBIARBf2oiBA0ACwsCQCAKIAcvAagCIghGDQAgBygCECIHIAkoAgBqIgEgB0kN
BSABQQdxDQUgAUUNBSAIRQ0AA0AgASgBAkGA+P8fcQ0CIAFBCGohASAIQX9qIggNAAsLIAkgCSkDAEL//////36DNwMAC0EAIQYgCkEDdCIBRQ0CIBBBACAB
/AsADAILIAopAwAhDQsCQCANQoCAgICAAoNQDQACQCAMRQ0AIAshASALIQggDCEEA0ACQCABLQAFQSBxRQ0AIAggBygCECIFayIPIAhLDQUgBSAHKAKkAWoi
BiAFSQ0FIAZFDQUgAyAGNgIUIANBGGogBiAPELCCgIAAIAMtACBFDQAgAygCHC8BACIFRQ0FIAcoArQBIAVNDQUgBygCECIPIAcoAqwBaiIGIA9JDQUgBkED
cQ0FIAZFDQUgBiAFQRxsaiIGLwEYIgVFDQUgBkEYaiAFQX9qIgY7AQACQCAGQf//A3ENACAHKAKwASIGQX9qIgUgBksNBiAHIAU2ArABCyADQRRqIAMoAhgQ
sYKAgAAgASABKQMAQv///////3eDNwMACyABQQhqIQEgCEEIaiEIIARBf2oiBA0ACwsCQCAMIAcvAagCIghHDQAgCiAKKQMAQv//////fYMiDTcDAAwBCyAH
KAIQIgQgCigCAGoiASAESQ0CIAFBB3ENAiABRQ0CAkACQCAIRQ0AA0AgAS0ABUEgcQ0CIAFBCGohASAIQX9qIggNAAsLIAogCikDAEL//////32DIg03AwAM
AQsgCikDACENCwJAIA1CgICAgIABg1ANAAJAIAxFDQAgCyEBIAwhBANAAkAgASkDACINQoCAgOD//wCDUA0AIAcoAkggDUIaiKdB//8DcSIFTQ0FIAcoAhAi
BiAHKAJAaiIIIAZJDQUgCEEDcQ0FIAhFDQUgCCAFQSRsaiIILwEgIgZFDQUgCEEgaiAGQX9qIgg7AQAgCEH//wNxDQAgBygCRCIIQX9qIgYgCEsNBSAHIAY2
AkQLIAFBCGohASAEQX9qIgQNAAsLAkAgDCAHLwGoAiIIRg0AIAcoAhAiBCAKKAIAaiIBIARJDQMgAUEHcQ0DIAFFDQMgCEUNAANAIAEoAQJBgPj/H3ENAiAB
QQhqIQEgCEF/aiIIDQALCyAKIAopAwBC//////9+gzcDAAsCQCAMQQN0IgFFDQAgC0EAIAH8CwALIAopAwAhDQJAIAJBA3QiAUUNACAKIApBCGogAfwKAAAL
IAogAWogDTcDACAHQQE6AKwCAkAgAC0AkAFBAkcNACAALQCIAUUNACAAKAKAASIBKAIAIAdHDQAgAS8BBCIBRQ0AIAEgCUH//wNxSQ0AIAkgAmogAUkNACAA
KAKEASIBQX9qIgggAUsNAiAAIAg2AoQBCyAAKAJ0IgFBgICAgARPDQFBACEGAkACQCABDQBBqtWq1XohAUEAIQgMAQsgACgCbCIBQQNxDQIgACgCcCEICyAI
RQ0AIAkgAmohACAJQf//A3EhBQNAAkAgASgCACIEKAIAIAdHDQAgBC8BBCIGIAVJDQAgACAGSQ0AAkAgBg0AIARBADsBBgwBCyAEIAZBf2o7AQQLIAFBBGoh
ASAIQX9qIggNAAtBACEGCyADQfAAaiSAgICAACAGDwsAC4EDAQJ/I4CAgIAAQSBrIgMkgICAgAACQAJAAkACQAJAAkACQAJAAkAgAkEDcQ4EAQACAgELIAEt
AJABDgQEAwIFBAsgASgCWCICRQ0EIAEvAY4BIQEDQAJAIAFB//8DcSACLwGqAiIESw0AIABBADoACCAAQQA7AQYgACACNgIAIAAgBCABazsBBAwICyABIARr
IQEgAigCCCICDQAMBQsLIAEoAlQiAkUNAyAAQQA6AAggAEEANgEEIAAgAjYCAAwFCyABKAKAASECDAMLIAEoAlQiAkUNASADQQA6ABwgA0EANgIYIAMgAjYC
FCADQRRqIQIMAgsgASgCWCICRQ0AIAEvAY4BIQEDQAJAIAFB//8DcSACLwGqAiIESw0AIANBADoAECADQQA7AQ4gAyACNgIIIAMgBCABazsBDCADQQhqIQIM
AwsgASAEayEBIAIoAggiAg0ACwsACyAAIAIpAgA3AgAgAEEIaiACQQhqKAIANgIACyADQSBqJICAgIAAC4kGAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJA
QYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQvIOAgABB//8DcQ4DAAQBBAsC
QCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQS1BAEEAIANCIIinKAIAEYCAgIAAgICA
gAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQSVqQqrVqtWq1arVqn83AAAgAkEgakKq1arV
qtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQi03A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABC8g4CAAEH//wNx
DgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBq
IAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8
CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABB
ACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICA
AAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALuAICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEErIANrIgVqIgYgBEkNAiAD
QYfiyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBsuLIgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYg
BWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIg
CachBQsgAyAFaiIFIANJDQIgBSEDIAVBK0kNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAvdBAMLfwF+BX8jgICAgABB
wABrIgIkgICAgAACQAJAIAEvATAgAC8BjAFPDQAgAS8BMiAALwGOAU8NACAAELqBgIAAIAJBGGpBIGoiAyAAQcABaiIEKAIANgIAIAJBGGpBGGoiBSAAQbgB
aiIGKQMANwMAIAJBGGpBEGoiByAAQbABaiIIKQMANwMAIAJBGGpBCGoiCSAAQagBaiIKKQMANwMAIAJBCGpBCGoiCyAAQd4Bai8BADsBACACIAApA6ABNwMY
IAIgACkB1gE3AwggAEHIAWoiDCkDACENIABB0AFqIg4vAQAhDyAAKALEASEQIAAvAdIBIREgAC8B1AEhEiAOIAFBMGopAgA3AgAgBCABQSBqKQIANwIAIABB
2AFqIAFBOGopAgA3AgAgDCABQShqKQIANwIAIAYgAUEYaikCADcCACAIIAFBEGopAgA3AgAgCiABQQhqKQIANwIAIAAgASkCADcCoAEgAEEAOwHWASAAIBI7
AdQBIAAgETsB0gEgDiAPOwEAIAAgEDYCxAEgBEEANgIAAkAgABDAgICAAEH//wNxIgRFDQAgAEGgAWoiASACKQMYNwMAIAAgEjsB1AEgACAROwHSASAAIA87
AdABIAAgDTcDyAEgACAQNgLEASAAQdYBaiIAIAIpAwg3AQAgAUEgaiADKAIANgIAIAFBGGogBSkDADcDACABQRBqIAcpAwA3AwAgAUEIaiAJKQMANwMAIABB
CGogCy8BADsBAAwCCyAAIAEvATAgAS8BMhDVgICAAAwBCwALIAJBwABqJICAgIAAIAQL4wgDB38BfgF/I4CAgIAAQYADayIDJICAgIAAAkACQAJAIAFBA3EO
AwIAAQILIAJBAXENASAALQAoQQFxRQ0BIABBAkEAEIaBgIAADAELIAJBAXFFDQAgACgCGCIEQQE6AIwCIAQgACgCGC8B0AE7AfwBIAQgACgCGC8B0gE7Af4B
IAQgACgCGCIFKQKgATcC4AEgBEH4AWogBUG4AWooAgA2AgAgBEHwAWogBUGwAWopAgA3AgAgBEHoAWogBUGoAWopAgA3AgAgBCAAKAIYLQDaAUEBcToAgAIg
BCAAKAIYLQDZAUEBcToAgQIgBCAAKAIAQQh2QQFxOgCCAiAEIAAoAhgpALUCNwCDAgsCQAJAAkACQAJAAkACQAJAIAJBAXEiBCAALQAoQQFxRg0AIAAoAhgi
BhC6gYCAAAJAQQEgBHQiByAALQAkIgVxIghFDQAgACACQQFxQQJ0aigCHCIEDQQLIAVBAXFFDQcgACgCHCIJRQ0HIAMgBikCmAEiCjcDECAALwH4ESEFIAAv
AfoRIQtBACEEIAJBAXFFDQEMAgtBACEFIAFBA3FBAkcNBQwDCyAJKAJgIQQLIABBHGohCSADIAU7ASYgAyALOwEkIANBADYCICADIAQ2AhwCQCAIRQ0AIAkg
AkEBcUECdGooAgAiBA0BCwJAIAqnIghB0AJBA0EAIApCIIinIgsoAgARgICAgACAgICAACIEDQBBGSEFDAQLAkBB0AJFDQAgBEGqAUHQAvwLAAsgBEEHcQ0E
IANBKGogA0EQaiADQRxqEIKAgIAAAkAgAy8B+AIiBUUNACAIIARB0AJBA0EAIAtBDGooAgARgYCAgACAgICAAAwECwJAQdACRQ0AIAQgA0EoakHQAvwKAAAL
IAkgAkEBcUECdGogBDYCACAAIAAtACQgB3I6ACQLIAQvAdYBDQMgBCAGKQC1AjcAtQIgBEGQAmohBQJAIAQtALACRQ0AAkAgBC0AqAJBAXFFDQAgBCgClAIh
CCAEIAQoApACEOWAgIAAIAQgCBDlgICAAAsgBCAELQDKAkEBcjoAygILIAVCADcDACAFQSBqQQA2AgAgBUEYakIANwMAIAVBEGpCADcDACAFQQhqQgA3AwAg
ACACQQFxIgU6ACggAEH2EWoiBCAELQAAQQJyOgAAIAcgAC0AJHFFDQMgACAFQQJ0aigCHCIERQ0DIAAgBDYCGCABQQNxQQJHDQEgBiEFCyAALQAoIQQCQCAC
QQFxRQ0AIARBAXFFDQMgAEECQQAQhoGAgAACQCAFDQBBACEFDAMLAkAgACgCGCAFQaABahC9g4CAAEH//wNxIgANAEEAIQUMAwsgAyAAOwEMIANBDGoQv4OA
gABBACEFDAILIARBAXENAiAAEMGBgIAAQQAhBQwBC0EAIQUgBCAGQaABahC9g4CAAEH//wNxIgBFDQAgAyAAOwEOIANBDmoQv4OAgAALIANBgANqJICAgIAA
IAUPCwALsQYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJA
AkACQAJAAkAgAUGIEGogABDFg4CAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBB
ACkD4PrJgAAiA6dBwABBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arV
qtWqfzcAACACQThqQqrVqtWq1arVqn83AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq
1arVqtWq1ap/NwAAIAFCwAA3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDFg4CAAEH//wNxDgMABAEECyABKAKcECIA
IAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEg
ASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAF
KAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEo
ApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAP
CyAAIAIQiICAgAAACwALvy0FEn8BfAF+Gn8DfiOAgICAAEGAAWsiAySAgICAACADQcAAaiAAIAEgAhCfg4CAACADQeQAaiADQcAAahCgg4CAAAJAAkACQAJA
AkACQCADLQBsDQBBACEEDAELQQAhBANAIAMvAWgNAgJAAkACQAJAAkAgAy8BaiIFIAMoAmQiBi8BqgIiAkcNAAJAIAYoAgwNACAGKAIIDQAgBCAFaiIHIARJ
DQgCQCAGKAIUQXhxIgJFDQAgBigCEEEAIAL8CwALIAYvAaICIgggBi8BoAIiCWwiCkGAgICAAk8NCCAIQQN0IgUgCkEDdGoiCyAFSQ0IIAYvAaYCIQwgBigC
nAIhBCAGKAKYAiENIAYoAhAhDgJAAkAgBi8BpAIiDw0AQQAhEEEAIRFBACESQQAhE0EAIRQMAQtCAUEgIA9Bf2pnIgJrrUL//wODhqciEbhEAAAAAAAA6j+i
IhVEAAAAAAAA8L9lDQkgFUQAAAAAAADwQWYNCSAV/AMiEK1CJH4iFkIgiKcNCSARQQF0QQNqQfz///8HcSISIBanaiITIBJJDQlBf0EAIAJrdEF/cyEUCyAL
IBNqIhcgC0kNCCANQQ9qIgIgDUkNCCACQYCAgIABTw0IIAJBBHZBP2pBBnYiGEEDdCIZIAJBBHRBgH5xaiICIBlJDQggF0EHaiIaIBdJDQggGkF4cSIbIAJq
IhwgG0kNCCANDQJBACEdDAULIAQgBWoiAiAESQ0HIAAgBhDUg4CAACACIQQMAwsgBigCECIXIAYoAhhqIh4gF0kNBiAeQQdxDQYgHkUNBiACIAVrIgxB//8D
cSIfIAxHDQYCQCAfRQ0AIB9BAXEhCUEAIQ4CQCAfQQFGDQAgBUEDdCEgIB9B/v8DcSEhQQAhDiAeIQIDQCACKQMAIRYgAiACICBqIhopAwA3AwAgGiAWNwMA
IAJBCGoiFykDACEWIBcgGkEIaiIaKQMANwMAIBogFjcDACACIAIpAwBCgICAgIAghDcDACAXIBcpAwBCgICAgIAghDcDACACQRBqIQIgISAOQQJqIg5HDQAL
CwJAIAlFDQAgHiAOQQN0aiICKQMAIRYgAiACIAVBA3RqIhcpAwA3AwAgFyAWNwMAIAIgAikDAEKAgICAgCCENwMACyAGLwGqAiECCyACQf//A3EiAiAfayIc
IAJLDQYgHEUNAUEAIRkDQCAfIBlqIgIgH0kNByAGKAIQIhcgHiACQQN0aiIIKAIAaiIbIBdJDQcgG0EHcQ0HIBtFDQcgBi8BqAIhHQJAIAgpAwAiFkKAgICA
wACDUA0AAkACQCAdRQ0AQQAhCwNAAkAgGyALQQN0aiIiKQMAQgODQgFSDQAgIiAGKAIQIiNrIgogIksNDCAjIAYoAjhqIg4gI0kNDCAORQ0MIA5BA3ENDCAO
QXxqIg0oAgBFDQwgDkF4aigCACICQX9qIiEgAksNDCADQTBqIAqtQoGAgIAQfiIWQtvR0IWa2t+BZ4VCACAWQqya4tOj0vD6H4VCABCvhYCAACADQSBqIAMp
AzhC29HQhZra34FnhUIAIAMpAzBCq8j1xcesh7ugf4VCABCvhYCAACAOICEgAykDKCADKQMghSIWp3EiGmotAAAiF0UNDCAOQXBqISQgFkI5iKchCSAOQXRq
ISUgISEgAkADQCAgIQICQCAXwEF/Sg0AIAkgF0H/AHFHDQAgDiAlKAIAaiIXIA5JDQ8gF0EDcQ0PIBdFDQ8gCiAXIBpBAnRqIhcoAgBGDQILIBpBAWoiF0UN
DiAOIBcgIXEiGmotAAAiF0UNDiACQX9qISAgAkUNDgwACwsgDiAkKAIAaiICIA5JDQwgAkEDcQ0MIAJFDQwgIyACIBpBA3RqIhooAgBqIgIgI0kNDCACQQNx
DQwgAkUNDAJAAkAgGigCBCIaDQBBACEaDAELIBpBgICAgARPDQ0gGkECdCEaCyAaQQ9qIiAgGkkNDCAjIAYoAjRqIhogI0kNDCAaRQ0MIAIgGmsiGiACSw0M
IBpBD3ENDCAjIAYoAixqIgIgI0kNDCACQQdxDQwgAkUNDCAgQQ9NDQwgAiAaQQp2IgpBA3RqIgkgCSkDAEJ/QcAAICBBBHYiIEHAACAaQQR2QT9xIhprIiEg
ICAhSRsiIWutiCAarYaENwMAICAgIWsiGiAgSw0MAkACQCAaQcEATw0AIApBAWohCQwBCwJAIBpBv39qIhpBA3ZB+P///wFxQQhqIiNFDQAgCUEIakH/ASAj
/AsACyAaQQZ2IApqQQJqIQkgICAhIBpBQHFqa0FAaiEaCwJAIBpFDQAgAiAJQQN0aiICIAIpAwBCf0EAIBprQT9xrYiENwMACyAOICUoAgBqIgIgDkkNDCAC
QQNxDQwgAkUNDCAXIAJrIhogF0sNDCAOIBpBAnYiF2pBAToAACACIBpqQarVqtV6NgIAIA4gJCgCAGoiAiAOSQ0MIAJBA3ENDCACRQ0MIAIgF0EDdGpCqtWq
1arVqtWqfzcCACANKAIAIgJBf2oiFyACSw0MIA0gFzYCACAiICIpAwBCfIM3AwALIAtBAWoiCyAdRw0ACyAdIAYvAagCIhdHDQELIAggCCkDAEL/////v3+D
IhY3AwAMAQsgBigCECIaIAgoAgBqIgIgGkkNCCACQQdxDQggAkUNCAJAAkAgF0UNAANAIAIpAwBCA4NCAVENAiACQQhqIQIgF0F/aiIXDQALCyAIIAgpAwBC
/////79/gyIWNwMADAELIAgpAwAhFgsCQCAWQoCAgICAAoNQDQACQCAdRQ0AQQAhCgNAAkAgGyAKQQN0aiILLQAFQSBxRQ0AIAsgBigCECIiayIlIAtLDQsg
IiAGKAKkAWoiFyAiSQ0LIBdFDQsgF0EDcQ0LIBdBfGoiJCgCAEUNACAXQXhqKAIAIgJBf2oiISACSw0LIANBEGogJa1CgYCAgBB+IhZC29HQhZra34FnhUIA
IBZCrJri06PS8PofhUIAEK+FgIAAIAMgAykDGELb0dCFmtrfgWeFQgAgAykDEEKryPXFx6yHu6B/hUIAEK+FgIAAIBcgISADKQMIIAMpAwCFIhancSIOai0A
ACIaRQ0AIBdBcGohDSAWQjmIpyEJIBdBdGohIyAhISACQANAICAhAgJAIBrAQX9KDQAgCSAaQf8AcUcNACAXICMoAgBqIhogF0kNDiAaQQNxDQ4gGkUNDiAl
IBogDkECdGoiGigCAEYNAgsgDkEBaiIaRQ0NIBcgGiAhcSIOai0AACIaRQ0CIAJBf2ohICACRQ0CDAALCyAXIA0oAgBqIgIgF0kNCyACQQFxDQsgAkUNCyAC
IA5BAXRqLwEAIg5FDQsgBigCtAEgDk0NCyAiIAYoAqwBaiICICJJDQsgAkEDcQ0LIAJFDQsgAiAOQRxsaiICLwEYIg5FDQsgAkEYaiAOQX9qIgI7AQACQCAC
Qf//A3ENACAGKAKwASICQX9qIg4gAksNDCAGIA42ArABCyAXICMoAgBqIgIgF0kNCyACQQNxDQsgAkUNCyAaIAJrIg4gGksNCyAXIA5BAnYiGmpBAToAACAC
IA5qQarVqtV6NgIAIBcgDSgCAGoiAiAXSQ0LIAJBAXENCyACRQ0LIAIgGkEBdGpBqtUCOwEAICQoAgAiAkF/aiIXIAJLDQsgJCAXNgIAIAsgCykDAEL/////
//93gzcDAAsgCkEBaiIKIB1HDQALCwJAIB0gBi8BqAIiF0cNACAIIAgpAwBC//////99gyIWNwMADAELIAYoAhAiGiAIKAIAaiICIBpJDQggAkEHcQ0IIAJF
DQgCQAJAIBdFDQADQCACLQAFQSBxDQIgAkEIaiECIBdBf2oiFw0ACwsgCCAIKQMAQv//////fYMiFjcDAAwBCyAIKQMAIRYLAkAgFkKAgICAgAGDUA0AAkAg
HUUNACAbIQIgHSEaA0ACQCACKQMAIhZCgICA4P//AINQDQAgBigCSCAWQhqIp0H//wNxIiBNDQsgBigCECIOIAYoAkBqIhcgDkkNCyAXQQNxDQsgF0UNCyAX
ICBBJGxqIhcvASAiDkUNCyAXQSBqIA5Bf2oiFzsBACAXQf//A3ENACAGKAJEIhdBf2oiDiAXSw0LIAYgDjYCRAsgAkEIaiECIBpBf2oiGg0ACwsCQCAdIAYv
AagCIhdGDQAgBigCECIaIAgoAgBqIgIgGkkNCSACQQdxDQkgAkUNCSAXRQ0AA0AgAigBAkGA+P8fcQ0CIAJBCGohAiAXQX9qIhcNAAsLIAggCCkDAEL/////
/36DNwMACwJAIB1BA3QiAkUNACAbQQAgAvwLAAsgGUEBaiIZIBxHDQAMAgsLQgFBICANQX9qQQR2Z2utQv//A4OGpyEdDAILIAAoAnQiAkGAgICABE8NBAJA
AkAgAg0AQQAhF0Gq1arVeiECDAELIAAoAmwiAkEDcQ0FIAAoAnAhFwsCQCAXRQ0AA0ACQCACKAIAIhooAgAgBkcNAAJAIBovAQQiDiAFSQ0AIBogDiAFazsB
BAwBCyAaQQA2AQQLIAJBBGohAiAXQX9qIhcNAAsLIAYgDDsBqgIgBCAFaiICIARJIRcgAiEEIBcNBAsgA0HkAGogA0HAAGoQoIOAgAAgAy0AbEUNAgwBCwsg
HCAdQRNqQXxxIiYgHUECdGoiJyAdQQN0amoiFyAcSQ0BIARBH2oiAiAESQ0BIAJBgICAwABPDQEgAkEFdkE/akEGdiIoQQN0Ih8gAkEFdEGAeHFqIgIgH0kN
ASAXQQdqIhogF0kNASAaQXhxIiQgAmoiIiAkSQ0BIAxBHG4hF0EAISkgIiECQQAhKkEAIStBACEsQQAhHkEAISMCQCAMQRxJDQBCAUEgIBdBf2pnIhprrUL/
/wODhqciLLhEAAAAAAAA6j+iIhVEAAAAAAAA8L9lDQIgFUQAAAAAAADwQWYNAiAV/AMiHq1CHH4iFkIgiKcNAiAsQQF0QQNqQfz///8HcSIrIBanaiIqICtJ
DQIgIiAqaiICICJJDQJBf0EAIBprdEF/cyEpQgFBICAXQQR0QX9qZ2uthqchIwsgAkEDaiIXIAJJDQEgF0F8cSItICNBAXQgI0ETakF8cSIuICNBAnRqIi9q
QQNqQXxxaiICIC1JDQEgAkH//wNqIjAgAkkNASAOIAVqIgIgDkkNASACQQdxDQEgAkUNASAOQQdxDQEgDkUNAQJAAkACQCAIRQ0AIAlBA3QhJUEAIRcgDiEa
IAghIANAIBcgCk8NAiACIA5rIiEgAksNBSAaICGtNwMAIAIgJWohAiAXIAlqIRcgGkEIaiEaICBBf2oiIA0ACwsgMEGAgHxxISUgDkH4/wNxRQ0BICVFDQEM
AwsgFyAKEIiAgIAAAAsgCyASaiIhIAtJDQEgDiALaiICIA5JDQEgAkUNAQJAIBFBAXQiF0UNACACQQAgF/wLAAsgDiAhaiIgIA5JDQEgIEUNAQJAIBBFDQAg
ICECAkAgEEEkbCIwQVxqIgpBJG4iF0EDcUEDRg0AQQAgF0EBakEDcWshF0EAKAK8uciAACEaQQApArS5yIAAIRZBACkCrLnIgAAhMUEAKQKkuciAACEyQQAp
Apy5yIAAITMgICECA0AgAkEgaiAaNgIAIAJBGGogFjcCACACQRBqIDE3AgAgAkEIaiAyNwIAIAIgMzcCACACQSRqIQIgF0EBaiIXDQALCyAKQewASQ0AICAg
MGohGkEAKAK8uciAACEXQQApArS5yIAAIRZBACkCrLnIgAAhMUEAKQKkuciAACEyQQApApy5yIAAITMDQCACQSBqIBc2AgAgAkEYaiAWNwIAIAJBEGogMTcC
ACACQQhqIDI3AgAgAiAzNwIAIAJBJGogMzcCACACQSxqIDI3AgAgAkE0aiAxNwIAIAJBPGogFjcCACACQcQAaiAXNgIAIAJByABqIDM3AgAgAkHQAGogMjcC
ACACQdgAaiAxNwIAIAJB4ABqIBY3AgAgAkHoAGogFzYCACACQYwBaiAXNgIAIAJBhAFqIBY3AgAgAkH8AGogMTcCACACQfQAaiAyNwIAIAJB7ABqIDM3AgAg
AkGQAWoiAiAaRw0ACwsgDiAkaiICIA5JDQEgAkUNAQJAIB9FDQAgAkH/ASAf/AsACyAkIB9qIh8gJEkNASAOIBtqIgIgDkkNASACRQ0BAkAgGUUNACACQf8B
IBn8CwALIBsgGWoiGSAbSQ0BIA4gHGoiAkEQaiIXQQNxDQEgAkEANgIMIAIgHTYCCCACICZBcGo2AgQgAiAnQXBqNgIAAkAgHUUNACAXQQAgHfwLAAsgFyAO
ayIdIBdLDQEgDiAtaiICQQA2AgwgAiAjNgIIIAIgLkFwajYCBCACIC9BcGo2AgAgAkEQaiECAkAgI0UNACACQQAgI/wLAAsgLUEQaiIjIAJLDQEgIiAraiIK
ICJJDQEgDiAiaiICIA5JDQEgAkUNAQJAICxBAXQiF0UNACACQQAgF/wLAAsgDiAKaiIgIA5JDQEgIEUNASAErSEzAkAgHkUNACAgIQICQCAeQRxsIgRBZGoi
HEEcbiIXQQNxQQNGDQBBACAXQQFqQQNxayEXQQAoAti5yIAAIRpBACkC0LnIgAAhFkEAKQLIuciAACExQQApAsC5yIAAITIgICECA0AgAkEYaiAaNgIAIAJB
EGogFjcCACACQQhqIDE3AgAgAiAyNwIAIAJBHGohAiAXQQFqIhcNAAsLIBxB1ABJDQAgICAEaiEaQQAoAti5yIAAIRdBACkC0LnIgAAhFkEAKQLIuciAACEx
QQApAsC5yIAAITIDQCACQRhqIBc2AgAgAkEQaiAWNwIAIAJBCGogMTcCACACIDI3AgAgAkEcaiAyNwIAIAJBJGogMTcCACACQSxqIBY3AgAgAkE0aiAXNgIA
IAJBOGogMjcCACACQcAAaiAxNwIAIAJByABqIBY3AgAgAkHQAGogFzYCACACQdQAaiAyNwIAIAJB3ABqIDE3AgAgAkHkAGogFjcCACACQewAaiAXNgIAIAJB
8ABqIgIgGkcNAAsLIAxBEHQhAiAzQiCGIRYgDa0hMSAGIBQ7AVwgBiATNgJYIAYgEjYCVCAGQQA2AlAgBiARNgJMIAYgEDYCSCAGQQA2AkQgBiAhNgJAIAYg
CzYCPCAGIB02AjggBiAZNgI0IAYgGDYCMCAGIBs2AiwgBiAfNgIoIAYgKDYCJCAGICQ2AiAgBiAFNgIcIAZBADYCGCAGICU2AhQgBiAONgIQIANBADoAcAJA
QcIARQ0AIAZB4ABqQQBBwgD8CwALIAIgD3IhAiAWIDGEIRYgBiApOwHIASAGICo2AsQBIAYgKzYCwAEgBkEANgK8ASAGICw2ArgBIAYgHjYCtAEgBkEANgKw
ASAGIAo2AqwBIAYgIjYCqAEgBiAjNgKkASAGQQE7AaIBAkBBygBFDQAgBkHMAWpBAEHKAPwLAAsgBiAJOwGoAiAGIAI2AqQCIAYgCDsBogIgBiAJOwGgAiAG
IBY3ApgCIAZBATsBlgIgBkEAOwGqAiAGIAMtAHA6AKwCIAchBAsgACgCaCICIARrIhcgAksNACAAIBc2AmggAS0ACA0CIARFDQIgBCECDAELAAsCQANAIANB
9ABqIAAQroOAgAAgAy8BeCIXDQEgAkF/aiICRQ0CDAALCyADIBc7AX4gA0H+AGoQ1YOAgAAMAQsgACAEEKGDgIAACyADQYABaiSAgICAAAv6BgEKfyOAgICA
AEEQayIBJICAgIAAAkAgACgCWCICRQ0AIABBoAFqIQMgACgCxAEhBCACIQUgAC8BjgEiBiEHAkADQCAHQf//A3EgBS8BqgIiCE0NASAHIAhrIQcgBSgCCCIF
DQAMAgsLIAggB2shByAELwEGIQkCQAJAAkAgBCgCACIKIAVHDQAgBC8BBCIFIAdB//8DcUkNASAFIAdrQf//A3EhBQwCCyAIIAdB//8DcWsiB0H//wNxIAdH
DQIgBSgCDCIFRQ0AIAdB//8DcSEHA0ACQCAFIApHDQAgByAELwEEaiIFIAdJDQQMAwsgByAFLwGqAmoiCCAHSQ0DIAghByAFKAIMIgUNAAsLIAAvAYwBRQ0B
AkADQCAGQf//A3EgAi8BqgIiBU0NASAGIAVrIQYgAigCCCICRQ0DDAALCyAFIAZrIgZB//8DcUEBaiIHQf//A3EiCCAHRw0BIAUgCEkNASAEQQA2AgggBEEA
OwEGIAQgBjsBBCAEIAI2AgAgACgCWCIFRQ0BIAAvAY4BIQcCQANAIAdB//8DcSAFLwGqAiIITQ0BIAcgCGshByAFKAIIIgVFDQMMAAsLIAggB2shBwJAAkAg
AiAFRw0AIAZB//8DcSAHQf//A3FJDQMgBiAHa0H//wNxIQUMAQsgCCAHQf//A3FrIgdB//8DcSAHRw0CIAUoAgwiBUUNAiAHQf//A3EhBwNAAkAgBSACRw0A
IAcgBkH//wNxaiIFIAdJDQQMAgsgByAFLwGqAmoiCCAHSQ0DIAghByAFKAIMIgVFDQMMAAsLQQAhCQsCQEEBDQBBAEEAEMuDgIAAAAsgACAJOwHQASAFQYCA
BE8NACAAIAU7AdIBIAAoAsQBIgcvAQQiAiAHKAIAIgUvAaoCTw0AIAcvAQYiCCAFLwGoAk8NACAFKAIQIgcgBSgCGGoiBSAHSQ0AIAVBB3ENACAFRQ0AIAcg
BSACQQN0aiICKAIAaiIFIAdJDQAgBUEHcQ0AIAVFDQAgACACNgLIASAAIAUgCEEDdGo2AswBAkAgAC8B1AFFDQAgABDAgICAAEH//wNxIgVFDQAgASAFOwEC
IAFBAmoQx4OAgAAgA0EYakEANgIAIANBEGpCADcDACADQQhqQgA3AwAgA0IANwMAIABBADsB1AELIAFBEGokgICAgAAPCwAL4QIBC38jgICAgABBEGsiASSA
gICAAAJAIAAoAlgiAkUNAEEAIQMCQANAIAIoAhAiBCACKAIYaiIFIARJDQIgBUEHcQ0CIAVFDQICQAJAIAIvAaoCIgZFDQAgAyAGaiEHIAUgBkEDdGohCEEA
IQkgAyEKA0AgCSAGRg0FIAQgCCAJQX9zQQN0aigCAGoiBSAESQ0FIAVBB3ENBSAFRQ0FAkAgAC8BjAEiC0UNAAJAA0AgBSkDAEL+//+DgIADg1BFDQEgBUEI
aiEFIAtBf2oiC0UNAgwACwsgAC8BjgEiBSAKayILIAVLDQYgC0UNAwNAIAFBCGogABCug4CAACABLwEMIgsNBiAKIAVBf2oiBUYNBAwACwsgAyAJc0F/Rg0F
IApBAWoiCiAALwGOAUsNAiAJQQFqIgkgBkcNAAsgByEDCyACKAIIIgINAQsLQQAhCwsgAUEQaiSAgICAACALDwsAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSA
gICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ+IOAgABB//8DcQ4D
AAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcMAQQBBACADQiCIpygCABGA
gICAAICAgIAAIgJFDQACQEHDAEUNACACQaoBQcMA/AsACyABQsMANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ+IOAgABB
//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiAB
QbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGq
ASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEg
AiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAA
gICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC58DAQd/I4CAgIAAQSBrIgIkgICAgAACQAJAAkACQAJAAkAgAS0ALEUNACABQSRqIQMgAS8BKiEE
IAEvASghBSABKAIkIQYgAkEAOgAEIAEvATAhByABLQAgQQFxRQ0CIAdB//8DcUEBaiIFQf//A3EiCCAFRg0BDAULIABCADcCACAAQQhqQgA3AgAMAwsgASAF
OwEwIAggBEH//wNxSQ0BIAJBCGogARCgg4CAACADQQhqIAJBCGpBCGooAgA2AgAgAyACKQIINwIAIAEtACxFDQEgASABLwEoOwEwDAELAkAgB0H//wNxIgRF
DQACQCAEIAVB//8DcUcNACADQgA3AgAgA0EIakEANgIADAILIAEgB0F/ajsBMAwBCyACQRRqIAEQoIOAgAAgA0EIaiACQRRqQQhqKAIANgIAIAMgAikCFDcC
ACABLQAsRQ0AIAEvASpBf2oiA0H//wNxIANHDQIgASADOwEwCyAAQQE6AAwgACACLQAEOgAIIABBADsBBiAAIAc7AQQgACAGNgIACyACQSBqJICAgIAADwsA
C7gCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBPiADayIFaiIGIARJDQIgA0Gy4siAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQfDiyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQT5JDQAL
IAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL7TQFAn8BfgZ/A34CfyOAgICAAEGAB2siBCSAgICAAAJAAkACQAJAIAAvAfoR
IAJB//8DcUcNAEEAIQUgAC8B+BEgA0H//wNxRg0CDAELAkAgACgCNCIFRQ0AIAEpAgAhBiAAKAIwIQcCQCAFRQ0AIAdBqgEgBfwLAAsgBqcgByAFQQBBACAG
QiCIpygCDBGBgICAAICAgIAACyAAQSxqIQgCQEHMAEUNACAIQaoBQcwA/AsACyABKQIAIQYgBEGoBmpBACgCvPjJgAA2AgAgBEEAKQK0+MmAADcDoAZBqtWq
1XohBUEAIQkCQAJAIAJB//8DcSIBQYEESQ0AQQANASAGpyABQYB8aiIJQQBBACAGQiCIpygCABGAgICAAICAgIAAIgVFDQECQCAJRQ0AIAVBACAJ/AsACyAE
IAk2AqgGIAQgBTYCpAYLIARBtAZqQgA3AgAgBEG8BmpCADcCACAEQcQGakIANwIAIARBzAZqQgA3AgAgBEHUBmpCADcCACAEQdwGakIANwIAIARB5AZqQgA3
AgAgBEIANwKsBiAEIAE2AqAGAkAgCUUNACAFQQAgCfwLAAsCQCABQX9qIgdBCUkNACAFQUFqIQogBEGtBmohC0EAIQFBCCEFA0ACQAJAIAVB/wNLDQAgCyAB
aiIMIAwtAABBAXI6AAAMAQsgAUFBaiAJTw0GIAogAWoiDCAMLQAAQQFyOgAACyABQQFqIQEgBUEIaiIFIAdJDQALCwJAQcwARSIBDQAgBCAEQaAGakHMAPwK
AAALIAENASAIIARBzAD8CgAADAELQRkhBQwBCyAALQAkQQFxRQ0BIAAoAhwiDEUNASAANQIAIQ0gAC8B9BEhASAMKQKgASEGIAxCADcCoAEgDEGwAWoiBSkC
ACEOIAVCADcCACAMQbgBaiIFKAIAIQcgBUEANgIAIAxBqAFqIgUpAgAhDyAFQgA3AgAgBEHYAGogDzcDACAEQeAAaiAONwMAIARB6ABqIAc2AgAgBCAGNwNQ
IAQgAUEDcToATwJAAkACQCAMEMCAgIAAQf//A3EiAQ0AIAwoAsABIQkgDC8B1gEiBw0BDAILIAEQjICAgAAACyAMKALEASgCACIFKAK0ASAHTQ0CIAUoAhAi
CyAFKAKsAWoiASALSQ0CIAFBA3ENAiABRQ0CIAEgB0EcbGoiAS8BGCIHRQ0CIAFBGGogB0F/aiIBOwEAAkAgAUH//wNxDQAgBSgCsAEiAUF/aiIHIAFLDQMg
BSAHNgKwAQsgDEEANgLAASAMQQA7AdYBCyAMQaABaiEKQQAhCwJAAkACQAJAAkAgDC0AjAJFDQAgDC8B/AEiECAMLwGMAU8NACAMLwH+ASEIIARBoAZqIAxB
ABC6g4CAACAELwGkBiIRQQFqIgFB//8DcSIFIAFHDQYgBCgCoAYiAS8BqgIiByAFayIFQf//A3EgBUcNBgJAAkAgCCAFQf//A3EiBUsNACARIAhqIgVB//8D
IAVB//8DSRshBwwBCwJAAkAgASgCDCIBRQ0AIAggBWshBQNAIAUgAS8BqgIiB00NAiAFIAdrIQUgASgCDCIBDQALCyAHDQIMCAsgBUF/aiIHIAVLDQcLAkAC
QCAMKAJQIgtFDQAgDCALKAIANgJQDAELQRkhBUEADQMgDEHAAGpBDEECIAEQj4CAgAAiC0UNAyALQqrVqtWq1arVqn83AAAgC0EIakGq1arVejYAACALQQNx
DQcLIAtBADYCCCALIBA7AQYgCyAHOwEEIAsgATYCACAMQewAaiAMQRBqIAsQk4CAgAAiBUH//wNxDQELIAQtAE8iAUEDcUEBRw0CDAMLIAtCqtWq1arVqtWq
fzcCACALQQhqQarVqtV6NgIAIAsgDCgCUDYCACAMIAs2AlALIAogBCkDUDcCACAKQRhqIgEgBEHQAGpBGGooAgA2AgAgCkEQaiIHIARB0ABqQRBqKQMANwIA
IApBCGoiACAEQdAAakEIaikDADcCACAMEMCAgIAAQf//A3EiCUUNAiAEIAk7AW4gBEHuAGoQx4OAgAAgAUEANgIAIAdCADcCACAAQgA3AgAgCkIANwIAIAxB
ADsB1AEMAgsgDC0A2wFBA3FFDQACQAJAIAFBA3EOBAEEAAQBCyAMKALEASgCACIFKAIQIgcgDCgCyAEiCCgCAGoiASAHSQ0DIAFBB3ENAyABRQ0DIAwgBUEQ
aiAIIAEgBS8BqAIQs4KAgAAMAQsgBEGIAWpCADcDACAEQfAAakEIaiAMKALEASIBQQhqKAIANgIAIARBADoAlAEgBEIANwOAASAEQQE6AHwgBEEAOgCQASAE
IAEpAgA3A3AgBEGYAWogBEHwAGoQyIOAgAACQAJAAkAgBC0ApAFFDQAgBEHgAWpBCGogBEGYAWpBCGooAgA2AgAgBCAEKQKYATcD4AEgBEEBOgCIAiAEQQA6
AIwCIARBAToA7AEgBEEAOgD8ASAEQQE6AIACIARBkAJqIARB4AFqEKCDgIAAIAQtAJgCRQ0BIARB2AJqQSBqIARB4AFqQSBqKAIANgIAIARB2AJqQRhqIARB
4AFqQRhqKQMANwMAIARB2AJqQRBqIARB4AFqQRBqKQMANwMAIARB2AJqQQhqIARB4AFqQQhqKQMANwMAIAQgBCkD4AE3A9gCIARBAToAhAMgBCAEKQOQAiIG
NwL8AiAEIAZCIIg9AYgDIARB2AJqIQEMAgsQyYOAgAAMAgsgBEHKAmpCADcBACAEQaACakEgaiAEQeABakEgaigCADYCACAEQaACakEYaiAEQeABakEYaikD
ADcDACAEQaACakEQaiAEQeABakEQaikDADcDACAEQaACakEIaiAEQeABakEIaikDADcDACAEIAQpA+ABNwOgAiAEQgA3AsQCIARBoAJqIQELIARBqAFqQTBq
IAFBMGooAgA2AgAgBEGoAWpBKGogAUEoaikCADcDACAEQagBakEgaiABQSBqKQIANwMAIARBqAFqQRhqIAFBGGopAgA3AwAgBEGoAWpBEGogAUEQaikCADcD
ACAEQagBakEIaiABQQhqKQIANwMAIAQgASkCADcDqAEgBEGQA2ogBEGoAWoQxIOAgAAgBC0AnANFDQADQCAELwGUAyIIIAQoApADIgEvAaoCTw0DIAQvAZYD
IAEvAagCIhBPDQMgASgCECIFIAEoAhhqIgcgBUkNAyAHQQdxDQMgB0UNAyAFIAcgCEEDdGoiCCgCAGoiByAFSQ0DIAdBB3ENAyAHRQ0DIAwgAUEQaiAIIAcg
EBCzgoCAACAEQZADaiAEQagBahDEg4CAACAELQCcAw0ACwsgBEEBOgCmAyAEIAM7AaQDIARBAToAogMgBCACOwGgAyAEQQE6AKwDIAQgDUKABINCAFI6AK4D
IAQgDCgB0AE2AagDAkACQCAMIARBoANqEMqDgIAAIgVB//8DcUUNACALRQ0BIAwgCxDlgICAAAwBCwJAIAwtALQCRQ0AIAxB1J/HgABBtPrJgAAQwIOAgAAL
IAwQwYOAgAACQCALRQ0AIAwtAIwCRQ0DIAwoAlgiAUUNAyAMLwGOASEFAkADQCAFQf//A3EgAS8BqgIiB00NASAFIAdrIQUgASgCCCIBRQ0FDAALCyAHIAVr
IQUgCy8BBiEQAkACQAJAIAsoAgAiCCABRw0AIAsvAQQiASAFQf//A3FJDQEgASAFa0H//wNxIQEMAgsgByAFQf//A3FrIgVB//8DcSAFRw0FIAEoAgwiAUUN
ACAFQf//A3EhBQNAAkAgASAIRw0AIAUgCy8BBGoiASAFSQ0HDAMLIAUgAS8BqgJqIgcgBUkNBiAHIQUgASgCDCIBDQALCyAMQQA6AIECIAxBADYB/AEMAQsC
QEEBDQBBAEEAEMuDgIAAAAsgDCAQOwH8ASABQYCABE8NAyAMIAE7Af4BIAwtAIECRQ0AIBBB//8DcSIBIAJBf2pB//8DcUYNACAMQQA6AIECIAFBAWoiAUH/
/wNxIAFHDQMgDCABOwH8AQsCQCAJRQ0AAkAgDCAJKAIMIAkoAhBBACAJKAIAIAktAAhBAXEiARtBACAJKAIEIAEbEJGDgIAAIgFB//8DcUUNACAEIAE7AbID
IARBsgNqEMyDgIAACyAMKQKYASIGQiCIpyEFIAanIQcCQCAJKAIQIgFFDQAgCSgCDCEIAkAgAUUNACAIQaoBIAH8CwALIAcgCCABQQBBACAFKAIMEYGAgIAA
gICAgAALAkAgCS0ACEEBcQ0AIAkoAgQiAUUNACAJKAIAIQgCQCABRQ0AIAhBqgEgAfwLAAsgByAIIAFBAEEAIAUoAgwRgYCAgACAgICAAAsgDCgCmAEgCUEU
QQJBACAMQZwBaigCAEEMaigCABGBgICAAICAgIAACwJAIAtFDQAgDCALEOWAgIAACyAKIAQpA1A3AgAgCkEYaiIBIARB0ABqQRhqKAIANgIAIApBEGoiBSAE
QdAAakEQaikDADcCACAKQQhqIgcgBEHQAGpBCGopAwA3AgACQCAMEMCAgIAAQf//A3EiCUUNACAEIAk7AbQDIARBtANqEMeDgIAAIAFBADYCACAFQgA3AgAg
B0IANwIAIApCADcCACAMQQA7AdQBCwJAIAAtACRBAnFFDQAgACgCICIMRQ0AIAwpAqABIQYgDEIANwKgASAMQagBaiIBKQIAIQ4gAUIANwIAIARBwANqIA43
AwAgDEGwAWoiASkCACEOIAFCADcCACAEQcgDaiAONwMAIAxBuAFqIgEoAgAhBSABQQA2AgAgBEHQA2ogBTYCACAEQQE6ALcDIAQgBjcDuAMCQAJAIAwQwICA
gABB//8DcSIBDQAgDCgCwAEhCSAMLwHWASIHRQ0BIAwoAsQBKAIAIgUoArQBIAdNDQUgBSgCECILIAUoAqwBaiIBIAtJDQUgAUEDcQ0FIAFFDQUgASAHQRxs
aiIBLwEYIgdFDQUgAUEYaiAHQX9qIgE7AQACQCABQf//A3ENACAFKAKwASIBQX9qIgcgAUsNBiAFIAc2ArABCyAMQQA2AsABIAxBADsB1gEMAQsgARCMgICA
AAALIAxBoAFqIQpBACELAkACQAJAAkACQCAMLQCMAkUNACAMLwH8ASIQIAwvAYwBTw0AIAwvAf4BIQggBEGgBmogDEEAELqDgIAAIAQvAaQGIhFBAWoiAUH/
/wNxIgUgAUcNCCAEKAKgBiIBLwGqAiIHIAVrIgVB//8DcSAFRw0IAkACQCAIIAVB//8DcSIFSw0AIBEgCGoiBUH//wMgBUH//wNJGyEHDAELAkACQCABKAIM
IgFFDQAgCCAFayEFA0AgBSABLwGqAiIHTQ0CIAUgB2shBSABKAIMIgENAAsLIAdFDQoMAgsgBUF/aiIHIAVLDQkLAkACQCAMKAJQIgtFDQAgDCALKAIANgJQ
DAELQRkhBUEADQMgDEHAAGpBDEECIAEQj4CAgAAiC0UNAyALQqrVqtWq1arVqn83AAAgC0EIakGq1arVejYAACALQQNxDQkLIAtBADYCCCALIBA7AQYgCyAH
OwEEIAsgATYCACAMQewAaiAMQRBqIAsQk4CAgAAiBUH//wNxDQELIAQtALcDIgFBA3FBAUcNAgwDCyALQqrVqtWq1arVqn83AgAgC0EIakGq1arVejYCACAL
IAwoAlA2AgAgDCALNgJQCyAKIAQpA7gDNwIAIApBGGoiASAEQbgDakEYaigCADYCACAKQRBqIgcgBEG4A2pBEGopAwA3AgAgCkEIaiIAIARBuANqQQhqKQMA
NwIAIAwQwICAgABB//8DcSIJRQ0EIAQgCTsB1gMgBEHWA2oQx4OAgAAgAUEANgIAIAdCADcCACAAQgA3AgAgCkIANwIAIAxBADsB1AEMBAsgDC0A2wFBA3FF
DQACQAJAIAFBA3EOBAEGAAYBCyAMKALEASgCACIFKAIQIgcgDCgCyAEiCCgCAGoiASAHSQ0FIAFBB3ENBSABRQ0FIAwgBUEQaiAIIAEgBS8BqAIQs4KAgAAM
AQsgBEHwA2pCADcDACAEQdgDakEIaiAMKALEASIBQQhqKAIANgIAIARBADoA/AMgBEIANwPoAyAEQQE6AOQDIARBADoA+AMgBCABKQIANwPYAyAEQYAEaiAE
QdgDahDIg4CAAAJAAkACQCAELQCMBEUNACAEQcgEakEIaiAEQYAEakEIaigCADYCACAEIAQpAoAENwPIBCAEQQE6APAEIARBADoA9AQgBEEBOgDUBCAEQQA6
AOQEIARBAToA6AQgBEH4BGogBEHIBGoQoIOAgAAgBC0AgAVFDQEgBEHABWpBIGogBEHIBGpBIGooAgA2AgAgBEHABWpBGGogBEHIBGpBGGopAwA3AwAgBEHA
BWpBEGogBEHIBGpBEGopAwA3AwAgBEHABWpBCGogBEHIBGpBCGopAwA3AwAgBCAEKQPIBDcDwAUgBEEBOgDsBSAEIAQpA/gEIgY3AuQFIAQgBkIgiD0B8AUg
BEHABWohAQwCCxDJg4CAAAwCCyAEQbIFakIANwEAIARBiAVqQSBqIARByARqQSBqKAIANgIAIARBiAVqQRhqIARByARqQRhqKQMANwMAIARBiAVqQRBqIARB
yARqQRBqKQMANwMAIARBiAVqQQhqIARByARqQQhqKQMANwMAIAQgBCkDyAQ3A4gFIARCADcCrAUgBEGIBWohAQsgBEGQBGpBMGogAUEwaigCADYCACAEQZAE
akEoaiABQShqKQIANwMAIARBkARqQSBqIAFBIGopAgA3AwAgBEGQBGpBGGogAUEYaikCADcDACAEQZAEakEQaiABQRBqKQIANwMAIARBkARqQQhqIAFBCGop
AgA3AwAgBCABKQIANwOQBCAEQfgFaiAEQZAEahDEg4CAACAELQCEBkUNAANAIAQvAfwFIgggBCgC+AUiAS8BqgJPDQUgBC8B/gUgAS8BqAIiEE8NBSABKAIQ
IgUgASgCGGoiByAFSQ0FIAdBB3ENBSAHRQ0FIAUgByAIQQN0aiIIKAIAaiIHIAVJDQUgB0EHcQ0FIAdFDQUgDCABQRBqIAggByAQELOCgIAAIARB+AVqIARB
kARqEMSDgIAAIAQtAIQGDQALCyAEQQA6AJgGIARBAToAkAYgBCADOwGOBiAEQQE6AIwGIAQgAjsBigYgBEEBOgCWBiAEIAwoAdABNgGSBgJAAkAgDCAEQYoG
ahDKg4CAACIFQf//A3FFDQAgC0UNASAMIAsQ5YCAgAAMAQsCQCAMLQC0AkUNACAMQdSfx4AAQbT6yYAAEMCDgIAACyAMEMGDgIAAAkAgC0UNACAMLQCMAkUN
BSAMKAJYIgFFDQUgDC8BjgEhBQJAA0AgBUH//wNxIAEvAaoCIgdNDQEgBSAHayEFIAEoAggiAUUNBwwACwsgByAFayEFIAsvAQYhEAJAAkACQCALKAIAIggg
AUcNACALLwEEIgEgBUH//wNxSQ0BIAEgBWtB//8DcSEBDAILIAcgBUH//wNxayIFQf//A3EgBUcNByABKAIMIgFFDQAgBUH//wNxIQUDQAJAIAEgCEcNACAF
IAsvAQRqIgEgBUkNCQwDCyAFIAEvAaoCaiIHIAVJDQggByEFIAEoAgwiAQ0ACwsgDEEAOgCBAiAMQQA2AfwBDAELAkBBAQ0AQQBBABDLg4CAAAALIAwgEDsB
/AEgAUGAgARPDQUgDCABOwH+ASAMLQCBAkUNACAQQf//A3EiASACQX9qQf//A3FGDQAgDEEAOgCBAiABQQFqIgFB//8DcSABRw0FIAwgATsB/AELAkAgCUUN
AAJAIAwgCSgCDCAJKAIQQQAgCSgCACAJLQAIQQFxIgEbQQAgCSgCBCABGxCRg4CAACIBQf//A3FFDQAgBCABOwGcBiAEQZwGahDMg4CAAAsgDCkCmAEiBkIg
iKchBSAGpyEHAkAgCSgCECIBRQ0AIAkoAgwhCAJAIAFFDQAgCEGqASAB/AsACyAHIAggAUEAQQAgBSgCDBGBgICAAICAgIAACwJAIAktAAhBAXENACAJKAIE
IgFFDQAgCSgCACEIAkAgAUUNACAIQaoBIAH8CwALIAcgCCABQQBBACAFKAIMEYGAgIAAgICAgAALIAwoApgBIAlBFEECQQAgDEGcAWooAgBBDGooAgARgYCA
gACAgICAAAsCQCALRQ0AIAwgCxDlgICAAAsgCiAEKQO4AzcCACAKQRhqIgEgBEG4A2pBGGooAgA2AgAgCkEQaiIFIARBuANqQRBqKQMANwIAIApBCGoiByAE
QbgDakEIaikDADcCACAMEMCAgIAAQf//A3EiCUUNASAEIAk7AZ4GIARBngZqEMeDgIAAIAFBADYCACAFQgA3AgAgB0IANwIAIApCADcCACAMQQA7AdQBDAEL
IAogBCkDuAM3AgAgCkEYaiIBIARBuANqQRhqKAIANgIAIApBEGoiByAEQbgDakEQaikDADcCACAKQQhqIgAgBEG4A2pBCGopAwA3AgAgDBDAgICAAEH//wNx
IglFDQIgBCAJOwGaBiAEQZoGahDHg4CAACABQQA2AgAgB0IANwIAIABCADcCACAKQgA3AgAgDEEAOwHUAQwCCyAAIAI7AfoRQQAhBSAAQQA7AfwRIAAgAzsB
+BEgAEH2EWoiASABLQAAQQJyOgAAIANB//8DcUF/aiIBQf//A3EgAUcNAiAAQQA7AYASIAAgATsB/hEgACACQX9qOwGCEgwBCyAKIAQpA1A3AgAgCkEYaiIB
IARB0ABqQRhqKAIANgIAIApBEGoiByAEQdAAakEQaikDADcCACAKQQhqIgAgBEHQAGpBCGopAwA3AgAgDBDAgICAAEH//wNxIglFDQAgBCAJOwGwAyAEQbAD
ahDHg4CAACABQQA2AgAgB0IANwIAIABCADcCACAKQgA3AgAgDEEAOwHUAQsgBEGAB2okgICAgAAgBQ8LAAuvBgMCfwF+An8jgICAgABBwBBrIgEkgICAgAAC
QEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEM2DgIAAQf//A3EOAwAEAQQL
AkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0E9QQBBACADQiCIpygCABGAgICAAICA
gIAAIgJFDQAgAkKq1arVqtWq1ap/NwAAIAJBEGpCqtWq1arVqtWqfzcAACACQQhqQqrVqtWq1arVqn83AAAgAkE1akKq1arVqtWq1ap/NwAAIAJBMGpCqtWq
1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQj03A5gQIAEgAjYClBAgAUHclceA
ADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABDNg4CAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2
ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKc
EAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKo
ECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALqxIBEH8jgICAgABBwABrIgIkgICA
gAACQAJAAkACQAJAAkAgAS0AIEEBcQ0AIAEtAAxFDQIgAUEIaiEDIAEvAQYhBCABLwEWIQUgASgCACEGIAEvAQQhByABLQAcIghB/wFxIQkDQCADKAIAIQpB
ACELAkAgCUUNACABKAIQIAZGIAEvARRB//8DcSAHQf//A3FGcSAFQf//A3EgBEH//wNxRnEhCwsgB0H//wNxIgwgBi8BqgJPDQYgBEH//wNxIAYvAagCTw0G
IAYoAhAiAyAGKAIYaiINIANJDQYgDUEHcQ0GIA1FDQYgAyANIAxBA3RqIg4oAgBqIg0gA0kNBiANQQdxDQYgDUUNBgJAAkACQAJAIA4pAwBCJYinQQNxIgNF
DQAgA0F/ag4DAgEKAgsgC0UNAgwFCwJAIAsNACAIQf8BcSEPIAohDiAGIQwgByELA0AgDCEJIA4hEAJAAkACQAJAIAsiCEH//wNxRQ0AIAhBf2ohCyAJIQwM
AQsgCSgCCCIMRQ0BQQEhAwNAAkAgAyAMLwGqAiINSw0AIA0gA2shCwwCCyADIA1rIQMgDCgCCCIMDQAMAgsLQQAhDiACQQA6ACAgD0UNASABKAIQIAxHDQEg
AS8BFEH//wNxIAtB//8DcUcNASAFQf//A3EgBEH//wNxRw0BCyABQgA3AgAgAUEIakIANwIAQQEhDSACQQRqIQMMCgsgC0H//wNxIhEgDC8BqgJPDQogBEH/
/wNxIAwvAagCTw0KIAwoAhAiDSAMKAIYaiIDIA1JDQogA0EHcQ0KIANFDQogDSADIBFBA3RqIhEoAgBqIgMgDUkNCiADQQdxDQogA0UNCiARKQMAQiWIp0ED
cSIDQQJGDQALAkACQAJAAkAgAw4EAQANDQELAkAgC0H//wNxRQ0AIAtBf2ohAyAMIQYMAgtB+OXHgAAhDSAMKAIIIgZFDQJBASEDA0ACQCADIAYvAaoCIgdL
DQAgByADayEDDAMLIAMgB2shAyAGKAIIIgZFDQMMAAsLQQEhDSABQQE6AAwgAUEANgIIIAEgCzsBBCABIAw2AgAgAkEEaiEDIAkhBiAIIQcgECEKDAoLIAIg
BDsBLiACIAM7ASwgAiAGNgIoIAJBADoAJCACQQE6ADQgAkEAOgAwIAJBKGohDQsgASANKQIANwIAIAFBCGogDUEIaikCADcCAEEBIQ0gAkEEaiEDIAwhBiAL
IQdBACEKDAgLIAFCADcCACABQQhqQgA3AgBBASENIAJBBGohAwwHC0H45ceAACEOAkAgCw0AAkACQCAHQf//A3FFDQAgB0F/aiENIAYhAwwBCyAGKAIIIgNF
DQFBASENA0ACQCANIAMvAaoCIgxLDQAgDCANayENDAILIA0gDGshDSADKAIIIgMNAAwCCwsgAiAEOwEWIAIgDTsBFCACIAM2AhAgAkEAOgAMIAJBAToAHCAC
QQA6ABggAkEQaiEOCyABIA4pAgA3AgAgAUEIaiAOQQhqKQIANwIAQQEhDSACQQRqIQMMBgsCQAJAIAxFDQAgB0F/aiEHDAELIAYoAggiBkUNA0EBIQMDQAJA
IAMgBi8BqgIiB0sNACAHIANrIQcMAgsgAyAHayEDIAYoAggiBkUNBAwACwsgAkEAOgA8IAJBADoAOCACQTxqIQMMAAsLAkAgAS0ADEUNACABQQhqIQMgAS8B
BiEEIAEvARYhCCABKAIAIQYgAS8BBCEHIAEtABwiEUH/AXEhBQNAIAMoAgAhCkEAIQ4CQCAFRQ0AIAEoAhAgBkYgAS8BFEH//wNxIAdB//8DcUZxIAhB//8D
cSAEQf//A3FGcSEOCyAHQf//A3EiCyAGLwGqAiIMTw0GIARB//8DcSAGLwGoAk8NBiAGKAIQIgMgBigCGGoiDSADSQ0GIA1BB3ENBiANRQ0GIAMgDSALQQN0
aiIJKAIAaiINIANJDQYgDUEHcQ0GIA1FDQYCQAJAAkACQCAJKQMAQiWIp0EDcSIDRQ0AIANBf2oOAwEBCgELIA5FDQEMAgsCQCAODQAgEUH/AXEhESAHIQsg
BiEDAkACQANAAkAgDEH//wNxIAtBAWoiC0H//wNxRw0AAkACQAJAIAMoAgwiDUUNAEEBIQMDQCADIA0vAaoCIgxNDQMgAyAMayEDIA0oAgwiDQ0ADAILCyAD
LwGqAiEMCyAMQf//A3FFDQ4MAwsgA0F/aiILIANLDQ0gDSEDCyALQf//A3EiCSAMQf//A3FPDQwgBEH//wNxIg8gAy8BqAJPDQwgAygCECINIAMoAhhqIg4g
DUkNDCAOQQdxDQwgDkUNDCANIA4gCUEDdGoiBSgCAGoiDiANSQ0MIA5BB3ENDCAORQ0MAkAgBSkDAEIliKdBA3EiDUECRg0AIA0OBAMDDQ0DCyARRQ0AIAEo
AhAgA0cNACABLwEUQf//A3EgCUcNACAIQf//A3EgD0cNAAsLIAFCADcCACABQQhqQgA3AgBBASENIAJBCGohAwwJC0EBIQ0gAUEBOgAMIAFBADYCCCABIAs7
AQQgASADNgIAIAJBADoAPCACQQhqIQMMCAsgAUIANwIAIAFBCGpCADcCAEEBIQ0gAkEIaiEDDAcLAkACQCAMIAdBAWpB//8DcUYNACALQf7/AyALQf7/A0kb
QQFqIQcMAQsgBigCDCIGRQ0BQQEhAwJAA0AgAyAGLwGqAiIHTQ0BIAMgB2shAyAGKAIMIgYNAAsgB0UNCQwCCyADQX9qIgcgA0sNCAsgAkEAOgAoIAJBADoA
ECACQShqIQMMAQsLIAFCADcCACABQQhqQgA3AgBBACEGIAJBCmpBADoAACACQQA7AQggAkEIaiEDDAMLQQAhBiACQQpqQQA6AAAgAkEAOwEIIAJBCGohAwwC
CyABQgA3AgAgAUEIakIANwIAQQAhBiACQQZqQQA6AAAgAkEAOwEEIAJBBGohAwwBC0EAIQYgAkEGakEAOgAAIAJBADsBBCACQQRqIQMLQQAhB0EAIQpBACEN
CyAAIA06AAwgACAKNgIIIABBADsBBiAAIAc7AQQgACAGNgIAIAAgAy8AADsADSAAQQ9qIANBAmotAAA6AAAgAkHAAGokgICAgAAPCwALmAkDB38BfgF/I4CA
gIAAQcAQayIAJICAgIAAAkBBgBBFDQAgAEGqAUGAEPwLAAsgAEKAEDcCiBAgAEG43sGAADYCgBAgACAANgKEEEEAIQECQAJAAkADQCAAKAKMECICQckAIAFr
IgNqIgQgAkkNAiABQavjyIAAaiEFAkACQCAEIAAoAogQIgZLDQACQCAEIAZLDQACQCAAKAKEECACaiIEQfTjyIAATw0AIAUgBCADakkNBgsCQCADRQ0AIAQg
BSAD/AoAAAsgACgCjBAiBCADaiICIARJDQUgACACNgKMEAwCCyAEIAYQiICAgAAACyAAKAKAECgCACEEIAAgAzYCnBAgACAFNgKYECAAQbAQaiAAQYAQaiAA
QZgQakEBQQEgBBGBgICAAICAgIAAIAApA7AQIgenIQMCQCAHQiCIp0H//wNxDgMBBAAEC0EADQJBACkD4PrJgAAiB6dByQBBAEEAIAdCIIinKAIAEYCAgIAA
gICAgAAiAUUNAgJAQckARQ0AIAFBqgFByQD8CwALIABCyQA3A5AQIAAgATYCjBAgAEHclceAADYCiBAgACAHNwOAECAAQYgQaiEIQQAhAQNAIAAoApQQIgJB
yQAgAWsiA2oiBCACSQ0EIAFBq+PIgABqIQUCQAJAIAQgACgCkBAiBksNAAJAIAQgBksNAAJAIAAoAowQIAJqIgRB9OPIgABPDQAgBSAEIANqSQ0ICwJAIANF
DQAgBCAFIAP8CgAACyAAKAKUECIEIANqIgIgBEkNByAAIAI2ApQQDAILIAQgBhCIgICAAAALIAAoAogQKAIAIQQgACADNgKsECAAIAU2AqgQIABBsBBqIAgg
AEGoEGpBAUEBIAQRgYCAgACAgICAACAAKQOwECIHpyEDAkAgB0IgiKdB//8DcQ4DAQYABgsgACgCkBAiAUUNBCAAKAKAECEEIAAoAoQQIQIgACgCjBAhAwJA
IAFFDQAgA0GqASAB/AsACyAEIAMgAUEAQQAgAigCDBGBgICAAICAgIAADAQLIAEgA2oiAyABSQ0EIAMhASADQckASQ0ACwJAIAAoApQQIgEgACgCkBAiA0sN
ACAAQgA3A5AQIAAoAowQIQQgAEGq1arVejYCjBAgACADNgK4ECAAIAE2ArQQIAAgBDYCsBAgAEGYEGogAEGwEGogAEGAEGoQuICAgAAgACAAKAK4ECIBNgKQ
ECAAIAAoArAQIgM2AowQIAAgACgCtBA2ApQQAkAgAUUNACAAKAKAECEEIAAoAoQQIQICQCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACA
gICAAAsgAC8BoBANAyAAKAKYECIDIAAoApwQIgEQgICAgAAgAUUNAwJAIAFFDQAgA0GqASAB/AsACyAAIAMgAUEAIAAQuYCAgAAMAwsgASADEIiAgIAAAAsg
ASADaiIDIAFJDQIgAyEBIANByQBJDQALIAAoAowQIgEgACgCiBAiA0sNAiAAKAKEECABEICAgIAACyAAQcAQaiSAgICAAA8LAAsgASADEIiAgIAAAAvEBQII
fwF+I4CAgIAAQTBrIgIkgICAgAACQAJAAkAgAS0AAkUNACABLwEARQ0BCyABQQRqIQMCQCABLQAGRQ0AIAMvAQBFDQELIABCADcChAECQCABLQAODQAgACAB
EM6DgIAAIQQMAgsgAyAAQY4BaiIFIAEtAAYbLwEAIQMgACgCZCEGIAJBHGpBqN/BgAAgASAAQYwBaiIEIAEtAAIbLwEAQQEQkoCAgABBAiEHAkACQAJAAkAC
QEHXASACLwEmIAIvASwbIghB//8DcSIJIANPDQAgCUUNASADQX9qQf//A3EgCEH//wNxbkECaiEHCyAHrUKAgCR+IgpCIIinDQQgACAKpzYCZAJAAkACQCAB
IAQgAS0AAhsvAQAiAyAALwGMASIHRg0AIAMgB0kNAiADIAdNDQcgACADIAFBCGoQz4OAgAAiBEH//wNxRQ0BDAYLIAAgARDOg4CAACIEQf//A3ENBQwECyAA
IAEQzoOAgAAiBEH//wNxRQ0DDAILIAJBCGpBCGogAUEIaiIJKQEANwMAIAIgASkBADcDCCACQQE6AAogAiAHOwEIIAAgAkEIahDOg4CAACIEQf//A3ENASAA
IAMgCRDPg4CAACIEQf//A3FFDQIgACAGNgJkDAULQRoQjICAgAAACyAAIAY2AmQMAwtBACEEIAAtAJABQR50QR51IgFBf0oNAgJAIAFBA3FBfmoOAgACAAsg
ACgCWCIBRQ0BIAAoAoABIQggBS8BACEDAkADQCADQf//A3EgAS8BqgIiB00NASADIAdrIQMgASgCCCIBRQ0DDAALCwJAAkAgCCgCACIJIAFGDQADQCABKAIM
IgFFDQUgASAJRw0ADAILCyAILwEEIAcgA2tB//8DcUkNAwtBACEEIABBADoAkAEMAgsgACAGNgJkDAELAAsgAkEwaiSAgICAACAEC0wBAX8jgICAgABBIGsi
AiSAgICAACACQQhqIAEQ14OAgAAgAiAAENeDgIAAIAIgAikDCDcCECACIAIpAwA3AhggAkEQahCZgoCAAAALqAYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAA
AkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABDYg4CAAEH//wNxDgMABAEE
CwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBOkEAQQAgA0IgiKcoAgARgICAgACA
gICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBOGpBqtV+OwAAIAJBMGpCqtWq1arVqtWq
fzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjo3A5gQIAEgAjYClBAgAUHclceAADYCkBAg
ASADNwOIEAJAAkAgAUGIEGpBCGogABDYg4CAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEg
AjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABF
DQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICA
gIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJB
qgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALuAICB38BfiOAgICAAEEQayICJICAgIAAQQAh
AwJAAkACQANAIAAoAgwiBEE7IANrIgVqIgYgBEkNAiADQfDiyIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBq+PIgABPDQAgByAG
IAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEg
BhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBO0kNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAg
BQ8LAAsgBiAIEIiAgIAAAAuVXwoGfwF+GX8BfhR/AXwQfwF+AX8DfiOAgICAAEGgE2siAiSAgICAACAAKAJkIgMhBAJAAkACQAJAAkACQAJAIAEtAA4NACAB
QQRqIABBjgFqIAEtAAYbLwEAIQQgAkHYAmpBqN/BgAAgASAAQYwBaiABLQACGy8BAEEBEJKAgIAAQQIhBQJAAkBB1wEgAi8B4gIgAi8B6AIbIgZB//8DcSIH
IARPDQAgB0UNASAEQX9qQf//A3EgBkH//wNxbkECaiEFCyAFrUKAgCR+IghCIIinDQIgCKchBAwBC0EaEIyAgIAAAAsgACAENgJkIAEtAAJFDQMgACgCaCEJ
IAEvAQAiCiAALwGMASILQf//A3EiBEYNAgJAAkACQAJAIAogBEkNACAKIAtB//8DcU0NBCACQfAAaiAAQYjmx4AAQbT6yYAAEJ+DgIAAIAJBlAFqIAJB8ABq
EKCDgIAAIAItAJwBDQEMBQsgAkHAAGogAEGI5seAAEG0+smAABCfg4CAACACQeQAaiACQcAAahCgg4CAACACLQBsRQ0CA0AgAigCZCIMKAIQIgQgDCgCGGoi
DSAESQ0EIA1BB3ENBCANRQ0EAkAgDC8BqgIiDkUNAEEAIQ8DQCAMKAIQIgUgDSAPQQN0aiIQKAIAaiIEIAVJDQYgBEEHcQ0GIARFDQYgCiAALwGMASIRSw0E
IAQgCkEDdGohEiARIAprIRMCQCAQKQMAIghCgICAgMAAg1ANAAJAIBEgCkYNAEEAIRQDQAJAIBIgFEEDdGoiFSkDAEIDg0IBUg0AIBUgDCgCECIWayIXIBVL
DQogFiAMKAI4aiIGIBZJDQogBkUNCiAGQQNxDQogBkF8aiIYKAIARQ0KIAZBeGooAgAiBEF/aiIZIARLDQogAkEwaiAXrUKBgICAEH4iCELb0dCFmtrfgWeF
QgAgCEKsmuLTo9Lw+h+FQgAQr4WAgAAgAkEgaiACKQM4QtvR0IWa2t+BZ4VCACACKQMwQqvI9cXHrIe7oH+FQgAQr4WAgAAgBiAZIAIpAyggAikDIIUiCKdx
IgdqLQAAIgVFDQogBkFwaiEaIAhCOYinIRsgBkF0aiEcIBkhHQJAA0AgHSEEAkAgBcBBf0oNACAbIAVB/wBxRw0AIAYgHCgCAGoiBSAGSQ0NIAVBA3ENDSAF
RQ0NIBcgBSAHQQJ0aiIFKAIARg0CCyAHQQFqIgVFDQwgBiAFIBlxIgdqLQAAIgVFDQwgBEF/aiEdIAQNAAwMCwsgBiAaKAIAaiIEIAZJDQogBEEDcQ0KIARF
DQogFiAEIAdBA3RqIgcoAgBqIgQgFkkNCiAEQQNxDQogBEUNCgJAAkAgBygCBCIHDQBBACEHDAELIAdBgICAgARPDQsgB0ECdCEHCyAHQQ9qIh0gB0kNCiAW
IAwoAjRqIgcgFkkNCiAHRQ0KIAQgB2siByAESw0KIAdBD3ENCiAWIAwoAixqIgQgFkkNCiAEQQdxDQogBEUNCiAdQQ9NDQogBCAHQQp2IhdBA3RqIhsgGykD
AEJ/QcAAIB1BBHYiHUHAACAHQQR2QT9xIgdrIhkgHSAZSRsiGWutiCAHrYaENwMAIB0gGWsiByAdSw0KAkACQCAHQcEATw0AIBdBAWohGwwBCwJAIAdBv39q
IgdBA3ZB+P///wFxQQhqIhZFDQAgG0EIakH/ASAW/AsACyAHQQZ2IBdqQQJqIRsgHSAZIAdBQHFqa0FAaiEHCwJAIAdFDQAgBCAbQQN0aiIEIAQpAwBCf0EA
IAdrQT9xrYiENwMACyAGIBwoAgBqIgQgBkkNCiAEQQNxDQogBEUNCiAFIARrIgcgBUsNCiAGIAdBAnYiBWpBAToAACAEIAdqQarVqtV6NgIAIAYgGigCAGoi
BCAGSQ0KIARBA3ENCiAERQ0KIAQgBUEDdGpCqtWq1arVqtWqfzcCACAYKAIAIgRBf2oiBSAESw0KIBggBTYCACAVIBUpAwBCfIM3AwALIBRBAWoiFCATRw0A
CwsCQCATIAwvAagCIgVHDQAgECAQKQMAQv////+/f4MiCDcDAAwBCyAMKAIQIgcgECgCAGoiBCAHSQ0HIARBB3ENByAERQ0HAkACQCAFRQ0AA0AgBCkDAEID
g0IBUQ0CIARBCGohBCAFQX9qIgUNAAsLIBAgECkDAEL/////v3+DIgg3AwAMAQsgECkDACEICwJAIAhCgICAgIACg1ANAAJAIBEgCkYNAEEAIRcDQAJAIBIg
F0EDdGoiFC0ABUEgcUUNACAUIAwoAhAiFWsiHCAUSw0KIBUgDCgCpAFqIgUgFUkNCiAFRQ0KIAVBA3ENCiAFQXxqIhooAgBFDQAgBUF4aigCACIEQX9qIhkg
BEsNCiACQRBqIBytQoGAgIAQfiIIQtvR0IWa2t+BZ4VCACAIQqya4tOj0vD6H4VCABCvhYCAACACIAIpAxhC29HQhZra34FnhUIAIAIpAxBCq8j1xcesh7ug
f4VCABCvhYCAACAFIBkgAikDCCACKQMAhSIIp3EiBmotAAAiB0UNACAFQXBqIRggCEI5iKchGyAFQXRqIRYgGSEdAkADQCAdIQQCQCAHwEF/Sg0AIBsgB0H/
AHFHDQAgBSAWKAIAaiIHIAVJDQ0gB0EDcQ0NIAdFDQ0gHCAHIAZBAnRqIgcoAgBGDQILIAZBAWoiB0UNDCAFIAcgGXEiBmotAAAiB0UNAiAEQX9qIR0gBEUN
AgwACwsgBSAYKAIAaiIEIAVJDQogBEEBcQ0KIARFDQogBCAGQQF0ai8BACIGRQ0KIAwoArQBIAZNDQogFSAMKAKsAWoiBCAVSQ0KIARBA3ENCiAERQ0KIAQg
BkEcbGoiBC8BGCIGRQ0KIARBGGogBkF/aiIEOwEAAkAgBEH//wNxDQAgDCgCsAEiBEF/aiIGIARLDQsgDCAGNgKwAQsgBSAWKAIAaiIEIAVJDQogBEEDcQ0K
IARFDQogByAEayIGIAdLDQogBSAGQQJ2IgdqQQE6AAAgBCAGakGq1arVejYCACAFIBgoAgBqIgQgBUkNCiAEQQFxDQogBEUNCiAEIAdBAXRqQarVAjsBACAa
KAIAIgRBf2oiBSAESw0KIBogBTYCACAUIBQpAwBC////////d4M3AwALIBdBAWoiFyATRw0ACwsCQCATIAwvAagCIgVHDQAgECAQKQMAQv//////fYMiCDcD
AAwBCyAMKAIQIgcgECgCAGoiBCAHSQ0HIARBB3ENByAERQ0HAkACQCAFRQ0AA0AgBC0ABUEgcQ0CIARBCGohBCAFQX9qIgUNAAsLIBAgECkDAEL//////32D
Igg3AwAMAQsgECkDACEICwJAIAhCgICAgIABg1ANACASIQQgEyEHAkAgESAKRg0AA0ACQCAEKQMAIghCgICA4P//AINQDQAgDCgCSCAIQhqIp0H//wNxIh1N
DQogDCgCECIGIAwoAkBqIgUgBkkNCiAFQQNxDQogBUUNCiAFIB1BJGxqIgUvASAiBkUNCiAFQSBqIAZBf2oiBTsBACAFQf//A3ENACAMKAJEIgVBf2oiBiAF
Sw0KIAwgBjYCRAsgBEEIaiEEIAdBf2oiBw0ACwsCQCATIAwvAagCIgVGDQAgDCgCECIHIBAoAgBqIgQgB0kNCCAEQQdxDQggBEUNCCAFRQ0AA0AgBCgBAkGA
+P8fcQ0CIARBCGohBCAFQX9qIgUNAAsLIBAgECkDAEL//////36DNwMACwJAIBNBA3QiBEUNACASQQAgBPwLAAsgD0EBaiIPIA5HDQALCyAMIAo7AagCIAJB
5ABqIAJBwABqEKCDgIAAIAItAGwNAAwDCwsgC0H//wNxQX9qIgRB//8DcSIeIARHIR8gAEEsaiEgIABBGGohIUEAKQPg+smAACIiQiCIpyEjIAJB6BJqQQhq
ISQgAkG+AWohJSACQcQBakECciEmIAJBvAFqIScgAkGwAWpBCGohKCACQboBaiEpICKnISoDQCACKAKUASEXIAAgCjsBjAECQAJAIBcvAaACIApJDQAgFygC
ECIHIBcoAhhqIgUgB0kNBSAFQQdxDQUgBUUNBQJAIBcvAaoCIgZFDQACQCAfRQ0AIAcgBSgCAGoiBCAHSQ0HIARBB3EaAAsDQCAHIAUoAgBqIgQgB0kNByAE
QQdxDQcgBEUNBwJAIB4gFy8BqAIiHU8NACAEIB5BA3RqKQMAQoCAgICAgAODQoCAgICAgANRDQMgBUEIaiEFIAZBf2oiBkUNAgwBCwsgHiAdEIiAgIAAAAsg
FyAKOwGoAgwBCyACQbABaiAXQZgCaiAKQQEQkoCAgAACQAJAIAIvAcABDQAgAkGwAWohKyApISwgKCEtICchLiAlIS8MAQsgAiAKOwHMASACIBcpApgCNwPQ
ASACIBcoAqQCNgLEASACIBcvAaoCIgQgFy8BogIiBSAEIAVJGzsBygEgAkHQAWohKyACQcQBaiEuIAJBzAFqIS0gAkHKAWohLCAmIS8LIBdBEGohFUEAITAC
QCAXKAIIIgxFDQAgDC8BogIiByAMLwGqAiIFTQ0AIBcoAhAiBiAXKAIYaiIEIAZJDQUgBEEHcQ0FIARFDQUgDCgCECIdIAwoAhhqIgYgHUkNBSAGQQdxDQUg
BkUNBQJAIAcgBWtB//8DcSIdIBcvAaoCIhkgHSAZSRsiGUUNACAMQRBqIR0gBiAFQQN0aiEHQQAhMCAZIQYCQANAIAwvAaoCQQFqIgVB//8DcSAFRw0IIAwg
BTsBqgICQCAdIBUgByAEQQAgDC8BqAIQl4OAgABB//8DcQ0AIARBCGohBCAHQQhqIQcgMEEBaiEwIAZBf2oiBkUNAgwBCwsgDC8BqgJBf2oiBEH//wNxIARH
DQcgDCAEOwGqAgwCCyAMLwGiAiEHIAwvAaoCIQULIAVB//8DcSAHQf//A3FLDQUgACgCdCIEQYCAgIAETw0FAkACQCAEDQBBACEFQarVqtV6IQQMAQsgACgC
bCIEQQNxDQYgACgCcCEFCwJAIAUNACAZITAMAQsDQAJAIAQoAgAiBygCACAXRw0AIAcvAQQiBiAZTw0AIAcgDDYCACAMLwGqAiAZayIdQf//A3EiGyAdRw0H
IAYgG2oiBkH//wNxIAZHDQcgByAGOwEECyAEQQRqIQQgBUF/aiIFDQALIBkhMAsgMCEZAkACQCAwQf//A3EgFy8BqgIiBEkNACAwIRkMAQsDQCAZIRMCQAJA
AkACQCAAKAIoIgVFDQAgACAFKAIANgIoDAELQQANASAhQbACQQMgBBCPgICAACIFRQ0BAkBBsAJFDQAgBUGqAUGwAvwLAAsgBUEHcQ0JCwJAQbACRQ0AIAVB
qgFBsAL8CwALIC0vAQAiHCAsLwEAIg1sIhZBgICAgAJPDQggDUEDdCIxIBZBA3RqIhogMUkNCAJAAkAgLi8BACIEDQBBACEyQQAhM0EAITRBACE1QQAhNgwB
C0IBQSAgBEF/amciBGutQv//A4OGpyIzuEQAAAAAAADqP6IiN0QAAAAAAADwv2UNCSA3RAAAAAAAAPBBZg0JIDf8AyIyrUIkfiIIQiCIpw0JIDNBAXRBA2pB
/P///wdxIjQgCKdqIjUgNEkNCUF/QQAgBGt0QX9zITYLIBogNWoiBiAaSQ0IICsoAgAiB0EPaiIEIAdJDQggBEGAgICAAU8NCCAEQQR2QT9qQQZ2IjhBA3Qi
OSAEQQR0QYB+cWoiBCA5SQ0IIAZBB2oiHSAGSQ0IIB1BeHEiESAEaiI6IBFJDQgCQAJAIAcNAEEAIRAMAQtCAUEgIAdBf2pBBHZna61C//8Dg4anIRALIDog
EEETakF8cSI7IBBBAnRqIjwgEEEDdGpqIgcgOkkNCCArKAIEIgZBH2oiBCAGSQ0IIARBgICAwABPDQggBEEFdkE/akEGdiI9QQN0Ij4gBEEFdEGAeHFqIgQg
PkkNCCAHQQdqIgYgB0kNCCAGQXhxIg8gBGoiGCAPSQ0IIC8vAQAiB0EcbiEGQQAhPyAYIQRBACFAQQAhQUEAIUJBACFDQQAhEgJAIAdBHEkNAEIBQSAgBkF/
amciB2utQv//A4OGpyJCuEQAAAAAAADqP6IiN0QAAAAAAADwv2UNCSA3RAAAAAAAAPBBZg0JIDf8AyJDrUIcfiIIQiCIpw0JIEJBAXRBA2pB/P///wdxIkEg
CKdqIkAgQUkNCSAYIEBqIgQgGEkNCUF/QQAgB2t0QX9zIT9CAUEgIAZBBHRBf2pna62GpyESCyAEQQNqIgcgBEkNCCAHQXxxIkQgEkEBdCASQRNqQXxxIkUg
EkECdGoiRmpBA2pBfHFqIgQgREkNCCAEQf//A2oiByAESQ0IIC4oAgAhRyArKQIAIUgCQAJAIAdBgIB8cSIOQYGAJEkNAEEADQEgICkCACIIpyAOQRBBACAI
QiCIpygCABGAgICAAICAgIAAIh1FDQECQCAORQ0AIB1BqgEgDvwLAAsgDiFJIB1B//8DcQ0KDAMLAkACQCAAKAI8Ih1FDQAgACAdKAIANgI8DAELQQANASAg
QYCAJEEQIAQQj4CAgAAiHUUNAQJAQYCAJEUNACAdQaoBQYCAJPwLAAsgHUH//wNxDQoLQYCAJCFJQYCAJEUNAiAdQaoBQYCAJPwLAAwCCwJAQbACRQ0AIAVB
qgFBsAL8CwALIAUgACgCKDYCACAAIAU2AigLAkAgFygCCCIERQ0AIAQgDEYNAANAAkACQAJAAkAgBCgCCCIFRQ0AIAUgBCgCDCIHNgIMIAdFDQEMAgsgACAE
KAIMIgc2AlQgBw0BCyAAIAQoAgg2AlgMAQsgByAEKAIINgIICyAAKAJcIgcgBCgCFGsiBiAHSw0OIAAgBjYCXAJAAkAgBCgCFCIHQYCAJEsNAAJAIAdFDQAg
BCgCEEEAIAf8CwALIAQoAhAhBwJAQYCAJEUNACAHQaoBQYCAJPwLAAsgByAAKAI8NgIAIAAgBzYCPAwBCyAAKAIsIR0gACgCMCEZIAQoAhAhBgJAIAdFDQAg
BkGqASAH/AsACyAdIAYgB0EQQQAgGSgCDBGBgICAAICAgIAACwJAQbACRQ0AIARBqgFBsAL8CwALIAQgACgCKDYCACAAIAQ2AiggBUUNASAFIQQgBSAMRw0A
CwsCQCAwQf//A3FFDQAgDEUNDSAMLwGqAiIFIDBB//8DcWsiGEH//wNxIBhHDQ0gDCgCECIHIAwoAhhqIgQgB0kNDSAEQQdxDQ0gBEUNDQJAIBhB//8DcSIH
IAVLDQACQCAFIAdGDQAgBCAHQQN0aiESIAUgB2shGiAMQSxqIRVBACETA0AgDCgCECIEIBIgE0EDdGoiFigCAGoiFCAESQ0QIBRBB3ENECAURQ0QIAwvAagC
IRwCQCAWKQMAIghCgICAgMAAg1ANAAJAAkAgHEUNACAUIQUgFCEEIBwhBgNAAkAgBCkDAEIDg0IBUg0AIAUgDCgCECIHayIZIAVLDRUgByAMKAI4aiIdIAdJ
DRUgHUUNFSACIB02AqgCIAJBrAJqIB0gGRCtgoCAACACLQC0AkUNFSAHIAIoArACIhkoAgBqIh0gB0kNFSAdQQNxDRUgHUUNFSACKAKsAiEbIBUgByAdIBko
AgQQooOAgAAgAkGoAmogGxCugoCAACAEIAQpAwBCfIM3AwALIAVBCGohBSAEQQhqIQQgBkF/aiIGDQALIBwgDC8BqAIiBUcNASAWKQMAIQgLIBYgCEL/////
v3+DIgg3AwAMAQsgDCgCECIHIBYoAgBqIgQgB0kNESAEQQdxDREgBEUNEQJAAkAgBUUNAANAIAQpAwBCA4NCAVENAiAEQQhqIQQgBUF/aiIFDQALCyAWIBYp
AwBC/////79/gyIINwMADAELIBYpAwAhCAsCQCAIQoCAgICAAoNQDQACQCAcRQ0AIBQhBSAUIQQgHCEGA0ACQCAELQAFQSBxRQ0AIAUgDCgCECIHayIZIAVL
DRQgByAMKAKkAWoiHSAHSQ0UIB1FDRQgAiAdNgK4AiACQbwCaiAdIBkQsIKAgAAgAi0AxAJFDQAgAigCwAIvAQAiGUUNFCAMKAK0ASAZTQ0UIAcgDCgCrAFq
Ih0gB0kNFCAdQQNxDRQgHUUNFCAdIBlBHGxqIgcvARgiHUUNFCAHQRhqIB1Bf2oiBzsBAAJAIAdB//8DcQ0AIAwoArABIgdBf2oiHSAHSw0VIAwgHTYCsAEL
IAJBuAJqIAIoArwCELGCgIAAIAQgBCkDAEL///////93gzcDAAsgBUEIaiEFIARBCGohBCAGQX9qIgYNAAsLAkAgHCAMLwGoAiIFRw0AIBYgFikDAEL/////
/32DIgg3AwAMAQsgDCgCECIHIBYoAgBqIgQgB0kNESAEQQdxDREgBEUNEQJAAkAgBUUNAANAIAQtAAVBIHENAiAEQQhqIQQgBUF/aiIFDQALCyAWIBYpAwBC
//////99gyIINwMADAELIBYpAwAhCAsCQCAIQoCAgICAAYNQDQACQCAcRQ0AIBQhBCAcIQcDQAJAIAQpAwAiCEKAgIDg//8Ag1ANACAMKAJIIAhCGoinQf//
A3EiHU0NFCAMKAIQIgYgDCgCQGoiBSAGSQ0UIAVBA3ENFCAFRQ0UIAUgHUEkbGoiBS8BICIGRQ0UIAVBIGogBkF/aiIFOwEAIAVB//8DcQ0AIAwoAkQiBUF/
aiIGIAVLDRQgDCAGNgJECyAEQQhqIQQgB0F/aiIHDQALCwJAIBwgDC8BqAIiBUYNACAMKAIQIgcgFigCAGoiBCAHSQ0SIARBB3ENEiAERQ0SIAVFDQADQCAE
KAECQYD4/x9xDQIgBEEIaiEEIAVBf2oiBQ0ACwsgFiAWKQMAQv//////foM3AwALAkAgHEEDdCIERQ0AIBRBACAE/AsACyATQQFqIhMgGkcNAAsLIAwgGDsB
qgIMAQsgByAFEIqAgIAAAAsCQCAXKAIIIgRFDQAgBCAMRg0AA0ACQAJAAkACQCAEKAIIIgVFDQAgBSAEKAIMIgc2AgwgB0UNAQwCCyAAIAQoAgwiBzYCVCAH
DQELIAAgBCgCCDYCWAwBCyAHIAQoAgg2AggLIAAoAlwiByAEKAIUayIGIAdLDQ4gACAGNgJcAkACQCAEKAIUIgdBgIAkSw0AAkAgB0UNACAEKAIQQQAgB/wL
AAsgBCgCECEHAkBBgIAkRQ0AIAdBqgFBgIAk/AsACyAHIAAoAjw2AgAgACAHNgI8DAELIAAoAiwhHSAAKAIwIRkgBCgCECEGAkAgB0UNACAGQaoBIAf8CwAL
IB0gBiAHQRBBACAZKAIMEYGAgIAAgICAgAALAkBBsAJFDQAgBEGqAUGwAvwLAAsgBCAAKAIoNgIAIAAgBDYCKCAFRQ0BIAUhBCAFIAxHDQALCyAAIAs7AYwB
IAAoAmggCUcNDCAAIAM2AmRBGSEEDAsLAkAgSUUNACAdQQAgSfwLAAsgHSAxaiIEIB1JDQYgBEEHcQ0GIARFDQYgHUEHcQ0GAkACQAJAAkACQAJAAkAgDUUN
ACAcQQN0IRRBACEHIB0hBiANIRkDQCAHIBZPDQIgBCAdayIbIARLDQ4gBiAbrTcDACAGQQhqIQYgBCAUaiEEIAcgHGohByAZQX9qIhkNAAsLAkAgDkUNACAd
Qfj/A3ENEgsgGiA0aiIZIBpJDQwgHSAaaiIEIB1JDQwgBEUNDAJAIDNBAXQiB0UNACAEQQAgB/wLAAsgHSAZaiIGIB1JDQwgBkUNDAJAIDJFDQAgBiEEAkAg
MkEkbCIWQVxqIhtBJG4iB0EDcUEDRg0AQQAgB0EBakEDcWshByAGIQQDQCAEQSBqQQAoAry5yIAANgIAIARBGGpBACkCtLnIgAA3AgAgBEEQakEAKQKsuciA
ADcCACAEQQhqQQApAqS5yIAANwIAIARBACkCnLnIgAA3AgAgBEEkaiEEIAdBAWoiBw0ACwsgG0HsAEkNACAGIBZqIQYDQCAEQSBqQQAoAry5yIAAIgc2AgAg
BEEYakEAKQK0uciAACIINwIAIARBEGpBACkCrLnIgAAiSjcCACAEQQhqQQApAqS5yIAAIks3AgAgBEEAKQKcuciAACJMNwIAIARBJGogTDcCACAEQSxqIEs3
AgAgBEE0aiBKNwIAIARBPGogCDcCACAEQcQAaiAHNgIAIARByABqIEw3AgAgBEHQAGogSzcCACAEQdgAaiBKNwIAIARB4ABqIAg3AgAgBEHoAGogBzYCACAE
QYwBaiAHNgIAIARBhAFqIAg3AgAgBEH8AGogSjcCACAEQfQAaiBLNwIAIARB7ABqIEw3AgAgBEGQAWoiBCAGRw0ACwsCQEHCAEUiFg0AIAJB4AFqQQBBwgD8
CwALIB0gD2oiBCAdSQ0MIARFDRECQCA+RQ0AIARB/wEgPvwLAAsgDyA+aiIUIA9JDREgHSARaiIEIB1JDREgBEUNEQJAIDlFDQAgBEH/ASA5/AsACyARIDlq
IjkgEUkNESAdIDpqIgRBEGoiB0EDcQ0RIARBADYCDCAEIBA2AgggBCA7QXBqNgIEIAQgPEFwajYCAAJAIBBFDQAgB0EAIBD8CwALIAcgHWsiECAHSw0RIB0g
RGoiBEEANgIMIAQgEjYCCCAEIEVBcGo2AgQgBCBGQXBqNgIAIARBEGohBAJAIBJFDQAgBEEAIBL8CwALIAQgHWsiEiAESw0RIBggQWoiGyAYSQ0RIB0gGGoi
BCAdSQ0RIARFDRECQCBCQQF0IgdFDQAgBEEAIAf8CwALIB0gG2oiBiAdSQ0RIAZFDRECQCBDRQ0AIAYhBAJAIENBHGwiOkFkaiI+QRxuIgdBA3FBA0YNAEEA
IAdBAWpBA3FrIQcgBiEEA0AgBEEYakEAKALYuciAADYCACAEQRBqQQApAtC5yIAANwIAIARBCGpBACkCyLnIgAA3AgAgBEEAKQLAuciAADcCACAEQRxqIQQg
B0EBaiIHDQALCyA+QdQASQ0AIAYgOmohBgNAIARBGGpBACgC2LnIgAAiBzYCACAEQRBqQQApAtC5yIAAIgg3AgAgBEEIakEAKQLIuciAACJKNwIAIARBACkC
wLnIgAAiSzcCACAEQRxqIEs3AgAgBEEkaiBKNwIAIARBLGogCDcCACAEQTRqIAc2AgAgBEE4aiBLNwIAIARBwABqIEo3AgAgBEHIAGogCDcCACAEQdAAaiAH
NgIAIARB1ABqIEs3AgAgBEHcAGogSjcCACAEQeQAaiAINwIAIARB7ABqIAc2AgAgBEHwAGoiBCAGRw0ACwsgBSA2OwFcIAUgNTYCWCAFIDQ2AlQgBUEANgJQ
IAUgMzYCTCAFIDI2AkggBUEANgJEIAUgGTYCQCAFIBo2AjwgBSAQNgI4IAUgOTYCNCAFIDg2AjAgBSARNgIsIAUgFDYCKCAFID02AiQgBSAPNgIgIAUgMTYC
HCAFQQA2AhggBSAONgIUIAUgHTYCECACQQA6ANwBAkAgFg0AIAVB4ABqIAJB4AFqQcIA/AoAAAsgBSA/OwHIASAFIEA2AsQBIAUgQTYCwAEgBUEANgK8ASAF
IEI2ArgBIAUgQzYCtAEgBUEANgKwASAFIBs2AqwBIAUgGDYCqAEgBSASNgKkASAFQQE7AaIBAkBBygBFDQAgBUHMAWpBAEHKAPwLAAsgBSANOwGqAiAFIBw7
AagCIAUgRzYCpAIgBSANOwGiAiAFIBw7AaACIAUgSDcCmAIgBUEBOwGWAiAFIAItANwBOgCsAiAFIAApAwA3AwAgBUEAOwGqAiAFQgA3AgggACkDAEIBfCII
UA0RIAAgCDcDACAAKAJcIgQgSWoiByAESQ0RIAAgBzYCXCAXLwGqAiATQf//A3EiHGsiB0H//wNxIgQgB0cNESAXKAIQIgYgFygCGGoiByAGSQ0RIAdBB3EN
ESAHRQ0RIBwgLC8BACIGIAQgBiAESRsiBmoiFEH//wNxIh0gFEcNESAFKAIQIhkgBSgCGGoiBCAZSQ0RIARBB3ENESAERQ0RIB0gHGsgBkcNESATIRkgBkUN
AiAFQRBqIRYgByAcQQN0aiEGIBwgHWshGyATQX9zQf//A3EhHSATIRkDQCAFLwGqAkEBaiIHQf//A3EgB0cNEiAFIAc7AaoCIBYgFSAEIAZBACAFLwGoAhCX
g4CAACIHQf//A3ENAiAdRQ0SIAZBCGohBiAEQQhqIQQgHUF/aiEdIBlBAWohGSAbQQFqIhsNAAsgFCEZDAILIAcgFhCIgICAAAALIAIgBzsBygICQEGAEEUN
ACACQdgCakGqAUGAEPwLAAsgAkKAEDcC8BIgAkG43sGAADYC6BIgAiACQdgCajYC7BICQAJAAkAgAkHoEmogAkHKAmoQ+oOAgABB//8DcQ4DABIBEgsgAigC
9BIiBCACKALwEiIHSw0EIAIoAuwSIAQQgICAgAAMAQtBAA0AICpB2gBBAEEAICMoAgARgICAgACAgICAACIERQ0AAkBB2gBFDQAgBEGqAUHaAPwLAAsgAkLa
ADcD+BIgAiAENgL0EiACQdyVx4AANgLwEiACICI3A+gSAkACQCAkIAJBygJqEPqDgIAAQf//A3EOAwASARILIAIoAvwSIgQgAigC+BIiB0sNAyACQgA3A/gS
IAIoAvQSIQYgAkGq1arVejYC9BIgAiAHNgKYEyACIAQ2ApQTIAIgBjYCkBMgAkHYEmogAkGQE2ogAkHoEmoQuICAgAAgAiACKAKYEyIENgL4EiACIAIoApAT
Igc2AvQSIAIgAigClBM2AvwSAkAgBEUNACACKALoEiEGIAIoAuwSIR0CQCAERQ0AIAdBqgEgBPwLAAsgBiAHIARBAEEAIB0oAgwRgYCAgACAgICAAAsgAi8B
4BINASACKALYEiIHIAIoAtwSIgQQgICAgAAgBEUNAQJAIARFDQAgB0GqASAE/AsACyAEIAcgBEEAIAQQuYCAgAAMAQsgAigC+BIiBEUNACACKALoEiEGIAIo
AuwSIR0gAigC9BIhBwJAIARFDQAgB0GqASAE/AsACyAGIAcgBEEAQQAgHSgCDBGBgICAAICAgIAACyAFLwGqAkF/aiIEQf//A3EgBEcNDyAFIAQ7AaoCCyAF
IBc2AgwCQCAXKAIIIgRFDQAgBSAENgIIIAQgBTYCDAwDCyAFQQA2AgggACAFNgJUDAILIAQgBxCIgICAAAALIAQgBxCIgICAAAALIBcgBTYCCCAAKAJ0IgRB
gICAgARPDQsCQAJAAkAgBA0AQQAhB0Gq1arVeiEEQQBFDQIMAQsgACgCbCIEQQNxDQ0gACgCcCIHRQ0BCwNAAkAgBCgCACIGKAIAIBdHDQAgBi8BBCIdIBxJ
DQAgHSAZQf//A3FPDQAgBiAFNgIAIAYgHSATazsBBAsgBEEEaiEEIAdBf2oiBw0ACwsgGUH//wNxIBcvAaoCIgRJDQALCyAZQf//A3EgBEcNCSAXKAIMIQQC
QAJAIBcoAggiBUUNACAFIAQ2AgwMAQsgACAENgJUCyAXKAIIIQUCQAJAIARFDQAgBCAFNgIIDAELIAAgBTYCWAsgACgCXCIEIBcoAhRrIgUgBEsNCSAAIAU2
AlwCQAJAIBcoAhQiBEGAgCRLDQACQCAERQ0AIBUoAgBBACAE/AsACyAVKAIAIQQCQEGAgCRFDQAgBEGqAUGAgCT8CwALIAQgACgCPDYCACAAIAQ2AjwMAQsg
ACgCLCEHIAAoAjAhBiAVKAIAIQUCQCAERQ0AIAVBqgEgBPwLAAsgByAFIARBEEEAIAYoAgwRgYCAgACAgICAAAsCQEGwAkUNACAXQaoBQbAC/AsACyAXIAAo
Aig2AgAgACAXNgIoCyACQZQBaiACQfAAahCgg4CAACACLQCcAUUNBAwACwsgCiAREIqAgIAAAAsgACgCdCIEQYCAgIAETw0AAkACQCAEDQBBACEFQarVqtV6
IQQMAQsgACgCbCIEQQNxDQEgACgCcCEFCyAFRQ0BIApBf2oiBkH//wNxIAZHIR0DQAJAIAQoAgAiBy8BBiAKSQ0AIB0NAiAHIAY7AQYLIARBBGohBCAFQX9q
IgVFDQIMAAsLAAsgACAKOwGMAQsgACgCaCAJRw0CCwJAIAEtAAYNAEEAIQQMAQsCQCAALwGOASIEIAEvAQQiBkcNAEEAIQQMAQsCQAJAAkACQAJAIAQgBksN
ACAEIAZPDQYgAS0ADA0BDAQLIAAoAlgiBUUNBSAFLwGqAkF/aiIHQf//A3EgB0cNBSAFLwGoAkF/aiIdQf//A3EgHUcNBSACQQA6AKwCIAJBADoA2BIgAkEA
OgC8AiACQQE6APQSIAJBADoA8BIgAiAdOwHuEiACIAc7AewSIAIgBTYC6BIgAkEAOgCEEyACQQA6AIgTIAJBkBNqIAJB6BJqEKCDgIAAIAItAJgTRQ0BIAJB
2AJqQSBqIAJB6BJqQSBqKAIANgIAIAJB2AJqQRhqIAJB6BJqQRhqKQIANwMAIAJB2AJqQRBqIAJB6BJqQRBqKQIANwMAIAJB2AJqQQhqIAJB6BJqQQhqKQIA
NwMAIAIgAikC6BI3A9gCIAJBAToAhAMgAiACKQOQEyIINwL8AiAIQjCIp0F/aiIFQf//A3EgBUcNBSACIAU7AYgDIAJB2AJqIQUMAgsgBEF/aiIFQf//A3Ei
ByAFRw0EIAEvAQpB//8DcSAHTw0CIAYgBGsiBEH//wNxIgUgBEcNBCAAIAY7AY4BAkAgBQ0AQQAhBAwECwJAA0AgAkGgAWogABCug4CAACACLwGkASIEDQEg
BUF/aiIFDQALQQAhBAwECyAAIAM2AmQMAwsgAkGCA2pCADcBACACQdgCakEgaiACQegSakEgaigCADYCACACQdgCakEYaiACQegSakEYaikCADcDACACQdgC
akEQaiACQegSakEQaikCADcDACACQdgCakEIaiACQegSakEIaikCADcDACACIAIpAugSNwPYAiACQgA3AvwCIAJB2AJqIQULIAQgBmshHCACQeABakEwaiAF
QTBqKAIANgIAIAJB4AFqQShqIAVBKGopAgA3AwAgAkHgAWpBIGogBUEgaikCADcDACACQeABakEYaiAFQRhqKQIANwMAIAJB4AFqQRBqIAVBEGopAgA3AwAg
AkHgAWpBCGogBUEIaikCADcDACACIAUpAgA3A+ABQQAhGwJAA0AgAkGwAWogAkHgAWoQxIOAgAAgAi0AvAFFDQEgAi8BtAEiGSACKAKwASIHLwGqAiIXTw0E
IAIvAbYBIAcvAagCIgVPDQQgBygCECIdIAcoAhhqIgQgHUkNBCAEQQdxDQQgBEUNBCAdIAQgGUEDdGooAgBqIgQgHUkNBCAEQQdxDQQgBEUNBANAAkAgBCkD
ACIIQgKDQgBSDQAgCEL8//8Dg1BFDQMLIARBCGohBCAFQX9qIgUNAAsgACgCdCIEQYCAgIAETw0EAkACQCAEDQBBACEFQarVqtV6IQQMAQsgACgCbCIEQQNx
DQUgACgCcCEFCwJAIAVFDQADQAJAIAQoAgAiHSgCACAHRw0AIB0vAQQgGUYNBAsgBEEEaiEEIAVBf2oiBQ0ACwsgF0F/aiIEQf//A3EiBSAERw0EIAcgBDsB
qgICQCAFDQAgACAHENSDgIAACyAbQQFqIhtB//8DcSAcQf//A3FJDQALIBwhGwsgACgCaCIEIBtB//8DcWsiBSAESw0CIAAgBjsBjgEgACAFNgJoQQAhBAwB
CyAAIAY7AY4BAkACQAJAIAAoAlQiBA0AQQAhBQwBC0EAIQUDQCAFIAQvAaoCaiIFIAZPDQIgBCgCDCIEDQALCyAGIAVrIQUCQANAIAJBqAFqIAAQroOAgAAg
Ai8BrAEiBA0BIAVBf2oiBUUNAgwACwsgACADNgJkDAELQQAhBCAALQCQAUEedEEedSIFQX9KDQACQCAFQQNxQX5qDgIAAgALIAAoAlgiBUUNASAAKAKAASEZ
IAAvAY4BIQcCQANAIAdB//8DcSAFLwGqAiIGTQ0BIAcgBmshByAFKAIIIgVFDQMMAAsLAkACQCAZKAIAIh0gBUYNAANAIAUoAgwiBUUNAyAFIB1HDQAMAgsL
IBkvAQQgBiAHa0H//wNxSQ0BC0EAIQQgAEEAOgCQAQsgAkGgE2okgICAgAAgBA8LAAuXjQEICn8Bfgt/AXwVfwF+An8DfiOAgICAAEGAGGsiAySAgICAAAJA
IAFB//8DcSAALwGMAUYNACAAIAE7AYwBQQEhBAJAAkACQCACLQAERQ0AIAIvAQAiBSABQf//A3FPDQAgAi8BAiEGIANByAdqIABBABC6g4CAACADLwHMByIH
QQFqIgJB//8DcSACRw0DIAMoAsgHIggvAaoCIgkgAkH//wNxayIKQf//A3EiAiAKRw0DAkACQCAGIAJLDQAgByAGaiICQf//AyACQf//A0kbIQcMAQsCQAJA
IAgoAgwiCEUNACAGIAJrIQIDQCACIAgvAaoCIglNDQIgAiAJayECIAgoAgwiCA0ACwsgCQ0CDAULIAJBf2oiByACSw0ECwJAAkAgAC8BjAENAEEAIQlBACEC
DAELIAAoAlgiAkUNBCAALwGOASEJAkADQCAJQf//A3EgAi8BqgIiCk0NASAJIAprIQkgAigCCCICDQAMBgsLIAogCWsiC0H//wNxQQFqIglB//8DcSIMIAlH
DQQgCiAMSQ0EQQAhCiADQQA6AMgHQQIhCQsgAyAJOgC8ASADQQA6ALgBIAMgCjYCrAEgA0EAOwGqASADIAs7AagBIAMgAjYCpAEgA0EBOgCgASADQQA2ApwB
IAMgBTsBmgEgAyAHOwGYASADIAg2ApQBIAMgCToAsAEgA0EAOgC0ASADQcABaiADQZQBahCgg4CAAAJAAkAgAy0AyAENACADQfoBakIANwEAIANB0AFqQSBq
IANBlAFqQSBqKAIANgIAIANB0AFqQRhqIANBlAFqQRhqKQIANwMAIANB0AFqQRBqIANBlAFqQRBqKQIANwMAIANB0AFqQQhqIANBlAFqQQhqKQIANwMAIAMg
AykClAE3A9ABIANCADcC9AEgA0HQAWohAgwBCyADQYgCakEgaiADQZQBakEgaigCADYCACADQYgCakEYaiADQZQBakEYaikCADcDACADQYgCakEQaiADQZQB
akEQaikCADcDACADQYgCakEIaiADQZQBakEIaikCADcDACADIAMpApQBNwOIAiADQQE6ALQCIAMgAykDwAEiDTcCrAIgDUIwiKdBf2oiAkH//wNxIAJHDQQg
AyACOwG4AiADQYgCaiECCyADQeAAakEwaiACQTBqKAIANgIAIANB4ABqQShqIAJBKGopAgA3AwAgA0HgAGpBIGogAkEgaikCADcDACADQeAAakEYaiACQRhq
KQIANwMAIANB4ABqQRBqIAJBEGopAgA3AwAgA0HgAGpBCGogAkEIaikCADcDACADIAIpAgA3A2AgA0HAAmogA0HgAGoQxIOAgAACQAJAIAMtAMwCDQBBACEO
DAELQQAhDgNAIAMvAcQCIgogAygCwAIiAi8BqgJPDQUgAy8BxgIgAi8BqAJPDQUgAigCECIJIAIoAhhqIgIgCUkNBSACQQdxDQUgAkUNBSAJIAIgCkEDdGoi
CigCAGoiAiAJSQ0FIAJBB3ENBSACRQ0FAkAgCi0ABEECcUUNACAOQQFqIg5FDQYLIANBwAJqIANB4ABqEMSDgIAAIAMtAMwCDQALCwJAAkAgACgCUCIPRQ0A
IAAgDygCADYCUAwBC0EZIQJBAA0DIABBwABqQQxBAiADEI+AgIAAIg9FDQMgD0Kq1arVqtWq1ap/NwAAIA9BCGpBqtWq1Xo2AAAgD0EDcQ0ECyAPQQA2Aggg
DyAFOwEGIA8gBzsBBCAPIAg2AgACQCAAQewAaiAAQRBqIA8Qk4CAgAAiAkH//wNxRQ0AIA9CqtWq1arVqtWqfzcCACAPQQhqQarVqtV6NgIAIA8gACgCUDYC
ACAAIA82AlAMAwsgAC8BjgEgBmsiAkH//wNxIAJHDQMgAkH//wNxQX9qIgJB//8DcSIQIAJHDQNBACEEDAELCyAAKAJUIgJFDQEgA0HQAmogAkGYAmogAUEB
EJKAgIAAAkACQCADLwHgAg0AIANB3gJqIQUgA0HcAmohASADQdgCaiEJIANB2gJqIQogA0HQAmohAgwBCyADQeQCakECciEFIAMgATsB7AIgAyACKQKYAjcD
8AIgAyACKAKkAjYC5AIgAyACLwGqAiIJIAIvAaICIgIgCSACSRs7AeoCIANB8AJqIQIgA0HkAmohASADQewCaiEJIANB6gJqIQoLAkACQAJAAkAgACgCKCII
RQ0AIAAgCCgCADYCKAwBC0EADQEgAEEYakGwAkEDIAMQj4CAgAAiCEUNAQJAQbACRQ0AIAhBqgFBsAL8CwALIAhBB3ENBAsCQEGwAkUNACAIQaoBQbAC/AsA
CyAJLwEAIgcgCi8BACIRbCILQYCAgIACTw0DIBFBA3QiEiALQQN0aiITIBJJDQMCQAJAIAEvAQAiCQ0AQQAhFEEAIRVBACEWQQAhF0EAIRgMAQtCAUEgIAlB
f2pnIglrrUL//wODhqciF7hEAAAAAAAA6j+iIhlEAAAAAAAA8L9lDQQgGUQAAAAAAADwQWYNBCAZ/AMiGK1CJH4iDUIgiKcNBCAXQQF0QQNqQfz///8HcSIW
IA2naiIVIBZJDQRBf0EAIAlrdEF/cyEUCyATIBVqIgYgE0kNAyACKAIAIgpBD2oiCSAKSQ0DIAlBgICAgAFPDQMgCUEEdkE/akEGdiIaQQN0IhsgCUEEdEGA
fnFqIgkgG0kNAyAGQQdqIgwgBkkNAyAMQXhxIhwgCWoiHSAcSQ0DAkACQCAKDQBBACEeDAELQgFBICAKQX9qQQR2Z2utQv//A4OGpyEeCyAdIB5BE2pBfHEi
HyAeQQJ0aiIgIB5BA3RqaiIKIB1JDQMgAigCBCIGQR9qIgkgBkkNAyAJQYCAgMAATw0DIAlBBXZBP2pBBnYiIUEDdCIiIAlBBXRBgHhxaiIJICJJDQMgCkEH
aiIGIApJDQMgBkF4cSIjIAlqIiQgI0kNAyAFLwEAIgpBHG4hBkEAISUgJCEJQQAhJkEAISdBACEoQQAhKUEAISoCQCAKQRxJDQBCAUEgIAZBf2pnIgprrUL/
/wODhqciKLhEAAAAAAAA6j+iIhlEAAAAAAAA8L9lDQQgGUQAAAAAAADwQWYNBCAZ/AMiKa1CHH4iDUIgiKcNBCAoQQF0QQNqQfz///8HcSInIA2naiImICdJ
DQQgJCAmaiIJICRJDQRBf0EAIAprdEF/cyElQgFBICAGQQR0QX9qZ2uthqchKgsgCUEDaiIKIAlJDQMgCkF8cSIrICpBAXQgKkETakF8cSIsICpBAnRqIi1q
QQNqQXxxaiIJICtJDQMgCUH//wNqIgogCUkNAyAAQSxqIQkgASgCACEuIAIpAgAhLwJAAkAgCkGAgHxxIjBBgYAkSQ0AQQANASAJKQIAIg2nIDBBEEEAIA1C
IIinKAIAEYCAgIAAgICAgAAiAUUNAQJAIDBFDQAgAUGqASAw/AsACyAwITEgAUH//wNxDQUMAwsCQAJAIAAoAjwiAUUNACAAIAEoAgA2AjwMAQtBAA0BIAlB
gIAkQRAgAxCPgICAACIBRQ0BAkBBgIAkRQ0AIAFBqgFBgIAk/AsACyABQf//A3ENBQtBgIAkITFBgIAkRQ0CIAFBqgFBgIAk/AsADAILAkBBsAJFDQAgCEGq
AUGwAvwLAAsgCCAAKAIoNgIAIAAgCDYCKAtBGSECIAQNASAAIA8Q5YCAgAAMAQsCQCAxRQ0AIAFBACAx/AsACyABIBJqIgIgAUkNASACQQdxDQEgAkUNASAB
QQdxDQECQAJAIBFFDQAgB0EDdCEMQQAhCSABIQogESEGA0AgCSALTw0CIAIgAWsiBSACSw0EIAogBa03AwAgAiAMaiECIAkgB2ohCSAKQQhqIQogBkF/aiIG
DQALCwJAIDBFDQAgAUH4/wNxDQMLIBMgFmoiBSATSQ0CIAEgE2oiAiABSQ0CIAJFDQICQCAXQQF0IglFDQAgAkEAIAn8CwALIAEgBWoiBiABSQ0CIAZFDQIC
QCAYRQ0AIAYhAgJAIBhBJGwiDEFcaiILQSRuIglBA3FBA0YNAEEAIAlBAWpBA3FrIQlBACgCvLnIgAAhCkEAKQK0uciAACENQQApAqy5yIAAITJBACkCpLnI
gAAhM0EAKQKcuciAACE0IAYhAgNAIAJBIGogCjYCACACQRhqIA03AgAgAkEQaiAyNwIAIAJBCGogMzcCACACIDQ3AgAgAkEkaiECIAlBAWoiCQ0ACwsgC0Hs
AEkNACAGIAxqIQpBACgCvLnIgAAhCUEAKQK0uciAACENQQApAqy5yIAAITJBACkCpLnIgAAhM0EAKQKcuciAACE0A0AgAkEgaiAJNgIAIAJBGGogDTcCACAC
QRBqIDI3AgAgAkEIaiAzNwIAIAIgNDcCACACQSRqIDQ3AgAgAkEsaiAzNwIAIAJBNGogMjcCACACQTxqIA03AgAgAkHEAGogCTYCACACQcgAaiA0NwIAIAJB
0ABqIDM3AgAgAkHYAGogMjcCACACQeAAaiANNwIAIAJB6ABqIAk2AgAgAkGMAWogCTYCACACQYQBaiANNwIAIAJB/ABqIDI3AgAgAkH0AGogMzcCACACQewA
aiA0NwIAIAJBkAFqIgIgCkcNAAsLAkBBwgBFDQAgA0H8AmpBAEHCAPwLAAsgASAjaiICIAFJDQIgAkUNAgJAICJFDQAgAkH/ASAi/AsACyAjICJqIgwgI0kN
AiABIBxqIgIgAUkNAiACRQ0CAkAgG0UNACACQf8BIBv8CwALIBwgG2oiGyAcSQ0CIAEgHWoiAkEQaiIJQQNxDQIgAkEANgIMIAIgHjYCCCACIB9BcGo2AgQg
AiAgQXBqNgIAAkAgHkUNACAJQQAgHvwLAAsgCSABayIeIAlLDQIgASAraiICQQA2AgwgAiAqNgIIIAIgLEFwajYCBCACIC1BcGo2AgAgAkEQaiECAkAgKkUN
ACACQQAgKvwLAAsgAiABayIqIAJLDQIgJCAnaiILICRJDQIgASAkaiICIAFJDQIgAkUNAgJAIChBAXQiCUUNACACQQAgCfwLAAsgASALaiIGIAFJDQIgBkUN
AgJAIClFDQAgBiECAkAgKUEcbCIiQWRqIh1BHG4iCUEDcUEDRg0AQQAgCUEBakEDcWshCUEAKALYuciAACEKQQApAtC5yIAAIQ1BACkCyLnIgAAhMkEAKQLA
uciAACEzIAYhAgNAIAJBGGogCjYCACACQRBqIA03AgAgAkEIaiAyNwIAIAIgMzcCACACQRxqIQIgCUEBaiIJDQALCyAdQdQASQ0AIAYgImohCkEAKALYuciA
ACEJQQApAtC5yIAAIQ1BACkCyLnIgAAhMkEAKQLAuciAACEzA0AgAkEYaiAJNgIAIAJBEGogDTcCACACQQhqIDI3AgAgAiAzNwIAIAJBHGogMzcCACACQSRq
IDI3AgAgAkEsaiANNwIAIAJBNGogCTYCACACQThqIDM3AgAgAkHAAGogMjcCACACQcgAaiANNwIAIAJB0ABqIAk2AgAgAkHUAGogMzcCACACQdwAaiAyNwIA
IAJB5ABqIA03AgAgAkHsAGogCTYCACACQfAAaiICIApHDQALCyAIIBQ7AVwgCCAVNgJYIAggFjYCVCAIQQA2AlAgCCAXNgJMIAggGDYCSCAIQQA2AkQgCCAF
NgJAIAggEzYCPCAIIB42AjggCCAbNgI0IAggGjYCMCAIIBw2AiwgCCAMNgIoIAggITYCJCAIICM2AiAgCCASNgIcIAhBADYCGCAIIDA2AhQgCCABNgIQIANB
ADoA+AICQEHCAEUNACAIQeAAaiADQfwCakHCAPwKAAALIAggJTsByAEgCCAmNgLEASAIICc2AsABIAhBADYCvAEgCCAoNgK4ASAIICk2ArQBIAhBADYCsAEg
CCALNgKsASAIICQ2AqgBIAggKjYCpAEgCEEBOwGiAQJAQcoARQ0AIAhBzAFqQQBBygD8CwALIAggETsBqgIgCCAHOwGoAiAIIC42AqQCIAggETsBogIgCCAH
OwGgAiAIIC83ApgCIAhBATsBlgIgCCADLQD4AjoArAIgCCAAKQMANwMAIAhBADsBqgIgCEIANwIIIAApAwBCAXwiDVANAiAAIA03AwAgACgCXCICIDFqIgkg
AkkNAiAAIAk2AlwgCEEBOwGqAiADQcADaiAAQYjmx4AAQbT6yYAAEPuDgIAAIAAgCDYCWCAAIAg2AlQgCCgCECIJIAgoAhhqIgIgCUkNAiACQQdxDQIgAkUN
AiADQQA6AJQEIAkgAigCAGoiCiAJSQ0CIApBB3ENAiAKRQ0CQQAhFiADQQA2AowEIANBADYChAQgAyAKNgKABCADIAI2AvwDIAMgCEEQajYC+AMgAyADLQCU
BDoAkAQgAyAINgL0AyADIAgvAaoCNgKIBCADQZgEaiADQcADahDEg4CAAAJAAkACQAJAIAMtAKQERQ0AQQApA+D6yYAAIjNCIIinIRUgA0HIF2pBCGohJkEA
LQCUusiAACEYQQAtAJi6yIAAIRdBAC0AnLrIgAAhIyAzpyErA0AgAy8BnAQiDCADKAKYBCIILwGqAk8NByADLwGeBEH//wNxIAgvAagCIh1PDQcgCCgCECIC
IAgoAhhqIgkgAkkNByAJQQdxDQcgCUUNByACIAkgDEEDdGoiEigCAGoiESACSQ0HIBFBB3ENByARRQ0HIB0hCgJAIBIpAwAiMkKAgICAEINCAFINACAdQQFq
IQogESAdQQN0akF4aiECAkADQCAKQQFGDQEgCkF/aiEKIAIpAwAhDSACQXhqIQIgDUL+//+DgIADg1ANAAwCCwsgMkKAgICAgAyDQgBSIQoLIAAoAnQiAkGA
gICABE8NBwJAAkAgAg0AQQAhCUGq1arVeiECDAELIAAoAmwiAkEDcQ0IIAAoAnAhCQsCQCAJRQ0AA0ACQCACKAIAIgEoAgAgCEcNACABLwEEIAxHDQACQCAB
LwEGIgYgCkH//wNxIgpJDQAgAygC+AMvAZgCQX9qIgVB//8DcSIHIAVHDQsgByADLwGMBGsiB0H//wNxIgUgB0cNCyABIAYgBSAGIAVJGyIGOwEGCyAGQQFq
IgZB//8DcSIBIAZHDQogCiABIAogAUsbIQoLIAJBBGohAiAJQX9qIgkNAAsLIAhBEGohKAJAAkACQCAKQf//A3EiGw0AIBItAARBAnENAiADKAKEBEEBaiIC
RQ0KDAELIANBzAZqIAhBmAJqIgIgAygC+AMvAZgCIglBARCSgICAAAJAAkAgAy8B3AYNACADQcwGaiEnDAELIANB4AZqQQhqIgogAkEIaikCADcDACAKIAk7
AQAgAyACKQIANwPgBiADIAgvAaoCIgJB1wEgAkHXAUkbOwHqBiADQeAGaiEnCwJAAkACQCADKAKEBCICRQ0AIAMoAogEIQkDQCAJQQFqIglFDQ0gAygC+AMi
Ci8BkgIiBUF/aiIBQf//A3EiBiABRw0NAkACQCADLwGOBCIBIAZHDQAgA0H0A2ogACAnEPyDgIAAIgJB//8DcQ0EIAMoAoQEIQoMAQsgCi8BmgIiBkF/aiIH
Qf//A3EiCyAHRw0OIAEgC0cNDiAGIAVPDQ4gCiAGQQFqOwGaAiADIAMoAvwDIgZBCGo2AvwDIAooAgAiBSAGKAIIaiIKIAVJDQ4gCkEHcQ0OIApFDQ4gA0EA
OgCQBCADIAo2AoAEIANBADsBjAQgAyABQQFqOwGOBCACIQoLIAMgCTYCiAQgCkF/aiICIApLDQ0gAyACNgKEBCACDQALCyADKAL8AyICIAIpAwBC//////9z
gyASKQMAQoCAgICADIOENwMAQQAhKgNAAkAgAy0AkARFDQAgAygC/AMiAiACKQMAQoCAgIAQhDcDACADKAKIBEEBaiIBRQ0NIAMoAvgDIgkvAZICIgVBf2oi
AkH//wNxIgogAkcNDQJAAkACQCADLwGOBCIGIApHDQAgA0H0A2ogACAnEPyDgIAAIgJB//8DcQ0CIAMoAvwDIQIMAQsgCS8BmgIiAkF/aiIKQf//A3EiByAK
Rw0PIAYgB0cNDyACIAVPDQ8gCSACQQFqOwGaAiADIAMoAvwDIgpBCGoiAjYC/AMgCSgCACIFIAooAghqIgkgBUkNDyAJQQdxDQ8gCUUNDyADQQA6AJAEIAMg
CTYCgAQgA0EAOwGMBCADIAZBAWo7AY4ECyADIAE2AogEIAIgAikDAEL//////3ODIBIpAwBCgICAgIAMg4Q3AwAgAygC/AMiAiACKQMAQoCAgIAghDcDAAwB
CyADIAE2AogEDAMLIAAoAnQiAkGAgICABE8NDAJAAkAgAg0AQQAhCUGq1arVeiECDAELIAAoAmwiAkEDcQ0NIAAoAnAhCQsCQCAJRQ0AA0ACQCACKAIAIgoo
AgAgCEcNACAKLwEEIAxHDQAgKiAKLwEGRw0AIAogAygC9AM2AgAgCiADLwGMBDsBBiAKIAMvAY4EOwEECyACQQRqIQIgCUF/aiIJDQALCwJAICogHUkNACAq
IB0QiICAgAAACwJAAkACQAJAAkACQAJAAkAgESAqQQN0aiITKQMAIg1CAoNCAFINACAjIQIgDUIqiKdBA3EOBAMCAQYDCyADKAKABCANNwMAIAMoAvgDLwGY
AkF/aiICQf//A3EiCSACRw0TIAMvAYwEIgIgCUYNAyADIAMoAoAEQQhqNgKABCACQQFqIgJB//8DcSACRw0TIAMgAjsBjAQMBAsgAygC+AMvAZgCQQFNDQMM
AQsCQCADKAL4Ay8BmAIiAkEBSw0AIAMoAoAEIgIgAikDAEKDgIB8gzcDACADKAKABCICIAIpAwBC////////fIM3AwAgAygC+AMvAZgCQX9qIgJB//8DcSIJ
IAJHDRICQCADLwGMBCICIAlHDQAgA0EBOgCQBCAXIQIMBQsgAyADKAKABEEIajYCgAQgAkEBaiICQf//A3EgAkcNEiADIAI7AYwEIBchAgwECyADLwGMBCAC
QX9qQf//A3FHDQAgAygCgAQiAiACKQMAQoCAgJyAgECDQoCAgICAgAOENwMAIAJBADsBBiADKAL4Ay8BmAJBf2oiAkH//wNxIgkgAkcNEQJAIAMvAYwEIgIg
CUcNACADQQE6AJAEIBghAgwECyADIAMoAoAEQQhqNgKABCACQQFqIgJB//8DcSACRw0RIAMgAjsBjAQgGCECDAMLIAMoAoAEIA03AwAgAygCgAQiAiACKQMA
QnyDNwMAIAMoAoAEIgIgAikDAEL///////93gzcDACADKAKABCICIAIpAwBC////n4CAf4M3AwACQAJAAkACQCATKQMAIg1CA4NCAVINACATICgoAgAiImsi
CyATSw0UICIgCCgCOGoiCiAiSQ0UIApFDRQgCkEDcQ0UAkACQCAKQXxqKAIARQ0AIApBeGooAgAiAkF/aiIFIAJLDRYgA0HQAGogC61CgYCAgBB+Ig1C29HQ
hZra34FnhUIAIA1CrJri06PS8PofhUIAEK+FgIAAIANBwABqIAMpA1hC29HQhZra34FnhUIAIAMpA1BCq8j1xcesh7ugf4VCABCvhYCAACAKIAUgAykDSCAD
KQNAhSINp3EiAWotAAAiCUUNACAKQXBqISkgDUI5iKchByAKQXRqIR4gBSEGAkADQCAGIQICQCAJwEF/Sg0AIAcgCUH/AHFHDQAgCiAeKAIAaiIJIApJDRkg
CUEDcQ0ZIAlFDRkgCyAJIAFBAnRqKAIARg0CCyABQQFqIglFDRhBACEcAkAgCiAJIAVxIgFqLQAAIgkNAEEAISQMBAsgAkF/aiEGQQAhJCACDQAMAwsLIAog
KSgCAGoiAiAKSQ0WIAJBA3ENFiACRQ0WICIgAiABQQN0aikCACINp2oiHCAiSQ0WIBxBA3ENFiAcRQ0WIA1CIIinISQMAQtBACEcQQAhJAsgHEUNFCADKAL4
AyICKAIAIgkgAigCKGoiAiAJSQ0UIAJFDRQgAkEDcQ0UAkAgAkF8aigCACACQXhqKAIASQ0AIANB9ANqIABB7N7IgAAQ/YOAgAAiAkH//wNxDQILICRFDRQg
JEECdCIiQX9qQQR2QQFqQQAgIhshHiADKAL4AyEKICRB/////wNLIQcCQAJAAkADQCAKKAIAIQIgAyAHOgDsFwJAIAcNACACIAooAhxqIgkgAkkNGSAJQQdx
DRkgCUUNGSADQfAXaiAJIAooAiAgHhCkg4CAACADLQD0F0UNACACIAooAiRqIgkgAkkNGSAJRQ0ZIAMoAvAXIgVB/////wBLDRkgCUEDcQ0ZICRBgICAgARP
DRYgIkEPaiIGICJJDRYgAygC+AMiASgCACIKIAEoAiRqIgIgCkkNFiACRQ0WIAkgBUEEdGoiBSACayIJIAVLDRYgCUEPcQ0WIAogASgCHGoiAiAKSQ0WIAJB
B3ENFiACRQ0WIAZBD00NFiACIAlBCnYiB0EDdGoiBSAFKQMAQn9BwAAgBkEEdiIKQcAAIAlBBHZBP3EiCWsiASAKIAFJGyIBa62IIAmthoQ3AwAgCiABayIJ
IApLDRYCQAJAIAlBwQBPDQAgB0EBaiEGDAELAkAgCUG/f2oiCUEDdkH4////AXFBCGoiBkUNACAFQQhqQf8BIAb8CwALIAcgCUEGdmpBAmohBiAKIAEgCUFA
cWprQUBqIQkLAkAgCUUNACACIAZBA3RqIgIgAikDAEJ/QQAgCWtBP3GtiIQ3AwALIAMoAoAEIgopAwAiDUICg0IAUg0WIA1C/P//A4NQDRYgDUIBg1BFDRYg
CiADKAL4AyIJKAIAIgJrIgUgCksNFiACIAkoAihqIgEgAkkNFiABRQ0WIAMoAvwDIQYgAyABNgLwBkEADQMgAiAJQRxqIgcoAgBqIgEgAkkNFiABQQdxDRYg
AUUNFiADQfAXaiABIAkoAiAgHhCkg4CAACADLQD0F0UNAyACIAkoAiRqIgEgAkkNFiABRQ0WIAMoAvAXIgJB/////wBLDRYgASACQQR0aiICQQNxDRYCQCAC
IBwgImpPDQAgHCACICJqSQ0XCwJAICJFDQAgAiAcICL8CgAACyACIAkoAgBrIgEgAksNFiADQfAGaiAFICStQiCGIAGthBClg4CAAEH//wNxRQ0CIAcgCSgC
ACACICQQooOAgABBIyECDAQLIAMvAYwEIQYgAy8BjgQhASADKAKIBCELIANB8BdqIAAgAygC9ANB7N7IgAAQ6IOAgAAgAy8B9BciAg0FIAMoAvAXIgIoAhAi
CiACKAIYaiIJIApJDRUgCUEHcQ0VIAlFDRUgA0EAOgDsFyAKIAkoAgBqIgUgCkkNFSAFQQdxDRUgBUUNFSADQQA2AowEIANBADYChAQgAyAFNgKABCADIAJB
EGoiCjYC+AMgAyADLQDsFzoAkAQgAyACNgL0AyADIAIvAaoCIgU2AogEIAYgAi8BqAJPDRUgASAFTw0VIAMgCSABQQN0aiICNgL8AyAKKAIAIgkgAigCAGoi
AiAJSQ0VIAJBB3ENFSACRQ0VIANBADoAkAQgAyABOwGOBCADIAY7AYwEIAMgCzYCiAQgAyACIAZBA3RqNgKABAwACwsgCiAKKQMAQnyDQgGENwMAIAYgBikD
AEKAgICAwACENwMAIBMpAwAhDQwCC0EkIQILIAMgAjsB9gYgA0H2BmoQ/oOAgAAACwJAIA1CgICAgICACINQDQAgEyAoKAIAIhxrIgsgE0sNESAcIAgoAqQB
aiIBIBxJDREgAUUNESABQQNxDREgAUF8aigCAEUNESABQXhqKAIAIgJBf2oiBSACSw0RIANBMGogC61CgYCAgBB+Ig1C29HQhZra34FnhUIAIA1CrJri06PS
8PofhUIAEK+FgIAAIANBIGogAykDOELb0dCFmtrfgWeFQgAgAykDMEKryPXFx6yHu6B/hUIAEK+FgIAAIAEgBSADKQMoIAMpAyCFIg2ncSIKai0AACIJRQ0R
IAFBcGohJCANQjmIpyEHIAFBdGohHiAFIQYCQANAIAYhAgJAIAnAQX9KDQAgByAJQf8AcUcNACABIB4oAgBqIgkgAUkNFCAJQQNxDRQgCUUNFCALIAkgCkEC
dGooAgBGDQILIApBAWoiCUUNEyABIAkgBXEiCmotAAAiCUUNEyACQX9qIQYgAg0ADBMLCyABICQoAgBqIgIgAUkNESACQQFxDREgAkUNESACIApBAXRqLwEA
IiRFDREgCCgCtAEgJE0NESAcIAgoAqwBaiICIBxJDREgAkEDcQ0RIAJFDREgAiAkQRxsaiIeLwEYRQ0RIAMoAvgDIgIoAgAiCSACKAKUAWoiAiAJSQ0RIAJF
DREgAkEDcQ0RAkAgAkF8aigCACACQXhqKAIASQ0AIANB9ANqIABB7t7IgAAQ/YOAgAAiAkH//wNxDQILIB4oAhAiAkEAIB4oAgQgHi0ACEEBcRtqIhwgAkkN
ESAcRQ0RIBxBf2pBBXZBAWohCyADKAL4AyEGAkACQAJAA0ACQEEADQAgBigCACIJIAYoAhBqIgIgCUkNFiACQQdxDRYgAkUNFiADQfAXaiACIAYoAhQgCxCk
g4CAACADLQD0F0UNACAJIAYoAhhqIgIgCUkNFiACRQ0WIAMoAvAXIgFB////P0sNFiAcQR9qIgUgHEkNFiADKAL4AyIGKAIAIgkgBigCGGoiCiAJSQ0WIApF
DRYgAiABQQV0aiICIAprIgEgAksNFiABQR9xDRYgCSAGKAIQaiICIAlJDRYgAkEHcQ0WIAJFDRYgBUEfTQ0WIAIgAUELdiIHQQN0aiIGIAYpAwBCf0HAACAF
QQV2IgpBwAAgAUEFdkE/cSIJayIBIAogAUkbIgFrrYggCa2GhDcDACAKIAFrIgkgCksNFgJAAkAgCUHBAE8NACAHQQFqIQYMAQsCQCAJQb9/aiIJQQN2Qfj/
//8BcUEIaiIFRQ0AIAZBCGpB/wEgBfwLAAsgByAJQQZ2akECaiEGIAogASAJQUBxamtBQGohCQsCQCAJRQ0AIAIgBkEDdGoiAiACKQMAQn9BACAJa0E/ca2I
hDcDAAsgA0H4BmogHiAoIAMoAvgDEKmDgIAAIAMvAYwHIgINAiADQQA2ApgHIAMgAygC+AMiAjYClAcgAygCiAchASADKAKEByEKIAMtAIAHIQkgAygC/AYh
CyADKAL4BiEHIANBnAdqIAJBmAFqIAIoAgAgA0H4BmogJCADQZQHahCqg4CAACADLwGgByIGDQMgA0GcB2ohAgwECyADLwGMBCEBIAMvAY4EIQogAygCiAQh
ByADQfAXaiAAIAMoAvQDQfDeyIAAEOiDgIAAIAMvAfQXIgINBSADKALwFyICKAIQIgYgAigCGGoiCSAGSQ0VIAlBB3ENFSAJRQ0VIANBADoA7BcgBiAJKAIA
aiIFIAZJDRUgBUEHcQ0VIAVFDRUgA0EANgKMBCADQQA2AoQEIAMgBTYCgAQgAyACQRBqIgY2AvgDIAMgAy0A7Bc6AJAEIAMgAjYC9AMgAyACLwGqAiIFNgKI
BCABIAIvAagCTw0VIAogBU8NFSADIAkgCkEDdGoiAjYC/AMgBigCACIJIAIoAgBqIgIgCUkNFSACQQdxDRUgAkUNFSADQQA6AJAEIAMgCjsBjgQgAyABOwGM
BCADIAc2AogEIAMgAiABQQN0ajYCgAQMAAsLIAMgAjsBkgcgA0GSB2oQ/4OAgAAACyADKAL4AyICQRBqIQUCQCAJQQFxDQAgAigCACIJIAdqIgcgCUkNEyAH
RQ0TIAUgCSAHIAsQ8IOAgAALIAIoAgAiAiAKaiIJIAJJDRIgCUUNEiAFIAIgCSABEPCDgIAAQbD5yYAAIQICQAJAIAZBZ2oODgAUFBQUFBQUFBQUFBQBFAtB
7t7IgAAhAgsgA0H0A2ogACACEP2DgIAAIgJB//8DcQ0CIANByBdqIB4gKCADKAL4AxCpg4CAAAJAAkAgAy8B3BciAg0AIANBADYC6BcgAyADKAL4AyICNgLk
FyADQaQHaiACQZgBaiACKAIAIANByBdqICQgA0HkF2oQqoOAgAAgAy8BqAciAg0BIANBpAdqIQIMAgsgAyACOwGiByADQaIHahD/g4CAAAALIAMgAjsBqgcg
A0GqB2oQgISAgAAACwJAIAItAAJFDQAgAi8BACEkCyADKAKABCIiIAMoAvgDIhwoAgAiAmsiCyAiSw0RIAIgHCgClAFqIgEgAkkNESABRQ0RIAMoAvwDISkg
AyABNgKsByABQQNxDREgAUF4aigCACICIAFBfGooAgAiCUkNEQJAAkACQAJAAkAgAiAJRg0AIANB8BdqIANBrAdqIAsQsoKAgAAgAygC9BchAiADLQD4F0EB
cQ0CIAMoAvAXIQkMAQsCQAJAIAJFDQAgA0EQaiALrUKBgICAEH4iDULb0dCFmtrfgWeFQgAgDUKsmuLTo9Lw+h+FQgAQr4WAgAAgAyADKQMYQtvR0IWa2t+B
Z4VCACADKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgASACQX9qIgUgAykDCCADKQMAhSINp3EiCmotAAAiCUUNACABQXBqITAgDUI5iKchByABQXRqIR4gBSEG
A0AgBiECAkAgCcBBf0oNACAHIAlB/wBxRw0AIAEgHigCAGoiCSABSQ0ZIAlBA3ENGSAJRQ0ZIAsgCSAKQQJ0aiIJKAIARg0DCyAKQQFqIglFDRggASAJIAVx
IgpqLQAAIglFDQEgAkF/aiEGIAINAAsLIANBIjsBsAcgA0GwB2oQgYSAgAAACyABIDAoAgBqIgIgAUkNFSACQQFxDRUgAkUNFSADQQE6AOwXIAIgCkEBdGoh
AkEBDQELIAkgCzYCAAwBCyACLwEAIgpFDRMgHCgCpAEgCk0NEyAcKAIAIgEgHCgCnAFqIgkgAUkNEyAJQQNxDRMgCUUNEyAJIApBHGxqIgkvARgiCkUNEyAJ
QRhqIApBf2oiCTsBAAJAIAlB//8DcQ0AIBwoAqABIglBf2oiCiAJSw0UIBwgCjYCoAELIAIvAQAgJEH//wNxRw0AICktAAQhAkKAgICAgIAIIQ0gIiEpIAJB
EHFFDRMMAQsgAiAkOwEAICIgIikDAEKAgICAgIAIhDcDAEKAgICAgAIhDQsgKSApKQMAIA2ENwMAIBMpAwAhDQsgDUIaiKciCkH//wNxIglFDQIgCCgCSCAJ
TQ0QIAgoAhAiASAIKAJAaiICIAFJDRAgAkEDcQ0QIAJFDRAgAiAJQSRsaiICLwEgRQ0QIANByAdqQRhqIAJBGGooAgA2AgAgA0HIB2pBEGogAkEQaikCADcD
ACADQcgHakEIaiACQQhqKQIANwMAIAMgAikCADcDyAcgA0GyB2ogAygC+AMiAkEsaiACKAIAIANByAdqIAoQq4OAgABBsPnJgAAhAgJAAkACQCADLwG2ByIJ
QWdqDg4BExMTExMTExMTExMTAgALIAkNEiADQbIHaiECDAMLQfLeyIAAIQILIANB9ANqIAAgAhD9g4CAACICQf//A3ENACADQbgHaiADKAL4AyICQSxqIAIo
AgAgA0HIB2ogEykDAEIaiKcQq4OAgAACQCADLwG8ByICDQAgA0G4B2ohAgwCCyADIAI7Ab4HIANBvgdqEIKEgIAAAAsCQCACQf//A3EiCUEcRg0AIAlBGUYN
CQwQCwJAIAMvAY4EIgJFDQAgAygC+AMiCS8BmgJBf2oiCkH//wNxIgEgCkcNDiACIAFHDQ4gAy0AkAQNDiADKAKIBCEpIAMvAYwEIQogAygC/AMhHiADKAL0
AyEcAkACQCADQfQDaiAAICcQ/IOAgAAiAkH//wNxDQAgAygC9AMgHEYNECADLwGOBA0QIAogAygC+AMiAi8BmAIiBU8NECACLwGaAkUNECACKAIAIgYgAygC
/AMiBygCAGoiASAGSQ0QIAFBB3ENECABRQ0QIANBADoAkAQgA0EAOwGOBCADIAo7AYwEIAMgASAKQQN0ajYCgAQCQAJAIAIgCSAHIB5BACAFEJeDgIAAIgJB
//8DcQ0AIAAoAnQiAkGAgICABE8NEgJAAkAgAg0AQQAhCkGq1arVeiECDAELIAAoAmwiAkEDcQ0TIAAoAnAhCgsgCg0BDAMLIAMgAjsB5BcgA0HkF2oQg4SA
gAAACwNAAkAgAigCACIBKAIAQRBqIAlHDQAgCS8BmgJBf2oiBkH//wNxIgUgBkcNEiABLwEEIAVHDQAgASADKAL0AzYCACABIAMvAY4EOwEECyACQQRqIQIg
CkF/aiIKRQ0CDAALCyADICk2AogEDAoLIAkoAgAiAiAeKAIAaiIiIAJJDRAgIkEHcQ0OICJFDQ4gCS8BmAIhEwJAIB4pAwAiDUKAgICAwACDUA0AAkACQCAT
RQ0AIAlBHGohJCAiIQogIiECIBMhBgNAAkAgAikDAEIDg0IBUg0AIAogCSgCACIBayIHIApLDRMgASAJKAIoaiIFIAFJDRMgBUUNEyADIAU2AvAXIANByBdq
IAUgBxCtgoCAACADLQDQF0UNEyABIAMoAswXIgcoAgBqIgUgAUkNEyAFQQNxDRMgBUUNEyADKALIFyELICQgASAFIAcoAgQQooOAgAAgA0HwF2ogCxCugoCA
ACACIAIpAwBCfIM3AwALIApBCGohCiACQQhqIQIgBkF/aiIGDQALIBMgCS8BmAIiCkcNASAeKQMAIQ0LIB4gDUL/////v3+DIg03AwAMAQsgCSgCACIBIB4o
AgBqIgIgAUkNDyACQQdxDQ8gAkUNDwJAAkAgCkUNAANAIAIpAwBCA4NCAVENAiACQQhqIQIgCkF/aiIKDQALCyAeIB4pAwBC/////79/gyINNwMADAELIB4p
AwAhDQsCQCANQoCAgICAAoNQDQACQCATRQ0AICIhCiAiIQIgEyEGA0ACQCACLQAFQSBxRQ0AIAogCSgCACIBayIHIApLDRIgASAJKAKUAWoiBSABSQ0SIAVF
DRIgAyAFNgL4BiADQcgHaiAFIAcQsIKAgAAgAy0A0AdFDQAgAygCzAcvAQAiB0UNEiAJKAKkASAHTQ0SIAEgCSgCnAFqIgUgAUkNEiAFQQNxDRIgBUUNEiAF
IAdBHGxqIgEvARgiBUUNEiABQRhqIAVBf2oiATsBAAJAIAFB//8DcQ0AIAkoAqABIgFBf2oiBSABSw0TIAkgBTYCoAELIANB+AZqIAMoAsgHELGCgIAAIAIg
AikDAEL///////93gzcDAAsgCkEIaiEKIAJBCGohAiAGQX9qIgYNAAsLAkAgEyAJLwGYAiIKRw0AIB4gHikDAEL//////32DIg03AwAMAQsgCSgCACIBIB4o
AgBqIgIgAUkNDyACQQdxDQ8gAkUNDwJAAkAgCkUNAANAIAItAAVBIHENAiACQQhqIQIgCkF/aiIKDQALCyAeIB4pAwBC//////99gyINNwMADAELIB4pAwAh
DQsCQCANQoCAgICAAYNQDQACQCATRQ0AICIhAiATIQEDQAJAIAIpAwAiDUKAgIDg//8Ag1ANACAJKAI4IA1CGoinQf//A3EiBU0NEiAJKAIAIgYgCSgCMGoi
CiAGSQ0SIApBA3ENEiAKRQ0SIAogBUEkbGoiCi8BICIGRQ0SIApBIGogBkF/aiIKOwEAIApB//8DcQ0AIAkoAjQiCkF/aiIGIApLDRIgCSAGNgI0CyACQQhq
IQIgAUF/aiIBDQALCwJAIBMgCS8BmAIiCkYNACAJKAIAIgEgHigCAGoiAiABSQ0QIAJBB3ENECACRQ0QIApFDQADQCACKAECQYD4/x9xDQIgAkEIaiECIApB
f2oiCg0ACwsgHiAeKQMAQv//////foM3AwALAkAgE0EDdCICRQ0AICJBACAC/AsACyAJLwGaAkF/aiICQf//A3EiCiACRw0QIAkgAjsBmgICQCAKDQAgHCgC
DCECAkACQCAcKAIIIglFDQAgCSACNgIMDAELIAAgAjYCVAsgHCgCCCEJAkACQCACRQ0AIAIgCTYCCAwBCyAAIAk2AlgLIAAoAlwiAiAcKAIUayIJIAJLDQ8g
ACAJNgJcAkACQCAcKAIUIgJBgIAkSw0AAkAgAkUNACAcKAIQQQAgAvwLAAsgHCgCECECAkBBgIAkRQ0AIAJBqgFBgIAk/AsACyACIAAoAjw2AgAgACACNgI8
DAELIAAoAiwhCiAAKAIwIQEgHCgCECEJAkAgAkUNACAJQaoBIAL8CwALIAogCSACQRBBACABKAIMEYGAgIAAgICAgAALAkBBsAJFDQAgHEGqAUGwAvwLAAsg
HCAAKAIoNgIAIAAgHDYCKAsgAyApNgKIBAwGCwJAQYAQRQ0AIANByAdqQaoBQYAQ/AsACyADQoAQNwLQFyADQbjewYAANgLIFyADIANByAdqNgLMF0EAIQIC
QAJAA0AgAygC1BciAUHVACACayIJaiIKIAFJDRIgAkGD2smAAGohBgJAAkAgCiADKALQFyIFSw0AAkAgCiAFSw0AAkAgAygCzBcgAWoiCkHY2smAAE8NACAG
IAogCWpJDRYLAkAgCUUNACAKIAYgCfwKAAALIAMoAtQXIgogCWoiASAKSQ0TIAMgATYC1BcMAgsgCiAFEIiAgIAAAAsgAygCyBcoAgAhCiADIAk2AvQXIAMg
BjYC8BcgA0H4BmogA0HIF2ogA0HwF2pBAUEBIAoRgYCAgACAgICAACADKQP4BiINpyEJAkAgDUIgiKdB//8DcQ4DARQAFAtBAA0CICtB1QBBAEEAIBUoAgAR
gICAgACAgICAACICRQ0CAkBB1QBFDQAgAkGqAUHVAPwLAAsgA0LVADcD2BcgAyACNgLUFyADQdyVx4AANgLQFyADIDM3A8gXQQAhAgNAIAMoAtwXIgFB1QAg
AmsiCWoiCiABSQ0SIAJBg9rJgABqIQYCQAJAIAogAygC2BciBUsNAAJAIAogBUsNAAJAIAMoAtQXIAFqIgpB2NrJgABPDQAgBiAKIAlqSQ0WCwJAIAlFDQAg
CiAGIAn8CgAACyADKALcFyIKIAlqIgEgCkkNFSADIAE2AtwXDAILIAogBRCIgICAAAALIAMoAtAXKAIAIQogAyAJNgLoFyADIAY2AuQXIANB+AZqICYgA0Hk
F2pBAUEBIAoRgYCAgACAgICAACADKQP4BiINpyEJAkAgDUIgiKdB//8DcQ4DARQAFAsgAygC2BciAkUNBCADKALIFyEKIAMoAswXIQEgAygC1BchCQJAIAJF
DQAgCUGqASAC/AsACyAKIAkgAkEAQQAgASgCDBGBgICAAICAgIAADAQLIAIgCWoiCSACSQ0SIAkhAiAJQdUASQ0ACwJAIAMoAtwXIgIgAygC2BciCUsNACAD
QgA3A9gXIAMoAtQXIQogA0Gq1arVejYC1BcgAyAJNgKAByADIAI2AvwGIAMgCjYC+AYgA0HwF2ogA0H4BmogA0HIF2oQuICAgAAgAyADKAKAByICNgLYFyAD
IAMoAvgGIgk2AtQXIAMgAygC/AY2AtwXAkAgAkUNACADKALIFyEKIAMoAswXIQECQCACRQ0AIAlBqgEgAvwLAAsgCiAJIAJBAEEAIAEoAgwRgYCAgACAgICA
AAsgAy8B+BcNAyADKALwFyIJIAMoAvQXIgIQgICAgAAgAkUNAwJAIAJFDQAgCUGqASAC/AsACyADIAkgAkEAIAMQuYCAgAAMAwsgAiAJEIiAgIAADBELIAIg
CWoiCSACSQ0QIAkhAiAJQdUASQ0ACyADKALUFyICIAMoAtAXIglLDQEgAygCzBcgAhCAgICAAAsgAygC+AMvAZgCQX9qIgJB//8DcSIJIAJHDQ4gKkEBaiEq
AkAgAy8BjAQiAiAJRw0AIANBAToAkAQMBwsgAyADKAKABEEIajYCgAQgAkEBaiICQf//A3EgAkcNDiADIAI7AYwEDAYLIAIgCRCIgICAAAALAkACQCACLQAC
RQ0AIAIvAQAhAgwBCyATKQMAQhqIpyECCyADKAL8AyIJIAkpAwBCgICAgIABhDcDACADKAKABCIJIAkpAwBC////n4CAf4MgAq1C//8Dg0IahoQ3AwALIAMo
AvgDLwGYAkF/aiICQf//A3EiCSACRw0NIAMvAYwEIgIgCUYNACADIAMoAoAEQQhqNgKABCACQQFqIgJB//8DcSACRw0NIAMgAjsBjAQMAQsgA0EBOgCQBAsg
IyECCwJAAkAgAkEDcQ4EAAIBDAALICpBAWohKgwBCyAAKAJ0IgJBgICAgARPDQoCQAJAIAINAEEAIQlBqtWq1XohAgwBCyAAKAJsIgJBA3ENCyAAKAJwIQkL
AkAgCUUNACAqQQFqIQEDQAJAIAIoAgAiCigCACAIRw0AIAovAQQgDEcNACABIAovAQZHDQAgCiADKAL0AzYCACAKIAMvAYwEOwEGIAogAy8BjgQ7AQQLIAJB
BGohAiAJQX9qIgkNAAsLICpBAmohKgsgKiAbSQ0ACyASLQAEQQFxDQMgAygChARBAWoiAg0CDAgLIAMgCTYCiAQLAkAgAy0A7ANFDQAgAygC5AMiCUUNAANA
IAAoAlwiCiAJKAIUayIBIApLDQYgCSgCDCEIIAAgATYCXAJAAkAgCSgCFCIKQYCAJEsNAAJAIApFDQAgCSgCEEEAIAr8CwALIAkoAhAhCgJAQYCAJEUNACAK
QaoBQYCAJPwLAAsgCiAAKAI8NgIAIAAgCjYCPAwBCyAAKAIsIQYgACgCMCEFIAkoAhAhAQJAIApFDQAgAUGqASAK/AsACyAGIAEgCkEQQQAgBSgCDBGBgICA
AICAgIAACwJAQbACRQ0AIAlBqgFBsAL8CwALIAkgACgCKDYCACAAIAk2AiggCCEJIAgNAAsLIAQNCCAAIA8Q5YCAgAAMCAsgAyACNgKEBAsgCC8BqgJBf2oi
AkH//wNxIgkgAkcNBAJAIAwgCUcNACAAKAJcIgIgCCgCFGsiCSACSw0DIAAgCTYCXAJAAkAgCCgCFCICQYCAJEsNAAJAIAJFDQAgKCgCAEEAIAL8CwALICgo
AgAhAgJAQYCAJEUNACACQaoBQYCAJPwLAAsgAiAAKAI8NgIAIAAgAjYCPAwBCyAAKAIsIQogACgCMCEBICgoAgAhCQJAIAJFDQAgCUGqASAC/AsACyAKIAkg
AkEQQQAgASgCDBGBgICAAICAgIAACwJAQbACRQ0AIAhBqgFBsAL8CwALIAggACgCKDYCACAAIAg2AigLIANBmARqIANBwANqEMSDgIAAIAMtAKQEDQALCyAA
IAMoAogENgJoIAAvAY4BIQoCQAJAIAAoAlQiAkUNAEEAIRYDQCAWIAIvAaoCaiIWIApPDQIgAigCDCICDQALCyAKIBZrIgkgCksNASAJRQ0AAkADQCADQagE
aiAAEK6DgIAAIAMvAawEIgINASAJQX9qIglFDQIMAAsLAkAgAy0A7ANFDQAgAygC5AMiCUUNAANAIAAoAlwiCiAJKAIUayIIIApLDQMgCSgCDCEKIAAgCDYC
XAJAAkAgCSgCFCIIQYCAJEsNAAJAIAhFDQAgCSgCEEEAIAj8CwALIAkoAhAhCAJAQYCAJEUNACAIQaoBQYCAJPwLAAsgCCAAKAI8NgIAIAAgCDYCPAwBCyAA
KAIsIQYgACgCMCEFIAkoAhAhAQJAIAhFDQAgAUGqASAI/AsACyAGIAEgCEEQQQAgBSgCDBGBgICAAICAgIAACwJAQbACRQ0AIAlBqgFBsAL8CwALIAkgACgC
KDYCACAAIAk2AiggCiEJIAoNAAsLIAQNBSAAIA8Q5YCAgAAMBQtBACECIAQNBCAAKAJYIghFDQIgCCECIAAvAY4BIgEhCQJAA0AgCUH//wNxIAIvAaoCIgpN
DQEgCSAKayEJIAIoAggiAkUNBAwACwsgCiAJayEJAkACQCAPKAIAIgYgAkcNACAPLwEEIgIgCUH//wNxSQ0DIAIgCWtB//8DcSEGDAELIAogCUH//wNxayIJ
Qf//A3EgCUcNAyACKAIMIgJFDQIgCUH//wNxIQkDQAJAIAIgBkcNACAJIA8vAQRqIgYgCUkNBQwCCyAJIAIvAaoCaiIKIAlJDQQgCiEJIAIoAgwiAg0ADAML
CwJAAkAgAC8BjAENAEEAIQJBACEIDAELAkADQCABQf//A3EgCC8BqgIiAk0NASABIAJrIQEgCCgCCCIIRQ0FDAALCyACIAFrIglB//8DcUEBaiIKQf//A3Ei
ASAKRw0BIAIgAUkNAUEAIQEgA0EAOgDIB0ECIQILIA8pAgAhDSADQegEakEIaiAPQQhqKAIAIgU2AgAgA0H4BGpBCGoiCiAFNgIAIANBADoAoAUgAyACOgCk
BSADIA03A+gEIAMgDTcD+AQgAyABNgKQBSADQQA7AY4FIAMgCTsBjAUgAyAINgKIBSADQQE6AIQFIAMgAjoAlAUgA0EAOgCYBSADQagFaiADQfgEahCgg4CA
AAJAAkAgAy0AsAVFDQAgA0HwBWpBIGogA0H4BGpBIGooAgA2AgAgA0HwBWpBGGogA0H4BGpBGGopAwA3AwAgA0HwBWpBEGogA0H4BGpBEGopAwA3AwAgA0Hw
BWpBCGogCikDADcDACADIAMpA/gENwPwBSADQQE6AJwGIAMgAykDqAUiDTcClAYgDUIwiKdBf2oiAkH//wNxIAJHDQQgAyACOwGgBiADQfAFaiECDAELIANB
4gVqQgA3AQAgA0G4BWpBIGogA0H4BGpBIGooAgA2AgAgA0G4BWpBGGogA0H4BGpBGGopAwA3AwAgA0G4BWpBEGogA0H4BGpBEGopAwA3AwAgA0G4BWpBCGog
CikDADcDACADIAMpA/gENwO4BSADQgA3AtwFIANBuAVqIQILIANBsARqQTBqIAJBMGooAgA2AgAgA0GwBGpBKGogAkEoaikCADcDACADQbAEakEgaiACQSBq
KQIANwMAIANBsARqQRhqIAJBGGopAgA3AwAgA0GwBGpBEGogAkEQaikCADcDACADQbAEakEIaiACQQhqKQIANwMAIAMgAikCADcDsAQgA0GkBmogA0GwBGoQ
xIOAgAACQAJAIAMtALAGDQBBACEIDAELQQAhCANAIAMvAagGIgogAygCpAYiAi8BqgJPDQIgAy8BqgYgAi8BqAJPDQIgAigCECIJIAIoAhhqIgIgCUkNAiAC
QQdxDQIgAkUNAiAJIAIgCkEDdGoiCigCAGoiAiAJSQ0CIAJBB3ENAiACRQ0CAkAgCi0ABEECcUUNACAIQQFqIghFDQULIANBpAZqIANBsARqEMSDgIAAIAMt
ALAGDQALCwJAAkBBAUUNACAALwGOASIJIAZrIgIgCUsNBCACQX9qIgogAksNAkEAIBBBACAIIA5rIgIgAiAISxsiAmsiCCAIIBBLGyAKTQ0DIBBBf3MgECAC
IBAgAkkbIAlqaiAGayEJDAELQQBBABDLg4CAAAALAkADQCADQbQGaiAAEK6DgIAAIAMvAbgGIgINASAJQQFqIglFDQMMAAsLAkAgAy0A7ANFDQAgAygC5AMi
CUUNAANAIAAoAlwiCiAJKAIUayIIIApLDQIgCSgCDCEKIAAgCDYCXAJAAkAgCSgCFCIIQYCAJEsNAAJAIAhFDQAgCSgCEEEAIAj8CwALIAkoAhAhCAJAQYCA
JEUNACAIQaoBQYCAJPwLAAsgCCAAKAI8NgIAIAAgCDYCPAwBCyAAKAIsIQYgACgCMCEFIAkoAhAhAQJAIAhFDQAgAUGqASAI/AsACyAGIAEgCEEQQQAgBSgC
DBGBgICAAICAgIAACwJAQbACRQ0AIAlBqgFBsAL8CwALIAkgACgCKDYCACAAIAk2AiggCiEJIAoNAAsLIAAgDxDlgICAAAwECwALIAAgDxDlgICAAEEAIQIM
AgsACyAJIAsQiICAgAAACyADQYAYaiSAgICAACACDwsAC58BAQR/I4CAgIAAQRBrIgMkgICAgAACQCACLQAAIgRBIE8NACADQQhqIAAgASACIAJBBGpBASAE
dCIFELeEgIAAAkAgAy0ADCIBRQ0AIAAoAgQiBkF/aiIEIAZLDQECQCAEIAMoAggiBkYNACAAIAIgBCAGIAJBBGogBRC4hICAAAsgACAGENODgIAACyADQRBq
JICAgIAAIAFBAEcPCwALnwEBBH8jgICAgABBEGsiAySAgICAAAJAIAItAAAiBEEgTw0AIANBCGogACABIAIgAkEEakEBIAR0IgUQs4SAgAACQCADLQAMIgFF
DQAgACgCBCIGQX9qIgQgBksNAQJAIAQgAygCCCIGRg0AIAAgAiAEIAYgAkEEaiAFELSEgIAACyAAIAYQ04OAgAALIANBEGokgICAgAAgAUEARw8LAAufAQEE
fyOAgICAAEEQayIDJICAgIAAAkAgAi0AACIEQSBPDQAgA0EIaiAAIAEgAiACQQRqQQEgBHQiBRC1hICAAAJAIAMtAAwiAUUNACAAKAIEIgZBf2oiBCAGSw0B
AkAgBCADKAIIIgZGDQAgACACIAQgBiACQQRqIAUQtoSAgAALIAAgBhDTg4CAAAsgA0EQaiSAgICAACABQQBHDwsAC6MCAQR/AkACQAJAAkACQAJAAkAgACgC
CCICQYCAgIAETw0AIAEgACgCBCIDQQAgAhsiBE8NASADQX9qIgUgA0sNACAFIARPDQICQAJAIAINAEEAIQNBqtWq1XohAgwBCyAAKAIAIgJBA3ENAQsgASAD
Tw0DIAUgA08NBCACIAFBAnRqIAIgBUECdGooAgA2AgAgACgCBCIFQX9qIgEgBUsNACABIANPDQUgAiABQQJ0akGq1arVejYCACAAKAIEIgFBf2oiAyABSw0A
IAMgBE8NBiAAIAM2AgQPCwALIAEgBBCIgICAAAALIAUgBBCIgICAAAALIAEgAxCIgICAAAALIAUgAxCIgICAAAALIAEgAxCIgICAAAALIAMgBBCIgICAAAAL
yQMBBH8CQAJAIAEoAgwNACABKAIIRQ0BCyAAKAJ0IgJBgICAgARPDQACQAJAIAINAEEAIQNBqtWq1XohAgwBCyAAKAJsIgJBA3ENASAAKAJwIQMLAkAgA0UN
AANAAkAgAigCACIEKAIAIAFHDQACQCABKAIIIgUNACABKAIMIgVFDQQLIARBADYBBCAEIAU2AgALIAJBBGohAiADQX9qIgMNAAsLIAEoAgwhAgJAAkAgASgC
CCIDRQ0AIAMgAjYCDAwBCyAAIAI2AlQLIAEoAgghAwJAAkAgAkUNACACIAM2AggMAQsgACADNgJYCyAAKAJcIgIgASgCFGsiAyACSw0AIAAgAzYCXAJAAkAg
ASgCFCICQYCAJEsNAAJAIAJFDQAgASgCEEEAIAL8CwALIAEoAhAhAgJAQYCAJEUNACACQaoBQYCAJPwLAAsgAiAAKAI8NgIAIAAgAjYCPAwBCyAAKAIsIQQg
ACgCMCEFIAEoAhAhAwJAIAJFDQAgA0GqASAC/AsACyAEIAMgAkEQQQAgBSgCDBGBgICAAICAgIAACwJAQbACRQ0AIAFBqgFBsAL8CwALIAEgACgCKDYCACAA
IAE2AigPCwALrQUDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKM
EAJAAkACQAJAAkAgAUGIEGogABCEhICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EA
DQBBACkD4PrJgAAiA6dBwQBBAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNAAJAQcEARQ0AIAJBqgFBwQD8CwALIAFCwQA3A5gQIAEgAjYClBAgAUHclceA
ADYCkBAgASADNwOIEAJAAkAgAUGQEGogABCEhICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQ
IAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJA
IABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIA
EICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0A
IAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALnQMBBX8jgICAgABB0ABrIgQkgICAgAAg
BEEIaiABIAIQuoOAgAAgAy8BBiEFAkACQAJAIAMoAgAiBiAEKAIIIgFHDQACQCADLwEEIgEgBC8BDCIHTw0AIABCADcCACAAQQhqQgA3AgAMAwsgASAHa0H/
/wNxIQgMAQsCQCABLwGqAiAELwEMayIHQf//A3EgB0cNAAJAIAEoAgwiAUUNACAHQf//A3EhBwNAAkAgASAGRw0AIAcgAy8BBGoiCCAHTw0EDAMLIAcgAS8B
qgJqIgggB0kNAiAIIQcgASgCDCIBDQALCyAAQgA3AgAgAEEIakIANwIADAILAAsCQAJAAkACQCACQQNxDgMAAQIACyAEQQA6ABQgBEEUaiEHIARBGGohAQwC
CyAEQQE6ACggBEEoaiEHIARBLGohAQwBCyAEQQI6ADwgBEE8aiEHIARBwABqIQELIAEgCDYCBCABIAU7AQAgAUEBOgAMIAFBCGoiCCAHLQAAOgAAIAAgASkC
ADcCACAAQQhqIAgpAgA3AgALIARB0ABqJICAgIAAC1oBAn9BBiECQZaZwIAAIQMCQAJAAkACQCABQQNxDgQDAAECAwtBCCECQfSEwIAAIQMMAgtB3JLAgAAh
AwwBC0EHIQJBx4DAgAAhAwsgACADNgIAIAAgAjYCBAu4AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTggA2siBWoiBiAESQ0C
IANB9OPIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGs5MiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwi
BiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3EN
AiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE4SQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC5oDAQx/AkAgAC8B+hFB
f2oiAUH//wNxIgIgAUcNAAJAIAAoAhgiAy8B0AEiBCACRw0AIAMoAsgBIgIgAikDAEKAgICAEIQ3AwALIAMtANwBIQUgAy0A2wEhBgJAIAAQyICAgAAiB0H/
/wNxDQAgAC8BgBIiCCAAKAIYIgIvAYwBTw0BIAIoAsQBIAg7AQYgAigCxAEiCS8BBCIKIAkoAgAiCy8BqgJPDQEgCS8BBiIMIAsvAagCTw0BIAsoAhAiCSAL
KAIYaiILIAlJDQEgC0EHcQ0BIAtFDQEgCSALIApBA3RqKAIAaiILIAlJDQEgC0EHcQ0BIAtFDQEgAiAIOwHQASACIAsgDEEDdGo2AswBIAMgACgCGEcNASAD
IAVBAXE6ANwBIAMgBkEDcSICOgDbAQJAIAZBHnRBHnVBf0oNAAJAIAJBfmoOAgADAAsgAygCyAEiAyADKQMAQv//////c4NCgICAgIAIhDcDAAsgBCABQf//
A3FHDQAgACgCGCgCyAEiACAAKQMAQoCAgIAghDcDAAsgBw8LAAuAAgACQAJAAkACQCAAQf8BcSAAQYD+/wBxQQd2QaCdwoAAai8BAGoiAEH/9wFLDQAgAEEB
dEGgncOAAGovAQAiAEEeTw0BIAFB/wFxIAFBgP7/AHFBB3ZBoJ3CgABqLwEAaiIBQYD4AU8NAiABQQF0QaCdw4AAai8BACIBQR5JDQMgAUEeEIiAgIAAAAsg
AEGA+AEQiICAgAAACyAAQR4QiICAgAAACyABQYD4ARCIgICAAAALIAIgAUEBdEGgjceAAGovAQBBBXRBgD5xIABBAXRBoI3HgABqLwEAQfgBcXIgAi0AAEEH
cXJB4J/HgABqLQAAIgBBAXZBB3E6AAAgAAvbAgEDfyOAgICAAEEQayIDJICAgIAAAkACQAJAIAAoAsQBKAIAQRBqIAAoAsgBIAEgAhDkg4CAACIEQf//A3Ei
BUEZRg0AIAUNAQwCCyABIAAvAdABQQN0IAAoAswBa2oiBSABSw0AIANBCGogACAAKALEASgCAEHs3siAABCVg4CAACADLwEMIgQNAQJAAkACQAJAIAVBA3Yi
ASAALwHQASIERg0AIAEgBEkNASABIARLDQIMBAsgACgCzAEhAQwCCyAAKALMASABIARrQQN0aiEBDAELIAEgBGsiAUGAgARPDQEgBCABQf//A3FqIgRB//8D
cSIFIARHDQEgBSAALwGMAU8NASAAKALMASABQQN0aiEBC0EAIQQgACgCxAEoAgBBEGogACgCyAEgASACEOSDgIAAIgBB//8DcUUNARDlg4CAACAAIQQMAQsA
CyADQRBqJICAgIAAIAQLgQsEBH8BfgN/AX4jgICAgABBEGsiAySAgICAAAJAAkAgACgCGCIELQC8AkUNACAELQC7AiEFIARBADsAuwIgACgCGCEEDAELIAQt
ALkCIQULAkAgBCAFQQNxQQJzQQJ0Qbz+yYAAaigCAGotAAAiBUEedEEedUF/Sg0AIAFB////AHEhBkEgIQEgBkH/AUsNAEHckceAAEHcjceAACAFQQFxGyAG
QQF0ai8BACEBCwJAAkACQCACQQNxIAQoAswBIgUpAwAiB0IqiKciBkEDcUYNAAJAAkACQCAGQQNxDgQDAAEDAwsgAC8B+hFBf2oiBkH//wNxIgggBkcNAyAE
LwHQASIGQf//A3EgCE8NAiAGQQFqQf//A3EgBC8BjAFPDQMgBCAEKALEASgCAEEQaiAEKALIASAFQQhqQQEQs4KAgAAgACgCGCIELwHSAUUNAiAELwHQAUEB
Sw0CIAQQ3YOAgAAiBCkDACIHQoCAgICAgAODQoCAgICAgANRDQEMAgsgBC8B0AFFDQIgBSAHQv///////3yDNwMAIAAoAhgiBC8B0AFFDQIgBCAEKALEASgC
AEEQaiAEKALIASAEKALMAUF4akEBELOCgIAAIAAoAhgiBC8B0gFFDQEgBC8B0AFBAUsNASAEEN2DgIAAIgQpAwAiB0KAgICAgIADg0KAgICAgIADUg0BCyAE
IAdC////////fIM3AwALIAAoAhghCAJAIAUpAwBCA4NCAVINACAIKALEASgCACIGQRBqIAUQrIOAgAAgBigCECIJIAAoAhgiCCgCyAEiCigCAGoiBCAJSQ0B
IARBB3ENASAERQ0BAkAgBi8BqAIiBkUNAANAIAQpAwBCA4NCAVENAiAEQQhqIQQgBkF/aiIGDQALCyAKIAopAwBC/////79/gzcDACAAKAIYIQgLAkAgCC8B
1AEgBSkDAEIaiKciCkH//wNxIgZGIgQNACAGRQ0AIAgoAsgBLQAEQQhxRQ0BIAgoAsQBKAIAIggoAkggCkH//wNxIglNDQEgCCgCECIKIAgoAkBqIgYgCkkN
ASAGQQNxDQEgBkUNASAGIAlBJGxqIgYvASAiCkUNASAGQSBqIApBf2oiBjsBACAGQf//A3ENACAIKAJEIgZBf2oiCiAGSw0BIAggCjYCRAsgBSAFKQMAIgtC
gICAfIMgAa1C////AINCAoaEIgc3AwAgBSAAKAIYMwHUAUIahiAHQvz//5+AgHyDhCACrUIDg0IqhoQiBzcDACAFIAdC/P//////e4NCgICAgICABEIAIAAo
AhgtANoBG4QiBzcDACAFIAAoAhgxANsBQgODQi6GIAdC/P//////R4OENwMAIAVBADsBBgJAIAQNACAFKQMAIgdCgICA4P//AINQDQAgACgCGCgCxAEoAgAi
BCgCSCAHQhqIp0H//wNxIghNDQEgBCgCECIGIAQoAkBqIgQgBkkNASAEQQNxDQEgBEUNASAEIAhBJGxqIgYvASAiBEUNASAEQQFqIgRB//8DcSAERw0BIAZB
IGogBDsBACAAKAIYKALIASIEIAQpAwBCgICAgIABhDcDAAsCQCAAKAIYIgQvAdYBRQ0AIAQQ3oOAgAAiBEH//wNxRQ0CIAMgBDsBDiADQQ5qEN+DgIAAIAUt
AAVBIHFFDQIMAQsgC0KAgICAgIAIg1ANASAEKALEASgCACIGQRBqIAUQrYOAgAAgBigCECIFIAAoAhgoAsgBIgAoAgBqIgQgBUkNACAEQQdxDQAgBEUNAAJA
IAYvAagCIgVFDQADQCAELQAFQSBxDQMgBEEIaiEEIAVBf2oiBQ0ACwsgACAAKQMAQv//////fYM3AwAMAQsACyADQRBqJICAgIAAC/kBAQN/AkAgAC8B0gFF
DQAgACgCxAEiASgCACECAkACQCABLwEEIgFFDQAgAUF/aiEDDAELIAIoAggiAkUNAUEBIQEDQAJAIAEgAi8BqgIiA0sNACADIAFrIQMMAgsgASADayEBIAIo
AggiAg0ADAILCyAALwGMAUF/aiIBQf//A3EgAUcNACADQf//A3EiACACLwGqAk8NACABQf//A3EgAi8BqAJPDQAgAigCECIDIAIoAhhqIgIgA0kNACACQQdx
DQAgAkUNACADIAIgAEEDdGooAgBqIgIgA0kNACACQQdxDQAgAkUNACACIAFB//8DcUEDdGoPCwALhQYCCn8BfiOAgICAAEEwayIBJICAgIAAIAAvAdYBIQIC
QAJAA0ACQAJAIAJB//8DcUUNACAAKALMASIDIAAoAsQBKAIAIgQoAhAiBWsiBiADSw0AIAUgBCgCpAFqIgcgBUkNACAHRQ0AIARBEGohBSAAKALIASEIIAEg
BzYCACABQQRqIAEgBhCng4CAAAJAIAEvARBFDQADQCAAKALAASIERQ0DIAQoAhAiB0UNAgJAQQANACAFKAIAIgIgBSgCEGoiBCACSQ0DIARBB3ENAyAERQ0D
IAFBJGogBCAFKAIUIAdBf2pBBXZBAWoQpIOAgAAgAS0AKEUNACACIAUoAhhqIgUgAkkNAyAFRQ0DIAEoAiRB////P00NBAwDCyABQRRqIAAgACgCxAEoAgBB
8N7IgAAQlYOAgAAgAS8BGCIFDQYgASgCFCIFIAAoAsQBKAIARw0CIAVBEGohBQwACwsCQAJAAkAgAS0ADA0AIAEoAgghBwwBCyABKAIIIgcvAQAiCUUNAiAE
KAK0ASAJTQ0CIAUoAgAiCiAEKAKsAWoiBiAKSQ0CIAZBA3ENAiAGRQ0CIAYgCUEcbGoiBi8BGCIJRQ0CIAZBGGogCUF/aiIGOwEAAkAgBkH//wNxDQAgBCgC
sAEiBkF/aiIJIAZLDQMgBCAJNgKwAQsgBy8BACACQf//A3FHDQAgCC0ABCEHQoCAgICAgAghCyADIQggB0EQcUUNAgwBCyAHIAI7AQAgAyADKQMAQoCAgICA
gAiENwMAQoCAgICAAiELCyAIIAgpAwAgC4Q3AwAgAC8B1gEiB0UNACAEKAK0ASAHTQ0AIAUoAgAiBSAEKAKsAWoiACAFSQ0AIABBA3ENACAARQ0AIAAgB0Ec
bGoiBS8BGCIARQ0AIABBAWoiAEH//wNxIABHDQAgBUEYaiAAOwEADAMLAAsgAUEcaiAAIAAoAsQBKAIAQe7eyIAAEJWDgIAAIAEvASAiBQ0CIAAvAdYBIgIN
AAsLQQAhBQsgAUEwaiSAgICAACAFC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2
AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQ5oOAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQ
IAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQdkAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHZAEUNACACQaoBQdkA/AsACyABQtkANwOYECAB
IAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQ5oOAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKYECICSw0CIAFCADcDmBAgASgClBAh
BCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQIgA2ApgQIAEgASgCtBAiAjYClBAg
ASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABLwGsEA0BIAEo
AqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIARQ0AIAEoAogQIQQgASgCjBAhBSAB
KAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACACEIiAgIAAAAsAC5gJAwd/AX4BfyOA
gICAAEHAEGsiACSAgICAAAJAQYAQRQ0AIABBqgFBgBD8CwALIABCgBA3AogQIABBuN7BgAA2AoAQIAAgADYChBBBACEBAkACQAJAA0AgACgCjBAiAkHJACAB
ayIDaiIEIAJJDQIgAUGs5MiAAGohBQJAAkAgBCAAKAKIECIGSw0AAkAgBCAGSw0AAkAgACgChBAgAmoiBEH15MiAAE8NACAFIAQgA2pJDQYLAkAgA0UNACAE
IAUgA/wKAAALIAAoAowQIgQgA2oiAiAESQ0FIAAgAjYCjBAMAgsgBCAGEIiAgIAAAAsgACgCgBAoAgAhBCAAIAM2ApwQIAAgBTYCmBAgAEGwEGogAEGAEGog
AEGYEGpBAUEBIAQRgYCAgACAgICAACAAKQOwECIHpyEDAkAgB0IgiKdB//8DcQ4DAQQABAtBAA0CQQApA+D6yYAAIgenQckAQQBBACAHQiCIpygCABGAgICA
AICAgIAAIgFFDQICQEHJAEUNACABQaoBQckA/AsACyAAQskANwOQECAAIAE2AowQIABB3JXHgAA2AogQIAAgBzcDgBAgAEGIEGohCEEAIQEDQCAAKAKUECIC
QckAIAFrIgNqIgQgAkkNBCABQazkyIAAaiEFAkACQCAEIAAoApAQIgZLDQACQCAEIAZLDQACQCAAKAKMECACaiIEQfXkyIAATw0AIAUgBCADakkNCAsCQCAD
RQ0AIAQgBSAD/AoAAAsgACgClBAiBCADaiICIARJDQcgACACNgKUEAwCCyAEIAYQiICAgAAACyAAKAKIECgCACEEIAAgAzYCrBAgACAFNgKoECAAQbAQaiAI
IABBqBBqQQFBASAEEYGAgIAAgICAgAAgACkDsBAiB6chAwJAIAdCIIinQf//A3EOAwEGAAYLIAAoApAQIgFFDQQgACgCgBAhBCAAKAKEECECIAAoAowQIQMC
QCABRQ0AIANBqgEgAfwLAAsgBCADIAFBAEEAIAIoAgwRgYCAgACAgICAAAwECyABIANqIgMgAUkNBCADIQEgA0HJAEkNAAsCQCAAKAKUECIBIAAoApAQIgNL
DQAgAEIANwOQECAAKAKMECEEIABBqtWq1Xo2AowQIAAgAzYCuBAgACABNgK0ECAAIAQ2ArAQIABBmBBqIABBsBBqIABBgBBqELiAgIAAIAAgACgCuBAiATYC
kBAgACAAKAKwECIDNgKMECAAIAAoArQQNgKUEAJAIAFFDQAgACgCgBAhBCAAKAKEECECAkAgAUUNACADQaoBIAH8CwALIAQgAyABQQBBACACKAIMEYGAgIAA
gICAgAALIAAvAaAQDQMgACgCmBAiAyAAKAKcECIBEICAgIAAIAFFDQMCQCABRQ0AIANBqgEgAfwLAAsgACADIAFBACAAELmAgIAADAMLIAEgAxCIgICAAAAL
IAEgA2oiAyABSQ0CIAMhASADQckASQ0ACyAAKAKMECIBIAAoAogQIgNLDQIgACgChBAgARCAgICAAAsgAEHAEGokgICAgAAPCwALIAEgAxCIgICAAAALcAEC
fwJAIAAvAdABIAFB//8DcSICSQ0AIAAgACgCzAEgAkEDdGs2AswBIAAoAsQBIgMvAQYgAmsiAkH//wNxIAJHDQAgAyACOwEGIAAvAdABIAFB//8DcWsiAUH/
/wNxIAFHDQAgACABOwHQAQ8LAAuHAQECfwJAIAAvAdABIAFB//8DcWoiAkH//wNxIgMgAkcNACADIAAvAYwBTw0AIAAgACgCzAEgAUH//wNxIgFBA3RqNgLM
ASAAKALEASIDLwEGIAFqIgJB//8DcSACRw0AIAMgAjsBBiAALwHQASABaiIBQf//A3EgAUcNACAAIAE7AdABDwsAC9ABAQJ/AkAgAiABLwEEIgNLDQAgAEEA
OgAQIABBADoACCAAIAMgAms7AQQgACABKAIANgIAIAAgAS8BBjsBBg8LIAIgA2shAiABKAIAIQMCQANAIAMoAggiBEUNAQJAIAIgBCIDLwGqAiIESw0AIAAg
AzYCACAAQQA6ABAgAEEAOgAIIAAgBCACazsBBCAAIAEvAQY7AQYPCyACIARrIQIMAAsLIABBADsBBCAAIAM2AgAgAEEBOgAQIAAgAjYCDCAAQQA6AAggACAB
LwEGOwEGC/MJAwl/An4BfyOAgICAAEHAAGsiBCSAgICAAAJAAkAgAiAAKAIAIgVrIgYgAksNACAFIAAoAihqIgcgBUkNACAHRQ0AIAQgBzYCLAJAIAIpAwBC
A4NCAVENAEEZIQhBAA0CIAUgAEEcaiIJKAIAaiIKIAVJDQEgCkEHcQ0BIApFDQEgACgCICIHRQ0CQQAhC0EAIQwCQANAIAopAwAiDUIAUg0BIApBCGohCiAL
QYAIaiELIAcgDEEBaiIMRw0ADAQLCyAKQgEgDXoiDoYgDYU3AwAgDEGAgIAgTw0BIAUgACgCJGoiCiAFSQ0BIApFDQEgDEH///8BSw0BIAogDqdBBHRqIAtq
IgpBA3ENASAKIAM7AQAgCkECaiADQf///wBxQRB2OgAAIAogACgCAGsiDCAKSw0BAkAgBEEsaiAGIAytQoCAgIAQhBClg4CAACIIQf//A3FFDQAgCSAAKAIA
IApBARCig4CAAAwDCyACIAIpAwBCfINCAYQ3AwAgASABKQMAQoCAgIDAAIQ3AwBBACEIDAILIAEtAARBBHFFDQAgB0EDcQ0AIAdBfGooAgBFDQAgB0F4aigC
ACIKQX9qIgggCksNACAEQRBqIAatQoGAgIAQfiINQtvR0IWa2t+BZ4VCACANQqya4tOj0vD6H4VCABCvhYCAACAEIAQpAxhC29HQhZra34FnhUIAIAQpAxBC
q8j1xcesh7ugf4VCABCvhYCAACAHIAggBCkDCCAEKQMAhSINp3EiC2otAAAiDEUNACAHQXBqIQ8gDUI5iKchASAKQX9qIQIgB0F0aiEJAkADQCACIQoCQCAM
wEF/Sg0AIAEgDEH/AHFHDQAgByAJKAIAaiIMIAdJDQMgDEEDcQ0DIAxFDQMgBiAMIAtBAnRqKAIARg0CCyALQQFqIgxFDQIgByAMIAhxIgtqLQAAIgxFDQIg
CkF/aiECIAoNAAwCCwsgByAPKAIAaiIKIAdJDQAgCkEDcQ0AIApFDQACQCAKIAtBA3RqIgooAgQiC0EDcUUNACAFIAooAgBqIgwgBUkNASAMQQNxDQEgDEUN
ASAMIAtBAnRqIgwgAzsBACAMQQJqIANB////AHFBEHY6AAAgCigCBEEBaiIMRQ0BIAogDDYCBEEAIQgMAgsgBCALQf////8DSyIMOgA8AkACQAJAIAwNACAF
IABBHGoiBigCAGoiDCAFSQ0DIAxBB3ENAyAMRQ0DIARBNGogDCAAKAIgIAtBAXIiAUECdEF/akEEdkEBahCkg4CAACAELQA4RQ0AIAUgACgCJGoiDCAFSQ0D
IAxFDQMgBCgCNCIHQf////8ASw0DIAwgB0EEdGoiB0EDcQ0DIAAoAgAiAiAKKAIAaiIMIAJJDQMgDEEDcQ0DIAxFDQMgCigCBCIIIAFLDQECQCAHIAhBAnQi
AmogDE0NACAHIAwgAmpJDQQLAkAgAkUNACAHIAwgAvwKAAALIAooAgQiAiALSw0CIAcgAkECdGoiCyADOwEAIAtBAmogA0H///8AcUEQdjoAACAHIAAoAgBr
IgsgB0sNAyAKIAs2AgAgCigCBEEBaiILRQ0DIAogCzYCBCAGIAAoAgAgDCAIEKKDgIAAQQAhCAwEC0EZIQgMAwsgCCABEIiAgIAAAAsgAiABEIiAgIAAAAsA
CyAEQcAAaiSAgICAACAIC5YKAwd/AX4BfyOAgICAAEHAEGsiACSAgICAAAJAQYAQRQ0AIABBqgFBgBD8CwALIABCgBA3AogQIABBuN7BgAA2AoAQIAAgADYC
hBBBACEBAkACQAJAA0AgACgCjBAiAkE9IAFrIgNqIgQgAkkNAiABQazoyIAAaiEFAkACQCAEIAAoAogQIgZLDQACQCAEIAZLDQACQCAAKAKEECACaiIEQeno
yIAATw0AIAUgBCADakkNBgsCQCADRQ0AIAQgBSAD/AoAAAsgACgCjBAiBCADaiICIARJDQUgACACNgKMEAwCCyAEIAYQiICAgAAACyAAKAKAECgCACEEIAAg
AzYCnBAgACAFNgKYECAAQbAQaiAAQYAQaiAAQZgQakEBQQEgBBGBgICAAICAgIAAIAApA7AQIgenIQMCQCAHQiCIp0H//wNxDgMBBAAEC0EADQJBACkD4PrJ
gAAiB6dBPUEAQQAgB0IgiKcoAgARgICAgACAgICAACIBRQ0CIAFCqtWq1arVqtWqfzcAACABQRBqQqrVqtWq1arVqn83AAAgAUEIakKq1arVqtWq1ap/NwAA
IAFBNWpCqtWq1arVqtWqfzcAACABQTBqQqrVqtWq1arVqn83AAAgAUEoakKq1arVqtWq1ap/NwAAIAFBIGpCqtWq1arVqtWqfzcAACABQRhqQqrVqtWq1arV
qn83AAAgAEI9NwOQECAAIAE2AowQIABB3JXHgAA2AogQIAAgBzcDgBAgAEGAEGpBCGohCEEAIQEDQCAAKAKUECICQT0gAWsiA2oiBCACSQ0EIAFBrOjIgABq
IQUCQAJAIAQgACgCkBAiBksNAAJAIAQgBksNAAJAIAAoAowQIAJqIgRB6ejIgABPDQAgBSAEIANqSQ0ICwJAIANFDQAgBCAFIAP8CgAACyAAKAKUECIEIANq
IgIgBEkNByAAIAI2ApQQDAILIAQgBhCIgICAAAALIAAoAogQKAIAIQQgACADNgKsECAAIAU2AqgQIABBsBBqIAggAEGoEGpBAUEBIAQRgYCAgACAgICAACAA
KQOwECIHpyEDAkAgB0IgiKdB//8DcQ4DAQYABgsgACgCkBAiAUUNBCAAKAKAECEEIAAoAoQQIQIgACgCjBAhAwJAIAFFDQAgA0GqASAB/AsACyAEIAMgAUEA
QQAgAigCDBGBgICAAICAgIAADAQLIAEgA2oiAyABSQ0EIAMhASADQT1JDQALAkAgACgClBAiASAAKAKQECIDSw0AIABCADcDkBAgACgCjBAhBCAAQarVqtV6
NgKMECAAIAM2ArgQIAAgATYCtBAgACAENgKwECAAQZgQaiAAQbAQaiAAQYAQahC4gICAACAAIAAoArgQIgE2ApAQIAAgACgCsBAiAzYCjBAgACAAKAK0EDYC
lBACQCABRQ0AIAAoAoAQIQQgACgChBAhAgJAIAFFDQAgA0GqASAB/AsACyAEIAMgAUEAQQAgAigCDBGBgICAAICAgIAACyAALwGgEA0DIAAoApgQIgMgACgC
nBAiARCAgICAACABRQ0DAkAgAUUNACADQaoBIAH8CwALIAAgAyABQQAgABC5gICAAAwDCyABIAMQiICAgAAACyABIANqIgMgAUkNAiADIQEgA0E9SQ0ACyAA
KAKMECIBIAAoAogQIgNLDQIgACgChBAgARCAgICAAAsgAEHAEGokgICAgAAPCwALIAEgAxCIgICAAAALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJA
AkACQANAIAAoAgwiBEHXACADayIFaiIGIARJDQIgA0H15MiAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQczlyIAATw0AIAcgBiAF
akkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYR
gYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQdcASQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAF
DwsACyAGIAgQiICAgAAAC4kBAQV/AkAgACgCBCIBQX9qIgIgAUsNAEEAIQMCQCAAKAIMIgQgAk8NAEEAIQNBAEEYIARrIgIgAkEYSxshAiABIARBf3NqIQUg
AEEKaiEBA0AgAiADRg0CIAAvAQggAS0AAEEQdHIgBCADanZBAXFFDQEgBSADQQFqIgNHDQALIAUhAwsgAw8LAAv0OgUKfwF8AX4ifwN+I4CAgIAAQaARayIE
JICAgIAAIAIvAaYCIQUgAi8BogIhBiACLwGgAiEHIAIoApwCIQggAigCmAIhCSACLwGkAiIKwSELAkACQAJAIAMtAAFFDQACQAJAAkACQAJAAkACQCADLQAA
QQNxDgQAAQIDAAsgBCALQQBIIgM6AGACQCADDQAgC0EBdCELDAYLIAtBf0YhA0H//wMhCyADRQ0FIABCgICAgMADNwIADAcLIAQgCUEASCIDOgBgAkAgAw0A
IAlBAXQhAwwEC0F/IQMgCUF/Rw0DIABCgICAgMADNwIADAYLIAQgBcFBAEgiAzoAYAJAIAMNACAFQQF0IQwMAgtB//8DIQwgBUH//wNxQf//A0cNASAAQoCA
gIDAAzcCAAwFCyAEIAhBAEgiAzoAYAJAAkAgAw0AIAhBAXQhDAwBC0F/IQwgCEF/Rw0AIABCgICAgMADNwIADAULIAYgB2wiA0H/////AUsNBSAGQQN0Ig0g
A0EDdGoiAyANSQ0FAkACQCALDQBBACEKDAELQgFBICAKQX9qZ2utQv//A4OGpyIKuEQAAAAAAADqP6IiDkQAAAAAAADwv2UNBiAORAAAAAAAAPBBZg0GIA78
A61CJH4iD0IgiKcNBiAKQQF0QQNqQfz///8HcSINIA+naiIKIA1JDQYLIAMgCmoiCiADSQ0FIAlBD2oiAyAJSQ0FIANBgICAgAFPDQUgA0EEdkE/akEDdkH4
//8BcSINIANBBHRBgH5xaiIDIA1JDQUgCkEHaiINIApJDQUgDUF4cSIKIANqIgMgCkkNBQJAAkAgCQ0AQQAhCgwBC0IBQSAgCUF/akEEdmdrrUL//wODhqch
CgsgAyAKQRNqQXxxIApBDGxqaiIKIANJDQUgDEEfaiIDIAxJDQUgA0GAgIDAAE8NBSADQQV2QT9qQQN2Qfj/P3EiDSADQQV0QYB4cWoiAyANSQ0FIApBB2oi
DSAKSQ0FIA1BeHEiCiADaiIDIApJDQUgBUH//wNxIgpBHG4hDQJAAkAgCkEcTw0AQQAhCgwBC0IBQSAgDUF/amdrrUL//wODhqciCrhEAAAAAAAA6j+iIg5E
AAAAAAAA8L9lDQYgDkQAAAAAAADwQWYNBiAO/AOtQhx+Ig9CIIinDQYgCkEBdEEDakH8////B3EiCiAPp2oiECAKSQ0GIAMgEGoiECADSQ0GQgFBICANQQR0
QX9qZ2uthqchCiAQIQMLIANBA2oiDSADSQ0FIA1BfHEiAyAKQQZsIApBE2pBfHFqQQNqQXxxaiIKIANJDQUgDCEIIApBgYB8SQ0DDAULIAYgB2wiA0H/////
AUsNBCAGQQN0Ig0gA0EDdGoiAyANSQ0EAkACQCALDQBBACEKDAELQgFBICAKQX9qZ2utQv//A4OGpyIKuEQAAAAAAADqP6IiDkQAAAAAAADwv2UNBSAORAAA
AAAAAPBBZg0FIA78A61CJH4iD0IgiKcNBSAKQQF0QQNqQfz///8HcSINIA+naiIKIA1JDQULIAMgCmoiCiADSQ0EIAlBD2oiAyAJSQ0EIANBgICAgAFPDQQg
A0EEdkE/akEDdkH4//8BcSINIANBBHRBgH5xaiIDIA1JDQQgCkEHaiINIApJDQQgDUF4cSIKIANqIgMgCkkNBAJAAkAgCQ0AQQAhCgwBC0IBQSAgCUF/akEE
dmdrrUL//wODhqchCgsgAyAKQRNqQXxxIApBDGxqaiIKIANJDQQgCEEfaiIDIAhJDQQgA0GAgIDAAE8NBCADQQV2QT9qQQN2Qfj/P3EiDSADQQV0QYB4cWoi
AyANSQ0EIApBB2oiDSAKSQ0EIA1BeHEiCiADaiIDIApJDQQgDEH//wNxIgpBHG4hDQJAAkAgCkEcTw0AQQAhCgwBC0IBQSAgDUF/amdrrUL//wODhqciCrhE
AAAAAAAA6j+iIg5EAAAAAAAA8L9lDQUgDkQAAAAAAADwQWYNBSAO/AOtQhx+Ig9CIIinDQUgCkEBdEEDakH8////B3EiCiAPp2oiECAKSQ0FIAMgEGoiECAD
SQ0FQgFBICANQQR0QX9qZ2uthqchCiAQIQMLIANBA2oiDSADSQ0EIA1BfHEiAyAKQQZsIApBE2pBfHFqQQNqQXxxaiIKIANJDQQgDCEFIApBgYB8SQ0CDAQL
IAYgB2wiDEH/////AUsNAyAGQQN0Ig0gDEEDdGoiDCANSQ0DAkACQCALDQBBACEKDAELQgFBICAKQX9qZ2utQv//A4OGpyIKuEQAAAAAAADqP6IiDkQAAAAA
AADwv2UNBCAORAAAAAAAAPBBZg0EIA78A61CJH4iD0IgiKcNBCAKQQF0QQNqQfz///8HcSINIA+naiIKIA1JDQQLIAwgCmoiDSAMSQ0DIANBD2oiCiADSQ0D
IApBgICAgAFPDQMgCkEEdkE/akEDdkH4//8BcSIMIApBBHRBgH5xaiIKIAxJDQMgDUEHaiIMIA1JDQMgDEF4cSIMIApqIgogDEkNAwJAAkAgAw0AQQAhDAwB
C0IBQSAgA0F/akEEdmdrrUL//wODhqchDAsgCiAMQRNqQXxxIAxBDGxqaiIMIApJDQMgCEEfaiIKIAhJDQMgCkGAgIDAAE8NAyAKQQV2QT9qQQN2Qfj/P3Ei
DSAKQQV0QYB4cWoiCiANSQ0DIAxBB2oiDSAMSQ0DIA1BeHEiDCAKaiIKIAxJDQMgBUH//wNxIgxBHG4hDQJAAkAgDEEcTw0AQQAhDAwBC0IBQSAgDUF/amdr
rUL//wODhqciDLhEAAAAAAAA6j+iIg5EAAAAAAAA8L9lDQQgDkQAAAAAAADwQWYNBCAO/AOtQhx+Ig9CIIinDQQgDEEBdEEDakH8////B3EiDCAPp2oiECAM
SQ0EIAogEGoiECAKSQ0EQgFBICANQQR0QX9qZ2uthqchDCAQIQoLIApBA2oiDSAKSQ0DIA1BfHEiCiAMQQZsIAxBE2pBfHFqQQNqQXxxaiIMIApJDQMgAyEJ
IAxBgYB8SQ0BDAMLIAYgB2wiA0H/////AUsNAiAGQQN0IgogA0EDdGoiAyAKSQ0CAkACQCALQf//A3EiCg0AQQAhCgwBC0IBQSAgCkF/amdrrUL//wODhqci
CrhEAAAAAAAA6j+iIg5EAAAAAAAA8L9lDQMgDkQAAAAAAADwQWYNAyAO/AOtQiR+Ig9CIIinDQMgCkEBdEEDakH8////B3EiDCAPp2oiCiAMSQ0DCyADIApq
IgogA0kNAiAJQQ9qIgMgCUkNAiADQYCAgIABTw0CIANBBHZBP2pBA3ZB+P//AXEiDCADQQR0QYB+cWoiAyAMSQ0CIApBB2oiDCAKSQ0CIAxBeHEiCiADaiID
IApJDQICQAJAIAkNAEEAIQoMAQtCAUEgIAlBf2pBBHZna61C//8Dg4anIQoLIAMgCkETakF8cSAKQQxsamoiCiADSQ0CIAhBH2oiAyAISQ0CIANBgICAwABP
DQIgA0EFdkE/akEDdkH4/z9xIgwgA0EFdEGAeHFqIgMgDEkNAiAKQQdqIgwgCkkNAiAMQXhxIgogA2oiAyAKSQ0CIAVB//8DcSIKQRxuIQwCQAJAIApBHE8N
AEEAIQoMAQtCAUEgIAxBf2pna61C//8Dg4anIgq4RAAAAAAAAOo/oiIORAAAAAAAAPC/ZQ0DIA5EAAAAAAAA8EFmDQMgDvwDrUIcfiIPQiCIpw0DIApBAXRB
A2pB/P///wdxIgogD6dqIg0gCkkNAyADIA1qIg0gA0kNA0IBQSAgDEEEdEF/amdrrYanIQogDSEDCyADQQNqIgwgA0kNAiAMQXxxIgMgCkEGbCAKQRNqQXxx
akEDakF8cWoiCiADSQ0CIApBgYB8Tw0CCyAEIAU7ARYgBCALOwEUIAQgBjsBEiAEIAc7ARAgBCAINgIMIAQgCTYCCAJAQYAQRQ0AIARB4ABqQaoBQYAQ/AsA
CyAEQoAQNwLoECAEQbjewYAANgLgECAEIARB4ABqNgLkEAJAAkACQAJAAkACQAJAIARB4BBqIARBCGoQjYSAgABB//8DcQ4DAAgBCAsCQCAEKALsECIDIAQo
AugQIgpNDQAgAyAKEIiAgIAADAgLIAQoAuQQIAMQgICAgAAMAQtBAA0AQQApA+D6yYAAIg+nQStBAEEAIA9CIIinKAIAEYCAgIAAgICAgAAiA0UNACADQqrV
qtWq1arVqn83AAAgA0EQakKq1arVqtWq1ap/NwAAIANBCGpCqtWq1arVqtWqfzcAACADQSdqQarVqtV6NgAAIANBIGpCqtWq1arVqtWqfzcAACADQRhqQqrV
qtWq1arVqn83AAAgBEIrNwPwECAEIAM2AuwQIARB3JXHgAA2AugQIAQgDzcD4BACQAJAIARB4BBqQQhqIARBCGoQjYSAgABB//8DcQ4DAAgBCAsgBCgC9BAi
AyAEKALwECIKSw0CIARCADcD8BAgBCgC7BAhDCAEQarVqtV6NgLsECAEIAo2ApARIAQgAzYCjBEgBCAMNgKIESAEQfgQaiAEQYgRaiAEQeAQahC4gICAACAE
IAQoApARIgM2AvAQIAQgBCgCiBEiCjYC7BAgBCAEKAKMETYC9BACQCADRQ0AIAQoAuAQIQwgBCgC5BAhDQJAIANFDQAgCkGqASAD/AsACyAMIAogA0EAQQAg
DSgCDBGBgICAAICAgIAACyAELwGAEQ0BIAQoAvgQIgogBCgC/BAiAxCAgICAACADRQ0BAkAgA0UNACAKQaoBIAP8CwALIAsgCiADQQAgCxC5gICAAAwBCyAE
KALwECIDRQ0AIAQoAuAQIQwgBCgC5BAhDSAEKALsECEKAkAgA0UNACAKQaoBIAP8CwALIAwgCiADQQBBACANKAIMEYGAgIAAgICAgAALAkAgASgCKCIDRQ0A
IAEgAygCADYCKAwCC0EADQIgAUEYakGwAkEDIAsQj4CAgAAiA0UNAgJAQbACRQ0AIANBqgFBsAL8CwALIANBB3FFDQEMBQsgAyAKEIiAgIAAAAsCQEGwAkUN
ACADQaoBQbAC/AsACyAGIAdsIhFBgICAgAJPDQMgBkEDdCISIBFBA3RqIhMgEkkNAwJAAkAgC0H//wNxIhQNAEEAIRVBACEWQQAhF0EAIRhBACEZDAELQgFB
ICAUQX9qZyILa61C//8Dg4anIhi4RAAAAAAAAOo/oiIORAAAAAAAAPC/ZQ0EIA5EAAAAAAAA8EFmDQQgDvwDIhmtQiR+Ig9CIIinDQQgGEEBdEEDakH8////
B3EiFyAPp2oiFiAXSQ0EQX9BACALa3RBf3MhFQsgEyAWaiIKIBNJDQMgCUEPaiILIAlJDQMgC0GAgICAAU8NAyALQQR2QT9qQQZ2IhpBA3QiGyALQQR0QYB+
cWoiCyAbSQ0DIApBB2oiDCAKSQ0DIAxBeHEiHCALaiIdIBxJDQMCQAJAIAkNAEEAIR4MAQtCAUEgIAlBf2pBBHZna61C//8Dg4anIR4LIB0gHkETakF8cSIf
IB5BAnRqIiAgHkEDdGpqIgogHUkNAyAIQR9qIgsgCEkNAyALQYCAgMAATw0DIAtBBXZBP2pBBnYiIUEDdCIiIAtBBXRBgHhxaiILICJJDQMgCkEHaiIMIApJ
DQMgDEF4cSIjIAtqIiQgI0kNAyAFQf//A3EiCkEcbiEMQQAhJSAkIQtBACEmQQAhJ0EAIShBACEpQQAhKgJAIApBHEkNAEIBQSAgDEF/amciCmutQv//A4OG
pyIouEQAAAAAAADqP6IiDkQAAAAAAADwv2UNBCAORAAAAAAAAPBBZg0EIA78AyIprUIcfiIPQiCIpw0EIChBAXRBA2pB/P///wdxIicgD6dqIiYgJ0kNBCAk
ICZqIgsgJEkNBEF/QQAgCmt0QX9zISVCAUEgIAxBBHRBf2pna62GpyEqCyALQQNqIgogC0kNAyAKQXxxIisgKkEBdCAqQRNqQXxxIiwgKkECdGoiLWpBA2pB
fHFqIgsgK0kNAyALQf//A2oiCiALSQ0DIAFBLGohCwJAAkAgCkGAgHxxIi5BgYAkSQ0AQQANASALKQIAIg+nIC5BEEEAIA9CIIinKAIAEYCAgIAAgICAgAAi
DUUNAQJAIC5FDQAgDUGqASAu/AsACyAuIS8gDUH//wNxDQUMAwsCQAJAIAEoAjwiDUUNACABIA0oAgA2AjwMAQtBAA0BIAtBgIAkQRAgCxCPgICAACINRQ0B
AkBBgIAkRQ0AIA1BqgFBgIAk/AsACyANQf//A3ENBQtBgIAkIS9BgIAkRQ0CIA1BqgFBgIAk/AsADAILAkBBsAJFDQAgA0GqAUGwAvwLAAsgAyABKAIoNgIA
IAEgAzYCKAsgAEEZOwEEDAELAkAgL0UNACANQQAgL/wLAAsgDSASaiILIA1JDQEgC0EHcQ0BIAtFDQEgDUEHcQ0BAkACQAJAAkACQCAGRQ0AIAdBA3QhMEEA
IQogDSEMIAYhEANAIAogEU8NAiALIA1rIjEgC0sNByAMIDGtNwMAIAsgMGohCyAKIAdqIQogDEEIaiEMIBBBf2oiEA0ACwsCQCAuRQ0AIA1B+P8DcQ0ECyAT
IBdqIjEgE0kNBSANIBNqIgsgDUkNBSALRQ0FAkAgGEEBdCIKRQ0AIAtBACAK/AsACyANIDFqIhAgDUkNBSAQRQ0FAkAgGUUNACAQIQsCQCAZQSRsIjBBXGoi
EUEkbiIKQQNxQQNGDQBBACAKQQFqQQNxayEKQQAoAry5yIAAIQxBACkCtLnIgAAhD0EAKQKsuciAACEyQQApAqS5yIAAITNBACkCnLnIgAAhNCAQIQsDQCAL
QSBqIAw2AgAgC0EYaiAPNwIAIAtBEGogMjcCACALQQhqIDM3AgAgCyA0NwIAIAtBJGohCyAKQQFqIgoNAAsLIBFB7ABJDQAgECAwaiEMQQAoAry5yIAAIQpB
ACkCtLnIgAAhD0EAKQKsuciAACEyQQApAqS5yIAAITNBACkCnLnIgAAhNANAIAtBIGogCjYCACALQRhqIA83AgAgC0EQaiAyNwIAIAtBCGogMzcCACALIDQ3
AgAgC0EkaiA0NwIAIAtBLGogMzcCACALQTRqIDI3AgAgC0E8aiAPNwIAIAtBxABqIAo2AgAgC0HIAGogNDcCACALQdAAaiAzNwIAIAtB2ABqIDI3AgAgC0Hg
AGogDzcCACALQegAaiAKNgIAIAtBjAFqIAo2AgAgC0GEAWogDzcCACALQfwAaiAyNwIAIAtB9ABqIDM3AgAgC0HsAGogNDcCACALQZABaiILIAxHDQALCwJA
QcIARQ0AIARBHGpBAEHCAPwLAAsgDSAjaiILIA1JDQUgC0UNBQJAICJFDQAgC0H/ASAi/AsACyAjICJqIjAgI0kNBSANIBxqIgsgDUkNBSALRQ0FAkAgG0UN
ACALQf8BIBv8CwALIBwgG2oiGyAcSQ0FIA0gHWoiC0EQaiIKQQNxDQUgC0EANgIMIAsgHjYCCCALIB9BcGo2AgQgCyAgQXBqNgIAAkAgHkUNACAKQQAgHvwL
AAsgCiANayIeIApLDQUgDSAraiILQQA2AgwgCyAqNgIIIAsgLEFwajYCBCALIC1BcGo2AgAgC0EQaiELAkAgKkUNACALQQAgKvwLAAsgCyANayIqIAtLDQUg
JCAnaiIRICRJDQUgDSAkaiILIA1JDQUgC0UNAwJAIChBAXQiCkUNACALQQAgCvwLAAsgDSARaiIQIA1JDQMgEEUNAyAIrSE0AkAgKUUNACAQIQsCQCApQRxs
Ih1BZGoiCEEcbiIKQQNxQQNGDQBBACAKQQFqQQNxayEKQQAoAti5yIAAIQxBACkC0LnIgAAhD0EAKQLIuciAACEyQQApAsC5yIAAITMgECELA0AgC0EYaiAM
NgIAIAtBEGogDzcCACALQQhqIDI3AgAgCyAzNwIAIAtBHGohCyAKQQFqIgoNAAsLIAhB1ABJDQAgECAdaiEMQQAoAti5yIAAIQpBACkC0LnIgAAhD0EAKQLI
uciAACEyQQApAsC5yIAAITMDQCALQRhqIAo2AgAgC0EQaiAPNwIAIAtBCGogMjcCACALIDM3AgAgC0EcaiAzNwIAIAtBJGogMjcCACALQSxqIA83AgAgC0E0
aiAKNgIAIAtBOGogMzcCACALQcAAaiAyNwIAIAtByABqIA83AgAgC0HQAGogCjYCACALQdQAaiAzNwIAIAtB3ABqIDI3AgAgC0HkAGogDzcCACALQewAaiAK
NgIAIAtB8ABqIgsgDEcNAAsLIAVBEHQhCyA0QiCGIQ8gCa0hMiADIBU7AVwgAyAWNgJYIAMgFzYCVCADQQA2AlAgAyAYNgJMIAMgGTYCSCADQQA2AkQgAyAx
NgJAIAMgEzYCPCADIB42AjggAyAbNgI0IAMgGjYCMCADIBw2AiwgAyAwNgIoIAMgITYCJCADICM2AiAgAyASNgIcIANBADYCGCADIC42AhQgAyANNgIQIARB
ADoAGAJAQcIARQ0AIANB4ABqIARBHGpBwgD8CgAACyALIBRyIQsgDyAyhCEPIAMgJTsByAEgAyAmNgLEASADICc2AsABIANBADYCvAEgAyAoNgK4ASADICk2
ArQBIANBADYCsAEgAyARNgKsASADICQ2AqgBIAMgKjYCpAEgA0EBOwGiAQJAQcoARQ0AIANBzAFqQQBBygD8CwALIAMgBjsBqgIgAyAHOwGoAiADIAs2AqQC
IAMgBjsBogIgAyAHOwGgAiADIA83ApgCIANBATsBlgIgAyAELQAYOgCsAiADIAEpAwA3AwAgA0EAOwGqAiADQgA3AgggASkDAEIBfCIPUA0DIAEgDzcDACAB
KAJcIgsgL2oiCiALSQ0DIAEgCjYCXCADLwGiAiACLwGiAkkNAyADLwGgAiACLwGgAkkNAyADIAIvAaoCIgs7AaoCIAMgAi8BqAI7AagCIAIvAaoCIgwgC0sN
AyACKAIQIgogAigCGGoiCyAKSQ0DIAtBB3ENAyALRQ0DIAMoAhAiDSADKAIYaiIKIA1JDQMgCkEHcQ0DIApFDQMgAkEQaiENIAxFDQIgA0EQaiEQDAELIAog
ERCIgICAAAALAkADQCAQIA0gCiALQQAgAy8BqAIQl4OAgAAiMUH//wNxDQEgCkEIaiEKIAtBCGohCyAMQX9qIgxFDQIMAAsLIAQgMTsBXiAEQd4AahCOhICA
AAALIAMgAi0ArAJBAXE6AKwCIAEoAnQiC0GAgICABE8NAAJAAkAgCw0AQQAhC0Gq1arVeiExDAELIAEoAmwiMUEDcQ0BIAEoAnAhCwsCQCALRQ0AIAtBAXEh
B0EAIQoCQCALQQFGDQAgC0F+cSEQQQAhCiAxIQsDQAJAIAsoAgAiDCgCACACRw0AIAwgAzYCAAsCQCALQQRqKAIAIgwoAgAgAkcNACAMIAM2AgALIAtBCGoh
CyAQIApBAmoiCkcNAAsLIAdFDQAgMSAKQQJ0aigCACILKAIAIAJHDQAgCyADNgIACyADIAI2AgwCQAJAIAIoAggiC0UNACADIAs2AgggCyADNgIMDAELIANB
ADYCCCABIAM2AlQLIAIgAzYCCCADIAIoAgwiCzYCDCACKAIIIQoCQAJAIAtFDQAgCyAKNgIIDAELIAEgCjYCWAsgASgCXCILIAIoAhRrIgogC0sNACABIAo2
AlwCQAJAIAIoAhQiC0GAgCRLDQACQCALRQ0AIA0oAgBBACAL/AsACyANKAIAIQsCQEGAgCRFDQAgC0GqAUGAgCT8CwALIAsgASgCPDYCACABIAs2AjwMAQsg
ASgCLCEMIAEoAjAhECANKAIAIQoCQCALRQ0AIApBqgEgC/wLAAsgDCAKIAtBEEEAIBAoAgwRgYCAgACAgICAAAsCQEGwAkUNACACQaoBQbAC/AsACyACIAEo
Aig2AgAgAEEAOwEEIAAgAzYCACABIAI2AigMAQsACyAEQaARaiSAgICAAA8LAAu6AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIE
QdgAIANrIgVqIgYgBEkNAiADQbzHyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBlMjJgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAg
BiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIp
AwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVB2ABJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICA
AAALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHcACADayIFaiIGIARJDQIgA0GUyMmAAGohBwJAAkAgBiAAKAIIIghLDQAg
BiAISw0FAkAgACgCBCAEaiIGQfDIyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgC
ACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQdwA
SQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC4oGAQV/I4CAgIAAQRBrIgMkgICAgAACQAJAAkACQAJAAkACQAJAIAJB
A3EOBAIAAgECCwJAAkACQAJAIAEtAJABDgQAAQIKAAsgASgCWCICRQ0JIAEvAY4BIQQDQAJAIARB//8DcSACLwGqAiIFSw0AIAUgBGshBgwECyAEIAVrIQQg
AigCCCICDQAMCgsLQQAhBiABKAJUIgJFDQgMAQsgASgCgAEiAi8BBCEGIAIoAgAhAgsgAS8BjgFBf2oiBEH//wNxIgcgBEcNBiAGQf//A3FBAWoiBUH//wNx
IAVHDQYgAi8BqgIgBUH//wNxayIFQf//A3EiASAFRw0GIARB//8DcSABSw0CIAcgBkH//wNxaiIEQf//AyAEQf//A0kbIQUMAwsgASgCWCICRQ0FIAEvAY4B
IQQCQANAIARB//8DcSACLwGqAiIFTQ0BIAQgBWshBCACKAIIIgJFDQcMAAsLAkAgBEH//wNxIAVGDQAgBSAEQX9zaiEFDAQLAkAgAigCCCICRQ0AQQEhBANA
AkAgBCACLwGqAiIFSw0AIAUgBGshBQwGCyAEIAVrIQQgAigCCCICDQALCyAAQgA3AgAgAEEIakIANwIADAQLIAEoAlgiAkUNBCAAIAI2AgAgAEEBOgAMIAIv
AaoCQX9qIgRB//8DcSAERw0EIAAgBDsBBCACLwGoAkF/aiICQf//A3EgAkcNBCAAQQA6AAggACACOwEGDAMLIAIoAgwiAkUNAyAEIAVrQf//A3EhBANAAkAg
BCACLwGqAiIFSw0AIARBf2oiBSAESw0FDAILIAQgBWshBCACKAIMIgJFDQQMAAsLIANBADoACCACLwGoAkF/aiIEQf//A3EgBEcNAiAAQQE6AAwgACADLQAI
NgIIIAAgBDsBBiAAIAU7AQQgACACNgIADAELIANBADoADCACLwGoAkF/aiIEQf//A3EgBEcNASAAQQE6AAwgACADLQAMNgIIIAAgBDsBBiAAIAU7AQQgACAC
NgIACyADQRBqJICAgIAADwsAC4EBAQN/AkACQCAAKAJYIgJFDQAgAC8BjgEhAANAIABB//8DcSACLwGqAiIDTQ0CIAAgA2shACACKAIIIgINAAsLAAsCQCAB
KAIAIgQgAkYNAAJAA0AgAigCDCICQQBHIQAgAiAERg0BIAINAAsLIAAPCyABLwEEIAMgAGtB//8DcU8L5gIDBH8BfgV/I4CAgIAAQSBrIgIkgICAgAACQCAA
RQ0AIABBA3ENAEEAIQMCQCAAQXxqKAIARQ0AIABBeGooAgAiBEF/aiIFIARLDQEgAkEQaiABrUKBgICAEH4iBkLb0dCFmtrfgWeFQgAgBkKsmuLTo9Lw+h+F
QgAQr4WAgAAgAiACKQMYQtvR0IWa2t+BZ4VCACACKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgACAFIAIpAwggAikDAIUiBqdxIgdqLQAAIghFDQAgBkI5iKch
CSAEQX9qIQogAEF0aiELA0AgCiEEAkAgCMBBf0oNACAJIAhB/wBxRw0AIAAgCygCAGoiCCAASQ0DIAhBA3ENAyAIRQ0DIAEgCCAHQQJ0aigCAEcNAEEBIQMM
AgsgB0EBaiIIRQ0CIAAgCCAFcSIHai0AACIIRQ0BIARBf2ohCiAEDQALCyACQSBqJICAgIAAIAMPCwALhgoCB38DfiOAgICAAEGgAWsiAySAgICAACACKAIQ
IQQgAigCDCEFIAItAAghBiACKAIEIQcgAigCACEIAkACQAJAAkACQAJAAkACQAJAIAENACAAIQEgAEUNAQsgASgCACECIANBOGpCADcDACADQeAAaiIJQgA3
AwAgA0HoAGpCADcDACADQfAAakIANwMAIANB+ABqQgA3AwAgA0GAAWpCADcDACADQYgBakIANwMAIANCADcDWCADQgA3AzAgA0KsmuLTo9Lw+h83A1AgA0Ks
muLTo9Lw+h83A0ggA0KsmuLTo9Lw+h83A0AgA0EBNgJYIAMgBkEBcSIGOgCQASADQeEAaiEBAkAgCSADQZABakEBak8NACADQZABaiABSQ0BCyADIAMtAJAB
OgBgIANBATYCXAJAAkAgBkUNACADQQU2AlgCQCABIANBlAFqTw0AIANBkAFqIANB5QBqSQ0DCyADQQU2AlwgAyAINgBhDAELIAIgCGoiASACSQ0BIAFFDQEg
A0EwaiABIAcQj4SAgAALIAIgBWoiASACSQ0AIAFFDQAgA0EwaiABIAQQj4SAgAAgAygCXCIAQTFPDQEgAykDQCEKAkACQAJAAkACQCADKAJYIgdBEEsNACAA
QRFPDQUgAEEDSw0BQgAhCyAADQNCACEMDAQLIAMpA1AhCyADKQNIIQwgA0GQAWpBCGpCqtWq1arVqtWqfzcDACADQqrVqtWq1arVqn83A5ABQQAhAgJAIABB
EE8NAAJAIAkgAGoiCEEgaiIBIANBkAFqQRAgAGsiAmoiBk8NACADQZABaiABIAJqSQ0GCwJAIAJFDQAgA0GQAWogASAC/AoAAAsCQCAJIANBoAFqTw0AIAYg
CEkNBgsCQCAARQ0AIAYgCSAA/AoAAAsgA0GQAWohCUEQIQALIAAgAmshBiAJIAJqIQggDCALhSAKhSEKQQAhAUEQIQIDQCACIAZPDQIgBiABSQ0KIAYgAWsi
BUEHTQ0LIAYgAUEIaiIFayIEQQdNDQwgAiEEIANBIGogCCAFaikAACAKhUIAIAggAWopAABC29HQhZra34FnhUIAEK+FgIAAIAJBEGoiASACSSEFIAMpAygg
AykDIIUhCiABIQIgBCEBIAVFDQAMBQsLIAAgAEEBdkEMcSICSQ0FIAAgAmsiAUEDTQ0GIABBfGoiBiACayIBIAZLDQMgACABSQ0HAkAgACABayIIQQNNDQAg
AzUCYEIghiAJIAJqNQIAhCEMIAkgBmo1AABCIIYgCSABajUAAIQhCwwDC0EEIAgQiICAgAAACyAAQXBqIgIgAEsNAgJAIAJBCGoiASAASw0AIAkgAmopAAAh
DCAJIABqQXhqKQAAIQsMAgsgASAAEIiAgIAAAAsgCSAAQQF2ajEAAEIIhiADMQBgQhCGhCAJIABqQX9qMQAAhCEMCyADQRBqIAxC29HQhZra34FnhUIAIAsg
CoVCABCvhYCAACADIAMpAxhC29HQhZra34FnhUIAIAetIAMpAxCFQq/I9cXHrIe7oH+FQgAQr4WAgAAgAykDACEKIAMpAwghCyADQaABaiSAgICAACALIAqF
DwsACyAAQTAQiICAgAAACyACIAAQioCAgAAAC0EEIAEQiICAgAAACyABIAAQioCAgAAACyABIAYQioCAgAAAC0EIIAUQiICAgAAAC0EIIAQQiICAgAAAC7QC
AQl/IAMoAhAhBCADKAIMIQUgAy0ACCEGIAMoAgQhByADKAIAIQggAigCECEJIAIoAgwhCiACLQAIIQMgAigCBCELIAIoAgAhDAJAAkACQCABRQ0AIAANAQwC
CyAAIQEgAEUNAQtBACECAkACQCADIAZzQQFxDQAgACgCACEAIAEoAgAhAQJAAkAgA0EBcUUNAAJAIAZBAXFFDQAgDCAIRg0CDAMLQQBBARCQhICAAAALIAEg
DGoiAyABSQ0DIANFDQMgBkEBcQ0CIAAgCGoiBiAASQ0DIAZFDQMgAyALIAYgBxCEgoCAAEEBcUUNAQsgASAKaiIDIAFJDQIgA0UNAiAAIAVqIgIgAEkNAiAC
RQ0CIAMgCSACIAQQhIKAgAAhAgsgAg8LQQFBABCQhICAAAALAAurAgECfwJAIANBH2oiBCADSQ0AIAEgACgCCGoiAyABSQ0AIANFDQAgAiADayIDIAJLDQAg
A0EfcQ0AIAEgACgCAGoiAiABSQ0AIAJBB3ENACACRQ0AIARBH00NACACIANBC3YiBUEDdGoiASABKQMAQn9BwAAgBEEFdiIAQcAAIANBBXZBP3EiAWsiAyAA
IANJGyIDa62IIAGthoQ3AwAgACADayIBIABLDQAgBUEBaiEDAkAgAUHBAEkNAAJAIAFBv39qQQN2Qfj///8BcUEIaiIARQ0AIAIgBUEDdGpBCGpB/wEgAPwL
AAsDQCADQQFqIQMgAUFAaiIBQcAASw0ACwsCQCABRQ0AIAIgA0EDdGoiAyADKQMAQn9BACABa0E/ca2IhDcDAAsPCwALnwYBCn8CQAJAAkACQAJAAkACQCAB
IAAoAgBqIgQgAUkNACAEQQFxDQAgBEUNACABIAAoAgRqIgUgAUkNACAFQQNxDQAgBUUNACAAKAIQIAUgAkH//wNxIgZBHGxqIgEvARQiAkkNBSAEIAJBAXRq
IgcvAQAgBkcNACADpyIGRQ0AIAEvARYhCCABKAIQIQkgASgCDCEKIAZBEGohCwJAIAEtAAhBAXENACAGKAIAIgwgASgCAGoiDSAMSQ0BIA1FDQEgCyAMIA0g
ASgCBBDwg4CAAAsgBigCACIGIApqIgogBkkNACAKRQ0AIAsgBiAKIAkQ8IOAgAAgCEEfSw0BIABBLmoiCyAIQQF0aiIILwEAQX9qIgZB//8DcSAGRw0AIAgg
BjsBACAHQQA7AQAgAUEYakEAKALYuciAADYCACABQRBqQQApAtC5yIAANwIAIAFBCGpBACkCyLnIgAA3AgAgAUEAKQLAuciAADcCACAEIAAvASAgAkEBanEi
CEH//wNxQQF0aiIBLwEAIgpFDQMDQCAIIQYgBSAKQf//A3FBHGxqIggvARZFDQQgCEEUaiACOwEAIAUgAS8BAEEcbGovARYiCEEgTw0DIAsgCEEBdGoiCi8B
AEF/aiIIQf//A3EgCEcNASAKIAg7AQAgBSABLwEAQRxsaiIKLwEWQX9qIghB//8DcSAIRw0BIApBFmogCDsBAAJAIAUgAS8BAEEcbGovARYiCEEfSw0AIAsg
CEEBdGoiCi8BAEEBaiIIQf//A3EgCEcNAiAKIAg7AQAgBCACQf//A3FBAXRqIAEvAQA7AQAgBiECIAQgAC8BICAGQQFqcSIIQf//A3FBAXRqIgEvAQAiCg0B
DAYLCyAIQSAQiICAgAALAAsgCEEgEIiAgIAAAAsgCEEgEIiAgIAAAAsgAiEGCwJAIAAvASwiAUUNACABQQF0IABqQS5qIQUDQCABQR9LDQMgBS8BAA0BIAAg
AUF/aiIBOwEsIAVBfmohBSABQf//A3ENAAsLIAQgBkH//wNxQQF0akEAOwEACw8LIAFBIBCIgICAAAALkwgDBX8Bfgh/I4CAgIAAQTBrIgUkgICAgAAgBUEM
aiAAIAEgAiAEEKiDgIAAAkACQAJAAkACQAJAAkACQCAFLQAODQAgASAAKAIAaiIGIAFJDQAgBkEBcQ0AIAZFDQAgASAAKAIEaiIHIAFJDQAgB0EDcQ0AIAdF
DQAgBUEQakEQaiACQRBqKAIANgIAIAVBEGpBCGogAkEIaikCADcDACAFQQA7ASggBUH//wM2AiQgBSACKQIANwMQIASnIgggBEIgiKcgAhDug4CAACEEIAAo
AhAiAkF/aiIBIAJLDQAgAUUNByAAQS5qIQkgAa0hCiAFQRBqIQJBACEBIAMhCwNAAkAgBiAALwEgIASncSIMQf//A3FBAXRqIg0vAQAiDg0AIA0gCzsBACAC
IAw7ARQgAi8BFiIBQSBPDQMgCSABQQF0aiIPLwEAQQFqIgFB//8DcSABRw0CIA8gATsBACAALwEsIgEgAi8BFiICIAEgAksbIQEMCAsCQCAHIA5BHGxqIg8v
ARgiEEUNAAJAAkAgDy8BFiIRIAFB//8DcSISSQ0AIBEgEkcNASAQIAIvARhPDQELIA0gCzsBACACIAw7ARQgAi8BFiIBQR9LDQYgCSABQQF0aiIMLwEAQQFq
IgFB//8DcSABRw0DIAwgATsBACAAIAAvASwiASACLwEWIgIgASACSxs7ASwgDy8BFiIBQR9LDQcgCSABQQF0aiICLwEAQX9qIgFB//8DcSABRw0DIAIgATsB
ACAPLwEWIQEgDiELIA8hAgsgAUH//wNxQQFqIgFB//8DcSABRw0CIAIgATsBFiAEQgF8IQQgCkJ/fCIKUEUNAQwJCwsgCCAPEJGEgIAAIA8vARYiAUEgTw0F
IAkgAUEBdGoiES8BAEF/aiIBQf//A3EgAUcNACARIAE7AQAgD0EYakEAKALYuciAADYCACAPQRBqQQApAtC5yIAANwIAIA9BCGpBACkCyLnIgAA3AgAgD0EA
KQLAuciAADcCACANIAs7AQAgAiAMOwEUIAIvARYiAUEfSw0CIAkgAUEBdGoiDy8BAEEBaiIBQf//A3EgAUcNACAOIANB//8DcSIMIA4gDEkbIQMgDyABOwEA
IAAvASwiASACLwEWIgIgASACSxshAQwGCwALIAFBIBCIgICAAAALIAFBIBCIgICAAAALIAFBIBCIgICAAAALIAFBIBCIgICAAAALIAFBIBCIgICAAAALIAAg
ATsBLAsgBiAFLwEkQQF0aiADOwEAIAcgA0H//wNxQRxsaiIBIAUpAxA3AgAgAUEIaiAFQRBqQQhqKQMANwIAIAFBEGogBUEQakEQaikDADcCACABQRhqIAVB
EGpBGGooAgA2AgAgBUEwaiSAgICAACADC/4EAgZ/AXwjgICAgABBEGsiBSSAgICAAAJAIAIgASgCBGoiBiACSQ0AIAZBA3ENACAGRQ0AIAQoAgAhBwJAIAEv
AW4iCEECSQ0AA0AgBiAIQX9qIghB//8DcUEcbGovARgNASABIAg7AW4gASACIAggBCkCABDxg4CAACABLwFuIghBAUsNAAsLIAVBDGogASACIAMgBCkCABCo
g4CAAAJAAkACQCAFLQAORQ0AIAdFDQMgAygCECEJIAMoAgwhCCAFLwEMIQIgB0EQaiEEIAMtAAhBAXENASAHKAIAIgEgAygCAGoiCiABSQ0DIApFDQMgBCAB
IAogAygCBBDwg4CAAAwBCwJAIAEvAWxFDQAgAEGAgOQANgEADAILAkAgASgCDCIIIAEvAW4iB0sNACAIuETNzMzMzMzsP6IiC0QAAAAAAADwv2UNAyALRAAA
AAAAAPBAZg0DAkAgASgCCCAL/ANPDQAgAEGAgJgBNgEADAMLIABBgIDkADYBAAwCCyAGIAEgAiADIAcgBCkCABDyg4CAACICQf//A3FBHGxqIgYvARhBAWoi
CEH//wNxIgQgCEcNAiAGQRhqIAg7AQAgBEEBRw0CIAEoAghBAWoiCEUNAiABIAg2AggCQCACQf//A3EiCCABLwFuRw0AIAhBAWoiCEH//wNxIAhHDQMgASAI
OwFuCyAAQQA7AQIgACACOwEADAELIAcoAgAiASAIaiIIIAFJDQEgCEUNASAEIAEgCCAJEPCDgIAAIAYgAkEcbGoiCC8BGEEBaiIBQf//A3EgAUcNASAAQQA7
AQIgACACOwEAIAhBGGogATsBAAsgBUEQaiSAgICAAA8LAAu2BQEGfwJAAkACQAJAAkACQAJAIAEgACgCAGoiAyABSQ0AIANBAXENACADRQ0AIAEgACgCBGoi
BCABSQ0AIARBA3ENACAERQ0AIAAoAhAgBCACQf//A3EiBUEkbGoiAS8BHCICSQ0FIAMgAkEBdGoiBi8BACAFRw0AIAEvAR4iBUEfSw0BIABBJmoiByAFQQF0
aiIILwEAQX9qIgVB//8DcSAFRw0AIAggBTsBACAGQQA7AQAgAUEgakEAKAK8uciAADYCACABQRhqQQApArS5yIAANwIAIAFBEGpBACkCrLnIgAA3AgAgAUEI
akEAKQKkuciAADcCACABQQApApy5yIAANwIAIAMgAC8BICACQQFqcSIGQf//A3FBAXRqIgEvAQAiCEUNAwNAIAYhBSAEIAhB//8DcUEkbGoiBi8BHkUNBCAG
QRxqIAI7AQAgBCABLwEAQSRsai8BHiIGQSBPDQMgByAGQQF0aiIILwEAQX9qIgZB//8DcSAGRw0BIAggBjsBACAEIAEvAQBBJGxqIggvAR5Bf2oiBkH//wNx
IAZHDQEgCEEeaiAGOwEAAkAgBCABLwEAQSRsai8BHiIGQR9LDQAgByAGQQF0aiIILwEAQQFqIgZB//8DcSAGRw0CIAggBjsBACADIAJB//8DcUEBdGogAS8B
ADsBACAFIQIgAyAALwEgIAVBAWpxIgZB//8DcUEBdGoiAS8BACIIDQEMBgsLIAZBIBCIgICAAAsACyAFQSAQiICAgAAACyAGQSAQiICAgAAACyACIQULAkAg
AC8BJCIBRQ0AIAFBAXQgAGpBJmohBANAIAFBH0sNAyAELwEADQEgACABQX9qIgE7ASQgBEF+aiEEIAFB//8DcQ0ACwsgAyAFQf//A3FBAXRqQQA7AQALDwsg
AUEgEIiAgIAAAAu0BQMCfwV+Dn8CQAJAAkAgAiABKAIAaiIEIAJJDQAgBEEBcQ0AIARFDQAgAiABKAIEaiIFIAJJDQAgBUEDcQ0AIAVFDQAgAxCbhICAACEG
IAEvASRBAWoiAkH//wNxIAJHDQAgAq1C//8DgyEHIAEzASAhCEIAIQkDQAJAIAQgBiAJfCAIg6dBAXRqLwEAIgENACAAQQA2AQAPCwJAIAkgBSABQSRsaiIC
MwEeIgpYDQAgAEEANgEADwsCQCAJIApSDQAgAi8BIEH//wNxRQ0AIAMvARggAi8BGEH//wNxRw0AIAMtAARB/wFxIAItAAQiC0cNACACQRJqLQAAIQwgAi8B
ECENIAJBCmotAAAhDiACLwEIIQ8gAi8BACACQQJqLQAAQRB0ciEQIAItABQhESACLQAMIRIgA0ESai0AACETIAMvARAhFCADQQpqLQAAIRUgAy8BCCEWIAMv
AQAgA0ECai0AAEEQdHIhFyADLQAUIRggAy0ADCECAkACQAJAAkAgCw4DAgABBgsgEEH/AXEgF0H/AXFHDQMgAiASQf8BcUYNAgwDCyAXQf///wdxIBBB////
B3FHDQIgAiASQf8BcUYNAQwCCyACIBJB/wFxRw0BCyAPIA5BEHRyIQsgFiAVQRB0ciESAkACQAJAAkAgAg4DAgABBgsgC0H/AXEgEkH/AXFHDQMgGCARQf8B
cUYNAgwDCyASQf///wdxIAtB////B3FHDQIgGCARQf8BcUYNAQwCCyAYIBFB/wFxRw0BCyANIAxBEHRyIQIgFCATQRB0ciELAkACQCAYDgMFAQAECyALQf//
/wdxIAJB////B3FHDQEMBAsgAkH/AXEgC0H/AXFGDQMLIAcgCUIBfCIJUQ0DDAALCwALIABBAToAAiAAIAE7AQAPCyAAQQA2AQALtAgFA38BfgF/AX4IfyOA
gICAAEEwayIEJICAgIAAIARBBGogACABIAIQ9YOAgAACQAJAAkACQAJAAkACQAJAIAQtAAYNACABIAAoAgBqIgUgAUkNACAFQQFxDQAgBUUNACABIAAoAgRq
IgYgAUkNACAGQQNxDQAgBkUNACAEQQhqQRhqIAJBGGooAgA2AgAgBEEIakEQaiACQRBqKQIANwMAIARBCGpBCGogAkEIaikCADcDACAEQQA7ASggBEH//wM2
AiQgBCACKQIANwMIIAIQm4SAgAAhByAAKAIQIgFBf2oiAiABSw0AIAJFDQcgAEEmaiEIIAKtIQkgBEEIaiEBQQAhAiADIQoDQAJAIAUgAC8BICAHp3EiC0H/
/wNxQQF0aiIMLwEAIg0NACAMIAo7AQAgASALOwEcIAEvAR4iAkEgTw0DIAggAkEBdGoiDi8BAEEBaiICQf//A3EgAkcNAiAOIAI7AQAgAC8BJCICIAEvAR4i
ASACIAFLGyECDAgLIAYgDUEkbGoiDi8BHiEPAkAgDi8BICIQRQ0AAkACQCAPIAJB//8DcSIRSQ0AIA8gEUcNASAQIAEvASBPDQELIAwgCjsBACABIAs7ARwg
AS8BHiICQR9LDQYgCCACQQF0aiIPLwEAQQFqIgJB//8DcSACRw0DIA8gAjsBACAAIAAvASQiAiABLwEeIgEgAiABSxs7ASQgDi8BHiICQR9LDQcgCCACQQF0
aiIBLwEAQX9qIgJB//8DcSACRw0DIAEgAjsBACAOLwEeIQIgDSEKIA4hAQsgAkH//wNxQQFqIgJB//8DcSACRw0CIAEgAjsBHiAHQgF8IQcgCUJ/fCIJUEUN
AQwJCwsgD0EgTw0FIAggD0EBdGoiDy8BAEF/aiICQf//A3EgAkcNACAPIAI7AQAgDkEgakEAKAK8uciAADYCACAOQRhqQQApArS5yIAANwIAIA5BEGpBACkC
rLnIgAA3AgAgDkEIakEAKQKkuciAADcCACAOQQApApy5yIAANwIAIAwgCjsBACABIAs7ARwgAS8BHiICQR9LDQIgCCACQQF0aiIOLwEAQQFqIgJB//8DcSAC
Rw0AIA0gA0H//wNxIg8gDSAPSRshAyAOIAI7AQAgAC8BJCICIAEvAR4iASACIAFLGyECDAYLAAsgAkEgEIiAgIAAAAsgAkEgEIiAgIAAAAsgAkEgEIiAgIAA
AAsgAkEgEIiAgIAAAAsgD0EgEIiAgIAAAAsgACACOwEkCyAFIAQvASRBAXRqIAM7AQAgBiADQf//A3FBJGxqIgIgBCkDCDcCACACQQhqIARBCGpBCGopAwA3
AgAgAkEQaiAEQQhqQRBqKQMANwIAIAJBGGogBEEIakEYaikDADcCACACQSBqIARBCGpBIGooAgA2AgAgBEEwaiSAgICAACADC7gCAgd/AX4jgICAgABBEGsi
AiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBMCADayIFaiIGIARJDQIgA0H85siAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQazn
yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEI
aiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTBJDQALIAAgAS8BABCfhICAACEFCyAC
QRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHBACADayIFaiIGIARJDQIgA0Gs
58iAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQe3nyIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVq
IgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmn
IQULIAMgBWoiBSADSQ0CIAUhAyAFQcEASQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC5UGAwJ/AX4CfyOAgICAAEHA
EGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQkoSAgABB
//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQTJBAEEAIANCIIin
KAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq1arVqtWqfzcAACACQTBqQarVfjsAACACQShq
QqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAAIAJBGGpCqtWq1arVqtWqfzcAACABQjI3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJA
AkAgAUGIEGpBCGogABCShICAAEH//wNxDgMABAEECyABKAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAA
NgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiAgIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAh
BCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQEC
QCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAADAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsg
BCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGokgICAgAAPCyAAIAIQiICAgAAACwALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANA
IAAoAgwiBEHYACADayIFaiIGIARJDQIgA0Gr2cmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQYPayYAATw0AIAcgBiAFakkNBQsC
QCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACA
gICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQdgASQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAG
IAgQiICAgAAAC/kIAgd/AX4jgICAgABB4AFrIgQkgICAgAACQCACLwEAIgUgAS8BjAFPDQAgAigCBCEGIARB0AFqIAEgAi0ACBC6g4CAACAELwHUASIHQQFq
IghB//8DcSIJIAhHDQAgBCgC0AEiCC8BqgIgCWsiCUH//wNxIAlHDQACQAJAIAYgCUH//wNxIglLDQAgBiAHaiIJQf//AyAJQf//A0kbIQcMAQsgCCgCDCII
RQ0BIAYgCWshCQJAA0AgCSAILwGqAiIGTQ0BIAkgBmshCSAIKAIMIggNAAwDCwsgCUF/aiIHIAlLDQELAkACQAJAAkAgAy0ADEUNACADLwEAIgogAS8BjAFP
DQQgAygCBCECIARB0AFqIAEgAy0ACBC6g4CAACAELwHUASIBQQFqIglB//8DcSIGIAlHDQQgBCgC0AEiCS8BqgIgBmsiBkH//wNxIAZHDQQCQAJAIAIgBkH/
/wNxIgZLDQAgAiABaiIGQf//AyAGQf//A0kbIQEMAQsgCSgCDCIJRQ0FIAIgBmshBgJAA0AgBiAJLwGqAiIBTQ0BIAYgAWshBiAJKAIMIglFDQcMAAsLIAZB
f2oiASAGSw0FCyAEQQA2AgwgBCAKOwEKIAQgATsBCCAEIAk2AgQgBEEAOgDcASAEQQRqIQkMAQsgBEEUaiABIAItAAgQ64OAgAAgBEEUaiEJIAQtACBFDQEL
IARBJGpBGGoiBiAJQQhqKAIANgIAIARBAToASCAEQQE6ADAgBEEANgIsIAQgBTsBKiAEIAc7ASggBCAINgIkIARBAjoATCAEQQI6AEAgBEEBOgBEIAQgCSkC
ADcCNCAEQdAAaiAEQSRqEKCDgIAAAkACQCAELQBYRQ0AIARBmAFqQSBqIARBJGpBIGooAgA2AgAgBEGYAWpBGGogBikCADcDACAEQZgBakEQaiAEQSRqQRBq
KQIANwMAIARBmAFqQQhqIARBJGpBCGopAgA3AwAgBCAEKQIkNwOYASAEQQE6AMQBIAQgBCkDUCILNwK8ASAEIAtCIIg9AcgBIARBmAFqIQgMAQsgBEGKAWpC
ADcBACAEQeAAakEgaiAEQSRqQSBqKAIANgIAIARB4ABqQRhqIAYpAgA3AwAgBEHgAGpBEGogBEEkakEQaikCADcDACAEQeAAakEIaiAEQSRqQQhqKQIANwMA
IAQgBCkCJDcDYCAEQgA3AoQBIARB4ABqIQgLIAAgCCkCADcCACAAQTBqIAhBMGooAgA2AgAgAEEoaiAIQShqKQIANwIAIABBIGogCEEgaikCADcCACAAQRhq
IAhBGGopAgA3AgAgAEEQaiAIQRBqKQIANwIAIABBCGogCEEIaikCADcCAAwBCyAAQqrVqtWq1arVqn83AgAgAEIANwIkIABBIGpBqtWq1Xo2AgAgAEEYakKq
1arVqtWq1ap/NwIAIABBEGpCqtWq1arVqtWqfzcCACAAQQhqQqrVqtWq1arVqn83AgAgAEEqakIANwEACyAEQeABaiSAgICAAA8LAAvrGQcOfwF8AX4YfwF+
BH8DfiOAgICAAEHgAGsiAySAgICAACAAKAIQIQQCQAJAAkACQCABKAIoIgVFDQAgASAFKAIANgIoDAELQRkhBkEADQEgAUEYakGwAkEDIAIQj4CAgAAiBUUN
AQJAQbACRQ0AIAVBqgFBsAL8CwALIAVBB3ENAgsCQEGwAkUNACAFQaoBQbAC/AsACyACLwEIIgcgAi8BCiIIbCIJQYCAgIACTw0BIAhBA3QiCiAJQQN0aiIL
IApJDQECQAJAIAIvAQwiBg0AQQAhDEEAIQ1BACEOQQAhD0EAIRAMAQtCAUEgIAZBf2pnIgZrrUL//wODhqciDbhEAAAAAAAA6j+iIhFEAAAAAAAA8L9lDQIg
EUQAAAAAAADwQWYNAiAR/AMiDK1CJH4iEkIgiKcNAiANQQF0QQNqQfz///8HcSIOIBKnaiIPIA5JDQJBf0EAIAZrdEF/cyEQCyALIA9qIhMgC0kNASACKAIA
IhRBD2oiBiAUSQ0BIAZBgICAgAFPDQEgBkEEdkE/akEGdiIVQQN0IhYgBkEEdEGAfnFqIgYgFkkNASATQQdqIhcgE0kNASAXQXhxIhggBmoiGSAYSQ0BAkAC
QCAUDQBBACEaDAELQgFBICAUQX9qQQR2Z2utQv//A4OGpyEaCyAZIBpBE2pBfHEiGyAaQQJ0aiIcIBpBA3RqaiIUIBlJDQEgAigCBCITQR9qIgYgE0kNASAG
QYCAgMAATw0BIAZBBXZBP2pBBnYiHUEDdCIeIAZBBXRBgHhxaiIGIB5JDQEgFEEHaiITIBRJDQEgE0F4cSIfIAZqIiAgH0kNASACLwEOIhRBHG4hE0EAISEg
ICEGQQAhIkEAISNBACEkQQAhJUEAISYCQCAUQRxJDQBCAUEgIBNBf2pnIhRrrUL//wODhqciJLhEAAAAAAAA6j+iIhFEAAAAAAAA8L9lDQIgEUQAAAAAAADw
QWYNAiAR/AMiJa1CHH4iEkIgiKcNAiAkQQF0QQNqQfz///8HcSIjIBKnaiIiICNJDQIgICAiaiIGICBJDQJBf0EAIBRrdEF/cyEhQgFBICATQQR0QX9qZ2ut
hqchJgsgBkEDaiIUIAZJDQEgFEF8cSInICZBAXQgJkETakF8cSIoICZBAnRqIilqQQNqQXxxaiIGICdJDQEgBkH//wNqIhQgBkkNASABQSxqIQYgAigCDCEq
IAIpAgAhKwJAAkACQAJAIBRBgIB8cSIsQYGAJEkNAEEADQEgBikCACISpyAsQRBBACASQiCIpygCABGAgICAAICAgIAAIhNFDQECQCAsRQ0AIBNBqgEgLPwL
AAsgLCEtIBNB//8DcQ0FDAMLAkACQAJAIAEoAjwiE0UNACABIBMoAgA2AjwMAQtBAA0BIAZBgIAkQRAgAhCPgICAACITRQ0BAkBBgIAkRQ0AIBNBqgFBgIAk
/AsACyATQf//A3ENBgtBgIAkIS1BgIAkRQ0DIBNBqgFBgIAk/AsADAMLAkBBsAJFDQAgBUGqAUGwAvwLAAsgBSABKAIoNgIADAELAkBBsAJFDQAgBUGqAUGw
AvwLAAsgBSABKAIoNgIACyABIAU2AihBGSEGDAELAkAgLUUNACATQQAgLfwLAAsgEyAKaiICIBNJDQEgAkEHcQ0BIAJFDQEgE0EHcQ0BAkACQAJAIAhFDQAg
B0EDdCEuQQAhBiATIRQgCCEXA0AgBiAJTw0CIAIgE2siLyACSw0FIBQgL603AwAgAiAuaiECIAYgB2ohBiAUQQhqIRQgF0F/aiIXDQALCwJAICxFDQAgE0H4
/wNxDQQLIAsgDmoiLyALSQ0DIBMgC2oiAiATSQ0DIAJFDQMCQCANQQF0IgZFDQAgAkEAIAb8CwALIBMgL2oiFyATSQ0DIBdFDQMCQCAMRQ0AIBchAgJAIAxB
JGwiLkFcaiIJQSRuIgZBA3FBA0YNAEEAIAZBAWpBA3FrIQZBACgCvLnIgAAhFEEAKQK0uciAACESQQApAqy5yIAAITBBACkCpLnIgAAhMUEAKQKcuciAACEy
IBchAgNAIAJBIGogFDYCACACQRhqIBI3AgAgAkEQaiAwNwIAIAJBCGogMTcCACACIDI3AgAgAkEkaiECIAZBAWoiBg0ACwsgCUHsAEkNACAXIC5qIRRBACgC
vLnIgAAhBkEAKQK0uciAACESQQApAqy5yIAAITBBACkCpLnIgAAhMUEAKQKcuciAACEyA0AgAkEgaiAGNgIAIAJBGGogEjcCACACQRBqIDA3AgAgAkEIaiAx
NwIAIAIgMjcCACACQSRqIDI3AgAgAkEsaiAxNwIAIAJBNGogMDcCACACQTxqIBI3AgAgAkHEAGogBjYCACACQcgAaiAyNwIAIAJB0ABqIDE3AgAgAkHYAGog
MDcCACACQeAAaiASNwIAIAJB6ABqIAY2AgAgAkGMAWogBjYCACACQYQBaiASNwIAIAJB/ABqIDA3AgAgAkH0AGogMTcCACACQewAaiAyNwIAIAJBkAFqIgIg
FEcNAAsLAkBBwgBFDQAgA0EMakEAQcIA/AsACyATIB9qIgIgE0kNAyACRQ0DAkAgHkUNACACQf8BIB78CwALIB8gHmoiLiAfSQ0DIBMgGGoiAiATSQ0DIAJF
DQMCQCAWRQ0AIAJB/wEgFvwLAAsgGCAWaiIWIBhJDQMgEyAZaiICQRBqIgZBA3ENAyACQQA2AgwgAiAaNgIIIAIgG0FwajYCBCACIBxBcGo2AgACQCAaRQ0A
IAZBACAa/AsACyAGIBNrIhogBksNAyATICdqIgJBADYCDCACICY2AgggAiAoQXBqNgIEIAIgKUFwajYCACACQRBqIQICQCAmRQ0AIAJBACAm/AsACyACIBNr
IiYgAksNAyAgICNqIgkgIEkNAyATICBqIgIgE0kNAyACRQ0DAkAgJEEBdCIGRQ0AIAJBACAG/AsACyATIAlqIhcgE0kNAyAXRQ0DAkAgJUUNACAXIQICQCAl
QRxsIh5BZGoiGUEcbiIGQQNxQQNGDQBBACAGQQFqQQNxayEGQQAoAti5yIAAIRRBACkC0LnIgAAhEkEAKQLIuciAACEwQQApAsC5yIAAITEgFyECA0AgAkEY
aiAUNgIAIAJBEGogEjcCACACQQhqIDA3AgAgAiAxNwIAIAJBHGohAiAGQQFqIgYNAAsLIBlB1ABJDQAgFyAeaiEUQQAoAti5yIAAIQZBACkC0LnIgAAhEkEA
KQLIuciAACEwQQApAsC5yIAAITEDQCACQRhqIAY2AgAgAkEQaiASNwIAIAJBCGogMDcCACACIDE3AgAgAkEcaiAxNwIAIAJBJGogMDcCACACQSxqIBI3AgAg
AkE0aiAGNgIAIAJBOGogMTcCACACQcAAaiAwNwIAIAJByABqIBI3AgAgAkHQAGogBjYCACACQdQAaiAxNwIAIAJB3ABqIDA3AgAgAkHkAGogEjcCACACQewA
aiAGNgIAIAJB8ABqIgIgFEcNAAsLIAUgEDsBXCAFIA82AlggBSAONgJUIAVBADYCUCAFIA02AkwgBSAMNgJIIAVBADYCRCAFIC82AkAgBSALNgI8IAUgGjYC
OCAFIBY2AjQgBSAVNgIwIAUgGDYCLCAFIC42AiggBSAdNgIkIAUgHzYCICAFIAo2AhwgBUEANgIYIAUgLDYCFCAFIBM2AhAgA0EAOgAIAkBBwgBFDQAgBUHg
AGogA0EMakHCAPwKAAALIAUgITsByAEgBSAiNgLEASAFICM2AsABIAVBADYCvAEgBSAkNgK4ASAFICU2ArQBIAVBADYCsAEgBSAJNgKsASAFICA2AqgBIAUg
JjYCpAEgBUEBOwGiAQJAQcoARQ0AIAVBzAFqQQBBygD8CwALIAUgCDsBqgIgBSAHOwGoAiAFICo2AqQCIAUgCDsBogIgBSAHOwGgAiAFICs3ApgCIAVBATsB
lgIgBSADLQAIOgCsAiAFIAEpAwA3AwAgBUEAOwGqAiAFQgA3AgggASkDAEIBfCISUA0DIAEgEjcDACABKAJcIgIgLWoiBiACSQ0DIAEgBjYCXCAFQQE7AaoC
IAUgACgCACICNgIIAkAgAigCDCIGRQ0AIAUgBjYCDCAGIAU2AggMAgsgBUEANgIMIAEgBTYCWAwBCyAGIAkQiICAgAAACyACIAU2AgwgBSgCECIGIAUoAhhq
IgIgBkkNASACQQdxDQEgAkUNASADQQA6AFAgBiACKAIAaiIUIAZJDQEgFEEHcQ0BIBRFDQEgBS8BqgIhE0EAIQYgAEEANgIYIAAgEzYCFCAAIBQ2AgwgACAC
NgIIIAAgBUEQajYCBCAAIAU2AgAgACADLQBQOgAcIAAgBDYCEAsgA0HgAGokgICAgAAgBg8LAAvZAgEHfyOAgICAAEEQayIDJICAgIAAIAAoAhQhBCAALwEa
IQUgAC8BGCEGIANBBGogASAAKAIAIAIQ6IOAgAACQAJAIAMvAQgiBw0AIAMoAgQiAigCECIIIAIoAhhqIgEgCEkNASABQQdxDQEgAUUNASADQQA6AAwgCCAB
KAIAaiIJIAhJDQEgCUEHcQ0BIAlFDQEgAi8BqgIhCCAAQQA2AhggACAINgIUIABBADYCECAAIAk2AgwgACABNgIIIAAgAkEQaiIINgIEIAAgAjYCACAAIAMt
AAw6ABwgBiACLwGoAk8NASAFIAIvAaoCTw0BIAAgASAFQQN0aiICNgIIIAgoAgAiASACKAIAaiICIAFJDQEgAkEHcQ0BIAJFDQEgAEEAOgAcIAAgBTsBGiAA
IAY7ARggACAENgIUIAAgAiAGQQN0ajYCDAsgA0EQaiSAgICAACAHDwsAC60FAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8
CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYCjBACQAJAAkACQAJAIAFBiBBqIAAQqYSAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJN
DQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtBAA0AQQApA+D6yYAAIgOnQcQAQQBBACADQiCIpygCABGAgICAAICAgIAAIgJFDQACQEHEAEUNACAC
QaoBQcQA/AsACyABQsQANwOYECABIAI2ApQQIAFB3JXHgAA2ApAQIAEgAzcDiBACQAJAIAFBkBBqIAAQqYSAgABB//8DcQ4DAAQBBAsgASgCnBAiACABKAKY
ECICSw0CIAFCADcDmBAgASgClBAhBCABQarVqtV6NgKUECABIAI2ArwQIAEgADYCuBAgASAENgK0ECABQaQQaiABQbQQaiABQYgQahC4gICAACABIAEoArwQ
IgA2ApgQIAEgASgCtBAiAjYClBAgASABKAK4EDYCnBACQCAARQ0AIAEoAogQIQQgASgCjBAhBQJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGB
gICAAICAgIAACyABLwGsEA0BIAEoAqQQIgIgASgCqBAiABCAgICAACAARQ0BAkAgAEUNACACQaoBIAD8CwALIAEgAiAAQQAgARC5gICAAAwBCyABKAKYECIA
RQ0AIAEoAogQIQQgASgCjBAhBSABKAKUECECAkAgAEUNACACQaoBIAD8CwALIAQgAiAAQQBBACAFKAIMEYGAgIAAgICAgAALIAFBwBBqJICAgIAADwsgACAC
EIiAgIAAAAsAC68GAwJ/AX4CfyOAgICAAEHAEGsiASSAgICAAAJAQYAQRQ0AIAFBCGpBqgFBgBD8CwALIAFCgBA3ApAQIAFBuN7BgAA2AogQIAEgAUEIajYC
jBACQAJAAkACQAJAIAFBiBBqIAAQqoSAgABB//8DcQ4DAAQBBAsCQCABKAKUECIAIAEoApAQIgJNDQAgACACEIiAgIAADAQLIAEoAowQIAAQgICAgAAMAQtB
AA0AQQApA+D6yYAAIgOnQT1BAEEAIANCIIinKAIAEYCAgIAAgICAgAAiAkUNACACQqrVqtWq1arVqn83AAAgAkEQakKq1arVqtWq1ap/NwAAIAJBCGpCqtWq
1arVqtWqfzcAACACQTVqQqrVqtWq1arVqn83AAAgAkEwakKq1arVqtWq1ap/NwAAIAJBKGpCqtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEY
akKq1arVqtWq1ap/NwAAIAFCPTcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQYgQakEIaiAAEKqEgIAAQf//A3EOAwAEAQQLIAEoApwQ
IgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAg
ASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEA
IAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsg
ASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICA
AA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECAB
IAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEKuEgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICA
gIAADAELQQANAEEAKQPg+smAACIDp0HIAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBByABFDQAgAkGqAUHIAPwLAAsgAULIADcDmBAgASACNgKU
ECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEKuEgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq
1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgC
uBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECIC
IAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAh
AgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABB
wBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEKyEgIAA
Qf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HEAEEAQQAgA0Ig
iKcoAgARgICAgACAgICAACICRQ0AAkBBxABFDQAgAkGqAUHEAPwLAAsgAULEADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAA
EKyEgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAg
AUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAA
RQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA
/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgC
DBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsA
CyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQAkACQAJAAkACQCABQYgQaiAAEK2EgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0A
IAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQANAEEAKQPg+smAACIDp0HBAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBwQBFDQAgAkGq
AUHBAPwLAAsgAULBADcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkACQCABQZAQaiAAEK2EgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAi
AksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIA
NgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCA
gACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUN
ACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCI
gICAAAALAAutBQMCfwF+An8jgICAgABBwBBrIgEkgICAgAACQEGAEEUNACABQQhqQaoBQYAQ/AsACyABQoAQNwKQECABQbjewYAANgKIECABIAFBCGo2AowQ
AkACQAJAAkACQCABQYgQaiAAEK6EgIAAQf//A3EOAwAEAQQLAkAgASgClBAiACABKAKQECICTQ0AIAAgAhCIgICAAAwECyABKAKMECAAEICAgIAADAELQQAN
AEEAKQPg+smAACIDp0HKAEEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AAkBBygBFDQAgAkGqAUHKAPwLAAsgAULKADcDmBAgASACNgKUECABQdyVx4AA
NgKQECABIAM3A4gQAkACQCABQZAQaiAAEK6EgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAg
ASACNgK8ECABIAA2ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAg
AEUNACABKAKIECEEIAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQ
gICAgAAgAEUNAQJAIABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAg
AkGqASAA/AsACyAEIAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAu4AgIHfwF+I4CAgIAAQRBrIgIkgICAgABB
ACEDAkACQAJAA0AgACgCDCIEIANBP3MiBWoiBiAESQ0CIANB7efIgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGs6MiAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUE/SQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICA
ACAFDwsACyAGIAgQiICAgAAAC/cFAgd/AX4jgICAgABBsCBrIgIkgICAgAACQEGPIEUNACACQQFqQaoBQY8g/AsACyACQoAgNwKYICACQbjewYAANgKQICAC
IAJBAWo2ApQgQQAhAwJAAkACQAJAA0AgAigCnCAiBEEcIANrIgVqIgYgBEkNAiADQZ66yIAAaiEHAkACQCAGIAIoApggIghLDQAgBiAISw0FAkAgAigClCAg
BGoiBkG6usiAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAIoApwgIgYgBWoiBCAGSQ0EIAIgBDYCnCAMAQsgAigCkCAoAgAhBiACIAU2AqQg
IAIgBzYCoCAgAkGoIGogAkGQIGogAkGgIGpBAUEBIAYRgYCAgACAgICAACACKQOoICIJQoCAgIDw/z+DQgBSDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVB
HEkNAAtBACEDIAJBkCBqIABB/wFxQQpBAEGw2MmAABCVgICAAEH//wNxDQADQCACKAKcICIEQQggA2siBWoiBiAESQ0CIANBurrIgABqIQcCQAJAIAYgAigC
mCAiCEsNACAGIAhLDQYCQCACKAKUICAEaiIGQcK6yIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgAigCnCAiBiAFaiIEIAZJDQQgAiAENgKc
IAwBCyACKAKQICgCACEGIAIgBTYCpCAgAiAHNgKgICACQaggaiACQZAgaiACQaAgakEBQQEgBhGBgICAAICAgIAAIAIpA6ggIglCgICAgPD/P4NCAFINAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUEISQ0ACyACQZAgaiABQf8BcUEKQQBBsNjJgAAQlYCAgABB//8DcQ0AIAIoApwgIgMgAigCmCAiAk0NASADIAIQiICA
gAAACyACQYEgakGV08CAAE8NACACQZAgakGG08CAAE0aCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAAL2AMBAn9BByECQeCfwIAAIQMCQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCABQR9xDhkYAAECAwQFBgcICQoLDA0ODxAREhMUFRYXGAtBEyECQe2bwIAAIQMMFwtBEiEC
QfyRwIAAIQMMFgtBDyECQYGGwIAAIQMMFQtBEiECQbaJwIAAIQMMFAtBCiECQZ+ewIAAIQMMEwtBCyECQZ+bwIAAIQMMEgtBDyECQeuQwIAAIQMMEQtBFCEC
Qa2TwIAAIQMMEAtBGSECQbGRwIAAIQMMDwtBDyECQcCFwIAAIQMMDgtBDSECQaCfwIAAIQMMDQtBDCECQdmOwIAAIQMMDAtBFyECQciCwIAAIQMMCwtBFyEC
QYGcwIAAIQMMCgtBFiECQf2EwIAAIQMMCQtBESECQaGEwIAAIQMMCAtBDyECQa2PwIAAIQMMBwtBEiECQemJwIAAIQMMBgtBIiECQeOcwIAAIQMMBQtBFiEC
QYyRwIAAIQMMBAtBDiECQdCGwIAAIQMMAwtBESECQdSWwIAAIQMMAgtBGCECQcKTwIAAIQMMAQtBDiECQaqUwIAAIQMLIAAgAzYCACAAIAI2AgQLugICB38B
fiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHIACADayIFaiIGIARJDQIgA0H66siAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAg
ACgCBCAEaiIGQcLryIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYC
BCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcgASQ0ACyAAIAEv
AQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC6UnCAd/An4EfwF+AX8CfgF/AXwjgICAgABBkAFrIgMkgICAgAACQAJAAkACQAJAAkAC
QAJAIAJFDQAgAiABLQAAIgRBVWpB/QFxRSIFRg0AIAEgBWohBgJAAkACQAJAAkACQAJAAkACQCACIAVrIgcOAhABAAsgBi0AAEEwRw0AIAYtAAEiAkG/f2pB
/wFxQRpJQQV0IAJyQf8BcUH4AEYNAQsgA0IANwJYIAMgBzYCVCADIAY2AlAgA0IANwNgIANB0ABqIANB4ABqEJOEgIAAIAMoAlgiCCADKAJcIgFrIgkgCEsN
DiAJQX9MDQ4gCEEBaiICRQ0OQgAhCgJAAkAgAiADKAJUIgVLDQAgCCAFTw0DIAMoAlAgCGotAABBLkYNAQtCACELIAkhDAwGCyADIAI2AlggAiABayINIAJL
DQ4gA0HQAGogA0HgAGoQk4SAgAAgAygCWCIIIAMoAlwiAWsiBSAISw0OIAUgDWsiAiAFSw0OIAJBf0wNDiACQQBIIAkgAmoiDCAJSEcNDkIAIAKtfSELDAUL
IAMgB0F+aiIMNgJUIAMgBkECaiIONgJQQQAhAUIAIQtBACENQQEhBQNAIAEhAiAFIQECQANAIAEgDEsNASACIAxPDQQCQAJAIA4gAmotAAAiBUFQaiIIQf8B
cUEKSQ0AAkAgBUGff2pB/wFxQQVLDQAgBUGpf2ohCAwBCyAFQb9/akH/AXFBBUsNASAFQUlqIQgLIAtCBIYgCK1C/wGDfCELIAFBAWoiBUUNEQwDCyAFQf8B
cUHfAEcNASANQX9GDRAgDUEBaiENIAEhAiABQQFqIgUhASAFRQ0QDAALCwsgAyANNgJcIAIgDWsiDyACSw0NIA9Bf0wNDUIAIRAgAiAMTw0CIA4gAmotAABB
LkcNAiACQQFqIgEgDWsiESABSw0NIAJBAmoiBSACSQ0NA0AgASECIAUhAQJAA0AgASAMSw0BIAIgDE8NBAJAAkAgDiACai0AACIFQVBqIghB/wFxQQpJDQAC
QCAFQZ9/akH/AXFBBUsNACAFQal/aiEIDAELIAVBv39qQf8BcUEFSw0BIAVBSWohCAsgC0IEhiAIrUL/AYN8IQsgAUEBaiIFRQ0RDAMLIAVB/wFxQd8ARw0B
IA1BAWoiDUUNECABIQIgAUEBaiIFIQEgBUUNEAwACwsLIAMgDTYCXCACIA1rIgUgAksNDSAFIBFrIgEgBUsNDSABQX9MDQ0gAUEASCAPIAFqIgkgD0hHDQ1C
ACABrUIChn0hCgwDCyAIIAUQiICAgAAACyACIAwQiICAgAAAC0IAIQogDyEJC0ECIQgCQAJAIAlFDQBCACESAkACQAJAIAIgDE8NACAOIAJqLQAAQd8BcUHQ
AEYNAQsgCiETDAELIAMgAkEBajYCWCADQfAAaiADQdAAahCUhICAAAJAIAMtAHgNAEEAIQxCACEQDAMLIAMpA3AiEkIAUyAKIBJ8IhMgClNHDQwgAygCWCEC
CyACQQJqIgggAkkNCwJAIAMoAlxFDQAgAygCVCIORQ0AIAMoAlAhEUEAIQEDQAJAIBEgAWoiAi0AAEHfAEcNAEIAIRAgAUUNAyABQQFqIgFFDQ4gASAORg0D
AkAgAkF/ai0AACIUQVBqQf8BcUEKSQ0AQQAhDEIAIQpBACEFQQAhDUEAIQIgFEH/AXFBv39qDiYAAAAAAAAHBwcHBwcHBwcHBwcHBwcHBwcHBwcHBwcHBwAA
AAAAAAcLAkAgASAOTw0AIBEgAWotAAAiFEFQakH/AXFBCkkNAUEAIQxCACEKQQAhBUEAIQ1BACECIBRB/wFxQb9/ag4mAQEBAQEBBwcHBwcHBwcHBwcHBwcH
BwcHBwcHBwcHBwcBAQEBAQEHCyABIA4QiICAgAAACyABQQFqIgFFDQ0gASAOSQ0ACwsCQAJAIAlBEEoNACADQQA6AIgBIAMgBEH/AXFBLUY6AGAgA0GIAWoh
AiADQeAAaiEBIANBhgFqIQUMAQsgCUFwaiENIAMoAlQhASADKAJQIQxBACERQQEhAkEAIQUCQAJAAkACQAJAA0ACQAJAAkACQAJAAkAgAiABSw0AIAUgAU8N
AQJAIAwgBWotAAAiBUFSag4DBQEFAAsgBUHfAEYNAwsgDUEBSA0KQgAhC0EAIQJBACEFA0AgAiERIAJBAWoiDiECIA5FDRgDQCACIAFLDQggESABTw0DAkAC
QCAMIBFqLQAAIg5BUGoiCUH/AXFBCkkNAAJAIA5Bn39qQf8BcUEFSw0AIA5BqX9qIQkMAQsgDkG/f2pB/wFxQQVLDQEgDkFJaiEJCyALQgSGIAmtQv8Bg3wi
C0KAgJqm6q/jAVQNAgwKCyAOQf8BcUHfAEcNCCAFQX9GDRkgBUEBaiEFIAIhESACQQFqIg4hAiAORQ0ZDAALCwsgBSABEIiAgIAAAAsgESABEIiAgIAAAAsg
EUEBaiIRRQ0UDAELQQAgBUFRaiIOIA4gBUsbIgVBAEogDSAFayIFIA1Icw0TIAUhDQsgAiEFIAJBAWoiAkUNEgwACwsgC0KAgJqm6q/jAVQNASARIQILIAIg
BWsiASACSw0PDAELIBFBAWoiAiAFayIPIAJLDQ4CQANAIAIhESACQQFqIg4hAiAORQ0QA0ACQCACIAFNDQAgESECDAMLAkAgESABTw0AAkACQCAMIBFqLQAA
Ig5BUGoiCUH/AXFBCkkNAAJAIA5Bn39qQf8BcUEFSw0AIA5BqX9qIQkMAQsgDkG/f2pB/wFxQQVLDQEgDkFJaiEJCyALQgSGIAmtQv8Bg3wiC0KAgJqm6q/j
AVQNAwwECwJAIA5B/wFxQd8ARg0AIBEhAgwECyAFQX9GDRIgBUEBaiEFIAIhESACQQFqIg4hAiAORQ0SDAELCwsgESABEIiAgIAAAAsgAiAFayIBIAJLDQ4L
IBJCAFMgD60gAa19QgKGIhAgEnwiEyAQU0cNDQsgAyANQQBKOgCIASADIARB/wFxQS1GOgBgIANBiAFqIQIgA0HgAGohASADQYYBaiEFC0EBIQ0gBUEBOgAA
IAEtAAAhDCACLQAAIQUgEyEQIAshCkEBIQIMAwtBACEMC0IAIQpBACEFQQAhDUEAIQIMAQsCQAJAIAwNAEEAIQgMAQsgCEEBaiICRQ0JQgAhEwJAAkACQAJA
AkACQAJAAkACQAJAIAIgAygCVCIFTQ0AIAshEAwBCyAIIAVPDQECQCADKAJQIAhqLQAAQd8BcUHFAEYNACALIRAMAQsgAyACNgJYIANB8ABqIANB0ABqEJSE
gIAAAkAgAy0AeA0AQgAhCkEAIQgMCgsgAykDcCITQgBTIAsgE3wiECALU0cNEiADKAJcIQEgAygCWCEICwJAIAFFDQAgAygCVCIBRQ0AIAMoAlAhBUEAIQID
QAJAIAUgAmoiDS0AAEHfAEcNAEIAIQogAkUNCyACQQFqIgJFDRQgAiABRg0LIA1Bf2otAABBUGpB/wFxQQlLDQsCQCACIAFPDQAgBSACai0AAEFQakH/AXFB
CkkNAQwMCyACIAEQiICAgAAACyACQQFqIgJFDRMgAiABSQ0ACwsCQCAMQRNKDQAgA0EAOgCGASADIARB/wFxQS1GOgCIASADQYYBaiECIANBiAFqIQEgA0Hu
AGohBSADKQNgIQoMCAsgDEFtaiENIAMoAlQhASADKAJQIQxBACERQQEhAkEAIQUDQAJAAkACQAJAAkACQAJAIAIgAUsNACAFIAFPDQECQCAMIAVqLQAAIgVB
UmoOAwYBBgALIAVB3wBGDQQLIA1BAUgNAkIAIQpBACECQQAhDgNAIA5BAWoiBUUNGQNAIAUgAUsNCiAOIAFPDQMgBSERAkAgDCAOai0AACIPQVBqIhRB/wFx
QQpJDQAgD0H/AXFB3wBHDQsgAkF/Rg0bIAJBAWohAiARIQ4gBUEBaiIFDQEMGwsLIBEhDiAKQgp+IBStQv8Bg3wiCkKAgJC7utat8A1UDQAMCgsLIAUgARCI
gICAAAALIA4gARCIgICAAAALIAMpA2AhCgwKCyARQQFqIhENAQwUC0EAIAVBUWoiDiAOIAVLGyIFQQBKIA0gBWsiBSANSHMNEyAFIQ0LIAIhBSACQQFqIgIN
AAwSCwsgCCAFEIiAgIAAAAsgCkKAgJC7utat8A1UDQEgDiERCyARIAJrIgIgEUsNDgwBCyAOQQFqIg4gAmsiCSAOSw0NA0AgDkEBaiIFRQ0OAkADQAJAIAUg
AU0NACAOIQUMAgsgDiABTw0EAkAgDCAOai0AACIRQVBqIg9B/wFxQQpJDQACQCARQf8BcUHfAEYNACAOIQUMAwsgAkF/Rg0RIAJBAWohAiAFIQ4gBUEBaiIR
IQUgEUUNEQwBCwsgBSEOIApCCn4gD61C/wGDfCIKQoCAkLu61q3wDVQNAQsLIAUgAmsiAiAFSw0NCyATQgBTIAmtIAKtfSILIBN8IhAgC1NzDQwMAQsgDiAB
EIiAgIAAAAsgAyANQQBKOgCGASADIARB/wFxQS1GOgCIASADQYYBaiECIANBiAFqIQEgA0HuAGohBQtBACENIAVBADoAACABLQAAIQwgAi0AACEFQQEhAgwB
C0EAIQJBACENQQAhBUEAIQxCACEQCwJAIAJFDQAgByAIRw0AIA0NAiAQQlp8QkRUDQUgCkKAgICAgICAEFYNBSAFQQFxRQ0DDAULIAdBA0kNAAJAAkACQCAG
LQAAIgJBv39qQf8BcUEaSUEFdCACckH/AXFBl39qDgYAAwMDAwEDCyAGLQABIgJBv39qQf8BcUEaSUEFdCACckH/AXFB7gBHDQIgBi0AAiICQb9/akH/AXFB
GklBBXQgAnJB/wFxQeYARw0CQQMhAgJAIAdBfWpBBUkNACAGLQADIgFBv39qQf8BcUEaSUEFdCABckH/AXFB6QBHDQAgBi0ABCIBQb9/akH/AXFBGklBBXQg
AXJB/wFxQe4ARw0AIAYtAAUiAUG/f2pB/wFxQRpJQQV0IAFyQf8BcUHpAEcNACAGLQAGIgFBv39qQf8BcUEaSUEFdCABckH/AXFB9ABHDQBBCEEDIAYtAAci
AkG/f2pB/wFxQRpJQQV0IAJyQf8BcUH5AEYbIQILRAAAAAAAAPD/RAAAAAAAAPB/IARB/wFxQS1GGyEVDAELIAYtAAEiAkG/f2pB/wFxQRpJQQV0IAJyQf8B
cUHhAEcNASAGLQACIgJBv39qQf8BcUEaSUEFdCACckH/AXFB7gBHDQFBAyECRAAAAAAAAPh/IRULIAcgAkYNBQsgAEErNgEADAULAkAgCkIAUg0ARAAAAAAA
AACARAAAAAAAAAAAIAxBAXEbIRUMBAsgEEI0fCILIBBTDQUCQCAKQv////////8fVg0AA0AgC0J/fCIQIAtZDQcgECELIApCAYYiCkJ/fEL/////////H1QN
AAsgCiAFrUIBg4QhCgwCCyAKIAWtQgGDhCETAkAgCkKAgICAgICAwABaDQAgEyEKIAshEAwCCwNAIAtCAXwiECALUw0GIBNCgICAgICAgIABVCECIBAhCyAT
QgGIIBNCAYOEIgohEyACDQIMAAsLAkACQCAQQhZVDQAgEKchAiAKuiEVAkAgEEJ/VQ0AIBVByOvIgAAgAkEDdGsrAwCjIRUMAgsgAkEDdEHI68iAAGorAwAg
FaIhFQwBCyADIApCACAQp0EDdEGY7MiAAGopAwBCABCvhYCAACADIAMpAwhCAFIiAjoAcCACDQIgAykDACILQoCAgICAgIAQVg0CIAu6RJLVTQbP8IBEoiEV
CyAVmiAVIAxBAXEbIRUMAgsCQAJAIApCAlQNACAQQv93VQ0AA0AgCkIBiCAKQgGDhCELIBBCAXwhEyAKQgRUDQIgEEL/d1QhAiATIRAgCyEKIAINAAwCCwsg
CiELIBAhEwsgE0ICfCIKIBNTDQMCQAJAAkAgC0ICiCIQQgGDIAtCA4OEQgNSDQAgEEIBfCIQQoCAgICAgIAQUg0AIApCAXwiCyAKUw0GQoCAgICAgIAIIRAg
CyEKDAELQoF4IQsgEEKAgICAgICACFQNAQsgCiELIApC/wdXDQBEAAAAAAAA8P9EAAAAAAAA8H8gDEEBcRshFQwCCyALQjSGQoCAgICAgID4P3xCgICAgICA
gPj/AIMgEEL/////////B4OEIhBCgICAgICAgICAf4QgECAMQQFxG78hFQwBCyADQRBqIBAgChCVhICAAAJAIAMtACBFDQAgAygCGCECIAMpAxAhCwJAIAVB
AXENACACQX9MDQQgAq1CNIYgC4S/IhWaIBUgDEEBcRshFQwCCyAKQgF8IgpCAFENAyADQShqIBAgChCVhICAACADLQA4RQ0AIAsgAykDKFINACACIAMoAjBH
DQAgAkF/TA0DIAKtQjSGIAuEvyIVmiAVIAxBAXEbIRUMAQsgA0HAAGogBiAHEJaEgIAAIAMoAkgiAkF/TA0CIAMpA0AgAq1CNIaEvyIVmiAVIARB/wFxQS1G
GyEVCwJAAkAgFUQAAAAAAAAAAGMNACAVRAAAAAAAAPA/ZEUNAQsgAEErNgEADAELIBVEAAAAAADgb0CiIhVEAAAAAAAA8L9lDQEgFUQAAAAAAABwQGYNASAA
QQA7AQAgACAV/AM6AAILIANBkAFqJICAgIAADwsAC/cFAQR/I4CAgIAAQRBrIgMkgICAgAACQAJAIAJBe2pBfE8NACAAQSs2AQAMAQsCQAJAIAEtAABB3wBG
DQAgASACakF/ai0AAEHfAEYNAEEAIQQCQCABLQAAIgVB3wBGDQACQCAFQVBqIgRB/wFxQQpJDQACQAJAIAVBv39qQf8BcUEaTw0AQckBIQQMAQsgBUGff2pB
/wFxQRpPDQNBqQEhBAsgBSAEaiIEQf8BcUEQTw0CCyADQQA6AA5BAUUNASAEQf8BcSEECwJAIAJBAUYNAAJAIAEtAAEiBUHfAEYNAAJAIAVBUGoiBkH/AXFB
CkkNAAJAAkAgBUG/f2pB/wFxQRpPDQBByQEhBgwBCyAFQZ9/akH/AXFBGUsNBEGpASEGCyAFIAZqIgZB/wFxQQ9LDQMLQQAhBQJAIARFDQAgA0EAOgAMQQAN
AyAEQQR0IQULIANBADoADkEADQIgBSAGQf8BcXIhBAsgAkECRg0AAkAgAS0AAiIFQd8ARg0AAkAgBUFQaiIGQf8BcUEKSQ0AAkACQCAFQb9/akH/AXFBGk8N
AEHJASEGDAELIAVBn39qQf8BcUEZSw0EQakBIQYLIAUgBmoiBkH/AXFBD0sNAwtBACEFAkAgBEUNACADIARB/x9LIgU6AAwgBQ0DIARBBHQhBQsgA0EAOgAO
QQANAiAFIAZB/wFxciEECyACQQNGDQAgAS0AAyIBQd8ARg0AAkAgAUFQaiIFQf8BcUEKSQ0AAkACQCABQb9/akH/AXFBGk8NAEHJASEFDAELIAFBn39qQf8B
cUEZSw0DQakBIQULIAEgBWoiBUH/AXFBD0sNAgtBACEBAkAgBEUNACADIARB/x9LIgE6AAwgAQ0CIARBBHQhAQsgA0EAOgAOQQANASABIAVB/wFxciEECyAE
Qf//A3FB/wFsIAJBAnRB6P7JgABqKAIAbiICQYACSQ0BAAsgAEErNgEADAELIAAgAjoAAiAAQQA7AQALIANBEGokgICAgAALlQYDAn8BfgJ/I4CAgIAAQcAQ
ayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcCkBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCYhICAAEH/
/wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICAgAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBMkEAQQAgA0IgiKco
AgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBqQqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBMGpBqtV+OwAAIAJBKGpC
qtWq1arVqtWqfzcAACACQSBqQqrVqtWq1arVqn83AAAgAkEYakKq1arVqtWq1ap/NwAAIAFCMjcDmBAgASACNgKUECABQdyVx4AANgKQECABIAM3A4gQAkAC
QCABQYgQakEIaiAAEJiEgIAAQf//A3EOAwAEAQQLIAEoApwQIgAgASgCmBAiAksNAiABQgA3A5gQIAEoApQQIQQgAUGq1arVejYClBAgASACNgK8ECABIAA2
ArgQIAEgBDYCtBAgAUGkEGogAUG0EGogAUGIEGoQuICAgAAgASABKAK8ECIANgKYECABIAEoArQQIgI2ApQQIAEgASgCuBA2ApwQAkAgAEUNACABKAKIECEE
IAEoAowQIQUCQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAS8BrBANASABKAKkECICIAEoAqgQIgAQgICAgAAgAEUNAQJA
IABFDQAgAkGqASAA/AsACyABIAIgAEEAIAEQuYCAgAAMAQsgASgCmBAiAEUNACABKAKIECEEIAEoAowQIQUgASgClBAhAgJAIABFDQAgAkGqASAA/AsACyAE
IAIgAEEAQQAgBSgCDBGBgICAAICAgIAACyABQcAQaiSAgICAAA8LIAAgAhCIgICAAAALAAuaAwMBfwF+An8CQCACDQAgAEEAOwAADwsCQAJAAkACQANAQQAh
A0KAgICAECEEAkADQCABIANqLQAAQTtGDQEgBEIBfCEEIAIgA0EBaiIDRw0AC0IAIQQLIAIgBKcgBEKAgICA8B+DUBsiBSACSw0CAkACQCAFRQ0AQQAhAwJA
A0AgASADai0AAEE9Rg0BIAUgA0EBaiIDRg0CDAALCyADIAVLDQUgASADQceVwIAAQQEQhIKAgABBAXENAQtBACEGIAUgAk8NAiABIAVBAWoiA2ohASACIANr
IgINAQwCCwsgA0EBaiIDRQ0DAkACQCAFIANJDQAgBSADa0EBRg0BIABBADsAAA8LIAMgBRCKgICAAAALQfLeyIAAIQICQAJAAkACQAJAIAEgA2otAABBnX9q
DhEBAwMDAwMEAwMDAwMDAwMAAgMLQezeyIAAIQIMAwtB7t7IgAAhAgwCC0Hw3siAACECDAELQbD5yYAAIQILIAIvAAAhBgsgACAGOwAADwsgBSACEIiAgIAA
AAsgAyAFEIiAgIAACwALxQQCCX8BfiOAgICAAEEQayICJICAgIAAIAEtAAghAyABKAIEIQQgASgCACEFQQAhAQJAAkACQAJAA0AgACgCDCIGQQQgAWsiB2oi
CCAGSQ0CIAFBzuDIgABqIQkCQAJAIAggACgCCCIKSw0AIAggCksNBQJAIAAoAgQgBmoiCEHS4MiAAE8NACAJIAggB2pJDQULAkAgB0UNACAIIAkgB/wKAAAL
IAAoAgwiCCAHaiIGIAhJDQQgACAGNgIMDAELIAAoAgAoAgAhCCACIAc2AgQgAiAJNgIAIAJBCGogACACQQFBASAIEYGAgIAAgICAgAAgAikDCCILQiCIpyIH
Qf//A3ENAiALpyEHCyABIAdqIgcgAUkNAiAHIQEgB0EESQ0ACyAAIAUgBCAEQQAtAMDYyYAAQSAQloCAgAAiB0H//wNxDQADQCAAKAIMIgFBAWoiB0UNAgJA
AkAgByAAKAIIIghLDQAgASAITw0GAkAgACgCBCABaiIBQdPgyIAATw0AIAFBAWpB0uDIgABLDQULIAFBIDoAACAAKAIMQQFqIgFFDQQgACABNgIMDAELIAAo
AgAoAgAhASACQQE2AgQgAkHS4MiAADYCACACQQhqIAAgAkEBQQEgARGBgICAAICAgIAAIAIpAwgiC0IgiKciB0H//wNxDQIgC6dFDQELCyACIAM6AAggACAC
QQhqQQEQoICAgAAhBwsgAkEQaiSAgICAACAHDwsACyAIIAoQiICAgAAACyABQQFqIAgQiICAgAAAC7ErAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAA0AgACgCDCIEQSkgA2siBWoiBiAESQ0CIANBhtjJgABqIQcCQAJAIAYgACgCCCII
Sw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGv2MmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAo
AgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMg
BUEpSQ0AC0EAIQMDQCAAKAIMIgRBAiADayIFaiIGIARJDQIgA0GwgMCAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0GAkAgACgCBCAEaiIGQbKAwIAATw0A
IAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJB
AUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQJJDQALQQAhAwNAIAAoAgwiBEECIANrIgVqIgYg
BEkNAiADQYPTwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQcCQCAAKAIEIARqIgZBhdPAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAA
KAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH/
/wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBAkkNAAtBACEDA0AgACgCDCIEQQQgA2siBWoiBiAESQ0CIANBw4rAgABqIQcCQAJAIAYgACgCCCIISw0A
IAYgCEsNCAJAIAAoAgQgBGoiBkHHisCAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAo
AgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEE
SQ0AC0EAIQMDQCAAKAIMIgQgA0EDcyIFaiIGIARJDQIgA0Gn08CAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0JAkAgACgCBCAEaiIGQarTwIAATw0AIAcg
BiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEB
IAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQNJDQALQQAhAyAAIAEvAQhBCkEAQbDYyYAAEJWAgIAA
IgVB//8DcQ0AA0AgACgCDCIEIANBA3MiBWoiBiAESQ0CIANBgtPAgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNCgJAIAAoAgQgBGoiBkGF08CAAE8NACAH
IAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFB
ASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEDSQ0AC0EAIQMDQCAAKAIMIgRBBCADayIFaiIGIARJ
DQIgA0HiiMCAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0LAkAgACgCBCAEaiIGQeaIwIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgC
DCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8D
cQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQRJDQALQQAhAwNAIAAoAgwiBCADQQNzIgVqIgYgBEkNAiADQafTwIAAaiEHAkACQCAGIAAoAggiCEsNACAG
IAhLDQwCQCAAKAIEIARqIgZBqtPAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIA
IQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBA0kN
AAtBACEDIAAgAS8BCkEKQQBBsNjJgAAQlYCAgAAiBUH//wNxDQADQCAAKAIMIgQgA0EDcyIFaiIGIARJDQIgA0GC08CAAGohBwJAAkAgBiAAKAIIIghLDQAg
BiAISw0NAkAgACgCBCAEaiIGQYXTwIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgC
ACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQNJ
DQALQQAhAwNAIAAoAgwiBEEGIANrIgVqIgYgBEkNAiADQdeLwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQ4CQCAAKAIEIARqIgZB3YvAgABPDQAgByAG
IAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEg
BhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBBkkNAAtBACEDA0AgACgCDCIEIANBA3MiBWoiBiAESQ0C
IANBp9PAgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNDwJAIAAoAgQgBGoiBkGq08CAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwi
BiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3EN
AiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEDSQ0AC0EAIQMgACABLwEMQQpBAEGw2MmAABCVgICAACIFQf//A3ENAANAIAAoAgwiBCADQQNzIgVqIgYgBEkN
AiADQYLTwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDRACQCAAKAIEIARqIgZBhdPAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIM
IgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNx
DQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBA0kNAAtBACEDA0AgACgCDCIEIANBD3MiBWoiBiAESQ0CIANB8YrAgABqIQcCQAJAIAYgACgCCCIISw0AIAYg
CEsNEQJAIAAoAgQgBGoiBkGAi8CAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAh
BiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEPSQ0A
C0EAIQMDQCAAKAIMIgQgA0EDcyIFaiIGIARJDQIgA0Gn08CAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0SAkAgACgCBCAEaiIGQarTwIAATw0AIAcgBiAF
akkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYR
gYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQNJDQALQQAhAyAAIAEvAQ5BCkEAQbDYyYAAEJWAgIAAIgVB
//8DcQ0AA0AgACgCDCIEIANBA3MiBWoiBiAESQ0CIANBgtPAgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNEwJAIAAoAgQgBGoiBkGF08CAAE8NACAHIAYg
BWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAG
EYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEDSQ0AC0EAIQMDQCAAKAIMIgRBDiADayIFaiIGIARJDQIg
A0GOi8CAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0UAkAgACgCBCAEaiIGQZyLwIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIG
IAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0C
IAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQ5JDQALQQAhAwNAIAAoAgwiBCADQQNzIgVqIgYgBEkNAiADQafTwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhL
DRUCQCAAKAIEIARqIgZBqtPAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYg
AiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBA0kNAAtB
ACEDIAAgASgCAEEKQQBBsNjJgAAQlYCAgAAiBUH//wNxDQADQCAAKAIMIgQgA0EDcyIFaiIGIARJDQIgA0GC08CAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0WAkAgACgCBCAEaiIGQYXTwIAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQNJDQAL
QQAhAwNAIAAoAgwiBEEMIANrIgVqIgYgBEkNAiADQYGLwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDRcCQCAAKAIEIARqIgZBjYvAgABPDQAgByAGIAVq
SQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGB
gICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBDEkNAAtBACEDA0AgACgCDCIEIANBA3MiBWoiBiAESQ0CIANB
p9PAgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNGAJAIAAoAgQgBGoiBkGq08CAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAF
aiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJ
pyEFCyADIAVqIgUgA0kNAiAFIQMgBUEDSQ0AC0EAIQMgACABKAIEQQpBAEGw2MmAABCVgICAACIFQf//A3ENAANAIAAoAgwiBEECIANrIgVqIgYgBEkNAiAD
Qa2AwIAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDRkCQCAAKAIEIARqIgZBr4DAgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYg
BWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIg
CachBQsgAyAFaiIFIANJDQIgBSEDIAVBAkkNAAtBACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYg
CBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICA
AAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALIAYg
CBCIgICAAAALIAYgCBCIgICAAAALIAYgCBCIgICAAAALnAYDAn8BfgJ/I4CAgIAAQcAQayIBJICAgIAAAkBBgBBFDQAgAUEIakGqAUGAEPwLAAsgAUKAEDcC
kBAgAUG43sGAADYCiBAgASABQQhqNgKMEAJAAkACQAJAAkAgAUGIEGogABCchICAAEH//wNxDgMABAEECwJAIAEoApQQIgAgASgCkBAiAk0NACAAIAIQiICA
gAAMBAsgASgCjBAgABCAgICAAAwBC0EADQBBACkD4PrJgAAiA6dBNkEAQQAgA0IgiKcoAgARgICAgACAgICAACICRQ0AIAJCqtWq1arVqtWqfzcAACACQRBq
QqrVqtWq1arVqn83AAAgAkEIakKq1arVqtWq1ap/NwAAIAJBLmpCqtWq1arVqtWqfzcAACACQShqQqrVqtWq1arVqn83AAAgAkEgakKq1arVqtWq1ap/NwAA
IAJBGGpCqtWq1arVqtWqfzcAACABQjY3A5gQIAEgAjYClBAgAUHclceAADYCkBAgASADNwOIEAJAAkAgAUGIEGpBCGogABCchICAAEH//wNxDgMABAEECyAB
KAKcECIAIAEoApgQIgJLDQIgAUIANwOYECABKAKUECEEIAFBqtWq1Xo2ApQQIAEgAjYCvBAgASAANgK4ECABIAQ2ArQQIAFBpBBqIAFBtBBqIAFBiBBqELiA
gIAAIAEgASgCvBAiADYCmBAgASABKAK0ECICNgKUECABIAEoArgQNgKcEAJAIABFDQAgASgCiBAhBCABKAKMECEFAkAgAEUNACACQaoBIAD8CwALIAQgAiAA
QQBBACAFKAIMEYGAgIAAgICAgAALIAEvAawQDQEgASgCpBAiAiABKAKoECIAEICAgIAAIABFDQECQCAARQ0AIAJBqgEgAPwLAAsgASACIABBACABELmAgIAA
DAELIAEoApgQIgBFDQAgASgCiBAhBCABKAKMECEFIAEoApQQIQICQCAARQ0AIAJBqgEgAPwLAAsgBCACIABBAEEAIAUoAgwRgYCAgACAgICAAAsgAUHAEGok
gICAgAAPCyAAIAIQiICAgAAACwALmgcBCX8jgICAgABB8ABrIgMkgICAgAACQAJAAkAgAg0AIAAoAighBAwBCyADQewAaiAAQTFqTyAAQTBqIgUgA0HsAGpB
AWoiBk9yIQcgACgCKCEIQQAhBANAIAQgCHNBf0YNAiABIARqLQAAIQkgACAIIARqQQFqNgIoIAAoAiwiCkExTw0CAkACQCAKQTBGDQACQCAFIApqIgsgBk8N
ACADQewAaiALQQFqSQ0FCyALIAk6AAAgCkEBaiEKDAELIANB0ABqIAApAxAgACkDOIVCACAAKQMwQtvR0IWa2t+BZ4VCABCvhYCAACAAIAMpA1ggAykDUIU3
AxAgA0HAAGogACkDGCAAKQNIhUIAIAApA0BC442j5Inemt6Of4VCABCvhYCAACAAIAMpA0ggAykDQIU3AxggA0EwaiAAKQMgIAApA1iFQgAgACkDUELDmd2p
x7nZzNgAhUIAEK+FgIAAIAAgAykDOCADKQMwhTcDICAHRQ0DIAUgCToAAEEBIQoLIAAgCjYCLCACIARBAWoiBEcNAAsgCCAEaiEECyADIAI2AmwgBEEEaiIK
IARJDQAgACAKNgIoQTAgACgCLCIKayIEQTFPDQACQAJAAkACQAJAAkAgBEEESQ0AIApBLUkNASAKQQRqQTAQiICAgAAACyAKDQFBACEEDAILAkAgACAKakEw
aiIEIANB7ABqQQRqTw0AIANB7ABqIARBBGpJDQULIAQgAjYAACAAKAIsIgRBBGoiCiAETw0CDAQLIAQgCmoiCEExTw0CAkAgACAKakEwaiIKIANB7ABqIARq
Tw0AIANB7ABqIAogBGpJDQQLAkAgBEUNACAKIANB7ABqIAT8CgAACyADQSBqIAApAxAgACkDOIVCACAAKQMwQtvR0IWa2t+BZ4VCABCvhYCAACAAIAMpAygg
AykDIIU3AxAgA0EQaiAAKQMYIAApA0iFQgAgACkDQELjjaPkid6a3o5/hUIAEK+FgIAAIAAgAykDGCADKQMQhTcDGCADIAApAyAgACkDWIVCACAAKQNQQsOZ
3anHudnM2ACFQgAQr4WAgAAgACADKQMIIAMpAwCFNwMgC0EEIARrIQogA0HsAGogBGohBAJAIABBMGoiCCADQewAakEEak8NACAEIAggCmpJDQMLIApFDQAg
CCAEIAr8CgAACyAAIAo2AiwgA0HwAGokgICAgAAPCyAIQTAQiICAgAAACwALWgEBfyOAgICAAEEQayICJICAgIAAIAJBCDYCDCACQQg2AgQgAkGBh8CAAEH4
hsCAACAAQQFxGzYCCCACQYGHwIAAQfiGwIAAIAFBAXEbNgIAIAIQmYKAgAAAC34BBX8CQCAARQ0AIAEoAhAhAiABKAIMIQMgAEEQaiEEAkAgAS0ACEEBcQ0A
IAAoAgAiBSABKAIAaiIGIAVJDQEgBkUNASAEIAUgBiABKAIEEPCDgIAACyAAKAIAIgEgA2oiACABSQ0AIABFDQAgBCABIAAgAhDwg4CAAA8LAAu8AgIHfwF+
I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTAgA2siBWoiBiAESQ0CIANB8MjJgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAo
AgQgBGoiBkGgycmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwKAAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQg
AiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCIpyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEwSQ0ACyAAQQBBACAB
LQAAELKEgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAv6AwMEfwN+A38jgICAgABBEGsiAiSAgICAAAJAIAAoAggiA0EIaiIEIANJDQACQAJA
AkADQCAEIAAoAgQiBUsNASADQQhqIgQgBUsNAiAAKAIAIANqKQAAIgZCxoyZsuTIkaPGAHwgBkLQn7/+/Pnz5098IgeEQoCBgoSIkKDAgH+DQgBSDQEgByAG
Vg0EIAEpAwAhBiACIAdCAEIKQgAQr4WAgAAgAikDCFBFDQQgAikDACIIIAdCCIh8IgcgCFQNBCABIAZCgMLXL34gB0IQiEL/gYCA8B+DQoGAgICA4gl+IAdC
/4GAgPAfg0LkgICAgMjQB358QiCIfDcDACAAKAIIIgRBCGoiAyAESQ0EIAAgAzYCCCADQQhqIgQgA08NAAwECwsgA0EBaiEEA0AgACgCBCEJIAAoAgAhCgJA
A0AgBCAJSw0BIAMgCU8NBCAEIQUCQCAKIANqLQAAIgNBUGoiC0H/AXFBCkkNACADQf8BcUHfAEcNAiAAIAU2AgggACgCDEEBaiIDRQ0GIAAgAzYCDCAFIQMg
BEEBaiIERQ0GDAELCyAAIAU2AgggASABKQMAQgp+IAutQv8Bg3w3AwAgACgCCCIDQQFqIgRFDQQMAQsLIAJBEGokgICAgAAPCyAEIAUQiICAgAAACyADIAkQ
iICAgAAACwALjwQCCH8BfiOAgICAAEEwayICJICAgIAAAkACQAJAAkACQCABKAIIIgNBAWoiBEUNACAERSEFIAEoAgAhBkEAIQcCQCAEIAEoAgQiCEsNACAD
IAhPDQUgBiADai0AACIJQS1GIQcCQCAJQVVqDgMAAQABCyABIAQiAzYCCCAEQQFqIgRFIQULIAVBAXENAAJAAkAgBCAISw0AIAMgCE8NBiAGIANqLQAAQVBq
Qf8BcUEJSw0AQgAhCgNAIAMhBQJAAkADQCAEIAhLDQUgBSAITw0BIAQhAwJAIAYgBWotAAAiBUFQaiIJQf8BcUEKSQ0AIAVB/wFxQd8ARw0GIAEgAzYCCCAB
KAIMQQFqIgVFDQcgASAFNgIMIAMhBSAEQQFqIgQNAQwHCwsgASADNgIIIApCgICAgAFZDQEgAiAKIApCP4dCCkIAEK+FgIAAIAIpAwggAikDACIKQj+HUg0F
IAogCa1C/wGDfCEKDAELIAUgCBCIgICAAAALIARBAWoiBA0ADAMLCyAAQgA3AwAgAEEIakIANwMADAQLIAdFDQEgCkIAVUIAIAp9IgpCAFNzDQAgAiAKNwMQ
IAJBEGohBAwCCwALIAIgCjcDICACQSBqIQQLIARBCGoiAUEBOgAAIAAgBCkDADcDACAAQQhqIAEpAwA3AwALIAJBMGokgICAgAAPCyADIAgQiICAgAAAC8IE
AgN/BX4jgICAgABBIGsiAySAgICAAAJAAkACQCACUA0AIAFCqn1ZDQELIABBAToAECAAQQA2AgggAEIANwMADAELAkAgAUK1AlMNACAAQQE6ABAgAEH/DzYC
CCAAQgA3AwAMAQsgA0EQaiABpyIEQQR0QaiZyYAAaiIFKQMAQgAgAiACeSIGhiIHQgAQr4WAgAAgAykDECECAkAgAykDGCIIQv8Dg0L/A1INACADIAUpAwhC
ACAHQgAQr4WAgAAgAykDCCIHIAJ8IgIgB1StIAh8IQgLAkACQCACQn9SDQAgAUJIfEKtf1QNAQsgCCAIQj+IIglCCXwiCoghBwJAAkAgBEHqpA1sQRB1IAan
ayAJp2pBP2oiBEGCeEgNAEGACEH/ByAHQvz/////////AIMgByAHIAqGIAhRGyAHIAdCA4NCAVEbIAcgAUIEfEIcVBsgByACQgJUGyIBQgGDIAF8IgFC////
/////x9WIgUbIARqIgRB/g9NDQEgAEEBOgAQIABB/w82AgggAEIANwMADAMLAkAgBEHCd0sNACAAQQE6ABAgAEEANgIIIABCADcDAAwDCyAAQQE6ABAgACAH
QYJ4IARrrYgiAUIBgyABfCIBQv////////8PVjYCCCAAIAFCAYg3AwAMAgsgAEEBOgAQIAAgBDYCCCAAQgAgAUIBiEL/////////9/8AgyAFGzcDAAwBCyAA
QgA3AwAgAEEQakIANwMAIABBCGpCADcDAAsgA0EgaiSAgICAAAvFEwIKfwJ+I4CAgIAAQbAMayIDJICAgIAAQQAhBAJAQYUGRQ0AIANBqAZqQQBBhQb8CwAL
QQEhBQJAAkACQANAIAUgAksNASAEIAJPDQICQCABIARqLQAAIgZB3wBGDQAgBkEwRw0CCyAFIQQgBUEBaiIGIQUgBg0ADAMLCyAEQQFqIQYgA0GtBmohB0EA
IQVBACEIA0AgBiEGAkACQAJAAkACQAJAAkACQAJAAkADQCAGIAJLDQEgBCACTw0MAkAgASAEai0AACIJQVBqIgpB/wFxQQpJDQAgCUH/AXFB3wBHDQIgBUF/
Rg0OIAVBAWohBSAGIQQgBkEBaiIJIQYgCUUNDgwBCwsgCEGABkkNAQwJCyADIAg2AqQGQQAhCgJAAkAgBCACTw0AIAEgBGotAABBLkYNAQtBACELIAQhCQwD
CyAEQQFqIgkgBWsiDCAJSw0LIAgNASAEQQJqIgYgBEkNCwNAIAYgAksNAiAJIAJPDQQgASAJai0AAEEwRw0CIAYhCSAGQQFqIgQhBiAERQ0MDAALCyAHIAhq
IAo6AAAMBwsgCUEIaiIEIAlJDQkCQAJAAkADQCAEIAJLDQEgCEEIaiIGIAhJDQ0gBkH/BUsNASAJQQhqIgYgAksNAyABIAlqKQAAIg1CxoyZsuTIkaPGAHwg
DULQn7/+/Pnz5098Ig6EQoCBgoSIkKDAgH+DQgBSDQEgCEH4BUsNAiAOIA1WDQ0gByAIaiAONwAAIAMoAqQGIgZBCGoiCCAGSQ0NIAQhCSADIAg2AqQGIARB
CGoiBiAESSELIAYhBCALDQ0MAAsLIAlBAWohBANAIAQhBAJAAkACQANAIAQgAksNASAJIAJPDQgCQCABIAlqLQAAIgZBUGoiC0H/AXFBCkkNACAGQf8BcUHf
AEcNAiAFQX9GDREgBUEBaiEFIAQhCSAEQQFqIgYhBCAGRQ0RDAELCyAIQYAGSQ0BDAILIAMgCDYCpAYgDEF/TA0OIAkgBWsiBCAJSw0OIARBf0wNDiADIAwg
BGsiCzYCqAYMBQsgByAIaiALOgAACyAIQQFqIghFDQwgBCEJIARBAWoiBEUNDAwACwsgCEEIakGABhCIgICAAAALIAYgAhCIgICAAAALAkAgCEUNACAJIAVr
IgYgCUsNCSAGQX9qIgQgBksNCQJAIAQgAk8NAEEAIQoCQANAAkACQCABIARqLQAAQVJqDgMBAwADCyAKQQFqIgpFDQ0LIARBf2oiBiAESw0MIAYhBCAGDQAL
CyAKQX9MDQogCkEASCALIApqIgQgC0hHDQogCCAKayIKIAhLDQogAyAKNgKkBiAKQX9MDQogCkEASCAEIApqIgsgBEhHDQogAyALNgKoBiAKQYEGSQ0BQYAG
IQogA0GABjYCpAYgA0EBOgCsBgwBCyAEIAIQiICAgAAACyAJQQFqIgRFDQggBCACSw0EIAkgAk8NACABIAlqLQAAQd8BcUHFAEcNBCAEQQFqIglFDQhBACEM
AkAgCSACTQ0AIAQhCQwECyAEIAJPDQEgASAEai0AACIGQS1HDQJBASEMDAMLIAkgAhCIgICAAAALIAQgAhCIgICAAAALIAkgBCAGQStGGyEJCyAJQQFqIgRF
DQRBACEHAkADQCAJIQYCQANAIAQgAksNASAGIAJPDQMgBCEJAkAgASAGai0AACIGQVBqQf8BcSIIQQpJDQAgBkH/AXFB3wBHDQIgBUF/Rg0JIAVBAWohBSAJ
IQYgBEEBaiIERQ0JDAELCwJAIAdBgIAETg0AIAesQgp+Ig1CIIinIA2nIgZBH3VHDQggBiAIaiEHCyAEQQFqIgRFDQcMAQsLAkAgDEUNACAHQQBKQQAgB2si
B0EASEcNBgsgB0EASCALIAdqIgQgC0hzDQUgAyAENgKoBgwBCyAGIAIQiICAgAAACwJAIApBEksNAEETIAprIgRFDQAgA0GkBmogCmpBCWpBACAE/AsACwJA
QYwGRQ0AIANBGGogA0GkBmpBjAb8CgAACwJAAkACQCADKAIYRQ0AIAMoAhwiBEG8fU4NAQsgAEEANgIIIABCADcDAAwBCwJAAkAgBEG1AkoNAAJAIARBAU4N
AEEAIQIMAgtBACEFA0BBPCECAkAgBEESSw0AIARB+L/JgABqLQAAIQILIANBGGogAhCvhICAAAJAIAMoAhwiBEGAcEoNACAAQQA2AgggAEIANwMADAQLIAJB
AEggBSACaiICIAVIRw0HIAIhBSAEQQBMDQIMAAsLIABB/w82AgggAEIANwMADAELAkADQAJAAkAgBA0AQQIhBSADLQAhIgRBAkkNAQJAIARBe2pB/wFxQQVP
DQAgAiEFDAQLQQEhBQwBCyAEQQBKQQAgBGsiBEEASHMNByAEQX9MDQcCQCAEQRJNDQBBPCEFDAELIARB+L/JgABqLQAAIQULIANBGGogBRCwhICAAAJAIAMo
AhwiBEH/D0wNACAAQf8PNgIIIABCADcDAAwDCyAFQQBKIAIgBWsiBSACSEcNBiAFIQIgBEEBSA0ACwsgBUF/aiIEIAVODQQCQCAEQYF4Sg0AA0AgA0EYakGC
eCAEayICQTwgAkE8SRsiAhCvhICAACACIARqIgRBgnhJDQALCyAEQf8HaiIFIARIIgkNBAJAIAVB/w9JDQAgAEH/DzYCCCAAQgA3AwAMAQsgA0EYakE1ELCE
gIAAQgAhDQJAAkAgAygCGCIGRQ0AIAMoAhwiAUEASA0AAkACQCABQRJLDQACQAJAIAENAEIAIQ0MAQsgA0EhaiEIQQAhAkIAIQ0DQCADIA1CAEIKQgAQr4WA
gAAgAykDCEIAUg0KIAMpAwAhDQJAIAIgBk8NACANIAggAmoxAAB8Ig4gDVQNCyAOIQ0LIAEgAkEBaiICRw0ACwsCQCABIAZPDQACQAJAIANBIWogAWoiCC0A
ACICQQVHDQAgAUEBaiAGRw0AIAMtACANASABRQ0CIAhBf2otAABBAXENAQwCCyACQQRNDQELIA1CAXwiDUIAUQ0JCyANQv////////8PWA0BCyADQRhqQQEQ
r4SAgAAgBEEBaiICIARIDQcgA0EYahCxhICAACENIAJB/wdqIgUgAkgNBwJAIAVB/w9ODQAgBSACSCEJDAELIABB/w82AgggAEIANwMADAMLIAlBAXENBiAN
QoCAgICAgIAIWg0BCyAFQX9qIgQgBU4NBSAEIQULIAAgBTYCCCAAIA1C/////////weDNwMACyADQbAMaiSAgICAAA8LIAhBAWoiCEUNAiAGIQQgBkEBaiIG
RQ0CDAALCyAEIAIQiICAgAAACwALpQIBBX8CQAJAAkACQAJAAkACQCABKAIIIgIgASgCBCIDTw0AIAEoAgAhBCABLQAMQf8BcSEFA0AgBCACai0AACAFRw0B
IAEgAkEBaiICNgIIIAMgAkcNAAtBACEFDAELQQAhBSACIANGDQAgASgCACEFIAIhBAJAIAIgA08NACABLQAMQf8BcSEGIAIhBANAIAUgBGotAAAgBkYNASAD
IARBAWoiBEcNAAsgAyEECyAEIAJJDQIgBCADSw0DIAUgAmohBSAEIAJrIQQgAiEDDAELQQAhBAsCQCAFDQBBACEFQQAhBAwECyADIARqIgIgA0kNAiABIAI2
AggMAwsgAiAEEIqAgIAAAAsgBCADEIiAgIAAAAsACyAAIAU2AgAgACAENgIEC7gCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB
MCADayIFaiIGIARJDQIgA0Gq1cmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQdrVyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTBJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
ugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHKACADayIFaiIGIARJDQIgA0Ha1cmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAI
Sw0FAkAgACgCBCAEaiIGQaTWyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEG
IAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQcoASQ0A
CyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7gCAgd/AX4jgICAgABBEGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRB
OSADayIFaiIGIARJDQIgA0Hb2smAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZTbyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYg
ByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMI
IglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQTlJDQALIAAgAS8BABCfhICAACEFCyACQRBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
4wICC38BfiAAQRJqLQAAIQEgAC8BECECIABBCmotAAAhAyAALwEIIQQgAC8BACAAQQJqLQAAQRB0ciEFIAAvARghBiAALQAUIQcgAC0ADCEIQQAhCUEAIQoC
QAJAAkACQAJAAkAgAC0ABCILDgMCAQADCyAFIQoMAQsgBUH/AXEhCgsgBCADQRB0ciEAAkACQAJAIAgOAwIBAAMLIAAhCQwBCyAAQf8BcSEJCyACIAFBEHRy
IQNBACEAAkAgBw4DAwIAAQsgAyEADAILAAsgA0H/AXEhAAsgCK1CCIYgC62EIAetQhCGIAmtQjCGIAqtQv///weDQhiGhISEIACtQv///weDQgiGIAatQv//
A4NCIIaEIAlBEHatQv8Bg4SFIgxCIIggDIVC7arR2Z6/idG+f34iDEIdiCAMhULtqtHZnr+J0b5/fiIMQiCIIAyFQu2q0dmev4nRvn9+IgxCHYggDIULuAIC
B38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE0IANrIgVqIgYgBEkNAiADQcTYyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUC
QCAAKAIEIARqIgZB+NjJgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAF
NgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBNEkNAAsgACAB
LwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu4AgIHfwF+I4CAgIAAQRBrIgIkgICAgABBACEDAkACQAJAA0AgACgCDCIEQTMgA2si
BWoiBiAESQ0CIANB+NjJgABqIQcCQAJAIAYgACgCCCIISw0AIAYgCEsNBQJAIAAoAgQgBGoiBkGr2cmAAE8NACAHIAYgBWpJDQULAkAgBUUNACAGIAcgBfwK
AAALIAAoAgwiBiAFaiIEIAZJDQQgACAENgIMDAELIAAoAgAoAgAhBiACIAU2AgQgAiAHNgIAIAJBCGogACACQQFBASAGEYGAgIAAgICAgAAgAikDCCIJQiCI
pyIFQf//A3ENAiAJpyEFCyADIAVqIgUgA0kNAiAFIQMgBUEzSQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC90PAwR/
AX4CfyOAgICAAEEgayIEJICAgIAAAkACQAJAAkAgAUH/AXENACACQf8BcQ0AIARBATYCDCAEQYTTwIAANgIIQQUhAkG4hsCAACEBAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIANB/wBxDl1c
AAECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxwdHh8gISIjJCUmJygpKissLS4vMDEyMzQ1Njc4OTo7PD0+P0BBQkNERUZHSElKS0xNTk9QUVJTVFVWV1hZ
WltcC0EMIQJB1YjAgAAhAQxbC0GClMCAACEBQQQhAgxaC0EJIQJBip7AgAAhAQxZC0EOIQJB16HAgAAhAQxYC0ETIQJBtZXAgAAhAQxXC0EIIQJBiaHAgAAh
AQxWC0EPIQJB6o/AgAAhAQxVC0EHIQJB7YHAgAAhAQxUC0EOIQJB54fAgAAhAQxTC0EJIQJBiI7AgAAhAQxSC0ELIQJB0o/AgAAhAQxRC0ELIQJBrYfAgAAh
AQxQC0EMIQJBj4fAgAAhAQxPC0EKIQJB25PAgAAhAQxOC0EKIQJB/YLAgAAhAQxNC0ETIQJBsZnAgAAhAQxMC0ETIQJBnZnAgAAhAQxLC0EKIQJBn4rAgAAh
AQxKC0EMIQJB4JvAgAAhAQxJC0ETIQJBkYPAgAAhAQxIC0ETIQJBgpnAgAAhAQxHC0EWIQJBl5rAgAAhAQxGC0EYIQJBjJXAgAAhAQxFC0EdIQJBrprAgAAh
AQxEC0EQIQJBnIfAgAAhAQxDC0EPIQJBuYfAgAAhAQxCC0ETIQJBzJrAgAAhAQxBC0EkIQJB5o7AgAAhAQxAC0EMIQJBhorAgAAhAQw/C0ELIQJBk4rAgAAh
AQw+C0EMIQJBvYvAgAAhAQw9C0ENIQJB44rAgAAhAQw8C0EMIQJByovAgAAhAQw7C0EJIQJBko7AgAAhAQw6C0ELIQJB3o/AgAAhAQw5C0ERIQJBvobAgAAh
AQw4C0ENIQJBh5TAgAAhAQw3C0EHIQJBnojAgAAhAQw2C0EJIQJBgYjAgAAhAQw1C0HogsCAACEBDDQLQQkhAkG3m8CAACEBDDMLQQ0hAkHggsCAACEBDDIL
QQohAkH2h8CAACEBDDELQQghAkHAncCAACEBDDALQQohAkG+ncCAACEBDC8LQQkhAkHJncCAACEBDC4LQQwhAkHTncCAACEBDC0LQQwhAkGxncCAACEBDCwL
QRQhAkG9j8CAACEBDCsLQRUhAkGsksCAACEBDCoLQRUhAkGWksCAACEBDCkLQR8hAkH1iMCAACEBDCgLQQshAkGkjMCAACEBDCcLQQ4hAkGwjMCAACEBDCYL
QREhAkGmiMCAACEBDCULQRMhAkGAm8CAACEBDCQLQRIhAkHxl8CAACEBDCMLQRIhAkGLj8CAACEBDCILQRIhAkGoocCAACEBDCELQQshAkGnhcCAACEBDCAL
QQohAkHTlcCAACEBDB8LQQkhAkGsjsCAACEBDB4LQQkhAkHLkcCAACEBDB0LQREhAkGdi8CAACEBDBwLQQ0hAkHniMCAACEBDBsLQRQhAkH1gcCAACEBDBoL
QRMhAkHelcCAACEBDBkLQRIhAkG2jsCAACEBDBgLQRIhAkGLiMCAACEBDBcLQRUhAkHFjMCAACEBDBYLQRYhAkGRhsCAACEBDBULQQghAkHRlMCAACEBDBQL
QQchAkGzhMCAACEBDBMLQQohAkHGlMCAACEBDBILQQkhAkHQhcCAACEBDBELQQchAkGun8CAACEBDBALQQchAkG7hMCAACEBDA8LQQ0hAkHqlMCAACEBDA4L
QRUhAkGygsCAACEBDA0LQQYhAkGPksCAACEBDAwLQQwhAkH0m8CAACEBDAsLQQohAkGfnsCAACEBDAoLQRkhAkGxkcCAACEBDAkLQQ8hAkGEhcCAACEBDAgL
QQ8hAkHalMCAACEBDAcLQRIhAkG2icCAACEBDAYLQQshAkGfm8CAACEBDAULQREhAkHVh8CAACEBDAQLQQ0hAkGJmsCAACEBDAMLQRIhAkGUhcCAACEBDAIL
QQ8hAkHrkMCAACEBDAELQQ8hAkGBhsCAACEBCyAEQQhqQQRqIQUgBCABNgIQIAQgAjYCFEEAIQMgBEEIaiEBQQEhBkEAIQIDQCABIAYgAms2AgQgASABKAIA
IgcgAmo2AgAgBEEYaiAAIAFBAiADa0EBEKOAgIAAIAQpAxgiCEIgiKciCUH//wNxDQMgAiAIp2oiCiACSQ0BIAUgBjYCACABIAc2AgACQAJAIAogBEEIaiAD
QQN0aigCBCIBTw0AIAohAgwBCyADDQVBASEDIAogAWsiAiAEKAIUTw0FCyAEQQhqIANBA3RqIgFBBGohBSACIAEoAgQiBksNAgwACwsACyACIAYQioCAgAAA
CyAFIAY2AgAgASAHNgIACyAEQSBqJICAgIAAIAkLygIDBn8BfgJ/I4CAgIAAQSBrIgIkgICAgABBBiEDIAJBBjYCDCACQfbSwIAANgIIIAIgAUH//wNxQQN0
Qej6yYAAaikCADcCECACQQhqQQRqIQRBACEFIAJBCGohAUEAIQYCQAJAAkADQCABIAMgBms2AgQgASABKAIAIgcgBmo2AgAgAkEYaiAAIAFBAiAFa0EBEKOA
gIAAIAIpAxgiCEIgiKciCUH//wNxDQEgBiAIp2oiCiAGSQ0CIAQgAzYCACABIAc2AgACQAJAIAogAkEIaiAFQQN0aigCBCIBTw0AIAohBgwBCyAFDQRBASEF
IAogAWsiBiACKAIUTw0ECyACQQhqIAVBA3RqIgFBBGohBCAGIAEoAgQiA00NAAsgBiADEIqAgIAAAAsgBCADNgIAIAEgBzYCAAwBCwALIAJBIGokgICAgAAg
CQvmAgEDfyOAgICAAEEQayIDJICAgIAAAkACQCACDQAgAEGAgKgBNgEADAELIANBADsBAgJAAkAgAS0AAEHfAEYNACABIAJqQX9qLQAAQd8ARg0ADAELIABB
gICoATYBAAwBCwJAAkADQAJAIAEtAAAiBEHfAEYNAAJAIARBUGpB/wFxIgRBCkkNACAAQSo7AQIMBQsCQAJAIAMvAQJB/wNxIgUNAEEAIQUMAQsgAyAFQQps
IgVB/wNxOwEMIAMgBUEJdiIFQQBHOgAKAkAgBUUNACAAQQE7AQIMBgsgAyADLwEMQf8DcSIFOwECCyADIAUgBGoiBEH/A3EiBTsBDCADIAUgBEc6AAogBSAE
Rw0CIAMgAy8BDEH/A3E7AQILIAFBAWohASACQX9qIgJFDQIMAAsLIABBATsBAgwBCyAAQQA7AQIgACADLwECQf8DcSIBOwEAIAMgATsBBAsgA0EQaiSAgICA
AAuHBwECfwJAIAJBP3EiA0EuSw0AQQUhAkHUhcCAACEEAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCADDi8uAAECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxwdHh8gISIjJCUmJygpKissLS4L
QQchAkHgn8CAACEEDC0LQQEhAkH00sCAACEEDCwLQQEhAkHe0sCAACEEDCsLQQEhAkHG0sCAACEEDCoLQQEhAkGu0sCAACEEDCkLQQEhAkGb0sCAACEEDCgL
QQEhAkH+0cCAACEEDCcLQQEhAkGtpMCAACEEDCYLQQEhAkGbpMCAACEEDCULQQEhAkGDpMCAACEEDCQLQQEhAkHjo8CAACEEDCMLQQIhAkHg0sCAACEEDCIL
QQMhAkHy0sCAACEEDCELQQQhAkGApMCAACEEDCALQQIhAkHv0sCAACEEDB8LQQIhAkHd0sCAACEEDB4LQQIhAkHF0sCAACEEDB0LQQIhAkGt0sCAACEEDBwL
QQIhAkGS0sCAACEEDBsLQQIhAkH10cCAACEEDBoLQQIhAkGspMCAACEEDBkLQQIhAkGapMCAACEEDBgLQQIhAkH9o8CAACEEDBcLQQIhAkHio8CAACEEDBYL
QQIhAkHO0sCAACEEDBULQQIhAkG20sCAACEEDBQLQQIhAkGx0sCAACEEDBMLQQIhAkG1pMCAACEEDBILQQIhAkGdpMCAACEEDBELQQIhAkGGpMCAACEEDBAL
QQMhAkGZ0sCAACEEDA8LQQMhAkHu0sCAACEEDA4LQQMhAkHc0sCAACEEDA0LQQMhAkHE0sCAACEEDAwLQQMhAkGs0sCAACEEDAsLQQMhAkGR0sCAACEEDAoL
QQMhAkH00cCAACEEDAkLQQMhAkGrpMCAACEEDAgLQQMhAkGZpMCAACEEDAcLQQMhAkH8o8CAACEEDAYLQQMhAkHho8CAACEEDAULQQMhAkGd0sCAACEEDAQL
QQMhAkGw0sCAACEEDAMLQQMhAkGFpMCAACEEDAILQQQhAkGJpMCAACEEDAELQQQhAkG00sCAACEECyAAIAQgAiABKAIIIAIgAS0ADBsgAS0AECABLQAREJaA
gIAADwsAC65yAwh/AX4EfyOAgICAAEEwayICJICAgIAAIAFBDGohA0EAIQQCQAJAAkACQAJAAkACQAJAAkACQANAIAMoAgAiBUEWIARrIgZqIgcgBUkNCiAE
QZmQwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQMCQCABKAIEIAVqIgdBr5DAgABPDQAgCCAHIAZqSQ0NCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcg
BmoiBSAHSQ0MIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQog
CqchBgsgBCAGaiIGIARJDQogBiEEIAZBFkkNAAtBACEEA0AgAygCACIFIARBA3MiBmoiByAFSQ0KIARB/tLAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsN
BAJAIAEoAgQgBWoiB0GB08CAAE8NACAIIAcgBmpJDQ0LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQwgAyAFNgIADAELIAEoAgAoAgAhByAC
IAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENCiAKpyEGCyAEIAZqIgYgBEkNCiAGIQQgBkEDSQ0ACyAA
LQAsIgdBCksNCUEFIQRBuIbAgAAhBgJAAkACQAJAAkACQAJAAkACQAJAAkAgBw4LCgABAgMEBQYHCAkKC0EHIQRBgZrAgAAhBgwJC0EMIQRBrZbAgAAhBgwI
C0EMIQRBx5bAgAAhBgwHC0EMIQRBupbAgAAhBgwGC0EIIQRB0ZTAgAAhBgwFC0EHIQRBs4TAgAAhBgwEC0EKIQRBxpTAgAAhBgwDC0EJIQRB0IXAgAAhBgwC
C0EHIQRBu4TAgAAhBgwBC0EHIQRBrp/AgAAhBgsgASAGIAQQoICAgAAiBkH//wNxDQhBACEEA0AgAygCACIFIARBA3MiBmoiByAFSQ0KIARBp9PAgABqIQgC
QAJAIAcgASgCCCIJSw0AIAcgCUsNBQJAIAEoAgQgBWoiB0Gq08CAAE8NACAIIAcgBmpJDQ0LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQwg
AyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENCiAKpyEGCyAEIAZq
IgYgBEkNCiAGIQQgBkEDSQ0ACyAALQAsIgQNByAALwEAIABBAmotAABBEHRyIQsDQCADKAIAIgRBAWoiBkUNCgJAAkAgBiABKAIIIgdLDQAgBCAHTw0GAkAg
ASgCBCAEaiIEQZ3pyIAATw0AIARBAWpBnOnIgABLDQ0LIARBJzoAACADKAIAQQFqIgRFDQwgAyAENgIADAELIAEoAgAoAgAhBCACQQE2AiwgAkGc6ciAADYC
KCACIAEgAkEoakEBQQEgBBGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQogCqdFDQELCyACQarVqtV6NgIAAkACQCALQf///wBxIgRB/wBLDQBBACgC
oN7JgAAhBgwBCwJAIARB/w9LDQBBACgCpN7JgAAhBgwBCwJAIAtB////AHEiBEH//wNLDQBBACgCqN7JgAAhBgwBC0EAKAKs3smAAEE2IARBgIDEAEkbIQYL
AkACQAJAIAZB//8DcSIEDQAgBkEQdiIGQQdxIgRBBU8NDAJAAkACQAJAIARBf2oOBAECAAUQCyALQYDw/wBxQYCwA0cNAgwLCyALQf///wBxQYACTw0OIAIg
CzoAAAwECyALQf///wBxQYCAAU8NDSACIAtBP3FBgAFyOgABIAIgC0EGdkHAAXI6AAAMAwsgC0ELdEELdUF/TA0MIAIgC0E/cUGAAXI6AAIgAiALQQx2QeAB
cjoAACACIAtBBnZBP3FBgAFyOgABDAILIARBS2pBAkkNByAERQ0IDAsLIAIgC0E/cUGAAXI6AAMgAiALQQZ2QT9xQYABcjoAAiACIAtBDHZBP3FBgAFyOgAB
IAIgC0GAgPAAcUESdkHwAXI6AAALIAZBB3EiBEEFTw0EDAYLIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAYgBxCIgICAAAALIARBBBCI
gICAAAALIAJBAC0A2trJgAA6AAIgAkEALwDY2smAADsBAEEDIQQLIAEgAiAEEKCAgIAAIgZB//8DcQ0BQQAhBAJAAkACQANAIAMoAgAiBUEFIARrIgZqIgcg
BUkNBiAEQa/HyYAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQMCQCABKAIEIAVqIgdBtMfJgABPDQAgCCAHIAZqSQ0JCwJAIAZFDQAgByAIIAb8CgAACyAD
KAIAIgcgBmoiBSAHSQ0IIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH/
/wNxDQYgCqchBgsgBCAGaiIGIARJDQYgBiEEIAZBBUkNAAsgAkEgakGqfzoAACACQRhqQqrVqtWq1arVqn83AwAgAkEQakKq1arVqtWq1ap/NwMAIAJBCGpC
qtWq1arVqtWqfzcDACACQqrVqtWq1arVqn83AwAgC0H///8AcSEEQQAhBgNAIAIgBmpBIGogBEEPcSIHQTByIAdBN2ogB0EKSRs6AAAgBkF/aiEGIARBD0sh
ByAEQQR2IQQgBw0ACyABIAIgBmpBIWpBACAGayIEIARBAC0AwNjJgABBIBCWgICAACIGQf//A3ENBANAIAMoAgAiBEEBaiIGRQ0GAkAgBiABKAIIIgdLDQAg
BCAHTw0DAkAgASgCBCAEaiIEQafCyYAATw0AIARBAWpBpsLJgABLDQgLIARBKToAACADKAIAQQFqIgRFDQcgAyAENgIADAQLIAEoAgAoAgAhBCACQQE2Aiwg
AkGmwsmAADYCKCACIAEgAkEoakEBQQEgBBGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQUgCqcNAwwACwsgByAJEIiAgIAAAAsgBEEBaiAHEIiAgIAA
AAsgAC0ALCEECwJAIARBD3FBAUcNACABIAAtAAAQuYSAgAAiBkH//wNxDQEgAC0ALCEECwJAIARBD3FBAkcNAEEAIQQCQAJAAkACQAJAAkACQAJAAkACQANA
IAMoAgAiBUECIARrIgZqIgcgBUkNDSAEQbCAwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQMCQCABKAIEIAVqIgdBsoDAgABPDQAgCCAHIAZqSQ0QCwJA
IAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0PIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICA
gIAAIAIpAwAiCkIgiKciBkH//wNxDQ0gCqchBgsgBCAGaiIGIARJDQ0gBiEEIAZBAkkNAAtBACEEA0AgAygCACIFQQIgBGsiBmoiByAFSQ0NIARBg9PAgABq
IQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBAJAIAEoAgQgBWoiB0GF08CAAE8NACAIIAcgBmpJDRALAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJ
DQ8gAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDSAKpyEGCyAE
IAZqIgYgBEkNDSAGIQQgBkECSQ0AC0EAIQQDQCADKAIAIgVBDSAEayIGaiIHIAVJDQ0gBEGvi8CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0FAkAgASgC
BCAFaiIHQbyLwIAATw0AIAggByAGakkNEAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDyADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCAC
IAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0NIAqnIQYLIAQgBmoiBiAESQ0NIAYhBCAGQQ1JDQALQQAhBANAIAMo
AgAiBSAEQQNzIgZqIgcgBUkNDSAEQafTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQYCQCABKAIEIAVqIgdBqtPAgABPDQAgCCAHIAZqSQ0QCwJAIAZF
DQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0PIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAA
IAIpAwAiCkIgiKciBkH//wNxDQ0gCqchBgsgBCAGaiIGIARJDQ0gBiEEIAZBA0kNAAsgASAAKAIAIAAoAgQQuoSAgAAiBkH//wNxDQtBACEEA0AgAygCACIF
IARBA3MiBmoiByAFSQ0NIARBgtPAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBwJAIAEoAgQgBWoiB0GF08CAAE8NACAIIAcgBmpJDRALAkAgBkUNACAH
IAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQ8gAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikD
ACIKQiCIpyIGQf//A3ENDSAKpyEGCyAEIAZqIgYgBEkNDSAGIQQgBkEDSQ0AC0EAIQQDQCADKAIAIgVBBiAEayIGaiIHIAVJDQ0gBEGwisCAAGohCAJAAkAg
ByABKAIIIglLDQAgByAJSw0IAkAgASgCBCAFaiIHQbaKwIAATw0AIAggByAGakkNEAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDyADIAU2
AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0NIAqnIQYLIAQgBmoiBiAE
SQ0NIAYhBCAGQQZJDQALQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNDSAEQafTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQkCQCABKAIEIAVqIgdB
qtPAgABPDQAgCCAHIAZqSQ0QCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0PIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCAC
IAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQ0gCqchBgsgBCAGaiIGIARJDQ0gBiEEIAZBA0kNAAsgACgCDCEMIAAoAgghDUEA
IQQDQCADKAIAIgVBAiAEayIGaiIHIAVJDQ0gBEGk08CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0KAkAgASgCBCAFaiIHQabTwIAATw0AIAggByAGakkN
EAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDyADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCA
gACAgICAACACKQMAIgpCIIinIgZB//8DcQ0NIAqnIQYLIAQgBmoiBiAESQ0NIAYhBCAGQQJJDQALIAxFDQkgDEF/aiEOQQAhCwNAIAEgDSALQQF0ai8BAEEK
QQBBsNjJgAAQlYCAgAAiBkH//wNxDQwCQCALIA5GDQBBACEEA0AgAygCACIFQQIgBGsiBmoiByAFSQ0PIARBq9PAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcg
CUsNDQJAIAEoAgQgBWoiB0Gt08CAAE8NACAIIAcgBmpJDRILAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDREgAyAFNgIADAELIAEoAgAoAgAh
ByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDyAKpyEGCyAEIAZqIgYgBEkNDyAGIQQgBkECSQ0A
CwsgC0EBaiILIAxHDQAMCgsLIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAAL
IAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALQQAhBAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkADQCADKAIAIgVBAiAEayIGaiIHIAVJ
DREgBEGtgMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0DAkAgASgCBCAFaiIHQa+AwIAATw0AIAggByAGakkNFAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygC
ACIHIAZqIgUgB0kNEyADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8D
cQ0RIAqnIQYLIAQgBmoiBiAESQ0RIAYhBCAGQQJJDQALQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNESAEQYLTwIAAaiEIAkACQCAHIAEoAggiCUsNACAH
IAlLDQQCQCABKAIEIAVqIgdBhdPAgABPDQAgCCAHIAZqSQ0UCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0TIAMgBTYCAAwBCyABKAIAKAIA
IQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDREgCqchBgsgBCAGaiIGIARJDREgBiEEIAZBA0kN
AAtBACEEA0AgAygCACIFQQogBGsiBmoiByAFSQ0RIARBzo7AgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBQJAIAEoAgQgBWoiB0HYjsCAAE8NACAIIAcg
BmpJDRQLAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDRMgAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAH
EYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENESAKpyEGCyAEIAZqIgYgBEkNESAGIQQgBkEKSQ0AC0EAIQQDQCADKAIAIgUgBEEDcyIGaiIHIAVJDREg
BEGn08CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0GAkAgASgCBCAFaiIHQarTwIAATw0AIAggByAGakkNFAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIH
IAZqIgUgB0kNEyADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0R
IAqnIQYLIAQgBmoiBiAESQ0RIAYhBCAGQQNJDQALIAAvARAgAEESai0AAEEQdHIhC0EAIQQDQCADKAIAIgVBAiAEayIGaiIHIAVJDREgBEGwgMCAAGohCAJA
AkAgByABKAIIIglLDQAgByAJSw0HAkAgASgCBCAFaiIHQbKAwIAATw0AIAggByAGakkNFAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNEyAD
IAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0RIAqnIQYLIAQgBmoi
BiAESQ0RIAYhBCAGQQJJDQALQQAhBANAIAMoAgAiBUECIARrIgZqIgcgBUkNESAEQYPTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQgCQCABKAIEIAVq
IgdBhdPAgABPDQAgCCAHIAZqSQ0UCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0TIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYC
KCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDREgCqchBgsgBCAGaiIGIARJDREgBiEEIAZBAkkNAAtBACEEA0AgAygCACIF
QQQgBGsiBmoiByAFSQ0RIARBuZTAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNCQJAIAEoAgQgBWoiB0G9lMCAAE8NACAIIAcgBmpJDRQLAkAgBkUNACAH
IAggBvwKAAALIAMoAgAiByAGaiIFIAdJDRMgAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikD
ACIKQiCIpyIGQf//A3ENESAKpyEGCyAEIAZqIgYgBEkNESAGIQQgBkEESQ0AC0EAIQQDQCADKAIAIgUgBEEDcyIGaiIHIAVJDREgBEGn08CAAGohCAJAAkAg
ByABKAIIIglLDQAgByAJSw0KAkAgASgCBCAFaiIHQarTwIAATw0AIAggByAGakkNFAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNEyADIAU2
AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0RIAqnIQYLIAQgBmoiBiAE
SQ0RIAYhBCAGQQNJDQALQQAhBCABIAtB////B3FBCkEAQbDYyYAAEJWAgIAAIgZB//8DcQ0PA0AgAygCACIFQQIgBGsiBmoiByAFSQ0RIARBrYDAgABqIQgC
QAJAIAcgASgCCCIJSw0AIAcgCUsNCwJAIAEoAgQgBWoiB0GvgMCAAE8NACAIIAcgBmpJDRQLAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDRMg
AyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENESAKpyEGCyAEIAZq
IgYgBEkNESAGIQQgBkECSQ0AC0EAIQQDQCADKAIAIgUgBEEDcyIGaiIHIAVJDREgBEGC08CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0MAkAgASgCBCAF
aiIHQYXTwIAATw0AIAggByAGakkNFAsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNEyADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2
AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0RIAqnIQYLIAQgBmoiBiAESQ0RIAYhBCAGQQNJDQALQQAhBANAIAMoAgAi
BUEFIARrIgZqIgcgBUkNESAEQaSUwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQ0CQCABKAIEIAVqIgdBqZTAgABPDQAgCCAHIAZqSQ0UCwJAIAZFDQAg
ByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0TIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIp
AwAiCkIgiKciBkH//wNxDREgCqchBgsgBCAGaiIGIARJDREgBiEEIAZBBUkNAAtBACEEA0AgAygCACIFIARBA3MiBmoiByAFSQ0RIARBp9PAgABqIQgCQAJA
IAcgASgCCCIJSw0AIAcgCUsNDgJAIAEoAgQgBWoiB0Gq08CAAE8NACAIIAcgBmpJDRQLAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDRMgAyAF
NgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENESAKpyEGCyAEIAZqIgYg
BEkNESAGIQQgBkEDSQ0AC0EAIQQgASAALQAUQQpBAEGw2MmAABCVgICAACIGQf//A3ENDwNAIAMoAgAiBUECIARrIgZqIgcgBUkNESAEQa2AwIAAaiEIAkAC
QCAHIAEoAggiCUsNACAHIAlLDQ8CQCABKAIEIAVqIgdBr4DAgABPDQAgCCAHIAZqSQ0UCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0TIAMg
BTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDREgCqchBgsgBCAGaiIG
IARJDREgBiEEIAZBAk8NDgwACwsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAA
AAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgByAJEIiAgIAAAAsgAC0A
LCEECwJAAkAgBEEPcUEDRw0AQQAhBAJAAkACQAJAAkACQAJAAkACQANAIAMoAgAiBUECIARrIgZqIgcgBUkNDSAEQbCAwIAAaiEIAkACQCAHIAEoAggiCUsN
ACAHIAlLDQMCQCABKAIEIAVqIgdBsoDAgABPDQAgCCAHIAZqSQ0QCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0NIAMgBTYCAAwBCyABKAIA
KAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQ0gCqchBgsgBCAGaiIGIARJDQsgBiEEIAZB
AkkNAAtBACEEA0AgAygCACIFQQIgBGsiBmoiByAFSQ0LIARBg9PAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBAJAIAEoAgQgBWoiB0GF08CAAE8NACAI
IAcgBmpJDQ4LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQ0gAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFB
ASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDSAKpyEGCyAEIAZqIgYgBEkNCyAGIQQgBkECSQ0AC0EAIQQDQCADKAIAIgVBDSAEayIGaiIHIAVJ
DQsgBEGvi8CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0FAkAgASgCBCAFaiIHQbyLwIAATw0AIAggByAGakkNDgsCQCAGRQ0AIAcgCCAG/AoAAAsgAygC
ACIHIAZqIgUgB0kNDSADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8D
cQ0NIAqnIQYLIAQgBmoiBiAESQ0LIAYhBCAGQQ1JDQALQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNCyAEQafTwIAAaiEIAkACQCAHIAEoAggiCUsNACAH
IAlLDQYCQCABKAIEIAVqIgdBqtPAgABPDQAgCCAHIAZqSQ0OCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0NIAMgBTYCAAwBCyABKAIAKAIA
IQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQ0gCqchBgsgBCAGaiIGIARJDQsgBiEEIAZBA0kN
AAsgASAAKAIAIAAoAgQQuoSAgAAiBkH//wNxDQtBACEEA0AgAygCACIFIARBA3MiBmoiByAFSQ0LIARBgtPAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsN
BwJAIAEoAgQgBWoiB0GF08CAAE8NACAIIAcgBmpJDQ4LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQ0gAyAFNgIADAELIAEoAgAoAgAhByAC
IAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDSAKpyEGCyAEIAZqIgYgBEkNCyAGIQQgBkEDSQ0AC0EA
IQQDQCADKAIAIgVBBSAEayIGaiIHIAVJDQsgBEGklMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0IAkAgASgCBCAFaiIHQamUwIAATw0AIAggByAGakkN
DgsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDSADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCA
gACAgICAACACKQMAIgpCIIinIgZB//8DcQ0NIAqnIQYLIAQgBmoiBiAESQ0LIAYhBCAGQQVJDQALQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNCyAEQafT
wIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQkCQCABKAIEIAVqIgdBqtPAgABPDQAgCCAHIAZqSQ0OCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoi
BSAHSQ0NIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQ0gCqch
BgsgBCAGaiIGIARJDQsgBiEEIAZBA0kNAAtBACEEIAEgAC0ACEEKQQBBsNjJgAAQlYCAgAAiBkH//wNxDQsDQCADKAIAIgVBAiAEayIGaiIHIAVJDQsgBEGt
gMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0KAkAgASgCBCAFaiIHQa+AwIAATw0AIAggByAGakkNDgsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZq
IgUgB0kNDSADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0NIAqn
IQYLIAQgBmoiBiAESQ0LIAYhBCAGQQJPDQkMAAsLIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAAL
IAcgCRCIgICAAAALIAcgCRCIgICAAAALIAcgCRCIgICAAAALIAAtACwhBAsCQCAEQQ9xQQRHDQAgASAAELuEgIAAIgZB//8DcQ0CIAAtACwhBAsCQCAEQQ9x
QQVHDQBBACEEAkACQAJAAkACQAJAAkACQAJAAkADQCADKAIAIgVBAiAEayIGaiIHIAVJDQwgBEGwgMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0DAkAg
ASgCBCAFaiIHQbKAwIAATw0AIAggByAGakkNDwsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDiADIAU2AgAMAQsgASgCACgCACEHIAIgBjYC
LCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0OIAqnIQYLIAQgBmoiBiAESQ0MIAYhBCAGQQJJDQALQQAhBANA
IAMoAgAiBUECIARrIgZqIgcgBUkNDCAEQYPTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQQCQCABKAIEIAVqIgdBhdPAgABPDQAgCCAHIAZqSQ0PCwJA
IAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0OIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICA
gIAAIAIpAwAiCkIgiKciBkH//wNxDQ4gCqchBgsgBCAGaiIGIARJDQwgBiEEIAZBAkkNAAtBACEEA0AgAygCACIFQQ0gBGsiBmoiByAFSQ0MIARBr4vAgABq
IQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBQJAIAEoAgQgBWoiB0G8i8CAAE8NACAIIAcgBmpJDQ8LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJ
DQ4gAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDiAKpyEGCyAE
IAZqIgYgBEkNDCAGIQQgBkENSQ0AC0EAIQQDQCADKAIAIgUgBEEDcyIGaiIHIAVJDQwgBEGn08CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0GAkAgASgC
BCAFaiIHQarTwIAATw0AIAggByAGakkNDwsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDiADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCAC
IAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0OIAqnIQYLIAQgBmoiBiAESQ0MIAYhBCAGQQNJDQALIAEgACgCACAA
KAIEELqEgIAAIgZB//8DcQ0MQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNDCAEQYLTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQcCQCABKAIEIAVq
IgdBhdPAgABPDQAgCCAHIAZqSQ0PCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0OIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYC
KCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQ4gCqchBgsgBCAGaiIGIARJDQwgBiEEIAZBA0kNAAtBACEEA0AgAygCACIF
QQYgBGsiBmoiByAFSQ0MIARBsIrAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNCAJAIAEoAgQgBWoiB0G2isCAAE8NACAIIAcgBmpJDQ8LAkAgBkUNACAH
IAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQ4gAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikD
ACIKQiCIpyIGQf//A3ENDiAKpyEGCyAEIAZqIgYgBEkNDCAGIQQgBkEGSQ0AC0EAIQQDQCADKAIAIgUgBEEDcyIGaiIHIAVJDQwgBEGn08CAAGohCAJAAkAg
ByABKAIIIglLDQAgByAJSw0JAkAgASgCBCAFaiIHQarTwIAATw0AIAggByAGakkNDwsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNDiADIAU2
AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0OIAqnIQYLIAQgBmoiBiAE
SQ0MIAYhBCAGQQNJDQALIAAoAgwhDCAAKAIIIQ1BACEEA0AgAygCACIFQQIgBGsiBmoiByAFSQ0MIARBpNPAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsN
CgJAIAEoAgQgBWoiB0Gm08CAAE8NACAIIAcgBmpJDQ8LAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQ4gAyAFNgIADAELIAEoAgAoAgAhByAC
IAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENDiAKpyEGCyAEIAZqIgYgBEkNDCAGIQQgBkECSQ0ACyAM
RQ0JIAxBf2ohDkEAIQsDQCABIA0gC0EBdGovAQBBCkEAQbDYyYAAEJWAgIAAIgZB//8DcQ0NAkAgCyAORg0AQQAhBANAIAMoAgAiBUECIARrIgZqIgcgBUkN
DiAEQavTwIAAaiEIAkACQCAHIAEoAggiCUsNACAHIAlLDQ0CQCABKAIEIAVqIgdBrdPAgABPDQAgCCAHIAZqSQ0RCwJAIAZFDQAgByAIIAb8CgAACyADKAIA
IgcgBmoiBSAHSQ0QIAMgBTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNx
DRAgCqchBgsgBCAGaiIGIARJDQ4gBiEEIAZBAkkNAAsLIAtBAWoiCyAMRw0ADAoLCyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQ
iICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAAC0EAIQQCQAJAAkACQAJAAkADQCADKAIA
IgVBAiAEayIGaiIHIAVJDQggBEGtgMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0DAkAgASgCBCAFaiIHQa+AwIAATw0AIAggByAGakkNCwsCQCAGRQ0A
IAcgCCAG/AoAAAsgAygCACIHIAZqIgUgB0kNCiADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACAC
KQMAIgpCIIinIgZB//8DcQ0KIAqnIQYLIAQgBmoiBiAESQ0IIAYhBCAGQQJJDQALQQAhBANAIAMoAgAiBSAEQQNzIgZqIgcgBUkNCCAEQYLTwIAAaiEIAkAC
QCAHIAEoAggiCUsNACAHIAlLDQQCQCABKAIEIAVqIgdBhdPAgABPDQAgCCAHIAZqSQ0LCwJAIAZFDQAgByAIIAb8CgAACyADKAIAIgcgBmoiBSAHSQ0KIAMg
BTYCAAwBCyABKAIAKAIAIQcgAiAGNgIsIAIgCDYCKCACIAEgAkEoakEBQQEgBxGBgICAAICAgIAAIAIpAwAiCkIgiKciBkH//wNxDQogCqchBgsgBCAGaiIG
IARJDQggBiEEIAZBA0kNAAtBACEEA0AgAygCACIFQQUgBGsiBmoiByAFSQ0IIARBpJTAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBQJAIAEoAgQgBWoi
B0GplMCAAE8NACAIIAcgBmpJDQsLAkAgBkUNACAHIAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQogAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIo
IAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikDACIKQiCIpyIGQf//A3ENCiAKpyEGCyAEIAZqIgYgBEkNCCAGIQQgBkEFSQ0AC0EAIQQDQCADKAIAIgUg
BEEDcyIGaiIHIAVJDQggBEGn08CAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0GAkAgASgCBCAFaiIHQarTwIAATw0AIAggByAGakkNCwsCQCAGRQ0AIAcg
CCAG/AoAAAsgAygCACIHIAZqIgUgB0kNCiADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMA
IgpCIIinIgZB//8DcQ0KIAqnIQYLIAQgBmoiBiAESQ0IIAYhBCAGQQNJDQALQQAhBCABIAAtABBBCkEAQbDYyYAAEJWAgIAAIgZB//8DcQ0IA0AgAygCACIF
QQIgBGsiBmoiByAFSQ0IIARBrYDAgABqIQgCQAJAIAcgASgCCCIJSw0AIAcgCUsNBwJAIAEoAgQgBWoiB0GvgMCAAE8NACAIIAcgBmpJDQsLAkAgBkUNACAH
IAggBvwKAAALIAMoAgAiByAGaiIFIAdJDQogAyAFNgIADAELIAEoAgAoAgAhByACIAY2AiwgAiAINgIoIAIgASACQShqQQFBASAHEYGAgIAAgICAgAAgAikD
ACIKQiCIpyIGQf//A3ENCiAKpyEGCyAEIAZqIgYgBEkNCCAGIQQgBkECTw0GDAALCyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQiICAgAAACyAHIAkQ
iICAgAAACyAHIAkQiICAgAAACyAALQAsIQQLAkAgBEEPcUEGRw0AIAEgAC0AABC5hICAACIGQf//A3ENAiAALQAsIQQLAkAgBEEPcUEHRw0AIAFB25/AgABB
BBCggICAACIGQf//A3ENAiAALQAsIQQLAkAgBEEPcUEIRw0AIAFB25/AgABBBBCggICAACIGQf//A3ENAiAALQAsIQQLAkAgBEEPcUEJRw0AIAEgAC0AABC5
hICAACIGQf//A3ENAiAALQAsIQQLAkAgBEEPcUEKRw0AIAFB25/AgABBBBCggICAACIGQf//A3ENAgtBACEEAkADQCADKAIAIgVBAiAEayIGaiIHIAVJDQIg
BEGtgMCAAGohCAJAAkAgByABKAIIIglLDQAgByAJSw0DAkAgASgCBCAFaiIHQa+AwIAATw0AIAggByAGakkNBQsCQCAGRQ0AIAcgCCAG/AoAAAsgAygCACIH
IAZqIgUgB0kNBCADIAU2AgAMAQsgASgCACgCACEHIAIgBjYCLCACIAg2AiggAiABIAJBKGpBAUEBIAcRgYCAgACAgICAACACKQMAIgpCIIinIgZB//8DcQ0E
IAqnIQYLIAQgBmoiBiAESQ0CIAYhBCAGQQJJDQALQQAhBgwCCyAHIAkQiICAgAALAAsgAkEwaiSAgICAACAGDwsAC+sMAgt/AX4jgICAgABBEGsiAiSAgICA
ACABLQAUIQMgASgCDCEEIAEoAgghBSABKAIEIQYgASgCACEHQQAhAQJAAkACQAJAAkACQAJAAkACQANAIAAoAgwiCEEGIAFrIglqIgogCEkNByABQbbHyYAA
aiELAkACQCAKIAAoAggiDEsNACAKIAxLDQMCQCAAKAIEIAhqIgpBvMfJgABPDQAgCyAKIAlqSQ0KCwJAIAlFDQAgCiALIAn8CgAACyAAKAIMIgogCWoiCCAK
SQ0JIAAgCDYCDAwBCyAAKAIAKAIAIQogAiAJNgIEIAIgCzYCACACQQhqIAAgAkEBQQEgChGBgICAAICAgIAAIAIpAwgiDUIgiKciCUH//wNxDQcgDachCQsg
ASAJaiIJIAFJDQcgCSEBIAlBBkkNAAsgACAHIAYgBkEALQDA2MmAAEEgEJaAgIAAIglB//8DcQ0FA0AgACgCDCIBQQFqIglFDQcCQAJAIAkgACgCCCIKSw0A
IAEgCk8NBAJAIAAoAgQgAWoiAUHT4MiAAE8NACABQQFqQdLgyIAASw0KCyABQSA6AAAgACgCDEEBaiIBRQ0JIAAgATYCDAwBCyAAKAIAKAIAIQEgAkEBNgIE
IAJB0uDIgAA2AgAgAkEIaiAAIAJBAUEBIAERgYCAgACAgICAACACKQMIIg1CIIinIglB//8DcQ0HIA2nRQ0BCwtBACEBA0AgACgCDCIIQQIgAWsiCWoiCiAI
SQ0HIAFBpNPAgABqIQsCQAJAIAogACgCCCIMSw0AIAogDEsNBQJAIAAoAgQgCGoiCkGm08CAAE8NACALIAogCWpJDQoLAkAgCUUNACAKIAsgCfwKAAALIAAo
AgwiCiAJaiIIIApJDQkgACAINgIMDAELIAAoAgAoAgAhCiACIAk2AgQgAiALNgIAIAJBCGogACACQQFBASAKEYGAgIAAgICAgAAgAikDCCINQiCIpyIJQf//
A3ENByANpyEJCyABIAlqIgkgAUkNByAJIQEgCUECSQ0ACyAERQ0EIARBf2ohB0EAIQYDQCAAIAUgBkEBdGovAQBBCkEAQbDYyYAAEJWAgIAAIglB//8DcQ0G
AkAgBiAHRg0AQQAhAQNAIAAoAgwiCEECIAFrIglqIgogCEkNCSABQavTwIAAaiELAkACQCAKIAAoAggiDEsNACAKIAxLDQgCQCAAKAIEIAhqIgpBrdPAgABP
DQAgCyAKIAlqSQ0MCwJAIAlFDQAgCiALIAn8CgAACyAAKAIMIgogCWoiCCAKSQ0LIAAgCDYCDAwBCyAAKAIAKAIAIQogAiAJNgIEIAIgCzYCACACQQhqIAAg
AkEBQQEgChGBgICAAICAgIAAIAIpAwgiDUIgiKciCUH//wNxDQkgDachCQsgASAJaiIJIAFJDQkgCSEBIAlBAkkNAAsLIAZBAWoiBiAERw0ADAULCyAKIAwQ
iICAgAAACyABQQFqIAoQiICAgAAACyAKIAwQiICAgAAACyAKIAwQiICAgAAAC0EAIQEDQCAAKAIMIghBAiABayIJaiIKIAhJDQIgAUGtgMCAAGohCwJAAkAg
CiAAKAIIIgxLDQAgCiAMSw0FAkAgACgCBCAIaiIKQa+AwIAATw0AIAsgCiAJakkNBQsCQCAJRQ0AIAogCyAJ/AoAAAsgACgCDCIKIAlqIgggCkkNBCAAIAg2
AgwMAQsgACgCACgCACEKIAIgCTYCBCACIAs2AgAgAkEIaiAAIAJBAUEBIAoRgYCAgACAgICAACACKQMIIg1CIIinIglB//8DcQ0CIA2nIQkLIAEgCWoiCSAB
SQ0CIAkhASAJQQJJDQALA0AgACgCDCIBQQFqIglFDQICQAJAIAkgACgCCCIKSw0AIAEgCk8NBgJAIAAoAgQgAWoiAUHT4MiAAE8NACABQQFqQdLgyIAASw0F
CyABQSA6AAAgACgCDEEBaiIBRQ0EIAAgATYCDAwBCyAAKAIAKAIAIQEgAkEBNgIEIAJB0uDIgAA2AgAgAkEIaiAAIAJBAUEBIAERgYCAgACAgICAACACKQMI
Ig1CIIinIglB//8DcQ0CIA2nRQ0BCwsgAiADOgAIIAAgAkEIakEBEKCAgIAAIQkLIAJBEGokgICAgAAgCQ8LAAsgCiAMEIiAgIAAAAsgAUEBaiAKEIiAgIAA
AAvYBgMEfwF+An8jgICAgABBIGsiBCSAgICAAAJAIAFB/wFxDQAgAkH/AXENAEEHIQJByIbAgAAhAQJAAkACQCADQf8BcQ4EAgEBAAELQQMhAkGRlMCAACEB
DAELQQAhAUEAIQILAkACQAJAAkACQCABRQ0AIAQgATYCCCAEQQE2AgQgBEGE08CAADYCACAEIAI2AgwgBEEEaiEFQQAhBiAEIQFBASEHQQAhAgNAIAEgByAC
azYCBCABIAEoAgAiAyACajYCACAEQRhqIAAgAUECIAZrQQEQo4CAgAAgBCkDGCIIQiCIpyIJQf//A3ENAyACIAinaiIKIAJJDQYgBSAHNgIAIAEgAzYCAAJA
AkAgCiAEIAZBA3RqKAIEIgFPDQAgCiECDAELQQAhCSAGDQZBASEGIAogAWsiAiAEKAIMTw0GCyAEIAZBA3RqIgFBBGohBSACIAEoAgQiB0sNAgwACwtBACEB
A0AgACgCDCIHQQ0gAWsiAmoiBiAHSQ0FIAFBltPAgABqIQoCQAJAIAYgACgCCCIFSw0AIAYgBUsNBQJAIAAoAgQgB2oiBkGj08CAAE8NACAKIAYgAmpJDQgL
AkAgAkUNACAGIAogAvwKAAALIAAoAgwiBiACaiIHIAZJDQcgACAHNgIMDAELIAAoAgAoAgAhBiAEIAI2AhwgBCAKNgIYIAQgACAEQRhqQQFBASAGEYGAgIAA
gICAgAAgBCkDACIIQiCIpyIJQf//A3ENBSAIpyECCyABIAJqIgIgAUkNBSACIQEgAkENSQ0ACyAAIANB/wFxQQpBAEGw2MmAABCVgICAACIJQf//A3ENAwJA
A0AgACgCCCICIAAoAgwiAUkNBgJAIAIgAUcNACAEQSk6ABcgACgCACgCACEBIARBATYCHCAEIARBF2o2AhggBCAAIARBGGpBAUEBIAERgYCAgACAgICAACAE
LwEEIgkNBiAEKAIARQ0BDAILCwJAIAEgAkkNACABIAIQiICAgAAMBgsgACgCBCABakEpOgAAIAAoAgxBAWoiAUUNBSAAIAE2AgwLQQAhCQwDCyACIAcQioCA
gAAACyAFIAc2AgAgASADNgIADAELIAYgBRCIgICAAAALIARBIGokgICAgAAgCQ8LAAvsAQEHfyOAgICAAEEQayIEJICAgIAAAkACQCAAQQNxDQAgBCACQR9x
IgU6AAsgACgCDCEGIAAoAgghBwJAAkACQCAFDQBBACEFDAELIARBASACdCIIIAYgB2oiBWpBf2oiCSAFSSIKOgAMIAoNASAJQQAgCGtxIgogBWsiBSAKSw0C
CyAHIAVqIgUgB0kNASAFIAFqIgcgBUkNASAHIAAoAhBLDQAgACAHNgIIIAZFDQAgBiAFaiEADAILIAAoAgAgASACIAMgACgCBCgCABGAgICAAICAgIAAIQAM
AQsACyAEQRBqJICAgIAAIAAL2wEBA38CQAJAIABBA3ENAAJAIAEgAEEMaigCACIGSQ0AIAYgAEEQaigCACIHaiIIIAZJDQEgCCABTQ0AIAEgAmoiBSABSQ0B
IAUgCEsNAQJAIAUgBiAAKAIIIgFqRg0AIAQgAk0hAQwDCwJAIAQgAksNACABIAQgAmtqIgIgAUsNAiAAIAI2AghBAQ8LIAQgAmsiBiABaiICIAZJDQFBACEB
IAIgB0sNAiAAIAI2AghBAQ8LIAAoAgAgASACIAMgBCAFIAAoAgQoAgQRgoCAgACAgICAAA8LAAsgAQvZAQEDfwJAAkAgAEEDcQ0AAkAgASAAQQxqKAIAIgZJ
DQAgBiAAQRBqKAIAIgdqIgggBkkNASAIIAFNDQAgASACaiIFIAFJDQEgBSAISw0BAkACQAJAIAUgBiAAKAIIIgNqRw0AAkAgBCACSw0AIAMgBCACa2oiAiAD
Sw0FDAYLIAQgAmsiBiADaiICIAZJDQQgAiAHTQ0FDAELIAQgAk0NAQtBACEBCyABDwsgACgCACABIAIgAyAEIAUgACgCBCgCCBGCgICAAICAgIAADwsACyAA
IAI2AgggAQuUAQECfwJAIABBA3ENAAJAIAEgAEEMaigCACIFSQ0AIAUgAEEQaigCAGoiBiAFSQ0BIAYgAU0NACABIAJqIgQgAUkNASAEIAZLDQECQCAEIAUg
ACgCCCIBakcNACABIAJrIgUgAUsNAiAAIAU2AggLDwsgACgCACABIAIgAyAEIAAoAgQoAgwRgYCAgACAgICAAA8LAAu6AgIHfwF+I4CAgIAAQRBrIgIkgICA
gABBACEDAkACQAJAA0AgACgCDCIEQcIAIANrIgVqIgYgBEkNAiADQZTbyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB1tvJgABP
DQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAg
AkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBwgBJDQALIAAgAS8BABCfhICAACEFCyACQRBq
JICAgIAAIAUPCwALIAYgCBCIgICAAAALuAICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEE7IANrIgVqIgYgBEkNAiADQdbbyYAA
aiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZBkdzJgABPDQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAG
SQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAgAkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsg
AyAFaiIFIANJDQIgBSEDIAVBO0kNAAsgACABLwEAEJ+EgIAAIQULIAJBEGokgICAgAAgBQ8LAAsgBiAIEIiAgIAAAAu6AgIHfwF+I4CAgIAAQRBrIgIkgICA
gABBACEDAkACQAJAA0AgACgCDCIEQcYAIANrIgVqIgYgBEkNAiADQZHcyYAAaiEHAkACQCAGIAAoAggiCEsNACAGIAhLDQUCQCAAKAIEIARqIgZB19zJgABP
DQAgByAGIAVqSQ0FCwJAIAVFDQAgBiAHIAX8CgAACyAAKAIMIgYgBWoiBCAGSQ0EIAAgBDYCDAwBCyAAKAIAKAIAIQYgAiAFNgIEIAIgBzYCACACQQhqIAAg
AkEBQQEgBhGBgICAAICAgIAAIAIpAwgiCUIgiKciBUH//wNxDQIgCachBQsgAyAFaiIFIANJDQIgBSEDIAVBxgBJDQALIAAgAS8BABCfhICAACEFCyACQRBq
JICAgIAAIAUPCwALIAYgCBCIgICAAAALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHCACADayIFaiIGIARJDQIgA0HX3MmA
AGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQZndyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQg
BkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQUL
IAMgBWoiBSADSQ0CIAUhAyAFQcIASQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC7gCAgd/AX4jgICAgABBEGsiAiSA
gICAAEEAIQMCQAJAAkADQCAAKAIMIgQgA0E/cyIFaiIGIARJDQIgA0GZ3cmAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQdjdyYAA
Tw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAA
IAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQT9JDQALIAAgAS8BABCfhICAACEFCyACQRBq
JICAgIAAIAUPCwALIAYgCBCIgICAAAALugICB38BfiOAgICAAEEQayICJICAgIAAQQAhAwJAAkACQANAIAAoAgwiBEHIACADayIFaiIGIARJDQIgA0HY3cmA
AGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQaDeyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsgACgCDCIGIAVqIgQg
BkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB//8DcQ0CIAmnIQUL
IAMgBWoiBSADSQ0CIAUhAyAFQcgASQ0ACyAAIAEvAQAQn4SAgAAhBQsgAkEQaiSAgICAACAFDwsACyAGIAgQiICAgAAAC4MICAJ/AX4DfwJ+AX8BfgN/AX4j
gICAgABBgAFrIgIkgICAgAAgAEEJaiEDIAFBP3GtIQQgACgCACEFAkACQAJAAkACQAJAAkAgAUE/SyIGDQBBACEHQgAhCANAIAUgB0YNAiACQfAAaiAIQgBC
CkIAEK+FgIAAIAIpA3hCAFINByAHQYAGRg0DIAIpA3AiCSADIAdqMQAAfCIIIAlUDQcgB0EBaiEHIAggBIhQDQALQQAhBgwECyAFRQ0EQQAhB0IAIQgDQCAC
QeAAaiAIQgBCCkIAEK+FgIAAIAIpA2hCAFINBiAHQYAGRg0CIAIpA2AiCSAAIAdqIgpBCWoxAAB8IgggCVQNBiAHQQFqIgcgBUYNASACQdAAaiAIQgBCCkIA
EK+FgIAAIAIpA1hCAFINBiACKQNQIgkgCkEKajEAAHwiCCAJVA0GIAdBAWoiByAFRw0ACwsgCFANAwJAIAFBP0siBw0AIAggBIhCAFENACAFIQcMAgsCQCAH
DQAgBSEHA0AgAkHAAGogCEIAQgpCABCvhYCAACACKQNIUEUNBiAHQQFqIgdFDQYgAikDQCIIIASIUEUNAwwACwsgAkEQaiAIQgBCCkIAEK+FgIAAIAIpAxhC
AFINBCACKQMQIQgDQCAFQX9GDQUgAiAIQgBCCkIAEK+FgIAAIAVBAWohBSACKQMAIQggAikDCEIAUg0FDAALC0GABkGABhCIgICAAAALIAdBf0wNAgsgB0F/
aiIKQQBKIAAoAgQiASAKayIKIAFIRw0BIAAgCjYCBAJAIApBgHBKDQAgAEIANwIAIABBCGpBADoAAAwBC0IAQgEgBIYgBhsiCUJ/fCILIAlWDQFBACEBAkAC
QAJAIAUgB00NAEEAIQpBAEGABiAHayIBIAFBgAZLGyEMIAcgBWshDSADIAdqIQ4gBSAHayEBA0BCACAIIASIIAYbIglCgAJaDQUgAkEwaiAIIAuDQgBCCkIA
EK+FgIAAIAIpAzhQRQ0FIAwgCkYNAiACKQMwIg8gDiAKajEAAHwiCCAPVA0FIAMgCmogCac6AAAgDSAKQQFqIgpqDQALCyAIUA0BA0BCACAIIASIIAYbIglC
gAJaDQQgAkEgaiAIIAuDQgBCCkIAEK+FgIAAIAIpAyhQRQ0EIAIpAyAhCAJAAkACQCABQYAGSQ0AIAlQRQ0BDAILIAMgAWogCac6AAAgAUEBaiEBDAELIABB
AToACAsgCFANAgwACwsgByAKakGABhCIgICAAAALIAAgATYCACABQYAGSw0BIAFFDQAgAUEIaiEHA0AgACAHai0AAA0BIAAgB0F3ajYCACAHQX9qIgdBCEcN
AAsLIAJBgAFqJICAgIAADwsAC+AFAgp/BH4CQAJAIAAoAgAiAkUNACABQYABTw0BIAFBDGwiA0GoycmAAGotAAAhBAJAIANBpMnJgABqKAIAIgVFDQAgA0Gg
ycmAAGooAgAhBiAAQQpqIQdBACEDIAIhCAJAAkADQCAIRQ0CIANBgAZGDQEgBi0AAEH/AXFBUGoiCUH/AXEiCiAJRw0FAkAgB0F/ai0AACILIApHDQAgA0EB
ciIJIAVGDQQgCSACRg0DIAZBAWotAABB/wFxQVBqIglB/wFxIgogCUcNBiAHLQAAIgsgCkcNACAIQX5qIQggB0ECaiEHIAZBAmohBiADQQJqIgMgBUcNAQwE
CwsgCyAJQf8BcU8NAiAEQf8BcUF/aiIEQf8BcSAERw0EDAILQYAGQYAGEIiAgIAAAAsgBEH/AXFBf2oiBEH/AXEgBEcNAgsgAiAEQf8BcSIGaiIFIAJJDQEg
AEEJaiEIIAFBP3GtIQwgAUHAAEkhB0IAIQ0DQCAGIAJqIglBf2oiAyAJSw0CAkACQCACQYAGSw0AIA0gACACakEIajEAACAMhkIAIAcbfCIOIA1UDQQgDiAO
QgqAIg1CCn59Ig8gDlYNBAJAAkAgA0GABkkNACAPUEUNAQwDCyAPQoACWg0FIAggA2ogDzwAAAwCCyAAQQE6AAgMAQsgAkF/akGABhCIgICAAAALIAJBf2oi
Ag0ACwJAIA5CClQNACAGIAJqIQIDQCANIQ4gAiIDQX9qIgIgA0sNAyAOQgqAIg1Cdn4gDnwhDwJAAkACQCACQYAGSQ0AIA9QRQ0BDAILIA9CgAJaDQUgCCAC
aiAPPAAADAELIABBAToACAsgDkIKWg0ACwsgACAFQYAGIAVBgAZJGyIJNgIAIAZBAEggACgCBCICIAZqIgMgAkhHDQEgACADNgIEIAVFDQAgCUEIaiECA0Ag
ACACai0AAA0BIAAgAkF3ajYCACACQX9qIgJBCEcNAAsLDwsAC50CBAF/AX4EfwF+I4CAgIAAQRBrIgEkgICAgABCACECAkACQCAAKAIAIgNFDQAgACgCBCIE
QX9MDQBCfyECIARBEksNAAJAAkAgBA0AQgAhAgwBCyAAQQlqIQVBACEGQgAhAgNAIAEgAkIAQgpCABCvhYCAACABKQMIQgBSDQMgASkDACECAkAgBiADTw0A
IAIgBSAGajEAAHwiByACVA0EIAchAgsgBCAGQQFqIgZHDQALCyAEIANPDQACQAJAIABBCWogBGoiBS0AACIGQQVHDQAgBEEBaiADRw0AIAAtAAgNASAERQ0C
IAVBf2otAABBAXENAQwCCyAGQQRNDQELIAJCAXwiAkIAUQ0BCyABQRBqJICAgIAAIAIPCwAL+wYDBH8BfgJ/I4CAgIAAQSBrIgQkgICAgAACQCABQf8BcQ0A
IAJB/wFxDQBBBSECQaeHwIAAIQECQAJAAkACQAJAIANB/wFxDgUEAAEDAgMLQQQhAkHEh8CAACEBDAMLQQghAkHXmsCAACEBDAILQRkhAkHxjsCAACEBDAEL
QQAhAUEAIQILAkACQAJAAkACQCABRQ0AIAQgATYCCCAEQQE2AgQgBEGE08CAADYCACAEIAI2AgwgBEEEaiEFQQAhBiAEIQFBASEHQQAhAgNAIAEgByACazYC
BCABIAEoAgAiAyACajYCACAEQRhqIAAgAUECIAZrQQEQo4CAgAAgBCkDGCIIQiCIpyIJQf//A3ENAyACIAinaiIKIAJJDQYgBSAHNgIAIAEgAzYCAAJAAkAg
CiAEIAZBA3RqKAIEIgFPDQAgCiECDAELQQAhCSAGDQZBASEGIAogAWsiAiAEKAIMTw0GCyAEIAZBA3RqIgFBBGohBSACIAEoAgQiB0sNAgwACwtBACEBA0Ag
ACgCDCIHQQ0gAWsiAmoiBiAHSQ0FIAFBltPAgABqIQoCQAJAIAYgACgCCCIFSw0AIAYgBUsNBQJAIAAoAgQgB2oiBkGj08CAAE8NACAKIAYgAmpJDQgLAkAg
AkUNACAGIAogAvwKAAALIAAoAgwiBiACaiIHIAZJDQcgACAHNgIMDAELIAAoAgAoAgAhBiAEIAI2AhwgBCAKNgIYIAQgACAEQRhqQQFBASAGEYGAgIAAgICA
gAAgBCkDACIIQiCIpyIJQf//A3ENBSAIpyECCyABIAJqIgIgAUkNBSACIQEgAkENSQ0ACyAAIANB/wFxQQpBAEGw2MmAABCVgICAACIJQf//A3ENAwJAA0Ag
ACgCCCICIAAoAgwiAUkNBgJAIAIgAUcNACAEQSk6ABcgACgCACgCACEBIARBATYCHCAEIARBF2o2AhggBCAAIARBGGpBAUEBIAERgYCAgACAgICAACAELwEE
IgkNBiAEKAIARQ0BDAILCwJAIAEgAkkNACABIAIQiICAgAAMBgsgACgCBCABakEpOgAAIAAoAgxBAWoiAUUNBSAAIAE2AgwLQQAhCQwDCyACIAcQioCAgAAA
CyAFIAc2AgAgASADNgIADAELIAYgBRCIgICAAAALIARBIGokgICAgAAgCQ8LAAuPAQEBfyOAgICAAEEQayIGJICAgIAAIAZBCGogASACIAMgBCAFEKSAgIAA
AkACQCAGLQAMRQ0AAkAgBigCCCICIAVPDQAgBCACQQF0ai0AACEBIAIgAyAEIAUQvYSAgAAgAEEBOgAEIAAgATYCAAwCCyACIAUQiICAgAAACyAAQgA3AgAL
IAZBEGokgICAgAALQAACQAJAIAAgAiABIAQgBRDAhICAACIBIAVPDQAgA0GAAk8NASAEIAFBAXRqIAM6AAAPCyABIAUQiICAgAALAAuPAQEBfyOAgICAAEEQ
ayIGJICAgIAAIAZBCGogASACIAMgBCAFEKWAgIAAAkACQCAGLQAMRQ0AAkAgBigCCCICIAVPDQAgBCACQQJ0ai8BACEBIAIgAyAEIAUQvoSAgAAgAEEBOgAE
IAAgATYCAAwCCyACIAUQiICAgAAACyAAQgA3AgALIAZBEGokgICAgAALQQACQAJAIAAgAiABIAQgBRDBhICAACIBIAVPDQAgA0GAgARPDQEgBCABQQJ0aiAD
OwEADwsgASAFEIiAgIAACwALjwEBAX8jgICAgABBEGsiBiSAgICAACAGQQhqIAEgAiADIAQgBRCmgICAAAJAAkAgBi0ADEUNAAJAIAYoAggiAiAFTw0AIAQg
AkEDdGooAgAhASACIAMgBCAFEL+EgIAAIABBAToABCAAIAE2AgAMAgsgAiAFEIiAgIAAAAsgAEIANwIACyAGQRBqJICAgIAACzUAAkAgACACIAEgBCAFEMKE
gIAAIgEgBU8NACAEIAFBA3RqIAM2AgAPCyABIAUQiICAgAAAC/UDAgd/AX4jgICAgABBMGsiAiSAgICAAEEAIQMCQAJAAkADQCAAKAIMIgRBAiADayIFaiIG
IARJDQIgA0G0x8mAAGohBwJAAkAgBiAAKAIIIghLDQAgBiAISw0FAkAgACgCBCAEaiIGQbbHyYAATw0AIAcgBiAFakkNBQsCQCAFRQ0AIAYgByAF/AoAAAsg
ACgCDCIGIAVqIgQgBkkNBCAAIAQ2AgwMAQsgACgCACgCACEGIAIgBTYCBCACIAc2AgAgAkEIaiAAIAJBAUEBIAYRgYCAgACAgICAACACKQMIIglCIIinIgVB
//8DcQ0CIAmnIQULIAMgBWoiBSADSQ0CIAUhAyAFQQJJDQALIAJBCGpBIGpBqn86AAAgAkEgakKq1arVqtWq1ap/NwMAIAJBGGpCqtWq1arVqtWqfzcDACAC
QRBqQqrVqtWq1arVqn83AwAgAkKq1arVqtWq1ap/NwMIIAFB/wFxIQNBACEFA0AgAkEIaiAFakEgaiADQQ9xIgZBMHIgBkHXAGogBkEKSRs6AAAgBUF/aiEF
IANBD0shBiADQQR2IQMgBg0ACyAAIAJBCGogBWpBIWpBACAFayIDIANBAC0AwNjJgABBIBCWgICAACEFCyACQTBqJICAgIAAIAUPCwALIAYgCBCIgICAAAAL
+gYDB38BfgJ/I4CAgIAAQRBrIgMkgICAgABBACEEAkACQAJAAkACQAJAA0AgACgCDCIFQQIgBGsiBmoiByAFSQ0FIARBpNPAgABqIQgCQAJAIAcgACgCCCIJ
Sw0AIAcgCUsNAwJAIAAoAgQgBWoiB0Gm08CAAE8NACAIIAcgBmpJDQgLAkAgBkUNACAHIAggBvwKAAALIAAoAgwiByAGaiIFIAdJDQcgACAFNgIMDAELIAAo
AgAoAgAhByADIAY2AgQgAyAINgIAIANBCGogACADQQFBASAHEYGAgIAAgICAgAAgAykDCCIKQiCIpyIGQf//A3ENBSAKpyEGCyAEIAZqIgYgBEkNBSAGIQQg
BkECSQ0ACyACRQ0CIAJBf2ohC0EAIQwDQCAAIAEgDGotAABBCkEAQbDYyYAAEJWAgIAAIgZB//8DcQ0EAkAgDCALRg0AQQAhBANAIAAoAgwiBUECIARrIgZq
IgcgBUkNByAEQavTwIAAaiEIAkACQCAHIAAoAggiCUsNACAHIAlLDQYCQCAAKAIEIAVqIgdBrdPAgABPDQAgCCAHIAZqSQ0KCwJAIAZFDQAgByAIIAb8CgAA
CyAAKAIMIgcgBmoiBSAHSQ0JIAAgBTYCDAwBCyAAKAIAKAIAIQcgAyAGNgIEIAMgCDYCACADQQhqIAAgA0EBQQEgBxGBgICAAICAgIAAIAMpAwgiCkIgiKci
BkH//wNxDQcgCqchBgsgBCAGaiIGIARJDQcgBiEEIAZBAkkNAAsLIAxBAWoiDCACRw0ADAMLCyAHIAkQiICAgAAACyAHIAkQiICAgAAAC0EAIQQDQCAAKAIM
IgVBAiAEayIGaiIHIAVJDQIgBEGtgMCAAGohCAJAAkAgByAAKAIIIglLDQAgByAJSw0FAkAgACgCBCAFaiIHQa+AwIAATw0AIAggByAGakkNBQsCQCAGRQ0A
IAcgCCAG/AoAAAsgACgCDCIHIAZqIgUgB0kNBCAAIAU2AgwMAQsgACgCACgCACEHIAMgBjYCBCADIAg2AgAgA0EIaiAAIANBAUEBIAcRgYCAgACAgICAACAD
KQMIIgpCIIinIgZB//8DcQ0CIAqnIQYLIAQgBmoiBiAESQ0CIAYhBCAGQQJJDQALQQAhBgsgA0EQaiSAgICAACAGDwsACyAHIAkQiICAgAAAC+bfAgMKfwF+
An8jgICAgABBIGsiAiSAgICAACAAQQRqIQMgAEEIaiEEIABBDGohBUEAIQYCQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAA0AgBSgC
ACIHQQQgBmsiCGoiCSAHSQ0EIAZB/dLAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAgB2oiCUGB08CAAE8NACAKIAkgCGpJDQcLAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENQiAMpyEICyAGIAhqIgggBkkNBCAIIQYgCEEESQ0ACyABLQAoIglBGEsNA0EHIQZB4J/AgAAhCAJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAkOGRgAAQIDBAUGBwgJCgsMDQ4PEBESExQVFhcYC0ETIQZB7ZvAgAAhCAwXC0ESIQZB/JHA
gAAhCAwWC0EPIQZBgYbAgAAhCAwVC0ESIQZBtonAgAAhCAwUC0EKIQZBn57AgAAhCAwTC0ELIQZBn5vAgAAhCAwSC0EPIQZB65DAgAAhCAwRC0EUIQZBrZPA
gAAhCAwQC0EZIQZBsZHAgAAhCAwPC0EPIQZBwIXAgAAhCAwOC0ENIQZBoJ/AgAAhCAwNC0EMIQZB2Y7AgAAhCAwMC0EXIQZByILAgAAhCAwLC0EXIQZBgZzA
gAAhCAwKC0EWIQZB/YTAgAAhCAwJC0ERIQZBoYTAgAAhCAwIC0EPIQZBrY/AgAAhCAwHC0ESIQZB6YnAgAAhCAwGC0EiIQZB45zAgAAhCAwFC0EWIQZBjJHA
gAAhCAwEC0EOIQZB0IbAgAAhCAwDC0ERIQZB1JbAgAAhCAwCC0EYIQZBwpPAgAAhCAwBC0EOIQZBqpTAgAAhCAsgACAIIAYQoICAgAAiB0H//wNxDUBBACEG
A0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0EIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDQcL
AkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGA
gIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENQiAMpyEICyAGIAhqIgggBkkNBCAIIQYgCEEDSQ0ACwJAIAEtACgiBg0AIABB25/AgABBBBCggICAACIHQf//
A3ENQSABLQAoIQYLIAZBH3FBAUcNAiAAIAEoAgAgASgCBBC6hICAACIHQf//A3ENQCABLQAoIQYMAgsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsCQCAGQR9x
QQJHDQAgACABKAIAIAEoAgQQuoSAgAAiB0H//wNxDT4gAS0AKCEGCwJAIAZBH3FBA0cNAEEAIQYCQAJAAkACQAJAAkACQAJAAkACQAJAA0AgBSgCACIHQQIg
BmsiCGoiCSAHSQ0NIAZBsIDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAgB2oiCUGygMCAAE8NACAKIAkgCGpJDRALAkAgCEUNACAJIAog
CPwKAAALIAUoAgAiCSAIaiIHIAlJDQ8gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikD
CCIMQiCIpyIHQf//A3ENSyAMpyEICyAGIAhqIgggBkkNDSAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQ0gBkGD08CAAGohCgJAAkAg
CSAEKAIAIgtLDQAgCSALSw0EAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNEAsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDyAFIAc2
AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1LIAynIQgLIAYgCGoi
CCAGSQ0NIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiB0EGIAZrIghqIgkgB0kNDSAGQZKQwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdq
IglBmJDAgABPDQAgCiAJIAhqSQ0QCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0PIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUsgDKchCAsgBiAIaiIIIAZJDQ0gCCEGIAhBBkkNAAtBACEGA0AgBSgC
ACIHIAZBA3MiCGoiCSAHSQ0NIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRALAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQ8gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENSyAMpyEICyAGIAhqIgggBkkNDSAIIQYgCEEDSQ0ACyABLQAIIQkgAkEBNgIMIAJBhNPAgAA2AghBCiEGQcGbwIAAIQgC
QAJAAkACQAJAAkACQAJAIAlBB3EOCAcAAQIDBAUGBwtBFSEGQeuFwIAAIQgMBgtBCyEGQbafwIAAIQgMBQtBDCEGQbOFwIAAIQgMBAtBFiEGQYqEwIAAIQgM
AwtBJCEGQYiTwIAAIQgMAgtBFiEGQdODwIAAIQgMAQtBCyEGQcKfwIAAIQgLIAJBCGpBBGohCyACIAg2AhAgAiAGNgIUQQAhCSACQQhqIQZBASEKQQAhCANA
IAYgCiAIazYCBCAGIAYoAgAiDSAIajYCACACQRhqIAAgBkECIAlrQQEQo4CAgAAgAikDGCIMQiCIpyIHQf//A3ENBiAIIAynaiIHIAhJDQ0gCyAKNgIAIAYg
DTYCAAJAAkACQCAHIAJBCGogCUEDdGooAgQiBk8NACAHIQgMAQsgCQ0BQQEhCSAHIAZrIgggAigCFE8NAQsgAkEIaiAJQQN0aiIGQQRqIQsgCCAGKAIEIgpL
DQYMAQsLQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNDSAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQkCQCADKAIAIAdqIglBhdPAgABPDQAg
CiAJIAhqSQ0QCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0PIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEY
akEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUsgDKchCAsgBiAIaiIIIAZJDQ0gCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHQRMgBmsiCGoi
CSAHSQ0NIAZBt6DAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCgJAIAMoAgAgB2oiCUHKoMCAAE8NACAKIAkgCGpJDRALAkAgCEUNACAJIAogCPwKAAAL
IAUoAgAiCSAIaiIHIAlJDQ8gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCI
pyIHQf//A3ENSyAMpyEICyAGIAhqIgggBkkNDSAIIQYgCEETSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQ0gBkGn08CAAGohCgJAAkAgCSAEKAIA
IgtLDQAgCSALSw0LAkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNEAsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDyAFIAc2AgAMAQsg
ACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1LIAynIQgLIAYgCGoiCCAGSQ0N
IAghBiAIQQNJDQALIAAgASgCACABKAIEELqEgIAAIgdB//8DcQ1JQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNDSAGQa2AwIAAaiEKAkACQCAJIAQoAgAi
C0sNACAJIAtLDQwCQCADKAIAIAdqIglBr4DAgABPDQAgCiAJIAhqSQ0QCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0PIAUgBzYCAAwBCyAA
KAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUsgDKchCAsgBiAIaiIIIAZJDQ0g
CCEGIAhBAk8NCwwACwsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCCAKEIqAgIAAAAsgCyAKNgIAIAYgDTYCAAxD
CyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyABLQAoIQYLAkAgBkEfcUEERw0AQQAhBgJAAkACQAJAAkACQAJAAkAC
QANAIAUoAgAiB0ECIAZrIghqIgkgB0kNCyAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdqIglBsoDAgABPDQAgCiAJIAhqSQ0O
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUkgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0LIAZB
g9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDQ0gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
SSAMpyEICyAGIAhqIgggBkkNCyAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBBCAGayIIaiIJIAdJDQsgBkGbn8CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSAL
Sw0FAkAgAygCACAHaiIJQZ+fwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJ
IAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1JIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQRJ
DQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNCyAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJ
IAhqSQ0OCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEB
QQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUkgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBA0kNAAtBACEGIAAgAS0ACEEKQQBBsNjJgAAQlYCA
gAAiB0H//wNxDUcDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQsgBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0HAkAgAygCACAHaiIJQYXTwIAATw0A
IAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJB
GGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1JIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EEIAZrIghq
IgkgB0kNCyAGQe+hwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQgCQCADKAIAIAdqIglB86HAgABPDQAgCiAJIAhqSQ0OCwJAIAhFDQAgCSAKIAj8CgAA
CyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIg
iKciB0H//wNxDUkgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBBEkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0LIAZBp9PAgABqIQoCQAJAIAkgBCgC
ACILSw0AIAkgC0sNCQJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQ0gBSAHNgIADAEL
IAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENSSAMpyEICyAGIAhqIgggBkkN
CyAIIQYgCEEDSQ0ACyAAIAEoAgAgASgCBBC6hICAACIHQf//A3ENR0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQsgBkGtgMCAAGohCgJAAkAgCSAEKAIA
IgtLDQAgCSALSw0KAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsg
ACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1JIAynIQgLIAYgCGoiCCAGSQ0L
IAghBiAIQQJPDQkMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkg
CxCIgICAAAALIAkgCxCIgICAAAALIAEtACghBgsCQCAGQR9xQQVHDQBBACEGAkACQAJAAkACQAJAA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0IIAZBsIDAgABq
IQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAgB2oiCUGygMCAAE8NACAKIAkgCGpJDQsLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJ
DQogBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENRiAMpyEI
CyAGIAhqIgggBkkNCCAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQggBkGD08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0EAkAg
AygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNCwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNCiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1GIAynIQgLIAYgCGoiCCAGSQ0IIAghBiAIQQJJDQALQQAh
BgNAIAUoAgAiB0EFIAZrIghqIgkgB0kNCCAGQcqZwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdqIglBz5nAgABPDQAgCiAJIAhqSQ0L
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0KIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDUYgDKchCAsgBiAIaiIIIAZJDQggCCEGIAhBBUkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0IIAZB
p9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDQsLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDQogBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
RiAMpyEICyAGIAhqIgggBkkNCCAIIQYgCEEDSQ0ACyAAIAEoAgAgASgCBBC6hICAACIHQf//A3ENREEAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQggBkGt
gMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0HAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNCwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhq
IgcgCUkNCiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1G
IAynIQgLIAYgCGoiCCAGSQ0IIAghBiAIQQJPDQYMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICA
AAALIAEtACghBgsCQCAGQR9xQQZHDQBBACEGAkACQAJAAkACQAJAA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0IIAZBsIDAgABqIQoCQAJAIAkgBCgCACILSw0A
IAkgC0sNAwJAIAMoAgAgB2oiCUGygMCAAE8NACAKIAkgCGpJDQsLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQogBSAHNgIADAELIAAoAgAo
AgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENRiAMpyEICyAGIAhqIgggBkkNCCAIIQYg
CEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQggBkGD08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0EAkAgAygCACAHaiIJQYXTwIAATw0A
IAogCSAIakkNCwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNCiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJB
GGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1GIAynIQgLIAYgCGoiCCAGSQ0IIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiB0EFIAZrIghq
IgkgB0kNCCAGQcqZwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdqIglBz5nAgABPDQAgCiAJIAhqSQ0LCwJAIAhFDQAgCSAKIAj8CgAA
CyAFKAIAIgkgCGoiByAJSQ0KIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIg
iKciB0H//wNxDUYgDKchCAsgBiAIaiIIIAZJDQggCCEGIAhBBUkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0IIAZBp9PAgABqIQoCQAJAIAkgBCgC
ACILSw0AIAkgC0sNBgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDQsLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQogBSAHNgIADAEL
IAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENRiAMpyEICyAGIAhqIgggBkkN
CCAIIQYgCEEDSQ0ACyAAIAEoAgAgASgCBBC6hICAACIHQf//A3ENREEAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQggBkGtgMCAAGohCgJAAkAgCSAEKAIA
IgtLDQAgCSALSw0HAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNCwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNCiAFIAc2AgAMAQsg
ACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ1GIAynIQgLIAYgCGoiCCAGSQ0I
IAghBiAIQQJPDQYMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAEtACghBgsgBkEfcUEH
Rw0cQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNASAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQQCQCADKAIAIAdqIglBsoDAgABPDQAgCiAJ
IAhqSQ0ECwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0DIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEB
QQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8gDKchCAsgBiAIaiIIIAZJDQEgCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAH
SQ0BIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBQJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUo
AgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIH
Qf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDR4gBkHGjsCAAGohCgJAAkAgCSAEKAIAIgtL
DQAgCSALSw0GAkAgAygCACAHaiIJQciOwIAATw0AIAogCSAIakkNIQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgC
ACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAgh
BiAIQQJJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNHiAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQcCQCADKAIAIAdqIglBqtPAgABP
DQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0gIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAg
AkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8gDKchCAsgBiAIaiIIIAZJDR4gCCEGIAhBA0kNAAsgAS0AJCEGIAJBATYCDCACQYTT
wIAANgIIIAZBH3EiCUEXSw0dQQUhCEGA0sCAACEGAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAkOGBcAAQIDBAUG
BwgJCgsMDQ4PEBESExQVFhcLQa+kwIAAIQYMFgtBBiEIQePSwIAAIQYMFQtBBiEIQdHSwIAAIQYMFAtBBiEIQbnSwIAAIQYMEwtBBiEIQaHSwIAAIQYMEgtB
BiEIQYbSwIAAIQYMEQtBBiEIQenRwIAAIQYMEAtBBiEIQaCkwIAAIQYMDwtBBiEIQY6kwIAAIQYMDgtBBiEIQfGjwIAAIQYMDQtBBiEIQdajwIAAIQYMDAtB
ByEIQZXSwIAAIQYMCwtBByEIQfjRwIAAIQYMCgtBByEIQerSwIAAIQYMCQtBByEIQdjSwIAAIQYMCAtBByEIQcDSwIAAIQYMBwtBByEIQajSwIAAIQYMBgtB
ByEIQY3SwIAAIQYMBQtBByEIQfDRwIAAIQYMBAtBByEIQaekwIAAIQYMAwtBByEIQZWkwIAAIQYMAgtBByEIQfijwIAAIQYMAQtBByEIQd2jwIAAIQYLIAJB
DGohCyACIAY2AhAgAiAINgIUQQAhCSACQQhqIQZBASEKQQAhCANAIAYgCiAIazYCBCAGIAYoAgAiDSAIajYCACACQRhqIAAgBkECIAlrQQEQo4CAgAAgAikD
GCIMQiCIpyIHQf//A3ENGyAIIAynaiIHIAhJDR4gCyAKNgIAIAYgDTYCAAJAAkACQCAHIAJBCGogCUEDdGooAgQiBk8NACAHIQgMAQsgCQ0BQQEhCSAHIAZr
IgggAigCFE8NAQsgAkEIaiAJQQN0aiIGQQRqIQsgCCAGKAIEIgpLDQcMAQsLQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNHiAGQYLTwIAAaiEKAkACQCAJ
IAQoAgAiC0sNACAJIAtLDQkCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0gIAUgBzYC
AAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8gDKchCAsgBiAIaiII
IAZJDR4gCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHQQggBmsiCGoiCSAHSQ0eIAZBoInAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCgJAIAMoAgAgB2oi
CUGoicCAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIY
IAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEEISQ0AC0EAIQYDQCAFKAIA
IgcgBkEDcyIIaiIJIAdJDR4gBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0LAkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNIQsCQCAIRQ0A
IAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICA
ACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNHiAGQbCAwIAAaiEK
AkACQCAJIAQoAgAiC0sNACAJIAtLDQwCQCADKAIAIAdqIglBsoDAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0g
IAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8gDKchCAsg
BiAIaiIIIAZJDR4gCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0eIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDQJAIAMo
AgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2Ahwg
AiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEECSQ0AC0EAIQYD
QCAFKAIAIgdBECAGayIIaiIJIAdJDR4gBkHfhsCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0OAkAgAygCACAHaiIJQe+GwIAATw0AIAogCSAIakkNIQsC
QCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCA
gACAgICAACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQRBJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNHiAGQafT
wIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQ8CQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoi
ByAJSQ0gIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8g
DKchCAsgBiAIaiIIIAZJDR4gCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHIAZBB3MiCGoiCSAHSQ0eIAZBqIDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sN
EAJAIAMoAgAgB2oiCUGvgMCAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSAC
IAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEEHSQ0A
C0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDR4gBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0RAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAI
akkNIQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEB
IAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EQIAZrIghqIgkgB0kN
HiAGQcmJwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRICQCADKAIAIAdqIglB2YnAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIA
IgkgCGoiByAJSQ0gIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H/
/wNxDT8gDKchCAsgBiAIaiIIIAZJDR4gCCEGIAhBEEkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0eIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0A
IAkgC0sNEwJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAo
AgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYg
CEEDSQ0AC0EAIQYDQCAFKAIAIgcgBkEHcyIIaiIJIAdJDR4gBkGogMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0UAkAgAygCACAHaiIJQa+AwIAATw0A
IAogCSAIakkNIQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJB
GGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQQdJDQALQQAhBgNAIAUoAgAiByAGQQNzIghq
IgkgB0kNHiAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRUCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAA
CyAFKAIAIgkgCGoiByAJSQ0gIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIg
iKciB0H//wNxDT8gDKchCAsgBiAIaiIIIAZJDR4gCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0eIAZB2JLAgABqIQoCQAJAIAkgBCgC
ACILSw0AIAkgC0sNFgJAIAMoAgAgB2oiCUHbksCAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAEL
IAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkN
HiAIIQYgCEEDSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDR4gBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0XAkAgAygCACAHaiIJQarT
wIAATw0AIAogCSAIakkNIQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEI
aiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQQNJDQALQQAhBiAAIAEoAiBBCkEA
QbDYyYAAEJWAgIAAIgdB//8DcQ09A0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0eIAZBrYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNGAJAIAMoAgAgB2oi
CUGvgMCAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIY
IAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEECSQ0AC0EAIQYDQCAFKAIA
IgcgBkEDcyIIaiIJIAdJDR4gBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0ZAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNIQsCQCAIRQ0A
IAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNICAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICA
ACACKQMIIgxCIIinIgdB//8DcQ0/IAynIQgLIAYgCGoiCCAGSQ0eIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EKIAZrIghqIgkgB0kNHiAGQY2MwIAAaiEK
AkACQCAJIAQoAgAiC0sNACAJIAtLDRoCQCADKAIAIAdqIglBl4zAgABPDQAgCiAJIAhqSQ0hCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0g
IAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDT8gDKchCAsg
BiAIaiIIIAZJDR4gCCEGIAhBCkkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0eIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNGwJAIAMo
AgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2Ahwg
AiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEEDSQ0ACyAAIAEt
ACUQvISAgAAiB0H//wNxDT1BACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0eIAZBrYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNHAJAIAMoAgAgB2oi
CUGvgMCAAE8NACAKIAkgCGpJDSELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIY
IAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENPyAMpyEICyAGIAhqIgggBkkNHiAIIQYgCEECTw0cDAALCwALIAkgCxCI
gICAAAALIAkgCxCIgICAAAwaCyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAIIAoQioCAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAA
CyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQ
iICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAA
CyAJIAsQiICAgAAACyAJIAsQiICAgAAACyALIAo2AgAgBiANNgIADCILIAEtACghBgsCQAJAAkACQAJAAkAgBkEfcUEIRw0AQQAhBgJAAkACQAJAAkACQAJA
AkACQAJAAkACQAJAAkACQAJAAkACQANAIAUoAgAiB0ECIAZrIghqIgkgB0kNGSAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdq
IglBsoDAgABPDQAgCiAJIAhqSQ0cCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0bIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTogDKchCAsgBiAIaiIIIAZJDRkgCCEGIAhBAkkNAAtBACEGA0AgBSgC
ACIHQQIgBmsiCGoiCSAHSQ0ZIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDRwLAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRsgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNGSAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBBCAGayIIaiIJIAdJDRkgBkHqhMCAAGoh
CgJAAkAgCSAEKAIAIgtLDQAgCSALSw0FAkAgAygCACAHaiIJQe6EwIAATw0AIAogCSAIakkNHAsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkN
GyAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ06IAynIQgL
IAYgCGoiCCAGSQ0ZIAghBiAIQQRJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNGSAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYCQCAD
KAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0cCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0bIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIc
IAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTogDKchCAsgBiAIaiIIIAZJDRkgCCEGIAhBA0kNAAtBACEG
A0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0ZIAZBsIDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBwJAIAMoAgAgB2oiCUGygMCAAE8NACAKIAkgCGpJDRwL
AkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRsgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGA
gIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNGSAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDRkgBkGD
08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0IAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNHAsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhq
IgcgCUkNGyAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ06
IAynIQgLIAYgCGoiCCAGSQ0ZIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiB0EFIAZrIghqIgkgB0kNFCAGQaqKwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtL
DQkCQCADKAIAIAdqIglBr4rAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkg
AiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTogDKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBBUkN
AAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0UIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkg
CGpJDRcLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFB
ASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEEDSQ0AC0EAIQYDQCAFKAIAIgcgBkEHcyIIaiIJIAdJ
DRQgBkGogMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0LAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNFwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgC
ACIJIAhqIgcgCUkNFiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB
//8DcQ06IAynIQgLIAYgCGoiCCAGSQ0UIAghBiAIQQdJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNFCAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sN
ACAJIAtLDQwCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIA
KAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTogDKchCAsgBiAIaiIIIAZJDRQgCCEG
IAhBA0kNAAtBACEGA0AgBSgCACIHQQggBmsiCGoiCSAHSQ0UIAZBvoDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDQJAIAMoAgAgB2oiCUHGgMCAAE8N
ACAKIAkgCGpJDRcLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACAC
QRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEEISQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyII
aiIJIAdJDRQgBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0OAkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNFwsCQCAIRQ0AIAkgCiAI/AoA
AAsgBSgCACIJIAhqIgcgCUkNFiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxC
IIinIgdB//8DcQ06IAynIQgLIAYgCGoiCCAGSQ0UIAghBiAIQQNJDQALQQAhBiAAIAEoAghBCkEAQbDYyYAAEJWAgIAAIgdB//8DcQ04A0AgBSgCACIHQQIg
BmsiCGoiCSAHSQ0UIAZBrYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDwJAIAMoAgAgB2oiCUGvgMCAAE8NACAKIAkgCGpJDRcLAkAgCEUNACAJIAog
CPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikD
CCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDRQgBkGC08CAAGohCgJAAkAg
CSAEKAIAIgtLDQAgCSALSw0QAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNFwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNFiAFIAc2
AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ06IAynIQgLIAYgCGoi
CCAGSQ0UIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EKIAZrIghqIgkgB0kNFCAGQY2MwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRECQCADKAIAIAdq
IglBl4zAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTogDKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBCkkNAAtBACEGA0AgBSgC
ACIHIAZBA3MiCGoiCSAHSQ0UIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNEgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRcLAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEEDSQ0ACyAAIAEtAAwQvISAgAAiB0H//wNxDThBACEGA0AgBSgCACIHQQIg
BmsiCGoiCSAHSQ0UIAZBrYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNEwJAIAMoAgAgB2oiCUGvgMCAAE8NACAKIAkgCGpJDRcLAkAgCEUNACAJIAog
CPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikD
CCIMQiCIpyIHQf//A3ENOiAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEECTw0SDAALCyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQ
iICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAA
CyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyABLQAoIQYLAkAgBkEfcUEJ
Rw0AQQAhBgJAAkACQAJAAkACQAJAAkACQANAIAUoAgAiB0ECIAZrIghqIgkgB0kNCyAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIA
IAdqIglBsoDAgABPDQAgCiAJIAhqSQ0OCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIg
CjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBAkkNAAtBACEGA0Ag
BSgCACIHQQIgBmsiCGoiCSAHSQ0LIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQ4LAkAg
CEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQ0gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAA
gICAgAAgAikDCCIMQiCIpyIHQf//A3ENMSAMpyEICyAGIAhqIgggBkkNCyAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBBSAGayIIaiIJIAdJDQsgBkGTnMCA
AGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0FAkAgAygCACAHaiIJQZicwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcg
CUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0xIAyn
IQgLIAYgCGoiCCAGSQ0LIAghBiAIQQVJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNCyAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYC
QCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0OCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAI
NgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBA0kNAAsg
ACABKAIAIAEoAgQQuoSAgAAiB0H//wNxDS9BACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0LIAZBgtPAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBwJA
IAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQ0gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2
AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENMSAMpyEICyAGIAhqIgggBkkNCyAIIQYgCEEDSQ0AC0EA
IQYDQCAFKAIAIgdBBCAGayIIaiIJIAdJDQsgBkGtgsCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0IAkAgAygCACAHaiIJQbGCwIAATw0AIAogCSAIakkN
DgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkR
gYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0xIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQRJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNCyAG
QafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQkCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0OCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkg
CGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNx
DTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBA0kNAAsgACABKAIIIAEoAgwQuoSAgAAiB0H//wNxDS9BACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0LIAZB
rYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCgJAIAMoAgAgB2oiCUGvgMCAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDQ0gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
MSAMpyEICyAGIAhqIgggBkkNCyAIIQYgCEECTw0JDAALCyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICA
gAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyABLQAoIQYLAkAgBkEfcUEKRw0AQQAhBgJAAkACQAJAAkACQAJAAkACQANAIAUoAgAi
B0ECIAZrIghqIgkgB0kNCyAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdqIglBsoDAgABPDQAgCiAJIAhqSQ0OCwJAIAhFDQAg
CSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAA
IAIpAwgiDEIgiKciB0H//wNxDTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0LIAZBg9PAgABqIQoC
QAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQ0g
BSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENMSAMpyEICyAG
IAhqIgggBkkNCyAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQsgBkHln8CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0FAkAgAygC
ACAHaiIJQeefwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCAC
IAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0xIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQJJDQALQQAhBgNA
IAUoAgAiByAGQQNzIghqIgkgB0kNCyAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0OCwJA
IAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICA
AICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBA0kNAAsCQAJAIAEoAgAiBkUNACAAIAYgASgCBBC6hICAACEHDAEL
IABB5pPAgABBBBCggICAACEHCyAHQf//A3ENL0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQsgBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0H
AkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIg
CDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0xIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQNJDQAL
QQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNCyAGQcmVwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQgCQCADKAIAIAdqIglBzJXAgABPDQAgCiAJIAhq
SQ0OCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0NIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEg
CRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDTEgDKchCAsgBiAIaiIIIAZJDQsgCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0L
IAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCQJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDQ4LAkAgCEUNACAJIAogCPwKAAALIAUoAgAi
CSAIaiIHIAlJDQ0gBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//
A3ENMSAMpyEICyAGIAhqIgggBkkNCyAIIQYgCEEDSQ0ACyAAIAEoAgggASgCDBC6hICAACIHQf//A3ENL0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQsg
BkGtgMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0KAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNDgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJ
IAhqIgcgCUkNDSAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8D
cQ0xIAynIQgLIAYgCGoiCCAGSQ0LIAghBiAIQQJPDQkMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCI
gICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAEtACghBgsCQCAGQR9xQQtHDQAgAEHbn8CAAEEEEKCAgIAAIgdB//8DcQ0mIAEt
ACghBgsgBkEfcUEMRw0EQQAhBgJAAkACQANAIAUoAgAiB0ECIAZrIghqIgkgB0kNBCAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIA
IAdqIglBsoDAgABPDQAgCiAJIAhqSQ0HCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0GIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIg
CjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSogDKchCAsgBiAIaiIIIAZJDQQgCCEGIAhBAkkNAAtBACEGA0Ag
BSgCACIHQQIgBmsiCGoiCSAHSQ0EIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQcLAkAg
CEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAA
gICAgAAgAikDCCIMQiCIpyIHQf//A3ENKiAMpyEICyAGIAhqIgggBkkNBCAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBCyAGayIIaiIJIAdJDQQgBkG3isCA
AGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0FAkAgAygCACAHaiIJQcKKwIAATw0AIAogCSAIakkNBwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcg
CUkNBiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0qIAyn
IQgLIAYgCGoiCCAGSQ0EIAghBiAIQQtJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNEiAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQcC
QCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0VCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0UIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAI
NgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSogDKchCAsgBiAIaiIIIAZJDRIgCCEGIAhBA0kNAAtB
ACEGIAAgAS8BAEEKQQBBsNjJgAAQlYCAgAAiB0H//wNxDSgDQCAFKAIAIgdBAiAGayIIaiIJIAdJDRIgBkGtgMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSAL
Sw0IAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNFQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNFCAFIAc2AgAMAQsgACgCACgCACEJ
IAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0qIAynIQgLIAYgCGoiCCAGSQ0SIAghBiAIQQJP
DQcMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgAS0AKCEGCwJAIAZBH3FBDUcNACAA
IAEoAgAgASgCBBC6hICAACIHQf//A3ENISABLQAoIQYLAkAgBkEfcUEORw0AQQAhBgJAAkACQAJAA0AgBSgCACIHQQQgBmsiCGoiCSAHSQ0PIAZB/dLAgABq
IQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAgB2oiCUGB08CAAE8NACAKIAkgCGpJDRILAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJ
DREgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENJyAMpyEI
CyAGIAhqIgggBkkNDyAIIQYgCEEESQ0AC0HPmcCAAEGKiMCAACABLQAIIgYbIQ5BypnAgABBhYjAgAAgBhshDUEAIQYDQCAFKAIAIgdBBSAGayIIaiIJIAdJ
DQ8gDSAGaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQQCQCADKAIAIAdqIgkgDk8NACAKIAkgCGpJDRILAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIH
IAlJDREgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENJyAM
pyEICyAGIAhqIgggBkkNDyAIIQYgCEEFSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQ8gBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0F
AkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNEgsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNESAFIAc2AgAMAQsgACgCACgCACEJIAIg
CDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0nIAynIQgLIAYgCGoiCCAGSQ0PIAghBiAIQQNJDQAL
AkAgAS0ACA0AIABB25/AgABBBBCggICAACIHQf//A3ENJiABLQAIRQ0ECyAAIAEoAgAgASgCBBC6hICAACIHQf//A3FFDQMMJQsgCSALEIiAgIAAAAsgCSAL
EIiAgIAAAAsgCSALEIiAgIAAAAtBACEGAkADQCAFKAIAIgdBAiAGayIIaiIJIAdJDQwgBkGtgMCAAGohCgJAAkACQCAJIAQoAgAiC0sNACAJIAtLDQICQCAD
KAIAIAdqIglBr4DAgABPDQAgCiAJIAhqSQ0QCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0PIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIc
IAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSUgDKchCAsgBiAIaiIIIAZJDQ0gCCEGIAhBAk8NAgwBCwsg
CSALEIiAgIAAAAsgAS0AKCEGCwJAIAZBH3FBD0cNAEEAIQYCQAJAAkACQAJAAkACQAJAAkACQAJAA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0WIAZBsIDAgABq
IQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAgB2oiCUGygMCAAE8NACAKIAkgCGpJDRkLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJ
DRggBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENLiAMpyEI
CyAGIAhqIgggBkkNFiAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDRYgBkGD08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0EAkAg
AygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNGQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNGCAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0uIAynIQgLIAYgCGoiCCAGSQ0WIAghBiAIQQJJDQALQQAh
BgNAIAUoAgAiB0EFIAZrIghqIgkgB0kNFiAGQeCawIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdqIglB5ZrAgABPDQAgCiAJIAhqSQ0Z
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0YIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDS4gDKchCAsgBiAIaiIIIAZJDRYgCCEGIAhBBUkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0WIAZB
p9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRkLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDRggBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
LiAMpyEICyAGIAhqIgggBkkNFiAIIQYgCEEDSQ0ACyABKAIAIQYgAkEBNgIMIAJBhNPAgAA2AgggBkEESw0MQQYhCEHymMCAACEJAkACQAJAAkACQCAGDgUE
AAECAwQLQQMhCEGiiMCAACEJDAMLQQUhCEG/jMCAACEJDAILQQ0hCEHmmsCAACEJDAELQQUhCEH0msCAACEJCyACQQhqQQRqIQsgAiAJNgIQIAIgCDYCFEEA
IQkgAkEIaiEGQQEhCkEAIQgDQCAGIAogCGs2AgQgBiAGKAIAIg0gCGo2AgAgAkEYaiAAIAZBAiAJa0EBEKOAgIAAIAIpAxgiDEIgiKciB0H//wNxDQogCCAM
p2oiByAISQ0WIAsgCjYCACAGIA02AgACQAJAAkAgByACQQhqIAlBA3RqKAIEIgZPDQAgByEIDAELIAkNAUEBIQkgByAGayIIIAIoAhRPDQELIAJBCGogCUED
dGoiBkEEaiELIAggBigCBCIKSw0GDAELC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDRYgBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0IAkAg
AygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNGQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNGCAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0uIAynIQgLIAYgCGoiCCAGSQ0WIAghBiAIQQNJDQALQQAh
BgNAIAUoAgAiB0EIIAZrIghqIgkgB0kNFiAGQeCJwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQkCQCADKAIAIAdqIglB6InAgABPDQAgCiAJIAhqSQ0Z
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0YIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDS4gDKchCAsgBiAIaiIIIAZJDRYgCCEGIAhBCEkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0WIAZB
p9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCgJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRkLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDRggBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
LiAMpyEICyAGIAhqIgggBkkNFiAIIQYgCEEDSQ0ACwJAAkAgAS0ABUUNACAAIAEtAARBCkEAQbDYyYAAEJWAgIAAIQcMAQsgAEHmk8CAAEEEEKCAgIAAIQcL
IAdB//8DcQ0sQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNFiAGQa2AwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQsCQCADKAIAIAdqIglBr4DAgABP
DQAgCiAJIAhqSQ0ZCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0YIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAg
AkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDS4gDKchCAsgBiAIaiIIIAZJDRYgCCEGIAhBAk8NCwwACwsgCSALEIiAgIAAAAsgCSAL
EIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCCAKEIqAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAA
AAsgCyAKNgIAIAYgDTYCAAwiCyABLQAoIQYLAkAgBkEfcUEQRw0AIABB25/AgABBBBCggICAACIHQf//A3ENISABLQAoIQYLAkAgBkEfcUERRw0AIAAgASgC
ACABKAIEELqEgIAAIgdB//8DcQ0hIAEtACghBgsCQCAGQR9xQRJHDQAgACABKAIAIAEoAgQQuoSAgAAiB0H//wNxDSEgAS0AKCEGCwJAIAZBH3FBE0cNACAA
IAEoAgAgASgCBBC6hICAACIHQf//A3ENISABLQAoIQYLAkAgBkEfcUEURw0AQQAhBgJAAkACQAJAAkACQAJAAkACQANAIAUoAgAiB0ECIAZrIghqIgkgB0kN
FCAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdqIglBsoDAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIA
IgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H/
/wNxDSwgDKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0UIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0A
IAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDRcLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAo
AgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENLCAMpyEICyAGIAhqIgggBkkNFCAIIQYg
CEECSQ0AC0EAIQYDQCAFKAIAIgdBCCAGayIIaiIJIAdJDRQgBkGqnsCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0FAkAgAygCACAHaiIJQbKewIAATw0A
IAogCSAIakkNFwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNFiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJB
GGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0sIAynIQgLIAYgCGoiCCAGSQ0UIAghBiAIQQhJDQALQQAhBgNAIAUoAgAiByAGQQNzIghq
IgkgB0kNFCAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAA
CyAFKAIAIgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIg
iKciB0H//wNxDSwgDKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBA0kNAAsCQAJAIAEtAAFFDQAgAEHFmcCAAEH6msCAACABLQAAIgYbQQRBBSAGGyIGIAZBAC0A
wNjJgABBIBCWgICAACEHDAELIABB5pPAgABBBBCggICAACEHCyAHQf//A3ENKkEAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDRQgBkGC08CAAGohCgJAAkAg
CSAEKAIAIgtLDQAgCSALSw0HAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNFwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNFiAFIAc2
AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0sIAynIQgLIAYgCGoi
CCAGSQ0UIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EGIAZrIghqIgkgB0kNFCAGQeODwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQgCQCADKAIAIAdq
IglB6YPAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSwgDKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBBkkNAAtBACEGA0AgBSgC
ACIHIAZBA3MiCGoiCSAHSQ0UIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCQJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRcLAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRYgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENLCAMpyEICyAGIAhqIgggBkkNFCAIIQYgCEEDSQ0ACwJAAkAgAS0AA0UNACAAQcWZwIAAQfqawIAAIAEtAAIiBhtBBEEF
IAYbIgYgBkEALQDA2MmAAEEgEJaAgIAAIQcMAQsgAEHmk8CAAEEEEKCAgIAAIQcLIAdB//8DcQ0qQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNFCAGQa2A
wIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQoCQCADKAIAIAdqIglBr4DAgABPDQAgCiAJIAhqSQ0XCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoi
ByAJSQ0WIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSwg
DKchCAsgBiAIaiIIIAZJDRQgCCEGIAhBAk8NCQwACwsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAA
AAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgAS0AKCEGCwJAIAZBH3FBFUcNACAAIAEoAgAgASgCBBC6hICAACIHQf//A3ENISABLQAo
IQYLIAZBH3FBFkcNHkEAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQogBkGwgMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0EAkAgAygCACAHaiIJQbKA
wIAATw0AIAogCSAIakkNDQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDCAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEI
aiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0KIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiB0EC
IAZrIghqIgkgB0kNCiAGQYPTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJIAhqSQ0NCwJAIAhFDQAgCSAK
IAj8CgAACyAFKAIAIgkgCGoiByAJSQ0MIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIp
AwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDQogCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQUgBmsiCGoiCSAHSQ0KIAZBlZ3AgABqIQoCQAJA
IAkgBCgCACILSw0AIAkgC0sNBgJAIAMoAgAgB2oiCUGancCAAE8NACAKIAkgCGpJDQ0LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQwgBSAH
NgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENIiAMpyEICyAGIAhq
IgggBkkNCiAIIQYgCEEFSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQogBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0HAkAgAygCACAH
aiIJQarTwIAATw0AIAogCSAIakkNDQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDCAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2
AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0KIAghBiAIQQNJDQALQQAhBiAAIAEt
AAhBB3FBCkEAQbDYyYAAEJWAgIAAIgdB//8DcQ0gA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0KIAZBgtPAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCAJA
IAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDQ0LAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDQwgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2
AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENIiAMpyEICyAGIAhqIgggBkkNCiAIIQYgCEEDSQ0AC0EA
IQYDQCAFKAIAIgdBBSAGayIIaiIJIAdJDQogBkHNlcCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0JAkAgAygCACAHaiIJQdKVwIAATw0AIAogCSAIakkN
DQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNDCAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkR
gYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0KIAghBiAIQQVJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNCiAG
QafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQoCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0NCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkg
CGoiByAJSQ0MIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNx
DSIgDKchCAsgBiAIaiIIIAZJDQogCCEGIAhBA0kNAAtBACEGIAAgAS0ACUEHcUEKQQBBsNjJgAAQlYCAgAAiB0H//wNxDSADQCAFKAIAIgcgBkEDcyIIaiIJ
IAdJDQogBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0LAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNDQsCQCAIRQ0AIAkgCiAI/AoAAAsg
BSgCACIJIAhqIgcgCUkNIiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIin
IgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0gIAghBiAIQQNJDQALQQAhBgNAIAUoAgAiB0EJIAZrIghqIgkgB0kNICAGQYOMwIAAaiEKAkACQCAJIAQoAgAi
C0sNACAJIAtLDQ0CQCADKAIAIAdqIglBjIzAgABPDQAgCiAJIAhqSQ0jCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAA
KAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAg
CCEGIAhBCUkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0gIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDgJAIAMoAgAgB2oiCUGq08CA
AE8NACAKIAkgCGpJDSMLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGog
ACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENIiAMpyEICyAGIAhqIgggBkkNICAIIQYgCEEDSQ0AC0EAIQYgACABLQAKQQ9xQQpB
AEGw2MmAABCVgICAACIHQf//A3ENIANAIAUoAgAiByAGQQNzIghqIgkgB0kNICAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQ8CQCADKAIAIAdq
IglBhdPAgABPDQAgCiAJIAhqSQ0jCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAgCCEGIAhBA0kNAAtBACEGA0AgBSgC
ACIHQQsgBmsiCGoiCSAHSQ0gIAZBmIzAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNEAJAIAMoAgAgB2oiCUGjjMCAAE8NACAKIAkgCGpJDSMLAkAgCEUN
ACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDSIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICA
gAAgAikDCCIMQiCIpyIHQf//A3ENIiAMpyEICyAGIAhqIgggBkkNICAIIQYgCEELSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDSAgBkGn08CAAGoh
CgJAAkAgCSAEKAIAIgtLDQAgCSALSw0RAkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNIwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkN
IiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgL
IAYgCGoiCCAGSQ0gIAghBiAIQQNJDQALQQAhBiAAIAEtAAtBD3FBCkEAQbDYyYAAEJWAgIAAIgdB//8DcQ0gA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0gIAZB
gtPAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNEgJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDSMLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDSIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
IiAMpyEICyAGIAhqIgggBkkNICAIIQYgCEEDSQ0AC0EAIQYDQCAFKAIAIgdBBiAGayIIaiIJIAdJDSAgBkHKksCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSAL
Sw0TAkAgAygCACAHaiIJQdCSwIAATw0AIAogCSAIakkNIwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNIiAFIAc2AgAMAQsgACgCACgCACEJ
IAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0gIAghBiAIQQZJ
DQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNICAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRQCQCADKAIAIAdqIglBqtPAgABPDQAgCiAJ
IAhqSQ0jCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEB
QQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAgCCEGIAhBA0kNAAsgAS0ADCEIIAJBATYCDCACQYTTwIAANgII
QQMhBiAIQQNxIglBA0YNAEGcjsCAACEIAkACQAJAIAkOAwIAAQILQQYhBkHuksCAACEIDAELQQYhBkGHjcCAACEICyACQQxqIQsgAiAINgIQIAIgBjYCFEEA
IQkgAkEIaiEGQQEhCkEAIQgDQCAGIAogCGs2AgQgBiAGKAIAIg0gCGo2AgAgAkEYaiAAIAZBAiAJa0EBEKOAgIAAIAIpAxgiDEIgiKciB0H//wNxDRwgCCAM
p2oiByAISQ0gIAsgCjYCACAGIA02AgACQAJAAkAgByACQQhqIAlBA3RqKAIEIgZPDQAgByEIDAELIAkNAUEBIQkgByAGayIIIAIoAhRPDQELIAJBCGogCUED
dGoiBkEEaiELIAggBigCBCIKSw0UDAELC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDSAgBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0WAkAg
AygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNIwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNIiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0gIAghBiAIQQNJDQALQQAh
BgNAIAUoAgAiB0EGIAZrIghqIgkgB0kNICAGQdGSwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRcCQCADKAIAIAdqIglB15LAgABPDQAgCiAJIAhqSQ0j
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAgCCEGIAhBBkkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0gIAZB
p9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNGAJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDSMLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDSIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
IiAMpyEICyAGIAhqIgggBkkNICAIIQYgCEEDSQ0ACyABLQANIQYgAkEBNgIMIAJBhNPAgAA2AgggBkEDcSIJQQNGDQBBxIfAgAAhBkEEIQgCQAJAAkAgCQ4D
AgABAgtBBSEIQaeHwIAAIQYMAQtBBiEIQYeNwIAAIQYLIAJBDGohCyACIAY2AhAgAiAINgIUQQAhCSACQQhqIQZBASEKQQAhCANAIAYgCiAIazYCBCAGIAYo
AgAiDSAIajYCACACQRhqIAAgBkECIAlrQQEQo4CAgAAgAikDGCIMQiCIpyIHQf//A3ENHSAIIAynaiIHIAhJDSAgCyAKNgIAIAYgDTYCAAJAAkACQCAHIAJB
CGogCUEDdGooAgQiBk8NACAHIQgMAQsgCQ0BQQEhCSAHIAZrIgggAigCFE8NAQsgAkEIaiAJQQN0aiIGQQRqIQsgCCAGKAIEIgpLDRgMAQsLQQAhBgNAIAUo
AgAiByAGQQNzIghqIgkgB0kNICAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDRoCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJIAhqSQ0jCwJAIAhF
DQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICA
gIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAgCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHQQQgBmsiCGoiCSAHSQ0gIAZBzoPAgABq
IQoCQAJAIAkgBCgCACILSw0AIAkgC0sNGwJAIAMoAgAgB2oiCUHSg8CAAE8NACAKIAkgCGpJDSMLAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJ
DSIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENIiAMpyEI
CyAGIAhqIgggBkkNICAIIQYgCEEESQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDSAgBkGn08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0cAkAg
AygCACAHaiIJQarTwIAATw0AIAogCSAIakkNIwsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNIiAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0iIAynIQgLIAYgCGoiCCAGSQ0gIAghBiAIQQNJDQALIAAg
ASgCACABKAIEELqEgIAAIgdB//8DcQ0gQQAhBgNAIAUoAgAiB0ECIAZrIghqIgkgB0kNICAGQa2AwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDR0CQCAD
KAIAIAdqIglBr4DAgABPDQAgCiAJIAhqSQ0jCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0iIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIc
IAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDSIgDKchCAsgBiAIaiIIIAZJDSAgCCEGIAhBAk8NHgwACwsA
CyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQ
iICAgAALAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAA
AAsgCSALEIiAgIAAAAsgCCAKEIqAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCCAKEIqAgIAAAAsgCSALEIiAgIAAAAsgCSAL
EIiAgIAAAAsgCSALEIiAgIAAAAsgCSALEIiAgIAAAAsgCyAKNgIAIAYgDTYCAAwECyALIAo2AgAgBiANNgIADAMLIAEtACghBgsCQCAGQR9xQRdHDQBBACEG
AkACQAJAAkACQAJAAkACQAJAAkACQAJAA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0OIAZBsIDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNAwJAIAMoAgAg
B2oiCUGygMCAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAK
NgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENECAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEECSQ0AC0EAIQYDQCAF
KAIAIgdBAiAGayIIaiIJIAdJDQ4gBkGD08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0EAkAgAygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNEQsCQCAI
RQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACA
gICAACACKQMIIgxCIIinIgdB//8DcQ0QIAynIQgLIAYgCGoiCCAGSQ0OIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiB0EIIAZrIghqIgkgB0kNDiAGQeuhwIAA
aiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQUCQCADKAIAIAdqIglB86HAgABPDQAgCiAJIAhqSQ0RCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJ
SQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRAgDKch
CAsgBiAIaiIIIAZJDQ4gCCEGIAhBCEkNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0OIAZBp9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBgJA
IAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2
AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENECAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEEDSQ0ACyAA
IAEoAgAgASgCBBC6hICAACIHQf//A3ENDkEAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQ4gBkGC08CAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0HAkAg
AygCACAHaiIJQYXTwIAATw0AIAogCSAIakkNEQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYC
HCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0QIAynIQgLIAYgCGoiCCAGSQ0OIAghBiAIQQNJDQALQQAh
BgNAIAUoAgAiByAGQQdzIghqIgkgB0kNDiAGQZKhwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQgCQCADKAIAIAdqIglBmaHAgABPDQAgCiAJIAhqSQ0R
CwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGB
gICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRAgDKchCAsgBiAIaiIIIAZJDQ4gCCEGIAhBB0kNAAtBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0OIAZB
p9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCQJAIAMoAgAgB2oiCUGq08CAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
ECAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEEDSQ0ACwJAAkAgASgCCCIGRQ0AIAAgBiABKAIMELqEgIAAIQcMAQsgAEHmk8CAAEEEEKCAgIAAIQcLIAdB//8D
cQ0OQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNDiAGQYLTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQoCQCADKAIAIAdqIglBhdPAgABPDQAgCiAJ
IAhqSQ0RCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEB
QQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRAgDKchCAsgBiAIaiIIIAZJDQ4gCCEGIAhBA0kNAAtBACEGA0AgBSgCACIHQQogBmsiCGoiCSAH
SQ0OIAZBjYzAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCwJAIAMoAgAgB2oiCUGXjMCAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUo
AgAiCSAIaiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIH
Qf//A3ENECAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEEKSQ0AC0EAIQYDQCAFKAIAIgcgBkEDcyIIaiIJIAdJDQ4gBkGn08CAAGohCgJAAkAgCSAEKAIAIgtL
DQAgCSALSw0MAkAgAygCACAHaiIJQarTwIAATw0AIAogCSAIakkNEQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsgACgC
ACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0QIAynIQgLIAYgCGoiCCAGSQ0OIAgh
BiAIQQNJDQALIAAgAS0AEBC8hICAACIHQf//A3ENDkEAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQ4gBkGtgMCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSAL
Sw0NAkAgAygCACAHaiIJQa+AwIAATw0AIAogCSAIakkNEQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsgACgCACgCACEJ
IAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0QIAynIQgLIAYgCGoiCCAGSQ0OIAghBiAIQQJP
DQwMAAsLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAAL
IAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAEtACghBgsCQCAGQR9xQRhHDQBBACEGAkACQAJAAkACQAJAAkACQAJA
AkACQAJAAkACQANAIAUoAgAiB0ECIAZrIghqIgkgB0kNECAGQbCAwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdqIglBsoDAgABPDQAg
CiAJIAhqSQ0TCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0SIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEY
akEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRIgDKchCAsgBiAIaiIIIAZJDRAgCCEGIAhBAkkNAAtBACEGA0AgBSgCACIHQQIgBmsiCGoi
CSAHSQ0QIAZBg9PAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNBAJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDRMLAkAgCEUNACAJIAogCPwKAAAL
IAUoAgAiCSAIaiIHIAlJDRIgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCI
pyIHQf//A3ENEiAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEECSQ0AC0EAIQYDQCAFKAIAIgdBBiAGayIIaiIJIAdJDQ4gBkGSkMCAAGohCgJAAkAgCSAEKAIA
IgtLDQAgCSALSw0FAkAgAygCACAHaiIJQZiQwIAATw0AIAogCSAIakkNEQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsg
ACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0SIAynIQgLIAYgCGoiCCAGSQ0O
IAghBiAIQQZJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNDiAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQYCQCADKAIAIAdqIglBqtPA
gABPDQAgCiAJIAhqSQ0RCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhq
IAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRIgDKchCAsgBiAIaiIIIAZJDQ4gCCEGIAhBA0kNAAsgAS0AECEGIAJBATYCDCAC
QYTTwIAANgIIIAJBA0EFIAZBAXEiBhs2AhQgAkGyn8CAAEHUhcCAACAGGzYCECACQQhqQQRqIQtBACEJIAJBCGohBkEBIQpBACEIA0AgBiAKIAhrNgIEIAYg
BigCACINIAhqNgIAIAJBGGogACAGQQIgCWtBARCjgICAACACKQMYIgxCIIinIgdB//8DcQ0GIAggDKdqIgcgCEkNDiALIAo2AgAgBiANNgIAAkACQAJAIAcg
AkEIaiAJQQN0aigCBCIGTw0AIAchCAwBCyAJDQFBASEJIAcgBmsiCCACKAIUTw0BCyACQQhqIAlBA3RqIgZBBGohCyAIIAYoAgQiCksNBgwBCwtBACEGA0Ag
BSgCACIHIAZBA3MiCGoiCSAHSQ0OIAZBgtPAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNCQJAIAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDRELAkAg
CEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAA
gICAgAAgAikDCCIMQiCIpyIHQf//A3ENEiAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEEDSQ0AC0EAIQYDQCAFKAIAIgdBAiAGayIIaiIJIAdJDQ4gBkHln8CA
AGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0KAkAgAygCACAHaiIJQeefwIAATw0AIAogCSAIakkNEQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcg
CUkNECAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkRgYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0SIAyn
IQgLIAYgCGoiCCAGSQ0OIAghBiAIQQJJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNDiAGQafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQsC
QCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0RCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAI
NgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDRIgDKchCAsgBiAIaiIIIAZJDQ4gCCEGIAhBA0kNAAsg
ACABKAIAIAEoAgQQuoSAgAAiB0H//wNxDRBBACEGA0AgBSgCACIHIAZBA3MiCGoiCSAHSQ0OIAZBgtPAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDAJA
IAMoAgAgB2oiCUGF08CAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAIaiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2
AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3ENEiAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEEDSQ0AC0EA
IQYDQCAFKAIAIgdBCCAGayIIaiIJIAdJDQ4gBkHrocCAAGohCgJAAkAgCSAEKAIAIgtLDQAgCSALSw0NAkAgAygCACAHaiIJQfOhwIAATw0AIAogCSAIakkN
EQsCQCAIRQ0AIAkgCiAI/AoAAAsgBSgCACIJIAhqIgcgCUkNECAFIAc2AgAMAQsgACgCACgCACEJIAIgCDYCHCACIAo2AhggAkEIaiAAIAJBGGpBAUEBIAkR
gYCAgACAgICAACACKQMIIgxCIIinIgdB//8DcQ0SIAynIQgLIAYgCGoiCCAGSQ0OIAghBiAIQQhJDQALQQAhBgNAIAUoAgAiByAGQQNzIghqIgkgB0kNDiAG
QafTwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQ4CQCADKAIAIAdqIglBqtPAgABPDQAgCiAJIAhqSQ0RCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkg
CGoiByAJSQ0QIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYCGCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNx
DRIgDKchCAsgBiAIaiIIIAZJDQ4gCCEGIAhBA0kNAAsgACABKAIIIAEoAgwQuoSAgAAiB0H//wNxDRBBACEGA0AgBSgCACIHQQIgBmsiCGoiCSAHSQ0OIAZB
rYDAgABqIQoCQAJAIAkgBCgCACILSw0AIAkgC0sNDwJAIAMoAgAgB2oiCUGvgMCAAE8NACAKIAkgCGpJDRELAkAgCEUNACAJIAogCPwKAAALIAUoAgAiCSAI
aiIHIAlJDRAgBSAHNgIADAELIAAoAgAoAgAhCSACIAg2AhwgAiAKNgIYIAJBCGogACACQRhqQQFBASAJEYGAgIAAgICAgAAgAikDCCIMQiCIpyIHQf//A3EN
EiAMpyEICyAGIAhqIgggBkkNDiAIIQYgCEECTw0PDAALCyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAJIAsQiICAgAAACyAIIAoQioCA
gAAACyALIAo2AgAgBiANNgIADAoLIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICAAAALIAkgCxCIgICA
AAALIAkgCxCIgICAAAsAC0EAIQYCQANAIAUoAgAiB0ECIAZrIghqIgkgB0kNAiAGQa2AwIAAaiEKAkACQCAJIAQoAgAiC0sNACAJIAtLDQMCQCADKAIAIAdq
IglBr4DAgABPDQAgCiAJIAhqSQ0FCwJAIAhFDQAgCSAKIAj8CgAACyAFKAIAIgkgCGoiByAJSQ0EIAUgBzYCAAwBCyAAKAIAKAIAIQkgAiAINgIcIAIgCjYC
GCACQQhqIAAgAkEYakEBQQEgCRGBgICAAICAgIAAIAIpAwgiDEIgiKciB0H//wNxDQQgDKchCAsgBiAIaiIIIAZJDQIgCCEGIAhBAkkNAAtBACEHDAILIAkg
CxCIgICAAAALAAsgAkEgaiSAgICAACAHC9YCAwZ/AX4CfyOAgICAAEEgayICJICAgIAAIAJBATYCDCACQYTTwIAANgIIIAJBA0ECIAFBAXEiARs2AhQgAkGd
lMCAAEHxhMCAACABGzYCECACQQhqQQRqIQNBACEEIAJBCGohAUEBIQVBACEGAkACQAJAA0AgASAFIAZrNgIEIAEgASgCACIHIAZqNgIAIAJBGGogACABQQIg
BGtBARCjgICAACACKQMYIghCIIinIglB//8DcQ0BIAYgCKdqIgogBkkNAiADIAU2AgAgASAHNgIAAkACQCAKIAJBCGogBEEDdGooAgQiAU8NACAKIQYMAQsg
BA0EQQEhBCAKIAFrIgYgAigCFE8NBAsgAkEIaiAEQQN0aiIBQQRqIQMgBiABKAIEIgVNDQALIAYgBRCKgICAAAALIAMgBTYCACABIAc2AgAMAQsACyACQSBq
JICAgIAAIAkLyAEBBX8gAEEBaiEEQQAhBQJAAkACQAJAA0AgASgCACIGQeABcQ0BIAQgBWpBfyAGdEF/c3EiBiADTw0CAkACQCACIAZBAXRqIgctAAAiCEH/
AUYNACAHLQABIgdB/wFxDQELIAAgA0kNBAwFCyAAIANPDQQgAiAAQQF0aiIAIAdBf2o6AAEgACAIOgAAIAYhACADIAVBAWoiBUcNAAsLAAsgBiADEIiAgIAA
AAsgAiAAQQF0akH/1QI7AAAPCyAAIAMQiICAgAAAC8wBAQV/IABBAWohBEEAIQUCQAJAAkACQANAIAEoAgAiBkHgAXENASAEIAVqQX8gBnRBf3NxIgYgA08N
AgJAAkAgAiAGQQJ0aiIHLwEAIghB//8DRg0AIAcvAQIiB0H//wNxDQELIAAgA0kNBAwFCyAAIANPDQQgAiAAQQJ0aiIAIAdBf2o7AQIgACAIOwEAIAYhACAD
IAVBAWoiBUcNAAsLAAsgBiADEIiAgIAAAAsgAiAAQQJ0akH//6vVejYBAA8LIAAgAxCIgICAAAALygEBBX8gAEEBaiEEQQAhBQJAAkACQAJAA0AgASgCACIG
QeABcQ0BIAQgBWpBfyAGdEF/c3EiBiADTw0CAkACQCACIAZBA3RqIgcoAgAiCEF/Rg0AIAcoAgQiBw0BCyAAIANJDQQMBQsgACADTw0EIAIgAEEDdGoiACAH
QX9qNgIEIAAgCDYCACAGIQAgAyAFQQFqIgVHDQALCwALIAYgAxCIgICAAAALIAIgAEEDdGpC/////6/VqtWqfzcCAA8LIAAgAxCIgICAAAAL+gIDAn8BfgR/
I4CAgIAAQSBrIgUkgICAgAACQAJAAkACQCAAKAIIIgZBgICAgARPDQACQAJAIAYNAEEAIQBBqtWq1XohBgwBCyAAKAIAIgZBA3ENASAAKAIEIQALIAEgAE8N
ASAFQRBqIAYgAUECdGo1AgBCgYCAgBB+IgdC29HQhZra34FnhUIAIAdCrJri06PS8PofhUIAEK+FgIAAIAUgBSkDGELb0dCFmtrfgWeFQgAgBSkDEEKryPXF
x6yHu6B/hUIAEK+FgIAAIAIoAgAiAEHgAXENAEF/IAB0QX9zIQggBSkDCCAFKQMAhachCUEAIQBBACEGA0AgCSAAaiAIcSICIARPDQMgAyACQQF0aiIKLQAA
IgtB/wFGDQEgCi0AASAGQf8BcSIGSQ0BIAEgC0YNBCAGQQFqIgZB/wFxIAZHDQEgBCAAQQFqIgBHDQALCwALIAEgABCIgICAAAALIAIgBBCIgICAAAALIAVB
IGokgICAgAAgAgv9AgMCfwF+BH8jgICAgABBIGsiBSSAgICAAAJAAkACQAJAIAAoAggiBkGAgICABE8NAAJAAkAgBg0AQQAhAEGq1arVeiEGDAELIAAoAgAi
BkEDcQ0BIAAoAgQhAAsgASAATw0BIAVBEGogBiABQQJ0ajUCAEKBgICAEH4iB0Lb0dCFmtrfgWeFQgAgB0KsmuLTo9Lw+h+FQgAQr4WAgAAgBSAFKQMYQtvR
0IWa2t+BZ4VCACAFKQMQQqvI9cXHrIe7oH+FQgAQr4WAgAAgAigCACIAQeABcQ0AQX8gAHRBf3MhCCAFKQMIIAUpAwCFpyEJQQAhAEEAIQYDQCAJIABqIAhx
IgIgBE8NAyADIAJBAnRqIgovAQAiC0H//wNGDQEgCi8BAiAGQf//A3EiBkkNASABIAtGDQQgBkEBaiIGQf//A3EgBkcNASAEIABBAWoiAEcNAAsLAAsgASAA
EIiAgIAAAAsgAiAEEIiAgIAAAAsgBUEgaiSAgICAACACC98CAwJ/AX4DfyOAgICAAEEgayIFJICAgIAAAkACQAJAAkAgACgCCCIGQYCAgIAETw0AAkACQCAG
DQBBACEAQarVqtV6IQYMAQsgACgCACIGQQNxDQEgACgCBCEACyABIABPDQEgBUEQaiAGIAFBAnRqNQIAQoGAgIAQfiIHQtvR0IWa2t+BZ4VCACAHQqya4tOj
0vD6H4VCABCvhYCAACAFIAUpAxhC29HQhZra34FnhUIAIAUpAxBCq8j1xcesh7ugf4VCABCvhYCAACACKAIAIgBB4AFxDQBBfyAAdEF/cyEIIAUpAwggBSkD
AIWnIQlBACEAA0AgCSAAaiAIcSIGIARPDQMgAyAGQQN0aiICKAIAIgpBf0YNASACKAIEIABJDQEgCiABRg0EIAQgAEEBaiIARw0ACwsACyABIAAQiICAgAAA
CyAGIAQQiICAgAAACyAFQSBqJICAgIAAIAYLWAEBfyOAgICAAEEQayICJICAgIAAAkACQCAADQBBASEADAELIABBGGooAgAhACACQQM6AAwgAiABNgIAIAAg
AhDEhICAAEEAIQALIAJBEGokgICAgAAgAAv4GgELfyOAgICAAEHQAWsiAiSAgICAAAJAAkACQCAAKAJgDQAgAEEAOgCQAQwBCwJAAkACQAJAAkACQAJAAkAC
QAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAgAS0ADA4GAAEHBAMCGwsgAEEAOgCQAQwZCyAAQQE6AJABDBgLIAIgASgBBjYCCCACIAFBCmov
AQA7AQwgACgCWCIDRQ0YIAEvAQQhBCABKAIAIQUgAC8BjgEhAQJAA0AgAUH//wNxIAMvAaoCIgZNDQEgASAGayEBIAMoAggiAw0ADBoLCyAFIANGDQIDQCAD
KAIMIgNFDQQgAyAFRw0ADBcLCyABKAIAIgdFDRYgByEFAkAgB0EASiIDDQAgA0EAIAdrIgVBAEhHDRggBUF/TA0YCwJAAkACQAJAAkACQAJAAkAgAC0AkAEO
BAABAh8ACyAAKAJYIgNFDR4gAC8BjgEhAQNAAkAgAUH//wNxIAMvAaoCIgZLDQAgBiABayEIQQAhCQwECyABIAZrIQEgAygCCCIDRQ0fDAALCyAAKAJUIgNF
DR1BACEIQQAhCSAHQQFODQIMGAsgACgCgAEiAy8BBiEJIAMvAQQhCCADKAIAIQMLIAdBAU4NACAIQf//A3ENASAJIQgMFgsgCEH//wNxQQFqIgpB//8DcSIB
IApHDRogAy8BqgIiCyABSQ0aIAshASADIQQgCyAKQf//A3FHDQIgAygCDCIERQ0ZQQEhBgNAIAYgBC8BqgIiAU0NAiAGIAFrIQYgBCgCDCIEDQALIAFFDRoM
GQsgCEF/aiEBDBULIAZBf2oiCiAGSw0YC0EAIQwgAkEAOgB4IAIgCTsBdiACIAo7AXQgAiAENgJwIAJBADoAgAEgCEH//wNxIgggC08NFyAJQf//A3EgAy8B
qAJPDRcgAygCECIGIAMoAhhqIgMgBkkNFyADQQdxDRcgA0UNFyAGIAMgCEEDdGoiCygCAGoiAyAGSQ0XIANBB3ENFyADRQ0XIAJB8ABqIQggCy0ABEHgAHFF
DRQgCkH//wNxIAFPDRcDQCAJQf//A3EgBC8BqAJPDRggBCgCECIDIAQoAhhqIgYgA0kNGCAGQQdxDRggBkUNGCADIAYgCkH//wNxQQN0aiILKAIAaiIGIANJ
DRggBkEHcQ0YIAZFDRggCykDAEKAgICAgAyDQoCAgICACFINEgJAAkAgAUH//wNxIApBAWoiA0H//wNxRg0AIAMhCgwBCyAEKAIMIgZFDQ5BASEDAkADQCAD
IAYvAaoCIgFNDQEgAyABayEDIAYoAgwiBg0ACyACIAw6AHggAiAJOwF2IAIgCjsBdCACIAQ2AnAMEwsgA0F/aiIKIANLDRkgBiEEC0EAIQwgAkEAOgCEASAK
Qf//A3EgAUH//wNxTw0YDAALCyABKAIAIQYgAC0AkAEOBAMEBRYDCyAEQf//A3EgBiABa0H//wNxTw0TCwJAIARB//8DcQ0AIAAoAlQiA0UNFSAFIANHDQAg
AEEBOgCQAQwUCyAAKAKAASIDIAQ7AQQgAyAFNgIAIAMgAigCCDYBBiADQQpqIAIvAQw7AQAgAEECOgCQASAAQgA3AoQBDBMLIAEoAgAiA0UNCyAAKAJoIgEg
AC8BjgFrIgYgAUsNEwJAIAMgBkkNACAAQQA6AJABDBMLAkACQAJAAkACQAJAIAAtAJABQR50QR51IgZBf0oNAAJAIAZBA3FBfmoOAgAaAAsgAC0AiAENAQsg
AEECOgCQASAAQQE6AIgBIAAgAzYChAEgAyABQQF2Tw0DIANB//8DSw0BIAAoAlQiAUUNBAwCCyADQX9MDRcgACgChAEiAUF/TA0XIAJBAzoAHCACIAMgAWs2
AhAgACACQRBqEMSEgIAADBYLIABBADoAkAEMFQsCQANAIANB//8DcSABLwGqAiIGSQ0BIAMgBmshAyABKAIMIgFFDQMMAAsLIAAoAoABIgZBADoACCAGQQA7
AQYgBiADOwEEIAYgATYCAAwUCyABIANrIgMgAUsNFCADQf//A0sNBiAAKAJYIgFFDQADQAJAIANB//8DcSABLwGqAiIGSw0AIAAoAoABIgYgATYCACABLwGq
AiADQf//A3FrIgNB//8DcSADRw0WIAZBADoACCAGQQA7AQYgBiADOwEEDBULIAMgBmshAyABKAIIIgENAAsLIABBADoAkAEMEgsgBkF/Sg0RIAAoAlgiBUUN
EiAALwGOASEDA0AgA0H//wNxIAUvAaoCIgFNDQMgAyABayEDIAUoAggiBUUNEwwACwsgBkEBSA0QIAAoAlQiBUUNESAFLwGqAiIBQX9qIgRB//8DcSIDIARH
DREgBiADSw0CIAJBADoAZCACQQA7AWIgAiAFNgJcIAJBADoAbCACIAZB//8DIAZB//8DSRsiBDsBYCACQdwAaiEKDAgLAkACQCAGQR92QQIgBhsOAwEAEQEL
IAZBAEpBACAGayIDQQBIcw0RIANBf0wNESACQSBqIAAoAoABIgEgAxDjg4CAACACLQAwDQQgASACKQIgNwIAIAFBCGogAkEgakEIaigCADYCACAALQCIAUUN
ECAAKAKEASIBIANrIgMgAUsNESAAIAM2AoQBDBALIAZBf0wNECACQTRqIAAoAoABIgMgBhCvg4CAACACLQBEDQQCQCAAIAJBNGoQ7IOAgABBAXFFDQAgAEEA
OgCQAQwQCyADIAIpAjQ3AgAgA0EIaiACQTRqQQhqKAIANgIAIAAtAIgBRQ0PIAAoAoQBIgMgBmoiASADSQ0QIAAgATYChAEMDwsgBkEASkEAIAZrIgZBAEhz
DQ8gBkF/TA0PAkAgBiABIANrIgNB//8DcSIBSw0AIAJBADoAUCACQQA7AU4gAiAFNgJIIAJBADoAWCACIAMgBmsiBDsBTCACQcgAaiEKDAcLIAYgAWshAwNA
IAUoAggiAUUNBgJAIAMgASIFLwGqAiIBSw0AIAJBADoAUCACQQA7AU4gAiAFNgJIIAJBADoAWCACIAEgA2siBDsBTCACQcgAaiEKDAgLIAMgAWshAwwACwsg
BiADayEDAkACQANAIAUoAgwiBkUNASADIAYiBS8BqgIiAU0NAiADIAFrIQMMAAsLIAIgBTYCXCACQQE6AGwgAUF/aiIEQf//A3EgBEcNDyACIAM2AmggAkEA
OgBkIAJBADsBYiACIAQ7AWAgAkHcAGohCgwGCyACIAU2AlwgAkEAOgBsIANBf2oiBCADSw0OIAJBADoAZCACQQA7AWIgAiAEOwFgIAJB3ABqIQoMBQsgAEEA
OgCQAQwMCyAAQQE6AJABDAsLIABBADoAkAEMCgsgAiAMOgB4IAIgCTsBdiACIAo7AXQgAiAENgJwIAQvAaoCIQEMAwsgAiADNgJUQQAhBCACQQA6AFAgAkEA
NgJMIAIgBTYCSCACQQE6AFggAkHIAGohCgsgACgCWCIDRQ0IIAAvAY4BIQECQANAIAFB//8DcSADLwGqAiIGTQ0BIAEgBmshASADKAIIIgNFDQoMAAsLAkAC
QAJAIAUgA0YNAANAIAMoAgwiA0UNAiADIAVHDQAMAwsLIARB//8DcSAGIAFrQf//A3FPDQELAkAgBEH//wNxDQAgACgCVCIDRQ0KIAUgA0cNACAAQQE6AJAB
DAkLIAAoAoABIgMgCikCADcCACADQQhqIApBCGooAgA2AgAgAEECOgCQASAAQgA3AoQBDAgLIABBADoAkAEMBwsgAEEBOgCQAQwGCyABQf//A3FFDQYMAwsg
AiAMOgB4IAIgCTsBdiACIAo7AXQgAiAENgJwDAILIAMoAggiA0UNA0EBIQECQANAIAEgAy8BqgIiBk0NASABIAZrIQEgAygCCCIDRQ0FDAALCyAGIAFrIQEg
CCEJCyACIAk7AXYgAiABOwF0IAIgAzYCcCACQQA6AIQBIAJBAToAfCACQQA6AHggAkHwAGohCAsgAkGgAWpCADcDACACQYgBakEIaiAIQQhqKAIANgIAIAJC
ADcDmAEgAkEBOgCUASACIAgpAgA3A4gBIAIgB0EASiIDOgCsASACIAM6AKgBIAJBsAFqIAJBiAFqEMiDgIAAIAItALwBRQ0BIAJBtgFqIgNBBGohAQJAA0Ag
AiADKAEANgLAASACIAEvAQA7AcQBIAIvAbQBIQogAigCsAEhBCAFQX9qIgVFDQEgAkGwAWogAkGIAWoQyIOAgAAgAi0AvAENAAsgAkHAAWohAwsgAiADKAEA
NgLIASACIANBBGovAQA7AcwBIAAoAlgiA0UNAiAALwGOASEBAkADQCABQf//A3EgAy8BqgIiBk0NASABIAZrIQEgAygCCCIDRQ0EDAALCwJAAkACQCAEIANG
DQADQCADKAIMIgNFDQMgAyAERw0ADAILCyAKQf//A3EgBiABa0H//wNxSQ0BCyAAQQA6AJABDAILIAAoAoABIgMgCjsBBCADIAQ2AgAgAyACKALIATYBBiAD
QQpqIAIvAcwBOwEAIABBAjoAkAEgAEIANwKEAQwBCyAAQQA6AJABCyACQdABaiSAgICAAA8LAAvkAQEFfwJAAkAgAA0AQQAhAQwBCwJAIAAoAhgiAigCYA0A
IAIvAY4BDwsgAigCaCEBAkACQAJAAkACQCACLQCQAQ4EAQUAAwELIAItAIgBDQQgAigCWCIARQ0CQQAhAwwBCyABIAIvAY4BTw0DDAELA0AgAyAALwGqAiIE
aiIFIANJDQECQCAAIAIoAoABIgMoAgBGDQAgBSEDIAAoAggiAEUNAgwBCwsgBCADLwEEIgBNDQAgBSAAayIAIAVLDQAgASAAayIAIAFNDQELAAsgAkEBOgCI
ASACIAA2AoQBIAEPCyABC90BAQV/QQAhAQJAAkAgAEUNACAAKAIYIgIoAmBFDQAgAigCaCEDAkACQCACLQCQAQ4EAAIBAwALIAMgAi8BjgFrIgEgA00NAQwC
CwJAIAItAIgBRQ0AIAIoAoQBDwsgAigCWCIARQ0BQQAhAQNAIAEgAC8BqgIiBGoiBSABSQ0CAkAgACACKAKAASIBKAIARg0AIAUhASAAKAIIIgBFDQMMAQsL
IAQgAS8BBCIATQ0BIAUgAGsiACAFSw0BIAMgAGsiASADSw0BIAJBAToAiAEgAiABNgKEAQsgAQ8LAAvXAQEFfwJAIAANAEEADwsCQAJAIAAoAhgiASgCYEUN
ACABKAJoIQICQAJAIAEtAJABDgQBAgADAQsgAS0AiAENASABKAJYIgBFDQJBACEDA0AgAyAALwGqAiIEaiIFIANJDQMCQCAAIAEoAoABIgMoAgBGDQAgBSED
IAAoAggiAEUNBAwBCwsgBCADLwEEIgBNDQIgBSAAayIAIAVLDQIgAiAAayIAIAJLDQIgAUEBOgCIASABIAA2AoQBIAEvAY4BDwsgAiABLwGOAUkNAQsgAS8B
jgEPCwALIwEBf0EAIQECQCAARQ0AIAAoAtwuRQ0AIAAoAtguIQELIAELEgACQCAADQBBAA8LIAAoAtwuC6QCAQl/AkAgAA0AQQEPC0EAIQICQCABRQ0AIAAo
AtwuIgNFDQBBACECQQAhBAJAIAEgA08NAAJAAkACQCADIAEgAyABIANJGyIFayIEIANNIgFFDQAgAUUNASAAKALYLiEGIARBA3EhB0EAIQEgBSADa0F8Sw0C
IAYgBWohCCAEQXxxIQlBACEBA0AgBiABaiIDIAggAWoiCi0AADoAACADQQFqIApBAWotAAA6AAAgA0ECaiAKQQJqLQAAOgAAIANBA2ogCkEDai0AADoAACAJ
IAFBBGoiAUcNAAwDCwsACyAEIAMQiICAgAAACyAHRQ0AIAYgAWohAQNAIAEgASAFai0AADoAACABQQFqIQEgB0F/aiIHDQALCyAAIAQ2AtwuCyACC1EBAX8j
gICAgABBEGsiASSAgICAAAJAAkAgAA0AQQAhAAwBCyABIAAoAhgiACAALQDGAkEHcWotAL4CQR9xIgA6AA8LIAFBEGokgICAgAAgAAvZAQACQCAADQBBAQ8L
AkAgAUF/Rg0AIABBAToAtAEgAEIANwOoASAAQbIBaiABOgAAIAAgAUGA/gNxIAFBgID8B3FBEHZyOwGwAQsCQCACQX9GDQAgAEEBOgCkASAAQgA3A5gBIABB
ogFqIAI6AAAgACACQYD+A3EgAkGAgPwHcUEQdnI7AaABCwJAIANBf0YNACAAQQE6AMQBIABCADcDuAEgAEHCAWogAzoAACAAIANBgP4DcSADQYCA/AdxQRB2
cjsBwAELIAAgAC8B9BFBgIACcjsB9BFBAAunAQEEfwJAIAANAEEBDwsCQCACDQBBAA8LIAJBgAIgAkGAAkkbIQMgAEHIEWohBCAAQcgBaiECQQAhBQNAIAEv
AAAhBiACQQJqIAFBAmotAAA6AAAgAiAGOwEAIAQgBUEDdkH8////AXFqIgYgBigCAEEBIAV0cjYCACACQQRqIQIgAUEDaiEBIAMgBUEBaiIFRw0ACyAAIAAv
AfQRQYCAAnI7AfQRQQALvgEBA38jgICAgABBoBBrIgEkgICAgAACQAJAIAANAEEBIQAMAQsgAEHICWohAgJAQYAIRSIDDQAgASACQYAI/AoAAAsCQCADDQAg
AUGACGogAkGACPwKAAALIAFBmBBqQgA3AgAgAUGQEGpCADcCACABQYgQakIANwIAIAFCADcCgBACQEGgEEUNACAAQcgBaiABQaAQ/AoAAAsgACAALwH0EUGA
gAJyOwH0EUEAIQALIAFBoBBqJICAgIAAIAALqAECAn8BfiOAgICAAEEQayIBJICAgIAAQX8hAgJAIABFDQACQAJAIAAtAKwBRQ0AIAFBAToADCABIAAvAagB
OwEIIAEgAEGqAWotAAA6AAogAUEIaiEADAELIABBsAFqIQALIAApAgAiA0KAgICA8B+DQgBRDQAgA6ciAEEQdCAAQYD+A3FyIABBgID8B3FBEHZyQf///wdx
IQILIAFBEGokgICAgAAgAguoAQICfwF+I4CAgIAAQRBrIgEkgICAgABBfyECAkAgAEUNAAJAAkAgAC0AnAFFDQAgAUEBOgAMIAEgAC8BmAE7AQggASAAQZoB
ai0AADoACiABQQhqIQAMAQsgAEGgAWohAAsgACkCACIDQoCAgIDwH4NCAFENACADpyIAQRB0IABBgP4DcXIgAEGAgPwHcUEQdnJB////B3EhAgsgAUEQaiSA
gICAACACC6gBAgJ/AX4jgICAgABBEGsiASSAgICAAEF/IQICQCAARQ0AAkACQCAALQC8AUUNACABQQE6AAwgASAALwG4ATsBCCABIABBugFqLQAAOgAKIAFB
CGohAAwBCyAAQcABaiEACyAAKQIAIgNCgICAgPAfg0IAUQ0AIAOnIgBBEHQgAEGA/gNxciAAQYCA/AdxQRB2ckH///8HcSECCyABQRBqJICAgIAAIAILRwEB
f0F/IQICQCAARQ0AIAFBgAJPDQAgACABQQJ0aiIALwHIASICQRB0IAJBgP4DcXIgAEHKAWotAAByQf///wdxIQILIAIL0gEBBH8jgICAgABBgAhrIgIkgICA
gAACQAJAIAANAEEBIQAMAQsCQEGACEUNACACIABByAFqQYAI/AoAAAsgAiEAQYB6IQMDQCABIANqIgRBggZqIABBAmotAAA6AAAgBEGBBmogAC8BACIFQQh2
OgAAIARBgAZqIAU6AAAgBEGFBmogAEEGai0AADoAACAEQYMGaiAAQQRqLwEAIgU6AAAgBEGEBmogBUEIdjoAACAAQQhqIQAgA0EGaiIDDQALQQAhAAsgAkGA
CGokgICAgAAgAAvvAQEEfwJAIAANAEEBDwtBAyEDAkAgAUUNACACRQ0AAkAgACAAQbAbaiABIAIQxoOAgABB//8DcUUNAEEEDwtBACEDIAAvAfoRIgFFDQAg
AC8B+BEiAkH//wNxRQ0AAkAgAC8BgBIiBCABTw0AIAAvAYISQf//A3EiBSABTw0AIAQgBU8NACAALwH8ESIFQf//A3EgAkH//wNxIgRPDQAgAC8B/hEiBkH/
/wNxIARPDQAgBUH//wNxIAZB//8DcUkNAQtBACEDIABBADsBgBIgAEEAOwH8ESAAIAFBf2o7AYISIAAgAkF/ajsB/hELIAMLMwEBfwJAIAANAEEBDwtBAyED
AkAgAUUNACACRQ0AIAAgAjYCfCAAIAE2AnhBACEDCyADC7cmBAl/AX4KfwF+I4CAgIAAQYDcAGsiAySAgICAAAJAAkACQCAADQBBASEEDAELQQMhBCACRQ0A
IAMgAjYCECADIAE2AgxBAA0AIAEtAABBAUcNAEEADQAgAkEDSQ0AQQANACACQQVJDQAgAS8AASEFIANBBTYCFCAFQf//A3FFDQAgAS8AAyIGQf//A3FFDQBB
AA0AIAJBBUYNACABLQAFIgdBfWpB/wFxQf4BSQ0AQQANACACQQdJDQACQAJAAkACQEEAKAKc+cmAACIIQTcgAS0ABiIJQQFGG0EAKAKY+cmAACIKIAkbIgtB
//8DcUUNACALIQkMAQsgAyAGOwGGIyADIAU7AYQjIANBADYCsBIgAyAAQRhqKAIAQZgBaikCACIMNwMYAkBB0BBFDQAgA0G0EmpB2OfJgABB0BD8CgAACyAD
QgA8AKwSIANChMSAkAM+AqgSIANBiCNqIANBGGogA0GoEmoQgYCAgAAgAy8BkDUiCQ0AAkBBiBJFDQAgA0EgaiADQYgjakGIEvwKAAALIANBADoAoFkCQAJA
AkACQAJAAkACQEEADQAgAkEISQ0AIAhBNyABLQAHIglBAUYbIAogCRsiCUH//wNxDQYgA0EAOgCgWUEADQkgAkEMSQ0JIAMgASgACCINQQxqIg4gDUkiDToA
oFkgDQ0IIA4gAksNCAJAIA1FDQBBDCAOEIqAgIAAAAsgDEIgiKchDyAMpyEQIA5BdGohDSABQQxqIRECQAJAAkACQAJAAkACQCAJQYCABHEiEg0AQQAhEyAD
LQBEIRQMAQsgAyAGOwGmNSADIAU7AaQ1IANCgICAgICAtIkTNwKcNQJAAkAgAy0ARCIUQQJxRQ0AIAMoAkAiFQ0BCwJAIBBB0AJBA0EAIA8oAgARgICAgACA
gICAACIVDQBBGSEJDA4LAkBB0AJFDQAgFUGqAUHQAvwLAAsgFUEHcQ0KIANBoNkAaiADQRhqIANBnDVqEIKAgIAAIAMvAfBbIgkNAgJAQdACRQ0AIBUgA0Gg
2QBqQdAC/AoAAAsgAyAVNgJAIAMgAy0AREECciIUOgBECyAVIBEgDRDXhICAACIJQf//A3ENDAJAAkAgB0EBRg0AIAMgDkEBaiINRSIJOgCgWSANRQ0IIA0g
AksNCCAJDQcgCEE3IAEgDmotAAAiCUEBRhsgCiAJGyIJQf//A3ENDiADIA1BBGoiFiANSSIOOgCgWSAODREgFiACSw0RIA4NCSADIBYgASANaigAAGoiDiAW
SSINOgCgWSANDRAgDiACSw0QIA0NEyAJQYCABHFFDQEMDAsCQCADKAJUIgJFDQAgAygCUCEAAkAgAkUNACAAQaoBIAL8CwALIBAgACACQQBBACAPKAIMEYGA
gIAAgICAgAALAkBBzABFDQAgA0HMAGpBqgFBzAD8CwALIANBOGogDBCDgICAACADQaABaiECAkAgAygCqAEiAEUNACADKAKgASEBAkAgAEUNACABQaoBIAD8
CwALIBAgASAAQQBBACAPKAIMEYGAgIAAgICAgAALIAJCqtWq1arVqtWqfzcDACACQQhqQarVqtV6NgIAIAMoArQBIgJFDREgAygCrAEhAAJAIAJFDQAgAEGq
ASAC/AsACyAQIAAgAkEAQQAgDygCDBGBgICAAICAgIAADBELIAEgFmohESAOIBZrIQ1BASETCyAUQQFxRQ0IIAMoAjwiCUUNCCAJIBEgDRDXhICAACIJQf//
A3ENCyALQYCABHEhCyATQQFqIAdGDQMgAyAOQQFqIg1FIgk6AKBZIA1FDQUgDSACSw0FIAkNBCAIQTcgASAOai0AACIJQQFGGyAKIAkbIglB//8DcQ0LIAMg
DUEEaiIWIA1JIg46AKBZIA4NDiAWIAJLDQ4gDg0GIAMgFiABIA1qKAAAaiIOIBZJIg06AKBZIA0NDSAOIAJLDQ0gDQ0QIAlBgIAEcUUNCiASDQkgAyAGOwGm
NSADIAU7AaQ1IANCgICAgICAtIkTNwKcNQJAIBRBAnFFDQAgAygCQCIVDQMLAkAgEEHQAkEDQQAgDygCABGAgICAAICAgIAAIhUNAEEZIQkMDAsCQEHQAkUN
ACAVQaoBQdAC/AsACyAVQQdxDQggA0Gg2QBqIANBGGogA0GcNWoQgoCAgAAgAy8B8FsiCUUNAQsgECAVQdACQQNBACAPQQxqKAIAEYGAgIAAgICAgAAMCgsC
QEHQAkUNACAVIANBoNkAakHQAvwKAAALIAMgFTYCQCADIAMtAERBAnI6AEQLIBUgASAWaiAOIBZrENeEgIAAIglB//8DcQ0IAkAgE0ECciAHRw0AIAMgDjYC
FAwFCyADIA5BAWoiDUUiADoAoFkgDUUNAiANIAJLDQIgAA0BIAhBNyABIA5qLQAAIgBBAUYbIAogABsiCUH//wNxDQggAyANQQRqIhYgDUkiADoAoFkgAA0L
IBYgAksNCyAADQMgAyAWIAEgDWooAABqIg4gFkkiADoAoFkgAA0KIA4gAksNCiAADQ0gCUGAgARxRQ0HDAYLIAMgDjYCFAJAAkAgEg0AIAsNAQsgEg0EIANB
OGogDBDjgICAAAwECyADQSBqIANBGGoQqoCAgAAMCwtBfyANEIqAgIAAAAsgA0EgaiADQRhqEKqAgIAADAkLIA0gFhCKgICAAAwBCyADQag1aiADQQxqENiE
gIAAIAMvAao1IgkNAyADIAMvAag1OwGcEiADQaw1aiADQQxqENiEgIAAIAMvAa41IgkNAyADIAMvAaw1OwGeEiADQbA1aiADQQxqENiEgIAAIAMvAbI1IgkN
AyADIAMvAbA1OwGgEiADQbQ1aiADQQxqENiEgIAAIAMvAbY1IgkNAyADIAMvAbQ1IgI7AaISAkACQAJAAkAgAy8BnhIiASADLwGYEk8NACACQf//A3EgAy8B
mhJB//8DcU8NACADLwGcEkH//wNxIAFLDQAgAy8BoBJB//8DcSACQf//A3FLDQAgA0G4NWogA0EMahDZhICAACADLwHQNSIJDQcgA0EgakEQaiADQbg1akEQ
aikDADcDACADQSBqQQhqIANBuDVqQQhqKQMANwMAIAMgAykDuDU3AyAgA0HcNWogA0EMahDahICAACADLwGsRiIJDQcCQEHQEEUNACADQbgBaiADQdw1akHQ
EPwKAAALIANBDGogDCADQcwAahDbhICAACIJQf//A3ENByADQbDGAGogA0EMahDchICAACADLwG4RiIJDQcgAyADKQOwRjcDiBIgA0HAxgBqIANBDGoQ3YSA
gAAgAy8BwEYiCQ0HIAMgAy0AwkZBAXE6AKQSIANBxMYAaiADQQxqEN6EgIAAIAMvAchGIgkNByADIAMoAsRGNgKQEiADQczGAGogA0EMahDfhICAACADLwHM
RiIJDQcgAyADLwGUEkH8/wNxIAMtAM5GcjsBlBIgA0HQxgBqIANBDGoQ4ISAgAAgAy8B0EYiCQ0HIAMgAy8BlBJB+/8DcUEEQQAgAy0A0kYbcjsBlBIgA0HU
xgBqIANBDGoQ4YSAgAAgAy8B1EYiCQ0HIAMgAy0A1kZBA3QgAy8BlBJBx/8DcXI7AZQSIANB2MYAaiADQQxqEOGEgIAAIAMvAdhGIgkNByADIAMtANpGQQZ0
IAMvAZQSQb/8A3FyOwGUEiADQdzGAGogA0EMahDfhICAACADLwHcRiIJDQcgAyADLQDeRkEJdCADLwGUEkH/8wNxcjsBlBIgA0HgxgBqIANBDGoQ4oSAgAAg
Ay8B6EYiCQ0HIANBIGogAygC4EYgAygC5EYQjoOAgAAiCUH//wNxDQcgA0HsxgBqIANBDGoQ4oSAgAAgAy8B9EYiCQ0HIANBIGogAygC7EYgAygC8EYQi4OA
gAAiCUH//wNxDQcgAygCECICIAMoAhQiAUkNBCACIAFHDQEgAyALQQBHOgBIIAMtAERBA3EgC0EQdiICdkEBcUUNBCADQTxqIAJBAnRqKAIAIgJFDQQgAyAC
NgI4AkBBiBJFIgINACADQfjGAGogAEGIEvwKAAALIAMgAygCkEdBmAFqKQIANwOAWQJAIAINACAAIANBIGpBiBL8CgAACyAAIAAvAfQRIABB9hFqIgItAABB
EHRyQYCACnIiBDsB9BEgAiAEQRB2OgAAIANB+MYAaiADQYDZAGoQqoCAgAAgAC0AtBwNAgwDCyADQSBqIANBGGoQqoCAgAAMCgsgA0EgaiADQRhqEKqAgIAA
DAkLIAAtALAcRQ0AIABBmBxqIQICQCAAKAKoHCIERQ0AIABBnBxqKAIAIQUgACgCmBwhBiAAKAKkHCEBAkAgBEUNACABQaoBIAT8CwALIAYgASAEQQBBACAF
KAIMEYGAgIAAgICAgAALIAJCqtWq1arVqtWqfzcDACACQRBqQqrVqtWq1arVqn83AwAgAkEIakKq1arVqtWq1ap/NwMACyAALQDgHCICQRhLDQACQCACQQhH
DQAgAC0AkBxFDQAgACkDiBwhDAJAAkAgAEHAHGooAgAiAg0AQQAhAkGq1arVeiEEDAELIAKtQgx+IhdCIIinDQIgACgCuBwhBCAXpyECCyACRQ0AIAxCIIin
IQEgDKchBQJAIAJFDQAgBEGqASAC/AsACyAFIAQgAkECQQAgASgCDBGBgICAAICAgIAACyAAQYAcaiICLQAAQQNGDQAgA0GTI2pCqtWq1arVqtWqfzcAACAD
QZsjakKq1arVqtWq1ap/NwAAIANBoyNqQqrVqtWq1arVqn83AAAgA0GrI2pCqtWq1arVqtWqfzcAACADQbMjakKq1arVqtWq1ap/NwAAIANCqtWq1arVqtWq
fzcAiyMgACkDsBshDEEAIQQgAEGGHGpBADoAACAAQQA7AYQcIABCADcDyBsgACAMNwO4GyAAQdAbakIANwMAIABB2BtqQgA3AwAgAEHgG2pCADcDACAAQegb
akIANwMAIABB8BtqQgA3AwAgAEH4G2pBADYCACAAQQE6AJAcIAAgDDcDiBwgAkEAOgAAIAAgAEHYLmo2AvwbIAAgADYCxBsgACAANgLAGwJAQdMARQ0AIABB
kRxqQQBB0wD8CwALIABBADoA5BwCQEGAEEUNACAAQeUcakGqAUGAEPwLAAsgAEEAKQOw3sGAADcCpC0gAEEAOgChLSAAQQA7AJ8tIABBqtWq1Xo2AJstIABB
ADoAmi0gAEEAOwGYLSAAQZQtaiADQbcjaigAADYAACAAQY0taiADQbAjaikAADcAACAAQYUtaiADQagjaikAADcAACAAQf0saiADQaAjaikAADcAACAAQfUs
aiADQZgjaikAADcAACAAQe0saiADQZAjaikAADcAACAAIAMpAIgjNwDlLCAAQYgSaiICIABBsBtqEKiAgIAAAkBBqAlFDQAgAkGw3smAAEGoCfwKAAALIAAv
AfoRIgJFDQcgAC8B+BEiAUH//wNxRQ0HAkAgAC8BgBIiBSACTw0AIAAvAYISQf//A3EiBiACTw0AIAUgBk8NACAALwH8ESIGQf//A3EgAUH//wNxIgVPDQAg
AC8B/hEiB0H//wNxIAVPDQAgBkH//wNxIAdB//8DcUkNCAtBACEEIABBADsBgBIgAEEAOwH8ESAAIAJBf2o7AYISIAAgAUF/ajsB/hEMBwsACwJAIAMoAlQi
AkUNACADKAJQIQACQCACRQ0AIABBqgEgAvwLAAsgECAAIAJBAEEAIA8oAgwRgYCAgACAgICAAAsCQEHMAEUNACADQcwAakGqAUHMAPwLAAsgA0E4aiAMEIOA
gIAAIANBoAFqIQICQCADKAKoASIARQ0AIAMoAqABIQECQCAARQ0AIAFBqgEgAPwLAAsgECABIABBAEEAIA8oAgwRgYCAgACAgICAAAsgAkKq1arVqtWq1ap/
NwMAIAJBCGpBqtWq1Xo2AgAgAygCtAEiAkUNBSADKAKsASEAAkAgAkUNACAAQaoBIAL8CwALIBAgACACQQBBACAPKAIMEYGAgIAAgICAgAAMBQsCQCADKAJU
IgJFDQAgAygCUCEAAkAgAkUNACAAQaoBIAL8CwALIBAgACACQQBBACAPKAIMEYGAgIAAgICAgAALAkBBzABFDQAgA0HMAGpBqgFBzAD8CwALIANBOGogDBCD
gICAACADQaABaiECAkAgAygCqAEiAEUNACADKAKgASEBAkAgAEUNACABQaoBIAD8CwALIBAgASAAQQBBACAPKAIMEYGAgIAAgICAgAALIAJCqtWq1arVqtWq
fzcDACACQQhqQarVqtV6NgIAIAMoArQBIgJFDQQgAygCrAEhAAJAIAJFDQAgAEGqASAC/AsACyAQIAAgAkEAQQAgDygCDBGBgICAAICAgIAADAQLIANBIGog
A0EYahCqgICAAAsgCUH//wNxQRlHDQJBAiEEDAILIANBIGogA0EYahCqgICAAAwBCyADQSBqIANBGGoQqoCAgAALIANBgNwAaiSAgICAACAEDwsgFiAOEIqA
gIAAAAuNeggCfwF+E38Bfg1/AXwWfwR+I4CAgIAAQfABayIDJICAgIAAIAMgAjYCCCADIAE2AgRBNyEEAkACQEEADQAgAkUNACABLQAAQQFHDQBBAA0AIAJB
CUkNAEEADQAgAkENSQ0AIAEpAAEhBSADQQ02AgwgASgACSIGRQ0AIANBGGpBACgCsPjJgAA2AgAgA0EAKQKo+MmAADcDEEEAKAKc+cmAACEHQQAoApj5yYAA
IQhBqtWq1XohCUEAIQoCQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAA0AgAyADKAIMIgRBBGoiASAE
SSILOgCIAQJAAkAgCw0AIAEgAygCCCICSw0AIAtFDQEgBCABEIqAgIAAAAsgAyAJNgIQIANBEGogACkDmAEQ44SAgABBNyEEDB8LIAMoAgQiCyAEaigAACEM
IAMgATYCDCADIAFBAmoiBCABSSINOgCIAQJAAkAgDQ0AIAQgAksNACANRQ0BIAEgBBCKgICAAAALIAMgCTYCECADQRBqIAApA5gBEOOEgIAAQTchBAwfCyAL
IAFqLwAAIQ4gAyAEQQJqIgEgBEkiDToAiAECQAJAIA0NACABIAJLDQAgDUUNASAEIAEQioCAgAAACyADIAk2AhAgA0EQaiAAKQOYARDjhICAAEE3IQQMHwsg
CyAEai8AACEPIAMgAUECaiIEIAFJIg06AIgBAkACQCANDQAgBCACSw0AIA1FDQEgASAEEIqAgIAAAAsgAyAJNgIQIANBEGogACkDmAEQ44SAgABBNyEEDB8L
IAsgAWovAAAhECADIARBAmoiDSAESSIBOgCIAQJAAkAgAQ0AIA0gAksNACABRQ0BIAQgDRCKgICAAAALIAMgCTYCECADQRBqIAApA5gBEOOEgIAAQTchBAwf
CyALIARqLwAAIREgAyANQQJqIgEgDUkiBDoAiAECQAJAIAQNACABIAJLDQAgBEUNASANIAEQioCAgAAACyADIAk2AhAgA0EQaiAAKQOYARDjhICAAEE3IQQM
HwsgCyANai8AACESIAMgATYCDCADIAFBAmoiBCABSSINOgCIAQJAAkAgDQ0AIAQgAksNACANRQ0BIAEgBBCKgICAAAALIAMgCTYCECADQRBqIAApA5gBEOOE
gIAAQTchBAwfCyALIAFqLwAAIRMgAyAEQQRqIgEgBEkiDToAiAECQAJAIA0NACABIAJLDQAgDUUNASAEIAEQioCAgAAACyADIAk2AhAgA0EQaiAAKQOYARDj
hICAAEE3IQQMHwsgCyAEaigAACEUIAMgAUEEaiIEIAFJIg06AIgBAkACQCANDQAgBCACSw0AIA1FDQEgASAEEIqAgIAAAAsgAyAJNgIQIANBEGogACkDmAEQ
44SAgABBNyEEDB8LIAsgAWooAAAhFSADIARBAWoiDUUiAToAiAECQAJAAkACQAJAAkACQAJAAkACQAJAAkAgDUUNACANIAJNDQFBNyEEDAILIAMgCTYCEEE3
IQQMAgsgAQ0DIAdBNyALIARqLQAAIgFBAUYbIAggARsiBEH//wNxRQ0CCyADIAk2AhALIANBEGogACkDmAEQ44SAgAAMJgsgAyANIAxqIgwgDUkiAToAiAEC
QAJAIAENACAMIAJLDQAgAUUNASANIAwQioCAgAAACyADIAk2AhAgA0EQaiAAKQOYARDjhICAAEE3IQQMJgsgAyAEQYCABHFBAEc6ACAgAyAMNgIMIAMoAhQi
FkEBaiIERQ0IAkAgAygCGCIXIARJDQAgCSEYIBchAgwECyAAKQOYASEZIBchAgNAQX8gAkEBdiACakECaiIBIAEgAkkbIgIgBEkNAAsgGUIgiKchASAZpyEa
IBdFDQEgF0GAgIDAAE8NCCADIAJB////P0s6AIgBIAJBgICAwABPDQFBACEYAkAgGiAJIBdBBXRBAiACQQV0IhtBACABKAIIEYKAgIAAgICAgAAiHA0AQQAh
GwwDCwJAIBtFDQAgHEEDcQ0JCwJAIBsNAEEAIRtBqtWq1XohGAwDCyACQf///z9xIRsgHCEYDAILIAQgDRCKgICAAAwHC0EAIRhBACEbCwJAAkAgGEUNACAb
IQIMAQsgAyACQf///z9LOgCIASACQYCAgMAATw0CAkACQCACQQV0IhsNAEF8IRgMAQsgGiAbQQJBACABKAIAEYCAgIAAgICAgAAiGEUNAwJAIBtFDQAgGEGq
ASAb/AsACyAYQQNxDQcLIBYgAksNAwJAIBggCSAWQQV0IhtqTw0AIAkgGCAbakkNBwsCQCAbRQ0AIBggCSAb/AoAAAsgF0UNAAJAIBdBBXQiF0UNACAJQaoB
IBf8CwALIBogCSAXQQJBACABKAIMEYGAgIAAgICAgAALIAMgAjYCGAsgFiACSQ0CDAQLIAMgCTYCECADQRBqIAApA5gBEOOEgIAAQRkhBAwgCyAWIAIQiICA
gAAACyADIAQ2AhQgGCAWQQV0aiICIA87ARogAiAOOwEYIAIgDCANazYCFCACIAsgDWo2AhAgAiATOwEOIAIgEjsBDCACIBE7AQogAiAQOwEIIAIgFTYCBCAC
IBQ2AgAgAiADLQAgOgAcIBghCSAKQQFqIgogBkcNAAsgAyAYNgIQQTchBCADKAIUIh1FDRwgAEEsaiEeIABBGGohH0EAISBBACEhQQAhB0EAISICQAJAAkAD
QCAYICFBBXRqIgIvARohIyACLwEYIRwgAigCFCEkIAIoAhAhJSACLwEOIQwgAi8BDCENIAIvAQohDyACLwEIIRcgAigCBCEJIAIoAgAhCyADIAItABw6AOgB
AkACQCAcRQ0AICMNAQsgB0UNIQNAIAAoAlwiAiAHKAIUayILIAJLDSQgBygCDCEBIAAgCzYCXAJAAkAgBygCFCICQYCAJEsNAAJAIAJFDQAgBygCEEEAIAL8
CwALIAcoAhAhAgJAQYCAJEUNACACQaoBQYCAJPwLAAsgAiAAKAI8NgIAIAAgAjYCPAwBCyAAKAIsIQ0gACgCMCEJIAcoAhAhCwJAIAJFDQAgC0GqASAC/AsA
CyANIAsgAkEQQQAgCSgCDBGBgICAAICAgIAACwJAQbACRQ0AIAdBqgFBsAL8CwALIAcgACgCKDYCACAAIAc2AiggASEHIAENAAwiCwsCQAJAIBwgF0sNACAj
IA9NDQELIAdFDSEDQCAAKAJcIgIgBygCFGsiASACSw0kIAcoAgwhAiAAIAE2AlwCQAJAIAcoAhQiAUGAgCRLDQACQCABRQ0AIAcoAhBBACAB/AsACyAHKAIQ
IQECQEGAgCRFDQAgAUGqAUGAgCT8CwALIAEgACgCPDYCACAAIAE2AjwMAQsgACgCLCENIAAoAjAhCSAHKAIQIQsCQCABRQ0AIAtBqgEgAfwLAAsgDSALIAFB
EEEAIAkoAgwRgYCAgACAgICAAAsCQEGwAkUNACAHQaoBQbAC/AsACyAHIAAoAig2AgAgACAHNgIoIAIhByACDQAMIgsLIA8gF2wiCkH/////AUsNIiAPQQN0
IiYgCkEDdGoiBiAmSQ0iAkACQCANDQBBACECDAELQgFBICANQX9qZ2utQv//A4OGpyICuEQAAAAAAADqP6IiJ0QAAAAAAADwv2UNIyAnRAAAAAAAAPBBZg0j
ICf8A61CJH4iGUIgiKcNIyACQQF0QQNqQfz///8HcSIBIBmnaiICIAFJDSMLIAYgAmoiASAGSQ0iIAtBD2oiAiALSQ0iIAJBgICAgAFPDSIgAkEEdkE/akEG
diIoQQN0IgggAkEEdEGAfnFqIhYgCEkNIiABQQdqIgIgAUkNIiACQXhxIgEgFmoiAiABSQ0iAkACQCALDQBBACEBDAELQgFBICALQX9qQQR2Z2utQv//A4OG
pyEBCyACIAFBE2pBfHEgAUEMbGpqIgEgAkkNIiAJQR9qIgIgCUkNIiACQYCAgMAATw0iIAJBBXZBP2pBBnYiKUEDdCIbIAJBBXRBgHhxaiIOIBtJDSIgAUEH
aiICIAFJDSIgAkF4cSIBIA5qIgIgAUkNIiAMQRxuIRUCQAJAIAxBHEkiGkUNAEEAIQEMAQtCAUEgIBVBf2pna61C//8Dg4anIgG4RAAAAAAAAOo/oiInRAAA
AAAAAPC/ZQ0jICdEAAAAAAAA8EFmDSMgJ/wDrUIcfiIZQiCIpw0jIAFBAXRBA2pB/P///wdxIgEgGadqIhAgAUkNIyACIBBqIhAgAkkNI0IBQSAgFUEEdEF/
amdrrYanIQEgECECCyACQQNqIhAgAkkNIiAQQXxxIhAgAUEGbCABQRNqQXxxakEDakF8cWoiAiAQSQ0iIAJB//8DaiIqIAJJDSICQCAkIAJPDQAgB0UNIQNA
IAAoAlwiAiAHKAIUayIBIAJLDSQgBygCDCECIAAgATYCXAJAAkAgBygCFCIBQYCAJEsNAAJAIAFFDQAgBygCEEEAIAH8CwALIAcoAhAhAQJAQYCAJEUNACAB
QaoBQYCAJPwLAAsgASAAKAI8NgIAIAAgATYCPAwBCyAAKAIsIQ0gACgCMCEJIAcoAhAhCwJAIAFFDQAgC0GqASAB/AsACyANIAsgAUEQQQAgCSgCDBGBgICA
AICAgIAACwJAQbACRQ0AIAdBqgFBsAL8CwALIAcgACgCKDYCACAAIAc2AiggAiEHIAJFDSIMAAsLAkACQCAAKAIoIgFFDQAgACABKAIANgIoDAELQQANAyAf
QbACQQMgAxCPgICAACIBRQ0DAkBBsAJFDQAgAUGqAUGwAvwLAAsgAUEHcQ0jCwJAQbACRQ0AIAFBqgFBsAL8CwALAkACQCANDQBBACErQQAhLEEAIS1BACEu
QQAhLwwBC0IBQSAgDUF/amciAmutQv//A4OGpyIuuEQAAAAAAADqP6IiJ0QAAAAAAADwv2UNIyAnRAAAAAAAAPBBZg0jICf8AyIvrUIkfiIZQiCIpw0jIC5B
AXRBA2pB/P///wdxIisgGadqIiwgK0kNI0F/QQAgAmt0QX9zIS0LIAYgLGoiAiAGSQ0iIAJBB2oiECACSQ0iIBBBeHEiFCAWaiIwIBRJDSICQAJAIAsNAEEA
IRIMAQtCAUEgIAtBf2pBBHZna61C//8Dg4anIRILIDAgEkETakF8cSIxIBJBAnRqIjIgEkEDdGpqIgIgMEkNIiACQQdqIhYgAkkNIiAWQXhxIhMgDmoiESAT
SQ0iQQAhMyARIQJBACE0QQAhNUEAITZBACE3QQAhEAJAIBoNAEIBQSAgFUF/amciFmutQv//A4OGpyI2uEQAAAAAAADqP6IiJ0QAAAAAAADwv2UNIyAnRAAA
AAAAAPBBZg0jICf8AyI3rUIcfiIZQiCIpw0jIDZBAXRBA2pB/P///wdxIjUgGadqIjQgNUkNIyARIDRqIgIgEUkNI0F/QQAgFmt0QX9zITNCAUEgIBVBBHRB
f2pna62GpyEQCyACQQNqIhYgAkkNIiAWQXxxIjggEEEBdCAQQRNqQXxxIg4gEEECdGoiGmpBA2pBfHFqIgIgOEkNIiACQf//A2oiFiACSQ0iAkACQCAWQYCA
fHEiFUGBgCRJDQBBAA0DIB4pAgAiGacgFUEQQQAgGUIgiKcoAgARgICAgACAgICAACIWRQ0DAkAgFUUNACAWQaoBIBX8CwALIBUhOSAWQf//A3ENJAwBCwJA
AkAgACgCPCIWRQ0AIAAgFigCADYCPAwBC0EADQMgHkGAgCRBECADEI+AgIAAIhZFDQMCQEGAgCRFDQAgFkGqAUGAgCT8CwALIBZB//8DcQ0kC0GAgCQhOUGA
gCRFDQAgFkGqAUGAgCT8CwALAkAgOUUNACAWQQAgOfwLAAsgFiAmaiICIBZJDSIgAkEHcQ0iIAJFDSIgKkGAgHxxITogMkFwaiEyIDFBcGohMSAaQXBqITsg
DkFwaiE8IAFBEGohGiAMQRB0IA1yIT0gCa1CIIYgC62EIT4gF0EDdCEOQQAhCyAWIQ0gDyEJAkACQANAIAsgCk8NASACIBZrIgwgAksNJSANIAytNwMAIA1B
CGohDSACIA5qIQIgCyAXaiELIAlBf2oiCQ0ACwJAIBVFDQAgFkH4/wNxDSULIAYgK2oiCSAGSQ0kIBYgBmoiAiAWSQ0kIAJFDSQCQCAuQQF0IgtFDQAgAkEA
IAv8CwALIBYgCWoiDSAWSQ0kIA1FDSQCQCAvRQ0AIA0hAgJAIC9BJGwiCkFcaiIMQSRuIgtBA3FBA0YNAEEAIAtBAWpBA3FrIQsgDSECA0AgAkEgakEAKAK8
uciAADYCACACQRhqQQApArS5yIAANwIAIAJBEGpBACkCrLnIgAA3AgAgAkEIakEAKQKkuciAADcCACACQQApApy5yIAANwIAIAJBJGohAiALQQFqIgsNAAsL
IAxB7ABJDQAgDSAKaiENA0AgAkEgakEAKAK8uciAACILNgIAIAJBGGpBACkCtLnIgAAiGTcCACACQRBqQQApAqy5yIAAIj83AgAgAkEIakEAKQKkuciAACJA
NwIAIAJBACkCnLnIgAAiQTcCACACQSRqIEE3AgAgAkEsaiBANwIAIAJBNGogPzcCACACQTxqIBk3AgAgAkHEAGogCzYCACACQcgAaiBBNwIAIAJB0ABqIEA3
AgAgAkHYAGogPzcCACACQeAAaiAZNwIAIAJB6ABqIAs2AgAgAkGMAWogCzYCACACQYQBaiAZNwIAIAJB/ABqID83AgAgAkH0AGogQDcCACACQewAaiBBNwIA
IAJBkAFqIgIgDUcNAAsLAkBBwgBFIioNACADQYgBakEAQcIA/AsACyAWIBNqIgIgFkkNJCACRQ0kAkAgG0UNACACQf8BIBv8CwALIBMgG2oiCiATSQ0kIBYg
FGoiAiAWSQ0kIAJFDSQCQCAIRQ0AIAJB/wEgCPwLAAsgFCAIaiIOIBRJDSQgFiAwaiICQRBqIgtBA3ENJCACQQA2AgwgAiASNgIIIAIgMTYCBCACIDI2AgAC
QCASRQ0AIAtBACAS/AsACyALIBZrIhIgC0sNJCAWIDhqIgJBADYCDCACIBA2AgggAiA8NgIEIAIgOzYCACACQRBqIQICQCAQRQ0AIAJBACAQ/AsACyACIBZr
IhAgAksNJCARIDVqIgwgEUkNJCAWIBFqIgIgFkkNJCACRQ0kAkAgNkEBdCILRQ0AIAJBACAL/AsACyAWIAxqIg0gFkkNJCANRQ0kAkAgN0UNACANIQICQCA3
QRxsIhtBZGoiCEEcbiILQQNxQQNGDQBBACALQQFqQQNxayELIA0hAgNAIAJBGGpBACgC2LnIgAA2AgAgAkEQakEAKQLQuciAADcCACACQQhqQQApAsi5yIAA
NwIAIAJBACkCwLnIgAA3AgAgAkEcaiECIAtBAWoiCw0ACwsgCEHUAEkNACANIBtqIQ0DQCACQRhqQQAoAti5yIAAIgs2AgAgAkEQakEAKQLQuciAACIZNwIA
IAJBCGpBACkCyLnIgAAiPzcCACACQQApAsC5yIAAIkA3AgAgAkEcaiBANwIAIAJBJGogPzcCACACQSxqIBk3AgAgAkE0aiALNgIAIAJBOGogQDcCACACQcAA
aiA/NwIAIAJByABqIBk3AgAgAkHQAGogCzYCACACQdQAaiBANwIAIAJB3ABqID83AgAgAkHkAGogGTcCACACQewAaiALNgIAIAJB8ABqIgIgDUcNAAsLIAEg
LTsBXCABICw2AlggASArNgJUIAFBADYCUCABIC42AkwgASAvNgJIIAFBADYCRCABIAk2AkAgASAGNgI8IAEgEjYCOCABIA42AjQgASAoNgIwIAEgFDYCLCAB
IAo2AiggASApNgIkIAEgEzYCICABICY2AhwgAUEANgIYIAEgFTYCFCABIBY2AhAgA0EAOgDsASABQeAAaiEGAkAgKg0AIAYgA0GIAWpBwgD8CgAACyABIDM7
AcgBIAEgNDYCxAEgASA1NgLAASABQQA2ArwBIAEgNjYCuAEgASA3NgK0ASABQQA2ArABIAEgDDYCrAEgASARNgKoASABIBA2AqQBIAFBATsBogECQEHKAEUN
ACABQcwBakEAQcoA/AsACyABIA87AaoCIAEgFzsBqAIgASA9NgKkAiABIA87AaICIAEgFzsBoAIgASA+NwKYAiABQQE7AZYCIAEgAy0A7AE6AKwCIAEgACkD
ADcDACABQQA7AaoCIAFCADcCCCAAKQMAQgF8IhlQDSQgACAZNwMAIAAoAlwiAiA5aiILIAJJDSQgACALNgJcICQgOiAkIDpJGyICIAEoAhQiC00NASACIAsQ
iICAgAAACyALIAoQiICAgAAACwJAIAEoAhAiCyAlIAJqTw0AICUgCyACakkNIwsCQCACRQ0AIAsgJSAC/AoAAAsgASAjOwGqAiABIBw7AagCIAFBADYCRCAB
IAMtAOgBQQFxOgCsAiABKAIQIRYCQCAqDQAgBkEAQcIA/AsACyABQQE7AaIBAkAgASgCSCIPRQ0AIBYgASgCPGoiECAWSQ0jIBBBAXENIyAQRQ0jIBYgASgC
QGoiFCAWSQ0jIBRBA3ENIyAURQ0jIAFB4gBqIRMCQAJAAkACQAJAIAEoAkwiDg0AQQAhDQwBC0EAIREgECEJQQAhCkEAIQJBACENA0ACQCAJLwEAIgtFDQAC
QAJAIA8gC00NACACQYCABE8NKwJAIBQgC0EkbGoiDC8BHCACQf//A3FHDQAgDC8BHiIXQR9LDQAgAiAMEJuEgIAAIBetfCABMwFcg6dHDQAgEyAXQQF0aiIS
LwEAQQFqIhdB//8DcSAXRw0sIBIgFzsBACAGIApB//8DcSIXIAwvAR4iCiAXIApLGyIKOwEAIAwvASBFDQIgEUEBaiIRRQ0sIAEgETYCRAwCCyABQQA2AkQC
QCAqDQAgBkEAQcIA/AsACyABQQE7AaIBAkAgDkEBdCICRQ0AIBBBACAC/AsACyAPQX9qIRsMBgsgD0F/aiEbDAQLIA0gCyANIAtLGyENCyAJQQJqIQkgDiAC
QQFqIgJHDQALCwJAIA9Bf2oiG0UNACAUQcQAaiECQQEhCwNAAkACQCAOIAJBfGovAQAiCUsNACACLwEAIAJBfmovAQBB//8DcXJFDQEMBAsgC0F/akH//wNP
DSkgECAJQQF0ai8BACALQf//A3FHDQMgDSALIA0gC0sbIQ0LIAJBJGohAiAPIAtBAWoiC0cNAAsLIAEgDUEBaiICQf//AyACQf//A0kbOwGiAQwDC0EAIQgg
AUEANgJEAkAgKg0AIAZBAEHCAPwLAAsgAUEBOwGiAQJAIA5BAXQiAkUNACAQQQAgAvwLAAsgG0UNAQsgFEEkaiEcQQAhFUEAIQgDQCAVIgJB//8DRg0lIAJB
AWohFQJAAkAgHCACQSRsaiIWLwEgDQAgFkEANgEcDAELIAEoAkRBAWoiAkUNJiABIAI2AkQgCCAVIAggFUsbIQggFhCbhICAACEZIAEoAkwiDEUNACAZpyER
QQAhAiAVIRIDQAJAAkACQAJAAkAgDiABLwFcIAIgEWpxIhdB//8DcSILTQ0AAkACQAJAIBAgC0EBdGoiCi8BACILRQ0AIA8gC00NASAUIAtBJGxqIgkvAR4g
AkH//wNxIg1JDQIgDUEBaiICQf//A3EgAkcNLwwHCyAKIBI7AQAgFiACOwEeIBYgFzsBHCACQf//A3EiAkEfSw0DIBMgAkEBdGoiDS8BAEEBaiILQf//A3Eg
C0cNLiANIAs7AQAgBiAGLwEAIgsgAiALIAJLGzsBAAwICyALIA8QiICAgAAACyAKIBI7AQAgFiACOwEeIBYgFzsBHCANQR9LDQIgEyANQQF0aiIWLwEAQQFq
IgJB//8DcSACRw0sIBYgAjsBACAGIAYvAQAiAiANIAIgDUsbOwEAIAkvAR4iAkEfSw0DIBMgAkEBdGoiDS8BAEF/aiICQf//A3EgAkcNLCANIAI7AQAgCyES
IAkhFiAJLwEeQQFqIgJB//8DcSACRw0sDAQLIAsgDhCIgICAAAALIAJBIBCIgICAAAALIA1BIBCIgICAAAALIAJBIBCIgICAAAALIAxBf2oiDA0ACwsgFSAb
Rw0ACwsgCEEBaiICRQ0jIAEgAkH//wMgAkH//wNJGzsBogEgASgCECEWCyABQQA2ArABIAFB1AFqIQ4CQCAqDQAgDkEAQcIA/AsACyABQQE7AZYCAkAgASgC
tAEiF0UNACAWIAEoAqgBaiIRIBZJDSMgEUEBcQ0jIBFFDSMgFiABKAKsAWoiECAWSQ0jIBBBA3ENIyAQRQ0jIAFB1gFqIQYCQAJAAkACQAJAIAEoArgBIgoN
AEEAIQkMAQtBACELIBEhDEEAIQkDQAJAIAwvAQAiAkUNACABKAK0ASIPIAJNDQQCQAJAAkAgFyACTQ0AIAtBgIAETw0sIBAgAkEcbGoiDS8BFCALQf//A3FH
DQYgDS8BFkEfSw0GIAsgGkEAIA0Q7oOAgAAgDS8BFiIWrXwgATMByAGDp0cNBiAWQR9LDQEgBiAWQQF0aiIPLwEAQQFqIhZB//8DcSAWRw0sIA8gFjsBACAO
IA4vAQAiFiANLwEWIg8gFiAPSxs7AQAgDS8BGEUNAiABKAKwAUEBaiINRQ0sIAEgDTYCsAEMAgsgAiAXEIiAgIAAAAsgFkEgEIiAgIAAAAsgCSACIAkgAksb
IQkLIAxBAmohDCAKIAtBAWoiC0cNAAsLAkACQCAXQQFGDQAgEEE0aiECIAEoArgBIQxBASELA0ACQAJAIAwgAkF8ai8BACINTQ0AIAogDU0NBCALQX9qQf//
A08NKyARIA1BAXRqLwEAIAtB//8DcUcNBSAJIAsgCSALSxshCQwBCyACLwEAIAJBfmovAQBB//8DcXINBAsgAkEcaiECIBcgC0EBaiILRw0ACwsgCUEBaiEC
DAMLIA0gChCIgICAAAALIAEoArQBIQ8LIAFBADYCsAEgASgCECEWAkAgKg0AIA5BAEHCAPwLAAsgAUEBOwGWAiAPRQ0BIBYgASgCqAFqIhIgFkkNJCASQQFx
DSQgEkUNJCAWIAEoAqwBaiIUIBZJDSQgFEEDcQ0kIBRFDSRBACEIAkAgASgCuAEiE0EBdCICRQ0AIBJBACAC/AsACwJAIA9Bf2oiG0UNACAUQRxqIRxBACEV
QQAhCANAIBUiAkH//wNGDSYgAkEBaiEVAkACQCAcIAJBHGxqIhYvARgNACAWQQA2ARQMAQsgASgCsAFBAWoiAkUNJyABIAI2ArABIAggFSAIIBVLGyEIIBpB
ACAWEO6DgIAAIRkgASgCuAEiDEUNACAZpyEQQQAhAiAVIREDQAJAAkACQAJAAkAgEyABLwHIASACIBBqcSIXQf//A3EiC00NAAJAAkACQCASIAtBAXRqIgov
AQAiC0UNACAPIAtNDQEgFCALQRxsaiIJLwEWIAJB//8DcSINSQ0CIA1BAWoiAkH//wNxIAJHDTAMBwsgCiAROwEAIBYgAjsBFiAWIBc7ARQgAkH//wNxIgJB
H0sNAyAGIAJBAXRqIg0vAQBBAWoiC0H//wNxIAtHDS8gDSALOwEAIA4gDi8BACILIAIgCyACSxs7AQAMCAsgCyAPEIiAgIAAAAsgCiAROwEAIBYgAjsBFiAW
IBc7ARQgDUEfSw0CIAYgDUEBdGoiFi8BAEEBaiICQf//A3EgAkcNLSAWIAI7AQAgDiAOLwEAIgIgDSACIA1LGzsBACAJLwEWIgJBH0sNAyAGIAJBAXRqIg0v
AQBBf2oiAkH//wNxIAJHDS0gDSACOwEAIAshESAJIRYgCS8BFkEBaiICQf//A3EgAkcNLQwECyALIBMQiICAgAAACyACQSAQiICAgAAACyANQSAQiICAgAAA
CyACQSAQiICAgAAACyAMQX9qIgwNAAsLIBUgG0cNAAsLIAhBAWoiAkUNJAsgASACQf//AyACQf//A0kbOwGWAiABKAIQIRYLIBYgASgCGGoiBiAWSQ0iIAZB
B3ENIiAGRQ0iAkAgAS8BogIiD0UNAEEAIRcDQCAGIBdBA3RqIQoCQAJAIBcgAS8BqgJJDQAgCikDAEL/////v2yDIRlCACE/DAELIBooAgAiAiAKKAIAaiIO
IAJJDSUgDkEHcQ0lIA5FDSUCQAJAIAEvAagCIgJFDQAgAkEBcSEQQQAhC0EAIQ1BACEJQQAhDCACQQFGDQEgAkH+/wNxIRZBACELIA4hAkEAIQ1BACEJQQAh
DANAIAkgAikDACIZQgODQgFRciACQQhqKQMAIj9CA4NCAVFyIQkgAkEQaiECIAsgPyAZhCIZQoCAgOD//wCDQgBSciELIA0gGUKAgICAgIAIg0ItiKdyIQ0g
FiAMQQJqIgxHDQAMAgsLIAopAwBC/////79sgyEZQgAhPwwBCwJAIBBFDQAgCSAOIAxBA3RqKQMAIhlCA4NCAVFyIQkgCyAZQoCAgOD//wCDQgBSciELIA0g
GUKAgICAgIAIg0ItiKdyIQ0LIAopAwBC/////79sgyEZAkACQAJAAkAgC0EBcUUNACAZQoCAgICAAYQhGSANQQFxDQEMAgsgDUEBcUUNAQsgGUKAgICAgAKE
IRkgCUEBcQ0BQgAhPwwCCyAJQQFxDQBCACE/DAELQoCAgIDAACE/CyAKID8gGYQ3AwAgF0EBaiIXIA9HDQALCwJAAkAgIkUNACABICI2AggCQCAiKAIMIgJF
DQAgASACNgIMIAIgATYCCCAiIAE2AgwgByECDAILIAFBADYCDCAiIAE2AgwgASEiIAchAgwBCwJAAkACQCAHRQ0AIAEgBzYCDCAHKAIIIgJFDQEgASACNgII
IAIgATYCDCAHIQIMAgsgAUIANwIIIAEhIiABIQIMAgsgAUEANgIIIAEhAgsgByABNgIIQQAhIgsgAyAgICNqIgsgIEkiDToA2AECQCANDQAgCyEgIAIhByAh
QQFqIiEgHUYNBAwBCwsgACgCXCILIAEoAhRrIg0gC0sNISAAIA02AlwCQAJAIAEoAhQiC0GAgCRLDQACQCALRQ0AIBooAgBBACAL/AsACyAaKAIAIQsCQEGA
gCRFDQAgC0GqAUGAgCT8CwALIAsgACgCPDYCACAAIAs2AjwMAQsgACgCLCEJIAAoAjAhDCAaKAIAIQ0CQCALRQ0AIA1BqgEgC/wLAAsgCSANIAtBEEEAIAwo
AgwRgYCAgACAgICAAAsCQEGwAkUNACABQaoBQbAC/AsACyABIAAoAig2AgAgACABNgIoIAJFDR8DQCAAKAJcIgEgAigCFGsiDSABSw0iIAIoAgwhCyAAIA02
AlwCQAJAIAIoAhQiAUGAgCRLDQACQCABRQ0AIAIoAhBBACAB/AsACyACKAIQIQECQEGAgCRFDQAgAUGqAUGAgCT8CwALIAEgACgCPDYCACAAIAE2AjwMAQsg
ACgCLCEJIAAoAjAhDCACKAIQIQ0CQCABRQ0AIA1BqgEgAfwLAAsgCSANIAFBEEEAIAwoAgwRgYCAgACAgICAAAsCQEGwAkUNACACQaoBQbAC/AsACyACIAAo
Aig2AgAgACACNgIoIAshAiALRQ0gDAALCwJAQbACRQ0AIAFBqgFBsAL8CwALIAEgACgCKDYCACAAIAE2AigLQRkhBCAHRQ0dA0AgACgCXCICIAcoAhRrIgEg
AksNICAHKAIMIQIgACABNgJcAkACQCAHKAIUIgFBgIAkSw0AAkAgAUUNACAHKAIQQQAgAfwLAAsgBygCECEBAkBBgIAkRQ0AIAFBqgFBgIAk/AsACyABIAAo
Ajw2AgAgACABNgI8DAELIAAoAiwhDSAAKAIwIQkgBygCECELAkAgAUUNACALQaoBIAH8CwALIA0gCyABQRBBACAJKAIMEYGAgIAAgICAgAALAkBBsAJFDQAg
B0GqAUGwAvwLAAsgByAAKAIoNgIAIAAgBzYCKCACIQcgAkUNHgwACwsCQCALIAAvAY4BTw0AIAJFDR0DQCAAKAJcIgEgAigCFGsiCyABSw0gIAIoAgwhASAA
IAs2AlwCQAJAIAIoAhQiC0GAgCRLDQACQCALRQ0AIAIoAhBBACAL/AsACyACKAIQIQsCQEGAgCRFDQAgC0GqAUGAgCT8CwALIAsgACgCPDYCACAAIAs2AjwM
AQsgACgCLCEJIAAoAjAhDCACKAIQIQ0CQCALRQ0AIA1BqgEgC/wLAAsgCSANIAtBEEEAIAwoAgwRgYCAgACAgICAAAsCQEGwAkUNACACQaoBQbAC/AsACyAC
IAAoAig2AgAgACACNgIoIAEhAiABDQAMHgsLAkAgACgCVCIBRQ0AA0AgACgCXCIEIAEoAhRrIg0gBEsNICABKAIMIQQgACANNgJcAkACQCABKAIUIg1BgIAk
Sw0AAkAgDUUNACABKAIQQQAgDfwLAAsgASgCECENAkBBgIAkRQ0AIA1BqgFBgIAk/AsACyANIAAoAjw2AgAgACANNgI8DAELIAAoAiwhDCAAKAIwIRYgASgC
ECEJAkAgDUUNACAJQaoBIA38CwALIAwgCSANQRBBACAWKAIMEYGAgIAAgICAgAALAkBBsAJFDQAgAUGqAUGwAvwLAAsgASAAKAIoNgIAIAAgATYCKCAEIQEg
BA0ACwsgACALNgJoIAAgIjYCWCAAIAI2AlQgAEEAOgCQASAAQgA3AwggAEIANwKEASAAKAJ0IgJBgICAgARPDR4gACgCcCEEAkACQCACDQBBqtWq1XohAgwB
CyAAKAJsIgJBA3ENHwsCQCAERQ0AA0AgACgCVCILRQ0gIAIoAgAiASALNgIAIAFBAToACCABQQA2AQQgAkEEaiECIARBf2oiBA0ACwtBACECIAAoAoABQQA6
AAgCQAJAAkAgBUL/////D1YNACAAIAU+AmAgACAFUDoAtAIgAyADKAIMIgJBAmoiBCACSSIBOgCIASABDQEgBCADKAIIIgtLDQEgAUUNAiACIAQQioCAgAAA
CyAAKQOYASEZAkACQCADKAIYIgENAEGq1arVeiEADAELIAFBgICAwABPDQMgAygCECEAIAFBBXQhAgtBNyEEIAJFDR8gGUIgiKchASAZpyELAkAgAkUNACAA
QaoBIAL8CwALIAsgACACQQJBACABKAIMEYGAgIAAgICAgAAMHwsgA0EQaiAAKQOYARDjhICAAEE3IQQMHgsgAygCBCIJIAJqLwAAIQ0gAyAEQQJqIgEgBEki
AjoAiAECQAJAIAINACABIAtLDQAgAkUNASAEIAEQioCAgAAACyADQRBqIAApA5gBEOOEgIAAQTchBAweCyADIAE2AgwCQAJAIA1B//8DcSAALwGMAU8NACAJ
IARqLwAAIgEgAC8BjgFJDQELIAApA5gBIRkCQAJAIAMoAhgiAg0AQQAhAkGq1arVeiEBDAELIAJBgICAwABPDQIgAygCECEBIAJBBXQhAgtBNyEEIAJFDR4g
GUIgiKchACAZpyELAkAgAkUNACABQaoBIAL8CwALIAsgASACQQJBACAAKAIMEYGAgIAAgICAgAAMHgsgA0GIAWogAEEAELqDgIAAIAMvAYwBIglBAWoiAkH/
/wNxIgsgAkcNACADKAKIASICLwGqAiIEIAtrIgtB//8DcSALRw0AAkACQCABIAtB//8DcSILSw0AIAkgAWoiAUH//wMgAUH//wNJGyEEDAELAkACQCACKAIM
IgJFDQAgASALayEBA0AgASACLwGqAiIETQ0CIAEgBGshASACKAIMIgINAAsLIARFDQIgA0EQaiAAKQOYARDjhICAAEE3IQQMHwsgAUF/aiIEIAFLDQELIAAo
AsQBIgFBADYCCCABIA07AQYgASAEOwEEIAEgAjYCACADIAMoAgwiAUEBaiICRSIEOgCIAQJAAkAgAkUNACACIAMoAggiC0sNACAEDQMgAyACNgIMQQAtANL5
yYAAQQNxIAMoAgQiDSABai0AACIBRg0BQQAtANP5yYAAQQNxIAFGDQFBAC0A1PnJgABBA3EgAUYNAUEALQDV+cmAAEEDcSABRg0BCyADQRBqIAApA5gBEOOE
gIAAQTchBAweCyAAIAFBA3E6ANgBIAMgAkEBaiIBRSIJOgCIASABRQ0CQTchBCABIAtLDRsgCQ0DQQAoApz5yYAAIglBNyANIAJqLQAAIgJBAUYbQQAoApj5
yYAAIgwgAhsiBEH//wNxDRsgACAEQRB2QQFxOgDZASADIAFBAWoiAkUiFjoAiAEgAkUNBEE3IQQgAiALSw0aIBYNBSADIAI2AgwgCUE3IA0gAWotAAAiAkEB
RhsgDCACGyIEQf//A3ENGiAAIARBEHZBAXE6ANoBIANBJGogA0EEahDkhICAAAJAIAMvAUAiBEUNACADQRBqIAApA5gBEOOEgIAADB4LIABBoAFqIgIgAykC
JDcCACACQRhqIANBJGpBGGooAgA2AgAgAkEQaiADQSRqQRBqKQIANwIAIAJBCGogA0EkakEIaikCADcCACADIAMoAgwiAkECaiIEIAJJIgE6AIgBAkACQCAB
DQAgBCADKAIIIgtLDQAgAUUNASACIAQQioCAgAAACyADQRBqIAApA5gBEOOEgIAAQTchBAweCyADKAIEIgEgAmovAAAhFiADIARBAWoiAkUiDToAiAEgAkUN
FiACIAtLDRYgDQ0GIAMgAjYCDAJAQQAtAM/5yYAAQQNxIAEgBGotAAAiBEYNAEEALQDQ+cmAAEEDcSAERg0AQQAtANH5yYAAQQNxIARHDRcLIARBA3FBA0YN
ACAAIARBA3E6ANsBIAMgAkEBaiINRSIXOgCIASANRQ0HQTchBCANIAtLDRkgFw0IIAlBNyABIAJqLQAAIgJBAUYbIAwgAhsiBEH//wNxDRkgACAEQRB2QQFx
OgDcASADIA1BBGoiBCANSSICOgCIAQJAAkAgAg0AIAQgC0sNACACRQ0BIA0gBBCKgICAAAALIANBEGogACkDmAEQ44SAgABBNyEEDB4LIAEgDWooAAAhFyAD
IARBAmoiDSAESSICOgCIAQJAAkAgAg0AIA0gC0sNACACRQ0BIAQgDRCKgICAAAALIANBEGogACkDmAEQ44SAgABBNyEEDB4LIAEgBGovAAAhCiADIA1BAWoi
AkUiBDoA7AECQAJAAkAgAkUNACACIAtLDQAgBA0MIAMgAjYCDCAJQTcgASANai0AACIEQQFGGyAMIAQbIgRB//8DcQ0BAkACQCAEQYCABHENAEEAIQQgA0Ho
AGpBADYCAEIAIRkgA0HgAGpCADcDACADQdgAakIANwMAIANByABqQQA6AAAgA0IANwNQIANBADoATCADQQA6AEsgA0EAOgBKIANBADsBRkEAIQtBACENQQAh
DkEAIQFBACECDAELIAMgAkECaiIOIAJJIg06AOwBIA0NAUE3IQQgDiALSw0CIA0NDiABIAJqLwAAIQIgAyAOQQJqIgYgDkkiDToA7AEgDQ0BIAYgC0sNAiAN
DQ8gAyAGNgIMIAEgDmovAAAhASADQYgBaiADQQRqEOSEgIAAIAMvAaQBIgQNAiADQdAAakEYaiADQYgBakEYaigCADYCACADQdAAakEQaiADQYgBakEQaikC
ADcDACADQdAAakEIaiADQYgBakEIaikCADcDACADIAMpAogBNwNQIAMgAygCDCINQQFqIgtFIg46AOwBIAtFDQFBNyEEIAsgAygCCCIGSw0CIA4NECAJQTcg
AygCBCIOIA1qLQAAIgRBAUYbIAwgBBsiBEH//wNxDQIgAyAEQRB2QQFxOgBMIAMgC0EBaiINRSIEOgDsASANRQ0BIA0gBksNASAEDREgCUE3IA4gC2otAAAi
BEEBRhsgDCAEGyIEQf//A3ENAiADIARBEHZBAXE6AEsgAyANQQFqIgRFIgs6AOwBIARFDQEgBCAGSw0BIAsNEiADIAQ2AgwgCUE3IA4gDWotAAAiBEEBRhsg
DCAEGyIEQf//A3ENAiADIARBEHZBAXE6AEogA0HYAWogA0EEahDlhICAACADLwHYASIEDQJBASEOIAMtAEohBCADLQBLIQsgAy0ATCENIAMpAdoBIRkLIAAg
AykDUDcD4AEgACAOOgCMAiAAQQA6AIsCIAAgGTcAgwIgACAEOgCCAiAAIAs6AIECIAAgDToAgAIgACABOwH+ASAAIAI7AfwBIABB+AFqIANB6ABqKAIANgIA
IABB8AFqIANB4ABqKQMANwMAIABB6AFqIANB2ABqKQMANwMAIAAgAy8BRjsAjQIgAEGPAmogA0HIAGotAAA6AAAgA0HuAGogA0EEahDlhICAACADLwFuIgRF
DQIgA0EQaiAAKQOYARDjhICAAAwgC0E3IQQLIANBEGogACkDmAEQ44SAgAAMHgsgACADKQFwNwC1AiADIAMoAgwiAUEBaiICRSIEOgCIASACRQ0XIAIgAygC
CCILSw0XIAQNDyADIAI2AgwCQEEALQDP+cmAAEEDcSADKAIEIg0gAWotAAAiAUYNAEEALQDQ+cmAAEEDcSABRg0AQQAtANH5yYAAQQNxIAFHDRgLIAFBA3FB
A0YNACAAIAFBA3E6AL0CIAMgAkEBaiIBRSIEOgCIAQJAAkAgAUUNACABIAtLDQAgBA0SIAMgATYCDCANIAJqLQAAIgJBB0sNACADIAJBB3E6AOIBIAMgAUEB
aiICRToAiAEgAkUNACACIAtLDQAgAUF/Rg0TIA0gAWotAAAiBEEfSw0AIAMgBEEfcToA2AEgAyACQQFqIgRFOgCIASAERQ0AIAQgC0sNACABQX5GDRMgDSAC
ai0AACICQR9LDQAgAyACQR9xOgDsASADIARBAWoiAkU6AIgBIAJFDQAgAiALSw0AIAFBfUYNEyANIARqLQAAIgRBH0sNACADIARBH3E6AOgBIAMgAkEBaiIE
RToAiAEgBEUNACAEIAtLDQAgAUF8Rg0TIA0gAmotAAAiAkEfSw0AIAMgAkEfcToA5wEgAyAEQQFqIgJFOgCIASACRQ0AIAIgC0sNACABQXtGDRMgDSAEai0A
ACIEQR9LDQAgAyAEQR9xOgDmASADIAJBAWoiBEU6AIgBIARFDQAgBCALSw0AIAFBekYNEyANIAJqLQAAIgJBH0sNACADIAJBH3E6AOUBIAMgBEEBaiIORToA
iAEgDkUNACAOIAtLDQAgAUF5Rg0TIA0gBGotAAAiAkEfSw0AIAMgAkEfcToA5AEgAyAOQQFqIgJFOgCIASACRQ0AIAIgC0sNACABQXhGDRMgDSAOai0AACIB
QSBJDQELIANBEGogACkDmAEQ44SAgABBNyEEDB4LIAMgAUEfcSIBOgDjASADLQDYASEEIAMtAOwBIQ4gAy0A6AEhBiADLQDnASEPIAMtAOYBIRAgAy0A5QEh
ESADLQDkASESIAAgAy0A4gE6AMYCIAAgAToAxQIgACASOgDEAiAAIBE6AMMCIAAgEDoAwgIgACAPOgDBAiAAIAY6AMACIAAgDjoAvwIgACAEOgC+AiADIAJB
AWoiAUUiDjoAiAEgAUUNEkE3IQQgASALSw0YIA4NEyAJQTcgDSACai0AACICQQFGGyAMIAIbIgRB//8DcQ0YIAAgBEEQdkEBcToAxwIgAyABQQFqIgJFIgQ6
AIgBAkACQAJAAkAgAkUNACACIAtLDQAgBA0YAkACQCANIAFqLQAADgMDAAECC0EALQC4+cmAACEEDAMLIANBAjoAeiADIAJBAWoiAUUiBDoAiAEgAUUNACAB
IAtLDQAgBA0ZAkBBAC0A0vnJgABBA3EgDSACai0AACICRg0AQQAtANP5yYAAQQNxIAJGDQBBAC0A1PnJgABBA3EgAkYNAEEALQDV+cmAAEEDcSACRw0BCyAD
IAJBA3E6AHkgAy0AeiEEDAMLIANBEGogACkDmAEQ44SAgABBNyEEDCALQQAtALT5yYAAIQQLIAIhAQsgACADLQB5OgDJAiAAIAQ6AMgCIAsgAUkNAAJAIAsg
AUYNACADQRBqIAApA5gBEOOEgIAAQTchBAweCyAAEMGDgIAAAkAgACAWEOaEgIAAIgRB//8DcUUNACADQRBqIAApA5gBEOOEgIAADB4LIAAgFzYCvAEgACAK
EOeEgIAAIQQgA0EQaiAAKQOYARDjhICAAAwdCwALIAEgAhCKgICAAAALQTchBAwYCyACIAEQioCAgAAAC0E3IQQMFQsgASACEIqAgIAAAAsgBCACEIqAgIAA
AAtBNyEEDBELIAIgDRCKgICAAAALIA0gAhCKgICAAAALIAIgDhCKgICAAAALIA4gBhCKgICAAAALIA0gCxCKgICAAAALIAsgDRCKgICAAAALIA0gBBCKgICA
AAALIAEgAhCKgICAAAALIAIgARCKgICAAAALQX9BABCKgICAAAALQTchBAwFCyACIAEQioCAgAAACyABIAIQioCAgAAACyACIAEQioCAgAAACyADQRBqIAAp
A5gBEOOEgIAAQTchBAwGCyADQRBqIAApA5gBEOOEgIAAQTchBAwFCyADQRBqIAApA5gBEOOEgIAADAQLIANBEGogACkDmAEQ44SAgAAMAwsgA0EQaiAAKQOY
ARDjhICAAAwCCyADQRBqIAApA5gBEOOEgIAADAELIANBEGogACkDmAEQ44SAgAALIANB8AFqJICAgIAAIAQPCwALiQEBBX8jgICAgABBEGsiAiSAgICAACAC
IAEoAggiA0ECaiIEIANJIgU6AAwCQAJAAkAgBQ0AQTchBiAEIAEoAgRLDQIgBUUNASADIAQQioCAgAAAC0E3IQYMAQsgASAENgIIIAAgASgCACADai8AADsB
AEEAIQYLIAAgBjsBAiACQRBqJICAgIAAC9wCAQZ/I4CAgIAAQRBrIgIkgICAgAAgAEEAOwEYIAIgASgCCCIDQQVqIgQgA0kiBToABAJAAkACQCAFDQAgBCAB
KAIEIgZLDQAgBUUNASADIAQQioCAgAAACyAAQTc7ARgMAQsgASAENgIIIAAgASgCACIHIANqIgM1AAA+AgAgAEEEaiADQQRqMQAAPAAAIAIgBEEFaiIDIARJ
IgU6AAgCQAJAIAUNACADIAZLDQAgBUUNASAEIAMQioCAgAAACyAAQTc7ARgMAQsgASADNgIIIAAgByAEaiIENQAAPgIIIABBDGogBEEEajEAADwAACACIANB
BWoiBSADSSIEOgAMAkACQCAEDQAgBSAGSw0AIARFDQEgAyAFEIqAgIAAAAsgAEE3OwEYDAELIAEgBTYCCCAAIAcgA2oiATUAAD4CECAAQRRqIAFBBGoxAAA8
AAALIAJBEGokgICAgAALtQgBB38jgICAgABBwBlrIgIkgICAgAACQEGgCEUNACACQbgIakGqAUGgCPwLAAsgAkHcEGogARDohICAAAJAIAIvAewQIgMNACAC
QQhqQQhqIAJB3BBqQQhqKQIANwMAIAIgAikC3BA3AwggAkHwEGogARDohICAACACLwGAESIDDQAgAkEgaiACQfAQakEIaikCADcDACACIAIpAvAQNwMYIAJB
hBFqIAEQ6ISAgAAgAi8BlBEiAw0AIAJBMGogAkGMEWopAgA3AwAgAiACKQKEETcDKAJAQYAIRQ0AIAJBmBFqQaoBQYAI/AsAC0EAIQQDQCACQZgZaiABEOmE
gIAAIAIvAZwZIgMNASACQZgRaiAEaiIDIAIvAZgZOwEAIANBAmogAi0Amhk6AAAgBEEEaiIEQYAIRw0ACwJAQYAIRSIEDQAgAkE4aiACQZgRakGACPwKAAAL
AkAgBA0AIAJBmBFqQaoBQYAI/AsAC0EAIQQDQCACQZgZaiABEOmEgIAAIAIvAZwZIgMNASACQZgRaiAEaiIDIAIvAZgZOwEAIANBAmogAi0Amhk6AAAgBEEE
aiIEQYAIRw0ACwJAQYAIRQ0AIAJBuAhqIAJBmBFqQYAI/AoAAAsgAiABKAIIIgVBBGoiBCAFSSIGOgCYEQJAIAYNAEE3IQMgBCABKAIEIgdLDQECQCAGDQAg
ASAENgIIIAIgBCABKAIAIgggBWooAABqIgYgBEkiBToAmBEgBQ0CIAYgB0sNAgJAIAUNACABIAY2AgggBiAEa0EgRw0DIAggBGohCCACQaAZakEYakIANwMA
IAJBoBlqQRBqQgA3AwAgAkGgGWpBCGpCADcDACACQgA3A6AZQQAhBkEAIQUDQCAGQRhxIQMgAkGgGWogBUF8cWohBCAIIAVqLQAAIgfAIQECQCAHQQFxRQ0A
IAQgBCgCAEEBIAN0cjYCAAsCQCABQQJxRQ0AIAQgBCgCAEECIAN0cjYCAAsCQCABQQRxRQ0AIAQgBCgCAEEEIAN0cjYCAAsCQCABQQhxRQ0AIAQgBCgCAEEI
IAN0cjYCAAsCQCABQRBxRQ0AIAQgBCgCAEEQIAN0cjYCAAsCQCABQSBxRQ0AIAQgBCgCAEEgIAN0cjYCAAsCQCABQcAAcUUNACAEIAQoAgBBwAAgA3RyNgIA
CwJAIAFBf0oNACAEIAQoAgBBgAEgA3RyNgIACyAGQQhqIQYgBUEBaiIFQSBHDQALIAJB0BBqIAJBuBlqKQMANwMAIAJByBBqIAJBsBlqKQMANwMAIAJBwBBq
IAJBqBlqKQMANwMAIAIgAikDoBk3A7gQAkBB0BBFDQAgACACQQhqQdAQ/AoAAAtBACEDDAMLIAQgBhCKgICAAAALIAUgBBCKgICAAAALQTchAwsgACADOwHQ
ECACQcAZaiSAgICAAAvKBwEMfyOAgICAAEEgayIDJICAgIAAIAMgACgCCCIEQQRqIgUgBEkiBjoACAJAAkACQCAGDQBBNyEHIAUgACgCBCIISw0CIAZFDQEg
BCAFEIqAgIAAAAtBNyEHDAELIAAgBTYCCCAAKAIAIgkgBGooAAAhCiADIAVBBGoiBCAFSSIGOgAMIAYNACAEIAhLDQACQAJAAkACQAJAAkACQAJAIAYNACAA
IAQ2AgggAyAEIAkgBWooAABqIgUgBEkiBjoAECAGDQggBSAISw0IIAYNASAAIAU2AgggAyAFQQRqIgYgBUkiCzoAFCALDQggBiAISw0IIAsNAiAAIAY2Aggg
AyAGIAkgBWooAABqIgwgBkkiCzoAGCALDQggDCAISw0IIAsNAyAAIAw2AgggBSAEa0HAAEcNCAJAAkAgAigCCCIADQAgAUIgiKchDSABpyEODAELIAIoAgQh
BQJAIABFDQAgBUGqASAA/AsACyABpyIOIAUgAEEAQQAgAUIgiKciDSgCDBGBgICAAICAgIAACwJAQcwARQ0AIAJBtPjJgABBzAD8CgAACwJAIApBgQRPDQBB
qtWq1XohC0EAIQgMBwtBGSEHQQANCCAOIApBgHxqIghBAEEAIA0oAgARgICAgACAgICAACILRQ0IAkAgCEUNACALQQAgCPwLAAsgAigCCCIARQ0FIAAgCEsN
BAJAIAIoAgQiBSALIABqTw0AIAsgBSAAakkNCAsCQCAARQ0AIAsgBSAA/AoAAAsgAigCCCIARQ0FIAIoAgQhBQJAIABFDQAgBUGqASAA/AsACyAOIAUgAEEA
QQAgDSgCDBGBgICAAICAgIAADAULIAUgBBCKgICAAAALIAQgBRCKgICAAAALIAUgBhCKgICAAAALIAYgDBCKgICAAAALIAAgCBCIgICAAAALIAIgCDYCCCAC
IAs2AgQLIAIgCjYCACACQQxqIQACQCAJIARqIgUgAkHMAGpPDQAgACAFQcAAakkNAQsgACAFKQAANwAAIABBOGogBUE4aikAADcAACAAQTBqIAVBMGopAAA3
AAAgAEEoaiAFQShqKQAANwAAIABBIGogBUEgaikAADcAACAAQRhqIAVBGGopAAA3AAAgAEEQaiAFQRBqKQAANwAAIABBCGogBUEIaikAADcAAEE3IQcgDCAG
ayIAIAhHDQEgCSAGaiEFAkAgCyAJIAxqTw0AIAUgCyAAakkNAQsCQCAARQ0AIAsgBSAA/AoAAAsgAiAKNgIAQQAhBwwBCwALIANBIGokgICAgAAgBwv5AgEG
fyOAgICAAEEQayICJICAgIAAIAIgASgCCCIDQQFqIgRFIgU6AAQCQAJAAkACQAJAIARFDQBBNyEGIAQgASgCBCIHSw0BIAUNAyABIAQ2AghBACgCnPnJgABB
NyABKAIAIgUgA2otAAAiBkEBRhtBACgCmPnJgAAgBhsiBkH//wNxDQEgBkGAgARxDQIgAEIANwIAIABBCGpBADYCAAwEC0E3IQYLIAAgBjsBCAwCCyACIARB
BGoiAyAESSIGOgAIAkACQCAGDQAgAyAHSw0AIAZFDQEgBCADEIqAgIAAAAsgAEE3OwEIDAILIAEgAzYCCAJAIAUgBGooAAAiBEH///8ASw0AIAAgBDsBACAA
QQA7AQggAEEBOgAEIABBAmogBEEQdkEfcSIBOgAAIAIgBDsBACACIAE6AAIMAgsgAEEIakEAKAKI+cmAADYCACAAQQApAoD5yYAANwIADAELIAMgBBCKgICA
AAALIAJBEGokgICAgAALywEBBH8jgICAgABBEGsiAiSAgICAACACIAEoAggiA0EBaiIERSIFOgAMAkACQAJAIARFDQAgBCABKAIESw0AIAVFDQEgAyAEEIqA
gIAAAAsgAEE3OwEADAELIAEgBDYCCAJAAkBBAC0AyPnJgABBAXEgASgCACADai0AACIBRg0AQQAtAMn5yYAAQQFxIAFGDQBBjPnJgAAhAQwBCyACQQA7AQgg
AiABQQFxOgAKIAJBCGohAQsgACABKAEANgEACyACQRBqJICAgIAAC6kBAQR/I4CAgIAAQRBrIgIkgICAgAAgAiABKAIIIgNBBGoiBCADSSIFOgAMAkACQAJA
IAUNACAEIAEoAgRLDQAgBUUNASADIAQQioCAgAAACyAAQTc7AQQMAQsgASAENgIIQZD5yYAAIQUCQCABKAIAIANqKAAAIgFBIUsNACACIAE2AgQgAkEAOwEI
IAJBBGohBQsgACAFKQIANwIACyACQRBqJICAgIAAC+0BAQR/I4CAgIAAQRBrIgIkgICAgAAgAiABKAIIIgNBAWoiBEUiBToADAJAAkACQCAERQ0AIAQgASgC
BEsNACAFRQ0BIAMgBBCKgICAAAALIABBNzsBAAwBCyABIAQ2AggCQAJAAkBBAC0Az/nJgABBA3EgASgCACADai0AACIBRg0AQQAtAND5yYAAQQNxIAFGDQBB
AC0A0fnJgABBA3EgAUcNAQsCQCABQQNxQQNHDQAACyACQQA7AQggAiABQQNxOgAKIAJBCGohAQwBC0Gk+cmAACEBCyAAIAEoAQA2AQALIAJBEGokgICAgAAL
mQEBBH8jgICAgABBEGsiAiSAgICAACACIAEoAggiA0EBaiIERSIFOgAMAkACQAJAIARFDQAgBCABKAIESw0AIAVFDQEgAyAEEIqAgIAAAAsgAEE3OwEADAEL
IAEgBDYCCCAAQQAoApz5yYAAQTcgASgCACADai0AACIBQQFGG0EAKAKY+cmAACABGzYBAAsgAkEQaiSAgICAAAuPAgEEfyOAgICAAEEQayICJICAgIAAIAIg
ASgCCCIDQQFqIgRFIgU6AAwCQAJAAkAgBEUNACAEIAEoAgRLDQAgBUUNASADIAQQioCAgAAACyAAQTc7AQAMAQsgASAENgIIAkACQAJAQQAtAMr5yYAAQQdx
IAEoAgAgA2otAAAiAUYNAEEALQDL+cmAAEEHcSABRg0AQQAtAMz5yYAAQQdxIAFGDQBBAC0AzfnJgABBB3EgAUYNAEEALQDO+cmAAEEHcSABRw0BCwJAIAFB
B3FBBUkNAAALIAJBADsBCCACIAFBB3E6AAogAkEIaiEBDAELQaD5yYAAIQELIAAgASgBADYBAAsgAkEQaiSAgICAAAvoAQEGfyOAgICAAEEQayICJICAgIAA
IAIgASgCCCIDQQRqIgQgA0kiBToACAJAAkACQCAFDQBBNyEGIAQgASgCBCIHSw0CIAVFDQEgAyAEEIqAgIAAAAtBNyEGDAELIAEgBDYCCCACIAQgASgCACIG
IANqKAAAaiIFIARJIgM6AAwCQAJAAkACQCADDQAgBSAHTQ0BC0E3IQYMAQsgAw0BIAEgBTYCCCAFIARrIQEgBiAEaiEEQQAhBgsgACABNgIEIAAgBDYCAAwB
CyAEIAUQioCAgAAACyAAIAY7AQggAkEQaiSAgICAAAuhAQEEfwJAAkACQCAAQQhqKAIAIgINAEEAIQJBqtWq1XohAwwBCyACQYCAgMAATw0BIAAoAgAhAyAC
QQV0IQILAkAgAkUNACABQiCIpyEEIAGnIQUCQCACRQ0AIANBqgEgAvwLAAsgBSADIAJBAkEAIAQoAgwRgYCAgACAgICAAAsgAEKq1arVqtWq1ap/NwIAIABB
CGpBqtWq1Xo2AgAPCwALiAIBBH8jgICAgABBMGsiAiSAgICAACAAQQA7ARwgAiABEOqEgIAAAkACQCACLwEIIgNFDQAgACADOwEcDAELIAAgAikDADcCACAC
QRBqIAEQ6oSAgAACQCACLwEYIgNFDQAgACADOwEcDAELIAAgAikDEDcCCCACQSBqIAEQ6oSAgAACQCACLwEoIgNFDQAgACADOwEcDAELIAAgAikDIDcCECAC
IAEoAggiA0ECaiIEIANJIgU6ACwCQAJAIAUNACAEIAEoAgRLDQAgBUUNASADIAQQioCAgAAACyAAQTc7ARwMAQsgASAENgIIIAAgASgCACADai8AADsBGAsg
AkEwaiSAgICAAAvPCgEIfyOAgICAAEEQayICJICAgIAAIAJBADoAAyACQQA6AAQgAkEAOgACIAJBAC0ArPnJgAA6AAEgAkEALQCt+cmAADoAACACIAEoAggi
A0EBaiIERSIFOgAMAkACQAJAAkACQAJAAkACQAJAAkACQCAERQ0AIAQgASgCBCIGSw0AIAUNAiABIAQ2AghBAC0A0vnJgABBA3EiByABKAIAIgUgA2otAAAi
A0YNAUEALQDT+cmAAEEDcSADRg0BQQAtANT5yYAAQQNxIANGDQFBAC0A1fnJgABBA3EgA0YNAQsgAEE3OwEADAkLIAIgA0EDcToABSACIARBAWoiA0UiCDoA
DAJAAkAgA0UNACADIAZLDQAgCA0DIAEgAzYCCCAHIAUgBGotAAAiBEYNAUEALQDT+cmAAEEDcSAERg0BQQAtANT5yYAAQQNxIARGDQFBAC0A1fnJgABBA3Eg
BEYNAQsgAEE3OwEADAkLIAIgBEEDcToABCACIANBAWoiBEUiCDoADAJAAkAgBEUNACAEIAZLDQAgCA0EIAEgBDYCCCAHIAUgA2otAAAiA0YNAUEALQDT+cmA
AEEDcSADRg0BQQAtANT5yYAAQQNxIANGDQFBAC0A1fnJgABBA3EgA0YNAQsgAEE3OwEADAkLIAIgA0EDcToAAyACIARBAWoiA0UiCDoADAJAAkAgA0UNACAD
IAZLDQAgCA0FIAEgAzYCCCAHIAUgBGotAAAiBEYNAUEALQDT+cmAAEEDcSAERg0BQQAtANT5yYAAQQNxIARGDQFBAC0A1fnJgABBA3EgBEYNAQsgAEE3OwEA
DAkLIAIgBEEDcToAAiACIANBAWoiBEUiBzoADAJAAkAgBEUNACAEIAZLDQAgBw0GIAEgBDYCCEEALQDS+cmAAEEDcSIHIAUgA2otAAAiA0YNAUEALQDT+cmA
AEEDcSADRg0BQQAtANT5yYAAQQNxIANGDQFBAC0A1fnJgABBA3EgA0YNAQsgAEE3OwEADAkLIAIgA0EDcToAASACIARBAWoiA0UiCDoADAJAAkAgA0UNACAD
IAZLDQAgCA0HIAEgAzYCCCAHIAUgBGotAAAiBEYNAUEALQDT+cmAAEEDcSAERg0BQQAtANT5yYAAQQNxIARGDQFBAC0A1fnJgABBA3EgBEYNAQsgAEE3OwEA
DAkLIAIgBEEDcToAACACIANBAWoiBEUiCToADAJAAkACQAJAIARFDQBBNyEIIAQgBksNASAJDQogASAENgIIQQAoApz5yYAAQTcgBSADai0AACIDQQFGG0EA
KAKY+cmAACADGyIIQf//A3ENAUGw+cmAACEDIAhBgIAEcUUNAyACIARBAWoiA0UiCDoADAJAIANFDQAgAyAGSw0AIAgNDCABIAM2AgggByAFIARqLQAAIgFG
DQNBAC0A0/nJgABBA3EgAUYNA0EALQDU+cmAAEEDcSABRg0DQQAtANX5yYAAQQNxIAFGDQMLIABBNzsBAAwMC0E3IQgLIAAgCDsBAAwKCyACQQE6AAcgAiAB
QQNxOgAGIAJBBmohAwsgACACLQAAOgAHIAAgAi0AAToABiAAIAItAAI6AAUgACACLQADOgAEIAAgAi0ABDoAAyAAIAItAAU6AAIgAEEAOwEAIAAgAy8AADsB
CAwICyADIAQQioCAgAAACyAEIAMQioCAgAAACyADIAQQioCAgAAACyAEIAMQioCAgAAACyADIAQQioCAgAAACyAEIAMQioCAgAAACyADIAQQioCAgAAACyAE
IAMQioCAgAAACyACQRBqJICAgIAAC+4DAQl/AkACQAJAAkAgAUH//wNxIgINAEE3IQMgAC8BuAENASAALQCkAUH/AXENASAALQCsAUH/AXENAUEAIQEgAC0A
tAFB/wFxDQEMAwtBNyEDIAAoAsQBKAIAIgQoAkggAk0NACAEKAIQIgUgBCgCQGoiBCAFSQ0BIARBA3ENASAERQ0BIAQgAkEkbGoiAi8BIEUNACACLwEYIAAv
AbgBRw0AIAItAAQiBCAALQCkAUcNACACQRJqLQAAIQYgAi8BECEHIAJBCmotAAAhCCACLwEIIQkgAi8BACACQQJqLQAAQRB0ciEFIAItABQhCiACLQAMIQIC
QAJAAkAgBA4DAgABBAsgAC0AoAEgBUH/AXFGDQEMAgsgBUH///8HcSAALwGgASAAQaIBai0AAEEQdHJHDQELIAIgAC0ArAFHDQAgCSAIQRB0ciEEAkACQAJA
IAIOAwIAAQQLIAAtAKgBIARB/wFxRg0BDAILIARB////B3EgAC8BqAEgAEGqAWotAABBEHRyRw0BCyAKIAAtALQBRw0AIAcgBkEQdHIhAgJAAkAgCg4DBAAB
AwsgAC0AsAEgAkH/AXFGDQMMAQsgAkH///8HcSAALwGwASAAQbIBai0AAEEQdHJGDQILIAMPCwALIAAgATsB1AFBAAv8BQMGfwF+A38jgICAgABBEGsiAiSA
gICAAEEAIQMgAEEANgLAASAAQQA7AdYBAkAgAUH//wNxIgRFDQBBNyEDIAAoAsQBKAIAIgUoArQBIARNDQACQAJAIAUoAhAiBiAFKAKsAWoiByAGSQ0AIAdB
A3ENACAHRQ0AIAcgBEEcbGoiBy8BGEUNAkEZIQMgACkDmAEiCKdBFEECQQAgCEIgiKcoAgARgICAgACAgICAACIERQ0CIARCqtWq1arVqtWqfzcAACAEQRBq
QarVqtV6NgAAIARBCGpCqtWq1arVqtWqfzcAACAEQQNxDQAgBSgCECIJIAcoAgxqIgYgCUkNACAGRQ0AIAApA5gBIghCIIinIQogCKchCwJAAkACQEEADQAC
QCAHKAIQIgkNAEF/IQoMAwsgCyAJQQBBACAKKAIAEYCAgIAAgICAgAAiCg0BIABBnAFqKAIAIQogACgCmAEhCwsgCyAEQRRBAkEAIApBDGooAgARgYCAgACA
gICAAAwECyAJRQ0AIApBqgEgCfwLAAsCQCAKIAYgCWpPDQAgCiAJaiAGSw0BCwJAIAlFDQAgCiAGIAn8CgAACyAEIAk2AhAgBCAKNgIMIAcoAgAhBgJAIAct
AAhBAXFFDQAgBCAGNgIAIARBAToACAwCCyAHKAIEIQcgBEEAOgAIIAUoAhAiCSAGaiIFIAlJDQAgBUUNACAAKQOYASIIQiCIpyEGIAinIQkCQAJAAkBBAA0A
AkAgBw0AQX8hBgwDCyAJIAdBAEEAIAYoAgARgICAgACAgICAACIGDQEgAEGcAWooAgAhBiAAKAKYASEJCyAJIARBFEECQQAgBkEMaigCABGBgICAAICAgIAA
DAQLIAdFDQAgBkGqASAH/AsACwJAIAYgBSAHak8NACAGIAdqIAVLDQELAkAgB0UNACAGIAUgB/wKAAALIAQgBzYCBCAEIAY2AgAMAQsACyAAIAQ2AsABIAAg
ATsB1gFBACEDCyACQRBqJICAgIAAIAML+gMBB38jgICAgABBMGsiAiSAgICAACAAQQA7ARAgAiABKAIIIgNBAWoiBEUiBToALAJAAkACQAJAAkACQCAERQ0A
QTchBiAEIAEoAgRLDQEgBQ0DIAEgBDYCCEEAKAKc+cmAACIFQTcgASgCACADai0AACIGQQFGG0EAKAKY+cmAACIHIAYbIgZB//8DcQ0BQdj6yYAAIQMgBkGA
gARxDQIMBAtBNyEGCyAAIAY7ARAMAwsgAkEMaiABEOmEgIAAAkAgAi8BECIERQ0AIAAgBDsBEAwDCyACIAItAA46ABYgAiACLwEMOwEUIAJBAToAGCABKAII
IQQgAkEUaiEDDAELIAMgBBCKgICAAAALIAAgAykCADcCACACIARBAWoiBkUiCDoALAJAAkACQAJAAkAgBkUNAEE3IQMgBiABKAIESw0BIAgNBCABIAY2Aggg
BUE3IAEoAgAgBGotAAAiBEEBRhsgByAEGyIDQf//A3ENAUHY+smAACEEIANBgIAEcUUNAyACQRxqIAEQ6YSAgAAgAi8BICIBRQ0CIAAgATsBEAwFC0E3IQML
IAAgAzsBEAwDCyACIAItAB46ACYgAiACLwEcOwEkIAJBAToAKCACQSRqIQQLIAAgBCkCADcCCAwBCyAEIAYQioCAgAAACyACQTBqJICAgIAAC6MCAQZ/I4CA
gIAAQRBrIgIkgICAgAAgAEEAOwEEIAIgASgCCCIDQQFqIgRFIgU6AAQCQAJAAkAgBEUNACAEIAEoAgQiBksNACAFRQ0BIAMgBBCKgICAAAALIABBNzsBBAwB
CyABIAQ2AgggACABKAIAIgUgA2otAAA6AAAgAiAEQQFqIgNFIgc6AAgCQAJAIANFDQAgAyAGSw0AIAdFDQEgBCADEIqAgIAAAAsgAEE3OwEEDAELIAEgAzYC
CCAAIAUgBGotAAA6AAEgAiADQQFqIgRFIgc6AAwCQAJAIARFDQAgBCAGSw0AIAdFDQEgAyAEEIqAgIAAAAsgAEE3OwEEDAELIAEgBDYCCCAAIAUgA2otAAA6
AAILIAJBEGokgICAgAAL5AIBBn8jgICAgABBEGsiAiSAgICAACACIAEoAggiA0EBaiIERSIFOgAIAkACQAJAIARFDQAgBCABKAIEIgZLDQAgBUUNASADIAQQ
ioCAgAAACyAAQTc7AQgMAQsgASAENgIIAkACQAJAAkACQAJAIAEoAgAiBSADai0AAA4DAAEFAgsgAEIANwIAIABBCGpBADYCAAwFCyAAQQE6AAQgAEEAOwEI
IAIgBEEBaiIDRSIHOgAMIANFDQEgAyAGSw0BIAdFDQIgBCADEIqAgIAAAAsgAEEIakEAKALE+cmAADYCACAAQQApArz5yYAANwIADAMLIABBNzsBCAwCCyAB
IAM2AgggACAFIARqLQAAOgAADAELIABBAjoABCAAQQA7AQggAiABEOmEgIAAAkAgAi8BBCIBRQ0AIAAgATsBCAwBCyAAIAIvAQA7AQAgAEECaiACLQACOgAA
CyACQRBqJICAgIAAC90/AwR/AX4EfyOAgICAAEHAEWsiASSAgICAAAJAAkACQCAADQBBASECDAELIAFBEGpBACgC4PnJgAA2AgAgAUEAKQLY+cmAADcDCCAB
QQA6ACgCQEEBRQ0AAkACQAJAIAFBCGogAEGwG2oiAykDAEHAABCCgoCAACICQf//A3ENACABKAIMIgRBAWoiAkUNBSACIAEoAhBLDQUgASACNgIMIAEoAggg
BGoiAiABQZgRakEBak8NASABQZgRaiACQQFqTw0BDAULIAEoAhAiBEUNASADKQMAIQUgASgCCCEGAkAgBEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIg
iKcoAgwRgYCAgACAgICAAAwBCyACQQE6AAAgAC8B+hEhByABIAEoAgwiBEECaiIGIARJIgg6AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIMAQsCQCABKAIQ
IgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJIQgLIAhBAXEN
BCAGIAEoAhBLDQQgASAGNgIMAkAgASgCCCAEaiICIAFBKGpBAmpPDQAgAUEoaiACQQJqSQ0FCyACIAc7AAAgAC8B+BEhByABIAEoAgwiBEECaiIGIARJIgg6
AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIMAQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKC
gIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJIQgLIAhBAXENBSAGIAEoAhBLDQUgASAGNgIMAkAgASgCCCAEaiICIAFBKGpBAmpPDQAgAUEoaiACQQJqSQ0G
CyACIAc7AABBASEJQQEhCAJAIAAtACRBAnFFDQBBAkEBIAAoAiAiAhshCCACRSEJCyABIAEoAgwiB0EBaiIGRSIEOgAoIAEgBDoAmBECQAJAIAYNAEEZIQIM
AQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiB0EBaiIGRSEE
CyAEQQFxDQYgBiABKAIQSw0GIAEgBjYCDAJAIAEoAgggB2oiAiABQfgQakEBak8NACABQfgQaiACQQFqSQ0HCyACIAg6AAAgAC0AKCEHIAEgASgCDCIIQQFq
IgZFIgQ6ACggASAEOgCYEQJAAkAgBg0AQRkhAgwBCwJAIAEoAhAiAiAGTw0AA0BBfyACQQF2IAJqQcAAaiIEIAQgAkkbIgIgBkkNAAsgAUEIaiADKQMAIAIQ
goKAgAAiAkH//wNxDQEgASgCDCIIQQFqIgZFIQQLIARBAXENByAGIAEoAhBLDQcgASAGNgIMAkAgASgCCCAIaiICIAFB+BBqQQFqTw0AIAFB+BBqIAJBAWpJ
DQgLIAIgB0EBcToAACAALQAkQQFxRQ0HIAAoAhwiAkUNBwJAIAFBCGogA0EAIAIQ7ISAgAAiAkH//wNxRQ0AIAEoAhAiBEUNBSADKQMAIQUgASgCCCEGAkAg
BEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwFCwJAIAkNACAALQAkQQJxRQ0IIAAoAiAiAkUNCCABQQhqIANBASACEOyE
gIAAIgJB//8DcUUNACABKAIQIgRFDQUgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMBQsg
AC8B/BEhByABIAEoAgwiBEECaiIGIARJIgg6AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIMAQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAE
IAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJIQgLIAhBAXENCCAGIAEoAhBLDQggASAGNgIMAkAgASgCCCAE
aiICIAFBKGpBAmpPDQAgAUEoaiACQQJqSQ0JCyACIAc7AAAgAC8B/hEhByABIAEoAgwiBEECaiIGIARJIgg6AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIM
AQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJ
IQgLIAhBAXENCSAGIAEoAhBLDQkgASAGNgIMAkAgASgCCCAEaiICIAFBKGpBAmpPDQAgAUEoaiACQQJqSQ0KCyACIAc7AAAgAC8BgBIhByABIAEoAgwiBEEC
aiIGIARJIgg6AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIMAQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGog
AykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJIQgLIAhBAXENCiAGIAEoAhBLDQogASAGNgIMAkAgASgCCCAEaiICIAFBKGpBAmpPDQAgAUEo
aiACQQJqSQ0LCyACIAc7AAAgAC8BghIhByABIAEoAgwiBEECaiIGIARJIgg6AJgRIAEgCDoA+BACQAJAIAYgBE8NAEEZIQIMAQsCQCABKAIQIgIgBk8NAANA
QX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiBEECaiIGIARJIQgLIAhBAXENCyAGIAEoAhBL
DQsgASAGNgIMAkAgASgCCCAEaiICIAFBKGpBAmpPDQAgAUEoaiACQQJqSQ0MCyACIAc7AAAgADUCACEFIAEgAEEEajEAADwALCABIAU+AiggASABKAIMIgRB
BWoiBiAESSIIOgCYESABIAg6APgQAkACQCAGIARJDQACQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACAC
EIKCgIAAIgJB//8DcQ0CIAEoAgwiBEEFaiIGIARJIQgLIAhBAXENDSAGIAEoAhBLDQ0gASAGNgIMAkAgASgCCCAEaiICIAFBKGpBBXJPDQAgAUEoaiACQQVq
SQ0OCyACIAEoAig2AAAgAkEEaiABLQAsOgAAIAA1AgghBSABIABBDGoxAAA8ACwgASAFPgIoIAEgASgCDCIEQQVqIgYgBEkiCDoAmBEgASAIOgD4ECAGIARJ
DQACQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0CIAEoAgwiBEEFaiIGIARJ
IQgLIAhBAXENDSAGIAEoAhBLDQ0gASAGNgIMAkAgASgCCCAEaiICIAFBKGpBBXJPDQAgAUEoaiACQQVqSQ0OCyACIAEoAig2AAAgAkEEaiABLQAsOgAAIAA1
AhAhBSABIABBFGoxAAA8ACwgASAFPgIoIAEgASgCDCIEQQVqIgYgBEkiCDoAmBEgASAIOgD4ECAGIARJDQACQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHA
AGoiBCAEIAJJGyICIAZJDQALIAFBCGogAykDACACEIKCgIAAIgJB//8DcQ0CIAEoAgwiBEEFaiIGIARJIQgLIAhBAXENDSAGIAEoAhBLDQ0gASAGNgIMAkAg
ASgCCCAEaiICIAFBKGpBBXJPDQAgAUEoaiACQQVqSQ0OCyACIAEoAig2AAAgAkEEaiABLQAsOgAAIABBmAFqIQICQEHQEEUNACABQShqIAJB0BD8CgAACwJA
AkAgAUEIaiADIAIQ7YSAgAAiAkH//wNxDQAgAUEIaiADIABBqAFqEO2EgIAAIgJB//8DcQ0AIAFBCGogAyAAQbgBahDthICAACICQf//A3ENAEEwIQQCQANA
IAFBCGogAyABQShqIARqIgIvAQAgAkECai0AAEEQdHIQ7oSAgAAiAkH//wNxDQEgBEGsCEchBiAEQQRqIQQgBg0ACwsgAkH//wNxDQBBsAghBAJAA0AgAUEI
aiADIAFBKGogBGoiAi8BACACQQJqLQAAQRB0chDuhICAACICQf//A3ENASAEQawQRyEGIARBBGohBCAGDQALCyACQf//A3ENACABQfgQakEYakIANwMAIAFB
+BBqQRBqQgA3AwAgAUH4EGpBCGpCADcDACABQgA3A/gQIABByBFqIQRBACECA0AgAUGYEWpBGGogBEEYaikCADcDACABQZgRakEQaiAEQRBqKQIANwMAIAFB
mBFqQQhqIARBCGopAgA3AwAgASAEKQIANwOYEQJAIAFBmBFqIAJBA3YiBkH8////AXFqKAIAIAJ2QQFxRQ0AIAFB+BBqIAZqIgYgBi0AAEEBIAJBB3F0cjoA
AAsgAkEBaiICQYACRw0ACyABQQhqIAMgAUH4EGpBIBDvhICAACICQf//A3FFDQELIAEoAhAiBEUNCyADKQMAIQUgASgCCCEGAkAgBEUNACAGQaoBIAT8CwAL
IAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwLCwJAQcwARQ0AIAFBKGogAEEsakHMAPwKAAALIAAoAiwhByABIAEoAgwiBEEEaiIGIARJIgg6AL8R
IAEgCDoA+BACQAJAAkAgAS0AvxFBAXFFDQBBGSECDAELAkAgASgCECICIAZPDQADQEF/IAJBAXYgAmpBwABqIgQgBCACSRsiAiAGSQ0ACyABQQhqIAMpAwAg
AhCCgoCAACICQf//A3ENASABKAIMIgRBBGoiBiAESSEICyAIQQFxDQ8gBiABKAIQSw0PIAEgBjYCDAJAIAEoAgggBGoiAiABQZgRakEEak8NACABQZgRaiAC
QQRqSQ0QCyACIAc2AAAgAUEIaiADIAFBNGpBwAAQ74SAgAAiAkH//wNxDQAgAUEIaiADIAAoAjAgACgCNBDvhICAACICQf//A3FFDQELIAEoAhAiBEUNCyAD
KQMAIQUgASgCCCEGAkAgBEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwLCyAALQDsESEHIAEgASgCDCIIQQFqIgZFIgQ6
ACggASAEOgCYEQJAAkAgBkUNAAJAIAEoAhAiAiAGTw0AA0BBfyACQQF2IAJqQcAAaiIEIAQgAkkbIgIgBkkNAAsgAUEIaiADKQMAIAIQgoKAgAAiAkH//wNx
DQIgASgCDCIIQQFqIgZFIQQLIARBAXENDyAGIAEoAhBLDQ8gASAGNgIMAkAgASgCCCAIaiICIAFB+BBqQQFqTw0AIAFB+BBqIAJBAWpJDRALIAIgB0EARzoA
AAJAIAAtAOwRRQ0AIABB6hFqLQAAIQcgAC8B6BEhCSABIAEoAgwiBEEEaiIGIARJIgg6AJgRIAEgCDoA+BAgBiAESQ0BAkAgASgCECICIAZPDQADQEF/IAJB
AXYgAmpBwABqIgQgBCACSRsiAiAGSQ0ACyABQQhqIAMpAwAgAhCCgoCAACICQf//A3ENAyABKAIMIgRBBGoiBiAESSEICyAIQQFxDRAgBiABKAIQSw0QIAEg
BjYCDAJAIAEoAgggBGoiAiABQShqQQRqTw0AIAFBKGogAkEEakkNEQsgAiAJIAdBEHRyNgAACyAALQCEEiEHIAEgASgCDCIIQQFqIgZFIgQ6ACggASAEOgCY
EQJAAkAgBg0AQRkhAgwBCwJAIAEoAhAiAiAGTw0AA0BBfyACQQF2IAJqQcAAaiIEIAQgAkkbIgIgBkkNAAsgAUEIaiADKQMAIAIQgoKAgAAiAkH//wNxDQEg
ASgCDCIIQQFqIgZFIQQLIARBAXENECAGIAEoAhBLDRAgASAGNgIMAkAgASgCCCAIaiICIAFB+BBqQQFqTw0AIAFB+BBqIAJBAWpJDRELIAIgB0EBcToAACAA
KALwESEHIAEgASgCDCIEQQRqIgYgBEkiCDoAmBEgASAIOgD4EAJAAkAgBiAETw0AQRkhAgwBCwJAIAEoAhAiAiAGTw0AA0BBfyACQQF2IAJqQcAAaiIEIAQg
AkkbIgIgBkkNAAsgAUEIaiADKQMAIAIQgoKAgAAiAkH//wNxDQEgASgCDCIEQQRqIgYgBEkhCAsgCEEBcQ0RIAYgASgCEEsNESABIAY2AgwCQCABKAIIIARq
IgIgAUEoakEEak8NACABQShqIAJBBGpJDRILIAIgBzYAACAAQfYRai0AACEHIAAvAfQRIQkgASABKAIMIghBAWoiBkUiBDoAKCABIAQ6AJgRAkACQCAGDQBB
GSECDAELAkAgASgCECICIAZPDQADQEF/IAJBAXYgAmpBwABqIgQgBCACSRsiAiAGSQ0ACyABQQhqIAMpAwAgAhCCgoCAACICQf//A3ENASABKAIMIghBAWoi
BkUhBAsgBEEBcQ0SIAYgASgCEEsNEiABIAY2AgwCQCABKAIIIAhqIgIgAUH4EGpBAWpPDQAgAUH4EGogAkEBakkNEwsgAiAJIAdBEHRyQQNxOgAAIAAtAPQR
IQcgASABKAIMIghBAWoiBkUiBDoAKCABIAQ6AJgRAkACQCAGDQBBGSECDAELAkAgASgCECICIAZPDQADQEF/IAJBAXYgAmpBwABqIgQgBCACSRsiAiAGSQ0A
CyABQQhqIAMpAwAgAhCCgoCAACICQf//A3ENASABKAIMIghBAWoiBkUhBAsgBEEBcQ0TIAYgASgCEEsNEyABIAY2AgwCQCABKAIIIAhqIgIgAUH4EGpBAWpP
DQAgAUH4EGogAkEBakkNFAsgAiAHQQJ2QQFxOgAAIAAtAPQRIQcgASABKAIMIghBAWoiBkUiBDoAKCABIAQ6AJgRAkACQCAGDQBBGSECDAELAkAgASgCECIC
IAZPDQADQEF/IAJBAXYgAmpBwABqIgQgBCACSRsiAiAGSQ0ACyABQQhqIAMpAwAgAhCCgoCAACICQf//A3ENASABKAIMIghBAWoiBkUhBAsgBEEBcQ0UIAYg
ASgCEEsNFCABIAY2AgwCQCABKAIIIAhqIgIgAUH4EGpBAWpPDQAgAUH4EGogAkEBakkNFQsgAiAHQQN2QQdxOgAAIABB9hFqLQAAIQcgAC8B9BEhCSABIAEo
AgwiCEEBaiIGRSIEOgAoIAEgBDoAmBECQAJAIAYNAEEZIQIMAQsCQCABKAIQIgIgBk8NAANAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALIAFBCGog
AykDACACEIKCgIAAIgJB//8DcQ0BIAEoAgwiCEEBaiIGRSEECyAEQQFxDRUgBiABKAIQSw0VIAEgBjYCDAJAIAEoAgggCGoiAiABQfgQakEBak8NACABQfgQ
aiACQQFqSQ0WCyACIAkgB0EQdHJBBnZBB3E6AAAgAEH2EWotAAAhByAALwH0ESEJIAEgASgCDCIIQQFqIgZFIgQ6ACggASAEOgCYEQJAAkAgBg0AQRkhAgwB
CwJAIAEoAhAiAiAGTw0AA0BBfyACQQF2IAJqQcAAaiIEIAQgAkkbIgIgBkkNAAsgAUEIaiADKQMAIAIQgoKAgAAiAkH//wNxDQEgASgCDCIIQQFqIgZFIQQL
IARBAXENFiAGIAEoAhBLDRYgASAGNgIMAkAgASgCCCAIaiICIAFB+BBqQQFqTw0AIAFB+BBqIAJBAWpJDRcLIAIgCSAHQRB0ckEJdkEDcToAAAJAAkACQAJA
IABBhAFqKAIAIgQNAEEAIQJBACEEDAELIABBgAFqKAIAIgIgBEF/aiIEai0AACIGDQELAkAgAUEIaiADIAJBot7BgAAgAhsgBEEAIAIbEO+EgIAAIgJB//8D
cUUNACABKAIQIgRFDRYgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMFgsCQAJAIABBkAFq
KAIAIgQNAEEAIQJBACEEDAELIABBjAFqKAIAIgIgBEF/aiIEai0AACIGDQILAkAgAUEIaiADIAJBot7BgAAgAhsgBEEAIAIbEO+EgIAAIgJB//8DcUUNACAB
KAIQIgRFDRYgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMFgsgAUEYaiABQQhqIAMQuICA
gAACQCABLwEgIgJFDQAgASgCECIERQ0WIAMpAwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADBYL
IAEoAhwhBiABKAIYIQhBACEEIABBADYC6C4gAUEAOgAoIAFBADoAmBECQAJAAkBBAQ0AQQIhAgwBCwJAAkAgACgC7C4iAiAGSQ0AIAYhAkEAIQMMAQsgAEHk
LmohBwNAQX8gAkEBdiACakHAAGoiBCAEIAJJGyICIAZJDQALAkAgByADKQMAIAIQgoKAgABB//8DcUUNAEECIQIMAwsgACgC6C4iAyAGaiICIANJIQQLIARB
AXENGiACIAAoAuwuSw0aIAAgAjYC6C4CQCAAKALkLiADaiICIAggBmpPDQAgCCACIAZqSQ0bCwJAIAZFDQAgAiAIIAb8CgAAC0EAIQILIAZFDRgLIABBtBtq
KAIAIQQgACgCsBshAAJAIAZFDQAgCEGqASAG/AsACyAAIAggBkEAQQAgBCgCDBGBgICAAICAgIAADBcLIAYQg4KAgAAACyAGEIOCgIAAAAsgASgCECIERQ0S
IAMpAwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADBILIAEoAhAiBEUNESADKQMAIQUgASgCCCEG
AkAgBEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwRCyABKAIQIgRFDRAgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE
/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMEAsgASgCECIERQ0PIAMpAwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBB
ACAFQiCIpygCDBGBgICAAICAgIAADA8LIAEoAhAiBEUNDiADKQMAIQUgASgCCCEGAkAgBEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCA
gACAgICAAAwOCyABKAIQIgRFDQ0gAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMDQsgASgC
ECIERQ0MIAMpAwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADAwLQRkhAgsgASgCECIERQ0KIAMp
AwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADAoLQRkhAgsgASgCECIERQ0IIAMpAwAhBSABKAII
IQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADAgLIAEoAhAiBEUNByADKQMAIQUgASgCCCEGAkAgBEUNACAGQaoB
IAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwHCyABKAIQIgRFDQYgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARB
AEEAIAVCIIinKAIMEYGAgIAAgICAgAAMBgsgASgCECIERQ0FIAMpAwAhBSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGB
gICAAICAgIAADAULIAEoAhAiBEUNBCADKQMAIQUgASgCCCEGAkAgBEUNACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwECyAB
KAIQIgRFDQMgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAFpyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAAMAwsgASgCECIERQ0CIAMpAwAh
BSABKAIIIQYCQCAERQ0AIAZBqgEgBPwLAAsgBacgBiAEQQBBACAFQiCIpygCDBGBgICAAICAgIAADAILIAEoAhAiBEUNASADKQMAIQUgASgCCCEGAkAgBEUN
ACAGQaoBIAT8CwALIAWnIAYgBEEAQQAgBUIgiKcoAgwRgYCAgACAgICAAAwBCyABKAIQIgRFDQAgAykDACEFIAEoAgghBgJAIARFDQAgBkGqASAE/AsACyAF
pyAGIARBAEEAIAVCIIinKAIMEYGAgIAAgICAgAALIAJB//8DcUEZRg0AQQQhAgwBC0ECIQILIAFBwBFqJICAgIAAIAIPCwALyVoDA38Bfgh/I4CAgIAAQdAA
ayIEJICAgIAAIARBEGpBACgC4PnJgAA2AgAgBEEAKQLY+cmAADcDCCAEQQA6ABgCQAJAQQENAEEZIQUMAQsCQAJAAkAgBEEIaiABKQIAQcAAEIKCgIAAIgVB
//8DcQ0AIAQoAgwiBUEBaiIGRQ0CIAYgBCgCEEsNAiAEIAY2AgwgBCgCCCAFaiIGIARByABqQQFqTw0BIARByABqIAZBAWpPDQEMAgsgBCgCECIGRQ0CIAEp
AgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAILIAZBAToAACADNQJgIQcgBCAEKAIMIgVBCGoi
CCAFSSIJOgBIIAQgCToATAJAAkAgCCAFTw0AQRkhBQwBCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIA
IAYQgoKAgAAiBUH//wNxDQEgBCgCDCIFQQhqIgggBUkhCQsgCUEBcQ0BIAggBCgCEEsNASAEIAg2AgwCQCAEKAIIIAVqIgYgBEEYakEIak8NACAEQRhqIAZB
CGpJDQILIAYgBzcAAAJAAkAgAygCVCIFDQBBACEGDAELQQAhBgNAIAZBAWoiBkUNAyAFKAIMIgUNAAsLIAQgBCgCDCIIQQRqIgkgCEkiCjoASCAEIAo6AEwC
QAJAIAkgCE8NAEEZIQUMAQsCQCAEKAIQIgUgCU8NAANAQX8gBUEBdiAFakHAAGoiCCAIIAVJGyIFIAlJDQALIARBCGogASkCACAFEIKCgIAAIgVB//8DcQ0B
IAQoAgwiCEEEaiIJIAhJIQoLIApBAXENAiAJIAQoAhBLDQIgBCAJNgIMAkAgBCgCCCAIaiIFIARBGGpBBGpPDQAgBEEYaiAFQQRqSQ0DCyAFIAY2AAACQAJA
AkAgAygCVCIJRQ0AIARBzABqQQFqIQsgBEEYakEEaiEMIARBGGpBAmohCgJAAkACQAJAAkACQAJAAkADQCAJKAIUIQ0gBCAEKAIMIgVBBGoiCCAFSSIOOgBI
IAQgDjoATAJAIAggBU8NAEEZIQUMDAsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB
//8DcQ0MIAQoAgwiBUEEaiIIIAVJIQ4LIA5BAXENDiAIIAQoAhBLDQ4gBCAINgIMAkAgBCgCCCAFaiIGIAxPDQAgBEEYaiAGQQRqSQ0PCyAGIA02AAAgCS8B
qAIhDSAEIAQoAgwiBUECaiIIIAVJIg46AEggBCAOOgBMAkAgCCAFTw0AQRkhBQwLCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYg
CEkNAAsgBEEIaiABKQIAIAYQgoKAgAAiBUH//wNxDQsgBCgCDCIFQQJqIgggBUkhDgsgDkEBcQ0OIAggBCgCEEsNDiAEIAg2AgwCQCAEKAIIIAVqIgYgCk8N
ACAEQRhqIAZBAmpJDQ8LIAYgDTsAACAJLwGqAiENIAQgBCgCDCIFQQJqIgggBUkiDjoASCAEIA46AEwCQCAIIAVPDQBBGSEFDAkLAkAgBCgCECIGIAhPDQAD
QEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENCSAEKAIMIgVBAmoiCCAFSSEOCyAOQQFxDQ4gCCAEKAIQ
Sw0OIAQgCDYCDAJAIAQoAgggBWoiBiAKTw0AIARBGGogBkECakkNDwsgBiANOwAAIAkvAaACIQ0gBCAEKAIMIgVBAmoiCCAFSSIOOgBIIAQgDjoATAJAIAgg
BU8NAEEZIQUMCAsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0IIAQoAgwi
BUECaiIIIAVJIQ4LIA5BAXENDiAIIAQoAhBLDQ4gBCAINgIMAkAgBCgCCCAFaiIGIApPDQAgBEEYaiAGQQJqSQ0PCyAGIA07AAAgCS8BogIhDSAEIAQoAgwi
BUECaiIIIAVJIg46AEggBCAOOgBMAkAgCCAFTw0AQRkhBQwHCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiAB
KQIAIAYQgoKAgAAiBUH//wNxDQcgBCgCDCIFQQJqIgggBUkhDgsgDkEBcQ0OIAggBCgCEEsNDiAEIAg2AgwCQCAEKAIIIAVqIgYgCk8NACAEQRhqIAZBAmpJ
DQ8LIAYgDTsAACAJLwGkAiENIAQgBCgCDCIFQQJqIgggBUkiDjoASCAEIA46AEwCQCAIIAVPDQBBGSEFDAYLAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpB
wABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENBiAEKAIMIgVBAmoiCCAFSSEOCyAOQQFxDQ4gCCAEKAIQSw0OIAQgCDYCDAJA
IAQoAgggBWoiBiAKTw0AIARBGGogBkECakkNDwsgBiANOwAAIAkvAaYCIQ0gBCAEKAIMIgVBAmoiCCAFSSIOOgBIIAQgDjoATAJAIAggBU8NAEEZIQUMBQsC
QCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0FIAQoAgwiBUECaiIIIAVJIQ4L
IA5BAXENDiAIIAQoAhBLDQ4gBCAINgIMAkAgBCgCCCAFaiIGIApPDQAgBEEYaiAGQQJqSQ0PCyAGIA07AAAgCSgCmAIhDSAEIAQoAgwiBUEEaiIIIAVJIg46
AEggBCAOOgBMAkAgCCAFTw0AQRkhBQwECwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKAgAAi
BUH//wNxDQQgBCgCDCIFQQRqIgggBUkhDgsgDkEBcQ0OIAggBCgCEEsNDiAEIAg2AgwCQCAEKAIIIAVqIgYgDE8NACAEQRhqIAZBBGpJDQ8LIAYgDTYAACAJ
KAKcAiENIAQgBCgCDCIFQQRqIgggBUkiDjoASCAEIA46AEwCQCAIIAVPDQBBGSEFDAMLAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsi
BiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENAyAEKAIMIgVBBGoiCCAFSSEOCyAOQQFxDQ4gCCAEKAIQSw0OIAQgCDYCDAJAIAQoAgggBWoiBiAM
Tw0AIARBGGogBkEEakkNDwsgBiANNgAAIAktAKwCIQ0gBCAEKAIMIg5BAWoiCEUiBToAGCAEIAU6AEgCQCAIDQBBGSEFDAILAkAgBCgCECIGIAhPDQADQEF/
IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENAiAEKAIMIg5BAWoiCEUhBQsgBUEBcQ0OIAggBCgCEEsNDiAE
IAg2AgwCQCAEKAIIIA5qIgYgC08NACAEQcwAaiAGQQFqSQ0PCyAGIA06AAAgCSgCECEPIAQgBCgCDCIFIAkoAhQiDmoiCCAFSSINOgAYIAQgDToASAJAAkAg
CCAFTw0AQRkhBQwBCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKAgAAiBUH//wNxDQEgBCgC
DCIFIA5qIgggBUkhDQsgDUEBcQ0PIAggBCgCEEsNDyAEIAg2AgwCQCAEKAIIIAVqIgYgDyAOak8NACAPIAYgDmpJDRALAkAgDkUNACAGIA8gDvwKAAALIAko
AgwiCQ0BDAoLCyAEKAIQIgZFDQ4gASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMDgsgBCgC
ECIGRQ0NIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADA0LIAQoAhAiBkUNDCABKQIAIQcg
BCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwMCyAEKAIQIgZFDQsgASkCACEHIAQoAgghCAJAIAZFDQAg
CEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMCwsgBCgCECIGRQ0KIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cg
CCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAoLIAQoAhAiBkUNCSABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKco
AgwRgYCAgACAgICAAAwJCyAEKAIQIgZFDQggASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAM
CAsgBCgCECIGRQ0HIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAcLIAQoAhAiBkUNBiAB
KQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwGCyADLwHQASEKIAQgBCgCDCIFQQJqIgggBUki
CToASCAEIAk6AEwCQAJAIAggBU8NAEEZIQUMAQsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKC
gIAAIgVB//8DcQ0BIAQoAgwiBUECaiIIIAVJIQkLIAlBAXENBSAIIAQoAhBLDQUgBCAINgIMAkAgBCgCCCAFaiIGIARBGGpBAmpPDQAgBEEYaiAGQQJqSQ0G
CyAGIAo7AAAgAy8B0gEhCiAEIAQoAgwiBUECaiIIIAVJIgk6AEggBCAJOgBMAkACQCAIIAVPDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpB
wABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIgVBAmoiCCAFSSEJCyAJQQFxDQYgCCAEKAIQSw0GIAQgCDYCDAJA
IAQoAgggBWoiBiAEQRhqQQJqTw0AIARBGGogBkECakkNBwsgBiAKOwAAIAMtANgBIQogBCAEKAIMIglBAWoiCEUiBToAGCAEIAU6AEgCQAJAIAgNAEEZIQUM
AQsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0BIAQoAgwiCUEBaiIIRSEF
CyAFQQFxDQcgCCAEKAIQSw0HIAQgCDYCDAJAIAQoAgggCWoiBiAEQcwAakEBak8NACAEQcwAaiAGQQFqSQ0ICyAGIApBA3E6AAAgAy0A2QEhCiAEIAQoAgwi
CUEBaiIIRSIFOgAYIAQgBToASAJAAkAgCA0AQRkhBQwBCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIA
IAYQgoKAgAAiBUH//wNxDQEgBCgCDCIJQQFqIghFIQULIAVBAXENCCAIIAQoAhBLDQggBCAINgIMAkAgBCgCCCAJaiIGIARBzABqQQFqTw0AIARBzABqIAZB
AWpJDQkLIAYgCjoAACADLQDaASEKIAQgBCgCDCIJQQFqIghFIgU6ABggBCAFOgBIAkACQCAIDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpB
wABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIglBAWoiCEUhBQsgBUEBcQ0JIAggBCgCEEsNCSAEIAg2AgwCQCAE
KAIIIAlqIgYgBEHMAGpBAWpPDQAgBEHMAGogBkEBakkNCgsgBiAKOgAAAkAgBEEIaiABIANBoAFqEPCEgIAAIgVB//8DcUUNACAEKAIQIgZFDQsgASkCACEH
IAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMCwsgAy8B1AEhCiAEIAQoAgwiBUECaiIIIAVJIgk6AEgg
BCAJOgBMAkACQCAIIAVPDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIF
Qf//A3ENASAEKAIMIgVBAmoiCCAFSSEJCyAJQQFxDQogCCAEKAIQSw0KIAQgCDYCDAJAIAQoAgggBWoiBiAEQRhqQQJqTw0AIARBGGogBkECakkNCwsgBiAK
OwAAIAMtANsBIQogBCAEKAIMIglBAWoiCEUiBToAGCAEIAU6AEgCQAJAIAgNAEEZIQUMAQsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJ
GyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0BIAQoAgwiCUEBaiIIRSEFCyAFQQFxDQsgCCAEKAIQSw0LIAQgCDYCDAJAIAQoAgggCWoiBiAE
QcwAakEBak8NACAEQcwAaiAGQQFqSQ0MCyAGIApBA3E6AAAgAy0A3AEhCiAEIAQoAgwiCUEBaiIIRSIFOgAYIAQgBToASAJAAkAgCA0AQRkhBQwBCwJAIAQo
AhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKAgAAiBUH//wNxDQEgBCgCDCIJQQFqIghFIQULIAVBAXEN
DCAIIAQoAhBLDQwgBCAINgIMAkAgBCgCCCAJaiIGIARBzABqQQFqTw0AIARBzABqIAZBAWpJDQ0LIAYgCjoAACADKAK8ASEKIAQgBCgCDCIFQQRqIgggBUki
CToASCAEIAk6AEwCQAJAIAggBU8NAEEZIQUMAQsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKC
gIAAIgVB//8DcQ0BIAQoAgwiBUEEaiIIIAVJIQkLIAlBAXENDSAIIAQoAhBLDQ0gBCAINgIMAkAgBCgCCCAFaiIGIARBGGpBBGpPDQAgBEEYaiAGQQRqSQ0O
CyAGIAo2AAAgAy8B1gEhCiAEIAQoAgwiBUECaiIIIAVJIgk6AEggBCAJOgBMAkACQCAIIAVPDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpB
wABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIgVBAmoiCCAFSSEJCyAJQQFxDQ4gCCAEKAIQSw0OIAQgCDYCDAJA
IAQoAgggBWoiBiAEQRhqQQJqTw0AIARBGGogBkECakkNDwsgBiAKOwAAIAMtAIwCIQogBCAEKAIMIglBAWoiCEUiBToASCAEIAU6AEwCQAJAAkAgCA0AQRkh
BQwBCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKAgAAiBUH//wNxDQEgBCgCDCIJQQFqIghF
IQULIAVBAXENECAIIAQoAhBLDRAgBCAINgIMAkAgBCgCCCAJaiIGIARBywBqQQFqTw0AIARBywBqIAZBAWpJDRELIAYgCkEARzoAACADLQCMAkUNASAEQRhq
QRhqIANB4AFqIgZBGGopAgA3AwAgBEEYakEoaiAGQShqKAIANgIAIARBGGpBIGogBkEgaikCADcDACAEQRhqQRBqIAZBEGopAgA3AwAgBEEYakEIaiAGQQhq
KQIANwMAIAQgBikCADcDGCAELwE0IQogBCAEKAIMIgVBAmoiCCAFSSIJOgBMIAQgCToASwJAIAQtAEtBAXFFDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/
IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIgVBAmoiCCAFSSEJCyAJQQFxDRAgCCAEKAIQSw0Q
IAQgCDYCDAJAIAQoAgggBWoiBiAEQcgAakECak8NACAEQcgAaiAGQQJqSQ0RCyAGIAo7AAAgBC8BNiEKIAQgBCgCDCIFQQJqIgggBUkiCToATCAEIAk6AEsC
QCAIIAVPDQBBGSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAE
KAIMIgVBAmoiCCAFSSEJCyAJQQFxDRAgCCAEKAIQSw0QIAQgCDYCDAJAIAQoAgggBWoiBiAEQcgAakECak8NACAEQcgAaiAGQQJqSQ0RCyAGIAo7AAAgBEEI
aiABIARBGGoQ8ISAgAAiBUH//wNxDQAgBC0AOCEKIAQgBCgCDCIJQQFqIghFIgU6AEggBCAFOgBMAkAgCA0AQRkhBQwBCwJAIAQoAhAiBiAITw0AA0BBfyAG
QQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKAgAAiBUH//wNxDQEgBCgCDCIJQQFqIghFIQULIAVBAXENECAIIAQoAhBLDRAgBCAI
NgIMAkAgBCgCCCAJaiIGIARBywBqQQFqTw0AIARBywBqIAZBAWpJDRELIAYgCjoAACAELQA5IQogBCAEKAIMIglBAWoiCEUiBToASCAEIAU6AEwCQCAIDQBB
GSEFDAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIglBAWoi
CEUhBQsgBUEBcQ0QIAggBCgCEEsNECAEIAg2AgwCQCAEKAIIIAlqIgYgBEHLAGpBAWpPDQAgBEHLAGogBkEBakkNEQsgBiAKOgAAIAQtADohCiAEIAQoAgwi
CUEBaiIIRSIFOgBIIAQgBToATAJAIAgNAEEZIQUMAQsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAG
EIKCgIAAIgVB//8DcQ0BIAQoAgwiCUEBaiIIRSEFCyAFQQFxDRAgCCAEKAIQSw0QIAQgCDYCDAJAIAQoAgggCWoiBiAEQcsAakEBak8NACAEQcsAaiAGQQFq
SQ0RCyAGIAo6AAAgBEEIaiABIARBO2oQ8YSAgAAiBUH//wNxRQ0BCyAEKAIQIgZFDRAgASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZB
AEEAIAdCIIinKAIMEYGAgIAAgICAgAAMEAsCQCAEQQhqIAEgA0G1AmoQ8YSAgAAiBUH//wNxRQ0AIAQoAhAiBkUNECABKQIAIQcgBCgCCCEIAkAgBkUNACAI
QaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwQCyADLQC9AiEKIAQgBCgCDCIJQQFqIghFIgU6ABggBCAFOgBIAkACQCAIDQBBGSEF
DAELAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENASAEKAIMIglBAWoiCEUh
BQsgBUEBcQ0PIAggBCgCEEsNDyAEIAg2AgwCQCAEKAIIIAlqIgYgBEHMAGpBAWpPDQAgBEHMAGogBkEBakkNEAsgBiAKQQNxOgAAIAMtAMYCIQogBCAEKAIM
IglBAWoiCEUiBToASCAEIAU6AEwCQAJAIAhFDQACQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKC
gIAAIgVB//8DcQ0CIAQoAgwiCUEBaiIIRSEFCyAFQQFxDREgCCAEKAIQSw0RIAQgCDYCDAJAIAQoAgggCWoiBiAEQcsAakEBaiIMTw0AIARBywBqIAZBAWpJ
DRILIAYgCkEHcToAACAEIAMpAb4CNwMYQQAhCQNAIAQgBEEYaiAJai0AAEEfcSIOOgBKIAQgBCgCDCIKQQFqIghFIgU6AEggBCAFOgBMIAhFDQECQCAEKAIQ
IgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAFIAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0DIAQoAgwiCkEBaiIIRSEFCyAFQQFxDRIg
CCAEKAIQSw0SIAQgCDYCDAJAIAQoAgggCmoiBiAMTw0AIARBywBqIAZBAWpJDRMLIAYgDjoAACAJQQFqIglBCEcNAAsgAy0AxwIhCiAEIAQoAgwiCUEBaiII
RSIFOgAYIAQgBToASAJAAkAgCA0AQRkhBQwBCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiABKQIAIAYQgoKA
gAAiBUH//wNxDQEgBCgCDCIJQQFqIghFIQULIAVBAXENEiAIIAQoAhBLDRIgBCAINgIMAkAgBCgCCCAJaiIGIARBzABqQQFqTw0AIARBzABqIAZBAWpJDRML
IAYgCjoAAAJAAkACQAJAAkACQAJAIAMtAMgCDgQAAQIZAAsgBCAEKAIMIglBAWoiCEUiBToAGCAEIAU6AEgCQCAIDQBBGSEFDAQLAkAgBCgCECIGIAhPDQAD
QEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENBCAEKAIMIglBAWoiCEUhBQsgBUEBcQ0YIAggBCgCEEsN
GCAEIAg2AgwCQCAEKAIIIAlqIgYgBEEYakEBak8NACAEQRhqIAZBAWpJDRkLQQAhBSAGQQA6AAAMAwsgBCAEKAIMIglBAWoiCEUiBToAGCAEIAU6AEgCQCAI
DQBBGSEFDAMLAkAgBCgCECIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAEQQhqIAEpAgAgBhCCgoCAACIFQf//A3ENAyAEKAIMIglB
AWoiCEUhBQsgBUEBcQ0XIAggBCgCEEsNFyAEIAg2AgwCQCAEKAIIIAlqIgYgBEEYakEBak8NACAEQRhqIAZBAWpJDRgLIAZBAToAAAwBCyADLQDJAiEDIAQg
BCgCDCIJQQFqIghFIgU6ABggBCAFOgBIAkAgCA0AQRkhBQwDCwJAIAQoAhAiBiAITw0AA0BBfyAGQQF2IAZqQcAAaiIFIAUgBkkbIgYgCEkNAAsgBEEIaiAB
KQIAIAYQgoKAgAAiBUH//wNxDQMgBCgCDCIJQQFqIghFIQULIAVBAXENFiAIIAQoAhBLDRYgBCAINgIMAkAgBCgCCCAJaiIGIARBzABqQQFqTw0AIARBzABq
IAZBAWpJDRcLIAZBAjoAACAEIAQoAgwiCUEBaiIIRSIFOgAYIAQgBToASAJAIAgNAEEZIQUMAgsCQCAEKAIQIgYgCE8NAANAQX8gBkEBdiAGakHAAGoiBSAF
IAZJGyIGIAhJDQALIARBCGogASkCACAGEIKCgIAAIgVB//8DcQ0CIAQoAgwiCUEBaiIIRSEFCyAFQQFxDRYgCCAEKAIQSw0WIAQgCDYCDAJAIAQoAgggCWoi
BiAEQRhqQQFqTw0AIARBGGogBkEBakkNFwsgBiADQQNxOgAAC0EAIQULIAVB//8DcUUNAQsgBCgCECIGRQ0UIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEg
BvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADBQLIAQgACgCBCIJQQFqIghFIgU6ABggBCAFOgBIAkACQCAIDQBBGSEFDAELAkAgACgCCCIG
IAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAAIAEpAgAgBhCCgoCAACIFQf//A3ENASAAKAIEIglBAWoiCEUhBQsgBUEBcQ0TIAggACgC
CEsNEyAAIAg2AgQCQCAAKAIAIAlqIgYgBEHMAGpBAWpPDQAgBEHMAGogBkEBakkNFAsgBiACQQFxOgAAIAQoAgwhAyAEIAAoAgQiBUEEaiIIIAVJIgk6AEgg
BCAJOgBMAkACQCAIIAVPDQBBGSEFDAELAkAgACgCCCIGIAhPDQADQEF/IAZBAXYgBmpBwABqIgUgBSAGSRsiBiAISQ0ACyAAIAEpAgAgBhCCgoCAACIFQf//
A3ENASAAKAIEIgVBBGoiCCAFSSEJCyAJQQFxDRQgCCAAKAIISw0UIAAgCDYCBAJAIAAoAgAgBWoiBiAEQRhqQQRqTw0AIARBGGogBkEEakkNFQsgBiADNgAA
IAQoAgghCiAEIAAoAgQiBSAEKAIMIgNqIgggBUkiCToAGCAEIAk6AEgCQAJAIAggBU8NAEEZIQUMAQsCQCAAKAIIIgYgCE8NAANAQX8gBkEBdiAGakHAAGoi
BSAFIAZJGyIGIAhJDQALIAAgASkCACAGEIKCgIAAIgVB//8DcQ0BIAAoAgQiBSADaiIIIAVJIQkLIAlBAXENFSAIIAAoAghLDRUgACAINgIEAkAgACgCACAF
aiIGIAogA2pPDQAgCiAGIANqSQ0WCwJAIANFDQAgBiAKIAP8CgAAC0EAIQULIAQoAhAiBkUNFSABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAen
IAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwVCyAEKAIQIgZFDRQgASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIin
KAIMEYGAgIAAgICAgAAMFAsgBCgCECIGRQ0TIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAA
DBMLIAQoAhAiBkUNEiABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwSC0EZIQULIAQoAhAi
BkUNECABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwQCyAEKAIQIgZFDQ8gASkCACEHIAQo
AgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMDwsgBCgCECIGRQ0OIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhB
qgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADA4LIAQoAhAiBkUNDSABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAgg
BkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwNCyAEKAIQIgZFDQwgASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIM
EYGAgIAAgICAgAAMDAsgBCgCECIGRQ0LIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAsL
IAQoAhAiBkUNCiABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwKCyAEKAIQIgZFDQkgASkC
ACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMCQsgBCgCECIGRQ0IIAEpAgAhByAEKAIIIQgCQCAG
RQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAgLIAQoAhAiBkUNByABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwAL
IAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwHCyAEKAIQIgZFDQYgASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdC
IIinKAIMEYGAgIAAgICAgAAMBgsgBCgCECIGRQ0FIAEpAgAhByAEKAIIIQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICA
gIAADAULIAQoAhAiBkUNBCABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoBIAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwECyAEKAIQIgZF
DQMgASkCACEHIAQoAgghCAJAIAZFDQAgCEGqASAG/AsACyAHpyAIIAZBAEEAIAdCIIinKAIMEYGAgIAAgICAgAAMAwsgBCgCECIGRQ0CIAEpAgAhByAEKAII
IQgCQCAGRQ0AIAhBqgEgBvwLAAsgB6cgCCAGQQBBACAHQiCIpygCDBGBgICAAICAgIAADAILIAQoAhAiBkUNASABKQIAIQcgBCgCCCEIAkAgBkUNACAIQaoB
IAb8CwALIAenIAggBkEAQQAgB0IgiKcoAgwRgYCAgACAgICAAAwBCwALIARB0ABqJICAgIAAIAULnwQBBn8jgICAgABBEGsiAySAgICAACACLQAEIQQgAyAA
KAIEIgVBAWoiBkUiBzoACCADIAc6AAcCQAJAIAYNAEEZIQgMAQsCQCAAKAIIIgggBk8NAANAQX8gCEEBdiAIakHAAGoiByAHIAhJGyIIIAZJDQALIAAgASkC
ACAIEIKCgIAAIghB//8DcQ0BIAAoAgQiBUEBaiIGRSEHCwJAIAdBAXENACAGIAAoAghLDQAgACAGNgIEAkAgACgCACAFaiIIIANBBmpBAWpPDQAgA0EGaiAI
QQFqSQ0BCyAIIARBAEc6AAACQCACLQAERQ0AIAAgASACLwEAIAJBAmotAABBEHRyEO6EgIAAIghB//8DcQ0CCyACLQAMIQQgAyAAKAIEIgVBAWoiBkUiBzoA
DCADIAc6AAsCQCAGDQBBGSEIDAILAkAgACgCCCIIIAZPDQADQEF/IAhBAXYgCGpBwABqIgcgByAISRsiCCAGSQ0ACyAAIAEpAgAgCBCCgoCAACIIQf//A3EN
AiAAKAIEIgVBAWoiBkUhBwsgB0EBcQ0AIAYgACgCCEsNACAAIAY2AgQCQCAAKAIAIAVqIgggA0EKakEBak8NACADQQpqIAhBAWpJDQELIAggBEEARzoAAAJA
IAItAAwNAEEAIQgMAgsgACABIAIvAQggAkEKai0AAEEQdHIQ7oSAgAAhCAwBCwALIANBEGokgICAgAAgCAuGBQEFfyOAgICAAEEQayIDJICAgIAAIAMgACgC
BCIEQQFqIgVFIgY6AAQgAyAGOgADAkACQAJAIAUNAEEZIQcMAQsCQCAAKAIIIgcgBU8NAANAQX8gB0EBdiAHakHAAGoiBiAGIAdJGyIHIAVJDQALIAAgASkC
ACAHEIKCgIAAIgdB//8DcQ0BIAAoAgQiBEEBaiIFRSEGCyAGQQFxDQEgBSAAKAIISw0BIAAgBTYCBAJAIAAoAgAgBGoiByADQQJqQQFqTw0AIANBAmogB0EB
akkNAgsgByACOgAAIAMgACgCBCIEQQFqIgVFIgY6AAggAyAGOgAHAkAgBQ0AQRkhBwwBCwJAIAAoAggiByAFTw0AA0BBfyAHQQF2IAdqQcAAaiIGIAYgB0kb
IgcgBUkNAAsgACABKQIAIAcQgoKAgAAiB0H//wNxDQEgACgCBCIEQQFqIgVFIQYLIAZBAXENASAFIAAoAghLDQEgACAFNgIEAkAgACgCACAEaiIHIANBBmpB
AWpPDQAgA0EGaiAHQQFqSQ0CCyAHIAJBgP7/B3FBCHY6AAAgAyAAKAIEIgRBAWoiBUUiBjoADCADIAY6AAsCQCAFDQBBGSEHDAELAkAgACgCCCIHIAVPDQAD
QEF/IAdBAXYgB2pBwABqIgYgBiAHSRsiByAFSQ0ACyAAIAEpAgAgBxCCgoCAACIHQf//A3ENASAAKAIEIgRBAWoiBUUhBgsgBkEBcQ0BIAUgACgCCEsNASAA
IAU2AgQCQCAAKAIAIARqIgcgA0EKakEBak8NACADQQpqIAdBAWpJDQILIAcgAkGAgPwHcUEQdjoAAEEAIQcLIANBEGokgICAgAAgBw8LAAvAAwEFfyOAgICA
AEEQayIEJICAgIAAIAQgACgCBCIFQQRqIgYgBUkiBzoACCAEIAc6AAcCQAJAAkACQCAGIAVJDQACQCAAKAIIIgggBk8NAANAQX8gCEEBdiAIakHAAGoiBSAF
IAhJGyIIIAZJDQALIAAgASkCACAIEIKCgIAAIghB//8DcQ0EIAAoAgQiBUEEaiIGIAVJIQcLIAdBAXENAiAGIAAoAghLDQIgACAGNgIEAkAgACgCACAFaiII
IARBA2pBBGpPDQAgBEEDaiAIQQRqSQ0DCyAIIAM2AAAgBCAAKAIEIgUgA2oiBiAFSSIHOgAMIAQgBzoACyAGIAVPDQELQRkhCAwCCwJAIAAoAggiCCAGTw0A
A0BBfyAIQQF2IAhqQcAAaiIFIAUgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQIgACgCBCIFIANqIgYgBUkhBwsgB0EBcQ0AIAYgACgCCEsN
ACAAIAY2AgQCQCAAKAIAIAVqIgggAiADak8NACACIAggA2pJDQELAkAgA0UNACAIIAIgA/wKAAALQQAhCAwBCwALIARBEGokgICAgAAgCAvDAgEFfyOAgICA
AEEQayIDJICAgIAAAkACQCAAIAEgAhDyhICAACIEQf//A3ENACAAIAEgAkEIahDyhICAACIEQf//A3ENACAAIAEgAkEQahDyhICAACIEQf//A3ENACACLwEY
IQUgAyAAKAIEIgJBAmoiBiACSSIHOgAMIAMgBzoACwJAIAYgAk8NAEEZIQQMAQsCQCAAKAIIIgQgBk8NAANAQX8gBEEBdiAEakHAAGoiAiACIARJGyIEIAZJ
DQALIAAgASkCACAEEIKCgIAAIgRB//8DcQ0BIAAoAgQiAkECaiIGIAJJIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACACaiIEIANBCWpBAmpP
DQAgA0EJaiAEQQJqSQ0CCyAEIAU7AABBACEECyADQRBqJICAgIAAIAQPCwALmQ0BBn8jgICAgABBEGsiAySAgICAACACLQAAIQQgAyAAKAIEIgVBAWoiBkUi
BzoADCADIAc6AAsCQAJAAkAgBg0AQRkhCAwBCwJAIAAoAggiCCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAi
CEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACAFaiIIIANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARB
A3E6AAAgAi0AASEEIAMgACgCBCIFQQFqIgZFIgc6AAwgAyAHOgALAkAgBg0AQRkhCAwBCwJAIAAoAggiCCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkb
IgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACAFaiIIIANBCmpB
AWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARBA3E6AAAgAi0AAiEEIAMgACgCBCIFQQFqIgZFIgc6AAwgAyAHOgALAkAgBg0AQRkhCAwBCwJAIAAoAggiCCAGTw0A
A0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAoAghLDQEg
ACAGNgIEAkAgACgCACAFaiIIIANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARBA3E6AAAgAi0AAyEEIAMgACgCBCIFQQFqIgZFIgc6AAwgAyAHOgALAkAg
Bg0AQRkhCAwBCwJAIAAoAggiCCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQEgACgCBCIFQQFq
IgZFIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACAFaiIIIANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARBA3E6AAAgAi0ABCEEIAMgACgC
BCIFQQFqIgZFIgc6AAwgAyAHOgALAkAgBg0AQRkhCAwBCwJAIAAoAggiCCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkbIgggBkkNAAsgACABKQIAIAgQ
goKAgAAiCEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACAFaiIIIANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0C
CyAIIARBA3E6AAAgAi0ABSEEIAMgACgCBCIFQQFqIgZFIgc6AAwgAyAHOgALAkAgBg0AQRkhCAwBCwJAIAAoAggiCCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIH
IAcgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAoAghLDQEgACAGNgIEAkAgACgCACAFaiII
IANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARBA3E6AAAgAi0AByEEIAMgACgCBCIFQQFqIgZFIgc6AAwgAyAHOgALAkAgBg0AQRkhCAwBCwJAIAAoAggi
CCAGTw0AA0BBfyAIQQF2IAhqQcAAaiIHIAcgCEkbIgggBkkNAAsgACABKQIAIAgQgoKAgAAiCEH//wNxDQEgACgCBCIFQQFqIgZFIQcLIAdBAXENASAGIAAo
AghLDQEgACAGNgIEAkAgACgCACAFaiIIIANBCmpBAWpPDQAgA0EKaiAIQQFqSQ0CCyAIIARBAEc6AAACQCACLQAHRQ0AIAItAAYhBSADIAAoAgQiAkEBaiIG
RSIHOgAMIAMgBzoACwJAIAYNAEEZIQgMAgsCQCAAKAIIIgggBk8NAANAQX8gCEEBdiAIakHAAGoiByAHIAhJGyIIIAZJDQALIAAgASkCACAIEIKCgIAAIghB
//8DcQ0CIAAoAgQiAkEBaiIGRSEHCyAHQQFxDQIgBiAAKAIISw0CIAAgBjYCBAJAIAAoAgAgAmoiCCADQQxqQQFqTw0AIANBDGogCEEBakkNAwsgCCAFQQNx
OgAAC0EAIQgLIANBEGokgICAgAAgCA8LAAuABwEGfyOAgICAAEEgayIDJICAgIAAAkACQAJAAkACQAJAIAItAAQOAwABAgULIAMgACgCBCIEQQFqIgVFIgY6
ABAgAyAGOgAPAkAgBQ0AQRkhAgwECwJAIAAoAggiAiAFTw0AA0BBfyACQQF2IAJqQcAAaiIGIAYgAkkbIgIgBUkNAAsgACABKQIAIAIQgoKAgAAiAkH//wNx
DQQgACgCBCIEQQFqIgVFIQYLIAZBAXENBCAFIAAoAghLDQQgACAFNgIEAkAgACgCACAEaiIGIANBDmpBAWpPDQAgA0EOaiAGQQFqSQ0FC0EAIQIgBkEAOgAA
DAMLIAItAAAhByADIAAoAgQiBEEBaiIFRSIGOgAUIAMgBjoAEwJAIAUNAEEZIQIMAwsCQCAAKAIIIgIgBU8NAANAQX8gAkEBdiACakHAAGoiBiAGIAJJGyIC
IAVJDQALIAAgASkCACACEIKCgIAAIgJB//8DcQ0DIAAoAgQiBEEBaiIFRSEGCyAGQQFxDQMgBSAAKAIISw0DIAAgBTYCBCAAKAIAIARqIgIgA0ESakEBak8N
ASADQRJqIAJBAWpPDQEMAwsgAkECai0AACEHIAIvAQAhCCADIAAoAgQiBEEBaiIFRSIGOgAYIAMgBjoAFwJAIAUNAEEZIQIMAgsCQCAAKAIIIgIgBU8NAANA
QX8gAkEBdiACakHAAGoiBiAGIAJJGyICIAVJDQALIAAgASkCACACEIKCgIAAIgJB//8DcQ0CIAAoAgQiBEEBaiIFRSEGCyAGQQFxDQIgBSAAKAIISw0CIAAg
BTYCBAJAIAAoAgAgBGoiAiADQRZqQQFqTw0AIANBFmogAkEBakkNAwsgAkECOgAAIAAgASAIIAdBEHRyEO6EgIAAIQIMAQsgAkEBOgAAIAMgACgCBCIEQQFq
IgVFIgY6ABwgAyAGOgAbAkAgBQ0AQRkhAgwBCwJAIAAoAggiAiAFTw0AA0BBfyACQQF2IAJqQcAAaiIGIAYgAkkbIgIgBUkNAAsgACABKQIAIAIQgoKAgAAi
AkH//wNxDQEgACgCBCIEQQFqIgVFIQYLIAZBAXENASAFIAAoAghLDQEgACAFNgIEAkAgACgCACAEaiICIANBGmpBAWpPDQAgA0EaaiACQQFqSQ0CCyACIAc6
AABBACECCyADQSBqJICAgIAAIAIPCwALIwEBf0EAIQECQCAARQ0AIAAoAuguRQ0AIAAoAuQuIQELIAELEgACQCAADQBBAA8LIAAoAuguC+FNBQp/An4BfwJ+
G38jgICAgABB0ANrIgEkgICAgAACQAJAAkAgAA0AQQEhAgwBCyAAQQA2AtAuAkACQAJAAkAgACgCGCIDLQCQAQ4EAAECBQALIAMoAlgiAkUNBCADLwGOASEE
A0ACQCAEQf//A3EgAi8BqgIiBUsNAEEAIQYgAUEAOgDIAyABQQA7AcYDIAEgAjYCwAMgASAFIARrIgU7AcQDIAFBwANqIQQMBAsgBCAFayEEIAIoAggiAg0A
DAULCyADKAJUIgJFDQNBACEGIAFBADoAyAMgAUEANgLEAyABIAI2AsADIAFBwANqIQRBACEFDAELIAMoAoABIgQvAQYhBiAELwEEIQUgBCgCACECCyAEKAII
IQcgAC8B9BEgAEH2EWotAABBEHRyIQRBASEIAkAgAC0ArRsgAC0AKHNBAXENAEEBIQggBEH//x9xQYCAAk8NAEEBIQggAy0AygJBA3ENAEEBIQggAC8BqBsg
Ay8BjgFHDQBBASEIIAAvAaobIAMvAYwBRw0AAkAgAC0A6BoNAEEAIQgMAQsgACgC3BogAkcgAC8B4BpB//8DcSAFQf//A3FHciAALwHiGkH//wNxIAZB//8D
cUdyIQgLIAAgAy8BjgEiCTsBqBsgAy8BjAEhCiAAQQE6AOgaIAAgBzYC5BogACAGOwHiGiAAIAU7AeAaIAAgAjYC3BogACAKOwGqGyAAIAMvAdABOwGQEiAA
IAMvAdIBNgKUEiAAIAMoAswBKQMANwOIEiAAIAMpAqABNwKYEiAAQaASaiADQagBaikCADcCACAAQagSaiADQbABaikCADcCACAAQbASaiADQbgBaigCADYC
ACADLQDYASECIAAgBEEMdkEBcToAvRIgACACQQNxOgC8EiAAQgA3ArQSIAAgADUCACILpyICQQ12QQFxOgC+EiAAIAJBDHZBAXE6AL8SIABBBGohAgJAAkAg
AC0AvAFFDQAgAUEBOgDEAyABIAAvAbgBOwHAAyABIABBugFqLQAAOgDCAyABQcADaiEEDAELIABBwAFqIQQLIAIxAAAhDCAAIAQpAgA3A8gSIABB0BJqIQ0C
QEGACEUNACANIABByAFqQYAI/AoAAAsCQAJAIAAtAJwBRQ0AIAFBAToAxAMgASAALwGYATsBwAMgASAAQZoBai0AADoAwgMgAUHAA2ohAgwBCyAAQaABaiEC
CwJAIAIpAgAiDkKAgICA8B+DUA0AAkACQCAALQCsAUUNACABQQE6AMQDIAEgAC8BqAE7AcADIAEgAEGqAWotAAA6AMIDIAFBwANqIQIMAQsgAEGwAWohAgsg
AikCACIPQoCAgIDwH4NQDQAgACAPpyICIA6nIgQgCyAMQiCGhEKAAYNQIgUbIgY7AcQSIAAgBCACIAUbIgI7AcASIABBxhJqIAZBEHY6AAAgAEHCEmogAkEQ
djoAAAsgAEGwG2ohEAJAAkACQAJAAkAgACgC1BoiAiAJRw0AIAIhCQwBCwJAAkAgAiAJTw0AIABB0BpqIBAgCRD2hICAAEH//wNxDQMgACgC2BoiBEGAgICA
Ak8NByAErUIMfiILQiCIpw0HIAAoAtAaIQUgASAAKALUGiIJNgK0ASABIAQ2ArgBIAEgBTYCoAEgASAFIARBA3QiBmoiBTYCmAEgASAFIAZqIgY2ApwBIAEg
BiALpyIFaiIGNgKkASABIAYgBWoiBjYCsAEgASAGIAVqIgU2AqwBIAEgBSAEQQZsajYCqAEgAC8BqBsiBCACayIGIARLDQcgBkUNAkEAIQQDQCACIARqIgUg
AkkNCCABQZgBaiAFEPeEgIAAIAYgBEEBaiIERg0CDAALCyAAKALYGiIEQYCAgIACTw0GIAStQgx+IgtCIIinDQYCQAJAIARFDQAgACgC0BogBEEDdCIFaiIE
QQNxDQggBCAFaiALp2ohBSACIAlrIREMAQsCQCAJDQBBACERQarVqtV6IQRBqtWq1XohBUEAIQIMAQsgCUEAEIqAgIAAAAsCQCACIAlPDQAgCSACEIqAgIAA
AAsgESACIAlrRw0GAkAgEUUNACAFIAlBDGxqIRIgBCAJQQN0aiETQQAhBwNAIBIgB0EMbGohCgJAIBMgB0EDdGooAgAiAkUNACAQKAIAIQUgACgCtBshBgNA
IAJBfGohBCACKAIAIQIgBSAEIAQoAgBBAkEAIAYoAgwRgYCAgACAgICAACACDQALCyAKIBApAwAQq4CAgAAgB0EBaiIHIBFHDQALIAAvAagbIQkLIAAgCTYC
1BoMAQsgACgC1BohCQsgACgC2BoiAkGAgICAAk8NBCACrUIMfiILQiCIpw0EQQAhFAJAAkAgAg0AQarVqtV6IRVBqtWq1XohFkGq1arVeiEXQarVqtV6IRhB
qtWq1XohGUGq1arVeiEaQQAhCUGq1arVeiEbQQAhHAwBCyAAKALQGiIZIAJBA3QiBGoiGEEDcQ0FIBlBB3ENBSAYIARqIhcgC6ciBGoiFiAEaiIVIARqIhpB
AXENBSAaIAJBBmxqIRsgCSEcCyABQbwBaiADQaj6yYAAQbT6yYAAEPuDgIAAIAFB8AFqIAFBvAFqEMSDgIAAAkAgAS0A/AENAEEAIQJBACESDAMLQQAhAkEA
IRJBACEHA0AgAiEdIAEvAfQBIQIgASgC8AEhBQJAIAAtALoSDQAgBSADKALEASIEKAIARw0AIAIgBC8BBEcNACAAIB07AbYSIABBAToAuhIgACADLwHQASIE
OwG0EgJAIAQNACAAQQA6ALgSDAELIAAgAygCzAFBeGopAwBCgICAgICAA4NCgICAgICAAVE6ALgSCwJAAkACQAJAIB0gCUYNACABLwH2ASEGIBcgHUEMbCIR
aiIEIAEoAvgBNgEIIAQgBjsBBiAEIAI7AQQgBCAFNgIAIAIgBS8BqgJPDQkgBkH//wNxIAUvAagCTw0JIAUoAhAiBCAFKAIYaiIGIARJDQkgBkEHcQ0JIAZF
DQkgBCAGIAJBA3RqIgooAgBqIgIgBEkNCSACQQdxDQkgAkUNCSAIIAVBEGoiHiAHRnJBAUcNASAHIRQMAgsgCSAJEIiAgIAAAAsCQCAFLQCsAkUNACAeIRQg
B0UNASAHQQA6AJwCIB4hFAwBCyAHIRQgCi0ABUEBcUUNAQsgCiAKKQMAQv//////X4M3AwAgASAYIB1BA3QiBmoiHykCADcDiAIgASAQKQMANwOAAgJAIBYg
EWoiBygCBEUNACABQYACakHE+smAABDkgICAACAHQQA2AgQgGiAdQQZsaiICQQA2AQAgAkEEakEAOwEAIBUgEWoiAkEIakEAKALU+smAADYCACACQQApAsz6
yYAANwIACwJAAkACQAJAAkACQAJAIBwgHU0NACAbIB1qQQE6AAAgHigCACICIAooAgBqIgQgAkkNDSAEQQdxDQ0gBEUNDSAFLwGoAiITIAAvAaobRw0NIBkg
BmogCikDADcDACAHKAIIIgIgAC8BqhsiEU8NAwNAQX8gAkEBdiACakECaiIGIAYgAkkbIgIgEUkNAAsgAiAHKAIESQ0NQQANBCAQKQMAIgunIiAgAkEsbCIS
QQNBACALQiCIpyIhKAIAEYCAgIAAgICAgAAiBkUNBAJAIBJFDQAgBkGqASAS/AsACyAGQQdxDQ0gBygCBCISDQEgBzUCCEIsfiILQiCIpw0NIAunIhJFDQIg
BygCACEiAkAgEkUNACAiQaoBIBL8CwALICAgIiASQQNBACAhKAIMEYGAgIAAgICAgAAMAgsgHSAcEIiAgIAAAAsgBygCCCIiQcqkkskATw0LIAcoAgAhIwJA
AkAgIg0AQQAhJEGq1arVeiElDAELICNBB3ENDCAjISUgEiEkCyASICRHDQsCQCAlIAYgEkEDdCIkak8NACAGICUgJGpJDQwLICJBA3QhJgJAICRFIicNACAG
ICUgJPwKAAALICMgJmohKAJAAkAgIg0AQQAhJUGq1arVeiEjDAELIChBA3ENDCAoISMgEiElCyASICVHDQsCQCAjIAYgAkEDdCIpaiIlICRqTw0AICUgIyAk
akkNDAsCQCAnDQAgJSAjICT8CgAACwJAAkAgIg0AQQAhJEGq1arVeiEiDAELICggJmoiIkEDcQ0MIBIhJAsgEiAkRw0LAkAgIiAlIClqIiQgEkEcbCISak8N
ACAkICIgEmpJDQwLAkAgEkUNACAkICIgEvwKAAALIAc1AghCLH4iC0IgiKcNCyALpyISRQ0AIAcoAgAhIgJAIBJFDQAgIkGqASAS/AsACyAgICIgEkEDQQAg
ISgCDBGBgICAAICAgIAACyAHIAI2AgggByAGNgIACyAHIBE2AgQgAkHKpJLJAE8NCSAHKAIAIRICQAJAIAINAEEAIQdBqtWq1XohBgwBCyASQQdxDQogEiEG
IBEhBwsgByATSQ0BAkAgBiAEIBNBA3QiB2pPDQAgBiAHaiAESw0KCwJAIAdFDQAgBiAEIAf8CgAACyAKLQAEQRxxRQ0CAkACQCACDQBBACERQarVqtV6IRJB
qtWq1XohIwwBCyASIAJBA3QiAmoiEkEDcQ0KIBIgAmohIwsgE0UNAiATQRxsISRBACEHIAQhE0EAIQYDQAJAAkACQAJAAkAgBCkDACILQoCAgOD//wCDQgBR
DQAgBiARTw0BIAUoAkggC0IaiKdB//8DcSIiTQ0PIAUoAhAiCiAFKAJAaiICIApJDQ8gAkEDcQ0PIAJFDQ8gAiAiQSRsaiICLwEgRQ0PICMgB2oiCiACKQIA
NwIAIApBGGogAkEYaigCADYCACAKQRBqIAJBEGopAgA3AgAgCkEIaiACQQhqKQIANwIAIAQpAwAhCwsgC6dBA3EiAkUNAwJAAkACQCACQX9qDgMAAgEACyAG
IBFPDQIgEyAeKAIAIgJrIiIgE0sNECACIAUoAjhqIgogAkkNECAKRQ0QIAFBlAJqIAogIhCjg4CAAAJAIAEtAJwCDQBBACEKQQAhAgwFCyACIAEoApQCaiIK
IAJJDRAgCkEDcQ0QIApFDRAgASgCmAIhAgwECyAGIBFPDQEgIyAHaiICQQxqQQI6AAAgAkEIaiAEKQMAQgKIPAAAIAJBCWogBCkDAEIKiDwAACAEKQMAIQsg
AkEYakEAOwEAIAJBEGpCADcCACACQgA3AgAgAkEKaiALQhKIPAAADAQLIAYgEUkNAQsgBiAREIiAgIAAAAsgIyAHaiICQQxqQQE6AAAgBCkDACELIAJBGGpB
ADsBACACQRBqQgA3AgAgAkIANwIAIAJBCGogC0ICiDwAAAwBCyABIAJBACAKGyIlQf////8DSzoAwAMgJUGAgICABE8NAgJAAkAgJUECdCICDQBBfCEiDAEL
IAFBgAJqIAJBAiACEI+AgIAAIiJFDQMCQCACRQ0AICJBqgEgAvwLAAsgIkEDcQ0MCwJAICIgCkGq1arVeiAKGyIKIAJqTw0AIAogIiACakkNDAsCQCACRQ0A
ICIgCiAC/AoAAAsgEkEEaiAlNgIAIBIgIjYCAAsgBEEIaiEEIBNBCGohEyASQQhqIRIgBkEBaiEGICQgB0EcaiIHRg0DDAALCyAfIAEpA4gCNwIADAQLIBMg
BxCIgICAAAALIB8gASkDiAI3AgBBASESCyAdQf//A0YNBSAdQQFqIQIgAUHwAWogAUG8AWoQxIOAgAAgFCEHIAEtAPwBRQ0CDAALC0EEIQIMAgsgHUEBaiEC
CyACQf//A3EgAC8BqBtHDQECQAJAIAMtALACRQ0AIANBkAJqIR0gAEHsGmohAgJAAkAgAC0ApBtFIAhyQQFxDQAgAiAdEPiEgIAAQQFxRQ0AIBJBAXEhAkEB
IRIgAkUNAwwBCyABQaACaiAdIANB1ABqIgQoAgAQ+YSAgAAgAUGsAmogHSAEKAIAEPqEgIAAIABBAToApBsgAUHAAmpBCGoiBCABQaACakEIaiIFKAIANgIA
IAFB1AJqIAFBrAJqQQhqIgYoAgA2AgAgAy0ArAIhByACIAEpAqACIgs3AgAgAEEAOgCEGyABIAEpAqwCNwLMAiACQQhqIAQpAwA3AgAgAkEQaiABQcACakEQ
aikDADcCACABQQA6ALwCIAEgCzcDwAIgASAHQQFxIgI6ALgCIAAgAjoAiBsgAEGUG2ogBSgCADYCACAAIAEpAqACNwKMGyAAQaAbaiAGKAIANgIAIAAgASkC
rAI3ApgbCyABQdgCaiADQQIgAEGMG2oiIxDWg4CAACABLQDkAkUNAwJAAkAgAS0A4AIiAkECRw0AIAEpA9gCIQsgAUHoAmogA0ECIABBmBtqIh4Q1oOAgAAg
AS0A9AJFDQUCQAJAIAEtAPACIgJBAkcNACAJDQEMBAsgAkECEMuDgIAAAAsgC0IgiKchHyALpyEYIAEpA+gCIgtCIIinIRkgC6chHANAIAFB+AJqQQhqIBdB
CGooAgA2AgAgASAXKQIANwP4AiABQYgDaiADQQIgAUH4AmoQ1oOAgAAgAS0AlANFDQYgAS0AkAMiAkECRw0CIAFBnANqIB0gAyAjIB4gAUH4AmogGCAfIBwg
GSABKAKMAxD7hICAAAJAIAEtALwDRQ0AIAEvAa4DIREgAS8BrAMhEyABKAKoAyEHIAEoApwDIQQgAS0AtAMhAiABLwGiAyEFIAEgAS8BoAMiIjsBwAMgASAF
OwHOAyAEIQoCQCACQQFxIgJFDQAgBCgCACEKCyAEQQZqIAFBzgNqIAIbLwEAIQYgBEEEaiABQcADaiACGy8BACEkIAEgEzsBwAMgASAROwHOAyAFQRB0ICJy
IQQCQCACRQ0AIAQoAgAhBwsgCiAHRw0HIAZB//8DcSAEQQZqIAFBzgNqIAIbLwEAIgVB//8DcUsNByAkQf//A3EgBEEEaiABQcADaiACGy8BAEH//wNxRw0H
IBogBjsBACAaQQJqIAU7AQAgGkEEakEBOgAACyAXQQxqIRcgGkEGaiEaIAlBf2oiCUUNAwwACwsgAkECEMuDgIAAAAsgAkECEMuDgIAAAAsCQCAIRQ0AIABB
AjoArBsgACAALQAoQQFxOgCtGwwBCyASQQFxRQ0AIAAtAKwbQQNxDQAgAEEBOgCsGwsCQCAURQ0AIBRBADoAnAILIAAgAC8B9BEgAEH2EWoiAi0AAEEQdHJB
///hB3EiBDsB9BEgAiAEQRB2OgAAIANBADoAygIgAC8BqhshAgJAAkAgAC8BqBsiBCAALwGAL0cNACACIAAvAYIvRw0AIAAoArAtIAIgBGxGDQELIAFBDGog
ECkDACAEIAIQhYCAgAACQCABLwF8RQ0AQQIhAgwCCyAAQawtaiIFIAApA7AbEKmAgIAAAkBB8ABFDQAgBSABQQxqQfAA/AoAAAsgACACOwGCLyAAIAQ7AYAv
CyAAQQA2AsQuIABBADYCuC4gAEEANgKsLiAAQQA2AqAuIAAoAtgaIgJBgICAgAJPDQEgAq1CDH4iC0IgiKcNAQJAAkAgAg0AQQAhIEGq1arVeiEaQarVqtV6
IRVBqtWq1XohIQwBCyAAKALQGiACQQN0IgJqIAJqIhVBA3ENAiAAKALUGiEgIBUgC6ciAmoiGiACaiACaiEhCyABQYABakEIakIANwMAIAFCADcDgAECQAJA
IARFDQAgAEHALmohKiAAQbQuaiEpIABBqC5qIScgAEGcLmohJiAALwHAEiAAQcISai0AAEEQdHIhGCAALwHEEiAAQcYSai0AAEEQdHIhF0EAIQVBACEZA0Ag
GSAgRg0CAkACQCAhIBlBBmxqIgItAARFDQACQAJAIBkgACgCkC4iBE8NACAAKAKMLiAZQQF0IgRqIAIoAQAiBkH//wNxIgJB//8BIAJB//8BSRs7AQAgGSAA
KAKYLiICTw0BIAAoApQuIARqIAZBEHYiAkH//wEgAkH//wFJGzsBAAwDCyAZIAQQiICAgAAACyAZIAIQiICAgAAACwJAIBkgACgCkC4iAkkNACAZIAIQiICA
gAAACyAAKAKMLiAZQQF0IgJqQf//AzsBAAJAIBkgACgCmC4iBEkNACAZIAQQiICAgAAACyAAKAKULiACakH//wM7AQALIBkgIEYNAiAaIBlBDGwiBmoiBCgC
CCICQcqkkskATw0EAkACQCACDQBBACEkQarVqtV6IRZBqtWq1XohCEGq1arVeiEcDAELIAQoAgAiCEEHcQ0FIAQoAgQhJCAIIAJBA3QiAmoiFiACaiEcCyAZ
ICBGDQICQCAALwGCL0UNACAVIAZqIgIoAgAhE0EAIRIgAi8BBCIbQQN0ISUDQAJAAkACQAJAAkACQAJAAkACQAJAAkACQAJAAkAgEiAkRg0AIAUgACgCsC0i
Ak8NASAAKAKsLSAFQQJ0IhFqIAggEkEDdCIeaikDACILpyICQQJ2Ih9B////AHFBACACQR50QR51IgZBf0obNgIAIAUgACgCuC0iBE8NAiAAKAK0LSAFaiAC
QQNxOgAAIAUgACgCwC0iAk8NAyAAKAK8LSAFaiALQiqIpyICQQNxOgAAIAUgACgCyC0iBE8NBCAAKALELSAFQQF0IgdqIAtCgICAgICACIMiDEItiKciBEEC
ciAEIAtCA4NCAVEiIxsgAkEEcXI7AQACQCALQoCAgOD//wCDUEUNAEEAIQpBACEJQQAhIkEAIQQMBgsgEiAkTw0GIBwgEkEcbGoiAi8BECACQRJqLQAAQRB0
ciEdIAIvAQggAkEKai0AAEEQdHIhAyACLwEAIAJBAmotAABBEHRyIRQgAi8BGCEEIAItABQhIiACLQAMIQkgAi0ABCEKDAULICQgJBCIgICAAAALIAUgAhCI
gICAAAALIAUgBBCIgICAAAALIAUgAhCIgICAAAALIAUgBBCIgICAAAALAkACQAJAAkACQAJAAkACQAJAIAUgACgC0C0iAk8NACAAKALMLSAHaiAEOwEAIAUg
ACgC2C0iAk8NASAAKALULSAFaiAEQQh2QQdxOgAAIAUgACgC4C0iAk8NBSAAKALcLSARakEANgIAIBchAiAKQf8BcQ4DBAMCFwsgBSACEIiAgIAAAAsgBSAC
EIiAgIAAAAsgFCECDAELIA0gFEH/AXFBAnRqIgIvAQAgAkECai0AAEEQdHIhAgsCQAJAAkACQAJAAkAgBkEDcUF+ag4CAQQAC0HY+smAACEEIAlB/wFxDgMH
AgEXCyANIB9B/wFxQQJ0aiIELwEAIARBAmotAAAiBkEQdHIiB0EIdiEKDAMLIAEgAzsBvAEgASADQRB2OgC+ASABQbwBaiEEDAQLIAEgDSADQf8BcUECdGoi
BC8BADsBvAEgASAEQQJqLQAAOgC+ASABQbwBaiEEDAMLIAtCEoinIQYgC0IKiKchCiALQgKIpyEHC0EAIRQMAwsgBSACEIiAgIAAAAsgBEEBOgAECyAEKQIA
IgtCgICAgPAfg1AhFCALQhCIpyEGIAtCCIinIQogC6chBwtB2PrJgAAhBAJAAkACQAJAICJB/wFxDgMDAQASCyABIB07AbwBIAEgHUEQdjoAvgEgAUG8AWoh
BAwBCyABIA0gHUH/AXFBAnRqIgQvAQA7AbwBIAEgBEECai0AADoAvgEgAUG8AWohBAsgBEEBOgAECyAFIAAoAugtIiJPDQMgBCkCACELIAAoAuQtIBFqIAJB
gICAeHI2AgAgBSAAKALwLSIETw0EIAAoAuwtIBFqIBggBkEQdCAKQf8BcUEIdHIgB0H/AXFyIBQbQYCAgHhyNgIAAkAgBSAAKAL4LSIETw0AIAAoAvQtIBFq
IAIgC6cgC0KAgICA8B+DUBtBgICAeHI2AgAgI0UNAyASICRPDQEgACgCoC4hHSAWIB5qIgIoAgQiIg0CDAcLIAUgBBCIgICAAAALIBIgJBCIgICAAAALIAIo
AgAhAiABIB0gImoiBCAdSSIGOgC8ASABIAY6AJwDAkACQCAEIB1JDQAgIiEGICYgECAEEPyEgIAAQf//A3FFDQELIAEtAIwBQQFxDQ0CQCABKAKAASIEDQBB
AiECDA0LIARBA3ENDSAEQXxqKAIAIgBBDGoiAiAASQ0NIAJBA2oiBSACSQ0NIABBgICAgAJPDQ0gBUH8////B3EiAiAAQQN0aiIFIAJJDQ1BAiECIAUgAEEC
dGoiACAFSQ0NIABFDQwgECkDACILQiCIpyEFIAunIQYgBEF0aiEEAkAgAEUNACAEQaoBIAD8CwALQQIhAiAGIAQgAEECQQAgBSgCDBGBgICAAICAgIAADAwL
A0AgACgCoC4iBCAAKAKkLk8NDSACQQJqLQAAIQcgAi8BACEKIAAgBEEBajYCoC4gACgCnC4gBEECdGogCiAHQRB0cjYCACACQQRqIQIgBkF/aiIGRQ0FDAAL
CyAFIAAoAoAuIgJJDQIgBSACEIiAgIAAAAsgBSAiEIiAgIAAAAsgBSAEEIiAgIAAAAtBACEiIAAoAvwtIBFqQQA2AgAgBSAAKAKILiICSQ0BIAUgAhCIgICA
AAALAkAgBSAAKAKALiICTw0AIAAoAvwtIBFqIB02AgAgBSAAKAKILiICSQ0BIAUgAhCIgICAAAALIAUgAhCIgICAAAALIAAoAoQuIBFqICI2AgACQCAMUA0A
IBsgEy8BqgJPDQcgEiATLwGoAk8NByATKAIQIgIgEygCGGoiBCACSQ0HIARBB3ENByAERQ0HIAIgBCAlaigCAGoiBCACSQ0HIARBB3ENByAERQ0HIAQgHmoi
BCACayIGIARLDQcgAiATKAKkAWoiBCACSQ0HIARFDQcgAUGUAWogBCAGEKaDgIAAIAEtAJYBRQ0AIAEvAZQBIgZFDQcgEygCtAEgBk0NByACIBMoAqwBaiIE
IAJJDQcgBEEDcQ0HIARFDQcgBCAGQRxsaiIELwEYRQ0HIAIgBCgCDGoiCiACSQ0HIApFDQcgBCgCECIiRQ0AIAEoAoABIQIgAUG8AWogAUGAAWogCiAiEP2E
gIAAAkACQAJAAkACQAJAAkACQAJAAkACQAJAIAEtAMABDQAgASAAKALELiIHICJqIgYgB0kiBDoAvAEgASAEOgCcAyAEDQMgACgCyC4iAiAGSQ0BIAchAgwC
CyACRQ0SIAJBA3ENEiACQXRqKAIAIAEoArwBQQJ0aigCACEoDAoLA0BBfyACQQF2IAJqQcAAaiIEIAQgAkkbIgIgBkkNAAsgKiAQKQMAIAIQgoKAgABB//8D
cQ0BIAAoAsQuIgIgImoiBiACSSEECyAEQQFxDRAgBiAAKALILksNECAAIAY2AsQuAkAgACgCwC4gAmoiAiAKICJqTw0AIAIgImogCksNEQsCQCAiRQ0AIAIg
CiAi/AoAAAsgACgCrC5BAWoiAkUNECAnIBAgAhD8hICAAEH//wNxDQEgACgCrC4iAiAAKAKwLk8NECAAIAJBAWo2AqwuIAAoAqguIAJBAnRqIAc2AgAgACgC
uC5BAWoiAkUNECApIBAgAhD8hICAAEH//wNxDQQgACgCuC4iAiAAKAK8Lk8NECAAIAJBAWo2ArguIAAoArQuIAJBAnRqICI2AgAgAS0AjAFBAXENECAAKAKs
LiEoIAFBAToAjAECQAJAIAEoAogBDQBCACELAkAgASgCgAEiAkUNACACQQNxDRMgAkF8ajUCACIMQtAAfkLkAIAhCyAMQv7///8PWg0TCyALQuQAfkLkAHxC
0ACAp2ciAkUNBCABQYABaiAQQgFBICACa61C//8Dg4anEP6EgIAAQf//A3ENAQsgAS0AjAFBAXFFDREgAUEAOgCMASAKICIQ/4SAgAAhCyABKAKAASIdRQ0E
IB1BA3ENESAdQXxqIgMoAgAiI0F/aiIUICNLDQQgC0I5iKchHwJAIB0gFCALp3EiBmoiHi0AACICRQ0AIB1BeGohCSAUIQcDQCAHIQQCQAJAIALAQX9KDQAg
HyACQf8AcUcNACAKICIgCSgCACAGQQN0aiICKAIAIAIoAgQQhIKAgABBAXFFDQEgAUEBOgCcAyAdQXRqKAIAIAZBAnRqIQQgAUGcA2ohBgwKCyAjIAMoAgBH
DQAgBiAjIAJB/wFxQQFGGyEjCyAGQQFqIgJFDQYgHSACIBRxIgZqIh4tAAAiAkUNASAEQX9qIQcgBA0ACwsgASgCiAEiAkF/aiIHIAJLDQQgHSAjaiAeICMg
AygCAEkiAhsgH0GAAXI6AAAgHUF0aigCACEEIB1BeGooAgAgIyAGIAIbIgZBA3RqIgJCqtWq1arVqtWqfzcCACAEIAZBAnRqIgRBqtWq1Xo2AgAgASAHNgKI
ASABKAKEAUEBaiIGRQ0EIAFBADoAnAMgASAGNgKEASABQZwDaiEGDAYLIAFBvAFqIAFBgAFqIAogIhD9hICAAAJAIAEtAMABRQ0AIAEoAoABIgJFDQQgAkED
cQ0EIAEoArwBIQYgAUEBOgCYASABLQCMAUEBcUUNBCACQXRqKAIAIQQgAkF4aigCACECIAFBADoAjAEgBCAGQQJ0aiEEIAEtAJgBQQFxDQggAiAGQQN0aiEC
DAcLIAEtAIwBQQFxRQ0DIAFBADoAjAEgAUGAAWogEBCAhYCAAEECIQIMDwsgAUGAAWogEBCAhYCAAEECIQIMDgsgAUGAAWogEBCAhYCAAEECIQIMDQtBARCM
gICAAAALAAsgAUGAAWogEBCAhYCAAEECIQIMCgsgBi0AAEEBcQ0BCyACICI2AgQgAiAKNgIACyAEICg2AgALAkAgBSAAKALgLSICTw0AIAAoAtwtIBFqICg2
AgAMAQsgBSACEIiAgIAAAAsgBUEBaiEFIBJBAWoiEiAALwGCL0kNAAsLIBlBAWoiGSAALwGAL0kNAAsLQQAhAiAALQC6EiIEQQBHIAAtAL4ScSEFIAAtAL8S
IQYgAC0AvBIhBwJAAkAgBA0AQQAhBEEAIQpBACERDAELIAAtALgSQQFxIREgAC8BtBIhCiAALwG2EiEECyAAIBE6APcuIAAgCjsB8i4gACAEOwHwLiAAIAU6
APQuIAAgBkEBcToA9i4gAEGChgQgB0EDdEEYcUEQc3Y6APUuAkAgAC0AzBJFDQAgAC8ByBIgAEHKEmotAABBEHRyQYCAgHhyIQILIAAgAjYC+C4gAUGAAWog
EBCAhYCAAEEAIQIMAQsgICAgEIiAgIAAAAsgAUHQA2okgICAgAAgAg8LAAuKCAMDfwF+DH8jgICAgABBEGsiAySAgICAAAJAAkAgACgCCCIEIAJPDQADQEF/
IARBAXYgBGpBBWoiBSAFIARJGyIEIAJJDQALAkACQCAEIAAoAgRJDQBBGSEFQQANAyABKQIAIganIgcgBEE7bCIBQQNBACAGQiCIpyIIKAIAEYCAgIAAgICA
gAAiCUUNAwJAIAFFDQAgCUGqASAB/AsACyAJQQdxDQACQCAAKAIEIgUNACAANQIIQjt+IgZCIIinDQEgBqciBUUNAiAAKAIAIQECQCAFRQ0AIAFBqgEgBfwL
AAsgByABIAVBA0EAIAgoAgwRgYCAgACAgICAAAwCCyAAKAIIIgFBgICAgAJPDQAgAa1CDH4iBkIgiKcNACAAKAIAIgogAUEDdCILaiEMAkACQCABDQBBACEN
QarVqtV6IQ4MAQsgDEEDcQ0BIAwhDiAFIQ0LIAUgDUcNAAJAIA4gCSAEQQN0Ig9qIhAgBUEDdCINak8NACAQIA4gDWpJDQELAkAgDUUiEQ0AIBAgDiAN/AoA
AAsgDCALaiELAkACQCABDQBBACEMQarVqtV6IQ4MAQsgC0EDcQ0BIAshDiAFIQwLIAUgDEcNAAJAIA4gECAPaiIQIAVBDGwiDGpPDQAgECAOIAxqSQ0BCwJA
IAxFIhINACAQIA4gDPwKAAALAkACQCABDQBBACEOQarVqtV6IQoMAQsgCkEHcQ0BIAUhDgsgBSAORw0AAkAgCiAJIAVBA3QiDmpPDQAgCSAKIA5qSQ0BCyAG
pyEOAkAgEQ0AIAkgCiAN/AoAAAsgCyAOaiEKAkACQCABDQBBACELQarVqtV6IQ0MAQsgCkEDcQ0BIAohDSAFIQsLIAUgC0cNAAJAIA0gECAEQQxsIg9qIgsg
BUEMbCIQak8NACALIA0gEGpJDQELAkAgEg0AIAsgDSAM/AoAAAsgAUUNAAJAIAogDmoiDSAOaiIKIAFBBmxqIhAgCyAPaiIOIA9qIgEgBEEGbGoiCyAFak8N
ACALIBAgBWpJDQELAkAgBUUNACALIBAgBfwKAAALIAFBAXENACAKQQFxDQACQCAKIAEgBUEGbCILak8NACABIAogC2pJDQELAkAgC0UNACABIAogC/wKAAAL
IA5BA3ENACANQQNxDQACQCANIA4gBUEMbCIFak8NACAOIA0gBWpJDQELAkAgEg0AIA4gDSAM/AoAAAsgADUCCEI7fiIGQiCIpw0AIAanIgVFDQEgACgCACEB
AkAgBUUNACABQaoBIAX8CwALIAcgASAFQQNBACAIKAIMEYGAgIAAgICAgAAMAQsACyAAIAQ2AgggACAJNgIACyAAIAI2AgRBACEFCyADQRBqJICAgIAAIAUL
wAQBAn8CQAJAAkACQAJAAkACQAJAAkAgACgCIA0AQQAhAkGq1arVeiEDDAELIAAoAgAiA0EDcQ0BIAAoAhwhAgsgASACTw0BIAMgAUEDdGpCADcCAAJAAkAg
ACgCIA0AQQAhAkGq1arVeiEDDAELIAAoAgQiA0EDcQ0BIAAoAhwhAgsgASACTw0CQQAhAiADIAFBDGxqIgNBACkD+PnJgAA3AgAgA0EIakEAKAKA+smAADYC
ACAAKAIgRQ0DIAAtAAhBB3ENACABIAAoAhwiAk8NAyAAKAIMIgNBA3ENAEEAIQIgAyABQQxsaiIDQQApAoT6yYAANwIAIANBCGpBACgCjPrJgAA2AgACQAJA
IAAoAiANAEGq1arVeiEDDAELIAAoAhwhAiAAKAIQIQMLIAEgAk8NBCADIAFqQQE6AAACQAJAIAAoAiANAEEAIQJBqtWq1XohAwwBCyAAKAIUIgNBAXENASAA
KAIcIQILIAEgAk8NBUEAIQIgAyABQQZsaiIDQQAoApz6yYAANgEAIANBBGpBAC8BoPrJgAA7AQACQAJAIAAoAiANAEGq1arVeiEDDAELIAAoAhgiA0EDcQ0B
IAAoAhwhAgsgASACSQ0GIAEgAhCIgICAAAsACyABIAIQiICAgAAACyABIAIQiICAgAAACyABIAIQiICAgAAACyABIAIQiICAgAAACyABIAIQiICAgAAACyAD
IAFBDGxqIgBBACkDkPrJgAA3AgAgAEEIakEAKAKY+smAADYCAAuyBAESfyOAgICAAEEQayICJICAgIAAIAIgAC0AGCIDOgAAIAIgAS0AGDoABCAALwESIQQg
AC8BECEFIAAoAgwhBiAAKAIAIQcgASgCACEIIAEvAQQhCSABLwEGIQogASgCDCELIAEvARAhDCABLwESIQ0gAC8BBiEOIAIgAC8BBCIPOwEMIAIgDjsBCiAH
IRACQCADQQFxIgNFDQAgBygCACEQCyAHQQZqIAJBCmogAxsvAQAhESAHQQRqIAJBDGogAxsvAQAhEiACIAk7AQwgAiAKOwEKIAghAwJAIAItAAQiB0UNACAI
KAIAIQMLQQAhEwJAIBAgA0cNACASQf//A3EgCEEEaiACQQxqIAcbLwEAQf//A3FHDQAgEUH//wNxIAhBBmogAkEKaiAHGy8BAEH//wNxRw0AIAIgBTsBDCAC
IAQ7AQogAiACLQAAIgg6AAggDkEQdCAPciEHAkAgCEEBcSIIRQ0AIAcoAgAhBgsgB0EGaiACQQpqIAgbLwEAIQ4gB0EEaiACQQxqIAgbLwEAIQMgAiAMOwEM
IAIgDTsBCiACIAItAAQiCDoACCAKQRB0IAlyIQcCQCAIQQFxRQ0AIAcoAgAhCwsgBiALRw0AIANB//8DcSAHQQRqIAJBDGogCEEBcSIIGy8BAEH//wNxRw0A
IA5B//8DcSAHQQZqIAJBCmogCBsvAQBB//8DcUcNACAALQAcIAEtABxzQQFzIRMLIAJBEGokgICAgAAgEwvNBQIEfwF+I4CAgIAAQYABayIDJICAgIAAIANB
GGoiBCABQRhqKQIANwMAIANBEGoiBSABQRBqKQIANwMAIANBCGoiBiABQQhqKQIANwMAIAMgASkCADcDAAJAAkACQAJAAkAgAyACEIGFgIAAQQNxDgQAAQID
AAsgA0HgAGpBGGogBCgCACIBNgIAIANB4ABqQRBqIAUpAwA3AwAgA0HgAGpBCGogBikDADcDACADIAMpAwAiBzcDYCADQSBqQQhqIAenIANB4ABqIAFBAXEb
IgFBCGooAgA2AgAgAyABKQIANwMgIANBIGohAQwDCyADQeAAakEYaiAEKAIAIgE2AgAgA0HgAGpBEGogBSkDADcDACADQeAAakEIaiAGKQMANwMAIAMgAykD
ADcDYCADQTBqQQhqIAMoAmQgA0HsAGogAUEBcRsiAUEIaigCADYCACADIAEpAgA3AzAgA0EwaiEBDAILIANB4ABqQRhqIAQoAgAiATYCACADQeAAakEQaiAF
KQMANwMAIANB4ABqQQhqIAYpAwA3AwAgAyADKQMAIgc3A2AgA0HAAGpBCGogB6cgA0HgAGogAUEBcRsiAUEIaigCADYCACADIAEpAgA3A0AgAyADLwESOwFg
IAMgAygCBEEGaiADQeAAaiAELQAAQQFxGy8BADsBRiADQcAAaiEBDAELIANB4ABqQRhqIAQoAgAiATYCACADQeAAakEQaiAFKQMANwMAIANB4ABqQQhqIAYp
AwA3AwAgAyADKQMANwNgIANB0ABqQQhqIAMoAmQgA0HsAGogAUEBcRsiAUEIaigCADYCACADIAEpAgA3A1AgAyADLwEGOwFgIAMgAygCAEEGaiADQeAAaiAE
LQAAQQFxGy8BADsBViADQdAAaiEBCyAAIAEpAgA3AgAgAEEIaiABQQhqKAIANgIAIANBgAFqJICAgIAAC80FAgR/AX4jgICAgABBgAFrIgMkgICAgAAgA0EY
aiIEIAFBGGopAgA3AwAgA0EQaiIFIAFBEGopAgA3AwAgA0EIaiIGIAFBCGopAgA3AwAgAyABKQIANwMAAkACQAJAAkACQCADIAIQgYWAgABBA3EOBAABAgMA
CyADQeAAakEYaiAEKAIAIgE2AgAgA0HgAGpBEGogBSkDADcDACADQeAAakEIaiAGKQMANwMAIAMgAykDADcDYCADQSBqQQhqIAMoAmQgA0HsAGogAUEBcRsi
AUEIaigCADYCACADIAEpAgA3AyAgA0EgaiEBDAMLIANB4ABqQRhqIAQoAgAiATYCACADQeAAakEQaiAFKQMANwMAIANB4ABqQQhqIAYpAwA3AwAgAyADKQMA
Igc3A2AgA0EwakEIaiAHpyADQeAAaiABQQFxGyIBQQhqKAIANgIAIAMgASkCADcDMCADQTBqIQEMAgsgA0HgAGpBGGogBCgCACIBNgIAIANB4ABqQRBqIAUp
AwA3AwAgA0HgAGpBCGogBikDADcDACADIAMpAwA3A2AgA0HAAGpBCGogAygCZCADQewAaiABQQFxGyIBQQhqKAIANgIAIAMgASkCADcDQCADIAMvAQY7AWAg
AyADKAIAQQZqIANB4ABqIAQtAABBAXEbLwEAOwFGIANBwABqIQEMAQsgA0HgAGpBGGogBCgCACIBNgIAIANB4ABqQRBqIAUpAwA3AwAgA0HgAGpBCGogBikD
ADcDACADIAMpAwAiBzcDYCADQdAAakEIaiAHpyADQeAAaiABQQFxGyIBQQhqKAIANgIAIAMgASkCADcDUCADIAMvARI7AWAgAyADKAIEQQZqIANB4ABqIAQt
AABBAXEbLwEAOwFWIANB0ABqIQELIAAgASkCADcCACAAQQhqIAFBCGooAgA2AgAgA0GAAWokgICAgAALrwYCAX8BfiOAgICAAEHgAGsiCySAgICAAAJAAkAC
QCAKIAdJDQAgCiAJTQ0BCyAAQgA3AgAgAEEgakEANgIAIABBGGpCADcCACAAQRBqQgA3AgAgAEEIakIANwIADAELAkAgAS0AHEUNACAAIAY7AQYgAEEBOgAg
IABBAToAHCAAQQA6ABggACAIOwESIAAgBSgCACIKNgIAIAAgBSgCCCIHNgIIIAAgCjYCDCAAIAc2AhQgAEEEaiAFQQRqLwEAIgo7AQAgAEEQaiAKOwEAIAtB
ADoADCALQQE6AAgMAQsCQAJAAkAgCiAHRw0AAkAgCiAJRw0AIAtBGGpBCGoiCiADQQhqKAIANgIAIAtBLGogBEEIaigCADYCACAAQQA6ABwgAEEBOgAgIABB
ADoAGCAAIAMpAgAiDDcCACALIAQpAgA3AiQgAEEIaiAKKQMANwIAIABBEGogC0EYakEQaikDADcCACALQQA6ABAgC0EAOgAUIAsgDDcDGAwECyACLwGMAUF/
aiIKQf//A3EgCkcNASALQThqQQhqIgcgA0EIaigCADYCACAAQQE6ACAgAEEAOgAYIAAgCjsBEiAAQQA6ABwgACADKQIAIgw3AgAgACAFKAIINgIUIAtBOGpB
EGogBUEEai8BACIKOwEAIABBEGogCjsBACALIAUoAgA2AkQgAEEIaiAHKQMANwIAIAtBADoANCALQQA6ADAgCyAMNwM4DAMLIAUoAgghByAKIAlGDQEgAi8B
jAFBf2oiCkH//wNxIApHDQAgACAHNgIIIABBADsBBiAAQQE6ACAgAEEAOgAcIABBADoAGCAAIAc2AhQgACAKOwESIAAgBSgCACIKNgIAIAAgCjYCDCAAQQRq
IAVBBGovAQAiCjsBACAAQRBqIAo7AQAgC0EAOgBYIAtBADoAXAwCCwALIAAgBzYCCCAAQQA7AQYgAEEBOgAgIABBADoAHCAAQQA6ABggACAFKAIANgIAIAAg
BCkCADcCDCAAQQRqIAVBBGovAQA7AQAgAEEUaiAEQQhqKAIANgIAIAtBADoAUCALQQA6AFQLIAtB4ABqJICAgIAAC8AEAwR/AX4DfyOAgICAAEEQayIDJICA
gIAAAkACQCAAKAIIIgQgAk8NACAEIQUDQEF/IAVBAXYgBWpBEGoiBiAGIAVJGyIFIAJJDQALIAEpAgAiB0IgiKchASAAKAIAIQggB6chCQJAAkACQAJAIARF
DQAgBEGAgICABE8NAiADIAVB/////wNLOgAIIAVBgICAgARPDQBBACEGAkAgCSAIIARBAnRBAiAFQQJ0IgJBACABKAIIEYKAgIAAgICAgAAiCg0AQQAhAgwC
CwJAIAJFDQAgCkEDcQ0DCwJAIAINAEEAIQJBqtWq1XohBgwCCyAFQf////8DcSECIAohBgwBC0EAIQZBACECCwJAIAZFDQAgACAGNgIAIAAgAjYCCAwDCyAD
IAVB/////wNLOgAMAkAgBUGAgICABEkNAEEZIQUMBAsCQAJAIAVBAnQiAg0AQXwhBgwBCwJAIAkgAkECQQAgASgCABGAgICAAICAgIAAIgYNAEEZIQUMBQsC
QCACRQ0AIAZBqgEgAvwLAAsgBkEDcQ0BCyAAKAIEIgIgBUsNAQJAIAYgACgCACIKIAJBAnQiAmpPDQAgCiAGIAJqSQ0BCwJAIAJFDQAgBiAKIAL8CgAACwJA
IARFDQACQCAEQQJ0IgJFDQAgCEGqASAC/AsACyAJIAggAkECQQAgASgCDBGBgICAAICAgIAACyAAIAU2AgggACAGNgIADAILAAsgAiAFEIiAgIAAAAtBACEF
CyADQRBqJICAgIAAIAULkQIDAX8BfgZ/AkAgASgCBA0AIABCADcCAA8LIAEoAgAhBCACIAMQ/4SAgAAhBQJAIARFDQAgBEEDcQ0AIARBfGooAgAiBkF/aiIH
IAZLDQAgASgCACIIRQ0AAkAgCCAHIAWncSIJai0AACIIRQ0AIAVCOYinIQogBkF/aiEGIARBeGohCwNAIAYhBAJAIAjAQX9KDQAgCiAIQf8AcUcNACACIAMg
CygCACAJQQN0aiIIKAIAIAgoAgQQhIKAgABBAXFFDQAgAEEBOgAEIAAgCTYCAA8LIAlBAWoiCEUNAiABKAIAIgZFDQIgBiAIIAdxIglqLQAAIghFDQEgBEF/
aiEGIAQNAAsLIABCADcCAA8LAAu4BwMGfwF+C38jgICAgABBIGsiAySAgICAACACQQggAkEISxshBAJAAkAgACgCACIFRQ0AIAVBA3ENASAEIAVBfGooAgBN
DQELIAQgBEF/anENACACQYCAgIACTw0AIARBD2pBfHEiBiAEQQN0aiICIAZJDQAgAiAEQQJ0aiIHIAJJDQBBGSEFAkBBAA0AAkACQCAHDQBBfCEIDAELIAEp
AgAiCacgB0ECQQAgCUIgiKcoAgARgICAgACAgICAACIIRQ0BAkAgB0UNACAIQaoBIAf8CwALIAhBA3ENAgsgCEEDcQ0BIAggBDYCCCAIIAggBmo2AgQgCCAI
IAJqNgIAIANBAToAACAIQQxqIQJBACEKAkAgBEUNACACQQAgBPwLAAsgBK1C0AB+QuQAgKchCyAAKAIAIQwCQCAAKAIERQ0AIAxFDQIgDEEDcQ0CIAxBfGoo
AgAiDUUNACAMQXRqKAIAIQ4gDEF4aigCACEPIANBFWohECADLQAAIRFBACEHQQAhCgJAA0ACQCAMIAdqLAAAQX9KDQAgDiAHQQJ0aigCACESIA8gB0EDdGoi
BCgCBCETIAQoAgAhFCAQQQA7AAAgEEECakEAOgAAIAMgEToAFCADIAs2AhAgAyAKNgIMIAMgAjYCCCADQRhqIANBCGogFCATEP2EgIAAIAMtABwNBSAUIBMQ
/4SAgAAhCSAIKAIIIgRBf2oiBSAESw0FAkAgAiAFIAmncSIEaiIGLAAAQX9KDQADQCAEQQFqIgRFDQcgAiAEIAVxIgRqIgYsAABBAEgNAAsLIAtFDQUgBiAJ
QjmIp0GAAXI6AAAgCCgCBCAEQQN0aiIFIBM2AgQgBSAUNgIAIAgoAgAgBEECdGogEjYCACAKQQFqIgpFDQUgC0F/aiELIAogACgCBEYNAgsgB0EBaiIHIA1H
DQALCyAAKAIAIQwLQQAhBSAAQQA7AA0gACADLQAAOgAMIAAgCzYCCCAAIAo2AgQgACACNgIAIABBD2pBADoAACAMRQ0AIAxBA3ENASAMQXxqKAIAIgRBDGoi
AiAESQ0BIAJBA2oiBiACSQ0BIARBgICAgAJPDQEgBkH8////B3EiBiAEQQN0aiICIAZJDQEgAiAEQQJ0aiIEIAJJDQEgBEUNACABKQIAIglCIIinIQYgCach
ByAMQXRqIQICQCAERQ0AIAJBqgEgBPwLAAtBACEFIAcgAiAEQQJBACAGKAIMEYGAgIAAgICAgAALIANBIGokgICAgAAgBQ8LAAvaBwMBfwN+Bn8jgICAgABB
4ABrIgIkgICAgAACQAJAAkACQAJAAkACQAJAAkACQAJAAkACQCABQRBLDQAgAUEDSw0BQgAhA0KsmuLTo9Lw+h8hBCABDQJCACEFDAcLQQAhBiABQTBPDQJC
rJri06PS8PofIQQMBQsgASABQQF2QQxxIgZJDQMCQAJAIAEgBmsiB0EDTQ0AIAFBfGoiCCAGayIHIAhLDQwgASAHSQ0EIAEgB2siCUEDTQ0BIAA1AABCIIYg
ACAGajUAAIQhBSAAIAhqNQAAQiCGIAAgB2o1AACEIQNCrJri06PS8PofIQQMBwtBBCAHEIiAgIAADAsLQQQgCRCIgICAAAALIAAgAUEBdmoxAABCCIYgADEA
AEIQhoQgACABakF/ajEAAIQhBQwEC0KsmuLTo9Lw+h8hBEEwIQdCrJri06PS8PofIQNCrJri06PS8PofIQUDQAJAAkAgByABTw0AIAZBMGoiCCABTQ0BIAgg
ARCIgICAAAALIAMgBIUgBYUhBAwECyAHIQggAkHAAGogACAGaiIGKQAIIASFQgAgBikAAELb0dCFmtrfgWeFQgAQr4WAgAAgAkEwaiAGKQAYIAOFQgAgBikA
EELjjaPkid6a3o5/hUIAEK+FgIAAIAJBIGogBikAKCAFhUIAIAYpACBCw5ndqce52czYAIVCABCvhYCAACAHQTBqIgYgB0khCSACKQMoIAIpAyCFIQUgAikD
OCACKQMwhSEDIAIpA0ggAikDQIUhBCAGIQcgCCEGIAkNCQwACwsgByABEIqAgIAAAAsgBiABEIqAgIAAAAsgASAGayIIIAFLDQUgCEExTw0FIAYgAUsNBCAA
IAZqIQlBACEHQRAhBgJAA0AgBiAITw0BIAggB0kNAyAIIAdrIgpBB00NBCAIIAdBCGoiCmsiC0EHTQ0FIAYhCyACQdAAaiAJIApqKQAAIASFQgAgCSAHaikA
AELb0dCFmtrfgWeFQgAQr4WAgAAgBkEQaiIHIAZJIQogAikDWCACKQNQhSEEIAchBiALIQcgCg0HDAALCyAAIAFqIgZBeGopAAAhAyAGQXBqKQAAIQULIAJB
EGogBULb0dCFmtrfgWeFQgAgAyAEhUIAEK+FgIAAIAIgAikDGELb0dCFmtrfgWeFQgAgAa0gAikDEIVCr8j1xcesh7ugf4VCABCvhYCAACACKQMAIQQgAikD
CCEDIAJB4ABqJICAgIAAIAMgBIUPCyAHIAgQioCAgAAAC0EIIAoQiICAgAAAC0EIIAsQiICAgAAACyAGIAEQioCAgAAACwAL6AECBH8BfgJAIAAtAAxBAXEN
AAJAIAAoAgAiAkUNACACQQNxDQEgAkF8aigCACIDQQxqIgQgA0kNASAEQQNqIgUgBEkNASADQYCAgIACTw0BIAVB/P///wdxIgUgA0EDdGoiBCAFSQ0BIAQg
A0ECdGoiAyAESQ0BIANFDQAgASkCACIGQiCIpyEEIAanIQEgAkF0aiECAkAgA0UNACACQaoBIAP8CwALIAEgAiADQQJBACAEKAIMEYGAgIAAgICAgAALIABC
qtWq1arVqtWqfzcCACAAQQhqQqrVqtWq1arVqn83AgAPCwALkgUBDH8jgICAgABBIGsiAiSAgICAACACIAAtABgiAzoACCAALwESIQQgAC8BECEFIAAoAgwh
BiAAKAIAIQcgAC8BBiEIIAIgAC8BBCIJOwEMIAIgCDsBCiAHIQoCQCADQQFxIgNFDQAgBygCACEKCwJAAkAgAUUNACAHQQZqIAJBCmogAxsvAQAhCyAHQQRq
IAJBDGogAxsvAQAhDAJAIAogAUYNACABKAIMIgdFDQEgAS8BqgIhAwNAAkAgByAKRw0AIAMgDGoiDCADSQ0DDAILIAMgBy8BqgJqIg0gA0kNAiANIQMgBygC
DCIHDQAMAgsLAkBBAUUNACACIAU7ARggAiAEOwEWIAIgAi0ACCIDOgAUIAhBEHQgCXIhBwJAIANBAXFFDQAgBygCACEGCyAHQQZqIAJBFmogA0EBcSIDGy8B
ACEIIAdBBGogAkEYaiADGy8BACEKIAYgAUYNAiABKAIMIgdFDQEgAS8BqgIhAwNAAkAgByAGRw0AIAMgCmoiCiADSQ0DDAQLIAMgBy8BqgJqIg0gA0kNAiAN
IQMgBygCDCIHDQAMAgsLQQJBAhDLg4CAAAALAAsCQAJAAkACQAJAQQFFDQAgAC0AHEUNAUEBIQcCQCAMIApNIgMNACALQf//A3EgCEH//wNxTw0FCwJAIAwg
CkkiDQ0AIAtB//8DcSAIQf//A3FLDQULIAMNAiALQf//A3EgCEH//wNxTw0CQQMhBwwEC0ECQQIQy4OAgAAACyAMIApPDQFBACEHDAILQX5BACALQf//A3Eg
CEH//wNxSxtBACANGyEHDAELAkAgDCAKTQ0AQQEhBwwBCyALQf//A3EgCEH//wNxSyEHCyACQSBqJICAgIAAIAcLEgACQCAADQBBAA8LIAAoArAtCxIAAkAg
AA0AQQAPCyAAKAKsLQsSAAJAIAANAEEADwsgACgCtC0LEgACQCAADQBBAA8LIAAoArwtCxIAAkAgAA0AQQAPCyAAKALELQsSAAJAIAANAEEADwsgACgCzC0L
EgACQCAADQBBAA8LIAAoAtQtCxIAAkAgAA0AQQAPCyAAKALcLQsSAAJAIAANAEEADwsgACgC5C0LEgACQCAADQBBAA8LIAAoAuwtCxIAAkAgAA0AQQAPCyAA
KAL0LQsjAQF/QQAhAQJAIABFDQAgACgCrC5FDQAgACgCqC4hAQsgAQsjAQF/QQAhAQJAIABFDQAgACgCuC5FDQAgACgCtC4hAQsgAQsjAQF/QQAhAQJAIABF
DQAgACgCxC5FDQAgACgCwC4hAQsgAQsSAAJAIAANAEEADwsgACgCrC4LEgACQCAADQBBAA8LIAAoAsQuCxIAAkAgAA0AQQAPCyAAKAL8LQsSAAJAIAANAEEA
DwsgACgChC4LIwEBf0EAIQECQCAARQ0AIAAoAqAuRQ0AIAAoApwuIQELIAELEgACQCAADQBBAA8LIAAoAqAuCxIAAkAgAA0AQQAPCyAAKAKMLgsSAAJAIAAN
AEEADwsgACgClC4LDQAgAEHwLmpBACAAGwsSAAJAIAANAEEADwsgAC8BgC8LEgACQCAADQBBAA8LIAAvAYIvCxUAAkAgAA0AQQAPCyAAKAIYLwHQAQsVAAJA
IAANAEEADwsgACgCGC8B0gELEgACQCAADQBBAA8LIAAvAYASCxIAAkAgAA0AQQAPCyAALwGCEgsSAAJAIAANAEEADwsgAC8B+hELEgACQCAADQBBAA8LIAAv
AfgRCxUAAkAgAA0AQQAPCyAAKAIYLwGMAQsVAAJAIAANAEEADwsgACgCGC8BjgELBQBBxAALEgACQCAADQBBAA8LIAAoAtAuCyMBAX9BACEBAkAgAEUNACAA
KALQLkUNACAAKALMLiEBCyABC30DAn8BfgF/I4CAgIAAQRBrIgEkgICAgABBACECAkAgAEUNAEEADQBBACkD4PrJgAAhA0EAIQIgA6cgAEEAQQAgA0IgiKco
AgARgICAgACAgICAACIERQ0AAkAgAEUNACAEQaoBIAD8CwALIAQhAgsgAUEQaiSAgICAACACCzEAAkAgAEUNACABRQ0AAkAgAUUNACAAQaoBIAH8CwALIAEg
ACABQQAgARC5gICAAAsLAgALpQIBBH8jgICAgABBEGsiBiSAgICAACAGIANBH3E6AA8CQAJAAkAgAkEEaiIDIAJJDQAgA0EBIAYtAA90IgIgAyACSxsiB0F/
amciA0UNAUEgIANrIghB//8DcSIDQX1qIgkgA0sNAEF/IARBBGoiAyADIARJGyIDIAIgAyACSxshAgJAIAlBDEsNAAJAIAJBf2pnIgINAEEAIQIMBAtCASAI
rUL//wODhqdCAUEgIAJrrUL//wODhqdGIQIMAwsgB0GDgARqIgMgB0kNACACQYOABGoiBCACSQ0AQgFBICADQRB2QX9qZ2utQv//A4OGp0IBQSAgBEEQdkF/
amdrrUL//wODhqdGIQIMAgsAC0EBEIyAgIAAAAsgBkEQaiSAgICAACACC6MCAQR/I4CAgIAAQRBrIgYkgICAgAAgBiADQR9xOgAPAkACQCACQQRqIgMgAkkN
ACADQQEgBi0AD3QiAiADIAJLGyIHQX9qZyIDRQ0BQSAgA2siCEH//wNxIgNBfWoiCSADSw0AQX8gBEEEaiIDIAMgBEkbIgMgAiADIAJLGyECAkACQAJAIAlB
DEsNACACQX9qZyICRQ0BQgEgCK1C//8Dg4anQgFBICACa61C//8Dg4anRw0BDAILIAdBg4AEaiIDIAdJDQIgAkGDgARqIgQgAkkNAkIBQSAgA0EQdkF/amdr
rUL//wODhqdCAUEgIARBEHZBf2pna61C//8Dg4anRg0BC0EAIQELIAZBEGokgICAgAAgAQ8LAAtBARCMgICAAAALjQYFBH8BfgJ/AX4HfyOAgICAAEEgayIF
JICAgIAAAkAgA0F/aiIGIANLDQACQAJAAkACQCAGIANPDQAgAiAGQQN0aiIHKAIEIgitIAStfiIJQiCIpw0EIAFBeGoiCigCFCILIAooAhAiBksNASAKKQIA
IQwgBygCACENIAmnIQ4gCkIANwIQIAooAgwhDyAKQarVqtV6NgIMIAUgBjYCFCAFIAs2AhAgBSAPNgIMQQAhECALIQECQAJAAkADQCACIBBBA3RqIhEoAgQi
EiAOaiIHIBJJDQggB0EBaiIHRQ0IIBEoAgAhEyAFIAEgB2oiByABSSIROgAcIAUgEToAGyAHIAFJDQIgASERAkAgBiAHTw0AA0BBfyAGQQF2IAZqQcAAaiIB
IAEgBkkbIgYgB0kNAAsgBUEMaiAMIAYQgoKAgABB//8DcQ0CIAUoAhAhEQsgESASaiIBIBFJDQggASAFKAIUIgZLDQggBSABNgIQAkAgBSgCDCIPIBFqIgcg
EyASak8NACATIAcgEmpJDQkLAkAgEkUNACAHIBMgEvwKAAALIBBBAWoiECADRw0ACyAEDQIgASAIayIHIAFLDQcgByEBDAULIAUoAhAhASAFKAIUIQYgBSgC
DCEPCyAKIAE2AhQgCiAGNgIQIAogDzYCDCAAQoCAgIAgNwIADAQLAkACQCAIDgIEAAELIAQgAWpBf2oiByABSQ0FIAcgBksNBQJAIAcgAWsiEg0AIAchAQwE
CyAPIAFqIA0tAAAgEvwLACAHIQEMAwsgBEF/aiISRQ0CIA8gCGohEyANIAhqIRADQCABIAhqIgcgAUkNBSAHIAZLDQUCQCAPIAFqIhEgEE8NACANIBMgAWpJ
DQYLAkAgCEUNACARIA0gCPwKAAALIAchASASQX9qIhINAAsgByEBDAILIAYgAxCIgICAAAALIAsgBhCIgICAAAALIAEgC2siByABSw0BIAogATYCFCAKIAY2
AhAgCiAPNgIMIABBADsBBCAAIAc2AgALIAVBIGokgICAgAAPCwALDgAgAEKAgICA0AA3AgALnQMDBH8BfgF/I4CAgIAAQSBrIgMkgICAgAACQAJAAkACQCAA
QQxqKAIAIgQgAEEIaigCACIFSw0AIABBeGoiBikCACEHIAZCADcCECAGKAIMIQggBkGq1arVejYCDCADIAU2AhQgAyAENgIQIAMgCDYCDCADIAEgAmoiACAB
SSIBOgAYIAENAQJAAkAgBSAATw0AA0BBfyAFQQF2IAVqQcAAaiIEIAQgBUkbIgUgAEkNAAsgA0EMaiAHIAUQgoKAgAAhBSADKAIQIQQgBUH//wNxDQELIAMg
BCACaiIAIARJIgU6ABwgAyAFOgAbAkAgACAETw0AQQIhAQwEC0EAIQEgAygCFCIFIABPDQMDQEF/IAVBAXYgBWpBwABqIgQgBCAFSRsiBSAASQ0ACyADQQxq
IAcgBRCCgoCAAEH//wNxQQBHQQF0IQEgAygCECEEDAMLQQIhAQwCCyAEIAUQiICAgAAAC0ECIQEMAQsgAygCFCEFIAMoAgwhCAsgBiAENgIUIAYgBTYCECAG
IAg2AgwgA0EgaiSAgICAACABCwoAIAAQg4WAgAALdQEBfiAAIAQgAX4gAiADfnwgA0IgiCICIAFCIIgiBH58IANC/////w+DIgMgAUL/////D4MiAX4iBUIg
iCADIAR+fCIDQiCIfCADQv////8PgyACIAF+fCIBQiCIfDcDCCAAIAFCIIYgBUL/////D4OENwMACwuH/wkBAEGAgMAAC/z+CQEAAAACAAAAAwAAAAQAAACq
qqqqAAAAAAAAAACqqqqqAAAAAAAAAAB7IC4uLiB9AC57AGxpYmdob3N0dHkAY2FwYWNpdHkAaGlzdG9yeQBIeXBlcmxpbmtTZXRPdXRPZk1lbW9yeQBTdHls
ZVNldE91dE9mTWVtb3J5AFN0cmluZ3NPdXRPZk1lbW9yeQBIeXBlcmxpbmtNYXBPdXRPZk1lbW9yeQBHcmFwaGVtZU1hcE91dE9mTWVtb3J5AFN0cmluZ0Fs
bG9jT3V0T2ZNZW1vcnkAR3JhcGhlbWVBbGxvY091dE9mTWVtb3J5AGVucXVpcnkAa2l0dHlfa2V5Ym9hcmRfcXVlcnkAZG5kLWNvcHkARW5kQ29weQBub3Rp
ZnkAVW5rbm93bktleQBib2R5AGFjdGl2ZV9zdGF0dXNfZGlzcGxheQBjb25lbXVfc2hvd19tZXNzYWdlX2JveAByZXZlcnNlX2luZGV4AHF1ZXN0aW9uX2Fy
cm93AGN1cnNvcl9yb3cAT3ZlcmZsb3cAZXJhc2VfZGlzcGxheV9iZWxvdwByZWRyYXcAY29udGV4dC1tZW51AGN1cnNvcl90ZXh0AHZlcnRpY2FsLXRleHQA
ZW5kX2lucHV0X3N0YXJ0X291dHB1dABJbnB1dE91dHB1dABDbGVhckNhcHR1cmVkT3V0cHV0AGVuZF9wcm9tcHRfc3RhcnRfaW5wdXQAY29uZW11X3dhaXRf
aW5wdXQAZGNzX3B1dABhcGNfcHV0AHpvb20tb3V0AENvbm5lY3Rpb25UaW1lZE91dABSZW1vdGVIb3N0AGxpc3QAbGFzdAB2aWV3cG9ydABjb25lbXVfcHJv
Z3Jlc3NfcmVwb3J0AGtpdHR5X2NvbG9yX3JlcG9ydABzaXplX3JlcG9ydABwcm9tcHRfc3RhcnQAaHlwZXJsaW5rX3N0YXJ0AGFwY19zdGFydABGaWxlUGFy
dABDb3JydXB0AGZyZXNoX2xpbmVfbmV3X3Byb21wdABzZW1hbnRpY19wcm9tcHQAa2l0dHlfa2V5Ym9hcmRfc2V0X25vdABJbnZhbGlkU25hcHNob3QAcHJp
bnQAdGFiX2NsZWFyX2N1cnJlbnQAY29uZW11X2NvbW1lbnQAcHJlYWxsb2Nfc2VnbWVudABkZWZhdWx0AGV4cGxpY2l0AGltcGxpY2l0AHdhaXQAY3Vyc29y
X3JpZ2h0AGVyYXNlX2xpbmVfcmlnaHQAY3Vyc29yX2xlZnQAZXJhc2VfbGluZV9sZWZ0AE5vU3BhY2VMZWZ0AGNvbmZpZ3VyZV9jaGFyc2V0AGludm9rZV9j
aGFyc2V0AGZ1bGxfcmVzZXQAdGFiX3Jlc2V0AGtpdHR5X2tleWJvYXJkX3NldAB0YWJfc2V0AG1vZGlmeV9rZXlfZm9ybWF0AFNldEJhZGdlRm9ybWF0AElu
dmFsaWRGb3JtYXQAcHJpbnRfcmVwZWF0AHJvd3MAZGV2aWNlX3N0YXR1cwBsZWZ0X2FuZF9yaWdodF9tYXJnaW5fYW1iaWd1b3VzAFN0ZWFsRm9jdXMAcmVx
dWVzdHMAY2xpY2tfZXZlbnRzAGNsaXBib2FyZF9jb250ZW50cwBkeW5hbWljX3NlZ21lbnRzAGNyb3NzAHByb2dyZXNzAGNvbmVtdV9ydW5fcHJvY2VzcwBT
ZXRDb2xvcnMAZGVsZXRlX2NoYXJzAGVyYXNlX2NoYXJzAGN1cnNvcl9wb3MAaXRlbXMAcGFyYW1zAGR1cmF0aW9uX21zAGNvbHMAUG9wS2V5TGFiZWxzAFB1
c2hLZXlMYWJlbHMAaW5zZXJ0X2JsYW5rcwBoeXBlcmxpbmtfYnl0ZXMAc3RyaW5nX2J5dGVzAGdyYXBoZW1lX2J5dGVzAGRldmljZV9hdHRyaWJ1dGVzAGlu
dGVybWVkaWF0ZXMAaW5zZXJ0X2xpbmVzAGRlbGV0ZV9saW5lcwBzdHlsZXMAU3lzdGVtUmVzb3VyY2VzAGFsaWFzAGZsZXVyAGxlZnRfcHRyAG51bWVyYXRv
cgB0ZXJtaW5hdG9yAGRlbm9taW5hdG9yAHNhdmVfY3Vyc29yAHJlc3RvcmVfY3Vyc29yAGVycm9yAGtpdHR5X2tleWJvYXJkX3NldF9vcgBjcm9zc2hhaXIA
Q3VycmVudERpcgBJc0RpcgBEaXNpbnRlcgBwb2ludGVyAGNlbnRlcgBJbnZhbGlkQ2hhcmFjdGVyAHRvcF9yaWdodF9jb3JuZXIAYm90dG9tX3JpZ2h0X2Nv
cm5lcgB0b3BfbGVmdF9jb3JuZXIAYm90dG9tX2xlZnRfY29ybmVyAENvbm5lY3Rpb25SZXNldEJ5UGVlcgBTZXRVc2VyVmFyAGN1cnNvcl91cABzY3JvbGxf
dXAAdG9wAGRuZC1uby1kcm9wAHRpdGxlX3BvcABraXR0eV9rZXlib2FyZF9wb3AAaGVscABwYXJhbXNfc2VwAGNvbmVtdV9zbGVlcABlcmFzZV9saW5lX3Jp
Z2h0X3VubGVzc19wZW5kaW5nX3dyYXAAcHJvdGVjdGVkX21vZGVfaXNvAERpdmlzaW9uQnlaZXJvAGNvbmVtdV9ndWltYWNybwByZXF1ZXN0X21vZGVfdW5r
bm93bgBjdXJzb3JfZG93bgBzY3JvbGxfZG93bgBjYXJyaWFnZV9yZXR1cm4AQnV0dG9uAFJlcXVlc3RBdHRlbnRpb24AYWN0aW9uAHRlcm1pbmFsLlBhcnNl
ci5BY3Rpb24AVXRmOEV4cGVjdGVkQ29udGludWF0aW9uAEFkZEhpZGRlbkFubm90YXRpb24AQWRkQW5ub3RhdGlvbgBjb2xvcl9vcGVyYXRpb24ATWlzc2lu
Z09wZXJhdGlvbgBjb25lbXVfeHRlcm1fZW11bGF0aW9uAExvY2tWaW9sYXRpb24Ac2hvd19kZXNrdG9wX25vdGlmaWNhdGlvbgB4dHZlcnNpb24AU2hlbGxJ
bnRlZ3JhdGlvblZlcnNpb24AVW5pY29kZVZlcnNpb24AY2hhbmdlX3dpbmRvd19pY29uAGRlY2FsbgBsZWZ0X2FuZF9yaWdodF9tYXJnaW4AdG9wX2FuZF9i
b3R0b21fbWFyZ2luAHpvb20taW4AdmFsaWduAGhhbGlnbgBsZW4Ac2NyZWVuABtbMG4AeHRlcm0AYm90dG9tAEN1c3RvbQBFbmRPZlN0cmVhbQBlbmRfcHJv
bXB0X3N0YXJ0X2lucHV0X3Rlcm1pbmF0ZV9lb2wAa2l0dHlfY29sb3JfcHJvdG9jb2wAa2l0dHlfY2xpcGJvYXJkX3Byb3RvY29sAGN1cnNvcl9jb2wAbnVs
bABhbGwtc2Nyb2xsAGNlbGwAdmlzdWFsX2JlbGwAdGFiX2NsZWFyX2FsbABTZXRLZXlMYWJlbABjbABmaW5hbABjb250ZXh0X3NpZ25hbABtYXNrAFNldE1h
cmsAZGNzX3VuaG9vawBkY3NfaG9vawBzdGFydF9oeXBlcmxpbmsAZW5kX2h5cGVybGluawBkbmQtbGluawBXb3VsZEJsb2NrAGVyYXNlX2Rpc3BsYXlfc2Ny
b2xsYmFjawBDbGVhclNjcm9sbGJhY2sAaG9yaXpvbnRhbF90YWJfYmFjawB1cmkAd2lkdGgAdGl0bGVfcHVzaABraXR0eV9rZXlib2FyZF9wdXNoAEh5cGVy
bGlua1NldE5lZWRzUmVoYXNoAFN0eWxlU2V0TmVlZHNSZWhhc2gAbGVmdF9wdHJfd2F0Y2gAY3NpX2Rpc3BhdGNoAG9zY19kaXNwYXRjaABlc2NfZGlzcGF0
Y2gAa2l0dHlfdGV4dF9zaXppbmcAYWxsb2NhdGluZwBTdHJlYW1pbmcAVXRmOE92ZXJsb25nRW5jb2RpbmcATm90T3BlbkZvclJlYWRpbmcAZ3JhYmJpbmcA
SW52YWxpZEVudW1UYWcAVXRmOEVuY29kZXNTdXJyb2dhdGVIYWxmAFV0ZjhDYW5ub3RFbmNvZGVTdXJyb2dhdGVIYWxmAHByb3RlY3RlZF9tb2RlX29mZgBu
ZXN3LXJlc2l6ZQByb3ctcmVzaXplAG53LXJlc2l6ZQBldy1yZXNpemUAbnMtcmVzaXplAG4tcmVzaXplAGNvbC1yZXNpemUAbndzZS1yZXNpemUAbmUtcmVz
aXplAFJlcG9ydENlbGxTaXplAHJlbW92ZQBkbmQtbW92ZQBlcmFzZV9kaXNwbGF5X2Fib3ZlAGFjdGl2ZQBjdXJzb3Jfcm93X3JlbGF0aXZlAGN1cnNvcl9j
b2xfcmVsYXRpdmUAdHJ1ZQB2YWx1ZQBOb0RlZmF1bHRWYWx1ZQBJbnZhbGlkVmFsdWUAVXRmOEludmFsaWRTdGFydEJ5dGUAZXhlY3V0ZQBzZXRfYXR0cmli
dXRlAGVyYXNlX2Rpc3BsYXlfY29tcGxldGUAZXJhc2VfZGlzcGxheV9zY3JvbGxfY29tcGxldGUAZXJhc2VfbGluZV9jb21wbGV0ZQBzdGF0ZQBpbmRldGVy
bWluYXRlAHBhdXNlAGZhbHNlAG1vdXNlX3NoaWZ0X2NhcHR1cmUAQnJva2VuUGlwZQBtb3VzZV9zaGFwZQBDdXJzb3JTaGFwZQBuZXh0X2xpbmUAZnJlc2hf
bGluZQBIaWdobGlnaHRDdXJzb3JMaW5lAGN1cnNvcl9zdHlsZQBjaGFuZ2Vfd2luZG93X3RpdGxlAGNvbmVtdV9jaGFuZ2VfdGFiX3RpdGxlAFNldFByb2Zp
bGUATXVsdGlwYXJ0RmlsZQBTZXRCYWNrZ3JvdW5kSW1hZ2VGaWxlAGNyb3NzZWRfY2lyY2xlAFVuc2Vla2FibGUAY29uZW11X291dHB1dF9lbnZpcm9ubWVu
dF92YXJpYWJsZQBSZXBvcnRWYXJpYWJsZQBzY2FsZQBVdGY4Q29kZXBvaW50VG9vTGFyZ2UAcmVxdWVzdF9tb2RlAHJlc2V0X21vZGUAc2F2ZV9tb2RlAHJl
c3RvcmVfbW9kZQByaWdodF9zaWRlAGxlZnRfc2lkZQB0b3Bfc2lkZQBib3R0b21fc2lkZQBiYWNrc3BhY2UAT3V0T2ZTcGFjZQByZXBvcnRfcHdkAGtleWJv
YXJkAENvcHlUb0NsaXBib2FyZABzZWNvbmRfdHJhbnNwYXJlbnRfYmFja2dyb3VuZABzZWxlY3Rpb25fYmFja2dyb3VuZABzZWxlY3Rpb25fZm9yZWdyb3Vu
ZABQcm9jZXNzTm90Rm91bmQAa2luZABoeXBlcmxpbmtfZW5kAGFwY19lbmQAbmV3X2NvbW1hbmQAZW5kX2NvbW1hbmQAaGFuZABGaWxlRW5kAHZvaWQAaW52
YWxpZABmaXhlZABub3QtYWxsb3dlZABPcGVyYXRpb25BYm9ydGVkAFVuaW1wbGVtZW50ZWQAVW5leHBlY3RlZABTb2NrZXROb3RDb25uZWN0ZWQAb3B0aW9u
c191bnZhbGlkYXRlZABXcml0ZUZhaWxlZABSZWFkRmFpbGVkAENhbmNlbGVkAEFjY2Vzc0RlbmllZABQZXJtaXNzaW9uRGVuaWVkAGxpbmVmZWVkAHBheWxv
YWQAUmVxdWVzdFVwbG9hZABwcm90ZWN0ZWRfbW9kZV9kZWMAG1s/NjI7MjI7NTJjABtbPjE7MTA7MGMAcmdiAGhvcml6b250YWxfdGFiAGdyYWIAbWV0YWRh
dGEAG1wAT3BlblVSTAA/AHN0YXJ0PQBlbmQ9ADAwMDEwMjAzMDQwNTA2MDcwODA5MTAxMTEyMTMxNDE1MTYxNzE4MTkyMDIxMjIyMzI0MjUyNjI3MjgyOTMw
MzEzMjMzMzQzNTM2MzczODM5NDA0MTQyNDM0NDQ1NDY0NzQ4NDk1MDUxNTI1MzU0NTU1NjU3NTg1OTYwNjE2MjYzNjQ2NTY2Njc2ODY5NzA3MTcyNzM3NDc1
NzY3Nzc4Nzk4MDgxODI4Mzg0ODU4Njg3ODg4OTkwOTE5MjkzOTQ5NTk2OTc5ODk5AG9zY18xOQBvc2NfMTE5AEludmFsaWRVdGY4AG9zY18xOABvc2NfMTE4
ADMwMDgANzc3ADEzMzcAb3NjXzE3AG9zY18xMTcANjYAb3NjXzE2AG9zY18xMTYAb3NjXzUANTUAMjI3MzczNjc1NDQzMjMyMDU5NDc4NzU5NzY1NjI1ADEy
MzI1OTUxNjQ0MDc4MzA5NDU5NTU4MjU4ODMyNTQzNTM0ODM4NjQzODUwNTQ4NTc4NDg0NDQ5NTM1NjA4MjkxNjI1OTc2NTYyNQA1MjkzOTU1OTIwMzM5Mzc3
MTE5MTc3MDE1NjI5MjQ3NzYyMjYyODIxMTk3NTA5NzY1NjI1ADgwNzc5MzU2Njk0NjMxNjA4ODc0MTYxMDA1MDg0OTU3MzA5OTE4NTM2MzM4OTU1MTYzOTU1
Njg4NDc2NTYyNQAxNDkwMTE2MTE5Mzg0NzY1NjI1ADE4ODA3OTA5NjEzMTU2NjAwMTI3NDk5Nzg0NTk1NTU1OTMwODQ1MDk4NjQ4OTA4MzUzNDAwMzQ0MTQw
MDI3MzAwNDU0Njc2MTUxMjc1NjM0NzY1NjI1ADM0Njk0NDY5NTE5NTM2MTQxODg4MjM4NDg5NjI3ODM4MTM0NzY1NjI1ADM4MTQ2OTcyNjU2MjUAMjA2Nzk1
MTUzMTM4MjU2OTE4NzE3ODUyMTczMDE3NDkwNzEzMzkxNDUzMDI3NzI1MjE5NzI2NTYyNQA0ODE0ODI0ODYwOTY4MDg5NjMyNjM5OTQ0ODU2NDYyMzE4Mjk2
MzQ1MjU0MTIwNTM4NDcwNDg4MDk5ODQ2OTg4OTE2Mzk3MDk0NzI2NTYyNQA4ODgxNzg0MTk3MDAxMjUyMzIzMzg5MDUzMzQ0NzI2NTYyNQAzMTU1NDQzNjIw
ODg0MDQ3MjIxNjQ2OTE0MjYxMTMxMTQ0OTE4NjkyODI1NzQwNDM2MDkyMDE5MDgxMTE1NzIyNjU2MjUANTgyMDc2NjA5MTM0Njc0MDcyMjY1NjI1ADEzNTUy
NTI3MTU2MDY4ODA1NDI1MDkzMTYwMDEwODc0MjcxMzkyODIyMjY1NjI1ADUwNDg3MDk3OTM0MTQ0NzU1NTQ2MzUwNjI4MTc4MDk4MzE4Njk5MDg1MjExODQ2
OTc3NDcyMzA1Mjk3ODUxNTYyNQA5MzEzMjI1NzQ2MTU0Nzg1MTU2MjUAMTE3NTQ5NDM1MDgyMjI4NzUwNzk2ODczNjUzNzIyMjI0NTY3NzgxODY2NTU1Njc3
MjA4NzUyMTUwODc1MTcwNjI3ODQxNzI1OTQ1NDcyNzE3Mjg1MTU2MjUAMjE2ODQwNDM0NDk3MTAwODg2ODAxNDkwNTYwMTczOTg4MzQyMjg1MTU2MjUAMTQy
MTA4NTQ3MTUyMDIwMDM3MTc0MjI0ODUzNTE1NjI1ADc3MDM3MTk3Nzc1NDg5NDM0MTIyMjM5MTE3NzAzMzk3MDkyNzQxNTI0MDY1OTI4NjE1NTI3ODA5NTk3
NTUxODIyNjYyMzUzNTE1NjI1ADMzMDg3MjI0NTAyMTIxMTA2OTk0ODU2MzQ3NjgyNzk4NTE0MTQyNjMyNDg0NDM2MDM1MTU2MjUANjEwMzUxNTYyNQAxOTcy
MTUyMjYzMDUyNTI5NTEzNTI5MzIxNDEzMjA2OTY1NTc0MTgzMDE2MDg3NzcyNTU3NTExOTI1Njk3MzI2NjYwMTU2MjUAMzYzNzk3ODgwNzA5MTcxMjk1MTY2
MDE1NjI1ADg0NzAzMjk0NzI1NDMwMDMzOTA2ODMyMjUwMDY3OTY0MTk2MjA1MTM5MTYwMTU2MjUAMjM4NDE4NTc5MTAxNTYyNQAxMjkyNDY5NzA3MTE0MTA1
NzQxOTg2NTc2MDgxMzU5MzE2OTU4Njk2NTgxNDIzMjgyNjIzMjkxMDE1NjI1ADU1NTExMTUxMjMxMjU3ODI3MDIxMTgxNTgzNDA0NTQxMDE1NjI1ADMwMDky
NjU1MzgxMDUwNTYwMjAzOTk5NjU1MzUyODg5NDg5MzUyMTU3ODM4MjUzMzY1NDQwNTUwNjI0MDQzNjgwNzI3NDgxODQyMDQxMDE1NjI1ADE1MjU4Nzg5MDYy
NQA4MjcxODA2MTI1NTMwMjc2NzQ4NzE0MDg2OTIwNjk5NjI4NTM1NjU4MTIxMTA5MDA4Nzg5MDYyNQAxOTI1OTI5OTQ0Mzg3MjM1ODUzMDU1OTc3OTQyNTg0
OTI3MzE4NTM4MTAxNjQ4MjE1Mzg4MTk1MjM5OTM4Nzk1NTY2NTU4ODM3ODkwNjI1ADM1NTI3MTM2Nzg4MDA1MDA5MjkzNTU2MjEzMzc4OTA2MjUAMjMyODMw
NjQzNjUzODY5NjI4OTA2MjUAMTI2MjE3NzQ0ODM1MzYxODg4ODY1ODc2NTcwNDQ1MjQ1Nzk2NzQ3NzEzMDI5NjE3NDQzNjgwNzYzMjQ0NjI4OTA2MjUANTQy
MTAxMDg2MjQyNzUyMjE3MDAzNzI2NDAwNDM0OTcwODU1NzEyODkwNjI1ADU5NjA0NjQ0Nzc1MzkwNjI1ADMyMzExNzQyNjc3ODUyNjQzNTQ5NjY0NDAyMDMz
OTgyOTIzOTY3NDE0NTM1NTgyMDY1NTgyMjc1MzkwNjI1ADEzODc3Nzg3ODA3ODE0NDU2NzU1Mjk1Mzk1ODUxMTM1MjUzOTA2MjUANzUyMzE2Mzg0NTI2MjY0
MDA1MDk5OTkxMzgzODIyMjM3MjMzODAzOTQ1OTU2MzM0MTM2MDEzNzY1NjAxMDkyMDE4MTg3MDQ2MDUxMDI1MzkwNjI1ADQ5MzAzODA2NTc2MzEzMjM3ODM4
MjMzMDM1MzMwMTc0MTM5MzU0NTc1NDAyMTk0MzEzOTM3Nzk4MTQyNDMzMTY2NTAzOTA2MjUAOTA5NDk0NzAxNzcyOTI4MjM3OTE1MDM5MDYyNQAyMTE3NTgy
MzY4MTM1NzUwODQ3NjcwODA2MjUxNjk5MTA0OTA1MTI4NDc5MDAzOTA2MjUAMzM4ODEzMTc4OTAxNzIwMTM1NjI3MzI5MDAwMjcxODU2Nzg0ODIwNTU2NjQw
NjI1ADE0NTUxOTE1MjI4MzY2ODUxODA2NjQwNjI1ADc4ODg2MDkwNTIyMTAxMTgwNTQxMTcyODU2NTI4Mjc4NjIyOTY3MzIwNjQzNTEwOTAyMzAwNDc3MDI3
ODkzMDY2NDA2MjUAMTIwMzcwNjIxNTI0MjAyMjQwODE1OTk4NjIxNDExNTU3OTU3NDA4NjMxMzUzMDEzNDYxNzYyMjAyNDk2MTc0NzIyOTA5OTI3MzY4MTY0
MDYyNQAyMjIwNDQ2MDQ5MjUwMzEzMDgwODQ3MjYzMzM2MTgxNjQwNjI1ADUxNjk4Nzg4Mjg0NTY0MjI5Njc5NDYzMDQzMjU0MzcyNjc4MzQ3ODYzMjU2OTMx
MzA0OTMxNjQwNjI1ADk1MzY3NDMxNjQwNjI1ADQ3MDE5Nzc0MDMyODkxNTAwMzE4NzQ5NDYxNDg4ODg5ODI3MTEyNzQ2NjIyMjcwODgzNTAwODYwMzUwMDY4
MjUxMTM2NjkwMzc4MTg5MDg2OTE0MDYyNQA4NjczNjE3Mzc5ODg0MDM1NDcyMDU5NjIyNDA2OTU5NTMzNjkxNDA2MjUAMzcyNTI5MDI5ODQ2MTkxNDA2MjUA
MjAxOTQ4MzkxNzM2NTc5MDIyMTg1NDAyNTEyNzEyMzkzMjc0Nzk2MzQwODQ3Mzg3OTA5ODg5MjIxMTkxNDA2MjUANTY4NDM0MTg4NjA4MDgwMTQ4Njk2ODk5
NDE0MDYyNQAzMDgxNDg3OTExMDE5NTc3MzY0ODg5NTY0NzA4MTM1ODgzNzA5NjYwOTYyNjM3MTQ0NjIxMTEyMzgzOTAyMDcyOTA2NDk0MTQwNjI1ADEzMjM0
ODg5ODAwODQ4NDQyNzk3OTQyNTM5MDczMTE5NDA1NjU3MDUyOTkzNzc0NDE0MDYyNQAyNDQxNDA2MjUAMzg1MTg1OTg4ODc3NDQ3MTcwNjExMTk1NTg4NTE2
OTg1NDYzNzA3NjIwMzI5NjQzMDc3NjM5MDQ3OTg3NzU5MTEzMzExNzY3NTc4MTI1ADcxMDU0MjczNTc2MDEwMDE4NTg3MTEyNDI2NzU3ODEyNQAzMDUxNzU3
ODEyNQAxNjU0MzYxMjI1MTA2MDU1MzQ5NzQyODE3Mzg0MTM5OTI1NzA3MTMxNjI0MjIxODAxNzU3ODEyNQAyNTI0MzU0ODk2NzA3MjM3Nzc3MzE3NTMxNDA4
OTA0OTE1OTM0OTU0MjYwNTkyMzQ4ODczNjE1MjY0ODkyNTc4MTI1ADQ2NTY2MTI4NzMwNzczOTI1NzgxMjUANTg3NzQ3MTc1NDExMTQzNzUzOTg0MzY4MjY4
NjExMTIyODM4OTA5MzMyNzc4Mzg2MDQzNzYwNzU0Mzc1ODUzMTM5MjA4NjI5NzI3MzYzNTg2NDI1NzgxMjUAMTA4NDIwMjE3MjQ4NTUwNDQzNDAwNzQ1Mjgw
MDg2OTk0MTcxMTQyNTc4MTI1ADExOTIwOTI4OTU1MDc4MTI1ADY0NjIzNDg1MzU1NzA1Mjg3MDk5MzI4ODA0MDY3OTY1ODQ3OTM0ODI5MDcxMTY0MTMxMTY0
NTUwNzgxMjUAMjc3NTU1NzU2MTU2Mjg5MTM1MTA1OTA3OTE3MDIyNzA1MDc4MTI1ADE1MDQ2MzI3NjkwNTI1MjgwMTAxOTk5ODI3Njc2NDQ0NzQ0Njc2MDc4
OTE5MTI2NjgyNzIwMjc1MzEyMDIxODQwMzYzNzQwOTIxMDIwNTA3ODEyNQA0MjM1MTY0NzM2MjcxNTAxNjk1MzQxNjEyNTAzMzk4MjA5ODEwMjU2OTU4MDA3
ODEyNQAxODE4OTg5NDAzNTQ1ODU2NDc1ODMwMDc4MTI1ADk4NjA3NjEzMTUyNjI2NDc1Njc2NDY2MDcwNjYwMzQ4Mjc4NzA5MTUwODA0Mzg4NjI3ODc1NTk2
Mjg0ODY2MzMzMDA3ODEyNQAxMTM2ODY4Mzc3MjE2MTYwMjk3MzkzNzk4ODI4MTI1ADYxNjI5NzU4MjIwMzkxNTQ3Mjk3NzkxMjk0MTYyNzE3Njc0MTkzMjE5
MjUyNzQyODkyNDIyMjQ3Njc4MDQxNDU4MTI5ODgyODEyNQAyNjQ2OTc3OTYwMTY5Njg4NTU5NTg4NTA3ODE0NjIzODgxMTMxNDEwNTk4NzU0ODgyODEyNQAx
NzM0NzIzNDc1OTc2ODA3MDk0NDExOTI0NDgxMzkxOTA2NzM4MjgxMjUAOTQwMzk1NDgwNjU3ODMwMDA2Mzc0OTg5MjI5Nzc3Nzk2NTQyMjU0OTMyNDQ1NDE3
NjcwMDE3MjA3MDAxMzY1MDIyNzMzODA3NTYzNzgxNzM4MjgxMjUANzQ1MDU4MDU5NjkyMzgyODEyNQA0MDM4OTY3ODM0NzMxNTgwNDQzNzA4MDUwMjU0MjQ3
ODY1NDk1OTI2ODE2OTQ3NzU4MTk3Nzg0NDIzODI4MTI1ADEwMzM5NzU3NjU2OTEyODQ1OTM1ODkyNjA4NjUwODc0NTM1NjY5NTcyNjUxMzg2MjYwOTg2MzI4
MTI1ADE5MDczNDg2MzI4MTI1ADI0MDc0MTI0MzA0ODQwNDQ4MTYzMTk5NzI0MjgyMzExNTkxNDgxNzI2MjcwNjAyNjkyMzUyNDQwNDk5MjM0OTQ0NTgxOTg1
NDczNjMyODEyNQA0NDQwODkyMDk4NTAwNjI2MTYxNjk0NTI2NjcyMzYzMjgxMjUAMTU3NzcyMTgxMDQ0MjAyMzYxMDgyMzQ1NzEzMDU2NTU3MjQ1OTM0NjQx
Mjg3MDIxODA0NjAwOTU0MDU1Nzg2MTMyODEyNQAyOTEwMzgzMDQ1NjczMzcwMzYxMzI4MTI1ADY3NzYyNjM1NzgwMzQ0MDI3MTI1NDY1ODAwMDU0MzcxMzU2
OTY0MTExMzI4MTI1ADI5ODAyMzIyMzg3Njk1MzEyNQAxNjE1NTg3MTMzODkyNjMyMTc3NDgzMjIwMTAxNjk5MTQ2MTk4MzcwNzI2Nzc5MTAzMjc5MTEzNzY5
NTMxMjUANjkzODg5MzkwMzkwNzIyODM3NzY0NzY5NzkyNTU2NzYyNjk1MzEyNQAzNzYxNTgxOTIyNjMxMzIwMDI1NDk5OTU2OTE5MTExMTg2MTY5MDE5NzI5
NzgxNjcwNjgwMDY4ODI4MDA1NDYwMDkwOTM1MjMwMjU1MTI2OTUzMTI1ADQ1NDc0NzM1MDg4NjQ2NDExODk1NzUxOTUzMTI1ADI0NjUxOTAzMjg4MTU2NjE4
OTE5MTE2NTE3NjY1MDg3MDY5Njc3Mjg3NzAxMDk3MTU2OTY4ODk5MDcxMjE2NTgzMjUxOTUzMTI1ADEwNTg3OTExODQwNjc4NzU0MjM4MzU0MDMxMjU4NDk1
NTI0NTI1NjQyMzk1MDE5NTMxMjUAMTc3NjM1NjgzOTQwMDI1MDQ2NDY3NzgxMDY2ODk0NTMxMjUAOTYyOTY0OTcyMTkzNjE3OTI2NTI3OTg4OTcxMjkyNDYz
NjU5MjY5MDUwODI0MTA3Njk0MDk3NjE5OTY5Mzk3NzgzMjc5NDE4OTQ1MzEyNQA3NjI5Mzk0NTMxMjUANDEzNTkwMzA2Mjc2NTEzODM3NDM1NzA0MzQ2MDM0
OTgxNDI2NzgyOTA2MDU1NDUwNDM5NDUzMTI1ADI3MTA1MDU0MzEyMTM3NjEwODUwMTg2MzIwMDIxNzQ4NTQyNzg1NjQ0NTMxMjUAMTE2NDE1MzIxODI2OTM0
ODE0NDUzMTI1ADYzMTA4ODcyNDE3NjgwOTQ0NDMyOTM4Mjg1MjIyNjIyODk4MzczODU2NTE0ODA4NzIxODQwMzgxNjIyMzE0NDUzMTI1ADEwMDk3NDE5NTg2
ODI4OTUxMTA5MjcwMTI1NjM1NjE5NjYzNzM5ODE3MDQyMzY5Mzk1NDk0NDYxMDU5NTcwMzEyNQAxODYyNjQ1MTQ5MjMwOTU3MDMxMjUANDMzNjgwODY4OTk0
MjAxNzczNjAyOTgxMTIwMzQ3OTc2Njg0NTcwMzEyNQAyMzUwOTg4NzAxNjQ0NTc1MDE1OTM3NDczMDc0NDQ0NDkxMzU1NjM3MzMxMTEzNTQ0MTc1MDQzMDE3
NTAzNDEyNTU2ODM0NTE4OTA5NDU0MzQ1NzAzMTI1ADI4NDIxNzA5NDMwNDA0MDA3NDM0ODQ0OTcwNzAzMTI1ADE1NDA3NDM5NTU1MDk3ODg2ODI0NDQ3ODIz
NTQwNjc5NDE4NTQ4MzA0ODEzMTg1NzIzMTA1NTYxOTE5NTEwMzY0NTMyNDcwNzAzMTI1ADY2MTc0NDQ5MDA0MjQyMjEzOTg5NzEyNjk1MzY1NTk3MDI4Mjg1
MjY0OTY4ODcyMDcwMzEyNQAxMjIwNzAzMTI1ADI1ODQ5Mzk0MTQyMjgyMTE0ODM5NzMxNTIxNjI3MTg2MzM5MTczOTMxNjI4NDY1NjUyNDY1ODIwMzEyNQA0
NzY4MzcxNTgyMDMxMjUAMTExMDIyMzAyNDYyNTE1NjU0MDQyMzYzMTY2ODA5MDgyMDMxMjUANjAxODUzMTA3NjIxMDExMjA0MDc5OTkzMTA3MDU3Nzg5Nzg3
MDQzMTU2NzY1MDY3MzA4ODExMDEyNDgwODczNjE0NTQ5NjM2ODQwODIwMzEyNQAxNjk0MDY1ODk0NTA4NjAwNjc4MTM2NjQ1MDAxMzU5MjgzOTI0MTAyNzgz
MjAzMTI1ADM5NDQzMDQ1MjYxMDUwNTkwMjcwNTg2NDI4MjY0MTM5MzExNDgzNjYwMzIxNzU1NDUxMTUwMjM4NTEzOTQ2NTMzMjAzMTI1ADcyNzU5NTc2MTQx
ODM0MjU5MDMzMjAzMTI1AG9zY18xNQBvc2NfMTE1AG9zY18xMDUAb3NjXzQAb3NjXzE0AG9zY18xMTQAb3NjXzEwNAAxMzMAb3NjXzEzAG9zY18xMTMANTUy
ADU1MjIAb3NjXzEyAG9zY18xMTIAaGFuZDEAMjEAb3NjXzExAG9zY18xMTEAMzAAb3NjXzEwAG9zY18xMTAAMzAwAGVycm9yLgAueyAuACwgLgAobXNnIHRy
dW5jYXRlZCkAQGVudW1Gcm9tSW50KAB7IAAgPSAALCAAMjU1IDI1MCAyNTAJCXNub3cKMjQ4IDI0OCAyNTUJCWdob3N0IHdoaXRlCjI0OCAyNDggMjU1CQlH
aG9zdFdoaXRlCjI0NSAyNDUgMjQ1CQl3aGl0ZSBzbW9rZQoyNDUgMjQ1IDI0NQkJV2hpdGVTbW9rZQoyMjAgMjIwIDIyMAkJZ2FpbnNib3JvCjI1NSAyNTAg
MjQwCQlmbG9yYWwgd2hpdGUKMjU1IDI1MCAyNDAJCUZsb3JhbFdoaXRlCjI1MyAyNDUgMjMwCQlvbGQgbGFjZQoyNTMgMjQ1IDIzMAkJT2xkTGFjZQoyNTAg
MjQwIDIzMAkJbGluZW4KMjUwIDIzNSAyMTUJCWFudGlxdWUgd2hpdGUKMjUwIDIzNSAyMTUJCUFudGlxdWVXaGl0ZQoyNTUgMjM5IDIxMwkJcGFwYXlhIHdo
aXAKMjU1IDIzOSAyMTMJCVBhcGF5YVdoaXAKMjU1IDIzNSAyMDUJCWJsYW5jaGVkIGFsbW9uZAoyNTUgMjM1IDIwNQkJQmxhbmNoZWRBbG1vbmQKMjU1IDIy
OCAxOTYJCWJpc3F1ZQoyNTUgMjE4IDE4NQkJcGVhY2ggcHVmZgoyNTUgMjE4IDE4NQkJUGVhY2hQdWZmCjI1NSAyMjIgMTczCQluYXZham8gd2hpdGUKMjU1
IDIyMiAxNzMJCU5hdmFqb1doaXRlCjI1NSAyMjggMTgxCQltb2NjYXNpbgoyNTUgMjQ4IDIyMAkJY29ybnNpbGsKMjU1IDI1NSAyNDAJCWl2b3J5CjI1NSAy
NTAgMjA1CQlsZW1vbiBjaGlmZm9uCjI1NSAyNTAgMjA1CQlMZW1vbkNoaWZmb24KMjU1IDI0NSAyMzgJCXNlYXNoZWxsCjI0MCAyNTUgMjQwCQlob25leWRl
dwoyNDUgMjU1IDI1MAkJbWludCBjcmVhbQoyNDUgMjU1IDI1MAkJTWludENyZWFtCjI0MCAyNTUgMjU1CQlhenVyZQoyNDAgMjQ4IDI1NQkJYWxpY2UgYmx1
ZQoyNDAgMjQ4IDI1NQkJQWxpY2VCbHVlCjIzMCAyMzAgMjUwCQlsYXZlbmRlcgoyNTUgMjQwIDI0NQkJbGF2ZW5kZXIgYmx1c2gKMjU1IDI0MCAyNDUJCUxh
dmVuZGVyQmx1c2gKMjU1IDIyOCAyMjUJCW1pc3R5IHJvc2UKMjU1IDIyOCAyMjUJCU1pc3R5Um9zZQoyNTUgMjU1IDI1NQkJd2hpdGUKICAwICAgMCAgIDAJ
CWJsYWNrCiA0NyAgNzkgIDc5CQlkYXJrIHNsYXRlIGdyYXkKIDQ3ICA3OSAgNzkJCURhcmtTbGF0ZUdyYXkKIDQ3ICA3OSAgNzkJCWRhcmsgc2xhdGUgZ3Jl
eQogNDcgIDc5ICA3OQkJRGFya1NsYXRlR3JleQoxMDUgMTA1IDEwNQkJZGltIGdyYXkKMTA1IDEwNSAxMDUJCURpbUdyYXkKMTA1IDEwNSAxMDUJCWRpbSBn
cmV5CjEwNSAxMDUgMTA1CQlEaW1HcmV5CjExMiAxMjggMTQ0CQlzbGF0ZSBncmF5CjExMiAxMjggMTQ0CQlTbGF0ZUdyYXkKMTEyIDEyOCAxNDQJCXNsYXRl
IGdyZXkKMTEyIDEyOCAxNDQJCVNsYXRlR3JleQoxMTkgMTM2IDE1MwkJbGlnaHQgc2xhdGUgZ3JheQoxMTkgMTM2IDE1MwkJTGlnaHRTbGF0ZUdyYXkKMTE5
IDEzNiAxNTMJCWxpZ2h0IHNsYXRlIGdyZXkKMTE5IDEzNiAxNTMJCUxpZ2h0U2xhdGVHcmV5CjE5MCAxOTAgMTkwCQlncmF5CjE5MCAxOTAgMTkwCQlncmV5
CjE5MCAxOTAgMTkwCQl4MTEgZ3JheQoxOTAgMTkwIDE5MAkJWDExR3JheQoxOTAgMTkwIDE5MAkJeDExIGdyZXkKMTkwIDE5MCAxOTAJCVgxMUdyZXkKMTI4
IDEyOCAxMjgJCXdlYiBncmF5CjEyOCAxMjggMTI4CQlXZWJHcmF5CjEyOCAxMjggMTI4CQl3ZWIgZ3JleQoxMjggMTI4IDEyOAkJV2ViR3JleQoyMTEgMjEx
IDIxMQkJbGlnaHQgZ3JleQoyMTEgMjExIDIxMQkJTGlnaHRHcmV5CjIxMSAyMTEgMjExCQlsaWdodCBncmF5CjIxMSAyMTEgMjExCQlMaWdodEdyYXkKIDI1
ICAyNSAxMTIJCW1pZG5pZ2h0IGJsdWUKIDI1ICAyNSAxMTIJCU1pZG5pZ2h0Qmx1ZQogIDAgICAwIDEyOAkJbmF2eQogIDAgICAwIDEyOAkJbmF2eSBibHVl
CiAgMCAgIDAgMTI4CQlOYXZ5Qmx1ZQoxMDAgMTQ5IDIzNwkJY29ybmZsb3dlciBibHVlCjEwMCAxNDkgMjM3CQlDb3JuZmxvd2VyQmx1ZQogNzIgIDYxIDEz
OQkJZGFyayBzbGF0ZSBibHVlCiA3MiAgNjEgMTM5CQlEYXJrU2xhdGVCbHVlCjEwNiAgOTAgMjA1CQlzbGF0ZSBibHVlCjEwNiAgOTAgMjA1CQlTbGF0ZUJs
dWUKMTIzIDEwNCAyMzgJCW1lZGl1bSBzbGF0ZSBibHVlCjEyMyAxMDQgMjM4CQlNZWRpdW1TbGF0ZUJsdWUKMTMyIDExMiAyNTUJCWxpZ2h0IHNsYXRlIGJs
dWUKMTMyIDExMiAyNTUJCUxpZ2h0U2xhdGVCbHVlCiAgMCAgIDAgMjA1CQltZWRpdW0gYmx1ZQogIDAgICAwIDIwNQkJTWVkaXVtQmx1ZQogNjUgMTA1IDIy
NQkJcm95YWwgYmx1ZQogNjUgMTA1IDIyNQkJUm95YWxCbHVlCiAgMCAgIDAgMjU1CQlibHVlCiAzMCAxNDQgMjU1CQlkb2RnZXIgYmx1ZQogMzAgMTQ0IDI1
NQkJRG9kZ2VyQmx1ZQogIDAgMTkxIDI1NQkJZGVlcCBza3kgYmx1ZQogIDAgMTkxIDI1NQkJRGVlcFNreUJsdWUKMTM1IDIwNiAyMzUJCXNreSBibHVlCjEz
NSAyMDYgMjM1CQlTa3lCbHVlCjEzNSAyMDYgMjUwCQlsaWdodCBza3kgYmx1ZQoxMzUgMjA2IDI1MAkJTGlnaHRTa3lCbHVlCiA3MCAxMzAgMTgwCQlzdGVl
bCBibHVlCiA3MCAxMzAgMTgwCQlTdGVlbEJsdWUKMTc2IDE5NiAyMjIJCWxpZ2h0IHN0ZWVsIGJsdWUKMTc2IDE5NiAyMjIJCUxpZ2h0U3RlZWxCbHVlCjE3
MyAyMTYgMjMwCQlsaWdodCBibHVlCjE3MyAyMTYgMjMwCQlMaWdodEJsdWUKMTc2IDIyNCAyMzAJCXBvd2RlciBibHVlCjE3NiAyMjQgMjMwCQlQb3dkZXJC
bHVlCjE3NSAyMzggMjM4CQlwYWxlIHR1cnF1b2lzZQoxNzUgMjM4IDIzOAkJUGFsZVR1cnF1b2lzZQogIDAgMjA2IDIwOQkJZGFyayB0dXJxdW9pc2UKICAw
IDIwNiAyMDkJCURhcmtUdXJxdW9pc2UKIDcyIDIwOSAyMDQJCW1lZGl1bSB0dXJxdW9pc2UKIDcyIDIwOSAyMDQJCU1lZGl1bVR1cnF1b2lzZQogNjQgMjI0
IDIwOAkJdHVycXVvaXNlCiAgMCAyNTUgMjU1CQljeWFuCiAgMCAyNTUgMjU1CQlhcXVhCjIyNCAyNTUgMjU1CQlsaWdodCBjeWFuCjIyNCAyNTUgMjU1CQlM
aWdodEN5YW4KIDk1IDE1OCAxNjAJCWNhZGV0IGJsdWUKIDk1IDE1OCAxNjAJCUNhZGV0Qmx1ZQoxMDIgMjA1IDE3MAkJbWVkaXVtIGFxdWFtYXJpbmUKMTAy
IDIwNSAxNzAJCU1lZGl1bUFxdWFtYXJpbmUKMTI3IDI1NSAyMTIJCWFxdWFtYXJpbmUKICAwIDEwMCAgIDAJCWRhcmsgZ3JlZW4KICAwIDEwMCAgIDAJCURh
cmtHcmVlbgogODUgMTA3ICA0NwkJZGFyayBvbGl2ZSBncmVlbgogODUgMTA3ICA0NwkJRGFya09saXZlR3JlZW4KMTQzIDE4OCAxNDMJCWRhcmsgc2VhIGdy
ZWVuCjE0MyAxODggMTQzCQlEYXJrU2VhR3JlZW4KIDQ2IDEzOSAgODcJCXNlYSBncmVlbgogNDYgMTM5ICA4NwkJU2VhR3JlZW4KIDYwIDE3OSAxMTMJCW1l
ZGl1bSBzZWEgZ3JlZW4KIDYwIDE3OSAxMTMJCU1lZGl1bVNlYUdyZWVuCiAzMiAxNzggMTcwCQlsaWdodCBzZWEgZ3JlZW4KIDMyIDE3OCAxNzAJCUxpZ2h0
U2VhR3JlZW4KMTUyIDI1MSAxNTIJCXBhbGUgZ3JlZW4KMTUyIDI1MSAxNTIJCVBhbGVHcmVlbgogIDAgMjU1IDEyNwkJc3ByaW5nIGdyZWVuCiAgMCAyNTUg
MTI3CQlTcHJpbmdHcmVlbgoxMjQgMjUyICAgMAkJbGF3biBncmVlbgoxMjQgMjUyICAgMAkJTGF3bkdyZWVuCiAgMCAyNTUgICAwCQlncmVlbgogIDAgMjU1
ICAgMAkJbGltZQogIDAgMjU1ICAgMAkJeDExIGdyZWVuCiAgMCAyNTUgICAwCQlYMTFHcmVlbgogIDAgMTI4ICAgMAkJd2ViIGdyZWVuCiAgMCAxMjggICAw
CQlXZWJHcmVlbgoxMjcgMjU1ICAgMAkJY2hhcnRyZXVzZQogIDAgMjUwIDE1NAkJbWVkaXVtIHNwcmluZyBncmVlbgogIDAgMjUwIDE1NAkJTWVkaXVtU3By
aW5nR3JlZW4KMTczIDI1NSAgNDcJCWdyZWVuIHllbGxvdwoxNzMgMjU1ICA0NwkJR3JlZW5ZZWxsb3cKIDUwIDIwNSAgNTAJCWxpbWUgZ3JlZW4KIDUwIDIw
NSAgNTAJCUxpbWVHcmVlbgoxNTQgMjA1ICA1MAkJeWVsbG93IGdyZWVuCjE1NCAyMDUgIDUwCQlZZWxsb3dHcmVlbgogMzQgMTM5ICAzNAkJZm9yZXN0IGdy
ZWVuCiAzNCAxMzkgIDM0CQlGb3Jlc3RHcmVlbgoxMDcgMTQyICAzNQkJb2xpdmUgZHJhYgoxMDcgMTQyICAzNQkJT2xpdmVEcmFiCjE4OSAxODMgMTA3CQlk
YXJrIGtoYWtpCjE4OSAxODMgMTA3CQlEYXJrS2hha2kKMjQwIDIzMCAxNDAJCWtoYWtpCjIzOCAyMzIgMTcwCQlwYWxlIGdvbGRlbnJvZAoyMzggMjMyIDE3
MAkJUGFsZUdvbGRlbnJvZAoyNTAgMjUwIDIxMAkJbGlnaHQgZ29sZGVucm9kIHllbGxvdwoyNTAgMjUwIDIxMAkJTGlnaHRHb2xkZW5yb2RZZWxsb3cKMjU1
IDI1NSAyMjQJCWxpZ2h0IHllbGxvdwoyNTUgMjU1IDIyNAkJTGlnaHRZZWxsb3cKMjU1IDI1NSAgIDAJCXllbGxvdwoyNTUgMjE1ICAgMAkJZ29sZAoyMzgg
MjIxIDEzMAkJbGlnaHQgZ29sZGVucm9kCjIzOCAyMjEgMTMwCQlMaWdodEdvbGRlbnJvZAoyMTggMTY1ICAzMgkJZ29sZGVucm9kCjE4NCAxMzQgIDExCQlk
YXJrIGdvbGRlbnJvZAoxODQgMTM0ICAxMQkJRGFya0dvbGRlbnJvZAoxODggMTQzIDE0MwkJcm9zeSBicm93bgoxODggMTQzIDE0MwkJUm9zeUJyb3duCjIw
NSAgOTIgIDkyCQlpbmRpYW4gcmVkCjIwNSAgOTIgIDkyCQlJbmRpYW5SZWQKMTM5ICA2OSAgMTkJCXNhZGRsZSBicm93bgoxMzkgIDY5ICAxOQkJU2FkZGxl
QnJvd24KMTYwICA4MiAgNDUJCXNpZW5uYQoyMDUgMTMzICA2MwkJcGVydQoyMjIgMTg0IDEzNQkJYnVybHl3b29kCjI0NSAyNDUgMjIwCQliZWlnZQoyNDUg
MjIyIDE3OQkJd2hlYXQKMjQ0IDE2NCAgOTYJCXNhbmR5IGJyb3duCjI0NCAxNjQgIDk2CQlTYW5keUJyb3duCjIxMCAxODAgMTQwCQl0YW4KMjEwIDEwNSAg
MzAJCWNob2NvbGF0ZQoxNzggIDM0ICAzNAkJZmlyZWJyaWNrCjE2NSAgNDIgIDQyCQlicm93bgoyMzMgMTUwIDEyMgkJZGFyayBzYWxtb24KMjMzIDE1MCAx
MjIJCURhcmtTYWxtb24KMjUwIDEyOCAxMTQJCXNhbG1vbgoyNTUgMTYwIDEyMgkJbGlnaHQgc2FsbW9uCjI1NSAxNjAgMTIyCQlMaWdodFNhbG1vbgoyNTUg
MTY1ICAgMAkJb3JhbmdlCjI1NSAxNDAgICAwCQlkYXJrIG9yYW5nZQoyNTUgMTQwICAgMAkJRGFya09yYW5nZQoyNTUgMTI3ICA4MAkJY29yYWwKMjQwIDEy
OCAxMjgJCWxpZ2h0IGNvcmFsCjI0MCAxMjggMTI4CQlMaWdodENvcmFsCjI1NSAgOTkgIDcxCQl0b21hdG8KMjU1ICA2OSAgIDAJCW9yYW5nZSByZWQKMjU1
ICA2OSAgIDAJCU9yYW5nZVJlZAoyNTUgICAwICAgMAkJcmVkCjI1NSAxMDUgMTgwCQlob3QgcGluawoyNTUgMTA1IDE4MAkJSG90UGluawoyNTUgIDIwIDE0
NwkJZGVlcCBwaW5rCjI1NSAgMjAgMTQ3CQlEZWVwUGluawoyNTUgMTkyIDIwMwkJcGluawoyNTUgMTgyIDE5MwkJbGlnaHQgcGluawoyNTUgMTgyIDE5MwkJ
TGlnaHRQaW5rCjIxOSAxMTIgMTQ3CQlwYWxlIHZpb2xldCByZWQKMjE5IDExMiAxNDcJCVBhbGVWaW9sZXRSZWQKMTc2ICA0OCAgOTYJCW1hcm9vbgoxNzYg
IDQ4ICA5NgkJeDExIG1hcm9vbgoxNzYgIDQ4ICA5NgkJWDExTWFyb29uCjEyOCAgIDAgICAwCQl3ZWIgbWFyb29uCjEyOCAgIDAgICAwCQlXZWJNYXJvb24K
MTk5ICAyMSAxMzMJCW1lZGl1bSB2aW9sZXQgcmVkCjE5OSAgMjEgMTMzCQlNZWRpdW1WaW9sZXRSZWQKMjA4ICAzMiAxNDQJCXZpb2xldCByZWQKMjA4ICAz
MiAxNDQJCVZpb2xldFJlZAoyNTUgICAwIDI1NQkJbWFnZW50YQoyNTUgICAwIDI1NQkJZnVjaHNpYQoyMzggMTMwIDIzOAkJdmlvbGV0CjIyMSAxNjAgMjIx
CQlwbHVtCjIxOCAxMTIgMjE0CQlvcmNoaWQKMTg2ICA4NSAyMTEJCW1lZGl1bSBvcmNoaWQKMTg2ICA4NSAyMTEJCU1lZGl1bU9yY2hpZAoxNTMgIDUwIDIw
NAkJZGFyayBvcmNoaWQKMTUzICA1MCAyMDQJCURhcmtPcmNoaWQKMTQ4ICAgMCAyMTEJCWRhcmsgdmlvbGV0CjE0OCAgIDAgMjExCQlEYXJrVmlvbGV0CjEz
OCAgNDMgMjI2CQlibHVlIHZpb2xldAoxMzggIDQzIDIyNgkJQmx1ZVZpb2xldAoxNjAgIDMyIDI0MAkJcHVycGxlCjE2MCAgMzIgMjQwCQl4MTEgcHVycGxl
CjE2MCAgMzIgMjQwCQlYMTFQdXJwbGUKMTI4ICAgMCAxMjgJCXdlYiBwdXJwbGUKMTI4ICAgMCAxMjgJCVdlYlB1cnBsZQoxNDcgMTEyIDIxOQkJbWVkaXVt
IHB1cnBsZQoxNDcgMTEyIDIxOQkJTWVkaXVtUHVycGxlCjIxNiAxOTEgMjE2CQl0aGlzdGxlCjI1NSAyNTAgMjUwCQlzbm93MQoyMzggMjMzIDIzMwkJc25v
dzIKMjA1IDIwMSAyMDEJCXNub3czCjEzOSAxMzcgMTM3CQlzbm93NAoyNTUgMjQ1IDIzOAkJc2Vhc2hlbGwxCjIzOCAyMjkgMjIyCQlzZWFzaGVsbDIKMjA1
IDE5NyAxOTEJCXNlYXNoZWxsMwoxMzkgMTM0IDEzMAkJc2Vhc2hlbGw0CjI1NSAyMzkgMjE5CQlBbnRpcXVlV2hpdGUxCjIzOCAyMjMgMjA0CQlBbnRpcXVl
V2hpdGUyCjIwNSAxOTIgMTc2CQlBbnRpcXVlV2hpdGUzCjEzOSAxMzEgMTIwCQlBbnRpcXVlV2hpdGU0CjI1NSAyMjggMTk2CQliaXNxdWUxCjIzOCAyMTMg
MTgzCQliaXNxdWUyCjIwNSAxODMgMTU4CQliaXNxdWUzCjEzOSAxMjUgMTA3CQliaXNxdWU0CjI1NSAyMTggMTg1CQlQZWFjaFB1ZmYxCjIzOCAyMDMgMTcz
CQlQZWFjaFB1ZmYyCjIwNSAxNzUgMTQ5CQlQZWFjaFB1ZmYzCjEzOSAxMTkgMTAxCQlQZWFjaFB1ZmY0CjI1NSAyMjIgMTczCQlOYXZham9XaGl0ZTEKMjM4
IDIwNyAxNjEJCU5hdmFqb1doaXRlMgoyMDUgMTc5IDEzOQkJTmF2YWpvV2hpdGUzCjEzOSAxMjEgIDk0CQlOYXZham9XaGl0ZTQKMjU1IDI1MCAyMDUJCUxl
bW9uQ2hpZmZvbjEKMjM4IDIzMyAxOTEJCUxlbW9uQ2hpZmZvbjIKMjA1IDIwMSAxNjUJCUxlbW9uQ2hpZmZvbjMKMTM5IDEzNyAxMTIJCUxlbW9uQ2hpZmZv
bjQKMjU1IDI0OCAyMjAJCWNvcm5zaWxrMQoyMzggMjMyIDIwNQkJY29ybnNpbGsyCjIwNSAyMDAgMTc3CQljb3Juc2lsazMKMTM5IDEzNiAxMjAJCWNvcm5z
aWxrNAoyNTUgMjU1IDI0MAkJaXZvcnkxCjIzOCAyMzggMjI0CQlpdm9yeTIKMjA1IDIwNSAxOTMJCWl2b3J5MwoxMzkgMTM5IDEzMQkJaXZvcnk0CjI0MCAy
NTUgMjQwCQlob25leWRldzEKMjI0IDIzOCAyMjQJCWhvbmV5ZGV3MgoxOTMgMjA1IDE5MwkJaG9uZXlkZXczCjEzMSAxMzkgMTMxCQlob25leWRldzQKMjU1
IDI0MCAyNDUJCUxhdmVuZGVyQmx1c2gxCjIzOCAyMjQgMjI5CQlMYXZlbmRlckJsdXNoMgoyMDUgMTkzIDE5NwkJTGF2ZW5kZXJCbHVzaDMKMTM5IDEzMSAx
MzQJCUxhdmVuZGVyQmx1c2g0CjI1NSAyMjggMjI1CQlNaXN0eVJvc2UxCjIzOCAyMTMgMjEwCQlNaXN0eVJvc2UyCjIwNSAxODMgMTgxCQlNaXN0eVJvc2Uz
CjEzOSAxMjUgMTIzCQlNaXN0eVJvc2U0CjI0MCAyNTUgMjU1CQlhenVyZTEKMjI0IDIzOCAyMzgJCWF6dXJlMgoxOTMgMjA1IDIwNQkJYXp1cmUzCjEzMSAx
MzkgMTM5CQlhenVyZTQKMTMxIDExMSAyNTUJCVNsYXRlQmx1ZTEKMTIyIDEwMyAyMzgJCVNsYXRlQmx1ZTIKMTA1ICA4OSAyMDUJCVNsYXRlQmx1ZTMKIDcx
ICA2MCAxMzkJCVNsYXRlQmx1ZTQKIDcyIDExOCAyNTUJCVJveWFsQmx1ZTEKIDY3IDExMCAyMzgJCVJveWFsQmx1ZTIKIDU4ICA5NSAyMDUJCVJveWFsQmx1
ZTMKIDM5ICA2NCAxMzkJCVJveWFsQmx1ZTQKICAwICAgMCAyNTUJCWJsdWUxCiAgMCAgIDAgMjM4CQlibHVlMgogIDAgICAwIDIwNQkJYmx1ZTMKICAwICAg
MCAxMzkJCWJsdWU0CiAzMCAxNDQgMjU1CQlEb2RnZXJCbHVlMQogMjggMTM0IDIzOAkJRG9kZ2VyQmx1ZTIKIDI0IDExNiAyMDUJCURvZGdlckJsdWUzCiAx
NiAgNzggMTM5CQlEb2RnZXJCbHVlNAogOTkgMTg0IDI1NQkJU3RlZWxCbHVlMQogOTIgMTcyIDIzOAkJU3RlZWxCbHVlMgogNzkgMTQ4IDIwNQkJU3RlZWxC
bHVlMwogNTQgMTAwIDEzOQkJU3RlZWxCbHVlNAogIDAgMTkxIDI1NQkJRGVlcFNreUJsdWUxCiAgMCAxNzggMjM4CQlEZWVwU2t5Qmx1ZTIKICAwIDE1NCAy
MDUJCURlZXBTa3lCbHVlMwogIDAgMTA0IDEzOQkJRGVlcFNreUJsdWU0CjEzNSAyMDYgMjU1CQlTa3lCbHVlMQoxMjYgMTkyIDIzOAkJU2t5Qmx1ZTIKMTA4
IDE2NiAyMDUJCVNreUJsdWUzCiA3NCAxMTIgMTM5CQlTa3lCbHVlNAoxNzYgMjI2IDI1NQkJTGlnaHRTa3lCbHVlMQoxNjQgMjExIDIzOAkJTGlnaHRTa3lC
bHVlMgoxNDEgMTgyIDIwNQkJTGlnaHRTa3lCbHVlMwogOTYgMTIzIDEzOQkJTGlnaHRTa3lCbHVlNAoxOTggMjI2IDI1NQkJU2xhdGVHcmF5MQoxODUgMjEx
IDIzOAkJU2xhdGVHcmF5MgoxNTkgMTgyIDIwNQkJU2xhdGVHcmF5MwoxMDggMTIzIDEzOQkJU2xhdGVHcmF5NAoyMDIgMjI1IDI1NQkJTGlnaHRTdGVlbEJs
dWUxCjE4OCAyMTAgMjM4CQlMaWdodFN0ZWVsQmx1ZTIKMTYyIDE4MSAyMDUJCUxpZ2h0U3RlZWxCbHVlMwoxMTAgMTIzIDEzOQkJTGlnaHRTdGVlbEJsdWU0
CjE5MSAyMzkgMjU1CQlMaWdodEJsdWUxCjE3OCAyMjMgMjM4CQlMaWdodEJsdWUyCjE1NCAxOTIgMjA1CQlMaWdodEJsdWUzCjEwNCAxMzEgMTM5CQlMaWdo
dEJsdWU0CjIyNCAyNTUgMjU1CQlMaWdodEN5YW4xCjIwOSAyMzggMjM4CQlMaWdodEN5YW4yCjE4MCAyMDUgMjA1CQlMaWdodEN5YW4zCjEyMiAxMzkgMTM5
CQlMaWdodEN5YW40CjE4NyAyNTUgMjU1CQlQYWxlVHVycXVvaXNlMQoxNzQgMjM4IDIzOAkJUGFsZVR1cnF1b2lzZTIKMTUwIDIwNSAyMDUJCVBhbGVUdXJx
dW9pc2UzCjEwMiAxMzkgMTM5CQlQYWxlVHVycXVvaXNlNAoxNTIgMjQ1IDI1NQkJQ2FkZXRCbHVlMQoxNDIgMjI5IDIzOAkJQ2FkZXRCbHVlMgoxMjIgMTk3
IDIwNQkJQ2FkZXRCbHVlMwogODMgMTM0IDEzOQkJQ2FkZXRCbHVlNAogIDAgMjQ1IDI1NQkJdHVycXVvaXNlMQogIDAgMjI5IDIzOAkJdHVycXVvaXNlMgog
IDAgMTk3IDIwNQkJdHVycXVvaXNlMwogIDAgMTM0IDEzOQkJdHVycXVvaXNlNAogIDAgMjU1IDI1NQkJY3lhbjEKICAwIDIzOCAyMzgJCWN5YW4yCiAgMCAy
MDUgMjA1CQljeWFuMwogIDAgMTM5IDEzOQkJY3lhbjQKMTUxIDI1NSAyNTUJCURhcmtTbGF0ZUdyYXkxCjE0MSAyMzggMjM4CQlEYXJrU2xhdGVHcmF5Mgox
MjEgMjA1IDIwNQkJRGFya1NsYXRlR3JheTMKIDgyIDEzOSAxMzkJCURhcmtTbGF0ZUdyYXk0CjEyNyAyNTUgMjEyCQlhcXVhbWFyaW5lMQoxMTggMjM4IDE5
OAkJYXF1YW1hcmluZTIKMTAyIDIwNSAxNzAJCWFxdWFtYXJpbmUzCiA2OSAxMzkgMTE2CQlhcXVhbWFyaW5lNAoxOTMgMjU1IDE5MwkJRGFya1NlYUdyZWVu
MQoxODAgMjM4IDE4MAkJRGFya1NlYUdyZWVuMgoxNTUgMjA1IDE1NQkJRGFya1NlYUdyZWVuMwoxMDUgMTM5IDEwNQkJRGFya1NlYUdyZWVuNAogODQgMjU1
IDE1OQkJU2VhR3JlZW4xCiA3OCAyMzggMTQ4CQlTZWFHcmVlbjIKIDY3IDIwNSAxMjgJCVNlYUdyZWVuMwogNDYgMTM5ICA4NwkJU2VhR3JlZW40CjE1NCAy
NTUgMTU0CQlQYWxlR3JlZW4xCjE0NCAyMzggMTQ0CQlQYWxlR3JlZW4yCjEyNCAyMDUgMTI0CQlQYWxlR3JlZW4zCiA4NCAxMzkgIDg0CQlQYWxlR3JlZW40
CiAgMCAyNTUgMTI3CQlTcHJpbmdHcmVlbjEKICAwIDIzOCAxMTgJCVNwcmluZ0dyZWVuMgogIDAgMjA1IDEwMgkJU3ByaW5nR3JlZW4zCiAgMCAxMzkgIDY5
CQlTcHJpbmdHcmVlbjQKICAwIDI1NSAgIDAJCWdyZWVuMQogIDAgMjM4ICAgMAkJZ3JlZW4yCiAgMCAyMDUgICAwCQlncmVlbjMKICAwIDEzOSAgIDAJCWdy
ZWVuNAoxMjcgMjU1ICAgMAkJY2hhcnRyZXVzZTEKMTE4IDIzOCAgIDAJCWNoYXJ0cmV1c2UyCjEwMiAyMDUgICAwCQljaGFydHJldXNlMwogNjkgMTM5ICAg
MAkJY2hhcnRyZXVzZTQKMTkyIDI1NSAgNjIJCU9saXZlRHJhYjEKMTc5IDIzOCAgNTgJCU9saXZlRHJhYjIKMTU0IDIwNSAgNTAJCU9saXZlRHJhYjMKMTA1
IDEzOSAgMzQJCU9saXZlRHJhYjQKMjAyIDI1NSAxMTIJCURhcmtPbGl2ZUdyZWVuMQoxODggMjM4IDEwNAkJRGFya09saXZlR3JlZW4yCjE2MiAyMDUgIDkw
CQlEYXJrT2xpdmVHcmVlbjMKMTEwIDEzOSAgNjEJCURhcmtPbGl2ZUdyZWVuNAoyNTUgMjQ2IDE0MwkJa2hha2kxCjIzOCAyMzAgMTMzCQlraGFraTIKMjA1
IDE5OCAxMTUJCWtoYWtpMwoxMzkgMTM0ICA3OAkJa2hha2k0CjI1NSAyMzYgMTM5CQlMaWdodEdvbGRlbnJvZDEKMjM4IDIyMCAxMzAJCUxpZ2h0R29sZGVu
cm9kMgoyMDUgMTkwIDExMgkJTGlnaHRHb2xkZW5yb2QzCjEzOSAxMjkgIDc2CQlMaWdodEdvbGRlbnJvZDQKMjU1IDI1NSAyMjQJCUxpZ2h0WWVsbG93MQoy
MzggMjM4IDIwOQkJTGlnaHRZZWxsb3cyCjIwNSAyMDUgMTgwCQlMaWdodFllbGxvdzMKMTM5IDEzOSAxMjIJCUxpZ2h0WWVsbG93NAoyNTUgMjU1ICAgMAkJ
eWVsbG93MQoyMzggMjM4ICAgMAkJeWVsbG93MgoyMDUgMjA1ICAgMAkJeWVsbG93MwoxMzkgMTM5ICAgMAkJeWVsbG93NAoyNTUgMjE1ICAgMAkJZ29sZDEK
MjM4IDIwMSAgIDAJCWdvbGQyCjIwNSAxNzMgICAwCQlnb2xkMwoxMzkgMTE3ICAgMAkJZ29sZDQKMjU1IDE5MyAgMzcJCWdvbGRlbnJvZDEKMjM4IDE4MCAg
MzQJCWdvbGRlbnJvZDIKMjA1IDE1NSAgMjkJCWdvbGRlbnJvZDMKMTM5IDEwNSAgMjAJCWdvbGRlbnJvZDQKMjU1IDE4NSAgMTUJCURhcmtHb2xkZW5yb2Qx
CjIzOCAxNzMgIDE0CQlEYXJrR29sZGVucm9kMgoyMDUgMTQ5ICAxMgkJRGFya0dvbGRlbnJvZDMKMTM5IDEwMSAgIDgJCURhcmtHb2xkZW5yb2Q0CjI1NSAx
OTMgMTkzCQlSb3N5QnJvd24xCjIzOCAxODAgMTgwCQlSb3N5QnJvd24yCjIwNSAxNTUgMTU1CQlSb3N5QnJvd24zCjEzOSAxMDUgMTA1CQlSb3N5QnJvd240
CjI1NSAxMDYgMTA2CQlJbmRpYW5SZWQxCjIzOCAgOTkgIDk5CQlJbmRpYW5SZWQyCjIwNSAgODUgIDg1CQlJbmRpYW5SZWQzCjEzOSAgNTggIDU4CQlJbmRp
YW5SZWQ0CjI1NSAxMzAgIDcxCQlzaWVubmExCjIzOCAxMjEgIDY2CQlzaWVubmEyCjIwNSAxMDQgIDU3CQlzaWVubmEzCjEzOSAgNzEgIDM4CQlzaWVubmE0
CjI1NSAyMTEgMTU1CQlidXJseXdvb2QxCjIzOCAxOTcgMTQ1CQlidXJseXdvb2QyCjIwNSAxNzAgMTI1CQlidXJseXdvb2QzCjEzOSAxMTUgIDg1CQlidXJs
eXdvb2Q0CjI1NSAyMzEgMTg2CQl3aGVhdDEKMjM4IDIxNiAxNzQJCXdoZWF0MgoyMDUgMTg2IDE1MAkJd2hlYXQzCjEzOSAxMjYgMTAyCQl3aGVhdDQKMjU1
IDE2NSAgNzkJCXRhbjEKMjM4IDE1NCAgNzMJCXRhbjIKMjA1IDEzMyAgNjMJCXRhbjMKMTM5ICA5MCAgNDMJCXRhbjQKMjU1IDEyNyAgMzYJCWNob2NvbGF0
ZTEKMjM4IDExOCAgMzMJCWNob2NvbGF0ZTIKMjA1IDEwMiAgMjkJCWNob2NvbGF0ZTMKMTM5ICA2OSAgMTkJCWNob2NvbGF0ZTQKMjU1ICA0OCAgNDgJCWZp
cmVicmljazEKMjM4ICA0NCAgNDQJCWZpcmVicmljazIKMjA1ICAzOCAgMzgJCWZpcmVicmljazMKMTM5ICAyNiAgMjYJCWZpcmVicmljazQKMjU1ICA2NCAg
NjQJCWJyb3duMQoyMzggIDU5ICA1OQkJYnJvd24yCjIwNSAgNTEgIDUxCQlicm93bjMKMTM5ICAzNSAgMzUJCWJyb3duNAoyNTUgMTQwIDEwNQkJc2FsbW9u
MQoyMzggMTMwICA5OAkJc2FsbW9uMgoyMDUgMTEyICA4NAkJc2FsbW9uMwoxMzkgIDc2ICA1NwkJc2FsbW9uNAoyNTUgMTYwIDEyMgkJTGlnaHRTYWxtb24x
CjIzOCAxNDkgMTE0CQlMaWdodFNhbG1vbjIKMjA1IDEyOSAgOTgJCUxpZ2h0U2FsbW9uMwoxMzkgIDg3ICA2NgkJTGlnaHRTYWxtb240CjI1NSAxNjUgICAw
CQlvcmFuZ2UxCjIzOCAxNTQgICAwCQlvcmFuZ2UyCjIwNSAxMzMgICAwCQlvcmFuZ2UzCjEzOSAgOTAgICAwCQlvcmFuZ2U0CjI1NSAxMjcgICAwCQlEYXJr
T3JhbmdlMQoyMzggMTE4ICAgMAkJRGFya09yYW5nZTIKMjA1IDEwMiAgIDAJCURhcmtPcmFuZ2UzCjEzOSAgNjkgICAwCQlEYXJrT3JhbmdlNAoyNTUgMTE0
ICA4NgkJY29yYWwxCjIzOCAxMDYgIDgwCQljb3JhbDIKMjA1ICA5MSAgNjkJCWNvcmFsMwoxMzkgIDYyICA0NwkJY29yYWw0CjI1NSAgOTkgIDcxCQl0b21h
dG8xCjIzOCAgOTIgIDY2CQl0b21hdG8yCjIwNSAgNzkgIDU3CQl0b21hdG8zCjEzOSAgNTQgIDM4CQl0b21hdG80CjI1NSAgNjkgICAwCQlPcmFuZ2VSZWQx
CjIzOCAgNjQgICAwCQlPcmFuZ2VSZWQyCjIwNSAgNTUgICAwCQlPcmFuZ2VSZWQzCjEzOSAgMzcgICAwCQlPcmFuZ2VSZWQ0CjI1NSAgIDAgICAwCQlyZWQx
CjIzOCAgIDAgICAwCQlyZWQyCjIwNSAgIDAgICAwCQlyZWQzCjEzOSAgIDAgICAwCQlyZWQ0CjI1NSAgMjAgMTQ3CQlEZWVwUGluazEKMjM4ICAxOCAxMzcJ
CURlZXBQaW5rMgoyMDUgIDE2IDExOAkJRGVlcFBpbmszCjEzOSAgMTAgIDgwCQlEZWVwUGluazQKMjU1IDExMCAxODAJCUhvdFBpbmsxCjIzOCAxMDYgMTY3
CQlIb3RQaW5rMgoyMDUgIDk2IDE0NAkJSG90UGluazMKMTM5ICA1OCAgOTgJCUhvdFBpbms0CjI1NSAxODEgMTk3CQlwaW5rMQoyMzggMTY5IDE4NAkJcGlu
azIKMjA1IDE0NSAxNTgJCXBpbmszCjEzOSAgOTkgMTA4CQlwaW5rNAoyNTUgMTc0IDE4NQkJTGlnaHRQaW5rMQoyMzggMTYyIDE3MwkJTGlnaHRQaW5rMgoy
MDUgMTQwIDE0OQkJTGlnaHRQaW5rMwoxMzkgIDk1IDEwMQkJTGlnaHRQaW5rNAoyNTUgMTMwIDE3MQkJUGFsZVZpb2xldFJlZDEKMjM4IDEyMSAxNTkJCVBh
bGVWaW9sZXRSZWQyCjIwNSAxMDQgMTM3CQlQYWxlVmlvbGV0UmVkMwoxMzkgIDcxICA5MwkJUGFsZVZpb2xldFJlZDQKMjU1ICA1MiAxNzkJCW1hcm9vbjEK
MjM4ICA0OCAxNjcJCW1hcm9vbjIKMjA1ICA0MSAxNDQJCW1hcm9vbjMKMTM5ICAyOCAgOTgJCW1hcm9vbjQKMjU1ICA2MiAxNTAJCVZpb2xldFJlZDEKMjM4
ICA1OCAxNDAJCVZpb2xldFJlZDIKMjA1ICA1MCAxMjAJCVZpb2xldFJlZDMKMTM5ICAzNCAgODIJCVZpb2xldFJlZDQKMjU1ICAgMCAyNTUJCW1hZ2VudGEx
CjIzOCAgIDAgMjM4CQltYWdlbnRhMgoyMDUgICAwIDIwNQkJbWFnZW50YTMKMTM5ICAgMCAxMzkJCW1hZ2VudGE0CjI1NSAxMzEgMjUwCQlvcmNoaWQxCjIz
OCAxMjIgMjMzCQlvcmNoaWQyCjIwNSAxMDUgMjAxCQlvcmNoaWQzCjEzOSAgNzEgMTM3CQlvcmNoaWQ0CjI1NSAxODcgMjU1CQlwbHVtMQoyMzggMTc0IDIz
OAkJcGx1bTIKMjA1IDE1MCAyMDUJCXBsdW0zCjEzOSAxMDIgMTM5CQlwbHVtNAoyMjQgMTAyIDI1NQkJTWVkaXVtT3JjaGlkMQoyMDkgIDk1IDIzOAkJTWVk
aXVtT3JjaGlkMgoxODAgIDgyIDIwNQkJTWVkaXVtT3JjaGlkMwoxMjIgIDU1IDEzOQkJTWVkaXVtT3JjaGlkNAoxOTEgIDYyIDI1NQkJRGFya09yY2hpZDEK
MTc4ICA1OCAyMzgJCURhcmtPcmNoaWQyCjE1NCAgNTAgMjA1CQlEYXJrT3JjaGlkMwoxMDQgIDM0IDEzOQkJRGFya09yY2hpZDQKMTU1ICA0OCAyNTUJCXB1
cnBsZTEKMTQ1ICA0NCAyMzgJCXB1cnBsZTIKMTI1ICAzOCAyMDUJCXB1cnBsZTMKIDg1ICAyNiAxMzkJCXB1cnBsZTQKMTcxIDEzMCAyNTUJCU1lZGl1bVB1
cnBsZTEKMTU5IDEyMSAyMzgJCU1lZGl1bVB1cnBsZTIKMTM3IDEwNCAyMDUJCU1lZGl1bVB1cnBsZTMKIDkzICA3MSAxMzkJCU1lZGl1bVB1cnBsZTQKMjU1
IDIyNSAyNTUJCXRoaXN0bGUxCjIzOCAyMTAgMjM4CQl0aGlzdGxlMgoyMDUgMTgxIDIwNQkJdGhpc3RsZTMKMTM5IDEyMyAxMzkJCXRoaXN0bGU0CiAgMCAg
IDAgICAwCQlncmF5MAogIDAgICAwICAgMAkJZ3JleTAKICAzICAgMyAgIDMJCWdyYXkxCiAgMyAgIDMgICAzCQlncmV5MQogIDUgICA1ICAgNQkJZ3JheTIK
ICA1ICAgNSAgIDUJCWdyZXkyCiAgOCAgIDggICA4CQlncmF5MwogIDggICA4ICAgOAkJZ3JleTMKIDEwICAxMCAgMTAJCWdyYXk0CiAxMCAgMTAgIDEwCQln
cmV5NAogMTMgIDEzICAxMwkJZ3JheTUKIDEzICAxMyAgMTMJCWdyZXk1CiAxNSAgMTUgIDE1CQlncmF5NgogMTUgIDE1ICAxNQkJZ3JleTYKIDE4ICAxOCAg
MTgJCWdyYXk3CiAxOCAgMTggIDE4CQlncmV5NwogMjAgIDIwICAyMAkJZ3JheTgKIDIwICAyMCAgMjAJCWdyZXk4CiAyMyAgMjMgIDIzCQlncmF5OQogMjMg
IDIzICAyMwkJZ3JleTkKIDI2ICAyNiAgMjYJCWdyYXkxMAogMjYgIDI2ICAyNgkJZ3JleTEwCiAyOCAgMjggIDI4CQlncmF5MTEKIDI4ICAyOCAgMjgJCWdy
ZXkxMQogMzEgIDMxICAzMQkJZ3JheTEyCiAzMSAgMzEgIDMxCQlncmV5MTIKIDMzICAzMyAgMzMJCWdyYXkxMwogMzMgIDMzICAzMwkJZ3JleTEzCiAzNiAg
MzYgIDM2CQlncmF5MTQKIDM2ICAzNiAgMzYJCWdyZXkxNAogMzggIDM4ICAzOAkJZ3JheTE1CiAzOCAgMzggIDM4CQlncmV5MTUKIDQxICA0MSAgNDEJCWdy
YXkxNgogNDEgIDQxICA0MQkJZ3JleTE2CiA0MyAgNDMgIDQzCQlncmF5MTcKIDQzICA0MyAgNDMJCWdyZXkxNwogNDYgIDQ2ICA0NgkJZ3JheTE4CiA0NiAg
NDYgIDQ2CQlncmV5MTgKIDQ4ICA0OCAgNDgJCWdyYXkxOQogNDggIDQ4ICA0OAkJZ3JleTE5CiA1MSAgNTEgIDUxCQlncmF5MjAKIDUxICA1MSAgNTEJCWdy
ZXkyMAogNTQgIDU0ICA1NAkJZ3JheTIxCiA1NCAgNTQgIDU0CQlncmV5MjEKIDU2ICA1NiAgNTYJCWdyYXkyMgogNTYgIDU2ICA1NgkJZ3JleTIyCiA1OSAg
NTkgIDU5CQlncmF5MjMKIDU5ICA1OSAgNTkJCWdyZXkyMwogNjEgIDYxICA2MQkJZ3JheTI0CiA2MSAgNjEgIDYxCQlncmV5MjQKIDY0ICA2NCAgNjQJCWdy
YXkyNQogNjQgIDY0ICA2NAkJZ3JleTI1CiA2NiAgNjYgIDY2CQlncmF5MjYKIDY2ICA2NiAgNjYJCWdyZXkyNgogNjkgIDY5ICA2OQkJZ3JheTI3CiA2OSAg
NjkgIDY5CQlncmV5MjcKIDcxICA3MSAgNzEJCWdyYXkyOAogNzEgIDcxICA3MQkJZ3JleTI4CiA3NCAgNzQgIDc0CQlncmF5MjkKIDc0ICA3NCAgNzQJCWdy
ZXkyOQogNzcgIDc3ICA3NwkJZ3JheTMwCiA3NyAgNzcgIDc3CQlncmV5MzAKIDc5ICA3OSAgNzkJCWdyYXkzMQogNzkgIDc5ICA3OQkJZ3JleTMxCiA4MiAg
ODIgIDgyCQlncmF5MzIKIDgyICA4MiAgODIJCWdyZXkzMgogODQgIDg0ICA4NAkJZ3JheTMzCiA4NCAgODQgIDg0CQlncmV5MzMKIDg3ICA4NyAgODcJCWdy
YXkzNAogODcgIDg3ICA4NwkJZ3JleTM0CiA4OSAgODkgIDg5CQlncmF5MzUKIDg5ICA4OSAgODkJCWdyZXkzNQogOTIgIDkyICA5MgkJZ3JheTM2CiA5MiAg
OTIgIDkyCQlncmV5MzYKIDk0ICA5NCAgOTQJCWdyYXkzNwogOTQgIDk0ICA5NAkJZ3JleTM3CiA5NyAgOTcgIDk3CQlncmF5MzgKIDk3ICA5NyAgOTcJCWdy
ZXkzOAogOTkgIDk5ICA5OQkJZ3JheTM5CiA5OSAgOTkgIDk5CQlncmV5MzkKMTAyIDEwMiAxMDIJCWdyYXk0MAoxMDIgMTAyIDEwMgkJZ3JleTQwCjEwNSAx
MDUgMTA1CQlncmF5NDEKMTA1IDEwNSAxMDUJCWdyZXk0MQoxMDcgMTA3IDEwNwkJZ3JheTQyCjEwNyAxMDcgMTA3CQlncmV5NDIKMTEwIDExMCAxMTAJCWdy
YXk0MwoxMTAgMTEwIDExMAkJZ3JleTQzCjExMiAxMTIgMTEyCQlncmF5NDQKMTEyIDExMiAxMTIJCWdyZXk0NAoxMTUgMTE1IDExNQkJZ3JheTQ1CjExNSAx
MTUgMTE1CQlncmV5NDUKMTE3IDExNyAxMTcJCWdyYXk0NgoxMTcgMTE3IDExNwkJZ3JleTQ2CjEyMCAxMjAgMTIwCQlncmF5NDcKMTIwIDEyMCAxMjAJCWdy
ZXk0NwoxMjIgMTIyIDEyMgkJZ3JheTQ4CjEyMiAxMjIgMTIyCQlncmV5NDgKMTI1IDEyNSAxMjUJCWdyYXk0OQoxMjUgMTI1IDEyNQkJZ3JleTQ5CjEyNyAx
MjcgMTI3CQlncmF5NTAKMTI3IDEyNyAxMjcJCWdyZXk1MAoxMzAgMTMwIDEzMAkJZ3JheTUxCjEzMCAxMzAgMTMwCQlncmV5NTEKMTMzIDEzMyAxMzMJCWdy
YXk1MgoxMzMgMTMzIDEzMwkJZ3JleTUyCjEzNSAxMzUgMTM1CQlncmF5NTMKMTM1IDEzNSAxMzUJCWdyZXk1MwoxMzggMTM4IDEzOAkJZ3JheTU0CjEzOCAx
MzggMTM4CQlncmV5NTQKMTQwIDE0MCAxNDAJCWdyYXk1NQoxNDAgMTQwIDE0MAkJZ3JleTU1CjE0MyAxNDMgMTQzCQlncmF5NTYKMTQzIDE0MyAxNDMJCWdy
ZXk1NgoxNDUgMTQ1IDE0NQkJZ3JheTU3CjE0NSAxNDUgMTQ1CQlncmV5NTcKMTQ4IDE0OCAxNDgJCWdyYXk1OAoxNDggMTQ4IDE0OAkJZ3JleTU4CjE1MCAx
NTAgMTUwCQlncmF5NTkKMTUwIDE1MCAxNTAJCWdyZXk1OQoxNTMgMTUzIDE1MwkJZ3JheTYwCjE1MyAxNTMgMTUzCQlncmV5NjAKMTU2IDE1NiAxNTYJCWdy
YXk2MQoxNTYgMTU2IDE1NgkJZ3JleTYxCjE1OCAxNTggMTU4CQlncmF5NjIKMTU4IDE1OCAxNTgJCWdyZXk2MgoxNjEgMTYxIDE2MQkJZ3JheTYzCjE2MSAx
NjEgMTYxCQlncmV5NjMKMTYzIDE2MyAxNjMJCWdyYXk2NAoxNjMgMTYzIDE2MwkJZ3JleTY0CjE2NiAxNjYgMTY2CQlncmF5NjUKMTY2IDE2NiAxNjYJCWdy
ZXk2NQoxNjggMTY4IDE2OAkJZ3JheTY2CjE2OCAxNjggMTY4CQlncmV5NjYKMTcxIDE3MSAxNzEJCWdyYXk2NwoxNzEgMTcxIDE3MQkJZ3JleTY3CjE3MyAx
NzMgMTczCQlncmF5NjgKMTczIDE3MyAxNzMJCWdyZXk2OAoxNzYgMTc2IDE3NgkJZ3JheTY5CjE3NiAxNzYgMTc2CQlncmV5NjkKMTc5IDE3OSAxNzkJCWdy
YXk3MAoxNzkgMTc5IDE3OQkJZ3JleTcwCjE4MSAxODEgMTgxCQlncmF5NzEKMTgxIDE4MSAxODEJCWdyZXk3MQoxODQgMTg0IDE4NAkJZ3JheTcyCjE4NCAx
ODQgMTg0CQlncmV5NzIKMTg2IDE4NiAxODYJCWdyYXk3MwoxODYgMTg2IDE4NgkJZ3JleTczCjE4OSAxODkgMTg5CQlncmF5NzQKMTg5IDE4OSAxODkJCWdy
ZXk3NAoxOTEgMTkxIDE5MQkJZ3JheTc1CjE5MSAxOTEgMTkxCQlncmV5NzUKMTk0IDE5NCAxOTQJCWdyYXk3NgoxOTQgMTk0IDE5NAkJZ3JleTc2CjE5NiAx
OTYgMTk2CQlncmF5NzcKMTk2IDE5NiAxOTYJCWdyZXk3NwoxOTkgMTk5IDE5OQkJZ3JheTc4CjE5OSAxOTkgMTk5CQlncmV5NzgKMjAxIDIwMSAyMDEJCWdy
YXk3OQoyMDEgMjAxIDIwMQkJZ3JleTc5CjIwNCAyMDQgMjA0CQlncmF5ODAKMjA0IDIwNCAyMDQJCWdyZXk4MAoyMDcgMjA3IDIwNwkJZ3JheTgxCjIwNyAy
MDcgMjA3CQlncmV5ODEKMjA5IDIwOSAyMDkJCWdyYXk4MgoyMDkgMjA5IDIwOQkJZ3JleTgyCjIxMiAyMTIgMjEyCQlncmF5ODMKMjEyIDIxMiAyMTIJCWdy
ZXk4MwoyMTQgMjE0IDIxNAkJZ3JheTg0CjIxNCAyMTQgMjE0CQlncmV5ODQKMjE3IDIxNyAyMTcJCWdyYXk4NQoyMTcgMjE3IDIxNwkJZ3JleTg1CjIxOSAy
MTkgMjE5CQlncmF5ODYKMjE5IDIxOSAyMTkJCWdyZXk4NgoyMjIgMjIyIDIyMgkJZ3JheTg3CjIyMiAyMjIgMjIyCQlncmV5ODcKMjI0IDIyNCAyMjQJCWdy
YXk4OAoyMjQgMjI0IDIyNAkJZ3JleTg4CjIyNyAyMjcgMjI3CQlncmF5ODkKMjI3IDIyNyAyMjcJCWdyZXk4OQoyMjkgMjI5IDIyOQkJZ3JheTkwCjIyOSAy
MjkgMjI5CQlncmV5OTAKMjMyIDIzMiAyMzIJCWdyYXk5MQoyMzIgMjMyIDIzMgkJZ3JleTkxCjIzNSAyMzUgMjM1CQlncmF5OTIKMjM1IDIzNSAyMzUJCWdy
ZXk5MgoyMzcgMjM3IDIzNwkJZ3JheTkzCjIzNyAyMzcgMjM3CQlncmV5OTMKMjQwIDI0MCAyNDAJCWdyYXk5NAoyNDAgMjQwIDI0MAkJZ3JleTk0CjI0MiAy
NDIgMjQyCQlncmF5OTUKMjQyIDI0MiAyNDIJCWdyZXk5NQoyNDUgMjQ1IDI0NQkJZ3JheTk2CjI0NSAyNDUgMjQ1CQlncmV5OTYKMjQ3IDI0NyAyNDcJCWdy
YXk5NwoyNDcgMjQ3IDI0NwkJZ3JleTk3CjI1MCAyNTAgMjUwCQlncmF5OTgKMjUwIDI1MCAyNTAJCWdyZXk5OAoyNTIgMjUyIDI1MgkJZ3JheTk5CjI1MiAy
NTIgMjUyCQlncmV5OTkKMjU1IDI1NSAyNTUJCWdyYXkxMDAKMjU1IDI1NSAyNTUJCWdyZXkxMDAKMTY5IDE2OSAxNjkJCWRhcmsgZ3JleQoxNjkgMTY5IDE2
OQkJRGFya0dyZXkKMTY5IDE2OSAxNjkJCWRhcmsgZ3JheQoxNjkgMTY5IDE2OQkJRGFya0dyYXkKICAwICAgMCAxMzkJCWRhcmsgYmx1ZQogIDAgICAwIDEz
OQkJRGFya0JsdWUKICAwIDEzOSAxMzkJCWRhcmsgY3lhbgogIDAgMTM5IDEzOQkJRGFya0N5YW4KMTM5ICAgMCAxMzkJCWRhcmsgbWFnZW50YQoxMzkgICAw
IDEzOQkJRGFya01hZ2VudGEKMTM5ICAgMCAgIDAJCWRhcmsgcmVkCjEzOSAgIDAgICAwCQlEYXJrUmVkCjE0NCAyMzggMTQ0CQlsaWdodCBncmVlbgoxNDQg
MjM4IDE0NAkJTGlnaHRHcmVlbgoyMjAgIDIwICA2MAkJY3JpbXNvbgogNzUgICAwIDEzMAkJaW5kaWdvCjEyOCAxMjggICAwCQlvbGl2ZQoxMDIgIDUxIDE1
MwkJcmViZWNjYSBwdXJwbGUKMTAyICA1MSAxNTMJCVJlYmVjY2FQdXJwbGUKMTkyIDE5MiAxOTIJCXNpbHZlcgogIDAgMTI4IDEyOAkJdGVhbAoAAAAAAAAA
AAAAAAAAAAAAAAAAAAAABQAAAAYAAAAHAAAACAAAAGluZGV4IG91dCBvZiBib3VuZHM6IGluZGV4ICwgbGVuIHN0YXJ0IGluZGV4ICBpcyBsYXJnZXIgdGhh
biBlbmQgaW5kZXggYXR0ZW1wdCB0byB1bndyYXAgZXJyb3I6IAAgAAAACAAA1wDXAIAAcAAAAAAAAAAAAAAAAAAAAAAAGQAAAAAAAAABAAAAAgAAAAQAAAAJ
AAAAEwAAACYAAABMAAAAmQAAADMBAABmAgAAzAQAAJkJAAAzEwAAZiYAAMxMAACZmQAAMzMBAGZmAgDMzAQAmZkJADMzEwBmZiYAzMxMAJmZmQAzMzMBZmZm
AszMzAT///8PAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAQEBAQEBAQEBAQEBAQEBCQkJCQkJCQkJCQkJCQkJCQcHBwcHBwcHBwcH
BwcHBwcHBwcHBwcHBwcHBwcHBwcHCAgCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIKAwMDAwMDAwMDAwMDBAMDCwYGBgUICAgICAgICAgICAAMGCQ8
YFQMDAwwSAwMDAwMDAwMDAwMDAwADAwMDAwADAAMDAwYDAwMDAwYDBgMDAwMDAwMDAwYDAwMDAwYDAwMDAwMDBgMDAwMDAwMDAwkDCQMDAwkDAwMDAwkDCQM
DAwkDAwMDAwMDAwMDAAAAAD9/wAAAQAAAAACAQADAQACAAADAAEBAAECAAEDAAABAAADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMG
AwcBCAEJAQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJ
AQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEA
AA0KAAMBAwIDAwMEAwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMB
AwIDAwMEAwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMBAwIDAwME
AwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMH
AQgBCQEKCAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMHAQgBCQEK
CAsBDAENCgADAQMCAwMDBAMFAwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMHAQgBCQEKCAsBDAEN
CgADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQAAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAMBAwIDAwMEAwUDBgMHAQgBCQEKCAsBDAENCgADAQMCAwMDBAMF
AwYDBwEIAQkBCggLAQwBDQoAAwEDAgMDAwQDBQMGAwcBCAEJAQoICwEMAQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAgQCBAQEBAQEBAYBCQQJ
BAkECggLAAwJDQoAAgIEAgQEBAQEBAQGAQkECQQJBAoICwAMCQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAgQCBAQEBAQEBAYBCQQJBAkECggL
AAwJDQoAAgIEAgQEBAQEBAQGAQkECQQJBAoICwAMCQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAgQCBAQEBAQEBAYBCQQJBAkECggLAAwJDQoA
AgIEAgQEBAQEBAQGAQkECQQJBAoICwAMCQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAgQCBAQEBAQEBAYBCQQJBAkECggLAAwJDQoAAgIEAgQE
BAQEBAQGAQkECQQJBAoICwAMCQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAgQCBAQEBAQEBAYBCQQJBAkECggLAAwJDQoAAgIEAgQEBAQEBAQG
AQkECQQJBAoICwAMCQ0KAAICBAIEBAQEBAQEBgEJBAkECQQKCAsADAkNCgACAAYABgUFBgAFBQYBCAUIBQsACggLAAwJDQoAAgAGAAYFBQYABQUGAQgFCAUL
AAoICwAMCQ0KAAIABgAGBQUGAAUFBgEIBQgFCwAKCAsADAkNCgACAAYABgUFBgAFBQYBCAUIBQsACggLAAwJDQoAAgAGAAYFBQYABQUGAQgFCAULAAoICwAM
CQ0KAAIABgAGBQUGAAUFBgEIBQgFCwAKCAsADAkNCgACAAYABgUFBgAFBQYBCAUIBQsACggLAAwJDQoAAgAGAAYFBQYABQUGAQgFCAULAAoICwAMCQ0KAAIA
BgAGBQUGAAUFBgEIBQgFCwAKCAsADAkNCgACAAYABgUFBgAFBQYBCAUIBQsACggLAAwJDQoAAgAGAAYGAAYABQUGAQsACwALAAoICwAMCQ0KAAIABgAGBQUG
AAUFBgEIBQgFCwAKCAsADAkNCgACAAYABgUEBgAGAAYBCAQLAAsACggLAAwJDQoAAgAGAAYFBAYABgAGAQgECwALAAoICwAMCQ0KAAIABgAGBQQGAAYABgEI
BAsACwAKCAsADAkNCgACAAYABgUEBgAGAAYBCAQLAAsACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAK
CAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkN
CgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYA
BgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcA
BwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAK
AAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIHAAAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggL
AAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoA
AgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAg0AAAYA
BwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgMAAAYABwAHAAcA
AAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACDAAABgAHAAcABwAACgAKAAoACggLAAwJDQoAAg0AAAYABwAHAAcAAAoACgAK
AAoICwAMCQ0KAAINAAAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAM
CQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIA
BgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcA
BwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAK
AAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAK
CAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkN
CgACAAYABgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYA
BgAHAAcABwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcA
BwAACgAKAAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAK
AAoACggLAAwJDQoAAgAGAAYABwAHAAcAAAoACgAKAAoICwAMCQ0KAAIABgAGAAcABwAHAAAKAAoACgAKCAsADAkNCgACAAYABgAHAAcABwAACgAKAAoACggL
AAwJDQoAAgEBAgEDAQQBBQEGAQcBCAEJAQoBCwAMCQ0KAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMA
AwADAAMAAwADAAMAAwADAAMAAwADAAMMCQADAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMA
AwADAAMAAwADAAMAAwADAAMMCQADAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMA
AwADAAMAAwADAAMMCQADAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMAAwADAAMA
AwADAAMMCQADAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMM
CQADAAMAAwADAAMAAwADAAMAAwADAAMAAwADDAkAAwcABwAHAAcABwAHAAcABwAHAAcABwAHAAwJBwAAAwADAAMAAwADAAMAAwADAAMAAwADAAMMCQADAAMA
AwADAAMAAwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMMCQADAAMAAwADAAMA
AwADAAMAAwADAAMAAwADDAkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMMCQADDQANAA0ADQANAA0ADQAN
AA0ADQANAA0ADAkNAAADAAMAAwADAAMAAwADAAMAAwADAAMAAwwJAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMMCQADAwADAAMAAwADAAMAAwADAAMAAwAD
AAMADAkDAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwJAAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMCQwADQANAA0ADQANAA0ADQANAA0ADQANAA0ADAkN
AA0ADQANAA0ADQANAA0ADQANAA0ADQANAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQAC
AAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAF
AAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAI
AAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgAL
AAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAA
AAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgAD
AAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAG
AAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJ
AAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAM
CQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAAB
AAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAE
AAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAH
AAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAK
AAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkN
AAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQAC
AAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAF
AAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAI
AAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgAL
AAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAA
AAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgAD
AAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAG
AAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJ
AAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAM
CQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAAB
AAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAE
AAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAH
AAgACQAKAAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAK
AAsADAkNAAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkN
AAAAAQACAAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQAC
AAMABAAFAAYABwAIAAkACgALAAwJDQAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMCQ0AAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAkNAAAAAQACAAMABAAF
AAYABwAIAAkACgALAAwJDQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACgAAAAEAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAABAAAAAAAAAQABAAIAAwAEAAUABgAHAAgACQAKAAsADAANAA4ADwAQAAEAEQABAAEA
AQASABMAFAAVABYAFwAYAAEAAQAZABoAAQAbABwAHQAeAB8AAQAgAAEAIQAiACMAJAAlACYAJwAoACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQAqAAEAKwABACwALQAuAC8AMAAxADIAMwA0ADUANgAwADEAMgAzADQANQA2ADAAMQAyADMANAA1ADYAMAAxADIAMwA0ADUANgAwADEA
MgAzADQANQA2ADAAMQAyADMANAA1ADYAMAA3ADgAOAA4ADgAOAA4ADgAOAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABACkAKQA5AAEAAQA6ADsAAQA8AD0APgABAAEAAQABAAEAAQA/AAEAAQBAAEEAQgBDAEQARQBGAEcASABJAEoASwBMAE0ATgBPAFAAUQBSAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQBTAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQBUAAEAAQABAAEAAQABAAEAAQBVAFYAAQBXAAEAWAApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApAFkAWgABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQBbACkAXABdAAEAAQABAAEAAQABAAEAAQABAF4AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAXwABAGAAYQBiAAEAAQABAAEA
AQABAGMAAQABAAEAAQABAGQAVgBlAAEAZgBnAGgAAQBpAGoAAQABAAEAAQABAAEAawBsAG0AbgBvAHAAcQByAHMAdAB1AAEAdgB2AHYAdwApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQB4ACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkAKQApACkA
KQApACkAKQApACkAKQApACkAKQApAHgAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQB5AHoAOAA4ADgAOAA4ADgAOAA4ADgAOAA4ADgAOAA4AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEA
AQABAAEAAQABAAEAAQABAAEAAQB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsA
ewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewB7AHsAewAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAEAAQACAAEAAQABAAEAAQABAAIAAQABAAEAAQABAAIAAgACAAIAAgACAAIAAgACAAIAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAQABAAEAAQABAAEAAQABAAEAAwABAAEAAQABAAMAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAABAAQAAQAEAAQAAQAEAAQAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAF
AAUABQAFAAUABQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAABAAAAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABQABAAQABAAE
AAQABAAEAAEAAQAEAAQAAQAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAUAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAE
AAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAEABAAE
AAQABAAEAAQABAAEAAQAAQAEAAQABAABAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABQAFAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAFAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAGAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAQABgAEAAEABgAGAAYABAAEAAQABAAEAAQABAAEAAYABgAGAAYACAAGAAYAAQAEAAQABAAEAAQABAAE
AAcABwAHAAcABwAHAAcABwABAAEABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcAAQAEAAYABgAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAEABwAHAAcABwAHAAcABwAB
AAcAAQABAAEABwAHAAcABwABAAEABAABAAkABgAGAAQABAAEAAQAAQABAAYABgABAAEABgAGAAgAAQABAAEAAQABAAEAAQABAAEACQABAAEAAQABAAcABwAB
AAcAAQABAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAEABAAEAAYAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQAAQAGAAYABgAEAAQAAQABAAEAAQAEAAQAAQABAAQABAAEAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAGAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcAAQAHAAcABwAHAAcABwAHAAEABwAHAAEABwAHAAcABwAHAAEAAQAEAAEABgAG
AAYABAAEAAQABAAEAAEABAAEAAYAAQAGAAYACAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAcABAAEAAQABAAEAAQAAQAEAAYABgABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAEABwAHAAcABwAHAAcABwABAAcABwABAAcABwAHAAcABwABAAEABAABAAkABAAGAAQABAAEAAQAAQAB
AAYABgABAAEABgAGAAgAAQABAAEAAQABAAEAAQAEAAQACQABAAEAAQABAAcABwABAAcAAQABAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAcAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAJAAYABAAGAAYAAQABAAEABgAGAAYAAQAGAAYABgAE
AAEAAQABAAEAAQABAAEAAQABAAkAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQAEAAYABgAGAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAEAAQAEAAEABAAEAAQABgAGAAYABgABAAQABAAEAAEABAAEAAQACAABAAEAAQABAAEAAQAB
AAQABAABAAcABwAHAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAE
AAYABgABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAYABAAJAAYACQAGAAYAAQAEAAkACQABAAkACQAEAAQAAQABAAEAAQABAAEAAQAJAAkAAQABAAEAAQAB
AAEAAQABAAEAAQABAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAGAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAGAAYAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAEAAQAAQAJAAYABgAEAAQABAAEAAEABgAGAAYAAQAGAAYABgAIAAUAAQABAAEAAQABAAEAAQABAAkAAQABAAEAAQABAAEAAQABAAEAAQAE
AAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABgAGAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAEAAQABAAkABgAGAAQABAAEAAEABAABAAYABgAGAAYABgAGAAYACQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAGAAYAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEABgAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAYABAAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAE
AAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAQAAQAEAAEAAQABAAEABgAGAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAYABAAEAAQABAAEAAEABAAEAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcAAQABAAQABAAEAAQABgAE
AAQABAAEAAQABAABAAgABAAGAAYABAAEAAcAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAGAAYABAAEAAcABwAHAAcABAAE
AAQABwABAAEAAQAHAAcAAQABAAEAAQABAAEAAQAHAAcABwAEAAQABAAEAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABAABAAYABAAEAAEAAQABAAEAAQAB
AAQABwABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAK
AAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAK
AAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAsADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAM
AAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAM
AAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQAN
AA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQAN
AA0ADQANAA0ADQANAA0ADQANAA0ADQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAkAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQAEAAQACQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAQABAAG
AAQABAAEAAQABAAEAAQABgAGAAYABgAGAAYABgAGAAQABgAGAAQABAAEAAQABAAEAAQABAAEAAgABAABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAAABAAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEABAAEAAQABgAGAAYABgAEAAQABgAGAAYAAQABAAEAAQAGAAYABAAGAAYABgAGAAYABgAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABgAGAAQAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABgAEAAYABAAE
AAQABAAEAAQABAABAAgAAQAEAAEAAQAEAAQABAAEAAQABAAEAAQABgAGAAYABgAGAAYABAAEAAQABAAEAAQABAAEAAQABAABAAEABAABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQAE
AAQABAAEAAQABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAGAAEAAQABAAEAAQABAAcABwAB
AAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAEAAkABAAEAAQABAAE
AAkABAAJAAYABgAGAAYABAAJAA4ABwAHAAcABwAHAAcABwAHAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAGAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAYABAAEAAQABAAGAAYABAAEAAkACAAEAAQABwAHAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABgAEAAQABgAGAAYABAAG
AAQABAAEAAkACQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAGAAYABgAGAAYABgAGAAYABAAEAAQABAAEAAQABAAEAAYABgAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAQABAAEAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAGAAQABAAEAAQABAAEAAQAAQABAAEAAQAEAAEAAQABAAEAAQABAAQAAQABAAYABAAEAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQAB
AAAADwAQAAAAAAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAAAAAAAAAAAAAAAAAAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAwABAAEAAQABAAEAAQABAAEAAQABAAEAAQADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAMAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQADAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAwADAAMAAwADAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAMAAwABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQARABEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAwASABIAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQARABEAEQARAAMAAwADABEAAwADABEAAQABAAEAAQADAAMAAwABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAMAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAwADAAEAAQABAAEAAQABAAEAAQABAAEAAwABAAEAAQABAAEAAQABAAEAAQADAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAwADABEAEQABAAMAAwADAAMAAwABAAEAAQABAAEAAQABAAEAAQADAAEAAQADAAEAAQARABEAAQABAAMAAQABAAEAAQATAAEAAQADAAEAAwAD
AAEAAQADAAEAAQABAAMAAQABAAEAAwADABIAEgASABIAEgASABIAEgADAAMAAwABAAEAAQABAAEAAwABAAMAAQABAAEAAQABABEAEQARABEAEQARABEAEQAR
ABEAEQARAAEAAQABAAEAAQABAAEAAQABAAEAAQADAAMAAQABAAMAAQADAAMAAQADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAQAB
AAMAEQABAAEAAQABAAEAAQABAAEAAQABABIAEgASABIAEgASAAEAAQADABEAAwADAAMAAwABAAMAAQADAAMAAQABAAEAAwARAAEAAQABAAEAAQADAAEAAQAR
ABEAAQABAAEAAQADAAMAAQABAAEAAQABAAEAAQABAAEAAQABABEAEQABAAEAAQABAAEAEQARAAEAAQADAAEAAQABAAEAAQARAAMAAQADAAEAAwARAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQADABEAAQABAAEAAQABAAMAAwARABEAAwARAAEAAwADABMAEQABAAEAEQABAAEAAQABAAMAAQAB
ABEAAQABAAMAAwAUABQAEwATAAEAAwABAAEAAwABAAMAAQADAAEAAQABAAEAAQABAAMAAQABAAEAAwABAAEAAQABAAEAAQARAAEAAQABAAEAAQABAAEAAQAB
AAEAAwADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAQABAAMAAQABAAEAAQARAAEAEQABAAEAAQABABEAEQARAAEAEQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAwADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABABEAEQARAAEAAQABAAEAAQABAAEAAQABAAMAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABABEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAwABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQADAAMAAwABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABEAEQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQARAAEAAQABAAEAEQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAEgASAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAEAAQABAAEABUAFQARABIAEgASABIAEgASABIAEgAS
ABIAEgASABEAEgABAAEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASAAEAAQAEAAQAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAAASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABAAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQABAAEAAQABABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgARABIAEQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAAQABABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAABAAQABAAEAAQABAAEAAQABAAEAAQAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAEAAQAE
AAEAAQABAAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAGAAQABAAGAAEAAQABAAEABAABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAGAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAGAAYABgAGAAYABgAG
AAYABgAGAAYABgAGAAYABgAGAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABgAJAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgAK
AAoACgAKAAoACgAKAAoACgAKAAoACgAKAAoACgABAAEAAQAEAAQABAAGAAEAAQABAAEAAQAHAAcABwABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAEAAYABgAEAAQABAAEAAYABgAEAAQABgAGAA4AAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABAABAAcABwAHAAcABwAHAAcABwAHAAEAAQABAAEAAQAB
AAEAAQABAAEABwAHAAcABwAHAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAQABAAEAAQABAAEAAYABgAEAAQABgAGAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEAAQABAAEAAQABAAEAAQAEAAYAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwABAAcABwAHAAEAAQABAAEAAQABAAcAAQAE
AAEABwAHAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAQAAQAEAAQABAABAAEABAAEAAEAAQABAAEAAQAEAAQAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAcABwAHAAcABwAHAAcABwAHAAcABwAGAAQABAAGAAYAAQABAAEAAQABAAYACAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwABAAEAAQABAAEAAQABAAEABgAG
AAQABgAGAAQABgAGAAEABgAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAW
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABYAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFgAXABcAFwAXABcAFwAX
ABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAWABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAXABcAFwAX
ABcAFwAXAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAAMAAwADAABAAEAAQABAA0ADQAN
AA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQANAA0ADQAN
AA0AAQABAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAASABIAEgASABIAEgAS
ABIAEgASAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABABIAEgASABIAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAAABABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEACQAJAAAAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAEgASABIAEgASABIAEgABAAEAAQABAAEAAQABAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAcABAAE
AAQAAQAEAAQAAQABAAEAAQABAAQABAAEAAQABwAHAAcABwABAAcABwAHAAEABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAEAAQAEAAQABAABAAEAAQABAAgAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAE
AAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAE
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAEAAYAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAEAAEAAQAEAAQAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAGAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAYABgAGAAQABAAEAAQABgAGAAQABAABAAEABQABAAEAAQAB
AAQAAQABAAEAAQABAAEAAQABAAEAAQAFAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAEAAQABAAEAAQABgAEAAQABAAEAAQABAAIAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABwAGAAYABwAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEABAAEAAYAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAGAAYABAAEAAQABAAEAAQABAAEAAQABgAJAAEABQAFAAEAAQABAAEAAQAEAAQABAAEAAEABgAE
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQAGAAYABgAEAAQABAAGAAYABAAJAAQABAABAAEAAQABAAEAAQAEAAEAAQAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEABAAGAAYABgAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAYABgABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQABAABAAkABgAEAAYABgAGAAYAAQABAAYABgABAAEABgAGAAkAAQABAAEAAQABAAEAAQABAAEACQABAAEAAQABAAEAAQABAAEAAQABAAYABgAB
AAEABAAEAAQABAAEAAQABAABAAEAAQAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAcABwAHAAcABwAHAAcABwAHAAcAAQAHAAEAAQAHAAEABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAEAAQAJAAYABgAEAAQABAAE
AAQABAABAAkAAQABAAkAAQAJAAkACQAGAAEABgAGAAQACQAIAAUABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAGAAYABAAEAAQABAAEAAQABAAEAAYABgAEAAQABAAG
AAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEACQAGAAYABAAEAAQABAAEAAQABgAEAAYABgAJAAYABAAEAAYABAAEAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEACQAGAAYABAAEAAQABAABAAEABgAGAAYABgAEAAQABgAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABgAGAAYABAAE
AAQABAAEAAQABAAEAAYABgAEAAYABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAGAAQABgAGAAQABAAEAAQABAAEAAkABAABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEABAAGAAQAAQABAAQABAAEAAQABgAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAGAAYABgAEAAQABAAEAAQABAAEAAQABAAGAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABwAHAAcABwAH
AAcABwABAAEABwABAAEABwAHAAcABwAHAAcABwAHAAEABwAHAAEABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcACQAG
AAYABgAGAAYAAQAGAAYAAQABAAQABAAJAAgABQAGAAUABgAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAYABgAGAAQABAAEAAQAAQABAAQABAAGAAYABgAGAAQAAQABAAEABgAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAHAAQABAAEAAQABAAEAAQABAAEAAQABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAEAAQABAAEAAQABAAGAAEABAAEAAQABAAB
AAEAAQABAAEAAQABAAEACAABAAEAAQABAAEAAQABAAEABwAEAAQABAAEAAQABAAGAAYABAAEAAQABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAFAAUABQAFAAUABQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAYABAAI
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABgAEAAQABAAGAAQABgABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAYABAAEAAQABAAEAAQABAABAAQABAAEAAQABAAEAAYABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAQAGAAQABAAEAAQABAAEAAQABgAEAAQABgAE
AAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAEAAQABAAQAAQAE
AAQAAQAEAAQABAAEAAQABAAEAAUABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAGAAYABgAGAAYAAQAEAAQAAQAGAAYABAAG
AAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAGAAYAAQABAAEAAQABAAEAAQAB
AAEABAAEAAUABgAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAEABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAHAAcABwAH
AAcABwAHAAcABwAHAAcABwAGAAYABAAEAAQABAAEAAEAAQABAAYABgAEAAkACAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAYABgAGAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQABAAE
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAwAAQABAAEADAAMAAwADAABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAEABgAG
AAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAG
AAYABgAGAAYABgAGAAYABgABAAEAAQABAAEAAQABAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABABIAEgASABIABAABAAEAAQABAAEAAQABAAEAAQABAAEAFQAVABIAEgASABIAEgABAAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAEgASABIAEgABABIAEgASABIAEgASABIAAQASABIAAQASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAEgABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAEgASABIAAQABABIAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABABIAEgASABIAAQABAAEAAQABAAEAAQABABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQAAQAAAAAAAAAAAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAJAAkABAAE
AAQAAQABAAEACQAJAAkACQAJAAkAAAAAAAAAAAAAAAAAAAAAAAQABAAEAAQABAAEAAQABAABAAEABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAB
AAEAAQABAAEAAQABAAEAAQASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAAQAB
AAEAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQAEAAQABAAEAAQAAQAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAABAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAB
AAEABAAEAAQABAAEAAQABAABAAQABAABAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAE
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQAEAAQABAAEAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAEAAQAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEABAABAAEABAABAAEAAQABAAEAAQABAAQABAABAAEAAQABAAEABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEABAAEAAQABAAEAAQABAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAQABAAEAAQABAAEAAQAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAGAAYABgAGAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABgAGAAYABgAGAAYABgAGAAYABgAGAAYAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABABgAGAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAYAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAZABgAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAYABgAGAAYABgAGAAYABgAGAAYAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAwADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAwABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAGQABAAEAGQAZABkAGQAZABkAGQAZABkAGQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAaABoAGgAaABoAGgAaABoAGgAaABoAGgAaABoAGgAaABoAGgAaABoAGgAaABoAGgAaABoAEgAZABEAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAS
ABIAEgASABIAEgASABIAEgASABEAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABEAEgASABkAGQAZABkAGQARABkAGQAZABIAGAAY
ABgAGAASABIAEgASABIAEgASABIAEgAYABgAGAAYABgAGAAYABkAGQAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAEgASABIAEgASABIAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABEAEQARABkAGQAZABkAGQARABkAGQAZABkAGQAZABEAGQAZ
ABkAGQADAAEAAQADAAMAAwADAAMAAwADAAMAAwAZABkAGQAZABkAGQAZABkAGQADABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAR
ABkAGQAZABkAAwAZABkAGQAZABkAGQAZABsAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQARAAEAAQADAAMAAQADAAMAAwABAAEAAwADABkAGQAZABkAGQAZ
ABkAEQAZABkAGQAZABEAEQARABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAFAAbABQAGQARABsAGQAZABQAEwATAAMAAwAZABkAGQAZ
ABkAAwADAAMAAwADAAMAAwADAAMAAwADAAMAEQAZABkAGQAZABkAGQAZABkAGQAZABkAGQARABkAGQAZAAEAAQADABkAAwABAAMAGQAZABkAHAAcABwAHAAc
ABkAGQAZABkAGQAZABkAGQARABkAGQAZABkAGQAZABkAGQAZABkAGQAZABEAGQAZABkAGQAZABkAGQAZABkAEQAZABkAGQAZABkAGQARABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZAAMAGQADABQAGwAZABkAFAAUABQAFAAbABsAGwAUABQAGwAbABkAGQARABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABsAGwAbABsAFAAbABsAGwAbABsAGwAbABsAGwAbABsAGwAbABsAGQAZABkAGwARABkAGQAZABsAGwAbABkAGwAb
ABsAGQAZABkAGQAZABkAGQAbABkAGwAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAEQAZABkAGQAZABkAGQAbABkAGQAZABkAGQARABkAGQAR
ABkAGQAZABkAGQAZABkAEQAZABkAGQARABkAGQAZABkAGQAZABkAGQAZABkAGQARABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQARABkAGQAZABkAEQAZ
ABkAGQAZABEAEQARABkAGQAZABEAEQARABEAGQAZABkAGQAZABkAGQAZABkAEQAZABEAEQARABkAAwABABkAGQAZABkAGQAZABkAGQAZABEAGQAZABkAGQAR
ABkAGQAZABkAEQARABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZAAEAAQABAAEAAQABAAEAAQABAAEAAQADAAMAGQAZABkAGQABABEAEQARABEAEQARABEAEQARABEAEQARABEAEQARABEAEQARABEAEQARABEAEQAR
AAEAAQABAAEAAQABAAEAAwADAAEAAQADABMAEwADAAMAAwADABsAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAwABAAEAAwADAAMAAwABAAEAEwABAAEAAQAB
ABsAGwABAAEAAQABAAEAAQABAAEAAQABAAEAAQABABkAAwABAAEAAwABAAEAAQABAAEAAQABAAEAAwADAAEAAQABAAEAAQABAAEAAQABAAMAAQABAAEAAQAB
AAMAAwADAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAMAAwADAAEAAQABAAEAAQABAAEAAQADAAMAAwABAAEAAwABAAMAAQABAAEAAQADAAEAAQABAAEAAQAB
AAMAAQABAAEAAwABAAEAAQABAAEAAQADABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABEAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAbABsAGwAZ
ABkAGQAbABsAGwAbABsAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAGQAZABkAGQAZABkAGQARABkAGQAZABkAGQARABkAGQAZABEAGQAZABEAGQAZABkAEQAZABkAGQAZABkAGQAZABkAGQAZ
ABsAGQAZABkAGQAZABkAGQAZABkAEQAZABkAGQAZABEAGQAbABsAGwAZABkAEQARABkAEQAZABkAGQAbABkAGQAZABkAGQABAAEAAQABAAEAAwAbAAMAAwAD
ABkAGQAZAAEAAQAZABkAGQAZABgAGAAYABkAGQAZABkAAwADAAMAAwADAAMAAQABAAEAAwABABkAGQAYABgAGAADAAEAAQADABkAGQAZABkAGQAZABkAGQAZ
ABgAGAAYAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAYABgAGAAY
ABgAGAAZABkAGQAZABkAGQAZABkAGQAZABkAGQAYABgAGAAYABkAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAGAAYABgAGAABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAGAAYABgAGAAYABgAGAAYAAEAAQABAAEAAQABAAEAAQABAAEAGAAYABgAGAAYABgAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAYABgAGAAYABgAGAAYABgAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAGAAYAAEAAQABAAEAAQABAAEAAQABAAEAAQABABgAGAAY
ABgAAQABABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAABAAEAAQABAAEAAQABAAEAAQAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAABAAEAAQABAAEAAQABAAEAAQABAAEAAQAbABkAGQAbABkAGQAZABkAGQAZABkAGQAb
ABsAGwAbABsAGwAbABsAGQAZABkAGQAZABkAGwAZABkAGQAZABkAGQAZABkAGQAbABsAGwAbABsAGwAbABsAGwAbABkAAQAbABsAGwAZABkAGQAZABkAGQAZ
AAEAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGwAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABsAGwAZABsAGwAZABsAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABsAGwAbABkAGwAbABsAGwAbABsAGwAbABsAGwAbABsAGwAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAB
AAEAAQABAAEAGAAYABgAGAAYABgAGAAYAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQAYABgAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAYABgAGAAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGAAYABgAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZABsAGwAbABkAGAAZABgAGAAYABgAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGQAZ
ABkAGQAYABgAGQAZABkAGQAZABkAGQAZABkAGQAZABkAGAAYABgAGAAZABsAGwAbABsAGwAbABsAGwAbABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAYABgAGAAY
ABgAGAAYABgAGAAYABgAAQABABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgAS
ABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgASABIAEgABAAEAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAE
AAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAEAAQABAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAd
AB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAd
AB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAd
AB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAd
AB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAd
AB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0AHQAdAB0ABAAB
AAEBWQF0AA0AGQCBAHwAcQAiACQALAA0AHkAVABMAFoBAgBhAWIBcgA6AEIAWQBaABIAYgBuAAUAAAABAAIAAwAEAAUABgAHAAgACQAKAAsADAANAA4ADwAQ
ABEAEgATABQAFQAWABcAGAAZABoAGwAcAB0AHgAfACAAIQAiAKMAJAAlACYAJwAoACkAKgArACwALQAuAC8AMAAxADIAMwA0ADUANgA3ADgAOQA6ADsAPAA9
AD4APwBAAEEAQgBDAEQARQBGAEcASABJAEoASwBMAE0ATgBPAFAAUQBSAFMAVABVAFYAVwBYAFkAWgBbAFwAXQBeAF8AYABhAGIAYwBkAGUAZgBnAGgAaQBq
AGsAbABtAG4AbwBwAHEAcgBzAHQAdQB2AHcAeAB5AHoAewB8AH0AfgB/AIAAgQCCAIMAhACFAIYAhwCIAIkAigCLAIwAjQCOAI8AkACRAJIAkwCUAJUAlgCX
AJgAmQCaAJsAnACdAJ4AnwCgAKEAogCjAKQApQCmAKcAqACpAKoAqwCsAK0ArgCvALAAsQCyALMAtAC1ALYAtwC4ALkAugC7ALwAvQC+AL8AwADBAMIAwwDE
AMUAxgDHAMgAyQDKAMsAzADNAM4AzwDQANEA0gDTANQA1QDWANcA2ADZANoA2wDcAN0A3gDfAOAA4QDiAOMA5ADlAOYA5wDoAOkA6gDrAOwA7QDuAO8A8ADx
APIA8wD0APUA9gD3APgA+QD6APsA/AD9AP4A/wAAAAEAAgADAAQABQAGAAcACAAJAAoACwAMAA0ADgAPABAAEQASABMAFAAVABYAFwAYABkAGgAbABwAHQAe
AB8AIAAhACIAIwAkACUAJgAnACgAKQAqACsALAAtAC4ALwAwADEAMgAzADQANQA2ADcAOAA5ADoAOwA8AD0APgA/AEAAQQBCAEMARABFAEYARwBIAEkASgBL
AEwATQBOAE8AUABRAFIAUwBUAFUAVgBXAFgAWQBaAFsAXABdAF4AXwDGJZIlCSQMJA0kCiSwALEAJCQLJBglECUMJRQlPCW6I7sjACW8I70jHCUkJTQlLCUC
JWQiZSLAA2AiowC3AH8AgACBAIIAgwCEAIUAhgCHAIgAiQCKAIsAjACNAI4AjwCQAJEAkgCTAJQAlQCWAJcAmACZAJoAmwCcAJ0AngCfAKAAoQCiAKMApACl
AKYApwCoAKkAqgCrAKwArQCuAK8AsACxALIAswC0ALUAtgC3ALgAuQC6ALsAvAC9AL4AvwDAAMEAwgDDAMQAxQDGAMcAyADJAMoAywDMAM0AzgDPANAA0QDS
ANMA1ADVANYA1wDYANkA2gDbANwA3QDeAN8A4ADhAOIA4wDkAOUA5gDnAOgA6QDqAOsA7ADtAO4A7wDwAPEA8gDzAPQA9QD2APcA+AD5APoA+wD8AP0A/gD/
AAkAAAAGAAAABwAAAAoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAAEAAAAA
AAAAAAAAAAAAAAAAAAAABgAAAAEAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAADQAAAAEAAAAAAAAAAAAAAAAAAAAAAAAADwAA
AAEAAAAAAAAAAAAAAAAAAAAAAAAAEQAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAEwAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAAEAAAAAAAAAAAAAAAAAAAAA
AAAABQAAAAEAAAAAAAAAAAAAAAAAAAAAAAAADgAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAEgAAAAEAAAAAAAAAAAAA
AAAAAAAAAAAAFAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAGQAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAGgAAAAEAAAAAAAAAAAAAAAAAAAAAAAAACwAAAAEAAAAA
AAAAAAAAAAAAAAAAAAAADAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAACgAAAAEAAABJBxAABAAAAIoDEAAEAAAA9gkQAAQAAADOARAABAAAAA4BEAAEAAAAfQwQ
AAQAAADmEBAABAAAAM4PEAAEAAAA9AUQAAUAAABIKRAABQAAAGgJEAAFAAAA2gQQAAUAAADuBRAABQAAACcLEAAFAAAAcAMQAAcAAAB/BhAABwAAACQHEAAH
AAAAQgkQAAcAAAAKARAACAAAADkMEAAIAAAAWgwQAAgAAAAwDBAACAAAACYMEAAIAAAA4AQQAAgAAAD1DhAACAAAAHkMEAAIAAAAQwIQAAgAAAD6BRAACAAA
AKILEAAIAAAAeAoQAAgAAABbBhAACQAAAFkMEAAJAAAAGwwQAAkAAABPDBAACQAAAAYMEAAJAAAALwwQAAkAAADrDhAACQAAACUMEAAJAAAAQgwQAAoAAAAQ
DBAACgAAAOsJEAAKAAAA4A4QAAoAAADuDxAACwAAACAHEAALAAAATQwQAAsAAAD+DhAACwAAAAQMEAALAAAArAEQAAwAAADFARAADQAAAG4BEAAOAAAASQ4Q
AA4AAAAeCxAADgAAAMQGEAAPAAAAnwYQABAAAADUBhAAEgAAALAGEAATAAAAAgAAAAUAAAAGAAAACAAAAAsAAAAMAAAADwAAAAMAAAARAAAADwAAAAgAAAAH
AAAACgAAAAUAAAAAAAAAAwAAAA0AAAAgAAAACwAAABQAAAAVAAAAFgAAABcAAAAEAAAAFAAAAAwAAAAhAAAAAAAAABAAAAAKAAAABwAAABgAAAAZAAAAGgAA
ABsAAAAdAAAAFwAAABwAAAASAAAAEwAAABEAAAAVAAAADgAAAA0AAAAfAAAAFgAAAB4AAAABAAAACQAAAAIAAAAOAAAABAAAABkAAAAYAAAAGwAAABoAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAgAAAAOAAAADgAAABIAAAAeAAAAJgAAACoAAAAvAAAAMAAAADEAAAA0AAAANQAAADYAAAA2AAAANwAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAAEBAQEBAAAAAAAAAAAAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAB
AQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAABAQEBAQAAAAAAAAAAAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAA
AAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEB
AQEAAAAAAAAAAAAAAAIBAgICAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEB
AAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAQEBAQAAAAAAAAAAAAAAAQEBAQEAAAABAQEBAQAA
AAAAAAAAAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAB
AQEBAQAAAAEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAAAAAAAAAA
AAAAAQEBAQEAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAAAAAAAAAAAAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAABAQEBAQAAAAAAAAAAAAAAAQEBAQEAAAABAQEBAQAAAAAAAAAAAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAB
AQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAQEBAQEAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAA
AAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAYIAAAAAAAEAAAAAAAEBAQEBAAAAAQEBAQE
AAAAAAAEAAAAAAAAAAQGCAAAAAAABAYIAAAABgYGBgYAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAABAQEBAQAAAAEBAQEBAAAAAAABAAAAAAAAAAE
AAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEAAAAAAAAAAAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAA
AAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEAAQEAAAABAQEBAQAAAAEBBQEBAAAAAQEFAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAB
AQEBAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAAAAAAAAAAAAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEB
AAAAAQEBAQEAAAABAQEBAQAAAAEBAAEBAAAAAQEBAQEAAAABAQUBAQAAAAEBBQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAQEBAQAAAAAAAAAAAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEB
AQEAAAABAQEBAQAAAAEBAQEBAAAAAQEFAQEAAAAEBAQEBAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABggAAAAA
AAQAAAAAAAQEBAQEAAAABAQEBAQAAAAAAAQAAAAAAAAABAYIAAAAAAAEBggAAAAGBgYGBgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgIAAAAAAAEAAAAAAAEBAQEBAAA
AAQEBAQEAAAAAAAEAAAAAAAAAAQICAAAAAAABAgIAAAACAgICAgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAQEBAQAAAAAAAAAAAAAAAQEB
AQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAAAAAAEBAQEBAAAAAQEBAQEAAAABAQEBAQAAAAEBAQEB
AAAAAQEBAQAAAAABAQEBAAAAAAEBAQcJAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAsAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAACqqqqqAAAAAAAAAAAWARAABAAAAEQOEAAEAAAAhgoQAAUAAAD6BxAABgAAAHUJEAAGAAAAPgoQAAcAAAD3
EBAABwAAABMBEAAHAAAA0w8QAAcAAAB2BhAACAAAANoCEAAIAAAA/AQQAAkAAABfAhAACgAAAJUEEAAKAAAA/QYQAAoAAAAZDhAACgAAAGUGEAAKAAAAqw0Q
AAsAAAAVChAACwAAAEgFEAAMAAAAXQgQAA0AAABVBRAADQAAACQOEAANAAAAmhAQAA0AAABjDBAADgAAAIYOEAAOAAAAOAQQAA4AAADtCBAADgAAADMPEAAP
AAAApQoQAA8AAAABCBAAEAAAAMwNEAATAAAA9gEQABMAAABJCBAAEwAAADIOEAAWAAAA1QgQABcAAAAGDQIDCh4SDA4LDxwVIiAfCAkdEwAUERkWFxsjBwUY
EAQBGiEAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAADAAAABQAAAAkAAAALAAAADAAAABEAAAATAAAAFAAAABgAAAAcAAAAHgAAAB8AAAAfAAAAHwAAACIAAAAi
AAAAIgAAACMAAAABAAIAAgHw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw
8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx
8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8QICAgICAgICAgICAgICAgICAgICAgICAgICAgICAhMDAwMDAwMDAwMDAwMjAwM0BAQERPHx8fHx8fHx
8fHxAAAAABkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAF49EAADAAAAyDsQAAMAAAApLxAABAAAAEVUEAAEAAAAkDQQAAQAAACiNBAABAAAAEk3EAAE
AAAAejAQAAQAAAAKOhAABAAAAEg7EAAEAAAAHW8QAAQAAADHPRAABAAAAFs/EAAEAAAAFy8QAAQAAAC7KRAABAAAADNUEAAEAAAAIVQQAAQAAAAvMhAABAAA
APFXEAAEAAAAV1QQAAQAAAADWBAABAAAABVYEAAEAAAAJ1gQAAQAAADtWBAABQAAAK9LEAAFAAAAolsQAAUAAACPWxAABQAAAHxbEAAFAAAAaVsQAAUAAAAm
WRAABQAAABNZEAAFAAAAmywQAAUAAAAAWRAABQAAAJ1dEAAFAAAAsF0QAAUAAADDXRAABQAAANZdEAAFAAAA/F0QAAUAAAAPXhAABQAAAFktEAAFAAAABl8Q
AAUAAABsLRAABQAAAEFREAAFAAAAIl4QAAUAAAB6URAABQAAAGdREAAFAAAAVFEQAAUAAAA1XhAABQAAAEheEAAFAAAAwksQAAUAAAD4KxAABQAAAJxLEAAF
AAAAiUsQAAUAAADCRhAABQAAAK9GEAAFAAAAnEYQAAUAAABbXhAABQAAAOldEAAFAAAAiUYQAAUAAAA+QRAABQAAACtBEAAFAAAAGEEQAAUAAAAFQRAABQAA
AG5eEAAFAAAAgV4QAAUAAAC/bhAABQAAANc8EAAFAAAABzwQAAUAAACkKhAABQAAAIQ7EAAFAAAAcTsQAAUAAACUXhAABQAAAKdeEAAFAAAAul4QAAUAAAAz
ORAABQAAAM1eEAAFAAAANjcQAAUAAADgXhAABQAAAPNeEAAFAAAAkV8QAAYAAAA1YRAABgAAAJ1iEAAGAAAACW8QAAYAAABpXxAABgAAAKtuEAAGAAAAsWIQ
AAYAAABVXxAABgAAAEFfEAAGAAAALV8QAAYAAADFYhAABgAAAA1UEAAGAAAA+VMQAAYAAAAZXxAABgAAAOVTEAAGAAAA0VMQAAYAAAA9VRAABgAAAFFVEAAG
AAAAZVUQAAYAAACJYhAABgAAAHViEAAGAAAAYWIQAAYAAABNYhAABgAAANliEAAGAAAApV8QAAYAAADtYhAABgAAAAFjEAAGAAAAuV8QAAYAAAA5YhAABgAA
ACViEAAGAAAAFWMQAAYAAAAVbRAABgAAAAFtEAAGAAAA7WwQAAYAAAApYxAABgAAANlsEAAGAAAAxWwQAAYAAAA9YxAABgAAAFFjEAAGAAAAZWMQAAYAAAB5
YxAABgAAALFsEAAGAAAAnWwQAAYAAADNXxAABgAAAOFfEAAGAAAAjWMQAAYAAACJbBAABgAAAHVsEAAGAAAAYWwQAAYAAABNbBAABgAAAKFjEAAGAAAAtWMQ
AAYAAAA5bBAABgAAACVsEAAGAAAAEWwQAAYAAAD9axAABgAAAPVfEAAGAAAA/U8QAAYAAADpaxAABgAAANVrEAAGAAAACWAQAAYAAADpTxAABgAAAMFrEAAG
AAAA1U8QAAYAAADBTxAABgAAAMljEAAGAAAA3WMQAAYAAADxYxAABgAAAB1gEAAGAAAArWsQAAYAAACZaxAABgAAAIVrEAAGAAAAcWsQAAYAAABWKxAABgAA
AAVkEAAGAAAAXWsQAAYAAABJaxAABgAAADVrEAAGAAAAIWsQAAYAAAARYhAABgAAAP1hEAAGAAAAMWAQAAYAAADtVhAABgAAABlkEAAGAAAADWsQAAYAAAD5
ahAABgAAAOVqEAAGAAAA0WoQAAYAAAC9ahAABgAAAKlqEAAGAAAA9jkQAAYAAACVahAABgAAAIFqEAAGAAAAbWoQAAYAAABZahAABgAAAC1kEAAGAAAARWoQ
AAYAAABFYBAABgAAAEFkEAAGAAAAVWQQAAYAAABpZBAABgAAADFqEAAGAAAAHWoQAAYAAAA0OxAABgAAAAlqEAAGAAAAYU4QAAYAAABNThAABgAAADlOEAAG
AAAA9WkQAAYAAAB9ZBAABgAAAJFkEAAGAAAApWQQAAYAAAC5ZBAABgAAAAFXEAAGAAAA4WkQAAYAAABLPBAABgAAAHVOEAAGAAAAzWkQAAYAAAC5aRAABgAA
AJI8EAAGAAAApWkQAAYAAAAVVxAABgAAAClXEAAGAAAAkWkQAAYAAADNZBAABgAAABs9EAAGAAAA4WQQAAYAAAD1ZBAABgAAAFlgEAAGAAAACWUQAAYAAAAd
ZRAABgAAADFlEAAGAAAARWUQAAYAAABZZRAABgAAAG1gEAAGAAAAbWUQAAYAAAB9aRAABgAAAGlpEAAGAAAAQD4QAAYAAACBYBAABgAAAIFlEAAGAAAAlWAQ
AAYAAACVZRAABgAAAFVpEAAGAAAAQWkQAAYAAACpZRAABgAAAOlhEAAGAAAA1WEQAAYAAAC9ZRAABgAAAEc/EAAGAAAA0WUQAAYAAABtPxAABgAAAC1pEAAG
AAAAGWkQAAYAAAAFaRAABgAAAOVlEAAGAAAA8WgQAAYAAAD5ZRAABgAAAN1oEAAGAAAAwWEQAAYAAABJQBAABgAAAKlgEAAGAAAArWEQAAYAAAC9YBAABgAA
AA1mEAAGAAAAyWgQAAYAAADRYBAABgAAACFmEAAGAAAANWYQAAYAAABJZhAABgAAAJlhEAAGAAAAhWEQAAYAAAB9XxAABgAAAF1mEAAGAAAAcWYQAAYAAACF
ZhAABgAAACFhEAAGAAAAtWgQAAYAAAChaBAABgAAAI1oEAAGAAAAmWYQAAYAAACtZhAABgAAAMFmEAAGAAAA1WYQAAYAAADlYBAABgAAAPlgEAAGAAAADWEQ
AAYAAAApVRAABgAAAHloEAAGAAAAZWgQAAYAAABRaBAABgAAAD1oEAAGAAAAKWgQAAYAAAAVaBAABgAAAAFoEAAGAAAA7WcQAAYAAADpZhAABgAAAP1mEAAG
AAAAEWcQAAYAAAAlZxAABgAAAP1DEAAGAAAAEUQQAAYAAAAlRBAABgAAADlEEAAGAAAAOWcQAAYAAABNZxAABgAAAGFnEAAGAAAAdWcQAAYAAADZZxAABgAA
AMVnEAAGAAAAsWcQAAYAAACdZxAABgAAAElhEAAGAAAAXWEQAAYAAABxYRAABgAAAIlnEAAGAAAAeUUQAAYAAACNRRAABgAAAKFFEAAGAAAAtUUQAAYAAADE
XBAABwAAALhVEAAHAAAAP1sQAAcAAAAqWxAABwAAABVbEAAHAAAAMC4QAAcAAACPKhAABwAAANIvEAAHAAAAPVcQAAcAAACWbhAABwAAAFBuEAAHAAAA7VAQ
AAcAAAA1VhAABwAAACxREAAHAAAAvDIQAAcAAACFPRAABwAAAKcvEAAHAAAASFoQAAcAAAAzWhAABwAAAB5aEAAHAAAAeVUQAAcAAABYQhAABwAAAENCEAAH
AAAALkIQAAcAAABUWxAABwAAAAUuEAAHAAAAF1EQAAcAAABSVxAABwAAABlCEAAHAAAAXFMQAAcAAABHUxAABwAAADJTEAAHAAAACVoQAAcAAAAdUxAABwAA
AD5tEAAHAAAAUS8QAAcAAAB0VhAABwAAAHwvEAAHAAAA8EAQAAcAAAACURAABwAAAI5VEAAHAAAAhVwQAAcAAACjVRAABwAAACltEAAHAAAAZ1cQAAcAAAB8
VxAABwAAAF9WEAAHAAAAHT8QAAcAAACaXBAABwAAAK9cEAAHAAAASlYQAAcAAAAyPxAABwAAAKtYEAAIAAAAl20QAAgAAACRLxAACAAAAGptEAAIAAAA3SwQ
AAgAAACVWBAACAAAAMwrEAAIAAAAwVgQAAgAAADXWBAACAAAAGYvEAAIAAAAOy8QAAgAAABDSBAACAAAAC1IEAAIAAAAF0gQAAgAAADvLRAACAAAAAFIEAAI
AAAAvVoQAAgAAACxPRAACAAAABouEAAIAAAAxG0QAAgAAABvPRAACAAAAKYyEAAIAAAAvC8QAAgAAABFXRAACAAAAPFtEAAIAAAAOm4QAAgAAABWLBAACAAA
AHkqEAAIAAAAQCwQAAgAAADTWhAACAAAAFtdEAAIAAAAcV0QAAgAAADpWhAACAAAAB02EAAIAAAAozAQAAgAAACHXRAACAAAAOIrEAAIAAAAcjcQAAgAAACf
NxAACAAAAP9aEAAIAAAApEAQAAkAAADqOhAACQAAALs6EAAJAAAAVToQAAkAAACMMBAACQAAABw5EAAJAAAA7TgQAAkAAACCKxAACQAAAFg4EAAJAAAAWjsQ
AAkAAACINxAACQAAAC4wEAAJAAAAWzcQAAkAAAD/LxAACQAAAB83EAAJAAAAvTYQAAkAAADZOxAACQAAAAY2EAAJAAAAfzUQAAkAAAD7NBAACQAAAPA7EAAJ
AAAAzDQQAAkAAABaTRAACQAAAHk0EAAJAAAALE0QAAkAAABDTRAACQAAABVNEAAJAAAARz0QAAkAAACaPRAACQAAAPE9EAAJAAAAbD4QAAkAAACbPhAACQAA
AF0uEAAJAAAAU20QAAkAAACMLhAACQAAAAY/EAAJAAAAOVgQAAkAAACIMxAACQAAAFBYEAAJAAAAZ1gQAAkAAACAbRAACQAAAH5YEAAJAAAAHzMQAAkAAAAv
KhAACQAAAK1tEAAJAAAAxiwQAAkAAACELBAACQAAAHVAEAAJAAAAUUEQAAkAAABoQRAACQAAAH9BEAAJAAAAlkEQAAkAAAChQxAACQAAALhDEAAJAAAAz0MQ
AAkAAADmQxAACQAAANptEAAJAAAATUQQAAkAAABkRBAACQAAAHtEEAAJAAAAGDIQAAkAAACSRBAACQAAAEIxEAAJAAAAQi0QAAkAAADdSBAACgAAAIFUEAAK
AAAAmVQQAAoAAACxVBAACgAAAMlUEAAKAAAA4VQQAAoAAAD5VBAACgAAABFVEAAKAAAAaVQQAAoAAAC5UxAACgAAAKFTEAAKAAAAiVMQAAoAAABxUxAACgAA
AAVTEAAKAAAA7VIQAAoAAADVUhAACgAAAL1SEAAKAAAApVIQAAoAAACNUhAACgAAAHVSEAAKAAAAKjEQAAoAAADtThAACgAAANVREAAKAAAAvVEQAAoAAACl
URAACgAAAI1REAAKAAAANU8QAAoAAAAdTxAACgAAAAVPEAAKAAAAuU0QAAoAAAChTRAACgAAAIlNEAAKAAAAcU0QAAoAAABFLhAACgAAAHQuEAAKAAAAcUsQ
AAoAAACRVxAACgAAAKlXEAAKAAAAwVcQAAoAAADZVxAACgAAACotEAAKAAAAWUsQAAoAAABBSxAACgAAAClLEAAKAAAAEUsQAAoAAAD5ShAACgAAAOFKEAAK
AAAAyUoQAAoAAABBShAACgAAAClKEAAKAAAAEUoQAAoAAAD5SRAACgAAAOFJEAAKAAAAriwQAAoAAADJSRAACgAAAGwsEAAKAAAAOVkQAAoAAABRWRAACgAA
AGlZEAAKAAAAgVkQAAoAAACxSRAACgAAAJlJEAAKAAAADUkQAAoAAAD1SBAACgAAAMVIEAAKAAAAgUcQAAoAAABpRxAACgAAAFFHEAAKAAAAXVoQAAoAAAB1
WhAACgAAAI1aEAAKAAAApVoQAAoAAAA5RxAACgAAAHFGEAAKAAAAWUYQAAoAAABBRhAACgAAAClGEAAKAAAAEUYQAAoAAAD5RRAACgAAAOFFEAAKAAAAyUUQ
AAoAAABhRRAACgAAAElFEAAKAAAAMUUQAAoAAAAZRRAACgAAALVCEAAKAAAAnUIQAAoAAACFQhAACgAAAG1CEAAKAAAAXVIQAAoAAACMQBAACgAAAF1AEAAK
AAAAMUAQAAoAAAAAQBAACgAAAM8/EAAKAAAA7j4QAAoAAACDPhAACgAAAFQ+EAAKAAAA2T0QAAoAAAAvPRAACgAAAAM9EAAKAAAAvzwQAAoAAADnLxAACgAA
ALA7EAAKAAAAFjAQAAoAAADSOhAACgAAAKM6EAAKAAAABDkQAAoAAADVOBAACgAAAGorEAAKAAAAQDgQAAoAAAC1NxAACgAAAAc3EAAKAAAABSsQAAoAAACl
NhAACgAAAGc1EAAKAAAATzUQAAoAAADjNBAACgAAALQ0EAAKAAAAuDMQAAoAAABwMxAACgAAAAczEAAKAAAAFyoQAAoAAAAzPBAACgAAAFoyEAAKAAAAADIQ
AAoAAADoMRAACgAAAH5uEAAKAAAA5ikQAAoAAAB3TBAACwAAANVGEAALAAAAzSkQAAsAAABgKhAACwAAAM8xEAALAAAAZW4QAAsAAADsKhAACwAAAEEyEAAL
AAAAIW4QAAsAAACNMhAACwAAALMrEAALAAAAnzMQAAsAAADuNhAACwAAACc4EAALAAAAiTgQAAsAAABsXBAACwAAAFNcEAALAAAAOlwQAAsAAAAhXBAACwAA
ALw4EAALAAAA3TkQAAsAAAAbOxAACwAAAJc7EAALAAAAGjwQAAsAAAB5PBAACwAAAKY8EAALAAAA6jwQAAsAAAC2PxAACwAAAOc/EAALAAAAGEAQAAsAAAD+
KRAACwAAAO5GEAALAAAA1FYQAAsAAAC7VhAACwAAAKJWEAALAAAAiVYQAAsAAAAHRxAACwAAACBHEAALAAAARUwQAAsAAABeTBAACwAAAJBMEAALAAAAiU4Q
AAsAAACiThAACwAAALtOEAALAAAA1E4QAAsAAACFUBAADAAAAMM5EAAMAAAA01AQAAwAAAC5UBAADAAAAJ9QEAAMAAAARioQAAwAAACcPxAADAAAAM1CEAAM
AAAA0ioQAAwAAADnQhAADAAAAAduEAAMAAAAAUMQAAwAAACZKxAADAAAAO0yEAAMAAAAG0MQAAwAAAAmLBAADAAAANZAEAAMAAAAXzwQAAwAAAABOxAADAAA
AB9OEAAMAAAABU4QAAwAAADrTRAADAAAANFNEAAMAAAAG1YQAAwAAAABVhAADAAAAJlHEAAMAAAAs0cQAAwAAADsNRAADAAAANQ2EAAMAAAAzVUQAAwAAADn
VRAADAAAAM1HEAAMAAAADTgQAAwAAADnRxAADAAAAG84EAAMAAAAYDAQAAwAAACiOBAADAAAAAZcEAANAAAA0FsQAA0AAACBPxAADQAAAIo2EAANAAAA61sQ
AA0AAADZXBAADQAAAKpIEAANAAAAj0gQAA0AAAB0SBAADQAAAFlIEAANAAAA9FwQAA0AAAAPXRAADQAAAKlMEAANAAAAxEwQAA0AAADfTBAADQAAAPpMEAAN
AAAADy0QAA0AAACIOhAADQAAAJwtEAANAAAArUEQAA0AAABFMBAADQAAAGI5EAANAAAAtVsQAA0AAAAjNBAADQAAAAssEAANAAAA7DMQAA0AAACGQxAADQAA
AGtDEAANAAAAUEMQAA0AAAA1QxAADQAAACpdEAANAAAAcjIQAA0AAAAPMRAADQAAALcqEAANAAAA/kEQAA0AAADjQRAADQAAAMhBEAANAAAAu0AQAA0AAAAl
PhAADQAAAO5uEAANAAAA1C0QAA0AAAC1WRAADgAAANFZEAAOAAAA7VEQAA4AAADSbhAADgAAAJlZEAAOAAAAszEQAA4AAADtWRAADgAAAKlEEAAOAAAAxUQQ
AA4AAADhRBAADgAAAP1EEAAOAAAA+y4QAA4AAAA6KxAADgAAAMEuEAAOAAAA0TIQAA4AAADzLBAADgAAAGw6EAAOAAAAOToQAA4AAABUMxAADgAAACVSEAAO
AAAA0DMQAA4AAAAJUhAADgAAAAc0EAAOAAAA1jAQAA4AAABZShAADgAAAHVKEAAOAAAARjkQAA4AAACRShAADgAAAK1KEAAOAAAA1UsQAA4AAADxSxAADgAA
AA1MEAAOAAAAtDUQAA4AAADQNRAADgAAAFE2EAAOAAAAKUwQAA4AAABBUhAADgAAACVJEAAPAAAAEVAQAA8AAAC5MBAADwAAANE+EAAPAAAAfy0QAA8AAABo
UBAADwAAAHgxEAAPAAAACD4QAA8AAABLUBAADwAAAC5QEAAPAAAAHDoQAA8AAADyMBAADwAAAFw0EAAPAAAAHSsQAA8AAAC3LRAADwAAAKRPEAAPAAAAX0kQ
AA8AAABCSRAADwAAAHxJEAAPAAAAbTYQAA8AAACHTxAADwAAAE1PEAAPAAAAak8QAA8AAACjLhAAEAAAADYzEAAQAAAA3S4QABAAAAA+NBAAEAAAADE1EAAQ
AAAAljUQABAAAACVMRAAEAAAADM2EAAQAAAAEjUQABEAAADuNxAAEQAAALI+EAARAAAAWTEQABEAAADNNxAAEwAAAKE5EAAUAAAAfTkQABYAAAD/AAAA0rSM
AL6+vgDNhT8AAP//AAD//wAA/wAAAACAAP/XAADNhT8AAICAAP/AywDdoN0Avr6+AP/6+gDumkkA/6VPAAAA/wD/AAAAi1orAO4AAADNAAAAiwAAAP+1xQAA
zc0Ai2aLAM2WzQDuru4A/7v/AItjbADNkZ4A8P//AO6puAAAAAAAAAAAAAMDAwADAwMABQUFAAgICAD///8AFxcXAAAAAAD/1wAACAgIAIt1AADNrQAA7skA
AAoKCgAKCgoAAIuLAP//8AAA7u4AAP//AAAAiwAAAM0AAADuAA0NDQAFBQUAAAD/AIuJiQDNyckA7unpAP/6+gANDQ0ADw8PAICAAAD/f1AApSoqAPrw5gD1
3rMA9fXcAA8PDwASEhIAEhISAPDmjAAUFBQAAP8AABQUFAAXFxcAISEhADs7OwBSUlIAwMDAAB8fHwBLAIIAVFRUABwcHAAcHBwAGhoaAFRUVACLfmYAzbqW
ABoaGgDu2K4A/+e6AO47OwDNMzMAiyMjAFJSUgBPT08AT09PAE1NTQBXV1cAISEhAFdXVwBZWVkAJCQkAE1NTQBKSkoAWVlZAPz8/AD8/PwA+vr6AFxcXAD6
+voA9/f3AFxcXABeXl4AXl5eAGFhYQD39/cA9fX1ACQkJAAmJiYAYWFhAPX19QDy8vIA8vLyAPDw8ABjY2MAY2NjAPDw8ADt7e0A7e3tAOvr6wAmJiYAi4ZO
AOvr6wDo6OgAKSkpAM3GcwDo6OgA7uaFAP/2jwBmZmYAZmZmAGlpaQApKSkA5eXlAOXl5QDj4+MA4+PjAP/kxABpaWkA4ODgAODg4ADe3t4A3t7eAEpKSgBH
R0cAKysrAP9yVgBra2sA29vbANvb2wDZ2dkA2dnZANbW1gDW1tYA//8AANTU1ADU1NQA0dHRANHR0QBra2sAz8/PACsrKwBubm4Abm5uAHBwcADPz88AzMzM
AKBSLQDMzMwAAM0AAADuAAAA/wAAycnJAHBwcABzc3MAc3NzAHV1dQDualAAycnJAPqAcgAAiwAAx8fHAMfHxwD/pQAAxMTEAM1bRQCLPi8AxMTEAHV1dQD/
Y0cAeHh4AHh4eAAuLi4Aenp6AHp6egB9fX0AfX19AH9/fwAuLi4Af39/AMLCwgDCwsIAsDBgADAwMACCgoIAMDAwAIKCggC/v78Av7+/AIWFhQBHR0cARUVF
AIWFhQDugu4Ah4eHANpw1gC9vb0Avb29ALq6ugCHh4cAurq6AIqKigC4uLgARUVFAKAg8AAzMzMAQkJCADMzMwCKiooAuLi4ADY2NgCMjIwAjIyMAI+PjwBC
QkIAQEBAAB8fHwCPj48AkZGRAJGRkQA7OzsAtbW1ALW1tQCzs7MAlJSUAJSUlACWlpYAlpaWADY2NgA4ODgAODg4AP9AQACzs7MAsLCwALCwsACtra0Ara2t
AKurqwCrq6sAqKioAJmZmQCZmZkAnJycAJycnAD///AA7u7gAM3NwQCLi4MAnp6eAJ6engChoaEAoaGhAKioqACmpqYApqamAKOjowA9PT0APT09AEBAQACj
o6MA8P//AODu7gDBzc0Ag4uLAFUaiwCLTDkAzWnJAO566QD/g/oAaWlpAP315gCAgIAA/2NHANwUPACLAAAA//8AAP+lAACLiwAAh87rAP9ptACAgIAAixxi
AM0pkADuMKcA/4xpAIt9awDNt54A7tW3AItHiQBpaWkAzc0AAO5cQgD/5MQAi0cmAM1oOQDueUIA/zSzAP+CRwD///8Avr6+AItaAAC+vr4A2L/YAO7uAADu
gmIAmzD/AM1wVAD///8AzU85AIs2JgDNhQAA/wD/AJEs7gB9Js0A7poAAP8A/wDuaqcAqampAICAgACpqakA5ub6AP9utAD/5LUAzWCQAIs6YgC+vr4Avr6+
AEpwiwBsps0AfsDuAGlpaQCHzv8A/wD/AP8UkwBpaWkAAACLAP9ptACHzusAgICAAP/h/wAAi4sAiwAAAPD/8AD99eYA//XuAO4A7gDu0u4AzbXNAM0AzQAu
i1cAAACAAIt7iwD/+NwAAP8AAACAAACLAIsAgACAAM1cXAC8j48A2qUgAAAAgAC9t2sAa44jAP/auQAyzTIA3riHAACAAADT09MAAP8AANPT0wB8/AAAmPuY
ANJpHgAui1cAAGQAAF+eoACyIiIA4P//AC6LVwBA4NAATu6UAEPNgABU/58A/0UAAP8UkwD/tsEAsDBgAIAAAABwgJAAqampAHCAkADQIJAA/xSTAK3Y5gDu
EokAzRB2AKmpqQCLClAARoK0ANzc3AAAAIsA8Pj/APX/+gCgIPAA//XuAO7l3gDNxb8Ai4aCAP/43ADu6M0AzcixAIuIeAAAi4sA8P/wAODu4ADBzcEAQWnh
AIOLgwBqWs0A/+ThALnT7gDudiEAzWYdAItFEwD/MDAA7iwsAM0mJgCLGhoA/38kAItzVQDNqn0A7sWRAP/TmwCLOjoAzVVVAO5jYwD/amoAi2lpAM2bmwDu
tLQAalrNAMD/PgCLaRQAzZsdAO60IgD/wSUAaYsiAJrNMgCz7joAVItUAHzNfACQ7pAAmv+aAHCAkABwgJAAAIaLAP9FAADuQAAAzTcAAIslAAD/5OEAAMXN
AADl7gAA9f8AU4aLAHrFzQCO5e4AmPX/AHqLiwC0zc0A0e7uAOD//wBog4sA8Pj/AJrAzQD1//oA/665AO6irQDNjJUAi19lALLf7gC/7/8AbHuLAJ+2zQDG
4v8ANmSLAE+UzQBcrO4A/z6WAO46jADNMngAiyJSAGO4/wAnQIsAOl/NAENu7gBIdv8ARzyLAGlZzQB6Z+4Ag2//AIt9ewDNt7UA7tXSAP/k4QCLd2UAza+V
AO7LrQD/2rkA/8HBAIAAgACgIPAAiiviAJQA0wCZMswA0CCQAIAAAACwMGAA/7bBAP9FAADwgIAA/4wAANPT0wD0pGAA09PTAM1cXAC8j48AvbdrAGuOIwD/
2rkAMs0yAH//AAB8/AAA/+/VAJj7mAAAZAAAf//UAF+eoADg//8AsODmAK3Y5gBGgrQA9fX1AOmWegAekP8AQWnhAAAAzQCQ7pAA+Pj/AGbNqgAekP8A+Pj/
AP/68AAAAM0AkO6QAP/v1QAekP8AiwCLAAC//wD/3q0AsODmAAD/fwCt/y8Ams0yAGgiiwCaMs0AsjruAL8+/wAiiyIA///gAItFEwD0pGAA6ZZ6AP+gegD/
jAAA8ICAAJkyzACUANMAiiviAPX19QAchu4Ai0UAAM1mAADudgAA/38AABh0zQAQTosAf//UAHbuxgBFi3QAf/8AAHbuAABmzQAARYsAAP//4AD//+AAi4t6
AM3NtADu7tEA//rwALpV0wD/3q0A+uvXAO7PoQCLAIsAzbOLAP/erQCHzvoAi3leAP/6zQCTcNsA/6B6AItFEwAAi0UAAM1mAADudgAA/38Ai1dCAM2BYgAA
v/8AALLuAI+8jwAA/38A/6B6AO6VcgAAms0Arf8vAABoiwCazTIAGRlwACKLIgB6N4sA0V/uALpV0wAgsqoAtFLNAKuC/wBge4sAjbbNAKTT7gCw4v8An3nu
AIlozQDB/8EAtO60AJvNmwBpi2kA//D1ALiGCwAvT08A/+/bABkZcADu6KoA4Gb/AADO0QD/+s0Ar+7uAIuJcADNyaUA7um/AP/6zQBdR4sAAL//AEg9iwD6
69cAi4N4AM3AsADu38wAk3DbANtwkwBmM5kAL09PAO55nwDNaIkA/7kPAGYzmQD/gqsAhHD/AItHXQD/8PUA7uDlAM3BxQCLg4YAd4iZAP/rzQB3iJkAh876
AP/w9QC4hgsA7t2CALDE3gDNlQwAr+7uAO6tDgAAztEAZJXtALv//wCu7u4A7uiqAJbNzQBmi4sAl///AI3u7gB5zc0AVWsvAI+8jwA8s3EAUouLAItlCADK
4f8A/+yLAGSV7QDHFYUAL09PAIuBTAB7aO4A23CTAM2+cADu3IIA7t2CAEg9iwBI0cwA/+vNAC9PTwBuiz0AorXNALzS7gBue4sAILKqAKLNWgDK/3AAvO5o
AHeImQCwxN4Ad4iZAEjRzABmzaoAVWsvAIRw/wA8s3EAZs2qAAD6mgDHFYUAe2juAAD6mgD6+tIA+vrSAAAAAAAAAAAAAAAAAAAAAAACAAAAFwAAAE8AAAAr
AQAAXwEAAIcBAADHAQAASAIAAHUCAACaAgAAwwIAAOgCAAD/AgAABwMAAAsDAAALAwAADAMAAA0DAAANAwAAYWNjZXNzIG9mIHVuaW9uIGZpZWxkICcnIHdo
aWxlIGZpZWxkICcnIGlzIGFjdGl2ZRtbO1IbWz91aW5mbyhzdHJlYW0pOiBleGVjdXRpbmcgQzEgMHggYXMgRVNDIHdhcm5pbmcoc3RyZWFtKTogaW52YWxp
ZCBjaGFyc2V0IGludGVybWVkaWF0ZTogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogdW5pbXBsZW1lbnRlZCBF
U0MgYWN0aW9uOiAAAAAAAAB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgaW5kZXggY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIG5leHQgbGlu
ZSBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgdGFiIHNldCBjb21tYW5kOiAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAd2Fy
bmluZyhzdHJlYW0pOiBpbnZhbGlkIHJldmVyc2UgaW5kZXggY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIHNpbmdsZSBzaGlmdCAyIGNvbW1h
bmQ6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBzaW5nbGUgc2hpZnQgMyBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IHVuaW1wbGVtZW50ZWQgRVNDIGNh
bGxiYWNrOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgZnVsbCByZXNldCBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgbG9ja2luZyBzaGlm
dCAxIHJpZ2h0IGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBsb2NraW5nIHNoaWZ0IDIgcmlnaHQgY29tbWFuZDogd2FybmluZyhzdHJlYW0p
OiBpbnZhbGlkIGxvY2tpbmcgc2hpZnQgMyByaWdodCBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IHVuaW1wbGVtZW50ZWQgc2V0TW9kZTogd2FybmluZyhz
dHJlYW0pOiBpbnZhbGlkIEMwIGNoYXJhY3RlciwgaWdub3Jpbmc6IDB4ADgGEAAGAAAAgA8QAAoAAABrDxAACgAAALkBEAALAAAA+wkQAAsAAAB2DxAAFAAA
AGEPEAAUAAAAQw8QAB0AAAAEAAEFBgIDBwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAQAAAAEAAAABAAAAAwAAAAUAAAAFAAAABQAAAAUAAAAF
AAAABQAAAAUAAAAFAAAABQAAAAcAAAAHAAAABwAAAAcAAAAHAAAABwAAAAcAAAAHAAAABwAAAAAAAAAAAAAAAwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AKqqqqoAAAAAAAAAAAECBAgQIECACwAAAAwAAAANAAAADgAAABtdbBtcAAABAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD//wAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAA//8AAAAAAAB3YXJuaW5nKHJlc3R0eSk6IGVycm9yIGhhbmRsaW5nIFZUIGFjdGlvbiBhY3Rpb249IGVycj0AAAEAAAACAAAAAABz
ZW50aW5lbCBtaXNtYXRjaDogZXhwZWN0ZWQgLCBmb3VuZCA0AAAAAAB3YXJuaW5nKHBhcnNlcik6IGludmFsaWQgaW50ZXJtZWRpYXRlcyBjb3VudHdhcm5p
bmcob3NjKTogQW4gYWxsb2NhdG9yIGlzIHJlcXVpcmVkIHRvIHByb2Nlc3MgT1NDICBidXQgbm9uZSB3YXMgcHJvdmlkZWQud2FybmluZyhwYXJzZXIpOiBD
U0kgY29sb24gb3IgbWl4ZWQgc2VwYXJhdG9ycyBvbmx5IGFsbG93ZWQgZm9yICdtJyBjb21tYW5kLCBnb3Q6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBj
dXJzb3IgZG93biBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIEIgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJu
aW5nKHN0cmVhbSk6IGludmFsaWQgY3Vyc29yIHJpZ2h0IGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgQyB3
aXRoIGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBjdXJzb3IgdXAgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1
bmltcGxlbWVudGVkIENTSSBFIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBGIHdpdGgg
aW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIGhvcml6b250YWwgdGFiIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaWdub3Jpbmcg
dW5pbXBsZW1lbnRlZCBDU0kgSSB3aXRoIGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBlcmFzZSBkaXNwbGF5IGNvbW1hbmQ6IHdh
cm5pbmcoc3RyZWFtKTogaW52YWxpZCBlcmFzZSBsaW5lIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBJTCBjb21tYW5kOiB3YXJuaW5nKHN0
cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIEwgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgREwgY29tbWFu
ZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBNIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZh
bGlkIGRlbGV0ZSBjaGFyYWN0ZXJzIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgUCB3aXRoIGludGVybWVk
aWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBzY3JvbGwgdXAgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVk
IENTSSBTIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIHNjcm9sbCBkb3duIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTog
aWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgVCB3aXRoIGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBjdXJzb3IgdGFidWxhdGlv
biBjb250cm9sOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIFcgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVh
bSk6IGludmFsaWQgZXJhc2UgY2hhcmFjdGVycyBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIFggd2l0aCBp
bnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgaG9yaXpvbnRhbCB0YWIgYmFjayBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9y
aW5nIHVuaW1wbGVtZW50ZWQgQ1NJIFogd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgSFBSIGNvbW1hbmQ6IHdhcm5pbmco
c3RyZWFtKTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgYSB3aXRoIGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBwcmludCBy
ZXBlYXQgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBiIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhz
dHJlYW0pOiBpbnZhbGlkIGRldmljZSBhdHRyaWJ1dGVzIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBWUEEgY29tbWFuZDogd2FybmluZyhz
dHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBkIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIFZQUiBjb21t
YW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIGUgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVhbSk6IGlu
dmFsaWQgdGFiIGNsZWFyIG1vZGU6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCB0YWIgY2xlYXIgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiB1bmtub3du
IHRhYiBjbGVhciBtb2RlOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIGcgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5n
KHN0cmVhbSk6IGludmFsaWQgc2V0IG1vZGUgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiB1bmltcGxlbWVudGVkIG1vZGU6IHdhcm5pbmcoc3RyZWFtKTog
aW52YWxpZCBzZXRNb2RpZnlLZXlGb3JtYXQ6IHdhcm5pbmcoc3RyZWFtKTogdW5rbm93biBDU0kgbSB3aXRoIGludGVybWVkaWF0ZTogd2FybmluZyhzdHJl
YW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBtIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIGRldmljZSBzdGF0
dXMgcmVwb3J0IGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogdW5rbm93biBDU0kgbiB3aXRoIGludGVybWVkaWF0ZTogd2FybmluZyhzdHJlYW0pOiBpZ25v
cmluZyB1bmltcGxlbWVudGVkIENTSSBuIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBw
IHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIERFQ1JRTSBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgY3Vy
c29yIHN0eWxlIHZhbHVlOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgc2V0IGN1cnNvciBzdHlsZSBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGludmFs
aWQgc2V0IHByb3RlY3RlZCBtb2RlIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgcSB3aXRoIGludGVybWVk
aWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBERUNTVEJNIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFtKTogdW5pbXBsZW1lbnRlZCByZXN0b3JlIG1v
ZGU6IHdhcm5pbmcoc3RyZWFtKTogdW5rbm93biBDU0kgcyB3aXRoIGludGVybWVkaWF0ZTogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVu
dGVkIENTSSBzIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIERFQ1NMUk0gY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiB1
bmltcGxlbWVudGVkIHNhdmUgbW9kZTogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIFhUU0hJRlRFU0NBUEUgY29tbWFuZDogdBtbNDsbWzY7G1s4O3dhcm5p
bmcoc3RyZWFtKTogaWdub3JpbmcgQ1NJIDE0IHQgd2l0aCBleHRyYSBwYXJhbWV0ZXJzOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIENTSSAxNiB0IHdp
dGggZXh0cmEgcGFyYW1ldGVyczogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyBDU0kgMTggdCB3aXRoIGV4dHJhIHBhcmFtZXRlcnM6IHdhcm5pbmcoc3Ry
ZWFtKTogaWdub3JpbmcgQ1NJIDIxIHQgd2l0aCBleHRyYSBwYXJhbWV0ZXJzOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIENTSSAyMi8yMyB0IHdpdGgg
ZXh0cmEgcGFyYW1ldGVyczogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyBDU0kgdCB3aXRoIHVuaW1wbGVtZW50ZWQgcGFyYW1ldGVyOiBlcnJvcihzdHJl
YW0pOiBpZ25vcmluZyBDU0kgdCB3aXRoIG5vIHBhcmFtZXRlcnM6IHdhcm5pbmcoc3RyZWFtKTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgdCB3aXRo
IGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogaW52YWxpZCBwdXNoS2l0dHlLZXlib2FyZCBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGludmFs
aWQgc2V0S2l0dHlLZXlib2FyZCBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIHU6IHdhcm5pbmcoc3RyZWFt
KTogaW52YWxpZCBJQ0ggY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENTSSBAOiB3YXJuaW5nKHN0cmVhbSk6IHVu
aW1wbGVtZW50ZWQgQ1NJIGNhbGxiYWNrOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVuaW1wbGVtZW50ZWQgQ1NJIEEgd2l0aCBpbnRlcm1lZGlhdGVz
OiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgY3Vyc29yIGxlZnQgY29tbWFuZDogd2FybmluZyhzdHJlYW0pOiBpZ25vcmluZyB1bmltcGxlbWVudGVkIENT
SSBEIHdpdGggaW50ZXJtZWRpYXRlczogd2FybmluZyhzdHJlYW0pOiBpbnZhbGlkIEhQQSBjb21tYW5kOiB3YXJuaW5nKHN0cmVhbSk6IGlnbm9yaW5nIHVu
aW1wbGVtZW50ZWQgQ1NJIEcgd2l0aCBpbnRlcm1lZGlhdGVzOiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgQ1VQIGNvbW1hbmQ6IHdhcm5pbmcoc3RyZWFt
KTogaWdub3JpbmcgdW5pbXBsZW1lbnRlZCBDU0kgSCB3aXRoIGludGVybWVkaWF0ZXM6IHdhcm5pbmcoc3RyZWFtKTogdW5pbXBsZW1lbnRlZCBDU0kgYWN0
aW9uOiABAQIBAwEAAXdhcm5pbmcoc3RyZWFtKTogY2hhbmdlIHRpdGxlIHJlcXVlc3Q6IGludmFsaWQgdXRmLTgsIGlnbm9yaW5nIHJlcXVlc3RpbmZvKHN0
cmVhbSk6IE9TQyAxIChjaGFuZ2UgaWNvbikgcmVjZWl2ZWQgYW5kIGlnbm9yZWQgaWNvbj13YXJuaW5nKHN0cmVhbSk6IHVua25vd24gY3Vyc29yIHNoYXBl
OiB3YXJuaW5nKHN0cmVhbSk6IGludmFsaWQgT1NDLCBzaG91bGQgbmV2ZXIgaGFwcGVuRVNDICB3YXJuaW5nKHN0cmVhbV90ZXJtaW5hbCk6IGVycm9yIGhh
bmRsaW5nIFZUIGFjdGlvbiBhY3Rpb249ZXJyb3Ioc2NyZWVuKTogZmFpbGVkIHRvIHVwZGF0ZSBzdHlsZSBvbiBjdXJzb3IgY2hhbmdlIGVycj1lcnJvcihz
Y3JlZW4pOiBmYWlsZWQgdG8gdXBkYXRlIGh5cGVybGluayBvbiBjdXJzb3IgY2hhbmdlIGVycj13YXJuaW5nOiBkZWxldGVMaW5lcyBtYW51YWxTdHlsZVVw
ZGF0ZSBlcnI9d2FybmluZyh0ZXJtaW5hbCk6IGN1cnNvciBjb3B5IGZhaWxlZCBlbnRlcmluZyBhbHQgc2NyZWVuIGVycj1lcnJvcihzY3JlZW4pOiBmYWls
ZWQgdG8gdXBkYXRlIHN0eWxlIG9uIGN1cnNvciByZWxvYWQgZXJyPXdhcm5pbmcoc2NyZWVuKTogY3Vyc29yIG9uIHByb21wdCBsaW5lIGJ1dCBwcm9tcHRJ
dGVyYXRvciBmb3VuZCBubyBwcm9tcHRlcnJvcihzY3JlZW4pOiBmYWlsZWQgdG8gdXBkYXRlIGh5cGVybGluayBvbiByZXNpemUgZXJyPXdhcm5pbmcodGVy
bWluYWwpOiB6ZXJvLXdpZHRoIGNoYXJhY3RlciB3aXRoIG5vIHByaW9yIGNoYXJhY3RlciwgaWdub3Jpbmd3YXJuaW5nKHRlcm1pbmFsKTogZXJyb3IgcmVh
bGxvY2F0aW5nIGZvciBtb3JlIGh5cGVybGluayBzcGFjZSwgaWdub3JpbmcgaHlwZXJsaW5rIGVycj0keRtQPnx3YXJuaW5nKHRlcm1pbmFsKTogcmVzdG9y
ZUN1cnNvciBlcnJvciB1cGRhdGluZyBzdHlsZSBlcnI9ZXJyb3Ioc2NyZWVuKTogc3R5bGUgYWRkaXRpb24gZmFpbGVkIGFmdGVyIGNhcGFjaXR5IGluY3Jl
YXNlZXJyb3Ioc2NyZWVuKTogc3R5bGUgcmVoYXNoIHJlc3VsdGVkIGluIG5lZWRzIHJlaGFzaGVycm9yKHRlcm1pbmFsKTogZGVsZXRlTGluZXMgdHJhY2tQ
aW4gZXJyb3IgZXJyPXdhcm5pbmcodGVybWluYWwpOiBzY3JvbGwgY2xlYXIgZmFpbGVkLCBkb2luZyBhIG5vcm1hbCBjbGVhciBlcnI9ZXJyb3IocGFnZV9s
aXN0KTogZmFpbGVkIHRvIHJlZ3JvdyBhY3RpdmUgYXJlYSBhZnRlciBlcmFzZSBlcnI9ZXJyb3Ioc2NyZWVuKTogZ3JhcGhlbWUgYXBwZW5kIGZhaWxlZCBh
ZnRlciBjYXBhY2l0eSBpbmNyZWFzZXdhcm5pbmcob3NjX2h5cGVybGluayk6IHVua25vd24gaHlwZXJsaW5rIG9wdGlvbjogJyd3YXJuaW5nKG9zY19raXR0
eV9jb2xvcik6IGV4Y2VlZGVkIGxpbWl0IGZvciBudW1iZXIgb2Yga2V5cyBpbiBraXR0eSBjb2xvciBwcm90b2NvbCwgaWdub3Jpbmd3YXJuaW5nKG9zY19r
aXR0eV9jb2xvcik6IHplcm8gbGVuZ3RoIGtleSBpbiBraXR0eSBjb2xvciBwcm90b2NvbHdhcm5pbmcob3NjX2tpdHR5X2NvbG9yKTogdW5rbm93biBrZXkg
aW4ga2l0dHkgY29sb3IgcHJvdG9jb2w6IHdhcm5pbmcob3NjX2tpdHR5X2NvbG9yKTogdW5hYmxlIHRvIGFwcGVuZCBraXR0eSBjb2xvciBwcm90b2NvbCBv
cHRpb246IAAAAAAAAAAAAAAAAPA/AAAAAAAAJEAAAAAAAABZQAAAAAAAQI9AAAAAAACIw0AAAAAAAGr4QAAAAACAhC5BAAAAANASY0EAAAAAhNeXQQAAAABl
zc1BAAAAIF+gAkIAAADodkg3QgAAAKKUGm1CAABA5ZwwokIAAJAexLzWQgAANCb1awxDAIDgN3nDQUMAoNiFVzR2QwDITmdtwatDAD2RYORY4UNAjLV4Ha8V
RFDv4tbkGktEktVNBs/wgEQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB
AAAAAAAAAAoAAAAAAAAAZAAAAAAAAADoAwAAAAAAABAnAAAAAAAAoIYBAAAAAABAQg8AAAAAAICWmAAAAAAAAOH1BQAAAAAAypo7AAAAAADkC1QCAAAAAOh2
SBcAAAAAEKXU6AAAAACgck4YCQAAAEB6EPNaAAAAgMakfo0DAFrWO5LWU/TuPzuhBimqPxH4ZWUbZrRYlQfFJKRZysdKdr8+on/hrrpJ9i0N8Lx5XVNvzorf
mVrp3HN5ECws2PSUBcG2K6DYkWnoS4qbGwd5+UZxpDbITraE4t5sguJIl7eYjU1EeuLjJZsWCCMbG/1yf3iwaoxtjvcgDuX18DD+T5+WXIXvCLI1qVFeMy29
vSNHvLNmK4veghPmNYB4LK12rFUwIPsWizHMryFQyztMkxdrPOi53K09vxsqJL5K33jdhUti6FPZDa+iNK1tHddrqjNvPXHUh2it5UCMZHKGBpUAy4yNyanC
GB9Rr/0OaEi6wP3v8DvU8t5mJRu9EgJtdJj+lXalhFdLYPcwtksBiJE+fjvUzqUtXjg1vaOeQeo1zl1KiULPuXWGgqxMBlKy4aB6zpWJgZMJlNHr70NzHxpJ
GUL766H4C/nF5usUEKZgm58S+mbK9k53d+AmGtTQOIJHl7gA/bQiVZWYsCCJgmOxjF5zIJ6wNVVdX260VWK83S82kKjFHYOqNPeJIet7K9W7Q7QS9+Qj1QF1
7OmlLTtlVaqwa5puNiUhyTOyR/iJvurUnAbBCoRuabvAnpl2LG4lCkRI8Q0lykPqcAbAyttkV4YqzZYoV15qkgYEOLwSPu0ndYC88uz1BDcIBcZrl43ocZKg
6y5oM8ZESob3o35YMYdbRJMdIeD7au6zekyerv1ochW4ZCnYugXqYFnfRRo9A88a5r0zjimHJLlvq2swBmLB0I9W4Ph51LbTpZaGvIe68cSzbBh3mImkSI88
qKspKS624IfelP6rzRozJUkLutnccYwUCx1/i8Dwn28bjigQVI6v2U3kXq7w7AdKorEyFOlx21BhnfbZLOjJbgWvn6wxJ4nSXCI6CBwxvsrGmscX/nCrBvSq
SApjvW19eIG5nT1N1gix1drMuywJTuvwk4JG8IWljsUIYPW7JSEm7TgjWGynTvL2CrjyKq+qbygHLG5H0eGutA1mr/UaykV5hNukzIJN7ZDIn43ZUDyXl2US
zn+joCi1ugfxD+UMff3+lsFfzMhyYqlJ7VMeT9y8vvyxd/96D7sTnOjoJbEJNvc9z6qfrOlUjGGRsXcdjAN1DYOVxxckau+59Z3VJW9E0tDjevkdrURrKHMF
S3fFaoNizuybMuwKQ/ln407VdkUk+wHowj+nzZP3QZwiitRW7XkCovMPEcF4dVJDa9ZEVjSMQUWYqap4a4kTCoMM1mtB75FWvlPVVsZrmMwjj8vGEWs27O2o
iuy3hr6/LDk/HOsCorOUqdbzMhTX93sHT+Olg4rguVPMsD/ZzPXaySJcjyStWOho/5yPD0Cz0b6Vmdk2bDeRoR/CuQkIECMt+/+PREeFtYqnMigMCtSr+fn/
sxWZ5uJsUT8yjwzJFjv8f5CtH9CN45Jnf9mnPa5K+5/0mCdEsZx3Qd/PEc2ZHfrHMX8xld2D1RHXQ1ZAQFL8HH/vPn2KciVrZuo1KEhmO+Req44crc/uBQBl
QzLaQEqdNlayY9iCagdAPtS+kGhOIuJ1Tz6HkaIE6KZEd1oC4qpaU+MNqTbLBaLQFRVxg5pVMShcUdMDPofKRFtaDZGA1R6Z2RKEwoaU/gp5WOi24Ipm/48X
pXKoOb5Nl25i45gtQP9zXc6PEsgtIT0K+45/HIh/aPqAmQudvDRm5nxynyNqnwI5oYBOxOvB/x8cToesREdDh8kgYrVmsv8noyKp1xUZFOn7qLpiAJ//8Uu1
yaatj6xxnam0PWDDP3dvInwQmbMXzsTTIU04tA9VyyubVH+gnQH2SGpgRqFTKn774JRPhALBmW1C/MtEdNouORl6YyVDMcAIU/v+VRGR+oifWLzukz3wyie6
fqtVNXm1Y7c1dXwmlt5YNC+LVcFLojwlg5IbsLsWbwH77aqxnsuL7iN3Ipzq3MrBeakVXkZfF3V2ipWhkskeGeyJzfoLNl0SFO36Sbd7Zh9n7ID5zoT0Flmo
eRzlGkDngCfht4LSWK43CcwxjxCIkLC47LLRB++ZhQs//rIVqrTc5qcfhslqAGfOzr3fmtThk+CRp2e9QmAAQaHWi+AkbVwsu8jgbVN4QJFJzK4Ybohz9+n6
WEholpD1W3/anolqUHWkOa8tAV56eZmPiAOWQlLJBoRteIH12Nd/s6qDO9OmewjlyNbhMs/NX2DVZAqIkJpKHvsmzX+h4DtchX8GVZqg7vJcb8DfydhKs6Ye
SOrASKov9IuwV/yOHWDQJtok8dqUO/FXzrZdeRI8glgIt9YIPcV27YEktRcXy6JuymQMS4x2VGiibaLd3H3LCf19z10vlKkCCwkLFVRd/kx8XUM1O/nT4abl
Jo1U+p6vbRpKAcV7xJoQn3Cw6bjGGwmhnEG2mjXA1MaMHCRn+GJLyQPSYwHD+ET815F2QJsdz11CY97geTZW+002lBDC5EL1EvwVWZjEK3rhQ7mU8p2Tshd7
W28+WlvsbMrznJdCnM/uLJkFpzFyJwi9MIS9U4ODKnj/xlC9TjFK7Dzl7ChkJDVWv/ikNtFerhNGD5SZvjbhlXcbh4SF9pmYFxO5P26EWXtV4ijlJnTAft1X
58+J5S/a6hozT5hIOG/qlpAhdu9dyNLwP2O+WgYLpby0qVNrdXoH7Q/7bfHHTc7r4ZQoxhJZSejTveT2nPBgM41c2bur1y1xZOydNMQsOYCws8+qlk15jb1n
xUH1d0eg3KCDVfyg1/DsYBtJ+aos5IlEcrWdxIYW9Dlim7fVN12s1c4ixXUoHDHHOoIly4V014uCazaTMmN9vGRx957TqIaXMQMCnP9druu9TbWGCFOo/P2D
AoN/9dlmLaFiqMpn0nv9JMNj33LQYLykPanegINtHvdZnstHQnjrDY1TFmGkCOZ08IW+2VJWZlFw6Ft5zYsfkmwnLpBn9t8yRnHZa4C2U9uj2By6APOXv5fN
z4agpCjSzA6k6IDwfa/9wIOoyM2yBoASzSJhbF0bPbGk0vqBXwggV4BreWMaMcbupsOcsDsFdDYw48v8YL13qpD0w5yKBhFE/Nu+O7msFdW08fRELUgVVfuS
7sXziy0FEReZShxNLRXdG3W28O54RtVcv11joHha1GLS5KwqF5gKNO80fMgWcYn7hg6seg6fhoCVoE09ruY1XdQSVxnSRqjgugmhzFlgg3SJ16yfhljSmOlL
yT9wOKTRKwbMI1R3g/+Rz90nRqMGY3sIvywpVWR/tkLVsRdMyDsayu53c2o9H+STSp4dX7rKID71KohihpOOnO6Ccnu0flSNsjUq+2c4skOqI0+aYZ7pMR/D
9PmBxt7UlOziAPoFZH7z+Tg8ETyLBN3TjUC8g95ecDhHixULrkXUSLFQqySWdowGGe7ajdlXCZvdJNatO8kXpM/UqPiH1uWACtelTOW8HY0DCtP2qUwfIc1M
z59eK2VwhMyHdNQfZ2kAIMNHdjs/xtLf1MiEc+BBAPTZ7CkJz3fHFwr7pZBYUgBxEGj0zMJVuZ3Mec+07mZAjRSCcb+Z1ZPiH6yBMFVASNhM8cYvAMs42ycX
onxqUFoOoK24O8D9BtLxnMocheTwEQjZpkowvYhGLkT9Y6YdbRZKj5AuPnYV7JxKnv6HMgROjlmaus3TGidE3cX9KT+F4fHvQCjBiOEwlVT3fPSO5lnuK9G5
ePWMPt2Ums5YGTD4dLuC59YyMI4UOsEBrx88NlJq46GMP7yxmYjxwZony8PmRNzlt6cVD2D1lrnA+F46EKsp3qUR2xK4srzn8Lb2SNQVdFYP1pEXZt/rIa1k
NFtJGxGVySW7zp9rkzTsvgDZDbHK+zvvacKHRrhCp+5AT1FdPfoKawSzKVjmElEqEaOltAzc5sLiDxr3j6tyuuqF5/BHk6Bz25Pg9LNWD2llZyHtWbiIUNK4
GPLgLFPDPsFpaDBzVXKDc0+XjPsTOscYQkEez+pOZFAjva/6mAj5npLR5YOlYn0kbKzbOb9Kt0b3Rd9yp13OlsNLiYO3jjKMuotrTxH1gXy0nqtkZTI/L6lu
BqJVcqKbYYbWvf7+DntTCsiFdYdFAf0ThjZfX+ksdAa951LplkH8mKcEN7cjOBFILKCno/xRO3/RxQSlLIYVWvfESOY9E4Xvgvsi59tzTZia9dpfDVhmq6O6
6+DS0GA+wbPRtxDuP5bMqCaZBwX5jTEfxuWU6c+7/1Jwf0lGd/H905sP/fFh1Z8zpu/ti+q2/siCU3xuusrHwI9r6S6lZP57Y2gbCmm9+bBzxqN6zv09LT4h
UaZhFpxOCFymDKG+BriNaeUP+hvDYgrzz09Jbkgm8cPek/ji8/rM78Oj24lat3Y6a1zbbZgc4HVaRimW+GUUCYYzUom+I1gT8Zezu/Z/WYtnwKYr7iwuWO19
oGp07xe3QDhI25TcHFe0TqTCqOvd5FBGGhK6E+RsYWJN85JmFR7l16CW6BcdyPm6ILB3YM0y74YkXpEuEh3cdBTOCriA/6qorbW1ulYkE5KZgQ3mYL/VEhkj
42ls7Zf2/+EQj5yXxavv9Y3BY/Qe+j+NyrOD/baWa3OxsnyxpviPML2g5LxkfEbQ3d7bXdD2s3ys5A72vg0sooprqTpCevDNa52Ssy4Rt0qtxlPJ0phswYZE
d2B61WSd2Leoewe/x3Hoi0p8bAVfYodySa1k1xxHES1dm8fG9jqpz5vYPQ3kmNV5NIJ5eLSJ08PCTo0QHf9Ky2DxS8sQNoS6OVFYKnLfzv647R7+lEOlKIhl
7rROl8I+J6mmPXqUzjLq/iliIj1zh7gpiGbMHIFfUj9afTUGCKgmNCqA/2Oh9ybPsNzCB8pSMME0YP+8ybXwAt2Ts4n8Z3zxQTg/LPzirEPUeCCsu8DtNimD
p5udDUyqhEuUS9UxqYTzY5ECxRHf1GVeeZ4KfdNl8Lw1Q/bVFkr/tRdGTS6kPxaWAeqZRU6Ov9HOS1A5jc+b+4FkwNbhcS+Gwl7kiHDDgnqiffBMWk67J3N2
XVUmupGMhU6Wb/gQ1fgHajrqryi27ybiu4s2VQr3iQSJ5duyo6uw2uouhOrMdKxFK2/JT0ZrrsiSnZISAMmLCzvLu+MXBtp6t0Q3F0C7bs4JvarcnYeQWeUV
BR0QagpCzLbqqcJU+lePLSMSSoJGqZ9kZVTz6fgts/mrltwimJNHvX4pcCR3+d/3VryTK354WTbvGcZ26vuLWrZVPNtO61cDa6B3FOX6rvEjawuSIubtxIWI
lVmeudrt7EWONqtf6ZtTdf33ArSIFLTrGALL2xGBqNL8tQPhqhmhJp/CvVLWolIHfKNEmdVfSfBGM23nS6WThC3myn+F2y1WDECkcG+OuOW4n73fplK5aw9Q
zUzLsiYfpwetl9Cnp0YTpAAgfi94c8gkzF6CyCgMjGYA1I47VpD6LX/2ovoyDy+AAIlyyms0efketMu5/9I6oEArT7yGgde3JqH+qL+HScgQ9uI29LDmMrgk
n8nX9C19ytkNQzFdoD/m7ca7DXJ5HD1QkZR9dIjPX6n4KpHOl2NMpHV8zki14dtpm7oa4T6+r4bJGwKbItpSRMJoYZnOrVvo+6LCQauQZ9Xyw7k/Qply4vql
GQlrumDFlxrUZ8mfh83cD2DLBem4tr0gycG7h+kAVBM4PkcjZyTtaDuyqukjASkL44YMdsA2lCFlrwpytqD5zpuoj5NwRLlpPluNDuQI+MLCknO4jJXnBA6y
MBIdC7a5uTtI83e9kMJIb14r8saxKKhKGvDV7LTzGgs2tq44HjJS3SBsCyjisOGNw2PaxiVfU4qUIwdZjQ6tOFp+SJxXN+iseexIr7BR2MbwnVqDLUQiGJgn
G9vcZY74bEUx5PhrFQ+/+PAIiv9YG2TLno4bxdrS7jYti6w/LyI9fkZy4neRh6qE+K3XD7tqzB3YDlvqupTqUrvMhum0wp8SR+mYpek5pSfqf6gkYrNH15gj
Pw5kiI6x5J/SrTqgGQ1/7I6JPhX57u6jg6wkBDBoz1MZK45at6rqjKTXLQU8QsOoX7YxMWVVJbDNTXkGyxL0kjcRvz5fVReOgNAL5L6L2Lvi1m4OtyqdsaDE
Dp2urs5qW4sK0mR1BN7IdVJEWlqCRfIujQa+koUV+xJn1fDw4tbuPRjEtntz7ZxrYIWW1k1GVUwedaRa0CjEhrgmPEzhl6rfZZJNcQQz9ahmMEuf2T3Vq397
0MbiP5kpQP6OA6hG5ZZfmoR424+/M9C9cgRSmN5898ClVtJz70BEbY+FZj6WrZqYJ3ZjqJWoSqR5EwDn3VnBfrFTfBK7Ul0NWBjAYFWvcd6daBvX6aa0EG4e
8LiqDQerYiFxJpLocMoEE5azytHIVbtpDbC2Ig39xZd7YD0FOysqxBBc5GpQfLd9mriM4wRbmnqKuY5Csq2SjmDzdxzG8UAZ7Wey0x5ZN7I48FWjNy6RX+gB
34hmL8XeRmxrxuK8ujsxYYsVoD07S6wjI3cbbKmKfTmuGggNCl6X7KtVIsdT7dzH2SFKkIw1veeWdXVcVBTqHIhULtp3QdZQftKSc2mZJCSq6bnQ1dEL5d2H
d9DDvy2t1GToREvGTl6VtEpi2pc87IQ+EQvvO/FavWHd+tC9SyemjtXN6oqtsey6lDlFrR6xz/JKgaXtGN5n9PxDSyyzzoHXznCHlM/qgDH8FF73X0KijQJN
qXmDJaE+O5o19ffSyjBDoBNY5G4JDcoAg/K1h/38U4gYbp3Ki0h+4JG30XSefjRVz2SiXnfanVh2JQYSxp6BKgP+SjaVUcXu066HlvcFIvWDvd2DOlI7dUTN
FL6aQzV5cpZqksQnipKVAJptwZSCFw88Bbd1sSz3uoAAyfE5Y90Si8YkU+572nRQoB2XBF7K6xb89tPqGhGSZAjlvIX1vKYcu/SIpWGVtn1KHuzmMmzQ4+kx
KwddHZKO7pKT0J9DYi4y/zpJtKQ2Mqp3uMOH1Pq5/r4JW+FNxL6Ulea0qYl5aL4uTNmssDr3fB2QEQr2SwE3nQ8P2FwJNdwktJWM857BhIRTEw60S0ITLuG6
b7AG8qVlKMuIUG8JzLyM1EUuRLeHP/n+qiTLC//rr0nXORWlaY/3vtXtvc7+5tscTYhaDkRztZeltDZBX3CJMTCV+IgKaDH8zmGEEXfMqz58ujYrDcL9vEJ6
5dWUv9ZNG2kEdpAyPbVpbK8FvTeGELHBwkmaP6YjhEcbR6zFp1QdcjPcgM8PK2UZ4lgXt9GppE5AE2HD0zvfT42XbhKD6iYxCKwcWmQK16NwPQrXo6RwPQrX
o3A9zMzMzMzMzMzNzMzMzMzMzAAAAAAAAACAAAAAAAAAAAAAAAAAAAAAoAAAAAAAAAAAAAAAAAAAAMgAAAAAAAAAAAAAAAAAAAD6AAAAAAAAAAAAAAAAAABA
nAAAAAAAAAAAAAAAAAAAUMMAAAAAAAAAAAAAAAAAACT0AAAAAAAAAAAAAAAAAICWmAAAAAAAAAAAAAAAAAAgvL4AAAAAAAAAAAAAAAAAKGvuAAAAAAAAAAAA
AAAAAPkClQAAAAAAAAAAAAAAAEC3Q7oAAAAAAAAAAAAAAAAQpdToAAAAAAAAAAAAAAAAKueEkQAAAAAAAAAAAAAAgPQg5rUAAAAAAAAAAAAAAKAxqV/jAAAA
AAAAAAAAAAAEv8kbjgAAAAAAAAAAAAAAxS68orEAAAAAAAAAAAAAQHY6awveAAAAAAAAAAAAAOiJBCPHigAAAAAAAAAAAABirMXreK0AAAAAAAAAAACAehe3
JtfYAAAAAAAAAAAAkKxuMniGhwAAAAAAAAAAALRXCj8WaKkAAAAAAAAAAACh7czOG8LTAAAAAAAAAACghBRAYVFZhAAAAAAAAAAAyKUZkLmlb6UAAAAAAAAA
ADoPIPQnj8vOAAAAAAAAAACECZT4eDk/gQAAAAAAAABA5Qu5NtcHj6EAAAAAAAAAUN5OZwTNyfLJAAAAAAAAAKSWIoFFQHxv/AAAAAAAAABNnbVwK6itxZ0A
AAAAAAAg8AXjTDYSGTfFAAAAAAAAKGzGG+DDVt+E9gAAAAAAADLHXBFsOpYLE5oAAAAAAEB/PLMVB8l7zpfAAAAAAAAQn0sg20i7GsK98AAAAAAA1IYe9IgN
tVCZdpYAAAAAgEQUEzHrUOKkPxS8AAAAAKBV2Rf9JeUajk8Z6wAAAAAIq89dvjfP0LjR75IAAAAA5cqhWq0FAwUnxqu3AAAAQJ49SvEZx0PGsLeW5QAAANAF
zZxtb1zqe84yfo8AAACiIwCC5Ivz5BqCv12zAACAiiyAot1uMJ6hYi814AAAIK03IAvVRd4CpZ09IYwAADTMIvQmRdaVQw4FjSmvAABBfyuxcJZMe9RRRvDz
2gBAEV923Qw8D80k8yt22IgAyGr7aQqIpVMA7u+2kw6rAHpFegQN6o5ogOmrpDjS1YDY1phFkKRyQfBx62Zjo4VQR4Z/K9qmR1FsTqZAPAynJNlnX7aQkJll
B+LPUEvP0G3PQffjtPT/n0TtgRKPgYKkIYl6DvH4v8eVaCLX8iGjDWorGVIt9685uwLrjG/qy5BEdp+m+PSbCGrDJXAL5f601VNH0DbyAkUimhcmJ0+fkGWU
LEJi1wHWqoCd7/Aix/V+ubfSOk1Ci9XghCut6/iy3qdlh4ng0neFDDM7TJObL+uIn/RVzGPVps//SR94wvsla8dxa788ipDDfxwnFvN670U5Tkbvi1Y62s9x
2O2XrLXL4/CLdZfsyNBDjk7pvRejvhzt7lI9J/vE1DGiY+3dS+5jqKqnTPgc+yRfRV6Uau90PqnK6I825DnuttZ1uUQrEo5T/eKzRF3IqWRM0+cWtpZxqLzb
YEo6Heq+D+SQzTH+RulVibzdiKSkrhMdtUG+vZhjq6trFKvNTZpYZOLRLe1+PJaWxuyKoHBgt36NojxUz+UdHvyorciMOGXesMtLKUNfpSU7Etn6r4b+Fd2+
nvMTtw7vSavH/C0Uvy2KN0N4bDJpNW6W+Xs52S65rARUlgd/w8JJ+/fah49659cG6XvJXnQz3P3a6LSZrPCGo3HtPbsooGm8ESMiwNesqAzOaA3qMgjEK9ar
KrAN2NKQAcOQpD8K9dtlqxqOCMeD+uB52sZnJnlSP1ahscq4pDhZGJG4AXBXJs+rCV795s2Gb161JgJM7XhhC8ZaXrCAtAVbMViBT1TWOY538XXcoCHHsT2u
YWNpTMhx1W2TE8npOB7NGTq8A186zkpJeFj7I8dlQKBIqwR75MDOLUsXnXacPyhkDetimh1xQvkdXcSUg08yvdClOwBlDZN3ZXT1eWTjfuxEj8ogX+i7ar9o
mcseTs8Ti5l+6HbiakXvwr9+piHD2O0/nqIUm8UWq7PvHhDq807pz8Xl7IA77krQlRJKcljR8aG7HyhhyqldRLuX3I6uRW6KKiZy+TwUdRXqvZMyGtcJLfVY
5xumLGlNklacX3AmJjxZLuGiz3fD4LZsg3cMsC+Lb3qZi8NV9JjkR2SVD5z7bQvsPzeatZjfjqxevYlBvSRH5w/FAON+l7JXtizskeztWOFT9sCbXj3f7eM3
Z7ZnKS9s9JlYIVuGi3TuggDS4Hm9h3HArunxZ64RqqOABlnY7OmNcBpk7gHalZTMIEhvDuiyWIaQ/jRBiN3cfxSNBQkx3u6nND6CUaoV1J9Z8EZLvZbq0cHN
4uXUGskHcKwYnmyeMiOZwK0PhbDdBMZrz+IDRf9rvzCZU6YcFYa3RoPbhBb/Ru98f+jPY5pnZRhkEuZuX4wVrk/xgX7AYD+PfstPSXfvmpmjbaKd8DgPM16+
4xxVqwGADAnLxSwH07/1rVxjKhYCoE/L/fb3yMcvc9lzftpNAcQRn576mt3c/ednKB1RoQE11kbGuAEVVP3hgbJlpQlCwovY9yZCGql8WiIfXwdGaVlX55pY
abDpjXh1MzeJl8MvLaHBroMcZLHWUgCEa320e3gJ8pqkI71djGfAMmPOUE3rRZfgRjaWurdA+P/7AaUgZhe9mNjDO6nlULb/ekLOqD9d7L7OtIoTH+Wj34zp
gMlHupM3AbE2bDNvxhfwI+G72ai4hEFdREcAC7gd7GzZKhDT5uWRdBVZwA2mkhPkxxrqQ5Av22itN5jIh3cY3Xmh5FS0+xHDmEW+uimUXlTYyR1q4XrW8/7W
bSn0Hbs0J55S4owMZlhfpuSZGOTpAbFF5xqwj38u989dwF5dZEIdF6Eh3HMf+vRDdXB2un5Jcq4ElYmoUxx5SkkGamne2w7aRfqrkmhjF53bhwQD1pKSUNf4
1rZCPF2E0qlFwsWbW5KGW4ayqUW6kiOKCzK3gvI2aPKnHhTXaHesbI7/ZCOvRALv0SbZDEOV1wcyHx927WphNYO4B+hJveZEf+em06jFuQKkpglinGwgFl+h
kAgTN2gDzQ+MesOHqNs2ZFrlayIhIoCJlyzaVElJwv2w3gZrqSqgbL23EKqb2/I9XZbIxVM1yMes5ZSUgpJvjPS7OreoQvr5Fx+6OSN3y9d4tYRyqWmc+25T
FAR2Kv8N1+IlzxOEw7pKaBmFE/X+0Yxb78IYZfRpXcJfZliyfgI4mdV5L7+YYXrZ+z93L+8Dhv9KWPvuvvrYz/oPVfuqhGe/XS66qu44z4P5Uyq6lbKgl/pc
tCqVg2Hye3RalN3fiD05dGF1uuT57poRcfmUF+uMR9G5EulduKoBVs03eu4SuMwitKuROrMKwVXgYqyqF+Z/K6EWtglgTTFrmHtXlJ3fX3ZJnOMLuKD9hX5a
7X3C6/vprUGOB3OEvhOPWBQcs+Z6ZBnSsciPJa7Ysm5Z41+gmb2fRt67867Zjl/Kb+47BIDWI+yKVFgNSLl73iXpSgUgzCynrWquEJqnGlavpJ0GKP/3ENkE
2pSAUaErG4YiBHn/mqqHQghd8NJE+5AoK0VXv0GVqVNKdKwHFjo18nUWLS+S+tPoXJGXiZuIQrcJLnxdm3yEEdq6/jVhlWkljDnbNMKbpZWQaX6DufpDLu8H
EsKyAs+79ANe5Gf5lH31REu5r2GB9XjCuu7gGx3cMhaepxu6oTIXc2kq2WJkk7+bhZGiKMr+3M8DdY97fXivAuc1y7L8PtTDRFJz2lyrrWGwAb/vnadk+moT
iAg6Fhl6HMKua8XQ/bhFGKqKCFufmKNymsb2RT0nV55UrYqZYz+mhyA8mkuGePbiVKw2fzzPj6koy8Ddpxa0G2pXhJ8Lw/PT8v3w1VEcoaJEbWVD51l4xLee
liWzsaTlSmSfFGFwlrVlRrzuH94Nn109h1l5DPwi/1fr6qdV0Qa1DKnYy4fddf8Wk/KI1UIk8acJzr7pVFO/3Lcv64pTbe0RDIEuJCoo79Pl+qVtqMhoFo8Q
nVYaeXWkj7yHRGl9AW75VUTsYNeSjbOsqZXD3IHJN2pVJzmN93DgFxR79FPiu4VilbhDuJpGjI7szHh0bZWTu7qmVGZBWK+yJwCX0ch6OGpp0Om/US7bnjHA
/AV7mQbiQSLyF/P8iAMf+L3j7B9EWtKq7t0vPKvDJnatHOgn1fGGVWrVOwvWdLDT2CPicYpWdHViZQXHhUlOhGdWLYf2bNESu77GOKfbYWUBrPgotMeF12lu
+AbRUrq+Adc2M+GcsyYCRVukgnM0F2FGAsDshGCwQhZyTaOQAV351wLwJ6V4XNObziDM9EG0940D7DHOljPIQgIp/3FSoXVxBGd+QT4gvWmheZ+G04TpxmIA
D9FNaCzECVjHaAjmo3h7wFJFYYI3NQwu+YKK38xWmnCny3yxQqHHvJuRtgtAdmCmiP7bXZOJ+avCNaQO0JP4z2r+UjX46/dW80NNEsS49oMF3lMhe/NaFphK
cIt6M3pyw9ao6Vmw8Ru+XEwuWcAYT3QME2RwHO6i7XPfeW/w3mIR54s+xtHUhZSoK6xFVsvdiuEuzjcGSqe5kjYX1ys+lW2ZusHFhxwR6DcE3cy2jfrIoBSZ
29SxCpGiIgpAkpicHchZfxJKXk21S6sM0La+AyU6MB+X3LWg4h3WD4RkrkQuJH5z3qlxpI3S5YnS/uzqXK1dEFYUjg2xR18shz6oJXQYdZRrmfFQ3Rl39yhO
Ei/RL8k84/+WUopvqprZcGu9gnv7C9y/POesC1UBEE3GbGNa+g7T7wsh2E6qAVTg90c8eFzp43WnFIdxCoE07PqsZZaz41xT0dmoDU2hQac5GH98oBw0qEUQ
01CgCRIRSN4eTeSRIIkr6oMyBEarCu1Kk2BdtmhrtuSkP4UXVk2oHfi59ONCBuQdzo5mnatgEiU283jO6YOu0oAZYEJrfCvXwTAXQuQkWgehH/gShlv2TLL8
nFIdrjBJySe2l2fyM+DePESnpNl8m/uxo30B70CYFqWK6AYILkGdTobuYJUoH45OraIIinmRxOInKrm68qbxoljLiuzXtfXbsXRnaa8QrmUXv9bzppGZKe+o
4KFtyqw/3W7MsBD2v/Mq01gKCf0XjpSK/9yU8++w9QfvTEv83dmcth8KPfiVjvlkFRCvvUoPRKSnTEx2u/E3vhrUGm2dE1WN0V/fU+rtxW0hiWHIhCxV+OKb
a3SStJvktPU8/TJ3arbbgoYRt6HCHSIzjLw/FQWkkiPo1eRKM6XqP6+rDy2DpjsWsQWPDkCn8odNyyn4I5DKWx3HshIQUe/pID509iw0vbLkeN8WVCVrJKlN
kRqcQLbvjquLjlT3wraJ0Bogw9Cjq3KWrrEptXMkrISh6PPEjFYPPNoedKKQLdflyXEY+xeWiWWIkohlenymL36N3vmd++t+qrfq/pgbkLvdMVZ4hfqmHtVl
pT5/InQqVd41a5NcKDOFXyeHj5WIOtVWA0a4c/J/pjfxaPO6KomKLIRXphDvH9CFLUOwaXUrLZuy9mdq9ROCc/wpDmIpO5xCX/QBxfKYoo97tJG680mDE3dx
QnYvP8tzmiE2qXAcJNfUDdNT+w7+EAGqg9OMI+0GpehjFF3JnqpASjIEODb0SM7ifFm0e8bV0Nw+BcZDsdqBG9xvoRr4CgWUjoa3lN0oMZHp5aQQmyaDHBm0
8nzKcn31Yx/O1MHwo2MfYS8c/c/c8jynAUry7Iw8Zzk7Y7wByheGCEFulxPYheADBb7Vgrydp0rRSb0YTqfYRIYtS6IrhVGdRZzsniHRDtbn+N1FO/NSgqvh
kwO1QsnlkLvKFwqw52IW2rhDYpM7H3VqPZ0MnKH7mxDn1Dp4CmcSxQzihwFFfWFqkMUki2aAK/sn2ulBltz5hLT27S2AYPb5sVFk0rtTOKbhc2k5oPhzeF6y
fmNVNOMHjejhI2R7SAvbX168agHcSbBi2iw9mhrOkfd1a8UBU1zc+xB4zEChQXa6KWMb4bO5iZ0Ky3/IBOmpKfQ7YtkgKKxEzb2f+kVjVDPxyroPKTLXlUCt
R3kXfKnA1r7UqVl/hl1IzMyrju1JcIzuSRQwH6h0Wv+/VvJoXIwvalwZ/CbSETH/b+wug3O3XcLZj11Yg6t+/8VT/THIJfUy0PN0LqRVXn+3qHw+um+yP8Qw
EjrN6zVf5dIbziiFz6d6XktEgLOBW89j0YB5ZsNRGTZeVaAfYjLDvAXh10A0pp/DtWrIp/r+8ytH2Y1QwY+HNGOF+lG5/vD2mE+x0ti51ABek5zTM59Wmr/R
bgdP6AmBNbjDyABH7IAvhgrIYmJM4UKm9PrAWCdhuyfNvX29z8zp55iceJe4HNU4gCzdrANA5CG/w1a95mMKR+B4FJgEUF3q7nSsbOD8zFgYywzfAlJ6UpXI
60MMHoA3D/3PloPmGKe6uuZUjyVgBdP9g3wkIN9Q6WkgKvMuuMZHftLNFnSL0pFBVPpXHTPcTB1HgRxRLke2Uun4reQ/E+DlmKFj5fnY46Yjd9ndDxhYj/9E
Xi+cZ45Iduqn6gkPVwADBgkNEBMXGh0hJCcrLjE1ODt3YXJuaW5nKG9zY19raXR0eV9jb2xvcik6IGludmFsaWQgY29sb3IgZm9ybWF0IGluIGtpdHR5IGNv
bG9yIHByb3RvY29sOiB3YXJuaW5nKG9zY19jb250ZXh0X3NpZ25hbCk6IE9TQyAzMDA4OiBleHBlY3RlZCAnc3RhcnQ9JyBvciAnZW5kPScgcHJlZml4LCBn
b3Q6IHdhcm5pbmcob3NjX2NvbnRleHRfc2lnbmFsKTogT1NDIDMwMDg6IG1pc3NpbmcgY29udGV4dCBJRHdhcm5pbmcob3NjX2NvbnRleHRfc2lnbmFsKTog
T1NDIDMwMDg6IGNvbnRleHQgSUQgbGVuZ3RoICBvdXQgb2YgcmFuZ2UgKDEtKXdhcm5pbmcob3NjX2NvbnRleHRfc2lnbmFsKTogT1NDIDMwMDg6IGludmFs
aWQgY2hhcmFjdGVyIDB4AAAAAAAAAAAAAAIAAAABAAAAAjAAACBpbiBjb250ZXh0IElEd2FybmluZyhraXR0eV90ZXh0X3NpemluZyk6IG1pc3Npbmcgc2Vt
aWNvbG9uIGJlZm9yZSBwYXlsb2Fkd2FybmluZyhraXR0eV90ZXh0X3NpemluZyk6IHBheWxvYWQgaXMgdG9vIGxvbmd3YXJuaW5nKGtpdHR5X3RleHRfc2l6
aW5nKTogcGF5bG9hZCBpcyBub3QgZXNjYXBlIGNvZGUgc2FmZSBVVEYtOHdhcm5pbmcoa2l0dHlfdGV4dF9zaXppbmcpOiBrZXkgbXVzdCBiZSBhIHNpbmds
ZSBjaGFyYWN0ZXJ3YXJuaW5nKGtpdHR5X3RleHRfc2l6aW5nKTogbWlzc2luZyB2YWx1ZXdhcm5pbmcoa2l0dHlfdGV4dF9zaXppbmcpOiB1bmtub3duIGtl
eTogJ3dhcm5pbmcoa2l0dHlfdGV4dF9zaXppbmcpOiBpbnZhbGlkIHZhbHVlIGZvciBrZXkgJyc6IHdhcm5pbmcob3NjX3J4dnRfZXh0ZW5zaW9uKTogdW5r
bm93biByeHZ0IGV4dGVuc2lvbjogd2FybmluZyhvc2Nfcnh2dF9leHRlbnNpb24pOiByeHZ0IG5vdGlmeSBleHRlbnNpb24gaXMgbWlzc2luZyB0aGUgdGl0
bGVpbmZvKG9zY19jb2xvcik6IGZhaWxlZCB0byBwYXJzZSBPU0MgIGNvbG9yIHJlcXVlc3QgZXJyPSBkYXRhPScgKFUrMHhFU0MgWyB3YXJuaW5nKHNjcmVl
bik6IChTY3JlZW4uaW5jcmVhc2VDYXBhY2l0eSkgRmFpbGVkIHRvIGFkZCBjdXJzb3Igc3R5bGUgYmFjayB0byBwYWdlLCBlcnI9d2FybmluZyhzY3JlZW4p
OiAoU2NyZWVuLmluY3JlYXNlQ2FwYWNpdHkpIEZhaWxlZCB0byBhZGQgY3Vyc29yIGh5cGVybGluayBiYWNrIHRvIHBhZ2UsIGVycj1lcnJvcih0ZXJtaW5h
bCk6IHVuaW1wbGVtZW50ZWQgZXJhc2UgbGluZSBtb2RlOiAibxAAAAAAAAAAAAD+KBAAAQAAAAEAAADmKBAAAgAAAAEAAADlKBAAAwAAAAEAAAB3HRAAAwAA
AAIAAADkKBAABAAAAAIAAADoFxAABQAAAAIAAAB0IBAABQAAAAMAAACVGhAABgAAAAMAAACuJBAABwAAAAMAAADQEhAABwAAAAQAAAATIRAACAAAAAQAAABx
HRAACQAAAAQAAACCJxAACgAAAAQAAACCFhAACgAAAAUAAADsHRAACwAAAAUAAADuFxAADAAAAAUAAAAqJRAADAAAAAYAAACtExAADQAAAAYAAAAvIhAADgAA
AAYAAADkGxAADgAAAAcAAADKJxAADwAAAAcAAAAkFxAAEAAAAAcAAAAUHxAAEQAAAAcAAAAyGRAAEQAAAAgAAABGIxAAEgAAAAgAAAAYExAAEwAAAAgAAACe
IRAAEwAAAAkAAAB2HBAAFAAAAAkAAABBJhAAFQAAAAkAAAA9FRAAFQAAAAoAAAB1HhAAFgAAAAoAAACnGBAAFwAAAAoAAACgJRAAGAAAAAoAAACxFBAAGAAA
AAsAAAD8IhAAGQAAAAsAAADNGhAAGgAAAAsAAADOKBAAGgAAAAwAAADWFhAAGwAAAAwAAAAUIBAAHAAAAAwAAABLGhAAHAAAAA0AAAAYJBAAHQAAAA0AAAA4
EhAAHgAAAA0AAAB6IBAAHwAAAA0AAADNHBAAHwAAAA4AAADcJhAAIAAAAA4AAADaFRAAIQAAAA4AAADKHRAAIQAAAA8AAACEGBAAIgAAAA8AAAC2JBAAIwAA
AA8AAABHFBAAIwAAABAAAACQIhAAJAAAABAAAACCGxAAJQAAABAAAADaJxAAJgAAABAAAABzFxAAJgAAABEAAABkHxAAJwAAABEAAACDGRAAKAAAABEAAACZ
IxAAKAAAABIAAACDExAAKQAAABIAAAAcIRAAKgAAABIAAABLHBAAKgAAABMAAABXJhAAKwAAABMAAACtFRAALAAAABMAAADmHhAALQAAABMAAAAEGRAALQAA
ABQAAABxJRAALgAAABQAAADKFBAALwAAABQAAAAWIxAALwAAABUAAACcGhAAMAAAABUAAABUKBAAMQAAABUAAADyFhAAMQAAABYAAADhHxAAMgAAABYAAABo
GhAAMwAAABYAAACBJBAANAAAABYAAACjEhAANAAAABcAAADmIBAANQAAABcAAAA6HRAANgAAABcAAABLJxAANgAAABgAAABKFhAANwAAABgAAAD4HRAAOAAA
ABgAAAD7FxAAOAAAABkAAAA3JRAAOQAAABkAAAC7ExAAOgAAABkAAADzIRAAOwAAABkAAACoGxAAOwAAABoAAACNJxAAPAAAABoAAAA1FxAAPQAAABoAAAAm
HxAAPQAAABsAAABEGRAAPgAAABsAAABZIxAAPwAAABsAAADYEhAAPwAAABwAAACyIRAAQAAAABwAAACLHBAAQQAAABwAAAD+JRAAQgAAABwAAAD6FBAAQgAA
AB0AAAAxHhAAQwAAAB0AAAC/GBAARAAAAB0AAAC5JRAARAAAAB4AAABrFBAARQAAAB4AAAC1IhAARgAAAB4AAADoGhAARgAAAB8AAACGKBAARwAAAB8AAACN
FhAASAAAAB8AAAAxIBAASAAAACAAAAABGhAASQAAACAAAAA2JBAASgAAACAAAABXEhAASwAAACAAAACaIBAASwAAACEAAADtHBAATAAAACEAAAD9JhAATQAA
ACEAAAD8FRAATQAAACIAAAB7HRAATgAAACIAAAA0GBAATwAAACIAAADaJBAATwAAACMAAAD2ExAAUAAAACMAAAA+IhAAUQAAACMAAAAvGxAAUgAAACMAAAAB
KBAAUgAAACQAAACaFxAAUwAAACQAAACMHxAAVAAAACQAAACsGRAAVAAAACUAAADCIxAAVQAAACUAAAAsExAAVgAAACUAAABHIRAAVgAAACYAAADzGxAAVwAA
ACYAAACDJhAAWAAAACYAAABTFRAAWQAAACYAAACMHhAAWQAAACcAAAAKCwwNDg8QERITZXJyb3IodGVybWluYWwpOiBpbnNlcnRMaW5lcyB0cmFja1BpbiBl
cnJvciBlcnI9d2FybmluZyhzY3JlZW4pOiBzZXRBdHRyaWJ1dGUgZXJyb3IgcmVzdG9yaW5nIG9sZCBzdHlsZSBhZnRlciBmYWlsdXJlIGVycj13YXJuaW5n
KHN0cmVhbV90ZXJtaW5hbCk6IGVycm9yIGVuY29kaW5nIHNpemUgcmVwb3J0IGVycj13YXJuaW5nKHN0cmVhbV90ZXJtaW5hbCk6IHRpdGxlIGxlbmd0aCAg
ZXhjZWVkcyBtYXggbGVuZ3RoICwgdHJ1bmNhdGluZ3dhcm5pbmcoc3RyZWFtX3Rlcm1pbmFsKTogZXJyb3Igc2V0dGluZyB0aXRsZSBlcnI9d2FybmluZyhz
dHJlYW1fdGVybWluYWwpOiBlcnJvciBzZXR0aW5nIHB3ZCBlcnI9aW5mbyhwYWdlX2xpc3QpOiBhZGp1c3RpbmcgcGFnZSBjYXBhY2l0eT0AAAAAAAAAAAAA
AAAAAAAAAAIgAABlcnJvcihwYWdlX2xpc3QpOiBpbmNyZWFzZUNhcGFjaXR5IGNsb25lIGZhaWxlZCBlcnI9ZXJyb3IocGFnZV9saXN0KTogZXJyb3IgY2xv
bmluZyByb3dzIGZvciBzcGxpdCBlcnI9d2FybmluZyhwYWdlX2xpc3QpOiB1bmV4cGVjdGVkIGNsb25lUm93RnJvbSBmYWlsdXJlIGR1cmluZyByZXNpemVX
aXRob3V0UmVmbG93R3Jvd0NvbHM6IHdhcm5pbmcocGFnZV9saXN0KTogcmVmbG93Um93IE91dE9mU3BhY2Ugb24gZmlyc3Qgcm93LCBkaXNjYXJkaW5nIGNl
bGwgbWFuYWdlZCBtZW1vcnnvv713YXJuaW5nKHN0cmVhbV90ZXJtaW5hbCk6IGVycm9yIGVuY29kaW5nIG1vZGUgcmVwb3J0IGVycj1lcnJvcihwYWdlX2xp
c3QpOiBzZXRHcmFwaGVtZXMgZmFpbGVkIGFmdGVyIGNhcGFjaXR5IGluY3JlYXNlIGVycj1lcnJvcihwYWdlX2xpc3QpOiBsaW5rIGR1cGUgZmFpbGVkIHdp
dGggY2FwYWNpdHkgY2hlY2sgZXJyPWVycm9yKHBhZ2VfbGlzdCk6IGFkZFdpdGhJZENvbnRleHQgZmFpbGVkIGFmdGVyIGNhcGFjaXR5IGluY3JlYXNlIGVy
cj1lcnJvcihwYWdlX2xpc3QpOiBzZXRIeXBlcmxpbmsgZmFpbGVkIGFmdGVyIGNhcGFjaXR5IGluY3JlYXNlIGVycj1lcnJvcihwYWdlX2xpc3QpOiBhZGRX
aXRoSWQgZmFpbGVkIGFmdGVyIGNhcGFjaXR5IGluY3JlYXNlIGVycj1lcnJvcihwYWdlX2xpc3QpOiBlcnJvciBjbG9uaW5nIHNpbmdsZSByb3cgZm9yIG1v
dmVMYXN0Um93VG9OZXdQYWdlIGVycj0AAAEAAAACAAAAAwAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAEAAQAAAAAA////AAAAAAAAAAAAHR8hAMxmZgC1vWgA8MZ0AIGivgCylLsAir63AMXIxgBmZmYA1U5TALnKSgDnxUcAeqbaAMOX2ABwwLEA6urqAAAAAAAA
AF8AAACHAAAArwAAANcAAAD/AABfAAAAX18AAF+HAABfrwAAX9cAAF//AACHAAAAh18AAIeHAACHrwAAh9cAAIf/AACvAAAAr18AAK+HAACvrwAAr9cAAK//
AADXAAAA118AANeHAADXrwAA19cAANf/AAD/AAAA/18AAP+HAAD/rwAA/9cAAP//AF8AAABfAF8AXwCHAF8ArwBfANcAXwD/AF9fAABfX18AX1+HAF9frwBf
X9cAX1//AF+HAABfh18AX4eHAF+HrwBfh9cAX4f/AF+vAABfr18AX6+HAF+vrwBfr9cAX6//AF/XAABf118AX9eHAF/XrwBf19cAX9f/AF//AABf/18AX/+H
AF//rwBf/9cAX///AIcAAACHAF8AhwCHAIcArwCHANcAhwD/AIdfAACHX18Ah1+HAIdfrwCHX9cAh1//AIeHAACHh18Ah4eHAIeHrwCHh9cAh4f/AIevAACH
r18Ah6+HAIevrwCHr9cAh6//AIfXAACH118Ah9eHAIfXrwCH19cAh9f/AIf/AACH/18Ah/+HAIf/rwCH/9cAh///AK8AAACvAF8ArwCHAK8ArwCvANcArwD/
AK9fAACvX18Ar1+HAK9frwCvX9cAr1//AK+HAACvh18Ar4eHAK+HrwCvh9cAr4f/AK+vAACvr18Ar6+HAK+vrwCvr9cAr6//AK/XAACv118Ar9eHAK/XrwCv
19cAr9f/AK//AACv/18Ar/+HAK//rwCv/9cAr///ANcAAADXAF8A1wCHANcArwDXANcA1wD/ANdfAADXX18A11+HANdfrwDXX9cA11//ANeHAADXh18A14eH
ANeHrwDXh9cA14f/ANevAADXr18A16+HANevrwDXr9cA16//ANfXAADX118A19eHANfXrwDX19cA19f/ANf/AADX/18A1/+HANf/rwDX/9cA1///AP8AAAD/
AF8A/wCHAP8ArwD/ANcA/wD/AP9fAAD/X18A/1+HAP9frwD/X9cA/1//AP+HAAD/h18A/4eHAP+HrwD/h9cA/4f/AP+vAAD/r18A/6+HAP+vrwD/r9cA/6//
AP/XAAD/118A/9eHAP/XrwD/19cA/9f/AP//AAD//18A//+HAP//rwD//9cA////AAgICAASEhIAHBwcACYmJgAwMDAAOjo6AERERABOTk4AWFhYAGJiYgBs
bGwAdnZ2AICAgACKiooAlJSUAJ6engCoqKgAsrKyALy8vADGxsYA0NDQANra2gDk5OQA7u7uAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAB0fIQDMZmYAtb1oAPDGdACBor4AspS7AIq+twDFyMYAZmZmANVOUwC5ykoA58VHAHqm2gDDl9gAcMCxAOrq6gAAAAAAAABf
AAAAhwAAAK8AAADXAAAA/wAAXwAAAF9fAABfhwAAX68AAF/XAABf/wAAhwAAAIdfAACHhwAAh68AAIfXAACH/wAArwAAAK9fAACvhwAAr68AAK/XAACv/wAA
1wAAANdfAADXhwAA168AANfXAADX/wAA/wAAAP9fAAD/hwAA/68AAP/XAAD//wBfAAAAXwBfAF8AhwBfAK8AXwDXAF8A/wBfXwAAX19fAF9fhwBfX68AX1/X
AF9f/wBfhwAAX4dfAF+HhwBfh68AX4fXAF+H/wBfrwAAX69fAF+vhwBfr68AX6/XAF+v/wBf1wAAX9dfAF/XhwBf168AX9fXAF/X/wBf/wAAX/9fAF//hwBf
/68AX//XAF///wCHAAAAhwBfAIcAhwCHAK8AhwDXAIcA/wCHXwAAh19fAIdfhwCHX68Ah1/XAIdf/wCHhwAAh4dfAIeHhwCHh68Ah4fXAIeH/wCHrwAAh69f
AIevhwCHr68Ah6/XAIev/wCH1wAAh9dfAIfXhwCH168Ah9fXAIfX/wCH/wAAh/9fAIf/hwCH/68Ah//XAIf//wCvAAAArwBfAK8AhwCvAK8ArwDXAK8A/wCv
XwAAr19fAK9fhwCvX68Ar1/XAK9f/wCvhwAAr4dfAK+HhwCvh68Ar4fXAK+H/wCvrwAAr69fAK+vhwCvr68Ar6/XAK+v/wCv1wAAr9dfAK/XhwCv168Ar9fX
AK/X/wCv/wAAr/9fAK//hwCv/68Ar//XAK///wDXAAAA1wBfANcAhwDXAK8A1wDXANcA/wDXXwAA119fANdfhwDXX68A11/XANdf/wDXhwAA14dfANeHhwDX
h68A14fXANeH/wDXrwAA169fANevhwDXr68A16/XANev/wDX1wAA19dfANfXhwDX168A19fXANfX/wDX/wAA1/9fANf/hwDX/68A1//XANf//wD/AAAA/wBf
AP8AhwD/AK8A/wDXAP8A/wD/XwAA/19fAP9fhwD/X68A/1/XAP9f/wD/hwAA/4dfAP+HhwD/h68A/4fXAP+H/wD/rwAA/69fAP+vhwD/r68A/6/XAP+v/wD/
1wAA/9dfAP/XhwD/168A/9fXAP/X/wD//wAA//9fAP//hwD//68A///XAP///wAICAgAEhISABwcHAAmJiYAMDAwADo6OgBEREQATk5OAFhYWABiYmIAbGxs
AHZ2dgCAgIAAioqKAJSUlACenp4AqKioALKysgC8vLwAxsbGANDQ0ADa2toA5OTkAO7u7gAdHyEAzGZmALW9aADwxnQAgaK+ALKUuwCKvrcAxcjGAGZmZgDV
TlMAucpKAOfFRwB6ptoAw5fYAHDAsQDq6uoAAAAAAAAAXwAAAIcAAACvAAAA1wAAAP8AAF8AAABfXwAAX4cAAF+vAABf1wAAX/8AAIcAAACHXwAAh4cAAIev
AACH1wAAh/8AAK8AAACvXwAAr4cAAK+vAACv1wAAr/8AANcAAADXXwAA14cAANevAADX1wAA1/8AAP8AAAD/XwAA/4cAAP+vAAD/1wAA//8AXwAAAF8AXwBf
AIcAXwCvAF8A1wBfAP8AX18AAF9fXwBfX4cAX1+vAF9f1wBfX/8AX4cAAF+HXwBfh4cAX4evAF+H1wBfh/8AX68AAF+vXwBfr4cAX6+vAF+v1wBfr/8AX9cA
AF/XXwBf14cAX9evAF/X1wBf1/8AX/8AAF//XwBf/4cAX/+vAF//1wBf//8AhwAAAIcAXwCHAIcAhwCvAIcA1wCHAP8Ah18AAIdfXwCHX4cAh1+vAIdf1wCH
X/8Ah4cAAIeHXwCHh4cAh4evAIeH1wCHh/8Ah68AAIevXwCHr4cAh6+vAIev1wCHr/8Ah9cAAIfXXwCH14cAh9evAIfX1wCH1/8Ah/8AAIf/XwCH/4cAh/+v
AIf/1wCH//8ArwAAAK8AXwCvAIcArwCvAK8A1wCvAP8Ar18AAK9fXwCvX4cAr1+vAK9f1wCvX/8Ar4cAAK+HXwCvh4cAr4evAK+H1wCvh/8Ar68AAK+vXwCv
r4cAr6+vAK+v1wCvr/8Ar9cAAK/XXwCv14cAr9evAK/X1wCv1/8Ar/8AAK//XwCv/4cAr/+vAK//1wCv//8A1wAAANcAXwDXAIcA1wCvANcA1wDXAP8A118A
ANdfXwDXX4cA11+vANdf1wDXX/8A14cAANeHXwDXh4cA14evANeH1wDXh/8A168AANevXwDXr4cA16+vANev1wDXr/8A19cAANfXXwDX14cA19evANfX1wDX
1/8A1/8AANf/XwDX/4cA1/+vANf/1wDX//8A/wAAAP8AXwD/AIcA/wCvAP8A1wD/AP8A/18AAP9fXwD/X4cA/1+vAP9f1wD/X/8A/4cAAP+HXwD/h4cA/4ev
AP+H1wD/h/8A/68AAP+vXwD/r4cA/6+vAP+v1wD/r/8A/9cAAP/XXwD/14cA/9evAP/X1wD/1/8A//8AAP//XwD//4cA//+vAP//1wD///8ACAgIABISEgAc
HBwAJiYmADAwMAA6OjoAREREAE5OTgBYWFgAYmJiAGxsbAB2dnYAgICAAIqKigCUlJQAnp6eAKioqACysrIAvLy8AMbGxgDQ0NAA2traAOTk5ADu7u4AAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACqqqqqAAAAAAAAAAAAAAAAqqqqqgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA3AAAANwAAAAAAAAA3AAAAAAAAAAAAAQA3AAAANwAAAAAAAAAAAgAAAAAAAAAAAAABAAAAAAAA
AAAAAAA3AAAAAAEAAQIDBAABAgABAgMAAKqqqqoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAqqqqqgAAAAAA
AAAAAAAAAAAAAQAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAqqqqqgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAiAEQ
AAgAAABLEBAACwAAAFcQEAAKAAAAfAkQAAsAAAALEBAADQAAAOoBEAALAAAA3gUQAA8AAABwBhAABQAAAPoPEAAQAAAAlA0QAAoAAADnBhAAFQAAAEwCEAAS
AAAAkAsQABEAAAAkEBAAEgAAAIEKEAAKAAAAYhAQAAgAAABrEBAADAAAAIsPEAAPAAAAowgQAA0AAAAZEBAACgAAAHgQEAAQAAAAcQsQAAkAAABYDhAACgAA
ANAMEAAOAAAA4QAQAAsAAACeBxAADgAAAKsLEAAOAAAAFA8QAAoAAABnABAAEwAAAAoLEAATAAAAvQAQABYAAABPABAAFwAAAPIKEAAXAAAAjgAQABcAAACm
ABAAFgAAANQAEAAYAAAAyQMQAAsAAAASCxAACwAAAHsAEAASAAAAbAAQAA4AAAAPCxAADgAAAI4GEAAQAAAARwQQAA0AAAAiARAACgAAAN8MEAAMAAAAewgQ
ABAAAADlERAACwAAADAIEAAYAAAAewsQABQAAAC6CxAAGAAAAJsOEAAVAAAA7AwQABQAAADTCxAAHQAAAJ8OEAARAAAAKAMQAA8AAADjAhAABwAAAHIvEgBu
LxIAci8SADcBAAA4AQAANQEAADYBAAByLxIAsHwSAHAvEgBuLxIAsHwSAG4vEgBsLxIAbC8SAA8AAAD/AAAA/w8AAP//AAA=
`;

// src/wasm/runtime/abi.ts
function resolveWasmAbi(exports) {
  if (exports.restty_render_info) {
    return { kind: "info" };
  }
  if (exports.restty_render_codepoints_ptr) {
    return { kind: "render" };
  }
  if (exports.restty_cell_codepoints_ptr) {
    return { kind: "cells" };
  }
  return null;
}
function ptrFromOffset(base, offset, memSize) {
  if (!offset)
    return 0;
  const absolute = base + offset;
  if (absolute > 0 && absolute < memSize)
    return absolute;
  if (offset > 0 && offset < memSize)
    return offset;
  return 0;
}
function unpackCursor(buffer, ptr) {
  if (!ptr)
    return null;
  const view = new DataView(buffer, ptr, 16);
  return {
    row: view.getUint16(0, true),
    col: view.getUint16(2, true),
    visible: view.getUint8(4),
    style: view.getUint8(5),
    blinking: view.getUint8(6),
    wideTail: view.getUint8(7),
    color: view.getUint32(8, true)
  };
}
function readRenderInfo(exports, handle) {
  if (!exports.restty_render_info)
    return null;
  const base = exports.restty_render_info(handle);
  if (!base)
    return null;
  const mem = exports.memory;
  const view = new DataView(mem.buffer, base, 64);
  const version = view.getUint32(0, true);
  if (version !== 1) {
    return null;
  }
  const rows = view.getUint16(4, true);
  const cols = view.getUint16(6, true);
  const memSize = mem.buffer.byteLength;
  const codepointsPtr = ptrFromOffset(base, view.getUint32(8, true), memSize);
  const fgPtr = ptrFromOffset(base, view.getUint32(12, true), memSize);
  const bgPtr = ptrFromOffset(base, view.getUint32(16, true), memSize);
  const ulPtr = ptrFromOffset(base, view.getUint32(20, true), memSize);
  const ulStylePtr = ptrFromOffset(base, view.getUint32(24, true), memSize);
  const graphemeOffsetPtr = ptrFromOffset(base, view.getUint32(28, true), memSize);
  const graphemeLenPtr = ptrFromOffset(base, view.getUint32(32, true), memSize);
  const graphemeBufferPtr = ptrFromOffset(base, view.getUint32(36, true), memSize);
  const graphemeBufferLen = view.getUint32(40, true);
  const selectionStartPtr = ptrFromOffset(base, view.getUint32(44, true), memSize);
  const selectionEndPtr = ptrFromOffset(base, view.getUint32(48, true), memSize);
  const cursorPtr = ptrFromOffset(base, view.getUint32(52, true), memSize);
  return {
    rows,
    cols,
    codepointsPtr,
    contentTagsPtr: 0,
    fgPtr,
    bgPtr,
    ulPtr,
    ulStylePtr,
    widePtr: 0,
    flagsPtr: 0,
    styleFlagsPtr: 0,
    linkIdsPtr: 0,
    graphemeOffsetPtr,
    graphemeLenPtr,
    graphemeBufferPtr,
    graphemeBufferLen,
    selectionStartPtr,
    selectionEndPtr,
    cursorPtr
  };
}
function readRenderPtrs(exports, handle) {
  const rows = exports.restty_render_rows ? exports.restty_render_rows(handle) : exports.restty_rows(handle);
  const cols = exports.restty_render_cols ? exports.restty_render_cols(handle) : exports.restty_cols(handle);
  return {
    rows,
    cols,
    codepointsPtr: exports.restty_render_codepoints_ptr(handle),
    contentTagsPtr: 0,
    widePtr: 0,
    flagsPtr: 0,
    styleFlagsPtr: 0,
    linkIdsPtr: 0,
    fgPtr: exports.restty_render_fg_rgba_ptr(handle),
    bgPtr: exports.restty_render_bg_rgba_ptr(handle),
    ulPtr: exports.restty_render_ul_rgba_ptr(handle),
    ulStylePtr: exports.restty_render_ul_style_ptr(handle),
    graphemeOffsetPtr: exports.restty_render_grapheme_offset_ptr(handle),
    graphemeLenPtr: exports.restty_render_grapheme_len_ptr(handle),
    graphemeBufferPtr: exports.restty_render_grapheme_buffer_ptr(handle),
    graphemeBufferLen: exports.restty_render_grapheme_buffer_len ? exports.restty_render_grapheme_buffer_len(handle) : 0,
    selectionStartPtr: exports.restty_render_selection_start_ptr(handle),
    selectionEndPtr: exports.restty_render_selection_end_ptr(handle),
    cursorPtr: exports.restty_render_cursor_ptr(handle)
  };
}
function readCellPtrs(exports, handle) {
  const rows = exports.restty_rows(handle);
  const cols = exports.restty_cols(handle);
  return {
    rows,
    cols,
    codepointsPtr: exports.restty_cell_codepoints_ptr(handle),
    contentTagsPtr: exports.restty_cell_content_tags_ptr ? exports.restty_cell_content_tags_ptr(handle) : 0,
    widePtr: exports.restty_cell_wide_ptr ? exports.restty_cell_wide_ptr(handle) : 0,
    flagsPtr: exports.restty_cell_flags_ptr ? exports.restty_cell_flags_ptr(handle) : 0,
    styleFlagsPtr: exports.restty_cell_style_flags_ptr ? exports.restty_cell_style_flags_ptr(handle) : 0,
    linkIdsPtr: exports.restty_cell_link_ids_ptr ? exports.restty_cell_link_ids_ptr(handle) : 0,
    fgPtr: exports.restty_cell_fg_rgba_ptr(handle),
    bgPtr: exports.restty_cell_bg_rgba_ptr(handle),
    ulPtr: exports.restty_cell_ul_rgba_ptr(handle),
    ulStylePtr: exports.restty_cell_underline_styles_ptr(handle),
    graphemeOffsetPtr: exports.restty_cell_grapheme_offsets_ptr(handle),
    graphemeLenPtr: exports.restty_cell_grapheme_lengths_ptr(handle),
    graphemeBufferPtr: exports.restty_grapheme_buffer_ptr(handle),
    graphemeBufferLen: exports.restty_grapheme_buffer_len(handle),
    selectionStartPtr: exports.restty_row_selection_start_ptr(handle),
    selectionEndPtr: exports.restty_row_selection_end_ptr(handle),
    cursorPtr: exports.restty_cursor_info_ptr(handle)
  };
}
function readRenderStatePtrs(abi, exports, handle) {
  if (abi.kind === "info") {
    return readRenderInfo(exports, handle);
  }
  if (abi.kind === "render") {
    return readRenderPtrs(exports, handle);
  }
  return readCellPtrs(exports, handle);
}

// src/wasm/runtime/kitty.ts
var KITTY_PLACEMENT_STRIDE_V1 = 68;
var KITTY_PLACEMENT_STRIDE_V2 = 76;
function readKittyPlacements(exports, memory, handle) {
  if (!exports.restty_kitty_placement_count || !exports.restty_kitty_placements_ptr) {
    return [];
  }
  const count = exports.restty_kitty_placement_count(handle) >>> 0;
  if (!count)
    return [];
  const ptr = exports.restty_kitty_placements_ptr(handle) >>> 0;
  if (!ptr)
    return [];
  const stride = exports.restty_kitty_placement_stride ? exports.restty_kitty_placement_stride() >>> 0 : KITTY_PLACEMENT_STRIDE_V1;
  if (!stride)
    return [];
  const view = new DataView(memory.buffer, ptr, count * stride);
  const placements = new Array(count);
  const hasPlacementIdentity = stride >= KITTY_PLACEMENT_STRIDE_V2;
  for (let i = 0;i < count; i += 1) {
    const base = i * stride;
    placements[i] = {
      imageId: view.getUint32(base + 0, true),
      placementId: hasPlacementIdentity ? view.getUint32(base + 68, true) : 0,
      placementExternal: hasPlacementIdentity ? view.getUint8(base + 72) !== 0 : false,
      imageFormat: view.getUint8(base + 4),
      imageWidth: view.getUint32(base + 8, true),
      imageHeight: view.getUint32(base + 12, true),
      imageDataPtr: view.getUint32(base + 16, true),
      imageDataLen: view.getUint32(base + 20, true),
      x: view.getInt32(base + 24, true),
      y: view.getInt32(base + 28, true),
      z: view.getInt32(base + 32, true),
      width: view.getUint32(base + 36, true),
      height: view.getUint32(base + 40, true),
      cellOffsetX: view.getUint32(base + 44, true),
      cellOffsetY: view.getUint32(base + 48, true),
      sourceX: view.getUint32(base + 52, true),
      sourceY: view.getUint32(base + 56, true),
      sourceWidth: view.getUint32(base + 60, true),
      sourceHeight: view.getUint32(base + 64, true)
    };
  }
  return placements;
}

// src/wasm/runtime/view-cache.ts
function makeViewEntry() {
  return { buffer: null, ptr: 0, len: 0, view: null };
}
function makeRenderViewCache() {
  return {
    codepoints: makeViewEntry(),
    contentTags: makeViewEntry(),
    wide: makeViewEntry(),
    cellFlags: makeViewEntry(),
    styleFlags: makeViewEntry(),
    linkIds: makeViewEntry(),
    fgBytes: makeViewEntry(),
    bgBytes: makeViewEntry(),
    ulBytes: makeViewEntry(),
    ulStyle: makeViewEntry(),
    linkOffsets: makeViewEntry(),
    linkLengths: makeViewEntry(),
    linkBuffer: makeViewEntry(),
    graphemeOffset: makeViewEntry(),
    graphemeLen: makeViewEntry(),
    graphemeBuffer: makeViewEntry(),
    selectionStart: makeViewEntry(),
    selectionEnd: makeViewEntry()
  };
}
function getCachedView(entry, buffer, ptr, len, Ctor) {
  if (!ptr || len <= 0) {
    entry.buffer = buffer;
    entry.ptr = 0;
    entry.len = 0;
    entry.view = null;
    return null;
  }
  if (entry.view && entry.buffer === buffer && entry.ptr === ptr && entry.len === len) {
    return entry.view;
  }
  const view = new Ctor(buffer, ptr, len);
  entry.buffer = buffer;
  entry.ptr = ptr;
  entry.len = len;
  entry.view = view;
  return view;
}

// src/wasm/runtime/render-state.ts
function readRenderState(abi, exports, memory, handle, cache) {
  const info = readRenderStatePtrs(abi, exports, handle);
  if (!info)
    return null;
  const { rows, cols } = info;
  if (!rows || !cols)
    return null;
  const cellCount = rows * cols;
  const buffer = memory.buffer;
  const codepoints = getCachedView(cache.codepoints, buffer, info.codepointsPtr, cellCount, Uint32Array);
  const contentTags = getCachedView(cache.contentTags, buffer, info.contentTagsPtr, cellCount, Uint8Array);
  const wide = getCachedView(cache.wide, buffer, info.widePtr, cellCount, Uint8Array);
  const cellFlags = getCachedView(cache.cellFlags, buffer, info.flagsPtr, cellCount, Uint16Array);
  const styleFlags = getCachedView(cache.styleFlags, buffer, info.styleFlagsPtr, cellCount, Uint16Array);
  const linkIds = getCachedView(cache.linkIds, buffer, info.linkIdsPtr, cellCount, Uint32Array);
  const fgBytes = getCachedView(cache.fgBytes, buffer, info.fgPtr, cellCount * 4, Uint8Array);
  const bgBytes = getCachedView(cache.bgBytes, buffer, info.bgPtr, cellCount * 4, Uint8Array);
  const ulBytes = getCachedView(cache.ulBytes, buffer, info.ulPtr, cellCount * 4, Uint8Array);
  const ulStyle = getCachedView(cache.ulStyle, buffer, info.ulStylePtr, cellCount, Uint8Array);
  const linkCount = exports.restty_link_count ? exports.restty_link_count(handle) : 0;
  const linkOffsetsPtr = linkCount && exports.restty_link_offsets_ptr ? exports.restty_link_offsets_ptr(handle) : 0;
  const linkLengthsPtr = linkCount && exports.restty_link_lengths_ptr ? exports.restty_link_lengths_ptr(handle) : 0;
  const linkOffsets = getCachedView(cache.linkOffsets, buffer, linkOffsetsPtr, linkCount, Uint32Array);
  const linkLengths = getCachedView(cache.linkLengths, buffer, linkLengthsPtr, linkCount, Uint32Array);
  const linkBufferLen = exports.restty_link_buffer_len ? exports.restty_link_buffer_len(handle) : 0;
  const linkBufferPtr = linkBufferLen && exports.restty_link_buffer_ptr ? exports.restty_link_buffer_ptr(handle) : 0;
  const linkBuffer = getCachedView(cache.linkBuffer, buffer, linkBufferPtr, linkBufferLen, Uint8Array);
  const graphemeOffset = getCachedView(cache.graphemeOffset, buffer, info.graphemeOffsetPtr, cellCount, Uint32Array);
  const graphemeLen = getCachedView(cache.graphemeLen, buffer, info.graphemeLenPtr, cellCount, Uint32Array);
  const graphemeBuffer = getCachedView(cache.graphemeBuffer, buffer, info.graphemeBufferPtr, info.graphemeBufferLen, Uint32Array);
  const selectionStart = getCachedView(cache.selectionStart, buffer, info.selectionStartPtr, rows, Int16Array);
  const selectionEnd = getCachedView(cache.selectionEnd, buffer, info.selectionEndPtr, rows, Int16Array);
  const cursor = info.cursorPtr ? unpackCursor(buffer, info.cursorPtr) : null;
  return {
    rows,
    cols,
    cellCount,
    codepoints,
    contentTags,
    wide,
    cellFlags,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    linkOffsets,
    linkLengths,
    linkBuffer,
    graphemeOffset,
    graphemeLen,
    graphemeBuffer,
    selectionStart,
    selectionEnd,
    cursor
  };
}

// src/wasm/runtime/restty-wasm.ts
var textDecoder2 = new TextDecoder;
var textEncoder = new TextEncoder;
var WASM_ERROR_NAMES = {
  0: "ok",
  1: "invalid_handle",
  2: "out_of_memory",
  3: "invalid_arg",
  4: "internal"
};
function wasmErrorName(code) {
  return WASM_ERROR_NAMES[code] ?? `unknown(${code})`;
}
var SEARCH_STATUS_BYTES = 16;
var SEARCH_VIEWPORT_MATCH_BYTES = 8;
var requiredWasmExports = [
  "memory",
  "restty_create",
  "restty_destroy",
  "restty_write",
  "restty_resize",
  "restty_render_update",
  "restty_alloc",
  "restty_free"
];

class ResttyWasm {
  exports;
  abi;
  memory;
  renderViewCaches;
  constructor(exports, abi) {
    this.exports = exports;
    this.abi = abi;
    this.memory = exports.memory;
    this.renderViewCaches = new Map;
  }
  static async load(options = {}) {
    const bytes = decodeBase64Bytes(WASM_BASE64);
    let memory = null;
    const log = options.log;
    const imports = {
      env: {
        log: (ptr, len) => {
          if (!memory || !ptr || !len)
            return;
          const view = new Uint8Array(memory.buffer, ptr, len);
          const text = textDecoder2.decode(view);
          if (log)
            log(text);
        }
      }
    };
    const { instance } = await WebAssembly.instantiate(bytes, imports);
    const exports = instance.exports;
    memory = exports.memory ?? null;
    for (const name of requiredWasmExports) {
      if (!(name in exports)) {
        throw new Error(`missing WASM export: ${name}`);
      }
    }
    const abi = resolveWasmAbi(exports);
    if (!abi) {
      throw new Error("missing render ABI exports");
    }
    return new ResttyWasm(exports, abi);
  }
  create(cols, rows, maxScrollback) {
    return this.exports.restty_create(cols, rows, maxScrollback);
  }
  destroy(handle) {
    this.renderViewCaches.delete(handle);
    this.exports.restty_destroy(handle);
  }
  getRenderViewCache(handle) {
    let cache = this.renderViewCaches.get(handle);
    if (!cache) {
      cache = makeRenderViewCache();
      this.renderViewCaches.set(handle, cache);
    }
    return cache;
  }
  resize(handle, cols, rows) {
    this.exports.restty_resize(handle, cols, rows);
  }
  setPixelSize(handle, widthPx, heightPx) {
    if (!this.exports.restty_set_pixel_size)
      return;
    this.exports.restty_set_pixel_size(handle, widthPx, heightPx);
  }
  renderUpdate(handle) {
    this.exports.restty_render_update(handle);
  }
  scrollViewport(handle, delta) {
    if (!this.exports.restty_scroll_viewport)
      return;
    this.exports.restty_scroll_viewport(handle, delta);
  }
  drainOutput(handle) {
    if (!this.exports.restty_output_ptr || !this.exports.restty_output_len)
      return "";
    const len = this.exports.restty_output_len(handle);
    if (!len)
      return "";
    const ptr = this.exports.restty_output_ptr(handle);
    if (!ptr)
      return "";
    const bytes = new Uint8Array(this.memory.buffer, ptr, len);
    const copy = new Uint8Array(len);
    copy.set(bytes);
    if (this.exports.restty_output_consume) {
      this.exports.restty_output_consume(handle, len);
    }
    return textDecoder2.decode(copy);
  }
  getKittyKeyboardFlags(handle) {
    if (!this.exports.restty_kitty_keyboard_flags)
      return 0;
    return this.exports.restty_kitty_keyboard_flags(handle) >>> 0;
  }
  setSearchQuery(handle, query) {
    if (!this.exports.restty_search_set_query)
      return;
    const bytes = textEncoder.encode(query);
    if (!bytes.length) {
      this.clearSearch(handle);
      return;
    }
    const ptr = this.exports.restty_alloc(bytes.length);
    if (!ptr)
      return;
    const view = new Uint8Array(this.memory.buffer, ptr, bytes.length);
    view.set(bytes);
    this.exports.restty_search_set_query(handle, ptr, bytes.length);
    this.exports.restty_free(ptr, bytes.length);
  }
  clearSearch(handle) {
    if (!this.exports.restty_search_clear)
      return;
    this.exports.restty_search_clear(handle);
  }
  stepSearch(handle, budget) {
    if (!this.exports.restty_search_step)
      return;
    this.exports.restty_search_step(handle, Math.max(0, Math.floor(budget)));
  }
  searchNext(handle) {
    if (!this.exports.restty_search_select_next)
      return;
    this.exports.restty_search_select_next(handle);
  }
  searchPrevious(handle) {
    if (!this.exports.restty_search_select_prev)
      return;
    this.exports.restty_search_select_prev(handle);
  }
  getSearchStatus(handle) {
    const ptr = this.exports.restty_search_status_ptr?.(handle) ?? 0;
    if (!ptr) {
      return {
        active: false,
        pending: false,
        complete: false,
        generation: 0,
        totalMatches: 0,
        selectedIndex: null
      };
    }
    const view = new DataView(this.memory.buffer, ptr, SEARCH_STATUS_BYTES);
    const selectedIndex = view.getInt32(12, true);
    return {
      active: view.getUint8(0) !== 0,
      pending: view.getUint8(1) !== 0,
      complete: view.getUint8(2) !== 0,
      generation: view.getUint32(4, true),
      totalMatches: view.getUint32(8, true),
      selectedIndex: selectedIndex >= 0 ? selectedIndex : null
    };
  }
  getSearchViewportMatches(handle) {
    const count = this.exports.restty_search_viewport_match_count?.(handle) ?? 0;
    const ptr = this.exports.restty_search_viewport_matches_ptr?.(handle) ?? 0;
    if (!count || !ptr)
      return [];
    const view = new DataView(this.memory.buffer, ptr, count * SEARCH_VIEWPORT_MATCH_BYTES);
    const matches = [];
    for (let i = 0;i < count; i += 1) {
      const offset = i * SEARCH_VIEWPORT_MATCH_BYTES;
      matches.push({
        row: view.getUint16(offset, true),
        startCol: view.getUint16(offset + 2, true),
        endCol: view.getUint16(offset + 4, true),
        selected: view.getUint8(offset + 6) !== 0
      });
    }
    return matches;
  }
  getKittyPlacements(handle) {
    return readKittyPlacements(this.exports, this.memory, handle);
  }
  write(handle, text) {
    if (!text)
      return;
    const bytes = textEncoder.encode(text);
    const ptr = this.exports.restty_alloc(bytes.length);
    if (!ptr)
      return;
    const view = new Uint8Array(this.memory.buffer, ptr, bytes.length);
    view.set(bytes);
    this.exports.restty_write(handle, ptr, bytes.length);
    this.exports.restty_free(ptr, bytes.length);
  }
  writeBytes(handle, data) {
    if (!data.length)
      return;
    const ptr = this.exports.restty_alloc(data.length);
    if (!ptr)
      return;
    const view = new Uint8Array(this.memory.buffer, ptr, data.length);
    view.set(data);
    this.exports.restty_write(handle, ptr, data.length);
    this.exports.restty_free(ptr, data.length);
  }
  setDefaultColors(handle, fg, bg, cursor) {
    if (!this.exports.restty_set_default_colors)
      return;
    this.exports.restty_set_default_colors(handle, fg, bg, cursor);
  }
  setPalette(handle, colors, count) {
    if (!this.exports.restty_set_palette)
      return;
    if (count <= 0 || colors.length < count * 3)
      return;
    const byteLen = count * 3;
    const ptr = this.exports.restty_alloc(byteLen);
    if (!ptr)
      return;
    const view = new Uint8Array(this.memory.buffer, ptr, byteLen);
    view.set(colors.subarray(0, byteLen));
    this.exports.restty_set_palette(handle, ptr, count);
    this.exports.restty_free(ptr, byteLen);
  }
  resetPalette(handle) {
    if (!this.exports.restty_reset_palette)
      return;
    this.exports.restty_reset_palette(handle);
  }
  getColorForeground(handle) {
    if (!this.exports.restty_get_color_foreground)
      return null;
    const v = this.exports.restty_get_color_foreground(handle);
    return v === 4294967295 ? null : v;
  }
  getColorBackground(handle) {
    if (!this.exports.restty_get_color_background)
      return null;
    const v = this.exports.restty_get_color_background(handle);
    return v === 4294967295 ? null : v;
  }
  getColorCursor(handle) {
    if (!this.exports.restty_get_color_cursor)
      return null;
    const v = this.exports.restty_get_color_cursor(handle);
    return v === 4294967295 ? null : v;
  }
  getPaletteColor(handle, index) {
    if (!this.exports.restty_get_palette_color)
      return null;
    const v = this.exports.restty_get_palette_color(handle, index);
    return v === 4294967295 ? null : v;
  }
  getPalette(handle) {
    if (!this.exports.restty_get_palette)
      return null;
    const byteLen = 256 * 3;
    const ptr = this.exports.restty_alloc(byteLen);
    if (!ptr)
      return null;
    const result = this.exports.restty_get_palette(handle, ptr);
    if (result !== 0) {
      this.exports.restty_free(ptr, byteLen);
      return null;
    }
    const out = new Uint8Array(byteLen);
    out.set(new Uint8Array(this.memory.buffer, ptr, byteLen));
    this.exports.restty_free(ptr, byteLen);
    return out;
  }
  loadBinarySnapshot(handle, data) {
    const snapshotImport = this.exports.restty_snapshot_import;
    if (!snapshotImport)
      return "snapshot_import export not available";
    if (data.byteLength === 0)
      return "empty snapshot data";
    const ptr = this.exports.restty_alloc(data.byteLength);
    if (!ptr)
      return `alloc failed for ${data.byteLength} bytes`;
    new Uint8Array(this.memory.buffer, ptr, data.byteLength).set(data);
    const result = snapshotImport(handle, ptr, data.byteLength);
    this.exports.restty_free(ptr, data.byteLength);
    if (result !== 0)
      return `snapshot_import error=${result} (${wasmErrorName(result)})`;
    const renderResult = this.exports.restty_render_update(handle);
    if (renderResult !== 0)
      return `render_update error=${renderResult} (${wasmErrorName(renderResult)})`;
    return null;
  }
  getRenderState(handle) {
    return readRenderState(this.abi, this.exports, this.memory, handle, this.getRenderViewCache(handle));
  }
}
async function loadResttyWasm(options = {}) {
  return ResttyWasm.load(options);
}
// src/input/keymap/constants.ts
var sequences = {
  enter: "\r",
  backspace: "",
  delete: "\x1B[3~",
  tab: "\t",
  shiftTab: "\x1B[Z",
  escape: "\x1B"
};
var DEFAULT_CONFIG = {
  enableCtrlCombos: true
};

// src/input/keymap/before-input.ts
function encodeBeforeInputEvent(event) {
  if (!event)
    return "";
  const type = event.inputType;
  if (type === "insertText")
    return event.data || "";
  if (type === "insertLineBreak" || type === "insertParagraph")
    return sequences.enter;
  if (type === "deleteContentBackward" || type === "deleteWordBackward" || type === "deleteSoftLineBackward" || type === "deleteHardLineBackward" || type === "deleteEntireSoftLine") {
    return sequences.backspace;
  }
  if (type === "deleteContentForward" || type === "deleteWordForward" || type === "deleteSoftLineForward" || type === "deleteHardLineForward") {
    return sequences.delete;
  }
  if (type === "insertFromPaste") {
    return event.dataTransfer?.getData("text/plain") || "";
  }
  return "";
}

// src/input/keymap/control.ts
function ctrlCharForKey(key) {
  if (key === " ")
    return "\x00";
  if (key === "@")
    return "\x00";
  if (key === "[")
    return "\x1B";
  if (key === "\\")
    return "\x1C";
  if (key === "]")
    return "\x1D";
  if (key === "^")
    return "\x1E";
  if (key === "_")
    return "\x1F";
  if (key === "?")
    return "";
  if (key.length === 1) {
    const code = key.toUpperCase().charCodeAt(0);
    if (code >= 64 && code <= 95) {
      return String.fromCharCode(code & 31);
    }
  }
  return "";
}
function modifierCode(event) {
  let mod = 1;
  if (event.shiftKey)
    mod += 1;
  if (event.altKey)
    mod += 2;
  if (event.ctrlKey)
    mod += 4;
  return mod;
}

// src/input/keymap/default-mapping.ts
var FUNCTION_KEY_SEQUENCE_BY_NUMBER = {
  1: "\x1BOP",
  2: "\x1BOQ",
  3: "\x1BOR",
  4: "\x1BOS",
  5: "\x1B[15~",
  6: "\x1B[17~",
  7: "\x1B[18~",
  8: "\x1B[19~",
  9: "\x1B[20~",
  10: "\x1B[21~",
  11: "\x1B[23~",
  12: "\x1B[24~"
};
var MODIFIER_CAPABLE_CSI_FINAL_BY_KEY = {
  ArrowUp: "A",
  ArrowDown: "B",
  ArrowRight: "C",
  ArrowLeft: "D",
  Home: "H",
  End: "F"
};
var SIMPLE_KEY_SEQUENCES = {
  Enter: sequences.enter,
  Backspace: sequences.backspace,
  Delete: sequences.delete,
  Del: sequences.delete,
  Escape: sequences.escape,
  PageUp: "\x1B[5~",
  PageDown: "\x1B[6~",
  Insert: "\x1B[2~"
};
function encodeDefaultKeyEvent(event) {
  const key = event.key ?? "";
  if (key === "Tab") {
    return event.shiftKey ? sequences.shiftTab : sequences.tab;
  }
  const modifierFinal = MODIFIER_CAPABLE_CSI_FINAL_BY_KEY[key];
  if (modifierFinal) {
    return event.shiftKey || event.altKey || event.ctrlKey ? `\x1B[1;${modifierCode(event)}${modifierFinal}` : `\x1B[${modifierFinal}`;
  }
  const direct = SIMPLE_KEY_SEQUENCES[key];
  if (direct)
    return direct;
  if (key.startsWith("F")) {
    const fn = Number(key.slice(1));
    return FUNCTION_KEY_SEQUENCE_BY_NUMBER[fn] ?? "";
  }
  if (key.length === 1) {
    return event.altKey ? `\x1B${key}` : key;
  }
  return "";
}

// src/input/keymap/normalization.ts
var UN_SHIFTED_CODE_BY_CODE = {
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/"
};
var SHIFTED_CODE_BY_CODE = {
  Backquote: "~",
  Digit1: "!",
  Digit2: "@",
  Digit3: "#",
  Digit4: "$",
  Digit5: "%",
  Digit6: "^",
  Digit7: "&",
  Digit8: "*",
  Digit9: "(",
  Digit0: ")",
  Minus: "_",
  Equal: "+",
  BracketLeft: "{",
  BracketRight: "}",
  Backslash: "|",
  Semicolon: ":",
  Quote: '"',
  Comma: "<",
  Period: ">",
  Slash: "?"
};
function deriveUnshiftedCodepoint(event) {
  const code = event.code || "";
  if (code.startsWith("Key") && code.length === 4) {
    return code.slice(3).toLowerCase().codePointAt(0) ?? 0;
  }
  if (code.startsWith("Digit") && code.length === 6) {
    return code.slice(5).codePointAt(0) ?? 0;
  }
  const punctuation = UN_SHIFTED_CODE_BY_CODE[code];
  if (punctuation)
    return punctuation.codePointAt(0) ?? 0;
  if (event.key?.length === 1)
    return event.key.codePointAt(0) ?? 0;
  return 0;
}
function deriveShiftedCodepoint(event, unshiftedCodepoint) {
  if (!event.shiftKey)
    return 0;
  if (event.key?.length === 1) {
    const cp2 = event.key.codePointAt(0) ?? 0;
    if (cp2 > 0 && cp2 !== unshiftedCodepoint)
      return cp2;
  }
  const shifted = SHIFTED_CODE_BY_CODE[event.code || ""];
  if (!shifted)
    return 0;
  const cp = shifted.codePointAt(0) ?? 0;
  return cp !== unshiftedCodepoint ? cp : 0;
}
function deriveBaseLayoutCodepoint(event) {
  const code = event.code || "";
  if (code.startsWith("Key") && code.length === 4) {
    return code.slice(3).toLowerCase().codePointAt(0) ?? 0;
  }
  if (code.startsWith("Digit") && code.length === 6) {
    return code.slice(5).codePointAt(0) ?? 0;
  }
  const punctuation = UN_SHIFTED_CODE_BY_CODE[code];
  if (punctuation)
    return punctuation.codePointAt(0) ?? 0;
  return 0;
}
function toCodepoints(text) {
  const points = [];
  for (const ch of text) {
    points.push(ch.codePointAt(0) ?? 0);
  }
  return points.filter((cp) => cp > 0);
}

// src/input/keymap/kitty.ts
var KITTY_FLAG_DISAMBIGUATE = 1 << 0;
var KITTY_FLAG_REPORT_EVENTS = 1 << 1;
var KITTY_FLAG_REPORT_ALTERNATE = 1 << 2;
var KITTY_FLAG_REPORT_ALL = 1 << 3;
var KITTY_FLAG_REPORT_ASSOCIATED_TEXT = 1 << 4;
var KITTY_SPECIAL_KEYS = {
  Escape: { code: 27, final: "u" },
  Enter: { code: 13, final: "u" },
  Tab: { code: 9, final: "u" },
  Backspace: { code: 127, final: "u" },
  Insert: { code: 2, final: "~" },
  Delete: { code: 3, final: "~" },
  Del: { code: 3, final: "~" },
  ArrowLeft: { code: 1, final: "D" },
  ArrowRight: { code: 1, final: "C" },
  ArrowUp: { code: 1, final: "A" },
  ArrowDown: { code: 1, final: "B" },
  PageUp: { code: 5, final: "~" },
  PageDown: { code: 6, final: "~" },
  Home: { code: 1, final: "H" },
  End: { code: 1, final: "F" },
  CapsLock: { code: 57358, final: "u" },
  ScrollLock: { code: 57359, final: "u" },
  NumLock: { code: 57360, final: "u" },
  PrintScreen: { code: 57361, final: "u" },
  Pause: { code: 57362, final: "u" },
  ContextMenu: { code: 57363, final: "u" },
  Menu: { code: 57363, final: "u" },
  F1: { code: 1, final: "P" },
  F2: { code: 1, final: "Q" },
  F3: { code: 13, final: "~" },
  F4: { code: 1, final: "S" },
  F5: { code: 15, final: "~" },
  F6: { code: 17, final: "~" },
  F7: { code: 18, final: "~" },
  F8: { code: 19, final: "~" },
  F9: { code: 20, final: "~" },
  F10: { code: 21, final: "~" },
  F11: { code: 23, final: "~" },
  F12: { code: 24, final: "~" },
  F13: { code: 57376, final: "u" },
  F14: { code: 57377, final: "u" },
  F15: { code: 57378, final: "u" },
  F16: { code: 57379, final: "u" },
  F17: { code: 57380, final: "u" },
  F18: { code: 57381, final: "u" },
  F19: { code: 57382, final: "u" },
  F20: { code: 57383, final: "u" },
  F21: { code: 57384, final: "u" },
  F22: { code: 57385, final: "u" },
  F23: { code: 57386, final: "u" },
  F24: { code: 57387, final: "u" }
};
var KITTY_KEYPAD_BY_CODE = {
  Numpad0: { code: 57399, final: "u" },
  Numpad1: { code: 57400, final: "u" },
  Numpad2: { code: 57401, final: "u" },
  Numpad3: { code: 57402, final: "u" },
  Numpad4: { code: 57403, final: "u" },
  Numpad5: { code: 57404, final: "u" },
  Numpad6: { code: 57405, final: "u" },
  Numpad7: { code: 57406, final: "u" },
  Numpad8: { code: 57407, final: "u" },
  Numpad9: { code: 57408, final: "u" },
  NumpadDecimal: { code: 57409, final: "u" },
  NumpadDivide: { code: 57410, final: "u" },
  NumpadMultiply: { code: 57411, final: "u" },
  NumpadSubtract: { code: 57412, final: "u" },
  NumpadAdd: { code: 57413, final: "u" },
  NumpadEnter: { code: 57414, final: "u" },
  NumpadEqual: { code: 57415, final: "u" }
};
var KITTY_LOCK_KEYS = new Set(["CapsLock", "NumLock", "ScrollLock"]);
function kittyModifierCode(event, includeLockModifiers) {
  let code = 1;
  if (event.shiftKey)
    code += 1;
  if (event.altKey)
    code += 2;
  if (event.ctrlKey)
    code += 4;
  if (event.metaKey)
    code += 8;
  if (includeLockModifiers) {
    if (event.getModifierState?.("CapsLock"))
      code += 64;
    if (event.getModifierState?.("NumLock"))
      code += 128;
  }
  return code;
}
function kittyEventType(event, reportEvents) {
  if (!reportEvents)
    return 0;
  if (event.type === "keyup")
    return 3;
  if (event.repeat)
    return 2;
  return 1;
}
function encodeKittySequence(code, final, modifiers, eventType, alternates, associatedText) {
  if (final !== "u" && final !== "~") {
    if (eventType !== 0)
      return `\x1B[1;${modifiers}:${eventType}${final}`;
    if (modifiers > 1)
      return `\x1B[1;${modifiers}${final}`;
    return `\x1B[${final}`;
  }
  let keyPart = `${code}`;
  if (final === "u" && alternates && (alternates.shifted || alternates.base)) {
    const shifted = alternates.shifted ? `${alternates.shifted}` : "";
    const base = alternates.base ? `${alternates.base}` : "";
    keyPart += `:${shifted}`;
    if (base)
      keyPart += `:${base}`;
  }
  const hasAssocText = Boolean(associatedText?.length);
  if (eventType === 0 && modifiers <= 1 && !hasAssocText) {
    return `\x1B[${keyPart}${final}`;
  }
  let seq = `\x1B[${keyPart};${modifiers}`;
  if (eventType !== 0)
    seq += `:${eventType}`;
  if (hasAssocText)
    seq += `;${associatedText.join(":")}`;
  seq += final;
  return seq;
}
function encodeKittyKeyEvent(event, kittyFlags) {
  const reportEvents = (kittyFlags & KITTY_FLAG_REPORT_EVENTS) !== 0;
  const reportAlternate = (kittyFlags & KITTY_FLAG_REPORT_ALTERNATE) !== 0;
  const reportAll = (kittyFlags & KITTY_FLAG_REPORT_ALL) !== 0;
  const reportAssociatedText = (kittyFlags & KITTY_FLAG_REPORT_ASSOCIATED_TEXT) !== 0;
  const disambiguate = (kittyFlags & KITTY_FLAG_DISAMBIGUATE) !== 0;
  const key = event.key ?? "";
  const special = KITTY_KEYPAD_BY_CODE[event.code || ""] ?? KITTY_SPECIAL_KEYS[key];
  const isLockKey = KITTY_LOCK_KEYS.has(key);
  const hasText = key.length === 1;
  const hasTextModifiers = event.altKey || event.ctrlKey || event.metaKey;
  const eventType = kittyEventType(event, reportEvents);
  const isRelease = event.type === "keyup";
  const isLegacyTextKey = !special && hasText && !reportAll && !hasTextModifiers;
  const isLegacyControlKey = (key === "Enter" || key === "Tab" || key === "Backspace") && !reportAll && !hasTextModifiers && !disambiguate;
  if (isRelease && !reportEvents)
    return "";
  if (isRelease && isLegacyTextKey)
    return "";
  if (isRelease && isLegacyControlKey)
    return "";
  if (isLockKey && !reportAll)
    return "";
  if (isLegacyTextKey) {
    return key;
  }
  if (isLegacyControlKey) {
    switch (key) {
      case "Enter":
        return sequences.enter;
      case "Tab":
        return sequences.tab;
      case "Backspace":
        return sequences.backspace;
      default:
        break;
    }
  }
  const mods = kittyModifierCode(event, reportAll);
  if (special) {
    return encodeKittySequence(special.code, special.final, mods, eventType);
  }
  const unshifted = deriveUnshiftedCodepoint(event);
  if (unshifted > 0 && (reportAll || disambiguate || hasTextModifiers || eventType !== 0)) {
    const alternates = reportAlternate && reportAll ? {
      shifted: deriveShiftedCodepoint(event, unshifted) || undefined,
      base: (() => {
        const base = deriveBaseLayoutCodepoint(event);
        return base > 0 && base !== unshifted ? base : undefined;
      })()
    } : undefined;
    const associated = reportAll && reportAssociatedText && hasText ? toCodepoints(key) : undefined;
    return encodeKittySequence(unshifted, "u", mods, eventType, alternates, associated);
  }
  if (hasText) {
    return event.altKey ? `\x1B${key}` : key;
  }
  return "";
}

// src/input/keymap/pty-map.ts
function parseKittyKeySequence(seq) {
  const csi = "\x1B[";
  if (!seq.startsWith(csi))
    return null;
  const final = seq.endsWith("u") ? "u" : seq.endsWith("~") ? "~" : null;
  if (!final)
    return null;
  const body = seq.slice(csi.length, -1);
  if (!body)
    return null;
  const parts = body.split(";");
  const keyPart = parts[0] ?? "";
  if (!keyPart)
    return null;
  const keyParts = keyPart.split(":");
  if (!keyParts.every((part) => /^[0-9]+$/.test(part)))
    return null;
  const code = Number(keyParts[0]);
  if (!Number.isFinite(code))
    return null;
  let modifiers = 1;
  let eventType = 0;
  const modifierPart = parts[1] ?? "";
  if (modifierPart) {
    const [modifierText, eventTypeText = ""] = modifierPart.split(":");
    if (!modifierText || !/^[0-9]+$/.test(modifierText))
      return null;
    modifiers = Number(modifierText);
    if (!Number.isFinite(modifiers))
      return null;
    if (eventTypeText) {
      if (!/^[0-9]+$/.test(eventTypeText))
        return null;
      eventType = Number(eventTypeText);
      if (!Number.isFinite(eventType))
        return null;
    }
  }
  return {
    code,
    final,
    modifiers,
    eventType,
    hasAlternates: keyParts.length > 1,
    hasAssociatedText: parts.length > 2 && parts.slice(2).some((part) => part.length > 0)
  };
}
function mapKeySequenceForPty(seq) {
  const kitty = parseKittyKeySequence(seq);
  if (kitty) {
    if (kitty.eventType === 3)
      return seq;
    const legacyCompatible = kitty.modifiers === 1 && !kitty.hasAlternates && !kitty.hasAssociatedText;
    if (legacyCompatible) {
      if (kitty.final === "u") {
        if (kitty.code === 127)
          return "";
        if (kitty.code === 13)
          return "\r";
        if (kitty.code === 9)
          return "\t";
      }
      if (kitty.final === "~" && kitty.code === 3)
        return "\x1B[3~";
    }
    return seq;
  }
  if (seq === sequences.backspace || seq === "\b" || seq === "\b\x1B[P")
    return "";
  if (seq === sequences.delete || seq === "\x1B[P")
    return "\x1B[3~";
  if (seq === sequences.enter || seq === `\r
`)
    return "\r";
  return seq;
}

// src/input/keymap.ts
function encodeKeyEvent(event, config = DEFAULT_CONFIG, kittyFlags = 0) {
  if (!event)
    return "";
  if (event.isComposing)
    return "";
  if (kittyFlags !== 0) {
    return encodeKittyKeyEvent(event, kittyFlags);
  }
  if (event.metaKey)
    return "";
  const cfg = { ...DEFAULT_CONFIG, ...config };
  let seq = "";
  if (cfg.enableCtrlCombos && event.ctrlKey) {
    seq = ctrlCharForKey(event.key);
    if (event.altKey && seq)
      seq = `\x1B${seq}`;
  }
  if (!seq) {
    seq = encodeDefaultKeyEvent(event);
  }
  return seq;
}
function encodeBeforeInput(event) {
  return encodeBeforeInputEvent(event);
}
function mapKeyForPty(seq) {
  return mapKeySequenceForPty(seq);
}

// src/input/ansi.ts
var ESC = "\x1B";
function parsePrivateModeSeq(seq) {
  if (!seq.startsWith(`${ESC}[?`) || seq.length < 5)
    return null;
  const final = seq[seq.length - 1];
  if (final !== "h" && final !== "l")
    return null;
  const body = seq.slice(3, -1);
  if (!body || /[^0-9;]/.test(body))
    return null;
  const parts = body.split(";");
  const codes = [];
  for (let i = 0;i < parts.length; i += 1) {
    const part = parts[i];
    if (!part)
      return null;
    const code = Number(part);
    if (!Number.isFinite(code))
      return null;
    codes.push(code);
  }
  return { codes, enabled: final === "h" };
}
function parseWindowOpSeq(seq) {
  if (!seq.startsWith(`${ESC}[`) || !seq.endsWith("t"))
    return null;
  const body = seq.slice(2, -1);
  if (/[^0-9;]/.test(body))
    return null;
  return body ? body.split(";").map((part) => Number(part)) : [];
}
function isDeviceAttributesQuery(seq) {
  if (!seq.startsWith(`${ESC}[`) || !seq.endsWith("c"))
    return false;
  const body = seq.slice(2, -1);
  let i = 0;
  while (i < body.length && (body[i] === "?" || body[i] === ">"))
    i += 1;
  while (i < body.length && body.charCodeAt(i) >= 48 && body.charCodeAt(i) <= 57)
    i += 1;
  if (i < body.length && body[i] === ";") {
    i += 1;
    while (i < body.length && body.charCodeAt(i) >= 48 && body.charCodeAt(i) <= 57)
      i += 1;
  }
  return i === body.length;
}

// src/input/mouse.ts
class MouseController {
  mode = "auto";
  enabled = false;
  format = "x10";
  motion = "none";
  pressed = false;
  button = 0;
  flags = { 1000: false, 1002: false, 1003: false };
  x10Event = false;
  sendReply;
  positionToCell;
  positionToPixel;
  constructor(options) {
    this.sendReply = options.sendReply;
    this.positionToCell = options.positionToCell;
    this.positionToPixel = options.positionToPixel;
  }
  setReplySink(fn) {
    this.sendReply = fn;
  }
  setPositionToCell(fn) {
    this.positionToCell = fn;
  }
  setPositionToPixel(fn) {
    this.positionToPixel = fn;
  }
  setMode(mode) {
    this.mode = mode;
    if (mode === "on") {
      this.enabled = true;
      this.format = "sgr";
      this.motion = "drag";
    } else if (mode === "off") {
      this.enabled = false;
      this.format = "x10";
      this.motion = "none";
    } else {
      this.enabled = this.x10Event || this.flags[1000] || this.flags[1002] || this.flags[1003];
      if (this.flags[1003])
        this.motion = "any";
      else if (this.flags[1002])
        this.motion = "drag";
      else
        this.motion = "none";
    }
  }
  handleModeSeq(seq) {
    const mode = parsePrivateModeSeq(seq);
    if (!mode)
      return false;
    const { enabled, codes } = mode;
    let handled = false;
    for (const code of codes) {
      if (code === 9) {
        this.x10Event = enabled;
        handled = true;
        continue;
      }
      if (code === 1006) {
        this.format = enabled ? "sgr" : "x10";
        handled = true;
        continue;
      }
      if (code === 1016) {
        this.format = enabled ? "sgr_pixels" : "x10";
        handled = true;
        continue;
      }
      if (code === 1005) {
        this.format = enabled ? "utf8" : "x10";
        handled = true;
        continue;
      }
      if (code === 1015) {
        this.format = enabled ? "urxvt" : "x10";
        handled = true;
        continue;
      }
      if (code === 1000 || code === 1002 || code === 1003) {
        this.updateFlags(code, enabled);
        handled = true;
      }
    }
    return handled;
  }
  isActive() {
    if (this.mode === "off")
      return false;
    if (this.mode === "on")
      return true;
    return this.enabled;
  }
  getStatus() {
    return { mode: this.mode, active: this.isActive(), detail: this.format, enabled: this.enabled };
  }
  sendMouseEvent(kind, event) {
    if (!this.isActive())
      return false;
    if (!this.positionToCell)
      return false;
    if (this.isX10EventMode() && kind !== "down")
      return false;
    const cell = this.positionToCell(event);
    const col = cell.col + 1;
    const row = cell.row + 1;
    const pixel = this.positionToPixel ? this.positionToPixel(event) : null;
    const isSgr = this.format === "sgr" || this.format === "sgr_pixels";
    const base = "button" in event && event.button === 1 ? 1 : ("button" in event) && event.button === 2 ? 2 : 0;
    const mods = this.modifiers(event, !this.isX10EventMode());
    if (kind === "down") {
      this.pressed = true;
      this.button = base;
      const code = base + mods;
      return this.sendMouse(code, col, row, pixel, false);
    }
    if (kind === "up") {
      const btn = this.pressed ? this.button : base;
      this.pressed = false;
      const code = isSgr ? btn + mods : 3 + mods;
      return this.sendMouse(code, col, row, pixel, true);
    }
    if (kind === "move") {
      if (this.motion === "none")
        return false;
      if (this.motion === "drag" && !this.pressed)
        return false;
      const btn = this.pressed ? this.button : 3;
      const code = btn + mods + 32;
      return this.sendMouse(code, col, row, pixel, false);
    }
    if (kind === "wheel") {
      const delta = Math.sign(event.deltaY);
      if (!delta)
        return false;
      const code = (delta < 0 ? 64 : 65) + mods;
      return this.sendMouse(code, col, row, pixel, false);
    }
    return false;
  }
  updateFlags(code, enabled) {
    if (!(code in this.flags))
      return;
    this.flags[code] = enabled;
    this.enabled = this.x10Event || this.flags[1000] || this.flags[1002] || this.flags[1003];
    if (this.flags[1003])
      this.motion = "any";
    else if (this.flags[1002])
      this.motion = "drag";
    else
      this.motion = "none";
  }
  isX10EventMode() {
    if (!this.x10Event)
      return false;
    return !(this.flags[1000] || this.flags[1002] || this.flags[1003]);
  }
  modifiers(event, enabled) {
    if (!enabled)
      return 0;
    let mod = 0;
    if (event.shiftKey)
      mod |= 4;
    if (event.altKey)
      mod |= 8;
    if (event.ctrlKey)
      mod |= 16;
    return mod;
  }
  sendMouse(code, col, row, pixel, release) {
    if (this.format === "x10") {
      if (col > 223 || row > 223)
        return false;
      const cb = 32 + code;
      const cx = 32 + col;
      const cy = 32 + row;
      this.sendReply(`\x1B[M${String.fromCharCode(cb, cx, cy)}`);
      return true;
    }
    if (this.format === "utf8") {
      const cb = String.fromCharCode(32 + code);
      const cx = String.fromCodePoint(32 + col);
      const cy = String.fromCodePoint(32 + row);
      this.sendReply(`\x1B[M${cb}${cx}${cy}`);
      return true;
    }
    if (this.format === "urxvt") {
      this.sendReply(`\x1B[${32 + code};${col};${row}M`);
      return true;
    }
    const suffix = release ? "m" : "M";
    if (this.format === "sgr_pixels" && pixel) {
      this.sendReply(`\x1B[<${code};${pixel.x};${pixel.y}${suffix}`);
      return true;
    }
    this.sendReply(`\x1B[<${code};${col};${row}${suffix}`);
    return true;
  }
}

// src/input/output/csi.ts
function deriveAltScreen(seq, current) {
  const altMode = parsePrivateModeSeq(seq);
  if (!altMode)
    return current;
  const { enabled, codes } = altMode;
  if (codes.some((code) => code === 47 || code === 1047 || code === 1049)) {
    return enabled;
  }
  return current;
}
function applyTrackedPrivateModes(seq, state2) {
  const mode = parsePrivateModeSeq(seq);
  if (!mode)
    return false;
  const { enabled, codes } = mode;
  let handled = false;
  for (const code of codes) {
    if (code === 2004) {
      state2.bracketedPaste = enabled;
      handled = true;
    } else if (code === 1004) {
      state2.focusReporting = enabled;
      handled = true;
    } else if (code === 2026) {
      state2.synchronizedOutput = enabled;
    }
  }
  return handled;
}
function handleWindowOpSequence(seq, handlers) {
  const params = parseWindowOpSeq(seq);
  if (!params)
    return false;
  const op = params[0] ?? 0;
  const metrics = handlers.getWindowMetrics?.();
  if (metrics && op === 14 && params.length === 1) {
    handlers.sendReply(`\x1B[4;${metrics.heightPx};${metrics.widthPx}t`);
    return true;
  }
  if (metrics && op === 16 && params.length === 1) {
    handlers.sendReply(`\x1B[6;${metrics.cellHeightPx};${metrics.cellWidthPx}t`);
    return true;
  }
  if (metrics && op === 18 && params.length === 1) {
    handlers.sendReply(`\x1B[8;${metrics.rows};${metrics.cols}t`);
    return true;
  }
  if (!handlers.onWindowOp)
    return false;
  if (params[0] === 8 && params.length >= 3) {
    handlers.onWindowOp({
      type: "resize",
      rows: params[1] ?? 0,
      cols: params[2] ?? 0,
      params,
      raw: seq
    });
  } else {
    handlers.onWindowOp({ type: "unknown", params, raw: seq });
  }
  return true;
}
function handleCoreCsiSequence(seq, handlers) {
  if (seq === "\x1B[6n") {
    const { row, col } = handlers.getCursorPosition();
    handlers.sendReply(`\x1B[${row};${col}R`);
    return true;
  }
  if (seq === "\x1B[>q") {
    handlers.sendReply("\x1BP>|ghostty 1.0\x1B\\");
    return true;
  }
  if (isDeviceAttributesQuery(seq)) {
    handlers.sendReply("\x1B[?1;2c");
    return true;
  }
  return false;
}

// src/input/output/osc.ts
var textDecoder3 = new TextDecoder;
var textEncoder2 = new TextEncoder;
function replyOscColor(code, rgb, sendReply) {
  const toHex4 = (value) => Math.round(Math.max(0, Math.min(255, value)) * 257).toString(16).padStart(4, "0");
  const r = toHex4(rgb[0]);
  const g = toHex4(rgb[1]);
  const b = toHex4(rgb[2]);
  sendReply(`\x1B]${code};rgb:${r}/${g}/${b}\x07`);
}
function handleOscSequence(seq, handlers) {
  const content = seq.slice(2);
  const parts = content.split(";");
  const code = parts[0] ?? "";
  if (code === "9") {
    const firstSep = content.indexOf(";");
    const body = firstSep >= 0 ? content.slice(firstSep + 1) : "";
    if (/^(?:[2-9]|1[0-2]?)(?:;|$)/.test(body)) {
      return true;
    }
    handlers.onDesktopNotification?.({
      title: "",
      body,
      source: "osc9",
      raw: seq
    });
    return true;
  }
  if (code === "777") {
    const firstSep = content.indexOf(";");
    const rest = firstSep >= 0 ? content.slice(firstSep + 1) : "";
    if (!rest.startsWith("notify;")) {
      return true;
    }
    const payload = rest.slice("notify;".length);
    const titleSep = payload.indexOf(";");
    if (titleSep < 0) {
      return true;
    }
    handlers.onDesktopNotification?.({
      title: payload.slice(0, titleSep),
      body: payload.slice(titleSep + 1),
      source: "osc777",
      raw: seq
    });
    return true;
  }
  if (code === "52") {
    const target = parts[1] ?? "c";
    const payload = parts.slice(2).join(";");
    if (payload === "?") {
      if (!handlers.onClipboardRead)
        return true;
      Promise.resolve(handlers.onClipboardRead()).then((text2) => {
        const safeText = text2 ?? "";
        const bytes2 = textEncoder2.encode(safeText);
        try {
          const encoded = encodeBase64Bytes(bytes2);
          handlers.sendReply(`\x1B]52;${target};${encoded}\x07`);
        } catch {
        }
      }).catch(() => {
      });
      return true;
    }
    if (!handlers.onClipboardWrite)
      return true;
    let bytes = new Uint8Array(0);
    try {
      bytes = decodeBase64Bytes(payload);
    } catch {
    }
    const text = textDecoder3.decode(bytes);
    Promise.resolve(handlers.onClipboardWrite(text)).catch(() => {
    });
    return true;
  }
  const param = parts[1];
  if (param !== "?")
    return false;
  const colors = handlers.getDefaultColors?.();
  if (!colors)
    return false;
  if (code === "10" && colors.fg) {
    replyOscColor(code, colors.fg, handlers.sendReply);
    return true;
  }
  if (code === "11" && colors.bg) {
    replyOscColor(code, colors.bg, handlers.sendReply);
    return true;
  }
  if (code === "12" && colors.cursor) {
    replyOscColor(code, colors.cursor, handlers.sendReply);
    return true;
  }
  return false;
}

// src/input/output/prompt.ts
function createPromptState() {
  return {
    semanticPromptSeen: false,
    promptClickEvents: false,
    promptInputActive: false,
    commandRunning: false
  };
}
function isPromptClickEventsEnabled(state2, altScreen) {
  return state2.semanticPromptSeen && state2.promptClickEvents && state2.promptInputActive && !state2.commandRunning && !altScreen;
}
function readOsc133BoolOption(options, key) {
  if (!options)
    return null;
  const prefix = `${key}=`;
  const fields = options.split(";");
  for (let i = 0;i < fields.length; i += 1) {
    const field = fields[i];
    if (!field.startsWith(prefix))
      continue;
    const value = field.slice(prefix.length);
    if (value === "1")
      return true;
    if (value === "0")
      return false;
    return null;
  }
  return null;
}
function observeSemanticPromptOsc(state2, action, options) {
  const clickEvents = readOsc133BoolOption(options, "click_events");
  if (clickEvents !== null)
    state2.promptClickEvents = clickEvents;
  switch (action) {
    case "A":
    case "B":
    case "I":
      state2.semanticPromptSeen = true;
      state2.promptInputActive = true;
      state2.commandRunning = false;
      break;
    case "C":
      state2.semanticPromptSeen = true;
      state2.promptInputActive = false;
      state2.commandRunning = true;
      break;
    case "D":
      state2.semanticPromptSeen = true;
      state2.promptInputActive = false;
      state2.commandRunning = false;
      break;
    case "P":
      state2.semanticPromptSeen = true;
      break;
    default:
      break;
  }
}
function observeOscPromptState(state2, seq) {
  const content = seq.slice(2);
  const sep = content.indexOf(";");
  if (sep < 0)
    return;
  const code = content.slice(0, sep);
  if (code !== "133")
    return;
  const rest = content.slice(sep + 1);
  if (!rest)
    return;
  const action = rest[0] ?? "";
  if (!action)
    return;
  const options = rest.length > 2 && rest[1] === ";" ? rest.slice(2) : "";
  observeSemanticPromptOsc(state2, action, options);
}

// src/input/output/index.ts
var BYTE_TEXT_CHUNK = 32768;
function mapBytesToControlText(bytes) {
  let out = "";
  for (let i = 0;i < bytes.length; i += BYTE_TEXT_CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + BYTE_TEXT_CHUNK));
  }
  return out;
}
function normalizeCursorPosition(value) {
  if (!value)
    return null;
  const row = Number(value.row);
  const col = Number(value.col);
  if (!Number.isFinite(row) || !Number.isFinite(col))
    return null;
  return {
    row: Math.max(1, Math.floor(row)),
    col: Math.max(1, Math.floor(col))
  };
}
function resolveCursorStepParam(raw) {
  if (!raw)
    return 1;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0)
    return 1;
  return Math.floor(parsed);
}
function resolveCursorSetParam(raw) {
  if (!raw)
    return 1;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed))
    return 1;
  return Math.max(1, Math.floor(parsed));
}
function applyTextToCursorHint(cursor, text, cols, trackHighBytesAsPrintable) {
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    if (code === 13) {
      cursor.col = 1;
      continue;
    }
    if (code === 10) {
      cursor.row += 1;
      continue;
    }
    if (code === 8) {
      cursor.col = Math.max(1, cursor.col - 1);
      continue;
    }
    if (code === 9) {
      const nextTabCol = Math.floor((cursor.col - 1) / 8) * 8 + 9;
      cursor.col = cols > 0 ? Math.min(cols, nextTabCol) : nextTabCol;
      continue;
    }
    if (code < 32 || code === 127)
      continue;
    if (!trackHighBytesAsPrintable && code >= 128)
      continue;
    cursor.col += 1;
    if (cols > 0 && cursor.col > cols) {
      cursor.col = 1;
      cursor.row += 1;
    }
  }
}
function applyCsiToCursorHint(cursor, seq, cols) {
  if (!seq.startsWith("\x1B[") || seq.length < 3)
    return;
  const final = seq[seq.length - 1];
  const body = seq.slice(2, -1);
  if (!/^[0-9;]*$/.test(body))
    return;
  const parts = body.length ? body.split(";") : [];
  switch (final) {
    case "A": {
      cursor.row = Math.max(1, cursor.row - resolveCursorStepParam(parts[0]));
      return;
    }
    case "B": {
      cursor.row += resolveCursorStepParam(parts[0]);
      return;
    }
    case "C": {
      const next = cursor.col + resolveCursorStepParam(parts[0]);
      cursor.col = cols > 0 ? Math.min(cols, next) : next;
      return;
    }
    case "D": {
      cursor.col = Math.max(1, cursor.col - resolveCursorStepParam(parts[0]));
      return;
    }
    case "E": {
      cursor.row += resolveCursorStepParam(parts[0]);
      cursor.col = 1;
      return;
    }
    case "F": {
      cursor.row = Math.max(1, cursor.row - resolveCursorStepParam(parts[0]));
      cursor.col = 1;
      return;
    }
    case "G": {
      const next = resolveCursorSetParam(parts[0]);
      cursor.col = cols > 0 ? Math.min(cols, next) : next;
      return;
    }
    case "H":
    case "f": {
      cursor.row = resolveCursorSetParam(parts[0]);
      const next = resolveCursorSetParam(parts[1]);
      cursor.col = cols > 0 ? Math.min(cols, next) : next;
      return;
    }
    case "d": {
      cursor.row = resolveCursorSetParam(parts[0]);
      return;
    }
  }
}

class OutputFilter {
  remainder = "";
  getCursorPosition;
  sendReply;
  mouse;
  altScreen = false;
  bracketedPaste = false;
  focusReporting = false;
  synchronizedOutput = false;
  windowOpHandler;
  getWindowMetrics;
  clipboardWrite;
  clipboardRead;
  getDefaultColors;
  desktopNotificationHandler;
  promptState = createPromptState();
  cursorHint = null;
  constructor(options) {
    this.getCursorPosition = options.getCursorPosition;
    this.sendReply = options.sendReply;
    this.mouse = options.mouse;
    this.getDefaultColors = options.getDefaultColors;
    this.clipboardWrite = options.onClipboardWrite;
    this.clipboardRead = options.onClipboardRead;
    this.windowOpHandler = options.onWindowOp;
    this.getWindowMetrics = options.getWindowMetrics;
    this.desktopNotificationHandler = options.onDesktopNotification;
  }
  setCursorProvider(fn) {
    this.getCursorPosition = fn;
    this.cursorHint = null;
  }
  setReplySink(fn) {
    this.sendReply = fn;
  }
  setWindowOpHandler(fn) {
    this.windowOpHandler = fn;
  }
  isAltScreen() {
    return this.altScreen;
  }
  isBracketedPaste() {
    return this.bracketedPaste;
  }
  isFocusReporting() {
    return this.focusReporting;
  }
  isSynchronizedOutput() {
    return this.synchronizedOutput;
  }
  isPromptClickEventsEnabled() {
    return isPromptClickEventsEnabled(this.promptState, this.altScreen);
  }
  encodePromptClickEvent(cell) {
    if (!this.isPromptClickEventsEnabled())
      return "";
    const row = Math.max(1, Math.floor(cell.row) + 1);
    const col = Math.max(1, Math.floor(cell.col) + 1);
    return `\x1B[<0;${col};${row}M`;
  }
  observeOsc(seq) {
    observeOscPromptState(this.promptState, seq);
  }
  handleOsc(seq) {
    return handleOscSequence(seq, {
      sendReply: this.sendReply,
      getDefaultColors: this.getDefaultColors,
      onClipboardWrite: this.clipboardWrite,
      onClipboardRead: this.clipboardRead,
      onDesktopNotification: this.desktopNotificationHandler
    });
  }
  handleModeSeq(seq) {
    const modeState = {
      bracketedPaste: this.bracketedPaste,
      focusReporting: this.focusReporting,
      synchronizedOutput: this.synchronizedOutput
    };
    const handled = applyTrackedPrivateModes(seq, modeState);
    this.bracketedPaste = modeState.bracketedPaste;
    this.focusReporting = modeState.focusReporting;
    this.synchronizedOutput = modeState.synchronizedOutput;
    return handled;
  }
  handleWindowOp(seq) {
    return handleWindowOpSequence(seq, {
      sendReply: this.sendReply,
      getWindowMetrics: this.getWindowMetrics,
      onWindowOp: this.windowOpHandler
    });
  }
  filterInternal(output, trackHighBytesAsPrintable) {
    if (!output)
      return output;
    let data = this.remainder + output;
    this.remainder = "";
    const metricCols = this.getWindowMetrics?.()?.cols;
    const colsHint = Number.isFinite(metricCols) && Number(metricCols) > 1 ? Math.floor(Number(metricCols)) : 0;
    let trackedCursor = this.cursorHint ?? normalizeCursorPosition(this.getCursorPosition()) ?? { row: 1, col: 1 };
    const getReplyCursor = () => trackedCursor;
    let result = "";
    let i = 0;
    while (i < data.length) {
      const ch = data[i];
      if (ch !== "\x1B") {
        result += ch;
        applyTextToCursorHint(trackedCursor, ch, colsHint, trackHighBytesAsPrintable);
        i += 1;
        continue;
      }
      if (i + 1 >= data.length) {
        this.remainder = data.slice(i);
        break;
      }
      if (data[i + 1] === "]") {
        let j2 = i + 2;
        let terminatorLen = 0;
        while (j2 < data.length) {
          const code = data.charCodeAt(j2);
          if (code === 7) {
            terminatorLen = 1;
            break;
          }
          if (code === 27 && j2 + 1 < data.length && data[j2 + 1] === "\\") {
            terminatorLen = 2;
            break;
          }
          j2 += 1;
        }
        if (!terminatorLen) {
          this.remainder = data.slice(i);
          break;
        }
        const seq2 = data.slice(i, j2);
        this.observeOsc(seq2);
        if (!this.handleOsc(seq2)) {
          result += data.slice(i, j2 + terminatorLen);
        }
        i = j2 + terminatorLen;
        continue;
      }
      if (data[i + 1] !== "[") {
        result += ch;
        i += 1;
        continue;
      }
      let j = i + 2;
      while (j < data.length) {
        const code = data.charCodeAt(j);
        if (code >= 64 && code <= 126)
          break;
        j += 1;
      }
      if (j >= data.length) {
        this.remainder = data.slice(i);
        break;
      }
      const seq = data.slice(i, j + 1);
      this.altScreen = deriveAltScreen(seq, this.altScreen);
      const mouseHandled = this.mouse.handleModeSeq(seq);
      const modeHandled = this.handleModeSeq(seq);
      if (mouseHandled || modeHandled) {
        i = j + 1;
        continue;
      }
      if (seq.endsWith("t") && this.handleWindowOp(seq)) {
        i = j + 1;
        continue;
      }
      if (!handleCoreCsiSequence(seq, {
        sendReply: this.sendReply,
        getCursorPosition: getReplyCursor
      })) {
        result += seq;
        applyCsiToCursorHint(trackedCursor, seq, colsHint);
      }
      i = j + 1;
    }
    this.cursorHint = { ...trackedCursor };
    return result;
  }
  filter(output) {
    return this.filterInternal(output, true);
  }
  filterBytes(output) {
    if (!output.length)
      return;
    this.filterInternal(mapBytesToControlText(output), false);
  }
}

// src/input/index.ts
function createInputHandler(options = {}) {
  const config = options.config || {};
  const cursorProvider = options.getCursorPosition || (() => ({ row: 1, col: 1 }));
  const replySink = options.sendReply || (() => {
  });
  const positionToCell = options.positionToCell || (() => ({ row: 0, col: 0 }));
  const positionToPixel = options.positionToPixel || null;
  const mouse = new MouseController({
    sendReply: replySink,
    positionToCell,
    positionToPixel: positionToPixel ?? undefined
  });
  const filter = new OutputFilter({
    getCursorPosition: cursorProvider,
    sendReply: replySink,
    mouse,
    getDefaultColors: options.getDefaultColors,
    onClipboardRead: options.onClipboardRead,
    onClipboardWrite: options.onClipboardWrite,
    onWindowOp: options.onWindowOp,
    getWindowMetrics: options.getWindowMetrics,
    onDesktopNotification: options.onDesktopNotification
  });
  return {
    sequences,
    encodeKeyEvent: (event) => encodeKeyEvent(event, config, options.getKittyKeyboardFlags?.() ?? 0),
    encodeBeforeInput,
    mapKeyForPty,
    filterOutput: (output) => filter.filter(output),
    filterOutputBytes: (output) => filter.filterBytes(output),
    setReplySink: (fn) => {
      mouse.setReplySink(fn);
      filter.setReplySink(fn);
    },
    setCursorProvider: (fn) => {
      filter.setCursorProvider(fn);
    },
    setPositionToCell: (fn) => {
      mouse.setPositionToCell(fn);
    },
    setPositionToPixel: (fn) => {
      mouse.setPositionToPixel(fn);
    },
    setWindowOpHandler: (fn) => {
      filter.setWindowOpHandler(fn);
    },
    setMouseMode: (mode) => {
      mouse.setMode(mode);
    },
    getMouseStatus: () => mouse.getStatus(),
    isMouseActive: () => mouse.isActive(),
    isBracketedPaste: () => filter.isBracketedPaste(),
    isFocusReporting: () => filter.isFocusReporting(),
    isAltScreen: () => filter.isAltScreen(),
    isSynchronizedOutput: () => filter.isSynchronizedOutput(),
    isPromptClickEventsEnabled: () => filter.isPromptClickEventsEnabled(),
    encodePromptClickEvent: (cell) => filter.encodePromptClickEvent(cell),
    sendMouseEvent: (kind, event) => mouse.sendMouseEvent(kind, event)
  };
}

// src/fonts/manager/classification.ts
var SYMBOL_FONT_HINTS = [/symbols nerd font/i, /noto sans symbols/i, /apple symbols/i, /symbola/i];
var NERD_SYMBOL_FONT_HINTS = [/symbols nerd font/i, /nerd fonts symbols/i];
var COLOR_EMOJI_FONT_HINTS = [
  /apple color emoji/i,
  /noto color emoji/i,
  /segoe ui emoji/i,
  /twemoji/i
];
var WIDE_FONT_HINTS = [
  /cjk/i,
  /emoji/i,
  /openmoji/i,
  /source han/i,
  /pingfang/i,
  /hiragino/i,
  /yu gothic/i,
  /meiryo/i,
  /yahei/i,
  /ms gothic/i,
  /simhei/i,
  /simsun/i,
  /nanum/i,
  /apple sd gothic/i
];
function isSymbolFont(entry) {
  if (!entry?.label)
    return false;
  const label = String(entry.label).toLowerCase();
  return SYMBOL_FONT_HINTS.some((rule) => rule.test(label));
}
function isNerdSymbolFont(entry) {
  if (!entry?.label)
    return false;
  const label = String(entry.label).toLowerCase();
  return NERD_SYMBOL_FONT_HINTS.some((rule) => rule.test(label));
}
function isColorEmojiFont(entry) {
  if (!entry?.label)
    return false;
  const label = String(entry.label).toLowerCase();
  return COLOR_EMOJI_FONT_HINTS.some((rule) => rule.test(label));
}
function fontMaxCellSpan(entry) {
  if (!entry?.label)
    return 1;
  const label = String(entry.label).toLowerCase();
  for (const rule of WIDE_FONT_HINTS) {
    if (rule.test(label))
      return 2;
  }
  return 1;
}
function fontScaleOverride(entry, overrides = []) {
  if (!entry?.label)
    return 1;
  const label = String(entry.label).toLowerCase();
  for (const rule of overrides) {
    if (rule.match.test(label))
      return rule.scale;
  }
  return 1;
}
function fontRasterScale(entry, fontIndex, maxSymbolAtlasScale, overrides = []) {
  const scale = fontScaleOverride(entry, overrides);
  if (fontIndex > 0 && isSymbolFont(entry))
    return scale * maxSymbolAtlasScale;
  return scale;
}

// src/grid/grid.ts
function fontHeightUnits(font) {
  if (!font)
    return 0;
  const height = font.height;
  if (height !== undefined && Number.isFinite(height) && height > 0)
    return height;
  const asc = font.ascender ?? 0;
  const desc = font.descender ?? 0;
  const fallback = asc - desc;
  if (Number.isFinite(fallback) && fallback > 0)
    return fallback;
  return font.unitsPerEm || font.upem || 1000;
}
function computeCellMetrics(font, config, dpr, shapeCluster) {
  if (!font)
    return null;
  const fontSizePx = Math.max(1, Math.round(config.fontSize * dpr));
  const scale = font.scaleForSize(fontSizePx, config.sizeMode);
  const glyphId = font.glyphIdForChar("M");
  const advanceUnits = glyphId !== undefined && glyphId !== null ? font.advanceWidth(glyphId) : shapeCluster("M").advance;
  const cellW = Math.max(1, Math.round(advanceUnits * scale));
  const lineHeight = fontHeightUnits(font) * scale;
  const cellH = Math.max(1, Math.round(lineHeight));
  const baselineOffset = font.ascender * scale;
  const yPad = Math.max(0, (cellH - lineHeight) * 0.5);
  return { cellW, cellH, fontSizePx, scale, lineHeight, baselineOffset, yPad };
}
function createGridState() {
  return {
    cols: 0,
    rows: 0,
    cellW: 0,
    cellH: 0,
    fontSizePx: 0,
    scale: 1,
    lineHeight: 0,
    baselineOffset: 0,
    yPad: 0
  };
}
function updateGridState(state2, metrics, canvasWidth, canvasHeight) {
  const cols = Math.max(1, Math.floor(canvasWidth / metrics.cellW));
  const rows = Math.max(1, Math.floor(canvasHeight / metrics.cellH));
  if (!Number.isFinite(cols) || !Number.isFinite(rows)) {
    return { changed: false, cols: state2.cols, rows: state2.rows };
  }
  const changed = cols !== state2.cols || rows !== state2.rows || metrics.fontSizePx !== state2.fontSizePx || metrics.cellW !== state2.cellW || metrics.cellH !== state2.cellH;
  Object.assign(state2, metrics, { cols, rows });
  return { changed, cols, rows };
}
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// src/fonts/manager/entries.ts
function createFontEntry(font, label) {
  return {
    font,
    label,
    glyphCache: new Map,
    boundsCache: new Map,
    colorGlyphTexts: new Map,
    glyphIds: new Set,
    atlas: null,
    fontSizePx: 0,
    atlasScale: 1,
    advanceUnits: 0,
    constraintSignature: "",
    atlasHinting: false,
    atlasHintTarget: "auto"
  };
}
function resetFontEntry(entry) {
  entry.glyphCache.clear();
  entry.boundsCache.clear();
  entry.colorGlyphTexts.clear();
  entry.glyphIds.clear();
  entry.atlas = null;
  entry.fontSizePx = 0;
  entry.atlasScale = 1;
  entry.advanceUnits = 0;
  entry.constraintSignature = "";
  entry.atlasHinting = false;
  entry.atlasHintTarget = "auto";
}
function createFontManagerState() {
  return {
    font: null,
    fonts: [],
    fontSizePx: 0,
    sizeMode: "height",
    fontPickCache: new Map
  };
}
function fontHasGlyph(font, ch) {
  const glyphId = font.glyphIdForChar(ch);
  return glyphId !== undefined && glyphId !== null && glyphId !== 0;
}
function fontAdvanceUnits(entry, shapeClusterWithFont) {
  if (!entry?.font)
    return 0;
  if (entry.advanceUnits)
    return entry.advanceUnits;
  const glyphId = entry.font.glyphIdForChar("M");
  let advance = 0;
  if (glyphId !== undefined && glyphId !== null) {
    advance = entry.font.advanceWidth(glyphId);
  }
  if (!advance) {
    advance = shapeClusterWithFont(entry, "M").advance;
  }
  if (!advance) {
    advance = fontHeightUnits(entry.font) || entry.font.unitsPerEm || 1000;
  }
  entry.advanceUnits = advance;
  return advance;
}
function glyphWidthUnits(entry, glyphId) {
  if (!entry?.font || glyphId === undefined || glyphId === null)
    return 0;
  if (!entry.boundsCache)
    entry.boundsCache = new Map;
  if (entry.boundsCache.has(glyphId))
    return entry.boundsCache.get(glyphId);
  const bounds = entry.font.getGlyphBounds(glyphId);
  let width = 0;
  if (bounds) {
    width = bounds.xMax - bounds.xMin;
  }
  if (!Number.isFinite(width) || width <= 0) {
    width = entry.font.advanceWidth(glyphId) || 0;
  }
  entry.boundsCache.set(glyphId, width);
  return width;
}

// src/fonts/nerd-ranges.ts
var NERD_SYMBOL_RANGES = [
  [57344, 57354],
  [57504, 57507],
  [57520, 57544],
  [57546, 57546],
  [57548, 57559],
  [57856, 58025],
  [58112, 58339],
  [58874, 59063],
  [59136, 59631],
  [60000, 60446],
  [60672, 62207],
  [60928, 60939],
  [62208, 62337],
  [62464, 62771],
  [983041, 989936]
];
function isNerdSymbolCodepoint(cp) {
  for (const [start, end] of NERD_SYMBOL_RANGES) {
    if (cp >= start && cp <= end)
      return true;
  }
  return false;
}

// src/fonts/manager/picker.ts
function isLikelyEmojiCodepoint(cp) {
  if (cp >= 127462 && cp <= 127487)
    return true;
  if (cp >= 127744 && cp <= 129791)
    return true;
  return false;
}
function isVariationSelectorCodepoint(cp) {
  if (cp >= 65024 && cp <= 65039)
    return true;
  if (cp >= 917760 && cp <= 917999)
    return true;
  return false;
}
function isCombiningMarkCodepoint(cp) {
  if (cp >= 768 && cp <= 879)
    return true;
  if (cp >= 6832 && cp <= 6911)
    return true;
  if (cp >= 7616 && cp <= 7679)
    return true;
  if (cp >= 8400 && cp <= 8447)
    return true;
  if (cp >= 65056 && cp <= 65071)
    return true;
  return false;
}
function isCoverageIgnorableCodepoint(cp) {
  if (cp === 8204 || cp === 8205)
    return true;
  if (isVariationSelectorCodepoint(cp))
    return true;
  if (isCombiningMarkCodepoint(cp))
    return true;
  if (cp >= 917536 && cp <= 917631)
    return true;
  return false;
}
function resolvePresentationPreference(text, chars) {
  if (text.includes("️"))
    return "emoji";
  if (text.includes("︎"))
    return "text";
  if (text.includes("‍"))
    return "emoji";
  for (const ch of chars) {
    const cp = ch.codePointAt(0) ?? 0;
    if (isLikelyEmojiCodepoint(cp))
      return "emoji";
  }
  return "auto";
}
function pickFontIndexForText(state2, text, expectedSpan) {
  if (!state2.fonts.length)
    return 0;
  const cacheKey = `${expectedSpan}:${text}`;
  const cached = state2.fontPickCache.get(cacheKey);
  if (cached !== undefined)
    return cached;
  const chars = Array.from(text);
  const requiredChars = chars.filter((ch) => {
    const cp = ch.codePointAt(0) ?? 0;
    return !isCoverageIgnorableCodepoint(cp);
  });
  const firstCp = text.codePointAt(0) ?? 0;
  const nerdSymbol = isNerdSymbolCodepoint(firstCp);
  const presentation = resolvePresentationPreference(text, chars);
  const pickFirstMatch = (predicate) => {
    for (let i = 0;i < state2.fonts.length; i += 1) {
      const entry = state2.fonts[i];
      if (!entry?.font)
        continue;
      if (predicate && !predicate(entry))
        continue;
      let ok = true;
      for (const ch of requiredChars) {
        if (!fontHasGlyph(entry.font, ch)) {
          ok = false;
          break;
        }
      }
      if (ok)
        return i;
    }
    return -1;
  };
  const tryIndex = (index) => {
    if (index < 0)
      return null;
    state2.fontPickCache.set(cacheKey, index);
    return index;
  };
  if (nerdSymbol) {
    const symbolIndex = pickFirstMatch((entry) => isNerdSymbolFont(entry) || isSymbolFont(entry));
    const result = tryIndex(symbolIndex);
    if (result !== null)
      return result;
  }
  if (presentation === "emoji") {
    const emojiIndex = pickFirstMatch((entry) => isColorEmojiFont(entry));
    const result = tryIndex(emojiIndex);
    if (result !== null)
      return result;
  } else if (presentation === "text") {
    const textIndex = pickFirstMatch((entry) => !isColorEmojiFont(entry));
    const result = tryIndex(textIndex);
    if (result !== null)
      return result;
  }
  const firstIndex = pickFirstMatch();
  if (firstIndex >= 0) {
    state2.fontPickCache.set(cacheKey, firstIndex);
    return firstIndex;
  }
  state2.fontPickCache.set(cacheKey, 0);
  return 0;
}

// src/fonts/manager/sources.ts
async function tryFetchFontBuffer(url) {
  try {
    const response = await fetch(url);
    if (response.ok)
      return response.arrayBuffer();
  } catch {
  }
  return null;
}
async function tryLocalFontBuffer(matchers) {
  const globalAccess = globalThis;
  const nav = globalAccess.navigator ?? navigator;
  const queryLocalFonts = typeof globalAccess.queryLocalFonts === "function" ? globalAccess.queryLocalFonts.bind(globalAccess) : typeof nav.queryLocalFonts === "function" ? nav.queryLocalFonts.bind(nav) : null;
  if (!queryLocalFonts)
    return null;
  const normalizedMatchers = matchers.map((matcher) => matcher.toLowerCase()).filter(Boolean);
  if (!normalizedMatchers.length)
    return null;
  const queryPermission = nav.permissions?.query;
  if (queryPermission) {
    try {
      const status = await queryPermission({ name: "local-fonts" });
      if (status?.state === "denied")
        return null;
    } catch {
    }
  }
  try {
    const fonts = await queryLocalFonts();
    const match = fonts.find((font) => {
      const name = `${font.family ?? ""} ${font.fullName ?? ""} ${font.postscriptName ?? ""}`.toLowerCase();
      return normalizedMatchers.some((matcher) => name.includes(matcher));
    });
    if (match) {
      const blob = await match.blob();
      return blob.arrayBuffer();
    }
  } catch (err) {
    console.warn("queryLocalFonts failed", err);
  }
  return null;
}
async function loadPrimaryFontBuffer(localMatchers, fallbackUrl, fallbackLocalMatchers) {
  const nerdLocal = await tryLocalFontBuffer(localMatchers);
  if (nerdLocal)
    return nerdLocal;
  const buffer = await tryFetchFontBuffer(fallbackUrl);
  if (buffer)
    return buffer;
  const local = await tryLocalFontBuffer(fallbackLocalMatchers);
  if (local)
    return local;
  throw new Error("Unable to load primary font.");
}
async function loadFallbackFontBuffers(sources) {
  const results = [];
  for (const source of sources) {
    const buffer = await tryFetchFontBuffer(source.url);
    if (buffer) {
      results.push({ name: source.name, buffer });
      continue;
    }
    if (source.matchers && source.matchers.length) {
      const local = await tryLocalFontBuffer(source.matchers);
      if (local)
        results.push({ name: source.name, buffer: local });
    }
  }
  return results;
}

// src/fonts/nerd-constraints.ts
var NERD_CONSTRAINTS = [
  {
    start: 9211,
    end: 9214,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 9776,
    end: 9776,
    constraint: {
      size: "cover",
      height: "icon",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      pad_left: 0.05,
      pad_right: 0.05,
      pad_top: 0.05,
      pad_bottom: 0.05
    }
  },
  {
    start: 9829,
    end: 9829,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 9889,
    end: 9889,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 10092,
    end: 10093,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7142857142857143,
      relative_height: 0.8910614525139665,
      relative_x: 0.1428571428571428,
      relative_y: 0.0349162011173184,
      pad_top: 0.15,
      pad_bottom: 0.15
    }
  },
  {
    start: 10094,
    end: 10095,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9885714285714285,
      relative_height: 0.8910614525139665,
      relative_x: 0.0057142857142857,
      relative_y: 0.0125698324022346,
      pad_top: 0.15,
      pad_bottom: 0.15
    }
  },
  {
    start: 10096,
    end: 10097,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      pad_top: 0.15,
      pad_bottom: 0.15
    }
  },
  {
    start: 11096,
    end: 11096,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 57344,
    end: 57354,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 57504,
    end: 57507,
    constraint: {
      size: "fit_cover1",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 57520,
    end: 57520,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.03,
      pad_right: -0.03,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57521,
    end: 57521,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57522,
    end: 57522,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.03,
      pad_right: -0.03,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57523,
    end: 57523,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57524,
    end: 57524,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.03,
      pad_right: -0.03,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.59
    }
  },
  {
    start: 57525,
    end: 57525,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      max_xy_ratio: 0.5
    }
  },
  {
    start: 57526,
    end: 57526,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.03,
      pad_right: -0.03,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.59
    }
  },
  {
    start: 57527,
    end: 57527,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      max_xy_ratio: 0.5
    }
  },
  {
    start: 57528,
    end: 57528,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57529,
    end: 57529,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1"
    }
  },
  {
    start: 57530,
    end: 57530,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57531,
    end: 57531,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1"
    }
  },
  {
    start: 57532,
    end: 57532,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57533,
    end: 57533,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1"
    }
  },
  {
    start: 57534,
    end: 57534,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57535,
    end: 57535,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1"
    }
  },
  {
    start: 57536,
    end: 57536,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57537,
    end: 57537,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1"
    }
  },
  {
    start: 57538,
    end: 57538,
    constraint: {
      size: "stretch",
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57539,
    end: 57539,
    constraint: {
      size: "stretch",
      align_horizontal: "end",
      align_vertical: "center1"
    }
  },
  {
    start: 57540,
    end: 57540,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015,
      max_xy_ratio: 0.86
    }
  },
  {
    start: 57541,
    end: 57541,
    constraint: {
      size: "stretch",
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015,
      max_xy_ratio: 0.86
    }
  },
  {
    start: 57542,
    end: 57542,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015,
      max_xy_ratio: 0.78
    }
  },
  {
    start: 57543,
    end: 57543,
    constraint: {
      size: "stretch",
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015,
      max_xy_ratio: 0.78
    }
  },
  {
    start: 57544,
    end: 57544,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57546,
    end: 57546,
    constraint: {
      size: "stretch",
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 57548,
    end: 57548,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.01,
      pad_right: -0.01,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.85
    }
  },
  {
    start: 57549,
    end: 57549,
    constraint: {
      size: "stretch",
      align_horizontal: "start",
      align_vertical: "center1",
      max_xy_ratio: 0.865
    }
  },
  {
    start: 57550,
    end: 57550,
    constraint: {
      size: "fit_cover1",
      align_horizontal: "start",
      align_vertical: "center1"
    }
  },
  {
    start: 57551,
    end: 57551,
    constraint: {
      size: "fit_cover1",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 57552,
    end: 57553,
    constraint: {
      size: "fit_cover1",
      align_horizontal: "start",
      align_vertical: "center1"
    }
  },
  {
    start: 57554,
    end: 57554,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.01,
      pad_right: -0.01,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57556,
    end: 57556,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.01,
      pad_right: -0.01,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57558,
    end: 57558,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57559,
    end: 57559,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005,
      max_xy_ratio: 0.7
    }
  },
  {
    start: 57856,
    end: 58025,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58112,
    end: 58112,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8984375,
      relative_y: 0.0986328125
    }
  },
  {
    start: 58113,
    end: 58113,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8798828125,
      relative_y: 0.1171875
    }
  },
  {
    start: 58114,
    end: 58114,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7646484375,
      relative_y: 0.2314453125
    }
  },
  {
    start: 58115,
    end: 58115,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.87890625,
      relative_y: 0.1171875
    }
  },
  {
    start: 58116,
    end: 58116,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9755859375,
      relative_y: 0.0244140625
    }
  },
  {
    start: 58117,
    end: 58117,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.99609375,
      relative_y: 0.001953125
    }
  },
  {
    start: 58118,
    end: 58118,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.986328125,
      relative_y: 0.009765625
    }
  },
  {
    start: 58119,
    end: 58119,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9951171875,
      relative_y: 0.00390625
    }
  },
  {
    start: 58120,
    end: 58120,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.978515625,
      relative_y: 0.01953125
    }
  },
  {
    start: 58121,
    end: 58121,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9736328125,
      relative_y: 0.021484375
    }
  },
  {
    start: 58122,
    end: 58122,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.96484375,
      relative_y: 0.0302734375
    }
  },
  {
    start: 58123,
    end: 58123,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.84375,
      relative_y: 0.1513671875
    }
  },
  {
    start: 58124,
    end: 58124,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.802734375,
      relative_y: 0.18359375
    }
  },
  {
    start: 58125,
    end: 58125,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.775390625,
      relative_y: 0.1083984375
    }
  },
  {
    start: 58126,
    end: 58126,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9833984375,
      relative_y: 0.0166015625
    }
  },
  {
    start: 58127,
    end: 58127,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9716796875,
      relative_y: 0.0263671875
    }
  },
  {
    start: 58128,
    end: 58128,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.662109375,
      relative_y: 0.0986328125
    }
  },
  {
    start: 58129,
    end: 58129,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.642578125,
      relative_y: 0.1171875
    }
  },
  {
    start: 58130,
    end: 58130,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5322265625,
      relative_y: 0.2314453125
    }
  },
  {
    start: 58131,
    end: 58131,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6416015625,
      relative_y: 0.1181640625
    }
  },
  {
    start: 58132,
    end: 58132,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.73828125,
      relative_y: 0.01953125
    }
  },
  {
    start: 58133,
    end: 58133,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6787109375,
      relative_y: 0.1357421875
    }
  },
  {
    start: 58134,
    end: 58134,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.748046875,
      relative_y: 0.009765625
    }
  },
  {
    start: 58135,
    end: 58135,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7529296875,
      relative_y: 0.0048828125
    }
  },
  {
    start: 58136,
    end: 58136,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7314453125,
      relative_y: 0.0263671875
    }
  },
  {
    start: 58137,
    end: 58137,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.740234375,
      relative_y: 0.01953125
    }
  },
  {
    start: 58138,
    end: 58138,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7294921875,
      relative_y: 0.0283203125
    }
  },
  {
    start: 58139,
    end: 58139,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.607421875,
      relative_y: 0.150390625
    }
  },
  {
    start: 58140,
    end: 58140,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.736328125,
      relative_y: 0.0224609375
    }
  },
  {
    start: 58141,
    end: 58141,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.74609375,
      relative_y: 0.0126953125
    }
  },
  {
    start: 58142,
    end: 58142,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.267578125,
      relative_y: 0.3310546875
    }
  },
  {
    start: 58143,
    end: 58143,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.736328125,
      relative_y: 0.0986328125
    }
  },
  {
    start: 58144,
    end: 58144,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7177734375,
      relative_y: 0.1171875
    }
  },
  {
    start: 58145,
    end: 58145,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.80859375,
      relative_y: 0.025390625
    }
  },
  {
    start: 58146,
    end: 58146,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7509765625,
      relative_y: 0.083984375
    }
  },
  {
    start: 58147,
    end: 58147,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.828125,
      relative_y: 0.009765625
    }
  },
  {
    start: 58148,
    end: 58148,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8349609375
    }
  },
  {
    start: 58149,
    end: 58149,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8154296875,
      relative_y: 0.021484375
    }
  },
  {
    start: 58150,
    end: 58150,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.814453125,
      relative_y: 0.01953125
    }
  },
  {
    start: 58151,
    end: 58151,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8076171875,
      relative_y: 0.02734375
    }
  },
  {
    start: 58152,
    end: 58152,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6845703125,
      relative_y: 0.150390625
    }
  },
  {
    start: 58153,
    end: 58153,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8173828125,
      relative_y: 0.017578125
    }
  },
  {
    start: 58154,
    end: 58154,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.810546875,
      relative_y: 0.0263671875
    }
  },
  {
    start: 58155,
    end: 58155,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.517578125,
      relative_y: 0.2421875
    }
  },
  {
    start: 58156,
    end: 58156,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.69921875,
      relative_y: 0.1005859375
    }
  },
  {
    start: 58157,
    end: 58157,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6787109375,
      relative_y: 0.1201171875
    }
  },
  {
    start: 58158,
    end: 58158,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5654296875,
      relative_y: 0.232421875
    }
  },
  {
    start: 58159,
    end: 58159,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.771484375,
      relative_y: 0.02734375
    }
  },
  {
    start: 58160,
    end: 58160,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.71484375,
      relative_y: 0.0830078125
    }
  },
  {
    start: 58161,
    end: 58161,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7919921875,
      relative_y: 0.009765625
    }
  },
  {
    start: 58162,
    end: 58162,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.787109375,
      relative_y: 0.0126953125
    }
  },
  {
    start: 58163,
    end: 58163,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.771484375,
      relative_y: 0.0263671875
    }
  },
  {
    start: 58164,
    end: 58164,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.77734375,
      relative_y: 0.01953125
    }
  },
  {
    start: 58165,
    end: 58165,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.771484375,
      relative_y: 0.0283203125
    }
  },
  {
    start: 58166,
    end: 58166,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.650390625,
      relative_y: 0.150390625
    }
  },
  {
    start: 58167,
    end: 58167,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.775390625,
      relative_y: 0.0234375
    }
  },
  {
    start: 58168,
    end: 58168,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.779296875,
      relative_y: 0.0185546875
    }
  },
  {
    start: 58169,
    end: 58169,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8445945945945946
    }
  },
  {
    start: 58170,
    end: 58170,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5283203125,
      relative_y: 0.232421875
    }
  },
  {
    start: 58171,
    end: 58171,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.544921875,
      relative_y: 0.21484375
    }
  },
  {
    start: 58172,
    end: 58173,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.52734375,
      relative_y: 0.232421875
    }
  },
  {
    start: 58174,
    end: 58174,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.3293918918918919,
      relative_y: 0.6706081081081081
    }
  },
  {
    start: 58175,
    end: 58175,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.52,
      relative_y: 0.2707692307692308
    }
  },
  {
    start: 58176,
    end: 58176,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8307692307692308,
      relative_y: 0.0861538461538462
    }
  },
  {
    start: 58177,
    end: 58177,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8327702702702703,
      relative_y: 0.0050675675675676
    }
  },
  {
    start: 58178,
    end: 58179,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58180,
    end: 58180,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5307692307692308,
      relative_y: 0.2092307692307692
    }
  },
  {
    start: 58181,
    end: 58181,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5332112630208333,
      relative_y: 0.2040934244791667
    }
  },
  {
    start: 58182,
    end: 58182,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58183,
    end: 58183,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8307692307692308,
      relative_y: 0.1246153846153846
    }
  },
  {
    start: 58184,
    end: 58184,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58185,
    end: 58185,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5307967032967034,
      relative_y: 0.2615384615384616
    }
  },
  {
    start: 58186,
    end: 58187,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58188,
    end: 58188,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8659995118379302,
      relative_y: 0.1340004881620698
    }
  },
  {
    start: 58189,
    end: 58189,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9890163534293386,
      relative_y: 0.0002440810349036
    }
  },
  {
    start: 58190,
    end: 58190,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58191,
    end: 58191,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5751953125,
      relative_y: 0.1142578125
    }
  },
  {
    start: 58192,
    end: 58192,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58193,
    end: 58193,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6533203125,
      relative_y: 0.1328125
    }
  },
  {
    start: 58194,
    end: 58194,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5215384615384615,
      relative_y: 0.2846153846153846
    }
  },
  {
    start: 58195,
    end: 58195,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8308012820512821,
      relative_y: 0.1230448717948718
    }
  },
  {
    start: 58196,
    end: 58198,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9935233160621761,
      relative_y: 0.0025906735751295
    }
  },
  {
    start: 58199,
    end: 58199,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9961139896373057
    }
  },
  {
    start: 58200,
    end: 58201,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9935233160621761,
      relative_y: 0.0025906735751295
    }
  },
  {
    start: 58202,
    end: 58202,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9935233160621761,
      relative_y: 0.0012953367875648
    }
  },
  {
    start: 58203,
    end: 58203,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9987046632124352,
      relative_y: 0.0012953367875648
    }
  },
  {
    start: 58204,
    end: 58205,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58206,
    end: 58206,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7294921875,
      relative_y: 0.0283203125
    }
  },
  {
    start: 58207,
    end: 58207,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.96484375,
      relative_y: 0.0302734375
    }
  },
  {
    start: 58208,
    end: 58208,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.76953125,
      relative_y: 0.0302734375
    }
  },
  {
    start: 58209,
    end: 58209,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8076171875,
      relative_y: 0.02734375
    }
  },
  {
    start: 58210,
    end: 58210,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.990234375,
      relative_y: 0.009765625
    }
  },
  {
    start: 58211,
    end: 58211,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7900390625,
      relative_y: 0.009765625
    }
  },
  {
    start: 58212,
    end: 58212,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8251953125,
      relative_y: 0.009765625
    }
  },
  {
    start: 58213,
    end: 58213,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9833984375,
      relative_y: 0.0166015625
    }
  },
  {
    start: 58214,
    end: 58214,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.783203125,
      relative_y: 0.0166015625
    }
  },
  {
    start: 58215,
    end: 58215,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8173828125,
      relative_y: 0.017578125
    }
  },
  {
    start: 58216,
    end: 58216,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58217,
    end: 58217,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.490234375,
      relative_y: 0.2548828125
    }
  },
  {
    start: 58218,
    end: 58218,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58219,
    end: 58219,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9333658774713205,
      relative_y: 0.0266048328044911
    }
  },
  {
    start: 58220,
    end: 58220,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7076171875,
      relative_y: 0.1083984375
    }
  },
  {
    start: 58221,
    end: 58221,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8427734375,
      relative_y: 0.0625
    }
  },
  {
    start: 58222,
    end: 58222,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7529721467391304,
      relative_y: 0.0956606657608696
    }
  },
  {
    start: 58223,
    end: 58223,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.68359375,
      relative_y: 0.125
    }
  },
  {
    start: 58224,
    end: 58224,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8642578125,
      relative_y: 0.0625
    }
  },
  {
    start: 58225,
    end: 58225,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6103515625,
      relative_y: 0.193359375
    }
  },
  {
    start: 58226,
    end: 58226,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.794921875,
      relative_y: 0.0576171875
    }
  },
  {
    start: 58227,
    end: 58227,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.865234375,
      relative_y: 0.005859375
    }
  },
  {
    start: 58228,
    end: 58228,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.3154296875,
      relative_y: 0.2861328125
    }
  },
  {
    start: 58229,
    end: 58229,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.67724609375,
      relative_y: 0.13037109375
    }
  },
  {
    start: 58230,
    end: 58230,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.69921875,
      relative_y: 0.1337890625
    }
  },
  {
    start: 58231,
    end: 58231,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7314453125,
      relative_y: 0.1552734375
    }
  },
  {
    start: 58232,
    end: 58232,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7314453125,
      relative_y: 0.154296875
    }
  },
  {
    start: 58233,
    end: 58233,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5751953125,
      relative_y: 0.1826171875
    }
  },
  {
    start: 58234,
    end: 58234,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5263671875,
      relative_y: 0.228515625
    }
  },
  {
    start: 58235,
    end: 58235,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5751953125,
      relative_y: 0.18359375
    }
  },
  {
    start: 58236,
    end: 58236,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58237,
    end: 58237,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.900390625,
      relative_y: 0.095703125
    }
  },
  {
    start: 58238,
    end: 58238,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6015625,
      relative_y: 0.232421875
    }
  },
  {
    start: 58239,
    end: 58239,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.52,
      relative_y: 0.2784615384615385
    }
  },
  {
    start: 58240,
    end: 58240,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.52,
      relative_y: 0.2630769230769231
    }
  },
  {
    start: 58241,
    end: 58253,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58254,
    end: 58257,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4990253411306043,
      relative_height: 0.9987012987012988,
      relative_x: 0.4996751137102014
    }
  },
  {
    start: 58258,
    end: 58259,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4996751137102014,
      relative_height: 0.9987012987012988,
      relative_x: 0.4990253411306043
    }
  },
  {
    start: 58260,
    end: 58260,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4990253411306043,
      relative_height: 0.9987012987012988,
      relative_x: 0.4996751137102014
    }
  },
  {
    start: 58261,
    end: 58261,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5471085120207927,
      relative_height: 0.9987012987012988,
      relative_x: 0.451591942820013
    }
  },
  {
    start: 58262,
    end: 58262,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.594541910331384,
      relative_height: 0.9987012987012988,
      relative_x: 0.4041585445094217
    }
  },
  {
    start: 58263,
    end: 58263,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6426250812215725,
      relative_x: 0.3573749187784275
    }
  },
  {
    start: 58264,
    end: 58264,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6900584795321637,
      relative_x: 0.3099415204678362
    }
  },
  {
    start: 58265,
    end: 58265,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7381416504223521,
      relative_x: 0.2618583495776478
    }
  },
  {
    start: 58266,
    end: 58266,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7855750487329435,
      relative_x: 0.2144249512670565
    }
  },
  {
    start: 58267,
    end: 58267,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9987004548408057,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58268,
    end: 58268,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8323586744639376,
      relative_height: 0.9935064935064936
    }
  },
  {
    start: 58269,
    end: 58269,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7855750487329435,
      relative_height: 0.9948051948051948
    }
  },
  {
    start: 58270,
    end: 58270,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7381416504223521,
      relative_height: 0.9961038961038962
    }
  },
  {
    start: 58271,
    end: 58271,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6907082521117609,
      relative_height: 0.9961038961038962
    }
  },
  {
    start: 58272,
    end: 58272,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6426250812215725,
      relative_height: 0.9961038961038962
    }
  },
  {
    start: 58273,
    end: 58273,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.594541910331384,
      relative_height: 0.9974025974025974
    }
  },
  {
    start: 58274,
    end: 58275,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4990253411306043,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58276,
    end: 58276,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4996751137102014,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58277,
    end: 58277,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4990253411306043,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58278,
    end: 58278,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4996751137102014,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58279,
    end: 58280,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4990253411306043,
      relative_height: 0.9987012987012988
    }
  },
  {
    start: 58281,
    end: 58281,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9961139896373057
    }
  },
  {
    start: 58282,
    end: 58282,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.990234375,
      relative_y: 0.0078125
    }
  },
  {
    start: 58283,
    end: 58283,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7900390625,
      relative_y: 0.005859375
    }
  },
  {
    start: 58284,
    end: 58284,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8251953125,
      relative_y: 0.0078125
    }
  },
  {
    start: 58285,
    end: 58285,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.751953125,
      relative_y: 0.0068359375
    }
  },
  {
    start: 58286,
    end: 58286,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.615234375,
      relative_y: 0.232421875
    }
  },
  {
    start: 58287,
    end: 58287,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9986072423398329,
      relative_y: 0.0013927576601671
    }
  },
  {
    start: 58288,
    end: 58290,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9958217270194986,
      relative_y: 0.0041782729805014
    }
  },
  {
    start: 58291,
    end: 58291,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9986072423398329,
      relative_y: 0.0013927576601671
    }
  },
  {
    start: 58292,
    end: 58292,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58293,
    end: 58299,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9986072423398329,
      relative_y: 0.0013927576601671
    }
  },
  {
    start: 58300,
    end: 58304,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58305,
    end: 58305,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6590187942396876,
      relative_y: 0.1349768123016842
    }
  },
  {
    start: 58306,
    end: 58306,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7939956065413717
    }
  },
  {
    start: 58307,
    end: 58339,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 58874,
    end: 59064,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 59136,
    end: 59631,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60000,
    end: 60000,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60001,
    end: 60001,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7513020833333334,
      relative_height: 0.9291573452647278,
      relative_x: 0.0846354166666667,
      relative_y: 0.0708426547352722
    }
  },
  {
    start: 60002,
    end: 60028,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60029,
    end: 60029,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8394854586129754,
      relative_height: 0.8751387347391787,
      relative_x: 0.0917225950782998,
      relative_y: 0.0416204217536071
    }
  },
  {
    start: 60030,
    end: 60040,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60042,
    end: 60044,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60047,
    end: 60056,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60057,
    end: 60057,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9395973154362416,
      relative_height: 0.4778024417314096,
      relative_x: 0.0302013422818792,
      relative_y: 0.2269700332963374
    }
  },
  {
    start: 60058,
    end: 60058,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.767337807606264,
      relative_height: 0.8523862375138734,
      relative_x: 0.1526845637583893,
      relative_y: 0.0754716981132075
    }
  },
  {
    start: 60059,
    end: 60059,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8590604026845637,
      relative_height: 0.7613762486126526,
      relative_x: 0.0721476510067114,
      relative_y: 0.0871254162042175
    }
  },
  {
    start: 60060,
    end: 60060,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8590604026845637,
      relative_height: 0.7574916759156493,
      relative_x: 0.0721476510067114,
      relative_y: 0.0832408435072142
    }
  },
  {
    start: 60061,
    end: 60061,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4082774049217002,
      relative_height: 0.5077691453940066,
      relative_x: 0.2863534675615212,
      relative_y: 0.2763596004439512
    }
  },
  {
    start: 60062,
    end: 60063,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.511744966442953,
      relative_height: 0.4051054384017758,
      relative_x: 0.2136465324384788,
      relative_y: 0.306881243063263
    }
  },
  {
    start: 60064,
    end: 60064,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4082774049217002,
      relative_height: 0.5077691453940066,
      relative_x: 0.2863534675615212,
      relative_y: 0.2763596004439512
    }
  },
  {
    start: 60065,
    end: 60065,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.767337807606264,
      relative_height: 0.8523862375138734,
      relative_x: 0.1526845637583893,
      relative_y: 0.0754716981132075
    }
  },
  {
    start: 60066,
    end: 60066,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8061116965226555,
      relative_height: 0.9438247156716689,
      relative_x: 0.0679662802950474,
      relative_y: 0.0147523709167545
    }
  },
  {
    start: 60067,
    end: 60083,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60084,
    end: 60084,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9945482866043613,
      relative_height: 0.5264797507788161,
      relative_y: 0.2024922118380062
    }
  },
  {
    start: 60085,
    end: 60085,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5264797507788161,
      relative_height: 0.9945482866043613,
      relative_x: 0.2024922118380062,
      relative_y: 0.0054517133956386
    }
  },
  {
    start: 60086,
    end: 60086,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5264797507788161,
      relative_height: 0.9945482866043613,
      relative_x: 0.2710280373831775
    }
  },
  {
    start: 60087,
    end: 60087,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9945482866043613,
      relative_height: 0.5264797507788161,
      relative_x: 0.0054517133956386,
      relative_y: 0.2710280373831775
    }
  },
  {
    start: 60088,
    end: 60103,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60105,
    end: 60105,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60108,
    end: 60115,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60116,
    end: 60117,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7069825436408977,
      relative_x: 0.1483790523690773
    }
  },
  {
    start: 60118,
    end: 60118,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8780760626398211,
      relative_y: 0.0687919463087248
    }
  },
  {
    start: 60119,
    end: 60169,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60171,
    end: 60226,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60227,
    end: 60227,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7335766423357665,
      relative_height: 0.9996188152778837,
      relative_x: 0.1991657977059437,
      relative_y: 0.0003811847221163
    }
  },
  {
    start: 60228,
    end: 60238,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60240,
    end: 60269,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60270,
    end: 60270,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4954604409857328,
      relative_y: 0.2522697795071336
    }
  },
  {
    start: 60271,
    end: 60271,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4973958333333333,
      relative_x: 0.2493489583333333
    }
  },
  {
    start: 60272,
    end: 60272,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4973958333333333,
      relative_height: 0.9961089494163424,
      relative_x: 0.2493489583333333,
      relative_y: 0.0038910505836576
    }
  },
  {
    start: 60273,
    end: 60273,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4954604409857328,
      relative_y: 0.2522697795071336
    }
  },
  {
    start: 60274,
    end: 60297,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60298,
    end: 60298,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.3468834688346883,
      relative_height: 0.335361578525641,
      relative_x: 0.2642276422764228,
      relative_y: 0.3313050881410256
    }
  },
  {
    start: 60299,
    end: 60313,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60314,
    end: 60314,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8740779768177028,
      relative_height: 0.9438247156716689,
      relative_x: 0.0679662802950474,
      relative_y: 0.0147523709167545
    }
  },
  {
    start: 60315,
    end: 60372,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60373,
    end: 60373,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9322210636079249,
      relative_height: 0.9318897917604415,
      relative_y: 0.0681102082395584
    }
  },
  {
    start: 60374,
    end: 60374,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9996446423917936,
      relative_y: 0.0003553576082064
    }
  },
  {
    start: 60375,
    end: 60422,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60423,
    end: 60423,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.3495911047345768,
      relative_height: 0.3355179398148149,
      relative_x: 0.2615335565120357,
      relative_y: 0.3311487268518519
    }
  },
  {
    start: 60424,
    end: 60426,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60427,
    end: 60427,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9327424400417101,
      relative_height: 0.9996188152778837,
      relative_y: 0.0003811847221163
    }
  },
  {
    start: 60428,
    end: 60428,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8008342022940563,
      relative_x: 0.1991657977059437
    }
  },
  {
    start: 60429,
    end: 60446,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60672,
    end: 60927,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 60928,
    end: 60928,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      relative_width: 0.8681172291296625,
      relative_height: 0.8626692456479691,
      relative_x: 0.1314387211367673,
      relative_y: 0.0686653771760155,
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60929,
    end: 60929,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8626692456479691,
      relative_y: 0.0686653771760155,
      pad_left: -0.05,
      pad_right: -0.05,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60930,
    end: 60930,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      relative_width: 0.8685612788632326,
      relative_height: 0.8626692456479691,
      relative_y: 0.0686653771760155,
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60931,
    end: 60931,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "end",
      align_vertical: "center1",
      relative_width: 0.8681172291296625,
      relative_height: 0.8626692456479691,
      relative_x: 0.1314387211367673,
      relative_y: 0.0686653771760155,
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60932,
    end: 60932,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8626692456479691,
      relative_y: 0.0686653771760155,
      pad_left: -0.05,
      pad_right: -0.05,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60933,
    end: 60933,
    constraint: {
      size: "stretch",
      max_constraint_width: 1,
      align_horizontal: "start",
      align_vertical: "center1",
      relative_width: 0.8685612788632326,
      relative_height: 0.8626692456479691,
      relative_y: 0.0686653771760155,
      pad_left: -0.025,
      pad_right: -0.025,
      pad_top: -0.005,
      pad_bottom: -0.005
    }
  },
  {
    start: 60934,
    end: 60934,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7059415911379657,
      relative_height: 0.2234524408656266,
      relative_x: 0.1470292044310171,
      relative_y: 0.7765475591343735,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60935,
    end: 60935,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5,
      relative_height: 0.7498741821841973,
      relative_x: 0.5,
      relative_y: 0.2501258178158027,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60936,
    end: 60936,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6299093655589124,
      relative_height: 0.8535480624056366,
      relative_x: 0.3700906344410876,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60937,
    end: 60937,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4997483643683945,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60938,
    end: 60938,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6299093655589124,
      relative_height: 0.8535480624056366,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60939,
    end: 60939,
    constraint: {
      size: "cover",
      max_constraint_width: 1,
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5,
      relative_height: 0.7498741821841973,
      relative_y: 0.2501258178158027,
      pad_left: 0.015,
      pad_right: 0.015,
      pad_top: 0.015,
      pad_bottom: 0.015
    }
  },
  {
    start: 60940,
    end: 61390,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61440,
    end: 61444,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61445,
    end: 61445,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9999664113932554,
      relative_y: 0.0000335886067446
    }
  },
  {
    start: 61446,
    end: 61477,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61478,
    end: 61479,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.978618435460558,
      relative_y: 0.0103951316192896
    }
  },
  {
    start: 61480,
    end: 61482,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61483,
    end: 61483,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9758052740827267,
      relative_y: 0.0238869355863696
    }
  },
  {
    start: 61484,
    end: 61488,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61489,
    end: 61491,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.998792270531401,
      relative_y: 0.0006038647342995
    }
  },
  {
    start: 61492,
    end: 61492,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61493,
    end: 61493,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9989935587761675,
      relative_y: 0.000402576489533
    }
  },
  {
    start: 61494,
    end: 61507,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61508,
    end: 61508,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9925925925925926
    }
  },
  {
    start: 61509,
    end: 61509,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61510,
    end: 61510,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8751322751322751,
      relative_y: 0.0624338624338624
    }
  },
  {
    start: 61511,
    end: 61511,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61512,
    end: 61512,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8577706898990622,
      relative_y: 0.0711892586341537
    }
  },
  {
    start: 61513,
    end: 61513,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8579450878868969,
      relative_y: 0.0710148606463189
    }
  },
  {
    start: 61514,
    end: 61514,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8577706898990622,
      relative_y: 0.0711892586341537
    }
  },
  {
    start: 61515,
    end: 61515,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9997041418532618,
      relative_y: 0.0002958581467381
    }
  },
  {
    start: 61516,
    end: 61517,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8572940020656472,
      relative_y: 0.0713404035569438
    }
  },
  {
    start: 61518,
    end: 61518,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8577706898990622,
      relative_y: 0.0711892586341537
    }
  },
  {
    start: 61519,
    end: 61519,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7138835298072554,
      relative_y: 0.14334792953172
    }
  },
  {
    start: 61520,
    end: 61520,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8579450878868969,
      relative_y: 0.0710148606463189
    }
  },
  {
    start: 61521,
    end: 61521,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8577706898990622,
      relative_y: 0.0711892586341537
    }
  },
  {
    start: 61522,
    end: 61522,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.999974809179535
    }
  },
  {
    start: 61523,
    end: 61535,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61536,
    end: 61537,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.856797583081571,
      relative_y: 0.0719033232628399
    }
  },
  {
    start: 61538,
    end: 61538,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61539,
    end: 61539,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9987915407854985,
      relative_y: 0.0006042296072508
    }
  },
  {
    start: 61540,
    end: 61558,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61559,
    end: 61559,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5700483091787439,
      relative_y: 0.286231884057971
    }
  },
  {
    start: 61560,
    end: 61560,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5700483091787439,
      relative_y: 0.143719806763285
    }
  },
  {
    start: 61561,
    end: 61565,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61566,
    end: 61566,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4989429175475687,
      relative_y: 0.2505285412262157
    }
  },
  {
    start: 61567,
    end: 61576,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61577,
    end: 61577,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9998488512696494,
      relative_y: 0.0001511487303507
    }
  },
  {
    start: 61578,
    end: 61603,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61604,
    end: 61605,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7502645502645503,
      relative_y: 0.1248677248677249
    }
  },
  {
    start: 61606,
    end: 61654,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61655,
    end: 61655,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4281400966183575,
      relative_y: 0.2053140096618357
    }
  },
  {
    start: 61656,
    end: 61656,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.4281400966183575,
      relative_y: 0.3472222222222222
    }
  },
  {
    start: 61657,
    end: 61657,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7140772371750631,
      relative_y: 0.1333462732919255
    }
  },
  {
    start: 61658,
    end: 61658,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7140396210163651,
      relative_y: 0.1333838894506235
    }
  },
  {
    start: 61659,
    end: 61659,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61660,
    end: 61660,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1"
    }
  },
  {
    start: 61661,
    end: 61661,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      relative_height: 0.427536231884058,
      relative_y: 0.001207729468599
    }
  },
  {
    start: 61662,
    end: 61662,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      relative_height: 0.428743961352657,
      relative_y: 0.571256038647343
    }
  },
  {
    start: 61663,
    end: 61695,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61696,
    end: 61697,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8573155985489722,
      relative_y: 0.0713422007255139
    }
  },
  {
    start: 61698,
    end: 61698,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9286577992744861,
      relative_y: 0.0713422007255139
    }
  },
  {
    start: 61699,
    end: 61699,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9286577992744861
    }
  },
  {
    start: 61700,
    end: 61701,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8573155985489722,
      relative_y: 0.0713422007255139
    }
  },
  {
    start: 61702,
    end: 61703,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.5,
      relative_y: 0.2853688029020556
    }
  },
  {
    start: 61704,
    end: 61743,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61744,
    end: 61744,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9998602571268865
    }
  },
  {
    start: 61745,
    end: 61760,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61761,
    end: 61761,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.2593984962406015,
      relative_y: 0.3696741854636592
    }
  },
  {
    start: 61762,
    end: 61778,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61779,
    end: 61780,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8751322751322751,
      relative_y: 0.0624338624338624
    }
  },
  {
    start: 61781,
    end: 61781,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61782,
    end: 61782,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8752505446623093,
      relative_y: 0.0623155929038282
    }
  },
  {
    start: 61783,
    end: 61783,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8756468797564688,
      relative_y: 0.0624338624338624
    }
  },
  {
    start: 61784,
    end: 61784,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8751322751322751,
      relative_y: 0.0624338624338624
    }
  },
  {
    start: 61785,
    end: 61785,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.8756067947646895,
      relative_y: 0.0623492063492063
    }
  },
  {
    start: 61786,
    end: 61812,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61813,
    end: 61813,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9989423585404548,
      relative_y: 0.0005288207297726
    }
  },
  {
    start: 61814,
    end: 61814,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61815,
    end: 61816,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6250661025912215,
      relative_y: 0.1877313590692755
    }
  },
  {
    start: 61817,
    end: 61825,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61826,
    end: 61826,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9998046921689268
    }
  },
  {
    start: 61827,
    end: 61984,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61985,
    end: 61985,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9994854643684076
    }
  },
  {
    start: 61986,
    end: 61986,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.874681988394363,
      relative_y: 0.0624017379870223
    }
  },
  {
    start: 61987,
    end: 61987,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 61988,
    end: 61990,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9994854643684076
    }
  },
  {
    start: 61991,
    end: 61991,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.874681988394363,
      relative_y: 0.0624017379870223
    }
  },
  {
    start: 61992,
    end: 61992,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9994854643684076
    }
  },
  {
    start: 61993,
    end: 61993,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9370837263813853,
      relative_y: 0.0624017379870223
    }
  },
  {
    start: 61994,
    end: 61994,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9994854643684076
    }
  },
  {
    start: 61995,
    end: 61995,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6874767744332962,
      relative_y: 0.1560043449675557
    }
  },
  {
    start: 61996,
    end: 61996,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9994854643684076
    }
  },
  {
    start: 61997,
    end: 61997,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6874767744332962,
      relative_y: 0.1560043449675557
    }
  },
  {
    start: 61998,
    end: 62036,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62037,
    end: 62038,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9993997599039616
    }
  },
  {
    start: 62039,
    end: 62039,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7810124049619848,
      relative_y: 0.0935945806894186
    }
  },
  {
    start: 62040,
    end: 62040,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.7498142113988452,
      relative_y: 0.1247927742525582
    }
  },
  {
    start: 62041,
    end: 62041,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62042,
    end: 62042,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9993997599039616
    }
  },
  {
    start: 62043,
    end: 62043,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.9975006099019084
    }
  },
  {
    start: 62044,
    end: 62337,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62464,
    end: 62485,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62486,
    end: 62486,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_height: 0.6090604026845637,
      relative_y: 0.2119686800894855
    }
  },
  {
    start: 62487,
    end: 62499,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62500,
    end: 62500,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.501953125,
      relative_height: 0.575503355704698,
      relative_x: 0.248046875,
      relative_y: 0.2108501118568233
    }
  },
  {
    start: 62501,
    end: 62512,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62513,
    end: 62513,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6240234375,
      relative_height: 0.7695749440715883,
      relative_x: 0.203125,
      relative_y: 0.1420581655480984
    }
  },
  {
    start: 62514,
    end: 62514,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.671875,
      relative_height: 0.714765100671141,
      relative_x: 0.1875,
      relative_y: 0.1610738255033557
    }
  },
  {
    start: 62515,
    end: 62515,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6240234375,
      relative_height: 0.7695749440715883,
      relative_x: 0.2041015625,
      relative_y: 0.0883668903803132
    }
  },
  {
    start: 62516,
    end: 62516,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.671875,
      relative_height: 0.714765100671141,
      relative_x: 0.140625,
      relative_y: 0.1599552572706935
    }
  },
  {
    start: 62517,
    end: 62519,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62520,
    end: 62520,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.24365234375,
      relative_height: 0.4560546875,
      relative_x: 0.38134765625,
      relative_y: 0.27197265625
    }
  },
  {
    start: 62521,
    end: 62525,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62526,
    end: 62526,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5029296875,
      relative_height: 0.575503355704698,
      relative_x: 0.25,
      relative_y: 0.2136465324384788
    }
  },
  {
    start: 62527,
    end: 62530,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62531,
    end: 62531,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.75,
      relative_x: 0.125
    }
  },
  {
    start: 62532,
    end: 62533,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5,
      relative_height: 0.5,
      relative_x: 0.25,
      relative_y: 0.25
    }
  },
  {
    start: 62534,
    end: 62537,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62538,
    end: 62538,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.24365234375,
      relative_height: 0.4560546875,
      relative_x: 0.375,
      relative_y: 0.27197265625
    }
  },
  {
    start: 62539,
    end: 62539,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4560546875,
      relative_height: 0.24365234375,
      relative_x: 0.27197265625,
      relative_y: 0.31884765625
    }
  },
  {
    start: 62540,
    end: 62555,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62556,
    end: 62556,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.501953125,
      relative_height: 0.5749440715883669,
      relative_x: 0.248046875,
      relative_y: 0.2114093959731544
    }
  },
  {
    start: 62557,
    end: 62559,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62560,
    end: 62560,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.359375,
      relative_height: 0.6240234375,
      relative_x: 0.375,
      relative_y: 0.1884765625
    }
  },
  {
    start: 62561,
    end: 62561,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.6237816764132553,
      relative_height: 0.9988851727982163,
      relative_x: 0.1881091617933723
    }
  },
  {
    start: 62562,
    end: 62566,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62567,
    end: 62567,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.56396484375,
      relative_height: 0.56494140625,
      relative_x: 0.21875,
      relative_y: 0.2177734375
    }
  },
  {
    start: 62568,
    end: 62571,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62572,
    end: 62572,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.50390625,
      relative_height: 0.5771812080536913,
      relative_x: 0.2490234375,
      relative_y: 0.20917225950783
    }
  },
  {
    start: 62573,
    end: 62575,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62576,
    end: 62576,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.99267578125,
      relative_height: 0.26904296875,
      relative_y: 0.6865234375
    }
  },
  {
    start: 62577,
    end: 62581,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62582,
    end: 62582,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8732325694783033,
      relative_x: 0.0633837152608484
    }
  },
  {
    start: 62583,
    end: 62585,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62586,
    end: 62586,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.584307992202729,
      relative_height: 0.9509476031215162,
      relative_x: 0.2066276803118908,
      relative_y: 0.0234113712374582
    }
  },
  {
    start: 62587,
    end: 62588,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.625,
      relative_height: 0.359375,
      relative_x: 0.1875,
      relative_y: 0.328125
    }
  },
  {
    start: 62589,
    end: 62589,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.359375,
      relative_height: 0.6240234375,
      relative_x: 0.265625,
      relative_y: 0.1875
    }
  },
  {
    start: 62590,
    end: 62590,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.4560546875,
      relative_height: 0.24365234375,
      relative_x: 0.27197265625,
      relative_y: 0.375
    }
  },
  {
    start: 62591,
    end: 62602,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62603,
    end: 62603,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.71875,
      relative_height: 0.09375,
      relative_x: 0.125,
      relative_y: 0.46875
    }
  },
  {
    start: 62604,
    end: 62610,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62611,
    end: 62611,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8313840155945419,
      relative_height: 0.9509476031215162,
      relative_x: 0.084307992202729,
      relative_y: 0.0234113712374582
    }
  },
  {
    start: 62612,
    end: 62617,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62618,
    end: 62618,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.8727450024378351,
      relative_x: 0.0633837152608484
    }
  },
  {
    start: 62619,
    end: 62658,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62659,
    end: 62659,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5,
      relative_height: 0.5,
      relative_x: 0.25,
      relative_y: 0.25
    }
  },
  {
    start: 62660,
    end: 62702,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62703,
    end: 62703,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7142857142857143,
      relative_x: 0.1428571428571428
    }
  },
  {
    start: 62704,
    end: 62704,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9642857142857143,
      relative_height: 0.7407407407407407,
      relative_y: 0.1111111111111111
    }
  },
  {
    start: 62705,
    end: 62705,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.9642857142857143,
      relative_height: 0.7407407407407407,
      relative_x: 0.0357142857142857,
      relative_y: 0.1111111111111111
    }
  },
  {
    start: 62706,
    end: 62706,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.7142857142857143,
      relative_x: 0.1428571428571428
    }
  },
  {
    start: 62707,
    end: 62748,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 62749,
    end: 62749,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1",
      relative_width: 0.5,
      relative_height: 0.5,
      relative_x: 0.25,
      relative_y: 0.25
    }
  },
  {
    start: 62750,
    end: 62771,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  },
  {
    start: 983041,
    end: 989936,
    constraint: {
      size: "fit_cover1",
      height: "icon",
      align_horizontal: "center1",
      align_vertical: "center1"
    }
  }
];
function getNerdConstraint(cp) {
  let lo = 0;
  let hi = NERD_CONSTRAINTS.length - 1;
  while (lo <= hi) {
    const mid = lo + hi >> 1;
    const entry = NERD_CONSTRAINTS[mid];
    if (cp < entry.start) {
      hi = mid - 1;
    } else if (cp > entry.end) {
      lo = mid + 1;
    } else {
      return entry.constraint;
    }
  }
  return null;
}
// src/pty/pty.ts
function createPtyConnection() {
  return {
    socket: null,
    status: "idle",
    url: "",
    connectId: 0
  };
}
function setConnectionStatus(state2, status) {
  state2.status = status;
}
function connectPty(state2, options, callbacks) {
  if (state2.status === "connecting" || state2.status === "connected" || state2.status === "closing") {
    return false;
  }
  const url = options.url?.trim?.() ?? "";
  if (!url)
    return false;
  const ws = new WebSocket(url);
  const connectId = state2.connectId + 1;
  state2.connectId = connectId;
  state2.url = url;
  state2.socket = ws;
  setConnectionStatus(state2, "connecting");
  ws.binaryType = "arraybuffer";
  let disconnectedNotified = false;
  const notifyDisconnected = () => {
    if (disconnectedNotified)
      return;
    disconnectedNotified = true;
    callbacks.onDisconnect?.();
  };
  const clearCurrentSocket = () => {
    if (state2.connectId !== connectId)
      return;
    if (state2.socket === ws) {
      state2.socket = null;
    }
    setConnectionStatus(state2, "idle");
  };
  ws.addEventListener("open", () => {
    if (state2.connectId !== connectId) {
      try {
        ws.close();
      } catch {
      }
      return;
    }
    if (state2.socket !== ws)
      return;
    setConnectionStatus(state2, "connected");
    callbacks.onConnect?.();
    if (Number.isFinite(options.cols) && Number.isFinite(options.rows)) {
      const cols = Math.max(0, Number(options.cols));
      const rows = Math.max(0, Number(options.rows));
      sendPtyResize(state2, cols, rows);
    }
  });
  ws.addEventListener("close", () => {
    clearCurrentSocket();
    notifyDisconnected();
  });
  ws.addEventListener("error", () => {
    clearCurrentSocket();
    notifyDisconnected();
  });
  ws.addEventListener("message", (event) => {
    if (state2.connectId !== connectId || state2.socket !== ws)
      return;
    const payload = event.data;
    if (payload instanceof ArrayBuffer) {
      const bytes = new Uint8Array(payload);
      if (bytes.length)
        callbacks.onData?.(bytes);
      return;
    }
    if (payload instanceof Blob) {
      payload.arrayBuffer().then((buf) => {
        if (state2.connectId !== connectId || state2.socket !== ws)
          return;
        const bytes = new Uint8Array(buf);
        if (bytes.length)
          callbacks.onData?.(bytes);
      });
      return;
    }
    if (typeof payload === "string") {
      if (handleServerMessage(payload, callbacks))
        return;
      callbacks.onData?.(payload);
    }
  });
  return true;
}
function disconnectPty(state2) {
  const socket = state2.socket;
  if (!socket) {
    setConnectionStatus(state2, "idle");
    return;
  }
  setConnectionStatus(state2, "closing");
  if (socket) {
    try {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      } else {
        if (state2.socket === socket) {
          state2.socket = null;
        }
        setConnectionStatus(state2, "idle");
      }
    } catch {
      if (state2.socket === socket) {
        state2.socket = null;
      }
      setConnectionStatus(state2, "idle");
    }
  }
}
function sendPtyInput(state2, data) {
  if (!state2.socket || state2.socket.readyState !== WebSocket.OPEN)
    return false;
  const message = { type: "input", data };
  state2.socket.send(JSON.stringify(message));
  return true;
}
function sendPtyResize(state2, cols, rows, meta) {
  if (!state2.socket || state2.socket.readyState !== WebSocket.OPEN)
    return false;
  const message = { type: "resize", cols, rows };
  if (meta) {
    if (Number.isFinite(meta.widthPx))
      message.widthPx = Math.max(0, Number(meta.widthPx));
    if (Number.isFinite(meta.heightPx))
      message.heightPx = Math.max(0, Number(meta.heightPx));
    if (Number.isFinite(meta.cellW))
      message.cellW = Math.max(0, Number(meta.cellW));
    if (Number.isFinite(meta.cellH))
      message.cellH = Math.max(0, Number(meta.cellH));
  }
  state2.socket.send(JSON.stringify(message));
  return true;
}
function handleServerMessage(payload, callbacks) {
  try {
    const msg = JSON.parse(payload);
    if (msg.type === "status") {
      callbacks.onStatus?.(msg.shell ?? "");
      return true;
    }
    if (msg.type === "error") {
      callbacks.onError?.(msg.message ?? "", msg.errors);
      return true;
    }
    if (msg.type === "exit") {
      callbacks.onExit?.(msg.code ?? 0);
      return true;
    }
  } catch {
  }
  return false;
}
function isPtyConnected(state2) {
  return state2.status === "connected" && state2.socket?.readyState === WebSocket.OPEN;
}
function createWebSocketPtyTransport(state2 = createPtyConnection()) {
  return {
    connect: (options) => {
      const url = options.url?.trim?.() ?? "";
      if (!url) {
        throw new Error("PTY URL is required for WebSocket transport");
      }
      const connected = connectPty(state2, options, options.callbacks);
      if (!connected && state2.status !== "connected") {
        throw new Error(`PTY connection is busy (${state2.status})`);
      }
    },
    disconnect: () => {
      disconnectPty(state2);
    },
    sendInput: (data) => {
      return sendPtyInput(state2, data);
    },
    resize: (cols, rows, meta) => {
      return sendPtyResize(state2, cols, rows, meta);
    },
    isConnected: () => {
      return isPtyConnected(state2);
    },
    destroy: () => {
      disconnectPty(state2);
    }
  };
}
// src/ime/ime.ts
var DEFAULT_IME_FONT_FAMILY = '"JetBrains Mono","Fira Code","SFMono-Regular","Menlo","Consolas","Liberation Mono",monospace';
function createImeState() {
  return {
    composing: false,
    preedit: "",
    selectionStart: 0,
    selectionEnd: 0
  };
}
function setPreedit(state2, text, imeInput) {
  state2.preedit = text || "";
  if (imeInput) {
    imeInput.value = state2.preedit;
  }
}
function clearPreedit(state2, imeInput) {
  state2.preedit = "";
  state2.selectionStart = 0;
  state2.selectionEnd = 0;
  if (imeInput) {
    imeInput.value = "";
  }
}
function startComposition(state2, data, imeInput) {
  state2.composing = true;
  setPreedit(state2, data || imeInput?.value || "");
}
function updateComposition(state2, data, imeInput) {
  setPreedit(state2, data || imeInput?.value || "");
}
function endComposition(state2) {
  state2.composing = false;
  const text = state2.preedit;
  state2.preedit = "";
  state2.selectionStart = 0;
  state2.selectionEnd = 0;
  return text;
}
function syncImeSelection(state2, imeInput) {
  if (!imeInput)
    return;
  const start = imeInput.selectionStart ?? 0;
  const end = imeInput.selectionEnd ?? start;
  state2.selectionStart = Math.max(0, Math.min(start, imeInput.value.length));
  state2.selectionEnd = Math.max(state2.selectionStart, Math.min(end, imeInput.value.length));
}
function updateImePosition(imeInput, cursor, cellW, cellH, dpr, canvasRect) {
  if (!imeInput || !cursor)
    return;
  const scale = dpr || 1;
  const x = canvasRect.left + cursor.col * (cellW / scale);
  const y = canvasRect.top + cursor.row * (cellH / scale);
  imeInput.style.transform = "none";
  imeInput.style.left = `${Math.round(x)}px`;
  imeInput.style.top = `${Math.round(y)}px`;
}
function resolveImeAnchor(cursor, cols, rows) {
  if (!cursor)
    return null;
  const maxCol = Math.max(0, Math.floor(cols) - 1);
  const maxRow = Math.max(0, Math.floor(rows) - 1);
  let col = Number.isFinite(cursor.col) ? Math.floor(cursor.col) : 0;
  if (cursor.wideTail && col > 0) {
    col -= 1;
  }
  const row = Number.isFinite(cursor.row) ? Math.floor(cursor.row) : 0;
  return {
    col: Math.max(0, Math.min(maxCol, col)),
    row: Math.max(0, Math.min(maxRow, row))
  };
}
function syncImeInputTypography(imeInput, fontSizePt, fontFamily = DEFAULT_IME_FONT_FAMILY) {
  if (!imeInput)
    return;
  const size = Number.isFinite(fontSizePt) ? Math.max(10, Math.min(64, Math.round(fontSizePt))) : 18;
  imeInput.style.fontSize = `${size}pt`;
  imeInput.style.lineHeight = `${size}pt`;
  imeInput.style.fontFamily = fontFamily;
  imeInput.style.fontWeight = "400";
  imeInput.style.letterSpacing = "0";
}
var PREEDIT_BG = [0.16, 0.16, 0.2, 1];
var PREEDIT_ACTIVE_BG = [0.3, 0.32, 0.42, 1];
var PREEDIT_FG = [0.95, 0.95, 0.98, 1];
var PREEDIT_UL = [0.7, 0.7, 0.8, 0.9];
var PREEDIT_CARET = [0.95, 0.95, 0.98, 1];
// src/selection/core.ts
function createSelectionState() {
  return {
    active: false,
    dragging: false,
    anchor: null,
    focus: null
  };
}
function clearSelection(state2) {
  state2.active = false;
  state2.dragging = false;
  state2.anchor = null;
  state2.focus = null;
}
function startSelection(state2, cell) {
  state2.active = true;
  state2.dragging = true;
  state2.anchor = cell;
  state2.focus = cell;
}
function updateSelection(state2, cell) {
  if (!state2.dragging)
    return;
  state2.focus = cell;
}
function endSelection(state2, cell) {
  if (!state2.dragging)
    return false;
  state2.dragging = false;
  state2.focus = cell;
  if (state2.anchor && state2.focus && state2.anchor.row === state2.focus.row && state2.anchor.col === state2.focus.col) {
    clearSelection(state2);
    return false;
  }
  return true;
}
function selectionForRow(state2, row, cols) {
  if (!state2.active || !state2.anchor || !state2.focus)
    return null;
  const a = state2.anchor;
  const f = state2.focus;
  const forward = f.row > a.row || f.row === a.row && f.col >= a.col;
  const start = forward ? a : f;
  const end = forward ? f : a;
  if (start.row === end.row && row === start.row) {
    const left = Math.min(start.col, end.col);
    const right = Math.max(start.col, end.col) + 1;
    return { start: clamp(left, 0, cols), end: clamp(right, 0, cols) };
  }
  if (row < start.row || row > end.row)
    return null;
  if (row === start.row) {
    return { start: clamp(start.col, 0, cols), end: cols };
  }
  if (row === end.row) {
    return { start: 0, end: clamp(end.col + 1, 0, cols) };
  }
  return { start: 0, end: cols };
}
function normalizeSelectionCell(cell, rows, cols, wideFlags) {
  if (!cell)
    return cell;
  if (!rows || !cols)
    return cell;
  const row = clamp(cell.row, 0, rows - 1);
  const col = clamp(cell.col, 0, cols - 1);
  if (!wideFlags)
    return { row, col };
  const idx = row * cols + col;
  const flag = wideFlags[idx] ?? 0;
  if (flag === 2) {
    const left = col > 0 ? col - 1 : col;
    return { row, col: left };
  }
  if (flag === 3 && row > 0) {
    const prevRow = row - 1;
    for (let c = cols - 1;c >= 0; c -= 1) {
      const f = wideFlags[prevRow * cols + c] ?? 0;
      if (f !== 2 && f !== 3)
        return { row: prevRow, col: c };
    }
  }
  return { row, col };
}
function positionToCell(clientX, clientY, canvasRect, dpr, cellW, cellH, cols, rows, canvasWidth, canvasHeight) {
  const safeDpr = dpr || 1;
  const safeCols = cols || 1;
  const safeRows = rows || 1;
  const safeCellW = cellW || 1;
  const safeCellH = cellH || 1;
  const scaleX = canvasRect.width > 0 ? (canvasWidth && canvasWidth > 0 ? canvasWidth : safeDpr) / canvasRect.width : safeDpr;
  const scaleY = canvasRect.height > 0 ? (canvasHeight && canvasHeight > 0 ? canvasHeight : safeDpr) / canvasRect.height : safeDpr;
  const x = (clientX - canvasRect.left) * scaleX;
  const y = (clientY - canvasRect.top) * scaleY;
  const col = clamp(Math.floor(x / safeCellW), 0, safeCols - 1);
  const row = clamp(Math.floor(y / safeCellH), 0, safeRows - 1);
  return { row, col };
}

// src/selection/text.ts
function getSelectionText(state2, rows, cols, getCellText) {
  if (!state2.active || !state2.anchor || !state2.focus)
    return "";
  if (!rows || !cols)
    return "";
  const a = state2.anchor;
  const f = state2.focus;
  const forward = f.row > a.row || f.row === a.row && f.col >= a.col;
  const startRow = forward ? a.row : f.row;
  const endRow = forward ? f.row : a.row;
  const lines = [];
  for (let row = startRow;row <= endRow; row += 1) {
    const range = selectionForRow(state2, row, cols);
    if (!range)
      continue;
    let line = "";
    for (let col = range.start;col < range.end; col += 1) {
      const idx = row * cols + col;
      line += getCellText(idx);
    }
    line = line.replace(/[ \t]+$/g, "");
    lines.push(line);
  }
  return lines.join(`
`);
}

// src/selection/clipboard.ts
async function copyToClipboard(text) {
  if (!text)
    return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const temp = document.createElement("textarea");
    temp.value = text;
    temp.style.position = "fixed";
    temp.style.opacity = "0";
    document.body.appendChild(temp);
    temp.select();
    try {
      document.execCommand("copy");
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(temp);
    }
  }
}
async function pasteFromClipboard() {
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}
// src/theme/ghostty.ts
var BASIC_COLOR_NAMES = {
  black: { r: 0, g: 0, b: 0 },
  white: { r: 255, g: 255, b: 255 },
  red: { r: 255, g: 0, b: 0 },
  green: { r: 0, g: 255, b: 0 },
  blue: { r: 0, g: 0, b: 255 },
  yellow: { r: 255, g: 255, b: 0 },
  cyan: { r: 0, g: 255, b: 255 },
  magenta: { r: 255, g: 0, b: 255 },
  gray: { r: 128, g: 128, b: 128 },
  grey: { r: 128, g: 128, b: 128 }
};
function clampByte(value) {
  if (!Number.isFinite(value))
    return 0;
  return Math.max(0, Math.min(255, Math.round(value)));
}
function parseHexColor(value) {
  const trimmed = value.trim();
  if (!trimmed)
    return null;
  const raw = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  const hex = raw.toLowerCase();
  if (!/^[0-9a-f]+$/i.test(hex))
    return null;
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 4) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    const a = parseInt(hex[3] + hex[3], 16);
    return { r, g, b, a };
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }
  if (hex.length === 8) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = parseInt(hex.slice(6, 8), 16);
    return { r, g, b, a };
  }
  return null;
}
function parseRgbColor(value) {
  const match = value.trim().match(/^rgba?\(([^)]+)\)$/i);
  if (!match)
    return null;
  const parts = match[1].split(",").map((p) => p.trim());
  if (parts.length < 3)
    return null;
  const rgb = parts.slice(0, 3).map((p) => {
    if (p.endsWith("%")) {
      const v = parseFloat(p.slice(0, -1));
      return clampByte(v / 100 * 255);
    }
    return clampByte(Number.parseFloat(p));
  });
  let a;
  if (parts.length >= 4) {
    const alphaRaw = parts[3];
    if (alphaRaw.endsWith("%")) {
      const v = parseFloat(alphaRaw.slice(0, -1));
      a = clampByte(v / 100 * 255);
    } else {
      const v = Number.parseFloat(alphaRaw);
      a = clampByte(v <= 1 ? v * 255 : v);
    }
  }
  return { r: rgb[0], g: rgb[1], b: rgb[2], a };
}
function parseNamedColor(value) {
  const key = value.trim().toLowerCase();
  if (!key)
    return null;
  if (BASIC_COLOR_NAMES[key])
    return BASIC_COLOR_NAMES[key];
  if (typeof document !== "undefined") {
    const el = document.createElement("span");
    el.style.color = key;
    document.body.appendChild(el);
    const computed = getComputedStyle(el).color;
    el.remove();
    const parsed = parseRgbColor(computed);
    if (parsed)
      return parsed;
  }
  return null;
}
function parseGhosttyColor(value) {
  return parseHexColor(value) || parseRgbColor(value) || parseNamedColor(value);
}
function parseGhosttyTerminalColor(value) {
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "cell-foreground" || trimmed === "cell-background") {
    return trimmed;
  }
  return parseGhosttyColor(value);
}
function colorToFloats(color, alphaOverride) {
  const a = alphaOverride ?? color.a ?? 255;
  return [color.r / 255, color.g / 255, color.b / 255, a / 255];
}
function colorToRgbU32(color) {
  return (color.r & 255) << 16 | (color.g & 255) << 8 | color.b & 255;
}
function parseGhosttyTheme(text2) {
  const raw = {};
  const palette = new Array(256).fill(undefined);
  const colors = { palette };
  for (const line of text2.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed)
      continue;
    if (trimmed.startsWith("#") || trimmed.startsWith(";") || trimmed.startsWith("//"))
      continue;
    const eq = trimmed.indexOf("=");
    if (eq === -1)
      continue;
    const key = trimmed.slice(0, eq).trim();
    let value = trimmed.slice(eq + 1).trim();
    if (!key)
      continue;
    if (value.startsWith('"') && value.endsWith('"') || value.startsWith("'") && value.endsWith("'")) {
      value = value.slice(1, -1).trim();
    }
    raw[key] = value;
    if (key === "palette") {
      const match = value.match(/^(\d+)\s*=\s*(.+)$/);
      if (!match)
        continue;
      const idx = Number.parseInt(match[1], 10);
      if (!Number.isFinite(idx) || idx < 0 || idx > 255)
        continue;
      const color = parseGhosttyColor(match[2]);
      if (color)
        palette[idx] = color;
      continue;
    }
    switch (key) {
      case "background":
        colors.background = parseGhosttyColor(value) ?? undefined;
        break;
      case "foreground":
        colors.foreground = parseGhosttyColor(value) ?? undefined;
        break;
      case "cursor-color":
        colors.cursor = parseGhosttyColor(value) ?? undefined;
        break;
      case "cursor-text":
        colors.cursorText = parseGhosttyColor(value) ?? undefined;
        break;
      case "selection-background":
        colors.selectionBackground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      case "selection-foreground":
        colors.selectionForeground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      case "search-background":
        colors.searchBackground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      case "search-foreground":
        colors.searchForeground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      case "search-selected-background":
        colors.searchSelectedBackground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      case "search-selected-foreground":
        colors.searchSelectedForeground = parseGhosttyTerminalColor(value) ?? undefined;
        break;
      default:
        break;
    }
  }
  return {
    name: raw.name,
    colors,
    raw
  };
}

// src/theme/builtin-themes.ts
var BUILTIN_THEME_NAMES = [
  "0x96f",
  "12-bit Rainbow",
  "3024 Day",
  "3024 Night",
  "Aardvark Blue",
  "Abernathy",
  "Adventure",
  "Adventure Time",
  "Adwaita",
  "Adwaita Dark",
  "Afterglow",
  "Aizen Dark",
  "Aizen Light",
  "Alabaster",
  "Alien Blood",
  "Andromeda",
  "Apple Classic",
  "Apple System Colors",
  "Apple System Colors Light",
  "Arcoiris",
  "Ardoise",
  "Argonaut",
  "Arthur",
  "Atelier Sulphurpool",
  "Atom",
  "Atom One Dark",
  "Atom One Light",
  "Aura",
  "Aurora",
  "Ayu",
  "Ayu Light",
  "Ayu Mirage",
  "Banana Blueberry",
  "Batman",
  "Belafonte Day",
  "Belafonte Night",
  "Birds Of Paradise",
  "Black Metal",
  "Black Metal (Bathory)",
  "Black Metal (Burzum)",
  "Black Metal (Dark Funeral)",
  "Black Metal (Gorgoroth)",
  "Black Metal (Immortal)",
  "Black Metal (Khold)",
  "Black Metal (Marduk)",
  "Black Metal (Mayhem)",
  "Black Metal (Nile)",
  "Black Metal (Venom)",
  "Blazer",
  "Blue Berry Pie",
  "Blue Dolphin",
  "Blue Matrix",
  "Bluloco Dark",
  "Bluloco Light",
  "Borland",
  "Box",
  "branch",
  "Breadog",
  "Breeze",
  "Bright Lights",
  "Broadcast",
  "Brogrammer",
  "Builtin Dark",
  "Builtin Light",
  "Builtin Pastel Dark",
  "Builtin Tango Dark",
  "Builtin Tango Light",
  "C64",
  "Calamity",
  "Carbonfox",
  "Catppuccin Frappe",
  "Catppuccin Latte",
  "Catppuccin Macchiato",
  "Catppuccin Mocha",
  "CGA",
  "Chalk",
  "Chalkboard",
  "Challenger Deep",
  "Chester",
  "Ciapre",
  "Citruszest",
  "CLRS",
  "Cobalt Neon",
  "Cobalt Next",
  "Cobalt Next Dark",
  "Cobalt Next Minimal",
  "Cobalt2",
  "Coffee Theme",
  "Crayon Pony Fish",
  "Cursor Dark",
  "Cutie Pro",
  "Cyberdyne",
  "Cyberpunk",
  "Cyberpunk Scarlet Protocol",
  "Dark Modern",
  "Dark Pastel",
  "Dark+",
  "Darkermatrix",
  "Darkmatrix",
  "Darkside",
  "Dawnfox",
  "Dayfox",
  "Deep",
  "Desert",
  "Detuned",
  "Dimidium",
  "Dimmed Monokai",
  "Django",
  "Django Reborn Again",
  "Django Smooth",
  "Doom One",
  "Doom Peacock",
  "Dot Gov",
  "Dracula",
  "Dracula+",
  "Duckbones",
  "Duotone Dark",
  "Duskfox",
  "Earthsong",
  "Electron Highlighter",
  "Elegant",
  "Elemental",
  "Elementary",
  "Embark",
  "Embers Dark",
  "ENCOM",
  "Espresso",
  "Espresso Libre",
  "Everblush",
  "Everforest Dark Hard",
  "Everforest Light Med",
  "Fahrenheit",
  "Fairyfloss",
  "Farmhouse Dark",
  "Farmhouse Light",
  "Fideloper",
  "Firefly Traditional",
  "Firefox Dev",
  "Firewatch",
  "Fish Tank",
  "Flat",
  "Flatland",
  "Flexoki Dark",
  "Flexoki Light",
  "Floraverse",
  "Forest Blue",
  "Framer",
  "Front End Delight",
  "Fun Forrest",
  "Galaxy",
  "Galizur",
  "Ghostty Default Style Dark",
  "GitHub",
  "GitHub Dark",
  "GitHub Dark Colorblind",
  "GitHub Dark Default",
  "GitHub Dark Dimmed",
  "GitHub Dark High Contrast",
  "GitHub Light Colorblind",
  "GitHub Light Default",
  "GitHub Light High Contrast",
  "GitLab Dark",
  "GitLab Dark Grey",
  "GitLab Light",
  "Glacier",
  "Grape",
  "Grass",
  "Grey Green",
  "Gruber Darker",
  "Gruvbox Dark",
  "Gruvbox Dark Hard",
  "Gruvbox Light",
  "Gruvbox Light Hard",
  "Gruvbox Material",
  "Gruvbox Material Dark",
  "Gruvbox Material Light",
  "Guezwhoz",
  "Hacktober",
  "Hardcore",
  "Harper",
  "Havn Daggry",
  "Havn Skumring",
  "HaX0R Blue",
  "HaX0R Gr33N",
  "HaX0R R3D",
  "Heeler",
  "Highway",
  "Hipster Green",
  "Hivacruz",
  "Homebrew",
  "Hopscotch",
  "Hopscotch.256",
  "Horizon",
  "Horizon Bright",
  "Hot Dog Stand",
  "Hot Dog Stand (Mustard)",
  "Hurtado",
  "Hybrid",
  "IBM 5153 CGA",
  "IBM 5153 CGA (Black)",
  "IC Green PPL",
  "IC Orange PPL",
  "Iceberg Dark",
  "Iceberg Light",
  "Idea",
  "Idle Toes",
  "IR Black",
  "IRIX Console",
  "IRIX Terminal",
  "iTerm2 Dark Background",
  "iTerm2 Default",
  "iTerm2 Light Background",
  "iTerm2 Pastel Dark Background",
  "iTerm2 Smoooooth",
  "iTerm2 Solarized Dark",
  "iTerm2 Solarized Light",
  "iTerm2 Tango Dark",
  "iTerm2 Tango Light",
  "Jackie Brown",
  "Japanesque",
  "Jellybeans",
  "JetBrains Darcula",
  "Jubi",
  "Kanagawa Dragon",
  "Kanagawa Lotus",
  "Kanagawa Wave",
  "Kanagawabones",
  "Kanso Ink",
  "Kanso Mist",
  "Kanso Pearl",
  "Kanso Zen",
  "Kibble",
  "Kitty Default",
  "Kitty Low Contrast",
  "Kolorit",
  "Konsolas",
  "Kurokula",
  "Lab Fox",
  "Laser",
  "Later This Evening",
  "Lavandula",
  "Light Owl",
  "Liquid Carbon",
  "Liquid Carbon Transparent",
  "Lovelace",
  "Man Page",
  "Mariana",
  "Material",
  "Material Dark",
  "Material Darker",
  "Material Design Colors",
  "Material Ocean",
  "Mathias",
  "Matrix",
  "Matte Black",
  "Medallion",
  "Melange Dark",
  "Melange Light",
  "Mellifluous",
  "Mellow",
  "Miasma",
  "Midnight In Mojave",
  "Mirage",
  "Misterioso",
  "Molokai",
  "Mona Lisa",
  "Monokai Classic",
  "Monokai Pro",
  "Monokai Pro Light",
  "Monokai Pro Light Sun",
  "Monokai Pro Machine",
  "Monokai Pro Octagon",
  "Monokai Pro Ristretto",
  "Monokai Pro Spectrum",
  "Monokai Remastered",
  "Monokai Soda",
  "Monokai Vivid",
  "Moonfly",
  "N0Tch2K",
  "Neobones Dark",
  "Neobones Light",
  "Neon",
  "Neopolitan",
  "Neutron",
  "Night Lion V1",
  "Night Lion V2",
  "Night Owl",
  "Night Owlish Light",
  "Nightfox",
  "Niji",
  "No Clown Fiesta",
  "No Clown Fiesta Light",
  "Nocturnal Winter",
  "Nord",
  "Nord Light",
  "Nord Wave",
  "Nordfox",
  "Novel",
  "novmbr",
  "Nvim Dark",
  "Nvim Light",
  "Obsidian",
  "Ocean",
  "Oceanic Material",
  "Oceanic Next",
  "Ollie",
  "One Dark Two",
  "One Double Dark",
  "One Double Light",
  "One Half Dark",
  "One Half Light",
  "Onenord",
  "Onenord Light",
  "Operator Mono Dark",
  "Overnight Slumber",
  "owl",
  "Oxocarbon",
  "Pale Night Hc",
  "Pandora",
  "Paraiso Dark",
  "Paul Millr",
  "Pencil Dark",
  "Pencil Light",
  "Peppermint",
  "Phala Green Dark",
  "Piatto Light",
  "Pnevma",
  "Poimandres",
  "Poimandres Darker",
  "Poimandres Storm",
  "Poimandres White",
  "Popping And Locking",
  "Powershell",
  "Primary",
  "Pro",
  "Pro Light",
  "Purple Rain",
  "Purplepeter",
  "Rapture",
  "Raycast Dark",
  "Raycast Light",
  "Rebecca",
  "Red Alert",
  "Red Planet",
  "Red Sands",
  "Relaxed",
  "Retro",
  "Retro Legends",
  "Rippedcasts",
  "Rose Pine",
  "Rose Pine Dawn",
  "Rose Pine Moon",
  "Rouge 2",
  "Royal",
  "Ryuuko",
  "Sakura",
  "Scarlet Protocol",
  "Sea Shells",
  "Seafoam Pastel",
  "Selenized Black",
  "Selenized Dark",
  "Selenized Light",
  "Seoulbones Dark",
  "Seoulbones Light",
  "Seti",
  "Shades Of Purple",
  "Shaman",
  "Slate",
  "Sleepy Hollow",
  "Smyck",
  "Snazzy",
  "Snazzy Soft",
  "Soft Server",
  "Solarized Darcula",
  "Solarized Dark Higher Contrast",
  "Solarized Dark Patched",
  "Solarized Osaka Night",
  "Sonokai",
  "Spacedust",
  "Spacegray",
  "Spacegray Bright",
  "Spacegray Eighties",
  "Spacegray Eighties Dull",
  "Spiderman",
  "Spring",
  "Square",
  "Squirrelsong Dark",
  "Srcery",
  "Starlight",
  "Sublette",
  "Subliminal",
  "Sugarplum",
  "Sundried",
  "Symfonic",
  "Synthwave",
  "Synthwave Alpha",
  "Synthwave Everything",
  "Tango Adapted",
  "Tango Half Adapted",
  "Tearout",
  "Teerb",
  "Terafox",
  "Terminal Basic",
  "Terminal Basic Dark",
  "Thayer Bright",
  "The Hulk",
  "Tinacious Design Dark",
  "Tinacious Design Light",
  "TokyoNight",
  "TokyoNight Day",
  "TokyoNight Moon",
  "TokyoNight Night",
  "TokyoNight Storm",
  "Tomorrow",
  "Tomorrow Night",
  "Tomorrow Night Blue",
  "Tomorrow Night Bright",
  "Tomorrow Night Burns",
  "Tomorrow Night Eighties",
  "Toy Chest",
  "traffic",
  "Treehouse",
  "Twilight",
  "Ubuntu",
  "Ultra Dark",
  "Ultra Violent",
  "Under The Sea",
  "Unikitty",
  "urban",
  "Urple",
  "Vague",
  "Vaughn",
  "Vercel",
  "Vesper",
  "Vibrant Ink",
  "Vimbones",
  "Violet Dark",
  "Violet Light",
  "Violite",
  "Warm Neon",
  "Wez",
  "Whimsy",
  "Wild Cherry",
  "Wilmersdorf",
  "Wombat",
  "Wryan",
  "Xcode Dark",
  "Xcode Dark hc",
  "Xcode Light",
  "Xcode Light hc",
  "Xcode WWDC",
  "Zenbones",
  "Zenbones Dark",
  "Zenbones Light",
  "Zenburn",
  "Zenburned",
  "Zenwritten Dark",
  "Zenwritten Light"
];
var BUILTIN_THEME_SOURCES = {
  "0x96f": `palette = 0=#262427
palette = 1=#ff666d
palette = 2=#b3e03a
palette = 3=#ffc739
palette = 4=#00cde8
palette = 5=#a392e8
palette = 6=#9deaf6
palette = 7=#fcfcfa
palette = 8=#545452
palette = 9=#ff7e83
palette = 10=#bee55e
palette = 11=#ffd05e
palette = 12=#1bd5eb
palette = 13=#b0a3eb
palette = 14=#acedf8
palette = 15=#fcfcfa
background = #262427
foreground = #fcfcfa
cursor-color = #fcfcfa
cursor-text = #000000
selection-background = #fcfcfa
selection-foreground = #262427
`,
  "12-bit Rainbow": `palette = 0=#000000
palette = 1=#a03050
palette = 2=#40d080
palette = 3=#e09040
palette = 4=#3060b0
palette = 5=#603090
palette = 6=#0090c0
palette = 7=#dbded8
palette = 8=#685656
palette = 9=#c06060
palette = 10=#90d050
palette = 11=#e0d000
palette = 12=#00b0c0
palette = 13=#801070
palette = 14=#20b0c0
palette = 15=#ffffff
background = #040404
foreground = #feffff
cursor-color = #e0d000
cursor-text = #000000
selection-background = #606060
selection-foreground = #ffffff
`,
  "3024 Day": `palette = 0=#090300
palette = 1=#db2d20
palette = 2=#01a252
palette = 3=#caba00
palette = 4=#01a0e4
palette = 5=#a16a94
palette = 6=#8fbece
palette = 7=#a5a2a2
palette = 8=#5c5855
palette = 9=#dbaec3
palette = 10=#3a3432
palette = 11=#4a4543
palette = 12=#807d7c
palette = 13=#bcbbba
palette = 14=#cdab53
palette = 15=#f7f7f7
background = #f7f7f7
foreground = #4a4543
cursor-color = #4a4543
cursor-text = #f7f7f7
selection-background = #a5a2a2
selection-foreground = #4a4543
`,
  "3024 Night": `palette = 0=#090300
palette = 1=#db2d20
palette = 2=#01a252
palette = 3=#fded02
palette = 4=#01a0e4
palette = 5=#a16a94
palette = 6=#b5e4f4
palette = 7=#a5a2a2
palette = 8=#5c5855
palette = 9=#e8bbd0
palette = 10=#47413f
palette = 11=#4a4543
palette = 12=#807d7c
palette = 13=#d6d5d4
palette = 14=#cdab53
palette = 15=#f7f7f7
background = #090300
foreground = #a5a2a2
cursor-color = #a5a2a2
cursor-text = #090300
selection-background = #4a4543
selection-foreground = #a5a2a2
`,
  "Aardvark Blue": `palette = 0=#191919
palette = 1=#aa342e
palette = 2=#4b8c0f
palette = 3=#dbba00
palette = 4=#1370d3
palette = 5=#c43ac3
palette = 6=#008eb0
palette = 7=#bebebe
palette = 8=#525252
palette = 9=#f05b50
palette = 10=#95dc55
palette = 11=#ffe763
palette = 12=#60a4ec
palette = 13=#e26be2
palette = 14=#60b6cb
palette = 15=#f7f7f7
background = #102040
foreground = #dddddd
cursor-color = #007acc
cursor-text = #bfdbfe
selection-background = #bfdbfe
selection-foreground = #000000
`,
  Abernathy: `palette = 0=#000000
palette = 1=#cd0000
palette = 2=#00cd00
palette = 3=#cdcd00
palette = 4=#1093f5
palette = 5=#cd00cd
palette = 6=#00cdcd
palette = 7=#faebd7
palette = 8=#404040
palette = 9=#ff0000
palette = 10=#00ff00
palette = 11=#ffff00
palette = 12=#11b5f6
palette = 13=#ff00ff
palette = 14=#00ffff
palette = 15=#ffffff
background = #111416
foreground = #eeeeec
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #eeeeec
selection-foreground = #333333
`,
  Adventure: `palette = 0=#040404
palette = 1=#d84a33
palette = 2=#5da602
palette = 3=#eebb6e
palette = 4=#417ab3
palette = 5=#e5c499
palette = 6=#bdcfe5
palette = 7=#dbded8
palette = 8=#685656
palette = 9=#d76b42
palette = 10=#99b52c
palette = 11=#ffb670
palette = 12=#97d7ef
palette = 13=#aa7900
palette = 14=#bdcfe5
palette = 15=#e4d5c7
background = #040404
foreground = #feffff
cursor-color = #feffff
cursor-text = #000000
selection-background = #606060
selection-foreground = #ffffff
`,
  "Adventure Time": `palette = 0=#050404
palette = 1=#bd0013
palette = 2=#4ab118
palette = 3=#e7741e
palette = 4=#0f4ac6
palette = 5=#665993
palette = 6=#70a598
palette = 7=#f8dcc0
palette = 8=#4e7cbf
palette = 9=#fc5f5a
palette = 10=#9eff6e
palette = 11=#efc11a
palette = 12=#1997c6
palette = 13=#9b5953
palette = 14=#c8faf4
palette = 15=#f6f5fb
background = #1f1d45
foreground = #f8dcc0
cursor-color = #efbf38
cursor-text = #08080a
selection-background = #706b4e
selection-foreground = #f3d9c4
`,
  Adwaita: `palette = 0=#241f31
palette = 1=#c01c28
palette = 2=#2ec27e
palette = 3=#e8b504
palette = 4=#1e78e4
palette = 5=#9841bb
palette = 6=#0ab9dc
palette = 7=#c0bfbc
palette = 8=#5e5c64
palette = 9=#ed333b
palette = 10=#4ad67c
palette = 11=#d2be36
palette = 12=#51a1ff
palette = 13=#c061cb
palette = 14=#4fd2fd
palette = 15=#f6f5f4
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #c0bfbc
selection-foreground = #000000
`,
  "Adwaita Dark": `palette = 0=#241f31
palette = 1=#c01c28
palette = 2=#2ec27e
palette = 3=#f5c211
palette = 4=#1e78e4
palette = 5=#9841bb
palette = 6=#0ab9dc
palette = 7=#c0bfbc
palette = 8=#5e5c64
palette = 9=#ed333b
palette = 10=#57e389
palette = 11=#f8e45c
palette = 12=#51a1ff
palette = 13=#c061cb
palette = 14=#4fd2fd
palette = 15=#f6f5f4
background = #1d1d20
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #1d1d20
selection-background = #ffffff
selection-foreground = #5e5c64
`,
  Afterglow: `palette = 0=#151515
palette = 1=#ac4142
palette = 2=#7e8e50
palette = 3=#e5b567
palette = 4=#6c99bb
palette = 5=#9f4e85
palette = 6=#7dd6cf
palette = 7=#d0d0d0
palette = 8=#505050
palette = 9=#ac4142
palette = 10=#7e8e50
palette = 11=#e5b567
palette = 12=#6c99bb
palette = 13=#9f4e85
palette = 14=#7dd6cf
palette = 15=#f5f5f5
background = #212121
foreground = #d0d0d0
cursor-color = #d0d0d0
cursor-text = #151515
selection-background = #303030
selection-foreground = #d0d0d0
`,
  "Aizen Dark": `palette = 0=#1a1a1a
palette = 1=#f08898
palette = 2=#a4e09c
palette = 3=#f5dea4
palette = 4=#84b4f8
palette = 5=#c8a2f4
palette = 6=#90dcd0
palette = 7=#d0d6f0
palette = 8=#444444
palette = 9=#f08898
palette = 10=#a4e09c
palette = 11=#f5dea4
palette = 12=#84b4f8
palette = 13=#c8a2f4
palette = 14=#90dcd0
palette = 15=#ffffff
background = #1a1a1a
foreground = #d0d6f0
cursor-color = #f8b080
cursor-text = #1a1a1a
selection-background = #333333
selection-foreground = #d0d6f0
`,
  "Aizen Light": `palette = 0=#f0f2f6
palette = 1=#d00c36
palette = 2=#3e9e28
palette = 3=#dd8c1a
palette = 4=#1c64f2
palette = 5=#8636ec
palette = 6=#159096
palette = 7=#4a4d66
palette = 8=#adb2bc
palette = 9=#d00c36
palette = 10=#3e9e28
palette = 11=#dd8c1a
palette = 12=#1c64f2
palette = 13=#8636ec
palette = 14=#159096
palette = 15=#4a4d66
background = #f0f2f6
foreground = #4a4d66
cursor-color = #fc6008
cursor-text = #f0f2f6
selection-background = #bdc2cc
selection-foreground = #4a4d66
`,
  Alabaster: `palette = 0=#000000
palette = 1=#aa3731
palette = 2=#448c27
palette = 3=#cb9000
palette = 4=#325cc0
palette = 5=#7a3e9d
palette = 6=#0083b2
palette = 7=#b7b7b7
palette = 8=#777777
palette = 9=#f05050
palette = 10=#60cb00
palette = 11=#f2af50
palette = 12=#007acc
palette = 13=#e64ce6
palette = 14=#00aacb
palette = 15=#f7f7f7
background = #f7f7f7
foreground = #000000
cursor-color = #007acc
cursor-text = #bfdbfe
selection-background = #bfdbfe
selection-foreground = #000000
`,
  "Alien Blood": `palette = 0=#112616
palette = 1=#7f2b27
palette = 2=#2f7e25
palette = 3=#717f24
palette = 4=#2f6a7f
palette = 5=#47587f
palette = 6=#327f77
palette = 7=#647d75
palette = 8=#3c4812
palette = 9=#e08009
palette = 10=#18e000
palette = 11=#bde000
palette = 12=#00aae0
palette = 13=#0058e0
palette = 14=#00e0c4
palette = 15=#73fa91
background = #0f1610
foreground = #637d75
cursor-color = #73fa91
cursor-text = #0f1610
selection-background = #1d4125
selection-foreground = #73fa91
`,
  Andromeda: `palette = 0=#000000
palette = 1=#cd3131
palette = 2=#05bc79
palette = 3=#e5e512
palette = 4=#2472c8
palette = 5=#bc3fbc
palette = 6=#0fa8cd
palette = 7=#e5e5e5
palette = 8=#666666
palette = 9=#cd3131
palette = 10=#05bc79
palette = 11=#e5e512
palette = 12=#2472c8
palette = 13=#bc3fbc
palette = 14=#0fa8cd
palette = 15=#e5e5e5
background = #262a33
foreground = #e5e5e5
cursor-color = #f8f8f0
cursor-text = #b5b5a8
selection-background = #5a5c62
selection-foreground = #ece7e7
`,
  "Apple Classic": `palette = 0=#000000
palette = 1=#c91b00
palette = 2=#00c200
palette = 3=#c7c400
palette = 4=#1c3fe1
palette = 5=#ca30c7
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#5ffa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#ff77ff
palette = 14=#60fdff
palette = 15=#ffffff
background = #2c2b2b
foreground = #d5a200
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #6b5b02
selection-foreground = #67e000
`,
  "Apple System Colors": `palette = 0=#1a1a1a
palette = 1=#cc372e
palette = 2=#26a439
palette = 3=#cdac08
palette = 4=#0869cb
palette = 5=#9647bf
palette = 6=#479ec2
palette = 7=#98989d
palette = 8=#464646
palette = 9=#ff453a
palette = 10=#32d74b
palette = 11=#ffd60a
palette = 12=#0a84ff
palette = 13=#bf5af2
palette = 14=#76d6ff
palette = 15=#ffffff
background = #1e1e1e
foreground = #ffffff
cursor-color = #98989d
cursor-text = #ffffff
selection-background = #3f638b
selection-foreground = #ffffff
`,
  "Apple System Colors Light": `palette = 0=#1a1a1a
palette = 1=#cc372e
palette = 2=#26a439
palette = 3=#cdac08
palette = 4=#0869cb
palette = 5=#9647bf
palette = 6=#479ec2
palette = 7=#98989d
palette = 8=#464646
palette = 9=#ff453a
palette = 10=#32d74b
palette = 11=#e5bc00
palette = 12=#0a84ff
palette = 13=#bf5af2
palette = 14=#69c9f2
palette = 15=#ffffff
background = #feffff
foreground = #000000
cursor-color = #98989d
cursor-text = #ffffff
selection-background = #abd8ff
selection-foreground = #000000
`,
  Arcoiris: `palette = 0=#333333
palette = 1=#da2700
palette = 2=#12c258
palette = 3=#ffc656
palette = 4=#518bfc
palette = 5=#e37bd9
palette = 6=#63fad5
palette = 7=#bab2b2
palette = 8=#777777
palette = 9=#ffb9b9
palette = 10=#e3f6aa
palette = 11=#ffddaa
palette = 12=#b3e8f3
palette = 13=#cbbaf9
palette = 14=#bcffc7
palette = 15=#efefef
background = #201f1e
foreground = #eee4d9
cursor-color = #872929
cursor-text = #fffbf2
selection-background = #25524a
selection-foreground = #f3fffd
`,
  Ardoise: `palette = 0=#2c2c2c
palette = 1=#d3322d
palette = 2=#588b35
palette = 3=#fca93a
palette = 4=#2465c2
palette = 5=#7332b4
palette = 6=#64e1b8
palette = 7=#f7f7f7
palette = 8=#535353
palette = 9=#fa5852
palette = 10=#8dc252
palette = 11=#ffea51
palette = 12=#6ab5f8
palette = 13=#be68ca
palette = 14=#89ffdb
palette = 15=#fefefe
background = #1e1e1e
foreground = #eaeaea
cursor-color = #f7f7f7
cursor-text = #000000
selection-background = #46515e
selection-foreground = #f1f3f5
`,
  Argonaut: `palette = 0=#232323
palette = 1=#ff000f
palette = 2=#8ce10b
palette = 3=#ffb900
palette = 4=#008df8
palette = 5=#6d43a6
palette = 6=#00d8eb
palette = 7=#ffffff
palette = 8=#444444
palette = 9=#ff2740
palette = 10=#abe15b
palette = 11=#ffd242
palette = 12=#0092ff
palette = 13=#9a5feb
palette = 14=#67fff0
palette = 15=#ffffff
background = #0e1019
foreground = #fffaf4
cursor-color = #ff0018
cursor-text = #ff8ca4
selection-background = #002a3b
selection-foreground = #ffffff
`,
  Arthur: `palette = 0=#3d352a
palette = 1=#cd5c5c
palette = 2=#86af80
palette = 3=#e8ae5b
palette = 4=#6495ed
palette = 5=#deb887
palette = 6=#b0c4de
palette = 7=#bbaa99
palette = 8=#554444
palette = 9=#cc5533
palette = 10=#88aa22
palette = 11=#ffa75d
palette = 12=#87ceeb
palette = 13=#996600
palette = 14=#b0c4de
palette = 15=#ddccbb
background = #1c1c1c
foreground = #ddeedd
cursor-color = #e2bbef
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  "Atelier Sulphurpool": `palette = 0=#202746
palette = 1=#c94922
palette = 2=#ac9739
palette = 3=#c08b30
palette = 4=#3d8fd1
palette = 5=#6679cc
palette = 6=#22a2c9
palette = 7=#979db4
palette = 8=#6b7394
palette = 9=#c76b29
palette = 10=#4f587c
palette = 11=#5e6687
palette = 12=#898ea4
palette = 13=#dfe2f1
palette = 14=#9c637a
palette = 15=#f5f7ff
background = #202746
foreground = #979db4
cursor-color = #979db4
cursor-text = #202746
selection-background = #5e6687
selection-foreground = #979db4
`,
  Atom: `palette = 0=#000000
palette = 1=#fd5ff1
palette = 2=#87c38a
palette = 3=#ffd7b1
palette = 4=#85befd
palette = 5=#b9b6fc
palette = 6=#85befd
palette = 7=#e0e0e0
palette = 8=#4c4c4c
palette = 9=#fd5ff1
palette = 10=#94fa36
palette = 11=#f5ffa8
palette = 12=#96cbfe
palette = 13=#b9b6fc
palette = 14=#85befd
palette = 15=#e0e0e0
background = #161719
foreground = #c5c8c6
cursor-color = #d0d0d0
cursor-text = #151515
selection-background = #444444
selection-foreground = #c5c8c6
`,
  "Atom One Dark": `palette = 0=#21252b
palette = 1=#e06c75
palette = 2=#98c379
palette = 3=#e5c07b
palette = 4=#61afef
palette = 5=#c678dd
palette = 6=#56b6c2
palette = 7=#abb2bf
palette = 8=#767676
palette = 9=#e06c75
palette = 10=#98c379
palette = 11=#e5c07b
palette = 12=#61afef
palette = 13=#c678dd
palette = 14=#56b6c2
palette = 15=#abb2bf
background = #21252b
foreground = #abb2bf
cursor-color = #abb2bf
cursor-text = #21252b
selection-background = #323844
selection-foreground = #abb2bf
`,
  "Atom One Light": `palette = 0=#000000
palette = 1=#de3e35
palette = 2=#3f953a
palette = 3=#d2b67c
palette = 4=#2f5af3
palette = 5=#950095
palette = 6=#3f953a
palette = 7=#bbbbbb
palette = 8=#000000
palette = 9=#de3e35
palette = 10=#3f953a
palette = 11=#d2b67c
palette = 12=#2f5af3
palette = 13=#a00095
palette = 14=#3f953a
palette = 15=#ffffff
background = #f9f9f9
foreground = #2a2c33
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #ededed
selection-foreground = #2a2c33
`,
  Aura: `palette = 0=#110f18
palette = 1=#ff6767
palette = 2=#61ffca
palette = 3=#ffca85
palette = 4=#a277ff
palette = 5=#a277ff
palette = 6=#61ffca
palette = 7=#edecee
palette = 8=#4d4d4d
palette = 9=#ffca85
palette = 10=#a277ff
palette = 11=#ffca85
palette = 12=#a277ff
palette = 13=#a277ff
palette = 14=#61ffca
palette = 15=#edecee
background = #15141b
foreground = #edecee
cursor-color = #a277ff
cursor-text = #edecee
selection-background = #a277ff
selection-foreground = #edecee
`,
  Aurora: `palette = 0=#23262e
palette = 1=#f0266f
palette = 2=#8fd46d
palette = 3=#ffe66d
palette = 4=#102ee4
palette = 5=#ee5d43
palette = 6=#03d6b8
palette = 7=#c74ded
palette = 8=#4f545e
palette = 9=#f92672
palette = 10=#8fd46d
palette = 11=#ffe66d
palette = 12=#03d6b8
palette = 13=#ee5d43
palette = 14=#03d6b8
palette = 15=#c74ded
background = #23262e
foreground = #ffca28
cursor-color = #ee5d43
cursor-text = #ffd29c
selection-background = #292e38
selection-foreground = #00e8c6
`,
  Ayu: `palette = 0=#11151c
palette = 1=#ea6c73
palette = 2=#7fd962
palette = 3=#f9af4f
palette = 4=#53bdfa
palette = 5=#cda1fa
palette = 6=#90e1c6
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#f07178
palette = 10=#aad94c
palette = 11=#ffb454
palette = 12=#59c2ff
palette = 13=#d2a6ff
palette = 14=#95e6cb
palette = 15=#ffffff
background = #0b0e14
foreground = #bfbdb6
cursor-color = #e6b450
cursor-text = #0b0e14
selection-background = #409fff
selection-foreground = #0b0e14
`,
  "Ayu Light": `palette = 0=#000000
palette = 1=#ea6c6d
palette = 2=#6cbf43
palette = 3=#eca944
palette = 4=#3199e1
palette = 5=#9e75c7
palette = 6=#46ba94
palette = 7=#bababa
palette = 8=#686868
palette = 9=#f07171
palette = 10=#86b300
palette = 11=#f2ae49
palette = 12=#399ee6
palette = 13=#a37acc
palette = 14=#4cbf99
palette = 15=#d1d1d1
background = #f8f9fa
foreground = #5c6166
cursor-color = #ffaa33
cursor-text = #f8f9fa
selection-background = #035bd6
selection-foreground = #f8f9fa
`,
  "Ayu Mirage": `palette = 0=#171b24
palette = 1=#ed8274
palette = 2=#87d96c
palette = 3=#facc6e
palette = 4=#6dcbfa
palette = 5=#dabafa
palette = 6=#90e1c6
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#f28779
palette = 10=#d5ff80
palette = 11=#ffd173
palette = 12=#73d0ff
palette = 13=#dfbfff
palette = 14=#95e6cb
palette = 15=#ffffff
background = #1f2430
foreground = #cccac2
cursor-color = #ffcc66
cursor-text = #1f2430
selection-background = #409fff
selection-foreground = #1f2430
`,
  "Banana Blueberry": `palette = 0=#17141f
palette = 1=#ff6b7f
palette = 2=#00bd9c
palette = 3=#e6c62f
palette = 4=#22e8df
palette = 5=#dc396a
palette = 6=#56b6c2
palette = 7=#f1f1f1
palette = 8=#495162
palette = 9=#fe9ea1
palette = 10=#98c379
palette = 11=#f9e46b
palette = 12=#91fff4
palette = 13=#da70d6
palette = 14=#bcf3ff
palette = 15=#ffffff
background = #191323
foreground = #cccccc
cursor-color = #e07d13
cursor-text = #ffffff
selection-background = #220525
selection-foreground = #f4f4f4
`,
  Batman: `palette = 0=#1b1d1e
palette = 1=#e6dc44
palette = 2=#c8be46
palette = 3=#f4fd22
palette = 4=#737174
palette = 5=#747271
palette = 6=#62605f
palette = 7=#c6c5bf
palette = 8=#505354
palette = 9=#fff78e
palette = 10=#fff27d
palette = 11=#feed6c
palette = 12=#919495
palette = 13=#9a9a9d
palette = 14=#a3a3a6
palette = 15=#dadbd6
background = #1b1d1e
foreground = #6f6f6f
cursor-color = #fcef0c
cursor-text = #000000
selection-background = #4d504c
selection-foreground = #f0e04a
`,
  "Belafonte Day": `palette = 0=#20111b
palette = 1=#be100e
palette = 2=#858162
palette = 3=#d08b30
palette = 4=#426a79
palette = 5=#97522c
palette = 6=#989a9c
palette = 7=#968c83
palette = 8=#5e5252
palette = 9=#be100e
palette = 10=#858162
palette = 11=#d08b30
palette = 12=#426a79
palette = 13=#97522c
palette = 14=#989a9c
palette = 15=#d5ccba
background = #d5ccba
foreground = #45373c
cursor-color = #45373c
cursor-text = #d5ccba
selection-background = #968c83
selection-foreground = #45373c
`,
  "Belafonte Night": `palette = 0=#20111b
palette = 1=#be100e
palette = 2=#858162
palette = 3=#eaa549
palette = 4=#426a79
palette = 5=#97522c
palette = 6=#989a9c
palette = 7=#968c83
palette = 8=#5e5252
palette = 9=#be100e
palette = 10=#858162
palette = 11=#eaa549
palette = 12=#426a79
palette = 13=#97522c
palette = 14=#989a9c
palette = 15=#d5ccba
background = #20111b
foreground = #968c83
cursor-color = #968c83
cursor-text = #20111b
selection-background = #45373c
selection-foreground = #968c83
`,
  "Birds Of Paradise": `palette = 0=#573d26
palette = 1=#be2d26
palette = 2=#6ba18a
palette = 3=#e99d2a
palette = 4=#5a86ad
palette = 5=#ac80a6
palette = 6=#74a6ad
palette = 7=#e0dbb7
palette = 8=#9b6c4a
palette = 9=#e84627
palette = 10=#95d8ba
palette = 11=#d0d150
palette = 12=#b8d3ed
palette = 13=#d19ecb
palette = 14=#93cfd7
palette = 15=#fff9d5
background = #2a1f1d
foreground = #e0dbb7
cursor-color = #644a33
cursor-text = #8a7059
selection-background = #563c27
selection-foreground = #e0dbbb
`,
  "Black Metal": `palette = 0=#000000
palette = 1=#486e6f
palette = 2=#dd9999
palette = 3=#a06666
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#486e6f
palette = 10=#dd9999
palette = 11=#a06666
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Bathory)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#fbcb97
palette = 3=#e78a53
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#fbcb97
palette = 11=#e78a53
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Burzum)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#ddeecc
palette = 3=#99bbaa
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#ddeecc
palette = 11=#99bbaa
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Dark Funeral)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#d0dfee
palette = 3=#5f81a5
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#d0dfee
palette = 11=#5f81a5
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Gorgoroth)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#9b8d7f
palette = 3=#8c7f70
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#9b8d7f
palette = 11=#8c7f70
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Immortal)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#7799bb
palette = 3=#556677
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#7799bb
palette = 11=#556677
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Khold)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#eceee3
palette = 3=#974b46
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#eceee3
palette = 11=#974b46
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Marduk)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#a5aaa7
palette = 3=#626b67
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#a5aaa7
palette = 11=#626b67
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Mayhem)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#f3ecd4
palette = 3=#eecc6c
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#f3ecd4
palette = 11=#eecc6c
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Nile)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#aa9988
palette = 3=#777755
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#aa9988
palette = 11=#777755
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  "Black Metal (Venom)": `palette = 0=#000000
palette = 1=#5f8787
palette = 2=#f8f7f2
palette = 3=#79241f
palette = 4=#888888
palette = 5=#999999
palette = 6=#aaaaaa
palette = 7=#c1c1c1
palette = 8=#404040
palette = 9=#5f8787
palette = 10=#f8f7f2
palette = 11=#79241f
palette = 12=#888888
palette = 13=#999999
palette = 14=#aaaaaa
palette = 15=#c1c1c1
background = #000000
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #8e8e8e
selection-background = #c1c1c1
selection-foreground = #000000
`,
  Blazer: `palette = 0=#000000
palette = 1=#b87a7a
palette = 2=#7ab87a
palette = 3=#b8b87a
palette = 4=#7a7ab8
palette = 5=#b87ab8
palette = 6=#7ab8b8
palette = 7=#d9d9d9
palette = 8=#4c4c4c
palette = 9=#dbbdbd
palette = 10=#bddbbd
palette = 11=#dbdbbd
palette = 12=#bdbddb
palette = 13=#dbbddb
palette = 14=#bddbdb
palette = 15=#ffffff
background = #0d1926
foreground = #d9e6f2
cursor-color = #d9e6f2
cursor-text = #0d1926
selection-background = #c1ddff
selection-foreground = #000000
`,
  "Blue Berry Pie": `palette = 0=#0a4c62
palette = 1=#99246e
palette = 2=#5cb1b3
palette = 3=#eab9a8
palette = 4=#90a5bd
palette = 5=#9d54a7
palette = 6=#7e83cc
palette = 7=#f0e8d6
palette = 8=#463c5d
palette = 9=#c87272
palette = 10=#0a6c7e
palette = 11=#7a3188
palette = 12=#5f3d63
palette = 13=#bc94b7
palette = 14=#5e6071
palette = 15=#0a6c7e
background = #1c0c28
foreground = #babab9
cursor-color = #fcfad6
cursor-text = #000000
selection-background = #606060
selection-foreground = #ffffff
`,
  "Blue Dolphin": `palette = 0=#292d3e
palette = 1=#ff8288
palette = 2=#b4e88d
palette = 3=#f4d69f
palette = 4=#82aaff
palette = 5=#e9c1ff
palette = 6=#89ebff
palette = 7=#d0d0d0
palette = 8=#9094a4
palette = 9=#ff8b92
palette = 10=#ddffa7
palette = 11=#ffe585
palette = 12=#9cc4ff
palette = 13=#ddb0f6
palette = 14=#a3f7ff
palette = 15=#ffffff
background = #006984
foreground = #c5f2ff
cursor-color = #ffcc00
cursor-text = #292d3e
selection-background = #2baeca
selection-foreground = #eceff1
`,
  "Blue Matrix": `palette = 0=#101116
palette = 1=#ff5680
palette = 2=#00ff9c
palette = 3=#fffc58
palette = 4=#00b0ff
palette = 5=#d57bff
palette = 6=#76c1ff
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#5ffa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#d682ec
palette = 14=#60fdff
palette = 15=#ffffff
background = #101116
foreground = #00a2ff
cursor-color = #76ff9f
cursor-text = #a6a6a6
selection-background = #c1deff
selection-foreground = #000000
`,
  "Bluloco Dark": `palette = 0=#41444d
palette = 1=#fc2f52
palette = 2=#25a45c
palette = 3=#ff936a
palette = 4=#3476ff
palette = 5=#7a82da
palette = 6=#4483aa
palette = 7=#cdd4e0
palette = 8=#8f9aae
palette = 9=#ff6480
palette = 10=#3fc56b
palette = 11=#f9c859
palette = 12=#10b1fe
palette = 13=#ff78f8
palette = 14=#5fb9bc
palette = 15=#ffffff
background = #282c34
foreground = #b9c0cb
cursor-color = #ffcc00
cursor-text = #282c34
selection-background = #b9c0ca
selection-foreground = #272b33
`,
  "Bluloco Light": `palette = 0=#373a41
palette = 1=#d52753
palette = 2=#23974a
palette = 3=#df631c
palette = 4=#275fe4
palette = 5=#823ff1
palette = 6=#27618d
palette = 7=#babbc2
palette = 8=#676a77
palette = 9=#ff6480
palette = 10=#3cbc66
palette = 11=#c5a332
palette = 12=#0099e1
palette = 13=#ce33c0
palette = 14=#6d93bb
palette = 15=#d3d3d3
background = #f9f9f9
foreground = #373a41
cursor-color = #f32759
cursor-text = #ffffff
selection-background = #daf0ff
selection-foreground = #373a41
`,
  Borland: `palette = 0=#4f4f4f
palette = 1=#ff6c60
palette = 2=#a8ff60
palette = 3=#ffffb6
palette = 4=#96cbfe
palette = 5=#ff73fd
palette = 6=#c6c5fe
palette = 7=#eeeeee
palette = 8=#7c7c7c
palette = 9=#ffb6b0
palette = 10=#ceffac
palette = 11=#ffffcc
palette = 12=#b5dcff
palette = 13=#ff9cfe
palette = 14=#dfdffe
palette = 15=#ffffff
background = #0000a4
foreground = #ffff4e
cursor-color = #ffa560
cursor-text = #ffffff
selection-background = #a4a4a4
selection-foreground = #0000a4
`,
  Box: `palette = 0=#000000
palette = 1=#cc0403
palette = 2=#19cb00
palette = 3=#cecb00
palette = 4=#0d73cc
palette = 5=#cb1ed1
palette = 6=#0dcdcd
palette = 7=#dddddd
palette = 8=#767676
palette = 9=#f2201f
palette = 10=#23fd00
palette = 11=#fffd00
palette = 12=#1a8fff
palette = 13=#fd28ff
palette = 14=#14ffff
palette = 15=#ffffff
background = #141d2b
foreground = #9fef00
cursor-color = #9fef00
cursor-text = #111111
selection-background = #a4b1cd
selection-foreground = #141d2b
`,
  branch: `palette = 0=#35241c
palette = 1=#c2562d
palette = 2=#96a65e
palette = 3=#d29b5a
palette = 4=#3b8e8c
palette = 5=#c47e5b
palette = 6=#639a90
palette = 7=#cfc1a9
palette = 8=#564a45
palette = 9=#c2562d
palette = 10=#96a65e
palette = 11=#d29b5a
palette = 12=#3b8e8c
palette = 13=#c47e5b
palette = 14=#639a90
palette = 15=#cfc1a9
background = #32221a
foreground = #cfc1a9
cursor-color = #cfc1a9
cursor-text = #32221a
selection-background = #cfc1a9
selection-foreground = #32221a
`,
  Breadog: `palette = 0=#362c24
palette = 1=#b10b00
palette = 2=#007232
palette = 3=#8b4c00
palette = 4=#005cb4
palette = 5=#9b0097
palette = 6=#006a78
palette = 7=#baa99d
palette = 8=#514337
palette = 9=#de1100
palette = 10=#008f40
palette = 11=#ae6000
palette = 12=#0074e1
palette = 13=#c300bd
palette = 14=#008697
palette = 15=#eae1da
background = #f1ebe6
foreground = #362c24
cursor-color = #362c24
cursor-text = #f1ebe6
selection-background = #362c24
selection-foreground = #f1ebe6
`,
  Breeze: `palette = 0=#31363b
palette = 1=#ed1515
palette = 2=#11d116
palette = 3=#f67400
palette = 4=#1d99f3
palette = 5=#9b59b6
palette = 6=#1abc9c
palette = 7=#eff0f1
palette = 8=#7f8c8d
palette = 9=#c0392b
palette = 10=#1cdc9a
palette = 11=#fdbc4b
palette = 12=#3daee9
palette = 13=#8e44ad
palette = 14=#16a085
palette = 15=#fcfcfc
background = #31363b
foreground = #eff0f1
cursor-color = #eff0f1
cursor-text = #31363b
selection-background = #eff0f1
selection-foreground = #31363b
`,
  "Bright Lights": `palette = 0=#191919
palette = 1=#ff355b
palette = 2=#b7e876
palette = 3=#ffc251
palette = 4=#76d4ff
palette = 5=#ba76e7
palette = 6=#6cbfb5
palette = 7=#c2c8d7
palette = 8=#4c4c4c
palette = 9=#ff355b
palette = 10=#b7e876
palette = 11=#ffc251
palette = 12=#76d5ff
palette = 13=#ba76e7
palette = 14=#6cbfb5
palette = 15=#c2c8d7
background = #191919
foreground = #b3c9d7
cursor-color = #f34b00
cursor-text = #002831
selection-background = #b3c9d7
selection-foreground = #191919
`,
  Broadcast: `palette = 0=#000000
palette = 1=#da4939
palette = 2=#519f50
palette = 3=#ffd24a
palette = 4=#6d9cbe
palette = 5=#d0d0ff
palette = 6=#6e9cbe
palette = 7=#ffffff
palette = 8=#585858
palette = 9=#ff7b6b
palette = 10=#83d182
palette = 11=#ffff7c
palette = 12=#9fcef0
palette = 13=#ffffff
palette = 14=#a0cef0
palette = 15=#ffffff
background = #2b2b2b
foreground = #e6e1dc
cursor-color = #ffffff
cursor-text = #c0bbb6
selection-background = #5a647e
selection-foreground = #e6e1dc
`,
  Brogrammer: `palette = 0=#1f1f1f
palette = 1=#f81118
palette = 2=#2dc55e
palette = 3=#ecba0f
palette = 4=#2a84d2
palette = 5=#4e5ab7
palette = 6=#1081d6
palette = 7=#d6dbe5
palette = 8=#d6dbe5
palette = 9=#de352e
palette = 10=#1dd361
palette = 11=#f3bd09
palette = 12=#1081d6
palette = 13=#5350b9
palette = 14=#0f7ddb
palette = 15=#ffffff
background = #131313
foreground = #d6dbe5
cursor-color = #b9b9b9
cursor-text = #101010
selection-background = #1f1f1f
selection-foreground = #d6dbe5
`,
  "Builtin Dark": `palette = 0=#000000
palette = 1=#bb0000
palette = 2=#00bb00
palette = 3=#bbbb00
palette = 4=#0d0dc8
palette = 5=#bb00bb
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #bbbbbb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Builtin Light": `palette = 0=#000000
palette = 1=#bb0000
palette = 2=#00bb00
palette = 3=#bbbb00
palette = 4=#0000bb
palette = 5=#bb00bb
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#2fd92f
palette = 11=#bfbf15
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#22cccc
palette = 15=#ffffff
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Builtin Pastel Dark": `palette = 0=#4f4f4f
palette = 1=#ff6c60
palette = 2=#a8ff60
palette = 3=#ffffb6
palette = 4=#96cbfe
palette = 5=#ff73fd
palette = 6=#c6c5fe
palette = 7=#eeeeee
palette = 8=#7c7c7c
palette = 9=#ffb6b0
palette = 10=#ceffac
palette = 11=#ffffcc
palette = 12=#b5dcff
palette = 13=#ff9cfe
palette = 14=#dfdffe
palette = 15=#ffffff
background = #000000
foreground = #bbbbbb
cursor-color = #ffa560
cursor-text = #ffffff
selection-background = #363983
selection-foreground = #f2f2f2
`,
  "Builtin Tango Dark": `palette = 0=#000000
palette = 1=#cc0000
palette = 2=#4e9a06
palette = 3=#c4a000
palette = 4=#3465a4
palette = 5=#75507b
palette = 6=#06989a
palette = 7=#d3d7cf
palette = 8=#555753
palette = 9=#ef2929
palette = 10=#8ae234
palette = 11=#fce94f
palette = 12=#729fcf
palette = 13=#ad7fa8
palette = 14=#34e2e2
palette = 15=#eeeeec
background = #000000
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #000000
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Builtin Tango Light": `palette = 0=#000000
palette = 1=#cc0000
palette = 2=#4e9a06
palette = 3=#c4a000
palette = 4=#3465a4
palette = 5=#75507b
palette = 6=#06989a
palette = 7=#b9bdb5
palette = 8=#555753
palette = 9=#ef2929
palette = 10=#7dd527
palette = 11=#d6c329
palette = 12=#729fcf
palette = 13=#ad7fa8
palette = 14=#27d5d5
palette = 15=#eeeeec
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  C64: `palette = 0=#090300
palette = 1=#a2524c
palette = 2=#55a049
palette = 3=#bfce72
palette = 4=#6657b3
palette = 5=#984ca3
palette = 6=#67b6bd
palette = 7=#ffffff
palette = 8=#000000
palette = 9=#a2524c
palette = 10=#55a049
palette = 11=#bfce72
palette = 12=#6657b3
palette = 13=#984ca3
palette = 14=#67b6bd
palette = 15=#f7f7f7
background = #40318d
foreground = #7869c4
cursor-color = #7869c4
cursor-text = #40318d
selection-background = #7869c4
selection-foreground = #40318d
`,
  Calamity: `palette = 0=#2f2833
palette = 1=#fc644d
palette = 2=#a5f69c
palette = 3=#e9d7a5
palette = 4=#3b79c7
palette = 5=#f92672
palette = 6=#74d3de
palette = 7=#d5ced9
palette = 8=#7e6c88
palette = 9=#fc644d
palette = 10=#a5f69c
palette = 11=#e9d7a5
palette = 12=#3b79c7
palette = 13=#f92672
palette = 14=#74d3de
palette = 15=#ffffff
background = #2f2833
foreground = #d5ced9
cursor-color = #d5ced9
cursor-text = #2f2833
selection-background = #7e6c88
selection-foreground = #d5ced9
`,
  Carbonfox: `palette = 0=#282828
palette = 1=#ee5396
palette = 2=#25be6a
palette = 3=#08bdba
palette = 4=#78a9ff
palette = 5=#be95ff
palette = 6=#33b1ff
palette = 7=#dfdfe0
palette = 8=#484848
palette = 9=#f16da6
palette = 10=#46c880
palette = 11=#2dc7c4
palette = 12=#8cb6ff
palette = 13=#c8a5ff
palette = 14=#52bdff
palette = 15=#e4e4e5
background = #161616
foreground = #f2f4f8
cursor-color = #f2f4f8
cursor-text = #161616
selection-background = #2a2a2a
selection-foreground = #f2f4f8
`,
  "Catppuccin Frappe": `palette = 0=#51576d
palette = 1=#e78284
palette = 2=#a6d189
palette = 3=#e5c890
palette = 4=#8caaee
palette = 5=#f4b8e4
palette = 6=#81c8be
palette = 7=#a5adce
palette = 8=#626880
palette = 9=#e67172
palette = 10=#8ec772
palette = 11=#d9ba73
palette = 12=#7b9ef0
palette = 13=#f2a4db
palette = 14=#5abfb5
palette = 15=#b5bfe2
background = #303446
foreground = #c6d0f5
cursor-color = #f2d5cf
cursor-text = #303446
selection-background = #626880
selection-foreground = #c6d0f5
`,
  "Catppuccin Latte": `palette = 0=#5c5f77
palette = 1=#d20f39
palette = 2=#40a02b
palette = 3=#df8e1d
palette = 4=#1e66f5
palette = 5=#ea76cb
palette = 6=#179299
palette = 7=#acb0be
palette = 8=#6c6f85
palette = 9=#de293e
palette = 10=#49af3d
palette = 11=#eea02d
palette = 12=#456eff
palette = 13=#fe85d8
palette = 14=#2d9fa8
palette = 15=#bcc0cc
background = #eff1f5
foreground = #4c4f69
cursor-color = #dc8a78
cursor-text = #eff1f5
selection-background = #acb0be
selection-foreground = #4c4f69
`,
  "Catppuccin Macchiato": `palette = 0=#494d64
palette = 1=#ed8796
palette = 2=#a6da95
palette = 3=#eed49f
palette = 4=#8aadf4
palette = 5=#f5bde6
palette = 6=#8bd5ca
palette = 7=#a5adcb
palette = 8=#5b6078
palette = 9=#ec7486
palette = 10=#8ccf7f
palette = 11=#e1c682
palette = 12=#78a1f6
palette = 13=#f2a9dd
palette = 14=#63cbc0
palette = 15=#b8c0e0
background = #24273a
foreground = #cad3f5
cursor-color = #f4dbd6
cursor-text = #24273a
selection-background = #5b6078
selection-foreground = #cad3f5
`,
  "Catppuccin Mocha": `palette = 0=#45475a
palette = 1=#f38ba8
palette = 2=#a6e3a1
palette = 3=#f9e2af
palette = 4=#89b4fa
palette = 5=#f5c2e7
palette = 6=#94e2d5
palette = 7=#a6adc8
palette = 8=#585b70
palette = 9=#f37799
palette = 10=#89d88b
palette = 11=#ebd391
palette = 12=#74a8fc
palette = 13=#f2aede
palette = 14=#6bd7ca
palette = 15=#bac2de
background = #1e1e2e
foreground = #cdd6f4
cursor-color = #f5e0dc
cursor-text = #1e1e2e
selection-background = #585b70
selection-foreground = #cdd6f4
`,
  CGA: `palette = 0=#000000
palette = 1=#aa0000
palette = 2=#00aa00
palette = 3=#aa5500
palette = 4=#0d0db7
palette = 5=#aa00aa
palette = 6=#00aaaa
palette = 7=#aaaaaa
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #aaaaaa
cursor-color = #b8b8b8
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  Chalk: `palette = 0=#7d8b8f
palette = 1=#b23a52
palette = 2=#789b6a
palette = 3=#b9ac4a
palette = 4=#2a7fac
palette = 5=#bd4f5a
palette = 6=#44a799
palette = 7=#d2d8d9
palette = 8=#888888
palette = 9=#f24840
palette = 10=#80c470
palette = 11=#ffeb62
palette = 12=#4196ff
palette = 13=#fc5275
palette = 14=#53cdbd
palette = 15=#d2d8d9
background = #2b2d2e
foreground = #d2d8d9
cursor-color = #708284
cursor-text = #002831
selection-background = #e4e8ed
selection-foreground = #3f4041
`,
  Chalkboard: `palette = 0=#000000
palette = 1=#c37372
palette = 2=#72c373
palette = 3=#c2c372
palette = 4=#7372c3
palette = 5=#c372c2
palette = 6=#72c2c3
palette = 7=#d9d9d9
palette = 8=#585858
palette = 9=#dbaaaa
palette = 10=#aadbaa
palette = 11=#dadbaa
palette = 12=#aaaadb
palette = 13=#dbaada
palette = 14=#aadadb
palette = 15=#ffffff
background = #29262f
foreground = #d9e6f2
cursor-color = #d9e6f2
cursor-text = #29262f
selection-background = #073642
selection-foreground = #ffffff
`,
  "Challenger Deep": `palette = 0=#141228
palette = 1=#ff5458
palette = 2=#62d196
palette = 3=#ffb378
palette = 4=#65b2ff
palette = 5=#906cff
palette = 6=#63f2f1
palette = 7=#a6b3cc
palette = 8=#565575
palette = 9=#ff8080
palette = 10=#95ffa4
palette = 11=#ffe9aa
palette = 12=#91ddff
palette = 13=#c991e1
palette = 14=#aaffe4
palette = 15=#cbe3e7
background = #1e1c31
foreground = #cbe1e7
cursor-color = #fbfcfc
cursor-text = #ff271d
selection-background = #cbe1e7
selection-foreground = #1e1c31
`,
  Chester: `palette = 0=#080200
palette = 1=#fa5e5b
palette = 2=#16c98d
palette = 3=#ffc83f
palette = 4=#288ad6
palette = 5=#d34590
palette = 6=#28ddde
palette = 7=#e7e7e7
palette = 8=#6f6b68
palette = 9=#fa5e5b
palette = 10=#16c98d
palette = 11=#feef6d
palette = 12=#278ad6
palette = 13=#d34590
palette = 14=#27dede
palette = 15=#ffffff
background = #2c3643
foreground = #ffffff
cursor-color = #b4b1b1
cursor-text = #000000
selection-background = #67747c
selection-foreground = #ffffff
`,
  Ciapre: `palette = 0=#181818
palette = 1=#8e0d16
palette = 2=#48513b
palette = 3=#cc8b3f
palette = 4=#576d8c
palette = 5=#724d7c
palette = 6=#5c4f4b
palette = 7=#aea47f
palette = 8=#555555
palette = 9=#ac3835
palette = 10=#a6a75d
palette = 11=#dcdf7c
palette = 12=#3097c6
palette = 13=#d33061
palette = 14=#f3dbb2
palette = 15=#f4f4f4
background = #191c27
foreground = #aea47a
cursor-color = #92805b
cursor-text = #181818
selection-background = #172539
selection-foreground = #aea47f
`,
  Citruszest: `palette = 0=#404040
palette = 1=#ff5454
palette = 2=#00cc7a
palette = 3=#ffd400
palette = 4=#00bfff
palette = 5=#ff90fe
palette = 6=#48d1cc
palette = 7=#bfbfbf
palette = 8=#808080
palette = 9=#ff1a75
palette = 10=#1affa3
palette = 11=#ffff00
palette = 12=#33cfff
palette = 13=#ffb2fe
palette = 14=#00fff2
palette = 15=#f9f9f9
background = #121212
foreground = #bfbfbf
cursor-color = #666666
cursor-text = #f9f9f9
selection-background = #ff8c00
selection-foreground = #f4f4f4
`,
  CLRS: `palette = 0=#000000
palette = 1=#f8282a
palette = 2=#328a5d
palette = 3=#fa701d
palette = 4=#135cd0
palette = 5=#9f00bd
palette = 6=#33c3c1
palette = 7=#b3b3b3
palette = 8=#555753
palette = 9=#fb0416
palette = 10=#2cc631
palette = 11=#e3bd0e
palette = 12=#1670ff
palette = 13=#e900b0
palette = 14=#3ad5ce
palette = 15=#eeeeec
background = #ffffff
foreground = #262626
cursor-color = #62c6ef
cursor-text = #ffffff
selection-background = #6fd3fc
selection-foreground = #041730
`,
  "Cobalt Neon": `palette = 0=#142631
palette = 1=#ff2320
palette = 2=#3ba5ff
palette = 3=#e9e75c
palette = 4=#8ff586
palette = 5=#781aa0
palette = 6=#8ff586
palette = 7=#ba46b2
palette = 8=#fff688
palette = 9=#d4312e
palette = 10=#8ff586
palette = 11=#e9f06d
palette = 12=#3c7dd2
palette = 13=#8230a7
palette = 14=#6cbc67
palette = 15=#8ff586
background = #142838
foreground = #8ff586
cursor-color = #c4206f
cursor-text = #8ff586
selection-background = #094fb1
selection-foreground = #8ff586
`,
  "Cobalt Next": `palette = 0=#000000
palette = 1=#ff527b
palette = 2=#8cc98f
palette = 3=#ffc64c
palette = 4=#409dd4
palette = 5=#cba3c7
palette = 6=#37b5b4
palette = 7=#d7deea
palette = 8=#62747f
palette = 9=#e47e8b
palette = 10=#baddbb
palette = 11=#ffdc91
palette = 12=#7ac0eb
palette = 13=#f3ccef
palette = 14=#84e4e3
palette = 15=#ffffff
background = #162c35
foreground = #d7deea
cursor-color = #ffc64c
cursor-text = #8a919d
selection-background = #4c5b67
selection-foreground = #ffffff
`,
  "Cobalt Next Dark": `palette = 0=#262f37
palette = 1=#f94967
palette = 2=#8cc98f
palette = 3=#ffc64c
palette = 4=#409dd4
palette = 5=#cba3c7
palette = 6=#37b5b4
palette = 7=#d7deea
palette = 8=#62747f
palette = 9=#e47e8b
palette = 10=#baddbb
palette = 11=#ffdc91
palette = 12=#7ac0eb
palette = 13=#f3ccef
palette = 14=#84e4e3
palette = 15=#ffffff
background = #0b1c24
foreground = #d7deea
cursor-color = #ffc64c
cursor-text = #999999
selection-background = #37b5b4
selection-foreground = #ffffff
`,
  "Cobalt Next Minimal": `palette = 0=#323d47
palette = 1=#ff657a
palette = 2=#8cc98f
palette = 3=#ffc64c
palette = 4=#409dd4
palette = 5=#cba3c7
palette = 6=#37b5b4
palette = 7=#d7deea
palette = 8=#62747f
palette = 9=#e47e8b
palette = 10=#baddbb
palette = 11=#ffdc91
palette = 12=#7ac0eb
palette = 13=#f3ccef
palette = 14=#84e4e3
palette = 15=#ffffff
background = #0b1c24
foreground = #d7deea
cursor-color = #37b5b4
cursor-text = #ffffff
selection-background = #37b5b4
selection-foreground = #ffffff
`,
  Cobalt2: `palette = 0=#000000
palette = 1=#ff0000
palette = 2=#38de21
palette = 3=#ffe50a
palette = 4=#1460d2
palette = 5=#ff005d
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#f40e17
palette = 10=#3bd01d
palette = 11=#edc809
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#6ae3fa
palette = 15=#ffffff
background = #132738
foreground = #ffffff
cursor-color = #f0cc09
cursor-text = #98998c
selection-background = #18354f
selection-foreground = #b5b5b5
`,
  "Coffee Theme": `palette = 0=#000000
palette = 1=#c91b00
palette = 2=#00c200
palette = 3=#adaa00
palette = 4=#0225c7
palette = 5=#ca30c7
palette = 6=#00b8ba
palette = 7=#a1a1a1
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#1fba28
palette = 11=#b2af1b
palette = 12=#6871ff
palette = 13=#f26af2
palette = 14=#20bdbf
palette = 15=#ffffff
background = #f5deb3
foreground = #000000
cursor-color = #a1a1a1
cursor-text = #fffc67
selection-background = #c1deff
selection-foreground = #000000
`,
  "Crayon Pony Fish": `palette = 0=#2b1b1d
palette = 1=#91002b
palette = 2=#579524
palette = 3=#ab311b
palette = 4=#8c87b0
palette = 5=#692f50
palette = 6=#e8a866
palette = 7=#68525a
palette = 8=#4a383b
palette = 9=#c5255d
palette = 10=#8dff57
palette = 11=#c8381d
palette = 12=#cfc9ff
palette = 13=#fc6cba
palette = 14=#ffceaf
palette = 15=#b0949d
background = #150707
foreground = #68525a
cursor-color = #68525a
cursor-text = #140707
selection-background = #2b1b1d
selection-foreground = #69525a
`,
  "Cursor Dark": `palette = 0=#2a2a2a
palette = 1=#bf616a
palette = 2=#a3be8c
palette = 3=#ebcb8b
palette = 4=#81a1c1
palette = 5=#b48ead
palette = 6=#88c0d0
palette = 7=#d8dee9
palette = 8=#505050
palette = 9=#bf616a
palette = 10=#a3be8c
palette = 11=#ebcb8b
palette = 12=#81a1c1
palette = 13=#b48ead
palette = 14=#88c0d0
palette = 15=#ffffff
background = #141414
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #141414
selection-background = #303030
selection-foreground = #ffffff
`,
  "Cutie Pro": `palette = 0=#000000
palette = 1=#f56e7f
palette = 2=#bec975
palette = 3=#f58669
palette = 4=#42d9c5
palette = 5=#d286b7
palette = 6=#37cb8a
palette = 7=#d5c3c3
palette = 8=#88847f
palette = 9=#e5a1a3
palette = 10=#e8d6a7
palette = 11=#f1bb79
palette = 12=#80c5de
palette = 13=#b294bb
palette = 14=#9dccbb
palette = 15=#ffffff
background = #181818
foreground = #d5d0c9
cursor-color = #efc4cd
cursor-text = #181818
selection-background = #363636
selection-foreground = #d5d0c9
`,
  Cyberdyne: `palette = 0=#080808
palette = 1=#ff8373
palette = 2=#00c172
palette = 3=#d2a700
palette = 4=#0071cf
palette = 5=#ff90fe
palette = 6=#6bffdd
palette = 7=#f1f1f1
palette = 8=#484848
palette = 9=#ffc4be
palette = 10=#d6fcba
palette = 11=#fffed5
palette = 12=#c2e3ff
palette = 13=#ffb2fe
palette = 14=#e6e7fe
palette = 15=#ffffff
background = #151144
foreground = #00ff92
cursor-color = #00ff9c
cursor-text = #a6a6a6
selection-background = #454d96
selection-foreground = #f4f4f4
`,
  Cyberpunk: `palette = 0=#000000
palette = 1=#ff7092
palette = 2=#00fbac
palette = 3=#fffa6a
palette = 4=#00bfff
palette = 5=#df95ff
palette = 6=#86cbfe
palette = 7=#ffffff
palette = 8=#595959
palette = 9=#ff8aa4
palette = 10=#21f6bc
palette = 11=#fff787
palette = 12=#1bccfd
palette = 13=#e6aefe
palette = 14=#99d6fc
palette = 15=#ffffff
background = #332a57
foreground = #e5e5e5
cursor-color = #21f6bc
cursor-text = #999999
selection-background = #c1deff
selection-foreground = #000000
`,
  "Cyberpunk Scarlet Protocol": `palette = 0=#101116
palette = 1=#ff0051
palette = 2=#01dc84
palette = 3=#faf945
palette = 4=#0271b6
palette = 5=#c930c7
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#60fa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#bd35ec
palette = 14=#60fdff
palette = 15=#ffffff
background = #101116
foreground = #e41951
cursor-color = #76ff9f
cursor-text = #a6a6a6
selection-background = #c1deff
selection-foreground = #000000
`,
  "Dark Modern": `palette = 0=#272727
palette = 1=#f74949
palette = 2=#2ea043
palette = 3=#9e6a03
palette = 4=#0078d4
palette = 5=#d01273
palette = 6=#1db4d6
palette = 7=#cccccc
palette = 8=#5d5d5d
palette = 9=#dc5452
palette = 10=#23d18b
palette = 11=#f5f543
palette = 12=#3b8eea
palette = 13=#d670d6
palette = 14=#29b8db
palette = 15=#e5e5e5
background = #1f1f1f
foreground = #cccccc
cursor-color = #ffffff
cursor-text = #000000
selection-background = #3a3d41
selection-foreground = #e0e0e0
`,
  "Dark Pastel": `palette = 0=#000000
palette = 1=#ff5555
palette = 2=#55ff55
palette = 3=#ffff55
palette = 4=#5555ff
palette = 5=#ff55ff
palette = 6=#55ffff
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #ffffff
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Dark+": `palette = 0=#000000
palette = 1=#cd3131
palette = 2=#0dbc79
palette = 3=#e5e510
palette = 4=#2472c8
palette = 5=#bc3fbc
palette = 6=#11a8cd
palette = 7=#e5e5e5
palette = 8=#666666
palette = 9=#f14c4c
palette = 10=#23d18b
palette = 11=#f5f543
palette = 12=#3b8eea
palette = 13=#d670d6
palette = 14=#29b8db
palette = 15=#e5e5e5
background = #1e1e1e
foreground = #cccccc
cursor-color = #ffffff
cursor-text = #000000
selection-background = #3a3d41
selection-foreground = #e0e0e0
`,
  Darkermatrix: `palette = 0=#091013
palette = 1=#1a4831
palette = 2=#6fa64c
palette = 3=#595900
palette = 4=#00cb6b
palette = 5=#4e375a
palette = 6=#125459
palette = 7=#1a4833
palette = 8=#404040
palette = 9=#0d452a
palette = 10=#90d762
palette = 11=#e2e500
palette = 12=#00ff87
palette = 13=#4e375a
palette = 14=#176c73
palette = 15=#00381e
background = #070c0e
foreground = #35451a
cursor-color = #444733
cursor-text = #00ff87
selection-background = #0f191c
selection-foreground = #00ff87
`,
  Darkmatrix: `palette = 0=#091013
palette = 1=#006536
palette = 2=#6fa64c
palette = 3=#7e8000
palette = 4=#2c9a84
palette = 5=#523a60
palette = 6=#114d53
palette = 7=#006536
palette = 8=#404040
palette = 9=#00733d
palette = 10=#90d762
palette = 11=#e2e500
palette = 12=#46d8b8
palette = 13=#573d66
palette = 14=#12545a
palette = 15=#006536
background = #070c0e
foreground = #3e5715
cursor-color = #9fa86e
cursor-text = #00ff87
selection-background = #0f191c
selection-foreground = #00ff87
`,
  Darkside: `palette = 0=#000000
palette = 1=#e8341c
palette = 2=#68c256
palette = 3=#f2d42c
palette = 4=#1c98e8
palette = 5=#8e69c9
palette = 6=#1c98e8
palette = 7=#bababa
palette = 8=#4c4c4c
palette = 9=#e05a4f
palette = 10=#77b869
palette = 11=#efd64b
palette = 12=#387cd3
palette = 13=#957bbe
palette = 14=#3d97e2
palette = 15=#bababa
background = #222324
foreground = #bababa
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #303333
selection-foreground = #bababa
`,
  Dawnfox: `palette = 0=#575279
palette = 1=#b4637a
palette = 2=#618774
palette = 3=#ea9d34
palette = 4=#286983
palette = 5=#907aa9
palette = 6=#56949f
palette = 7=#b2b6bd
palette = 8=#5f5695
palette = 9=#c26d85
palette = 10=#629f81
palette = 11=#eea846
palette = 12=#2d81a3
palette = 13=#9a80b9
palette = 14=#5ca7b4
palette = 15=#e6ebf3
background = #faf4ed
foreground = #575279
cursor-color = #575279
cursor-text = #faf4ed
selection-background = #d0d8d8
selection-foreground = #575279
`,
  Dayfox: `palette = 0=#352c24
palette = 1=#a5222f
palette = 2=#396847
palette = 3=#ac5402
palette = 4=#2848a9
palette = 5=#6e33ce
palette = 6=#287980
palette = 7=#bfb6ae
palette = 8=#534c45
palette = 9=#b3434e
palette = 10=#577f63
palette = 11=#b86e28
palette = 12=#4863b6
palette = 13=#8452d5
palette = 14=#488d93
palette = 15=#f4ece6
background = #f6f2ee
foreground = #3d2b5a
cursor-color = #3d2b5a
cursor-text = #f6f2ee
selection-background = #e7d2be
selection-foreground = #3d2b5a
`,
  Deep: `palette = 0=#000000
palette = 1=#d70005
palette = 2=#1cd915
palette = 3=#d9bd26
palette = 4=#5665ff
palette = 5=#b052da
palette = 6=#50d2da
palette = 7=#e0e0e0
palette = 8=#535353
palette = 9=#fb0007
palette = 10=#22ff18
palette = 11=#fedc2b
palette = 12=#9fa9ff
palette = 13=#e09aff
palette = 14=#8df9ff
palette = 15=#ffffff
background = #090909
foreground = #cdcdcd
cursor-color = #d0d0d0
cursor-text = #151515
selection-background = #780002
selection-foreground = #ececec
`,
  Desert: `palette = 0=#4d4d4d
palette = 1=#ff2b2b
palette = 2=#98fb98
palette = 3=#f0e68c
palette = 4=#cd853f
palette = 5=#ffdead
palette = 6=#ffa0a0
palette = 7=#f5deb3
palette = 8=#626262
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#87ceff
palette = 13=#ff55ff
palette = 14=#ffd700
palette = 15=#ffffff
background = #333333
foreground = #ffffff
cursor-color = #00ff00
cursor-text = #000000
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Detuned: `palette = 0=#171717
palette = 1=#fe4386
palette = 2=#a6e32d
palette = 3=#e6da73
palette = 4=#0094d9
palette = 5=#9b37ff
palette = 6=#50b7d9
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#fa80ac
palette = 10=#bde371
palette = 11=#fff27f
palette = 12=#00beff
palette = 13=#be9eff
palette = 14=#5ed7ff
palette = 15=#ffffff
background = #000000
foreground = #c7c7c7
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #c1deff
selection-foreground = #000000
`,
  Dimidium: `palette = 0=#000000
palette = 1=#cf494c
palette = 2=#60b442
palette = 3=#db9c11
palette = 4=#0575d8
palette = 5=#af5ed2
palette = 6=#1db6bb
palette = 7=#bab7b6
palette = 8=#817e7e
palette = 9=#ff643b
palette = 10=#37e57b
palette = 11=#fccd1a
palette = 12=#688dfd
palette = 13=#ed6fe9
palette = 14=#32e0fb
palette = 15=#dee3e4
background = #141414
foreground = #bab7b6
cursor-color = #37e57b
cursor-text = #141414
selection-background = #8db8e5
selection-foreground = #141414
`,
  "Dimmed Monokai": `palette = 0=#3a3d43
palette = 1=#be3f48
palette = 2=#879a3b
palette = 3=#c5a635
palette = 4=#4f76a1
palette = 5=#855c8d
palette = 6=#578fa4
palette = 7=#b9bcba
palette = 8=#888987
palette = 9=#fb001f
palette = 10=#0f722f
palette = 11=#c47033
palette = 12=#186de3
palette = 13=#fb0067
palette = 14=#2e706d
palette = 15=#fdffb9
background = #1f1f1f
foreground = #b9bcba
cursor-color = #f83e19
cursor-text = #171717
selection-background = #2a2d32
selection-foreground = #b9bcba
`,
  Django: `palette = 0=#000000
palette = 1=#fd6209
palette = 2=#41a83e
palette = 3=#ffe862
palette = 4=#315d3f
palette = 5=#f8f8f8
palette = 6=#9df39f
palette = 7=#ffffff
palette = 8=#585858
palette = 9=#ff943b
palette = 10=#73da70
palette = 11=#ffff94
palette = 12=#568264
palette = 13=#ffffff
palette = 14=#cfffd1
palette = 15=#ffffff
background = #0b2f20
foreground = #f8f8f8
cursor-color = #336442
cursor-text = #f8f8f8
selection-background = #245032
selection-foreground = #f8f8f8
`,
  "Django Reborn Again": `palette = 0=#000000
palette = 1=#fd6209
palette = 2=#41a83e
palette = 3=#ffe862
palette = 4=#245032
palette = 5=#f8f8f8
palette = 6=#9df39f
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#ff943b
palette = 10=#73da70
palette = 11=#ffff94
palette = 12=#568264
palette = 13=#ffffff
palette = 14=#cfffd1
palette = 15=#ffffff
background = #051f14
foreground = #dadedc
cursor-color = #ffcc00
cursor-text = #9a9e9c
selection-background = #203727
selection-foreground = #dadedc
`,
  "Django Smooth": `palette = 0=#000000
palette = 1=#fd6209
palette = 2=#41a83e
palette = 3=#ffe862
palette = 4=#989898
palette = 5=#f8f8f8
palette = 6=#9df39f
palette = 7=#e8e8e7
palette = 8=#727272
palette = 9=#ff943b
palette = 10=#73da70
palette = 11=#ffff94
palette = 12=#cacaca
palette = 13=#ffffff
palette = 14=#cfffd1
palette = 15=#ffffff
background = #245032
foreground = #f8f8f8
cursor-color = #4c7e5c
cursor-text = #f8f8f8
selection-background = #336442
selection-foreground = #f8f8f8
`,
  "Doom One": `palette = 0=#000000
palette = 1=#ff6c6b
palette = 2=#98be65
palette = 3=#ecbe7b
palette = 4=#a9a1e1
palette = 5=#c678dd
palette = 6=#51afef
palette = 7=#bbc2cf
palette = 8=#595959
palette = 9=#ff6655
palette = 10=#99bb66
palette = 11=#ecbe7b
palette = 12=#a9a1e1
palette = 13=#c678dd
palette = 14=#51afef
palette = 15=#bfbfbf
background = #282c34
foreground = #bbc2cf
cursor-color = #51afef
cursor-text = #1b1b1b
selection-background = #42444b
selection-foreground = #bbc2cf
`,
  "Doom Peacock": `palette = 0=#1c1f24
palette = 1=#cb4b16
palette = 2=#26a6a6
palette = 3=#bcd42a
palette = 4=#2a6cc6
palette = 5=#a9a1e1
palette = 6=#5699af
palette = 7=#ede0ce
palette = 8=#51504d
palette = 9=#ff5d38
palette = 10=#98be65
palette = 11=#e6f972
palette = 12=#51afef
palette = 13=#c678dd
palette = 14=#46d9ff
palette = 15=#dfdfdf
background = #2b2a27
foreground = #ede0ce
cursor-color = #9c9c9d
cursor-text = #36312b
selection-background = #a60033
selection-foreground = #ffffff
`,
  "Dot Gov": `palette = 0=#191919
palette = 1=#bf091d
palette = 2=#3d9751
palette = 3=#f6bb34
palette = 4=#17b2e0
palette = 5=#7830b0
palette = 6=#8bd2ed
palette = 7=#ffffff
palette = 8=#595959
palette = 9=#bf091d
palette = 10=#3d9751
palette = 11=#f6bb34
palette = 12=#17b2e0
palette = 13=#7830b0
palette = 14=#8bd2ed
palette = 15=#ffffff
background = #262c35
foreground = #ebebeb
cursor-color = #d9002f
cursor-text = #ffffff
selection-background = #1a4080
selection-foreground = #ffffff
`,
  Dracula: `palette = 0=#21222c
palette = 1=#ff5555
palette = 2=#50fa7b
palette = 3=#f1fa8c
palette = 4=#bd93f9
palette = 5=#ff79c6
palette = 6=#8be9fd
palette = 7=#f8f8f2
palette = 8=#6272a4
palette = 9=#ff6e6e
palette = 10=#69ff94
palette = 11=#ffffa5
palette = 12=#d6acff
palette = 13=#ff92df
palette = 14=#a4ffff
palette = 15=#ffffff
background = #282a36
foreground = #f8f8f2
cursor-color = #f8f8f2
cursor-text = #282a36
selection-background = #44475a
selection-foreground = #ffffff
`,
  "Dracula+": `palette = 0=#21222c
palette = 1=#ff5555
palette = 2=#50fa7b
palette = 3=#ffcb6b
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#8be9fd
palette = 7=#f8f8f2
palette = 8=#545454
palette = 9=#ff6e6e
palette = 10=#69ff94
palette = 11=#ffcb6b
palette = 12=#d6acff
palette = 13=#ff92df
palette = 14=#a4ffff
palette = 15=#f8f8f2
background = #212121
foreground = #f8f8f2
cursor-color = #eceff4
cursor-text = #282828
selection-background = #f8f8f2
selection-foreground = #545454
`,
  Duckbones: `palette = 0=#0e101a
palette = 1=#e03600
palette = 2=#5dcd97
palette = 3=#e39500
palette = 4=#00a3cb
palette = 5=#795ccc
palette = 6=#00a3cb
palette = 7=#ebefc0
palette = 8=#444860
palette = 9=#ff4821
palette = 10=#58db9e
palette = 11=#f6a100
palette = 12=#00b4e0
palette = 13=#b3a1e6
palette = 14=#00b4e0
palette = 15=#b3b692
background = #0e101a
foreground = #ebefc0
cursor-color = #edf2c2
cursor-text = #0e101a
selection-background = #37382d
selection-foreground = #ebefc0
`,
  "Duotone Dark": `palette = 0=#1f1d27
palette = 1=#d9393e
palette = 2=#2dcd73
palette = 3=#d9b76e
palette = 4=#ffc284
palette = 5=#de8d40
palette = 6=#2488ff
palette = 7=#b7a1ff
palette = 8=#4e4a60
palette = 9=#d9393e
palette = 10=#2dcd73
palette = 11=#d9b76e
palette = 12=#ffc284
palette = 13=#de8d40
palette = 14=#2488ff
palette = 15=#eae5ff
background = #1f1d27
foreground = #b7a1ff
cursor-color = #ff9839
cursor-text = #1f1d27
selection-background = #353147
selection-foreground = #b7a2ff
`,
  Duskfox: `palette = 0=#393552
palette = 1=#eb6f92
palette = 2=#a3be8c
palette = 3=#f6c177
palette = 4=#569fba
palette = 5=#c4a7e7
palette = 6=#9ccfd8
palette = 7=#e0def4
palette = 8=#544d8a
palette = 9=#f083a2
palette = 10=#b1d196
palette = 11=#f9cb8c
palette = 12=#65b1cd
palette = 13=#ccb1ed
palette = 14=#a6dae3
palette = 15=#e2e0f7
background = #232136
foreground = #e0def4
cursor-color = #e0def4
cursor-text = #232136
selection-background = #433c59
selection-foreground = #e0def4
`,
  Earthsong: `palette = 0=#121418
palette = 1=#c94234
palette = 2=#85c54c
palette = 3=#f5ae2e
palette = 4=#1398b9
palette = 5=#d0633d
palette = 6=#509552
palette = 7=#e5c6aa
palette = 8=#675f54
palette = 9=#ff645a
palette = 10=#98e036
palette = 11=#e0d561
palette = 12=#5fdaff
palette = 13=#ff9269
palette = 14=#84f088
palette = 15=#f6f7ec
background = #292520
foreground = #e5c7a9
cursor-color = #f6f7ec
cursor-text = #292520
selection-background = #121418
selection-foreground = #e5c7a9
`,
  "Electron Highlighter": `palette = 0=#15161f
palette = 1=#ff6c8d
palette = 2=#00ffc3
palette = 3=#ffd7a9
palette = 4=#77abff
palette = 5=#daa4f4
palette = 6=#00fdff
palette = 7=#778faf
palette = 8=#4a6789
palette = 9=#ff6c8d
palette = 10=#00ffc3
palette = 11=#ffd7a9
palette = 12=#77abff
palette = 13=#daa4f4
palette = 14=#00fdff
palette = 15=#c3cee2
background = #23283d
foreground = #a5b6d4
cursor-color = #a5b6d4
cursor-text = #1a1b27
selection-background = #25345a
selection-foreground = #a5b6d4
`,
  Elegant: `palette = 0=#0a1222
palette = 1=#ff0257
palette = 2=#85cc95
palette = 3=#ffcb8b
palette = 4=#8dabe1
palette = 5=#c792eb
palette = 6=#78ccf0
palette = 7=#ffffff
palette = 8=#575656
palette = 9=#ff0057
palette = 10=#85cc95
palette = 11=#ffcb8b
palette = 12=#8dabe1
palette = 13=#c792eb
palette = 14=#3facef
palette = 15=#ffffff
background = #292b31
foreground = #ced2d6
cursor-color = #00beff
cursor-text = #ffffff
selection-background = #d5d5d5
selection-foreground = #144385
`,
  Elemental: `palette = 0=#3c3c30
palette = 1=#98290f
palette = 2=#479a43
palette = 3=#7f7111
palette = 4=#497f7d
palette = 5=#7f4e2f
palette = 6=#387f58
palette = 7=#807974
palette = 8=#555445
palette = 9=#e0502a
palette = 10=#61e070
palette = 11=#d69927
palette = 12=#79d9d9
palette = 13=#cd7c54
palette = 14=#59d599
palette = 15=#fff1e9
background = #22211d
foreground = #807a74
cursor-color = #facb80
cursor-text = #161611
selection-background = #413829
selection-foreground = #facd77
`,
  Elementary: `palette = 0=#242424
palette = 1=#d71c15
palette = 2=#5aa513
palette = 3=#fdb40c
palette = 4=#134899
palette = 5=#e40038
palette = 6=#2595e1
palette = 7=#efefef
palette = 8=#4b4b4b
palette = 9=#fc1c18
palette = 10=#6bc219
palette = 11=#fec80e
palette = 12=#0955ff
palette = 13=#fb0050
palette = 14=#3ea8fc
palette = 15=#8c00ec
background = #181818
foreground = #efefef
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Embark: `palette = 0=#1e1c31
palette = 1=#f0719b
palette = 2=#a1efd3
palette = 3=#ffe9aa
palette = 4=#57c7ff
palette = 5=#c792ea
palette = 6=#87dfeb
palette = 7=#f8f8f2
palette = 8=#585273
palette = 9=#f02e6e
palette = 10=#2ce592
palette = 11=#ffb378
palette = 12=#1da0e2
palette = 13=#a742ea
palette = 14=#63f2f1
palette = 15=#a6b3cc
background = #1e1c31
foreground = #eeffff
cursor-color = #a1efd3
cursor-text = #1e1c31
selection-background = #fbfcfc
selection-foreground = #1e1c31
`,
  "Embers Dark": `palette = 0=#16130f
palette = 1=#826d57
palette = 2=#57826d
palette = 3=#6d8257
palette = 4=#6d5782
palette = 5=#82576d
palette = 6=#576d82
palette = 7=#a39a90
palette = 8=#5a5047
palette = 9=#828257
palette = 10=#464039
palette = 11=#50483f
palette = 12=#8a8075
palette = 13=#beb6ae
palette = 14=#825757
palette = 15=#dbd6d1
background = #16130f
foreground = #a39a90
cursor-color = #a39a90
cursor-text = #16130f
selection-background = #433b32
selection-foreground = #a39a90
`,
  ENCOM: `palette = 0=#000000
palette = 1=#9f0000
palette = 2=#008b00
palette = 3=#ffd000
palette = 4=#0081ff
palette = 5=#bc00ca
palette = 6=#008b8b
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff0000
palette = 10=#00ee00
palette = 11=#ffff00
palette = 12=#0000ff
palette = 13=#ff00ff
palette = 14=#00cdcd
palette = 15=#ffffff
background = #000000
foreground = #00a595
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #00a48c
selection-foreground = #3de1c9
`,
  Espresso: `palette = 0=#353535
palette = 1=#d25252
palette = 2=#a5c261
palette = 3=#ffc66d
palette = 4=#6c99bb
palette = 5=#d197d9
palette = 6=#bed6ff
palette = 7=#eeeeec
palette = 8=#606060
palette = 9=#f00c0c
palette = 10=#c2e075
palette = 11=#e1e48b
palette = 12=#8ab7d9
palette = 13=#efb5f7
palette = 14=#dcf4ff
palette = 15=#ffffff
background = #323232
foreground = #ffffff
cursor-color = #d6d6d6
cursor-text = #999999
selection-background = #5b5b5b
selection-foreground = #ffffff
`,
  "Espresso Libre": `palette = 0=#000000
palette = 1=#cc0000
palette = 2=#1a921c
palette = 3=#f0e53a
palette = 4=#0066ff
palette = 5=#c5656b
palette = 6=#06989a
palette = 7=#d3d7cf
palette = 8=#555753
palette = 9=#ef2929
palette = 10=#9aff87
palette = 11=#fffb5c
palette = 12=#43a8ed
palette = 13=#ff818a
palette = 14=#34e2e2
palette = 15=#eeeeec
background = #2a211c
foreground = #b8a898
cursor-color = #ffffff
cursor-text = #000000
selection-background = #c3dcff
selection-foreground = #ab9b8b
`,
  Everblush: `palette = 0=#232a2d
palette = 1=#e57474
palette = 2=#8ccf7e
palette = 3=#e5c76b
palette = 4=#67b0e8
palette = 5=#c47fd5
palette = 6=#6cbfbf
palette = 7=#b3b9b8
palette = 8=#464e50
palette = 9=#ef7e7e
palette = 10=#96d988
palette = 11=#f4d67a
palette = 12=#71baf2
palette = 13=#ce89df
palette = 14=#67cbe7
palette = 15=#bdc3c2
background = #141b1e
foreground = #dadada
cursor-color = #dadada
cursor-text = #141b1e
selection-background = #141b1e
selection-foreground = #dadada
`,
  "Everforest Dark Hard": `palette = 0=#7a8478
palette = 1=#e67e80
palette = 2=#a7c080
palette = 3=#dbbc7f
palette = 4=#7fbbb3
palette = 5=#d699b6
palette = 6=#83c092
palette = 7=#f2efdf
palette = 8=#a6b0a0
palette = 9=#f85552
palette = 10=#8da101
palette = 11=#dfa000
palette = 12=#3a94c5
palette = 13=#df69ba
palette = 14=#35a77c
palette = 15=#fffbef
background = #1e2326
foreground = #d3c6aa
cursor-color = #e69875
cursor-text = #4c3743
selection-background = #4c3743
selection-foreground = #d3c6aa
`,
  "Everforest Light Med": `palette = 0=#7a8478
palette = 1=#e67e80
palette = 2=#9ab373
palette = 3=#c1a266
palette = 4=#7fbbb3
palette = 5=#d699b6
palette = 6=#83c092
palette = 7=#b2af9f
palette = 8=#a6b0a0
palette = 9=#f85552
palette = 10=#8da101
palette = 11=#dfa000
palette = 12=#3a94c5
palette = 13=#df69ba
palette = 14=#35a77c
palette = 15=#fffbef
background = #efebd4
foreground = #5c6a72
cursor-color = #f57d26
cursor-text = #eaedc8
selection-background = #eaedc8
selection-foreground = #5c6a72
`,
  Fahrenheit: `palette = 0=#1d1d1d
palette = 1=#cda074
palette = 2=#9e744d
palette = 3=#fecf75
palette = 4=#7f0e0f
palette = 5=#734c4d
palette = 6=#979797
palette = 7=#ffffce
palette = 8=#404040
palette = 9=#fecea0
palette = 10=#cc734d
palette = 11=#fd9f4d
palette = 12=#cb4a05
palette = 13=#4e739f
palette = 14=#fed04d
palette = 15=#ffffff
background = #000000
foreground = #ffffce
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #4e739f
selection-foreground = #ffffce
`,
  Fairyfloss: `palette = 0=#040303
palette = 1=#f92672
palette = 2=#c2ffdf
palette = 3=#e6c000
palette = 4=#c2ffdf
palette = 5=#ffb8d1
palette = 6=#c5a3ff
palette = 7=#f8f8f0
palette = 8=#6090cb
palette = 9=#ff857f
palette = 10=#c2ffdf
palette = 11=#ffea00
palette = 12=#c2ffdf
palette = 13=#ffb8d1
palette = 14=#c5a3ff
palette = 15=#f8f8f0
background = #5a5475
foreground = #f8f8f2
cursor-color = #f8f8f0
cursor-text = #060709
selection-background = #8077a8
selection-foreground = #f6e1ce
`,
  "Farmhouse Dark": `palette = 0=#1d2027
palette = 1=#ba0004
palette = 2=#549d00
palette = 3=#c87300
palette = 4=#0049e6
palette = 5=#9f1b61
palette = 6=#1fb65c
palette = 7=#e8e4e1
palette = 8=#464d54
palette = 9=#eb0009
palette = 10=#7ac100
palette = 11=#ea9a00
palette = 12=#006efe
palette = 13=#bf3b7f
palette = 14=#19e062
palette = 15=#f4eef0
background = #1d2027
foreground = #e8e4e1
cursor-color = #006efe
cursor-text = #e8e4e1
selection-background = #4d5658
selection-foreground = #b3b1aa
`,
  "Farmhouse Light": `palette = 0=#1d2027
palette = 1=#8d0003
palette = 2=#3a7d00
palette = 3=#a95600
palette = 4=#092ccd
palette = 5=#820046
palette = 6=#229256
palette = 7=#a8a4a1
palette = 8=#394047
palette = 9=#eb0009
palette = 10=#7ac100
palette = 11=#ea9a00
palette = 12=#006efe
palette = 13=#bf3b7f
palette = 14=#00c649
palette = 15=#f4eef0
background = #e8e4e1
foreground = #1d2027
cursor-color = #006efe
cursor-text = #1d2027
selection-background = #b3b1aa
selection-foreground = #4d5658
`,
  Fideloper: `palette = 0=#292f33
palette = 1=#cb1e2d
palette = 2=#edb8ac
palette = 3=#b7ab9b
palette = 4=#2e78c2
palette = 5=#c0236f
palette = 6=#309186
palette = 7=#eae3ce
palette = 8=#496068
palette = 9=#d4605a
palette = 10=#d4605a
palette = 11=#a86671
palette = 12=#7c85c4
palette = 13=#5c5db2
palette = 14=#819090
palette = 15=#fcf4df
background = #292f33
foreground = #dbdae0
cursor-color = #d4605a
cursor-text = #fefff2
selection-background = #efb8ac
selection-foreground = #8c8c8c
`,
  "Firefly Traditional": `palette = 0=#000000
palette = 1=#c23720
palette = 2=#33bc26
palette = 3=#afad24
palette = 4=#5a63ff
palette = 5=#d53ad2
palette = 6=#33bbc7
palette = 7=#cccccc
palette = 8=#828282
palette = 9=#ff3b1e
palette = 10=#2ee720
palette = 11=#ecec16
palette = 12=#838dff
palette = 13=#ff5cfe
palette = 14=#29f0f0
palette = 15=#ebebeb
background = #000000
foreground = #f5f5f5
cursor-color = #00f900
cursor-text = #999999
selection-background = #cfeac6
selection-foreground = #000000
`,
  "Firefox Dev": `palette = 0=#002831
palette = 1=#e63853
palette = 2=#5eb83c
palette = 3=#a57706
palette = 4=#359ddf
palette = 5=#d75cff
palette = 6=#4b73a2
palette = 7=#dcdcdc
palette = 8=#26444d
palette = 9=#e1003f
palette = 10=#1d9000
palette = 11=#cd9409
palette = 12=#006fc0
palette = 13=#a200da
palette = 14=#005794
palette = 15=#e2e2e2
background = #0e1011
foreground = #7c8fa4
cursor-color = #708284
cursor-text = #002831
selection-background = #163c61
selection-foreground = #f2f5f9
`,
  Firewatch: `palette = 0=#585f6d
palette = 1=#d95360
palette = 2=#5ab977
palette = 3=#dfb563
palette = 4=#4d89c4
palette = 5=#d55119
palette = 6=#44a8b6
palette = 7=#e6e5ff
palette = 8=#585f6d
palette = 9=#d95360
palette = 10=#5ab977
palette = 11=#dfb563
palette = 12=#4c89c5
palette = 13=#d55119
palette = 14=#44a8b6
palette = 15=#e6e5ff
background = #1e2027
foreground = #9ba2b2
cursor-color = #f6f7ec
cursor-text = #b7b8a8
selection-background = #2f363e
selection-foreground = #7d8fa4
`,
  "Fish Tank": `palette = 0=#03073c
palette = 1=#c6004a
palette = 2=#acf157
palette = 3=#fecd5e
palette = 4=#525fb8
palette = 5=#986f82
palette = 6=#968763
palette = 7=#ecf0fc
palette = 8=#6c5b30
palette = 9=#da4b8a
palette = 10=#dbffa9
palette = 11=#fee6a9
palette = 12=#b2befa
palette = 13=#fda5cd
palette = 14=#a5bd86
palette = 15=#f6ffec
background = #232537
foreground = #ecf0fe
cursor-color = #fecd5e
cursor-text = #232537
selection-background = #fcf7e9
selection-foreground = #232537
`,
  Flat: `palette = 0=#222d3f
palette = 1=#a82320
palette = 2=#32a548
palette = 3=#e58d11
palette = 4=#3167ac
palette = 5=#781aa0
palette = 6=#2c9370
palette = 7=#b0b6ba
palette = 8=#475262
palette = 9=#d4312e
palette = 10=#2d9440
palette = 11=#e5be0c
palette = 12=#3c7dd2
palette = 13=#8230a7
palette = 14=#35b387
palette = 15=#e7eced
background = #002240
foreground = #2cc55d
cursor-color = #e5be0c
cursor-text = #ffffff
selection-background = #792b9c
selection-foreground = #ffffff
`,
  Flatland: `palette = 0=#1d1d19
palette = 1=#f18339
palette = 2=#9fd364
palette = 3=#f4ef6d
palette = 4=#5096be
palette = 5=#695abc
palette = 6=#d63865
palette = 7=#ffffff
palette = 8=#50504c
palette = 9=#d22a24
palette = 10=#a7d42c
palette = 11=#ff8949
palette = 12=#61b9d0
palette = 13=#695abc
palette = 14=#d63865
palette = 15=#ffffff
background = #1d1f21
foreground = #b8dbef
cursor-color = #708284
cursor-text = #002831
selection-background = #2b2a24
selection-foreground = #ffffff
`,
  "Flexoki Dark": `palette = 0=#100f0f
palette = 1=#d14d41
palette = 2=#879a39
palette = 3=#d0a215
palette = 4=#4385be
palette = 5=#ce5d97
palette = 6=#3aa99f
palette = 7=#878580
palette = 8=#575653
palette = 9=#af3029
palette = 10=#66800b
palette = 11=#ad8301
palette = 12=#205ea6
palette = 13=#a02f6f
palette = 14=#24837b
palette = 15=#cecdc3
background = #100f0f
foreground = #cecdc3
cursor-color = #cecdc3
cursor-text = #100f0f
selection-background = #403e3c
selection-foreground = #cecdc3
`,
  "Flexoki Light": `palette = 0=#100f0f
palette = 1=#af3029
palette = 2=#66800b
palette = 3=#ad8301
palette = 4=#205ea6
palette = 5=#a02f6f
palette = 6=#24837b
palette = 7=#6f6e69
palette = 8=#b7b5ac
palette = 9=#d14d41
palette = 10=#879a39
palette = 11=#d0a215
palette = 12=#4385be
palette = 13=#ce5d97
palette = 14=#3aa99f
palette = 15=#cecdc3
background = #fffcf0
foreground = #100f0f
cursor-color = #100f0f
cursor-text = #fffcf0
selection-background = #cecdc3
selection-foreground = #100f0f
`,
  Floraverse: `palette = 0=#08002e
palette = 1=#7e1a46
palette = 2=#5d731a
palette = 3=#cd751c
palette = 4=#1d6da1
palette = 5=#b7077e
palette = 6=#42a38c
palette = 7=#f3e0b8
palette = 8=#4c3866
palette = 9=#d02063
palette = 10=#b4ce59
palette = 11=#fac357
palette = 12=#40a4cf
palette = 13=#f12aae
palette = 14=#62caa8
palette = 15=#fff5db
background = #0e0d15
foreground = #dbd1b9
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #f3e0b8
selection-foreground = #08002e
`,
  "Forest Blue": `palette = 0=#333333
palette = 1=#f8818e
palette = 2=#92d3a2
palette = 3=#1a8e63
palette = 4=#8ed0ce
palette = 5=#5e468c
palette = 6=#31658c
palette = 7=#e2d8cd
palette = 8=#4a4a4a
palette = 9=#fb3d66
palette = 10=#6bb48d
palette = 11=#30c85a
palette = 12=#39a7a2
palette = 13=#7e62b3
palette = 14=#6096bf
palette = 15=#e2d8cd
background = #051519
foreground = #e2d8cd
cursor-color = #9e9ecb
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  Framer: `palette = 0=#141414
palette = 1=#ff5555
palette = 2=#98ec65
palette = 3=#ffcc33
palette = 4=#00aaff
palette = 5=#aa88ff
palette = 6=#88ddff
palette = 7=#cccccc
palette = 8=#414141
palette = 9=#ff8888
palette = 10=#b6f292
palette = 11=#ffd966
palette = 12=#33bbff
palette = 13=#cebbff
palette = 14=#bbecff
palette = 15=#ffffff
background = #111111
foreground = #777777
cursor-color = #fcdc08
cursor-text = #161616
selection-background = #666666
selection-foreground = #ffffff
`,
  "Front End Delight": `palette = 0=#242526
palette = 1=#f8511b
palette = 2=#565747
palette = 3=#fa771d
palette = 4=#2c70b7
palette = 5=#f02e4f
palette = 6=#3ca1a6
palette = 7=#adadad
palette = 8=#5fac6d
palette = 9=#f74319
palette = 10=#74ec4c
palette = 11=#fdc325
palette = 12=#3393ca
palette = 13=#e75e4f
palette = 14=#4fbce6
palette = 15=#8c735b
background = #1b1c1d
foreground = #adadad
cursor-color = #cdcdcd
cursor-text = #1b1c1d
selection-background = #ea6154
selection-foreground = #1b1c1d
`,
  "Fun Forrest": `palette = 0=#000000
palette = 1=#d6262b
palette = 2=#919c00
palette = 3=#be8a13
palette = 4=#4699a3
palette = 5=#8d4331
palette = 6=#da8213
palette = 7=#ddc265
palette = 8=#7f6a55
palette = 9=#e55a1c
palette = 10=#bfc65a
palette = 11=#ffcb1b
palette = 12=#7cc9cf
palette = 13=#d26349
palette = 14=#e6a96b
palette = 15=#ffeaa3
background = #251200
foreground = #dec165
cursor-color = #e5591c
cursor-text = #000000
selection-background = #e5591c
selection-foreground = #000000
`,
  Galaxy: `palette = 0=#000000
palette = 1=#f9555f
palette = 2=#21b089
palette = 3=#fef02a
palette = 4=#589df6
palette = 5=#944d95
palette = 6=#1f9ee7
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#fa8c8f
palette = 10=#35bb9a
palette = 11=#ffff55
palette = 12=#589df6
palette = 13=#e75699
palette = 14=#3979bc
palette = 15=#ffffff
background = #1d2837
foreground = #ffffff
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Galizur: `palette = 0=#223344
palette = 1=#aa1122
palette = 2=#33aa11
palette = 3=#ccaa22
palette = 4=#2255cc
palette = 5=#7755aa
palette = 6=#22bbdd
palette = 7=#8899aa
palette = 8=#556677
palette = 9=#ff1133
palette = 10=#33ff11
palette = 11=#ffdd33
palette = 12=#3377ff
palette = 13=#aa77ff
palette = 14=#33ddff
palette = 15=#bbccdd
background = #071317
foreground = #ddeeff
cursor-color = #ddeeff
cursor-text = #071317
selection-background = #071317
selection-foreground = #ddeeff
`,
  "Ghostty Default Style Dark": `palette = 0=#1d1f21
palette = 1=#cc6566
palette = 2=#b6bd68
palette = 3=#f0c674
palette = 4=#82a2be
palette = 5=#b294bb
palette = 6=#8abeb7
palette = 7=#c4c8c6
palette = 8=#666666
palette = 9=#d54e53
palette = 10=#b9ca4b
palette = 11=#e7c547
palette = 12=#7aa6da
palette = 13=#c397d8
palette = 14=#70c0b1
palette = 15=#eaeaea
background = #282c34
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #353a44
selection-background = #ffffff
selection-foreground = #282c34
`,
  GitHub: `palette = 0=#3e3e3e
palette = 1=#970b16
palette = 2=#07962a
palette = 3=#c5bb94
palette = 4=#003e8a
palette = 5=#e94691
palette = 6=#7cc4df
palette = 7=#b2b2b2
palette = 8=#666666
palette = 9=#de0000
palette = 10=#7ac895
palette = 11=#d7b600
palette = 12=#2e6cba
palette = 13=#f29592
palette = 14=#00c7cb
palette = 15=#ffffff
background = #f4f4f4
foreground = #3e3e3e
cursor-color = #3f3f3f
cursor-text = #f4f4f4
selection-background = #a9c1e2
selection-foreground = #535353
`,
  "GitHub Dark": `palette = 0=#000000
palette = 1=#f78166
palette = 2=#56d364
palette = 3=#e3b341
palette = 4=#6ca4f8
palette = 5=#db61a2
palette = 6=#2b7489
palette = 7=#ffffff
palette = 8=#4d4d4d
palette = 9=#f78166
palette = 10=#56d364
palette = 11=#e3b341
palette = 12=#6ca4f8
palette = 13=#db61a2
palette = 14=#2b7489
palette = 15=#ffffff
background = #101216
foreground = #8b949e
cursor-color = #c9d1d9
cursor-text = #101216
selection-background = #3b5070
selection-foreground = #ffffff
`,
  "GitHub Dark Colorblind": `palette = 0=#484f58
palette = 1=#ec8e2c
palette = 2=#58a6ff
palette = 3=#d29922
palette = 4=#58a6ff
palette = 5=#bc8cff
palette = 6=#39c5cf
palette = 7=#b1bac4
palette = 8=#6e7681
palette = 9=#fdac54
palette = 10=#79c0ff
palette = 11=#e3b341
palette = 12=#79c0ff
palette = 13=#d2a8ff
palette = 14=#56d4dd
palette = 15=#ffffff
background = #0d1117
foreground = #c9d1d9
cursor-color = #58a6ff
cursor-text = #98e6ff
selection-background = #c9d1d9
selection-foreground = #0d1117
`,
  "GitHub Dark Default": `palette = 0=#484f58
palette = 1=#ff7b72
palette = 2=#3fb950
palette = 3=#d29922
palette = 4=#58a6ff
palette = 5=#bc8cff
palette = 6=#39c5cf
palette = 7=#b1bac4
palette = 8=#6e7681
palette = 9=#ffa198
palette = 10=#56d364
palette = 11=#e3b341
palette = 12=#79c0ff
palette = 13=#d2a8ff
palette = 14=#56d4dd
palette = 15=#ffffff
background = #0d1117
foreground = #e6edf3
cursor-color = #2f81f7
cursor-text = #6fc1ff
selection-background = #e6edf3
selection-foreground = #0d1117
`,
  "GitHub Dark Dimmed": `palette = 0=#545d68
palette = 1=#f47067
palette = 2=#57ab5a
palette = 3=#c69026
palette = 4=#539bf5
palette = 5=#b083f0
palette = 6=#39c5cf
palette = 7=#909dab
palette = 8=#636e7b
palette = 9=#ff938a
palette = 10=#6bc46d
palette = 11=#daaa3f
palette = 12=#6cb6ff
palette = 13=#dcbdfb
palette = 14=#56d4dd
palette = 15=#cdd9e5
background = #22272e
foreground = #adbac7
cursor-color = #539bf5
cursor-text = #93dbff
selection-background = #adbac7
selection-foreground = #22272e
`,
  "GitHub Dark High Contrast": `palette = 0=#7a828e
palette = 1=#ff9492
palette = 2=#26cd4d
palette = 3=#f0b72f
palette = 4=#71b7ff
palette = 5=#cb9eff
palette = 6=#39c5cf
palette = 7=#d9dee3
palette = 8=#9ea7b3
palette = 9=#ffb1af
palette = 10=#4ae168
palette = 11=#f7c843
palette = 12=#91cbff
palette = 13=#dbb7ff
palette = 14=#56d4dd
palette = 15=#ffffff
background = #0a0c10
foreground = #f0f3f6
cursor-color = #71b7ff
cursor-text = #b1f7ff
selection-background = #f0f3f6
selection-foreground = #0a0c10
`,
  "GitHub Light Colorblind": `palette = 0=#24292f
palette = 1=#b35900
palette = 2=#0550ae
palette = 3=#4d2d00
palette = 4=#0969da
palette = 5=#8250df
palette = 6=#1b7c83
palette = 7=#6e7781
palette = 8=#57606a
palette = 9=#8a4600
palette = 10=#0969da
palette = 11=#633c01
palette = 12=#218bff
palette = 13=#a475f9
palette = 14=#3192aa
palette = 15=#8c959f
background = #ffffff
foreground = #24292f
cursor-color = #0969da
cursor-text = #3c9cff
selection-background = #24292f
selection-foreground = #ffffff
`,
  "GitHub Light Default": `palette = 0=#24292f
palette = 1=#cf222e
palette = 2=#116329
palette = 3=#4d2d00
palette = 4=#0969da
palette = 5=#8250df
palette = 6=#1b7c83
palette = 7=#6e7781
palette = 8=#57606a
palette = 9=#a40e26
palette = 10=#1a7f37
palette = 11=#633c01
palette = 12=#218bff
palette = 13=#a475f9
palette = 14=#3192aa
palette = 15=#8c959f
background = #ffffff
foreground = #1f2328
cursor-color = #0969da
cursor-text = #3c9cff
selection-background = #1f2328
selection-foreground = #ffffff
`,
  "GitHub Light High Contrast": `palette = 0=#0e1116
palette = 1=#a0111f
palette = 2=#024c1a
palette = 3=#3f2200
palette = 4=#0349b4
palette = 5=#622cbc
palette = 6=#1b7c83
palette = 7=#66707b
palette = 8=#4b535d
palette = 9=#86061d
palette = 10=#055d20
palette = 11=#4e2c00
palette = 12=#1168e3
palette = 13=#844ae7
palette = 14=#3192aa
palette = 15=#88929d
background = #ffffff
foreground = #0e1116
cursor-color = #0349b4
cursor-text = #367ce7
selection-background = #0e1116
selection-foreground = #ffffff
`,
  "GitLab Dark": `palette = 0=#000000
palette = 1=#f57f6c
palette = 2=#52b87a
palette = 3=#d99530
palette = 4=#7fb6ed
palette = 5=#f88aaf
palette = 6=#32c5d2
palette = 7=#ffffff
palette = 8=#666666
palette = 9=#fcb5aa
palette = 10=#91d4a8
palette = 11=#e9be74
palette = 12=#498dd1
palette = 13=#fcacc5
palette = 14=#5edee3
palette = 15=#ffffff
background = #28262b
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #bfbfbf
selection-background = #ad95e9
selection-foreground = #28262b
`,
  "GitLab Dark Grey": `palette = 0=#000000
palette = 1=#f57f6c
palette = 2=#52b87a
palette = 3=#d99530
palette = 4=#7fb6ed
palette = 5=#f88aaf
palette = 6=#32c5d2
palette = 7=#ffffff
palette = 8=#666666
palette = 9=#fcb5aa
palette = 10=#91d4a8
palette = 11=#e9be74
palette = 12=#498dd1
palette = 13=#fcacc5
palette = 14=#5edee3
palette = 15=#ffffff
background = #222222
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #bfbfbf
selection-background = #ad95e9
selection-foreground = #222222
`,
  "GitLab Light": `palette = 0=#303030
palette = 1=#a31700
palette = 2=#0a7f3d
palette = 3=#af551d
palette = 4=#006cd8
palette = 5=#583cac
palette = 6=#00798a
palette = 7=#303030
palette = 8=#303030
palette = 9=#a31700
palette = 10=#0a7f3d
palette = 11=#af551d
palette = 12=#006cd8
palette = 13=#583cac
palette = 14=#00798a
palette = 15=#303030
background = #fafaff
foreground = #303030
cursor-color = #303030
cursor-text = #565656
selection-background = #ad95e9
selection-foreground = #fafaff
`,
  Glacier: `palette = 0=#2e343c
palette = 1=#bd0f2f
palette = 2=#35a770
palette = 3=#fb9435
palette = 4=#1f5872
palette = 5=#bd2523
palette = 6=#778397
palette = 7=#ffffff
palette = 8=#404a55
palette = 9=#bd0f2f
palette = 10=#49e998
palette = 11=#fddf6e
palette = 12=#2a8bc1
palette = 13=#ea4727
palette = 14=#a0b6d3
palette = 15=#ffffff
background = #0c1115
foreground = #ffffff
cursor-color = #6c6c6c
cursor-text = #9f9f9f
selection-background = #bd2523
selection-foreground = #ffffff
`,
  Grape: `palette = 0=#2d283f
palette = 1=#ed2261
palette = 2=#1fa91b
palette = 3=#8ddc20
palette = 4=#487df4
palette = 5=#8d35c9
palette = 6=#3bdeed
palette = 7=#9e9ea0
palette = 8=#59516a
palette = 9=#f0729a
palette = 10=#53aa5e
palette = 11=#b2dc87
palette = 12=#a9bcec
palette = 13=#ad81c2
palette = 14=#9de3eb
palette = 15=#a288f7
background = #171423
foreground = #9f9fa1
cursor-color = #a288f7
cursor-text = #171422
selection-background = #493d70
selection-foreground = #171422
`,
  Grass: `palette = 0=#000000
palette = 1=#ff5959
palette = 2=#00bb00
palette = 3=#e7b000
palette = 4=#0000a3
palette = 5=#ee59bb
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#959595
palette = 9=#ff5959
palette = 10=#00bb00
palette = 11=#e7b000
palette = 12=#0000bb
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #13773d
foreground = #fff0a5
cursor-color = #d9744c
cursor-text = #ffffff
selection-background = #b64926
selection-foreground = #ffffff
`,
  "Grey Green": `palette = 0=#000000
palette = 1=#fe1414
palette = 2=#74ff00
palette = 3=#f1ff01
palette = 4=#00deff
palette = 5=#ff00f0
palette = 6=#00ffbc
palette = 7=#ffffff
palette = 8=#666666
palette = 9=#ff3939
palette = 10=#00ff44
palette = 11=#ffd100
palette = 12=#00afff
palette = 13=#ff008a
palette = 14=#00ffd3
palette = 15=#f5ecec
background = #002a1a
foreground = #ffffff
cursor-color = #fff400
cursor-text = #aeb1b0
selection-background = #517e50
selection-foreground = #e2e2e2
`,
  "Gruber Darker": `palette = 0=#181818
palette = 1=#ff0a36
palette = 2=#42dc00
palette = 3=#ffdb00
palette = 4=#92a7cb
palette = 5=#a095cb
palette = 6=#90aa9e
palette = 7=#e4e4e4
palette = 8=#54494e
palette = 9=#ff3851
palette = 10=#42dc00
palette = 11=#ffdb00
palette = 12=#92a7cb
palette = 13=#afafda
palette = 14=#90aa9e
palette = 15=#f5f5f5
background = #181818
foreground = #e4e4e4
cursor-color = #ffdb00
cursor-text = #a6a6a6
selection-background = #ffffff
selection-foreground = #54494e
`,
  "Gruvbox Dark": `palette = 0=#282828
palette = 1=#cc241d
palette = 2=#98971a
palette = 3=#d79921
palette = 4=#458588
palette = 5=#b16286
palette = 6=#689d6a
palette = 7=#a89984
palette = 8=#928374
palette = 9=#fb4934
palette = 10=#b8bb26
palette = 11=#fabd2f
palette = 12=#83a598
palette = 13=#d3869b
palette = 14=#8ec07c
palette = 15=#ebdbb2
background = #282828
foreground = #ebdbb2
cursor-color = #ebdbb2
cursor-text = #282828
selection-background = #665c54
selection-foreground = #ebdbb2
`,
  "Gruvbox Dark Hard": `palette = 0=#1d2021
palette = 1=#cc241d
palette = 2=#98971a
palette = 3=#d79921
palette = 4=#458588
palette = 5=#b16286
palette = 6=#689d6a
palette = 7=#a89984
palette = 8=#928374
palette = 9=#fb4934
palette = 10=#b8bb26
palette = 11=#fabd2f
palette = 12=#83a598
palette = 13=#d3869b
palette = 14=#8ec07c
palette = 15=#ebdbb2
background = #1d2021
foreground = #ebdbb2
cursor-color = #ebdbb2
cursor-text = #1d2021
selection-background = #665c54
selection-foreground = #ebdbb2
`,
  "Gruvbox Light": `palette = 0=#fbf1c7
palette = 1=#cc241d
palette = 2=#98971a
palette = 3=#d79921
palette = 4=#458588
palette = 5=#b16286
palette = 6=#689d6a
palette = 7=#7c6f64
palette = 8=#928374
palette = 9=#9d0006
palette = 10=#79740e
palette = 11=#b57614
palette = 12=#076678
palette = 13=#8f3f71
palette = 14=#427b58
palette = 15=#3c3836
background = #fbf1c7
foreground = #3c3836
cursor-color = #3c3836
cursor-text = #fbf1c7
selection-background = #3c3836
selection-foreground = #fbf1c7
`,
  "Gruvbox Light Hard": `palette = 0=#f9f5d7
palette = 1=#cc241d
palette = 2=#98971a
palette = 3=#d79921
palette = 4=#458588
palette = 5=#b16286
palette = 6=#689d6a
palette = 7=#7c6f64
palette = 8=#928374
palette = 9=#9d0006
palette = 10=#79740e
palette = 11=#b57614
palette = 12=#076678
palette = 13=#8f3f71
palette = 14=#427b58
palette = 15=#3c3836
background = #f9f5d7
foreground = #3c3836
cursor-color = #3c3836
cursor-text = #f9f5d7
selection-background = #3c3836
selection-foreground = #f9f5d7
`,
  "Gruvbox Material": `palette = 0=#141617
palette = 1=#ea6926
palette = 2=#c1d041
palette = 3=#eecf75
palette = 4=#6da3ec
palette = 5=#fd9bc1
palette = 6=#fe9d6e
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#d3573b
palette = 10=#c1d041
palette = 11=#eecf75
palette = 12=#2c86ff
palette = 13=#fd9bc1
palette = 14=#92a5df
palette = 15=#ffffff
background = #1d2021
foreground = #d4be98
cursor-color = #ffffff
cursor-text = #000000
selection-background = #2b2c3f
selection-foreground = #7cfb70
`,
  "Gruvbox Material Dark": `palette = 0=#282828
palette = 1=#ea6962
palette = 2=#a9b665
palette = 3=#d8a657
palette = 4=#7daea3
palette = 5=#d3869b
palette = 6=#89b482
palette = 7=#d4be98
palette = 8=#7c6f64
palette = 9=#ea6962
palette = 10=#a9b665
palette = 11=#d8a657
palette = 12=#7daea3
palette = 13=#d3869b
palette = 14=#89b482
palette = 15=#ddc7a1
background = #282828
foreground = #d4be98
cursor-color = #d4be98
cursor-text = #282828
selection-background = #d4be98
selection-foreground = #282828
`,
  "Gruvbox Material Light": `palette = 0=#fbf1c7
palette = 1=#c14a4a
palette = 2=#6c782e
palette = 3=#b47109
palette = 4=#45707a
palette = 5=#945e80
palette = 6=#4c7a5d
palette = 7=#654735
palette = 8=#a89984
palette = 9=#c14a4a
palette = 10=#6c782e
palette = 11=#b47109
palette = 12=#45707a
palette = 13=#945e80
palette = 14=#4c7a5d
palette = 15=#4f3829
background = #fbf1c7
foreground = #654735
cursor-color = #654735
cursor-text = #fbf1c7
selection-background = #654735
selection-foreground = #fbf1c7
`,
  Guezwhoz: `palette = 0=#333333
palette = 1=#e85181
palette = 2=#7ad694
palette = 3=#b7d074
palette = 4=#5aa0d6
palette = 5=#9a90e0
palette = 6=#58d6ce
palette = 7=#d9d9d9
palette = 8=#808080
palette = 9=#e85181
palette = 10=#afd7af
palette = 11=#d1ed85
palette = 12=#64b2ed
palette = 13=#a398ed
palette = 14=#61ede4
palette = 15=#ededed
background = #1d1d1d
foreground = #d9d9d9
cursor-color = #99d4b1
cursor-text = #1d1d1d
selection-background = #245354
selection-foreground = #58d6ce
`,
  Hacktober: `palette = 0=#191918
palette = 1=#b34538
palette = 2=#587744
palette = 3=#d08949
palette = 4=#206ec5
palette = 5=#864651
palette = 6=#ac9166
palette = 7=#f1eee7
palette = 8=#464444
palette = 9=#b33323
palette = 10=#42824a
palette = 11=#c75a22
palette = 12=#5389c5
palette = 13=#e795a5
palette = 14=#ebc587
palette = 15=#ffffff
background = #141414
foreground = #c9c9c9
cursor-color = #c9c9c9
cursor-text = #141414
selection-background = #141414
selection-foreground = #c9c9c9
`,
  Hardcore: `palette = 0=#1b1d1e
palette = 1=#f92672
palette = 2=#a6e22e
palette = 3=#fd971f
palette = 4=#66d9ef
palette = 5=#9e6ffe
palette = 6=#5e7175
palette = 7=#ccccc6
palette = 8=#505354
palette = 9=#ff669d
palette = 10=#beed5f
palette = 11=#e6db74
palette = 12=#66d9ef
palette = 13=#9e6ffe
palette = 14=#a3babf
palette = 15=#f8f8f2
background = #121212
foreground = #a0a0a0
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #453b39
selection-foreground = #b6bbc0
`,
  Harper: `palette = 0=#010101
palette = 1=#f8b63f
palette = 2=#7fb5e1
palette = 3=#d6da25
palette = 4=#489e48
palette = 5=#b296c6
palette = 6=#f5bfd7
palette = 7=#a8a49d
palette = 8=#726e6a
palette = 9=#f8b63f
palette = 10=#7fb5e1
palette = 11=#d6da25
palette = 12=#489e48
palette = 13=#b296c6
palette = 14=#f5bfd7
palette = 15=#fefbea
background = #010101
foreground = #a8a49d
cursor-color = #a8a49d
cursor-text = #010101
selection-background = #5a5753
selection-foreground = #a8a49d
`,
  "Havn Daggry": `palette = 0=#1f2842
palette = 1=#985248
palette = 2=#577159
palette = 3=#be6b00
palette = 4=#3a577d
palette = 5=#7c5c97
palette = 6=#925780
palette = 7=#b0b5c5
palette = 8=#1f2842
palette = 9=#cc4a35
palette = 10=#719679
palette = 11=#f1a527
palette = 12=#6089c0
palette = 13=#7d7396
palette = 14=#aa869d
palette = 15=#d6dbeb
background = #f8f9fb
foreground = #3b4a7a
cursor-color = #226c4f
cursor-text = #d6dbeb
selection-background = #cfe9dd
selection-foreground = #313c64
`,
  "Havn Skumring": `palette = 0=#252c47
palette = 1=#ea563e
palette = 2=#6ead7b
palette = 3=#f8b330
palette = 4=#596cf7
palette = 5=#7c719e
palette = 6=#d588c1
palette = 7=#dce0ee
palette = 8=#38425c
palette = 9=#d17264
palette = 10=#8c9e8f
palette = 11=#eac58c
palette = 12=#5186cb
palette = 13=#9b7cee
palette = 14=#d17ab6
palette = 15=#fff6e1
background = #111522
foreground = #d6dbeb
cursor-color = #277a6f
cursor-text = #e0e4f3
selection-background = #2b514b
selection-foreground = #dce0ef
`,
  "HaX0R Blue": `palette = 0=#010921
palette = 1=#10b6ff
palette = 2=#10b6ff
palette = 3=#10b6ff
palette = 4=#10b6ff
palette = 5=#10b6ff
palette = 6=#10b6ff
palette = 7=#fafafa
palette = 8=#484157
palette = 9=#00b3f7
palette = 10=#00b3f7
palette = 11=#00b3f7
palette = 12=#00b3f7
palette = 13=#00b3f7
palette = 14=#00b3f7
palette = 15=#fefefe
background = #010515
foreground = #11b7ff
cursor-color = #10b6ff
cursor-text = #ffffff
selection-background = #c1e4ff
selection-foreground = #a9a9a9
`,
  "HaX0R Gr33N": `palette = 0=#001f0b
palette = 1=#15d00d
palette = 2=#15d00d
palette = 3=#15d00d
palette = 4=#15d00d
palette = 5=#15d00d
palette = 6=#15d00d
palette = 7=#fafafa
palette = 8=#334843
palette = 9=#19e20e
palette = 10=#19e20e
palette = 11=#19e20e
palette = 12=#19e20e
palette = 13=#19e20e
palette = 14=#19e20e
palette = 15=#fefefe
background = #020f01
foreground = #16b10e
cursor-color = #15d00d
cursor-text = #ffffff
selection-background = #d4ffc1
selection-foreground = #b0b0b0
`,
  "HaX0R R3D": `palette = 0=#1f0000
palette = 1=#b00d0d
palette = 2=#b00d0d
palette = 3=#b00d0d
palette = 4=#b00d0d
palette = 5=#b00d0d
palette = 6=#b00d0d
palette = 7=#fafafa
palette = 8=#554040
palette = 9=#ff1111
palette = 10=#ff1010
palette = 11=#ff1010
palette = 12=#ff1010
palette = 13=#ff1010
palette = 14=#ff1010
palette = 15=#fefefe
background = #200101
foreground = #b10e0e
cursor-color = #b00d0d
cursor-text = #ffffff
selection-background = #ebc1ff
selection-foreground = #979797
`,
  Heeler: `palette = 0=#000000
palette = 1=#e44c2e
palette = 2=#bdd100
palette = 3=#f4ce65
palette = 4=#5ba5f2
palette = 5=#ff95c2
palette = 6=#ff9763
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#e44c2e
palette = 10=#bdd100
palette = 11=#f4ce65
palette = 12=#0088ff
palette = 13=#ff95c2
palette = 14=#8da6e4
palette = 15=#ffffff
background = #211f46
foreground = #fdfdfd
cursor-color = #ffffff
cursor-text = #000000
selection-background = #2b2c41
selection-foreground = #2eff58
`,
  Highway: `palette = 0=#000000
palette = 1=#d00e18
palette = 2=#138034
palette = 3=#ffcb3e
palette = 4=#006bb3
palette = 5=#783482
palette = 6=#455271
palette = 7=#ededed
palette = 8=#5d504a
palette = 9=#f07e18
palette = 10=#b1d130
palette = 11=#fff120
palette = 12=#4fc2fd
palette = 13=#de0071
palette = 14=#5d504a
palette = 15=#ffffff
background = #222225
foreground = #ededed
cursor-color = #e0d9b9
cursor-text = #1f192a
selection-background = #384564
selection-foreground = #ededed
`,
  "Hipster Green": `palette = 0=#000000
palette = 1=#b6214a
palette = 2=#00a600
palette = 3=#bfbf00
palette = 4=#246eb2
palette = 5=#b200b2
palette = 6=#00a6b2
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#e50000
palette = 10=#86a93e
palette = 11=#e5e500
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00e5e5
palette = 15=#e5e5e5
background = #100b05
foreground = #84c138
cursor-color = #23ff18
cursor-text = #ff0018
selection-background = #083905
selection-foreground = #ffffff
`,
  Hivacruz: `palette = 0=#202746
palette = 1=#c94922
palette = 2=#ac9739
palette = 3=#c08b30
palette = 4=#3d8fd1
palette = 5=#6679cc
palette = 6=#22a2c9
palette = 7=#979db4
palette = 8=#6b7394
palette = 9=#c76b29
palette = 10=#73ad43
palette = 11=#5e6687
palette = 12=#898ea4
palette = 13=#dfe2f1
palette = 14=#9c637a
palette = 15=#f5f7ff
background = #132638
foreground = #ede4e4
cursor-color = #979db4
cursor-text = #202746
selection-background = #5e6687
selection-foreground = #979db4
`,
  Homebrew: `palette = 0=#000000
palette = 1=#990000
palette = 2=#00a600
palette = 3=#999900
palette = 4=#0d0dbf
palette = 5=#b200b2
palette = 6=#00a6b2
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#e50000
palette = 10=#00d900
palette = 11=#e5e500
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00e5e5
palette = 15=#e5e5e5
background = #000000
foreground = #00ff00
cursor-color = #23ff18
cursor-text = #ff0018
selection-background = #083905
selection-foreground = #ffffff
`,
  Hopscotch: `palette = 0=#322931
palette = 1=#dd464c
palette = 2=#8fc13e
palette = 3=#fdcc59
palette = 4=#1290bf
palette = 5=#c85e7c
palette = 6=#149b93
palette = 7=#b9b5b8
palette = 8=#797379
palette = 9=#fd8b19
palette = 10=#5c545c
palette = 11=#5c545b
palette = 12=#989498
palette = 13=#d5d3d5
palette = 14=#b33508
palette = 15=#ffffff
background = #322931
foreground = #b9b5b8
cursor-color = #b9b5b8
cursor-text = #322931
selection-background = #5c545b
selection-foreground = #b9b5b8
`,
  "Hopscotch.256": `palette = 0=#322931
palette = 1=#dd464c
palette = 2=#8fc13e
palette = 3=#fdcc59
palette = 4=#1290bf
palette = 5=#c85e7c
palette = 6=#149b93
palette = 7=#b9b5b8
palette = 8=#797379
palette = 9=#dd464c
palette = 10=#8fc13e
palette = 11=#fdcc59
palette = 12=#1290bf
palette = 13=#c85e7c
palette = 14=#149b93
palette = 15=#ffffff
background = #322931
foreground = #b9b5b8
cursor-color = #b9b5b8
cursor-text = #322931
selection-background = #5c545b
selection-foreground = #b9b5b8
`,
  Horizon: `palette = 0=#000000
palette = 1=#e95678
palette = 2=#29d398
palette = 3=#fab795
palette = 4=#26bbd9
palette = 5=#ee64ac
palette = 6=#59e1e3
palette = 7=#e5e5e5
palette = 8=#666666
palette = 9=#ec6a88
palette = 10=#3fdaa4
palette = 11=#fbc3a7
palette = 12=#3fc4de
palette = 13=#f075b5
palette = 14=#6be4e6
palette = 15=#e5e5e5
background = #1c1e26
foreground = #d5d8da
cursor-color = #6c6f93
cursor-text = #9fa2c6
selection-background = #6c6f93
selection-foreground = #1c1e26
`,
  "Horizon Bright": `palette = 0=#16161d
palette = 1=#fc4777
palette = 2=#00ca86
palette = 3=#f2a682
palette = 4=#00bedd
palette = 5=#ff58b1
palette = 6=#00cdcb
palette = 7=#bfafac
palette = 8=#1a1c24
palette = 9=#fe5f87
palette = 10=#00d193
palette = 11=#e5a688
palette = 12=#00c9e2
palette = 13=#ff6cba
palette = 14=#00c3c2
palette = 15=#fff2ef
background = #fdf0ed
foreground = #16161d
cursor-color = #d3a89d
cursor-text = #ffe8dc
selection-background = #f9cec3
selection-foreground = #16161d
`,
  "Hot Dog Stand": `palette = 0=#000000
palette = 1=#ffff54
palette = 2=#ffff54
palette = 3=#ffff54
palette = 4=#000000
palette = 5=#ffff54
palette = 6=#ffffff
palette = 7=#c6c6c6
palette = 8=#000000
palette = 9=#ffff54
palette = 10=#ffff54
palette = 11=#ffff54
palette = 12=#000000
palette = 13=#ffff54
palette = 14=#ffffff
palette = 15=#c6c6c6
background = #ea3323
foreground = #ffffff
cursor-color = #ffff54
cursor-text = #ea3323
selection-background = #ffff54
selection-foreground = #000000
`,
  "Hot Dog Stand (Mustard)": `palette = 0=#000000
palette = 1=#ea3323
palette = 2=#ea3323
palette = 3=#ea3323
palette = 4=#000000
palette = 5=#ea3323
palette = 6=#000000
palette = 7=#b9b9b9
palette = 8=#000000
palette = 9=#ea3323
palette = 10=#ea3323
palette = 11=#ea3323
palette = 12=#000000
palette = 13=#ea3323
palette = 14=#000000
palette = 15=#c6c6c6
background = #ffff54
foreground = #000000
cursor-color = #ea3323
cursor-text = #ffff54
selection-background = #ea3323
selection-foreground = #ffffff
`,
  Hurtado: `palette = 0=#575757
palette = 1=#ff1b00
palette = 2=#a5e055
palette = 3=#fbe74a
palette = 4=#496487
palette = 5=#fd5ff1
palette = 6=#86e9fe
palette = 7=#cbcccb
palette = 8=#404040
palette = 9=#d51d00
palette = 10=#a5df55
palette = 11=#fbe84a
palette = 12=#89beff
palette = 13=#c001c1
palette = 14=#86eafe
palette = 15=#dbdbdb
background = #000000
foreground = #dbdbdb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Hybrid: `palette = 0=#2a2e33
palette = 1=#b84d51
palette = 2=#b3bf5a
palette = 3=#e4b55e
palette = 4=#6e90b0
palette = 5=#a17eac
palette = 6=#7fbfb4
palette = 7=#b5b9b6
palette = 8=#434548
palette = 9=#8d2e32
palette = 10=#798431
palette = 11=#e58a50
palette = 12=#4b6b88
palette = 13=#6e5079
palette = 14=#4d7b74
palette = 15=#5a626a
background = #161719
foreground = #b7bcba
cursor-color = #b7bcba
cursor-text = #1e1f22
selection-background = #1e1f22
selection-foreground = #b7bcba
`,
  "IBM 5153 CGA": `palette = 0=#141414
palette = 1=#d03333
palette = 2=#1bd01b
palette = 3=#d08c1b
palette = 4=#1b1bd0
palette = 5=#d01bd0
palette = 6=#1bd0d0
palette = 7=#cecece
palette = 8=#4e4e4e
palette = 9=#dc4e4e
palette = 10=#4edc4e
palette = 11=#f3f34e
palette = 12=#4e4edc
palette = 13=#f34ef3
palette = 14=#4ef3f3
palette = 15=#ffffff
background = #141414
foreground = #cecece
cursor-color = #cecece
cursor-text = #141414
selection-background = #cecece
selection-foreground = #141414
`,
  "IBM 5153 CGA (Black)": `palette = 0=#000000
palette = 1=#c40000
palette = 2=#00c400
palette = 3=#c47e00
palette = 4=#0000c4
palette = 5=#c400c4
palette = 6=#00c4c4
palette = 7=#c4c4c4
palette = 8=#4e4e4e
palette = 9=#dc4e4e
palette = 10=#4edc4e
palette = 11=#f3f34e
palette = 12=#4e4edc
palette = 13=#f34ef3
palette = 14=#4ef3f3
palette = 15=#ffffff
background = #000000
foreground = #c4c4c4
cursor-color = #c4c4c4
cursor-text = #000000
selection-background = #c4c4c4
selection-foreground = #000000
`,
  "IC Green PPL": `palette = 0=#014401
palette = 1=#ff2736
palette = 2=#41a638
palette = 3=#76a831
palette = 4=#2ec3b9
palette = 5=#50a096
palette = 6=#3ca078
palette = 7=#e6fef2
palette = 8=#106910
palette = 9=#b4fa5c
palette = 10=#aefb86
palette = 11=#dafa87
palette = 12=#2efaeb
palette = 13=#50fafa
palette = 14=#3cfac8
palette = 15=#e0f1dc
background = #2c2c2c
foreground = #e0f1dc
cursor-color = #47fa6b
cursor-text = #292929
selection-background = #116b41
selection-foreground = #e0f1dc
`,
  "IC Orange PPL": `palette = 0=#000000
palette = 1=#c13900
palette = 2=#a4a900
palette = 3=#caaf00
palette = 4=#bd6d00
palette = 5=#fc5e00
palette = 6=#f79500
palette = 7=#ffc88a
palette = 8=#6a4f2a
palette = 9=#ff8c68
palette = 10=#f6ff40
palette = 11=#ffe36e
palette = 12=#ffbe55
palette = 13=#fc874f
palette = 14=#c69752
palette = 15=#fafaff
background = #262626
foreground = #ffcb83
cursor-color = #fc531d
cursor-text = #ffc88a
selection-background = #c14020
selection-foreground = #ffc88a
`,
  "Iceberg Dark": `palette = 0=#1e2132
palette = 1=#e27878
palette = 2=#b4be82
palette = 3=#e2a478
palette = 4=#84a0c6
palette = 5=#a093c7
palette = 6=#89b8c2
palette = 7=#c6c8d1
palette = 8=#6b7089
palette = 9=#e98989
palette = 10=#c0ca8e
palette = 11=#e9b189
palette = 12=#91acd1
palette = 13=#ada0d3
palette = 14=#95c4ce
palette = 15=#d2d4de
background = #161821
foreground = #c6c8d1
cursor-color = #c6c8d1
cursor-text = #161821
selection-background = #c6c8d1
selection-foreground = #161821
`,
  "Iceberg Light": `palette = 0=#dcdfe7
palette = 1=#cc517a
palette = 2=#668e3d
palette = 3=#c57339
palette = 4=#2d539e
palette = 5=#7759b4
palette = 6=#3f83a6
palette = 7=#33374c
palette = 8=#8389a3
palette = 9=#cc3768
palette = 10=#598030
palette = 11=#b6662d
palette = 12=#22478e
palette = 13=#6845ad
palette = 14=#327698
palette = 15=#262a3f
background = #e8e9ec
foreground = #33374c
cursor-color = #33374c
cursor-text = #e8e9ec
selection-background = #33374c
selection-foreground = #e8e9ec
`,
  Idea: `palette = 0=#adadad
palette = 1=#fc5256
palette = 2=#98b61c
palette = 3=#ccb444
palette = 4=#437ee7
palette = 5=#9d74b0
palette = 6=#248887
palette = 7=#4b4b4b
palette = 8=#ffffff
palette = 9=#fc7072
palette = 10=#98b61c
palette = 11=#ffff0b
palette = 12=#6c9ced
palette = 13=#fc7eff
palette = 14=#248887
palette = 15=#181818
background = #202020
foreground = #adadad
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #44475a
selection-foreground = #ffffff
`,
  "Idle Toes": `palette = 0=#323232
palette = 1=#d25252
palette = 2=#7fe173
palette = 3=#ffc66d
palette = 4=#4099ff
palette = 5=#f680ff
palette = 6=#bed6ff
palette = 7=#eeeeec
palette = 8=#606060
palette = 9=#f07070
palette = 10=#9dff91
palette = 11=#ffe48b
palette = 12=#5eb7f7
palette = 13=#ff9dff
palette = 14=#dcf4ff
palette = 15=#ffffff
background = #323232
foreground = #ffffff
cursor-color = #d6d6d6
cursor-text = #000000
selection-background = #5b5b5b
selection-foreground = #000000
`,
  "IR Black": `palette = 0=#4f4f4f
palette = 1=#fa6c60
palette = 2=#a8ff60
palette = 3=#fffeb7
palette = 4=#96cafe
palette = 5=#fa73fd
palette = 6=#c6c5fe
palette = 7=#efedef
palette = 8=#7b7b7b
palette = 9=#fcb6b0
palette = 10=#cfffab
palette = 11=#ffffcc
palette = 12=#b5dcff
palette = 13=#fb9cfe
palette = 14=#e0e0fe
palette = 15=#ffffff
background = #000000
foreground = #f1f1f1
cursor-color = #808080
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "IRIX Console": `palette = 0=#1a1919
palette = 1=#d42426
palette = 2=#37a327
palette = 3=#c29d28
palette = 4=#0739e2
palette = 5=#911f9c
palette = 6=#4497df
palette = 7=#cccccc
palette = 8=#767676
palette = 9=#f34f59
palette = 10=#45c731
palette = 11=#f9f2a7
palette = 12=#4079ff
palette = 13=#c31ba2
palette = 14=#6ed7d7
palette = 15=#f2f2f2
background = #0c0c0c
foreground = #f2f2f2
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #c2deff
selection-foreground = #000000
`,
  "IRIX Terminal": `palette = 0=#1a1919
palette = 1=#ff2b1e
palette = 2=#57ff3d
palette = 3=#ffff44
palette = 4=#0004ff
palette = 5=#ff2cff
palette = 6=#56ffff
palette = 7=#ffffff
palette = 8=#ffff44
palette = 9=#ffff44
palette = 10=#ffff44
palette = 11=#fffc72
palette = 12=#ffff44
palette = 13=#ffff44
palette = 14=#ffff44
palette = 15=#ffff44
background = #000043
foreground = #f2f2f2
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #c2deff
selection-foreground = #000000
`,
  "iTerm2 Dark Background": `palette = 0=#000000
palette = 1=#c91b00
palette = 2=#00c200
palette = 3=#c7c400
palette = 4=#0225c7
palette = 5=#ca30c7
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#5ffa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#ff77ff
palette = 14=#60fdff
palette = 15=#ffffff
background = #000000
foreground = #c7c7c7
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #c1deff
selection-foreground = #000000
`,
  "iTerm2 Default": `palette = 0=#000000
palette = 1=#c91b00
palette = 2=#00c200
palette = 3=#c7c400
palette = 4=#2225c4
palette = 5=#ca30c7
palette = 6=#00c5c7
palette = 7=#ffffff
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#5ffa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#ff77ff
palette = 14=#60fdff
palette = 15=#ffffff
background = #000000
foreground = #ffffff
cursor-color = #e5e5e5
cursor-text = #000000
selection-background = #c1deff
selection-foreground = #000000
`,
  "iTerm2 Light Background": `palette = 0=#000000
palette = 1=#c91b00
palette = 2=#00c200
palette = 3=#c7c400
palette = 4=#0225c7
palette = 5=#ca30c7
palette = 6=#00c5c7
palette = 7=#bababa
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#39d442
palette = 11=#ccc934
palette = 12=#6871ff
palette = 13=#ff77ff
palette = 14=#3ad7d9
palette = 15=#ffffff
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  "iTerm2 Pastel Dark Background": `palette = 0=#626262
palette = 1=#ff8373
palette = 2=#b4fb73
palette = 3=#fffdc3
palette = 4=#a5d5fe
palette = 5=#ff90fe
palette = 6=#d1d1fe
palette = 7=#f1f1f1
palette = 8=#8f8f8f
palette = 9=#ffc4be
palette = 10=#d6fcba
palette = 11=#fffed5
palette = 12=#c2e3ff
palette = 13=#ffb2fe
palette = 14=#e6e6fe
palette = 15=#ffffff
background = #000000
foreground = #c7c7c7
cursor-color = #ffb473
cursor-text = #8c8c8c
selection-background = #454d96
selection-foreground = #f4f4f4
`,
  "iTerm2 Smoooooth": `palette = 0=#14191e
palette = 1=#b43c2a
palette = 2=#00c200
palette = 3=#c7c400
palette = 4=#2744c7
palette = 5=#c040be
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#dd7975
palette = 10=#58e790
palette = 11=#ece100
palette = 12=#a7abf2
palette = 13=#e17ee1
palette = 14=#60fdff
palette = 15=#ffffff
background = #15191f
foreground = #dcdcdc
cursor-color = #ffffff
cursor-text = #000000
selection-background = #b3d7ff
selection-foreground = #000000
`,
  "iTerm2 Solarized Dark": `palette = 0=#073642
palette = 1=#dc322f
palette = 2=#859900
palette = 3=#b58900
palette = 4=#268bd2
palette = 5=#d33682
palette = 6=#2aa198
palette = 7=#eee8d5
palette = 8=#335e69
palette = 9=#cb4b16
palette = 10=#586e75
palette = 11=#657b83
palette = 12=#839496
palette = 13=#6c71c4
palette = 14=#93a1a1
palette = 15=#fdf6e3
background = #002b36
foreground = #839496
cursor-color = #839496
cursor-text = #073642
selection-background = #073642
selection-foreground = #93a1a1
`,
  "iTerm2 Solarized Light": `palette = 0=#073642
palette = 1=#dc322f
palette = 2=#859900
palette = 3=#b58900
palette = 4=#268bd2
palette = 5=#d33682
palette = 6=#2aa198
palette = 7=#bbb5a2
palette = 8=#002b36
palette = 9=#cb4b16
palette = 10=#586e75
palette = 11=#657b83
palette = 12=#839496
palette = 13=#6c71c4
palette = 14=#93a1a1
palette = 15=#fdf6e3
background = #fdf6e3
foreground = #657b83
cursor-color = #657b83
cursor-text = #eee8d5
selection-background = #eee8d5
selection-foreground = #586e75
`,
  "iTerm2 Tango Dark": `palette = 0=#000000
palette = 1=#d81e00
palette = 2=#5ea702
palette = 3=#cfae00
palette = 4=#427ab3
palette = 5=#89658e
palette = 6=#00a7aa
palette = 7=#dbded8
palette = 8=#686a66
palette = 9=#f54235
palette = 10=#99e343
palette = 11=#fdeb61
palette = 12=#84b0d8
palette = 13=#bc94b7
palette = 14=#37e6e8
palette = 15=#f1f1f0
background = #000000
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #000000
selection-background = #c1deff
selection-foreground = #000000
`,
  "iTerm2 Tango Light": `palette = 0=#000000
palette = 1=#d81e00
palette = 2=#5ea702
palette = 3=#cfae00
palette = 4=#427ab3
palette = 5=#89658e
palette = 6=#00a7aa
palette = 7=#c1c4be
palette = 8=#686a66
palette = 9=#f54235
palette = 10=#8cd636
palette = 11=#d7c53b
palette = 12=#84b0d8
palette = 13=#bc94b7
palette = 14=#1eccce
palette = 15=#f1f1f0
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  "Jackie Brown": `palette = 0=#2c1d16
palette = 1=#ef5734
palette = 2=#2baf2b
palette = 3=#bebf00
palette = 4=#246eb2
palette = 5=#d05ec1
palette = 6=#00acee
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#e50000
palette = 10=#86a93e
palette = 11=#e5e500
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00e5e5
palette = 15=#e5e5e5
background = #2c1d16
foreground = #ffcc2f
cursor-color = #23ff18
cursor-text = #ff0018
selection-background = #af8d21
selection-foreground = #ffffff
`,
  Japanesque: `palette = 0=#343935
palette = 1=#cf3f61
palette = 2=#7bb75b
palette = 3=#e9b32a
palette = 4=#4c9ad4
palette = 5=#a57fc4
palette = 6=#389aad
palette = 7=#fafaf6
palette = 8=#595b59
palette = 9=#d18fa6
palette = 10=#767f2c
palette = 11=#78592f
palette = 12=#135979
palette = 13=#604291
palette = 14=#76bbca
palette = 15=#b2b5ae
background = #1e1e1e
foreground = #f7f6ec
cursor-color = #edcf4f
cursor-text = #343935
selection-background = #175877
selection-foreground = #f7f6ec
`,
  Jellybeans: `palette = 0=#929292
palette = 1=#e27373
palette = 2=#94b979
palette = 3=#ffba7b
palette = 4=#97bedc
palette = 5=#e1c0fa
palette = 6=#00988e
palette = 7=#dedede
palette = 8=#bdbdbd
palette = 9=#ffa1a1
palette = 10=#bddeab
palette = 11=#ffdca0
palette = 12=#b1d8f6
palette = 13=#fbdaff
palette = 14=#1ab2a8
palette = 15=#ffffff
background = #121212
foreground = #dedede
cursor-color = #ffa560
cursor-text = #ffffff
selection-background = #474e91
selection-foreground = #f4f4f4
`,
  "JetBrains Darcula": `palette = 0=#000000
palette = 1=#fa5355
palette = 2=#126e00
palette = 3=#c2c300
palette = 4=#4581eb
palette = 5=#fa54ff
palette = 6=#33c2c1
palette = 7=#adadad
palette = 8=#555555
palette = 9=#fb7172
palette = 10=#67ff4f
palette = 11=#ffff00
palette = 12=#6d9df1
palette = 13=#fb82ff
palette = 14=#60d3d1
palette = 15=#eeeeee
background = #202020
foreground = #adadad
cursor-color = #ffffff
cursor-text = #000000
selection-background = #1a3272
selection-foreground = #adadad
`,
  Jubi: `palette = 0=#3b3750
palette = 1=#cf7b98
palette = 2=#90a94b
palette = 3=#6ebfc0
palette = 4=#576ea6
palette = 5=#bc4f68
palette = 6=#75a7d2
palette = 7=#c3d3de
palette = 8=#a874ce
palette = 9=#de90ab
palette = 10=#bcdd61
palette = 11=#87e9ea
palette = 12=#8c9fcd
palette = 13=#e16c87
palette = 14=#b7c9ef
palette = 15=#d5e5f1
background = #262b33
foreground = #c3d3de
cursor-color = #c3d3de
cursor-text = #1e1b2e
selection-background = #5b5184
selection-foreground = #1e1b2e
`,
  "Kanagawa Dragon": `palette = 0=#0d0c0c
palette = 1=#c4746e
palette = 2=#8a9a7b
palette = 3=#c4b28a
palette = 4=#8ba4b0
palette = 5=#a292a3
palette = 6=#8ea4a2
palette = 7=#c8c093
palette = 8=#a6a69c
palette = 9=#e46876
palette = 10=#87a987
palette = 11=#e6c384
palette = 12=#7fb4ca
palette = 13=#938aa9
palette = 14=#7aa89f
palette = 15=#c5c9c5
background = #181616
foreground = #c5c9c5
cursor-color = #c8c093
cursor-text = #181616
selection-background = #c5c9c5
selection-foreground = #181616
`,
  "Kanagawa Lotus": `palette = 0=#1f1f28
palette = 1=#c84053
palette = 2=#6f894e
palette = 3=#77713f
palette = 4=#4d699b
palette = 5=#b35b79
palette = 6=#597b75
palette = 7=#545464
palette = 8=#8a8980
palette = 9=#d7474b
palette = 10=#6e915f
palette = 11=#836f4a
palette = 12=#6693bf
palette = 13=#624c83
palette = 14=#5e857a
palette = 15=#43436c
background = #f2ecbc
foreground = #545464
cursor-color = #43436c
cursor-text = #f2ecbc
selection-background = #545464
selection-foreground = #f2ecbc
`,
  "Kanagawa Wave": `palette = 0=#090618
palette = 1=#c34043
palette = 2=#76946a
palette = 3=#c0a36e
palette = 4=#7e9cd8
palette = 5=#957fb8
palette = 6=#6a9589
palette = 7=#c8c093
palette = 8=#727169
palette = 9=#e82424
palette = 10=#98bb6c
palette = 11=#e6c384
palette = 12=#7fb4ca
palette = 13=#938aa9
palette = 14=#7aa89f
palette = 15=#dcd7ba
background = #1f1f28
foreground = #dcd7ba
cursor-color = #dcd7ba
cursor-text = #1f1f28
selection-background = #dcd7ba
selection-foreground = #1f1f28
`,
  Kanagawabones: `palette = 0=#1f1f28
palette = 1=#e46a78
palette = 2=#98bc6d
palette = 3=#e5c283
palette = 4=#7eb3c9
palette = 5=#957fb8
palette = 6=#7eb3c9
palette = 7=#ddd8bb
palette = 8=#49495e
palette = 9=#ec818c
palette = 10=#9ec967
palette = 11=#f1c982
palette = 12=#7bc2df
palette = 13=#a98fd2
palette = 14=#7bc2df
palette = 15=#a8a48d
background = #1f1f28
foreground = #ddd8bb
cursor-color = #e6e0c2
cursor-text = #1f1f28
selection-background = #49473e
selection-foreground = #ddd8bb
`,
  "Kanso Ink": `palette = 0=#14171d
palette = 1=#c4746e
palette = 2=#8a9a7b
palette = 3=#c4b28a
palette = 4=#8ba4b0
palette = 5=#a292a3
palette = 6=#8ea4a2
palette = 7=#c8c093
palette = 8=#a4a7a4
palette = 9=#e46876
palette = 10=#87a987
palette = 11=#e6c384
palette = 12=#7fb4ca
palette = 13=#938aa9
palette = 14=#7aa89f
palette = 15=#c5c9c7
background = #14171d
foreground = #c5c9c7
cursor-color = #c5c9c7
cursor-text = #14171d
selection-background = #22262d
selection-foreground = #c5c9c7
`,
  "Kanso Mist": `palette = 0=#22262d
palette = 1=#c4746e
palette = 2=#8a9a7b
palette = 3=#c4b28a
palette = 4=#8ba4b0
palette = 5=#a292a3
palette = 6=#8ea4a2
palette = 7=#a4a7a4
palette = 8=#5c6066
palette = 9=#e46876
palette = 10=#87a987
palette = 11=#e6c384
palette = 12=#7fb4ca
palette = 13=#938aa9
palette = 14=#7aa89f
palette = 15=#c5c9c7
background = #22262d
foreground = #c5c9c7
cursor-color = #c5c9c7
cursor-text = #22262d
selection-background = #43464e
selection-foreground = #c5c9c7
`,
  "Kanso Pearl": `palette = 0=#22262d
palette = 1=#c84053
palette = 2=#6f894e
palette = 3=#77713f
palette = 4=#4d699b
palette = 5=#b35b79
palette = 6=#597b75
palette = 7=#545464
palette = 8=#6d6f6e
palette = 9=#d7474b
palette = 10=#6e915f
palette = 11=#836f4a
palette = 12=#6693bf
palette = 13=#624c83
palette = 14=#5e857a
palette = 15=#43436c
background = #f2f1ef
foreground = #22262d
cursor-color = #22262d
cursor-text = #f2f1ef
selection-background = #e2e1df
selection-foreground = #22262d
`,
  "Kanso Zen": `palette = 0=#0d0c0c
palette = 1=#c4746e
palette = 2=#8a9a7b
palette = 3=#c4b28a
palette = 4=#8ba4b0
palette = 5=#a292a3
palette = 6=#8ea4a2
palette = 7=#c8c093
palette = 8=#a4a7a4
palette = 9=#e46876
palette = 10=#87a987
palette = 11=#e6c384
palette = 12=#7fb4ca
palette = 13=#938aa9
palette = 14=#7aa89f
palette = 15=#c5c9c7
background = #090e13
foreground = #c5c9c7
cursor-color = #c5c9c7
cursor-text = #090e13
selection-background = #393b44
selection-foreground = #c5c9c7
`,
  Kibble: `palette = 0=#4d4d4d
palette = 1=#c70031
palette = 2=#29cf13
palette = 3=#d8e30e
palette = 4=#3449d1
palette = 5=#8400ff
palette = 6=#0798ab
palette = 7=#e2d1e3
palette = 8=#5a5a5a
palette = 9=#f01578
palette = 10=#6ce05c
palette = 11=#f3f79e
palette = 12=#97a4f7
palette = 13=#c495f0
palette = 14=#68f2e0
palette = 15=#ffffff
background = #0e100a
foreground = #f7f7f7
cursor-color = #9fda9c
cursor-text = #000000
selection-background = #9ba787
selection-foreground = #000000
`,
  "Kitty Default": `palette = 0=#000000
palette = 1=#cc0403
palette = 2=#19cb00
palette = 3=#cecb00
palette = 4=#0d73cc
palette = 5=#cb1ed1
palette = 6=#0dcdcd
palette = 7=#dddddd
palette = 8=#767676
palette = 9=#f2201f
palette = 10=#23fd00
palette = 11=#fffd00
palette = 12=#1a8fff
palette = 13=#fd28ff
palette = 14=#14ffff
palette = 15=#ffffff
background = #000000
foreground = #dddddd
cursor-color = #cccccc
cursor-text = #111111
selection-background = #fffacd
selection-foreground = #000000
`,
  "Kitty Low Contrast": `palette = 0=#000000
palette = 1=#cc0403
palette = 2=#19cb00
palette = 3=#cecb00
palette = 4=#0d73cc
palette = 5=#cb1ed1
palette = 6=#0dcdcd
palette = 7=#dddddd
palette = 8=#767676
palette = 9=#f2201f
palette = 10=#23fd00
palette = 11=#fffd00
palette = 12=#1a8fff
palette = 13=#fd28ff
palette = 14=#14ffff
palette = 15=#ffffff
background = #333333
foreground = #ffffff
cursor-color = #cccccc
cursor-text = #111111
selection-background = #fffacd
selection-foreground = #000000
`,
  Kolorit: `palette = 0=#1d1a1e
palette = 1=#ff5b82
palette = 2=#47d7a1
palette = 3=#e8e562
palette = 4=#5db4ee
palette = 5=#da6cda
palette = 6=#57e9eb
palette = 7=#ededed
palette = 8=#504d51
palette = 9=#ff5b82
palette = 10=#47d7a1
palette = 11=#e8e562
palette = 12=#5db4ee
palette = 13=#da6cda
palette = 14=#57e9eb
palette = 15=#ededed
background = #1d1a1e
foreground = #efecec
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #e1925c
selection-foreground = #1d1a1e
`,
  Konsolas: `palette = 0=#000000
palette = 1=#aa1717
palette = 2=#18b218
palette = 3=#ebae1f
palette = 4=#2323a5
palette = 5=#ad1edc
palette = 6=#42b0c8
palette = 7=#c8c1c1
palette = 8=#7b716e
palette = 9=#ff4141
palette = 10=#5fff5f
palette = 11=#ffff55
palette = 12=#4b4bff
palette = 13=#ff54ff
palette = 14=#69ffff
palette = 15=#ffffff
background = #060606
foreground = #c8c1c1
cursor-color = #c8c1c1
cursor-text = #060606
selection-background = #060606
selection-foreground = #c8c1c1
`,
  Kurokula: `palette = 0=#333333
palette = 1=#c35a52
palette = 2=#78b3a9
palette = 3=#e1b917
palette = 4=#5c91dd
palette = 5=#8b79a6
palette = 6=#867268
palette = 7=#e0cfc2
palette = 8=#515151
palette = 9=#ffc34c
palette = 10=#afffa5
palette = 11=#fff700
palette = 12=#90dbff
palette = 13=#ad93ff
palette = 14=#ffcdb6
palette = 15=#ffffff
background = #141515
foreground = #e0cfc2
cursor-color = #7a1c1c
cursor-text = #fffbf2
selection-background = #515151
selection-foreground = #ffc34c
`,
  "Lab Fox": `palette = 0=#2e2e2e
palette = 1=#fc6d26
palette = 2=#3eb383
palette = 3=#fca121
palette = 4=#db3b21
palette = 5=#6b40a8
palette = 6=#6e49cb
palette = 7=#ffffff
palette = 8=#535353
palette = 9=#ff6517
palette = 10=#53eaa8
palette = 11=#fca013
palette = 12=#db501f
palette = 13=#6a36b6
palette = 14=#7d53e7
palette = 15=#ffffff
background = #2e2e2e
foreground = #ffffff
cursor-color = #7f7f7f
cursor-text = #b2b2b2
selection-background = #cb392e
selection-foreground = #ffffff
`,
  Laser: `palette = 0=#626262
palette = 1=#ff8373
palette = 2=#b4fb73
palette = 3=#09b4bd
palette = 4=#fed300
palette = 5=#ff90fe
palette = 6=#d1d1fe
palette = 7=#f1f1f1
palette = 8=#8f8f8f
palette = 9=#ffc4be
palette = 10=#d6fcba
palette = 11=#fffed5
palette = 12=#f92883
palette = 13=#ffb2fe
palette = 14=#e6e7fe
palette = 15=#ffffff
background = #030d18
foreground = #f106e3
cursor-color = #00ff9c
cursor-text = #a6a6a6
selection-background = #2e206a
selection-foreground = #f4f4f4
`,
  "Later This Evening": `palette = 0=#2b2b2b
palette = 1=#d45a60
palette = 2=#afba67
palette = 3=#e5d289
palette = 4=#a0bad6
palette = 5=#c092d6
palette = 6=#91bfb7
palette = 7=#494a4a
palette = 8=#525454
palette = 9=#d3232f
palette = 10=#aabb39
palette = 11=#e5be39
palette = 12=#6699d6
palette = 13=#ab53d6
palette = 14=#5fc0ae
palette = 15=#c1c2c2
background = #222222
foreground = #959595
cursor-color = #4f4f4f
cursor-text = #959595
selection-background = #424242
selection-foreground = #959595
`,
  Lavandula: `palette = 0=#230046
palette = 1=#7d1625
palette = 2=#337e6f
palette = 3=#7f6f49
palette = 4=#4f4a7f
palette = 5=#5a3f7f
palette = 6=#58777f
palette = 7=#736e7d
palette = 8=#443a53
palette = 9=#e05167
palette = 10=#52e0c4
palette = 11=#e0c386
palette = 12=#8e87e0
palette = 13=#a776e0
palette = 14=#9ad4e0
palette = 15=#8c91fa
background = #050014
foreground = #736e7d
cursor-color = #8c91fa
cursor-text = #050014
selection-background = #37323c
selection-foreground = #8c91fa
`,
  "Light Owl": `palette = 0=#403f53
palette = 1=#de3d3b
palette = 2=#08916a
palette = 3=#e0af02
palette = 4=#288ed7
palette = 5=#d6438a
palette = 6=#2aa298
palette = 7=#bdbdbd
palette = 8=#989fb1
palette = 9=#de3d3b
palette = 10=#08916a
palette = 11=#daaa01
palette = 12=#288ed7
palette = 13=#d6438a
palette = 14=#2aa298
palette = 15=#f0f0f0
background = #fbfbfb
foreground = #403f53
cursor-color = #403f53
cursor-text = #f6f6f6
selection-background = #e0e0e0
selection-foreground = #403f53
`,
  "Liquid Carbon": `palette = 0=#000000
palette = 1=#ff3030
palette = 2=#559a70
palette = 3=#ccac00
palette = 4=#0099cc
palette = 5=#cc69c8
palette = 6=#7ac4cc
palette = 7=#bccccc
palette = 8=#595959
palette = 9=#ff3030
palette = 10=#559a70
palette = 11=#ccac00
palette = 12=#0099cc
palette = 13=#cc69c8
palette = 14=#7ac4cc
palette = 15=#bccccc
background = #303030
foreground = #afc2c2
cursor-color = #ffffff
cursor-text = #000000
selection-background = #7dbeff
selection-foreground = #000000
`,
  "Liquid Carbon Transparent": `palette = 0=#000000
palette = 1=#ff3030
palette = 2=#559a70
palette = 3=#ccac00
palette = 4=#0099cc
palette = 5=#cc69c8
palette = 6=#7ac4cc
palette = 7=#bccccc
palette = 8=#404040
palette = 9=#ff3030
palette = 10=#559a70
palette = 11=#ccac00
palette = 12=#0099cc
palette = 13=#cc69c8
palette = 14=#7ac4cc
palette = 15=#bccccc
background = #000000
foreground = #afc2c2
cursor-color = #ffffff
cursor-text = #000000
selection-background = #7dbeff
selection-foreground = #000000
`,
  Lovelace: `palette = 0=#282a36
palette = 1=#f37f97
palette = 2=#5adecd
palette = 3=#f2a272
palette = 4=#8897f4
palette = 5=#c574dd
palette = 6=#79e6f3
palette = 7=#fdfdfd
palette = 8=#4e5165
palette = 9=#ff4971
palette = 10=#18e3c8
palette = 11=#ff8037
palette = 12=#556fff
palette = 13=#b043d1
palette = 14=#3fdcee
palette = 15=#bebec1
background = #1d1f28
foreground = #fdfdfd
cursor-color = #c574dd
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  "Man Page": `palette = 0=#000000
palette = 1=#cc0000
palette = 2=#00a600
palette = 3=#999900
palette = 4=#0000b2
palette = 5=#b200b2
palette = 6=#00a6b2
palette = 7=#b2b2b2
palette = 8=#666666
palette = 9=#e50000
palette = 10=#00cc00
palette = 11=#bfbf00
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00cbcb
palette = 15=#e5e5e5
background = #fef49c
foreground = #000000
cursor-color = #7f7f7f
cursor-text = #000000
selection-background = #a4c9cd
selection-foreground = #000000
`,
  Mariana: `palette = 0=#000000
palette = 1=#ec5f66
palette = 2=#99c794
palette = 3=#f9ae58
palette = 4=#6699cc
palette = 5=#c695c6
palette = 6=#5fb4b4
palette = 7=#f7f7f7
palette = 8=#666666
palette = 9=#f97b58
palette = 10=#acd1a8
palette = 11=#fac761
palette = 12=#85add6
palette = 13=#d8b6d8
palette = 14=#82c4c4
palette = 15=#ffffff
background = #343d46
foreground = #d8dee9
cursor-color = #fcbb6a
cursor-text = #8c8c8c
selection-background = #4e5a65
selection-foreground = #d8dee9
`,
  Material: `palette = 0=#212121
palette = 1=#b7141f
palette = 2=#457b24
palette = 3=#f6981e
palette = 4=#134eb2
palette = 5=#560088
palette = 6=#0e717c
palette = 7=#afafaf
palette = 8=#424242
palette = 9=#e83b3f
palette = 10=#7aba3a
palette = 11=#bfaa00
palette = 12=#54a4f3
palette = 13=#aa4dbc
palette = 14=#26bbd1
palette = 15=#d9d9d9
background = #eaeaea
foreground = #232322
cursor-color = #16afca
cursor-text = #2e2e2d
selection-background = #c2c2c2
selection-foreground = #4e4e4e
`,
  "Material Dark": `palette = 0=#212121
palette = 1=#b7141f
palette = 2=#457b24
palette = 3=#f6981e
palette = 4=#134eb2
palette = 5=#701aa2
palette = 6=#0e717c
palette = 7=#efefef
palette = 8=#4f4f4f
palette = 9=#e83b3f
palette = 10=#7aba3a
palette = 11=#ffea2e
palette = 12=#54a4f3
palette = 13=#aa4dbc
palette = 14=#26bbd1
palette = 15=#d9d9d9
background = #232322
foreground = #e5e5e5
cursor-color = #16afca
cursor-text = #dfdfdf
selection-background = #dfdfdf
selection-foreground = #3d3d3d
`,
  "Material Darker": `palette = 0=#000000
palette = 1=#ff5370
palette = 2=#c3e88d
palette = 3=#ffcb6b
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#89ddff
palette = 7=#ffffff
palette = 8=#545454
palette = 9=#ff5370
palette = 10=#c3e88d
palette = 11=#ffcb6b
palette = 12=#82aaff
palette = 13=#c792ea
palette = 14=#89ddff
palette = 15=#ffffff
background = #212121
foreground = #eeffff
cursor-color = #ffffff
cursor-text = #bfbfbf
selection-background = #eeffff
selection-foreground = #545454
`,
  "Material Design Colors": `palette = 0=#435b67
palette = 1=#fc3841
palette = 2=#5cf19e
palette = 3=#fed032
palette = 4=#37b6ff
palette = 5=#fc226e
palette = 6=#59ffd1
palette = 7=#ffffff
palette = 8=#a1b0b8
palette = 9=#fc746d
palette = 10=#adf7be
palette = 11=#fee16c
palette = 12=#70cfff
palette = 13=#fc669b
palette = 14=#9affe6
palette = 15=#ffffff
background = #1d262a
foreground = #e7ebed
cursor-color = #eaeaea
cursor-text = #000000
selection-background = #4e6a78
selection-foreground = #e7ebed
`,
  "Material Ocean": `palette = 0=#546e7a
palette = 1=#ff5370
palette = 2=#c3e88d
palette = 3=#ffcb6b
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#89ddff
palette = 7=#ffffff
palette = 8=#546e7a
palette = 9=#ff5370
palette = 10=#c3e88d
palette = 11=#ffcb6b
palette = 12=#82aaff
palette = 13=#c792ea
palette = 14=#89ddff
palette = 15=#ffffff
background = #0f111a
foreground = #8f93a2
cursor-color = #ffcc00
cursor-text = #0f111a
selection-background = #1f2233
selection-foreground = #8f93a2
`,
  Mathias: `palette = 0=#000000
palette = 1=#e52222
palette = 2=#a6e32d
palette = 3=#fc951e
palette = 4=#c48dff
palette = 5=#fa2573
palette = 6=#67d9f0
palette = 7=#f2f2f2
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #bbbbbb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #555555
selection-foreground = #f2f2f2
`,
  Matrix: `palette = 0=#0f191c
palette = 1=#23755a
palette = 2=#82d967
palette = 3=#ffd700
palette = 4=#3f5242
palette = 5=#409931
palette = 6=#50b45a
palette = 7=#507350
palette = 8=#688060
palette = 9=#2fc079
palette = 10=#90d762
palette = 11=#faff00
palette = 12=#4f7e7e
palette = 13=#11ff25
palette = 14=#c1ff8a
palette = 15=#678c61
background = #0f191c
foreground = #426644
cursor-color = #384545
cursor-text = #00ff00
selection-background = #18282e
selection-foreground = #00ff87
`,
  "Matte Black": `palette = 0=#333333
palette = 1=#d35f5f
palette = 2=#ffc107
palette = 3=#b91c1c
palette = 4=#e68e0d
palette = 5=#d35f5f
palette = 6=#bebebe
palette = 7=#bebebe
palette = 8=#8a8a8d
palette = 9=#891c1c
palette = 10=#ffc107
palette = 11=#b90a0a
palette = 12=#f59e0b
palette = 13=#b91c1c
palette = 14=#eaeaea
palette = 15=#ffffff
background = #121212
foreground = #bebebe
cursor-color = #eaeaea
cursor-text = #121212
selection-background = #333333
selection-foreground = #eaeaea
`,
  Medallion: `palette = 0=#000000
palette = 1=#b64c00
palette = 2=#7c8b16
palette = 3=#d3bd26
palette = 4=#616bb0
palette = 5=#8c5a90
palette = 6=#916c25
palette = 7=#cac29a
palette = 8=#5e5219
palette = 9=#ff9149
palette = 10=#b2ca3b
palette = 11=#ffe54a
palette = 12=#acb8ff
palette = 13=#ffa0ff
palette = 14=#ffbc51
palette = 15=#fed698
background = #1d1908
foreground = #cac296
cursor-color = #d3ba30
cursor-text = #fffc7d
selection-background = #626dac
selection-foreground = #cac29a
`,
  "Melange Dark": `palette = 0=#34302c
palette = 1=#bd8183
palette = 2=#78997a
palette = 3=#e49b5d
palette = 4=#7f91b2
palette = 5=#b380b0
palette = 6=#7b9695
palette = 7=#c1a78e
palette = 8=#867462
palette = 9=#d47766
palette = 10=#85b695
palette = 11=#ebc06d
palette = 12=#a3a9ce
palette = 13=#cf9bc2
palette = 14=#89b3b6
palette = 15=#ece1d7
background = #292522
foreground = #ece1d7
cursor-color = #ece1d7
cursor-text = #292522
selection-background = #ece1d7
selection-foreground = #403a36
`,
  "Melange Light": `palette = 0=#e9e1db
palette = 1=#c77b8b
palette = 2=#6e9b72
palette = 3=#bc5c00
palette = 4=#7892bd
palette = 5=#be79bb
palette = 6=#739797
palette = 7=#7d6658
palette = 8=#a98a78
palette = 9=#bf0021
palette = 10=#3a684a
palette = 11=#a06d00
palette = 12=#465aa4
palette = 13=#904180
palette = 14=#3d6568
palette = 15=#54433a
background = #f1f1f1
foreground = #54433a
cursor-color = #54433a
cursor-text = #f1f1f1
selection-background = #54433a
selection-foreground = #d9d3ce
`,
  Mellifluous: `palette = 0=#1a1a1a
palette = 1=#d29393
palette = 2=#b3b393
palette = 3=#cbaa89
palette = 4=#a8a1be
palette = 5=#b39fb0
palette = 6=#c0af8c
palette = 7=#dadada
palette = 8=#5b5b5b
palette = 9=#c95954
palette = 10=#828040
palette = 11=#a6794c
palette = 12=#5a6599
palette = 13=#9c6995
palette = 14=#74a39e
palette = 15=#ffffff
background = #1a1a1a
foreground = #dadada
cursor-color = #bfad9e
cursor-text = #1a1a1a
selection-background = #2d2d2d
selection-foreground = #c0af8c
`,
  Mellow: `palette = 0=#27272a
palette = 1=#f5a191
palette = 2=#90b99f
palette = 3=#e6b99d
palette = 4=#aca1cf
palette = 5=#e29eca
palette = 6=#ea83a5
palette = 7=#c1c0d4
palette = 8=#424246
palette = 9=#ffae9f
palette = 10=#9dc6ac
palette = 11=#f0c5a9
palette = 12=#b9aeda
palette = 13=#ecaad6
palette = 14=#f591b2
palette = 15=#cac9dd
background = #161617
foreground = #c9c7cd
cursor-color = #cac9dd
cursor-text = #161617
selection-background = #2a2a2d
selection-foreground = #c1c0d4
`,
  Miasma: `palette = 0=#000000
palette = 1=#685742
palette = 2=#5f875f
palette = 3=#b36d43
palette = 4=#78824b
palette = 5=#bb7744
palette = 6=#c9a554
palette = 7=#d7c483
palette = 8=#666666
palette = 9=#685742
palette = 10=#5f875f
palette = 11=#b36d43
palette = 12=#78824b
palette = 13=#bb7744
palette = 14=#c9a554
palette = 15=#d7c483
background = #222222
foreground = #c2c2b0
cursor-color = #c7c7c7
cursor-text = #959595
selection-background = #e5c47b
selection-foreground = #000000
`,
  "Midnight In Mojave": `palette = 0=#1e1e1e
palette = 1=#ff453a
palette = 2=#32d74b
palette = 3=#ffd60a
palette = 4=#0a84ff
palette = 5=#bf5af2
palette = 6=#5ac8fa
palette = 7=#ffffff
palette = 8=#515151
palette = 9=#ff453a
palette = 10=#32d74b
palette = 11=#ffd60a
palette = 12=#0a84ff
palette = 13=#bf5af2
palette = 14=#5ac8fa
palette = 15=#ffffff
background = #1e1e1e
foreground = #ffffff
cursor-color = #32d74b
cursor-text = #1c1c1c
selection-background = #4a504d
selection-foreground = #ffffff
`,
  Mirage: `palette = 0=#011627
palette = 1=#ff9999
palette = 2=#85cc95
palette = 3=#ffd700
palette = 4=#7fb5ff
palette = 5=#ddb3ff
palette = 6=#21c7a8
palette = 7=#ffffff
palette = 8=#575656
palette = 9=#ff9999
palette = 10=#85cc95
palette = 11=#ffd700
palette = 12=#7fb5ff
palette = 13=#ddb3ff
palette = 14=#85cc95
palette = 15=#ffffff
background = #1b2738
foreground = #a6b2c0
cursor-color = #ddb3ff
cursor-text = #ffffff
selection-background = #273951
selection-foreground = #d3dbe5
`,
  Misterioso: `palette = 0=#000000
palette = 1=#ff4242
palette = 2=#74af68
palette = 3=#ffad29
palette = 4=#338f86
palette = 5=#9414e6
palette = 6=#23d7d7
palette = 7=#e1e1e0
palette = 8=#626262
palette = 9=#ff3242
palette = 10=#74cd68
palette = 11=#ffb929
palette = 12=#23d7d7
palette = 13=#ff37ff
palette = 14=#00ede1
palette = 15=#ffffff
background = #2d3743
foreground = #e1e1e0
cursor-color = #666666
cursor-text = #ffffff
selection-background = #2d37ff
selection-foreground = #000000
`,
  Molokai: `palette = 0=#121212
palette = 1=#fa2573
palette = 2=#98e123
palette = 3=#dfd460
palette = 4=#1080d0
palette = 5=#8700ff
palette = 6=#43a8d0
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#f6669d
palette = 10=#b1e05f
palette = 11=#fff26d
palette = 12=#00afff
palette = 13=#af87ff
palette = 14=#51ceff
palette = 15=#ffffff
background = #121212
foreground = #bbbbbb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Mona Lisa": `palette = 0=#351b0e
palette = 1=#9b291c
palette = 2=#636232
palette = 3=#c36e28
palette = 4=#515c5d
palette = 5=#9b1d29
palette = 6=#588056
palette = 7=#f7d75c
palette = 8=#874228
palette = 9=#ff4331
palette = 10=#b4b264
palette = 11=#ff9566
palette = 12=#9eb2b4
palette = 13=#ff5b6a
palette = 14=#8acd8f
palette = 15=#ffe598
background = #120b0d
foreground = #f7d66a
cursor-color = #c46c32
cursor-text = #120b0d
selection-background = #f7d66a
selection-foreground = #120b0d
`,
  "Monokai Classic": `palette = 0=#272822
palette = 1=#f92672
palette = 2=#a6e22e
palette = 3=#e6db74
palette = 4=#fd971f
palette = 5=#ae81ff
palette = 6=#66d9ef
palette = 7=#fdfff1
palette = 8=#6e7066
palette = 9=#f92672
palette = 10=#a6e22e
palette = 11=#e6db74
palette = 12=#fd971f
palette = 13=#ae81ff
palette = 14=#66d9ef
palette = 15=#fdfff1
background = #272822
foreground = #fdfff1
cursor-color = #c0c1b5
cursor-text = #8d8e82
selection-background = #57584f
selection-foreground = #fdfff1
`,
  "Monokai Pro": `palette = 0=#2d2a2e
palette = 1=#ff6188
palette = 2=#a9dc76
palette = 3=#ffd866
palette = 4=#fc9867
palette = 5=#ab9df2
palette = 6=#78dce8
palette = 7=#fcfcfa
palette = 8=#727072
palette = 9=#ff6188
palette = 10=#a9dc76
palette = 11=#ffd866
palette = 12=#fc9867
palette = 13=#ab9df2
palette = 14=#78dce8
palette = 15=#fcfcfa
background = #2d2a2e
foreground = #fcfcfa
cursor-color = #c1c0c0
cursor-text = #8e8d8d
selection-background = #5b595c
selection-foreground = #fcfcfa
`,
  "Monokai Pro Light": `palette = 0=#faf4f2
palette = 1=#e14775
palette = 2=#269d69
palette = 3=#cc7a0a
palette = 4=#e16032
palette = 5=#7058be
palette = 6=#1c8ca8
palette = 7=#29242a
palette = 8=#a59fa0
palette = 9=#e14775
palette = 10=#269d69
palette = 11=#cc7a0a
palette = 12=#e16032
palette = 13=#7058be
palette = 14=#1c8ca8
palette = 15=#29242a
background = #faf4f2
foreground = #29242a
cursor-color = #706b6e
cursor-text = #a39ea1
selection-background = #bfb9ba
selection-foreground = #29242a
`,
  "Monokai Pro Light Sun": `palette = 0=#f8efe7
palette = 1=#ce4770
palette = 2=#218871
palette = 3=#b16803
palette = 4=#d4572b
palette = 5=#6851a2
palette = 6=#2473b6
palette = 7=#2c232e
palette = 8=#a59c9c
palette = 9=#ce4770
palette = 10=#218871
palette = 11=#b16803
palette = 12=#d4572b
palette = 13=#6851a2
palette = 14=#2473b6
palette = 15=#2c232e
background = #f8efe7
foreground = #2c232e
cursor-color = #72696d
cursor-text = #a59ca0
selection-background = #beb5b3
selection-foreground = #2c232e
`,
  "Monokai Pro Machine": `palette = 0=#273136
palette = 1=#ff6d7e
palette = 2=#a2e57b
palette = 3=#ffed72
palette = 4=#ffb270
palette = 5=#baa0f8
palette = 6=#7cd5f1
palette = 7=#f2fffc
palette = 8=#6b7678
palette = 9=#ff6d7e
palette = 10=#a2e57b
palette = 11=#ffed72
palette = 12=#ffb270
palette = 13=#baa0f8
palette = 14=#7cd5f1
palette = 15=#f2fffc
background = #273136
foreground = #f2fffc
cursor-color = #b8c4c3
cursor-text = #859190
selection-background = #545f62
selection-foreground = #f2fffc
`,
  "Monokai Pro Octagon": `palette = 0=#282a3a
palette = 1=#ff657a
palette = 2=#bad761
palette = 3=#ffd76d
palette = 4=#ff9b5e
palette = 5=#c39ac9
palette = 6=#9cd1bb
palette = 7=#eaf2f1
palette = 8=#696d77
palette = 9=#ff657a
palette = 10=#bad761
palette = 11=#ffd76d
palette = 12=#ff9b5e
palette = 13=#c39ac9
palette = 14=#9cd1bb
palette = 15=#eaf2f1
background = #282a3a
foreground = #eaf2f1
cursor-color = #b2b9bd
cursor-text = #f2f9fd
selection-background = #535763
selection-foreground = #eaf2f1
`,
  "Monokai Pro Ristretto": `palette = 0=#2c2525
palette = 1=#fd6883
palette = 2=#adda78
palette = 3=#f9cc6c
palette = 4=#f38d70
palette = 5=#a8a9eb
palette = 6=#85dacc
palette = 7=#fff1f3
palette = 8=#72696a
palette = 9=#fd6883
palette = 10=#adda78
palette = 11=#f9cc6c
palette = 12=#f38d70
palette = 13=#a8a9eb
palette = 14=#85dacc
palette = 15=#fff1f3
background = #2c2525
foreground = #fff1f3
cursor-color = #c3b7b8
cursor-text = #fff7f8
selection-background = #5b5353
selection-foreground = #fff1f3
`,
  "Monokai Pro Spectrum": `palette = 0=#222222
palette = 1=#fc618d
palette = 2=#7bd88f
palette = 3=#fce566
palette = 4=#fd9353
palette = 5=#948ae3
palette = 6=#5ad4e6
palette = 7=#f7f1ff
palette = 8=#69676c
palette = 9=#fc618d
palette = 10=#7bd88f
palette = 11=#fce566
palette = 12=#fd9353
palette = 13=#948ae3
palette = 14=#5ad4e6
palette = 15=#f7f1ff
background = #222222
foreground = #f7f1ff
cursor-color = #bab6c0
cursor-text = #faf6ff
selection-background = #525053
selection-foreground = #f7f1ff
`,
  "Monokai Remastered": `palette = 0=#1a1a1a
palette = 1=#f4005f
palette = 2=#98e024
palette = 3=#fd971f
palette = 4=#9d65ff
palette = 5=#f4005f
palette = 6=#58d1eb
palette = 7=#c4c5b5
palette = 8=#625e4c
palette = 9=#f4005f
palette = 10=#98e024
palette = 11=#e0d561
palette = 12=#9d65ff
palette = 13=#f4005f
palette = 14=#58d1eb
palette = 15=#f6f6ef
background = #0c0c0c
foreground = #d9d9d9
cursor-color = #fc971f
cursor-text = #000000
selection-background = #343434
selection-foreground = #ffffff
`,
  "Monokai Soda": `palette = 0=#1a1a1a
palette = 1=#f4005f
palette = 2=#98e024
palette = 3=#fa8419
palette = 4=#9d65ff
palette = 5=#f4005f
palette = 6=#58d1eb
palette = 7=#c4c5b5
palette = 8=#625e4c
palette = 9=#f4005f
palette = 10=#98e024
palette = 11=#e0d561
palette = 12=#9d65ff
palette = 13=#f4005f
palette = 14=#58d1eb
palette = 15=#f6f6ef
background = #1a1a1a
foreground = #c4c5b5
cursor-color = #f6f7ec
cursor-text = #b7b8a8
selection-background = #343434
selection-foreground = #c4c5b5
`,
  "Monokai Vivid": `palette = 0=#121212
palette = 1=#fa2934
palette = 2=#98e123
palette = 3=#fff30a
palette = 4=#0443ff
palette = 5=#f800f8
palette = 6=#01b6ed
palette = 7=#ffffff
palette = 8=#838383
palette = 9=#f6669d
palette = 10=#b1e05f
palette = 11=#fff26d
palette = 12=#0443ff
palette = 13=#f200f6
palette = 14=#51ceff
palette = 15=#ffffff
background = #121212
foreground = #f9f9f9
cursor-color = #fb0007
cursor-text = #ff8c95
selection-background = #ffffff
selection-foreground = #000000
`,
  Moonfly: `palette = 0=#323437
palette = 1=#ff5454
palette = 2=#8cc85f
palette = 3=#e3c78a
palette = 4=#80a0ff
palette = 5=#cf87e8
palette = 6=#79dac8
palette = 7=#c6c6c6
palette = 8=#949494
palette = 9=#ff5189
palette = 10=#36c692
palette = 11=#c6c684
palette = 12=#74b2ff
palette = 13=#ae81ff
palette = 14=#85dc85
palette = 15=#e4e4e4
background = #080808
foreground = #bdbdbd
cursor-color = #9e9e9e
cursor-text = #080808
selection-background = #b2ceee
selection-foreground = #080808
`,
  N0Tch2K: `palette = 0=#383838
palette = 1=#a95551
palette = 2=#666666
palette = 3=#a98051
palette = 4=#657d3e
palette = 5=#767676
palette = 6=#c9c9c9
palette = 7=#d0b8a3
palette = 8=#545454
palette = 9=#a97775
palette = 10=#8c8c8c
palette = 11=#a99175
palette = 12=#98bd5e
palette = 13=#a3a3a3
palette = 14=#dcdcdc
palette = 15=#d8c8bb
background = #222222
foreground = #a0a0a0
cursor-color = #aa9175
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  "Neobones Dark": `palette = 0=#0f191f
palette = 1=#de6e7c
palette = 2=#90ff6b
palette = 3=#b77e64
palette = 4=#8190d4
palette = 5=#b279a7
palette = 6=#66a5ad
palette = 7=#c6d5cf
palette = 8=#334652
palette = 9=#e8838f
palette = 10=#a0ff85
palette = 11=#d68c67
palette = 12=#92a0e2
palette = 13=#cf86c1
palette = 14=#65b8c1
palette = 15=#98a39e
background = #0f191f
foreground = #c6d5cf
cursor-color = #ceddd7
cursor-text = #0f191f
selection-background = #3a3e3d
selection-foreground = #c6d5cf
`,
  "Neobones Light": `palette = 0=#e5ede6
palette = 1=#a8334c
palette = 2=#567a30
palette = 3=#944927
palette = 4=#286486
palette = 5=#88507d
palette = 6=#3b8992
palette = 7=#202e18
palette = 8=#99ac9c
palette = 9=#94253e
palette = 10=#3f5a22
palette = 11=#803d1c
palette = 12=#1d5573
palette = 13=#7b3b70
palette = 14=#2b747c
palette = 15=#415934
background = #e5ede6
foreground = #202e18
cursor-color = #202e18
cursor-text = #e5ede6
selection-background = #ade48c
selection-foreground = #202e18
`,
  Neon: `palette = 0=#000000
palette = 1=#ff3045
palette = 2=#5ffa74
palette = 3=#fffc7e
palette = 4=#0f15d8
palette = 5=#f924e7
palette = 6=#00fffc
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff5a5a
palette = 10=#75ff88
palette = 11=#fffd96
palette = 12=#3c40cb
palette = 13=#f15be5
palette = 14=#88fffe
palette = 15=#ffffff
background = #14161a
foreground = #00fffc
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #0013ff
selection-foreground = #08d2cf
`,
  Neopolitan: `palette = 0=#000000
palette = 1=#9a1a1a
palette = 2=#61ce3c
palette = 3=#fbde2d
palette = 4=#324883
palette = 5=#ff0080
palette = 6=#8da6ce
palette = 7=#f8f8f8
palette = 8=#4c4c4c
palette = 9=#9a1a1a
palette = 10=#61ce3c
palette = 11=#fbde2d
palette = 12=#324883
palette = 13=#ff0080
palette = 14=#8da6ce
palette = 15=#f8f8f8
background = #271f19
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #bfbfbf
selection-background = #253b76
selection-foreground = #ffffff
`,
  Neutron: `palette = 0=#23252b
palette = 1=#b54036
palette = 2=#5ab977
palette = 3=#deb566
palette = 4=#6a7c93
palette = 5=#a4799d
palette = 6=#3f94a8
palette = 7=#e6e8ef
palette = 8=#494b51
palette = 9=#b54036
palette = 10=#5ab977
palette = 11=#deb566
palette = 12=#6a7c93
palette = 13=#a4799d
palette = 14=#3f94a8
palette = 15=#ebedf2
background = #1c1e22
foreground = #e6e8ef
cursor-color = #f6f7ec
cursor-text = #b7b8a8
selection-background = #2f363e
selection-foreground = #7d8fa4
`,
  "Night Lion V1": `palette = 0=#4c4c4c
palette = 1=#bb0000
palette = 2=#5fde8f
palette = 3=#f3f167
palette = 4=#276bd8
palette = 5=#bb00bb
palette = 6=#00dadf
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #bbbbbb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Night Lion V2": `palette = 0=#4c4c4c
palette = 1=#bb0000
palette = 2=#04f623
palette = 3=#f3f167
palette = 4=#64d0f0
palette = 5=#ce6fdb
palette = 6=#00dadf
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#7df71d
palette = 11=#ffff55
palette = 12=#62cbe8
palette = 13=#ff9bf5
palette = 14=#00ccd8
palette = 15=#ffffff
background = #171717
foreground = #bbbbbb
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Night Owl": `palette = 0=#011627
palette = 1=#ef5350
palette = 2=#22da6e
palette = 3=#addb67
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#21c7a8
palette = 7=#ffffff
palette = 8=#575656
palette = 9=#ef5350
palette = 10=#22da6e
palette = 11=#ffeb95
palette = 12=#82aaff
palette = 13=#c792ea
palette = 14=#7fdbca
palette = 15=#ffffff
background = #011627
foreground = #d6deeb
cursor-color = #7e57c2
cursor-text = #ffffff
selection-background = #5f7e97
selection-foreground = #dfe5ee
`,
  "Night Owlish Light": `palette = 0=#011627
palette = 1=#d3423e
palette = 2=#2aa298
palette = 3=#daaa01
palette = 4=#4876d6
palette = 5=#403f53
palette = 6=#08916a
palette = 7=#7a8181
palette = 8=#7a8181
palette = 9=#f76e6e
palette = 10=#49d0c5
palette = 11=#dac26b
palette = 12=#5ca7e4
palette = 13=#697098
palette = 14=#00c990
palette = 15=#989fb1
background = #ffffff
foreground = #403f53
cursor-color = #403f53
cursor-text = #fbfbfb
selection-background = #f2f2f2
selection-foreground = #403f53
`,
  Nightfox: `palette = 0=#393b44
palette = 1=#c94f6d
palette = 2=#81b29a
palette = 3=#dbc074
palette = 4=#719cd6
palette = 5=#9d79d6
palette = 6=#63cdcf
palette = 7=#dfdfe0
palette = 8=#575860
palette = 9=#d16983
palette = 10=#8ebaa4
palette = 11=#e0c989
palette = 12=#86abdc
palette = 13=#baa1e2
palette = 14=#7ad5d6
palette = 15=#e4e4e5
background = #192330
foreground = #cdcecf
cursor-color = #cdcecf
cursor-text = #192330
selection-background = #2b3b51
selection-foreground = #cdcecf
`,
  Niji: `palette = 0=#333333
palette = 1=#d23e08
palette = 2=#54ca74
palette = 3=#fff700
palette = 4=#2ab9ff
palette = 5=#ff50da
palette = 6=#1ef9f5
palette = 7=#ddd0c4
palette = 8=#515151
palette = 9=#ffb7b7
palette = 10=#c1ffae
palette = 11=#fcffb8
palette = 12=#8efff3
palette = 13=#ffa2ed
palette = 14=#bcffc7
palette = 15=#ffffff
background = #141515
foreground = #ffffff
cursor-color = #ffc663
cursor-text = #141515
selection-background = #515151
selection-foreground = #ffc663
`,
  "No Clown Fiesta": `palette = 0=#151515
palette = 1=#b46958
palette = 2=#90a959
palette = 3=#f4bf75
palette = 4=#bad7ff
palette = 5=#aa759f
palette = 6=#88afa2
palette = 7=#e1e1e1
palette = 8=#727272
palette = 9=#7e97ab
palette = 10=#90a959
palette = 11=#f4bf75
palette = 12=#bad7ff
palette = 13=#aa759f
palette = 14=#88afa2
palette = 15=#afafaf
background = #101010
foreground = #e0e1e4
cursor-color = #e0e1e4
cursor-text = #18191b
selection-background = #696d79
selection-foreground = #e0e1e4
`,
  "No Clown Fiesta Light": `palette = 0=#d6d6d6
palette = 1=#874e42
palette = 2=#677940
palette = 3=#b89058
palette = 4=#8ba1bf
palette = 5=#aa759f
palette = 6=#3e5f66
palette = 7=#151515
palette = 8=#2b2b2b
palette = 9=#637786
palette = 10=#677940
palette = 11=#b89058
palette = 12=#93a2ab
palette = 13=#aa759f
palette = 14=#99ab93
palette = 15=#373737
background = #e1e1e1
foreground = #151515
cursor-color = #151515
cursor-text = #d0d0d0
selection-background = #c6d5de
selection-foreground = #151515
`,
  "Nocturnal Winter": `palette = 0=#4d4d4d
palette = 1=#f12d52
palette = 2=#09cd7e
palette = 3=#f5f17a
palette = 4=#3182e0
palette = 5=#ff2b6d
palette = 6=#09c87a
palette = 7=#fcfcfc
palette = 8=#808080
palette = 9=#f16d86
palette = 10=#0ae78d
palette = 11=#fffc67
palette = 12=#6096ff
palette = 13=#ff78a2
palette = 14=#0ae78d
palette = 15=#ffffff
background = #0d0d17
foreground = #e6e5e5
cursor-color = #e6e5e5
cursor-text = #a6a6a6
selection-background = #adbdd0
selection-foreground = #000000
`,
  Nord: `palette = 0=#3b4252
palette = 1=#bf616a
palette = 2=#a3be8c
palette = 3=#ebcb8b
palette = 4=#81a1c1
palette = 5=#b48ead
palette = 6=#88c0d0
palette = 7=#e5e9f0
palette = 8=#596377
palette = 9=#bf616a
palette = 10=#a3be8c
palette = 11=#ebcb8b
palette = 12=#81a1c1
palette = 13=#b48ead
palette = 14=#8fbcbb
palette = 15=#eceff4
background = #2e3440
foreground = #d8dee9
cursor-color = #eceff4
cursor-text = #282828
selection-background = #eceff4
selection-foreground = #4c566a
`,
  "Nord Light": `palette = 0=#3b4252
palette = 1=#bf616a
palette = 2=#96b17f
palette = 3=#c5a565
palette = 4=#81a1c1
palette = 5=#b48ead
palette = 6=#7bb3c3
palette = 7=#a5abb6
palette = 8=#4c566a
palette = 9=#bf616a
palette = 10=#96b17f
palette = 11=#c5a565
palette = 12=#81a1c1
palette = 13=#b48ead
palette = 14=#82afae
palette = 15=#eceff4
background = #e5e9f0
foreground = #414858
cursor-color = #7bb3c3
cursor-text = #3b4252
selection-background = #d8dee9
selection-foreground = #4c556a
`,
  "Nord Wave": `palette = 0=#3b4252
palette = 1=#bf616a
palette = 2=#a3be8c
palette = 3=#ebcb8b
palette = 4=#81a1c1
palette = 5=#b48ead
palette = 6=#88c0d0
palette = 7=#e5e9f0
palette = 8=#4c566a
palette = 9=#bf616a
palette = 10=#a3be8c
palette = 11=#ebcb8b
palette = 12=#81a1c1
palette = 13=#b48ead
palette = 14=#8fbcbb
palette = 15=#eceff4
background = #212121
foreground = #d8dee9
cursor-color = #ebcb8b
cursor-text = #b89858
selection-background = #d8dee9
selection-foreground = #212121
`,
  Nordfox: `palette = 0=#3b4252
palette = 1=#bf616a
palette = 2=#a3be8c
palette = 3=#ebcb8b
palette = 4=#81a1c1
palette = 5=#b48ead
palette = 6=#88c0d0
palette = 7=#e5e9f0
palette = 8=#53648d
palette = 9=#d06f79
palette = 10=#b1d196
palette = 11=#f0d399
palette = 12=#8cafd2
palette = 13=#c895bf
palette = 14=#93ccdc
palette = 15=#e7ecf4
background = #2e3440
foreground = #cdcecf
cursor-color = #cdcecf
cursor-text = #2e3440
selection-background = #3e4a5b
selection-foreground = #cdcecf
`,
  Novel: `palette = 0=#000000
palette = 1=#cc0000
palette = 2=#009600
palette = 3=#d06b00
palette = 4=#0000cc
palette = 5=#cc00cc
palette = 6=#0087cc
palette = 7=#a6a6a6
palette = 8=#808080
palette = 9=#cc0000
palette = 10=#009600
palette = 11=#d06b00
palette = 12=#0000cc
palette = 13=#cc00cc
palette = 14=#0087cc
palette = 15=#ffffff
background = #dfdbc3
foreground = #3b2322
cursor-color = #73635a
cursor-text = #000000
selection-background = #a4a390
selection-foreground = #000000
`,
  novmbr: `palette = 0=#282a2e
palette = 1=#9f6434
palette = 2=#9dac5f
palette = 3=#cca75f
palette = 4=#2f7d7c
palette = 5=#b5896e
palette = 6=#52877f
palette = 7=#c7b8ac
palette = 8=#5d4e47
palette = 9=#9f6434
palette = 10=#9dac5f
palette = 11=#cca75f
palette = 12=#2f7d7c
palette = 13=#b5896e
palette = 14=#52877f
palette = 15=#c7b8ac
background = #241d1a
foreground = #c7b8ac
cursor-color = #c7b8ac
cursor-text = #241d1a
selection-background = #c7b8ac
selection-foreground = #241d1a
`,
  "Nvim Dark": `palette = 0=#07080d
palette = 1=#ffc0b9
palette = 2=#b3f6c0
palette = 3=#fce094
palette = 4=#a6dbff
palette = 5=#ffcaff
palette = 6=#8cf8f7
palette = 7=#eef1f8
palette = 8=#4f5258
palette = 9=#ffc0b9
palette = 10=#b3f6c0
palette = 11=#fce094
palette = 12=#a6dbff
palette = 13=#ffcaff
palette = 14=#8cf8f7
palette = 15=#eef1f8
background = #14161b
foreground = #e0e2ea
cursor-color = #9b9ea4
cursor-text = #e0e2ea
selection-background = #4f5258
selection-foreground = #e0e2ea
`,
  "Nvim Light": `palette = 0=#07080d
palette = 1=#590008
palette = 2=#005523
palette = 3=#6b5300
palette = 4=#004c73
palette = 5=#470045
palette = 6=#007373
palette = 7=#a1a4ab
palette = 8=#4f5258
palette = 9=#590008
palette = 10=#005523
palette = 11=#6b5300
palette = 12=#004c73
palette = 13=#470045
palette = 14=#007373
palette = 15=#eef1f8
background = #e0e2ea
foreground = #14161b
cursor-color = #9b9ea4
cursor-text = #14161b
selection-background = #9b9ea4
selection-foreground = #14161b
`,
  Obsidian: `palette = 0=#000000
palette = 1=#b30d0e
palette = 2=#00bb00
palette = 3=#fecd22
palette = 4=#3a9bdb
palette = 5=#bb00bb
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#555555
palette = 9=#ff0003
palette = 10=#93c863
palette = 11=#fef874
palette = 12=#a1d7ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #283033
foreground = #cdcdcd
cursor-color = #c0cad0
cursor-text = #8d8d8d
selection-background = #3e4c4f
selection-foreground = #dfe1e2
`,
  Ocean: `palette = 0=#000000
palette = 1=#e64c4c
palette = 2=#00a600
palette = 3=#999900
palette = 4=#0000b2
palette = 5=#d826d8
palette = 6=#00a6b2
palette = 7=#bfbfbf
palette = 8=#808080
palette = 9=#ff1a1a
palette = 10=#00d900
palette = 11=#e5e500
palette = 12=#7373ff
palette = 13=#e500e5
palette = 14=#00e5e5
palette = 15=#e5e5e5
background = #224fbc
foreground = #ffffff
cursor-color = #7f7f7f
cursor-text = #ffffff
selection-background = #216dff
selection-foreground = #ffffff
`,
  "Oceanic Material": `palette = 0=#000000
palette = 1=#ee2b2a
palette = 2=#40a33f
palette = 3=#ffea2e
palette = 4=#1e80f0
palette = 5=#8800a0
palette = 6=#16afca
palette = 7=#a4a4a4
palette = 8=#777777
palette = 9=#dc5c60
palette = 10=#70be71
palette = 11=#fff163
palette = 12=#54a4f3
palette = 13=#aa4dbc
palette = 14=#42c7da
palette = 15=#ffffff
background = #1c262b
foreground = #c2c8d7
cursor-color = #b3b8c3
cursor-text = #ffffff
selection-background = #6dc2b8
selection-foreground = #e8eefd
`,
  "Oceanic Next": `palette = 0=#162c35
palette = 1=#ec5f67
palette = 2=#99c794
palette = 3=#fac863
palette = 4=#6699cc
palette = 5=#c594c5
palette = 6=#5fb3b3
palette = 7=#ffffff
palette = 8=#65737e
palette = 9=#ec5f67
palette = 10=#99c794
palette = 11=#fac863
palette = 12=#6699cc
palette = 13=#c594c5
palette = 14=#5fb3b3
palette = 15=#ffffff
background = #162c35
foreground = #c0c5ce
cursor-color = #c0c5ce
cursor-text = #1b2b34
selection-background = #4f5b66
selection-foreground = #c0c5ce
`,
  Ollie: `palette = 0=#000000
palette = 1=#ac2e31
palette = 2=#31ac61
palette = 3=#ac4300
palette = 4=#2d57ac
palette = 5=#b08528
palette = 6=#1fa6ac
palette = 7=#8a8eac
palette = 8=#684432
palette = 9=#ff3d48
palette = 10=#3bff99
palette = 11=#ff5e1e
palette = 12=#4488ff
palette = 13=#ffc21d
palette = 14=#1ffaff
palette = 15=#5b6ea7
background = #222125
foreground = #8a8dae
cursor-color = #5b6ea7
cursor-text = #2a292d
selection-background = #1e3a66
selection-foreground = #8a8eac
`,
  "One Dark Two": `palette = 0=#1d1f23
palette = 1=#e27881
palette = 2=#98c379
palette = 3=#eac786
palette = 4=#71b9f4
palette = 5=#c88bda
palette = 6=#62bac6
palette = 7=#c9ccd3
palette = 8=#4a505a
palette = 9=#e68991
palette = 10=#a8cc8e
palette = 11=#edcf97
palette = 12=#8dc7f6
palette = 13=#d3a2e2
palette = 14=#78c4ce
palette = 15=#e6e6e6
background = #21252b
foreground = #e6e6e6
cursor-color = #e6e6e6
cursor-text = #1d1f23
selection-background = #393e47
selection-foreground = #e6e6e6
`,
  "One Double Dark": `palette = 0=#3d4452
palette = 1=#f16372
palette = 2=#8cc570
palette = 3=#ecbe70
palette = 4=#3fb1f5
palette = 5=#d373e3
palette = 6=#17b9c4
palette = 7=#dbdfe5
palette = 8=#525d6f
palette = 9=#ff777b
palette = 10=#82d882
palette = 11=#f5c065
palette = 12=#6dcaff
palette = 13=#ff7bf4
palette = 14=#00e5fb
palette = 15=#f7f9fc
background = #282c34
foreground = #dbdfe5
cursor-color = #f5e0dc
cursor-text = #9aa3c1
selection-background = #585b70
selection-foreground = #cdd6f4
`,
  "One Double Light": `palette = 0=#454b58
palette = 1=#f74840
palette = 2=#25a343
palette = 3=#cc8100
palette = 4=#0087c1
palette = 5=#b50da9
palette = 6=#009ab7
palette = 7=#c5b2b3
palette = 8=#0e131f
palette = 9=#ff3711
palette = 10=#00b90e
palette = 11=#ec9900
palette = 12=#1065de
palette = 13=#e500d8
palette = 14=#00b4dd
palette = 15=#ffffff
background = #fafafa
foreground = #383a43
cursor-color = #1a1919
cursor-text = #dbdfe5
selection-background = #454e5e
selection-foreground = #1a1919
`,
  "One Half Dark": `palette = 0=#282c34
palette = 1=#e06c75
palette = 2=#98c379
palette = 3=#e5c07b
palette = 4=#61afef
palette = 5=#c678dd
palette = 6=#56b6c2
palette = 7=#dcdfe4
palette = 8=#5d677a
palette = 9=#e06c75
palette = 10=#98c379
palette = 11=#e5c07b
palette = 12=#61afef
palette = 13=#c678dd
palette = 14=#56b6c2
palette = 15=#dcdfe4
background = #282c34
foreground = #dcdfe4
cursor-color = #a3b3cc
cursor-text = #e9ecf1
selection-background = #474e5d
selection-foreground = #dcdfe4
`,
  "One Half Light": `palette = 0=#383a42
palette = 1=#e45649
palette = 2=#50a14f
palette = 3=#c18401
palette = 4=#0184bc
palette = 5=#a626a4
palette = 6=#0997b3
palette = 7=#bababa
palette = 8=#4f525e
palette = 9=#e06c75
palette = 10=#98c379
palette = 11=#d8b36e
palette = 12=#61afef
palette = 13=#c678dd
palette = 14=#56b6c2
palette = 15=#ffffff
background = #fafafa
foreground = #383a42
cursor-color = #a5b4e5
cursor-text = #383a42
selection-background = #bfceff
selection-foreground = #383a42
`,
  Onenord: `palette = 0=#3b4252
palette = 1=#e06c75
palette = 2=#9ec183
palette = 3=#ebcb8b
palette = 4=#81a1c1
palette = 5=#b988b0
palette = 6=#88c0d0
palette = 7=#e5e9f0
palette = 8=#596377
palette = 9=#e06c75
palette = 10=#9ec183
palette = 11=#ebcb8b
palette = 12=#81a1c1
palette = 13=#b988b0
palette = 14=#8fbcbb
palette = 15=#eceff4
background = #2e3440
foreground = #e5e9f0
cursor-color = #81a1c1
cursor-text = #3b4252
selection-background = #434c5e
selection-foreground = #d8dee9
`,
  "Onenord Light": `palette = 0=#2e3440
palette = 1=#cb4f53
palette = 2=#48a53d
palette = 3=#ee5e25
palette = 4=#3879c5
palette = 5=#9f4aca
palette = 6=#3ea1ad
palette = 7=#b2b6bd
palette = 8=#646a76
palette = 9=#d16366
palette = 10=#5f9e9d
palette = 11=#ba793e
palette = 12=#1b40a6
palette = 13=#9665af
palette = 14=#8fbcbb
palette = 15=#eceff4
background = #f7f8fa
foreground = #2e3440
cursor-color = #3879c5
cursor-text = #f7f8fa
selection-background = #eaebed
selection-foreground = #000000
`,
  "Operator Mono Dark": `palette = 0=#5a5a5a
palette = 1=#ca372d
palette = 2=#4d7b3a
palette = 3=#d4d697
palette = 4=#4387cf
palette = 5=#b86cb4
palette = 6=#72d5c6
palette = 7=#ced4cd
palette = 8=#9a9b99
palette = 9=#c37d62
palette = 10=#83d0a2
palette = 11=#fdfdc5
palette = 12=#89d3f6
palette = 13=#ff2c7a
palette = 14=#82eada
palette = 15=#fdfdf6
background = #191919
foreground = #c3cac2
cursor-color = #fcdc08
cursor-text = #161616
selection-background = #19273b
selection-foreground = #dde5dc
`,
  "Overnight Slumber": `palette = 0=#0a1222
palette = 1=#ffa7c4
palette = 2=#85cc95
palette = 3=#ffcb8b
palette = 4=#8dabe1
palette = 5=#c792eb
palette = 6=#78ccf0
palette = 7=#ffffff
palette = 8=#575656
palette = 9=#ffa7c4
palette = 10=#85cc95
palette = 11=#ffcb8b
palette = 12=#8dabe1
palette = 13=#c792eb
palette = 14=#ffa7c4
palette = 15=#ffffff
background = #0e1729
foreground = #ced2d6
cursor-color = #ffa7c4
cursor-text = #ffffff
selection-background = #1f2b41
selection-foreground = #ced2d6
`,
  owl: `palette = 0=#302c2c
palette = 1=#5a5a5a
palette = 2=#989898
palette = 3=#cacaca
palette = 4=#656565
palette = 5=#b1b1b1
palette = 6=#7f7f7f
palette = 7=#dedede
palette = 8=#5d595b
palette = 9=#da5b2c
palette = 10=#989898
palette = 11=#cacaca
palette = 12=#656565
palette = 13=#b1b1b1
palette = 14=#7f7f7f
palette = 15=#ffffff
background = #2f2b2c
foreground = #dedede
cursor-color = #dedede
cursor-text = #2f2b2c
selection-background = #dedede
selection-foreground = #2f2b2c
`,
  Oxocarbon: `palette = 0=#161616
palette = 1=#00dfdb
palette = 2=#00b4ff
palette = 3=#ff4297
palette = 4=#00c15a
palette = 5=#c693ff
palette = 6=#ff74b8
palette = 7=#f2f4f8
palette = 8=#585858
palette = 9=#00dfdb
palette = 10=#00b4ff
palette = 11=#ff4297
palette = 12=#00c15a
palette = 13=#c693ff
palette = 14=#ff74b8
palette = 15=#f2f4f8
background = #161616
foreground = #f2f4f8
cursor-color = #ffffff
cursor-text = #000000
selection-background = #393939
selection-foreground = #626262
`,
  "Pale Night Hc": `palette = 0=#000000
palette = 1=#f07178
palette = 2=#c3e88d
palette = 3=#ffcb6b
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#89ddff
palette = 7=#ffffff
palette = 8=#737373
palette = 9=#f6a9ae
palette = 10=#dbf1ba
palette = 11=#ffdfa6
palette = 12=#b4ccff
palette = 13=#ddbdf2
palette = 14=#b8eaff
palette = 15=#999999
background = #3e4251
foreground = #cccccc
cursor-color = #ffcb6b
cursor-text = #323232
selection-background = #717cb4
selection-foreground = #80cbc4
`,
  Pandora: `palette = 0=#000000
palette = 1=#ff4242
palette = 2=#74af68
palette = 3=#ffad29
palette = 4=#338f86
palette = 5=#9414e6
palette = 6=#23d7d7
palette = 7=#e2e2e2
palette = 8=#3f5648
palette = 9=#ff3242
palette = 10=#74cd68
palette = 11=#ffb929
palette = 12=#23d7d7
palette = 13=#ff37ff
palette = 14=#00ede1
palette = 15=#ffffff
background = #141e43
foreground = #e1e1e1
cursor-color = #43d58e
cursor-text = #ffffff
selection-background = #2d37ff
selection-foreground = #82e0ff
`,
  "Paraiso Dark": `palette = 0=#2f1e2e
palette = 1=#ef6155
palette = 2=#48b685
palette = 3=#fec418
palette = 4=#06b6ef
palette = 5=#815ba4
palette = 6=#5bc4bf
palette = 7=#a39e9b
palette = 8=#776e71
palette = 9=#ef6155
palette = 10=#48b685
palette = 11=#fec418
palette = 12=#06b6ef
palette = 13=#815ba4
palette = 14=#5bc4bf
palette = 15=#e7e9db
background = #2f1e2e
foreground = #a39e9b
cursor-color = #a39e9b
cursor-text = #2f1e2e
selection-background = #4f424c
selection-foreground = #a39e9b
`,
  "Paul Millr": `palette = 0=#2a2a2a
palette = 1=#ff0000
palette = 2=#79ff0f
palette = 3=#e7bf00
palette = 4=#396bd7
palette = 5=#b449be
palette = 6=#66ccff
palette = 7=#bbbbbb
palette = 8=#666666
palette = 9=#ff0080
palette = 10=#66ff66
palette = 11=#f3d64e
palette = 12=#709aed
palette = 13=#db67e6
palette = 14=#7adff2
palette = 15=#ffffff
background = #000000
foreground = #f2f2f2
cursor-color = #4d4d4d
cursor-text = #ffffff
selection-background = #414141
selection-foreground = #ffffff
`,
  "Pencil Dark": `palette = 0=#212121
palette = 1=#c30771
palette = 2=#10a778
palette = 3=#a89c14
palette = 4=#008ec4
palette = 5=#5f4986
palette = 6=#20a5ba
palette = 7=#d9d9d9
palette = 8=#4f4f4f
palette = 9=#fb007a
palette = 10=#5fd7af
palette = 11=#f3e430
palette = 12=#20bbfc
palette = 13=#6855de
palette = 14=#4fb8cc
palette = 15=#f1f1f1
background = #212121
foreground = #f1f1f1
cursor-color = #20bbfc
cursor-text = #f1f1f1
selection-background = #b6d6fd
selection-foreground = #989898
`,
  "Pencil Light": `palette = 0=#212121
palette = 1=#c30771
palette = 2=#10a778
palette = 3=#a89c14
palette = 4=#008ec4
palette = 5=#523c79
palette = 6=#20a5ba
palette = 7=#b3b3b3
palette = 8=#424242
palette = 9=#fb007a
palette = 10=#52caa2
palette = 11=#c0b100
palette = 12=#20bbfc
palette = 13=#6855de
palette = 14=#4fb8cc
palette = 15=#f1f1f1
background = #f1f1f1
foreground = #424242
cursor-color = #20bbfc
cursor-text = #424242
selection-background = #b6d6fd
selection-foreground = #424242
`,
  Peppermint: `palette = 0=#353535
palette = 1=#e74669
palette = 2=#89d287
palette = 3=#dab853
palette = 4=#449fd0
palette = 5=#da62dc
palette = 6=#65aaaf
palette = 7=#b4b4b4
palette = 8=#535353
palette = 9=#e4859b
palette = 10=#a3cca2
palette = 11=#e1e487
palette = 12=#6fbce2
palette = 13=#e586e7
palette = 14=#96dcdb
palette = 15=#dfdfdf
background = #000000
foreground = #c8c8c8
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #e6e6e6
selection-foreground = #000000
`,
  "Phala Green Dark": `palette = 0=#000000
palette = 1=#ab1500
palette = 2=#00b100
palette = 3=#a9a700
palette = 4=#0223c0
palette = 5=#c22ec0
palette = 6=#00b4c0
palette = 7=#cbcbcb
palette = 8=#797979
palette = 9=#ed2200
palette = 10=#00db00
palette = 11=#eae700
palette = 12=#0433ff
palette = 13=#ed3aea
palette = 14=#00e8ea
palette = 15=#eaeaea
background = #000000
foreground = #c1fc03
cursor-color = #c1fc03
cursor-text = #ff271d
selection-background = #014804
selection-foreground = #ffffff
`,
  "Piatto Light": `palette = 0=#414141
palette = 1=#b23771
palette = 2=#66781e
palette = 3=#cd6f34
palette = 4=#3c5ea8
palette = 5=#a454b2
palette = 6=#66781e
palette = 7=#bfbfbf
palette = 8=#3f3f3f
palette = 9=#db3365
palette = 10=#829429
palette = 11=#cd6f34
palette = 12=#3c5ea8
palette = 13=#a454b2
palette = 14=#829429
palette = 15=#f2f2f2
background = #ffffff
foreground = #414141
cursor-color = #5e77c8
cursor-text = #abbee5
selection-background = #706b4e
selection-foreground = #acbcdc
`,
  Pnevma: `palette = 0=#2f2e2d
palette = 1=#a36666
palette = 2=#90a57d
palette = 3=#d7af87
palette = 4=#7fa5bd
palette = 5=#c79ec4
palette = 6=#8adbb4
palette = 7=#d0d0d0
palette = 8=#4a4845
palette = 9=#d78787
palette = 10=#afbea2
palette = 11=#e4c9af
palette = 12=#a1bdce
palette = 13=#d7beda
palette = 14=#b1e7dd
palette = 15=#efefef
background = #1c1c1c
foreground = #d0d0d0
cursor-color = #e4c9af
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  Poimandres: `palette = 0=#1a1e28
palette = 1=#d0679d
palette = 2=#5de4c7
palette = 3=#fffac2
palette = 4=#89ddff
palette = 5=#fcc5e9
palette = 6=#add7ff
palette = 7=#ffffff
palette = 8=#a6accd
palette = 9=#d0679d
palette = 10=#5de4c7
palette = 11=#fffac2
palette = 12=#add7ff
palette = 13=#fae4fc
palette = 14=#89ddff
palette = 15=#ffffff
background = #1a1e28
foreground = #a6accd
cursor-color = #ffffff
cursor-text = #1a1e29
selection-background = #a6accd
selection-foreground = #ffffff
`,
  "Poimandres Darker": `palette = 0=#16161e
palette = 1=#d0679d
palette = 2=#5de4c7
palette = 3=#fffac2
palette = 4=#89ddff
palette = 5=#fcc5e9
palette = 6=#add7ff
palette = 7=#ffffff
palette = 8=#a6accd
palette = 9=#d0679d
palette = 10=#5de4c7
palette = 11=#fffac2
palette = 12=#add7ff
palette = 13=#fae4fc
palette = 14=#89ddff
palette = 15=#ffffff
background = #16161e
foreground = #a6accd
cursor-color = #ffffff
cursor-text = #16161e
selection-background = #a6accd
selection-foreground = #ffffff
`,
  "Poimandres Storm": `palette = 0=#252b37
palette = 1=#d0679d
palette = 2=#5de4c7
palette = 3=#fffac2
palette = 4=#89ddff
palette = 5=#f087bd
palette = 6=#89ddff
palette = 7=#ffffff
palette = 8=#a6accd
palette = 9=#d0679d
palette = 10=#5de4c7
palette = 11=#fffac2
palette = 12=#add7ff
palette = 13=#f087bd
palette = 14=#add7ff
palette = 15=#ffffff
background = #252b37
foreground = #a6accd
cursor-color = #ffffff
cursor-text = #252b37
selection-background = #a6accd
selection-foreground = #ffffff
`,
  "Poimandres White": `palette = 0=#fefeff
palette = 1=#ff2090
palette = 2=#01dab2
palette = 3=#e5ba4e
palette = 4=#8abacd
palette = 5=#eb8394
palette = 6=#8abacd
palette = 7=#000000
palette = 8=#969cbd
palette = 9=#ff2090
palette = 10=#01dab2
palette = 11=#e5ba4e
palette = 12=#0ebfff
palette = 13=#eb8394
palette = 14=#0ebfff
palette = 15=#000000
background = #fefeff
foreground = #969cbd
cursor-color = #969cbd
cursor-text = #fefeff
selection-background = #969cbd
selection-foreground = #3b3e48
`,
  "Popping And Locking": `palette = 0=#1d2021
palette = 1=#cc241d
palette = 2=#98971a
palette = 3=#d79921
palette = 4=#458588
palette = 5=#b16286
palette = 6=#689d6a
palette = 7=#a89984
palette = 8=#928374
palette = 9=#f42c3e
palette = 10=#b8bb26
palette = 11=#fabd2f
palette = 12=#99c6ca
palette = 13=#d3869b
palette = 14=#7ec16e
palette = 15=#ebdbb2
background = #181921
foreground = #ebdbb2
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #ebdbb2
selection-foreground = #928374
`,
  Powershell: `palette = 0=#000000
palette = 1=#981a22
palette = 2=#098003
palette = 3=#c4a000
palette = 4=#4140c3
palette = 5=#d33682
palette = 6=#0e807f
palette = 7=#7f7c7f
palette = 8=#808080
palette = 9=#ef2929
palette = 10=#1cfe3c
palette = 11=#fefe45
palette = 12=#268ad2
palette = 13=#fe13fa
palette = 14=#29fffe
palette = 15=#c2c1c3
background = #052454
foreground = #f6f6f7
cursor-color = #f6f6f7
cursor-text = #052454
selection-background = #f6f6f7
selection-foreground = #052454
`,
  Primary: `palette = 0=#000000
palette = 1=#db4437
palette = 2=#0f9d58
palette = 3=#f4b400
palette = 4=#4285f4
palette = 5=#db4437
palette = 6=#4285f4
palette = 7=#bfbfbf
palette = 8=#000000
palette = 9=#db4437
palette = 10=#0f9d58
palette = 11=#f4b400
palette = 12=#4285f4
palette = 13=#4285f4
palette = 14=#0f9d58
palette = 15=#ffffff
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #656565
selection-foreground = #eeeeee
`,
  Pro: `palette = 0=#000000
palette = 1=#990000
palette = 2=#00a600
palette = 3=#999900
palette = 4=#2009db
palette = 5=#b200b2
palette = 6=#00a6b2
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#e50000
palette = 10=#00d900
palette = 11=#e5e500
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00e5e5
palette = 15=#e5e5e5
background = #000000
foreground = #f2f2f2
cursor-color = #4d4d4d
cursor-text = #ffffff
selection-background = #414141
selection-foreground = #000000
`,
  "Pro Light": `palette = 0=#000000
palette = 1=#e5492b
palette = 2=#50d148
palette = 3=#c6c440
palette = 4=#3b75ff
palette = 5=#ed66e8
palette = 6=#4ed2de
palette = 7=#c2c2c2
palette = 8=#9f9f9f
palette = 9=#ff6640
palette = 10=#48d53e
palette = 11=#bfbe23
palette = 12=#0082ff
palette = 13=#ff7eff
palette = 14=#3bd1d2
palette = 15=#f2f2f2
background = #ffffff
foreground = #191919
cursor-color = #4d4d4d
cursor-text = #f2f2f2
selection-background = #c1ddff
selection-foreground = #191919
`,
  "Purple Rain": `palette = 0=#000000
palette = 1=#ff260e
palette = 2=#9be205
palette = 3=#ffc400
palette = 4=#00a2fa
palette = 5=#815bb5
palette = 6=#00deef
palette = 7=#ffffff
palette = 8=#565656
palette = 9=#ff4250
palette = 10=#b8e36e
palette = 11=#ffd852
palette = 12=#00a6ff
palette = 13=#ac7bf0
palette = 14=#74fdf3
palette = 15=#ffffff
background = #21084a
foreground = #fffbf6
cursor-color = #ff271d
cursor-text = #ff9a90
selection-background = #287691
selection-foreground = #ffffff
`,
  Purplepeter: `palette = 0=#0a0520
palette = 1=#ff796d
palette = 2=#99b481
palette = 3=#efdfac
palette = 4=#66d9ef
palette = 5=#e78fcd
palette = 6=#ba8cff
palette = 7=#ffba81
palette = 8=#504b63
palette = 9=#f99f92
palette = 10=#b4be8f
palette = 11=#f2e9bf
palette = 12=#79daed
palette = 13=#ba91d4
palette = 14=#a0a0d6
palette = 15=#b9aed3
background = #2a1a4a
foreground = #ece7fa
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #8689c2
selection-foreground = #271c50
`,
  Rapture: `palette = 0=#000000
palette = 1=#fc644d
palette = 2=#7afde1
palette = 3=#fff09b
palette = 4=#6c9bf5
palette = 5=#ff4fa1
palette = 6=#64e0ff
palette = 7=#c0c9e5
palette = 8=#304b66
palette = 9=#fc644d
palette = 10=#7afde1
palette = 11=#fff09b
palette = 12=#6c9bf5
palette = 13=#ff4fa1
palette = 14=#64e0ff
palette = 15=#ffffff
background = #111e2a
foreground = #c0c9e5
cursor-color = #ffffff
cursor-text = #111e2a
selection-background = #304b66
selection-foreground = #ffffff
`,
  "Raycast Dark": `palette = 0=#000000
palette = 1=#ff5360
palette = 2=#59d499
palette = 3=#ffc531
palette = 4=#56c2ff
palette = 5=#cf2f98
palette = 6=#52eee5
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#ff6363
palette = 10=#59d499
palette = 11=#ffc531
palette = 12=#56c2ff
palette = 13=#cf2f98
palette = 14=#52eee5
palette = 15=#ffffff
background = #1a1a1a
foreground = #ffffff
cursor-color = #cccccc
cursor-text = #999999
selection-background = #333333
selection-foreground = #595959
`,
  "Raycast Light": `palette = 0=#000000
palette = 1=#b12424
palette = 2=#006b4f
palette = 3=#f8a300
palette = 4=#138af2
palette = 5=#9a1b6e
palette = 6=#3eb8bf
palette = 7=#bfbfbf
palette = 8=#000000
palette = 9=#b12424
palette = 10=#006b4f
palette = 11=#f8a300
palette = 12=#138af2
palette = 13=#9a1b6e
palette = 14=#3eb8bf
palette = 15=#ffffff
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #404040
selection-background = #e5e5e5
selection-foreground = #000000
`,
  Rebecca: `palette = 0=#12131e
palette = 1=#dd7755
palette = 2=#04dbb5
palette = 3=#f2e7b7
palette = 4=#7aa5ff
palette = 5=#bf9cf9
palette = 6=#56d3c2
palette = 7=#e4e3e9
palette = 8=#666699
palette = 9=#ff92cd
palette = 10=#01eac0
palette = 11=#fffca8
palette = 12=#69c0fa
palette = 13=#c17ff8
palette = 14=#8bfde1
palette = 15=#f4f2f9
background = #292a44
foreground = #e8e6ed
cursor-color = #b89bf9
cursor-text = #292a44
selection-background = #663399
selection-foreground = #f4f2f9
`,
  "Red Alert": `palette = 0=#000000
palette = 1=#d62e4e
palette = 2=#71be6b
palette = 3=#beb86b
palette = 4=#489bee
palette = 5=#e979d7
palette = 6=#6bbeb8
palette = 7=#d6d6d6
palette = 8=#666666
palette = 9=#e02553
palette = 10=#aff08c
palette = 11=#dfddb7
palette = 12=#65aaf1
palette = 13=#ddb7df
palette = 14=#b7dfdd
palette = 15=#ffffff
background = #762423
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #762423
selection-background = #073642
selection-foreground = #ffffff
`,
  "Red Planet": `palette = 0=#202020
palette = 1=#8c3432
palette = 2=#728271
palette = 3=#e8bf6a
palette = 4=#69819e
palette = 5=#896492
palette = 6=#5b8390
palette = 7=#b9aa99
palette = 8=#676767
palette = 9=#b55242
palette = 10=#869985
palette = 11=#ebeb91
palette = 12=#60827e
palette = 13=#de4974
palette = 14=#38add8
palette = 15=#d6bfb8
background = #222222
foreground = #c2b790
cursor-color = #c2b790
cursor-text = #202020
selection-background = #1b324a
selection-foreground = #bcb291
`,
  "Red Sands": `palette = 0=#000000
palette = 1=#ff3f00
palette = 2=#00bb00
palette = 3=#e7b000
palette = 4=#0072ff
palette = 5=#bb00bb
palette = 6=#00bbbb
palette = 7=#bbbbbb
palette = 8=#6e6e6e
palette = 9=#d41a1a
palette = 10=#00bb00
palette = 11=#e7b000
palette = 12=#0072ae
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #7a251e
foreground = #d7c9a7
cursor-color = #ffffff
cursor-text = #000000
selection-background = #a4a390
selection-foreground = #000000
`,
  Relaxed: `palette = 0=#151515
palette = 1=#bc5653
palette = 2=#909d63
palette = 3=#ebc17a
palette = 4=#6a8799
palette = 5=#b06698
palette = 6=#c9dfff
palette = 7=#d9d9d9
palette = 8=#636363
palette = 9=#bc5653
palette = 10=#a0ac77
palette = 11=#ebc17a
palette = 12=#7eaac7
palette = 13=#b06698
palette = 14=#acbbd0
palette = 15=#f7f7f7
background = #353a44
foreground = #d9d9d9
cursor-color = #d9d9d9
cursor-text = #1b1b1b
selection-background = #6a7985
selection-foreground = #d9d9d9
`,
  Retro: `palette = 0=#13a10e
palette = 1=#13a10e
palette = 2=#13a10e
palette = 3=#13a10e
palette = 4=#13a10e
palette = 5=#13a10e
palette = 6=#13a10e
palette = 7=#13a10e
palette = 8=#16ba10
palette = 9=#16ba10
palette = 10=#16ba10
palette = 11=#16ba10
palette = 12=#16ba10
palette = 13=#16ba10
palette = 14=#16ba10
palette = 15=#16ba10
background = #000000
foreground = #13a10e
cursor-color = #13a10e
cursor-text = #000000
selection-background = #ffffff
selection-foreground = #000000
`,
  "Retro Legends": `palette = 0=#262626
palette = 1=#de5454
palette = 2=#45eb45
palette = 3=#f7bf2b
palette = 4=#4066f2
palette = 5=#bf4cf2
palette = 6=#40d9e6
palette = 7=#bfe6bf
palette = 8=#4c594c
palette = 9=#ff6666
palette = 10=#59ff59
palette = 11=#ffd933
palette = 12=#4c80ff
palette = 13=#e666ff
palette = 14=#59e6ff
palette = 15=#f2fff2
background = #0d0d0d
foreground = #45eb45
cursor-color = #45eb45
cursor-text = #0d0d0d
selection-background = #336633
selection-foreground = #f2fff2
`,
  Rippedcasts: `palette = 0=#000000
palette = 1=#cdaf95
palette = 2=#a8ff60
palette = 3=#bfbb1f
palette = 4=#75a5b0
palette = 5=#ff73fd
palette = 6=#5a647e
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#eecbad
palette = 10=#bcee68
palette = 11=#e5e500
palette = 12=#86bdc9
palette = 13=#e500e5
palette = 14=#8c9bc4
palette = 15=#e5e5e5
background = #2b2b2b
foreground = #ffffff
cursor-color = #7f7f7f
cursor-text = #ffffff
selection-background = #5a647e
selection-foreground = #f2f2f2
`,
  "Rose Pine": `palette = 0=#26233a
palette = 1=#eb6f92
palette = 2=#31748f
palette = 3=#f6c177
palette = 4=#9ccfd8
palette = 5=#c4a7e7
palette = 6=#ebbcba
palette = 7=#e0def4
palette = 8=#6e6a86
palette = 9=#eb6f92
palette = 10=#31748f
palette = 11=#f6c177
palette = 12=#9ccfd8
palette = 13=#c4a7e7
palette = 14=#ebbcba
palette = 15=#e0def4
background = #191724
foreground = #e0def4
cursor-color = #e0def4
cursor-text = #191724
selection-background = #403d52
selection-foreground = #e0def4
`,
  "Rose Pine Dawn": `palette = 0=#f2e9e1
palette = 1=#b4637a
palette = 2=#286983
palette = 3=#ea9d34
palette = 4=#56949f
palette = 5=#907aa9
palette = 6=#d7827e
palette = 7=#575279
palette = 8=#9893a5
palette = 9=#b4637a
palette = 10=#286983
palette = 11=#ea9d34
palette = 12=#56949f
palette = 13=#907aa9
palette = 14=#d7827e
palette = 15=#575279
background = #faf4ed
foreground = #575279
cursor-color = #575279
cursor-text = #faf4ed
selection-background = #dfdad9
selection-foreground = #575279
`,
  "Rose Pine Moon": `palette = 0=#393552
palette = 1=#eb6f92
palette = 2=#3e8fb0
palette = 3=#f6c177
palette = 4=#9ccfd8
palette = 5=#c4a7e7
palette = 6=#ea9a97
palette = 7=#e0def4
palette = 8=#6e6a86
palette = 9=#eb6f92
palette = 10=#3e8fb0
palette = 11=#f6c177
palette = 12=#9ccfd8
palette = 13=#c4a7e7
palette = 14=#ea9a97
palette = 15=#e0def4
background = #232136
foreground = #e0def4
cursor-color = #e0def4
cursor-text = #232136
selection-background = #44415a
selection-foreground = #e0def4
`,
  "Rouge 2": `palette = 0=#5d5d6b
palette = 1=#c6797e
palette = 2=#969e92
palette = 3=#dbcdab
palette = 4=#6e94b9
palette = 5=#4c4e78
palette = 6=#8ab6c1
palette = 7=#e8e8ea
palette = 8=#616274
palette = 9=#c6797e
palette = 10=#e6dcc4
palette = 11=#e6dcc4
palette = 12=#98b3cd
palette = 13=#8283a1
palette = 14=#abcbd3
palette = 15=#e8e8ea
background = #17182b
foreground = #a2a3aa
cursor-color = #969e92
cursor-text = #ffffff
selection-background = #5d5d6b
selection-foreground = #dfe5ee
`,
  Royal: `palette = 0=#241f2b
palette = 1=#91284c
palette = 2=#23801c
palette = 3=#b49d27
palette = 4=#6580b0
palette = 5=#674d96
palette = 6=#8aaabe
palette = 7=#524966
palette = 8=#3e3a4a
palette = 9=#d5356c
palette = 10=#2cd946
palette = 11=#fde83b
palette = 12=#90baf9
palette = 13=#a479e3
palette = 14=#acd4eb
palette = 15=#9e8cbd
background = #100815
foreground = #514968
cursor-color = #524966
cursor-text = #100613
selection-background = #1f1d2b
selection-foreground = #a593cd
`,
  Ryuuko: `palette = 0=#2c3941
palette = 1=#865f5b
palette = 2=#66907d
palette = 3=#b1a990
palette = 4=#6a8e95
palette = 5=#b18a73
palette = 6=#88b2ac
palette = 7=#ececec
palette = 8=#5d7079
palette = 9=#865f5b
palette = 10=#66907d
palette = 11=#b1a990
palette = 12=#6a8e95
palette = 13=#b18a73
palette = 14=#88b2ac
palette = 15=#ececec
background = #2c3941
foreground = #ececec
cursor-color = #ececec
cursor-text = #002831
selection-background = #002831
selection-foreground = #819090
`,
  Sakura: `palette = 0=#000000
palette = 1=#d52370
palette = 2=#41af1a
palette = 3=#bc7053
palette = 4=#6964ab
palette = 5=#c71fbf
palette = 6=#939393
palette = 7=#998eac
palette = 8=#786d69
palette = 9=#f41d99
palette = 10=#22e529
palette = 11=#f59574
palette = 12=#9892f1
palette = 13=#e90cdd
palette = 14=#eeeeee
palette = 15=#cbb6ff
background = #18131e
foreground = #dd7bdc
cursor-color = #ff65fd
cursor-text = #24242e
selection-background = #c05cbf
selection-foreground = #24242e
`,
  "Scarlet Protocol": `palette = 0=#101116
palette = 1=#ff0051
palette = 2=#00dc84
palette = 3=#faf945
palette = 4=#0271b6
palette = 5=#ca30c7
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#ff6e67
palette = 10=#5ffa68
palette = 11=#fffc67
palette = 12=#6871ff
palette = 13=#bd35ec
palette = 14=#60fdff
palette = 15=#ffffff
background = #1c153d
foreground = #e41951
cursor-color = #76ff9f
cursor-text = #a6a6a6
selection-background = #c1deff
selection-foreground = #000000
`,
  "Sea Shells": `palette = 0=#17384c
palette = 1=#d15123
palette = 2=#027c9b
palette = 3=#fca02f
palette = 4=#1e4950
palette = 5=#68d4f1
palette = 6=#50a3b5
palette = 7=#deb88d
palette = 8=#434b53
palette = 9=#d48678
palette = 10=#628d98
palette = 11=#fdd39f
palette = 12=#1bbcdd
palette = 13=#bbe3ee
palette = 14=#87acb4
palette = 15=#fee4ce
background = #09141b
foreground = #deb88d
cursor-color = #fca02f
cursor-text = #08131a
selection-background = #1e4962
selection-foreground = #fee4ce
`,
  "Seafoam Pastel": `palette = 0=#757575
palette = 1=#825d4d
palette = 2=#728c62
palette = 3=#ada16d
palette = 4=#4d7b82
palette = 5=#8a7267
palette = 6=#729494
palette = 7=#e0e0e0
palette = 8=#8a8a8a
palette = 9=#cf937a
palette = 10=#98d9aa
palette = 11=#fae79d
palette = 12=#7ac3cf
palette = 13=#d6b2a1
palette = 14=#ade0e0
palette = 15=#e0e0e0
background = #243435
foreground = #d4e7d4
cursor-color = #57647a
cursor-text = #323232
selection-background = #ffffff
selection-foreground = #9e8b13
`,
  "Selenized Black": `palette = 0=#252525
palette = 1=#ed4a46
palette = 2=#70b433
palette = 3=#dbb32d
palette = 4=#368aeb
palette = 5=#eb6eb7
palette = 6=#3fc5b7
palette = 7=#b9b9b9
palette = 8=#777777
palette = 9=#ff5e56
palette = 10=#83c746
palette = 11=#efc541
palette = 12=#4f9cfe
palette = 13=#ff81ca
palette = 14=#56d8c9
palette = 15=#dedede
background = #181818
foreground = #b9b9b9
cursor-color = #777777
cursor-text = #181818
selection-background = #3b3b3b
selection-foreground = #646464
`,
  "Selenized Dark": `palette = 0=#184956
palette = 1=#fa5750
palette = 2=#75b938
palette = 3=#dbb32d
palette = 4=#4695f7
palette = 5=#f275be
palette = 6=#41c7b9
palette = 7=#adbcbc
palette = 8=#72898f
palette = 9=#ff665c
palette = 10=#84c747
palette = 11=#ebc13d
palette = 12=#58a3ff
palette = 13=#ff84cd
palette = 14=#53d6c7
palette = 15=#cad8d9
background = #103c48
foreground = #adbcbc
cursor-color = #adbcbc
cursor-text = #103c48
selection-background = #184956
selection-foreground = #53d6c7
`,
  "Selenized Light": `palette = 0=#ece3cc
palette = 1=#d2212d
palette = 2=#489100
palette = 3=#ad8900
palette = 4=#0072d4
palette = 5=#ca4898
palette = 6=#009c8f
palette = 7=#53676d
palette = 8=#909995
palette = 9=#cc1729
palette = 10=#428b00
palette = 11=#a78300
palette = 12=#006dce
palette = 13=#c44392
palette = 14=#00978a
palette = 15=#3a4d53
background = #fbf3db
foreground = #53676d
cursor-color = #53676d
cursor-text = #fbf3db
selection-background = #ece3cc
selection-foreground = #00978a
`,
  "Seoulbones Dark": `palette = 0=#4b4b4b
palette = 1=#e388a3
palette = 2=#98bd99
palette = 3=#ffdf9b
palette = 4=#97bdde
palette = 5=#a5a6c5
palette = 6=#6fbdbe
palette = 7=#dddddd
palette = 8=#797172
palette = 9=#eb99b1
palette = 10=#8fcd92
palette = 11=#ffe5b3
palette = 12=#a2c8e9
palette = 13=#b2b3da
palette = 14=#6bcacb
palette = 15=#a8a8a8
background = #4b4b4b
foreground = #dddddd
cursor-color = #e2e2e2
cursor-text = #4b4b4b
selection-background = #777777
selection-foreground = #dddddd
`,
  "Seoulbones Light": `palette = 0=#e2e2e2
palette = 1=#dc5284
palette = 2=#628562
palette = 3=#c48562
palette = 4=#0084a3
palette = 5=#896788
palette = 6=#008586
palette = 7=#555555
palette = 8=#a5a0a1
palette = 9=#be3c6d
palette = 10=#487249
palette = 11=#a76b48
palette = 12=#006f89
palette = 13=#7f4c7e
palette = 14=#006f70
palette = 15=#777777
background = #e2e2e2
foreground = #555555
cursor-color = #555555
cursor-text = #e2e2e2
selection-background = #cccccc
selection-foreground = #555555
`,
  Seti: `palette = 0=#323232
palette = 1=#c22832
palette = 2=#8ec43d
palette = 3=#e0c64f
palette = 4=#43a5d5
palette = 5=#8b57b5
palette = 6=#8ec43d
palette = 7=#eeeeee
palette = 8=#3f3f3f
palette = 9=#c22832
palette = 10=#8ec43d
palette = 11=#e0c64f
palette = 12=#43a5d5
palette = 13=#8b57b5
palette = 14=#8ec43d
palette = 15=#ffffff
background = #111213
foreground = #cacecd
cursor-color = #e3bf21
cursor-text = #ad8b00
selection-background = #303233
selection-foreground = #cacecd
`,
  "Shades Of Purple": `palette = 0=#000000
palette = 1=#d90429
palette = 2=#3ad900
palette = 3=#ffe700
palette = 4=#6943ff
palette = 5=#ff2c70
palette = 6=#00c5c7
palette = 7=#c7c7c7
palette = 8=#686868
palette = 9=#f92a1c
palette = 10=#43d426
palette = 11=#f1d000
palette = 12=#6871ff
palette = 13=#ff77ff
palette = 14=#79e8fb
palette = 15=#ffffff
background = #1e1d40
foreground = #ffffff
cursor-color = #fad000
cursor-text = #98998e
selection-background = #b362ff
selection-foreground = #c2c2c2
`,
  Shaman: `palette = 0=#012026
palette = 1=#b2302d
palette = 2=#00a941
palette = 3=#5e8baa
palette = 4=#449a86
palette = 5=#00599d
palette = 6=#5d7e19
palette = 7=#405555
palette = 8=#384451
palette = 9=#ff4242
palette = 10=#2aea5e
palette = 11=#8ed4fd
palette = 12=#61d5ba
palette = 13=#1298ff
palette = 14=#98d028
palette = 15=#58fbd6
background = #001015
foreground = #405555
cursor-color = #4afcd6
cursor-text = #031413
selection-background = #415555
selection-foreground = #5afad6
`,
  Slate: `palette = 0=#222222
palette = 1=#e2a8bf
palette = 2=#81d778
palette = 3=#c4c9c0
palette = 4=#335856
palette = 5=#a481d3
palette = 6=#15ab9c
palette = 7=#02c5e0
palette = 8=#ffffff
palette = 9=#ffcdd9
palette = 10=#beffa8
palette = 11=#d0ccca
palette = 12=#7ab0d2
palette = 13=#c5a7d9
palette = 14=#8cdfe0
palette = 15=#e0e0e0
background = #222222
foreground = #35b1d2
cursor-color = #87d3c4
cursor-text = #323232
selection-background = #0f3754
selection-foreground = #2dffc0
`,
  "Sleepy Hollow": `palette = 0=#572100
palette = 1=#ba3934
palette = 2=#91773f
palette = 3=#b55600
palette = 4=#5f63b4
palette = 5=#a17c7b
palette = 6=#8faea9
palette = 7=#af9a91
palette = 8=#4e4b61
palette = 9=#d9443f
palette = 10=#d6b04e
palette = 11=#f66813
palette = 12=#8086ef
palette = 13=#e2c2bb
palette = 14=#a4dce7
palette = 15=#d2c7a9
background = #121214
foreground = #af9a91
cursor-color = #af9a91
cursor-text = #391a02
selection-background = #575256
selection-foreground = #d2c7a9
`,
  Smyck: `palette = 0=#000000
palette = 1=#b84131
palette = 2=#7da900
palette = 3=#c4a500
palette = 4=#62a3c4
palette = 5=#ba8acc
palette = 6=#207383
palette = 7=#a1a1a1
palette = 8=#7a7a7a
palette = 9=#d6837c
palette = 10=#c4f137
palette = 11=#fee14d
palette = 12=#8dcff0
palette = 13=#f79aff
palette = 14=#6ad9cf
palette = 15=#f7f7f7
background = #1b1b1b
foreground = #f7f7f7
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #207483
selection-foreground = #f7f7f7
`,
  Snazzy: `palette = 0=#000000
palette = 1=#fc4346
palette = 2=#50fb7c
palette = 3=#f0fb8c
palette = 4=#49baff
palette = 5=#fc4cb4
palette = 6=#8be9fe
palette = 7=#ededec
palette = 8=#555555
palette = 9=#fc4346
palette = 10=#50fb7c
palette = 11=#f0fb8c
palette = 12=#49baff
palette = 13=#fc4cb4
palette = 14=#8be9fe
palette = 15=#ededec
background = #1e1f29
foreground = #ebece6
cursor-color = #e4e4e4
cursor-text = #a9a9a9
selection-background = #81aec6
selection-foreground = #000000
`,
  "Snazzy Soft": `palette = 0=#000000
palette = 1=#ff5c57
palette = 2=#5af78e
palette = 3=#f3f99d
palette = 4=#57c7ff
palette = 5=#ff6ac1
palette = 6=#9aedfe
palette = 7=#f1f1f0
palette = 8=#686868
palette = 9=#ff5c57
palette = 10=#5af78e
palette = 11=#f3f99d
palette = 12=#57c7ff
palette = 13=#ff6ac1
palette = 14=#9aedfe
palette = 15=#f1f1f0
background = #282a36
foreground = #eff0eb
cursor-color = #eaeaea
cursor-text = #282a36
selection-background = #92bcd0
selection-foreground = #000000
`,
  "Soft Server": `palette = 0=#000000
palette = 1=#a2686a
palette = 2=#9aa56a
palette = 3=#a3906a
palette = 4=#6b8fa3
palette = 5=#6a71a3
palette = 6=#6ba58f
palette = 7=#99a3a2
palette = 8=#666c6c
palette = 9=#dd5c60
palette = 10=#bfdf55
palette = 11=#deb360
palette = 12=#62b1df
palette = 13=#606edf
palette = 14=#64e39c
palette = 15=#d2e0de
background = #242626
foreground = #99a3a2
cursor-color = #d2e0de
cursor-text = #000000
selection-background = #7f8786
selection-foreground = #effffe
`,
  "Solarized Darcula": `palette = 0=#25292a
palette = 1=#f24840
palette = 2=#629655
palette = 3=#b68800
palette = 4=#2075c7
palette = 5=#797fd4
palette = 6=#15968d
palette = 7=#d2d8d9
palette = 8=#65696a
palette = 9=#f24840
palette = 10=#629655
palette = 11=#b68800
palette = 12=#2075c7
palette = 13=#797fd4
palette = 14=#15968d
palette = 15=#d2d8d9
background = #3d3f41
foreground = #d2d8d9
cursor-color = #708284
cursor-text = #002831
selection-background = #214283
selection-foreground = #d2d8d9
`,
  "Solarized Dark Higher Contrast": `palette = 0=#002831
palette = 1=#d11c24
palette = 2=#6cbe6c
palette = 3=#a57706
palette = 4=#2176c7
palette = 5=#c61c6f
palette = 6=#259286
palette = 7=#eae3cb
palette = 8=#006488
palette = 9=#f5163b
palette = 10=#51ef84
palette = 11=#b27e28
palette = 12=#178ec8
palette = 13=#e24d8e
palette = 14=#00b39e
palette = 15=#fcf4dc
background = #001e27
foreground = #9cc2c3
cursor-color = #f34b00
cursor-text = #002831
selection-background = #003748
selection-foreground = #7a8f8e
`,
  "Solarized Dark Patched": `palette = 0=#002831
palette = 1=#d11c24
palette = 2=#738a05
palette = 3=#a57706
palette = 4=#2176c7
palette = 5=#c61c6f
palette = 6=#259286
palette = 7=#eae3cb
palette = 8=#475b62
palette = 9=#bd3613
palette = 10=#475b62
palette = 11=#536870
palette = 12=#708284
palette = 13=#5956ba
palette = 14=#819090
palette = 15=#fcf4dc
background = #001e27
foreground = #708284
cursor-color = #708284
cursor-text = #002831
selection-background = #002831
selection-foreground = #819090
`,
  "Solarized Osaka Night": `palette = 0=#15161e
palette = 1=#f7768e
palette = 2=#9ece6a
palette = 3=#e0af68
palette = 4=#7aa2f7
palette = 5=#bb9af7
palette = 6=#7dcfff
palette = 7=#a9b1d6
palette = 8=#414868
palette = 9=#f7768e
palette = 10=#9ece6a
palette = 11=#e0af68
palette = 12=#7aa2f7
palette = 13=#bb9af7
palette = 14=#7dcfff
palette = 15=#c0caf5
background = #1a1b26
foreground = #c0caf5
cursor-color = #c0caf5
cursor-text = #1a1b26
selection-background = #283457
selection-foreground = #c0caf5
`,
  Sonokai: `palette = 0=#181819
palette = 1=#fc5d7c
palette = 2=#9ed072
palette = 3=#e7c664
palette = 4=#76cce0
palette = 5=#b39df3
palette = 6=#f39660
palette = 7=#e2e2e3
palette = 8=#7f8490
palette = 9=#fc5d7c
palette = 10=#9ed072
palette = 11=#e7c664
palette = 12=#76cce0
palette = 13=#b39df3
palette = 14=#f39660
palette = 15=#e2e2e3
background = #2c2e34
foreground = #e2e2e3
cursor-color = #e2e2e3
cursor-text = #2c2e34
selection-background = #414550
selection-foreground = #e2e2e3
`,
  Spacedust: `palette = 0=#6e5346
palette = 1=#e35b00
palette = 2=#5cab96
palette = 3=#e3cd7b
palette = 4=#0f548b
palette = 5=#e35b00
palette = 6=#06afc7
palette = 7=#f0f1ce
palette = 8=#684c31
palette = 9=#ff8a3a
palette = 10=#aecab8
palette = 11=#ffc878
palette = 12=#67a0ce
palette = 13=#ff8a3a
palette = 14=#83a7b4
palette = 15=#fefff1
background = #0a1e24
foreground = #ecf0c1
cursor-color = #708284
cursor-text = #002831
selection-background = #0a385c
selection-foreground = #ffffff
`,
  Spacegray: `palette = 0=#000000
palette = 1=#b04b57
palette = 2=#87b379
palette = 3=#e5c179
palette = 4=#7d8fa4
palette = 5=#a47996
palette = 6=#85a7a5
palette = 7=#b3b8c3
palette = 8=#4c4c4c
palette = 9=#b04b57
palette = 10=#87b379
palette = 11=#e5c179
palette = 12=#7d8fa4
palette = 13=#a47996
palette = 14=#85a7a5
palette = 15=#ffffff
background = #20242d
foreground = #b3b8c3
cursor-color = #b3b8c3
cursor-text = #1d1f21
selection-background = #16181e
selection-foreground = #b3b8c3
`,
  "Spacegray Bright": `palette = 0=#080808
palette = 1=#bc5553
palette = 2=#a0b56c
palette = 3=#f6c987
palette = 4=#7baec1
palette = 5=#b98aae
palette = 6=#85c9b8
palette = 7=#d8d8d8
palette = 8=#626262
palette = 9=#bc5553
palette = 10=#a0b56c
palette = 11=#f6c987
palette = 12=#7baec1
palette = 13=#b98aae
palette = 14=#85c9b8
palette = 15=#f7f7f7
background = #2a2e3a
foreground = #f3f3f3
cursor-color = #c6c6c6
cursor-text = #8c8c8c
selection-background = #cacaca
selection-foreground = #000000
`,
  "Spacegray Eighties": `palette = 0=#15171c
palette = 1=#ec5f67
palette = 2=#81a764
palette = 3=#fec254
palette = 4=#5486c0
palette = 5=#bf83c1
palette = 6=#57c2c1
palette = 7=#efece7
palette = 8=#555555
palette = 9=#ff6973
palette = 10=#93d493
palette = 11=#ffd256
palette = 12=#4d84d1
palette = 13=#ff55ff
palette = 14=#83e9e4
palette = 15=#ffffff
background = #222222
foreground = #bdbaae
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #272e35
selection-foreground = #ffffff
`,
  "Spacegray Eighties Dull": `palette = 0=#15171c
palette = 1=#b24a56
palette = 2=#92b477
palette = 3=#c6735a
palette = 4=#7c8fa5
palette = 5=#a5789e
palette = 6=#80cdcb
palette = 7=#b3b8c3
palette = 8=#555555
palette = 9=#ec5f67
palette = 10=#89e986
palette = 11=#fec254
palette = 12=#5486c0
palette = 13=#bf83c1
palette = 14=#58c2c1
palette = 15=#ffffff
background = #222222
foreground = #c9c6bc
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #272e36
selection-foreground = #ffffff
`,
  Spiderman: `palette = 0=#1b1d1e
palette = 1=#e60813
palette = 2=#e22928
palette = 3=#e24756
palette = 4=#2c3fff
palette = 5=#2435db
palette = 6=#3256ff
palette = 7=#fffef6
palette = 8=#505354
palette = 9=#ff0325
palette = 10=#ff3338
palette = 11=#fe3a35
palette = 12=#1d50ff
palette = 13=#747cff
palette = 14=#6184ff
palette = 15=#fffff9
background = #1b1d1e
foreground = #e3e3e3
cursor-color = #2c3fff
cursor-text = #000000
selection-background = #070e50
selection-foreground = #f0272d
`,
  Spring: `palette = 0=#000000
palette = 1=#ff4d83
palette = 2=#1f8c3b
palette = 3=#1fc95b
palette = 4=#1dd3ee
palette = 5=#8959a8
palette = 6=#3e999f
palette = 7=#bfbfbf
palette = 8=#000000
palette = 9=#ff0021
palette = 10=#1fc231
palette = 11=#d5b807
palette = 12=#15a9fd
palette = 13=#8959a8
palette = 14=#3e999f
palette = 15=#ffffff
background = #ffffff
foreground = #4d4d4c
cursor-color = #4d4d4c
cursor-text = #ffffff
selection-background = #d6d6d6
selection-foreground = #4d4d4c
`,
  Square: `palette = 0=#050505
palette = 1=#e9897c
palette = 2=#b6377d
palette = 3=#ecebbe
palette = 4=#a9cdeb
palette = 5=#75507b
palette = 6=#c9caec
palette = 7=#f2f2f2
palette = 8=#474747
palette = 9=#f99286
palette = 10=#c3f786
palette = 11=#fcfbcc
palette = 12=#b6defb
palette = 13=#ad7fa8
palette = 14=#d7d9fc
palette = 15=#e2e2e2
background = #1a1a1a
foreground = #acacab
cursor-color = #fcfbcc
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  "Squirrelsong Dark": `palette = 0=#372920
palette = 1=#ba4138
palette = 2=#468336
palette = 3=#d4b139
palette = 4=#4395c6
palette = 5=#855fb8
palette = 6=#2f9794
palette = 7=#d3b9a2
palette = 8=#704f39
palette = 9=#df4d43
palette = 10=#659a4c
palette = 11=#e8c23f
palette = 12=#4ca4db
palette = 13=#9d70da
palette = 14=#60aca9
palette = 15=#f2d4bb
background = #372920
foreground = #b19b89
cursor-color = #b19b89
cursor-text = #372920
selection-background = #5b402e
selection-foreground = #b19b89
`,
  Srcery: `palette = 0=#1c1b19
palette = 1=#ef2f27
palette = 2=#519f50
palette = 3=#fbb829
palette = 4=#2c78bf
palette = 5=#e02c6d
palette = 6=#0aaeb3
palette = 7=#baa67f
palette = 8=#918175
palette = 9=#f75341
palette = 10=#98bc37
palette = 11=#fed06e
palette = 12=#68a8e4
palette = 13=#ff5c8f
palette = 14=#2be4d0
palette = 15=#fce8c3
background = #1c1b19
foreground = #fce8c3
cursor-color = #fbb829
cursor-text = #1c1b19
selection-background = #fce8c3
selection-foreground = #1c1b19
`,
  Starlight: `palette = 0=#242424
palette = 1=#f62b5a
palette = 2=#47b413
palette = 3=#e3c401
palette = 4=#24acd4
palette = 5=#f2affd
palette = 6=#13c299
palette = 7=#e6e6e6
palette = 8=#616161
palette = 9=#ff4d51
palette = 10=#35d450
palette = 11=#e9e836
palette = 12=#5dc5f8
palette = 13=#feabf2
palette = 14=#24dfc4
palette = 15=#ffffff
background = #242424
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #242424
selection-background = #ffffff
selection-foreground = #242424
`,
  Sublette: `palette = 0=#253045
palette = 1=#ee5577
palette = 2=#55ee77
palette = 3=#ffdd88
palette = 4=#5588ff
palette = 5=#ff77cc
palette = 6=#44eeee
palette = 7=#f5f5da
palette = 8=#405570
palette = 9=#ee6655
palette = 10=#99ee77
palette = 11=#ffff77
palette = 12=#77bbff
palette = 13=#aa88ff
palette = 14=#55ffbb
palette = 15=#ffffee
background = #202535
foreground = #ccced0
cursor-color = #ccced0
cursor-text = #202535
selection-background = #ccced0
selection-foreground = #202535
`,
  Subliminal: `palette = 0=#7f7f7f
palette = 1=#e15a60
palette = 2=#a9cfa4
palette = 3=#ffe2a9
palette = 4=#6699cc
palette = 5=#f1a5ab
palette = 6=#5fb3b3
palette = 7=#d4d4d4
palette = 8=#7f7f7f
palette = 9=#e15a60
palette = 10=#a9cfa4
palette = 11=#ffe2a9
palette = 12=#6699cc
palette = 13=#f1a5ab
palette = 14=#5fb3b3
palette = 15=#d4d4d4
background = #282c35
foreground = #d4d4d4
cursor-color = #c7c7c7
cursor-text = #8c8c8c
selection-background = #484e5b
selection-foreground = #ffffff
`,
  Sugarplum: `palette = 0=#111147
palette = 1=#5ca8dc
palette = 2=#53b397
palette = 3=#249a84
palette = 4=#db7ddd
palette = 5=#d0beee
palette = 6=#f9f3f9
palette = 7=#a175d4
palette = 8=#44447a
palette = 9=#5cb5dc
palette = 10=#52deb5
palette = 11=#01f5c7
palette = 12=#fa5dfd
palette = 13=#c6a5fd
palette = 14=#ffffff
palette = 15=#b577fd
background = #111147
foreground = #db7ddd
cursor-color = #53b397
cursor-text = #93f3d7
selection-background = #5ca8dc
selection-foreground = #ead8ff
`,
  Sundried: `palette = 0=#302b2a
palette = 1=#a7463d
palette = 2=#587744
palette = 3=#9d602a
palette = 4=#485b98
palette = 5=#864651
palette = 6=#9c814f
palette = 7=#c9c9c9
palette = 8=#4d4e48
palette = 9=#aa000c
palette = 10=#128c21
palette = 11=#fc6a21
palette = 12=#7999f7
palette = 13=#fd8aa1
palette = 14=#fad484
palette = 15=#ffffff
background = #1a1818
foreground = #c9c9c9
cursor-color = #ffffff
cursor-text = #191717
selection-background = #302b2a
selection-foreground = #c9c9c9
`,
  Symfonic: `palette = 0=#000000
palette = 1=#dc322f
palette = 2=#56db3a
palette = 3=#ff8400
palette = 4=#0084d4
palette = 5=#b729d9
palette = 6=#ccccff
palette = 7=#ffffff
palette = 8=#414347
palette = 9=#dc322f
palette = 10=#56db3a
palette = 11=#ff8400
palette = 12=#0084d4
palette = 13=#b729d9
palette = 14=#ccccff
palette = 15=#ffffff
background = #000000
foreground = #ffffff
cursor-color = #dc322f
cursor-text = #ffffff
selection-background = #073642
selection-foreground = #ffffff
`,
  Synthwave: `palette = 0=#000000
palette = 1=#f6188f
palette = 2=#1ebb2b
palette = 3=#fdf834
palette = 4=#2186ec
palette = 5=#f85a21
palette = 6=#12c3e2
palette = 7=#ffffff
palette = 8=#7f7094
palette = 9=#f841a0
palette = 10=#25c141
palette = 11=#fdf454
palette = 12=#2f9ded
palette = 13=#f97137
palette = 14=#19cde6
palette = 15=#ffffff
background = #000000
foreground = #dad9c7
cursor-color = #19cde6
cursor-text = #ffffed
selection-background = #19cde6
selection-foreground = #000000
`,
  "Synthwave Alpha": `palette = 0=#241b30
palette = 1=#e60a70
palette = 2=#00986c
palette = 3=#adad3e
palette = 4=#6e29ad
palette = 5=#b300ad
palette = 6=#00b0b1
palette = 7=#b9b1bc
palette = 8=#7f7094
palette = 9=#e60a70
palette = 10=#0ae4a4
palette = 11=#f9f972
palette = 12=#aa54f9
palette = 13=#ff00f6
palette = 14=#00fbfd
palette = 15=#f2f2e3
background = #241b30
foreground = #f2f2e3
cursor-color = #f2f2e3
cursor-text = #241b30
selection-background = #6e29ad
selection-foreground = #f2f2e3
`,
  "Synthwave Everything": `palette = 0=#fefefe
palette = 1=#f97e72
palette = 2=#72f1b8
palette = 3=#fede5d
palette = 4=#6d77b3
palette = 5=#c792ea
palette = 6=#f772e0
palette = 7=#fefefe
palette = 8=#fefefe
palette = 9=#f88414
palette = 10=#72f1b8
palette = 11=#fff951
palette = 12=#36f9f6
palette = 13=#e1acff
palette = 14=#f92aad
palette = 15=#fefefe
background = #2a2139
foreground = #f0eff1
cursor-color = #72f1b8
cursor-text = #1a1a1a
selection-background = #181521
selection-foreground = #f0eff1
`,
  "Tango Adapted": `palette = 0=#000000
palette = 1=#ff0000
palette = 2=#59d600
palette = 3=#e3be00
palette = 4=#00a2ff
palette = 5=#c17ecc
palette = 6=#00d0d6
palette = 7=#c0c5bb
palette = 8=#8f928b
palette = 9=#ff0013
palette = 10=#6dd900
palette = 11=#ccbe00
palette = 12=#88c9ff
palette = 13=#e9a7e1
palette = 14=#00d8d9
palette = 15=#f6f6f4
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  "Tango Half Adapted": `palette = 0=#000000
palette = 1=#ff0000
palette = 2=#4cc300
palette = 3=#e2c000
palette = 4=#008ef6
palette = 5=#a96cb3
palette = 6=#00bdc3
palette = 7=#babfb5
palette = 8=#797d76
palette = 9=#ff0013
palette = 10=#70dc00
palette = 11=#d9c600
palette = 12=#76bfff
palette = 13=#d898d1
palette = 14=#00d0d4
palette = 15=#f4f4f2
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #c1deff
selection-foreground = #000000
`,
  Tearout: `palette = 0=#685742
palette = 1=#cc967b
palette = 2=#97976d
palette = 3=#6c9861
palette = 4=#b5955e
palette = 5=#c9a554
palette = 6=#d7c483
palette = 7=#b5955e
palette = 8=#75644f
palette = 9=#cc967b
palette = 10=#97976d
palette = 11=#6c9861
palette = 12=#b5955e
palette = 13=#c9a554
palette = 14=#d7c483
palette = 15=#b5955e
background = #34392d
foreground = #f4d2ae
cursor-color = #d7c483
cursor-text = #141415
selection-background = #e4c47a
selection-foreground = #000000
`,
  Teerb: `palette = 0=#1c1c1c
palette = 1=#d68686
palette = 2=#aed686
palette = 3=#d7af87
palette = 4=#86aed6
palette = 5=#d6aed6
palette = 6=#8adbb4
palette = 7=#d0d0d0
palette = 8=#4f4f4f
palette = 9=#d68686
palette = 10=#aed686
palette = 11=#e4c9af
palette = 12=#86aed6
palette = 13=#d6aed6
palette = 14=#b1e7dd
palette = 15=#efefef
background = #262626
foreground = #d0d0d0
cursor-color = #e4c9af
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  Terafox: `palette = 0=#2f3239
palette = 1=#e85c51
palette = 2=#7aa4a1
palette = 3=#fda47f
palette = 4=#5a93aa
palette = 5=#ad5c7c
palette = 6=#a1cdd8
palette = 7=#ebebeb
palette = 8=#4e5157
palette = 9=#eb746b
palette = 10=#8eb2af
palette = 11=#fdb292
palette = 12=#73a3b7
palette = 13=#b97490
palette = 14=#afd4de
palette = 15=#eeeeee
background = #152528
foreground = #e6eaea
cursor-color = #e6eaea
cursor-text = #152528
selection-background = #293e40
selection-foreground = #e6eaea
`,
  "Terminal Basic": `palette = 0=#000000
palette = 1=#990000
palette = 2=#00a600
palette = 3=#999900
palette = 4=#0000b2
palette = 5=#b200b2
palette = 6=#00a6b2
palette = 7=#bfbfbf
palette = 8=#666666
palette = 9=#e50000
palette = 10=#00d900
palette = 11=#bfbf00
palette = 12=#0000ff
palette = 13=#e500e5
palette = 14=#00d8d8
palette = 15=#e5e5e5
background = #ffffff
foreground = #000000
cursor-color = #7f7f7f
cursor-text = #000000
selection-background = #a4c9ff
selection-foreground = #000000
`,
  "Terminal Basic Dark": `palette = 0=#000000
palette = 1=#c65339
palette = 2=#6ac44b
palette = 3=#b8b74a
palette = 4=#6444ed
palette = 5=#d357db
palette = 6=#69c1cf
palette = 7=#d1d1d1
palette = 8=#909090
palette = 9=#eb5a3a
palette = 10=#77ea51
palette = 11=#efef53
palette = 12=#d09af9
palette = 13=#eb5af7
palette = 14=#78f1f2
palette = 15=#ededed
background = #1d1e1d
foreground = #ffffff
cursor-color = #9d9d9d
cursor-text = #1d1e1d
selection-background = #3f638a
selection-foreground = #ffffff
`,
  "Thayer Bright": `palette = 0=#1b1d1e
palette = 1=#f92672
palette = 2=#4df840
palette = 3=#f4fd22
palette = 4=#2757d6
palette = 5=#8c54fe
palette = 6=#38c8b5
palette = 7=#ccccc6
palette = 8=#505354
palette = 9=#ff5995
palette = 10=#b6e354
palette = 11=#feed6c
palette = 12=#3f78ff
palette = 13=#9e6ffe
palette = 14=#23cfd5
palette = 15=#f8f8f2
background = #1b1d1e
foreground = #f8f8f8
cursor-color = #fc971f
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  "The Hulk": `palette = 0=#1b1d1e
palette = 1=#269d1b
palette = 2=#13ce30
palette = 3=#63e457
palette = 4=#2525f5
palette = 5=#712c81
palette = 6=#378ca9
palette = 7=#d9d8d1
palette = 8=#505354
palette = 9=#8dff2a
palette = 10=#48ff77
palette = 11=#3afe16
palette = 12=#506b95
palette = 13=#72589d
palette = 14=#4085a6
palette = 15=#e5e6e1
background = #1b1d1e
foreground = #b5b5b5
cursor-color = #16b61b
cursor-text = #000000
selection-background = #4d504c
selection-foreground = #31892f
`,
  "Tinacious Design Dark": `palette = 0=#1d1d26
palette = 1=#ff3399
palette = 2=#00d364
palette = 3=#ffcc66
palette = 4=#00cbff
palette = 5=#cc66ff
palette = 6=#00ceca
palette = 7=#cbcbf0
palette = 8=#636667
palette = 9=#ff2f92
palette = 10=#00d364
palette = 11=#ffd479
palette = 12=#00cbff
palette = 13=#d783ff
palette = 14=#00d5d4
palette = 15=#d5d6f3
background = #1d1d26
foreground = #cbcbf0
cursor-color = #cbcbf0
cursor-text = #999999
selection-background = #ff3399
selection-foreground = #ffffff
`,
  "Tinacious Design Light": `palette = 0=#1d1d26
palette = 1=#ff3399
palette = 2=#00d364
palette = 3=#e5b24d
palette = 4=#00cbff
palette = 5=#cc66ff
palette = 6=#00ceca
palette = 7=#b1b1d6
palette = 8=#636667
palette = 9=#ff2f92
palette = 10=#00d364
palette = 11=#d9ae53
palette = 12=#00cbff
palette = 13=#d783ff
palette = 14=#00c8c7
palette = 15=#d5d6f3
background = #f8f8ff
foreground = #1d1d26
cursor-color = #b1b1d6
cursor-text = #ffffff
selection-background = #ff3399
selection-foreground = #ffffff
`,
  TokyoNight: `palette = 0=#15161e
palette = 1=#f7768e
palette = 2=#9ece6a
palette = 3=#e0af68
palette = 4=#7aa2f7
palette = 5=#bb9af7
palette = 6=#7dcfff
palette = 7=#a9b1d6
palette = 8=#414868
palette = 9=#f7768e
palette = 10=#9ece6a
palette = 11=#e0af68
palette = 12=#7aa2f7
palette = 13=#bb9af7
palette = 14=#7dcfff
palette = 15=#c0caf5
background = #1a1b26
foreground = #c0caf5
cursor-color = #c0caf5
cursor-text = #15161e
selection-background = #33467c
selection-foreground = #c0caf5
`,
  "TokyoNight Day": `palette = 0=#e9e9ed
palette = 1=#f52a65
palette = 2=#587539
palette = 3=#8c6c3e
palette = 4=#2e7de9
palette = 5=#9854f1
palette = 6=#007197
palette = 7=#6172b0
palette = 8=#a1a6c5
palette = 9=#f52a65
palette = 10=#587539
palette = 11=#8c6c3e
palette = 12=#2e7de9
palette = 13=#9854f1
palette = 14=#007197
palette = 15=#3760bf
background = #e1e2e7
foreground = #3760bf
cursor-color = #3760bf
cursor-text = #e1e2e7
selection-background = #99a7df
selection-foreground = #3760bf
`,
  "TokyoNight Moon": `palette = 0=#1b1d2b
palette = 1=#ff757f
palette = 2=#c3e88d
palette = 3=#ffc777
palette = 4=#82aaff
palette = 5=#c099ff
palette = 6=#86e1fc
palette = 7=#828bb8
palette = 8=#444a73
palette = 9=#ff757f
palette = 10=#c3e88d
palette = 11=#ffc777
palette = 12=#82aaff
palette = 13=#c099ff
palette = 14=#86e1fc
palette = 15=#c8d3f5
background = #222436
foreground = #c8d3f5
cursor-color = #c8d3f5
cursor-text = #222436
selection-background = #2d3f76
selection-foreground = #c8d3f5
`,
  "TokyoNight Night": `palette = 0=#15161e
palette = 1=#f7768e
palette = 2=#9ece6a
palette = 3=#e0af68
palette = 4=#7aa2f7
palette = 5=#bb9af7
palette = 6=#7dcfff
palette = 7=#a9b1d6
palette = 8=#414868
palette = 9=#f7768e
palette = 10=#9ece6a
palette = 11=#e0af68
palette = 12=#7aa2f7
palette = 13=#bb9af7
palette = 14=#7dcfff
palette = 15=#c0caf5
background = #1a1b26
foreground = #c0caf5
cursor-color = #c0caf5
cursor-text = #1a1b26
selection-background = #283457
selection-foreground = #c0caf5
`,
  "TokyoNight Storm": `palette = 0=#1d202f
palette = 1=#f7768e
palette = 2=#9ece6a
palette = 3=#e0af68
palette = 4=#7aa2f7
palette = 5=#bb9af7
palette = 6=#7dcfff
palette = 7=#a9b1d6
palette = 8=#4e5575
palette = 9=#f7768e
palette = 10=#9ece6a
palette = 11=#e0af68
palette = 12=#7aa2f7
palette = 13=#bb9af7
palette = 14=#7dcfff
palette = 15=#c0caf5
background = #24283b
foreground = #c0caf5
cursor-color = #c0caf5
cursor-text = #1d202f
selection-background = #364a82
selection-foreground = #c0caf5
`,
  Tomorrow: `palette = 0=#000000
palette = 1=#c82829
palette = 2=#718c00
palette = 3=#eab700
palette = 4=#4271ae
palette = 5=#8959a8
palette = 6=#3e999f
palette = 7=#bfbfbf
palette = 8=#000000
palette = 9=#c82829
palette = 10=#718c00
palette = 11=#eab700
palette = 12=#4271ae
palette = 13=#8959a8
palette = 14=#3e999f
palette = 15=#ffffff
background = #ffffff
foreground = #4d4d4c
cursor-color = #4d4d4c
cursor-text = #ffffff
selection-background = #d6d6d6
selection-foreground = #4d4d4c
`,
  "Tomorrow Night": `palette = 0=#000000
palette = 1=#cc6666
palette = 2=#b5bd68
palette = 3=#f0c674
palette = 4=#81a2be
palette = 5=#b294bb
palette = 6=#8abeb7
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#cc6666
palette = 10=#b5bd68
palette = 11=#f0c674
palette = 12=#81a2be
palette = 13=#b294bb
palette = 14=#8abeb7
palette = 15=#ffffff
background = #1d1f21
foreground = #c5c8c6
cursor-color = #c5c8c6
cursor-text = #1d1f21
selection-background = #373b41
selection-foreground = #c5c8c6
`,
  "Tomorrow Night Blue": `palette = 0=#000000
palette = 1=#ff9da4
palette = 2=#d1f1a9
palette = 3=#ffeead
palette = 4=#bbdaff
palette = 5=#ebbbff
palette = 6=#99ffff
palette = 7=#ffffff
palette = 8=#4c4c4c
palette = 9=#ff9da4
palette = 10=#d1f1a9
palette = 11=#ffeead
palette = 12=#bbdaff
palette = 13=#ebbbff
palette = 14=#99ffff
palette = 15=#ffffff
background = #002451
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #003f8e
selection-background = #003f8e
selection-foreground = #ffffff
`,
  "Tomorrow Night Bright": `palette = 0=#000000
palette = 1=#d54e53
palette = 2=#b9ca4a
palette = 3=#e7c547
palette = 4=#7aa6da
palette = 5=#c397d8
palette = 6=#70c0b1
palette = 7=#ffffff
palette = 8=#404040
palette = 9=#d54e53
palette = 10=#b9ca4a
palette = 11=#e7c547
palette = 12=#7aa6da
palette = 13=#c397d8
palette = 14=#70c0b1
palette = 15=#ffffff
background = #000000
foreground = #eaeaea
cursor-color = #eaeaea
cursor-text = #000000
selection-background = #424242
selection-foreground = #eaeaea
`,
  "Tomorrow Night Burns": `palette = 0=#252525
palette = 1=#832e31
palette = 2=#a63c40
palette = 3=#d3494e
palette = 4=#fc595f
palette = 5=#df9395
palette = 6=#ba8586
palette = 7=#f5f5f5
palette = 8=#5d6f71
palette = 9=#832e31
palette = 10=#a63c40
palette = 11=#d2494e
palette = 12=#fc595f
palette = 13=#df9395
palette = 14=#ba8586
palette = 15=#f5f5f5
background = #151515
foreground = #a1b0b8
cursor-color = #ff443e
cursor-text = #b0c2c4
selection-background = #b0bec5
selection-foreground = #2a2d32
`,
  "Tomorrow Night Eighties": `palette = 0=#000000
palette = 1=#f2777a
palette = 2=#99cc99
palette = 3=#ffcc66
palette = 4=#6699cc
palette = 5=#cc99cc
palette = 6=#66cccc
palette = 7=#ffffff
palette = 8=#595959
palette = 9=#f2777a
palette = 10=#99cc99
palette = 11=#ffcc66
palette = 12=#6699cc
palette = 13=#cc99cc
palette = 14=#66cccc
palette = 15=#ffffff
background = #2d2d2d
foreground = #cccccc
cursor-color = #cccccc
cursor-text = #2d2d2d
selection-background = #515151
selection-foreground = #cccccc
`,
  "Toy Chest": `palette = 0=#2c3f58
palette = 1=#be2d26
palette = 2=#1a9172
palette = 3=#db8e27
palette = 4=#325d96
palette = 5=#8a5edc
palette = 6=#35a08f
palette = 7=#23d183
palette = 8=#336889
palette = 9=#dd5944
palette = 10=#31d07b
palette = 11=#e7d84b
palette = 12=#34a6da
palette = 13=#ae6bdc
palette = 14=#42c3ae
palette = 15=#d5d5d5
background = #24364b
foreground = #31d07b
cursor-color = #d5d5d5
cursor-text = #141c25
selection-background = #5f217a
selection-foreground = #d5d5d5
`,
  traffic: `palette = 0=#282d31
palette = 1=#934e46
palette = 2=#637268
palette = 3=#c79e84
palette = 4=#515e67
palette = 5=#715f5e
palette = 6=#5c6f7d
palette = 7=#cfb9a8
palette = 8=#4e575e
palette = 9=#934e46
palette = 10=#637270
palette = 11=#c79e84
palette = 12=#515e67
palette = 13=#715f5e
palette = 14=#5c6f7d
palette = 15=#ddcec2
background = #272c30
foreground = #cfb9a8
cursor-color = #cfb9a8
cursor-text = #272c30
selection-background = #cfb9a8
selection-foreground = #272c30
`,
  Treehouse: `palette = 0=#321300
palette = 1=#b2270e
palette = 2=#44a900
palette = 3=#aa820c
palette = 4=#58859a
palette = 5=#97363d
palette = 6=#b25a1e
palette = 7=#786b53
palette = 8=#504333
palette = 9=#ed5d20
palette = 10=#55f238
palette = 11=#f2b732
palette = 12=#85cfed
palette = 13=#e14c5a
palette = 14=#f07d14
palette = 15=#ffc800
background = #191919
foreground = #786b53
cursor-color = #fac814
cursor-text = #191919
selection-background = #786b53
selection-foreground = #fac800
`,
  Twilight: `palette = 0=#141414
palette = 1=#c06d44
palette = 2=#afb97a
palette = 3=#c2a86c
palette = 4=#44474a
palette = 5=#b4be7c
palette = 6=#778385
palette = 7=#ffffd4
palette = 8=#404040
palette = 9=#de7c4c
palette = 10=#ccd88c
palette = 11=#e2c47e
palette = 12=#5a5e62
palette = 13=#d0dc8e
palette = 14=#8a989b
palette = 15=#ffffd4
background = #141414
foreground = #ffffd4
cursor-color = #ffffff
cursor-text = #000000
selection-background = #313131
selection-foreground = #ffffd4
`,
  Ubuntu: `palette = 0=#2e3436
palette = 1=#cc0000
palette = 2=#4e9a06
palette = 3=#c4a000
palette = 4=#3465a4
palette = 5=#75507b
palette = 6=#06989a
palette = 7=#d3d7cf
palette = 8=#555753
palette = 9=#ef2929
palette = 10=#8ae234
palette = 11=#fce94f
palette = 12=#729fcf
palette = 13=#ad7fa8
palette = 14=#34e2e2
palette = 15=#eeeeec
background = #300a24
foreground = #eeeeec
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  "Ultra Dark": `palette = 0=#000000
palette = 1=#f07178
palette = 2=#c3e88d
palette = 3=#ffcb6b
palette = 4=#82aaff
palette = 5=#c792ea
palette = 6=#89ddff
palette = 7=#cccccc
palette = 8=#404040
palette = 9=#f6a9ae
palette = 10=#dbf1ba
palette = 11=#ffdfa6
palette = 12=#b4ccff
palette = 13=#ddbdf2
palette = 14=#b8eaff
palette = 15=#ffffff
background = #000000
foreground = #ffffff
cursor-color = #fefefe
cursor-text = #000000
selection-background = #222222
selection-foreground = #cccccc
`,
  "Ultra Violent": `palette = 0=#242728
palette = 1=#ff0090
palette = 2=#b6ff00
palette = 3=#fff727
palette = 4=#47e0fb
palette = 5=#d731ff
palette = 6=#0effbb
palette = 7=#e1e1e1
palette = 8=#636667
palette = 9=#fb58b4
palette = 10=#deff8c
palette = 11=#ebe087
palette = 12=#7fecff
palette = 13=#e681ff
palette = 14=#69fcd3
palette = 15=#f9f9f5
background = #242728
foreground = #c1c1c1
cursor-color = #c1c1c1
cursor-text = #ffffff
selection-background = #574c49
selection-foreground = #c3c7cb
`,
  "Under The Sea": `palette = 0=#022026
palette = 1=#b2302d
palette = 2=#00a941
palette = 3=#59819c
palette = 4=#459a86
palette = 5=#00599d
palette = 6=#5d7e19
palette = 7=#405555
palette = 8=#384451
palette = 9=#ff4242
palette = 10=#2aea5e
palette = 11=#8ed4fd
palette = 12=#61d5ba
palette = 13=#1298ff
palette = 14=#98d028
palette = 15=#58fbd6
background = #011116
foreground = #ffffff
cursor-color = #4afcd6
cursor-text = #031413
selection-background = #415555
selection-foreground = #4dffda
`,
  Unikitty: `palette = 0=#0c0c0c
palette = 1=#a80f20
palette = 2=#c7ff98
palette = 3=#fff964
palette = 4=#145fcd
palette = 5=#ffe9ff
palette = 6=#9effef
palette = 7=#fcf1fb
palette = 8=#434343
palette = 9=#d91329
palette = 10=#d3ffaf
palette = 11=#ffef50
palette = 12=#0075ea
palette = 13=#ffefff
palette = 14=#93ffef
palette = 15=#fff3fe
background = #ff8cd9
foreground = #0b0b0b
cursor-color = #c7ff98
cursor-text = #202020
selection-background = #3ea9fe
selection-foreground = #ffffff
`,
  urban: `palette = 0=#33303b
palette = 1=#87404f
palette = 2=#74934e
palette = 3=#ae835a
palette = 4=#615772
palette = 5=#854b64
palette = 6=#625464
palette = 7=#c0a79a
palette = 8=#5c5865
palette = 9=#87404f
palette = 10=#74934e
palette = 11=#ae835a
palette = 12=#615772
palette = 13=#854b64
palette = 14=#625464
palette = 15=#c0a79a
background = #312e39
foreground = #c0a79a
cursor-color = #c0a79a
cursor-text = #312e39
selection-background = #c0a79a
selection-foreground = #312e39
`,
  Urple: `palette = 0=#000000
palette = 1=#b0425b
palette = 2=#37a415
palette = 3=#ad5c42
palette = 4=#564d9b
palette = 5=#6c3ca1
palette = 6=#808080
palette = 7=#87799c
palette = 8=#6a3f32
palette = 9=#ff6388
palette = 10=#29e620
palette = 11=#f08161
palette = 12=#867aed
palette = 13=#a05eee
palette = 14=#eaeaea
palette = 15=#bfa3ff
background = #1b1b23
foreground = #877a9b
cursor-color = #a063eb
cursor-text = #1b1b22
selection-background = #a063eb
selection-foreground = #1b1b22
`,
  Vague: `palette = 0=#252530
palette = 1=#d8647e
palette = 2=#7fa563
palette = 3=#f3be7c
palette = 4=#6e94b2
palette = 5=#bb9dbd
palette = 6=#aeaed1
palette = 7=#cdcdcd
palette = 8=#606079
palette = 9=#e08398
palette = 10=#99b782
palette = 11=#f5cb96
palette = 12=#8ba9c1
palette = 13=#c9b1ca
palette = 14=#bebeda
palette = 15=#d7d7d7
background = #141415
foreground = #cdcdcd
cursor-color = #cdcdcd
cursor-text = #141415
selection-background = #252530
selection-foreground = #cdcdcd
`,
  Vaughn: `palette = 0=#25234f
palette = 1=#705050
palette = 2=#60b48a
palette = 3=#dfaf8f
palette = 4=#5555ff
palette = 5=#f08cc3
palette = 6=#8cd0d3
palette = 7=#709080
palette = 8=#709080
palette = 9=#dca3a3
palette = 10=#60b48a
palette = 11=#f0dfaf
palette = 12=#5555ff
palette = 13=#ec93d3
palette = 14=#93e0e3
palette = 15=#ffffff
background = #25234f
foreground = #dcdccc
cursor-color = #ff5555
cursor-text = #ffffff
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Vercel: `palette = 0=#000000
palette = 1=#fc0036
palette = 2=#29a948
palette = 3=#ffae00
palette = 4=#006aff
palette = 5=#f32882
palette = 6=#00ac96
palette = 7=#feffff
palette = 8=#a8a8a8
palette = 9=#ff8080
palette = 10=#4be15d
palette = 11=#ffae00
palette = 12=#49aeff
palette = 13=#f97ea8
palette = 14=#00e4c4
palette = 15=#fefefe
background = #101010
foreground = #fafafa
cursor-color = #f32882
cursor-text = #fafafa
selection-background = #005be7
selection-foreground = #fafafa
`,
  Vesper: `palette = 0=#101010
palette = 1=#f5a191
palette = 2=#90b99f
palette = 3=#e6b99d
palette = 4=#aca1cf
palette = 5=#e29eca
palette = 6=#ea83a5
palette = 7=#a0a0a0
palette = 8=#7e7e7e
palette = 9=#ff8080
palette = 10=#99ffe4
palette = 11=#ffc799
palette = 12=#b9aeda
palette = 13=#ecaad6
palette = 14=#f591b2
palette = 15=#ffffff
background = #101010
foreground = #ffffff
cursor-color = #acb1ab
cursor-text = #ffffff
selection-background = #988049
selection-foreground = #b9beb8
`,
  "Vibrant Ink": `palette = 0=#878787
palette = 1=#ff6600
palette = 2=#ccff04
palette = 3=#ffcc00
palette = 4=#44b4cc
palette = 5=#9933cc
palette = 6=#44b4cc
palette = 7=#f5f5f5
palette = 8=#555555
palette = 9=#ff0000
palette = 10=#00ff00
palette = 11=#ffff00
palette = 12=#0000ff
palette = 13=#ff00ff
palette = 14=#00ffff
palette = 15=#e5e5e5
background = #000000
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #000000
selection-background = #b5d5ff
selection-foreground = #000000
`,
  Vimbones: `palette = 0=#f0f0ca
palette = 1=#a8334c
palette = 2=#4f6c31
palette = 3=#944927
palette = 4=#286486
palette = 5=#88507d
palette = 6=#3b8992
palette = 7=#353535
palette = 8=#acac89
palette = 9=#94253e
palette = 10=#3f5a22
palette = 11=#803d1c
palette = 12=#1d5573
palette = 13=#7b3b70
palette = 14=#2b747c
palette = 15=#5c5c5c
background = #f0f0ca
foreground = #353535
cursor-color = #353535
cursor-text = #f0f0ca
selection-background = #d7d7d7
selection-foreground = #353535
`,
  "Violet Dark": `palette = 0=#56595c
palette = 1=#c94c22
palette = 2=#85981c
palette = 3=#b4881d
palette = 4=#2e8bce
palette = 5=#d13a82
palette = 6=#32a198
palette = 7=#c9c6bd
palette = 8=#45484b
palette = 9=#bd3613
palette = 10=#738a04
palette = 11=#a57705
palette = 12=#2176c7
palette = 13=#c61c6f
palette = 14=#259286
palette = 15=#c9c6bd
background = #1c1d1f
foreground = #708284
cursor-color = #708284
cursor-text = #1c1d1f
selection-background = #595ab7
selection-foreground = #1c1d1f
`,
  "Violet Light": `palette = 0=#56595c
palette = 1=#c94c22
palette = 2=#85981c
palette = 3=#b4881d
palette = 4=#2e8bce
palette = 5=#d13a82
palette = 6=#32a198
palette = 7=#b9b6af
palette = 8=#45484b
palette = 9=#bd3613
palette = 10=#738a04
palette = 11=#a57705
palette = 12=#2176c7
palette = 13=#c61c6f
palette = 14=#259286
palette = 15=#c9c6bd
background = #fcf4dc
foreground = #536870
cursor-color = #536870
cursor-text = #fcf4dc
selection-background = #595ab7
selection-foreground = #fcf4dc
`,
  Violite: `palette = 0=#241c36
palette = 1=#ec7979
palette = 2=#79ecb3
palette = 3=#ece279
palette = 4=#a979ec
palette = 5=#ec79ec
palette = 6=#79ecec
palette = 7=#eef4f6
palette = 8=#56447a
palette = 9=#ef8f8f
palette = 10=#9fefbf
palette = 11=#efe78f
palette = 12=#b78fef
palette = 13=#ef8fcf
palette = 14=#9fefef
palette = 15=#f8fafc
background = #241c36
foreground = #eef4f6
cursor-color = #eef4f6
cursor-text = #241c36
selection-background = #49376d
selection-foreground = #eef4f6
`,
  "Warm Neon": `palette = 0=#000000
palette = 1=#e24346
palette = 2=#39b13a
palette = 3=#dae145
palette = 4=#4261c5
palette = 5=#f920fb
palette = 6=#2abbd4
palette = 7=#d0b8a3
palette = 8=#fefcfc
palette = 9=#e97071
palette = 10=#9cc090
palette = 11=#ddda7a
palette = 12=#7b91d6
palette = 13=#f674ba
palette = 14=#5ed1e5
palette = 15=#d8c8bb
background = #404040
foreground = #afdab6
cursor-color = #30ff24
cursor-text = #0bbc04
selection-background = #b0ad21
selection-foreground = #ffffff
`,
  Wez: `palette = 0=#000000
palette = 1=#cc5555
palette = 2=#55cc55
palette = 3=#cdcd55
palette = 4=#5555cc
palette = 5=#cc55cc
palette = 6=#7acaca
palette = 7=#cccccc
palette = 8=#555555
palette = 9=#ff5555
palette = 10=#55ff55
palette = 11=#ffff55
palette = 12=#5555ff
palette = 13=#ff55ff
palette = 14=#55ffff
palette = 15=#ffffff
background = #000000
foreground = #b3b3b3
cursor-color = #53ae71
cursor-text = #000000
selection-background = #4d52f8
selection-foreground = #000000
`,
  Whimsy: `palette = 0=#535178
palette = 1=#ef6487
palette = 2=#5eca89
palette = 3=#fdd877
palette = 4=#65aef7
palette = 5=#aa7ff0
palette = 6=#43c1be
palette = 7=#ffffff
palette = 8=#535178
palette = 9=#ef6487
palette = 10=#5eca89
palette = 11=#fdd877
palette = 12=#65aef7
palette = 13=#aa7ff0
palette = 14=#43c1be
palette = 15=#ffffff
background = #29283b
foreground = #b3b0d6
cursor-color = #b3b0d6
cursor-text = #535178
selection-background = #3d3c58
selection-foreground = #ffffff
`,
  "Wild Cherry": `palette = 0=#000507
palette = 1=#d94085
palette = 2=#2ab250
palette = 3=#ffd16f
palette = 4=#883cdc
palette = 5=#ececec
palette = 6=#c1b8b7
palette = 7=#fff8de
palette = 8=#009cc9
palette = 9=#da6bac
palette = 10=#f4dca5
palette = 11=#eac066
palette = 12=#308cba
palette = 13=#ae636b
palette = 14=#ff919d
palette = 15=#e4838d
background = #1f1726
foreground = #dafaff
cursor-color = #dd00ff
cursor-text = #ff7fff
selection-background = #002831
selection-foreground = #e4ffff
`,
  Wilmersdorf: `palette = 0=#34373e
palette = 1=#e06383
palette = 2=#7ebebd
palette = 3=#cccccc
palette = 4=#a6c1e0
palette = 5=#e1c1ee
palette = 6=#5b94ab
palette = 7=#ababab
palette = 8=#50545d
palette = 9=#fa7193
palette = 10=#8fd7d6
palette = 11=#d1dfff
palette = 12=#b2cff0
palette = 13=#efccfd
palette = 14=#69abc5
palette = 15=#d3d3d3
background = #282b33
foreground = #c6c6c6
cursor-color = #7ebebd
cursor-text = #1f2024
selection-background = #1f2024
selection-foreground = #c6c6c6
`,
  Wombat: `palette = 0=#000000
palette = 1=#ff615a
palette = 2=#b1e969
palette = 3=#ebd99c
palette = 4=#5da9f6
palette = 5=#e86aff
palette = 6=#82fff7
palette = 7=#dedacf
palette = 8=#4a4a4a
palette = 9=#f58c80
palette = 10=#ddf88f
palette = 11=#eee5b2
palette = 12=#a5c7ff
palette = 13=#ddaaff
palette = 14=#b7fff9
palette = 15=#ffffff
background = #171717
foreground = #dedacf
cursor-color = #bbbbbb
cursor-text = #ffffff
selection-background = #453b39
selection-foreground = #b6bbc0
`,
  Wryan: `palette = 0=#333333
palette = 1=#8c4665
palette = 2=#287373
palette = 3=#7c7c99
palette = 4=#395573
palette = 5=#5e468c
palette = 6=#31658c
palette = 7=#899ca1
palette = 8=#3d3d3d
palette = 9=#bf4d80
palette = 10=#53a6a6
palette = 11=#9e9ecb
palette = 12=#477ab3
palette = 13=#7e62b3
palette = 14=#6096bf
palette = 15=#c0c0c0
background = #101010
foreground = #999993
cursor-color = #9e9ecb
cursor-text = #000000
selection-background = #4d4d4d
selection-foreground = #ffffff
`,
  "Xcode Dark": `palette = 0=#414453
palette = 1=#ff8170
palette = 2=#78c2b3
palette = 3=#d9c97c
palette = 4=#4eb0cc
palette = 5=#ff7ab2
palette = 6=#b281eb
palette = 7=#dfdfe0
palette = 8=#7f8c98
palette = 9=#ff8170
palette = 10=#acf2e4
palette = 11=#ffa14f
palette = 12=#6bdfff
palette = 13=#ff7ab2
palette = 14=#dabaff
palette = 15=#dfdfe0
background = #292a30
foreground = #dfdfe0
cursor-color = #dfdfe0
cursor-text = #292a30
selection-background = #414453
selection-foreground = #dfdfe0
`,
  "Xcode Dark hc": `palette = 0=#43454b
palette = 1=#ff8a7a
palette = 2=#83c9bc
palette = 3=#d9c668
palette = 4=#4ec4e6
palette = 5=#ff85b8
palette = 6=#cda1ff
palette = 7=#ffffff
palette = 8=#838991
palette = 9=#ff8a7a
palette = 10=#b1faeb
palette = 11=#ffa14f
palette = 12=#6bdfff
palette = 13=#ff85b8
palette = 14=#e5cfff
palette = 15=#ffffff
background = #1f1f24
foreground = #ffffff
cursor-color = #ffffff
cursor-text = #1f1f24
selection-background = #43454b
selection-foreground = #ffffff
`,
  "Xcode Light": `palette = 0=#b4d8fd
palette = 1=#d12f1b
palette = 2=#3e8087
palette = 3=#78492a
palette = 4=#0f68a0
palette = 5=#ad3da4
palette = 6=#804fb8
palette = 7=#262626
palette = 8=#8a99a6
palette = 9=#d12f1b
palette = 10=#23575c
palette = 11=#78492a
palette = 12=#0b4f79
palette = 13=#ad3da4
palette = 14=#4b21b0
palette = 15=#262626
background = #ffffff
foreground = #262626
cursor-color = #262626
cursor-text = #ffffff
selection-background = #b4d8fd
selection-foreground = #262626
`,
  "Xcode Light hc": `palette = 0=#b4d8fd
palette = 1=#ad1805
palette = 2=#355d61
palette = 3=#78492a
palette = 4=#0058a1
palette = 5=#9c2191
palette = 6=#703daa
palette = 7=#000000
palette = 8=#8a99a6
palette = 9=#ad1805
palette = 10=#174145
palette = 11=#78492a
palette = 12=#003f73
palette = 13=#9c2191
palette = 14=#441ea1
palette = 15=#000000
background = #ffffff
foreground = #000000
cursor-color = #000000
cursor-text = #ffffff
selection-background = #b4d8fd
selection-foreground = #000000
`,
  "Xcode WWDC": `palette = 0=#494d5c
palette = 1=#bb383a
palette = 2=#94c66e
palette = 3=#d28e5d
palette = 4=#8884c5
palette = 5=#b73999
palette = 6=#00aba4
palette = 7=#e7e8eb
palette = 8=#7f869e
palette = 9=#bb383a
palette = 10=#94c66e
palette = 11=#d28e5d
palette = 12=#8884c5
palette = 13=#b73999
palette = 14=#00aba4
palette = 15=#e7e8eb
background = #292c36
foreground = #e7e8eb
cursor-color = #e7e8eb
cursor-text = #292c36
selection-background = #494d5c
selection-foreground = #e7e8eb
`,
  Zenbones: `palette = 0=#f0edec
palette = 1=#a8334c
palette = 2=#4f6c31
palette = 3=#944927
palette = 4=#286486
palette = 5=#88507d
palette = 6=#3b8992
palette = 7=#2c363c
palette = 8=#b5a7a0
palette = 9=#94253e
palette = 10=#3f5a22
palette = 11=#803d1c
palette = 12=#1d5573
palette = 13=#7b3b70
palette = 14=#2b747c
palette = 15=#4f5e68
background = #f0edec
foreground = #2c363c
cursor-color = #2c363c
cursor-text = #f0edec
selection-background = #cbd9e3
selection-foreground = #2c363c
`,
  "Zenbones Dark": `palette = 0=#1c1917
palette = 1=#de6e7c
palette = 2=#819b69
palette = 3=#b77e64
palette = 4=#6099c0
palette = 5=#b279a7
palette = 6=#66a5ad
palette = 7=#b4bdc3
palette = 8=#4d4540
palette = 9=#e8838f
palette = 10=#8bae68
palette = 11=#d68c67
palette = 12=#61abda
palette = 13=#cf86c1
palette = 14=#65b8c1
palette = 15=#888f94
background = #1c1917
foreground = #b4bdc3
cursor-color = #c4cacf
cursor-text = #1c1917
selection-background = #3d4042
selection-foreground = #b4bdc3
`,
  "Zenbones Light": `palette = 0=#f0edec
palette = 1=#a8334c
palette = 2=#4f6c31
palette = 3=#944927
palette = 4=#286486
palette = 5=#88507d
palette = 6=#3b8992
palette = 7=#2c363c
palette = 8=#b5a7a0
palette = 9=#94253e
palette = 10=#3f5a22
palette = 11=#803d1c
palette = 12=#1d5573
palette = 13=#7b3b70
palette = 14=#2b747c
palette = 15=#4f5e68
background = #f0edec
foreground = #2c363c
cursor-color = #2c363c
cursor-text = #f0edec
selection-background = #cbd9e3
selection-foreground = #2c363c
`,
  Zenburn: `palette = 0=#4d4d4d
palette = 1=#7d5d5d
palette = 2=#60b48a
palette = 3=#f0dfaf
palette = 4=#5d6d7d
palette = 5=#dc8cc3
palette = 6=#8cd0d3
palette = 7=#dcdccc
palette = 8=#709080
palette = 9=#dca3a3
palette = 10=#c3bf9f
palette = 11=#e0cf9f
palette = 12=#94bff3
palette = 13=#ec93d3
palette = 14=#93e0e3
palette = 15=#ffffff
background = #3f3f3f
foreground = #dcdccc
cursor-color = #73635a
cursor-text = #000000
selection-background = #21322f
selection-foreground = #c2d87a
`,
  Zenburned: `palette = 0=#404040
palette = 1=#e3716e
palette = 2=#819b69
palette = 3=#b77e64
palette = 4=#6099c0
palette = 5=#b279a7
palette = 6=#66a5ad
palette = 7=#f0e4cf
palette = 8=#6f6768
palette = 9=#ec8685
palette = 10=#8bae68
palette = 11=#d68c67
palette = 12=#61abda
palette = 13=#cf86c1
palette = 14=#65b8c1
palette = 15=#c0ab86
background = #404040
foreground = #f0e4cf
cursor-color = #f3eadb
cursor-text = #404040
selection-background = #746956
selection-foreground = #f0e4cf
`,
  "Zenwritten Dark": `palette = 0=#191919
palette = 1=#de6e7c
palette = 2=#819b69
palette = 3=#b77e64
palette = 4=#6099c0
palette = 5=#b279a7
palette = 6=#66a5ad
palette = 7=#bbbbbb
palette = 8=#4a4546
palette = 9=#e8838f
palette = 10=#8bae68
palette = 11=#d68c67
palette = 12=#61abda
palette = 13=#cf86c1
palette = 14=#65b8c1
palette = 15=#8e8e8e
background = #191919
foreground = #bbbbbb
cursor-color = #c9c9c9
cursor-text = #191919
selection-background = #404040
selection-foreground = #bbbbbb
`,
  "Zenwritten Light": `palette = 0=#eeeeee
palette = 1=#a8334c
palette = 2=#4f6c31
palette = 3=#944927
palette = 4=#286486
palette = 5=#88507d
palette = 6=#3b8992
palette = 7=#353535
palette = 8=#aca9a9
palette = 9=#94253e
palette = 10=#3f5a22
palette = 11=#803d1c
palette = 12=#1d5573
palette = 13=#7b3b70
palette = 14=#2b747c
palette = 15=#5c5c5c
background = #eeeeee
foreground = #353535
cursor-color = #353535
cursor-text = #eeeeee
selection-background = #d7d7d7
selection-foreground = #353535
`
};

// src/theme/catalog.ts
var parsedThemeCache = new Map;
function listBuiltinThemeNames() {
  return [...BUILTIN_THEME_NAMES];
}
function isBuiltinThemeName(name) {
  return Object.prototype.hasOwnProperty.call(BUILTIN_THEME_SOURCES, name);
}
function getBuiltinThemeSource(name) {
  if (!isBuiltinThemeName(name))
    return null;
  return BUILTIN_THEME_SOURCES[name];
}
function getBuiltinTheme(name) {
  if (!isBuiltinThemeName(name))
    return null;
  const cached = parsedThemeCache.get(name);
  if (cached)
    return cached;
  const parsed = parseGhosttyTheme(BUILTIN_THEME_SOURCES[name]);
  if (!parsed.name)
    parsed.name = name;
  parsedThemeCache.set(name, parsed);
  return parsed;
}
// src/surface/restty-plugin-types.ts
var RESTTY_PLUGIN_API_VERSION = 1;

// src/surface/restty-pane-handle.ts
class ResttyPaneHandle {
  resolvePane;
  searchUiOps;
  constructor(resolvePane, searchUiOps) {
    this.resolvePane = resolvePane;
    this.searchUiOps = searchUiOps;
  }
  get id() {
    return this.resolvePane().id;
  }
  setRenderer(value) {
    this.resolvePane().app.setRenderer(value);
  }
  setPaused(value) {
    this.resolvePane().app.setPaused(value);
  }
  togglePause() {
    this.resolvePane().app.togglePause();
  }
  setFontSize(value) {
    this.resolvePane().app.setFontSize(value);
  }
  applyTheme(theme, sourceLabel) {
    this.resolvePane().app.applyTheme(theme, sourceLabel);
  }
  resetTheme() {
    this.resolvePane().app.resetTheme();
  }
  sendInput(text2, source) {
    this.resolvePane().app.sendInput(text2, source);
  }
  sendKeyInput(text2, source) {
    this.resolvePane().app.sendKeyInput(text2, source);
  }
  clearScreen() {
    this.resolvePane().app.clearScreen();
  }
  loadBinarySnapshot(data) {
    return this.resolvePane().app.loadBinarySnapshot(data);
  }
  getColorForeground() {
    return this.resolvePane().app.getColorForeground();
  }
  getColorBackground() {
    return this.resolvePane().app.getColorBackground();
  }
  getColorCursor() {
    return this.resolvePane().app.getColorCursor();
  }
  getPaletteColor(index) {
    return this.resolvePane().app.getPaletteColor(index);
  }
  getPalette() {
    return this.resolvePane().app.getPalette();
  }
  connectPty(url = "") {
    this.resolvePane().app.connectPty(url);
  }
  disconnectPty() {
    this.resolvePane().app.disconnectPty();
  }
  isPtyConnected() {
    return this.resolvePane().app.isPtyConnected();
  }
  setMouseMode(value) {
    this.resolvePane().app.setMouseMode(value);
  }
  getMouseStatus() {
    return this.resolvePane().app.getMouseStatus();
  }
  copySelectionToClipboard() {
    return this.resolvePane().app.copySelectionToClipboard();
  }
  pasteFromClipboard() {
    return this.resolvePane().app.pasteFromClipboard();
  }
  setSearchQuery(query) {
    this.resolvePane().app.setSearchQuery(query);
  }
  clearSearch() {
    this.resolvePane().app.clearSearch();
  }
  searchNext() {
    this.resolvePane().app.searchNext();
  }
  searchPrevious() {
    this.resolvePane().app.searchPrevious();
  }
  getSearchState() {
    return this.resolvePane().app.getSearchState();
  }
  openSearch(options) {
    this.searchUiOps.open(this.id, options);
  }
  closeSearch(options) {
    this.searchUiOps.close(this.id, options);
  }
  toggleSearch(options) {
    this.searchUiOps.toggle(this.id, options);
  }
  isSearchOpen() {
    return this.searchUiOps.isOpen(this.id);
  }
  dumpAtlasForCodepoint(cp) {
    this.resolvePane().app.dumpAtlasForCodepoint(cp);
  }
  resize(cols, rows) {
    this.resolvePane().app.resize(cols, rows);
  }
  focus() {
    this.resolvePane().app.focus();
  }
  blur() {
    this.resolvePane().app.blur();
  }
  updateSize(force) {
    this.resolvePane().app.updateSize(force);
  }
  getBackend() {
    return this.resolvePane().app.getBackend();
  }
  getSearchUiStyleOptions() {
    return this.searchUiOps.getStyleOptions();
  }
  setSearchUiStyleOptions(options) {
    this.searchUiOps.setStyleOptions(options);
  }
  setShaderStages(stages) {
    this.resolvePane().app.setShaderStages(stages);
  }
  getShaderStages() {
    return this.resolvePane().app.getShaderStages();
  }
  getRawPane() {
    return this.resolvePane();
  }
}

// src/surface/panes/default-context-menu-items.ts
function getResttyShortcutModifierLabel() {
  const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
  return isMac ? "Cmd" : "Ctrl";
}
function createDefaultResttyPaneContextMenuItems(options) {
  const { pane, manager, getPtyUrl } = options;
  const mod = options.modKeyLabel ?? getResttyShortcutModifierLabel();
  const closeEnabled = manager.getPanes().length > 1;
  const pauseLabel = typeof pane.paused === "boolean" ? pane.paused ? "Resume Renderer" : "Pause Renderer" : "Toggle Renderer Pause";
  return [
    {
      label: "Copy",
      shortcut: `${mod}+C`,
      action: async () => {
        await pane.app.copySelectionToClipboard();
      }
    },
    {
      label: "Paste",
      shortcut: `${mod}+V`,
      action: async () => {
        await pane.app.pasteFromClipboard();
      }
    },
    "separator",
    {
      label: "Split Right",
      shortcut: `${mod}+D`,
      action: () => {
        manager.splitPane(pane.id, "vertical");
      }
    },
    {
      label: "Split Down",
      shortcut: `${mod}+Shift+D`,
      action: () => {
        manager.splitPane(pane.id, "horizontal");
      }
    },
    {
      label: "Close Pane",
      enabled: closeEnabled,
      danger: true,
      action: () => {
        manager.closePane(pane.id);
      }
    },
    "separator",
    {
      label: "Clear Screen",
      action: () => {
        pane.app.clearScreen();
      }
    },
    {
      label: pane.app.isPtyConnected() ? "Disconnect PTY" : "Connect PTY",
      action: () => {
        if (pane.app.isPtyConnected()) {
          pane.app.disconnectPty();
          return;
        }
        const url = (getPtyUrl?.() ?? "").trim();
        pane.app.connectPty(url);
      }
    },
    {
      label: pauseLabel,
      action: () => {
        if (typeof pane.setPaused === "function") {
          pane.setPaused(!(pane.paused ?? false));
          return;
        }
        pane.app.togglePause();
      }
    }
  ];
}

// src/surface/panes-context-menu.ts
function createPaneContextMenuController(options) {
  const contextMenuEl = options.doc.createElement("div");
  contextMenuEl.className = "pane-context-menu";
  contextMenuEl.hidden = true;
  options.doc.body.appendChild(contextMenuEl);
  const hide = () => {
    contextMenuEl.hidden = true;
    contextMenuEl.innerHTML = "";
  };
  const addSeparator = () => {
    const separator = options.doc.createElement("div");
    separator.className = "pane-context-menu-separator";
    contextMenuEl.appendChild(separator);
  };
  const render = (items) => {
    contextMenuEl.innerHTML = "";
    for (const item of items) {
      if (item === "separator") {
        addSeparator();
        continue;
      }
      const button = options.doc.createElement("button");
      button.type = "button";
      button.className = "pane-context-menu-item";
      if (item.danger)
        button.classList.add("is-danger");
      if (item.enabled === false)
        button.disabled = true;
      const label = options.doc.createElement("span");
      label.className = "pane-context-menu-label";
      label.textContent = item.label;
      button.appendChild(label);
      if (item.shortcut) {
        const shortcut = options.doc.createElement("span");
        shortcut.className = "pane-context-menu-shortcut";
        shortcut.textContent = item.shortcut;
        button.appendChild(shortcut);
      }
      button.addEventListener("click", () => {
        hide();
        item.action();
      });
      contextMenuEl.appendChild(button);
    }
  };
  const show = (pane, clientX, clientY, manager) => {
    const items = options.contextMenu.getItems(pane, manager);
    render(items);
    contextMenuEl.hidden = false;
    const margin = 8;
    const rect2 = contextMenuEl.getBoundingClientRect();
    const maxX = Math.max(margin, options.win.innerWidth - rect2.width - margin);
    const maxY = Math.max(margin, options.win.innerHeight - rect2.height - margin);
    const left = Math.min(Math.max(clientX, margin), maxX);
    const top = Math.min(Math.max(clientY, margin), maxY);
    contextMenuEl.style.left = `${left}px`;
    contextMenuEl.style.top = `${top}px`;
  };
  const destroy = () => {
    hide();
    contextMenuEl.remove();
  };
  return {
    element: contextMenuEl,
    isOpen: () => !contextMenuEl.hidden,
    containsTarget: (target) => target instanceof Node && contextMenuEl.contains(target),
    show,
    hide,
    destroy
  };
}

// src/surface/panes-styles.ts
var RESTTY_PANE_ROOT_CLASS = "restty-pane-root";
var RESTTY_PANE_STYLE_MARKER = "data-restty-pane-styles";
var RESTTY_PANE_STYLE_TEXT = `
.${RESTTY_PANE_ROOT_CLASS} {
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-split {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  gap: 0;
  padding: 0;
  background: var(--restty-pane-split-background, #000);
}

.${RESTTY_PANE_ROOT_CLASS} .pane-split.is-vertical {
  flex-direction: row;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-split.is-horizontal {
  flex-direction: column;
}

.${RESTTY_PANE_ROOT_CLASS} .pane {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  background: var(--restty-pane-background, #000);
  border: 0;
  overflow: hidden;
  opacity: var(--restty-pane-inactive-opacity, 0.9);
  transition: opacity var(--restty-pane-opacity-transition, 140ms) ease-out;
}

.${RESTTY_PANE_ROOT_CLASS} .pane.is-active {
  opacity: var(--restty-pane-active-opacity, 1);
}

.${RESTTY_PANE_ROOT_CLASS} .pane-divider {
  position: relative;
  z-index: 2;
  flex: 0 0 var(--restty-pane-divider-thickness, 1px);
  touch-action: none;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-vertical {
  cursor: col-resize;
  background: transparent;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-horizontal {
  cursor: row-resize;
  background: transparent;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-vertical:hover,
.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-vertical.is-dragging {
  background:
    radial-gradient(
      100px 46% at 50% 50%,
      rgba(235, 235, 235, 0.92) 0%,
      rgba(200, 200, 200, 0.48) 46%,
      rgba(155, 155, 155, 0.12) 68%,
      rgba(120, 120, 120, 0) 100%
    ),
    rgba(185, 185, 185, 0.24);
}

.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-horizontal:hover,
.${RESTTY_PANE_ROOT_CLASS} .pane-divider.is-horizontal.is-dragging {
  background:
    radial-gradient(
      46% 100px at 50% 50%,
      rgba(235, 235, 235, 0.92) 0%,
      rgba(200, 200, 200, 0.48) 46%,
      rgba(155, 155, 155, 0.12) 68%,
      rgba(120, 120, 120, 0) 100%
    ),
    rgba(185, 185, 185, 0.24);
}

body.is-resizing-split {
  user-select: none;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-canvas {
  width: 100%;
  height: 100%;
  display: block;
  outline: none;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-ime-input {
  position: fixed;
  left: 0;
  top: 0;
  width: 1em;
  height: 1em;
  padding: 0;
  margin: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  overflow: hidden;
  resize: none;
  opacity: 0;
  pointer-events: none;
}

.${RESTTY_PANE_ROOT_CLASS} .pane-term-debug {
  display: none;
}

.pane-context-menu {
  position: fixed;
  z-index: 9999;
  min-width: 200px;
  padding: 6px;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  background: #161616;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.45);
}

.pane-context-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 9px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #d6d6d6;
  text-align: left;
  cursor: pointer;
}

.pane-context-menu-item:hover {
  background: #252525;
}

.pane-context-menu-item:disabled {
  opacity: 0.4;
  cursor: default;
}

.pane-context-menu-item.is-danger {
  color: #f1a1a1;
}

.pane-context-menu-label {
  font-size: 12px;
}

.pane-context-menu-shortcut {
  font-size: 10px;
  color: #868686;
}

.pane-context-menu-separator {
  height: 1px;
  margin: 6px 4px;
  background: #2a2a2a;
}
`;
var DEFAULT_RESTTY_PANE_STYLE_OPTIONS = {
  splitBackground: "#000",
  paneBackground: "#000",
  inactivePaneOpacity: 0.9,
  activePaneOpacity: 1,
  opacityTransitionMs: 140,
  dividerThicknessPx: 1
};
function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function normalizeColor(value, fallback) {
  if (typeof value !== "string")
    return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}
function normalizePaneStyleOptions(options) {
  const inactivePaneOpacity = Number.isFinite(options.inactivePaneOpacity) ? clampNumber(Number(options.inactivePaneOpacity), 0, 1) : DEFAULT_RESTTY_PANE_STYLE_OPTIONS.inactivePaneOpacity;
  const activePaneOpacity = Number.isFinite(options.activePaneOpacity) ? clampNumber(Number(options.activePaneOpacity), 0, 1) : DEFAULT_RESTTY_PANE_STYLE_OPTIONS.activePaneOpacity;
  const opacityTransitionMs = Number.isFinite(options.opacityTransitionMs) ? clampNumber(Number(options.opacityTransitionMs), 0, 5000) : DEFAULT_RESTTY_PANE_STYLE_OPTIONS.opacityTransitionMs;
  const dividerThicknessPx = Number.isFinite(options.dividerThicknessPx) ? clampNumber(Number(options.dividerThicknessPx), 1, 32) : DEFAULT_RESTTY_PANE_STYLE_OPTIONS.dividerThicknessPx;
  return {
    splitBackground: normalizeColor(options.splitBackground, DEFAULT_RESTTY_PANE_STYLE_OPTIONS.splitBackground),
    paneBackground: normalizeColor(options.paneBackground, DEFAULT_RESTTY_PANE_STYLE_OPTIONS.paneBackground),
    inactivePaneOpacity,
    activePaneOpacity,
    opacityTransitionMs,
    dividerThicknessPx
  };
}
function ensureResttyPaneStylesDocument(doc) {
  if (doc.querySelector(`style[${RESTTY_PANE_STYLE_MARKER}="1"]`))
    return;
  const style = doc.createElement("style");
  style.setAttribute(RESTTY_PANE_STYLE_MARKER, "1");
  style.textContent = RESTTY_PANE_STYLE_TEXT;
  doc.head.appendChild(style);
}
function applyPaneStyleOptionsToRoot(root, options) {
  root.classList.add(RESTTY_PANE_ROOT_CLASS);
  root.style.setProperty("--restty-pane-split-background", options.splitBackground);
  root.style.setProperty("--restty-pane-background", options.paneBackground);
  root.style.setProperty("--restty-pane-inactive-opacity", options.inactivePaneOpacity.toFixed(3));
  root.style.setProperty("--restty-pane-active-opacity", options.activePaneOpacity.toFixed(3));
  root.style.setProperty("--restty-pane-opacity-transition", `${options.opacityTransitionMs}ms`);
  root.style.setProperty("--restty-pane-divider-thickness", `${options.dividerThicknessPx}px`);
}
function clearPaneStyleOptionsFromRoot(root) {
  root.classList.remove(RESTTY_PANE_ROOT_CLASS);
  root.style.removeProperty("--restty-pane-split-background");
  root.style.removeProperty("--restty-pane-background");
  root.style.removeProperty("--restty-pane-inactive-opacity");
  root.style.removeProperty("--restty-pane-active-opacity");
  root.style.removeProperty("--restty-pane-opacity-transition");
  root.style.removeProperty("--restty-pane-divider-thickness");
}

// src/surface/panes/layout.ts
var getSplitBranches = (split) => {
  const branches = [];
  for (const child of Array.from(split.children)) {
    if (!(child instanceof HTMLElement))
      continue;
    if (child.classList.contains("pane-divider"))
      continue;
    branches.push(child);
  }
  return branches;
};
var getRectEdgeDistanceSquared = (sourceRect, targetRect) => {
  const dx = Math.max(targetRect.left - sourceRect.right, sourceRect.left - targetRect.right, 0);
  const dy = Math.max(targetRect.top - sourceRect.bottom, sourceRect.top - targetRect.bottom, 0);
  return dx ** 2 + dy ** 2;
};
var getRectCenterDistanceSquared = (sourceRect, targetRect) => {
  const sourceCenterX = sourceRect.left + sourceRect.width * 0.5;
  const sourceCenterY = sourceRect.top + sourceRect.height * 0.5;
  const targetCenterX = targetRect.left + targetRect.width * 0.5;
  const targetCenterY = targetRect.top + targetRect.height * 0.5;
  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;
  return dx ** 2 + dy ** 2;
};
function findClosestPaneToRect(sourceRect, panes) {
  if (!sourceRect)
    return null;
  let closestPane = null;
  let closestEdgeDistance = Number.POSITIVE_INFINITY;
  let closestCenterDistance = Number.POSITIVE_INFINITY;
  for (const candidate of panes) {
    const targetRect = candidate.container.getBoundingClientRect();
    const edgeDistance = getRectEdgeDistanceSquared(sourceRect, targetRect);
    const centerDistance = getRectCenterDistanceSquared(sourceRect, targetRect);
    if (edgeDistance < closestEdgeDistance || edgeDistance === closestEdgeDistance && centerDistance < closestCenterDistance) {
      closestPane = candidate;
      closestEdgeDistance = edgeDistance;
      closestCenterDistance = centerDistance;
    }
  }
  return closestPane;
}
function collapseSplitAncestors(start) {
  let current = start;
  while (current && current.classList.contains("pane-split")) {
    const branches = getSplitBranches(current);
    if (branches.length > 1)
      return;
    const onlyChild = branches[0];
    const parent = current.parentElement;
    if (!parent || !onlyChild)
      return;
    const inheritedFlex = current.style.flex;
    if (inheritedFlex) {
      onlyChild.style.flex = inheritedFlex;
    } else {
      onlyChild.style.flex = "";
    }
    parent.replaceChild(onlyChild, current);
    current = parent;
  }
}
function createSplitDividerFactory(options) {
  let splitResizeState = null;
  const createSplitDivider = (direction) => {
    const divider = document.createElement("div");
    divider.className = `pane-divider ${direction === "vertical" ? "is-vertical" : "is-horizontal"}`;
    divider.setAttribute("role", "separator");
    divider.setAttribute("aria-orientation", direction === "vertical" ? "vertical" : "horizontal");
    const onPointerMove = (event) => {
      const state2 = splitResizeState;
      if (!state2 || event.pointerId !== state2.pointerId)
        return;
      event.preventDefault();
      const coord = state2.axis === "x" ? event.clientX : event.clientY;
      const delta = coord - state2.startCoord;
      const maxFirst = Math.max(options.minPaneSize, state2.total - options.minPaneSize);
      const nextFirst = Math.min(maxFirst, Math.max(options.minPaneSize, state2.startFirst + delta));
      const nextSecond = Math.max(options.minPaneSize, state2.total - nextFirst);
      const firstPercent = nextFirst / (nextFirst + nextSecond) * 100;
      const secondPercent = 100 - firstPercent;
      state2.first.style.flex = `0 0 ${firstPercent.toFixed(5)}%`;
      state2.second.style.flex = `0 0 ${secondPercent.toFixed(5)}%`;
      options.requestLayoutSync();
    };
    const endResize = () => {
      if (!splitResizeState)
        return;
      splitResizeState.divider.classList.remove("is-dragging");
      document.body.classList.remove("is-resizing-split");
      splitResizeState = null;
    };
    const onPointerEnd = (event) => {
      if (!splitResizeState || event.pointerId !== splitResizeState.pointerId)
        return;
      try {
        divider.releasePointerCapture(splitResizeState.pointerId);
      } catch {
      }
      divider.removeEventListener("pointermove", onPointerMove);
      divider.removeEventListener("pointerup", onPointerEnd);
      divider.removeEventListener("pointercancel", onPointerEnd);
      endResize();
    };
    divider.addEventListener("pointerdown", (event) => {
      if (event.button !== 0)
        return;
      const first = divider.previousElementSibling;
      const second = divider.nextElementSibling;
      const split = divider.parentElement;
      if (!first || !second || !split)
        return;
      const splitRect = split.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      const axis = direction === "vertical" ? "x" : "y";
      const total = axis === "x" ? splitRect.width : splitRect.height;
      if (total <= 0)
        return;
      endResize();
      event.preventDefault();
      event.stopPropagation();
      splitResizeState = {
        pointerId: event.pointerId,
        axis,
        divider,
        first,
        second,
        startCoord: axis === "x" ? event.clientX : event.clientY,
        startFirst: axis === "x" ? firstRect.width : firstRect.height,
        total
      };
      divider.classList.add("is-dragging");
      document.body.classList.add("is-resizing-split");
      divider.setPointerCapture(event.pointerId);
      divider.addEventListener("pointermove", onPointerMove);
      divider.addEventListener("pointerup", onPointerEnd);
      divider.addEventListener("pointercancel", onPointerEnd);
    });
    return divider;
  };
  return { createSplitDivider };
}

// src/surface/panes/pane-interactions.ts
function createPaneInteractions(options) {
  const paneCleanupFns = new Map;
  const bindPaneInteractions = (pane) => {
    const cleanupFns = [];
    const { id, container } = pane;
    const onPointerDown = () => {
      options.markPaneFocused(id);
    };
    container.addEventListener("pointerdown", onPointerDown);
    cleanupFns.push(() => {
      container.removeEventListener("pointerdown", onPointerDown);
    });
    const focusTarget = pane.focusTarget;
    if (focusTarget) {
      const onFocus = () => {
        options.markPaneFocused(id);
      };
      focusTarget.addEventListener("focus", onFocus);
      cleanupFns.push(() => {
        focusTarget.removeEventListener("focus", onFocus);
      });
    }
    if (options.contextMenu) {
      const onContextMenu = (event) => {
        if (event.defaultPrevented)
          return;
        if (options.contextMenu?.canOpen && !options.contextMenu.canOpen(event, pane)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        options.markPaneFocused(id);
        options.contextMenuController?.show(pane, event.clientX, event.clientY, options.getManager());
      };
      container.addEventListener("contextmenu", onContextMenu);
      cleanupFns.push(() => {
        container.removeEventListener("contextmenu", onContextMenu);
      });
    }
    paneCleanupFns.set(id, cleanupFns);
  };
  const cleanupPaneInteractions = (id) => {
    const cleanupFns = paneCleanupFns.get(id) ?? [];
    paneCleanupFns.delete(id);
    for (const cleanup of cleanupFns) {
      cleanup();
    }
  };
  return {
    bindPaneInteractions,
    cleanupPaneInteractions
  };
}

// src/surface/panes/window-events.ts
function attachPaneManagerWindowEvents(options) {
  const onWindowPointerDown = (event) => {
    if (!options.contextMenuController?.isOpen())
      return;
    if (options.contextMenuController.containsTarget(event.target))
      return;
    options.hideContextMenu();
  };
  const onWindowBlur = () => {
    options.hideContextMenu();
  };
  const onWindowKeyDown = (event) => {
    if (options.contextMenuController?.isOpen() && event.key === "Escape") {
      options.hideContextMenu();
      return;
    }
    if (options.shortcutOptions.enabled === false)
      return;
    if (options.shortcutOptions.canHandleEvent && !options.shortcutOptions.canHandleEvent(event)) {
      return;
    }
    const target = event.target;
    if (target && ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target.tagName)) {
      const allowed = options.shortcutOptions.isAllowedInputTarget?.(target) ?? false;
      if (!allowed)
        return;
    }
    const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
    const hasCommandModifier = isMac ? event.metaKey : event.ctrlKey;
    if (!hasCommandModifier || event.altKey || event.code !== "KeyD" || event.repeat) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    options.splitActivePane(event.shiftKey ? "horizontal" : "vertical");
  };
  window.addEventListener("pointerdown", onWindowPointerDown);
  window.addEventListener("blur", onWindowBlur);
  window.addEventListener("keydown", onWindowKeyDown, { capture: true });
  return () => {
    window.removeEventListener("pointerdown", onWindowPointerDown);
    window.removeEventListener("blur", onWindowBlur);
    window.removeEventListener("keydown", onWindowKeyDown, { capture: true });
  };
}

// src/surface/panes/manager.ts
function createResttyPaneManager(options) {
  const { root, createPane } = options;
  const isElementLike = typeof HTMLElement !== "undefined" ? root instanceof HTMLElement : !!root && typeof root.ownerDocument !== "undefined";
  if (!isElementLike) {
    throw new Error("createResttyPaneManager requires a root HTMLElement");
  }
  const panes = new Map;
  const minPaneSize = Number.isFinite(options.minPaneSize) ? Math.max(24, Number(options.minPaneSize)) : 96;
  const shortcutOptions = typeof options.shortcuts === "object" ? options.shortcuts : { enabled: options.shortcuts !== false };
  const stylesInput = typeof options.styles === "object" && options.styles ? options.styles : undefined;
  const stylesEnabled = options.styles === false ? false : stylesInput?.enabled ?? true;
  let styleOptions = normalizePaneStyleOptions({
    ...DEFAULT_RESTTY_PANE_STYLE_OPTIONS,
    ...stylesInput
  });
  if (stylesEnabled) {
    const doc = root.ownerDocument ?? document;
    ensureResttyPaneStylesDocument(doc);
    applyPaneStyleOptionsToRoot(root, styleOptions);
  }
  let nextPaneId = 1;
  let activePaneId = null;
  let focusedPaneId = null;
  let resizeRaf = 0;
  const ownerDoc = root.ownerDocument ?? document;
  const ownerWin = ownerDoc.defaultView ?? window;
  const contextMenuController = options.contextMenu ? createPaneContextMenuController({
    contextMenu: options.contextMenu,
    doc: ownerDoc,
    win: ownerWin
  }) : null;
  const requestLayoutSync = () => {
    if (resizeRaf)
      return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      options.onLayoutChanged?.();
    });
  };
  const { createSplitDivider } = createSplitDividerFactory({ minPaneSize, requestLayoutSync });
  const getStyleOptions = () => ({
    ...styleOptions
  });
  const setStyleOptions = (next) => {
    styleOptions = normalizePaneStyleOptions({
      ...styleOptions,
      ...next
    });
    if (!stylesEnabled)
      return;
    applyPaneStyleOptionsToRoot(root, styleOptions);
  };
  const getPanes = () => Array.from(panes.values());
  const getPaneById = (id) => panes.get(id) ?? null;
  const findPaneByElement = (element) => {
    if (!(element instanceof HTMLElement))
      return null;
    const host = element.closest(".pane");
    if (!host)
      return null;
    const id = Number(host.dataset.paneId ?? "");
    if (!Number.isFinite(id))
      return null;
    return panes.get(id) ?? null;
  };
  const getActivePane = () => activePaneId === null ? null : panes.get(activePaneId) ?? null;
  const getFocusedPane = () => {
    if (focusedPaneId !== null) {
      const focused = panes.get(focusedPaneId);
      if (focused)
        return focused;
    }
    if (typeof document === "undefined")
      return null;
    return findPaneByElement(document.activeElement);
  };
  const setActivePane = (id, config) => {
    const pane = panes.get(id);
    if (!pane)
      return;
    activePaneId = id;
    for (const current of panes.values()) {
      current.container.classList.toggle("is-active", current.id === id);
    }
    options.onActivePaneChange?.(pane);
    if (config?.focus) {
      const target = pane.focusTarget ?? pane.container;
      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
      }
    }
  };
  const markPaneFocused = (id, config) => {
    focusedPaneId = id;
    setActivePane(id, config);
  };
  const hideContextMenu = () => contextMenuController?.hide();
  let api;
  const paneInteractions = createPaneInteractions({
    contextMenu: options.contextMenu,
    contextMenuController,
    getManager: () => api,
    markPaneFocused
  });
  const createPaneInternal = (sourcePane) => {
    const id = nextPaneId;
    nextPaneId += 1;
    const pane = createPane({ id, sourcePane, manager: api });
    if (pane.id !== id) {
      throw new Error(`createResttyPaneManager expected pane.id=${id}, received ${pane.id}`);
    }
    if (!(pane.container instanceof HTMLDivElement)) {
      throw new Error("createResttyPaneManager createPane() must return { container: HTMLDivElement }");
    }
    pane.container.classList.add("pane");
    pane.container.dataset.paneId = `${id}`;
    panes.set(id, pane);
    paneInteractions.bindPaneInteractions(pane);
    options.onPaneCreated?.(pane);
    return pane;
  };
  const splitPane = (id, direction) => {
    const target = panes.get(id);
    if (!target)
      return null;
    const parent = target.container.parentElement;
    if (!parent)
      return null;
    const split = document.createElement("div");
    split.className = `pane-split ${direction === "vertical" ? "is-vertical" : "is-horizontal"}`;
    const inheritedFlex = target.container.style.flex;
    if (inheritedFlex) {
      split.style.flex = inheritedFlex;
    }
    parent.replaceChild(split, target.container);
    target.container.style.flex = "0 0 50%";
    split.appendChild(target.container);
    split.appendChild(createSplitDivider(direction));
    const created = createPaneInternal(target);
    created.container.style.flex = "0 0 50%";
    split.appendChild(created.container);
    markPaneFocused(created.id, { focus: true });
    requestLayoutSync();
    options.onPaneSplit?.(target, created, direction);
    return created;
  };
  const splitActivePane = (direction) => {
    const target = getFocusedPane() ?? getActivePane();
    if (!target)
      return null;
    return splitPane(target.id, direction);
  };
  const closePane = (id) => {
    if (panes.size <= 1)
      return false;
    const pane = panes.get(id);
    if (!pane)
      return false;
    const closingRect = pane.container.getBoundingClientRect();
    paneInteractions.cleanupPaneInteractions(id);
    options.destroyPane?.(pane);
    panes.delete(id);
    if (activePaneId === id)
      activePaneId = null;
    if (focusedPaneId === id)
      focusedPaneId = null;
    const parent = pane.container.parentElement;
    pane.container.remove();
    collapseSplitAncestors(parent);
    const fallback = getActivePane() ?? findClosestPaneToRect(closingRect, panes.values()) ?? getPanes()[0] ?? null;
    if (fallback) {
      markPaneFocused(fallback.id, { focus: true });
    } else {
      options.onActivePaneChange?.(null);
    }
    options.onPaneClosed?.(pane);
    requestLayoutSync();
    return true;
  };
  const createInitialPane = (config) => {
    if (panes.size) {
      return getPanes()[0];
    }
    const first = createPaneInternal(null);
    root.appendChild(first.container);
    markPaneFocused(first.id, { focus: config?.focus !== false });
    requestLayoutSync();
    return first;
  };
  const removeWindowEvents = attachPaneManagerWindowEvents({
    contextMenuController,
    hideContextMenu,
    shortcutOptions,
    splitActivePane
  });
  const destroy = () => {
    removeWindowEvents();
    if (resizeRaf) {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = 0;
    }
    for (const pane of getPanes()) {
      paneInteractions.cleanupPaneInteractions(pane.id);
      options.destroyPane?.(pane);
    }
    panes.clear();
    activePaneId = null;
    focusedPaneId = null;
    root.replaceChildren();
    hideContextMenu();
    contextMenuController?.destroy();
    if (stylesEnabled) {
      clearPaneStyleOptionsFromRoot(root);
    }
  };
  api = {
    getPanes,
    getPaneById,
    getActivePane,
    getFocusedPane,
    createInitialPane,
    setActivePane,
    markPaneFocused,
    splitPane,
    splitActivePane,
    closePane,
    getStyleOptions,
    setStyleOptions,
    requestLayoutSync,
    hideContextMenu,
    destroy
  };
  return api;
}

// node_modules/text-shaper/dist/index.js
var exports_dist = {};
__export(exports_dist, {
  verticalLayoutFeatures: () => Zz,
  verticalKanaAlternates: () => HW,
  verticalForms: () => UW,
  verticalAlternatesRotation: () => WW,
  vectorLength: () => X4,
  validateOutline: () => w4,
  useTypoMetrics: () => OX,
  upscale: () => H4,
  updateMinTransformedX: () => fH,
  truncPixel: () => t6,
  translateOutline: () => lH,
  translate2D: () => t8,
  transformPoint3x3: () => $6,
  transformPoint2D: () => Q6,
  transformOutline3D: () => b9,
  transformOutline2D: () => O6,
  transformBitmap3D: () => D8,
  transformBitmap2D: () => e6,
  traditionalForms: () => Gz,
  thirdWidthForms: () => Hz,
  tagToString: () => h9,
  tag: () => C,
  tabularFigures: () => c2,
  swash: () => x2,
  superscript: () => r2,
  subtractBitmap: () => B4,
  subscript: () => a2,
  subBitmaps: () => c0,
  stylisticSets: () => S2,
  stylisticSet: () => ZW,
  stylisticAlternates: () => u2,
  strokeUniform: () => d4,
  strokePath: () => H9,
  strokeAsymmetricCombined: () => c9,
  strokeAsymmetric: () => V8,
  standardLigatures: () => y2,
  splitWords: () => tG,
  splitGraphemes: () => aG,
  smallCaps: () => qW,
  slashedZero: () => s2,
  simplifiedForms: () => zz,
  signedDistanceToQuadratic: () => q_,
  signedDistanceToLine: () => Z_,
  signedDistanceToCubic: () => U_,
  sign: () => Y4,
  shouldGridFit: () => QV,
  shouldDoGray: () => ZV,
  shiftBitmap: () => l9,
  shearBitmapY: () => J9,
  shearBitmapX: () => q9,
  shear2D: () => y8,
  shapedTextToSVGWithVariation: () => qK,
  shapedTextToSVG: () => ZK,
  shapeInto: () => iW,
  shape: () => xz,
  setSize: () => y0,
  setCodeRange: () => a8,
  scientificInferiors: () => t2,
  scaleOutlinePow2: () => xH,
  scaleOutline: () => dH,
  scale2D: () => S8,
  runProgram: () => S9,
  runGlyphProgram: () => k0,
  runFontProgram: () => I0,
  runCVTProgram: () => v0,
  ruby: () => Kz,
  roundUpToGrid: () => o5,
  roundToHalfGrid: () => n5,
  roundToGrid: () => U8,
  roundToDoubleGrid: () => p5,
  roundSuper45: () => r5,
  roundSuper: () => s5,
  roundPixel: () => J4,
  roundOff: () => i5,
  roundDownToGrid: () => c5,
  round: () => E8,
  rotateOutline90: () => uH,
  rotateOutline: () => nH,
  rotate2D: () => h8,
  resolveDupeGlyph: () => SU,
  resizeBitmapBilinear: () => Z9,
  resizeBitmap: () => x8,
  reorderMarks: () => E5,
  reorderGlyphs: () => HH,
  renderShapedTextWithVariation: () => KK,
  renderShapedText: () => QK,
  renderSdf: () => f8,
  renderMsdf: () => l8,
  rasterizeText: () => SK,
  rasterizePathWithGradient: () => W9,
  rasterizePath: () => $8,
  rasterizeLcd: () => Y2,
  rasterizeGlyphWithTransform: () => CK,
  rasterizeGlyph: () => i9,
  quarterWidthForms: () => Wz,
  proportionalWidthForms: () => Uz,
  proportionalFigures: () => p2,
  processRearrangement: () => u5,
  processLigature: () => f5,
  processInsertion: () => l5,
  processContextual: () => x5,
  processBidi: () => hG,
  pipe: () => tK,
  petiteCaps: () => f2,
  perspectiveMatrix: () => F$,
  pathToSVGWithMatrix3D: () => UK,
  pathToSVGWithMatrix: () => JK,
  pathToSVG: () => L8,
  pathToCanvasWithMatrix3D: () => d0,
  pathToCanvasWithMatrix: () => l0,
  pathToCanvas: () => $9,
  path: () => I_,
  parseSuperRound: () => v9,
  parseScriptList: () => q0,
  parsePrep: () => P1,
  parsePost: () => x1,
  parseOs2: () => u1,
  parseName: () => V5,
  parseMath: () => b1,
  parseLookupHeaders: () => NV,
  parseJstf: () => v1,
  parseGasp: () => G1,
  parseFpgm: () => O1,
  parseFontDirectory: () => V0,
  parseFeatureList: () => J0,
  parseFeat: () => z1,
  parseCvt: () => I1,
  parseCpal: () => X1,
  parseCoverageAt: () => s,
  parseCoverage: () => eJ,
  parseColr: () => V1,
  parseClassDefAt: () => R6,
  parseClassDef: () => aJ,
  parseCff2: () => H1,
  parseCff: () => U1,
  parseBase: () => J1,
  padBitmap: () => K9,
  ordinals: () => i2,
  openTypeTagToAat: () => $V,
  oldstyleFigures: () => d2,
  obliquePath: () => _9,
  normalizeVector: () => z4,
  normalizeAxisValue: () => p$,
  normalize: () => I2,
  mustBreakAt: () => xG,
  multiply3x3: () => y9,
  multiply2D: () => b8,
  mulFix: () => N4,
  mulDiv: () => M$,
  mulBitmaps: () => I$,
  msdfAtlasToRGBA: () => sK,
  msdfAtlasToRGB: () => iK,
  median: () => Q_,
  measureRasterGlyph: () => C$,
  maxBitmaps: () => k$,
  matrixToSVGTransform: () => WK,
  matchAxisValue: () => nX,
  loadFontProgram: () => S0,
  loadCVTProgram: () => h0,
  liningFigures: () => n2,
  lcdToRGBA: () => A2,
  kerning: () => Qz,
  justifyParagraph: () => N2,
  justify: () => iU,
  jis90Forms: () => Vz,
  jis83Forms: () => Nz,
  jis78Forms: () => _z,
  jis2004Forms: () => Xz,
  italicizeOutline: () => pH,
  isVariationIndexTable: () => $U,
  isTrueType: () => l1,
  isScript: () => nG,
  isRTL: () => kG,
  isOlderSiblingFont: () => lX,
  isMonospaced: () => vX,
  isLTR: () => CG,
  isItalic: () => MX,
  isExtendedShape: () => tV,
  isExclusiveFeature: () => sN,
  isElidableAxisValue: () => fX,
  isComplexScript: () => sG,
  isColrV1: () => lN,
  isCFF: () => yX,
  isBold: () => TX,
  interpolateGradient: () => o0,
  identity3x3: () => A$,
  identity2D: () => I6,
  hypot: () => _K,
  historicalLigatures: () => m2,
  hintedToPixels: () => I7,
  hintGlyph: () => b0,
  hasVertOriginY: () => aX,
  hasSvgGlyph: () => pX,
  hasSettingValue: () => tN,
  hasGlyphOutline: () => CH,
  hasGlyphBitmap: () => hX,
  hasColorGlyph: () => bN,
  hasColorBitmap: () => XN,
  halfWidthForms: () => qz,
  glyphVar: () => P_,
  glyphToSVG: () => oH,
  glyphBufferToShapedGlyphs: () => iH,
  glyph: () => T_,
  getXHeightDelta: () => GX,
  getWordBreakProperty: () => AH,
  getVorgDelta: () => Z2,
  getVisualOrder: () => IG,
  getVerticalVariants: () => eV,
  getVerticalMetrics: () => sX,
  getVerticalAssembly: () => QX,
  getVertOriginY: () => rX,
  getVersion: () => DX,
  getTsbDelta: () => $2,
  getTrackingValue: () => uU,
  getTopAccentAttachment: () => aV,
  getTextWidth: () => sH,
  getSvgGlyphIds: () => cX,
  getSvgDocument: () => bU,
  getSubstitutedLookups: () => V2,
  getStrikeForPpem: () => CU,
  getShrinkageMods: () => S1,
  getSettingByValue: () => aN,
  getScripts: () => dG,
  getScriptTag: () => cG,
  getScriptRuns: () => pG,
  getScriptDirection: () => rG,
  getScript: () => L0,
  getSbixGlyphBitmap: () => CX,
  getPostScriptName: () => FX,
  getPathBounds: () => _8,
  getOrCreateShapePlan: () => L5,
  getNameById: () => x6,
  getMirror: () => _H,
  getMinMaxExtent: () => WN,
  getMetricDelta: () => N0,
  getMarkAttachmentType: () => W5,
  getLineBreakClass: () => GH,
  getLayerPaint: () => dN,
  getKerxValue: () => lV,
  getKernValue: () => y1,
  getJstfPriorities: () => C1,
  getItalicsCorrection: () => rV,
  getHorizontalVariants: () => $X,
  getHorizontalAssembly: () => ZX,
  getHDescenderDelta: () => zX,
  getHAscenderDelta: () => XX,
  getGraphemeBreakProperty: () => RQ,
  getGlyphUV: () => uK,
  getGlyphPathWithVariation: () => g9,
  getGlyphPath: () => y6,
  getGlyphName: () => IX,
  getGlyphLocation: () => x0,
  getGlyphDelta: () => kH,
  getGaspBehavior: () => Y1,
  getFullName: () => LX,
  getFontSubfamily: () => AX,
  getFontFamily: () => wX,
  getFillRuleFromFlags: () => A4,
  getFeature: () => H5,
  getExtensionMods: () => h1,
  getExtenderGlyphs: () => k1,
  getExactBounds: () => EK,
  getEmbeddings: () => WH,
  getEmbeddingPermission: () => PX,
  getDeviceDelta: () => QU,
  getDefaultSetting: () => rN,
  getDefaultBaseline: () => UN,
  getCombiningClass: () => G8,
  getColorVariationDelta: () => xN,
  getColorPaint: () => SJ,
  getColorLayers: () => CJ,
  getColorBitmapSizes: () => zN,
  getColor: () => nN,
  getClipBox: () => uN,
  getCharType: () => vG,
  getCffString: () => EN,
  getCffGlyphWidth: () => AN,
  getCapHeightDelta: () => YX,
  getBsbDelta: () => Q2,
  getBitmapGlyph: () => NN,
  getBaselineForScript: () => JN,
  getAxisValuesForAxis: () => uX,
  getAxisValueNumber: () => dX,
  getAxisRecord: () => mX,
  getAxisIndex: () => gX,
  getAvailablePpemSizes: () => SX,
  getAllFeatures: () => iN,
  getAllBreakOpportunities: () => fG,
  getAdvanceWidthDelta: () => o$,
  getAdvanceHeightDelta: () => eX,
  getAatFeature: () => oN,
  gaussianBlur: () => i6,
  fullWidthForms: () => Jz,
  fractions: () => o2,
  fracPixel: () => m8,
  floorPixel: () => U4,
  floatToPixel: () => q4,
  floatToF26Dot6: () => Z4,
  fixOutlineBitmap: () => R4,
  fixOutline: () => YK,
  findWordBoundaries: () => LH,
  findScript: () => U0,
  findNextBreak: () => gG,
  findMatchingFeatureVariation: () => X0,
  findLangSys: () => j1,
  findGraphemeBoundaries: () => MQ,
  findAxisValueByNameId: () => xX,
  features: () => Yz,
  feature: () => _W,
  fastGaussianBlur: () => a9,
  f26Dot6ToFloat: () => K4,
  expandToFit: () => L4,
  expandRasterMetrics: () => S$,
  executeFontProgram: () => E$,
  executeCffCharString: () => W1,
  executeCff2CharString: () => J5,
  execute: () => j$,
  evaluateConditionSet: () => sU,
  emboldenPath: () => V9,
  emboldenBitmapWithBearing: () => d9,
  emboldenBitmap: () => u8,
  downscale: () => _4,
  divFix: () => V4,
  discretionaryLigatures: () => b2,
  detectScript: () => lG,
  detectDirection: () => SG,
  decomposePath: () => F8,
  decompose: () => w5,
  createShapePlan: () => Dz,
  createPath2D: () => R$,
  createLookupDigest: () => XV,
  createHintingEngine: () => C0,
  createGradientBitmap: () => i0,
  createGlyphZone: () => J8,
  createGaussianKernel: () => m$,
  createFace: () => S_,
  createExecContext: () => M0,
  createDefaultGraphicsState: () => d5,
  createBitmap: () => r,
  countGraphemes: () => eG,
  copyBitmap: () => U6,
  convertBitmap: () => N8,
  contourToPath: () => B$,
  contextualAlternates: () => g2,
  condensePath: () => N9,
  computeTightBounds: () => f0,
  computeControlBox: () => L$,
  compositeBitmaps: () => v$,
  compensate: () => W8,
  combinePaths: () => m9,
  combineFeatures: () => jz,
  combine: () => k_,
  colorToRgba: () => pN,
  colorToHex: () => cN,
  clonePath: () => e8,
  clearBitmap: () => O$,
  clamp: () => G4,
  characterVariants: () => h2,
  characterVariant: () => KW,
  char: () => O_,
  ceilPixel: () => W4,
  caseSensitiveForms: () => e2,
  cascadeBlur: () => d8,
  capsToSmallCaps: () => JW,
  capitalSpacing: () => $z,
  canBreakAt: () => uG,
  calculateVariationDelta: () => IN,
  calculateTupleScalar: () => u0,
  calculateLineWidth: () => j5,
  buildStringAtlas: () => bK,
  buildMsdfStringAtlas: () => oK,
  buildMsdfAtlas: () => e0,
  buildMsdfAsciiAtlas: () => cK,
  buildAtlas: () => t0,
  buildAsciiAtlas: () => yK,
  breakIntoLines: () => _2,
  boxBlur: () => B8,
  blurBitmap: () => wK,
  blendBitmap: () => p0,
  bitmapToRGBA: () => s9,
  bitmapToGray: () => r9,
  bitmap: () => v_,
  atlasToRGBA: () => mK,
  atlasToAlpha: () => gK,
  assignEdgeColors: () => pK,
  applyTracking: () => iX,
  applyNonContextual: () => g1,
  applyMirroring: () => NH,
  applyMatrixToContext: () => rH,
  applyFeatureVariations: () => X2,
  applyFallbackMarkPositioning: () => o1,
  applyFallbackKerning: () => i1,
  applyDeviceAdjustment: () => WV,
  applyAvarMapping: () => cq,
  applyAvar: () => n$,
  analyzeLineBreaksFromCodepoints: () => BQ,
  analyzeLineBreaksForGlyphs: () => mG,
  analyzeLineBreaks: () => bG,
  allSmallCaps: () => l2,
  addBitmaps: () => P$,
  adaptiveBlur: () => t9,
  abs: () => l6,
  aatToOpenTypeTag: () => eN,
  WordBreakProperty: () => wH,
  WindowsEncodingId: () => jX,
  WidthClass: () => RX,
  WeightClass: () => BX,
  VerticalPositionSetting: () => mJ,
  UpperCaseSetting: () => pJ,
  UnicodeBuffer: () => C7,
  TouchFlag: () => k8,
  Tags: () => p,
  StylisticAlternativesSetting: () => lJ,
  SmartSwashSetting: () => cJ,
  SetDigest: () => V6,
  Script: () => jH,
  SbixGraphicType: () => f1,
  RoundMode: () => s8,
  Reader: () => D6,
  PlatformId: () => F9,
  PixelMode: () => F6,
  PathBuilder: () => q6,
  PaletteType: () => hJ,
  PaintFormat: () => IJ,
  PIXEL_MASK: () => aH,
  PIXEL_BITS: () => W6,
  OutlineError: () => GK,
  Opcode: () => B,
  ONE_PIXEL: () => Z6,
  NumberSpacingSetting: () => uJ,
  NumberCaseSetting: () => gJ,
  NormalizationMode: () => QW,
  NameId: () => u6,
  MvarTags: () => _0,
  MorxSubtableType: () => m1,
  LowerCaseSetting: () => nJ,
  LookupFlag: () => b6,
  LineBreakClass: () => VH,
  LigatureSetting: () => bJ,
  LcdMode: () => aU,
  LCD_FILTER_LIGHT: () => z2,
  LCD_FILTER_LEGACY: () => G2,
  LCD_FILTER_DEFAULT: () => rU,
  JustifyMode: () => oU,
  JstfPriority: () => CV,
  GraphemeBreakProperty: () => EH,
  GlyphClass: () => g0,
  GlyphBuffer: () => C8,
  GaspFlag: () => K0,
  FsType: () => X5,
  FsSelection: () => z5,
  FractionsSetting: () => xJ,
  FontCollection: () => p1,
  Font: () => Z8,
  FillRule: () => H8,
  FeatureType: () => yJ,
  FeatureTags: () => pY,
  FeatureFlags: () => sJ,
  Face: () => n8,
  F26DOT6_SHIFT: () => tH,
  F26DOT6_ONE: () => eH,
  F16DOT16_SHIFT: () => $4,
  F16DOT16_ONE: () => Q4,
  Extend: () => vJ,
  EMPTY_CLASS_DEF: () => rJ,
  Direction: () => m0,
  DiacriticsSetting: () => oJ,
  Coverage: () => E9,
  ContextualAlternativesSetting: () => dJ,
  CompositeMode: () => kJ,
  CodeRange: () => r8,
  ClusterLevel: () => v7,
  ClassDef: () => O8,
  CharacterShapeSetting: () => iJ,
  CbdtImageFormat: () => HN,
  CaseSensitiveLayoutSetting: () => fJ,
  BufferFlags: () => k7,
  BreakOpportunity: () => zH,
  BreakAction: () => XH,
  BitmapBuilder: () => i,
  BidiType: () => i8,
  BaselineTag: () => ZN,
  AxisValueFlags: () => d1,
  $translate: () => Qq,
  $transformGlyph3D: () => kq,
  $transformGlyph2D: () => vq,
  $transformBitmap3D: () => Iq,
  $transformBitmap2D: () => Pq,
  $toSVG: () => dq,
  $toRGBA: () => fq,
  $toGray: () => lq,
  $strokePath: () => Vq,
  $strokeAsymmetricCombined: () => Yq,
  $strokeAsymmetric: () => Gq,
  $shift: () => bq,
  $shearGlyphY: () => yq,
  $shearGlyphX: () => hq,
  $shearBitmapY: () => Sq,
  $shearBitmapX: () => Cq,
  $shear: () => qq,
  $scale: () => $q,
  $rotateDeg: () => Kq,
  $rotate: () => Zq,
  $resizeBilinear: () => gq,
  $resize: () => mq,
  $renderSdf: () => Aq,
  $renderMsdf: () => Lq,
  $rasterizeWithGradient: () => wq,
  $rasterizeAuto: () => Eq,
  $rasterize: () => jq,
  $perspective: () => Wq,
  $pad: () => uq,
  $obliquePath: () => Nq,
  $matrix: () => Uq,
  $italic: () => Jq,
  $fromGlyph: () => eK,
  $fastBlur: () => Mq,
  $emboldenPath: () => Hq,
  $emboldenGlyph: () => Oq,
  $embolden: () => Tq,
  $copy: () => nq,
  $convert: () => xq,
  $condensePath: () => _q,
  $combinePaths: () => zq,
  $clone: () => Xq,
  $cascadeBlur: () => Bq,
  $boxBlur: () => Dq,
  $blur: () => Fq,
  $adaptiveBlur: () => Rq
});
var { defineProperty: g5, getOwnPropertyNames: DH, getOwnPropertyDescriptor: BH } = Object;
var RH = Object.prototype.hasOwnProperty;
var TQ = new WeakMap;
var B0 = ($) => {
  var Q = TQ.get($), Z;
  if (Q)
    return Q;
  if (Q = g5({}, "__esModule", { value: true }), $ && typeof $ === "object" || typeof $ === "function")
    DH($).map((K) => !RH.call(Q, K) && g5(Q, K, { get: () => $[K], enumerable: !(Z = BH($, K)) || Z.enumerable }));
  return TQ.set($, Q), Q;
};
var OQ = ($, Q) => {
  for (var Z in Q)
    g5($, Z, { get: Q[Z], enumerable: true, configurable: true, set: (K) => Q[Z] = () => K });
};
var I9 = ($, Q) => () => ($ && (Q = $($ = 0)), Q);
var u9 = {};
OQ(u9, { createBottomUpBitmap: () => VK, createBitmap: () => r, clearBitmap: () => O$, PixelMode: () => F6, FillRule: () => H8 });
function r($, Q, Z = 1) {
  let K;
  switch (Z) {
    case 0:
      K = 0.125;
      break;
    case 1:
      K = 1;
      break;
    case 2:
    case 3:
      K = 3;
      break;
    case 4:
      K = 4;
      break;
  }
  let q = Z === 0 ? Math.ceil($ / 8) : $ * K;
  return { buffer: new Uint8Array(q * Q), width: $, rows: Q, pitch: q, pixelMode: Z, numGrays: Z === 0 ? 2 : 256 };
}
function O$($) {
  $.buffer.fill(0);
}
function VK($, Q, Z = 1) {
  let K = r($, Q, Z);
  return K.pitch = -K.pitch, K;
}
var F6;
var H8;
var G6 = I9(() => {
  ((J) => {
    J[J.Mono = 0] = "Mono";
    J[J.Gray = 1] = "Gray";
    J[J.LCD = 2] = "LCD";
    J[J.LCD_V = 3] = "LCD_V";
    J[J.RGBA = 4] = "RGBA";
  })(F6 ||= {});
  ((Z) => {
    Z[Z.NonZero = 0] = "NonZero";
    Z[Z.EvenOdd = 1] = "EvenOdd";
  })(H8 ||= {});
});
var r$;
var $0;
var tq = I9(() => {
  r$ = new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 0, 4, 4, 0, 0, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 12, 16, 12, 12, 20, 12, 16, 24, 28, 12, 12, 32, 12, 36, 12, 44, 44, 44, 44, 44, 44, 44, 44, 44, 44, 32, 32, 24, 40, 28, 12, 12, 48, 52, 52, 52, 48, 52, 52, 52, 48, 52, 52, 52, 52, 52, 48, 52, 52, 52, 52, 52, 48, 52, 52, 52, 52, 52, 24, 12, 28, 12, 12, 12, 56, 60, 60, 60, 56, 60, 60, 60, 56, 60, 60, 60, 60, 60, 56, 60, 60, 60, 60, 60, 56, 60, 60, 60, 60, 60, 24, 12, 28, 12, 0, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 7, 0, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 56, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 6, 6, 6, 6, 7, 7, 7, 7, 8, 8, 8, 8, 9, 9, 9, 9, 10, 10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15, 16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18, 19, 19, 19, 19, 20, 20, 20, 20, 21, 21, 21, 21, 22, 22, 22, 22, 23, 23, 23, 23, 24, 24, 24, 24, 25, 25, 25, 25, 26, 26, 26, 26, 27, 27, 27, 27, 28, 28, 28, 28, 29, 29, 29, 29, 30, 30, 30, 30, 31, 31, 31, 31, 32, 32, 32, 32, 33, 33, 33, 33, 34, 34, 34, 34, 35, 35, 35, 35, 36, 36, 36, 36, 37, 37, 37, 37, 38, 38, 38, 38, 39, 39, 39, 39, 40, 40, 40, 40, 41, 41, 41, 41, 42, 42, 42, 42, 43, 43, 43, 43, 44, 44, 44, 44, 45, 45, 45, 45, 46, 46, 46, 46, 47, 47, 47, 47, 48, 48, 48, 48, 49, 49, 49, 49, 50, 50, 50, 50, 51, 51, 51, 51, 52, 52, 52, 52, 53, 53, 53, 53, 54, 54, 54, 54, 55, 55, 55, 55, 56, 56, 56, 56, 57, 57, 57, 57, 58, 58, 58, 58, 59, 59, 59, 59, 60, 60, 60, 60, 61, 61, 61, 61, 62, 62, 62, 62, 63, 63, 63, 63, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]), $0 = new Uint16Array([1024, 1536, 1280, 1536, 0, 256, 768, 512]);
});
var eq;
var $J;
var QJ;
var ZJ = I9(() => {
  eq = new Uint32Array([0, 0, 0, 0, 0, 4096, 9216, 21504, 35840, 44032, 53248, 63488, 74752, 87040, 93696, 100864, 104704, 106752, 108928, 113536, 115968, 118528, 119872, 121280, 122016]), $J = new Uint8Array([0, 0, 0, 0, 10, 10, 11, 11, 10, 10, 10, 10, 10, 9, 9, 8, 7, 7, 8, 7, 7, 6, 6, 5, 5]), QJ = new Uint8Array(122784);
});
function b($, Q, Z) {
  let K = new Uint8Array($.length), q = new Uint8Array(Z.length);
  for (let J = 0;J < $.length; J++)
    K[J] = $.charCodeAt(J);
  for (let J = 0;J < Z.length; J++)
    q[J] = Z.charCodeAt(J);
  return { prefix: K, transform: Q, suffix: q };
}
function KJ($, Q) {
  if ($[Q] < 192) {
    if ($[Q] >= 97 && $[Q] <= 122)
      $[Q] ^= 32;
    return 1;
  }
  if ($[Q] < 224)
    return $[Q + 1] ^= 32, 2;
  return $[Q + 2] ^= 5, 3;
}
function qJ($, Q, Z, K, q, J) {
  let U = a$[q], W = U.transform, H = W < 12 ? 0 : W - 11, _ = Q;
  if (H > K)
    H = K;
  for (let z = 0;z < U.prefix.length; z++)
    $[Q++] = U.prefix[z];
  let N = Z + H, V = K - H;
  if (W <= 9)
    V -= W;
  for (let z = 0;z < V; z++)
    $[Q++] = J[N + z];
  let X = Q - V;
  if (W === 10)
    KJ($, X);
  else if (W === 11)
    while (V > 0) {
      let z = KJ($, X);
      X += z, V -= z;
    }
  for (let z = 0;z < U.suffix.length; z++)
    $[Q++] = U.suffix[z];
  return Q - _;
}
var a$;
var JJ = I9(() => {
  a$ = [b("", 0, ""), b("", 0, " "), b(" ", 0, " "), b("", 12, ""), b("", 10, " "), b("", 0, " the "), b(" ", 0, ""), b("s ", 0, " "), b("", 0, " of "), b("", 10, ""), b("", 0, " and "), b("", 13, ""), b("", 1, ""), b(", ", 0, " "), b("", 0, ", "), b(" ", 10, " "), b("", 0, " in "), b("", 0, " to "), b("e ", 0, " "), b("", 0, '"'), b("", 0, "."), b("", 0, '">'), b("", 0, `
`), b("", 3, ""), b("", 0, "]"), b("", 0, " for "), b("", 14, ""), b("", 2, ""), b("", 0, " a "), b("", 0, " that "), b(" ", 10, ""), b("", 0, ". "), b(".", 0, ""), b(" ", 0, ", "), b("", 15, ""), b("", 0, " with "), b("", 0, "'"), b("", 0, " from "), b("", 0, " by "), b("", 16, ""), b("", 17, ""), b(" the ", 0, ""), b("", 4, ""), b("", 0, ". The "), b("", 11, ""), b("", 0, " on "), b("", 0, " as "), b("", 0, " is "), b("", 7, ""), b("", 1, "ing "), b("", 0, `
	`), b("", 0, ":"), b(" ", 0, ". "), b("", 0, "ed "), b("", 20, ""), b("", 18, ""), b("", 6, ""), b("", 0, "("), b("", 10, ", "), b("", 8, ""), b("", 0, " at "), b("", 0, "ly "), b(" the ", 0, " of "), b("", 5, ""), b("", 9, ""), b(" ", 10, ", "), b("", 10, '"'), b(".", 0, "("), b("", 11, " "), b("", 10, '">'), b("", 0, '="'), b(" ", 0, "."), b(".com/", 0, ""), b(" the ", 0, " of the "), b("", 10, "'"), b("", 0, ". This "), b("", 0, ","), b(".", 0, " "), b("", 10, "("), b("", 10, "."), b("", 0, " not "), b(" ", 0, '="'), b("", 0, "er "), b(" ", 11, " "), b("", 0, "al "), b(" ", 11, ""), b("", 0, "='"), b("", 11, '"'), b("", 10, ". "), b(" ", 0, "("), b("", 0, "ful "), b(" ", 10, ". "), b("", 0, "ive "), b("", 0, "less "), b("", 11, "'"), b("", 0, "est "), b(" ", 10, "."), b("", 11, '">'), b(" ", 0, "='"), b("", 10, ","), b("", 0, "ize "), b("", 11, "."), b("Â ", 0, ""), b(" ", 0, ","), b("", 10, '="'), b("", 11, '="'), b("", 0, "ous "), b("", 11, ", "), b("", 10, "='"), b(" ", 10, ","), b(" ", 11, '="'), b(" ", 11, ", "), b("", 11, ","), b("", 11, "("), b("", 11, ". "), b(" ", 11, "."), b("", 11, "='"), b(" ", 11, ". "), b(" ", 10, '="'), b(" ", 11, "='"), b(" ", 10, "='")];
});
var XJ = {};
OQ(XJ, { decompress: () => o_, __testing: () => i_ });

class Z1 {
  data;
  buf;
  pos = 0;
  val = 0;
  bitPos = 0;
  bitEndPos = 0;
  eos = false;
  constructor($) {
    this.data = $;
    this.buf = new Uint8Array(8224), this.fillBuffer();
    for (let Q = 0;Q < 4; Q++)
      this.val |= this.buf[this.pos] << 8 * Q, this.pos++;
  }
  fillBuffer() {
    if (this.bitEndPos > 256)
      return;
    if (this.eos) {
      if (this.bitPos > this.bitEndPos)
        throw Error("Unexpected end of input");
      return;
    }
    let $ = this.data.length - this.pos, Q = Math.min(4096, $);
    if (Q > 0)
      this.buf.set(this.data.subarray(this.pos, this.pos + Q), 0), this.pos = 0;
    if (Q < 4096) {
      this.eos = true;
      for (let Z = 0;Z < 32; Z++)
        this.buf[Q + Z] = 0;
    }
    this.bitEndPos += Q << 3;
  }
  readMoreInput() {
    if (this.bitEndPos > 256)
      return;
    if (this.eos) {
      if (this.bitPos > this.bitEndPos)
        throw Error("Unexpected end of input");
      return;
    }
    let $ = this.pos & 4095, Q = Math.min(4096, this.data.length - (this.pos & -4096));
    if (Q > 0) {
      let Z = this.pos & -4096;
      this.buf.set(this.data.subarray(Z, Z + Q), $ === 0 ? 0 : 4096);
    }
    if (Q < 4096) {
      this.eos = true;
      for (let Z = 0;Z < 32; Z++)
        this.buf[$ + Q + Z] = 0;
    }
    this.bitEndPos += Q << 3;
  }
  fillBitWindow() {
    while (this.bitPos >= 8)
      this.val >>>= 8, this.val |= this.buf[this.pos & 8191] << 24, this.pos++, this.bitPos -= 8, this.bitEndPos -= 8;
  }
  readBits($) {
    if (32 - this.bitPos < $)
      this.fillBitWindow();
    let Q = this.val >>> this.bitPos & (1 << $) - 1;
    return this.bitPos += $, Q;
  }
  get currentBitPos() {
    return this.bitPos;
  }
  set currentBitPos($) {
    this.bitPos = $;
  }
  get currentVal() {
    return this.val;
  }
}
function K1($, Q, Z, K, q) {
  let U = new Int32Array(16), W = new Int32Array(16), H = new Int32Array(q);
  for (let E = 0;E < q; E++)
    U[K[E]]++;
  W[1] = 0;
  for (let E = 1;E < 15; E++)
    W[E + 1] = W[E] + U[E];
  for (let E = 0;E < q; E++)
    if (K[E] !== 0)
      H[W[K[E]]++] = E;
  let _ = Z, N = 1 << _, V = N;
  if (W[15] === 1) {
    for (let E = 0;E < V; E++)
      $[Q + E] = { bits: 0, value: H[0] & 65535 };
    return V;
  }
  let X = 0, z = 0;
  for (let E = 1, w = 2;E <= Z; E++, w <<= 1)
    for (;U[E] > 0; U[E]--) {
      let L = { bits: E & 255, value: H[z++] & 65535 };
      Q1($, Q + X, w, N, L), X = $1(X, E);
    }
  let G = V - 1, Y = -1, j = Q;
  for (let E = Z + 1, w = 2;E <= 15; E++, w <<= 1)
    for (;U[E] > 0; U[E]--) {
      if ((X & G) !== Y)
        j += N, _ = _J(U, E, Z), N = 1 << _, V += N, Y = X & G, $[Q + Y] = { bits: _ + Z & 255, value: j - Q - Y & 65535 };
      let L = { bits: E - Z & 255, value: H[z++] & 65535 };
      Q1($, j + (X >> Z), w, N, L), X = $1(X, E);
    }
  return V;
}
function $1($, Q) {
  let Z = 1 << Q - 1;
  while ($ & Z)
    Z >>= 1;
  return ($ & Z - 1) + Z;
}
function Q1($, Q, Z, K, q) {
  do
    K -= Z, $[Q + K] = { bits: q.bits, value: q.value };
  while (K > 0);
}
function _J($, Q, Z) {
  let K = 1 << Q - Z;
  while (Q < 15) {
    if (K -= $[Q], K <= 0)
      break;
    Q++, K <<= 1;
  }
  return Q - Z;
}
function R8($, Q, Z) {
  Z.fillBitWindow();
  let K = Q + (Z.currentVal >>> Z.currentBitPos & m_), q = $[K].bits - e$;
  if (q > 0)
    Z.currentBitPos += e$, K += $[K].value, K += Z.currentVal >>> Z.currentBitPos & (1 << q) - 1;
  return Z.currentBitPos += $[K].bits, $[K].value;
}
function NJ($) {
  if ($.readBits(1)) {
    let Q = $.readBits(3);
    if (Q === 0)
      return 1;
    return $.readBits(Q) + (1 << Q);
  }
  return 0;
}
function n_($) {
  if ($.readBits(1) === 0)
    return 16;
  let Q = $.readBits(3);
  if (Q > 0)
    return 17 + Q;
  if (Q = $.readBits(3), Q > 0)
    return 8 + Q;
  return 17;
}
function p_($) {
  let Q = $.readBits(1) === 1;
  if (Q && $.readBits(1))
    return { length: 0, isLast: true, isUncompressed: false, isMetadata: false };
  let Z = $.readBits(2) + 4;
  if (Z === 7) {
    if ($.readBits(1) !== 0)
      throw Error("Invalid reserved bit");
    let J = $.readBits(2);
    if (J === 0)
      return { length: 0, isLast: Q, isUncompressed: false, isMetadata: true };
    let U = 0;
    for (let W = 0;W < J; W++) {
      let H = $.readBits(8);
      if (W + 1 === J && J > 1 && H === 0)
        throw Error("Invalid size byte");
      U |= H << W * 8;
    }
    return { length: U + 1, isLast: Q, isUncompressed: false, isMetadata: true };
  }
  let K = 0;
  for (let J = 0;J < Z; J++) {
    let U = $.readBits(4);
    if (J + 1 === Z && Z > 4 && U === 0)
      throw Error("Invalid size nibble");
    K |= U << J * 4;
  }
  K++;
  let q = !Q ? $.readBits(1) === 1 : false;
  return { length: K, isLast: Q, isUncompressed: q, isMetadata: false };
}
function VJ($, Q, Z, K) {
  let U = 0, W = 8, H = 0, _ = 0, N = 32768, V = [];
  for (let X = 0;X < 32; X++)
    V.push({ bits: 0, value: 0 });
  K1(V, 0, 5, $, Q5);
  while (U < Q && N > 0) {
    K.readMoreInput(), K.fillBitWindow();
    let X = K.currentVal >>> K.currentBitPos & 31;
    K.currentBitPos += V[X].bits;
    let z = V[X].value & 255;
    if (z < 16) {
      if (H = 0, Z[U++] = z, z !== 0)
        W = z, N -= 32768 >> z;
    } else {
      let G = z - 14, Y = 0;
      if (z === 16)
        Y = W;
      if (_ !== Y)
        H = 0, _ = Y;
      let j = H;
      if (H > 0)
        H -= 2, H <<= G;
      H += K.readBits(G) + 3;
      let E = H - j;
      if (U + E > Q)
        throw Error("Symbol overflow");
      for (let w = 0;w < E; w++)
        Z[U + w] = _;
      if (U += E, _ !== 0)
        N -= E << 15 - _;
    }
  }
  if (N !== 0)
    throw Error("Invalid code lengths");
  for (;U < Q; U++)
    Z[U] = 0;
}
function Z5($, Q, Z, K) {
  let q = new Uint8Array($);
  K.readMoreInput();
  let J = K.readBits(2);
  if (J === 1) {
    let U = $ - 1, W = 0;
    while (U)
      U >>= 1, W++;
    let H = new Int32Array(4), _ = K.readBits(2) + 1;
    for (let N = 0;N < _; N++)
      H[N] = K.readBits(W) % $, q[H[N]] = 2;
    switch (q[H[0]] = 1, _) {
      case 2:
        if (H[0] === H[1])
          throw Error("Invalid symbols");
        q[H[1]] = 1;
        break;
      case 4:
        if (K.readBits(1))
          q[H[2]] = 3, q[H[3]] = 3;
        else
          q[H[0]] = 2;
        break;
    }
  } else {
    let U = new Uint8Array(Q5), W = 32, H = 0, _ = [{ bits: 2, value: 0 }, { bits: 2, value: 4 }, { bits: 2, value: 3 }, { bits: 3, value: 2 }, { bits: 2, value: 0 }, { bits: 2, value: 4 }, { bits: 2, value: 3 }, { bits: 4, value: 1 }, { bits: 2, value: 0 }, { bits: 2, value: 4 }, { bits: 2, value: 3 }, { bits: 3, value: 2 }, { bits: 2, value: 0 }, { bits: 2, value: 4 }, { bits: 2, value: 3 }, { bits: 4, value: 5 }];
    for (let N = J;N < Q5 && W > 0; N++) {
      let V = g_[N];
      K.fillBitWindow();
      let X = K.currentVal >>> K.currentBitPos & 15;
      K.currentBitPos += _[X].bits;
      let z = _[X].value;
      if (U[V] = z, z !== 0)
        W -= 32 >> z, H++;
    }
    if (!(H === 1 || W === 0))
      throw Error("Invalid code length codes");
    VJ(U, $, q, K);
  }
  return K1(Q, Z, e$, q, $);
}

class $5 {
  alphabetSize;
  numHTrees;
  codes = [];
  htrees;
  constructor($, Q) {
    this.alphabetSize = $;
    this.numHTrees = Q;
    this.htrees = new Uint32Array(Q);
    let Z = this.getMaxTableSize();
    for (let K = 0;K < Q + Q * Z; K++)
      this.codes.push({ bits: 0, value: 0 });
  }
  getMaxTableSize() {
    let $ = [256, 402, 436, 468, 500, 534, 566, 598, 630, 662, 694, 726, 758, 790, 822, 854, 886, 920, 952, 984, 1016, 1048, 1080], Q = this.alphabetSize + 31 >>> 5;
    return $[Math.min(Q, $.length - 1)];
  }
  decode($) {
    let Q = 0;
    for (let Z = 0;Z < this.numHTrees; Z++) {
      this.htrees[Z] = Q;
      let K = Z5(this.alphabetSize, this.codes, Q, $);
      Q += K;
    }
  }
}
function HJ($, Q) {
  Q.readMoreInput();
  let Z = NJ(Q) + 1, K = new Uint8Array($);
  if (Z <= 1)
    return { numHTrees: Z, contextMap: K };
  let q = Q.readBits(1) === 1, J = 0;
  if (q)
    J = Q.readBits(4) + 1;
  let U = [];
  for (let W = 0;W < X8; W++)
    U.push({ bits: 0, value: 0 });
  Z5(Z + J, U, 0, Q);
  for (let W = 0;W < $; ) {
    Q.readMoreInput();
    let H = R8(U, 0, Q);
    if (H === 0)
      K[W++] = 0;
    else if (H <= J) {
      let _ = 1 + (1 << H) + Q.readBits(H);
      for (let N = 1;N < _ && W < $; N++)
        K[W++] = 0;
    } else
      K[W++] = H - J;
  }
  if (Q.readBits(1)) {
    let W = new Uint8Array(256);
    for (let H = 0;H < 256; H++)
      W[H] = H;
    for (let H = 0;H < $; H++) {
      let _ = K[H];
      if (K[H] = W[_], _) {
        let N = W[_];
        for (let V = _;V > 0; V--)
          W[V] = W[V - 1];
        W[0] = N;
      }
    }
  }
  return { numHTrees: Z, contextMap: K };
}
function Q0($, Q, Z) {
  let K = R8($, Q, Z), q = f_[K];
  return q.offset + Z.readBits(q.nbits);
}
function c_($, Q, Z) {
  if ($ < t$) {
    let K = Z + u_[$] & 3;
    return Q[K] + x_[$];
  }
  return $ - t$ + 1;
}
function o_($) {
  let Q = new Z1($), Z = n_(Q), K = (1 << Z) - 16, q = 1 << Z, J = q - 1, U = new Uint8Array(q + 578), W = 0, H = 0, _ = [16, 15, 11, 4], N = 0, V = 0, X = 0, z = [];
  while (true) {
    Q.readMoreInput();
    let G = p_(Q);
    if (G.length === 0 && G.isLast)
      break;
    if (G.isMetadata) {
      Q.currentBitPos = Q.currentBitPos + 7 & -8;
      for (let n = 0;n < G.length; n++)
        Q.readMoreInput(), Q.readBits(8);
      continue;
    }
    let Y = G.length;
    if (Y === 0)
      continue;
    if (G.isUncompressed) {
      Q.currentBitPos = Q.currentBitPos + 7 & -8;
      for (let n = 0;n < Y; n++) {
        Q.readMoreInput();
        let e = Q.readBits(8);
        if (U[W & J] = e, (W & J) === J)
          z.push(...U.slice(0, q));
        W++;
      }
      continue;
    }
    let j = [1, 1, 1], E = [268435456, 268435456, 268435456], w = [0, 0, 0], L = [0, 1, 0, 1, 0, 1], A = [0, 0, 0], D = [], F = [];
    for (let n = 0;n < 3 * X8; n++)
      D.push({ bits: 0, value: 0 }), F.push({ bits: 0, value: 0 });
    for (let n = 0;n < 3; n++)
      if (j[n] = NJ(Q) + 1, j[n] >= 2)
        Z5(j[n] + 2, D, n * X8, Q), Z5(b_, F, n * X8, Q), E[n] = Q0(F, n * X8, Q), A[n] = 1;
    Q.readMoreInput();
    let M = Q.readBits(2), R = t$ + (Q.readBits(4) << M), P = (1 << M) - 1, T = R + (48 << M), m = new Uint8Array(j[0]);
    for (let n = 0;n < j[0]; n++)
      Q.readMoreInput(), m[n] = Q.readBits(2) << 1;
    let v = HJ(j[0] << 6, Q), f = HJ(j[2] << 2, Q), l = [new $5(h_, v.numHTrees), new $5(y_, j[1]), new $5(T, f.numHTrees)];
    for (let n = 0;n < 3; n++)
      l[n].decode(Q);
    let k = 0, O = 0, h = m[w[0]], S = $0[h], d = $0[h + 1], o = l[1].htrees[0];
    while (Y > 0) {
      if (Q.readMoreInput(), E[1] === 0) {
        let g6 = R8(D, X8, Q), S6;
        if (g6 === 0)
          S6 = L[2 + (A[1] & 1)];
        else if (g6 === 1)
          S6 = L[2 + (A[1] - 1 & 1)] + 1;
        else
          S6 = g6 - 2;
        if (S6 >= j[1])
          S6 -= j[1];
        w[1] = S6, L[2 + (A[1] & 1)] = S6, A[1]++, E[1] = Q0(F, X8, Q), o = l[1].htrees[w[1]];
      }
      E[1]--;
      let n = R8(l[1].codes, o, Q), e = n >> 6, u;
      if (e >= 2)
        u = -1;
      else
        u = 0;
      let N6 = l_[e] + (n >> 3 & 7), y = d_[e] + (n & 7), t = UJ[N6].offset + Q.readBits(UJ[N6].nbits), g = WJ[y].offset + Q.readBits(WJ[y].nbits);
      V = U[W - 1 & J], X = U[W - 2 & J];
      for (let g6 = 0;g6 < t; g6++) {
        if (Q.readMoreInput(), E[0] === 0) {
          let M6 = R8(D, 0, Q), c6;
          if (M6 === 0)
            c6 = L[A[0] & 1];
          else if (M6 === 1)
            c6 = L[A[0] - 1 & 1] + 1;
          else
            c6 = M6 - 2;
          if (c6 >= j[0])
            c6 -= j[0];
          w[0] = c6, L[A[0] & 1] = c6, A[0]++, E[0] = Q0(F, 0, Q), k = w[0] << 6, h = m[w[0]], S = $0[h], d = $0[h + 1];
        }
        let S6 = r$[S + V] | r$[d + X], r6 = v.contextMap[k + S6];
        if (E[0]--, X = V, V = R8(l[0].codes, l[0].htrees[r6], Q), U[W & J] = V, (W & J) === J)
          z.push(...U.slice(0, q));
        W++;
      }
      if (Y -= t, Y <= 0)
        break;
      if (u < 0) {
        if (Q.readMoreInput(), E[2] === 0) {
          let r6 = R8(D, 2 * X8, Q), M6;
          if (r6 === 0)
            M6 = L[4 + (A[2] & 1)];
          else if (r6 === 1)
            M6 = L[4 + (A[2] - 1 & 1)] + 1;
          else
            M6 = r6 - 2;
          if (M6 >= j[2])
            M6 -= j[2];
          w[2] = M6, L[4 + (A[2] & 1)] = M6, A[2]++, E[2] = Q0(F, 2 * X8, Q), O = w[2] << 2;
        }
        E[2]--;
        let g6 = (g > 4 ? 3 : g - 2) & 255, S6 = f.contextMap[O + g6];
        if (u = R8(l[2].codes, l[2].htrees[S6], Q), u >= R) {
          u -= R;
          let r6 = u & P;
          u >>= M;
          let M6 = (u >> 1) + 1, c6 = (2 + (u & 1) << M6) - 4;
          u = R + (c6 + Q.readBits(M6) << M) + r6;
        }
      }
      let w6 = c_(u, _, N);
      if (w6 < 0)
        throw Error("Invalid distance");
      if (W < K && H !== K)
        H = W;
      else
        H = K;
      let F0 = W & J;
      if (w6 > H)
        if (g >= 4 && g <= 24) {
          let g6 = eq[g], S6 = w6 - H - 1, r6 = $J[g], M6 = (1 << r6) - 1, c6 = S6 & M6, b5 = S6 >> r6, FH = g6 + c6 * g;
          if (b5 < a$.length) {
            let m5 = qJ(U, F0, FH, g, b5, QJ);
            if (F0 += m5, W += m5, Y -= m5, F0 >= q) {
              z.push(...U.slice(0, q));
              for (let D0 = 0;D0 < F0 - q; D0++)
                U[D0] = U[q + D0];
            }
          } else
            throw Error("Invalid backward reference");
        } else
          throw Error("Invalid backward reference");
      else {
        if (u > 0)
          _[N & 3] = w6, N++;
        if (g > Y)
          throw Error("Invalid backward reference");
        for (let g6 = 0;g6 < g; g6++) {
          if (U[W & J] = U[W - w6 & J], (W & J) === J)
            z.push(...U.slice(0, q));
          W++, Y--;
        }
      }
      V = U[W - 1 & J], X = U[W - 2 & J];
    }
    if (G.isLast)
      break;
  }
  return z.push(...U.slice(0, W & J)), new Uint8Array(z);
}
var X8 = 1080;
var Q5 = 18;
var h_ = 256;
var y_ = 704;
var b_ = 26;
var t$ = 16;
var e$ = 8;
var m_ = 255;
var g_;
var u_;
var x_;
var f_;
var UJ;
var WJ;
var l_;
var d_;
var i_;
var zJ = I9(() => {
  tq();
  ZJ();
  JJ();
  g_ = new Uint8Array([1, 2, 3, 4, 0, 5, 17, 6, 16, 7, 8, 9, 10, 11, 12, 13, 14, 15]), u_ = new Uint8Array([3, 2, 1, 0, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 2]), x_ = new Int8Array([0, 0, 0, 0, -1, 1, -2, 2, -3, 3, -1, 1, -2, 2, -3, 3]), f_ = [{ offset: 1, nbits: 2 }, { offset: 5, nbits: 2 }, { offset: 9, nbits: 2 }, { offset: 13, nbits: 2 }, { offset: 17, nbits: 3 }, { offset: 25, nbits: 3 }, { offset: 33, nbits: 3 }, { offset: 41, nbits: 3 }, { offset: 49, nbits: 4 }, { offset: 65, nbits: 4 }, { offset: 81, nbits: 4 }, { offset: 97, nbits: 4 }, { offset: 113, nbits: 5 }, { offset: 145, nbits: 5 }, { offset: 177, nbits: 5 }, { offset: 209, nbits: 5 }, { offset: 241, nbits: 6 }, { offset: 305, nbits: 6 }, { offset: 369, nbits: 7 }, { offset: 497, nbits: 8 }, { offset: 753, nbits: 9 }, { offset: 1265, nbits: 10 }, { offset: 2289, nbits: 11 }, { offset: 4337, nbits: 12 }, { offset: 8433, nbits: 13 }, { offset: 16625, nbits: 24 }], UJ = [{ offset: 0, nbits: 0 }, { offset: 1, nbits: 0 }, { offset: 2, nbits: 0 }, { offset: 3, nbits: 0 }, { offset: 4, nbits: 0 }, { offset: 5, nbits: 0 }, { offset: 6, nbits: 1 }, { offset: 8, nbits: 1 }, { offset: 10, nbits: 2 }, { offset: 14, nbits: 2 }, { offset: 18, nbits: 3 }, { offset: 26, nbits: 3 }, { offset: 34, nbits: 4 }, { offset: 50, nbits: 4 }, { offset: 66, nbits: 5 }, { offset: 98, nbits: 5 }, { offset: 130, nbits: 6 }, { offset: 194, nbits: 7 }, { offset: 322, nbits: 8 }, { offset: 578, nbits: 9 }, { offset: 1090, nbits: 10 }, { offset: 2114, nbits: 12 }, { offset: 6210, nbits: 14 }, { offset: 22594, nbits: 24 }], WJ = [{ offset: 2, nbits: 0 }, { offset: 3, nbits: 0 }, { offset: 4, nbits: 0 }, { offset: 5, nbits: 0 }, { offset: 6, nbits: 0 }, { offset: 7, nbits: 0 }, { offset: 8, nbits: 0 }, { offset: 9, nbits: 0 }, { offset: 10, nbits: 1 }, { offset: 12, nbits: 1 }, { offset: 14, nbits: 2 }, { offset: 18, nbits: 2 }, { offset: 22, nbits: 3 }, { offset: 30, nbits: 3 }, { offset: 38, nbits: 4 }, { offset: 54, nbits: 4 }, { offset: 70, nbits: 5 }, { offset: 102, nbits: 5 }, { offset: 134, nbits: 6 }, { offset: 198, nbits: 7 }, { offset: 326, nbits: 8 }, { offset: 582, nbits: 9 }, { offset: 1094, nbits: 10 }, { offset: 2118, nbits: 24 }], l_ = [0, 0, 8, 8, 0, 16, 8, 16, 16], d_ = [0, 8, 0, 8, 16, 0, 16, 8, 16];
  i_ = { getNextKey: $1, replicateValue: Q1, nextTableBitSize: _J, buildHuffmanTable: K1, readBlockLength: Q0, readHuffmanCodeLengths: VJ, BitReader: Z1, HuffmanCode: {}, CODE_LENGTH_CODES: Q5 };
});
function R0($, Q) {
  if (Q < 0 || Q >= $.classArray.length)
    return 1;
  return $.classArray[Q] ?? 1;
}
function u5($, Q) {
  let { stateTable: Z } = $, K = 0, q = 0, J = 0;
  for (let U = 0;U <= Q.length; U++) {
    let H = U >= Q.length ? 0 : R0(Z.classTable, Q[U]?.glyphId), _ = Z.stateArray[K];
    if (!_)
      break;
    let N = _[H];
    if (!N)
      break;
    let V = N.flags;
    if (V & 32768)
      q = U;
    if (V & 8192)
      J = U;
    let X = V & 15;
    if (X !== 0 && q <= J && J < Q.length)
      MH(Q, q, J, X);
    if (!(V & 16384))
      ;
    K = N.newState;
  }
}
function MH($, Q, Z, K) {
  if (Q >= Z || Q >= $.length || Z >= $.length)
    return;
  let q = $[Q], J = $[Q + 1], U = $[Z - 1], W = $[Z];
  if (!q || !W)
    return;
  switch (K) {
    case 1:
      if (J)
        $[Q] = J, $[Q + 1] = q;
      break;
    case 2:
      if (U)
        $[Z] = U, $[Z - 1] = W;
      break;
    case 3:
      $[Q] = W, $[Z] = q;
      break;
    case 4:
      if (J && U) {
        let H = $.slice(Q, Q + 2), [_, N] = H, V = $[Q + 2];
        if (_ && N && V)
          $[Q] = V, $[Q + 1] = _, $[Q + 2] = N;
      }
      break;
    case 5:
      if (J && U) {
        let H = $.slice(Q, Q + 2), [_, N] = H, V = $[Q + 2];
        if (_ && N && V)
          $[Q] = V, $[Q + 1] = N, $[Q + 2] = _;
      }
      break;
    case 6:
      if (U && J) {
        let H = $.slice(Z - 1, Z + 1), [_, N] = H, V = $[Z - 2];
        if (_ && N && V)
          $[Z] = V, $[Z - 1] = N, $[Z - 2] = _;
      }
      break;
    case 7:
      if (U && J) {
        let H = $.slice(Z - 1, Z + 1), [_, N] = H, V = $[Z - 2];
        if (_ && N && V)
          $[Z] = V, $[Z - 1] = _, $[Z - 2] = N;
      }
      break;
    case 8:
      if (U) {
        let H = q;
        $[Q] = U, $[Z - 1] = W, $[Z] = H;
      }
      break;
    case 9:
      if (U) {
        let H = q;
        $[Q] = W, $[Z - 1] = U, $[Z] = H;
      }
      break;
    case 10:
      if (J && U) {
        let H = q, _ = J, N = U;
        $[Q] = W, $[Q + 1] = N, $[Z - 1] = H, $[Z] = _;
      }
      break;
    case 11:
      if (J && U) {
        let H = q, _ = J, N = U;
        $[Q] = W, $[Q + 1] = N, $[Z - 1] = _, $[Z] = H;
      }
      break;
    case 12:
      if (J && U) {
        let H = [q, J];
        $[Q] = U, $[Q + 1] = W, $[Z - 1] = H[0], $[Z] = H[1];
      }
      break;
    case 13:
      if (J && U) {
        let H = [q, J];
        $[Q] = U, $[Q + 1] = W, $[Z - 1] = H[1], $[Z] = H[0];
      }
      break;
    case 14:
      if (J && U) {
        let H = [q, J];
        $[Q] = W, $[Q + 1] = U, $[Z - 1] = H[0], $[Z] = H[1];
      }
      break;
    case 15:
      if (J && U) {
        let H = [q, J];
        $[Q] = W, $[Q + 1] = U, $[Z - 1] = H[1], $[Z] = H[0];
      }
      break;
  }
}
function x5($, Q) {
  let { stateTable: Z, substitutionTable: K } = $, q = 0, J = -1;
  for (let U = 0;U <= Q.length; U++) {
    let W = U >= Q.length, H = W ? 0 : R0(Z.classTable, Q[U]?.glyphId), _ = Z.stateArray[q];
    if (!_)
      break;
    let N = _[H];
    if (!N)
      break;
    if (N.flags & 32768)
      J = U;
    if (N.markIndex !== 65535 && J >= 0 && J < Q.length) {
      let V = K[N.markIndex];
      if (V) {
        let X = Q[J];
        if (X) {
          let z = V.get(X.glyphId);
          if (z !== undefined)
            X.glyphId = z;
        }
      }
    }
    if (!W && N.currentIndex !== 65535) {
      let V = K[N.currentIndex];
      if (V) {
        let X = Q[U];
        if (X) {
          let z = V.get(X.glyphId);
          if (z !== undefined)
            X.glyphId = z;
        }
      }
    }
    if (!(N.flags & 16384))
      ;
    q = N.newState;
  }
}
function f5($, Q) {
  let { stateTable: Z, ligatureActions: K, components: q, ligatures: J } = $, U = 0, W = [], H = [], _ = new Set;
  for (let N = 0;N <= Q.length; N++) {
    let X = N >= Q.length ? 0 : R0(Z.classTable, Q[N]?.glyphId), z = Z.stateArray[U];
    if (!z)
      break;
    let G = z[X];
    if (!G)
      break;
    if (G.flags & 32768)
      W.push(N);
    if (G.flags & 8192 && G.ligActionIndex < K.length) {
      let Y = G.ligActionIndex, j = 0, E = [];
      while (Y < K.length) {
        let w = K[Y];
        if (w === undefined)
          break;
        let L = (w & 2147483648) !== 0, A = (w & 1073741824) !== 0, D = (w & 1073741823) << 2 >> 2, F = W.pop();
        if (F !== undefined && F < Q.length) {
          if (E.push(F), Q[F]) {
            let R = D;
            if (R >= 0 && R < q.length) {
              let P = q[R];
              if (P !== undefined)
                j = j + P;
            }
          }
        }
        if (A && j < J.length) {
          let M = E[E.length - 1];
          if (M !== undefined && M < Q.length) {
            let R = Q[M], P = J[j];
            if (R && P !== undefined) {
              R.glyphId = P;
              for (let T = 0;T < E.length; T++)
                if (T < E.length - 1) {
                  let m = E[T];
                  _.add(m);
                }
            }
          }
          j = 0;
        }
        if (L)
          break;
        Y++;
      }
    }
    if (!(G.flags & 16384))
      ;
    U = G.newState;
  }
  for (let N = 0;N < Q.length; N++)
    if (!_.has(N)) {
      let V = Q[N];
      H.push(V);
    }
  return H;
}
function l5($, Q) {
  let { stateTable: Z, insertionGlyphs: K } = $, q = 0, J = -1, U = [], W = new Map;
  for (let H = 0;H <= Q.length; H++) {
    let _ = H >= Q.length, N = _ ? 0 : R0(Z.classTable, Q[H]?.glyphId), V = Z.stateArray[q];
    if (!V)
      break;
    let X = V[N];
    if (!X)
      break;
    if (X.flags & 32768)
      J = H;
    if (X.markedInsertIndex !== 65535 && J >= 0) {
      let z = X.flags >> 5 & 31, G = (X.flags & 2048) !== 0, Y = K.slice(X.markedInsertIndex, X.markedInsertIndex + z), j = W.get(J);
      if (!j)
        j = { before: [], after: [] }, W.set(J, j);
      if (G)
        j.before.push(...Y);
      else
        j.after.push(...Y);
    }
    if (!_ && X.currentInsertIndex !== 65535) {
      let z = X.flags & 31, G = (X.flags & 32) !== 0, Y = K.slice(X.currentInsertIndex, X.currentInsertIndex + z), j = W.get(H);
      if (!j)
        j = { before: [], after: [] }, W.set(H, j);
      if (G)
        j.before.push(...Y);
      else
        j.after.push(...Y);
    }
    if (!(X.flags & 16384))
      ;
    q = X.newState;
  }
  for (let H = 0;H < Q.length; H++) {
    let _ = Q[H], N = W.get(H);
    if (N)
      for (let V = 0;V < N.before.length; V++) {
        let X = N.before[V];
        U.push({ glyphId: X, cluster: _.cluster, mask: _.mask, codepoint: 0 });
      }
    if (U.push(_), N)
      for (let V = 0;V < N.after.length; V++) {
        let X = N.after[V];
        U.push({ glyphId: X, cluster: _.cluster, mask: _.mask, codepoint: 0 });
      }
  }
  return U;
}
function PQ($, Q, Z) {
  if (Z === 0)
    return ($ ^ Q) < 0 ? -2147483647 : 2147483647;
  let K = 1;
  if ($ < 0)
    $ = -$, K = -K;
  if (Q < 0)
    Q = -Q, K = -K;
  if (Z < 0)
    Z = -Z, K = -K;
  let q = Math.floor(($ * Q + (Z >> 1)) / Z);
  return K < 0 ? -q : q;
}
function IQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "ADD: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z + Q;
}
function vQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "SUB: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z - Q;
}
function kQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "DIV: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  if (Q === 0) {
    $.error = "DIV: division by zero", $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = PQ(Z, 64, Q);
}
function CQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "MUL: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = PQ(Z, Q, 64);
}
function SQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "ABS: stack underflow";
    return;
  }
  $.stack[$.stackTop - 1] = Q < 0 ? -Q : Q;
}
function hQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "NEG: stack underflow";
    return;
  }
  $.stack[$.stackTop - 1] = -Q;
}
function yQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "FLOOR: stack underflow";
    return;
  }
  $.stack[$.stackTop - 1] = Q & -64;
}
function bQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "CEILING: stack underflow";
    return;
  }
  $.stack[$.stackTop - 1] = Q + 63 & -64;
}
function mQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "MAX: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z > Q ? Z : Q;
}
function gQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "MIN: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z < Q ? Z : Q;
}
function uQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "LT: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z < Q ? 1 : 0;
}
function xQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "LTEQ: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z <= Q ? 1 : 0;
}
function fQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "GT: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z > Q ? 1 : 0;
}
function lQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "GTEQ: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z >= Q ? 1 : 0;
}
function dQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "EQ: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z === Q ? 1 : 0;
}
function nQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "NEQ: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z !== Q ? 1 : 0;
}
function pQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "ODD: stack underflow";
    return;
  }
  let Z = Q + 32 & -64;
  $.stack[$.stackTop - 1] = Z & 64 ? 1 : 0;
}
function cQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "EVEN: stack underflow";
    return;
  }
  let Z = Q + 32 & -64;
  $.stack[$.stackTop - 1] = Z & 64 ? 0 : 1;
}
function oQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "AND: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z && Q ? 1 : 0;
}
function iQ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z === undefined || Q === undefined) {
    $.error = "OR: stack underflow", $.stackTop += 2, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = Z || Q ? 1 : 0;
}
function sQ($) {
  let Q = $.stack[$.stackTop - 1];
  if (Q === undefined) {
    $.error = "NOT: stack underflow";
    return;
  }
  $.stack[$.stackTop - 1] = Q ? 0 : 1;
}
function rQ($) {
  let Q = $.stack[--$.stackTop];
  if (Q === undefined) {
    $.error = "IF: stack underflow", $.stackTop++;
    return;
  }
  if (Q)
    return;
  let Z = 1;
  while ($.IP < $.codeSize) {
    let K = $.code[$.IP++];
    if (K === undefined)
      break;
    switch (K) {
      case 88:
        Z++;
        break;
      case 27:
        if (Z === 1)
          return;
        break;
      case 89:
        if (Z--, Z === 0)
          return;
        break;
      case 64:
        $.IP += 1 + ($.code[$.IP] ?? 0);
        break;
      case 65:
        $.IP += 1 + ($.code[$.IP] ?? 0) * 2;
        break;
      default:
        if (K >= 176 && K <= 183)
          $.IP += K - 176 + 1;
        else if (K >= 184 && K <= 191)
          $.IP += (K - 184 + 1) * 2;
    }
  }
  $.error = "IF: missing EIF";
}
function aQ($) {
  let Q = 1;
  while ($.IP < $.codeSize) {
    let Z = $.code[$.IP++];
    if (Z === undefined)
      break;
    switch (Z) {
      case 88:
        Q++;
        break;
      case 89:
        if (Q--, Q === 0)
          return;
        break;
      case 64:
        $.IP += 1 + ($.code[$.IP] ?? 0);
        break;
      case 65:
        $.IP += 1 + ($.code[$.IP] ?? 0) * 2;
        break;
      default:
        if (Z >= 176 && Z <= 183)
          $.IP += Z - 176 + 1;
        else if (Z >= 184 && Z <= 191)
          $.IP += (Z - 184 + 1) * 2;
    }
  }
  $.error = "ELSE: missing EIF";
}
function tQ($) {
}
function eQ($) {
  let Q = $.stack[--$.stackTop];
  if (Q === undefined) {
    $.error = "JMPR: stack underflow", $.stackTop++;
    return;
  }
  $.IP += Q - 1;
}
function $Z($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Q === undefined || Z === undefined) {
    $.error = "JROT: stack underflow", $.stackTop += 2;
    return;
  }
  if (Q)
    $.IP += Z - 1;
}
function QZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Q === undefined || Z === undefined) {
    $.error = "JROF: stack underflow", $.stackTop += 2;
    return;
  }
  if (!Q)
    $.IP += Z - 1;
}
function ZZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q === undefined) {
    $.error = "FDEF: stack underflow", $.stackTop++;
    return;
  }
  if (Q < 0 || Q > 65535) {
    $.error = `FDEF: invalid function number ${Q}`;
    return;
  }
  let Z = $.FDefs.find((K) => K.active && K.id === Q);
  if (!Z)
    Z = $.FDefs.find((K) => !K.active);
  if (!Z) {
    $.error = "FDEF: too many function definitions";
    return;
  }
  Z.id = Q, Z.start = $.IP, Z.active = true, Z.range = $.currentRange;
  while ($.IP < $.codeSize) {
    let K = $.code[$.IP++];
    if (K === undefined)
      break;
    if (K === 45) {
      Z.end = $.IP;
      return;
    }
    if (K === 64)
      $.IP += 1 + ($.code[$.IP] ?? 0);
    else if (K === 65)
      $.IP += 1 + ($.code[$.IP] ?? 0) * 2;
    else if (K >= 176 && K <= 183)
      $.IP += K - 176 + 1;
    else if (K >= 184 && K <= 191)
      $.IP += (K - 184 + 1) * 2;
  }
  $.error = "FDEF: missing ENDF";
}
function KZ($) {
  if ($.callStackTop <= 0) {
    $.error = "ENDF: not in function call";
    return;
  }
  let Q = $.callStack[$.callStackTop - 1];
  if (!Q) {
    $.error = "ENDF: missing call frame";
    return;
  }
  if (Q.count--, Q.count > 0)
    $.IP = Q.def.start;
  else {
    $.callStackTop--, $.IP = Q.callerIP, $.currentRange = Q.callerRange;
    let Z = $.codeRanges.get($.currentRange);
    if (Z)
      $.code = Z.code, $.codeSize = Z.size;
  }
}
function qZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q === undefined) {
    $.error = "CALL: stack underflow", $.stackTop++;
    return;
  }
  if (Q < 0 || Q > 65535) {
    $.error = `CALL: invalid function number ${Q}`;
    return;
  }
  let Z = $.FDefs.find((J) => J.active && J.id === Q);
  if (!Z) {
    $.error = `CALL: function ${Q} not defined`;
    return;
  }
  if ($.callStackTop >= $.maxCallStack) {
    $.error = "CALL: call stack overflow";
    return;
  }
  let K = $.callStack[$.callStackTop++];
  if (!K) {
    $.error = "CALL: no call frame available", $.callStackTop--;
    return;
  }
  K.callerIP = $.IP, K.callerRange = $.currentRange, K.def = Z, K.count = 1, $.currentRange = Z.range;
  let q = $.codeRanges.get($.currentRange);
  if (q)
    $.code = q.code, $.codeSize = q.size;
  $.IP = Z.start;
}
function JZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Q === undefined || Z === undefined) {
    $.error = "LOOPCALL: stack underflow", $.stackTop += 2;
    return;
  }
  if (Q < 0 || Q > 65535) {
    $.error = `LOOPCALL: invalid function number ${Q}`;
    return;
  }
  let K = $.FDefs.find((U) => U.active && U.id === Q);
  if (!K) {
    $.error = `LOOPCALL: function ${Q} not defined`;
    return;
  }
  if (Z <= 0)
    return;
  if ($.callStackTop >= $.maxCallStack) {
    $.error = "LOOPCALL: call stack overflow";
    return;
  }
  let q = $.callStack[$.callStackTop++];
  if (!q) {
    $.error = "LOOPCALL: no call frame available", $.callStackTop--;
    return;
  }
  q.callerIP = $.IP, q.callerRange = $.currentRange, q.def = K, q.count = Z, $.currentRange = K.range;
  let J = $.codeRanges.get($.currentRange);
  if (J)
    $.code = J.code, $.codeSize = J.size;
  $.IP = K.start;
}
function UZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q === undefined) {
    $.error = "IDEF: stack underflow", $.stackTop++;
    return;
  }
  if (Q < 0 || Q >= $.maxIDefs) {
    $.error = `IDEF: invalid opcode ${Q}`;
    return;
  }
  let Z = $.IDefs[Q];
  if (!Z) {
    $.error = `IDEF: no slot for opcode ${Q}`;
    return;
  }
  Z.opcode = Q, Z.start = $.IP, Z.active = true, Z.range = $.currentRange;
  while ($.IP < $.codeSize) {
    let K = $.code[$.IP++];
    if (K === undefined)
      break;
    if (K === 45) {
      Z.end = $.IP;
      return;
    }
    if (K === 64)
      $.IP += 1 + ($.code[$.IP] ?? 0);
    else if (K === 65)
      $.IP += 1 + ($.code[$.IP] ?? 0) * 2;
    else if (K >= 176 && K <= 183)
      $.IP += K - 176 + 1;
    else if (K >= 184 && K <= 191)
      $.IP += (K - 184 + 1) * 2;
  }
  $.error = "IDEF: missing ENDF";
}
var s8;
((H) => {
  H[H.ToHalfGrid = 0] = "ToHalfGrid";
  H[H.ToGrid = 1] = "ToGrid";
  H[H.ToDoubleGrid = 2] = "ToDoubleGrid";
  H[H.DownToGrid = 3] = "DownToGrid";
  H[H.UpToGrid = 4] = "UpToGrid";
  H[H.Off = 5] = "Off";
  H[H.Super = 6] = "Super";
  H[H.Super45 = 7] = "Super45";
})(s8 ||= {});
var k8;
((K) => {
  K[K.X = 16] = "X";
  K[K.Y = 32] = "Y";
  K[K.Both = 48] = "Both";
})(k8 ||= {});
function d5() {
  return { rp0: 0, rp1: 0, rp2: 0, dualVector: { x: 16384, y: 0 }, projVector: { x: 16384, y: 0 }, freeVector: { x: 16384, y: 0 }, loop: 1, minimumDistance: 64, roundState: 1, autoFlip: true, controlValueCutIn: 68, singleWidthCutIn: 0, singleWidthValue: 0, deltaBase: 9, deltaShift: 3, instructControl: 0, scanControl: 0, scanType: 0, gep0: 1, gep1: 1, gep2: 1, period: 64, phase: 0, threshold: 32 };
}
var r8;
((q) => {
  q[q.None = 0] = "None";
  q[q.Font = 1] = "Font";
  q[q.CVT = 2] = "CVT";
  q[q.Glyph = 3] = "Glyph";
})(r8 ||= {});
function J8($, Q) {
  return { nPoints: 0, nContours: 0, org: Array($).fill(null).map(() => ({ x: 0, y: 0 })), cur: Array($).fill(null).map(() => ({ x: 0, y: 0 })), orus: Array($).fill(null).map(() => ({ x: 0, y: 0 })), tags: new Uint8Array($), contours: new Uint16Array(Q) };
}
function M0($ = 256, Q = 64, Z = 64, K = 64, q = 32, J = 16) {
  let U = d5();
  return { GS: { ...U }, defaultGS: U, zp0: J8(0, 0), zp1: J8(0, 0), zp2: J8(0, 0), twilight: J8(J, 1), pts: J8(0, 0), stack: new Int32Array($), stackTop: 0, IP: 0, code: new Uint8Array(0), codeSize: 0, currentRange: 0, opcode: 0, numArgs: 0, cvt: new Int32Array(0), cvtSize: 0, storage: new Int32Array(Q), storageSize: Q, FDefs: Array(Z).fill(null).map((W, H) => ({ id: H, start: 0, end: 0, active: false, range: 0 })), maxFDefs: Z, IDefs: Array(K).fill(null).map((W, H) => ({ opcode: H, start: 0, end: 0, active: false, range: 0 })), maxIDefs: K, callStack: Array(q).fill(null).map(() => ({ callerIP: 0, callerRange: 0, def: { id: 0, start: 0, end: 0, active: false, range: 0 }, count: 0 })), callStackTop: 0, maxCallStack: q, codeRanges: new Map, ppem: 12, pointSize: 12, scale: 1, scaleFix: 65536, lightMode: false, grayscale: true, renderMode: "gray", backwardCompatibility: 0, isComposite: false, error: null, instructionCount: 0, maxInstructions: 1e6 };
}
var B = { NPUSHB: 64, NPUSHW: 65, PUSHB_0: 176, PUSHB_1: 177, PUSHB_2: 178, PUSHB_3: 179, PUSHB_4: 180, PUSHB_5: 181, PUSHB_6: 182, PUSHB_7: 183, PUSHW_0: 184, PUSHW_1: 185, PUSHW_2: 186, PUSHW_3: 187, PUSHW_4: 188, PUSHW_5: 189, PUSHW_6: 190, PUSHW_7: 191, RS: 67, WS: 66, RCVT: 69, WCVTP: 68, WCVTF: 112, DUP: 32, POP: 33, CLEAR: 34, SWAP: 35, DEPTH: 36, CINDEX: 37, MINDEX: 38, ROLL: 138, ADD: 96, SUB: 97, DIV: 98, MUL: 99, ABS: 100, NEG: 101, FLOOR: 102, CEILING: 103, MAX: 139, MIN: 140, LT: 80, LTEQ: 81, GT: 82, GTEQ: 83, EQ: 84, NEQ: 85, ODD: 86, EVEN: 87, AND: 90, OR: 91, NOT: 92, IF: 88, ELSE: 27, EIF: 89, JMPR: 28, JROT: 120, JROF: 121, FDEF: 44, ENDF: 45, CALL: 43, LOOPCALL: 42, IDEF: 137, SVTCA_Y: 0, SVTCA_X: 1, SPVTCA_Y: 2, SPVTCA_X: 3, SFVTCA_Y: 4, SFVTCA_X: 5, SPVTL_0: 6, SPVTL_1: 7, SFVTL_0: 8, SFVTL_1: 9, SDPVTL_0: 134, SDPVTL_1: 135, SPVFS: 10, SFVFS: 11, GPV: 12, GFV: 13, SFVTPV: 14, ISECT: 15, SRP0: 16, SRP1: 17, SRP2: 18, SZP0: 19, SZP1: 20, SZP2: 21, SZPS: 22, SLOOP: 23, RTG: 24, RTHG: 25, SMD: 26, RDTG: 125, RUTG: 124, ROFF: 122, SROUND: 118, S45ROUND: 119, SCVTCI: 29, SSWCI: 30, SSW: 31, FLIPON: 77, FLIPOFF: 78, SANGW: 126, SDB: 94, SDS: 95, GC_0: 70, GC_1: 71, SCFS: 72, MD_0: 73, MD_1: 74, MPPEM: 75, MPS: 76, FLIPPT: 128, FLIPRGON: 129, FLIPRGOFF: 130, SHP_0: 50, SHP_1: 51, SHC_0: 52, SHC_1: 53, SHZ_0: 54, SHZ_1: 55, SHPIX: 56, IP: 57, MSIRP_0: 58, MSIRP_1: 59, ALIGNRP: 60, RTDG: 61, MIAP_0: 62, MIAP_1: 63, ALIGNPTS: 39, UTP: 41, MDAP_0: 46, MDAP_1: 47, IUP_Y: 48, IUP_X: 49, DELTAP1: 93, DELTAP2: 113, DELTAP3: 114, DELTAC1: 115, DELTAC2: 116, DELTAC3: 117, ROUND_0: 104, ROUND_1: 105, ROUND_2: 106, ROUND_3: 107, NROUND_0: 108, NROUND_1: 109, NROUND_2: 110, NROUND_3: 111, GETINFO: 136, INSTCTRL: 142, SCANCTRL: 133, SCANTYPE: 141, AA: 127, DEBUG: 79, MDRP_BASE: 192, MIRP_BASE: 224 };
var qY = { [B.RS]: 1, [B.WS]: 2, [B.RCVT]: 1, [B.WCVTP]: 2, [B.WCVTF]: 2, [B.DUP]: 1, [B.POP]: 1, [B.CLEAR]: 0, [B.SWAP]: 2, [B.DEPTH]: 0, [B.CINDEX]: 1, [B.MINDEX]: 1, [B.ROLL]: 3, [B.ADD]: 2, [B.SUB]: 2, [B.DIV]: 2, [B.MUL]: 2, [B.ABS]: 1, [B.NEG]: 1, [B.FLOOR]: 1, [B.CEILING]: 1, [B.MAX]: 2, [B.MIN]: 2, [B.LT]: 2, [B.LTEQ]: 2, [B.GT]: 2, [B.GTEQ]: 2, [B.EQ]: 2, [B.NEQ]: 2, [B.ODD]: 1, [B.EVEN]: 1, [B.AND]: 2, [B.OR]: 2, [B.NOT]: 1, [B.IF]: 1, [B.JMPR]: 1, [B.JROT]: 2, [B.JROF]: 2, [B.CALL]: 1, [B.LOOPCALL]: 2, [B.SRP0]: 1, [B.SRP1]: 1, [B.SRP2]: 1, [B.SZP0]: 1, [B.SZP1]: 1, [B.SZP2]: 1, [B.SZPS]: 1, [B.SLOOP]: 1, [B.SMD]: 1, [B.SCVTCI]: 1, [B.SSWCI]: 1, [B.SSW]: 1, [B.SDB]: 1, [B.SDS]: 1, [B.SPVFS]: 2, [B.SFVFS]: 2, [B.SPVTL_0]: 2, [B.SPVTL_1]: 2, [B.SFVTL_0]: 2, [B.SFVTL_1]: 2, [B.SCFS]: 2, [B.GC_0]: 1, [B.GC_1]: 1, [B.MD_0]: 2, [B.MD_1]: 2, [B.ISECT]: 5, [B.ALIGNRP]: 0, [B.IP]: 0, [B.SHPIX]: 1, [B.MSIRP_0]: 2, [B.MSIRP_1]: 2, [B.MIAP_0]: 2, [B.MIAP_1]: 2, [B.MDAP_0]: 1, [B.MDAP_1]: 1, [B.DELTAP1]: 1, [B.DELTAP2]: 1, [B.DELTAP3]: 1, [B.DELTAC1]: 1, [B.DELTAC2]: 1, [B.DELTAC3]: 1, [B.SROUND]: 1, [B.S45ROUND]: 1, [B.ROUND_0]: 1, [B.ROUND_1]: 1, [B.ROUND_2]: 1, [B.ROUND_3]: 1, [B.NROUND_0]: 1, [B.NROUND_1]: 1, [B.NROUND_2]: 1, [B.NROUND_3]: 1, [B.INSTCTRL]: 2, [B.SCANCTRL]: 1, [B.SCANTYPE]: 1, [B.GETINFO]: 1, [B.FLIPPT]: 0, [B.FLIPRGON]: 2, [B.FLIPRGOFF]: 2 };
function U8($, Q) {
  if ($ >= 0) {
    let Z = $ + Q + 32 & -64;
    return Z < 0 ? 0 : Z;
  } else {
    let Z = -(-$ + Q + 32 & -64);
    return Z > 0 ? 0 : Z;
  }
}
function n5($, Q) {
  if ($ >= 0) {
    let Z = ($ + Q & -64) + 32;
    return Z < 0 ? 32 : Z;
  } else {
    let Z = -((-$ + Q & -64) + 32);
    return Z > 0 ? -32 : Z;
  }
}
function p5($, Q) {
  if ($ >= 0) {
    let Z = $ + Q + 16 & -32;
    return Z < 0 ? 0 : Z;
  } else {
    let Z = -(-$ + Q + 16 & -32);
    return Z > 0 ? 0 : Z;
  }
}
function c5($, Q) {
  if ($ >= 0) {
    let Z = $ + Q & -64;
    return Z < 0 ? 0 : Z;
  } else {
    let Z = -(-$ + Q & -64);
    return Z > 0 ? 0 : Z;
  }
}
function o5($, Q) {
  if ($ >= 0) {
    let Z = $ + Q + 63 & -64;
    return Z < 0 ? 0 : Z;
  } else {
    let Z = -(-$ + Q + 63 & -64);
    return Z > 0 ? 0 : Z;
  }
}
function i5($, Q) {
  return $;
}
function s5($, Q, Z) {
  let { period: K, phase: q, threshold: J } = Z;
  if ($ >= 0)
    return ($ + J - q + Q & -K) + q;
  else
    return -((-$ + J - q + Q & -K) + q);
}
function r5($, Q, Z) {
  let { period: K, phase: q, threshold: J } = Z, U = Math.round(K * 46 / 64);
  if ($ >= 0)
    return ($ + J - q + Q & -U) + q;
  else
    return -((-$ + J - q + Q & -U) + q);
}
function E8($, Q, Z) {
  switch (Z.roundState) {
    case 1:
      return U8($, Q);
    case 0:
      return n5($, Q);
    case 2:
      return p5($, Q);
    case 3:
      return c5($, Q);
    case 4:
      return o5($, Q);
    case 5:
      return i5($, Q);
    case 6:
      return s5($, Q, Z);
    case 7:
      return r5($, Q, Z);
    default:
      return U8($, Q);
  }
}
function v9($, Q) {
  switch ($ >> 6 & 3) {
    case 0:
      Q.period = 32;
      break;
    case 1:
      Q.period = 64;
      break;
    case 2:
      Q.period = 128;
      break;
    default:
      Q.period = 64;
  }
  switch ($ >> 4 & 3) {
    case 0:
      Q.phase = 0;
      break;
    case 1:
      Q.phase = Q.period >> 2;
      break;
    case 2:
      Q.phase = Q.period >> 1;
      break;
    case 3:
      Q.phase = Q.period * 3 >> 2;
      break;
  }
  let Z = $ & 15;
  if (Z === 0)
    Q.threshold = Q.period - 1;
  else
    Q.threshold = (Z - 4) * Q.period >> 3;
}
function W8($, Q) {
  return 0;
}
function a5($, Q) {
  if ($ === 0 || Q === 0)
    return 0;
  let Z = 1, K = $, q = Q;
  if (K < 0)
    K = -K, Z = -Z;
  if (q < 0)
    q = -q, Z = -Z;
  let J = Math.floor((K * q + 32768) / 65536);
  return Z < 0 ? -J : J;
}
function WZ($, Q) {
  if (Q === 0)
    return $ < 0 ? -2147483647 : 2147483647;
  let Z = 1, K = $, q = Q;
  if (K < 0)
    K = -K, Z = -Z;
  if (q < 0)
    q = -q, Z = -Z;
  let J = Math.floor((K * 65536 + (q >> 1)) / q);
  return Z < 0 ? -J : J;
}
function h6($, Q) {
  return a5($, Q);
}
function _Z($, Q, Z, K) {
  let q = $ * Z + Q * K, J = q + 8192 + (q < 0 ? -1 : 0);
  return Math.floor(J / 16384);
}
function T0($, Q) {
  let Z = $ * Q, K = Z + 8192 + (Z < 0 ? -1 : 0);
  return Math.floor(K / 16384);
}
function TH($, Q, Z, K) {
  let q = $ * Z + Q * K, J = q + 8192 + (q < 0 ? -1 : 0);
  return Math.floor(J / 16384);
}
function o6($, Q, Z) {
  if (Z === 0)
    return ($ ^ Q) < 0 ? -2147483647 : 2147483647;
  let K = 1;
  if ($ < 0)
    $ = -$, K = -K;
  if (Q < 0)
    Q = -Q, K = -K;
  if (Z < 0)
    Z = -Z, K = -K;
  let q = Math.floor(($ * Q + (Z >> 1)) / Z);
  return K < 0 ? -q : q;
}
function O0($, Q) {
  return _Z(Q.x, Q.y, $.GS.projVector.x, $.GS.projVector.y);
}
function NZ($, Q) {
  return _Z(Q.x, Q.y, $.GS.dualVector.x, $.GS.dualVector.y);
}
function P6($, Q, Z, K) {
  let q = Q.cur[Z], J = $.GS.freeVector, U = $.GS.projVector, W = TH(J.x, J.y, U.x, U.y);
  if (W === 0)
    return;
  let H = o6(K, J.x, W), _ = o6(K, J.y, W), N = H, V = _;
  if ($.backwardCompatibility) {
    if (J.x !== 0)
      N = 0;
    if (J.y !== 0 && $.backwardCompatibility === 7)
      V = 0;
  }
  if (q.x += N, q.y += V, Q === $.twilight) {
    let X = Q.org[Z];
    X.x += N, X.y += V;
  }
}
function z6($, Q, Z) {
  let K = Q.cur[Z];
  if (!K)
    return 0;
  return O0($, K);
}
function a6($, Q, Z) {
  let K = Q.org[Z];
  if (!K)
    return 0;
  return NZ($, K);
}
function T6($, Q, Z) {
  let K = $.GS.freeVector;
  if (K.y !== 0)
    Q.tags[Z] |= 32;
  if (K.x !== 0)
    Q.tags[Z] |= 16;
}
function t5($, Q) {
  let Z = $.stack[--$.stackTop], K = $.zp0;
  if (Z < 0 || Z >= K.nPoints) {
    $.error = `MDAP: invalid point ${Z}`;
    return;
  }
  let q = z6($, K, Z);
  if (Q) {
    let J = W8(q, $.GS);
    q = E8(q, J, $.GS) - q;
  } else
    q = 0;
  P6($, K, Z, q), T6($, K, Z), $.GS.rp0 = Z, $.GS.rp1 = Z;
}
function e5($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = $.zp0;
  if (K < 0 || K >= q.nPoints) {
    $.error = `MIAP: invalid point ${K}`;
    return;
  }
  if (Z < 0 || Z >= $.cvtSize)
    return;
  let J = $.cvt[Z], U = z6($, q, K);
  if (Q) {
    if (Math.abs(J - U) > $.GS.controlValueCutIn)
      J = U;
    let _ = W8(J, $.GS);
    J = E8(J, _, $.GS);
  }
  let W = J - U;
  P6($, q, K, W), T6($, q, K), $.GS.rp0 = K, $.GS.rp1 = K;
}
function VZ($, Q) {
  let Z = $.stack[--$.stackTop], K = (Q & 16) !== 0, q = (Q & 8) !== 0, J = (Q & 4) !== 0, U = Q & 3, W = $.GS.roundState;
  if (U === 1)
    W = 1;
  else if (U === 2)
    W = 0;
  else if (U === 3)
    W = 2;
  let { zp0: H, zp1: _ } = $;
  if (Z < 0 || Z >= _.nPoints) {
    $.error = `MDRP: invalid point ${Z}`;
    return;
  }
  let N = $.GS.rp0;
  if (N < 0 || N >= H.nPoints) {
    $.error = `MDRP: invalid rp0 ${N}`;
    return;
  }
  let V = a6($, _, Z) - a6($, H, N);
  if ($.GS.singleWidthCutIn > 0 && Math.abs(V - $.GS.singleWidthValue) < $.GS.singleWidthCutIn)
    V = V >= 0 ? $.GS.singleWidthValue : -$.GS.singleWidthValue;
  let X = W8(V, $.GS), z;
  if (J) {
    let j = $.GS.roundState;
    $.GS.roundState = W, z = E8(V, X, $.GS), $.GS.roundState = j;
  } else
    z = V + X;
  if (q) {
    if (V >= 0) {
      if (z < $.GS.minimumDistance)
        z = $.GS.minimumDistance;
    } else if (z > -$.GS.minimumDistance)
      z = -$.GS.minimumDistance;
  }
  let G = z6($, _, Z) - z6($, H, N), Y = z - G;
  if (P6($, _, Z, Y), T6($, _, Z), $.GS.rp1 = $.GS.rp0, $.GS.rp2 = Z, K)
    $.GS.rp0 = Z;
}
function XZ($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = (Q & 16) !== 0, J = (Q & 8) !== 0, U = (Q & 4) !== 0, W = Q & 3, H = $.GS.roundState;
  if (W === 1)
    H = 1;
  else if (W === 2)
    H = 0;
  else if (W === 3)
    H = 2;
  let { zp0: _, zp1: N } = $;
  if (K < 0 || K >= N.nPoints) {
    $.error = `MIRP: invalid point ${K}`;
    return;
  }
  if (Z < -1 || Z >= $.cvtSize)
    return;
  let V = $.GS.rp0;
  if (V < 0 || V >= _.nPoints) {
    $.error = `MIRP: invalid rp0 ${V}`;
    return;
  }
  let X = a6($, N, K) - a6($, _, V), z = Z === -1 ? 0 : $.cvt[Z];
  if ($.GS.singleWidthCutIn > 0 && Math.abs(z - $.GS.singleWidthValue) < $.GS.singleWidthCutIn)
    z = z >= 0 ? $.GS.singleWidthValue : -$.GS.singleWidthValue;
  if ($.GS.gep1 === 0) {
    let w = $.zp0.org[V], L = T0(z, $.GS.freeVector.x), A = T0(z, $.GS.freeVector.y);
    $.zp1.org[K].x = w.x + L, $.zp1.org[K].y = w.y + A, $.zp1.cur[K].x = $.zp1.org[K].x, $.zp1.cur[K].y = $.zp1.org[K].y;
  }
  if ($.GS.autoFlip) {
    if ((X ^ z) < 0)
      z = -z;
  }
  let G = z, Y = W8(z, $.GS);
  if (U) {
    if ($.GS.gep0 === $.GS.gep1) {
      if (Math.abs(z - X) > $.GS.controlValueCutIn)
        G = X;
    }
    let w = $.GS.roundState;
    $.GS.roundState = H, G = E8(G, Y, $.GS), $.GS.roundState = w;
  } else
    G = G + Y;
  if (J) {
    if (X >= 0) {
      if (G < $.GS.minimumDistance)
        G = $.GS.minimumDistance;
    } else if (G > -$.GS.minimumDistance)
      G = -$.GS.minimumDistance;
  }
  let j = z6($, N, K) - z6($, _, V), E = G - j;
  if (P6($, N, K, E), T6($, N, K), $.GS.rp1 = $.GS.rp0, $.GS.rp2 = K, q)
    $.GS.rp0 = K;
}
function $$($, Q) {
  let Z = Q ? $.zp0 : $.zp1, K = Q ? $.GS.rp1 : $.GS.rp2;
  if (K < 0 || K >= Z.nPoints) {
    $.error = `SHP: invalid reference point ${K}`;
    return;
  }
  let q = O0($, Z.org[K]), U = z6($, Z, K) - q, W = $.zp2, H = $.GS.loop;
  $.GS.loop = 1;
  for (let _ = 0;_ < H; _++) {
    let N = $.stack[--$.stackTop];
    if (N < 0 || N >= W.nPoints) {
      $.error = `SHP: invalid point ${N}`;
      return;
    }
    P6($, W, N, U), T6($, W, N);
  }
}
function Q$($, Q) {
  let Z = $.stack[--$.stackTop], K = Q ? $.zp0 : $.zp1, q = Q ? $.GS.rp1 : $.GS.rp2;
  if (q < 0 || q >= K.nPoints) {
    $.error = `SHC: invalid reference point ${q}`;
    return;
  }
  let J = $.zp2;
  if (Z < 0 || Z >= J.nContours) {
    $.error = `SHC: invalid contour ${Z}`;
    return;
  }
  let U = O0($, K.org[q]), H = z6($, K, q) - U, _ = Z === 0 ? 0 : J.contours[Z - 1] + 1, N = J.contours[Z];
  for (let V = _;V <= N; V++) {
    if (J === K && V === q)
      continue;
    P6($, J, V, H), T6($, J, V);
  }
}
function Z$($, Q) {
  let Z = $.stack[--$.stackTop], K = Q ? $.zp0 : $.zp1, q = Q ? $.GS.rp1 : $.GS.rp2;
  if (q < 0 || q >= K.nPoints) {
    $.error = `SHZ: invalid reference point ${q}`;
    return;
  }
  let J = Z === 0 ? $.twilight : $.pts, U = O0($, K.org[q]), H = z6($, K, q) - U;
  for (let _ = 0;_ < J.nPoints; _++) {
    if (J === K && _ === q)
      continue;
    P6($, J, _, H);
  }
}
function HZ($, Q, Z, K, q) {
  let J = $.GS.freeVector;
  if (J.x !== 0) {
    if (!$.backwardCompatibility)
      Q.cur[Z].x += K;
  }
  if (J.y !== 0) {
    if ($.backwardCompatibility !== 7)
      Q.cur[Z].y += q;
  }
  if (Q === $.twilight) {
    let U = Q.org[Z];
    if (J.x !== 0 && !$.backwardCompatibility)
      U.x += K;
    if (J.y !== 0 && $.backwardCompatibility !== 7)
      U.y += q;
  }
}
function zZ($) {
  let Q = $.stack[--$.stackTop], Z = $.zp2, K = $.GS.loop;
  $.GS.loop = 1;
  let q = $.GS.gep0 === 0 || $.GS.gep1 === 0 || $.GS.gep2 === 0, J = T0(Q, $.GS.freeVector.x), U = T0(Q, $.GS.freeVector.y);
  for (let W = 0;W < K; W++) {
    let H = $.stack[--$.stackTop];
    if (H < 0 || H >= Z.nPoints) {
      $.error = `SHPIX: invalid point ${H}`;
      return;
    }
    if ($.backwardCompatibility) {
      if (q || $.backwardCompatibility !== 7 && ($.isComposite && $.GS.freeVector.y !== 0 || Z.tags[H] & 32))
        HZ($, Z, H, 0, U), T6($, Z, H);
    } else
      HZ($, Z, H, J, U), T6($, Z, H);
  }
}
function GZ($) {
  let Q = $.GS.rp1, Z = $.GS.rp2;
  if (Q < 0 || Q >= $.zp0.nPoints) {
    $.error = `IP: invalid rp1 ${Q}`;
    return;
  }
  if (Z < 0 || Z >= $.zp1.nPoints) {
    $.error = `IP: invalid rp2 ${Z}`;
    return;
  }
  let K = a6($, $.zp1, Z) - a6($, $.zp0, Q), q = z6($, $.zp1, Z) - z6($, $.zp0, Q), J = a6($, $.zp0, Q), U = z6($, $.zp0, Q), W = $.zp2, H = $.GS.loop;
  $.GS.loop = 1;
  for (let _ = 0;_ < H; _++) {
    let N = $.stack[--$.stackTop];
    if (N < 0 || N >= W.nPoints) {
      $.error = `IP: invalid point ${N}`;
      return;
    }
    let V = a6($, W, N) - J, X = z6($, W, N) - U, z;
    if (V !== 0)
      if (K !== 0)
        z = o6(V, q, K);
      else
        z = V;
    else
      z = 0;
    P6($, W, N, z - X), T6($, W, N);
  }
}
function YZ($) {
  let Q = $.GS.rp0;
  if (Q < 0 || Q >= $.zp0.nPoints) {
    $.error = `ALIGNRP: invalid rp0 ${Q}`;
    return;
  }
  let Z = z6($, $.zp0, Q), K = $.zp1, q = $.GS.loop;
  $.GS.loop = 1;
  for (let J = 0;J < q; J++) {
    let U = $.stack[--$.stackTop];
    if (U < 0 || U >= K.nPoints) {
      $.error = `ALIGNRP: invalid point ${U}`;
      return;
    }
    let W = z6($, K, U), H = Z - W;
    P6($, K, U, H), T6($, K, U);
  }
}
function K$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = $.zp0, J = $.zp1;
  if (K < 0 || K >= J.nPoints) {
    $.error = `MSIRP: invalid point ${K}`;
    return;
  }
  let U = $.GS.rp0;
  if (U < 0 || U >= q.nPoints) {
    $.error = `MSIRP: invalid rp0 ${U}`;
    return;
  }
  let W = z6($, J, K) - z6($, q, U), H = Z - W;
  if (P6($, J, K, H), T6($, J, K), $.GS.rp1 = $.GS.rp0, $.GS.rp2 = K, Q)
    $.GS.rp0 = K;
}
function jZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = $.stack[--$.stackTop], J = $.stack[--$.stackTop], U = $.zp0, W = $.zp1, H = $.zp2;
  if (q < 0 || q >= W.nPoints || K < 0 || K >= W.nPoints) {
    $.error = "ISECT: invalid line A points";
    return;
  }
  if (Z < 0 || Z >= U.nPoints || Q < 0 || Q >= U.nPoints) {
    $.error = "ISECT: invalid line B points";
    return;
  }
  if (J < 0 || J >= H.nPoints) {
    $.error = `ISECT: invalid point ${J}`;
    return;
  }
  let _ = W.cur[q], N = W.cur[K], V = U.cur[Z], X = U.cur[Q], z = X.x - V.x, G = X.y - V.y, Y = N.x - _.x, j = N.y - _.y, E = V.x - _.x, w = V.y - _.y, L = o6(Y, -G, 64) + o6(j, z, 64), A = o6(Y, z, 64) + o6(j, G, 64), D = H.cur[J];
  if (Math.abs(L) * 19 > Math.abs(A) && L !== 0) {
    let F = o6(E, -G, 64) + o6(w, z, 64), M = o6(F, Y, L), R = o6(F, j, L);
    D.x = _.x + M, D.y = _.y + R;
  } else
    D.x = _.x + N.x + V.x + X.x >> 2, D.y = _.y + N.y + V.y + X.y >> 2;
  H.tags[J] |= 48;
}
function EZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = $.zp0, q = $.zp1;
  if (Z < 0 || Z >= K.nPoints) {
    $.error = `ALIGNPTS: invalid point ${Z}`;
    return;
  }
  if (Q < 0 || Q >= q.nPoints) {
    $.error = `ALIGNPTS: invalid point ${Q}`;
    return;
  }
  let J = z6($, K, Z), U = z6($, q, Q), W = J + U >> 1;
  P6($, K, Z, W - J), P6($, q, Q, W - U), T6($, K, Z), T6($, q, Q);
}
function q$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.zp2;
  if (Z < 0 || Z >= K.nPoints) {
    $.error = `GC: invalid point ${Z}`, $.stack[$.stackTop++] = 0;
    return;
  }
  let q = Q ? NZ($, K.org[Z]) : z6($, K, Z);
  $.stack[$.stackTop++] = q;
}
function wZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = $.zp2;
  if (Z < 0 || Z >= K.nPoints) {
    $.error = `SCFS: invalid point ${Z}`;
    return;
  }
  let q = z6($, K, Z);
  P6($, K, Z, Q - q), T6($, K, Z);
}
function J$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = $.zp0, J = $.zp1;
  if (K < 0 || K >= q.nPoints) {
    $.error = `MD: invalid point ${K}`, $.stack[$.stackTop++] = 0;
    return;
  }
  if (Z < 0 || Z >= J.nPoints) {
    $.error = `MD: invalid point ${Z}`, $.stack[$.stackTop++] = 0;
    return;
  }
  let U;
  if (Q)
    U = a6($, J, Z) - a6($, q, K);
  else
    U = z6($, J, Z) - z6($, q, K);
  $.stack[$.stackTop++] = U;
}
function AZ($) {
  $.stack[$.stackTop++] = $.ppem;
}
function LZ($) {
  $.stack[$.stackTop++] = $.pointSize;
}
function FZ($) {
  if ($.backwardCompatibility === 7)
    return;
  let Q = $.pts, Z = $.GS.loop;
  $.GS.loop = 1;
  for (let K = 0;K < Z; K++) {
    let q = $.stack[--$.stackTop];
    if (q < 0 || q >= Q.nPoints) {
      $.error = `FLIPPT: invalid point ${q}`;
      return;
    }
    Q.tags[q] ^= 1;
  }
}
function DZ($) {
  if ($.backwardCompatibility === 7)
    return;
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = $.pts;
  if (Z < 0 || Q >= K.nPoints || Z > Q) {
    $.error = `FLIPRGON: invalid range ${Z}-${Q}`;
    return;
  }
  for (let q = Z;q <= Q; q++)
    K.tags[q] |= 1;
}
function BZ($) {
  if ($.backwardCompatibility === 7)
    return;
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = $.pts;
  if (Z < 0 || Q >= K.nPoints || Z > Q) {
    $.error = `FLIPRGOFF: invalid range ${Z}-${Q}`;
    return;
  }
  for (let q = Z;q <= Q; q++)
    K.tags[q] &= -2;
}
function k9($, Q) {
  let Z = $.stack[--$.stackTop], K = W8(Z, $.GS);
  $.stack[$.stackTop++] = E8(Z, K, $.GS);
}
function C9($, Q) {
  let Z = $.stack[--$.stackTop], K = W8(Z, $.GS);
  $.stack[$.stackTop++] = Z + K;
}
function RZ($) {
  U$($, 0);
}
function MZ($) {
  U$($, 16);
}
function TZ($) {
  U$($, 32);
}
function U$($, Q) {
  let Z = $.stack[--$.stackTop];
  if (Z < 0) {
    $.error = `DELTAP: invalid count ${Z}`;
    return;
  }
  let K = $.zp0;
  for (let q = 0;q < Z; q++) {
    let J = $.stack[--$.stackTop], U = $.stack[--$.stackTop];
    if (J < 0 || J >= K.nPoints) {
      $.error = `DELTAP: invalid point ${J}`;
      return;
    }
    let W = (U >> 4 & 15) + $.GS.deltaBase + Q, H = U & 15;
    if (W !== $.ppem)
      continue;
    let _ = 1 << 6 - $.GS.deltaShift, N = H - 8;
    if (N >= 0)
      N += 1;
    let V = N * _;
    if ($.backwardCompatibility) {
      if ($.backwardCompatibility === 7)
        continue;
      if (!($.isComposite && $.GS.freeVector.y !== 0 || K.tags[J] & 32))
        continue;
    }
    P6($, K, J, V), T6($, K, J);
  }
}
function OZ($) {
  W$($, 0);
}
function PZ($) {
  W$($, 16);
}
function IZ($) {
  W$($, 32);
}
function W$($, Q) {
  let Z = $.stack[--$.stackTop];
  if (Z < 0) {
    $.error = `DELTAC: invalid count ${Z}`;
    return;
  }
  for (let K = 0;K < Z; K++) {
    let q = $.stack[--$.stackTop], J = $.stack[--$.stackTop];
    if (q < 0 || q >= $.cvtSize) {
      $.error = `DELTAC: invalid CVT index ${q}`;
      return;
    }
    let U = (J >> 4 & 15) + $.GS.deltaBase + Q, W = J & 15;
    if (U !== $.ppem)
      continue;
    let H = 1 << 6 - $.GS.deltaShift, _ = W - 8;
    if (_ >= 0)
      _ += 1;
    let N = _ * H;
    $.cvt[q] += N;
  }
}
function H$($, Q) {
  if (Q === 0)
    $.GS.projVector = { x: 0, y: 16384 }, $.GS.freeVector = { x: 0, y: 16384 }, $.GS.dualVector = { x: 0, y: 16384 };
  else
    $.GS.projVector = { x: 16384, y: 0 }, $.GS.freeVector = { x: 16384, y: 0 }, $.GS.dualVector = { x: 16384, y: 0 };
}
function _$($, Q) {
  if (Q === 0)
    $.GS.projVector = { x: 0, y: 16384 }, $.GS.dualVector = { x: 0, y: 16384 };
  else
    $.GS.projVector = { x: 16384, y: 0 }, $.GS.dualVector = { x: 16384, y: 0 };
}
function N$($, Q) {
  if (Q === 0)
    $.GS.freeVector = { x: 0, y: 16384 };
  else
    $.GS.freeVector = { x: 16384, y: 0 };
}
function P0($, Q, Z, K, q, J) {
  let U = K === 0 ? $.twilight : $.pts, W = q === 0 ? $.twilight : $.pts, H = J ? U.org[Q] : U.cur[Q], _ = J ? W.org[Z] : W.cur[Z];
  if (!H || !_)
    return { vec: { x: 16384, y: 0 }, isZero: true };
  let N = H.x - _.x, V = H.y - _.y;
  if (N === 0 && V === 0)
    return { vec: { x: 16384, y: 0 }, isZero: true };
  let X = Math.sqrt(N * N + V * V);
  if (X === 0)
    return { vec: { x: 16384, y: 0 }, isZero: true };
  return { vec: { x: Math.round(N / X * 16384), y: Math.round(V / X * 16384) }, isZero: false };
}
function V$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], { vec: q, isZero: J } = P0($, Z, K, $.GS.gep1, $.GS.gep2, false);
  if (Q && !J) {
    let U = q.x;
    q.x = -q.y, q.y = U;
  }
  $.GS.projVector = q, $.GS.dualVector = { ...q };
}
function X$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], { vec: q, isZero: J } = P0($, Z, K, $.GS.gep1, $.GS.gep2, false);
  if (Q && !J) {
    let U = q.x;
    q.x = -q.y, q.y = U;
  }
  $.GS.freeVector = q;
}
function z$($, Q) {
  let Z = $.stack[--$.stackTop], K = $.stack[--$.stackTop], q = P0($, Z, K, $.GS.gep1, $.GS.gep2, true), J = P0($, Z, K, $.GS.gep1, $.GS.gep2, false);
  if (Q && !q.isZero) {
    let U = q.vec.x;
    q.vec.x = -q.vec.y, q.vec.y = U;
  }
  if (Q && !J.isZero) {
    let U = J.vec.x;
    J.vec.x = -J.vec.y, J.vec.y = U;
  }
  $.GS.dualVector = q.vec, $.GS.projVector = J.vec;
}
function vZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = Math.sqrt(Z * Z + Q * Q);
  if (K === 0)
    $.GS.projVector = { x: 16384, y: 0 };
  else
    $.GS.projVector = { x: Math.round(Z / K * 16384), y: Math.round(Q / K * 16384) };
  $.GS.dualVector = { ...$.GS.projVector };
}
function kZ($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop], K = Math.sqrt(Z * Z + Q * Q);
  if (K === 0)
    $.GS.freeVector = { x: 16384, y: 0 };
  else
    $.GS.freeVector = { x: Math.round(Z / K * 16384), y: Math.round(Q / K * 16384) };
}
function CZ($) {
  $.stack[$.stackTop++] = $.GS.projVector.x, $.stack[$.stackTop++] = $.GS.projVector.y;
}
function SZ($) {
  $.stack[$.stackTop++] = $.GS.freeVector.x, $.stack[$.stackTop++] = $.GS.freeVector.y;
}
function hZ($) {
  $.GS.freeVector = { ...$.GS.projVector };
}
function yZ($) {
  $.GS.rp0 = $.stack[--$.stackTop];
}
function bZ($) {
  $.GS.rp1 = $.stack[--$.stackTop];
}
function mZ($) {
  $.GS.rp2 = $.stack[--$.stackTop];
}
function gZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q !== 0 && Q !== 1) {
    $.error = `SZP0: invalid zone ${Q}`;
    return;
  }
  $.GS.gep0 = Q, $.zp0 = Q === 0 ? $.twilight : $.pts;
}
function uZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q !== 0 && Q !== 1) {
    $.error = `SZP1: invalid zone ${Q}`;
    return;
  }
  $.GS.gep1 = Q, $.zp1 = Q === 0 ? $.twilight : $.pts;
}
function xZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q !== 0 && Q !== 1) {
    $.error = `SZP2: invalid zone ${Q}`;
    return;
  }
  $.GS.gep2 = Q, $.zp2 = Q === 0 ? $.twilight : $.pts;
}
function fZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q !== 0 && Q !== 1) {
    $.error = `SZPS: invalid zone ${Q}`;
    return;
  }
  $.GS.gep0 = Q, $.GS.gep1 = Q, $.GS.gep2 = Q;
  let Z = Q === 0 ? $.twilight : $.pts;
  $.zp0 = Z, $.zp1 = Z, $.zp2 = Z;
}
function lZ($) {
  let Q = $.stack[--$.stackTop];
  if (Q <= 0) {
    $.GS.loop = 0;
    return;
  }
  $.GS.loop = Q;
}
function dZ($) {
  $.GS.minimumDistance = $.stack[--$.stackTop];
}
function nZ($) {
  $.GS.controlValueCutIn = $.stack[--$.stackTop];
}
function pZ($) {
  $.GS.singleWidthCutIn = $.stack[--$.stackTop];
}
function cZ($) {
  let Q = $.stack[--$.stackTop];
  $.GS.singleWidthValue = h6(Q, $.scaleFix);
}
function oZ($) {
  $.GS.deltaBase = $.stack[--$.stackTop];
}
function iZ($) {
  $.GS.deltaShift = $.stack[--$.stackTop];
}
function sZ($) {
  $.GS.roundState = 1;
}
function rZ($) {
  $.GS.roundState = 0;
}
function aZ($) {
  $.GS.roundState = 2;
}
function tZ($) {
  $.GS.roundState = 3;
}
function eZ($) {
  $.GS.roundState = 4;
}
function $7($) {
  $.GS.roundState = 5;
}
function Q7($) {
  let Q = $.stack[--$.stackTop];
  v9(Q, $.GS), $.GS.roundState = 6;
}
function Z7($) {
  let Q = $.stack[--$.stackTop];
  v9(Q, $.GS), $.GS.roundState = 7;
}
function K7($) {
  $.GS.autoFlip = true;
}
function q7($) {
  $.GS.autoFlip = false;
}
function J7($) {
  $.GS.scanControl = $.stack[--$.stackTop];
}
function U7($) {
  $.GS.scanType = $.stack[--$.stackTop];
}
function W7($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Q === 1 || Q === 2 || Q === 3 && $.currentRange === 2) {
    let K = 1 << Q - 1;
    if (Z)
      $.GS.instructControl |= K;
    else
      $.GS.instructControl &= ~K;
  }
  if (Q === 3 && $.currentRange === 3 && $.lightMode)
    $.backwardCompatibility = Z & 4 ^ 4;
}
function H7($) {
  let Q = $.stack[--$.stackTop], Z = 0, K = $.renderMode, q = K === "mono", J = K === "lcd", U = K === "lcd_v", W = $.grayscale, H = $.lightMode;
  if (Q & 1)
    Z |= 40;
  if (Q & 32 && W)
    Z |= 4096;
  if (H && !q) {
    if (Q & 64)
      Z |= 8192;
    if (Q & 256 && U)
      Z |= 32768;
    if (Q & 1024)
      Z |= 131072;
    if (Q & 2048 && !q)
      Z |= 262144;
    if (Q & 4096 && !J && !U)
      Z |= 524288;
  }
  $.stack[$.stackTop++] = Z;
}
function _7($) {
  let Q = $.stack[--$.stackTop];
  if (Q < 0 || Q >= $.storageSize) {
    $.error = `RS: invalid index ${Q}`, $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = $.storage[Q];
}
function N7($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z < 0 || Z >= $.storageSize) {
    $.error = `WS: invalid index ${Z}`;
    return;
  }
  $.storage[Z] = Q;
}
function V7($) {
  let Q = $.stack[--$.stackTop];
  if (Q < 0 || Q >= $.cvtSize) {
    $.stack[$.stackTop++] = 0;
    return;
  }
  $.stack[$.stackTop++] = $.cvt[Q];
}
function X7($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z < 0 || Z >= $.cvtSize)
    return;
  $.cvt[Z] = Q;
}
function z7($) {
  let Q = $.stack[--$.stackTop], Z = $.stack[--$.stackTop];
  if (Z < 0 || Z >= $.cvtSize)
    return;
  $.cvt[Z] = h6(Q, $.scaleFix);
}
function G7($) {
  let Q = $.stack[--$.stackTop], Z = $.zp0;
  if (Q < 0 || Q >= Z.nPoints) {
    $.error = `UTP: invalid point ${Q}`;
    return;
  }
  let K = $.GS.freeVector;
  if (K.y !== 0)
    Z.tags[Q] &= ~32;
  if (K.x !== 0)
    Z.tags[Q] &= ~16;
}
function j7($) {
  if ($.backwardCompatibility) {
    if ($.backwardCompatibility === 7)
      return;
    $.backwardCompatibility |= 2;
  }
  w7($, 16, true);
}
function E7($) {
  if ($.backwardCompatibility) {
    if ($.backwardCompatibility === 7)
      return;
    $.backwardCompatibility |= 1;
  }
  w7($, 32, false);
}
function w7($, Q, Z) {
  let K = $.pts, q = K.nPoints, J = K.nContours;
  if (q === 0 || J === 0)
    return;
  let U = 0;
  for (let W = 0;W < J; W++) {
    let H = K.contours[W], _ = -1;
    for (let z = U;z <= H; z++)
      if (K.tags[z] & Q) {
        _ = z;
        break;
      }
    if (_ < 0) {
      U = H + 1;
      continue;
    }
    let N = _, V = _ + 1, X = false;
    while (true) {
      if (V > H) {
        if (X)
          break;
        V = U, X = true;
      }
      if (V === _ && X) {
        if (N !== _)
          Y7(K, N, _, U, H, Z);
        else
          OH(K, U, H, _, Z);
        break;
      }
      if (K.tags[V] & Q) {
        if (N !== V)
          Y7(K, N, V, U, H, Z);
        N = V;
      }
      V++;
    }
    U = H + 1;
  }
}
function OH($, Q, Z, K, q) {
  let J = q ? $.org[K]?.x : $.org[K]?.y, W = (q ? $.cur[K]?.x : $.cur[K]?.y) - J;
  if (W === 0)
    return;
  for (let H = Q;H <= Z; H++) {
    if (H === K)
      continue;
    if (q)
      $.cur[H].x += W;
    else
      $.cur[H].y += W;
  }
}
function Y7($, Q, Z, K, q, J) {
  let U = Q, W = Z, H = J ? $.orus[U]?.x : $.orus[U]?.y, _ = J ? $.orus[W]?.x : $.orus[W]?.y;
  if (H > _) {
    let L = H;
    H = _, _ = L;
    let A = U;
    U = W, W = A;
  }
  let N = J ? $.org[U]?.x : $.org[U]?.y, V = J ? $.org[W]?.x : $.org[W]?.y, X = J ? $.cur[U]?.x : $.cur[U]?.y, z = J ? $.cur[W]?.x : $.cur[W]?.y, G = X - N, Y = z - V, j = X === z || H === _, E = j ? 0 : WZ(z - X, _ - H), w = Q + 1;
  if (w > q)
    w = K;
  while (w !== Z) {
    let L = J ? $.org[w]?.x : $.org[w]?.y, A;
    if (L <= N)
      A = L + G;
    else if (L >= V)
      A = L + Y;
    else if (j)
      A = X;
    else {
      let D = J ? $.orus[w]?.x : $.orus[w]?.y;
      A = X + a5(D - H, E);
    }
    if (J)
      $.cur[w].x = A;
    else
      $.cur[w].y = A;
    if (w++, w > q)
      w = K;
  }
}
function A7($) {
  let Q = $.stack[$.stackTop - 1];
  $.stack[$.stackTop++] = Q;
}
function L7($) {
  $.stackTop--;
}
function F7($) {
  $.stackTop = 0;
}
function D7($) {
  let Q = $.stack[$.stackTop - 1], Z = $.stack[$.stackTop - 2];
  $.stack[$.stackTop - 1] = Z, $.stack[$.stackTop - 2] = Q;
}
function B7($) {
  let Q = $.stackTop;
  $.stack[$.stackTop++] = Q;
}
function R7($) {
  let Q = $.stack[--$.stackTop];
  if (Q <= 0 || Q > $.stackTop) {
    $.error = `CINDEX: invalid index ${Q}`;
    return;
  }
  let Z = $.stack[$.stackTop - Q];
  $.stack[$.stackTop++] = Z;
}
function M7($) {
  let Q = $.stack[--$.stackTop];
  if (Q <= 0 || Q > $.stackTop) {
    $.error = `MINDEX: invalid index ${Q}`;
    return;
  }
  let Z = $.stack[$.stackTop - Q];
  for (let K = $.stackTop - Q;K < $.stackTop - 1; K++)
    $.stack[K] = $.stack[K + 1];
  $.stack[$.stackTop - 1] = Z;
}
function T7($) {
  let Q = $.stack[$.stackTop - 1], Z = $.stack[$.stackTop - 2], K = $.stack[$.stackTop - 3];
  $.stack[$.stackTop - 3] = Z, $.stack[$.stackTop - 2] = Q, $.stack[$.stackTop - 1] = K;
}
function G$($, Q) {
  for (let Z = 0;Z < Q; Z++)
    $.stack[$.stackTop++] = $.code[$.IP++];
}
function Y$($, Q) {
  for (let Z = 0;Z < Q; Z++) {
    let K = $.code[$.IP++], q = $.code[$.IP++], J = K << 8 | q;
    if (J >= 32768)
      J -= 65536;
    $.stack[$.stackTop++] = J;
  }
}
function O7($) {
  let Q = $.code[$.IP++];
  G$($, Q);
}
function P7($) {
  let Q = $.code[$.IP++];
  Y$($, Q);
}
function j$($) {
  while ($.IP < $.codeSize && $.error === null) {
    if (++$.instructionCount > $.maxInstructions) {
      $.error = "Instruction limit exceeded (possible infinite loop)";
      return;
    }
    let Q = $.code[$.IP++];
    $.opcode = Q, PH($, Q);
  }
}
function PH($, Q) {
  if (Q >= 176 && Q <= 183) {
    G$($, Q - 176 + 1);
    return;
  }
  if (Q >= 184 && Q <= 191) {
    Y$($, Q - 184 + 1);
    return;
  }
  if (Q >= 192 && Q <= 223) {
    VZ($, Q & 31);
    return;
  }
  if (Q >= 224 && Q <= 255) {
    XZ($, Q & 31);
    return;
  }
  switch (Q) {
    case B.SVTCA_Y:
      H$($, 0);
      break;
    case B.SVTCA_X:
      H$($, 1);
      break;
    case B.SPVTCA_Y:
      _$($, 0);
      break;
    case B.SPVTCA_X:
      _$($, 1);
      break;
    case B.SFVTCA_Y:
      N$($, 0);
      break;
    case B.SFVTCA_X:
      N$($, 1);
      break;
    case B.SPVTL_0:
      V$($, false);
      break;
    case B.SPVTL_1:
      V$($, true);
      break;
    case B.SFVTL_0:
      X$($, false);
      break;
    case B.SFVTL_1:
      X$($, true);
      break;
    case B.SDPVTL_0:
      z$($, false);
      break;
    case B.SDPVTL_1:
      z$($, true);
      break;
    case B.SPVFS:
      vZ($);
      break;
    case B.SFVFS:
      kZ($);
      break;
    case B.GPV:
      CZ($);
      break;
    case B.GFV:
      SZ($);
      break;
    case B.SFVTPV:
      hZ($);
      break;
    case B.ISECT:
      jZ($);
      break;
    case B.SRP0:
      yZ($);
      break;
    case B.SRP1:
      bZ($);
      break;
    case B.SRP2:
      mZ($);
      break;
    case B.SZP0:
      gZ($);
      break;
    case B.SZP1:
      uZ($);
      break;
    case B.SZP2:
      xZ($);
      break;
    case B.SZPS:
      fZ($);
      break;
    case B.SLOOP:
      lZ($);
      break;
    case B.RTG:
      sZ($);
      break;
    case B.RTHG:
      rZ($);
      break;
    case B.SMD:
      dZ($);
      break;
    case B.ELSE:
      aQ($);
      break;
    case B.JMPR:
      eQ($);
      break;
    case B.SCVTCI:
      nZ($);
      break;
    case B.SSWCI:
      pZ($);
      break;
    case B.SSW:
      cZ($);
      break;
    case B.DUP:
      A7($);
      break;
    case B.POP:
      L7($);
      break;
    case B.CLEAR:
      F7($);
      break;
    case B.SWAP:
      D7($);
      break;
    case B.DEPTH:
      B7($);
      break;
    case B.CINDEX:
      R7($);
      break;
    case B.MINDEX:
      M7($);
      break;
    case B.ROLL:
      T7($);
      break;
    case B.FDEF:
      ZZ($);
      break;
    case B.ENDF:
      KZ($);
      break;
    case B.CALL:
      qZ($);
      break;
    case B.LOOPCALL:
      JZ($);
      break;
    case B.MDAP_0:
      t5($, false);
      break;
    case B.MDAP_1:
      t5($, true);
      break;
    case B.IUP_Y:
      E7($);
      break;
    case B.IUP_X:
      j7($);
      break;
    case B.SHP_0:
      $$($, false);
      break;
    case B.SHP_1:
      $$($, true);
      break;
    case B.SHC_0:
      Q$($, false);
      break;
    case B.SHC_1:
      Q$($, true);
      break;
    case B.SHZ_0:
      Z$($, false);
      break;
    case B.SHZ_1:
      Z$($, true);
      break;
    case B.SHPIX:
      zZ($);
      break;
    case B.IP:
      GZ($);
      break;
    case B.MSIRP_0:
      K$($, false);
      break;
    case B.MSIRP_1:
      K$($, true);
      break;
    case B.ALIGNRP:
      YZ($);
      break;
    case B.ALIGNPTS:
      EZ($);
      break;
    case B.UTP:
      G7($);
      break;
    case B.RTDG:
      aZ($);
      break;
    case B.MIAP_0:
      e5($, false);
      break;
    case B.MIAP_1:
      e5($, true);
      break;
    case B.NPUSHB:
      O7($);
      break;
    case B.NPUSHW:
      P7($);
      break;
    case B.WS:
      N7($);
      break;
    case B.RS:
      _7($);
      break;
    case B.WCVTP:
      X7($);
      break;
    case B.RCVT:
      V7($);
      break;
    case B.GC_0:
      q$($, false);
      break;
    case B.GC_1:
      q$($, true);
      break;
    case B.SCFS:
      wZ($);
      break;
    case B.MD_0:
      J$($, true);
      break;
    case B.MD_1:
      J$($, false);
      break;
    case B.MPPEM:
      AZ($);
      break;
    case B.MPS:
      LZ($);
      break;
    case B.FLIPON:
      K7($);
      break;
    case B.FLIPOFF:
      q7($);
      break;
    case B.DEBUG:
      break;
    case B.LT:
      uQ($);
      break;
    case B.LTEQ:
      xQ($);
      break;
    case B.GT:
      fQ($);
      break;
    case B.GTEQ:
      lQ($);
      break;
    case B.EQ:
      dQ($);
      break;
    case B.NEQ:
      nQ($);
      break;
    case B.ODD:
      pQ($);
      break;
    case B.EVEN:
      cQ($);
      break;
    case B.IF:
      rQ($);
      break;
    case B.EIF:
      tQ($);
      break;
    case B.AND:
      oQ($);
      break;
    case B.OR:
      iQ($);
      break;
    case B.NOT:
      sQ($);
      break;
    case B.DELTAP1:
      RZ($);
      break;
    case B.SDB:
      oZ($);
      break;
    case B.SDS:
      iZ($);
      break;
    case B.ADD:
      IQ($);
      break;
    case B.SUB:
      vQ($);
      break;
    case B.DIV:
      kQ($);
      break;
    case B.MUL:
      CQ($);
      break;
    case B.ABS:
      SQ($);
      break;
    case B.NEG:
      hQ($);
      break;
    case B.FLOOR:
      yQ($);
      break;
    case B.CEILING:
      bQ($);
      break;
    case B.ROUND_0:
      k9($, 0);
      break;
    case B.ROUND_1:
      k9($, 1);
      break;
    case B.ROUND_2:
      k9($, 2);
      break;
    case B.ROUND_3:
      k9($, 3);
      break;
    case B.NROUND_0:
      C9($, 0);
      break;
    case B.NROUND_1:
      C9($, 1);
      break;
    case B.NROUND_2:
      C9($, 2);
      break;
    case B.NROUND_3:
      C9($, 3);
      break;
    case B.WCVTF:
      z7($);
      break;
    case B.DELTAP2:
      MZ($);
      break;
    case B.DELTAP3:
      TZ($);
      break;
    case B.DELTAC1:
      OZ($);
      break;
    case B.DELTAC2:
      PZ($);
      break;
    case B.DELTAC3:
      IZ($);
      break;
    case B.SROUND:
      Q7($);
      break;
    case B.S45ROUND:
      Z7($);
      break;
    case B.JROT:
      $Z($);
      break;
    case B.JROF:
      QZ($);
      break;
    case B.ROFF:
      $7($);
      break;
    case B.RUTG:
      eZ($);
      break;
    case B.RDTG:
      tZ($);
      break;
    case B.SANGW:
      $.stackTop--;
      break;
    case B.AA:
      $.stackTop--;
      break;
    case B.FLIPPT:
      FZ($);
      break;
    case B.FLIPRGON:
      DZ($);
      break;
    case B.FLIPRGOFF:
      BZ($);
      break;
    case B.SCANCTRL:
      J7($);
      break;
    case B.SCANTYPE:
      U7($);
      break;
    case B.MAX:
      mQ($);
      break;
    case B.MIN:
      gQ($);
      break;
    case B.GETINFO:
      H7($);
      break;
    case B.IDEF:
      UZ($);
      break;
    case B.INSTCTRL:
      W7($);
      break;
    default:
      if (Q < $.maxIDefs && $.IDefs[Q]?.active) {
        let Z = $.IDefs[Q];
        if ($.callStackTop >= $.maxCallStack) {
          $.error = "IDEF call: call stack overflow";
          return;
        }
        let K = $.callStack[$.callStackTop++];
        K.callerIP = $.IP, K.callerRange = $.currentRange, K.def = { id: Q, start: Z.start, end: Z.end, active: true, range: Z.range }, K.count = 1, $.currentRange = Z.range;
        let q = $.codeRanges.get($.currentRange);
        if (q)
          $.code = q.code, $.codeSize = q.size;
        $.IP = Z.start;
      } else
        $.error = `Unknown opcode 0x${Q.toString(16)}`;
  }
}
function a8($, Q, Z) {
  $.codeRanges.set(Q, { code: Z, size: Z.length });
}
function S9($, Q) {
  let Z = $.codeRanges.get(Q);
  if (!Z)
    return;
  $.stackTop = 0, $.callStackTop = 0, $.currentRange = Q, $.code = Z.code, $.codeSize = Z.size, $.IP = 0, $.instructionCount = 0, j$($);
}
function I0($) {
  $.backwardCompatibility = 0, $.twilight.nPoints = $.twilight.org.length;
  for (let Q = 0;Q < $.twilight.nPoints; Q++)
    $.twilight.org[Q].x = 0, $.twilight.org[Q].y = 0, $.twilight.cur[Q].x = 0, $.twilight.cur[Q].y = 0, $.twilight.tags[Q] = 0;
  $.zp0 = $.twilight, $.zp1 = $.twilight, $.zp2 = $.twilight, $.GS.gep0 = 0, $.GS.gep1 = 0, $.GS.gep2 = 0, S9($, 1);
}
function v0($) {
  $.GS = { ...$.defaultGS }, $.backwardCompatibility = 0, $.twilight.nPoints = $.twilight.org.length;
  for (let Q = 0;Q < $.twilight.nPoints; Q++)
    $.twilight.org[Q].x = 0, $.twilight.org[Q].y = 0, $.twilight.cur[Q].x = 0, $.twilight.cur[Q].y = 0, $.twilight.tags[Q] = 0;
  $.zp0 = $.twilight, $.zp1 = $.twilight, $.zp2 = $.twilight, $.GS.gep0 = 0, $.GS.gep1 = 0, $.GS.gep2 = 0, S9($, 2), $.defaultGS = { ...$.GS }, $.defaultGS.projVector = { x: 16384, y: 0 }, $.defaultGS.freeVector = { x: 16384, y: 0 }, $.defaultGS.dualVector = { x: 16384, y: 0 }, $.defaultGS.rp0 = 0, $.defaultGS.rp1 = 0, $.defaultGS.rp2 = 0, $.defaultGS.loop = 1, $.defaultGS.gep0 = 1, $.defaultGS.gep1 = 1, $.defaultGS.gep2 = 1;
}
function k0($, Q) {
  $.GS = { ...$.defaultGS }, $.GS.rp0 = 0, $.GS.rp1 = 0, $.GS.rp2 = 0, $.GS.loop = 1, $.zp0 = $.pts, $.zp1 = $.pts, $.zp2 = $.pts, $.GS.gep0 = 1, $.GS.gep1 = 1, $.GS.gep2 = 1, a8($, 3, Q), S9($, 3);
}
function C0($, Q = 256, Z = 64, K = 64, q = 16, J) {
  let U = M0(Q, Z, K, K, 32, q), W;
  if (J)
    W = new Int32Array(J), U.cvt = new Int32Array(J), U.cvtSize = J.length;
  return U.twilight.nPoints = q, U.zp0 = U.twilight, U.zp1 = U.twilight, U.zp2 = U.twilight, { ctx: U, unitsPerEM: $, cvtOriginal: W, fpgmExecuted: false, currentPpem: 0 };
}
function S0($, Q) {
  a8($.ctx, 1, Q);
}
function h0($, Q) {
  a8($.ctx, 2, Q);
}
function E$($) {
  if ($.fpgmExecuted)
    return null;
  return $.ctx.error = null, I0($.ctx), $.fpgmExecuted = true, $.ctx.error;
}
function y0($, Q, Z) {
  if (!$.fpgmExecuted) {
    let K = E$($);
    if (K)
      return K;
  }
  if ($.currentPpem === Q)
    return null;
  return $.ctx.scale = Q * 64 / $.unitsPerEM, $.ctx.scaleFix = Math.round($.ctx.scale * 65536), $.ctx.ppem = Q, $.ctx.pointSize = Z * 64, $.ctx.grayscale = $.ctx.renderMode !== "mono" && !$.ctx.lightMode, IH($.ctx, $.cvtOriginal), $.ctx.error = null, v0($.ctx), $.currentPpem = Q, $.cvtBase = new Int32Array($.ctx.cvt), $.ctx.error;
}
function IH($, Q) {
  if (!Q) {
    for (let Z = 0;Z < $.cvtSize; Z++)
      $.cvt[Z] = h6($.cvt[Z], $.scaleFix);
    return;
  }
  for (let Z = 0;Z < $.cvtSize; Z++)
    $.cvt[Z] = h6(Q[Z], $.scaleFix);
}
function b0($, Q) {
  let Z = $.ctx, K = Q.xCoords.length, q = Q.contourEnds.length, J = K + 4, U = J8(J, q);
  U.nPoints = J, U.nContours = q;
  for (let G = 0;G < K; G++) {
    let Y = h6(Q.xCoords[G], Z.scaleFix), j = h6(Q.yCoords[G], Z.scaleFix);
    U.org[G].x = Y, U.org[G].y = j, U.cur[G].x = Y, U.cur[G].y = j, U.orus[G].x = Q.xCoords[G], U.orus[G].y = Q.yCoords[G], U.tags[G] = Q.flags[G];
  }
  let W = 1 / 0;
  for (let G = 0;G < K; G++)
    if (Q.xCoords[G] < W)
      W = Q.xCoords[G];
  if (!Number.isFinite(W))
    W = 0;
  let H = Q.lsb ?? 0, _ = Q.advanceWidth ?? 0, N = h6(W - H, Z.scaleFix);
  U.org[K].x = N, U.org[K].y = 0, U.cur[K].x = N, U.cur[K].y = 0, U.orus[K].x = W - H, U.orus[K].y = 0, U.tags[K] = 0;
  let V = h6(W - H + _, Z.scaleFix);
  U.org[K + 1].x = V, U.org[K + 1].y = 0, U.cur[K + 1].x = V, U.cur[K + 1].y = 0, U.orus[K + 1].x = W - H + _, U.orus[K + 1].y = 0, U.tags[K + 1] = 0;
  for (let G = K + 2;G < J; G++)
    U.org[G].x = 0, U.org[G].y = 0, U.cur[G].x = 0, U.cur[G].y = 0, U.orus[G].x = 0, U.orus[G].y = 0, U.tags[G] = 0;
  U.cur[K].x = U8(U.cur[K].x, 0), U.cur[K + 1].x = U8(U.cur[K + 1].x, 0), U.cur[K + 2].y = U8(U.cur[K + 2].y, 0), U.cur[K + 3].y = U8(U.cur[K + 3].y, 0);
  for (let G = 0;G < q; G++)
    U.contours[G] = Q.contourEnds[G];
  Z.pts = U, Z.zp0 = U, Z.zp1 = U, Z.zp2 = U, Z.twilight.nPoints = Z.twilight.org.length;
  for (let G = 0;G < Z.twilight.nPoints; G++)
    Z.twilight.org[G].x = 0, Z.twilight.org[G].y = 0, Z.twilight.cur[G].x = 0, Z.twilight.cur[G].y = 0, Z.twilight.tags[G] = 0;
  if (Z.isComposite = Q.isComposite ?? false, Z.backwardCompatibility = Z.lightMode ? 4 : 0, $.cvtBase)
    Z.cvt.set($.cvtBase);
  if (Z.error = null, (Z.GS.instructControl & 1) !== 0)
    ;
  else if (Q.instructions.length > 0)
    k0(Z, Q.instructions);
  let X = Array(K), z = Array(K);
  for (let G = 0;G < K; G++)
    X[G] = U.cur[G]?.x, z[G] = U.cur[G]?.y;
  return { xCoords: X, yCoords: z, flags: Q.flags, contourEnds: Q.contourEnds, error: Z.error };
}
function I7($) {
  return $.map((Q) => Q / 64);
}
var m0;
((J) => {
  J[J.Invalid = 0] = "Invalid";
  J[J.LTR = 4] = "LTR";
  J[J.RTL = 5] = "RTL";
  J[J.TTB = 6] = "TTB";
  J[J.BTT = 7] = "BTT";
})(m0 ||= {});
var v7;
((K) => {
  K[K.MonotoneGraphemes = 0] = "MonotoneGraphemes";
  K[K.MonotoneCharacters = 1] = "MonotoneCharacters";
  K[K.Characters = 2] = "Characters";
})(v7 ||= {});
var k7;
((U) => {
  U[U.Default = 0] = "Default";
  U[U.BeginningOfText = 1] = "BeginningOfText";
  U[U.EndOfText = 2] = "EndOfText";
  U[U.PreserveDefaultIgnorables = 4] = "PreserveDefaultIgnorables";
  U[U.RemoveDefaultIgnorables = 8] = "RemoveDefaultIgnorables";
  U[U.DoNotInsertDottedCircle = 16] = "DoNotInsertDottedCircle";
})(k7 ||= {});
var g0;
((q) => {
  q[q.Base = 1] = "Base";
  q[q.Ligature = 2] = "Ligature";
  q[q.Mark = 3] = "Mark";
  q[q.Component = 4] = "Component";
})(g0 ||= {});
function C($) {
  if ($.length !== 4)
    throw Error(`Tag must be exactly 4 characters: "${$}"`);
  return $.charCodeAt(0) << 24 | $.charCodeAt(1) << 16 | $.charCodeAt(2) << 8 | $.charCodeAt(3);
}
function h9($) {
  return String.fromCharCode($ >> 24 & 255, $ >> 16 & 255, $ >> 8 & 255, $ & 255);
}
var p = { head: C("head"), hhea: C("hhea"), hmtx: C("hmtx"), maxp: C("maxp"), cmap: C("cmap"), loca: C("loca"), glyf: C("glyf"), name: C("name"), OS2: C("OS/2"), post: C("post"), GDEF: C("GDEF"), GSUB: C("GSUB"), GPOS: C("GPOS"), BASE: C("BASE"), JSTF: C("JSTF"), MATH: C("MATH"), CFF: C("CFF "), CFF2: C("CFF2"), fvar: C("fvar"), gvar: C("gvar"), avar: C("avar"), HVAR: C("HVAR"), VVAR: C("VVAR"), MVAR: C("MVAR"), morx: C("morx"), kerx: C("kerx"), kern: C("kern"), trak: C("trak"), feat: C("feat"), COLR: C("COLR"), CPAL: C("CPAL"), SVG: C("SVG "), sbix: C("sbix"), CBDT: C("CBDT"), CBLC: C("CBLC"), STAT: C("STAT"), vhea: C("vhea"), vmtx: C("vmtx"), VORG: C("VORG"), fpgm: C("fpgm"), prep: C("prep"), cvt: C("cvt "), gasp: C("gasp") };
var pY = { ccmp: C("ccmp"), locl: C("locl"), rlig: C("rlig"), liga: C("liga"), clig: C("clig"), calt: C("calt"), rclt: C("rclt"), dlig: C("dlig"), smcp: C("smcp"), c2sc: C("c2sc"), isol: C("isol"), init: C("init"), medi: C("medi"), fina: C("fina"), kern: C("kern"), mark: C("mark"), mkmk: C("mkmk"), curs: C("curs"), dist: C("dist") };

class C8 {
  direction = 4;
  script = "Zyyy";
  language = null;
  infos = [];
  positions = [];
  _deleted = null;
  _deletedCount = 0;
  _infoPool = [];
  _posPool = [];
  _capacity = 0;
  static withCapacity($) {
    let Q = new C8;
    return Q._capacity = $, Q;
  }
  get length() {
    return this.infos.length;
  }
  reset() {
    this.infos.length = 0, this.positions.length = 0, this._deleted = null, this._deletedCount = 0;
  }
  initFromCodepoints($, Q, Z) {
    let K = $.length, q = this._infoPool.length;
    if (q < K) {
      let J = K - q;
      for (let U = 0;U < J; U++)
        this._infoPool.push({ glyphId: 0, cluster: 0, mask: 0, codepoint: 0 }), this._posPool.push({ xAdvance: 0, yAdvance: 0, xOffset: 0, yOffset: 0 });
    }
    this.infos.length = K, this.positions.length = K;
    for (let J = 0;J < K; J++) {
      let U = $[J], W = this._infoPool[J];
      W.glyphId = Z(U), W.cluster = Q[J], W.mask = 4294967295, W.codepoint = U, this.infos[J] = W;
      let H = this._posPool[J];
      H.xAdvance = 0, H.yAdvance = 0, H.xOffset = 0, H.yOffset = 0, this.positions[J] = H;
    }
    this._deleted = null, this._deletedCount = 0;
  }
  initFromCodepointsWithFont($, Q, Z) {
    let K = $.length, q = this._infoPool.length;
    if (q < K) {
      let J = K - q;
      for (let U = 0;U < J; U++)
        this._infoPool.push({ glyphId: 0, cluster: 0, mask: 0, codepoint: 0 }), this._posPool.push({ xAdvance: 0, yAdvance: 0, xOffset: 0, yOffset: 0 });
    }
    this.infos.length = K, this.positions.length = K;
    for (let J = 0;J < K; J++) {
      let U = $[J], W = this._infoPool[J];
      W.glyphId = Z.glyphId(U), W.cluster = Q[J], W.mask = 4294967295, W.codepoint = U, this.infos[J] = W;
      let H = this._posPool[J];
      H.xAdvance = 0, H.yAdvance = 0, H.xOffset = 0, H.yOffset = 0, this.positions[J] = H;
    }
    this._deleted = null, this._deletedCount = 0;
  }
  initFromInfos($) {
    this.infos = $;
    let Q = $.length, Z = Array(Q);
    for (let K = 0;K < Q; K++)
      Z[K] = { xAdvance: 0, yAdvance: 0, xOffset: 0, yOffset: 0 };
    this.positions = Z;
  }
  setAdvance($, Q, Z = 0) {
    let K = this.positions[$];
    if (K)
      K.xAdvance = Q, K.yAdvance = Z;
  }
  addOffset($, Q, Z) {
    let K = this.positions[$];
    if (K)
      K.xOffset += Q, K.yOffset += Z;
  }
  replaceGlyph($, Q) {
    let Z = this.infos[$];
    if (Z)
      Z.glyphId = Q;
  }
  insertGlyph($, Q, Z) {
    if (this.infos.splice($, 0, Q), this.positions.splice($, 0, Z), this._deleted) {
      let K = new Uint8Array(this.infos.length);
      for (let q = 0;q < $; q++)
        K[q] = this._deleted[q];
      K[$] = 0;
      for (let q = $;q < this._deleted.length; q++)
        K[q + 1] = this._deleted[q];
      this._deleted = K;
    }
  }
  removeRange($, Q) {
    let Z = Q - $;
    if (this.infos.splice($, Z), this.positions.splice($, Z), this._deleted && this._deleted.length > this.infos.length)
      this._deleted = this._deleted.slice(0, this.infos.length);
  }
  markDeleted($) {
    if (!this._deleted)
      this._deleted = new Uint8Array(this.infos.length);
    if (!this._deleted[$])
      this._deleted[$] = 1, this._deletedCount++;
  }
  isDeleted($) {
    return this._deleted ? this._deleted[$] === 1 : false;
  }
  hasPendingDeletions() {
    return this._deletedCount > 0;
  }
  compact() {
    if (this._deletedCount === 0 || !this._deleted)
      return;
    let $ = 0;
    for (let Q = 0;Q < this.infos.length; Q++)
      if (!this._deleted[Q]) {
        if ($ !== Q)
          this.infos[$] = this.infos[Q], this.positions[$] = this.positions[Q];
        $++;
      }
    this.infos.length = $, this.positions.length = $, this._deleted = null, this._deletedCount = 0;
  }
  mergeClusters($, Q) {
    if ($ >= Q || $ < 0 || Q >= this.infos.length)
      return;
    let Z = this.infos[$]?.cluster;
    for (let K = $ + 1;K <= Q; K++) {
      let q = this.infos[K];
      if (q)
        q.cluster = Z;
    }
  }
  reverse() {
    this.infos.reverse(), this.positions.reverse();
  }
  reverseRange($, Q) {
    let Z = $, K = Q - 1;
    while (Z < K) {
      let q = this.infos[Z], J = this.infos[K];
      if (!q || !J)
        break;
      this.infos[Z] = J, this.infos[K] = q;
      let U = this.positions[Z], W = this.positions[K];
      if (!U || !W)
        break;
      this.positions[Z] = W, this.positions[K] = U, Z++, K--;
    }
  }
  getTotalAdvance() {
    let $ = 0, Q = 0, Z = this.positions;
    for (let K = 0;K < Z.length; K++) {
      let q = Z[K];
      $ += q.xAdvance, Q += q.yAdvance;
    }
    return { x: $, y: Q };
  }
  serialize() {
    let $ = [], Q = this.infos, Z = this.positions;
    for (let K = 0;K < Q.length; K++) {
      let q = Q[K], J = Z[K];
      if (!J)
        continue;
      let U = `${q.glyphId}`;
      if (K === 0 || q.cluster !== Q[K - 1]?.cluster)
        U += `=${q.cluster}`;
      if (J.xOffset !== 0 || J.yOffset !== 0)
        U += `@${J.xOffset},${J.yOffset}`;
      if (J.xAdvance !== 0)
        U += `+${J.xAdvance}`;
      $.push(U);
    }
    return `[${$.join("|")}]`;
  }
  glyphIds() {
    return this.infos.map(($) => $.glyphId);
  }
  clusters() {
    return this.infos.map(($) => $.cluster);
  }
  *[Symbol.iterator]() {
    let $ = this.infos, Q = this.positions;
    for (let Z = 0;Z < $.length; Z++) {
      let K = $[Z], q = Q[Z];
      if (!q)
        continue;
      yield { info: K, position: q };
    }
  }
}

class C7 {
  _direction = 4;
  _script = "Zyyy";
  _language = null;
  _clusterLevel = 0;
  _flags = 0;
  codepoints = [];
  clusters = [];
  preContext = [];
  postContext = [];
  addStr($, Q = 0) {
    let Z = $.length;
    if (Z === 0)
      return this;
    if (this.codepoints.length > 0)
      return this._addStrAppend($, Q);
    let q = Array(Z), J = Array(Z), U = Q, W = 0;
    for (let H = 0;H < Z; H++) {
      let _ = $.charCodeAt(H);
      if (_ >= 55296 && _ <= 56319 && H + 1 < Z) {
        let N = $.charCodeAt(H + 1);
        if (N >= 56320 && N <= 57343) {
          q[W] = (_ - 55296 << 10) + (N - 56320) + 65536, J[W] = U, W++, U++, H++;
          continue;
        }
      }
      q[W] = _, J[W] = U, W++, U++;
    }
    if (W < Z)
      q.length = W, J.length = W;
    return this.codepoints = q, this.clusters = J, this;
  }
  _addStrAppend($, Q) {
    let Z = Q, K = $.length;
    for (let q = 0;q < K; q++) {
      let J = $.charCodeAt(q);
      if (J >= 55296 && J <= 56319 && q + 1 < K) {
        let U = $.charCodeAt(q + 1);
        if (U >= 56320 && U <= 57343) {
          this.codepoints.push((J - 55296 << 10) + (U - 56320) + 65536), this.clusters.push(Z), Z++, q++;
          continue;
        }
      }
      this.codepoints.push(J), this.clusters.push(Z), Z++;
    }
    return this;
  }
  addCodepoints($, Q = 0) {
    let Z = Q;
    for (let K = 0;K < $.length; K++) {
      let q = $[K];
      this.codepoints.push(q), this.clusters.push(Z), Z++;
    }
    return this;
  }
  addCodepoint($, Q) {
    return this.codepoints.push($), this.clusters.push(Q ?? this.codepoints.length - 1), this;
  }
  setDirection($) {
    return this._direction = $, this;
  }
  setScript($) {
    return this._script = $, this;
  }
  setLanguage($) {
    return this._language = $, this;
  }
  setClusterLevel($) {
    return this._clusterLevel = $, this;
  }
  setFlags($) {
    return this._flags = $, this;
  }
  setPreContext($) {
    this.preContext = [];
    let Q = $.length;
    for (let Z = 0;Z < Q; Z++) {
      let K = $.charCodeAt(Z);
      if (K >= 55296 && K <= 56319 && Z + 1 < Q) {
        let q = $.charCodeAt(Z + 1);
        if (q >= 56320 && q <= 57343) {
          this.preContext.push((K - 55296 << 10) + (q - 56320) + 65536), Z++;
          continue;
        }
      }
      this.preContext.push(K);
    }
    return this;
  }
  setPostContext($) {
    this.postContext = [];
    let Q = $.length;
    for (let Z = 0;Z < Q; Z++) {
      let K = $.charCodeAt(Z);
      if (K >= 55296 && K <= 56319 && Z + 1 < Q) {
        let q = $.charCodeAt(Z + 1);
        if (q >= 56320 && q <= 57343) {
          this.postContext.push((K - 55296 << 10) + (q - 56320) + 65536), Z++;
          continue;
        }
      }
      this.postContext.push(K);
    }
    return this;
  }
  clear() {
    return this.codepoints.length = 0, this.clusters.length = 0, this.preContext.length = 0, this.postContext.length = 0, this;
  }
  get length() {
    return this.codepoints.length;
  }
  get direction() {
    return this._direction;
  }
  get script() {
    return this._script;
  }
  get language() {
    return this._language;
  }
  get clusterLevel() {
    return this._clusterLevel;
  }
  get flags() {
    return this._flags;
  }
  toGlyphInfos() {
    return this.codepoints.map(($, Q) => ({ glyphId: 0, cluster: this.clusters[Q] ?? 0, mask: 0, codepoint: $ }));
  }
}
function y7($, Q) {
  let Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.offset32(), W = $.uint16(), H = $.uint16(), _ = $.offset32(), N = H & 1 ? 4 : 2, V = [];
  for (let G = 0;G <= W; G++) {
    let Y = N === 4 ? $.uint32() : $.uint16() * 2;
    V.push(Y);
  }
  let X = [];
  if (J > 0) {
    let G = $.sliceFrom(U);
    for (let Y = 0;Y < J; Y++) {
      let j = [];
      for (let E = 0;E < q; E++)
        j.push(G.f2dot14());
      X.push(j);
    }
  }
  let z = [];
  for (let G = 0;G < W; G++) {
    let Y = V[G], j = V[G + 1];
    if (Y === undefined || j === undefined) {
      z.push({ tupleVariationHeaders: [] });
      continue;
    }
    let E = _ + Y, w = _ + j;
    if (E === w) {
      z.push({ tupleVariationHeaders: [] });
      continue;
    }
    let L = $.sliceFrom(E), A = vH(L, w - E, q, X);
    z.push(A);
  }
  return { majorVersion: Z, minorVersion: K, axisCount: q, sharedTupleCount: J, sharedTuples: X, glyphVariationData: z };
}
function vH($, Q, Z, K) {
  if (Q === 0)
    return { tupleVariationHeaders: [] };
  let q = $.offset, J = $.uint16(), U = $.offset16(), W = J & 4095, H = (J & 32768) !== 0, _ = [];
  for (let z = 0;z < W; z++) {
    let G = $.uint16(), Y = $.uint16(), j = null, E = null, w = null;
    if (Y & 32768) {
      j = [];
      for (let L = 0;L < Z; L++)
        j.push($.f2dot14());
    } else {
      let L = Y & 4095;
      j = K[L] || null;
    }
    if (Y & 16384) {
      E = [], w = [];
      for (let L = 0;L < Z; L++)
        E.push($.f2dot14());
      for (let L = 0;L < Z; L++)
        w.push($.f2dot14());
    }
    _.push({ variationDataSize: G, tupleIndex: Y, peakTuple: j, intermediateStartTuple: E, intermediateEndTuple: w });
  }
  let N = $.sliceFrom(q + U), V = null;
  if (H)
    V = S7(N);
  let X = [];
  for (let z = 0;z < _.length; z++) {
    let G = _[z], Y = (G.tupleIndex & 8192) !== 0, j;
    if (Y)
      j = S7(N);
    else
      j = V;
    let E = j ? j.length : 0, w = E > 0 ? h7(N, E) : [], L = E > 0 ? h7(N, E) : [], A = [];
    for (let D = 0;D < w.length; D++) {
      let F = w[D], M = L[D];
      A.push({ x: F ?? 0, y: M ?? 0 });
    }
    X.push({ variationDataSize: G.variationDataSize, tupleIndex: G.tupleIndex, peakTuple: G.peakTuple, intermediateStartTuple: G.intermediateStartTuple, intermediateEndTuple: G.intermediateEndTuple, serializedData: new Uint8Array(0), pointNumbers: j, deltas: A });
  }
  return { tupleVariationHeaders: X };
}
function S7($) {
  let Q = $.uint8(), Z = Q === 0 ? 0 : Q & 128 ? (Q & 127) << 8 | $.uint8() : Q;
  if (Z === 0)
    return [];
  let K = [], q = 0;
  while (K.length < Z) {
    let J = $.uint8(), U = (J & 127) + 1, W = (J & 128) !== 0;
    for (let H = 0;H < U && K.length < Z; H++) {
      let _ = W ? $.uint16() : $.uint8();
      q += _, K.push(q);
    }
  }
  return K;
}
function h7($, Q) {
  let Z = [];
  while (Z.length < Q) {
    let K = $.uint8(), q = (K & 63) + 1, J = (K & 128) !== 0, U = (K & 64) !== 0;
    for (let W = 0;W < q && Z.length < Q; W++)
      if (J)
        Z.push(0);
      else if (U)
        Z.push($.int16());
      else
        Z.push($.int8());
  }
  return Z;
}
function u0($, Q, Z, K) {
  let q = 1;
  for (let J = 0;J < $.length; J++) {
    let U = $[J], W = Q[J] ?? 0;
    if (U === 0 || W === 0) {
      if (U !== 0)
        q = 0;
      continue;
    }
    if (Z && K) {
      let H = Z[J], _ = K[J];
      if (H === undefined || _ === undefined)
        continue;
      if (W < H || W > _) {
        q = 0;
        break;
      }
      if (W < U)
        q *= (W - H) / (U - H);
      else if (W > U)
        q *= (_ - W) / (_ - U);
    } else {
      if (U > 0 && W < 0 || U < 0 && W > 0) {
        q = 0;
        break;
      }
      if (Math.abs(W) < Math.abs(U))
        q *= W / U;
    }
  }
  return q;
}
function kH($, Q, Z, K) {
  let q = $.glyphVariationData[Q];
  if (!q)
    return { x: 0, y: 0 };
  let J = 0, U = 0;
  for (let W = 0;W < q.tupleVariationHeaders.length; W++) {
    let H = q.tupleVariationHeaders[W];
    if (!H.peakTuple)
      continue;
    let _ = u0(H.peakTuple, K, H.intermediateStartTuple, H.intermediateEndTuple);
    if (_ === 0)
      continue;
    if (H.pointNumbers !== null) {
      let N = H.pointNumbers.indexOf(Z);
      if (N < 0)
        continue;
      let V = H.deltas[N];
      if (V)
        J += V.x * _, U += V.y * _;
    } else {
      let N = H.deltas[Z];
      if (N)
        J += N.x * _, U += N.y * _;
    }
  }
  return { x: Math.round(J), y: Math.round(U) };
}
function b7($, Q, Z) {
  let K = Z === 0, q = [], J = Q + 1;
  if (K)
    for (let U = 0;U < J; U++)
      q.push($.uint16() * 2);
  else
    for (let U = 0;U < J; U++)
      q.push($.uint32());
  return { offsets: q, isShort: K };
}
function x0($, Q) {
  if (Q < 0 || Q >= $.offsets.length - 1)
    return null;
  let Z = $.offsets[Q], K = $.offsets[Q + 1];
  if (Z === undefined || K === undefined)
    return null;
  let q = K - Z;
  if (q === 0)
    return null;
  return { offset: Z, length: q };
}
function CH($, Q) {
  return x0($, Q) !== null;
}
var w8 = { OnCurve: 1, XShortVector: 2, YShortVector: 4, Repeat: 8, XIsSameOrPositive: 16, YIsSameOrPositive: 32, OverlapSimple: 64 };
var A6 = { Arg1And2AreWords: 1, ArgsAreXYValues: 2, RoundXYToGrid: 4, WeHaveAScale: 8, MoreComponents: 32, WeHaveAnXAndYScale: 64, WeHaveATwoByTwo: 128, WeHaveInstructions: 256, UseMyMetrics: 512, OverlapCompound: 1024, ScaledComponentOffset: 2048, UnscaledComponentOffset: 4096 };
function g7($) {
  return { reader: $ };
}
function A8($, Q, Z) {
  let K = x0(Q, Z);
  if (!K)
    return { type: "empty" };
  let q = $.reader.slice(K.offset, K.length);
  return SH(q);
}
function SH($) {
  let Q = $.int16(), Z = $.int16(), K = $.int16(), q = $.int16(), J = $.int16();
  if (Q >= 0)
    return hH($, Q, Z, K, q, J);
  else
    return yH($, Q, Z, K, q, J);
}
function hH($, Q, Z, K, q, J) {
  if (Q === 0)
    return { type: "simple", numberOfContours: Q, xMin: Z, yMin: K, xMax: q, yMax: J, contours: [], instructions: new Uint8Array(0) };
  let U = [];
  for (let w = 0;w < Q; w++)
    U.push($.uint16());
  let W = U[Q - 1];
  if (W === undefined)
    return { type: "simple", numberOfContours: Q, xMin: Z, yMin: K, xMax: q, yMax: J, contours: [], instructions: new Uint8Array(0) };
  let H = W + 1, _ = $.uint16(), N = $.bytes(_), V = [];
  while (V.length < H) {
    let w = $.uint8();
    if (V.push(w), w & w8.Repeat) {
      let L = $.uint8();
      for (let A = 0;A < L; A++)
        V.push(w);
    }
  }
  let X = [], z = 0;
  for (let w = 0;w < V.length; w++) {
    let L = V[w];
    if (L & w8.XShortVector) {
      let A = $.uint8();
      z += L & w8.XIsSameOrPositive ? A : -A;
    } else if (!(L & w8.XIsSameOrPositive))
      z += $.int16();
    X.push(z);
  }
  let G = [], Y = 0;
  for (let w = 0;w < V.length; w++) {
    let L = V[w];
    if (L & w8.YShortVector) {
      let A = $.uint8();
      Y += L & w8.YIsSameOrPositive ? A : -A;
    } else if (!(L & w8.YIsSameOrPositive))
      Y += $.int16();
    G.push(Y);
  }
  let j = [], E = 0;
  for (let w = 0;w < U.length; w++) {
    let L = U[w], A = [];
    while (E <= L) {
      let D = X[E], F = G[E], M = V[E];
      if (D === undefined || F === undefined || M === undefined)
        break;
      A.push({ x: D, y: F, onCurve: (M & w8.OnCurve) !== 0 }), E++;
    }
    j.push(A);
  }
  return { type: "simple", numberOfContours: Q, xMin: Z, yMin: K, xMax: q, yMax: J, contours: j, instructions: N };
}
function yH($, Q, Z, K, q, J) {
  let U = [], W;
  do {
    W = $.uint16();
    let _ = $.uint16(), N, V;
    if (W & A6.Arg1And2AreWords)
      if (W & A6.ArgsAreXYValues)
        N = $.int16(), V = $.int16();
      else
        N = $.uint16(), V = $.uint16();
    else if (W & A6.ArgsAreXYValues)
      N = $.int8(), V = $.int8();
    else
      N = $.uint8(), V = $.uint8();
    let X = 1, z = 0, G = 0, Y = 1;
    if (W & A6.WeHaveAScale)
      X = Y = $.f2dot14();
    else if (W & A6.WeHaveAnXAndYScale)
      X = $.f2dot14(), Y = $.f2dot14();
    else if (W & A6.WeHaveATwoByTwo)
      X = $.f2dot14(), z = $.f2dot14(), G = $.f2dot14(), Y = $.f2dot14();
    U.push({ glyphId: _, flags: W, arg1: N, arg2: V, transform: [X, z, G, Y] });
  } while (W & A6.MoreComponents);
  let H = new Uint8Array(0);
  if (W & A6.WeHaveInstructions) {
    let _ = $.uint16();
    H = $.bytes(_);
  }
  return { type: "composite", numberOfContours: Q, xMin: Z, yMin: K, xMax: q, yMax: J, components: U, instructions: H };
}
function bH($, Q, Z, K, q) {
  if ($ & A6.UnscaledComponentOffset)
    return false;
  if ($ & A6.ScaledComponentOffset)
    return true;
  return Q !== 1 || Z !== 0 || K !== 0 || q !== 1;
}
function mH($) {
  let Q = [];
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z];
    for (let q = 0;q < K.length; q++)
      Q.push(K[q]);
  }
  return Q;
}
function gH($, Q, Z, K, q, J, U) {
  let W = Array($.length);
  for (let H = 0;H < $.length; H++) {
    let _ = $[H], N = Math.round(Q * _.x + K * _.y + J), V = Math.round(Z * _.x + q * _.y + U), X = { x: N, y: V, onCurve: _.onCurve };
    if (_.cubic)
      X.cubic = true;
    W[H] = X;
  }
  return W;
}
function u7($, Q, Z, K) {
  let [q, J, U, W] = Z.transform, H = (Z.flags & A6.ArgsAreXYValues) !== 0, _ = mH(K), N = 0, V = 0;
  if (H) {
    if (N = Z.arg1, V = Z.arg2, bH(Z.flags, q, J, U, W)) {
      let X = q * N + U * V, z = J * N + W * V;
      N = X, V = z;
    }
    if (Z.flags & A6.RoundXYToGrid)
      N = Math.round(N), V = Math.round(V);
  } else {
    let { arg1: X, arg2: z } = Z;
    if (X >= 0 && X < Q.length && z >= 0 && z < _.length) {
      let G = Q[X], Y = _[z], j = q * Y.x + U * Y.y, E = J * Y.x + W * Y.y;
      N = G.x - j, V = G.y - E;
    }
  }
  for (let X = 0;X < K.length; X++) {
    let z = K[X], G = gH(z, q, J, U, W, N, V);
    $.push(G);
    for (let Y = 0;Y < G.length; Y++)
      Q.push(G[Y]);
  }
}
function w$($, Q, Z, K = 0) {
  if (K > 32)
    return [];
  let q = [], J = [];
  for (let U = 0;U < Z.components.length; U++) {
    let W = Z.components[U], H = A8($, Q, W.glyphId), _;
    if (H.type === "simple")
      _ = H.contours;
    else if (H.type === "composite")
      _ = w$($, Q, H, K + 1);
    else
      continue;
    u7(q, J, W, _);
  }
  return q;
}
var m7 = new WeakMap;
var x7 = 256;
function f7($) {
  let Q = m7.get($);
  if (!Q)
    Q = new Map, m7.set($, Q);
  return Q;
}
function l7($, Q, Z) {
  let K = A8($, Q, Z);
  if (K.type === "empty")
    return [];
  else if (K.type === "simple")
    return K.contours;
  else {
    let q = f7($), J = q.get(Z);
    if (J)
      return J;
    let U = w$($, Q, K);
    if (q.size >= x7) {
      let W = q.keys().next().value;
      if (W !== undefined)
        q.delete(W);
    }
    return q.set(Z, U), U;
  }
}
function d7($, Q, Z) {
  let K = A8($, Q, Z);
  if (K.type === "empty")
    return { contours: [], bounds: null };
  let q = { xMin: K.xMin, yMin: K.yMin, xMax: K.xMax, yMax: K.yMax };
  if (K.type === "simple")
    return { contours: K.contours, bounds: q };
  let J = f7($), U = J.get(Z);
  if (!U) {
    if (U = w$($, Q, K), J.size >= x7) {
      let X = J.keys().next().value;
      if (X !== undefined)
        J.delete(X);
    }
    J.set(Z, U);
  }
  if (U.length === 0)
    return { contours: U, bounds: null };
  let W = 1 / 0, H = 1 / 0, _ = -1 / 0, N = -1 / 0;
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    for (let G = 0;G < z.length; G++) {
      let Y = z[G];
      W = Math.min(W, Y.x), H = Math.min(H, Y.y), _ = Math.max(_, Y.x), N = Math.max(N, Y.y);
    }
  }
  return { contours: U, bounds: W === 1 / 0 ? null : { xMin: W, yMin: H, xMax: _, yMax: N } };
}
function n7($, Q, Z) {
  let K = A8($, Q, Z);
  if (K.type === "empty")
    return null;
  return { xMin: K.xMin, yMin: K.yMin, xMax: K.xMax, yMax: K.yMax };
}
function p7($, Q, Z, K) {
  let q = $.glyphVariationData[Q];
  if (!q)
    return Array(Z).fill({ x: 0, y: 0 });
  let J = Array(Z).fill(null).map(() => ({ x: 0, y: 0 }));
  for (let U = 0;U < q.tupleVariationHeaders.length; U++) {
    let W = q.tupleVariationHeaders[U];
    if (!W.peakTuple)
      continue;
    let H = u0(W.peakTuple, K, W.intermediateStartTuple, W.intermediateEndTuple);
    if (H === 0)
      continue;
    if (W.pointNumbers !== null)
      for (let _ = 0;_ < W.pointNumbers.length; _++) {
        let N = W.pointNumbers[_], V = J[N], X = W.deltas[_];
        if (N < Z && V && X)
          V.x += X.x * H, V.y += X.y * H;
      }
    else
      for (let _ = 0;_ < Math.min(W.deltas.length, Z); _++) {
        let N = J[_], V = W.deltas[_];
        if (N && V)
          N.x += V.x * H, N.y += V.y * H;
      }
  }
  for (let U = 0;U < J.length; U++) {
    let W = J[U];
    W.x = Math.round(W.x), W.y = Math.round(W.y);
  }
  return J;
}
function c7($, Q) {
  let Z = [], K = 0;
  for (let q = 0;q < $.length; q++) {
    let J = $[q], U = [];
    for (let W = 0;W < J.length; W++) {
      let H = J[W], _ = Q[K] ?? { x: 0, y: 0 };
      U.push({ x: H.x + _.x, y: H.y + _.y, onCurve: H.onCurve }), K++;
    }
    Z.push(U);
  }
  return Z;
}
function o7($, Q, Z, K, q) {
  let J = A8($, Q, K);
  if (J.type === "empty")
    return [];
  let U;
  if (J.type === "simple")
    U = J.contours;
  else
    U = i7($, Q, Z, J, q);
  if (Z && q && q.length > 0) {
    let W = 0;
    for (let _ = 0;_ < U.length; _++) {
      let N = U[_];
      W += N.length;
    }
    W += 4;
    let H = p7(Z, K, W, q);
    U = c7(U, H);
  }
  return U;
}
function i7($, Q, Z, K, q, J = 0) {
  if (J > 32)
    return [];
  let U = [], W = [];
  for (let H = 0;H < K.components.length; H++) {
    let _ = K.components[H], N = A8($, Q, _.glyphId), V;
    if (N.type === "simple") {
      if (V = N.contours, Z && q && q.length > 0) {
        let X = 0;
        for (let G = 0;G < V.length; G++) {
          let Y = V[G];
          X += Y.length;
        }
        X += 4;
        let z = p7(Z, _.glyphId, X, q);
        V = c7(V, z);
      }
    } else if (N.type === "composite")
      V = i7($, Q, Z, N, q, J + 1);
    else
      continue;
    u7(U, W, _, V);
  }
  return U;
}
function I6() {
  return [1, 0, 0, 1, 0, 0];
}
function A$() {
  return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
}
function t8($, Q) {
  return [1, 0, 0, 1, $, Q];
}
function S8($, Q) {
  return [$, 0, 0, Q, 0, 0];
}
function h8($) {
  let Q = Math.cos($), Z = Math.sin($);
  return [Q, Z, -Z, Q, 0, 0];
}
function y8($, Q) {
  return [1, Q, $, 1, 0, 0];
}
function b8($, Q) {
  return [$[0] * Q[0] + $[2] * Q[1], $[1] * Q[0] + $[3] * Q[1], $[0] * Q[2] + $[2] * Q[3], $[1] * Q[2] + $[3] * Q[3], $[0] * Q[4] + $[2] * Q[5] + $[4], $[1] * Q[4] + $[3] * Q[5] + $[5]];
}
function y9($, Q) {
  let Z = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let K = 0;K < 3; K++)
    for (let q = 0;q < 3; q++)
      Z[K][q] = $[K][0] * Q[0][q] + $[K][1] * Q[1][q] + $[K][2] * Q[2][q];
  return Z;
}
function Q6($, Q, Z) {
  return { x: Z[0] * $ + Z[2] * Q + Z[4], y: Z[1] * $ + Z[3] * Q + Z[5] };
}
function $6($, Q, Z) {
  let K = Z[2][0] * $ + Z[2][1] * Q + Z[2][2], q = 0.01;
  if (K < 0.01)
    return { x: (Z[0][0] * $ + Z[0][1] * Q + Z[0][2]) / 0.01, y: (Z[1][0] * $ + Z[1][1] * Q + Z[1][2]) / 0.01 };
  return { x: (Z[0][0] * $ + Z[0][1] * Q + Z[0][2]) / K, y: (Z[1][0] * $ + Z[1][1] * Q + Z[1][2]) / K };
}
function uH($, Q = 0, Z = 0) {
  let K = $.commands.map((J) => {
    switch (J.type) {
      case "M":
      case "L":
        return { type: J.type, x: -J.y + Q, y: J.x + Z };
      case "Q":
        return { type: "Q", x1: -J.y1 + Q, y1: J.x1 + Z, x: -J.y + Q, y: J.x + Z };
      case "C":
        return { type: "C", x1: -J.y1 + Q, y1: J.x1 + Z, x2: -J.y2 + Q, y2: J.x2 + Z, x: -J.y + Q, y: J.x + Z };
      case "Z":
        return { type: "Z" };
      default:
        return J;
    }
  }), q = null;
  if ($.bounds) {
    let J = $.bounds;
    if (q = { xMin: -J.yMax + Q, yMin: J.xMin + Z, xMax: -J.yMin + Q, yMax: J.xMax + Z }, q.xMin > q.xMax)
      [q.xMin, q.xMax] = [q.xMax, q.xMin];
    if (q.yMin > q.yMax)
      [q.yMin, q.yMax] = [q.yMax, q.yMin];
  }
  return { commands: K, bounds: q, flags: $.flags };
}
function xH($, Q, Z) {
  let K = Q >= 0 ? 1 << Q : 1 / (1 << -Q), q = Z >= 0 ? 1 << Z : 1 / (1 << -Z), J = $.commands.map((W) => {
    switch (W.type) {
      case "M":
      case "L":
        return { type: W.type, x: W.x * K, y: W.y * q };
      case "Q":
        return { type: "Q", x1: W.x1 * K, y1: W.y1 * q, x: W.x * K, y: W.y * q };
      case "C":
        return { type: "C", x1: W.x1 * K, y1: W.y1 * q, x2: W.x2 * K, y2: W.y2 * q, x: W.x * K, y: W.y * q };
      case "Z":
        return { type: "Z" };
      default:
        return W;
    }
  }), U = null;
  if ($.bounds)
    U = { xMin: $.bounds.xMin * K, yMin: $.bounds.yMin * q, xMax: $.bounds.xMax * K, yMax: $.bounds.yMax * q };
  return { commands: J, bounds: U, flags: $.flags };
}
function O6($, Q) {
  let Z = $.commands.map((q) => {
    switch (q.type) {
      case "M":
      case "L": {
        let J = Q6(q.x, q.y, Q);
        return { type: q.type, x: J.x, y: J.y };
      }
      case "Q": {
        let J = Q6(q.x1, q.y1, Q), U = Q6(q.x, q.y, Q);
        return { type: "Q", x1: J.x, y1: J.y, x: U.x, y: U.y };
      }
      case "C": {
        let J = Q6(q.x1, q.y1, Q), U = Q6(q.x2, q.y2, Q), W = Q6(q.x, q.y, Q);
        return { type: "C", x1: J.x, y1: J.y, x2: U.x, y2: U.y, x: W.x, y: W.y };
      }
      case "Z":
        return { type: "Z" };
      default:
        return q;
    }
  }), K = null;
  if ($.bounds) {
    let q = $.bounds, J = [Q6(q.xMin, q.yMin, Q), Q6(q.xMax, q.yMin, Q), Q6(q.xMin, q.yMax, Q), Q6(q.xMax, q.yMax, Q)];
    K = { xMin: Math.min(...J.map((U) => U.x)), yMin: Math.min(...J.map((U) => U.y)), xMax: Math.max(...J.map((U) => U.x)), yMax: Math.max(...J.map((U) => U.y)) };
  }
  return { commands: Z, bounds: K, flags: $.flags };
}
function b9($, Q) {
  let Z = $.commands.map((q) => {
    switch (q.type) {
      case "M":
      case "L": {
        let J = $6(q.x, q.y, Q);
        return { type: q.type, x: J.x, y: J.y };
      }
      case "Q": {
        let J = $6(q.x1, q.y1, Q), U = $6(q.x, q.y, Q);
        return { type: "Q", x1: J.x, y1: J.y, x: U.x, y: U.y };
      }
      case "C": {
        let J = $6(q.x1, q.y1, Q), U = $6(q.x2, q.y2, Q), W = $6(q.x, q.y, Q);
        return { type: "C", x1: J.x, y1: J.y, x2: U.x, y2: U.y, x: W.x, y: W.y };
      }
      case "Z":
        return { type: "Z" };
      default:
        return q;
    }
  }), K = null;
  if ($.bounds) {
    let q = $.bounds, J = [$6(q.xMin, q.yMin, Q), $6(q.xMax, q.yMin, Q), $6(q.xMin, q.yMax, Q), $6(q.xMax, q.yMax, Q)];
    K = { xMin: Math.min(...J.map((U) => U.x)), yMin: Math.min(...J.map((U) => U.y)), xMax: Math.max(...J.map((U) => U.x)), yMax: Math.max(...J.map((U) => U.y)) };
  }
  return { commands: Z, bounds: K, flags: $.flags };
}
function L$($) {
  let Q = 1 / 0, Z = 1 / 0, K = -1 / 0, q = -1 / 0;
  for (let J = 0;J < $.commands.length; J++) {
    let U = $.commands[J];
    switch (U.type) {
      case "M":
      case "L":
        Q = Math.min(Q, U.x), Z = Math.min(Z, U.y), K = Math.max(K, U.x), q = Math.max(q, U.y);
        break;
      case "Q":
        Q = Math.min(Q, U.x1, U.x), Z = Math.min(Z, U.y1, U.y), K = Math.max(K, U.x1, U.x), q = Math.max(q, U.y1, U.y);
        break;
      case "C":
        Q = Math.min(Q, U.x1, U.x2, U.x), Z = Math.min(Z, U.y1, U.y2, U.y), K = Math.max(K, U.x1, U.x2, U.x), q = Math.max(q, U.y1, U.y2, U.y);
        break;
    }
  }
  if (!Number.isFinite(Q))
    return { xMin: 0, yMin: 0, xMax: 0, yMax: 0 };
  return { xMin: Q, yMin: Z, xMax: K, yMax: q };
}
function s7($, Q, Z) {
  let K = $ - 2 * Q + Z;
  if (Math.abs(K) < 0.0000000001)
    return [];
  let q = ($ - Q) / K;
  if (q > 0 && q < 1)
    return [q];
  return [];
}
function r7($, Q, Z, K) {
  let q = -$ + 3 * Q - 3 * Z + K, J = 2 * ($ - 2 * Q + Z), U = -$ + Q, W = [];
  if (Math.abs(q) < 0.0000000001) {
    if (Math.abs(J) > 0.0000000001) {
      let H = -U / J;
      if (H > 0 && H < 1)
        W.push(H);
    }
  } else {
    let H = J * J - 4 * q * U;
    if (H >= 0) {
      let _ = Math.sqrt(H), N = (-J + _) / (2 * q), V = (-J - _) / (2 * q);
      if (N > 0 && N < 1)
        W.push(N);
      if (V > 0 && V < 1)
        W.push(V);
    }
  }
  return W;
}
function a7($, Q, Z, K) {
  let q = 1 - K;
  return q * q * $ + 2 * q * K * Q + K * K * Z;
}
function t7($, Q, Z, K, q) {
  let J = 1 - q;
  return J * J * J * $ + 3 * J * J * q * Q + 3 * J * q * q * Z + q * q * q * K;
}
function f0($) {
  let Q = 1 / 0, Z = 1 / 0, K = -1 / 0, q = -1 / 0, J = 0, U = 0;
  for (let W = 0;W < $.commands.length; W++) {
    let H = $.commands[W];
    switch (H.type) {
      case "M":
        J = H.x, U = H.y, Q = Math.min(Q, J), Z = Math.min(Z, U), K = Math.max(K, J), q = Math.max(q, U);
        break;
      case "L":
        J = H.x, U = H.y, Q = Math.min(Q, J), Z = Math.min(Z, U), K = Math.max(K, J), q = Math.max(q, U);
        break;
      case "Q": {
        Q = Math.min(Q, H.x), Z = Math.min(Z, H.y), K = Math.max(K, H.x), q = Math.max(q, H.y);
        let _ = s7(J, H.x1, H.x);
        for (let V = 0;V < _.length; V++) {
          let X = _[V], z = a7(J, H.x1, H.x, X);
          Q = Math.min(Q, z), K = Math.max(K, z);
        }
        let N = s7(U, H.y1, H.y);
        for (let V = 0;V < N.length; V++) {
          let X = N[V], z = a7(U, H.y1, H.y, X);
          Z = Math.min(Z, z), q = Math.max(q, z);
        }
        J = H.x, U = H.y;
        break;
      }
      case "C": {
        Q = Math.min(Q, H.x), Z = Math.min(Z, H.y), K = Math.max(K, H.x), q = Math.max(q, H.y);
        let _ = r7(J, H.x1, H.x2, H.x);
        for (let V = 0;V < _.length; V++) {
          let X = _[V], z = t7(J, H.x1, H.x2, H.x, X);
          Q = Math.min(Q, z), K = Math.max(K, z);
        }
        let N = r7(U, H.y1, H.y2, H.y);
        for (let V = 0;V < N.length; V++) {
          let X = N[V], z = t7(U, H.y1, H.y2, H.y, X);
          Z = Math.min(Z, z), q = Math.max(q, z);
        }
        J = H.x, U = H.y;
        break;
      }
    }
  }
  if (!Number.isFinite(Q))
    return { xMin: 0, yMin: 0, xMax: 0, yMax: 0 };
  return { xMin: Q, yMin: Z, xMax: K, yMax: q };
}
function fH($, Q, Z) {
  let K = Z;
  for (let q = 0;q < $.commands.length; q++) {
    let J = $.commands[q];
    switch (J.type) {
      case "M":
      case "L": {
        let U = $6(J.x, J.y, Q);
        K = Math.min(K, U.x);
        break;
      }
      case "Q": {
        let U = $6(J.x1, J.y1, Q), W = $6(J.x, J.y, Q);
        K = Math.min(K, U.x, W.x);
        break;
      }
      case "C": {
        let U = $6(J.x1, J.y1, Q), W = $6(J.x2, J.y2, Q), H = $6(J.x, J.y, Q);
        K = Math.min(K, U.x, W.x, H.x);
        break;
      }
    }
  }
  return K;
}
function lH($, Q, Z) {
  return O6($, t8(Q, Z));
}
function dH($, Q, Z = Q) {
  return O6($, S8(Q, Z));
}
function nH($, Q) {
  return O6($, h8(Q));
}
function pH($, Q) {
  let Z = Math.tan(Q * Math.PI / 180);
  return O6($, y8(Z, 0));
}
function F$($, Q, Z) {
  return [[1, 0, -$ * Z], [0, 1, -Q * Z], [0, 0, 1]];
}
function m9($) {
  let Q = [], Z = 1 / 0, K = 1 / 0, q = -1 / 0, J = -1 / 0;
  for (let W = 0;W < $.length; W++) {
    let H = $[W];
    if (Q.push(...H.commands), H.bounds)
      Z = Math.min(Z, H.bounds.xMin), K = Math.min(K, H.bounds.yMin), q = Math.max(q, H.bounds.xMax), J = Math.max(J, H.bounds.yMax);
  }
  let U = Number.isFinite(Z) ? { xMin: Z, yMin: K, xMax: q, yMax: J } : null;
  return { commands: Q, bounds: U };
}
function e8($) {
  return { commands: $.commands.map((Q) => ({ ...Q })), bounds: $.bounds ? { ...$.bounds } : null, flags: $.flags };
}
function B$($) {
  if ($.length === 0)
    return [];
  for (let Q = 0;Q < $.length; Q++) {
    let Z = $[Q];
    if (!Z.onCurve)
      return Z.cubic ? cH($) : e7($);
  }
  return e7($);
}
function cH($) {
  if ($.length === 0)
    return [];
  let Q = [], Z = 0, K = $[0];
  if (!K)
    return [];
  Q.push({ type: "M", x: K.x, y: K.y }), Z = 1;
  while (Z < $.length) {
    let q = $[Z];
    if (!q)
      break;
    if (q.onCurve)
      Q.push({ type: "L", x: q.x, y: q.y }), Z++;
    else if (q.cubic) {
      let J = q, U = $[Z + 1], W = $[Z + 2];
      if (!U || !W) {
        Z++;
        continue;
      }
      Q.push({ type: "C", x1: J.x, y1: J.y, x2: U.x, y2: U.y, x: W.x, y: W.y }), Z += 3;
    } else {
      let J = q, U = $[Z + 1];
      if (!U) {
        Z++;
        continue;
      }
      let W;
      if (U.onCurve)
        W = U, Z += 2;
      else
        W = { x: (J.x + U.x) / 2, y: (J.y + U.y) / 2, onCurve: true }, Z++;
      Q.push({ type: "Q", x1: J.x, y1: J.y, x: W.x, y: W.y });
    }
  }
  return Q.push({ type: "Z" }), Q;
}
function e7($) {
  if ($.length === 0)
    return [];
  let Q = [], Z = 0;
  for (let _ = 0;_ < $.length; _++)
    if ($[_].onCurve) {
      Z = _;
      break;
    }
  let K = $.every((_) => !_.onCurve), q;
  if (K) {
    let _ = $[0], N = $[$.length - 1];
    if (!_ || !N)
      return [];
    q = { x: (_.x + N.x) / 2, y: (_.y + N.y) / 2, onCurve: true }, Z = 0;
  } else {
    let _ = $[Z];
    if (!_)
      return [];
    q = _;
  }
  Q.push({ type: "M", x: q.x, y: q.y });
  let J = $.length, U = K ? 0 : Z + 1;
  if (U >= J)
    U = 0;
  let W = q, H = 0;
  while (H < J) {
    let _ = $[U];
    if (!_)
      break;
    if (_.onCurve)
      Q.push({ type: "L", x: _.x, y: _.y }), W = _;
    else {
      let N = U + 1 < J ? U + 1 : 0, V = $[N];
      if (!V)
        break;
      let X;
      if (V.onCurve)
        X = V, U = N, H++;
      else
        X = { x: (_.x + V.x) / 2, y: (_.y + V.y) / 2, onCurve: true };
      Q.push({ type: "Q", x1: _.x, y1: _.y, x: X.x, y: X.y }), W = X;
    }
    if (U++, U >= J)
      U = 0;
    if (H++, W.x === q.x && W.y === q.y)
      break;
  }
  return Q.push({ type: "Z" }), Q;
}
var D$ = new WeakMap;
function y6($, Q) {
  let Z = D$.get($);
  if (Z) {
    let U = Z.get(Q);
    if (U !== undefined)
      return U;
  }
  let K = $.getGlyphContoursAndBounds(Q);
  if (!K) {
    if (!Z)
      Z = new Map, D$.set($, Z);
    return Z.set(Q, null), null;
  }
  let q = [];
  for (let U = 0;U < K.contours.length; U++) {
    let W = K.contours[U];
    q.push(...B$(W));
  }
  let J = { commands: q, bounds: K.bounds };
  if (!Z)
    Z = new Map, D$.set($, Z);
  return Z.set(Q, J), J;
}
var j6 = 10;
var $K = new WeakMap;
function L8($, Q) {
  let Z = Q?.scale ?? 1, K = Q?.flipY ?? true;
  if (Z === 1 && K) {
    let W = $K.get($);
    if (W)
      return W;
  }
  let q = Z * j6, J = K ? -q : q, U = "";
  for (let W = 0;W < $.commands.length; W++) {
    let H = $.commands[W];
    if (W > 0)
      U += " ";
    switch (H.type) {
      case "M":
        U += "M " + Math.round(H.x * q) + " " + Math.round(H.y * J);
        break;
      case "L":
        U += "L " + Math.round(H.x * q) + " " + Math.round(H.y * J);
        break;
      case "Q":
        U += "Q " + Math.round(H.x1 * q) + " " + Math.round(H.y1 * J) + " " + Math.round(H.x * q) + " " + Math.round(H.y * J);
        break;
      case "C":
        U += "C " + Math.round(H.x1 * q) + " " + Math.round(H.y1 * J) + " " + Math.round(H.x2 * q) + " " + Math.round(H.y2 * J) + " " + Math.round(H.x * q) + " " + Math.round(H.y * J);
        break;
      case "Z":
        U += "Z";
        break;
    }
  }
  if (Z === 1 && K)
    $K.set($, U);
  return U;
}
function $9($, Q, Z) {
  let K = Z?.scale ?? 1, q = Z?.flipY ?? true, J = Z?.offsetX ?? 0, U = Z?.offsetY ?? 0;
  for (let W = 0;W < Q.commands.length; W++) {
    let H = Q.commands[W];
    switch (H.type) {
      case "M":
        $.moveTo(H.x * K + J, (q ? -H.y : H.y) * K + U);
        break;
      case "L":
        $.lineTo(H.x * K + J, (q ? -H.y : H.y) * K + U);
        break;
      case "Q":
        $.quadraticCurveTo(H.x1 * K + J, (q ? -H.y1 : H.y1) * K + U, H.x * K + J, (q ? -H.y : H.y) * K + U);
        break;
      case "C":
        $.bezierCurveTo(H.x1 * K + J, (q ? -H.y1 : H.y1) * K + U, H.x2 * K + J, (q ? -H.y2 : H.y2) * K + U, H.x * K + J, (q ? -H.y : H.y) * K + U);
        break;
      case "Z":
        $.closePath();
        break;
    }
  }
}
function oH($, Q, Z) {
  let K = y6($, Q);
  if (!K)
    return null;
  let q = Z?.fontSize ?? 100, J = Z?.fill ?? "currentColor", U = Z?.stroke, W = Z?.strokeWidth ?? 1, H = q / $.unitsPerEm, _ = K.bounds;
  if (!_)
    return null;
  let N = U ? W / 2 : 0, V = Math.ceil((_.xMax - _.xMin) * H + N * 2), X = Math.ceil((_.yMax - _.yMin) * H + N * 2), z = Math.round((_.xMin - N) * j6), G = Math.round((-_.yMax - N) * j6), Y = Math.round((_.xMax - _.xMin + N * 2) * j6), j = Math.round((_.yMax - _.yMin + N * 2) * j6), E = `${z} ${G} ${Y} ${j}`, w = L8(K, { flipY: true, scale: 1 }), L = U ? ` stroke="${U}" stroke-width="${W * j6}"` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${V}" height="${X}" viewBox="${E}">
  <path d="${w}" fill="${J}"${L}/>
</svg>`;
}
function QK($, Q, Z, K) {
  let q = K?.fontSize ?? 16, J = K?.x ?? 0, U = K?.y ?? 0, W = K?.fill ?? "black", H = K?.stroke, _ = K?.strokeWidth ?? 1, N = K?.matrix, V = K?.matrix3D, X = q / Q.unitsPerEm;
  if ($.fillStyle = W, H) {
    if ($.strokeStyle = H, $.lineWidth = _ * X, K?.lineCap)
      $.lineCap = K.lineCap;
    if (K?.lineJoin)
      $.lineJoin = K.lineJoin;
  }
  let z = J, G = U;
  for (let Y = 0;Y < Z.length; Y++) {
    let j = Z[Y], E = y6(Q, j.glyphId);
    if (E) {
      if ($.beginPath(), V) {
        let w = z + j.xOffset * X, L = G - j.yOffset * X, A = [[V[0][0] * X, V[0][1] * X, V[0][0] * w + V[0][1] * L + V[0][2]], [V[1][0] * X, V[1][1] * X, V[1][0] * w + V[1][1] * L + V[1][2]], [V[2][0] * X, V[2][1] * X, V[2][0] * w + V[2][1] * L + V[2][2]]];
        d0($, E, A);
      } else if (N) {
        let w = z + j.xOffset * X, L = G - j.yOffset * X, A = [N[0] * X, N[1] * X, N[2] * X, N[3] * X, N[0] * w + N[2] * L + N[4], N[1] * w + N[3] * L + N[5]];
        l0($, E, A);
      } else
        $9($, E, { scale: X, flipY: true, offsetX: z + j.xOffset * X, offsetY: G - j.yOffset * X });
      if (W !== "none")
        $.fill();
      if (H)
        $.stroke();
    }
    z += j.xAdvance * X, G += j.yAdvance * X;
  }
}
function ZK($, Q, Z) {
  let K = Z?.fontSize ?? 100, q = Z?.fill ?? "currentColor", J = Z?.stroke, U = Z?.strokeWidth ?? 1, W = Z?.matrix, H = Z?.matrix3D, _ = Z?.useNativeTransform ?? false, N = K / $.unitsPerEm, V = "", X = 0, z = 0, G = 1 / 0, Y = -1 / 0, j = 1 / 0, E = -1 / 0;
  for (let v = 0;v < Q.length; v++) {
    let f = Q[v], l = y6($, f.glyphId);
    if (l?.bounds) {
      let k = X + f.xOffset * N, O = z - f.yOffset * N, h;
      if (H && !_) {
        let S = [[H[0][0] * N, H[0][1] * N, H[0][0] * k + H[0][1] * O + H[0][2]], [H[1][0] * N, H[1][1] * N, H[1][0] * k + H[1][1] * O + H[1][2]], [H[2][0] * N, H[2][1] * N, H[2][0] * k + H[2][1] * O + H[2][2]]];
        h = UK(l, S);
        let d = l.bounds, o = [$6(d.xMin * N + k, -d.yMax * N + O, H), $6(d.xMax * N + k, -d.yMax * N + O, H), $6(d.xMin * N + k, -d.yMin * N + O, H), $6(d.xMax * N + k, -d.yMin * N + O, H)];
        for (let n = 0;n < o.length; n++) {
          let e = o[n];
          G = Math.min(G, e.x), Y = Math.max(Y, e.x), j = Math.min(j, e.y), E = Math.max(E, e.y);
        }
      } else if (W && !_) {
        let S = [W[0] * N, W[1] * N, W[2] * N, W[3] * N, W[0] * k + W[2] * O + W[4], W[1] * k + W[3] * O + W[5]];
        h = JK(l, S);
        let d = l.bounds, o = [Q6(d.xMin * N + k, -d.yMax * N + O, W), Q6(d.xMax * N + k, -d.yMax * N + O, W), Q6(d.xMin * N + k, -d.yMin * N + O, W), Q6(d.xMax * N + k, -d.yMin * N + O, W)];
        for (let n = 0;n < o.length; n++) {
          let e = o[n];
          G = Math.min(G, e.x), Y = Math.max(Y, e.x), j = Math.min(j, e.y), E = Math.max(E, e.y);
        }
      } else {
        h = HK(l, N, k, O);
        let S = l.bounds;
        G = Math.min(G, k + S.xMin * N), Y = Math.max(Y, k + S.xMax * N), j = Math.min(j, O - S.yMax * N), E = Math.max(E, O - S.yMin * N);
      }
      if (V)
        V += " ";
      V += h;
    }
    X += f.xAdvance * N, z += f.yAdvance * N;
  }
  if (!V)
    return '<svg xmlns="http://www.w3.org/2000/svg"></svg>';
  let w = J ? U / 2 : 0, L = Math.ceil(Y - G + w * 2), A = Math.ceil(E - j + w * 2), D = Math.floor((G - w) * j6), F = Math.floor((j - w) * j6), M = Math.ceil(L * j6), R = Math.ceil(A * j6), P = `${D} ${F} ${M} ${R}`, T = J ? ` stroke="${J}" stroke-width="${U * j6}"${Z?.lineCap ? ` stroke-linecap="${Z.lineCap}"` : ""}${Z?.lineJoin ? ` stroke-linejoin="${Z.lineJoin}"` : ""}` : "", m = _ && W ? ` transform="${WK(W)}"` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${L}" height="${A}" viewBox="${P}">
  <path d="${V}" fill="${q}"${T}${m}/>
</svg>`;
}
function iH($) {
  let Q = [];
  for (let Z = 0;Z < $.infos.length; Z++) {
    let K = $.infos[Z], q = $.positions[Z];
    if (!q)
      continue;
    Q.push({ glyphId: K.glyphId, xOffset: q.xOffset, yOffset: q.yOffset, xAdvance: q.xAdvance, yAdvance: q.yAdvance });
  }
  return Q;
}
function g9($, Q, Z) {
  let K = $.getGlyphContoursWithVariation(Q, Z);
  if (!K)
    return null;
  let q = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    q.push(...B$(W));
  }
  let J = $.getGlyphBounds(Q);
  return { commands: q, bounds: J };
}
function KK($, Q, Z, K, q) {
  let J = q?.fontSize ?? 16, U = q?.x ?? 0, W = q?.y ?? 0, H = q?.fill ?? "black", _ = q?.stroke, N = q?.strokeWidth ?? 1, V = J / Q.unitsPerEm;
  if ($.fillStyle = H, _) {
    if ($.strokeStyle = _, $.lineWidth = N * V, q?.lineCap)
      $.lineCap = q.lineCap;
    if (q?.lineJoin)
      $.lineJoin = q.lineJoin;
  }
  let X = U, z = W;
  for (let G = 0;G < Z.length; G++) {
    let Y = Z[G], j = g9(Q, Y.glyphId, K);
    if (j) {
      if ($.beginPath(), $9($, j, { scale: V, flipY: true, offsetX: X + Y.xOffset * V, offsetY: z - Y.yOffset * V }), H !== "none")
        $.fill();
      if (_)
        $.stroke();
    }
    X += Y.xAdvance * V, z += Y.yAdvance * V;
  }
}
function qK($, Q, Z, K) {
  let q = K?.fontSize ?? 100, J = K?.fill ?? "currentColor", U = K?.stroke, W = K?.strokeWidth ?? 1, H = q / $.unitsPerEm, _ = "", N = 0, V = 0, X = 1 / 0, z = -1 / 0, G = 1 / 0, Y = -1 / 0;
  for (let P = 0;P < Q.length; P++) {
    let T = Q[P], m = g9($, T.glyphId, Z);
    if (m?.bounds) {
      let v = N + T.xOffset * H, f = V - T.yOffset * H, l = HK(m, H, v, f);
      if (_)
        _ += " ";
      _ += l;
      let k = m.bounds;
      X = Math.min(X, v + k.xMin * H), z = Math.max(z, v + k.xMax * H), G = Math.min(G, f - k.yMax * H), Y = Math.max(Y, f - k.yMin * H);
    }
    N += T.xAdvance * H, V += T.yAdvance * H;
  }
  if (!_)
    return '<svg xmlns="http://www.w3.org/2000/svg"></svg>';
  let j = U ? W / 2 : 0, E = Math.ceil(z - X + j * 2), w = Math.ceil(Y - G + j * 2), L = Math.floor((X - j) * j6), A = Math.floor((G - j) * j6), D = Math.ceil(E * j6), F = Math.ceil(w * j6), M = `${L} ${A} ${D} ${F}`, R = U ? ` stroke="${U}" stroke-width="${W * j6}"${K?.lineCap ? ` stroke-linecap="${K.lineCap}"` : ""}${K?.lineJoin ? ` stroke-linejoin="${K.lineJoin}"` : ""}` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${E}" height="${w}" viewBox="${M}">
  <path d="${_}" fill="${J}"${R}/>
</svg>`;
}
function sH($, Q, Z) {
  let K = Z / Q.unitsPerEm, q = 0;
  for (let J = 0;J < $.length; J++) {
    let U = $[J];
    q += U.xAdvance;
  }
  return q * K;
}
function R$($, Q) {
  let Z = new Path2D;
  return $9(Z, $, Q), Z;
}
function l0($, Q, Z, K) {
  let J = K?.flipY ?? true ? -1 : 1;
  for (let U = 0;U < Q.commands.length; U++) {
    let W = Q.commands[U];
    switch (W.type) {
      case "M": {
        let H = Q6(W.x, W.y * J, Z);
        $.moveTo(H.x, H.y);
        break;
      }
      case "L": {
        let H = Q6(W.x, W.y * J, Z);
        $.lineTo(H.x, H.y);
        break;
      }
      case "Q": {
        let H = Q6(W.x1, W.y1 * J, Z), _ = Q6(W.x, W.y * J, Z);
        $.quadraticCurveTo(H.x, H.y, _.x, _.y);
        break;
      }
      case "C": {
        let H = Q6(W.x1, W.y1 * J, Z), _ = Q6(W.x2, W.y2 * J, Z), N = Q6(W.x, W.y * J, Z);
        $.bezierCurveTo(H.x, H.y, _.x, _.y, N.x, N.y);
        break;
      }
      case "Z":
        $.closePath();
        break;
    }
  }
}
function JK($, Q, Z) {
  let q = Z?.flipY ?? true ? -1 : 1, J = "";
  for (let U = 0;U < $.commands.length; U++) {
    let W = $.commands[U];
    if (U > 0)
      J += " ";
    switch (W.type) {
      case "M": {
        let H = Q6(W.x, W.y * q, Q);
        J += "M " + H.x + " " + H.y;
        break;
      }
      case "L": {
        let H = Q6(W.x, W.y * q, Q);
        J += "L " + H.x + " " + H.y;
        break;
      }
      case "Q": {
        let H = Q6(W.x1, W.y1 * q, Q), _ = Q6(W.x, W.y * q, Q);
        J += "Q " + H.x + " " + H.y + " " + _.x + " " + _.y;
        break;
      }
      case "C": {
        let H = Q6(W.x1, W.y1 * q, Q), _ = Q6(W.x2, W.y2 * q, Q), N = Q6(W.x, W.y * q, Q);
        J += "C " + H.x + " " + H.y + " " + _.x + " " + _.y + " " + N.x + " " + N.y;
        break;
      }
      case "Z":
        J += "Z";
        break;
    }
  }
  return J;
}
function d0($, Q, Z, K) {
  let J = K?.flipY ?? true ? -1 : 1;
  for (let U = 0;U < Q.commands.length; U++) {
    let W = Q.commands[U];
    switch (W.type) {
      case "M": {
        let H = $6(W.x, W.y * J, Z);
        $.moveTo(H.x, H.y);
        break;
      }
      case "L": {
        let H = $6(W.x, W.y * J, Z);
        $.lineTo(H.x, H.y);
        break;
      }
      case "Q": {
        let H = $6(W.x1, W.y1 * J, Z), _ = $6(W.x, W.y * J, Z);
        $.quadraticCurveTo(H.x, H.y, _.x, _.y);
        break;
      }
      case "C": {
        let H = $6(W.x1, W.y1 * J, Z), _ = $6(W.x2, W.y2 * J, Z), N = $6(W.x, W.y * J, Z);
        $.bezierCurveTo(H.x, H.y, _.x, _.y, N.x, N.y);
        break;
      }
      case "Z":
        $.closePath();
        break;
    }
  }
}
function UK($, Q, Z) {
  let q = Z?.flipY ?? true ? -1 : 1, J = "";
  for (let U = 0;U < $.commands.length; U++) {
    let W = $.commands[U];
    if (U > 0)
      J += " ";
    switch (W.type) {
      case "M": {
        let H = $6(W.x, W.y * q, Q);
        J += "M " + H.x + " " + H.y;
        break;
      }
      case "L": {
        let H = $6(W.x, W.y * q, Q);
        J += "L " + H.x + " " + H.y;
        break;
      }
      case "Q": {
        let H = $6(W.x1, W.y1 * q, Q), _ = $6(W.x, W.y * q, Q);
        J += "Q " + H.x + " " + H.y + " " + _.x + " " + _.y;
        break;
      }
      case "C": {
        let H = $6(W.x1, W.y1 * q, Q), _ = $6(W.x2, W.y2 * q, Q), N = $6(W.x, W.y * q, Q);
        J += "C " + H.x + " " + H.y + " " + _.x + " " + _.y + " " + N.x + " " + N.y;
        break;
      }
      case "Z":
        J += "Z";
        break;
    }
  }
  return J;
}
function rH($, Q) {
  $.transform(Q[0], Q[1], Q[2], Q[3], Q[4], Q[5]);
}
function WK($) {
  return `matrix(${$[0]} ${$[1]} ${$[2]} ${$[3]} ${$[4]} ${$[5]})`;
}
function HK($, Q, Z, K) {
  let q = "", J = Q * j6, U = -Q * j6, W = Z * j6, H = K * j6;
  for (let _ = 0;_ < $.commands.length; _++) {
    let N = $.commands[_];
    if (_ > 0)
      q += " ";
    switch (N.type) {
      case "M":
        q += "M " + Math.round(N.x * J + W) + " " + Math.round(N.y * U + H);
        break;
      case "L":
        q += "L " + Math.round(N.x * J + W) + " " + Math.round(N.y * U + H);
        break;
      case "Q":
        q += "Q " + Math.round(N.x1 * J + W) + " " + Math.round(N.y1 * U + H) + " " + Math.round(N.x * J + W) + " " + Math.round(N.y * U + H);
        break;
      case "C":
        q += "C " + Math.round(N.x1 * J + W) + " " + Math.round(N.y1 * U + H) + " " + Math.round(N.x2 * J + W) + " " + Math.round(N.y2 * U + H) + " " + Math.round(N.x * J + W) + " " + Math.round(N.y * U + H);
        break;
      case "Z":
        q += "Z";
        break;
    }
  }
  return q;
}
var W6 = 8;
var Z6 = 256;
var aH = 255;
var tH = 6;
var eH = 64;
var $4 = 16;
var Q4 = 65536;
function Z4($) {
  return Math.round($ * 64);
}
function K4($) {
  return $ / 64;
}
function q4($, Q) {
  return Math.round($ * Q * 256);
}
function t6($) {
  return $ >> 8;
}
function m8($) {
  return $ & 255;
}
function J4($) {
  return $ + 128 >> 8;
}
function U4($) {
  return $ & -256;
}
function W4($) {
  return $ + 255 & -256;
}
function H4($) {
  return $ << 2;
}
function _4($) {
  return $ >> 2;
}
function M$($, Q, Z) {
  if (Z === 0)
    return 0;
  let K = $ < 0 ? -$ : $, q = Q < 0 ? -Q : Q;
  if (K < 8388607 && q < 8388607)
    return Math.trunc($ * Q / Z);
  return Number(BigInt($) * BigInt(Q) / BigInt(Z));
}
function N4($, Q) {
  return M$($, Q, 65536);
}
function V4($, Q) {
  if (Q === 0)
    return 0;
  return M$($, 65536, Q);
}
function _K($, Q) {
  return $ = l6($), Q = l6(Q), $ > Q ? $ + (3 * Q >> 3) : Q + (3 * $ >> 3);
}
function X4($, Q) {
  return _K($, Q);
}
function z4($, Q) {
  let Z = Math.sqrt($ * $ + Q * Q);
  if (Z === 0)
    return { x: 65536, y: 0 };
  return { x: Math.round($ / Z * 65536), y: Math.round(Q / Z * 65536) };
}
function G4($, Q, Z) {
  return $ < Q ? Q : $ > Z ? Z : $;
}
function l6($) {
  return $ < 0 ? -$ : $;
}
function Y4($) {
  return $ < 0 ? -1 : $ > 0 ? 1 : 0;
}
var j4 = 16384;
var NK = 2147483647;

class g8 extends Error {
  constructor() {
    super("Cell pool overflow");
    this.name = "PoolOverflowError";
  }
}

class T$ {
  pool;
  poolSize;
  freeIndex;
  ycells;
  bandMinY = 0;
  bandMaxY = 0;
  minY = 1 / 0;
  maxY = -1 / 0;
  minX = 1 / 0;
  maxX = -1 / 0;
  currentX = 0;
  currentY = 0;
  currentCellIndex = -1;
  clipMinX = -1 / 0;
  clipMinY = -1 / 0;
  clipMaxX = 1 / 0;
  clipMaxY = 1 / 0;
  nullCellIndex;
  bandSet = false;
  constructor($ = j4) {
    this.poolSize = $, this.nullCellIndex = $ - 1, this.pool = Array($);
    for (let Q = 0;Q < $; Q++)
      this.pool[Q] = { x: 0, area: 0, cover: 0, next: -1 };
    this.pool[this.nullCellIndex].x = NK, this.pool[this.nullCellIndex].next = -1, this.ycells = [], this.freeIndex = 0, this.bandMinY = -1e4, this.bandMaxY = 1e4;
  }
  setClip($, Q, Z, K) {
    this.clipMinX = $, this.clipMinY = Q, this.clipMaxX = Z, this.clipMaxY = K;
  }
  setBandBounds($, Q) {
    this.bandMinY = $, this.bandMaxY = Q, this.bandSet = true;
    let Z = Q - $;
    if (this.ycells.length < Z)
      this.ycells = Array(Z);
    for (let K = 0;K < Z; K++)
      this.ycells[K] = this.nullCellIndex;
    this.freeIndex = 0;
  }
  reset() {
    if (this.freeIndex = 0, this.pool[this.nullCellIndex].x = NK, this.pool[this.nullCellIndex].area = 0, this.pool[this.nullCellIndex].cover = 0, this.pool[this.nullCellIndex].next = -1, this.bandSet)
      for (let $ = 0;$ < this.ycells.length; $++)
        this.ycells[$] = this.nullCellIndex;
    else
      this.ycells = [], this.bandMinY = -1e5, this.bandMaxY = 1e5;
    this.minY = 1 / 0, this.maxY = -1 / 0, this.minX = 1 / 0, this.maxX = -1 / 0, this.currentCellIndex = -1;
  }
  setCurrentCell($, Q) {
    let Z = t6($), K = t6(Q);
    if (this.currentCellIndex >= 0 && this.currentX === Z && this.currentY === K)
      return;
    if (K < this.clipMinY || K >= this.clipMaxY || Z < this.clipMinX || Z >= this.clipMaxX || this.bandSet && (K < this.bandMinY || K >= this.bandMaxY)) {
      this.currentCellIndex = this.nullCellIndex, this.currentX = Z, this.currentY = K;
      return;
    }
    this.currentX = Z, this.currentY = K, this.currentCellIndex = this.findOrCreateCell(Z, K), this.minY = Math.min(this.minY, K), this.maxY = Math.max(this.maxY, K), this.minX = Math.min(this.minX, Z), this.maxX = Math.max(this.maxX, Z);
  }
  findOrCreateCell($, Q) {
    if (!this.bandSet)
      this.ensureYCellsCapacity(Q);
    let Z = Q - this.bandMinY;
    if (Z < 0 || Z >= this.ycells.length)
      return this.nullCellIndex;
    let K = -1, q = this.ycells[Z];
    while (q !== this.nullCellIndex) {
      let W = this.pool[q];
      if (W.x === $)
        return q;
      if (W.x > $)
        break;
      K = q, q = W.next;
    }
    if (this.freeIndex >= this.nullCellIndex)
      throw new g8;
    let J = this.freeIndex++, U = this.pool[J];
    if (U.x = $, U.area = 0, U.cover = 0, U.next = q, K === -1)
      this.ycells[Z] = J;
    else
      this.pool[K].next = J;
    return J;
  }
  ensureYCellsCapacity($) {
    if (this.ycells.length === 0) {
      this.bandMinY = Math.min($, 0), this.bandMaxY = Math.max($ + 1, 256);
      let Q = this.bandMaxY - this.bandMinY;
      this.ycells = Array(Q);
      for (let Z = 0;Z < Q; Z++)
        this.ycells[Z] = this.nullCellIndex;
      return;
    }
    if ($ < this.bandMinY) {
      let Q = this.bandMinY - $, Z = Array(this.ycells.length + Q);
      for (let K = 0;K < Q; K++)
        Z[K] = this.nullCellIndex;
      for (let K = 0;K < this.ycells.length; K++)
        Z[Q + K] = this.ycells[K];
      this.ycells = Z, this.bandMinY = $;
    } else if ($ >= this.bandMaxY) {
      let Q = $ + 1, Z = this.ycells.length, K = Q - this.bandMinY;
      if (K > Z) {
        let q = Array(K);
        for (let J = 0;J < Z; J++)
          q[J] = this.ycells[J];
        for (let J = Z;J < K; J++)
          q[J] = this.nullCellIndex;
        this.ycells = q;
      }
      this.bandMaxY = Q;
    }
  }
  addArea($, Q) {
    if (this.currentCellIndex >= 0) {
      let Z = this.pool[this.currentCellIndex];
      Z.area += $, Z.cover += Q;
    }
  }
  getArea() {
    if (this.currentCellIndex >= 0)
      return this.pool[this.currentCellIndex]?.area;
    return 0;
  }
  getCover() {
    if (this.currentCellIndex >= 0)
      return this.pool[this.currentCellIndex]?.cover;
    return 0;
  }
  getCellsForRow($) {
    let Q = $ - this.bandMinY;
    if (Q < 0 || Q >= this.ycells.length)
      return [];
    let Z = [], K = this.ycells[Q];
    while (K !== this.nullCellIndex)
      Z.push(this.pool[K]), K = this.pool[K].next;
    return Z;
  }
  *iterateCells() {
    for (let $ = 0;$ < this.ycells.length; $++) {
      let Q = this.bandMinY + $, Z = this.ycells[$];
      if (Z === this.nullCellIndex)
        continue;
      let K = [];
      while (Z !== this.nullCellIndex)
        K.push(this.pool[Z]), Z = this.pool[Z].next;
      if (K.length > 0)
        yield { y: Q, cells: K };
    }
  }
  *iterateScanlines() {
    for (let $ = 0;$ < this.ycells.length; $++) {
      let Q = this.ycells[$];
      if (Q !== this.nullCellIndex)
        yield { y: this.bandMinY + $, firstCellIndex: Q };
    }
  }
  getPool() {
    return this.pool;
  }
  getNullIndex() {
    return this.nullCellIndex;
  }
  getYCells() {
    return this.ycells;
  }
  getBandMinY() {
    return this.bandMinY;
  }
  *iterateRowCells($) {
    let Q = $ - this.bandMinY;
    if (Q < 0 || Q >= this.ycells.length)
      return;
    let Z = this.ycells[Q];
    while (Z !== this.nullCellIndex)
      yield this.pool[Z], Z = this.pool[Z].next;
  }
  getCellCount() {
    return this.freeIndex;
  }
  isNearCapacity() {
    return this.freeIndex > this.poolSize * 0.9;
  }
}
G6();
var E4 = 32;
var XK = 16;

class Q9 {
  cells;
  x = 0;
  y = 0;
  minX = 0;
  minY = 0;
  maxX = 0;
  maxY = 0;
  constructor() {
    this.cells = new T$;
  }
  setClip($, Q, Z, K) {
    this.minX = $, this.minY = Q, this.maxX = Z, this.maxY = K, this.cells.setClip($, Q, Z, K);
  }
  setBandBounds($, Q) {
    this.cells.setBandBounds($, Q);
  }
  reset() {
    this.cells.reset(), this.x = 0, this.y = 0;
  }
  moveTo($, Q) {
    this.x = $, this.y = Q, this.cells.setCurrentCell($, Q);
  }
  lineTo($, Q) {
    this.renderLine($, Q), this.x = $, this.y = Q;
  }
  renderLine($, Q) {
    let Z = t6(this.y), K = t6(Q);
    if (Z >= this.maxY && K >= this.maxY || Z < this.minY && K < this.minY)
      return;
    let q = m8(this.y), J = m8(Q);
    if (Z === K) {
      this.renderScanline(Z, this.x, q, $, J);
      return;
    }
    let U = $ - this.x, W = Q - this.y;
    if (U === 0) {
      let j = t6(this.x), E = m8(this.x) * 2, w, L;
      if (W > 0)
        w = Z6, L = 1;
      else
        w = 0, L = -1;
      let A = w - q;
      this.cells.setCurrentCell(this.x, Z << W6), this.cells.addArea(A * E, A), Z += L, this.cells.setCurrentCell(this.x, Z << W6), A = w + w - Z6;
      while (Z !== K)
        this.cells.addArea(A * E, A), Z += L, this.cells.setCurrentCell(this.x, Z << W6);
      A = J - Z6 + w, this.cells.addArea(A * E, A);
      return;
    }
    let H = this.x, _, N, V, X = l6(W);
    if (W > 0)
      N = Z6, _ = 1, V = (Z6 - q) * U;
    else
      N = 0, _ = -1, V = q * U;
    let z = Math.trunc(V / X), G = V % X;
    if (G < 0)
      z--, G += X;
    let Y = H + z;
    if (this.renderScanline(Z, H, q, Y, N), H = Y, Z += _, this.cells.setCurrentCell(H, Z << W6), Z !== K) {
      V = Z6 * U;
      let j = Math.trunc(V / X), E = V % X;
      if (E < 0)
        j--, E += X;
      while (Z !== K) {
        if (z = j, G += E, G >= X)
          G -= X, z++;
        Y = H + z, this.renderScanline(Z, H, Z6 - N, Y, N), H = Y, Z += _, this.cells.setCurrentCell(H, Z << W6);
      }
    }
    this.renderScanline(Z, H, Z6 - N, $, J);
  }
  renderScanline($, Q, Z, K, q) {
    let J = t6(Q), U = t6(K);
    if (Z === q) {
      this.cells.setCurrentCell(K, $ << W6);
      return;
    }
    let W = m8(Q), H = m8(K);
    if (J === U) {
      let L = q - Z;
      this.cells.setCurrentCell(Q, $ << W6), this.cells.addArea(L * (W + H), L);
      return;
    }
    let _ = K - Q, N = q - Z, V = l6(_), X, z, G;
    if (_ > 0)
      X = Z6, z = 1, G = (Z6 - W) * N;
    else
      X = 0, z = -1, G = W * N;
    let Y = Math.trunc(G / V), j = G % V;
    if (j < 0)
      Y--, j += V;
    this.cells.setCurrentCell(Q, $ << W6), this.cells.addArea(Y * (W + X), Y);
    let E = Z + Y, w = J + z;
    if (this.cells.setCurrentCell(w << W6, $ << W6), w !== U) {
      G = Z6 * N;
      let L = Math.trunc(G / V), A = G % V;
      if (A < 0)
        L--, A += V;
      while (w !== U) {
        if (Y = L, j += A, j >= V)
          j -= V, Y++;
        this.cells.addArea(Y * Z6, Y), E += Y, w += z, this.cells.setCurrentCell(w << W6, $ << W6);
      }
    }
    Y = q - E, this.cells.addArea(Y * (H + Z6 - X), Y);
  }
  mulDiv($, Q, Z) {
    if (Z === 0)
      return 0;
    return Math.trunc($ * Q / Z);
  }
  conicTo($, Q, Z, K) {
    this.subdivConic(this.x, this.y, $, Q, Z, K, 0), this.x = Z, this.y = K;
  }
  subdivConic($, Q, Z, K, q, J, U) {
    if (U > 16) {
      this.renderLine(q, J), this.x = q, this.y = J;
      return;
    }
    let W = l6($ + q - 2 * Z), H = l6(Q + J - 2 * K);
    if (W < H)
      W = H;
    if (W <= Z6 >> 2) {
      this.renderLine(q, J), this.x = q, this.y = J;
      return;
    }
    let _ = $ + Z >> 1, N = Q + K >> 1, V = Z + q >> 1, X = K + J >> 1, z = _ + V >> 1, G = N + X >> 1;
    this.subdivConic($, Q, _, N, z, G, U + 1), this.subdivConic(z, G, V, X, q, J, U + 1);
  }
  cubicTo($, Q, Z, K, q, J) {
    this.subdivCubic(this.x, this.y, $, Q, Z, K, q, J, 0), this.x = q, this.y = J;
  }
  subdivCubic($, Q, Z, K, q, J, U, W, H) {
    if (H > 16) {
      this.renderLine(U, W), this.x = U, this.y = W;
      return;
    }
    if (l6(2 * U - 3 * q + $) > Z6 >> 1 || l6(2 * W - 3 * J + Q) > Z6 >> 1 || l6(U - 3 * Z + 2 * $) > Z6 >> 1 || l6(W - 3 * K + 2 * Q) > Z6 >> 1) {
      let _ = $ + Z >> 1, N = Q + K >> 1, V = Z + q >> 1, X = K + J >> 1, z = q + U >> 1, G = J + W >> 1, Y = _ + V >> 1, j = N + X >> 1, E = V + z >> 1, w = X + G >> 1, L = Y + E >> 1, A = j + w >> 1;
      this.subdivCubic($, Q, _, N, Y, j, L, A, H + 1), this.subdivCubic(L, A, E, w, z, G, U, W, H + 1);
      return;
    }
    this.renderLine(U, W), this.x = U, this.y = W;
  }
  sweep($, Q = 0) {
    let Z = $.pitch, K = Z < 0 ? ($.rows - 1) * -Z : 0, q = $.width, J = $.rows, U = this.cells.getPool(), W = this.cells.getNullIndex(), H = this.cells.getYCells(), _ = this.cells.getBandMinY(), N = H.length;
    for (let V = 0;V < N; V++) {
      let X = H[V];
      if (X === W)
        continue;
      let z = _ + V;
      if (z < 0 || z >= J)
        continue;
      let G = 0, Y = 0, j = Z < 0 ? K - z * -Z : z * Z, E = X;
      while (E !== W) {
        let w = U[E];
        if (w.x > Y && G !== 0) {
          let D = this.applyFillRule(G >> W6 + 1, Q);
          if (D > 0) {
            let F = Y < 0 ? 0 : Y, M = w.x > q ? q : w.x;
            this.fillSpan($, j, F, M, D);
          }
        }
        G += w.cover * (Z6 * 2);
        let L = G - w.area, A = this.applyFillRule(L >> W6 + 1, Q);
        if (A > 0 && w.x >= 0 && w.x < q)
          this.setPixel($, j, w.x, A);
        Y = w.x + 1, E = w.next;
      }
      if (Y < q && G !== 0) {
        let w = this.applyFillRule(G >> W6 + 1, Q);
        if (w > 0)
          this.fillSpan($, j, Y, q, w);
      }
    }
  }
  applyFillRule($, Q) {
    let Z = $;
    if (Z < 0)
      Z = -Z;
    if (Q === 1) {
      if (Z &= 511, Z > 256)
        Z = 512 - Z;
    }
    return Z > 255 ? 255 : Z;
  }
  fillSpan($, Q, Z, K, q) {
    if ($.pixelMode === 1)
      $.buffer.fill(q, Q + Z, Q + K);
    else if ($.pixelMode === 0) {
      if (q >= 128)
        for (let J = Z;J < K; J++) {
          let U = Q + (J >> 3), W = 7 - (J & 7);
          $.buffer[U] |= 1 << W;
        }
    } else if ($.pixelMode === 2 || $.pixelMode === 3)
      for (let J = Z;J < K; J++) {
        let U = Q + J * 3;
        $.buffer[U] = q, $.buffer[U + 1] = q, $.buffer[U + 2] = q;
      }
    else if ($.pixelMode === 4)
      for (let J = Z;J < K; J++) {
        let U = Q + J * 4;
        $.buffer[U] = 0, $.buffer[U + 1] = 0, $.buffer[U + 2] = 0, $.buffer[U + 3] = q;
      }
  }
  setPixel($, Q, Z, K) {
    if ($.pixelMode === 1)
      $.buffer[Q + Z] = K;
    else if ($.pixelMode === 0) {
      if (K >= 128) {
        let q = Q + (Z >> 3), J = 7 - (Z & 7);
        $.buffer[q] |= 1 << J;
      }
    } else if ($.pixelMode === 2 || $.pixelMode === 3) {
      let q = Q + Z * 3;
      $.buffer[q] = K, $.buffer[q + 1] = K, $.buffer[q + 2] = K;
    } else if ($.pixelMode === 4) {
      let q = Q + Z * 4;
      $.buffer[q] = 0, $.buffer[q + 1] = 0, $.buffer[q + 2] = 0, $.buffer[q + 3] = K;
    }
  }
  sweepSpans($, Q = 0, Z) {
    let K = this.cells.getYCells(), q = this.cells.getPool(), J = this.cells.getNullIndex(), U = this.cells.getBandMinY();
    for (let W = 0;W < K.length; W++) {
      let H = K[W];
      if (H === J)
        continue;
      let _ = U + W, N = [], V = 0, X = -1;
      while (H !== J) {
        let z = q[H];
        if (V !== 0 && z.x > X + 1) {
          let j = this.applyFillRule(V >> W6 + 1, Q);
          if (j > 0)
            N.push({ x: X + 1, len: z.x - X - 1, coverage: j });
        }
        V += z.cover * (Z6 * 2);
        let G = V - z.area, Y = this.applyFillRule(G >> W6 + 1, Q);
        if (Y > 0)
          N.push({ x: z.x, len: 1, coverage: Y });
        X = z.x, H = z.next;
      }
      if (N.length > 0)
        $(_, N, Z);
    }
  }
  sweepDirect($, Q = 0, Z = 0, K = 1 / 0, q) {
    let J = [], U = this.cells.getYCells(), W = this.cells.getPool(), H = this.cells.getNullIndex(), _ = this.cells.getBandMinY();
    for (let N = 0;N < U.length; N++) {
      let V = U[N];
      if (V === H)
        continue;
      let X = _ + N, z = 0, G = Z;
      while (V !== H) {
        let Y = W[V];
        if (z !== 0 && Y.x > G) {
          let w = this.applyFillRule(z >> W6 + 1, Q);
          if (w > 0) {
            if (J.push({ x: G, len: Y.x - G, coverage: w }), J.length >= XK)
              $(X, J.splice(0, J.length), q);
          }
        }
        z += Y.cover * (Z6 * 2);
        let j = z - Y.area, E = this.applyFillRule(j >> W6 + 1, Q);
        if (E > 0 && Y.x >= Z && Y.x < K) {
          if (J.push({ x: Y.x, len: 1, coverage: E }), J.length >= XK)
            $(X, J.splice(0, J.length), q);
        }
        G = Y.x + 1, V = Y.next;
      }
      if (z !== 0 && G < K) {
        let Y = this.applyFillRule(z >> W6 + 1, Q);
        if (Y > 0)
          J.push({ x: G, len: Math.min(K, this.maxX + 1) - G, coverage: Y });
      }
      if (J.length > 0)
        $(X, J.splice(0, J.length), q);
    }
  }
  renderWithBands($, Q, Z, K = 0) {
    let J = Z.maxY - Z.minY, U = Math.max(1, Math.floor(256));
    if (J <= U)
      U = J;
    let W = Z.minX ?? 0, H = Z.maxX ?? $.width, _ = [];
    for (let N = Z.minY;N < Z.maxY; N += U)
      _.push({ minY: N, maxY: Math.min(N + U, Z.maxY), minX: W, maxX: H, depth: 0 });
    while (_.length > 0) {
      let N = _.pop();
      if (!N)
        break;
      if (this.renderBandWithXClip($, Q, N.minY, N.maxY, N.minX, N.maxX, K))
        continue;
      let V = N.depth;
      if (V >= E4) {
        console.warn(`Rasterizer: band overflow at (${N.minX},${N.minY}), depth limit reached`);
        continue;
      }
      let X = V + 1, z = N.minX + N.maxX >> 1;
      if (z > N.minX) {
        _.push({ minY: N.minY, maxY: N.maxY, minX: z, maxX: N.maxX, depth: X }), _.push({ minY: N.minY, maxY: N.maxY, minX: N.minX, maxX: z, depth: X });
        continue;
      }
      let G = N.minY + N.maxY >> 1;
      if (G > N.minY) {
        _.push({ minY: G, maxY: N.maxY, minX: N.minX, maxX: N.maxX, depth: X }), _.push({ minY: N.minY, maxY: G, minX: N.minX, maxX: N.maxX, depth: X });
        continue;
      }
      console.warn(`Rasterizer: band overflow at (${N.minX},${N.minY}), cannot bisect further`);
    }
  }
  renderBandWithXClip($, Q, Z, K, q, J, U) {
    this.setClip(q, Z, J, K), this.cells.setBandBounds(Z, K), this.cells.reset(), this.minY = Z, this.maxY = K;
    try {
      return Q(), this.sweepBandWithXClip($, Z, K, q, J, U), true;
    } catch (W) {
      if (W instanceof g8)
        return false;
      throw W;
    }
  }
  sweepBandWithXClip($, Q, Z, K, q, J) {
    let U = $.pitch, W = U < 0 ? ($.rows - 1) * -U : 0, H = this.cells.getYCells(), _ = this.cells.getPool(), N = this.cells.getNullIndex(), V = this.cells.getBandMinY();
    for (let X = Q;X < Z; X++) {
      if (X < 0 || X >= $.rows)
        continue;
      let z = X - V;
      if (z < 0 || z >= H.length)
        continue;
      let G = H[z];
      if (G === N)
        continue;
      let Y = 0, j = K, E = U < 0 ? W - X * -U : X * U;
      while (G !== N) {
        let w = _[G];
        if (w.x < K) {
          Y += w.cover * (Z6 * 2), G = w.next;
          continue;
        }
        if (w.x >= q) {
          if (Y !== 0 && j < q) {
            let D = this.applyFillRule(Y >> W6 + 1, J);
            if (D > 0)
              this.fillSpan($, E, Math.max(0, j), Math.min($.width, q), D);
          }
          break;
        }
        if (w.x > j && Y !== 0) {
          let D = this.applyFillRule(Y >> W6 + 1, J);
          if (D > 0) {
            let F = Math.max(0, j), M = Math.min($.width, w.x);
            this.fillSpan($, E, F, M, D);
          }
        }
        Y += w.cover * (Z6 * 2);
        let L = Y - w.area, A = this.applyFillRule(L >> W6 + 1, J);
        if (A > 0 && w.x >= 0 && w.x < $.width)
          this.setPixel($, E, w.x, A);
        j = w.x + 1, G = w.next;
      }
      if (j < q && j < $.width && Y !== 0) {
        let w = this.applyFillRule(Y >> W6 + 1, J);
        if (w > 0)
          this.fillSpan($, E, j, Math.min($.width, q), w);
      }
    }
  }
}
G6();
var GK;
((q) => {
  q[q.Ok = 0] = "Ok";
  q[q.InvalidOutline = 1] = "InvalidOutline";
  q[q.InvalidArgument = 2] = "InvalidArgument";
  q[q.EmptyOutline = 3] = "EmptyOutline";
})(GK ||= {});
function w4($, Q = true) {
  if (!$)
    return { error: 1, message: "Path is null or undefined" };
  if (!$.commands)
    return { error: 1, message: "Path commands array is missing" };
  if ($.commands.length === 0) {
    if (Q)
      return { error: 3 };
    return { error: 1, message: "Path is empty" };
  }
  let Z = false, K = false, q = 0;
  for (let J = 0;J < $.commands.length; J++) {
    let U = $.commands[J];
    switch (U.type) {
      case "M":
        if (Z = true, K = true, q++, !Number.isFinite(U.x) || !Number.isFinite(U.y))
          return { error: 1, message: `Invalid coordinates at command ${J}: (${U.x}, ${U.y})` };
        break;
      case "L":
        if (!Z)
          return { error: 1, message: `Line command at ${J} without preceding moveTo` };
        if (!Number.isFinite(U.x) || !Number.isFinite(U.y))
          return { error: 1, message: `Invalid coordinates at command ${J}` };
        break;
      case "Q":
        if (!Z)
          return { error: 1, message: `Quadratic curve at ${J} without preceding moveTo` };
        if (!Number.isFinite(U.x1) || !Number.isFinite(U.y1) || !Number.isFinite(U.x) || !Number.isFinite(U.y))
          return { error: 1, message: `Invalid coordinates at command ${J}` };
        break;
      case "C":
        if (!Z)
          return { error: 1, message: `Cubic curve at ${J} without preceding moveTo` };
        if (!Number.isFinite(U.x1) || !Number.isFinite(U.y1) || !Number.isFinite(U.x2) || !Number.isFinite(U.y2) || !Number.isFinite(U.x) || !Number.isFinite(U.y))
          return { error: 1, message: `Invalid coordinates at command ${J}` };
        break;
      case "Z":
        K = false;
        break;
      default:
        return { error: 1, message: `Unknown command type at ${J}: ${U.type}` };
    }
  }
  if (q === 0 && !Q)
    return { error: 3, message: "No contours in path" };
  return { error: 0 };
}
function F8($, Q, Z, K = 0, q = 0, J = true) {
  let U = 0, W = 0, H = false, _ = Z * Z6, N = (J ? -Z : Z) * Z6, V = K * Z6, X = q * Z6;
  for (let z = 0;z < Q.commands.length; z++) {
    let G = Q.commands[z];
    switch (G.type) {
      case "M": {
        if (H)
          $.lineTo(U, W);
        let Y = Math.round(G.x * _ + V), j = Math.round(G.y * N + X);
        $.moveTo(Y, j), U = Y, W = j, H = true;
        break;
      }
      case "L": {
        let Y = Math.round(G.x * _ + V), j = Math.round(G.y * N + X);
        $.lineTo(Y, j);
        break;
      }
      case "Q": {
        let Y = Math.round(G.x1 * _ + V), j = Math.round(G.y1 * N + X), E = Math.round(G.x * _ + V), w = Math.round(G.y * N + X);
        $.conicTo(Y, j, E, w);
        break;
      }
      case "C": {
        let Y = Math.round(G.x1 * _ + V), j = Math.round(G.y1 * N + X), E = Math.round(G.x2 * _ + V), w = Math.round(G.y2 * N + X), L = Math.round(G.x * _ + V), A = Math.round(G.y * N + X);
        $.cubicTo(Y, j, E, w, L, A);
        break;
      }
      case "Z": {
        if (H)
          $.lineTo(U, W), H = false;
        break;
      }
    }
  }
  if (H)
    $.lineTo(U, W);
}
function zK($, Q) {
  if ($ === 0 || Q === 0)
    return 0;
  let Z = 1, K = $, q = Q;
  if (K < 0)
    K = -K, Z = -Z;
  if (q < 0)
    q = -q, Z = -Z;
  let J = Math.floor((K * q + 32768) / 65536);
  return Z < 0 ? -J : J;
}
function _8($, Q, Z = true, K = false) {
  if (K) {
    let J = Math.round(Q * 64 * 65536), U = 1 / 0, W = 1 / 0, H = -1 / 0, _ = -1 / 0, N = (V, X) => {
      let z = zK(V, J), G = zK(X, J);
      if (z < U)
        U = z;
      if (z > H)
        H = z;
      if (G < W)
        W = G;
      if (G > _)
        _ = G;
    };
    for (let V of $.commands)
      switch (V.type) {
        case "M":
        case "L":
          N(V.x, V.y);
          break;
        case "Q":
          N(V.x1, V.y1), N(V.x, V.y);
          break;
        case "C":
          N(V.x1, V.y1), N(V.x2, V.y2), N(V.x, V.y);
          break;
        default:
          break;
      }
    if (!Number.isFinite(U) || !Number.isFinite(W))
      return null;
    if (Z) {
      let V = -_, X = -W;
      return { minX: Math.floor(U / 64), minY: Math.floor(V / 64), maxX: Math.floor((H + 63) / 64), maxY: Math.floor((X + 63) / 64) };
    }
    return { minX: Math.floor(U / 64), minY: Math.floor(W / 64), maxX: Math.floor((H + 63) / 64), maxY: Math.floor((_ + 63) / 64) };
  }
  if (!$.bounds)
    return null;
  let q = $.bounds;
  if (Z)
    return { minX: Math.floor(q.xMin * Q), minY: Math.floor(-q.yMax * Q), maxX: Math.ceil(q.xMax * Q), maxY: Math.ceil(-q.yMin * Q) };
  else
    return { minX: Math.floor(q.xMin * Q), minY: Math.floor(q.yMin * Q), maxX: Math.ceil(q.xMax * Q), maxY: Math.ceil(q.yMax * Q) };
}
function A4($, Q = 0) {
  if (!$?.flags)
    return Q;
  return ($.flags & 1) !== 0 ? 1 : 0;
}
G6();
G6();
function u8($, Q, Z) {
  if (Q === 0 && Z === 0)
    return U6($);
  let K = r($.width, $.rows, $.pixelMode);
  if ($.pixelMode === 1)
    for (let q = 0;q < $.rows; q++)
      for (let J = 0;J < $.width; J++) {
        let U = 0;
        for (let W = -Math.floor(Z);W <= Math.ceil(Z); W++)
          for (let H = -Math.floor(Q);H <= Math.ceil(Q); H++) {
            let _ = J + H, N = q + W;
            if (_ >= 0 && _ < $.width && N >= 0 && N < $.rows) {
              let V = $.buffer[N * $.pitch + _] ?? 0;
              U = Math.max(U, V);
            }
          }
        K.buffer[q * K.pitch + J] = U;
      }
  else if ($.pixelMode === 0)
    for (let q = 0;q < $.rows; q++)
      for (let J = 0;J < $.width; J++) {
        let U = false;
        for (let W = -Math.floor(Z);W <= Math.ceil(Z); W++) {
          for (let H = -Math.floor(Q);H <= Math.ceil(Q); H++) {
            let _ = J + H, N = q + W;
            if (_ >= 0 && _ < $.width && N >= 0 && N < $.rows) {
              let V = N * $.pitch + (_ >> 3), X = 7 - (_ & 7);
              if (($.buffer[V] ?? 0) >> X & 1) {
                U = true;
                break;
              }
            }
          }
          if (U)
            break;
        }
        if (U) {
          let W = q * K.pitch + (J >> 3), H = 7 - (J & 7);
          K.buffer[W] |= 1 << H;
        }
      }
  else if ($.pixelMode === 2 || $.pixelMode === 3)
    for (let q = 0;q < $.rows; q++)
      for (let J = 0;J < $.width; J++) {
        let U = 0, W = 0, H = 0;
        for (let N = -Math.floor(Z);N <= Math.ceil(Z); N++)
          for (let V = -Math.floor(Q);V <= Math.ceil(Q); V++) {
            let X = J + V, z = q + N;
            if (X >= 0 && X < $.width && z >= 0 && z < $.rows) {
              let G = z * $.pitch + X * 3;
              U = Math.max(U, $.buffer[G] ?? 0), W = Math.max(W, $.buffer[G + 1] ?? 0), H = Math.max(H, $.buffer[G + 2] ?? 0);
            }
          }
        let _ = q * K.pitch + J * 3;
        K.buffer[_] = U, K.buffer[_ + 1] = W, K.buffer[_ + 2] = H;
      }
  return K;
}
function N8($, Q) {
  if ($.pixelMode === Q)
    return $;
  let Z = r($.width, $.rows, Q);
  if ($.pixelMode === 1 && Q === 0) {
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++)
        if (($.buffer[K * $.pitch + q] ?? 0) >= 128) {
          let U = K * Z.pitch + (q >> 3), W = 7 - (q & 7);
          Z.buffer[U] |= 1 << W;
        }
  } else if ($.pixelMode === 0 && Q === 1)
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++) {
        let J = K * $.pitch + (q >> 3), U = 7 - (q & 7), W = ($.buffer[J] ?? 0) >> U & 1;
        Z.buffer[K * Z.pitch + q] = W ? 255 : 0;
      }
  else if ($.pixelMode === 1 && Q === 2)
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++) {
        let J = $.buffer[K * $.pitch + q] ?? 0, U = K * Z.pitch + q * 3;
        Z.buffer[U] = J, Z.buffer[U + 1] = J, Z.buffer[U + 2] = J;
      }
  else if ($.pixelMode === 0 && Q === 2)
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++) {
        let J = K * $.pitch + (q >> 3), U = 7 - (q & 7), H = ($.buffer[J] ?? 0) >> U & 1 ? 255 : 0, _ = K * Z.pitch + q * 3;
        Z.buffer[_] = H, Z.buffer[_ + 1] = H, Z.buffer[_ + 2] = H;
      }
  else if ($.pixelMode === 1 && Q === 3)
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++) {
        let J = $.buffer[K * $.pitch + q] ?? 0, U = K * Z.pitch + q * 3;
        Z.buffer[U] = J, Z.buffer[U + 1] = J, Z.buffer[U + 2] = J;
      }
  else if ($.pixelMode === 2 && Q === 1)
    for (let K = 0;K < $.rows; K++)
      for (let q = 0;q < $.width; q++) {
        let J = K * $.pitch + q * 3, U = $.buffer[J] ?? 0, W = $.buffer[J + 1] ?? 0, H = $.buffer[J + 2] ?? 0;
        Z.buffer[K * Z.pitch + q] = Math.floor((U + W + H) / 3);
      }
  return Z;
}
function p0($, Q, Z, K, q) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  q = Math.max(0, Math.min(1, q));
  let J = Math.max(0, -Z), U = Math.max(0, -K), W = Math.min(Q.width, $.width - Z), H = Math.min(Q.rows, $.rows - K);
  for (let _ = U;_ < H; _++)
    for (let N = J;N < W; N++) {
      let V = Z + N, X = K + _;
      if (V >= 0 && V < $.width && X >= 0 && X < $.rows) {
        let z = Q.buffer[_ * Q.pitch + N] ?? 0, Y = ($.buffer[X * $.pitch + V] ?? 0) + z * q;
        $.buffer[X * $.pitch + V] = Math.min(255, Math.floor(Y));
      }
    }
}
function U6($) {
  return { buffer: new Uint8Array($.buffer), width: $.width, rows: $.rows, pitch: $.pitch, pixelMode: $.pixelMode, numGrays: $.numGrays };
}
function x8($, Q, Z) {
  let K = r(Q, Z, $.pixelMode), q = $.width / Q, J = $.rows / Z;
  if ($.pixelMode === 1)
    for (let U = 0;U < Z; U++)
      for (let W = 0;W < Q; W++) {
        let H = Math.floor(W * q), _ = Math.floor(U * J);
        K.buffer[U * K.pitch + W] = $.buffer[_ * $.pitch + H] ?? 0;
      }
  else if ($.pixelMode === 0)
    for (let U = 0;U < Z; U++)
      for (let W = 0;W < Q; W++) {
        let H = Math.floor(W * q), N = Math.floor(U * J) * $.pitch + (H >> 3), V = 7 - (H & 7);
        if (($.buffer[N] ?? 0) >> V & 1) {
          let z = U * K.pitch + (W >> 3), G = 7 - (W & 7);
          K.buffer[z] |= 1 << G;
        }
      }
  else if ($.pixelMode === 2 || $.pixelMode === 3)
    for (let U = 0;U < Z; U++)
      for (let W = 0;W < Q; W++) {
        let H = Math.floor(W * q), N = Math.floor(U * J) * $.pitch + H * 3, V = U * K.pitch + W * 3;
        K.buffer[V] = $.buffer[N] ?? 0, K.buffer[V + 1] = $.buffer[N + 1] ?? 0, K.buffer[V + 2] = $.buffer[N + 2] ?? 0;
      }
  return K;
}
function Z9($, Q, Z) {
  let K = r(Q, Z, $.pixelMode), q = ($.width - 1) / Math.max(1, Q - 1), J = ($.rows - 1) / Math.max(1, Z - 1);
  if ($.pixelMode === 1)
    for (let U = 0;U < Z; U++) {
      let W = U * J, H = Math.floor(W), _ = Math.min(H + 1, $.rows - 1), N = W - H;
      for (let V = 0;V < Q; V++) {
        let X = V * q, z = Math.floor(X), G = Math.min(z + 1, $.width - 1), Y = X - z, j = $.buffer[H * $.pitch + z] ?? 0, E = $.buffer[H * $.pitch + G] ?? 0, w = $.buffer[_ * $.pitch + z] ?? 0, L = $.buffer[_ * $.pitch + G] ?? 0, A = j + (E - j) * Y, D = w + (L - w) * Y, F = A + (D - A) * N;
        K.buffer[U * K.pitch + V] = Math.round(F);
      }
    }
  else if ($.pixelMode === 0)
    return x8($, Q, Z);
  else if ($.pixelMode === 2 || $.pixelMode === 3)
    for (let U = 0;U < Z; U++) {
      let W = U * J, H = Math.floor(W), _ = Math.min(H + 1, $.rows - 1), N = W - H;
      for (let V = 0;V < Q; V++) {
        let X = V * q, z = Math.floor(X), G = Math.min(z + 1, $.width - 1), Y = X - z, j = H * $.pitch + z * 3, E = H * $.pitch + G * 3, w = _ * $.pitch + z * 3, L = _ * $.pitch + G * 3;
        for (let A = 0;A < 3; A++) {
          let D = $.buffer[j + A] ?? 0, F = $.buffer[E + A] ?? 0, M = $.buffer[w + A] ?? 0, R = $.buffer[L + A] ?? 0, P = D + (F - D) * Y, T = M + (R - M) * Y, m = P + (T - P) * N;
          K.buffer[U * K.pitch + V * 3 + A] = Math.round(m);
        }
      }
    }
  return K;
}
function P$($, Q, Z = 0, K = 0) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let q = Math.max(0, -Z), J = Math.max(0, -K), U = Math.min(Q.width, $.width - Z), W = Math.min(Q.rows, $.rows - K);
  for (let H = J;H < W; H++) {
    let _ = K + H;
    if (_ < 0 || _ >= $.rows)
      continue;
    for (let N = q;N < U; N++) {
      let V = Z + N;
      if (V < 0 || V >= $.width)
        continue;
      let X = Q.buffer[H * Q.pitch + N] ?? 0, z = $.buffer[_ * $.pitch + V] ?? 0;
      $.buffer[_ * $.pitch + V] = Math.min(255, X + z);
    }
  }
}
function I$($, Q, Z = 0, K = 0) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let q = Math.max(0, -Z), J = Math.max(0, -K), U = Math.min(Q.width, $.width - Z), W = Math.min(Q.rows, $.rows - K);
  for (let H = J;H < W; H++) {
    let _ = K + H;
    if (_ < 0 || _ >= $.rows)
      continue;
    for (let N = q;N < U; N++) {
      let V = Z + N;
      if (V < 0 || V >= $.width)
        continue;
      let X = Q.buffer[H * Q.pitch + N] ?? 0, z = $.buffer[_ * $.pitch + V] ?? 0;
      $.buffer[_ * $.pitch + V] = Math.floor((X * z + 127) / 255);
    }
  }
}
function c0($, Q, Z = 0, K = 0) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let q = Math.max(0, -Z), J = Math.max(0, -K), U = Math.min(Q.width, $.width - Z), W = Math.min(Q.rows, $.rows - K);
  for (let H = J;H < W; H++) {
    let _ = K + H;
    if (_ < 0 || _ >= $.rows)
      continue;
    for (let N = q;N < U; N++) {
      let V = Z + N;
      if (V < 0 || V >= $.width)
        continue;
      let X = Q.buffer[H * Q.pitch + N] ?? 0, z = $.buffer[_ * $.pitch + V] ?? 0;
      $.buffer[_ * $.pitch + V] = Math.max(0, z - X);
    }
  }
}
function v$($, Q, Z = 0, K = 0) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let q = Math.max(0, -Z), J = Math.max(0, -K), U = Math.min(Q.width, $.width - Z), W = Math.min(Q.rows, $.rows - K);
  for (let H = J;H < W; H++) {
    let _ = K + H;
    if (_ < 0 || _ >= $.rows)
      continue;
    for (let N = q;N < U; N++) {
      let V = Z + N;
      if (V < 0 || V >= $.width)
        continue;
      let X = Q.buffer[H * Q.pitch + N] ?? 0, z = $.buffer[_ * $.pitch + V] ?? 0, G = X + Math.floor((z * (255 - X) + 127) / 255);
      $.buffer[_ * $.pitch + V] = Math.min(255, G);
    }
  }
}
function l9($, Q, Z) {
  let K = r($.width, $.rows, $.pixelMode);
  if ($.pixelMode === 1)
    for (let q = 0;q < $.rows; q++) {
      let J = q - Z;
      if (J < 0 || J >= $.rows)
        continue;
      for (let U = 0;U < $.width; U++) {
        let W = U - Q;
        if (W < 0 || W >= $.width)
          continue;
        K.buffer[q * K.pitch + U] = $.buffer[J * $.pitch + W] ?? 0;
      }
    }
  else if ($.pixelMode === 0)
    for (let q = 0;q < $.rows; q++) {
      let J = q - Z;
      if (J < 0 || J >= $.rows)
        continue;
      for (let U = 0;U < $.width; U++) {
        let W = U - Q;
        if (W < 0 || W >= $.width)
          continue;
        let H = J * $.pitch + (W >> 3), _ = 7 - (W & 7);
        if (($.buffer[H] ?? 0) >> _ & 1) {
          let V = q * K.pitch + (U >> 3), X = 7 - (U & 7);
          K.buffer[V] |= 1 << X;
        }
      }
    }
  else if ($.pixelMode === 2 || $.pixelMode === 3)
    for (let q = 0;q < $.rows; q++) {
      let J = q - Z;
      if (J < 0 || J >= $.rows)
        continue;
      for (let U = 0;U < $.width; U++) {
        let W = U - Q;
        if (W < 0 || W >= $.width)
          continue;
        let H = J * $.pitch + W * 3, _ = q * K.pitch + U * 3;
        K.buffer[_] = $.buffer[H] ?? 0, K.buffer[_ + 1] = $.buffer[H + 1] ?? 0, K.buffer[_ + 2] = $.buffer[H + 2] ?? 0;
      }
    }
  return K;
}
function YK($, Q, Z = 0, K = 0, q = 128) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let J = Math.max(0, -Z), U = Math.max(0, -K), W = Math.min(Q.width, $.width - Z), H = Math.min(Q.rows, $.rows - K);
  for (let _ = U;_ < H; _++) {
    let N = K + _;
    if (N < 0 || N >= $.rows)
      continue;
    for (let V = J;V < W; V++) {
      let X = Z + V;
      if (X < 0 || X >= $.width)
        continue;
      if ((Q.buffer[_ * Q.pitch + V] ?? 0) >= q)
        $.buffer[N * $.pitch + X] = 0;
    }
  }
}
function k$($, Q, Z = 0, K = 0) {
  if ($.pixelMode !== 1 || Q.pixelMode !== 1)
    return;
  let q = Math.max(0, -Z), J = Math.max(0, -K), U = Math.min(Q.width, $.width - Z), W = Math.min(Q.rows, $.rows - K);
  for (let H = J;H < W; H++) {
    let _ = K + H;
    if (_ < 0 || _ >= $.rows)
      continue;
    for (let N = q;N < U; N++) {
      let V = Z + N;
      if (V < 0 || V >= $.width)
        continue;
      let X = Q.buffer[H * Q.pitch + N] ?? 0, z = $.buffer[_ * $.pitch + V] ?? 0;
      $.buffer[_ * $.pitch + V] = Math.max(X, z);
    }
  }
}
function K9($, Q, Z, K, q) {
  let J = $.width + Q + K, U = $.rows + Z + q, W = r(J, U, $.pixelMode);
  if ($.pixelMode === 1)
    for (let H = 0;H < $.rows; H++)
      for (let _ = 0;_ < $.width; _++)
        W.buffer[(H + Z) * W.pitch + (_ + Q)] = $.buffer[H * $.pitch + _] ?? 0;
  else if ($.pixelMode === 2 || $.pixelMode === 3)
    for (let H = 0;H < $.rows; H++)
      for (let _ = 0;_ < $.width; _++) {
        let N = H * $.pitch + _ * 3, V = (H + Z) * W.pitch + (_ + Q) * 3;
        W.buffer[V] = $.buffer[N] ?? 0, W.buffer[V + 1] = $.buffer[N + 1] ?? 0, W.buffer[V + 2] = $.buffer[N + 2] ?? 0;
      }
  return W;
}
function L4($, Q, Z, K) {
  let { width: U, rows: W } = $, H = Z, _ = K, N = Z + Q.width, V = K + Q.rows, X = Math.min(0, H), z = Math.min(0, _), G = Math.max(U, N), Y = Math.max(W, V), j = G - X, E = Y - z, w = r(j, E, $.pixelMode), L = 0 - X, A = 0 - z, D = H - X, F = _ - z;
  if ($.pixelMode === 1)
    for (let M = 0;M < $.rows; M++)
      for (let R = 0;R < $.width; R++)
        w.buffer[(M + A) * w.pitch + (R + L)] = $.buffer[M * $.pitch + R] ?? 0;
  return { expanded: w, dstOffsetX: L, dstOffsetY: A, srcOffsetX: D, srcOffsetY: F };
}
function f9($, Q) {
  let Z = $.pitch, K = Math.abs(Z);
  return (Z < 0 ? ($.rows - 1) * K : 0) + Q * Z;
}
function n0($, Q, Z, K) {
  if (Q < 0 || Z < 0 || Q >= $.width || Z >= $.rows)
    return 0;
  let q = f9($, Z);
  switch ($.pixelMode) {
    case 1:
      return $.buffer[q + Q] ?? 0;
    case 0: {
      let J = q + (Q >> 3), U = 7 - (Q & 7);
      return ($.buffer[J] ?? 0) >> U & 1 ? 255 : 0;
    }
    case 2:
    case 3: {
      let J = q + Q * 3 + K;
      return $.buffer[J] ?? 0;
    }
    case 4: {
      let J = q + Q * 4 + K;
      return $.buffer[J] ?? 0;
    }
  }
}
function jK($, Q, Z, K, q) {
  let J = Math.floor(Q), U = Math.floor(Z), W = J + 1, H = U + 1, _ = Q - J, N = Z - U, V = (1 - _) * (1 - N), X = _ * (1 - N), z = (1 - _) * N, G = _ * N;
  for (let Y = 0;Y < K; Y++) {
    let j = n0($, J, U, Y), E = n0($, W, U, Y), w = n0($, J, H, Y), L = n0($, W, H, Y), A = j * V + E * X + w * z + L * G;
    q[Y] = Math.min(255, Math.max(0, Math.round(A)));
  }
}
function F4($) {
  let [Q, Z, K, q, J, U] = $, W = Q * q - Z * K;
  if (W === 0)
    return null;
  let H = q / W, _ = -Z / W, N = -K / W, V = Q / W, X = (K * U - q * J) / W, z = (Z * J - Q * U) / W;
  return { inv: [H, _, N, V, X, z], det: W };
}
function D4($) {
  let Q = $[0][0], Z = $[0][1], K = $[0][2], q = $[1][0], J = $[1][1], U = $[1][2], W = $[2][0], H = $[2][1], _ = $[2][2], N = J * _ - U * H, V = K * H - Z * _, X = Z * U - K * J, z = U * W - q * _, G = Q * _ - K * W, Y = K * q - Q * U, j = q * H - J * W, E = Z * W - Q * H, w = Q * J - Z * q, L = Q * N + Z * z + K * j;
  if (L === 0)
    return null;
  let A = 1 / L;
  return [[N * A, V * A, X * A], [z * A, G * A, Y * A], [j * A, E * A, w * A]];
}
function x9($, Q, Z) {
  let K = Z[2][0] * $ + Z[2][1] * Q + Z[2][2], q = 0.000001, J = Math.abs(K) < 0.000001 ? K < 0 ? -0.000001 : 0.000001 : K;
  return { x: (Z[0][0] * $ + Z[0][1] * Q + Z[0][2]) / J, y: (Z[1][0] * $ + Z[1][1] * Q + Z[1][2]) / J };
}
function B4($, Q, Z = 0, K = 0) {
  c0($, Q, Z, K);
}
function R4($, Q, Z = 0, K = 0, q = 128) {
  YK($, Q, Z, K, q);
}
function C$($, Q, Z) {
  let K = 1 / 0, q = -1 / 0;
  for (let W = 0;W < $.rows; W++)
    for (let H = 0;H < $.width; H++) {
      let _ = false;
      switch ($.pixelMode) {
        case 1: {
          let N = f9($, W);
          _ = ($.buffer[N + H] ?? 0) > 0;
          break;
        }
        case 0: {
          let V = f9($, W) + (H >> 3), X = 7 - (H & 7);
          _ = (($.buffer[V] ?? 0) >> X & 1) === 1;
          break;
        }
        case 2:
        case 3: {
          let V = f9($, W) + H * 3;
          _ = ($.buffer[V] ?? 0) > 0 || ($.buffer[V + 1] ?? 0) > 0 || ($.buffer[V + 2] ?? 0) > 0;
          break;
        }
        case 4: {
          let V = f9($, W) + H * 4;
          _ = ($.buffer[V + 3] ?? 0) > 0;
          break;
        }
      }
      if (_)
        K = Math.min(K, W), q = Math.max(q, W);
    }
  if (!Number.isFinite(K) || !Number.isFinite(q))
    return { ascent: 0, descent: 0 };
  let J = Z - K, U = q + 1 - Z;
  return { ascent: J, descent: U };
}
function S$($, Q) {
  let Z = Q.blur ?? 0, K = Q.be ?? 0, q = Q.border ?? 0, J = Q.shadowX ?? 0, U = Q.shadowY ?? 0, W = Math.ceil(Z + K + q), H = W + Math.max(0, -J), _ = W + Math.max(0, J), N = W + Math.max(0, -U), V = W + Math.max(0, U);
  return { width: $.width + H + _, height: $.height + N + V, bearingX: $.bearingX - H, bearingY: $.bearingY + N, ascent: $.ascent + N, descent: $.descent + V, padLeft: H, padRight: _, padTop: N, padBottom: V };
}
function d9($, Q, Z, K, q) {
  let J = Math.max(0, Math.ceil(K)), U = Math.max(0, Math.ceil(q)), W = K9($, J, U, J, U);
  return { bitmap: u8(W, K, q), bearingX: Q - J, bearingY: Z + U };
}
function e6($, Q, Z = {}) {
  let K = Z.bearingX ?? 0, q = Z.bearingY ?? 0, J = (Z.offsetX26 ?? 0) / 64, U = (Z.offsetY26 ?? 0) / 64, [W, H, _, N, V, X] = Q, z = [W, H, _, N, V + J, X + U], G = K, Y = q, j = G + $.width, E = Y - $.rows, w = [{ x: z[0] * G + z[2] * Y + z[4], y: z[1] * G + z[3] * Y + z[5] }, { x: z[0] * j + z[2] * Y + z[4], y: z[1] * j + z[3] * Y + z[5] }, { x: z[0] * G + z[2] * E + z[4], y: z[1] * G + z[3] * E + z[5] }, { x: z[0] * j + z[2] * E + z[4], y: z[1] * j + z[3] * E + z[5] }], L = 1 / 0, A = -1 / 0, D = 1 / 0, F = -1 / 0;
  for (let S of w)
    L = Math.min(L, S.x), A = Math.max(A, S.x), D = Math.min(D, S.y), F = Math.max(F, S.y);
  if (!Number.isFinite(L) || !Number.isFinite(D))
    return { bitmap: r(1, 1, $.pixelMode), bearingX: 0, bearingY: 0 };
  let M = Math.floor(L), R = Math.ceil(A), P = Math.floor(D), T = Math.ceil(F), m = Math.max(1, R - M), v = Math.max(1, T - P), f = r(m, v, $.pixelMode), l = F4(z);
  if (!l)
    return { bitmap: f, bearingX: M, bearingY: T };
  let k = l.inv, O = $.pixelMode === 2 || $.pixelMode === 3 ? 3 : $.pixelMode === 4 ? 4 : 1, h = Array(O).fill(0);
  for (let S = 0;S < v; S++)
    for (let d = 0;d < m; d++) {
      let o = M + d + 0.5, n = T - S - 0.5, e = k[0] * o + k[2] * n + k[4], u = k[1] * o + k[3] * n + k[5], N6 = e - K - 0.5, y = q - u - 0.5;
      if (jK($, N6, y, O, h), $.pixelMode === 0) {
        if ((h[0] ?? 0) >= 128) {
          let g = S * f.pitch + (d >> 3), w6 = 7 - (d & 7);
          f.buffer[g] |= 1 << w6;
        }
      } else if ($.pixelMode === 1)
        f.buffer[S * f.pitch + d] = h[0] ?? 0;
      else if ($.pixelMode === 2 || $.pixelMode === 3) {
        let t = S * f.pitch + d * 3;
        f.buffer[t] = h[0] ?? 0, f.buffer[t + 1] = h[1] ?? 0, f.buffer[t + 2] = h[2] ?? 0;
      } else if ($.pixelMode === 4) {
        let t = S * f.pitch + d * 4;
        f.buffer[t] = h[0] ?? 0, f.buffer[t + 1] = h[1] ?? 0, f.buffer[t + 2] = h[2] ?? 0, f.buffer[t + 3] = h[3] ?? 0;
      }
    }
  return { bitmap: f, bearingX: M, bearingY: T };
}
function D8($, Q, Z = {}) {
  let K = Z.bearingX ?? 0, q = Z.bearingY ?? 0, J = (Z.offsetX26 ?? 0) / 64, U = (Z.offsetY26 ?? 0) / 64, W = [[Q[0][0], Q[0][1], Q[0][2] + J], [Q[1][0], Q[1][1], Q[1][2] + U], [Q[2][0], Q[2][1], Q[2][2]]], H = K, _ = q, N = H + $.width, V = _ - $.rows, X = [x9(H, _, W), x9(N, _, W), x9(H, V, W), x9(N, V, W)], z = 1 / 0, G = -1 / 0, Y = 1 / 0, j = -1 / 0;
  for (let m of X)
    z = Math.min(z, m.x), G = Math.max(G, m.x), Y = Math.min(Y, m.y), j = Math.max(j, m.y);
  if (!Number.isFinite(z) || !Number.isFinite(Y))
    return { bitmap: r(1, 1, $.pixelMode), bearingX: 0, bearingY: 0 };
  let E = Math.floor(z), w = Math.ceil(G), L = Math.floor(Y), A = Math.ceil(j), D = Math.max(1, w - E), F = Math.max(1, A - L), M = r(D, F, $.pixelMode), R = D4(W);
  if (!R)
    return { bitmap: M, bearingX: E, bearingY: A };
  let P = $.pixelMode === 2 || $.pixelMode === 3 ? 3 : $.pixelMode === 4 ? 4 : 1, T = Array(P).fill(0);
  for (let m = 0;m < F; m++)
    for (let v = 0;v < D; v++) {
      let f = E + v + 0.5, l = A - m - 0.5, k = x9(f, l, R), O = k.x - K - 0.5, h = q - k.y - 0.5;
      if (jK($, O, h, P, T), $.pixelMode === 0) {
        if ((T[0] ?? 0) >= 128) {
          let d = m * M.pitch + (v >> 3), o = 7 - (v & 7);
          M.buffer[d] |= 1 << o;
        }
      } else if ($.pixelMode === 1)
        M.buffer[m * M.pitch + v] = T[0] ?? 0;
      else if ($.pixelMode === 2 || $.pixelMode === 3) {
        let S = m * M.pitch + v * 3;
        M.buffer[S] = T[0] ?? 0, M.buffer[S + 1] = T[1] ?? 0, M.buffer[S + 2] = T[2] ?? 0;
      } else if ($.pixelMode === 4) {
        let S = m * M.pitch + v * 4;
        M.buffer[S] = T[0] ?? 0, M.buffer[S + 1] = T[1] ?? 0, M.buffer[S + 2] = T[2] ?? 0, M.buffer[S + 3] = T[3] ?? 0;
      }
    }
  return { bitmap: M, bearingX: E, bearingY: A };
}
function q9($, Q, Z = {}) {
  return e6($, [1, 0, Q, 1, 0, 0], Z);
}
function J9($, Q, Z = {}) {
  return e6($, [1, Q, 0, 1, 0, 0], Z);
}
function h$($, Q, Z) {
  if (Z !== "height")
    return Q;
  if (!Number.isFinite(Q) || Q <= 0)
    return Q;
  let K = $.ascender - $.descender, q = $.isCFF ? K : K + $.lineGap;
  if (!Number.isFinite(q) || q <= 0)
    return Q;
  return Q * $.unitsPerEm / q;
}
function U9($, Q, Z) {
  return h$($, Q, Z) / $.unitsPerEm;
}
function EK($) {
  if (!$.commands || $.commands.length === 0)
    return null;
  let Q = 1 / 0, Z = 1 / 0, K = -1 / 0, q = -1 / 0, J = 0, U = 0, W = false, H = (_, N) => {
    Q = Math.min(Q, _), Z = Math.min(Z, N), K = Math.max(K, _), q = Math.max(q, N), W = true;
  };
  for (let _ = 0;_ < $.commands.length; _++) {
    let N = $.commands[_];
    switch (N.type) {
      case "M": {
        J = N.x, U = N.y, H(J, U);
        break;
      }
      case "L": {
        J = N.x, U = N.y, H(J, U);
        break;
      }
      case "Q": {
        H(N.x, N.y);
        let V = y$(J, N.x1, N.x);
        for (let Y = 0;Y < V.length; Y++) {
          let j = V[Y], E = n9(J, N.x1, N.x, j);
          H(E, U);
        }
        let X = y$(U, N.y1, N.y);
        for (let Y = 0;Y < X.length; Y++) {
          let j = X[Y], E = n9(U, N.y1, N.y, j);
          H(J, E);
        }
        let G = [...new Set([...V, ...X])];
        for (let Y = 0;Y < G.length; Y++) {
          let j = G[Y], E = n9(J, N.x1, N.x, j), w = n9(U, N.y1, N.y, j);
          H(E, w);
        }
        J = N.x, U = N.y;
        break;
      }
      case "C": {
        H(N.x, N.y);
        let V = b$(J, N.x1, N.x2, N.x);
        for (let Y = 0;Y < V.length; Y++) {
          let j = V[Y], E = p9(J, N.x1, N.x2, N.x, j);
          H(E, U);
        }
        let X = b$(U, N.y1, N.y2, N.y);
        for (let Y = 0;Y < X.length; Y++) {
          let j = X[Y], E = p9(U, N.y1, N.y2, N.y, j);
          H(J, E);
        }
        let G = [...new Set([...V, ...X])];
        for (let Y = 0;Y < G.length; Y++) {
          let j = G[Y], E = p9(J, N.x1, N.x2, N.x, j), w = p9(U, N.y1, N.y2, N.y, j);
          H(E, w);
        }
        J = N.x, U = N.y;
        break;
      }
      case "Z":
        break;
    }
  }
  if (!W)
    return null;
  return { xMin: Q, yMin: Z, xMax: K, yMax: q };
}
function y$($, Q, Z) {
  let K = $ - 2 * Q + Z;
  if (Math.abs(K) < 0.0000000001)
    return [];
  let q = ($ - Q) / K;
  if (q > 0 && q < 1)
    return [q];
  return [];
}
function b$($, Q, Z, K) {
  let q = 3 * (K - 3 * Z + 3 * Q - $), J = 6 * (Z - 2 * Q + $), U = 3 * (Q - $), W = [];
  if (Math.abs(q) < 0.0000000001) {
    if (Math.abs(J) < 0.0000000001)
      return [];
    let X = -U / J;
    if (X > 0 && X < 1)
      W.push(X);
    return W;
  }
  let H = J * J - 4 * q * U;
  if (H < 0)
    return [];
  if (Math.abs(H) < 0.0000000001) {
    let X = -J / (2 * q);
    if (X > 0 && X < 1)
      W.push(X);
    return W;
  }
  let _ = Math.sqrt(H), N = (-J + _) / (2 * q), V = (-J - _) / (2 * q);
  if (N > 0 && N < 1)
    W.push(N);
  if (V > 0 && V < 1)
    W.push(V);
  return W;
}
function n9($, Q, Z, K) {
  let q = 1 - K;
  return q * q * $ + 2 * q * K * Q + K * K * Z;
}
function p9($, Q, Z, K, q) {
  let J = 1 - q, U = J * J, W = q * q;
  return U * J * $ + 3 * U * q * Q + 3 * J * W * Z + W * q * K;
}
G6();
function m$($) {
  if ($ <= 0)
    return new Float32Array([1]);
  let Q = $, Z = Math.ceil($ * 2) * 2 + 1, K = new Float32Array(Z), q = Math.floor(Z / 2), J = 0;
  for (let U = 0;U < Z; U++) {
    let W = U - q, H = Math.exp(-(W * W) / (2 * Q * Q));
    K[U] = H, J += H;
  }
  for (let U = 0;U < Z; U++)
    K[U] /= J;
  return K;
}
function M4($, Q, Z, K, q, J, U, W) {
  let H = Math.floor(U.length / 2);
  for (let _ = 0;_ < K; _++)
    for (let N = 0;N < Z; N++)
      for (let V = 0;V < W; V++) {
        let X = 0;
        for (let G = 0;G < U.length; G++) {
          let Y = N + G - H, j = Math.max(0, Math.min(Z - 1, Y)), E = _ * q + j * W + V;
          X += ($[E] ?? 0) * U[G];
        }
        let z = _ * J + N * W + V;
        Q[z] = Math.min(255, Math.max(0, Math.round(X)));
      }
}
function T4($, Q, Z, K, q, J, U, W) {
  let H = Math.floor(U.length / 2);
  for (let _ = 0;_ < K; _++)
    for (let N = 0;N < Z; N++)
      for (let V = 0;V < W; V++) {
        let X = 0;
        for (let G = 0;G < U.length; G++) {
          let Y = _ + G - H, E = Math.max(0, Math.min(K - 1, Y)) * q + N * W + V;
          X += ($[E] ?? 0) * U[G];
        }
        let z = _ * J + N * W + V;
        Q[z] = Math.min(255, Math.max(0, Math.round(X)));
      }
}
function i6($, Q) {
  if ($.pixelMode === 0) {
    let J = N8($, 1);
    return i6(J, Q);
  }
  if (Q <= 0)
    return $;
  let Z = m$(Q), K = 1;
  if ($.pixelMode === 2 || $.pixelMode === 3)
    K = 3;
  else if ($.pixelMode === 4)
    K = 4;
  let q = new Uint8Array($.buffer.length);
  return M4($.buffer, q, $.width, $.rows, $.pitch, $.pitch, Z, K), T4(q, $.buffer, $.width, $.rows, $.pitch, $.pitch, Z, K), $;
}
function B8($, Q) {
  if ($.pixelMode === 0) {
    let U = N8($, 1);
    return B8(U, Q);
  }
  if (Q <= 0)
    return $;
  let Z = 1;
  if ($.pixelMode === 2 || $.pixelMode === 3)
    Z = 3;
  else if ($.pixelMode === 4)
    Z = 4;
  let K = Math.floor(Q), q = K * 2 + 1, J = new Uint8Array($.buffer.length);
  for (let U = 0;U < $.rows; U++)
    for (let W = 0;W < Z; W++) {
      let H = 0, _ = 0;
      for (let V = -K;V <= K; V++)
        if (V >= 0 && V < $.width) {
          let X = U * $.pitch + V * Z + W;
          H += $.buffer[X] ?? 0, _++;
        }
      let N = U * $.pitch + 0 * Z + W;
      J[N] = Math.round(H / _);
      for (let V = 1;V < $.width; V++) {
        let X = V - K - 1;
        if (X >= 0) {
          let Y = U * $.pitch + X * Z + W;
          H -= $.buffer[Y] ?? 0, _--;
        }
        let z = V + K;
        if (z < $.width) {
          let Y = U * $.pitch + z * Z + W;
          H += $.buffer[Y] ?? 0, _++;
        }
        let G = U * $.pitch + V * Z + W;
        J[G] = Math.round(H / _);
      }
    }
  for (let U = 0;U < $.width; U++)
    for (let W = 0;W < Z; W++) {
      let H = 0, _ = 0;
      for (let V = -K;V <= K; V++)
        if (V >= 0 && V < $.rows) {
          let X = V * $.pitch + U * Z + W;
          H += J[X] ?? 0, _++;
        }
      let N = 0 * $.pitch + U * Z + W;
      $.buffer[N] = Math.round(H / _);
      for (let V = 1;V < $.rows; V++) {
        let X = V - K - 1;
        if (X >= 0) {
          let Y = X * $.pitch + U * Z + W;
          H -= J[Y] ?? 0, _--;
        }
        let z = V + K;
        if (z < $.rows) {
          let Y = z * $.pitch + U * Z + W;
          H += J[Y] ?? 0, _++;
        }
        let G = V * $.pitch + U * Z + W;
        $.buffer[G] = Math.round(H / _);
      }
    }
  return $;
}
function wK($, Q, Z = "gaussian") {
  if (Z === "box")
    return B8($, Q);
  else
    return i6($, Q);
}
G6();
function O4($, Q, Z) {
  return [Math.round($[0] + (Q[0] - $[0]) * Z), Math.round($[1] + (Q[1] - $[1]) * Z), Math.round($[2] + (Q[2] - $[2]) * Z), Math.round($[3] + (Q[3] - $[3]) * Z)];
}
function o0($, Q, Z) {
  if ($.stops.length === 0)
    return [0, 0, 0, 0];
  if ($.stops.length === 1)
    return $.stops[0].color;
  let K;
  if ($.type === "linear") {
    let J = $.x1 - $.x0, U = $.y1 - $.y0, W = J * J + U * U;
    if (W === 0)
      return $.stops[0].color;
    let H = Q - $.x0, _ = Z - $.y0;
    K = (H * J + _ * U) / W;
  } else {
    let J = Q - $.cx, U = Z - $.cy, W = Math.sqrt(J * J + U * U);
    K = $.radius > 0 ? W / $.radius : 0;
  }
  K = Math.max(0, Math.min(1, K));
  let q = [...$.stops].sort((J, U) => J.offset - U.offset);
  if (K <= q[0].offset)
    return q[0].color;
  if (K >= q[q.length - 1].offset)
    return q[q.length - 1].color;
  for (let J = 0;J < q.length - 1; J++) {
    let U = q[J], W = q[J + 1];
    if (K >= U.offset && K <= W.offset) {
      let H = W.offset - U.offset;
      if (H === 0)
        return W.color;
      let _ = (K - U.offset) / H;
      return O4(U.color, W.color, _);
    }
  }
  return q[q.length - 1].color;
}
function i0($, Q, Z) {
  let K = r($, Q, 4);
  for (let q = 0;q < Q; q++)
    for (let J = 0;J < $; J++) {
      let U = o0(Z, J, q), W = (q * $ + J) * 4;
      K.buffer[W] = U[0], K.buffer[W + 1] = U[1], K.buffer[W + 2] = U[2], K.buffer[W + 3] = U[3];
    }
  return K;
}
function W9($, Q, Z) {
  let { width: K, height: q } = Z, J = $8($, { ...Z, pixelMode: 1, flipY: Z.flipY ?? false }), U = r(K, q, 4);
  for (let W = 0;W < q; W++)
    for (let H = 0;H < K; H++) {
      let _ = J.buffer[W * J.pitch + H] ?? 0;
      if (_ > 0) {
        let N = o0(Q, H, W), V = (W * K + H) * 4, X = N[3] * _ / 255;
        U.buffer[V] = N[0], U.buffer[V + 1] = N[1], U.buffer[V + 2] = N[2], U.buffer[V + 3] = Math.round(X);
      }
    }
  return U;
}
G6();
function f8($, Q) {
  let { width: Z, height: K, scale: q, offsetX: J = 0, offsetY: U = 0, flipY: W = false, spread: H = 8 } = Q, _ = r(Z, K, 1), N = P4($, q, J, U, W);
  if (N.length === 0)
    return _.buffer.fill(0), _;
  for (let V = 0;V < K; V++)
    for (let X = 0;X < Z; X++) {
      let z = X + 0.5, G = V + 0.5, Y = 1 / 0;
      for (let A = 0;A < N.length; A++) {
        let D = N[A], F = I4(z, G, D);
        Y = Math.min(Y, F);
      }
      let w = 128 + (S4(z, G, N) ? Y : -Y) / H * 127, L = Math.max(0, Math.min(255, Math.round(w)));
      _.buffer[V * _.pitch + X] = L;
    }
  return _;
}
function P4($, Q, Z, K, q) {
  let J = [], U = null, W = null, H = (_, N) => ({ x: _ * Q + Z, y: q ? -(N * Q) + K : N * Q + K });
  for (let _ = 0;_ < $.commands.length; _++) {
    let N = $.commands[_];
    switch (N.type) {
      case "M":
        U = H(N.x, N.y), W = U;
        break;
      case "L":
        if (U) {
          let V = H(N.x, N.y);
          J.push({ type: "line", p0: U, p1: V }), U = V;
        }
        break;
      case "Q":
        if (U) {
          let V = H(N.x1, N.y1), X = H(N.x, N.y);
          J.push({ type: "quadratic", p0: U, p1: V, p2: X }), U = X;
        }
        break;
      case "C":
        if (U) {
          let V = H(N.x1, N.y1), X = H(N.x2, N.y2), z = H(N.x, N.y);
          J.push({ type: "cubic", p0: U, p1: V, p2: X, p3: z }), U = z;
        }
        break;
      case "Z":
        if (U && W) {
          if (Math.abs(U.x - W.x) > 0.001 || Math.abs(U.y - W.y) > 0.001)
            J.push({ type: "line", p0: U, p1: W });
          U = W;
        }
        break;
    }
  }
  return J;
}
function I4($, Q, Z) {
  switch (Z.type) {
    case "line":
      return v4($, Q, Z.p0, Z.p1);
    case "quadratic":
      return k4($, Q, Z.p0, Z.p1, Z.p2);
    case "cubic":
      return C4($, Q, Z.p0, Z.p1, Z.p2, Z.p3);
  }
}
function v4($, Q, Z, K) {
  let q = K.x - Z.x, J = K.y - Z.y, U = q * q + J * J;
  if (U < 0.0001) {
    let X = $ - Z.x, z = Q - Z.y;
    return Math.sqrt(X * X + z * z);
  }
  let W = (($ - Z.x) * q + (Q - Z.y) * J) / U;
  W = Math.max(0, Math.min(1, W));
  let H = Z.x + W * q, _ = Z.y + W * J, N = $ - H, V = Q - _;
  return Math.sqrt(N * N + V * V);
}
function k4($, Q, Z, K, q) {
  let J = 1 / 0, U = 32;
  for (let W = 0;W <= U; W++) {
    let H = W / U, _ = 1 - H, N = _ * _ * Z.x + 2 * _ * H * K.x + H * H * q.x, V = _ * _ * Z.y + 2 * _ * H * K.y + H * H * q.y, X = $ - N, z = Q - V, G = Math.sqrt(X * X + z * z);
    J = Math.min(J, G);
  }
  return J;
}
function C4($, Q, Z, K, q, J) {
  let U = 1 / 0, W = 32;
  for (let H = 0;H <= W; H++) {
    let _ = H / W, N = 1 - _, V = N * N * N * Z.x + 3 * N * N * _ * K.x + 3 * N * _ * _ * q.x + _ * _ * _ * J.x, X = N * N * N * Z.y + 3 * N * N * _ * K.y + 3 * N * _ * _ * q.y + _ * _ * _ * J.y, z = $ - V, G = Q - X, Y = Math.sqrt(z * z + G * G);
    U = Math.min(U, Y);
  }
  return U;
}
function S4($, Q, Z) {
  let K = 0;
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q], U = h4(J);
    for (let W = 0;W < U.length - 1; W++) {
      let H = U[W], _ = U[W + 1];
      if (!H || !_)
        continue;
      if (H.y > Q !== _.y > Q) {
        let N = (_.x - H.x) / (_.y - H.y), V = H.x + N * (Q - H.y);
        if ($ < V)
          K++;
      }
    }
  }
  return (K & 1) === 1;
}
function h4($) {
  switch ($.type) {
    case "line":
      return [$.p0, $.p1];
    case "quadratic": {
      let Q = [$.p0], Z = 16;
      for (let K = 1;K <= 16; K++) {
        let q = K / 16, J = 1 - q;
        Q.push({ x: J * J * $.p0.x + 2 * J * q * $.p1.x + q * q * $.p2.x, y: J * J * $.p0.y + 2 * J * q * $.p1.y + q * q * $.p2.y });
      }
      return Q;
    }
    case "cubic": {
      let Q = [$.p0], Z = 16;
      for (let K = 1;K <= 16; K++) {
        let q = K / 16, J = 1 - q;
        Q.push({ x: J * J * J * $.p0.x + 3 * J * J * q * $.p1.x + 3 * J * q * q * $.p2.x + q * q * q * $.p3.x, y: J * J * J * $.p0.y + 3 * J * J * q * $.p1.y + 3 * J * q * q * $.p2.y + q * q * q * $.p3.y });
      }
      return Q;
    }
  }
}
function g$($) {
  let Q = Math.sqrt($.x * $.x + $.y * $.y);
  if (Q === 0)
    return { x: 0, y: 0 };
  return { x: $.x / Q, y: $.y / Q };
}
function d6($) {
  return { x: -$.y, y: $.x };
}
function y4($, Q) {
  let Z = $.x * Q.x + $.y * Q.y, K = $.x * Q.y - $.y * Q.x;
  return Math.atan2(K, Z);
}
function AK($, Q, Z, K, q) {
  let J = d6(Z), U = { x: Q.x + J.x * K * (q ? 1 : -1), y: Q.y + J.y * K * (q ? 1 : -1) }, W = { x: Q.x - J.x * K * (q ? 1 : -1), y: Q.y - J.y * K * (q ? 1 : -1) }, H = { x: Q.x + Z.x * K, y: Q.y + Z.y * K }, _ = { x: U.x + Z.x * K * 0.5523, y: U.y + Z.y * K * 0.5523 }, N = { x: W.x + Z.x * K * 0.5523, y: W.y + Z.y * K * 0.5523 };
  $.push({ type: "Q", x1: _.x, y1: _.y, x: H.x, y: H.y }), $.push({ type: "Q", x1: N.x, y1: N.y, x: W.x, y: W.y });
}
function LK($, Q, Z, K) {
  let q = d6(Z), J = { x: Q.x + Z.x * K, y: Q.y + Z.y * K }, U = { x: J.x + q.x * K, y: J.y + q.y * K }, W = { x: J.x - q.x * K, y: J.y - q.y * K };
  $.push({ type: "L", x: U.x, y: U.y }), $.push({ type: "L", x: W.x, y: W.y });
}
function s0($, Q, Z, K, q, J, U, W) {
  let H = y4(Z, K), _ = W ? H < 0 : H > 0;
  if (Math.abs(H) < 0.01)
    return;
  let N = d6(Z), V = d6(K), X = W ? 1 : -1, z = { x: Q.x + N.x * q * X, y: Q.y + N.y * q * X }, G = { x: Q.x + V.x * q * X, y: Q.y + V.y * q * X };
  if (!_) {
    $.push({ type: "L", x: G.x, y: G.y });
    return;
  }
  switch (J) {
    case "round": {
      let Y = Math.abs(H), j = Math.max(2, Math.ceil(Y / (Math.PI / 4)));
      for (let E = 1;E <= j; E++) {
        let w = E / j, L = Math.atan2(N.y, N.x) + H * w * X, A = Q.x + Math.cos(L) * q * X, D = Q.y + Math.sin(L) * q * X;
        $.push({ type: "L", x: A, y: D });
      }
      break;
    }
    case "miter": {
      if (1 / Math.sin(Math.abs(H) / 2) <= U) {
        let j = H / 2, E = g$({ x: Z.x + K.x, y: Z.y + K.y }), w = q / Math.cos(j), L = { x: Q.x + E.x * w * X, y: Q.y + E.y * w * X };
        $.push({ type: "L", x: L.x, y: L.y }), $.push({ type: "L", x: G.x, y: G.y });
      } else
        $.push({ type: "L", x: G.x, y: G.y });
      break;
    }
    default:
      $.push({ type: "L", x: G.x, y: G.y });
      break;
  }
}
function b4($, Q) {
  if ($.length < 2)
    return [];
  let { width: Z, lineJoin: K, miterLimit: q } = Q, J = Z / 2, U = [], W = $.length, H = [];
  for (let j = 0;j < W; j++) {
    let E = $[j], w = $[(j + 1) % W];
    if (!E || !w)
      continue;
    H.push(g$({ x: w.x - E.x, y: w.y - E.y }));
  }
  let _ = H[0], N = $[0];
  if (!_ || !N)
    return [];
  let V = d6(_);
  U.push({ type: "M", x: N.x + V.x * J, y: N.y + V.y * J });
  for (let j = 0;j < W; j++) {
    let E = $[j], w = $[(j + 1) % W], L = H[j], A = H[(j + 1) % W];
    if (!E || !w || !L || !A)
      continue;
    let D = d6(L);
    U.push({ type: "L", x: w.x + D.x * J, y: w.y + D.y * J }), s0(U, w, L, A, J, K, q, true);
  }
  U.push({ type: "Z" });
  let X = W - 1, z = H[X], G = $[0];
  if (!z || !G)
    return U;
  let Y = d6(_);
  U.push({ type: "M", x: N.x - Y.x * J, y: N.y - Y.y * J });
  for (let j = 0;j < W; j++) {
    let E = $[j], w = $[(j + 1) % W], L = H[j], A = H[(j + 1) % W];
    if (!E || !w || !L || !A)
      continue;
    let D = d6(L);
    U.push({ type: "L", x: w.x - D.x * J, y: w.y - D.y * J }), s0(U, w, L, A, J, K, q, false);
  }
  return U.push({ type: "Z" }), U;
}
function m4($, Q) {
  if ($.length < 2)
    return [];
  let { width: Z, lineCap: K, lineJoin: q, miterLimit: J } = Q, U = Z / 2, W = [], H = $.length, _ = [];
  for (let E = 0;E < H - 1; E++) {
    let w = $[E], L = $[E + 1];
    if (!w || !L)
      continue;
    _.push(g$({ x: L.x - w.x, y: L.y - w.y }));
  }
  let N = $[0], V = $[H - 1], X = _[0], z = _[_.length - 1];
  if (!N || !V || !X || !z)
    return [];
  let G = d6(X);
  W.push({ type: "M", x: N.x + G.x * U, y: N.y + G.y * U });
  for (let E = 0;E < H - 1; E++) {
    let w = $[E + 1], L = _[E], A = _[E + 1];
    if (!w || !L)
      continue;
    let D = d6(L);
    if (W.push({ type: "L", x: w.x + D.x * U, y: w.y + D.y * U }), A)
      s0(W, w, L, A, U, q, J, true);
  }
  let Y = { x: -z.x, y: -z.y };
  switch (K) {
    case "round":
      AK(W, V, z, U, true);
      break;
    case "square":
      LK(W, V, z, U);
      break;
    default: {
      let E = d6(z);
      W.push({ type: "L", x: V.x - E.x * U, y: V.y - E.y * U });
      break;
    }
  }
  for (let E = H - 2;E >= 0; E--) {
    let w = $[E], L = _[E], A = _[E - 1];
    if (!w || !L)
      continue;
    let D = d6(L);
    if (W.push({ type: "L", x: w.x - D.x * U, y: w.y - D.y * U }), A)
      s0(W, w, { x: -L.x, y: -L.y }, { x: -A.x, y: -A.y }, U, q, J, true);
  }
  let j = { x: -X.x, y: -X.y };
  switch (K) {
    case "round":
      AK(W, N, j, U, true);
      break;
    case "square":
      LK(W, N, j, U);
      break;
    default:
      break;
  }
  return W.push({ type: "Z" }), W;
}
function g4($) {
  let Q = [], Z = [];
  for (let K = 0;K < $.commands.length; K++) {
    let q = $.commands[K];
    switch (q.type) {
      case "M":
        if (Z.length > 0)
          Q.push({ points: Z, closed: false });
        Z = [{ x: q.x, y: q.y }];
        break;
      case "L":
        Z.push({ x: q.x, y: q.y });
        break;
      case "Q": {
        let J = Z[Z.length - 1];
        if (J)
          for (let W = 1;W <= 8; W++) {
            let H = W / 8, _ = 1 - H, N = _ * _ * J.x + 2 * _ * H * q.x1 + H * H * q.x, V = _ * _ * J.y + 2 * _ * H * q.y1 + H * H * q.y;
            Z.push({ x: N, y: V });
          }
        break;
      }
      case "C": {
        let J = Z[Z.length - 1];
        if (J)
          for (let W = 1;W <= 12; W++) {
            let H = W / 12, _ = 1 - H, N = _ * _ * _ * J.x + 3 * _ * _ * H * q.x1 + 3 * _ * H * H * q.x2 + H * H * H * q.x, V = _ * _ * _ * J.y + 3 * _ * _ * H * q.y1 + 3 * _ * H * H * q.y2 + H * H * H * q.y;
            Z.push({ x: N, y: V });
          }
        break;
      }
      case "Z":
        if (Z.length > 0)
          Q.push({ points: Z, closed: true }), Z = [];
        break;
    }
  }
  if (Z.length > 0)
    Q.push({ points: Z, closed: false });
  return Q;
}
function H9($, Q) {
  let Z = { width: Q.width, lineCap: Q.lineCap ?? "butt", lineJoin: Q.lineJoin ?? "miter", miterLimit: Q.miterLimit ?? 4 }, K = g4($), q = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W.points.length < 2)
      continue;
    let H = W.closed ? b4(W.points, Z) : m4(W.points, Z);
    q.push(...H);
  }
  let J = $.bounds;
  if (J) {
    let U = Z.width / 2;
    J = { xMin: J.xMin - U, yMin: J.yMin - U, xMax: J.xMax + U, yMax: J.yMax + U };
  }
  return { commands: q, bounds: J };
}
function u4($, Q, Z, K) {
  let q = -Q * K, J = $ * Z, U = Math.sqrt(q * q + J * J);
  if (U < 0.0000000001)
    return { x: 0, y: 0, len: 0 };
  return { x: q / U, y: J / U, len: U };
}
function u$($, Q, Z, K, q) {
  let J = Z.x - $.x, U = Z.y - $.y;
  if (Math.abs((Q.x - $.x) * U - (Q.y - $.y) * J) / Math.sqrt(J * J + U * U + 0.0000000001) <= K) {
    q.push(Z);
    return;
  }
  let H = { x: ($.x + Q.x) / 2, y: ($.y + Q.y) / 2 }, _ = { x: (Q.x + Z.x) / 2, y: (Q.y + Z.y) / 2 }, N = { x: (H.x + _.x) / 2, y: (H.y + _.y) / 2 };
  u$($, H, N, K, q), u$(N, _, Z, K, q);
}
function x$($, Q, Z, K, q, J) {
  let U = K.x - $.x, W = K.y - $.y, H = U * U + W * W + 0.0000000001, _ = Math.abs((Q.x - $.x) * W - (Q.y - $.y) * U) / Math.sqrt(H), N = Math.abs((Z.x - $.x) * W - (Z.y - $.y) * U) / Math.sqrt(H);
  if (_ <= q && N <= q) {
    J.push(K);
    return;
  }
  let V = { x: ($.x + Q.x) / 2, y: ($.y + Q.y) / 2 }, X = { x: (Q.x + Z.x) / 2, y: (Q.y + Z.y) / 2 }, z = { x: (Z.x + K.x) / 2, y: (Z.y + K.y) / 2 }, G = { x: (V.x + X.x) / 2, y: (V.y + X.y) / 2 }, Y = { x: (X.x + z.x) / 2, y: (X.y + z.y) / 2 }, j = { x: (G.x + Y.x) / 2, y: (G.y + Y.y) / 2 };
  x$($, V, G, j, q, J), x$(j, Y, z, K, q, J);
}
function x4($, Q) {
  let Z = [], K = [], q = { x: 0, y: 0 }, J = { x: 0, y: 0 };
  for (let U = 0;U < $.commands.length; U++) {
    let W = $.commands[U];
    switch (W.type) {
      case "M":
        if (K.length > 1)
          Z.push({ points: K, closed: false });
        q = { x: W.x, y: W.y }, J = q, K = [q];
        break;
      case "L":
        q = { x: W.x, y: W.y }, K.push(q);
        break;
      case "Q":
        u$(q, { x: W.x1, y: W.y1 }, { x: W.x, y: W.y }, Q, K), q = { x: W.x, y: W.y };
        break;
      case "C":
        x$(q, { x: W.x1, y: W.y1 }, { x: W.x2, y: W.y2 }, { x: W.x, y: W.y }, Q, K), q = { x: W.x, y: W.y };
        break;
      case "Z":
        if (K.length > 1) {
          let H = K[0], _ = K[K.length - 1];
          if (H && _) {
            let N = _.x - H.x, V = _.y - H.y;
            if (N * N + V * V > Q * Q)
              K.push({ ...H });
          }
          Z.push({ points: K, closed: true });
        }
        K = [], q = J;
        break;
    }
  }
  if (K.length > 1)
    Z.push({ points: K, closed: false });
  return Z;
}
function f4($) {
  if ($.length < 3)
    return 0;
  let Q = $.length, Z = $[0], K = $[Q - 1];
  if (Z && K) {
    let J = K.x - Z.x, U = K.y - Z.y;
    if (J * J + U * U < 1)
      Q -= 1;
  }
  if (Q < 3)
    return 0;
  let q = 0;
  for (let J = 0;J < Q; J++) {
    let U = $[J], W = $[(J + 1) % Q];
    if (!U || !W)
      continue;
    q += U.x * W.y - W.x * U.y;
  }
  return q;
}
function FK($, Q, Z, K, q, J, U) {
  let W = Z.x * K.x + Z.y * K.y, H = Z.x * K.y - Z.y * K.x, _ = Math.atan2(H, W), N = Math.max(2, Math.ceil(Math.abs(_) / (Math.PI / 8))), V = U ? 1 : -1, X = Math.atan2(Z.y, Z.x);
  for (let z = 1;z <= N; z++) {
    let G = z / N, Y = X + _ * G, j = Math.cos(Y), E = Math.sin(Y);
    $.push({ x: Q.x + j * q * V, y: Q.y + E * J * V });
  }
}
function DK($, Q, Z, K, q, J, U, W) {
  let H = W ? 1 : -1, _ = Z.x * K.x + Z.y * K.y, N = Math.sqrt((1 - _) / 2);
  if (N < 0.0000000001)
    return false;
  let V = 1 / N;
  if (V > U)
    return false;
  let X = Z.x + K.x, z = Z.y + K.y, G = Math.sqrt(X * X + z * z);
  if (G < 0.0000000001)
    return false;
  let Y = V * Math.sqrt(q * q + J * J) / 2;
  return $.push({ x: Q.x + X / G * Y * H, y: Q.y + z / G * Y * H }), true;
}
function l4($, Q, Z, K, q, J) {
  let U = $.length;
  if (U < 3)
    return { outer: [], inner: [] };
  let W = U - 1, H = $[0], _ = $[W], N = U;
  if (H && _) {
    let Y = _.x - H.x, j = _.y - H.y;
    if (Y * Y + j * j < 1)
      N = U - 1;
  }
  if (N < 3)
    return { outer: [], inner: [] };
  let V = J ? 1 : -1, X = [];
  for (let Y = 0;Y < N; Y++) {
    let j = $[Y], E = $[(Y + 1) % N];
    if (!j || !E)
      continue;
    let w = E.x - j.x, L = E.y - j.y, A = Math.sqrt(w * w + L * L);
    if (A < 0.0000000001)
      X.push({ x: 0, y: 0, len: 0 });
    else {
      let D = u4(w / A, L / A, Q, Z);
      if (V < 0)
        D.x = -D.x, D.y = -D.y;
      X.push(D);
    }
  }
  let z = [], G = [];
  for (let Y = 0;Y < N; Y++) {
    let j = $[Y], E = X[(Y - 1 + N) % N], w = X[Y];
    if (!j || !E || !w)
      continue;
    if (E.len < 0.0000000001 || w.len < 0.0000000001) {
      z.push({ x: j.x + (w.len > 0 ? w.x * Q : 0), y: j.y + (w.len > 0 ? w.y * Z : 0) });
      continue;
    }
    let L = { x: j.x + w.x * Q, y: j.y + w.y * Z }, A = E.x * w.y - E.y * w.x, D = A > 0;
    if (Math.abs(A) < 0.01)
      z.push(L);
    else if (D)
      switch (K) {
        case "round":
          FK(z, j, E, w, Q, Z, true);
          break;
        case "miter":
          if (!DK(z, j, E, w, Q, Z, q, true))
            z.push(L);
          break;
        default:
          z.push(L);
          break;
      }
    else
      z.push(L);
  }
  for (let Y = 0;Y < N; Y++) {
    let j = $[Y], E = X[(Y - 1 + N) % N], w = X[Y];
    if (!j || !E || !w)
      continue;
    if (E.len < 0.0000000001 || w.len < 0.0000000001) {
      G.push({ x: j.x - (w.len > 0 ? w.x * Q : 0), y: j.y - (w.len > 0 ? w.y * Z : 0) });
      continue;
    }
    let L = { x: j.x - w.x * Q, y: j.y - w.y * Z }, A = E.x * w.y - E.y * w.x, D = A < 0;
    if (Math.abs(A) < 0.01)
      G.push(L);
    else if (D)
      switch (K) {
        case "round":
          FK(G, j, E, w, Q, Z, false);
          break;
        case "miter":
          if (!DK(G, j, E, w, Q, Z, q, false))
            G.push(L);
          break;
        default:
          G.push(L);
          break;
      }
    else
      G.push(L);
  }
  return { outer: z, inner: G };
}
function BK($, Q) {
  if ($.length === 0)
    return [];
  let Z = [], K = $[0];
  if (!K)
    return [];
  Z.push({ type: "M", x: K.x, y: K.y });
  for (let q = 1;q < $.length; q++) {
    let J = $[q];
    if (!J)
      continue;
    Z.push({ type: "L", x: J.x, y: J.y });
  }
  if (Q)
    Z.push({ type: "Z" });
  return Z;
}
function V8($, Q) {
  let { xBorder: Z, yBorder: K, eps: q = 1, lineJoin: J = "round", miterLimit: U = 4 } = Q;
  if (Z <= 0 && K <= 0)
    return { outer: { commands: [], bounds: null }, inner: { commands: [], bounds: null } };
  let W = x4($, q), H = [], _ = [], N = [], V = 1, X = 0;
  for (let Y = 0;Y < W.length; Y++) {
    let j = W[Y], E = f4(j.points);
    N[Y] = E;
    let w = Math.abs(E);
    if (w > X)
      X = w, V = E >= 0 ? 1 : -1;
  }
  if (X === 0)
    V = 1;
  for (let Y = 0;Y < W.length; Y++) {
    let j = W[Y];
    if (!j.closed) {
      let R = j.points[0];
      if (R)
        j.points.push(R);
      j.closed = true;
    }
    let w = (N[Y] ?? 0) >= 0 ? 1 : -1, L = w > 0, D = w === V ? !L : L, { outer: F, inner: M } = l4(j.points, Z, K, J, U, D);
    M.reverse(), H.push(...BK(F, true)), _.push(...BK(M, true));
  }
  let z = null, G = null;
  if ($.bounds)
    z = { xMin: $.bounds.xMin - Z, yMin: $.bounds.yMin - K, xMax: $.bounds.xMax + Z, yMax: $.bounds.yMax + K }, G = { xMin: $.bounds.xMin + Z, yMin: $.bounds.yMin + K, xMax: $.bounds.xMax - Z, yMax: $.bounds.yMax - K };
  return { outer: { commands: H, bounds: z, flags: $.flags }, inner: { commands: _, bounds: G, flags: $.flags } };
}
function c9($, Q) {
  let { outer: Z, inner: K } = V8($, Q), q = [...Z.commands, ...K.commands], J = Z.bounds;
  return { commands: q, bounds: J, flags: $.flags };
}
function d4($, Q, Z) {
  return V8($, { xBorder: Q, yBorder: Q, ...Z });
}
function RK($) {
  let Q = 1 / 0, Z = 1 / 0, K = -1 / 0, q = -1 / 0;
  for (let J = 0;J < $.length; J++) {
    let U = $[J];
    switch (U.type) {
      case "M":
      case "L":
        Q = Math.min(Q, U.x), K = Math.max(K, U.x), Z = Math.min(Z, U.y), q = Math.max(q, U.y);
        break;
      case "Q":
        Q = Math.min(Q, U.x, U.x1), K = Math.max(K, U.x, U.x1), Z = Math.min(Z, U.y, U.y1), q = Math.max(q, U.y, U.y1);
        break;
      case "C":
        Q = Math.min(Q, U.x, U.x1, U.x2), K = Math.max(K, U.x, U.x1, U.x2), Z = Math.min(Z, U.y, U.y1, U.y2), q = Math.max(q, U.y, U.y1, U.y2);
        break;
      case "Z":
        break;
    }
  }
  if (!Number.isFinite(Q))
    return null;
  return { xMin: Q, yMin: Z, xMax: K, yMax: q };
}
function _9($, Q) {
  let Z = [];
  for (let q = 0;q < $.commands.length; q++) {
    let J = $.commands[q];
    switch (J.type) {
      case "M":
      case "L":
        Z.push({ type: J.type, x: J.x + J.y * Q, y: J.y });
        break;
      case "Q":
        Z.push({ type: "Q", x1: J.x1 + J.y1 * Q, y1: J.y1, x: J.x + J.y * Q, y: J.y });
        break;
      case "C":
        Z.push({ type: "C", x1: J.x1 + J.y1 * Q, y1: J.y1, x2: J.x2 + J.y2 * Q, y2: J.y2, x: J.x + J.y * Q, y: J.y });
        break;
      case "Z":
        Z.push({ type: "Z" });
        break;
    }
  }
  let K = RK(Z);
  return { commands: Z, bounds: K, flags: $.flags };
}
function N9($, Q) {
  let Z = [];
  for (let q = 0;q < $.commands.length; q++) {
    let J = $.commands[q];
    switch (J.type) {
      case "M":
      case "L":
        Z.push({ type: J.type, x: J.x * Q, y: J.y });
        break;
      case "Q":
        Z.push({ type: "Q", x1: J.x1 * Q, y1: J.y1, x: J.x * Q, y: J.y });
        break;
      case "C":
        Z.push({ type: "C", x1: J.x1 * Q, y1: J.y1, x2: J.x2 * Q, y2: J.y2, x: J.x * Q, y: J.y });
        break;
      case "Z":
        Z.push({ type: "Z" });
        break;
    }
  }
  let K = $.bounds;
  if (K)
    K = { xMin: K.xMin * Q, yMin: K.yMin, xMax: K.xMax * Q, yMax: K.yMax };
  return { commands: Z, bounds: K, flags: $.flags };
}
function V9($, Q) {
  if (Q === 0)
    return { ...$, commands: [...$.commands] };
  if (Q > 0) {
    let { outer: J } = V8($, { xBorder: Q, yBorder: Q, eps: 0.25, lineJoin: "round" });
    return { commands: J.commands, bounds: J.bounds ?? RK(J.commands), flags: $.flags };
  }
  let Z = n4($), K = [];
  for (let J = 0;J < Z.length; J++) {
    let U = Z[J];
    if (U.length < 3)
      continue;
    let W = p4(U, Q);
    if (W.length > 0) {
      K.push({ type: "M", x: W[0].x, y: W[0].y });
      for (let H = 1;H < W.length; H++)
        K.push({ type: "L", x: W[H].x, y: W[H].y });
      K.push({ type: "Z" });
    }
  }
  let q = null;
  if (K.length > 0) {
    let J = 1 / 0, U = 1 / 0, W = -1 / 0, H = -1 / 0;
    for (let _ = 0;_ < K.length; _++) {
      let N = K[_];
      if (N.type === "M" || N.type === "L")
        J = Math.min(J, N.x), U = Math.min(U, N.y), W = Math.max(W, N.x), H = Math.max(H, N.y);
    }
    if (Number.isFinite(J))
      q = { xMin: J, yMin: U, xMax: W, yMax: H };
  }
  return { commands: K, bounds: q, flags: $.flags };
}
function n4($) {
  let Q = [], Z = [];
  for (let K = 0;K < $.commands.length; K++) {
    let q = $.commands[K];
    switch (q.type) {
      case "M":
        if (Z.length > 0)
          Q.push(Z);
        Z = [{ x: q.x, y: q.y }];
        break;
      case "L":
        Z.push({ x: q.x, y: q.y });
        break;
      case "Q": {
        let J = Z[Z.length - 1];
        if (J)
          for (let W = 1;W <= 8; W++) {
            let H = W / 8, _ = 1 - H, N = _ * _ * J.x + 2 * _ * H * q.x1 + H * H * q.x, V = _ * _ * J.y + 2 * _ * H * q.y1 + H * H * q.y;
            Z.push({ x: N, y: V });
          }
        break;
      }
      case "C": {
        let J = Z[Z.length - 1];
        if (J)
          for (let W = 1;W <= 12; W++) {
            let H = W / 12, _ = 1 - H, N = _ * _ * _ * J.x + 3 * _ * _ * H * q.x1 + 3 * _ * H * H * q.x2 + H * H * H * q.x, V = _ * _ * _ * J.y + 3 * _ * _ * H * q.y1 + 3 * _ * H * H * q.y2 + H * H * H * q.y;
            Z.push({ x: N, y: V });
          }
        break;
      }
      case "Z":
        if (Z.length > 0)
          Q.push(Z), Z = [];
        break;
    }
  }
  if (Z.length > 0)
    Q.push(Z);
  return Q;
}
function p4($, Q) {
  let Z = $.length;
  if (Z < 3)
    return $;
  let K = 0;
  for (let W = 0;W < Z; W++) {
    let H = (W + 1) % Z, _ = $[W], N = $[H];
    if (_ && N)
      K += (N.x - _.x) * (N.y + _.y);
  }
  let q = K < 0 ? -1 : 1, J = Q * q, U = [];
  for (let W = 0;W < Z; W++) {
    let H = $[(W - 1 + Z) % Z], _ = $[W], N = $[(W + 1) % Z];
    if (!H || !_ || !N)
      continue;
    let V = { x: _.x - H.x, y: _.y - H.y }, X = { x: N.x - _.x, y: N.y - _.y }, z = Math.sqrt(V.x * V.x + V.y * V.y), G = Math.sqrt(X.x * X.x + X.y * X.y);
    if (z === 0 || G === 0) {
      U.push(_);
      continue;
    }
    V.x /= z, V.y /= z, X.x /= G, X.y /= G;
    let Y = { x: -V.y, y: V.x }, j = { x: -X.y, y: X.x }, E = (Y.x + j.x) / 2, w = (Y.y + j.y) / 2, L = Math.sqrt(E * E + w * w);
    if (L > 0.001) {
      E /= L, w /= L;
      let A = Y.x * j.x + Y.y * j.y, D = A > -0.999 ? 1 / Math.sqrt((1 + A) / 2) : 1, F = Math.min(D, 3);
      U.push({ x: _.x + E * J * F, y: _.y + w * J * F });
    } else
      U.push(_);
  }
  return U;
}
G6();
var MK = new WeakMap;
var f$ = null;
function a0() {
  if (!f$)
    f$ = new Q9;
  return f$;
}
var o9 = null;
var TK = 0;
function c4($) {
  if ($ <= TK && o9)
    return o9.fill(0, 0, $), o9;
  let Q = Math.max($, 4096);
  return o9 = new Uint8Array(Q), TK = Q, o9;
}
function PK($, Q, Z) {
  let K = Z === 4 ? 4 : Z === 2 || Z === 3 ? 3 : Z === 0 ? 0.125 : 1, q = Z === 0 ? Math.ceil($ / 8) : Math.ceil($ * K), J = q * Q, U = c4(J);
  return { width: $, rows: Q, pitch: q, buffer: U.subarray(0, J), pixelMode: Z, numGrays: Z === 0 ? 2 : 256 };
}
var OK = new WeakMap;
function IK($) {
  if ($ & A6.UnscaledComponentOffset)
    return false;
  if ($ & A6.ScaledComponentOffset)
    return true;
  return false;
}
function r0($) {
  return Math.round($ / 64) * 64;
}
function l$($, Q, Z, K = 0, q) {
  if (K > 32)
    return null;
  let J = $.getGlyph(Q);
  if (!J || J.type === "empty")
    return null;
  let U = q?.roundCompositeOffsets ?? true;
  if (J.type === "simple") {
    let z = [], G = [], Y = [], j = [], E = 0;
    for (let w of J.contours) {
      for (let L of w)
        z.push(Math.round(L.x * Z)), G.push(Math.round(L.y * Z)), Y.push(L.onCurve ? 1 : 0), E++;
      j.push(E - 1);
    }
    return { xCoords: z, yCoords: G, flags: new Uint8Array(Y), contourEnds: j };
  }
  let W = [], H = [], _ = [], N = [], V = [], X = 0;
  for (let z of J.components) {
    let G = l$($, z.glyphId, Z, K + 1, { roundCompositeOffsets: U });
    if (!G || G.xCoords.length === 0)
      continue;
    let [Y, j, E, w] = z.transform, L = (z.flags & A6.ArgsAreXYValues) !== 0, A = Array(G.xCoords.length), D = Array(G.yCoords.length);
    for (let R = 0;R < G.xCoords.length; R++) {
      let P = G.xCoords[R], T = G.yCoords[R];
      A[R] = Math.round(Y * P + E * T), D[R] = Math.round(j * P + w * T);
    }
    let F = 0, M = 0;
    if (L) {
      let { arg1: R, arg2: P } = z;
      if (IK(z.flags)) {
        let T = Math.round(R * Z), m = Math.round(P * Z);
        F = Math.round(Y * T + E * m), M = Math.round(j * T + w * m);
      } else
        F = Math.round(R * Z), M = Math.round(P * Z);
      if (U && z.flags & A6.RoundXYToGrid)
        F = r0(F), M = r0(M);
    } else {
      let { arg1: R, arg2: P } = z;
      if (R >= 0 && R < V.length && P >= 0 && P < A.length) {
        let T = V[R];
        F = T.x - A[P], M = T.y - D[P];
      }
    }
    for (let R = 0;R < A.length; R++) {
      let P = A[R] + F, T = D[R] + M;
      W.push(P), H.push(T), _.push(G.flags[R] ?? 0), V.push({ x: P, y: T });
    }
    for (let R = 0;R < G.contourEnds.length; R++)
      N.push(X + G.contourEnds[R]);
    X += A.length;
  }
  if (W.length === 0)
    return null;
  return { xCoords: W, yCoords: H, flags: new Uint8Array(_), contourEnds: N };
}
function o4($, Q, Z, K, q) {
  if (Z.type !== "composite")
    return null;
  if (q > 16)
    return null;
  let J = [], U = [], W = [], H = [], _ = [], N = 0;
  for (let V = 0;V < Z.components.length; V++) {
    let X = Z.components[V], z = vK($, Q, X.glyphId, K, q + 1);
    if (!z || z.xCoords.length === 0)
      continue;
    let [G, Y, j, E] = X.transform, w = (X.flags & A6.ArgsAreXYValues) !== 0, L = 0, A = 0;
    if (w) {
      let { arg1: D, arg2: F } = X;
      if (IK(X.flags)) {
        let M = G * D + j * F, R = Y * D + E * F;
        D = M, F = R;
      }
      if (L = h6(D, $.ctx.scaleFix), A = h6(F, $.ctx.scaleFix), X.flags & A6.RoundXYToGrid)
        L = r0(L), A = r0(A);
    } else {
      let { arg1: D, arg2: F } = X;
      if (D >= 0 && D < _.length && F >= 0 && F < z.xCoords.length) {
        let M = _[D], R = G * z.xCoords[F] + j * z.yCoords[F], P = Y * z.xCoords[F] + E * z.yCoords[F];
        L = Math.round(M.x - R), A = Math.round(M.y - P);
      }
    }
    for (let D = 0;D < z.xCoords.length; D++) {
      let F = z.xCoords[D], M = z.yCoords[D], R = Math.round(G * F + j * M + L), P = Math.round(Y * F + E * M + A);
      J.push(R), U.push(P), W.push(z.flags[D] ?? 0), _.push({ x: R, y: P });
    }
    for (let D = 0;D < z.contourEnds.length; D++)
      H.push(N + z.contourEnds[D]);
    N += z.xCoords.length;
  }
  if (J.length === 0)
    return null;
  return { xCoords: J, yCoords: U, flags: new Uint8Array(W), contourEnds: H, error: null };
}
function vK($, Q, Z, K, q, J = 0) {
  let U = Math.round(q * 64), W = `${Z}:${K}:${U}:${$.ctx.lightMode ? "light" : "full"}`, H = OK.get(Q);
  if (!H)
    H = new Map, OK.set(Q, H);
  let _ = H.get(W);
  if (_ !== undefined)
    return _;
  let N = Q.getGlyph(Z);
  if (!N || N.type === "empty")
    return H.set(W, null), null;
  if (y0($, K, q))
    return H.set(W, null), null;
  if (N.type === "composite" && N.instructions.length === 0) {
    let G = o4($, Q, N, K, J);
    if (G && G.xCoords.length > 0)
      return H.set(W, G), G;
  }
  let X = s4(Q, Z, $.ctx.scale);
  if (!X)
    return H.set(W, null), null;
  let z = b0($, X);
  if (z.error || z.xCoords.length === 0)
    return H.set(W, null), null;
  return H.set(W, z), z;
}
function i4($) {
  if (!$.isTrueType || !$.hasHinting)
    return null;
  let Q = MK.get($);
  if (Q)
    return Q;
  let Z = $.cvtTable, K = Z ? new Int32Array(Z.values) : undefined, q = $.maxp;
  Q = C0($.unitsPerEm, "maxStackElements" in q ? q.maxStackElements : 256, "maxStorage" in q ? q.maxStorage : 64, "maxFunctionDefs" in q ? q.maxFunctionDefs : 64, "maxTwilightPoints" in q ? q.maxTwilightPoints : 16, K);
  let J = $.fpgm;
  if (J)
    S0(Q, J.instructions);
  let U = $.prep;
  if (U)
    h0(Q, U.instructions);
  return MK.set($, Q), Q;
}
function s4($, Q, Z) {
  let K = $.getGlyph(Q);
  if (!K || K.type === "empty")
    return null;
  let q = [], J = [], U = [], W = [], H = $.advanceWidth(Q), _ = $.leftSideBearing(Q);
  if (K.type === "composite" && K.instructions.length > 0 && Z) {
    let N = l$($, Q, Z);
    if (!N || N.xCoords.length === 0)
      return null;
    let V = 1 / Z;
    for (let X = 0;X < N.xCoords.length; X++)
      q.push(N.xCoords[X] * V), J.push(N.yCoords[X] * V), U.push(N.flags[X] ?? 0);
    for (let X = 0;X < N.contourEnds.length; X++)
      W.push(N.contourEnds[X]);
  } else {
    let N = K.type === "simple" ? K.contours : $.getGlyphContours(Q);
    if (!N || N.length === 0)
      return null;
    let V = 0;
    for (let X = 0;X < N.length; X++) {
      let z = N[X];
      for (let G = 0;G < z.length; G++) {
        let Y = z[G];
        q.push(Y.x), J.push(Y.y), U.push(Y.onCurve ? 1 : 0), V++;
      }
      W.push(V - 1);
    }
  }
  return { xCoords: q, yCoords: J, flags: new Uint8Array(U), contourEnds: W, instructions: K.instructions, lsb: _, advanceWidth: H, isComposite: K.type === "composite" };
}
function kK($, Q, Z, K) {
  let { xCoords: q, yCoords: J, flags: U, contourEnds: W } = Q, H = 0, _ = 0;
  for (let N = 0;N < q.length; N++) {
    let V = W[H], X = N === V, z = (q[N] << 2 | 0) + (Z << 8), G = (-J[N] << 2 | 0) + (K << 8), Y = (U[N] & 1) !== 0;
    if (N === _)
      $.moveTo(z, G);
    else if (Y)
      $.lineTo(z, G);
    else {
      let j = X ? _ : N + 1, E = (q[j] << 2 | 0) + (Z << 8), w = (-J[j] << 2 | 0) + (K << 8);
      if ((U[j] & 1) !== 0) {
        if ($.conicTo(z, G, E, w), !X) {
          if (N++, N === V) {
            let A = (q[_] << 2 | 0) + (Z << 8), D = (-J[_] << 2 | 0) + (K << 8);
            $.lineTo(A, D), H++, _ = N + 1;
          }
        }
      } else
        $.conicTo(z, G, z + E >> 1, G + w >> 1);
    }
    if (X && N === V) {
      let j = (q[_] << 2 | 0) + (Z << 8), E = (-J[_] << 2 | 0) + (K << 8);
      if (Y && N !== _)
        $.lineTo(j, E);
      else if (!Y) {
        let w = (U[_] & 1) !== 0;
      }
      H++, _ = N + 1;
    }
  }
}
var r4 = 256;
function $8($, Q) {
  let { width: Z, height: K, scale: q, offsetX: J = 0, offsetY: U = 0, pixelMode: W = 1, fillRule: H = 0, flipY: _ = true } = Q, N = r(Z, K, W), V = a0();
  if (V.setClip(0, 0, Z, K), K > r4) {
    let X = () => F8(V, $, q, J, U, _);
    V.renderWithBands(N, X, { minY: 0, maxY: K }, H);
  } else
    V.setBandBounds(0, K), V.reset(), F8(V, $, q, J, U, _), V.sweep(N, H);
  return N;
}
function i9($, Q, Z, K) {
  let q = K?.padding ?? 0, J = K?.pixelMode ?? 1, U = K?.hinting ?? false, W = K?.sizeMode, H = h$($, Z, W);
  if (U && $.hasHinting) {
    let w = a4($, Q, H, q, J, W === "height" ? Z : H);
    if (w)
      return w;
  }
  if ($.isTrueType) {
    let E = H * 64 / $.unitsPerEm, w = l$($, Q, E, 0, { roundCompositeOffsets: false });
    if (w) {
      let L = t4(w, q, J);
      if (L)
        return L;
    }
  }
  let _ = y6($, Q);
  if (!_)
    return null;
  let N = H / $.unitsPerEm, V = _8(_, N, true, true);
  if (!V)
    return { bitmap: r(1, 1, J), bearingX: 0, bearingY: 0 };
  let X = V.maxX - V.minX + q * 2, z = V.maxY - V.minY + q * 2;
  if (X <= 0 || z <= 0)
    return { bitmap: r(1, 1, J), bearingX: 0, bearingY: 0 };
  let G = -V.minX + q, Y = -V.minY + q;
  return { bitmap: $8(_, { width: X, height: z, scale: N, offsetX: G, offsetY: Y, pixelMode: J, flipY: true }), bearingX: V.minX - q, bearingY: -(V.minY - q) };
}
function CK($, Q, Z, K, q) {
  let J = i9($, Q, Z, q);
  if (!J)
    return null;
  let U = { bearingX: J.bearingX, bearingY: J.bearingY, offsetX26: q?.offsetX26, offsetY26: q?.offsetY26 };
  if (Array.isArray(K[0]))
    return D8(J.bitmap, K, U);
  return e6(J.bitmap, K, U);
}
function a4($, Q, Z, K, q, J = Z) {
  let U = i4($);
  if (!U)
    return null;
  let W = Math.round(Z);
  U.ctx.lightMode = q === 1, U.ctx.renderMode = q === 0 ? "mono" : q === 2 ? "lcd" : q === 3 ? "lcd_v" : "gray", U.ctx.grayscale = U.ctx.renderMode !== "mono" && !U.ctx.lightMode;
  let H = vK(U, $, Q, W, J);
  if (!H)
    return null;
  let _ = H, N = _.xCoords, V = _.yCoords, X = N[0], z = V[0], G = N[0], Y = V[0];
  for (let f = 1;f < N.length; f++) {
    let l = N[f], k = V[f];
    if (l < X)
      X = l;
    if (l > G)
      G = l;
    if (k < z)
      z = k;
    if (k > Y)
      Y = k;
  }
  if (!Number.isFinite(X))
    return { bitmap: r(1, 1, q), bearingX: 0, bearingY: 0 };
  let j = $.getGlyphBounds(Q);
  if (j) {
    let f = Z / $.unitsPerEm, l = (j.xMax - j.xMin) * f, k = (j.yMax - j.yMin) * f, O = Math.max(l * 8, Z * 8, l + 64), h = Math.max(k * 8, Z * 8, k + 64), S = (G - X) / 64, d = (Y - z) / 64;
    if (S > O || d > h)
      return null;
  }
  let E = Math.floor(X / 64), w = Math.floor(z / 64), L = Math.floor((G + 63) / 64), A = Math.floor((Y + 63) / 64), D = L - E + K * 2, F = A - w + K * 2;
  if (D <= 0 || F <= 0)
    return { bitmap: r(1, 1, q), bearingX: 0, bearingY: 0 };
  let M = PK(D, F, q), R = a0();
  R.setClip(0, 0, D, F), R.setBandBounds(0, F), R.reset();
  let P = -E + K, T = A + K, m = () => kK(R, _, P, T);
  try {
    m(), R.sweep(M, 0);
  } catch (f) {
    if (f instanceof g8)
      R.reset(), R.renderWithBands(M, m, { minY: 0, maxY: F, minX: 0, maxX: D }, 0);
    else
      throw f;
  }
  let v = r(D, F, q);
  return v.buffer.set(M.buffer), { bitmap: v, bearingX: E - K, bearingY: A + K };
}
function t4($, Q, Z) {
  let { xCoords: K, yCoords: q } = $;
  if (K.length === 0)
    return null;
  let J = K[0], U = q[0], W = K[0], H = q[0];
  for (let F = 1;F < K.length; F++) {
    let M = K[F], R = q[F];
    if (M < J)
      J = M;
    if (M > W)
      W = M;
    if (R < U)
      U = R;
    if (R > H)
      H = R;
  }
  if (!Number.isFinite(J))
    return { bitmap: r(1, 1, Z), bearingX: 0, bearingY: 0 };
  let _ = Math.floor(J / 64), N = Math.floor(U / 64), V = Math.floor((W + 63) / 64), X = Math.floor((H + 63) / 64), z = V - _ + Q * 2, G = X - N + Q * 2;
  if (z <= 0 || G <= 0)
    return { bitmap: r(1, 1, Z), bearingX: 0, bearingY: 0 };
  let Y = PK(z, G, Z), j = a0();
  j.setClip(0, 0, z, G), j.setBandBounds(0, G), j.reset();
  let E = -_ + Q, w = X + Q, L = { xCoords: $.xCoords, yCoords: $.yCoords, flags: $.flags, contourEnds: $.contourEnds, error: null }, A = () => kK(j, L, E, w);
  try {
    A(), j.sweep(Y, 0);
  } catch (F) {
    if (F instanceof g8)
      j.reset(), j.renderWithBands(Y, A, { minY: 0, maxY: G, minX: 0, maxX: z }, 0);
    else
      throw F;
  }
  let D = r(z, G, Z);
  return D.buffer.set(Y.buffer), { bitmap: D, bearingX: _ - Q, bearingY: X + Q };
}
function SK($, Q, Z, K) {
  let q = U9($, Z, K?.sizeMode), J = K?.padding ?? 0, U = K?.pixelMode ?? 1, W = [], H = 0, _ = 0, N = 0, V = [...Q];
  for (let w = 0;w < V.length; w++) {
    let A = V[w].codePointAt(0);
    if (A === undefined)
      continue;
    let D = $.glyphId(A);
    if (D === undefined)
      continue;
    let F = $.advanceWidth(D) * q, M = y6($, D);
    if (M?.bounds)
      _ = Math.max(_, -M.bounds.yMin * q), N = Math.max(N, M.bounds.yMax * q);
    W.push({ glyphId: D, advance: F }), H += F;
  }
  if (W.length === 0)
    return null;
  let X = Math.ceil(H) + J * 2, z = Math.ceil(_ + N) + J * 2, G = r(X, z, U), Y = a0();
  Y.setClip(0, 0, X, z), Y.setBandBounds(0, z);
  let j = J, E = N + J;
  for (let w = 0;w < W.length; w++) {
    let L = W[w], A = y6($, L.glyphId);
    if (A)
      Y.reset(), F8(Y, A, q, j, E, true), Y.sweep(G);
    j += L.advance;
  }
  return G;
}
function s9($) {
  let Q = $.pixelMode === 2, Z = $.pixelMode === 3, K = new Uint8Array($.width * $.rows * 4), q = $.pitch, J = Math.abs(q), U = q < 0 ? ($.rows - 1) * J : 0;
  for (let W = 0;W < $.rows; W++) {
    let H = U + W * q;
    for (let _ = 0;_ < $.width; _++) {
      let N = (W * $.width + _) * 4;
      if ($.pixelMode === 1) {
        let V = H + _, X = $.buffer[V] ?? 0;
        K[N] = 255 - X, K[N + 1] = 255 - X, K[N + 2] = 255 - X, K[N + 3] = 255;
      } else if ($.pixelMode === 0) {
        let V = H + (_ >> 3), X = 7 - (_ & 7), z = ($.buffer[V] ?? 0) >> X & 1 ? 255 : 0;
        K[N] = 255 - z, K[N + 1] = 255 - z, K[N + 2] = 255 - z, K[N + 3] = 255;
      } else if (Q || Z) {
        let V = H + _ * 3, X = $.buffer[V] ?? 0, z = $.buffer[V + 1] ?? 0, G = $.buffer[V + 2] ?? 0;
        K[N] = 255 - X, K[N + 1] = 255 - z, K[N + 2] = 255 - G, K[N + 3] = 255;
      } else if ($.pixelMode === 4) {
        let V = H + _ * 4;
        K[N] = $.buffer[V] ?? 0, K[N + 1] = $.buffer[V + 1] ?? 0, K[N + 2] = $.buffer[V + 2] ?? 0, K[N + 3] = $.buffer[V + 3] ?? 0;
      } else {
        let V = H + _, X = $.buffer[V] ?? 0;
        K[N] = 255 - X, K[N + 1] = 255 - X, K[N + 2] = 255 - X, K[N + 3] = 255;
      }
    }
  }
  return K;
}
function r9($) {
  if ($.pixelMode === 1 && $.pitch === $.width)
    return $.buffer;
  let Q = new Uint8Array($.width * $.rows), Z = $.pitch, K = Math.abs(Z), q = Z < 0 ? ($.rows - 1) * K : 0;
  for (let J = 0;J < $.rows; J++) {
    let U = q + J * Z;
    for (let W = 0;W < $.width; W++) {
      let H = J * $.width + W;
      if ($.pixelMode === 1)
        Q[H] = $.buffer[U + W] ?? 0;
      else if ($.pixelMode === 0) {
        let _ = U + (W >> 3), N = 7 - (W & 7);
        Q[H] = ($.buffer[_] ?? 0) >> N & 1 ? 255 : 0;
      } else if ($.pixelMode === 2 || $.pixelMode === 3) {
        let _ = U + W * 3, N = $.buffer[_] ?? 0, V = $.buffer[_ + 1] ?? 0, X = $.buffer[_ + 2] ?? 0;
        Q[H] = Math.round((N + V + X) / 3);
      } else if ($.pixelMode === 4) {
        let _ = U + W * 4;
        Q[H] = $.buffer[_ + 3] ?? 0;
      }
    }
  }
  return Q;
}
G6();
function t0($, Q, Z) {
  let { fontSize: K, sizeMode: q, padding: J = 1, maxWidth: U = 2048, maxHeight: W = 2048, pixelMode: H = 1 } = Z, _ = [], N = U9($, K, q);
  for (let j = 0;j < Q.length; j++) {
    let E = Q[j], w = i9($, E, K, { padding: 0, pixelMode: H, sizeMode: q });
    if (!w)
      continue;
    let L = $.advanceWidth(E) * N;
    _.push({ glyphId: E, bitmap: w.bitmap, bearingX: w.bearingX, bearingY: w.bearingY, advance: L });
  }
  _.sort((j, E) => E.bitmap.rows - j.bitmap.rows);
  let { width: V, height: X, placements: z } = e4(_.map((j) => ({ width: j.bitmap.width + J * 2, height: j.bitmap.rows + J * 2 })), U, W), G = r(V, X, H), Y = new Map;
  for (let j = 0;j < _.length; j++) {
    let E = _[j], w = z[j];
    if (!w.placed)
      continue;
    $_(E.bitmap, G, w.x + J, w.y + J), Y.set(E.glyphId, { glyphId: E.glyphId, atlasX: w.x + J, atlasY: w.y + J, width: E.bitmap.width, height: E.bitmap.rows, bearingX: E.bearingX, bearingY: E.bearingY, advance: E.advance });
  }
  return { bitmap: G, glyphs: Y, fontSize: K };
}
function yK($, Q) {
  let Z = [];
  for (let K = 32;K <= 126; K++) {
    let q = $.glyphId(K);
    if (q !== undefined && q !== 0)
      Z.push(q);
  }
  return t0($, Z, Q);
}
function bK($, Q, Z) {
  let K = new Set, q = [...Q];
  for (let J = 0;J < q.length; J++) {
    let W = q[J].codePointAt(0);
    if (W === undefined)
      continue;
    let H = $.glyphId(W);
    if (H !== undefined && H !== 0)
      K.add(H);
  }
  return t0($, [...K], Z);
}
function e4($, Q, Z) {
  let K = [], q = [], J = 0, U = 0;
  for (let _ = 0;_ < $.length; _++) {
    let N = $[_], V = false, X = -1, z = Z;
    for (let G = 0;G < K.length; G++) {
      let Y = K[G];
      if (Y.width + N.width <= Q && N.height <= Y.height) {
        if (Y.y < z)
          X = G, z = Y.y;
      }
    }
    if (X >= 0) {
      let G = K[X];
      q.push({ x: G.width, y: G.y, placed: true }), G.width += N.width, J = Math.max(J, G.width), V = true;
    } else {
      let G = U;
      if (G + N.height <= Z && N.width <= Q)
        K.push({ y: G, height: N.height, width: N.width }), q.push({ x: 0, y: G, placed: true }), U = G + N.height, J = Math.max(J, N.width), V = true;
    }
    if (!V)
      q.push({ x: 0, y: 0, placed: false });
  }
  let W = hK(J), H = hK(U);
  return { width: Math.min(W, Q), height: Math.min(H, Z), placements: q };
}
function $_($, Q, Z, K) {
  let q = $.pixelMode === 2 ? 3 : 1;
  for (let J = 0;J < $.rows; J++) {
    let U = J * $.pitch, W = (K + J) * Q.pitch + Z * q;
    for (let H = 0;H < $.width * q; H++)
      Q.buffer[W + H] = $.buffer[U + H];
  }
}
function hK($) {
  if ($ <= 0)
    return 1;
  return $--, $ |= $ >> 1, $ |= $ >> 2, $ |= $ >> 4, $ |= $ >> 8, $ |= $ >> 16, $ + 1;
}
function mK($) {
  let { bitmap: Q } = $, Z = new Uint8Array(Q.width * Q.rows * 4);
  for (let K = 0;K < Q.rows; K++)
    for (let q = 0;q < Q.width; q++) {
      let J = K * Q.pitch + q, U = (K * Q.width + q) * 4, W = Q.buffer[J] ?? 0;
      Z[U] = 255, Z[U + 1] = 255, Z[U + 2] = 255, Z[U + 3] = W;
    }
  return Z;
}
function gK($) {
  let { bitmap: Q } = $;
  if (Q.pitch === Q.width)
    return Q.buffer;
  let Z = new Uint8Array(Q.width * Q.rows);
  for (let K = 0;K < Q.rows; K++)
    for (let q = 0;q < Q.width; q++)
      Z[K * Q.width + q] = Q.buffer[K * Q.pitch + q];
  return Z;
}
function uK($, Q) {
  let Z = $.glyphs.get(Q);
  if (!Z)
    return null;
  let { bitmap: K } = $;
  return { u0: Z.atlasX / K.width, v0: Z.atlasY / K.rows, u1: (Z.atlasX + Z.width) / K.width, v1: (Z.atlasY + Z.height) / K.rows };
}
G6();
function Q_($, Q, Z) {
  return Math.max(Math.min($, Q), Math.min(Math.max($, Q), Z));
}
function Z_($, Q, Z, K) {
  let q = K.x - Z.x, J = K.y - Z.y, U = q * q + J * J;
  if (U < 0.0000000001)
    return { distance: Math.sqrt(($ - Z.x) ** 2 + (Q - Z.y) ** 2), t: 0 };
  let W = (($ - Z.x) * q + (Q - Z.y) * J) / U;
  W = Math.max(0, Math.min(1, W));
  let H = Math.sqrt(($ - Z.x - W * q) ** 2 + (Q - Z.y - W * J) ** 2);
  return { distance: (q * (Q - Z.y) - J * ($ - Z.x) >= 0 ? 1 : -1) * H, t: W };
}
function K_($, Q, Z, K) {
  let q = K.x - Z.x, J = K.y - Z.y, U = q * q + J * J;
  if (U < 0.0000000001)
    return Math.sqrt(($ - Z.x) ** 2 + (Q - Z.y) ** 2);
  let W = (($ - Z.x) * q + (Q - Z.y) * J) / U;
  return W = Math.max(0, Math.min(1, W)), Math.sqrt(($ - Z.x - W * q) ** 2 + (Q - Z.y - W * J) ** 2);
}
function q_($, Q, Z, K, q) {
  let J = Z.x - 2 * K.x + q.x, U = Z.y - 2 * K.y + q.y, W = Math.min(Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2), Math.sqrt((q.x - $) ** 2 + (q.y - Q) ** 2)), H = W === Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2) ? 0 : 1;
  for (let j = 1;j < 6; j++) {
    let E = j / 6;
    for (let F = 0;F < 3; F++) {
      let M = 1 - E, R = M * M * Z.x + 2 * M * E * K.x + E * E * q.x, P = M * M * Z.y + 2 * M * E * K.y + E * E * q.y, T = 2 * (M * (K.x - Z.x) + E * (q.x - K.x)), m = 2 * (M * (K.y - Z.y) + E * (q.y - K.y)), v = R - $, f = P - Q, l = v * T + f * m, k = T * T + m * m + v * 2 * J + f * 2 * U;
      if (Math.abs(k) < 0.0000000001)
        break;
      E = Math.max(0, Math.min(1, E - l / k));
    }
    let w = 1 - E, L = w * w * Z.x + 2 * w * E * K.x + E * E * q.x, A = w * w * Z.y + 2 * w * E * K.y + E * E * q.y, D = Math.sqrt((L - $) ** 2 + (A - Q) ** 2);
    if (D < W)
      W = D, H = E;
  }
  let _ = 1 - H, N = 2 * (_ * (K.x - Z.x) + H * (q.x - K.x)), V = 2 * (_ * (K.y - Z.y) + H * (q.y - K.y)), X = _ * _ * Z.x + 2 * _ * H * K.x + H * H * q.x, z = _ * _ * Z.y + 2 * _ * H * K.y + H * H * q.y;
  return { distance: (N * (Q - z) - V * ($ - X) >= 0 ? 1 : -1) * W, t: H };
}
function J_($, Q, Z, K, q) {
  let J = Z.x - 2 * K.x + q.x, U = Z.y - 2 * K.y + q.y, W = Math.min(Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2), Math.sqrt((q.x - $) ** 2 + (q.y - Q) ** 2));
  for (let H = 1;H < 6; H++) {
    let _ = H / 6;
    for (let X = 0;X < 3; X++) {
      let z = 1 - _, G = z * z * Z.x + 2 * z * _ * K.x + _ * _ * q.x, Y = z * z * Z.y + 2 * z * _ * K.y + _ * _ * q.y, j = 2 * (z * (K.x - Z.x) + _ * (q.x - K.x)), E = 2 * (z * (K.y - Z.y) + _ * (q.y - K.y)), w = G - $, L = Y - Q, A = j * j + E * E + w * 2 * J + L * 2 * U;
      if (Math.abs(A) < 0.0000000001)
        break;
      _ = Math.max(0, Math.min(1, _ - (w * j + L * E) / A));
    }
    let N = 1 - _, V = Math.sqrt((N * N * Z.x + 2 * N * _ * K.x + _ * _ * q.x - $) ** 2 + (N * N * Z.y + 2 * N * _ * K.y + _ * _ * q.y - Q) ** 2);
    if (V < W)
      W = V;
  }
  return W;
}
function U_($, Q, Z, K, q, J) {
  let U = Math.min(Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2), Math.sqrt((J.x - $) ** 2 + (J.y - Q) ** 2)), W = U === Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2) ? 0 : 1;
  for (let L = 1;L < 6; L++) {
    let A = L / 6;
    for (let f = 0;f < 3; f++) {
      let l = 1 - A, k = l * l, O = k * l, h = A * A, S = h * A, d = O * Z.x + 3 * k * A * K.x + 3 * l * h * q.x + S * J.x, o = O * Z.y + 3 * k * A * K.y + 3 * l * h * q.y + S * J.y, n = 3 * k * (K.x - Z.x) + 6 * l * A * (q.x - K.x) + 3 * h * (J.x - q.x), e = 3 * k * (K.y - Z.y) + 6 * l * A * (q.y - K.y) + 3 * h * (J.y - q.y), u = 6 * l * (q.x - 2 * K.x + Z.x) + 6 * A * (J.x - 2 * q.x + K.x), N6 = 6 * l * (q.y - 2 * K.y + Z.y) + 6 * A * (J.y - 2 * q.y + K.y), y = d - $, t = o - Q, g = y * n + t * e, w6 = n * n + e * e + y * u + t * N6;
      if (Math.abs(w6) < 0.0000000001)
        break;
      A = Math.max(0, Math.min(1, A - g / w6));
    }
    let D = 1 - A, F = D * D, M = F * D, R = A * A, P = R * A, T = M * Z.x + 3 * F * A * K.x + 3 * D * R * q.x + P * J.x, m = M * Z.y + 3 * F * A * K.y + 3 * D * R * q.y + P * J.y, v = Math.sqrt((T - $) ** 2 + (m - Q) ** 2);
    if (v < U)
      U = v, W = A;
  }
  let H = 1 - W, _ = H * H, N = W * W, V = 3 * _ * (K.x - Z.x) + 6 * H * W * (q.x - K.x) + 3 * N * (J.x - q.x), X = 3 * _ * (K.y - Z.y) + 6 * H * W * (q.y - K.y) + 3 * N * (J.y - q.y), z = _ * H, G = N * W, Y = z * Z.x + 3 * _ * W * K.x + 3 * H * N * q.x + G * J.x, j = z * Z.y + 3 * _ * W * K.y + 3 * H * N * q.y + G * J.y;
  return { distance: (V * (Q - j) - X * ($ - Y) >= 0 ? 1 : -1) * U, t: W };
}
function W_($, Q, Z, K, q, J) {
  let U = Math.min(Math.sqrt((Z.x - $) ** 2 + (Z.y - Q) ** 2), Math.sqrt((J.x - $) ** 2 + (J.y - Q) ** 2));
  for (let W = 1;W < 6; W++) {
    let H = W / 6;
    for (let Y = 0;Y < 3; Y++) {
      let j = 1 - H, E = j * j, w = E * j, L = H * H, A = L * H, D = w * Z.x + 3 * E * H * K.x + 3 * j * L * q.x + A * J.x, F = w * Z.y + 3 * E * H * K.y + 3 * j * L * q.y + A * J.y, M = 3 * E * (K.x - Z.x) + 6 * j * H * (q.x - K.x) + 3 * L * (J.x - q.x), R = 3 * E * (K.y - Z.y) + 6 * j * H * (q.y - K.y) + 3 * L * (J.y - q.y), P = 6 * j * (q.x - 2 * K.x + Z.x) + 6 * H * (J.x - 2 * q.x + K.x), T = 6 * j * (q.y - 2 * K.y + Z.y) + 6 * H * (J.y - 2 * q.y + K.y), m = D - $, v = F - Q, f = M * M + R * R + m * P + v * T;
      if (Math.abs(f) < 0.0000000001)
        break;
      H = Math.max(0, Math.min(1, H - (m * M + v * R) / f));
    }
    let _ = 1 - H, N = _ * _, V = N * _, X = H * H, z = X * H, G = Math.sqrt((V * Z.x + 3 * N * H * K.x + 3 * _ * X * q.x + z * J.x - $) ** 2 + (V * Z.y + 3 * N * H * K.y + 3 * _ * X * q.y + z * J.y - Q) ** 2);
    if (G < U)
      U = G;
  }
  return U;
}
function xK($) {
  switch ($.type) {
    case "line":
      return { x: $.p1.x - $.p0.x, y: $.p1.y - $.p0.y };
    case "quadratic":
      return { x: 2 * ($.p1.x - $.p0.x), y: 2 * ($.p1.y - $.p0.y) };
    case "cubic":
      return { x: 3 * ($.p1.x - $.p0.x), y: 3 * ($.p1.y - $.p0.y) };
  }
}
function fK($) {
  switch ($.type) {
    case "line":
      return { x: $.p1.x - $.p0.x, y: $.p1.y - $.p0.y };
    case "quadratic":
      return { x: 2 * ($.p2.x - $.p1.x), y: 2 * ($.p2.y - $.p1.y) };
    case "cubic":
      return { x: 3 * ($.p3.x - $.p2.x), y: 3 * ($.p3.y - $.p2.y) };
  }
}
function lK($) {
  let Q = Math.sqrt($.x * $.x + $.y * $.y);
  if (Q < 0.0000000001)
    return { x: 0, y: 0 };
  return { x: $.x / Q, y: $.y / Q };
}
function H_($, Q) {
  return $.x * Q.x + $.y * Q.y;
}
function dK($, Q, Z = Math.PI / 3) {
  let K = lK($), q = lK(Q), J = H_(K, q), U = Math.acos(Math.max(-1, Math.min(1, J)));
  return U > Z && U < Math.PI - Z;
}
function pK($) {
  let Q = Math.PI / 4;
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z];
    if (K.length === 0)
      continue;
    if (K.length === 1) {
      K[0].color = 0;
      continue;
    }
    let q = 0;
    for (let J = 0;J < K.length; J++) {
      let U = K[J], W = K[(J - 1 + K.length) % K.length];
      if (J === 0)
        U.color = q;
      else {
        let H = fK(W), _ = xK(U);
        if (dK(H, _, Q))
          q = (q + 1) % 3;
        U.color = q;
      }
    }
    if (K.length >= 2) {
      let J = K[K.length - 1], U = K[0], W = fK(J), H = xK(U);
      if (dK(W, H, Q)) {
        if (J.color === U.color) {
          let _ = K[K.length - 2]?.color ?? 0, N = K[1]?.color ?? 0;
          for (let V = 0;V < 3; V++)
            if (V !== U.color && V !== _) {
              J.color = V;
              break;
            }
        }
      }
    }
  }
}
function __($, Q, Z, K, q) {
  let J = [], U = [], W = null, H = null, _ = (N, V) => ({ x: N * Q + Z, y: q ? -(V * Q) + K : V * Q + K });
  for (let N = 0;N < $.commands.length; N++) {
    let V = $.commands[N];
    switch (V.type) {
      case "M":
        if (U.length > 0)
          J.push(U), U = [];
        W = _(V.x, V.y), H = W;
        break;
      case "L":
        if (W) {
          let X = _(V.x, V.y);
          if (Math.abs(X.x - W.x) > 0.000001 || Math.abs(X.y - W.y) > 0.000001)
            U.push({ type: "line", p0: W, p1: X, color: 0, minX: Math.min(W.x, X.x), maxX: Math.max(W.x, X.x), minY: Math.min(W.y, X.y), maxY: Math.max(W.y, X.y) });
          W = X;
        }
        break;
      case "Q":
        if (W) {
          let X = _(V.x1, V.y1), z = _(V.x, V.y);
          U.push({ type: "quadratic", p0: W, p1: X, p2: z, color: 0, minX: Math.min(W.x, X.x, z.x), maxX: Math.max(W.x, X.x, z.x), minY: Math.min(W.y, X.y, z.y), maxY: Math.max(W.y, X.y, z.y) }), W = z;
        }
        break;
      case "C":
        if (W) {
          let X = _(V.x1, V.y1), z = _(V.x2, V.y2), G = _(V.x, V.y);
          U.push({ type: "cubic", p0: W, p1: X, p2: z, p3: G, color: 0, minX: Math.min(W.x, X.x, z.x, G.x), maxX: Math.max(W.x, X.x, z.x, G.x), minY: Math.min(W.y, X.y, z.y, G.y), maxY: Math.max(W.y, X.y, z.y, G.y) }), W = G;
        }
        break;
      case "Z":
        if (W && H) {
          if (Math.abs(W.x - H.x) > 0.000001 || Math.abs(W.y - H.y) > 0.000001)
            U.push({ type: "line", p0: W, p1: H, color: 0, minX: Math.min(W.x, H.x), maxX: Math.max(W.x, H.x), minY: Math.min(W.y, H.y), maxY: Math.max(W.y, H.y) });
          W = H;
        }
        if (U.length > 0)
          J.push(U), U = [];
        break;
    }
  }
  if (U.length > 0)
    J.push(U);
  return J;
}
function N_($, Q, Z) {
  switch (Z.type) {
    case "line":
      return K_($, Q, Z.p0, Z.p1);
    case "quadratic":
      return J_($, Q, Z.p0, Z.p1, Z.p2);
    case "cubic":
      return W_($, Q, Z.p0, Z.p1, Z.p2, Z.p3);
  }
}
function V_($) {
  let Q = [];
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z], q = [];
    for (let J = 0;J < K.length; J++) {
      let U = K[J];
      if (U.type === "line")
        q.push([U.p0, U.p1]);
      else if (U.type === "quadratic") {
        let W = U.p0;
        for (let H = 1;H <= 4; H++) {
          let _ = H / 4, N = 1 - _, V = { x: N * N * U.p0.x + 2 * N * _ * U.p1.x + _ * _ * U.p2.x, y: N * N * U.p0.y + 2 * N * _ * U.p1.y + _ * _ * U.p2.y };
          q.push([W, V]), W = V;
        }
      } else {
        let W = U.p0;
        for (let H = 1;H <= 4; H++) {
          let _ = H / 4, N = 1 - _, V = N * N, X = V * N, z = _ * _, G = z * _, Y = { x: X * U.p0.x + 3 * V * _ * U.p1.x + 3 * N * z * U.p2.x + G * U.p3.x, y: X * U.p0.y + 3 * V * _ * U.p1.y + 3 * N * z * U.p2.y + G * U.p3.y };
          q.push([W, Y]), W = Y;
        }
      }
    }
    Q.push(q);
  }
  return Q;
}
function X_($, Q, Z) {
  let K = 0;
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q];
    for (let U = 0;U < J.length; U++) {
      let [W, H] = J[U];
      if (W.y > Q !== H.y > Q) {
        let _ = W.x + (H.x - W.x) * (Q - W.y) / (H.y - W.y);
        if ($ < _)
          K++;
      }
    }
  }
  return (K & 1) === 1;
}
function d$($, Q, Z) {
  let K = 1 / 0;
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q], U = $ < J.minX ? J.minX - $ : $ > J.maxX ? $ - J.maxX : 0, W = Q < J.minY ? J.minY - Q : Q > J.maxY ? Q - J.maxY : 0;
    if (U * U + W * W >= K * K)
      continue;
    let H = N_($, Q, J);
    if (H < K) {
      if (K = H, H < 0.5)
        break;
    }
  }
  return K;
}
function l8($, Q) {
  let { width: Z, height: K, scale: q, offsetX: J = 0, offsetY: U = 0, flipY: W = false, spread: H = 8 } = Q, _ = r(Z, K, 2), N = __($, q, J, U, W);
  if (N.length === 0 || N.every((L) => L.length === 0))
    return _.buffer.fill(0), _;
  pK(N);
  let V = V_(N), X = N.flat(), z = X.filter((L) => L.color === 0), G = X.filter((L) => L.color === 1), Y = X.filter((L) => L.color === 2), j = z.length > 0 ? z : X, E = G.length > 0 ? G : X, w = Y.length > 0 ? Y : X;
  for (let L = 0;L < K; L++)
    for (let A = 0;A < Z; A++) {
      let D = A + 0.5, F = L + 0.5, M = d$(D, F, j), R = d$(D, F, E), P = d$(D, F, w), m = X_(D, F, V) ? 1 : -1, v = M * m, f = R * m, l = P * m, k = (h) => {
        let S = 128 + h / H * 127;
        return Math.max(0, Math.min(255, Math.round(S)));
      }, O = L * _.pitch + A * 3;
      _.buffer[O] = k(v), _.buffer[O + 1] = k(f), _.buffer[O + 2] = k(l);
    }
  return _;
}
function z_($, Q, Z) {
  let K = [], q = [], J = 0, U = 0;
  for (let _ = 0;_ < $.length; _++) {
    let N = $[_], V = false, X = -1, z = Z;
    for (let G = 0;G < K.length; G++) {
      let Y = K[G];
      if (Y.width + N.width <= Q && N.height <= Y.height) {
        if (Y.y < z)
          X = G, z = Y.y;
      }
    }
    if (X >= 0) {
      let G = K[X];
      q.push({ x: G.width, y: G.y, placed: true }), G.width += N.width, J = Math.max(J, G.width), V = true;
    } else {
      let G = U;
      if (G + N.height <= Z && N.width <= Q)
        K.push({ y: G, height: N.height, width: N.width }), q.push({ x: 0, y: G, placed: true }), U = G + N.height, J = Math.max(J, N.width), V = true;
    }
    if (!V)
      q.push({ x: 0, y: 0, placed: false });
  }
  let W = nK(J), H = nK(U);
  return { width: Math.min(W, Q), height: Math.min(H, Z), placements: q };
}
function nK($) {
  if ($ <= 0)
    return 1;
  return $--, $ |= $ >> 1, $ |= $ >> 2, $ |= $ >> 4, $ |= $ >> 8, $ |= $ >> 16, $ + 1;
}
function G_($, Q, Z, K) {
  for (let q = 0;q < $.rows; q++) {
    let J = q * $.pitch, U = (K + q) * Q.pitch + Z * 3;
    for (let W = 0;W < $.width * 3; W++)
      Q.buffer[U + W] = $.buffer[J + W];
  }
}
function e0($, Q, Z) {
  let { fontSize: K, sizeMode: q, padding: J = 2, maxWidth: U = 2048, maxHeight: W = 2048, spread: H = 4 } = Z, _ = U9($, K, q), N = [];
  for (let j = 0;j < Q.length; j++) {
    let E = Q[j], w = y6($, E);
    if (!w || !w.bounds)
      continue;
    let L = w.bounds, A = Math.ceil((L.xMax - L.xMin) * _) + H * 2, D = Math.ceil((L.yMax - L.yMin) * _) + H * 2;
    if (A <= 0 || D <= 0)
      continue;
    let F = l8(w, { width: A, height: D, scale: _, offsetX: -L.xMin * _ + H, offsetY: L.yMax * _ + H, flipY: true, spread: H }), M = $.advanceWidth(E) * _, R = L.xMin * _ - H, P = L.yMax * _ + H;
    N.push({ glyphId: E, bitmap: F, bearingX: R, bearingY: P, advance: M });
  }
  N.sort((j, E) => E.bitmap.rows - j.bitmap.rows);
  let { width: V, height: X, placements: z } = z_(N.map((j) => ({ width: j.bitmap.width + J * 2, height: j.bitmap.rows + J * 2 })), U, W), G = r(V, X, 2), Y = new Map;
  for (let j = 0;j < N.length; j++) {
    let E = N[j], w = z[j];
    if (!w.placed)
      continue;
    G_(E.bitmap, G, w.x + J, w.y + J), Y.set(E.glyphId, { glyphId: E.glyphId, atlasX: w.x + J, atlasY: w.y + J, width: E.bitmap.width, height: E.bitmap.rows, bearingX: E.bearingX, bearingY: E.bearingY, advance: E.advance });
  }
  return { bitmap: G, glyphs: Y, fontSize: K };
}
function cK($, Q) {
  let Z = [];
  for (let K = 32;K <= 126; K++) {
    let q = $.glyphId(K);
    if (q !== undefined && q !== 0)
      Z.push(q);
  }
  return e0($, Z, Q);
}
function oK($, Q, Z) {
  let K = new Set, q = Array.from(Q);
  for (let J = 0;J < q.length; J++) {
    let W = q[J].codePointAt(0);
    if (W === undefined)
      continue;
    let H = $.glyphId(W);
    if (H !== undefined && H !== 0)
      K.add(H);
  }
  return e0($, [...K], Z);
}
function iK($) {
  let { bitmap: Q } = $;
  if (Q.pitch === Q.width * 3)
    return Q.buffer;
  let Z = new Uint8Array(Q.width * Q.rows * 3);
  for (let K = 0;K < Q.rows; K++)
    for (let q = 0;q < Q.width * 3; q++)
      Z[K * Q.width * 3 + q] = Q.buffer[K * Q.pitch + q];
  return Z;
}
function sK($) {
  let { bitmap: Q } = $, Z = new Uint8Array(Q.width * Q.rows * 4);
  for (let K = 0;K < Q.rows; K++)
    for (let q = 0;q < Q.width; q++) {
      let J = K * Q.pitch + q * 3, U = (K * Q.width + q) * 4;
      Z[U] = Q.buffer[J], Z[U + 1] = Q.buffer[J + 1], Z[U + 2] = Q.buffer[J + 2], Z[U + 3] = 255;
    }
  return Z;
}
G6();
var Y_ = Math.PI;
function j_($, Q, Z) {
  let K = 0.5 / Z, q = Math.exp(-K), J = q * q, U = Math.sqrt(K / Y_);
  $[0] = U, U *= q, $[1] = U;
  for (let W = 2;W < Q; W++)
    q *= J, U *= q, $[W] = U;
}
function rK($, Q, Z) {
  let K = $[1], q = $[2], J = $[3];
  for (let U = 0;U < Q; U++) {
    let W = $[U] * Z[0] + (K + $[U + 1]) * Z[1] + (q + $[U + 2]) * Z[2] + (J + $[U + 3]) * Z[3];
    J = q, q = K, K = $[U], $[U] = W;
  }
}
function E_($, Q, Z) {
  for (let K = 0;K < Z; K++) {
    $[K][K] = Q[2 * K + 2] + 3 * Q[0] - 4 * Q[K + 1];
    for (let q = K + 1;q < Z; q++)
      $[K][q] = $[q][K] = Q[K + q + 2] + Q[q - K] + 2 * (Q[0] - Q[K + 1] - Q[q + 1]);
  }
  for (let K = 0;K < Z; K++) {
    let q = 1 / $[K][K];
    $[K][K] = 1;
    for (let J = 0;J < Z; J++) {
      if (J === K)
        continue;
      let U = $[J][K] * q;
      $[J][K] = 0;
      for (let W = 0;W < Z; W++)
        $[J][W] -= $[K][W] * U;
    }
    for (let J = 0;J < Z; J++)
      $[K][J] *= q;
  }
}
function w_($, Q, Z, K) {
  let J = [((0.27116402116402116 * K + 0.09027777777777778) * K + 0.20833333333333334) * K + 0.43022486772486773, ((-0.20337301587301587 * K - 0.022569444444444444) * K - 0.017361111111111112) * K + 0.24330357142857142, ((0.08134920634920635 * K - 0.04513888888888889) * K - 0.0763888888888889) * K + 0.04017857142857143, ((-0.013558201058201057 * K + 0.022569444444444444) * K - 0.010416666666666666) * K + 0.0014054232804232803], U = new Float64Array(17);
  U[0] = J[0], U[1] = J[1], U[2] = J[2], U[3] = J[3], rK(U, 7, J);
  let W = new Float64Array(12);
  j_(W, Q + 4, Z * K), rK(W, Q + 1, J);
  let H = [];
  for (let N = 0;N < 8; N++)
    H.push(new Float64Array(8));
  E_(H, U, Q);
  let _ = new Float64Array(8);
  for (let N = 0;N < Q; N++)
    _[N] = U[0] - U[N + 1] - W[0] + W[N + 1];
  for (let N = 0;N < Q; N++) {
    let V = 0;
    for (let X = 0;X < Q; X++)
      V += H[N][X] * _[X];
    $[N] = Math.max(0, V);
  }
}
function aK($) {
  let Q = new Float64Array(8), Z, K;
  if ($ < 0.5)
    Z = 0, K = 4, Q[1] = 0.085 * $ * $ * $, Q[0] = 0.5 * $ - 4 * Q[1], Q[2] = Q[3] = 0;
  else {
    let J = Math.sqrt(0.11569 * $ + 0.20591047);
    Z = Math.floor(Math.log2(J)) + 1;
    let U = J / 2 ** (Z - 1) - 1, W = 0.25 ** Z;
    K = 8 - Math.floor((10.1525 + 0.8335 * W) * (1 - U)), K = Math.max(K, 4), K = Math.min(K, 8), w_(Q, K, $, W);
  }
  let q = new Float32Array(8);
  for (let J = 0;J < K; J++)
    q[J] = Q[J];
  return { level: Z, radius: K, coeff: q };
}
function A_($, Q, Z, K, q, J) {
  let U = Math.floor((Z + 5) / 2);
  for (let W = 0;W < K; W++) {
    let H = W * q, _ = W * J;
    for (let N = 0;N < U; N++) {
      let V = N * 2, X = 0;
      X += (Q[H + Math.max(0, V - 2)] ?? 0) * 1, X += (Q[H + Math.max(0, V - 1)] ?? 0) * 5, X += (Q[H + Math.min(Z - 1, V)] ?? 0) * 10, X += (Q[H + Math.min(Z - 1, V + 1)] ?? 0) * 10, X += (Q[H + Math.min(Z - 1, V + 2)] ?? 0) * 5, X += (Q[H + Math.min(Z - 1, V + 3)] ?? 0) * 1, $[_ + N] = X / 32;
    }
  }
}
function L_($, Q, Z, K, q, J) {
  let U = Math.floor((K + 5) / 2);
  for (let W = 0;W < Z; W++)
    for (let H = 0;H < U; H++) {
      let _ = H * 2, N = 0;
      N += (Q[Math.max(0, _ - 2) * q + W] ?? 0) * 1, N += (Q[Math.max(0, _ - 1) * q + W] ?? 0) * 5, N += (Q[Math.min(K - 1, _) * q + W] ?? 0) * 10, N += (Q[Math.min(K - 1, _ + 1) * q + W] ?? 0) * 10, N += (Q[Math.min(K - 1, _ + 2) * q + W] ?? 0) * 5, N += (Q[Math.min(K - 1, _ + 3) * q + W] ?? 0) * 1, $[H * J + W] = N / 32;
    }
}
function F_($, Q, Z, K, q, J) {
  let U = Z * 2 + 4;
  for (let W = 0;W < K; W++) {
    let H = W * q, _ = W * J;
    for (let N = 0;N < U; N++) {
      let V = Math.floor((N - 2) / 2), X = N - 2 & 1, z = 0;
      if (X === 0)
        z += (Q[H + Math.max(0, Math.min(Z - 1, V - 1))] ?? 0) * 1, z += (Q[H + Math.max(0, Math.min(Z - 1, V))] ?? 0) * 30, z += (Q[H + Math.max(0, Math.min(Z - 1, V + 1))] ?? 0) * 1;
      else
        z += (Q[H + Math.max(0, Math.min(Z - 1, V))] ?? 0) * 16, z += (Q[H + Math.max(0, Math.min(Z - 1, V + 1))] ?? 0) * 16;
      $[_ + N] = z / 32;
    }
  }
}
function D_($, Q, Z, K, q, J) {
  let U = K * 2 + 4;
  for (let W = 0;W < Z; W++)
    for (let H = 0;H < U; H++) {
      let _ = Math.floor((H - 2) / 2), N = H - 2 & 1, V = 0;
      if (N === 0)
        V += (Q[Math.max(0, Math.min(K - 1, _ - 1)) * q + W] ?? 0) * 1, V += (Q[Math.max(0, Math.min(K - 1, _)) * q + W] ?? 0) * 30, V += (Q[Math.max(0, Math.min(K - 1, _ + 1)) * q + W] ?? 0) * 1;
      else
        V += (Q[Math.max(0, Math.min(K - 1, _)) * q + W] ?? 0) * 16, V += (Q[Math.max(0, Math.min(K - 1, _ + 1)) * q + W] ?? 0) * 16;
      $[H * J + W] = V / 32;
    }
}
function B_($, Q, Z, K, q, J, U, W) {
  let H = Z + 2 * U;
  for (let _ = 0;_ < K; _++) {
    let N = _ * q, V = _ * J;
    for (let X = 0;X < H; X++) {
      let z = X - U, G = 0;
      for (let Y = 0;Y < U; Y++) {
        let j = Math.max(0, Math.min(Z - 1, z - Y - 1)), E = Math.max(0, Math.min(Z - 1, z + Y));
        G += ((Q[N + j] ?? 0) + (Q[N + E] ?? 0)) * W[Y];
      }
      $[V + X] = G;
    }
  }
}
function R_($, Q, Z, K, q, J, U, W) {
  let H = K + 2 * U;
  for (let _ = 0;_ < Z; _++)
    for (let N = 0;N < H; N++) {
      let V = N - U, X = 0;
      for (let z = 0;z < U; z++) {
        let G = Math.max(0, Math.min(K - 1, V - z - 1)), Y = Math.max(0, Math.min(K - 1, V + z));
        X += ((Q[G * q + _] ?? 0) + (Q[Y * q + _] ?? 0)) * W[z];
      }
      $[N * J + _] = X;
    }
}
function M_($, Q, Z) {
  let K = (Q + Z) / 2;
  return i6($, K);
}
function d8($, Q, Z = Q) {
  if (Q <= 0 && Z <= 0)
    return $;
  if ($.pixelMode === 4)
    return M_($, Q, Z);
  let K = Q * Q, q = Z * Z, J = aK(K), U = aK(q), W = $.width, H = $.rows, _ = (2 * J.radius + 9 << J.level) - 5, N = (2 * U.radius + 9 << U.level) - 5, V = (W + _ & ~((1 << J.level) - 1)) - 4, X = (H + N & ~((1 << U.level) - 1)) - 4, z = Math.max(W * H, V * X) * 2, G = new Float32Array(z), Y = new Float32Array(z);
  for (let R = 0;R < H; R++)
    for (let P = 0;P < W; P++)
      G[R * W + P] = $.buffer[R * $.pitch + P] ?? 0;
  let j = G, E = Y, w = W;
  for (let R = 0;R < U.level; R++) {
    let P = Math.floor((H + 5) / 2);
    L_(E, j, W, H, w, W), H = P, [j, E] = [E, j];
  }
  for (let R = 0;R < J.level; R++) {
    let P = Math.floor((W + 5) / 2);
    A_(E, j, W, H, w, P), W = P, w = P, [j, E] = [E, j];
  }
  let L = W + 2 * J.radius;
  B_(E, j, W, H, w, L, J.radius, J.coeff), W = L, w = L, [j, E] = [E, j];
  let A = H + 2 * U.radius;
  R_(E, j, W, H, w, w, U.radius, U.coeff), H = A, [j, E] = [E, j];
  for (let R = 0;R < J.level; R++) {
    let P = W * 2 + 4;
    F_(E, j, W, H, w, P), W = P, w = P, [j, E] = [E, j];
  }
  for (let R = 0;R < U.level; R++) {
    let P = H * 2 + 4;
    D_(E, j, W, H, w, w), H = P, [j, E] = [E, j];
  }
  let D = Math.min(W, V), F = Math.min(H, X), M = new Uint8Array(D * F);
  for (let R = 0;R < F; R++)
    for (let P = 0;P < D; P++) {
      let T = j[R * w + P] ?? 0;
      M[R * D + P] = Math.max(0, Math.min(255, Math.round(T)));
    }
  return { buffer: M, width: D, rows: F, pitch: D, pixelMode: 1, numGrays: 256 };
}
function a9($, Q) {
  return d8($, Q, Q);
}
function t9($, Q, Z) {
  let K = Z ?? Q;
  if (Math.max(Q, K) <= 3)
    return i6($, (Q + K) / 2);
  return d8($, Q, K);
}
G6();

class i {
  _bitmap;
  _bearingX;
  _bearingY;
  constructor($, Q = 0, Z = 0) {
    this._bitmap = $;
    this._bearingX = Q;
    this._bearingY = Z;
  }
  static fromBitmap($) {
    return new i(U6($));
  }
  static fromBitmapWithBearing($, Q, Z) {
    return new i(U6($), Q, Z);
  }
  static fromRasterizedGlyph($) {
    return new i(U6($.bitmap), $.bearingX, $.bearingY);
  }
  static create($, Q, Z = 1) {
    return new i(r($, Q, Z));
  }
  static fromGradient($, Q, Z) {
    return new i(i0($, Q, Z));
  }
  blur($) {
    let Q = i6(U6(this._bitmap), $);
    return new i(Q, this._bearingX, this._bearingY);
  }
  boxBlur($) {
    let Q = B8(U6(this._bitmap), $);
    return new i(Q, this._bearingX, this._bearingY);
  }
  cascadeBlur($, Q) {
    let Z = d8(U6(this._bitmap), $, Q ?? $);
    return new i(Z, this._bearingX, this._bearingY);
  }
  adaptiveBlur($, Q) {
    let Z = t9(U6(this._bitmap), $, Q ?? $);
    return new i(Z, this._bearingX, this._bearingY);
  }
  fastBlur($) {
    let Q = a9(U6(this._bitmap), $);
    return new i(Q, this._bearingX, this._bearingY);
  }
  embolden($, Q) {
    let Z = u8(this._bitmap, $, Q ?? $);
    return new i(Z, this._bearingX, this._bearingY);
  }
  emboldenWithBearing($, Q) {
    let Z = d9(this._bitmap, this._bearingX, this._bearingY, $, Q ?? $);
    return new i(Z.bitmap, Z.bearingX, Z.bearingY);
  }
  transform2D($, Q) {
    let Z = e6(this._bitmap, $, { bearingX: this._bearingX, bearingY: this._bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
    return new i(Z.bitmap, Z.bearingX, Z.bearingY);
  }
  transform3D($, Q) {
    let Z = D8(this._bitmap, $, { bearingX: this._bearingX, bearingY: this._bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
    return new i(Z.bitmap, Z.bearingX, Z.bearingY);
  }
  shearX($, Q) {
    let Z = q9(this._bitmap, $, { bearingX: this._bearingX, bearingY: this._bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
    return new i(Z.bitmap, Z.bearingX, Z.bearingY);
  }
  shearY($, Q) {
    let Z = J9(this._bitmap, $, { bearingX: this._bearingX, bearingY: this._bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
    return new i(Z.bitmap, Z.bearingX, Z.bearingY);
  }
  shift($, Q) {
    let Z = l9(this._bitmap, $, Q);
    return new i(Z, this._bearingX + $, this._bearingY - Q);
  }
  resize($, Q) {
    let Z = x8(this._bitmap, $, Q);
    return new i(Z, this._bearingX, this._bearingY);
  }
  resizeBilinear($, Q) {
    let Z = Z9(this._bitmap, $, Q);
    return new i(Z, this._bearingX, this._bearingY);
  }
  pad($, Q, Z, K) {
    let q = $, J = Q ?? $, U = Z ?? $, W = K ?? $, H = K9(this._bitmap, q, J, U, W);
    return new i(H, this._bearingX - q, this._bearingY + J);
  }
  measure() {
    return C$(this._bitmap, this._bearingX, this._bearingY);
  }
  metrics() {
    let { ascent: $, descent: Q } = this.measure();
    return { width: this._bitmap.width, height: this._bitmap.rows, bearingX: this._bearingX, bearingY: this._bearingY, ascent: $, descent: Q };
  }
  expandMetrics($) {
    return S$(this.metrics(), $);
  }
  blend($, Q, Z, K = 1) {
    let q = $ instanceof i ? $._bitmap : $, J = U6(this._bitmap);
    return p0(J, q, Q, Z, K), new i(J, this._bearingX, this._bearingY);
  }
  composite($, Q = 0, Z = 0) {
    let K = $ instanceof i ? $._bitmap : $, q = U6(this._bitmap);
    return v$(q, K, Q, Z), new i(q, this._bearingX, this._bearingY);
  }
  add($, Q = 0, Z = 0) {
    let K = $ instanceof i ? $._bitmap : $, q = U6(this._bitmap);
    return P$(q, K, Q, Z), new i(q, this._bearingX, this._bearingY);
  }
  subtract($, Q = 0, Z = 0) {
    let K = $ instanceof i ? $._bitmap : $, q = U6(this._bitmap);
    return c0(q, K, Q, Z), new i(q, this._bearingX, this._bearingY);
  }
  multiply($, Q = 0, Z = 0) {
    let K = $ instanceof i ? $._bitmap : $, q = U6(this._bitmap);
    return I$(q, K, Q, Z), new i(q, this._bearingX, this._bearingY);
  }
  max($, Q = 0, Z = 0) {
    let K = $ instanceof i ? $._bitmap : $, q = U6(this._bitmap);
    return k$(q, K, Q, Z), new i(q, this._bearingX, this._bearingY);
  }
  convert($) {
    let Q = N8(this._bitmap, $);
    return new i(Q, this._bearingX, this._bearingY);
  }
  toRGBA() {
    return s9(this._bitmap);
  }
  toGray() {
    return r9(this._bitmap);
  }
  toBitmap() {
    return U6(this._bitmap);
  }
  toRasterizedGlyph() {
    return { bitmap: U6(this._bitmap), bearingX: this._bearingX, bearingY: this._bearingY };
  }
  clone() {
    return new i(U6(this._bitmap), this._bearingX, this._bearingY);
  }
  get width() {
    return this._bitmap.width;
  }
  get height() {
    return this._bitmap.rows;
  }
  get pixelMode() {
    return this._bitmap.pixelMode;
  }
  get bearingX() {
    return this._bearingX;
  }
  get bearingY() {
    return this._bearingY;
  }
}
G6();

class q6 {
  _path;
  _transform;
  _font;
  constructor($, Q, Z) {
    this._path = $;
    this._transform = Q;
    this._font = Z;
  }
  static fromGlyph($, Q) {
    let Z = y6($, Q);
    if (!Z)
      return null;
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, $);
  }
  static fromGlyphWithVariation($, Q, Z) {
    let K = g9($, Q, Z);
    if (!K)
      return null;
    return new q6(K, { matrix2D: I6(), matrix3D: null }, $);
  }
  static fromPath($) {
    return new q6(e8($), { matrix2D: I6(), matrix3D: null }, null);
  }
  static combine(...$) {
    let Q = $.map((K) => K.toPath()), Z = m9(Q);
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, null);
  }
  scale($, Q = $) {
    let Z = b8(this._transform.matrix2D, S8($, Q));
    return new q6(this._path, { ...this._transform, matrix2D: Z }, this._font);
  }
  translate($, Q) {
    let Z = b8(this._transform.matrix2D, t8($, Q));
    return new q6(this._path, { ...this._transform, matrix2D: Z }, this._font);
  }
  rotate($) {
    let Q = b8(this._transform.matrix2D, h8($));
    return new q6(this._path, { ...this._transform, matrix2D: Q }, this._font);
  }
  rotateDeg($) {
    return this.rotate($ * Math.PI / 180);
  }
  shear($, Q) {
    let Z = b8(this._transform.matrix2D, y8($, Q));
    return new q6(this._path, { ...this._transform, matrix2D: Z }, this._font);
  }
  italic($) {
    let Q = Math.tan($ * Math.PI / 180);
    return this.shear(Q, 0);
  }
  matrix($) {
    let Q = b8(this._transform.matrix2D, $);
    return new q6(this._path, { ...this._transform, matrix2D: Q }, this._font);
  }
  resetTransform() {
    return new q6(this._path, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  perspective($) {
    let Q = this._transform.matrix3D ? y9(this._transform.matrix3D, $) : $;
    return new q6(this._path, { ...this._transform, matrix3D: Q }, this._font);
  }
  perspectiveVanish($, Q, Z) {
    let K = F$($, Q, Z);
    return this.perspective(K);
  }
  apply() {
    let $ = this.applyTransformToPath();
    return new q6($, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  embolden($) {
    let Q = this.applyTransformToPath(), Z = V9(Q, $);
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  condense($) {
    let Q = this.applyTransformToPath(), Z = N9(Q, $);
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  oblique($) {
    let Q = this.applyTransformToPath(), Z = _9(Q, $);
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  stroke($, Q, Z) {
    let K = this.applyTransformToPath(), J = H9(K, typeof $ === "number" ? { width: $, lineCap: Q, lineJoin: Z } : $);
    return new q6(J, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  strokeAsymmetric($) {
    let Q = this.applyTransformToPath(), { outer: Z, inner: K } = V8(Q, $);
    return { outer: new q6(Z, { matrix2D: I6(), matrix3D: null }, this._font), inner: new q6(K, { matrix2D: I6(), matrix3D: null }, this._font) };
  }
  strokeAsymmetricCombined($) {
    let Q = this.applyTransformToPath(), Z = c9(Q, $);
    return new q6(Z, { matrix2D: I6(), matrix3D: null }, this._font);
  }
  controlBox() {
    let $ = this.applyTransformToPath();
    return L$($);
  }
  tightBounds() {
    let $ = this.applyTransformToPath();
    return f0($);
  }
  getTransformMatrix() {
    return [...this._transform.matrix2D];
  }
  getTransformMatrix3D() {
    if (!this._transform.matrix3D)
      return null;
    return this._transform.matrix3D.map(($) => [...$]);
  }
  rasterize($) {
    let Q = this.applyTransformToPath(), { width: Z = 100, height: K = 100, scale: q = 1, offsetX: J = 0, offsetY: U = 0, pixelMode: W = 1, fillRule: H = 0, flipY: _ = true } = $, N = $8(Q, { width: Z, height: K, scale: q, offsetX: J, offsetY: U, pixelMode: W, fillRule: H, flipY: _ });
    return i.fromBitmap(N);
  }
  rasterizeAuto($) {
    let Q = this.applyTransformToPath(), { padding: Z = 1, scale: K = 1, pixelMode: q = 1, fillRule: J = 0, flipY: U = true } = $ ?? {}, W = _8(Q, K, U);
    if (!W) {
      let { createBitmap: z } = (G6(), B0(u9));
      return i.fromBitmap(z(1, 1, q));
    }
    let H = W.maxX - W.minX + Z * 2, _ = W.maxY - W.minY + Z * 2, N = -W.minX + Z, V = -W.minY + Z, X = $8(Q, { width: H, height: _, scale: K, offsetX: N, offsetY: V, pixelMode: q, fillRule: J, flipY: U });
    return i.fromBitmapWithBearing(X, W.minX - Z, -(W.minY - Z));
  }
  rasterizeWithGradient($, Q) {
    let Z = this.applyTransformToPath(), { width: K = 100, height: q = 100, scale: J = 1, offsetX: U = 0, offsetY: W = 0, pixelMode: H = 1, fillRule: _ = 0, flipY: N = true } = Q, V = W9(Z, $, { width: K, height: q, scale: J, offsetX: U, offsetY: W, pixelMode: H, fillRule: _, flipY: N });
    return i.fromBitmap(V);
  }
  toSdf($) {
    let Q = this.applyTransformToPath(), Z = f8(Q, $);
    return i.fromBitmap(Z);
  }
  toSdfAuto($) {
    let Q = this.applyTransformToPath(), { padding: Z = 1, scale: K = 1, spread: q = 8, flipY: J = true } = $ ?? {}, U = _8(Q, K, J);
    if (!U) {
      let { createBitmap: X } = (G6(), B0(u9));
      return i.fromBitmap(X(1, 1, 1));
    }
    let W = U.maxX - U.minX + Z * 2 + q * 2, H = U.maxY - U.minY + Z * 2 + q * 2, _ = -U.minX + Z + q, N = J ? U.maxY + Z + q : -U.minY + Z + q, V = f8(Q, { width: W, height: H, scale: K, offsetX: _, offsetY: N, flipY: J, spread: q });
    return i.fromBitmapWithBearing(V, U.minX - Z - q, -(U.minY - Z - q));
  }
  toMsdf($) {
    let Q = this.applyTransformToPath(), Z = l8(Q, $);
    return i.fromBitmap(Z);
  }
  toMsdfAuto($) {
    let Q = this.applyTransformToPath(), { padding: Z = 1, scale: K = 1, spread: q = 8, flipY: J = true } = $ ?? {}, U = _8(Q, K, J);
    if (!U) {
      let { createBitmap: X } = (G6(), B0(u9));
      return i.fromBitmap(X(1, 1, 2));
    }
    let W = U.maxX - U.minX + Z * 2 + q * 2, H = U.maxY - U.minY + Z * 2 + q * 2, _ = -U.minX + Z + q, N = J ? U.maxY + Z + q : -U.minY + Z + q, V = l8(Q, { width: W, height: H, scale: K, offsetX: _, offsetY: N, flipY: J, spread: q });
    return i.fromBitmapWithBearing(V, U.minX - Z - q, -(U.minY - Z - q));
  }
  toSVG($) {
    let { flipY: Q = true, scale: Z = 1 } = $ ?? {};
    if (this.hasTransform()) {
      let K = this.applyTransformToPath();
      return L8(K, { flipY: Q, scale: Z });
    }
    return L8(this._path, { flipY: Q, scale: Z });
  }
  toSVGElement($) {
    let { fontSize: Q = 100, fill: Z = "currentColor", stroke: K, strokeWidth: q = 1 } = $ ?? {}, J = this.applyTransformToPath(), U = f0(J), W = this._font ? Q / this._font.unitsPerEm : 1, H = W !== 1 ? O6(J, S8(W, W)) : J, _ = W !== 1 ? { xMin: U.xMin * W, yMin: U.yMin * W, xMax: U.xMax * W, yMax: U.yMax * W } : U, N = K ? q / 2 : 0, V = Math.ceil(_.xMax - _.xMin + N * 2), X = Math.ceil(_.yMax - _.yMin + N * 2), z = `${_.xMin - N} ${-_.yMax - N} ${_.xMax - _.xMin + N * 2} ${_.yMax - _.yMin + N * 2}`, G = L8(H, { flipY: true, scale: 1 }), Y = K ? ` stroke="${K}" stroke-width="${q}"` : "";
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${V}" height="${X}" viewBox="${z}">
  <path d="${G}" fill="${Z}"${Y}/>
</svg>`;
  }
  toCanvas($, Q) {
    let { flipY: Z = true, scale: K = 1, offsetX: q = 0, offsetY: J = 0, fill: U = "black", stroke: W, strokeWidth: H = 1 } = Q ?? {};
    if ($.beginPath(), this._transform.matrix3D) {
      let _ = this.combinedMatrix3D(K, q, J);
      d0($, this._path, _, { flipY: Z });
    } else if (this.hasTransform2D()) {
      let _ = this.combinedMatrix2D(K, q, J);
      l0($, this._path, _, { flipY: Z });
    } else
      $9($, this._path, { flipY: Z, scale: K, offsetX: q, offsetY: J });
    if (U !== "none")
      $.fillStyle = U, $.fill();
    if (W)
      $.strokeStyle = W, $.lineWidth = H, $.stroke();
  }
  toPath2D($) {
    let Q = this.applyTransformToPath();
    return R$(Q, $);
  }
  toPath() {
    return this.applyTransformToPath();
  }
  clone() {
    return new q6(e8(this._path), { matrix2D: [...this._transform.matrix2D], matrix3D: this._transform.matrix3D ? this._transform.matrix3D.map(($) => [...$]) : null }, this._font);
  }
  applyTransformToPath() {
    if (this._transform.matrix3D) {
      let $ = this._transform.matrix2D, Q = [[$[0], $[2], $[4]], [$[1], $[3], $[5]], [0, 0, 1]], Z = y9(this._transform.matrix3D, Q);
      return b9(this._path, Z);
    }
    return O6(this._path, this._transform.matrix2D);
  }
  hasTransform() {
    return this.hasTransform2D() || this._transform.matrix3D !== null;
  }
  hasTransform2D() {
    let $ = this._transform.matrix2D;
    return $[0] !== 1 || $[1] !== 0 || $[2] !== 0 || $[3] !== 1 || $[4] !== 0 || $[5] !== 0;
  }
  combinedMatrix2D($, Q, Z) {
    let K = this._transform.matrix2D;
    return [K[0] * $, K[1] * $, K[2] * $, K[3] * $, K[0] * Q + K[2] * Z + K[4] * $, K[1] * Q + K[3] * Z + K[5] * $];
  }
  combinedMatrix3D($, Q, Z) {
    let K = this._transform.matrix2D, q = this._transform.matrix3D ?? A$(), J = [[K[0] * $, K[2] * $, K[4] * $ + Q], [K[1] * $, K[3] * $, K[5] * $ + Z], [0, 0, 1]];
    return y9(q, J);
  }
}
G6();
function tK($, ...Q) {
  return Q.reduce((Z, K) => K(Z), $);
}
function eK($, Q) {
  return y6($, Q);
}
function $q($, Q) {
  return (Z) => O6(Z, S8($, Q ?? $));
}
function Qq($, Q) {
  return (Z) => O6(Z, t8($, Q));
}
function Zq($) {
  return (Q) => O6(Q, h8($));
}
function Kq($) {
  return (Q) => O6(Q, h8($ * Math.PI / 180));
}
function qq($, Q) {
  return (Z) => O6(Z, y8($, Q));
}
function Jq($) {
  let Q = Math.tan($ * Math.PI / 180);
  return (Z) => O6(Z, y8(Q, 0));
}
function Uq($) {
  return (Q) => O6(Q, $);
}
function Wq($) {
  return (Q) => b9(Q, $);
}
function Hq($) {
  return (Q) => V9(Q, $);
}
function _q($) {
  return (Q) => N9(Q, $);
}
function Nq($) {
  return (Q) => _9(Q, $);
}
function Vq($, Q, Z) {
  let K = typeof $ === "number" ? { width: $, lineCap: Q, lineJoin: Z } : $;
  return (q) => H9(q, K);
}
function Xq() {
  return ($) => e8($);
}
function zq($) {
  return m9($);
}
function Gq($) {
  return (Q) => V8(Q, $);
}
function Yq($) {
  return (Q) => c9(Q, $);
}
function jq($) {
  return (Q) => $8(Q, { width: $.width ?? 100, height: $.height ?? 100, scale: $.scale ?? 1, offsetX: $.offsetX ?? 0, offsetY: $.offsetY ?? 0, pixelMode: $.pixelMode ?? 1, fillRule: $.fillRule ?? 0, flipY: $.flipY ?? true });
}
function Eq($) {
  let { padding: Q = 1, scale: Z = 1, pixelMode: K = 1 } = $ ?? {};
  return (q) => {
    let J = _8(q, Z, true);
    if (!J) {
      let { createBitmap: N } = (G6(), B0(u9));
      return N(1, 1, K);
    }
    let U = J.maxX - J.minX + Q * 2, W = J.maxY - J.minY + Q * 2, H = -J.minX + Q, _ = -J.minY + Q;
    return $8(q, { width: U, height: W, scale: Z, offsetX: H, offsetY: _, pixelMode: K, fillRule: 0, flipY: true });
  };
}
function wq($, Q) {
  return (Z) => W9(Z, $, { width: Q.width ?? 100, height: Q.height ?? 100, scale: Q.scale ?? 1, offsetX: Q.offsetX ?? 0, offsetY: Q.offsetY ?? 0, pixelMode: Q.pixelMode ?? 1, fillRule: Q.fillRule ?? 0, flipY: Q.flipY ?? true });
}
function Aq($) {
  return (Q) => f8(Q, $);
}
function Lq($) {
  return (Q) => l8(Q, $);
}
function Fq($) {
  return (Q) => i6(U6(Q), $);
}
function Dq($) {
  return (Q) => B8(U6(Q), $);
}
function Bq($, Q) {
  return (Z) => d8(U6(Z), $, Q ?? $);
}
function Rq($, Q) {
  return (Z) => t9(U6(Z), $, Q ?? $);
}
function Mq($) {
  return (Q) => a9(U6(Q), $);
}
function Tq($, Q) {
  return (Z) => u8(Z, $, Q ?? $);
}
function Oq($, Q) {
  return (Z) => d9(Z.bitmap, Z.bearingX, Z.bearingY, $, Q ?? $);
}
function Pq($, Q) {
  return (Z) => e6(Z, $, { bearingX: 0, bearingY: 0, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 }).bitmap;
}
function Iq($, Q) {
  return (Z) => D8(Z, $, { bearingX: 0, bearingY: 0, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 }).bitmap;
}
function vq($, Q) {
  return (Z) => e6(Z.bitmap, $, { bearingX: Z.bearingX, bearingY: Z.bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
}
function kq($, Q) {
  return (Z) => D8(Z.bitmap, $, { bearingX: Z.bearingX, bearingY: Z.bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
}
function Cq($, Q) {
  return (Z) => q9(Z, $, { bearingX: 0, bearingY: 0, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 }).bitmap;
}
function Sq($, Q) {
  return (Z) => J9(Z, $, { bearingX: 0, bearingY: 0, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 }).bitmap;
}
function hq($, Q) {
  return (Z) => q9(Z.bitmap, $, { bearingX: Z.bearingX, bearingY: Z.bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
}
function yq($, Q) {
  return (Z) => J9(Z.bitmap, $, { bearingX: Z.bearingX, bearingY: Z.bearingY, offsetX26: Q?.offsetX26, offsetY26: Q?.offsetY26 });
}
function bq($, Q) {
  return (Z) => l9(Z, $, Q);
}
function mq($, Q) {
  return (Z) => x8(Z, $, Q);
}
function gq($, Q) {
  return (Z) => Z9(Z, $, Q);
}
function uq($, Q, Z, K) {
  let q = $, J = Q ?? $, U = Z ?? $, W = K ?? $;
  return (H) => K9(H, q, J, U, W);
}
function xq($) {
  return (Q) => N8(Q, $);
}
function fq($) {
  return s9($);
}
function lq($) {
  return r9($);
}
function dq($) {
  return (Q) => L8(Q, $);
}
function nq($) {
  return U6($);
}
function T_($, Q) {
  return q6.fromGlyph($, Q);
}
function O_($, Q) {
  let Z = Q.codePointAt(0);
  if (Z === undefined)
    return null;
  let K = $.glyphId(Z);
  if (K === undefined)
    return null;
  return q6.fromGlyph($, K);
}
function P_($, Q, Z) {
  return q6.fromGlyphWithVariation($, Q, Z);
}
function I_($) {
  return q6.fromPath($);
}
function v_($) {
  return i.fromBitmap($);
}
function k_(...$) {
  return q6.combine(...$);
}

class D6 {
  data;
  start;
  end;
  pos;
  constructor($, Q = 0, Z) {
    if ($ instanceof ArrayBuffer)
      this.data = new DataView($), this.start = Q, this.end = Z !== undefined ? Q + Z : $.byteLength;
    else
      this.data = $, this.start = $.byteOffset + Q, this.end = Z !== undefined ? this.start + Z : $.byteOffset + $.byteLength;
    this.pos = this.start;
  }
  get offset() {
    return this.pos - this.start;
  }
  get remaining() {
    return this.end - this.pos;
  }
  get length() {
    return this.end - this.start;
  }
  seek($) {
    this.pos = this.start + $;
  }
  skip($) {
    this.pos += $;
  }
  slice($, Q) {
    return new D6(this.data, this.start + $, Q);
  }
  sliceFrom($) {
    return new D6(this.data, this.start + $, this.end - this.start - $);
  }
  peek($) {
    let Q = this.pos, Z = $();
    return this.pos = Q, Z;
  }
  uint8() {
    let $ = this.data.getUint8(this.pos);
    return this.pos += 1, $;
  }
  int8() {
    let $ = this.data.getInt8(this.pos);
    return this.pos += 1, $;
  }
  uint16() {
    let $ = this.data.getUint16(this.pos, false);
    return this.pos += 2, $;
  }
  int16() {
    let $ = this.data.getInt16(this.pos, false);
    return this.pos += 2, $;
  }
  uint32() {
    let $ = this.data.getUint32(this.pos, false);
    return this.pos += 4, $;
  }
  int32() {
    let $ = this.data.getInt32(this.pos, false);
    return this.pos += 4, $;
  }
  fixed() {
    return this.int32() / 65536;
  }
  f2dot14() {
    return this.int16() / 16384;
  }
  fword() {
    return this.int16();
  }
  ufword() {
    return this.uint16();
  }
  longDateTime() {
    let $ = this.uint32(), Q = this.uint32();
    return BigInt($) << 32n | BigInt(Q);
  }
  tag() {
    return this.uint32();
  }
  tagString() {
    let $ = this.uint32();
    return String.fromCharCode($ >> 24 & 255, $ >> 16 & 255, $ >> 8 & 255, $ & 255);
  }
  offset16() {
    return this.uint16();
  }
  offset32() {
    return this.uint32();
  }
  uint24() {
    let $ = this.data.getUint8(this.pos), Q = this.data.getUint8(this.pos + 1), Z = this.data.getUint8(this.pos + 2);
    return this.pos += 3, $ << 16 | Q << 8 | Z;
  }
  uint8Array($) {
    let Q = new Uint8Array($);
    for (let Z = 0;Z < $; Z++)
      Q[Z] = this.uint8();
    return Q;
  }
  uint16Array($) {
    let Q = new Uint16Array($);
    for (let Z = 0;Z < $; Z++)
      Q[Z] = this.uint16();
    return Q;
  }
  int16Array($) {
    let Q = new Int16Array($);
    for (let Z = 0;Z < $; Z++)
      Q[Z] = this.int16();
    return Q;
  }
  uint32Array($) {
    let Q = new Uint32Array($);
    for (let Z = 0;Z < $; Z++)
      Q[Z] = this.uint32();
    return Q;
  }
  array($, Q) {
    let Z = Array($);
    for (let K = 0;K < $; K++)
      Z[K] = Q(this);
    return Z;
  }
  ascii($) {
    let Q = "";
    for (let Z = 0;Z < $; Z++)
      Q += String.fromCharCode(this.uint8());
    return Q;
  }
  utf16be($) {
    let Q = [], Z = $ / 2;
    for (let K = 0;K < Z; K++)
      Q.push(this.uint16());
    return String.fromCharCode(...Q);
  }
  hasRemaining($) {
    return this.remaining >= $;
  }
  ensureRemaining($) {
    if (this.remaining < $)
      throw Error(`Unexpected end of data: need ${$} bytes, have ${this.remaining}`);
  }
  bytes($) {
    let Q = new Uint8Array(this.data.buffer, this.data.byteOffset + this.pos, $);
    return this.pos += $, Q;
  }
  readAt($, Q) {
    let Z = this.pos;
    this.pos = this.start + $;
    let K = Q(this);
    return this.pos = Z, K;
  }
}
function pq($, Q) {
  let Z = $.uint16(), K = $.uint16();
  $.skip(2);
  let q = [];
  for (let J = 0;J < Q; J++) {
    let U = $.uint16(), W = [];
    for (let H = 0;H < U; H++)
      W.push({ fromCoordinate: $.f2dot14(), toCoordinate: $.f2dot14() });
    q.push({ axisValueMaps: W });
  }
  return { majorVersion: Z, minorVersion: K, axisSegmentMaps: q };
}
function cq($, Q) {
  let Z = $.axisValueMaps;
  if (Z.length === 0)
    return Q;
  for (let J = 0;J < Z.length - 1; J++) {
    let U = Z[J], W = Z[J + 1];
    if (!U || !W)
      continue;
    if (Q >= U.fromCoordinate && Q <= W.fromCoordinate) {
      let H = (Q - U.fromCoordinate) / (W.fromCoordinate - U.fromCoordinate);
      return U.toCoordinate + H * (W.toCoordinate - U.toCoordinate);
    }
  }
  let K = Z[0], q = Z[Z.length - 1];
  if (K && Q <= K.fromCoordinate)
    return K.toCoordinate;
  return q?.toCoordinate ?? Q;
}
function n$($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.axisSegmentMaps[K];
    if (J)
      Z.push(cq(J, q));
    else
      Z.push(q);
  }
  return Z;
}
function oq($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset16();
  $.skip(2);
  let q = $.uint16(), J = $.uint16(), U = $.uint16(), W = $.uint16(), H = [];
  $.seek(K);
  for (let V = 0;V < q; V++) {
    let X = $.offset, z = $.uint32(), G = $.fixed(), Y = $.fixed(), j = $.fixed(), E = $.uint16(), w = $.uint16();
    H.push({ tag: z, minValue: G, defaultValue: Y, maxValue: j, flags: E, axisNameId: w }), $.seek(X + J);
  }
  let _ = [], N = W >= 4 + q * 4 + 2;
  for (let V = 0;V < U; V++) {
    let X = $.offset, z = $.uint16(), G = $.uint16(), Y = [];
    for (let E = 0;E < q; E++)
      Y.push($.fixed());
    let j = { subfamilyNameId: z, flags: G, coordinates: Y };
    if (N)
      j.postScriptNameId = $.uint16();
    _.push(j), $.seek(X + W);
  }
  return { majorVersion: Q, minorVersion: Z, axes: H, instances: _ };
}
function p$($, Q) {
  if (Q < $.defaultValue) {
    if (Q < $.minValue)
      Q = $.minValue;
    if ($.defaultValue === $.minValue)
      return 0;
    return (Q - $.defaultValue) / ($.defaultValue - $.minValue);
  } else if (Q > $.defaultValue) {
    if (Q > $.maxValue)
      Q = $.maxValue;
    if ($.defaultValue === $.maxValue)
      return 0;
    return (Q - $.defaultValue) / ($.maxValue - $.defaultValue);
  }
  return 0;
}
function iq($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset32(), q = $.offset32(), J = $.offset32(), U = $.offset32(), W = C_($.sliceFrom(K)), H = q !== 0 ? c$($.sliceFrom(q)) : null, _ = J !== 0 ? c$($.sliceFrom(J)) : null, N = U !== 0 ? c$($.sliceFrom(U)) : null;
  return { majorVersion: Q, minorVersion: Z, itemVariationStore: W, advanceWidthMapping: H, lsbMapping: _, rsbMapping: N };
}
function C_($) {
  let Q = $.uint16(), Z = $.offset32(), K = $.uint16(), q = [];
  for (let N = 0;N < K; N++)
    q.push($.offset32());
  let J = $.sliceFrom(Z), U = J.uint16(), W = J.uint16(), H = [];
  for (let N = 0;N < W; N++) {
    let V = [];
    for (let X = 0;X < U; X++)
      V.push({ startCoord: J.f2dot14(), peakCoord: J.f2dot14(), endCoord: J.f2dot14() });
    H.push({ regionAxes: V });
  }
  let _ = [];
  for (let N = 0;N < q.length; N++) {
    let V = q[N], X = $.sliceFrom(V), z = X.uint16(), G = X.uint16(), Y = X.uint16(), j = [];
    for (let D = 0;D < Y; D++)
      j.push(X.uint16());
    let E = (G & 32768) !== 0, w = G & 32767, L = Y - w, A = [];
    for (let D = 0;D < z; D++) {
      let F = [];
      for (let M = 0;M < w; M++)
        if (E)
          F.push(X.int32());
        else
          F.push(X.int16());
      for (let M = 0;M < L; M++)
        if (E)
          F.push(X.int16());
        else
          F.push(X.int8());
      A.push(F);
    }
    _.push({ itemCount: z, regionIndexes: j, deltaSets: A });
  }
  return { format: Q, variationRegions: H, itemVariationData: _ };
}
function c$($) {
  let Q = $.uint8(), Z = $.uint8(), K = Q === 0 ? $.uint16() : $.uint32(), q = (Z & 15) + 1, J = (Z >> 4 & 3) + 1, U = [];
  for (let W = 0;W < K; W++) {
    let H = 0;
    for (let V = 0;V < J; V++)
      H = H << 8 | $.uint8();
    let _ = H & (1 << q) - 1, N = H >> q;
    U.push({ outer: N, inner: _ });
  }
  return { format: Q, mapCount: K, entryFormat: Z, innerIndexBitCount: q, mapData: U };
}
function e9($, Q) {
  let Z = 1;
  for (let K = 0;K < $.regionAxes.length && K < Q.length; K++) {
    let q = $.regionAxes[K], J = Q[K];
    if (q === undefined || J === undefined)
      continue;
    if (J < q.startCoord || J > q.endCoord)
      return 0;
    if (J === q.peakCoord)
      continue;
    if (J < q.peakCoord) {
      if (q.peakCoord === q.startCoord)
        continue;
      Z *= (J - q.startCoord) / (q.peakCoord - q.startCoord);
    } else {
      if (q.peakCoord === q.endCoord)
        continue;
      Z *= (q.endCoord - J) / (q.endCoord - q.peakCoord);
    }
  }
  return Z;
}
function sq($, Q, Z, K) {
  let q, J;
  if (K && Q < K.mapData.length) {
    let _ = K.mapData[Q];
    if (!_)
      q = 0, J = Q;
    else
      q = _.outer, J = _.inner;
  } else
    q = 0, J = Q;
  let U = $.itemVariationStore.itemVariationData[q];
  if (!U || J >= U.itemCount)
    return 0;
  let W = U.deltaSets[J];
  if (!W)
    return 0;
  let H = 0;
  for (let _ = 0;_ < U.regionIndexes.length; _++) {
    let N = U.regionIndexes[_], V = $.itemVariationStore.variationRegions[N];
    if (!V)
      continue;
    let X = e9(V, Z), z = W[_] ?? 0;
    H += X * z;
  }
  return Math.round(H);
}
function o$($, Q, Z) {
  return sq($, Q, Z, $.advanceWidthMapping);
}
function rq($, Q, Z) {
  if (!$.lsbMapping)
    return 0;
  return sq($, Q, Z, $.lsbMapping);
}

class n8 {
  font;
  _coords;
  _variations;
  _advanceDeltas = null;
  constructor($, Q) {
    this.font = $, this._coords = [], this._variations = new Map;
    let Z = $.fvar;
    if (Z) {
      if (this._coords = Array(Z.axes.length).fill(0), Q)
        this.setVariations(Q);
    }
  }
  setVariations($) {
    let Q = this.font.fvar;
    if (!Q)
      return;
    if (this._advanceDeltas = null, Array.isArray($))
      for (let K = 0;K < $.length; K++) {
        let q = $[K];
        this._variations.set(q.tag, q.value);
      }
    else {
      let K = Object.entries($);
      for (let q = 0;q < K.length; q++) {
        let [J, U] = K[q], W = C(J.padEnd(4, " "));
        this._variations.set(W, U);
      }
    }
    for (let K = 0;K < Q.axes.length; K++) {
      let q = Q.axes[K], J = this._variations.get(q.tag) ?? q.defaultValue;
      this._coords[K] = p$(q, J);
    }
    let Z = this.font.avar;
    if (Z)
      this._coords = n$(Z, this._coords);
  }
  get normalizedCoords() {
    return this._coords;
  }
  get isVariable() {
    return this.font.isVariable;
  }
  get axes() {
    return this.font.fvar?.axes ?? [];
  }
  getAxisValue($) {
    let Q = typeof $ === "string" ? C($.padEnd(4, " ")) : $, Z = this.font.fvar;
    if (!Z)
      return null;
    let K = this._variations.get(Q);
    if (K !== undefined)
      return K;
    return Z.axes.find((J) => J.tag === Q)?.defaultValue ?? null;
  }
  advanceWidth($) {
    let Q = this.font.advanceWidth($);
    if (this._coords.length > 0 && this.font.hvar) {
      if (this._advanceDeltas) {
        let K = this._advanceDeltas.get($);
        if (K !== undefined)
          return Q + K;
      } else
        this._advanceDeltas = new Map;
      let Z = o$(this.font.hvar, $, this._coords);
      return this._advanceDeltas.set($, Z), Q + Z;
    }
    return Q;
  }
  leftSideBearing($) {
    let Q = this.font.leftSideBearing($);
    if (this._coords.length > 0 && this.font.hvar) {
      let Z = rq(this.font.hvar, $, this._coords);
      Q += Z;
    }
    return Q;
  }
  get numGlyphs() {
    return this.font.numGlyphs;
  }
  get unitsPerEm() {
    return this.font.unitsPerEm;
  }
  get ascender() {
    return this.font.ascender;
  }
  get descender() {
    return this.font.descender;
  }
  get lineGap() {
    return this.font.lineGap;
  }
  glyphId($) {
    return this.font.glyphId($);
  }
  glyphIdForChar($) {
    return this.font.glyphIdForChar($);
  }
  hasTable($) {
    return this.font.hasTable($);
  }
  get gdef() {
    return this.font.gdef;
  }
  get gsub() {
    return this.font.gsub;
  }
  get gpos() {
    return this.font.gpos;
  }
  get kern() {
    return this.font.kern;
  }
  get morx() {
    return this.font.morx;
  }
  get cmap() {
    return this.font.cmap;
  }
  get hmtx() {
    return this.font.hmtx;
  }
  get hhea() {
    return this.font.hhea;
  }
}
function S_($, Q) {
  return new n8($, Q);
}
var aq = 1953784678;
function i$($) {
  return new DataView($).getUint32(0, false) === aq;
}
function s$($) {
  let Q = new D6($);
  if (Q.uint32() !== aq)
    throw Error("Invalid TTC header");
  let K = Q.uint32(), q = Q.uint32();
  if (q <= 0)
    throw Error("Invalid TTC font count");
  let J = Math.floor(($.byteLength - 12) / 4);
  if (q > J)
    throw Error("Invalid TTC font count");
  let U = Array(q);
  for (let W = 0;W < q; W++)
    U[W] = Q.uint32();
  if (K === 131072 && Q.remaining >= 12) {
    let W = Q.uint32(), H = Q.uint32(), _ = Q.uint32();
    return { version: K, numFonts: q, offsets: U, dsigTag: W, dsigOffset: H, dsigLength: _ };
  }
  return { version: K, numFonts: q, offsets: U };
}
var s_ = ["cmap", "head", "hhea", "hmtx", "maxp", "name", "OS/2", "post", "cvt ", "fpgm", "glyf", "loca", "prep", "CFF ", "VORG", "EBDT", "EBLC", "gasp", "hdmx", "kern", "LTSH", "PCLT", "VDMX", "vhea", "vmtx", "BASE", "GDEF", "GPOS", "GSUB", "EBSC", "JSTF", "MATH", "CBDT", "CBLC", "COLR", "CPAL", "SVG ", "sbix", "acnt", "avar", "bdat", "bloc", "bsln", "cvar", "fdsc", "feat", "fmtx", "fvar", "gvar", "hsty", "just", "lcar", "mort", "morx", "opbd", "prop", "trak", "Zapf", "Silf", "Glat", "Gloc", "Feat", "Sill"];
function GJ($, Q) {
  let Z = 0;
  for (let K = 0;K < 5; K++) {
    let q = $[Q.value++];
    if (K === 0 && q === 128)
      throw Error("Invalid UIntBase128: leading zeros");
    if (Z > 2097151)
      throw Error("UIntBase128 overflow");
    if (Z = Z << 7 | q & 127, (q & 128) === 0)
      return Z;
  }
  throw Error("UIntBase128 too long");
}
function q1($, Q) {
  let Z = $[Q.value++];
  if (Z === 253) {
    let K = $[Q.value++], q = $[Q.value++];
    return K << 8 | q;
  } else if (Z === 255)
    return $[Q.value++] + 506;
  else if (Z === 254)
    return $[Q.value++] + 253;
  return Z;
}
function r_($, Q, Z) {
  let K = [];
  for (let q = 0;q < Z; q++) {
    let J = $[Q.value++], U = J & 63, W = J >> 6 & 3, H;
    if (U === 63)
      H = String.fromCharCode($[Q.value++], $[Q.value++], $[Q.value++], $[Q.value++]);
    else
      H = s_[U];
    let _ = GJ($, Q), N = _;
    if (H === "glyf" || H === "loca" ? W === 0 : W !== 0)
      N = GJ($, Q);
    K.push({ tag: H, origLength: _, transformLength: N, transformVersion: W });
  }
  return K;
}
async function a_($) {
  if (typeof DecompressionStream < "u")
    try {
      let Z = new DecompressionStream("brotli"), q = new Blob([$.buffer]).stream().pipeThrough(Z), J = await new Response(q).arrayBuffer();
      return new Uint8Array(J);
    } catch {
    }
  let { decompress: Q } = await Promise.resolve().then(() => (zJ(), XJ));
  return Q($);
}
function n6($, Q, Z) {
  $[Q] = Z >> 8 & 255, $[Q + 1] = Z & 255;
}
function X9($, Q, Z) {
  $[Q] = Z >> 24 & 255, $[Q + 1] = Z >> 16 & 255, $[Q + 2] = Z >> 8 & 255, $[Q + 3] = Z & 255;
}
function M8($, Q) {
  return $[Q] << 8 | $[Q + 1];
}
function Q8($, Q) {
  let Z = M8($, Q);
  return Z >= 32768 ? Z - 65536 : Z;
}
function p8($, Q) {
  return ($[Q] << 24 | $[Q + 1] << 16 | $[Q + 2] << 8 | $[Q + 3]) >>> 0;
}
function YJ($, Q, Z) {
  let K = 0, q = Math.ceil(Z / 4);
  for (let J = 0;J < q; J++) {
    let U = Q + J * 4;
    K = K + (($[U] || 0) << 24 | ($[U + 1] || 0) << 16 | ($[U + 2] || 0) << 8 | ($[U + 3] || 0)) >>> 0;
  }
  return K;
}
function K5($) {
  return $ + 3 & -4;
}
function t_($, Q, Z, K, q) {
  let J = [], U = 0, W = 0;
  function H(_, N) {
    return _ & 1 ? N : -N;
  }
  for (let _ = 0;_ < Z; _++) {
    let N = $[K.value++], V = N >> 7 === 0, X = N & 127, z = 0, G = 0;
    if (X < 10)
      z = 0, G = H(N, ((X & 14) << 7) + Q[q.value++]);
    else if (X < 20)
      z = H(N, ((X - 10 & 14) << 7) + Q[q.value++]), G = 0;
    else if (X < 84) {
      let Y = X - 20, j = Q[q.value++];
      z = H(N, 1 + (Y & 48) + (j >> 4)), G = H(N >> 1, 1 + ((Y & 12) << 2) + (j & 15));
    } else if (X < 120) {
      let Y = X - 84;
      z = H(N, 1 + (Math.floor(Y / 12) << 8) + Q[q.value++]), G = H(N >> 1, 1 + (Y % 12 >> 2 << 8) + Q[q.value++]);
    } else if (X < 124) {
      let Y = Q[q.value++], j = Q[q.value++], E = Q[q.value++];
      z = H(N, (Y << 4) + (j >> 4)), G = H(N >> 1, ((j & 15) << 8) + E);
    } else
      z = H(N, (Q[q.value++] << 8) + Q[q.value++]), G = H(N >> 1, (Q[q.value++] << 8) + Q[q.value++]);
    U += z, W += G, J.push({ x: U, y: W, onCurve: V });
  }
  return J;
}
function e_($, Q, Z) {
  let K = 0, q = M8($, K);
  if (K += 2, q !== 0)
    throw Error(`Unsupported glyf transform version: ${q}`);
  let J = M8($, K);
  K += 2;
  let U = M8($, K);
  K += 2;
  let W = M8($, K);
  K += 2;
  let H = p8($, K);
  K += 4;
  let _ = p8($, K);
  K += 4;
  let N = p8($, K);
  K += 4;
  let V = p8($, K);
  K += 4;
  let X = p8($, K);
  K += 4;
  let z = p8($, K);
  K += 4;
  let G = p8($, K);
  K += 4;
  let Y = $.slice(K, K + H);
  K += H;
  let j = $.slice(K, K + _);
  K += _;
  let E = $.slice(K, K + N);
  K += N;
  let w = $.slice(K, K + V);
  K += V;
  let L = $.slice(K, K + X);
  K += X;
  let A = $.slice(K, K + z);
  K += z;
  let D = $.slice(K, K + G), F = { value: 0 }, M = { value: 0 }, R = { value: 0 }, P = { value: 0 }, T = { value: 0 }, m = { value: 0 }, v = { value: 0 }, f = [0], l = [], k = 0;
  for (let o = 0;o < Q; o++) {
    let n = Q8(Y, F.value);
    if (F.value += 2, n === 0) {
      l.push(new Uint8Array(0)), f.push(k);
      continue;
    }
    if (n > 0) {
      let e = $N(n, j, M, E, R, w, P, A, m, D, v, J);
      l.push(e), k += K5(e.length), f.push(k);
    } else {
      let e = QN(L, T, A, m, D, v, J);
      l.push(e), k += K5(e.length), f.push(k);
    }
  }
  let O = new Uint8Array(k), h = 0;
  for (let o = 0;o < l.length; o++) {
    let n = l[o];
    O.set(n, h), h += K5(n.length);
  }
  let S = Z === 0 ? (Q + 1) * 2 : (Q + 1) * 4, d = new Uint8Array(S);
  for (let o = 0;o <= Q; o++)
    if (Z === 0)
      n6(d, o * 2, f[o] / 2);
    else
      X9(d, o * 4, f[o]);
  return { glyf: O, loca: d };
}
function $N($, Q, Z, K, q, J, U, W, H, _, N, V) {
  let X = [], z = 0;
  for (let h = 0;h < $; h++) {
    let S = q1(Q, Z);
    z += S, X.push(z - 1);
  }
  let G = t_(K, J, z, q, U), Y, j, E, w;
  if ((V & 1) === 0 && H.value + 8 <= W.length)
    Y = Q8(W, H.value), H.value += 2, j = Q8(W, H.value), H.value += 2, E = Q8(W, H.value), H.value += 2, w = Q8(W, H.value), H.value += 2;
  else {
    Y = j = 32767, E = w = -32768;
    for (let h = 0;h < G.length; h++) {
      let S = G[h];
      Y = Math.min(Y, S.x), j = Math.min(j, S.y), E = Math.max(E, S.x), w = Math.max(w, S.y);
    }
  }
  let A = q1(J, U), D = _.slice(N.value, N.value + A);
  N.value += A;
  let F = [], M = [], R = 0, P = 0;
  for (let h = 0;h < G.length; h++) {
    let S = G[h];
    F.push(S.x - R), M.push(S.y - P), R = S.x, P = S.y;
  }
  let T = [], m = [], v = [];
  for (let h = 0;h < z; h++) {
    let S = G[h].onCurve ? 1 : 0, d = F[h], o = M[h];
    if (d === 0)
      S |= 16;
    else if (d >= -255 && d <= 255) {
      if (S |= 2, d > 0)
        S |= 16;
      m.push(Math.abs(d));
    } else
      m.push(d >> 8 & 255, d & 255);
    if (o === 0)
      S |= 32;
    else if (o >= -255 && o <= 255) {
      if (S |= 4, o > 0)
        S |= 32;
      v.push(Math.abs(o));
    } else
      v.push(o >> 8 & 255, o & 255);
    T.push(S);
  }
  let l = 10 + $ * 2 + 2 + A + T.length + m.length + v.length, k = new Uint8Array(l), O = 0;
  n6(k, O, $), O += 2, n6(k, O, Y & 65535), O += 2, n6(k, O, j & 65535), O += 2, n6(k, O, E & 65535), O += 2, n6(k, O, w & 65535), O += 2;
  for (let h = 0;h < X.length; h++) {
    let S = X[h];
    n6(k, O, S), O += 2;
  }
  n6(k, O, A), O += 2, k.set(D, O), O += A;
  for (let h = 0;h < T.length; h++) {
    let S = T[h];
    k[O++] = S;
  }
  for (let h = 0;h < m.length; h++) {
    let S = m[h];
    k[O++] = S;
  }
  for (let h = 0;h < v.length; h++) {
    let S = v[h];
    k[O++] = S;
  }
  return k.slice(0, O);
}
function QN($, Q, Z, K, q, J, U) {
  let W = [], H = Q8(Z, K.value);
  K.value += 2;
  let _ = Q8(Z, K.value);
  K.value += 2;
  let N = Q8(Z, K.value);
  K.value += 2;
  let V = Q8(Z, K.value);
  K.value += 2, W.push(255, 255), W.push(H >> 8 & 255, H & 255), W.push(_ >> 8 & 255, _ & 255), W.push(N >> 8 & 255, N & 255), W.push(V >> 8 & 255, V & 255);
  let X = true, z = false;
  while (X) {
    let G = M8($, Q.value);
    Q.value += 2;
    let Y = M8($, Q.value);
    if (Q.value += 2, W.push(G >> 8 & 255, G & 255), W.push(Y >> 8 & 255, Y & 255), G & 1)
      W.push($[Q.value++]), W.push($[Q.value++]), W.push($[Q.value++]), W.push($[Q.value++]);
    else
      W.push($[Q.value++]), W.push($[Q.value++]);
    if (G & 8)
      W.push($[Q.value++]), W.push($[Q.value++]);
    else if (G & 64)
      for (let j = 0;j < 4; j++)
        W.push($[Q.value++]);
    else if (G & 128)
      for (let j = 0;j < 8; j++)
        W.push($[Q.value++]);
    if (X = (G & 32) !== 0, G & 256)
      z = true;
  }
  if (z) {
    let G = q1(q, J);
    W.push(G >> 8 & 255, G & 255);
    for (let Y = 0;Y < G; Y++)
      W.push(q[J.value++]);
  }
  return new Uint8Array(W);
}
async function jJ($) {
  let Q = new Uint8Array($), Z = new DataView($);
  if (Z.getUint32(0, false) !== 2001684018)
    throw Error("Not a valid WOFF2 file");
  let q = Z.getUint32(4, false), J = Z.getUint16(12, false), U = Z.getUint32(20, false), W = { value: 48 }, H = r_(Q, W, J), _ = Q.slice(W.value, W.value + U), N = await a_(_), V = new Map, X = 0;
  for (let v = 0;v < H.length; v++) {
    let f = H[v], l = N.slice(X, X + f.transformLength);
    V.set(f.tag, l), X += f.transformLength;
  }
  let z = V.get("maxp"), G = V.get("head");
  if (!z || !G)
    throw Error("Missing required tables");
  let Y = M8(z, 4), j = Q8(G, 50), E = H.find((v) => v.tag === "glyf"), w = H.find((v) => v.tag === "loca");
  if (E && E.transformVersion === 0) {
    let v = V.get("glyf");
    if (!v)
      throw Error("Missing glyf table data for transform");
    let { glyf: f, loca: l } = e_(v, Y, j);
    if (V.set("glyf", f), V.set("loca", l), E.origLength = f.length, w)
      w.origLength = l.length;
  }
  let L = 12, A = J * 16, D = L + A, F = [];
  for (let v = 0;v < H.length; v++) {
    let f = H[v];
    F.push(D), D += K5(f.origLength);
  }
  let M = new Uint8Array(D), R = 2 ** Math.floor(Math.log2(J)) * 16, P = Math.floor(Math.log2(J)), T = J * 16 - R;
  X9(M, 0, q), n6(M, 4, J), n6(M, 6, R), n6(M, 8, P), n6(M, 10, T);
  let m = -1;
  for (let v = 0;v < H.length; v++) {
    let f = H[v], l = V.get(f.tag);
    if (!l)
      throw Error(`Missing table data for ${f.tag}`);
    let k = L + v * 16, O = F[v];
    if (f.tag === "head" && O !== undefined)
      m = O;
    for (let S = 0;S < 4; S++)
      M[k + S] = f.tag.charCodeAt(S);
    let h = YJ(l, 0, l.length);
    X9(M, k + 4, h), X9(M, k + 8, F[v]), X9(M, k + 12, f.origLength), M.set(l.slice(0, f.origLength), F[v]);
  }
  if (m >= 0) {
    let f = 2981146554 - YJ(M, 0, M.length) >>> 0;
    X9(M, m + 8, f);
  }
  return M.buffer;
}
var ZN = { hang: 1751215719, icfb: 1768121954, icft: 1768121972, ideo: 1768187247, idtp: 1768191088, math: 1835103336, romn: 1919905134 };
function Z0($) {
  let Q = $.uint16(), Z = $.int16(), K = { format: Q, coordinate: Z };
  if (Q === 2)
    K.referenceGlyph = $.uint16(), K.baseCoordPoint = $.uint16();
  else if (Q === 3)
    K.deviceOffset = $.uint16();
  return K;
}
function EJ($, Q) {
  if (Q === 0)
    return null;
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = Z.uint16(), J = Z.uint16(), U = null, W = null;
  if (K !== 0) {
    let _ = $.sliceFrom(Q + K);
    U = Z0(_).coordinate;
  }
  if (q !== 0) {
    let _ = $.sliceFrom(Q + q);
    W = Z0(_).coordinate;
  }
  let H = [];
  for (let _ = 0;_ < J; _++) {
    let N = Z.uint32(), V = Z.uint16(), X = Z.uint16(), z = null, G = null;
    if (V !== 0) {
      let Y = $.sliceFrom(Q + V);
      z = Z0(Y).coordinate;
    }
    if (X !== 0) {
      let Y = $.sliceFrom(Q + X);
      G = Z0(Y).coordinate;
    }
    H.push({ featureTag: N, minCoord: z, maxCoord: G });
  }
  return { minCoord: U, maxCoord: W, featMinMaxRecords: H };
}
function KN($, Q, Z) {
  if (Q === 0)
    return null;
  let K = $.sliceFrom(Q), q = K.uint16(), J = K.uint16(), U = [];
  for (let H = 0;H < J; H++)
    U.push(K.uint16());
  let W = [];
  for (let H = 0;H < U.length; H++) {
    let _ = U[H];
    if (_ !== 0) {
      let N = $.sliceFrom(Q + _);
      W.push(Z0(N).coordinate);
    } else
      W.push(0);
  }
  return { defaultBaselineIndex: q, baseCoords: W };
}
function qN($, Q, Z) {
  let K = $.sliceFrom(Q), q = K.uint16(), J = K.uint16(), U = K.uint16(), W = new Map, H = [];
  for (let V = 0;V < U; V++) {
    let X = K.uint32(), z = K.uint16();
    H.push({ tag: X, offset: z });
  }
  let _ = KN($, Q + q, Z), N = EJ($, Q + J);
  for (let V = 0;V < H.length; V++) {
    let X = H[V], z = EJ($, Q + X.offset);
    if (z)
      W.set(X.tag, z);
  }
  return { baseValues: _, defaultMinMax: N, baseLangSysRecords: W };
}
function wJ($, Q) {
  if (Q === 0)
    return null;
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = Z.uint16(), J = [];
  if (K !== 0) {
    let W = $.sliceFrom(Q + K), H = W.uint16();
    for (let _ = 0;_ < H; _++)
      J.push(W.uint32());
  }
  let U = [];
  if (q !== 0) {
    let W = $.sliceFrom(Q + q), H = W.uint16(), _ = [];
    for (let N = 0;N < H; N++) {
      let V = W.uint32(), X = W.uint16();
      _.push({ tag: V, offset: X });
    }
    for (let N = 0;N < _.length; N++) {
      let V = _[N], X = qN($, Q + q + V.offset, J);
      U.push({ scriptTag: V.tag, ...X });
    }
  }
  return { baseTagList: J, baseScriptList: U };
}
function J1($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = wJ($, K), U = wJ($, q);
  return { majorVersion: Q, minorVersion: Z, horizAxis: J, vertAxis: U };
}
function JN($, Q, Z, K = true) {
  let q = K ? $.horizAxis : $.vertAxis;
  if (!q)
    return null;
  let J = q.baseScriptList.find((W) => W.scriptTag === Q);
  if (!J?.baseValues)
    return null;
  let U = q.baseTagList.indexOf(Z);
  if (U === -1)
    return null;
  return J.baseValues.baseCoords[U] ?? null;
}
function UN($, Q, Z = true) {
  let K = Z ? $.horizAxis : $.vertAxis;
  if (!K)
    return null;
  let q = K.baseScriptList.find((H) => H.scriptTag === Q);
  if (!q?.baseValues)
    return null;
  let J = q.baseValues.defaultBaselineIndex, U = K.baseTagList[J], W = q.baseValues.baseCoords[J];
  if (U === undefined || W === undefined)
    return null;
  return { tag: U, coordinate: W };
}
function WN($, Q, Z, K = true) {
  let q = K ? $.horizAxis : $.vertAxis;
  if (!q)
    return null;
  let J = q.baseScriptList.find((U) => U.scriptTag === Q);
  if (!J)
    return null;
  if (Z !== undefined) {
    let U = J.baseLangSysRecords.get(Z);
    if (U)
      return { minCoord: U.minCoord, maxCoord: U.maxCoord };
  }
  if (J.defaultMinMax)
    return { minCoord: J.defaultMinMax.minCoord, maxCoord: J.defaultMinMax.maxCoord };
  return null;
}
var HN = { SmallMetrics: 1, SmallMetricsPng: 17, BigMetrics: 2, BigMetricsPng: 18, CompressedPng: 19 };
function LJ($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint16(), q = $.uint32(), J = [];
  for (let U = 0;U < q; U++) {
    let W = $.uint32(), H = $.uint32(), _ = $.uint32(), N = $.uint32(), V = AJ($), X = AJ($), z = $.uint16(), G = $.uint16(), Y = $.uint8(), j = $.uint8(), E = $.uint8(), w = $.int8();
    J.push({ indexSubTableArrayOffset: W, indexTablesSize: H, numberOfIndexSubTables: _, colorRef: N, hori: V, vert: X, startGlyphIndex: z, endGlyphIndex: G, ppemX: Y, ppemY: j, bitDepth: E, flags: w, indexSubTables: [] });
  }
  for (let U = 0;U < J.length; U++) {
    let W = J[U], H = $.sliceFrom(Q + W.indexSubTableArrayOffset), _ = [];
    for (let N = 0;N < W.numberOfIndexSubTables; N++)
      _.push({ firstGlyphIndex: H.uint16(), lastGlyphIndex: H.uint16(), additionalOffsetToIndexSubtable: H.uint32() });
    for (let N = 0;N < _.length; N++) {
      let V = _[N], X = _N($, Q + W.indexSubTableArrayOffset + V.additionalOffsetToIndexSubtable, V.firstGlyphIndex, V.lastGlyphIndex);
      W.indexSubTables.push(X);
    }
  }
  return { majorVersion: Z, minorVersion: K, bitmapSizes: J };
}
function AJ($) {
  return { ascender: $.int8(), descender: $.int8(), widthMax: $.uint8(), caretSlopeNumerator: $.int8(), caretSlopeDenominator: $.int8(), caretOffset: $.int8(), minOriginSB: $.int8(), minAdvanceSB: $.int8(), maxBeforeBL: $.int8(), minAfterBL: $.int8(), pad1: $.int8(), pad2: $.int8() };
}
function _N($, Q, Z, K) {
  let q = $.sliceFrom(Q), J = q.uint16(), U = q.uint16(), W = q.uint32(), H = new Map, _ = K - Z + 1;
  switch (J) {
    case 1: {
      let N = [];
      for (let V = 0;V <= _; V++)
        N.push(q.uint32());
      for (let V = 0;V < _; V++) {
        let X = N[V], z = N[V + 1];
        if (X === undefined || z === undefined)
          continue;
        if (z > X)
          H.set(Z + V, { offset: W + X, length: z - X });
      }
      break;
    }
    case 2: {
      let N = q.uint32(), V = { height: q.uint8(), width: q.uint8(), horiBearingX: q.int8(), horiBearingY: q.int8(), horiAdvance: q.uint8(), vertBearingX: q.int8(), vertBearingY: q.int8(), vertAdvance: q.uint8() };
      for (let X = 0;X < _; X++)
        H.set(Z + X, { offset: W + X * N, length: N });
      break;
    }
    case 3: {
      let N = [];
      for (let V = 0;V <= _; V++)
        N.push(q.uint16());
      for (let V = 0;V < _; V++) {
        let X = N[V], z = N[V + 1];
        if (X === undefined || z === undefined)
          continue;
        if (z > X)
          H.set(Z + V, { offset: W + X, length: z - X });
      }
      break;
    }
    case 4: {
      let N = q.uint32(), V = [];
      for (let X = 0;X <= N; X++)
        V.push({ glyphId: q.uint16(), offset: q.uint16() });
      for (let X = 0;X < N; X++) {
        let z = V[X], G = V[X + 1];
        if (z === undefined || G === undefined)
          continue;
        H.set(z.glyphId, { offset: W + z.offset, length: G.offset - z.offset });
      }
      break;
    }
    case 5: {
      let N = q.uint32();
      q.skip(8);
      let V = q.uint32();
      for (let X = 0;X < V; X++) {
        let z = q.uint16();
        H.set(z, { offset: W + X * N, length: N });
      }
      break;
    }
  }
  return { firstGlyphIndex: Z, lastGlyphIndex: K, indexFormat: J, imageFormat: U, imageDataOffset: W, glyphOffsets: H };
}
function FJ($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.bytes($.remaining);
  return { majorVersion: Q, minorVersion: Z, data: K };
}
function NN($, Q, Z, K) {
  let q = null, J = 1 / 0;
  for (let U = 0;U < $.bitmapSizes.length; U++) {
    let W = $.bitmapSizes[U];
    if (Z < W.startGlyphIndex || Z > W.endGlyphIndex)
      continue;
    let H = Math.abs(W.ppemX - K);
    if (H < J)
      J = H, q = W;
  }
  if (!q)
    return null;
  for (let U = 0;U < q.indexSubTables.length; U++) {
    let W = q.indexSubTables[U], H = W.glyphOffsets.get(Z);
    if (!H)
      continue;
    let _ = Q.data.slice(H.offset - 4, H.offset - 4 + H.length);
    return VN(_, W.imageFormat);
  }
  return null;
}
function VN($, Q) {
  if ($.length === 0)
    return null;
  let Z = 0, K;
  switch (Q) {
    case 1:
    case 2:
    case 17:
    case 18: {
      if (Q === 1 || Q === 17) {
        if (Z + 5 > $.length)
          return null;
        K = { height: $[Z++] ?? 0, width: $[Z++] ?? 0, bearingX: ($[Z++] ?? 0) << 24 >> 24, bearingY: ($[Z++] ?? 0) << 24 >> 24, advance: $[Z++] ?? 0 };
      } else {
        if (Z + 8 > $.length)
          return null;
        K = { height: $[Z++] ?? 0, width: $[Z++] ?? 0, bearingX: ($[Z++] ?? 0) << 24 >> 24, bearingY: ($[Z++] ?? 0) << 24 >> 24, advance: $[Z++] ?? 0 }, Z += 3;
      }
      break;
    }
    case 19: {
      K = { height: 0, width: 0, bearingX: 0, bearingY: 0, advance: 0 };
      break;
    }
    default:
      return null;
  }
  return { metrics: K, imageFormat: Q, data: $.slice(Z) };
}
function XN($, Q, Z) {
  for (let K = 0;K < $.bitmapSizes.length; K++) {
    let q = $.bitmapSizes[K];
    if (Z !== undefined && q.ppemX !== Z)
      continue;
    if (Q < q.startGlyphIndex || Q > q.endGlyphIndex)
      continue;
    for (let J = 0;J < q.indexSubTables.length; J++)
      if (q.indexSubTables[J].glyphOffsets.has(Q))
        return true;
  }
  return false;
}
function zN($) {
  let Q = new Set;
  for (let Z = 0;Z < $.bitmapSizes.length; Z++) {
    let K = $.bitmapSizes[Z];
    Q.add(K.ppemX);
  }
  return [...Q].sort((Z, K) => Z - K);
}
var G9 = [".notdef", "space", "exclam", "quotedbl", "numbersign", "dollar", "percent", "ampersand", "quoteright", "parenleft", "parenright", "asterisk", "plus", "comma", "hyphen", "period", "slash", "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "colon", "semicolon", "less", "equal", "greater", "question", "at", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "bracketleft", "backslash", "bracketright", "asciicircum", "underscore", "quoteleft", "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "braceleft", "bar", "braceright", "asciitilde", "exclamdown", "cent", "sterling", "fraction", "yen", "florin", "section", "currency", "quotesingle", "quotedblleft", "guillemotleft", "guilsinglleft", "guilsinglright", "fi", "fl", "endash", "dagger", "daggerdbl", "periodcentered", "paragraph", "bullet", "quotesinglbase", "quotedblbase", "quotedblright", "guillemotright", "ellipsis", "perthousand", "questiondown", "grave", "acute", "circumflex", "tilde", "macron", "breve", "dotaccent", "dieresis", "ring", "cedilla", "hungarumlaut", "ogonek", "caron", "emdash", "AE", "ordfeminine", "Lslash", "Oslash", "OE", "ordmasculine", "ae", "dotlessi", "lslash", "oslash", "oe", "germandbls", "onesuperior", "logicalnot", "mu", "trademark", "Eth", "onehalf", "plusminus", "Thorn", "onequarter", "divide", "brokenbar", "degree", "thorn", "threequarters", "twosuperior", "registered", "minus", "eth", "multiply", "threesuperior", "copyright", "Aacute", "Acircumflex", "Adieresis", "Agrave", "Aring", "Atilde", "Ccedilla", "Eacute", "Ecircumflex", "Edieresis", "Egrave", "Iacute", "Icircumflex", "Idieresis", "Igrave", "Ntilde", "Oacute", "Ocircumflex", "Odieresis", "Ograve", "Otilde", "Scaron", "Uacute", "Ucircumflex", "Udieresis", "Ugrave", "Yacute", "Ydieresis", "Zcaron", "aacute", "acircumflex", "adieresis", "agrave", "aring", "atilde", "ccedilla", "eacute", "ecircumflex", "edieresis", "egrave", "iacute", "icircumflex", "idieresis", "igrave", "ntilde", "oacute", "ocircumflex", "odieresis", "ograve", "otilde", "scaron", "uacute", "ucircumflex", "udieresis", "ugrave", "yacute", "ydieresis", "zcaron", "exclamsmall", "Hungarumlautsmall", "dollaroldstyle", "dollarsuperior", "ampersandsmall", "Acutesmall", "parenleftsuperior", "parenrightsuperior", "twodotenleader", "onedotenleader", "zerooldstyle", "oneoldstyle", "twooldstyle", "threeoldstyle", "fouroldstyle", "fiveoldstyle", "sixoldstyle", "sevenoldstyle", "eightoldstyle", "nineoldstyle", "commasuperior", "threequartersemdash", "periodsuperior", "questionsmall", "asuperior", "bsuperior", "centsuperior", "dsuperior", "esuperior", "isuperior", "lsuperior", "msuperior", "nsuperior", "osuperior", "rsuperior", "ssuperior", "tsuperior", "ff", "ffi", "ffl", "parenleftinferior", "parenrightinferior", "Circumflexsmall", "hyphensuperior", "Gravesmall", "Asmall", "Bsmall", "Csmall", "Dsmall", "Esmall", "Fsmall", "Gsmall", "Hsmall", "Ismall", "Jsmall", "Ksmall", "Lsmall", "Msmall", "Nsmall", "Osmall", "Psmall", "Qsmall", "Rsmall", "Ssmall", "Tsmall", "Usmall", "Vsmall", "Wsmall", "Xsmall", "Ysmall", "Zsmall", "colonmonetary", "onefitted", "rupiah", "Tildesmall", "exclamdownsmall", "centoldstyle", "Lslashsmall", "Scaronsmall", "Zcaronsmall", "Dieresissmall", "Brevesmall", "Caronsmall", "Dotaccentsmall", "Macronsmall", "figuredash", "hypheninferior", "Ogoneksmall", "Ringsmall", "Cedillasmall", "questiondownsmall", "oneeighth", "threeeighths", "fiveeighths", "seveneighths", "onethird", "twothirds", "zerosuperior", "foursuperior", "fivesuperior", "sixsuperior", "sevensuperior", "eightsuperior", "ninesuperior", "zeroinferior", "oneinferior", "twoinferior", "threeinferior", "fourinferior", "fiveinferior", "sixinferior", "seveninferior", "eightinferior", "nineinferior", "centinferior", "dollarinferior", "periodinferior", "commainferior", "Agravesmall", "Aacutesmall", "Acircumflexsmall", "Atildesmall", "Adieresissmall", "Aringsmall", "AEsmall", "Ccedillasmall", "Egravesmall", "Eacutesmall", "Ecircumflexsmall", "Edieresissmall", "Igravesmall", "Iacutesmall", "Icircumflexsmall", "Idieresissmall", "Ethsmall", "Ntildesmall", "Ogravesmall", "Oacutesmall", "Ocircumflexsmall", "Otildesmall", "Odieresissmall", "OEsmall", "Oslashsmall", "Ugravesmall", "Uacutesmall", "Ucircumflexsmall", "Udieresissmall", "Yacutesmall", "Thornsmall", "Ydieresissmall", "001.000", "001.001", "001.002", "001.003", "Black", "Bold", "Book", "Light", "Medium", "Regular", "Roman", "Semibold"];
function U1($) {
  let Q = $.offset, Z = $.uint8(), K = $.uint8(), q = $.uint8(), J = $.uint8();
  $.seek(Q + q);
  let U = T8($).map((j) => new TextDecoder().decode(j)), W = T8($), H = [], N = T8($).map((j) => new TextDecoder().decode(j)), V = T8($);
  for (let j = 0;j < W.length; j++) {
    let E = W[j];
    H.push(DJ(new D6(E.buffer, E.byteOffset, E.byteLength), N));
  }
  let X = [], z = [], G = [], Y = [];
  for (let j = 0;j < H.length; j++) {
    let E = H[j];
    if (E.charStrings !== undefined)
      $.seek(Q + E.charStrings), X.push(T8($));
    else
      X.push([]);
    if (E.private) {
      let [w, L] = E.private, A = BJ($.slice(L, w), N);
      if (A.subrs !== undefined)
        $.seek(Q + L + A.subrs), z.push(T8($));
      else
        z.push([]);
    } else
      z.push([]);
    if (E.fdArray !== undefined) {
      $.seek(Q + E.fdArray);
      let w = T8($), L = [];
      for (let A = 0;A < w.length; A++) {
        let D = w[A], F = DJ(new D6(D.buffer, D.byteOffset, D.byteLength), N);
        if (F.private) {
          let [M, R] = F.private, P = BJ($.slice(Q + R, M), N);
          if (Object.assign(F, P), P.subrs !== undefined)
            $.seek(Q + R + P.subrs), F.localSubrs = T8($);
        }
        L.push(F);
      }
      G.push(L);
    } else
      G.push([]);
    if (E.fdSelect !== undefined) {
      $.seek(Q + E.fdSelect);
      let w = X[X.length - 1];
      Y.push(jN($, w?.length ?? 0));
    } else
      Y.push({ format: 0, select: () => 0 });
  }
  return { version: { major: Z, minor: K }, names: U, topDicts: H, strings: N, globalSubrs: V, charStrings: X, localSubrs: z, fdArrays: G, fdSelects: Y };
}
function T8($) {
  let Q = $.uint16();
  if (Q === 0)
    return [];
  let Z = $.uint8(), K = [];
  for (let J = 0;J <= Q; J++)
    K.push(GN($, Z));
  let q = [];
  for (let J = 0;J < Q; J++) {
    let U = K[J], W = K[J + 1];
    if (U === undefined || W === undefined)
      continue;
    let H = W - U;
    q.push($.bytes(H));
  }
  return q;
}
function GN($, Q) {
  switch (Q) {
    case 1:
      return $.uint8();
    case 2:
      return $.uint16();
    case 3:
      return $.uint24();
    case 4:
      return $.uint32();
    default:
      throw Error(`Invalid offset size: ${Q}`);
  }
}
function RJ($) {
  let Q = new Map, Z = [];
  while ($.remaining > 0) {
    let K = $.uint8();
    if (K <= 21) {
      let q = K;
      if (K === 12)
        q = 3072 | $.uint8();
      Q.set(q, [...Z]), Z.length = 0;
    } else if (K === 28)
      Z.push($.int16());
    else if (K === 29)
      Z.push($.int32());
    else if (K === 30)
      Z.push(YN($));
    else if (K >= 32 && K <= 246)
      Z.push(K - 139);
    else if (K >= 247 && K <= 250) {
      let q = $.uint8();
      Z.push((K - 247) * 256 + q + 108);
    } else if (K >= 251 && K <= 254) {
      let q = $.uint8();
      Z.push(-(K - 251) * 256 - q - 108);
    }
  }
  return Q;
}
function YN($) {
  let Q = "", Z = "0123456789.EE -", K = false;
  while (!K) {
    let q = $.uint8();
    for (let J = 0;J < 2; J++) {
      let U = J === 0 ? q >> 4 : q & 15;
      if (U === 15) {
        K = true;
        break;
      }
      if (U === 12)
        Q += "E-";
      else
        Q += Z[U];
    }
  }
  return parseFloat(Q);
}
function DJ($, Q) {
  let Z = RJ($), K = {}, q = (U) => {
    if (U < G9.length)
      return G9[U] ?? "";
    return Q[U - G9.length] ?? "";
  }, J = [...Z];
  for (let U = 0;U < J.length; U++) {
    let [W, H] = J[U], _ = H[0], N = H[1], V = H[2];
    switch (W) {
      case 0:
        if (_ !== undefined)
          K.version = q(_);
        break;
      case 1:
        if (_ !== undefined)
          K.notice = q(_);
        break;
      case 3072:
        if (_ !== undefined)
          K.copyright = q(_);
        break;
      case 2:
        if (_ !== undefined)
          K.fullName = q(_);
        break;
      case 3:
        if (_ !== undefined)
          K.familyName = q(_);
        break;
      case 4:
        if (_ !== undefined)
          K.weight = q(_);
        break;
      case 3073:
        K.isFixedPitch = _ !== 0;
        break;
      case 3074:
        K.italicAngle = _;
        break;
      case 3075:
        K.underlinePosition = _;
        break;
      case 3076:
        K.underlineThickness = _;
        break;
      case 3077:
        K.paintType = _;
        break;
      case 3078:
        K.charstringType = _;
        break;
      case 3079:
        K.fontMatrix = H;
        break;
      case 13:
        K.uniqueID = _;
        break;
      case 5:
        K.fontBBox = H;
        break;
      case 3080:
        K.strokeWidth = _;
        break;
      case 15:
        K.charset = _;
        break;
      case 16:
        K.encoding = _;
        break;
      case 17:
        K.charStrings = _;
        break;
      case 18:
        if (_ !== undefined && N !== undefined)
          K.private = [_, N];
        break;
      case 3092:
        K.syntheticBase = _;
        break;
      case 3093:
        if (_ !== undefined)
          K.postScript = q(_);
        break;
      case 3094:
        if (_ !== undefined)
          K.baseFontName = q(_);
        break;
      case 3095:
        K.baseFontBlend = H;
        break;
      case 3102:
        if (_ !== undefined && N !== undefined && V !== undefined)
          K.ros = { registry: q(_), ordering: q(N), supplement: V };
        break;
      case 3103:
        K.cidFontVersion = _;
        break;
      case 3104:
        K.cidFontRevision = _;
        break;
      case 3105:
        K.cidFontType = _;
        break;
      case 3106:
        K.cidCount = _;
        break;
      case 3107:
        K.uidBase = _;
        break;
      case 3108:
        K.fdArray = _;
        break;
      case 3109:
        K.fdSelect = _;
        break;
      case 3110:
        if (_ !== undefined)
          K.fontName = q(_);
        break;
    }
  }
  return K;
}
function BJ($, Q) {
  let Z = RJ($), K = {}, q = [...Z];
  for (let J = 0;J < q.length; J++) {
    let [U, W] = q[J], H = W[0];
    switch (U) {
      case 6:
        K.blueValues = z9(W);
        break;
      case 7:
        K.otherBlues = z9(W);
        break;
      case 8:
        K.familyBlues = z9(W);
        break;
      case 9:
        K.familyOtherBlues = z9(W);
        break;
      case 3081:
        K.blueScale = H;
        break;
      case 3082:
        K.blueShift = H;
        break;
      case 3083:
        K.blueFuzz = H;
        break;
      case 10:
        K.stdHW = H;
        break;
      case 11:
        K.stdVW = H;
        break;
      case 3084:
        K.stemSnapH = z9(W);
        break;
      case 3085:
        K.stemSnapV = z9(W);
        break;
      case 3086:
        K.forceBold = H !== 0;
        break;
      case 3089:
        K.languageGroup = H;
        break;
      case 3090:
        K.expansionFactor = H;
        break;
      case 3091:
        K.initialRandomSeed = H;
        break;
      case 19:
        K.subrs = H;
        break;
      case 20:
        K.defaultWidthX = H;
        break;
      case 21:
        K.nominalWidthX = H;
        break;
    }
  }
  return K;
}
function z9($) {
  let Q = [], Z = 0;
  for (let K = 0;K < $.length; K++) {
    let q = $[K];
    Z += q, Q.push(Z);
  }
  return Q;
}
function jN($, Q) {
  let Z = $.uint8();
  if (Z === 0) {
    let K = $.uint8Array(Q);
    return { format: Z, select: (q) => K[q] ?? 0 };
  } else if (Z === 3) {
    let K = $.uint16(), q = [];
    for (let U = 0;U < K; U++)
      q.push({ first: $.uint16(), fd: $.uint8() });
    let J = $.uint16();
    return { format: Z, select: (U) => {
      let W = 0, H = q.length - 1;
      while (W < H) {
        let N = Math.ceil((W + H) / 2), V = q[N];
        if (V && V.first <= U)
          W = N;
        else
          H = N - 1;
      }
      return q[W]?.fd ?? 0;
    } };
  }
  return { format: Z, select: () => 0 };
}
function EN($, Q) {
  if (Q < G9.length)
    return G9[Q] ?? "";
  return $.strings[Q - G9.length] ?? "";
}
function K6($) {
  return $.stack.length - $.stackPos;
}
function x($) {
  if ($.stackPos >= $.stack.length)
    return;
  return $.stack[$.stackPos++];
}
function Y9($) {
  $.stack.length = 0, $.stackPos = 0;
}
function W1($, Q, Z = 0) {
  let K = $.charStrings[Z];
  if (!K || Q >= K.length)
    return null;
  let q = K[Q];
  if (!q)
    return null;
  let J = $.globalSubrs, W = $.topDicts[Z]?.ros !== undefined, H = [];
  if (W && $.fdSelects[Z] && $.fdArrays[Z]) {
    let N = $.fdSelects[Z]?.select(Q) ?? 0;
    H = $.fdArrays[Z]?.[N]?.localSubrs || $.localSubrs[Z] || [];
  } else
    H = $.localSubrs[Z] || [];
  let _ = { x: 0, y: 0, stack: [], stackPos: 0, nStems: 0, haveWidth: false, width: 0, contours: [], currentContour: [], transientArray: Array(32).fill(0), callStack: [], vsindex: 0, axisCoords: null, vstore: null };
  if (q5(_, q, J, H), _.currentContour.length > 0)
    _.contours.push(_.currentContour);
  return _.contours;
}
function J5($, Q, Z = null) {
  if (Q >= $.charStrings.length)
    return null;
  let K = $.charStrings[Q];
  if (!K)
    return null;
  let q = $.globalSubrs, J = $.fdSelect?.select(Q) ?? 0, U = $.fdArray[J], W = U?.localSubrs || [], H = { x: 0, y: 0, stack: [], stackPos: 0, nStems: 0, haveWidth: true, width: 0, contours: [], currentContour: [], transientArray: Array(32).fill(0), callStack: [], vsindex: U?.private?.vsindex ?? 0, axisCoords: Z, vstore: $.vstore };
  if (q5(H, K, q, W), H.currentContour.length > 0)
    H.contours.push(H.currentContour);
  return H.contours;
}
function q5($, Q, Z, K) {
  let q = 0;
  while (q < Q.length) {
    let J = Q[q++];
    if (J === undefined)
      return;
    if (J === 28) {
      let U = Q[q++];
      if (U === undefined)
        return;
      let W = Q[q++];
      if (W === undefined)
        return;
      $.stack.push((U << 8 | W) << 16 >> 16);
    } else if (J === 255) {
      let U = Q[q++];
      if (U === undefined)
        return;
      let W = Q[q++];
      if (W === undefined)
        return;
      let H = Q[q++];
      if (H === undefined)
        return;
      let _ = Q[q++];
      if (_ === undefined)
        return;
      let N = (U << 24 | W << 16 | H << 8 | _) >> 0;
      $.stack.push(N / 65536);
    } else if (J >= 32 && J <= 246)
      $.stack.push(J - 139);
    else if (J >= 247 && J <= 250) {
      let U = Q[q++];
      if (U === undefined)
        return;
      $.stack.push((J - 247) * 256 + U + 108);
    } else if (J >= 251 && J <= 254) {
      let U = Q[q++];
      if (U === undefined)
        return;
      $.stack.push(-(J - 251) * 256 - U - 108);
    } else if (J === 12) {
      let U = Q[q++];
      if (U === undefined)
        return;
      let W = 3072 | U;
      MJ($, W, Z, K);
    } else if (J === 19 || J === 20) {
      if (K6($) % 2 !== 0 && !$.haveWidth) {
        let _ = x($);
        if (_ !== undefined)
          $.width = _, $.haveWidth = true;
      }
      $.nStems += K6($) / 2, Y9($);
      let H = Math.ceil($.nStems / 8);
      q += H;
    } else
      MJ($, J, Z, K);
    if ($.callStack.length > 0) {
      let U = $.callStack[$.callStack.length - 1];
      if (U && U.pos >= U.data.length)
        $.callStack.pop();
    }
  }
}
function MJ($, Q, Z, K) {
  let q = $.stack;
  switch (Q) {
    case 1:
    case 3:
    case 18:
    case 23: {
      if (K6($) % 2 !== 0 && !$.haveWidth) {
        let W = x($);
        if (W === undefined)
          break;
        $.width = W, $.haveWidth = true;
      }
      $.nStems += K6($) / 2, Y9($);
      break;
    }
    case 19:
    case 20:
      break;
    case 21: {
      if (K6($) > 2 && !$.haveWidth) {
        let W = x($);
        if (W === undefined)
          break;
        $.width = W, $.haveWidth = true;
      }
      if ($.currentContour.length > 0)
        $.contours.push($.currentContour), $.currentContour = [];
      let J = x($);
      if (J === undefined)
        break;
      let U = x($);
      if (U === undefined)
        break;
      $.x += J, $.y += U, $.currentContour.push({ x: $.x, y: $.y, onCurve: true }), Y9($);
      break;
    }
    case 22: {
      if (K6($) > 1 && !$.haveWidth) {
        let U = x($);
        if (U === undefined)
          break;
        $.width = U, $.haveWidth = true;
      }
      if ($.currentContour.length > 0)
        $.contours.push($.currentContour), $.currentContour = [];
      let J = x($);
      if (J === undefined)
        break;
      $.x += J, $.currentContour.push({ x: $.x, y: $.y, onCurve: true }), Y9($);
      break;
    }
    case 4: {
      if (K6($) > 1 && !$.haveWidth) {
        let U = x($);
        if (U === undefined)
          break;
        $.width = U, $.haveWidth = true;
      }
      if ($.currentContour.length > 0)
        $.contours.push($.currentContour), $.currentContour = [];
      let J = x($);
      if (J === undefined)
        break;
      $.y += J, $.currentContour.push({ x: $.x, y: $.y, onCurve: true }), Y9($);
      break;
    }
    case 5: {
      while (K6($) >= 2) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        $.x += J, $.y += U, $.currentContour.push({ x: $.x, y: $.y, onCurve: true });
      }
      break;
    }
    case 6: {
      let J = true;
      while (K6($) >= 1) {
        let U = x($);
        if (U === undefined)
          break;
        if (J)
          $.x += U;
        else
          $.y += U;
        $.currentContour.push({ x: $.x, y: $.y, onCurve: true }), J = !J;
      }
      break;
    }
    case 7: {
      let J = true;
      while (K6($) >= 1) {
        let U = x($);
        if (U === undefined)
          break;
        if (J)
          $.y += U;
        else
          $.x += U;
        $.currentContour.push({ x: $.x, y: $.y, onCurve: true }), J = !J;
      }
      break;
    }
    case 8: {
      while (K6($) >= 6) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        B6($, J, U, W, H, _, N);
      }
      break;
    }
    case 27: {
      let J = 0;
      if (K6($) % 4 === 1) {
        let U = x($);
        if (U === undefined)
          break;
        J = U;
      }
      while (K6($) >= 4) {
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        B6($, U, J, W, H, _, 0), J = 0;
      }
      break;
    }
    case 26: {
      let J = 0;
      if (K6($) % 4 === 1) {
        let U = x($);
        if (U === undefined)
          break;
        J = U;
      }
      while (K6($) >= 4) {
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        B6($, J, U, W, H, 0, _), J = 0;
      }
      break;
    }
    case 31: {
      let J = true;
      while (K6($) >= 4) {
        if (J) {
          let U = x($);
          if (U === undefined)
            break;
          let W = x($);
          if (W === undefined)
            break;
          let H = x($);
          if (H === undefined)
            break;
          let _ = x($);
          if (_ === undefined)
            break;
          let N = K6($) === 1 ? x($) ?? 0 : 0;
          B6($, U, 0, W, H, N, _);
        } else {
          let U = x($);
          if (U === undefined)
            break;
          let W = x($);
          if (W === undefined)
            break;
          let H = x($);
          if (H === undefined)
            break;
          let _ = x($);
          if (_ === undefined)
            break;
          let N = K6($) === 1 ? x($) ?? 0 : 0;
          B6($, 0, U, W, H, _, N);
        }
        J = !J;
      }
      break;
    }
    case 30: {
      let J = true;
      while (K6($) >= 4) {
        if (J) {
          let U = x($);
          if (U === undefined)
            break;
          let W = x($);
          if (W === undefined)
            break;
          let H = x($);
          if (H === undefined)
            break;
          let _ = x($);
          if (_ === undefined)
            break;
          let N = K6($) === 1 ? x($) ?? 0 : 0;
          B6($, 0, U, W, H, _, N);
        } else {
          let U = x($);
          if (U === undefined)
            break;
          let W = x($);
          if (W === undefined)
            break;
          let H = x($);
          if (H === undefined)
            break;
          let _ = x($);
          if (_ === undefined)
            break;
          let N = K6($) === 1 ? x($) ?? 0 : 0;
          B6($, U, 0, W, H, N, _);
        }
        J = !J;
      }
      break;
    }
    case 24: {
      while (K6($) >= 8) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        B6($, J, U, W, H, _, N);
      }
      if (K6($) >= 2) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        $.x += J, $.y += U, $.currentContour.push({ x: $.x, y: $.y, onCurve: true });
      }
      break;
    }
    case 25: {
      while (K6($) >= 8) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        $.x += J, $.y += U, $.currentContour.push({ x: $.x, y: $.y, onCurve: true });
      }
      if (K6($) >= 6) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        B6($, J, U, W, H, _, N);
      }
      break;
    }
    case 10: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = J + TJ(K.length), W = K[U];
      if (W)
        q5($, W, Z, K);
      break;
    }
    case 29: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = J + TJ(Z.length), W = Z[U];
      if (W)
        q5($, W, Z, K);
      break;
    }
    case 11:
      break;
    case 14: {
      if (K6($) > 0 && !$.haveWidth) {
        let J = x($);
        if (J === undefined)
          break;
        $.width = J, $.haveWidth = true;
      }
      if ($.currentContour.length > 0)
        $.contours.push($.currentContour), $.currentContour = [];
      Y9($);
      break;
    }
    case 3107: {
      if (K6($) >= 13) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        let V = x($);
        if (V === undefined)
          break;
        let X = x($);
        if (X === undefined)
          break;
        let z = x($);
        if (z === undefined)
          break;
        let G = x($);
        if (G === undefined)
          break;
        let Y = x($);
        if (Y === undefined)
          break;
        let j = x($);
        if (j === undefined)
          break;
        x($), B6($, J, U, W, H, _, N), B6($, V, X, z, G, Y, j);
      }
      break;
    }
    case 3106: {
      if (K6($) >= 7) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        let V = x($);
        if (V === undefined)
          break;
        B6($, J, 0, U, W, H, 0), B6($, _, 0, N, -W, V, 0);
      }
      break;
    }
    case 3108: {
      if (K6($) >= 9) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        let V = x($);
        if (V === undefined)
          break;
        let X = x($);
        if (X === undefined)
          break;
        let z = x($);
        if (z === undefined)
          break;
        B6($, J, U, W, H, _, 0), B6($, N, 0, V, X, z, -(U + H + X));
      }
      break;
    }
    case 3109: {
      if (K6($) >= 11) {
        let J = x($);
        if (J === undefined)
          break;
        let U = x($);
        if (U === undefined)
          break;
        let W = x($);
        if (W === undefined)
          break;
        let H = x($);
        if (H === undefined)
          break;
        let _ = x($);
        if (_ === undefined)
          break;
        let N = x($);
        if (N === undefined)
          break;
        let V = x($);
        if (V === undefined)
          break;
        let X = x($);
        if (X === undefined)
          break;
        let z = x($);
        if (z === undefined)
          break;
        let G = x($);
        if (G === undefined)
          break;
        let Y = x($);
        if (Y === undefined)
          break;
        let j = J + W + _ + V + z, E = U + H + N + X + G, w, L;
        if (Math.abs(j) > Math.abs(E))
          w = Y, L = -E;
        else
          w = -j, L = Y;
        B6($, J, U, W, H, _, N), B6($, V, X, z, G, w, L);
      }
      break;
    }
    case 3075: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U && J ? 1 : 0);
      break;
    }
    case 3076: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U || J ? 1 : 0);
      break;
    }
    case 3077: {
      let J = q.pop();
      if (J === undefined)
        break;
      q.push(J ? 0 : 1);
      break;
    }
    case 3081: {
      let J = q.pop();
      if (J === undefined)
        break;
      q.push(Math.abs(J));
      break;
    }
    case 3082: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U + J);
      break;
    }
    case 3083: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U - J);
      break;
    }
    case 3084: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U / J);
      break;
    }
    case 3086: {
      let J = q.pop();
      if (J === undefined)
        break;
      q.push(-J);
      break;
    }
    case 3087: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U === J ? 1 : 0);
      break;
    }
    case 3090: {
      q.pop();
      break;
    }
    case 3092: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      $.transientArray[J] = U;
      break;
    }
    case 3093: {
      let J = q.pop();
      if (J === undefined)
        break;
      q.push($.transientArray[J] ?? 0);
      break;
    }
    case 3094: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      let W = q.pop();
      if (W === undefined)
        break;
      let H = q.pop();
      if (H === undefined)
        break;
      q.push(U <= J ? H : W);
      break;
    }
    case 3095: {
      q.push(Math.random());
      break;
    }
    case 3096: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(U * J);
      break;
    }
    case 3098: {
      let J = q.pop();
      if (J === undefined)
        break;
      q.push(Math.sqrt(J));
      break;
    }
    case 3099: {
      let J = q[q.length - 1];
      if (J === undefined)
        break;
      q.push(J);
      break;
    }
    case 3100: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      q.push(J, U);
      break;
    }
    case 3101: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.length - 1 - J;
      q.push(q[U] ?? 0);
      break;
    }
    case 3102: {
      let J = q.pop();
      if (J === undefined)
        break;
      let U = q.pop();
      if (U === undefined)
        break;
      if (U > 0) {
        let W = q.splice(-U), H = (J % U + U) % U;
        for (let _ = 0;_ < U; _++) {
          let N = W[(_ + H) % U];
          if (N !== undefined)
            q.push(N);
        }
      }
      break;
    }
    case 16: {
      if (!$.axisCoords || !$.vstore)
        break;
      let J = q.pop();
      if (J === undefined)
        break;
      let U = $.vstore.itemVariationData[$.vsindex]?.regionIndexCount ?? 0, W = J * U, H = q.splice(-W), _ = q.splice(-J);
      for (let N = 0;N < J; N++) {
        let V = _[N];
        if (V === undefined)
          continue;
        let X = V;
        for (let z = 0;z < U; z++) {
          let G = H[N * U + z];
          if (G === undefined)
            continue;
          let Y = wN($.vstore, $.vsindex, z, $.axisCoords);
          X += G * Y;
        }
        q.push(X);
      }
      break;
    }
    case 15: {
      let J = q.pop();
      if (J === undefined)
        break;
      $.vsindex = J;
      break;
    }
    case 3072:
      break;
  }
}
function B6($, Q, Z, K, q, J, U) {
  let { x: W, y: H } = $, _ = W + Q, N = H + Z, V = _ + K, X = N + q, z = V + J, G = X + U;
  $.currentContour.push({ x: _, y: N, onCurve: false, cubic: true }), $.currentContour.push({ x: V, y: X, onCurve: false, cubic: true }), $.currentContour.push({ x: z, y: G, onCurve: true }), $.x = z, $.y = G;
}
function TJ($) {
  if ($ < 1240)
    return 107;
  if ($ < 33900)
    return 1131;
  return 32768;
}
function wN($, Q, Z, K) {
  let q = $.itemVariationData[Q];
  if (!q)
    return 0;
  let J = q.regionIndexes[Z];
  if (J === undefined)
    return 0;
  let U = $.variationRegionList.regions[J];
  if (!U)
    return 0;
  let W = 1;
  for (let H = 0;H < U.axes.length && H < K.length; H++) {
    let _ = U.axes[H];
    if (!_)
      continue;
    let N = K[H];
    if (N === undefined)
      continue;
    if (N < _.startCoord || N > _.endCoord)
      return 0;
    if (N === _.peakCoord)
      continue;
    if (N < _.peakCoord)
      W *= (N - _.startCoord) / (_.peakCoord - _.startCoord);
    else
      W *= (_.endCoord - N) / (_.endCoord - _.peakCoord);
  }
  return W;
}
function AN($, Q, Z = 0) {
  let K = $.topDicts[Z];
  return 0;
}
function H1($) {
  let Q = $.offset, Z = $.uint8(), K = $.uint8(), q = $.uint8(), J = $.uint16();
  $.seek(Q + q);
  let U = $.slice($.offset - Q, J);
  $.skip(J);
  let W = DN(U), H = U5($), _ = [];
  if (W.charStrings !== undefined)
    $.seek(Q + W.charStrings), _ = U5($);
  let N = [];
  if (W.fdArray !== undefined) {
    $.seek(Q + W.fdArray);
    let z = U5($);
    for (let G = 0;G < z.length; G++) {
      let Y = z[G], j = BN(new D6(Y.buffer, Y.byteOffset, Y.byteLength));
      if (j.privateOffset !== undefined && j.privateSize !== undefined) {
        $.seek(Q + j.privateOffset);
        let E = $.slice(0, j.privateSize);
        if (j.private = RN(E), j.private.subrs !== undefined)
          $.seek(Q + j.privateOffset + j.private.subrs), j.localSubrs = U5($);
      }
      N.push(j);
    }
  }
  let V = null;
  if (W.fdSelect !== undefined)
    $.seek(Q + W.fdSelect), V = MN($, _.length);
  let X = null;
  if (W.vstore !== undefined)
    $.seek(Q + W.vstore), X = TN($);
  return { version: { major: Z, minor: K }, topDict: W, globalSubrs: H, charStrings: _, fdArray: N, fdSelect: V, vstore: X };
}
function U5($) {
  let Q = $.uint32();
  if (Q === 0)
    return [];
  let Z = $.uint8(), K = [];
  for (let J = 0;J <= Q; J++)
    K.push(LN($, Z));
  let q = [];
  for (let J = 0;J < Q; J++) {
    let U = K[J], W = K[J + 1];
    if (U === undefined || W === undefined)
      continue;
    let H = W - U;
    q.push($.bytes(H));
  }
  return q;
}
function LN($, Q) {
  switch (Q) {
    case 1:
      return $.uint8();
    case 2:
      return $.uint16();
    case 3:
      return $.uint24();
    case 4:
      return $.uint32();
    default:
      throw Error(`Invalid offset size: ${Q}`);
  }
}
function _1($) {
  let Q = new Map, Z = [];
  while ($.remaining > 0) {
    let K = $.uint8();
    if (K <= 21) {
      let q = K;
      if (K === 12)
        q = 3072 | $.uint8();
      Q.set(q, [...Z]), Z.length = 0;
    } else if (K === 22)
      Q.set(22, [...Z]), Z.length = 0;
    else if (K === 23)
      Q.set(23, [...Z]), Z.length = 0;
    else if (K === 24)
      Q.set(24, [...Z]), Z.length = 0;
    else if (K === 28)
      Z.push($.int16());
    else if (K === 29)
      Z.push($.int32());
    else if (K === 30)
      Z.push(FN($));
    else if (K >= 32 && K <= 246)
      Z.push(K - 139);
    else if (K >= 247 && K <= 250) {
      let q = $.uint8();
      Z.push((K - 247) * 256 + q + 108);
    } else if (K >= 251 && K <= 254) {
      let q = $.uint8();
      Z.push(-(K - 251) * 256 - q - 108);
    }
  }
  return Q;
}
function FN($) {
  let Q = "", Z = "0123456789.EE -", K = false;
  while (!K) {
    let q = $.uint8();
    for (let J = 0;J < 2; J++) {
      let U = J === 0 ? q >> 4 : q & 15;
      if (U === 15) {
        K = true;
        break;
      }
      if (U === 12)
        Q += "E-";
      else {
        let W = Z[U];
        if (W !== undefined)
          Q += W;
      }
    }
  }
  return parseFloat(Q);
}
function DN($) {
  let Q = _1($), Z = {}, K = [...Q];
  for (let q = 0;q < K.length; q++) {
    let [J, U] = K[q];
    switch (J) {
      case 3079:
        Z.fontMatrix = U;
        break;
      case 17:
        Z.charStrings = U[0];
        break;
      case 3108:
        Z.fdArray = U[0];
        break;
      case 3109:
        Z.fdSelect = U[0];
        break;
      case 24:
        Z.vstore = U[0];
        break;
    }
  }
  return Z;
}
function BN($) {
  let Q = _1($), Z = {}, K = Q.get(18);
  if (K && K.length >= 2)
    Z.privateSize = K[0], Z.privateOffset = K[1];
  return Z;
}
function RN($) {
  let Q = _1($), Z = {}, K = [...Q];
  for (let q = 0;q < K.length; q++) {
    let [J, U] = K[q], W = U[0];
    switch (J) {
      case 6:
        Z.blueValues = j9(U);
        break;
      case 7:
        Z.otherBlues = j9(U);
        break;
      case 8:
        Z.familyBlues = j9(U);
        break;
      case 9:
        Z.familyOtherBlues = j9(U);
        break;
      case 3081:
        Z.blueScale = W;
        break;
      case 3082:
        Z.blueShift = W;
        break;
      case 3083:
        Z.blueFuzz = W;
        break;
      case 10:
        Z.stdHW = W;
        break;
      case 11:
        Z.stdVW = W;
        break;
      case 3084:
        Z.stemSnapH = j9(U);
        break;
      case 3085:
        Z.stemSnapV = j9(U);
        break;
      case 3089:
        Z.languageGroup = W;
        break;
      case 3090:
        Z.expansionFactor = W;
        break;
      case 19:
        Z.subrs = W;
        break;
      case 22:
        Z.vsindex = W;
        break;
      case 23:
        Z.blend = U;
        break;
    }
  }
  return Z;
}
function j9($) {
  let Q = [], Z = 0;
  for (let K = 0;K < $.length; K++) {
    let q = $[K];
    Z += q, Q.push(Z);
  }
  return Q;
}
function MN($, Q) {
  let Z = $.uint8();
  if (Z === 0) {
    let K = $.uint8Array(Q);
    return { format: Z, select: (q) => K[q] ?? 0 };
  } else if (Z === 3) {
    let K = $.uint16(), q = [];
    for (let J = 0;J < K; J++)
      q.push({ first: $.uint16(), fd: $.uint8() });
    return $.uint16(), { format: Z, select: (J) => {
      let U = 0, W = q.length - 1;
      while (U < W) {
        let _ = Math.ceil((U + W) / 2), N = q[_];
        if (N && N.first <= J)
          U = _;
        else
          W = _ - 1;
      }
      return q[U]?.fd ?? 0;
    } };
  } else if (Z === 4) {
    let K = $.uint32(), q = [];
    for (let J = 0;J < K; J++)
      q.push({ first: $.uint32(), fd: $.uint16() });
    return $.uint32(), { format: Z, select: (J) => {
      let U = 0, W = q.length - 1;
      while (U < W) {
        let _ = Math.ceil((U + W) / 2), N = q[_];
        if (N && N.first <= J)
          U = _;
        else
          W = _ - 1;
      }
      return q[U]?.fd ?? 0;
    } };
  }
  return { format: Z, select: () => 0 };
}
function TN($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint16(), q = $.uint32(), J = $.uint16(), U = [];
  for (let _ = 0;_ < J; _++)
    U.push($.uint32());
  $.seek(Q + q);
  let W = ON($), H = [];
  for (let _ = 0;_ < U.length; _++) {
    let N = U[_];
    $.seek(Q + N), H.push(PN($));
  }
  return { format: K, variationRegionList: W, itemVariationData: H };
}
function ON($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let q = 0;q < Z; q++) {
    let J = [];
    for (let U = 0;U < Q; U++)
      J.push({ startCoord: $.f2dot14(), peakCoord: $.f2dot14(), endCoord: $.f2dot14() });
    K.push({ axes: J });
  }
  return { axisCount: Q, regionCount: Z, regions: K };
}
function PN($) {
  let Q = $.uint16(), Z = $.uint16(), K = Z & 32767, q = (Z & 32768) !== 0, J = [];
  for (let W = 0;W < K; W++)
    J.push($.uint16());
  let U = [];
  for (let W = 0;W < Q; W++) {
    let H = [];
    for (let _ = 0;_ < K; _++)
      if (q)
        H.push($.int32());
      else
        H.push($.int16());
    U.push(H);
  }
  return { itemCount: Q, regionIndexCount: K, regionIndexes: J, deltaSets: U };
}
function IN($, Q, Z, K) {
  let q = $.itemVariationData[Q];
  if (!q)
    return 0;
  let J = q.deltaSets[Z];
  if (!J)
    return 0;
  let U = 0;
  for (let W = 0;W < q.regionIndexCount; W++) {
    let H = q.regionIndexes[W];
    if (H === undefined)
      continue;
    let _ = $.variationRegionList.regions[H];
    if (!_)
      continue;
    let N = 1;
    for (let V = 0;V < _.axes.length; V++) {
      let X = _.axes[V], z = K[V] ?? 0;
      if (X.peakCoord === 0)
        continue;
      if (z < X.startCoord || z > X.endCoord) {
        N = 0;
        break;
      }
      if (z === X.peakCoord)
        continue;
      if (z < X.peakCoord)
        N *= (z - X.startCoord) / (X.peakCoord - X.startCoord);
      else
        N *= (X.endCoord - z) / (X.endCoord - X.peakCoord);
    }
    U += N * (J[W] ?? 0);
  }
  return Math.round(U);
}
function OJ($, Q) {
  let Z = $.offset, K = $.uint16(), q = $.uint16(), J = [];
  for (let N = 0;N < q; N++)
    J.push({ platformId: $.uint16(), encodingId: $.uint16(), offset: $.uint32() });
  let U = new Map, W = new Set;
  for (let N = 0;N < J.length; N++) {
    let V = J[N];
    if (W.has(V.offset)) {
      let G = `${V.platformId}-${V.encodingId}`, Y = [...U.entries()];
      for (let j = 0;j < Y.length; j++) {
        let [E, w] = Y[j], A = E.split("@")[0];
        if (A && Number.parseInt(A, 10) === V.offset) {
          U.set(G, w);
          break;
        }
      }
      continue;
    }
    W.add(V.offset);
    let X = $.slice(V.offset, Q - V.offset), z = vN(X);
    if (z) {
      let G = `${V.platformId}-${V.encodingId}`;
      U.set(G, z);
    }
  }
  let H = ["3-10", "0-4", "3-1", "0-3", "0-6", "1-0"], _ = null;
  for (let N = 0;N < H.length; N++) {
    let V = H[N], X = U.get(V);
    if (X && X.format !== 14) {
      _ = X;
      break;
    }
  }
  if (!_) {
    let N = [...U.values()];
    for (let V = 0;V < N.length; V++) {
      let X = N[V];
      if (X.format !== 14) {
        _ = X;
        break;
      }
    }
  }
  return { version: K, numTables: q, encodingRecords: J, subtables: U, bestSubtable: _ };
}
function vN($) {
  switch ($.uint16()) {
    case 0:
      return kN($);
    case 4:
      return CN($);
    case 12:
      return SN($);
    case 14:
      return hN($);
    default:
      return null;
  }
}
function kN($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint8Array(256);
  return { format: 0, glyphIdArray: K, lookup(q) {
    if (q >= 0 && q < 256)
      return K[q];
    return;
  } };
}
function CN($) {
  let Q = $.uint16(), Z = $.uint16(), q = $.uint16() / 2;
  $.skip(6);
  let J = $.uint16Array(q);
  $.skip(2);
  let U = $.uint16Array(q), W = $.int16Array(q), H = $.offset, _ = $.uint16Array(q), V = $.remaining / 2, X = $.uint16Array(V), z = (Y) => {
    let j = 0, E = q - 1;
    while (j <= E) {
      let w = j + E >>> 1, L = J[w];
      if (Y > L)
        j = w + 1;
      else {
        let A = U[w];
        if (Y < A)
          E = w - 1;
        else {
          let D = _[w], F = W[w];
          if (D === 0)
            return Y + F & 65535;
          let M = D / 2 - (q - w) + (Y - A), R = X[M];
          if (R === undefined || R === 0)
            return 0;
          return R + F & 65535;
        }
      }
    }
    return;
  }, G = new Uint16Array(256);
  for (let Y = 0;Y < 256; Y++) {
    let j = z(Y);
    G[Y] = j !== undefined ? j + 1 : 0;
  }
  return { format: 4, segCount: q, endCodes: J, startCodes: U, idDeltas: W, idRangeOffsets: _, glyphIdArray: X, lookup(Y) {
    if (Y < 256) {
      let j = G[Y];
      return j === 0 ? undefined : j - 1;
    }
    if (Y > 65535)
      return;
    return z(Y);
  } };
}
function SN($) {
  $.skip(2);
  let Q = $.uint32(), Z = $.uint32(), K = $.uint32(), q = Array(K);
  for (let W = 0;W < K; W++)
    q[W] = { startCharCode: $.uint32(), endCharCode: $.uint32(), startGlyphId: $.uint32() };
  let J = (W) => {
    let H = 0, _ = q.length - 1;
    while (H <= _) {
      let N = H + _ >>> 1, V = q[N];
      if (!V)
        break;
      if (W > V.endCharCode)
        H = N + 1;
      else if (W < V.startCharCode)
        _ = N - 1;
      else
        return V.startGlyphId + (W - V.startCharCode);
    }
    return;
  }, U = new Uint16Array(128);
  for (let W = 0;W < 128; W++) {
    let H = J(W);
    U[W] = H !== undefined ? H + 1 : 0;
  }
  return { format: 12, groups: q, lookup(W) {
    if (W < 128) {
      let H = U[W];
      return H === 0 ? undefined : H - 1;
    }
    return J(W);
  } };
}
function hN($) {
  let Q = $.offset - 2, Z = $.uint32(), K = $.uint32(), q = [];
  for (let U = 0;U < K; U++)
    q.push({ varSelector: $.uint24(), defaultUVSOffset: $.uint32(), nonDefaultUVSOffset: $.uint32() });
  let J = [];
  for (let U = 0;U < q.length; U++) {
    let W = q[U], H = null, _ = null;
    if (W.defaultUVSOffset !== 0) {
      let N = $.sliceFrom(Q + W.defaultUVSOffset), V = N.uint32();
      H = [];
      for (let X = 0;X < V; X++)
        H.push({ startUnicodeValue: N.uint24(), additionalCount: N.uint8() });
    }
    if (W.nonDefaultUVSOffset !== 0) {
      let N = $.sliceFrom(Q + W.nonDefaultUVSOffset), V = N.uint32();
      _ = [];
      for (let X = 0;X < V; X++)
        _.push({ unicodeValue: N.uint24(), glyphId: N.uint16() });
    }
    J.push({ varSelector: W.varSelector, defaultUVS: H, nonDefaultUVS: _ });
  }
  return { format: 14, varSelectorRecords: J, lookup(U) {
    return;
  }, lookupVariation(U, W) {
    let H = 0, _ = J.length - 1, N = null;
    while (H <= _) {
      let V = H + _ >>> 1, X = J[V];
      if (!X)
        break;
      if (W > X.varSelector)
        H = V + 1;
      else if (W < X.varSelector)
        _ = V - 1;
      else {
        N = X;
        break;
      }
    }
    if (!N)
      return;
    if (N.nonDefaultUVS) {
      let V = 0, X = N.nonDefaultUVS.length - 1;
      while (V <= X) {
        let z = V + X >>> 1, G = N.nonDefaultUVS[z];
        if (!G)
          break;
        if (U > G.unicodeValue)
          V = z + 1;
        else if (U < G.unicodeValue)
          X = z - 1;
        else
          return G.glyphId;
      }
    }
    if (N.defaultUVS)
      for (let V = 0;V < N.defaultUVS.length; V++) {
        let X = N.defaultUVS[V], z = X.startUnicodeValue + X.additionalCount;
        if (U >= X.startUnicodeValue && U <= z)
          return "default";
      }
    return;
  } };
}
function PJ($, Q) {
  return $.bestSubtable?.lookup(Q) ?? 0;
}
var IJ;
((O) => {
  O[O.ColrLayers = 1] = "ColrLayers";
  O[O.Solid = 2] = "Solid";
  O[O.VarSolid = 3] = "VarSolid";
  O[O.LinearGradient = 4] = "LinearGradient";
  O[O.VarLinearGradient = 5] = "VarLinearGradient";
  O[O.RadialGradient = 6] = "RadialGradient";
  O[O.VarRadialGradient = 7] = "VarRadialGradient";
  O[O.SweepGradient = 8] = "SweepGradient";
  O[O.VarSweepGradient = 9] = "VarSweepGradient";
  O[O.Glyph = 10] = "Glyph";
  O[O.ColrGlyph = 11] = "ColrGlyph";
  O[O.Transform = 12] = "Transform";
  O[O.VarTransform = 13] = "VarTransform";
  O[O.Translate = 14] = "Translate";
  O[O.VarTranslate = 15] = "VarTranslate";
  O[O.Scale = 16] = "Scale";
  O[O.VarScale = 17] = "VarScale";
  O[O.ScaleAroundCenter = 18] = "ScaleAroundCenter";
  O[O.VarScaleAroundCenter = 19] = "VarScaleAroundCenter";
  O[O.ScaleUniform = 20] = "ScaleUniform";
  O[O.VarScaleUniform = 21] = "VarScaleUniform";
  O[O.ScaleUniformAroundCenter = 22] = "ScaleUniformAroundCenter";
  O[O.VarScaleUniformAroundCenter = 23] = "VarScaleUniformAroundCenter";
  O[O.Rotate = 24] = "Rotate";
  O[O.VarRotate = 25] = "VarRotate";
  O[O.RotateAroundCenter = 26] = "RotateAroundCenter";
  O[O.VarRotateAroundCenter = 27] = "VarRotateAroundCenter";
  O[O.Skew = 28] = "Skew";
  O[O.VarSkew = 29] = "VarSkew";
  O[O.SkewAroundCenter = 30] = "SkewAroundCenter";
  O[O.VarSkewAroundCenter = 31] = "VarSkewAroundCenter";
  O[O.Composite = 32] = "Composite";
})(IJ ||= {});
var vJ;
((K) => {
  K[K.Pad = 0] = "Pad";
  K[K.Repeat = 1] = "Repeat";
  K[K.Reflect = 2] = "Reflect";
})(vJ ||= {});
var kJ;
((v) => {
  v[v.Clear = 0] = "Clear";
  v[v.Src = 1] = "Src";
  v[v.Dest = 2] = "Dest";
  v[v.SrcOver = 3] = "SrcOver";
  v[v.DestOver = 4] = "DestOver";
  v[v.SrcIn = 5] = "SrcIn";
  v[v.DestIn = 6] = "DestIn";
  v[v.SrcOut = 7] = "SrcOut";
  v[v.DestOut = 8] = "DestOut";
  v[v.SrcAtop = 9] = "SrcAtop";
  v[v.DestAtop = 10] = "DestAtop";
  v[v.Xor = 11] = "Xor";
  v[v.Plus = 12] = "Plus";
  v[v.Screen = 13] = "Screen";
  v[v.Overlay = 14] = "Overlay";
  v[v.Darken = 15] = "Darken";
  v[v.Lighten = 16] = "Lighten";
  v[v.ColorDodge = 17] = "ColorDodge";
  v[v.ColorBurn = 18] = "ColorBurn";
  v[v.HardLight = 19] = "HardLight";
  v[v.SoftLight = 20] = "SoftLight";
  v[v.Difference = 21] = "Difference";
  v[v.Exclusion = 22] = "Exclusion";
  v[v.Multiply = 23] = "Multiply";
  v[v.Hue = 24] = "Hue";
  v[v.Saturation = 25] = "Saturation";
  v[v.Color = 26] = "Color";
  v[v.Luminosity = 27] = "Luminosity";
})(kJ ||= {});
function V1($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint16(), q = $.uint32(), J = $.uint32(), U = $.uint16(), W = [];
  if (q !== 0 && K > 0) {
    $.seek(Q + q);
    for (let N = 0;N < K; N++)
      W.push({ glyphId: $.uint16(), firstLayerIndex: $.uint16(), numLayers: $.uint16() });
  }
  let H = [];
  if (J !== 0 && U > 0) {
    $.seek(Q + J);
    for (let N = 0;N < U; N++)
      H.push({ glyphId: $.uint16(), paletteIndex: $.uint16() });
  }
  let _ = { version: Z, baseGlyphRecords: W, layerRecords: H };
  if (Z >= 1) {
    $.seek(Q + 14);
    let N = $.uint32(), V = $.uint32(), X = $.uint32(), z = $.uint32(), G = $.uint32();
    if (N !== 0) {
      let Y = Q + N;
      $.seek(Y);
      let j = $.uint32();
      _.baseGlyphPaintRecords = [];
      for (let E = 0;E < j; E++) {
        let w = $.uint16(), L = $.uint32(), A = $.offset;
        $.seek(Y + L);
        let D = v6($, Q);
        $.seek(A), _.baseGlyphPaintRecords.push({ glyphId: w, paint: D });
      }
    }
    if (V !== 0) {
      $.seek(Q + V);
      let Y = $.uint32(), j = [];
      for (let E = 0;E < Y; E++)
        j.push($.uint32());
      _.layerList = [];
      for (let E = 0;E < j.length; E++) {
        let w = j[E];
        $.seek(Q + V + w), _.layerList.push(v6($, Q));
      }
    }
    if (X !== 0)
      $.seek(Q + X), _.clipList = yN($, Q);
    if (z !== 0)
      $.seek(Q + z), _.varIdxMap = mN($);
    if (G !== 0)
      $.seek(Q + G), _.itemVariationStore = gN($);
  }
  return _;
}
function v6($, Q) {
  let Z = $.offset, K = $.uint8();
  switch (K) {
    case 1:
      return { format: K, numLayers: $.uint8(), firstLayerIndex: $.uint32() };
    case 2:
      return { format: K, paletteIndex: $.uint16(), alpha: $.f2dot14() };
    case 3:
      return { format: K, paletteIndex: $.uint16(), alpha: $.f2dot14(), varIndexBase: $.uint32() };
    case 4:
    case 5: {
      let q = $.uint24(), J = $.fword(), U = $.fword(), W = $.fword(), H = $.fword(), _ = $.fword(), N = $.fword(), V = $.offset;
      $.seek(Z + q);
      let X = N1($);
      return $.seek(V), { format: K, colorLine: X, x0: J, y0: U, x1: W, y1: H, x2: _, y2: N };
    }
    case 6:
    case 7: {
      let q = $.uint24(), J = $.fword(), U = $.fword(), W = $.ufword(), H = $.fword(), _ = $.fword(), N = $.ufword(), V = $.offset;
      $.seek(Z + q);
      let X = N1($);
      return $.seek(V), { format: K, colorLine: X, x0: J, y0: U, radius0: W, x1: H, y1: _, radius1: N };
    }
    case 8:
    case 9: {
      let q = $.uint24(), J = $.fword(), U = $.fword(), W = $.f2dot14(), H = $.f2dot14(), _ = $.offset;
      $.seek(Z + q);
      let N = N1($);
      return $.seek(_), { format: K, colorLine: N, centerX: J, centerY: U, startAngle: W, endAngle: H };
    }
    case 10: {
      let q = $.uint24(), J = $.uint16(), U = $.offset;
      $.seek(Z + q);
      let W = v6($, Q);
      return $.seek(U), { format: K, paint: W, glyphId: J };
    }
    case 11:
      return { format: K, glyphId: $.uint16() };
    case 12:
    case 13: {
      let q = $.uint24(), J = $.uint24();
      $.seek(Z + J);
      let U = { xx: $.fixed(), yx: $.fixed(), xy: $.fixed(), yy: $.fixed(), dx: $.fixed(), dy: $.fixed() };
      $.seek(Z + q);
      let W = v6($, Q);
      return { format: K, paint: W, transform: U };
    }
    case 14:
    case 15: {
      let q = $.uint24(), J = $.fword(), U = $.fword();
      $.seek(Z + q);
      let W = v6($, Q);
      return { format: K, paint: W, dx: J, dy: U };
    }
    case 16:
    case 17: {
      let q = $.uint24(), J = $.f2dot14(), U = $.f2dot14();
      $.seek(Z + q);
      let W = v6($, Q);
      return { format: K, paint: W, scaleX: J, scaleY: U };
    }
    case 18:
    case 19: {
      let q = $.uint24(), J = $.f2dot14(), U = $.f2dot14(), W = $.fword(), H = $.fword();
      $.seek(Z + q);
      let _ = v6($, Q);
      return { format: K, paint: _, scaleX: J, scaleY: U, centerX: W, centerY: H };
    }
    case 20:
    case 21: {
      let q = $.uint24(), J = $.f2dot14();
      $.seek(Z + q);
      let U = v6($, Q);
      return { format: K, paint: U, scaleX: J, scaleY: J };
    }
    case 22:
    case 23: {
      let q = $.uint24(), J = $.f2dot14(), U = $.fword(), W = $.fword();
      $.seek(Z + q);
      let H = v6($, Q);
      return { format: K, paint: H, scaleX: J, scaleY: J, centerX: U, centerY: W };
    }
    case 24:
    case 25: {
      let q = $.uint24(), J = $.f2dot14();
      $.seek(Z + q);
      let U = v6($, Q);
      return { format: K, paint: U, angle: J };
    }
    case 26:
    case 27: {
      let q = $.uint24(), J = $.f2dot14(), U = $.fword(), W = $.fword();
      $.seek(Z + q);
      let H = v6($, Q);
      return { format: K, paint: H, angle: J, centerX: U, centerY: W };
    }
    case 28:
    case 29: {
      let q = $.uint24(), J = $.f2dot14(), U = $.f2dot14();
      $.seek(Z + q);
      let W = v6($, Q);
      return { format: K, paint: W, xSkewAngle: J, ySkewAngle: U };
    }
    case 30:
    case 31: {
      let q = $.uint24(), J = $.f2dot14(), U = $.f2dot14(), W = $.fword(), H = $.fword();
      $.seek(Z + q);
      let _ = v6($, Q);
      return { format: K, paint: _, xSkewAngle: J, ySkewAngle: U, centerX: W, centerY: H };
    }
    case 32: {
      let q = $.uint24(), J = $.uint8(), U = $.uint24();
      $.seek(Z + q);
      let W = v6($, Q);
      $.seek(Z + U);
      let H = v6($, Q);
      return { format: K, sourcePaint: W, compositeMode: J, backdropPaint: H };
    }
    default:
      throw Error(`Unknown paint format: ${K}`);
  }
}
function N1($) {
  let Q = $.uint8(), Z = $.uint16(), K = [];
  for (let q = 0;q < Z; q++)
    K.push({ stopOffset: $.f2dot14(), paletteIndex: $.uint16(), alpha: $.f2dot14() });
  return { extend: Q, colorStops: K };
}
function yN($, Q) {
  let Z = $.offset, K = $.uint8(), q = $.uint32(), J = [];
  for (let U = 0;U < q; U++) {
    let W = $.uint16(), H = $.uint16(), _ = $.uint24(), N = $.offset;
    $.seek(Z + _);
    let V = $.uint8(), X = { format: V, xMin: $.fword(), yMin: $.fword(), xMax: $.fword(), yMax: $.fword() };
    if (V === 2)
      X.varIndexBase = $.uint32();
    $.seek(N), J.push({ startGlyphId: W, endGlyphId: H, clipBox: X });
  }
  return J;
}
function CJ($, Q) {
  let Z = $.baseGlyphRecords, K = 0, q = Z.length - 1;
  while (K <= q) {
    let J = K + q >>> 1, U = Z[J];
    if (!U)
      break;
    if (U.glyphId === Q) {
      let W = [];
      for (let H = 0;H < U.numLayers; H++) {
        let _ = $.layerRecords[U.firstLayerIndex + H];
        if (_)
          W.push(_);
      }
      return W;
    } else if (U.glyphId < Q)
      K = J + 1;
    else
      q = J - 1;
  }
  return null;
}
function SJ($, Q) {
  if (!$.baseGlyphPaintRecords)
    return null;
  let Z = $.baseGlyphPaintRecords, K = 0, q = Z.length - 1;
  while (K <= q) {
    let J = K + q >>> 1, U = Z[J];
    if (!U)
      break;
    if (U.glyphId === Q)
      return U.paint;
    else if (U.glyphId < Q)
      K = J + 1;
    else
      q = J - 1;
  }
  return null;
}
function bN($, Q) {
  return CJ($, Q) !== null || SJ($, Q) !== null;
}
function mN($) {
  let Q = $.uint8(), Z = $.uint8(), K = Q === 0 ? $.uint16() : $.uint32(), q = (Z & 15) + 1, J = (Z >> 4 & 15) + 1, U = Math.ceil((q + J) / 8), W = [];
  for (let H = 0;H < K; H++) {
    let _ = 0;
    for (let z = 0;z < U; z++)
      _ = _ << 8 | $.uint8();
    let N = (1 << q) - 1, V = _ & N, X = _ >> q;
    W.push(X << 16 | V);
  }
  return W;
}
function gN($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint32(), q = $.uint16(), J = [];
  for (let N = 0;N < q; N++)
    J.push($.uint32());
  $.seek(Q + K);
  let U = $.uint16(), W = $.uint16(), H = [];
  for (let N = 0;N < W; N++) {
    let V = [];
    for (let X = 0;X < U; X++)
      V.push({ startCoord: $.f2dot14(), peakCoord: $.f2dot14(), endCoord: $.f2dot14() });
    H.push({ regionAxes: V });
  }
  let _ = [];
  for (let N = 0;N < J.length; N++) {
    let V = J[N];
    $.seek(Q + V);
    let X = $.uint16(), z = $.uint16(), G = $.uint16(), Y = [];
    for (let L = 0;L < G; L++)
      Y.push($.uint16());
    let j = (z & 32768) !== 0, E = z & 32767, w = [];
    for (let L = 0;L < X; L++) {
      let A = [];
      for (let D = 0;D < G; D++)
        if (D < E)
          A.push(j ? $.int32() : $.int16());
        else
          A.push(j ? $.int16() : $.int8());
      w.push(A);
    }
    _.push({ itemCount: X, wordDeltaCount: z, regionIndexCount: G, regionIndexes: Y, deltaSets: w });
  }
  return { format: Z, variationRegionListOffset: K, itemVariationDataCount: q, itemVariationDataOffsets: J, variationRegions: H, itemVariationData: _ };
}
function uN($, Q) {
  if (!$.clipList)
    return null;
  for (let Z = 0;Z < $.clipList.length; Z++) {
    let K = $.clipList[Z];
    if (Q >= K.startGlyphId && Q <= K.endGlyphId)
      return K.clipBox;
  }
  return null;
}
function xN($, Q, Z) {
  if (!$.itemVariationStore || !$.varIdxMap)
    return 0;
  let K = $.varIdxMap[Q];
  if (K === undefined)
    return 0;
  let q = K >> 16, J = K & 65535, U = $.itemVariationStore, W = U.itemVariationData[q];
  if (!W)
    return 0;
  let H = W.deltaSets[J];
  if (!H)
    return 0;
  let _ = 0;
  for (let N = 0;N < W.regionIndexCount; N++) {
    let V = W.regionIndexes[N];
    if (V === undefined)
      continue;
    let X = U.variationRegions[V];
    if (!X)
      continue;
    let z = 1;
    for (let Y = 0;Y < X.regionAxes.length && Y < Z.length; Y++) {
      let j = X.regionAxes[Y], E = Z[Y];
      if (j === undefined || E === undefined)
        continue;
      if (z *= fN(E, j.startCoord, j.peakCoord, j.endCoord), z === 0)
        break;
    }
    let G = H[N];
    if (G !== undefined)
      _ += G * z;
  }
  return _;
}
function fN($, Q, Z, K) {
  if (Q === 0 && Z === 0 && K === 0)
    return 1;
  if ($ === Z)
    return 1;
  if ($ < Q || $ > K)
    return 0;
  if ($ < Z) {
    if (Q === Z)
      return 1;
    return ($ - Q) / (Z - Q);
  } else {
    if (Z === K)
      return 1;
    return (K - $) / (K - Z);
  }
}
function lN($) {
  return $.version >= 1 && $.baseGlyphPaintRecords !== undefined;
}
function dN($, Q) {
  return $.layerList?.[Q] ?? null;
}
var hJ;
((Z) => {
  Z[Z.UsableWithLightBackground = 1] = "UsableWithLightBackground";
  Z[Z.UsableWithDarkBackground = 2] = "UsableWithDarkBackground";
})(hJ ||= {});
function X1($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.uint32(), W = [];
  for (let z = 0;z < q; z++)
    W.push($.uint16());
  $.seek(Q + U);
  let H = [];
  for (let z = 0;z < J; z++)
    H.push({ blue: $.uint8(), green: $.uint8(), red: $.uint8(), alpha: $.uint8() });
  let _ = [];
  for (let z = 0;z < q; z++) {
    let G = W[z];
    if (G === undefined)
      continue;
    let Y = [];
    for (let j = 0;j < K; j++) {
      let E = H[G + j];
      if (E)
        Y.push(E);
    }
    _.push({ colors: Y });
  }
  let N, V, X;
  if (Z >= 1) {
    $.seek(Q + 12 + q * 2);
    let z = $.uint32(), G = $.uint32(), Y = $.uint32();
    if (z !== 0) {
      $.seek(Q + z), N = [];
      for (let j = 0;j < q; j++)
        N.push($.uint32());
    }
    if (G !== 0) {
      $.seek(Q + G), V = [];
      for (let j = 0;j < q; j++)
        V.push($.uint16());
    }
    if (Y !== 0) {
      $.seek(Q + Y), X = [];
      for (let j = 0;j < K; j++)
        X.push($.uint16());
    }
  }
  return { version: Z, numPalettes: q, numPaletteEntries: K, palettes: _, paletteTypes: N, paletteLabels: V, paletteEntryLabels: X };
}
function nN($, Q, Z) {
  let K = $.palettes[Q];
  if (!K)
    return null;
  return K.colors[Z] ?? null;
}
function pN($) {
  return `rgba(${$.red}, ${$.green}, ${$.blue}, ${($.alpha / 255).toFixed(3)})`;
}
function cN($) {
  let Q = $.red.toString(16).padStart(2, "0"), Z = $.green.toString(16).padStart(2, "0"), K = $.blue.toString(16).padStart(2, "0");
  if ($.alpha === 255)
    return `#${Q}${Z}${K}`;
  let q = $.alpha.toString(16).padStart(2, "0");
  return `#${Q}${Z}${K}${q}`;
}
var yJ;
((u) => {
  u[u.AllTypographicFeatures = 0] = "AllTypographicFeatures";
  u[u.Ligatures = 1] = "Ligatures";
  u[u.CursiveConnection = 2] = "CursiveConnection";
  u[u.LetterCase = 3] = "LetterCase";
  u[u.VerticalSubstitution = 4] = "VerticalSubstitution";
  u[u.LinguisticRearrangement = 5] = "LinguisticRearrangement";
  u[u.NumberSpacing = 6] = "NumberSpacing";
  u[u.SmartSwashes = 8] = "SmartSwashes";
  u[u.Diacritics = 9] = "Diacritics";
  u[u.VerticalPosition = 10] = "VerticalPosition";
  u[u.Fractions = 11] = "Fractions";
  u[u.OverlappingCharacters = 13] = "OverlappingCharacters";
  u[u.TypographicExtras = 14] = "TypographicExtras";
  u[u.MathematicalExtras = 15] = "MathematicalExtras";
  u[u.OrnamentSets = 16] = "OrnamentSets";
  u[u.CharacterAlternatives = 17] = "CharacterAlternatives";
  u[u.DesignComplexity = 18] = "DesignComplexity";
  u[u.StyleOptions = 19] = "StyleOptions";
  u[u.CharacterShape = 20] = "CharacterShape";
  u[u.NumberCase = 21] = "NumberCase";
  u[u.TextSpacing = 22] = "TextSpacing";
  u[u.Transliteration = 23] = "Transliteration";
  u[u.Annotation = 24] = "Annotation";
  u[u.KanaSpacing = 25] = "KanaSpacing";
  u[u.IdeographicSpacing = 26] = "IdeographicSpacing";
  u[u.UnicodeDecomposition = 27] = "UnicodeDecomposition";
  u[u.RubyKana = 28] = "RubyKana";
  u[u.CJKSymbolAlternatives = 29] = "CJKSymbolAlternatives";
  u[u.IdeographicAlternatives = 30] = "IdeographicAlternatives";
  u[u.CJKVerticalRomanPlacement = 31] = "CJKVerticalRomanPlacement";
  u[u.ItalicCJKRoman = 32] = "ItalicCJKRoman";
  u[u.CaseSensitiveLayout = 33] = "CaseSensitiveLayout";
  u[u.AlternateKana = 34] = "AlternateKana";
  u[u.StylisticAlternatives = 35] = "StylisticAlternatives";
  u[u.ContextualAlternatives = 36] = "ContextualAlternatives";
  u[u.LowerCase = 37] = "LowerCase";
  u[u.UpperCase = 38] = "UpperCase";
  u[u.LanguageTag = 39] = "LanguageTag";
  u[u.CJKRomanSpacing = 103] = "CJKRomanSpacing";
})(yJ ||= {});
var bJ;
((F) => {
  F[F.RequiredLigaturesOn = 0] = "RequiredLigaturesOn";
  F[F.RequiredLigaturesOff = 1] = "RequiredLigaturesOff";
  F[F.CommonLigaturesOn = 2] = "CommonLigaturesOn";
  F[F.CommonLigaturesOff = 3] = "CommonLigaturesOff";
  F[F.RareLigaturesOn = 4] = "RareLigaturesOn";
  F[F.RareLigaturesOff = 5] = "RareLigaturesOff";
  F[F.LogosOn = 6] = "LogosOn";
  F[F.LogosOff = 7] = "LogosOff";
  F[F.RebusPicturesOn = 8] = "RebusPicturesOn";
  F[F.RebusPicturesOff = 9] = "RebusPicturesOff";
  F[F.DiphthongLigaturesOn = 10] = "DiphthongLigaturesOn";
  F[F.DiphthongLigaturesOff = 11] = "DiphthongLigaturesOff";
  F[F.SquaredLigaturesOn = 12] = "SquaredLigaturesOn";
  F[F.SquaredLigaturesOff = 13] = "SquaredLigaturesOff";
  F[F.AbbrevSquaredLigaturesOn = 14] = "AbbrevSquaredLigaturesOn";
  F[F.AbbrevSquaredLigaturesOff = 15] = "AbbrevSquaredLigaturesOff";
  F[F.SymbolLigaturesOn = 16] = "SymbolLigaturesOn";
  F[F.SymbolLigaturesOff = 17] = "SymbolLigaturesOff";
  F[F.ContextualLigaturesOn = 18] = "ContextualLigaturesOn";
  F[F.ContextualLigaturesOff = 19] = "ContextualLigaturesOff";
  F[F.HistoricalLigaturesOn = 20] = "HistoricalLigaturesOn";
  F[F.HistoricalLigaturesOff = 21] = "HistoricalLigaturesOff";
})(bJ ||= {});
var mJ;
((J) => {
  J[J.NormalPosition = 0] = "NormalPosition";
  J[J.Superiors = 1] = "Superiors";
  J[J.Inferiors = 2] = "Inferiors";
  J[J.Ordinals = 3] = "Ordinals";
  J[J.ScientificInferiors = 4] = "ScientificInferiors";
})(mJ ||= {});
var gJ;
((Z) => {
  Z[Z.LowerCaseNumbers = 0] = "LowerCaseNumbers";
  Z[Z.UpperCaseNumbers = 1] = "UpperCaseNumbers";
})(gJ ||= {});
var uJ;
((q) => {
  q[q.MonospacedNumbers = 0] = "MonospacedNumbers";
  q[q.ProportionalNumbers = 1] = "ProportionalNumbers";
  q[q.ThirdWidthNumbers = 2] = "ThirdWidthNumbers";
  q[q.QuarterWidthNumbers = 3] = "QuarterWidthNumbers";
})(uJ ||= {});
var xJ;
((K) => {
  K[K.NoFractions = 0] = "NoFractions";
  K[K.VerticalFractions = 1] = "VerticalFractions";
  K[K.DiagonalFractions = 2] = "DiagonalFractions";
})(xJ ||= {});
var fJ;
((q) => {
  q[q.CaseSensitiveLayoutOn = 0] = "CaseSensitiveLayoutOn";
  q[q.CaseSensitiveLayoutOff = 1] = "CaseSensitiveLayoutOff";
  q[q.CaseSensitiveSpacingOn = 2] = "CaseSensitiveSpacingOn";
  q[q.CaseSensitiveSpacingOff = 3] = "CaseSensitiveSpacingOff";
})(fJ ||= {});
var lJ;
((y) => {
  y[y.NoStylisticAlternates = 0] = "NoStylisticAlternates";
  y[y.StylisticAltOneOn = 2] = "StylisticAltOneOn";
  y[y.StylisticAltOneOff = 3] = "StylisticAltOneOff";
  y[y.StylisticAltTwoOn = 4] = "StylisticAltTwoOn";
  y[y.StylisticAltTwoOff = 5] = "StylisticAltTwoOff";
  y[y.StylisticAltThreeOn = 6] = "StylisticAltThreeOn";
  y[y.StylisticAltThreeOff = 7] = "StylisticAltThreeOff";
  y[y.StylisticAltFourOn = 8] = "StylisticAltFourOn";
  y[y.StylisticAltFourOff = 9] = "StylisticAltFourOff";
  y[y.StylisticAltFiveOn = 10] = "StylisticAltFiveOn";
  y[y.StylisticAltFiveOff = 11] = "StylisticAltFiveOff";
  y[y.StylisticAltSixOn = 12] = "StylisticAltSixOn";
  y[y.StylisticAltSixOff = 13] = "StylisticAltSixOff";
  y[y.StylisticAltSevenOn = 14] = "StylisticAltSevenOn";
  y[y.StylisticAltSevenOff = 15] = "StylisticAltSevenOff";
  y[y.StylisticAltEightOn = 16] = "StylisticAltEightOn";
  y[y.StylisticAltEightOff = 17] = "StylisticAltEightOff";
  y[y.StylisticAltNineOn = 18] = "StylisticAltNineOn";
  y[y.StylisticAltNineOff = 19] = "StylisticAltNineOff";
  y[y.StylisticAltTenOn = 20] = "StylisticAltTenOn";
  y[y.StylisticAltTenOff = 21] = "StylisticAltTenOff";
  y[y.StylisticAltElevenOn = 22] = "StylisticAltElevenOn";
  y[y.StylisticAltElevenOff = 23] = "StylisticAltElevenOff";
  y[y.StylisticAltTwelveOn = 24] = "StylisticAltTwelveOn";
  y[y.StylisticAltTwelveOff = 25] = "StylisticAltTwelveOff";
  y[y.StylisticAltThirteenOn = 26] = "StylisticAltThirteenOn";
  y[y.StylisticAltThirteenOff = 27] = "StylisticAltThirteenOff";
  y[y.StylisticAltFourteenOn = 28] = "StylisticAltFourteenOn";
  y[y.StylisticAltFourteenOff = 29] = "StylisticAltFourteenOff";
  y[y.StylisticAltFifteenOn = 30] = "StylisticAltFifteenOn";
  y[y.StylisticAltFifteenOff = 31] = "StylisticAltFifteenOff";
  y[y.StylisticAltSixteenOn = 32] = "StylisticAltSixteenOn";
  y[y.StylisticAltSixteenOff = 33] = "StylisticAltSixteenOff";
  y[y.StylisticAltSeventeenOn = 34] = "StylisticAltSeventeenOn";
  y[y.StylisticAltSeventeenOff = 35] = "StylisticAltSeventeenOff";
  y[y.StylisticAltEighteenOn = 36] = "StylisticAltEighteenOn";
  y[y.StylisticAltEighteenOff = 37] = "StylisticAltEighteenOff";
  y[y.StylisticAltNineteenOn = 38] = "StylisticAltNineteenOn";
  y[y.StylisticAltNineteenOff = 39] = "StylisticAltNineteenOff";
  y[y.StylisticAltTwentyOn = 40] = "StylisticAltTwentyOn";
  y[y.StylisticAltTwentyOff = 41] = "StylisticAltTwentyOff";
})(lJ ||= {});
var dJ;
((U) => {
  U[U.ContextualAlternatesOn = 0] = "ContextualAlternatesOn";
  U[U.ContextualAlternatesOff = 1] = "ContextualAlternatesOff";
  U[U.SwashAlternatesOn = 2] = "SwashAlternatesOn";
  U[U.SwashAlternatesOff = 3] = "SwashAlternatesOff";
  U[U.ContextualSwashAlternatesOn = 4] = "ContextualSwashAlternatesOn";
  U[U.ContextualSwashAlternatesOff = 5] = "ContextualSwashAlternatesOff";
})(dJ ||= {});
var nJ;
((K) => {
  K[K.DefaultLowerCase = 0] = "DefaultLowerCase";
  K[K.LowerCaseSmallCaps = 1] = "LowerCaseSmallCaps";
  K[K.LowerCasePetiteCaps = 2] = "LowerCasePetiteCaps";
})(nJ ||= {});
var pJ;
((K) => {
  K[K.DefaultUpperCase = 0] = "DefaultUpperCase";
  K[K.UpperCaseSmallCaps = 1] = "UpperCaseSmallCaps";
  K[K.UpperCasePetiteCaps = 2] = "UpperCasePetiteCaps";
})(pJ ||= {});
var cJ;
((N) => {
  N[N.WordInitialSwashesOn = 0] = "WordInitialSwashesOn";
  N[N.WordInitialSwashesOff = 1] = "WordInitialSwashesOff";
  N[N.WordFinalSwashesOn = 2] = "WordFinalSwashesOn";
  N[N.WordFinalSwashesOff = 3] = "WordFinalSwashesOff";
  N[N.LineInitialSwashesOn = 4] = "LineInitialSwashesOn";
  N[N.LineInitialSwashesOff = 5] = "LineInitialSwashesOff";
  N[N.LineFinalSwashesOn = 6] = "LineFinalSwashesOn";
  N[N.LineFinalSwashesOff = 7] = "LineFinalSwashesOff";
  N[N.NonFinalSwashesOn = 8] = "NonFinalSwashesOn";
  N[N.NonFinalSwashesOff = 9] = "NonFinalSwashesOff";
})(cJ ||= {});
var oJ;
((K) => {
  K[K.ShowDiacritics = 0] = "ShowDiacritics";
  K[K.HideDiacritics = 1] = "HideDiacritics";
  K[K.DecomposeDiacritics = 2] = "DecomposeDiacritics";
})(oJ ||= {});
var iJ;
((G) => {
  G[G.TraditionalCharacters = 0] = "TraditionalCharacters";
  G[G.SimplifiedCharacters = 1] = "SimplifiedCharacters";
  G[G.JIS1978Characters = 2] = "JIS1978Characters";
  G[G.JIS1983Characters = 3] = "JIS1983Characters";
  G[G.JIS1990Characters = 4] = "JIS1990Characters";
  G[G.TraditionalAltOne = 5] = "TraditionalAltOne";
  G[G.TraditionalAltTwo = 6] = "TraditionalAltTwo";
  G[G.TraditionalAltThree = 7] = "TraditionalAltThree";
  G[G.TraditionalAltFour = 8] = "TraditionalAltFour";
  G[G.TraditionalAltFive = 9] = "TraditionalAltFive";
  G[G.ExpertCharacters = 10] = "ExpertCharacters";
  G[G.NLCCharacters = 13] = "NLCCharacters";
  G[G.JIS2004Characters = 11] = "JIS2004Characters";
  G[G.HojoCharacters = 12] = "HojoCharacters";
})(iJ ||= {});
var sJ;
((Z) => {
  Z[Z.Exclusive = 32768] = "Exclusive";
  Z[Z.UseDefault = 16384] = "UseDefault";
})(sJ ||= {});
function z1($) {
  let Q = $.offset, Z = $.fixed(), K = $.uint16();
  $.skip(2), $.skip(4);
  let q = [];
  for (let J = 0;J < K; J++) {
    let U = $.uint16(), W = $.uint16(), H = $.offset32(), _ = $.uint16(), N = _ & 255, V = $.uint16(), X = [], z = $.offset;
    $.seek(Q + H);
    for (let G = 0;G < W; G++)
      X.push({ settingValue: $.uint16(), nameId: $.uint16() });
    $.seek(z), q.push({ featureType: U, nSettings: W, settingTableOffset: H, featureFlags: _, defaultSettingIndex: N, nameId: V, settings: X });
  }
  return { version: Z, features: q };
}
function oN($, Q) {
  return $.features.find((Z) => Z.featureType === Q);
}
function iN($) {
  return $.features;
}
function sN($) {
  return ($.featureFlags & 32768) !== 0;
}
function rN($) {
  return $.settings[$.defaultSettingIndex];
}
function aN($, Q) {
  return $.settings.find((Z) => Z.settingValue === Q);
}
function tN($, Q) {
  return $.settings.some((Z) => Z.settingValue === Q);
}
function eN($, Q) {
  switch ($) {
    case 1:
      switch (Q) {
        case 2:
          return "liga";
        case 4:
          return "dlig";
        case 20:
          return "hlig";
        case 18:
          return "clig";
        case 0:
          return "rlig";
      }
      break;
    case 10:
      switch (Q) {
        case 1:
          return "sups";
        case 2:
          return "subs";
        case 3:
          return "ordn";
        case 4:
          return "sinf";
      }
      break;
    case 11:
      if (Q === 1 || Q === 2)
        return "frac";
      break;
    case 21:
      switch (Q) {
        case 0:
          return "onum";
        case 1:
          return "lnum";
      }
      break;
    case 6:
      switch (Q) {
        case 0:
          return "tnum";
        case 1:
          return "pnum";
      }
      break;
    case 33:
      if (Q === 0)
        return "case";
      break;
    case 37:
      switch (Q) {
        case 1:
          return "smcp";
        case 2:
          return "pcap";
      }
      break;
    case 38:
      switch (Q) {
        case 1:
          return "c2sc";
        case 2:
          return "c2pc";
      }
      break;
    case 8:
      if (Q === 0 || Q === 2)
        return "swsh";
      break;
    case 36:
      switch (Q) {
        case 0:
          return "calt";
        case 2:
          return "swsh";
      }
      break;
    case 35:
      if (Q >= 2 && Q <= 41) {
        let Z = Math.floor((Q - 2) / 2) + 1;
        if (Z <= 20)
          return `ss${Z.toString().padStart(2, "0")}`;
      }
      break;
    case 20:
      switch (Q) {
        case 0:
          return "trad";
        case 1:
          return "smpl";
        case 2:
          return "jp78";
        case 3:
          return "jp83";
        case 4:
          return "jp90";
        case 11:
          return "jp04";
        case 13:
          return "nlck";
        case 10:
          return "expt";
        case 12:
          return "hojo";
      }
      break;
    case 4:
      return "vert";
    case 24:
      return "nalt";
    case 28:
      return "ruby";
  }
  return null;
}
function $V($) {
  switch ($) {
    case "liga":
      return { featureType: 1, settingValue: 2 };
    case "dlig":
      return { featureType: 1, settingValue: 4 };
    case "hlig":
      return { featureType: 1, settingValue: 20 };
    case "clig":
      return { featureType: 1, settingValue: 18 };
    case "rlig":
      return { featureType: 1, settingValue: 0 };
    case "sups":
      return { featureType: 10, settingValue: 1 };
    case "subs":
      return { featureType: 10, settingValue: 2 };
    case "ordn":
      return { featureType: 10, settingValue: 3 };
    case "sinf":
      return { featureType: 10, settingValue: 4 };
    case "frac":
      return { featureType: 11, settingValue: 2 };
    case "onum":
      return { featureType: 21, settingValue: 0 };
    case "lnum":
      return { featureType: 21, settingValue: 1 };
    case "tnum":
      return { featureType: 6, settingValue: 0 };
    case "pnum":
      return { featureType: 6, settingValue: 1 };
    case "case":
      return { featureType: 33, settingValue: 0 };
    case "smcp":
      return { featureType: 37, settingValue: 1 };
    case "pcap":
      return { featureType: 37, settingValue: 2 };
    case "c2sc":
      return { featureType: 38, settingValue: 1 };
    case "c2pc":
      return { featureType: 38, settingValue: 2 };
    case "swsh":
      return { featureType: 8, settingValue: 0 };
    case "calt":
      return { featureType: 36, settingValue: 0 };
    case "trad":
      return { featureType: 20, settingValue: 0 };
    case "smpl":
      return { featureType: 20, settingValue: 1 };
    case "jp78":
      return { featureType: 20, settingValue: 2 };
    case "jp83":
      return { featureType: 20, settingValue: 3 };
    case "jp90":
      return { featureType: 20, settingValue: 4 };
    case "jp04":
      return { featureType: 20, settingValue: 11 };
    case "nlck":
      return { featureType: 20, settingValue: 13 };
    case "expt":
      return { featureType: 20, settingValue: 10 };
    case "hojo":
      return { featureType: 20, settingValue: 12 };
    case "vert":
      return { featureType: 4, settingValue: 0 };
    case "nalt":
      return { featureType: 24, settingValue: 0 };
    case "ruby":
      return { featureType: 28, settingValue: 0 };
  }
  if ($.startsWith("ss") && $.length === 4) {
    let Q = parseInt($.slice(2), 10);
    if (Q >= 1 && Q <= 20)
      return { featureType: 35, settingValue: (Q - 1) * 2 + 2 };
  }
  return null;
}
var K0 = { GridFit: 1, DoGray: 2, SymmetricGridFit: 4, SymmetricSmoothing: 8 };
function G1($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let q = 0;q < Z; q++) {
    let J = $.uint16(), U = $.uint16();
    K.push({ maxPPEM: J, behavior: U });
  }
  return K.sort((q, J) => q.maxPPEM - J.maxPPEM), { version: Q, ranges: K };
}
function Y1($, Q) {
  for (let Z = 0;Z < $.ranges.length; Z++) {
    let K = $.ranges[Z];
    if (Q <= K.maxPPEM)
      return K.behavior;
  }
  if ($.ranges.length > 0)
    return $.ranges[$.ranges.length - 1]?.behavior;
  return K0.GridFit | K0.DoGray;
}
function QV($, Q) {
  return (Y1($, Q) & K0.GridFit) !== 0;
}
function ZV($, Q) {
  return (Y1($, Q) & K0.DoGray) !== 0;
}

class O8 {
  startGlyphId;
  classValueArray;
  ranges;
  glyphMap;
  isEmpty;
  constructor($, Q, Z, K, q) {
    this.startGlyphId = $, this.classValueArray = Q, this.ranges = Z, this.glyphMap = K, this.isEmpty = q;
  }
  static empty() {
    return new O8(0, null, null, null, true);
  }
  static format1($, Q) {
    return new O8($, Q, null, null, false);
  }
  static format2($) {
    let Q = 0;
    for (let K = 0;K < $.length; K++) {
      let q = $[K];
      Q += q.endGlyphId - q.startGlyphId + 1;
    }
    let Z = null;
    if (Q < 1e4) {
      Z = new Map;
      for (let K = 0;K < $.length; K++) {
        let q = $[K];
        for (let J = q.startGlyphId;J <= q.endGlyphId; J++)
          Z.set(J, q.classValue);
      }
    }
    return new O8(0, null, $, Z, false);
  }
  get($) {
    if (this.isEmpty)
      return 0;
    if (this.classValueArray) {
      let q = $ - this.startGlyphId;
      return q >= 0 && q < this.classValueArray.length ? this.classValueArray[q] : 0;
    }
    if (this.glyphMap)
      return this.glyphMap.get($) ?? 0;
    let Q = this.ranges, Z = 0, K = Q.length - 1;
    while (Z <= K) {
      let q = Z + K >>> 1, J = Q[q];
      if ($ > J.endGlyphId)
        Z = q + 1;
      else if ($ < J.startGlyphId)
        K = q - 1;
      else
        return J.classValue;
    }
    return 0;
  }
  glyphsInClass($) {
    if (this.isEmpty)
      return [];
    if (this.classValueArray) {
      let K = [];
      for (let q = 0;q < this.classValueArray.length; q++)
        if (this.classValueArray[q] === $)
          K.push(this.startGlyphId + q);
      return K;
    }
    let Q = [], Z = this.ranges;
    for (let K = 0;K < Z.length; K++) {
      let q = Z[K];
      if (q.classValue === $)
        for (let J = q.startGlyphId;J <= q.endGlyphId; J++)
          Q.push(J);
    }
    return Q;
  }
}
var rJ = O8.empty();
function aJ($) {
  let Q = $.uint16();
  if (Q === 1) {
    let Z = $.uint16(), K = $.uint16(), q = $.uint16Array(K);
    return O8.format1(Z, q);
  }
  if (Q === 2) {
    let Z = $.uint16(), K = Array(Z);
    for (let q = 0;q < Z; q++)
      K[q] = { startGlyphId: $.uint16(), endGlyphId: $.uint16(), classValue: $.uint16() };
    return O8.format2(K);
  }
  throw Error(`Unknown ClassDef format: ${Q}`);
}
function R6($, Q) {
  if (Q === 0)
    return rJ;
  return aJ($.sliceFrom(Q));
}
function tJ($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset16(), q = $.offset16(), J = $.offset16(), U = $.offset16(), W = 0;
  if (Q === 1 && Z >= 2)
    W = $.offset16();
  let H = R6($, K), _ = null;
  if (q !== 0)
    _ = KV($.sliceFrom(q));
  let N = null;
  if (J !== 0)
    N = qV($.sliceFrom(J));
  let V = R6($, U), X = null;
  if (W !== 0)
    X = JV($.sliceFrom(W));
  return { version: { major: Q, minor: Z }, glyphClassDef: H, attachList: _, ligCaretList: N, markAttachClassDef: V, markGlyphSets: X };
}
function KV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = $.sliceFrom(Q), J = q.uint16(), U = [];
  if (J === 1) {
    let H = q.uint16();
    for (let _ = 0;_ < H; _++)
      U.push(q.uint16());
  } else if (J === 2) {
    let H = q.uint16();
    for (let _ = 0;_ < H; _++) {
      let N = q.uint16(), V = q.uint16();
      q.skip(2);
      for (let X = N;X <= V; X++)
        U.push(X);
    }
  }
  let W = new Map;
  for (let H = 0;H < K.length; H++) {
    let _ = K[H], N = U[H];
    if (N === undefined)
      continue;
    let V = $.sliceFrom(_), X = V.uint16(), z = V.uint16Array(X), G = Array(z.length);
    for (let Y = 0;Y < z.length; Y++)
      G[Y] = z[Y];
    W.set(N, { pointIndices: G });
  }
  return W;
}
function qV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = $.sliceFrom(Q), J = q.uint16(), U = [];
  if (J === 1) {
    let H = q.uint16();
    for (let _ = 0;_ < H; _++)
      U.push(q.uint16());
  } else if (J === 2) {
    let H = q.uint16();
    for (let _ = 0;_ < H; _++) {
      let N = q.uint16(), V = q.uint16();
      q.skip(2);
      for (let X = N;X <= V; X++)
        U.push(X);
    }
  }
  let W = new Map;
  for (let H = 0;H < K.length; H++) {
    let _ = K[H], N = U[H];
    if (N === undefined)
      continue;
    let V = $.sliceFrom(_), X = V.uint16(), z = V.uint16Array(X), G = [];
    for (let Y = 0;Y < z.length; Y++) {
      let j = z[Y], E = $.sliceFrom(_ + j), w = E.uint16();
      if (w === 1)
        G.push(E.int16());
      else if (w === 2)
        G.push(E.uint16());
      else if (w === 3)
        G.push(E.int16());
    }
    W.set(N, { caretValues: G });
  }
  return W;
}
function JV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint32Array(Z), q = [];
  for (let J = 0;J < K.length; J++) {
    let U = K[J], W = $.sliceFrom(U), H = W.uint16(), _ = new Set;
    if (H === 1) {
      let N = W.uint16();
      for (let V = 0;V < N; V++)
        _.add(W.uint16());
    } else if (H === 2) {
      let N = W.uint16();
      for (let V = 0;V < N; V++) {
        let X = W.uint16(), z = W.uint16();
        W.skip(2);
        for (let G = X;G <= z; G++)
          _.add(G);
      }
    }
    q.push(_);
  }
  return { has(J, U) {
    let W = q[J];
    return W ? W.has(U) : false;
  } };
}
function z8($, Q) {
  if (!$)
    return 0;
  return $.glyphClassDef.get(Q);
}

class E9 {
  glyphMap;
  ranges;
  glyphArray;
  size;
  constructor($, Q, Z, K) {
    this.glyphMap = $, this.ranges = Q, this.glyphArray = Z, this.size = K;
  }
  static format1($) {
    let Q = new Map;
    for (let Z = 0;Z < $.length; Z++)
      Q.set($[Z], Z);
    return new E9(Q, null, $, $.length);
  }
  static format2($) {
    let Q = 0;
    if ($.length > 0) {
      let Z = $[$.length - 1];
      if (Z)
        Q = Z.startCoverageIndex + (Z.endGlyphId - Z.startGlyphId + 1);
    }
    return new E9(null, $, null, Q);
  }
  get($) {
    if (this.glyphMap)
      return this.glyphMap.get($) ?? null;
    let Q = this.ranges, Z = 0, K = Q.length - 1;
    while (Z <= K) {
      let q = Z + K >>> 1, J = Q[q];
      if ($ > J.endGlyphId)
        Z = q + 1;
      else if ($ < J.startGlyphId)
        K = q - 1;
      else
        return J.startCoverageIndex + ($ - J.startGlyphId);
    }
    return null;
  }
  covers($) {
    return this.get($) !== null;
  }
  glyphs() {
    if (this.glyphArray) {
      let Z = this.glyphArray, K = Array(Z.length);
      for (let q = 0;q < Z.length; q++)
        K[q] = Z[q];
      return K;
    }
    let $ = [], Q = this.ranges;
    for (let Z = 0;Z < Q.length; Z++) {
      let K = Q[Z];
      for (let q = K.startGlyphId;q <= K.endGlyphId; q++)
        $.push(q);
    }
    return $;
  }
}
function eJ($) {
  let Q = $.uint16();
  if (Q === 1) {
    let Z = $.uint16(), K = $.uint16Array(Z);
    return E9.format1(K);
  }
  if (Q === 2) {
    let Z = $.uint16(), K = Array(Z);
    for (let q = 0;q < Z; q++)
      K[q] = { startGlyphId: $.uint16(), endGlyphId: $.uint16(), startCoverageIndex: $.uint16() };
    return E9.format2(K);
  }
  throw Error(`Unknown Coverage format: ${Q}`);
}
function s($, Q) {
  return eJ($.sliceFrom(Q));
}
function $U($) {
  return "deltaSetOuterIndex" in $;
}
function c8($, Q) {
  if (Q === 0)
    return null;
  return UV($.sliceFrom(Q));
}
function UV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16();
  if (K === 32768)
    return { deltaSetOuterIndex: Q, deltaSetInnerIndex: Z };
  let q = [];
  if (K >= 1 && K <= 3) {
    let J = Z - Q + 1, U = 1 << K, W = 16 / U, H = (1 << U) - 1, _ = 1 << U - 1, N = Math.ceil(J / W), V = 0;
    for (let X = 0;X < N; X++) {
      let z = $.uint16();
      for (let G = 0;G < W && V < J; G++, V++) {
        let Y = 16 - U * (G + 1), j = z >> Y & H;
        if (j & _)
          j = j - (1 << U);
        q.push(j);
      }
    }
  }
  return { startSize: Q, endSize: Z, deltaFormat: K, deltaValues: q };
}
function QU($, Q) {
  if (Q < $.startSize || Q > $.endSize)
    return 0;
  let Z = Q - $.startSize;
  return $.deltaValues[Z] ?? 0;
}
function WV($, Q, Z) {
  if (!$)
    return Q;
  if ($U($))
    return Q;
  return Q + QU($, Z);
}
var b6 = { RightToLeft: 1, IgnoreBaseGlyphs: 2, IgnoreLigatures: 4, IgnoreMarks: 8, UseMarkFilteringSet: 16, MarkAttachmentTypeMask: 65280 };
function W5($) {
  return ($ & b6.MarkAttachmentTypeMask) >> 8;
}
function q0($) {
  let Q = $.uint16(), Z = [];
  for (let q = 0;q < Q; q++)
    Z.push({ tag: $.tag(), offset: $.offset16() });
  let K = [];
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q], U = $.sliceFrom(J.offset), W = HV(U);
    K.push({ scriptTag: J.tag, script: W });
  }
  return { scripts: K };
}
function HV($) {
  let Q = $.offset16(), Z = $.uint16(), K = [];
  for (let U = 0;U < Z; U++)
    K.push({ tag: $.tag(), offset: $.offset16() });
  let q = null;
  if (Q !== 0)
    q = ZU($.sliceFrom(Q));
  let J = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U], H = ZU($.sliceFrom(W.offset));
    J.push({ langSysTag: W.tag, langSys: H });
  }
  return { defaultLangSys: q, langSysRecords: J };
}
function ZU($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16(), q = $.uint16Array(K), J = Array(K);
  for (let U = 0;U < K; U++)
    J[U] = q[U];
  return { requiredFeatureIndex: Z, featureIndices: J };
}
function J0($) {
  let Q = $.uint16(), Z = [];
  for (let q = 0;q < Q; q++)
    Z.push({ tag: $.tag(), offset: $.offset16() });
  let K = [];
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q], U = $.sliceFrom(J.offset), W = _V(U);
    K.push({ featureTag: J.tag, feature: W });
  }
  return { features: K };
}
function _V($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = Array(Z);
  for (let J = 0;J < Z; J++)
    q[J] = K[J];
  return { featureParamsOffset: Q, lookupListIndices: q };
}
function NV($) {
  let Q = $.uint16(), Z = $.uint16Array(Q), K = [];
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q], U = $.sliceFrom(J);
    K.push(VV(U));
  }
  return K;
}
function VV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16Array(K), J = Array(K);
  for (let W = 0;W < K; W++)
    J[W] = q[W];
  let U;
  if (Z & b6.UseMarkFilteringSet)
    U = $.uint16();
  return { lookupType: Q, lookupFlag: Z, subtableOffsets: J, markFilteringSet: U };
}
function U0($, Q) {
  for (let Z = 0;Z < $.scripts.length; Z++) {
    let K = $.scripts[Z];
    if (K.scriptTag === Q)
      return K.script;
  }
  return null;
}
function j1($, Q) {
  if (Q === null)
    return $.defaultLangSys;
  for (let Z = 0;Z < $.langSysRecords.length; Z++) {
    let K = $.langSysRecords[Z];
    if (K.langSysTag === Q)
      return K.langSys;
  }
  return $.defaultLangSys;
}
function H5($, Q) {
  return $.features[Q] ?? null;
}

class V6 {
  mask0 = 0;
  mask1 = 0;
  mask2 = 0;
  add($) {
    this.mask0 |= 1 << ($ & 31), this.mask1 |= 1 << ($ >> 4 & 31), this.mask2 |= 1 << ($ >> 9 & 31);
  }
  addRange($, Q) {
    if (Q - $ < 32)
      for (let Z = $;Z <= Q; Z++)
        this.add(Z);
    else
      this.mask0 = 4294967295, this.mask1 = 4294967295, this.mask2 = 4294967295;
  }
  mayHave($) {
    return (this.mask0 & 1 << ($ & 31)) !== 0 && (this.mask1 & 1 << ($ >> 4 & 31)) !== 0 && (this.mask2 & 1 << ($ >> 9 & 31)) !== 0;
  }
  addCoverage($) {
    let Q = $.glyphs();
    for (let Z = 0;Z < Q.length; Z++) {
      let K = Q[Z];
      this.add(K);
    }
  }
  mayIntersect($) {
    return (this.mask0 & $.mask0) !== 0 && (this.mask1 & $.mask1) !== 0 && (this.mask2 & $.mask2) !== 0;
  }
  getMasks() {
    return { mask0: this.mask0, mask1: this.mask1, mask2: this.mask2 };
  }
}
function XV($) {
  let Q = new V6;
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z];
    Q.addCoverage(K);
  }
  return Q;
}
function E1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    switch (J.uint16()) {
      case 1:
        Z.push(zV(J));
        break;
      case 2:
        Z.push(GV(J));
        break;
      case 3:
        Z.push(YV(J));
        break;
    }
  }
  return Z;
}
function zV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = s($, Q), J = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W === 0) {
      J.push(null);
      continue;
    }
    let H = $.sliceFrom(W), _ = H.uint16(), N = H.uint16Array(_), V = [];
    for (let X = 0;X < N.length; X++) {
      let z = N[X], G = H.sliceFrom(z), Y = G.uint16(), j = G.uint16(), E = G.uint16Array(Y - 1), w = Array(E.length);
      for (let A = 0;A < E.length; A++)
        w[A] = E[A];
      let L = w9(G, j);
      V.push({ glyphCount: Y, inputSequence: w, lookupRecords: L });
    }
    J.push(V);
  }
  return { format: 1, coverage: q, ruleSets: J };
}
function GV($) {
  let Q = $.offset16(), Z = $.offset16(), K = $.uint16(), q = $.uint16Array(K), J = s($, Q), U = R6($, Z), W = [];
  for (let H = 0;H < q.length; H++) {
    let _ = q[H];
    if (_ === 0) {
      W.push(null);
      continue;
    }
    let N = $.sliceFrom(_), V = N.uint16(), X = N.uint16Array(V), z = [];
    for (let G = 0;G < X.length; G++) {
      let Y = X[G], j = N.sliceFrom(Y), E = j.uint16(), w = j.uint16(), L = j.uint16Array(E - 1), A = Array(L.length);
      for (let F = 0;F < L.length; F++)
        A[F] = L[F];
      let D = w9(j, w);
      z.push({ glyphCount: E, inputClasses: A, lookupRecords: D });
    }
    W.push(z);
  }
  return { format: 2, coverage: J, classDef: U, classRuleSets: W };
}
function YV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16Array(Q), q = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    q.push(s($, W));
  }
  let J = w9($, Z);
  return { format: 3, coverages: q, lookupRecords: J };
}
function w1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    switch (J.uint16()) {
      case 1:
        Z.push(jV(J));
        break;
      case 2:
        Z.push(EV(J));
        break;
      case 3:
        Z.push(wV(J));
        break;
    }
  }
  return Z;
}
function jV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = s($, Q), J = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W === 0) {
      J.push(null);
      continue;
    }
    let H = $.sliceFrom(W), _ = H.uint16(), N = H.uint16Array(_), V = [];
    for (let X = 0;X < N.length; X++) {
      let z = N[X], G = H.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = Array(j.length);
      for (let T = 0;T < j.length; T++)
        E[T] = j[T];
      let w = G.uint16(), L = G.uint16Array(w - 1), A = Array(L.length);
      for (let T = 0;T < L.length; T++)
        A[T] = L[T];
      let D = G.uint16(), F = G.uint16Array(D), M = Array(F.length);
      for (let T = 0;T < F.length; T++)
        M[T] = F[T];
      let R = G.uint16(), P = w9(G, R);
      V.push({ backtrackSequence: E, inputSequence: A, lookaheadSequence: M, lookupRecords: P });
    }
    J.push(V);
  }
  return { format: 1, coverage: q, chainRuleSets: J };
}
function EV($) {
  let Q = $.offset16(), Z = $.offset16(), K = $.offset16(), q = $.offset16(), J = $.uint16(), U = $.uint16Array(J), W = s($, Q), H = R6($, Z), _ = R6($, K), N = R6($, q), V = [];
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    if (z === 0) {
      V.push(null);
      continue;
    }
    let G = $.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = [];
    for (let w = 0;w < j.length; w++) {
      let L = j[w], A = G.sliceFrom(L), D = A.uint16(), F = A.uint16Array(D), M = Array(F.length);
      for (let O = 0;O < F.length; O++)
        M[O] = F[O];
      let R = A.uint16(), P = A.uint16Array(R - 1), T = Array(P.length);
      for (let O = 0;O < P.length; O++)
        T[O] = P[O];
      let m = A.uint16(), v = A.uint16Array(m), f = Array(v.length);
      for (let O = 0;O < v.length; O++)
        f[O] = v[O];
      let l = A.uint16(), k = w9(A, l);
      E.push({ backtrackClasses: M, inputClasses: T, lookaheadClasses: f, lookupRecords: k });
    }
    V.push(E);
  }
  return { format: 2, coverage: W, backtrackClassDef: H, inputClassDef: _, lookaheadClassDef: N, chainClassRuleSets: V };
}
function wV($) {
  let Q = $.uint16(), Z = $.uint16Array(Q), K = $.uint16(), q = $.uint16Array(K), J = $.uint16(), U = $.uint16Array(J), W = $.uint16(), H = w9($, W), _ = [];
  for (let X = 0;X < Z.length; X++) {
    let z = Z[X];
    _.push(s($, z));
  }
  let N = [];
  for (let X = 0;X < q.length; X++) {
    let z = q[X];
    N.push(s($, z));
  }
  let V = [];
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    V.push(s($, z));
  }
  return { format: 3, backtrackCoverages: _, inputCoverages: N, lookaheadCoverages: V, lookupRecords: H };
}
function w9($, Q) {
  let Z = [];
  for (let K = 0;K < Q; K++)
    Z.push({ sequenceIndex: $.uint16(), lookupListIndex: $.uint16() });
  return Z;
}
function KU($) {
  let Q = $.uint16(), Z = $.int16(), K = $.int16(), q = { xCoordinate: Z, yCoordinate: K };
  if (Q === 2)
    q.anchorPoint = $.uint16();
  else if (Q === 3)
    q.xDeviceOffset = $.uint16(), q.yDeviceOffset = $.uint16();
  return q;
}
function W0($, Q) {
  if (Q === 0)
    return null;
  return KU($.sliceFrom(Q));
}
function A1($) {
  let Q = $.uint16(), Z = [], K = [];
  for (let q = 0;q < Q; q++)
    K.push({ markClass: $.uint16(), anchorOffset: $.uint16() });
  for (let q = 0;q < K.length; q++) {
    let J = K[q], U = KU($.sliceFrom(J.anchorOffset));
    Z.push({ markClass: J.markClass, markAnchor: U });
  }
  return { markRecords: Z };
}
function L1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.uint16(), _ = [];
      for (let X = 0;X < H; X++)
        _.push({ entryOffset: J.uint16(), exitOffset: J.uint16() });
      let N = s(J, W), V = [];
      for (let X = 0;X < _.length; X++) {
        let z = _[X];
        V.push({ entryAnchor: W0(J, z.entryOffset), exitAnchor: W0(J, z.exitOffset) });
      }
      Z.push({ coverage: N, entryExitRecords: V });
    }
  }
  return Z;
}
function F1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.offset16(), _ = J.uint16(), N = J.offset16(), V = J.offset16(), X = s(J, W), z = s(J, H), G = A1(J.sliceFrom(N)), Y = J.sliceFrom(V), j = Y.uint16(), E = [], w = [];
      for (let L = 0;L < j; L++) {
        let A = [];
        for (let D = 0;D < _; D++)
          A.push(Y.uint16());
        w.push(A);
      }
      for (let L = 0;L < w.length; L++) {
        let A = w[L], D = [];
        for (let F = 0;F < A.length; F++) {
          let M = A[F];
          D.push(W0(Y, M));
        }
        E.push({ baseAnchors: D });
      }
      Z.push({ markCoverage: X, baseCoverage: z, markClassCount: _, markArray: G, baseArray: E });
    }
  }
  return Z;
}
function D1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.offset16(), _ = J.uint16(), N = J.offset16(), V = J.offset16(), X = s(J, W), z = s(J, H), G = A1(J.sliceFrom(N)), Y = J.sliceFrom(V), j = Y.uint16(), E = Y.uint16Array(j), w = [];
      for (let L = 0;L < E.length; L++) {
        let A = E[L], D = Y.sliceFrom(A), F = D.uint16(), M = [], R = [];
        for (let P = 0;P < F; P++) {
          let T = [];
          for (let m = 0;m < _; m++)
            T.push(D.uint16());
          R.push(T);
        }
        for (let P = 0;P < R.length; P++) {
          let T = R[P], m = [];
          for (let v = 0;v < T.length; v++) {
            let f = T[v];
            m.push(W0(D, f));
          }
          M.push({ ligatureAnchors: m });
        }
        w.push({ componentRecords: M });
      }
      Z.push({ markCoverage: X, ligatureCoverage: z, markClassCount: _, markArray: G, ligatureArray: w });
    }
  }
  return Z;
}
function B1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.offset16(), _ = J.uint16(), N = J.offset16(), V = J.offset16(), X = s(J, W), z = s(J, H), G = A1(J.sliceFrom(N)), Y = J.sliceFrom(V), j = Y.uint16(), E = [], w = [];
      for (let L = 0;L < j; L++) {
        let A = [];
        for (let D = 0;D < _; D++)
          A.push(Y.uint16());
        w.push(A);
      }
      for (let L = 0;L < w.length; L++) {
        let A = w[L], D = [];
        for (let F = 0;F < A.length; F++) {
          let M = A[F];
          D.push(W0(Y, M));
        }
        E.push({ mark2Anchors: D });
      }
      Z.push({ mark1Coverage: X, mark2Coverage: z, markClassCount: _, mark1Array: G, mark2Array: E });
    }
  }
  return Z;
}
var P8 = { XPlacement: 1, YPlacement: 2, XAdvance: 4, YAdvance: 8, XPlaDevice: 16, YPlaDevice: 32, XAdvDevice: 64, YAdvDevice: 128 };
function qU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset16(), q = $.offset16(), J = $.offset16();
  if (Q === 1 && Z >= 1)
    $.offset32();
  let U = q0($.sliceFrom(K)), W = J0($.sliceFrom(q)), H = $.sliceFrom(J), _ = H.uint16(), N = H.uint16Array(_), V = [];
  for (let X = 0;X < N.length; X++) {
    let z = N[X], G = H.sliceFrom(z), Y = AV(G);
    if (Y)
      V.push(Y);
  }
  return { version: { major: Q, minor: Z }, scriptList: U, featureList: W, lookups: V };
}
function AV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16Array(K), J = Array(q.length);
  for (let N = 0;N < q.length; N++)
    J[N] = q[N];
  let U;
  if (Z & b6.UseMarkFilteringSet)
    U = $.uint16();
  let W = { flag: Z, markFilteringSet: U }, H = (N) => {
    let V = new V6;
    for (let X = 0;X < N.length; X++) {
      let z = N[X];
      V.addCoverage(z.coverage);
    }
    return V;
  }, _ = (N) => {
    let V = new V6;
    for (let X = 0;X < N.length; X++) {
      let z = N[X];
      V.addCoverage(z.markCoverage);
    }
    return V;
  };
  switch (Q) {
    case 1: {
      let N = JU($, J);
      return { type: 1, ...W, subtables: N, digest: H(N) };
    }
    case 2: {
      let N = UU($, J);
      return { type: 2, ...W, subtables: N, digest: H(N) };
    }
    case 3: {
      let N = L1($, J);
      return { type: 3, ...W, subtables: N, digest: H(N) };
    }
    case 4: {
      let N = F1($, J);
      return { type: 4, ...W, subtables: N, digest: _(N) };
    }
    case 5: {
      let N = D1($, J);
      return { type: 5, ...W, subtables: N, digest: _(N) };
    }
    case 6: {
      let N = B1($, J), V = new V6;
      for (let X = 0;X < N.length; X++) {
        let z = N[X];
        V.addCoverage(z.mark1Coverage);
      }
      return { type: 6, ...W, subtables: N, digest: V };
    }
    case 7: {
      let N = E1($, J), V = new V6;
      for (let X = 0;X < N.length; X++) {
        let z = N[X];
        if ("coverage" in z && z.coverage)
          V.addCoverage(z.coverage);
      }
      return { type: 7, ...W, subtables: N, digest: V };
    }
    case 8: {
      let N = w1($, J), V = new V6;
      for (let X = 0;X < N.length; X++) {
        let z = N[X];
        if ("coverage" in z && z.coverage)
          V.addCoverage(z.coverage);
        else if ("inputCoverages" in z && z.inputCoverages?.[0])
          V.addCoverage(z.inputCoverages[0]);
      }
      return { type: 8, ...W, subtables: N, digest: V };
    }
    case 9:
      return DV($, J, W);
    default:
      return null;
  }
}
function A9($, Q, Z) {
  let K = {};
  if (Q & P8.XPlacement)
    K.xPlacement = $.int16();
  if (Q & P8.YPlacement)
    K.yPlacement = $.int16();
  if (Q & P8.XAdvance)
    K.xAdvance = $.int16();
  if (Q & P8.YAdvance)
    K.yAdvance = $.int16();
  let q = Z ?? $;
  if (Q & P8.XPlaDevice) {
    let J = $.uint16();
    if (J !== 0)
      K.xPlaDevice = c8(q, J) ?? undefined;
  }
  if (Q & P8.YPlaDevice) {
    let J = $.uint16();
    if (J !== 0)
      K.yPlaDevice = c8(q, J) ?? undefined;
  }
  if (Q & P8.XAdvDevice) {
    let J = $.uint16();
    if (J !== 0)
      K.xAdvDevice = c8(q, J) ?? undefined;
  }
  if (Q & P8.YAdvDevice) {
    let J = $.uint16();
    if (J !== 0)
      K.yAdvDevice = c8(q, J) ?? undefined;
  }
  return K;
}
function JU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q), U = $.sliceFrom(q), W = U.uint16();
    if (W === 1) {
      let H = U.offset16(), _ = U.uint16(), N = A9(U, _, J), V = s(J, H);
      Z.push({ format: 1, coverage: V, valueFormat: _, value: N });
    } else if (W === 2) {
      let H = U.offset16(), _ = U.uint16(), N = U.uint16(), V = [];
      for (let z = 0;z < N; z++)
        V.push(A9(U, _, J));
      let X = s(J, H);
      Z.push({ format: 2, coverage: X, valueFormat: _, values: V });
    }
  }
  return Z;
}
function UU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q), U = $.sliceFrom(q), W = U.uint16();
    if (W === 1)
      Z.push(LV(U, J));
    else if (W === 2)
      Z.push(FV(U, J));
  }
  return Z;
}
function LV($, Q) {
  let Z = $.offset16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.uint16Array(J), W = s(Q, Z), H = [];
  for (let _ = 0;_ < U.length; _++) {
    let N = U[_], V = Q.sliceFrom(N), X = Q.sliceFrom(N), z = X.uint16(), G = [];
    for (let Y = 0;Y < z; Y++) {
      let j = X.uint16(), E = A9(X, K, V), w = A9(X, q, V);
      G.push({ secondGlyph: j, value1: E, value2: w });
    }
    H.push({ pairValueRecords: G });
  }
  return { format: 1, coverage: W, valueFormat1: K, valueFormat2: q, pairSets: H };
}
function FV($, Q) {
  let Z = $.offset16(), K = $.uint16(), q = $.uint16(), J = $.offset16(), U = $.offset16(), W = $.uint16(), H = $.uint16(), _ = s(Q, Z), N = R6(Q, J), V = R6(Q, U), X = [];
  for (let z = 0;z < W; z++) {
    let G = [];
    for (let Y = 0;Y < H; Y++) {
      let j = A9($, K, Q), E = A9($, q, Q);
      G.push({ value1: j, value2: E });
    }
    X.push({ class2Records: G });
  }
  return { format: 2, coverage: _, valueFormat1: K, valueFormat2: q, classDef1: N, classDef2: V, class1Count: W, class2Count: H, class1Records: X };
}
function DV($, Q, Z) {
  if (Q.length === 0)
    return null;
  let K = [];
  for (let W = 0;W < Q.length; W++) {
    let H = Q[W], _ = $.sliceFrom(H);
    if (_.uint16() !== 1)
      continue;
    let V = _.uint16(), X = _.uint32();
    K.push({ type: V, reader: _.sliceFrom(X) });
  }
  if (K.length === 0)
    return null;
  let q = K[0]?.type, J = (W) => {
    let H = new V6;
    for (let _ = 0;_ < W.length; _++) {
      let N = W[_];
      H.addCoverage(N.coverage);
    }
    return H;
  }, U = (W) => {
    let H = new V6;
    for (let _ = 0;_ < W.length; _++) {
      let N = W[_];
      H.addCoverage(N.markCoverage);
    }
    return H;
  };
  switch (q) {
    case 1: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...JU(_.reader, [0]));
      }
      return { type: 1, ...Z, subtables: W, digest: J(W) };
    }
    case 2: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...UU(_.reader, [0]));
      }
      return { type: 2, ...Z, subtables: W, digest: J(W) };
    }
    case 3: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...L1(_.reader, [0]));
      }
      return { type: 3, ...Z, subtables: W, digest: J(W) };
    }
    case 4: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...F1(_.reader, [0]));
      }
      return { type: 4, ...Z, subtables: W, digest: U(W) };
    }
    case 5: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...D1(_.reader, [0]));
      }
      return { type: 5, ...Z, subtables: W, digest: U(W) };
    }
    case 6: {
      let W = [];
      for (let _ = 0;_ < K.length; _++) {
        let N = K[_];
        W.push(...B1(N.reader, [0]));
      }
      let H = new V6;
      for (let _ = 0;_ < W.length; _++) {
        let N = W[_];
        H.addCoverage(N.mark1Coverage);
      }
      return { type: 6, ...Z, subtables: W, digest: H };
    }
    case 7: {
      let W = [];
      for (let _ = 0;_ < K.length; _++) {
        let N = K[_];
        W.push(...E1(N.reader, [0]));
      }
      let H = new V6;
      for (let _ = 0;_ < W.length; _++) {
        let N = W[_];
        if ("coverage" in N && N.coverage)
          H.addCoverage(N.coverage);
      }
      return { type: 7, ...Z, subtables: W, digest: H };
    }
    case 8: {
      let W = [];
      for (let _ = 0;_ < K.length; _++) {
        let N = K[_];
        W.push(...w1(N.reader, [0]));
      }
      let H = new V6;
      for (let _ = 0;_ < W.length; _++) {
        let N = W[_];
        if ("coverage" in N && N.coverage)
          H.addCoverage(N.coverage);
        else if ("inputCoverages" in N && N.inputCoverages?.[0])
          H.addCoverage(N.inputCoverages[0]);
      }
      return { type: 8, ...Z, subtables: W, digest: H };
    }
    default:
      return null;
  }
}
function BV($, Q) {
  let Z = 0, K = $.length - 1;
  while (Z <= K) {
    let q = Z + K >>> 1, J = $[q], U = J.secondGlyph;
    if (U < Q)
      Z = q + 1;
    else if (U > Q)
      K = q - 1;
    else
      return J;
  }
  return null;
}
function R1($, Q, Z, K, q) {
  for (let J = 0;J < $.subtables.length; J++) {
    let U = $.subtables[J], W = U.coverage.get(Q);
    if (W === null)
      continue;
    if (U.format === 1) {
      let H = U.pairSets[W];
      if (!H)
        continue;
      let _ = BV(H.pairValueRecords, Z);
      if (_) {
        let N = _.value1.xAdvance, V = _.value2.xAdvance;
        if (N)
          K.xAdvance += N;
        if (V)
          q.xAdvance += V;
        return true;
      }
    } else if (U.format === 2) {
      let H = U.classDef1.get(Q), _ = U.class1Records;
      if (H >= _.length)
        continue;
      let N = _[H], V = U.classDef2.get(Z), X = N.class2Records;
      if (V >= X.length)
        continue;
      let z = X[V], G = z.value1.xAdvance, Y = z.value2.xAdvance;
      if (G)
        K.xAdvance += G;
      if (Y)
        q.xAdvance += Y;
      return true;
    }
  }
  return false;
}
function M1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    switch (J.uint16()) {
      case 1:
        Z.push(RV(J));
        break;
      case 2:
        Z.push(MV(J));
        break;
      case 3:
        Z.push(TV(J));
        break;
    }
  }
  return Z;
}
function RV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = s($, Q), J = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W === 0) {
      J.push(null);
      continue;
    }
    let H = $.sliceFrom(W), _ = H.uint16(), N = H.uint16Array(_), V = [];
    for (let X = 0;X < N.length; X++) {
      let z = N[X], G = H.sliceFrom(z), Y = G.uint16(), j = G.uint16(), E = G.uint16Array(Y - 1), w = Array(E.length);
      for (let A = 0;A < E.length; A++)
        w[A] = E[A];
      let L = L9(G, j);
      V.push({ glyphCount: Y, inputSequence: w, lookupRecords: L });
    }
    J.push(V);
  }
  return { format: 1, coverage: q, ruleSets: J };
}
function MV($) {
  let Q = $.offset16(), Z = $.offset16(), K = $.uint16(), q = $.uint16Array(K), J = s($, Q), U = R6($, Z), W = [];
  for (let H = 0;H < q.length; H++) {
    let _ = q[H];
    if (_ === 0) {
      W.push(null);
      continue;
    }
    let N = $.sliceFrom(_), V = N.uint16(), X = N.uint16Array(V), z = [];
    for (let G = 0;G < X.length; G++) {
      let Y = X[G], j = N.sliceFrom(Y), E = j.uint16(), w = j.uint16(), L = j.uint16Array(E - 1), A = Array(L.length);
      for (let F = 0;F < L.length; F++)
        A[F] = L[F];
      let D = L9(j, w);
      z.push({ glyphCount: E, inputClasses: A, lookupRecords: D });
    }
    W.push(z);
  }
  return { format: 2, coverage: J, classDef: U, classRuleSets: W };
}
function TV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16Array(Q), q = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    q.push(s($, W));
  }
  let J = L9($, Z);
  return { format: 3, coverages: q, lookupRecords: J };
}
function T1($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    switch (J.uint16()) {
      case 1:
        Z.push(OV(J));
        break;
      case 2:
        Z.push(PV(J));
        break;
      case 3:
        Z.push(IV(J));
        break;
    }
  }
  return Z;
}
function OV($) {
  let Q = $.offset16(), Z = $.uint16(), K = $.uint16Array(Z), q = s($, Q), J = [];
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W === 0) {
      J.push(null);
      continue;
    }
    let H = $.sliceFrom(W), _ = H.uint16(), N = H.uint16Array(_), V = [];
    for (let X = 0;X < N.length; X++) {
      let z = N[X], G = H.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = Array(j.length);
      for (let T = 0;T < j.length; T++)
        E[T] = j[T];
      let w = G.uint16(), L = G.uint16Array(w - 1), A = Array(L.length);
      for (let T = 0;T < L.length; T++)
        A[T] = L[T];
      let D = G.uint16(), F = G.uint16Array(D), M = Array(F.length);
      for (let T = 0;T < F.length; T++)
        M[T] = F[T];
      let R = G.uint16(), P = L9(G, R);
      V.push({ backtrackSequence: E, inputSequence: A, lookaheadSequence: M, lookupRecords: P });
    }
    J.push(V);
  }
  return { format: 1, coverage: q, chainRuleSets: J };
}
function PV($) {
  let Q = $.offset16(), Z = $.offset16(), K = $.offset16(), q = $.offset16(), J = $.uint16(), U = $.uint16Array(J), W = s($, Q), H = R6($, Z), _ = R6($, K), N = R6($, q), V = [];
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    if (z === 0) {
      V.push(null);
      continue;
    }
    let G = $.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = [];
    for (let w = 0;w < j.length; w++) {
      let L = j[w], A = G.sliceFrom(L), D = A.uint16(), F = A.uint16Array(D), M = Array(F.length);
      for (let O = 0;O < F.length; O++)
        M[O] = F[O];
      let R = A.uint16(), P = A.uint16Array(R - 1), T = Array(P.length);
      for (let O = 0;O < P.length; O++)
        T[O] = P[O];
      let m = A.uint16(), v = A.uint16Array(m), f = Array(v.length);
      for (let O = 0;O < v.length; O++)
        f[O] = v[O];
      let l = A.uint16(), k = L9(A, l);
      E.push({ backtrackClasses: M, inputClasses: T, lookaheadClasses: f, lookupRecords: k });
    }
    V.push(E);
  }
  return { format: 2, coverage: W, backtrackClassDef: H, inputClassDef: _, lookaheadClassDef: N, chainClassRuleSets: V };
}
function IV($) {
  let Q = $.uint16(), Z = $.uint16Array(Q), K = $.uint16(), q = $.uint16Array(K), J = $.uint16(), U = $.uint16Array(J), W = $.uint16(), H = L9($, W), _ = [];
  for (let X = 0;X < Z.length; X++) {
    let z = Z[X];
    _.push(s($, z));
  }
  let N = [];
  for (let X = 0;X < q.length; X++) {
    let z = q[X];
    N.push(s($, z));
  }
  let V = [];
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    V.push(s($, z));
  }
  return { format: 3, backtrackCoverages: _, inputCoverages: N, lookaheadCoverages: V, lookupRecords: H };
}
function L9($, Q) {
  let Z = [];
  for (let K = 0;K < Q; K++)
    Z.push({ sequenceIndex: $.uint16(), lookupListIndex: $.uint16() });
  return Z;
}
function WU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset16(), q = $.offset16(), J = $.offset16();
  if (Q === 1 && Z >= 1)
    $.offset32();
  let U = q0($.sliceFrom(K)), W = J0($.sliceFrom(q)), H = $.sliceFrom(J), _ = H.uint16(), N = H.uint16Array(_), V = [];
  for (let X = 0;X < N.length; X++) {
    let z = N[X], G = H.sliceFrom(z), Y = vV(G, H, z);
    if (Y)
      V.push(Y);
  }
  return { version: { major: Q, minor: Z }, scriptList: U, featureList: W, lookups: V };
}
function vV($, Q, Z) {
  let K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.uint16Array(J), W = Array(U.length);
  for (let V = 0;V < U.length; V++)
    W[V] = U[V];
  let H;
  if (q & b6.UseMarkFilteringSet)
    H = $.uint16();
  let _ = { flag: q, markFilteringSet: H }, N = (V) => {
    let X = new V6;
    for (let z = 0;z < V.length; z++) {
      let G = V[z];
      X.addCoverage(G.coverage);
    }
    return X;
  };
  switch (K) {
    case 1: {
      let V = HU($, W);
      return { type: 1, ..._, subtables: V, digest: N(V) };
    }
    case 2: {
      let V = _U($, W);
      return { type: 2, ..._, subtables: V, digest: N(V) };
    }
    case 3: {
      let V = NU($, W);
      return { type: 3, ..._, subtables: V, digest: N(V) };
    }
    case 4: {
      let V = VU($, W);
      return { type: 4, ..._, subtables: V, digest: N(V) };
    }
    case 5: {
      let V = M1($, W), X = new V6;
      for (let z = 0;z < V.length; z++) {
        let G = V[z];
        if ("coverage" in G && G.coverage)
          X.addCoverage(G.coverage);
      }
      return { type: 5, ..._, subtables: V, digest: X };
    }
    case 6: {
      let V = T1($, W), X = new V6;
      for (let z = 0;z < V.length; z++) {
        let G = V[z];
        if ("coverage" in G && G.coverage)
          X.addCoverage(G.coverage);
        else if ("inputCoverages" in G && G.inputCoverages?.[0])
          X.addCoverage(G.inputCoverages[0]);
      }
      return { type: 6, ..._, subtables: V, digest: X };
    }
    case 7:
      return kV($, W, _);
    case 8: {
      let V = XU($, W);
      return { type: 8, ..._, subtables: V, digest: N(V) };
    }
    default:
      return null;
  }
}
function HU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q), U = J.uint16();
    if (U === 1) {
      let W = J.offset16(), H = J.int16(), _ = s(J, W);
      Z.push({ format: 1, coverage: _, deltaGlyphId: H });
    } else if (U === 2) {
      let W = J.offset16(), H = J.uint16(), _ = J.uint16Array(H), N = Array(_.length);
      for (let X = 0;X < _.length; X++)
        N[X] = _[X];
      let V = s(J, W);
      Z.push({ format: 2, coverage: V, substituteGlyphIds: N });
    }
  }
  return Z;
}
function _U($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.uint16(), _ = J.uint16Array(H), N = s(J, W), V = [];
      for (let X = 0;X < _.length; X++) {
        let z = _[X], G = J.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = Array(j.length);
        for (let w = 0;w < j.length; w++)
          E[w] = j[w];
        V.push(E);
      }
      Z.push({ coverage: N, sequences: V });
    }
  }
  return Z;
}
function NU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.uint16(), _ = J.uint16Array(H), N = s(J, W), V = [];
      for (let X = 0;X < _.length; X++) {
        let z = _[X], G = J.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = Array(j.length);
        for (let w = 0;w < j.length; w++)
          E[w] = j[w];
        V.push(E);
      }
      Z.push({ coverage: N, alternateSets: V });
    }
  }
  return Z;
}
function VU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.uint16(), _ = J.uint16Array(H), N = s(J, W), V = [];
      for (let X = 0;X < _.length; X++) {
        let z = _[X], G = J.sliceFrom(z), Y = G.uint16(), j = G.uint16Array(Y), E = [];
        for (let w = 0;w < j.length; w++) {
          let L = j[w], A = G.sliceFrom(L), D = A.uint16(), F = A.uint16(), M = A.uint16Array(F - 1), R = Array(M.length);
          for (let P = 0;P < M.length; P++)
            R[P] = M[P];
          E.push({ ligatureGlyph: D, componentGlyphIds: R });
        }
        V.push({ ligatures: E });
      }
      Z.push({ coverage: N, ligatureSets: V });
    }
  }
  return Z;
}
function XU($, Q) {
  let Z = [];
  for (let K = 0;K < Q.length; K++) {
    let q = Q[K], J = $.sliceFrom(q);
    if (J.uint16() === 1) {
      let W = J.offset16(), H = J.uint16(), _ = J.uint16Array(H), N = J.uint16(), V = J.uint16Array(N), X = J.uint16(), z = J.uint16Array(X), G = Array(z.length);
      for (let w = 0;w < z.length; w++)
        G[w] = z[w];
      let Y = s(J, W), j = [];
      for (let w = 0;w < _.length; w++) {
        let L = _[w];
        j.push(s(J, L));
      }
      let E = [];
      for (let w = 0;w < V.length; w++) {
        let L = V[w];
        E.push(s(J, L));
      }
      Z.push({ coverage: Y, backtrackCoverages: j, lookaheadCoverages: E, substituteGlyphIds: G });
    }
  }
  return Z;
}
function kV($, Q, Z) {
  if (Q.length === 0)
    return null;
  let K = [];
  for (let W = 0;W < Q.length; W++) {
    let H = Q[W], _ = $.sliceFrom(H);
    if (_.uint16() !== 1)
      continue;
    let V = _.uint16(), X = _.uint32();
    K.push({ type: V, reader: _.sliceFrom(X) });
  }
  if (K.length === 0)
    return null;
  let q = K[0]?.type, J = K.map((W, H) => 0), U = (W) => {
    let H = new V6;
    for (let _ = 0;_ < W.length; _++) {
      let N = W[_];
      H.addCoverage(N.coverage);
    }
    return H;
  };
  switch (q) {
    case 1: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...HU(_.reader, [0]));
      }
      return { type: 1, ...Z, subtables: W, digest: U(W) };
    }
    case 2: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(..._U(_.reader, [0]));
      }
      return { type: 2, ...Z, subtables: W, digest: U(W) };
    }
    case 3: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...NU(_.reader, [0]));
      }
      return { type: 3, ...Z, subtables: W, digest: U(W) };
    }
    case 4: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...VU(_.reader, [0]));
      }
      return { type: 4, ...Z, subtables: W, digest: U(W) };
    }
    case 5: {
      let W = [];
      for (let _ = 0;_ < K.length; _++) {
        let N = K[_];
        W.push(...M1(N.reader, [0]));
      }
      let H = new V6;
      for (let _ = 0;_ < W.length; _++) {
        let N = W[_];
        if ("coverage" in N && N.coverage)
          H.addCoverage(N.coverage);
      }
      return { type: 5, ...Z, subtables: W, digest: H };
    }
    case 6: {
      let W = [];
      for (let _ = 0;_ < K.length; _++) {
        let N = K[_];
        W.push(...T1(N.reader, [0]));
      }
      let H = new V6;
      for (let _ = 0;_ < W.length; _++) {
        let N = W[_];
        if ("coverage" in N && N.coverage)
          H.addCoverage(N.coverage);
        else if ("inputCoverages" in N && N.inputCoverages?.[0])
          H.addCoverage(N.inputCoverages[0]);
      }
      return { type: 6, ...Z, subtables: W, digest: H };
    }
    case 8: {
      let W = [];
      for (let H = 0;H < K.length; H++) {
        let _ = K[H];
        W.push(...XU(_.reader, [0]));
      }
      return { type: 8, ...Z, subtables: W, digest: U(W) };
    }
    default:
      return null;
  }
}
function H0($, Q) {
  for (let Z = 0;Z < $.subtables.length; Z++) {
    let K = $.subtables[Z], q = K.coverage.get(Q);
    if (q === null)
      continue;
    if (K.format === 1 && K.deltaGlyphId !== undefined)
      return Q + K.deltaGlyphId & 65535;
    if (K.format === 2 && K.substituteGlyphIds)
      return K.substituteGlyphIds[q] ?? null;
  }
  return null;
}
function zU($, Q, Z, K) {
  let q = Q[K];
  if (q === undefined)
    return null;
  for (let J = 0;J < $.subtables.length; J++) {
    let U = $.subtables[J], W = U.coverage.get(q);
    if (W === null)
      continue;
    let H = U.ligatureSets[W];
    if (!H)
      continue;
    for (let _ = 0;_ < H.ligatures.length; _++) {
      let N = H.ligatures[_], V = N.componentGlyphIds.length;
      if (K + 1 + V > Z)
        continue;
      let X = true;
      for (let z = 0;z < V; z++)
        if (Q[K + 1 + z] !== N.componentGlyphIds[z]) {
          X = false;
          break;
        }
      if (X)
        return { ligatureGlyph: N.ligatureGlyph, consumed: 1 + V };
    }
  }
  return null;
}
function GU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.fixed(), q = $.uint32(), J = $.uint32();
  if (J !== 1594834165)
    throw Error(`Invalid head table magic number: 0x${J.toString(16)}`);
  let U = $.uint16(), W = $.uint16(), H = $.longDateTime(), _ = $.longDateTime(), N = $.fword(), V = $.fword(), X = $.fword(), z = $.fword(), G = $.uint16(), Y = $.uint16(), j = $.int16(), E = $.int16(), w = $.int16();
  return { majorVersion: Q, minorVersion: Z, fontRevision: K, checksumAdjustment: q, magicNumber: J, flags: U, unitsPerEm: W, created: H, modified: _, xMin: N, yMin: V, xMax: X, yMax: z, macStyle: G, lowestRecPPEM: Y, fontDirectionHint: j, indexToLocFormat: E, glyphDataFormat: w };
}
function YU($) {
  return { majorVersion: $.uint16(), minorVersion: $.uint16(), ascender: $.fword(), descender: $.fword(), lineGap: $.fword(), advanceWidthMax: $.ufword(), minLeftSideBearing: $.fword(), minRightSideBearing: $.fword(), xMaxExtent: $.fword(), caretSlopeRise: $.int16(), caretSlopeRun: $.int16(), caretOffset: $.int16(), reserved1: $.int16(), reserved2: $.int16(), reserved3: $.int16(), reserved4: $.int16(), metricDataFormat: $.int16(), numberOfHMetrics: $.uint16() };
}
function O1($) {
  return { instructions: $.bytes($.remaining) };
}
function P1($) {
  return { instructions: $.bytes($.remaining) };
}
function I1($) {
  let Q = Math.floor($.remaining / 2), Z = new Int16Array(Q);
  for (let K = 0;K < Q; K++)
    Z[K] = $.int16();
  return { values: Z };
}
function jU($, Q, Z) {
  let K = Array(Q);
  for (let U = 0;U < Q; U++)
    K[U] = { advanceWidth: $.ufword(), lsb: $.fword() };
  let q = Z - Q, J = Array(q);
  for (let U = 0;U < q; U++)
    J[U] = $.fword();
  return { hMetrics: K, leftSideBearings: J };
}
function EU($, Q) {
  if (Q < $.hMetrics.length)
    return $.hMetrics[Q]?.advanceWidth;
  return $.hMetrics[$.hMetrics.length - 1]?.advanceWidth;
}
function wU($, Q) {
  if (Q < $.hMetrics.length)
    return $.hMetrics[Q]?.lsb;
  let Z = Q - $.hMetrics.length;
  return $.leftSideBearings[Z] ?? 0;
}
var CV = { ShrinkGpos: 0, DisableGpos: 1, ShrinkGsub: 2, DisableGsub: 3, EnableGpos: 4, EnableGsub: 5, MaxExtendGpos: 6, MaxExtendGsub: 7 };
function I8($, Q) {
  if (Q === 0)
    return null;
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = [];
  for (let J = 0;J < K; J++)
    q.push(Z.uint16());
  return { lookupIndices: q };
}
function AU($, Q) {
  if (Q === 0)
    return null;
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = [];
  for (let J = 0;J < K; J++)
    q.push(Z.uint16());
  return { lookupIndices: q };
}
function SV($, Q) {
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = Z.uint16(), J = Z.uint16(), U = Z.uint16(), W = Z.uint16(), H = Z.uint16(), _ = Z.uint16(), N = Z.uint16(), V = Z.uint16(), X = Z.uint16();
  return { shrinkageEnableGsub: I8($, Q + K), shrinkageDisableGsub: I8($, Q + q), shrinkageEnableGpos: I8($, Q + J), shrinkageDisableGpos: I8($, Q + U), shrinkageJstfMax: AU($, Q + W), extensionEnableGsub: I8($, Q + H), extensionDisableGsub: I8($, Q + _), extensionEnableGpos: I8($, Q + N), extensionDisableGpos: I8($, Q + V), extensionJstfMax: AU($, Q + X) };
}
function LU($, Q) {
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = [];
  for (let U = 0;U < K; U++)
    q.push(Z.uint16());
  let J = [];
  for (let U = 0;U < q.length; U++) {
    let W = q[U];
    J.push(SV($, Q + W));
  }
  return { priorities: J };
}
function hV($, Q) {
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = Z.uint16(), J = Z.uint16(), U = [];
  for (let N = 0;N < J; N++) {
    let V = Z.uint32(), X = Z.uint16();
    U.push({ tag: V, offset: X });
  }
  let W = [];
  if (K !== 0) {
    let N = $.sliceFrom(Q + K), V = N.uint16();
    for (let X = 0;X < V; X++)
      W.push(N.uint16());
  }
  let H = q !== 0 ? LU($, Q + q) : null, _ = new Map;
  for (let N = 0;N < U.length; N++) {
    let { tag: V, offset: X } = U[N];
    _.set(V, LU($, Q + X));
  }
  return { extenderGlyphs: W, defaultLangSys: H, langSysRecords: _ };
}
function v1($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = [];
  for (let U = 0;U < K; U++) {
    let W = $.uint32(), H = $.uint16();
    q.push({ tag: W, offset: H });
  }
  let J = [];
  for (let U = 0;U < q.length; U++) {
    let { tag: W, offset: H } = q[U], _ = hV($, H);
    J.push({ scriptTag: W, ..._ });
  }
  return { majorVersion: Q, minorVersion: Z, scripts: J };
}
function k1($, Q) {
  return $.scripts.find((K) => K.scriptTag === Q)?.extenderGlyphs ?? [];
}
function C1($, Q, Z) {
  let K = $.scripts.find((q) => q.scriptTag === Q);
  if (!K)
    return [];
  if (Z !== undefined) {
    let q = K.langSysRecords.get(Z);
    if (q)
      return q.priorities;
  }
  return K.defaultLangSys?.priorities ?? [];
}
function S1($) {
  return { enableGsub: $.shrinkageEnableGsub?.lookupIndices ?? [], disableGsub: $.shrinkageDisableGsub?.lookupIndices ?? [], enableGpos: $.shrinkageEnableGpos?.lookupIndices ?? [], disableGpos: $.shrinkageDisableGpos?.lookupIndices ?? [], maxLookups: $.shrinkageJstfMax?.lookupIndices ?? [] };
}
function h1($) {
  return { enableGsub: $.extensionEnableGsub?.lookupIndices ?? [], disableGsub: $.extensionDisableGsub?.lookupIndices ?? [], enableGpos: $.extensionEnableGpos?.lookupIndices ?? [], disableGpos: $.extensionDisableGpos?.lookupIndices ?? [], maxLookups: $.extensionJstfMax?.lookupIndices ?? [] };
}
function FU($) {
  let Q = $.uint16(), Z = [];
  if (Q === 0) {
    let K = $.uint16();
    for (let q = 0;q < K; q++) {
      let J = yV($);
      if (J)
        Z.push(J);
    }
  } else if (Q === 1) {
    $.skip(2);
    let K = $.uint32();
    for (let q = 0;q < K; q++) {
      let J = bV($);
      if (J)
        Z.push(J);
    }
  }
  return { version: Q, subtables: Z };
}
function yV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = { horizontal: (K & 1) !== 0, minimum: (K & 2) !== 0, crossStream: (K & 4) !== 0, override: (K & 8) !== 0 }, J = K >> 8 & 255;
  if (J === 0)
    return DU($, q);
  else if (J === 2)
    return BU($, q, Z - 6);
  return $.skip(Z - 6), null;
}
function bV($) {
  let Q = $.uint32(), Z = $.uint16(), K = $.uint16(), q = { horizontal: (Z & 32768) === 0, minimum: false, crossStream: (Z & 16384) !== 0, override: (Z & 8192) !== 0 }, J = Z & 255;
  if (J === 0)
    return DU($, q);
  else if (J === 2)
    return BU($, q, Q - 8);
  return $.skip(Q - 8), null;
}
function DU($, Q) {
  let Z = $.uint16();
  $.skip(6);
  let K = new Map;
  for (let q = 0;q < Z; q++) {
    let J = $.uint16(), U = $.uint16(), W = $.int16(), H = J << 16 | U;
    K.set(H, W);
  }
  return { format: 0, coverage: Q, pairs: K };
}
function BU($, Q, Z) {
  let K = $.offset, q = $.uint16(), J = $.uint16(), U = $.uint16(), W = $.uint16(), H = new Map;
  $.seek(K + J);
  let _ = $.uint16(), N = $.uint16();
  for (let E = 0;E < N; E++) {
    let w = $.uint16();
    if (w !== 0)
      H.set(_ + E, w);
  }
  let V = new Map;
  $.seek(K + U);
  let X = $.uint16(), z = $.uint16();
  for (let E = 0;E < z; E++) {
    let w = $.uint16();
    if (w !== 0)
      V.set(X + E, w);
  }
  $.seek(K + W);
  let G = q > 0 ? Math.floor(Z / q) : 0, Y = q / 2, j = [];
  for (let E = 0;E < G; E++) {
    let w = [];
    for (let L = 0;L < Y; L++)
      w.push($.int16());
    j.push(w);
  }
  return { format: 2, coverage: Q, rowWidth: q, leftClassTable: H, rightClassTable: V, kerningValues: j };
}
function y1($, Q, Z) {
  let K = 0;
  for (let q = 0;q < $.subtables.length; q++) {
    let J = $.subtables[q];
    if (!J.coverage.horizontal)
      continue;
    if (J.format === 0) {
      let U = Q << 16 | Z, W = J.pairs.get(U);
      if (W !== undefined)
        if (J.coverage.override)
          K = W;
        else
          K += W;
    } else if (J.format === 2) {
      let U = J.leftClassTable.get(Q) ?? 0, W = J.rightClassTable.get(Z) ?? 0;
      if (U > 0 && W > 0) {
        let H = Math.floor(U / 2), _ = Math.floor(W / 2), N = J.kerningValues[H];
        if (N) {
          let V = N[_];
          if (V !== undefined)
            if (J.coverage.override)
              K = V;
            else
              K += V;
        }
      }
    }
  }
  return K;
}
function MU($) {
  let Q = $.uint16();
  $.skip(2);
  let Z = $.uint32(), K = [];
  for (let q = 0;q < Z; q++) {
    let J = mV($);
    if (J)
      K.push(J);
  }
  return { version: Q, nTables: Z, subtables: K };
}
function mV($) {
  let Q = $.uint32(), Z = $.uint32(), K = $.uint16();
  $.skip(2);
  let q = Z & 255, J = { vertical: (Z & 2147483648) !== 0, crossStream: (Z & 1073741824) !== 0, variation: (Z & 536870912) !== 0 }, U = { length: Q, coverage: J, tupleCount: K }, W = $.offset + Q - 12, H = null;
  switch (q) {
    case 0:
      H = gV($, U);
      break;
    case 1:
      H = uV($, U);
      break;
    case 2:
      H = xV($, U);
      break;
    case 6:
      H = fV($, U);
      break;
  }
  return $.seek(W), H;
}
function gV($, Q) {
  let Z = $.uint32();
  $.skip(12);
  let K = [];
  for (let q = 0;q < Z; q++)
    K.push({ left: $.uint16(), right: $.uint16(), value: $.int16() }), $.skip(2);
  return { ...Q, format: 0, nPairs: Z, pairs: K };
}
function uV($, Q) {
  let Z = { nClasses: $.uint32(), classTableOffset: $.offset32(), stateArrayOffset: $.offset32(), entryTableOffset: $.offset32(), valueTableOffset: $.offset32() };
  return { ...Q, format: 1, stateHeader: Z };
}
function xV($, Q) {
  let Z = $.uint16();
  $.skip(2);
  let K = $.offset32(), q = $.offset32(), J = $.offset32(), U = RU($.sliceFrom(K)), W = RU($.sliceFrom(q)), H = $.sliceFrom(J), _ = U.nGlyphs > 0 ? Math.max(...[...U.classes]) + 1 : 0, N = Z / 2, V = new Int16Array(_ * N);
  for (let X = 0;X < V.length; X++)
    V[X] = H.int16();
  return { ...Q, format: 2, rowWidth: Z, leftClassTable: U, rightClassTable: W, kerningArray: V };
}
function RU($) {
  let Q = $.uint16(), Z = $.uint16(), K = new Uint8Array(Z);
  for (let q = 0;q < Z; q++)
    K[q] = $.uint8();
  return { firstGlyph: Q, nGlyphs: Z, classes: K };
}
function fV($, Q) {
  let Z = $.uint32(), K = $.uint16(), q = $.uint16(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = $.offset32();
  return { ...Q, format: 6, flags: Z, rowCount: K, columnCount: q, rowIndexTableOffset: J, columnIndexTableOffset: U, kerningArrayOffset: W, kerningVectorOffset: H };
}
function lV($, Q, Z) {
  for (let K = 0;K < $.subtables.length; K++) {
    let q = $.subtables[K];
    if (q.coverage.vertical)
      continue;
    switch (q.format) {
      case 0: {
        let J = q.pairs, U = 0, W = J.length - 1;
        while (U <= W) {
          let H = U + W >> 1, _ = J[H];
          if (!_)
            break;
          let N = _.left << 16 | _.right, V = Q << 16 | Z;
          if (N === V)
            return _.value;
          else if (N < V)
            U = H + 1;
          else
            W = H - 1;
        }
        break;
      }
      case 2: {
        let { leftClassTable: J, rightClassTable: U } = q;
        if (Q < J.firstGlyph || Q >= J.firstGlyph + J.nGlyphs)
          continue;
        if (Z < U.firstGlyph || Z >= U.firstGlyph + U.nGlyphs)
          continue;
        let W = J.classes[Q - J.firstGlyph], H = U.classes[Z - U.firstGlyph];
        if (W === undefined || H === undefined)
          continue;
        let _ = q.rowWidth / 2, N = W * _ + H;
        if (N < q.kerningArray.length) {
          let V = q.kerningArray[N];
          if (V !== undefined && V !== 0)
            return V;
        }
        break;
      }
    }
  }
  return 0;
}
function c($, Q) {
  let Z = $.int16(), K = $.uint16();
  return { value: Z, device: c8(Q, K) };
}
function dV($) {
  let Q = $, Z = $.int16(), K = $.int16(), q = $.uint16(), J = $.uint16();
  return { scriptPercentScaleDown: Z, scriptScriptPercentScaleDown: K, delimitedSubFormulaMinHeight: q, displayOperatorMinHeight: J, mathLeading: c($, Q), axisHeight: c($, Q), accentBaseHeight: c($, Q), flattenedAccentBaseHeight: c($, Q), subscriptShiftDown: c($, Q), subscriptTopMax: c($, Q), subscriptBaselineDropMin: c($, Q), superscriptShiftUp: c($, Q), superscriptShiftUpCramped: c($, Q), superscriptBottomMin: c($, Q), superscriptBaselineDropMax: c($, Q), subSuperscriptGapMin: c($, Q), superscriptBottomMaxWithSubscript: c($, Q), spaceAfterScript: c($, Q), upperLimitGapMin: c($, Q), upperLimitBaselineRiseMin: c($, Q), lowerLimitGapMin: c($, Q), lowerLimitBaselineDropMin: c($, Q), stackTopShiftUp: c($, Q), stackTopDisplayStyleShiftUp: c($, Q), stackBottomShiftDown: c($, Q), stackBottomDisplayStyleShiftDown: c($, Q), stackGapMin: c($, Q), stackDisplayStyleGapMin: c($, Q), stretchStackTopShiftUp: c($, Q), stretchStackBottomShiftDown: c($, Q), stretchStackGapAboveMin: c($, Q), stretchStackGapBelowMin: c($, Q), fractionNumeratorShiftUp: c($, Q), fractionNumeratorDisplayStyleShiftUp: c($, Q), fractionDenominatorShiftDown: c($, Q), fractionDenominatorDisplayStyleShiftDown: c($, Q), fractionNumeratorGapMin: c($, Q), fractionNumDisplayStyleGapMin: c($, Q), fractionRuleThickness: c($, Q), fractionDenominatorGapMin: c($, Q), fractionDenomDisplayStyleGapMin: c($, Q), skewedFractionHorizontalGap: c($, Q), skewedFractionVerticalGap: c($, Q), overbarVerticalGap: c($, Q), overbarRuleThickness: c($, Q), overbarExtraAscender: c($, Q), underbarVerticalGap: c($, Q), underbarRuleThickness: c($, Q), underbarExtraDescender: c($, Q), radicalVerticalGap: c($, Q), radicalDisplayStyleVerticalGap: c($, Q), radicalRuleThickness: c($, Q), radicalExtraAscender: c($, Q), radicalKernBeforeDegree: c($, Q), radicalKernAfterDegree: c($, Q), radicalDegreeBottomRaisePercent: $.int16() };
}
function nV($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let J = 0;J < Z; J++)
    K.push(c($, $));
  return { coverage: s($, Q), values: K };
}
function pV($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let J = 0;J < Z; J++)
    K.push(c($, $));
  return { coverage: s($, Q), values: K };
}
function _5($, Q) {
  let Z = $.sliceFrom(Q), K = Z.uint16(), q = [];
  for (let U = 0;U < K; U++)
    q.push(c(Z, Z));
  let J = [];
  for (let U = 0;U < K + 1; U++)
    J.push(c(Z, Z));
  return { correctionHeights: q, kernValues: J };
}
function cV($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let U = 0;U < Z; U++)
    K.push({ topRightOffset: $.uint16(), topLeftOffset: $.uint16(), bottomRightOffset: $.uint16(), bottomLeftOffset: $.uint16() });
  let q = s($, Q), J = K.map((U) => ({ topRight: U.topRightOffset !== 0 ? _5($, U.topRightOffset) : null, topLeft: U.topLeftOffset !== 0 ? _5($, U.topLeftOffset) : null, bottomRight: U.bottomRightOffset !== 0 ? _5($, U.bottomRightOffset) : null, bottomLeft: U.bottomLeftOffset !== 0 ? _5($, U.bottomLeftOffset) : null }));
  return { coverage: q, kernInfo: J };
}
function oV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = null;
  if (Q !== 0)
    J = nV($.sliceFrom(Q));
  let U = null;
  if (Z !== 0)
    U = pV($.sliceFrom(Z));
  let W = null;
  if (K !== 0)
    W = { coverage: s($, K) };
  let H = null;
  if (q !== 0)
    H = cV($.sliceFrom(q));
  return { italicsCorrection: J, topAccentAttachment: U, extendedShapeCoverage: W, kernInfo: H };
}
function iV($) {
  let Q = c($, $), Z = $.uint16(), K = [];
  for (let q = 0;q < Z; q++)
    K.push({ glyphId: $.uint16(), startConnectorLength: $.uint16(), endConnectorLength: $.uint16(), fullAdvance: $.uint16(), partFlags: $.uint16() });
  return { italicsCorrection: Q, parts: K };
}
function TU($) {
  let Q = $.uint16(), Z = $.uint16(), K = [];
  for (let J = 0;J < Z; J++)
    K.push({ variantGlyph: $.uint16(), advanceMeasurement: $.uint16() });
  let q = null;
  if (Q !== 0)
    q = iV($.sliceFrom(Q));
  return { glyphAssembly: q, variants: K };
}
function sV($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = [];
  for (let X = 0;X < q; X++)
    U.push($.uint16());
  let W = [];
  for (let X = 0;X < J; X++)
    W.push($.uint16());
  let H = Z !== 0 ? s($, Z) : null, _ = K !== 0 ? s($, K) : null, N = U.map((X) => TU($.sliceFrom(X))), V = W.map((X) => TU($.sliceFrom(X)));
  return { minConnectorOverlap: Q, vertGlyphCoverage: H, horizGlyphCoverage: _, vertGlyphConstruction: N, horizGlyphConstruction: V };
}
function b1($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = null;
  if (K !== 0)
    U = dV($.sliceFrom(K));
  let W = null;
  if (q !== 0)
    W = oV($.sliceFrom(q));
  let H = null;
  if (J !== 0)
    H = sV($.sliceFrom(J));
  return { majorVersion: Q, minorVersion: Z, constants: U, glyphInfo: W, variants: H };
}
function rV($, Q) {
  let Z = $.glyphInfo?.italicsCorrection;
  if (!Z)
    return null;
  let K = Z.coverage.get(Q);
  if (K === null)
    return null;
  return Z.values[K] ?? null;
}
function aV($, Q) {
  let Z = $.glyphInfo?.topAccentAttachment;
  if (!Z)
    return null;
  let K = Z.coverage.get(Q);
  if (K === null)
    return null;
  return Z.values[K] ?? null;
}
function tV($, Q) {
  let Z = $.glyphInfo?.extendedShapeCoverage;
  if (!Z)
    return false;
  return Z.coverage.get(Q) !== null;
}
function eV($, Q) {
  let Z = $.variants;
  if (!Z?.vertGlyphCoverage)
    return null;
  let K = Z.vertGlyphCoverage.get(Q);
  if (K === null)
    return null;
  return Z.vertGlyphConstruction[K]?.variants ?? null;
}
function $X($, Q) {
  let Z = $.variants;
  if (!Z?.horizGlyphCoverage)
    return null;
  let K = Z.horizGlyphCoverage.get(Q);
  if (K === null)
    return null;
  return Z.horizGlyphConstruction[K]?.variants ?? null;
}
function QX($, Q) {
  let Z = $.variants;
  if (!Z?.vertGlyphCoverage)
    return null;
  let K = Z.vertGlyphCoverage.get(Q);
  if (K === null)
    return null;
  return Z.vertGlyphConstruction[K]?.glyphAssembly ?? null;
}
function ZX($, Q) {
  let Z = $.variants;
  if (!Z?.horizGlyphCoverage)
    return null;
  let K = Z.horizGlyphCoverage.get(Q);
  if (K === null)
    return null;
  return Z.horizGlyphConstruction[K]?.glyphAssembly ?? null;
}
function OU($) {
  let Q = $.uint32(), Z = $.uint16();
  if (Q === 20480)
    return { version: Q, numGlyphs: Z };
  if (Q === 65536)
    return { version: Q, numGlyphs: Z, maxPoints: $.uint16(), maxContours: $.uint16(), maxCompositePoints: $.uint16(), maxCompositeContours: $.uint16(), maxZones: $.uint16(), maxTwilightPoints: $.uint16(), maxStorage: $.uint16(), maxFunctionDefs: $.uint16(), maxInstructionDefs: $.uint16(), maxStackElements: $.uint16(), maxSizeOfInstructions: $.uint16(), maxComponentElements: $.uint16(), maxComponentDepth: $.uint16() };
  throw Error(`Unknown maxp version: 0x${Q.toString(16)}`);
}
var m1;
((J) => {
  J[J.Rearrangement = 0] = "Rearrangement";
  J[J.Contextual = 1] = "Contextual";
  J[J.Ligature = 2] = "Ligature";
  J[J.NonContextual = 4] = "NonContextual";
  J[J.Insertion = 5] = "Insertion";
})(m1 ||= {});
function PU($) {
  let Q = $.uint16();
  if ($.skip(2), Q < 2)
    return { version: Q, chains: [] };
  let Z = $.uint32(), K = [];
  for (let q = 0;q < Z; q++) {
    let J = KX($);
    K.push(J);
  }
  return { version: Q, chains: K };
}
function KX($) {
  let Q = $.uint32(), Z = $.uint32(), K = $.uint32(), q = $.uint32(), J = [];
  for (let W = 0;W < K; W++)
    J.push({ featureType: $.uint16(), featureSetting: $.uint16(), enableFlags: $.uint32(), disableFlags: $.uint32() });
  let U = [];
  for (let W = 0;W < q; W++) {
    let H = qX($);
    if (H)
      U.push(H);
  }
  return { defaultFlags: Q, features: J, subtables: U };
}
function qX($) {
  let Q = $.uint32(), Z = $.uint32(), K = $.uint32(), q = Z & 255, J = { vertical: (Z & 2147483648) !== 0, descending: (Z & 1073741824) !== 0, logical: (Z & 268435456) !== 0 }, W = $.offset + Q - 12, H = null;
  switch (q) {
    case 0:
      H = HX($, J, K);
      break;
    case 1:
      H = UX($, J, K);
      break;
    case 2:
      H = WX($, J, K);
      break;
    case 4:
      H = JX($, J, K);
      break;
    case 5:
      H = _X($, J, K);
      break;
  }
  return $.seek(W), H;
}
function JX($, Q, Z) {
  let K = NX($);
  return { type: 4, coverage: Q, subFeatureFlags: Z, lookupTable: K };
}
function UX($, Q, Z) {
  let K = $.offset, q = $.uint32(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = $.offset32(), _ = N5($.sliceFrom(K + J));
  return { type: 1, coverage: Q, subFeatureFlags: Z, stateTable: { nClasses: q, classTable: _, stateArray: [] }, substitutionTable: [] };
}
function WX($, Q, Z) {
  let K = $.offset, q = $.uint32(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = $.offset32(), _ = $.offset32(), N = $.offset32(), V = N5($.sliceFrom(K + J));
  return { type: 2, coverage: Q, subFeatureFlags: Z, stateTable: { nClasses: q, classTable: V, stateArray: [] }, ligatureActions: [], components: [], ligatures: [] };
}
function HX($, Q, Z) {
  let K = $.offset, q = $.uint32(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = N5($.sliceFrom(K + J)), _ = $.sliceFrom(K + U), N = $.sliceFrom(K + W), V = [], X = 256;
  for (let Y = 0;Y < 256; Y++)
    V.push({ newState: N.uint16(), flags: N.uint16() });
  let z = [], G = Math.min(256, Math.ceil((W - U) / (q * 2)));
  for (let Y = 0;Y < G; Y++) {
    let j = [];
    for (let E = 0;E < q; E++) {
      let w = _.uint16();
      j.push(V[w] ?? { newState: 0, flags: 0 });
    }
    z.push(j);
  }
  return { type: 0, coverage: Q, subFeatureFlags: Z, stateTable: { nClasses: q, classTable: H, stateArray: z } };
}
function _X($, Q, Z) {
  let K = $.offset, q = $.uint32(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = $.offset32(), _ = N5($.sliceFrom(K + J)), N = $.sliceFrom(K + H), V = [], X = 1024;
  for (let L = 0;L < 1024; L++)
    try {
      V.push(N.uint16());
    } catch {
      break;
    }
  let z = $.sliceFrom(K + W), G = [], Y = 256;
  for (let L = 0;L < Y; L++)
    G.push({ newState: z.uint16(), flags: z.uint16(), currentInsertIndex: z.uint16(), markedInsertIndex: z.uint16() });
  let j = $.sliceFrom(K + U), E = [], w = Math.min(256, Math.ceil((W - U) / (q * 2)));
  for (let L = 0;L < w; L++) {
    let A = [];
    for (let D = 0;D < q; D++) {
      let F = j.uint16();
      A.push(G[F] ?? { newState: 0, flags: 0, currentInsertIndex: 65535, markedInsertIndex: 65535 });
    }
    E.push(A);
  }
  return { type: 5, coverage: Q, subFeatureFlags: Z, stateTable: { nClasses: q, classTable: _, stateArray: E }, insertionGlyphs: V };
}
function NX($) {
  let Q = $.uint16(), Z = new Map;
  switch (Q) {
    case 0:
      break;
    case 2: {
      let K = $.uint16(), q = $.uint16();
      $.skip(6);
      for (let J = 0;J < q; J++) {
        let U = $.uint16(), W = $.uint16(), H = $.uint16();
        for (let _ = W;_ <= U; _++)
          Z.set(_, H);
      }
      break;
    }
    case 4: {
      let K = $.uint16(), q = $.uint16();
      $.skip(6);
      for (let J = 0;J < q; J++) {
        let U = $.uint16(), W = $.uint16(), H = $.uint16();
      }
      break;
    }
    case 6: {
      let K = $.uint16(), q = $.uint16();
      $.skip(6);
      for (let J = 0;J < q; J++) {
        let U = $.uint16(), W = $.uint16();
        Z.set(U, W);
      }
      break;
    }
    case 8: {
      let K = $.uint16(), q = $.uint16();
      for (let J = 0;J < q; J++) {
        let U = $.uint16();
        if (U !== 0)
          Z.set(K + J, U);
      }
      break;
    }
  }
  return { format: Q, mapping: Z };
}
function N5($) {
  let Q = $.uint16(), Z = [];
  if (Q === 2) {
    let K = $.uint16(), q = $.uint16();
    $.skip(6);
    let J = [];
    for (let W = 0;W < q; W++)
      J.push({ last: $.uint16(), first: $.uint16(), classValue: $.uint16() });
    let U = Math.max(...J.map((W) => W.last), 0);
    for (let W = 0;W <= U; W++) {
      let H = J.find((_) => W >= _.first && W <= _.last);
      Z[W] = H?.classValue ?? 1;
    }
  }
  return { format: Q, classArray: Z };
}
function g1($, Q) {
  return $.lookupTable.mapping.get(Q) ?? null;
}
var _0 = { hasc: C("hasc"), hdsc: C("hdsc"), hlgp: C("hlgp"), hcla: C("hcla"), hcld: C("hcld"), hcof: C("hcof"), hcrn: C("hcrn"), hcrs: C("hcrs"), vasc: C("vasc"), vdsc: C("vdsc"), vlgp: C("vlgp"), vcof: C("vcof"), vcrn: C("vcrn"), vcrs: C("vcrs"), xhgt: C("xhgt"), cpht: C("cpht"), sbxs: C("sbxs"), sbys: C("sbys"), sbxo: C("sbxo"), sbyo: C("sbyo"), spxs: C("spxs"), spys: C("spys"), spxo: C("spxo"), spyo: C("spyo"), strs: C("strs"), stro: C("stro"), undo: C("undo"), unds: C("unds"), gsp0: C("gsp0"), gsp1: C("gsp1"), gsp2: C("gsp2"), gsp3: C("gsp3") };
function IU($) {
  let Q = $.uint16(), Z = $.uint16();
  $.uint16();
  let K = $.uint16(), q = $.uint16(), J = $.offset16(), U = [];
  for (let H = 0;H < q; H++)
    if (U.push({ valueTag: $.tag(), deltaSetOuterIndex: $.uint16(), deltaSetInnerIndex: $.uint16() }), K > 8)
      $.skip(K - 8);
  let W = VX($.sliceFrom(J));
  return { majorVersion: Q, minorVersion: Z, itemVariationStore: W, valueRecords: U };
}
function VX($) {
  let Q = $.uint16(), Z = $.offset32(), K = $.uint16(), q = [];
  for (let N = 0;N < K; N++)
    q.push($.offset32());
  let J = $.sliceFrom(Z), U = J.uint16(), W = J.uint16(), H = [];
  for (let N = 0;N < W; N++) {
    let V = [];
    for (let X = 0;X < U; X++)
      V.push({ startCoord: J.f2dot14(), peakCoord: J.f2dot14(), endCoord: J.f2dot14() });
    H.push({ regionAxes: V });
  }
  let _ = [];
  for (let N = 0;N < q.length; N++) {
    let V = q[N], X = $.sliceFrom(V), z = X.uint16(), G = X.uint16(), Y = X.uint16(), j = [];
    for (let D = 0;D < Y; D++)
      j.push(X.uint16());
    let E = (G & 32768) !== 0, w = G & 32767, L = Y - w, A = [];
    for (let D = 0;D < z; D++) {
      let F = [];
      for (let M = 0;M < w; M++)
        if (E)
          F.push(X.int32());
        else
          F.push(X.int16());
      for (let M = 0;M < L; M++)
        if (E)
          F.push(X.int16());
        else
          F.push(X.int8());
      A.push(F);
    }
    _.push({ itemCount: z, regionIndexes: j, deltaSets: A });
  }
  return { format: Q, variationRegions: H, itemVariationData: _ };
}
function N0($, Q, Z) {
  let K = $.valueRecords.find((_) => _.valueTag === Q);
  if (!K)
    return 0;
  let { deltaSetOuterIndex: q, deltaSetInnerIndex: J } = K, U = $.itemVariationStore.itemVariationData[q];
  if (!U || J >= U.itemCount)
    return 0;
  let W = U.deltaSets[J];
  if (!W)
    return 0;
  let H = 0;
  for (let _ = 0;_ < U.regionIndexes.length; _++) {
    let N = U.regionIndexes[_], V = $.itemVariationStore.variationRegions[N];
    if (!V)
      continue;
    let X = e9(V, Z), z = W[_] ?? 0;
    H += X * z;
  }
  return Math.round(H);
}
function XX($, Q) {
  return N0($, _0.hasc, Q);
}
function zX($, Q) {
  return N0($, _0.hdsc, Q);
}
function GX($, Q) {
  return N0($, _0.xhgt, Q);
}
function YX($, Q) {
  return N0($, _0.cpht, Q);
}
var u6 = { Copyright: 0, FontFamily: 1, FontSubfamily: 2, UniqueID: 3, FullName: 4, Version: 5, PostScriptName: 6, Trademark: 7, Manufacturer: 8, Designer: 9, Description: 10, ManufacturerURL: 11, DesignerURL: 12, License: 13, LicenseURL: 14, Reserved: 15, TypographicFamily: 16, TypographicSubfamily: 17, CompatibleFullName: 18, SampleText: 19, PostScriptCIDFindfontName: 20, WWSFamily: 21, WWSSubfamily: 22, LightBackgroundPalette: 23, DarkBackgroundPalette: 24, VariationsPostScriptNamePrefix: 25 };
var F9 = { Unicode: 0, Macintosh: 1, Reserved: 2, Windows: 3 };
var jX = { Symbol: 0, UnicodeBMP: 1, ShiftJIS: 2, PRC: 3, Big5: 4, Wansung: 5, Johab: 6, UnicodeFullRepertoire: 10 };
function V5($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.uint16(), q = [], J = [];
  for (let U = 0;U < Z; U++)
    J.push({ platformId: $.uint16(), encodingId: $.uint16(), languageId: $.uint16(), nameId: $.uint16(), length: $.uint16(), offset: $.uint16() });
  for (let U = 0;U < J.length; U++) {
    let W = J[U], H = $.sliceFrom(K + W.offset), _ = EX(H, W.length, W.platformId, W.encodingId);
    if (_ !== null)
      q.push({ platformId: W.platformId, encodingId: W.encodingId, languageId: W.languageId, nameId: W.nameId, value: _ });
  }
  return { format: Q, records: q };
}
function EX($, Q, Z, K) {
  if (Z === F9.Unicode || Z === F9.Windows && (K === 1 || K === 10)) {
    let q = [];
    for (let J = 0;J < Q; J += 2) {
      let U = $.uint16();
      q.push(String.fromCharCode(U));
    }
    return q.join("");
  }
  if (Z === F9.Macintosh && K === 0) {
    let q = [];
    for (let J = 0;J < Q; J++)
      q.push($.uint8());
    return String.fromCharCode(...q);
  }
  return null;
}
function x6($, Q, Z) {
  for (let K = 0;K < $.records.length; K++) {
    let q = $.records[K];
    if (q.nameId !== Q)
      continue;
    if (q.platformId === F9.Windows && q.encodingId === 1) {
      if (Z === undefined || q.languageId === Z)
        return q.value;
    }
  }
  for (let K = 0;K < $.records.length; K++) {
    let q = $.records[K];
    if (q.nameId !== Q)
      continue;
    if (q.platformId === F9.Unicode) {
      if (Z === undefined || q.languageId === Z)
        return q.value;
    }
  }
  for (let K = 0;K < $.records.length; K++) {
    let q = $.records[K];
    if (q.nameId !== Q)
      continue;
    if (Z === undefined || q.languageId === Z)
      return q.value;
  }
  return null;
}
function wX($) {
  return x6($, u6.TypographicFamily) ?? x6($, u6.FontFamily);
}
function AX($) {
  return x6($, u6.TypographicSubfamily) ?? x6($, u6.FontSubfamily);
}
function LX($) {
  return x6($, u6.FullName);
}
function FX($) {
  return x6($, u6.PostScriptName);
}
function DX($) {
  return x6($, u6.Version);
}
var BX = { Thin: 100, ExtraLight: 200, Light: 300, Normal: 400, Medium: 500, SemiBold: 600, Bold: 700, ExtraBold: 800, Black: 900 };
var RX = { UltraCondensed: 1, ExtraCondensed: 2, Condensed: 3, SemiCondensed: 4, Normal: 5, SemiExpanded: 6, Expanded: 7, ExtraExpanded: 8, UltraExpanded: 9 };
var z5 = { Italic: 1, Underscore: 2, Negative: 4, Outlined: 8, Strikeout: 16, Bold: 32, Regular: 64, UseTypoMetrics: 128, WWS: 256, Oblique: 512 };
var X5 = { InstallableEmbedding: 0, RestrictedLicense: 2, PreviewAndPrint: 4, Editable: 8, NoSubsetting: 256, BitmapOnly: 512 };
function u1($) {
  let Q = $.uint16(), Z = $.int16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.int16(), W = $.int16(), H = $.int16(), _ = $.int16(), N = $.int16(), V = $.int16(), X = $.int16(), z = $.int16(), G = $.int16(), Y = $.int16(), j = $.int16(), E = [];
  for (let O = 0;O < 10; O++)
    E.push($.uint8());
  let w = $.uint32(), L = $.uint32(), A = $.uint32(), D = $.uint32(), F = String.fromCharCode($.uint8(), $.uint8(), $.uint8(), $.uint8()), M = $.uint16(), R = $.uint16(), P = $.uint16(), T = $.int16(), m = $.int16(), v = $.int16(), f = $.uint16(), l = $.uint16(), k = { version: Q, xAvgCharWidth: Z, usWeightClass: K, usWidthClass: q, fsType: J, ySubscriptXSize: U, ySubscriptYSize: W, ySubscriptXOffset: H, ySubscriptYOffset: _, ySuperscriptXSize: N, ySuperscriptYSize: V, ySuperscriptXOffset: X, ySuperscriptYOffset: z, yStrikeoutSize: G, yStrikeoutPosition: Y, sFamilyClass: j, panose: E, ulUnicodeRange1: w, ulUnicodeRange2: L, ulUnicodeRange3: A, ulUnicodeRange4: D, achVendID: F, fsSelection: M, usFirstCharIndex: R, usLastCharIndex: P, sTypoAscender: T, sTypoDescender: m, sTypoLineGap: v, usWinAscent: f, usWinDescent: l };
  if (Q >= 1)
    k.ulCodePageRange1 = $.uint32(), k.ulCodePageRange2 = $.uint32();
  if (Q >= 2)
    k.sxHeight = $.int16(), k.sCapHeight = $.int16(), k.usDefaultChar = $.uint16(), k.usBreakChar = $.uint16(), k.usMaxContext = $.uint16();
  if (Q >= 5)
    k.usLowerOpticalPointSize = $.uint16(), k.usUpperOpticalPointSize = $.uint16();
  return k;
}
function MX($) {
  return ($.fsSelection & z5.Italic) !== 0;
}
function TX($) {
  return ($.fsSelection & z5.Bold) !== 0;
}
function OX($) {
  return ($.fsSelection & z5.UseTypoMetrics) !== 0;
}
function PX($) {
  let Q = $.fsType;
  if ((Q & X5.RestrictedLicense) !== 0)
    return "restricted";
  if ((Q & X5.PreviewAndPrint) !== 0)
    return "preview";
  if ((Q & X5.Editable) !== 0)
    return "editable";
  return "installable";
}
var vU = [".notdef", ".null", "nonmarkingreturn", "space", "exclam", "quotedbl", "numbersign", "dollar", "percent", "ampersand", "quotesingle", "parenleft", "parenright", "asterisk", "plus", "comma", "hyphen", "period", "slash", "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "colon", "semicolon", "less", "equal", "greater", "question", "at", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "bracketleft", "backslash", "bracketright", "asciicircum", "underscore", "grave", "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "braceleft", "bar", "braceright", "asciitilde", "Adieresis", "Aring", "Ccedilla", "Eacute", "Ntilde", "Odieresis", "Udieresis", "aacute", "agrave", "acircumflex", "adieresis", "atilde", "aring", "ccedilla", "eacute", "egrave", "ecircumflex", "edieresis", "iacute", "igrave", "icircumflex", "idieresis", "ntilde", "oacute", "ograve", "ocircumflex", "odieresis", "otilde", "uacute", "ugrave", "ucircumflex", "udieresis", "dagger", "degree", "cent", "sterling", "section", "bullet", "paragraph", "germandbls", "registered", "copyright", "trademark", "acute", "dieresis", "notequal", "AE", "Oslash", "infinity", "plusminus", "lessequal", "greaterequal", "yen", "mu", "partialdiff", "summation", "product", "pi", "integral", "ordfeminine", "ordmasculine", "Omega", "ae", "oslash", "questiondown", "exclamdown", "logicalnot", "radical", "florin", "approxequal", "Delta", "guillemotleft", "guillemotright", "ellipsis", "nonbreakingspace", "Agrave", "Atilde", "Otilde", "OE", "oe", "endash", "emdash", "quotedblleft", "quotedblright", "quoteleft", "quoteright", "divide", "lozenge", "ydieresis", "Ydieresis", "fraction", "currency", "guilsinglleft", "guilsinglright", "fi", "fl", "daggerdbl", "periodcentered", "quotesinglbase", "quotedblbase", "perthousand", "Acircumflex", "Ecircumflex", "Aacute", "Edieresis", "Egrave", "Iacute", "Icircumflex", "Idieresis", "Igrave", "Oacute", "Ocircumflex", "apple", "Ograve", "Uacute", "Ucircumflex", "Ugrave", "dotlessi", "circumflex", "tilde", "macron", "breve", "dotaccent", "ring", "cedilla", "hungarumlaut", "ogonek", "caron", "Lslash", "lslash", "Scaron", "scaron", "Zcaron", "zcaron", "brokenbar", "Eth", "eth", "Yacute", "yacute", "Thorn", "thorn", "minus", "multiply", "onesuperior", "twosuperior", "threesuperior", "onehalf", "onequarter", "threequarters", "franc", "Gbreve", "gbreve", "Idotaccent", "Scedilla", "scedilla", "Cacute", "cacute", "Ccaron", "ccaron", "dcroat"];
function x1($) {
  let Q = $.uint16(), Z = $.uint16(), K = Q + Z / 65536, q = $.fixed(), J = $.int16(), U = $.int16(), W = $.uint32(), H = $.uint32(), _ = $.uint32(), N = $.uint32(), V = $.uint32(), X = { version: K, italicAngle: q, underlinePosition: J, underlineThickness: U, isFixedPitch: W, minMemType42: H, maxMemType42: _, minMemType1: N, maxMemType1: V };
  if (K === 2) {
    let z = $.uint16(), G = [];
    for (let w = 0;w < z; w++)
      G.push($.uint16());
    let Y = [], j = 0;
    for (let w = 0;w < G.length; w++) {
      let L = G[w];
      if (L >= 258 && L > j)
        j = L;
    }
    let E = j >= 258 ? j - 257 : 0;
    for (let w = 0;w < E; w++) {
      let L = $.uint8(), A = [];
      for (let D = 0;D < L; D++)
        A.push(String.fromCharCode($.uint8()));
      Y.push(A.join(""));
    }
    X.numberOfGlyphs = z, X.glyphNameIndex = G, X.names = Y;
  }
  return X;
}
function IX($, Q) {
  if ($.version === 1) {
    let Z = vU[Q];
    return Z !== undefined ? Z : null;
  }
  if ($.version === 2 && $.glyphNameIndex) {
    let Z = $.glyphNameIndex[Q];
    if (Z === undefined)
      return null;
    if (Z < 258)
      return vU[Z] ?? null;
    let K = Z - 258;
    return $.names?.[K] ?? null;
  }
  return null;
}
function vX($) {
  return $.isFixedPitch !== 0;
}
var f1 = { PNG: "png ", JPG: "jpg ", TIFF: "tiff", PDF: "pdf ", MASK: "mask", DUPE: "dupe" };
function kU($, Q) {
  let Z = $.offset, K = $.uint16(), q = $.uint16(), J = $.uint32(), U = [];
  for (let H = 0;H < J; H++)
    U.push($.uint32());
  let W = [];
  for (let H = 0;H < U.length; H++) {
    let _ = U[H], N = kX($, Z + _, Q);
    W.push(N);
  }
  return { version: K, flags: q, strikes: W };
}
function kX($, Q, Z) {
  let K = $.sliceFrom(Q), q = K.uint16(), J = K.uint16(), U = [];
  for (let H = 0;H <= Z; H++)
    U.push(K.uint32());
  let W = new Map;
  for (let H = 0;H < Z; H++) {
    let _ = U[H], N = U[H + 1];
    if (_ === undefined || N === undefined)
      continue;
    let V = N - _;
    if (V <= 8)
      continue;
    let X = $.sliceFrom(Q + _), z = X.int16(), G = X.int16(), Y = X.tagString(), j = V - 8, E = X.bytes(j);
    W.set(H, { originOffsetX: z, originOffsetY: G, graphicType: Y, data: E });
  }
  return { ppem: q, ppi: J, glyphData: W };
}
function CX($, Q, Z) {
  let K = null, q = 1 / 0;
  for (let J = 0;J < $.strikes.length; J++) {
    let U = $.strikes[J], W = Math.abs(U.ppem - Z);
    if (W < q)
      q = W, K = U;
  }
  if (!K)
    return null;
  return K.glyphData.get(Q) ?? null;
}
function CU($, Q) {
  return $.strikes.find((Z) => Z.ppem === Q) ?? null;
}
function SX($) {
  return $.strikes.map((Q) => Q.ppem).sort((Q, Z) => Q - Z);
}
function hX($, Q, Z) {
  if (Z !== undefined)
    return CU($, Z)?.glyphData.has(Q) ?? false;
  for (let K = 0;K < $.strikes.length; K++)
    if ($.strikes[K].glyphData.has(Q))
      return true;
  return false;
}
function SU($, Q, Z) {
  if (Z.graphicType !== f1.DUPE)
    return Z;
  if (Z.data.length < 2)
    return null;
  let K = (Z.data[0] ?? 0) << 8 | (Z.data[1] ?? 0), q = Q.glyphData.get(K);
  if (!q)
    return null;
  if (q.graphicType === f1.DUPE)
    return SU($, Q, q);
  return q;
}
function V0($) {
  let Q = $.uint32();
  if (Q !== 65536 && Q !== 1330926671 && Q !== 1953658213)
    throw Error(`Invalid sfnt version: 0x${Q.toString(16).padStart(8, "0")}`);
  let Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = new Map;
  for (let W = 0;W < Z; W++) {
    let H = $.tag(), _ = $.uint32(), N = $.uint32(), V = $.uint32();
    U.set(H, { tag: H, checksum: _, offset: N, length: V });
  }
  return { sfntVersion: Q, numTables: Z, searchRange: K, entrySelector: q, rangeShift: J, tables: U };
}
function l1($) {
  return $.sfntVersion === 65536 || $.sfntVersion === 1953658213;
}
function yX($) {
  return $.sfntVersion === 1330926671;
}
var d1 = { OlderSiblingFontAttribute: 1, ElidableAxisValueName: 2 };
function hU($) {
  let Q = $.offset, Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = $.uint16(), U = $.offset32(), W = $.uint16(), H = $.offset32(), _;
  if (Z >= 1 && K >= 1)
    _ = $.uint16();
  let N = [];
  if (U !== 0) {
    let X = $.sliceFrom(Q + U);
    for (let z = 0;z < J; z++)
      if (N.push({ axisTag: X.tag(), axisNameID: X.uint16(), axisOrdering: X.uint16() }), q > 8)
        X.skip(q - 8);
  }
  let V = [];
  if (H !== 0 && W > 0) {
    let X = $.sliceFrom(Q + H), z = [];
    for (let G = 0;G < W; G++)
      z.push(X.uint16());
    for (let G = 0;G < z.length; G++) {
      let Y = z[G], j = $.sliceFrom(Q + H + Y), E = bX(j);
      if (E)
        V.push(E);
    }
  }
  return { majorVersion: Z, minorVersion: K, designAxisCount: J, designAxes: N, axisValueCount: W, axisValues: V, elidedFallbackNameID: _ };
}
function bX($) {
  switch ($.uint16()) {
    case 1:
      return { format: 1, axisIndex: $.uint16(), flags: $.uint16(), valueNameID: $.uint16(), value: $.fixed() };
    case 2:
      return { format: 2, axisIndex: $.uint16(), flags: $.uint16(), valueNameID: $.uint16(), nominalValue: $.fixed(), rangeMinValue: $.fixed(), rangeMaxValue: $.fixed() };
    case 3:
      return { format: 3, axisIndex: $.uint16(), flags: $.uint16(), valueNameID: $.uint16(), value: $.fixed(), linkedValue: $.fixed() };
    case 4: {
      let Z = $.uint16(), K = $.uint16(), q = $.uint16(), J = [];
      for (let U = 0;U < Z; U++)
        J.push({ axisIndex: $.uint16(), value: $.fixed() });
      return { format: 4, axisCount: Z, flags: K, valueNameID: q, axisValues: J };
    }
    default:
      return null;
  }
}
function mX($, Q) {
  return $.designAxes.find((Z) => Z.axisTag === Q) ?? null;
}
function gX($, Q) {
  return $.designAxes.findIndex((Z) => Z.axisTag === Q);
}
function uX($, Q) {
  return $.axisValues.filter((Z) => {
    if (Z.format === 4)
      return Z.axisValues.some((K) => K.axisIndex === Q);
    return Z.axisIndex === Q;
  });
}
function xX($, Q) {
  return $.axisValues.find((Z) => Z.valueNameID === Q) ?? null;
}
function fX($) {
  return ($.flags & d1.ElidableAxisValueName) !== 0;
}
function lX($) {
  return ($.flags & d1.OlderSiblingFontAttribute) !== 0;
}
function dX($) {
  switch ($.format) {
    case 1:
    case 3:
      return $.value;
    case 2:
      return $.nominalValue;
    case 4:
      return null;
  }
}
function nX($, Q) {
  switch ($.format) {
    case 1:
    case 3: {
      let Z = Q.get($.axisIndex);
      return Z !== undefined && Z === $.value;
    }
    case 2: {
      let Z = Q.get($.axisIndex);
      return Z !== undefined && Z >= $.rangeMinValue && Z <= $.rangeMaxValue;
    }
    case 4:
      return $.axisValues.every((Z) => {
        let K = Q.get(Z.axisIndex);
        return K !== undefined && K === Z.value;
      });
  }
}
function yU($) {
  let Q = $.uint16(), Z = $.offset32();
  $.skip(4);
  let K = $.sliceFrom(Z), q = K.uint16();
  if (q === 0)
    return { version: Q, documentRecords: [] };
  let J = [];
  for (let H = 0;H < q; H++)
    J.push({ startGlyphID: K.uint16(), endGlyphID: K.uint16(), svgDocOffset: K.offset32(), svgDocLength: K.uint32() });
  let U = [], W = new TextDecoder("utf-8");
  for (let H = 0;H < J.length; H++) {
    let _ = J[H], V = K.sliceFrom(_.svgDocOffset).bytes(_.svgDocLength), X;
    if (V[0] === 31 && V[1] === 139)
      try {
        let z = oX(V);
        X = W.decode(z);
      } catch {
        X = W.decode(V);
      }
    else
      X = W.decode(V);
    U.push({ startGlyphID: _.startGlyphID, endGlyphID: _.endGlyphID, svgDoc: X });
  }
  return { version: Q, documentRecords: U };
}
function bU($, Q) {
  for (let Z = 0;Z < $.documentRecords.length; Z++) {
    let K = $.documentRecords[Z];
    if (Q >= K.startGlyphID && Q <= K.endGlyphID)
      return K.svgDoc;
  }
  return null;
}
function pX($, Q) {
  return bU($, Q) !== null;
}
function cX($) {
  let Q = [];
  for (let Z = 0;Z < $.documentRecords.length; Z++) {
    let K = $.documentRecords[Z];
    for (let q = K.startGlyphID;q <= K.endGlyphID; q++)
      Q.push(q);
  }
  return Q;
}
function oX($) {
  if (typeof DecompressionStream < "u")
    return $;
  return $;
}
function gU($) {
  let Q = $, Z = $.uint32() / 65536, K = $.uint16(), q = $.offset16(), J = $.offset16();
  $.skip(2);
  let U = null, W = null;
  if (q !== 0)
    U = mU($.sliceFrom(q), Q);
  if (J !== 0)
    W = mU($.sliceFrom(J), Q);
  return { version: Z, format: K, horizData: U, vertData: W };
}
function mU($, Q) {
  let Z = $.uint16(), K = $.uint16(), q = $.offset32(), J = [];
  for (let H = 0;H < Z; H++) {
    let _ = $.int32() / 65536, N = $.uint16(), V = $.uint16();
    J.push({ track: _, nameIndex: N, offset: V, perSizeTracking: [] });
  }
  for (let H = 0;H < J.length; H++) {
    let _ = J[H], N = Q.sliceFrom(_.offset);
    _.perSizeTracking = [];
    for (let V = 0;V < K; V++)
      _.perSizeTracking.push(N.int16());
  }
  let U = Q.sliceFrom(q), W = [];
  for (let H = 0;H < K; H++)
    W.push(U.int32() / 65536);
  return { nTracks: Z, nSizes: K, sizeTableOffset: q, trackTable: J, sizeTable: W };
}
function uU($, Q, Z) {
  let K = null;
  for (let q = 0;q < $.trackTable.length; q++) {
    let J = $.trackTable[q];
    if (J.track === Q) {
      K = J;
      break;
    }
  }
  if (!K) {
    let q = null, J = null;
    for (let U = 0;U < $.trackTable.length; U++) {
      let W = $.trackTable[U];
      if (W.track <= Q && (!q || W.track > q.track))
        q = W;
      if (W.track >= Q && (!J || W.track < J.track))
        J = W;
    }
    if (q && J && q !== J) {
      let U = (Q - q.track) / (J.track - q.track), W = n1($, q, Z), H = n1($, J, Z);
      return Math.round(W + U * (H - W));
    } else if (q)
      K = q;
    else if (J)
      K = J;
    else
      return 0;
  }
  if (!K)
    return 0;
  return n1($, K, Z);
}
function n1($, Q, Z) {
  let K = $.sizeTable, q = Q.perSizeTracking;
  if (K.length === 0 || q.length === 0)
    return 0;
  let J = K[0], U = q[0];
  if (J === undefined || U === undefined)
    return 0;
  if (Z <= J)
    return U;
  let W = K[K.length - 1], H = q[q.length - 1];
  if (W === undefined || H === undefined)
    return 0;
  if (Z >= W)
    return H;
  for (let _ = 0;_ < K.length - 1; _++) {
    let N = K[_], V = K[_ + 1], X = q[_], z = q[_ + 1];
    if (N === undefined || V === undefined || X === undefined || z === undefined)
      continue;
    if (Z >= N && Z <= V) {
      let G = (Z - N) / (V - N);
      return Math.round(X + G * (z - X));
    }
  }
  return 0;
}
function iX($, Q, Z, K = 0, q = false) {
  let J = q ? $.vertData : $.horizData;
  if (!J)
    return;
  let U = uU(J, K, Z);
  if (U === 0)
    return;
  for (let W = 0;W < Q.length; W++) {
    let H = Q[W];
    Q[W] = (H ?? 0) + U;
  }
}
function xU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.fword(), q = $.fword(), J = $.fword(), U = $.uint16(), W = $.fword(), H = $.fword(), _ = $.fword(), N = $.int16(), V = $.int16(), X = $.int16();
  $.skip(8);
  let z = $.int16(), G = $.uint16();
  return { version: { major: Q, minor: Z }, ascender: K, descender: q, lineGap: J, advanceHeightMax: U, minTopSideBearing: W, minBottomSideBearing: H, yMaxExtent: _, caretSlopeRise: N, caretSlopeRun: V, caretOffset: X, metricDataFormat: z, numberOfVMetrics: G };
}
function fU($, Q, Z) {
  let K = [];
  for (let U = 0;U < Q; U++)
    K.push({ advanceHeight: $.uint16(), topSideBearing: $.int16() });
  let q = [], J = Z - Q;
  for (let U = 0;U < J; U++)
    q.push($.int16());
  return { vMetrics: K, topSideBearings: q };
}
function sX($, Q) {
  if (Q < $.vMetrics.length) {
    let U = $.vMetrics[Q];
    if (U)
      return { advanceHeight: U.advanceHeight, topSideBearing: U.topSideBearing };
  }
  let K = $.vMetrics[$.vMetrics.length - 1]?.advanceHeight ?? 0, q = Q - $.vMetrics.length, J = $.topSideBearings[q] ?? 0;
  return { advanceHeight: K, topSideBearing: J };
}
function lU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.int16(), q = $.uint16(), J = [];
  for (let U = 0;U < q; U++)
    J.push({ glyphIndex: $.uint16(), vertOriginY: $.int16() });
  return J.sort((U, W) => U.glyphIndex - W.glyphIndex), { majorVersion: Q, minorVersion: Z, defaultVertOriginY: K, vertOriginYMetrics: J };
}
function rX($, Q) {
  let Z = $.vertOriginYMetrics, K = 0, q = Z.length - 1;
  while (K <= q) {
    let J = K + q >>> 1, U = Z[J];
    if (U === undefined)
      break;
    if (U.glyphIndex === Q)
      return U.vertOriginY;
    else if (U.glyphIndex < Q)
      K = J + 1;
    else
      q = J - 1;
  }
  return $.defaultVertOriginY;
}
function aX($, Q) {
  let Z = $.vertOriginYMetrics, K = 0, q = Z.length - 1;
  while (K <= q) {
    let J = K + q >>> 1, U = Z[J];
    if (U === undefined)
      break;
    if (U.glyphIndex === Q)
      return true;
    else if (U.glyphIndex < Q)
      K = J + 1;
    else
      q = J - 1;
  }
  return false;
}
function dU($) {
  let Q = $.uint16(), Z = $.uint16(), K = $.offset32(), q = $.offset32(), J = $.offset32(), U = $.offset32(), W = $.offset32(), H = tX($.sliceFrom(K)), _ = q !== 0 ? G5($.sliceFrom(q)) : null, N = J !== 0 ? G5($.sliceFrom(J)) : null, V = U !== 0 ? G5($.sliceFrom(U)) : null, X = W !== 0 ? G5($.sliceFrom(W)) : null;
  return { majorVersion: Q, minorVersion: Z, itemVariationStore: H, advanceHeightMapping: _, tsbMapping: N, bsbMapping: V, vOrgMapping: X };
}
function tX($) {
  let Q = $.uint16(), Z = $.offset32(), K = $.uint16(), q = [];
  for (let N = 0;N < K; N++)
    q.push($.offset32());
  let J = $.sliceFrom(Z), U = J.uint16(), W = J.uint16(), H = [];
  for (let N = 0;N < W; N++) {
    let V = [];
    for (let X = 0;X < U; X++)
      V.push({ startCoord: J.f2dot14(), peakCoord: J.f2dot14(), endCoord: J.f2dot14() });
    H.push({ regionAxes: V });
  }
  let _ = [];
  for (let N = 0;N < q.length; N++) {
    let V = q[N], X = $.sliceFrom(V), z = X.uint16(), G = X.uint16(), Y = X.uint16(), j = [];
    for (let D = 0;D < Y; D++)
      j.push(X.uint16());
    let E = (G & 32768) !== 0, w = G & 32767, L = Y - w, A = [];
    for (let D = 0;D < z; D++) {
      let F = [];
      for (let M = 0;M < w; M++)
        if (E)
          F.push(X.int32());
        else
          F.push(X.int16());
      for (let M = 0;M < L; M++)
        if (E)
          F.push(X.int16());
        else
          F.push(X.int8());
      A.push(F);
    }
    _.push({ itemCount: z, regionIndexes: j, deltaSets: A });
  }
  return { format: Q, variationRegions: H, itemVariationData: _ };
}
function G5($) {
  let Q = $.uint8(), Z = $.uint8(), K = Q === 0 ? $.uint16() : $.uint32(), q = (Z & 15) + 1, J = (Z >> 4 & 3) + 1, U = [];
  for (let W = 0;W < K; W++) {
    let H = 0;
    for (let V = 0;V < J; V++)
      H = H << 8 | $.uint8();
    let _ = H & (1 << q) - 1, N = H >> q;
    U.push({ outer: N, inner: _ });
  }
  return { format: Q, mapCount: K, entryFormat: Z, innerIndexBitCount: q, mapData: U };
}
function eX($, Q, Z) {
  let K = $.advanceHeightMapping, q, J;
  if (K && Q < K.mapData.length) {
    let U = K.mapData[Q];
    if (!U)
      return 0;
    q = U.outer, J = U.inner;
  } else
    q = 0, J = Q;
  return Y5($.itemVariationStore, q, J, Z);
}
function $2($, Q, Z) {
  let K = $.tsbMapping;
  if (!K)
    return 0;
  if (Q >= K.mapData.length)
    return 0;
  let q = K.mapData[Q];
  if (!q)
    return 0;
  return Y5($.itemVariationStore, q.outer, q.inner, Z);
}
function Q2($, Q, Z) {
  let K = $.bsbMapping;
  if (!K)
    return 0;
  if (Q >= K.mapData.length)
    return 0;
  let q = K.mapData[Q];
  if (!q)
    return 0;
  return Y5($.itemVariationStore, q.outer, q.inner, Z);
}
function Z2($, Q, Z) {
  let K = $.vOrgMapping;
  if (!K)
    return 0;
  if (Q >= K.mapData.length)
    return 0;
  let q = K.mapData[Q];
  if (!q)
    return 0;
  return Y5($.itemVariationStore, q.outer, q.inner, Z);
}
function Y5($, Q, Z, K) {
  let q = $.itemVariationData[Q];
  if (!q || Z >= q.itemCount)
    return 0;
  let J = q.deltaSets[Z];
  if (!J)
    return 0;
  let U = 0;
  for (let W = 0;W < q.regionIndexes.length; W++) {
    let H = q.regionIndexes[W], _ = $.variationRegions[H];
    if (!_)
      continue;
    let N = e9(_, K), V = J[W] ?? 0;
    U += N * V;
  }
  return Math.round(U);
}
var K2 = 2001684038;
var q2 = 2001684018;
function nU($) {
  return new DataView($).getUint32(0, false) === q2;
}
function pU($) {
  return new DataView($).getUint32(0, false) === K2;
}

class p1 {
  buffer;
  header;
  loadFace;
  namesCache = null;
  constructor($, Q, Z) {
    this.buffer = $, this.header = Q, this.loadFace = Z;
  }
  get count() {
    return this.header.numFonts;
  }
  get($, Q) {
    return this.loadFace($, Q);
  }
  names() {
    if (this.namesCache)
      return this.namesCache;
    let $ = [], Q = new D6(this.buffer);
    for (let Z = 0;Z < this.header.numFonts; Z++) {
      let K = this.header.offsets[Z] ?? 0, q = { index: Z };
      if (K >= 0 && K < this.buffer.byteLength)
        try {
          let U = V0(new D6(this.buffer, K)).tables.get(p.name);
          if (U) {
            let W = Q.slice(U.offset, U.length), H = V5(W);
            q.fullName = x6(H, u6.FullName) ?? undefined, q.family = x6(H, u6.FontFamily) ?? undefined, q.subfamily = x6(H, u6.FontSubfamily) ?? undefined, q.postScriptName = x6(H, u6.PostScriptName) ?? undefined;
          }
        } catch {
        }
      $.push(q);
    }
    return this.namesCache = $, $;
  }
}

class Z8 {
  reader;
  directory;
  _head = null;
  _maxp = null;
  _hhea = null;
  _hmtx = null;
  _cmap = null;
  _gdef = undefined;
  _gsub = undefined;
  _gpos = undefined;
  _kern = undefined;
  _fvar = undefined;
  _hvar = undefined;
  _vhea = undefined;
  _vmtx = undefined;
  _morx = undefined;
  _gvar = undefined;
  _avar = undefined;
  _kerx = undefined;
  _trak = undefined;
  _cff = undefined;
  _cff2 = undefined;
  _colr = undefined;
  _cpal = undefined;
  _vvar = undefined;
  _mvar = undefined;
  _os2 = undefined;
  _name = undefined;
  _post = undefined;
  _base = undefined;
  _jstf = undefined;
  _math = undefined;
  _loca = undefined;
  _glyf = undefined;
  _svg = undefined;
  _vorg = undefined;
  _sbix = undefined;
  _stat = undefined;
  _cbdt = undefined;
  _cblc = undefined;
  _feat = undefined;
  _fpgm = undefined;
  _prep = undefined;
  _cvt = undefined;
  _gasp = undefined;
  constructor($, Q, Z = {}) {
    this.reader = $, this.directory = Q;
  }
  static load($, Q) {
    if (nU($))
      throw Error("WOFF2 requires async loading. Use Font.loadAsync() instead.");
    if (pU($))
      throw Error("WOFF format is not supported. Please use TTF, OTF, or WOFF2.");
    return Z8.loadFromBuffer($, Q);
  }
  static async loadAsync($, Q) {
    if (nU($))
      $ = await jJ($);
    else if (pU($))
      throw Error("WOFF format is not supported. Please use TTF, OTF, or WOFF2.");
    return Z8.loadFromBuffer($, Q);
  }
  static async fromURL($, Q) {
    let Z = await fetch($);
    if (!Z.ok)
      throw Error(`Failed to fetch font: ${Z.status} ${Z.statusText}`);
    let K = await Z.arrayBuffer();
    return Z8.loadAsync(K, Q);
  }
  static async fromFile($, Q) {
    let K = await Bun.file($).arrayBuffer();
    return Z8.loadAsync(K, Q);
  }
  static collection($) {
    if (!i$($))
      return null;
    let Q = s$($);
    return new p1($, Q, (Z, K) => Z8.loadFromTtc($, K, Q, Z));
  }
  static loadFromBuffer($, Q) {
    if (i$($))
      return Z8.loadFromTtc($, Q);
    let Z = new D6($), K = V0(Z);
    return new Z8(Z, K, Q);
  }
  static loadFromTtc($, Q, Z, K) {
    let q = Z ?? s$($), J = K ?? Q?.collectionIndex ?? 0;
    if (!Number.isInteger(J) || J < 0)
      throw Error(`Invalid TTC collection index: ${J}`);
    if (J >= q.numFonts)
      throw Error(`TTC collection index out of range: ${J} (count ${q.numFonts})`);
    let U = q.offsets[J] ?? 0;
    if (!Number.isFinite(U) || U < 0 || U >= $.byteLength)
      throw Error(`Invalid TTC font offset: ${U}`);
    let W = new D6($), H = V0(new D6($, U));
    return new Z8(W, H, Q);
  }
  hasTable($) {
    return this.directory.tables.has($);
  }
  getTableRecord($) {
    return this.directory.tables.get($);
  }
  getTableReader($) {
    let Q = this.directory.tables.get($);
    if (!Q)
      return null;
    return this.reader.slice(Q.offset, Q.length);
  }
  get head() {
    if (!this._head) {
      let $ = this.getTableReader(p.head);
      if (!$)
        throw Error("Missing required 'head' table");
      this._head = GU($);
    }
    return this._head;
  }
  get maxp() {
    if (!this._maxp) {
      let $ = this.getTableReader(p.maxp);
      if (!$)
        throw Error("Missing required 'maxp' table");
      this._maxp = OU($);
    }
    return this._maxp;
  }
  get hhea() {
    if (!this._hhea) {
      let $ = this.getTableReader(p.hhea);
      if (!$)
        throw Error("Missing required 'hhea' table");
      this._hhea = YU($);
    }
    return this._hhea;
  }
  get hmtx() {
    if (!this._hmtx) {
      let $ = this.getTableReader(p.hmtx);
      if (!$)
        throw Error("Missing required 'hmtx' table");
      this._hmtx = jU($, this.hhea.numberOfHMetrics, this.numGlyphs);
    }
    return this._hmtx;
  }
  get cmap() {
    if (!this._cmap) {
      let $ = this.getTableRecord(p.cmap), Q = this.getTableReader(p.cmap);
      if (!Q || !$)
        throw Error("Missing required 'cmap' table");
      this._cmap = OJ(Q, $.length);
    }
    return this._cmap;
  }
  get gdef() {
    if (this._gdef === undefined) {
      let $ = this.getTableReader(p.GDEF);
      this._gdef = $ ? tJ($) : null;
    }
    return this._gdef;
  }
  get gsub() {
    if (this._gsub === undefined) {
      let $ = this.getTableReader(p.GSUB);
      this._gsub = $ ? WU($) : null;
    }
    return this._gsub;
  }
  get gpos() {
    if (this._gpos === undefined) {
      let $ = this.getTableReader(p.GPOS);
      this._gpos = $ ? qU($) : null;
    }
    return this._gpos;
  }
  get kern() {
    if (this._kern === undefined) {
      let $ = this.getTableReader(p.kern);
      this._kern = $ ? FU($) : null;
    }
    return this._kern;
  }
  get fvar() {
    if (this._fvar === undefined) {
      let $ = this.getTableReader(p.fvar);
      this._fvar = $ ? oq($) : null;
    }
    return this._fvar;
  }
  get hvar() {
    if (this._hvar === undefined) {
      let $ = this.getTableReader(p.HVAR);
      this._hvar = $ ? iq($) : null;
    }
    return this._hvar;
  }
  get vhea() {
    if (this._vhea === undefined) {
      let $ = this.getTableReader(p.vhea);
      this._vhea = $ ? xU($) : null;
    }
    return this._vhea;
  }
  get vmtx() {
    if (this._vmtx === undefined) {
      let $ = this.vhea;
      if (!$)
        this._vmtx = null;
      else {
        let Q = this.getTableReader(p.vmtx);
        this._vmtx = Q ? fU(Q, $.numberOfVMetrics, this.numGlyphs) : null;
      }
    }
    return this._vmtx;
  }
  get morx() {
    if (this._morx === undefined) {
      let $ = this.getTableReader(p.morx);
      this._morx = $ ? PU($) : null;
    }
    return this._morx;
  }
  get gvar() {
    if (this._gvar === undefined) {
      let $ = this.getTableReader(p.gvar);
      this._gvar = $ ? y7($, this.numGlyphs) : null;
    }
    return this._gvar;
  }
  get avar() {
    if (this._avar === undefined) {
      let $ = this.fvar;
      if (!$)
        this._avar = null;
      else {
        let Q = this.getTableReader(p.avar);
        this._avar = Q ? pq(Q, $.axes.length) : null;
      }
    }
    return this._avar;
  }
  get kerx() {
    if (this._kerx === undefined) {
      let $ = this.getTableReader(p.kerx);
      this._kerx = $ ? MU($) : null;
    }
    return this._kerx;
  }
  get trak() {
    if (this._trak === undefined) {
      let $ = this.getTableReader(p.trak);
      this._trak = $ ? gU($) : null;
    }
    return this._trak;
  }
  get cff() {
    if (this._cff === undefined) {
      let $ = this.getTableReader(p.CFF);
      this._cff = $ ? U1($) : null;
    }
    return this._cff;
  }
  get cff2() {
    if (this._cff2 === undefined) {
      let $ = this.getTableReader(p.CFF2);
      this._cff2 = $ ? H1($) : null;
    }
    return this._cff2;
  }
  get colr() {
    if (this._colr === undefined) {
      let $ = this.getTableReader(p.COLR);
      this._colr = $ ? V1($) : null;
    }
    return this._colr;
  }
  get cpal() {
    if (this._cpal === undefined) {
      let $ = this.getTableReader(p.CPAL);
      this._cpal = $ ? X1($) : null;
    }
    return this._cpal;
  }
  get vvar() {
    if (this._vvar === undefined) {
      let $ = this.getTableReader(p.VVAR);
      this._vvar = $ ? dU($) : null;
    }
    return this._vvar;
  }
  get mvar() {
    if (this._mvar === undefined) {
      let $ = this.getTableReader(p.MVAR);
      this._mvar = $ ? IU($) : null;
    }
    return this._mvar;
  }
  get os2() {
    if (this._os2 === undefined) {
      let $ = this.getTableReader(p.OS2);
      this._os2 = $ ? u1($) : null;
    }
    return this._os2;
  }
  get name() {
    if (this._name === undefined) {
      let $ = this.getTableReader(p.name);
      this._name = $ ? V5($) : null;
    }
    return this._name;
  }
  get post() {
    if (this._post === undefined) {
      let $ = this.getTableReader(p.post);
      this._post = $ ? x1($) : null;
    }
    return this._post;
  }
  get base() {
    if (this._base === undefined) {
      let $ = this.getTableReader(p.BASE);
      this._base = $ ? J1($) : null;
    }
    return this._base;
  }
  get jstf() {
    if (this._jstf === undefined) {
      let $ = this.getTableReader(p.JSTF);
      this._jstf = $ ? v1($) : null;
    }
    return this._jstf;
  }
  get math() {
    if (this._math === undefined) {
      let $ = this.getTableReader(p.MATH);
      this._math = $ ? b1($) : null;
    }
    return this._math;
  }
  get loca() {
    if (this._loca === undefined) {
      let $ = this.getTableReader(p.loca);
      this._loca = $ ? b7($, this.numGlyphs, this.head.indexToLocFormat) : null;
    }
    return this._loca;
  }
  get glyf() {
    if (this._glyf === undefined) {
      let $ = this.getTableReader(p.glyf);
      this._glyf = $ ? g7($) : null;
    }
    return this._glyf;
  }
  get svg() {
    if (this._svg === undefined) {
      let $ = this.getTableReader(p.SVG);
      this._svg = $ ? yU($) : null;
    }
    return this._svg;
  }
  get vorg() {
    if (this._vorg === undefined) {
      let $ = this.getTableReader(p.VORG);
      this._vorg = $ ? lU($) : null;
    }
    return this._vorg;
  }
  get sbix() {
    if (this._sbix === undefined) {
      let $ = this.getTableReader(p.sbix);
      this._sbix = $ ? kU($, this.numGlyphs) : null;
    }
    return this._sbix;
  }
  get stat() {
    if (this._stat === undefined) {
      let $ = this.getTableReader(p.STAT);
      this._stat = $ ? hU($) : null;
    }
    return this._stat;
  }
  get cblc() {
    if (this._cblc === undefined) {
      let $ = this.getTableReader(p.CBLC);
      this._cblc = $ ? LJ($) : null;
    }
    return this._cblc;
  }
  get cbdt() {
    if (this._cbdt === undefined) {
      let $ = this.getTableReader(p.CBDT);
      this._cbdt = $ ? FJ($) : null;
    }
    return this._cbdt;
  }
  get feat() {
    if (this._feat === undefined) {
      let $ = this.getTableReader(p.feat);
      this._feat = $ ? z1($) : null;
    }
    return this._feat;
  }
  get fpgm() {
    if (this._fpgm === undefined) {
      let $ = this.getTableReader(p.fpgm);
      this._fpgm = $ ? O1($) : null;
    }
    return this._fpgm;
  }
  get prep() {
    if (this._prep === undefined) {
      let $ = this.getTableReader(p.prep);
      this._prep = $ ? P1($) : null;
    }
    return this._prep;
  }
  get cvtTable() {
    if (this._cvt === undefined) {
      let $ = this.getTableReader(p.cvt);
      this._cvt = $ ? I1($) : null;
    }
    return this._cvt;
  }
  get gasp() {
    if (this._gasp === undefined) {
      let $ = this.getTableReader(p.gasp);
      this._gasp = $ ? G1($) : null;
    }
    return this._gasp;
  }
  get numGlyphs() {
    return this.maxp.numGlyphs;
  }
  get unitsPerEm() {
    return this.head.unitsPerEm;
  }
  get ascender() {
    return this.hhea.ascender;
  }
  get descender() {
    return this.hhea.descender;
  }
  get lineGap() {
    return this.hhea.lineGap;
  }
  get height() {
    return this.ascender - this.descender;
  }
  scaleForSize($, Q) {
    if (Q === "height") {
      let Z = this.height;
      if (!Number.isFinite(Z) || Z <= 0)
        return $ / this.unitsPerEm;
      return $ / Z;
    }
    return $ / this.unitsPerEm;
  }
  get isTrueType() {
    return l1(this.directory);
  }
  get isCFF() {
    return this.hasTable(p.CFF) || this.hasTable(p.CFF2);
  }
  get isVariable() {
    return this.hasTable(p.fvar);
  }
  get hasOpenTypeLayout() {
    return this.hasTable(p.GSUB) || this.hasTable(p.GPOS);
  }
  get hasAATLayout() {
    return this.hasTable(p.morx) || this.hasTable(p.kerx);
  }
  get isColorFont() {
    return this.hasTable(p.COLR) || this.hasTable(p.SVG) || this.hasTable(p.sbix) || this.hasTable(p.CBDT);
  }
  get hasHinting() {
    return this.isTrueType && (this.hasTable(p.fpgm) || this.hasTable(p.prep));
  }
  glyphId($) {
    return PJ(this.cmap, $);
  }
  glyphIdForChar($) {
    let Q = $.codePointAt(0);
    if (Q === undefined)
      return 0;
    return this.glyphId(Q);
  }
  advanceWidth($) {
    return EU(this.hmtx, $);
  }
  leftSideBearing($) {
    return wU(this.hmtx, $);
  }
  listTables() {
    return [...this.directory.tables.keys()].map(h9);
  }
  getGlyph($) {
    if (!this.glyf || !this.loca)
      return null;
    return A8(this.glyf, this.loca, $);
  }
  getGlyphContours($) {
    if (this.glyf && this.loca)
      return l7(this.glyf, this.loca, $);
    if (this.cff)
      return W1(this.cff, $, 0);
    if (this.cff2)
      return J5(this.cff2, $, null);
    return null;
  }
  getGlyphBounds($) {
    if (this.glyf && this.loca)
      return n7(this.glyf, this.loca, $);
    let Q = this.getGlyphContours($);
    if (!Q || Q.length === 0)
      return null;
    let Z = 1 / 0, K = 1 / 0, q = -1 / 0, J = -1 / 0;
    for (let U = 0;U < Q.length; U++) {
      let W = Q[U];
      for (let H = 0;H < W.length; H++) {
        let _ = W[H];
        Z = Math.min(Z, _.x), K = Math.min(K, _.y), q = Math.max(q, _.x), J = Math.max(J, _.y);
      }
    }
    if (Z === 1 / 0)
      return null;
    return { xMin: Z, yMin: K, xMax: q, yMax: J };
  }
  getGlyphContoursAndBounds($) {
    if (this.glyf && this.loca)
      return d7(this.glyf, this.loca, $);
    let Q = this.getGlyphContours($);
    if (!Q)
      return null;
    if (Q.length === 0)
      return { contours: Q, bounds: null };
    let Z = 1 / 0, K = 1 / 0, q = -1 / 0, J = -1 / 0;
    for (let W = 0;W < Q.length; W++) {
      let H = Q[W];
      for (let _ = 0;_ < H.length; _++) {
        let N = H[_];
        Z = Math.min(Z, N.x), K = Math.min(K, N.y), q = Math.max(q, N.x), J = Math.max(J, N.y);
      }
    }
    return { contours: Q, bounds: Z === 1 / 0 ? null : { xMin: Z, yMin: K, xMax: q, yMax: J } };
  }
  getGlyphContoursWithVariation($, Q) {
    if (this.glyf && this.loca)
      return o7(this.glyf, this.loca, this.gvar, $, Q);
    if (this.cff2)
      return J5(this.cff2, $, Q);
    return this.getGlyphContours($);
  }
}
var oU;
((K) => {
  K.Shrink = "shrink";
  K.Extend = "extend";
  K.Auto = "auto";
})(oU ||= {});
function j5($) {
  let Q = 0;
  for (let Z = 0;Z < $.positions.length; Z++) {
    let K = $.positions[Z];
    Q += K.xAdvance;
  }
  return Q;
}
function iU($, Q, Z) {
  let { targetWidth: K, script: q = C("DFLT"), language: J, mode: U = "auto", maxPriority: W = 10, enableKashida: H = true, minWordSpacingFactor: _ = 0.8, maxWordSpacingFactor: N = 1.5, enableLetterSpacing: V = true, maxLetterSpacing: X = 100 } = Z, z = j5(Q), G = K - z;
  if (Math.abs(G) < 1)
    return { success: true, finalWidth: z, delta: 0, priorityLevel: 0, adjustments: [] };
  let Y;
  if (U === "auto")
    Y = G > 0 ? "extend" : "shrink";
  else
    Y = U;
  let j = [], E = G, w = 0, L = $.jstf;
  if (L) {
    let F = C1(L, q, J);
    for (let M = 0;M < Math.min(F.length, W); M++) {
      let R = F[M], P = Y === "shrink" ? S1(R) : h1(R);
      if (P.enableGsub.length > 0 || P.disableGsub.length > 0 || P.enableGpos.length > 0 || P.disableGpos.length > 0)
        j.push({ type: "lookup", glyphIndices: [], value: M }), w = M;
    }
    if (H && Y === "extend") {
      let M = k1(L, q);
      if (M.length > 0) {
        let R = J2(Q, M[0], E, $);
        E -= R.totalExtension, j.push(...R.adjustments);
      }
    }
  }
  let A = $.glyphId(32);
  if (A !== 0) {
    let F = W2(Q, A, E, Y === "shrink" ? _ : N);
    E -= F.totalAdjustment, j.push(...F.adjustments);
  }
  if (V && Math.abs(E) > 1) {
    let F = H2(Q, E, X);
    E -= F.totalAdjustment, j.push(...F.adjustments);
  }
  let D = j5(Q);
  return { success: Math.abs(E) < 1, finalWidth: D, delta: K - D, priorityLevel: w, adjustments: j };
}
function J2($, Q, Z, K) {
  let q = [], J = 0, U = [];
  for (let N = 0;N < $.infos.length - 1; N++) {
    let V = $.infos[N];
    if (U2(V.codepoint))
      U.push(N);
  }
  if (U.length === 0)
    return { totalExtension: 0, adjustments: [] };
  let W = K.advanceWidth(Q);
  if (W <= 0)
    return { totalExtension: 0, adjustments: [] };
  let H = Math.ceil(Z / W / U.length), _ = Math.min(H * W, Z / U.length);
  for (let N = 0;N < U.length; N++) {
    if (J >= Z)
      break;
    let V = U[N];
    $.positions[V].xAdvance += _, J += _, q.push({ type: "kashida", glyphIndices: [V], value: _ });
  }
  return { totalExtension: J, adjustments: q };
}
function U2($) {
  return $ >= 1568 && $ <= 1791;
}
function W2($, Q, Z, K) {
  let q = [], J = 0, U = [], W = 0;
  for (let X = 0;X < $.infos.length; X++)
    if ($.infos[X]?.glyphId === Q)
      U.push(X), W += $.positions[X]?.xAdvance;
  if (U.length === 0)
    return { totalAdjustment: 0, adjustments: [] };
  let H = Z / U.length, N = W / U.length * (K - 1), V = Z > 0 ? Math.min(H, N) : Math.max(H, -N);
  for (let X = 0;X < U.length; X++) {
    let z = U[X];
    $.positions[z].xAdvance += V, J += V;
  }
  if (J !== 0)
    q.push({ type: "spacing", glyphIndices: U, value: V });
  return { totalAdjustment: J, adjustments: q };
}
function H2($, Q, Z) {
  let K = [], q = $.infos.length;
  if (q <= 1)
    return { totalAdjustment: 0, adjustments: [] };
  let J = q - 1, U = Q / J, W = Q > 0 ? Math.min(U, Z) : Math.max(U, -Z), H = [], _ = 0;
  for (let N = 0;N < q - 1; N++)
    $.positions[N].xAdvance += W, _ += W, H.push(N);
  if (_ !== 0)
    K.push({ type: "spacing", glyphIndices: H, value: W });
  return { totalAdjustment: _, adjustments: K };
}
function _2($, Q, Z) {
  let K = [], q = [];
  if ($.infos.length === 0)
    return { lines: [], breakPoints: [] };
  let J = 0, U = 0, W = -1, H = 0;
  for (let _ = 0;_ < $.infos.length; _++) {
    let N = $.positions[_], V = $.infos[_];
    if (U += N.xAdvance, Z !== undefined && V.glyphId === Z)
      W = _, H = U;
    if (U > Q && J < _) {
      let X;
      if (W > J)
        X = W + 1;
      else
        X = _;
      let z = cU($, J, X);
      K.push(z), q.push(X), J = X, U = 0, W = -1;
      for (let G = J;G <= _; G++)
        U += $.positions[G]?.xAdvance;
    }
  }
  if (J < $.infos.length) {
    let _ = cU($, J, $.infos.length);
    K.push(_);
  }
  return { lines: K, breakPoints: q };
}
function cU($, Q, Z) {
  let K = new C8;
  K.direction = $.direction, K.script = $.script, K.language = $.language;
  for (let q = Q;q < Z; q++)
    K.infos.push({ ...$.infos[q] }), K.positions.push({ ...$.positions[q] });
  return K;
}
function N2($, Q, Z) {
  let K = [];
  for (let q = 0;q < Q.length; q++) {
    let J = Q[q];
    if (q === Q.length - 1)
      K.push({ success: true, finalWidth: j5(J), delta: 0, priorityLevel: 0, adjustments: [] });
    else
      K.push(iU($, J, Z));
  }
  return K;
}
function sU($, Q) {
  for (let Z = 0;Z < $.conditions.length; Z++) {
    let K = $.conditions[Z], q = Q[K.axisIndex] ?? 0;
    if (q < K.filterRangeMinValue || q > K.filterRangeMaxValue)
      return false;
  }
  return true;
}
function X0($, Q) {
  for (let Z = 0;Z < $.featureVariationRecords.length; Z++) {
    let K = $.featureVariationRecords[Z];
    if (sU(K.conditionSet, Q))
      return K;
  }
  return null;
}
function V2($, Q, Z, K) {
  if (!$ || !K)
    return Z;
  let q = X0($, K);
  if (!q)
    return Z;
  let J = q.featureTableSubstitution.substitutions.find((U) => U.featureIndex === Q);
  if (J)
    return J.alternateFeature.lookupListIndices;
  return Z;
}
function X2($, Q, Z, K) {
  if (!$ || !K)
    return Q;
  let q = X0($, K);
  if (!q)
    return Q;
  let J = new Map(Q), U = q.featureTableSubstitution.substitutions;
  for (let W = 0;W < U.length; W++) {
    let H = U[W], _ = [...Z.entries()];
    for (let N = 0;N < _.length; N++) {
      let [V, X] = _[N];
      if (X === H.featureIndex) {
        J.set(V, H.alternateFeature.lookupListIndices);
        break;
      }
    }
  }
  return J;
}
G6();
var z2 = [0, 85, 86, 85, 0];
var rU = [8, 77, 86, 77, 8];
var G2 = [0, 64, 128, 64, 0];
var aU;
((q) => {
  q[q.RGB = 0] = "RGB";
  q[q.BGR = 1] = "BGR";
  q[q.RGB_V = 2] = "RGB_V";
  q[q.BGR_V = 3] = "BGR_V";
})(aU ||= {});
function Y2($, Q, Z, K, q, J, U = 0, W = rU) {
  if (U === 2 || U === 3)
    return j2($, Q, Z, K, q, J, U, W);
  let _ = Q * 3, N = r(_, Z, 1), V = new Q9;
  V.setClip(0, 0, _, Z), V.reset(), F8(V, $, K * 3, q * 3, J, true), V.sweep(N);
  let X = r(Q, Z, 2);
  return E2(N, X, W, U === 1), X;
}
function j2($, Q, Z, K, q, J, U, W) {
  let H = Z * 3, _ = r(Q, H, 1), N = new Q9;
  N.setClip(0, 0, Q, H), N.reset(), F8(N, $, K, q, J * 3, true), N.sweep(_);
  let V = r(Q, Z, 3);
  return w2(_, V, W, U === 3), V;
}
function E2($, Q, Z, K) {
  let [q, J, U, W, H] = Z, _ = q + J + U + W + H;
  for (let N = 0;N < Q.rows; N++) {
    let V = N * $.pitch, X = N * Q.pitch;
    for (let z = 0;z < Q.width; z++) {
      let G = z * 3, Y = G - 2 >= 0 ? $.buffer[V + G - 2] : 0, j = G - 1 >= 0 ? $.buffer[V + G - 1] : 0, E = $.buffer[V + G], w = G + 1 < $.width ? $.buffer[V + G + 1] : 0, L = G + 2 < $.width ? $.buffer[V + G + 2] : 0, A = Math.min(255, Math.round((Y * q + j * J + E * U + w * W + L * H) / _)), D = G - 1 >= 0 ? $.buffer[V + G - 1] : 0, F = $.buffer[V + G], M = G + 1 < $.width ? $.buffer[V + G + 1] : 0, R = G + 2 < $.width ? $.buffer[V + G + 2] : 0, P = G + 3 < $.width ? $.buffer[V + G + 3] : 0, T = Math.min(255, Math.round((D * q + F * J + M * U + R * W + P * H) / _)), m = $.buffer[V + G], v = G + 1 < $.width ? $.buffer[V + G + 1] : 0, f = G + 2 < $.width ? $.buffer[V + G + 2] : 0, l = G + 3 < $.width ? $.buffer[V + G + 3] : 0, k = G + 4 < $.width ? $.buffer[V + G + 4] : 0, O = Math.min(255, Math.round((m * q + v * J + f * U + l * W + k * H) / _)), h = X + z * 3;
      if (K)
        Q.buffer[h] = O, Q.buffer[h + 1] = T, Q.buffer[h + 2] = A;
      else
        Q.buffer[h] = A, Q.buffer[h + 1] = T, Q.buffer[h + 2] = O;
    }
  }
}
function w2($, Q, Z, K) {
  let [q, J, U, W, H] = Z, _ = q + J + U + W + H;
  for (let N = 0;N < Q.rows; N++) {
    let V = N * 3, X = N * Q.pitch;
    for (let z = 0;z < Q.width; z++) {
      let G = V - 2 >= 0 ? $.buffer[(V - 2) * $.pitch + z] : 0, Y = V - 1 >= 0 ? $.buffer[(V - 1) * $.pitch + z] : 0, j = $.buffer[V * $.pitch + z], E = V + 1 < $.rows ? $.buffer[(V + 1) * $.pitch + z] : 0, w = V + 2 < $.rows ? $.buffer[(V + 2) * $.pitch + z] : 0, L = Math.min(255, Math.round((G * q + Y * J + j * U + E * W + w * H) / _)), A = V - 1 >= 0 ? $.buffer[(V - 1) * $.pitch + z] : 0, D = $.buffer[V * $.pitch + z], F = V + 1 < $.rows ? $.buffer[(V + 1) * $.pitch + z] : 0, M = V + 2 < $.rows ? $.buffer[(V + 2) * $.pitch + z] : 0, R = V + 3 < $.rows ? $.buffer[(V + 3) * $.pitch + z] : 0, P = Math.min(255, Math.round((A * q + D * J + F * U + M * W + R * H) / _)), T = $.buffer[V * $.pitch + z], m = V + 1 < $.rows ? $.buffer[(V + 1) * $.pitch + z] : 0, v = V + 2 < $.rows ? $.buffer[(V + 2) * $.pitch + z] : 0, f = V + 3 < $.rows ? $.buffer[(V + 3) * $.pitch + z] : 0, l = V + 4 < $.rows ? $.buffer[(V + 4) * $.pitch + z] : 0, k = Math.min(255, Math.round((T * q + m * J + v * U + f * W + l * H) / _)), O = X + z * 3;
      if (K)
        Q.buffer[O] = k, Q.buffer[O + 1] = P, Q.buffer[O + 2] = L;
      else
        Q.buffer[O] = L, Q.buffer[O + 1] = P, Q.buffer[O + 2] = k;
    }
  }
}
function A2($, Q = [255, 255, 255], Z = [0, 0, 0]) {
  let K = new Uint8Array($.width * $.rows * 4), [q, J, U] = Q, [W, H, _] = Z;
  for (let N = 0;N < $.rows; N++) {
    let V = N * $.pitch, X = N * $.width * 4;
    for (let z = 0;z < $.width; z++) {
      let G = V + z * 3, Y = X + z * 4, j = $.buffer[G], E = $.buffer[G + 1], w = $.buffer[G + 2];
      K[Y] = c1(q, W, j), K[Y + 1] = c1(J, H, E), K[Y + 2] = c1(U, _, w), K[Y + 3] = 255;
    }
  }
  return K;
}
function c1($, Q, Z) {
  return Math.round($ + (Q - $) * Z / 255);
}
G6();
var QW;
((q) => {
  q[q.None = 0] = "None";
  q[q.Decompose = 1] = "Decompose";
  q[q.Compose = 2] = "Compose";
  q[q.Auto = 3] = "Auto";
})(QW ||= {});
function G8($) {
  if ($ < 768)
    return 0;
  if ($ >= 880 && $ < 1328)
    return 0;
  if ($ >= 12288) {
    if ($ >= 44032 && $ <= 55215)
      return 0;
    if ($ >= 19968 && $ <= 40959)
      return 0;
    if ($ >= 13312 && $ <= 19903)
      return 0;
    if ($ >= 12352 && $ < 12441)
      return 0;
    if ($ >= 12443 && $ <= 12543)
      return 0;
    if ($ >= 12544 && $ <= 12591)
      return 0;
    if ($ >= 12800 && $ <= 13055)
      return 0;
  }
  if ($ >= 768 && $ <= 879)
    return M2($);
  if ($ >= 1425 && $ <= 1469)
    return B2($);
  if ($ === 1471)
    return 23;
  if ($ === 1473)
    return 24;
  if ($ === 1474)
    return 25;
  if ($ === 1476)
    return 230;
  if ($ === 1477)
    return 220;
  if ($ === 1479)
    return 18;
  if ($ >= 1611 && $ <= 1631)
    return R2($);
  if ($ === 1648)
    return 35;
  if ($ >= 1552 && $ <= 1562)
    return 230;
  if ($ >= 1750 && $ <= 1756)
    return 230;
  if ($ >= 1759 && $ <= 1764)
    return 230;
  if ($ >= 1767 && $ <= 1768)
    return 230;
  if ($ >= 1770 && $ <= 1773)
    return 220;
  if ($ === 2260)
    return 230;
  if ($ >= 2275 && $ <= 2303)
    return 220;
  if ($ === 2364)
    return 7;
  if ($ === 2381)
    return 9;
  if ($ >= 2385 && $ <= 2388)
    return 230;
  if ($ === 2389)
    return 0;
  if ($ >= 2390 && $ <= 2391)
    return 0;
  if ($ === 2492)
    return 7;
  if ($ === 2509)
    return 9;
  if ($ === 2558)
    return 230;
  if ($ === 2620)
    return 7;
  if ($ === 2637)
    return 9;
  if ($ === 2748)
    return 7;
  if ($ === 2765)
    return 9;
  if ($ === 2876)
    return 7;
  if ($ === 2893)
    return 9;
  if ($ === 3021)
    return 9;
  if ($ === 3149)
    return 9;
  if ($ === 3157)
    return 84;
  if ($ === 3158)
    return 91;
  if ($ === 3260)
    return 7;
  if ($ === 3277)
    return 9;
  if ($ === 3405)
    return 9;
  if ($ === 3530)
    return 9;
  if ($ >= 3633 && $ <= 3642)
    return 0;
  if ($ >= 3655 && $ <= 3662)
    return tU($);
  if ($ >= 3761 && $ <= 3772)
    return 0;
  if ($ >= 3784 && $ <= 3789)
    return tU($);
  if ($ >= 3864 && $ <= 3865)
    return 220;
  if ($ === 3893)
    return 220;
  if ($ === 3895)
    return 220;
  if ($ === 3897)
    return 216;
  if ($ >= 3953 && $ <= 3966)
    return eU($);
  if ($ >= 3968 && $ <= 3972)
    return eU($);
  if ($ >= 3974 && $ <= 3975)
    return 230;
  if ($ === 4151)
    return 7;
  if ($ === 4153)
    return 9;
  if ($ === 4154)
    return 9;
  if ($ >= 12330 && $ <= 12335)
    return L2($);
  if ($ >= 12441 && $ <= 12442)
    return 8;
  if ($ >= 6832 && $ <= 6911)
    return F2($);
  if ($ >= 7616 && $ <= 7679)
    return D2($);
  if ($ >= 65056 && $ <= 65071)
    return 230;
  return 0;
}
function tU($) {
  if ($ >= 3656 && $ <= 3659)
    return 107;
  if ($ === 3660)
    return 0;
  if ($ === 3661)
    return 0;
  if ($ === 3662)
    return 0;
  if ($ >= 3784 && $ <= 3787)
    return 122;
  return 0;
}
function eU($) {
  if ($ === 3953)
    return 129;
  if ($ === 3954)
    return 130;
  if ($ === 3955)
    return 0;
  if ($ === 3956)
    return 132;
  if ($ === 3957)
    return 0;
  if ($ === 3958)
    return 0;
  if ($ === 3959)
    return 0;
  if ($ === 3960)
    return 0;
  if ($ === 3961)
    return 0;
  if ($ === 3962)
    return 130;
  if ($ === 3963)
    return 130;
  if ($ === 3964)
    return 130;
  if ($ === 3965)
    return 130;
  if ($ === 3966)
    return 0;
  if ($ === 3968)
    return 130;
  if ($ === 3969)
    return 0;
  if ($ === 3970)
    return 230;
  if ($ === 3971)
    return 230;
  return 9;
}
function L2($) {
  if ($ === 12330)
    return 218;
  if ($ === 12331)
    return 228;
  if ($ === 12332)
    return 232;
  if ($ === 12333)
    return 222;
  if ($ === 12334)
    return 224;
  return 224;
}
function F2($) {
  if ($ >= 6832 && $ <= 6846)
    return 230;
  if ($ === 6847)
    return 220;
  if ($ === 6848)
    return 220;
  return 230;
}
function D2($) {
  if ($ >= 7616 && $ <= 7617)
    return 230;
  if ($ === 7618)
    return 220;
  if ($ >= 7619 && $ <= 7626)
    return 230;
  if ($ === 7627)
    return 230;
  if ($ === 7628)
    return 230;
  if ($ === 7629)
    return 234;
  if ($ === 7630)
    return 214;
  if ($ === 7631)
    return 220;
  if ($ === 7632)
    return 202;
  if ($ >= 7633 && $ <= 7669)
    return 230;
  if ($ >= 7670 && $ <= 7672)
    return 232;
  if ($ === 7673)
    return 220;
  if ($ === 7674)
    return 218;
  return 230;
}
function B2($) {
  if ($ >= 1425 && $ <= 1441)
    return 220;
  if ($ >= 1442 && $ <= 1455)
    return 230;
  if ($ >= 1456 && $ <= 1465) {
    if ($ === 1456)
      return 10;
    if ($ === 1457)
      return 11;
    if ($ === 1458)
      return 12;
    if ($ === 1459)
      return 13;
    if ($ === 1460)
      return 14;
    if ($ === 1461)
      return 15;
    if ($ === 1462)
      return 16;
    if ($ === 1463)
      return 17;
    if ($ === 1464)
      return 18;
    return 19;
  }
  if ($ === 1466)
    return 19;
  if ($ === 1467)
    return 20;
  if ($ === 1468)
    return 21;
  return 22;
}
function R2($) {
  if ($ === 1611)
    return 27;
  if ($ === 1612)
    return 28;
  if ($ === 1613)
    return 29;
  if ($ === 1614)
    return 30;
  if ($ === 1615)
    return 31;
  if ($ === 1616)
    return 32;
  if ($ === 1617)
    return 33;
  if ($ === 1618)
    return 34;
  if ($ >= 1619 && $ <= 1621)
    return 230;
  if ($ === 1622)
    return 220;
  if ($ === 1623)
    return 230;
  if ($ === 1624)
    return 230;
  return 230;
}
function M2($) {
  if ($ >= 768 && $ <= 788)
    return 230;
  if ($ >= 789 && $ <= 789)
    return 232;
  if ($ >= 790 && $ <= 793)
    return 220;
  if ($ >= 794 && $ <= 794)
    return 232;
  if ($ >= 795 && $ <= 795)
    return 216;
  if ($ >= 796 && $ <= 800)
    return 220;
  if ($ >= 801 && $ <= 802)
    return 202;
  if ($ >= 803 && $ <= 806)
    return 220;
  if ($ >= 807 && $ <= 808)
    return 202;
  if ($ >= 809 && $ <= 819)
    return 220;
  if ($ >= 820 && $ <= 824)
    return 1;
  if ($ >= 825 && $ <= 828)
    return 220;
  if ($ >= 829 && $ <= 836)
    return 230;
  if ($ === 837)
    return 240;
  if ($ >= 838 && $ <= 846)
    return 230;
  if ($ === 847)
    return 0;
  if ($ >= 848 && $ <= 850)
    return 230;
  if ($ >= 851 && $ <= 854)
    return 220;
  if ($ >= 855 && $ <= 856)
    return 230;
  if ($ >= 857 && $ <= 858)
    return 220;
  if ($ >= 859 && $ <= 859)
    return 230;
  if ($ >= 860 && $ <= 860)
    return 233;
  if ($ >= 861 && $ <= 862)
    return 234;
  if ($ >= 863 && $ <= 863)
    return 233;
  if ($ >= 864 && $ <= 865)
    return 234;
  if ($ >= 866 && $ <= 866)
    return 233;
  if ($ >= 867 && $ <= 879)
    return 230;
  return 0;
}
function E5($) {
  let Q = $.length, Z = 1;
  while (Z < Q) {
    let K = $[Z];
    if (!K) {
      Z++;
      continue;
    }
    let q = G8(K.codepoint);
    if (q === 0) {
      Z++;
      continue;
    }
    let J = Z;
    while (J > 0) {
      let U = $[J - 1];
      if (!U)
        break;
      let W = G8(U.codepoint);
      if (W === 0)
        break;
      if (W <= q)
        break;
      $[J] = U, $[J - 1] = K, J--;
    }
    Z++;
  }
}
var T2 = new Map([[192, [65, 768]], [193, [65, 769]], [194, [65, 770]], [195, [65, 771]], [196, [65, 776]], [197, [65, 778]], [199, [67, 807]], [200, [69, 768]], [201, [69, 769]], [202, [69, 770]], [203, [69, 776]], [204, [73, 768]], [205, [73, 769]], [206, [73, 770]], [207, [73, 776]], [209, [78, 771]], [210, [79, 768]], [211, [79, 769]], [212, [79, 770]], [213, [79, 771]], [214, [79, 776]], [217, [85, 768]], [218, [85, 769]], [219, [85, 770]], [220, [85, 776]], [221, [89, 769]], [224, [97, 768]], [225, [97, 769]], [226, [97, 770]], [227, [97, 771]], [228, [97, 776]], [229, [97, 778]], [231, [99, 807]], [232, [101, 768]], [233, [101, 769]], [234, [101, 770]], [235, [101, 776]], [236, [105, 768]], [237, [105, 769]], [238, [105, 770]], [239, [105, 776]], [241, [110, 771]], [242, [111, 768]], [243, [111, 769]], [244, [111, 770]], [245, [111, 771]], [246, [111, 776]], [249, [117, 768]], [250, [117, 769]], [251, [117, 770]], [252, [117, 776]], [253, [121, 769]], [255, [121, 776]], [256, [65, 772]], [257, [97, 772]], [258, [65, 774]], [259, [97, 774]], [260, [65, 808]], [261, [97, 808]], [262, [67, 769]], [263, [99, 769]], [264, [67, 770]], [265, [99, 770]], [266, [67, 775]], [267, [99, 775]], [268, [67, 780]], [269, [99, 780]], [270, [68, 780]], [271, [100, 780]], [274, [69, 772]], [275, [101, 772]], [276, [69, 774]], [277, [101, 774]], [278, [69, 775]], [279, [101, 775]], [280, [69, 808]], [281, [101, 808]], [282, [69, 780]], [283, [101, 780]], [284, [71, 770]], [285, [103, 770]], [286, [71, 774]], [287, [103, 774]], [288, [71, 775]], [289, [103, 775]], [290, [71, 807]], [291, [103, 807]], [292, [72, 770]], [293, [104, 770]], [296, [73, 771]], [297, [105, 771]], [298, [73, 772]], [299, [105, 772]], [300, [73, 774]], [301, [105, 774]], [302, [73, 808]], [303, [105, 808]], [304, [73, 775]], [308, [74, 770]], [309, [106, 770]], [310, [75, 807]], [311, [107, 807]], [313, [76, 769]], [314, [108, 769]], [315, [76, 807]], [316, [108, 807]], [317, [76, 780]], [318, [108, 780]], [323, [78, 769]], [324, [110, 769]], [325, [78, 807]], [326, [110, 807]], [327, [78, 780]], [328, [110, 780]], [332, [79, 772]], [333, [111, 772]], [334, [79, 774]], [335, [111, 774]], [336, [79, 779]], [337, [111, 779]], [340, [82, 769]], [341, [114, 769]], [342, [82, 807]], [343, [114, 807]], [344, [82, 780]], [345, [114, 780]], [346, [83, 769]], [347, [115, 769]], [348, [83, 770]], [349, [115, 770]], [350, [83, 807]], [351, [115, 807]], [352, [83, 780]], [353, [115, 780]], [354, [84, 807]], [355, [116, 807]], [356, [84, 780]], [357, [116, 780]], [360, [85, 771]], [361, [117, 771]], [362, [85, 772]], [363, [117, 772]], [364, [85, 774]], [365, [117, 774]], [366, [85, 778]], [367, [117, 778]], [368, [85, 779]], [369, [117, 779]], [370, [85, 808]], [371, [117, 808]], [372, [87, 770]], [373, [119, 770]], [374, [89, 770]], [375, [121, 770]], [376, [89, 776]], [377, [90, 769]], [378, [122, 769]], [379, [90, 775]], [380, [122, 775]], [381, [90, 780]], [382, [122, 780]], [7840, [65, 803]], [7841, [97, 803]], [7842, [65, 777]], [7843, [97, 777]], [7864, [69, 803]], [7865, [101, 803]], [7866, [69, 777]], [7867, [101, 777]], [7868, [69, 771]], [7869, [101, 771]], [7880, [73, 777]], [7881, [105, 777]], [7882, [73, 803]], [7883, [105, 803]], [7884, [79, 803]], [7885, [111, 803]], [7886, [79, 777]], [7887, [111, 777]], [7908, [85, 803]], [7909, [117, 803]], [7910, [85, 777]], [7911, [117, 777]], [7922, [89, 768]], [7923, [121, 768]], [7924, [89, 803]], [7925, [121, 803]], [7926, [89, 777]], [7927, [121, 777]], [7928, [89, 771]], [7929, [121, 771]], [7936, [945, 787]], [7937, [945, 788]], [7944, [913, 787]], [7945, [913, 788]], [1081, [1080, 774]], [1049, [1048, 774]], [1105, [1077, 776]], [1025, [1045, 776]]]);
function w5($) {
  return T2.get($) ?? null;
}
var O2 = new Map([[65, new Map([[768, 192], [769, 193], [770, 194], [771, 195], [776, 196], [778, 197], [808, 260], [780, 461], [772, 256], [774, 258]])], [67, new Map([[807, 199], [769, 262], [770, 264], [780, 268], [775, 266]])], [69, new Map([[768, 200], [769, 201], [770, 202], [776, 203], [808, 280], [780, 282], [772, 274], [774, 276], [775, 278]])], [73, new Map([[768, 204], [769, 205], [770, 206], [776, 207], [771, 296], [772, 298], [774, 300], [808, 302], [775, 304]])], [78, new Map([[771, 209], [769, 323], [807, 325], [780, 327]])], [79, new Map([[768, 210], [769, 211], [770, 212], [771, 213], [776, 214], [772, 332], [774, 334], [779, 336], [808, 490]])], [85, new Map([[768, 217], [769, 218], [770, 219], [776, 220], [771, 360], [772, 362], [774, 364], [778, 366], [779, 368], [808, 370], [780, 467]])], [89, new Map([[769, 221], [770, 374], [776, 376]])], [97, new Map([[768, 224], [769, 225], [770, 226], [771, 227], [776, 228], [778, 229], [808, 261], [780, 462], [772, 257], [774, 259]])], [99, new Map([[807, 231], [769, 263], [770, 265], [780, 269], [775, 267]])], [101, new Map([[768, 232], [769, 233], [770, 234], [776, 235], [808, 281], [780, 283], [772, 275], [774, 277], [775, 279]])], [105, new Map([[768, 236], [769, 237], [770, 238], [776, 239], [771, 297], [772, 299], [774, 301], [808, 303]])], [110, new Map([[771, 241], [769, 324], [807, 326], [780, 328]])], [111, new Map([[768, 242], [769, 243], [770, 244], [771, 245], [776, 246], [772, 333], [774, 335], [779, 337], [808, 491]])], [117, new Map([[768, 249], [769, 250], [770, 251], [776, 252], [771, 361], [772, 363], [774, 365], [778, 367], [779, 369], [808, 371], [780, 468]])], [121, new Map([[769, 253], [776, 255], [770, 375]])], [83, new Map([[769, 346], [770, 348], [807, 350], [780, 352]])], [115, new Map([[769, 347], [770, 349], [807, 351], [780, 353]])], [90, new Map([[769, 377], [775, 379], [780, 381]])], [122, new Map([[769, 378], [775, 380], [780, 382]])]]);
function $W($, Q) {
  let Z = O2.get($);
  if (!Z)
    return null;
  return Z.get(Q) ?? null;
}
function P2($) {
  if ($.length === 0)
    return $;
  let Q = [], Z = 0;
  while (Z < $.length) {
    let K = $[Z];
    if (G8(K.codepoint) === 0) {
      let J = K.codepoint, U = 0, W = Z + 1;
      while (W < $.length) {
        let H = $[W], _ = G8(H.codepoint);
        if (_ === 0)
          break;
        if (_ > U || U === 0) {
          let N = $W(J, H.codepoint);
          if (N !== null) {
            J = N, W++;
            continue;
          }
        }
        U = _, W++;
      }
      Q.push({ glyphId: K.glyphId, cluster: K.cluster, mask: K.mask, codepoint: J });
      for (let H = Z + 1;H < W; H++) {
        let _ = $[H];
        if (!_)
          continue;
        let N = G8(_.codepoint);
        if ($W(J, _.codepoint) === null && N !== 0)
          Q.push(_);
      }
      Z = W;
    } else
      Q.push(K), Z++;
  }
  return Q;
}
function I2($, Q) {
  if (Q === 0)
    return $;
  if (Q === 1) {
    let Z = [];
    for (let K = 0;K < $.length; K++) {
      let q = $[K], J = w5(q.codepoint);
      if (J)
        for (let U = 0;U < J.length; U++) {
          let W = J[U];
          Z.push({ glyphId: q.glyphId, cluster: q.cluster, mask: q.mask, codepoint: W });
        }
      else
        Z.push(q);
    }
    return E5(Z), Z;
  }
  if (Q === 2) {
    let Z = [];
    for (let K = 0;K < $.length; K++) {
      let q = $[K], J = w5(q.codepoint);
      if (J)
        for (let U = 0;U < J.length; U++) {
          let W = J[U];
          Z.push({ glyphId: q.glyphId, cluster: q.cluster, mask: q.mask, codepoint: W });
        }
      else
        Z.push(q);
    }
    return E5(Z), P2(Z);
  }
  if (Q === 3) {
    let Z = [];
    for (let K = 0;K < $.length; K++) {
      let q = $[K], J = w5(q.codepoint);
      if (J)
        for (let U = 0;U < J.length; U++) {
          let W = J[U];
          Z.push({ glyphId: q.glyphId, cluster: q.cluster, mask: q.mask, codepoint: W });
        }
      else
        Z.push(q);
    }
    return E5(Z), Z;
  }
  return $;
}
function v2($) {
  for (let Q = 0;Q < $.length; Q++) {
    let Z = $[Q];
    if (!Z)
      continue;
    let K = Z.codepoint;
    if (K >= 768 && K < 880)
      return true;
    if (K >= 1425 && K < 1480)
      return true;
    if (K >= 1552 && K < 2304)
      return true;
    if (K >= 2364 && K < 2416)
      return true;
    if (K >= 2492 && K < 2559)
      return true;
    if (K >= 2620 && K < 2677)
      return true;
    if (K >= 2748 && K < 2815)
      return true;
    if (K >= 2876 && K < 2928)
      return true;
    if (K >= 3021 && K < 3032)
      return true;
    if (K >= 3149 && K < 3184)
      return true;
    if (K >= 3260 && K < 3327)
      return true;
    if (K >= 3405 && K < 3440)
      return true;
    if (K >= 3530 && K < 3573)
      return true;
    if (K >= 3633 && K < 3664)
      return true;
    if (K >= 3761 && K < 3792)
      return true;
    if (K >= 3864 && K < 3976)
      return true;
    if (K >= 4151 && K < 4155)
      return true;
    if (K >= 6832 && K < 6912)
      return true;
    if (K >= 7616 && K < 7680)
      return true;
    if (K >= 12330 && K < 12336)
      return true;
    if (K >= 12441 && K < 12443)
      return true;
    if (K >= 65056 && K < 65072)
      return true;
  }
  return false;
}
function o1($, Q, Z) {
  if (!v2(Q))
    return;
  let K = new Map, q = (J) => {
    let U = K.get(J);
    if (U === undefined)
      U = $.gdef ? z8($.gdef, J) : 0, K.set(J, U);
    return U;
  };
  for (let J = 0;J < Q.length; J++) {
    let U = Q[J], W = Z[J], H = q(U.glyphId), _ = G8(U.codepoint);
    if (H !== 3 && _ === 0)
      continue;
    let N = -1;
    for (let G = J - 1;G >= 0; G--) {
      let Y = Q[G], j = q(Y.glyphId), E = G8(Y.codepoint);
      if (j === 1 || j === 0 && E === 0) {
        N = G;
        break;
      }
    }
    if (N < 0)
      continue;
    let V = Q[N], X = Z[N], z = $.advanceWidth(V.glyphId);
    k2($, U, W, V, X, z, _), W.xAdvance = 0, W.yAdvance = 0;
  }
}
function k2($, Q, Z, K, q, J, U) {
  let W = $.advanceWidth(Q.glyphId), H = $.unitsPerEm, _ = (J - W) / 2, N = 0;
  if (U >= 200 && U <= 240)
    N = H * 0.7, _ = (J - W) / 2;
  else if (U >= 202 && U <= 220)
    N = -H * 0.15, _ = (J - W) / 2;
  else if (U === 1)
    _ = (J - W) / 2, N = H * 0.3;
  else if (U >= 7 && U <= 9)
    N = -H * 0.1, _ = (J - W) / 2;
  else if (U >= 10 && U <= 35) {
    if (U <= 22)
      N = -H * 0.2;
    else
      N = U < 30 ? -H * 0.15 : H * 0.6;
    _ = (J - W) / 2;
  }
  Z.xOffset = q.xOffset + _ - J, Z.yOffset = q.yOffset + N;
}
function i1($, Q, Z) {
  if (!$.kern)
    return;
  for (let q = 0;q < Q.length - 1; q++) {
    let J = Q[q], U = Q[q + 1];
    if (!J || !U)
      continue;
    let W = Z[q];
    if (!W)
      continue;
    let H = $.gdef ? z8($.gdef, J.glyphId) : 0, _ = $.gdef ? z8($.gdef, U.glyphId) : 0;
    if (H === 3 || _ === 3)
      continue;
    let N = C2($, J.glyphId, U.glyphId);
    if (N !== 0)
      W.xAdvance += N;
  }
}
function C2($, Q, Z) {
  let K = $.kern;
  if (!K)
    return 0;
  return y1(K, Q, Z);
}
function ZW($, Q = true) {
  if ($ < 1 || $ > 20)
    throw Error(`Stylistic set number must be 1-20, got ${$}`);
  let Z = `ss${$.toString().padStart(2, "0")}`;
  return { tag: C(Z), enabled: Q };
}
function S2($, Q = true) {
  return $.map((Z) => ZW(Z, Q));
}
function KW($, Q = true) {
  if ($ < 1 || $ > 99)
    throw Error(`Character variant number must be 1-99, got ${$}`);
  let Z = `cv${$.toString().padStart(2, "0")}`;
  return { tag: C(Z), enabled: Q };
}
function h2($, Q = true) {
  return $.map((Z) => KW(Z, Q));
}
function y2($ = true) {
  return { tag: C("liga"), enabled: $ };
}
function b2($ = true) {
  return { tag: C("dlig"), enabled: $ };
}
function m2($ = true) {
  return { tag: C("hlig"), enabled: $ };
}
function g2($ = true) {
  return { tag: C("calt"), enabled: $ };
}
function u2($ = true) {
  return { tag: C("salt"), enabled: $ };
}
function x2($ = true) {
  return { tag: C("swsh"), enabled: $ };
}
function qW($ = true) {
  return { tag: C("smcp"), enabled: $ };
}
function JW($ = true) {
  return { tag: C("c2sc"), enabled: $ };
}
function f2($ = true) {
  return { tag: C("pcap"), enabled: $ };
}
function l2($ = true) {
  return [qW($), JW($)];
}
function d2($ = true) {
  return { tag: C("onum"), enabled: $ };
}
function n2($ = true) {
  return { tag: C("lnum"), enabled: $ };
}
function p2($ = true) {
  return { tag: C("pnum"), enabled: $ };
}
function c2($ = true) {
  return { tag: C("tnum"), enabled: $ };
}
function o2($ = true) {
  return { tag: C("frac"), enabled: $ };
}
function i2($ = true) {
  return { tag: C("ordn"), enabled: $ };
}
function s2($ = true) {
  return { tag: C("zero"), enabled: $ };
}
function r2($ = true) {
  return { tag: C("sups"), enabled: $ };
}
function a2($ = true) {
  return { tag: C("subs"), enabled: $ };
}
function t2($ = true) {
  return { tag: C("sinf"), enabled: $ };
}
function e2($ = true) {
  return { tag: C("case"), enabled: $ };
}
function $z($ = true) {
  return { tag: C("cpsp"), enabled: $ };
}
function Qz($ = true) {
  return { tag: C("kern"), enabled: $ };
}
function UW($ = true) {
  return { tag: C("vert"), enabled: $ };
}
function WW($ = true) {
  return { tag: C("vrt2"), enabled: $ };
}
function HW($ = true) {
  return { tag: C("vkna"), enabled: $ };
}
function Zz($ = true) {
  return [UW($), WW($), HW($)];
}
function Kz($ = true) {
  return { tag: C("ruby"), enabled: $ };
}
function qz($ = true) {
  return { tag: C("hwid"), enabled: $ };
}
function Jz($ = true) {
  return { tag: C("fwid"), enabled: $ };
}
function Uz($ = true) {
  return { tag: C("pwid"), enabled: $ };
}
function Wz($ = true) {
  return { tag: C("qwid"), enabled: $ };
}
function Hz($ = true) {
  return { tag: C("twid"), enabled: $ };
}
function _z($ = true) {
  return { tag: C("jp78"), enabled: $ };
}
function Nz($ = true) {
  return { tag: C("jp83"), enabled: $ };
}
function Vz($ = true) {
  return { tag: C("jp90"), enabled: $ };
}
function Xz($ = true) {
  return { tag: C("jp04"), enabled: $ };
}
function zz($ = true) {
  return { tag: C("smpl"), enabled: $ };
}
function Gz($ = true) {
  return { tag: C("trad"), enabled: $ };
}
function _W($, Q = true) {
  return { tag: C($), enabled: Q };
}
function Yz($, Q = true) {
  return $.map((Z) => _W(Z, Q));
}
function jz(...$) {
  let Q = [];
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z];
    if (Array.isArray(K))
      Q.push(...K);
    else
      Q.push(K);
  }
  return Q;
}
var Ez = new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 1, 2, 1, 2, 2, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 2, 5, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 2, 2, 2, 2, 1, 1, 0, 2, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 2, 2, 0, 0, 2]);
var z0 = 0;
var NW = 1;
var s1 = 2;
var VW = 3;
var a1 = 4;
var A5 = 5;
function r1($) {
  if ($ >= 1536 && $ <= 1791)
    return Ez[$ - 1536];
  if ($ < 1536)
    return z0;
  if ($ >= 1808 && $ <= 1810)
    return a1;
  if ($ >= 2259 && $ <= 2303)
    return A5;
  return z0;
}
function XW() {
  return ["ccmp", "locl", "isol", "init", "medi", "fina", "rlig", "rclt", "calt", "liga"];
}
function t1($) {
  let Q = $.length;
  for (let Z = 0;Z < Q; Z++) {
    let K = $[Z], q = K.codepoint ?? 0, J = r1(q);
    if (J === z0 || J === A5)
      continue;
    let U = z0;
    for (let V = Z - 1;V >= 0; V--) {
      let z = $[V].codepoint ?? 0, G = r1(z);
      if (G !== A5) {
        U = G;
        break;
      }
    }
    let W = z0;
    for (let V = Z + 1;V < Q; V++) {
      let z = $[V].codepoint ?? 0, G = r1(z);
      if (G !== A5) {
        W = G;
        break;
      }
    }
    let H = U === s1 || U === a1 || U === VW, _ = W === s1 || W === NW || W === VW, N = 0;
    if (J === s1)
      if (H && _)
        N = 4;
      else if (H)
        N = 2;
      else if (_)
        N = 8;
      else
        N = 1;
    else if (J === NW)
      if (H)
        N = 2;
      else
        N = 1;
    else if (J === a1)
      if (_)
        N = 8;
      else
        N = 1;
    if (N !== 0)
      K.mask = K.mask & 4294967280 | N;
  }
}
function zW() {
  return ["ccmp", "locl", "calt", "liga"];
}
function GW($) {
  return $ === "ethi" || $ === "Ethi";
}
function YW() {
  return ["ccmp", "locl", "calt", "liga"];
}
function jW($) {
  return $ === "geor" || $ === "Geor" || $ === "geok" || $ === "Geok";
}
function EW() {
  return ["ccmp", "locl", "isol", "init", "medi", "fina", "rlig", "calt", "liga", "vert"];
}
function wW($) {
  return ["mong", "Mong", "phag", "Phag"].includes($);
}
function AW() {
  return ["ccmp", "locl", "stch", "isol", "init", "medi", "fina", "rlig", "calt", "liga"];
}
function LW($) {
  return $ === "syrc" || $ === "Syrc";
}
function FW() {
  return ["ccmp", "locl", "abvs", "blws", "calt", "liga"];
}
function DW($) {
  return $ === "tibt" || $ === "Tibt";
}
var BW = new WeakMap;
var wz = 64;
var Az = ["ccmp", "locl", "rlig", "rclt", "calt", "liga"];
var RW = ["kern", "mark", "mkmk"];
function Lz($) {
  let Q = new Set(Az), Z = $.padEnd(4, " "), K = Z.toLowerCase();
  if (LW(Z))
    for (let q of AW())
      Q.add(q);
  else if (K === "arab" || K === "mand" || K === "nko " || K === "nkoo")
    for (let q of XW())
      Q.add(q);
  else if (wW(Z))
    for (let q of EW())
      Q.add(q);
  else if (DW(Z))
    for (let q of FW())
      Q.add(q);
  else if (GW(Z))
    for (let q of zW())
      Q.add(q);
  else if (jW(Z))
    for (let q of YW())
      Q.add(q);
  return [...Q];
}
function Fz($, Q, Z, K, q) {
  if (K.length === 0 && q === null)
    return Q === null ? `${$}||${Z}||` : `${$}|${Q}|${Z}||`;
  let J = K.map((W) => `${h9(W.tag)}:${W.enabled ? "1" : "0"}`).sort().join(","), U = q ? q.map((W) => W.toFixed(4)).join(",") : "";
  return `${$}|${Q || ""}|${Z}|${J}|${U}`;
}
function L5($, Q, Z, K, q = [], J = null) {
  let U = Fz(Q, Z, K, q, J), W = BW.get($);
  if (!W)
    W = new Map, BW.set($, W);
  let H = W.get(U);
  if (H)
    return H;
  let _ = Bz($, Q, Z, K, q, J);
  if (W.size >= wz) {
    let N = W.keys().next().value;
    if (N !== undefined)
      W.delete(N);
  }
  return W.set(U, _), _;
}
function Dz($, Q, Z, K, q = [], J = null) {
  return L5($, Q, Z, K, q, J);
}
function Bz($, Q, Z, K, q = [], J = null) {
  let U = C(Q.padEnd(4, " ")), W = Z ? C(Z.padEnd(4, " ")) : null, H = new Set, _ = Lz(Q);
  for (let G = 0;G < _.length; G++)
    H.add(C(_[G]));
  for (let G = 0;G < RW.length; G++)
    H.add(C(RW[G]));
  for (let G = 0;G < q.length; G++) {
    let Y = q[G];
    if (Y.enabled)
      H.add(Y.tag);
    else
      H.delete(Y.tag);
  }
  let N = MW($.gsub, U, W, H, J), V = MW($.gpos, U, W, H, J), X = new Map;
  for (let G = 0;G < N.length; G++) {
    let Y = N[G];
    X.set(Y.index, Y);
  }
  let z = new Map;
  for (let G = 0;G < V.length; G++) {
    let Y = V[G];
    z.set(Y.index, Y);
  }
  return { script: U, language: W, direction: K, gsubLookups: N, gposLookups: V, gsubLookupMap: X, gposLookupMap: z };
}
function MW($, Q, Z, K, q) {
  if (!$)
    return [];
  let J = $, U = new Set, W = U0(J.scriptList, Q);
  if (!W)
    W = U0(J.scriptList, C("DFLT"));
  if (!W)
    W = U0(J.scriptList, C("latn"));
  if (!W)
    return [];
  let H = j1(W, Z);
  if (!H)
    return [];
  let _ = J.featureVariations, N = _ && q ? X0(_, q) : null, V = new Map;
  if (N) {
    let Y = N.featureTableSubstitution.substitutions;
    for (let j = 0;j < Y.length; j++) {
      let E = Y[j];
      V.set(E.featureIndex, E.alternateFeature.lookupListIndices);
    }
  }
  if (H.requiredFeatureIndex !== 65535) {
    let Y = H5(J.featureList, H.requiredFeatureIndex);
    if (Y) {
      let E = V.get(H.requiredFeatureIndex) ?? Y.feature.lookupListIndices;
      for (let w = 0;w < E.length; w++)
        U.add(E[w]);
    }
  }
  let X = H.featureIndices;
  for (let Y = 0;Y < X.length; Y++) {
    let j = X[Y], E = H5(J.featureList, j);
    if (!E)
      continue;
    if (K.has(E.featureTag)) {
      let L = V.get(j) ?? E.feature.lookupListIndices;
      for (let A = 0;A < L.length; A++)
        U.add(L[A]);
    }
  }
  let z = [], G = [...U].sort((Y, j) => Y - j);
  for (let Y = 0;Y < G.length; Y++) {
    let j = G[Y], E = J.lookups[j];
    if (E)
      z.push({ index: j, lookup: E });
  }
  return z;
}
function $Q($) {
  return $ >= 44032 && $ <= 55203;
}
function Rz($) {
  return $ >= 4352 && $ <= 4607 || $ >= 43360 && $ <= 43388 || $ >= 55216 && $ <= 55291;
}
function Mz($) {
  return $ >= 4352 && $ < 4371 || $ >= 43360 && $ <= 43388;
}
function Tz($) {
  return $ >= 4449 && $ < 4470 || $ >= 55216 && $ <= 55238;
}
function Oz($) {
  return $ > 4519 && $ <= 4546 || $ >= 55243 && $ <= 55291;
}
function Pz($) {
  if (!$Q($))
    return [$];
  let Q = $ - 44032, Z = Math.floor(Q / 588), K = Math.floor(Q % 588 / 28), q = Q % 28, J = [4352 + Z, 4449 + K];
  if (q > 0)
    J.push(4519 + q);
  return J;
}
function TW($, Q, Z = 0) {
  let K = $ - 4352, q = Q - 4449, J = Z === 0 ? 0 : Z - 4519;
  if (K < 0 || K >= 19)
    return null;
  if (q < 0 || q >= 21)
    return null;
  if (J < 0 || J >= 28)
    return null;
  return 44032 + K * 588 + q * 28 + J;
}
function G0($) {
  if (Mz($))
    return 1;
  if (Tz($))
    return 2;
  if (Oz($))
    return 3;
  if ($Q($))
    return ($ - 44032) % 28 === 0 ? 4 : 5;
  return 0;
}
var e1 = { ljmo: 1, vjmo: 2, tjmo: 4 };
function QQ($) {
  for (let Q = 0;Q < $.length; Q++) {
    let Z = $[Q];
    switch (G0(Z.codepoint)) {
      case 1:
        Z.mask |= e1.ljmo;
        break;
      case 2:
        Z.mask |= e1.vjmo;
        break;
      case 3:
        Z.mask |= e1.tjmo;
        break;
      case 4:
      case 5:
        break;
    }
  }
}
function ZQ($) {
  let Q = [], Z = 0;
  while (Z < $.length) {
    let K = $[Z];
    if (!K) {
      Z++;
      continue;
    }
    let q = G0(K.codepoint);
    if (q === 1 && Z + 1 < $.length) {
      let J = $[Z + 1];
      if (!J) {
        Q.push(K), Z++;
        continue;
      }
      if (G0(J.codepoint) === 2) {
        let W = 0, H = 2;
        if (Z + 2 < $.length) {
          let N = $[Z + 2];
          if (N) {
            if (G0(N.codepoint) === 3)
              W = N.codepoint, H = 3;
          }
        }
        let _ = TW(K.codepoint, J.codepoint, W);
        if (_ !== null) {
          Q.push({ glyphId: K.glyphId, cluster: K.cluster, mask: K.mask, codepoint: _ }), Z += H;
          continue;
        }
      }
    }
    if (q === 4 && Z + 1 < $.length) {
      let J = $[Z + 1];
      if (!J) {
        Q.push(K), Z++;
        continue;
      }
      if (G0(J.codepoint) === 3) {
        let W = Pz(K.codepoint), [H, _] = W;
        if (W.length === 2 && H !== undefined && _ !== undefined) {
          let N = TW(H, _, J.codepoint);
          if (N !== null) {
            Q.push({ glyphId: K.glyphId, cluster: K.cluster, mask: K.mask, codepoint: N }), Z += 2;
            continue;
          }
        }
      }
    }
    Q.push(K), Z++;
  }
  return Q;
}
function OW($) {
  return $Q($) || Rz($) || $ >= 12593 && $ <= 12686;
}
function Iz($) {
  return $ >= 1424 && $ <= 1535 || $ >= 64285 && $ <= 64335;
}
function vz($) {
  if ($ >= 1425 && $ <= 1455)
    return 6;
  if ($ >= 1456 && $ <= 1469)
    return 2;
  if ($ === 1470)
    return 7;
  if ($ === 1471)
    return 5;
  if ($ === 1472 || $ === 1475)
    return 8;
  if ($ === 1473 || $ === 1474)
    return 4;
  if ($ === 1468)
    return 3;
  if ($ === 1469)
    return 2;
  if ($ >= 1488 && $ <= 1514)
    return 1;
  if ($ >= 1520 && $ <= 1524)
    return 1;
  if ($ >= 64285 && $ <= 64335)
    return 1;
  if (Iz($))
    return 0;
  return 0;
}
function KQ($) {
  let Q = 0;
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z];
    if (!K)
      continue;
    if (vz(K.codepoint) === 1)
      Q = Z;
    K.mask = K.mask & 4294901760 | Q & 65535;
  }
}
function PW($) {
  return $ >= 2304 && $ <= 2431;
}
function IW($) {
  return $ >= 2432 && $ <= 2559;
}
function vW($) {
  return $ >= 2560 && $ <= 2687;
}
function kW($) {
  return $ >= 2688 && $ <= 2815;
}
function CW($) {
  return $ >= 2816 && $ <= 2943;
}
function SW($) {
  return $ >= 2944 && $ <= 3071;
}
function hW($) {
  return $ >= 3072 && $ <= 3199;
}
function yW($) {
  return $ >= 3200 && $ <= 3327;
}
function bW($) {
  return $ >= 3328 && $ <= 3455;
}
function mW($) {
  return PW($) || IW($) || vW($) || kW($) || CW($) || SW($) || hW($) || yW($) || bW($);
}
function K8($) {
  if ($ === 8204)
    return 5;
  if ($ === 8205)
    return 6;
  if ($ === 9676)
    return 12;
  if (PW($)) {
    if ($ >= 2362 && $ <= 2363 || $ >= 2366 && $ <= 2380 || $ >= 2382 && $ <= 2383 || $ >= 2389 && $ <= 2391)
      return 7;
    if ($ === 2381)
      return 4;
    if ($ === 2364)
      return 3;
    if ($ >= 2305 && $ <= 2307)
      return 8;
    if ($ >= 2385 && $ <= 2388)
      return 9;
    if ($ >= 2308 && $ <= 2324 || $ === 2400 || $ === 2401 || $ === 2418 || $ >= 2422 && $ <= 2423)
      return 2;
    if ($ >= 2325 && $ <= 2361 || $ >= 2392 && $ <= 2399 || $ === 2424 || $ === 2425 || $ === 2426 || $ >= 2427 && $ <= 2428 || $ >= 2430 && $ <= 2431) {
      if ($ === 2352)
        return 15;
      return 1;
    }
    if ($ >= 2406 && $ <= 2415)
      return 17;
    return 0;
  }
  if (IW($)) {
    if ($ >= 2494 && $ <= 2500 || $ >= 2503 && $ <= 2504 || $ >= 2507 && $ <= 2508 || $ === 2519)
      return 7;
    if ($ === 2509)
      return 4;
    if ($ === 2492)
      return 3;
    if ($ >= 2433 && $ <= 2435)
      return 8;
    if ($ >= 2437 && $ <= 2444 || $ >= 2447 && $ <= 2448 || $ >= 2451 && $ <= 2452 || $ === 2528 || $ === 2529)
      return 2;
    if ($ >= 2453 && $ <= 2472 || $ >= 2474 && $ <= 2480 || $ === 2482 || $ >= 2486 && $ <= 2489 || $ >= 2524 && $ <= 2525 || $ >= 2527 && $ <= 2529) {
      if ($ === 2480)
        return 15;
      return 1;
    }
    return 0;
  }
  if (vW($) || kW($) || CW($) || SW($) || hW($) || yW($) || bW($)) {
    let Q = $ & 127;
    if (Q >= 1 && Q <= 3)
      return 8;
    if (Q >= 5 && Q <= 20)
      return 2;
    if (Q >= 21 && Q <= 57)
      return 1;
    if (Q === 60)
      return 3;
    if (Q >= 62 && Q <= 76)
      return 7;
    if (Q === 77)
      return 4;
    return 0;
  }
  return 0;
}
function gW($) {
  let Q = [], Z = $.length;
  if (Z === 0)
    return Q;
  let K = 0;
  while (K < Z) {
    let q = kz($, K);
    Q.push(q), K = q.end;
  }
  return Q;
}
function kz($, Q) {
  let Z = $.length, K = Q, q = -1, J = false;
  if (K + 1 < Z) {
    let W = $[K], H = $[K + 1];
    if (W && H) {
      let _ = K8(W.codepoint ?? 0), N = K8(H.codepoint ?? 0);
      if (_ === 15 && N === 4)
        J = true, K += 2;
    }
  }
  let U = -1;
  while (K < Z) {
    let W = $[K];
    if (!W) {
      K++;
      continue;
    }
    let H = W.codepoint ?? 0, _ = K8(H);
    if (_ === 1 || _ === 15) {
      if (U = K, K++, K < Z) {
        let N = $[K];
        if (N && K8(N.codepoint ?? 0) === 3)
          K++;
      }
      if (K < Z) {
        let N = $[K];
        if (N && K8(N.codepoint ?? 0) === 4) {
          if (K++, K < Z) {
            let V = $[K];
            if (V) {
              let X = K8(V.codepoint ?? 0);
              if (X === 6 || X === 5)
                K++;
            }
          }
          continue;
        }
      }
      break;
    } else if (_ === 2) {
      K++;
      break;
    } else if (_ === 3)
      K++;
    else {
      if (U === -1)
        K++;
      break;
    }
  }
  q = U >= 0 ? U : Q;
  while (K < Z) {
    let W = $[K];
    if (!W) {
      K++;
      continue;
    }
    let H = W.codepoint ?? 0, _ = K8(H);
    if (_ === 7 || _ === 8 || _ === 9 || _ === 3)
      K++;
    else if (_ === 4) {
      K++;
      break;
    } else
      break;
  }
  if (K === Q)
    K = Q + 1;
  return { start: Q, end: K, hasReph: J, baseConsonant: q };
}
var E6 = { nukt: 1, akhn: 2, rphf: 4, rkrf: 8, pref: 16, blwf: 32, abvf: 64, half: 128, pstf: 256, vatu: 512, cjct: 1024, init: 2048, pres: 4096, abvs: 8192, blws: 16384, psts: 32768 };
function uW($) {
  if ($ >= 2304 && $ <= 2431) {
    if ($ === 2367)
      return 0;
    if ($ >= 2373 && $ <= 2376)
      return 1;
    if ($ >= 2369 && $ <= 2372)
      return 2;
    return 3;
  }
  if ($ >= 2432 && $ <= 2559) {
    if ($ === 2495)
      return 0;
    if ($ === 2503 || $ === 2504)
      return 0;
    if ($ >= 2497 && $ <= 2500)
      return 2;
    return 3;
  }
  if ($ >= 2944 && $ <= 3071) {
    if ($ >= 3014 && $ <= 3016)
      return 0;
    if ($ === 3009 || $ === 3010)
      return 2;
    return 3;
  }
  if ($ >= 3072 && $ <= 3199) {
    if ($ >= 3134 && $ <= 3136 || $ >= 3142 && $ <= 3144)
      return 1;
    if ($ >= 3137 && $ <= 3140)
      return 2;
    return 3;
  }
  if ($ >= 3200 && $ <= 3327) {
    if ($ >= 3262 && $ <= 3264 || $ >= 3270 && $ <= 3272)
      return 1;
    if ($ >= 3265 && $ <= 3268)
      return 2;
    return 3;
  }
  if ($ >= 3328 && $ <= 3455) {
    if ($ >= 3398 && $ <= 3400)
      return 0;
    if ($ >= 3393 && $ <= 3395)
      return 2;
    return 3;
  }
  if ($ >= 2560 && $ <= 2687) {
    if ($ === 2623)
      return 0;
    if ($ === 2625 || $ === 2626)
      return 2;
    return 3;
  }
  if ($ >= 2688 && $ <= 2815) {
    if ($ === 2751)
      return 0;
    if ($ >= 2753 && $ <= 2756)
      return 2;
    return 3;
  }
  if ($ >= 2816 && $ <= 2943) {
    if ($ === 2879)
      return 0;
    if ($ >= 2881 && $ <= 2883)
      return 2;
    return 3;
  }
  return 3;
}
function qQ($) {
  let Q = gW($);
  for (let Z = 0;Z < Q.length; Z++) {
    let K = Q[Z];
    for (let q = K.start;q < K.end; q++) {
      let J = $[q];
      if (J) {
        J.mask = J.mask & 65535 | (Z & 65535) << 16;
        let U = K8(J.codepoint);
        if (U === 3)
          J.mask |= E6.nukt;
        if (U === 4) {
          if (q < K.baseConsonant)
            J.mask |= E6.half;
          else if (q > K.baseConsonant)
            J.mask |= E6.blwf | E6.pstf;
        }
        if (U === 1 || U === 15) {
          if (q < K.baseConsonant)
            J.mask |= E6.half | E6.cjct;
          else if (q > K.baseConsonant)
            J.mask |= E6.blwf | E6.pstf | E6.vatu;
        }
        if (K.hasReph && q < K.start + 2)
          J.mask |= E6.rphf;
        if (U === 7)
          switch (uW(J.codepoint)) {
            case 0:
              J.mask |= E6.pref | E6.pres;
              break;
            case 1:
              J.mask |= E6.abvf | E6.abvs;
              break;
            case 2:
              J.mask |= E6.blwf | E6.blws;
              break;
            case 3:
              J.mask |= E6.pstf | E6.psts;
              break;
          }
        if (U === 8)
          J.mask |= E6.abvs | E6.psts;
      }
    }
  }
}
function JQ($) {
  let Q = gW($);
  for (let Z = 0;Z < Q.length; Z++) {
    let K = Q[Z];
    Cz($, K);
  }
}
function Cz($, Q) {
  let { start: Z, end: K, baseConsonant: q, hasReph: J } = Q, U = [];
  for (let W = q + 1;W < K; W++) {
    let H = $[W];
    if (!H)
      continue;
    if (K8(H.codepoint) === 7) {
      if (uW(H.codepoint) === 0)
        U.push({ index: W, info: H });
    }
  }
  if (U.length > 0) {
    U.sort((W, H) => H.index - W.index);
    for (let W = 0;W < U.length; W++) {
      let { index: H, info: _ } = U[W];
      $.splice(H, 1);
      let N = J ? Z + 2 : Z;
      $.splice(N, 0, _);
    }
  }
  if (J && K > Z + 2) {
    let W = $[Z], H = $[Z + 1];
    if (W && H) {
      let _ = K - 1;
      while (_ > q) {
        let N = $[_];
        if (!N)
          break;
        let V = K8(N.codepoint);
        if (V === 8 || V === 9)
          _--;
        else
          break;
      }
      if (_ > Z + 1) {
        $.splice(Z, 2);
        let N = _ - 2;
        $.splice(N + 1, 0, W, H);
      }
    }
  }
}
function D9($) {
  if ($ < 6016 || $ > 6143)
    return 0;
  if ($ >= 6016 && $ <= 6050)
    return 1;
  if ($ === 6051 || $ === 6052)
    return 2;
  if ($ >= 6053 && $ <= 6067)
    return 2;
  if ($ >= 6070 && $ <= 6085)
    return 3;
  if ($ === 6086)
    return 8;
  if ($ === 6087)
    return 9;
  if ($ === 6088)
    return 7;
  if ($ === 6089 || $ === 6090)
    return 5;
  if ($ === 6098)
    return 4;
  if ($ === 6092)
    return 6;
  if ($ >= 6091 && $ <= 6097)
    return 7;
  if ($ >= 6099 && $ <= 6109)
    return 7;
  return 0;
}
var v8 = { pref: 1, blwf: 2, abvf: 4, pstf: 8, cfar: 16, pres: 32, abvs: 64, blws: 128, psts: 256, clig: 512 };
function xW($) {
  return $ >= 6016 && $ <= 6143 || $ >= 6624 && $ <= 6655;
}
function UQ($) {
  let Q = 0;
  while (Q < $.length) {
    let Z = $[Q];
    if (!Z) {
      Q++;
      continue;
    }
    let K = D9(Z.codepoint);
    if (K === 0) {
      Q++;
      continue;
    }
    let q = Q, J = -1;
    if (K === 1)
      J = Q;
    let U = Q + 1;
    while (U < $.length) {
      let W = $[U];
      if (!W) {
        U++;
        continue;
      }
      let H = D9(W.codepoint);
      if (H === 0)
        break;
      if (H === 1) {
        let _ = $[U - 1];
        if (_ && D9(_.codepoint) !== 4)
          break;
      }
      if (H === 4 && U + 1 < $.length) {
        let _ = $[U + 1];
        if (_ && D9(_.codepoint) === 1) {
          W.mask |= v8.blwf, _.mask |= v8.blwf, U += 2;
          continue;
        }
      }
      if (H === 3)
        if (W.codepoint >= 6081 && W.codepoint <= 6083)
          W.mask |= v8.pref;
        else if (W.codepoint >= 6071 && W.codepoint <= 6074)
          W.mask |= v8.abvf;
        else if (W.codepoint === 6075 || W.codepoint === 6076 || W.codepoint === 6077)
          W.mask |= v8.blwf;
        else
          W.mask |= v8.pstf;
      if (H === 5)
        W.mask |= v8.abvs;
      if (H === 6)
        W.mask |= v8.abvs;
      U++;
    }
    Q = U;
  }
}
function WQ($) {
  let Q = 0;
  while (Q < $.length) {
    let Z = $[Q];
    if (!Z) {
      Q++;
      continue;
    }
    if (D9(Z.codepoint) !== 1) {
      Q++;
      continue;
    }
    let q = Q, J = Q + 1;
    while (J < $.length) {
      let U = $[J];
      if (!U)
        break;
      if (D9(U.codepoint) === 4 && J + 1 < $.length)
        J += 2;
      else
        break;
    }
    if (J < $.length) {
      let U = $[J];
      if (U) {
        let W = U.codepoint;
        if (W >= 6081 && W <= 6083) {
          let H = U;
          for (let _ = J;_ > q; _--) {
            let N = $[_ - 1];
            if (N)
              $[_] = N;
          }
          $[q] = H;
        }
      }
    }
    Q = J + 1;
  }
}
function B9($) {
  if ($ >= 4096 && $ <= 4255) {
    if ($ >= 4096 && $ <= 4129)
      return 1;
    if ($ >= 4131 && $ <= 4135)
      return 2;
    if ($ >= 4137 && $ <= 4138)
      return 2;
    if ($ >= 4139 && $ <= 4149)
      return 3;
    if ($ === 4150)
      return 6;
    if ($ === 4151)
      return 8;
    if ($ === 4152)
      return 7;
    if ($ === 4153)
      return 5;
    if ($ === 4154)
      return 5;
    if ($ >= 4155 && $ <= 4158)
      return 4;
    if ($ >= 4159 && $ <= 4169) {
      if ($ === 4159)
        return 1;
      return 9;
    }
    if ($ >= 4170 && $ <= 4175)
      return 8;
    if ($ >= 4176 && $ <= 4185)
      return 1;
    if ($ >= 4186 && $ <= 4189)
      return 1;
    if ($ >= 4192 && $ <= 4193)
      return 1;
    if ($ >= 4194 && $ <= 4196)
      return 3;
    if ($ >= 4197 && $ <= 4198)
      return 1;
    if ($ >= 4199 && $ <= 4205)
      return 3;
    if ($ >= 4206 && $ <= 4208)
      return 1;
    if ($ >= 4209 && $ <= 4212)
      return 3;
    if ($ >= 4213 && $ <= 4225)
      return 1;
    if ($ >= 4226 && $ <= 4226)
      return 4;
    if ($ >= 4227 && $ <= 4236)
      return 3;
    if ($ === 4237)
      return 8;
    if ($ === 4238)
      return 1;
    if ($ === 4239)
      return 8;
    if ($ >= 4240 && $ <= 4249)
      return 9;
  }
  if ($ >= 43616 && $ <= 43647) {
    if ($ >= 43616 && $ <= 43638)
      return 1;
    if ($ >= 43639 && $ <= 43641)
      return 8;
    if ($ === 43642)
      return 1;
    if ($ === 43643)
      return 8;
    if ($ === 43644)
      return 8;
    if ($ === 43645)
      return 8;
    if ($ >= 43646 && $ <= 43647)
      return 1;
  }
  if ($ >= 43488 && $ <= 43519) {
    if ($ >= 43488 && $ <= 43492)
      return 1;
    if ($ === 43493)
      return 3;
    if ($ >= 43494 && $ <= 43503)
      return 1;
    if ($ >= 43504 && $ <= 43513)
      return 9;
    if ($ >= 43514 && $ <= 43518)
      return 1;
  }
  return 0;
}
var s6 = { rphf: 1, pref: 2, blwf: 4, pstf: 8, pres: 16, abvs: 32, blws: 64, psts: 128 };
function fW($) {
  return $ >= 4096 && $ <= 4255 || $ >= 43616 && $ <= 43647 || $ >= 43488 && $ <= 43519;
}
function HQ($) {
  let Q = 0;
  while (Q < $.length) {
    let Z = $[Q];
    if (!Z) {
      Q++;
      continue;
    }
    let K = B9(Z.codepoint);
    if (K === 0) {
      Q++;
      continue;
    }
    let q = -1, J = false;
    if (K === 1)
      q = Q;
    let U = Q + 1;
    while (U < $.length) {
      let W = $[U];
      if (!W) {
        U++;
        continue;
      }
      let H = B9(W.codepoint);
      if (H === 0)
        break;
      if (H === 5) {
        if (J = true, W.mask |= s6.blwf, U + 1 < $.length) {
          let _ = $[U + 1];
          if (_ && B9(_.codepoint) === 1) {
            _.mask |= s6.blwf, U += 2;
            continue;
          }
        }
      }
      if (H === 4) {
        let _ = W.codepoint;
        if (_ === 4155)
          W.mask |= s6.pref;
        else if (_ === 4156)
          W.mask |= s6.pref;
        else if (_ === 4157)
          W.mask |= s6.blwf;
        else if (_ === 4158)
          W.mask |= s6.blwf;
      }
      if (H === 3) {
        let _ = W.codepoint;
        if (_ === 4145)
          W.mask |= s6.pref;
        else if (_ === 4141 || _ === 4142 || _ === 4146)
          W.mask |= s6.abvs;
        else if (_ === 4143 || _ === 4144)
          W.mask |= s6.blws;
        else
          W.mask |= s6.psts;
      }
      if (H === 6 || H === 8)
        W.mask |= s6.abvs;
      if (H === 1 && !J) {
        if (U > 0) {
          let _ = $[U - 1];
          if (_) {
            if (B9(_.codepoint) !== 5)
              break;
          }
        }
      }
      J = false, U++;
    }
    Q = U;
  }
}
function _Q($) {
  let Q = 0;
  while (Q < $.length) {
    let Z = $[Q];
    if (!Z) {
      Q++;
      continue;
    }
    if (B9(Z.codepoint) !== 1) {
      Q++;
      continue;
    }
    let q = Q, J = [], U = Q + 1;
    while (U < $.length) {
      let W = $[U];
      if (!W) {
        U++;
        continue;
      }
      let H = B9(W.codepoint);
      if (W.codepoint === 4145) {
        J.push(W), $.splice(U, 1);
        continue;
      }
      if (W.codepoint === 4156) {
        J.push(W), $.splice(U, 1);
        continue;
      }
      if (H === 1 || H === 0)
        break;
      U++;
    }
    if (J.length > 0)
      $.splice(q, 0, ...J), Q += J.length;
    Q++;
  }
}
function VQ($) {
  return $ >= 3584 && $ <= 3711;
}
function XQ($) {
  return $ >= 3712 && $ <= 3839;
}
function NQ($) {
  if (VQ($)) {
    if ($ >= 3585 && $ <= 3630)
      return 1;
    if ($ === 3631)
      return 1;
    if ($ >= 3648 && $ <= 3652)
      return 2;
    if ($ === 3633)
      return 3;
    if ($ >= 3636 && $ <= 3639)
      return 3;
    if ($ === 3655)
      return 3;
    if ($ >= 3640 && $ <= 3642)
      return 4;
    if ($ === 3632)
      return 5;
    if ($ === 3634 || $ === 3635)
      return 5;
    if ($ === 3653)
      return 5;
    if ($ === 3635)
      return 8;
    if ($ >= 3656 && $ <= 3659)
      return 6;
    if ($ === 3660)
      return 6;
    if ($ === 3661)
      return 7;
    if ($ === 3662)
      return 7;
    if ($ >= 3664 && $ <= 3675)
      return 9;
    return 0;
  }
  if (XQ($)) {
    if ($ >= 3713 && $ <= 3758)
      return 1;
    if ($ >= 3776 && $ <= 3780)
      return 2;
    if ($ === 3761)
      return 3;
    if ($ >= 3764 && $ <= 3767)
      return 3;
    if ($ === 3771)
      return 3;
    if ($ >= 3768 && $ <= 3769)
      return 4;
    if ($ === 3772)
      return 4;
    if ($ === 3760)
      return 5;
    if ($ === 3762 || $ === 3763)
      return 5;
    if ($ >= 3784 && $ <= 3789)
      return 6;
    if ($ >= 3792 && $ <= 3801)
      return 9;
    return 0;
  }
  return 0;
}
function F5($) {
  let Q = 0, Z = -1;
  for (let K = 0;K < $.length; K++) {
    let q = $[K];
    if (!q)
      continue;
    let J = NQ(q.codepoint);
    if (J === 1)
      Q++, Z = K;
    if (q.mask = q.mask & 4294967040 | J & 255, q.mask = q.mask & 65535 | (Q & 65535) << 16, J === 2)
      q.mask |= 256;
  }
}
function D5($) {
  let Q = 0;
  while (Q < $.length) {
    let Z = $[Q];
    if (!Z) {
      Q++;
      continue;
    }
    if (NQ(Z.codepoint) === 2) {
      let q = Q + 1;
      while (q < $.length) {
        let J = $[q];
        if (!J) {
          q++;
          continue;
        }
        let U = NQ(J.codepoint ?? 0);
        if (U === 1) {
          let W = Z;
          $[Q] = J, $[q] = W;
          break;
        }
        if (U !== 2)
          break;
        q++;
      }
    }
    Q++;
  }
}
function lW($) {
  return ["bali", "batk", "brah", "bugi", "buhd", "cakm", "cham", "dupl", "egyp", "gran", "hano", "java", "kthi", "khar", "khmr", "khoj", "lana", "lepc", "limb", "mahj", "modi", "mtei", "mymr", "newa", "phlp", "rjng", "saur", "shrd", "sidd", "sind", "sinh", "sund", "sylo", "tagb", "takr", "tale", "talu", "tavt", "tibt", "tirh"].includes($);
}
function Y8($) {
  if ($ === 8204)
    return 21;
  if ($ === 8205)
    return 20;
  if ($ === 847)
    return 2;
  if ($ === 8288)
    return 19;
  if ($ >= 65024 && $ <= 65039)
    return 18;
  if ($ >= 917760 && $ <= 917999)
    return 18;
  if ($ >= 4096 && $ <= 4255) {
    if ($ >= 4096 && $ <= 4128)
      return 1;
    if ($ >= 4129 && $ <= 4138)
      return 10;
    if ($ >= 4139 && $ <= 4146)
      return 31;
    if ($ >= 4150 && $ <= 4151)
      return 32;
    if ($ === 4153)
      return 8;
    if ($ === 4154)
      return 8;
    if ($ >= 4155 && $ <= 4158)
      return 38;
    if ($ >= 4160 && $ <= 4169)
      return 7;
    return 0;
  }
  if ($ >= 6016 && $ <= 6143) {
    if ($ >= 6016 && $ <= 6050)
      return 1;
    if ($ >= 6051 && $ <= 6067)
      return 10;
    if ($ >= 6070 && $ <= 6085)
      return 31;
    if ($ === 6098)
      return 8;
    if ($ >= 6086 && $ <= 6088)
      return 32;
    return 0;
  }
  if ($ >= 3840 && $ <= 4095) {
    if ($ >= 3840 && $ <= 3863)
      return 14;
    if ($ >= 3953 && $ <= 3965)
      return 28;
    if ($ >= 3984 && $ <= 4028)
      return 17;
    if ($ >= 3904 && $ <= 3948)
      return 1;
    return 0;
  }
  if ($ >= 3584 && $ <= 3711) {
    if ($ >= 3585 && $ <= 3630)
      return 1;
    if ($ >= 3632 && $ <= 3642)
      return 31;
    if ($ >= 3648 && $ <= 3652)
      return 30;
    if ($ >= 3656 && $ <= 3659)
      return 32;
    return 0;
  }
  if ($ >= 3712 && $ <= 3839) {
    if ($ >= 3713 && $ <= 3747)
      return 1;
    if ($ >= 3760 && $ <= 3772)
      return 31;
    if ($ >= 3776 && $ <= 3780)
      return 30;
    if ($ >= 3784 && $ <= 3789)
      return 32;
    return 0;
  }
  if ($ >= 57344 && $ <= 57599) {
    if ($ === 57344)
      return 13;
    if ($ === 57345)
      return 24;
    if ($ === 57346)
      return 25;
    if ($ === 57347)
      return 26;
    if ($ === 57348)
      return 27;
    if ($ === 57349)
      return 4;
    if ($ === 57350)
      return 12;
    if ($ === 57351)
      return 9;
    if ($ === 57352)
      return 23;
    if ($ === 57353)
      return 29;
    if ($ === 57354)
      return 37;
    if ($ === 57355)
      return 39;
    if ($ === 57356)
      return 40;
    if ($ === 57357)
      return 33;
    if ($ === 57358)
      return 34;
    if ($ === 57359)
      return 35;
    if ($ === 57360)
      return 36;
    if ($ === 57361)
      return 5;
    if ($ === 57362)
      return 6;
  }
  return 0;
}
var X6 = { rphf: 1, pref: 2, blwf: 4, abvf: 8, pstf: 16, half: 32, cjct: 64, vatu: 128, pres: 256, abvs: 512, blws: 1024, psts: 2048, haln: 4096 };
function dW($) {
  let Q = [], Z = $.length;
  if (Z === 0)
    return Q;
  let K = 0;
  while (K < Z) {
    let q = Sz($, K);
    Q.push(q), K = q.end;
  }
  return Q;
}
function Sz($, Q) {
  let Z = $.length, K = Q, q = -1, J = false;
  if (K + 1 < Z) {
    let U = $[K], W = $[K + 1];
    if (U && W) {
      let H = Y8(U.codepoint ?? 0), _ = Y8(W.codepoint ?? 0);
      if (H === 13 && _ === 8)
        J = true, K += 2;
    }
  }
  while (K < Z) {
    let U = $[K];
    if (!U) {
      K++;
      continue;
    }
    let W = Y8(U.codepoint ?? 0);
    if (W === 1 || W === 10 || W === 7 || W === 22) {
      q = K, K++;
      break;
    }
    if (W === 26 || W === 30 || W === 39) {
      K++;
      continue;
    }
    if (q === -1)
      K++;
    break;
  }
  if (q === -1)
    q = Q;
  while (K < Z) {
    let U = $[K];
    if (!U) {
      K++;
      continue;
    }
    let W = Y8(U.codepoint ?? 0);
    if (W === 8) {
      if (K++, K < Z) {
        let H = $[K];
        if (H) {
          let _ = Y8(H.codepoint ?? 0);
          if (_ === 1 || _ === 4 || _ === 17) {
            K++;
            continue;
          }
          if (_ === 20 || _ === 21)
            K++;
        }
      }
      continue;
    }
    if (W === 17 || W === 4) {
      K++;
      continue;
    }
    if (W === 12 || W === 9) {
      K++;
      continue;
    }
    break;
  }
  while (K < Z) {
    let U = $[K];
    if (!U) {
      K++;
      continue;
    }
    let W = Y8(U.codepoint ?? 0);
    if (W === 28 || W === 29 || W === 30 || W === 31 || W === 23) {
      K++;
      continue;
    }
    if (W === 37 || W === 38 || W === 39 || W === 40) {
      K++;
      continue;
    }
    if (W === 24 || W === 25 || W === 26 || W === 27) {
      K++;
      continue;
    }
    if (W === 32 || W === 33) {
      K++;
      continue;
    }
    if (W === 34 || W === 35 || W === 36 || W === 5 || W === 6) {
      K++;
      continue;
    }
    if (W === 2 || W === 18) {
      K++;
      continue;
    }
    break;
  }
  if (K === Q)
    K = Q + 1;
  return { start: Q, end: K, base: q, hasReph: J };
}
function nW($) {
  let Q = dW($);
  for (let Z = 0;Z < Q.length; Z++) {
    let K = Q[Z];
    for (let q = K.start;q < K.end; q++) {
      let J = $[q];
      if (!J)
        continue;
      J.mask = J.mask & 65535 | (Z & 65535) << 16;
      let U = Y8(J.codepoint);
      if (K.hasReph && q < K.start + 2)
        J.mask |= X6.rphf;
      if (q < K.base) {
        if (U === 1 || U === 4 || U === 17)
          J.mask |= X6.half | X6.cjct;
      }
      if (q > K.base) {
        if (U === 1 || U === 4 || U === 17)
          J.mask |= X6.blwf | X6.pstf | X6.vatu;
      }
      if (U === 8 || U === 9)
        if (q < K.base)
          J.mask |= X6.half;
        else
          J.mask |= X6.haln;
      if (U === 30)
        J.mask |= X6.pref | X6.pres;
      else if (U === 28)
        J.mask |= X6.abvf | X6.abvs;
      else if (U === 29)
        J.mask |= X6.blwf | X6.blws;
      else if (U === 31 || U === 23)
        J.mask |= X6.pstf | X6.psts;
      if (U === 37)
        J.mask |= X6.abvs;
      else if (U === 38)
        J.mask |= X6.blws;
      else if (U === 39)
        J.mask |= X6.pres;
      else if (U === 40)
        J.mask |= X6.psts;
      if (U === 32)
        J.mask |= X6.abvs;
      else if (U === 33)
        J.mask |= X6.blws;
      if (U === 34)
        J.mask |= X6.abvs;
      else if (U === 35)
        J.mask |= X6.blws;
      else if (U === 36 || U === 5 || U === 6)
        J.mask |= X6.psts;
    }
  }
}
function pW($) {
  let Q = dW($);
  for (let Z = 0;Z < Q.length; Z++) {
    let K = Q[Z];
    hz($, K);
  }
}
function hz($, Q) {
  let { start: Z, end: K, base: q, hasReph: J } = Q, U = [];
  for (let W = q + 1;W < K; W++) {
    let H = $[W];
    if (!H)
      continue;
    let _ = Y8(H.codepoint);
    if (_ === 30 || _ === 39)
      U.push({ index: W, info: H });
  }
  if (U.length > 0) {
    U.sort((W, H) => H.index - W.index);
    for (let W = 0;W < U.length; W++) {
      let { index: H, info: _ } = U[W];
      $.splice(H, 1);
      let N = J ? Z + 2 : Z;
      $.splice(N, 0, _);
    }
  }
  if (J && K > Z + 2) {
    let W = $[Z], H = $[Z + 1];
    if (W && H) {
      let _ = K - 1;
      while (_ > q) {
        let N = $[_];
        if (!N)
          break;
        let V = Y8(N.codepoint);
        if (V === 32 || V === 33 || V === 34 || V === 35 || V === 36 || V === 5 || V === 6)
          _--;
        else
          break;
      }
      if (_ > Z + 1) {
        $.splice(Z, 2);
        let N = _ - 2;
        $.splice(N + 1, 0, W, H);
      }
    }
  }
}
var zQ = new Uint16Array(16);
var GQ = new Uint16Array(16);
function yz($) {
  return $ instanceof n8 ? $.font : $;
}
var cW = new WeakMap;
function bz($) {
  if ($ instanceof n8)
    return $;
  let Q = cW.get($);
  if (!Q)
    Q = new n8($), cW.set($, Q);
  return Q;
}
function j8($, Q, Z) {
  let K = new Uint8Array(Q.infos.length), q = $.gdef;
  if (!q || Z === 0)
    return K;
  let J = Z & b6.IgnoreBaseGlyphs, U = Z & b6.IgnoreLigatures, W = Z & b6.IgnoreMarks, H = W5(Z);
  for (let _ = 0;_ < Q.infos.length; _++) {
    let N = Q.infos[_];
    if (!N)
      continue;
    let V = z8(q, N.glyphId);
    if (J && V === 1)
      K[_] = 1;
    else if (U && V === 2)
      K[_] = 1;
    else if (W && V === 3)
      K[_] = 1;
    else if (H !== 0 && V === 3) {
      if (q.markAttachClassDef.get(N.glyphId) !== H)
        K[_] = 1;
    }
  }
  return K;
}
function oW($, Q) {
  let Z = new Int16Array(Q), K = -1;
  for (let q = Q - 1;q >= 0; q--)
    if (Z[q] = K, !$[q])
      K = q;
  return Z;
}
function mz($, Q) {
  if (!Q.gdef)
    return false;
  let { infos: Z, length: K } = $;
  for (let q = 0;q < K; q++) {
    let J = Z[q];
    if (!J)
      continue;
    if (z8(Q.gdef, J.glyphId) === 3)
      return true;
  }
  return false;
}
function gz($, Q, Z) {
  let K = new Int16Array($.infos.length);
  K.fill(-1);
  let q = -1;
  for (let J = 0;J < $.infos.length; J++) {
    let U = $.infos[J];
    if (!U)
      continue;
    let W = o8(Z, U.glyphId, Q);
    if (W === 1 || W === 0 || W === 2)
      q = J;
    else if (W === 3)
      K[J] = q;
  }
  return K;
}
var uz = [];
function xz($, Q, Z = {}) {
  let K = uz.pop();
  if (!K)
    K = C8.withCapacity(64);
  return iW($, Q, K, Z), K;
}
function iW($, Q, Z, K = {}) {
  let q = yz($), J = bz($), U = K.script ?? Q.script ?? "latn", W = K.language ?? Q.language ?? null, H = K.direction ?? "ltr", _ = K.features ?? [], N = true;
  if (_.length) {
    let G = C("kern");
    for (let Y = 0;Y < _.length; Y++) {
      let j = _[Y];
      if (j.tag === G) {
        N = j.enabled;
        break;
      }
    }
  }
  let V = J.normalizedCoords.length > 0 ? J.normalizedCoords : null, X = L5(q, U, W, H, _, V);
  if (Z.reset(), Z.direction = Q.direction, Z.script = U, Z.language = W, Z.initFromCodepointsWithFont(Q.codepoints, Q.clusters, q), fz(Z, U), dz(q, Z, X), qG(J, Z), q.gpos !== null && X.gposLookups.length > 0)
    JG(q, Z, X);
  else {
    if (N)
      i1(q, Z.infos, Z.positions);
    o1(q, Z.infos, Z.positions);
  }
  if (!q.gsub && q.morx)
    DG(q, Z);
  if (H === "rtl")
    Z.reverse();
}
function fz($, Q) {
  if (Q === "arab" || Q === "syrc" || Q === "mand" || Q === "nko ") {
    t1($.infos);
    return;
  }
  if (Q === "hebr") {
    KQ($.infos);
    return;
  }
  if (Q === "hang" || Q === "kore") {
    let Z = ZQ($.infos);
    if (Z.length !== $.infos.length)
      $.initFromInfos(Z);
    QQ($.infos);
    return;
  }
  if (Q === "deva" || Q === "beng" || Q === "guru" || Q === "gujr" || Q === "orya" || Q === "taml" || Q === "telu" || Q === "knda" || Q === "mlym") {
    qQ($.infos), JQ($.infos);
    return;
  }
  if (Q === "thai" || Q === "lao ") {
    F5($.infos), D5($.infos);
    return;
  }
  if (Q === "khmr") {
    UQ($.infos), WQ($.infos);
    return;
  }
  if (Q === "mymr") {
    HQ($.infos), _Q($.infos);
    return;
  }
  if (lW(Q)) {
    nW($.infos), pW($.infos);
    return;
  }
  if (Q === "Zyyy" || Q === "Zinh" || Q === "Zzzz")
    lz($.infos);
}
function lz($) {
  if ($.length === 0)
    return;
  let Q = Math.min(10, $.length);
  for (let Z = 0;Z < Q; Z++) {
    let q = $[Z].codepoint;
    if (q >= 1536 && q <= 1791 || q >= 1872 && q <= 1919 || q >= 2208 && q <= 2303) {
      t1($);
      return;
    }
    if (q >= 1424 && q <= 1535) {
      KQ($);
      return;
    }
    if (OW(q)) {
      let J = ZQ($);
      if (J.length !== $.length)
        $.length = 0, $.push(...J);
      QQ($);
      return;
    }
    if (mW(q)) {
      qQ($), JQ($);
      return;
    }
    if (VQ(q)) {
      F5($), D5($);
      return;
    }
    if (XQ(q)) {
      F5($), D5($);
      return;
    }
    if (xW(q)) {
      UQ($), WQ($);
      return;
    }
    if (fW(q)) {
      HQ($), _Q($);
      return;
    }
  }
}
function dz($, Q, Z) {
  let K = Z.gsubLookups, q = new V6, J = Q.infos;
  for (let U = 0;U < Q.length; U++)
    q.add(J[U].glyphId);
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (!q.mayIntersect(W.lookup.digest))
      continue;
    let H = Q.length;
    if (nz($, Q, W.lookup, Z), Q.length !== H) {
      q = new V6;
      for (let _ = 0;_ < Q.length; _++)
        q.add(J[_].glyphId);
    }
  }
  Q.compact();
}
function nz($, Q, Z, K) {
  switch (Z.type) {
    case 1:
      pz($, Q, Z);
      break;
    case 2:
      cz($, Q, Z);
      break;
    case 3:
      oz($, Q, Z);
      break;
    case 4:
      iz($, Q, Z);
      break;
    case 5:
      sz($, Q, Z, K);
      break;
    case 6:
      rz($, Q, Z, K);
      break;
    case 8:
      az($, Q, Z);
      break;
  }
}
function pz($, Q, Z) {
  let K = Q.infos, q = K.length, J = Z.digest;
  if (Z.flag === 0 || !$.gdef) {
    if (Z.subtables.length === 1) {
      let W = Z.subtables[0];
      if (W.format === 1 && W.deltaGlyphId !== undefined) {
        let H = W.deltaGlyphId;
        for (let _ = 0;_ < q; _++) {
          let N = K[_];
          if (!J.mayHave(N.glyphId))
            continue;
          if (W.coverage.get(N.glyphId) !== null)
            N.glyphId = N.glyphId + H & 65535;
        }
      } else if (W.format === 2 && W.substituteGlyphIds) {
        let H = W.substituteGlyphIds;
        for (let _ = 0;_ < q; _++) {
          let N = K[_];
          if (!J.mayHave(N.glyphId))
            continue;
          let V = W.coverage.get(N.glyphId);
          if (V !== null) {
            let X = H[V];
            if (X !== undefined)
              N.glyphId = X;
          }
        }
      }
      return;
    }
    for (let W = 0;W < q; W++) {
      let H = K[W];
      if (!J.mayHave(H.glyphId))
        continue;
      let _ = H0(Z, H.glyphId);
      if (_ !== null)
        H.glyphId = _;
    }
    return;
  }
  let U = j8($, Q, Z.flag);
  for (let W = 0;W < q; W++) {
    if (U[W])
      continue;
    let H = K[W];
    if (!J.mayHave(H.glyphId))
      continue;
    let _ = H0(Z, H.glyphId);
    if (_ !== null)
      H.glyphId = _;
  }
}
function cz($, Q, Z) {
  let K = Z.digest, q = 0;
  while (q < Q.infos.length) {
    let J = Q.infos[q];
    if (!J) {
      q++;
      continue;
    }
    if (Y6($, J.glyphId, Z.flag)) {
      q++;
      continue;
    }
    if (!K.mayHave(J.glyphId)) {
      q++;
      continue;
    }
    let U = false, W = Z.subtables;
    for (let H = 0;H < W.length; H++) {
      let _ = W[H], N = _.coverage.get(J.glyphId);
      if (N === null)
        continue;
      let V = _.sequences[N];
      if (!V || V.length === 0)
        continue;
      let X = V[0];
      if (X === undefined)
        continue;
      J.glyphId = X;
      for (let z = 1;z < V.length; z++) {
        let Y = { glyphId: V[z], cluster: J.cluster, mask: J.mask, codepoint: J.codepoint }, j = { xAdvance: 0, yAdvance: 0, xOffset: 0, yOffset: 0 };
        Q.insertGlyph(q + z, Y, j);
      }
      q += V.length, U = true;
      break;
    }
    if (!U)
      q++;
  }
}
function oz($, Q, Z) {
  let K = Q.infos, q = Z.subtables, J = Z.digest;
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (Y6($, W.glyphId, Z.flag))
      continue;
    if (!J.mayHave(W.glyphId))
      continue;
    for (let H = 0;H < q.length; H++) {
      let _ = q[H], N = _.coverage.get(W.glyphId);
      if (N === null)
        continue;
      let V = _.alternateSets[N];
      if (!V || V.length === 0)
        continue;
      let X = V[0];
      if (X === undefined)
        continue;
      W.glyphId = X;
      break;
    }
  }
}
function iz($, Q, Z) {
  let K = Q.infos, q = K.length, J = Z.flag !== 0 && $.gdef !== null, U = Z.digest, W = null;
  if (J)
    W = j8($, Q, Z.flag);
  let H = 0;
  while (H < q) {
    if (Q.isDeleted(H)) {
      H++;
      continue;
    }
    let _ = K[H];
    if (!_) {
      H++;
      continue;
    }
    if (W?.[H]) {
      H++;
      continue;
    }
    if (!U.mayHave(_.glyphId)) {
      H++;
      continue;
    }
    let N = 1;
    zQ[0] = H, GQ[0] = _.glyphId;
    for (let X = H + 1;X < q && N < 16; X++) {
      if (Q.isDeleted(X))
        continue;
      let z = K[X];
      if (!z)
        continue;
      if (W?.[X])
        continue;
      zQ[N] = X, GQ[N] = z.glyphId, N++;
    }
    let V = zU(Z, GQ, N, 0);
    if (V) {
      _.glyphId = V.ligatureGlyph;
      for (let X = 1;X < V.consumed; X++) {
        let z = zQ[X];
        if (z !== undefined) {
          let G = K[z];
          if (G)
            _.cluster = Math.min(_.cluster, G.cluster);
          Q.markDeleted(z);
        }
      }
    }
    H++;
  }
}
function sz($, Q, Z, K) {
  let q = Q.infos, J = q.length, U = Z.digest, W = null;
  if (Z.flag !== 0 && $.gdef !== null)
    W = j8($, Q, Z.flag);
  let H = Z.subtables;
  for (let _ = 0;_ < J; _++) {
    let N = q[_];
    if (!N)
      continue;
    if (W?.[_])
      continue;
    if (!U.mayHave(N.glyphId))
      continue;
    for (let V = 0;V < H.length; V++) {
      let X = H[V], z = false, G = [];
      if (X.format === 1) {
        let Y = tz($, Q, _, X, Z.flag);
        if (Y)
          z = true, G = Y;
      } else if (X.format === 2) {
        let Y = ez($, Q, _, X, Z.flag);
        if (Y)
          z = true, G = Y;
      } else if (X.format === 3) {
        if ($G($, Q, _, X, Z.flag))
          z = true, G = X.lookupRecords;
      }
      if (z) {
        sW($, Q, _, G, K);
        break;
      }
    }
  }
}
function rz($, Q, Z, K) {
  let q = Q.infos, J = q.length, U = Z.digest, W = null;
  if (Z.flag !== 0 && $.gdef !== null)
    W = j8($, Q, Z.flag);
  let H = Z.subtables;
  for (let _ = 0;_ < J; _++) {
    let N = q[_];
    if (!N)
      continue;
    if (W?.[_])
      continue;
    if (!U.mayHave(N.glyphId))
      continue;
    for (let V = 0;V < H.length; V++) {
      let X = H[V], z = false, G = [];
      if (X.format === 1) {
        let Y = QG($, Q, _, X, Z.flag);
        if (Y)
          z = true, G = Y;
      } else if (X.format === 2) {
        let Y = ZG($, Q, _, X, Z.flag);
        if (Y)
          z = true, G = Y;
      } else if (X.format === 3) {
        if (KG($, Q, _, X, Z.flag))
          z = true, G = X.lookupRecords;
      }
      if (z) {
        sW($, Q, _, G, K);
        break;
      }
    }
  }
}
function az($, Q, Z) {
  let K = Q.infos, q = Z.subtables, J = Z.digest;
  for (let U = K.length - 1;U >= 0; U--) {
    let W = K[U];
    if (!W)
      continue;
    if (Y6($, W.glyphId, Z.flag))
      continue;
    if (!J.mayHave(W.glyphId))
      continue;
    for (let H = 0;H < q.length; H++) {
      let _ = q[H], N = _.coverage.get(W.glyphId);
      if (N === null)
        continue;
      let V = true, X = U + 1, z = _.backtrackCoverages;
      for (let w = 0;w < z.length; w++) {
        let L = z[w];
        while (X < K.length && Y6($, K[X]?.glyphId, Z.flag))
          X++;
        if (X >= K.length || L.get(K[X]?.glyphId) === null) {
          V = false;
          break;
        }
        X++;
      }
      if (!V)
        continue;
      let G = true, Y = U - 1, j = _.lookaheadCoverages;
      for (let w = 0;w < j.length; w++) {
        let L = j[w];
        while (Y >= 0 && Y6($, K[Y]?.glyphId, Z.flag))
          Y--;
        if (Y < 0 || L.get(K[Y]?.glyphId) === null) {
          G = false;
          break;
        }
        Y--;
      }
      if (!G)
        continue;
      let E = _.substituteGlyphIds[N];
      if (E !== undefined)
        W.glyphId = E;
      break;
    }
  }
}
function tz($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId, U = K.coverage.get(J);
  if (U === null)
    return null;
  let W = K.ruleSets[U];
  if (!W)
    return null;
  for (let H = 0;H < W.length; H++) {
    let _ = W[H];
    if (R9($, Q, Z + 1, _.inputSequence, q))
      return _.lookupRecords;
  }
  return null;
}
function ez($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId;
  if (K.coverage.get(J) === null)
    return null;
  let W = K.classDef.get(J), H = K.classRuleSets[W];
  if (!H)
    return null;
  for (let _ = 0;_ < H.length; _++) {
    let N = H[_];
    if (M9($, Q, Z + 1, N.inputClasses, K.classDef, q))
      return N.lookupRecords;
  }
  return null;
}
function $G($, Q, Z, K, q) {
  let J = Q.infos, U = K.coverages, W = Z;
  for (let H = 0;H < U.length; H++) {
    let _ = U[H];
    while (W < J.length && Y6($, J[W]?.glyphId, q))
      W++;
    if (W >= J.length)
      return false;
    if (_.get(J[W]?.glyphId) === null)
      return false;
    W++;
  }
  return true;
}
function QG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId, U = K.coverage.get(J);
  if (U === null)
    return null;
  let W = K.chainRuleSets[U];
  if (!W)
    return null;
  for (let H = 0;H < W.length; H++) {
    let _ = W[H];
    if (!aW($, Q, Z - 1, _.backtrackSequence, q))
      continue;
    if (!R9($, Q, Z + 1, _.inputSequence, q))
      continue;
    let N = Z + 1;
    for (let V = 0;V < _.inputSequence.length; V++) {
      while (N < Q.infos.length && Y6($, Q.infos[N]?.glyphId, q))
        N++;
      N++;
    }
    if (!R9($, Q, N, _.lookaheadSequence, q))
      continue;
    return _.lookupRecords;
  }
  return null;
}
function ZG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId;
  if (K.coverage.get(J) === null)
    return null;
  let W = K.inputClassDef.get(J), H = K.chainClassRuleSets[W];
  if (!H)
    return null;
  for (let _ = 0;_ < H.length; _++) {
    let N = H[_];
    if (!tW($, Q, Z - 1, N.backtrackClasses, K.backtrackClassDef, q))
      continue;
    if (!M9($, Q, Z + 1, N.inputClasses, K.inputClassDef, q))
      continue;
    let V = Q.infos, X = Z + 1;
    for (let z = 0;z < N.inputClasses.length; z++) {
      while (X < V.length && Y6($, V[X]?.glyphId, q))
        X++;
      X++;
    }
    if (!M9($, Q, X, N.lookaheadClasses, K.lookaheadClassDef, q))
      continue;
    return N.lookupRecords;
  }
  return null;
}
function KG($, Q, Z, K, q) {
  let J = Q.infos, U = Z - 1, W = K.backtrackCoverages;
  for (let X = 0;X < W.length; X++) {
    let z = W[X];
    while (U >= 0 && Y6($, J[U]?.glyphId, q))
      U--;
    if (U < 0)
      return false;
    if (z.get(J[U]?.glyphId) === null)
      return false;
    U--;
  }
  let H = Z, _ = K.inputCoverages;
  for (let X = 0;X < _.length; X++) {
    let z = _[X];
    while (H < J.length && Y6($, J[H]?.glyphId, q))
      H++;
    if (H >= J.length)
      return false;
    if (z.get(J[H]?.glyphId) === null)
      return false;
    H++;
  }
  let N = H, V = K.lookaheadCoverages;
  for (let X = 0;X < V.length; X++) {
    let z = V[X];
    while (N < J.length && Y6($, J[N]?.glyphId, q))
      N++;
    if (N >= J.length)
      return false;
    if (z.get(J[N]?.glyphId) === null)
      return false;
    N++;
  }
  return true;
}
function sW($, Q, Z, K, q) {
  let J = K.length;
  if (J === 0)
    return;
  if (J === 1) {
    let W = K[0], H = q.gsubLookupMap.get(W.lookupListIndex);
    if (!H)
      return;
    let _ = Z + W.sequenceIndex;
    if (_ >= Q.infos.length)
      return;
    let N = Q.infos[_];
    if (!N)
      return;
    if (H.lookup.type === 1) {
      let V = H0(H.lookup, N.glyphId);
      if (V !== null)
        N.glyphId = V;
    }
    return;
  }
  let U = new Uint8Array(J);
  for (let W = 0;W < J; W++) {
    let H = -1, _ = -1;
    for (let G = 0;G < J; G++) {
      if (U[G])
        continue;
      let Y = K[G]?.sequenceIndex;
      if (Y > _)
        _ = Y, H = G;
    }
    if (H < 0)
      break;
    U[H] = 1;
    let N = K[H], V = q.gsubLookupMap.get(N.lookupListIndex);
    if (!V)
      continue;
    let X = Z + N.sequenceIndex;
    if (X >= Q.infos.length)
      continue;
    let z = Q.infos[X];
    if (!z)
      continue;
    if (V.lookup.type === 1) {
      let G = H0(V.lookup, z.glyphId);
      if (G !== null)
        z.glyphId = G;
    }
  }
}
function qG($, Q) {
  let { infos: Z, positions: K } = Q, q = Z.length, U = $.font.hmtx.hMetrics, W = U.length, H = U[W - 1]?.advanceWidth ?? 0;
  if (!($.normalizedCoords.length > 0)) {
    for (let N = 0;N < q; N++) {
      let V = Z[N].glyphId;
      K[N].xAdvance = V < W ? U[V]?.advanceWidth ?? 0 : H;
    }
    return;
  }
  for (let N = 0;N < q; N++) {
    let V = Z[N];
    K[N].xAdvance = $.advanceWidth(V.glyphId);
  }
}
function o8($, Q, Z) {
  let K = Z.get(Q);
  if (K === undefined)
    K = z8($.gdef, Q), Z.set(Q, K);
  return K;
}
function JG($, Q, Z) {
  let K = new V6, q = Q.infos, J = Q.length;
  for (let N = 0;N < J; N++)
    K.add(q[N].glyphId);
  let U = mz(Q, $), W, H;
  if (U)
    H = new Map, W = gz(Q, H, $);
  else
    W = UG, H = WG;
  let _ = Z.gposLookups;
  for (let N = 0;N < _.length; N++) {
    let V = _[N];
    if (!K.mayIntersect(V.lookup.digest))
      continue;
    YQ($, Q, V.lookup, Z, H, W, U);
  }
}
var UG = new Int16Array(0);
var WG = new Map;
function YQ($, Q, Z, K, q, J, U) {
  switch (Z.type) {
    case 1:
      HG($, Q, Z, U);
      break;
    case 2:
      _G($, Q, Z, U);
      break;
    case 3:
      NG($, Q, Z, U);
      break;
    case 4:
      if (!U)
        break;
      VG($, Q, Z, q, J);
      break;
    case 5:
      if (!U)
        break;
      XG($, Q, Z, q, J);
      break;
    case 6:
      if (!U)
        break;
      zG($, Q, Z, q);
      break;
    case 7:
      GG($, Q, Z, K, q, J, U);
      break;
    case 8:
      YG($, Q, Z, K, q, J, U);
      break;
  }
}
function HG($, Q, Z, K) {
  let { infos: q, positions: J } = Q, U = q.length, W = Z.digest, H = Z.subtables, _ = K && Z.flag !== 0 && $.gdef !== null;
  if (H.length === 1 && !_) {
    let X = H[0];
    if (X.format === 1 && X.value) {
      let z = X.value, G = z.xPlacement !== undefined && z.xPlacement !== 0, Y = z.yPlacement !== undefined && z.yPlacement !== 0, j = z.xAdvance !== undefined && z.xAdvance !== 0, E = z.yAdvance !== undefined && z.yAdvance !== 0;
      if (!G && !Y && !j && !E)
        return;
      for (let w = 0;w < U; w++) {
        let L = q[w];
        if (!W.mayHave(L.glyphId))
          continue;
        if (X.coverage.get(L.glyphId) === null)
          continue;
        let A = J[w];
        if (G)
          A.xOffset += z.xPlacement;
        if (Y)
          A.yOffset += z.yPlacement;
        if (j)
          A.xAdvance += z.xAdvance;
        if (E)
          A.yAdvance += z.yAdvance;
      }
      return;
    }
  }
  let N = (X) => {
    let z = q[X];
    if (!W.mayHave(z.glyphId))
      return;
    let G = J[X];
    if (!G)
      return;
    for (let Y = 0;Y < H.length; Y++) {
      let j = H[Y], E = j.coverage.get(z.glyphId);
      if (E === null)
        continue;
      let w = j.format === 1 ? j.value : j.values?.[E];
      if (w) {
        if (w.xPlacement)
          G.xOffset += w.xPlacement;
        if (w.yPlacement)
          G.yOffset += w.yPlacement;
        if (w.xAdvance)
          G.xAdvance += w.xAdvance;
        if (w.yAdvance)
          G.yAdvance += w.yAdvance;
      }
      break;
    }
  };
  if (!_) {
    for (let X = 0;X < U; X++)
      N(X);
    return;
  }
  let V = j8($, Q, Z.flag);
  for (let X = 0;X < U; X++) {
    if (V[X])
      continue;
    N(X);
  }
}
function _G($, Q, Z, K) {
  let { infos: q, positions: J } = Q, U = q.length, W = Z.digest, H = Z.subtables, _ = H.length;
  if (!(K && Z.flag !== 0 && $.gdef !== null)) {
    if (_ === 1) {
      let z = H[0], G = z.coverage;
      if (z.format === 1) {
        let Y = z.pairSets;
        for (let j = 0;j < U - 1; j++) {
          let E = q[j].glyphId;
          if (!W.mayHave(E))
            continue;
          let w = G.get(E);
          if (w === null)
            continue;
          let L = Y[w];
          if (!L)
            continue;
          let A = q[j + 1].glyphId, D = L.pairValueRecords, F = 0, M = D.length - 1;
          while (F <= M) {
            let R = F + M >>> 1, P = D[R], T = P.secondGlyph;
            if (T < A)
              F = R + 1;
            else if (T > A)
              M = R - 1;
            else {
              let m = P.value1.xAdvance;
              if (m)
                J[j].xAdvance += m;
              break;
            }
          }
        }
      } else {
        let { classDef1: Y, classDef2: j, class1Records: E } = z, w = E.length;
        for (let L = 0;L < U - 1; L++) {
          let A = q[L].glyphId;
          if (!W.mayHave(A))
            continue;
          if (G.get(A) === null)
            continue;
          let D = Y.get(A);
          if (D >= w)
            continue;
          let F = E[D], M = q[L + 1].glyphId, R = j.get(M), P = F.class2Records;
          if (R >= P.length)
            continue;
          let T = P[R].value1.xAdvance;
          if (T)
            J[L].xAdvance += T;
        }
      }
      return;
    }
    if (_ === 2) {
      let z = H[0], G = H[1];
      if (z.format === 1 && G.format === 2) {
        let { coverage: Y, pairSets: j } = z, E = G.coverage, w = G.classDef1, L = G.classDef2, A = G.class1Records, D = A.length;
        for (let F = 0;F < U - 1; F++) {
          let M = q[F].glyphId;
          if (!W.mayHave(M))
            continue;
          let R = q[F + 1].glyphId, P = Y.get(M);
          if (P !== null) {
            let T = j[P];
            if (T) {
              let m = T.pairValueRecords, v = 0, f = m.length - 1, l = false;
              while (v <= f) {
                let k = v + f >>> 1, O = m[k], h = O.secondGlyph;
                if (h < R)
                  v = k + 1;
                else if (h > R)
                  f = k - 1;
                else {
                  let S = O.value1.xAdvance;
                  if (S)
                    J[F].xAdvance += S;
                  l = true;
                  break;
                }
              }
              if (l)
                continue;
            }
          }
          if (E.get(M) !== null) {
            let T = w.get(M);
            if (T < D) {
              let m = A[T], v = L.get(R), f = m.class2Records;
              if (v < f.length) {
                let l = f[v].value1.xAdvance;
                if (l)
                  J[F].xAdvance += l;
              }
            }
          }
        }
        return;
      }
    }
    for (let z = 0;z < U - 1; z++) {
      let G = q[z];
      if (!W.mayHave(G.glyphId))
        continue;
      let Y = q[z + 1], j = J[z], E = J[z + 1];
      R1(Z, G.glyphId, Y.glyphId, j, E);
    }
    return;
  }
  let V = j8($, Q, Z.flag), X = oW(V, U);
  for (let z = 0;z < U - 1; z++) {
    if (V[z])
      continue;
    let G = X[z];
    if (G < 0)
      break;
    let Y = q[z];
    if (!W.mayHave(Y.glyphId))
      continue;
    let j = q[G], E = J[z], w = J[G];
    R1(Z, Y.glyphId, j.glyphId, E, w);
  }
}
function NG($, Q, Z, K) {
  let { infos: q, positions: J } = Q, U = q.length, W = Z.digest, H = Z.subtables, _ = (z, G) => {
    let Y = q[z], j = q[G];
    if (!W.mayHave(Y.glyphId) && !W.mayHave(j.glyphId))
      return;
    for (let E = 0;E < H.length; E++) {
      let w = H[E], L = w.coverage.get(Y.glyphId), A = w.coverage.get(j.glyphId);
      if (L === null || A === null)
        continue;
      let D = w.entryExitRecords[L], F = w.entryExitRecords[A];
      if (!D?.exitAnchor || !F?.entryAnchor)
        continue;
      let M = D.exitAnchor, R = F.entryAnchor, P = J[G];
      if (P)
        P.yOffset = M.yCoordinate - R.yCoordinate;
      break;
    }
  };
  if (!(K && Z.flag !== 0 && $.gdef !== null)) {
    for (let z = 0;z < U - 1; z++)
      _(z, z + 1);
    return;
  }
  let V = j8($, Q, Z.flag), X = oW(V, U);
  for (let z = 0;z < U - 1; z++) {
    if (V[z])
      continue;
    let G = X[z];
    if (G < 0)
      break;
    _(z, G);
  }
}
function VG($, Q, Z, K, q) {
  let J = Z.digest, U = Q.infos, W = Q.positions, H = Z.subtables;
  for (let _ = 0;_ < U.length; _++) {
    let N = U[_];
    if (!N)
      continue;
    if (!J.mayHave(N.glyphId))
      continue;
    if (o8($, N.glyphId, K) !== 3)
      continue;
    let V = q[_];
    if (V < 0)
      continue;
    let X = U[V];
    if (!X)
      continue;
    for (let z = 0;z < H.length; z++) {
      let G = H[z], Y = G.markCoverage.get(N.glyphId), j = G.baseCoverage.get(X.glyphId);
      if (Y === null || j === null)
        continue;
      let E = G.markArray.markRecords[Y], w = G.baseArray[j];
      if (!E || !w)
        continue;
      let L = w.baseAnchors[E.markClass];
      if (!L)
        continue;
      let A = E.markAnchor, D = W[_], F = W[V];
      if (!D || !F)
        continue;
      D.xOffset = L.xCoordinate - A.xCoordinate + F.xOffset, D.yOffset = L.yCoordinate - A.yCoordinate + F.yOffset, D.xAdvance = 0, D.yAdvance = 0;
      break;
    }
  }
}
function XG($, Q, Z, K, q) {
  let J = Z.digest, U = Q.infos, W = Q.positions, H = Z.subtables;
  for (let _ = 0;_ < U.length; _++) {
    let N = U[_];
    if (!N)
      continue;
    if (!J.mayHave(N.glyphId))
      continue;
    if (o8($, N.glyphId, K) !== 3)
      continue;
    let V = q[_];
    if (V < 0)
      continue;
    let X = U[V];
    if (!X)
      continue;
    if (o8($, X.glyphId, K) !== 2)
      continue;
    let z = 0;
    for (let G = V + 1;G < _; G++) {
      let Y = U[G];
      if (Y && o8($, Y.glyphId, K) === 3)
        z++;
    }
    for (let G = 0;G < H.length; G++) {
      let Y = H[G], j = Y.markCoverage.get(N.glyphId), E = Y.ligatureCoverage.get(X.glyphId);
      if (j === null || E === null)
        continue;
      let w = Y.markArray.markRecords[j], L = Y.ligatureArray[E];
      if (!w || !L)
        continue;
      let A = Math.min(z, L.componentRecords.length - 1), D = L.componentRecords[A];
      if (!D)
        continue;
      let F = D.ligatureAnchors[w.markClass];
      if (!F)
        continue;
      let M = w.markAnchor, R = W[_], P = W[V];
      if (!R || !P)
        continue;
      R.xOffset = F.xCoordinate - M.xCoordinate + P.xOffset, R.yOffset = F.yCoordinate - M.yCoordinate + P.yOffset, R.xAdvance = 0, R.yAdvance = 0;
      break;
    }
  }
}
function zG($, Q, Z, K) {
  let q = Z.digest, J = Q.infos, U = Q.positions, W = Z.subtables;
  for (let H = 0;H < J.length; H++) {
    let _ = J[H];
    if (!_)
      continue;
    if (!q.mayHave(_.glyphId))
      continue;
    if (o8($, _.glyphId, K) !== 3)
      continue;
    let N = -1;
    if (H > 0) {
      let X = J[H - 1];
      if (X) {
        if (o8($, X.glyphId, K) === 3)
          N = H - 1;
      }
    }
    if (N < 0)
      continue;
    let V = J[N];
    if (!V)
      continue;
    for (let X = 0;X < W.length; X++) {
      let z = W[X], G = z.mark1Coverage.get(_.glyphId), Y = z.mark2Coverage.get(V.glyphId);
      if (G === null || Y === null)
        continue;
      let j = z.mark1Array.markRecords[G], E = z.mark2Array[Y];
      if (!j || !E)
        continue;
      let w = E.mark2Anchors[j.markClass];
      if (!w)
        continue;
      let L = j.markAnchor, A = U[H], D = U[N];
      if (!A || !D)
        continue;
      A.xOffset = w.xCoordinate - L.xCoordinate + D.xOffset, A.yOffset = w.yCoordinate - L.yCoordinate + D.yOffset;
      break;
    }
  }
}
function GG($, Q, Z, K, q, J, U) {
  let W = Q.infos, H = W.length, _ = Z.digest, N = Z.subtables, V = null;
  if (Z.flag !== 0 && $.gdef !== null)
    V = j8($, Q, Z.flag);
  for (let X = 0;X < H; X++) {
    let z = W[X];
    if (!z)
      continue;
    if (V?.[X])
      continue;
    if (!_.mayHave(z.glyphId))
      continue;
    for (let G = 0;G < N.length; G++) {
      let Y = N[G], j = false, E = [];
      if (Y.format === 1) {
        let w = jG($, Q, X, Y, Z.flag);
        if (w)
          j = true, E = w;
      } else if (Y.format === 2) {
        let w = EG($, Q, X, Y, Z.flag);
        if (w)
          j = true, E = w;
      } else if (Y.format === 3) {
        if (wG($, Q, X, Y, Z.flag))
          j = true, E = Y.lookupRecords;
      }
      if (j) {
        rW($, Q, X, E, K, q, J, U);
        break;
      }
    }
  }
}
function YG($, Q, Z, K, q, J, U) {
  let W = Q.infos, H = W.length, _ = Z.digest, N = Z.subtables, V = null;
  if (Z.flag !== 0 && $.gdef !== null)
    V = j8($, Q, Z.flag);
  for (let X = 0;X < H; X++) {
    let z = W[X];
    if (!z)
      continue;
    if (V?.[X])
      continue;
    if (!_.mayHave(z.glyphId))
      continue;
    for (let G = 0;G < N.length; G++) {
      let Y = N[G], j = false, E = [];
      if (Y.format === 1) {
        let w = AG($, Q, X, Y, Z.flag);
        if (w)
          j = true, E = w;
      } else if (Y.format === 2) {
        let w = LG($, Q, X, Y, Z.flag);
        if (w)
          j = true, E = w;
      } else if (Y.format === 3) {
        if (FG($, Q, X, Y, Z.flag))
          j = true, E = Y.lookupRecords;
      }
      if (j) {
        rW($, Q, X, E, K, q, J, U);
        break;
      }
    }
  }
}
function jG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId, U = K.coverage.get(J);
  if (U === null)
    return null;
  let W = K.ruleSets[U];
  if (!W)
    return null;
  for (let H = 0;H < W.length; H++) {
    let _ = W[H];
    if (R9($, Q, Z + 1, _.inputSequence, q))
      return _.lookupRecords;
  }
  return null;
}
function EG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId;
  if (K.coverage.get(J) === null)
    return null;
  let W = K.classDef.get(J), H = K.classRuleSets[W];
  if (!H)
    return null;
  for (let _ = 0;_ < H.length; _++) {
    let N = H[_];
    if (M9($, Q, Z + 1, N.inputClasses, K.classDef, q))
      return N.lookupRecords;
  }
  return null;
}
function wG($, Q, Z, K, q) {
  let J = Q.infos, U = K.coverages, W = Z;
  for (let H = 0;H < U.length; H++) {
    let _ = U[H];
    while (W < J.length && Y6($, J[W]?.glyphId, q))
      W++;
    if (W >= J.length)
      return false;
    if (_.get(J[W]?.glyphId) === null)
      return false;
    W++;
  }
  return true;
}
function AG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId, U = K.coverage.get(J);
  if (U === null)
    return null;
  let W = K.chainRuleSets[U];
  if (!W)
    return null;
  for (let H = 0;H < W.length; H++) {
    let _ = W[H];
    if (!aW($, Q, Z - 1, _.backtrackSequence, q))
      continue;
    if (!R9($, Q, Z + 1, _.inputSequence, q))
      continue;
    let N = Z + 1;
    for (let V = 0;V < _.inputSequence.length; V++) {
      while (N < Q.infos.length && Y6($, Q.infos[N]?.glyphId, q))
        N++;
      N++;
    }
    if (!R9($, Q, N, _.lookaheadSequence, q))
      continue;
    return _.lookupRecords;
  }
  return null;
}
function LG($, Q, Z, K, q) {
  let J = Q.infos[Z]?.glyphId;
  if (K.coverage.get(J) === null)
    return null;
  let W = K.inputClassDef.get(J), H = K.chainClassRuleSets[W];
  if (!H)
    return null;
  for (let _ = 0;_ < H.length; _++) {
    let N = H[_];
    if (!tW($, Q, Z - 1, N.backtrackClasses, K.backtrackClassDef, q))
      continue;
    if (!M9($, Q, Z + 1, N.inputClasses, K.inputClassDef, q))
      continue;
    let V = Q.infos, X = Z + 1;
    for (let z = 0;z < N.inputClasses.length; z++) {
      while (X < V.length && Y6($, V[X]?.glyphId, q))
        X++;
      X++;
    }
    if (!M9($, Q, X, N.lookaheadClasses, K.lookaheadClassDef, q))
      continue;
    return N.lookupRecords;
  }
  return null;
}
function FG($, Q, Z, K, q) {
  let J = Q.infos, U = Z - 1, W = K.backtrackCoverages;
  for (let X = 0;X < W.length; X++) {
    let z = W[X];
    while (U >= 0 && Y6($, J[U]?.glyphId, q))
      U--;
    if (U < 0)
      return false;
    if (z.get(J[U]?.glyphId) === null)
      return false;
    U--;
  }
  let H = Z, _ = K.inputCoverages;
  for (let X = 0;X < _.length; X++) {
    let z = _[X];
    while (H < J.length && Y6($, J[H]?.glyphId, q))
      H++;
    if (H >= J.length)
      return false;
    if (z.get(J[H]?.glyphId) === null)
      return false;
    H++;
  }
  let N = H, V = K.lookaheadCoverages;
  for (let X = 0;X < V.length; X++) {
    let z = V[X];
    while (N < J.length && Y6($, J[N]?.glyphId, q))
      N++;
    if (N >= J.length)
      return false;
    if (z.get(J[N]?.glyphId) === null)
      return false;
    N++;
  }
  return true;
}
function rW($, Q, Z, K, q, J, U, W) {
  let H = K.length;
  if (H === 0)
    return;
  if (H === 1) {
    let N = K[0], V = q.gposLookupMap.get(N.lookupListIndex);
    if (!V)
      return;
    if (Z + N.sequenceIndex >= Q.infos.length)
      return;
    YQ($, Q, V.lookup, q, J, U, W);
    return;
  }
  let _ = new Uint8Array(H);
  for (let N = 0;N < H; N++) {
    let V = -1, X = -1;
    for (let j = 0;j < H; j++) {
      if (_[j])
        continue;
      let E = K[j].sequenceIndex;
      if (E > X)
        X = E, V = j;
    }
    if (V < 0)
      break;
    _[V] = 1;
    let z = K[V], G = q.gposLookupMap.get(z.lookupListIndex);
    if (!G)
      continue;
    if (Z + z.sequenceIndex >= Q.infos.length)
      continue;
    YQ($, Q, G.lookup, q, J, U, W);
  }
}
function R9($, Q, Z, K, q) {
  let J = Z;
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    while (J < Q.infos.length && Y6($, Q.infos[J]?.glyphId, q))
      J++;
    if (J >= Q.infos.length)
      return false;
    if (Q.infos[J]?.glyphId !== W)
      return false;
    J++;
  }
  return true;
}
function aW($, Q, Z, K, q) {
  let J = Q.infos, U = Z;
  for (let W = 0;W < K.length; W++) {
    let H = K[W];
    while (U >= 0 && Y6($, J[U]?.glyphId, q))
      U--;
    if (U < 0)
      return false;
    if (J[U]?.glyphId !== H)
      return false;
    U--;
  }
  return true;
}
function M9($, Q, Z, K, q, J) {
  let U = Q.infos, W = Z;
  for (let H = 0;H < K.length; H++) {
    let _ = K[H];
    while (W < U.length && Y6($, U[W]?.glyphId, J))
      W++;
    if (W >= U.length)
      return false;
    if (q.get(U[W]?.glyphId) !== _)
      return false;
    W++;
  }
  return true;
}
function tW($, Q, Z, K, q, J) {
  let U = Q.infos, W = Z;
  for (let H = 0;H < K.length; H++) {
    let _ = K[H];
    while (W >= 0 && Y6($, U[W]?.glyphId, J))
      W--;
    if (W < 0)
      return false;
    if (q.get(U[W]?.glyphId) !== _)
      return false;
    W--;
  }
  return true;
}
function Y6($, Q, Z) {
  if ((Z & 14) === 0 && (Z & 65280) === 0)
    return false;
  let K = $.gdef;
  if (!K)
    return false;
  let q = z8(K, Q);
  if (Z & b6.IgnoreBaseGlyphs && q === 1)
    return true;
  if (Z & b6.IgnoreLigatures && q === 2)
    return true;
  if (Z & b6.IgnoreMarks && q === 3)
    return true;
  let J = W5(Z);
  if (J !== 0 && q === 3) {
    if (K.markAttachClassDef.get(Q) !== J)
      return true;
  }
  return false;
}
function DG($, Q) {
  let Z = $.morx;
  if (!Z)
    return;
  let K = Z.chains;
  for (let q = 0;q < K.length; q++) {
    let J = K[q], U = J.subtables;
    for (let W = 0;W < U.length; W++) {
      let H = U[W];
      if ((J.defaultFlags & H.subFeatureFlags) === 0)
        continue;
      switch (H.type) {
        case 4: {
          let _ = Q.infos;
          for (let N = 0;N < _.length; N++) {
            let V = _[N], X = g1(H, V.glyphId);
            if (X !== null)
              V.glyphId = X;
          }
          break;
        }
        case 0:
          u5(H, Q.infos);
          break;
        case 1:
          x5(H, Q.infos);
          break;
        case 2: {
          let _ = f5(H, Q.infos);
          if (_.length !== Q.infos.length)
            Q.initFromInfos(_);
          break;
        }
        case 5: {
          let _ = l5(H, Q.infos);
          if (_.length !== Q.infos.length)
            Q.initFromInfos(_);
          break;
        }
      }
    }
  }
}
var jQ = { pairs: "14>1,1e>2,u>2,2wt>1,1>1,1ge>1,1wp>1,1j>1,f>1,hm>1,1>1,u>1,u6>1,1>1,+5,28>1,w>1,1>1,+3,b8>1,1>1,+3,1>3,-1>-1,3>1,1>1,+2,1s>1,1>1,x>1,th>1,1>1,+2,db>1,1>1,+3,3>1,1>1,+2,14qm>1,1>1,+1,4q>1,1e>2,u>2,2>1,+1", canonical: "6f1>-6dx,6dy>-6dx,6ec>-6ed,6ee>-6ed,6ww>2jj,-2ji>2jj,14r4>-1e7l,1e7m>-1e7l,1e7m>-1e5c,1e5d>-1e5b,1e5c>-14qx,14qy>-14qx,14vn>-1ecg,1ech>-1ecg,1edu>-1ecg,1eci>-1ecg,1eda>-1ecg,1eci>-1ecg,1eci>-168q,168r>-168q,168s>-14ye,14yf>-14ye" };
function Y0($, Q) {
  let K = 0, q = new Map, J = Q ? new Map : null, U = "";
  function W(H) {
    if (H.indexOf("+") !== -1)
      for (let _ = +H;_--; )
        W(U);
    else {
      U = H;
      let _ = H.split(">"), N = _[0] ?? "", V = _[1] ?? "";
      K += parseInt(N, 36);
      let X = String.fromCodePoint(K);
      K += parseInt(V, 36);
      let z = String.fromCodePoint(K);
      if (q.set(X, z), J)
        J.set(z, X);
    }
  }
  return $.split(",").forEach(W), { map: q, reverseMap: J };
}
var EQ = null;
var eW = null;
var $H = null;
function wQ() {
  if (!EQ) {
    let { map: $, reverseMap: Q } = Y0(jQ.pairs, true);
    EQ = $, eW = Q, $H = Y0(jQ.canonical, false).map;
  }
}
function B5($) {
  return wQ(), EQ?.get($) || null;
}
function R5($) {
  return wQ(), eW?.get($) || null;
}
function M5($) {
  return wQ(), $H?.get($) || null;
}
var j0 = { R: "13k,1a,2,3,3,2+1j,ch+16,a+1,5+2,2+n,5,a,4,6+16,4+3,h+1b,4mo,179q,2+9,2+11,2i9+7y,2+68,4,3+4,5+13,4+3,2+4k,3+29,8+cf,1t+7z,w+17,3+3m,1t+3z,16o1+5r,8+30,8+mc,29+1r,29+4v,75+73", EN: "1c+9,3d+1,6,187+9,513,4+5,7+9,sf+j,175h+9,qw+q,161f+1d,4xt+a,25i+9", ES: "17,2,6dp+1,f+1,av,16vr,mx+1,4o,2", ET: "z+2,3h+3,b+1,ym,3e+1,2o,p4+1,8,6u,7c,g6,1wc,1n9+4,30+1b,2n,6d,qhx+1,h0m,a+1,49+2,63+1,4+1,6bb+3,12jj", AN: "16o+5,2j+9,2+1,35,ed,1ff2+9,87+u", CS: "18,2+1,b,2u,12k,55v,l,17v0,2,3,53,2+1,b", B: "a,3,f+2,2v,690", S: "9,2,k", WS: "c,k,4f4,1vk+a,u,1j,335", ON: "x+1,4+4,h+5,r+5,r+3,z,5+3,2+1,2+1,5,2+2,3+4,o,w,ci+1,8+d,3+d,6+8,2+g,39+1,9,6+1,2,33,b8,3+1,3c+1,7+1,5r,b,7h+3,sa+5,2,3i+6,jg+3,ur+9,2v,ij+1,9g+9,7+a,8m,4+1,49+x,14u,2+2,c+2,e+2,e+2,e+1,i+n,e+e,2+p,u+2,e+2,36+1,2+3,2+1,b,2+2,6+5,2,2,2,h+1,5+4,6+3,3+f,16+2,5+3l,3+81,1y+p,2+40,q+a,m+13,2r+ch,2+9e,75+hf,3+v,2+2w,6e+5,f+6,75+2a,1a+p,2+2g,d+5x,r+b,6+3,4+o,g,6+1,6+2,2k+1,4,2j,5h+z,1m+1,1e+f,t+2,1f+e,d+3,4o+3,2s+1,w,535+1r,h3l+1i,93+2,2s,b+1,3l+x,2v,4g+3,21+3,kz+1,g5v+1,5a,j+9,n+v,2,3,2+8,2+1,3+2,2,3,46+1,4+4,h+5,r+5,r+a,3h+2,4+6,b+4,78,1r+24,4+c,4,1hb,ey+6,103+j,16j+c,1ux+7,5+g,fsh,jdq+1t,4,57+2e,p1,1m,1m,1m,1m,4kt+1,7j+17,5+2r,d+e,3+e,2+e,2+10,m+4,w,1n+5,1q,4z+5,4b+rb,9+c,4+c,4+37,d+2g,8+b,l+b,5+1j,9+9,7+13,9+t,3+1,27+3c,2+29,2+3q,d+d,3+4,4+2,6+6,a+o,8+6,a+2,e+6,16+42,2+1i", BN: "0+8,6+d,2s+5,2+p,e,4m9,1kt+2,2b+5,5+5,17q9+v,7k,6p+8,6+1,119d+3,440+7,96s+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+1,1ekf+75,6p+2rz,1ben+1,1ekf+1,1ekf+1", NSM: "lc+33,7o+6,7c+18,2,2+1,2+1,2,21+a,1d+k,h,2u+6,3+5,3+1,2+3,10,v+q,2k+a,1n+8,a,p+3,2+8,2+2,2+4,18+2,3c+e,2+v,1k,2,5+7,5,4+6,b+1,u,1n,5+3,9,l+1,r,3+1,1m,5+1,5+1,3+2,4,v+1,4,c+1,1m,5+4,2+1,5,l+1,n+5,2,1n,3,2+3,9,8+1,c+1,v,1q,d,1f,4,1m+2,6+2,2+3,8+1,c+1,u,1n,g+1,l+1,t+1,1m+1,5+3,9,l+1,u,21,8+2,2,2j,3+6,d+7,2r,3+8,c+5,23+1,s,2,2,1k+d,2+4,2+1,6+a,2+z,a,2v+3,2+5,2+1,3+1,q+1,5+2,h+3,e,3+1,7,g,jk+2,qb+2,u+2,u+1,v+1,1t+1,2+6,9,3+a,a,1a+2,3c+1,z,3b+2,5+1,a,7+2,64+1,3,1n,2+6,2,2,3+7,7+9,3,1d+g,1s+3,1d,2+4,2,6,15+8,d+1,x+3,3+1,2+2,1l,2+1,4,2+2,1n+7,3+1,49+2,2+c,2+6,5,7,4+1,5j+1l,2+4,k1+w,2db+2,3y,2p+v,ff+3,30+1,n9x+3,2+9,x+1,29+1,7l,4,5,q+1,6,48+1,r+h,e,13+7,q+a,1b+2,1d,3+3,3+1,14,1w+5,3+1,3+1,d,9,1c,1g,2+2,3+1,6+1,2,17+1,9,6n,3,5,fn5,ki+f,h+f,r2,6b,46+4,1af+2,2+1,6+3,15+2,5,4m+1,fy+3,as+1,4a+a,4x,1j+e,1l+2,1e+3,3+1,1y+2,11+4,2+7,1r,d+1,1h+8,b+3,3,2o+2,3,2+1,7,4h,4+7,m+1,1m+1,4,12+6,4+4,5g+7,3+2,2,o,2d+5,2,5+1,2+1,6n+3,7+1,2+1,s+1,2e+7,3,2+1,2z,2,3+5,2,2u+2,3+3,2+4,78+8,2+1,75+1,2,5,41+3,3+1,5,x+5,3+1,15+5,3+3,9,a+5,3+2,1b+c,2+1,bb+6,2+5,2d+l,3+6,2+1,2+1,3f+5,4,2+1,2+6,2,21+1,4,2,9o+1,f0c+4,1o+6,t5,1s+3,2a,f5l+1,43t+2,i+7,3+6,v+3,45+2,1j0+1i,5+1d,9,f,n+4,2+e,11t+6,2+g,3+6,2+1,2+4,7a+6,c6+3,15t+6,32+6,gzhy+6n", AL: "16w,3,2,e+1b,z+2,2+2s,g+1,8+1,b+m,2+t,s+2i,c+e,4h+f,1d+1e,1bwe+dp,3+3z,x+c,2+1,35+3y,2rm+z,5+7,b+5,dt+l,c+u,17nl+27,1t+27,4x+6n,3+d", LRO: "6ct", RLO: "6cu", LRE: "6cq", RLE: "6cr", PDF: "6cs", LRI: "6ee", RLI: "6ef", FSI: "6eg", PDI: "6eh" };
var J6 = {};
var AQ = {};
J6.L = 1;
AQ[1] = "L";
Object.keys(j0).forEach(($, Q) => {
  J6[$] = 1 << Q + 1;
  let Z = J6[$];
  if (Z !== undefined)
    AQ[Z] = $;
});
Object.freeze(J6);
function H6($) {
  return J6[$] ?? 0;
}
var q8 = H6("LRI") | H6("RLI") | H6("FSI");
var O5 = H6("L") | H6("R") | H6("AL");
var k6 = H6("B") | H6("S") | H6("WS") | H6("ON") | H6("FSI") | H6("LRI") | H6("RLI") | H6("PDI");
var L6 = H6("BN") | H6("RLE") | H6("LRE") | H6("RLO") | H6("LRO") | H6("PDF");
var E0 = H6("S") | H6("WS") | H6("B") | q8 | H6("PDI") | L6;
var T5 = null;
function BG() {
  if (!T5) {
    T5 = new Map;
    let $ = 0;
    for (let Q in j0)
      if (Object.hasOwn(j0, Q)) {
        let Z = j0[Q], K = "", q = 0, J = false, U = 0;
        for (let W = 0;W <= Z.length + 1; W += 1) {
          let H = Z[W];
          if (H !== "," && W !== Z.length)
            if (H === "+")
              J = true, U = $ = U + parseInt(K, 36), K = "";
            else
              K += H;
          else {
            if (!J)
              U = $ = U + parseInt(K, 36), q = $;
            else
              q = $ + parseInt(K, 36);
            J = false, K = "", U = q;
            let _ = H6(Q);
            for (let N = $;N < q + 1; N += 1)
              T5.set(N, _);
          }
        }
      }
  }
}
function m6($) {
  BG();
  let Q = $.codePointAt(0);
  if (Q === undefined)
    return H6("L");
  return T5?.get(Q) ?? H6("L");
}
var f6 = J6.L ?? 1;
var p6 = J6.R ?? 2;
var C6 = J6.EN ?? 4;
var w0 = J6.ES ?? 8;
var P5 = J6.ET ?? 16;
var A0 = J6.AN ?? 32;
var I5 = J6.CS ?? 64;
var T9 = J6.B ?? 128;
var RG = J6.S ?? 256;
var v5 = J6.ON ?? 512;
var MG = J6.BN ?? 1024;
var k5 = J6.NSM ?? 2048;
var C5 = J6.AL ?? 4096;
var QH = J6.LRO ?? 8192;
var S5 = J6.RLO ?? 16384;
var ZH = J6.LRE ?? 32768;
var LQ = J6.RLE ?? 65536;
var KH = J6.PDF ?? 131072;
var TG = J6.LRI ?? 262144;
var qH = J6.RLI ?? 524288;
var OG = J6.FSI ?? 1048576;
var O9 = J6.PDI ?? 2097152;
function a($, Q) {
  return $[Q] ?? 0;
}
function _6($, Q) {
  return $[Q] ?? 0;
}
function P9($, Q) {
  return $[Q] ?? "";
}
function FQ($, Q) {
  let K = new Uint32Array($.length);
  for (let Y = 0;Y < $.length; Y++)
    K[Y] = m6(P9($, Y));
  let q = new Map;
  function J(Y, j) {
    let E = a(K, Y);
    if (K[Y] = j, q.set(E, (q.get(E) ?? 0) - 1), E & k6)
      q.set(k6, (q.get(k6) ?? 0) - 1);
    if (q.set(j, (q.get(j) ?? 0) + 1), j & k6)
      q.set(k6, (q.get(k6) ?? 0) + 1);
  }
  let U = new Uint8Array($.length), W = new Map, H = [], _ = null;
  function N(Y, j) {
    for (let E = Y;E < $.length; E++) {
      let w = a(K, E);
      if (w & (p6 | C5))
        return 1;
      if (w & (T9 | f6) || j && w === O9)
        return 0;
      if (w & q8) {
        let L = V(E);
        E = L === -1 ? $.length : L;
      }
    }
    return 0;
  }
  function V(Y) {
    let j = 1;
    for (let E = Y + 1;E < $.length; E++) {
      let w = a(K, E);
      if (w & T9)
        break;
      if (w & O9) {
        if (--j === 0)
          return E;
      } else if (w & q8)
        j++;
    }
    return -1;
  }
  for (let Y = 0;Y < $.length; Y++) {
    if (!_)
      _ = { start: Y, end: $.length - 1, level: Q === "rtl" ? 1 : Q === "ltr" ? 0 : N(Y, false) }, H.push(_);
    if (a(K, Y) & T9)
      _.end = Y, _ = null;
  }
  let X = LQ | ZH | S5 | QH | q8 | O9 | KH | T9, z = (Y) => Y + (Y & 1 ? 1 : 2), G = (Y) => Y + (Y & 1 ? 2 : 1);
  for (let Y = 0;Y < H.length; Y++) {
    let j = H[Y];
    if (!j)
      continue;
    _ = j;
    let E = [{ _level: _.level, _override: 0, _isolate: 0 }], w = 0, L = 0, A = 0;
    q.clear();
    for (let R = _.start;R <= _.end; R++) {
      let P = a(K, R), T = E[E.length - 1];
      if (!T)
        continue;
      if (q.set(P, (q.get(P) ?? 0) + 1), P & k6)
        q.set(k6, (q.get(k6) ?? 0) + 1);
      if (P & X) {
        if (P & (LQ | ZH)) {
          U[R] = T._level;
          let m = (P === LQ ? G : z)(T._level);
          if (m <= 125 && !w && !L)
            E.push({ _level: m, _override: 0, _isolate: 0 });
          else if (!w)
            L++;
        } else if (P & (S5 | QH)) {
          U[R] = T._level;
          let m = (P === S5 ? G : z)(T._level);
          if (m <= 125 && !w && !L)
            E.push({ _level: m, _override: P & S5 ? p6 : f6, _isolate: 0 });
          else if (!w)
            L++;
        } else if (P & q8) {
          if (P & OG)
            P = N(R + 1, true) === 1 ? qH : TG;
          if (U[R] = T._level, T._override)
            J(R, T._override);
          let m = (P === qH ? G : z)(T._level);
          if (m <= 125 && w === 0 && L === 0)
            A++, E.push({ _level: m, _override: 0, _isolate: 1, _isolInitIndex: R });
          else
            w++;
        } else if (P & O9) {
          if (w > 0)
            w--;
          else if (A > 0) {
            L = 0;
            while (E.length > 0) {
              if (E[E.length - 1]?._isolate)
                break;
              E.pop();
            }
            let v = E[E.length - 1]?._isolInitIndex;
            if (v != null)
              W.set(v, R), W.set(R, v);
            E.pop(), A--;
          }
          if (T = E[E.length - 1], !T)
            continue;
          if (U[R] = T._level, T._override)
            J(R, T._override);
        } else if (P & KH) {
          if (w === 0) {
            if (L > 0)
              L--;
            else if (!T._isolate && E.length > 1) {
              if (E.pop(), T = E[E.length - 1], !T)
                continue;
            }
          }
          U[R] = T._level;
        } else if (P & T9)
          U[R] = _.level;
      } else if (U[R] = T._level, T._override && P !== MG)
        J(R, T._override);
    }
    let D = [], F = null;
    for (let R = _.start;R <= _.end; R++) {
      let P = a(K, R);
      if (!(P & L6)) {
        let T = U[R] ?? 0, m = !!(P & q8), v = P === O9;
        if (F && T === F._level)
          F._end = R, F._endsWithIsolInit = m;
        else
          F = { _start: R, _end: R, _level: T, _startsWithPDI: v, _endsWithIsolInit: m }, D.push(F);
      }
    }
    let M = [];
    for (let R = 0;R < D.length; R++) {
      let P = D[R];
      if (!P)
        continue;
      if (!P._startsWithPDI || P._startsWithPDI && !W.has(P._start)) {
        F = P;
        let T = [P];
        while (F?._endsWithIsolInit) {
          let S = W.get(F._end);
          if (S == null)
            break;
          let d = false;
          for (let o = R + 1;o < D.length; o++) {
            let n = D[o];
            if (n?._start === S) {
              F = n, T.push(n), d = true;
              break;
            }
          }
          if (!d)
            break;
        }
        let m = [];
        for (let S = 0;S < T.length; S++) {
          let d = T[S];
          for (let o = d._start;o <= d._end; o++)
            m.push(o);
        }
        let v = m[0] ?? 0, f = U[v] ?? 0, l = _.level;
        for (let S = v - 1;S >= 0; S--)
          if (!(a(K, S) & L6)) {
            l = U[S] ?? 0;
            break;
          }
        let k = m[m.length - 1] ?? 0, O = U[k] ?? 0, h = _.level;
        if (!(a(K, k) & q8)) {
          for (let S = k + 1;S <= _.end; S++)
            if (!(a(K, S) & L6)) {
              h = U[S] ?? 0;
              break;
            }
        }
        M.push({ _seqIndices: m, _sosType: Math.max(l, f) % 2 ? p6 : f6, _eosType: Math.max(h, O) % 2 ? p6 : f6 });
      }
    }
    for (let R = 0;R < M.length; R++) {
      let P = M[R], { _seqIndices: T, _sosType: m, _eosType: v } = P, f = T[0] ?? 0, l = (U[f] ?? 0) & 1 ? p6 : f6;
      if (q.get(k5))
        for (let k = 0;k < T.length; k++) {
          let O = _6(T, k);
          if (a(K, O) & k5) {
            let h = m;
            for (let S = k - 1;S >= 0; S--) {
              let d = _6(T, S);
              if (!(a(K, d) & L6)) {
                h = a(K, d);
                break;
              }
            }
            J(O, h & (q8 | O9) ? v5 : h);
          }
        }
      if (q.get(C6))
        for (let k = 0;k < T.length; k++) {
          let O = _6(T, k);
          if (a(K, O) & C6)
            for (let h = k - 1;h >= -1; h--) {
              let S = h === -1 ? m : a(K, _6(T, h));
              if (S & O5) {
                if (S === C5)
                  J(O, A0);
                break;
              }
            }
        }
      if (q.get(C5))
        for (let k = 0;k < T.length; k++) {
          let O = _6(T, k);
          if (a(K, O) & C5)
            J(O, p6);
        }
      if (q.get(w0) || q.get(I5))
        for (let k = 1;k < T.length - 1; k++) {
          let O = _6(T, k);
          if (a(K, O) & (w0 | I5)) {
            let h = 0, S = 0;
            for (let d = k - 1;d >= 0; d--)
              if (h = a(K, _6(T, d)), !(h & L6))
                break;
            for (let d = k + 1;d < T.length; d++)
              if (S = a(K, _6(T, d)), !(S & L6))
                break;
            if (h === S && (a(K, O) === w0 ? h === C6 : h & (C6 | A0)))
              J(O, h);
          }
        }
      if (q.get(C6))
        for (let k = 0;k < T.length; k++) {
          let O = _6(T, k);
          if (a(K, O) & C6) {
            for (let h = k - 1;h >= 0; h--) {
              let S = _6(T, h);
              if (!(a(K, S) & (P5 | L6)))
                break;
              J(S, C6);
            }
            for (k++;k < T.length; k++) {
              let h = _6(T, k);
              if (!(a(K, h) & (P5 | L6 | C6)))
                break;
              if (a(K, h) !== C6)
                J(h, C6);
            }
          }
        }
      if (q.get(P5) || q.get(w0) || q.get(I5))
        for (let k = 0;k < T.length; k++) {
          let O = _6(T, k);
          if (a(K, O) & (P5 | w0 | I5)) {
            J(O, v5);
            for (let h = k - 1;h >= 0; h--) {
              let S = _6(T, h);
              if (!(a(K, S) & L6))
                break;
              J(S, v5);
            }
            for (let h = k + 1;h < T.length; h++) {
              let S = _6(T, h);
              if (!(a(K, S) & L6))
                break;
              J(S, v5);
            }
          }
        }
      if (q.get(C6)) {
        let k = m;
        for (let O = 0;O < T.length; O++) {
          let h = _6(T, O), S = a(K, h);
          if (S & C6) {
            if (k === f6)
              J(h, f6);
          } else if (S & O5)
            k = S;
        }
      }
      if (q.get(k6)) {
        let k = p6 | C6 | A0, O = k | f6, h = [], S = [];
        for (let d = 0;d < T.length; d++) {
          let o = _6(T, d);
          if (a(K, o) & k6) {
            let n = P9($, o);
            if (B5(n) !== null)
              if (S.length < 63)
                S.push({ char: n, seqIndex: d });
              else
                break;
            else {
              let u = R5(n);
              if (u !== null)
                for (let N6 = S.length - 1;N6 >= 0; N6--) {
                  let y = S[N6];
                  if (!y)
                    continue;
                  let t = y.char, g = M5(n), w6 = M5(t);
                  if (t === u || g && t === R5(g) || w6 && B5(w6) === n) {
                    h.push([y.seqIndex, d]), S.length = N6;
                    break;
                  }
                }
            }
          }
        }
        h.sort((d, o) => d[0] - o[0]);
        for (let d = 0;d < h.length; d++) {
          let o = h[d], [n, e] = o, u = false, N6 = 0;
          for (let y = n + 1;y < e; y++) {
            let t = _6(T, y), g = a(K, t);
            if (g & O) {
              u = true;
              let w6 = g & k ? p6 : f6;
              if (w6 === l) {
                N6 = w6;
                break;
              }
            }
          }
          if (u && !N6) {
            N6 = m;
            for (let y = n - 1;y >= 0; y--) {
              let t = _6(T, y), g = a(K, t);
              if (g & O) {
                let w6 = g & k ? p6 : f6;
                N6 = w6 !== l ? w6 : l;
                break;
              }
            }
          }
          if (N6) {
            if (K[_6(T, n)] = N6, K[_6(T, e)] = N6, N6 !== l) {
              for (let y = n + 1;y < T.length; y++) {
                let t = _6(T, y);
                if (!(a(K, t) & L6)) {
                  if (m6(P9($, t)) & k5)
                    K[t] = N6;
                  break;
                }
              }
              for (let y = e + 1;y < T.length; y++) {
                let t = _6(T, y);
                if (!(a(K, t) & L6)) {
                  if (m6(P9($, t)) & k5)
                    K[t] = N6;
                  break;
                }
              }
            }
          }
        }
        for (let d = 0;d < T.length; d++) {
          let o = _6(T, d);
          if (a(K, o) & k6) {
            let n = d, e = d, u = m;
            for (let y = d - 1;y >= 0; y--) {
              let t = _6(T, y);
              if (a(K, t) & L6)
                n = y;
              else {
                u = a(K, t) & k ? p6 : f6;
                break;
              }
            }
            let N6 = v;
            for (let y = d + 1;y < T.length; y++) {
              let t = _6(T, y);
              if (a(K, t) & (k6 | L6))
                e = y;
              else {
                N6 = a(K, t) & k ? p6 : f6;
                break;
              }
            }
            for (let y = n;y <= e; y++)
              K[_6(T, y)] = u === N6 ? u : l;
            d = e;
          }
        }
      }
    }
    for (let R = _.start;R <= _.end; R++) {
      let P = U[R] ?? 0, T = a(K, R);
      if (P & 1) {
        if (T & (f6 | C6 | A0))
          U[R]++;
      } else if (T & p6)
        U[R]++;
      else if (T & (A0 | C6))
        U[R] += 2;
      if (T & L6)
        U[R] = R === 0 ? _.level : U[R - 1] ?? _.level;
      if (R === _.end || m6(P9($, R)) & (RG | T9))
        for (let m = R;m >= 0 && m6(P9($, m)) & E0; m--)
          U[m] = _.level;
    }
  }
  return { levels: U, paragraphs: H };
}
var JH = "14>1,j>2,t>2,u>2,1a>g,2v3>1,1>1,1ge>1,1wd>1,b>1,1j>1,f>1,ai>3,-2>3,+1,8>1k0,-1jq>1y7,-1y6>1hf,-1he>1h6,-1h5>1ha,-1h8>1qi,-1pu>1,6>3u,-3s>7,6>1,1>1,f>1,1>1,+2,3>1,1>1,+13,4>1,1>1,6>1eo,-1ee>1,3>1mg,-1me>1mk,-1mj>1mi,-1mg>1mi,-1md>1,1>1,+2,1>10k,-103>1,1>1,4>1,5>1,1>1,+10,3>1,1>8,-7>8,+1,-6>7,+1,a>1,1>1,u>1,u6>1,1>1,+5,26>1,1>1,2>1,2>2,8>1,7>1,4>1,1>1,+5,b8>1,1>1,+3,1>3,-2>1,2>1,1>1,+2,c>1,3>1,1>1,+2,h>1,3>1,a>1,1>1,2>1,3>1,1>1,d>1,f>1,3>1,1a>1,1>1,6>1,7>1,13>1,k>1,1>1,+19,4>1,1>1,+2,2>1,1>1,+18,m>1,a>1,1>1,lk>1,1>1,4>1,2>1,f>1,3>1,1>1,+3,db>1,1>1,+3,3>1,1>1,+2,14qm>1,1>1,+1,6>1,4j>1,j>2,t>2,u>2,2>1,+1";
var DQ = null;
function PG() {
  if (!DQ) {
    let { map: $, reverseMap: Q } = Y0(JH, true);
    if (Q)
      Q.forEach((Z, K) => {
        $.set(K, Z);
      });
    DQ = $;
  }
}
function h5($) {
  return PG(), DQ?.get($) || null;
}
function UH($, Q, Z, K) {
  let q = $.length, J = Math.max(0, Z == null ? 0 : +Z), U = Math.min(q - 1, K == null ? q - 1 : +K), W = [];
  for (let H = 0;H < Q.paragraphs.length; H++) {
    let _ = Q.paragraphs[H], N = Math.max(J, _.start), V = Math.min(U, _.end);
    if (N < V) {
      let X = Q.levels.slice(N, V + 1);
      for (let Y = V;Y >= N; Y--) {
        let j = $[Y];
        if (j === undefined)
          break;
        if (!(m6(j) & E0))
          break;
        X[Y - N] = _.level;
      }
      let z = _.level, G = 1 / 0;
      for (let Y = 0;Y < X.length; Y++) {
        let j = X[Y] ?? 0;
        if (j > z)
          z = j;
        if (j < G)
          G = j | 1;
      }
      for (let Y = z;Y >= G; Y--)
        for (let j = 0;j < X.length; j++)
          if ((X[j] ?? 0) >= Y) {
            let w = j;
            while (j + 1 < X.length) {
              if ((X[j + 1] ?? 0) < Y)
                break;
              j++;
            }
            if (j > w)
              W.push([w + N, j + N]);
          }
    }
  }
  return W;
}
function y5($, Q, Z, K) {
  let q = UH($, Q, Z, K), J = [];
  for (let U = 0;U < $.length; U++)
    J[U] = U;
  for (let U = 0;U < q.length; U++) {
    let W = q[U], H = W[0], _ = W[1], N = J.slice(H, _ + 1);
    for (let V = N.length;V--; ) {
      let X = N[V];
      if (X !== undefined)
        J[_ - V] = X;
    }
  }
  return J;
}
function WH($, Q = 4) {
  let Z = Q === 5 ? "rtl" : Q === 4 ? "ltr" : "auto", K = FQ($, Z);
  return { levels: K.levels, paragraphs: K.paragraphs };
}
function IG($, Q, Z = 0, K = $.length) {
  return [...y5($, Q, Z, K)];
}
function HH($, Q) {
  if ($.length === 0)
    return $;
  let Z = "x".repeat($.length), K = y5(Z, Q, 0, $.length), q = [];
  for (let J = 0;J < K.length; J++) {
    let U = K[J];
    if (U < $.length) {
      let W = $[U];
      if (W)
        q.push(W);
    }
  }
  return q;
}
function _H($) {
  let Q = String.fromCodePoint($), Z = h5(Q);
  return Z ? Z.codePointAt(0) ?? $ : $;
}
function NH($, Q) {
  for (let Z = 0;Z < $.length; Z++) {
    let K = $[Z], q = Q[Z];
    if (q === undefined)
      continue;
    if (q & 1) {
      let J = _H(K.codepoint);
      if (J !== K.codepoint)
        K.codepoint = J;
    }
  }
}
var i8 = { L: 1, R: 2, EN: 4, ES: 8, ET: 16, AN: 32, CS: 64, B: 128, S: 256, WS: 512, ON: 1024, BN: 2048, NSM: 4096, AL: 8192, LRO: 16384, RLO: 32768, LRE: 65536, RLE: 131072, PDF: 262144, LRI: 524288, RLI: 1048576, FSI: 2097152, PDI: 4194304 };
function vG($) {
  return m6($);
}
function kG($) {
  let Q = String.fromCodePoint($);
  return (m6(Q) & (i8.R | i8.AL)) !== 0;
}
function CG($) {
  let Q = String.fromCodePoint($);
  return (m6(Q) & i8.L) !== 0;
}
function SG($) {
  let Q = [...$];
  for (let Z = 0;Z < Q.length; Z++) {
    let K = Q[Z], q = m6(K);
    if (q & i8.L)
      return 4;
    if (q & (i8.R | i8.AL))
      return 5;
  }
  return 4;
}
function hG($, Q = 4) {
  if ($.length === 0)
    return { infos: $, levels: new Uint8Array(0) };
  let Z = $.map((J) => String.fromCodePoint(J.codepoint)).join(""), K = WH(Z, Q);
  return NH($, K.levels), { infos: HH($, K), levels: K.levels };
}
var VH;
((g) => {
  g[g.BK = 0] = "BK";
  g[g.CR = 1] = "CR";
  g[g.LF = 2] = "LF";
  g[g.CM = 3] = "CM";
  g[g.NL = 4] = "NL";
  g[g.SG = 5] = "SG";
  g[g.WJ = 6] = "WJ";
  g[g.ZW = 7] = "ZW";
  g[g.GL = 8] = "GL";
  g[g.SP = 9] = "SP";
  g[g.ZWJ = 10] = "ZWJ";
  g[g.B2 = 11] = "B2";
  g[g.BA = 12] = "BA";
  g[g.BB = 13] = "BB";
  g[g.HY = 14] = "HY";
  g[g.CB = 15] = "CB";
  g[g.CL = 16] = "CL";
  g[g.CP = 17] = "CP";
  g[g.EX = 18] = "EX";
  g[g.IN = 19] = "IN";
  g[g.NS = 20] = "NS";
  g[g.OP = 21] = "OP";
  g[g.QU = 22] = "QU";
  g[g.IS = 23] = "IS";
  g[g.NU = 24] = "NU";
  g[g.PO = 25] = "PO";
  g[g.PR = 26] = "PR";
  g[g.SY = 27] = "SY";
  g[g.AI = 28] = "AI";
  g[g.AL = 29] = "AL";
  g[g.CJ = 30] = "CJ";
  g[g.EB = 31] = "EB";
  g[g.EM = 32] = "EM";
  g[g.H2 = 33] = "H2";
  g[g.H3 = 34] = "H3";
  g[g.HL = 35] = "HL";
  g[g.ID = 36] = "ID";
  g[g.JL = 37] = "JL";
  g[g.JV = 38] = "JV";
  g[g.JT = 39] = "JT";
  g[g.RI = 40] = "RI";
  g[g.SA = 41] = "SA";
  g[g.XX = 42] = "XX";
})(VH ||= {});
var XH;
((U) => {
  U[U.Direct = 0] = "Direct";
  U[U.Indirect = 1] = "Indirect";
  U[U.CombiningIndirect = 2] = "CombiningIndirect";
  U[U.CombiningProhibited = 3] = "CombiningProhibited";
  U[U.Prohibited = 4] = "Prohibited";
  U[U.Explicit = 5] = "Explicit";
})(XH ||= {});
var zH;
((K) => {
  K[K.NoBreak = 0] = "NoBreak";
  K[K.Optional = 1] = "Optional";
  K[K.Mandatory = 2] = "Mandatory";
})(zH ||= {});
function GH($) {
  if ($ === 10)
    return 2;
  if ($ === 13)
    return 1;
  if ($ === 133)
    return 4;
  if ($ === 11 || $ === 12)
    return 0;
  if ($ === 8232)
    return 0;
  if ($ === 8233)
    return 0;
  if ($ === 8203)
    return 7;
  if ($ === 8205)
    return 10;
  if ($ === 8288)
    return 6;
  if ($ === 65279)
    return 6;
  if ($ === 32)
    return 9;
  if ($ === 160)
    return 8;
  if ($ === 8239)
    return 8;
  if ($ === 8199)
    return 8;
  if ($ === 8209)
    return 8;
  if ($ === 9)
    return 12;
  if ($ >= 8192 && $ <= 8202)
    return 12;
  if ($ >= 768 && $ <= 879)
    return 3;
  if ($ >= 1155 && $ <= 1161)
    return 3;
  if ($ >= 1425 && $ <= 1469)
    return 3;
  if ($ >= 1471 && $ <= 1479)
    return 3;
  if ($ >= 1552 && $ <= 1562)
    return 3;
  if ($ >= 1611 && $ <= 1631)
    return 3;
  if ($ >= 1648 && $ <= 1648)
    return 3;
  if ($ >= 1750 && $ <= 1773)
    return 3;
  if ($ >= 1809 && $ <= 1809)
    return 3;
  if ($ >= 1840 && $ <= 1866)
    return 3;
  if ($ >= 1958 && $ <= 1968)
    return 3;
  if ($ >= 2070 && $ <= 2083)
    return 3;
  if ($ >= 2085 && $ <= 2093)
    return 3;
  if ($ >= 2137 && $ <= 2139)
    return 3;
  if ($ >= 2259 && $ <= 2273)
    return 3;
  if ($ >= 2275 && $ <= 2307)
    return 3;
  if ($ >= 2362 && $ <= 2364)
    return 3;
  if ($ >= 2366 && $ <= 2383)
    return 3;
  if ($ >= 2385 && $ <= 2391)
    return 3;
  if ($ >= 2402 && $ <= 2403)
    return 3;
  if ($ >= 2433 && $ <= 2435)
    return 3;
  if ($ === 2492)
    return 3;
  if ($ >= 2494 && $ <= 2509)
    return 3;
  if ($ >= 2519 && $ <= 2519)
    return 3;
  if ($ >= 2530 && $ <= 2531)
    return 3;
  if ($ >= 2558 && $ <= 2558)
    return 3;
  if ($ >= 2561 && $ <= 2563)
    return 3;
  if ($ >= 2620 && $ <= 2641)
    return 3;
  if ($ >= 2672 && $ <= 2673)
    return 3;
  if ($ >= 2677 && $ <= 2677)
    return 3;
  if ($ >= 2689 && $ <= 2691)
    return 3;
  if ($ >= 2748 && $ <= 2765)
    return 3;
  if ($ >= 2786 && $ <= 2787)
    return 3;
  if ($ >= 2810 && $ <= 2815)
    return 3;
  if ($ >= 2817 && $ <= 2819)
    return 3;
  if ($ >= 2876 && $ <= 2903)
    return 3;
  if ($ >= 2914 && $ <= 2915)
    return 3;
  if ($ >= 2946 && $ <= 2946)
    return 3;
  if ($ >= 3006 && $ <= 3021)
    return 3;
  if ($ >= 3031 && $ <= 3031)
    return 3;
  if ($ >= 3072 && $ <= 3076)
    return 3;
  if ($ >= 3134 && $ <= 3158)
    return 3;
  if ($ >= 3170 && $ <= 3171)
    return 3;
  if ($ >= 3201 && $ <= 3203)
    return 3;
  if ($ >= 3260 && $ <= 3286)
    return 3;
  if ($ >= 3298 && $ <= 3299)
    return 3;
  if ($ >= 3328 && $ <= 3331)
    return 3;
  if ($ >= 3387 && $ <= 3405)
    return 3;
  if ($ >= 3415 && $ <= 3415)
    return 3;
  if ($ >= 3426 && $ <= 3427)
    return 3;
  if ($ >= 3457 && $ <= 3459)
    return 3;
  if ($ >= 3530 && $ <= 3571)
    return 3;
  if ($ >= 3864 && $ <= 3865)
    return 3;
  if ($ >= 3893 && $ <= 3897)
    return 3;
  if ($ >= 3902 && $ <= 3903)
    return 3;
  if ($ >= 3953 && $ <= 3972)
    return 3;
  if ($ >= 3974 && $ <= 3975)
    return 3;
  if ($ >= 3981 && $ <= 4028)
    return 3;
  if ($ === 4038)
    return 3;
  if ($ >= 5906 && $ <= 5908)
    return 3;
  if ($ >= 5938 && $ <= 5940)
    return 3;
  if ($ >= 5970 && $ <= 5971)
    return 3;
  if ($ >= 6002 && $ <= 6003)
    return 3;
  if ($ >= 6068 && $ <= 6099)
    return 3;
  if ($ === 6109)
    return 3;
  if ($ >= 6155 && $ <= 6157)
    return 3;
  if ($ === 6159)
    return 3;
  if ($ >= 6277 && $ <= 6278)
    return 3;
  if ($ === 6313)
    return 3;
  if ($ >= 6432 && $ <= 6459)
    return 3;
  if ($ >= 6679 && $ <= 6683)
    return 3;
  if ($ >= 6741 && $ <= 6783)
    return 3;
  if ($ >= 6832 && $ <= 6862)
    return 3;
  if ($ >= 6912 && $ <= 6916)
    return 3;
  if ($ >= 6964 && $ <= 6980)
    return 3;
  if ($ >= 7019 && $ <= 7027)
    return 3;
  if ($ >= 7040 && $ <= 7042)
    return 3;
  if ($ >= 7073 && $ <= 7085)
    return 3;
  if ($ >= 7142 && $ <= 7155)
    return 3;
  if ($ >= 7204 && $ <= 7223)
    return 3;
  if ($ >= 7376 && $ <= 7417)
    return 3;
  if ($ >= 7616 && $ <= 7679)
    return 3;
  if ($ >= 8400 && $ <= 8432)
    return 3;
  if ($ >= 11503 && $ <= 11505)
    return 3;
  if ($ === 11647)
    return 3;
  if ($ >= 11744 && $ <= 11775)
    return 3;
  if ($ >= 12330 && $ <= 12335)
    return 3;
  if ($ >= 12441 && $ <= 12442)
    return 3;
  if ($ >= 42607 && $ <= 42610)
    return 3;
  if ($ >= 42612 && $ <= 42621)
    return 3;
  if ($ >= 42654 && $ <= 42655)
    return 3;
  if ($ >= 42736 && $ <= 42737)
    return 3;
  if ($ >= 43010 && $ <= 43047)
    return 3;
  if ($ >= 43052 && $ <= 43052)
    return 3;
  if ($ >= 43136 && $ <= 43137)
    return 3;
  if ($ >= 43188 && $ <= 43205)
    return 3;
  if ($ >= 43232 && $ <= 43249)
    return 3;
  if ($ === 43263)
    return 3;
  if ($ >= 43302 && $ <= 43309)
    return 3;
  if ($ >= 43335 && $ <= 43347)
    return 3;
  if ($ >= 43392 && $ <= 43395)
    return 3;
  if ($ >= 43443 && $ <= 43469)
    return 3;
  if ($ === 43493)
    return 3;
  if ($ >= 43561 && $ <= 43574)
    return 3;
  if ($ >= 43587 && $ <= 43587)
    return 3;
  if ($ >= 43596 && $ <= 43597)
    return 3;
  if ($ >= 43643 && $ <= 43645)
    return 3;
  if ($ >= 43696 && $ <= 43714)
    return 3;
  if ($ >= 43755 && $ <= 43759)
    return 3;
  if ($ >= 43765 && $ <= 43766)
    return 3;
  if ($ >= 44003 && $ <= 44010)
    return 3;
  if ($ >= 44012 && $ <= 44013)
    return 3;
  if ($ === 64286)
    return 3;
  if ($ >= 65024 && $ <= 65039)
    return 3;
  if ($ >= 65056 && $ <= 65071)
    return 3;
  if ($ >= 66045 && $ <= 66045)
    return 3;
  if ($ >= 66272 && $ <= 66272)
    return 3;
  if ($ >= 66422 && $ <= 66426)
    return 3;
  if ($ >= 68097 && $ <= 68111)
    return 3;
  if ($ >= 68152 && $ <= 68159)
    return 3;
  if ($ >= 68325 && $ <= 68326)
    return 3;
  if ($ >= 68900 && $ <= 68903)
    return 3;
  if ($ >= 69291 && $ <= 69292)
    return 3;
  if ($ >= 69446 && $ <= 69456)
    return 3;
  if ($ >= 69506 && $ <= 69509)
    return 3;
  if ($ >= 69632 && $ <= 69634)
    return 3;
  if ($ >= 69688 && $ <= 69702)
    return 3;
  if ($ >= 69744 && $ <= 69744)
    return 3;
  if ($ >= 69747 && $ <= 69748)
    return 3;
  if ($ >= 69759 && $ <= 69762)
    return 3;
  if ($ >= 69808 && $ <= 69826)
    return 3;
  if ($ >= 69888 && $ <= 69890)
    return 3;
  if ($ >= 69927 && $ <= 69940)
    return 3;
  if ($ === 69957)
    return 3;
  if ($ === 69958)
    return 3;
  if ($ >= 70003 && $ <= 70003)
    return 3;
  if ($ >= 70016 && $ <= 70018)
    return 3;
  if ($ >= 70067 && $ <= 70080)
    return 3;
  if ($ >= 70089 && $ <= 70092)
    return 3;
  if ($ === 70094)
    return 3;
  if ($ === 70095)
    return 3;
  if ($ >= 70188 && $ <= 70199)
    return 3;
  if ($ === 70206)
    return 3;
  if ($ >= 70367 && $ <= 70378)
    return 3;
  if ($ >= 70400 && $ <= 70403)
    return 3;
  if ($ >= 70459 && $ <= 70460)
    return 3;
  if ($ >= 70462 && $ <= 70477)
    return 3;
  if ($ >= 70487 && $ <= 70487)
    return 3;
  if ($ >= 70498 && $ <= 70516)
    return 3;
  if ($ >= 70709 && $ <= 70726)
    return 3;
  if ($ === 70750)
    return 3;
  if ($ >= 70832 && $ <= 70851)
    return 3;
  if ($ >= 71087 && $ <= 71104)
    return 3;
  if ($ >= 71132 && $ <= 71133)
    return 3;
  if ($ >= 71216 && $ <= 71232)
    return 3;
  if ($ >= 71339 && $ <= 71351)
    return 3;
  if ($ >= 71453 && $ <= 71467)
    return 3;
  if ($ >= 71724 && $ <= 71738)
    return 3;
  if ($ >= 71984 && $ <= 71989)
    return 3;
  if ($ >= 71991 && $ <= 71992)
    return 3;
  if ($ >= 71995 && $ <= 71998)
    return 3;
  if ($ === 72000)
    return 3;
  if ($ >= 72002 && $ <= 72003)
    return 3;
  if ($ >= 72145 && $ <= 72151)
    return 3;
  if ($ >= 72154 && $ <= 72160)
    return 3;
  if ($ === 72164)
    return 3;
  if ($ >= 72193 && $ <= 72202)
    return 3;
  if ($ >= 72243 && $ <= 72249)
    return 3;
  if ($ >= 72251 && $ <= 72254)
    return 3;
  if ($ === 72263)
    return 3;
  if ($ >= 72273 && $ <= 72283)
    return 3;
  if ($ >= 72330 && $ <= 72345)
    return 3;
  if ($ >= 72751 && $ <= 72758)
    return 3;
  if ($ >= 72760 && $ <= 72767)
    return 3;
  if ($ >= 72850 && $ <= 72871)
    return 3;
  if ($ >= 72873 && $ <= 72886)
    return 3;
  if ($ >= 73009 && $ <= 73029)
    return 3;
  if ($ === 73031)
    return 3;
  if ($ >= 73098 && $ <= 73111)
    return 3;
  if ($ >= 73459 && $ <= 73462)
    return 3;
  if ($ >= 92912 && $ <= 92916)
    return 3;
  if ($ >= 92976 && $ <= 92982)
    return 3;
  if ($ === 94031)
    return 3;
  if ($ >= 94033 && $ <= 94087)
    return 3;
  if ($ >= 94095 && $ <= 94098)
    return 3;
  if ($ >= 94180 && $ <= 94180)
    return 3;
  if ($ >= 94192 && $ <= 94193)
    return 3;
  if ($ >= 113821 && $ <= 113822)
    return 3;
  if ($ >= 118528 && $ <= 118598)
    return 3;
  if ($ >= 119141 && $ <= 119145)
    return 3;
  if ($ >= 119149 && $ <= 119154)
    return 3;
  if ($ >= 119163 && $ <= 119170)
    return 3;
  if ($ >= 119173 && $ <= 119179)
    return 3;
  if ($ >= 119210 && $ <= 119213)
    return 3;
  if ($ >= 119362 && $ <= 119364)
    return 3;
  if ($ >= 121344 && $ <= 121398)
    return 3;
  if ($ >= 121403 && $ <= 121452)
    return 3;
  if ($ === 121461)
    return 3;
  if ($ === 121476)
    return 3;
  if ($ >= 121499 && $ <= 121519)
    return 3;
  if ($ >= 122880 && $ <= 122922)
    return 3;
  if ($ >= 123184 && $ <= 123190)
    return 3;
  if ($ >= 123566 && $ <= 123566)
    return 3;
  if ($ >= 123628 && $ <= 123631)
    return 3;
  if ($ >= 125136 && $ <= 125142)
    return 3;
  if ($ >= 125252 && $ <= 125258)
    return 3;
  if ($ >= 917760 && $ <= 917999)
    return 3;
  if ($ === 33)
    return 18;
  if ($ === 63)
    return 18;
  if ($ === 34)
    return 22;
  if ($ === 39)
    return 22;
  if ($ === 40)
    return 21;
  if ($ === 41)
    return 17;
  if ($ === 91)
    return 21;
  if ($ === 93)
    return 17;
  if ($ === 123)
    return 21;
  if ($ === 125)
    return 16;
  if ($ === 44)
    return 23;
  if ($ === 46)
    return 23;
  if ($ === 58)
    return 23;
  if ($ === 59)
    return 23;
  if ($ === 45)
    return 14;
  if ($ === 8208)
    return 12;
  if ($ === 8211)
    return 12;
  if ($ === 8212)
    return 11;
  if ($ === 8216 || $ === 8217)
    return 22;
  if ($ === 8220 || $ === 8221)
    return 22;
  if ($ === 8230)
    return 19;
  if ($ === 12289 || $ === 12290)
    return 16;
  if ($ === 12296)
    return 21;
  if ($ === 12297)
    return 16;
  if ($ === 12298)
    return 21;
  if ($ === 12299)
    return 16;
  if ($ === 12300)
    return 21;
  if ($ === 12301)
    return 16;
  if ($ === 12302)
    return 21;
  if ($ === 12303)
    return 16;
  if ($ === 12304)
    return 21;
  if ($ === 12305)
    return 16;
  if ($ === 12308)
    return 21;
  if ($ === 12309)
    return 16;
  if ($ === 12310)
    return 21;
  if ($ === 12311)
    return 16;
  if ($ >= 12312 && $ <= 12315)
    return $ % 2 === 0 ? 21 : 16;
  if ($ === 65288)
    return 21;
  if ($ === 65289)
    return 16;
  if ($ === 65292)
    return 16;
  if ($ === 65294)
    return 16;
  if ($ === 65306)
    return 20;
  if ($ === 65307)
    return 20;
  if ($ === 65311)
    return 18;
  if ($ === 65281)
    return 18;
  if ($ >= 12353 && $ <= 12438) {
    if ($ === 12353 || $ === 12355 || $ === 12357 || $ === 12359 || $ === 12361 || $ === 12387 || $ === 12419 || $ === 12421 || $ === 12423 || $ === 12430 || $ === 12437 || $ === 12438)
      return 30;
    return 36;
  }
  if ($ >= 12449 && $ <= 12538) {
    if ($ === 12449 || $ === 12451 || $ === 12453 || $ === 12455 || $ === 12457 || $ === 12483 || $ === 12515 || $ === 12517 || $ === 12519 || $ === 12526 || $ === 12533 || $ === 12534)
      return 30;
    return 36;
  }
  if ($ === 12540)
    return 30;
  if ($ >= 4352 && $ <= 4447)
    return 37;
  if ($ >= 43360 && $ <= 43388)
    return 37;
  if ($ >= 4448 && $ <= 4519)
    return 38;
  if ($ >= 55216 && $ <= 55238)
    return 38;
  if ($ >= 4520 && $ <= 4607)
    return 39;
  if ($ >= 55243 && $ <= 55291)
    return 39;
  if ($ >= 44032 && $ <= 55203) {
    if (($ - 44032) % 28 === 0)
      return 33;
    return 34;
  }
  if ($ >= 48 && $ <= 57)
    return 24;
  if ($ >= 1632 && $ <= 1641)
    return 24;
  if ($ >= 1776 && $ <= 1785)
    return 24;
  if ($ >= 2406 && $ <= 2415)
    return 24;
  if ($ >= 65296 && $ <= 65305)
    return 24;
  if ($ === 36)
    return 26;
  if ($ === 163)
    return 26;
  if ($ === 165)
    return 26;
  if ($ === 8364)
    return 26;
  if ($ === 37)
    return 25;
  if ($ >= 1488 && $ <= 1514)
    return 35;
  if ($ >= 64285 && $ <= 64335)
    return 35;
  if ($ >= 19968 && $ <= 40959)
    return 36;
  if ($ >= 13312 && $ <= 19903)
    return 36;
  if ($ >= 131072 && $ <= 173791)
    return 36;
  if ($ >= 173824 && $ <= 177983)
    return 36;
  if ($ >= 177984 && $ <= 178207)
    return 36;
  if ($ >= 178208 && $ <= 183983)
    return 36;
  if ($ >= 183984 && $ <= 191471)
    return 36;
  if ($ >= 196608 && $ <= 201551)
    return 36;
  if ($ >= 63744 && $ <= 64255)
    return 36;
  if ($ >= 194560 && $ <= 195103)
    return 36;
  if ($ >= 127744 && $ <= 129535)
    return 36;
  if ($ >= 129536 && $ <= 129791)
    return 36;
  if ($ >= 9728 && $ <= 9983)
    return 36;
  if ($ >= 9984 && $ <= 10175)
    return 36;
  if ($ >= 127456 && $ <= 127487)
    return 40;
  if ($ >= 127995 && $ <= 127999)
    return 32;
  if ($ >= 3584 && $ <= 3711)
    return 41;
  if ($ >= 3712 && $ <= 3839)
    return 41;
  if ($ >= 4096 && $ <= 4255)
    return 41;
  if ($ >= 43488 && $ <= 43519)
    return 41;
  if ($ >= 43616 && $ <= 43647)
    return 41;
  if ($ >= 6016 && $ <= 6143)
    return 41;
  if ($ >= 6624 && $ <= 6655)
    return 41;
  if ($ >= 65 && $ <= 90)
    return 29;
  if ($ >= 97 && $ <= 122)
    return 29;
  if ($ >= 192 && $ <= 591)
    return 29;
  if ($ >= 1536 && $ <= 1791)
    return 29;
  if ($ >= 1872 && $ <= 1919)
    return 29;
  if ($ >= 2208 && $ <= 2303)
    return 29;
  if ($ >= 2304 && $ <= 2431)
    return 29;
  if ($ >= 2432 && $ <= 2559)
    return 29;
  if ($ >= 2560 && $ <= 2687)
    return 29;
  if ($ >= 2688 && $ <= 2815)
    return 29;
  if ($ >= 2816 && $ <= 2943)
    return 29;
  if ($ >= 2944 && $ <= 3071)
    return 29;
  if ($ >= 3072 && $ <= 3199)
    return 29;
  if ($ >= 3200 && $ <= 3327)
    return 29;
  if ($ >= 3328 && $ <= 3455)
    return 29;
  if ($ >= 1024 && $ <= 1279)
    return 29;
  if ($ >= 1280 && $ <= 1327)
    return 29;
  if ($ >= 880 && $ <= 1023)
    return 29;
  return 42;
}
function yG($, Q) {
  if ($ === 28)
    $ = 29;
  if ($ === 41)
    $ = 29;
  if ($ === 5)
    $ = 29;
  if ($ === 42)
    $ = 29;
  if ($ === 30)
    $ = 20;
  if (Q === 28)
    Q = 29;
  if (Q === 41)
    Q = 29;
  if (Q === 5)
    Q = 29;
  if (Q === 42)
    Q = 29;
  if (Q === 30)
    Q = 20;
  if ($ === 0)
    return 5;
  if ($ === 1 && Q === 2)
    return 4;
  if ($ === 1 || $ === 2 || $ === 4)
    return 5;
  if (Q === 0 || Q === 1 || Q === 2 || Q === 4)
    return 4;
  if (Q === 9 || Q === 7)
    return 4;
  if ($ === 7)
    return 0;
  if ($ === 10)
    return 4;
  if (Q === 3 || Q === 10) {
    if ($ !== 9)
      return 4;
  }
  let Z = $, K = Q;
  if ($ === 3 || $ === 10)
    Z = 29;
  if (Q === 3 || Q === 10)
    K = 29;
  if ($ = Z, Q = K, $ === 6 || Q === 6)
    return 4;
  if ($ === 8)
    return 4;
  if (Q === 8) {
    if ($ !== 9 && $ !== 12 && $ !== 14)
      return 4;
  }
  if (Q === 16 || Q === 17 || Q === 18 || Q === 23 || Q === 27)
    return 4;
  if ($ === 21)
    return 4;
  if ($ === 22 && Q === 21)
    return 4;
  if (($ === 16 || $ === 17) && Q === 20)
    return 4;
  if ($ === 11 && Q === 11)
    return 4;
  if ($ === 9)
    return 0;
  if ($ === 22 || Q === 22)
    return 4;
  if ($ === 15 || Q === 15)
    return 0;
  if (Q === 12 || Q === 14 || Q === 20)
    return 4;
  if ($ === 13)
    return 4;
  if ($ === 27 && Q === 35)
    return 4;
  if (Q === 19)
    return 4;
  if (($ === 29 || $ === 35) && Q === 24)
    return 4;
  if ($ === 24 && (Q === 29 || Q === 35))
    return 4;
  if ($ === 26 && Q === 36)
    return 4;
  if ($ === 36 && Q === 25)
    return 4;
  if (($ === 26 || $ === 25) && (Q === 29 || Q === 35))
    return 4;
  if (($ === 29 || $ === 35) && (Q === 26 || Q === 25))
    return 4;
  if (($ === 16 || $ === 17) && Q === 24)
    return 4;
  if ($ === 24 && (Q === 25 || Q === 26))
    return 4;
  if (($ === 25 || $ === 26 || $ === 14 || $ === 23 || $ === 24 || $ === 27) && Q === 24)
    return 4;
  if ($ === 37) {
    if (Q === 37 || Q === 38 || Q === 33 || Q === 34)
      return 4;
  }
  if ($ === 38 || $ === 33) {
    if (Q === 38 || Q === 39)
      return 4;
  }
  if ($ === 39 || $ === 34) {
    if (Q === 39)
      return 4;
  }
  if ($ === 37 || $ === 38 || $ === 39 || $ === 33 || $ === 34) {
    if (Q === 25)
      return 4;
  }
  if (Q === 37 || Q === 38 || Q === 39 || Q === 33 || Q === 34) {
    if ($ === 26)
      return 4;
  }
  if (($ === 29 || $ === 35) && (Q === 29 || Q === 35))
    return 4;
  if ($ === 23 && (Q === 29 || Q === 35))
    return 4;
  if (($ === 29 || $ === 35 || $ === 24) && Q === 21)
    return 4;
  if ($ === 17 && (Q === 29 || Q === 35 || Q === 24))
    return 4;
  if ($ === 40 && Q === 40)
    return 4;
  if ($ === 31 && Q === 32)
    return 4;
  return 0;
}
function bG($) {
  let Q = [], Z = [...$];
  for (let K = 0;K < Z.length; K++) {
    let q = Z[K];
    Q.push(q.codePointAt(0) ?? 0);
  }
  return BQ(Q);
}
function BQ($) {
  let Q = $.length, Z = [], K = [];
  for (let q = 0;q < $.length; q++) {
    let J = $[q];
    Z.push(GH(J));
  }
  K.push(0);
  for (let q = 1;q < Q; q++) {
    let J = Z[q - 1], U = Z[q];
    switch (yG(J, U)) {
      case 5:
        K.push(2);
        break;
      case 0:
        K.push(1);
        break;
      default:
        K.push(0);
    }
  }
  return K.push(2), { breaks: K, classes: Z };
}
function mG($) {
  let Q = $.map((Z) => Z.codepoint);
  return BQ(Q);
}
function gG($, Q) {
  for (let Z = Q + 1;Z < $.breaks.length; Z++)
    if ($.breaks[Z] !== 0)
      return Z;
  return $.breaks.length - 1;
}
function uG($, Q) {
  if (Q < 0 || Q >= $.breaks.length)
    return false;
  return $.breaks[Q] !== 0;
}
function xG($, Q) {
  if (Q < 0 || Q >= $.breaks.length)
    return false;
  return $.breaks[Q] === 2;
}
function fG($) {
  let Q = [];
  for (let Z = 0;Z < $.breaks.length; Z++)
    if ($.breaks[Z] !== 0)
      Q.push(Z);
  return Q;
}
var jH;
((I) => {
  I.Common = "Zyyy";
  I.Inherited = "Zinh";
  I.Unknown = "Zzzz";
  I.Latin = "Latn";
  I.Greek = "Grek";
  I.Cyrillic = "Cyrl";
  I.Armenian = "Armn";
  I.Hebrew = "Hebr";
  I.Arabic = "Arab";
  I.Syriac = "Syrc";
  I.Thaana = "Thaa";
  I.Devanagari = "Deva";
  I.Bengali = "Beng";
  I.Gurmukhi = "Guru";
  I.Gujarati = "Gujr";
  I.Oriya = "Orya";
  I.Tamil = "Taml";
  I.Telugu = "Telu";
  I.Kannada = "Knda";
  I.Malayalam = "Mlym";
  I.Sinhala = "Sinh";
  I.Thai = "Thai";
  I.Lao = "Laoo";
  I.Tibetan = "Tibt";
  I.Myanmar = "Mymr";
  I.Georgian = "Geor";
  I.Hangul = "Hang";
  I.Ethiopic = "Ethi";
  I.Cherokee = "Cher";
  I.CanadianAboriginal = "Cans";
  I.Ogham = "Ogam";
  I.Runic = "Runr";
  I.Khmer = "Khmr";
  I.Mongolian = "Mong";
  I.Hiragana = "Hira";
  I.Katakana = "Kana";
  I.Bopomofo = "Bopo";
  I.Han = "Hani";
  I.Yi = "Yiii";
  I.OldItalic = "Ital";
  I.Gothic = "Goth";
  I.Deseret = "Dsrt";
  I.Tagalog = "Tglg";
  I.Hanunoo = "Hano";
  I.Buhid = "Buhd";
  I.Tagbanwa = "Tagb";
  I.Limbu = "Limb";
  I.TaiLe = "Tale";
  I.LinearB = "Linb";
  I.Ugaritic = "Ugar";
  I.Shavian = "Shaw";
  I.Osmanya = "Osma";
  I.Cypriot = "Cprt";
  I.Braille = "Brai";
  I.Buginese = "Bugi";
  I.Coptic = "Copt";
  I.NewTaiLue = "Talu";
  I.Glagolitic = "Glag";
  I.Tifinagh = "Tfng";
  I.SylotiNagri = "Sylo";
  I.OldPersian = "Xpeo";
  I.Kharoshthi = "Khar";
  I.Balinese = "Bali";
  I.Cuneiform = "Xsux";
  I.Phoenician = "Phnx";
  I.PhagsPa = "Phag";
  I.Nko = "Nkoo";
  I.Sundanese = "Sund";
  I.Lepcha = "Lepc";
  I.OlChiki = "Olck";
  I.Vai = "Vaii";
  I.Saurashtra = "Saur";
  I.KayahLi = "Kali";
  I.Rejang = "Rjng";
  I.Lycian = "Lyci";
  I.Carian = "Cari";
  I.Lydian = "Lydi";
  I.Cham = "Cham";
  I.TaiTham = "Lana";
  I.TaiViet = "Tavt";
  I.Avestan = "Avst";
  I.EgyptianHieroglyphs = "Egyp";
  I.Samaritan = "Samr";
  I.Lisu = "Lisu";
  I.Bamum = "Bamu";
  I.Javanese = "Java";
  I.MeeteiMayek = "Mtei";
  I.ImperialAramaic = "Armi";
  I.OldSouthArabian = "Sarb";
  I.InscriptionalParthian = "Prti";
  I.InscriptionalPahlavi = "Phli";
  I.OldTurkic = "Orkh";
  I.Kaithi = "Kthi";
  I.Batak = "Batk";
  I.Brahmi = "Brah";
  I.Mandaic = "Mand";
  I.Chakma = "Cakm";
  I.MeroiticCursive = "Merc";
  I.MeroiticHieroglyphs = "Mero";
  I.Miao = "Plrd";
  I.Sharada = "Shrd";
  I.SoraSompeng = "Sora";
  I.Takri = "Takr";
  I.CaucasianAlbanian = "Aghb";
  I.BassaVah = "Bass";
  I.Duployan = "Dupl";
  I.Elbasan = "Elba";
  I.Grantha = "Gran";
  I.PahawhHmong = "Hmng";
  I.Khojki = "Khoj";
  I.LinearA = "Lina";
  I.Mahajani = "Mahj";
  I.Manichaean = "Mani";
  I.MendeKikakui = "Mend";
  I.Modi = "Modi";
  I.Mro = "Mroo";
  I.OldNorthArabian = "Narb";
  I.Nabataean = "Nbat";
  I.Palmyrene = "Palm";
  I.PauCinHau = "Pauc";
  I.OldPermic = "Perm";
  I.PsalterPahlavi = "Phlp";
  I.Siddham = "Sidd";
  I.Khudawadi = "Sind";
  I.Tirhuta = "Tirh";
  I.WarangCiti = "Wara";
  I.Ahom = "Ahom";
  I.AnatolianHieroglyphs = "Hluw";
  I.Hatran = "Hatr";
  I.Multani = "Mult";
  I.OldHungarian = "Hung";
  I.SignWriting = "Sgnw";
  I.Adlam = "Adlm";
  I.Bhaiksuki = "Bhks";
  I.Marchen = "Marc";
  I.Newa = "Newa";
  I.Osage = "Osge";
  I.Tangut = "Tang";
  I.MasaramGondi = "Gonm";
  I.Nushu = "Nshu";
  I.Soyombo = "Soyo";
  I.ZanabazarSquare = "Zanb";
  I.Dogra = "Dogr";
  I.GunjalaGondi = "Gong";
  I.Makasar = "Maka";
  I.Medefaidrin = "Medf";
  I.HanifiRohingya = "Rohg";
  I.Sogdian = "Sogd";
  I.OldSogdian = "Sogo";
  I.Elymaic = "Elym";
  I.Nandinagari = "Nand";
  I.NyiakengPuachueHmong = "Hmnp";
  I.Wancho = "Wcho";
  I.Yezidi = "Yezi";
  I.Chorasmian = "Chrs";
  I.DivesAkuru = "Diak";
  I.KhitanSmallScript = "Kits";
  I.Vithkuqi = "Vith";
  I.OldUyghur = "Ougr";
  I.Cypro_Minoan = "Cpmn";
  I.Tangsa = "Tnsa";
  I.Toto = "Toto";
  I.Kawi = "Kawi";
  I.NagMundari = "Nagm";
})(jH ||= {});
var YH = [{ start: 0, end: 127, script: "Zyyy" }, { start: 128, end: 255, script: "Latn" }, { start: 256, end: 383, script: "Latn" }, { start: 384, end: 591, script: "Latn" }, { start: 592, end: 687, script: "Latn" }, { start: 688, end: 767, script: "Zyyy" }, { start: 768, end: 879, script: "Zinh" }, { start: 880, end: 1023, script: "Grek" }, { start: 1024, end: 1279, script: "Cyrl" }, { start: 1280, end: 1327, script: "Cyrl" }, { start: 1328, end: 1423, script: "Armn" }, { start: 1424, end: 1535, script: "Hebr" }, { start: 1536, end: 1791, script: "Arab" }, { start: 1792, end: 1871, script: "Syrc" }, { start: 1872, end: 1919, script: "Arab" }, { start: 1920, end: 1983, script: "Thaa" }, { start: 1984, end: 2047, script: "Nkoo" }, { start: 2048, end: 2111, script: "Samr" }, { start: 2112, end: 2143, script: "Mand" }, { start: 2144, end: 2159, script: "Syrc" }, { start: 2160, end: 2207, script: "Arab" }, { start: 2208, end: 2303, script: "Arab" }, { start: 2304, end: 2431, script: "Deva" }, { start: 2432, end: 2559, script: "Beng" }, { start: 2560, end: 2687, script: "Guru" }, { start: 2688, end: 2815, script: "Gujr" }, { start: 2816, end: 2943, script: "Orya" }, { start: 2944, end: 3071, script: "Taml" }, { start: 3072, end: 3199, script: "Telu" }, { start: 3200, end: 3327, script: "Knda" }, { start: 3328, end: 3455, script: "Mlym" }, { start: 3456, end: 3583, script: "Sinh" }, { start: 3584, end: 3711, script: "Thai" }, { start: 3712, end: 3839, script: "Laoo" }, { start: 3840, end: 4095, script: "Tibt" }, { start: 4096, end: 4255, script: "Mymr" }, { start: 4256, end: 4351, script: "Geor" }, { start: 4352, end: 4607, script: "Hang" }, { start: 4608, end: 4991, script: "Ethi" }, { start: 4992, end: 5023, script: "Ethi" }, { start: 5024, end: 5119, script: "Cher" }, { start: 5120, end: 5759, script: "Cans" }, { start: 5760, end: 5791, script: "Ogam" }, { start: 5792, end: 5887, script: "Runr" }, { start: 5888, end: 5919, script: "Tglg" }, { start: 5920, end: 5951, script: "Hano" }, { start: 5952, end: 5983, script: "Buhd" }, { start: 5984, end: 6015, script: "Tagb" }, { start: 6016, end: 6143, script: "Khmr" }, { start: 6144, end: 6319, script: "Mong" }, { start: 6320, end: 6399, script: "Cans" }, { start: 6400, end: 6479, script: "Limb" }, { start: 6480, end: 6527, script: "Tale" }, { start: 6528, end: 6623, script: "Talu" }, { start: 6624, end: 6655, script: "Khmr" }, { start: 6656, end: 6687, script: "Bugi" }, { start: 6688, end: 6831, script: "Lana" }, { start: 6832, end: 6911, script: "Zinh" }, { start: 6912, end: 7039, script: "Bali" }, { start: 7040, end: 7103, script: "Sund" }, { start: 7104, end: 7167, script: "Batk" }, { start: 7168, end: 7247, script: "Lepc" }, { start: 7248, end: 7295, script: "Olck" }, { start: 7296, end: 7311, script: "Cyrl" }, { start: 7312, end: 7359, script: "Geor" }, { start: 7360, end: 7375, script: "Sund" }, { start: 7376, end: 7423, script: "Zinh" }, { start: 7424, end: 7551, script: "Latn" }, { start: 7552, end: 7615, script: "Latn" }, { start: 7616, end: 7679, script: "Zinh" }, { start: 7680, end: 7935, script: "Latn" }, { start: 7936, end: 8191, script: "Grek" }, { start: 8192, end: 8303, script: "Zyyy" }, { start: 8304, end: 8351, script: "Zyyy" }, { start: 8352, end: 8399, script: "Zyyy" }, { start: 8400, end: 8447, script: "Zinh" }, { start: 8448, end: 8527, script: "Zyyy" }, { start: 8528, end: 8591, script: "Zyyy" }, { start: 8592, end: 8703, script: "Zyyy" }, { start: 8704, end: 8959, script: "Zyyy" }, { start: 8960, end: 9215, script: "Zyyy" }, { start: 9216, end: 9279, script: "Zyyy" }, { start: 9280, end: 9311, script: "Zyyy" }, { start: 9312, end: 9471, script: "Zyyy" }, { start: 9472, end: 9599, script: "Zyyy" }, { start: 9600, end: 9631, script: "Zyyy" }, { start: 9632, end: 9727, script: "Zyyy" }, { start: 9728, end: 9983, script: "Zyyy" }, { start: 9984, end: 10175, script: "Zyyy" }, { start: 10176, end: 10223, script: "Zyyy" }, { start: 10224, end: 10239, script: "Zyyy" }, { start: 10240, end: 10495, script: "Brai" }, { start: 10496, end: 10623, script: "Zyyy" }, { start: 10624, end: 10751, script: "Zyyy" }, { start: 10752, end: 11007, script: "Zyyy" }, { start: 11008, end: 11263, script: "Zyyy" }, { start: 11264, end: 11359, script: "Glag" }, { start: 11360, end: 11391, script: "Latn" }, { start: 11392, end: 11519, script: "Copt" }, { start: 11520, end: 11567, script: "Geor" }, { start: 11568, end: 11647, script: "Tfng" }, { start: 11648, end: 11743, script: "Ethi" }, { start: 11744, end: 11775, script: "Cyrl" }, { start: 11776, end: 11903, script: "Zyyy" }, { start: 11904, end: 12031, script: "Hani" }, { start: 12032, end: 12255, script: "Hani" }, { start: 12272, end: 12287, script: "Zyyy" }, { start: 12288, end: 12351, script: "Zyyy" }, { start: 12352, end: 12447, script: "Hira" }, { start: 12448, end: 12543, script: "Kana" }, { start: 12544, end: 12591, script: "Bopo" }, { start: 12592, end: 12687, script: "Hang" }, { start: 12688, end: 12703, script: "Zyyy" }, { start: 12704, end: 12735, script: "Bopo" }, { start: 12736, end: 12783, script: "Zyyy" }, { start: 12784, end: 12799, script: "Kana" }, { start: 12800, end: 13055, script: "Zyyy" }, { start: 13056, end: 13311, script: "Zyyy" }, { start: 13312, end: 19903, script: "Hani" }, { start: 19904, end: 19967, script: "Zyyy" }, { start: 19968, end: 40959, script: "Hani" }, { start: 40960, end: 42127, script: "Yiii" }, { start: 42128, end: 42191, script: "Yiii" }, { start: 42192, end: 42239, script: "Lisu" }, { start: 42240, end: 42559, script: "Vaii" }, { start: 42560, end: 42655, script: "Cyrl" }, { start: 42656, end: 42751, script: "Bamu" }, { start: 42752, end: 42783, script: "Zyyy" }, { start: 42784, end: 43007, script: "Latn" }, { start: 43008, end: 43055, script: "Sylo" }, { start: 43056, end: 43071, script: "Zyyy" }, { start: 43072, end: 43135, script: "Phag" }, { start: 43136, end: 43231, script: "Saur" }, { start: 43232, end: 43263, script: "Deva" }, { start: 43264, end: 43311, script: "Kali" }, { start: 43312, end: 43359, script: "Rjng" }, { start: 43360, end: 43391, script: "Hang" }, { start: 43392, end: 43487, script: "Java" }, { start: 43488, end: 43519, script: "Mymr" }, { start: 43520, end: 43615, script: "Cham" }, { start: 43616, end: 43647, script: "Mymr" }, { start: 43648, end: 43743, script: "Tavt" }, { start: 43744, end: 43775, script: "Mtei" }, { start: 43776, end: 43823, script: "Ethi" }, { start: 43824, end: 43887, script: "Latn" }, { start: 43888, end: 43967, script: "Cher" }, { start: 43968, end: 44031, script: "Mtei" }, { start: 44032, end: 55215, script: "Hang" }, { start: 55216, end: 55295, script: "Hang" }, { start: 55296, end: 57343, script: "Zzzz" }, { start: 57344, end: 63743, script: "Zzzz" }, { start: 63744, end: 64255, script: "Hani" }, { start: 64256, end: 64335, script: "Latn" }, { start: 64336, end: 65023, script: "Arab" }, { start: 65024, end: 65039, script: "Zinh" }, { start: 65040, end: 65055, script: "Zyyy" }, { start: 65056, end: 65071, script: "Zinh" }, { start: 65072, end: 65103, script: "Zyyy" }, { start: 65104, end: 65135, script: "Zyyy" }, { start: 65136, end: 65279, script: "Arab" }, { start: 65280, end: 65519, script: "Zyyy" }, { start: 65520, end: 65535, script: "Zyyy" }, { start: 65536, end: 65663, script: "Linb" }, { start: 65664, end: 65791, script: "Linb" }, { start: 65792, end: 65855, script: "Zyyy" }, { start: 65856, end: 65935, script: "Grek" }, { start: 65936, end: 65999, script: "Zyyy" }, { start: 66000, end: 66047, script: "Zyyy" }, { start: 66176, end: 66207, script: "Lyci" }, { start: 66208, end: 66271, script: "Cari" }, { start: 66272, end: 66303, script: "Zinh" }, { start: 66304, end: 66351, script: "Ital" }, { start: 66352, end: 66383, script: "Goth" }, { start: 66384, end: 66431, script: "Perm" }, { start: 66432, end: 66463, script: "Ugar" }, { start: 66464, end: 66527, script: "Xpeo" }, { start: 66560, end: 66639, script: "Dsrt" }, { start: 66640, end: 66687, script: "Shaw" }, { start: 66688, end: 66735, script: "Osma" }, { start: 66736, end: 66815, script: "Osge" }, { start: 66816, end: 66863, script: "Elba" }, { start: 66864, end: 66927, script: "Aghb" }, { start: 66928, end: 67007, script: "Vith" }, { start: 67072, end: 67455, script: "Lina" }, { start: 67456, end: 67519, script: "Latn" }, { start: 67584, end: 67647, script: "Cprt" }, { start: 67648, end: 67679, script: "Armi" }, { start: 67680, end: 67711, script: "Palm" }, { start: 67712, end: 67759, script: "Nbat" }, { start: 67808, end: 67839, script: "Hatr" }, { start: 67840, end: 67871, script: "Phnx" }, { start: 67872, end: 67903, script: "Lydi" }, { start: 67968, end: 67999, script: "Mero" }, { start: 68000, end: 68095, script: "Merc" }, { start: 68096, end: 68191, script: "Khar" }, { start: 68192, end: 68223, script: "Sarb" }, { start: 68224, end: 68255, script: "Narb" }, { start: 68288, end: 68351, script: "Mani" }, { start: 68352, end: 68415, script: "Avst" }, { start: 68416, end: 68447, script: "Prti" }, { start: 68448, end: 68479, script: "Phli" }, { start: 68480, end: 68527, script: "Phlp" }, { start: 68608, end: 68687, script: "Orkh" }, { start: 68736, end: 68863, script: "Hung" }, { start: 68864, end: 68927, script: "Rohg" }, { start: 69248, end: 69311, script: "Yezi" }, { start: 69376, end: 69423, script: "Sogo" }, { start: 69424, end: 69487, script: "Sogd" }, { start: 69488, end: 69551, script: "Ougr" }, { start: 69552, end: 69599, script: "Chrs" }, { start: 69600, end: 69631, script: "Elym" }, { start: 69632, end: 69759, script: "Brah" }, { start: 69760, end: 69839, script: "Kthi" }, { start: 69840, end: 69887, script: "Sora" }, { start: 69888, end: 69967, script: "Cakm" }, { start: 69968, end: 70015, script: "Mahj" }, { start: 70016, end: 70111, script: "Shrd" }, { start: 70112, end: 70143, script: "Sinh" }, { start: 70144, end: 70223, script: "Khoj" }, { start: 70272, end: 70319, script: "Mult" }, { start: 70320, end: 70399, script: "Sind" }, { start: 70400, end: 70527, script: "Gran" }, { start: 70656, end: 70783, script: "Newa" }, { start: 70784, end: 70879, script: "Tirh" }, { start: 71040, end: 71167, script: "Sidd" }, { start: 71168, end: 71263, script: "Modi" }, { start: 71264, end: 71295, script: "Mong" }, { start: 71296, end: 71375, script: "Takr" }, { start: 71424, end: 71503, script: "Ahom" }, { start: 71680, end: 71759, script: "Dogr" }, { start: 71840, end: 71935, script: "Wara" }, { start: 71936, end: 72031, script: "Diak" }, { start: 72096, end: 72191, script: "Nand" }, { start: 72192, end: 72271, script: "Zanb" }, { start: 72272, end: 72367, script: "Soyo" }, { start: 72368, end: 72383, script: "Cans" }, { start: 72384, end: 72447, script: "Pauc" }, { start: 72704, end: 72815, script: "Bhks" }, { start: 72816, end: 72895, script: "Marc" }, { start: 72960, end: 73055, script: "Gonm" }, { start: 73056, end: 73135, script: "Gong" }, { start: 73440, end: 73471, script: "Maka" }, { start: 73472, end: 73567, script: "Kawi" }, { start: 73728, end: 74751, script: "Xsux" }, { start: 74752, end: 74879, script: "Xsux" }, { start: 74880, end: 75087, script: "Xsux" }, { start: 77712, end: 77823, script: "Cpmn" }, { start: 77824, end: 78895, script: "Egyp" }, { start: 78896, end: 78943, script: "Egyp" }, { start: 82944, end: 83583, script: "Hluw" }, { start: 92160, end: 92735, script: "Bamu" }, { start: 92736, end: 92783, script: "Mroo" }, { start: 92784, end: 92879, script: "Tnsa" }, { start: 92880, end: 92927, script: "Bass" }, { start: 92928, end: 93071, script: "Hmng" }, { start: 93760, end: 93855, script: "Medf" }, { start: 93952, end: 94111, script: "Plrd" }, { start: 94176, end: 94207, script: "Zyyy" }, { start: 94208, end: 100351, script: "Tang" }, { start: 100352, end: 101119, script: "Tang" }, { start: 101120, end: 101631, script: "Kits" }, { start: 101632, end: 101759, script: "Tang" }, { start: 110576, end: 110591, script: "Kana" }, { start: 110592, end: 110847, script: "Hira" }, { start: 110848, end: 110895, script: "Hira" }, { start: 110896, end: 110959, script: "Kana" }, { start: 110960, end: 111359, script: "Nshu" }, { start: 113664, end: 113823, script: "Dupl" }, { start: 113824, end: 113839, script: "Zyyy" }, { start: 118528, end: 118735, script: "Zyyy" }, { start: 118784, end: 119039, script: "Zyyy" }, { start: 119040, end: 119295, script: "Zyyy" }, { start: 119296, end: 119375, script: "Grek" }, { start: 119488, end: 119519, script: "Zyyy" }, { start: 119520, end: 119551, script: "Zyyy" }, { start: 119552, end: 119647, script: "Zyyy" }, { start: 119648, end: 119679, script: "Zyyy" }, { start: 119808, end: 120831, script: "Zyyy" }, { start: 120832, end: 121519, script: "Sgnw" }, { start: 122624, end: 122879, script: "Latn" }, { start: 122880, end: 122927, script: "Glag" }, { start: 122928, end: 123023, script: "Cyrl" }, { start: 123136, end: 123215, script: "Hmnp" }, { start: 123536, end: 123583, script: "Toto" }, { start: 123584, end: 123647, script: "Wcho" }, { start: 124112, end: 124159, script: "Nagm" }, { start: 124896, end: 124927, script: "Ethi" }, { start: 124928, end: 125151, script: "Mend" }, { start: 125184, end: 125279, script: "Adlm" }, { start: 126064, end: 126143, script: "Zyyy" }, { start: 126208, end: 126287, script: "Zyyy" }, { start: 126464, end: 126719, script: "Arab" }, { start: 126976, end: 127023, script: "Zyyy" }, { start: 127024, end: 127135, script: "Zyyy" }, { start: 127136, end: 127231, script: "Zyyy" }, { start: 127232, end: 127487, script: "Zyyy" }, { start: 127488, end: 127743, script: "Zyyy" }, { start: 127744, end: 128511, script: "Zyyy" }, { start: 128512, end: 128591, script: "Zyyy" }, { start: 128592, end: 128639, script: "Zyyy" }, { start: 128640, end: 128767, script: "Zyyy" }, { start: 128768, end: 128895, script: "Zyyy" }, { start: 128896, end: 129023, script: "Zyyy" }, { start: 129024, end: 129279, script: "Zyyy" }, { start: 129280, end: 129535, script: "Zyyy" }, { start: 129536, end: 129647, script: "Zyyy" }, { start: 129648, end: 129791, script: "Zyyy" }, { start: 129792, end: 130047, script: "Zyyy" }, { start: 131072, end: 173791, script: "Hani" }, { start: 173824, end: 177983, script: "Hani" }, { start: 177984, end: 178207, script: "Hani" }, { start: 178208, end: 183983, script: "Hani" }, { start: 183984, end: 191471, script: "Hani" }, { start: 194560, end: 195103, script: "Hani" }, { start: 196608, end: 201551, script: "Hani" }, { start: 201552, end: 205743, script: "Hani" }, { start: 917504, end: 917631, script: "Zyyy" }, { start: 917760, end: 917999, script: "Zinh" }, { start: 983040, end: 1048575, script: "Zzzz" }, { start: 1048576, end: 1114111, script: "Zzzz" }];
function L0($) {
  let Q = 0, Z = YH.length - 1;
  while (Q <= Z) {
    let K = Q + Z >>> 1, q = YH[K];
    if ($ < q.start)
      Z = K - 1;
    else if ($ > q.end)
      Q = K + 1;
    else
      return q.script;
  }
  return "Zzzz";
}
function lG($) {
  let Q = new Map, Z = [...$];
  for (let U = 0;U < Z.length; U++) {
    let H = Z[U].codePointAt(0) ?? 0, _ = L0(H);
    if (_ === "Zyyy" || _ === "Zinh")
      continue;
    Q.set(_, (Q.get(_) ?? 0) + 1);
  }
  if (Q.size === 0)
    return "Zyyy";
  let K = "Zyyy", q = 0, J = [...Q];
  for (let U = 0;U < J.length; U++) {
    let [W, H] = J[U];
    if (H > q)
      q = H, K = W;
  }
  return K;
}
function dG($) {
  let Q = new Set, Z = [...$];
  for (let K = 0;K < Z.length; K++) {
    let J = Z[K].codePointAt(0) ?? 0, U = L0(J);
    Q.add(U);
  }
  return [...Q];
}
function nG($, Q) {
  let Z = [...$];
  for (let K = 0;K < Z.length; K++) {
    let J = Z[K].codePointAt(0) ?? 0, U = L0(J);
    if (U !== Q && U !== "Zyyy" && U !== "Zinh")
      return false;
  }
  return true;
}
function pG($) {
  let Q = [];
  if ($.length === 0)
    return Q;
  let Z = null, K = 0, q = 0, J = [...$];
  for (let U = 0;U < J.length; U++) {
    let H = J[U].codePointAt(0) ?? 0, _ = L0(H);
    if (_ === "Zyyy" || _ === "Zinh") {
      if (Z !== null)
        _ = Z;
    }
    if (Z === null)
      Z = _, K = U;
    else if (_ !== Z && _ !== "Zyyy" && _ !== "Zinh")
      Q.push({ script: Z, start: K, end: U, text: J.slice(K, U).join("") }), Z = _, K = U;
  }
  if (Z !== null)
    Q.push({ script: Z, start: K, end: J.length, text: J.slice(K).join("") });
  return Q;
}
function cG($) {
  return { ["Zyyy"]: "DFLT", ["Zinh"]: "DFLT", ["Zzzz"]: "DFLT", ["Latn"]: "latn", ["Grek"]: "grek", ["Cyrl"]: "cyrl", ["Armn"]: "armn", ["Hebr"]: "hebr", ["Arab"]: "arab", ["Syrc"]: "syrc", ["Thaa"]: "thaa", ["Deva"]: "deva", ["Beng"]: "beng", ["Guru"]: "guru", ["Gujr"]: "gujr", ["Orya"]: "orya", ["Taml"]: "taml", ["Telu"]: "telu", ["Knda"]: "knda", ["Mlym"]: "mlym", ["Sinh"]: "sinh", ["Thai"]: "thai", ["Laoo"]: "lao ", ["Tibt"]: "tibt", ["Mymr"]: "mymr", ["Geor"]: "geor", ["Hang"]: "hang", ["Ethi"]: "ethi", ["Cher"]: "cher", ["Cans"]: "cans", ["Ogam"]: "ogam", ["Runr"]: "runr", ["Khmr"]: "khmr", ["Mong"]: "mong", ["Hira"]: "kana", ["Kana"]: "kana", ["Bopo"]: "bopo", ["Hani"]: "hani", ["Yiii"]: "yi  ", ["Ital"]: "ital", ["Goth"]: "goth", ["Dsrt"]: "dsrt", ["Tglg"]: "tglg", ["Hano"]: "hano", ["Buhd"]: "buhd", ["Tagb"]: "tagb", ["Limb"]: "limb", ["Tale"]: "tale", ["Linb"]: "linb", ["Ugar"]: "ugar", ["Shaw"]: "shaw", ["Osma"]: "osma", ["Cprt"]: "cprt", ["Brai"]: "brai", ["Bugi"]: "bugi", ["Copt"]: "copt", ["Talu"]: "talu", ["Glag"]: "glag", ["Tfng"]: "tfng", ["Sylo"]: "sylo", ["Xpeo"]: "xpeo", ["Khar"]: "khar", ["Bali"]: "bali", ["Xsux"]: "xsux", ["Phnx"]: "phnx", ["Phag"]: "phag", ["Nkoo"]: "nko ", ["Sund"]: "sund", ["Lepc"]: "lepc", ["Olck"]: "olck", ["Vaii"]: "vai ", ["Saur"]: "saur", ["Kali"]: "kali", ["Rjng"]: "rjng", ["Lyci"]: "lyci", ["Cari"]: "cari", ["Lydi"]: "lydi", ["Cham"]: "cham", ["Lana"]: "lana", ["Tavt"]: "tavt", ["Avst"]: "avst", ["Egyp"]: "egyp", ["Samr"]: "samr", ["Lisu"]: "lisu", ["Bamu"]: "bamu", ["Java"]: "java", ["Mtei"]: "mtei", ["Armi"]: "armi", ["Sarb"]: "sarb", ["Prti"]: "prti", ["Phli"]: "phli", ["Orkh"]: "orkh", ["Kthi"]: "kthi", ["Batk"]: "batk", ["Brah"]: "brah", ["Mand"]: "mand", ["Cakm"]: "cakm", ["Merc"]: "merc", ["Mero"]: "mero", ["Plrd"]: "plrd", ["Shrd"]: "shrd", ["Sora"]: "sora", ["Takr"]: "takr", ["Aghb"]: "aghb", ["Bass"]: "bass", ["Dupl"]: "dupl", ["Elba"]: "elba", ["Gran"]: "gran", ["Hmng"]: "hmng", ["Khoj"]: "khoj", ["Lina"]: "lina", ["Mahj"]: "mahj", ["Mani"]: "mani", ["Mend"]: "mend", ["Modi"]: "modi", ["Mroo"]: "mroo", ["Narb"]: "narb", ["Nbat"]: "nbat", ["Palm"]: "palm", ["Pauc"]: "pauc", ["Perm"]: "perm", ["Phlp"]: "phlp", ["Sidd"]: "sidd", ["Sind"]: "sind", ["Tirh"]: "tirh", ["Wara"]: "wara", ["Ahom"]: "ahom", ["Hluw"]: "hluw", ["Hatr"]: "hatr", ["Mult"]: "mult", ["Hung"]: "hung", ["Sgnw"]: "sgnw", ["Adlm"]: "adlm", ["Bhks"]: "bhks", ["Marc"]: "marc", ["Newa"]: "newa", ["Osge"]: "osge", ["Tang"]: "tang", ["Gonm"]: "gonm", ["Nshu"]: "nshu", ["Soyo"]: "soyo", ["Zanb"]: "zanb", ["Dogr"]: "dogr", ["Gong"]: "gong", ["Maka"]: "maka", ["Medf"]: "medf", ["Rohg"]: "rohg", ["Sogd"]: "sogd", ["Sogo"]: "sogo", ["Elym"]: "elym", ["Nand"]: "nand", ["Hmnp"]: "hmnp", ["Wcho"]: "wcho", ["Yezi"]: "yezi", ["Chrs"]: "chrs", ["Diak"]: "diak", ["Kits"]: "kits", ["Vith"]: "vith", ["Ougr"]: "ougr", ["Cpmn"]: "cpmn", ["Tnsa"]: "tnsa", ["Toto"]: "toto", ["Kawi"]: "kawi", ["Nagm"]: "nagm" }[$] ?? "DFLT";
}
var oG = new Set(["Arab", "Syrc", "Hebr", "Thaa", "Nkoo", "Deva", "Beng", "Guru", "Gujr", "Orya", "Taml", "Telu", "Knda", "Mlym", "Sinh", "Thai", "Laoo", "Tibt", "Mymr", "Khmr", "Mong", "Hang"]);
var iG = new Set(["Arab", "Hebr", "Syrc", "Thaa", "Nkoo", "Samr", "Mand", "Armi", "Phnx", "Sarb", "Narb", "Avst", "Prti", "Phli", "Phlp", "Hatr", "Lydi", "Nbat", "Palm", "Mani", "Mend", "Rohg", "Yezi", "Sogo", "Sogd", "Elym", "Chrs", "Ougr", "Adlm"]);
function sG($) {
  return oG.has($);
}
function rG($) {
  return iG.has($) ? "rtl" : "ltr";
}
var EH;
((Y) => {
  Y[Y.Other = 0] = "Other";
  Y[Y.CR = 1] = "CR";
  Y[Y.LF = 2] = "LF";
  Y[Y.Control = 3] = "Control";
  Y[Y.Extend = 4] = "Extend";
  Y[Y.ZWJ = 5] = "ZWJ";
  Y[Y.Regional_Indicator = 6] = "Regional_Indicator";
  Y[Y.Prepend = 7] = "Prepend";
  Y[Y.SpacingMark = 8] = "SpacingMark";
  Y[Y.L = 9] = "L";
  Y[Y.V = 10] = "V";
  Y[Y.T = 11] = "T";
  Y[Y.LV = 12] = "LV";
  Y[Y.LVT = 13] = "LVT";
  Y[Y.Extended_Pictographic = 14] = "Extended_Pictographic";
})(EH ||= {});
var wH;
((A) => {
  A[A.Other = 0] = "Other";
  A[A.CR = 1] = "CR";
  A[A.LF = 2] = "LF";
  A[A.Newline = 3] = "Newline";
  A[A.Extend = 4] = "Extend";
  A[A.ZWJ = 5] = "ZWJ";
  A[A.Regional_Indicator = 6] = "Regional_Indicator";
  A[A.Format = 7] = "Format";
  A[A.Katakana = 8] = "Katakana";
  A[A.Hebrew_Letter = 9] = "Hebrew_Letter";
  A[A.ALetter = 10] = "ALetter";
  A[A.Single_Quote = 11] = "Single_Quote";
  A[A.Double_Quote = 12] = "Double_Quote";
  A[A.MidNumLet = 13] = "MidNumLet";
  A[A.MidLetter = 14] = "MidLetter";
  A[A.MidNum = 15] = "MidNum";
  A[A.Numeric = 16] = "Numeric";
  A[A.ExtendNumLet = 17] = "ExtendNumLet";
  A[A.WSegSpace = 18] = "WSegSpace";
  A[A.Extended_Pictographic = 19] = "Extended_Pictographic";
})(wH ||= {});
function RQ($) {
  if ($ === 13)
    return 1;
  if ($ === 10)
    return 2;
  if ($ >= 0 && $ <= 31 && $ !== 10 && $ !== 13)
    return 3;
  if ($ >= 127 && $ <= 159)
    return 3;
  if ($ === 173)
    return 3;
  if ($ === 1564)
    return 3;
  if ($ === 6158)
    return 3;
  if ($ === 8203)
    return 3;
  if ($ >= 8206 && $ <= 8207)
    return 3;
  if ($ >= 8232 && $ <= 8238)
    return 3;
  if ($ >= 8288 && $ <= 8303)
    return 3;
  if ($ === 65279)
    return 3;
  if ($ >= 65520 && $ <= 65531)
    return 3;
  if ($ === 8205)
    return 5;
  if ($ >= 127456 && $ <= 127487)
    return 6;
  if ($ === 1536 || $ === 1537 || $ === 1538 || $ === 1539 || $ === 1540 || $ === 1541 || $ === 1757 || $ === 1807 || $ === 2192 || $ === 2193 || $ === 2274 || $ === 69821 || $ === 69837)
    return 7;
  if ($ >= 4352 && $ <= 4447)
    return 9;
  if ($ >= 43360 && $ <= 43388)
    return 9;
  if ($ >= 4448 && $ <= 4519)
    return 10;
  if ($ >= 55216 && $ <= 55238)
    return 10;
  if ($ >= 4520 && $ <= 4607)
    return 11;
  if ($ >= 55243 && $ <= 55291)
    return 11;
  if ($ >= 44032 && $ <= 55203) {
    if (($ - 44032) % 28 === 0)
      return 12;
    return 13;
  }
  if ($ >= 127744 && $ <= 129535)
    return 14;
  if ($ >= 129536 && $ <= 129791)
    return 14;
  if ($ >= 9728 && $ <= 9983)
    return 14;
  if ($ >= 9984 && $ <= 10175)
    return 14;
  if ($ === 169 || $ === 174)
    return 14;
  if ($ >= 8960 && $ <= 9215)
    return 14;
  if ($ >= 126976 && $ <= 127023)
    return 14;
  if ($ >= 127136 && $ <= 127231)
    return 14;
  if ($ >= 127232 && $ <= 127487)
    return 14;
  if ($ >= 127488 && $ <= 127743)
    return 14;
  if ($ >= 2307 && $ <= 2307)
    return 8;
  if ($ >= 2363 && $ <= 2363)
    return 8;
  if ($ >= 2366 && $ <= 2368)
    return 8;
  if ($ >= 2377 && $ <= 2380)
    return 8;
  if ($ >= 2382 && $ <= 2383)
    return 8;
  if ($ >= 2434 && $ <= 2435)
    return 8;
  if ($ >= 2494 && $ <= 2496)
    return 8;
  if ($ >= 2503 && $ <= 2508)
    return 8;
  if ($ === 2519)
    return 8;
  if ($ >= 2563 && $ <= 2563)
    return 8;
  if ($ >= 2622 && $ <= 2624)
    return 8;
  if ($ >= 2691 && $ <= 2691)
    return 8;
  if ($ >= 2750 && $ <= 2752)
    return 8;
  if ($ === 2761)
    return 8;
  if ($ >= 2763 && $ <= 2764)
    return 8;
  if ($ >= 2818 && $ <= 2819)
    return 8;
  if ($ === 2878)
    return 8;
  if ($ === 2880)
    return 8;
  if ($ >= 2887 && $ <= 2892)
    return 8;
  if ($ === 2903)
    return 8;
  if ($ >= 3006 && $ <= 3007)
    return 8;
  if ($ >= 3009 && $ <= 3020)
    return 8;
  if ($ === 3031)
    return 8;
  if ($ >= 3073 && $ <= 3075)
    return 8;
  if ($ >= 3137 && $ <= 3140)
    return 8;
  if ($ >= 3202 && $ <= 3203)
    return 8;
  if ($ === 3262)
    return 8;
  if ($ >= 3264 && $ <= 3268)
    return 8;
  if ($ >= 3271 && $ <= 3275)
    return 8;
  if ($ >= 3285 && $ <= 3286)
    return 8;
  if ($ >= 3330 && $ <= 3331)
    return 8;
  if ($ >= 3390 && $ <= 3392)
    return 8;
  if ($ >= 3398 && $ <= 3404)
    return 8;
  if ($ === 3415)
    return 8;
  if ($ >= 3458 && $ <= 3459)
    return 8;
  if ($ >= 3535 && $ <= 3537)
    return 8;
  if ($ >= 3544 && $ <= 3551)
    return 8;
  if ($ >= 3570 && $ <= 3571)
    return 8;
  if ($ === 3902 || $ === 3903)
    return 8;
  if ($ === 3967)
    return 8;
  if ($ >= 4139 && $ <= 4140)
    return 8;
  if ($ === 4145)
    return 8;
  if ($ === 4152)
    return 8;
  if ($ >= 4155 && $ <= 4156)
    return 8;
  if ($ >= 4182 && $ <= 4183)
    return 8;
  if ($ === 4194)
    return 8;
  if ($ >= 4199 && $ <= 4200)
    return 8;
  if ($ >= 4227 && $ <= 4228)
    return 8;
  if ($ >= 4231 && $ <= 4236)
    return 8;
  if ($ === 4239)
    return 8;
  if ($ >= 4250 && $ <= 4252)
    return 8;
  if ($ >= 6070 && $ <= 6070)
    return 8;
  if ($ >= 6078 && $ <= 6085)
    return 8;
  if ($ >= 6087 && $ <= 6088)
    return 8;
  if ($ >= 6435 && $ <= 6438)
    return 8;
  if ($ >= 6441 && $ <= 6443)
    return 8;
  if ($ >= 6448 && $ <= 6449)
    return 8;
  if ($ >= 6451 && $ <= 6456)
    return 8;
  if ($ >= 768 && $ <= 879)
    return 4;
  if ($ >= 1155 && $ <= 1161)
    return 4;
  if ($ >= 1425 && $ <= 1469)
    return 4;
  if ($ === 1471)
    return 4;
  if ($ >= 1473 && $ <= 1474)
    return 4;
  if ($ >= 1476 && $ <= 1477)
    return 4;
  if ($ === 1479)
    return 4;
  if ($ >= 1552 && $ <= 1562)
    return 4;
  if ($ >= 1611 && $ <= 1631)
    return 4;
  if ($ === 1648)
    return 4;
  if ($ >= 1750 && $ <= 1756)
    return 4;
  if ($ >= 1759 && $ <= 1764)
    return 4;
  if ($ >= 1767 && $ <= 1768)
    return 4;
  if ($ >= 1770 && $ <= 1773)
    return 4;
  if ($ === 1809)
    return 4;
  if ($ >= 1840 && $ <= 1866)
    return 4;
  if ($ >= 1958 && $ <= 1968)
    return 4;
  if ($ >= 2027 && $ <= 2035)
    return 4;
  if ($ === 2045)
    return 4;
  if ($ >= 2070 && $ <= 2073)
    return 4;
  if ($ >= 2075 && $ <= 2083)
    return 4;
  if ($ >= 2085 && $ <= 2087)
    return 4;
  if ($ >= 2089 && $ <= 2093)
    return 4;
  if ($ >= 2137 && $ <= 2139)
    return 4;
  if ($ >= 2259 && $ <= 2273)
    return 4;
  if ($ >= 2275 && $ <= 2306)
    return 4;
  if ($ === 2362)
    return 4;
  if ($ === 2364)
    return 4;
  if ($ >= 2369 && $ <= 2376)
    return 4;
  if ($ === 2381)
    return 4;
  if ($ >= 2385 && $ <= 2391)
    return 4;
  if ($ >= 2402 && $ <= 2403)
    return 4;
  if ($ === 2433)
    return 4;
  if ($ === 2492)
    return 4;
  if ($ >= 2497 && $ <= 2500)
    return 4;
  if ($ === 2509)
    return 4;
  if ($ >= 2530 && $ <= 2531)
    return 4;
  if ($ === 2558)
    return 4;
  if ($ >= 2561 && $ <= 2562)
    return 4;
  if ($ === 2620)
    return 4;
  if ($ >= 2625 && $ <= 2626)
    return 4;
  if ($ >= 2631 && $ <= 2632)
    return 4;
  if ($ >= 2635 && $ <= 2637)
    return 4;
  if ($ === 2641)
    return 4;
  if ($ >= 2672 && $ <= 2673)
    return 4;
  if ($ === 2677)
    return 4;
  if ($ >= 2689 && $ <= 2690)
    return 4;
  if ($ === 2748)
    return 4;
  if ($ >= 2753 && $ <= 2757)
    return 4;
  if ($ >= 2759 && $ <= 2760)
    return 4;
  if ($ === 2765)
    return 4;
  if ($ >= 2786 && $ <= 2787)
    return 4;
  if ($ >= 2810 && $ <= 2815)
    return 4;
  if ($ === 2817)
    return 4;
  if ($ === 2876)
    return 4;
  if ($ === 2879)
    return 4;
  if ($ >= 2881 && $ <= 2884)
    return 4;
  if ($ === 2893)
    return 4;
  if ($ >= 2901 && $ <= 2902)
    return 4;
  if ($ >= 2914 && $ <= 2915)
    return 4;
  if ($ === 2946)
    return 4;
  if ($ === 3008)
    return 4;
  if ($ === 3021)
    return 4;
  if ($ === 3072)
    return 4;
  if ($ === 3076)
    return 4;
  if ($ >= 3134 && $ <= 3136)
    return 4;
  if ($ >= 3142 && $ <= 3144)
    return 4;
  if ($ >= 3146 && $ <= 3149)
    return 4;
  if ($ >= 3157 && $ <= 3158)
    return 4;
  if ($ >= 3170 && $ <= 3171)
    return 4;
  if ($ === 3201)
    return 4;
  if ($ === 3260)
    return 4;
  if ($ === 3263)
    return 4;
  if ($ === 3270)
    return 4;
  if ($ >= 3276 && $ <= 3277)
    return 4;
  if ($ >= 3298 && $ <= 3299)
    return 4;
  if ($ >= 3328 && $ <= 3329)
    return 4;
  if ($ >= 3387 && $ <= 3388)
    return 4;
  if ($ >= 3393 && $ <= 3396)
    return 4;
  if ($ === 3405)
    return 4;
  if ($ >= 3426 && $ <= 3427)
    return 4;
  if ($ === 3457)
    return 4;
  if ($ === 3530)
    return 4;
  if ($ >= 3538 && $ <= 3540)
    return 4;
  if ($ === 3542)
    return 4;
  if ($ === 3633)
    return 4;
  if ($ >= 3636 && $ <= 3642)
    return 4;
  if ($ >= 3655 && $ <= 3662)
    return 4;
  if ($ === 3761)
    return 4;
  if ($ >= 3764 && $ <= 3772)
    return 4;
  if ($ >= 3784 && $ <= 3789)
    return 4;
  if ($ >= 3864 && $ <= 3865)
    return 4;
  if ($ === 3893)
    return 4;
  if ($ === 3895)
    return 4;
  if ($ === 3897)
    return 4;
  if ($ >= 3953 && $ <= 3966)
    return 4;
  if ($ >= 3968 && $ <= 3972)
    return 4;
  if ($ >= 3974 && $ <= 3975)
    return 4;
  if ($ >= 3981 && $ <= 3991)
    return 4;
  if ($ >= 3993 && $ <= 4028)
    return 4;
  if ($ === 4038)
    return 4;
  if ($ >= 4141 && $ <= 4144)
    return 4;
  if ($ >= 4146 && $ <= 4151)
    return 4;
  if ($ >= 4153 && $ <= 4154)
    return 4;
  if ($ >= 4157 && $ <= 4158)
    return 4;
  if ($ >= 4184 && $ <= 4185)
    return 4;
  if ($ >= 4190 && $ <= 4192)
    return 4;
  if ($ >= 4209 && $ <= 4212)
    return 4;
  if ($ === 4226)
    return 4;
  if ($ >= 4229 && $ <= 4230)
    return 4;
  if ($ === 4237)
    return 4;
  if ($ === 4253)
    return 4;
  if ($ >= 4957 && $ <= 4959)
    return 4;
  if ($ >= 5906 && $ <= 5908)
    return 4;
  if ($ >= 5938 && $ <= 5940)
    return 4;
  if ($ >= 5970 && $ <= 5971)
    return 4;
  if ($ >= 6002 && $ <= 6003)
    return 4;
  if ($ >= 6068 && $ <= 6069)
    return 4;
  if ($ >= 6071 && $ <= 6077)
    return 4;
  if ($ === 6086)
    return 4;
  if ($ >= 6089 && $ <= 6099)
    return 4;
  if ($ === 6109)
    return 4;
  if ($ >= 6155 && $ <= 6157)
    return 4;
  if ($ === 6159)
    return 4;
  if ($ >= 6277 && $ <= 6278)
    return 4;
  if ($ === 6313)
    return 4;
  if ($ >= 6432 && $ <= 6434)
    return 4;
  if ($ >= 6439 && $ <= 6440)
    return 4;
  if ($ === 6450)
    return 4;
  if ($ >= 6457 && $ <= 6459)
    return 4;
  if ($ >= 6679 && $ <= 6680)
    return 4;
  if ($ === 6683)
    return 4;
  if ($ === 6742)
    return 4;
  if ($ >= 6744 && $ <= 6750)
    return 4;
  if ($ === 6752)
    return 4;
  if ($ === 6754)
    return 4;
  if ($ >= 6757 && $ <= 6764)
    return 4;
  if ($ >= 6771 && $ <= 6780)
    return 4;
  if ($ === 6783)
    return 4;
  if ($ >= 6832 && $ <= 6862)
    return 4;
  if ($ >= 6912 && $ <= 6915)
    return 4;
  if ($ === 6964)
    return 4;
  if ($ >= 6966 && $ <= 6970)
    return 4;
  if ($ === 6972)
    return 4;
  if ($ === 6978)
    return 4;
  if ($ >= 7019 && $ <= 7027)
    return 4;
  if ($ >= 7040 && $ <= 7041)
    return 4;
  if ($ >= 7074 && $ <= 7077)
    return 4;
  if ($ >= 7080 && $ <= 7081)
    return 4;
  if ($ >= 7083 && $ <= 7085)
    return 4;
  if ($ === 7142)
    return 4;
  if ($ >= 7144 && $ <= 7145)
    return 4;
  if ($ === 7149)
    return 4;
  if ($ >= 7151 && $ <= 7153)
    return 4;
  if ($ >= 7212 && $ <= 7219)
    return 4;
  if ($ >= 7222 && $ <= 7223)
    return 4;
  if ($ >= 7376 && $ <= 7378)
    return 4;
  if ($ >= 7380 && $ <= 7392)
    return 4;
  if ($ >= 7394 && $ <= 7400)
    return 4;
  if ($ === 7405)
    return 4;
  if ($ === 7412)
    return 4;
  if ($ >= 7416 && $ <= 7417)
    return 4;
  if ($ >= 7616 && $ <= 7679)
    return 4;
  if ($ >= 8400 && $ <= 8432)
    return 4;
  if ($ >= 11503 && $ <= 11505)
    return 4;
  if ($ === 11647)
    return 4;
  if ($ >= 11744 && $ <= 11775)
    return 4;
  if ($ >= 12330 && $ <= 12335)
    return 4;
  if ($ >= 12441 && $ <= 12442)
    return 4;
  if ($ >= 42607 && $ <= 42610)
    return 4;
  if ($ >= 42612 && $ <= 42621)
    return 4;
  if ($ >= 42654 && $ <= 42655)
    return 4;
  if ($ >= 42736 && $ <= 42737)
    return 4;
  if ($ === 43010)
    return 4;
  if ($ === 43014)
    return 4;
  if ($ === 43019)
    return 4;
  if ($ >= 43045 && $ <= 43046)
    return 4;
  if ($ === 43052)
    return 4;
  if ($ >= 43204 && $ <= 43205)
    return 4;
  if ($ >= 43232 && $ <= 43249)
    return 4;
  if ($ === 43263)
    return 4;
  if ($ >= 43302 && $ <= 43309)
    return 4;
  if ($ >= 43335 && $ <= 43345)
    return 4;
  if ($ >= 43392 && $ <= 43394)
    return 4;
  if ($ === 43443)
    return 4;
  if ($ >= 43446 && $ <= 43449)
    return 4;
  if ($ >= 43452 && $ <= 43453)
    return 4;
  if ($ === 43493)
    return 4;
  if ($ >= 43561 && $ <= 43566)
    return 4;
  if ($ >= 43569 && $ <= 43570)
    return 4;
  if ($ >= 43573 && $ <= 43574)
    return 4;
  if ($ === 43587)
    return 4;
  if ($ === 43596)
    return 4;
  if ($ === 43644)
    return 4;
  if ($ === 43696)
    return 4;
  if ($ >= 43698 && $ <= 43700)
    return 4;
  if ($ >= 43703 && $ <= 43704)
    return 4;
  if ($ >= 43710 && $ <= 43711)
    return 4;
  if ($ === 43713)
    return 4;
  if ($ >= 43756 && $ <= 43757)
    return 4;
  if ($ === 43766)
    return 4;
  if ($ === 44005)
    return 4;
  if ($ === 44008)
    return 4;
  if ($ === 44013)
    return 4;
  if ($ === 64286)
    return 4;
  if ($ >= 65024 && $ <= 65039)
    return 4;
  if ($ >= 65056 && $ <= 65071)
    return 4;
  if ($ >= 127995 && $ <= 127999)
    return 4;
  if ($ >= 917760 && $ <= 917999)
    return 4;
  return 0;
}
function AH($) {
  if ($ === 13)
    return 1;
  if ($ === 10)
    return 2;
  if ($ === 11 || $ === 12 || $ === 133 || $ === 8232 || $ === 8233)
    return 3;
  if ($ === 8205)
    return 5;
  if ($ === 173)
    return 7;
  if ($ === 1564)
    return 7;
  if ($ === 8203)
    return 7;
  if ($ >= 8206 && $ <= 8207)
    return 7;
  if ($ >= 8288 && $ <= 8303)
    return 7;
  if ($ === 65279)
    return 7;
  if ($ >= 127456 && $ <= 127487)
    return 6;
  if ($ >= 127744 && $ <= 129535)
    return 19;
  if ($ >= 129536 && $ <= 129791)
    return 19;
  if ($ >= 9728 && $ <= 9983)
    return 19;
  if ($ >= 9984 && $ <= 10175)
    return 19;
  if ($ >= 1488 && $ <= 1514)
    return 9;
  if ($ >= 64285 && $ <= 64335)
    return 9;
  if ($ >= 12448 && $ <= 12543)
    return 8;
  if ($ === 12337 || $ === 12338 || $ === 12339 || $ === 12340 || $ === 12341)
    return 8;
  if ($ === 12443 || $ === 12444)
    return 8;
  if ($ >= 12784 && $ <= 12799)
    return 8;
  if ($ >= 13008 && $ <= 13054)
    return 8;
  if ($ >= 13056 && $ <= 13143)
    return 8;
  if ($ >= 65382 && $ <= 65437)
    return 8;
  if ($ === 39)
    return 11;
  if ($ === 34)
    return 12;
  if ($ === 46)
    return 13;
  if ($ === 8216 || $ === 8217)
    return 13;
  if ($ === 8228)
    return 13;
  if ($ === 65106)
    return 13;
  if ($ === 65287)
    return 13;
  if ($ === 65294)
    return 13;
  if ($ === 58)
    return 14;
  if ($ === 183)
    return 14;
  if ($ === 903)
    return 14;
  if ($ === 1524)
    return 14;
  if ($ === 8231)
    return 14;
  if ($ === 65043)
    return 14;
  if ($ === 65109)
    return 14;
  if ($ === 65306)
    return 14;
  if ($ === 44)
    return 15;
  if ($ === 59)
    return 15;
  if ($ === 894)
    return 15;
  if ($ === 1417)
    return 15;
  if ($ === 1548 || $ === 1549)
    return 15;
  if ($ === 1644)
    return 15;
  if ($ === 2040)
    return 15;
  if ($ === 8260)
    return 15;
  if ($ === 65040)
    return 15;
  if ($ === 65044)
    return 15;
  if ($ === 65104)
    return 15;
  if ($ === 65108)
    return 15;
  if ($ === 65292)
    return 15;
  if ($ === 65307)
    return 15;
  if ($ >= 48 && $ <= 57)
    return 16;
  if ($ >= 1632 && $ <= 1641)
    return 16;
  if ($ >= 1776 && $ <= 1785)
    return 16;
  if ($ >= 1984 && $ <= 1993)
    return 16;
  if ($ >= 2406 && $ <= 2415)
    return 16;
  if ($ >= 2534 && $ <= 2543)
    return 16;
  if ($ >= 2662 && $ <= 2671)
    return 16;
  if ($ >= 2790 && $ <= 2799)
    return 16;
  if ($ >= 2918 && $ <= 2927)
    return 16;
  if ($ >= 3046 && $ <= 3055)
    return 16;
  if ($ >= 3174 && $ <= 3183)
    return 16;
  if ($ >= 3302 && $ <= 3311)
    return 16;
  if ($ >= 3430 && $ <= 3439)
    return 16;
  if ($ >= 3558 && $ <= 3567)
    return 16;
  if ($ >= 3664 && $ <= 3673)
    return 16;
  if ($ >= 3792 && $ <= 3801)
    return 16;
  if ($ >= 3872 && $ <= 3881)
    return 16;
  if ($ >= 4160 && $ <= 4169)
    return 16;
  if ($ >= 4240 && $ <= 4249)
    return 16;
  if ($ >= 6112 && $ <= 6121)
    return 16;
  if ($ >= 6160 && $ <= 6169)
    return 16;
  if ($ >= 6470 && $ <= 6479)
    return 16;
  if ($ >= 6608 && $ <= 6617)
    return 16;
  if ($ >= 6784 && $ <= 6793)
    return 16;
  if ($ >= 6800 && $ <= 6809)
    return 16;
  if ($ >= 6992 && $ <= 7001)
    return 16;
  if ($ >= 7088 && $ <= 7097)
    return 16;
  if ($ >= 7232 && $ <= 7241)
    return 16;
  if ($ >= 7248 && $ <= 7257)
    return 16;
  if ($ >= 42528 && $ <= 42537)
    return 16;
  if ($ >= 43216 && $ <= 43225)
    return 16;
  if ($ >= 43264 && $ <= 43273)
    return 16;
  if ($ >= 43472 && $ <= 43481)
    return 16;
  if ($ >= 43504 && $ <= 43513)
    return 16;
  if ($ >= 43600 && $ <= 43609)
    return 16;
  if ($ >= 44016 && $ <= 44025)
    return 16;
  if ($ >= 65296 && $ <= 65305)
    return 16;
  if ($ === 95)
    return 17;
  if ($ === 8239)
    return 17;
  if ($ === 8256)
    return 17;
  if ($ === 8276)
    return 17;
  if ($ === 65075 || $ === 65076)
    return 17;
  if ($ >= 65101 && $ <= 65103)
    return 17;
  if ($ === 65343)
    return 17;
  if ($ === 32)
    return 18;
  if ($ === 5760)
    return 18;
  if ($ >= 8192 && $ <= 8202 && $ !== 8199)
    return 18;
  if ($ === 8287)
    return 18;
  if ($ === 12288)
    return 18;
  if (RQ($) === 4)
    return 4;
  if ($ >= 65 && $ <= 90)
    return 10;
  if ($ >= 97 && $ <= 122)
    return 10;
  if ($ >= 192 && $ <= 214)
    return 10;
  if ($ >= 216 && $ <= 246)
    return 10;
  if ($ >= 248 && $ <= 591)
    return 10;
  if ($ >= 592 && $ <= 687)
    return 10;
  if ($ >= 880 && $ <= 1023)
    return 10;
  if ($ >= 1024 && $ <= 1279)
    return 10;
  if ($ >= 1280 && $ <= 1327)
    return 10;
  if ($ >= 1329 && $ <= 1366)
    return 10;
  if ($ >= 1376 && $ <= 1416)
    return 10;
  if ($ >= 1536 && $ <= 1791)
    return 10;
  if ($ >= 2304 && $ <= 2431)
    return 10;
  if ($ >= 2432 && $ <= 2559)
    return 10;
  if ($ >= 2560 && $ <= 2687)
    return 10;
  if ($ >= 2688 && $ <= 2815)
    return 10;
  if ($ >= 2816 && $ <= 2943)
    return 10;
  if ($ >= 2944 && $ <= 3071)
    return 10;
  if ($ >= 3072 && $ <= 3199)
    return 10;
  if ($ >= 3200 && $ <= 3327)
    return 10;
  if ($ >= 3328 && $ <= 3455)
    return 10;
  if ($ >= 3584 && $ <= 3711)
    return 10;
  if ($ >= 3712 && $ <= 3839)
    return 10;
  if ($ >= 4096 && $ <= 4255)
    return 10;
  if ($ >= 4256 && $ <= 4351)
    return 10;
  if ($ >= 4352 && $ <= 4607)
    return 10;
  if ($ >= 6016 && $ <= 6143)
    return 10;
  if ($ >= 12352 && $ <= 12447)
    return 10;
  if ($ >= 7936 && $ <= 8191)
    return 10;
  if ($ >= 11264 && $ <= 11359)
    return 10;
  if ($ >= 40960 && $ <= 42127)
    return 10;
  if ($ >= 42192 && $ <= 42239)
    return 10;
  if ($ >= 42240 && $ <= 42559)
    return 10;
  if ($ >= 42560 && $ <= 42655)
    return 10;
  if ($ >= 42784 && $ <= 43007)
    return 10;
  if ($ >= 43776 && $ <= 43887)
    return 10;
  if ($ >= 44032 && $ <= 55215)
    return 10;
  if ($ >= 64256 && $ <= 64262)
    return 10;
  if ($ >= 65313 && $ <= 65338)
    return 10;
  if ($ >= 65345 && $ <= 65370)
    return 10;
  return 0;
}
function MQ($) {
  let Q = $.length, Z = [], K = [];
  for (let U = 0;U < $.length; U++) {
    let W = $[U];
    Z.push(RQ(W));
  }
  if (Q === 0)
    return { boundaries: K, properties: Z };
  let q = 0, J = false;
  for (let U = 1;U < Q; U++) {
    let W = Z[U - 1], H = Z[U], _ = true;
    if (W === 1 && H === 2)
      _ = false;
    else if (W === 3 || W === 1 || W === 2)
      _ = true;
    else if (H === 3 || H === 1 || H === 2)
      _ = true;
    else if (W === 9 && (H === 9 || H === 10 || H === 12 || H === 13))
      _ = false;
    else if ((W === 12 || W === 10) && (H === 10 || H === 11))
      _ = false;
    else if ((W === 13 || W === 11) && H === 11)
      _ = false;
    else if (H === 4 || H === 5)
      _ = false;
    else if (H === 8)
      _ = false;
    else if (W === 7)
      _ = false;
    else if (J && W === 5 && H === 14)
      _ = false;
    else if (W === 6 && H === 6) {
      if (q % 2 === 1)
        _ = false;
    }
    if (H === 14)
      J = true;
    else if (H !== 4 && H !== 5)
      J = false;
    if (H === 6)
      q++;
    else
      q = 0;
    if (_)
      K.push(U);
  }
  return K.push(Q), { boundaries: K, properties: Z };
}
function LH($) {
  let Q = $.length, Z = [], K = [];
  for (let J = 0;J < $.length; J++) {
    let U = $[J];
    Z.push(AH(U));
  }
  if (Q === 0)
    return { boundaries: K, properties: Z };
  K.push(0);
  let q = 0;
  for (let J = 1;J < Q; J++) {
    let U = Z[J - 1], W = Z[J], H = true;
    if (U === 1 && W === 2)
      H = false;
    else if (U === 3 || U === 1 || U === 2)
      H = true;
    else if (W === 3 || W === 1 || W === 2)
      H = true;
    else if (U === 5 && W === 19)
      H = false;
    else if (U === 18 && W === 18)
      H = false;
    else if (W === 7 || W === 4 || W === 5)
      H = false;
    else if ((U === 10 || U === 9) && (W === 10 || W === 9))
      H = false;
    else if ((U === 10 || U === 9) && (W === 14 || W === 13 || W === 11)) {
      if (J + 1 < Q) {
        let _ = Z[J + 1];
        if (_ === 10 || _ === 9)
          H = false;
      }
    } else if (U === 16 && W === 16)
      H = false;
    else if ((U === 10 || U === 9) && W === 16)
      H = false;
    else if (U === 16 && (W === 10 || W === 9))
      H = false;
    else if (U === 16 && (W === 15 || W === 13 || W === 11)) {
      if (J + 1 < Q && Z[J + 1] === 16)
        H = false;
    } else if (U === 8 && W === 8)
      H = false;
    else if ((U === 10 || U === 9 || U === 16 || U === 8 || U === 17) && W === 17)
      H = false;
    else if (U === 17 && (W === 10 || W === 9 || W === 16 || W === 8))
      H = false;
    else if (U === 6 && W === 6) {
      if (q % 2 === 1)
        H = false;
    }
    if (W === 6)
      q++;
    else
      q = 0;
    if (H)
      K.push(J);
  }
  return K.push(Q), { boundaries: K, properties: Z };
}
function aG($) {
  let Q = [], Z = [...$];
  for (let U = 0;U < Z.length; U++) {
    let W = Z[U];
    Q.push(W.codePointAt(0) ?? 0);
  }
  let { boundaries: K } = MQ(Q), q = [], J = 0;
  for (let U = 0;U < K.length; U++) {
    let W = K[U];
    if (W > J)
      q.push(Z.slice(J, W).join(""));
    J = W;
  }
  return q;
}
function tG($) {
  let Q = [], Z = [...$];
  for (let U = 0;U < Z.length; U++) {
    let W = Z[U];
    Q.push(W.codePointAt(0) ?? 0);
  }
  let { boundaries: K, properties: q } = LH(Q), J = [];
  for (let U = 0;U < K.length - 1; U++) {
    let W = K[U], H = K[U + 1], _ = false;
    for (let N = W;N < H; N++) {
      let V = q[N];
      if (V !== 18 && V !== 1 && V !== 2 && V !== 3) {
        _ = true;
        break;
      }
    }
    if (_)
      J.push(Z.slice(W, H).join(""));
  }
  return J;
}
function eG($) {
  let Q = [], Z = [...$];
  for (let q = 0;q < Z.length; q++) {
    let J = Z[q];
    Q.push(J.codePointAt(0) ?? 0);
  }
  let { boundaries: K } = MQ(Q);
  return K.length;
}

// src/runtime/create-app-io-utils.ts
function openLink(uri) {
  if (!uri || typeof window === "undefined")
    return;
  try {
    const url = new URL(uri, window.location.href);
    if (!["http:", "https:", "mailto:"].includes(url.protocol))
      return;
    const win = window.open(url.toString(), "_blank", "noopener,noreferrer");
    if (win)
      win.opener = null;
  } catch {
  }
}
function sourceLabelFromUrl(url, index) {
  const trimmed = url.trim();
  if (!trimmed)
    return `font-${index + 1}`;
  try {
    const parsed = new URL(trimmed, typeof window !== "undefined" ? window.location.href : "");
    const file = parsed.pathname.split("/").filter(Boolean).pop();
    return file || parsed.hostname || `font-${index + 1}`;
  } catch {
    const parts = trimmed.split("/").filter(Boolean);
    const file = parts[parts.length - 1] ?? "";
    return file || `font-${index + 1}`;
  }
}
function sourceBufferFromView(view) {
  const { buffer, byteOffset, byteLength } = view;
  if (byteOffset === 0 && byteLength === buffer.byteLength) {
    return buffer.slice(0);
  }
  return buffer.slice(byteOffset, byteOffset + byteLength);
}
function normalizeNewlines(text2) {
  return text2.replace(/\r?\n/g, `\r
`);
}
function fitTextTailToWidth(text2, maxWidthPx, measureWidthPx) {
  if (!text2) {
    return { text: "", offset: 0, widthPx: 0 };
  }
  const safeMaxWidth = Number.isFinite(maxWidthPx) ? Math.max(0, maxWidthPx) : 0;
  if (safeMaxWidth <= 0) {
    return { text: "", offset: text2.length, widthPx: 0 };
  }
  const fullWidth = measureWidthPx(text2);
  if (fullWidth <= safeMaxWidth) {
    return { text: text2, offset: 0, widthPx: fullWidth };
  }
  const boundaries = [];
  let codeUnitOffset = 0;
  for (const cp of text2) {
    boundaries.push(codeUnitOffset);
    codeUnitOffset += cp.length;
  }
  boundaries.push(text2.length);
  let fallbackOffset = boundaries[Math.max(0, boundaries.length - 2)] ?? 0;
  let fallbackText = text2.slice(fallbackOffset);
  let fallbackWidth = measureWidthPx(fallbackText);
  for (let i3 = 1;i3 < boundaries.length - 1; i3 += 1) {
    const offset = boundaries[i3] ?? 0;
    const candidate = text2.slice(offset);
    const candidateWidth = measureWidthPx(candidate);
    if (candidateWidth <= safeMaxWidth) {
      return { text: candidate, offset, widthPx: candidateWidth };
    }
    fallbackOffset = offset;
    fallbackText = candidate;
    fallbackWidth = candidateWidth;
  }
  return {
    text: fallbackText,
    offset: fallbackOffset,
    widthPx: Math.min(fallbackWidth, safeMaxWidth)
  };
}

// src/runtime/font-resource-store.ts
var DEFAULT_MAX_SOURCE_CACHE_BYTES = 256 * 1024 * 1024;
var DEFAULT_URL_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
var FAILED_SOURCE_RETRY_MS = 5000;
var URL_CACHE_DB_NAME = "restty-font-cache-v1";
var URL_CACHE_STORE_NAME = "url-bytes";
var LOCAL_FONTS_PERMISSION_NAME = "local-fonts";
function nowMs() {
  return Date.now();
}
function normalizeUrlKey(url) {
  const trimmed = url.trim();
  if (!trimmed)
    return trimmed;
  try {
    const base = typeof document !== "undefined" && typeof document.baseURI === "string" ? document.baseURI : undefined;
    return base ? new URL(trimmed, base).href : new URL(trimmed).href;
  } catch {
    return trimmed;
  }
}
function detectStyleHint(value) {
  const text2 = value.toLowerCase();
  let weight = 400;
  if (/\b(thin|hairline)\b/.test(text2))
    weight = 100;
  else if (/\b(extra[- ]?light|ultra[- ]?light)\b/.test(text2))
    weight = 200;
  else if (/\blight\b/.test(text2))
    weight = 300;
  else if (/\bmedium\b/.test(text2))
    weight = 500;
  else if (/\b(semi[- ]?bold|demi[- ]?bold)\b/.test(text2))
    weight = 600;
  else if (/\bbold\b/.test(text2))
    weight = 700;
  else if (/\b(extra[- ]?bold|ultra[- ]?bold)\b/.test(text2))
    weight = 800;
  else if (/\b(black|heavy)\b/.test(text2))
    weight = 900;
  return {
    bold: /\b(bold|semi[- ]?bold|demi[- ]?bold|extra[- ]?bold|black|heavy)\b/.test(text2),
    italic: /\b(italic|oblique)\b/.test(text2),
    regular: /\b(regular|book|roman|normal)\b/.test(text2),
    weight
  };
}
function resolveFaceLabel(baseLabel, face) {
  if (face.metadataLabel)
    return `${baseLabel} (${face.metadataLabel})`;
  if (face.index !== undefined)
    return `${baseLabel} ${face.index}`;
  return baseLabel;
}
function sourceBaseLabel(source, index) {
  if (source.label)
    return source.label;
  if (source.type === "url")
    return sourceLabelFromUrl(source.url, index);
  if (source.type === "local")
    return source.matchers[0] ?? `local-font-${index + 1}`;
  return `font-buffer-${index + 1}`;
}
function getSourceMatchers(source) {
  if (source.type !== "local")
    return [];
  const normalized = [];
  for (let i3 = 0;i3 < source.matchers.length; i3 += 1) {
    const matcher = source.matchers[i3];
    if (!matcher)
      continue;
    const next = matcher.trim().toLowerCase();
    if (!next)
      continue;
    normalized.push(next);
  }
  return normalized;
}
function toSourceBuffer(source) {
  if (source.type !== "buffer")
    return null;
  const data = source.data;
  if (data instanceof ArrayBuffer)
    return data;
  if (ArrayBuffer.isView(data))
    return sourceBufferFromView(data);
  return null;
}
function createSourceKeyResolver() {
  let nextBufferId = 1;
  const bufferIds = new WeakMap;
  const getBufferId = (buffer) => {
    const existing = bufferIds.get(buffer);
    if (existing)
      return existing;
    const assigned = nextBufferId;
    nextBufferId += 1;
    bufferIds.set(buffer, assigned);
    return assigned;
  };
  return (source) => {
    if (source.type === "url") {
      return `url:${normalizeUrlKey(source.url)}`;
    }
    if (source.type === "local") {
      const matchers = getSourceMatchers(source);
      return `local:${matchers.join("|")}`;
    }
    const data = source.data;
    if (data instanceof ArrayBuffer) {
      return `buffer:${getBufferId(data)}`;
    }
    if (ArrayBuffer.isView(data)) {
      const view = data;
      return `view:${getBufferId(view.buffer)}:${view.byteOffset}:${view.byteLength}`;
    }
    return "buffer:invalid";
  };
}
function createUrlByteCache(deps) {
  const now = deps.now ?? nowMs;
  const ttlMs = deps.urlCacheTtlMs ?? DEFAULT_URL_CACHE_TTL_MS;
  const enabled = deps.usePersistentUrlCache ?? true;
  let dbPromise = null;
  const openDb = async () => {
    if (!enabled || typeof indexedDB === "undefined")
      return null;
    if (dbPromise)
      return dbPromise;
    dbPromise = new Promise((resolve) => {
      try {
        const request = indexedDB.open(URL_CACHE_DB_NAME, 1);
        request.addEventListener("upgradeneeded", () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(URL_CACHE_STORE_NAME)) {
            db.createObjectStore(URL_CACHE_STORE_NAME, { keyPath: "key" });
          }
        });
        request.addEventListener("success", () => resolve(request.result));
        request.addEventListener("error", () => resolve(null));
      } catch {
        resolve(null);
      }
    });
    return dbPromise;
  };
  const get = async (key) => {
    const db = await openDb();
    if (!db)
      return null;
    return await new Promise((resolve) => {
      try {
        const tx = db.transaction(URL_CACHE_STORE_NAME, "readonly");
        const store = tx.objectStore(URL_CACHE_STORE_NAME);
        const request = store.get(key);
        request.addEventListener("success", () => {
          const result = request.result;
          if (!result?.buffer) {
            resolve(null);
            return;
          }
          resolve(result);
        });
        request.addEventListener("error", () => resolve(null));
      } catch {
        resolve(null);
      }
    });
  };
  const set = async (key, buffer) => {
    const db = await openDb();
    if (!db)
      return;
    const record = {
      key,
      buffer,
      storedAt: now(),
      expiresAt: now() + ttlMs
    };
    await new Promise((resolve) => {
      try {
        const tx = db.transaction(URL_CACHE_STORE_NAME, "readwrite");
        const store = tx.objectStore(URL_CACHE_STORE_NAME);
        const request = store.put(record);
        request.addEventListener("success", () => resolve());
        request.addEventListener("error", () => resolve());
      } catch {
        resolve();
      }
    });
  };
  const getFresh = async (key) => {
    const record = await get(key);
    if (!record)
      return null;
    if (record.expiresAt <= now())
      return null;
    return record.buffer;
  };
  const getStale = async (key) => {
    const record = await get(key);
    return record?.buffer ?? null;
  };
  return {
    getFresh,
    getStale,
    set
  };
}
async function tryLoadLocalFontBuffer(matchers, label) {
  const normalizedMatchers = matchers.map((matcher) => matcher.toLowerCase()).filter(Boolean);
  if (!normalizedMatchers.length)
    return null;
  const globalAccess = globalThis;
  const nav = globalAccess.navigator ?? (typeof navigator !== "undefined" ? navigator : undefined);
  if (!nav)
    return null;
  const queryLocalFonts = typeof globalAccess.queryLocalFonts === "function" ? globalAccess.queryLocalFonts.bind(globalAccess) : typeof nav.queryLocalFonts === "function" ? nav.queryLocalFonts.bind(nav) : null;
  if (!queryLocalFonts)
    return null;
  const sourceHint = detectStyleHint(`${label} ${normalizedMatchers.join(" ")}`);
  const queryPermission = nav.permissions?.query;
  if (queryPermission) {
    try {
      const status = await queryPermission({ name: LOCAL_FONTS_PERMISSION_NAME });
      if (status?.state === "denied")
        return null;
    } catch {
    }
  }
  try {
    const fonts = await queryLocalFonts();
    const matches = fonts.filter((font) => {
      const name = `${font.family ?? ""} ${font.fullName ?? ""} ${font.postscriptName ?? ""}`.toLowerCase();
      return normalizedMatchers.some((matcher) => name.includes(matcher));
    });
    if (!matches.length)
      return null;
    const scoreMatch = (font) => {
      const name = `${font.family ?? ""} ${font.fullName ?? ""} ${font.postscriptName ?? ""}`.toLowerCase();
      const hint = detectStyleHint(name);
      let score = 0;
      for (let i3 = 0;i3 < normalizedMatchers.length; i3 += 1) {
        if (name.includes(normalizedMatchers[i3]))
          score += 8;
      }
      if (sourceHint.bold || sourceHint.italic) {
        score += sourceHint.bold === hint.bold ? 40 : -40;
        score += sourceHint.italic === hint.italic ? 40 : -40;
      } else {
        score += !hint.bold && !hint.italic ? 60 : -30;
      }
      const targetWeight = sourceHint.bold ? 700 : 400;
      score -= Math.abs((hint.weight ?? 400) - targetWeight) * 0.25;
      if (!sourceHint.bold && hint.weight === 400)
        score += 12;
      if (!sourceHint.bold && hint.weight < 350)
        score -= 12;
      if (!sourceHint.bold && hint.weight > 650)
        score -= 8;
      if (sourceHint.regular && !hint.bold && !hint.italic)
        score += 20;
      return score;
    };
    let best = matches[0];
    let bestScore = Number.NEGATIVE_INFINITY;
    for (let i3 = 0;i3 < matches.length; i3 += 1) {
      const candidate = matches[i3];
      const score = scoreMatch(candidate);
      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    }
    const blob = await best.blob();
    return await blob.arrayBuffer();
  } catch {
    return null;
  }
}
async function parseFontFacesFromBuffer(buffer) {
  const { Font } = exports_dist;
  const parsed = [];
  const collection = Font.collection ? Font.collection(buffer) : null;
  if (collection) {
    const names = collection.names();
    for (let i3 = 0;i3 < names.length; i3 += 1) {
      const info = names[i3];
      try {
        const face = collection.get(info.index);
        parsed.push({
          font: face,
          metadataLabel: info.fullName || info.family || info.postScriptName || undefined,
          index: info.index
        });
      } catch {
      }
    }
    return parsed;
  }
  try {
    const face = await Font.loadAsync(buffer);
    parsed.push({ font: face });
  } catch {
    return [];
  }
  return parsed;
}
function createResttyFontResourceStore(options = {}) {
  const now = options.now ?? nowMs;
  const maxSourceCacheBytes = options.maxSourceCacheBytes ?? DEFAULT_MAX_SOURCE_CACHE_BYTES;
  const resolveSourceKey = createSourceKeyResolver();
  const urlByteCache = createUrlByteCache(options);
  const sourceCache = new Map;
  const parsedCache = new Map;
  const inFlightSourceLoads = new Map;
  const inFlightParses = new Map;
  const leaseSources = new Map;
  let totalCachedSourceBytes = 0;
  let nextLeaseId = 1;
  const removeSourceEntry = (key) => {
    const entry = sourceCache.get(key);
    if (!entry)
      return;
    totalCachedSourceBytes -= entry.byteLength;
    sourceCache.delete(key);
    parsedCache.delete(key);
  };
  const pruneSourceCache = () => {
    if (totalCachedSourceBytes <= maxSourceCacheBytes)
      return;
    const evictable = Array.from(sourceCache.values()).filter((entry) => entry.refCount === 0).sort((a3, b3) => a3.lastUsedAt - b3.lastUsedAt);
    for (let i3 = 0;i3 < evictable.length; i3 += 1) {
      if (totalCachedSourceBytes <= maxSourceCacheBytes)
        break;
      removeSourceEntry(evictable[i3].key);
    }
  };
  const loadUrlBuffer = async (sourceKey, url) => {
    const freshCached = await urlByteCache.getFresh(sourceKey);
    if (freshCached)
      return freshCached;
    try {
      const response = await fetch(url);
      if (response.ok) {
        const buffer = await response.arrayBuffer();
        if (buffer.byteLength > 0) {
          urlByteCache.set(sourceKey, buffer.slice(0));
        }
        return buffer;
      }
    } catch {
    }
    return await urlByteCache.getStale(sourceKey);
  };
  const defaultLoadSourceBuffer = async (source, sourceKey) => {
    if (source.type === "buffer") {
      return toSourceBuffer(source);
    }
    if (source.type === "local") {
      const matchers = getSourceMatchers(source);
      if (!matchers.length)
        return null;
      return await tryLoadLocalFontBuffer(matchers, source.label ?? source.matchers[0] ?? "local-font");
    }
    return await loadUrlBuffer(sourceKey, normalizeUrlKey(source.url));
  };
  const resolveSourceBuffer = options.loadSourceBuffer ?? defaultLoadSourceBuffer;
  const parseBuffer = options.parseBuffer ?? (async (buffer) => parseFontFacesFromBuffer(buffer));
  const loadSourceBufferCached = async (source, sourceKey) => {
    const cached = sourceCache.get(sourceKey);
    if (cached) {
      cached.lastUsedAt = now();
      if (cached.buffer)
        return cached.buffer;
      if (cached.failedAt !== null && now() - cached.failedAt < FAILED_SOURCE_RETRY_MS) {
        return null;
      }
    }
    const inFlight = inFlightSourceLoads.get(sourceKey);
    if (inFlight)
      return inFlight;
    const promise = (async () => {
      const loadedAt = now();
      const buffer = await resolveSourceBuffer(source, sourceKey);
      const nextByteLength = buffer?.byteLength ?? 0;
      const current = sourceCache.get(sourceKey);
      if (!current) {
        sourceCache.set(sourceKey, {
          key: sourceKey,
          buffer,
          byteLength: nextByteLength,
          loadedAt,
          lastUsedAt: loadedAt,
          failedAt: buffer ? null : loadedAt,
          refCount: 0
        });
        totalCachedSourceBytes += nextByteLength;
      } else {
        totalCachedSourceBytes -= current.byteLength;
        current.buffer = buffer;
        current.byteLength = nextByteLength;
        current.loadedAt = loadedAt;
        current.lastUsedAt = loadedAt;
        current.failedAt = buffer ? null : loadedAt;
        totalCachedSourceBytes += nextByteLength;
      }
      parsedCache.delete(sourceKey);
      pruneSourceCache();
      return buffer;
    })().finally(() => {
      inFlightSourceLoads.delete(sourceKey);
    });
    inFlightSourceLoads.set(sourceKey, promise);
    return promise;
  };
  const parseSourceFacesCached = async (sourceKey, buffer) => {
    const cached = parsedCache.get(sourceKey);
    if (cached) {
      cached.lastUsedAt = now();
      return cached.faces;
    }
    const inFlight = inFlightParses.get(sourceKey);
    if (inFlight)
      return inFlight;
    const promise = parseBuffer(buffer, sourceKey).then((faces) => {
      parsedCache.set(sourceKey, {
        key: sourceKey,
        faces,
        lastUsedAt: now()
      });
      return faces;
    }).finally(() => {
      inFlightParses.delete(sourceKey);
    });
    inFlightParses.set(sourceKey, promise);
    return promise;
  };
  const releaseLease = (leaseId) => {
    const sourceRefs = leaseSources.get(leaseId);
    if (!sourceRefs)
      return;
    leaseSources.delete(leaseId);
    const releasedAt = now();
    for (const [key, count] of sourceRefs.entries()) {
      const sourceEntry = sourceCache.get(key);
      if (!sourceEntry)
        continue;
      sourceEntry.refCount = Math.max(0, sourceEntry.refCount - count);
      sourceEntry.lastUsedAt = releasedAt;
    }
    pruneSourceCache();
  };
  const acquire = async (sources2) => {
    const faces = [];
    const sourceRefs = new Map;
    for (let sourceIndex = 0;sourceIndex < sources2.length; sourceIndex += 1) {
      const source = sources2[sourceIndex];
      const sourceKey = resolveSourceKey(source);
      const baseLabel = sourceBaseLabel(source, sourceIndex);
      let buffer;
      try {
        buffer = await loadSourceBufferCached(source, sourceKey);
      } catch {
        buffer = null;
      }
      if (!buffer) {
        if (source.type === "local") {
          const prefix = source.required ? "required local font missing" : "optional local font missing";
          console.warn(`[font] ${prefix} (${source.matchers.join(", ")})`);
        }
        continue;
      }
      let parsedFaces;
      try {
        parsedFaces = await parseSourceFacesCached(sourceKey, buffer);
      } catch {
        parsedFaces = [];
      }
      if (!parsedFaces.length)
        continue;
      const sourceEntry = sourceCache.get(sourceKey);
      if (sourceEntry) {
        sourceEntry.refCount += 1;
        sourceEntry.lastUsedAt = now();
      }
      sourceRefs.set(sourceKey, (sourceRefs.get(sourceKey) ?? 0) + 1);
      for (let faceIndex = 0;faceIndex < parsedFaces.length; faceIndex += 1) {
        const parsedFace = parsedFaces[faceIndex];
        faces.push({
          font: parsedFace.font,
          label: resolveFaceLabel(baseLabel, parsedFace)
        });
      }
    }
    const leaseId = nextLeaseId;
    nextLeaseId += 1;
    leaseSources.set(leaseId, sourceRefs);
    let released = false;
    return {
      faces,
      release: () => {
        if (released)
          return;
        released = true;
        releaseLease(leaseId);
      }
    };
  };
  return {
    acquire
  };
}

// src/runtime/session.ts
function createResttyAppSession() {
  let wasmPromise = null;
  let webgpuCorePromise = null;
  const wasmLogListeners = new Set;
  const fontResourceStore = createResttyFontResourceStore();
  const forwardWasmLog = (message) => {
    for (const listener of wasmLogListeners) {
      listener(message);
    }
  };
  return {
    getWasm: () => {
      if (!wasmPromise) {
        wasmPromise = loadResttyWasm({ log: forwardWasmLog });
      }
      return wasmPromise;
    },
    getWebGPUCore: (canvas) => {
      if (!webgpuCorePromise) {
        webgpuCorePromise = initWebGPUCore(canvas);
      }
      return webgpuCorePromise;
    },
    getFontResourceStore: () => fontResourceStore,
    addWasmLogListener: (listener) => {
      wasmLogListeners.add(listener);
    },
    removeWasmLogListener: (listener) => {
      wasmLogListeners.delete(listener);
    }
  };
}
var defaultResttyAppSession = null;
function getDefaultResttyAppSession() {
  if (!defaultResttyAppSession) {
    defaultResttyAppSession = createResttyAppSession();
  }
  return defaultResttyAppSession;
}

// src/runtime/atlas-builder.ts
function buildFontAtlasIfNeeded(params) {
  const {
    entry,
    neededGlyphIds,
    glyphMeta,
    fontSizePx,
    atlasScale,
    fontIndex,
    constraintContext,
    deps
  } = params;
  if (!entry?.font) {
    return { rebuilt: false, atlas: null, rgba: null, preferNearest: false };
  }
  const {
    fontScaleOverrides,
    sizeMode,
    isSymbolFont: isSymbolFont2,
    fontScaleOverride: fontScaleOverride2,
    resolveGlyphPixelMode,
    atlasBitmapToRGBA,
    padAtlasRGBA,
    buildAtlas,
    buildGlyphAtlasWithConstraints,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    hinting,
    hintTarget,
    nerdConstraintSignature,
    constants,
    resolvePreferNearest
  } = deps;
  const resolvedHintTarget = hintTarget ?? "auto";
  const scaleOverride = fontScaleOverride2(entry, fontScaleOverrides);
  const effectiveFontSizePx = Math.max(1, Math.round(fontSizePx * (atlasScale || 1) * scaleOverride));
  let needsRebuild = !entry.atlas || entry.fontSizePx !== effectiveFontSizePx || entry.atlasScale !== (atlasScale || 1) || entry.atlasHinting !== hinting || (entry.atlasHintTarget ?? "auto") !== resolvedHintTarget;
  const isSymbol = isSymbolFont2(entry);
  const constraintSignature = isSymbol ? nerdConstraintSignature(glyphMeta, constraintContext) : "";
  if (!needsRebuild && isSymbol && (entry.constraintSignature ?? "") !== constraintSignature) {
    needsRebuild = true;
  }
  if (!needsRebuild) {
    for (const glyphId of neededGlyphIds) {
      if (!entry.glyphIds.has(glyphId)) {
        needsRebuild = true;
        break;
      }
    }
  }
  if (!needsRebuild && glyphMeta && glyphMeta.size && isSymbol) {
    const widthMaps = entry.atlas?.glyphsByWidth;
    if (!widthMaps) {
      needsRebuild = true;
    } else {
      for (const [glyphId, meta] of glyphMeta.entries()) {
        const widths = meta.widths ?? new Set([meta.constraintWidth ?? 1]);
        for (const width of widths) {
          if (!widthMaps.get(width)?.has(glyphId)) {
            needsRebuild = true;
            break;
          }
        }
        if (needsRebuild)
          break;
      }
    }
  }
  if (!needsRebuild) {
    return {
      rebuilt: false,
      atlas: entry.atlas ?? null,
      rgba: null,
      preferNearest: false
    };
  }
  const union = new Set(entry.glyphIds);
  for (const glyphId of neededGlyphIds)
    union.add(glyphId);
  if (union.size === 0) {
    return { rebuilt: false, atlas: null, rgba: null, preferNearest: false };
  }
  const useHinting = hinting;
  const atlasPadding = isSymbol ? Math.max(constants.atlasPadding, constants.symbolAtlasPadding) : constants.atlasPadding;
  const atlasMaxSize = isSymbol ? constants.symbolAtlasMaxSize : constants.defaultAtlasMaxSize;
  const glyphPixelMode = resolveGlyphPixelMode(entry);
  const colorGlyphAtlas = glyphPixelMode === constants.pixelModeRgbaValue || glyphPixelMode === 4;
  const useCanvasColorAtlas = colorGlyphAtlas;
  let atlas = null;
  if (isSymbol && rasterizeGlyph && rasterizeGlyphWithTransform && constraintContext && glyphMeta) {
    const result = buildGlyphAtlasWithConstraints({
      font: entry.font,
      glyphIds: [...union],
      fontSize: effectiveFontSizePx,
      sizeMode,
      padding: atlasPadding,
      pixelMode: glyphPixelMode,
      hinting: useHinting,
      hintTarget: resolvedHintTarget,
      maxWidth: atlasMaxSize,
      maxHeight: atlasMaxSize,
      rasterizeGlyph,
      rasterizeGlyphWithTransform,
      glyphMeta,
      constraintContext
    });
    atlas = result?.atlas ?? null;
  }
  if (!atlas && useCanvasColorAtlas) {
    const result = buildColorEmojiAtlasWithCanvas({
      font: entry.font,
      fontEntry: entry,
      glyphIds: [...union],
      fontSize: effectiveFontSizePx,
      sizeMode,
      padding: atlasPadding,
      pixelMode: glyphPixelMode,
      maxWidth: atlasMaxSize,
      maxHeight: atlasMaxSize
    });
    atlas = result?.atlas ?? null;
  }
  if (!atlas && rasterizeGlyph && colorGlyphAtlas) {
    const result = buildGlyphAtlasWithConstraints({
      font: entry.font,
      glyphIds: [...union],
      fontSize: effectiveFontSizePx,
      sizeMode,
      padding: atlasPadding,
      pixelMode: glyphPixelMode,
      hinting: useHinting,
      hintTarget: resolvedHintTarget,
      maxWidth: atlasMaxSize,
      maxHeight: atlasMaxSize,
      rasterizeGlyph,
      rasterizeGlyphWithTransform
    });
    atlas = result?.atlas ?? null;
  }
  if (!atlas) {
    atlas = buildAtlas(entry.font, [...union], {
      fontSize: effectiveFontSizePx,
      sizeMode,
      padding: atlasPadding,
      pixelMode: glyphPixelMode,
      hinting: useHinting,
      hintTarget: resolvedHintTarget,
      maxWidth: atlasMaxSize,
      maxHeight: atlasMaxSize
    });
  }
  if (!atlas || !atlas.bitmap?.width || !atlas.bitmap?.rows) {
    return { rebuilt: false, atlas: null, rgba: null, preferNearest: false };
  }
  const colorGlyphs = colorGlyphAtlas ? new Set(atlas.glyphs.keys()) : undefined;
  atlas.colorGlyphs = colorGlyphs;
  let rgba = atlasBitmapToRGBA(atlas);
  if (!rgba) {
    return { rebuilt: false, atlas: null, rgba: null, preferNearest: false };
  }
  rgba = padAtlasRGBA(rgba, atlas, atlasPadding);
  entry.atlas = atlas;
  entry.glyphIds = union;
  entry.fontSizePx = effectiveFontSizePx;
  entry.atlasScale = atlasScale || 1;
  entry.constraintSignature = constraintSignature;
  entry.atlasHinting = useHinting;
  entry.atlasHintTarget = resolvedHintTarget;
  return {
    rebuilt: true,
    atlas,
    rgba,
    colorGlyphs,
    preferNearest: resolvePreferNearest({ fontIndex, isSymbol, atlasScale })
  };
}

// src/runtime/font-sources.ts
var DEFAULT_FONT_SOURCES = [
  {
    type: "local",
    matchers: [
      "jetbrainsmono nerd font",
      "jetbrains mono nerd font",
      "jetbrains mono nl nerd font mono",
      "jetbrains mono",
      "jetbrainsmono"
    ],
    label: "JetBrains Mono Nerd Font Regular (Local)"
  },
  {
    type: "local",
    matchers: [
      "jetbrainsmono nerd font bold",
      "jetbrains mono nerd font bold",
      "jetbrains mono nl nerd font mono bold",
      "jetbrains mono bold",
      "jetbrainsmono bold"
    ],
    label: "JetBrains Mono Nerd Font Bold (Local)"
  },
  {
    type: "local",
    matchers: [
      "jetbrainsmono nerd font italic",
      "jetbrains mono nerd font italic",
      "jetbrains mono nl nerd font mono italic",
      "jetbrains mono italic",
      "jetbrainsmono italic"
    ],
    label: "JetBrains Mono Nerd Font Italic (Local)"
  },
  {
    type: "local",
    matchers: [
      "jetbrainsmono nerd font bold italic",
      "jetbrains mono nerd font bold italic",
      "jetbrains mono nl nerd font mono bold italic",
      "jetbrains mono bold italic",
      "jetbrainsmono bold italic"
    ],
    label: "JetBrains Mono Nerd Font Bold Italic (Local)"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ryanoasis/nerd-fonts@v3.4.0/patched-fonts/JetBrainsMono/NoLigatures/Regular/JetBrainsMonoNLNerdFontMono-Regular.ttf",
    label: "JetBrains Mono Nerd Font Regular"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ryanoasis/nerd-fonts@v3.4.0/patched-fonts/JetBrainsMono/NoLigatures/Bold/JetBrainsMonoNLNerdFontMono-Bold.ttf",
    label: "JetBrains Mono Nerd Font Bold"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ryanoasis/nerd-fonts@v3.4.0/patched-fonts/JetBrainsMono/NoLigatures/Italic/JetBrainsMonoNLNerdFontMono-Italic.ttf",
    label: "JetBrains Mono Nerd Font Italic"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ryanoasis/nerd-fonts@v3.4.0/patched-fonts/JetBrainsMono/NoLigatures/BoldItalic/JetBrainsMonoNLNerdFontMono-BoldItalic.ttf",
    label: "JetBrains Mono Nerd Font Bold Italic"
  },
  {
    type: "local",
    matchers: [
      "symbols nerd font mono",
      "symbols nerd font",
      "nerd fonts symbols",
      "nerdfontssymbolsonly"
    ],
    label: "Symbols Nerd Font (Local)"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ryanoasis/nerd-fonts@v3.4.0/patched-fonts/NerdFontsSymbolsOnly/SymbolsNerdFontMono-Regular.ttf"
  },
  {
    type: "local",
    matchers: ["apple symbols", "applesymbols", "apple symbols regular"],
    label: "Apple Symbols"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/notofonts/noto-fonts@main/unhinted/ttf/NotoSansSymbols2/NotoSansSymbols2-Regular.ttf"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/ChiefMikeK/ttf-symbola@master/Symbola.ttf"
  },
  {
    type: "local",
    matchers: [
      "noto sans canadian aboriginal",
      "notosanscanadianaboriginal",
      "euphemia ucas",
      "euphemiaucas"
    ],
    label: "Noto Sans Canadian Aboriginal / Euphemia UCAS"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/notofonts/noto-fonts@main/unhinted/ttf/NotoSansCanadianAboriginal/NotoSansCanadianAboriginal-Regular.ttf",
    label: "Noto Sans Canadian Aboriginal"
  },
  {
    type: "local",
    matchers: ["apple color emoji", "applecoloremoji"],
    label: "Apple Color Emoji"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/googlefonts/noto-emoji@main/fonts/NotoColorEmoji.ttf"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/hfg-gmuend/openmoji@master/font/OpenMoji-black-glyf/OpenMoji-black-glyf.ttf"
  },
  {
    type: "url",
    url: "https://cdn.jsdelivr.net/gh/notofonts/noto-cjk@main/Sans/OTF/SimplifiedChinese/NotoSansCJKsc-Regular.otf"
  }
];
function validateFontSource(source, index) {
  if (!source || typeof source !== "object" || !("type" in source)) {
    throw new Error(`fontSources[${index}] must be a typed source object`);
  }
  if (source.type === "url") {
    if (typeof source.url !== "string" || !source.url.trim()) {
      throw new Error(`fontSources[${index}] url source requires a non-empty url`);
    }
    return source;
  }
  if (source.type === "buffer") {
    const data = source.data;
    if (!(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) {
      throw new Error(`fontSources[${index}] buffer source requires ArrayBuffer or ArrayBufferView`);
    }
    return source;
  }
  if (source.type === "local") {
    if (!Array.isArray(source.matchers)) {
      throw new Error(`fontSources[${index}] local source requires at least one matcher`);
    }
    let hasMatcher = false;
    for (let i3 = 0;i3 < source.matchers.length; i3 += 1) {
      if (source.matchers[i3]) {
        hasMatcher = true;
        break;
      }
    }
    if (!hasMatcher) {
      throw new Error(`fontSources[${index}] local source requires at least one matcher`);
    }
    return source;
  }
  throw new Error(`fontSources[${index}] has unsupported source type`);
}
function normalizeFontSources(sources2, preset) {
  if (sources2 && sources2.length) {
    const normalized = new Array(sources2.length);
    for (let i3 = 0;i3 < sources2.length; i3 += 1) {
      normalized[i3] = validateFontSource(sources2[i3], i3);
    }
    return normalized;
  }
  if (preset === "none")
    return [];
  return [...DEFAULT_FONT_SOURCES];
}

// src/runtime/pty-output-buffer.ts
function concatBytes(chunks) {
  let total = 0;
  for (const c3 of chunks)
    total += c3.length;
  const result = new Uint8Array(total);
  let offset = 0;
  for (const c3 of chunks) {
    result.set(c3, offset);
    offset += c3.length;
  }
  return result;
}
function createPtyOutputBufferController(options) {
  const { idleMs, maxMs, onFlush, onFlushBytes } = options;
  let buffer = "";
  let byteChunks = [];
  let bytesLen = 0;
  let idleTimer = 0;
  let maxTimer = 0;
  const cancel = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = 0;
    }
    if (maxTimer) {
      clearTimeout(maxTimer);
      maxTimer = 0;
    }
  };
  const flush = () => {
    const textOutput = buffer;
    buffer = "";
    const pendingBytes = bytesLen > 0 ? byteChunks : null;
    byteChunks = [];
    bytesLen = 0;
    cancel();
    if (textOutput)
      onFlush(textOutput);
    if (pendingBytes && onFlushBytes) {
      onFlushBytes(pendingBytes.length === 1 ? pendingBytes[0] : concatBytes(pendingBytes));
    }
  };
  const scheduleFlush = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }
    idleTimer = setTimeout(() => {
      idleTimer = 0;
      if (maxTimer) {
        clearTimeout(maxTimer);
        maxTimer = 0;
      }
      flush();
    }, idleMs);
    if (!maxTimer) {
      maxTimer = setTimeout(() => {
        maxTimer = 0;
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = 0;
        }
        flush();
      }, maxMs);
    }
  };
  const queue = (text2) => {
    if (!text2)
      return;
    buffer += text2;
    scheduleFlush();
  };
  const queueBytes = (data) => {
    if (!data.length)
      return;
    byteChunks.push(data);
    bytesLen += data.length;
    scheduleFlush();
  };
  const clear = () => {
    buffer = "";
    byteChunks = [];
    bytesLen = 0;
  };
  return {
    queue,
    queueBytes,
    flush,
    cancel,
    clear
  };
}

// src/runtime/text-decoration.ts
function drawUnderlineStyle(underlineData, style, x3, rowY, cellW, cellH, baseY, underlineOffsetPx, underlineThicknessPx, color) {
  if (style <= 0)
    return;
  const thickness = underlineThicknessPx;
  const minY = rowY + 1;
  const maxY = rowY + cellH - thickness - 1;
  const underlineY = clamp(baseY + underlineOffsetPx, minY, maxY);
  if (style === 1) {
    pushRect(underlineData, x3, underlineY, cellW, thickness, color);
    return;
  }
  if (style === 2) {
    pushRect(underlineData, x3, underlineY, cellW, thickness, color);
    const gap = Math.max(1, Math.round(thickness * 0.6));
    let secondY = underlineY + thickness + gap;
    if (secondY > maxY)
      secondY = Math.max(minY, underlineY - thickness - gap);
    pushRect(underlineData, x3, secondY, cellW, thickness, color);
    return;
  }
  if (style === 3) {
    const step = Math.max(2, Math.round(cellW * 0.25));
    const waveOffset = Math.max(1, Math.round(thickness * 0.8));
    for (let dx = 0;dx < cellW; dx += step) {
      const up = Math.floor(dx / step) % 2 === 0;
      const y = underlineY + (up ? 0 : waveOffset);
      pushRect(underlineData, x3 + dx, y, Math.min(step, cellW - dx), thickness, color);
    }
    return;
  }
  if (style === 4) {
    const dot = Math.max(1, Math.round(thickness));
    const gap = Math.max(1, Math.round(dot));
    for (let dx = 0;dx < cellW; dx += dot + gap) {
      pushRect(underlineData, x3 + dx, underlineY, Math.min(dot, cellW - dx), thickness, color);
    }
    return;
  }
  if (style === 5) {
    const dash = Math.max(1, Math.round(cellW * 0.6));
    const gap = Math.max(1, Math.round(cellW * 0.2));
    for (let dx = 0;dx < cellW; dx += dash + gap) {
      pushRect(underlineData, x3 + dx, underlineY, Math.min(dash, cellW - dx), thickness, color);
    }
  }
}
function drawStrikethrough(underlineData, x3, rowY, cellW, cellH, color) {
  const thickness = Math.max(1, Math.round(cellH * 0.08));
  const y = Math.round(rowY + cellH * 0.5 - thickness * 0.5);
  pushRect(underlineData, x3, y, cellW, thickness, color);
}
function drawOverline(underlineData, x3, rowY, cellW, color) {
  const thickness = 1;
  const y = Math.round(rowY + 1);
  pushRect(underlineData, x3, y, cellW, thickness, color);
}

// src/runtime/create-app-symbols.ts
var RENDERER_SYMBOL_FALLBACK_RANGES = [
  [9632, 9727],
  [11008, 11263]
];
var DEFAULT_SYMBOL_CONSTRAINT = {
  size: "fit"
};
var DEFAULT_APPLE_SYMBOLS_CONSTRAINT = {
  size: "fit_cover1",
  align_vertical: "center"
};
var DEFAULT_EMOJI_CONSTRAINT = {
  size: "cover",
  align_horizontal: "center",
  align_vertical: "center",
  pad_left: 0.025,
  pad_right: 0.025
};
function normalizeTouchSelectionMode(value) {
  if (value === "drag" || value === "long-press" || value === "off")
    return value;
  return "long-press";
}
function clampFiniteNumber(value, fallback, min, max, round = false) {
  if (!Number.isFinite(value))
    return fallback;
  const numeric = round ? Math.round(value) : Number(value);
  return Math.min(max, Math.max(min, numeric));
}
function isRenderSymbolLike(cp) {
  return isSymbolCp(cp) || isRendererSymbolFallbackRange(cp);
}
function resolveSymbolConstraint(cp) {
  return getNerdConstraint(cp);
}
function isRendererSymbolFallbackRange(cp) {
  for (let i3 = 0;i3 < RENDERER_SYMBOL_FALLBACK_RANGES.length; i3 += 1) {
    const [start, end] = RENDERER_SYMBOL_FALLBACK_RANGES[i3];
    if (cp >= start && cp <= end)
      return true;
  }
  return false;
}

// src/runtime/render-color-utils.ts
function decodePackedRGBA(color) {
  return [
    (color & 255) / 255,
    (color >>> 8 & 255) / 255,
    (color >>> 16 & 255) / 255,
    (color >>> 24 & 255) / 255
  ];
}
function decodeRGBAWithCache(bytes, index, cache) {
  const offset = index * 4;
  const packed = ((bytes[offset] ?? 0) | (bytes[offset + 1] ?? 0) << 8 | (bytes[offset + 2] ?? 0) << 16 | (bytes[offset + 3] ?? 0) << 24) >>> 0;
  const cached = cache.get(packed);
  if (cached)
    return cached;
  const decoded = decodePackedRGBA(packed);
  cache.set(packed, decoded);
  return decoded;
}
function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}
function brighten(color, amount) {
  return [
    clamp01(color[0] + (1 - color[0]) * amount),
    clamp01(color[1] + (1 - color[1]) * amount),
    clamp01(color[2] + (1 - color[2]) * amount),
    color[3]
  ];
}
function fade(color, factor) {
  return [color[0], color[1], color[2], clamp01(color[3] * factor)];
}

// src/runtime/codepoint-utils.ts
function isLikelyEmojiCodepoint2(cp) {
  if (cp >= 127462 && cp <= 127487)
    return true;
  if (cp >= 127744 && cp <= 129791)
    return true;
  return false;
}
function isVariationSelectorCodepoint2(cp) {
  if (cp >= 65024 && cp <= 65039)
    return true;
  if (cp >= 917760 && cp <= 917999)
    return true;
  return false;
}
function isCombiningMarkCodepoint2(cp) {
  if (cp >= 768 && cp <= 879)
    return true;
  if (cp >= 6832 && cp <= 6911)
    return true;
  if (cp >= 7616 && cp <= 7679)
    return true;
  if (cp >= 8400 && cp <= 8447)
    return true;
  if (cp >= 65056 && cp <= 65071)
    return true;
  return false;
}
function isEmojiModifierCodepoint(cp) {
  return cp >= 127995 && cp <= 127999;
}
function isCoverageIgnorableCodepoint2(cp) {
  if (cp === 8204 || cp === 8205)
    return true;
  if (isVariationSelectorCodepoint2(cp))
    return true;
  if (isCombiningMarkCodepoint2(cp))
    return true;
  if (cp >= 917536 && cp <= 917631)
    return true;
  return false;
}
function shouldMergeTrailingClusterCodepoint(cp) {
  if (cp === 8204 || cp === 8205)
    return true;
  if (isVariationSelectorCodepoint2(cp))
    return true;
  if (isCombiningMarkCodepoint2(cp))
    return true;
  if (isEmojiModifierCodepoint(cp))
    return true;
  return false;
}
function resolvePresentationPreference2(text2, chars) {
  if (text2.includes("️"))
    return "emoji";
  if (text2.includes("︎"))
    return "text";
  if (text2.includes("‍"))
    return "emoji";
  for (let i3 = 0;i3 < chars.length; i3 += 1) {
    const cp = chars[i3].codePointAt(0) ?? 0;
    if (isLikelyEmojiCodepoint2(cp))
      return "emoji";
  }
  return "auto";
}
function stylePreferenceFromFlags(bold, italic) {
  if (bold && italic)
    return "bold_italic";
  if (bold)
    return "bold";
  if (italic)
    return "italic";
  return "regular";
}
function isAppleSymbolsFont(entry) {
  return !!entry && /\bapple symbols\b/i.test(entry.label ?? "");
}
function fontEntryHasBoldStyle(entry) {
  return !!entry && /\bbold\b/i.test(entry.label ?? "");
}
function fontEntryHasItalicStyle(entry) {
  return !!entry && /\b(italic|oblique)\b/i.test(entry.label ?? "");
}

// src/runtime/font-atlas-utils/nerd-metrics-utils.ts
var NERD_CELL_FIT_COVER_SCALE = 1;
var NERD_ICON_FIT_COVER_SCALE = 1;
function resolveFontScaleForAtlas(font, fontSize, sizeMode) {
  if (font && typeof font.scaleForSize === "function") {
    return font.scaleForSize(fontSize, sizeMode ?? undefined);
  }
  const upem = font?.unitsPerEm ?? 1000;
  return upem > 0 ? fontSize / upem : 1;
}
function fontCapHeightUnits(font) {
  if (!font)
    return 1;
  const capFromOs2 = font?.os2?.sCapHeight ?? font?._os2?.sCapHeight;
  if (Number.isFinite(capFromOs2) && capFromOs2 > 0)
    return capFromOs2;
  if (typeof font.glyphIdForChar === "function" && typeof font.getGlyphBounds === "function") {
    const capGlyphId = font.glyphIdForChar("H");
    if (capGlyphId !== undefined && capGlyphId !== null && capGlyphId !== 0) {
      const bounds = font.getGlyphBounds(capGlyphId);
      const yMax = bounds?.yMax;
      if (Number.isFinite(yMax) && yMax > 0)
        return yMax;
      const height = (bounds?.yMax ?? 0) - (bounds?.yMin ?? 0);
      if (Number.isFinite(height) && height > 0)
        return height;
    }
  }
  const ascender = font?.ascender;
  if (Number.isFinite(ascender) && ascender > 0)
    return ascender * 0.75;
  const faceHeight = fontHeightUnits(font);
  if (Number.isFinite(faceHeight) && faceHeight > 0)
    return faceHeight * 0.6;
  return 1;
}
function buildNerdMetrics(cellW, cellH, lineHeight, primaryFont, primaryScale, iconScale) {
  let faceWidth = cellW;
  if (primaryFont && typeof primaryFont.glyphIdForChar === "function" && typeof primaryFont.advanceWidth === "function") {
    const mGlyphId = primaryFont.glyphIdForChar("M");
    if (mGlyphId !== undefined && mGlyphId !== null && mGlyphId !== 0) {
      const width = primaryFont.advanceWidth(mGlyphId) * primaryScale;
      if (Number.isFinite(width) && width > 0)
        faceWidth = width;
    }
  }
  const capHeight = fontCapHeightUnits(primaryFont) * primaryScale;
  const safeIconScale = Number.isFinite(iconScale) ? Math.max(0.5, Math.min(2, iconScale)) : 1;
  const iconHeight = lineHeight * safeIconScale;
  const iconHeightSingle = clamp((2 * capHeight + lineHeight) / 3 * safeIconScale, 1, iconHeight);
  return {
    cellWidth: cellW,
    cellHeight: cellH,
    faceWidth,
    faceHeight: lineHeight,
    faceY: (cellH - lineHeight) * 0.5,
    iconHeight,
    iconHeightSingle
  };
}
function nerdConstraintSignature(glyphMeta, constraintContext) {
  if (!glyphMeta?.size || !constraintContext)
    return "";
  const m = constraintContext.nerdMetrics;
  return [
    `ih:${m.iconHeight.toFixed(3)}`,
    `ih1:${m.iconHeightSingle.toFixed(3)}`,
    `iw:${m.cellWidth.toFixed(3)}`,
    `cw:${constraintContext.cellW.toFixed(3)}`,
    `ch:${constraintContext.cellH.toFixed(3)}`,
    `is:${NERD_ICON_FIT_COVER_SCALE.toFixed(4)}`,
    `cs:${NERD_CELL_FIT_COVER_SCALE.toFixed(4)}`
  ].join("|");
}
function scaleGlyphBoxAroundCenter(box, factor) {
  if (!Number.isFinite(factor) || factor <= 0 || Math.abs(factor - 1) < 0.000001)
    return box;
  const cx = box.x + box.width * 0.5;
  const cy = box.y + box.height * 0.5;
  const w = box.width * factor;
  const h = box.height * factor;
  return {
    x: cx - w * 0.5,
    y: cy - h * 0.5,
    width: w,
    height: h
  };
}
function scaleGlyphBoxAnchoredLeft(box, factor) {
  if (!Number.isFinite(factor) || factor <= 0 || Math.abs(factor - 1) < 0.000001)
    return box;
  const w = box.width * factor;
  const h = box.height * factor;
  return {
    x: box.x,
    y: box.y + (box.height - h) * 0.5,
    width: w,
    height: h
  };
}
function tightenNerdConstraintBox(box, constraint) {
  if (!constraint)
    return box;
  if (constraint.size !== "fit_cover1")
    return box;
  if (constraint.height === "icon") {
    return scaleGlyphBoxAnchoredLeft(box, NERD_ICON_FIT_COVER_SCALE);
  }
  if (constraint.height !== undefined && constraint.height !== "cell")
    return box;
  return scaleGlyphBoxAroundCenter(box, NERD_CELL_FIT_COVER_SCALE);
}

// src/runtime/font-atlas-utils/bitmap-utils.ts
function bitmapBytesPerPixel(pixelMode) {
  if (pixelMode === 2 || pixelMode === 3)
    return 3;
  if (pixelMode === 4)
    return 4;
  return 1;
}
function createAtlasBitmap(width, height, pixelMode) {
  const bytesPerPixel = bitmapBytesPerPixel(pixelMode);
  const pitch = Math.max(1, Math.ceil(width * bytesPerPixel));
  const size = pitch * height;
  return {
    width,
    rows: height,
    pitch,
    buffer: new Uint8Array(size),
    pixelMode,
    numGrays: pixelMode === 0 ? 2 : 256
  };
}
function cloneBitmap(bitmap, defaultPixelMode = 1) {
  const pitch = bitmap?.pitch ?? 0;
  const rows = bitmap?.rows ?? 0;
  const size = pitch * rows;
  const buffer = new Uint8Array(size);
  if (bitmap?.buffer) {
    buffer.set(bitmap.buffer.subarray(0, size));
  }
  return {
    width: bitmap?.width ?? 0,
    rows,
    pitch,
    buffer,
    pixelMode: bitmap?.pixelMode ?? defaultPixelMode,
    numGrays: bitmap?.numGrays ?? 256
  };
}
function copyBitmapToAtlas(src, dst, dstX, dstY) {
  const bytesPerPixel = bitmapBytesPerPixel(src.pixelMode ?? 1);
  const rowBytes = src.width * bytesPerPixel;
  for (let y = 0;y < src.rows; y += 1) {
    const srcRow = y * src.pitch;
    const dstRow = (dstY + y) * dst.pitch + dstX * bytesPerPixel;
    dst.buffer.set(src.buffer.subarray(srcRow, srcRow + rowBytes), dstRow);
  }
}

// src/runtime/font-atlas-utils/packing-utils.ts
function nextPowerOf2(n) {
  if (n <= 0)
    return 1;
  let v = n - 1;
  v |= v >> 1;
  v |= v >> 2;
  v |= v >> 4;
  v |= v >> 8;
  v |= v >> 16;
  return v + 1;
}
function packGlyphs(sizes, maxWidth, maxHeight) {
  const shelves = [];
  const placements = [];
  let atlasWidth = 0;
  let atlasHeight = 0;
  for (let i3 = 0;i3 < sizes.length; i3 += 1) {
    const size = sizes[i3];
    let placed = false;
    let bestShelf = -1;
    let bestY = maxHeight;
    for (let j = 0;j < shelves.length; j += 1) {
      const shelf = shelves[j];
      if (shelf.width + size.width <= maxWidth && size.height <= shelf.height) {
        if (shelf.y < bestY) {
          bestShelf = j;
          bestY = shelf.y;
        }
      }
    }
    if (bestShelf >= 0) {
      const shelf = shelves[bestShelf];
      placements.push({ x: shelf.width, y: shelf.y, placed: true });
      shelf.width += size.width;
      atlasWidth = Math.max(atlasWidth, shelf.width);
      placed = true;
    } else {
      const newY = atlasHeight;
      if (newY + size.height <= maxHeight && size.width <= maxWidth) {
        shelves.push({ y: newY, height: size.height, width: size.width });
        placements.push({ x: 0, y: newY, placed: true });
        atlasHeight = newY + size.height;
        atlasWidth = Math.max(atlasWidth, size.width);
        placed = true;
      }
    }
    if (!placed)
      placements.push({ x: 0, y: 0, placed: false });
  }
  const finalWidth = nextPowerOf2(atlasWidth);
  const finalHeight = nextPowerOf2(atlasHeight);
  return {
    width: Math.min(finalWidth, maxWidth),
    height: Math.min(finalHeight, maxHeight),
    placements
  };
}

// src/runtime/font-atlas-utils/glyph-atlas-builder.ts
function buildGlyphAtlasWithConstraints(options) {
  const {
    font,
    glyphIds,
    fontSize,
    sizeMode,
    padding,
    maxWidth,
    maxHeight,
    pixelMode,
    hinting,
    hintTarget,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    glyphMeta,
    constraintContext
  } = options;
  const scale = resolveFontScaleForAtlas(font, fontSize, sizeMode);
  const glyphData = [];
  if (!rasterizeGlyph) {
    return { atlas: null, constrainedGlyphWidths: null };
  }
  const rasterOptions = {
    padding: 0,
    pixelMode,
    sizeMode,
    hinting,
    hintTarget
  };
  for (let i3 = 0;i3 < glyphIds.length; i3 += 1) {
    const glyphId = glyphIds[i3];
    const raster = rasterizeGlyph(font, glyphId, fontSize, rasterOptions);
    if (!raster)
      continue;
    let didConstraint = false;
    const meta = glyphMeta?.get(glyphId);
    const widthSet = meta?.widths && meta.widths.size ? Array.from(meta.widths.values()) : [Math.max(1, meta?.constraintWidth ?? 1)];
    const widths = Array.from(new Set(widthSet.map((w) => Math.max(1, w)))).sort();
    const constraint = meta?.cp ? getNerdConstraint(meta.cp) : null;
    if (constraint && constraintContext && rasterizeGlyphWithTransform) {
      for (let j = 0;j < widths.length; j += 1) {
        const constraintWidth = widths[j];
        const maxCellWidth = constraintContext.cellW * constraintWidth;
        const maxCellHeight = constraintContext.cellH;
        let bitmapScale = 1;
        const widthUnits = glyphWidthUnits(constraintContext.fontEntry, glyphId);
        let glyphWidthPx = widthUnits * constraintContext.fontScale;
        if (!Number.isFinite(glyphWidthPx) || glyphWidthPx <= 0) {
          glyphWidthPx = raster.bitmap?.width ?? 0;
        }
        if (glyphWidthPx > 0 && maxCellWidth > 0) {
          const fit = maxCellWidth / glyphWidthPx;
          if (fit > 0 && fit < 1)
            bitmapScale = fit;
        }
        let gw = (raster.bitmap?.width ?? 0) * bitmapScale;
        let gh = (raster.bitmap?.rows ?? 0) * bitmapScale;
        if (gw > 0 && gh > 0 && maxCellWidth > 0 && maxCellHeight > 0) {
          const fitScale = Math.min(1, maxCellWidth / gw, maxCellHeight / gh);
          if (fitScale < 1) {
            bitmapScale *= fitScale;
            gw *= fitScale;
            gh *= fitScale;
          }
        }
        const baseY = constraintContext.yPad + constraintContext.baselineOffset + constraintContext.baselineAdjust;
        const scaledBox = {
          x: raster.bearingX * bitmapScale,
          y: baseY - raster.bearingY * bitmapScale,
          width: gw,
          height: gh
        };
        const adjusted = constrainGlyphBox(scaledBox, constraint, constraintContext.nerdMetrics, constraintWidth);
        const tightened = tightenNerdConstraintBox(adjusted, constraint);
        if (tightened.width > 0 && tightened.height > 0 && raster.bitmap?.width && raster.bitmap?.rows) {
          const targetLeft = tightened.x;
          const targetTop = baseY - tightened.y;
          const scaleX = tightened.width / raster.bitmap.width;
          const scaleY = tightened.height / raster.bitmap.rows;
          if (Number.isFinite(scaleX) && scaleX > 0 && Number.isFinite(scaleY) && scaleY > 0) {
            const tx = targetLeft - raster.bearingX * scaleX;
            const ty = targetTop - raster.bearingY * scaleY;
            const transformed = rasterizeGlyphWithTransform(font, glyphId, fontSize, [scaleX, 0, 0, scaleY, tx, ty], rasterOptions);
            if (transformed) {
              glyphData.push({
                glyphId,
                bitmap: cloneBitmap(transformed.bitmap),
                bearingX: transformed.bearingX,
                bearingY: transformed.bearingY,
                advance: font.advanceWidth(glyphId) * scale,
                constraintWidth
              });
              didConstraint = true;
            }
          }
        }
      }
    }
    if (!didConstraint) {
      const advance = font.advanceWidth(glyphId) * scale;
      glyphData.push({
        glyphId,
        bitmap: cloneBitmap(raster.bitmap),
        bearingX: raster.bearingX,
        bearingY: raster.bearingY,
        advance,
        constraintWidth: 0
      });
    }
  }
  glyphData.sort((a3, b3) => (b3.bitmap?.rows ?? 0) - (a3.bitmap?.rows ?? 0));
  const {
    width: atlasWidth,
    height: atlasHeight,
    placements
  } = packGlyphs(glyphData.map((g) => ({
    width: (g.bitmap?.width ?? 0) + padding * 2,
    height: (g.bitmap?.rows ?? 0) + padding * 2
  })), maxWidth, maxHeight);
  const atlas = createAtlasBitmap(atlasWidth, atlasHeight, pixelMode);
  const glyphMetrics = new Map;
  const glyphMetricsByWidth = new Map;
  for (let i3 = 0;i3 < glyphData.length; i3 += 1) {
    const glyph = glyphData[i3];
    const placement = placements[i3];
    if (!placement?.placed || !glyph.bitmap)
      continue;
    copyBitmapToAtlas(glyph.bitmap, atlas, placement.x + padding, placement.y + padding);
    const metrics = {
      glyphId: glyph.glyphId,
      atlasX: placement.x + padding,
      atlasY: placement.y + padding,
      width: glyph.bitmap.width,
      height: glyph.bitmap.rows,
      bearingX: glyph.bearingX,
      bearingY: glyph.bearingY,
      advance: glyph.advance
    };
    const widthKey = glyph.constraintWidth ?? 0;
    if (widthKey > 0) {
      let widthMap = glyphMetricsByWidth.get(widthKey);
      if (!widthMap) {
        widthMap = new Map;
        glyphMetricsByWidth.set(widthKey, widthMap);
      }
      widthMap.set(glyph.glyphId, metrics);
      if (!glyphMetrics.has(glyph.glyphId) || widthKey === 1) {
        glyphMetrics.set(glyph.glyphId, metrics);
      }
    } else {
      if (!glyphMetrics.has(glyph.glyphId)) {
        glyphMetrics.set(glyph.glyphId, metrics);
      }
    }
  }
  return {
    atlas: {
      bitmap: atlas,
      glyphs: glyphMetrics,
      glyphsByWidth: glyphMetricsByWidth,
      fontSize
    },
    constrainedGlyphWidths: null
  };
}

// src/runtime/create-runtime/blend-utils.ts
function srgbChannelToLinear(channel) {
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}
function srgbToLinearColor(color) {
  return [
    srgbChannelToLinear(color[0]),
    srgbChannelToLinear(color[1]),
    srgbChannelToLinear(color[2]),
    color[3]
  ];
}
function resolveBlendFlags(alphaBlending, backendType, state2) {
  if (alphaBlending === "native") {
    return { useLinearBlending: false, useLinearCorrection: false };
  }
  if (backendType === "webgl2") {
    return { useLinearBlending: false, useLinearCorrection: false };
  }
  if (backendType === "webgpu" && !state2?.srgbSwapchain) {
    return { useLinearBlending: false, useLinearCorrection: false };
  }
  return {
    useLinearBlending: true,
    useLinearCorrection: alphaBlending === "linear-corrected"
  };
}
function floatsToRgb(color) {
  return [
    Math.round((color[0] ?? 0) * 255),
    Math.round((color[1] ?? 0) * 255),
    Math.round((color[2] ?? 0) * 255)
  ];
}

// src/runtime/create-runtime/atlas-debug-utils.ts
function atlasRegionToImageData(atlas, x3, y, width, height, pixelModeGray, pixelModeRgba) {
  const rgba = new Uint8ClampedArray(width * height * 4);
  const pixelMode = atlas.bitmap?.pixelMode ?? pixelModeGray;
  const rgbaBitmap = pixelMode === pixelModeRgba || pixelMode === 4;
  for (let row = 0;row < height; row += 1) {
    const dstRow = row * width * 4;
    if (rgbaBitmap) {
      const srcRow2 = (y + row) * atlas.bitmap.pitch + x3 * 4;
      for (let col = 0;col < width; col += 1) {
        const srcIdx = srcRow2 + col * 4;
        const dstIdx = dstRow + col * 4;
        rgba[dstIdx] = atlas.bitmap.buffer[srcIdx] ?? 0;
        rgba[dstIdx + 1] = atlas.bitmap.buffer[srcIdx + 1] ?? 0;
        rgba[dstIdx + 2] = atlas.bitmap.buffer[srcIdx + 2] ?? 0;
        rgba[dstIdx + 3] = atlas.bitmap.buffer[srcIdx + 3] ?? 0;
      }
      continue;
    }
    const srcRow = (y + row) * atlas.bitmap.pitch + x3;
    for (let col = 0;col < width; col += 1) {
      const alpha = atlas.bitmap.buffer[srcRow + col] ?? 0;
      const dstIdx = dstRow + col * 4;
      rgba[dstIdx] = 255;
      rgba[dstIdx + 1] = 255;
      rgba[dstIdx + 2] = 255;
      rgba[dstIdx + 3] = alpha;
    }
  }
  return new ImageData(rgba, width, height);
}
function padAtlasRGBA(rgba, atlas, padding) {
  if (!padding || padding <= 0 || !atlas?.glyphs)
    return rgba;
  const width = atlas.bitmap?.width ?? 0;
  const height = atlas.bitmap?.rows ?? 0;
  if (!width || !height)
    return rgba;
  const out = new Uint8Array(rgba);
  const clamp2 = (v, min, max) => Math.max(min, Math.min(max, v));
  const writePixel = (dstX, dstY, srcX, srcY) => {
    if (dstX < 0 || dstY < 0 || dstX >= width || dstY >= height)
      return;
    if (srcX < 0 || srcY < 0 || srcX >= width || srcY >= height)
      return;
    const srcIdx = (srcY * width + srcX) * 4;
    const dstIdx = (dstY * width + dstX) * 4;
    out[dstIdx] = out[srcIdx];
    out[dstIdx + 1] = out[srcIdx + 1];
    out[dstIdx + 2] = out[srcIdx + 2];
    out[dstIdx + 3] = out[srcIdx + 3];
  };
  const padMetrics = (metrics) => {
    const x02 = metrics.atlasX;
    const y02 = metrics.atlasY;
    const x12 = metrics.atlasX + metrics.width - 1;
    const y12 = metrics.atlasY + metrics.height - 1;
    if (x02 < 0 || y02 < 0 || x12 < x02 || y12 < y02)
      return;
    const pad = padding;
    for (let y = y02;y <= y12; y += 1) {
      for (let px = 1;px <= pad; px += 1) {
        writePixel(x02 - px, y, x02, y);
        writePixel(x12 + px, y, x12, y);
      }
    }
    for (let x3 = x02 - pad;x3 <= x12 + pad; x3 += 1) {
      const clampedX = clamp2(x3, 0, width - 1);
      for (let py = 1;py <= pad; py += 1) {
        writePixel(clampedX, y02 - py, clampedX, y02);
        writePixel(clampedX, y12 + py, clampedX, y12);
      }
    }
  };
  for (const metrics of atlas.glyphs.values()) {
    padMetrics(metrics);
  }
  const glyphsByWidth = atlas.glyphsByWidth;
  if (glyphsByWidth && typeof glyphsByWidth.values === "function") {
    for (const map of glyphsByWidth.values()) {
      if (!map?.values)
        continue;
      for (const metrics of map.values()) {
        padMetrics(metrics);
      }
    }
  }
  return out;
}
function resolveGlyphPixelMode(entry, pixelModeGray, pixelModeRgba, isColorEmojiFont2) {
  if (pixelModeRgba !== undefined && pixelModeRgba !== null && isColorEmojiFont2(entry)) {
    return pixelModeRgba;
  }
  return pixelModeGray;
}
function atlasBitmapToRGBA(atlas, pixelModeRgba, atlasToRGBA) {
  const bitmap = atlas?.bitmap;
  if (!bitmap?.width || !bitmap?.rows)
    return null;
  if (bitmap.pixelMode === pixelModeRgba || bitmap.pixelMode === 4) {
    const width = bitmap.width;
    const height = bitmap.rows;
    const rgba = new Uint8Array(width * height * 4);
    for (let row = 0;row < height; row += 1) {
      const srcStart = row * bitmap.pitch;
      const srcEnd = srcStart + width * 4;
      const dstStart = row * width * 4;
      rgba.set(bitmap.buffer.subarray(srcStart, srcEnd), dstStart);
    }
    return rgba;
  }
  return atlasToRGBA(atlas);
}

// src/runtime/create-runtime/format-utils.ts
function formatPasteText(text2, bracketedPasteEnabled) {
  if (!bracketedPasteEnabled)
    return text2;
  return `\x1B[200~${text2}\x1B[201~`;
}
function formatCodepoint(cp) {
  const hex = cp.toString(16).toUpperCase();
  return `U+${hex.padStart(4, "0")}`;
}

// src/runtime/create-runtime/runtime-logger.ts
var DEFAULT_LOG_LIMIT = 200;
var DEFAULT_WASM_LOG_FILTERS = [
  {
    re: /warning\\(stream\\): ignoring CSI .* t/i,
    note: "[wasm] note: CSI t window ops not implemented (safe to ignore)"
  },
  {
    re: /warning\\(stream\\): unknown CSI m with intermediate/i,
    note: "[wasm] note: CSI m intermediates ignored (safe to ignore)"
  }
];
function createRuntimeLogger(options) {
  const logEl = options.logEl ?? null;
  const onLog = options.onLog ?? null;
  const logLimit = options.logLimit ?? DEFAULT_LOG_LIMIT;
  const wasmLogFilters = options.wasmLogFilters ?? DEFAULT_WASM_LOG_FILTERS;
  const logBuffer = [];
  const wasmLogNotes = new Set;
  function appendLog(line) {
    const timestamp = new Date().toISOString().slice(11, 23);
    const entry = `${timestamp} ${line}`;
    logBuffer.push(entry);
    if (logBuffer.length > logLimit) {
      logBuffer.splice(0, logBuffer.length - logLimit);
    }
    if (logEl)
      logEl.textContent = line;
    onLog?.(entry);
  }
  function log(msg) {
    appendLog(`[ui] ${msg}`);
  }
  function shouldSuppressWasmLog(text2) {
    for (const filter of wasmLogFilters) {
      if (!filter.re.test(text2))
        continue;
      if (!wasmLogNotes.has(filter.note)) {
        wasmLogNotes.add(filter.note);
        appendLog(filter.note);
      }
      return true;
    }
    return false;
  }
  return {
    log,
    appendLog,
    shouldSuppressWasmLog
  };
}

// src/runtime/shader-stages.ts
var RESTTY_SHADER_STAGE_UNIFORM_CAP = 8;
var MODE_RANK = {
  "before-main": 0,
  "after-main": 1,
  "replace-main": 2
};
function isStageMode(value) {
  return value === "before-main" || value === "after-main" || value === "replace-main";
}
function isStageBackend(value) {
  return value === "webgpu" || value === "webgl2" || value === "both";
}
function cloneShaderStage(stage) {
  return {
    ...stage,
    shader: { ...stage.shader },
    uniforms: stage.uniforms ? [...stage.uniforms] : undefined
  };
}
function cloneShaderStages(stages) {
  return stages.map((stage) => cloneShaderStage(stage));
}
function normalizeShaderStage(stage) {
  if (!stage || typeof stage !== "object") {
    throw new Error("Restty shader stage must be an object");
  }
  const id = stage.id?.trim?.() ?? "";
  if (!id) {
    throw new Error("Restty shader stage id is required");
  }
  const mode = isStageMode(stage.mode) ? stage.mode : "after-main";
  const backend = isStageBackend(stage.backend) ? stage.backend : "both";
  const priority = Number.isFinite(stage.priority) ? Number(stage.priority) : 0;
  const enabled = stage.enabled ?? true;
  const uniforms = (stage.uniforms ?? []).filter((value) => Number.isFinite(value)).slice(0, RESTTY_SHADER_STAGE_UNIFORM_CAP);
  const shader = {
    wgsl: stage.shader?.wgsl?.trim?.() || undefined,
    glsl: stage.shader?.glsl?.trim?.() || undefined
  };
  return {
    id,
    mode,
    backend,
    priority,
    enabled: Boolean(enabled),
    uniforms,
    shader,
    onError: stage.onError
  };
}
function normalizeShaderStages(stages) {
  const normalized = [];
  const ids = new Set;
  for (let i3 = 0;i3 < stages.length; i3 += 1) {
    const stage = normalizeShaderStage(stages[i3]);
    if (ids.has(stage.id)) {
      throw new Error(`Restty shader stage id ${stage.id} is duplicated`);
    }
    ids.add(stage.id);
    normalized.push(stage);
  }
  return normalized;
}
function sortShaderStages(stages) {
  const indexed = stages.map((stage, index) => ({ stage, index }));
  indexed.sort((a3, b3) => {
    const modeDiff = MODE_RANK[a3.stage.mode ?? "after-main"] - MODE_RANK[b3.stage.mode ?? "after-main"];
    if (modeDiff !== 0)
      return modeDiff;
    const priorityDiff = (a3.stage.priority ?? 0) - (b3.stage.priority ?? 0);
    if (priorityDiff !== 0)
      return priorityDiff;
    return a3.index - b3.index;
  });
  return indexed.map((entry) => entry.stage);
}
function packShaderStageUniforms(stage) {
  const out = new Float32Array(RESTTY_SHADER_STAGE_UNIFORM_CAP);
  const input = stage.uniforms ?? [];
  const count = input.length > RESTTY_SHADER_STAGE_UNIFORM_CAP ? RESTTY_SHADER_STAGE_UNIFORM_CAP : input.length;
  for (let i3 = 0;i3 < count; i3 += 1) {
    const value = input[i3];
    if (Number.isFinite(value))
      out[i3] = Number(value);
  }
  return out;
}
function isShaderStageEnabledForBackend(stage, backend) {
  if (stage.enabled === false)
    return false;
  const target = stage.backend ?? "both";
  if (target === "both")
    return true;
  return target === backend;
}

// src/runtime/render-stage-shaders.ts
var STAGE_UNIFORM_BUFFER_FLOATS = 12;
var FULLSCREEN_STAGE_VERTEX_SHADER_GL = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_quad;
out vec2 v_uv;
void main() {
  v_uv = a_quad;
  vec2 clip = vec2(a_quad.x * 2.0 - 1.0, 1.0 - a_quad.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
}
`;
var FULLSCREEN_STAGE_SHADER_GL_PREFIX = `#version 300 es
precision highp float;
uniform sampler2D u_source;
uniform vec2 u_resolution;
uniform float u_time;
uniform vec4 u_params0;
uniform vec4 u_params1;
in vec2 v_uv;
out vec4 fragColor;
`;
var FULLSCREEN_STAGE_SHADER_GL_SUFFIX = `
void main() {
  vec4 color = texture(u_source, v_uv);
  fragColor = resttyStage(color, v_uv, u_time, u_params0, u_params1);
}
`;
var FULLSCREEN_STAGE_SHADER_WGSL_PREFIX = `
struct StageUniforms {
  resolution: vec2f,
  time: f32,
  _pad0: f32,
  params0: vec4f,
  params1: vec4f,
};

@group(0) @binding(0) var sourceSampler: sampler;
@group(0) @binding(1) var sourceTex: texture_2d<f32>;
@group(0) @binding(2) var<uniform> stageUniforms: StageUniforms;

struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
};

@vertex
fn vsMain(@location(0) quad: vec2f) -> VSOut {
  var out: VSOut;
  out.uv = quad;
  let clip = vec2f(quad.x * 2.0 - 1.0, 1.0 - quad.y * 2.0);
  out.position = vec4f(clip.x, clip.y, 0.0, 1.0);
  return out;
}
`;
var FULLSCREEN_STAGE_SHADER_WGSL_SUFFIX = `
@fragment
fn fsMain(input: VSOut) -> @location(0) vec4f {
  let color = textureSample(sourceTex, sourceSampler, input.uv);
  return resttyStage(
    color,
    input.uv,
    stageUniforms.time,
    stageUniforms.params0,
    stageUniforms.params1,
  );
}
`;

// src/runtime/render-stage-runtime.ts
function compileShaderStageProgram(options) {
  const { gl, stage, reportError } = options;
  const createShader = (type, source) => {
    const shader = gl.createShader(type);
    if (!shader)
      return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader) ?? "compile error";
      reportError(stage, `GLSL compile failed: ${error}`);
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  };
  const vert = createShader(gl.VERTEX_SHADER, FULLSCREEN_STAGE_VERTEX_SHADER_GL);
  const frag = createShader(gl.FRAGMENT_SHADER, `${FULLSCREEN_STAGE_SHADER_GL_PREFIX}${stage.shader.glsl ?? ""}${FULLSCREEN_STAGE_SHADER_GL_SUFFIX}`);
  if (!vert || !frag) {
    if (vert)
      gl.deleteShader(vert);
    if (frag)
      gl.deleteShader(frag);
    return null;
  }
  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    reportError(stage, "GLSL link failed: program allocation failed");
    return null;
  }
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const error = gl.getProgramInfoLog(program) ?? "link error";
    reportError(stage, `GLSL link failed: ${error}`);
    gl.deleteProgram(program);
    return null;
  }
  const sourceLoc = gl.getUniformLocation(program, "u_source");
  const resolutionLoc = gl.getUniformLocation(program, "u_resolution");
  const timeLoc = gl.getUniformLocation(program, "u_time");
  const params0Loc = gl.getUniformLocation(program, "u_params0");
  const params1Loc = gl.getUniformLocation(program, "u_params1");
  if (!sourceLoc || !resolutionLoc || !timeLoc || !params0Loc || !params1Loc) {
    gl.deleteProgram(program);
    reportError(stage, "GLSL link failed: required uniforms are missing");
    return null;
  }
  return {
    stage,
    program,
    sourceLoc,
    resolutionLoc,
    timeLoc,
    params0Loc,
    params1Loc,
    params: packShaderStageUniforms(stage)
  };
}
function createWebGLStageTexture(gl, width, height) {
  const texture = gl.createTexture();
  if (!texture)
    return null;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return texture;
}
function createWebGLStageFramebuffer(gl, texture) {
  const fb = gl.createFramebuffer();
  if (!fb)
    return null;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteFramebuffer(fb);
    return null;
  }
  return fb;
}
function createWebGLStageTargets(gl, width, height) {
  const quadBuffer = gl.createBuffer();
  const quadVao = gl.createVertexArray();
  if (!quadBuffer || !quadVao)
    return null;
  const quadVertices = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);
  gl.bindVertexArray(quadVao);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  const sceneTexture = createWebGLStageTexture(gl, width, height);
  const pingTexture = createWebGLStageTexture(gl, width, height);
  const pongTexture = createWebGLStageTexture(gl, width, height);
  if (!sceneTexture || !pingTexture || !pongTexture) {
    return null;
  }
  const sceneFramebuffer = createWebGLStageFramebuffer(gl, sceneTexture);
  const pingFramebuffer = createWebGLStageFramebuffer(gl, pingTexture);
  const pongFramebuffer = createWebGLStageFramebuffer(gl, pongTexture);
  if (!sceneFramebuffer || !pingFramebuffer || !pongFramebuffer) {
    return null;
  }
  return {
    width,
    height,
    quadVao,
    quadBuffer,
    sceneTexture,
    sceneFramebuffer,
    pingTexture,
    pingFramebuffer,
    pongTexture,
    pongFramebuffer
  };
}
function compileShaderStagePipelineWebGPU(options) {
  const { device, format, stage, reportError } = options;
  const shaderSource = `${FULLSCREEN_STAGE_SHADER_WGSL_PREFIX}${stage.shader.wgsl ?? ""}${FULLSCREEN_STAGE_SHADER_WGSL_SUFFIX}`;
  const formatError = (error) => {
    if (error instanceof Error)
      return error.message;
    return String(error);
  };
  try {
    const module = device.createShaderModule({ code: shaderSource });
    const pipeline = device.createRenderPipeline({
      layout: "auto",
      vertex: {
        module,
        entryPoint: "vsMain",
        buffers: [
          { arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: "float32x2" }] }
        ]
      },
      fragment: {
        module,
        entryPoint: "fsMain",
        targets: [{ format }]
      },
      primitive: { topology: "triangle-list", cullMode: "none" }
    });
    const uniformBuffer = device.createBuffer({
      size: STAGE_UNIFORM_BUFFER_FLOATS * 4,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });
    const sampler = device.createSampler({
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge"
    });
    return {
      stage,
      pipeline,
      uniformBuffer,
      uniformData: new Float32Array(STAGE_UNIFORM_BUFFER_FLOATS),
      params: packShaderStageUniforms(stage),
      sampler,
      bindGroupScene: null,
      bindGroupPing: null,
      bindGroupPong: null
    };
  } catch (error) {
    reportError(stage, `WGSL compile failed: ${formatError(error)}`);
    return null;
  }
}
function createWebGPUStageTargets(device, format, width, height) {
  const usage = GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING;
  const sceneTexture = device.createTexture({
    size: [width, height, 1],
    format,
    usage
  });
  const pingTexture = device.createTexture({
    size: [width, height, 1],
    format,
    usage
  });
  const pongTexture = device.createTexture({
    size: [width, height, 1],
    format,
    usage
  });
  return {
    width,
    height,
    sceneTexture,
    sceneView: sceneTexture.createView(),
    pingTexture,
    pingView: pingTexture.createView(),
    pongTexture,
    pongView: pongTexture.createView()
  };
}
function rebuildWebGPUStageBindGroups(device, compiledStages, targets) {
  for (let i3 = 0;i3 < compiledStages.length; i3 += 1) {
    const stage = compiledStages[i3];
    const layout = stage.pipeline.getBindGroupLayout(0);
    stage.bindGroupScene = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: stage.sampler },
        { binding: 1, resource: targets.sceneView },
        { binding: 2, resource: { buffer: stage.uniformBuffer } }
      ]
    });
    stage.bindGroupPing = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: stage.sampler },
        { binding: 1, resource: targets.pingView },
        { binding: 2, resource: { buffer: stage.uniformBuffer } }
      ]
    });
    stage.bindGroupPong = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: stage.sampler },
        { binding: 1, resource: targets.pongView },
        { binding: 2, resource: { buffer: stage.uniformBuffer } }
      ]
    });
  }
}

// src/runtime/create-runtime/shader-stage-runtime.ts
function createShaderStageRuntime(options) {
  let shaderStages = [];
  let compiledWebGPUShaderStages = [];
  let compiledWebGLShaderStages = [];
  let webgpuStageTargets = null;
  let webglStageTargets = null;
  let shaderStagesDirty = true;
  function reportShaderStageError(stage, message) {
    const text2 = `[shader-stage:${stage.id}] ${message}`;
    options.appendLog(text2);
    console.warn(text2);
    try {
      stage.onError?.(text2);
    } catch {
    }
  }
  function parseShaderStages(stages) {
    return sortShaderStages(normalizeShaderStages(cloneShaderStages(stages)));
  }
  function getActiveShaderStagesForBackend(target) {
    const out = [];
    for (let i3 = 0;i3 < shaderStages.length; i3 += 1) {
      const stage = shaderStages[i3];
      if (!isShaderStageEnabledForBackend(stage, target))
        continue;
      if (stage.mode === "replace-main") {
        reportShaderStageError(stage, "replace-main is not supported yet; stage skipped");
        continue;
      }
      if (target === "webgpu" && !stage.shader.wgsl) {
        reportShaderStageError(stage, "missing WGSL source for webgpu backend; stage skipped");
        continue;
      }
      if (target === "webgl2" && !stage.shader.glsl) {
        reportShaderStageError(stage, "missing GLSL source for webgl2 backend; stage skipped");
        continue;
      }
      out.push(stage);
    }
    return out;
  }
  function clearWebGPUShaderStages() {
    for (let i3 = 0;i3 < compiledWebGPUShaderStages.length; i3 += 1) {
      try {
        compiledWebGPUShaderStages[i3].uniformBuffer.destroy();
      } catch {
      }
    }
    compiledWebGPUShaderStages = [];
  }
  function clearWebGLShaderStages(state2) {
    const gl = state2?.gl ?? options.getActiveWebGLState()?.gl ?? null;
    if (!gl) {
      compiledWebGLShaderStages = [];
      return;
    }
    for (let i3 = 0;i3 < compiledWebGLShaderStages.length; i3 += 1) {
      gl.deleteProgram(compiledWebGLShaderStages[i3].program);
    }
    compiledWebGLShaderStages = [];
  }
  function destroyWebGPUStageTargets() {
    if (!webgpuStageTargets)
      return;
    try {
      webgpuStageTargets.sceneTexture.destroy();
      webgpuStageTargets.pingTexture.destroy();
      webgpuStageTargets.pongTexture.destroy();
    } catch {
    }
    webgpuStageTargets = null;
  }
  function destroyWebGLStageTargets(state2) {
    if (!webglStageTargets)
      return;
    const gl = state2?.gl ?? options.getActiveWebGLState()?.gl ?? null;
    if (gl) {
      gl.deleteVertexArray(webglStageTargets.quadVao);
      gl.deleteBuffer(webglStageTargets.quadBuffer);
      gl.deleteFramebuffer(webglStageTargets.sceneFramebuffer);
      gl.deleteFramebuffer(webglStageTargets.pingFramebuffer);
      gl.deleteFramebuffer(webglStageTargets.pongFramebuffer);
      gl.deleteTexture(webglStageTargets.sceneTexture);
      gl.deleteTexture(webglStageTargets.pingTexture);
      gl.deleteTexture(webglStageTargets.pongTexture);
    }
    webglStageTargets = null;
  }
  function ensureWebGLStageTargets(state2) {
    const { width, height } = options.getCanvasSize();
    if (webglStageTargets && webglStageTargets.width === width && webglStageTargets.height === height) {
      return webglStageTargets;
    }
    destroyWebGLStageTargets(state2);
    webglStageTargets = createWebGLStageTargets(state2.gl, width, height);
    return webglStageTargets;
  }
  function ensureWebGPUStageTargets(state2) {
    const { width, height } = options.getCanvasSize();
    if (webgpuStageTargets && webgpuStageTargets.width === width && webgpuStageTargets.height === height) {
      return webgpuStageTargets;
    }
    destroyWebGPUStageTargets();
    webgpuStageTargets = createWebGPUStageTargets(state2.device, state2.format, width, height);
    if (compiledWebGPUShaderStages.length) {
      rebuildWebGPUStageBindGroups(state2.device, compiledWebGPUShaderStages, webgpuStageTargets);
    }
    return webgpuStageTargets;
  }
  function rebuildWebGPUShaderStages(state2) {
    clearWebGPUShaderStages();
    const nextStages = getActiveShaderStagesForBackend("webgpu");
    for (let i3 = 0;i3 < nextStages.length; i3 += 1) {
      const compiled = compileShaderStagePipelineWebGPU({
        device: state2.device,
        format: state2.format,
        stage: nextStages[i3],
        reportError: reportShaderStageError
      });
      if (compiled)
        compiledWebGPUShaderStages.push(compiled);
    }
    if (!compiledWebGPUShaderStages.length) {
      destroyWebGPUStageTargets();
      return;
    }
    const targets = ensureWebGPUStageTargets(state2);
    if (!targets)
      return;
    rebuildWebGPUStageBindGroups(state2.device, compiledWebGPUShaderStages, targets);
  }
  function rebuildWebGLShaderStages(state2) {
    clearWebGLShaderStages(state2);
    const nextStages = getActiveShaderStagesForBackend("webgl2");
    for (let i3 = 0;i3 < nextStages.length; i3 += 1) {
      const compiled = compileShaderStageProgram({
        gl: state2.gl,
        stage: nextStages[i3],
        reportError: reportShaderStageError
      });
      if (compiled)
        compiledWebGLShaderStages.push(compiled);
    }
    if (!compiledWebGLShaderStages.length) {
      destroyWebGLStageTargets(state2);
    }
  }
  function setShaderStages(stages) {
    try {
      shaderStages = parseShaderStages(stages ?? []);
    } catch (error) {
      const text2 = `[shader-stage] invalid configuration: ${error instanceof Error ? error.message : String(error)}`;
      options.appendLog(text2);
      console.warn(text2);
      shaderStages = [];
    }
    shaderStagesDirty = true;
    options.onShaderStagesChanged();
  }
  return {
    setShaderStages,
    getShaderStages: () => cloneShaderStages(shaderStages),
    isShaderStagesDirty: () => shaderStagesDirty,
    setShaderStagesDirty: (value) => {
      shaderStagesDirty = value;
    },
    getCompiledWebGPUShaderStages: () => compiledWebGPUShaderStages,
    getCompiledWebGLShaderStages: () => compiledWebGLShaderStages,
    clearWebGPUShaderStages,
    clearWebGLShaderStages,
    destroyWebGPUStageTargets,
    destroyWebGLStageTargets,
    ensureWebGPUStageTargets,
    ensureWebGLStageTargets,
    rebuildWebGPUShaderStages,
    rebuildWebGLShaderStages
  };
}

// src/runtime/create-runtime/color-glyph-atlas.ts
var COLOR_EMOJI_FONT_STACK = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","EmojiOne Color","Twemoji Mozilla",sans-serif';
function createColorGlyphAtlasHelpers(options) {
  const { pixelModeRgba, atlasToRGBA } = options;
  let colorGlyphCanvas = null;
  let colorGlyphCtx = null;
  const atlasBitmapToRGBA2 = (atlas) => atlasBitmapToRGBA(atlas, pixelModeRgba, atlasToRGBA);
  function getColorGlyphContext() {
    if (colorGlyphCtx)
      return colorGlyphCtx;
    if (typeof document === "undefined")
      return null;
    colorGlyphCanvas = document.createElement("canvas");
    colorGlyphCtx = colorGlyphCanvas.getContext("2d", { willReadFrequently: true });
    return colorGlyphCtx;
  }
  function resolveColorGlyphFontCss(entry, fontSize) {
    const label = String(entry.label ?? "").split("(")[0].trim().replace(/"/g, "\\\"");
    const families = [];
    if (label && !/openmoji/i.test(label)) {
      families.push(`"${label}"`);
    }
    families.push(COLOR_EMOJI_FONT_STACK);
    return `${Math.max(1, Math.round(fontSize))}px ${families.join(",")}`;
  }
  function rasterizeColorGlyphWithCanvas(entry, text2, fontSize) {
    if (!text2)
      return null;
    const ctx = getColorGlyphContext();
    if (!ctx)
      return null;
    const fontCss = resolveColorGlyphFontCss(entry, fontSize);
    ctx.save();
    ctx.font = fontCss;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    const metrics = ctx.measureText(text2);
    const left = Math.max(0, metrics.actualBoundingBoxLeft ?? 0);
    const right = Math.max(1, metrics.actualBoundingBoxRight ?? metrics.width ?? 1);
    const ascent = Math.max(1, metrics.actualBoundingBoxAscent ?? fontSize * 0.8);
    const descent = Math.max(0, metrics.actualBoundingBoxDescent ?? fontSize * 0.2);
    const width = Math.max(1, Math.ceil(left + right + 1));
    const height = Math.max(1, Math.ceil(ascent + descent + 1));
    if (!colorGlyphCanvas) {
      ctx.restore();
      return null;
    }
    if (colorGlyphCanvas.width !== width || colorGlyphCanvas.height !== height) {
      colorGlyphCanvas.width = width;
      colorGlyphCanvas.height = height;
    }
    ctx.clearRect(0, 0, width, height);
    ctx.font = fontCss;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.fillStyle = "#fff";
    ctx.fillText(text2, left, ascent);
    const image = ctx.getImageData(0, 0, width, height);
    ctx.restore();
    return {
      bitmap: {
        width,
        rows: height,
        pitch: width * 4,
        buffer: new Uint8Array(image.data),
        pixelMode: pixelModeRgba,
        numGrays: 256
      },
      bearingX: -left,
      bearingY: ascent
    };
  }
  const buildColorEmojiAtlasWithCanvas = (options2) => {
    const {
      font,
      fontEntry,
      glyphIds,
      fontSize,
      sizeMode,
      padding,
      maxWidth,
      maxHeight,
      pixelMode
    } = options2;
    if (pixelMode !== pixelModeRgba && pixelMode !== 4)
      return null;
    if (!fontEntry.colorGlyphTexts?.size)
      return null;
    const scale = resolveFontScaleForAtlas(font, fontSize, sizeMode);
    const glyphData = [];
    for (let i3 = 0;i3 < glyphIds.length; i3 += 1) {
      const glyphId = glyphIds[i3];
      const text2 = fontEntry.colorGlyphTexts.get(glyphId);
      if (!text2)
        continue;
      const raster = rasterizeColorGlyphWithCanvas(fontEntry, text2, fontSize);
      if (!raster)
        continue;
      glyphData.push({
        glyphId,
        bitmap: raster.bitmap,
        bearingX: raster.bearingX,
        bearingY: raster.bearingY,
        advance: font.advanceWidth(glyphId) * scale
      });
    }
    if (!glyphData.length)
      return null;
    glyphData.sort((a3, b3) => (b3.bitmap?.rows ?? 0) - (a3.bitmap?.rows ?? 0));
    const {
      width: atlasWidth,
      height: atlasHeight,
      placements
    } = packGlyphs(glyphData.map((glyph) => ({
      width: (glyph.bitmap?.width ?? 0) + padding * 2,
      height: (glyph.bitmap?.rows ?? 0) + padding * 2
    })), maxWidth, maxHeight);
    const atlasBitmap = createAtlasBitmap(atlasWidth, atlasHeight, pixelModeRgba);
    const glyphMetrics = new Map;
    for (let i3 = 0;i3 < glyphData.length; i3 += 1) {
      const glyph = glyphData[i3];
      const placement = placements[i3];
      if (!placement?.placed || !glyph.bitmap)
        continue;
      copyBitmapToAtlas(glyph.bitmap, atlasBitmap, placement.x + padding, placement.y + padding);
      glyphMetrics.set(glyph.glyphId, {
        glyphId: glyph.glyphId,
        atlasX: placement.x + padding,
        atlasY: placement.y + padding,
        width: glyph.bitmap.width,
        height: glyph.bitmap.rows,
        bearingX: glyph.bearingX,
        bearingY: glyph.bearingY,
        advance: glyph.advance
      });
    }
    return {
      atlas: {
        bitmap: atlasBitmap,
        glyphs: glyphMetrics,
        glyphsByWidth: new Map,
        fontSize,
        colorGlyphs: new Set(glyphMetrics.keys())
      },
      constrainedGlyphWidths: null
    };
  };
  const destroy = () => {
    if (colorGlyphCanvas) {
      colorGlyphCanvas.width = 0;
      colorGlyphCanvas.height = 0;
    }
    colorGlyphCtx = null;
    colorGlyphCanvas = null;
  };
  return {
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    buildColorEmojiAtlasWithCanvas,
    destroy
  };
}

// src/runtime/create-runtime/debug-tools/diagnose-codepoint.ts
function createDiagnoseCodepoint(options) {
  const {
    formatCodepoint: formatCodepoint2,
    isNerdSymbolCodepoint: isNerdSymbolCodepoint2,
    isSymbolCp: isSymbolCp2,
    fontState,
    isSymbolFont: isSymbolFont2,
    fontHasGlyph: fontHasGlyph2,
    pickFontIndexForText: pickFontIndexForText2,
    shapeClusterWithFont,
    getNerdConstraint: getNerdConstraint2
  } = options;
  return function diagnoseCodepoint(cp) {
    console.group(`Diagnosing codepoint ${formatCodepoint2(cp)}`);
    const isNerd = isNerdSymbolCodepoint2(cp);
    const isSymbol = isSymbolCp2(cp);
    console.log(`isNerdSymbolCodepoint: ${isNerd}, isSymbolCp: ${isSymbol}`);
    console.log(`Total fonts loaded: ${fontState.fonts.length}`);
    fontState.fonts.forEach((entry, idx) => {
      if (!entry?.font) {
        console.log(`  Font ${idx}: not loaded`);
        return;
      }
      const label = entry.label || "unknown";
      const isSym = isSymbolFont2(entry);
      const text3 = String.fromCodePoint(cp);
      const hasGlyph = fontHasGlyph2(entry.font, text3);
      const glyphId = entry.font.glyphIdForChar(text3);
      console.log(`  Font ${idx}: "${label}" isSymbolFont=${isSym} hasGlyph=${hasGlyph} glyphId=${glyphId}`);
    });
    const text2 = String.fromCodePoint(cp);
    const pickedIndex = pickFontIndexForText2(text2, 1);
    const pickedEntry = fontState.fonts[pickedIndex];
    console.log(`Picked font index: ${pickedIndex} (${pickedEntry?.label || "none"})`);
    if (pickedEntry?.font) {
      const shaped = shapeClusterWithFont(pickedEntry, text2);
      console.log(`Shaped glyphs: ${shaped.glyphs.length}, advance: ${shaped.advance}`);
      shaped.glyphs.forEach((glyph, index) => {
        console.log(`  Glyph ${index}: id=${glyph.glyphId} xAdvance=${glyph.xAdvance} xOffset=${glyph.xOffset} yOffset=${glyph.yOffset}`);
      });
    }
    const constraint = getNerdConstraint2(cp);
    console.log("Nerd constraint:", constraint || "none");
    console.groupEnd();
  };
}

// src/runtime/create-runtime/debug-tools/dump-atlas-for-codepoint.ts
function createDumpAtlasForCodepoint(options) {
  const {
    getActiveState,
    atlasCanvas,
    atlasInfoEl,
    pickFontIndexForText: pickFontIndexForText2,
    fontState,
    formatCodepoint: formatCodepoint2,
    ensureAtlasForFont,
    gridState,
    fontConfig,
    isSymbolFont: isSymbolFont2,
    symbolAtlasPadding,
    atlasPadding,
    pixelModeGray,
    pixelModeRgba
  } = options;
  return function dumpAtlasForCodepoint(cp) {
    if (!atlasCanvas || !atlasInfoEl)
      return;
    const state2 = getActiveState();
    if (!state2 || !("device" in state2)) {
      atlasInfoEl.textContent = "atlas debug unavailable (renderer not ready)";
      return;
    }
    const entryText = String.fromCodePoint(cp);
    const fontIndex = pickFontIndexForText2(entryText, 1);
    const entry = fontState.fonts[fontIndex];
    if (!entry?.font) {
      atlasInfoEl.textContent = "font not ready";
      return;
    }
    const glyphId = entry.font.glyphIdForChar(entryText);
    if (!glyphId) {
      atlasInfoEl.textContent = `missing glyph for ${formatCodepoint2(cp)}`;
      return;
    }
    const atlasScale = entry.atlasScale ?? 1;
    ensureAtlasForFont(state2.device, state2, entry, new Set([glyphId]), gridState.fontSizePx || fontConfig.sizePx, fontIndex, atlasScale);
    const atlas = entry.atlas;
    if (!atlas) {
      atlasInfoEl.textContent = "atlas missing";
      return;
    }
    const metrics = atlas.glyphs.get(glyphId);
    if (!metrics) {
      atlasInfoEl.textContent = `glyph not in atlas (${formatCodepoint2(cp)})`;
      return;
    }
    const width = Math.max(1, metrics.width);
    const height = Math.max(1, metrics.height);
    atlasCanvas.width = width;
    atlasCanvas.height = height;
    const ctx = atlasCanvas.getContext("2d");
    if (!ctx)
      return;
    ctx.imageSmoothingEnabled = false;
    const imageData = atlasRegionToImageData(atlas, metrics.atlasX, metrics.atlasY, width, height, pixelModeGray, pixelModeRgba);
    ctx.putImageData(imageData, 0, 0);
    atlasInfoEl.textContent = [
      `cp ${formatCodepoint2(cp)} glyph ${glyphId}`,
      `font ${fontIndex}: ${entry.label ?? "unknown"}`,
      `atlas ${atlas.bitmap.width}x${atlas.bitmap.rows} pad ${isSymbolFont2(entry) ? symbolAtlasPadding : atlasPadding}`,
      `glyph ${metrics.width}x${metrics.height} bearing ${metrics.bearingX},${metrics.bearingY}`
    ].join(`
`);
  };
}

// src/runtime/create-runtime/debug-tools/read-texture-to-image-data.ts
async function readTextureToImageData(device, texture, width, height, origin) {
  const bytesPerRow = width * 4;
  const alignedBytesPerRow = Math.ceil(bytesPerRow / 256) * 256;
  const buffer = device.createBuffer({
    size: alignedBytesPerRow * height,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
  });
  const encoder = device.createCommandEncoder();
  encoder.copyTextureToBuffer({ texture, origin }, { buffer, bytesPerRow: alignedBytesPerRow, rowsPerImage: height }, { width, height, depthOrArrayLayers: 1 });
  device.queue.submit([encoder.finish()]);
  await buffer.mapAsync(GPUMapMode.READ);
  const mapped = new Uint8Array(buffer.getMappedRange());
  const out = new Uint8ClampedArray(width * height * 4);
  for (let row = 0;row < height; row += 1) {
    const srcStart = row * alignedBytesPerRow;
    const srcEnd = srcStart + bytesPerRow;
    const dstStart = row * bytesPerRow;
    out.set(mapped.subarray(srcStart, srcEnd), dstStart);
  }
  buffer.unmap();
  return new ImageData(out, width, height);
}

// src/runtime/create-runtime/debug-tools/create-dump-glyph-render.ts
function createDumpGlyphRender(options) {
  const {
    getActiveState,
    pickFontIndexForText: pickFontIndexForText2,
    fontState,
    getCanvas,
    gridState,
    fontConfig,
    fontHeightUnits: fontHeightUnits2,
    fontScaleOverride: fontScaleOverride2,
    fontScaleOverrides,
    isSymbolFont: isSymbolFont2,
    isColorEmojiFont: isColorEmojiFont2,
    fontAdvanceUnits: fontAdvanceUnits2,
    fontMaxCellSpan: fontMaxCellSpan2,
    clamp: clamp2,
    buildNerdMetrics: buildNerdMetrics2,
    nerdIconScale,
    ensureAtlasForFont,
    formatCodepoint: formatCodepoint2,
    shapeClusterWithFont
  } = options;
  return async function dumpGlyphRender(cp, constraintWidth = 1) {
    const state2 = getActiveState();
    if (!state2 || !("device" in state2)) {
      console.warn("WebGPU not active");
      return null;
    }
    const text2 = String.fromCodePoint(cp);
    const span = Math.max(1, constraintWidth || 1);
    const fontIndex = pickFontIndexForText2(text2, span);
    const entry = fontState.fonts[fontIndex];
    if (!entry?.font) {
      console.warn("font not ready");
      return null;
    }
    const glyphId = entry.font.glyphIdForChar(text2);
    if (!glyphId) {
      console.warn("missing glyph");
      return null;
    }
    const runtimeCanvas = getCanvas();
    const cellW = gridState.cellW || runtimeCanvas.width / Math.max(1, gridState.cols || 1);
    const cellH = gridState.cellH || runtimeCanvas.height / Math.max(1, gridState.rows || 1);
    const fontSizePx = gridState.fontSizePx || fontConfig.sizePx;
    const primaryEntry = fontState.fonts[0];
    const primaryScale = primaryEntry?.font ? primaryEntry.font.scaleForSize(fontSizePx, fontState.sizeMode) : 1;
    const lineHeight = primaryEntry?.font ? fontHeightUnits2(primaryEntry.font) * primaryScale : cellH;
    const baselineOffset = primaryEntry?.font ? primaryEntry.font.ascender * primaryScale : 0;
    const yPad = gridState.yPad ?? (cellH - lineHeight) * 0.5;
    const baseScale = entry.font.scaleForSize(fontSizePx, fontState.sizeMode) * fontScaleOverride2(entry, fontScaleOverrides);
    let fontScale = baseScale;
    let metricBaselineAdjust = null;
    if (!isSymbolFont2(entry) && !isColorEmojiFont2(entry)) {
      const resolveFallbackMetric = (font, metric) => {
        if (!font)
          return 0;
        if (metric === "ic_width") {
          const ideographGlyph = font.glyphIdForChar("水");
          if (!ideographGlyph)
            return 0;
          const advance = font.advanceWidth(ideographGlyph);
          if (!Number.isFinite(advance) || advance <= 0)
            return 0;
          const bounds = font.getGlyphBounds(ideographGlyph);
          if (bounds && Number.isFinite(bounds.xMax - bounds.xMin) && bounds.xMax - bounds.xMin > advance) {
            return 0;
          }
          return advance;
        }
        if (metric === "ex_height") {
          const exHeight = font.os2?.sxHeight ?? 0;
          return Number.isFinite(exHeight) && exHeight > 0 ? exHeight : 0;
        }
        if (metric === "cap_height") {
          const capHeight = font.os2?.sCapHeight ?? 0;
          return Number.isFinite(capHeight) && capHeight > 0 ? capHeight : 0;
        }
        const lineHeightUnits = font.height;
        return Number.isFinite(lineHeightUnits) && lineHeightUnits > 0 ? lineHeightUnits : 0;
      };
      const metricOrder = [
        "ic_width",
        "ex_height",
        "cap_height",
        "line_height"
      ];
      let metricAdjust = 1;
      for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
        const metric = metricOrder[i3];
        const primaryMetric = resolveFallbackMetric(primaryEntry?.font, metric);
        const fallbackMetric = resolveFallbackMetric(entry.font, metric);
        if (primaryMetric <= 0 || fallbackMetric <= 0)
          continue;
        const factor = primaryMetric / fallbackMetric;
        if (Number.isFinite(factor) && factor > 0) {
          metricAdjust = factor;
          break;
        }
      }
      metricAdjust = clamp2(metricAdjust, 1, 2);
      fontScale = baseScale * metricAdjust;
      const maxSpan = fontMaxCellSpan2(entry);
      if (maxSpan > 1) {
        const advanceUnits = fontAdvanceUnits2(entry, shapeClusterWithFont);
        const widthPx = advanceUnits * fontScale;
        const widthAdjustRaw = widthPx > 0 ? cellW * maxSpan / widthPx : 1;
        const widthAdjust = clamp2(widthAdjustRaw, 0.5, 2);
        fontScale *= widthAdjust;
      }
      const adjustedHeightPx = fontHeightUnits2(entry.font) * fontScale;
      if (adjustedHeightPx > lineHeight && adjustedHeightPx > 0) {
        fontScale *= lineHeight / adjustedHeightPx;
      }
      for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
        const metric = metricOrder[i3];
        const primaryMetric = resolveFallbackMetric(primaryEntry?.font, metric);
        const fallbackMetric = resolveFallbackMetric(entry.font, metric);
        if (primaryMetric <= 0 || fallbackMetric <= 0)
          continue;
        metricBaselineAdjust = primaryMetric * primaryScale - fallbackMetric * fontScale;
        break;
      }
    }
    const baselineAdjust = metricBaselineAdjust ?? (primaryEntry?.font ? primaryEntry.font.ascender * primaryScale - entry.font.ascender * fontScale : 0);
    const atlasScale = clamp2(fontScale / (baseScale || 1), 0.5, 2);
    const meta = new Map;
    meta.set(glyphId, {
      cp,
      constraintWidth: span,
      widths: new Set([span]),
      variable: false
    });
    const constraintContext = {
      cellW,
      cellH,
      yPad,
      baselineOffset,
      baselineAdjust,
      fontScale,
      nerdMetrics: buildNerdMetrics2(cellW, cellH, lineHeight, primaryEntry?.font, primaryScale, nerdIconScale),
      fontEntry: entry
    };
    ensureAtlasForFont(state2.device, state2, entry, new Set([glyphId]), fontSizePx, fontIndex, atlasScale, meta, constraintContext);
    const atlas = entry.atlas;
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!atlas || !atlasState) {
      console.warn("atlas not ready");
      return null;
    }
    const widthMap = atlas.glyphsByWidth?.get(span);
    const metrics = widthMap?.get(glyphId) ?? atlas.glyphs.get(glyphId);
    if (!metrics) {
      console.warn("metrics missing");
      return null;
    }
    const atlasW = atlas.bitmap.width;
    const atlasH = atlas.bitmap.rows;
    const baseInset = Number.isFinite(atlas.inset) ? atlas.inset : 0;
    const uvInset = baseInset + (atlasState.nearest ? 0.5 : 0);
    const insetX = Math.min(uvInset, (metrics.width - 1) * 0.5);
    const insetY = Math.min(uvInset, (metrics.height - 1) * 0.5);
    const u02 = (metrics.atlasX + insetX) / atlasW;
    const v02 = (metrics.atlasY + insetY) / atlasH;
    const u12 = (metrics.atlasX + metrics.width - insetX) / atlasW;
    const v12 = (metrics.atlasY + metrics.height - insetY) / atlasH;
    const outW = Math.max(1, metrics.width);
    const outH = Math.max(1, metrics.height);
    const uniformBuffer = state2.device.createBuffer({
      size: 8 * 4,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });
    const uniforms = new Float32Array([outW, outH, 0, 0, 0, 0, 0, 0]);
    state2.device.queue.writeBuffer(uniformBuffer, 0, uniforms);
    const instance = new Float32Array([0, 0, outW, outH, u02, v02, u12, v12, 1, 1, 1, 1, 0, 0, 0, 1]);
    const instanceBuffer = state2.device.createBuffer({
      size: instance.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true
    });
    new Float32Array(instanceBuffer.getMappedRange()).set(instance);
    instanceBuffer.unmap();
    const renderTarget = state2.device.createTexture({
      size: [outW, outH, 1],
      format: state2.format,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC
    });
    const pipeline = atlasState.nearest ? state2.glyphPipelineNearest : state2.glyphPipeline;
    const bindGroup = state2.device.createBindGroup({
      layout: pipeline.getBindGroupLayout(0),
      entries: atlasState.nearest ? [
        { binding: 0, resource: { buffer: uniformBuffer } },
        {
          binding: 1,
          resource: atlasState.samplerNearest ?? state2.device.createSampler({
            magFilter: "nearest",
            minFilter: "nearest",
            addressModeU: "clamp-to-edge",
            addressModeV: "clamp-to-edge"
          })
        },
        { binding: 2, resource: atlasState.texture.createView() }
      ] : [
        { binding: 0, resource: { buffer: uniformBuffer } },
        {
          binding: 1,
          resource: atlasState.sampler ?? state2.device.createSampler({
            magFilter: "linear",
            minFilter: "linear",
            addressModeU: "clamp-to-edge",
            addressModeV: "clamp-to-edge"
          })
        },
        { binding: 2, resource: atlasState.texture.createView() }
      ]
    });
    const encoder = state2.device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: renderTarget.createView(),
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: "clear",
          storeOp: "store"
        }
      ]
    });
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.setVertexBuffer(0, state2.vertexBuffer);
    pass.setVertexBuffer(1, instanceBuffer);
    pass.draw(6, 1, 0, 0);
    pass.end();
    state2.device.queue.submit([encoder.finish()]);
    const image = await readTextureToImageData(state2.device, renderTarget, outW, outH);
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (ctx)
      ctx.putImageData(image, 0, 0);
    canvas.style.border = "1px solid #555";
    canvas.style.margin = "6px";
    canvas.style.imageRendering = "pixelated";
    canvas.style.width = `${outW * 3}px`;
    canvas.style.height = `${outH * 3}px`;
    document.body.appendChild(canvas);
    console.log("dumpGlyphRender", {
      cp: formatCodepoint2(cp),
      fontIndex,
      glyphId,
      constraintWidth: span,
      metrics,
      atlasW,
      atlasH,
      format: state2.format,
      u0: u02,
      v0: v02,
      u1: u12,
      v1: v12,
      nearest: atlasState.nearest
    });
    return image;
  };
}

// src/runtime/create-runtime/debug-tools/setup-debug-expose.ts
function createSetupDebugExpose(options, diagnoseCodepoint) {
  const { debugExpose, getWindow } = options;
  return function setupDebugExpose() {
    if (!debugExpose)
      return;
    const runtimeWindow = getWindow();
    if (!runtimeWindow)
      return;
    const debugWindow = runtimeWindow;
    debugWindow.diagnoseCodepoint = diagnoseCodepoint;
    debugWindow.dumpGlyphMetrics = createDumpGlyphMetrics(options);
    debugWindow.dumpAtlasRegion = createDumpAtlasRegion(options);
    debugWindow.dumpGlyphRender = createDumpGlyphRender(options);
  };
  function createDumpGlyphMetrics({
    pickFontIndexForText: pickFontIndexForText2,
    fontState
  }) {
    return (cp) => {
      const text2 = String.fromCodePoint(cp);
      const fontIndex = pickFontIndexForText2(text2, 1);
      const entry = fontState.fonts[fontIndex];
      if (!entry?.font || !entry.atlas) {
        console.warn("font/atlas not ready");
        return null;
      }
      const glyphId = entry.font.glyphIdForChar(text2);
      const atlas = entry.atlas;
      const atlasW = atlas.bitmap.width;
      const atlasH = atlas.bitmap.rows;
      const report = (label, metrics) => {
        if (!metrics) {
          console.log(`${label}: missing`);
          return;
        }
        const u02 = metrics.atlasX / atlasW;
        const v02 = metrics.atlasY / atlasH;
        const u12 = (metrics.atlasX + metrics.width) / atlasW;
        const v12 = (metrics.atlasY + metrics.height) / atlasH;
        console.log(`${label}:`, {
          glyphId,
          atlasX: metrics.atlasX,
          atlasY: metrics.atlasY,
          width: metrics.width,
          height: metrics.height,
          bearingX: metrics.bearingX,
          bearingY: metrics.bearingY,
          u0: u02,
          v0: v02,
          u1: u12,
          v1: v12,
          atlasW,
          atlasH
        });
      };
      console.group(`Glyph metrics U+${cp.toString(16).toUpperCase()}`);
      report("default", atlas.glyphs.get(glyphId));
      if (atlas.glyphsByWidth) {
        report("width=1", atlas.glyphsByWidth.get(1)?.get(glyphId));
        report("width=2", atlas.glyphsByWidth.get(2)?.get(glyphId));
      }
      console.groupEnd();
      return { fontIndex, glyphId };
    };
  }
  function createDumpAtlasRegion({ getActiveState }) {
    return async (fontIndex, x3, y, width, height) => {
      const state2 = getActiveState();
      if (!state2 || !("device" in state2)) {
        console.warn("WebGPU not active");
        return null;
      }
      const atlasState = state2.glyphAtlases?.get(fontIndex);
      if (!atlasState) {
        console.warn("atlas not ready");
        return null;
      }
      const image = await readTextureToImageData(state2.device, atlasState.texture, width, height, {
        x: x3,
        y
      });
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx)
        ctx.putImageData(image, 0, 0);
      canvas.style.border = "1px solid #555";
      canvas.style.margin = "6px";
      document.body.appendChild(canvas);
      return image;
    };
  }
}

// src/runtime/create-runtime/debug-tools.ts
function createRuntimeDebugTools(options) {
  const dumpAtlasForCodepoint = createDumpAtlasForCodepoint(options);
  const diagnoseCodepoint = createDiagnoseCodepoint(options);
  const setupDebugExpose = createSetupDebugExpose(options, diagnoseCodepoint);
  return {
    dumpAtlasForCodepoint,
    setupDebugExpose
  };
}

// src/runtime/create-runtime/input-hooks.ts
function createRuntimeInputHooks(options) {
  const { beforeInputHook, beforeRenderOutputHook } = options;
  const bytesDecoder = new TextDecoder("utf-8", { fatal: false });
  function runHook(hook, text2, source, errorLabel) {
    if (!hook)
      return text2;
    try {
      const next = hook({ text: text2, source });
      if (next === null)
        return null;
      if (typeof next === "string")
        return next;
      return text2;
    } catch (error) {
      console.error(errorLabel, error);
      return text2;
    }
  }
  return {
    runBeforeInputHook: (text2, source) => runHook(beforeInputHook, text2, source, "[restty] beforeInput hook error:"),
    runBeforeRenderOutputHook: (text2, source) => runHook(beforeRenderOutputHook, text2, source, "[restty] beforeRenderOutput hook error:"),
    runBeforeRenderOutputBytesHook: (bytes, source) => {
      if (!beforeRenderOutputHook)
        return true;
      try {
        return beforeRenderOutputHook({
          text: bytesDecoder.decode(bytes),
          source,
          bytes
        }) !== null;
      } catch (error) {
        console.error("[restty] beforeRenderOutput hook error:", error);
        return true;
      }
    }
  };
}

// src/runtime/clipboard-paste.ts
function readPastePayloadFromDataTransfer(dataTransfer) {
  if (!dataTransfer)
    return null;
  const text2 = dataTransfer.getData("text/plain") || "";
  return text2 ? {
    kind: "text",
    text: text2
  } : null;
}

// src/runtime/create-runtime/pty-input-runtime.ts
function formatError(err) {
  if (err && typeof err === "object" && "message" in err) {
    return String(err.message);
  }
  return String(err);
}
function createPtyInputRuntime(options) {
  const {
    ptyTransport,
    ptyOutputBuffer,
    inputHandler,
    ptyStatusEl,
    mouseStatusEl,
    onPtyStatus,
    onMouseStatus,
    appendLog,
    getGridSize,
    getResizeMeta,
    getCursorForCpr,
    sendInput,
    runBeforeInputHook,
    shouldClearSelection,
    clearSelection: clearSelection2,
    syncOutputResetMs,
    syncOutputResetSeq
  } = options;
  let lastReportedPtyStatus = "";
  let lastReportedMouseStatus = "";
  let syncOutputResetTimer = 0;
  function setPtyStatus(text2) {
    if (text2 === lastReportedPtyStatus)
      return;
    lastReportedPtyStatus = text2;
    if (ptyStatusEl)
      ptyStatusEl.textContent = text2;
    onPtyStatus?.(text2);
  }
  function setMouseStatus(text2) {
    if (text2 === lastReportedMouseStatus)
      return;
    lastReportedMouseStatus = text2;
    if (mouseStatusEl)
      mouseStatusEl.textContent = text2;
    onMouseStatus?.(text2);
  }
  function updateMouseStatus() {
    const status = inputHandler.getMouseStatus();
    const label = status.active ? `${status.mode} (${status.detail})` : status.mode;
    setMouseStatus(label);
  }
  function cancelPtyOutputFlush() {
    ptyOutputBuffer.cancel();
  }
  function cancelSyncOutputReset() {
    if (syncOutputResetTimer) {
      clearTimeout(syncOutputResetTimer);
      syncOutputResetTimer = 0;
    }
  }
  function scheduleSyncOutputReset() {
    if (syncOutputResetTimer)
      return;
    syncOutputResetTimer = setTimeout(() => {
      syncOutputResetTimer = 0;
      if (!inputHandler.isSynchronizedOutput())
        return;
      const sanitized = inputHandler.filterOutput(syncOutputResetSeq) || syncOutputResetSeq;
      sendInput(sanitized, "pty");
    }, syncOutputResetMs);
  }
  function flushPtyOutputBuffer() {
    ptyOutputBuffer.flush();
  }
  function queuePtyOutput(text2) {
    ptyOutputBuffer.queue(text2);
  }
  function queuePtyOutputBytes(data) {
    ptyOutputBuffer.queueBytes(data);
  }
  function disconnectPty2() {
    flushPtyOutputBuffer();
    cancelPtyOutputFlush();
    cancelSyncOutputReset();
    ptyOutputBuffer.clear();
    ptyTransport.disconnect();
    updateMouseStatus();
    setPtyStatus("disconnected");
  }
  function connectPty2(url = "") {
    if (ptyTransport.isConnected())
      return;
    const initialGrid = getGridSize();
    setPtyStatus("connecting...");
    try {
      const connectResult = ptyTransport.connect({
        url,
        cols: initialGrid.cols || 80,
        rows: initialGrid.rows || 24,
        callbacks: {
          onConnect: () => {
            setPtyStatus("connected");
            updateMouseStatus();
            const connectedGrid = getGridSize();
            if (connectedGrid.cols && connectedGrid.rows) {
              ptyTransport.resize(connectedGrid.cols, connectedGrid.rows, getResizeMeta?.() ?? undefined);
            }
            appendLog("[pty] connected");
          },
          onDisconnect: () => {
            appendLog("[pty] disconnected");
            setPtyStatus("disconnected");
            updateMouseStatus();
          },
          onStatus: (shell) => {
            appendLog(`[pty] shell ${shell ?? ""}`);
          },
          onError: (message, errors) => {
            appendLog(`[pty] error ${message ?? ""}`);
            if (errors) {
              for (const err of errors)
                appendLog(`[pty] spawn ${err}`);
            }
            disconnectPty2();
          },
          onExit: (code) => {
            appendLog(`[pty] exit ${code ?? ""}`);
            disconnectPty2();
          },
          onData: (data) => {
            if (data instanceof Uint8Array) {
              inputHandler.filterOutputBytes?.(data);
              updateMouseStatus();
              queuePtyOutputBytes(data);
            } else {
              const sanitized = inputHandler.filterOutput(data);
              updateMouseStatus();
              if (sanitized)
                queuePtyOutput(sanitized);
            }
          }
        }
      });
      Promise.resolve(connectResult).catch((err) => {
        appendLog(`[pty] error ${formatError(err)}`);
        disconnectPty2();
      });
    } catch (err) {
      appendLog(`[pty] error ${formatError(err)}`);
      disconnectPty2();
    }
  }
  function sendKeyInput(text2, source = "key") {
    if (!text2)
      return;
    const intercepted = runBeforeInputHook(text2, source);
    if (!intercepted)
      return;
    if (source !== "program" && shouldClearSelection()) {
      clearSelection2();
    }
    if (ptyTransport.isConnected()) {
      const payload = inputHandler.mapKeyForPty(intercepted);
      ptyTransport.sendInput(payload);
      return;
    }
    sendInput(intercepted, source, { skipHooks: true });
  }
  function sendPasteText(text2) {
    if (!text2)
      return;
    const bracketedPasteEnabled = !!inputHandler.isBracketedPaste();
    sendKeyInput(formatPasteText(text2, bracketedPasteEnabled));
  }
  function sendPastePayloadFromDataTransfer(dataTransfer) {
    const payload = readPastePayloadFromDataTransfer(dataTransfer);
    if (!payload)
      return false;
    sendPasteText(payload.text);
    return true;
  }
  function getCprPosition() {
    return getCursorForCpr();
  }
  return {
    setPtyStatus,
    updateMouseStatus,
    scheduleSyncOutputReset,
    cancelSyncOutputReset,
    connectPty: connectPty2,
    disconnectPty: disconnectPty2,
    sendKeyInput,
    sendPasteText,
    sendPastePayloadFromDataTransfer,
    getCprPosition
  };
}

// src/runtime/create-runtime/interaction-runtime/bind-ime-events.ts
function bindImeEvents(options) {
  const {
    bindOptions,
    imeInput,
    imeState,
    cleanupCanvasFns,
    getWasmReady,
    getWasmHandle,
    setPreedit: setPreedit2,
    syncImeSelection: syncImeSelection2
  } = options;
  const {
    inputHandler,
    sendKeyInput,
    sendPasteText,
    sendPastePayloadFromDataTransfer,
    getLastKeydownSeq,
    getLastKeydownSeqAt,
    keydownBeforeinputDedupeMs
  } = bindOptions;
  let suppressNextInput = false;
  let lastNormalizedKeydownSeq = "";
  let lastNormalizedKeydownSeqSource = "";
  const getNormalizedLastKeydownSeq = () => {
    const source = getLastKeydownSeq();
    if (!source) {
      lastNormalizedKeydownSeqSource = "";
      lastNormalizedKeydownSeq = "";
      return "";
    }
    if (source === lastNormalizedKeydownSeqSource)
      return lastNormalizedKeydownSeq;
    lastNormalizedKeydownSeqSource = source;
    lastNormalizedKeydownSeq = inputHandler.mapKeyForPty(source);
    return lastNormalizedKeydownSeq;
  };
  const onCompositionStart = (event) => {
    imeState.composing = true;
    setPreedit2(event.data || imeInput.value || "");
    requestAnimationFrame(syncImeSelection2);
  };
  const onCompositionUpdate = (event) => {
    setPreedit2(event.data || imeInput.value || "");
    requestAnimationFrame(syncImeSelection2);
  };
  const onCompositionEnd = (event) => {
    imeState.composing = false;
    setPreedit2("", true);
    imeState.selectionStart = 0;
    imeState.selectionEnd = 0;
    const text2 = event.data || "";
    if (text2) {
      suppressNextInput = true;
      sendKeyInput(text2);
    }
    imeInput.value = "";
  };
  const onBeforeInput = (event) => {
    if (!getWasmReady() || !getWasmHandle())
      return;
    if (imeState.composing)
      return;
    if (event.inputType === "insertFromPaste") {
      event.preventDefault();
      suppressNextInput = true;
      const pasteText = event.dataTransfer?.getData("text/plain") || event.data || "";
      if (pasteText) {
        sendPasteText(pasteText);
        imeInput.value = "";
        return;
      }
      sendPastePayloadFromDataTransfer(event.dataTransfer);
      imeInput.value = "";
      return;
    }
    const text2 = inputHandler.encodeBeforeInput(event);
    if (text2) {
      const normalizedText = inputHandler.mapKeyForPty(text2);
      const normalizedLastKeydownSeq = getNormalizedLastKeydownSeq();
      const now = performance.now();
      if (normalizedLastKeydownSeq && normalizedText === normalizedLastKeydownSeq && now - getLastKeydownSeqAt() <= keydownBeforeinputDedupeMs) {
        event.preventDefault();
        suppressNextInput = true;
        imeInput.value = "";
        return;
      }
      event.preventDefault();
      suppressNextInput = true;
      sendKeyInput(text2);
      imeInput.value = "";
    }
  };
  const onInput = (event) => {
    if (!getWasmReady() || !getWasmHandle())
      return;
    if (imeState.composing)
      return;
    if (suppressNextInput) {
      suppressNextInput = false;
      imeInput.value = "";
      return;
    }
    const text2 = event.data || imeInput.value;
    if (text2) {
      sendKeyInput(text2);
      imeInput.value = "";
    }
  };
  const onPaste = (event) => {
    if (!getWasmReady() || !getWasmHandle())
      return;
    event.preventDefault();
    suppressNextInput = true;
    const text2 = event.clipboardData?.getData("text/plain") || "";
    if (text2) {
      sendPasteText(text2);
      imeInput.value = "";
      return;
    }
    sendPastePayloadFromDataTransfer(event.clipboardData);
    imeInput.value = "";
  };
  imeInput.addEventListener("compositionstart", onCompositionStart);
  imeInput.addEventListener("compositionupdate", onCompositionUpdate);
  imeInput.addEventListener("compositionend", onCompositionEnd);
  imeInput.addEventListener("beforeinput", onBeforeInput);
  imeInput.addEventListener("input", onInput);
  imeInput.addEventListener("paste", onPaste);
  cleanupCanvasFns.push(() => {
    imeInput.removeEventListener("compositionstart", onCompositionStart);
    imeInput.removeEventListener("compositionupdate", onCompositionUpdate);
    imeInput.removeEventListener("compositionend", onCompositionEnd);
    imeInput.removeEventListener("beforeinput", onBeforeInput);
    imeInput.removeEventListener("input", onInput);
    imeInput.removeEventListener("paste", onPaste);
  });
}

// src/runtime/create-runtime/interaction-runtime/bind-pointer-aux-handlers.ts
function createPointerAuxHandlers(options) {
  const {
    inputHandler,
    shouldRoutePointerToAppMouse,
    scrollViewportByWheel = () => {
    },
    getWasmReady,
    getWasmHandle,
    getGridState,
    updateLinkHover,
    clearPendingDesktopSelection,
    clearPendingTouchSelection,
    isTouchPointer,
    selectionState,
    touchSelectionState,
    desktopSelectionState,
    updateCanvasCursor,
    markNeedsRender
  } = options;
  const onPointerCancel = (event) => {
    if (desktopSelectionState.pendingPointerId === event.pointerId) {
      clearPendingDesktopSelection();
    }
    if (isTouchPointer(event)) {
      if (touchSelectionState.pendingPointerId === event.pointerId) {
        clearPendingTouchSelection();
      }
      if (touchSelectionState.panPointerId === event.pointerId) {
        touchSelectionState.panPointerId = null;
      }
      if (touchSelectionState.activePointerId === event.pointerId) {
        touchSelectionState.activePointerId = null;
        if (selectionState.dragging) {
          selectionState.dragging = false;
          updateCanvasCursor();
          markNeedsRender();
        }
      }
    }
  };
  const onWheel = (event) => {
    if (shouldRoutePointerToAppMouse(event.shiftKey)) {
      if (inputHandler.sendMouseEvent("wheel", event)) {
        event.preventDefault();
        return;
      }
    }
    if (!getWasmReady() || !getWasmHandle() || !getGridState().cellH)
      return;
    scrollViewportByWheel(event);
    event.preventDefault();
  };
  const onContextMenu = (event) => {
    if (shouldRoutePointerToAppMouse(event.shiftKey))
      event.preventDefault();
  };
  const onPointerLeave = () => {
    updateLinkHover(null);
  };
  return {
    onPointerCancel,
    onWheel,
    onContextMenu,
    onPointerLeave
  };
}

// src/runtime/create-runtime/interaction-runtime/bind-pointer-up-handler.ts
function createPointerUpHandler(options) {
  const {
    inputHandler,
    sendKeyInput,
    openLink: openLink2,
    isTouchPointer,
    touchSelectionState,
    selectionState,
    normalizeSelectionCell: normalizeSelectionCell2,
    positionToCell: positionToCell2,
    clearPendingTouchSelection,
    clearPendingDesktopSelection,
    desktopSelectionState,
    clearSelection: clearSelection2,
    updateCanvasCursor,
    markNeedsRender,
    shouldRoutePointerToAppMouse,
    linkState,
    updateLinkHover
  } = options;
  return (event) => {
    if (isTouchPointer(event)) {
      if (touchSelectionState.pendingPointerId === event.pointerId) {
        clearPendingTouchSelection();
        touchSelectionState.activePointerId = null;
        touchSelectionState.panPointerId = null;
        return;
      }
      if (selectionState.dragging && touchSelectionState.activePointerId === event.pointerId) {
        const cell2 = normalizeSelectionCell2(positionToCell2(event));
        event.preventDefault();
        selectionState.dragging = false;
        selectionState.focus = cell2;
        touchSelectionState.activePointerId = null;
        if (selectionState.anchor && selectionState.focus && selectionState.anchor.row === selectionState.focus.row && selectionState.anchor.col === selectionState.focus.col) {
          clearSelection2();
        } else {
          updateCanvasCursor();
          markNeedsRender();
        }
        return;
      }
      if (touchSelectionState.panPointerId === event.pointerId) {
        touchSelectionState.panPointerId = null;
      }
      return;
    }
    const cell = normalizeSelectionCell2(positionToCell2(event));
    const clearSelectionFromClick = desktopSelectionState.pendingPointerId === event.pointerId && desktopSelectionState.startedWithActiveSelection && !selectionState.dragging;
    if (desktopSelectionState.pendingPointerId === event.pointerId) {
      clearPendingDesktopSelection();
    }
    if (clearSelectionFromClick)
      clearSelection2();
    if (selectionState.dragging) {
      event.preventDefault();
      selectionState.dragging = false;
      selectionState.focus = cell;
      if (selectionState.anchor && selectionState.focus && selectionState.anchor.row === selectionState.focus.row && selectionState.anchor.col === selectionState.focus.col) {
        clearSelection2();
      } else {
        updateCanvasCursor();
        markNeedsRender();
      }
    } else {
      if (shouldRoutePointerToAppMouse(event.shiftKey) && inputHandler.sendMouseEvent("up", event)) {
        event.preventDefault();
        return;
      }
      updateLinkHover(cell);
    }
    if (!selectionState.active && event.button === 0 && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey && inputHandler.isPromptClickEventsEnabled()) {
      const seq = inputHandler.encodePromptClickEvent(cell);
      if (seq) {
        event.preventDefault();
        sendKeyInput(seq);
        return;
      }
    }
    if (!selectionState.active && event.button === 0 && linkState.hoverUri) {
      openLink2(linkState.hoverUri);
    }
  };
}

// src/runtime/create-runtime/interaction-runtime/bind-pointer-events.ts
function bindPointerEvents(options) {
  const {
    canvas,
    bindOptions,
    touchSelectionMode,
    touchSelectionLongPressMs,
    touchSelectionMoveThresholdPx,
    selectionState,
    touchSelectionState,
    desktopSelectionState,
    linkState,
    cleanupCanvasFns,
    isTouchPointer,
    clearPendingTouchSelection,
    clearPendingDesktopSelection,
    tryActivatePendingTouchSelection,
    beginSelectionDrag,
    scrollViewportByWheel = () => {
    },
    normalizeSelectionCell: normalizeSelectionCell2,
    positionToCell: positionToCell2,
    scrollViewportByLines,
    clearSelection: clearSelection2,
    updateCanvasCursor,
    markNeedsRender,
    updateLinkHover,
    getGridState,
    getWasmReady,
    getWasmHandle
  } = options;
  const { inputHandler, sendKeyInput, openLink: openLink2 } = bindOptions;
  const shouldRoutePointerToAppMouse = (shiftKey) => {
    if (shiftKey)
      return false;
    return inputHandler.isMouseActive();
  };
  canvas.style.touchAction = touchSelectionMode === "long-press" || touchSelectionMode === "drag" ? "none" : "pan-y pinch-zoom";
  const onPointerDown = (event) => {
    if (shouldRoutePointerToAppMouse(event.shiftKey) && inputHandler.sendMouseEvent("down", event)) {
      clearPendingDesktopSelection();
      event.preventDefault();
      canvas.setPointerCapture?.(event.pointerId);
      return;
    }
    if (isTouchPointer(event)) {
      if (event.button !== 0)
        return;
      const cell2 = normalizeSelectionCell2(positionToCell2(event));
      touchSelectionState.activePointerId = null;
      touchSelectionState.panPointerId = null;
      if (touchSelectionMode === "off")
        return;
      if (touchSelectionMode === "drag") {
        event.preventDefault();
        beginSelectionDrag(cell2, event.pointerId);
        return;
      }
      clearPendingTouchSelection();
      touchSelectionState.pendingPointerId = event.pointerId;
      touchSelectionState.pendingCell = cell2;
      touchSelectionState.pendingStartedAt = performance.now();
      touchSelectionState.pendingStartX = event.clientX;
      touchSelectionState.pendingStartY = event.clientY;
      touchSelectionState.panPointerId = event.pointerId;
      touchSelectionState.panLastY = event.clientY;
      touchSelectionState.pendingTimer = setTimeout(() => {
        tryActivatePendingTouchSelection(event.pointerId);
      }, touchSelectionLongPressMs);
      return;
    }
    if (event.button !== 0)
      return;
    event.preventDefault();
    const cell = normalizeSelectionCell2(positionToCell2(event));
    updateLinkHover(cell);
    desktopSelectionState.pendingPointerId = event.pointerId;
    desktopSelectionState.pendingCell = cell;
    desktopSelectionState.startedWithActiveSelection = selectionState.active;
  };
  const onPointerMove = (event) => {
    if (isTouchPointer(event)) {
      if (touchSelectionState.pendingPointerId === event.pointerId) {
        const dx = event.clientX - touchSelectionState.pendingStartX;
        const dy = event.clientY - touchSelectionState.pendingStartY;
        if (dx * dx + dy * dy >= touchSelectionMoveThresholdPx * touchSelectionMoveThresholdPx) {
          clearPendingTouchSelection();
        } else {
          tryActivatePendingTouchSelection(event.pointerId);
        }
        if (touchSelectionState.pendingPointerId === event.pointerId) {
          if (touchSelectionMode === "long-press" && touchSelectionState.panPointerId === event.pointerId) {
            const deltaPx = touchSelectionState.panLastY - event.clientY;
            touchSelectionState.panLastY = event.clientY;
            scrollViewportByLines(deltaPx / Math.max(1, getGridState().cellH) * 1.5);
            event.preventDefault();
          }
          return;
        }
      }
      if (selectionState.dragging && touchSelectionState.activePointerId === event.pointerId) {
        const cell2 = normalizeSelectionCell2(positionToCell2(event));
        event.preventDefault();
        selectionState.focus = cell2;
        updateLinkHover(null);
        updateCanvasCursor();
        markNeedsRender();
        return;
      }
      if (touchSelectionMode === "long-press" && touchSelectionState.panPointerId === event.pointerId) {
        const deltaPx = touchSelectionState.panLastY - event.clientY;
        touchSelectionState.panLastY = event.clientY;
        scrollViewportByLines(deltaPx / Math.max(1, getGridState().cellH) * 1.5);
        event.preventDefault();
      }
      return;
    }
    const cell = normalizeSelectionCell2(positionToCell2(event));
    if (desktopSelectionState.pendingPointerId === event.pointerId && desktopSelectionState.pendingCell) {
      const anchor = desktopSelectionState.pendingCell;
      if (anchor.row !== cell.row || anchor.col !== cell.col) {
        beginSelectionDrag(anchor, event.pointerId);
        selectionState.focus = cell;
        updateLinkHover(null);
        updateCanvasCursor();
        markNeedsRender();
        return;
      }
      updateLinkHover(cell);
      return;
    }
    if (selectionState.dragging) {
      event.preventDefault();
      selectionState.focus = cell;
      updateLinkHover(null);
      updateCanvasCursor();
      markNeedsRender();
      return;
    }
    if (shouldRoutePointerToAppMouse(event.shiftKey) && inputHandler.sendMouseEvent("move", event)) {
      event.preventDefault();
      return;
    }
    updateLinkHover(cell);
  };
  const onPointerUp = createPointerUpHandler({
    inputHandler,
    sendKeyInput,
    openLink: openLink2,
    isTouchPointer,
    touchSelectionState,
    selectionState,
    normalizeSelectionCell: normalizeSelectionCell2,
    positionToCell: positionToCell2,
    clearPendingTouchSelection,
    clearPendingDesktopSelection,
    desktopSelectionState,
    clearSelection: clearSelection2,
    updateCanvasCursor,
    markNeedsRender,
    shouldRoutePointerToAppMouse,
    linkState,
    updateLinkHover
  });
  const { onPointerCancel, onWheel, onContextMenu, onPointerLeave } = createPointerAuxHandlers({
    inputHandler,
    shouldRoutePointerToAppMouse,
    scrollViewportByWheel,
    getWasmReady,
    getWasmHandle,
    getGridState,
    updateLinkHover,
    clearPendingDesktopSelection,
    clearPendingTouchSelection,
    isTouchPointer,
    selectionState,
    touchSelectionState,
    desktopSelectionState,
    updateCanvasCursor,
    markNeedsRender
  });
  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerCancel);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("contextmenu", onContextMenu);
  cleanupCanvasFns.push(() => {
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", onPointerUp);
    canvas.removeEventListener("pointercancel", onPointerCancel);
    canvas.removeEventListener("pointerleave", onPointerLeave);
    canvas.removeEventListener("wheel", onWheel);
    canvas.removeEventListener("contextmenu", onContextMenu);
    clearPendingTouchSelection();
  });
}

// src/runtime/overlay-scrollbar.ts
var OVERLAY_SCROLLBAR_WIDTH_CSS_PX = 7;
var OVERLAY_SCROLLBAR_MARGIN_CSS_PX = 4;
var OVERLAY_SCROLLBAR_INSET_Y_CSS_PX = 2;
var OVERLAY_SCROLLBAR_MIN_THUMB_CSS_PX = 28;
function computeOverlayScrollbarLayout(total, offset, len, canvasWidth, canvasHeight, currentDpr) {
  if (!(total > len && len > 0))
    return null;
  const dpr = Math.max(1, currentDpr || 1);
  const width = Math.max(1, Math.round(OVERLAY_SCROLLBAR_WIDTH_CSS_PX * dpr));
  const margin = Math.max(1, Math.round(OVERLAY_SCROLLBAR_MARGIN_CSS_PX * dpr));
  const insetY = Math.max(0, Math.round(OVERLAY_SCROLLBAR_INSET_Y_CSS_PX * dpr));
  const trackX = Math.max(0, canvasWidth - margin - width);
  const trackY = insetY;
  const trackH = Math.max(width, canvasHeight - insetY * 2);
  const denom = Math.max(1, total - len);
  const dynamicThumbH = Math.round(trackH * (len / total));
  const minThumbH = Math.max(width, Math.round(OVERLAY_SCROLLBAR_MIN_THUMB_CSS_PX * dpr));
  const thumbH = Math.min(trackH, Math.max(minThumbH, dynamicThumbH));
  const thumbY = trackY + Math.round(offset / denom * (trackH - thumbH));
  return { total, offset, len, denom, width, trackX, trackY, trackH, thumbY, thumbH };
}

// src/runtime/create-runtime/native-scrollbar-host.ts
var NATIVE_SCROLLBAR_STYLE_MARKER = "data-restty-native-scrollbar";
var MAX_NATIVE_SCROLL_RANGE_PX = 8000000;
var FADE_DELAY_MS = 160;
var FADE_DURATION_MS = 520;
var VISIBLE_OPACITY = 0.68;
var MIN_SCROLL_PX_PER_ROW = 8;
var MAX_SCROLL_PX_PER_ROW = 14;
function ensureNativeScrollbarStyles() {
  if (typeof document === "undefined")
    return;
  if (document.head.querySelector(`[${NATIVE_SCROLLBAR_STYLE_MARKER}]`))
    return;
  const style = document.createElement("style");
  style.setAttribute(NATIVE_SCROLLBAR_STYLE_MARKER, "true");
  style.textContent = `
.restty-native-scroll-host {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
}

.restty-native-scroll-host::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.restty-native-scroll-root {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.restty-native-scroll-canvas {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  z-index: 0;
  flex: none;
  will-change: transform;
}

.restty-native-scroll-spacer {
  width: 1px;
  min-width: 1px;
  pointer-events: none;
}

.restty-native-scroll-chrome {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
  overflow: hidden;
}

.restty-native-scroll-thumb {
  position: absolute;
  top: 0;
  left: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(245, 245, 245, 0.75);
  box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.18);
  opacity: 0;
  transition: opacity ${FADE_DURATION_MS}ms ease;
  pointer-events: auto;
  cursor: default;
}
`;
  document.head.append(style);
}
function createNativeScrollbarHost(options) {
  const { canvas, getGridState, noteScrollActivity, setViewportScrollOffset } = options;
  if (typeof document === "undefined") {
    return {
      flash: () => {
      },
      sync: () => {
      },
      destroy: () => {
      }
    };
  }
  const parent = canvas.parentElement;
  if (!parent) {
    return {
      flash: () => {
      },
      sync: () => {
      },
      destroy: () => {
      }
    };
  }
  ensureNativeScrollbarStyles();
  const root = document.createElement("div");
  root.className = "restty-native-scroll-root";
  const host = document.createElement("div");
  host.className = "restty-native-scroll-host";
  const spacer = document.createElement("div");
  spacer.className = "restty-native-scroll-spacer";
  const chrome = document.createElement("div");
  chrome.className = "restty-native-scroll-chrome";
  const thumb = document.createElement("div");
  thumb.className = "restty-native-scroll-thumb";
  chrome.append(thumb);
  parent.insertBefore(root, canvas);
  root.append(host, chrome);
  host.append(canvas, spacer);
  canvas.classList.add("restty-native-scroll-canvas");
  let destroyed = false;
  let currentLayout = null;
  let currentDenom = 0;
  let currentScrollRangePx = 0;
  let ignoreNextScroll = false;
  let fadeTimer = 0;
  let dragPointerId = null;
  let dragGrabRatio = 0.5;
  const resolveScrollPxPerRow = () => {
    const cellH = Math.max(1, Number(getGridState().cellH || 1));
    return clamp(cellH * 0.5, MIN_SCROLL_PX_PER_ROW, MAX_SCROLL_PX_PER_ROW);
  };
  const setThumbVisible = (visible) => {
    thumb.style.opacity = visible ? `${VISIBLE_OPACITY}` : "0";
  };
  const applyCanvasResidual = (offset) => {
    if (!currentDenom || currentScrollRangePx <= 0) {
      canvas.style.transform = "translate3d(0, 0, 0)";
      return;
    }
    const logicalScrollTop = offset / currentDenom * currentScrollRangePx;
    const residual = host.scrollTop - logicalScrollTop;
    canvas.style.transform = `translate3d(0, ${-residual}px, 0)`;
  };
  const scheduleThumbFade = () => {
    if (fadeTimer) {
      clearTimeout(fadeTimer);
      fadeTimer = 0;
    }
    setThumbVisible(true);
    fadeTimer = window.setTimeout(() => {
      fadeTimer = 0;
      if (dragPointerId === null) {
        setThumbVisible(false);
      }
    }, FADE_DELAY_MS);
  };
  const applyScrollTopToViewport = () => {
    if (!currentDenom || currentScrollRangePx <= 0)
      return;
    const ratio = clamp(host.scrollTop / currentScrollRangePx, 0, 1);
    const nextOffset = Math.round(ratio * currentDenom);
    applyCanvasResidual(nextOffset);
    setViewportScrollOffset(nextOffset);
  };
  const onHostScroll = () => {
    if (ignoreNextScroll) {
      ignoreNextScroll = false;
      return;
    }
    noteScrollActivity();
    scheduleThumbFade();
    applyScrollTopToViewport();
  };
  const onHostWheel = (event) => {
    if (!currentScrollRangePx)
      return;
    const maxScrollTop = Math.max(0, host.scrollHeight - host.clientHeight);
    const atTop = host.scrollTop <= 0;
    const atBottom = host.scrollTop >= maxScrollTop;
    if (atTop && event.deltaY < 0 || atBottom && event.deltaY > 0) {
      event.preventDefault();
    }
  };
  const pointerYToScrollTop = (clientY) => {
    if (!currentLayout || currentScrollRangePx <= 0)
      return 0;
    const rect2 = root.getBoundingClientRect();
    const localY = clientY - rect2.top;
    const thumbTop = localY - currentLayout.thumbH * dragGrabRatio;
    const trackSpan = Math.max(1, currentLayout.trackH - currentLayout.thumbH);
    const ratio = clamp((thumbTop - currentLayout.trackY) / trackSpan, 0, 1);
    return ratio * currentScrollRangePx;
  };
  const onThumbPointerMove = (event) => {
    if (dragPointerId !== event.pointerId)
      return;
    host.scrollTop = pointerYToScrollTop(event.clientY);
    event.preventDefault();
  };
  const endThumbDrag = (pointerId) => {
    if (pointerId === null || dragPointerId !== pointerId)
      return;
    dragPointerId = null;
    thumb.releasePointerCapture?.(pointerId);
    scheduleThumbFade();
  };
  const onThumbPointerUp = (event) => {
    endThumbDrag(event.pointerId);
  };
  const onThumbPointerCancel = (event) => {
    endThumbDrag(event.pointerId);
  };
  const onThumbPointerDown = (event) => {
    if (event.button !== 0 || !currentLayout)
      return;
    const rect2 = thumb.getBoundingClientRect();
    dragPointerId = event.pointerId;
    dragGrabRatio = clamp((event.clientY - rect2.top) / Math.max(1, rect2.height), 0, 1);
    thumb.setPointerCapture?.(event.pointerId);
    noteScrollActivity();
    setThumbVisible(true);
    host.scrollTop = pointerYToScrollTop(event.clientY);
    event.preventDefault();
  };
  const onThumbPointerEnter = () => {
    setThumbVisible(true);
  };
  const onThumbPointerLeave = () => {
    if (dragPointerId === null) {
      scheduleThumbFade();
    }
  };
  host.addEventListener("scroll", onHostScroll, { passive: true });
  host.addEventListener("wheel", onHostWheel, { passive: false });
  thumb.addEventListener("pointerdown", onThumbPointerDown);
  thumb.addEventListener("pointermove", onThumbPointerMove);
  thumb.addEventListener("pointerup", onThumbPointerUp);
  thumb.addEventListener("pointercancel", onThumbPointerCancel);
  thumb.addEventListener("pointerenter", onThumbPointerEnter);
  thumb.addEventListener("pointerleave", onThumbPointerLeave);
  return {
    flash: () => {
      if (!currentLayout)
        return;
      scheduleThumbFade();
    },
    sync: (total, offset, len) => {
      if (destroyed)
        return;
      const clientWidth = Math.max(1, host.clientWidth || canvas.clientWidth || canvas.width);
      const clientHeight = Math.max(1, host.clientHeight || canvas.clientHeight || canvas.height);
      if (!(total > len && len > 0)) {
        currentLayout = null;
        currentDenom = 0;
        currentScrollRangePx = 0;
        spacer.style.height = "0px";
        chrome.style.display = "none";
        canvas.style.transform = "translate3d(0, 0, 0)";
        if (host.scrollTop !== 0) {
          ignoreNextScroll = true;
          host.scrollTop = 0;
        }
        return;
      }
      currentDenom = Math.max(1, total - len);
      currentScrollRangePx = Math.min(MAX_NATIVE_SCROLL_RANGE_PX, Math.max(clientHeight, currentDenom * resolveScrollPxPerRow()));
      spacer.style.height = `${currentScrollRangePx}px`;
      const nextScrollTop = Math.round(offset / currentDenom * currentScrollRangePx);
      if (Math.abs(host.scrollTop - nextScrollTop) > 0.5) {
        ignoreNextScroll = true;
        host.scrollTop = nextScrollTop;
      }
      applyCanvasResidual(offset);
      currentLayout = computeOverlayScrollbarLayout(total, offset, len, clientWidth, clientHeight, 1);
      if (!currentLayout) {
        chrome.style.display = "none";
        return;
      }
      chrome.style.display = "block";
      thumb.style.left = `${currentLayout.trackX}px`;
      thumb.style.width = `${currentLayout.width}px`;
      thumb.style.height = `${currentLayout.thumbH}px`;
      thumb.style.transform = `translateY(${currentLayout.thumbY}px)`;
    },
    destroy: () => {
      if (destroyed)
        return;
      destroyed = true;
      if (fadeTimer) {
        clearTimeout(fadeTimer);
        fadeTimer = 0;
      }
      host.removeEventListener("scroll", onHostScroll);
      host.removeEventListener("wheel", onHostWheel);
      thumb.removeEventListener("pointerdown", onThumbPointerDown);
      thumb.removeEventListener("pointermove", onThumbPointerMove);
      thumb.removeEventListener("pointerup", onThumbPointerUp);
      thumb.removeEventListener("pointercancel", onThumbPointerCancel);
      thumb.removeEventListener("pointerenter", onThumbPointerEnter);
      thumb.removeEventListener("pointerleave", onThumbPointerLeave);
      const currentCanvas = root.querySelector("canvas");
      if (currentCanvas && root.parentElement) {
        currentCanvas.classList.remove("restty-native-scroll-canvas");
        currentCanvas.style.transform = "";
        root.parentElement.replaceChild(currentCanvas, root);
      } else {
        root.remove();
      }
    }
  };
}

// src/runtime/create-runtime/interaction-runtime/scrollbar-runtime.ts
function createScrollbarRuntime(options) {
  const {
    scrollbarState,
    selectionState,
    linkState,
    getCanvas,
    getGridState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    getWasmExports,
    updateLinkHover,
    markNeedsRender,
    markSearchDirty
  } = options;
  let scrollRemainder = 0;
  let pendingPrecisionScrollPx = 0;
  const hasCoarsePointer = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(any-pointer: coarse)").matches;
  const hasTouchPoints = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
  const nativeScrollHost = !hasCoarsePointer && !hasTouchPoints && typeof document !== "undefined" ? createNativeScrollbarHost({
    canvas: getCanvas(),
    getGridState,
    noteScrollActivity: () => {
      scrollbarState.lastInputAt = performance.now();
    },
    setViewportScrollOffset: (nextOffset) => {
      setViewportScrollOffset(nextOffset);
    }
  }) : null;
  const getViewportScrollOffset = () => {
    const wasmHandle = getWasmHandle();
    const wasmExports = getWasmExports();
    if (!wasmHandle || !wasmExports?.restty_scrollbar_offset)
      return 0;
    return wasmExports.restty_scrollbar_offset(wasmHandle) || 0;
  };
  const shiftSelectionByRows = (deltaRows) => {
    if (!deltaRows)
      return;
    if (!selectionState.active && !selectionState.dragging)
      return;
    if (!selectionState.anchor || !selectionState.focus)
      return;
    const { rows } = getGridState();
    const maxAbs = Math.max(1024, (rows || 24) * 128);
    selectionState.anchor = {
      row: clamp(selectionState.anchor.row + deltaRows, -maxAbs, maxAbs),
      col: selectionState.anchor.col
    };
    selectionState.focus = {
      row: clamp(selectionState.focus.row + deltaRows, -maxAbs, maxAbs),
      col: selectionState.focus.col
    };
    markNeedsRender();
  };
  const noteScrollActivity = () => {
    scrollbarState.lastInputAt = performance.now();
    nativeScrollHost?.flash();
  };
  const scrollViewportByLines = (lines) => {
    const wasm = getWasm();
    const wasmHandle = getWasmHandle();
    const { cellH } = getGridState();
    if (!getWasmReady() || !wasm || !wasmHandle || !cellH)
      return;
    scrollRemainder += lines;
    const delta = Math.trunc(scrollRemainder);
    scrollRemainder -= delta;
    if (!delta)
      return;
    const beforeOffset = getViewportScrollOffset();
    wasm.scrollViewport(wasmHandle, delta);
    const afterOffset = getViewportScrollOffset();
    shiftSelectionByRows(beforeOffset - afterOffset);
    if (linkState.hoverId)
      updateLinkHover(null);
    wasm.renderUpdate(wasmHandle);
    markSearchDirty?.();
    markNeedsRender();
    noteScrollActivity();
  };
  const scrollViewportByWheel = (event) => {
    const { cellH, rows } = getGridState();
    if (!cellH)
      return;
    const isPrecision = event.deltaMode === 0;
    if (isPrecision) {
      const precisionMultiplier = 2;
      const adjustedPx = event.deltaY * precisionMultiplier;
      const pendingPx = pendingPrecisionScrollPx + adjustedPx;
      if (Math.abs(pendingPx) < cellH) {
        pendingPrecisionScrollPx = pendingPx;
        noteScrollActivity();
        return;
      }
      const amount = pendingPx / cellH;
      pendingPrecisionScrollPx = pendingPx - Math.trunc(amount) * cellH;
      if (amount) {
        scrollViewportByLines(Math.trunc(amount));
      }
      return;
    }
    pendingPrecisionScrollPx = 0;
    if (event.deltaMode === 1) {
      const discreteMultiplier = 3;
      const yoff = event.deltaY > 0 ? Math.max(event.deltaY, 1) : Math.min(event.deltaY, -1);
      scrollViewportByLines(yoff * discreteMultiplier);
      return;
    }
    const pageLines = rows > 0 ? rows : 24;
    scrollViewportByLines(event.deltaY * pageLines);
  };
  const setViewportScrollOffset = (nextOffset) => {
    const wasm = getWasm();
    const wasmHandle = getWasmHandle();
    const wasmExports = getWasmExports();
    if (!getWasmReady() || !wasm || !wasmHandle || !wasmExports?.restty_scrollbar_total)
      return;
    const total = wasmExports.restty_scrollbar_total(wasmHandle) || 0;
    const len = wasmExports.restty_scrollbar_len ? wasmExports.restty_scrollbar_len(wasmHandle) : 0;
    const current = wasmExports.restty_scrollbar_offset ? wasmExports.restty_scrollbar_offset(wasmHandle) : 0;
    const maxOffset = Math.max(0, total - len);
    const clamped = clamp(Math.round(nextOffset), 0, maxOffset);
    const delta = clamped - current;
    if (!delta)
      return;
    const beforeOffset = getViewportScrollOffset();
    wasm.scrollViewport(wasmHandle, delta);
    const afterOffset = getViewportScrollOffset();
    shiftSelectionByRows(beforeOffset - afterOffset);
    if (linkState.hoverId)
      updateLinkHover(null);
    wasm.renderUpdate(wasmHandle);
    markSearchDirty?.();
    markNeedsRender();
    noteScrollActivity();
  };
  return {
    destroy: () => {
      nativeScrollHost?.destroy();
    },
    noteScrollActivity,
    scrollViewportByLines,
    scrollViewportByWheel,
    syncScrollbar: (total, offset, len) => {
      nativeScrollHost?.sync(total, offset, len);
    }
  };
}

// src/runtime/create-runtime/interaction-runtime.ts
function createRuntimeInteraction(options) {
  const {
    attachCanvasEvents,
    touchSelectionMode,
    touchSelectionLongPressMs,
    touchSelectionMoveThresholdPx,
    imeInput,
    cleanupCanvasFns,
    getCanvas,
    getCurrentDpr,
    getGridState,
    getLastRenderState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    getWasmExports,
    updateLinkHover,
    markNeedsRender,
    markSearchDirty
  } = options;
  const selectionState = createSelectionState();
  const touchSelectionState = {
    pendingPointerId: null,
    activePointerId: null,
    panPointerId: null,
    pendingCell: null,
    pendingStartedAt: 0,
    pendingStartX: 0,
    pendingStartY: 0,
    panLastY: 0,
    pendingTimer: 0
  };
  const desktopSelectionState = {
    pendingPointerId: null,
    pendingCell: null,
    startedWithActiveSelection: false
  };
  const linkState = {
    hoverId: 0,
    hoverUri: ""
  };
  const scrollbarState = {
    lastInputAt: 0,
    lastTotal: 0,
    lastOffset: 0,
    lastLen: 0
  };
  const imeState = {
    composing: false,
    preedit: "",
    selectionStart: 0,
    selectionEnd: 0
  };
  const updateCanvasCursor = () => {
    const canvas = getCanvas();
    if (!canvas)
      return;
    const showPointer = linkState.hoverId !== 0 && !selectionState.active && !selectionState.dragging;
    canvas.style.cursor = showPointer ? "pointer" : "text";
  };
  const isTouchPointer = (event) => {
    return event.pointerType === "touch";
  };
  const clearPendingTouchSelection = () => {
    if (touchSelectionState.pendingTimer) {
      clearTimeout(touchSelectionState.pendingTimer);
      touchSelectionState.pendingTimer = 0;
    }
    touchSelectionState.pendingPointerId = null;
    touchSelectionState.pendingCell = null;
    touchSelectionState.pendingStartedAt = 0;
  };
  const clearPendingDesktopSelection = () => {
    desktopSelectionState.pendingPointerId = null;
    desktopSelectionState.pendingCell = null;
    desktopSelectionState.startedWithActiveSelection = false;
  };
  const beginSelectionDrag = (cell, pointerId) => {
    clearPendingDesktopSelection();
    selectionState.active = true;
    selectionState.dragging = true;
    selectionState.anchor = cell;
    selectionState.focus = cell;
    touchSelectionState.activePointerId = pointerId;
    touchSelectionState.panPointerId = null;
    getCanvas().setPointerCapture?.(pointerId);
    updateCanvasCursor();
    markNeedsRender();
  };
  const tryActivatePendingTouchSelection = (pointerId) => {
    if (touchSelectionMode !== "long-press")
      return false;
    if (touchSelectionState.pendingPointerId !== pointerId || !touchSelectionState.pendingCell) {
      return false;
    }
    if (performance.now() - touchSelectionState.pendingStartedAt < touchSelectionLongPressMs) {
      return false;
    }
    const pendingCell = touchSelectionState.pendingCell;
    clearPendingTouchSelection();
    beginSelectionDrag(pendingCell, pointerId);
    return true;
  };
  const scrollbarRuntime = createScrollbarRuntime({
    scrollbarState,
    selectionState,
    linkState,
    getCanvas,
    getGridState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    getWasmExports,
    updateLinkHover: () => updateLinkHover(null),
    markNeedsRender,
    markSearchDirty
  });
  cleanupCanvasFns.push(() => {
    scrollbarRuntime.destroy();
  });
  const positionToCell2 = (event) => {
    const canvas = getCanvas();
    const rect2 = canvas.getBoundingClientRect();
    const { cellW, cellH, cols, rows } = getGridState();
    return positionToCell(event.clientX, event.clientY, rect2, getCurrentDpr(), cellW || 1, cellH || 1, cols || 1, rows || 1, canvas.width, canvas.height);
  };
  const positionToPixel = (event) => {
    const canvas = getCanvas();
    const rect2 = canvas.getBoundingClientRect();
    const fallbackScale = getCurrentDpr() || 1;
    const scaleX = rect2.width > 0 ? canvas.width / rect2.width : fallbackScale;
    const scaleY = rect2.height > 0 ? canvas.height / rect2.height : fallbackScale;
    const x3 = (event.clientX - rect2.left) * scaleX;
    const y = (event.clientY - rect2.top) * scaleY;
    return {
      x: Math.max(1, Math.round(x3 + 1)),
      y: Math.max(1, Math.round(y + 1))
    };
  };
  const normalizeSelectionCell2 = (cell) => {
    const renderState = getLastRenderState();
    const { rows, cols } = getGridState();
    return normalizeSelectionCell(cell, renderState?.rows ?? rows ?? 0, renderState?.cols ?? cols ?? 0, renderState?.wide);
  };
  const clearSelection2 = () => {
    clearPendingDesktopSelection();
    selectionState.active = false;
    selectionState.dragging = false;
    selectionState.anchor = null;
    selectionState.focus = null;
    touchSelectionState.activePointerId = null;
    updateCanvasCursor();
    markNeedsRender();
  };
  cleanupCanvasFns.push(() => {
    clearPendingTouchSelection();
    clearPendingDesktopSelection();
    touchSelectionState.activePointerId = null;
    touchSelectionState.panPointerId = null;
  });
  const setPreedit2 = (text2, updateInput = false) => {
    imeState.preedit = text2 || "";
    if (imeInput && updateInput) {
      imeInput.value = imeState.preedit;
    }
    markNeedsRender();
  };
  const updateImePosition2 = (cursor, cellW, cellH) => {
    if (!imeInput || !cursor)
      return;
    const canvas = getCanvas();
    const rect2 = canvas.getBoundingClientRect();
    const { cols, rows } = getGridState();
    const fallbackScale = getCurrentDpr() || 1;
    const scaleX = rect2.width > 0 ? canvas.width / rect2.width : fallbackScale;
    const scaleY = rect2.height > 0 ? canvas.height / rect2.height : fallbackScale;
    const cssCellW = cellW > 0 ? cellW / Math.max(0.000001, scaleX) : cols > 0 ? rect2.width / cols : 1;
    const cssCellH = cellH > 0 ? cellH / Math.max(0.000001, scaleY) : rows > 0 ? rect2.height / rows : 1;
    const x3 = rect2.left + cursor.col * cssCellW;
    const y = rect2.top + cursor.row * cssCellH;
    imeInput.style.transform = "none";
    imeInput.style.left = `${x3}px`;
    imeInput.style.top = `${y}px`;
  };
  const syncImeSelection2 = () => {
    if (!imeInput)
      return;
    const start = imeInput.selectionStart ?? 0;
    const end = imeInput.selectionEnd ?? start;
    imeState.selectionStart = Math.max(0, Math.min(start, imeInput.value.length));
    imeState.selectionEnd = Math.max(imeState.selectionStart, Math.min(end, imeInput.value.length));
  };
  const bindCanvasEvents = (bindOptions) => {
    if (!attachCanvasEvents)
      return;
    const canvas = getCanvas();
    bindPointerEvents({
      canvas,
      bindOptions,
      touchSelectionMode,
      touchSelectionLongPressMs,
      touchSelectionMoveThresholdPx,
      selectionState,
      touchSelectionState,
      desktopSelectionState,
      linkState,
      cleanupCanvasFns,
      isTouchPointer,
      clearPendingTouchSelection,
      clearPendingDesktopSelection,
      tryActivatePendingTouchSelection,
      beginSelectionDrag,
      scrollViewportByWheel: scrollbarRuntime.scrollViewportByWheel,
      normalizeSelectionCell: normalizeSelectionCell2,
      positionToCell: positionToCell2,
      scrollViewportByLines: scrollbarRuntime.scrollViewportByLines,
      clearSelection: clearSelection2,
      updateCanvasCursor,
      markNeedsRender,
      updateLinkHover,
      getGridState,
      getWasmReady,
      getWasmHandle
    });
    if (imeInput) {
      bindImeEvents({
        bindOptions,
        imeInput,
        imeState,
        cleanupCanvasFns,
        getWasmReady,
        getWasmHandle,
        setPreedit: setPreedit2,
        syncImeSelection: syncImeSelection2
      });
    }
  };
  return {
    selectionState,
    linkState,
    scrollbarState,
    imeState,
    updateCanvasCursor,
    updateLinkHover,
    positionToCell: positionToCell2,
    positionToPixel,
    clearSelection: clearSelection2,
    updateImePosition: updateImePosition2,
    syncScrollbar: scrollbarRuntime.syncScrollbar,
    bindCanvasEvents
  };
}

// src/runtime/create-runtime/kitty-overlay-utils.ts
function toKittySlice(placement, decoded, cellW, cellH) {
  const srcW = decoded.width >>> 0;
  const srcH = decoded.height >>> 0;
  if (!srcW || !srcH)
    return null;
  if (!placement.width || !placement.height)
    return null;
  const sxRaw = Math.max(0, Math.min(placement.sourceX >>> 0, srcW));
  const syRaw = Math.max(0, Math.min(placement.sourceY >>> 0, srcH));
  const swMax = Math.max(0, srcW - sxRaw);
  const shMax = Math.max(0, srcH - syRaw);
  const sx = sxRaw;
  const sy = syRaw;
  const sw = Math.max(0, Math.min(placement.sourceWidth >>> 0, swMax));
  const sh = Math.max(0, Math.min(placement.sourceHeight >>> 0, shMax));
  if (!sw || !sh)
    return null;
  const dx = placement.x * cellW + placement.cellOffsetX;
  const dy = placement.y * cellH + placement.cellOffsetY;
  return {
    sx,
    sy,
    sw,
    sh,
    dx,
    dy,
    dw: placement.width,
    dh: placement.height
  };
}

// src/runtime/create-runtime/interaction-runtime/kitty-image-cache.ts
var KITTY_FMT_GRAY = 1;
var KITTY_FMT_GRAY_ALPHA = 2;
var KITTY_FMT_RGB = 3;
var KITTY_FMT_RGBA = 4;
var KITTY_FMT_PNG = 100;
function createKittyImageCache(options) {
  const { getWasm, markNeedsRender } = options;
  const kittyImageCache = new Map;
  const kittyImageKeyById = new Map;
  const kittyDecodePending = new Set;
  const kittyDecodeWarnings = new Set;
  const releaseKittyImage = (entry) => {
    const source = entry?.source;
    if (source && typeof source.close === "function") {
      try {
        source.close();
      } catch {
      }
    }
  };
  const decodeRawKittyImage = (placement, key, bytes) => {
    const width = placement.imageWidth >>> 0;
    const height = placement.imageHeight >>> 0;
    if (!width || !height || typeof document === "undefined")
      return null;
    const pixelCount = width * height;
    const out = new Uint8ClampedArray(pixelCount * 4);
    if (placement.imageFormat === KITTY_FMT_GRAY) {
      if (bytes.length < pixelCount)
        return null;
      for (let i3 = 0;i3 < pixelCount; i3 += 1) {
        const v = bytes[i3] ?? 0;
        const o = i3 * 4;
        out[o] = v;
        out[o + 1] = v;
        out[o + 2] = v;
        out[o + 3] = 255;
      }
    } else if (placement.imageFormat === KITTY_FMT_GRAY_ALPHA) {
      if (bytes.length < pixelCount * 2)
        return null;
      for (let i3 = 0;i3 < pixelCount; i3 += 1) {
        const o = i3 * 4;
        const s3 = i3 * 2;
        const v = bytes[s3] ?? 0;
        out[o] = v;
        out[o + 1] = v;
        out[o + 2] = v;
        out[o + 3] = bytes[s3 + 1] ?? 255;
      }
    } else if (placement.imageFormat === KITTY_FMT_RGB) {
      if (bytes.length < pixelCount * 3)
        return null;
      for (let i3 = 0;i3 < pixelCount; i3 += 1) {
        const o = i3 * 4;
        const s3 = i3 * 3;
        out[o] = bytes[s3] ?? 0;
        out[o + 1] = bytes[s3 + 1] ?? 0;
        out[o + 2] = bytes[s3 + 2] ?? 0;
        out[o + 3] = 255;
      }
    } else if (placement.imageFormat === KITTY_FMT_RGBA) {
      if (bytes.length < pixelCount * 4)
        return null;
      out.set(bytes.subarray(0, pixelCount * 4));
    } else {
      return null;
    }
    const surface = document.createElement("canvas");
    surface.width = width;
    surface.height = height;
    const ctx = surface.getContext("2d");
    if (!ctx)
      return null;
    ctx.putImageData(new ImageData(out, width, height), 0, 0);
    return {
      key,
      width,
      height,
      source: surface,
      pixels: new Uint8Array(out.buffer, out.byteOffset, out.byteLength)
    };
  };
  const extractPngPixels = (bitmap) => {
    if (typeof document === "undefined")
      return;
    const surface = document.createElement("canvas");
    surface.width = bitmap.width;
    surface.height = bitmap.height;
    const ctx = surface.getContext("2d", { willReadFrequently: true });
    if (!ctx)
      return;
    try {
      ctx.drawImage(bitmap, 0, 0);
      const rgba = ctx.getImageData(0, 0, bitmap.width, bitmap.height).data;
      return new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength);
    } catch {
      return;
    }
  };
  const resolveKittyImage = (placement) => {
    const wasm = getWasm();
    if (!wasm)
      return null;
    const ptr = placement.imageDataPtr >>> 0;
    const len = placement.imageDataLen >>> 0;
    if (!ptr || !len)
      return null;
    const key = [
      placement.imageId,
      placement.imageFormat,
      placement.imageWidth,
      placement.imageHeight,
      ptr,
      len
    ].join(":");
    kittyImageKeyById.set(placement.imageId, key);
    const cached = kittyImageCache.get(placement.imageId);
    if (cached?.key === key)
      return cached;
    const memory = wasm.memory.buffer;
    if (ptr + len > memory.byteLength)
      return null;
    const copy = new Uint8Array(len);
    copy.set(new Uint8Array(memory, ptr, len));
    if (placement.imageFormat === KITTY_FMT_PNG) {
      if (kittyDecodePending.has(key))
        return null;
      kittyDecodePending.add(key);
      createImageBitmap(new Blob([copy], { type: "image/png" })).then((bitmap) => {
        kittyDecodePending.delete(key);
        if (kittyImageKeyById.get(placement.imageId) !== key) {
          bitmap.close();
          return;
        }
        const current = kittyImageCache.get(placement.imageId);
        if (current && current.key !== key)
          releaseKittyImage(current);
        kittyImageCache.set(placement.imageId, {
          key,
          width: bitmap.width,
          height: bitmap.height,
          source: bitmap,
          pixels: extractPngPixels(bitmap)
        });
        markNeedsRender();
      }).catch((error) => {
        kittyDecodePending.delete(key);
        if (!kittyDecodeWarnings.has(key)) {
          kittyDecodeWarnings.add(key);
          console.warn("[kitty] image decode failed", {
            imageId: placement.imageId,
            key,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      });
      return null;
    }
    const decoded = decodeRawKittyImage(placement, key, copy);
    if (!decoded)
      return null;
    if (cached && cached.key !== key)
      releaseKittyImage(cached);
    kittyImageCache.set(placement.imageId, decoded);
    return decoded;
  };
  const pruneInactiveImages = (activeImageIds) => {
    let cacheDirty = false;
    for (const [imageId, entry] of kittyImageCache.entries()) {
      if (activeImageIds.has(imageId))
        continue;
      releaseKittyImage(entry);
      kittyImageCache.delete(imageId);
      kittyImageKeyById.delete(imageId);
      cacheDirty = true;
    }
    return cacheDirty;
  };
  const clearKittyImageCache = () => {
    for (const entry of kittyImageCache.values()) {
      releaseKittyImage(entry);
    }
    kittyImageCache.clear();
    kittyImageKeyById.clear();
    kittyDecodePending.clear();
  };
  return {
    resolveKittyImage,
    clearKittyImageCache,
    pruneInactiveImages
  };
}

// src/runtime/create-runtime/kitty-render-runtime.ts
var GPU_TEXTURE_USAGE = 4 | 2;
function createKittyRenderRuntime(options) {
  const kittyImageCache = createKittyImageCache(options);
  const webglTextures = new Map;
  const webgpuTextures = new Map;
  const webgpuSamplerCache = new WeakMap;
  const webglUploadWarnings = new Set;
  const webgpuUploadWarnings = new Set;
  const releaseWebGLTexture = (entry) => {
    if (!entry)
      return;
    try {
      entry.gl.deleteTexture(entry.texture);
    } catch {
    }
  };
  const releaseWebGPUTexture = (entry) => {
    if (!entry)
      return;
    try {
      entry.texture.destroy();
    } catch {
    }
  };
  const pruneWebGLTextures = (activeImageIds) => {
    for (const [imageId, entry] of webglTextures.entries()) {
      if (activeImageIds.has(imageId))
        continue;
      releaseWebGLTexture(entry);
      webglTextures.delete(imageId);
    }
  };
  const pruneWebGPUTextures = (activeImageIds) => {
    for (const [imageId, entry] of webgpuTextures.entries()) {
      if (activeImageIds.has(imageId))
        continue;
      releaseWebGPUTexture(entry);
      webgpuTextures.delete(imageId);
    }
  };
  const clearWebGLKittyTextures = () => {
    for (const entry of webglTextures.values()) {
      releaseWebGLTexture(entry);
    }
    webglTextures.clear();
  };
  const clearWebGPUKittyTextures = () => {
    for (const entry of webgpuTextures.values()) {
      releaseWebGPUTexture(entry);
    }
    webgpuTextures.clear();
  };
  const clearKittyRenderCaches = () => {
    clearWebGLKittyTextures();
    clearWebGPUKittyTextures();
    kittyImageCache.clearKittyImageCache();
  };
  const collectKittyDrawPlan = (placements, cellW, cellH) => {
    const underlay = [];
    const overlay = [];
    const activeImageIds = new Set;
    const orderedPlacements = placements.map((placement, index) => ({ placement, index })).sort((a3, b3) => {
      const z = (a3.placement.z | 0) - (b3.placement.z | 0);
      return z !== 0 ? z : a3.index - b3.index;
    });
    for (const { placement } of orderedPlacements) {
      activeImageIds.add(placement.imageId);
      const decoded = kittyImageCache.resolveKittyImage(placement);
      if (!decoded)
        continue;
      const slice = toKittySlice(placement, decoded, cellW, cellH);
      if (!slice)
        continue;
      const target = (placement.z | 0) < 0 ? underlay : overlay;
      target.push({
        imageId: placement.imageId,
        key: decoded.key,
        source: decoded.source,
        pixels: decoded.pixels,
        imageWidth: decoded.width,
        imageHeight: decoded.height,
        sx: slice.sx,
        sy: slice.sy,
        sw: slice.sw,
        sh: slice.sh,
        dx: slice.dx,
        dy: slice.dy,
        dw: slice.dw,
        dh: slice.dh,
        z: placement.z | 0
      });
    }
    kittyImageCache.pruneInactiveImages(activeImageIds);
    pruneWebGLTextures(activeImageIds);
    pruneWebGPUTextures(activeImageIds);
    return { underlay, overlay };
  };
  const resolveKittyWebGLTexture = (gl, slice) => {
    const cached = webglTextures.get(slice.imageId);
    if (cached && cached.key === slice.key && cached.gl === gl) {
      return cached.texture;
    }
    if (cached) {
      releaseWebGLTexture(cached);
      webglTextures.delete(slice.imageId);
    }
    const texture = gl.createTexture();
    if (!texture)
      return null;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, slice.source);
    } catch {
      if (!webglUploadWarnings.has(slice.key)) {
        webglUploadWarnings.add(slice.key);
        console.warn("[kitty] WebGL texture upload failed", {
          imageId: slice.imageId,
          key: slice.key,
          width: slice.imageWidth,
          height: slice.imageHeight
        });
      }
      gl.deleteTexture(texture);
      return null;
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    webglTextures.set(slice.imageId, { gl, key: slice.key, texture });
    return texture;
  };
  const getWebGPUSamplers = (device) => {
    const cached = webgpuSamplerCache.get(device);
    if (cached)
      return cached;
    const samplers = {
      nearest: device.createSampler({
        minFilter: "nearest",
        magFilter: "nearest",
        mipmapFilter: "nearest"
      }),
      linear: device.createSampler({
        minFilter: "linear",
        magFilter: "linear",
        mipmapFilter: "linear"
      })
    };
    webgpuSamplerCache.set(device, samplers);
    return samplers;
  };
  const alignTo256 = (value) => value + 255 & ~255;
  const uploadKittyPixelsToWebGPU = (device, texture, width, height, pixels) => {
    const rowBytes = width * 4;
    const alignedRowBytes = alignTo256(rowBytes);
    const needsPadding = alignedRowBytes !== rowBytes;
    const data = needsPadding ? new Uint8Array(alignedRowBytes * height) : pixels;
    if (needsPadding) {
      for (let row = 0;row < height; row += 1) {
        const srcStart = row * rowBytes;
        const srcEnd = srcStart + rowBytes;
        data.set(pixels.subarray(srcStart, srcEnd), row * alignedRowBytes);
      }
    }
    device.queue.writeTexture({ texture }, data, { offset: 0, bytesPerRow: alignedRowBytes, rowsPerImage: height }, { width, height, depthOrArrayLayers: 1 });
  };
  const resolveKittyWebGPUBindGroup = (state2, slice, nearest = false) => {
    const { device } = state2;
    const cached = webgpuTextures.get(slice.imageId);
    if (cached && cached.key === slice.key && cached.device === device) {
      return nearest ? cached.bindGroupNearest : cached.bindGroupLinear;
    }
    if (cached) {
      releaseWebGPUTexture(cached);
      webgpuTextures.delete(slice.imageId);
    }
    const width = Math.max(1, slice.imageWidth | 0);
    const height = Math.max(1, slice.imageHeight | 0);
    const texture = device.createTexture({
      size: { width, height, depthOrArrayLayers: 1 },
      format: "rgba8unorm",
      usage: GPU_TEXTURE_USAGE
    });
    try {
      const pixelCount = width * height * 4;
      if (slice.pixels && slice.pixels.length >= pixelCount) {
        uploadKittyPixelsToWebGPU(device, texture, width, height, slice.pixels);
      } else {
        device.queue.copyExternalImageToTexture({ source: slice.source }, { texture }, { width, height, depthOrArrayLayers: 1 });
      }
    } catch {
      if (!webgpuUploadWarnings.has(slice.key)) {
        webgpuUploadWarnings.add(slice.key);
        console.warn("[kitty] WebGPU texture upload failed", {
          imageId: slice.imageId,
          key: slice.key,
          width,
          height
        });
      }
      texture.destroy();
      return null;
    }
    const textureView = texture.createView();
    const samplers = getWebGPUSamplers(device);
    const bindGroupNearest = device.createBindGroup({
      layout: state2.glyphPipelineNearest.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: state2.uniformBuffer } },
        { binding: 1, resource: samplers.nearest },
        { binding: 2, resource: textureView }
      ]
    });
    const bindGroupLinear = device.createBindGroup({
      layout: state2.glyphPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: state2.uniformBuffer } },
        { binding: 1, resource: samplers.linear },
        { binding: 2, resource: textureView }
      ]
    });
    webgpuTextures.set(slice.imageId, {
      device,
      key: slice.key,
      texture,
      bindGroupNearest,
      bindGroupLinear
    });
    return nearest ? bindGroupNearest : bindGroupLinear;
  };
  return {
    collectKittyDrawPlan,
    resolveKittyWebGLTexture,
    resolveKittyWebGPUBindGroup,
    clearWebGLKittyTextures,
    clearWebGPUKittyTextures,
    clearKittyRenderCaches
  };
}

// src/runtime/create-runtime/lifecycle-theme-size-canvas.ts
function createLifecycleCanvasHandlers(deps) {
  let sizeRaf = 0;
  let savedCanvasState = null;
  function destroyGlyphAtlases() {
    const activeState = deps.getActiveState();
    if (!activeState?.glyphAtlases)
      return;
    if ("gl" in activeState) {
      for (const atlas of activeState.glyphAtlases.values()) {
        try {
          activeState.gl.deleteTexture(atlas.texture);
        } catch {
        }
      }
      activeState.glyphAtlases.clear();
      return;
    }
    for (const atlas of activeState.glyphAtlases.values()) {
      try {
        atlas.texture.destroy();
      } catch {
      }
    }
    activeState.glyphAtlases.clear();
  }
  function saveCanvasState() {
    const canvas = deps.getCanvas();
    savedCanvasState = {
      width: canvas.width,
      height: canvas.height,
      dpr: deps.getCurrentDpr(),
      gridCols: deps.gridState.cols,
      gridRows: deps.gridState.rows,
      cellW: deps.gridState.cellW,
      cellH: deps.gridState.cellH,
      fontSizePx: deps.gridState.fontSizePx
    };
  }
  function restoreCanvasState() {
    if (!savedCanvasState)
      return;
    const canvas = deps.getCanvas();
    canvas.width = savedCanvasState.width;
    canvas.height = savedCanvasState.height;
    deps.setCurrentDpr(savedCanvasState.dpr);
    deps.gridState.cols = savedCanvasState.gridCols;
    deps.gridState.rows = savedCanvasState.gridRows;
    deps.gridState.cellW = savedCanvasState.cellW;
    deps.gridState.cellH = savedCanvasState.cellH;
    deps.gridState.fontSizePx = savedCanvasState.fontSizePx;
    savedCanvasState = null;
  }
  function replaceCanvas() {
    const canvas = deps.getCanvas();
    const parent = canvas.parentElement;
    if (!parent)
      return;
    saveCanvasState();
    for (const cleanup of deps.cleanupCanvasFns)
      cleanup();
    deps.cleanupCanvasFns.length = 0;
    deps.clearKittyRenderCaches();
    deps.destroyWebGPUStageTargets();
    const activeState = deps.getActiveState();
    if (activeState && "gl" in activeState) {
      deps.clearWebGLShaderStages(activeState);
      deps.destroyWebGLStageTargets(activeState);
    } else {
      deps.clearWebGLShaderStages();
      deps.destroyWebGLStageTargets();
    }
    const newCanvas = document.createElement("canvas");
    newCanvas.id = canvas.id;
    newCanvas.className = canvas.className;
    canvas.width = 0;
    canvas.height = 0;
    parent.replaceChild(newCanvas, canvas);
    deps.setCanvas(newCanvas);
    deps.setIsFocused(typeof document !== "undefined" ? document.activeElement === deps.getCanvas() : true);
    restoreCanvasState();
    deps.bindCanvasEvents();
    bindFocusEvents();
    deps.setCurrentContextType(null);
    for (const entry of deps.fontState.fonts) {
      if (entry) {
        entry.atlas = null;
        entry.glyphIds = new Set;
        entry.fontSizePx = 0;
      }
    }
    destroyGlyphAtlases();
    deps.setShaderStagesDirty(true);
  }
  function updateSize(force = false) {
    const canvas = deps.getCanvas();
    const dpr = window.devicePixelRatio || 1;
    const rect2 = canvas.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.floor(rect2.width * dpr));
    const nextHeight = Math.max(1, Math.floor(rect2.height * dpr));
    const sizeChanged = nextWidth !== canvas.width || nextHeight !== canvas.height || dpr !== deps.getCurrentDpr();
    if (!sizeChanged && !force)
      return;
    canvas.width = nextWidth;
    canvas.height = nextHeight;
    if (deps.dprEl)
      deps.dprEl.textContent = dpr.toFixed(2);
    deps.callbacks?.onDpr?.(dpr);
    if (deps.sizeEl)
      deps.sizeEl.textContent = `${canvas.width}x${canvas.height}`;
    deps.callbacks?.onCanvasSize?.(canvas.width, canvas.height);
    deps.setCurrentDpr(dpr);
    deps.resizeState.dpr = dpr;
    deps.resizeState.active = true;
    deps.resizeState.lastAt = performance.now();
    const metrics = deps.computeCellMetrics();
    if (metrics?.cellW && metrics?.cellH) {
      deps.resizeState.cols = Math.max(1, Math.floor(canvas.width / metrics.cellW));
      deps.resizeState.rows = Math.max(1, Math.floor(canvas.height / metrics.cellH));
    }
    deps.updateGrid();
    deps.markNeedsRender();
    deps.resetLastRenderTime();
  }
  function resize(cols, rows) {
    const nextCols = Math.max(1, Math.floor(Number(cols)));
    const nextRows = Math.max(1, Math.floor(Number(rows)));
    if (!Number.isFinite(nextCols) || !Number.isFinite(nextRows))
      return;
    const dpr = window.devicePixelRatio || 1;
    if (dpr !== deps.getCurrentDpr()) {
      deps.setCurrentDpr(dpr);
      if (deps.dprEl)
        deps.dprEl.textContent = dpr.toFixed(2);
      deps.callbacks?.onDpr?.(dpr);
    }
    const metrics = deps.computeCellMetrics();
    if (!metrics)
      return;
    const canvas = deps.getCanvas();
    canvas.width = Math.max(1, nextCols * metrics.cellW);
    canvas.height = Math.max(1, nextRows * metrics.cellH);
    if (deps.sizeEl)
      deps.sizeEl.textContent = `${canvas.width}x${canvas.height}`;
    deps.callbacks?.onCanvasSize?.(canvas.width, canvas.height);
    deps.resizeState.dpr = deps.getCurrentDpr();
    deps.resizeState.active = true;
    deps.resizeState.lastAt = performance.now();
    deps.resizeState.cols = nextCols;
    deps.resizeState.rows = nextRows;
    deps.updateGrid();
    deps.markNeedsRender();
    deps.resetLastRenderTime();
  }
  function scheduleSizeUpdate() {
    updateSize();
    if (sizeRaf)
      return;
    sizeRaf = requestAnimationFrame(() => {
      sizeRaf = 0;
      updateSize();
    });
  }
  function focusTypingInput() {
    const canvas = deps.getCanvas();
    canvas.focus({ preventScroll: true });
    if (!deps.imeInput)
      return;
    const focusImeInput = () => {
      deps.imeInput?.focus({ preventScroll: true });
    };
    focusImeInput();
    if (typeof document !== "undefined" && document.activeElement !== deps.imeInput) {
      requestAnimationFrame(() => {
        focusImeInput();
        if (document.activeElement !== deps.imeInput) {
          setTimeout(focusImeInput, 0);
        }
      });
    }
  }
  function focus() {
    focusTypingInput();
    const canvas = deps.getCanvas();
    deps.setIsFocused(typeof document !== "undefined" && deps.imeInput ? document.activeElement === canvas || document.activeElement === deps.imeInput : true);
  }
  function blur() {
    const canvas = deps.getCanvas();
    if (deps.imeInput && document.activeElement === deps.imeInput) {
      deps.imeInput.blur();
    }
    if (document.activeElement === canvas) {
      canvas.blur();
    }
    deps.setIsFocused(false);
  }
  function bindFocusEvents() {
    if (!deps.attachCanvasEvents)
      return;
    const canvas = deps.getCanvas();
    canvas.tabIndex = 0;
    const handleFocus = () => {
      deps.setIsFocused(true);
      focusTypingInput();
      deps.markNeedsRender();
      deps.resetLastRenderTime();
      if (deps.getInputHandler()?.isFocusReporting?.()) {
        deps.sendKeyInput("\x1B[I", "program");
      }
    };
    const handleBlur = () => {
      const stillFocused = typeof document !== "undefined" && deps.imeInput ? document.activeElement === deps.imeInput : false;
      deps.setIsFocused(stillFocused);
      deps.markNeedsRender();
      deps.resetLastRenderTime();
      if (!stillFocused && deps.getInputHandler()?.isFocusReporting?.()) {
        deps.sendKeyInput("\x1B[O", "program");
      }
    };
    const handlePointerFocus = () => {
      focusTypingInput();
    };
    canvas.addEventListener("pointerdown", handlePointerFocus);
    canvas.addEventListener("focus", handleFocus);
    canvas.addEventListener("blur", handleBlur);
    deps.cleanupCanvasFns.push(() => {
      canvas.removeEventListener("pointerdown", handlePointerFocus);
      canvas.removeEventListener("focus", handleFocus);
      canvas.removeEventListener("blur", handleBlur);
    });
  }
  function bindAutoResizeEvents() {
    const hasResizeObserver = deps.autoResize && "ResizeObserver" in window;
    if (deps.attachWindowEvents && deps.autoResize && !hasResizeObserver) {
      window.addEventListener("resize", scheduleSizeUpdate);
      window.addEventListener("load", scheduleSizeUpdate);
      deps.cleanupFns.push(() => {
        window.removeEventListener("resize", scheduleSizeUpdate);
        window.removeEventListener("load", scheduleSizeUpdate);
      });
    }
    if (hasResizeObserver) {
      const ro = new ResizeObserver(() => scheduleSizeUpdate());
      const target = deps.getCanvas().parentElement ?? document.body;
      ro.observe(target);
      deps.cleanupFns.push(() => ro.disconnect());
    }
  }
  function cancelScheduledSizeUpdate() {
    if (!sizeRaf)
      return;
    cancelAnimationFrame(sizeRaf);
    sizeRaf = 0;
  }
  return {
    replaceCanvas,
    updateSize,
    resize,
    scheduleSizeUpdate,
    focusTypingInput,
    focus,
    blur,
    bindFocusEvents,
    bindAutoResizeEvents,
    cancelScheduledSizeUpdate
  };
}

// src/runtime/create-runtime/highlight-terminal-color-utils.ts
function runtimeTerminalColorFromTheme(value) {
  if (value === "cell-foreground")
    return { kind: "cell-foreground" };
  if (value === "cell-background")
    return { kind: "cell-background" };
  return {
    kind: "color",
    color: colorToFloats(value, 255)
  };
}
function resolveHighlightBackgroundColor(value, cellFg, cellBg, inverse) {
  switch (value.kind) {
    case "color":
      return value.color;
    case "cell-foreground":
      return inverse ? cellBg : cellFg;
    case "cell-background":
      return inverse ? cellFg : cellBg;
  }
}
function resolveHighlightForegroundColor(value, cellFg, cellBg, inverse) {
  switch (value.kind) {
    case "color":
      return value.color;
    case "cell-foreground":
      return inverse ? cellBg : cellFg;
    case "cell-background":
      return inverse ? cellFg : cellBg;
  }
}

// src/runtime/create-runtime/lifecycle-theme-size-theme.ts
function createLifecycleThemeHandlers(deps) {
  function applyTheme(theme, sourceLabel = "theme") {
    if (!theme)
      return;
    if (theme.colors.background) {
      deps.setDefaultBg(colorToFloats(theme.colors.background, 255));
    }
    if (theme.colors.foreground) {
      deps.setDefaultFg(colorToFloats(theme.colors.foreground, 255));
    }
    if (theme.colors.selectionBackground) {
      deps.setSelectionBackgroundColor(runtimeTerminalColorFromTheme(theme.colors.selectionBackground));
    }
    if (theme.colors.selectionForeground) {
      deps.setSelectionForegroundColor(runtimeTerminalColorFromTheme(theme.colors.selectionForeground));
    }
    if (theme.colors.searchBackground) {
      deps.setSearchMatchBackgroundColor(runtimeTerminalColorFromTheme(theme.colors.searchBackground));
    }
    if (theme.colors.searchForeground) {
      deps.setSearchMatchTextColor(runtimeTerminalColorFromTheme(theme.colors.searchForeground));
    }
    if (theme.colors.searchSelectedBackground) {
      deps.setSearchCurrentMatchBackgroundColor(runtimeTerminalColorFromTheme(theme.colors.searchSelectedBackground));
    }
    if (theme.colors.searchSelectedForeground) {
      deps.setSearchCurrentMatchTextColor(runtimeTerminalColorFromTheme(theme.colors.searchSelectedForeground));
    }
    if (theme.colors.cursor) {
      deps.setCursorFallback(colorToFloats(theme.colors.cursor, 255));
    }
    deps.setActiveTheme(theme);
    const wasmReady = deps.getWasmReady();
    const wasm = deps.getWasm();
    const wasmHandle = deps.getWasmHandle();
    if (wasmReady && wasm && wasmHandle) {
      const fg = theme.colors.foreground ? colorToRgbU32(theme.colors.foreground) : 4294967295;
      const bg = theme.colors.background ? colorToRgbU32(theme.colors.background) : 4294967295;
      const cursor = theme.colors.cursor ? colorToRgbU32(theme.colors.cursor) : 4294967295;
      if (wasm.setDefaultColors) {
        wasm.setDefaultColors(wasmHandle, fg, bg, cursor);
      }
      const palette = theme.colors.palette;
      let maxIndex = -1;
      for (let i3 = palette.length - 1;i3 >= 0; i3 -= 1) {
        if (palette[i3]) {
          maxIndex = i3;
          break;
        }
      }
      if (maxIndex >= 0 && wasm.setPalette) {
        const count = maxIndex + 1;
        const bytes = new Uint8Array(count * 3);
        for (let i3 = 0;i3 < count; i3 += 1) {
          const color = palette[i3];
          if (!color)
            continue;
          const base = i3 * 3;
          bytes[base] = color.r & 255;
          bytes[base + 1] = color.g & 255;
          bytes[base + 2] = color.b & 255;
        }
        wasm.setPalette(wasmHandle, bytes, count);
      }
      wasm.renderUpdate(wasmHandle);
    }
    deps.markNeedsRender();
    deps.appendLog(`[ui] theme applied (${sourceLabel})`);
  }
  function resetTheme() {
    deps.setDefaultBg([...deps.defaultBgBase]);
    deps.setDefaultFg([...deps.defaultFgBase]);
    deps.setSelectionBackgroundColor(deps.selectionBackgroundBase);
    deps.setSelectionForegroundColor(deps.selectionForegroundBase);
    deps.setSearchMatchBackgroundColor(deps.searchMatchBackgroundBase);
    deps.setSearchCurrentMatchBackgroundColor(deps.searchCurrentMatchBackgroundBase);
    deps.setSearchMatchTextColor(deps.searchMatchTextBase);
    deps.setSearchCurrentMatchTextColor(deps.searchCurrentMatchTextBase);
    deps.setCursorFallback([...deps.cursorBase]);
    deps.setActiveTheme(null);
    const wasmReady = deps.getWasmReady();
    const wasm = deps.getWasm();
    const wasmHandle = deps.getWasmHandle();
    if (wasmReady && wasm && wasmHandle) {
      const fg = 16777215;
      const bg = 0;
      const cursor = 16777215;
      if (wasm.setDefaultColors) {
        wasm.setDefaultColors(wasmHandle, fg, bg, cursor);
      }
      if (wasm.resetPalette) {
        wasm.resetPalette(wasmHandle);
      }
      wasm.renderUpdate(wasmHandle);
    }
    deps.markNeedsRender();
    deps.appendLog("[ui] theme reset (default)");
  }
  return { applyTheme, resetTheme };
}

// src/runtime/create-runtime/lifecycle-theme-size.ts
function createRuntimeLifecycleThemeSize(deps) {
  const { applyTheme, resetTheme } = createLifecycleThemeHandlers(deps);
  const {
    replaceCanvas,
    updateSize,
    resize,
    scheduleSizeUpdate,
    focusTypingInput,
    focus,
    blur,
    bindFocusEvents,
    bindAutoResizeEvents,
    cancelScheduledSizeUpdate
  } = createLifecycleCanvasHandlers(deps);
  return {
    applyTheme,
    resetTheme,
    replaceCanvas,
    updateSize,
    resize,
    scheduleSizeUpdate,
    focusTypingInput,
    focus,
    blur,
    bindFocusEvents,
    bindAutoResizeEvents,
    cancelScheduledSizeUpdate,
    getActiveTheme: deps.getActiveTheme
  };
}

// src/runtime/create-runtime/search-runtime.ts
var SEARCH_STEP_BUDGET = 64;
function sortViewportMatches(matches) {
  if (matches.length < 2)
    return matches;
  matches.sort((a3, b3) => a3.row - b3.row || a3.startCol - b3.startCol || a3.endCol - b3.endCol || Number(b3.selected) - Number(a3.selected));
  return matches;
}
function createRuntimeSearch(options) {
  let rafId = 0;
  let pendingSync = false;
  let viewportDirty = false;
  let lastGeneration = 0;
  let viewportMatches = [];
  let state2 = {
    query: "",
    active: false,
    pending: false,
    complete: false,
    total: 0,
    selectedIndex: null
  };
  const emitState = (next) => {
    if (state2.query === next.query && state2.active === next.active && state2.pending === next.pending && state2.complete === next.complete && state2.total === next.total && state2.selectedIndex === next.selectedIndex) {
      return;
    }
    state2 = next;
    options.callbacks?.onSearchState?.(next);
  };
  const getWasmContext = () => {
    if (!options.getWasmReady())
      return null;
    const wasm = options.getWasm();
    const handle = options.getWasmHandle();
    if (!wasm || !handle)
      return null;
    return { wasm, handle };
  };
  const updateFromWasm = (status, matches) => {
    if (matches) {
      viewportMatches = sortViewportMatches(matches);
    }
    emitState({
      query: state2.query,
      active: status.active,
      pending: status.pending,
      complete: status.complete,
      total: status.totalMatches,
      selectedIndex: status.selectedIndex
    });
    if (status.generation !== lastGeneration || matches) {
      lastGeneration = status.generation;
      options.markNeedsRender();
    }
  };
  const syncNow = () => {
    const ctx = getWasmContext();
    if (!ctx)
      return false;
    const { wasm, handle } = ctx;
    if (pendingSync) {
      if (state2.query) {
        wasm.setSearchQuery(handle, state2.query);
      } else {
        wasm.clearSearch(handle);
      }
      pendingSync = false;
      viewportDirty = Boolean(state2.query);
    }
    if (state2.query && (state2.pending || viewportDirty)) {
      wasm.stepSearch(handle, viewportDirty && !state2.pending ? 1 : SEARCH_STEP_BUDGET);
      viewportDirty = false;
    }
    const status = wasm.getSearchStatus(handle);
    const matches = status.active ? wasm.getSearchViewportMatches(handle) : [];
    updateFromWasm(status, matches);
    return true;
  };
  const runFrame = () => {
    rafId = 0;
    const synced = syncNow();
    if (synced && state2.active && (state2.pending || viewportDirty || pendingSync)) {
      schedule();
    }
  };
  const schedule = () => {
    if (rafId)
      return;
    rafId = requestAnimationFrame(runFrame);
  };
  const resetLocal = () => {
    viewportMatches = [];
    lastGeneration = 0;
    emitState({
      query: "",
      active: false,
      pending: false,
      complete: false,
      total: 0,
      selectedIndex: null
    });
    options.markNeedsRender();
  };
  const clear = () => {
    pendingSync = true;
    viewportDirty = false;
    emitState({
      query: "",
      active: false,
      pending: false,
      complete: false,
      total: 0,
      selectedIndex: null
    });
    viewportMatches = [];
    schedule();
  };
  const setQuery = (query) => {
    const nextQuery = query ?? "";
    pendingSync = true;
    viewportDirty = Boolean(nextQuery);
    emitState({
      query: nextQuery,
      active: nextQuery.length > 0,
      pending: nextQuery.length > 0,
      complete: false,
      total: 0,
      selectedIndex: null
    });
    viewportMatches = [];
    schedule();
  };
  const navigate = (direction) => {
    const ctx = getWasmContext();
    if (!ctx || !state2.active)
      return;
    if (direction === "next") {
      ctx.wasm.searchNext(ctx.handle);
    } else {
      ctx.wasm.searchPrevious(ctx.handle);
    }
    ctx.wasm.renderUpdate(ctx.handle);
    const status = ctx.wasm.getSearchStatus(ctx.handle);
    const matches = status.active ? ctx.wasm.getSearchViewportMatches(ctx.handle) : [];
    updateFromWasm(status, matches);
    if (status.pending) {
      schedule();
    }
  };
  const markDirty = () => {
    if (!state2.active)
      return;
    viewportDirty = true;
    schedule();
  };
  const handleWasmReset = () => {
    if (!state2.query) {
      resetLocal();
      return;
    }
    pendingSync = true;
    viewportDirty = true;
    emitState({
      ...state2,
      active: true,
      pending: true,
      complete: false,
      total: 0,
      selectedIndex: null
    });
    schedule();
  };
  options.cleanupFns.push(() => {
    if (rafId)
      cancelAnimationFrame(rafId);
    rafId = 0;
  });
  return {
    setQuery,
    clear,
    next: () => navigate("next"),
    previous: () => navigate("previous"),
    getState: () => ({ ...state2 }),
    getViewportMatches: () => viewportMatches,
    markDirty,
    handleWasmReset
  };
}

// src/runtime/create-runtime/render-tick-webgl-context.ts
function buildWebGLTickContext(deps, state2) {
  const {
    isShaderStagesDirty,
    rebuildWebGLShaderStages,
    setShaderStagesDirty,
    getCompiledWebGLShaderStages,
    ensureWebGLStageTargets,
    canvas,
    defaultBg,
    fontError,
    termDebug,
    reportDebugText,
    updateGrid,
    getRenderState,
    fontState,
    resolveBlendFlags: resolveBlendFlags2,
    alphaBlending,
    reportTermSize,
    resolveCursorPosition,
    reportCursor,
    FORCE_CURSOR_BLINK,
    CURSOR_BLINK_MS,
    imeInput,
    resolveCursorStyle,
    isFocused,
    imeState,
    resolveImeAnchor: resolveImeAnchor2,
    wasmExports,
    wasmHandle,
    gridState,
    fontHeightUnits: fontHeightUnits2,
    updateImePosition: updateImePosition2,
    fontScaleOverride: fontScaleOverride2,
    FONT_SCALE_OVERRIDES,
    isSymbolFont: isSymbolFont2,
    isAppleSymbolsFont: isAppleSymbolsFont2,
    isColorEmojiFont: isColorEmojiFont2,
    fontAdvanceUnits: fontAdvanceUnits2,
    shapeClusterWithFont,
    fontMaxCellSpan: fontMaxCellSpan2,
    clamp: clamp2,
    buildNerdMetrics: buildNerdMetrics2,
    nerdIconScale
  } = deps;
  const { gl } = state2;
  if (isShaderStagesDirty()) {
    rebuildWebGLShaderStages(state2);
    setShaderStagesDirty(false);
  }
  const compiledWebGLStages = getCompiledWebGLShaderStages();
  const stageTargets = compiledWebGLStages.length > 0 ? ensureWebGLStageTargets(state2) : null;
  const hasShaderStages = compiledWebGLStages.length > 0 && !!stageTargets;
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.bindFramebuffer(gl.FRAMEBUFFER, hasShaderStages && stageTargets ? stageTargets.sceneFramebuffer : null);
  gl.clearColor(defaultBg[0], defaultBg[1], defaultBg[2], defaultBg[3]);
  gl.clear(gl.COLOR_BUFFER_BIT);
  if (fontError) {
    const text2 = `Font error: ${fontError.message}`;
    if (termDebug)
      termDebug.textContent = text2;
    reportDebugText(text2);
  }
  updateGrid();
  const render = getRenderState();
  if (!render || !fontState.font) {
    return null;
  }
  deps.lastRenderState = render;
  const {
    rows,
    cols,
    codepoints,
    contentTags,
    wide,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    graphemeOffset,
    graphemeLen,
    graphemeBuffer,
    cursor
  } = render;
  if (!codepoints || !fgBytes) {
    return null;
  }
  const mergedEmojiSkip = new Uint8Array(codepoints.length);
  const readCellCluster = (cellIndex) => {
    const flag = wide ? wide[cellIndex] ?? 0 : 0;
    if (flag === 2 || flag === 3)
      return null;
    const cp = codepoints[cellIndex] ?? 0;
    if (!cp)
      return null;
    let text2 = String.fromCodePoint(cp);
    const extra = graphemeLen && graphemeOffset && graphemeBuffer ? graphemeLen[cellIndex] ?? 0 : 0;
    if (extra > 0 && graphemeOffset && graphemeBuffer) {
      const start = graphemeOffset[cellIndex] ?? 0;
      const cps = [cp];
      for (let j = 0;j < extra; j += 1) {
        const extraCp = graphemeBuffer[start + j];
        if (extraCp)
          cps.push(extraCp);
      }
      text2 = String.fromCodePoint(...cps);
    }
    return { cp, text: text2, span: flag === 1 ? 2 : 1 };
  };
  const { useLinearBlending, useLinearCorrection } = resolveBlendFlags2(alphaBlending, "webgl2");
  reportTermSize(cols, rows);
  const cursorPos = cursor ? resolveCursorPosition(cursor) : null;
  reportCursor(cursorPos);
  const isBlinking = (cursor?.blinking || 0) !== 0 || FORCE_CURSOR_BLINK;
  const blinkVisible = !isBlinking || Math.floor(performance.now() / CURSOR_BLINK_MS) % 2 === 0;
  const imeFocused = typeof document !== "undefined" && imeInput ? document.activeElement === imeInput : false;
  const cursorStyle = cursor ? resolveCursorStyle(cursor, {
    focused: isFocused || imeFocused,
    preedit: Boolean(imeState.preedit),
    blinkVisible
  }) : null;
  let cursorCell = null;
  if (cursorStyle !== null && cursorPos) {
    let col = cursorPos.col;
    const row = cursorPos.row;
    let wideCell = false;
    if (cursorPos.wideTail && col > 0) {
      col -= 1;
      wideCell = true;
    }
    cursorCell = { row, col, wide: wideCell };
  }
  let imeCursorPos = cursorPos;
  if (cursor?.visible === 0 && wasmExports?.restty_active_cursor_x && wasmExports?.restty_active_cursor_y && wasmHandle) {
    const activeCol = wasmExports.restty_active_cursor_x(wasmHandle);
    const activeRow = wasmExports.restty_active_cursor_y(wasmHandle);
    const inViewport = Number.isFinite(activeCol) && Number.isFinite(activeRow) && activeCol >= 0 && activeRow >= 0 && activeCol < cols && activeRow < rows;
    if (inViewport) {
      imeCursorPos = {
        col: activeCol,
        row: activeRow,
        wideTail: cursor.wideTail === 1
      };
    }
  }
  const cursorImeAnchor = resolveImeAnchor2(imeCursorPos, cols, rows);
  const cellW = gridState.cellW || canvas.width / cols;
  const cellH = gridState.cellH || canvas.height / rows;
  const fontSizePx = gridState.fontSizePx || Math.max(1, Math.round(cellH));
  const primaryEntry = fontState.fonts[0];
  const primaryScale = gridState.scale || fontState.font.scaleForSize(fontSizePx, fontState.sizeMode);
  const lineHeight = gridState.lineHeight || fontHeightUnits2(fontState.font) * primaryScale;
  const baselineOffset = gridState.baselineOffset || fontState.font.ascender * primaryScale;
  const yPad = gridState.yPad ?? (cellH - lineHeight) / 2;
  const post = fontState.font.post;
  const underlinePosition = post?.underlinePosition ?? Math.round(-fontState.font.upem * 0.08);
  const underlineThickness = post?.underlineThickness ?? Math.round(fontState.font.upem * 0.05);
  const underlineOffsetPx = -underlinePosition * primaryScale;
  const underlineThicknessPx = Math.max(1, Math.ceil(underlineThickness * primaryScale));
  if (cursorImeAnchor) {
    updateImePosition2(cursorImeAnchor, cellW, cellH);
  }
  const bgData = [];
  const selectionData = [];
  const underlineData = [];
  const cursorData = [];
  const fgRectData = [];
  const overlayData = [];
  const glyphDataByFont = new Map;
  const glyphQueueByFont = new Map;
  const overlayGlyphDataByFont = new Map;
  const overlayGlyphQueueByFont = new Map;
  const neededGlyphIdsByFont = new Map;
  const neededGlyphMetaByFont = new Map;
  const fgColorCache = new Map;
  const bgColorCache = new Map;
  const ulColorCache = new Map;
  const resolveFallbackMetric = (font, metric) => {
    if (!font)
      return 0;
    if (metric === "ic_width") {
      const glyphId = font.glyphIdForChar("水");
      if (!glyphId)
        return 0;
      const advance = font.advanceWidth(glyphId);
      if (!Number.isFinite(advance) || advance <= 0)
        return 0;
      const bounds = font.getGlyphBounds(glyphId);
      if (bounds && Number.isFinite(bounds.xMax - bounds.xMin) && bounds.xMax - bounds.xMin > advance) {
        return 0;
      }
      return advance;
    }
    if (metric === "ex_height") {
      const exHeight = font.os2?.sxHeight ?? 0;
      return Number.isFinite(exHeight) && exHeight > 0 ? exHeight : 0;
    }
    if (metric === "cap_height") {
      const capHeight = font.os2?.sCapHeight ?? 0;
      return Number.isFinite(capHeight) && capHeight > 0 ? capHeight : 0;
    }
    const lineHeightUnits = font.height;
    return Number.isFinite(lineHeightUnits) && lineHeightUnits > 0 ? lineHeightUnits : 0;
  };
  const fallbackScaleAdjustment = (primary, entry) => {
    if (!primary?.font || !entry?.font)
      return 1;
    const metricOrder = [
      "ic_width",
      "ex_height",
      "cap_height",
      "line_height"
    ];
    for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
      const metric = metricOrder[i3];
      const primaryMetric = resolveFallbackMetric(primary.font, metric);
      const fallbackMetric = resolveFallbackMetric(entry.font, metric);
      if (primaryMetric <= 0 || fallbackMetric <= 0)
        continue;
      const factor = primaryMetric / fallbackMetric;
      if (Number.isFinite(factor) && factor > 0)
        return factor;
    }
    return 1;
  };
  const fallbackMetricAnchorUnits = (primary, entry) => {
    if (!primary?.font || !entry?.font)
      return null;
    const metricOrder = [
      "ic_width",
      "ex_height",
      "cap_height",
      "line_height"
    ];
    for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
      const metric = metricOrder[i3];
      const primaryMetric = resolveFallbackMetric(primary.font, metric);
      const fallbackMetric = resolveFallbackMetric(entry.font, metric);
      if (primaryMetric > 0 && fallbackMetric > 0) {
        return { primary: primaryMetric, fallback: fallbackMetric };
      }
    }
    return null;
  };
  const baseScaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font)
      return primaryScale;
    if (idx === 0)
      return primaryScale;
    return entry.font.scaleForSize(fontSizePx, fontState.sizeMode) * fontScaleOverride2(entry, FONT_SCALE_OVERRIDES);
  });
  const scaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font)
      return primaryScale;
    if (idx === 0)
      return primaryScale;
    const baseScale = baseScaleByFont[idx] ?? primaryScale;
    const preserveNaturalSymbolScale = isSymbolFont2(entry) && !isAppleSymbolsFont2(entry);
    if (preserveNaturalSymbolScale || isColorEmojiFont2(entry))
      return baseScale;
    const metricAdjust = clamp2(fallbackScaleAdjustment(primaryEntry, entry), 1, 2);
    let adjustedScale = baseScale * metricAdjust;
    const maxSpan = fontMaxCellSpan2(entry);
    if (maxSpan > 1) {
      const advanceUnits = fontAdvanceUnits2(entry, shapeClusterWithFont);
      const widthPx = advanceUnits * adjustedScale;
      const widthAdjustRaw = widthPx > 0 ? cellW * maxSpan / widthPx : 1;
      const widthAdjust = clamp2(widthAdjustRaw, 0.5, 2);
      adjustedScale *= widthAdjust;
    }
    const adjustedHeightPx = fontHeightUnits2(entry.font) * adjustedScale;
    if (adjustedHeightPx > lineHeight && adjustedHeightPx > 0) {
      adjustedScale *= lineHeight / adjustedHeightPx;
    }
    return adjustedScale;
  });
  const bitmapScaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font || idx === 0)
      return 1;
    if (isSymbolFont2(entry) && !isAppleSymbolsFont2(entry))
      return 1;
    const baseScale = baseScaleByFont[idx] ?? 0;
    if (baseScale <= 0)
      return 1;
    const targetScale = scaleByFont[idx] ?? baseScale;
    return clamp2(targetScale / baseScale, 0.5, 2);
  });
  const baselineAdjustByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font || idx === 0 || !primaryEntry?.font)
      return 0;
    const scale = scaleByFont[idx] ?? primaryScale;
    const preserveNaturalSymbolScale = isSymbolFont2(entry) && !isAppleSymbolsFont2(entry);
    if (!preserveNaturalSymbolScale && !isColorEmojiFont2(entry)) {
      const metricAnchor = fallbackMetricAnchorUnits(primaryEntry, entry);
      if (metricAnchor) {
        return metricAnchor.primary * primaryScale - metricAnchor.fallback * scale;
      }
    }
    return primaryEntry.font.ascender * primaryScale - entry.font.ascender * scale;
  });
  const nerdMetrics = buildNerdMetrics2(cellW, cellH, lineHeight, primaryEntry?.font, primaryScale, nerdIconScale);
  const getGlyphQueue = (fontIndex) => {
    if (!glyphQueueByFont.has(fontIndex))
      glyphQueueByFont.set(fontIndex, []);
    return glyphQueueByFont.get(fontIndex);
  };
  const getOverlayGlyphQueue = (fontIndex) => {
    if (!overlayGlyphQueueByFont.has(fontIndex))
      overlayGlyphQueueByFont.set(fontIndex, []);
    return overlayGlyphQueueByFont.get(fontIndex);
  };
  const getGlyphSet = (fontIndex) => {
    if (!neededGlyphIdsByFont.has(fontIndex))
      neededGlyphIdsByFont.set(fontIndex, new Set);
    return neededGlyphIdsByFont.get(fontIndex);
  };
  const getGlyphMeta = (fontIndex) => {
    if (!neededGlyphMetaByFont.has(fontIndex))
      neededGlyphMetaByFont.set(fontIndex, new Map);
    return neededGlyphMetaByFont.get(fontIndex);
  };
  const noteGlyphMeta = (fontIndex, glyphId, cp, constraintWidth) => {
    if (!glyphId || !cp)
      return;
    const meta = getGlyphMeta(fontIndex);
    const prev = meta.get(glyphId);
    if (!prev) {
      const width = Math.max(1, constraintWidth || 1);
      meta.set(glyphId, {
        cp,
        constraintWidth: width,
        widths: new Set([width]),
        variable: false
      });
      return;
    }
    if (prev.constraintWidth !== constraintWidth) {
      prev.widths?.add(Math.max(1, constraintWidth || 1));
      meta.set(glyphId, {
        ...prev,
        constraintWidth: Math.min(prev.constraintWidth, Math.max(1, constraintWidth || 1)),
        variable: true
      });
    }
  };
  const getGlyphData = (map, fontIndex) => {
    if (!map.has(fontIndex))
      map.set(fontIndex, []);
    return map.get(fontIndex);
  };
  return {
    deps,
    state: state2,
    rows,
    cols,
    codepoints,
    contentTags,
    wide,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    graphemeOffset,
    graphemeLen,
    graphemeBuffer,
    cursor,
    mergedEmojiSkip,
    readCellCluster,
    useLinearBlending,
    useLinearCorrection,
    blinkVisible,
    cursorPos,
    cursorStyle,
    cursorCell,
    cursorImeAnchor,
    cellW,
    cellH,
    fontSizePx,
    primaryEntry,
    primaryScale,
    lineHeight,
    baselineOffset,
    yPad,
    underlineOffsetPx,
    underlineThicknessPx,
    bgData,
    selectionData,
    underlineData,
    cursorData,
    fgRectData,
    overlayData,
    glyphDataByFont,
    glyphQueueByFont,
    overlayGlyphDataByFont,
    overlayGlyphQueueByFont,
    neededGlyphIdsByFont,
    neededGlyphMetaByFont,
    fgColorCache,
    bgColorCache,
    ulColorCache,
    scaleByFont,
    bitmapScaleByFont,
    baselineAdjustByFont,
    nerdMetrics,
    getGlyphQueue,
    getOverlayGlyphQueue,
    getGlyphSet,
    noteGlyphMeta,
    getGlyphData,
    compiledWebGLStages,
    stageTargets,
    hasShaderStages
  };
}

// src/runtime/create-runtime/render-tick-webgl-glyph-pipeline.ts
function renderWebGLGlyphPipeline(ctx) {
  const {
    deps,
    state: state2,
    cellW,
    cellH,
    fontSizePx,
    primaryScale,
    baselineOffset,
    yPad,
    bgData,
    selectionData,
    underlineData,
    cursorData,
    fgRectData,
    overlayData,
    glyphDataByFont,
    glyphQueueByFont,
    overlayGlyphDataByFont,
    overlayGlyphQueueByFont,
    neededGlyphIdsByFont,
    neededGlyphMetaByFont,
    scaleByFont,
    bitmapScaleByFont,
    baselineAdjustByFont,
    nerdMetrics,
    getGlyphData,
    useLinearBlending,
    useLinearCorrection,
    compiledWebGLStages,
    stageTargets,
    hasShaderStages
  } = ctx;
  const {
    fontState,
    defaultBg,
    buildFontAtlasIfNeeded: buildFontAtlasIfNeeded2,
    FONT_SCALE_OVERRIDES,
    isSymbolFont: isSymbolFont2,
    fontScaleOverride: fontScaleOverride2,
    getFontHinting,
    getFontHintTarget,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA: padAtlasRGBA2,
    buildAtlas,
    buildGlyphAtlasWithConstraints: buildGlyphAtlasWithConstraints2,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    nerdConstraintSignature: nerdConstraintSignature2,
    ATLAS_PADDING,
    SYMBOL_ATLAS_PADDING,
    SYMBOL_ATLAS_MAX_SIZE,
    PixelMode,
    isAppleSymbolsFont: isAppleSymbolsFont2,
    resolveSymbolConstraint: resolveSymbolConstraint2,
    DEFAULT_APPLE_SYMBOLS_CONSTRAINT: DEFAULT_APPLE_SYMBOLS_CONSTRAINT2,
    DEFAULT_SYMBOL_CONSTRAINT: DEFAULT_SYMBOL_CONSTRAINT2,
    DEFAULT_EMOJI_CONSTRAINT: DEFAULT_EMOJI_CONSTRAINT2,
    constrainGlyphBox: constrainGlyphBox2,
    tightenNerdConstraintBox: tightenNerdConstraintBox2,
    fontEntryHasItalicStyle: fontEntryHasItalicStyle2,
    fontEntryHasBoldStyle: fontEntryHasBoldStyle2,
    ITALIC_SLANT,
    BOLD_OFFSET,
    GLYPH_RENDER_MODE_COLOR,
    GLYPH_RENDER_MODE_MONO,
    ensureGLInstanceBuffer: ensureGLInstanceBuffer2,
    GLYPH_INSTANCE_FLOATS,
    clamp: clamp2,
    canvas,
    wasm,
    wasmHandle,
    collectKittyDrawPlan,
    resolveKittyWebGLTexture
  } = deps;
  const { gl } = state2;
  for (const [fontIndex, neededIds] of neededGlyphIdsByFont.entries()) {
    const fontEntry = fontState.fonts[fontIndex];
    if (!fontEntry?.font)
      continue;
    let atlasState = state2.glyphAtlases.get(fontIndex);
    const meta = neededGlyphMetaByFont.get(fontIndex);
    const bitmapScale = bitmapScaleByFont[fontIndex] ?? 1;
    const constraintContext = meta ? {
      cellW,
      cellH,
      yPad,
      baselineOffset,
      baselineAdjust: baselineAdjustByFont[fontIndex] ?? 0,
      fontScale: scaleByFont[fontIndex] ?? primaryScale,
      nerdMetrics,
      fontEntry
    } : null;
    const built = buildFontAtlasIfNeeded2({
      entry: fontEntry,
      neededGlyphIds: neededIds,
      glyphMeta: meta,
      fontSizePx,
      atlasScale: bitmapScale,
      fontIndex,
      constraintContext,
      deps: {
        fontScaleOverrides: FONT_SCALE_OVERRIDES,
        sizeMode: fontState.sizeMode,
        isSymbolFont: isSymbolFont2,
        fontScaleOverride: fontScaleOverride2,
        resolveGlyphPixelMode: resolveGlyphPixelMode2,
        atlasBitmapToRGBA: atlasBitmapToRGBA2,
        padAtlasRGBA: padAtlasRGBA2,
        buildAtlas,
        buildGlyphAtlasWithConstraints: buildGlyphAtlasWithConstraints2,
        buildColorEmojiAtlasWithCanvas,
        rasterizeGlyph,
        rasterizeGlyphWithTransform,
        hinting: getFontHinting(),
        hintTarget: getFontHintTarget(),
        nerdConstraintSignature: nerdConstraintSignature2,
        constants: {
          atlasPadding: ATLAS_PADDING,
          symbolAtlasPadding: SYMBOL_ATLAS_PADDING,
          symbolAtlasMaxSize: SYMBOL_ATLAS_MAX_SIZE,
          defaultAtlasMaxSize: 2048,
          pixelModeRgbaValue: PixelMode.RGBA ?? 4
        },
        resolvePreferNearest: ({ fontIndex: idx, isSymbol }) => idx === 0 || isSymbol
      }
    });
    if (!built.rebuilt || !built.atlas || !built.rgba)
      continue;
    const atlas = built.atlas;
    const colorGlyphs = built.colorGlyphs;
    const rgba = built.rgba;
    const preferNearest = built.preferNearest;
    if (atlasState) {
      gl.deleteTexture(atlasState.texture);
    }
    const texture = gl.createTexture();
    if (!texture)
      continue;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, atlas.bitmap.width, atlas.bitmap.rows, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(rgba));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, preferNearest ? gl.NEAREST : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, preferNearest ? gl.NEAREST : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    atlasState = {
      texture,
      width: atlas.bitmap.width,
      height: atlas.bitmap.rows,
      inset: atlas.inset,
      colorGlyphs,
      nearest: preferNearest
    };
    state2.glyphAtlases.set(fontIndex, atlasState);
  }
  const emitGlyphs = (queueByFont, targetMap) => {
    for (const [fontIndex, queue] of queueByFont.entries()) {
      const entry = fontState.fonts[fontIndex];
      const atlasState = state2.glyphAtlases?.get(fontIndex);
      if (!entry || !entry.atlas || !atlasState)
        continue;
      const atlas = entry.atlas;
      const atlasW = atlas.bitmap.width;
      const atlasH = atlas.bitmap.rows;
      const baseInset = Number.isFinite(atlas.inset) ? atlas.inset : 0;
      const uvInset = baseInset + (atlasState.nearest ? 0.5 : 0);
      const colorGlyphs = atlasState.colorGlyphs ?? atlas.colorGlyphs;
      const glyphData = getGlyphData(targetMap, fontIndex);
      for (const item of queue) {
        const bg = item.bg ?? defaultBg;
        let penX = 0;
        const scale = item.scale ?? primaryScale;
        const maxWidth = item.cellWidth ?? cellW;
        const maxHeight = cellH;
        const symbolLike = item.symbolLike;
        const symbolConstraint = item.symbolConstraint;
        let itemScale = scale;
        if (!symbolConstraint) {
          if (item.forceFit && item.glyphWidthPx && maxWidth > 0) {
            const fit = maxWidth / item.glyphWidthPx;
            if (fit > 0 && fit < 1)
              itemScale = scale * fit;
          }
          if (!symbolLike && fontIndex === 0) {
            const advancePx = item.shaped.advance * scale;
            if (advancePx > maxWidth && advancePx > 0) {
              itemScale = scale * (maxWidth / advancePx);
            }
          }
        }
        const scaleFactor = scale > 0 ? itemScale / scale : 1;
        const widthKey = item.constraintWidth ?? 0;
        const widthMap = atlas.glyphsByWidth?.get(widthKey);
        for (const glyph of item.shaped.glyphs) {
          const colorGlyph = !!colorGlyphs?.has(glyph.glyphId);
          const metrics = widthMap?.get(glyph.glyphId) ?? atlas.glyphs.get(glyph.glyphId);
          if (!metrics)
            continue;
          const applySymbolLayout = symbolLike && (fontIndex > 0 || symbolConstraint || colorGlyph);
          let bitmapScale = scaleFactor;
          const glyphConstrained = symbolLike && !!widthMap?.has(glyph.glyphId);
          if (glyphConstrained)
            bitmapScale = 1;
          if (fontIndex > 0 && !symbolLike) {
            const heightScale = maxHeight > 0 ? maxHeight / metrics.height : 1;
            const clampScale = Math.min(1, heightScale);
            bitmapScale *= clampScale;
          }
          const baselineAdjust = baselineAdjustByFont[fontIndex] ?? 0;
          const glyphBaselineAdjust = symbolLike && !symbolConstraint ? 0 : baselineAdjust;
          const rowY = item.baseY - yPad - baselineOffset;
          let gw = metrics.width * bitmapScale;
          let gh = metrics.height * bitmapScale;
          if (applySymbolLayout && !glyphConstrained) {
            const scaleToFit = gw > 0 && gh > 0 ? Math.min(maxWidth / gw, maxHeight / gh) : 1;
            if (scaleToFit < 1) {
              bitmapScale *= scaleToFit;
              gw *= scaleToFit;
              gh *= scaleToFit;
            }
            gw = Math.round(gw);
            gh = Math.round(gh);
          }
          let x3 = item.x + item.xPad + (penX + glyph.xOffset) * itemScale + metrics.bearingX * bitmapScale;
          if (fontIndex > 0 && item.shaped.glyphs.length === 1 && !symbolLike && maxWidth <= cellW * 1.05) {
            const center = item.x + (maxWidth - gw) * 0.5;
            x3 = center;
          }
          const minX = item.x;
          const maxX = item.x + maxWidth;
          if (x3 < minX)
            x3 = minX;
          if (x3 + gw > maxX)
            x3 = Math.max(minX, maxX - gw);
          let y = item.baseY + glyphBaselineAdjust - metrics.bearingY * bitmapScale - glyph.yOffset * itemScale;
          if (applySymbolLayout && !glyphConstrained && item.cp) {
            const nerdConstraint = resolveSymbolConstraint2(item.cp);
            const defaultConstraint = isAppleSymbolsFont2(entry) ? DEFAULT_APPLE_SYMBOLS_CONSTRAINT2 : DEFAULT_SYMBOL_CONSTRAINT2;
            const constraint = nerdConstraint ?? (colorGlyph ? DEFAULT_EMOJI_CONSTRAINT2 : defaultConstraint);
            const constraintWidth = Math.max(1, item.constraintWidth ?? Math.round(maxWidth / cellW));
            const adjusted = constrainGlyphBox2({
              x: x3 - item.x,
              y: y - rowY,
              width: gw,
              height: gh
            }, constraint, nerdMetrics, constraintWidth);
            const tightened = nerdConstraint ? tightenNerdConstraintBox2(adjusted, nerdConstraint) : adjusted;
            x3 = item.x + tightened.x;
            y = rowY + tightened.y;
            gw = tightened.width;
            gh = tightened.height;
          }
          if (gw < 1)
            gw = 1;
          if (gh < 1)
            gh = 1;
          const px = Math.round(x3);
          const py = Math.round(y);
          const insetX = Math.min(uvInset, (metrics.width - 1) * 0.5);
          const insetY = Math.min(uvInset, (metrics.height - 1) * 0.5);
          const u02 = (metrics.atlasX + insetX) / atlasW;
          const v02 = (metrics.atlasY + insetY) / atlasH;
          const u12 = (metrics.atlasX + metrics.width - insetX) / atlasW;
          const v12 = (metrics.atlasY + metrics.height - insetY) / atlasH;
          const italic = !!item.italic;
          const bold = !!item.bold;
          const syntheticItalic = italic && !fontEntryHasItalicStyle2(entry);
          const syntheticBold = bold && !fontEntryHasBoldStyle2(entry);
          const slant = syntheticItalic && !colorGlyph ? gh * ITALIC_SLANT : 0;
          const boldOffset = syntheticBold && !colorGlyph ? Math.max(1, Math.round(gw * BOLD_OFFSET)) : 0;
          const renderMode = colorGlyph ? GLYPH_RENDER_MODE_COLOR : GLYPH_RENDER_MODE_MONO;
          const pushGlyph = (xPos) => {
            glyphData.push(xPos, py, gw, gh, u02, v02, u12, v12, item.fg[0], item.fg[1], item.fg[2], item.fg[3], bg[0], bg[1], bg[2], bg[3], slant, renderMode);
          };
          pushGlyph(px);
          if (boldOffset > 0) {
            const minGlyphX = Math.round(item.x);
            const maxGlyphX = Math.round(item.x + maxWidth - gw);
            let bx = clamp2(px + boldOffset, minGlyphX, maxGlyphX);
            if (bx === px)
              bx = clamp2(px - boldOffset, minGlyphX, maxGlyphX);
            if (bx === px)
              pushGlyph(px);
            else
              pushGlyph(bx);
          }
          penX += glyph.xAdvance;
        }
      }
    }
  };
  emitGlyphs(glyphQueueByFont, glyphDataByFont);
  emitGlyphs(overlayGlyphQueueByFont, overlayGlyphDataByFont);
  const kittyPlacements = wasm && wasmHandle ? wasm.getKittyPlacements(wasmHandle) : [];
  const kittyPlan = collectKittyDrawPlan(kittyPlacements, cellW, cellH);
  const drawRects = (data) => {
    if (!data.length)
      return;
    const rectArray = new Float32Array(data);
    ensureGLInstanceBuffer2(state2, "rect", rectArray.byteLength);
    gl.bindVertexArray(state2.rectVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, state2.rectInstanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, rectArray);
    gl.useProgram(state2.rectProgram);
    gl.uniform2f(state2.rectResolutionLoc, canvas.width, canvas.height);
    gl.uniform2f(state2.rectBlendLoc, useLinearBlending ? 1 : 0, useLinearCorrection ? 1 : 0);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, data.length / 8);
    gl.bindVertexArray(null);
  };
  const drawGlyphs = (fontIndex, data) => {
    if (!data.length)
      return;
    const atlasState = state2.glyphAtlases.get(fontIndex);
    if (!atlasState)
      return;
    const glyphArray = new Float32Array(data);
    ensureGLInstanceBuffer2(state2, "glyph", glyphArray.byteLength);
    gl.bindVertexArray(state2.glyphVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, state2.glyphInstanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, glyphArray);
    gl.useProgram(state2.glyphProgram);
    gl.uniform2f(state2.glyphResolutionLoc, canvas.width, canvas.height);
    gl.uniform2f(state2.glyphBlendLoc, useLinearBlending ? 1 : 0, useLinearCorrection ? 1 : 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, atlasState.texture);
    gl.uniform1i(state2.glyphAtlasLoc, 0);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, data.length / GLYPH_INSTANCE_FLOATS);
    gl.bindVertexArray(null);
  };
  const drawKittySlices = (slices) => {
    if (!slices.length)
      return;
    const flush = (texture, data) => {
      if (!data.length)
        return;
      const glyphArray = new Float32Array(data);
      ensureGLInstanceBuffer2(state2, "glyph", glyphArray.byteLength);
      gl.bindVertexArray(state2.glyphVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, state2.glyphInstanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, glyphArray);
      gl.useProgram(state2.glyphProgram);
      gl.uniform2f(state2.glyphResolutionLoc, canvas.width, canvas.height);
      gl.uniform2f(state2.glyphBlendLoc, useLinearBlending ? 1 : 0, useLinearCorrection ? 1 : 0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(state2.glyphAtlasLoc, 0);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, data.length / GLYPH_INSTANCE_FLOATS);
      gl.bindVertexArray(null);
    };
    let currentTexture = null;
    let currentData = [];
    for (const slice of slices) {
      const texture = resolveKittyWebGLTexture(gl, slice);
      if (!texture)
        continue;
      if (currentTexture && texture !== currentTexture) {
        flush(currentTexture, currentData);
        currentData = [];
      }
      currentTexture = texture;
      const invW = slice.imageWidth > 0 ? 1 / slice.imageWidth : 0;
      const invH = slice.imageHeight > 0 ? 1 / slice.imageHeight : 0;
      const u02 = slice.sx * invW;
      const v02 = slice.sy * invH;
      const u12 = (slice.sx + slice.sw) * invW;
      const v12 = (slice.sy + slice.sh) * invH;
      currentData.push(slice.dx, slice.dy, slice.dw, slice.dh, u02, v02, u12, v12, 1, 1, 1, 1, 0, 0, 0, 0, 0, GLYPH_RENDER_MODE_COLOR);
    }
    if (currentTexture) {
      flush(currentTexture, currentData);
    }
  };
  drawRects(bgData);
  drawRects(selectionData);
  drawKittySlices(kittyPlan.underlay);
  drawRects(underlineData);
  drawRects(fgRectData);
  for (const [fontIndex, glyphData] of glyphDataByFont.entries()) {
    drawGlyphs(fontIndex, glyphData);
  }
  drawKittySlices(kittyPlan.overlay);
  drawRects(cursorData);
  drawRects(overlayData);
  for (const [fontIndex, glyphData] of overlayGlyphDataByFont.entries()) {
    drawGlyphs(fontIndex, glyphData);
  }
  if (hasShaderStages && stageTargets) {
    gl.disable(gl.BLEND);
    gl.bindVertexArray(stageTargets.quadVao);
    const nowSec = performance.now() * 0.001;
    let sourceTex = stageTargets.sceneTexture;
    for (let i3 = 0;i3 < compiledWebGLStages.length; i3 += 1) {
      const stage = compiledWebGLStages[i3];
      const isLast = i3 === compiledWebGLStages.length - 1;
      const nextIsPing = sourceTex === stageTargets.sceneTexture || sourceTex === stageTargets.pongTexture;
      const targetFramebuffer = isLast ? null : nextIsPing ? stageTargets.pingFramebuffer : stageTargets.pongFramebuffer;
      const nextTexture = nextIsPing ? stageTargets.pingTexture : stageTargets.pongTexture;
      gl.bindFramebuffer(gl.FRAMEBUFFER, targetFramebuffer);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(stage.program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, sourceTex);
      gl.uniform1i(stage.sourceLoc, 0);
      gl.uniform2f(stage.resolutionLoc, canvas.width, canvas.height);
      gl.uniform1f(stage.timeLoc, nowSec);
      gl.uniform4f(stage.params0Loc, stage.params[0] ?? 0, stage.params[1] ?? 0, stage.params[2] ?? 0, stage.params[3] ?? 0);
      gl.uniform4f(stage.params1Loc, stage.params[4] ?? 0, stage.params[5] ?? 0, stage.params[6] ?? 0, stage.params[7] ?? 0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      sourceTex = nextTexture;
    }
    gl.bindVertexArray(null);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  } else {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}

// src/runtime/create-runtime/render-tick-webgl-overlays.ts
function populateWebGLOverlays(ctx) {
  const {
    deps,
    rows,
    cols,
    cursor,
    cursorPos,
    cursorStyle,
    cursorCell,
    cursorImeAnchor,
    cellW,
    cellH,
    primaryScale,
    lineHeight,
    baselineOffset,
    yPad,
    underlineOffsetPx,
    underlineThicknessPx,
    bgData,
    underlineData,
    cursorData,
    fgRectData,
    overlayData,
    scaleByFont,
    getGlyphQueue,
    getOverlayGlyphQueue,
    getGlyphSet
  } = ctx;
  const {
    fontState,
    pickFontIndexForText: pickFontIndexForText2,
    fitTextTailToWidth: fitTextTailToWidth2,
    shapeClusterWithFont,
    noteColorGlyphText,
    imeState,
    PREEDIT_BG: PREEDIT_BG2,
    PREEDIT_UL: PREEDIT_UL2,
    PREEDIT_ACTIVE_BG: PREEDIT_ACTIVE_BG2,
    PREEDIT_CARET: PREEDIT_CARET2,
    PREEDIT_FG: PREEDIT_FG2,
    resizeState,
    RESIZE_OVERLAY_HOLD_MS,
    RESIZE_OVERLAY_FADE_MS,
    canvas,
    pushRect: pushRect2,
    pushRectBox: pushRectBox2,
    decodePackedRGBA: decodePackedRGBA2,
    cursorFallback,
    clamp: clamp2,
    wasmExports,
    wasmHandle,
    scrollbarState,
    syncScrollbar
  } = deps;
  if (cursor && imeState.preedit) {
    const preeditText = imeState.preedit;
    const preeditFontIndex = pickFontIndexForText2(preeditText, 1);
    const preeditEntry = fontState.fonts[preeditFontIndex] ?? fontState.fonts[0];
    const preeditScale = scaleByFont[preeditFontIndex] ?? primaryScale;
    const preeditRow = cursorImeAnchor?.row ?? cursorCell?.row ?? cursor.row;
    const preeditCol = cursorImeAnchor?.col ?? cursorCell?.col ?? cursor.col;
    const maxPreeditWidthPx = Math.max(cellW, (cols - preeditCol) * cellW);
    const fittedPreedit = fitTextTailToWidth2(preeditText, maxPreeditWidthPx, (value) => {
      if (!value)
        return 0;
      return shapeClusterWithFont(preeditEntry, value).advance * preeditScale;
    });
    const visiblePreeditText = fittedPreedit.text;
    if (!visiblePreeditText) {
    } else {
      const shaped = shapeClusterWithFont(preeditEntry, visiblePreeditText);
      noteColorGlyphText(preeditEntry, visiblePreeditText, shaped);
      const glyphSet = getGlyphSet(preeditFontIndex);
      for (const glyph of shaped.glyphs)
        glyphSet.add(glyph.glyphId);
      const baseY = preeditRow * cellH + yPad + baselineOffset;
      const x3 = preeditCol * cellW;
      const advancePx = shaped.advance * preeditScale;
      const widthPx = Math.max(cellW, Math.min(maxPreeditWidthPx, Math.max(fittedPreedit.widthPx, advancePx)));
      const rowY = preeditRow * cellH;
      pushRect2(bgData, x3, rowY, widthPx, cellH, PREEDIT_BG2);
      const thickness = underlineThicknessPx;
      const underlineBaseY = clamp2(baseY + underlineOffsetPx, rowY + 1, rowY + cellH - thickness - 1);
      pushRect2(underlineData, x3, underlineBaseY, widthPx, thickness, PREEDIT_UL2);
      const selectionOffset = fittedPreedit.offset;
      const rawSelStart = imeState.selectionStart || 0;
      const rawSelEnd = imeState.selectionEnd || 0;
      const selStart = Math.max(0, Math.min(visiblePreeditText.length, rawSelStart - selectionOffset));
      const selEnd = Math.max(selStart, Math.min(visiblePreeditText.length, rawSelEnd - selectionOffset));
      if (selEnd > selStart) {
        const leftWidth = shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(0, selStart)).advance * preeditScale;
        const selWidth = shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(selStart, selEnd)).advance * preeditScale;
        pushRect2(bgData, x3 + leftWidth, rowY, selWidth, cellH, PREEDIT_ACTIVE_BG2);
        pushRect2(underlineData, x3 + leftWidth, underlineBaseY, selWidth, thickness, PREEDIT_UL2);
      } else {
        const caretWidth = Math.max(1, Math.floor(cellW * 0.1));
        const caretX = x3 + shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(0, selStart)).advance * preeditScale;
        pushRect2(cursorData, caretX, rowY + 2, caretWidth, cellH - 4, PREEDIT_CARET2);
      }
      getGlyphQueue(preeditFontIndex).push({
        x: x3,
        baseY,
        xPad: 0,
        fg: PREEDIT_FG2,
        bg: PREEDIT_BG2,
        shaped,
        fontIndex: preeditFontIndex,
        scale: preeditScale,
        cellWidth: widthPx,
        symbolLike: false
      });
    }
  }
  const resizeAge = performance.now() - resizeState.lastAt;
  if (resizeState.cols > 0 && resizeState.rows > 0 && resizeAge >= 0 && resizeAge < RESIZE_OVERLAY_HOLD_MS + RESIZE_OVERLAY_FADE_MS) {
    const fade2 = resizeAge <= RESIZE_OVERLAY_HOLD_MS ? 1 : 1 - (resizeAge - RESIZE_OVERLAY_HOLD_MS) / RESIZE_OVERLAY_FADE_MS;
    const alpha = clamp2(fade2, 0, 1);
    if (alpha > 0.01) {
      const overlayText = `${resizeState.cols}x${resizeState.rows}`;
      const overlayEntry = fontState.fonts[0];
      if (overlayEntry?.font) {
        const shaped = shapeClusterWithFont(overlayEntry, overlayText);
        const glyphSet = getGlyphSet(0);
        for (const glyph of shaped.glyphs)
          glyphSet.add(glyph.glyphId);
        const textWidth = shaped.advance * primaryScale;
        const padX = Math.max(8, cellW * 0.6);
        const padY = Math.max(6, cellH * 0.4);
        const boxW = textWidth + padX * 2;
        const boxH = lineHeight + padY * 2;
        const boxX = (canvas.width - boxW) * 0.5;
        const boxY = (canvas.height - boxH) * 0.5;
        const overlayBg = [0, 0, 0, 0.6 * alpha];
        pushRectBox2(overlayData, boxX, boxY, boxW, boxH, overlayBg);
        pushRectBox2(overlayData, boxX, boxY, boxW, 1, [1, 1, 1, 0.12 * alpha]);
        const textRowY = boxY + (boxH - lineHeight) * 0.5;
        const baseY = textRowY + yPad + baselineOffset;
        getOverlayGlyphQueue(0).push({
          x: boxX + padX,
          baseY,
          xPad: 0,
          fg: [1, 1, 1, alpha],
          bg: overlayBg,
          shaped,
          fontIndex: 0,
          scale: primaryScale,
          cellWidth: textWidth,
          symbolLike: false
        });
      }
    }
  }
  if (cursorStyle !== null && cursorPos) {
    let cursorCol = cursorPos.col;
    let cursorRow = cursorPos.row;
    let cursorWidth = cellW;
    if (cursorPos.wideTail && cursorCol > 0) {
      cursorCol -= 1;
      cursorWidth = cellW * 2;
    }
    if (cursorRow < rows && cursorCol < cols) {
      const x3 = cursorCol * cellW;
      const y = cursorRow * cellH;
      const cursorColor = cursor?.color ? decodePackedRGBA2(cursor.color) : cursorFallback;
      const cursorThicknessPx = underlineThicknessPx;
      if (cursorStyle === 0) {
        pushRect2(fgRectData, x3, y, cursorWidth, cellH, cursorColor);
      } else if (cursorStyle === 1) {
        const offset = Math.floor((cursorThicknessPx + 1) / 2);
        pushRect2(cursorData, x3 - offset, y, cursorThicknessPx, cellH, cursorColor);
      } else if (cursorStyle === 2) {
        const baseY = cursorRow * cellH + yPad + baselineOffset;
        const underlineY = clamp2(baseY + underlineOffsetPx, y + 1, y + cellH - cursorThicknessPx - 1);
        pushRect2(cursorData, x3, underlineY, cursorWidth, cursorThicknessPx, cursorColor);
      } else if (cursorStyle === 3) {
        pushRect2(cursorData, x3, y, cursorWidth, cursorThicknessPx, cursorColor);
        pushRect2(cursorData, x3, y + cellH - cursorThicknessPx, cursorWidth, cursorThicknessPx, cursorColor);
        pushRect2(cursorData, x3, y, cursorThicknessPx, cellH, cursorColor);
        pushRect2(cursorData, x3 + cursorWidth - cursorThicknessPx, y, cursorThicknessPx, cellH, cursorColor);
      } else {
        pushRect2(cursorData, x3, y, cursorWidth, cellH, cursorColor);
      }
    }
  }
  if (wasmExports && wasmHandle && wasmExports.restty_scrollbar_total) {
    const total = wasmExports.restty_scrollbar_total(wasmHandle) || 0;
    const offset = wasmExports.restty_scrollbar_offset ? wasmExports.restty_scrollbar_offset(wasmHandle) : 0;
    const len = wasmExports.restty_scrollbar_len ? wasmExports.restty_scrollbar_len(wasmHandle) : rows;
    if (total !== scrollbarState.lastTotal || offset !== scrollbarState.lastOffset || len !== scrollbarState.lastLen) {
      scrollbarState.lastTotal = total;
      scrollbarState.lastOffset = offset;
      scrollbarState.lastLen = len;
    }
    syncScrollbar(total, offset, len);
  }
}

// src/runtime/create-runtime/search-highlight-utils.ts
function searchHighlightForColumn(matches, startIndex, endIndex, col, cols) {
  let nextIndex = startIndex;
  while (nextIndex < endIndex) {
    const match = matches[nextIndex];
    const start = Math.max(0, Math.min(cols, match.startCol));
    const end = Math.max(start, Math.min(cols, match.endCol));
    if (end <= col) {
      nextIndex += 1;
      continue;
    }
    if (start > col)
      break;
    return {
      nextIndex,
      kind: match.selected ? 2 : 1
    };
  }
  return {
    nextIndex,
    kind: 0
  };
}

// src/runtime/create-runtime/render-tick-webgl-scene.ts
function populateWebGLSceneData(ctx) {
  const {
    deps,
    rows,
    cols,
    codepoints,
    contentTags,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    mergedEmojiSkip,
    readCellCluster,
    blinkVisible,
    cursorStyle,
    cursorCell,
    cellW,
    cellH,
    primaryScale,
    baselineOffset,
    yPad,
    underlineOffsetPx,
    underlineThicknessPx,
    bgData,
    selectionData,
    underlineData,
    fgRectData,
    fgColorCache,
    bgColorCache,
    ulColorCache,
    scaleByFont,
    getGlyphQueue,
    getGlyphSet,
    noteGlyphMeta
  } = ctx;
  const {
    fontState,
    defaultBg,
    selectionState,
    selectionForRow: selectionForRow2,
    getSearchViewportMatches,
    pushRect: pushRect2,
    selectionBackgroundColor,
    selectionForegroundColor,
    searchMatchBackgroundColor,
    searchCurrentMatchBackgroundColor,
    searchMatchTextColor,
    searchCurrentMatchTextColor,
    STYLE_BOLD,
    STYLE_ITALIC,
    STYLE_FAINT,
    STYLE_BLINK,
    STYLE_INVERSE,
    STYLE_INVISIBLE,
    STYLE_STRIKE,
    STYLE_OVERLINE,
    STYLE_UNDERLINE_MASK,
    decodeRGBAWithCache: decodeRGBAWithCache2,
    brighten: brighten2,
    BOLD_BRIGHTEN,
    fade: fade2,
    FAINT_ALPHA,
    linkState,
    drawUnderlineStyle: drawUnderlineStyle2,
    drawStrikethrough: drawStrikethrough2,
    drawOverline: drawOverline2,
    KITTY_PLACEHOLDER_CP,
    isSpaceCp: isSpaceCp2,
    shouldMergeTrailingClusterCodepoint: shouldMergeTrailingClusterCodepoint2,
    isBlockElement: isBlockElement2,
    drawBlockElement: drawBlockElement2,
    isBoxDrawing: isBoxDrawing2,
    drawBoxDrawing: drawBoxDrawing2,
    isBraille: isBraille2,
    drawBraille: drawBraille2,
    isPowerline: isPowerline2,
    drawPowerline: drawPowerline2,
    pickFontIndexForText: pickFontIndexForText2,
    stylePreferenceFromFlags: stylePreferenceFromFlags2,
    noteColorGlyphText,
    isRenderSymbolLike: isRenderSymbolLike2,
    resolveSymbolConstraint: resolveSymbolConstraint2,
    isGraphicsElement: isGraphicsElement2,
    glyphWidthUnits: glyphWidthUnits2,
    shapeClusterWithFont
  } = deps;
  const isRegionalIndicator = (value) => value >= 127462 && value <= 127487;
  const searchMatches = getSearchViewportMatches();
  let searchMatchIndex = 0;
  const cursorBlock = cursorStyle === 0 && !!cursorCell;
  for (let row = 0;row < rows; row += 1) {
    const rowY = row * cellH;
    const baseY = rowY + yPad + baselineOffset;
    const localSel = selectionState.active ? selectionForRow2(row, cols) : null;
    const selStart = localSel?.start ?? -1;
    const selEnd = localSel?.end ?? -1;
    const rowSearchStartIndex = searchMatchIndex;
    while (searchMatchIndex < searchMatches.length) {
      const match = searchMatches[searchMatchIndex];
      if (match.row < row) {
        searchMatchIndex += 1;
        continue;
      }
      if (match.row > row)
        break;
      searchMatchIndex += 1;
    }
    const rowSearchEndIndex = searchMatchIndex;
    let rowSearchCursor = rowSearchStartIndex;
    for (let col = 0;col < cols; col += 1) {
      const idx = row * cols + col;
      const x3 = col * cellW;
      const tag = contentTags ? contentTags[idx] : 0;
      const bgOnly = tag === 2 || tag === 3;
      const flags = styleFlags ? styleFlags[idx] : 0;
      const bold = (flags & STYLE_BOLD) !== 0;
      const italic = (flags & STYLE_ITALIC) !== 0;
      const faint = (flags & STYLE_FAINT) !== 0;
      const blink = (flags & STYLE_BLINK) !== 0;
      const inverse = (flags & STYLE_INVERSE) !== 0;
      const invisible = (flags & STYLE_INVISIBLE) !== 0;
      const strike = (flags & STYLE_STRIKE) !== 0;
      const overline = (flags & STYLE_OVERLINE) !== 0;
      const underlineStyle = ulStyle ? ulStyle[idx] : (flags & STYLE_UNDERLINE_MASK) >> 8;
      let fg = decodeRGBAWithCache2(fgBytes, idx, fgColorCache);
      let bg = bgBytes ? decodeRGBAWithCache2(bgBytes, idx, bgColorCache) : defaultBg;
      let ul = ulBytes ? decodeRGBAWithCache2(ulBytes, idx, ulColorCache) : fg;
      const underlineUsesFg = ul[0] === fg[0] && ul[1] === fg[1] && ul[2] === fg[2] && ul[3] === fg[3];
      if (inverse) {
        const tmp = fg;
        fg = bg;
        bg = tmp;
        if (underlineUsesFg)
          ul = fg;
      }
      if (bold) {
        fg = brighten2(fg, BOLD_BRIGHTEN);
        ul = brighten2(ul, BOLD_BRIGHTEN);
      }
      if (faint) {
        fg = fade2(fg, FAINT_ALPHA);
        ul = fade2(ul, FAINT_ALPHA);
      }
      const selectionActiveForCell = selStart >= 0 && col >= selStart && col < selEnd;
      let searchHighlightKind = 0;
      if (!selectionActiveForCell && rowSearchCursor < rowSearchEndIndex) {
        const searchHighlight = searchHighlightForColumn(searchMatches, rowSearchCursor, rowSearchEndIndex, col, cols);
        rowSearchCursor = searchHighlight.nextIndex;
        searchHighlightKind = searchHighlight.kind;
      }
      if (selectionActiveForCell) {
        const selectionBg = resolveHighlightBackgroundColor(selectionBackgroundColor, fg, bg, inverse);
        pushRect2(selectionData, x3, rowY, cellW, cellH, selectionBg);
        if (selectionForegroundColor) {
          const selectionFg = resolveHighlightForegroundColor(selectionForegroundColor, fg, bg, inverse);
          fg = selectionFg;
          ul = selectionFg;
        }
      } else if (searchHighlightKind === 1 || searchHighlightKind === 2) {
        const searchBg = resolveHighlightBackgroundColor(searchHighlightKind === 2 ? searchCurrentMatchBackgroundColor : searchMatchBackgroundColor, fg, bg, inverse);
        const searchFg = resolveHighlightForegroundColor(searchHighlightKind === 2 ? searchCurrentMatchTextColor : searchMatchTextColor, fg, bg, inverse);
        pushRect2(selectionData, x3, rowY, cellW, cellH, searchBg);
        fg = searchFg;
        ul = searchFg;
      }
      const bgForText = bg[3] < 1 ? [
        bg[0] + defaultBg[0] * (1 - bg[3]),
        bg[1] + defaultBg[1] * (1 - bg[3]),
        bg[2] + defaultBg[2] * (1 - bg[3]),
        1
      ] : bg;
      if ((bgBytes || inverse) && bg[3] > 0)
        pushRect2(bgData, x3, rowY, cellW, cellH, bg);
      const linkId = linkIds ? linkIds[idx] ?? 0 : 0;
      const linkHovered = linkId && linkId === linkState.hoverId;
      const blinkOff = blink && !blinkVisible;
      const textHidden = invisible || blinkOff;
      if (!textHidden && !bgOnly) {
        if (underlineStyle > 0 && ul[3] > 0) {
          drawUnderlineStyle2(underlineData, underlineStyle, x3, rowY, cellW, cellH, baseY, underlineOffsetPx, underlineThicknessPx, ul);
        }
        if (linkHovered && !selectionState.active && !selectionState.dragging) {
          drawUnderlineStyle2(underlineData, 1, x3, rowY, cellW, cellH, baseY, underlineOffsetPx, underlineThicknessPx, ul);
        }
        if (strike)
          drawStrikethrough2(underlineData, x3, rowY, cellW, cellH, fg);
        if (overline)
          drawOverline2(underlineData, x3, rowY, cellW, fg);
      }
      if (bgOnly || textHidden)
        continue;
      if (mergedEmojiSkip[idx])
        continue;
      const cluster = readCellCluster(idx);
      if (!cluster)
        continue;
      const cp = cluster.cp;
      if (cp === KITTY_PLACEHOLDER_CP)
        continue;
      let text2 = cluster.text;
      let baseSpan = cluster.span;
      const rowEnd = row * cols + cols;
      if (isRegionalIndicator(cp)) {
        const nextIdx = idx + baseSpan;
        if (nextIdx < rowEnd && !mergedEmojiSkip[nextIdx]) {
          const next = readCellCluster(nextIdx);
          if (next && isRegionalIndicator(next.cp)) {
            text2 += next.text;
            baseSpan += next.span;
            mergedEmojiSkip[nextIdx] = 1;
          }
        }
      }
      let nextSeqIdx = idx + baseSpan;
      let guard = 0;
      while (nextSeqIdx < rowEnd && guard < 12) {
        const next = readCellCluster(nextSeqIdx);
        if (!next || !next.cp || isSpaceCp2(next.cp))
          break;
        const shouldMerge = text2.endsWith("‍") || shouldMergeTrailingClusterCodepoint2(next.cp);
        if (!shouldMerge)
          break;
        text2 += next.text;
        baseSpan += next.span;
        mergedEmojiSkip[nextSeqIdx] = 1;
        nextSeqIdx += next.span;
        guard += 1;
      }
      const extra = text2.length > String.fromCodePoint(cp).length ? 1 : 0;
      if (extra === 0 && isSpaceCp2(cp))
        continue;
      if (cursorBlock && cursorCell && row === cursorCell.row && col >= cursorCell.col && col < cursorCell.col + (cursorCell.wide ? 2 : 1)) {
        fg = [bgForText[0], bgForText[1], bgForText[2], 1];
      }
      if (isBlockElement2(cp)) {
        if (drawBlockElement2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (isBoxDrawing2(cp)) {
        if (drawBoxDrawing2(cp, x3, rowY, cellW, cellH, fg, fgRectData, underlineThicknessPx))
          continue;
      }
      if (isBraille2(cp)) {
        if (drawBraille2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (isPowerline2(cp)) {
        if (drawPowerline2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (extra > 0 && text2.trim() === "")
        continue;
      const fontIndex = pickFontIndexForText2(text2, baseSpan, stylePreferenceFromFlags2(bold, italic));
      const fontEntry = fontState.fonts[fontIndex] ?? fontState.fonts[0];
      const shaped = shapeClusterWithFont(fontEntry, text2);
      if (!shaped.glyphs.length)
        continue;
      noteColorGlyphText(fontEntry, text2, shaped);
      const glyphSet = getGlyphSet(fontIndex);
      for (const glyph of shaped.glyphs)
        glyphSet.add(glyph.glyphId);
      const fontScale = scaleByFont[fontIndex] ?? primaryScale;
      let cellSpan = baseSpan;
      const nerdConstraint = resolveSymbolConstraint2(cp);
      const symbolLike = isRenderSymbolLike2(cp) || !!nerdConstraint;
      const symbolConstraint = !!nerdConstraint;
      let constraintWidth = baseSpan;
      let forceFit = false;
      let glyphWidthPx = 0;
      if (symbolLike) {
        if (baseSpan === 1) {
          constraintWidth = 1;
          if (col < cols - 1) {
            if (col > 0) {
              const prevCp = codepoints[idx - 1];
              if (!((isRenderSymbolLike2(prevCp) || !!resolveSymbolConstraint2(prevCp)) && !isGraphicsElement2(prevCp))) {
                const nextCp = codepoints[idx + 1];
                if (!nextCp || isSpaceCp2(nextCp))
                  constraintWidth = 2;
              }
            } else {
              const nextCp = codepoints[idx + 1];
              if (!nextCp || isSpaceCp2(nextCp))
                constraintWidth = 2;
            }
          }
          cellSpan = constraintWidth;
        }
        if (shaped.glyphs.length === 1) {
          const glyphId = shaped.glyphs[0].glyphId;
          const widthUnits = glyphWidthUnits2(fontEntry, glyphId);
          if (widthUnits > 0) {
            glyphWidthPx = widthUnits * fontScale;
          }
        }
        if (!glyphWidthPx) {
          glyphWidthPx = shaped.advance * fontScale;
        }
        if (glyphWidthPx > cellW * cellSpan * 1.05) {
          forceFit = true;
        }
      }
      if (symbolConstraint) {
        for (const glyph of shaped.glyphs) {
          noteGlyphMeta(fontIndex, glyph.glyphId, cp, constraintWidth);
        }
      }
      const cellWidthPx = cellW * cellSpan;
      const xPad = 0;
      getGlyphQueue(fontIndex).push({
        x: x3,
        baseY,
        xPad,
        fg,
        bg: bgForText,
        shaped,
        fontIndex,
        scale: fontScale,
        cellWidth: cellWidthPx,
        symbolLike,
        symbolConstraint,
        constraintWidth,
        forceFit,
        glyphWidthPx,
        cp,
        italic,
        bold
      });
    }
  }
}

// src/runtime/create-runtime/render-tick-webgl.ts
function tickWebGL(deps, state2) {
  const ctx = buildWebGLTickContext(deps, state2);
  if (!ctx)
    return;
  populateWebGLSceneData(ctx);
  populateWebGLOverlays(ctx);
  renderWebGLGlyphPipeline(ctx);
}

// src/runtime/create-runtime/render-tick-webgpu-cell-pass.ts
function collectWebGPUCellPass(params) {
  const {
    deps,
    render,
    cellW,
    cellH,
    fontSizePx,
    primaryScale,
    lineHeight,
    baselineOffset,
    yPad,
    underlineOffsetPx,
    underlineThicknessPx,
    cursorBlock,
    cursorCell,
    blinkVisible,
    defaultBg
  } = params;
  const {
    rows,
    cols,
    codepoints,
    contentTags,
    wide,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    graphemeOffset,
    graphemeLen,
    graphemeBuffer
  } = render;
  const {
    fontState,
    fontHeightUnits: fontHeightUnits2,
    fontScaleOverride: fontScaleOverride2,
    FONT_SCALE_OVERRIDES,
    isSymbolFont: isSymbolFont2,
    isAppleSymbolsFont: isAppleSymbolsFont2,
    isColorEmojiFont: isColorEmojiFont2,
    fontAdvanceUnits: fontAdvanceUnits2,
    shapeClusterWithFont,
    fontMaxCellSpan: fontMaxCellSpan2,
    clamp: clamp2,
    buildNerdMetrics: buildNerdMetrics2,
    nerdIconScale,
    selectionState,
    selectionForRow: selectionForRow2,
    getSearchViewportMatches,
    pushRect: pushRect2,
    selectionBackgroundColor,
    selectionForegroundColor,
    searchMatchBackgroundColor,
    searchCurrentMatchBackgroundColor,
    searchMatchTextColor,
    searchCurrentMatchTextColor,
    STYLE_BOLD,
    STYLE_ITALIC,
    STYLE_FAINT,
    STYLE_BLINK,
    STYLE_INVERSE,
    STYLE_INVISIBLE,
    STYLE_STRIKE,
    STYLE_OVERLINE,
    STYLE_UNDERLINE_MASK,
    decodeRGBAWithCache: decodeRGBAWithCache2,
    brighten: brighten2,
    BOLD_BRIGHTEN,
    fade: fade2,
    FAINT_ALPHA,
    linkState,
    drawUnderlineStyle: drawUnderlineStyle2,
    drawStrikethrough: drawStrikethrough2,
    drawOverline: drawOverline2,
    KITTY_PLACEHOLDER_CP,
    isSpaceCp: isSpaceCp2,
    shouldMergeTrailingClusterCodepoint: shouldMergeTrailingClusterCodepoint2,
    isBlockElement: isBlockElement2,
    drawBlockElement: drawBlockElement2,
    isBoxDrawing: isBoxDrawing2,
    drawBoxDrawing: drawBoxDrawing2,
    isBraille: isBraille2,
    drawBraille: drawBraille2,
    isPowerline: isPowerline2,
    drawPowerline: drawPowerline2,
    pickFontIndexForText: pickFontIndexForText2,
    stylePreferenceFromFlags: stylePreferenceFromFlags2,
    noteColorGlyphText,
    isRenderSymbolLike: isRenderSymbolLike2,
    resolveSymbolConstraint: resolveSymbolConstraint2,
    isGraphicsElement: isGraphicsElement2,
    glyphWidthUnits: glyphWidthUnits2
  } = deps;
  const bgData = [];
  const selectionData = [];
  const underlineData = [];
  const cursorData = [];
  const fgRectData = [];
  const overlayData = [];
  const glyphDataNearestByFont = new Map;
  const glyphDataLinearByFont = new Map;
  const glyphQueueByFont = new Map;
  const overlayGlyphDataNearestByFont = new Map;
  const overlayGlyphDataLinearByFont = new Map;
  const overlayGlyphQueueByFont = new Map;
  const neededGlyphIdsByFont = new Map;
  const neededGlyphMetaByFont = new Map;
  const fgColorCache = new Map;
  const bgColorCache = new Map;
  const ulColorCache = new Map;
  const primaryEntry = fontState.fonts[0];
  const resolveFallbackMetric = (font, metric) => {
    if (!font)
      return 0;
    if (metric === "ic_width") {
      const glyphId = font.glyphIdForChar("水");
      if (!glyphId)
        return 0;
      const advance = font.advanceWidth(glyphId);
      if (!Number.isFinite(advance) || advance <= 0)
        return 0;
      const bounds = font.getGlyphBounds(glyphId);
      if (bounds) {
        const width = bounds.xMax - bounds.xMin;
        if (Number.isFinite(width) && width > advance)
          return 0;
      }
      return advance;
    }
    if (metric === "ex_height") {
      const exHeight = font.os2?.sxHeight ?? 0;
      return Number.isFinite(exHeight) && exHeight > 0 ? exHeight : 0;
    }
    if (metric === "cap_height") {
      const capHeight = font.os2?.sCapHeight ?? 0;
      return Number.isFinite(capHeight) && capHeight > 0 ? capHeight : 0;
    }
    const lineHeightUnits = font.height;
    return Number.isFinite(lineHeightUnits) && lineHeightUnits > 0 ? lineHeightUnits : 0;
  };
  const fallbackScaleAdjustment = (primary, entry) => {
    if (!primary?.font || !entry?.font)
      return 1;
    const metricOrder = [
      "ic_width",
      "ex_height",
      "cap_height",
      "line_height"
    ];
    for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
      const metric = metricOrder[i3];
      const primaryMetric = resolveFallbackMetric(primary.font, metric);
      const fallbackMetric = resolveFallbackMetric(entry.font, metric);
      if (primaryMetric <= 0 || fallbackMetric <= 0)
        continue;
      const factor = primaryMetric / fallbackMetric;
      if (Number.isFinite(factor) && factor > 0)
        return factor;
    }
    return 1;
  };
  const fallbackMetricAnchorUnits = (primary, entry) => {
    if (!primary?.font || !entry?.font)
      return null;
    const metricOrder = [
      "ic_width",
      "ex_height",
      "cap_height",
      "line_height"
    ];
    for (let i3 = 0;i3 < metricOrder.length; i3 += 1) {
      const metric = metricOrder[i3];
      const primaryMetric = resolveFallbackMetric(primary.font, metric);
      const fallbackMetric = resolveFallbackMetric(entry.font, metric);
      if (primaryMetric > 0 && fallbackMetric > 0) {
        return { primary: primaryMetric, fallback: fallbackMetric };
      }
    }
    return null;
  };
  const baseScaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font)
      return primaryScale;
    if (idx === 0)
      return primaryScale;
    return entry.font.scaleForSize(fontSizePx, fontState.sizeMode) * fontScaleOverride2(entry, FONT_SCALE_OVERRIDES);
  });
  const scaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font)
      return primaryScale;
    if (idx === 0)
      return primaryScale;
    const baseScale = baseScaleByFont[idx] ?? primaryScale;
    const preserveNaturalSymbolScale = isSymbolFont2(entry) && !isAppleSymbolsFont2(entry);
    if (preserveNaturalSymbolScale || isColorEmojiFont2(entry))
      return baseScale;
    const metricAdjust = clamp2(fallbackScaleAdjustment(primaryEntry, entry), 1, 2);
    let adjustedScale = baseScale * metricAdjust;
    const maxSpan = fontMaxCellSpan2(entry);
    if (maxSpan > 1) {
      const advanceUnits = fontAdvanceUnits2(entry, shapeClusterWithFont);
      const widthPx = advanceUnits * adjustedScale;
      const widthAdjustRaw = widthPx > 0 ? cellW * maxSpan / widthPx : 1;
      const widthAdjust = clamp2(widthAdjustRaw, 0.5, 2);
      adjustedScale *= widthAdjust;
    }
    const adjustedHeightPx = fontHeightUnits2(entry.font) * adjustedScale;
    if (adjustedHeightPx > lineHeight && adjustedHeightPx > 0) {
      adjustedScale *= lineHeight / adjustedHeightPx;
    }
    return adjustedScale;
  });
  const bitmapScaleByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font || idx === 0)
      return 1;
    if (isSymbolFont2(entry) && !isAppleSymbolsFont2(entry))
      return 1;
    const baseScale = baseScaleByFont[idx] ?? 0;
    if (baseScale <= 0)
      return 1;
    const targetScale = scaleByFont[idx] ?? baseScale;
    return clamp2(targetScale / baseScale, 0.5, 2);
  });
  const baselineAdjustByFont = fontState.fonts.map((entry, idx) => {
    if (!entry?.font || idx === 0 || !primaryEntry?.font)
      return 0;
    const scale = scaleByFont[idx] ?? primaryScale;
    const preserveNaturalSymbolScale = isSymbolFont2(entry) && !isAppleSymbolsFont2(entry);
    if (!preserveNaturalSymbolScale && !isColorEmojiFont2(entry)) {
      const metricAnchor = fallbackMetricAnchorUnits(primaryEntry, entry);
      if (metricAnchor) {
        return metricAnchor.primary * primaryScale - metricAnchor.fallback * scale;
      }
    }
    return primaryEntry.font.ascender * primaryScale - entry.font.ascender * scale;
  });
  const nerdMetrics = buildNerdMetrics2(cellW, cellH, lineHeight, primaryEntry?.font, primaryScale, nerdIconScale);
  const getGlyphQueue = (fontIndex) => {
    if (!glyphQueueByFont.has(fontIndex))
      glyphQueueByFont.set(fontIndex, []);
    return glyphQueueByFont.get(fontIndex);
  };
  const getOverlayGlyphQueue = (fontIndex) => {
    if (!overlayGlyphQueueByFont.has(fontIndex))
      overlayGlyphQueueByFont.set(fontIndex, []);
    return overlayGlyphQueueByFont.get(fontIndex);
  };
  const getGlyphSet = (fontIndex) => {
    if (!neededGlyphIdsByFont.has(fontIndex))
      neededGlyphIdsByFont.set(fontIndex, new Set);
    return neededGlyphIdsByFont.get(fontIndex);
  };
  const getGlyphMeta = (fontIndex) => {
    if (!neededGlyphMetaByFont.has(fontIndex))
      neededGlyphMetaByFont.set(fontIndex, new Map);
    return neededGlyphMetaByFont.get(fontIndex);
  };
  const noteGlyphMeta = (fontIndex, glyphId, cp, constraintWidth) => {
    if (!glyphId || !cp)
      return;
    const meta = getGlyphMeta(fontIndex);
    const prev = meta.get(glyphId);
    if (!prev) {
      const width = Math.max(1, constraintWidth || 1);
      meta.set(glyphId, {
        cp,
        constraintWidth: width,
        widths: new Set([width]),
        variable: false
      });
      return;
    }
    if (prev.constraintWidth !== constraintWidth) {
      prev.widths?.add(Math.max(1, constraintWidth || 1));
      meta.set(glyphId, {
        ...prev,
        constraintWidth: Math.min(prev.constraintWidth, Math.max(1, constraintWidth || 1)),
        variable: true
      });
    }
  };
  const mergedEmojiSkip = new Uint8Array(codepoints.length);
  const searchMatches = getSearchViewportMatches();
  let searchMatchIndex = 0;
  const isRegionalIndicator = (value) => value >= 127462 && value <= 127487;
  const readCellCluster = (cellIndex) => {
    const flag = wide ? wide[cellIndex] ?? 0 : 0;
    if (flag === 2 || flag === 3)
      return null;
    const cp = codepoints[cellIndex] ?? 0;
    if (!cp)
      return null;
    let text2 = String.fromCodePoint(cp);
    const extra = graphemeLen && graphemeOffset && graphemeBuffer ? graphemeLen[cellIndex] ?? 0 : 0;
    if (extra > 0 && graphemeOffset && graphemeBuffer) {
      const start = graphemeOffset[cellIndex] ?? 0;
      const cps = [cp];
      for (let j = 0;j < extra; j += 1) {
        const extraCp = graphemeBuffer[start + j];
        if (extraCp)
          cps.push(extraCp);
      }
      text2 = String.fromCodePoint(...cps);
    }
    return { cp, text: text2, span: flag === 1 ? 2 : 1 };
  };
  for (let row = 0;row < rows; row += 1) {
    const rowY = row * cellH;
    const baseY = rowY + yPad + baselineOffset;
    const localSel = selectionState.active ? selectionForRow2(row, cols) : null;
    const selStart = localSel?.start ?? -1;
    const selEnd = localSel?.end ?? -1;
    const rowSearchStartIndex = searchMatchIndex;
    while (searchMatchIndex < searchMatches.length) {
      const match = searchMatches[searchMatchIndex];
      if (match.row < row) {
        searchMatchIndex += 1;
        continue;
      }
      if (match.row > row)
        break;
      searchMatchIndex += 1;
    }
    const rowSearchEndIndex = searchMatchIndex;
    let rowSearchCursor = rowSearchStartIndex;
    for (let col = 0;col < cols; col += 1) {
      const idx = row * cols + col;
      const x3 = col * cellW;
      const tag = contentTags ? contentTags[idx] : 0;
      const bgOnly = tag === 2 || tag === 3;
      const flags = styleFlags ? styleFlags[idx] : 0;
      const bold = (flags & STYLE_BOLD) !== 0;
      const italic = (flags & STYLE_ITALIC) !== 0;
      const faint = (flags & STYLE_FAINT) !== 0;
      const blink = (flags & STYLE_BLINK) !== 0;
      const inverse = (flags & STYLE_INVERSE) !== 0;
      const invisible = (flags & STYLE_INVISIBLE) !== 0;
      const strike = (flags & STYLE_STRIKE) !== 0;
      const overline = (flags & STYLE_OVERLINE) !== 0;
      const underlineStyle = ulStyle ? ulStyle[idx] : (flags & STYLE_UNDERLINE_MASK) >> 8;
      let fg = decodeRGBAWithCache2(fgBytes, idx, fgColorCache);
      let bg = bgBytes ? decodeRGBAWithCache2(bgBytes, idx, bgColorCache) : defaultBg;
      let ul = ulBytes ? decodeRGBAWithCache2(ulBytes, idx, ulColorCache) : fg;
      const underlineUsesFg = ul[0] === fg[0] && ul[1] === fg[1] && ul[2] === fg[2] && ul[3] === fg[3];
      if (inverse) {
        const tmp = fg;
        fg = bg;
        bg = tmp;
        if (underlineUsesFg)
          ul = fg;
      }
      if (bold) {
        fg = brighten2(fg, BOLD_BRIGHTEN);
        ul = brighten2(ul, BOLD_BRIGHTEN);
      }
      if (faint) {
        fg = fade2(fg, FAINT_ALPHA);
        ul = fade2(ul, FAINT_ALPHA);
      }
      const selectionActiveForCell = selStart >= 0 && col >= selStart && col < selEnd;
      let searchHighlightKind = 0;
      if (!selectionActiveForCell && rowSearchCursor < rowSearchEndIndex) {
        const searchHighlight = searchHighlightForColumn(searchMatches, rowSearchCursor, rowSearchEndIndex, col, cols);
        rowSearchCursor = searchHighlight.nextIndex;
        searchHighlightKind = searchHighlight.kind;
      }
      if (selectionActiveForCell) {
        const selectionBg = resolveHighlightBackgroundColor(selectionBackgroundColor, fg, bg, inverse);
        pushRect2(selectionData, x3, rowY, cellW, cellH, selectionBg);
        if (selectionForegroundColor) {
          const selectionFg = resolveHighlightForegroundColor(selectionForegroundColor, fg, bg, inverse);
          fg = selectionFg;
          ul = selectionFg;
        }
      } else if (searchHighlightKind === 1 || searchHighlightKind === 2) {
        const searchBg = resolveHighlightBackgroundColor(searchHighlightKind === 2 ? searchCurrentMatchBackgroundColor : searchMatchBackgroundColor, fg, bg, inverse);
        const searchFg = resolveHighlightForegroundColor(searchHighlightKind === 2 ? searchCurrentMatchTextColor : searchMatchTextColor, fg, bg, inverse);
        pushRect2(selectionData, x3, rowY, cellW, cellH, searchBg);
        fg = searchFg;
        ul = searchFg;
      }
      const bgForText = bg[3] < 1 ? [
        bg[0] + defaultBg[0] * (1 - bg[3]),
        bg[1] + defaultBg[1] * (1 - bg[3]),
        bg[2] + defaultBg[2] * (1 - bg[3]),
        1
      ] : bg;
      if ((bgBytes || inverse) && bg[3] > 0)
        pushRect2(bgData, x3, rowY, cellW, cellH, bg);
      const linkId = linkIds ? linkIds[idx] ?? 0 : 0;
      const linkHovered = linkId && linkId === linkState.hoverId;
      const blinkOff = blink && !blinkVisible;
      const textHidden = invisible || blinkOff;
      if (!textHidden && !bgOnly) {
        if (underlineStyle > 0 && ul[3] > 0) {
          drawUnderlineStyle2(underlineData, underlineStyle, x3, rowY, cellW, cellH, baseY, underlineOffsetPx, underlineThicknessPx, ul);
        }
        if (linkHovered && !selectionState.active && !selectionState.dragging) {
          drawUnderlineStyle2(underlineData, 1, x3, rowY, cellW, cellH, baseY, underlineOffsetPx, underlineThicknessPx, ul);
        }
        if (strike)
          drawStrikethrough2(underlineData, x3, rowY, cellW, cellH, fg);
        if (overline)
          drawOverline2(underlineData, x3, rowY, cellW, fg);
      }
      if (bgOnly || textHidden)
        continue;
      if (mergedEmojiSkip[idx])
        continue;
      const cluster = readCellCluster(idx);
      if (!cluster)
        continue;
      const cp = cluster.cp;
      if (cp === KITTY_PLACEHOLDER_CP)
        continue;
      let text2 = cluster.text;
      let baseSpan = cluster.span;
      const rowEnd = row * cols + cols;
      if (isRegionalIndicator(cp)) {
        const nextIdx = idx + baseSpan;
        if (nextIdx < rowEnd && !mergedEmojiSkip[nextIdx]) {
          const next = readCellCluster(nextIdx);
          if (next && isRegionalIndicator(next.cp)) {
            text2 += next.text;
            baseSpan += next.span;
            mergedEmojiSkip[nextIdx] = 1;
          }
        }
      }
      let nextSeqIdx = idx + baseSpan;
      let guard = 0;
      while (nextSeqIdx < rowEnd && guard < 12) {
        const next = readCellCluster(nextSeqIdx);
        if (!next || !next.cp || isSpaceCp2(next.cp))
          break;
        const shouldMerge = text2.endsWith("‍") || shouldMergeTrailingClusterCodepoint2(next.cp);
        if (!shouldMerge)
          break;
        text2 += next.text;
        baseSpan += next.span;
        mergedEmojiSkip[nextSeqIdx] = 1;
        nextSeqIdx += next.span;
        guard += 1;
      }
      const extra = text2.length > String.fromCodePoint(cp).length ? 1 : 0;
      if (extra === 0 && isSpaceCp2(cp))
        continue;
      if (cursorBlock && cursorCell && row === cursorCell.row && col >= cursorCell.col && col < cursorCell.col + (cursorCell.wide ? 2 : 1)) {
        fg = [bgForText[0], bgForText[1], bgForText[2], 1];
      }
      if (isBlockElement2(cp)) {
        if (drawBlockElement2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (isBoxDrawing2(cp)) {
        if (drawBoxDrawing2(cp, x3, rowY, cellW, cellH, fg, fgRectData, underlineThicknessPx)) {
          continue;
        }
      }
      if (isBraille2(cp)) {
        if (drawBraille2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (isPowerline2(cp)) {
        if (drawPowerline2(cp, x3, rowY, cellW, cellH, fg, fgRectData))
          continue;
      }
      if (extra > 0 && text2.trim() === "")
        continue;
      const fontIndex = pickFontIndexForText2(text2, baseSpan, stylePreferenceFromFlags2(bold, italic));
      const fontEntry = fontState.fonts[fontIndex] ?? fontState.fonts[0];
      const shaped = shapeClusterWithFont(fontEntry, text2);
      if (!shaped.glyphs.length)
        continue;
      noteColorGlyphText(fontEntry, text2, shaped);
      const glyphSet = getGlyphSet(fontIndex);
      for (const glyph of shaped.glyphs)
        glyphSet.add(glyph.glyphId);
      const fontScale = scaleByFont[fontIndex] ?? primaryScale;
      let cellSpan = baseSpan;
      const nerdConstraint = resolveSymbolConstraint2(cp);
      const symbolLike = isRenderSymbolLike2(cp) || !!nerdConstraint;
      const symbolConstraint = !!nerdConstraint;
      let constraintWidth = baseSpan;
      let forceFit = false;
      let glyphWidthPx = 0;
      if (symbolLike) {
        if (baseSpan === 1) {
          constraintWidth = 1;
          if (col < cols - 1) {
            if (col > 0) {
              const prevCp = codepoints[idx - 1];
              if (!((isRenderSymbolLike2(prevCp) || !!resolveSymbolConstraint2(prevCp)) && !isGraphicsElement2(prevCp))) {
                const nextCp = codepoints[idx + 1];
                if (!nextCp || isSpaceCp2(nextCp))
                  constraintWidth = 2;
              }
            } else {
              const nextCp = codepoints[idx + 1];
              if (!nextCp || isSpaceCp2(nextCp))
                constraintWidth = 2;
            }
          }
          cellSpan = constraintWidth;
        }
        if (shaped.glyphs.length === 1) {
          const glyphId = shaped.glyphs[0].glyphId;
          const widthUnits = glyphWidthUnits2(fontEntry, glyphId);
          if (widthUnits > 0) {
            glyphWidthPx = widthUnits * fontScale;
          }
        }
        if (!glyphWidthPx) {
          glyphWidthPx = shaped.advance * fontScale;
        }
        if (glyphWidthPx > cellW * cellSpan * 1.05) {
          forceFit = true;
        }
      }
      if (symbolConstraint) {
        for (const glyph of shaped.glyphs) {
          noteGlyphMeta(fontIndex, glyph.glyphId, cp, constraintWidth);
        }
      }
      const cellWidthPx = cellW * cellSpan;
      const xPad = 0;
      getGlyphQueue(fontIndex).push({
        x: x3,
        baseY,
        xPad,
        fg,
        bg: bgForText,
        shaped,
        fontIndex,
        scale: fontScale,
        cellWidth: cellWidthPx,
        symbolLike,
        symbolConstraint,
        constraintWidth,
        forceFit,
        glyphWidthPx,
        cp,
        italic,
        bold
      });
    }
  }
  return {
    bgData,
    selectionData,
    underlineData,
    cursorData,
    fgRectData,
    overlayData,
    glyphDataNearestByFont,
    glyphDataLinearByFont,
    glyphQueueByFont,
    overlayGlyphDataNearestByFont,
    overlayGlyphDataLinearByFont,
    overlayGlyphQueueByFont,
    neededGlyphIdsByFont,
    neededGlyphMetaByFont,
    scaleByFont,
    bitmapScaleByFont,
    baselineAdjustByFont,
    nerdMetrics,
    getGlyphQueue,
    getOverlayGlyphQueue,
    getGlyphSet
  };
}

// src/runtime/create-runtime/render-tick-webgpu-emit-glyphs.ts
function emitWebGPUQueuedGlyphs(params) {
  const {
    deps,
    state: state2,
    frame,
    queueByFont,
    targetMaps,
    cellW,
    cellH,
    yPad,
    baselineOffset,
    primaryScale
  } = params;
  const {
    defaultBg,
    isAppleSymbolsFont: isAppleSymbolsFont2,
    resolveSymbolConstraint: resolveSymbolConstraint2,
    DEFAULT_APPLE_SYMBOLS_CONSTRAINT: DEFAULT_APPLE_SYMBOLS_CONSTRAINT2,
    DEFAULT_SYMBOL_CONSTRAINT: DEFAULT_SYMBOL_CONSTRAINT2,
    DEFAULT_EMOJI_CONSTRAINT: DEFAULT_EMOJI_CONSTRAINT2,
    constrainGlyphBox: constrainGlyphBox2,
    tightenNerdConstraintBox: tightenNerdConstraintBox2,
    fontEntryHasItalicStyle: fontEntryHasItalicStyle2,
    fontEntryHasBoldStyle: fontEntryHasBoldStyle2,
    ITALIC_SLANT,
    BOLD_OFFSET,
    GLYPH_RENDER_MODE_COLOR,
    GLYPH_RENDER_MODE_MONO,
    clamp: clamp2
  } = deps;
  const getGlyphData = (map, fontIndex) => {
    if (!map.has(fontIndex))
      map.set(fontIndex, []);
    return map.get(fontIndex);
  };
  for (const [fontIndex, queue] of queueByFont.entries()) {
    const entry = deps.fontState.fonts[fontIndex];
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!entry || !entry.atlas || !atlasState)
      continue;
    const atlas = entry.atlas;
    const atlasW = atlas.bitmap.width;
    const atlasH = atlas.bitmap.rows;
    const baseInset = Number.isFinite(atlas.inset) ? atlas.inset : 0;
    const colorGlyphs = atlasState.colorGlyphs ?? atlas.colorGlyphs;
    for (const item of queue) {
      const bg = item.bg ?? defaultBg;
      let penX = 0;
      const scale = item.scale ?? primaryScale;
      const maxWidth = item.cellWidth ?? cellW;
      const maxHeight = cellH;
      const symbolLike = item.symbolLike;
      const symbolConstraint = item.symbolConstraint;
      const glyphDataNearest = getGlyphData(targetMaps.nearest, fontIndex);
      const glyphDataLinear = getGlyphData(targetMaps.linear, fontIndex);
      let itemScale = scale;
      if (!symbolConstraint) {
        if (item.forceFit && item.glyphWidthPx && maxWidth > 0) {
          const fit = maxWidth / item.glyphWidthPx;
          if (fit > 0 && fit < 1)
            itemScale = scale * fit;
        }
        if (!symbolLike && fontIndex === 0) {
          const advancePx = item.shaped.advance * scale;
          if (advancePx > maxWidth && advancePx > 0) {
            itemScale = scale * (maxWidth / advancePx);
          }
        }
      }
      const scaleFactor = scale > 0 ? itemScale / scale : 1;
      const widthKey = item.constraintWidth ?? 0;
      const widthMap = atlas.glyphsByWidth?.get(widthKey);
      for (const glyph of item.shaped.glyphs) {
        const colorGlyph = !!colorGlyphs?.has(glyph.glyphId);
        const metrics = widthMap?.get(glyph.glyphId) ?? atlas.glyphs.get(glyph.glyphId);
        if (!metrics)
          continue;
        const applySymbolLayout = symbolLike && (fontIndex > 0 || symbolConstraint || colorGlyph);
        let bitmapScale = scaleFactor;
        const glyphConstrained = symbolLike && !!widthMap?.has(glyph.glyphId);
        if (glyphConstrained)
          bitmapScale = 1;
        if (fontIndex > 0 && !symbolLike) {
          const heightScale = maxHeight > 0 ? maxHeight / metrics.height : 1;
          const clampScale = Math.min(1, heightScale);
          bitmapScale *= clampScale;
        }
        const baselineAdjust = frame.baselineAdjustByFont[fontIndex] ?? 0;
        const glyphBaselineAdjust = symbolLike && !symbolConstraint ? 0 : baselineAdjust;
        const rowY = item.baseY - yPad - baselineOffset;
        let gw = metrics.width * bitmapScale;
        let gh = metrics.height * bitmapScale;
        if (applySymbolLayout && !glyphConstrained) {
          const scaleToFit = gw > 0 && gh > 0 ? Math.min(maxWidth / gw, maxHeight / gh) : 1;
          if (scaleToFit < 1) {
            bitmapScale *= scaleToFit;
            gw *= scaleToFit;
            gh *= scaleToFit;
          }
          gw = Math.max(1, Math.round(gw));
          gh = Math.max(1, Math.round(gh));
        }
        let x3 = item.x + item.xPad + (penX + glyph.xOffset) * itemScale + metrics.bearingX * bitmapScale;
        if (fontIndex > 0 && item.shaped.glyphs.length === 1 && !symbolLike && maxWidth <= cellW * 1.05) {
          x3 = item.x + (maxWidth - gw) * 0.5;
        }
        const minX = item.x;
        const maxX = item.x + maxWidth;
        if (x3 < minX)
          x3 = minX;
        if (x3 + gw > maxX)
          x3 = Math.max(minX, maxX - gw);
        let y = item.baseY + glyphBaselineAdjust - metrics.bearingY * bitmapScale - glyph.yOffset * itemScale;
        if (applySymbolLayout && !glyphConstrained && item.cp) {
          const nerdConstraint = resolveSymbolConstraint2(item.cp);
          const defaultConstraint = isAppleSymbolsFont2(entry) ? DEFAULT_APPLE_SYMBOLS_CONSTRAINT2 : DEFAULT_SYMBOL_CONSTRAINT2;
          const constraint = nerdConstraint ?? (colorGlyph ? DEFAULT_EMOJI_CONSTRAINT2 : defaultConstraint);
          const constraintWidth = Math.max(1, item.constraintWidth ?? Math.round(maxWidth / cellW));
          const adjusted = constrainGlyphBox2({ x: x3 - item.x, y: y - rowY, width: gw, height: gh }, constraint, frame.nerdMetrics, constraintWidth);
          const tightened = nerdConstraint ? tightenNerdConstraintBox2(adjusted, nerdConstraint) : adjusted;
          x3 = item.x + tightened.x;
          y = rowY + tightened.y;
          gw = tightened.width;
          gh = tightened.height;
        }
        if (gw < 1)
          gw = 1;
        if (gh < 1)
          gh = 1;
        const scaled = Math.abs(gw - metrics.width) > 0.01 || Math.abs(gh - metrics.height) > 0.01;
        const useNearest = atlasState.nearest && !scaled;
        if (useNearest) {
          gw = metrics.width;
          gh = metrics.height;
        }
        const uvInset = baseInset + (useNearest ? 0.5 : 0);
        const px = Math.round(x3);
        const py = Math.round(y);
        const insetX = Math.min(uvInset, (metrics.width - 1) * 0.5);
        const insetY = Math.min(uvInset, (metrics.height - 1) * 0.5);
        const u02 = (metrics.atlasX + insetX) / atlasW;
        const v02 = (metrics.atlasY + insetY) / atlasH;
        const u12 = (metrics.atlasX + metrics.width - insetX) / atlasW;
        const v12 = (metrics.atlasY + metrics.height - insetY) / atlasH;
        const glyphData = useNearest ? glyphDataNearest : glyphDataLinear;
        const italic = !!item.italic;
        const bold = !!item.bold;
        const syntheticItalic = italic && !fontEntryHasItalicStyle2(entry);
        const syntheticBold = bold && !fontEntryHasBoldStyle2(entry);
        const slant = syntheticItalic && !colorGlyph ? gh * ITALIC_SLANT : 0;
        const boldOffset = syntheticBold && !colorGlyph ? Math.max(1, Math.round(gw * BOLD_OFFSET)) : 0;
        const renderMode = colorGlyph ? GLYPH_RENDER_MODE_COLOR : GLYPH_RENDER_MODE_MONO;
        const pushGlyph = (xPos) => {
          glyphData.push(xPos, py, gw, gh, u02, v02, u12, v12, item.fg[0], item.fg[1], item.fg[2], item.fg[3], bg[0], bg[1], bg[2], bg[3], slant, renderMode);
        };
        pushGlyph(px);
        if (boldOffset > 0) {
          const minGlyphX = Math.round(item.x);
          const maxGlyphX = Math.round(item.x + maxWidth - gw);
          let bx = clamp2(px + boldOffset, minGlyphX, maxGlyphX);
          if (bx === px)
            bx = clamp2(px - boldOffset, minGlyphX, maxGlyphX);
          if (bx === px)
            pushGlyph(px);
          else
            pushGlyph(bx);
        }
        penX += glyph.xAdvance;
      }
    }
  }
}

// src/runtime/create-runtime/render-tick-webgpu-draw-pass.ts
function drawWebGPUFrame(params) {
  const {
    deps,
    state: state2,
    frame,
    cursor,
    cursorPos,
    cursorStyle,
    rows,
    cols,
    cellW,
    cellH,
    yPad,
    baselineOffset,
    underlineOffsetPx,
    underlineThicknessPx,
    primaryScale,
    useLinearBlending,
    useLinearCorrection,
    clearColor,
    hasShaderStages,
    stageTargets,
    compiledWebGPUStages
  } = params;
  const { device, context } = state2;
  const {
    wasmHandle,
    wasmExports,
    canvas,
    decodePackedRGBA: decodePackedRGBA2,
    cursorFallback,
    clamp: clamp2,
    scrollbarState,
    syncScrollbar,
    webgpuUniforms,
    ensureInstanceBuffer: ensureInstanceBuffer2,
    GLYPH_INSTANCE_FLOATS,
    wasm,
    collectKittyDrawPlan,
    resolveKittyWebGPUBindGroup
  } = deps;
  emitWebGPUQueuedGlyphs({
    deps,
    state: state2,
    frame,
    queueByFont: frame.glyphQueueByFont,
    targetMaps: {
      nearest: frame.glyphDataNearestByFont,
      linear: frame.glyphDataLinearByFont
    },
    cellW,
    cellH,
    yPad,
    baselineOffset,
    primaryScale
  });
  emitWebGPUQueuedGlyphs({
    deps,
    state: state2,
    frame,
    queueByFont: frame.overlayGlyphQueueByFont,
    targetMaps: {
      nearest: frame.overlayGlyphDataNearestByFont,
      linear: frame.overlayGlyphDataLinearByFont
    },
    cellW,
    cellH,
    yPad,
    baselineOffset,
    primaryScale
  });
  if (cursorStyle !== null && cursorPos) {
    let cursorCol = cursorPos.col;
    let cursorRow = cursorPos.row;
    let cursorWidth = cellW;
    if (cursorPos.wideTail && cursorCol > 0) {
      cursorCol -= 1;
      cursorWidth = cellW * 2;
    }
    if (cursorRow < rows && cursorCol < cols) {
      const x3 = cursorCol * cellW;
      const y = cursorRow * cellH;
      const cursorColor = cursor?.color ? decodePackedRGBA2(cursor.color) : cursorFallback;
      const cursorThicknessPx = underlineThicknessPx;
      if (cursorStyle === 0) {
        deps.pushRect(frame.fgRectData, x3, y, cursorWidth, cellH, cursorColor);
      } else if (cursorStyle === 1) {
        const offset = Math.floor((cursorThicknessPx + 1) / 2);
        deps.pushRect(frame.cursorData, x3 - offset, y, cursorThicknessPx, cellH, cursorColor);
      } else if (cursorStyle === 2) {
        const baseY = cursorRow * cellH + yPad + baselineOffset;
        const underlineY = clamp2(baseY + underlineOffsetPx, y + 1, y + cellH - cursorThicknessPx - 1);
        deps.pushRect(frame.cursorData, x3, underlineY, cursorWidth, cursorThicknessPx, cursorColor);
      } else if (cursorStyle === 3) {
        deps.pushRect(frame.cursorData, x3, y, cursorWidth, cursorThicknessPx, cursorColor);
        deps.pushRect(frame.cursorData, x3, y + cellH - cursorThicknessPx, cursorWidth, cursorThicknessPx, cursorColor);
        deps.pushRect(frame.cursorData, x3, y, cursorThicknessPx, cellH, cursorColor);
        deps.pushRect(frame.cursorData, x3 + cursorWidth - cursorThicknessPx, y, cursorThicknessPx, cellH, cursorColor);
      } else {
        deps.pushRect(frame.cursorData, x3, y, cursorWidth, cellH, cursorColor);
      }
    }
  }
  if (wasmExports && wasmHandle && wasmExports.restty_scrollbar_total) {
    const total = wasmExports.restty_scrollbar_total(wasmHandle) || 0;
    const offset = wasmExports.restty_scrollbar_offset ? wasmExports.restty_scrollbar_offset(wasmHandle) : 0;
    const len = wasmExports.restty_scrollbar_len ? wasmExports.restty_scrollbar_len(wasmHandle) : rows;
    if (total !== scrollbarState.lastTotal || offset !== scrollbarState.lastOffset || len !== scrollbarState.lastLen) {
      scrollbarState.lastTotal = total;
      scrollbarState.lastOffset = offset;
      scrollbarState.lastLen = len;
    }
    syncScrollbar(total, offset, len);
  }
  webgpuUniforms[0] = canvas.width;
  webgpuUniforms[1] = canvas.height;
  webgpuUniforms[2] = 0;
  webgpuUniforms[3] = 0;
  webgpuUniforms[4] = useLinearBlending ? 1 : 0;
  webgpuUniforms[5] = useLinearCorrection ? 1 : 0;
  webgpuUniforms[6] = 0;
  webgpuUniforms[7] = 0;
  device.queue.writeBuffer(state2.uniformBuffer, 0, webgpuUniforms);
  const presentView = context.getCurrentTexture().createView();
  const mainView = hasShaderStages && stageTargets ? stageTargets.sceneView : presentView;
  const encoder = device.createCommandEncoder();
  const pass = encoder.beginRenderPass({
    colorAttachments: [
      {
        view: mainView,
        clearValue: { r: clearColor[0], g: clearColor[1], b: clearColor[2], a: clearColor[3] },
        loadOp: "clear",
        storeOp: "store"
      }
    ]
  });
  const alignTo4 = (value) => value + 3 & ~3;
  const uploadInstanceBatches = (kind, batches) => {
    if (!batches.length)
      return;
    let totalBytes = 0;
    for (const batch of batches) {
      totalBytes = alignTo4(totalBytes);
      batch.offset = totalBytes;
      totalBytes += batch.array.byteLength;
    }
    ensureInstanceBuffer2(state2, kind, totalBytes);
    const buffer = kind === "rect" ? state2.rectInstanceBuffer : state2.glyphInstanceBuffer;
    for (const batch of batches) {
      device.queue.writeBuffer(buffer, batch.offset, batch.array);
    }
  };
  const rectBaseBatches = [];
  const rectDecorBatches = [];
  const rectTopBatches = [];
  const pushRectBatch = (target, data) => {
    if (!data.length)
      return;
    target.push({
      array: new Float32Array(data),
      offset: 0,
      instances: data.length / 8
    });
  };
  pushRectBatch(rectBaseBatches, frame.bgData);
  pushRectBatch(rectBaseBatches, frame.selectionData);
  pushRectBatch(rectDecorBatches, frame.underlineData);
  pushRectBatch(rectDecorBatches, frame.fgRectData);
  pushRectBatch(rectTopBatches, frame.cursorData);
  pushRectBatch(rectTopBatches, frame.overlayData);
  const glyphMainBatches = [];
  const glyphOverlayBatches = [];
  for (const [fontIndex, glyphData] of frame.glyphDataNearestByFont.entries()) {
    if (!glyphData.length)
      continue;
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!atlasState?.bindGroupNearest)
      continue;
    glyphMainBatches.push({
      array: new Float32Array(glyphData),
      offset: 0,
      instances: glyphData.length / GLYPH_INSTANCE_FLOATS,
      pipeline: state2.glyphPipelineNearest,
      bindGroup: atlasState.bindGroupNearest
    });
  }
  for (const [fontIndex, glyphData] of frame.glyphDataLinearByFont.entries()) {
    if (!glyphData.length)
      continue;
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!atlasState?.bindGroupLinear)
      continue;
    glyphMainBatches.push({
      array: new Float32Array(glyphData),
      offset: 0,
      instances: glyphData.length / GLYPH_INSTANCE_FLOATS,
      pipeline: state2.glyphPipeline,
      bindGroup: atlasState.bindGroupLinear
    });
  }
  for (const [fontIndex, glyphData] of frame.overlayGlyphDataNearestByFont.entries()) {
    if (!glyphData.length)
      continue;
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!atlasState?.bindGroupNearest)
      continue;
    glyphOverlayBatches.push({
      array: new Float32Array(glyphData),
      offset: 0,
      instances: glyphData.length / GLYPH_INSTANCE_FLOATS,
      pipeline: state2.glyphPipelineNearest,
      bindGroup: atlasState.bindGroupNearest
    });
  }
  for (const [fontIndex, glyphData] of frame.overlayGlyphDataLinearByFont.entries()) {
    if (!glyphData.length)
      continue;
    const atlasState = state2.glyphAtlases?.get(fontIndex);
    if (!atlasState?.bindGroupLinear)
      continue;
    glyphOverlayBatches.push({
      array: new Float32Array(glyphData),
      offset: 0,
      instances: glyphData.length / GLYPH_INSTANCE_FLOATS,
      pipeline: state2.glyphPipeline,
      bindGroup: atlasState.bindGroupLinear
    });
  }
  const kittyPlacements = wasm && wasmHandle ? wasm.getKittyPlacements(wasmHandle) : [];
  const kittyPlan = collectKittyDrawPlan(kittyPlacements, cellW, cellH);
  const kittyUnderlayBatches = [];
  const kittyOverlayBatches = [];
  const pushKittyBatches = (target, slices) => {
    let currentBindGroup = null;
    let currentData = [];
    const flush = () => {
      if (!currentBindGroup || !currentData.length)
        return;
      target.push({
        array: new Float32Array(currentData),
        offset: 0,
        instances: currentData.length / GLYPH_INSTANCE_FLOATS,
        pipeline: state2.glyphPipeline,
        bindGroup: currentBindGroup
      });
      currentData = [];
    };
    for (const slice of slices) {
      const bindGroup = resolveKittyWebGPUBindGroup(state2, slice, false);
      if (!bindGroup)
        continue;
      if (currentBindGroup && bindGroup !== currentBindGroup) {
        flush();
      }
      currentBindGroup = bindGroup;
      const invW = slice.imageWidth > 0 ? 1 / slice.imageWidth : 0;
      const invH = slice.imageHeight > 0 ? 1 / slice.imageHeight : 0;
      const u02 = slice.sx * invW;
      const v02 = slice.sy * invH;
      const u12 = (slice.sx + slice.sw) * invW;
      const v12 = (slice.sy + slice.sh) * invH;
      currentData.push(slice.dx, slice.dy, slice.dw, slice.dh, u02, v02, u12, v12, 1, 1, 1, 1, 0, 0, 0, 0, 0, 1);
    }
    flush();
  };
  pushKittyBatches(kittyUnderlayBatches, kittyPlan.underlay);
  pushKittyBatches(kittyOverlayBatches, kittyPlan.overlay);
  uploadInstanceBatches("rect", [...rectBaseBatches, ...rectDecorBatches, ...rectTopBatches]);
  uploadInstanceBatches("glyph", [
    ...glyphMainBatches,
    ...glyphOverlayBatches,
    ...kittyUnderlayBatches,
    ...kittyOverlayBatches
  ]);
  pass.setVertexBuffer(0, state2.vertexBuffer);
  const drawRectBatches = (batches) => {
    if (!batches.length)
      return;
    pass.setPipeline(state2.rectPipeline);
    pass.setBindGroup(0, state2.rectBindGroup);
    for (const batch of batches) {
      pass.setVertexBuffer(1, state2.rectInstanceBuffer, batch.offset, batch.array.byteLength);
      pass.draw(6, batch.instances, 0, 0);
    }
  };
  const drawGlyphBatches = (batches) => {
    for (const batch of batches) {
      pass.setPipeline(batch.pipeline);
      pass.setBindGroup(0, batch.bindGroup);
      pass.setVertexBuffer(1, state2.glyphInstanceBuffer, batch.offset, batch.array.byteLength);
      pass.draw(6, batch.instances, 0, 0);
    }
  };
  drawRectBatches(rectBaseBatches);
  drawGlyphBatches(kittyUnderlayBatches);
  drawRectBatches(rectDecorBatches);
  drawGlyphBatches(glyphMainBatches);
  drawGlyphBatches(kittyOverlayBatches);
  drawRectBatches(rectTopBatches);
  drawGlyphBatches(glyphOverlayBatches);
  pass.end();
  if (hasShaderStages && stageTargets) {
    let source = "scene";
    const nowSec = performance.now() * 0.001;
    for (let i3 = 0;i3 < compiledWebGPUStages.length; i3 += 1) {
      const stage = compiledWebGPUStages[i3];
      stage.uniformData[0] = canvas.width;
      stage.uniformData[1] = canvas.height;
      stage.uniformData[2] = nowSec;
      stage.uniformData[3] = 0;
      stage.uniformData[4] = stage.params[0] ?? 0;
      stage.uniformData[5] = stage.params[1] ?? 0;
      stage.uniformData[6] = stage.params[2] ?? 0;
      stage.uniformData[7] = stage.params[3] ?? 0;
      stage.uniformData[8] = stage.params[4] ?? 0;
      stage.uniformData[9] = stage.params[5] ?? 0;
      stage.uniformData[10] = stage.params[6] ?? 0;
      stage.uniformData[11] = stage.params[7] ?? 0;
      device.queue.writeBuffer(stage.uniformBuffer, 0, stage.uniformData);
      const isLast = i3 === compiledWebGPUStages.length - 1;
      const target = !isLast ? source === "ping" ? stageTargets.pongView : stageTargets.pingView : presentView;
      const bindGroup = source === "scene" ? stage.bindGroupScene : source === "ping" ? stage.bindGroupPing : stage.bindGroupPong;
      if (!bindGroup)
        continue;
      const stagePass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: target,
            clearValue: { r: 0, g: 0, b: 0, a: 1 },
            loadOp: "clear",
            storeOp: "store"
          }
        ]
      });
      stagePass.setPipeline(stage.pipeline);
      stagePass.setBindGroup(0, bindGroup);
      stagePass.setVertexBuffer(0, state2.vertexBuffer);
      stagePass.draw(6, 1, 0, 0);
      stagePass.end();
      if (!isLast) {
        source = source === "ping" ? "pong" : "ping";
      }
    }
  }
  device.queue.submit([encoder.finish()]);
}

// src/runtime/create-runtime/render-tick-webgpu-overlays-atlas.ts
function augmentWebGPUFrameWithOverlaysAndAtlas(params) {
  const {
    deps,
    state: state2,
    frame,
    cursor,
    cursorImeAnchor,
    cursorCell,
    cols,
    cellW,
    cellH,
    yPad,
    baselineOffset,
    underlineOffsetPx,
    underlineThicknessPx,
    lineHeight,
    primaryScale,
    fontSizePx
  } = params;
  const {
    fontState,
    imeState,
    pickFontIndexForText: pickFontIndexForText2,
    shapeClusterWithFont,
    noteColorGlyphText,
    fitTextTailToWidth: fitTextTailToWidth2,
    PREEDIT_BG: PREEDIT_BG2,
    PREEDIT_UL: PREEDIT_UL2,
    PREEDIT_ACTIVE_BG: PREEDIT_ACTIVE_BG2,
    PREEDIT_CARET: PREEDIT_CARET2,
    PREEDIT_FG: PREEDIT_FG2,
    resizeState,
    RESIZE_OVERLAY_HOLD_MS,
    RESIZE_OVERLAY_FADE_MS,
    clamp: clamp2,
    pushRect: pushRect2,
    pushRectBox: pushRectBox2,
    ensureAtlasForFont
  } = deps;
  if (cursor && imeState.preedit) {
    const preeditText = imeState.preedit;
    const preeditFontIndex = pickFontIndexForText2(preeditText, 1);
    const preeditEntry = fontState.fonts[preeditFontIndex] ?? fontState.fonts[0];
    const preeditScale = frame.scaleByFont[preeditFontIndex] ?? primaryScale;
    const preeditRow = cursorImeAnchor?.row ?? cursorCell?.row ?? cursor.row;
    const preeditCol = cursorImeAnchor?.col ?? cursorCell?.col ?? cursor.col;
    const maxPreeditWidthPx = Math.max(cellW, (cols - preeditCol) * cellW);
    const fittedPreedit = fitTextTailToWidth2(preeditText, maxPreeditWidthPx, (value) => {
      if (!value)
        return 0;
      return shapeClusterWithFont(preeditEntry, value).advance * preeditScale;
    });
    const visiblePreeditText = fittedPreedit.text;
    if (visiblePreeditText) {
      const shaped = shapeClusterWithFont(preeditEntry, visiblePreeditText);
      noteColorGlyphText(preeditEntry, visiblePreeditText, shaped);
      const glyphSet = frame.getGlyphSet(preeditFontIndex);
      for (const glyph of shaped.glyphs)
        glyphSet.add(glyph.glyphId);
      const baseY = preeditRow * cellH + yPad + baselineOffset;
      const x3 = preeditCol * cellW;
      const advancePx = shaped.advance * preeditScale;
      const widthPx = Math.max(cellW, Math.min(maxPreeditWidthPx, Math.max(fittedPreedit.widthPx, advancePx)));
      const rowY = preeditRow * cellH;
      pushRect2(frame.bgData, x3, rowY, widthPx, cellH, PREEDIT_BG2);
      const thickness = underlineThicknessPx;
      const underlineBaseY = clamp2(baseY + underlineOffsetPx, rowY + 1, rowY + cellH - thickness - 1);
      pushRect2(frame.underlineData, x3, underlineBaseY, widthPx, thickness, PREEDIT_UL2);
      const selectionOffset = fittedPreedit.offset;
      const rawSelStart = imeState.selectionStart || 0;
      const rawSelEnd = imeState.selectionEnd || 0;
      const selStart = Math.max(0, Math.min(visiblePreeditText.length, rawSelStart - selectionOffset));
      const selEnd = Math.max(selStart, Math.min(visiblePreeditText.length, rawSelEnd - selectionOffset));
      if (selEnd > selStart) {
        const leftWidth = shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(0, selStart)).advance * preeditScale;
        const selWidth = shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(selStart, selEnd)).advance * preeditScale;
        pushRect2(frame.bgData, x3 + leftWidth, rowY, selWidth, cellH, PREEDIT_ACTIVE_BG2);
        pushRect2(frame.underlineData, x3 + leftWidth, underlineBaseY, selWidth, thickness, PREEDIT_UL2);
      } else {
        const caretWidth = Math.max(1, Math.floor(cellW * 0.1));
        const caretX = x3 + shapeClusterWithFont(preeditEntry, visiblePreeditText.slice(0, selStart)).advance * preeditScale;
        pushRect2(frame.cursorData, caretX, rowY + 2, caretWidth, cellH - 4, PREEDIT_CARET2);
      }
      frame.getGlyphQueue(preeditFontIndex).push({
        x: x3,
        baseY,
        xPad: 0,
        fg: PREEDIT_FG2,
        bg: PREEDIT_BG2,
        shaped,
        fontIndex: preeditFontIndex,
        scale: preeditScale,
        cellWidth: widthPx,
        symbolLike: false
      });
    }
  }
  const resizeAge = performance.now() - resizeState.lastAt;
  if (resizeState.cols > 0 && resizeState.rows > 0 && resizeAge >= 0 && resizeAge < RESIZE_OVERLAY_HOLD_MS + RESIZE_OVERLAY_FADE_MS) {
    const fade2 = resizeAge <= RESIZE_OVERLAY_HOLD_MS ? 1 : 1 - (resizeAge - RESIZE_OVERLAY_HOLD_MS) / RESIZE_OVERLAY_FADE_MS;
    const alpha = clamp2(fade2, 0, 1);
    if (alpha > 0.01) {
      const overlayText = `${resizeState.cols}x${resizeState.rows}`;
      const overlayEntry = fontState.fonts[0];
      if (overlayEntry?.font) {
        const shaped = shapeClusterWithFont(overlayEntry, overlayText);
        const glyphSet = frame.getGlyphSet(0);
        for (const glyph of shaped.glyphs)
          glyphSet.add(glyph.glyphId);
        const textWidth = shaped.advance * primaryScale;
        const padX = Math.max(8, cellW * 0.6);
        const padY = Math.max(6, cellH * 0.4);
        const boxW = textWidth + padX * 2;
        const boxH = lineHeight + padY * 2;
        const boxX = (deps.canvas.width - boxW) * 0.5;
        const boxY = (deps.canvas.height - boxH) * 0.5;
        const overlayBg = [0, 0, 0, 0.6 * alpha];
        pushRectBox2(frame.overlayData, boxX, boxY, boxW, boxH, overlayBg);
        pushRectBox2(frame.overlayData, boxX, boxY, boxW, 1, [1, 1, 1, 0.12 * alpha]);
        const textRowY = boxY + (boxH - lineHeight) * 0.5;
        const baseY = textRowY + yPad + baselineOffset;
        frame.getOverlayGlyphQueue(0).push({
          x: boxX + padX,
          baseY,
          xPad: 0,
          fg: [1, 1, 1, alpha],
          bg: overlayBg,
          shaped,
          fontIndex: 0,
          scale: primaryScale,
          cellWidth: textWidth,
          symbolLike: false
        });
      }
    }
  }
  for (const [fontIndex, neededSet] of frame.neededGlyphIdsByFont.entries()) {
    const entry = fontState.fonts[fontIndex];
    if (!entry)
      continue;
    const atlasScale = frame.bitmapScaleByFont[fontIndex] ?? 1;
    const meta = frame.neededGlyphMetaByFont.get(fontIndex);
    const constraintContext = meta ? {
      cellW,
      cellH,
      yPad,
      baselineOffset,
      baselineAdjust: frame.baselineAdjustByFont[fontIndex] ?? 0,
      fontScale: frame.scaleByFont[fontIndex] ?? primaryScale,
      nerdMetrics: frame.nerdMetrics,
      fontEntry: entry
    } : null;
    ensureAtlasForFont(state2.device, state2, entry, neededSet, fontSizePx, fontIndex, atlasScale, meta, constraintContext);
  }
}

// src/runtime/create-runtime/render-tick-webgpu.ts
function tickWebGPU(deps, state2) {
  const {
    isShaderStagesDirty,
    rebuildWebGPUShaderStages,
    setShaderStagesDirty,
    getCompiledWebGPUShaderStages,
    ensureWebGPUStageTargets,
    fontError,
    termDebug,
    reportDebugText,
    updateGrid,
    getRenderState,
    fontState,
    resolveBlendFlags: resolveBlendFlags2,
    alphaBlending,
    srgbToLinearColor: srgbToLinearColor2,
    defaultBg,
    reportTermSize,
    resolveCursorPosition,
    reportCursor,
    FORCE_CURSOR_BLINK,
    CURSOR_BLINK_MS,
    imeInput,
    resolveCursorStyle,
    isFocused,
    imeState,
    resolveImeAnchor: resolveImeAnchor2,
    dbgEl,
    wasmExports,
    wasmHandle,
    gridState,
    canvas,
    fontHeightUnits: fontHeightUnits2,
    updateImePosition: updateImePosition2
  } = deps;
  const { device, context } = state2;
  if (isShaderStagesDirty()) {
    rebuildWebGPUShaderStages(state2);
    setShaderStagesDirty(false);
  }
  const compiledWebGPUStages = getCompiledWebGPUShaderStages();
  const shaderStageCount = compiledWebGPUStages.length;
  const stageTargets = shaderStageCount > 0 ? ensureWebGPUStageTargets(state2) : null;
  const hasShaderStages = shaderStageCount > 0 && !!stageTargets;
  if (fontError) {
    const text2 = `Font error: ${fontError.message}`;
    if (termDebug)
      termDebug.textContent = text2;
    reportDebugText(text2);
  }
  updateGrid();
  const render = getRenderState();
  if (!render || !fontState.font) {
    if (deps.lastRenderState) {
      return;
    }
    const { useLinearBlending: useLinearBlending2 } = resolveBlendFlags2(alphaBlending, "webgpu", state2);
    const clearColor2 = useLinearBlending2 ? srgbToLinearColor2(defaultBg) : defaultBg;
    const encoder = device.createCommandEncoder();
    const presentView = context.getCurrentTexture().createView();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: presentView,
          clearValue: { r: clearColor2[0], g: clearColor2[1], b: clearColor2[2], a: clearColor2[3] },
          loadOp: "clear",
          storeOp: "store"
        }
      ]
    });
    pass.end();
    device.queue.submit([encoder.finish()]);
    return;
  }
  deps.lastRenderState = render;
  const {
    rows,
    cols,
    codepoints,
    contentTags,
    wide,
    styleFlags,
    linkIds,
    fgBytes,
    bgBytes,
    ulBytes,
    ulStyle,
    graphemeOffset,
    graphemeLen,
    graphemeBuffer,
    cursor
  } = render;
  if (!codepoints || !fgBytes)
    return;
  const { useLinearBlending, useLinearCorrection } = resolveBlendFlags2(alphaBlending, "webgpu", state2);
  const clearColor = useLinearBlending ? srgbToLinearColor2(defaultBg) : defaultBg;
  reportTermSize(cols, rows);
  const cursorPos = cursor ? resolveCursorPosition(cursor) : null;
  reportCursor(cursorPos);
  const isBlinking = (cursor?.blinking || 0) !== 0 || FORCE_CURSOR_BLINK;
  const blinkVisible = !isBlinking || Math.floor(performance.now() / CURSOR_BLINK_MS) % 2 === 0;
  const imeFocused = typeof document !== "undefined" && imeInput ? document.activeElement === imeInput : false;
  const cursorStyle = cursor ? resolveCursorStyle(cursor, {
    focused: isFocused || imeFocused,
    preedit: Boolean(imeState.preedit),
    blinkVisible
  }) : null;
  let cursorCell = null;
  if (cursorStyle !== null && cursorPos) {
    let col = cursorPos.col;
    const row = cursorPos.row;
    let wideCell = false;
    if (cursorPos.wideTail && col > 0) {
      col -= 1;
      wideCell = true;
    }
    cursorCell = { row, col, wide: wideCell };
  }
  let imeCursorPos = cursorPos;
  if (cursor?.visible === 0 && wasmExports?.restty_active_cursor_x && wasmExports?.restty_active_cursor_y && wasmHandle) {
    const activeCol = wasmExports.restty_active_cursor_x(wasmHandle);
    const activeRow = wasmExports.restty_active_cursor_y(wasmHandle);
    const inViewport = Number.isFinite(activeCol) && Number.isFinite(activeRow) && activeCol >= 0 && activeRow >= 0 && activeCol < cols && activeRow < rows;
    if (inViewport) {
      imeCursorPos = {
        col: activeCol,
        row: activeRow,
        wideTail: cursor.wideTail === 1
      };
    }
  }
  const cursorImeAnchor = resolveImeAnchor2(imeCursorPos, cols, rows);
  if (dbgEl && wasmExports && wasmHandle) {
    const cx = wasmExports.restty_active_cursor_x ? wasmExports.restty_active_cursor_x(wasmHandle) : 0;
    const cy = wasmExports.restty_active_cursor_y ? wasmExports.restty_active_cursor_y(wasmHandle) : 0;
    const sl = wasmExports.restty_debug_scroll_left ? wasmExports.restty_debug_scroll_left(wasmHandle) : 0;
    const sr = wasmExports.restty_debug_scroll_right ? wasmExports.restty_debug_scroll_right(wasmHandle) : 0;
    const tc = wasmExports.restty_debug_term_cols ? wasmExports.restty_debug_term_cols(wasmHandle) : 0;
    const tr = wasmExports.restty_debug_term_rows ? wasmExports.restty_debug_term_rows(wasmHandle) : 0;
    const pc = wasmExports.restty_debug_page_cols ? wasmExports.restty_debug_page_cols(wasmHandle) : 0;
    const pr = wasmExports.restty_debug_page_rows ? wasmExports.restty_debug_page_rows(wasmHandle) : 0;
    const text2 = `${cx},${cy} | ${sl}-${sr} | t:${tc}x${tr} p:${pc}x${pr}`;
    reportDebugText(text2);
  }
  const cellW = gridState.cellW || canvas.width / cols;
  const cellH = gridState.cellH || canvas.height / rows;
  const fontSizePx = gridState.fontSizePx || Math.max(1, Math.round(cellH));
  const primaryScale = gridState.scale || fontState.font.scaleForSize(fontSizePx, fontState.sizeMode);
  const lineHeight = gridState.lineHeight || fontHeightUnits2(fontState.font) * primaryScale;
  const baselineOffset = gridState.baselineOffset || fontState.font.ascender * primaryScale;
  const yPad = gridState.yPad ?? (cellH - lineHeight) / 2;
  const post = fontState.font.post;
  const underlinePosition = post?.underlinePosition ?? Math.round(-fontState.font.upem * 0.08);
  const underlineThickness = post?.underlineThickness ?? Math.round(fontState.font.upem * 0.05);
  const underlineOffsetPx = -underlinePosition * primaryScale;
  const underlineThicknessPx = Math.max(1, Math.ceil(underlineThickness * primaryScale));
  if (cursorImeAnchor) {
    updateImePosition2(cursorImeAnchor, cellW, cellH);
  }
  const frame = collectWebGPUCellPass({
    deps,
    render: {
      rows,
      cols,
      codepoints,
      contentTags,
      wide,
      styleFlags,
      linkIds,
      fgBytes,
      bgBytes,
      ulBytes,
      ulStyle,
      graphemeOffset,
      graphemeLen,
      graphemeBuffer
    },
    cellW,
    cellH,
    fontSizePx,
    primaryScale,
    lineHeight,
    baselineOffset,
    yPad,
    underlineOffsetPx,
    underlineThicknessPx,
    cursorBlock: cursorStyle === 0 && !!cursorCell,
    cursorCell,
    blinkVisible,
    defaultBg
  });
  augmentWebGPUFrameWithOverlaysAndAtlas({
    deps,
    state: state2,
    frame,
    cursor,
    cursorImeAnchor,
    cursorCell,
    cols,
    cellW,
    cellH,
    yPad,
    baselineOffset,
    underlineOffsetPx,
    underlineThicknessPx,
    lineHeight,
    primaryScale,
    fontSizePx
  });
  drawWebGPUFrame({
    deps,
    state: state2,
    frame,
    cursor,
    cursorPos,
    cursorStyle,
    rows,
    cols,
    cellW,
    cellH,
    yPad,
    baselineOffset,
    underlineOffsetPx,
    underlineThicknessPx,
    primaryScale,
    useLinearBlending,
    useLinearCorrection,
    clearColor,
    hasShaderStages,
    stageTargets,
    compiledWebGPUStages
  });
}

// src/runtime/create-runtime/render-ticks.ts
function createRuntimeRenderTicks(deps) {
  function tickWebGPU2(state2) {
    return tickWebGPU(deps, state2);
  }
  function tickWebGL2(state2) {
    return tickWebGL(deps, state2);
  }
  return { tickWebGPU: tickWebGPU2, tickWebGL: tickWebGL2 };
}

// src/runtime/create-runtime/font-runtime-grid-helpers.ts
function createFontRuntimeGridHelpers(options) {
  const {
    fontState,
    fontConfig,
    gridState,
    callbacks,
    gridEl,
    cellEl,
    getCanvas,
    getCurrentDpr,
    getActiveState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    ptyTransport,
    setNeedsRender,
    markSearchDirty,
    shapeClusterWithFont
  } = options;
  function destroyGlyphAtlases() {
    const activeState = getActiveState();
    if (!activeState?.glyphAtlases)
      return;
    if ("gl" in activeState) {
      for (const atlas of activeState.glyphAtlases.values()) {
        try {
          activeState.gl.deleteTexture(atlas.texture);
        } catch {
        }
      }
      activeState.glyphAtlases.clear();
      return;
    }
    for (const atlas of activeState.glyphAtlases.values()) {
      try {
        atlas.texture.destroy();
      } catch {
      }
    }
    activeState.glyphAtlases.clear();
  }
  function computeCellMetrics2() {
    const primary = fontState.fonts[0];
    if (!primary)
      return null;
    const fontSizePx = Math.max(1, Math.round(fontConfig.sizePx * getCurrentDpr()));
    const scale = primary.font.scaleForSize(fontSizePx, fontState.sizeMode);
    const glyphId = primary.font.glyphIdForChar("M");
    const advanceUnits = glyphId !== undefined && glyphId !== null ? primary.font.advanceWidth(glyphId) : shapeClusterWithFont(primary, "M").advance;
    const cellW = Math.max(1, Math.round(advanceUnits * scale));
    const lineHeight = fontHeightUnits(primary.font) * scale;
    const cellH = Math.max(1, Math.round(lineHeight));
    const baselineOffset = primary.font.ascender * scale;
    const yPad = Math.max(0, (cellH - lineHeight) * 0.5);
    return { cellW, cellH, fontSizePx, scale, lineHeight, baselineOffset, yPad };
  }
  function commitTerminalResize(cols, rows) {
    const wasmReady = getWasmReady();
    const wasm = getWasm();
    const wasmHandle = getWasmHandle();
    const canvas = getCanvas();
    if (wasmReady && wasm && wasmHandle) {
      wasm.resize(wasmHandle, cols, rows);
      wasm.renderUpdate(wasmHandle);
      markSearchDirty?.();
    }
    if (ptyTransport.isConnected()) {
      ptyTransport.resize(cols, rows, {
        widthPx: canvas.width,
        heightPx: canvas.height,
        cellW: gridState.cellW,
        cellH: gridState.cellH
      });
    }
    setNeedsRender();
  }
  function updateGrid() {
    if (!fontState.fonts.length)
      return;
    const metrics = computeCellMetrics2();
    if (!metrics)
      return;
    const canvas = getCanvas();
    const cols = Math.max(1, Math.floor(canvas.width / metrics.cellW));
    const rows = Math.max(1, Math.floor(canvas.height / metrics.cellH));
    if (!Number.isFinite(cols) || !Number.isFinite(rows))
      return;
    const gridSizeChanged = cols !== gridState.cols || rows !== gridState.rows;
    const cellSizeChanged = metrics.cellW !== gridState.cellW || metrics.cellH !== gridState.cellH;
    if (gridSizeChanged) {
      if (gridEl)
        gridEl.textContent = `${cols}x${rows}`;
      callbacks?.onGridSize?.(cols, rows);
    }
    if (cellSizeChanged) {
      if (cellEl)
        cellEl.textContent = `${Math.round(metrics.cellW)}x${Math.round(metrics.cellH)}`;
      callbacks?.onCellSize?.(metrics.cellW, metrics.cellH);
    }
    const changed = gridSizeChanged || metrics.fontSizePx !== gridState.fontSizePx || cellSizeChanged;
    if (metrics.fontSizePx !== gridState.fontSizePx) {
      for (const entry of fontState.fonts)
        resetFontEntry(entry);
      destroyGlyphAtlases();
    }
    Object.assign(gridState, metrics, { cols, rows });
    const wasmReady = getWasmReady();
    const wasm = getWasm();
    const wasmHandle = getWasmHandle();
    if (wasmReady && wasm && wasmHandle) {
      wasm.setPixelSize(wasmHandle, canvas.width, canvas.height);
    }
    if (changed) {
      commitTerminalResize(cols, rows);
    }
  }
  return {
    computeCellMetrics: computeCellMetrics2,
    updateGrid
  };
}

// src/runtime/create-runtime/font-runtime-text-helpers.ts
function setBoundedMap(map, key, value, limit) {
  if (map.has(key)) {
    map.delete(key);
  }
  map.set(key, value);
  if (map.size <= limit)
    return;
  const oldest = map.keys().next().value;
  if (oldest !== undefined) {
    map.delete(oldest);
  }
}
function createFontRuntimeTextHelpers(options) {
  const {
    fontState,
    glyphShapeCacheLimit,
    fontPickCacheLimit,
    UnicodeBuffer,
    shape,
    glyphBufferToShapedGlyphs
  } = options;
  function shapeClusterWithFont(entry, text2) {
    const cached = entry.glyphCache.get(text2);
    if (cached)
      return cached;
    const buffer = new UnicodeBuffer;
    buffer.addStr(text2);
    const glyphBuffer = shape(entry.font, buffer);
    const glyphs = glyphBufferToShapedGlyphs(glyphBuffer);
    const advance = glyphs.reduce((sum, g) => sum + g.xAdvance, 0);
    const shaped = { glyphs, advance };
    setBoundedMap(entry.glyphCache, text2, shaped, glyphShapeCacheLimit);
    return shaped;
  }
  function noteColorGlyphText(entry, text2, shaped) {
    if (!isColorEmojiFont(entry) || shaped.glyphs.length !== 1)
      return;
    const glyphId = shaped.glyphs[0]?.glyphId;
    if (!glyphId)
      return;
    if (!entry.colorGlyphTexts)
      entry.colorGlyphTexts = new Map;
    entry.colorGlyphTexts.set(glyphId, text2);
  }
  function fontHasGlyph2(font, ch) {
    const glyphId = font.glyphIdForChar(ch);
    return glyphId !== undefined && glyphId !== null && glyphId !== 0;
  }
  function pickFontIndexForText2(text2, expectedSpan = 1, stylePreference = "regular") {
    if (!fontState.fonts.length)
      return 0;
    const cacheKey = `${expectedSpan}:${stylePreference}:${text2}`;
    const cached = fontState.fontPickCache.get(cacheKey);
    if (cached !== undefined)
      return cached;
    const chars = Array.from(text2);
    const requiredChars = chars.filter((ch) => {
      const cp = ch.codePointAt(0) ?? 0;
      return !isCoverageIgnorableCodepoint2(cp);
    });
    const firstCp = text2.codePointAt(0) ?? 0;
    const nerdSymbol = isNerdSymbolCodepoint(firstCp);
    const presentation = resolvePresentationPreference2(text2, chars);
    const styleHintsEnabled = stylePreference !== "regular" && presentation !== "emoji" && !nerdSymbol;
    const hasBoldHint = (entry) => /\bbold\b/i.test(entry.label ?? "");
    const hasItalicHint = (entry) => /\b(italic|oblique)\b/i.test(entry.label ?? "");
    const stylePredicates = stylePreference === "bold_italic" ? [
      (entry) => hasBoldHint(entry) && hasItalicHint(entry),
      (entry) => hasBoldHint(entry),
      (entry) => hasItalicHint(entry)
    ] : stylePreference === "bold" ? [(entry) => hasBoldHint(entry) && !hasItalicHint(entry), (entry) => hasBoldHint(entry)] : stylePreference === "italic" ? [
      (entry) => hasItalicHint(entry) && !hasBoldHint(entry),
      (entry) => hasItalicHint(entry)
    ] : [];
    const pickFirstMatch = (predicate, allowSequenceShapingFallback = false) => {
      for (let i3 = 0;i3 < fontState.fonts.length; i3 += 1) {
        const entry = fontState.fonts[i3];
        if (!entry?.font)
          continue;
        if (predicate && !predicate(entry))
          continue;
        let ok = true;
        for (const ch of requiredChars) {
          if (!fontHasGlyph2(entry.font, ch)) {
            ok = false;
            break;
          }
        }
        if (!ok && allowSequenceShapingFallback) {
          const shaped = shapeClusterWithFont(entry, text2);
          ok = shaped.glyphs.some((glyph) => (glyph.glyphId ?? 0) !== 0);
        }
        if (ok)
          return i3;
      }
      return -1;
    };
    const pickWithStyle = (predicate, allowSequenceShapingFallback = false) => {
      if (styleHintsEnabled) {
        for (let i3 = 0;i3 < stylePredicates.length; i3 += 1) {
          const stylePredicate = stylePredicates[i3];
          const styledIndex = pickFirstMatch((entry) => {
            if (!stylePredicate(entry))
              return false;
            return predicate ? !!predicate(entry) : true;
          }, allowSequenceShapingFallback);
          if (styledIndex >= 0)
            return styledIndex;
        }
      }
      return pickFirstMatch(predicate, allowSequenceShapingFallback);
    };
    const tryIndex = (index) => {
      if (index < 0)
        return null;
      setBoundedMap(fontState.fontPickCache, cacheKey, index, fontPickCacheLimit);
      return index;
    };
    if (nerdSymbol) {
      const symbolIndex = pickWithStyle((entry) => isNerdSymbolFont(entry) || isSymbolFont(entry));
      const result = tryIndex(symbolIndex);
      if (result !== null)
        return result;
    }
    if (presentation === "emoji") {
      const emojiIndex = pickFirstMatch((entry) => isColorEmojiFont(entry), true);
      const result = tryIndex(emojiIndex);
      if (result !== null)
        return result;
    } else if (presentation === "text") {
      const textIndex = pickFirstMatch((entry) => !isColorEmojiFont(entry));
      const result = tryIndex(textIndex);
      if (result !== null)
        return result;
    }
    const firstIndex = pickWithStyle();
    if (firstIndex >= 0) {
      setBoundedMap(fontState.fontPickCache, cacheKey, firstIndex, fontPickCacheLimit);
      return firstIndex;
    }
    setBoundedMap(fontState.fontPickCache, cacheKey, 0, fontPickCacheLimit);
    return 0;
  }
  return {
    shapeClusterWithFont,
    noteColorGlyphText,
    fontHasGlyph: fontHasGlyph2,
    pickFontIndexForText: pickFontIndexForText2
  };
}

// src/runtime/create-runtime/font-runtime-webgpu-atlas.ts
function createRuntimeWebGPUAtlasHelpers(options) {
  const {
    fontState,
    getFontHinting,
    getFontHintTarget,
    fontScaleOverrides,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA: padAtlasRGBA2,
    buildAtlas,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    pixelModeRgbaValue,
    atlasPadding,
    symbolAtlasPadding,
    symbolAtlasMaxSize
  } = options;
  function ensureAtlasForFont(device, state2, entry, neededGlyphIds, fontSizePx, fontIndex, atlasScale, glyphMeta, constraintContext) {
    const built = buildFontAtlasIfNeeded({
      entry,
      neededGlyphIds,
      glyphMeta,
      fontSizePx,
      atlasScale,
      fontIndex,
      constraintContext,
      deps: {
        fontScaleOverrides,
        sizeMode: fontState.sizeMode,
        isSymbolFont,
        fontScaleOverride,
        resolveGlyphPixelMode: resolveGlyphPixelMode2,
        atlasBitmapToRGBA: atlasBitmapToRGBA2,
        padAtlasRGBA: padAtlasRGBA2,
        buildAtlas,
        buildGlyphAtlasWithConstraints,
        buildColorEmojiAtlasWithCanvas,
        rasterizeGlyph,
        rasterizeGlyphWithTransform,
        hinting: getFontHinting(),
        hintTarget: getFontHintTarget(),
        nerdConstraintSignature,
        constants: {
          atlasPadding,
          symbolAtlasPadding,
          symbolAtlasMaxSize,
          defaultAtlasMaxSize: 2048,
          pixelModeRgbaValue
        },
        resolvePreferNearest: ({ fontIndex: idx, isSymbol, atlasScale: scale }) => {
          const scaleHint = scale ?? 1;
          return idx === 0 || isSymbol || scaleHint >= 0.99;
        }
      }
    });
    if (!built.rebuilt || !built.atlas || !built.rgba)
      return false;
    const atlas = built.atlas;
    const colorGlyphs = built.colorGlyphs;
    const preferNearest = built.preferNearest;
    const rgba = built.rgba;
    const texture = device.createTexture({
      size: [atlas.bitmap.width, atlas.bitmap.rows, 1],
      format: "rgba8unorm",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC | GPUTextureUsage.RENDER_ATTACHMENT
    });
    const width = atlas.bitmap.width;
    const height = atlas.bitmap.rows;
    const bytesPerRow = width * 4;
    const alignedBytesPerRow = Math.ceil(bytesPerRow / 256) * 256;
    let upload = rgba;
    if (alignedBytesPerRow !== bytesPerRow) {
      const padded = new Uint8Array(alignedBytesPerRow * height);
      for (let row = 0;row < height; row += 1) {
        const srcStart = row * bytesPerRow;
        const srcEnd = srcStart + bytesPerRow;
        const dstStart = row * alignedBytesPerRow;
        padded.set(rgba.subarray(srcStart, srcEnd), dstStart);
      }
      upload = padded;
    }
    device.queue.writeTexture({ texture }, upload, { bytesPerRow: alignedBytesPerRow, rowsPerImage: height }, { width, height, depthOrArrayLayers: 1 });
    const samplerNearest = device.createSampler({
      magFilter: "nearest",
      minFilter: "nearest",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge"
    });
    const samplerLinear = device.createSampler({
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge"
    });
    const bindGroupNearest = device.createBindGroup({
      layout: state2.glyphPipelineNearest.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: state2.uniformBuffer } },
        { binding: 1, resource: samplerNearest },
        { binding: 2, resource: texture.createView() }
      ]
    });
    const bindGroupLinear = device.createBindGroup({
      layout: state2.glyphPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: state2.uniformBuffer } },
        { binding: 1, resource: samplerLinear },
        { binding: 2, resource: texture.createView() }
      ]
    });
    if (!state2.glyphAtlases)
      state2.glyphAtlases = new Map;
    const inset = Number.isFinite(atlas.inset) ? atlas.inset : 0;
    state2.glyphAtlases.set(fontIndex, {
      texture,
      sampler: preferNearest ? undefined : samplerLinear,
      samplerNearest,
      samplerLinear,
      bindGroup: preferNearest ? bindGroupNearest : bindGroupLinear,
      bindGroupNearest,
      bindGroupLinear,
      width: atlas.bitmap.width,
      height: atlas.bitmap.rows,
      inset,
      colorGlyphs,
      nearest: preferNearest
    });
    return true;
  }
  return {
    ensureAtlasForFont
  };
}

// src/runtime/create-runtime/font-runtime-helpers.ts
function createRuntimeFontRuntimeHelpers(options) {
  const {
    fontState,
    fontConfig,
    gridState,
    callbacks,
    gridEl,
    cellEl,
    getCanvas,
    getCurrentDpr,
    getActiveState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    ptyTransport,
    setNeedsRender,
    markSearchDirty,
    getFontHinting,
    getFontHintTarget,
    fontScaleOverrides,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA: padAtlasRGBA2,
    buildAtlas,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    pixelModeRgbaValue,
    atlasPadding,
    symbolAtlasPadding,
    symbolAtlasMaxSize,
    glyphShapeCacheLimit,
    fontPickCacheLimit,
    UnicodeBuffer,
    shape,
    glyphBufferToShapedGlyphs
  } = options;
  const textHelpers = createFontRuntimeTextHelpers({
    fontState,
    glyphShapeCacheLimit,
    fontPickCacheLimit,
    UnicodeBuffer,
    shape,
    glyphBufferToShapedGlyphs
  });
  const gridHelpers = createFontRuntimeGridHelpers({
    fontState,
    fontConfig,
    gridState,
    callbacks,
    gridEl,
    cellEl,
    getCanvas,
    getCurrentDpr,
    getActiveState,
    getWasmReady,
    getWasm,
    getWasmHandle,
    ptyTransport,
    setNeedsRender,
    markSearchDirty,
    shapeClusterWithFont: textHelpers.shapeClusterWithFont
  });
  const { ensureAtlasForFont } = createRuntimeWebGPUAtlasHelpers({
    fontState,
    getFontHinting,
    getFontHintTarget,
    fontScaleOverrides,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA: padAtlasRGBA2,
    buildAtlas,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    pixelModeRgbaValue,
    atlasPadding,
    symbolAtlasPadding,
    symbolAtlasMaxSize
  });
  return {
    ...textHelpers,
    ...gridHelpers,
    ensureAtlasForFont
  };
}

// src/runtime/create-runtime/runtime-reporting.ts
function createRuntimeReporting(options) {
  let lastReportedTermCols = -1;
  let lastReportedTermRows = -1;
  let lastReportedCursorCol = -1;
  let lastReportedCursorRow = -1;
  let lastResolvedCursor = null;
  let lastReportedDebugText = "";
  function selectionForRow2(row, cols) {
    return selectionForRow(options.selectionState, row, cols);
  }
  function getCellText(render, idx) {
    const cp = render.codepoints[idx];
    if (!cp)
      return " ";
    let text2 = String.fromCodePoint(cp);
    if (render.graphemeLen && render.graphemeOffset && render.graphemeBuffer) {
      const extra = render.graphemeLen[idx] ?? 0;
      if (extra > 0) {
        const start = render.graphemeOffset[idx] ?? 0;
        const cps = [cp];
        for (let j = 0;j < extra; j += 1) {
          const extraCp = render.graphemeBuffer[start + j];
          if (extraCp)
            cps.push(extraCp);
        }
        text2 = String.fromCodePoint(...cps);
      }
    }
    return text2;
  }
  function getSelectionText2() {
    const lastRenderState = options.getLastRenderState();
    if (!lastRenderState)
      return "";
    const { rows, cols } = lastRenderState;
    return getSelectionText(options.selectionState, rows, cols, (idx) => getCellText(lastRenderState, idx));
  }
  function getRenderState() {
    const wasmReady = options.getWasmReady();
    const wasm = options.getWasm();
    const wasmHandle = options.getWasmHandle();
    if (!wasmReady || !wasm || !wasmHandle)
      return null;
    return wasm.getRenderState(wasmHandle);
  }
  function resolveCursorPosition(cursor) {
    if (!cursor)
      return lastResolvedCursor;
    if (cursor.visible === 0 && lastResolvedCursor) {
      return lastResolvedCursor;
    }
    let col = Number(cursor.col);
    let row = Number(cursor.row);
    if (!Number.isFinite(col))
      col = 0;
    if (!Number.isFinite(row))
      row = 0;
    const render = options.getLastRenderState();
    const cols = render?.cols ?? 0;
    const rows = render?.rows ?? 0;
    const inBounds = (valueCol, valueRow) => {
      if (!Number.isFinite(valueCol) || !Number.isFinite(valueRow))
        return false;
      if (cols <= 0 || rows <= 0)
        return true;
      return valueCol >= 0 && valueRow >= 0 && valueCol < cols && valueRow < rows;
    };
    const wasmExports = options.getWasmExports();
    const wasmHandle = options.getWasmHandle();
    const getActiveCursor = () => {
      if (!wasmExports?.restty_active_cursor_x || !wasmExports?.restty_active_cursor_y || !wasmHandle) {
        return null;
      }
      const activeCol = wasmExports.restty_active_cursor_x(wasmHandle);
      const activeRow = wasmExports.restty_active_cursor_y(wasmHandle);
      if (!inBounds(activeCol, activeRow))
        return null;
      return { col: activeCol, row: activeRow };
    };
    if (cursor.visible === 0 && !lastResolvedCursor) {
      const active = getActiveCursor();
      if (active) {
        lastResolvedCursor = {
          col: active.col,
          row: active.row,
          wideTail: cursor.wideTail === 1
        };
        return lastResolvedCursor;
      }
    }
    const activeFallback = !inBounds(col, row) ? getActiveCursor() : null;
    if (activeFallback) {
      col = activeFallback.col;
      row = activeFallback.row;
    }
    if (cols > 0 && rows > 0) {
      col = Math.max(0, Math.min(cols - 1, Math.floor(col)));
      row = Math.max(0, Math.min(rows - 1, Math.floor(row)));
    } else {
      col = Math.max(0, Math.floor(col));
      row = Math.max(0, Math.floor(row));
    }
    lastResolvedCursor = { col, row, wideTail: cursor.wideTail === 1 };
    return lastResolvedCursor;
  }
  function resolveCursorStyle(cursor, opts) {
    if (!cursor)
      return null;
    const visible = cursor.visible !== 0;
    if (!visible || opts.preedit)
      return null;
    if (!opts.focused)
      return 3;
    if (cursor.blinking && !opts.blinkVisible)
      return null;
    return cursor.style ?? 0;
  }
  function reportTermSize(cols, rows) {
    if (cols === lastReportedTermCols && rows === lastReportedTermRows)
      return;
    lastReportedTermCols = cols;
    lastReportedTermRows = rows;
    if (options.termSizeEl)
      options.termSizeEl.textContent = `${cols}x${rows}`;
    options.callbacks?.onTermSize?.(cols, rows);
  }
  function reportCursor(cursorPos) {
    if (!cursorPos)
      return;
    const { col, row } = cursorPos;
    if (options.cursorPosEl && (col !== lastReportedCursorCol || row !== lastReportedCursorRow)) {
      options.cursorPosEl.textContent = `${col},${row}`;
    }
    if (col !== lastReportedCursorCol || row !== lastReportedCursorRow) {
      options.callbacks?.onCursor?.(col, row);
      lastReportedCursorCol = col;
      lastReportedCursorRow = row;
    }
    options.setCursorForCpr({ row: row + 1, col: col + 1 });
  }
  function reportDebugText(text2) {
    if (text2 === lastReportedDebugText)
      return;
    lastReportedDebugText = text2;
    if (options.dbgEl)
      options.dbgEl.textContent = text2;
    options.callbacks?.onDebug?.(text2);
  }
  return {
    selectionForRow: selectionForRow2,
    getSelectionText: getSelectionText2,
    getRenderState,
    resolveCursorPosition,
    resolveCursorStyle,
    reportTermSize,
    reportCursor,
    reportDebugText
  };
}

// src/runtime/max-scrollback.ts
var DEFAULT_MAX_SCROLLBACK_BYTES = 1e7;
var MAX_MAX_SCROLLBACK_BYTES = 256000000;
function normalizeMaxScrollbackBytes(value) {
  if (!Number.isFinite(value))
    return DEFAULT_MAX_SCROLLBACK_BYTES;
  const asInt = Math.trunc(value);
  if (asInt <= 0)
    return 0;
  if (asInt >= MAX_MAX_SCROLLBACK_BYTES)
    return MAX_MAX_SCROLLBACK_BYTES;
  return asInt;
}
function resolveMaxScrollbackBytes(options) {
  return normalizeMaxScrollbackBytes(options.maxScrollbackBytes ?? options.maxScrollback);
}

// src/runtime/create-runtime/runtime-app-api.ts
function createRuntimeAppApi(options) {
  const {
    session,
    ptyTransport,
    inputHandler,
    ptyInputRuntime,
    interaction,
    lifecycleThemeSizeRuntime,
    cleanupFns,
    cleanupCanvasFns,
    callbacks,
    fpsEl,
    backendEl,
    inputDebugEl,
    imeInput,
    attachWindowEvents,
    isMacPlatform,
    textEncoder: textEncoder3,
    readState,
    writeState,
    appendLog,
    shouldSuppressWasmLog,
    runBeforeInputHook,
    runBeforeRenderOutputHook,
    runBeforeRenderOutputBytesHook,
    CURSOR_BLINK_MS,
    RESIZE_ACTIVE_MS,
    TARGET_RENDER_FPS,
    BACKGROUND_RENDER_FPS,
    KITTY_FLAG_REPORT_EVENTS: KITTY_FLAG_REPORT_EVENTS2,
    resizeState,
    tickWebGPU: tickWebGPU2,
    tickWebGL: tickWebGL2,
    updateGrid,
    gridState,
    getCanvas,
    applyTheme,
    ensureFont,
    updateSize,
    log,
    replaceCanvas,
    rebuildWebGPUShaderStages,
    rebuildWebGLShaderStages,
    setShaderStagesDirty,
    clearWebGPUShaderStages,
    destroyWebGPUStageTargets,
    clearWebGLShaderStages,
    destroyWebGLStageTargets,
    markSearchDirty,
    handleSearchWasmReset
  } = options;
  const internalState = {
    paused: false,
    backend: "none",
    preferredRenderer: options.initialPreferredRenderer,
    rafId: 0,
    frameCount: 0,
    lastFpsTime: performance.now(),
    nextBlinkTime: performance.now() + CURSOR_BLINK_MS
  };
  const maxScrollbackBytes = resolveMaxScrollbackBytes(options);
  function updateFps() {
    internalState.frameCount += 1;
    const now = performance.now();
    if (now - internalState.lastFpsTime >= 500) {
      const fps = Math.round(internalState.frameCount * 1000 / (now - internalState.lastFpsTime));
      if (fpsEl)
        fpsEl.textContent = `${fps}`;
      callbacks?.onFps?.(fps);
      internalState.frameCount = 0;
      internalState.lastFpsTime = now;
    }
  }
  function loop(state2) {
    if (!internalState.paused) {
      const now = performance.now();
      if (now >= internalState.nextBlinkTime) {
        internalState.nextBlinkTime = now + CURSOR_BLINK_MS;
        writeState({ needsRender: true });
      }
      const resizeActive = now - resizeState.lastAt <= RESIZE_ACTIVE_MS;
      if (resizeActive) {
        writeState({ needsRender: true });
      }
      const hidden = typeof document !== "undefined" && typeof document.visibilityState === "string" && document.visibilityState !== "visible";
      const targetRenderFps = hidden ? BACKGROUND_RENDER_FPS : TARGET_RENDER_FPS;
      const nextShared = readState();
      const renderBudget = resizeActive ? true : now - nextShared.lastRenderTime >= 1000 / targetRenderFps;
      if (nextShared.needsRender && renderBudget) {
        if (internalState.backend === "webgpu" && "device" in state2)
          tickWebGPU2(state2);
        if (internalState.backend === "webgl2" && "gl" in state2)
          tickWebGL2(state2);
        writeState({ lastRenderTime: now, needsRender: false });
        updateFps();
      }
    }
    internalState.rafId = requestAnimationFrame(() => loop(state2));
  }
  const onWasmLog = (text2) => {
    if (shouldSuppressWasmLog(text2))
      return;
    console.log(`[wasm] ${text2}`);
    appendLog(`[wasm] ${text2}`);
  };
  if (session.addWasmLogListener) {
    session.addWasmLogListener(onWasmLog);
    cleanupFns.push(() => session.removeWasmLogListener?.(onWasmLog));
  }
  async function initWasm() {
    const shared = readState();
    if (shared.wasmReady && shared.wasm)
      return shared.wasm;
    const instance = await session.getWasm();
    writeState({
      wasm: instance,
      wasmExports: instance.exports,
      wasmReady: true
    });
    return instance;
  }
  function writeToWasm(handle, text2) {
    const shared = readState();
    if (!shared.wasm)
      return;
    shared.wasm.write(handle, text2);
  }
  function flushWasmOutputToPty() {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return;
    if (!ptyTransport.isConnected())
      return;
    if (options.readOnly) {
      while (shared.wasm.drainOutput(shared.wasmHandle)) {
      }
      return;
    }
    let iterations = 0;
    while (iterations < 32) {
      const out = shared.wasm.drainOutput(shared.wasmHandle);
      if (!out)
        break;
      ptyTransport.sendInput(out);
      iterations += 1;
    }
  }
  function createWasmHandle(instance) {
    updateGrid();
    const cols = gridState.cols || 80;
    const rows = gridState.rows || 24;
    const wasmHandle = instance.create(cols, rows, maxScrollbackBytes);
    if (!wasmHandle) {
      throw new Error("restty create failed (restty_create returned 0)");
    }
    const canvas = getCanvas();
    instance.setPixelSize(wasmHandle, canvas.width, canvas.height);
    return wasmHandle;
  }
  function sendInput(text2, source = "program", config = {}) {
    const shared = readState();
    if (!shared.wasmReady || !shared.wasm || !shared.wasmHandle)
      return;
    if (!text2)
      return;
    let intercepted = text2;
    if (!config.skipHooks) {
      intercepted = source === "pty" ? runBeforeRenderOutputHook(text2, source) : runBeforeInputHook(text2, source);
    }
    if (!intercepted)
      return;
    const normalized = source === "pty" ? intercepted : normalizeNewlines(intercepted);
    if (source === "key") {
      const bytes = textEncoder3.encode(normalized);
      const hex = Array.from(bytes, (b3) => b3.toString(16).padStart(2, "0")).join(" ");
      const debugText = `${hex} (${bytes.length})`;
      if (inputDebugEl)
        inputDebugEl.textContent = debugText;
      callbacks?.onInputDebug?.(debugText);
    }
    if (source === "key") {
      let before = "";
      if (shared.wasmExports?.restty_active_cursor_x && shared.wasmExports?.restty_active_cursor_y) {
        const bx = shared.wasmExports.restty_active_cursor_x(shared.wasmHandle);
        const by = shared.wasmExports.restty_active_cursor_y(shared.wasmHandle);
        before = ` cursor=${bx},${by}`;
      }
      appendLog(`[key] ${JSON.stringify(normalized)}${before}`);
    }
    if (source === "key" && (interaction.selectionState.active || interaction.selectionState.dragging)) {
      interaction.clearSelection();
    }
    if (source === "pty" && interaction.linkState.hoverId)
      interaction.updateLinkHover(null);
    const canvas = getCanvas();
    shared.wasm.setPixelSize(shared.wasmHandle, canvas.width, canvas.height);
    writeToWasm(shared.wasmHandle, normalized);
    flushWasmOutputToPty();
    markSearchDirty();
    if (source === "pty" && inputHandler.isSynchronizedOutput?.()) {
      ptyInputRuntime.scheduleSyncOutputReset();
      return;
    }
    ptyInputRuntime.cancelSyncOutputReset();
    shared.wasm.renderUpdate(shared.wasmHandle);
    if (source === "key" && shared.wasmExports?.restty_active_cursor_x && shared.wasmExports?.restty_active_cursor_y) {
      const ax = shared.wasmExports.restty_active_cursor_x(shared.wasmHandle);
      const ay = shared.wasmExports.restty_active_cursor_y(shared.wasmHandle);
      appendLog(`[key] after cursor=${ax},${ay}`);
    }
    writeState({ needsRender: true });
  }
  function sendInputBytes(data) {
    const shared = readState();
    if (!shared.wasmReady || !shared.wasm || !shared.wasmHandle)
      return;
    if (!data.length)
      return;
    if (!runBeforeRenderOutputBytesHook(data, "pty"))
      return;
    if (interaction.linkState.hoverId)
      interaction.updateLinkHover(null);
    const canvas = getCanvas();
    shared.wasm.setPixelSize(shared.wasmHandle, canvas.width, canvas.height);
    shared.wasm.writeBytes(shared.wasmHandle, data);
    while (shared.wasm.drainOutput(shared.wasmHandle)) {
    }
    markSearchDirty();
    if (inputHandler.isSynchronizedOutput?.()) {
      ptyInputRuntime.scheduleSyncOutputReset();
      return;
    }
    ptyInputRuntime.cancelSyncOutputReset();
    shared.wasm.renderUpdate(shared.wasmHandle);
    writeState({ needsRender: true });
  }
  async function copySelectionToClipboard() {
    const text2 = options.getSelectionText();
    if (!text2)
      return false;
    const copied = await copyToClipboard(text2);
    if (copied) {
      appendLog("[ui] selection copied");
      return true;
    }
    appendLog("[ui] copy failed");
    return false;
  }
  async function pasteFromClipboard2() {
    const text2 = await pasteFromClipboard();
    if (text2 === null) {
      appendLog("[ui] paste failed");
      return false;
    }
    if (text2) {
      ptyInputRuntime.sendPasteText(text2);
      return true;
    }
    return false;
  }
  function clearScreen() {
    sendInput("\x1B[2J\x1B[H");
  }
  function loadBinarySnapshot(data) {
    const shared = readState();
    if (!shared.wasmReady || !shared.wasm || !shared.wasmHandle)
      return false;
    if (!(data instanceof Uint8Array) || data.byteLength === 0)
      return false;
    if (interaction.selectionState.active || interaction.selectionState.dragging) {
      interaction.clearSelection();
    }
    if (interaction.linkState.hoverId) {
      interaction.updateLinkHover(null);
    }
    let nextHandle = 0;
    try {
      nextHandle = createWasmHandle(shared.wasm);
    } catch (e) {
      appendLog(`[snapshot] createWasmHandle failed: ${e}`);
      return false;
    }
    const error = shared.wasm.loadBinarySnapshot(nextHandle, data);
    if (error) {
      appendLog(`[snapshot] loadBinarySnapshot failed: ${error}`);
      shared.wasm.destroy(nextHandle);
      return false;
    }
    try {
      shared.wasm.destroy(shared.wasmHandle);
    } catch {
    }
    writeState({ wasmHandle: nextHandle });
    ptyInputRuntime.cancelSyncOutputReset();
    handleSearchWasmReset();
    writeState({ needsRender: true });
    return true;
  }
  function getColorForeground() {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return null;
    return shared.wasm.getColorForeground(shared.wasmHandle);
  }
  function getColorBackground() {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return null;
    return shared.wasm.getColorBackground(shared.wasmHandle);
  }
  function getColorCursor() {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return null;
    return shared.wasm.getColorCursor(shared.wasmHandle);
  }
  function getPaletteColor(index) {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return null;
    return shared.wasm.getPaletteColor(shared.wasmHandle, index);
  }
  function getPalette() {
    const shared = readState();
    if (!shared.wasm || !shared.wasmHandle)
      return null;
    return shared.wasm.getPalette(shared.wasmHandle);
  }
  if (attachWindowEvents) {
    const hasInputFocus = () => {
      if (typeof document === "undefined")
        return true;
      const active = document.activeElement;
      const canvas = getCanvas();
      return active === canvas || (imeInput ? active === imeInput : false);
    };
    const ensureImeInputFocus = () => {
      if (!imeInput || typeof document === "undefined")
        return;
      if (document.activeElement === imeInput)
        return;
      imeInput.focus({ preventScroll: true });
    };
    const isMacInputSourceShortcut = (event) => isMacPlatform && event.ctrlKey && !event.metaKey && (event.code === "Space" || event.key === " " || event.key === "Spacebar");
    const shouldSkipKeyEvent = (event) => {
      const imeActive = typeof document !== "undefined" && imeInput ? document.activeElement === imeInput : false;
      const target = event.target;
      if (target && target !== imeInput && ["BUTTON", "SELECT", "INPUT", "TEXTAREA"].includes(target.tagName)) {
        return true;
      }
      if (target === imeInput) {
        if (interaction.imeState.composing || event.isComposing)
          return true;
        if (!event.ctrlKey && !event.metaKey && event.key.length === 1 && !event.repeat)
          return true;
      }
      if (imeInput && imeActive && !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1 && !event.repeat && !event.isComposing && !interaction.imeState.composing) {
        return true;
      }
      return false;
    };
    const onKeyDown = (event) => {
      if (isMacInputSourceShortcut(event)) {
        if (hasInputFocus())
          ensureImeInputFocus();
        return;
      }
      if (shouldSkipKeyEvent(event))
        return;
      if (!hasInputFocus())
        return;
      ensureImeInputFocus();
      writeState({ isFocused: true });
      const shared = readState();
      if (!shared.wasmReady || !shared.wasmHandle)
        return;
      const key = event.key?.toLowerCase?.() ?? "";
      const hasPrimaryShortcutModifier = isMacPlatform ? event.metaKey : event.ctrlKey;
      const wantsCopy = hasPrimaryShortcutModifier && !event.altKey && (key === "c" || event.shiftKey && key === "c");
      const wantsPaste = hasPrimaryShortcutModifier && !event.altKey && (key === "v" || event.shiftKey && key === "v");
      if (wantsCopy && interaction.selectionState.active) {
        event.preventDefault();
        copySelectionToClipboard();
        return;
      }
      if (wantsPaste) {
        if (imeInput) {
          ensureImeInputFocus();
          return;
        }
        event.preventDefault();
        const seq2 = inputHandler.encodeKeyEvent(event);
        if (seq2) {
          ptyInputRuntime.sendKeyInput(seq2);
        }
        return;
      }
      const seq = inputHandler.encodeKeyEvent(event);
      if (seq) {
        if (event.type === "keydown" && ["Backspace", "Delete", "Del", "Enter"].includes(event.key)) {
          writeState({
            lastKeydownSeq: seq,
            lastKeydownSeqAt: performance.now()
          });
        }
        event.preventDefault();
        ptyInputRuntime.sendKeyInput(seq);
      }
    };
    const onKeyUp = (event) => {
      if (isMacInputSourceShortcut(event))
        return;
      const shared = readState();
      if (!shared.wasm || !shared.wasmHandle)
        return;
      if ((shared.wasm.getKittyKeyboardFlags(shared.wasmHandle) & KITTY_FLAG_REPORT_EVENTS2) === 0) {
        return;
      }
      if (shouldSkipKeyEvent(event))
        return;
      if (!hasInputFocus())
        return;
      writeState({ isFocused: true });
      const nextShared = readState();
      if (!nextShared.wasmReady || !nextShared.wasmHandle)
        return;
      const seq = inputHandler.encodeKeyEvent(event);
      if (seq) {
        event.preventDefault();
        ptyInputRuntime.sendKeyInput(seq);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    cleanupFns.push(() => window.removeEventListener("keydown", onKeyDown));
    cleanupFns.push(() => window.removeEventListener("keyup", onKeyUp));
  }
  async function initWasmHarness() {
    try {
      const instance = await initWasm();
      const shared = readState();
      if (shared.wasmHandle) {
        instance.destroy(shared.wasmHandle);
        writeState({ wasmHandle: 0 });
      }
      const wasmHandle = createWasmHandle(instance);
      const activeTheme = lifecycleThemeSizeRuntime.getActiveTheme();
      if (activeTheme) {
        applyTheme(activeTheme, activeTheme.name ?? "cached theme");
      }
      instance.renderUpdate(wasmHandle);
      writeState({ wasmHandle, needsRender: true });
      handleSearchWasmReset();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`restty error: ${message}`);
    }
  }
  async function init() {
    cancelAnimationFrame(internalState.rafId);
    updateSize();
    log("initializing...");
    await ensureFont();
    updateGrid();
    const wasmPromise = initWasmHarness();
    const shared = readState();
    if (internalState.preferredRenderer !== "webgl2") {
      if (shared.currentContextType === "webgl2") {
        replaceCanvas();
      }
      const canvas = getCanvas();
      const gpuCore = await session.getWebGPUCore(canvas);
      const gpuState = gpuCore ? await initWebGPU(canvas, { core: gpuCore }) : null;
      if (gpuState) {
        internalState.backend = "webgpu";
        writeState({
          activeState: gpuState,
          currentContextType: "webgpu",
          needsRender: true
        });
        if (backendEl)
          backendEl.textContent = "webgpu";
        callbacks?.onBackend?.("webgpu");
        log("webgpu ready");
        clearWebGLShaderStages();
        destroyWebGLStageTargets();
        gpuState.context.configure({
          device: gpuState.device,
          format: gpuState.format,
          alphaMode: "opaque"
        });
        rebuildWebGPUShaderStages(gpuState);
        setShaderStagesDirty(false);
        updateGrid();
        await wasmPromise;
        internalState.rafId = requestAnimationFrame(() => loop(gpuState));
        return;
      }
    }
    if (internalState.preferredRenderer !== "webgpu") {
      const nextShared = readState();
      if (nextShared.currentContextType === "webgpu") {
        replaceCanvas();
      }
      const canvas = getCanvas();
      const glState = initWebGL(canvas);
      if (glState) {
        internalState.backend = "webgl2";
        writeState({
          activeState: glState,
          currentContextType: "webgl2",
          needsRender: true
        });
        if (backendEl)
          backendEl.textContent = "webgl2";
        callbacks?.onBackend?.("webgl2");
        log("webgl2 ready");
        clearWebGPUShaderStages();
        destroyWebGPUStageTargets();
        rebuildWebGLShaderStages(glState);
        setShaderStagesDirty(false);
        updateGrid();
        await wasmPromise;
        internalState.rafId = requestAnimationFrame(() => loop(glState));
        return;
      }
    }
    internalState.backend = "none";
    if (backendEl)
      backendEl.textContent = "none";
    callbacks?.onBackend?.("none");
    log("no GPU backend available");
    writeState({ activeState: null });
    await wasmPromise;
  }
  function destroy() {
    cancelAnimationFrame(internalState.rafId);
    internalState.rafId = 0;
    internalState.backend = "none";
    internalState.frameCount = 0;
    lifecycleThemeSizeRuntime.cancelScheduledSizeUpdate();
    ptyInputRuntime.cancelSyncOutputReset();
    ptyInputRuntime.disconnectPty();
    ptyTransport.destroy?.();
    const activeState = readState().activeState;
    const shared = readState();
    if (shared.wasm && shared.wasmHandle) {
      try {
        shared.wasm.destroy(shared.wasmHandle);
      } catch {
      }
      writeState({ wasmHandle: 0 });
    }
    clearWebGPUShaderStages();
    destroyWebGPUStageTargets();
    if (activeState && "gl" in activeState) {
      clearWebGLShaderStages(activeState);
      destroyWebGLStageTargets(activeState);
    } else {
      clearWebGLShaderStages();
      destroyWebGLStageTargets();
    }
    for (const cleanup of cleanupCanvasFns)
      cleanup();
    cleanupCanvasFns.length = 0;
    for (const cleanup of cleanupFns)
      cleanup();
    cleanupFns.length = 0;
    options.destroyActiveRenderer?.(activeState);
    const canvas = getCanvas();
    canvas.width = 0;
    canvas.height = 0;
    writeState({
      wasm: null,
      wasmExports: null,
      wasmHandle: 0,
      wasmReady: false,
      activeState: null,
      needsRender: false,
      lastRenderTime: 0,
      currentContextType: null,
      isFocused: false,
      lastKeydownSeq: "",
      lastKeydownSeqAt: 0
    });
  }
  function setRenderer(value) {
    if (value !== "auto" && value !== "webgpu" && value !== "webgl2")
      return;
    internalState.preferredRenderer = value;
    init();
  }
  function setPaused(value) {
    internalState.paused = Boolean(value);
  }
  function togglePause() {
    internalState.paused = !internalState.paused;
  }
  function setMouseMode(value) {
    inputHandler.setMouseMode(value);
    ptyInputRuntime.updateMouseStatus();
  }
  function getMouseStatus() {
    return inputHandler.getMouseStatus();
  }
  function createPublicApi(publicApiOptions) {
    ptyInputRuntime.setPtyStatus("disconnected");
    ptyInputRuntime.updateMouseStatus();
    return {
      init,
      destroy,
      setRenderer,
      setPaused,
      togglePause,
      setFontSize: publicApiOptions.setFontSize,
      setFontHinting: publicApiOptions.setFontHinting,
      setFontHintTarget: publicApiOptions.setFontHintTarget,
      setFontSources: publicApiOptions.setFontSources,
      applyTheme,
      resetTheme: publicApiOptions.resetTheme,
      sendInput,
      sendKeyInput: ptyInputRuntime.sendKeyInput,
      clearScreen,
      loadBinarySnapshot,
      getColorForeground,
      getColorBackground,
      getColorCursor,
      getPaletteColor,
      getPalette,
      connectPty: ptyInputRuntime.connectPty,
      disconnectPty: ptyInputRuntime.disconnectPty,
      isPtyConnected: () => ptyTransport.isConnected(),
      setMouseMode,
      getMouseStatus,
      copySelectionToClipboard,
      pasteFromClipboard: pasteFromClipboard2,
      setSearchQuery: publicApiOptions.setSearchQuery,
      clearSearch: publicApiOptions.clearSearch,
      searchNext: publicApiOptions.searchNext,
      searchPrevious: publicApiOptions.searchPrevious,
      getSearchState: publicApiOptions.getSearchState,
      dumpAtlasForCodepoint: publicApiOptions.dumpAtlasForCodepoint,
      resize: publicApiOptions.resize,
      focus: publicApiOptions.focus,
      blur: publicApiOptions.blur,
      updateSize: publicApiOptions.updateSize,
      getBackend: () => internalState.backend,
      setShaderStages: publicApiOptions.setShaderStages,
      getShaderStages: publicApiOptions.getShaderStages
    };
  }
  return {
    sendInput,
    sendInputBytes,
    createPublicApi
  };
}

// src/runtime/create-runtime.ts
var FALLBACK_LOCAL_FONT_SOURCES = [
  {
    type: "local",
    matchers: [
      "jetbrainsmono nerd font",
      "jetbrains mono nerd font",
      "fira code nerd font",
      "fira code nerd",
      "hack nerd font",
      "meslo lgm nerd font",
      "monaspace nerd font",
      "nerd font mono"
    ],
    label: "fallback-nerd-font"
  },
  {
    type: "local",
    matchers: ["jetbrains mono"],
    label: "fallback-jetbrains"
  }
];
function createResttyApp(options) {
  const { canvas: canvasInput, imeInput: imeInputInput, elements, callbacks } = options;
  const beforeInputHook = options.beforeInput;
  const beforeRenderOutputHook = options.beforeRenderOutput;
  const { runBeforeInputHook, runBeforeRenderOutputHook, runBeforeRenderOutputBytesHook } = createRuntimeInputHooks({
    beforeInputHook,
    beforeRenderOutputHook
  });
  const session = options.session ?? getDefaultResttyAppSession();
  const fontResourceStore = session.getFontResourceStore?.() ?? createResttyFontResourceStore();
  const textShaper = exports_dist;
  if (!canvasInput) {
    throw new Error("createResttyApp requires a canvas element");
  }
  const {
    UnicodeBuffer,
    shape,
    glyphBufferToShapedGlyphs,
    buildAtlas,
    atlasToRGBA,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    PixelMode
  } = textShaper;
  const attachWindowEvents = options.attachWindowEvents ?? true;
  const attachCanvasEvents = options.attachCanvasEvents ?? true;
  const autoResize = options.autoResize ?? true;
  const debugExpose = options.debugExpose ?? false;
  const touchSelectionMode = normalizeTouchSelectionMode(options.touchSelectionMode);
  const touchSelectionLongPressMs = clampFiniteNumber(options.touchSelectionLongPressMs, 450, 120, 2000, true);
  const touchSelectionMoveThresholdPx = clampFiniteNumber(options.touchSelectionMoveThresholdPx, 10, 1, 64);
  const resolveFontHintTarget = (value) => {
    if (value === "light" || value === "normal" || value === "auto")
      return value;
    return "auto";
  };
  let fontHinting = options.fontHinting ?? false;
  let fontHintTarget = resolveFontHintTarget(options.fontHintTarget);
  const nerdIconScale = Number.isFinite(options.nerdIconScale) ? Number(options.nerdIconScale) : 1;
  const alphaBlending = options.alphaBlending ?? "linear-corrected";
  const cleanupFns = [];
  const cleanupCanvasFns = [];
  let canvas = canvasInput;
  let currentContextType = null;
  const backendEl = elements?.backendEl ?? null;
  const fpsEl = elements?.fpsEl ?? null;
  const dprEl = elements?.dprEl ?? null;
  const sizeEl = elements?.sizeEl ?? null;
  const gridEl = elements?.gridEl ?? null;
  const cellEl = elements?.cellEl ?? null;
  const termSizeEl = elements?.termSizeEl ?? null;
  const cursorPosEl = elements?.cursorPosEl ?? null;
  const inputDebugEl = elements?.inputDebugEl ?? null;
  const dbgEl = elements?.dbgEl ?? null;
  const ptyStatusEl = elements?.ptyStatusEl ?? null;
  const mouseStatusEl = elements?.mouseStatusEl ?? null;
  const termDebug = elements?.termDebugEl ?? null;
  const atlasInfoEl = elements?.atlasInfoEl ?? null;
  const atlasCanvas = elements?.atlasCanvas ?? null;
  const logEl = elements?.logEl ?? null;
  const DEFAULT_BG_BASE = [0.08, 0.09, 0.1, 1];
  const DEFAULT_FG_BASE = [0.92, 0.93, 0.95, 1];
  const SELECTION_BACKGROUND_BASE = {
    kind: "color",
    color: [0.35, 0.55, 0.9, 0.45]
  };
  const SELECTION_FOREGROUND_BASE = null;
  const SEARCH_MATCH_BACKGROUND_BASE = {
    kind: "color",
    color: [1, 224 / 255, 130 / 255, 1]
  };
  const SEARCH_CURRENT_MATCH_BACKGROUND_BASE = {
    kind: "color",
    color: [242 / 255, 165 / 255, 126 / 255, 1]
  };
  const SEARCH_MATCH_TEXT_BASE = {
    kind: "color",
    color: [0, 0, 0, 1]
  };
  const SEARCH_CURRENT_MATCH_TEXT_BASE = {
    kind: "color",
    color: [0, 0, 0, 1]
  };
  const CURSOR_BASE = [0.95, 0.95, 0.95, 1];
  let defaultBg = [...DEFAULT_BG_BASE];
  let defaultFg = [...DEFAULT_FG_BASE];
  let selectionBackgroundColor = SELECTION_BACKGROUND_BASE;
  let selectionForegroundColor = SELECTION_FOREGROUND_BASE;
  let searchMatchBackgroundColor = SEARCH_MATCH_BACKGROUND_BASE;
  let searchCurrentMatchBackgroundColor = SEARCH_CURRENT_MATCH_BACKGROUND_BASE;
  let searchMatchTextColor = SEARCH_MATCH_TEXT_BASE;
  let searchCurrentMatchTextColor = SEARCH_CURRENT_MATCH_TEXT_BASE;
  let cursorFallback = [...CURSOR_BASE];
  const CURSOR_BLINK_MS = 600;
  const FORCE_CURSOR_BLINK = false;
  const STYLE_BOLD = 1 << 0;
  const STYLE_ITALIC = 1 << 1;
  const STYLE_FAINT = 1 << 2;
  const STYLE_BLINK = 1 << 3;
  const STYLE_INVERSE = 1 << 4;
  const STYLE_INVISIBLE = 1 << 5;
  const STYLE_STRIKE = 1 << 6;
  const STYLE_OVERLINE = 1 << 7;
  const STYLE_UNDERLINE_MASK = 1792;
  const ITALIC_SLANT = 0.2;
  const BOLD_BRIGHTEN = 0.18;
  const BOLD_OFFSET = 0.06;
  const FAINT_ALPHA = 0.6;
  const TARGET_RENDER_FPS = 120;
  const BACKGROUND_RENDER_FPS = 15;
  const GLYPH_SHAPE_CACHE_LIMIT = 12000;
  const FONT_PICK_CACHE_LIMIT = 16000;
  let currentDpr = window.devicePixelRatio || 1;
  let wasm = null;
  let wasmExports = null;
  let wasmHandle = 0;
  let wasmReady = false;
  let activeState = null;
  function destroyGlyphAtlases(state2) {
    if (!state2?.glyphAtlases)
      return;
    if ("gl" in state2) {
      for (const atlas of state2.glyphAtlases.values()) {
        try {
          state2.gl.deleteTexture(atlas.texture);
        } catch {
        }
      }
      state2.glyphAtlases.clear();
      return;
    }
    for (const atlas of state2.glyphAtlases.values()) {
      try {
        atlas.texture.destroy();
      } catch {
      }
    }
    state2.glyphAtlases.clear();
  }
  const RESIZE_OVERLAY_HOLD_MS = 500;
  const RESIZE_OVERLAY_FADE_MS = 400;
  const RESIZE_ACTIVE_MS = 180;
  const resizeState = {
    active: false,
    lastAt: 0,
    cols: 0,
    rows: 0,
    dpr: 1
  };
  let needsRender = true;
  let lastRenderTime = 0;
  const KEYDOWN_BEFOREINPUT_DEDUPE_MS = 80;
  let lastKeydownSeq = "";
  let lastKeydownSeqAt = 0;
  let runtimeAppApi = null;
  function sendInput(text2, source = "program", config = {}) {
    runtimeAppApi?.sendInput(text2, source, config);
  }
  function sendInputBytes(data) {
    runtimeAppApi?.sendInputBytes(data);
  }
  const ptyTransport = options.ptyTransport ?? createWebSocketPtyTransport();
  const PTY_OUTPUT_IDLE_MS = 10;
  const PTY_OUTPUT_MAX_MS = 40;
  const SYNC_OUTPUT_RESET_MS = 1000;
  const SYNC_OUTPUT_RESET_SEQ = "\x1B[?2026l";
  const ptyOutputBuffer = createPtyOutputBufferController({
    idleMs: PTY_OUTPUT_IDLE_MS,
    maxMs: PTY_OUTPUT_MAX_MS,
    onFlush: (output) => sendInput(output, "pty"),
    onFlushBytes: (output) => sendInputBytes(output)
  });
  let lastCursorForCpr = { row: 1, col: 1 };
  function resolveCursorForCpr() {
    if (wasmHandle && wasmExports?.restty_active_cursor_x && wasmExports?.restty_active_cursor_y) {
      const activeCol = wasmExports.restty_active_cursor_x(wasmHandle);
      const activeRow = wasmExports.restty_active_cursor_y(wasmHandle);
      const cols = lastRenderState?.cols ?? 0;
      const rows = lastRenderState?.rows ?? 0;
      const inBounds = cols > 0 && rows > 0 && Number.isFinite(activeCol) && Number.isFinite(activeRow) && activeCol >= 0 && activeRow >= 0 && activeCol < cols && activeRow < rows;
      if (inBounds) {
        lastCursorForCpr = {
          row: Math.floor(activeRow) + 1,
          col: Math.floor(activeCol) + 1
        };
      }
    }
    return lastCursorForCpr;
  }
  let inputHandler = null;
  let activeTheme = null;
  const webgpuUniforms = new Float32Array(8);
  const runtimeLogger = createRuntimeLogger({
    logEl,
    onLog: callbacks?.onLog
  });
  const { appendLog, log, shouldSuppressWasmLog } = runtimeLogger;
  const shaderStageRuntime = createShaderStageRuntime({
    appendLog,
    getCanvasSize: () => ({ width: canvas.width, height: canvas.height }),
    getActiveWebGLState: () => activeState && ("gl" in activeState) ? activeState : null,
    onShaderStagesChanged: () => {
      needsRender = true;
    }
  });
  const {
    setShaderStages,
    getShaderStages,
    isShaderStagesDirty,
    setShaderStagesDirty,
    getCompiledWebGPUShaderStages,
    getCompiledWebGLShaderStages,
    clearWebGPUShaderStages,
    clearWebGLShaderStages,
    destroyWebGPUStageTargets,
    destroyWebGLStageTargets,
    ensureWebGPUStageTargets,
    ensureWebGLStageTargets,
    rebuildWebGPUShaderStages,
    rebuildWebGLShaderStages
  } = shaderStageRuntime;
  const ATLAS_PADDING = 4;
  const SYMBOL_ATLAS_PADDING = 10;
  const SYMBOL_ATLAS_MAX_SIZE = 4096;
  const GLYPH_INSTANCE_FLOATS = 18;
  const GLYPH_RENDER_MODE_MONO = 0;
  const GLYPH_RENDER_MODE_COLOR = 1;
  const KITTY_PLACEHOLDER_CP = 1109742;
  const KITTY_FLAG_REPORT_EVENTS2 = 1 << 1;
  const textEncoder3 = new TextEncoder;
  const textDecoder4 = new TextDecoder;
  let isFocused = typeof document !== "undefined" ? document.activeElement === canvas : true;
  const imeInput = imeInputInput ?? null;
  const isMacPlatform = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
  const fontConfig = {
    sizePx: Number.isFinite(options.fontSize) ? Math.max(1, Math.round(options.fontSize)) : 18
  };
  function configureImeInputElement() {
    if (!imeInput)
      return;
    const style = imeInput.style;
    style.position = "fixed";
    style.left = "0";
    style.top = "0";
    style.width = "1em";
    style.height = "1em";
    style.padding = "0";
    style.margin = "0";
    style.border = "0";
    style.outline = "none";
    style.background = "transparent";
    style.color = "transparent";
    style.caretColor = "transparent";
    style.overflow = "hidden";
    style.resize = "none";
    style.opacity = "0";
    style.pointerEvents = "none";
    syncImeInputTypography(imeInput, fontConfig.sizePx);
  }
  configureImeInputElement();
  let lastRenderState = null;
  const runtimeInteraction = createRuntimeInteraction({
    attachCanvasEvents,
    touchSelectionMode,
    touchSelectionLongPressMs,
    touchSelectionMoveThresholdPx,
    imeInput,
    cleanupCanvasFns,
    getCanvas: () => canvas,
    getCurrentDpr: () => currentDpr,
    getGridState: () => gridState,
    getLastRenderState: () => lastRenderState,
    getWasmReady: () => wasmReady,
    getWasm: () => wasm,
    getWasmHandle: () => wasmHandle,
    getWasmExports: () => wasmExports,
    updateLinkHover,
    markNeedsRender: () => {
      needsRender = true;
    },
    markSearchDirty: () => {
      searchRuntime.markDirty();
    }
  });
  const {
    selectionState,
    linkState,
    scrollbarState,
    imeState,
    updateCanvasCursor,
    positionToCell: positionToCell2,
    positionToPixel,
    clearSelection: clearSelection2,
    updateImePosition: updateImePosition2,
    syncScrollbar,
    bindCanvasEvents: bindCanvasInteractionEvents
  } = runtimeInteraction;
  const searchRuntime = createRuntimeSearch({
    callbacks,
    cleanupFns,
    getWasmReady: () => wasmReady,
    getWasm: () => wasm,
    getWasmHandle: () => wasmHandle,
    markNeedsRender: () => {
      needsRender = true;
    }
  });
  const kittyRenderRuntime = createKittyRenderRuntime({
    getWasm: () => wasm,
    markNeedsRender: () => {
      needsRender = true;
    }
  });
  const {
    selectionForRow: selectionForRow2,
    getSelectionText: getSelectionText2,
    getRenderState,
    resolveCursorPosition,
    resolveCursorStyle,
    reportTermSize,
    reportCursor,
    reportDebugText
  } = createRuntimeReporting({
    selectionState,
    getLastRenderState: () => lastRenderState,
    getWasmReady: () => wasmReady,
    getWasm: () => wasm,
    getWasmHandle: () => wasmHandle,
    getWasmExports: () => wasmExports,
    callbacks,
    termSizeEl,
    cursorPosEl,
    dbgEl,
    setCursorForCpr: (value) => {
      lastCursorForCpr = value;
    }
  });
  inputHandler = createInputHandler({
    getCursorPosition: resolveCursorForCpr,
    sendReply: (data) => {
      ptyTransport.sendInput(data);
    },
    positionToCell: positionToCell2,
    positionToPixel,
    getDefaultColors: () => ({
      fg: floatsToRgb(defaultFg),
      bg: floatsToRgb(defaultBg),
      cursor: floatsToRgb(cursorFallback)
    }),
    getWindowMetrics: () => {
      const cols = Math.max(1, gridState.cols || 1);
      const rows = Math.max(1, gridState.rows || 1);
      const widthPx = Math.max(1, Math.round(canvas.width));
      const heightPx = Math.max(1, Math.round(canvas.height));
      const cellWidthPx = Math.max(1, Math.round(gridState.cellW || widthPx / Math.max(1, cols)));
      const cellHeightPx = Math.max(1, Math.round(gridState.cellH || heightPx / Math.max(1, rows)));
      return { rows, cols, widthPx, heightPx, cellWidthPx, cellHeightPx };
    },
    onClipboardWrite: async (text2) => {
      if (!text2)
        return;
      await copyToClipboard(text2);
    },
    onClipboardRead: async () => {
      return await pasteFromClipboard() ?? "";
    },
    getKittyKeyboardFlags: () => {
      if (!wasm || !wasmHandle)
        return 0;
      return wasm.getKittyKeyboardFlags(wasmHandle);
    },
    onWindowOp: (op) => {
      appendLog(`[term] window op ${op.type} ${op.params.join(";")}`);
    },
    onDesktopNotification: callbacks?.onDesktopNotification
  });
  inputHandler.setMouseMode("auto");
  const ptyInputRuntime = createPtyInputRuntime({
    ptyTransport,
    ptyOutputBuffer,
    inputHandler,
    ptyStatusEl,
    mouseStatusEl,
    onPtyStatus: callbacks?.onPtyStatus,
    onMouseStatus: callbacks?.onMouseStatus,
    appendLog,
    getGridSize: () => ({ cols: gridState.cols || 0, rows: gridState.rows || 0 }),
    getResizeMeta: () => {
      const cols = gridState.cols || 0;
      const rows = gridState.rows || 0;
      if (cols <= 0 || rows <= 0)
        return null;
      return {
        widthPx: canvas.width,
        heightPx: canvas.height,
        cellW: gridState.cellW,
        cellH: gridState.cellH
      };
    },
    getCursorForCpr: resolveCursorForCpr,
    sendInput,
    runBeforeInputHook,
    shouldClearSelection: () => selectionState.active || selectionState.dragging,
    clearSelection: clearSelection2,
    syncOutputResetMs: SYNC_OUTPUT_RESET_MS,
    syncOutputResetSeq: SYNC_OUTPUT_RESET_SEQ
  });
  const { sendKeyInput, sendPasteText } = ptyInputRuntime;
  const { sendPastePayloadFromDataTransfer, getCprPosition } = ptyInputRuntime;
  inputHandler.setCursorProvider(getCprPosition);
  function bindCanvasEvents() {
    bindCanvasInteractionEvents({
      inputHandler,
      sendKeyInput,
      sendPasteText,
      sendPastePayloadFromDataTransfer,
      getLastKeydownSeq: () => lastKeydownSeq,
      getLastKeydownSeqAt: () => lastKeydownSeqAt,
      keydownBeforeinputDedupeMs: KEYDOWN_BEFOREINPUT_DEDUPE_MS,
      openLink
    });
  }
  bindCanvasEvents();
  const fontState = {
    font: null,
    fonts: [],
    fontSizePx: 0,
    sizeMode: options.fontSizeMode === "em" ? "em" : "height",
    fontPickCache: new Map
  };
  const FONT_SCALE_OVERRIDES = options.fontScaleOverrides ?? [];
  function applyFontSize(value) {
    if (!Number.isFinite(value))
      return;
    const clamped = Math.max(10, Math.min(64, Math.round(value)));
    if (fontConfig.sizePx === clamped)
      return;
    fontConfig.sizePx = clamped;
    syncImeInputTypography(imeInput, clamped);
    for (const entry of fontState.fonts)
      resetFontEntry(entry);
    destroyGlyphAtlases(activeState);
    updateGrid();
    wasm?.renderUpdate?.(wasmHandle);
    needsRender = true;
    appendLog(`[ui] font size ${clamped}px`);
  }
  const resolveGlyphPixelMode2 = (entry) => resolveGlyphPixelMode(entry, PixelMode.Gray, PixelMode.RGBA ?? 4, isColorEmojiFont);
  const {
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    buildColorEmojiAtlasWithCanvas,
    destroy: destroyColorGlyphCanvas
  } = createColorGlyphAtlasHelpers({
    pixelModeRgba: PixelMode.RGBA ?? 4,
    atlasToRGBA
  });
  let configuredFontSources = normalizeFontSources(options.fontSources, options.fontPreset);
  const gridState = {
    cols: 0,
    rows: 0,
    cellW: 0,
    cellH: 0,
    fontSizePx: 0,
    scale: 1,
    lineHeight: 0,
    baselineOffset: 0,
    yPad: 0
  };
  const {
    shapeClusterWithFont,
    noteColorGlyphText,
    fontHasGlyph: fontHasGlyph2,
    pickFontIndexForText: pickFontIndexForText2,
    computeCellMetrics: computeCellMetrics2,
    updateGrid,
    ensureAtlasForFont
  } = createRuntimeFontRuntimeHelpers({
    fontState,
    fontConfig,
    gridState,
    callbacks,
    gridEl,
    cellEl,
    getCanvas: () => canvas,
    getCurrentDpr: () => currentDpr,
    getActiveState: () => activeState,
    getWasmReady: () => wasmReady,
    getWasm: () => wasm,
    getWasmHandle: () => wasmHandle,
    ptyTransport,
    setNeedsRender: () => {
      needsRender = true;
    },
    markSearchDirty: () => {
      searchRuntime.markDirty();
    },
    getFontHinting: () => fontHinting,
    getFontHintTarget: () => fontHintTarget,
    fontScaleOverrides: FONT_SCALE_OVERRIDES,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA,
    buildAtlas,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    pixelModeRgbaValue: PixelMode.RGBA ?? 4,
    atlasPadding: ATLAS_PADDING,
    symbolAtlasPadding: SYMBOL_ATLAS_PADDING,
    symbolAtlasMaxSize: SYMBOL_ATLAS_MAX_SIZE,
    glyphShapeCacheLimit: GLYPH_SHAPE_CACHE_LIMIT,
    fontPickCacheLimit: FONT_PICK_CACHE_LIMIT,
    UnicodeBuffer,
    shape,
    glyphBufferToShapedGlyphs
  });
  const { dumpAtlasForCodepoint, setupDebugExpose } = createRuntimeDebugTools({
    debugExpose,
    getWindow: () => typeof window !== "undefined" ? window : undefined,
    getActiveState: () => activeState,
    getCanvas: () => canvas,
    atlasCanvas,
    atlasInfoEl,
    fontState,
    gridState,
    fontConfig,
    pickFontIndexForText: pickFontIndexForText2,
    ensureAtlasForFont,
    formatCodepoint,
    isSymbolFont,
    isNerdSymbolCodepoint,
    isSymbolCp,
    fontHasGlyph: fontHasGlyph2,
    shapeClusterWithFont,
    getNerdConstraint,
    fontHeightUnits,
    fontScaleOverride,
    fontScaleOverrides: FONT_SCALE_OVERRIDES,
    fontAdvanceUnits,
    fontMaxCellSpan,
    clamp,
    buildNerdMetrics,
    nerdIconScale,
    isColorEmojiFont,
    atlasPadding: ATLAS_PADDING,
    symbolAtlasPadding: SYMBOL_ATLAS_PADDING,
    pixelModeGray: PixelMode.Gray,
    pixelModeRgba: PixelMode.RGBA ?? 4
  });
  setupDebugExpose();
  let fontPromise = null;
  let fontError = null;
  let fontLease = null;
  setShaderStages(options.shaderStages ?? []);
  const lifecycleThemeSizeRuntime = createRuntimeLifecycleThemeSize({
    attachCanvasEvents,
    attachWindowEvents,
    autoResize,
    imeInput,
    dprEl,
    sizeEl,
    callbacks,
    cleanupFns,
    cleanupCanvasFns,
    gridState,
    resizeState,
    fontState,
    defaultBgBase: DEFAULT_BG_BASE,
    defaultFgBase: DEFAULT_FG_BASE,
    selectionBackgroundBase: SELECTION_BACKGROUND_BASE,
    selectionForegroundBase: SELECTION_FOREGROUND_BASE,
    searchMatchBackgroundBase: SEARCH_MATCH_BACKGROUND_BASE,
    searchCurrentMatchBackgroundBase: SEARCH_CURRENT_MATCH_BACKGROUND_BASE,
    searchMatchTextBase: SEARCH_MATCH_TEXT_BASE,
    searchCurrentMatchTextBase: SEARCH_CURRENT_MATCH_TEXT_BASE,
    cursorBase: CURSOR_BASE,
    getCanvas: () => canvas,
    setCanvas: (nextCanvas) => canvas = nextCanvas,
    getCurrentDpr: () => currentDpr,
    setCurrentDpr: (dpr) => currentDpr = dpr,
    setCurrentContextType: (value) => currentContextType = value,
    getActiveState: () => activeState,
    getInputHandler: () => inputHandler,
    setIsFocused: (value) => isFocused = value,
    getActiveTheme: () => activeTheme,
    setActiveTheme: (theme) => activeTheme = theme,
    setDefaultBg: (value) => defaultBg = value,
    setDefaultFg: (value) => defaultFg = value,
    setSelectionBackgroundColor: (value) => selectionBackgroundColor = value,
    setSelectionForegroundColor: (value) => selectionForegroundColor = value,
    setSearchMatchBackgroundColor: (value) => searchMatchBackgroundColor = value,
    setSearchCurrentMatchBackgroundColor: (value) => searchCurrentMatchBackgroundColor = value,
    setSearchMatchTextColor: (value) => searchMatchTextColor = value,
    setSearchCurrentMatchTextColor: (value) => searchCurrentMatchTextColor = value,
    setCursorFallback: (value) => cursorFallback = value,
    getWasmReady: () => wasmReady,
    getWasm: () => wasm,
    getWasmHandle: () => wasmHandle,
    appendLog,
    bindCanvasEvents,
    computeCellMetrics: computeCellMetrics2,
    updateGrid,
    clearKittyRenderCaches: kittyRenderRuntime.clearKittyRenderCaches,
    sendKeyInput,
    clearWebGLShaderStages,
    destroyWebGLStageTargets,
    destroyWebGPUStageTargets,
    setShaderStagesDirty,
    markNeedsRender: () => needsRender = true,
    resetLastRenderTime: () => lastRenderTime = 0
  });
  const {
    applyTheme,
    resetTheme,
    replaceCanvas,
    updateSize,
    resize,
    focus,
    blur,
    bindFocusEvents
  } = lifecycleThemeSizeRuntime;
  bindFocusEvents();
  lifecycleThemeSizeRuntime.bindAutoResizeEvents();
  function destroyWebGPUAtlasState(state2) {
    destroyGlyphAtlases(state2);
  }
  function destroyWebGLAtlasState(state2) {
    destroyGlyphAtlases(state2);
  }
  function destroyWebGPUState(state2) {
    destroyWebGPUAtlasState(state2);
    try {
      state2.uniformBuffer.destroy();
    } catch {
    }
    try {
      state2.rectInstanceBuffer.destroy();
    } catch {
    }
    try {
      state2.glyphInstanceBuffer.destroy();
    } catch {
    }
    try {
      state2.context.unconfigure?.();
    } catch {
    }
  }
  function destroyWebGLState(state2) {
    destroyWebGLAtlasState(state2);
    try {
      state2.gl.bindVertexArray(null);
      state2.gl.bindBuffer(state2.gl.ARRAY_BUFFER, null);
      state2.gl.bindFramebuffer(state2.gl.FRAMEBUFFER, null);
      state2.gl.useProgram(null);
    } catch {
    }
    try {
      state2.gl.deleteVertexArray(state2.rectVao);
    } catch {
    }
    try {
      state2.gl.deleteVertexArray(state2.glyphVao);
    } catch {
    }
    try {
      state2.gl.deleteBuffer(state2.quadBuffer);
    } catch {
    }
    try {
      state2.gl.deleteBuffer(state2.rectInstanceBuffer);
    } catch {
    }
    try {
      state2.gl.deleteBuffer(state2.glyphInstanceBuffer);
    } catch {
    }
    try {
      state2.gl.deleteProgram(state2.rectProgram);
    } catch {
    }
    try {
      state2.gl.deleteProgram(state2.glyphProgram);
    } catch {
    }
    try {
      state2.gl.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
    }
  }
  function destroyActiveRendererState(state2) {
    if (!state2)
      return;
    if ("gl" in state2) {
      destroyWebGLState(state2);
      return;
    }
    destroyWebGPUState(state2);
  }
  function resolveLinkUri(render, linkId) {
    if (!render.linkOffsets || !render.linkLengths || !render.linkBuffer)
      return "";
    if (!linkId)
      return "";
    const idx = linkId - 1;
    const offset = render.linkOffsets[idx] ?? 0;
    const len = render.linkLengths[idx] ?? 0;
    if (!len)
      return "";
    return textDecoder4.decode(render.linkBuffer.subarray(offset, offset + len));
  }
  function updateLinkHover(cell) {
    if (!lastRenderState || !cell || !lastRenderState.linkIds) {
      if (linkState.hoverId !== 0) {
        linkState.hoverId = 0;
        linkState.hoverUri = "";
        updateCanvasCursor();
        needsRender = true;
      }
      return;
    }
    const { cols, rows, linkIds } = lastRenderState;
    if (cell.row < 0 || cell.col < 0 || cell.row >= rows || cell.col >= cols) {
      if (linkState.hoverId !== 0) {
        linkState.hoverId = 0;
        linkState.hoverUri = "";
        updateCanvasCursor();
        needsRender = true;
      }
      return;
    }
    const idx = cell.row * cols + cell.col;
    const linkId = linkIds[idx] ?? 0;
    if (linkId === linkState.hoverId)
      return;
    if (!linkId) {
      linkState.hoverId = 0;
      linkState.hoverUri = "";
      updateCanvasCursor();
      needsRender = true;
      return;
    }
    linkState.hoverId = linkId;
    linkState.hoverUri = resolveLinkUri(lastRenderState, linkId);
    updateCanvasCursor();
    needsRender = true;
  }
  function releaseFontLease() {
    if (!fontLease)
      return;
    fontLease.release();
    fontLease = null;
  }
  function clearFontRuntimeState() {
    for (let i3 = 0;i3 < fontState.fonts.length; i3 += 1) {
      resetFontEntry(fontState.fonts[i3]);
    }
    fontState.font = null;
    fontState.fonts = [];
    fontState.fontSizePx = 0;
    fontState.fontPickCache.clear();
    destroyGlyphAtlases(activeState);
  }
  async function setFontSources(sources2) {
    configuredFontSources = normalizeFontSources(sources2, undefined);
    fontPromise = null;
    fontError = null;
    releaseFontLease();
    clearFontRuntimeState();
    await ensureFont();
    updateGrid();
    needsRender = true;
    appendLog("[ui] font sources updated");
  }
  function invalidateFontAtlasesForHinting() {
    for (let i3 = 0;i3 < fontState.fonts.length; i3 += 1) {
      const entry = fontState.fonts[i3];
      entry.atlas = null;
      entry.glyphIds.clear();
      entry.fontSizePx = 0;
      entry.atlasScale = 1;
      entry.constraintSignature = "";
    }
    destroyGlyphAtlases(activeState);
    needsRender = true;
  }
  function setFontHinting(value) {
    const next = Boolean(value);
    if (fontHinting === next)
      return;
    fontHinting = next;
    invalidateFontAtlasesForHinting();
    appendLog(`[ui] font hinting ${next ? "on" : "off"}`);
  }
  function setFontHintTarget(value) {
    const next = resolveFontHintTarget(value);
    if (fontHintTarget === next)
      return;
    fontHintTarget = next;
    if (fontHinting) {
      invalidateFontAtlasesForHinting();
    }
    appendLog(`[ui] font hint target ${next}`);
  }
  async function ensureFont() {
    if (fontState.font || fontPromise)
      return fontPromise;
    fontPromise = (async () => {
      let acquiredLease = null;
      try {
        acquiredLease = await fontResourceStore.acquire(configuredFontSources);
        if (!acquiredLease.faces.length) {
          acquiredLease.release();
          acquiredLease = await fontResourceStore.acquire(FALLBACK_LOCAL_FONT_SOURCES);
        }
        if (!acquiredLease.faces.length) {
          throw new Error("Unable to load any configured font source.");
        }
        releaseFontLease();
        fontLease = acquiredLease;
        acquiredLease = null;
        const entries2 = [];
        for (let i3 = 0;i3 < fontLease.faces.length; i3 += 1) {
          const face = fontLease.faces[i3];
          entries2.push(createFontEntry(face.font, face.label));
        }
        if (!entries2.length) {
          throw new Error("Unable to parse any loaded font source.");
        }
        fontState.fonts = entries2;
        fontState.font = entries2[0].font;
        fontState.fontSizePx = 0;
        fontState.fontPickCache.clear();
        destroyGlyphAtlases(activeState);
        fontError = null;
        if (entries2.length > 1) {
          log(`font loaded (+${entries2.length - 1} fallback)`);
        } else {
          log("font loaded");
        }
        updateGrid();
      } catch (err) {
        if (acquiredLease) {
          acquiredLease.release();
        } else {
          releaseFontLease();
        }
        clearFontRuntimeState();
        const error = err instanceof Error ? err : new Error(String(err));
        fontError = error;
        console.error("font load error", err);
        log("font load failed");
      }
    })();
    return fontPromise;
  }
  cleanupFns.push(() => {
    releaseFontLease();
    clearFontRuntimeState();
    configuredFontSources = [];
    fontPromise = null;
    fontError = null;
  });
  const { tickWebGPU: tickWebGPU2, tickWebGL: tickWebGL2 } = createRuntimeRenderTicks({
    isShaderStagesDirty,
    rebuildWebGPUShaderStages,
    rebuildWebGLShaderStages,
    setShaderStagesDirty,
    getCompiledWebGPUShaderStages,
    getCompiledWebGLShaderStages,
    ensureWebGPUStageTargets,
    ensureWebGLStageTargets,
    get fontError() {
      return fontError;
    },
    termDebug,
    reportDebugText,
    updateGrid,
    getRenderState,
    fontState,
    resolveBlendFlags,
    alphaBlending,
    srgbToLinearColor,
    get defaultBg() {
      return defaultBg;
    },
    reportTermSize,
    resolveCursorPosition,
    reportCursor,
    FORCE_CURSOR_BLINK,
    CURSOR_BLINK_MS,
    imeInput,
    resolveCursorStyle,
    get isFocused() {
      return isFocused;
    },
    imeState,
    resolveImeAnchor,
    dbgEl,
    get wasmExports() {
      return wasmExports;
    },
    get wasmHandle() {
      return wasmHandle;
    },
    gridState,
    get canvas() {
      return canvas;
    },
    fontHeightUnits,
    updateImePosition: updateImePosition2,
    fontScaleOverride,
    FONT_SCALE_OVERRIDES,
    getFontHinting: () => fontHinting,
    getFontHintTarget: () => fontHintTarget,
    isSymbolFont,
    isColorEmojiFont,
    fontAdvanceUnits,
    shapeClusterWithFont,
    fontMaxCellSpan,
    clamp,
    buildNerdMetrics,
    nerdIconScale,
    selectionState,
    selectionForRow: selectionForRow2,
    getSearchViewportMatches: searchRuntime.getViewportMatches,
    pushRect,
    get selectionBackgroundColor() {
      return selectionBackgroundColor;
    },
    get selectionForegroundColor() {
      return selectionForegroundColor;
    },
    get searchMatchBackgroundColor() {
      return searchMatchBackgroundColor;
    },
    get searchCurrentMatchBackgroundColor() {
      return searchCurrentMatchBackgroundColor;
    },
    get searchMatchTextColor() {
      return searchMatchTextColor;
    },
    get searchCurrentMatchTextColor() {
      return searchCurrentMatchTextColor;
    },
    STYLE_BOLD,
    STYLE_ITALIC,
    STYLE_FAINT,
    STYLE_BLINK,
    STYLE_INVERSE,
    STYLE_INVISIBLE,
    STYLE_STRIKE,
    STYLE_OVERLINE,
    STYLE_UNDERLINE_MASK,
    decodeRGBAWithCache,
    brighten,
    BOLD_BRIGHTEN,
    fade,
    FAINT_ALPHA,
    linkState,
    drawUnderlineStyle,
    drawStrikethrough,
    drawOverline,
    KITTY_PLACEHOLDER_CP,
    isSpaceCp,
    shouldMergeTrailingClusterCodepoint,
    isBlockElement,
    drawBlockElement,
    isBoxDrawing,
    drawBoxDrawing,
    isBraille,
    drawBraille,
    isPowerline,
    drawPowerline,
    pickFontIndexForText: pickFontIndexForText2,
    stylePreferenceFromFlags,
    noteColorGlyphText,
    isRenderSymbolLike,
    resolveSymbolConstraint,
    isGraphicsElement,
    glyphWidthUnits,
    fitTextTailToWidth,
    PREEDIT_BG,
    PREEDIT_UL,
    PREEDIT_ACTIVE_BG,
    PREEDIT_CARET,
    PREEDIT_FG,
    resizeState,
    RESIZE_OVERLAY_HOLD_MS,
    RESIZE_OVERLAY_FADE_MS,
    pushRectBox,
    ensureAtlasForFont,
    isAppleSymbolsFont,
    DEFAULT_APPLE_SYMBOLS_CONSTRAINT,
    DEFAULT_SYMBOL_CONSTRAINT,
    DEFAULT_EMOJI_CONSTRAINT,
    constrainGlyphBox,
    tightenNerdConstraintBox,
    fontEntryHasItalicStyle,
    fontEntryHasBoldStyle,
    ITALIC_SLANT,
    BOLD_OFFSET,
    GLYPH_RENDER_MODE_COLOR,
    GLYPH_RENDER_MODE_MONO,
    decodePackedRGBA,
    get cursorFallback() {
      return cursorFallback;
    },
    scrollbarState,
    syncScrollbar,
    webgpuUniforms,
    ensureInstanceBuffer,
    GLYPH_INSTANCE_FLOATS,
    get wasm() {
      return wasm;
    },
    collectKittyDrawPlan: kittyRenderRuntime.collectKittyDrawPlan,
    resolveKittyWebGLTexture: kittyRenderRuntime.resolveKittyWebGLTexture,
    resolveKittyWebGPUBindGroup: kittyRenderRuntime.resolveKittyWebGPUBindGroup,
    buildFontAtlasIfNeeded,
    resolveGlyphPixelMode: resolveGlyphPixelMode2,
    atlasBitmapToRGBA: atlasBitmapToRGBA2,
    padAtlasRGBA,
    buildAtlas,
    buildGlyphAtlasWithConstraints,
    buildColorEmojiAtlasWithCanvas,
    rasterizeGlyph,
    rasterizeGlyphWithTransform,
    nerdConstraintSignature,
    ATLAS_PADDING,
    SYMBOL_ATLAS_PADDING,
    SYMBOL_ATLAS_MAX_SIZE,
    PixelMode,
    ensureGLInstanceBuffer,
    get lastRenderState() {
      return lastRenderState;
    },
    set lastRenderState(value) {
      lastRenderState = value;
    }
  });
  const readRuntimeAppApiState = () => ({
    wasm,
    wasmExports,
    wasmHandle,
    wasmReady,
    activeState,
    needsRender,
    lastRenderTime,
    currentContextType,
    isFocused,
    lastKeydownSeq,
    lastKeydownSeqAt
  });
  const writeRuntimeAppApiState = (patch) => {
    ({
      wasm = wasm,
      wasmExports = wasmExports,
      wasmHandle = wasmHandle,
      wasmReady = wasmReady,
      activeState = activeState,
      needsRender = needsRender,
      lastRenderTime = lastRenderTime,
      currentContextType = currentContextType,
      isFocused = isFocused,
      lastKeydownSeq = lastKeydownSeq,
      lastKeydownSeqAt = lastKeydownSeqAt
    } = patch);
  };
  cleanupFns.push(() => {
    destroyColorGlyphCanvas();
    kittyRenderRuntime.clearKittyRenderCaches();
  });
  runtimeAppApi = createRuntimeAppApi({
    session,
    ptyTransport,
    inputHandler,
    ptyInputRuntime,
    interaction: runtimeInteraction,
    lifecycleThemeSizeRuntime,
    cleanupFns,
    cleanupCanvasFns,
    callbacks,
    fpsEl,
    backendEl,
    inputDebugEl,
    imeInput,
    attachWindowEvents,
    isMacPlatform,
    textEncoder: textEncoder3,
    readState: readRuntimeAppApiState,
    writeState: writeRuntimeAppApiState,
    appendLog,
    shouldSuppressWasmLog,
    runBeforeInputHook,
    runBeforeRenderOutputHook,
    runBeforeRenderOutputBytesHook,
    CURSOR_BLINK_MS,
    RESIZE_ACTIVE_MS,
    TARGET_RENDER_FPS,
    BACKGROUND_RENDER_FPS,
    KITTY_FLAG_REPORT_EVENTS: KITTY_FLAG_REPORT_EVENTS2,
    resizeState,
    tickWebGPU: tickWebGPU2,
    tickWebGL: tickWebGL2,
    updateGrid,
    gridState,
    getCanvas: () => canvas,
    applyTheme,
    ensureFont,
    updateSize,
    log,
    replaceCanvas,
    rebuildWebGPUShaderStages,
    rebuildWebGLShaderStages,
    setShaderStagesDirty,
    clearWebGPUShaderStages,
    destroyWebGPUStageTargets,
    clearWebGLShaderStages,
    destroyWebGLStageTargets,
    destroyActiveRenderer: destroyActiveRendererState,
    markSearchDirty: () => {
      searchRuntime.markDirty();
    },
    handleSearchWasmReset: () => {
      searchRuntime.handleWasmReset();
    },
    getSelectionText: getSelectionText2,
    initialPreferredRenderer: options.renderer ?? "auto",
    maxScrollbackBytes: options.maxScrollbackBytes,
    maxScrollback: options.maxScrollback,
    readOnly: options.readOnly
  });
  return runtimeAppApi.createPublicApi({
    setFontSize: applyFontSize,
    setFontHinting,
    setFontHintTarget,
    setFontSources,
    resetTheme,
    setSearchQuery: searchRuntime.setQuery,
    clearSearch: searchRuntime.clear,
    searchNext: searchRuntime.next,
    searchPrevious: searchRuntime.previous,
    getSearchState: searchRuntime.getState,
    dumpAtlasForCodepoint,
    resize,
    focus,
    blur,
    updateSize,
    setShaderStages,
    getShaderStages
  });
}

// src/surface/app-factory.ts
function createResttyApp2(options) {
  return createResttyApp(options);
}

// src/surface/pane-search-ui.ts
var ROOT_CLASS = "restty-search-ui-root";
var STYLE_MARKER = "data-restty-pane-search-ui-styles";
var STYLE_TEXT = `
.${ROOT_CLASS} .restty-pane-search {
  position: absolute;
  top: var(--restty-search-ui-top, 10px);
  right: var(--restty-search-ui-right, 10px);
  z-index: var(--restty-search-ui-z-index, 8);
  width: min(var(--restty-search-ui-max-width, 332px), calc(100% - 20px));
  min-width: var(--restty-search-ui-min-width, 232px);
  display: none;
  align-items: center;
  padding: 6px;
  border: 1px solid var(--restty-search-ui-border, #2a2a2a);
  border-radius: var(--restty-search-ui-radius, 8px);
  background: var(--restty-search-ui-background, #161616);
  color: var(--restty-search-ui-text, #d6d6d6);
  backdrop-filter: blur(var(--restty-search-ui-blur, 8px));
  box-shadow: var(--restty-search-ui-shadow, 0 14px 40px rgba(0, 0, 0, 0.45));
}

.${ROOT_CLASS} .restty-pane-search[data-open="1"] {
  display: flex;
}

.${ROOT_CLASS} .restty-pane-search-row {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto auto auto auto auto;
  gap: 4px;
  align-items: center;
}

.${ROOT_CLASS} .restty-pane-search-input,
.${ROOT_CLASS} .restty-pane-search-button {
  min-width: 0;
  height: 28px;
  border: 1px solid transparent;
  border-radius: 6px;
  transition:
    background-color 120ms ease-out,
    border-color 120ms ease-out,
    color 120ms ease-out,
    transform 120ms ease-out;
}

.${ROOT_CLASS} .restty-pane-search-input {
  padding: 0 10px;
  background: var(--restty-search-ui-input-background, #252525);
  color: var(--restty-search-ui-input-text, #d6d6d6);
  outline: none;
  font-size: 11px;
  letter-spacing: 0.01em;
}

.${ROOT_CLASS} .restty-pane-search-input::placeholder {
  color: var(--restty-search-ui-input-placeholder, #868686);
}

.${ROOT_CLASS} .restty-pane-search-input:focus {
  border-color: #3a3a3a;
}

.${ROOT_CLASS} .restty-pane-search-button {
  padding: 0 8px;
  background: var(--restty-search-ui-button-background, transparent);
  color: var(--restty-search-ui-button-text, #d6d6d6);
  cursor: pointer;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.${ROOT_CLASS} .restty-pane-search-button:hover:not(:disabled) {
  background: var(--restty-search-ui-button-hover, #252525);
}

.${ROOT_CLASS} .restty-pane-search-button:disabled {
  cursor: default;
  opacity: var(--restty-search-ui-button-disabled-opacity, 0.42);
}

.${ROOT_CLASS} .restty-pane-search-button:focus-visible {
  outline: none;
  border-color: #3a3a3a;
}

.${ROOT_CLASS} .restty-pane-search-status {
  min-width: 0;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 6px;
  background: #252525;
  border: 1px solid #2a2a2a;
  font-size: 10px;
  line-height: 1;
  color: var(--restty-search-ui-status, #868686);
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.${ROOT_CLASS} .restty-pane-search-status[data-empty="1"] {
  display: none;
}

.${ROOT_CLASS} .restty-pane-search-status[data-active="1"] {
  color: var(--restty-search-ui-status-active, #d6d6d6);
}

.${ROOT_CLASS} .restty-pane-search-status[data-complete="1"] {
  color: var(--restty-search-ui-status-complete, #868686);
}
`;
var DEFAULT_STYLE_OPTIONS = {
  offsetTopPx: 10,
  offsetRightPx: 10,
  minWidthPx: 232,
  maxWidthPx: 332,
  zIndex: 8,
  borderRadiusPx: 8,
  backdropBlurPx: 8,
  panelBackground: "#161616",
  panelBorderColor: "#2a2a2a",
  panelTextColor: "#d6d6d6",
  panelShadow: "0 14px 40px rgba(0, 0, 0, 0.45)",
  inputBackground: "#252525",
  inputTextColor: "#d6d6d6",
  inputPlaceholderColor: "#868686",
  buttonBackground: "transparent",
  buttonTextColor: "#d6d6d6",
  buttonHoverBackground: "#252525",
  buttonDisabledOpacity: 0.42,
  statusTextColor: "#868686",
  statusActiveTextColor: "#d6d6d6",
  statusCompleteTextColor: "#868686"
};
function clampNumber2(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function normalizeColor2(value, fallback) {
  if (typeof value !== "string")
    return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}
function normalizeStyleOptions(options) {
  return {
    offsetTopPx: Number.isFinite(options?.offsetTopPx) ? clampNumber2(Number(options?.offsetTopPx), 0, 256) : DEFAULT_STYLE_OPTIONS.offsetTopPx,
    offsetRightPx: Number.isFinite(options?.offsetRightPx) ? clampNumber2(Number(options?.offsetRightPx), 0, 256) : DEFAULT_STYLE_OPTIONS.offsetRightPx,
    minWidthPx: Number.isFinite(options?.minWidthPx) ? clampNumber2(Number(options?.minWidthPx), 160, 800) : DEFAULT_STYLE_OPTIONS.minWidthPx,
    maxWidthPx: Number.isFinite(options?.maxWidthPx) ? clampNumber2(Number(options?.maxWidthPx), 180, 960) : DEFAULT_STYLE_OPTIONS.maxWidthPx,
    zIndex: Number.isFinite(options?.zIndex) ? clampNumber2(Number(options?.zIndex), 1, 9999) : DEFAULT_STYLE_OPTIONS.zIndex,
    borderRadiusPx: Number.isFinite(options?.borderRadiusPx) ? clampNumber2(Number(options?.borderRadiusPx), 0, 48) : DEFAULT_STYLE_OPTIONS.borderRadiusPx,
    backdropBlurPx: Number.isFinite(options?.backdropBlurPx) ? clampNumber2(Number(options?.backdropBlurPx), 0, 48) : DEFAULT_STYLE_OPTIONS.backdropBlurPx,
    panelBackground: normalizeColor2(options?.panelBackground, DEFAULT_STYLE_OPTIONS.panelBackground),
    panelBorderColor: normalizeColor2(options?.panelBorderColor, DEFAULT_STYLE_OPTIONS.panelBorderColor),
    panelTextColor: normalizeColor2(options?.panelTextColor, DEFAULT_STYLE_OPTIONS.panelTextColor),
    panelShadow: normalizeColor2(options?.panelShadow, DEFAULT_STYLE_OPTIONS.panelShadow),
    inputBackground: normalizeColor2(options?.inputBackground, DEFAULT_STYLE_OPTIONS.inputBackground),
    inputTextColor: normalizeColor2(options?.inputTextColor, DEFAULT_STYLE_OPTIONS.inputTextColor),
    inputPlaceholderColor: normalizeColor2(options?.inputPlaceholderColor, DEFAULT_STYLE_OPTIONS.inputPlaceholderColor),
    buttonBackground: normalizeColor2(options?.buttonBackground, DEFAULT_STYLE_OPTIONS.buttonBackground),
    buttonTextColor: normalizeColor2(options?.buttonTextColor, DEFAULT_STYLE_OPTIONS.buttonTextColor),
    buttonHoverBackground: normalizeColor2(options?.buttonHoverBackground, DEFAULT_STYLE_OPTIONS.buttonHoverBackground),
    buttonDisabledOpacity: Number.isFinite(options?.buttonDisabledOpacity) ? clampNumber2(Number(options?.buttonDisabledOpacity), 0, 1) : DEFAULT_STYLE_OPTIONS.buttonDisabledOpacity,
    statusTextColor: normalizeColor2(options?.statusTextColor, DEFAULT_STYLE_OPTIONS.statusTextColor),
    statusActiveTextColor: normalizeColor2(options?.statusActiveTextColor, DEFAULT_STYLE_OPTIONS.statusActiveTextColor),
    statusCompleteTextColor: normalizeColor2(options?.statusCompleteTextColor, DEFAULT_STYLE_OPTIONS.statusCompleteTextColor)
  };
}
function ensurePaneSearchUiStyles(doc) {
  if (doc.querySelector(`style[${STYLE_MARKER}="1"]`))
    return;
  const style = doc.createElement("style");
  style.setAttribute(STYLE_MARKER, "1");
  style.textContent = STYLE_TEXT;
  doc.head.appendChild(style);
}
function applySearchUiStyleOptions(root, options) {
  root.classList.add(ROOT_CLASS);
  root.style.setProperty("--restty-search-ui-top", `${options.offsetTopPx}px`);
  root.style.setProperty("--restty-search-ui-right", `${options.offsetRightPx}px`);
  root.style.setProperty("--restty-search-ui-min-width", `${options.minWidthPx}px`);
  root.style.setProperty("--restty-search-ui-max-width", `${options.maxWidthPx}px`);
  root.style.setProperty("--restty-search-ui-z-index", `${options.zIndex}`);
  root.style.setProperty("--restty-search-ui-radius", `${options.borderRadiusPx}px`);
  root.style.setProperty("--restty-search-ui-blur", `${options.backdropBlurPx}px`);
  root.style.setProperty("--restty-search-ui-background", options.panelBackground);
  root.style.setProperty("--restty-search-ui-border", options.panelBorderColor);
  root.style.setProperty("--restty-search-ui-text", options.panelTextColor);
  root.style.setProperty("--restty-search-ui-shadow", options.panelShadow);
  root.style.setProperty("--restty-search-ui-input-background", options.inputBackground);
  root.style.setProperty("--restty-search-ui-input-text", options.inputTextColor);
  root.style.setProperty("--restty-search-ui-input-placeholder", options.inputPlaceholderColor);
  root.style.setProperty("--restty-search-ui-button-background", options.buttonBackground);
  root.style.setProperty("--restty-search-ui-button-text", options.buttonTextColor);
  root.style.setProperty("--restty-search-ui-button-hover", options.buttonHoverBackground);
  root.style.setProperty("--restty-search-ui-button-disabled-opacity", options.buttonDisabledOpacity.toFixed(3));
  root.style.setProperty("--restty-search-ui-status", options.statusTextColor);
  root.style.setProperty("--restty-search-ui-status-active", options.statusActiveTextColor);
  root.style.setProperty("--restty-search-ui-status-complete", options.statusCompleteTextColor);
}
function clearSearchUiStyleOptions(root) {
  root.classList.remove(ROOT_CLASS);
  root.style.removeProperty("--restty-search-ui-top");
  root.style.removeProperty("--restty-search-ui-right");
  root.style.removeProperty("--restty-search-ui-min-width");
  root.style.removeProperty("--restty-search-ui-max-width");
  root.style.removeProperty("--restty-search-ui-z-index");
  root.style.removeProperty("--restty-search-ui-radius");
  root.style.removeProperty("--restty-search-ui-blur");
  root.style.removeProperty("--restty-search-ui-background");
  root.style.removeProperty("--restty-search-ui-border");
  root.style.removeProperty("--restty-search-ui-text");
  root.style.removeProperty("--restty-search-ui-shadow");
  root.style.removeProperty("--restty-search-ui-input-background");
  root.style.removeProperty("--restty-search-ui-input-text");
  root.style.removeProperty("--restty-search-ui-input-placeholder");
  root.style.removeProperty("--restty-search-ui-button-background");
  root.style.removeProperty("--restty-search-ui-button-text");
  root.style.removeProperty("--restty-search-ui-button-hover");
  root.style.removeProperty("--restty-search-ui-button-disabled-opacity");
  root.style.removeProperty("--restty-search-ui-status");
  root.style.removeProperty("--restty-search-ui-status-active");
  root.style.removeProperty("--restty-search-ui-status-complete");
}
function defaultStatusFormatter(state2) {
  if (!state2.query)
    return "";
  if (state2.pending)
    return "…";
  if (state2.total <= 0)
    return "0";
  if (state2.selectedIndex !== null) {
    return `${state2.selectedIndex + 1}/${state2.total}`;
  }
  return `${state2.total}`;
}
function isNodeWithinRoot(root, target) {
  return target instanceof Node && root.contains(target);
}
function createPaneSearchUiController(options) {
  const enabled = options.enabled ?? true;
  const paneStates = new Map;
  const ownerDoc = options.root.ownerDocument ?? document;
  const ownerWin = ownerDoc.defaultView ?? window;
  let styleOptions = normalizeStyleOptions(options.styles);
  const shortcutOptions = typeof options.shortcut === "object" ? options.shortcut : { enabled: options.shortcut !== false };
  const statusFormatter = options.statusFormatter ?? defaultStatusFormatter;
  const placeholder = options.placeholder ?? "Find in scrollback";
  const previousButtonText = options.previousButtonText ?? "↑";
  const nextButtonText = options.nextButtonText ?? "↓";
  const clearButtonText = options.clearButtonText ?? "Clear";
  const closeButtonText = options.closeButtonText ?? "×";
  if (enabled) {
    ensurePaneSearchUiStyles(ownerDoc);
    applySearchUiStyleOptions(options.root, styleOptions);
  }
  function getOpenPaneId() {
    for (const [paneId, state2] of paneStates) {
      if (state2.open)
        return paneId;
    }
    return null;
  }
  function syncPaneUi(paneState) {
    const { input, prevButton, nextButton, clearButton, root, status, state: state2 } = paneState;
    if (ownerDoc.activeElement !== input) {
      input.value = state2.query;
    }
    const hasMatches = state2.active && state2.total > 0;
    prevButton.disabled = !hasMatches;
    nextButton.disabled = !hasMatches;
    clearButton.disabled = !state2.query;
    root.dataset.open = paneState.open ? "1" : "0";
    status.textContent = statusFormatter(state2);
    status.dataset.active = state2.active ? "1" : "0";
    status.dataset.complete = state2.complete ? "1" : "0";
    status.dataset.empty = status.textContent ? "0" : "1";
  }
  function restoreFocus(paneState) {
    const target = paneState?.pane.focusTarget ?? paneState?.pane.container ?? null;
    if (target instanceof HTMLElement) {
      target.focus({ preventScroll: true });
    }
  }
  function closeAllExcept(paneId) {
    for (const [id, paneState] of paneStates) {
      if (id === paneId || !paneState.open)
        continue;
      paneState.open = false;
      syncPaneUi(paneState);
    }
  }
  function focusSearchInput(paneState, selectAll = false) {
    paneState.input.focus({ preventScroll: true });
    if (selectAll)
      paneState.input.select();
  }
  function open(paneId, config = {}) {
    if (!enabled)
      return;
    const paneState = paneStates.get(paneId);
    if (!paneState)
      return;
    closeAllExcept(paneId);
    paneState.open = true;
    paneState.state = { ...paneState.pane.app.getSearchState() };
    syncPaneUi(paneState);
    focusSearchInput(paneState, config.selectAll ?? true);
  }
  function close(paneId, config = {}) {
    const paneState = paneStates.get(paneId);
    if (!paneState || !paneState.open)
      return;
    paneState.open = false;
    syncPaneUi(paneState);
    if (config.restoreFocus !== false) {
      restoreFocus(paneState);
    }
  }
  function toggle(paneId, config = {}) {
    if (paneStates.get(paneId)?.open) {
      close(paneId, config);
      return;
    }
    open(paneId, config);
  }
  function registerPane(pane) {
    if (!enabled)
      return;
    const root = ownerDoc.createElement("div");
    root.className = "restty-pane-search";
    root.dataset.open = "0";
    root.setAttribute("role", "search");
    root.setAttribute("aria-label", "Search terminal scrollback");
    const row = ownerDoc.createElement("div");
    row.className = "restty-pane-search-row";
    const input = ownerDoc.createElement("input");
    input.className = "restty-pane-search-input";
    input.type = "text";
    input.placeholder = placeholder;
    input.spellcheck = false;
    input.autocapitalize = "off";
    input.autocomplete = "off";
    input.autocorrect = "off";
    const prevButton = ownerDoc.createElement("button");
    prevButton.className = "restty-pane-search-button";
    prevButton.type = "button";
    prevButton.textContent = previousButtonText;
    prevButton.title = "Match above";
    const nextButton = ownerDoc.createElement("button");
    nextButton.className = "restty-pane-search-button";
    nextButton.type = "button";
    nextButton.textContent = nextButtonText;
    nextButton.title = "Match below";
    const clearButton = ownerDoc.createElement("button");
    clearButton.className = "restty-pane-search-button";
    clearButton.type = "button";
    clearButton.textContent = clearButtonText;
    const closeButton = ownerDoc.createElement("button");
    closeButton.className = "restty-pane-search-button";
    closeButton.type = "button";
    closeButton.textContent = closeButtonText;
    closeButton.title = "Close search";
    closeButton.setAttribute("aria-label", "Close search");
    const status = ownerDoc.createElement("div");
    status.className = "restty-pane-search-status";
    row.append(input, status, prevButton, nextButton, clearButton, closeButton);
    root.append(row);
    if (!pane.container.style.position) {
      pane.container.style.position = "relative";
    }
    pane.container.appendChild(root);
    const paneState = {
      pane,
      root,
      input,
      prevButton,
      nextButton,
      clearButton,
      closeButton,
      status,
      cleanupFns: [],
      state: { ...pane.app.getSearchState() },
      open: false
    };
    const onInput = () => {
      pane.app.setSearchQuery(input.value);
    };
    input.addEventListener("input", onInput);
    paneState.cleanupFns.push(() => {
      input.removeEventListener("input", onInput);
    });
    const onInputKeyDown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (event.shiftKey) {
          pane.app.searchNext();
        } else {
          pane.app.searchPrevious();
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        close(pane.id);
      }
    };
    input.addEventListener("keydown", onInputKeyDown);
    paneState.cleanupFns.push(() => {
      input.removeEventListener("keydown", onInputKeyDown);
    });
    const onPrev = () => {
      pane.app.searchNext();
      focusSearchInput(paneState);
    };
    prevButton.addEventListener("click", onPrev);
    paneState.cleanupFns.push(() => {
      prevButton.removeEventListener("click", onPrev);
    });
    const onNext = () => {
      pane.app.searchPrevious();
      focusSearchInput(paneState);
    };
    nextButton.addEventListener("click", onNext);
    paneState.cleanupFns.push(() => {
      nextButton.removeEventListener("click", onNext);
    });
    const onClear = () => {
      pane.app.clearSearch();
      paneState.state = { ...pane.app.getSearchState() };
      syncPaneUi(paneState);
      focusSearchInput(paneState);
    };
    clearButton.addEventListener("click", onClear);
    paneState.cleanupFns.push(() => {
      clearButton.removeEventListener("click", onClear);
    });
    const onClose = () => {
      close(pane.id);
    };
    closeButton.addEventListener("click", onClose);
    paneState.cleanupFns.push(() => {
      closeButton.removeEventListener("click", onClose);
    });
    paneStates.set(pane.id, paneState);
    syncPaneUi(paneState);
  }
  function unregisterPane(paneId) {
    const paneState = paneStates.get(paneId);
    if (!paneState)
      return;
    for (const cleanup of paneState.cleanupFns) {
      cleanup();
    }
    paneState.root.remove();
    paneStates.delete(paneId);
  }
  function handleSearchState(paneId, state2) {
    const paneState = paneStates.get(paneId);
    if (!paneState)
      return;
    paneState.state = { ...state2 };
    syncPaneUi(paneState);
  }
  function handleActivePaneChange(paneId) {
    const openPaneId = getOpenPaneId();
    if (openPaneId !== null && openPaneId !== paneId) {
      close(openPaneId, { restoreFocus: false });
    }
  }
  function isOpen(paneId) {
    return paneStates.get(paneId)?.open ?? false;
  }
  const onWindowKeyDown = (event) => {
    if (!enabled)
      return;
    if (shortcutOptions.enabled === false)
      return;
    const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
    const primaryModifier = isMac ? event.metaKey : event.ctrlKey;
    if (!primaryModifier || event.altKey || event.repeat || event.key.toLowerCase() !== "f") {
      return;
    }
    if (!isNodeWithinRoot(options.root, event.target) && !isNodeWithinRoot(options.root, ownerDoc.activeElement)) {
      return;
    }
    const pane = options.getFocusedPane() ?? options.getActivePane();
    if (!pane)
      return;
    if (shortcutOptions.canOpen && !shortcutOptions.canOpen(event, pane.id)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    open(pane.id, { selectAll: true });
  };
  ownerWin.addEventListener("keydown", onWindowKeyDown, { capture: true });
  return {
    registerPane,
    unregisterPane,
    handleSearchState,
    handleActivePaneChange,
    open,
    close,
    toggle,
    isOpen,
    getStyleOptions: () => ({ ...styleOptions }),
    setStyleOptions: (next) => {
      styleOptions = normalizeStyleOptions({
        ...styleOptions,
        ...next
      });
      if (enabled) {
        applySearchUiStyleOptions(options.root, styleOptions);
      }
    },
    destroy: () => {
      ownerWin.removeEventListener("keydown", onWindowKeyDown, { capture: true });
      for (const paneId of Array.from(paneStates.keys())) {
        unregisterPane(paneId);
      }
      if (enabled) {
        clearSearchUiStyleOptions(options.root);
      }
    }
  };
}

// src/surface/pane-app-manager.ts
function createImeInput(className) {
  const imeInput = document.createElement("textarea");
  imeInput.className = className;
  imeInput.tabIndex = -1;
  imeInput.autocapitalize = "off";
  imeInput.autocomplete = "off";
  imeInput.autocorrect = "off";
  imeInput.spellcheck = false;
  imeInput.style.position = "fixed";
  imeInput.style.left = "0";
  imeInput.style.top = "0";
  imeInput.style.width = "1em";
  imeInput.style.height = "1em";
  imeInput.style.padding = "0";
  imeInput.style.margin = "0";
  imeInput.style.border = "0";
  imeInput.style.outline = "none";
  imeInput.style.background = "transparent";
  imeInput.style.color = "transparent";
  imeInput.style.caretColor = "transparent";
  imeInput.style.overflow = "hidden";
  imeInput.style.resize = "none";
  imeInput.style.opacity = "0";
  imeInput.style.pointerEvents = "none";
  return imeInput;
}
function defaultInputTargetPredicate(target) {
  return target.classList.contains("pane-ime-input") || target.classList.contains("restty-pane-ime-input");
}
function createResttyAppPaneManager(options) {
  const session = options.session ?? getDefaultResttyAppSession();
  const autoInit = options.autoInit ?? true;
  const paneClassName = options.paneDom?.paneClassName ?? "pane";
  const canvasClassName = options.paneDom?.canvasClassName ?? "pane-canvas";
  const imeInputClassName = options.paneDom?.imeInputClassName ?? "pane-ime-input restty-pane-ime-input";
  const termDebugClassName = options.paneDom?.termDebugClassName ?? "pane-term-debug";
  let contextMenu = options.contextMenu ?? null;
  if (!contextMenu) {
    const defaultMenuConfig = options.defaultContextMenu;
    const enabled = defaultMenuConfig === undefined ? true : typeof defaultMenuConfig === "boolean" ? defaultMenuConfig : defaultMenuConfig.enabled ?? true;
    if (enabled) {
      const config = typeof defaultMenuConfig === "object" && defaultMenuConfig ? defaultMenuConfig : undefined;
      contextMenu = {
        canOpen: config?.canOpen,
        getItems: (pane, manager2) => createDefaultResttyPaneContextMenuItems({
          pane,
          manager: manager2,
          modKeyLabel: config?.modKeyLabel,
          getPtyUrl: config?.getPtyUrl
        })
      };
    }
  }
  let shortcuts = options.shortcuts;
  if (shortcuts === undefined || shortcuts === true) {
    shortcuts = {
      enabled: true,
      isAllowedInputTarget: defaultInputTargetPredicate
    };
  } else if (typeof shortcuts === "object" && !shortcuts.isAllowedInputTarget) {
    shortcuts = {
      ...shortcuts,
      isAllowedInputTarget: defaultInputTargetPredicate
    };
  }
  let manager;
  const searchUiConfig = typeof options.searchUi === "object" && options.searchUi ? options.searchUi : undefined;
  const searchUiController = createPaneSearchUiController({
    root: options.root,
    enabled: options.searchUi === false ? false : searchUiConfig?.enabled ?? true,
    placeholder: searchUiConfig?.placeholder,
    previousButtonText: searchUiConfig?.previousButtonText,
    nextButtonText: searchUiConfig?.nextButtonText,
    clearButtonText: searchUiConfig?.clearButtonText,
    closeButtonText: searchUiConfig?.closeButtonText,
    statusFormatter: searchUiConfig?.statusFormatter,
    shortcut: searchUiConfig?.shortcut,
    styles: searchUiConfig?.styles,
    getPaneById: (paneId) => manager.getPaneById(paneId),
    getActivePane: () => manager.getActivePane(),
    getFocusedPane: () => manager.getFocusedPane()
  });
  manager = createResttyPaneManager({
    root: options.root,
    minPaneSize: options.minPaneSize,
    styles: options.paneStyles,
    shortcuts,
    contextMenu,
    createPane: ({ id, sourcePane }) => {
      const container = document.createElement("div");
      container.className = paneClassName;
      const canvas = document.createElement("canvas");
      canvas.className = canvasClassName;
      canvas.tabIndex = 0;
      const imeInput = createImeInput(imeInputClassName);
      const termDebugEl = document.createElement("pre");
      termDebugEl.className = termDebugClassName;
      termDebugEl.setAttribute("aria-live", "polite");
      container.append(canvas, imeInput, termDebugEl);
      const baseOptions = typeof options.appOptions === "function" ? options.appOptions({ id, sourcePane, canvas, imeInput, termDebugEl }) : options.appOptions ?? {};
      const mergedElements = {
        ...baseOptions.elements,
        termDebugEl: baseOptions.elements?.termDebugEl ?? termDebugEl
      };
      const mergedCallbacks = {
        ...baseOptions.callbacks,
        onSearchState: (state2) => {
          baseOptions.callbacks?.onSearchState?.(state2);
          searchUiController.handleSearchState(id, state2);
        }
      };
      const app = createResttyApp2({
        ...baseOptions,
        canvas,
        imeInput,
        session,
        elements: mergedElements,
        callbacks: mergedCallbacks
      });
      if (autoInit) {
        app.init();
      }
      const pane = {
        id,
        container,
        focusTarget: canvas,
        app,
        canvas,
        imeInput,
        termDebugEl
      };
      searchUiController.registerPane(pane);
      return pane;
    },
    destroyPane: (pane) => {
      searchUiController.unregisterPane(pane.id);
      pane.app.destroy();
    },
    onPaneCreated: options.onPaneCreated,
    onPaneClosed: options.onPaneClosed,
    onPaneSplit: options.onPaneSplit,
    onActivePaneChange: (pane) => {
      searchUiController.handleActivePaneChange(pane?.id ?? null);
      options.onActivePaneChange?.(pane);
    },
    onLayoutChanged: () => {
      options.onLayoutChanged?.();
    }
  });
  const destroy = () => {
    searchUiController.destroy();
    manager.destroy();
  };
  return {
    ...manager,
    openPaneSearch: (id, config) => {
      searchUiController.open(id, config);
    },
    closePaneSearch: (id, config) => {
      searchUiController.close(id, config);
    },
    togglePaneSearch: (id, config) => {
      searchUiController.toggle(id, config);
    },
    isPaneSearchOpen: (id) => searchUiController.isOpen(id),
    getSearchUiStyleOptions: () => searchUiController.getStyleOptions(),
    setSearchUiStyleOptions: (next) => {
      searchUiController.setStyleOptions(next);
    },
    destroy
  };
}

// src/surface/restty/active-pane-api.ts
class ResttyActivePaneApi {
  isPtyConnected() {
    return this.requireActivePaneHandle().isPtyConnected();
  }
  setRenderer(value) {
    this.requireActivePaneHandle().setRenderer(value);
  }
  setPaused(value) {
    this.requireActivePaneHandle().setPaused(value);
  }
  togglePause() {
    this.requireActivePaneHandle().togglePause();
  }
  setFontSize(value) {
    this.requireActivePaneHandle().setFontSize(value);
  }
  applyTheme(theme, sourceLabel) {
    this.requireActivePaneHandle().applyTheme(theme, sourceLabel);
  }
  resetTheme() {
    this.requireActivePaneHandle().resetTheme();
  }
  sendInput(text2, source) {
    this.requireActivePaneHandle().sendInput(text2, source);
  }
  sendKeyInput(text2, source) {
    this.requireActivePaneHandle().sendKeyInput(text2, source);
  }
  clearScreen() {
    this.requireActivePaneHandle().clearScreen();
  }
  loadBinarySnapshot(data) {
    return this.requireActivePaneHandle().loadBinarySnapshot(data);
  }
  getColorForeground() {
    return this.requireActivePaneHandle().getColorForeground();
  }
  getColorBackground() {
    return this.requireActivePaneHandle().getColorBackground();
  }
  getColorCursor() {
    return this.requireActivePaneHandle().getColorCursor();
  }
  getPaletteColor(index) {
    return this.requireActivePaneHandle().getPaletteColor(index);
  }
  getPalette() {
    return this.requireActivePaneHandle().getPalette();
  }
  setMouseMode(value) {
    this.requireActivePaneHandle().setMouseMode(value);
  }
  getMouseStatus() {
    return this.requireActivePaneHandle().getMouseStatus();
  }
  copySelectionToClipboard() {
    return this.requireActivePaneHandle().copySelectionToClipboard();
  }
  pasteFromClipboard() {
    return this.requireActivePaneHandle().pasteFromClipboard();
  }
  openSearch(options) {
    this.requireActivePaneHandle().openSearch(options);
  }
  closeSearch(options) {
    this.requireActivePaneHandle().closeSearch(options);
  }
  toggleSearch(options) {
    this.requireActivePaneHandle().toggleSearch(options);
  }
  isSearchOpen() {
    return this.requireActivePaneHandle().isSearchOpen();
  }
  getSearchUiStyleOptions() {
    return this.requireActivePaneHandle().getSearchUiStyleOptions();
  }
  setSearchUiStyleOptions(options) {
    this.requireActivePaneHandle().setSearchUiStyleOptions(options);
  }
  dumpAtlasForCodepoint(cp) {
    this.requireActivePaneHandle().dumpAtlasForCodepoint(cp);
  }
  updateSize(force) {
    this.requireActivePaneHandle().updateSize(force);
  }
  getBackend() {
    return this.requireActivePaneHandle().getBackend();
  }
}

// src/surface/restty/manager-options.ts
function createMergedPaneAppOptions(deps) {
  return (context) => {
    const paneId = context.id;
    const resolved = typeof deps.appOptions === "function" ? deps.appOptions(context) : deps.appOptions ?? {};
    const resolvedBeforeInput = resolved.beforeInput;
    const resolvedBeforeRenderOutput = resolved.beforeRenderOutput;
    const resolvedCallbacks = resolved.callbacks;
    const paneBaseStages = deps.shaderOps.normalizePaneShaderStages(resolved.shaderStages, paneId);
    deps.shaderOps.setPaneBaseShaderStages(paneId, paneBaseStages);
    const fontSources = deps.getFontSources();
    return {
      ...resolved,
      ...fontSources ? { fontSources } : {},
      shaderStages: deps.shaderOps.buildMergedShaderStages(paneBaseStages),
      callbacks: deps.onDesktopNotification || resolvedCallbacks?.onDesktopNotification ? {
        ...resolvedCallbacks,
        onDesktopNotification: (notification) => {
          resolvedCallbacks?.onDesktopNotification?.(notification);
          deps.onDesktopNotification?.({ ...notification, paneId });
        }
      } : resolvedCallbacks,
      beforeInput: ({ text: text2, source }) => {
        const maybeUserText = resolvedBeforeInput?.({ text: text2, source });
        if (maybeUserText === null)
          return null;
        const current = maybeUserText === undefined ? text2 : maybeUserText;
        return deps.pluginOps.applyInputInterceptors(paneId, current, source);
      },
      beforeRenderOutput: ({ text: text2, source }) => {
        deps.runRenderHooks({
          phase: "before",
          paneId,
          text: text2,
          source,
          dropped: false
        });
        const maybeUserText = resolvedBeforeRenderOutput?.({ text: text2, source });
        if (maybeUserText === null) {
          deps.runRenderHooks({
            phase: "after",
            paneId,
            text: text2,
            source,
            dropped: true
          });
          return null;
        }
        const current = maybeUserText === undefined ? text2 : maybeUserText;
        const next = deps.pluginOps.applyOutputInterceptors(paneId, current, source);
        deps.runRenderHooks({
          phase: "after",
          paneId,
          text: next === null ? current : next,
          source,
          dropped: next === null
        });
        return next;
      }
    };
  };
}
function createPaneManagerEventHandlers(deps) {
  return {
    onPaneCreated: (pane) => {
      deps.shaderOps.syncPaneShaderStages(pane.id);
      deps.emitPluginEvent("pane:created", { paneId: pane.id });
      deps.onPaneCreated?.(pane);
    },
    onPaneClosed: (pane) => {
      deps.shaderOps.removePaneBaseShaderStages(pane.id);
      deps.emitPluginEvent("pane:closed", { paneId: pane.id });
      deps.onPaneClosed?.(pane);
    },
    onPaneSplit: (sourcePane, createdPane, direction) => {
      deps.emitPluginEvent("pane:split", {
        sourcePaneId: sourcePane.id,
        createdPaneId: createdPane.id,
        direction
      });
      deps.onPaneSplit?.(sourcePane, createdPane, direction);
    },
    onActivePaneChange: (pane) => {
      deps.emitPluginEvent("pane:active-changed", { paneId: pane?.id ?? null });
      deps.onActivePaneChange?.(pane);
    },
    onLayoutChanged: () => {
      deps.emitPluginEvent("layout:changed", {});
      deps.onLayoutChanged?.();
    }
  };
}

// src/surface/restty-plugin-runtime.ts
function registerPluginInterceptor(bucket, pluginId, interceptor, options, seq) {
  const entry = {
    id: seq.nextId,
    pluginId,
    priority: Number.isFinite(options?.priority) ? Number(options?.priority) : 0,
    order: seq.nextOrder,
    interceptor
  };
  bucket.push(entry);
  bucket.sort((a3, b3) => {
    if (a3.priority !== b3.priority)
      return a3.priority - b3.priority;
    return a3.order - b3.order;
  });
  return {
    nextId: seq.nextId + 1,
    nextOrder: seq.nextOrder + 1,
    dispose: () => {
      const index = bucket.findIndex((current) => current.id === entry.id);
      if (index >= 0) {
        bucket.splice(index, 1);
      }
    }
  };
}
function applyPluginInterceptors(bucket, kind, payload) {
  let currentText = payload.text;
  for (let i3 = 0;i3 < bucket.length; i3 += 1) {
    const entry = bucket[i3];
    try {
      const result = entry.interceptor({ ...payload, text: currentText });
      if (result === null)
        return null;
      if (typeof result === "string")
        currentText = result;
    } catch (error) {
      console.error(`[restty plugin] ${kind} interceptor error (${entry.pluginId}):`, error);
    }
  }
  return currentText;
}
function runPluginHooks(bucket, kind, payload) {
  for (let i3 = 0;i3 < bucket.length; i3 += 1) {
    const entry = bucket[i3];
    try {
      entry.interceptor(payload);
    } catch (error) {
      console.error(`[restty plugin] ${kind} hook error (${entry.pluginId}):`, error);
    }
  }
}
function attachRuntimeDisposer(runtime, kind, dispose) {
  const entry = {
    kind,
    active: true,
    dispose: () => {
      if (!entry.active)
        return;
      entry.active = false;
      dispose();
    }
  };
  runtime.disposers.push(entry);
  return entry.dispose;
}
function teardownPluginRuntime(runtime) {
  for (let i3 = 0;i3 < runtime.disposers.length; i3 += 1) {
    try {
      runtime.disposers[i3].dispose();
    } catch {
    }
  }
  runtime.disposers.length = 0;
  const cleanup = runtime.cleanup;
  runtime.cleanup = null;
  if (!cleanup)
    return;
  try {
    cleanup();
  } catch (error) {
    console.error(`[restty plugin] cleanup error (${runtime.plugin.id}):`, error);
  }
}
function setPluginLoadError(pluginDiagnostics, pluginId, message) {
  pluginDiagnostics.set(pluginId, {
    id: pluginId,
    version: null,
    apiVersion: null,
    requires: null,
    active: false,
    activatedAt: null,
    lastError: message
  });
}
function patchPluginDiagnostic(pluginDiagnostics, pluginId, patch) {
  const current = pluginDiagnostics.get(pluginId);
  if (!current)
    return;
  pluginDiagnostics.set(pluginId, {
    ...current,
    ...patch
  });
}
function countActiveDisposers(runtime, kind) {
  if (!runtime)
    return 0;
  let count = 0;
  for (let i3 = 0;i3 < runtime.disposers.length; i3 += 1) {
    const entry = runtime.disposers[i3];
    if (entry.active && entry.kind === kind)
      count += 1;
  }
  return count;
}
function buildPluginInfo(pluginId, pluginDiagnostics, pluginRuntimes) {
  const diagnostic = pluginDiagnostics.get(pluginId) ?? null;
  const runtime = pluginRuntimes.get(pluginId) ?? null;
  if (!diagnostic && !runtime)
    return null;
  const plugin = runtime?.plugin;
  return {
    id: pluginId,
    version: plugin?.version?.trim?.() || diagnostic?.version || null,
    apiVersion: plugin?.apiVersion ?? (Number.isFinite(diagnostic?.apiVersion) ? diagnostic?.apiVersion : null),
    requires: plugin?.requires ?? diagnostic?.requires ?? null,
    active: runtime ? true : diagnostic?.active ?? false,
    activatedAt: runtime?.activatedAt ?? diagnostic?.activatedAt ?? null,
    lastError: diagnostic?.lastError ?? null,
    listeners: countActiveDisposers(runtime, "event"),
    inputInterceptors: countActiveDisposers(runtime, "input-interceptor"),
    outputInterceptors: countActiveDisposers(runtime, "output-interceptor"),
    lifecycleHooks: countActiveDisposers(runtime, "lifecycle-hook"),
    renderHooks: countActiveDisposers(runtime, "render-hook"),
    renderStages: countActiveDisposers(runtime, "render-stage")
  };
}
function onPluginEvent(pluginListeners, event, listener) {
  let listeners = pluginListeners.get(event);
  if (!listeners) {
    listeners = new Set;
    pluginListeners.set(event, listeners);
  }
  const wrapped = listener;
  listeners.add(wrapped);
  return () => {
    const current = pluginListeners.get(event);
    if (!current)
      return;
    current.delete(wrapped);
    if (current.size === 0) {
      pluginListeners.delete(event);
    }
  };
}
function emitPluginEvent(pluginListeners, event, payload) {
  const listeners = pluginListeners.get(event);
  if (!listeners || listeners.size === 0)
    return;
  const snapshot = Array.from(listeners);
  for (let i3 = 0;i3 < snapshot.length; i3 += 1) {
    try {
      snapshot[i3](payload);
    } catch (error) {
      console.error(`[restty plugin] listener error (${String(event)}):`, error);
    }
  }
}

// src/surface/restty-plugin-utils.ts
function errorToMessage(error) {
  if (error instanceof Error)
    return error.message || error.name || "Unknown error";
  return String(error);
}
function normalizePluginMetadata(plugin, pluginId) {
  return {
    ...plugin,
    id: pluginId,
    version: plugin.version?.trim?.() || undefined,
    apiVersion: Number.isFinite(plugin.apiVersion) ? Math.trunc(Number(plugin.apiVersion)) : undefined,
    requires: plugin.requires ?? undefined
  };
}
function assertPluginCompatibility(pluginId, plugin, pluginApiVersion) {
  const version = plugin.version?.trim?.();
  if (version !== undefined && !version) {
    throw new Error(`Restty plugin ${pluginId} has an empty version`);
  }
  if (plugin.apiVersion !== undefined) {
    if (!Number.isInteger(plugin.apiVersion) || plugin.apiVersion < 1) {
      throw new Error(`Restty plugin ${pluginId} has invalid apiVersion ${String(plugin.apiVersion)}`);
    }
    if (plugin.apiVersion !== pluginApiVersion) {
      throw new Error(`Restty plugin ${pluginId} requires apiVersion ${plugin.apiVersion}, current is ${pluginApiVersion}`);
    }
  }
  const requirement = plugin.requires?.pluginApi;
  if (requirement === undefined)
    return;
  if (typeof requirement === "number") {
    if (!Number.isInteger(requirement) || requirement < 1) {
      throw new Error(`Restty plugin ${pluginId} has invalid requires.pluginApi value`);
    }
    if (requirement !== pluginApiVersion) {
      throw new Error(`Restty plugin ${pluginId} requires pluginApi ${requirement}, current is ${pluginApiVersion}`);
    }
    return;
  }
  const min = requirement.min;
  const max = requirement.max;
  if (!Number.isInteger(min) || min < 1) {
    throw new Error(`Restty plugin ${pluginId} has invalid requires.pluginApi.min`);
  }
  if (max !== undefined && (!Number.isInteger(max) || max < min)) {
    throw new Error(`Restty plugin ${pluginId} has invalid requires.pluginApi.max`);
  }
  if (pluginApiVersion < min || max !== undefined && pluginApiVersion > max) {
    const range = max === undefined ? `>=${min}` : `${min}-${max}`;
    throw new Error(`Restty plugin ${pluginId} requires pluginApi range ${range}, current is ${pluginApiVersion}`);
  }
}
function lookupPluginRegistryEntry(registry, pluginId) {
  if (registry instanceof Map) {
    return registry.get(pluginId) ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(registry, pluginId)) {
    return registry[pluginId];
  }
  return null;
}
async function resolvePluginRegistryEntry(entry) {
  if (typeof entry === "function") {
    return await entry();
  }
  return entry;
}
function normalizePluginCleanup(cleanup) {
  if (!cleanup)
    return null;
  if (typeof cleanup === "function")
    return cleanup;
  if (typeof cleanup === "object" && typeof cleanup.dispose === "function") {
    return () => cleanup.dispose();
  }
  return null;
}

// src/surface/restty/plugin-dispatcher.ts
class ResttyPluginDispatcher {
  deps;
  pluginListeners = new Map;
  inputInterceptors = [];
  outputInterceptors = [];
  lifecycleHooks = [];
  renderHooks = [];
  nextInterceptorId = 1;
  nextInterceptorOrder = 1;
  constructor(deps) {
    this.deps = deps;
  }
  createPluginContext(runtime) {
    return {
      restty: this.deps.restty,
      options: runtime.options,
      panes: this.deps.panes,
      pane: this.deps.pane,
      activePane: this.deps.activePane,
      focusedPane: this.deps.focusedPane,
      on: (event, listener) => {
        return {
          dispose: this.attachRuntimeDisposer(runtime, "event", this.onPluginEvent(event, listener))
        };
      },
      addInputInterceptor: (interceptor, options) => {
        return {
          dispose: this.attachRuntimeDisposer(runtime, "input-interceptor", this.addInputInterceptor(runtime.plugin.id, interceptor, options))
        };
      },
      addOutputInterceptor: (interceptor, options) => {
        return {
          dispose: this.attachRuntimeDisposer(runtime, "output-interceptor", this.addOutputInterceptor(runtime.plugin.id, interceptor, options))
        };
      },
      addLifecycleHook: (hook, options) => {
        return {
          dispose: this.attachRuntimeDisposer(runtime, "lifecycle-hook", this.addLifecycleHook(runtime.plugin.id, hook, options))
        };
      },
      addRenderHook: (hook, options) => {
        return {
          dispose: this.attachRuntimeDisposer(runtime, "render-hook", this.addRenderHook(runtime.plugin.id, hook, options))
        };
      },
      addRenderStage: (stage) => {
        const rawId = stage?.id?.trim?.() ?? "";
        if (!rawId) {
          throw new Error(`Restty plugin ${runtime.plugin.id} render stage id is required`);
        }
        const stageId = `${runtime.plugin.id}:${rawId}`;
        const normalized = normalizeShaderStage({ ...stage, id: stageId });
        const handle = this.deps.addRenderStage(normalized, runtime.plugin.id);
        return {
          ...handle,
          dispose: this.attachRuntimeDisposer(runtime, "render-stage", handle.dispose)
        };
      }
    };
  }
  applyInputInterceptors(paneId, text2, source) {
    return this.applyInterceptors(this.inputInterceptors, "input", { paneId, text: text2, source });
  }
  applyOutputInterceptors(paneId, text2, source) {
    return this.applyInterceptors(this.outputInterceptors, "output", { paneId, text: text2, source });
  }
  runLifecycleHooks(payload) {
    this.runHooks(this.lifecycleHooks, "lifecycle", payload);
  }
  runRenderHooks(payload) {
    this.runHooks(this.renderHooks, "render", payload);
  }
  emitPluginEvent(event, payload) {
    emitPluginEvent(this.pluginListeners, event, payload);
  }
  attachRuntimeDisposer(runtime, kind, dispose) {
    return attachRuntimeDisposer(runtime, kind, dispose);
  }
  addInputInterceptor(pluginId, interceptor, options) {
    return this.registerInterceptor(this.inputInterceptors, pluginId, interceptor, options);
  }
  addOutputInterceptor(pluginId, interceptor, options) {
    return this.registerInterceptor(this.outputInterceptors, pluginId, interceptor, options);
  }
  addLifecycleHook(pluginId, hook, options) {
    return this.registerInterceptor(this.lifecycleHooks, pluginId, hook, options);
  }
  addRenderHook(pluginId, hook, options) {
    return this.registerInterceptor(this.renderHooks, pluginId, hook, options);
  }
  registerInterceptor(bucket, pluginId, interceptor, options) {
    const result = registerPluginInterceptor(bucket, pluginId, interceptor, options, {
      nextId: this.nextInterceptorId,
      nextOrder: this.nextInterceptorOrder
    });
    this.nextInterceptorId = result.nextId;
    this.nextInterceptorOrder = result.nextOrder;
    return result.dispose;
  }
  applyInterceptors(bucket, kind, payload) {
    return applyPluginInterceptors(bucket, kind, payload);
  }
  runHooks(bucket, kind, payload) {
    runPluginHooks(bucket, kind, payload);
  }
  onPluginEvent(event, listener) {
    return onPluginEvent(this.pluginListeners, event, listener);
  }
}

// src/surface/restty/plugin-ops.ts
class ResttyPluginOps {
  pluginRuntimes = new Map;
  pluginDiagnostics = new Map;
  dispatcher;
  constructor(deps) {
    this.dispatcher = new ResttyPluginDispatcher(deps);
  }
  async use(plugin, options) {
    if (!plugin || typeof plugin !== "object") {
      throw new Error("Restty plugin must be an object");
    }
    const pluginId = plugin.id?.trim?.() ?? "";
    if (!pluginId) {
      throw new Error("Restty plugin id is required");
    }
    if (typeof plugin.activate !== "function") {
      throw new Error(`Restty plugin ${pluginId} must define activate(context)`);
    }
    if (this.pluginRuntimes.has(pluginId))
      return;
    try {
      assertPluginCompatibility(pluginId, plugin, RESTTY_PLUGIN_API_VERSION);
    } catch (error) {
      this.pluginDiagnostics.set(pluginId, {
        id: pluginId,
        version: plugin.version?.trim?.() || null,
        apiVersion: Number.isFinite(plugin.apiVersion) ? Number(plugin.apiVersion) : null,
        requires: plugin.requires ?? null,
        active: false,
        activatedAt: null,
        lastError: errorToMessage(error)
      });
      throw error;
    }
    const runtime = {
      plugin: normalizePluginMetadata(plugin, pluginId),
      cleanup: null,
      activatedAt: Date.now(),
      options,
      disposers: []
    };
    this.pluginDiagnostics.set(pluginId, {
      id: pluginId,
      version: runtime.plugin.version?.trim?.() || null,
      apiVersion: Number.isFinite(runtime.plugin.apiVersion) ? Number(runtime.plugin.apiVersion) : null,
      requires: runtime.plugin.requires ?? null,
      active: false,
      activatedAt: null,
      lastError: null
    });
    this.pluginRuntimes.set(pluginId, runtime);
    try {
      const cleanup = await runtime.plugin.activate(this.dispatcher.createPluginContext(runtime), runtime.options);
      runtime.cleanup = normalizePluginCleanup(cleanup);
      runtime.activatedAt = Date.now();
      this.updatePluginDiagnostic(pluginId, {
        active: true,
        activatedAt: runtime.activatedAt,
        lastError: null
      });
      this.emitPluginEvent("plugin:activated", { pluginId });
    } catch (error) {
      this.teardownPluginRuntime(runtime);
      this.pluginRuntimes.delete(pluginId);
      this.updatePluginDiagnostic(pluginId, {
        active: false,
        activatedAt: null,
        lastError: errorToMessage(error)
      });
      throw error;
    }
  }
  async loadPlugins(manifest, registry) {
    const results = [];
    for (let i3 = 0;i3 < manifest.length; i3 += 1) {
      const item = manifest[i3];
      const pluginId = item.id?.trim?.() ?? "";
      if (!pluginId) {
        results.push({
          id: "",
          status: "failed",
          error: "Restty plugin manifest entry is missing id"
        });
        continue;
      }
      if (item.enabled === false) {
        results.push({ id: pluginId, status: "skipped", error: null });
        continue;
      }
      const entry = lookupPluginRegistryEntry(registry, pluginId);
      if (!entry) {
        const message = `Restty plugin ${pluginId} was not found in registry`;
        this.setPluginLoadError(pluginId, message);
        results.push({ id: pluginId, status: "missing", error: message });
        continue;
      }
      let plugin;
      try {
        plugin = await resolvePluginRegistryEntry(entry);
      } catch (error) {
        const message = errorToMessage(error);
        this.setPluginLoadError(pluginId, message);
        results.push({ id: pluginId, status: "failed", error: message });
        continue;
      }
      const resolvedId = plugin.id?.trim?.() ?? "";
      if (resolvedId !== pluginId) {
        const message = `Restty plugin registry entry ${pluginId} resolved to id ${resolvedId || "(empty)"}`;
        this.setPluginLoadError(pluginId, message);
        results.push({ id: pluginId, status: "failed", error: message });
        continue;
      }
      try {
        await this.use(plugin, item.options);
        results.push({ id: pluginId, status: "loaded", error: null });
      } catch (error) {
        results.push({
          id: pluginId,
          status: "failed",
          error: errorToMessage(error)
        });
      }
    }
    return results;
  }
  unuse(pluginId) {
    const key = pluginId?.trim?.() ?? "";
    if (!key)
      return false;
    const runtime = this.pluginRuntimes.get(key);
    if (!runtime)
      return false;
    this.pluginRuntimes.delete(key);
    this.teardownPluginRuntime(runtime);
    this.updatePluginDiagnostic(key, {
      active: false,
      activatedAt: null
    });
    this.emitPluginEvent("plugin:deactivated", { pluginId: key });
    return true;
  }
  plugins() {
    return Array.from(this.pluginRuntimes.keys());
  }
  pluginInfo(pluginId) {
    if (typeof pluginId === "string") {
      const key = pluginId.trim();
      if (!key)
        return null;
      return this.buildPluginInfo(key);
    }
    const keys = new Set;
    for (const key of this.pluginDiagnostics.keys())
      keys.add(key);
    for (const key of this.pluginRuntimes.keys())
      keys.add(key);
    return Array.from(keys).sort((a3, b3) => a3.localeCompare(b3)).map((key) => this.buildPluginInfo(key)).filter((entry) => entry !== null);
  }
  destroy() {
    const pluginIds = this.plugins();
    for (let i3 = 0;i3 < pluginIds.length; i3 += 1) {
      this.unuse(pluginIds[i3]);
    }
  }
  applyInputInterceptors(paneId, text2, source) {
    return this.dispatcher.applyInputInterceptors(paneId, text2, source);
  }
  applyOutputInterceptors(paneId, text2, source) {
    return this.dispatcher.applyOutputInterceptors(paneId, text2, source);
  }
  runLifecycleHooks(payload) {
    this.dispatcher.runLifecycleHooks(payload);
  }
  runRenderHooks(payload) {
    this.dispatcher.runRenderHooks(payload);
  }
  emitPluginEvent(event, payload) {
    this.dispatcher.emitPluginEvent(event, payload);
  }
  setPluginLoadError(pluginId, message) {
    setPluginLoadError(this.pluginDiagnostics, pluginId, message);
  }
  updatePluginDiagnostic(pluginId, patch) {
    patchPluginDiagnostic(this.pluginDiagnostics, pluginId, patch);
  }
  buildPluginInfo(pluginId) {
    return buildPluginInfo(pluginId, this.pluginDiagnostics, this.pluginRuntimes);
  }
  teardownPluginRuntime(runtime) {
    teardownPluginRuntime(runtime);
  }
}

// src/surface/restty/pane-ops.ts
function requirePaneById(getPaneById, id) {
  const pane = getPaneById(id);
  if (!pane)
    throw new Error(`Restty pane ${id} does not exist`);
  return pane;
}
function makePaneHandle(lookup, id) {
  return new ResttyPaneHandle(() => requirePaneById(lookup.getPaneById, id), {
    open: (paneId, options) => {
      lookup.openPaneSearch(paneId, options);
    },
    close: (paneId, options) => {
      lookup.closePaneSearch(paneId, options);
    },
    toggle: (paneId, options) => {
      lookup.togglePaneSearch(paneId, options);
    },
    isOpen: (paneId) => lookup.isPaneSearchOpen(paneId),
    getStyleOptions: () => lookup.getSearchUiStyleOptions(),
    setStyleOptions: (options) => {
      lookup.setSearchUiStyleOptions(options);
    }
  });
}
function requireActivePaneHandle(lookup) {
  const pane = lookup.getActivePane();
  if (!pane) {
    throw new Error("Restty has no active pane. Create or focus a pane first.");
  }
  return makePaneHandle(lookup, pane.id);
}
function panes(lookup) {
  return lookup.getPanes().map((pane) => makePaneHandle(lookup, pane.id));
}
function pane(lookup, id) {
  if (!lookup.getPaneById(id))
    return null;
  return makePaneHandle(lookup, id);
}
function activePane(lookup) {
  const active = lookup.getActivePane();
  if (!active)
    return null;
  return makePaneHandle(lookup, active.id);
}
function focusedPane(lookup) {
  const focused = lookup.getFocusedPane();
  if (!focused)
    return null;
  return makePaneHandle(lookup, focused.id);
}
function forEachPane(lookup, visitor) {
  const all = lookup.getPanes();
  for (let i3 = 0;i3 < all.length; i3 += 1) {
    visitor(makePaneHandle(lookup, all[i3].id));
  }
}
function createInitialPane(paneManager, hooks, options) {
  hooks.runLifecycleHooks({ phase: "before", action: "create-initial-pane" });
  const pane2 = paneManager.createInitialPane(options);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "create-initial-pane",
    paneId: pane2.id,
    ok: true
  });
  return pane2;
}
function splitActivePane(paneManager, lookup, hooks, direction) {
  const sourcePaneId = lookup.getActivePane()?.id ?? null;
  hooks.runLifecycleHooks({
    phase: "before",
    action: "split-active-pane",
    paneId: sourcePaneId,
    direction
  });
  const pane2 = paneManager.splitActivePane(direction);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "split-active-pane",
    sourcePaneId: sourcePaneId ?? undefined,
    createdPaneId: pane2?.id ?? null,
    direction,
    ok: !!pane2
  });
  return pane2;
}
function splitPane(paneManager, hooks, id, direction) {
  hooks.runLifecycleHooks({
    phase: "before",
    action: "split-pane",
    paneId: id,
    direction
  });
  const pane2 = paneManager.splitPane(id, direction);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "split-pane",
    sourcePaneId: id,
    createdPaneId: pane2?.id ?? null,
    direction,
    ok: !!pane2
  });
  return pane2;
}
function closePane(paneManager, hooks, id) {
  hooks.runLifecycleHooks({ phase: "before", action: "close-pane", paneId: id });
  const ok = paneManager.closePane(id);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "close-pane",
    paneId: id,
    ok
  });
  return ok;
}
function setActivePane(paneManager, lookup, hooks, id, options) {
  hooks.runLifecycleHooks({
    phase: "before",
    action: "set-active-pane",
    paneId: id
  });
  paneManager.setActivePane(id, options);
  const activePaneId = lookup.getActivePane()?.id ?? null;
  hooks.runLifecycleHooks({
    phase: "after",
    action: "set-active-pane",
    paneId: activePaneId,
    ok: activePaneId === id
  });
}
function markPaneFocused(paneManager, lookup, hooks, id, options) {
  hooks.runLifecycleHooks({
    phase: "before",
    action: "mark-pane-focused",
    paneId: id
  });
  paneManager.markPaneFocused(id, options);
  const focusedPaneId = lookup.getFocusedPane()?.id ?? null;
  hooks.runLifecycleHooks({
    phase: "after",
    action: "mark-pane-focused",
    paneId: focusedPaneId,
    ok: focusedPaneId === id
  });
}
function connectPty2(lookup, hooks, url = "") {
  const pane2 = requireActivePaneHandle(lookup);
  hooks.runLifecycleHooks({
    phase: "before",
    action: "connect-pty",
    paneId: pane2.id
  });
  pane2.connectPty(url);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "connect-pty",
    paneId: pane2.id,
    ok: true
  });
}
function disconnectPty2(lookup, hooks) {
  const pane2 = requireActivePaneHandle(lookup);
  hooks.runLifecycleHooks({
    phase: "before",
    action: "disconnect-pty",
    paneId: pane2.id
  });
  pane2.disconnectPty();
  hooks.runLifecycleHooks({
    phase: "after",
    action: "disconnect-pty",
    paneId: pane2.id,
    ok: true
  });
}
function resize(lookup, hooks, cols, rows) {
  const pane2 = requireActivePaneHandle(lookup);
  hooks.runLifecycleHooks({
    phase: "before",
    action: "resize",
    paneId: pane2.id,
    cols,
    rows
  });
  pane2.resize(cols, rows);
  hooks.runLifecycleHooks({
    phase: "after",
    action: "resize",
    paneId: pane2.id,
    cols,
    rows,
    ok: true
  });
  hooks.emitPluginEvent("pane:resized", { paneId: pane2.id, cols, rows });
}
function focus(lookup, hooks) {
  const pane2 = requireActivePaneHandle(lookup);
  hooks.runLifecycleHooks({
    phase: "before",
    action: "focus",
    paneId: pane2.id
  });
  pane2.focus();
  hooks.runLifecycleHooks({
    phase: "after",
    action: "focus",
    paneId: pane2.id,
    ok: true
  });
  hooks.emitPluginEvent("pane:focused", { paneId: pane2.id });
}
function blur(lookup, hooks) {
  const pane2 = requireActivePaneHandle(lookup);
  hooks.runLifecycleHooks({
    phase: "before",
    action: "blur",
    paneId: pane2.id
  });
  pane2.blur();
  hooks.runLifecycleHooks({
    phase: "after",
    action: "blur",
    paneId: pane2.id,
    ok: true
  });
  hooks.emitPluginEvent("pane:blurred", { paneId: pane2.id });
}
function getPaneStyleOptions(paneManager) {
  return paneManager.getStyleOptions();
}
function setPaneStyleOptions(paneManager, options) {
  paneManager.setStyleOptions(options);
}
function getSearchUiStyleOptions(paneManager) {
  return paneManager.getSearchUiStyleOptions();
}
function setSearchUiStyleOptions(paneManager, options) {
  paneManager.setSearchUiStyleOptions(options);
}

// src/surface/restty/shader-ops.ts
class ResttyShaderOps {
  paneBaseShaderStages = new Map;
  globalShaderStages = new Map;
  nextShaderStageOrder = 1;
  deps;
  constructor(deps, shaderStages) {
    this.deps = deps;
    if (shaderStages?.length) {
      const normalized = sortShaderStages(normalizeShaderStages(shaderStages));
      for (let i3 = 0;i3 < normalized.length; i3 += 1) {
        const stage = normalized[i3];
        this.globalShaderStages.set(stage.id, {
          id: stage.id,
          stage,
          order: this.nextShaderStageOrder++,
          ownerPluginId: null
        });
      }
    }
  }
  setShaderStages(stages) {
    this.globalShaderStages.clear();
    const normalized = sortShaderStages(normalizeShaderStages(stages ?? []));
    for (let i3 = 0;i3 < normalized.length; i3 += 1) {
      const stage = normalized[i3];
      this.globalShaderStages.set(stage.id, {
        id: stage.id,
        stage,
        order: this.nextShaderStageOrder++,
        ownerPluginId: null
      });
    }
    this.syncPaneShaderStages();
  }
  getShaderStages() {
    return cloneShaderStages(this.listGlobalShaderStages().map((entry) => entry.stage));
  }
  addShaderStage(stage) {
    const normalized = normalizeShaderStage(stage);
    return this.addManagedShaderStage(normalized, null);
  }
  addManagedShaderStage(stage, ownerPluginId) {
    const normalized = normalizeShaderStage(stage);
    this.globalShaderStages.set(normalized.id, {
      id: normalized.id,
      stage: normalized,
      order: this.nextShaderStageOrder++,
      ownerPluginId
    });
    this.syncPaneShaderStages();
    return {
      id: normalized.id,
      setUniforms: (uniforms) => {
        const current = this.globalShaderStages.get(normalized.id);
        if (!current)
          return;
        const next = normalizeShaderStage({
          ...current.stage,
          uniforms
        });
        this.globalShaderStages.set(normalized.id, {
          ...current,
          stage: next
        });
        this.syncPaneShaderStages();
      },
      setEnabled: (value) => {
        const current = this.globalShaderStages.get(normalized.id);
        if (!current)
          return;
        const next = normalizeShaderStage({
          ...current.stage,
          enabled: Boolean(value)
        });
        this.globalShaderStages.set(normalized.id, {
          ...current,
          stage: next
        });
        this.syncPaneShaderStages();
      },
      dispose: () => {
        this.removeShaderStage(normalized.id);
      }
    };
  }
  removeShaderStage(id) {
    const stageId = id?.trim?.() ?? "";
    if (!stageId)
      return false;
    const removed = this.globalShaderStages.delete(stageId);
    if (removed) {
      this.syncPaneShaderStages();
    }
    return removed;
  }
  normalizePaneShaderStages(stages, paneId) {
    if (!stages?.length)
      return [];
    try {
      return sortShaderStages(normalizeShaderStages(stages));
    } catch (error) {
      console.warn(`[restty shader-stage] invalid pane stage config for pane ${paneId}:`, error);
      return [];
    }
  }
  setPaneBaseShaderStages(paneId, stages) {
    this.paneBaseShaderStages.set(paneId, stages);
  }
  removePaneBaseShaderStages(paneId) {
    this.paneBaseShaderStages.delete(paneId);
  }
  buildMergedShaderStages(baseStages) {
    const merged = new Map;
    for (let i3 = 0;i3 < baseStages.length; i3 += 1) {
      const stage = baseStages[i3];
      merged.set(stage.id, stage);
    }
    const globals = this.listGlobalShaderStages();
    for (let i3 = 0;i3 < globals.length; i3 += 1) {
      const stage = globals[i3].stage;
      if (merged.has(stage.id))
        merged.delete(stage.id);
      merged.set(stage.id, stage);
    }
    return sortShaderStages(Array.from(merged.values()));
  }
  syncPaneShaderStages(paneId) {
    const panes2 = [];
    if (paneId === undefined) {
      panes2.push(...this.deps.getPanes());
    } else {
      const pane2 = this.deps.getPaneById(paneId);
      if (pane2)
        panes2.push(pane2);
    }
    for (let i3 = 0;i3 < panes2.length; i3 += 1) {
      const pane2 = panes2[i3];
      const base = this.paneBaseShaderStages.get(pane2.id) ?? [];
      pane2.app.setShaderStages(this.buildMergedShaderStages(base));
    }
  }
  clear() {
    this.globalShaderStages.clear();
    this.paneBaseShaderStages.clear();
  }
  listGlobalShaderStages() {
    return Array.from(this.globalShaderStages.values()).sort((a3, b3) => a3.order - b3.order);
  }
}

// src/surface/restty.ts
class Restty extends ResttyActivePaneApi {
  paneManager;
  fontSources;
  shaderOps;
  pluginOps;
  constructor(options) {
    super();
    const {
      createInitialPane: createInitialPane2 = true,
      appOptions,
      fontSources,
      shaderStages,
      onDesktopNotification,
      onPaneCreated,
      onPaneClosed,
      onPaneSplit,
      onActivePaneChange,
      onLayoutChanged,
      ...paneManagerOptions
    } = options;
    this.fontSources = fontSources ? [...fontSources] : undefined;
    this.shaderOps = new ResttyShaderOps({
      getPanes: () => this.paneManager.getPanes(),
      getPaneById: (id) => this.paneManager.getPaneById(id)
    }, shaderStages);
    this.pluginOps = new ResttyPluginOps({
      restty: this,
      panes: () => this.panes(),
      pane: (id) => this.pane(id),
      activePane: () => this.activePane(),
      focusedPane: () => this.focusedPane(),
      addRenderStage: (stage, ownerPluginId) => this.shaderOps.addManagedShaderStage(stage, ownerPluginId)
    });
    const mergedAppOptions = createMergedPaneAppOptions({
      appOptions,
      getFontSources: () => this.fontSources,
      onDesktopNotification,
      shaderOps: this.shaderOps,
      pluginOps: this.pluginOps,
      runRenderHooks: (payload) => this.runRenderHooks(payload)
    });
    const paneManagerEventHandlers = createPaneManagerEventHandlers({
      shaderOps: this.shaderOps,
      emitPluginEvent: (event, payload) => this.emitPluginEvent(event, payload),
      onPaneCreated,
      onPaneClosed,
      onPaneSplit,
      onActivePaneChange,
      onLayoutChanged
    });
    this.paneManager = createResttyAppPaneManager({
      ...paneManagerOptions,
      appOptions: mergedAppOptions,
      ...paneManagerEventHandlers
    });
    if (createInitialPane2) {
      const focus2 = typeof createInitialPane2 === "object" ? createInitialPane2.focus ?? true : true;
      this.createInitialPane({ focus: focus2 });
    }
  }
  getPanes() {
    return this.paneManager.getPanes();
  }
  getPaneById(id) {
    return this.paneManager.getPaneById(id);
  }
  getActivePane() {
    return this.paneManager.getActivePane();
  }
  getFocusedPane() {
    return this.paneManager.getFocusedPane();
  }
  panes() {
    return panes(this.paneLookup());
  }
  pane(id) {
    return pane(this.paneLookup(), id);
  }
  activePane() {
    return activePane(this.paneLookup());
  }
  focusedPane() {
    return focusedPane(this.paneLookup());
  }
  forEachPane(visitor) {
    forEachPane(this.paneLookup(), visitor);
  }
  async setFontSources(sources2) {
    this.fontSources = sources2.length ? [...sources2] : undefined;
    const panes2 = this.getPanes();
    const updates = new Array(panes2.length);
    for (let i3 = 0;i3 < panes2.length; i3 += 1) {
      updates[i3] = panes2[i3].app.setFontSources(this.fontSources ?? []);
    }
    await Promise.all(updates);
  }
  setShaderStages(stages) {
    this.shaderOps.setShaderStages(stages);
  }
  getShaderStages() {
    return this.shaderOps.getShaderStages();
  }
  addShaderStage(stage) {
    return this.shaderOps.addShaderStage(stage);
  }
  removeShaderStage(id) {
    return this.shaderOps.removeShaderStage(id);
  }
  createInitialPane(options) {
    return createInitialPane(this.paneManager, this.lifecycleHooks(), options);
  }
  splitActivePane(direction) {
    return splitActivePane(this.paneManager, this.paneLookup(), this.lifecycleHooks(), direction);
  }
  splitPane(id, direction) {
    return splitPane(this.paneManager, this.lifecycleHooks(), id, direction);
  }
  closePane(id) {
    return closePane(this.paneManager, this.lifecycleHooks(), id);
  }
  getPaneStyleOptions() {
    return getPaneStyleOptions(this.paneManager);
  }
  setPaneStyleOptions(options) {
    setPaneStyleOptions(this.paneManager, options);
  }
  getSearchUiStyleOptions() {
    return getSearchUiStyleOptions(this.paneManager);
  }
  setSearchUiStyleOptions(options) {
    setSearchUiStyleOptions(this.paneManager, options);
  }
  setActivePane(id, options) {
    setActivePane(this.paneManager, this.paneLookup(), this.lifecycleHooks(), id, options);
  }
  markPaneFocused(id, options) {
    markPaneFocused(this.paneManager, this.paneLookup(), this.lifecycleHooks(), id, options);
  }
  requestLayoutSync() {
    this.paneManager.requestLayoutSync();
  }
  hideContextMenu() {
    this.paneManager.hideContextMenu();
  }
  async use(plugin, options) {
    await this.pluginOps.use(plugin, options);
  }
  async loadPlugins(manifest, registry) {
    return this.pluginOps.loadPlugins(manifest, registry);
  }
  unuse(pluginId) {
    return this.pluginOps.unuse(pluginId);
  }
  plugins() {
    return this.pluginOps.plugins();
  }
  pluginInfo(pluginId) {
    if (typeof pluginId === "string")
      return this.pluginOps.pluginInfo(pluginId);
    return this.pluginOps.pluginInfo();
  }
  destroy() {
    this.pluginOps.destroy();
    this.shaderOps.clear();
    this.paneManager.destroy();
  }
  connectPty(url = "") {
    connectPty2(this.paneLookup(), this.lifecycleHooks(), url);
  }
  disconnectPty() {
    disconnectPty2(this.paneLookup(), this.lifecycleHooks());
  }
  resize(cols, rows) {
    resize(this.paneLookup(), this.lifecycleAndPluginHooks(), cols, rows);
  }
  focus() {
    focus(this.paneLookup(), this.lifecycleAndPluginHooks());
  }
  blur() {
    blur(this.paneLookup(), this.lifecycleAndPluginHooks());
  }
  paneLookup() {
    const paneManager = this.paneManager;
    return {
      getPanes: () => this.getPanes(),
      getPaneById: (id) => this.getPaneById(id),
      getActivePane: () => this.getActivePane(),
      getFocusedPane: () => this.getFocusedPane(),
      openPaneSearch: (id, options) => {
        paneManager.openPaneSearch(id, options);
      },
      closePaneSearch: (id, options) => {
        paneManager.closePaneSearch(id, options);
      },
      togglePaneSearch: (id, options) => {
        paneManager.togglePaneSearch(id, options);
      },
      isPaneSearchOpen: (id) => paneManager.isPaneSearchOpen(id),
      getSearchUiStyleOptions: () => paneManager.getSearchUiStyleOptions(),
      setSearchUiStyleOptions: (options) => {
        paneManager.setSearchUiStyleOptions(options);
      }
    };
  }
  lifecycleHooks() {
    return {
      runLifecycleHooks: (payload) => this.runLifecycleHooks(payload)
    };
  }
  lifecycleAndPluginHooks() {
    return {
      runLifecycleHooks: (payload) => this.runLifecycleHooks(payload),
      emitPluginEvent: (event, payload) => this.emitPluginEvent(event, payload)
    };
  }
  requireActivePaneHandle() {
    return requireActivePaneHandle(this.paneLookup());
  }
  runLifecycleHooks(payload) {
    this.pluginOps.runLifecycleHooks(payload);
  }
  runRenderHooks(payload) {
    this.pluginOps.runRenderHooks(payload);
  }
  emitPluginEvent(event, payload) {
    this.pluginOps.emitPluginEvent(event, payload);
  }
}
function createRestty(options) {
  return new Restty(options);
}

export { BOX_STYLE_NONE, BOX_STYLE_LIGHT, BOX_STYLE_HEAVY, BOX_STYLE_DOUBLE, isPrivateUse, isSpaceCp, isBoxDrawing, isBlockElement, isLegacyComputing, isPowerline, isBraille, isGraphicsElement, isSymbolCp, applyAlpha, pushRect, pushRectSnapped, pushRectBox, drawBlockElement, BOX_LINE_MAP, drawBoxDrawing, drawBraille, drawPowerline, constrainGlyphBox, RECT_SHADER, GLYPH_SHADER, initWebGPUCore, initWebGPU, initWebGL, ensureInstanceBuffer, configureContext, ensureGLInstanceBuffer, createResizeState, createScrollbarState, ResttyWasm, loadResttyWasm, createInputHandler, isSymbolFont, isNerdSymbolFont, fontMaxCellSpan, fontScaleOverride, fontRasterScale, fontHeightUnits, computeCellMetrics, createGridState, updateGridState, clamp, createFontEntry, resetFontEntry, createFontManagerState, fontHasGlyph, fontAdvanceUnits, glyphWidthUnits, NERD_SYMBOL_RANGES, isNerdSymbolCodepoint, pickFontIndexForText, tryFetchFontBuffer, tryLocalFontBuffer, loadPrimaryFontBuffer, loadFallbackFontBuffers, NERD_CONSTRAINTS, getNerdConstraint, createPtyConnection, connectPty, disconnectPty, sendPtyInput, sendPtyResize, isPtyConnected, createWebSocketPtyTransport, createImeState, setPreedit, clearPreedit, startComposition, updateComposition, endComposition, syncImeSelection, updateImePosition, PREEDIT_BG, PREEDIT_ACTIVE_BG, PREEDIT_FG, PREEDIT_UL, PREEDIT_CARET, createSelectionState, clearSelection, startSelection, updateSelection, endSelection, selectionForRow, normalizeSelectionCell, positionToCell, getSelectionText, copyToClipboard, pasteFromClipboard, parseGhosttyColor, colorToFloats, colorToRgbU32, parseGhosttyTheme, listBuiltinThemeNames, isBuiltinThemeName, getBuiltinThemeSource, getBuiltinTheme, RESTTY_PLUGIN_API_VERSION, ResttyPaneHandle, Restty, createRestty };
